(()=>{const THREE=(()=>{/**
 * @license
 * Copyright 2010-2024 Three.js Authors
 * SPDX-License-Identifier: MIT
 */
const REVISION = '169';

const MOUSE = { LEFT: 0, MIDDLE: 1, RIGHT: 2, ROTATE: 0, DOLLY: 1, PAN: 2 };
const TOUCH = { ROTATE: 0, PAN: 1, DOLLY_PAN: 2, DOLLY_ROTATE: 3 };
const CullFaceNone = 0;
const CullFaceBack = 1;
const CullFaceFront = 2;
const CullFaceFrontBack = 3;
const BasicShadowMap = 0;
const PCFShadowMap = 1;
const PCFSoftShadowMap = 2;
const VSMShadowMap = 3;
const FrontSide = 0;
const BackSide = 1;
const DoubleSide = 2;
const NoBlending = 0;
const NormalBlending = 1;
const AdditiveBlending = 2;
const SubtractiveBlending = 3;
const MultiplyBlending = 4;
const CustomBlending = 5;
const AddEquation = 100;
const SubtractEquation = 101;
const ReverseSubtractEquation = 102;
const MinEquation = 103;
const MaxEquation = 104;
const ZeroFactor = 200;
const OneFactor = 201;
const SrcColorFactor = 202;
const OneMinusSrcColorFactor = 203;
const SrcAlphaFactor = 204;
const OneMinusSrcAlphaFactor = 205;
const DstAlphaFactor = 206;
const OneMinusDstAlphaFactor = 207;
const DstColorFactor = 208;
const OneMinusDstColorFactor = 209;
const SrcAlphaSaturateFactor = 210;
const ConstantColorFactor = 211;
const OneMinusConstantColorFactor = 212;
const ConstantAlphaFactor = 213;
const OneMinusConstantAlphaFactor = 214;
const NeverDepth = 0;
const AlwaysDepth = 1;
const LessDepth = 2;
const LessEqualDepth = 3;
const EqualDepth = 4;
const GreaterEqualDepth = 5;
const GreaterDepth = 6;
const NotEqualDepth = 7;
const MultiplyOperation = 0;
const MixOperation = 1;
const AddOperation = 2;
const NoToneMapping = 0;
const LinearToneMapping = 1;
const ReinhardToneMapping = 2;
const CineonToneMapping = 3;
const ACESFilmicToneMapping = 4;
const CustomToneMapping = 5;
const AgXToneMapping = 6;
const NeutralToneMapping = 7;
const AttachedBindMode = 'attached';
const DetachedBindMode = 'detached';

const UVMapping = 300;
const CubeReflectionMapping = 301;
const CubeRefractionMapping = 302;
const EquirectangularReflectionMapping = 303;
const EquirectangularRefractionMapping = 304;
const CubeUVReflectionMapping = 306;
const RepeatWrapping = 1000;
const ClampToEdgeWrapping = 1001;
const MirroredRepeatWrapping = 1002;
const NearestFilter = 1003;
const NearestMipmapNearestFilter = 1004;
const NearestMipMapNearestFilter = 1004;
const NearestMipmapLinearFilter = 1005;
const NearestMipMapLinearFilter = 1005;
const LinearFilter = 1006;
const LinearMipmapNearestFilter = 1007;
const LinearMipMapNearestFilter = 1007;
const LinearMipmapLinearFilter = 1008;
const LinearMipMapLinearFilter = 1008;
const UnsignedByteType = 1009;
const ByteType = 1010;
const ShortType = 1011;
const UnsignedShortType = 1012;
const IntType = 1013;
const UnsignedIntType = 1014;
const FloatType = 1015;
const HalfFloatType = 1016;
const UnsignedShort4444Type = 1017;
const UnsignedShort5551Type = 1018;
const UnsignedInt248Type = 1020;
const UnsignedInt5999Type = 35902;
const AlphaFormat = 1021;
const RGBFormat = 1022;
const RGBAFormat = 1023;
const LuminanceFormat = 1024;
const LuminanceAlphaFormat = 1025;
const DepthFormat = 1026;
const DepthStencilFormat = 1027;
const RedFormat = 1028;
const RedIntegerFormat = 1029;
const RGFormat = 1030;
const RGIntegerFormat = 1031;
const RGBIntegerFormat = 1032;
const RGBAIntegerFormat = 1033;

const RGB_S3TC_DXT1_Format = 33776;
const RGBA_S3TC_DXT1_Format = 33777;
const RGBA_S3TC_DXT3_Format = 33778;
const RGBA_S3TC_DXT5_Format = 33779;
const RGB_PVRTC_4BPPV1_Format = 35840;
const RGB_PVRTC_2BPPV1_Format = 35841;
const RGBA_PVRTC_4BPPV1_Format = 35842;
const RGBA_PVRTC_2BPPV1_Format = 35843;
const RGB_ETC1_Format = 36196;
const RGB_ETC2_Format = 37492;
const RGBA_ETC2_EAC_Format = 37496;
const RGBA_ASTC_4x4_Format = 37808;
const RGBA_ASTC_5x4_Format = 37809;
const RGBA_ASTC_5x5_Format = 37810;
const RGBA_ASTC_6x5_Format = 37811;
const RGBA_ASTC_6x6_Format = 37812;
const RGBA_ASTC_8x5_Format = 37813;
const RGBA_ASTC_8x6_Format = 37814;
const RGBA_ASTC_8x8_Format = 37815;
const RGBA_ASTC_10x5_Format = 37816;
const RGBA_ASTC_10x6_Format = 37817;
const RGBA_ASTC_10x8_Format = 37818;
const RGBA_ASTC_10x10_Format = 37819;
const RGBA_ASTC_12x10_Format = 37820;
const RGBA_ASTC_12x12_Format = 37821;
const RGBA_BPTC_Format = 36492;
const RGB_BPTC_SIGNED_Format = 36494;
const RGB_BPTC_UNSIGNED_Format = 36495;
const RED_RGTC1_Format = 36283;
const SIGNED_RED_RGTC1_Format = 36284;
const RED_GREEN_RGTC2_Format = 36285;
const SIGNED_RED_GREEN_RGTC2_Format = 36286;
const LoopOnce = 2200;
const LoopRepeat = 2201;
const LoopPingPong = 2202;
const InterpolateDiscrete = 2300;
const InterpolateLinear = 2301;
const InterpolateSmooth = 2302;
const ZeroCurvatureEnding = 2400;
const ZeroSlopeEnding = 2401;
const WrapAroundEnding = 2402;
const NormalAnimationBlendMode = 2500;
const AdditiveAnimationBlendMode = 2501;
const TrianglesDrawMode = 0;
const TriangleStripDrawMode = 1;
const TriangleFanDrawMode = 2;
const BasicDepthPacking = 3200;
const RGBADepthPacking = 3201;
const RGBDepthPacking = 3202;
const RGDepthPacking = 3203;
const TangentSpaceNormalMap = 0;
const ObjectSpaceNormalMap = 1;

// Color space string identifiers, matching CSS Color Module Level 4 and WebGPU names where available.
const NoColorSpace = '';
const SRGBColorSpace = 'srgb';
const LinearSRGBColorSpace = 'srgb-linear';
const DisplayP3ColorSpace = 'display-p3';
const LinearDisplayP3ColorSpace = 'display-p3-linear';

const LinearTransfer = 'linear';
const SRGBTransfer = 'srgb';

const Rec709Primaries = 'rec709';
const P3Primaries = 'p3';

const ZeroStencilOp = 0;
const KeepStencilOp = 7680;
const ReplaceStencilOp = 7681;
const IncrementStencilOp = 7682;
const DecrementStencilOp = 7683;
const IncrementWrapStencilOp = 34055;
const DecrementWrapStencilOp = 34056;
const InvertStencilOp = 5386;

const NeverStencilFunc = 512;
const LessStencilFunc = 513;
const EqualStencilFunc = 514;
const LessEqualStencilFunc = 515;
const GreaterStencilFunc = 516;
const NotEqualStencilFunc = 517;
const GreaterEqualStencilFunc = 518;
const AlwaysStencilFunc = 519;

const NeverCompare = 512;
const LessCompare = 513;
const EqualCompare = 514;
const LessEqualCompare = 515;
const GreaterCompare = 516;
const NotEqualCompare = 517;
const GreaterEqualCompare = 518;
const AlwaysCompare = 519;

const StaticDrawUsage = 35044;
const DynamicDrawUsage = 35048;
const StreamDrawUsage = 35040;
const StaticReadUsage = 35045;
const DynamicReadUsage = 35049;
const StreamReadUsage = 35041;
const StaticCopyUsage = 35046;
const DynamicCopyUsage = 35050;
const StreamCopyUsage = 35042;

const GLSL1 = '100';
const GLSL3 = '300 es';

const WebGLCoordinateSystem = 2000;
const WebGPUCoordinateSystem = 2001;

/**
 * https://github.com/mrdoob/eventdispatcher.js/
 */

class EventDispatcher {

	addEventListener( type, listener ) {

		if ( this._listeners === undefined ) this._listeners = {};

		const listeners = this._listeners;

		if ( listeners[ type ] === undefined ) {

			listeners[ type ] = [];

		}

		if ( listeners[ type ].indexOf( listener ) === - 1 ) {

			listeners[ type ].push( listener );

		}

	}

	hasEventListener( type, listener ) {

		if ( this._listeners === undefined ) return false;

		const listeners = this._listeners;

		return listeners[ type ] !== undefined && listeners[ type ].indexOf( listener ) !== - 1;

	}

	removeEventListener( type, listener ) {

		if ( this._listeners === undefined ) return;

		const listeners = this._listeners;
		const listenerArray = listeners[ type ];

		if ( listenerArray !== undefined ) {

			const index = listenerArray.indexOf( listener );

			if ( index !== - 1 ) {

				listenerArray.splice( index, 1 );

			}

		}

	}

	dispatchEvent( event ) {

		if ( this._listeners === undefined ) return;

		const listeners = this._listeners;
		const listenerArray = listeners[ event.type ];

		if ( listenerArray !== undefined ) {

			event.target = this;

			// Make a copy, in case listeners are removed while iterating.
			const array = listenerArray.slice( 0 );

			for ( let i = 0, l = array.length; i < l; i ++ ) {

				array[ i ].call( this, event );

			}

			event.target = null;

		}

	}

}

const _lut = [ '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '0a', '0b', '0c', '0d', '0e', '0f', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '1a', '1b', '1c', '1d', '1e', '1f', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '2a', '2b', '2c', '2d', '2e', '2f', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '3a', '3b', '3c', '3d', '3e', '3f', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '4a', '4b', '4c', '4d', '4e', '4f', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '5a', '5b', '5c', '5d', '5e', '5f', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '6a', '6b', '6c', '6d', '6e', '6f', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '7a', '7b', '7c', '7d', '7e', '7f', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '8a', '8b', '8c', '8d', '8e', '8f', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '9a', '9b', '9c', '9d', '9e', '9f', 'a0', 'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'a9', 'aa', 'ab', 'ac', 'ad', 'ae', 'af', 'b0', 'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8', 'b9', 'ba', 'bb', 'bc', 'bd', 'be', 'bf', 'c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'ca', 'cb', 'cc', 'cd', 'ce', 'cf', 'd0', 'd1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9', 'da', 'db', 'dc', 'dd', 'de', 'df', 'e0', 'e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9', 'ea', 'eb', 'ec', 'ed', 'ee', 'ef', 'f0', 'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'fa', 'fb', 'fc', 'fd', 'fe', 'ff' ];

let _seed = 1234567;


const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

// http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
function generateUUID() {

	const d0 = Math.random() * 0xffffffff | 0;
	const d1 = Math.random() * 0xffffffff | 0;
	const d2 = Math.random() * 0xffffffff | 0;
	const d3 = Math.random() * 0xffffffff | 0;
	const uuid = _lut[ d0 & 0xff ] + _lut[ d0 >> 8 & 0xff ] + _lut[ d0 >> 16 & 0xff ] + _lut[ d0 >> 24 & 0xff ] + '-' +
			_lut[ d1 & 0xff ] + _lut[ d1 >> 8 & 0xff ] + '-' + _lut[ d1 >> 16 & 0x0f | 0x40 ] + _lut[ d1 >> 24 & 0xff ] + '-' +
			_lut[ d2 & 0x3f | 0x80 ] + _lut[ d2 >> 8 & 0xff ] + '-' + _lut[ d2 >> 16 & 0xff ] + _lut[ d2 >> 24 & 0xff ] +
			_lut[ d3 & 0xff ] + _lut[ d3 >> 8 & 0xff ] + _lut[ d3 >> 16 & 0xff ] + _lut[ d3 >> 24 & 0xff ];

	// .toLowerCase() here flattens concatenated strings to save heap memory space.
	return uuid.toLowerCase();

}

function clamp( value, min, max ) {

	return Math.max( min, Math.min( max, value ) );

}

// compute euclidean modulo of m % n
// https://en.wikipedia.org/wiki/Modulo_operation
function euclideanModulo( n, m ) {

	return ( ( n % m ) + m ) % m;

}

// Linear mapping from range <a1, a2> to range <b1, b2>
function mapLinear( x, a1, a2, b1, b2 ) {

	return b1 + ( x - a1 ) * ( b2 - b1 ) / ( a2 - a1 );

}

// https://www.gamedev.net/tutorials/programming/general-and-gameplay-programming/inverse-lerp-a-super-useful-yet-often-overlooked-function-r5230/
function inverseLerp( x, y, value ) {

	if ( x !== y ) {

		return ( value - x ) / ( y - x );

	} else {

		return 0;

	}

}

// https://en.wikipedia.org/wiki/Linear_interpolation
function lerp( x, y, t ) {

	return ( 1 - t ) * x + t * y;

}

// http://www.rorydriscoll.com/2016/03/07/frame-rate-independent-damping-using-lerp/
function damp( x, y, lambda, dt ) {

	return lerp( x, y, 1 - Math.exp( - lambda * dt ) );

}

// https://www.desmos.com/calculator/vcsjnyz7x4
function pingpong( x, length = 1 ) {

	return length - Math.abs( euclideanModulo( x, length * 2 ) - length );

}

// http://en.wikipedia.org/wiki/Smoothstep
function smoothstep( x, min, max ) {

	if ( x <= min ) return 0;
	if ( x >= max ) return 1;

	x = ( x - min ) / ( max - min );

	return x * x * ( 3 - 2 * x );

}

function smootherstep( x, min, max ) {

	if ( x <= min ) return 0;
	if ( x >= max ) return 1;

	x = ( x - min ) / ( max - min );

	return x * x * x * ( x * ( x * 6 - 15 ) + 10 );

}

// Random integer from <low, high> interval
function randInt( low, high ) {

	return low + Math.floor( Math.random() * ( high - low + 1 ) );

}

// Random float from <low, high> interval
function randFloat( low, high ) {

	return low + Math.random() * ( high - low );

}

// Random float from <-range/2, range/2> interval
function randFloatSpread( range ) {

	return range * ( 0.5 - Math.random() );

}

// Deterministic pseudo-random float in the interval [ 0, 1 ]
function seededRandom( s ) {

	if ( s !== undefined ) _seed = s;

	// Mulberry32 generator

	let t = _seed += 0x6D2B79F5;

	t = Math.imul( t ^ t >>> 15, t | 1 );

	t ^= t + Math.imul( t ^ t >>> 7, t | 61 );

	return ( ( t ^ t >>> 14 ) >>> 0 ) / 4294967296;

}

function degToRad( degrees ) {

	return degrees * DEG2RAD;

}

function radToDeg( radians ) {

	return radians * RAD2DEG;

}

function isPowerOfTwo( value ) {

	return ( value & ( value - 1 ) ) === 0 && value !== 0;

}

function ceilPowerOfTwo( value ) {

	return Math.pow( 2, Math.ceil( Math.log( value ) / Math.LN2 ) );

}

function floorPowerOfTwo( value ) {

	return Math.pow( 2, Math.floor( Math.log( value ) / Math.LN2 ) );

}

function setQuaternionFromProperEuler( q, a, b, c, order ) {

	// Intrinsic Proper Euler Angles - see https://en.wikipedia.org/wiki/Euler_angles

	// rotations are applied to the axes in the order specified by 'order'
	// rotation by angle 'a' is applied first, then by angle 'b', then by angle 'c'
	// angles are in radians

	const cos = Math.cos;
	const sin = Math.sin;

	const c2 = cos( b / 2 );
	const s2 = sin( b / 2 );

	const c13 = cos( ( a + c ) / 2 );
	const s13 = sin( ( a + c ) / 2 );

	const c1_3 = cos( ( a - c ) / 2 );
	const s1_3 = sin( ( a - c ) / 2 );

	const c3_1 = cos( ( c - a ) / 2 );
	const s3_1 = sin( ( c - a ) / 2 );

	switch ( order ) {

		case 'XYX':
			q.set( c2 * s13, s2 * c1_3, s2 * s1_3, c2 * c13 );
			break;

		case 'YZY':
			q.set( s2 * s1_3, c2 * s13, s2 * c1_3, c2 * c13 );
			break;

		case 'ZXZ':
			q.set( s2 * c1_3, s2 * s1_3, c2 * s13, c2 * c13 );
			break;

		case 'XZX':
			q.set( c2 * s13, s2 * s3_1, s2 * c3_1, c2 * c13 );
			break;

		case 'YXY':
			q.set( s2 * c3_1, c2 * s13, s2 * s3_1, c2 * c13 );
			break;

		case 'ZYZ':
			q.set( s2 * s3_1, s2 * c3_1, c2 * s13, c2 * c13 );
			break;

		default:
			console.warn( 'THREE.MathUtils: .setQuaternionFromProperEuler() encountered an unknown order: ' + order );

	}

}

function denormalize( value, array ) {

	switch ( array.constructor ) {

		case Float32Array:

			return value;

		case Uint32Array:

			return value / 4294967295.0;

		case Uint16Array:

			return value / 65535.0;

		case Uint8Array:

			return value / 255.0;

		case Int32Array:

			return Math.max( value / 2147483647.0, - 1.0 );

		case Int16Array:

			return Math.max( value / 32767.0, - 1.0 );

		case Int8Array:

			return Math.max( value / 127.0, - 1.0 );

		default:

			throw new Error( 'Invalid component type.' );

	}

}

function normalize( value, array ) {

	switch ( array.constructor ) {

		case Float32Array:

			return value;

		case Uint32Array:

			return Math.round( value * 4294967295.0 );

		case Uint16Array:

			return Math.round( value * 65535.0 );

		case Uint8Array:

			return Math.round( value * 255.0 );

		case Int32Array:

			return Math.round( value * 2147483647.0 );

		case Int16Array:

			return Math.round( value * 32767.0 );

		case Int8Array:

			return Math.round( value * 127.0 );

		default:

			throw new Error( 'Invalid component type.' );

	}

}

const MathUtils = {
	DEG2RAD: DEG2RAD,
	RAD2DEG: RAD2DEG,
	generateUUID: generateUUID,
	clamp: clamp,
	euclideanModulo: euclideanModulo,
	mapLinear: mapLinear,
	inverseLerp: inverseLerp,
	lerp: lerp,
	damp: damp,
	pingpong: pingpong,
	smoothstep: smoothstep,
	smootherstep: smootherstep,
	randInt: randInt,
	randFloat: randFloat,
	randFloatSpread: randFloatSpread,
	seededRandom: seededRandom,
	degToRad: degToRad,
	radToDeg: radToDeg,
	isPowerOfTwo: isPowerOfTwo,
	ceilPowerOfTwo: ceilPowerOfTwo,
	floorPowerOfTwo: floorPowerOfTwo,
	setQuaternionFromProperEuler: setQuaternionFromProperEuler,
	normalize: normalize,
	denormalize: denormalize
};

class Vector2 {

	constructor( x = 0, y = 0 ) {

		Vector2.prototype.isVector2 = true;

		this.x = x;
		this.y = y;

	}

	get width() {

		return this.x;

	}

	set width( value ) {

		this.x = value;

	}

	get height() {

		return this.y;

	}

	set height( value ) {

		this.y = value;

	}

	set( x, y ) {

		this.x = x;
		this.y = y;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;

		return this;

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ];
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ];

		return this;

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y;

	}

	cross( v ) {

		return this.x * v.y - this.y * v.x;

	}

	lengthSq() {

		return this.x * this.x + this.y * this.y;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	angle() {

		// computes the angle in radians with respect to the positive x-axis

		const angle = Math.atan2( - this.y, - this.x ) + Math.PI;

		return angle;

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y;
		return dx * dx + dy * dy;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );

		return this;

	}

	rotateAround( center, angle ) {

		const c = Math.cos( angle ), s = Math.sin( angle );

		const x = this.x - center.x;
		const y = this.y - center.y;

		this.x = x * c - y * s + center.x;
		this.y = x * s + y * c + center.y;

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;

	}

}

class Matrix3 {

	constructor( n11, n12, n13, n21, n22, n23, n31, n32, n33 ) {

		Matrix3.prototype.isMatrix3 = true;

		this.elements = [

			1, 0, 0,
			0, 1, 0,
			0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n21, n22, n23, n31, n32, n33 );

		}

	}

	set( n11, n12, n13, n21, n22, n23, n31, n32, n33 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 1 ] = n21; te[ 2 ] = n31;
		te[ 3 ] = n12; te[ 4 ] = n22; te[ 5 ] = n32;
		te[ 6 ] = n13; te[ 7 ] = n23; te[ 8 ] = n33;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0,
			0, 1, 0,
			0, 0, 1

		);

		return this;

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ];
		te[ 3 ] = me[ 3 ]; te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ];
		te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ]; te[ 8 ] = me[ 8 ];

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrix3Column( this, 0 );
		yAxis.setFromMatrix3Column( this, 1 );
		zAxis.setFromMatrix3Column( this, 2 );

		return this;

	}

	setFromMatrix4( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 4 ], me[ 8 ],
			me[ 1 ], me[ 5 ], me[ 9 ],
			me[ 2 ], me[ 6 ], me[ 10 ]

		);

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 3 ], a13 = ae[ 6 ];
		const a21 = ae[ 1 ], a22 = ae[ 4 ], a23 = ae[ 7 ];
		const a31 = ae[ 2 ], a32 = ae[ 5 ], a33 = ae[ 8 ];

		const b11 = be[ 0 ], b12 = be[ 3 ], b13 = be[ 6 ];
		const b21 = be[ 1 ], b22 = be[ 4 ], b23 = be[ 7 ];
		const b31 = be[ 2 ], b32 = be[ 5 ], b33 = be[ 8 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31;
		te[ 3 ] = a11 * b12 + a12 * b22 + a13 * b32;
		te[ 6 ] = a11 * b13 + a12 * b23 + a13 * b33;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31;
		te[ 4 ] = a21 * b12 + a22 * b22 + a23 * b32;
		te[ 7 ] = a21 * b13 + a22 * b23 + a23 * b33;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31;
		te[ 5 ] = a31 * b12 + a32 * b22 + a33 * b32;
		te[ 8 ] = a31 * b13 + a32 * b23 + a33 * b33;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 3 ] *= s; te[ 6 ] *= s;
		te[ 1 ] *= s; te[ 4 ] *= s; te[ 7 ] *= s;
		te[ 2 ] *= s; te[ 5 ] *= s; te[ 8 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const a = te[ 0 ], b = te[ 1 ], c = te[ 2 ],
			d = te[ 3 ], e = te[ 4 ], f = te[ 5 ],
			g = te[ 6 ], h = te[ 7 ], i = te[ 8 ];

		return a * e * i - a * f * h - b * d * i + b * f * g + c * d * h - c * e * g;

	}

	invert() {

		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ],
			n12 = te[ 3 ], n22 = te[ 4 ], n32 = te[ 5 ],
			n13 = te[ 6 ], n23 = te[ 7 ], n33 = te[ 8 ],

			t11 = n33 * n22 - n32 * n23,
			t12 = n32 * n13 - n33 * n12,
			t13 = n23 * n12 - n22 * n13,

			det = n11 * t11 + n21 * t12 + n31 * t13;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n31 * n23 - n33 * n21 ) * detInv;
		te[ 2 ] = ( n32 * n21 - n31 * n22 ) * detInv;

		te[ 3 ] = t12 * detInv;
		te[ 4 ] = ( n33 * n11 - n31 * n13 ) * detInv;
		te[ 5 ] = ( n31 * n12 - n32 * n11 ) * detInv;

		te[ 6 ] = t13 * detInv;
		te[ 7 ] = ( n21 * n13 - n23 * n11 ) * detInv;
		te[ 8 ] = ( n22 * n11 - n21 * n12 ) * detInv;

		return this;

	}

	transpose() {

		let tmp;
		const m = this.elements;

		tmp = m[ 1 ]; m[ 1 ] = m[ 3 ]; m[ 3 ] = tmp;
		tmp = m[ 2 ]; m[ 2 ] = m[ 6 ]; m[ 6 ] = tmp;
		tmp = m[ 5 ]; m[ 5 ] = m[ 7 ]; m[ 7 ] = tmp;

		return this;

	}

	getNormalMatrix( matrix4 ) {

		return this.setFromMatrix4( matrix4 ).invert().transpose();

	}

	transposeIntoArray( r ) {

		const m = this.elements;

		r[ 0 ] = m[ 0 ];
		r[ 1 ] = m[ 3 ];
		r[ 2 ] = m[ 6 ];
		r[ 3 ] = m[ 1 ];
		r[ 4 ] = m[ 4 ];
		r[ 5 ] = m[ 7 ];
		r[ 6 ] = m[ 2 ];
		r[ 7 ] = m[ 5 ];
		r[ 8 ] = m[ 8 ];

		return this;

	}

	setUvTransform( tx, ty, sx, sy, rotation, cx, cy ) {

		const c = Math.cos( rotation );
		const s = Math.sin( rotation );

		this.set(
			sx * c, sx * s, - sx * ( c * cx + s * cy ) + cx + tx,
			- sy * s, sy * c, - sy * ( - s * cx + c * cy ) + cy + ty,
			0, 0, 1
		);

		return this;

	}

	//

	scale( sx, sy ) {

		this.premultiply( _m3.makeScale( sx, sy ) );

		return this;

	}

	rotate( theta ) {

		this.premultiply( _m3.makeRotation( - theta ) );

		return this;

	}

	translate( tx, ty ) {

		this.premultiply( _m3.makeTranslation( tx, ty ) );

		return this;

	}

	// for 2D Transforms

	makeTranslation( x, y ) {

		if ( x.isVector2 ) {

			this.set(

				1, 0, x.x,
				0, 1, x.y,
				0, 0, 1

			);

		} else {

			this.set(

				1, 0, x,
				0, 1, y,
				0, 0, 1

			);

		}

		return this;

	}

	makeRotation( theta ) {

		// counterclockwise

		const c = Math.cos( theta );
		const s = Math.sin( theta );

		this.set(

			c, - s, 0,
			s, c, 0,
			0, 0, 1

		);

		return this;

	}

	makeScale( x, y ) {

		this.set(

			x, 0, 0,
			0, y, 0,
			0, 0, 1

		);

		return this;

	}

	//

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 9; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];

		array[ offset + 3 ] = te[ 3 ];
		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];

		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];
		array[ offset + 8 ] = te[ 8 ];

		return array;

	}

	clone() {

		return new this.constructor().fromArray( this.elements );

	}

}

const _m3 = /*@__PURE__*/ new Matrix3();

function arrayNeedsUint32( array ) {

	// assumes larger values usually on last

	for ( let i = array.length - 1; i >= 0; -- i ) {

		if ( array[ i ] >= 65535 ) return true; // account for PRIMITIVE_RESTART_FIXED_INDEX, #24565

	}

	return false;

}

const TYPED_ARRAYS = {
	Int8Array: Int8Array,
	Uint8Array: Uint8Array,
	Uint8ClampedArray: Uint8ClampedArray,
	Int16Array: Int16Array,
	Uint16Array: Uint16Array,
	Int32Array: Int32Array,
	Uint32Array: Uint32Array,
	Float32Array: Float32Array,
	Float64Array: Float64Array
};

function getTypedArray( type, buffer ) {

	return new TYPED_ARRAYS[ type ]( buffer );

}

function createElementNS( name ) {

	return document.createElementNS( 'http://www.w3.org/1999/xhtml', name );

}

function createCanvasElement() {

	const canvas = createElementNS( 'canvas' );
	canvas.style.display = 'block';
	return canvas;

}

const _cache = {};

function warnOnce( message ) {

	if ( message in _cache ) return;

	_cache[ message ] = true;

	console.warn( message );

}

function probeAsync( gl, sync, interval ) {

	return new Promise( function ( resolve, reject ) {

		function probe() {

			switch ( gl.clientWaitSync( sync, gl.SYNC_FLUSH_COMMANDS_BIT, 0 ) ) {

				case gl.WAIT_FAILED:
					reject();
					break;

				case gl.TIMEOUT_EXPIRED:
					setTimeout( probe, interval );
					break;

				default:
					resolve();

			}

		}

		setTimeout( probe, interval );

	} );

}

function toNormalizedProjectionMatrix( projectionMatrix ) {

	const m = projectionMatrix.elements;

	// Convert [-1, 1] to [0, 1] projection matrix
	m[ 2 ] = 0.5 * m[ 2 ] + 0.5 * m[ 3 ];
	m[ 6 ] = 0.5 * m[ 6 ] + 0.5 * m[ 7 ];
	m[ 10 ] = 0.5 * m[ 10 ] + 0.5 * m[ 11 ];
	m[ 14 ] = 0.5 * m[ 14 ] + 0.5 * m[ 15 ];

}

function toReversedProjectionMatrix( projectionMatrix ) {

	const m = projectionMatrix.elements;
	const isPerspectiveMatrix = m[ 11 ] === - 1;

	// Reverse [0, 1] projection matrix
	if ( isPerspectiveMatrix ) {

		m[ 10 ] = - m[ 10 ] - 1;
		m[ 14 ] = - m[ 14 ];

	} else {

		m[ 10 ] = - m[ 10 ];
		m[ 14 ] = - m[ 14 ] + 1;

	}

}

/**
 * Matrices converting P3 <-> Rec. 709 primaries, without gamut mapping
 * or clipping. Based on W3C specifications for sRGB and Display P3,
 * and ICC specifications for the D50 connection space. Values in/out
 * are _linear_ sRGB and _linear_ Display P3.
 *
 * Note that both sRGB and Display P3 use the sRGB transfer functions.
 *
 * Reference:
 * - http://www.russellcottrell.com/photo/matrixCalculator.htm
 */

const LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = /*@__PURE__*/ new Matrix3().set(
	0.8224621, 0.177538, 0.0,
	0.0331941, 0.9668058, 0.0,
	0.0170827, 0.0723974, 0.9105199,
);

const LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = /*@__PURE__*/ new Matrix3().set(
	1.2249401, - 0.2249404, 0.0,
	- 0.0420569, 1.0420571, 0.0,
	- 0.0196376, - 0.0786361, 1.0982735
);

/**
 * Defines supported color spaces by transfer function and primaries,
 * and provides conversions to/from the Linear-sRGB reference space.
 */
const COLOR_SPACES = {
	[ LinearSRGBColorSpace ]: {
		transfer: LinearTransfer,
		primaries: Rec709Primaries,
		luminanceCoefficients: [ 0.2126, 0.7152, 0.0722 ],
		toReference: ( color ) => color,
		fromReference: ( color ) => color,
	},
	[ SRGBColorSpace ]: {
		transfer: SRGBTransfer,
		primaries: Rec709Primaries,
		luminanceCoefficients: [ 0.2126, 0.7152, 0.0722 ],
		toReference: ( color ) => color.convertSRGBToLinear(),
		fromReference: ( color ) => color.convertLinearToSRGB(),
	},
	[ LinearDisplayP3ColorSpace ]: {
		transfer: LinearTransfer,
		primaries: P3Primaries,
		luminanceCoefficients: [ 0.2289, 0.6917, 0.0793 ],
		toReference: ( color ) => color.applyMatrix3( LINEAR_DISPLAY_P3_TO_LINEAR_SRGB ),
		fromReference: ( color ) => color.applyMatrix3( LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 ),
	},
	[ DisplayP3ColorSpace ]: {
		transfer: SRGBTransfer,
		primaries: P3Primaries,
		luminanceCoefficients: [ 0.2289, 0.6917, 0.0793 ],
		toReference: ( color ) => color.convertSRGBToLinear().applyMatrix3( LINEAR_DISPLAY_P3_TO_LINEAR_SRGB ),
		fromReference: ( color ) => color.applyMatrix3( LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 ).convertLinearToSRGB(),
	},
};

const SUPPORTED_WORKING_COLOR_SPACES = new Set( [ LinearSRGBColorSpace, LinearDisplayP3ColorSpace ] );

const ColorManagement = {

	enabled: true,

	_workingColorSpace: LinearSRGBColorSpace,

	get workingColorSpace() {

		return this._workingColorSpace;

	},

	set workingColorSpace( colorSpace ) {

		if ( ! SUPPORTED_WORKING_COLOR_SPACES.has( colorSpace ) ) {

			throw new Error( `Unsupported working color space, "${ colorSpace }".` );

		}

		this._workingColorSpace = colorSpace;

	},

	convert: function ( color, sourceColorSpace, targetColorSpace ) {

		if ( this.enabled === false || sourceColorSpace === targetColorSpace || ! sourceColorSpace || ! targetColorSpace ) {

			return color;

		}

		const sourceToReference = COLOR_SPACES[ sourceColorSpace ].toReference;
		const targetFromReference = COLOR_SPACES[ targetColorSpace ].fromReference;

		return targetFromReference( sourceToReference( color ) );

	},

	fromWorkingColorSpace: function ( color, targetColorSpace ) {

		return this.convert( color, this._workingColorSpace, targetColorSpace );

	},

	toWorkingColorSpace: function ( color, sourceColorSpace ) {

		return this.convert( color, sourceColorSpace, this._workingColorSpace );

	},

	getPrimaries: function ( colorSpace ) {

		return COLOR_SPACES[ colorSpace ].primaries;

	},

	getTransfer: function ( colorSpace ) {

		if ( colorSpace === NoColorSpace ) return LinearTransfer;

		return COLOR_SPACES[ colorSpace ].transfer;

	},

	getLuminanceCoefficients: function ( target, colorSpace = this._workingColorSpace ) {

		return target.fromArray( COLOR_SPACES[ colorSpace ].luminanceCoefficients );

	},

};


function SRGBToLinear( c ) {

	return ( c < 0.04045 ) ? c * 0.0773993808 : Math.pow( c * 0.9478672986 + 0.0521327014, 2.4 );

}

function LinearToSRGB( c ) {

	return ( c < 0.0031308 ) ? c * 12.92 : 1.055 * ( Math.pow( c, 0.41666 ) ) - 0.055;

}

let _canvas;

class ImageUtils {

	static getDataURL( image ) {

		if ( /^data:/i.test( image.src ) ) {

			return image.src;

		}

		if ( typeof HTMLCanvasElement === 'undefined' ) {

			return image.src;

		}

		let canvas;

		if ( image instanceof HTMLCanvasElement ) {

			canvas = image;

		} else {

			if ( _canvas === undefined ) _canvas = createElementNS( 'canvas' );

			_canvas.width = image.width;
			_canvas.height = image.height;

			const context = _canvas.getContext( '2d' );

			if ( image instanceof ImageData ) {

				context.putImageData( image, 0, 0 );

			} else {

				context.drawImage( image, 0, 0, image.width, image.height );

			}

			canvas = _canvas;

		}

		if ( canvas.width > 2048 || canvas.height > 2048 ) {

			console.warn( 'THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons', image );

			return canvas.toDataURL( 'image/jpeg', 0.6 );

		} else {

			return canvas.toDataURL( 'image/png' );

		}

	}

	static sRGBToLinear( image ) {

		if ( ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) ||
			( typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ) ||
			( typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap ) ) {

			const canvas = createElementNS( 'canvas' );

			canvas.width = image.width;
			canvas.height = image.height;

			const context = canvas.getContext( '2d' );
			context.drawImage( image, 0, 0, image.width, image.height );

			const imageData = context.getImageData( 0, 0, image.width, image.height );
			const data = imageData.data;

			for ( let i = 0; i < data.length; i ++ ) {

				data[ i ] = SRGBToLinear( data[ i ] / 255 ) * 255;

			}

			context.putImageData( imageData, 0, 0 );

			return canvas;

		} else if ( image.data ) {

			const data = image.data.slice( 0 );

			for ( let i = 0; i < data.length; i ++ ) {

				if ( data instanceof Uint8Array || data instanceof Uint8ClampedArray ) {

					data[ i ] = Math.floor( SRGBToLinear( data[ i ] / 255 ) * 255 );

				} else {

					// assuming float

					data[ i ] = SRGBToLinear( data[ i ] );

				}

			}

			return {
				data: data,
				width: image.width,
				height: image.height
			};

		} else {

			console.warn( 'THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied.' );
			return image;

		}

	}

}

let _sourceId = 0;

class Source {

	constructor( data = null ) {

		this.isSource = true;

		Object.defineProperty( this, 'id', { value: _sourceId ++ } );

		this.uuid = generateUUID();

		this.data = data;
		this.dataReady = true;

		this.version = 0;

	}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( ! isRootObject && meta.images[ this.uuid ] !== undefined ) {

			return meta.images[ this.uuid ];

		}

		const output = {
			uuid: this.uuid,
			url: ''
		};

		const data = this.data;

		if ( data !== null ) {

			let url;

			if ( Array.isArray( data ) ) {

				// cube texture

				url = [];

				for ( let i = 0, l = data.length; i < l; i ++ ) {

					if ( data[ i ].isDataTexture ) {

						url.push( serializeImage( data[ i ].image ) );

					} else {

						url.push( serializeImage( data[ i ] ) );

					}

				}

			} else {

				// texture

				url = serializeImage( data );

			}

			output.url = url;

		}

		if ( ! isRootObject ) {

			meta.images[ this.uuid ] = output;

		}

		return output;

	}

}

function serializeImage( image ) {

	if ( ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) ||
		( typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ) ||
		( typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap ) ) {

		// default images

		return ImageUtils.getDataURL( image );

	} else {

		if ( image.data ) {

			// images of DataTexture

			return {
				data: Array.from( image.data ),
				width: image.width,
				height: image.height,
				type: image.data.constructor.name
			};

		} else {

			console.warn( 'THREE.Texture: Unable to serialize Texture.' );
			return {};

		}

	}

}

let _textureId = 0;

class Texture extends EventDispatcher {

	constructor( image = Texture.DEFAULT_IMAGE, mapping = Texture.DEFAULT_MAPPING, wrapS = ClampToEdgeWrapping, wrapT = ClampToEdgeWrapping, magFilter = LinearFilter, minFilter = LinearMipmapLinearFilter, format = RGBAFormat, type = UnsignedByteType, anisotropy = Texture.DEFAULT_ANISOTROPY, colorSpace = NoColorSpace ) {

		super();

		this.isTexture = true;

		Object.defineProperty( this, 'id', { value: _textureId ++ } );

		this.uuid = generateUUID();

		this.name = '';

		this.source = new Source( image );
		this.mipmaps = [];

		this.mapping = mapping;
		this.channel = 0;

		this.wrapS = wrapS;
		this.wrapT = wrapT;

		this.magFilter = magFilter;
		this.minFilter = minFilter;

		this.anisotropy = anisotropy;

		this.format = format;
		this.internalFormat = null;
		this.type = type;

		this.offset = new Vector2( 0, 0 );
		this.repeat = new Vector2( 1, 1 );
		this.center = new Vector2( 0, 0 );
		this.rotation = 0;

		this.matrixAutoUpdate = true;
		this.matrix = new Matrix3();

		this.generateMipmaps = true;
		this.premultiplyAlpha = false;
		this.flipY = true;
		this.unpackAlignment = 4;	// valid values: 1, 2, 4, 8 (see http://www.khronos.org/opengles/sdk/docs/man/xhtml/glPixelStorei.xml)

		this.colorSpace = colorSpace;

		this.userData = {};

		this.version = 0;
		this.onUpdate = null;

		this.isRenderTargetTexture = false; // indicates whether a texture belongs to a render target or not
		this.pmremVersion = 0; // indicates whether this texture should be processed by PMREMGenerator or not (only relevant for render target textures)

	}

	get image() {

		return this.source.data;

	}

	set image( value = null ) {

		this.source.data = value;

	}

	updateMatrix() {

		this.matrix.setUvTransform( this.offset.x, this.offset.y, this.repeat.x, this.repeat.y, this.rotation, this.center.x, this.center.y );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.name = source.name;

		this.source = source.source;
		this.mipmaps = source.mipmaps.slice( 0 );

		this.mapping = source.mapping;
		this.channel = source.channel;

		this.wrapS = source.wrapS;
		this.wrapT = source.wrapT;

		this.magFilter = source.magFilter;
		this.minFilter = source.minFilter;

		this.anisotropy = source.anisotropy;

		this.format = source.format;
		this.internalFormat = source.internalFormat;
		this.type = source.type;

		this.offset.copy( source.offset );
		this.repeat.copy( source.repeat );
		this.center.copy( source.center );
		this.rotation = source.rotation;

		this.matrixAutoUpdate = source.matrixAutoUpdate;
		this.matrix.copy( source.matrix );

		this.generateMipmaps = source.generateMipmaps;
		this.premultiplyAlpha = source.premultiplyAlpha;
		this.flipY = source.flipY;
		this.unpackAlignment = source.unpackAlignment;
		this.colorSpace = source.colorSpace;

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		this.needsUpdate = true;

		return this;

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( ! isRootObject && meta.textures[ this.uuid ] !== undefined ) {

			return meta.textures[ this.uuid ];

		}

		const output = {

			metadata: {
				version: 4.6,
				type: 'Texture',
				generator: 'Texture.toJSON'
			},

			uuid: this.uuid,
			name: this.name,

			image: this.source.toJSON( meta ).uuid,

			mapping: this.mapping,
			channel: this.channel,

			repeat: [ this.repeat.x, this.repeat.y ],
			offset: [ this.offset.x, this.offset.y ],
			center: [ this.center.x, this.center.y ],
			rotation: this.rotation,

			wrap: [ this.wrapS, this.wrapT ],

			format: this.format,
			internalFormat: this.internalFormat,
			type: this.type,
			colorSpace: this.colorSpace,

			minFilter: this.minFilter,
			magFilter: this.magFilter,
			anisotropy: this.anisotropy,

			flipY: this.flipY,

			generateMipmaps: this.generateMipmaps,
			premultiplyAlpha: this.premultiplyAlpha,
			unpackAlignment: this.unpackAlignment

		};

		if ( Object.keys( this.userData ).length > 0 ) output.userData = this.userData;

		if ( ! isRootObject ) {

			meta.textures[ this.uuid ] = output;

		}

		return output;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

	transformUv( uv ) {

		if ( this.mapping !== UVMapping ) return uv;

		uv.applyMatrix3( this.matrix );

		if ( uv.x < 0 || uv.x > 1 ) {

			switch ( this.wrapS ) {

				case RepeatWrapping:

					uv.x = uv.x - Math.floor( uv.x );
					break;

				case ClampToEdgeWrapping:

					uv.x = uv.x < 0 ? 0 : 1;
					break;

				case MirroredRepeatWrapping:

					if ( Math.abs( Math.floor( uv.x ) % 2 ) === 1 ) {

						uv.x = Math.ceil( uv.x ) - uv.x;

					} else {

						uv.x = uv.x - Math.floor( uv.x );

					}

					break;

			}

		}

		if ( uv.y < 0 || uv.y > 1 ) {

			switch ( this.wrapT ) {

				case RepeatWrapping:

					uv.y = uv.y - Math.floor( uv.y );
					break;

				case ClampToEdgeWrapping:

					uv.y = uv.y < 0 ? 0 : 1;
					break;

				case MirroredRepeatWrapping:

					if ( Math.abs( Math.floor( uv.y ) % 2 ) === 1 ) {

						uv.y = Math.ceil( uv.y ) - uv.y;

					} else {

						uv.y = uv.y - Math.floor( uv.y );

					}

					break;

			}

		}

		if ( this.flipY ) {

			uv.y = 1 - uv.y;

		}

		return uv;

	}

	set needsUpdate( value ) {

		if ( value === true ) {

			this.version ++;
			this.source.needsUpdate = true;

		}

	}

	set needsPMREMUpdate( value ) {

		if ( value === true ) {

			this.pmremVersion ++;

		}

	}

}

Texture.DEFAULT_IMAGE = null;
Texture.DEFAULT_MAPPING = UVMapping;
Texture.DEFAULT_ANISOTROPY = 1;

class Vector4 {

	constructor( x = 0, y = 0, z = 0, w = 1 ) {

		Vector4.prototype.isVector4 = true;

		this.x = x;
		this.y = y;
		this.z = z;
		this.w = w;

	}

	get width() {

		return this.z;

	}

	set width( value ) {

		this.z = value;

	}

	get height() {

		return this.w;

	}

	set height( value ) {

		this.w = value;

	}

	set( x, y, z, w ) {

		this.x = x;
		this.y = y;
		this.z = z;
		this.w = w;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;
		this.z = scalar;
		this.w = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setZ( z ) {

		this.z = z;

		return this;

	}

	setW( w ) {

		this.w = w;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			case 2: this.z = value; break;
			case 3: this.w = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			case 2: return this.z;
			case 3: return this.w;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y, this.z, this.w );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;
		this.z = v.z;
		this.w = ( v.w !== undefined ) ? v.w : 1;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;
		this.z += v.z;
		this.w += v.w;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;
		this.z += s;
		this.w += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;
		this.z = a.z + b.z;
		this.w = a.w + b.w;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;
		this.z += v.z * s;
		this.w += v.w * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;
		this.z -= v.z;
		this.w -= v.w;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;
		this.z -= s;
		this.w -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;
		this.z = a.z - b.z;
		this.w = a.w - b.w;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;
		this.z *= v.z;
		this.w *= v.w;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;
		this.z *= scalar;
		this.w *= scalar;

		return this;

	}

	applyMatrix4( m ) {

		const x = this.x, y = this.y, z = this.z, w = this.w;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] * w;
		this.y = e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] * w;
		this.z = e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] * w;
		this.w = e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] * w;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	setAxisAngleFromQuaternion( q ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/quaternionToAngle/index.htm

		// q is assumed to be normalized

		this.w = 2 * Math.acos( q.w );

		const s = Math.sqrt( 1 - q.w * q.w );

		if ( s < 0.0001 ) {

			this.x = 1;
			this.y = 0;
			this.z = 0;

		} else {

			this.x = q.x / s;
			this.y = q.y / s;
			this.z = q.z / s;

		}

		return this;

	}

	setAxisAngleFromRotationMatrix( m ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToAngle/index.htm

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		let angle, x, y, z; // variables for result
		const epsilon = 0.01,		// margin to allow for rounding errors
			epsilon2 = 0.1,		// margin to distinguish between 0 and 180 degrees

			te = m.elements,

			m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ],
			m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ],
			m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ];

		if ( ( Math.abs( m12 - m21 ) < epsilon ) &&
		     ( Math.abs( m13 - m31 ) < epsilon ) &&
		     ( Math.abs( m23 - m32 ) < epsilon ) ) {

			// singularity found
			// first check for identity matrix which must have +1 for all terms
			// in leading diagonal and zero in other terms

			if ( ( Math.abs( m12 + m21 ) < epsilon2 ) &&
			     ( Math.abs( m13 + m31 ) < epsilon2 ) &&
			     ( Math.abs( m23 + m32 ) < epsilon2 ) &&
			     ( Math.abs( m11 + m22 + m33 - 3 ) < epsilon2 ) ) {

				// this singularity is identity matrix so angle = 0

				this.set( 1, 0, 0, 0 );

				return this; // zero angle, arbitrary axis

			}

			// otherwise this singularity is angle = 180

			angle = Math.PI;

			const xx = ( m11 + 1 ) / 2;
			const yy = ( m22 + 1 ) / 2;
			const zz = ( m33 + 1 ) / 2;
			const xy = ( m12 + m21 ) / 4;
			const xz = ( m13 + m31 ) / 4;
			const yz = ( m23 + m32 ) / 4;

			if ( ( xx > yy ) && ( xx > zz ) ) {

				// m11 is the largest diagonal term

				if ( xx < epsilon ) {

					x = 0;
					y = 0.707106781;
					z = 0.707106781;

				} else {

					x = Math.sqrt( xx );
					y = xy / x;
					z = xz / x;

				}

			} else if ( yy > zz ) {

				// m22 is the largest diagonal term

				if ( yy < epsilon ) {

					x = 0.707106781;
					y = 0;
					z = 0.707106781;

				} else {

					y = Math.sqrt( yy );
					x = xy / y;
					z = yz / y;

				}

			} else {

				// m33 is the largest diagonal term so base result on this

				if ( zz < epsilon ) {

					x = 0.707106781;
					y = 0.707106781;
					z = 0;

				} else {

					z = Math.sqrt( zz );
					x = xz / z;
					y = yz / z;

				}

			}

			this.set( x, y, z, angle );

			return this; // return 180 deg rotation

		}

		// as we have reached here there are no singularities so we can handle normally

		let s = Math.sqrt( ( m32 - m23 ) * ( m32 - m23 ) +
			( m13 - m31 ) * ( m13 - m31 ) +
			( m21 - m12 ) * ( m21 - m12 ) ); // used to normalize

		if ( Math.abs( s ) < 0.001 ) s = 1;

		// prevent divide by zero, should not happen if matrix is orthogonal and should be
		// caught by singularity test above, but I've left it in just in case

		this.x = ( m32 - m23 ) / s;
		this.y = ( m13 - m31 ) / s;
		this.z = ( m21 - m12 ) / s;
		this.w = Math.acos( ( m11 + m22 + m33 - 1 ) / 2 );

		return this;

	}

	setFromMatrixPosition( m ) {

		const e = m.elements;

		this.x = e[ 12 ];
		this.y = e[ 13 ];
		this.z = e[ 14 ];
		this.w = e[ 15 ];

		return this;

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );
		this.z = Math.min( this.z, v.z );
		this.w = Math.min( this.w, v.w );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );
		this.z = Math.max( this.z, v.z );
		this.w = Math.max( this.w, v.w );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );
		this.z = Math.max( min.z, Math.min( max.z, this.z ) );
		this.w = Math.max( min.w, Math.min( max.w, this.w ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );
		this.z = Math.max( minVal, Math.min( maxVal, this.z ) );
		this.w = Math.max( minVal, Math.min( maxVal, this.w ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );
		this.z = Math.floor( this.z );
		this.w = Math.floor( this.w );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );
		this.z = Math.ceil( this.z );
		this.w = Math.ceil( this.w );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );
		this.z = Math.round( this.z );
		this.w = Math.round( this.w );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );
		this.z = Math.trunc( this.z );
		this.w = Math.trunc( this.w );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;
		this.z = - this.z;
		this.w = - this.w;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y + this.z * v.z + this.w * v.w;

	}

	lengthSq() {

		return this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y ) + Math.abs( this.z ) + Math.abs( this.w );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;
		this.z += ( v.z - this.z ) * alpha;
		this.w += ( v.w - this.w ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;
		this.z = v1.z + ( v2.z - v1.z ) * alpha;
		this.w = v1.w + ( v2.w - v1.w ) * alpha;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) && ( v.z === this.z ) && ( v.w === this.w ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];
		this.z = array[ offset + 2 ];
		this.w = array[ offset + 3 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;
		array[ offset + 2 ] = this.z;
		array[ offset + 3 ] = this.w;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );
		this.z = attribute.getZ( index );
		this.w = attribute.getW( index );

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();
		this.z = Math.random();
		this.w = Math.random();

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;
		yield this.z;
		yield this.w;

	}

}

/*
 In options, we can specify:
 * Texture parameters for an auto-generated target texture
 * depthBuffer/stencilBuffer: Booleans to indicate if we should generate these buffers
*/
class RenderTarget extends EventDispatcher {

	constructor( width = 1, height = 1, options = {} ) {

		super();

		this.isRenderTarget = true;

		this.width = width;
		this.height = height;
		this.depth = 1;

		this.scissor = new Vector4( 0, 0, width, height );
		this.scissorTest = false;

		this.viewport = new Vector4( 0, 0, width, height );

		const image = { width: width, height: height, depth: 1 };

		options = Object.assign( {
			generateMipmaps: false,
			internalFormat: null,
			minFilter: LinearFilter,
			depthBuffer: true,
			stencilBuffer: false,
			resolveDepthBuffer: true,
			resolveStencilBuffer: true,
			depthTexture: null,
			samples: 0,
			count: 1
		}, options );

		const texture = new Texture( image, options.mapping, options.wrapS, options.wrapT, options.magFilter, options.minFilter, options.format, options.type, options.anisotropy, options.colorSpace );

		texture.flipY = false;
		texture.generateMipmaps = options.generateMipmaps;
		texture.internalFormat = options.internalFormat;

		this.textures = [];

		const count = options.count;
		for ( let i = 0; i < count; i ++ ) {

			this.textures[ i ] = texture.clone();
			this.textures[ i ].isRenderTargetTexture = true;

		}

		this.depthBuffer = options.depthBuffer;
		this.stencilBuffer = options.stencilBuffer;

		this.resolveDepthBuffer = options.resolveDepthBuffer;
		this.resolveStencilBuffer = options.resolveStencilBuffer;

		this.depthTexture = options.depthTexture;

		this.samples = options.samples;

	}

	get texture() {

		return this.textures[ 0 ];

	}

	set texture( value ) {

		this.textures[ 0 ] = value;

	}

	setSize( width, height, depth = 1 ) {

		if ( this.width !== width || this.height !== height || this.depth !== depth ) {

			this.width = width;
			this.height = height;
			this.depth = depth;

			for ( let i = 0, il = this.textures.length; i < il; i ++ ) {

				this.textures[ i ].image.width = width;
				this.textures[ i ].image.height = height;
				this.textures[ i ].image.depth = depth;

			}

			this.dispose();

		}

		this.viewport.set( 0, 0, width, height );
		this.scissor.set( 0, 0, width, height );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.width = source.width;
		this.height = source.height;
		this.depth = source.depth;

		this.scissor.copy( source.scissor );
		this.scissorTest = source.scissorTest;

		this.viewport.copy( source.viewport );

		this.textures.length = 0;

		for ( let i = 0, il = source.textures.length; i < il; i ++ ) {

			this.textures[ i ] = source.textures[ i ].clone();
			this.textures[ i ].isRenderTargetTexture = true;

		}

		// ensure image object is not shared, see #20328

		const image = Object.assign( {}, source.texture.image );
		this.texture.source = new Source( image );

		this.depthBuffer = source.depthBuffer;
		this.stencilBuffer = source.stencilBuffer;

		this.resolveDepthBuffer = source.resolveDepthBuffer;
		this.resolveStencilBuffer = source.resolveStencilBuffer;

		if ( source.depthTexture !== null ) this.depthTexture = source.depthTexture.clone();

		this.samples = source.samples;

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

}

class WebGLRenderTarget extends RenderTarget {

	constructor( width = 1, height = 1, options = {} ) {

		super( width, height, options );

		this.isWebGLRenderTarget = true;

	}

}

class DataArrayTexture extends Texture {

	constructor( data = null, width = 1, height = 1, depth = 1 ) {

		super( null );

		this.isDataArrayTexture = true;

		this.image = { data, width, height, depth };

		this.magFilter = NearestFilter;
		this.minFilter = NearestFilter;

		this.wrapR = ClampToEdgeWrapping;

		this.generateMipmaps = false;
		this.flipY = false;
		this.unpackAlignment = 1;

		this.layerUpdates = new Set();

	}

	addLayerUpdate( layerIndex ) {

		this.layerUpdates.add( layerIndex );

	}

	clearLayerUpdates() {

		this.layerUpdates.clear();

	}

}

class WebGLArrayRenderTarget extends WebGLRenderTarget {

	constructor( width = 1, height = 1, depth = 1, options = {} ) {

		super( width, height, options );

		this.isWebGLArrayRenderTarget = true;

		this.depth = depth;

		this.texture = new DataArrayTexture( null, width, height, depth );

		this.texture.isRenderTargetTexture = true;

	}

}

class Data3DTexture extends Texture {

	constructor( data = null, width = 1, height = 1, depth = 1 ) {

		// We're going to add .setXXX() methods for setting properties later.
		// Users can still set in DataTexture3D directly.
		//
		//	const texture = new THREE.DataTexture3D( data, width, height, depth );
		// 	texture.anisotropy = 16;
		//
		// See #14839

		super( null );

		this.isData3DTexture = true;

		this.image = { data, width, height, depth };

		this.magFilter = NearestFilter;
		this.minFilter = NearestFilter;

		this.wrapR = ClampToEdgeWrapping;

		this.generateMipmaps = false;
		this.flipY = false;
		this.unpackAlignment = 1;

	}

}

class WebGL3DRenderTarget extends WebGLRenderTarget {

	constructor( width = 1, height = 1, depth = 1, options = {} ) {

		super( width, height, options );

		this.isWebGL3DRenderTarget = true;

		this.depth = depth;

		this.texture = new Data3DTexture( null, width, height, depth );

		this.texture.isRenderTargetTexture = true;

	}

}

class Quaternion {

	constructor( x = 0, y = 0, z = 0, w = 1 ) {

		this.isQuaternion = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

	}

	static slerpFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1, t ) {

		// fuzz-free, array-based Quaternion SLERP operation

		let x0 = src0[ srcOffset0 + 0 ],
			y0 = src0[ srcOffset0 + 1 ],
			z0 = src0[ srcOffset0 + 2 ],
			w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 + 0 ],
			y1 = src1[ srcOffset1 + 1 ],
			z1 = src1[ srcOffset1 + 2 ],
			w1 = src1[ srcOffset1 + 3 ];

		if ( t === 0 ) {

			dst[ dstOffset + 0 ] = x0;
			dst[ dstOffset + 1 ] = y0;
			dst[ dstOffset + 2 ] = z0;
			dst[ dstOffset + 3 ] = w0;
			return;

		}

		if ( t === 1 ) {

			dst[ dstOffset + 0 ] = x1;
			dst[ dstOffset + 1 ] = y1;
			dst[ dstOffset + 2 ] = z1;
			dst[ dstOffset + 3 ] = w1;
			return;

		}

		if ( w0 !== w1 || x0 !== x1 || y0 !== y1 || z0 !== z1 ) {

			let s = 1 - t;
			const cos = x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1,
				dir = ( cos >= 0 ? 1 : - 1 ),
				sqrSin = 1 - cos * cos;

			// Skip the Slerp for tiny steps to avoid numeric problems:
			if ( sqrSin > Number.EPSILON ) {

				const sin = Math.sqrt( sqrSin ),
					len = Math.atan2( sin, cos * dir );

				s = Math.sin( s * len ) / sin;
				t = Math.sin( t * len ) / sin;

			}

			const tDir = t * dir;

			x0 = x0 * s + x1 * tDir;
			y0 = y0 * s + y1 * tDir;
			z0 = z0 * s + z1 * tDir;
			w0 = w0 * s + w1 * tDir;

			// Normalize in case we just did a lerp:
			if ( s === 1 - t ) {

				const f = 1 / Math.sqrt( x0 * x0 + y0 * y0 + z0 * z0 + w0 * w0 );

				x0 *= f;
				y0 *= f;
				z0 *= f;
				w0 *= f;

			}

		}

		dst[ dstOffset ] = x0;
		dst[ dstOffset + 1 ] = y0;
		dst[ dstOffset + 2 ] = z0;
		dst[ dstOffset + 3 ] = w0;

	}

	static multiplyQuaternionsFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1 ) {

		const x0 = src0[ srcOffset0 ];
		const y0 = src0[ srcOffset0 + 1 ];
		const z0 = src0[ srcOffset0 + 2 ];
		const w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 ];
		const y1 = src1[ srcOffset1 + 1 ];
		const z1 = src1[ srcOffset1 + 2 ];
		const w1 = src1[ srcOffset1 + 3 ];

		dst[ dstOffset ] = x0 * w1 + w0 * x1 + y0 * z1 - z0 * y1;
		dst[ dstOffset + 1 ] = y0 * w1 + w0 * y1 + z0 * x1 - x0 * z1;
		dst[ dstOffset + 2 ] = z0 * w1 + w0 * z1 + x0 * y1 - y0 * x1;
		dst[ dstOffset + 3 ] = w0 * w1 - x0 * x1 - y0 * y1 - z0 * z1;

		return dst;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get w() {

		return this._w;

	}

	set w( value ) {

		this._w = value;
		this._onChangeCallback();

	}

	set( x, y, z, w ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._w );

	}

	copy( quaternion ) {

		this._x = quaternion.x;
		this._y = quaternion.y;
		this._z = quaternion.z;
		this._w = quaternion.w;

		this._onChangeCallback();

		return this;

	}

	setFromEuler( euler, update = true ) {

		const x = euler._x, y = euler._y, z = euler._z, order = euler._order;

		// http://www.mathworks.com/matlabcentral/fileexchange/
		// 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
		//	content/SpinCalc.m

		const cos = Math.cos;
		const sin = Math.sin;

		const c1 = cos( x / 2 );
		const c2 = cos( y / 2 );
		const c3 = cos( z / 2 );

		const s1 = sin( x / 2 );
		const s2 = sin( y / 2 );
		const s3 = sin( z / 2 );

		switch ( order ) {

			case 'XYZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'YXZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'ZXY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'ZYX':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'YZX':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'XZY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			default:
				console.warn( 'THREE.Quaternion: .setFromEuler() encountered an unknown order: ' + order );

		}

		if ( update === true ) this._onChangeCallback();

		return this;

	}

	setFromAxisAngle( axis, angle ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm

		// assumes axis is normalized

		const halfAngle = angle / 2, s = Math.sin( halfAngle );

		this._x = axis.x * s;
		this._y = axis.y * s;
		this._z = axis.z * s;
		this._w = Math.cos( halfAngle );

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements,

			m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ],
			m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ],
			m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ],

			trace = m11 + m22 + m33;

		if ( trace > 0 ) {

			const s = 0.5 / Math.sqrt( trace + 1.0 );

			this._w = 0.25 / s;
			this._x = ( m32 - m23 ) * s;
			this._y = ( m13 - m31 ) * s;
			this._z = ( m21 - m12 ) * s;

		} else if ( m11 > m22 && m11 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m11 - m22 - m33 );

			this._w = ( m32 - m23 ) / s;
			this._x = 0.25 * s;
			this._y = ( m12 + m21 ) / s;
			this._z = ( m13 + m31 ) / s;

		} else if ( m22 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m22 - m11 - m33 );

			this._w = ( m13 - m31 ) / s;
			this._x = ( m12 + m21 ) / s;
			this._y = 0.25 * s;
			this._z = ( m23 + m32 ) / s;

		} else {

			const s = 2.0 * Math.sqrt( 1.0 + m33 - m11 - m22 );

			this._w = ( m21 - m12 ) / s;
			this._x = ( m13 + m31 ) / s;
			this._y = ( m23 + m32 ) / s;
			this._z = 0.25 * s;

		}

		this._onChangeCallback();

		return this;

	}

	setFromUnitVectors( vFrom, vTo ) {

		// assumes direction vectors vFrom and vTo are normalized

		let r = vFrom.dot( vTo ) + 1;

		if ( r < Number.EPSILON ) {

			// vFrom and vTo point in opposite directions

			r = 0;

			if ( Math.abs( vFrom.x ) > Math.abs( vFrom.z ) ) {

				this._x = - vFrom.y;
				this._y = vFrom.x;
				this._z = 0;
				this._w = r;

			} else {

				this._x = 0;
				this._y = - vFrom.z;
				this._z = vFrom.y;
				this._w = r;

			}

		} else {

			// crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3

			this._x = vFrom.y * vTo.z - vFrom.z * vTo.y;
			this._y = vFrom.z * vTo.x - vFrom.x * vTo.z;
			this._z = vFrom.x * vTo.y - vFrom.y * vTo.x;
			this._w = r;

		}

		return this.normalize();

	}

	angleTo( q ) {

		return 2 * Math.acos( Math.abs( clamp( this.dot( q ), - 1, 1 ) ) );

	}

	rotateTowards( q, step ) {

		const angle = this.angleTo( q );

		if ( angle === 0 ) return this;

		const t = Math.min( 1, step / angle );

		this.slerp( q, t );

		return this;

	}

	identity() {

		return this.set( 0, 0, 0, 1 );

	}

	invert() {

		// quaternion is assumed to have unit length

		return this.conjugate();

	}

	conjugate() {

		this._x *= - 1;
		this._y *= - 1;
		this._z *= - 1;

		this._onChangeCallback();

		return this;

	}

	dot( v ) {

		return this._x * v._x + this._y * v._y + this._z * v._z + this._w * v._w;

	}

	lengthSq() {

		return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;

	}

	length() {

		return Math.sqrt( this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w );

	}

	normalize() {

		let l = this.length();

		if ( l === 0 ) {

			this._x = 0;
			this._y = 0;
			this._z = 0;
			this._w = 1;

		} else {

			l = 1 / l;

			this._x = this._x * l;
			this._y = this._y * l;
			this._z = this._z * l;
			this._w = this._w * l;

		}

		this._onChangeCallback();

		return this;

	}

	multiply( q ) {

		return this.multiplyQuaternions( this, q );

	}

	premultiply( q ) {

		return this.multiplyQuaternions( q, this );

	}

	multiplyQuaternions( a, b ) {

		// from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm

		const qax = a._x, qay = a._y, qaz = a._z, qaw = a._w;
		const qbx = b._x, qby = b._y, qbz = b._z, qbw = b._w;

		this._x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby;
		this._y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz;
		this._z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx;
		this._w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz;

		this._onChangeCallback();

		return this;

	}

	slerp( qb, t ) {

		if ( t === 0 ) return this;
		if ( t === 1 ) return this.copy( qb );

		const x = this._x, y = this._y, z = this._z, w = this._w;

		// http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/

		let cosHalfTheta = w * qb._w + x * qb._x + y * qb._y + z * qb._z;

		if ( cosHalfTheta < 0 ) {

			this._w = - qb._w;
			this._x = - qb._x;
			this._y = - qb._y;
			this._z = - qb._z;

			cosHalfTheta = - cosHalfTheta;

		} else {

			this.copy( qb );

		}

		if ( cosHalfTheta >= 1.0 ) {

			this._w = w;
			this._x = x;
			this._y = y;
			this._z = z;

			return this;

		}

		const sqrSinHalfTheta = 1.0 - cosHalfTheta * cosHalfTheta;

		if ( sqrSinHalfTheta <= Number.EPSILON ) {

			const s = 1 - t;
			this._w = s * w + t * this._w;
			this._x = s * x + t * this._x;
			this._y = s * y + t * this._y;
			this._z = s * z + t * this._z;

			this.normalize(); // normalize calls _onChangeCallback()

			return this;

		}

		const sinHalfTheta = Math.sqrt( sqrSinHalfTheta );
		const halfTheta = Math.atan2( sinHalfTheta, cosHalfTheta );
		const ratioA = Math.sin( ( 1 - t ) * halfTheta ) / sinHalfTheta,
			ratioB = Math.sin( t * halfTheta ) / sinHalfTheta;

		this._w = ( w * ratioA + this._w * ratioB );
		this._x = ( x * ratioA + this._x * ratioB );
		this._y = ( y * ratioA + this._y * ratioB );
		this._z = ( z * ratioA + this._z * ratioB );

		this._onChangeCallback();

		return this;

	}

	slerpQuaternions( qa, qb, t ) {

		return this.copy( qa ).slerp( qb, t );

	}

	random() {

		// sets this quaternion to a uniform random unit quaternnion

		// Ken Shoemake
		// Uniform random rotations
		// D. Kirk, editor, Graphics Gems III, pages 124-132. Academic Press, New York, 1992.

		const theta1 = 2 * Math.PI * Math.random();
		const theta2 = 2 * Math.PI * Math.random();

		const x0 = Math.random();
		const r1 = Math.sqrt( 1 - x0 );
		const r2 = Math.sqrt( x0 );

		return this.set(
			r1 * Math.sin( theta1 ),
			r1 * Math.cos( theta1 ),
			r2 * Math.sin( theta2 ),
			r2 * Math.cos( theta2 ),
		);

	}

	equals( quaternion ) {

		return ( quaternion._x === this._x ) && ( quaternion._y === this._y ) && ( quaternion._z === this._z ) && ( quaternion._w === this._w );

	}

	fromArray( array, offset = 0 ) {

		this._x = array[ offset ];
		this._y = array[ offset + 1 ];
		this._z = array[ offset + 2 ];
		this._w = array[ offset + 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._w;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this._x = attribute.getX( index );
		this._y = attribute.getY( index );
		this._z = attribute.getZ( index );
		this._w = attribute.getW( index );

		this._onChangeCallback();

		return this;

	}

	toJSON() {

		return this.toArray();

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._w;

	}

}

class Vector3 {

	constructor( x = 0, y = 0, z = 0 ) {

		Vector3.prototype.isVector3 = true;

		this.x = x;
		this.y = y;
		this.z = z;

	}

	set( x, y, z ) {

		if ( z === undefined ) z = this.z; // sprite.scale.set(x,y)

		this.x = x;
		this.y = y;
		this.z = z;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;
		this.z = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setZ( z ) {

		this.z = z;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			case 2: this.z = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			case 2: return this.z;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y, this.z );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;
		this.z = v.z;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;
		this.z += v.z;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;
		this.z += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;
		this.z = a.z + b.z;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;
		this.z += v.z * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;
		this.z -= v.z;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;
		this.z -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;
		this.z = a.z - b.z;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;
		this.z *= v.z;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;
		this.z *= scalar;

		return this;

	}

	multiplyVectors( a, b ) {

		this.x = a.x * b.x;
		this.y = a.y * b.y;
		this.z = a.z * b.z;

		return this;

	}

	applyEuler( euler ) {

		return this.applyQuaternion( _quaternion$4.setFromEuler( euler ) );

	}

	applyAxisAngle( axis, angle ) {

		return this.applyQuaternion( _quaternion$4.setFromAxisAngle( axis, angle ) );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ] * z;
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ] * z;
		this.z = e[ 2 ] * x + e[ 5 ] * y + e[ 8 ] * z;

		return this;

	}

	applyNormalMatrix( m ) {

		return this.applyMatrix3( m ).normalize();

	}

	applyMatrix4( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		const w = 1 / ( e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] );

		this.x = ( e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] ) * w;
		this.y = ( e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] ) * w;
		this.z = ( e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] ) * w;

		return this;

	}

	applyQuaternion( q ) {

		// quaternion q is assumed to have unit length

		const vx = this.x, vy = this.y, vz = this.z;
		const qx = q.x, qy = q.y, qz = q.z, qw = q.w;

		// t = 2 * cross( q.xyz, v );
		const tx = 2 * ( qy * vz - qz * vy );
		const ty = 2 * ( qz * vx - qx * vz );
		const tz = 2 * ( qx * vy - qy * vx );

		// v + q.w * t + cross( q.xyz, t );
		this.x = vx + qw * tx + qy * tz - qz * ty;
		this.y = vy + qw * ty + qz * tx - qx * tz;
		this.z = vz + qw * tz + qx * ty - qy * tx;

		return this;

	}

	project( camera ) {

		return this.applyMatrix4( camera.matrixWorldInverse ).applyMatrix4( camera.projectionMatrix );

	}

	unproject( camera ) {

		return this.applyMatrix4( camera.projectionMatrixInverse ).applyMatrix4( camera.matrixWorld );

	}

	transformDirection( m ) {

		// input: THREE.Matrix4 affine matrix
		// vector interpreted as a direction

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z;
		this.y = e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z;
		this.z = e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z;

		return this.normalize();

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;
		this.z /= v.z;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );
		this.z = Math.min( this.z, v.z );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );
		this.z = Math.max( this.z, v.z );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );
		this.z = Math.max( min.z, Math.min( max.z, this.z ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );
		this.z = Math.max( minVal, Math.min( maxVal, this.z ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );
		this.z = Math.floor( this.z );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );
		this.z = Math.ceil( this.z );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );
		this.z = Math.round( this.z );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );
		this.z = Math.trunc( this.z );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;
		this.z = - this.z;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y + this.z * v.z;

	}

	// TODO lengthSquared?

	lengthSq() {

		return this.x * this.x + this.y * this.y + this.z * this.z;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y + this.z * this.z );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y ) + Math.abs( this.z );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;
		this.z += ( v.z - this.z ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;
		this.z = v1.z + ( v2.z - v1.z ) * alpha;

		return this;

	}

	cross( v ) {

		return this.crossVectors( this, v );

	}

	crossVectors( a, b ) {

		const ax = a.x, ay = a.y, az = a.z;
		const bx = b.x, by = b.y, bz = b.z;

		this.x = ay * bz - az * by;
		this.y = az * bx - ax * bz;
		this.z = ax * by - ay * bx;

		return this;

	}

	projectOnVector( v ) {

		const denominator = v.lengthSq();

		if ( denominator === 0 ) return this.set( 0, 0, 0 );

		const scalar = v.dot( this ) / denominator;

		return this.copy( v ).multiplyScalar( scalar );

	}

	projectOnPlane( planeNormal ) {

		_vector$c.copy( this ).projectOnVector( planeNormal );

		return this.sub( _vector$c );

	}

	reflect( normal ) {

		// reflect incident vector off plane orthogonal to normal
		// normal is assumed to have unit length

		return this.sub( _vector$c.copy( normal ).multiplyScalar( 2 * this.dot( normal ) ) );

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;

		return dx * dx + dy * dy + dz * dz;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y ) + Math.abs( this.z - v.z );

	}

	setFromSpherical( s ) {

		return this.setFromSphericalCoords( s.radius, s.phi, s.theta );

	}

	setFromSphericalCoords( radius, phi, theta ) {

		const sinPhiRadius = Math.sin( phi ) * radius;

		this.x = sinPhiRadius * Math.sin( theta );
		this.y = Math.cos( phi ) * radius;
		this.z = sinPhiRadius * Math.cos( theta );

		return this;

	}

	setFromCylindrical( c ) {

		return this.setFromCylindricalCoords( c.radius, c.theta, c.y );

	}

	setFromCylindricalCoords( radius, theta, y ) {

		this.x = radius * Math.sin( theta );
		this.y = y;
		this.z = radius * Math.cos( theta );

		return this;

	}

	setFromMatrixPosition( m ) {

		const e = m.elements;

		this.x = e[ 12 ];
		this.y = e[ 13 ];
		this.z = e[ 14 ];

		return this;

	}

	setFromMatrixScale( m ) {

		const sx = this.setFromMatrixColumn( m, 0 ).length();
		const sy = this.setFromMatrixColumn( m, 1 ).length();
		const sz = this.setFromMatrixColumn( m, 2 ).length();

		this.x = sx;
		this.y = sy;
		this.z = sz;

		return this;

	}

	setFromMatrixColumn( m, index ) {

		return this.fromArray( m.elements, index * 4 );

	}

	setFromMatrix3Column( m, index ) {

		return this.fromArray( m.elements, index * 3 );

	}

	setFromEuler( e ) {

		this.x = e._x;
		this.y = e._y;
		this.z = e._z;

		return this;

	}

	setFromColor( c ) {

		this.x = c.r;
		this.y = c.g;
		this.z = c.b;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) && ( v.z === this.z ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];
		this.z = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;
		array[ offset + 2 ] = this.z;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );
		this.z = attribute.getZ( index );

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();
		this.z = Math.random();

		return this;

	}

	randomDirection() {

		// https://mathworld.wolfram.com/SpherePointPicking.html

		const theta = Math.random() * Math.PI * 2;
		const u = Math.random() * 2 - 1;
		const c = Math.sqrt( 1 - u * u );

		this.x = c * Math.cos( theta );
		this.y = u;
		this.z = c * Math.sin( theta );

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;
		yield this.z;

	}

}

const _vector$c = /*@__PURE__*/ new Vector3();
const _quaternion$4 = /*@__PURE__*/ new Quaternion();

class Box3 {

	constructor( min = new Vector3( + Infinity, + Infinity, + Infinity ), max = new Vector3( - Infinity, - Infinity, - Infinity ) ) {

		this.isBox3 = true;

		this.min = min;
		this.max = max;

	}

	set( min, max ) {

		this.min.copy( min );
		this.max.copy( max );

		return this;

	}

	setFromArray( array ) {

		this.makeEmpty();

		for ( let i = 0, il = array.length; i < il; i += 3 ) {

			this.expandByPoint( _vector$b.fromArray( array, i ) );

		}

		return this;

	}

	setFromBufferAttribute( attribute ) {

		this.makeEmpty();

		for ( let i = 0, il = attribute.count; i < il; i ++ ) {

			this.expandByPoint( _vector$b.fromBufferAttribute( attribute, i ) );

		}

		return this;

	}

	setFromPoints( points ) {

		this.makeEmpty();

		for ( let i = 0, il = points.length; i < il; i ++ ) {

			this.expandByPoint( points[ i ] );

		}

		return this;

	}

	setFromCenterAndSize( center, size ) {

		const halfSize = _vector$b.copy( size ).multiplyScalar( 0.5 );

		this.min.copy( center ).sub( halfSize );
		this.max.copy( center ).add( halfSize );

		return this;

	}

	setFromObject( object, precise = false ) {

		this.makeEmpty();

		return this.expandByObject( object, precise );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( box ) {

		this.min.copy( box.min );
		this.max.copy( box.max );

		return this;

	}

	makeEmpty() {

		this.min.x = this.min.y = this.min.z = + Infinity;
		this.max.x = this.max.y = this.max.z = - Infinity;

		return this;

	}

	isEmpty() {

		// this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

		return ( this.max.x < this.min.x ) || ( this.max.y < this.min.y ) || ( this.max.z < this.min.z );

	}

	getCenter( target ) {

		return this.isEmpty() ? target.set( 0, 0, 0 ) : target.addVectors( this.min, this.max ).multiplyScalar( 0.5 );

	}

	getSize( target ) {

		return this.isEmpty() ? target.set( 0, 0, 0 ) : target.subVectors( this.max, this.min );

	}

	expandByPoint( point ) {

		this.min.min( point );
		this.max.max( point );

		return this;

	}

	expandByVector( vector ) {

		this.min.sub( vector );
		this.max.add( vector );

		return this;

	}

	expandByScalar( scalar ) {

		this.min.addScalar( - scalar );
		this.max.addScalar( scalar );

		return this;

	}

	expandByObject( object, precise = false ) {

		// Computes the world-axis-aligned bounding box of an object (including its children),
		// accounting for both the object's, and children's, world transforms

		object.updateWorldMatrix( false, false );

		const geometry = object.geometry;

		if ( geometry !== undefined ) {

			const positionAttribute = geometry.getAttribute( 'position' );

			// precise AABB computation based on vertex data requires at least a position attribute.
			// instancing isn't supported so far and uses the normal (conservative) code path.

			if ( precise === true && positionAttribute !== undefined && object.isInstancedMesh !== true ) {

				for ( let i = 0, l = positionAttribute.count; i < l; i ++ ) {

					if ( object.isMesh === true ) {

						object.getVertexPosition( i, _vector$b );

					} else {

						_vector$b.fromBufferAttribute( positionAttribute, i );

					}

					_vector$b.applyMatrix4( object.matrixWorld );
					this.expandByPoint( _vector$b );

				}

			} else {

				if ( object.boundingBox !== undefined ) {

					// object-level bounding box

					if ( object.boundingBox === null ) {

						object.computeBoundingBox();

					}

					_box$4.copy( object.boundingBox );


				} else {

					// geometry-level bounding box

					if ( geometry.boundingBox === null ) {

						geometry.computeBoundingBox();

					}

					_box$4.copy( geometry.boundingBox );

				}

				_box$4.applyMatrix4( object.matrixWorld );

				this.union( _box$4 );

			}

		}

		const children = object.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			this.expandByObject( children[ i ], precise );

		}

		return this;

	}

	containsPoint( point ) {

		return point.x >= this.min.x && point.x <= this.max.x &&
			point.y >= this.min.y && point.y <= this.max.y &&
			point.z >= this.min.z && point.z <= this.max.z;

	}

	containsBox( box ) {

		return this.min.x <= box.min.x && box.max.x <= this.max.x &&
			this.min.y <= box.min.y && box.max.y <= this.max.y &&
			this.min.z <= box.min.z && box.max.z <= this.max.z;

	}

	getParameter( point, target ) {

		// This can potentially have a divide by zero if the box
		// has a size dimension of 0.

		return target.set(
			( point.x - this.min.x ) / ( this.max.x - this.min.x ),
			( point.y - this.min.y ) / ( this.max.y - this.min.y ),
			( point.z - this.min.z ) / ( this.max.z - this.min.z )
		);

	}

	intersectsBox( box ) {

		// using 6 splitting planes to rule out intersections.
		return box.max.x >= this.min.x && box.min.x <= this.max.x &&
			box.max.y >= this.min.y && box.min.y <= this.max.y &&
			box.max.z >= this.min.z && box.min.z <= this.max.z;

	}

	intersectsSphere( sphere ) {

		// Find the point on the AABB closest to the sphere center.
		this.clampPoint( sphere.center, _vector$b );

		// If that point is inside the sphere, the AABB and sphere intersect.
		return _vector$b.distanceToSquared( sphere.center ) <= ( sphere.radius * sphere.radius );

	}

	intersectsPlane( plane ) {

		// We compute the minimum and maximum dot product values. If those values
		// are on the same side (back or front) of the plane, then there is no intersection.

		let min, max;

		if ( plane.normal.x > 0 ) {

			min = plane.normal.x * this.min.x;
			max = plane.normal.x * this.max.x;

		} else {

			min = plane.normal.x * this.max.x;
			max = plane.normal.x * this.min.x;

		}

		if ( plane.normal.y > 0 ) {

			min += plane.normal.y * this.min.y;
			max += plane.normal.y * this.max.y;

		} else {

			min += plane.normal.y * this.max.y;
			max += plane.normal.y * this.min.y;

		}

		if ( plane.normal.z > 0 ) {

			min += plane.normal.z * this.min.z;
			max += plane.normal.z * this.max.z;

		} else {

			min += plane.normal.z * this.max.z;
			max += plane.normal.z * this.min.z;

		}

		return ( min <= - plane.constant && max >= - plane.constant );

	}

	intersectsTriangle( triangle ) {

		if ( this.isEmpty() ) {

			return false;

		}

		// compute box center and extents
		this.getCenter( _center );
		_extents.subVectors( this.max, _center );

		// translate triangle to aabb origin
		_v0$3.subVectors( triangle.a, _center );
		_v1$7.subVectors( triangle.b, _center );
		_v2$4.subVectors( triangle.c, _center );

		// compute edge vectors for triangle
		_f0.subVectors( _v1$7, _v0$3 );
		_f1.subVectors( _v2$4, _v1$7 );
		_f2.subVectors( _v0$3, _v2$4 );

		// test against axes that are given by cross product combinations of the edges of the triangle and the edges of the aabb
		// make an axis testing of each of the 3 sides of the aabb against each of the 3 sides of the triangle = 9 axis of separation
		// axis_ij = u_i x f_j (u0, u1, u2 = face normals of aabb = x,y,z axes vectors since aabb is axis aligned)
		let axes = [
			0, - _f0.z, _f0.y, 0, - _f1.z, _f1.y, 0, - _f2.z, _f2.y,
			_f0.z, 0, - _f0.x, _f1.z, 0, - _f1.x, _f2.z, 0, - _f2.x,
			- _f0.y, _f0.x, 0, - _f1.y, _f1.x, 0, - _f2.y, _f2.x, 0
		];
		if ( ! satForAxes( axes, _v0$3, _v1$7, _v2$4, _extents ) ) {

			return false;

		}

		// test 3 face normals from the aabb
		axes = [ 1, 0, 0, 0, 1, 0, 0, 0, 1 ];
		if ( ! satForAxes( axes, _v0$3, _v1$7, _v2$4, _extents ) ) {

			return false;

		}

		// finally testing the face normal of the triangle
		// use already existing triangle edge vectors here
		_triangleNormal.crossVectors( _f0, _f1 );
		axes = [ _triangleNormal.x, _triangleNormal.y, _triangleNormal.z ];

		return satForAxes( axes, _v0$3, _v1$7, _v2$4, _extents );

	}

	clampPoint( point, target ) {

		return target.copy( point ).clamp( this.min, this.max );

	}

	distanceToPoint( point ) {

		return this.clampPoint( point, _vector$b ).distanceTo( point );

	}

	getBoundingSphere( target ) {

		if ( this.isEmpty() ) {

			target.makeEmpty();

		} else {

			this.getCenter( target.center );

			target.radius = this.getSize( _vector$b ).length() * 0.5;

		}

		return target;

	}

	intersect( box ) {

		this.min.max( box.min );
		this.max.min( box.max );

		// ensure that if there is no overlap, the result is fully empty, not slightly empty with non-inf/+inf values that will cause subsequence intersects to erroneously return valid values.
		if ( this.isEmpty() ) this.makeEmpty();

		return this;

	}

	union( box ) {

		this.min.min( box.min );
		this.max.max( box.max );

		return this;

	}

	applyMatrix4( matrix ) {

		// transform of empty box is an empty box.
		if ( this.isEmpty() ) return this;

		// NOTE: I am using a binary pattern to specify all 2^3 combinations below
		_points[ 0 ].set( this.min.x, this.min.y, this.min.z ).applyMatrix4( matrix ); // 000
		_points[ 1 ].set( this.min.x, this.min.y, this.max.z ).applyMatrix4( matrix ); // 001
		_points[ 2 ].set( this.min.x, this.max.y, this.min.z ).applyMatrix4( matrix ); // 010
		_points[ 3 ].set( this.min.x, this.max.y, this.max.z ).applyMatrix4( matrix ); // 011
		_points[ 4 ].set( this.max.x, this.min.y, this.min.z ).applyMatrix4( matrix ); // 100
		_points[ 5 ].set( this.max.x, this.min.y, this.max.z ).applyMatrix4( matrix ); // 101
		_points[ 6 ].set( this.max.x, this.max.y, this.min.z ).applyMatrix4( matrix ); // 110
		_points[ 7 ].set( this.max.x, this.max.y, this.max.z ).applyMatrix4( matrix ); // 111

		this.setFromPoints( _points );

		return this;

	}

	translate( offset ) {

		this.min.add( offset );
		this.max.add( offset );

		return this;

	}

	equals( box ) {

		return box.min.equals( this.min ) && box.max.equals( this.max );

	}

}

const _points = [
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3()
];

const _vector$b = /*@__PURE__*/ new Vector3();

const _box$4 = /*@__PURE__*/ new Box3();

// triangle centered vertices

const _v0$3 = /*@__PURE__*/ new Vector3();
const _v1$7 = /*@__PURE__*/ new Vector3();
const _v2$4 = /*@__PURE__*/ new Vector3();

// triangle edge vectors

const _f0 = /*@__PURE__*/ new Vector3();
const _f1 = /*@__PURE__*/ new Vector3();
const _f2 = /*@__PURE__*/ new Vector3();

const _center = /*@__PURE__*/ new Vector3();
const _extents = /*@__PURE__*/ new Vector3();
const _triangleNormal = /*@__PURE__*/ new Vector3();
const _testAxis = /*@__PURE__*/ new Vector3();

function satForAxes( axes, v0, v1, v2, extents ) {

	for ( let i = 0, j = axes.length - 3; i <= j; i += 3 ) {

		_testAxis.fromArray( axes, i );
		// project the aabb onto the separating axis
		const r = extents.x * Math.abs( _testAxis.x ) + extents.y * Math.abs( _testAxis.y ) + extents.z * Math.abs( _testAxis.z );
		// project all 3 vertices of the triangle onto the separating axis
		const p0 = v0.dot( _testAxis );
		const p1 = v1.dot( _testAxis );
		const p2 = v2.dot( _testAxis );
		// actual test, basically see if either of the most extreme of the triangle points intersects r
		if ( Math.max( - Math.max( p0, p1, p2 ), Math.min( p0, p1, p2 ) ) > r ) {

			// points of the projected triangle are outside the projected half-length of the aabb
			// the axis is separating and we can exit
			return false;

		}

	}

	return true;

}

const _box$3 = /*@__PURE__*/ new Box3();
const _v1$6 = /*@__PURE__*/ new Vector3();
const _v2$3 = /*@__PURE__*/ new Vector3();

class Sphere {

	constructor( center = new Vector3(), radius = - 1 ) {

		this.isSphere = true;

		this.center = center;
		this.radius = radius;

	}

	set( center, radius ) {

		this.center.copy( center );
		this.radius = radius;

		return this;

	}

	setFromPoints( points, optionalCenter ) {

		const center = this.center;

		if ( optionalCenter !== undefined ) {

			center.copy( optionalCenter );

		} else {

			_box$3.setFromPoints( points ).getCenter( center );

		}

		let maxRadiusSq = 0;

		for ( let i = 0, il = points.length; i < il; i ++ ) {

			maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( points[ i ] ) );

		}

		this.radius = Math.sqrt( maxRadiusSq );

		return this;

	}

	copy( sphere ) {

		this.center.copy( sphere.center );
		this.radius = sphere.radius;

		return this;

	}

	isEmpty() {

		return ( this.radius < 0 );

	}

	makeEmpty() {

		this.center.set( 0, 0, 0 );
		this.radius = - 1;

		return this;

	}

	containsPoint( point ) {

		return ( point.distanceToSquared( this.center ) <= ( this.radius * this.radius ) );

	}

	distanceToPoint( point ) {

		return ( point.distanceTo( this.center ) - this.radius );

	}

	intersectsSphere( sphere ) {

		const radiusSum = this.radius + sphere.radius;

		return sphere.center.distanceToSquared( this.center ) <= ( radiusSum * radiusSum );

	}

	intersectsBox( box ) {

		return box.intersectsSphere( this );

	}

	intersectsPlane( plane ) {

		return Math.abs( plane.distanceToPoint( this.center ) ) <= this.radius;

	}

	clampPoint( point, target ) {

		const deltaLengthSq = this.center.distanceToSquared( point );

		target.copy( point );

		if ( deltaLengthSq > ( this.radius * this.radius ) ) {

			target.sub( this.center ).normalize();
			target.multiplyScalar( this.radius ).add( this.center );

		}

		return target;

	}

	getBoundingBox( target ) {

		if ( this.isEmpty() ) {

			// Empty sphere produces empty bounding box
			target.makeEmpty();
			return target;

		}

		target.set( this.center, this.center );
		target.expandByScalar( this.radius );

		return target;

	}

	applyMatrix4( matrix ) {

		this.center.applyMatrix4( matrix );
		this.radius = this.radius * matrix.getMaxScaleOnAxis();

		return this;

	}

	translate( offset ) {

		this.center.add( offset );

		return this;

	}

	expandByPoint( point ) {

		if ( this.isEmpty() ) {

			this.center.copy( point );

			this.radius = 0;

			return this;

		}

		_v1$6.subVectors( point, this.center );

		const lengthSq = _v1$6.lengthSq();

		if ( lengthSq > ( this.radius * this.radius ) ) {

			// calculate the minimal sphere

			const length = Math.sqrt( lengthSq );

			const delta = ( length - this.radius ) * 0.5;

			this.center.addScaledVector( _v1$6, delta / length );

			this.radius += delta;

		}

		return this;

	}

	union( sphere ) {

		if ( sphere.isEmpty() ) {

			return this;

		}

		if ( this.isEmpty() ) {

			this.copy( sphere );

			return this;

		}

		if ( this.center.equals( sphere.center ) === true ) {

			 this.radius = Math.max( this.radius, sphere.radius );

		} else {

			_v2$3.subVectors( sphere.center, this.center ).setLength( sphere.radius );

			this.expandByPoint( _v1$6.copy( sphere.center ).add( _v2$3 ) );

			this.expandByPoint( _v1$6.copy( sphere.center ).sub( _v2$3 ) );

		}

		return this;

	}

	equals( sphere ) {

		return sphere.center.equals( this.center ) && ( sphere.radius === this.radius );

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

const _vector$a = /*@__PURE__*/ new Vector3();
const _segCenter = /*@__PURE__*/ new Vector3();
const _segDir = /*@__PURE__*/ new Vector3();
const _diff = /*@__PURE__*/ new Vector3();

const _edge1 = /*@__PURE__*/ new Vector3();
const _edge2 = /*@__PURE__*/ new Vector3();
const _normal$1 = /*@__PURE__*/ new Vector3();

class Ray {

	constructor( origin = new Vector3(), direction = new Vector3( 0, 0, - 1 ) ) {

		this.origin = origin;
		this.direction = direction;

	}

	set( origin, direction ) {

		this.origin.copy( origin );
		this.direction.copy( direction );

		return this;

	}

	copy( ray ) {

		this.origin.copy( ray.origin );
		this.direction.copy( ray.direction );

		return this;

	}

	at( t, target ) {

		return target.copy( this.origin ).addScaledVector( this.direction, t );

	}

	lookAt( v ) {

		this.direction.copy( v ).sub( this.origin ).normalize();

		return this;

	}

	recast( t ) {

		this.origin.copy( this.at( t, _vector$a ) );

		return this;

	}

	closestPointToPoint( point, target ) {

		target.subVectors( point, this.origin );

		const directionDistance = target.dot( this.direction );

		if ( directionDistance < 0 ) {

			return target.copy( this.origin );

		}

		return target.copy( this.origin ).addScaledVector( this.direction, directionDistance );

	}

	distanceToPoint( point ) {

		return Math.sqrt( this.distanceSqToPoint( point ) );

	}

	distanceSqToPoint( point ) {

		const directionDistance = _vector$a.subVectors( point, this.origin ).dot( this.direction );

		// point behind the ray

		if ( directionDistance < 0 ) {

			return this.origin.distanceToSquared( point );

		}

		_vector$a.copy( this.origin ).addScaledVector( this.direction, directionDistance );

		return _vector$a.distanceToSquared( point );

	}

	distanceSqToSegment( v0, v1, optionalPointOnRay, optionalPointOnSegment ) {

		// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteDistRaySegment.h
		// It returns the min distance between the ray and the segment
		// defined by v0 and v1
		// It can also set two optional targets :
		// - The closest point on the ray
		// - The closest point on the segment

		_segCenter.copy( v0 ).add( v1 ).multiplyScalar( 0.5 );
		_segDir.copy( v1 ).sub( v0 ).normalize();
		_diff.copy( this.origin ).sub( _segCenter );

		const segExtent = v0.distanceTo( v1 ) * 0.5;
		const a01 = - this.direction.dot( _segDir );
		const b0 = _diff.dot( this.direction );
		const b1 = - _diff.dot( _segDir );
		const c = _diff.lengthSq();
		const det = Math.abs( 1 - a01 * a01 );
		let s0, s1, sqrDist, extDet;

		if ( det > 0 ) {

			// The ray and segment are not parallel.

			s0 = a01 * b1 - b0;
			s1 = a01 * b0 - b1;
			extDet = segExtent * det;

			if ( s0 >= 0 ) {

				if ( s1 >= - extDet ) {

					if ( s1 <= extDet ) {

						// region 0
						// Minimum at interior points of ray and segment.

						const invDet = 1 / det;
						s0 *= invDet;
						s1 *= invDet;
						sqrDist = s0 * ( s0 + a01 * s1 + 2 * b0 ) + s1 * ( a01 * s0 + s1 + 2 * b1 ) + c;

					} else {

						// region 1

						s1 = segExtent;
						s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
						sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

					}

				} else {

					// region 5

					s1 = - segExtent;
					s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				}

			} else {

				if ( s1 <= - extDet ) {

					// region 4

					s0 = Math.max( 0, - ( - a01 * segExtent + b0 ) );
					s1 = ( s0 > 0 ) ? - segExtent : Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				} else if ( s1 <= extDet ) {

					// region 3

					s0 = 0;
					s1 = Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = s1 * ( s1 + 2 * b1 ) + c;

				} else {

					// region 2

					s0 = Math.max( 0, - ( a01 * segExtent + b0 ) );
					s1 = ( s0 > 0 ) ? segExtent : Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				}

			}

		} else {

			// Ray and segment are parallel.

			s1 = ( a01 > 0 ) ? - segExtent : segExtent;
			s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
			sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

		}

		if ( optionalPointOnRay ) {

			optionalPointOnRay.copy( this.origin ).addScaledVector( this.direction, s0 );

		}

		if ( optionalPointOnSegment ) {

			optionalPointOnSegment.copy( _segCenter ).addScaledVector( _segDir, s1 );

		}

		return sqrDist;

	}

	intersectSphere( sphere, target ) {

		_vector$a.subVectors( sphere.center, this.origin );
		const tca = _vector$a.dot( this.direction );
		const d2 = _vector$a.dot( _vector$a ) - tca * tca;
		const radius2 = sphere.radius * sphere.radius;

		if ( d2 > radius2 ) return null;

		const thc = Math.sqrt( radius2 - d2 );

		// t0 = first intersect point - entrance on front of sphere
		const t0 = tca - thc;

		// t1 = second intersect point - exit point on back of sphere
		const t1 = tca + thc;

		// test to see if t1 is behind the ray - if so, return null
		if ( t1 < 0 ) return null;

		// test to see if t0 is behind the ray:
		// if it is, the ray is inside the sphere, so return the second exit point scaled by t1,
		// in order to always return an intersect point that is in front of the ray.
		if ( t0 < 0 ) return this.at( t1, target );

		// else t0 is in front of the ray, so return the first collision point scaled by t0
		return this.at( t0, target );

	}

	intersectsSphere( sphere ) {

		return this.distanceSqToPoint( sphere.center ) <= ( sphere.radius * sphere.radius );

	}

	distanceToPlane( plane ) {

		const denominator = plane.normal.dot( this.direction );

		if ( denominator === 0 ) {

			// line is coplanar, return origin
			if ( plane.distanceToPoint( this.origin ) === 0 ) {

				return 0;

			}

			// Null is preferable to undefined since undefined means.... it is undefined

			return null;

		}

		const t = - ( this.origin.dot( plane.normal ) + plane.constant ) / denominator;

		// Return if the ray never intersects the plane

		return t >= 0 ? t : null;

	}

	intersectPlane( plane, target ) {

		const t = this.distanceToPlane( plane );

		if ( t === null ) {

			return null;

		}

		return this.at( t, target );

	}

	intersectsPlane( plane ) {

		// check if the ray lies on the plane first

		const distToPoint = plane.distanceToPoint( this.origin );

		if ( distToPoint === 0 ) {

			return true;

		}

		const denominator = plane.normal.dot( this.direction );

		if ( denominator * distToPoint < 0 ) {

			return true;

		}

		// ray origin is behind the plane (and is pointing behind it)

		return false;

	}

	intersectBox( box, target ) {

		let tmin, tmax, tymin, tymax, tzmin, tzmax;

		const invdirx = 1 / this.direction.x,
			invdiry = 1 / this.direction.y,
			invdirz = 1 / this.direction.z;

		const origin = this.origin;

		if ( invdirx >= 0 ) {

			tmin = ( box.min.x - origin.x ) * invdirx;
			tmax = ( box.max.x - origin.x ) * invdirx;

		} else {

			tmin = ( box.max.x - origin.x ) * invdirx;
			tmax = ( box.min.x - origin.x ) * invdirx;

		}

		if ( invdiry >= 0 ) {

			tymin = ( box.min.y - origin.y ) * invdiry;
			tymax = ( box.max.y - origin.y ) * invdiry;

		} else {

			tymin = ( box.max.y - origin.y ) * invdiry;
			tymax = ( box.min.y - origin.y ) * invdiry;

		}

		if ( ( tmin > tymax ) || ( tymin > tmax ) ) return null;

		if ( tymin > tmin || isNaN( tmin ) ) tmin = tymin;

		if ( tymax < tmax || isNaN( tmax ) ) tmax = tymax;

		if ( invdirz >= 0 ) {

			tzmin = ( box.min.z - origin.z ) * invdirz;
			tzmax = ( box.max.z - origin.z ) * invdirz;

		} else {

			tzmin = ( box.max.z - origin.z ) * invdirz;
			tzmax = ( box.min.z - origin.z ) * invdirz;

		}

		if ( ( tmin > tzmax ) || ( tzmin > tmax ) ) return null;

		if ( tzmin > tmin || tmin !== tmin ) tmin = tzmin;

		if ( tzmax < tmax || tmax !== tmax ) tmax = tzmax;

		//return point closest to the ray (positive side)

		if ( tmax < 0 ) return null;

		return this.at( tmin >= 0 ? tmin : tmax, target );

	}

	intersectsBox( box ) {

		return this.intersectBox( box, _vector$a ) !== null;

	}

	intersectTriangle( a, b, c, backfaceCulling, target ) {

		// Compute the offset origin, edges, and normal.

		// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteIntrRay3Triangle3.h

		_edge1.subVectors( b, a );
		_edge2.subVectors( c, a );
		_normal$1.crossVectors( _edge1, _edge2 );

		// Solve Q + t*D = b1*E1 + b2*E2 (Q = kDiff, D = ray direction,
		// E1 = kEdge1, E2 = kEdge2, N = Cross(E1,E2)) by
		//   |Dot(D,N)|*b1 = sign(Dot(D,N))*Dot(D,Cross(Q,E2))
		//   |Dot(D,N)|*b2 = sign(Dot(D,N))*Dot(D,Cross(E1,Q))
		//   |Dot(D,N)|*t = -sign(Dot(D,N))*Dot(Q,N)
		let DdN = this.direction.dot( _normal$1 );
		let sign;

		if ( DdN > 0 ) {

			if ( backfaceCulling ) return null;
			sign = 1;

		} else if ( DdN < 0 ) {

			sign = - 1;
			DdN = - DdN;

		} else {

			return null;

		}

		_diff.subVectors( this.origin, a );
		const DdQxE2 = sign * this.direction.dot( _edge2.crossVectors( _diff, _edge2 ) );

		// b1 < 0, no intersection
		if ( DdQxE2 < 0 ) {

			return null;

		}

		const DdE1xQ = sign * this.direction.dot( _edge1.cross( _diff ) );

		// b2 < 0, no intersection
		if ( DdE1xQ < 0 ) {

			return null;

		}

		// b1+b2 > 1, no intersection
		if ( DdQxE2 + DdE1xQ > DdN ) {

			return null;

		}

		// Line intersects triangle, check if ray does.
		const QdN = - sign * _diff.dot( _normal$1 );

		// t < 0, no intersection
		if ( QdN < 0 ) {

			return null;

		}

		// Ray intersects triangle.
		return this.at( QdN / DdN, target );

	}

	applyMatrix4( matrix4 ) {

		this.origin.applyMatrix4( matrix4 );
		this.direction.transformDirection( matrix4 );

		return this;

	}

	equals( ray ) {

		return ray.origin.equals( this.origin ) && ray.direction.equals( this.direction );

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

class Matrix4 {

	constructor( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		Matrix4.prototype.isMatrix4 = true;

		this.elements = [

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 );

		}

	}

	set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 4 ] = n12; te[ 8 ] = n13; te[ 12 ] = n14;
		te[ 1 ] = n21; te[ 5 ] = n22; te[ 9 ] = n23; te[ 13 ] = n24;
		te[ 2 ] = n31; te[ 6 ] = n32; te[ 10 ] = n33; te[ 14 ] = n34;
		te[ 3 ] = n41; te[ 7 ] = n42; te[ 11 ] = n43; te[ 15 ] = n44;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	clone() {

		return new Matrix4().fromArray( this.elements );

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ]; te[ 3 ] = me[ 3 ];
		te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ]; te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ];
		te[ 8 ] = me[ 8 ]; te[ 9 ] = me[ 9 ]; te[ 10 ] = me[ 10 ]; te[ 11 ] = me[ 11 ];
		te[ 12 ] = me[ 12 ]; te[ 13 ] = me[ 13 ]; te[ 14 ] = me[ 14 ]; te[ 15 ] = me[ 15 ];

		return this;

	}

	copyPosition( m ) {

		const te = this.elements, me = m.elements;

		te[ 12 ] = me[ 12 ];
		te[ 13 ] = me[ 13 ];
		te[ 14 ] = me[ 14 ];

		return this;

	}

	setFromMatrix3( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 3 ], me[ 6 ], 0,
			me[ 1 ], me[ 4 ], me[ 7 ], 0,
			me[ 2 ], me[ 5 ], me[ 8 ], 0,
			0, 0, 0, 1

		);

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrixColumn( this, 0 );
		yAxis.setFromMatrixColumn( this, 1 );
		zAxis.setFromMatrixColumn( this, 2 );

		return this;

	}

	makeBasis( xAxis, yAxis, zAxis ) {

		this.set(
			xAxis.x, yAxis.x, zAxis.x, 0,
			xAxis.y, yAxis.y, zAxis.y, 0,
			xAxis.z, yAxis.z, zAxis.z, 0,
			0, 0, 0, 1
		);

		return this;

	}

	extractRotation( m ) {

		// this method does not support reflection matrices

		const te = this.elements;
		const me = m.elements;

		const scaleX = 1 / _v1$5.setFromMatrixColumn( m, 0 ).length();
		const scaleY = 1 / _v1$5.setFromMatrixColumn( m, 1 ).length();
		const scaleZ = 1 / _v1$5.setFromMatrixColumn( m, 2 ).length();

		te[ 0 ] = me[ 0 ] * scaleX;
		te[ 1 ] = me[ 1 ] * scaleX;
		te[ 2 ] = me[ 2 ] * scaleX;
		te[ 3 ] = 0;

		te[ 4 ] = me[ 4 ] * scaleY;
		te[ 5 ] = me[ 5 ] * scaleY;
		te[ 6 ] = me[ 6 ] * scaleY;
		te[ 7 ] = 0;

		te[ 8 ] = me[ 8 ] * scaleZ;
		te[ 9 ] = me[ 9 ] * scaleZ;
		te[ 10 ] = me[ 10 ] * scaleZ;
		te[ 11 ] = 0;

		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromEuler( euler ) {

		const te = this.elements;

		const x = euler.x, y = euler.y, z = euler.z;
		const a = Math.cos( x ), b = Math.sin( x );
		const c = Math.cos( y ), d = Math.sin( y );
		const e = Math.cos( z ), f = Math.sin( z );

		if ( euler.order === 'XYZ' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = - c * f;
			te[ 8 ] = d;

			te[ 1 ] = af + be * d;
			te[ 5 ] = ae - bf * d;
			te[ 9 ] = - b * c;

			te[ 2 ] = bf - ae * d;
			te[ 6 ] = be + af * d;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YXZ' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce + df * b;
			te[ 4 ] = de * b - cf;
			te[ 8 ] = a * d;

			te[ 1 ] = a * f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b;

			te[ 2 ] = cf * b - de;
			te[ 6 ] = df + ce * b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZXY' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce - df * b;
			te[ 4 ] = - a * f;
			te[ 8 ] = de + cf * b;

			te[ 1 ] = cf + de * b;
			te[ 5 ] = a * e;
			te[ 9 ] = df - ce * b;

			te[ 2 ] = - a * d;
			te[ 6 ] = b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZYX' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = be * d - af;
			te[ 8 ] = ae * d + bf;

			te[ 1 ] = c * f;
			te[ 5 ] = bf * d + ae;
			te[ 9 ] = af * d - be;

			te[ 2 ] = - d;
			te[ 6 ] = b * c;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YZX' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = bd - ac * f;
			te[ 8 ] = bc * f + ad;

			te[ 1 ] = f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b * e;

			te[ 2 ] = - d * e;
			te[ 6 ] = ad * f + bc;
			te[ 10 ] = ac - bd * f;

		} else if ( euler.order === 'XZY' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = - f;
			te[ 8 ] = d * e;

			te[ 1 ] = ac * f + bd;
			te[ 5 ] = a * e;
			te[ 9 ] = ad * f - bc;

			te[ 2 ] = bc * f - ad;
			te[ 6 ] = b * e;
			te[ 10 ] = bd * f + ac;

		}

		// bottom row
		te[ 3 ] = 0;
		te[ 7 ] = 0;
		te[ 11 ] = 0;

		// last column
		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromQuaternion( q ) {

		return this.compose( _zero, q, _one );

	}

	lookAt( eye, target, up ) {

		const te = this.elements;

		_z.subVectors( eye, target );

		if ( _z.lengthSq() === 0 ) {

			// eye and target are in the same position

			_z.z = 1;

		}

		_z.normalize();
		_x.crossVectors( up, _z );

		if ( _x.lengthSq() === 0 ) {

			// up and z are parallel

			if ( Math.abs( up.z ) === 1 ) {

				_z.x += 0.0001;

			} else {

				_z.z += 0.0001;

			}

			_z.normalize();
			_x.crossVectors( up, _z );

		}

		_x.normalize();
		_y.crossVectors( _z, _x );

		te[ 0 ] = _x.x; te[ 4 ] = _y.x; te[ 8 ] = _z.x;
		te[ 1 ] = _x.y; te[ 5 ] = _y.y; te[ 9 ] = _z.y;
		te[ 2 ] = _x.z; te[ 6 ] = _y.z; te[ 10 ] = _z.z;

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 4 ], a13 = ae[ 8 ], a14 = ae[ 12 ];
		const a21 = ae[ 1 ], a22 = ae[ 5 ], a23 = ae[ 9 ], a24 = ae[ 13 ];
		const a31 = ae[ 2 ], a32 = ae[ 6 ], a33 = ae[ 10 ], a34 = ae[ 14 ];
		const a41 = ae[ 3 ], a42 = ae[ 7 ], a43 = ae[ 11 ], a44 = ae[ 15 ];

		const b11 = be[ 0 ], b12 = be[ 4 ], b13 = be[ 8 ], b14 = be[ 12 ];
		const b21 = be[ 1 ], b22 = be[ 5 ], b23 = be[ 9 ], b24 = be[ 13 ];
		const b31 = be[ 2 ], b32 = be[ 6 ], b33 = be[ 10 ], b34 = be[ 14 ];
		const b41 = be[ 3 ], b42 = be[ 7 ], b43 = be[ 11 ], b44 = be[ 15 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
		te[ 4 ] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
		te[ 8 ] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
		te[ 12 ] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
		te[ 5 ] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
		te[ 9 ] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
		te[ 13 ] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
		te[ 6 ] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
		te[ 10 ] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
		te[ 14 ] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;

		te[ 3 ] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
		te[ 7 ] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
		te[ 11 ] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
		te[ 15 ] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 4 ] *= s; te[ 8 ] *= s; te[ 12 ] *= s;
		te[ 1 ] *= s; te[ 5 ] *= s; te[ 9 ] *= s; te[ 13 ] *= s;
		te[ 2 ] *= s; te[ 6 ] *= s; te[ 10 ] *= s; te[ 14 ] *= s;
		te[ 3 ] *= s; te[ 7 ] *= s; te[ 11 ] *= s; te[ 15 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const n11 = te[ 0 ], n12 = te[ 4 ], n13 = te[ 8 ], n14 = te[ 12 ];
		const n21 = te[ 1 ], n22 = te[ 5 ], n23 = te[ 9 ], n24 = te[ 13 ];
		const n31 = te[ 2 ], n32 = te[ 6 ], n33 = te[ 10 ], n34 = te[ 14 ];
		const n41 = te[ 3 ], n42 = te[ 7 ], n43 = te[ 11 ], n44 = te[ 15 ];

		//TODO: make this more efficient
		//( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

		return (
			n41 * (
				+ n14 * n23 * n32
				 - n13 * n24 * n32
				 - n14 * n22 * n33
				 + n12 * n24 * n33
				 + n13 * n22 * n34
				 - n12 * n23 * n34
			) +
			n42 * (
				+ n11 * n23 * n34
				 - n11 * n24 * n33
				 + n14 * n21 * n33
				 - n13 * n21 * n34
				 + n13 * n24 * n31
				 - n14 * n23 * n31
			) +
			n43 * (
				+ n11 * n24 * n32
				 - n11 * n22 * n34
				 - n14 * n21 * n32
				 + n12 * n21 * n34
				 + n14 * n22 * n31
				 - n12 * n24 * n31
			) +
			n44 * (
				- n13 * n22 * n31
				 - n11 * n23 * n32
				 + n11 * n22 * n33
				 + n13 * n21 * n32
				 - n12 * n21 * n33
				 + n12 * n23 * n31
			)

		);

	}

	transpose() {

		const te = this.elements;
		let tmp;

		tmp = te[ 1 ]; te[ 1 ] = te[ 4 ]; te[ 4 ] = tmp;
		tmp = te[ 2 ]; te[ 2 ] = te[ 8 ]; te[ 8 ] = tmp;
		tmp = te[ 6 ]; te[ 6 ] = te[ 9 ]; te[ 9 ] = tmp;

		tmp = te[ 3 ]; te[ 3 ] = te[ 12 ]; te[ 12 ] = tmp;
		tmp = te[ 7 ]; te[ 7 ] = te[ 13 ]; te[ 13 ] = tmp;
		tmp = te[ 11 ]; te[ 11 ] = te[ 14 ]; te[ 14 ] = tmp;

		return this;

	}

	setPosition( x, y, z ) {

		const te = this.elements;

		if ( x.isVector3 ) {

			te[ 12 ] = x.x;
			te[ 13 ] = x.y;
			te[ 14 ] = x.z;

		} else {

			te[ 12 ] = x;
			te[ 13 ] = y;
			te[ 14 ] = z;

		}

		return this;

	}

	invert() {

		// based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ], n41 = te[ 3 ],
			n12 = te[ 4 ], n22 = te[ 5 ], n32 = te[ 6 ], n42 = te[ 7 ],
			n13 = te[ 8 ], n23 = te[ 9 ], n33 = te[ 10 ], n43 = te[ 11 ],
			n14 = te[ 12 ], n24 = te[ 13 ], n34 = te[ 14 ], n44 = te[ 15 ],

			t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
			t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
			t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
			t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;

		const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44 ) * detInv;
		te[ 2 ] = ( n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44 ) * detInv;
		te[ 3 ] = ( n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43 ) * detInv;

		te[ 4 ] = t12 * detInv;
		te[ 5 ] = ( n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44 ) * detInv;
		te[ 6 ] = ( n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44 ) * detInv;
		te[ 7 ] = ( n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43 ) * detInv;

		te[ 8 ] = t13 * detInv;
		te[ 9 ] = ( n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44 ) * detInv;
		te[ 10 ] = ( n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44 ) * detInv;
		te[ 11 ] = ( n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43 ) * detInv;

		te[ 12 ] = t14 * detInv;
		te[ 13 ] = ( n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34 ) * detInv;
		te[ 14 ] = ( n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34 ) * detInv;
		te[ 15 ] = ( n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33 ) * detInv;

		return this;

	}

	scale( v ) {

		const te = this.elements;
		const x = v.x, y = v.y, z = v.z;

		te[ 0 ] *= x; te[ 4 ] *= y; te[ 8 ] *= z;
		te[ 1 ] *= x; te[ 5 ] *= y; te[ 9 ] *= z;
		te[ 2 ] *= x; te[ 6 ] *= y; te[ 10 ] *= z;
		te[ 3 ] *= x; te[ 7 ] *= y; te[ 11 ] *= z;

		return this;

	}

	getMaxScaleOnAxis() {

		const te = this.elements;

		const scaleXSq = te[ 0 ] * te[ 0 ] + te[ 1 ] * te[ 1 ] + te[ 2 ] * te[ 2 ];
		const scaleYSq = te[ 4 ] * te[ 4 ] + te[ 5 ] * te[ 5 ] + te[ 6 ] * te[ 6 ];
		const scaleZSq = te[ 8 ] * te[ 8 ] + te[ 9 ] * te[ 9 ] + te[ 10 ] * te[ 10 ];

		return Math.sqrt( Math.max( scaleXSq, scaleYSq, scaleZSq ) );

	}

	makeTranslation( x, y, z ) {

		if ( x.isVector3 ) {

			this.set(

				1, 0, 0, x.x,
				0, 1, 0, x.y,
				0, 0, 1, x.z,
				0, 0, 0, 1

			);

		} else {

			this.set(

				1, 0, 0, x,
				0, 1, 0, y,
				0, 0, 1, z,
				0, 0, 0, 1

			);

		}

		return this;

	}

	makeRotationX( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			1, 0, 0, 0,
			0, c, - s, 0,
			0, s, c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationY( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			 c, 0, s, 0,
			 0, 1, 0, 0,
			- s, 0, c, 0,
			 0, 0, 0, 1

		);

		return this;

	}

	makeRotationZ( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			c, - s, 0, 0,
			s, c, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationAxis( axis, angle ) {

		// Based on http://www.gamedev.net/reference/articles/article1199.asp

		const c = Math.cos( angle );
		const s = Math.sin( angle );
		const t = 1 - c;
		const x = axis.x, y = axis.y, z = axis.z;
		const tx = t * x, ty = t * y;

		this.set(

			tx * x + c, tx * y - s * z, tx * z + s * y, 0,
			tx * y + s * z, ty * y + c, ty * z - s * x, 0,
			tx * z - s * y, ty * z + s * x, t * z * z + c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeScale( x, y, z ) {

		this.set(

			x, 0, 0, 0,
			0, y, 0, 0,
			0, 0, z, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeShear( xy, xz, yx, yz, zx, zy ) {

		this.set(

			1, yx, zx, 0,
			xy, 1, zy, 0,
			xz, yz, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	compose( position, quaternion, scale ) {

		const te = this.elements;

		const x = quaternion._x, y = quaternion._y, z = quaternion._z, w = quaternion._w;
		const x2 = x + x,	y2 = y + y, z2 = z + z;
		const xx = x * x2, xy = x * y2, xz = x * z2;
		const yy = y * y2, yz = y * z2, zz = z * z2;
		const wx = w * x2, wy = w * y2, wz = w * z2;

		const sx = scale.x, sy = scale.y, sz = scale.z;

		te[ 0 ] = ( 1 - ( yy + zz ) ) * sx;
		te[ 1 ] = ( xy + wz ) * sx;
		te[ 2 ] = ( xz - wy ) * sx;
		te[ 3 ] = 0;

		te[ 4 ] = ( xy - wz ) * sy;
		te[ 5 ] = ( 1 - ( xx + zz ) ) * sy;
		te[ 6 ] = ( yz + wx ) * sy;
		te[ 7 ] = 0;

		te[ 8 ] = ( xz + wy ) * sz;
		te[ 9 ] = ( yz - wx ) * sz;
		te[ 10 ] = ( 1 - ( xx + yy ) ) * sz;
		te[ 11 ] = 0;

		te[ 12 ] = position.x;
		te[ 13 ] = position.y;
		te[ 14 ] = position.z;
		te[ 15 ] = 1;

		return this;

	}

	decompose( position, quaternion, scale ) {

		const te = this.elements;

		let sx = _v1$5.set( te[ 0 ], te[ 1 ], te[ 2 ] ).length();
		const sy = _v1$5.set( te[ 4 ], te[ 5 ], te[ 6 ] ).length();
		const sz = _v1$5.set( te[ 8 ], te[ 9 ], te[ 10 ] ).length();

		// if determine is negative, we need to invert one scale
		const det = this.determinant();
		if ( det < 0 ) sx = - sx;

		position.x = te[ 12 ];
		position.y = te[ 13 ];
		position.z = te[ 14 ];

		// scale the rotation part
		_m1$4.copy( this );

		const invSX = 1 / sx;
		const invSY = 1 / sy;
		const invSZ = 1 / sz;

		_m1$4.elements[ 0 ] *= invSX;
		_m1$4.elements[ 1 ] *= invSX;
		_m1$4.elements[ 2 ] *= invSX;

		_m1$4.elements[ 4 ] *= invSY;
		_m1$4.elements[ 5 ] *= invSY;
		_m1$4.elements[ 6 ] *= invSY;

		_m1$4.elements[ 8 ] *= invSZ;
		_m1$4.elements[ 9 ] *= invSZ;
		_m1$4.elements[ 10 ] *= invSZ;

		quaternion.setFromRotationMatrix( _m1$4 );

		scale.x = sx;
		scale.y = sy;
		scale.z = sz;

		return this;

	}

	makePerspective( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const x = 2 * near / ( right - left );
		const y = 2 * near / ( top - bottom );

		const a = ( right + left ) / ( right - left );
		const b = ( top + bottom ) / ( top - bottom );

		let c, d;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			c = - ( far + near ) / ( far - near );
			d = ( - 2 * far * near ) / ( far - near );

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			c = - far / ( far - near );
			d = ( - far * near ) / ( far - near );

		} else {

			throw new Error( 'THREE.Matrix4.makePerspective(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = x;	te[ 4 ] = 0;	te[ 8 ] = a; 	te[ 12 ] = 0;
		te[ 1 ] = 0;	te[ 5 ] = y;	te[ 9 ] = b; 	te[ 13 ] = 0;
		te[ 2 ] = 0;	te[ 6 ] = 0;	te[ 10 ] = c; 	te[ 14 ] = d;
		te[ 3 ] = 0;	te[ 7 ] = 0;	te[ 11 ] = - 1;	te[ 15 ] = 0;

		return this;

	}

	makeOrthographic( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const w = 1.0 / ( right - left );
		const h = 1.0 / ( top - bottom );
		const p = 1.0 / ( far - near );

		const x = ( right + left ) * w;
		const y = ( top + bottom ) * h;

		let z, zInv;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			z = ( far + near ) * p;
			zInv = - 2 * p;

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			z = near * p;
			zInv = - 1 * p;

		} else {

			throw new Error( 'THREE.Matrix4.makeOrthographic(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = 2 * w;	te[ 4 ] = 0;		te[ 8 ] = 0; 		te[ 12 ] = - x;
		te[ 1 ] = 0; 		te[ 5 ] = 2 * h;	te[ 9 ] = 0; 		te[ 13 ] = - y;
		te[ 2 ] = 0; 		te[ 6 ] = 0;		te[ 10 ] = zInv;	te[ 14 ] = - z;
		te[ 3 ] = 0; 		te[ 7 ] = 0;		te[ 11 ] = 0;		te[ 15 ] = 1;

		return this;

	}

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 16; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 16; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];
		array[ offset + 3 ] = te[ 3 ];

		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];
		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];

		array[ offset + 8 ] = te[ 8 ];
		array[ offset + 9 ] = te[ 9 ];
		array[ offset + 10 ] = te[ 10 ];
		array[ offset + 11 ] = te[ 11 ];

		array[ offset + 12 ] = te[ 12 ];
		array[ offset + 13 ] = te[ 13 ];
		array[ offset + 14 ] = te[ 14 ];
		array[ offset + 15 ] = te[ 15 ];

		return array;

	}

}

const _v1$5 = /*@__PURE__*/ new Vector3();
const _m1$4 = /*@__PURE__*/ new Matrix4();
const _zero = /*@__PURE__*/ new Vector3( 0, 0, 0 );
const _one = /*@__PURE__*/ new Vector3( 1, 1, 1 );
const _x = /*@__PURE__*/ new Vector3();
const _y = /*@__PURE__*/ new Vector3();
const _z = /*@__PURE__*/ new Vector3();

const _matrix$2 = /*@__PURE__*/ new Matrix4();
const _quaternion$3 = /*@__PURE__*/ new Quaternion();

class Euler {

	constructor( x = 0, y = 0, z = 0, order = Euler.DEFAULT_ORDER ) {

		this.isEuler = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._order = order;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get order() {

		return this._order;

	}

	set order( value ) {

		this._order = value;
		this._onChangeCallback();

	}

	set( x, y, z, order = this._order ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._order = order;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._order );

	}

	copy( euler ) {

		this._x = euler._x;
		this._y = euler._y;
		this._z = euler._z;
		this._order = euler._order;

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m, order = this._order, update = true ) {

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements;
		const m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ];
		const m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ];
		const m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ];

		switch ( order ) {

			case 'XYZ':

				this._y = Math.asin( clamp( m13, - 1, 1 ) );

				if ( Math.abs( m13 ) < 0.9999999 ) {

					this._x = Math.atan2( - m23, m33 );
					this._z = Math.atan2( - m12, m11 );

				} else {

					this._x = Math.atan2( m32, m22 );
					this._z = 0;

				}

				break;

			case 'YXZ':

				this._x = Math.asin( - clamp( m23, - 1, 1 ) );

				if ( Math.abs( m23 ) < 0.9999999 ) {

					this._y = Math.atan2( m13, m33 );
					this._z = Math.atan2( m21, m22 );

				} else {

					this._y = Math.atan2( - m31, m11 );
					this._z = 0;

				}

				break;

			case 'ZXY':

				this._x = Math.asin( clamp( m32, - 1, 1 ) );

				if ( Math.abs( m32 ) < 0.9999999 ) {

					this._y = Math.atan2( - m31, m33 );
					this._z = Math.atan2( - m12, m22 );

				} else {

					this._y = 0;
					this._z = Math.atan2( m21, m11 );

				}

				break;

			case 'ZYX':

				this._y = Math.asin( - clamp( m31, - 1, 1 ) );

				if ( Math.abs( m31 ) < 0.9999999 ) {

					this._x = Math.atan2( m32, m33 );
					this._z = Math.atan2( m21, m11 );

				} else {

					this._x = 0;
					this._z = Math.atan2( - m12, m22 );

				}

				break;

			case 'YZX':

				this._z = Math.asin( clamp( m21, - 1, 1 ) );

				if ( Math.abs( m21 ) < 0.9999999 ) {

					this._x = Math.atan2( - m23, m22 );
					this._y = Math.atan2( - m31, m11 );

				} else {

					this._x = 0;
					this._y = Math.atan2( m13, m33 );

				}

				break;

			case 'XZY':

				this._z = Math.asin( - clamp( m12, - 1, 1 ) );

				if ( Math.abs( m12 ) < 0.9999999 ) {

					this._x = Math.atan2( m32, m22 );
					this._y = Math.atan2( m13, m11 );

				} else {

					this._x = Math.atan2( - m23, m33 );
					this._y = 0;

				}

				break;

			default:

				console.warn( 'THREE.Euler: .setFromRotationMatrix() encountered an unknown order: ' + order );

		}

		this._order = order;

		if ( update === true ) this._onChangeCallback();

		return this;

	}

	setFromQuaternion( q, order, update ) {

		_matrix$2.makeRotationFromQuaternion( q );

		return this.setFromRotationMatrix( _matrix$2, order, update );

	}

	setFromVector3( v, order = this._order ) {

		return this.set( v.x, v.y, v.z, order );

	}

	reorder( newOrder ) {

		// WARNING: this discards revolution information -bhouston

		_quaternion$3.setFromEuler( this );

		return this.setFromQuaternion( _quaternion$3, newOrder );

	}

	equals( euler ) {

		return ( euler._x === this._x ) && ( euler._y === this._y ) && ( euler._z === this._z ) && ( euler._order === this._order );

	}

	fromArray( array ) {

		this._x = array[ 0 ];
		this._y = array[ 1 ];
		this._z = array[ 2 ];
		if ( array[ 3 ] !== undefined ) this._order = array[ 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._order;

		return array;

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._order;

	}

}

Euler.DEFAULT_ORDER = 'XYZ';

class Layers {

	constructor() {

		this.mask = 1 | 0;

	}

	set( channel ) {

		this.mask = ( 1 << channel | 0 ) >>> 0;

	}

	enable( channel ) {

		this.mask |= 1 << channel | 0;

	}

	enableAll() {

		this.mask = 0xffffffff | 0;

	}

	toggle( channel ) {

		this.mask ^= 1 << channel | 0;

	}

	disable( channel ) {

		this.mask &= ~ ( 1 << channel | 0 );

	}

	disableAll() {

		this.mask = 0;

	}

	test( layers ) {

		return ( this.mask & layers.mask ) !== 0;

	}

	isEnabled( channel ) {

		return ( this.mask & ( 1 << channel | 0 ) ) !== 0;

	}

}

let _object3DId = 0;

const _v1$4 = /*@__PURE__*/ new Vector3();
const _q1 = /*@__PURE__*/ new Quaternion();
const _m1$3 = /*@__PURE__*/ new Matrix4();
const _target = /*@__PURE__*/ new Vector3();

const _position$3 = /*@__PURE__*/ new Vector3();
const _scale$2 = /*@__PURE__*/ new Vector3();
const _quaternion$2 = /*@__PURE__*/ new Quaternion();

const _xAxis = /*@__PURE__*/ new Vector3( 1, 0, 0 );
const _yAxis = /*@__PURE__*/ new Vector3( 0, 1, 0 );
const _zAxis = /*@__PURE__*/ new Vector3( 0, 0, 1 );

const _addedEvent = { type: 'added' };
const _removedEvent = { type: 'removed' };

const _childaddedEvent = { type: 'childadded', child: null };
const _childremovedEvent = { type: 'childremoved', child: null };

class Object3D extends EventDispatcher {

	constructor() {

		super();

		this.isObject3D = true;

		Object.defineProperty( this, 'id', { value: _object3DId ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'Object3D';

		this.parent = null;
		this.children = [];

		this.up = Object3D.DEFAULT_UP.clone();

		const position = new Vector3();
		const rotation = new Euler();
		const quaternion = new Quaternion();
		const scale = new Vector3( 1, 1, 1 );

		function onRotationChange() {

			quaternion.setFromEuler( rotation, false );

		}

		function onQuaternionChange() {

			rotation.setFromQuaternion( quaternion, undefined, false );

		}

		rotation._onChange( onRotationChange );
		quaternion._onChange( onQuaternionChange );

		Object.defineProperties( this, {
			position: {
				configurable: true,
				enumerable: true,
				value: position
			},
			rotation: {
				configurable: true,
				enumerable: true,
				value: rotation
			},
			quaternion: {
				configurable: true,
				enumerable: true,
				value: quaternion
			},
			scale: {
				configurable: true,
				enumerable: true,
				value: scale
			},
			modelViewMatrix: {
				value: new Matrix4()
			},
			normalMatrix: {
				value: new Matrix3()
			}
		} );

		this.matrix = new Matrix4();
		this.matrixWorld = new Matrix4();

		this.matrixAutoUpdate = Object3D.DEFAULT_MATRIX_AUTO_UPDATE;

		this.matrixWorldAutoUpdate = Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE; // checked by the renderer
		this.matrixWorldNeedsUpdate = false;

		this.layers = new Layers();
		this.visible = true;

		this.castShadow = false;
		this.receiveShadow = false;

		this.frustumCulled = true;
		this.renderOrder = 0;

		this.animations = [];

		this.userData = {};

	}

	onBeforeShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */ ) {}

	onAfterShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */ ) {}

	onBeforeRender( /* renderer, scene, camera, geometry, material, group */ ) {}

	onAfterRender( /* renderer, scene, camera, geometry, material, group */ ) {}

	applyMatrix4( matrix ) {

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		this.matrix.premultiply( matrix );

		this.matrix.decompose( this.position, this.quaternion, this.scale );

	}

	applyQuaternion( q ) {

		this.quaternion.premultiply( q );

		return this;

	}

	setRotationFromAxisAngle( axis, angle ) {

		// assumes axis is normalized

		this.quaternion.setFromAxisAngle( axis, angle );

	}

	setRotationFromEuler( euler ) {

		this.quaternion.setFromEuler( euler, true );

	}

	setRotationFromMatrix( m ) {

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		this.quaternion.setFromRotationMatrix( m );

	}

	setRotationFromQuaternion( q ) {

		// assumes q is normalized

		this.quaternion.copy( q );

	}

	rotateOnAxis( axis, angle ) {

		// rotate object on axis in object space
		// axis is assumed to be normalized

		_q1.setFromAxisAngle( axis, angle );

		this.quaternion.multiply( _q1 );

		return this;

	}

	rotateOnWorldAxis( axis, angle ) {

		// rotate object on axis in world space
		// axis is assumed to be normalized
		// method assumes no rotated parent

		_q1.setFromAxisAngle( axis, angle );

		this.quaternion.premultiply( _q1 );

		return this;

	}

	rotateX( angle ) {

		return this.rotateOnAxis( _xAxis, angle );

	}

	rotateY( angle ) {

		return this.rotateOnAxis( _yAxis, angle );

	}

	rotateZ( angle ) {

		return this.rotateOnAxis( _zAxis, angle );

	}

	translateOnAxis( axis, distance ) {

		// translate object by distance along axis in object space
		// axis is assumed to be normalized

		_v1$4.copy( axis ).applyQuaternion( this.quaternion );

		this.position.add( _v1$4.multiplyScalar( distance ) );

		return this;

	}

	translateX( distance ) {

		return this.translateOnAxis( _xAxis, distance );

	}

	translateY( distance ) {

		return this.translateOnAxis( _yAxis, distance );

	}

	translateZ( distance ) {

		return this.translateOnAxis( _zAxis, distance );

	}

	localToWorld( vector ) {

		this.updateWorldMatrix( true, false );

		return vector.applyMatrix4( this.matrixWorld );

	}

	worldToLocal( vector ) {

		this.updateWorldMatrix( true, false );

		return vector.applyMatrix4( _m1$3.copy( this.matrixWorld ).invert() );

	}

	lookAt( x, y, z ) {

		// This method does not support objects having non-uniformly-scaled parent(s)

		if ( x.isVector3 ) {

			_target.copy( x );

		} else {

			_target.set( x, y, z );

		}

		const parent = this.parent;

		this.updateWorldMatrix( true, false );

		_position$3.setFromMatrixPosition( this.matrixWorld );

		if ( this.isCamera || this.isLight ) {

			_m1$3.lookAt( _position$3, _target, this.up );

		} else {

			_m1$3.lookAt( _target, _position$3, this.up );

		}

		this.quaternion.setFromRotationMatrix( _m1$3 );

		if ( parent ) {

			_m1$3.extractRotation( parent.matrixWorld );
			_q1.setFromRotationMatrix( _m1$3 );
			this.quaternion.premultiply( _q1.invert() );

		}

	}

	add( object ) {

		if ( arguments.length > 1 ) {

			for ( let i = 0; i < arguments.length; i ++ ) {

				this.add( arguments[ i ] );

			}

			return this;

		}

		if ( object === this ) {

			console.error( 'THREE.Object3D.add: object can\'t be added as a child of itself.', object );
			return this;

		}

		if ( object && object.isObject3D ) {

			object.removeFromParent();
			object.parent = this;
			this.children.push( object );

			object.dispatchEvent( _addedEvent );

			_childaddedEvent.child = object;
			this.dispatchEvent( _childaddedEvent );
			_childaddedEvent.child = null;

		} else {

			console.error( 'THREE.Object3D.add: object not an instance of THREE.Object3D.', object );

		}

		return this;

	}

	remove( object ) {

		if ( arguments.length > 1 ) {

			for ( let i = 0; i < arguments.length; i ++ ) {

				this.remove( arguments[ i ] );

			}

			return this;

		}

		const index = this.children.indexOf( object );

		if ( index !== - 1 ) {

			object.parent = null;
			this.children.splice( index, 1 );

			object.dispatchEvent( _removedEvent );

			_childremovedEvent.child = object;
			this.dispatchEvent( _childremovedEvent );
			_childremovedEvent.child = null;

		}

		return this;

	}

	removeFromParent() {

		const parent = this.parent;

		if ( parent !== null ) {

			parent.remove( this );

		}

		return this;

	}

	clear() {

		return this.remove( ... this.children );

	}

	attach( object ) {

		// adds object as a child of this, while maintaining the object's world transform

		// Note: This method does not support scene graphs having non-uniformly-scaled nodes(s)

		this.updateWorldMatrix( true, false );

		_m1$3.copy( this.matrixWorld ).invert();

		if ( object.parent !== null ) {

			object.parent.updateWorldMatrix( true, false );

			_m1$3.multiply( object.parent.matrixWorld );

		}

		object.applyMatrix4( _m1$3 );

		object.removeFromParent();
		object.parent = this;
		this.children.push( object );

		object.updateWorldMatrix( false, true );

		object.dispatchEvent( _addedEvent );

		_childaddedEvent.child = object;
		this.dispatchEvent( _childaddedEvent );
		_childaddedEvent.child = null;

		return this;

	}

	getObjectById( id ) {

		return this.getObjectByProperty( 'id', id );

	}

	getObjectByName( name ) {

		return this.getObjectByProperty( 'name', name );

	}

	getObjectByProperty( name, value ) {

		if ( this[ name ] === value ) return this;

		for ( let i = 0, l = this.children.length; i < l; i ++ ) {

			const child = this.children[ i ];
			const object = child.getObjectByProperty( name, value );

			if ( object !== undefined ) {

				return object;

			}

		}

		return undefined;

	}

	getObjectsByProperty( name, value, result = [] ) {

		if ( this[ name ] === value ) result.push( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].getObjectsByProperty( name, value, result );

		}

		return result;

	}

	getWorldPosition( target ) {

		this.updateWorldMatrix( true, false );

		return target.setFromMatrixPosition( this.matrixWorld );

	}

	getWorldQuaternion( target ) {

		this.updateWorldMatrix( true, false );

		this.matrixWorld.decompose( _position$3, target, _scale$2 );

		return target;

	}

	getWorldScale( target ) {

		this.updateWorldMatrix( true, false );

		this.matrixWorld.decompose( _position$3, _quaternion$2, target );

		return target;

	}

	getWorldDirection( target ) {

		this.updateWorldMatrix( true, false );

		const e = this.matrixWorld.elements;

		return target.set( e[ 8 ], e[ 9 ], e[ 10 ] ).normalize();

	}

	raycast( /* raycaster, intersects */ ) {}

	traverse( callback ) {

		callback( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].traverse( callback );

		}

	}

	traverseVisible( callback ) {

		if ( this.visible === false ) return;

		callback( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].traverseVisible( callback );

		}

	}

	traverseAncestors( callback ) {

		const parent = this.parent;

		if ( parent !== null ) {

			callback( parent );

			parent.traverseAncestors( callback );

		}

	}

	updateMatrix() {

		this.matrix.compose( this.position, this.quaternion, this.scale );

		this.matrixWorldNeedsUpdate = true;

	}

	updateMatrixWorld( force ) {

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		if ( this.matrixWorldNeedsUpdate || force ) {

			if ( this.matrixWorldAutoUpdate === true ) {

				if ( this.parent === null ) {

					this.matrixWorld.copy( this.matrix );

				} else {

					this.matrixWorld.multiplyMatrices( this.parent.matrixWorld, this.matrix );

				}

			}

			this.matrixWorldNeedsUpdate = false;

			force = true;

		}

		// make sure descendants are updated if required

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			const child = children[ i ];

			child.updateMatrixWorld( force );

		}

	}

	updateWorldMatrix( updateParents, updateChildren ) {

		const parent = this.parent;

		if ( updateParents === true && parent !== null ) {

			parent.updateWorldMatrix( true, false );

		}

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		if ( this.matrixWorldAutoUpdate === true ) {

			if ( this.parent === null ) {

				this.matrixWorld.copy( this.matrix );

			} else {

				this.matrixWorld.multiplyMatrices( this.parent.matrixWorld, this.matrix );

			}

		}

		// make sure descendants are updated

		if ( updateChildren === true ) {

			const children = this.children;

			for ( let i = 0, l = children.length; i < l; i ++ ) {

				const child = children[ i ];

				child.updateWorldMatrix( false, true );

			}

		}

	}

	toJSON( meta ) {

		// meta is a string when called from JSON.stringify
		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		const output = {};

		// meta is a hash used to collect geometries, materials.
		// not providing it implies that this is the root object
		// being serialized.
		if ( isRootObject ) {

			// initialize meta obj
			meta = {
				geometries: {},
				materials: {},
				textures: {},
				images: {},
				shapes: {},
				skeletons: {},
				animations: {},
				nodes: {}
			};

			output.metadata = {
				version: 4.6,
				type: 'Object',
				generator: 'Object3D.toJSON'
			};

		}

		// standard Object3D serialization

		const object = {};

		object.uuid = this.uuid;
		object.type = this.type;

		if ( this.name !== '' ) object.name = this.name;
		if ( this.castShadow === true ) object.castShadow = true;
		if ( this.receiveShadow === true ) object.receiveShadow = true;
		if ( this.visible === false ) object.visible = false;
		if ( this.frustumCulled === false ) object.frustumCulled = false;
		if ( this.renderOrder !== 0 ) object.renderOrder = this.renderOrder;
		if ( Object.keys( this.userData ).length > 0 ) object.userData = this.userData;

		object.layers = this.layers.mask;
		object.matrix = this.matrix.toArray();
		object.up = this.up.toArray();

		if ( this.matrixAutoUpdate === false ) object.matrixAutoUpdate = false;

		// object specific properties

		if ( this.isInstancedMesh ) {

			object.type = 'InstancedMesh';
			object.count = this.count;
			object.instanceMatrix = this.instanceMatrix.toJSON();
			if ( this.instanceColor !== null ) object.instanceColor = this.instanceColor.toJSON();

		}

		if ( this.isBatchedMesh ) {

			object.type = 'BatchedMesh';
			object.perObjectFrustumCulled = this.perObjectFrustumCulled;
			object.sortObjects = this.sortObjects;

			object.drawRanges = this._drawRanges;
			object.reservedRanges = this._reservedRanges;

			object.visibility = this._visibility;
			object.active = this._active;
			object.bounds = this._bounds.map( bound => ( {
				boxInitialized: bound.boxInitialized,
				boxMin: bound.box.min.toArray(),
				boxMax: bound.box.max.toArray(),

				sphereInitialized: bound.sphereInitialized,
				sphereRadius: bound.sphere.radius,
				sphereCenter: bound.sphere.center.toArray()
			} ) );

			object.maxInstanceCount = this._maxInstanceCount;
			object.maxVertexCount = this._maxVertexCount;
			object.maxIndexCount = this._maxIndexCount;

			object.geometryInitialized = this._geometryInitialized;
			object.geometryCount = this._geometryCount;

			object.matricesTexture = this._matricesTexture.toJSON( meta );

			if ( this._colorsTexture !== null ) object.colorsTexture = this._colorsTexture.toJSON( meta );

			if ( this.boundingSphere !== null ) {

				object.boundingSphere = {
					center: object.boundingSphere.center.toArray(),
					radius: object.boundingSphere.radius
				};

			}

			if ( this.boundingBox !== null ) {

				object.boundingBox = {
					min: object.boundingBox.min.toArray(),
					max: object.boundingBox.max.toArray()
				};

			}

		}

		//

		function serialize( library, element ) {

			if ( library[ element.uuid ] === undefined ) {

				library[ element.uuid ] = element.toJSON( meta );

			}

			return element.uuid;

		}

		if ( this.isScene ) {

			if ( this.background ) {

				if ( this.background.isColor ) {

					object.background = this.background.toJSON();

				} else if ( this.background.isTexture ) {

					object.background = this.background.toJSON( meta ).uuid;

				}

			}

			if ( this.environment && this.environment.isTexture && this.environment.isRenderTargetTexture !== true ) {

				object.environment = this.environment.toJSON( meta ).uuid;

			}

		} else if ( this.isMesh || this.isLine || this.isPoints ) {

			object.geometry = serialize( meta.geometries, this.geometry );

			const parameters = this.geometry.parameters;

			if ( parameters !== undefined && parameters.shapes !== undefined ) {

				const shapes = parameters.shapes;

				if ( Array.isArray( shapes ) ) {

					for ( let i = 0, l = shapes.length; i < l; i ++ ) {

						const shape = shapes[ i ];

						serialize( meta.shapes, shape );

					}

				} else {

					serialize( meta.shapes, shapes );

				}

			}

		}

		if ( this.isSkinnedMesh ) {

			object.bindMode = this.bindMode;
			object.bindMatrix = this.bindMatrix.toArray();

			if ( this.skeleton !== undefined ) {

				serialize( meta.skeletons, this.skeleton );

				object.skeleton = this.skeleton.uuid;

			}

		}

		if ( this.material !== undefined ) {

			if ( Array.isArray( this.material ) ) {

				const uuids = [];

				for ( let i = 0, l = this.material.length; i < l; i ++ ) {

					uuids.push( serialize( meta.materials, this.material[ i ] ) );

				}

				object.material = uuids;

			} else {

				object.material = serialize( meta.materials, this.material );

			}

		}

		//

		if ( this.children.length > 0 ) {

			object.children = [];

			for ( let i = 0; i < this.children.length; i ++ ) {

				object.children.push( this.children[ i ].toJSON( meta ).object );

			}

		}

		//

		if ( this.animations.length > 0 ) {

			object.animations = [];

			for ( let i = 0; i < this.animations.length; i ++ ) {

				const animation = this.animations[ i ];

				object.animations.push( serialize( meta.animations, animation ) );

			}

		}

		if ( isRootObject ) {

			const geometries = extractFromCache( meta.geometries );
			const materials = extractFromCache( meta.materials );
			const textures = extractFromCache( meta.textures );
			const images = extractFromCache( meta.images );
			const shapes = extractFromCache( meta.shapes );
			const skeletons = extractFromCache( meta.skeletons );
			const animations = extractFromCache( meta.animations );
			const nodes = extractFromCache( meta.nodes );

			if ( geometries.length > 0 ) output.geometries = geometries;
			if ( materials.length > 0 ) output.materials = materials;
			if ( textures.length > 0 ) output.textures = textures;
			if ( images.length > 0 ) output.images = images;
			if ( shapes.length > 0 ) output.shapes = shapes;
			if ( skeletons.length > 0 ) output.skeletons = skeletons;
			if ( animations.length > 0 ) output.animations = animations;
			if ( nodes.length > 0 ) output.nodes = nodes;

		}

		output.object = object;

		return output;

		// extract data from the cache hash
		// remove metadata on each item
		// and return as array
		function extractFromCache( cache ) {

			const values = [];
			for ( const key in cache ) {

				const data = cache[ key ];
				delete data.metadata;
				values.push( data );

			}

			return values;

		}

	}

	clone( recursive ) {

		return new this.constructor().copy( this, recursive );

	}

	copy( source, recursive = true ) {

		this.name = source.name;

		this.up.copy( source.up );

		this.position.copy( source.position );
		this.rotation.order = source.rotation.order;
		this.quaternion.copy( source.quaternion );
		this.scale.copy( source.scale );

		this.matrix.copy( source.matrix );
		this.matrixWorld.copy( source.matrixWorld );

		this.matrixAutoUpdate = source.matrixAutoUpdate;

		this.matrixWorldAutoUpdate = source.matrixWorldAutoUpdate;
		this.matrixWorldNeedsUpdate = source.matrixWorldNeedsUpdate;

		this.layers.mask = source.layers.mask;
		this.visible = source.visible;

		this.castShadow = source.castShadow;
		this.receiveShadow = source.receiveShadow;

		this.frustumCulled = source.frustumCulled;
		this.renderOrder = source.renderOrder;

		this.animations = source.animations.slice();

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		if ( recursive === true ) {

			for ( let i = 0; i < source.children.length; i ++ ) {

				const child = source.children[ i ];
				this.add( child.clone() );

			}

		}

		return this;

	}

}

Object3D.DEFAULT_UP = /*@__PURE__*/ new Vector3( 0, 1, 0 );
Object3D.DEFAULT_MATRIX_AUTO_UPDATE = true;
Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE = true;

const _v0$2 = /*@__PURE__*/ new Vector3();
const _v1$3 = /*@__PURE__*/ new Vector3();
const _v2$2 = /*@__PURE__*/ new Vector3();
const _v3$2 = /*@__PURE__*/ new Vector3();

const _vab = /*@__PURE__*/ new Vector3();
const _vac = /*@__PURE__*/ new Vector3();
const _vbc = /*@__PURE__*/ new Vector3();
const _vap = /*@__PURE__*/ new Vector3();
const _vbp = /*@__PURE__*/ new Vector3();
const _vcp = /*@__PURE__*/ new Vector3();

const _v40 = /*@__PURE__*/ new Vector4();
const _v41 = /*@__PURE__*/ new Vector4();
const _v42 = /*@__PURE__*/ new Vector4();

class Triangle {

	constructor( a = new Vector3(), b = new Vector3(), c = new Vector3() ) {

		this.a = a;
		this.b = b;
		this.c = c;

	}

	static getNormal( a, b, c, target ) {

		target.subVectors( c, b );
		_v0$2.subVectors( a, b );
		target.cross( _v0$2 );

		const targetLengthSq = target.lengthSq();
		if ( targetLengthSq > 0 ) {

			return target.multiplyScalar( 1 / Math.sqrt( targetLengthSq ) );

		}

		return target.set( 0, 0, 0 );

	}

	// static/instance method to calculate barycentric coordinates
	// based on: http://www.blackpawn.com/texts/pointinpoly/default.html
	static getBarycoord( point, a, b, c, target ) {

		_v0$2.subVectors( c, a );
		_v1$3.subVectors( b, a );
		_v2$2.subVectors( point, a );

		const dot00 = _v0$2.dot( _v0$2 );
		const dot01 = _v0$2.dot( _v1$3 );
		const dot02 = _v0$2.dot( _v2$2 );
		const dot11 = _v1$3.dot( _v1$3 );
		const dot12 = _v1$3.dot( _v2$2 );

		const denom = ( dot00 * dot11 - dot01 * dot01 );

		// collinear or singular triangle
		if ( denom === 0 ) {

			target.set( 0, 0, 0 );
			return null;

		}

		const invDenom = 1 / denom;
		const u = ( dot11 * dot02 - dot01 * dot12 ) * invDenom;
		const v = ( dot00 * dot12 - dot01 * dot02 ) * invDenom;

		// barycentric coordinates must always sum to 1
		return target.set( 1 - u - v, v, u );

	}

	static containsPoint( point, a, b, c ) {

		// if the triangle is degenerate then we can't contain a point
		if ( this.getBarycoord( point, a, b, c, _v3$2 ) === null ) {

			return false;

		}

		return ( _v3$2.x >= 0 ) && ( _v3$2.y >= 0 ) && ( ( _v3$2.x + _v3$2.y ) <= 1 );

	}

	static getInterpolation( point, p1, p2, p3, v1, v2, v3, target ) {

		if ( this.getBarycoord( point, p1, p2, p3, _v3$2 ) === null ) {

			target.x = 0;
			target.y = 0;
			if ( 'z' in target ) target.z = 0;
			if ( 'w' in target ) target.w = 0;
			return null;

		}

		target.setScalar( 0 );
		target.addScaledVector( v1, _v3$2.x );
		target.addScaledVector( v2, _v3$2.y );
		target.addScaledVector( v3, _v3$2.z );

		return target;

	}

	static getInterpolatedAttribute( attr, i1, i2, i3, barycoord, target ) {

		_v40.setScalar( 0 );
		_v41.setScalar( 0 );
		_v42.setScalar( 0 );

		_v40.fromBufferAttribute( attr, i1 );
		_v41.fromBufferAttribute( attr, i2 );
		_v42.fromBufferAttribute( attr, i3 );

		target.setScalar( 0 );
		target.addScaledVector( _v40, barycoord.x );
		target.addScaledVector( _v41, barycoord.y );
		target.addScaledVector( _v42, barycoord.z );

		return target;

	}

	static isFrontFacing( a, b, c, direction ) {

		_v0$2.subVectors( c, b );
		_v1$3.subVectors( a, b );

		// strictly front facing
		return ( _v0$2.cross( _v1$3 ).dot( direction ) < 0 ) ? true : false;

	}

	set( a, b, c ) {

		this.a.copy( a );
		this.b.copy( b );
		this.c.copy( c );

		return this;

	}

	setFromPointsAndIndices( points, i0, i1, i2 ) {

		this.a.copy( points[ i0 ] );
		this.b.copy( points[ i1 ] );
		this.c.copy( points[ i2 ] );

		return this;

	}

	setFromAttributeAndIndices( attribute, i0, i1, i2 ) {

		this.a.fromBufferAttribute( attribute, i0 );
		this.b.fromBufferAttribute( attribute, i1 );
		this.c.fromBufferAttribute( attribute, i2 );

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( triangle ) {

		this.a.copy( triangle.a );
		this.b.copy( triangle.b );
		this.c.copy( triangle.c );

		return this;

	}

	getArea() {

		_v0$2.subVectors( this.c, this.b );
		_v1$3.subVectors( this.a, this.b );

		return _v0$2.cross( _v1$3 ).length() * 0.5;

	}

	getMidpoint( target ) {

		return target.addVectors( this.a, this.b ).add( this.c ).multiplyScalar( 1 / 3 );

	}

	getNormal( target ) {

		return Triangle.getNormal( this.a, this.b, this.c, target );

	}

	getPlane( target ) {

		return target.setFromCoplanarPoints( this.a, this.b, this.c );

	}

	getBarycoord( point, target ) {

		return Triangle.getBarycoord( point, this.a, this.b, this.c, target );

	}

	getInterpolation( point, v1, v2, v3, target ) {

		return Triangle.getInterpolation( point, this.a, this.b, this.c, v1, v2, v3, target );

	}

	containsPoint( point ) {

		return Triangle.containsPoint( point, this.a, this.b, this.c );

	}

	isFrontFacing( direction ) {

		return Triangle.isFrontFacing( this.a, this.b, this.c, direction );

	}

	intersectsBox( box ) {

		return box.intersectsTriangle( this );

	}

	closestPointToPoint( p, target ) {

		const a = this.a, b = this.b, c = this.c;
		let v, w;

		// algorithm thanks to Real-Time Collision Detection by Christer Ericson,
		// published by Morgan Kaufmann Publishers, (c) 2005 Elsevier Inc.,
		// under the accompanying license; see chapter 5.1.5 for detailed explanation.
		// basically, we're distinguishing which of the voronoi regions of the triangle
		// the point lies in with the minimum amount of redundant computation.

		_vab.subVectors( b, a );
		_vac.subVectors( c, a );
		_vap.subVectors( p, a );
		const d1 = _vab.dot( _vap );
		const d2 = _vac.dot( _vap );
		if ( d1 <= 0 && d2 <= 0 ) {

			// vertex region of A; barycentric coords (1, 0, 0)
			return target.copy( a );

		}

		_vbp.subVectors( p, b );
		const d3 = _vab.dot( _vbp );
		const d4 = _vac.dot( _vbp );
		if ( d3 >= 0 && d4 <= d3 ) {

			// vertex region of B; barycentric coords (0, 1, 0)
			return target.copy( b );

		}

		const vc = d1 * d4 - d3 * d2;
		if ( vc <= 0 && d1 >= 0 && d3 <= 0 ) {

			v = d1 / ( d1 - d3 );
			// edge region of AB; barycentric coords (1-v, v, 0)
			return target.copy( a ).addScaledVector( _vab, v );

		}

		_vcp.subVectors( p, c );
		const d5 = _vab.dot( _vcp );
		const d6 = _vac.dot( _vcp );
		if ( d6 >= 0 && d5 <= d6 ) {

			// vertex region of C; barycentric coords (0, 0, 1)
			return target.copy( c );

		}

		const vb = d5 * d2 - d1 * d6;
		if ( vb <= 0 && d2 >= 0 && d6 <= 0 ) {

			w = d2 / ( d2 - d6 );
			// edge region of AC; barycentric coords (1-w, 0, w)
			return target.copy( a ).addScaledVector( _vac, w );

		}

		const va = d3 * d6 - d5 * d4;
		if ( va <= 0 && ( d4 - d3 ) >= 0 && ( d5 - d6 ) >= 0 ) {

			_vbc.subVectors( c, b );
			w = ( d4 - d3 ) / ( ( d4 - d3 ) + ( d5 - d6 ) );
			// edge region of BC; barycentric coords (0, 1-w, w)
			return target.copy( b ).addScaledVector( _vbc, w ); // edge region of BC

		}

		// face region
		const denom = 1 / ( va + vb + vc );
		// u = va * denom
		v = vb * denom;
		w = vc * denom;

		return target.copy( a ).addScaledVector( _vab, v ).addScaledVector( _vac, w );

	}

	equals( triangle ) {

		return triangle.a.equals( this.a ) && triangle.b.equals( this.b ) && triangle.c.equals( this.c );

	}

}

const _colorKeywords = { 'aliceblue': 0xF0F8FF, 'antiquewhite': 0xFAEBD7, 'aqua': 0x00FFFF, 'aquamarine': 0x7FFFD4, 'azure': 0xF0FFFF,
	'beige': 0xF5F5DC, 'bisque': 0xFFE4C4, 'black': 0x000000, 'blanchedalmond': 0xFFEBCD, 'blue': 0x0000FF, 'blueviolet': 0x8A2BE2,
	'brown': 0xA52A2A, 'burlywood': 0xDEB887, 'cadetblue': 0x5F9EA0, 'chartreuse': 0x7FFF00, 'chocolate': 0xD2691E, 'coral': 0xFF7F50,
	'cornflowerblue': 0x6495ED, 'cornsilk': 0xFFF8DC, 'crimson': 0xDC143C, 'cyan': 0x00FFFF, 'darkblue': 0x00008B, 'darkcyan': 0x008B8B,
	'darkgoldenrod': 0xB8860B, 'darkgray': 0xA9A9A9, 'darkgreen': 0x006400, 'darkgrey': 0xA9A9A9, 'darkkhaki': 0xBDB76B, 'darkmagenta': 0x8B008B,
	'darkolivegreen': 0x556B2F, 'darkorange': 0xFF8C00, 'darkorchid': 0x9932CC, 'darkred': 0x8B0000, 'darksalmon': 0xE9967A, 'darkseagreen': 0x8FBC8F,
	'darkslateblue': 0x483D8B, 'darkslategray': 0x2F4F4F, 'darkslategrey': 0x2F4F4F, 'darkturquoise': 0x00CED1, 'darkviolet': 0x9400D3,
	'deeppink': 0xFF1493, 'deepskyblue': 0x00BFFF, 'dimgray': 0x696969, 'dimgrey': 0x696969, 'dodgerblue': 0x1E90FF, 'firebrick': 0xB22222,
	'floralwhite': 0xFFFAF0, 'forestgreen': 0x228B22, 'fuchsia': 0xFF00FF, 'gainsboro': 0xDCDCDC, 'ghostwhite': 0xF8F8FF, 'gold': 0xFFD700,
	'goldenrod': 0xDAA520, 'gray': 0x808080, 'green': 0x008000, 'greenyellow': 0xADFF2F, 'grey': 0x808080, 'honeydew': 0xF0FFF0, 'hotpink': 0xFF69B4,
	'indianred': 0xCD5C5C, 'indigo': 0x4B0082, 'ivory': 0xFFFFF0, 'khaki': 0xF0E68C, 'lavender': 0xE6E6FA, 'lavenderblush': 0xFFF0F5, 'lawngreen': 0x7CFC00,
	'lemonchiffon': 0xFFFACD, 'lightblue': 0xADD8E6, 'lightcoral': 0xF08080, 'lightcyan': 0xE0FFFF, 'lightgoldenrodyellow': 0xFAFAD2, 'lightgray': 0xD3D3D3,
	'lightgreen': 0x90EE90, 'lightgrey': 0xD3D3D3, 'lightpink': 0xFFB6C1, 'lightsalmon': 0xFFA07A, 'lightseagreen': 0x20B2AA, 'lightskyblue': 0x87CEFA,
	'lightslategray': 0x778899, 'lightslategrey': 0x778899, 'lightsteelblue': 0xB0C4DE, 'lightyellow': 0xFFFFE0, 'lime': 0x00FF00, 'limegreen': 0x32CD32,
	'linen': 0xFAF0E6, 'magenta': 0xFF00FF, 'maroon': 0x800000, 'mediumaquamarine': 0x66CDAA, 'mediumblue': 0x0000CD, 'mediumorchid': 0xBA55D3,
	'mediumpurple': 0x9370DB, 'mediumseagreen': 0x3CB371, 'mediumslateblue': 0x7B68EE, 'mediumspringgreen': 0x00FA9A, 'mediumturquoise': 0x48D1CC,
	'mediumvioletred': 0xC71585, 'midnightblue': 0x191970, 'mintcream': 0xF5FFFA, 'mistyrose': 0xFFE4E1, 'moccasin': 0xFFE4B5, 'navajowhite': 0xFFDEAD,
	'navy': 0x000080, 'oldlace': 0xFDF5E6, 'olive': 0x808000, 'olivedrab': 0x6B8E23, 'orange': 0xFFA500, 'orangered': 0xFF4500, 'orchid': 0xDA70D6,
	'palegoldenrod': 0xEEE8AA, 'palegreen': 0x98FB98, 'paleturquoise': 0xAFEEEE, 'palevioletred': 0xDB7093, 'papayawhip': 0xFFEFD5, 'peachpuff': 0xFFDAB9,
	'peru': 0xCD853F, 'pink': 0xFFC0CB, 'plum': 0xDDA0DD, 'powderblue': 0xB0E0E6, 'purple': 0x800080, 'rebeccapurple': 0x663399, 'red': 0xFF0000, 'rosybrown': 0xBC8F8F,
	'royalblue': 0x4169E1, 'saddlebrown': 0x8B4513, 'salmon': 0xFA8072, 'sandybrown': 0xF4A460, 'seagreen': 0x2E8B57, 'seashell': 0xFFF5EE,
	'sienna': 0xA0522D, 'silver': 0xC0C0C0, 'skyblue': 0x87CEEB, 'slateblue': 0x6A5ACD, 'slategray': 0x708090, 'slategrey': 0x708090, 'snow': 0xFFFAFA,
	'springgreen': 0x00FF7F, 'steelblue': 0x4682B4, 'tan': 0xD2B48C, 'teal': 0x008080, 'thistle': 0xD8BFD8, 'tomato': 0xFF6347, 'turquoise': 0x40E0D0,
	'violet': 0xEE82EE, 'wheat': 0xF5DEB3, 'white': 0xFFFFFF, 'whitesmoke': 0xF5F5F5, 'yellow': 0xFFFF00, 'yellowgreen': 0x9ACD32 };

const _hslA = { h: 0, s: 0, l: 0 };
const _hslB = { h: 0, s: 0, l: 0 };

function hue2rgb( p, q, t ) {

	if ( t < 0 ) t += 1;
	if ( t > 1 ) t -= 1;
	if ( t < 1 / 6 ) return p + ( q - p ) * 6 * t;
	if ( t < 1 / 2 ) return q;
	if ( t < 2 / 3 ) return p + ( q - p ) * 6 * ( 2 / 3 - t );
	return p;

}

class Color {

	constructor( r, g, b ) {

		this.isColor = true;

		this.r = 1;
		this.g = 1;
		this.b = 1;

		return this.set( r, g, b );

	}

	set( r, g, b ) {

		if ( g === undefined && b === undefined ) {

			// r is THREE.Color, hex or string

			const value = r;

			if ( value && value.isColor ) {

				this.copy( value );

			} else if ( typeof value === 'number' ) {

				this.setHex( value );

			} else if ( typeof value === 'string' ) {

				this.setStyle( value );

			}

		} else {

			this.setRGB( r, g, b );

		}

		return this;

	}

	setScalar( scalar ) {

		this.r = scalar;
		this.g = scalar;
		this.b = scalar;

		return this;

	}

	setHex( hex, colorSpace = SRGBColorSpace ) {

		hex = Math.floor( hex );

		this.r = ( hex >> 16 & 255 ) / 255;
		this.g = ( hex >> 8 & 255 ) / 255;
		this.b = ( hex & 255 ) / 255;

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setRGB( r, g, b, colorSpace = ColorManagement.workingColorSpace ) {

		this.r = r;
		this.g = g;
		this.b = b;

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setHSL( h, s, l, colorSpace = ColorManagement.workingColorSpace ) {

		// h,s,l ranges are in 0.0 - 1.0
		h = euclideanModulo( h, 1 );
		s = clamp( s, 0, 1 );
		l = clamp( l, 0, 1 );

		if ( s === 0 ) {

			this.r = this.g = this.b = l;

		} else {

			const p = l <= 0.5 ? l * ( 1 + s ) : l + s - ( l * s );
			const q = ( 2 * l ) - p;

			this.r = hue2rgb( q, p, h + 1 / 3 );
			this.g = hue2rgb( q, p, h );
			this.b = hue2rgb( q, p, h - 1 / 3 );

		}

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setStyle( style, colorSpace = SRGBColorSpace ) {

		function handleAlpha( string ) {

			if ( string === undefined ) return;

			if ( parseFloat( string ) < 1 ) {

				console.warn( 'THREE.Color: Alpha component of ' + style + ' will be ignored.' );

			}

		}


		let m;

		if ( m = /^(\w+)\(([^\)]*)\)/.exec( style ) ) {

			// rgb / hsl

			let color;
			const name = m[ 1 ];
			const components = m[ 2 ];

			switch ( name ) {

				case 'rgb':
				case 'rgba':

					if ( color = /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// rgb(255,0,0) rgba(255,0,0,0.5)

						handleAlpha( color[ 4 ] );

						return this.setRGB(
							Math.min( 255, parseInt( color[ 1 ], 10 ) ) / 255,
							Math.min( 255, parseInt( color[ 2 ], 10 ) ) / 255,
							Math.min( 255, parseInt( color[ 3 ], 10 ) ) / 255,
							colorSpace
						);

					}

					if ( color = /^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// rgb(100%,0%,0%) rgba(100%,0%,0%,0.5)

						handleAlpha( color[ 4 ] );

						return this.setRGB(
							Math.min( 100, parseInt( color[ 1 ], 10 ) ) / 100,
							Math.min( 100, parseInt( color[ 2 ], 10 ) ) / 100,
							Math.min( 100, parseInt( color[ 3 ], 10 ) ) / 100,
							colorSpace
						);

					}

					break;

				case 'hsl':
				case 'hsla':

					if ( color = /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// hsl(120,50%,50%) hsla(120,50%,50%,0.5)

						handleAlpha( color[ 4 ] );

						return this.setHSL(
							parseFloat( color[ 1 ] ) / 360,
							parseFloat( color[ 2 ] ) / 100,
							parseFloat( color[ 3 ] ) / 100,
							colorSpace
						);

					}

					break;

				default:

					console.warn( 'THREE.Color: Unknown color model ' + style );

			}

		} else if ( m = /^\#([A-Fa-f\d]+)$/.exec( style ) ) {

			// hex color

			const hex = m[ 1 ];
			const size = hex.length;

			if ( size === 3 ) {

				// #ff0
				return this.setRGB(
					parseInt( hex.charAt( 0 ), 16 ) / 15,
					parseInt( hex.charAt( 1 ), 16 ) / 15,
					parseInt( hex.charAt( 2 ), 16 ) / 15,
					colorSpace
				);

			} else if ( size === 6 ) {

				// #ff0000
				return this.setHex( parseInt( hex, 16 ), colorSpace );

			} else {

				console.warn( 'THREE.Color: Invalid hex color ' + style );

			}

		} else if ( style && style.length > 0 ) {

			return this.setColorName( style, colorSpace );

		}

		return this;

	}

	setColorName( style, colorSpace = SRGBColorSpace ) {

		// color keywords
		const hex = _colorKeywords[ style.toLowerCase() ];

		if ( hex !== undefined ) {

			// red
			this.setHex( hex, colorSpace );

		} else {

			// unknown color
			console.warn( 'THREE.Color: Unknown color ' + style );

		}

		return this;

	}

	clone() {

		return new this.constructor( this.r, this.g, this.b );

	}

	copy( color ) {

		this.r = color.r;
		this.g = color.g;
		this.b = color.b;

		return this;

	}

	copySRGBToLinear( color ) {

		this.r = SRGBToLinear( color.r );
		this.g = SRGBToLinear( color.g );
		this.b = SRGBToLinear( color.b );

		return this;

	}

	copyLinearToSRGB( color ) {

		this.r = LinearToSRGB( color.r );
		this.g = LinearToSRGB( color.g );
		this.b = LinearToSRGB( color.b );

		return this;

	}

	convertSRGBToLinear() {

		this.copySRGBToLinear( this );

		return this;

	}

	convertLinearToSRGB() {

		this.copyLinearToSRGB( this );

		return this;

	}

	getHex( colorSpace = SRGBColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		return Math.round( clamp( _color.r * 255, 0, 255 ) ) * 65536 + Math.round( clamp( _color.g * 255, 0, 255 ) ) * 256 + Math.round( clamp( _color.b * 255, 0, 255 ) );

	}

	getHexString( colorSpace = SRGBColorSpace ) {

		return ( '000000' + this.getHex( colorSpace ).toString( 16 ) ).slice( - 6 );

	}

	getHSL( target, colorSpace = ColorManagement.workingColorSpace ) {

		// h,s,l ranges are in 0.0 - 1.0

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		const r = _color.r, g = _color.g, b = _color.b;

		const max = Math.max( r, g, b );
		const min = Math.min( r, g, b );

		let hue, saturation;
		const lightness = ( min + max ) / 2.0;

		if ( min === max ) {

			hue = 0;
			saturation = 0;

		} else {

			const delta = max - min;

			saturation = lightness <= 0.5 ? delta / ( max + min ) : delta / ( 2 - max - min );

			switch ( max ) {

				case r: hue = ( g - b ) / delta + ( g < b ? 6 : 0 ); break;
				case g: hue = ( b - r ) / delta + 2; break;
				case b: hue = ( r - g ) / delta + 4; break;

			}

			hue /= 6;

		}

		target.h = hue;
		target.s = saturation;
		target.l = lightness;

		return target;

	}

	getRGB( target, colorSpace = ColorManagement.workingColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		target.r = _color.r;
		target.g = _color.g;
		target.b = _color.b;

		return target;

	}

	getStyle( colorSpace = SRGBColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		const r = _color.r, g = _color.g, b = _color.b;

		if ( colorSpace !== SRGBColorSpace ) {

			// Requires CSS Color Module Level 4 (https://www.w3.org/TR/css-color-4/).
			return `color(${ colorSpace } ${ r.toFixed( 3 ) } ${ g.toFixed( 3 ) } ${ b.toFixed( 3 ) })`;

		}

		return `rgb(${ Math.round( r * 255 ) },${ Math.round( g * 255 ) },${ Math.round( b * 255 ) })`;

	}

	offsetHSL( h, s, l ) {

		this.getHSL( _hslA );

		return this.setHSL( _hslA.h + h, _hslA.s + s, _hslA.l + l );

	}

	add( color ) {

		this.r += color.r;
		this.g += color.g;
		this.b += color.b;

		return this;

	}

	addColors( color1, color2 ) {

		this.r = color1.r + color2.r;
		this.g = color1.g + color2.g;
		this.b = color1.b + color2.b;

		return this;

	}

	addScalar( s ) {

		this.r += s;
		this.g += s;
		this.b += s;

		return this;

	}

	sub( color ) {

		this.r = Math.max( 0, this.r - color.r );
		this.g = Math.max( 0, this.g - color.g );
		this.b = Math.max( 0, this.b - color.b );

		return this;

	}

	multiply( color ) {

		this.r *= color.r;
		this.g *= color.g;
		this.b *= color.b;

		return this;

	}

	multiplyScalar( s ) {

		this.r *= s;
		this.g *= s;
		this.b *= s;

		return this;

	}

	lerp( color, alpha ) {

		this.r += ( color.r - this.r ) * alpha;
		this.g += ( color.g - this.g ) * alpha;
		this.b += ( color.b - this.b ) * alpha;

		return this;

	}

	lerpColors( color1, color2, alpha ) {

		this.r = color1.r + ( color2.r - color1.r ) * alpha;
		this.g = color1.g + ( color2.g - color1.g ) * alpha;
		this.b = color1.b + ( color2.b - color1.b ) * alpha;

		return this;

	}

	lerpHSL( color, alpha ) {

		this.getHSL( _hslA );
		color.getHSL( _hslB );

		const h = lerp( _hslA.h, _hslB.h, alpha );
		const s = lerp( _hslA.s, _hslB.s, alpha );
		const l = lerp( _hslA.l, _hslB.l, alpha );

		this.setHSL( h, s, l );

		return this;

	}

	setFromVector3( v ) {

		this.r = v.x;
		this.g = v.y;
		this.b = v.z;

		return this;

	}

	applyMatrix3( m ) {

		const r = this.r, g = this.g, b = this.b;
		const e = m.elements;

		this.r = e[ 0 ] * r + e[ 3 ] * g + e[ 6 ] * b;
		this.g = e[ 1 ] * r + e[ 4 ] * g + e[ 7 ] * b;
		this.b = e[ 2 ] * r + e[ 5 ] * g + e[ 8 ] * b;

		return this;

	}

	equals( c ) {

		return ( c.r === this.r ) && ( c.g === this.g ) && ( c.b === this.b );

	}

	fromArray( array, offset = 0 ) {

		this.r = array[ offset ];
		this.g = array[ offset + 1 ];
		this.b = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.r;
		array[ offset + 1 ] = this.g;
		array[ offset + 2 ] = this.b;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.r = attribute.getX( index );
		this.g = attribute.getY( index );
		this.b = attribute.getZ( index );

		return this;

	}

	toJSON() {

		return this.getHex();

	}

	*[ Symbol.iterator ]() {

		yield this.r;
		yield this.g;
		yield this.b;

	}

}

const _color = /*@__PURE__*/ new Color();

Color.NAMES = _colorKeywords;

let _materialId = 0;

class Material extends EventDispatcher {

	constructor() {

		super();

		this.isMaterial = true;

		Object.defineProperty( this, 'id', { value: _materialId ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'Material';

		this.blending = NormalBlending;
		this.side = FrontSide;
		this.vertexColors = false;

		this.opacity = 1;
		this.transparent = false;
		this.alphaHash = false;

		this.blendSrc = SrcAlphaFactor;
		this.blendDst = OneMinusSrcAlphaFactor;
		this.blendEquation = AddEquation;
		this.blendSrcAlpha = null;
		this.blendDstAlpha = null;
		this.blendEquationAlpha = null;
		this.blendColor = new Color( 0, 0, 0 );
		this.blendAlpha = 0;

		this.depthFunc = LessEqualDepth;
		this.depthTest = true;
		this.depthWrite = true;

		this.stencilWriteMask = 0xff;
		this.stencilFunc = AlwaysStencilFunc;
		this.stencilRef = 0;
		this.stencilFuncMask = 0xff;
		this.stencilFail = KeepStencilOp;
		this.stencilZFail = KeepStencilOp;
		this.stencilZPass = KeepStencilOp;
		this.stencilWrite = false;

		this.clippingPlanes = null;
		this.clipIntersection = false;
		this.clipShadows = false;

		this.shadowSide = null;

		this.colorWrite = true;

		this.precision = null; // override the renderer's default precision for this material

		this.polygonOffset = false;
		this.polygonOffsetFactor = 0;
		this.polygonOffsetUnits = 0;

		this.dithering = false;

		this.alphaToCoverage = false;
		this.premultipliedAlpha = false;
		this.forceSinglePass = false;

		this.visible = true;

		this.toneMapped = true;

		this.userData = {};

		this.version = 0;

		this._alphaTest = 0;

	}

	get alphaTest() {

		return this._alphaTest;

	}

	set alphaTest( value ) {

		if ( this._alphaTest > 0 !== value > 0 ) {

			this.version ++;

		}

		this._alphaTest = value;

	}

	// onBeforeRender and onBeforeCompile only supported in WebGLRenderer

	onBeforeRender( /* renderer, scene, camera, geometry, object, group */ ) {}

	onBeforeCompile( /* shaderobject, renderer */ ) {}

	customProgramCacheKey() {

		return this.onBeforeCompile.toString();

	}

	setValues( values ) {

		if ( values === undefined ) return;

		for ( const key in values ) {

			const newValue = values[ key ];

			if ( newValue === undefined ) {

				console.warn( `THREE.Material: parameter '${ key }' has value of undefined.` );
				continue;

			}

			const currentValue = this[ key ];

			if ( currentValue === undefined ) {

				console.warn( `THREE.Material: '${ key }' is not a property of THREE.${ this.type }.` );
				continue;

			}

			if ( currentValue && currentValue.isColor ) {

				currentValue.set( newValue );

			} else if ( ( currentValue && currentValue.isVector3 ) && ( newValue && newValue.isVector3 ) ) {

				currentValue.copy( newValue );

			} else {

				this[ key ] = newValue;

			}

		}

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( isRootObject ) {

			meta = {
				textures: {},
				images: {}
			};

		}

		const data = {
			metadata: {
				version: 4.6,
				type: 'Material',
				generator: 'Material.toJSON'
			}
		};

		// standard Material serialization
		data.uuid = this.uuid;
		data.type = this.type;

		if ( this.name !== '' ) data.name = this.name;

		if ( this.color && this.color.isColor ) data.color = this.color.getHex();

		if ( this.roughness !== undefined ) data.roughness = this.roughness;
		if ( this.metalness !== undefined ) data.metalness = this.metalness;

		if ( this.sheen !== undefined ) data.sheen = this.sheen;
		if ( this.sheenColor && this.sheenColor.isColor ) data.sheenColor = this.sheenColor.getHex();
		if ( this.sheenRoughness !== undefined ) data.sheenRoughness = this.sheenRoughness;
		if ( this.emissive && this.emissive.isColor ) data.emissive = this.emissive.getHex();
		if ( this.emissiveIntensity !== undefined && this.emissiveIntensity !== 1 ) data.emissiveIntensity = this.emissiveIntensity;

		if ( this.specular && this.specular.isColor ) data.specular = this.specular.getHex();
		if ( this.specularIntensity !== undefined ) data.specularIntensity = this.specularIntensity;
		if ( this.specularColor && this.specularColor.isColor ) data.specularColor = this.specularColor.getHex();
		if ( this.shininess !== undefined ) data.shininess = this.shininess;
		if ( this.clearcoat !== undefined ) data.clearcoat = this.clearcoat;
		if ( this.clearcoatRoughness !== undefined ) data.clearcoatRoughness = this.clearcoatRoughness;

		if ( this.clearcoatMap && this.clearcoatMap.isTexture ) {

			data.clearcoatMap = this.clearcoatMap.toJSON( meta ).uuid;

		}

		if ( this.clearcoatRoughnessMap && this.clearcoatRoughnessMap.isTexture ) {

			data.clearcoatRoughnessMap = this.clearcoatRoughnessMap.toJSON( meta ).uuid;

		}

		if ( this.clearcoatNormalMap && this.clearcoatNormalMap.isTexture ) {

			data.clearcoatNormalMap = this.clearcoatNormalMap.toJSON( meta ).uuid;
			data.clearcoatNormalScale = this.clearcoatNormalScale.toArray();

		}

		if ( this.dispersion !== undefined ) data.dispersion = this.dispersion;

		if ( this.iridescence !== undefined ) data.iridescence = this.iridescence;
		if ( this.iridescenceIOR !== undefined ) data.iridescenceIOR = this.iridescenceIOR;
		if ( this.iridescenceThicknessRange !== undefined ) data.iridescenceThicknessRange = this.iridescenceThicknessRange;

		if ( this.iridescenceMap && this.iridescenceMap.isTexture ) {

			data.iridescenceMap = this.iridescenceMap.toJSON( meta ).uuid;

		}

		if ( this.iridescenceThicknessMap && this.iridescenceThicknessMap.isTexture ) {

			data.iridescenceThicknessMap = this.iridescenceThicknessMap.toJSON( meta ).uuid;

		}

		if ( this.anisotropy !== undefined ) data.anisotropy = this.anisotropy;
		if ( this.anisotropyRotation !== undefined ) data.anisotropyRotation = this.anisotropyRotation;

		if ( this.anisotropyMap && this.anisotropyMap.isTexture ) {

			data.anisotropyMap = this.anisotropyMap.toJSON( meta ).uuid;

		}

		if ( this.map && this.map.isTexture ) data.map = this.map.toJSON( meta ).uuid;
		if ( this.matcap && this.matcap.isTexture ) data.matcap = this.matcap.toJSON( meta ).uuid;
		if ( this.alphaMap && this.alphaMap.isTexture ) data.alphaMap = this.alphaMap.toJSON( meta ).uuid;

		if ( this.lightMap && this.lightMap.isTexture ) {

			data.lightMap = this.lightMap.toJSON( meta ).uuid;
			data.lightMapIntensity = this.lightMapIntensity;

		}

		if ( this.aoMap && this.aoMap.isTexture ) {

			data.aoMap = this.aoMap.toJSON( meta ).uuid;
			data.aoMapIntensity = this.aoMapIntensity;

		}

		if ( this.bumpMap && this.bumpMap.isTexture ) {

			data.bumpMap = this.bumpMap.toJSON( meta ).uuid;
			data.bumpScale = this.bumpScale;

		}

		if ( this.normalMap && this.normalMap.isTexture ) {

			data.normalMap = this.normalMap.toJSON( meta ).uuid;
			data.normalMapType = this.normalMapType;
			data.normalScale = this.normalScale.toArray();

		}

		if ( this.displacementMap && this.displacementMap.isTexture ) {

			data.displacementMap = this.displacementMap.toJSON( meta ).uuid;
			data.displacementScale = this.displacementScale;
			data.displacementBias = this.displacementBias;

		}

		if ( this.roughnessMap && this.roughnessMap.isTexture ) data.roughnessMap = this.roughnessMap.toJSON( meta ).uuid;
		if ( this.metalnessMap && this.metalnessMap.isTexture ) data.metalnessMap = this.metalnessMap.toJSON( meta ).uuid;

		if ( this.emissiveMap && this.emissiveMap.isTexture ) data.emissiveMap = this.emissiveMap.toJSON( meta ).uuid;
		if ( this.specularMap && this.specularMap.isTexture ) data.specularMap = this.specularMap.toJSON( meta ).uuid;
		if ( this.specularIntensityMap && this.specularIntensityMap.isTexture ) data.specularIntensityMap = this.specularIntensityMap.toJSON( meta ).uuid;
		if ( this.specularColorMap && this.specularColorMap.isTexture ) data.specularColorMap = this.specularColorMap.toJSON( meta ).uuid;

		if ( this.envMap && this.envMap.isTexture ) {

			data.envMap = this.envMap.toJSON( meta ).uuid;

			if ( this.combine !== undefined ) data.combine = this.combine;

		}

		if ( this.envMapRotation !== undefined ) data.envMapRotation = this.envMapRotation.toArray();
		if ( this.envMapIntensity !== undefined ) data.envMapIntensity = this.envMapIntensity;
		if ( this.reflectivity !== undefined ) data.reflectivity = this.reflectivity;
		if ( this.refractionRatio !== undefined ) data.refractionRatio = this.refractionRatio;

		if ( this.gradientMap && this.gradientMap.isTexture ) {

			data.gradientMap = this.gradientMap.toJSON( meta ).uuid;

		}

		if ( this.transmission !== undefined ) data.transmission = this.transmission;
		if ( this.transmissionMap && this.transmissionMap.isTexture ) data.transmissionMap = this.transmissionMap.toJSON( meta ).uuid;
		if ( this.thickness !== undefined ) data.thickness = this.thickness;
		if ( this.thicknessMap && this.thicknessMap.isTexture ) data.thicknessMap = this.thicknessMap.toJSON( meta ).uuid;
		if ( this.attenuationDistance !== undefined && this.attenuationDistance !== Infinity ) data.attenuationDistance = this.attenuationDistance;
		if ( this.attenuationColor !== undefined ) data.attenuationColor = this.attenuationColor.getHex();

		if ( this.size !== undefined ) data.size = this.size;
		if ( this.shadowSide !== null ) data.shadowSide = this.shadowSide;
		if ( this.sizeAttenuation !== undefined ) data.sizeAttenuation = this.sizeAttenuation;

		if ( this.blending !== NormalBlending ) data.blending = this.blending;
		if ( this.side !== FrontSide ) data.side = this.side;
		if ( this.vertexColors === true ) data.vertexColors = true;

		if ( this.opacity < 1 ) data.opacity = this.opacity;
		if ( this.transparent === true ) data.transparent = true;

		if ( this.blendSrc !== SrcAlphaFactor ) data.blendSrc = this.blendSrc;
		if ( this.blendDst !== OneMinusSrcAlphaFactor ) data.blendDst = this.blendDst;
		if ( this.blendEquation !== AddEquation ) data.blendEquation = this.blendEquation;
		if ( this.blendSrcAlpha !== null ) data.blendSrcAlpha = this.blendSrcAlpha;
		if ( this.blendDstAlpha !== null ) data.blendDstAlpha = this.blendDstAlpha;
		if ( this.blendEquationAlpha !== null ) data.blendEquationAlpha = this.blendEquationAlpha;
		if ( this.blendColor && this.blendColor.isColor ) data.blendColor = this.blendColor.getHex();
		if ( this.blendAlpha !== 0 ) data.blendAlpha = this.blendAlpha;

		if ( this.depthFunc !== LessEqualDepth ) data.depthFunc = this.depthFunc;
		if ( this.depthTest === false ) data.depthTest = this.depthTest;
		if ( this.depthWrite === false ) data.depthWrite = this.depthWrite;
		if ( this.colorWrite === false ) data.colorWrite = this.colorWrite;

		if ( this.stencilWriteMask !== 0xff ) data.stencilWriteMask = this.stencilWriteMask;
		if ( this.stencilFunc !== AlwaysStencilFunc ) data.stencilFunc = this.stencilFunc;
		if ( this.stencilRef !== 0 ) data.stencilRef = this.stencilRef;
		if ( this.stencilFuncMask !== 0xff ) data.stencilFuncMask = this.stencilFuncMask;
		if ( this.stencilFail !== KeepStencilOp ) data.stencilFail = this.stencilFail;
		if ( this.stencilZFail !== KeepStencilOp ) data.stencilZFail = this.stencilZFail;
		if ( this.stencilZPass !== KeepStencilOp ) data.stencilZPass = this.stencilZPass;
		if ( this.stencilWrite === true ) data.stencilWrite = this.stencilWrite;

		// rotation (SpriteMaterial)
		if ( this.rotation !== undefined && this.rotation !== 0 ) data.rotation = this.rotation;

		if ( this.polygonOffset === true ) data.polygonOffset = true;
		if ( this.polygonOffsetFactor !== 0 ) data.polygonOffsetFactor = this.polygonOffsetFactor;
		if ( this.polygonOffsetUnits !== 0 ) data.polygonOffsetUnits = this.polygonOffsetUnits;

		if ( this.linewidth !== undefined && this.linewidth !== 1 ) data.linewidth = this.linewidth;
		if ( this.dashSize !== undefined ) data.dashSize = this.dashSize;
		if ( this.gapSize !== undefined ) data.gapSize = this.gapSize;
		if ( this.scale !== undefined ) data.scale = this.scale;

		if ( this.dithering === true ) data.dithering = true;

		if ( this.alphaTest > 0 ) data.alphaTest = this.alphaTest;
		if ( this.alphaHash === true ) data.alphaHash = true;
		if ( this.alphaToCoverage === true ) data.alphaToCoverage = true;
		if ( this.premultipliedAlpha === true ) data.premultipliedAlpha = true;
		if ( this.forceSinglePass === true ) data.forceSinglePass = true;

		if ( this.wireframe === true ) data.wireframe = true;
		if ( this.wireframeLinewidth > 1 ) data.wireframeLinewidth = this.wireframeLinewidth;
		if ( this.wireframeLinecap !== 'round' ) data.wireframeLinecap = this.wireframeLinecap;
		if ( this.wireframeLinejoin !== 'round' ) data.wireframeLinejoin = this.wireframeLinejoin;

		if ( this.flatShading === true ) data.flatShading = true;

		if ( this.visible === false ) data.visible = false;

		if ( this.toneMapped === false ) data.toneMapped = false;

		if ( this.fog === false ) data.fog = false;

		if ( Object.keys( this.userData ).length > 0 ) data.userData = this.userData;

		// TODO: Copied from Object3D.toJSON

		function extractFromCache( cache ) {

			const values = [];

			for ( const key in cache ) {

				const data = cache[ key ];
				delete data.metadata;
				values.push( data );

			}

			return values;

		}

		if ( isRootObject ) {

			const textures = extractFromCache( meta.textures );
			const images = extractFromCache( meta.images );

			if ( textures.length > 0 ) data.textures = textures;
			if ( images.length > 0 ) data.images = images;

		}

		return data;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.name = source.name;

		this.blending = source.blending;
		this.side = source.side;
		this.vertexColors = source.vertexColors;

		this.opacity = source.opacity;
		this.transparent = source.transparent;

		this.blendSrc = source.blendSrc;
		this.blendDst = source.blendDst;
		this.blendEquation = source.blendEquation;
		this.blendSrcAlpha = source.blendSrcAlpha;
		this.blendDstAlpha = source.blendDstAlpha;
		this.blendEquationAlpha = source.blendEquationAlpha;
		this.blendColor.copy( source.blendColor );
		this.blendAlpha = source.blendAlpha;

		this.depthFunc = source.depthFunc;
		this.depthTest = source.depthTest;
		this.depthWrite = source.depthWrite;

		this.stencilWriteMask = source.stencilWriteMask;
		this.stencilFunc = source.stencilFunc;
		this.stencilRef = source.stencilRef;
		this.stencilFuncMask = source.stencilFuncMask;
		this.stencilFail = source.stencilFail;
		this.stencilZFail = source.stencilZFail;
		this.stencilZPass = source.stencilZPass;
		this.stencilWrite = source.stencilWrite;

		const srcPlanes = source.clippingPlanes;
		let dstPlanes = null;

		if ( srcPlanes !== null ) {

			const n = srcPlanes.length;
			dstPlanes = new Array( n );

			for ( let i = 0; i !== n; ++ i ) {

				dstPlanes[ i ] = srcPlanes[ i ].clone();

			}

		}

		this.clippingPlanes = dstPlanes;
		this.clipIntersection = source.clipIntersection;
		this.clipShadows = source.clipShadows;

		this.shadowSide = source.shadowSide;

		this.colorWrite = source.colorWrite;

		this.precision = source.precision;

		this.polygonOffset = source.polygonOffset;
		this.polygonOffsetFactor = source.polygonOffsetFactor;
		this.polygonOffsetUnits = source.polygonOffsetUnits;

		this.dithering = source.dithering;

		this.alphaTest = source.alphaTest;
		this.alphaHash = source.alphaHash;
		this.alphaToCoverage = source.alphaToCoverage;
		this.premultipliedAlpha = source.premultipliedAlpha;
		this.forceSinglePass = source.forceSinglePass;

		this.visible = source.visible;

		this.toneMapped = source.toneMapped;

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	onBuild( /* shaderobject, renderer */ ) {

		console.warn( 'Material: onBuild() has been removed.' ); // @deprecated, r166

	}

}

class MeshBasicMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshBasicMaterial = true;

		this.type = 'MeshBasicMaterial';

		this.color = new Color( 0xffffff ); // emissive

		this.map = null;

		this.lightMap = null;
		this.lightMapIntensity = 1.0;

		this.aoMap = null;
		this.aoMapIntensity = 1.0;

		this.specularMap = null;

		this.alphaMap = null;

		this.envMap = null;
		this.envMapRotation = new Euler();
		this.combine = MultiplyOperation;
		this.reflectivity = 1;
		this.refractionRatio = 0.98;

		this.wireframe = false;
		this.wireframeLinewidth = 1;
		this.wireframeLinecap = 'round';
		this.wireframeLinejoin = 'round';

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.lightMap = source.lightMap;
		this.lightMapIntensity = source.lightMapIntensity;

		this.aoMap = source.aoMap;
		this.aoMapIntensity = source.aoMapIntensity;

		this.specularMap = source.specularMap;

		this.alphaMap = source.alphaMap;

		this.envMap = source.envMap;
		this.envMapRotation.copy( source.envMapRotation );
		this.combine = source.combine;
		this.reflectivity = source.reflectivity;
		this.refractionRatio = source.refractionRatio;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;
		this.wireframeLinecap = source.wireframeLinecap;
		this.wireframeLinejoin = source.wireframeLinejoin;

		this.fog = source.fog;

		return this;

	}

}

// Fast Half Float Conversions, http://www.fox-toolkit.org/ftp/fasthalffloatconversion.pdf

const _tables = /*@__PURE__*/ _generateTables();

function _generateTables() {

	// float32 to float16 helpers

	const buffer = new ArrayBuffer( 4 );
	const floatView = new Float32Array( buffer );
	const uint32View = new Uint32Array( buffer );

	const baseTable = new Uint32Array( 512 );
	const shiftTable = new Uint32Array( 512 );

	for ( let i = 0; i < 256; ++ i ) {

		const e = i - 127;

		// very small number (0, -0)

		if ( e < - 27 ) {

			baseTable[ i ] = 0x0000;
			baseTable[ i | 0x100 ] = 0x8000;
			shiftTable[ i ] = 24;
			shiftTable[ i | 0x100 ] = 24;

			// small number (denorm)

		} else if ( e < - 14 ) {

			baseTable[ i ] = 0x0400 >> ( - e - 14 );
			baseTable[ i | 0x100 ] = ( 0x0400 >> ( - e - 14 ) ) | 0x8000;
			shiftTable[ i ] = - e - 1;
			shiftTable[ i | 0x100 ] = - e - 1;

			// normal number

		} else if ( e <= 15 ) {

			baseTable[ i ] = ( e + 15 ) << 10;
			baseTable[ i | 0x100 ] = ( ( e + 15 ) << 10 ) | 0x8000;
			shiftTable[ i ] = 13;
			shiftTable[ i | 0x100 ] = 13;

			// large number (Infinity, -Infinity)

		} else if ( e < 128 ) {

			baseTable[ i ] = 0x7c00;
			baseTable[ i | 0x100 ] = 0xfc00;
			shiftTable[ i ] = 24;
			shiftTable[ i | 0x100 ] = 24;

			// stay (NaN, Infinity, -Infinity)

		} else {

			baseTable[ i ] = 0x7c00;
			baseTable[ i | 0x100 ] = 0xfc00;
			shiftTable[ i ] = 13;
			shiftTable[ i | 0x100 ] = 13;

		}

	}

	// float16 to float32 helpers

	const mantissaTable = new Uint32Array( 2048 );
	const exponentTable = new Uint32Array( 64 );
	const offsetTable = new Uint32Array( 64 );

	for ( let i = 1; i < 1024; ++ i ) {

		let m = i << 13; // zero pad mantissa bits
		let e = 0; // zero exponent

		// normalized
		while ( ( m & 0x00800000 ) === 0 ) {

			m <<= 1;
			e -= 0x00800000; // decrement exponent

		}

		m &= ~ 0x00800000; // clear leading 1 bit
		e += 0x38800000; // adjust bias

		mantissaTable[ i ] = m | e;

	}

	for ( let i = 1024; i < 2048; ++ i ) {

		mantissaTable[ i ] = 0x38000000 + ( ( i - 1024 ) << 13 );

	}

	for ( let i = 1; i < 31; ++ i ) {

		exponentTable[ i ] = i << 23;

	}

	exponentTable[ 31 ] = 0x47800000;
	exponentTable[ 32 ] = 0x80000000;

	for ( let i = 33; i < 63; ++ i ) {

		exponentTable[ i ] = 0x80000000 + ( ( i - 32 ) << 23 );

	}

	exponentTable[ 63 ] = 0xc7800000;

	for ( let i = 1; i < 64; ++ i ) {

		if ( i !== 32 ) {

			offsetTable[ i ] = 1024;

		}

	}

	return {
		floatView: floatView,
		uint32View: uint32View,
		baseTable: baseTable,
		shiftTable: shiftTable,
		mantissaTable: mantissaTable,
		exponentTable: exponentTable,
		offsetTable: offsetTable
	};

}

// float32 to float16

function toHalfFloat( val ) {

	if ( Math.abs( val ) > 65504 ) console.warn( 'THREE.DataUtils.toHalfFloat(): Value out of range.' );

	val = clamp( val, - 65504, 65504 );

	_tables.floatView[ 0 ] = val;
	const f = _tables.uint32View[ 0 ];
	const e = ( f >> 23 ) & 0x1ff;
	return _tables.baseTable[ e ] + ( ( f & 0x007fffff ) >> _tables.shiftTable[ e ] );

}

// float16 to float32

function fromHalfFloat( val ) {

	const m = val >> 10;
	_tables.uint32View[ 0 ] = _tables.mantissaTable[ _tables.offsetTable[ m ] + ( val & 0x3ff ) ] + _tables.exponentTable[ m ];
	return _tables.floatView[ 0 ];

}

const DataUtils = {
	toHalfFloat: toHalfFloat,
	fromHalfFloat: fromHalfFloat,
};

const _vector$9 = /*@__PURE__*/ new Vector3();
const _vector2$1 = /*@__PURE__*/ new Vector2();

class BufferAttribute {

	constructor( array, itemSize, normalized = false ) {

		if ( Array.isArray( array ) ) {

			throw new TypeError( 'THREE.BufferAttribute: array should be a Typed Array.' );

		}

		this.isBufferAttribute = true;

		this.name = '';

		this.array = array;
		this.itemSize = itemSize;
		this.count = array !== undefined ? array.length / itemSize : 0;
		this.normalized = normalized;

		this.usage = StaticDrawUsage;
		this.updateRanges = [];
		this.gpuType = FloatType;

		this.version = 0;

	}

	onUploadCallback() {}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	setUsage( value ) {

		this.usage = value;

		return this;

	}

	addUpdateRange( start, count ) {

		this.updateRanges.push( { start, count } );

	}

	clearUpdateRanges() {

		this.updateRanges.length = 0;

	}

	copy( source ) {

		this.name = source.name;
		this.array = new source.array.constructor( source.array );
		this.itemSize = source.itemSize;
		this.count = source.count;
		this.normalized = source.normalized;

		this.usage = source.usage;
		this.gpuType = source.gpuType;

		return this;

	}

	copyAt( index1, attribute, index2 ) {

		index1 *= this.itemSize;
		index2 *= attribute.itemSize;

		for ( let i = 0, l = this.itemSize; i < l; i ++ ) {

			this.array[ index1 + i ] = attribute.array[ index2 + i ];

		}

		return this;

	}

	copyArray( array ) {

		this.array.set( array );

		return this;

	}

	applyMatrix3( m ) {

		if ( this.itemSize === 2 ) {

			for ( let i = 0, l = this.count; i < l; i ++ ) {

				_vector2$1.fromBufferAttribute( this, i );
				_vector2$1.applyMatrix3( m );

				this.setXY( i, _vector2$1.x, _vector2$1.y );

			}

		} else if ( this.itemSize === 3 ) {

			for ( let i = 0, l = this.count; i < l; i ++ ) {

				_vector$9.fromBufferAttribute( this, i );
				_vector$9.applyMatrix3( m );

				this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

			}

		}

		return this;

	}

	applyMatrix4( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.applyMatrix4( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	applyNormalMatrix( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.applyNormalMatrix( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	transformDirection( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.transformDirection( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	set( value, offset = 0 ) {

		// Matching BufferAttribute constructor, do not normalize the array.
		this.array.set( value, offset );

		return this;

	}

	getComponent( index, component ) {

		let value = this.array[ index * this.itemSize + component ];

		if ( this.normalized ) value = denormalize( value, this.array );

		return value;

	}

	setComponent( index, component, value ) {

		if ( this.normalized ) value = normalize( value, this.array );

		this.array[ index * this.itemSize + component ] = value;

		return this;

	}

	getX( index ) {

		let x = this.array[ index * this.itemSize ];

		if ( this.normalized ) x = denormalize( x, this.array );

		return x;

	}

	setX( index, x ) {

		if ( this.normalized ) x = normalize( x, this.array );

		this.array[ index * this.itemSize ] = x;

		return this;

	}

	getY( index ) {

		let y = this.array[ index * this.itemSize + 1 ];

		if ( this.normalized ) y = denormalize( y, this.array );

		return y;

	}

	setY( index, y ) {

		if ( this.normalized ) y = normalize( y, this.array );

		this.array[ index * this.itemSize + 1 ] = y;

		return this;

	}

	getZ( index ) {

		let z = this.array[ index * this.itemSize + 2 ];

		if ( this.normalized ) z = denormalize( z, this.array );

		return z;

	}

	setZ( index, z ) {

		if ( this.normalized ) z = normalize( z, this.array );

		this.array[ index * this.itemSize + 2 ] = z;

		return this;

	}

	getW( index ) {

		let w = this.array[ index * this.itemSize + 3 ];

		if ( this.normalized ) w = denormalize( w, this.array );

		return w;

	}

	setW( index, w ) {

		if ( this.normalized ) w = normalize( w, this.array );

		this.array[ index * this.itemSize + 3 ] = w;

		return this;

	}

	setXY( index, x, y ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;

		return this;

	}

	setXYZ( index, x, y, z ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;
		this.array[ index + 2 ] = z;

		return this;

	}

	setXYZW( index, x, y, z, w ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );
			w = normalize( w, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;
		this.array[ index + 2 ] = z;
		this.array[ index + 3 ] = w;

		return this;

	}

	onUpload( callback ) {

		this.onUploadCallback = callback;

		return this;

	}

	clone() {

		return new this.constructor( this.array, this.itemSize ).copy( this );

	}

	toJSON() {

		const data = {
			itemSize: this.itemSize,
			type: this.array.constructor.name,
			array: Array.from( this.array ),
			normalized: this.normalized
		};

		if ( this.name !== '' ) data.name = this.name;
		if ( this.usage !== StaticDrawUsage ) data.usage = this.usage;

		return data;

	}

}

//

class Int8BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Int8Array( array ), itemSize, normalized );

	}

}

class Uint8BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint8Array( array ), itemSize, normalized );

	}

}

class Uint8ClampedBufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint8ClampedArray( array ), itemSize, normalized );

	}

}

class Int16BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Int16Array( array ), itemSize, normalized );

	}

}

class Uint16BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint16Array( array ), itemSize, normalized );

	}

}

class Int32BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Int32Array( array ), itemSize, normalized );

	}

}

class Uint32BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint32Array( array ), itemSize, normalized );

	}

}

class Float16BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint16Array( array ), itemSize, normalized );

		this.isFloat16BufferAttribute = true;

	}

	getX( index ) {

		let x = fromHalfFloat( this.array[ index * this.itemSize ] );

		if ( this.normalized ) x = denormalize( x, this.array );

		return x;

	}

	setX( index, x ) {

		if ( this.normalized ) x = normalize( x, this.array );

		this.array[ index * this.itemSize ] = toHalfFloat( x );

		return this;

	}

	getY( index ) {

		let y = fromHalfFloat( this.array[ index * this.itemSize + 1 ] );

		if ( this.normalized ) y = denormalize( y, this.array );

		return y;

	}

	setY( index, y ) {

		if ( this.normalized ) y = normalize( y, this.array );

		this.array[ index * this.itemSize + 1 ] = toHalfFloat( y );

		return this;

	}

	getZ( index ) {

		let z = fromHalfFloat( this.array[ index * this.itemSize + 2 ] );

		if ( this.normalized ) z = denormalize( z, this.array );

		return z;

	}

	setZ( index, z ) {

		if ( this.normalized ) z = normalize( z, this.array );

		this.array[ index * this.itemSize + 2 ] = toHalfFloat( z );

		return this;

	}

	getW( index ) {

		let w = fromHalfFloat( this.array[ index * this.itemSize + 3 ] );

		if ( this.normalized ) w = denormalize( w, this.array );

		return w;

	}

	setW( index, w ) {

		if ( this.normalized ) w = normalize( w, this.array );

		this.array[ index * this.itemSize + 3 ] = toHalfFloat( w );

		return this;

	}

	setXY( index, x, y ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );

		}

		this.array[ index + 0 ] = toHalfFloat( x );
		this.array[ index + 1 ] = toHalfFloat( y );

		return this;

	}

	setXYZ( index, x, y, z ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );

		}

		this.array[ index + 0 ] = toHalfFloat( x );
		this.array[ index + 1 ] = toHalfFloat( y );
		this.array[ index + 2 ] = toHalfFloat( z );

		return this;

	}

	setXYZW( index, x, y, z, w ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );
			w = normalize( w, this.array );

		}

		this.array[ index + 0 ] = toHalfFloat( x );
		this.array[ index + 1 ] = toHalfFloat( y );
		this.array[ index + 2 ] = toHalfFloat( z );
		this.array[ index + 3 ] = toHalfFloat( w );

		return this;

	}

}


class Float32BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Float32Array( array ), itemSize, normalized );

	}

}

let _id$2 = 0;

const _m1$2 = /*@__PURE__*/ new Matrix4();
const _obj = /*@__PURE__*/ new Object3D();
const _offset = /*@__PURE__*/ new Vector3();
const _box$2 = /*@__PURE__*/ new Box3();
const _boxMorphTargets = /*@__PURE__*/ new Box3();
const _vector$8 = /*@__PURE__*/ new Vector3();

class BufferGeometry extends EventDispatcher {

	constructor() {

		super();

		this.isBufferGeometry = true;

		Object.defineProperty( this, 'id', { value: _id$2 ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'BufferGeometry';

		this.index = null;
		this.attributes = {};

		this.morphAttributes = {};
		this.morphTargetsRelative = false;

		this.groups = [];

		this.boundingBox = null;
		this.boundingSphere = null;

		this.drawRange = { start: 0, count: Infinity };

		this.userData = {};

	}

	getIndex() {

		return this.index;

	}

	setIndex( index ) {

		if ( Array.isArray( index ) ) {

			this.index = new ( arrayNeedsUint32( index ) ? Uint32BufferAttribute : Uint16BufferAttribute )( index, 1 );

		} else {

			this.index = index;

		}

		return this;

	}

	getAttribute( name ) {

		return this.attributes[ name ];

	}

	setAttribute( name, attribute ) {

		this.attributes[ name ] = attribute;

		return this;

	}

	deleteAttribute( name ) {

		delete this.attributes[ name ];

		return this;

	}

	hasAttribute( name ) {

		return this.attributes[ name ] !== undefined;

	}

	addGroup( start, count, materialIndex = 0 ) {

		this.groups.push( {

			start: start,
			count: count,
			materialIndex: materialIndex

		} );

	}

	clearGroups() {

		this.groups = [];

	}

	setDrawRange( start, count ) {

		this.drawRange.start = start;
		this.drawRange.count = count;

	}

	applyMatrix4( matrix ) {

		const position = this.attributes.position;

		if ( position !== undefined ) {

			position.applyMatrix4( matrix );

			position.needsUpdate = true;

		}

		const normal = this.attributes.normal;

		if ( normal !== undefined ) {

			const normalMatrix = new Matrix3().getNormalMatrix( matrix );

			normal.applyNormalMatrix( normalMatrix );

			normal.needsUpdate = true;

		}

		const tangent = this.attributes.tangent;

		if ( tangent !== undefined ) {

			tangent.transformDirection( matrix );

			tangent.needsUpdate = true;

		}

		if ( this.boundingBox !== null ) {

			this.computeBoundingBox();

		}

		if ( this.boundingSphere !== null ) {

			this.computeBoundingSphere();

		}

		return this;

	}

	applyQuaternion( q ) {

		_m1$2.makeRotationFromQuaternion( q );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateX( angle ) {

		// rotate geometry around world x-axis

		_m1$2.makeRotationX( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateY( angle ) {

		// rotate geometry around world y-axis

		_m1$2.makeRotationY( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateZ( angle ) {

		// rotate geometry around world z-axis

		_m1$2.makeRotationZ( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	translate( x, y, z ) {

		// translate geometry

		_m1$2.makeTranslation( x, y, z );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	scale( x, y, z ) {

		// scale geometry

		_m1$2.makeScale( x, y, z );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	lookAt( vector ) {

		_obj.lookAt( vector );

		_obj.updateMatrix();

		this.applyMatrix4( _obj.matrix );

		return this;

	}

	center() {

		this.computeBoundingBox();

		this.boundingBox.getCenter( _offset ).negate();

		this.translate( _offset.x, _offset.y, _offset.z );

		return this;

	}

	setFromPoints( points ) {

		const position = [];

		for ( let i = 0, l = points.length; i < l; i ++ ) {

			const point = points[ i ];
			position.push( point.x, point.y, point.z || 0 );

		}

		this.setAttribute( 'position', new Float32BufferAttribute( position, 3 ) );

		return this;

	}

	computeBoundingBox() {

		if ( this.boundingBox === null ) {

			this.boundingBox = new Box3();

		}

		const position = this.attributes.position;
		const morphAttributesPosition = this.morphAttributes.position;

		if ( position && position.isGLBufferAttribute ) {

			console.error( 'THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.', this );

			this.boundingBox.set(
				new Vector3( - Infinity, - Infinity, - Infinity ),
				new Vector3( + Infinity, + Infinity, + Infinity )
			);

			return;

		}

		if ( position !== undefined ) {

			this.boundingBox.setFromBufferAttribute( position );

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					_box$2.setFromBufferAttribute( morphAttribute );

					if ( this.morphTargetsRelative ) {

						_vector$8.addVectors( this.boundingBox.min, _box$2.min );
						this.boundingBox.expandByPoint( _vector$8 );

						_vector$8.addVectors( this.boundingBox.max, _box$2.max );
						this.boundingBox.expandByPoint( _vector$8 );

					} else {

						this.boundingBox.expandByPoint( _box$2.min );
						this.boundingBox.expandByPoint( _box$2.max );

					}

				}

			}

		} else {

			this.boundingBox.makeEmpty();

		}

		if ( isNaN( this.boundingBox.min.x ) || isNaN( this.boundingBox.min.y ) || isNaN( this.boundingBox.min.z ) ) {

			console.error( 'THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.', this );

		}

	}

	computeBoundingSphere() {

		if ( this.boundingSphere === null ) {

			this.boundingSphere = new Sphere();

		}

		const position = this.attributes.position;
		const morphAttributesPosition = this.morphAttributes.position;

		if ( position && position.isGLBufferAttribute ) {

			console.error( 'THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.', this );

			this.boundingSphere.set( new Vector3(), Infinity );

			return;

		}

		if ( position ) {

			// first, find the center of the bounding sphere

			const center = this.boundingSphere.center;

			_box$2.setFromBufferAttribute( position );

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					_boxMorphTargets.setFromBufferAttribute( morphAttribute );

					if ( this.morphTargetsRelative ) {

						_vector$8.addVectors( _box$2.min, _boxMorphTargets.min );
						_box$2.expandByPoint( _vector$8 );

						_vector$8.addVectors( _box$2.max, _boxMorphTargets.max );
						_box$2.expandByPoint( _vector$8 );

					} else {

						_box$2.expandByPoint( _boxMorphTargets.min );
						_box$2.expandByPoint( _boxMorphTargets.max );

					}

				}

			}

			_box$2.getCenter( center );

			// second, try to find a boundingSphere with a radius smaller than the
			// boundingSphere of the boundingBox: sqrt(3) smaller in the best case

			let maxRadiusSq = 0;

			for ( let i = 0, il = position.count; i < il; i ++ ) {

				_vector$8.fromBufferAttribute( position, i );

				maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( _vector$8 ) );

			}

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					const morphTargetsRelative = this.morphTargetsRelative;

					for ( let j = 0, jl = morphAttribute.count; j < jl; j ++ ) {

						_vector$8.fromBufferAttribute( morphAttribute, j );

						if ( morphTargetsRelative ) {

							_offset.fromBufferAttribute( position, j );
							_vector$8.add( _offset );

						}

						maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( _vector$8 ) );

					}

				}

			}

			this.boundingSphere.radius = Math.sqrt( maxRadiusSq );

			if ( isNaN( this.boundingSphere.radius ) ) {

				console.error( 'THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.', this );

			}

		}

	}

	computeTangents() {

		const index = this.index;
		const attributes = this.attributes;

		// based on http://www.terathon.com/code/tangent.html
		// (per vertex tangents)

		if ( index === null ||
			 attributes.position === undefined ||
			 attributes.normal === undefined ||
			 attributes.uv === undefined ) {

			console.error( 'THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)' );
			return;

		}

		const positionAttribute = attributes.position;
		const normalAttribute = attributes.normal;
		const uvAttribute = attributes.uv;

		if ( this.hasAttribute( 'tangent' ) === false ) {

			this.setAttribute( 'tangent', new BufferAttribute( new Float32Array( 4 * positionAttribute.count ), 4 ) );

		}

		const tangentAttribute = this.getAttribute( 'tangent' );

		const tan1 = [], tan2 = [];

		for ( let i = 0; i < positionAttribute.count; i ++ ) {

			tan1[ i ] = new Vector3();
			tan2[ i ] = new Vector3();

		}

		const vA = new Vector3(),
			vB = new Vector3(),
			vC = new Vector3(),

			uvA = new Vector2(),
			uvB = new Vector2(),
			uvC = new Vector2(),

			sdir = new Vector3(),
			tdir = new Vector3();

		function handleTriangle( a, b, c ) {

			vA.fromBufferAttribute( positionAttribute, a );
			vB.fromBufferAttribute( positionAttribute, b );
			vC.fromBufferAttribute( positionAttribute, c );

			uvA.fromBufferAttribute( uvAttribute, a );
			uvB.fromBufferAttribute( uvAttribute, b );
			uvC.fromBufferAttribute( uvAttribute, c );

			vB.sub( vA );
			vC.sub( vA );

			uvB.sub( uvA );
			uvC.sub( uvA );

			const r = 1.0 / ( uvB.x * uvC.y - uvC.x * uvB.y );

			// silently ignore degenerate uv triangles having coincident or colinear vertices

			if ( ! isFinite( r ) ) return;

			sdir.copy( vB ).multiplyScalar( uvC.y ).addScaledVector( vC, - uvB.y ).multiplyScalar( r );
			tdir.copy( vC ).multiplyScalar( uvB.x ).addScaledVector( vB, - uvC.x ).multiplyScalar( r );

			tan1[ a ].add( sdir );
			tan1[ b ].add( sdir );
			tan1[ c ].add( sdir );

			tan2[ a ].add( tdir );
			tan2[ b ].add( tdir );
			tan2[ c ].add( tdir );

		}

		let groups = this.groups;

		if ( groups.length === 0 ) {

			groups = [ {
				start: 0,
				count: index.count
			} ];

		}

		for ( let i = 0, il = groups.length; i < il; ++ i ) {

			const group = groups[ i ];

			const start = group.start;
			const count = group.count;

			for ( let j = start, jl = start + count; j < jl; j += 3 ) {

				handleTriangle(
					index.getX( j + 0 ),
					index.getX( j + 1 ),
					index.getX( j + 2 )
				);

			}

		}

		const tmp = new Vector3(), tmp2 = new Vector3();
		const n = new Vector3(), n2 = new Vector3();

		function handleVertex( v ) {

			n.fromBufferAttribute( normalAttribute, v );
			n2.copy( n );

			const t = tan1[ v ];

			// Gram-Schmidt orthogonalize

			tmp.copy( t );
			tmp.sub( n.multiplyScalar( n.dot( t ) ) ).normalize();

			// Calculate handedness

			tmp2.crossVectors( n2, t );
			const test = tmp2.dot( tan2[ v ] );
			const w = ( test < 0.0 ) ? - 1.0 : 1.0;

			tangentAttribute.setXYZW( v, tmp.x, tmp.y, tmp.z, w );

		}

		for ( let i = 0, il = groups.length; i < il; ++ i ) {

			const group = groups[ i ];

			const start = group.start;
			const count = group.count;

			for ( let j = start, jl = start + count; j < jl; j += 3 ) {

				handleVertex( index.getX( j + 0 ) );
				handleVertex( index.getX( j + 1 ) );
				handleVertex( index.getX( j + 2 ) );

			}

		}

	}

	computeVertexNormals() {

		const index = this.index;
		const positionAttribute = this.getAttribute( 'position' );

		if ( positionAttribute !== undefined ) {

			let normalAttribute = this.getAttribute( 'normal' );

			if ( normalAttribute === undefined ) {

				normalAttribute = new BufferAttribute( new Float32Array( positionAttribute.count * 3 ), 3 );
				this.setAttribute( 'normal', normalAttribute );

			} else {

				// reset existing normals to zero

				for ( let i = 0, il = normalAttribute.count; i < il; i ++ ) {

					normalAttribute.setXYZ( i, 0, 0, 0 );

				}

			}

			const pA = new Vector3(), pB = new Vector3(), pC = new Vector3();
			const nA = new Vector3(), nB = new Vector3(), nC = new Vector3();
			const cb = new Vector3(), ab = new Vector3();

			// indexed elements

			if ( index ) {

				for ( let i = 0, il = index.count; i < il; i += 3 ) {

					const vA = index.getX( i + 0 );
					const vB = index.getX( i + 1 );
					const vC = index.getX( i + 2 );

					pA.fromBufferAttribute( positionAttribute, vA );
					pB.fromBufferAttribute( positionAttribute, vB );
					pC.fromBufferAttribute( positionAttribute, vC );

					cb.subVectors( pC, pB );
					ab.subVectors( pA, pB );
					cb.cross( ab );

					nA.fromBufferAttribute( normalAttribute, vA );
					nB.fromBufferAttribute( normalAttribute, vB );
					nC.fromBufferAttribute( normalAttribute, vC );

					nA.add( cb );
					nB.add( cb );
					nC.add( cb );

					normalAttribute.setXYZ( vA, nA.x, nA.y, nA.z );
					normalAttribute.setXYZ( vB, nB.x, nB.y, nB.z );
					normalAttribute.setXYZ( vC, nC.x, nC.y, nC.z );

				}

			} else {

				// non-indexed elements (unconnected triangle soup)

				for ( let i = 0, il = positionAttribute.count; i < il; i += 3 ) {

					pA.fromBufferAttribute( positionAttribute, i + 0 );
					pB.fromBufferAttribute( positionAttribute, i + 1 );
					pC.fromBufferAttribute( positionAttribute, i + 2 );

					cb.subVectors( pC, pB );
					ab.subVectors( pA, pB );
					cb.cross( ab );

					normalAttribute.setXYZ( i + 0, cb.x, cb.y, cb.z );
					normalAttribute.setXYZ( i + 1, cb.x, cb.y, cb.z );
					normalAttribute.setXYZ( i + 2, cb.x, cb.y, cb.z );

				}

			}

			this.normalizeNormals();

			normalAttribute.needsUpdate = true;

		}

	}

	normalizeNormals() {

		const normals = this.attributes.normal;

		for ( let i = 0, il = normals.count; i < il; i ++ ) {

			_vector$8.fromBufferAttribute( normals, i );

			_vector$8.normalize();

			normals.setXYZ( i, _vector$8.x, _vector$8.y, _vector$8.z );

		}

	}

	toNonIndexed() {

		function convertBufferAttribute( attribute, indices ) {

			const array = attribute.array;
			const itemSize = attribute.itemSize;
			const normalized = attribute.normalized;

			const array2 = new array.constructor( indices.length * itemSize );

			let index = 0, index2 = 0;

			for ( let i = 0, l = indices.length; i < l; i ++ ) {

				if ( attribute.isInterleavedBufferAttribute ) {

					index = indices[ i ] * attribute.data.stride + attribute.offset;

				} else {

					index = indices[ i ] * itemSize;

				}

				for ( let j = 0; j < itemSize; j ++ ) {

					array2[ index2 ++ ] = array[ index ++ ];

				}

			}

			return new BufferAttribute( array2, itemSize, normalized );

		}

		//

		if ( this.index === null ) {

			console.warn( 'THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed.' );
			return this;

		}

		const geometry2 = new BufferGeometry();

		const indices = this.index.array;
		const attributes = this.attributes;

		// attributes

		for ( const name in attributes ) {

			const attribute = attributes[ name ];

			const newAttribute = convertBufferAttribute( attribute, indices );

			geometry2.setAttribute( name, newAttribute );

		}

		// morph attributes

		const morphAttributes = this.morphAttributes;

		for ( const name in morphAttributes ) {

			const morphArray = [];
			const morphAttribute = morphAttributes[ name ]; // morphAttribute: array of Float32BufferAttributes

			for ( let i = 0, il = morphAttribute.length; i < il; i ++ ) {

				const attribute = morphAttribute[ i ];

				const newAttribute = convertBufferAttribute( attribute, indices );

				morphArray.push( newAttribute );

			}

			geometry2.morphAttributes[ name ] = morphArray;

		}

		geometry2.morphTargetsRelative = this.morphTargetsRelative;

		// groups

		const groups = this.groups;

		for ( let i = 0, l = groups.length; i < l; i ++ ) {

			const group = groups[ i ];
			geometry2.addGroup( group.start, group.count, group.materialIndex );

		}

		return geometry2;

	}

	toJSON() {

		const data = {
			metadata: {
				version: 4.6,
				type: 'BufferGeometry',
				generator: 'BufferGeometry.toJSON'
			}
		};

		// standard BufferGeometry serialization

		data.uuid = this.uuid;
		data.type = this.type;
		if ( this.name !== '' ) data.name = this.name;
		if ( Object.keys( this.userData ).length > 0 ) data.userData = this.userData;

		if ( this.parameters !== undefined ) {

			const parameters = this.parameters;

			for ( const key in parameters ) {

				if ( parameters[ key ] !== undefined ) data[ key ] = parameters[ key ];

			}

			return data;

		}

		// for simplicity the code assumes attributes are not shared across geometries, see #15811

		data.data = { attributes: {} };

		const index = this.index;

		if ( index !== null ) {

			data.data.index = {
				type: index.array.constructor.name,
				array: Array.prototype.slice.call( index.array )
			};

		}

		const attributes = this.attributes;

		for ( const key in attributes ) {

			const attribute = attributes[ key ];

			data.data.attributes[ key ] = attribute.toJSON( data.data );

		}

		const morphAttributes = {};
		let hasMorphAttributes = false;

		for ( const key in this.morphAttributes ) {

			const attributeArray = this.morphAttributes[ key ];

			const array = [];

			for ( let i = 0, il = attributeArray.length; i < il; i ++ ) {

				const attribute = attributeArray[ i ];

				array.push( attribute.toJSON( data.data ) );

			}

			if ( array.length > 0 ) {

				morphAttributes[ key ] = array;

				hasMorphAttributes = true;

			}

		}

		if ( hasMorphAttributes ) {

			data.data.morphAttributes = morphAttributes;
			data.data.morphTargetsRelative = this.morphTargetsRelative;

		}

		const groups = this.groups;

		if ( groups.length > 0 ) {

			data.data.groups = JSON.parse( JSON.stringify( groups ) );

		}

		const boundingSphere = this.boundingSphere;

		if ( boundingSphere !== null ) {

			data.data.boundingSphere = {
				center: boundingSphere.center.toArray(),
				radius: boundingSphere.radius
			};

		}

		return data;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		// reset

		this.index = null;
		this.attributes = {};
		this.morphAttributes = {};
		this.groups = [];
		this.boundingBox = null;
		this.boundingSphere = null;

		// used for storing cloned, shared data

		const data = {};

		// name

		this.name = source.name;

		// index

		const index = source.index;

		if ( index !== null ) {

			this.setIndex( index.clone( data ) );

		}

		// attributes

		const attributes = source.attributes;

		for ( const name in attributes ) {

			const attribute = attributes[ name ];
			this.setAttribute( name, attribute.clone( data ) );

		}

		// morph attributes

		const morphAttributes = source.morphAttributes;

		for ( const name in morphAttributes ) {

			const array = [];
			const morphAttribute = morphAttributes[ name ]; // morphAttribute: array of Float32BufferAttributes

			for ( let i = 0, l = morphAttribute.length; i < l; i ++ ) {

				array.push( morphAttribute[ i ].clone( data ) );

			}

			this.morphAttributes[ name ] = array;

		}

		this.morphTargetsRelative = source.morphTargetsRelative;

		// groups

		const groups = source.groups;

		for ( let i = 0, l = groups.length; i < l; i ++ ) {

			const group = groups[ i ];
			this.addGroup( group.start, group.count, group.materialIndex );

		}

		// bounding box

		const boundingBox = source.boundingBox;

		if ( boundingBox !== null ) {

			this.boundingBox = boundingBox.clone();

		}

		// bounding sphere

		const boundingSphere = source.boundingSphere;

		if ( boundingSphere !== null ) {

			this.boundingSphere = boundingSphere.clone();

		}

		// draw range

		this.drawRange.start = source.drawRange.start;
		this.drawRange.count = source.drawRange.count;

		// user data

		this.userData = source.userData;

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

}

const _inverseMatrix$3 = /*@__PURE__*/ new Matrix4();
const _ray$3 = /*@__PURE__*/ new Ray();
const _sphere$6 = /*@__PURE__*/ new Sphere();
const _sphereHitAt = /*@__PURE__*/ new Vector3();

const _vA$1 = /*@__PURE__*/ new Vector3();
const _vB$1 = /*@__PURE__*/ new Vector3();
const _vC$1 = /*@__PURE__*/ new Vector3();

const _tempA = /*@__PURE__*/ new Vector3();
const _morphA = /*@__PURE__*/ new Vector3();

const _intersectionPoint = /*@__PURE__*/ new Vector3();
const _intersectionPointWorld = /*@__PURE__*/ new Vector3();

class Mesh extends Object3D {

	constructor( geometry = new BufferGeometry(), material = new MeshBasicMaterial() ) {

		super();

		this.isMesh = true;

		this.type = 'Mesh';

		this.geometry = geometry;
		this.material = material;

		this.updateMorphTargets();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		if ( source.morphTargetInfluences !== undefined ) {

			this.morphTargetInfluences = source.morphTargetInfluences.slice();

		}

		if ( source.morphTargetDictionary !== undefined ) {

			this.morphTargetDictionary = Object.assign( {}, source.morphTargetDictionary );

		}

		this.material = Array.isArray( source.material ) ? source.material.slice() : source.material;
		this.geometry = source.geometry;

		return this;

	}

	updateMorphTargets() {

		const geometry = this.geometry;

		const morphAttributes = geometry.morphAttributes;
		const keys = Object.keys( morphAttributes );

		if ( keys.length > 0 ) {

			const morphAttribute = morphAttributes[ keys[ 0 ] ];

			if ( morphAttribute !== undefined ) {

				this.morphTargetInfluences = [];
				this.morphTargetDictionary = {};

				for ( let m = 0, ml = morphAttribute.length; m < ml; m ++ ) {

					const name = morphAttribute[ m ].name || String( m );

					this.morphTargetInfluences.push( 0 );
					this.morphTargetDictionary[ name ] = m;

				}

			}

		}

	}

	getVertexPosition( index, target ) {

		const geometry = this.geometry;
		const position = geometry.attributes.position;
		const morphPosition = geometry.morphAttributes.position;
		const morphTargetsRelative = geometry.morphTargetsRelative;

		target.fromBufferAttribute( position, index );

		const morphInfluences = this.morphTargetInfluences;

		if ( morphPosition && morphInfluences ) {

			_morphA.set( 0, 0, 0 );

			for ( let i = 0, il = morphPosition.length; i < il; i ++ ) {

				const influence = morphInfluences[ i ];
				const morphAttribute = morphPosition[ i ];

				if ( influence === 0 ) continue;

				_tempA.fromBufferAttribute( morphAttribute, index );

				if ( morphTargetsRelative ) {

					_morphA.addScaledVector( _tempA, influence );

				} else {

					_morphA.addScaledVector( _tempA.sub( target ), influence );

				}

			}

			target.add( _morphA );

		}

		return target;

	}

	raycast( raycaster, intersects ) {

		const geometry = this.geometry;
		const material = this.material;
		const matrixWorld = this.matrixWorld;

		if ( material === undefined ) return;

		// test with bounding sphere in world space

		if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();

		_sphere$6.copy( geometry.boundingSphere );
		_sphere$6.applyMatrix4( matrixWorld );

		// check distance from ray origin to bounding sphere

		_ray$3.copy( raycaster.ray ).recast( raycaster.near );

		if ( _sphere$6.containsPoint( _ray$3.origin ) === false ) {

			if ( _ray$3.intersectSphere( _sphere$6, _sphereHitAt ) === null ) return;

			if ( _ray$3.origin.distanceToSquared( _sphereHitAt ) > ( raycaster.far - raycaster.near ) ** 2 ) return;

		}

		// convert ray to local space of mesh

		_inverseMatrix$3.copy( matrixWorld ).invert();
		_ray$3.copy( raycaster.ray ).applyMatrix4( _inverseMatrix$3 );

		// test with bounding box in local space

		if ( geometry.boundingBox !== null ) {

			if ( _ray$3.intersectsBox( geometry.boundingBox ) === false ) return;

		}

		// test for intersections with geometry

		this._computeIntersections( raycaster, intersects, _ray$3 );

	}

	_computeIntersections( raycaster, intersects, rayLocalSpace ) {

		let intersection;

		const geometry = this.geometry;
		const material = this.material;

		const index = geometry.index;
		const position = geometry.attributes.position;
		const uv = geometry.attributes.uv;
		const uv1 = geometry.attributes.uv1;
		const normal = geometry.attributes.normal;
		const groups = geometry.groups;
		const drawRange = geometry.drawRange;

		if ( index !== null ) {

			// indexed buffer geometry

			if ( Array.isArray( material ) ) {

				for ( let i = 0, il = groups.length; i < il; i ++ ) {

					const group = groups[ i ];
					const groupMaterial = material[ group.materialIndex ];

					const start = Math.max( group.start, drawRange.start );
					const end = Math.min( index.count, Math.min( ( group.start + group.count ), ( drawRange.start + drawRange.count ) ) );

					for ( let j = start, jl = end; j < jl; j += 3 ) {

						const a = index.getX( j );
						const b = index.getX( j + 1 );
						const c = index.getX( j + 2 );

						intersection = checkGeometryIntersection( this, groupMaterial, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c );

						if ( intersection ) {

							intersection.faceIndex = Math.floor( j / 3 ); // triangle number in indexed buffer semantics
							intersection.face.materialIndex = group.materialIndex;
							intersects.push( intersection );

						}

					}

				}

			} else {

				const start = Math.max( 0, drawRange.start );
				const end = Math.min( index.count, ( drawRange.start + drawRange.count ) );

				for ( let i = start, il = end; i < il; i += 3 ) {

					const a = index.getX( i );
					const b = index.getX( i + 1 );
					const c = index.getX( i + 2 );

					intersection = checkGeometryIntersection( this, material, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c );

					if ( intersection ) {

						intersection.faceIndex = Math.floor( i / 3 ); // triangle number in indexed buffer semantics
						intersects.push( intersection );

					}

				}

			}

		} else if ( position !== undefined ) {

			// non-indexed buffer geometry

			if ( Array.isArray( material ) ) {

				for ( let i = 0, il = groups.length; i < il; i ++ ) {

					const group = groups[ i ];
					const groupMaterial = material[ group.materialIndex ];

					const start = Math.max( group.start, drawRange.start );
					const end = Math.min( position.count, Math.min( ( group.start + group.count ), ( drawRange.start + drawRange.count ) ) );

					for ( let j = start, jl = end; j < jl; j += 3 ) {

						const a = j;
						const b = j + 1;
						const c = j + 2;

						intersection = checkGeometryIntersection( this, groupMaterial, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c );

						if ( intersection ) {

							intersection.faceIndex = Math.floor( j / 3 ); // triangle number in non-indexed buffer semantics
							intersection.face.materialIndex = group.materialIndex;
							intersects.push( intersection );

						}

					}

				}

			} else {

				const start = Math.max( 0, drawRange.start );
				const end = Math.min( position.count, ( drawRange.start + drawRange.count ) );

				for ( let i = start, il = end; i < il; i += 3 ) {

					const a = i;
					const b = i + 1;
					const c = i + 2;

					intersection = checkGeometryIntersection( this, material, raycaster, rayLocalSpace, uv, uv1, normal, a, b, c );

					if ( intersection ) {

						intersection.faceIndex = Math.floor( i / 3 ); // triangle number in non-indexed buffer semantics
						intersects.push( intersection );

					}

				}

			}

		}

	}

}

function checkIntersection$1( object, material, raycaster, ray, pA, pB, pC, point ) {

	let intersect;

	if ( material.side === BackSide ) {

		intersect = ray.intersectTriangle( pC, pB, pA, true, point );

	} else {

		intersect = ray.intersectTriangle( pA, pB, pC, ( material.side === FrontSide ), point );

	}

	if ( intersect === null ) return null;

	_intersectionPointWorld.copy( point );
	_intersectionPointWorld.applyMatrix4( object.matrixWorld );

	const distance = raycaster.ray.origin.distanceTo( _intersectionPointWorld );

	if ( distance < raycaster.near || distance > raycaster.far ) return null;

	return {
		distance: distance,
		point: _intersectionPointWorld.clone(),
		object: object
	};

}

function checkGeometryIntersection( object, material, raycaster, ray, uv, uv1, normal, a, b, c ) {

	object.getVertexPosition( a, _vA$1 );
	object.getVertexPosition( b, _vB$1 );
	object.getVertexPosition( c, _vC$1 );

	const intersection = checkIntersection$1( object, material, raycaster, ray, _vA$1, _vB$1, _vC$1, _intersectionPoint );

	if ( intersection ) {

		const barycoord = new Vector3();
		Triangle.getBarycoord( _intersectionPoint, _vA$1, _vB$1, _vC$1, barycoord );

		if ( uv ) {

			intersection.uv = Triangle.getInterpolatedAttribute( uv, a, b, c, barycoord, new Vector2() );

		}

		if ( uv1 ) {

			intersection.uv1 = Triangle.getInterpolatedAttribute( uv1, a, b, c, barycoord, new Vector2() );

		}

		if ( normal ) {

			intersection.normal = Triangle.getInterpolatedAttribute( normal, a, b, c, barycoord, new Vector3() );

			if ( intersection.normal.dot( ray.direction ) > 0 ) {

				intersection.normal.multiplyScalar( - 1 );

			}

		}

		const face = {
			a: a,
			b: b,
			c: c,
			normal: new Vector3(),
			materialIndex: 0
		};

		Triangle.getNormal( _vA$1, _vB$1, _vC$1, face.normal );

		intersection.face = face;
		intersection.barycoord = barycoord;

	}

	return intersection;

}

class BoxGeometry extends BufferGeometry {

	constructor( width = 1, height = 1, depth = 1, widthSegments = 1, heightSegments = 1, depthSegments = 1 ) {

		super();

		this.type = 'BoxGeometry';

		this.parameters = {
			width: width,
			height: height,
			depth: depth,
			widthSegments: widthSegments,
			heightSegments: heightSegments,
			depthSegments: depthSegments
		};

		const scope = this;

		// segments

		widthSegments = Math.floor( widthSegments );
		heightSegments = Math.floor( heightSegments );
		depthSegments = Math.floor( depthSegments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		let numberOfVertices = 0;
		let groupStart = 0;

		// build each side of the box geometry

		buildPlane( 'z', 'y', 'x', - 1, - 1, depth, height, width, depthSegments, heightSegments, 0 ); // px
		buildPlane( 'z', 'y', 'x', 1, - 1, depth, height, - width, depthSegments, heightSegments, 1 ); // nx
		buildPlane( 'x', 'z', 'y', 1, 1, width, depth, height, widthSegments, depthSegments, 2 ); // py
		buildPlane( 'x', 'z', 'y', 1, - 1, width, depth, - height, widthSegments, depthSegments, 3 ); // ny
		buildPlane( 'x', 'y', 'z', 1, - 1, width, height, depth, widthSegments, heightSegments, 4 ); // pz
		buildPlane( 'x', 'y', 'z', - 1, - 1, width, height, - depth, widthSegments, heightSegments, 5 ); // nz

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

		function buildPlane( u, v, w, udir, vdir, width, height, depth, gridX, gridY, materialIndex ) {

			const segmentWidth = width / gridX;
			const segmentHeight = height / gridY;

			const widthHalf = width / 2;
			const heightHalf = height / 2;
			const depthHalf = depth / 2;

			const gridX1 = gridX + 1;
			const gridY1 = gridY + 1;

			let vertexCounter = 0;
			let groupCount = 0;

			const vector = new Vector3();

			// generate vertices, normals and uvs

			for ( let iy = 0; iy < gridY1; iy ++ ) {

				const y = iy * segmentHeight - heightHalf;

				for ( let ix = 0; ix < gridX1; ix ++ ) {

					const x = ix * segmentWidth - widthHalf;

					// set values to correct vector component

					vector[ u ] = x * udir;
					vector[ v ] = y * vdir;
					vector[ w ] = depthHalf;

					// now apply vector to vertex buffer

					vertices.push( vector.x, vector.y, vector.z );

					// set values to correct vector component

					vector[ u ] = 0;
					vector[ v ] = 0;
					vector[ w ] = depth > 0 ? 1 : - 1;

					// now apply vector to normal buffer

					normals.push( vector.x, vector.y, vector.z );

					// uvs

					uvs.push( ix / gridX );
					uvs.push( 1 - ( iy / gridY ) );

					// counters

					vertexCounter += 1;

				}

			}

			// indices

			// 1. you need three indices to draw a single face
			// 2. a single segment consists of two faces
			// 3. so we need to generate six (2*3) indices per segment

			for ( let iy = 0; iy < gridY; iy ++ ) {

				for ( let ix = 0; ix < gridX; ix ++ ) {

					const a = numberOfVertices + ix + gridX1 * iy;
					const b = numberOfVertices + ix + gridX1 * ( iy + 1 );
					const c = numberOfVertices + ( ix + 1 ) + gridX1 * ( iy + 1 );
					const d = numberOfVertices + ( ix + 1 ) + gridX1 * iy;

					// faces

					indices.push( a, b, d );
					indices.push( b, c, d );

					// increase counter

					groupCount += 6;

				}

			}

			// add a group to the geometry. this will ensure multi material support

			scope.addGroup( groupStart, groupCount, materialIndex );

			// calculate new start value for groups

			groupStart += groupCount;

			// update total number of vertices

			numberOfVertices += vertexCounter;

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new BoxGeometry( data.width, data.height, data.depth, data.widthSegments, data.heightSegments, data.depthSegments );

	}

}

/**
 * Uniform Utilities
 */

function cloneUniforms( src ) {

	const dst = {};

	for ( const u in src ) {

		dst[ u ] = {};

		for ( const p in src[ u ] ) {

			const property = src[ u ][ p ];

			if ( property && ( property.isColor ||
				property.isMatrix3 || property.isMatrix4 ||
				property.isVector2 || property.isVector3 || property.isVector4 ||
				property.isTexture || property.isQuaternion ) ) {

				if ( property.isRenderTargetTexture ) {

					console.warn( 'UniformsUtils: Textures of render targets cannot be cloned via cloneUniforms() or mergeUniforms().' );
					dst[ u ][ p ] = null;

				} else {

					dst[ u ][ p ] = property.clone();

				}

			} else if ( Array.isArray( property ) ) {

				dst[ u ][ p ] = property.slice();

			} else {

				dst[ u ][ p ] = property;

			}

		}

	}

	return dst;

}

function mergeUniforms( uniforms ) {

	const merged = {};

	for ( let u = 0; u < uniforms.length; u ++ ) {

		const tmp = cloneUniforms( uniforms[ u ] );

		for ( const p in tmp ) {

			merged[ p ] = tmp[ p ];

		}

	}

	return merged;

}

function cloneUniformsGroups( src ) {

	const dst = [];

	for ( let u = 0; u < src.length; u ++ ) {

		dst.push( src[ u ].clone() );

	}

	return dst;

}

function getUnlitUniformColorSpace( renderer ) {

	const currentRenderTarget = renderer.getRenderTarget();

	if ( currentRenderTarget === null ) {

		// https://github.com/mrdoob/three.js/pull/23937#issuecomment-1111067398
		return renderer.outputColorSpace;

	}

	// https://github.com/mrdoob/three.js/issues/27868
	if ( currentRenderTarget.isXRRenderTarget === true ) {

		return currentRenderTarget.texture.colorSpace;

	}

	return ColorManagement.workingColorSpace;

}

// Legacy

const UniformsUtils = { clone: cloneUniforms, merge: mergeUniforms };

var default_vertex = "void main() {\n\tgl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );\n}";

var default_fragment = "void main() {\n\tgl_FragColor = vec4( 1.0, 0.0, 0.0, 1.0 );\n}";

class ShaderMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isShaderMaterial = true;

		this.type = 'ShaderMaterial';

		this.defines = {};
		this.uniforms = {};
		this.uniformsGroups = [];

		this.vertexShader = default_vertex;
		this.fragmentShader = default_fragment;

		this.linewidth = 1;

		this.wireframe = false;
		this.wireframeLinewidth = 1;

		this.fog = false; // set to use scene fog
		this.lights = false; // set to use scene lights
		this.clipping = false; // set to use user-defined clipping planes

		this.forceSinglePass = true;

		this.extensions = {
			clipCullDistance: false, // set to use vertex shader clipping
			multiDraw: false // set to use vertex shader multi_draw / enable gl_DrawID
		};

		// When rendered geometry doesn't include these attributes but the material does,
		// use these default values in WebGL. This avoids errors when buffer data is missing.
		this.defaultAttributeValues = {
			'color': [ 1, 1, 1 ],
			'uv': [ 0, 0 ],
			'uv1': [ 0, 0 ]
		};

		this.index0AttributeName = undefined;
		this.uniformsNeedUpdate = false;

		this.glslVersion = null;

		if ( parameters !== undefined ) {

			this.setValues( parameters );

		}

	}

	copy( source ) {

		super.copy( source );

		this.fragmentShader = source.fragmentShader;
		this.vertexShader = source.vertexShader;

		this.uniforms = cloneUniforms( source.uniforms );
		this.uniformsGroups = cloneUniformsGroups( source.uniformsGroups );

		this.defines = Object.assign( {}, source.defines );

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;

		this.fog = source.fog;
		this.lights = source.lights;
		this.clipping = source.clipping;

		this.extensions = Object.assign( {}, source.extensions );

		this.glslVersion = source.glslVersion;

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.glslVersion = this.glslVersion;
		data.uniforms = {};

		for ( const name in this.uniforms ) {

			const uniform = this.uniforms[ name ];
			const value = uniform.value;

			if ( value && value.isTexture ) {

				data.uniforms[ name ] = {
					type: 't',
					value: value.toJSON( meta ).uuid
				};

			} else if ( value && value.isColor ) {

				data.uniforms[ name ] = {
					type: 'c',
					value: value.getHex()
				};

			} else if ( value && value.isVector2 ) {

				data.uniforms[ name ] = {
					type: 'v2',
					value: value.toArray()
				};

			} else if ( value && value.isVector3 ) {

				data.uniforms[ name ] = {
					type: 'v3',
					value: value.toArray()
				};

			} else if ( value && value.isVector4 ) {

				data.uniforms[ name ] = {
					type: 'v4',
					value: value.toArray()
				};

			} else if ( value && value.isMatrix3 ) {

				data.uniforms[ name ] = {
					type: 'm3',
					value: value.toArray()
				};

			} else if ( value && value.isMatrix4 ) {

				data.uniforms[ name ] = {
					type: 'm4',
					value: value.toArray()
				};

			} else {

				data.uniforms[ name ] = {
					value: value
				};

				// note: the array variants v2v, v3v, v4v, m4v and tv are not supported so far

			}

		}

		if ( Object.keys( this.defines ).length > 0 ) data.defines = this.defines;

		data.vertexShader = this.vertexShader;
		data.fragmentShader = this.fragmentShader;

		data.lights = this.lights;
		data.clipping = this.clipping;

		const extensions = {};

		for ( const key in this.extensions ) {

			if ( this.extensions[ key ] === true ) extensions[ key ] = true;

		}

		if ( Object.keys( extensions ).length > 0 ) data.extensions = extensions;

		return data;

	}

}

class Camera extends Object3D {

	constructor() {

		super();

		this.isCamera = true;

		this.type = 'Camera';

		this.matrixWorldInverse = new Matrix4();

		this.projectionMatrix = new Matrix4();
		this.projectionMatrixInverse = new Matrix4();

		this.coordinateSystem = WebGLCoordinateSystem;

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.matrixWorldInverse.copy( source.matrixWorldInverse );

		this.projectionMatrix.copy( source.projectionMatrix );
		this.projectionMatrixInverse.copy( source.projectionMatrixInverse );

		this.coordinateSystem = source.coordinateSystem;

		return this;

	}

	getWorldDirection( target ) {

		return super.getWorldDirection( target ).negate();

	}

	updateMatrixWorld( force ) {

		super.updateMatrixWorld( force );

		this.matrixWorldInverse.copy( this.matrixWorld ).invert();

	}

	updateWorldMatrix( updateParents, updateChildren ) {

		super.updateWorldMatrix( updateParents, updateChildren );

		this.matrixWorldInverse.copy( this.matrixWorld ).invert();

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

const _v3$1 = /*@__PURE__*/ new Vector3();
const _minTarget = /*@__PURE__*/ new Vector2();
const _maxTarget = /*@__PURE__*/ new Vector2();


class PerspectiveCamera extends Camera {

	constructor( fov = 50, aspect = 1, near = 0.1, far = 2000 ) {

		super();

		this.isPerspectiveCamera = true;

		this.type = 'PerspectiveCamera';

		this.fov = fov;
		this.zoom = 1;

		this.near = near;
		this.far = far;
		this.focus = 10;

		this.aspect = aspect;
		this.view = null;

		this.filmGauge = 35;	// width of the film (default in millimeters)
		this.filmOffset = 0;	// horizontal film offset (same unit as gauge)

		this.updateProjectionMatrix();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.fov = source.fov;
		this.zoom = source.zoom;

		this.near = source.near;
		this.far = source.far;
		this.focus = source.focus;

		this.aspect = source.aspect;
		this.view = source.view === null ? null : Object.assign( {}, source.view );

		this.filmGauge = source.filmGauge;
		this.filmOffset = source.filmOffset;

		return this;

	}

	/**
	 * Sets the FOV by focal length in respect to the current .filmGauge.
	 *
	 * The default film gauge is 35, so that the focal length can be specified for
	 * a 35mm (full frame) camera.
	 *
	 * Values for focal length and film gauge must have the same unit.
	 */
	setFocalLength( focalLength ) {

		/** see {@link http://www.bobatkins.com/photography/technical/field_of_view.html} */
		const vExtentSlope = 0.5 * this.getFilmHeight() / focalLength;

		this.fov = RAD2DEG * 2 * Math.atan( vExtentSlope );
		this.updateProjectionMatrix();

	}

	/**
	 * Calculates the focal length from the current .fov and .filmGauge.
	 */
	getFocalLength() {

		const vExtentSlope = Math.tan( DEG2RAD * 0.5 * this.fov );

		return 0.5 * this.getFilmHeight() / vExtentSlope;

	}

	getEffectiveFOV() {

		return RAD2DEG * 2 * Math.atan(
			Math.tan( DEG2RAD * 0.5 * this.fov ) / this.zoom );

	}

	getFilmWidth() {

		// film not completely covered in portrait format (aspect < 1)
		return this.filmGauge * Math.min( this.aspect, 1 );

	}

	getFilmHeight() {

		// film not completely covered in landscape format (aspect > 1)
		return this.filmGauge / Math.max( this.aspect, 1 );

	}

	/**
	 * Computes the 2D bounds of the camera's viewable rectangle at a given distance along the viewing direction.
	 * Sets minTarget and maxTarget to the coordinates of the lower-left and upper-right corners of the view rectangle.
	 */
	getViewBounds( distance, minTarget, maxTarget ) {

		_v3$1.set( - 1, - 1, 0.5 ).applyMatrix4( this.projectionMatrixInverse );

		minTarget.set( _v3$1.x, _v3$1.y ).multiplyScalar( - distance / _v3$1.z );

		_v3$1.set( 1, 1, 0.5 ).applyMatrix4( this.projectionMatrixInverse );

		maxTarget.set( _v3$1.x, _v3$1.y ).multiplyScalar( - distance / _v3$1.z );

	}

	/**
	 * Computes the width and height of the camera's viewable rectangle at a given distance along the viewing direction.
	 * Copies the result into the target Vector2, where x is width and y is height.
	 */
	getViewSize( distance, target ) {

		this.getViewBounds( distance, _minTarget, _maxTarget );

		return target.subVectors( _maxTarget, _minTarget );

	}

	/**
	 * Sets an offset in a larger frustum. This is useful for multi-window or
	 * multi-monitor/multi-machine setups.
	 *
	 * For example, if you have 3x2 monitors and each monitor is 1920x1080 and
	 * the monitors are in grid like this
	 *
	 *   +---+---+---+
	 *   | A | B | C |
	 *   +---+---+---+
	 *   | D | E | F |
	 *   +---+---+---+
	 *
	 * then for each monitor you would call it like this
	 *
	 *   const w = 1920;
	 *   const h = 1080;
	 *   const fullWidth = w * 3;
	 *   const fullHeight = h * 2;
	 *
	 *   --A--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 0, w, h );
	 *   --B--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 0, w, h );
	 *   --C--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 0, w, h );
	 *   --D--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 0, h * 1, w, h );
	 *   --E--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 1, h * 1, w, h );
	 *   --F--
	 *   camera.setViewOffset( fullWidth, fullHeight, w * 2, h * 1, w, h );
	 *
	 *   Note there is no reason monitors have to be the same size or in a grid.
	 */
	setViewOffset( fullWidth, fullHeight, x, y, width, height ) {

		this.aspect = fullWidth / fullHeight;

		if ( this.view === null ) {

			this.view = {
				enabled: true,
				fullWidth: 1,
				fullHeight: 1,
				offsetX: 0,
				offsetY: 0,
				width: 1,
				height: 1
			};

		}

		this.view.enabled = true;
		this.view.fullWidth = fullWidth;
		this.view.fullHeight = fullHeight;
		this.view.offsetX = x;
		this.view.offsetY = y;
		this.view.width = width;
		this.view.height = height;

		this.updateProjectionMatrix();

	}

	clearViewOffset() {

		if ( this.view !== null ) {

			this.view.enabled = false;

		}

		this.updateProjectionMatrix();

	}

	updateProjectionMatrix() {

		const near = this.near;
		let top = near * Math.tan( DEG2RAD * 0.5 * this.fov ) / this.zoom;
		let height = 2 * top;
		let width = this.aspect * height;
		let left = - 0.5 * width;
		const view = this.view;

		if ( this.view !== null && this.view.enabled ) {

			const fullWidth = view.fullWidth,
				fullHeight = view.fullHeight;

			left += view.offsetX * width / fullWidth;
			top -= view.offsetY * height / fullHeight;
			width *= view.width / fullWidth;
			height *= view.height / fullHeight;

		}

		const skew = this.filmOffset;
		if ( skew !== 0 ) left += near * skew / this.getFilmWidth();

		this.projectionMatrix.makePerspective( left, left + width, top, top - height, near, this.far, this.coordinateSystem );

		this.projectionMatrixInverse.copy( this.projectionMatrix ).invert();

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.object.fov = this.fov;
		data.object.zoom = this.zoom;

		data.object.near = this.near;
		data.object.far = this.far;
		data.object.focus = this.focus;

		data.object.aspect = this.aspect;

		if ( this.view !== null ) data.object.view = Object.assign( {}, this.view );

		data.object.filmGauge = this.filmGauge;
		data.object.filmOffset = this.filmOffset;

		return data;

	}

}

const fov = - 90; // negative fov is not an error
const aspect = 1;

class CubeCamera extends Object3D {

	constructor( near, far, renderTarget ) {

		super();

		this.type = 'CubeCamera';

		this.renderTarget = renderTarget;
		this.coordinateSystem = null;
		this.activeMipmapLevel = 0;

		const cameraPX = new PerspectiveCamera( fov, aspect, near, far );
		cameraPX.layers = this.layers;
		this.add( cameraPX );

		const cameraNX = new PerspectiveCamera( fov, aspect, near, far );
		cameraNX.layers = this.layers;
		this.add( cameraNX );

		const cameraPY = new PerspectiveCamera( fov, aspect, near, far );
		cameraPY.layers = this.layers;
		this.add( cameraPY );

		const cameraNY = new PerspectiveCamera( fov, aspect, near, far );
		cameraNY.layers = this.layers;
		this.add( cameraNY );

		const cameraPZ = new PerspectiveCamera( fov, aspect, near, far );
		cameraPZ.layers = this.layers;
		this.add( cameraPZ );

		const cameraNZ = new PerspectiveCamera( fov, aspect, near, far );
		cameraNZ.layers = this.layers;
		this.add( cameraNZ );

	}

	updateCoordinateSystem() {

		const coordinateSystem = this.coordinateSystem;

		const cameras = this.children.concat();

		const [ cameraPX, cameraNX, cameraPY, cameraNY, cameraPZ, cameraNZ ] = cameras;

		for ( const camera of cameras ) this.remove( camera );

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			cameraPX.up.set( 0, 1, 0 );
			cameraPX.lookAt( 1, 0, 0 );

			cameraNX.up.set( 0, 1, 0 );
			cameraNX.lookAt( - 1, 0, 0 );

			cameraPY.up.set( 0, 0, - 1 );
			cameraPY.lookAt( 0, 1, 0 );

			cameraNY.up.set( 0, 0, 1 );
			cameraNY.lookAt( 0, - 1, 0 );

			cameraPZ.up.set( 0, 1, 0 );
			cameraPZ.lookAt( 0, 0, 1 );

			cameraNZ.up.set( 0, 1, 0 );
			cameraNZ.lookAt( 0, 0, - 1 );

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			cameraPX.up.set( 0, - 1, 0 );
			cameraPX.lookAt( - 1, 0, 0 );

			cameraNX.up.set( 0, - 1, 0 );
			cameraNX.lookAt( 1, 0, 0 );

			cameraPY.up.set( 0, 0, 1 );
			cameraPY.lookAt( 0, 1, 0 );

			cameraNY.up.set( 0, 0, - 1 );
			cameraNY.lookAt( 0, - 1, 0 );

			cameraPZ.up.set( 0, - 1, 0 );
			cameraPZ.lookAt( 0, 0, 1 );

			cameraNZ.up.set( 0, - 1, 0 );
			cameraNZ.lookAt( 0, 0, - 1 );

		} else {

			throw new Error( 'THREE.CubeCamera.updateCoordinateSystem(): Invalid coordinate system: ' + coordinateSystem );

		}

		for ( const camera of cameras ) {

			this.add( camera );

			camera.updateMatrixWorld();

		}

	}

	update( renderer, scene ) {

		if ( this.parent === null ) this.updateMatrixWorld();

		const { renderTarget, activeMipmapLevel } = this;

		if ( this.coordinateSystem !== renderer.coordinateSystem ) {

			this.coordinateSystem = renderer.coordinateSystem;

			this.updateCoordinateSystem();

		}

		const [ cameraPX, cameraNX, cameraPY, cameraNY, cameraPZ, cameraNZ ] = this.children;

		const currentRenderTarget = renderer.getRenderTarget();
		const currentActiveCubeFace = renderer.getActiveCubeFace();
		const currentActiveMipmapLevel = renderer.getActiveMipmapLevel();

		const currentXrEnabled = renderer.xr.enabled;

		renderer.xr.enabled = false;

		const generateMipmaps = renderTarget.texture.generateMipmaps;

		renderTarget.texture.generateMipmaps = false;

		renderer.setRenderTarget( renderTarget, 0, activeMipmapLevel );
		renderer.render( scene, cameraPX );

		renderer.setRenderTarget( renderTarget, 1, activeMipmapLevel );
		renderer.render( scene, cameraNX );

		renderer.setRenderTarget( renderTarget, 2, activeMipmapLevel );
		renderer.render( scene, cameraPY );

		renderer.setRenderTarget( renderTarget, 3, activeMipmapLevel );
		renderer.render( scene, cameraNY );

		renderer.setRenderTarget( renderTarget, 4, activeMipmapLevel );
		renderer.render( scene, cameraPZ );

		// mipmaps are generated during the last call of render()
		// at this point, all sides of the cube render target are defined

		renderTarget.texture.generateMipmaps = generateMipmaps;

		renderer.setRenderTarget( renderTarget, 5, activeMipmapLevel );
		renderer.render( scene, cameraNZ );

		renderer.setRenderTarget( currentRenderTarget, currentActiveCubeFace, currentActiveMipmapLevel );

		renderer.xr.enabled = currentXrEnabled;

		renderTarget.texture.needsPMREMUpdate = true;

	}

}

class CubeTexture extends Texture {

	constructor( images, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy, colorSpace ) {

		images = images !== undefined ? images : [];
		mapping = mapping !== undefined ? mapping : CubeReflectionMapping;

		super( images, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy, colorSpace );

		this.isCubeTexture = true;

		this.flipY = false;

	}

	get images() {

		return this.image;

	}

	set images( value ) {

		this.image = value;

	}

}

class WebGLCubeRenderTarget extends WebGLRenderTarget {

	constructor( size = 1, options = {} ) {

		super( size, size, options );

		this.isWebGLCubeRenderTarget = true;

		const image = { width: size, height: size, depth: 1 };
		const images = [ image, image, image, image, image, image ];

		this.texture = new CubeTexture( images, options.mapping, options.wrapS, options.wrapT, options.magFilter, options.minFilter, options.format, options.type, options.anisotropy, options.colorSpace );

		// By convention -- likely based on the RenderMan spec from the 1990's -- cube maps are specified by WebGL (and three.js)
		// in a coordinate system in which positive-x is to the right when looking up the positive-z axis -- in other words,
		// in a left-handed coordinate system. By continuing this convention, preexisting cube maps continued to render correctly.

		// three.js uses a right-handed coordinate system. So environment maps used in three.js appear to have px and nx swapped
		// and the flag isRenderTargetTexture controls this conversion. The flip is not required when using WebGLCubeRenderTarget.texture
		// as a cube texture (this is detected when isRenderTargetTexture is set to true for cube textures).

		this.texture.isRenderTargetTexture = true;

		this.texture.generateMipmaps = options.generateMipmaps !== undefined ? options.generateMipmaps : false;
		this.texture.minFilter = options.minFilter !== undefined ? options.minFilter : LinearFilter;

	}

	fromEquirectangularTexture( renderer, texture ) {

		this.texture.type = texture.type;
		this.texture.colorSpace = texture.colorSpace;

		this.texture.generateMipmaps = texture.generateMipmaps;
		this.texture.minFilter = texture.minFilter;
		this.texture.magFilter = texture.magFilter;

		const shader = {

			uniforms: {
				tEquirect: { value: null },
			},

			vertexShader: /* glsl */`

				varying vec3 vWorldDirection;

				vec3 transformDirection( in vec3 dir, in mat4 matrix ) {

					return normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );

				}

				void main() {

					vWorldDirection = transformDirection( position, modelMatrix );

					#include <begin_vertex>
					#include <project_vertex>

				}
			`,

			fragmentShader: /* glsl */`

				uniform sampler2D tEquirect;

				varying vec3 vWorldDirection;

				#include <common>

				void main() {

					vec3 direction = normalize( vWorldDirection );

					vec2 sampleUV = equirectUv( direction );

					gl_FragColor = texture2D( tEquirect, sampleUV );

				}
			`
		};

		const geometry = new BoxGeometry( 5, 5, 5 );

		const material = new ShaderMaterial( {

			name: 'CubemapFromEquirect',

			uniforms: cloneUniforms( shader.uniforms ),
			vertexShader: shader.vertexShader,
			fragmentShader: shader.fragmentShader,
			side: BackSide,
			blending: NoBlending

		} );

		material.uniforms.tEquirect.value = texture;

		const mesh = new Mesh( geometry, material );

		const currentMinFilter = texture.minFilter;

		// Avoid blurred poles
		if ( texture.minFilter === LinearMipmapLinearFilter ) texture.minFilter = LinearFilter;

		const camera = new CubeCamera( 1, 10, this );
		camera.update( renderer, mesh );

		texture.minFilter = currentMinFilter;

		mesh.geometry.dispose();
		mesh.material.dispose();

		return this;

	}

	clear( renderer, color, depth, stencil ) {

		const currentRenderTarget = renderer.getRenderTarget();

		for ( let i = 0; i < 6; i ++ ) {

			renderer.setRenderTarget( this, i );

			renderer.clear( color, depth, stencil );

		}

		renderer.setRenderTarget( currentRenderTarget );

	}

}

const _vector1 = /*@__PURE__*/ new Vector3();
const _vector2 = /*@__PURE__*/ new Vector3();
const _normalMatrix = /*@__PURE__*/ new Matrix3();

class Plane {

	constructor( normal = new Vector3( 1, 0, 0 ), constant = 0 ) {

		this.isPlane = true;

		// normal is assumed to be normalized

		this.normal = normal;
		this.constant = constant;

	}

	set( normal, constant ) {

		this.normal.copy( normal );
		this.constant = constant;

		return this;

	}

	setComponents( x, y, z, w ) {

		this.normal.set( x, y, z );
		this.constant = w;

		return this;

	}

	setFromNormalAndCoplanarPoint( normal, point ) {

		this.normal.copy( normal );
		this.constant = - point.dot( this.normal );

		return this;

	}

	setFromCoplanarPoints( a, b, c ) {

		const normal = _vector1.subVectors( c, b ).cross( _vector2.subVectors( a, b ) ).normalize();

		// Q: should an error be thrown if normal is zero (e.g. degenerate plane)?

		this.setFromNormalAndCoplanarPoint( normal, a );

		return this;

	}

	copy( plane ) {

		this.normal.copy( plane.normal );
		this.constant = plane.constant;

		return this;

	}

	normalize() {

		// Note: will lead to a divide by zero if the plane is invalid.

		const inverseNormalLength = 1.0 / this.normal.length();
		this.normal.multiplyScalar( inverseNormalLength );
		this.constant *= inverseNormalLength;

		return this;

	}

	negate() {

		this.constant *= - 1;
		this.normal.negate();

		return this;

	}

	distanceToPoint( point ) {

		return this.normal.dot( point ) + this.constant;

	}

	distanceToSphere( sphere ) {

		return this.distanceToPoint( sphere.center ) - sphere.radius;

	}

	projectPoint( point, target ) {

		return target.copy( point ).addScaledVector( this.normal, - this.distanceToPoint( point ) );

	}

	intersectLine( line, target ) {

		const direction = line.delta( _vector1 );

		const denominator = this.normal.dot( direction );

		if ( denominator === 0 ) {

			// line is coplanar, return origin
			if ( this.distanceToPoint( line.start ) === 0 ) {

				return target.copy( line.start );

			}

			// Unsure if this is the correct method to handle this case.
			return null;

		}

		const t = - ( line.start.dot( this.normal ) + this.constant ) / denominator;

		if ( t < 0 || t > 1 ) {

			return null;

		}

		return target.copy( line.start ).addScaledVector( direction, t );

	}

	intersectsLine( line ) {

		// Note: this tests if a line intersects the plane, not whether it (or its end-points) are coplanar with it.

		const startSign = this.distanceToPoint( line.start );
		const endSign = this.distanceToPoint( line.end );

		return ( startSign < 0 && endSign > 0 ) || ( endSign < 0 && startSign > 0 );

	}

	intersectsBox( box ) {

		return box.intersectsPlane( this );

	}

	intersectsSphere( sphere ) {

		return sphere.intersectsPlane( this );

	}

	coplanarPoint( target ) {

		return target.copy( this.normal ).multiplyScalar( - this.constant );

	}

	applyMatrix4( matrix, optionalNormalMatrix ) {

		const normalMatrix = optionalNormalMatrix || _normalMatrix.getNormalMatrix( matrix );

		const referencePoint = this.coplanarPoint( _vector1 ).applyMatrix4( matrix );

		const normal = this.normal.applyMatrix3( normalMatrix ).normalize();

		this.constant = - referencePoint.dot( normal );

		return this;

	}

	translate( offset ) {

		this.constant -= offset.dot( this.normal );

		return this;

	}

	equals( plane ) {

		return plane.normal.equals( this.normal ) && ( plane.constant === this.constant );

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

const _sphere$5 = /*@__PURE__*/ new Sphere();
const _vector$7 = /*@__PURE__*/ new Vector3();

class Frustum {

	constructor( p0 = new Plane(), p1 = new Plane(), p2 = new Plane(), p3 = new Plane(), p4 = new Plane(), p5 = new Plane() ) {

		this.planes = [ p0, p1, p2, p3, p4, p5 ];

	}

	set( p0, p1, p2, p3, p4, p5 ) {

		const planes = this.planes;

		planes[ 0 ].copy( p0 );
		planes[ 1 ].copy( p1 );
		planes[ 2 ].copy( p2 );
		planes[ 3 ].copy( p3 );
		planes[ 4 ].copy( p4 );
		planes[ 5 ].copy( p5 );

		return this;

	}

	copy( frustum ) {

		const planes = this.planes;

		for ( let i = 0; i < 6; i ++ ) {

			planes[ i ].copy( frustum.planes[ i ] );

		}

		return this;

	}

	setFromProjectionMatrix( m, coordinateSystem = WebGLCoordinateSystem ) {

		const planes = this.planes;
		const me = m.elements;
		const me0 = me[ 0 ], me1 = me[ 1 ], me2 = me[ 2 ], me3 = me[ 3 ];
		const me4 = me[ 4 ], me5 = me[ 5 ], me6 = me[ 6 ], me7 = me[ 7 ];
		const me8 = me[ 8 ], me9 = me[ 9 ], me10 = me[ 10 ], me11 = me[ 11 ];
		const me12 = me[ 12 ], me13 = me[ 13 ], me14 = me[ 14 ], me15 = me[ 15 ];

		planes[ 0 ].setComponents( me3 - me0, me7 - me4, me11 - me8, me15 - me12 ).normalize();
		planes[ 1 ].setComponents( me3 + me0, me7 + me4, me11 + me8, me15 + me12 ).normalize();
		planes[ 2 ].setComponents( me3 + me1, me7 + me5, me11 + me9, me15 + me13 ).normalize();
		planes[ 3 ].setComponents( me3 - me1, me7 - me5, me11 - me9, me15 - me13 ).normalize();
		planes[ 4 ].setComponents( me3 - me2, me7 - me6, me11 - me10, me15 - me14 ).normalize();

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			planes[ 5 ].setComponents( me3 + me2, me7 + me6, me11 + me10, me15 + me14 ).normalize();

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			planes[ 5 ].setComponents( me2, me6, me10, me14 ).normalize();

		} else {

			throw new Error( 'THREE.Frustum.setFromProjectionMatrix(): Invalid coordinate system: ' + coordinateSystem );

		}

		return this;

	}

	intersectsObject( object ) {

		if ( object.boundingSphere !== undefined ) {

			if ( object.boundingSphere === null ) object.computeBoundingSphere();

			_sphere$5.copy( object.boundingSphere ).applyMatrix4( object.matrixWorld );

		} else {

			const geometry = object.geometry;

			if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();

			_sphere$5.copy( geometry.boundingSphere ).applyMatrix4( object.matrixWorld );

		}

		return this.intersectsSphere( _sphere$5 );

	}

	intersectsSprite( sprite ) {

		_sphere$5.center.set( 0, 0, 0 );
		_sphere$5.radius = 0.7071067811865476;
		_sphere$5.applyMatrix4( sprite.matrixWorld );

		return this.intersectsSphere( _sphere$5 );

	}

	intersectsSphere( sphere ) {

		const planes = this.planes;
		const center = sphere.center;
		const negRadius = - sphere.radius;

		for ( let i = 0; i < 6; i ++ ) {

			const distance = planes[ i ].distanceToPoint( center );

			if ( distance < negRadius ) {

				return false;

			}

		}

		return true;

	}

	intersectsBox( box ) {

		const planes = this.planes;

		for ( let i = 0; i < 6; i ++ ) {

			const plane = planes[ i ];

			// corner at max distance

			_vector$7.x = plane.normal.x > 0 ? box.max.x : box.min.x;
			_vector$7.y = plane.normal.y > 0 ? box.max.y : box.min.y;
			_vector$7.z = plane.normal.z > 0 ? box.max.z : box.min.z;

			if ( plane.distanceToPoint( _vector$7 ) < 0 ) {

				return false;

			}

		}

		return true;

	}

	containsPoint( point ) {

		const planes = this.planes;

		for ( let i = 0; i < 6; i ++ ) {

			if ( planes[ i ].distanceToPoint( point ) < 0 ) {

				return false;

			}

		}

		return true;

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

function WebGLAnimation() {

	let context = null;
	let isAnimating = false;
	let animationLoop = null;
	let requestId = null;

	function onAnimationFrame( time, frame ) {

		animationLoop( time, frame );

		requestId = context.requestAnimationFrame( onAnimationFrame );

	}

	return {

		start: function () {

			if ( isAnimating === true ) return;
			if ( animationLoop === null ) return;

			requestId = context.requestAnimationFrame( onAnimationFrame );

			isAnimating = true;

		},

		stop: function () {

			context.cancelAnimationFrame( requestId );

			isAnimating = false;

		},

		setAnimationLoop: function ( callback ) {

			animationLoop = callback;

		},

		setContext: function ( value ) {

			context = value;

		}

	};

}

function WebGLAttributes( gl ) {

	const buffers = new WeakMap();

	function createBuffer( attribute, bufferType ) {

		const array = attribute.array;
		const usage = attribute.usage;
		const size = array.byteLength;

		const buffer = gl.createBuffer();

		gl.bindBuffer( bufferType, buffer );
		gl.bufferData( bufferType, array, usage );

		attribute.onUploadCallback();

		let type;

		if ( array instanceof Float32Array ) {

			type = gl.FLOAT;

		} else if ( array instanceof Uint16Array ) {

			if ( attribute.isFloat16BufferAttribute ) {

				type = gl.HALF_FLOAT;

			} else {

				type = gl.UNSIGNED_SHORT;

			}

		} else if ( array instanceof Int16Array ) {

			type = gl.SHORT;

		} else if ( array instanceof Uint32Array ) {

			type = gl.UNSIGNED_INT;

		} else if ( array instanceof Int32Array ) {

			type = gl.INT;

		} else if ( array instanceof Int8Array ) {

			type = gl.BYTE;

		} else if ( array instanceof Uint8Array ) {

			type = gl.UNSIGNED_BYTE;

		} else if ( array instanceof Uint8ClampedArray ) {

			type = gl.UNSIGNED_BYTE;

		} else {

			throw new Error( 'THREE.WebGLAttributes: Unsupported buffer data format: ' + array );

		}

		return {
			buffer: buffer,
			type: type,
			bytesPerElement: array.BYTES_PER_ELEMENT,
			version: attribute.version,
			size: size
		};

	}

	function updateBuffer( buffer, attribute, bufferType ) {

		const array = attribute.array;
		const updateRanges = attribute.updateRanges;

		gl.bindBuffer( bufferType, buffer );

		if ( updateRanges.length === 0 ) {

			// Not using update ranges
			gl.bufferSubData( bufferType, 0, array );

		} else {

			// Before applying update ranges, we merge any adjacent / overlapping
			// ranges to reduce load on `gl.bufferSubData`. Empirically, this has led
			// to performance improvements for applications which make heavy use of
			// update ranges. Likely due to GPU command overhead.
			//
			// Note that to reduce garbage collection between frames, we merge the
			// update ranges in-place. This is safe because this method will clear the
			// update ranges once updated.

			updateRanges.sort( ( a, b ) => a.start - b.start );

			// To merge the update ranges in-place, we work from left to right in the
			// existing updateRanges array, merging ranges. This may result in a final
			// array which is smaller than the original. This index tracks the last
			// index representing a merged range, any data after this index can be
			// trimmed once the merge algorithm is completed.
			let mergeIndex = 0;

			for ( let i = 1; i < updateRanges.length; i ++ ) {

				const previousRange = updateRanges[ mergeIndex ];
				const range = updateRanges[ i ];

				// We add one here to merge adjacent ranges. This is safe because ranges
				// operate over positive integers.
				if ( range.start <= previousRange.start + previousRange.count + 1 ) {

					previousRange.count = Math.max(
						previousRange.count,
						range.start + range.count - previousRange.start
					);

				} else {

					++ mergeIndex;
					updateRanges[ mergeIndex ] = range;

				}

			}

			// Trim the array to only contain the merged ranges.
			updateRanges.length = mergeIndex + 1;

			for ( let i = 0, l = updateRanges.length; i < l; i ++ ) {

				const range = updateRanges[ i ];

				gl.bufferSubData( bufferType, range.start * array.BYTES_PER_ELEMENT,
					array, range.start, range.count );

			}

			attribute.clearUpdateRanges();

		}

		attribute.onUploadCallback();

	}

	//

	function get( attribute ) {

		if ( attribute.isInterleavedBufferAttribute ) attribute = attribute.data;

		return buffers.get( attribute );

	}

	function remove( attribute ) {

		if ( attribute.isInterleavedBufferAttribute ) attribute = attribute.data;

		const data = buffers.get( attribute );

		if ( data ) {

			gl.deleteBuffer( data.buffer );

			buffers.delete( attribute );

		}

	}

	function update( attribute, bufferType ) {

		if ( attribute.isInterleavedBufferAttribute ) attribute = attribute.data;

		if ( attribute.isGLBufferAttribute ) {

			const cached = buffers.get( attribute );

			if ( ! cached || cached.version < attribute.version ) {

				buffers.set( attribute, {
					buffer: attribute.buffer,
					type: attribute.type,
					bytesPerElement: attribute.elementSize,
					version: attribute.version
				} );

			}

			return;

		}

		const data = buffers.get( attribute );

		if ( data === undefined ) {

			buffers.set( attribute, createBuffer( attribute, bufferType ) );

		} else if ( data.version < attribute.version ) {

			if ( data.size !== attribute.array.byteLength ) {

				throw new Error( 'THREE.WebGLAttributes: The size of the buffer attribute\'s array buffer does not match the original size. Resizing buffer attributes is not supported.' );

			}

			updateBuffer( data.buffer, attribute, bufferType );

			data.version = attribute.version;

		}

	}

	return {

		get: get,
		remove: remove,
		update: update

	};

}

class PlaneGeometry extends BufferGeometry {

	constructor( width = 1, height = 1, widthSegments = 1, heightSegments = 1 ) {

		super();

		this.type = 'PlaneGeometry';

		this.parameters = {
			width: width,
			height: height,
			widthSegments: widthSegments,
			heightSegments: heightSegments
		};

		const width_half = width / 2;
		const height_half = height / 2;

		const gridX = Math.floor( widthSegments );
		const gridY = Math.floor( heightSegments );

		const gridX1 = gridX + 1;
		const gridY1 = gridY + 1;

		const segment_width = width / gridX;
		const segment_height = height / gridY;

		//

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		for ( let iy = 0; iy < gridY1; iy ++ ) {

			const y = iy * segment_height - height_half;

			for ( let ix = 0; ix < gridX1; ix ++ ) {

				const x = ix * segment_width - width_half;

				vertices.push( x, - y, 0 );

				normals.push( 0, 0, 1 );

				uvs.push( ix / gridX );
				uvs.push( 1 - ( iy / gridY ) );

			}

		}

		for ( let iy = 0; iy < gridY; iy ++ ) {

			for ( let ix = 0; ix < gridX; ix ++ ) {

				const a = ix + gridX1 * iy;
				const b = ix + gridX1 * ( iy + 1 );
				const c = ( ix + 1 ) + gridX1 * ( iy + 1 );
				const d = ( ix + 1 ) + gridX1 * iy;

				indices.push( a, b, d );
				indices.push( b, c, d );

			}

		}

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new PlaneGeometry( data.width, data.height, data.widthSegments, data.heightSegments );

	}

}

var alphahash_fragment = "#ifdef USE_ALPHAHASH\n\tif ( diffuseColor.a < getAlphaHashThreshold( vPosition ) ) discard;\n#endif";

var alphahash_pars_fragment = "#ifdef USE_ALPHAHASH\n\tconst float ALPHA_HASH_SCALE = 0.05;\n\tfloat hash2D( vec2 value ) {\n\t\treturn fract( 1.0e4 * sin( 17.0 * value.x + 0.1 * value.y ) * ( 0.1 + abs( sin( 13.0 * value.y + value.x ) ) ) );\n\t}\n\tfloat hash3D( vec3 value ) {\n\t\treturn hash2D( vec2( hash2D( value.xy ), value.z ) );\n\t}\n\tfloat getAlphaHashThreshold( vec3 position ) {\n\t\tfloat maxDeriv = max(\n\t\t\tlength( dFdx( position.xyz ) ),\n\t\t\tlength( dFdy( position.xyz ) )\n\t\t);\n\t\tfloat pixScale = 1.0 / ( ALPHA_HASH_SCALE * maxDeriv );\n\t\tvec2 pixScales = vec2(\n\t\t\texp2( floor( log2( pixScale ) ) ),\n\t\t\texp2( ceil( log2( pixScale ) ) )\n\t\t);\n\t\tvec2 alpha = vec2(\n\t\t\thash3D( floor( pixScales.x * position.xyz ) ),\n\t\t\thash3D( floor( pixScales.y * position.xyz ) )\n\t\t);\n\t\tfloat lerpFactor = fract( log2( pixScale ) );\n\t\tfloat x = ( 1.0 - lerpFactor ) * alpha.x + lerpFactor * alpha.y;\n\t\tfloat a = min( lerpFactor, 1.0 - lerpFactor );\n\t\tvec3 cases = vec3(\n\t\t\tx * x / ( 2.0 * a * ( 1.0 - a ) ),\n\t\t\t( x - 0.5 * a ) / ( 1.0 - a ),\n\t\t\t1.0 - ( ( 1.0 - x ) * ( 1.0 - x ) / ( 2.0 * a * ( 1.0 - a ) ) )\n\t\t);\n\t\tfloat threshold = ( x < ( 1.0 - a ) )\n\t\t\t? ( ( x < a ) ? cases.x : cases.y )\n\t\t\t: cases.z;\n\t\treturn clamp( threshold , 1.0e-6, 1.0 );\n\t}\n#endif";

var alphamap_fragment = "#ifdef USE_ALPHAMAP\n\tdiffuseColor.a *= texture2D( alphaMap, vAlphaMapUv ).g;\n#endif";

var alphamap_pars_fragment = "#ifdef USE_ALPHAMAP\n\tuniform sampler2D alphaMap;\n#endif";

var alphatest_fragment = "#ifdef USE_ALPHATEST\n\t#ifdef ALPHA_TO_COVERAGE\n\tdiffuseColor.a = smoothstep( alphaTest, alphaTest + fwidth( diffuseColor.a ), diffuseColor.a );\n\tif ( diffuseColor.a == 0.0 ) discard;\n\t#else\n\tif ( diffuseColor.a < alphaTest ) discard;\n\t#endif\n#endif";

var alphatest_pars_fragment = "#ifdef USE_ALPHATEST\n\tuniform float alphaTest;\n#endif";

var aomap_fragment = "#ifdef USE_AOMAP\n\tfloat ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;\n\treflectedLight.indirectDiffuse *= ambientOcclusion;\n\t#if defined( USE_CLEARCOAT ) \n\t\tclearcoatSpecularIndirect *= ambientOcclusion;\n\t#endif\n\t#if defined( USE_SHEEN ) \n\t\tsheenSpecularIndirect *= ambientOcclusion;\n\t#endif\n\t#if defined( USE_ENVMAP ) && defined( STANDARD )\n\t\tfloat dotNV = saturate( dot( geometryNormal, geometryViewDir ) );\n\t\treflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV, ambientOcclusion, material.roughness );\n\t#endif\n#endif";

var aomap_pars_fragment = "#ifdef USE_AOMAP\n\tuniform sampler2D aoMap;\n\tuniform float aoMapIntensity;\n#endif";

var batching_pars_vertex = "#ifdef USE_BATCHING\n\t#if ! defined( GL_ANGLE_multi_draw )\n\t#define gl_DrawID _gl_DrawID\n\tuniform int _gl_DrawID;\n\t#endif\n\tuniform highp sampler2D batchingTexture;\n\tuniform highp usampler2D batchingIdTexture;\n\tmat4 getBatchingMatrix( const in float i ) {\n\t\tint size = textureSize( batchingTexture, 0 ).x;\n\t\tint j = int( i ) * 4;\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\tvec4 v1 = texelFetch( batchingTexture, ivec2( x, y ), 0 );\n\t\tvec4 v2 = texelFetch( batchingTexture, ivec2( x + 1, y ), 0 );\n\t\tvec4 v3 = texelFetch( batchingTexture, ivec2( x + 2, y ), 0 );\n\t\tvec4 v4 = texelFetch( batchingTexture, ivec2( x + 3, y ), 0 );\n\t\treturn mat4( v1, v2, v3, v4 );\n\t}\n\tfloat getIndirectIndex( const in int i ) {\n\t\tint size = textureSize( batchingIdTexture, 0 ).x;\n\t\tint x = i % size;\n\t\tint y = i / size;\n\t\treturn float( texelFetch( batchingIdTexture, ivec2( x, y ), 0 ).r );\n\t}\n#endif\n#ifdef USE_BATCHING_COLOR\n\tuniform sampler2D batchingColorTexture;\n\tvec3 getBatchingColor( const in float i ) {\n\t\tint size = textureSize( batchingColorTexture, 0 ).x;\n\t\tint j = int( i );\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\treturn texelFetch( batchingColorTexture, ivec2( x, y ), 0 ).rgb;\n\t}\n#endif";

var batching_vertex = "#ifdef USE_BATCHING\n\tmat4 batchingMatrix = getBatchingMatrix( getIndirectIndex( gl_DrawID ) );\n#endif";

var begin_vertex = "vec3 transformed = vec3( position );\n#ifdef USE_ALPHAHASH\n\tvPosition = vec3( position );\n#endif";

var beginnormal_vertex = "vec3 objectNormal = vec3( normal );\n#ifdef USE_TANGENT\n\tvec3 objectTangent = vec3( tangent.xyz );\n#endif";

var bsdfs = "float G_BlinnPhong_Implicit( ) {\n\treturn 0.25;\n}\nfloat D_BlinnPhong( const in float shininess, const in float dotNH ) {\n\treturn RECIPROCAL_PI * ( shininess * 0.5 + 1.0 ) * pow( dotNH, shininess );\n}\nvec3 BRDF_BlinnPhong( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 specularColor, const in float shininess ) {\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\tvec3 F = F_Schlick( specularColor, 1.0, dotVH );\n\tfloat G = G_BlinnPhong_Implicit( );\n\tfloat D = D_BlinnPhong( shininess, dotNH );\n\treturn F * ( G * D );\n} // validated";

var iridescence_fragment = "#ifdef USE_IRIDESCENCE\n\tconst mat3 XYZ_TO_REC709 = mat3(\n\t\t 3.2404542, -0.9692660,  0.0556434,\n\t\t-1.5371385,  1.8760108, -0.2040259,\n\t\t-0.4985314,  0.0415560,  1.0572252\n\t);\n\tvec3 Fresnel0ToIor( vec3 fresnel0 ) {\n\t\tvec3 sqrtF0 = sqrt( fresnel0 );\n\t\treturn ( vec3( 1.0 ) + sqrtF0 ) / ( vec3( 1.0 ) - sqrtF0 );\n\t}\n\tvec3 IorToFresnel0( vec3 transmittedIor, float incidentIor ) {\n\t\treturn pow2( ( transmittedIor - vec3( incidentIor ) ) / ( transmittedIor + vec3( incidentIor ) ) );\n\t}\n\tfloat IorToFresnel0( float transmittedIor, float incidentIor ) {\n\t\treturn pow2( ( transmittedIor - incidentIor ) / ( transmittedIor + incidentIor ));\n\t}\n\tvec3 evalSensitivity( float OPD, vec3 shift ) {\n\t\tfloat phase = 2.0 * PI * OPD * 1.0e-9;\n\t\tvec3 val = vec3( 5.4856e-13, 4.4201e-13, 5.2481e-13 );\n\t\tvec3 pos = vec3( 1.6810e+06, 1.7953e+06, 2.2084e+06 );\n\t\tvec3 var = vec3( 4.3278e+09, 9.3046e+09, 6.6121e+09 );\n\t\tvec3 xyz = val * sqrt( 2.0 * PI * var ) * cos( pos * phase + shift ) * exp( - pow2( phase ) * var );\n\t\txyz.x += 9.7470e-14 * sqrt( 2.0 * PI * 4.5282e+09 ) * cos( 2.2399e+06 * phase + shift[ 0 ] ) * exp( - 4.5282e+09 * pow2( phase ) );\n\t\txyz /= 1.0685e-7;\n\t\tvec3 rgb = XYZ_TO_REC709 * xyz;\n\t\treturn rgb;\n\t}\n\tvec3 evalIridescence( float outsideIOR, float eta2, float cosTheta1, float thinFilmThickness, vec3 baseF0 ) {\n\t\tvec3 I;\n\t\tfloat iridescenceIOR = mix( outsideIOR, eta2, smoothstep( 0.0, 0.03, thinFilmThickness ) );\n\t\tfloat sinTheta2Sq = pow2( outsideIOR / iridescenceIOR ) * ( 1.0 - pow2( cosTheta1 ) );\n\t\tfloat cosTheta2Sq = 1.0 - sinTheta2Sq;\n\t\tif ( cosTheta2Sq < 0.0 ) {\n\t\t\treturn vec3( 1.0 );\n\t\t}\n\t\tfloat cosTheta2 = sqrt( cosTheta2Sq );\n\t\tfloat R0 = IorToFresnel0( iridescenceIOR, outsideIOR );\n\t\tfloat R12 = F_Schlick( R0, 1.0, cosTheta1 );\n\t\tfloat T121 = 1.0 - R12;\n\t\tfloat phi12 = 0.0;\n\t\tif ( iridescenceIOR < outsideIOR ) phi12 = PI;\n\t\tfloat phi21 = PI - phi12;\n\t\tvec3 baseIOR = Fresnel0ToIor( clamp( baseF0, 0.0, 0.9999 ) );\t\tvec3 R1 = IorToFresnel0( baseIOR, iridescenceIOR );\n\t\tvec3 R23 = F_Schlick( R1, 1.0, cosTheta2 );\n\t\tvec3 phi23 = vec3( 0.0 );\n\t\tif ( baseIOR[ 0 ] < iridescenceIOR ) phi23[ 0 ] = PI;\n\t\tif ( baseIOR[ 1 ] < iridescenceIOR ) phi23[ 1 ] = PI;\n\t\tif ( baseIOR[ 2 ] < iridescenceIOR ) phi23[ 2 ] = PI;\n\t\tfloat OPD = 2.0 * iridescenceIOR * thinFilmThickness * cosTheta2;\n\t\tvec3 phi = vec3( phi21 ) + phi23;\n\t\tvec3 R123 = clamp( R12 * R23, 1e-5, 0.9999 );\n\t\tvec3 r123 = sqrt( R123 );\n\t\tvec3 Rs = pow2( T121 ) * R23 / ( vec3( 1.0 ) - R123 );\n\t\tvec3 C0 = R12 + Rs;\n\t\tI = C0;\n\t\tvec3 Cm = Rs - T121;\n\t\tfor ( int m = 1; m <= 2; ++ m ) {\n\t\t\tCm *= r123;\n\t\t\tvec3 Sm = 2.0 * evalSensitivity( float( m ) * OPD, float( m ) * phi );\n\t\t\tI += Cm * Sm;\n\t\t}\n\t\treturn max( I, vec3( 0.0 ) );\n\t}\n#endif";

var bumpmap_pars_fragment = "#ifdef USE_BUMPMAP\n\tuniform sampler2D bumpMap;\n\tuniform float bumpScale;\n\tvec2 dHdxy_fwd() {\n\t\tvec2 dSTdx = dFdx( vBumpMapUv );\n\t\tvec2 dSTdy = dFdy( vBumpMapUv );\n\t\tfloat Hll = bumpScale * texture2D( bumpMap, vBumpMapUv ).x;\n\t\tfloat dBx = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdx ).x - Hll;\n\t\tfloat dBy = bumpScale * texture2D( bumpMap, vBumpMapUv + dSTdy ).x - Hll;\n\t\treturn vec2( dBx, dBy );\n\t}\n\tvec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {\n\t\tvec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );\n\t\tvec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );\n\t\tvec3 vN = surf_norm;\n\t\tvec3 R1 = cross( vSigmaY, vN );\n\t\tvec3 R2 = cross( vN, vSigmaX );\n\t\tfloat fDet = dot( vSigmaX, R1 ) * faceDirection;\n\t\tvec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );\n\t\treturn normalize( abs( fDet ) * surf_norm - vGrad );\n\t}\n#endif";

var clipping_planes_fragment = "#if NUM_CLIPPING_PLANES > 0\n\tvec4 plane;\n\t#ifdef ALPHA_TO_COVERAGE\n\t\tfloat distanceToPlane, distanceGradient;\n\t\tfloat clipOpacity = 1.0;\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {\n\t\t\tplane = clippingPlanes[ i ];\n\t\t\tdistanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;\n\t\t\tdistanceGradient = fwidth( distanceToPlane ) / 2.0;\n\t\t\tclipOpacity *= smoothstep( - distanceGradient, distanceGradient, distanceToPlane );\n\t\t\tif ( clipOpacity == 0.0 ) discard;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t\t#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES\n\t\t\tfloat unionClipOpacity = 1.0;\n\t\t\t#pragma unroll_loop_start\n\t\t\tfor ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {\n\t\t\t\tplane = clippingPlanes[ i ];\n\t\t\t\tdistanceToPlane = - dot( vClipPosition, plane.xyz ) + plane.w;\n\t\t\t\tdistanceGradient = fwidth( distanceToPlane ) / 2.0;\n\t\t\t\tunionClipOpacity *= 1.0 - smoothstep( - distanceGradient, distanceGradient, distanceToPlane );\n\t\t\t}\n\t\t\t#pragma unroll_loop_end\n\t\t\tclipOpacity *= 1.0 - unionClipOpacity;\n\t\t#endif\n\t\tdiffuseColor.a *= clipOpacity;\n\t\tif ( diffuseColor.a == 0.0 ) discard;\n\t#else\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {\n\t\t\tplane = clippingPlanes[ i ];\n\t\t\tif ( dot( vClipPosition, plane.xyz ) > plane.w ) discard;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t\t#if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES\n\t\t\tbool clipped = true;\n\t\t\t#pragma unroll_loop_start\n\t\t\tfor ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {\n\t\t\t\tplane = clippingPlanes[ i ];\n\t\t\t\tclipped = ( dot( vClipPosition, plane.xyz ) > plane.w ) && clipped;\n\t\t\t}\n\t\t\t#pragma unroll_loop_end\n\t\t\tif ( clipped ) discard;\n\t\t#endif\n\t#endif\n#endif";

var clipping_planes_pars_fragment = "#if NUM_CLIPPING_PLANES > 0\n\tvarying vec3 vClipPosition;\n\tuniform vec4 clippingPlanes[ NUM_CLIPPING_PLANES ];\n#endif";

var clipping_planes_pars_vertex = "#if NUM_CLIPPING_PLANES > 0\n\tvarying vec3 vClipPosition;\n#endif";

var clipping_planes_vertex = "#if NUM_CLIPPING_PLANES > 0\n\tvClipPosition = - mvPosition.xyz;\n#endif";

var color_fragment = "#if defined( USE_COLOR_ALPHA )\n\tdiffuseColor *= vColor;\n#elif defined( USE_COLOR )\n\tdiffuseColor.rgb *= vColor;\n#endif";

var color_pars_fragment = "#if defined( USE_COLOR_ALPHA )\n\tvarying vec4 vColor;\n#elif defined( USE_COLOR )\n\tvarying vec3 vColor;\n#endif";

var color_pars_vertex = "#if defined( USE_COLOR_ALPHA )\n\tvarying vec4 vColor;\n#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )\n\tvarying vec3 vColor;\n#endif";

var color_vertex = "#if defined( USE_COLOR_ALPHA )\n\tvColor = vec4( 1.0 );\n#elif defined( USE_COLOR ) || defined( USE_INSTANCING_COLOR ) || defined( USE_BATCHING_COLOR )\n\tvColor = vec3( 1.0 );\n#endif\n#ifdef USE_COLOR\n\tvColor *= color;\n#endif\n#ifdef USE_INSTANCING_COLOR\n\tvColor.xyz *= instanceColor.xyz;\n#endif\n#ifdef USE_BATCHING_COLOR\n\tvec3 batchingColor = getBatchingColor( getIndirectIndex( gl_DrawID ) );\n\tvColor.xyz *= batchingColor.xyz;\n#endif";

var common = "#define PI 3.141592653589793\n#define PI2 6.283185307179586\n#define PI_HALF 1.5707963267948966\n#define RECIPROCAL_PI 0.3183098861837907\n#define RECIPROCAL_PI2 0.15915494309189535\n#define EPSILON 1e-6\n#ifndef saturate\n#define saturate( a ) clamp( a, 0.0, 1.0 )\n#endif\n#define whiteComplement( a ) ( 1.0 - saturate( a ) )\nfloat pow2( const in float x ) { return x*x; }\nvec3 pow2( const in vec3 x ) { return x*x; }\nfloat pow3( const in float x ) { return x*x*x; }\nfloat pow4( const in float x ) { float x2 = x*x; return x2*x2; }\nfloat max3( const in vec3 v ) { return max( max( v.x, v.y ), v.z ); }\nfloat average( const in vec3 v ) { return dot( v, vec3( 0.3333333 ) ); }\nhighp float rand( const in vec2 uv ) {\n\tconst highp float a = 12.9898, b = 78.233, c = 43758.5453;\n\thighp float dt = dot( uv.xy, vec2( a,b ) ), sn = mod( dt, PI );\n\treturn fract( sin( sn ) * c );\n}\n#ifdef HIGH_PRECISION\n\tfloat precisionSafeLength( vec3 v ) { return length( v ); }\n#else\n\tfloat precisionSafeLength( vec3 v ) {\n\t\tfloat maxComponent = max3( abs( v ) );\n\t\treturn length( v / maxComponent ) * maxComponent;\n\t}\n#endif\nstruct IncidentLight {\n\tvec3 color;\n\tvec3 direction;\n\tbool visible;\n};\nstruct ReflectedLight {\n\tvec3 directDiffuse;\n\tvec3 directSpecular;\n\tvec3 indirectDiffuse;\n\tvec3 indirectSpecular;\n};\n#ifdef USE_ALPHAHASH\n\tvarying vec3 vPosition;\n#endif\nvec3 transformDirection( in vec3 dir, in mat4 matrix ) {\n\treturn normalize( ( matrix * vec4( dir, 0.0 ) ).xyz );\n}\nvec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {\n\treturn normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );\n}\nmat3 transposeMat3( const in mat3 m ) {\n\tmat3 tmp;\n\ttmp[ 0 ] = vec3( m[ 0 ].x, m[ 1 ].x, m[ 2 ].x );\n\ttmp[ 1 ] = vec3( m[ 0 ].y, m[ 1 ].y, m[ 2 ].y );\n\ttmp[ 2 ] = vec3( m[ 0 ].z, m[ 1 ].z, m[ 2 ].z );\n\treturn tmp;\n}\nbool isPerspectiveMatrix( mat4 m ) {\n\treturn m[ 2 ][ 3 ] == - 1.0;\n}\nvec2 equirectUv( in vec3 dir ) {\n\tfloat u = atan( dir.z, dir.x ) * RECIPROCAL_PI2 + 0.5;\n\tfloat v = asin( clamp( dir.y, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;\n\treturn vec2( u, v );\n}\nvec3 BRDF_Lambert( const in vec3 diffuseColor ) {\n\treturn RECIPROCAL_PI * diffuseColor;\n}\nvec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {\n\tfloat fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );\n\treturn f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );\n}\nfloat F_Schlick( const in float f0, const in float f90, const in float dotVH ) {\n\tfloat fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );\n\treturn f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );\n} // validated";

var cube_uv_reflection_fragment = "#ifdef ENVMAP_TYPE_CUBE_UV\n\t#define cubeUV_minMipLevel 4.0\n\t#define cubeUV_minTileSize 16.0\n\tfloat getFace( vec3 direction ) {\n\t\tvec3 absDirection = abs( direction );\n\t\tfloat face = - 1.0;\n\t\tif ( absDirection.x > absDirection.z ) {\n\t\t\tif ( absDirection.x > absDirection.y )\n\t\t\t\tface = direction.x > 0.0 ? 0.0 : 3.0;\n\t\t\telse\n\t\t\t\tface = direction.y > 0.0 ? 1.0 : 4.0;\n\t\t} else {\n\t\t\tif ( absDirection.z > absDirection.y )\n\t\t\t\tface = direction.z > 0.0 ? 2.0 : 5.0;\n\t\t\telse\n\t\t\t\tface = direction.y > 0.0 ? 1.0 : 4.0;\n\t\t}\n\t\treturn face;\n\t}\n\tvec2 getUV( vec3 direction, float face ) {\n\t\tvec2 uv;\n\t\tif ( face == 0.0 ) {\n\t\t\tuv = vec2( direction.z, direction.y ) / abs( direction.x );\n\t\t} else if ( face == 1.0 ) {\n\t\t\tuv = vec2( - direction.x, - direction.z ) / abs( direction.y );\n\t\t} else if ( face == 2.0 ) {\n\t\t\tuv = vec2( - direction.x, direction.y ) / abs( direction.z );\n\t\t} else if ( face == 3.0 ) {\n\t\t\tuv = vec2( - direction.z, direction.y ) / abs( direction.x );\n\t\t} else if ( face == 4.0 ) {\n\t\t\tuv = vec2( - direction.x, direction.z ) / abs( direction.y );\n\t\t} else {\n\t\t\tuv = vec2( direction.x, direction.y ) / abs( direction.z );\n\t\t}\n\t\treturn 0.5 * ( uv + 1.0 );\n\t}\n\tvec3 bilinearCubeUV( sampler2D envMap, vec3 direction, float mipInt ) {\n\t\tfloat face = getFace( direction );\n\t\tfloat filterInt = max( cubeUV_minMipLevel - mipInt, 0.0 );\n\t\tmipInt = max( mipInt, cubeUV_minMipLevel );\n\t\tfloat faceSize = exp2( mipInt );\n\t\thighp vec2 uv = getUV( direction, face ) * ( faceSize - 2.0 ) + 1.0;\n\t\tif ( face > 2.0 ) {\n\t\t\tuv.y += faceSize;\n\t\t\tface -= 3.0;\n\t\t}\n\t\tuv.x += face * faceSize;\n\t\tuv.x += filterInt * 3.0 * cubeUV_minTileSize;\n\t\tuv.y += 4.0 * ( exp2( CUBEUV_MAX_MIP ) - faceSize );\n\t\tuv.x *= CUBEUV_TEXEL_WIDTH;\n\t\tuv.y *= CUBEUV_TEXEL_HEIGHT;\n\t\t#ifdef texture2DGradEXT\n\t\t\treturn texture2DGradEXT( envMap, uv, vec2( 0.0 ), vec2( 0.0 ) ).rgb;\n\t\t#else\n\t\t\treturn texture2D( envMap, uv ).rgb;\n\t\t#endif\n\t}\n\t#define cubeUV_r0 1.0\n\t#define cubeUV_m0 - 2.0\n\t#define cubeUV_r1 0.8\n\t#define cubeUV_m1 - 1.0\n\t#define cubeUV_r4 0.4\n\t#define cubeUV_m4 2.0\n\t#define cubeUV_r5 0.305\n\t#define cubeUV_m5 3.0\n\t#define cubeUV_r6 0.21\n\t#define cubeUV_m6 4.0\n\tfloat roughnessToMip( float roughness ) {\n\t\tfloat mip = 0.0;\n\t\tif ( roughness >= cubeUV_r1 ) {\n\t\t\tmip = ( cubeUV_r0 - roughness ) * ( cubeUV_m1 - cubeUV_m0 ) / ( cubeUV_r0 - cubeUV_r1 ) + cubeUV_m0;\n\t\t} else if ( roughness >= cubeUV_r4 ) {\n\t\t\tmip = ( cubeUV_r1 - roughness ) * ( cubeUV_m4 - cubeUV_m1 ) / ( cubeUV_r1 - cubeUV_r4 ) + cubeUV_m1;\n\t\t} else if ( roughness >= cubeUV_r5 ) {\n\t\t\tmip = ( cubeUV_r4 - roughness ) * ( cubeUV_m5 - cubeUV_m4 ) / ( cubeUV_r4 - cubeUV_r5 ) + cubeUV_m4;\n\t\t} else if ( roughness >= cubeUV_r6 ) {\n\t\t\tmip = ( cubeUV_r5 - roughness ) * ( cubeUV_m6 - cubeUV_m5 ) / ( cubeUV_r5 - cubeUV_r6 ) + cubeUV_m5;\n\t\t} else {\n\t\t\tmip = - 2.0 * log2( 1.16 * roughness );\t\t}\n\t\treturn mip;\n\t}\n\tvec4 textureCubeUV( sampler2D envMap, vec3 sampleDir, float roughness ) {\n\t\tfloat mip = clamp( roughnessToMip( roughness ), cubeUV_m0, CUBEUV_MAX_MIP );\n\t\tfloat mipF = fract( mip );\n\t\tfloat mipInt = floor( mip );\n\t\tvec3 color0 = bilinearCubeUV( envMap, sampleDir, mipInt );\n\t\tif ( mipF == 0.0 ) {\n\t\t\treturn vec4( color0, 1.0 );\n\t\t} else {\n\t\t\tvec3 color1 = bilinearCubeUV( envMap, sampleDir, mipInt + 1.0 );\n\t\t\treturn vec4( mix( color0, color1, mipF ), 1.0 );\n\t\t}\n\t}\n#endif";

var defaultnormal_vertex = "vec3 transformedNormal = objectNormal;\n#ifdef USE_TANGENT\n\tvec3 transformedTangent = objectTangent;\n#endif\n#ifdef USE_BATCHING\n\tmat3 bm = mat3( batchingMatrix );\n\ttransformedNormal /= vec3( dot( bm[ 0 ], bm[ 0 ] ), dot( bm[ 1 ], bm[ 1 ] ), dot( bm[ 2 ], bm[ 2 ] ) );\n\ttransformedNormal = bm * transformedNormal;\n\t#ifdef USE_TANGENT\n\t\ttransformedTangent = bm * transformedTangent;\n\t#endif\n#endif\n#ifdef USE_INSTANCING\n\tmat3 im = mat3( instanceMatrix );\n\ttransformedNormal /= vec3( dot( im[ 0 ], im[ 0 ] ), dot( im[ 1 ], im[ 1 ] ), dot( im[ 2 ], im[ 2 ] ) );\n\ttransformedNormal = im * transformedNormal;\n\t#ifdef USE_TANGENT\n\t\ttransformedTangent = im * transformedTangent;\n\t#endif\n#endif\ntransformedNormal = normalMatrix * transformedNormal;\n#ifdef FLIP_SIDED\n\ttransformedNormal = - transformedNormal;\n#endif\n#ifdef USE_TANGENT\n\ttransformedTangent = ( modelViewMatrix * vec4( transformedTangent, 0.0 ) ).xyz;\n\t#ifdef FLIP_SIDED\n\t\ttransformedTangent = - transformedTangent;\n\t#endif\n#endif";

var displacementmap_pars_vertex = "#ifdef USE_DISPLACEMENTMAP\n\tuniform sampler2D displacementMap;\n\tuniform float displacementScale;\n\tuniform float displacementBias;\n#endif";

var displacementmap_vertex = "#ifdef USE_DISPLACEMENTMAP\n\ttransformed += normalize( objectNormal ) * ( texture2D( displacementMap, vDisplacementMapUv ).x * displacementScale + displacementBias );\n#endif";

var emissivemap_fragment = "#ifdef USE_EMISSIVEMAP\n\tvec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );\n\ttotalEmissiveRadiance *= emissiveColor.rgb;\n#endif";

var emissivemap_pars_fragment = "#ifdef USE_EMISSIVEMAP\n\tuniform sampler2D emissiveMap;\n#endif";

var colorspace_fragment = "gl_FragColor = linearToOutputTexel( gl_FragColor );";

var colorspace_pars_fragment = "\nconst mat3 LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = mat3(\n\tvec3( 0.8224621, 0.177538, 0.0 ),\n\tvec3( 0.0331941, 0.9668058, 0.0 ),\n\tvec3( 0.0170827, 0.0723974, 0.9105199 )\n);\nconst mat3 LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = mat3(\n\tvec3( 1.2249401, - 0.2249404, 0.0 ),\n\tvec3( - 0.0420569, 1.0420571, 0.0 ),\n\tvec3( - 0.0196376, - 0.0786361, 1.0982735 )\n);\nvec4 LinearSRGBToLinearDisplayP3( in vec4 value ) {\n\treturn vec4( value.rgb * LINEAR_SRGB_TO_LINEAR_DISPLAY_P3, value.a );\n}\nvec4 LinearDisplayP3ToLinearSRGB( in vec4 value ) {\n\treturn vec4( value.rgb * LINEAR_DISPLAY_P3_TO_LINEAR_SRGB, value.a );\n}\nvec4 LinearTransferOETF( in vec4 value ) {\n\treturn value;\n}\nvec4 sRGBTransferOETF( in vec4 value ) {\n\treturn vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );\n}";

var envmap_fragment = "#ifdef USE_ENVMAP\n\t#ifdef ENV_WORLDPOS\n\t\tvec3 cameraToFrag;\n\t\tif ( isOrthographic ) {\n\t\t\tcameraToFrag = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );\n\t\t} else {\n\t\t\tcameraToFrag = normalize( vWorldPosition - cameraPosition );\n\t\t}\n\t\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\t\t#ifdef ENVMAP_MODE_REFLECTION\n\t\t\tvec3 reflectVec = reflect( cameraToFrag, worldNormal );\n\t\t#else\n\t\t\tvec3 reflectVec = refract( cameraToFrag, worldNormal, refractionRatio );\n\t\t#endif\n\t#else\n\t\tvec3 reflectVec = vReflect;\n\t#endif\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tvec4 envColor = textureCube( envMap, envMapRotation * vec3( flipEnvMap * reflectVec.x, reflectVec.yz ) );\n\t#else\n\t\tvec4 envColor = vec4( 0.0 );\n\t#endif\n\t#ifdef ENVMAP_BLENDING_MULTIPLY\n\t\toutgoingLight = mix( outgoingLight, outgoingLight * envColor.xyz, specularStrength * reflectivity );\n\t#elif defined( ENVMAP_BLENDING_MIX )\n\t\toutgoingLight = mix( outgoingLight, envColor.xyz, specularStrength * reflectivity );\n\t#elif defined( ENVMAP_BLENDING_ADD )\n\t\toutgoingLight += envColor.xyz * specularStrength * reflectivity;\n\t#endif\n#endif";

var envmap_common_pars_fragment = "#ifdef USE_ENVMAP\n\tuniform float envMapIntensity;\n\tuniform float flipEnvMap;\n\tuniform mat3 envMapRotation;\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tuniform samplerCube envMap;\n\t#else\n\t\tuniform sampler2D envMap;\n\t#endif\n\t\n#endif";

var envmap_pars_fragment = "#ifdef USE_ENVMAP\n\tuniform float reflectivity;\n\t#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )\n\t\t#define ENV_WORLDPOS\n\t#endif\n\t#ifdef ENV_WORLDPOS\n\t\tvarying vec3 vWorldPosition;\n\t\tuniform float refractionRatio;\n\t#else\n\t\tvarying vec3 vReflect;\n\t#endif\n#endif";

var envmap_pars_vertex = "#ifdef USE_ENVMAP\n\t#if defined( USE_BUMPMAP ) || defined( USE_NORMALMAP ) || defined( PHONG ) || defined( LAMBERT )\n\t\t#define ENV_WORLDPOS\n\t#endif\n\t#ifdef ENV_WORLDPOS\n\t\t\n\t\tvarying vec3 vWorldPosition;\n\t#else\n\t\tvarying vec3 vReflect;\n\t\tuniform float refractionRatio;\n\t#endif\n#endif";

var envmap_vertex = "#ifdef USE_ENVMAP\n\t#ifdef ENV_WORLDPOS\n\t\tvWorldPosition = worldPosition.xyz;\n\t#else\n\t\tvec3 cameraToVertex;\n\t\tif ( isOrthographic ) {\n\t\t\tcameraToVertex = normalize( vec3( - viewMatrix[ 0 ][ 2 ], - viewMatrix[ 1 ][ 2 ], - viewMatrix[ 2 ][ 2 ] ) );\n\t\t} else {\n\t\t\tcameraToVertex = normalize( worldPosition.xyz - cameraPosition );\n\t\t}\n\t\tvec3 worldNormal = inverseTransformDirection( transformedNormal, viewMatrix );\n\t\t#ifdef ENVMAP_MODE_REFLECTION\n\t\t\tvReflect = reflect( cameraToVertex, worldNormal );\n\t\t#else\n\t\t\tvReflect = refract( cameraToVertex, worldNormal, refractionRatio );\n\t\t#endif\n\t#endif\n#endif";

var fog_vertex = "#ifdef USE_FOG\n\tvFogDepth = - mvPosition.z;\n#endif";

var fog_pars_vertex = "#ifdef USE_FOG\n\tvarying float vFogDepth;\n#endif";

var fog_fragment = "#ifdef USE_FOG\n\t#ifdef FOG_EXP2\n\t\tfloat fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );\n\t#else\n\t\tfloat fogFactor = smoothstep( fogNear, fogFar, vFogDepth );\n\t#endif\n\tgl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );\n#endif";

var fog_pars_fragment = "#ifdef USE_FOG\n\tuniform vec3 fogColor;\n\tvarying float vFogDepth;\n\t#ifdef FOG_EXP2\n\t\tuniform float fogDensity;\n\t#else\n\t\tuniform float fogNear;\n\t\tuniform float fogFar;\n\t#endif\n#endif";

var gradientmap_pars_fragment = "#ifdef USE_GRADIENTMAP\n\tuniform sampler2D gradientMap;\n#endif\nvec3 getGradientIrradiance( vec3 normal, vec3 lightDirection ) {\n\tfloat dotNL = dot( normal, lightDirection );\n\tvec2 coord = vec2( dotNL * 0.5 + 0.5, 0.0 );\n\t#ifdef USE_GRADIENTMAP\n\t\treturn vec3( texture2D( gradientMap, coord ).r );\n\t#else\n\t\tvec2 fw = fwidth( coord ) * 0.5;\n\t\treturn mix( vec3( 0.7 ), vec3( 1.0 ), smoothstep( 0.7 - fw.x, 0.7 + fw.x, coord.x ) );\n\t#endif\n}";

var lightmap_pars_fragment = "#ifdef USE_LIGHTMAP\n\tuniform sampler2D lightMap;\n\tuniform float lightMapIntensity;\n#endif";

var lights_lambert_fragment = "LambertMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;\nmaterial.specularStrength = specularStrength;";

var lights_lambert_pars_fragment = "varying vec3 vViewPosition;\nstruct LambertMaterial {\n\tvec3 diffuseColor;\n\tfloat specularStrength;\n};\nvoid RE_Direct_Lambert( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Lambert( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in LambertMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_Lambert\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Lambert";

var lights_pars_begin = "uniform bool receiveShadow;\nuniform vec3 ambientLightColor;\n#if defined( USE_LIGHT_PROBES )\n\tuniform vec3 lightProbe[ 9 ];\n#endif\nvec3 shGetIrradianceAt( in vec3 normal, in vec3 shCoefficients[ 9 ] ) {\n\tfloat x = normal.x, y = normal.y, z = normal.z;\n\tvec3 result = shCoefficients[ 0 ] * 0.886227;\n\tresult += shCoefficients[ 1 ] * 2.0 * 0.511664 * y;\n\tresult += shCoefficients[ 2 ] * 2.0 * 0.511664 * z;\n\tresult += shCoefficients[ 3 ] * 2.0 * 0.511664 * x;\n\tresult += shCoefficients[ 4 ] * 2.0 * 0.429043 * x * y;\n\tresult += shCoefficients[ 5 ] * 2.0 * 0.429043 * y * z;\n\tresult += shCoefficients[ 6 ] * ( 0.743125 * z * z - 0.247708 );\n\tresult += shCoefficients[ 7 ] * 2.0 * 0.429043 * x * z;\n\tresult += shCoefficients[ 8 ] * 0.429043 * ( x * x - y * y );\n\treturn result;\n}\nvec3 getLightProbeIrradiance( const in vec3 lightProbe[ 9 ], const in vec3 normal ) {\n\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\tvec3 irradiance = shGetIrradianceAt( worldNormal, lightProbe );\n\treturn irradiance;\n}\nvec3 getAmbientLightIrradiance( const in vec3 ambientLightColor ) {\n\tvec3 irradiance = ambientLightColor;\n\treturn irradiance;\n}\nfloat getDistanceAttenuation( const in float lightDistance, const in float cutoffDistance, const in float decayExponent ) {\n\tfloat distanceFalloff = 1.0 / max( pow( lightDistance, decayExponent ), 0.01 );\n\tif ( cutoffDistance > 0.0 ) {\n\t\tdistanceFalloff *= pow2( saturate( 1.0 - pow4( lightDistance / cutoffDistance ) ) );\n\t}\n\treturn distanceFalloff;\n}\nfloat getSpotAttenuation( const in float coneCosine, const in float penumbraCosine, const in float angleCosine ) {\n\treturn smoothstep( coneCosine, penumbraCosine, angleCosine );\n}\n#if NUM_DIR_LIGHTS > 0\n\tstruct DirectionalLight {\n\t\tvec3 direction;\n\t\tvec3 color;\n\t};\n\tuniform DirectionalLight directionalLights[ NUM_DIR_LIGHTS ];\n\tvoid getDirectionalLightInfo( const in DirectionalLight directionalLight, out IncidentLight light ) {\n\t\tlight.color = directionalLight.color;\n\t\tlight.direction = directionalLight.direction;\n\t\tlight.visible = true;\n\t}\n#endif\n#if NUM_POINT_LIGHTS > 0\n\tstruct PointLight {\n\t\tvec3 position;\n\t\tvec3 color;\n\t\tfloat distance;\n\t\tfloat decay;\n\t};\n\tuniform PointLight pointLights[ NUM_POINT_LIGHTS ];\n\tvoid getPointLightInfo( const in PointLight pointLight, const in vec3 geometryPosition, out IncidentLight light ) {\n\t\tvec3 lVector = pointLight.position - geometryPosition;\n\t\tlight.direction = normalize( lVector );\n\t\tfloat lightDistance = length( lVector );\n\t\tlight.color = pointLight.color;\n\t\tlight.color *= getDistanceAttenuation( lightDistance, pointLight.distance, pointLight.decay );\n\t\tlight.visible = ( light.color != vec3( 0.0 ) );\n\t}\n#endif\n#if NUM_SPOT_LIGHTS > 0\n\tstruct SpotLight {\n\t\tvec3 position;\n\t\tvec3 direction;\n\t\tvec3 color;\n\t\tfloat distance;\n\t\tfloat decay;\n\t\tfloat coneCos;\n\t\tfloat penumbraCos;\n\t};\n\tuniform SpotLight spotLights[ NUM_SPOT_LIGHTS ];\n\tvoid getSpotLightInfo( const in SpotLight spotLight, const in vec3 geometryPosition, out IncidentLight light ) {\n\t\tvec3 lVector = spotLight.position - geometryPosition;\n\t\tlight.direction = normalize( lVector );\n\t\tfloat angleCos = dot( light.direction, spotLight.direction );\n\t\tfloat spotAttenuation = getSpotAttenuation( spotLight.coneCos, spotLight.penumbraCos, angleCos );\n\t\tif ( spotAttenuation > 0.0 ) {\n\t\t\tfloat lightDistance = length( lVector );\n\t\t\tlight.color = spotLight.color * spotAttenuation;\n\t\t\tlight.color *= getDistanceAttenuation( lightDistance, spotLight.distance, spotLight.decay );\n\t\t\tlight.visible = ( light.color != vec3( 0.0 ) );\n\t\t} else {\n\t\t\tlight.color = vec3( 0.0 );\n\t\t\tlight.visible = false;\n\t\t}\n\t}\n#endif\n#if NUM_RECT_AREA_LIGHTS > 0\n\tstruct RectAreaLight {\n\t\tvec3 color;\n\t\tvec3 position;\n\t\tvec3 halfWidth;\n\t\tvec3 halfHeight;\n\t};\n\tuniform sampler2D ltc_1;\tuniform sampler2D ltc_2;\n\tuniform RectAreaLight rectAreaLights[ NUM_RECT_AREA_LIGHTS ];\n#endif\n#if NUM_HEMI_LIGHTS > 0\n\tstruct HemisphereLight {\n\t\tvec3 direction;\n\t\tvec3 skyColor;\n\t\tvec3 groundColor;\n\t};\n\tuniform HemisphereLight hemisphereLights[ NUM_HEMI_LIGHTS ];\n\tvec3 getHemisphereLightIrradiance( const in HemisphereLight hemiLight, const in vec3 normal ) {\n\t\tfloat dotNL = dot( normal, hemiLight.direction );\n\t\tfloat hemiDiffuseWeight = 0.5 * dotNL + 0.5;\n\t\tvec3 irradiance = mix( hemiLight.groundColor, hemiLight.skyColor, hemiDiffuseWeight );\n\t\treturn irradiance;\n\t}\n#endif";

var envmap_physical_pars_fragment = "#ifdef USE_ENVMAP\n\tvec3 getIBLIrradiance( const in vec3 normal ) {\n\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\tvec3 worldNormal = inverseTransformDirection( normal, viewMatrix );\n\t\t\tvec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, 1.0 );\n\t\t\treturn PI * envMapColor.rgb * envMapIntensity;\n\t\t#else\n\t\t\treturn vec3( 0.0 );\n\t\t#endif\n\t}\n\tvec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {\n\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\tvec3 reflectVec = reflect( - viewDir, normal );\n\t\t\treflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );\n\t\t\treflectVec = inverseTransformDirection( reflectVec, viewMatrix );\n\t\t\tvec4 envMapColor = textureCubeUV( envMap, envMapRotation * reflectVec, roughness );\n\t\t\treturn envMapColor.rgb * envMapIntensity;\n\t\t#else\n\t\t\treturn vec3( 0.0 );\n\t\t#endif\n\t}\n\t#ifdef USE_ANISOTROPY\n\t\tvec3 getIBLAnisotropyRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness, const in vec3 bitangent, const in float anisotropy ) {\n\t\t\t#ifdef ENVMAP_TYPE_CUBE_UV\n\t\t\t\tvec3 bentNormal = cross( bitangent, viewDir );\n\t\t\t\tbentNormal = normalize( cross( bentNormal, bitangent ) );\n\t\t\t\tbentNormal = normalize( mix( bentNormal, normal, pow2( pow2( 1.0 - anisotropy * ( 1.0 - roughness ) ) ) ) );\n\t\t\t\treturn getIBLRadiance( viewDir, bentNormal, roughness );\n\t\t\t#else\n\t\t\t\treturn vec3( 0.0 );\n\t\t\t#endif\n\t\t}\n\t#endif\n#endif";

var lights_toon_fragment = "ToonMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;";

var lights_toon_pars_fragment = "varying vec3 vViewPosition;\nstruct ToonMaterial {\n\tvec3 diffuseColor;\n};\nvoid RE_Direct_Toon( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {\n\tvec3 irradiance = getGradientIrradiance( geometryNormal, directLight.direction ) * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Toon( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in ToonMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_Toon\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Toon";

var lights_phong_fragment = "BlinnPhongMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb;\nmaterial.specularColor = specular;\nmaterial.specularShininess = shininess;\nmaterial.specularStrength = specularStrength;";

var lights_phong_pars_fragment = "varying vec3 vViewPosition;\nstruct BlinnPhongMaterial {\n\tvec3 diffuseColor;\n\tvec3 specularColor;\n\tfloat specularShininess;\n\tfloat specularStrength;\n};\nvoid RE_Direct_BlinnPhong( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n\treflectedLight.directSpecular += irradiance * BRDF_BlinnPhong( directLight.direction, geometryViewDir, geometryNormal, material.specularColor, material.specularShininess ) * material.specularStrength;\n}\nvoid RE_IndirectDiffuse_BlinnPhong( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in BlinnPhongMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\n#define RE_Direct\t\t\t\tRE_Direct_BlinnPhong\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_BlinnPhong";

var lights_physical_fragment = "PhysicalMaterial material;\nmaterial.diffuseColor = diffuseColor.rgb * ( 1.0 - metalnessFactor );\nvec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );\nfloat geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );\nmaterial.roughness = max( roughnessFactor, 0.0525 );material.roughness += geometryRoughness;\nmaterial.roughness = min( material.roughness, 1.0 );\n#ifdef IOR\n\tmaterial.ior = ior;\n\t#ifdef USE_SPECULAR\n\t\tfloat specularIntensityFactor = specularIntensity;\n\t\tvec3 specularColorFactor = specularColor;\n\t\t#ifdef USE_SPECULAR_COLORMAP\n\t\t\tspecularColorFactor *= texture2D( specularColorMap, vSpecularColorMapUv ).rgb;\n\t\t#endif\n\t\t#ifdef USE_SPECULAR_INTENSITYMAP\n\t\t\tspecularIntensityFactor *= texture2D( specularIntensityMap, vSpecularIntensityMapUv ).a;\n\t\t#endif\n\t\tmaterial.specularF90 = mix( specularIntensityFactor, 1.0, metalnessFactor );\n\t#else\n\t\tfloat specularIntensityFactor = 1.0;\n\t\tvec3 specularColorFactor = vec3( 1.0 );\n\t\tmaterial.specularF90 = 1.0;\n\t#endif\n\tmaterial.specularColor = mix( min( pow2( ( material.ior - 1.0 ) / ( material.ior + 1.0 ) ) * specularColorFactor, vec3( 1.0 ) ) * specularIntensityFactor, diffuseColor.rgb, metalnessFactor );\n#else\n\tmaterial.specularColor = mix( vec3( 0.04 ), diffuseColor.rgb, metalnessFactor );\n\tmaterial.specularF90 = 1.0;\n#endif\n#ifdef USE_CLEARCOAT\n\tmaterial.clearcoat = clearcoat;\n\tmaterial.clearcoatRoughness = clearcoatRoughness;\n\tmaterial.clearcoatF0 = vec3( 0.04 );\n\tmaterial.clearcoatF90 = 1.0;\n\t#ifdef USE_CLEARCOATMAP\n\t\tmaterial.clearcoat *= texture2D( clearcoatMap, vClearcoatMapUv ).x;\n\t#endif\n\t#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\t\tmaterial.clearcoatRoughness *= texture2D( clearcoatRoughnessMap, vClearcoatRoughnessMapUv ).y;\n\t#endif\n\tmaterial.clearcoat = saturate( material.clearcoat );\tmaterial.clearcoatRoughness = max( material.clearcoatRoughness, 0.0525 );\n\tmaterial.clearcoatRoughness += geometryRoughness;\n\tmaterial.clearcoatRoughness = min( material.clearcoatRoughness, 1.0 );\n#endif\n#ifdef USE_DISPERSION\n\tmaterial.dispersion = dispersion;\n#endif\n#ifdef USE_IRIDESCENCE\n\tmaterial.iridescence = iridescence;\n\tmaterial.iridescenceIOR = iridescenceIOR;\n\t#ifdef USE_IRIDESCENCEMAP\n\t\tmaterial.iridescence *= texture2D( iridescenceMap, vIridescenceMapUv ).r;\n\t#endif\n\t#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\t\tmaterial.iridescenceThickness = (iridescenceThicknessMaximum - iridescenceThicknessMinimum) * texture2D( iridescenceThicknessMap, vIridescenceThicknessMapUv ).g + iridescenceThicknessMinimum;\n\t#else\n\t\tmaterial.iridescenceThickness = iridescenceThicknessMaximum;\n\t#endif\n#endif\n#ifdef USE_SHEEN\n\tmaterial.sheenColor = sheenColor;\n\t#ifdef USE_SHEEN_COLORMAP\n\t\tmaterial.sheenColor *= texture2D( sheenColorMap, vSheenColorMapUv ).rgb;\n\t#endif\n\tmaterial.sheenRoughness = clamp( sheenRoughness, 0.07, 1.0 );\n\t#ifdef USE_SHEEN_ROUGHNESSMAP\n\t\tmaterial.sheenRoughness *= texture2D( sheenRoughnessMap, vSheenRoughnessMapUv ).a;\n\t#endif\n#endif\n#ifdef USE_ANISOTROPY\n\t#ifdef USE_ANISOTROPYMAP\n\t\tmat2 anisotropyMat = mat2( anisotropyVector.x, anisotropyVector.y, - anisotropyVector.y, anisotropyVector.x );\n\t\tvec3 anisotropyPolar = texture2D( anisotropyMap, vAnisotropyMapUv ).rgb;\n\t\tvec2 anisotropyV = anisotropyMat * normalize( 2.0 * anisotropyPolar.rg - vec2( 1.0 ) ) * anisotropyPolar.b;\n\t#else\n\t\tvec2 anisotropyV = anisotropyVector;\n\t#endif\n\tmaterial.anisotropy = length( anisotropyV );\n\tif( material.anisotropy == 0.0 ) {\n\t\tanisotropyV = vec2( 1.0, 0.0 );\n\t} else {\n\t\tanisotropyV /= material.anisotropy;\n\t\tmaterial.anisotropy = saturate( material.anisotropy );\n\t}\n\tmaterial.alphaT = mix( pow2( material.roughness ), 1.0, pow2( material.anisotropy ) );\n\tmaterial.anisotropyT = tbn[ 0 ] * anisotropyV.x + tbn[ 1 ] * anisotropyV.y;\n\tmaterial.anisotropyB = tbn[ 1 ] * anisotropyV.x - tbn[ 0 ] * anisotropyV.y;\n#endif";

var lights_physical_pars_fragment = "struct PhysicalMaterial {\n\tvec3 diffuseColor;\n\tfloat roughness;\n\tvec3 specularColor;\n\tfloat specularF90;\n\tfloat dispersion;\n\t#ifdef USE_CLEARCOAT\n\t\tfloat clearcoat;\n\t\tfloat clearcoatRoughness;\n\t\tvec3 clearcoatF0;\n\t\tfloat clearcoatF90;\n\t#endif\n\t#ifdef USE_IRIDESCENCE\n\t\tfloat iridescence;\n\t\tfloat iridescenceIOR;\n\t\tfloat iridescenceThickness;\n\t\tvec3 iridescenceFresnel;\n\t\tvec3 iridescenceF0;\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tvec3 sheenColor;\n\t\tfloat sheenRoughness;\n\t#endif\n\t#ifdef IOR\n\t\tfloat ior;\n\t#endif\n\t#ifdef USE_TRANSMISSION\n\t\tfloat transmission;\n\t\tfloat transmissionAlpha;\n\t\tfloat thickness;\n\t\tfloat attenuationDistance;\n\t\tvec3 attenuationColor;\n\t#endif\n\t#ifdef USE_ANISOTROPY\n\t\tfloat anisotropy;\n\t\tfloat alphaT;\n\t\tvec3 anisotropyT;\n\t\tvec3 anisotropyB;\n\t#endif\n};\nvec3 clearcoatSpecularDirect = vec3( 0.0 );\nvec3 clearcoatSpecularIndirect = vec3( 0.0 );\nvec3 sheenSpecularDirect = vec3( 0.0 );\nvec3 sheenSpecularIndirect = vec3(0.0 );\nvec3 Schlick_to_F0( const in vec3 f, const in float f90, const in float dotVH ) {\n    float x = clamp( 1.0 - dotVH, 0.0, 1.0 );\n    float x2 = x * x;\n    float x5 = clamp( x * x2 * x2, 0.0, 0.9999 );\n    return ( f - vec3( f90 ) * x5 ) / ( 1.0 - x5 );\n}\nfloat V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {\n\tfloat a2 = pow2( alpha );\n\tfloat gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );\n\tfloat gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );\n\treturn 0.5 / max( gv + gl, EPSILON );\n}\nfloat D_GGX( const in float alpha, const in float dotNH ) {\n\tfloat a2 = pow2( alpha );\n\tfloat denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0;\n\treturn RECIPROCAL_PI * a2 / pow2( denom );\n}\n#ifdef USE_ANISOTROPY\n\tfloat V_GGX_SmithCorrelated_Anisotropic( const in float alphaT, const in float alphaB, const in float dotTV, const in float dotBV, const in float dotTL, const in float dotBL, const in float dotNV, const in float dotNL ) {\n\t\tfloat gv = dotNL * length( vec3( alphaT * dotTV, alphaB * dotBV, dotNV ) );\n\t\tfloat gl = dotNV * length( vec3( alphaT * dotTL, alphaB * dotBL, dotNL ) );\n\t\tfloat v = 0.5 / ( gv + gl );\n\t\treturn saturate(v);\n\t}\n\tfloat D_GGX_Anisotropic( const in float alphaT, const in float alphaB, const in float dotNH, const in float dotTH, const in float dotBH ) {\n\t\tfloat a2 = alphaT * alphaB;\n\t\thighp vec3 v = vec3( alphaB * dotTH, alphaT * dotBH, a2 * dotNH );\n\t\thighp float v2 = dot( v, v );\n\t\tfloat w2 = a2 / v2;\n\t\treturn RECIPROCAL_PI * a2 * pow2 ( w2 );\n\t}\n#endif\n#ifdef USE_CLEARCOAT\n\tvec3 BRDF_GGX_Clearcoat( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material) {\n\t\tvec3 f0 = material.clearcoatF0;\n\t\tfloat f90 = material.clearcoatF90;\n\t\tfloat roughness = material.clearcoatRoughness;\n\t\tfloat alpha = pow2( roughness );\n\t\tvec3 halfDir = normalize( lightDir + viewDir );\n\t\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\t\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\t\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\t\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\t\tvec3 F = F_Schlick( f0, f90, dotVH );\n\t\tfloat V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );\n\t\tfloat D = D_GGX( alpha, dotNH );\n\t\treturn F * ( V * D );\n\t}\n#endif\nvec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in PhysicalMaterial material ) {\n\tvec3 f0 = material.specularColor;\n\tfloat f90 = material.specularF90;\n\tfloat roughness = material.roughness;\n\tfloat alpha = pow2( roughness );\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat dotVH = saturate( dot( viewDir, halfDir ) );\n\tvec3 F = F_Schlick( f0, f90, dotVH );\n\t#ifdef USE_IRIDESCENCE\n\t\tF = mix( F, material.iridescenceFresnel, material.iridescence );\n\t#endif\n\t#ifdef USE_ANISOTROPY\n\t\tfloat dotTL = dot( material.anisotropyT, lightDir );\n\t\tfloat dotTV = dot( material.anisotropyT, viewDir );\n\t\tfloat dotTH = dot( material.anisotropyT, halfDir );\n\t\tfloat dotBL = dot( material.anisotropyB, lightDir );\n\t\tfloat dotBV = dot( material.anisotropyB, viewDir );\n\t\tfloat dotBH = dot( material.anisotropyB, halfDir );\n\t\tfloat V = V_GGX_SmithCorrelated_Anisotropic( material.alphaT, alpha, dotTV, dotBV, dotTL, dotBL, dotNV, dotNL );\n\t\tfloat D = D_GGX_Anisotropic( material.alphaT, alpha, dotNH, dotTH, dotBH );\n\t#else\n\t\tfloat V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );\n\t\tfloat D = D_GGX( alpha, dotNH );\n\t#endif\n\treturn F * ( V * D );\n}\nvec2 LTC_Uv( const in vec3 N, const in vec3 V, const in float roughness ) {\n\tconst float LUT_SIZE = 64.0;\n\tconst float LUT_SCALE = ( LUT_SIZE - 1.0 ) / LUT_SIZE;\n\tconst float LUT_BIAS = 0.5 / LUT_SIZE;\n\tfloat dotNV = saturate( dot( N, V ) );\n\tvec2 uv = vec2( roughness, sqrt( 1.0 - dotNV ) );\n\tuv = uv * LUT_SCALE + LUT_BIAS;\n\treturn uv;\n}\nfloat LTC_ClippedSphereFormFactor( const in vec3 f ) {\n\tfloat l = length( f );\n\treturn max( ( l * l + f.z ) / ( l + 1.0 ), 0.0 );\n}\nvec3 LTC_EdgeVectorFormFactor( const in vec3 v1, const in vec3 v2 ) {\n\tfloat x = dot( v1, v2 );\n\tfloat y = abs( x );\n\tfloat a = 0.8543985 + ( 0.4965155 + 0.0145206 * y ) * y;\n\tfloat b = 3.4175940 + ( 4.1616724 + y ) * y;\n\tfloat v = a / b;\n\tfloat theta_sintheta = ( x > 0.0 ) ? v : 0.5 * inversesqrt( max( 1.0 - x * x, 1e-7 ) ) - v;\n\treturn cross( v1, v2 ) * theta_sintheta;\n}\nvec3 LTC_Evaluate( const in vec3 N, const in vec3 V, const in vec3 P, const in mat3 mInv, const in vec3 rectCoords[ 4 ] ) {\n\tvec3 v1 = rectCoords[ 1 ] - rectCoords[ 0 ];\n\tvec3 v2 = rectCoords[ 3 ] - rectCoords[ 0 ];\n\tvec3 lightNormal = cross( v1, v2 );\n\tif( dot( lightNormal, P - rectCoords[ 0 ] ) < 0.0 ) return vec3( 0.0 );\n\tvec3 T1, T2;\n\tT1 = normalize( V - N * dot( V, N ) );\n\tT2 = - cross( N, T1 );\n\tmat3 mat = mInv * transposeMat3( mat3( T1, T2, N ) );\n\tvec3 coords[ 4 ];\n\tcoords[ 0 ] = mat * ( rectCoords[ 0 ] - P );\n\tcoords[ 1 ] = mat * ( rectCoords[ 1 ] - P );\n\tcoords[ 2 ] = mat * ( rectCoords[ 2 ] - P );\n\tcoords[ 3 ] = mat * ( rectCoords[ 3 ] - P );\n\tcoords[ 0 ] = normalize( coords[ 0 ] );\n\tcoords[ 1 ] = normalize( coords[ 1 ] );\n\tcoords[ 2 ] = normalize( coords[ 2 ] );\n\tcoords[ 3 ] = normalize( coords[ 3 ] );\n\tvec3 vectorFormFactor = vec3( 0.0 );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 0 ], coords[ 1 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 1 ], coords[ 2 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 2 ], coords[ 3 ] );\n\tvectorFormFactor += LTC_EdgeVectorFormFactor( coords[ 3 ], coords[ 0 ] );\n\tfloat result = LTC_ClippedSphereFormFactor( vectorFormFactor );\n\treturn vec3( result );\n}\n#if defined( USE_SHEEN )\nfloat D_Charlie( float roughness, float dotNH ) {\n\tfloat alpha = pow2( roughness );\n\tfloat invAlpha = 1.0 / alpha;\n\tfloat cos2h = dotNH * dotNH;\n\tfloat sin2h = max( 1.0 - cos2h, 0.0078125 );\n\treturn ( 2.0 + invAlpha ) * pow( sin2h, invAlpha * 0.5 ) / ( 2.0 * PI );\n}\nfloat V_Neubelt( float dotNV, float dotNL ) {\n\treturn saturate( 1.0 / ( 4.0 * ( dotNL + dotNV - dotNL * dotNV ) ) );\n}\nvec3 BRDF_Sheen( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, vec3 sheenColor, const in float sheenRoughness ) {\n\tvec3 halfDir = normalize( lightDir + viewDir );\n\tfloat dotNL = saturate( dot( normal, lightDir ) );\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat dotNH = saturate( dot( normal, halfDir ) );\n\tfloat D = D_Charlie( sheenRoughness, dotNH );\n\tfloat V = V_Neubelt( dotNV, dotNL );\n\treturn sheenColor * ( D * V );\n}\n#endif\nfloat IBLSheenBRDF( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tfloat r2 = roughness * roughness;\n\tfloat a = roughness < 0.25 ? -339.2 * r2 + 161.4 * roughness - 25.9 : -8.48 * r2 + 14.3 * roughness - 9.95;\n\tfloat b = roughness < 0.25 ? 44.0 * r2 - 23.7 * roughness + 3.26 : 1.97 * r2 - 3.27 * roughness + 0.72;\n\tfloat DG = exp( a * dotNV + b ) + ( roughness < 0.25 ? 0.0 : 0.1 * ( roughness - 0.25 ) );\n\treturn saturate( DG * RECIPROCAL_PI );\n}\nvec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {\n\tfloat dotNV = saturate( dot( normal, viewDir ) );\n\tconst vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );\n\tconst vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );\n\tvec4 r = roughness * c0 + c1;\n\tfloat a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;\n\tvec2 fab = vec2( - 1.04, 1.04 ) * a004 + r.zw;\n\treturn fab;\n}\nvec3 EnvironmentBRDF( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness ) {\n\tvec2 fab = DFGApprox( normal, viewDir, roughness );\n\treturn specularColor * fab.x + specularF90 * fab.y;\n}\n#ifdef USE_IRIDESCENCE\nvoid computeMultiscatteringIridescence( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float iridescence, const in vec3 iridescenceF0, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {\n#else\nvoid computeMultiscattering( const in vec3 normal, const in vec3 viewDir, const in vec3 specularColor, const in float specularF90, const in float roughness, inout vec3 singleScatter, inout vec3 multiScatter ) {\n#endif\n\tvec2 fab = DFGApprox( normal, viewDir, roughness );\n\t#ifdef USE_IRIDESCENCE\n\t\tvec3 Fr = mix( specularColor, iridescenceF0, iridescence );\n\t#else\n\t\tvec3 Fr = specularColor;\n\t#endif\n\tvec3 FssEss = Fr * fab.x + specularF90 * fab.y;\n\tfloat Ess = fab.x + fab.y;\n\tfloat Ems = 1.0 - Ess;\n\tvec3 Favg = Fr + ( 1.0 - Fr ) * 0.047619;\tvec3 Fms = FssEss * Favg / ( 1.0 - Ems * Favg );\n\tsingleScatter += FssEss;\n\tmultiScatter += Fms * Ems;\n}\n#if NUM_RECT_AREA_LIGHTS > 0\n\tvoid RE_Direct_RectArea_Physical( const in RectAreaLight rectAreaLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\t\tvec3 normal = geometryNormal;\n\t\tvec3 viewDir = geometryViewDir;\n\t\tvec3 position = geometryPosition;\n\t\tvec3 lightPos = rectAreaLight.position;\n\t\tvec3 halfWidth = rectAreaLight.halfWidth;\n\t\tvec3 halfHeight = rectAreaLight.halfHeight;\n\t\tvec3 lightColor = rectAreaLight.color;\n\t\tfloat roughness = material.roughness;\n\t\tvec3 rectCoords[ 4 ];\n\t\trectCoords[ 0 ] = lightPos + halfWidth - halfHeight;\t\trectCoords[ 1 ] = lightPos - halfWidth - halfHeight;\n\t\trectCoords[ 2 ] = lightPos - halfWidth + halfHeight;\n\t\trectCoords[ 3 ] = lightPos + halfWidth + halfHeight;\n\t\tvec2 uv = LTC_Uv( normal, viewDir, roughness );\n\t\tvec4 t1 = texture2D( ltc_1, uv );\n\t\tvec4 t2 = texture2D( ltc_2, uv );\n\t\tmat3 mInv = mat3(\n\t\t\tvec3( t1.x, 0, t1.y ),\n\t\t\tvec3(    0, 1,    0 ),\n\t\t\tvec3( t1.z, 0, t1.w )\n\t\t);\n\t\tvec3 fresnel = ( material.specularColor * t2.x + ( vec3( 1.0 ) - material.specularColor ) * t2.y );\n\t\treflectedLight.directSpecular += lightColor * fresnel * LTC_Evaluate( normal, viewDir, position, mInv, rectCoords );\n\t\treflectedLight.directDiffuse += lightColor * material.diffuseColor * LTC_Evaluate( normal, viewDir, position, mat3( 1.0 ), rectCoords );\n\t}\n#endif\nvoid RE_Direct_Physical( const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\tfloat dotNL = saturate( dot( geometryNormal, directLight.direction ) );\n\tvec3 irradiance = dotNL * directLight.color;\n\t#ifdef USE_CLEARCOAT\n\t\tfloat dotNLcc = saturate( dot( geometryClearcoatNormal, directLight.direction ) );\n\t\tvec3 ccIrradiance = dotNLcc * directLight.color;\n\t\tclearcoatSpecularDirect += ccIrradiance * BRDF_GGX_Clearcoat( directLight.direction, geometryViewDir, geometryClearcoatNormal, material );\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tsheenSpecularDirect += irradiance * BRDF_Sheen( directLight.direction, geometryViewDir, geometryNormal, material.sheenColor, material.sheenRoughness );\n\t#endif\n\treflectedLight.directSpecular += irradiance * BRDF_GGX( directLight.direction, geometryViewDir, geometryNormal, material );\n\treflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectDiffuse_Physical( const in vec3 irradiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight ) {\n\treflectedLight.indirectDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );\n}\nvoid RE_IndirectSpecular_Physical( const in vec3 radiance, const in vec3 irradiance, const in vec3 clearcoatRadiance, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {\n\t#ifdef USE_CLEARCOAT\n\t\tclearcoatSpecularIndirect += clearcoatRadiance * EnvironmentBRDF( geometryClearcoatNormal, geometryViewDir, material.clearcoatF0, material.clearcoatF90, material.clearcoatRoughness );\n\t#endif\n\t#ifdef USE_SHEEN\n\t\tsheenSpecularIndirect += irradiance * material.sheenColor * IBLSheenBRDF( geometryNormal, geometryViewDir, material.sheenRoughness );\n\t#endif\n\tvec3 singleScattering = vec3( 0.0 );\n\tvec3 multiScattering = vec3( 0.0 );\n\tvec3 cosineWeightedIrradiance = irradiance * RECIPROCAL_PI;\n\t#ifdef USE_IRIDESCENCE\n\t\tcomputeMultiscatteringIridescence( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.iridescence, material.iridescenceFresnel, material.roughness, singleScattering, multiScattering );\n\t#else\n\t\tcomputeMultiscattering( geometryNormal, geometryViewDir, material.specularColor, material.specularF90, material.roughness, singleScattering, multiScattering );\n\t#endif\n\tvec3 totalScattering = singleScattering + multiScattering;\n\tvec3 diffuse = material.diffuseColor * ( 1.0 - max( max( totalScattering.r, totalScattering.g ), totalScattering.b ) );\n\treflectedLight.indirectSpecular += radiance * singleScattering;\n\treflectedLight.indirectSpecular += multiScattering * cosineWeightedIrradiance;\n\treflectedLight.indirectDiffuse += diffuse * cosineWeightedIrradiance;\n}\n#define RE_Direct\t\t\t\tRE_Direct_Physical\n#define RE_Direct_RectArea\t\tRE_Direct_RectArea_Physical\n#define RE_IndirectDiffuse\t\tRE_IndirectDiffuse_Physical\n#define RE_IndirectSpecular\t\tRE_IndirectSpecular_Physical\nfloat computeSpecularOcclusion( const in float dotNV, const in float ambientOcclusion, const in float roughness ) {\n\treturn saturate( pow( dotNV + ambientOcclusion, exp2( - 16.0 * roughness - 1.0 ) ) - 1.0 + ambientOcclusion );\n}";

var lights_fragment_begin = "\nvec3 geometryPosition = - vViewPosition;\nvec3 geometryNormal = normal;\nvec3 geometryViewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );\nvec3 geometryClearcoatNormal = vec3( 0.0 );\n#ifdef USE_CLEARCOAT\n\tgeometryClearcoatNormal = clearcoatNormal;\n#endif\n#ifdef USE_IRIDESCENCE\n\tfloat dotNVi = saturate( dot( normal, geometryViewDir ) );\n\tif ( material.iridescenceThickness == 0.0 ) {\n\t\tmaterial.iridescence = 0.0;\n\t} else {\n\t\tmaterial.iridescence = saturate( material.iridescence );\n\t}\n\tif ( material.iridescence > 0.0 ) {\n\t\tmaterial.iridescenceFresnel = evalIridescence( 1.0, material.iridescenceIOR, dotNVi, material.iridescenceThickness, material.specularColor );\n\t\tmaterial.iridescenceF0 = Schlick_to_F0( material.iridescenceFresnel, 1.0, dotNVi );\n\t}\n#endif\nIncidentLight directLight;\n#if ( NUM_POINT_LIGHTS > 0 ) && defined( RE_Direct )\n\tPointLight pointLight;\n\t#if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0\n\tPointLightShadow pointLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {\n\t\tpointLight = pointLights[ i ];\n\t\tgetPointLightInfo( pointLight, geometryPosition, directLight );\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )\n\t\tpointLightShadow = pointLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getPointShadow( pointShadowMap[ i ], pointLightShadow.shadowMapSize, pointLightShadow.shadowIntensity, pointLightShadow.shadowBias, pointLightShadow.shadowRadius, vPointShadowCoord[ i ], pointLightShadow.shadowCameraNear, pointLightShadow.shadowCameraFar ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_SPOT_LIGHTS > 0 ) && defined( RE_Direct )\n\tSpotLight spotLight;\n\tvec4 spotColor;\n\tvec3 spotLightCoord;\n\tbool inSpotLightMap;\n\t#if defined( USE_SHADOWMAP ) && NUM_SPOT_LIGHT_SHADOWS > 0\n\tSpotLightShadow spotLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {\n\t\tspotLight = spotLights[ i ];\n\t\tgetSpotLightInfo( spotLight, geometryPosition, directLight );\n\t\t#if ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )\n\t\t#define SPOT_LIGHT_MAP_INDEX UNROLLED_LOOP_INDEX\n\t\t#elif ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\t#define SPOT_LIGHT_MAP_INDEX NUM_SPOT_LIGHT_MAPS\n\t\t#else\n\t\t#define SPOT_LIGHT_MAP_INDEX ( UNROLLED_LOOP_INDEX - NUM_SPOT_LIGHT_SHADOWS + NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS )\n\t\t#endif\n\t\t#if ( SPOT_LIGHT_MAP_INDEX < NUM_SPOT_LIGHT_MAPS )\n\t\t\tspotLightCoord = vSpotLightCoord[ i ].xyz / vSpotLightCoord[ i ].w;\n\t\t\tinSpotLightMap = all( lessThan( abs( spotLightCoord * 2. - 1. ), vec3( 1.0 ) ) );\n\t\t\tspotColor = texture2D( spotLightMap[ SPOT_LIGHT_MAP_INDEX ], spotLightCoord.xy );\n\t\t\tdirectLight.color = inSpotLightMap ? directLight.color * spotColor.rgb : directLight.color;\n\t\t#endif\n\t\t#undef SPOT_LIGHT_MAP_INDEX\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\tspotLightShadow = spotLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( spotShadowMap[ i ], spotLightShadow.shadowMapSize, spotLightShadow.shadowIntensity, spotLightShadow.shadowBias, spotLightShadow.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_DIR_LIGHTS > 0 ) && defined( RE_Direct )\n\tDirectionalLight directionalLight;\n\t#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0\n\tDirectionalLightShadow directionalLightShadow;\n\t#endif\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {\n\t\tdirectionalLight = directionalLights[ i ];\n\t\tgetDirectionalLightInfo( directionalLight, directLight );\n\t\t#if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )\n\t\tdirectionalLightShadow = directionalLightShadows[ i ];\n\t\tdirectLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;\n\t\t#endif\n\t\tRE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if ( NUM_RECT_AREA_LIGHTS > 0 ) && defined( RE_Direct_RectArea )\n\tRectAreaLight rectAreaLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_RECT_AREA_LIGHTS; i ++ ) {\n\t\trectAreaLight = rectAreaLights[ i ];\n\t\tRE_Direct_RectArea( rectAreaLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n\t}\n\t#pragma unroll_loop_end\n#endif\n#if defined( RE_IndirectDiffuse )\n\tvec3 iblIrradiance = vec3( 0.0 );\n\tvec3 irradiance = getAmbientLightIrradiance( ambientLightColor );\n\t#if defined( USE_LIGHT_PROBES )\n\t\tirradiance += getLightProbeIrradiance( lightProbe, geometryNormal );\n\t#endif\n\t#if ( NUM_HEMI_LIGHTS > 0 )\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {\n\t\t\tirradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], geometryNormal );\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n#endif\n#if defined( RE_IndirectSpecular )\n\tvec3 radiance = vec3( 0.0 );\n\tvec3 clearcoatRadiance = vec3( 0.0 );\n#endif";

var lights_fragment_maps = "#if defined( RE_IndirectDiffuse )\n\t#ifdef USE_LIGHTMAP\n\t\tvec4 lightMapTexel = texture2D( lightMap, vLightMapUv );\n\t\tvec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;\n\t\tirradiance += lightMapIrradiance;\n\t#endif\n\t#if defined( USE_ENVMAP ) && defined( STANDARD ) && defined( ENVMAP_TYPE_CUBE_UV )\n\t\tiblIrradiance += getIBLIrradiance( geometryNormal );\n\t#endif\n#endif\n#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )\n\t#ifdef USE_ANISOTROPY\n\t\tradiance += getIBLAnisotropyRadiance( geometryViewDir, geometryNormal, material.roughness, material.anisotropyB, material.anisotropy );\n\t#else\n\t\tradiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );\n\t#endif\n\t#ifdef USE_CLEARCOAT\n\t\tclearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );\n\t#endif\n#endif";

var lights_fragment_end = "#if defined( RE_IndirectDiffuse )\n\tRE_IndirectDiffuse( irradiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n#endif\n#if defined( RE_IndirectSpecular )\n\tRE_IndirectSpecular( radiance, iblIrradiance, clearcoatRadiance, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );\n#endif";

var logdepthbuf_fragment = "#if defined( USE_LOGDEPTHBUF )\n\tgl_FragDepth = vIsPerspective == 0.0 ? gl_FragCoord.z : log2( vFragDepth ) * logDepthBufFC * 0.5;\n#endif";

var logdepthbuf_pars_fragment = "#if defined( USE_LOGDEPTHBUF )\n\tuniform float logDepthBufFC;\n\tvarying float vFragDepth;\n\tvarying float vIsPerspective;\n#endif";

var logdepthbuf_pars_vertex = "#ifdef USE_LOGDEPTHBUF\n\tvarying float vFragDepth;\n\tvarying float vIsPerspective;\n#endif";

var logdepthbuf_vertex = "#ifdef USE_LOGDEPTHBUF\n\tvFragDepth = 1.0 + gl_Position.w;\n\tvIsPerspective = float( isPerspectiveMatrix( projectionMatrix ) );\n#endif";

var map_fragment = "#ifdef USE_MAP\n\tvec4 sampledDiffuseColor = texture2D( map, vMapUv );\n\t#ifdef DECODE_VIDEO_TEXTURE\n\t\tsampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );\n\t\n\t#endif\n\tdiffuseColor *= sampledDiffuseColor;\n#endif";

var map_pars_fragment = "#ifdef USE_MAP\n\tuniform sampler2D map;\n#endif";

var map_particle_fragment = "#if defined( USE_MAP ) || defined( USE_ALPHAMAP )\n\t#if defined( USE_POINTS_UV )\n\t\tvec2 uv = vUv;\n\t#else\n\t\tvec2 uv = ( uvTransform * vec3( gl_PointCoord.x, 1.0 - gl_PointCoord.y, 1 ) ).xy;\n\t#endif\n#endif\n#ifdef USE_MAP\n\tdiffuseColor *= texture2D( map, uv );\n#endif\n#ifdef USE_ALPHAMAP\n\tdiffuseColor.a *= texture2D( alphaMap, uv ).g;\n#endif";

var map_particle_pars_fragment = "#if defined( USE_POINTS_UV )\n\tvarying vec2 vUv;\n#else\n\t#if defined( USE_MAP ) || defined( USE_ALPHAMAP )\n\t\tuniform mat3 uvTransform;\n\t#endif\n#endif\n#ifdef USE_MAP\n\tuniform sampler2D map;\n#endif\n#ifdef USE_ALPHAMAP\n\tuniform sampler2D alphaMap;\n#endif";

var metalnessmap_fragment = "float metalnessFactor = metalness;\n#ifdef USE_METALNESSMAP\n\tvec4 texelMetalness = texture2D( metalnessMap, vMetalnessMapUv );\n\tmetalnessFactor *= texelMetalness.b;\n#endif";

var metalnessmap_pars_fragment = "#ifdef USE_METALNESSMAP\n\tuniform sampler2D metalnessMap;\n#endif";

var morphinstance_vertex = "#ifdef USE_INSTANCING_MORPH\n\tfloat morphTargetInfluences[ MORPHTARGETS_COUNT ];\n\tfloat morphTargetBaseInfluence = texelFetch( morphTexture, ivec2( 0, gl_InstanceID ), 0 ).r;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tmorphTargetInfluences[i] =  texelFetch( morphTexture, ivec2( i + 1, gl_InstanceID ), 0 ).r;\n\t}\n#endif";

var morphcolor_vertex = "#if defined( USE_MORPHCOLORS )\n\tvColor *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\t#if defined( USE_COLOR_ALPHA )\n\t\t\tif ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ) * morphTargetInfluences[ i ];\n\t\t#elif defined( USE_COLOR )\n\t\t\tif ( morphTargetInfluences[ i ] != 0.0 ) vColor += getMorph( gl_VertexID, i, 2 ).rgb * morphTargetInfluences[ i ];\n\t\t#endif\n\t}\n#endif";

var morphnormal_vertex = "#ifdef USE_MORPHNORMALS\n\tobjectNormal *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tif ( morphTargetInfluences[ i ] != 0.0 ) objectNormal += getMorph( gl_VertexID, i, 1 ).xyz * morphTargetInfluences[ i ];\n\t}\n#endif";

var morphtarget_pars_vertex = "#ifdef USE_MORPHTARGETS\n\t#ifndef USE_INSTANCING_MORPH\n\t\tuniform float morphTargetBaseInfluence;\n\t\tuniform float morphTargetInfluences[ MORPHTARGETS_COUNT ];\n\t#endif\n\tuniform sampler2DArray morphTargetsTexture;\n\tuniform ivec2 morphTargetsTextureSize;\n\tvec4 getMorph( const in int vertexIndex, const in int morphTargetIndex, const in int offset ) {\n\t\tint texelIndex = vertexIndex * MORPHTARGETS_TEXTURE_STRIDE + offset;\n\t\tint y = texelIndex / morphTargetsTextureSize.x;\n\t\tint x = texelIndex - y * morphTargetsTextureSize.x;\n\t\tivec3 morphUV = ivec3( x, y, morphTargetIndex );\n\t\treturn texelFetch( morphTargetsTexture, morphUV, 0 );\n\t}\n#endif";

var morphtarget_vertex = "#ifdef USE_MORPHTARGETS\n\ttransformed *= morphTargetBaseInfluence;\n\tfor ( int i = 0; i < MORPHTARGETS_COUNT; i ++ ) {\n\t\tif ( morphTargetInfluences[ i ] != 0.0 ) transformed += getMorph( gl_VertexID, i, 0 ).xyz * morphTargetInfluences[ i ];\n\t}\n#endif";

var normal_fragment_begin = "float faceDirection = gl_FrontFacing ? 1.0 : - 1.0;\n#ifdef FLAT_SHADED\n\tvec3 fdx = dFdx( vViewPosition );\n\tvec3 fdy = dFdy( vViewPosition );\n\tvec3 normal = normalize( cross( fdx, fdy ) );\n#else\n\tvec3 normal = normalize( vNormal );\n\t#ifdef DOUBLE_SIDED\n\t\tnormal *= faceDirection;\n\t#endif\n#endif\n#if defined( USE_NORMALMAP_TANGENTSPACE ) || defined( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY )\n\t#ifdef USE_TANGENT\n\t\tmat3 tbn = mat3( normalize( vTangent ), normalize( vBitangent ), normal );\n\t#else\n\t\tmat3 tbn = getTangentFrame( - vViewPosition, normal,\n\t\t#if defined( USE_NORMALMAP )\n\t\t\tvNormalMapUv\n\t\t#elif defined( USE_CLEARCOAT_NORMALMAP )\n\t\t\tvClearcoatNormalMapUv\n\t\t#else\n\t\t\tvUv\n\t\t#endif\n\t\t);\n\t#endif\n\t#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )\n\t\ttbn[0] *= faceDirection;\n\t\ttbn[1] *= faceDirection;\n\t#endif\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\t#ifdef USE_TANGENT\n\t\tmat3 tbn2 = mat3( normalize( vTangent ), normalize( vBitangent ), normal );\n\t#else\n\t\tmat3 tbn2 = getTangentFrame( - vViewPosition, normal, vClearcoatNormalMapUv );\n\t#endif\n\t#if defined( DOUBLE_SIDED ) && ! defined( FLAT_SHADED )\n\t\ttbn2[0] *= faceDirection;\n\t\ttbn2[1] *= faceDirection;\n\t#endif\n#endif\nvec3 nonPerturbedNormal = normal;";

var normal_fragment_maps = "#ifdef USE_NORMALMAP_OBJECTSPACE\n\tnormal = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;\n\t#ifdef FLIP_SIDED\n\t\tnormal = - normal;\n\t#endif\n\t#ifdef DOUBLE_SIDED\n\t\tnormal = normal * faceDirection;\n\t#endif\n\tnormal = normalize( normalMatrix * normal );\n#elif defined( USE_NORMALMAP_TANGENTSPACE )\n\tvec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;\n\tmapN.xy *= normalScale;\n\tnormal = normalize( tbn * mapN );\n#elif defined( USE_BUMPMAP )\n\tnormal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );\n#endif";

var normal_pars_fragment = "#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n\t#ifdef USE_TANGENT\n\t\tvarying vec3 vTangent;\n\t\tvarying vec3 vBitangent;\n\t#endif\n#endif";

var normal_pars_vertex = "#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n\t#ifdef USE_TANGENT\n\t\tvarying vec3 vTangent;\n\t\tvarying vec3 vBitangent;\n\t#endif\n#endif";

var normal_vertex = "#ifndef FLAT_SHADED\n\tvNormal = normalize( transformedNormal );\n\t#ifdef USE_TANGENT\n\t\tvTangent = normalize( transformedTangent );\n\t\tvBitangent = normalize( cross( vNormal, vTangent ) * tangent.w );\n\t#endif\n#endif";

var normalmap_pars_fragment = "#ifdef USE_NORMALMAP\n\tuniform sampler2D normalMap;\n\tuniform vec2 normalScale;\n#endif\n#ifdef USE_NORMALMAP_OBJECTSPACE\n\tuniform mat3 normalMatrix;\n#endif\n#if ! defined ( USE_TANGENT ) && ( defined ( USE_NORMALMAP_TANGENTSPACE ) || defined ( USE_CLEARCOAT_NORMALMAP ) || defined( USE_ANISOTROPY ) )\n\tmat3 getTangentFrame( vec3 eye_pos, vec3 surf_norm, vec2 uv ) {\n\t\tvec3 q0 = dFdx( eye_pos.xyz );\n\t\tvec3 q1 = dFdy( eye_pos.xyz );\n\t\tvec2 st0 = dFdx( uv.st );\n\t\tvec2 st1 = dFdy( uv.st );\n\t\tvec3 N = surf_norm;\n\t\tvec3 q1perp = cross( q1, N );\n\t\tvec3 q0perp = cross( N, q0 );\n\t\tvec3 T = q1perp * st0.x + q0perp * st1.x;\n\t\tvec3 B = q1perp * st0.y + q0perp * st1.y;\n\t\tfloat det = max( dot( T, T ), dot( B, B ) );\n\t\tfloat scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );\n\t\treturn mat3( T * scale, B * scale, N );\n\t}\n#endif";

var clearcoat_normal_fragment_begin = "#ifdef USE_CLEARCOAT\n\tvec3 clearcoatNormal = nonPerturbedNormal;\n#endif";

var clearcoat_normal_fragment_maps = "#ifdef USE_CLEARCOAT_NORMALMAP\n\tvec3 clearcoatMapN = texture2D( clearcoatNormalMap, vClearcoatNormalMapUv ).xyz * 2.0 - 1.0;\n\tclearcoatMapN.xy *= clearcoatNormalScale;\n\tclearcoatNormal = normalize( tbn2 * clearcoatMapN );\n#endif";

var clearcoat_pars_fragment = "#ifdef USE_CLEARCOATMAP\n\tuniform sampler2D clearcoatMap;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tuniform sampler2D clearcoatNormalMap;\n\tuniform vec2 clearcoatNormalScale;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tuniform sampler2D clearcoatRoughnessMap;\n#endif";

var iridescence_pars_fragment = "#ifdef USE_IRIDESCENCEMAP\n\tuniform sampler2D iridescenceMap;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tuniform sampler2D iridescenceThicknessMap;\n#endif";

var opaque_fragment = "#ifdef OPAQUE\ndiffuseColor.a = 1.0;\n#endif\n#ifdef USE_TRANSMISSION\ndiffuseColor.a *= material.transmissionAlpha;\n#endif\ngl_FragColor = vec4( outgoingLight, diffuseColor.a );";

var packing = "vec3 packNormalToRGB( const in vec3 normal ) {\n\treturn normalize( normal ) * 0.5 + 0.5;\n}\nvec3 unpackRGBToNormal( const in vec3 rgb ) {\n\treturn 2.0 * rgb.xyz - 1.0;\n}\nconst float PackUpscale = 256. / 255.;const float UnpackDownscale = 255. / 256.;const float ShiftRight8 = 1. / 256.;\nconst float Inv255 = 1. / 255.;\nconst vec4 PackFactors = vec4( 1.0, 256.0, 256.0 * 256.0, 256.0 * 256.0 * 256.0 );\nconst vec2 UnpackFactors2 = vec2( UnpackDownscale, 1.0 / PackFactors.g );\nconst vec3 UnpackFactors3 = vec3( UnpackDownscale / PackFactors.rg, 1.0 / PackFactors.b );\nconst vec4 UnpackFactors4 = vec4( UnpackDownscale / PackFactors.rgb, 1.0 / PackFactors.a );\nvec4 packDepthToRGBA( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec4( 0., 0., 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec4( 1., 1., 1., 1. );\n\tfloat vuf;\n\tfloat af = modf( v * PackFactors.a, vuf );\n\tfloat bf = modf( vuf * ShiftRight8, vuf );\n\tfloat gf = modf( vuf * ShiftRight8, vuf );\n\treturn vec4( vuf * Inv255, gf * PackUpscale, bf * PackUpscale, af );\n}\nvec3 packDepthToRGB( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec3( 0., 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec3( 1., 1., 1. );\n\tfloat vuf;\n\tfloat bf = modf( v * PackFactors.b, vuf );\n\tfloat gf = modf( vuf * ShiftRight8, vuf );\n\treturn vec3( vuf * Inv255, gf * PackUpscale, bf );\n}\nvec2 packDepthToRG( const in float v ) {\n\tif( v <= 0.0 )\n\t\treturn vec2( 0., 0. );\n\tif( v >= 1.0 )\n\t\treturn vec2( 1., 1. );\n\tfloat vuf;\n\tfloat gf = modf( v * 256., vuf );\n\treturn vec2( vuf * Inv255, gf );\n}\nfloat unpackRGBAToDepth( const in vec4 v ) {\n\treturn dot( v, UnpackFactors4 );\n}\nfloat unpackRGBToDepth( const in vec3 v ) {\n\treturn dot( v, UnpackFactors3 );\n}\nfloat unpackRGToDepth( const in vec2 v ) {\n\treturn v.r * UnpackFactors2.r + v.g * UnpackFactors2.g;\n}\nvec4 pack2HalfToRGBA( const in vec2 v ) {\n\tvec4 r = vec4( v.x, fract( v.x * 255.0 ), v.y, fract( v.y * 255.0 ) );\n\treturn vec4( r.x - r.y / 255.0, r.y, r.z - r.w / 255.0, r.w );\n}\nvec2 unpackRGBATo2Half( const in vec4 v ) {\n\treturn vec2( v.x + ( v.y / 255.0 ), v.z + ( v.w / 255.0 ) );\n}\nfloat viewZToOrthographicDepth( const in float viewZ, const in float near, const in float far ) {\n\treturn ( viewZ + near ) / ( near - far );\n}\nfloat orthographicDepthToViewZ( const in float depth, const in float near, const in float far ) {\n\treturn depth * ( near - far ) - near;\n}\nfloat viewZToPerspectiveDepth( const in float viewZ, const in float near, const in float far ) {\n\treturn ( ( near + viewZ ) * far ) / ( ( far - near ) * viewZ );\n}\nfloat perspectiveDepthToViewZ( const in float depth, const in float near, const in float far ) {\n\treturn ( near * far ) / ( ( far - near ) * depth - far );\n}";

var premultiplied_alpha_fragment = "#ifdef PREMULTIPLIED_ALPHA\n\tgl_FragColor.rgb *= gl_FragColor.a;\n#endif";

var project_vertex = "vec4 mvPosition = vec4( transformed, 1.0 );\n#ifdef USE_BATCHING\n\tmvPosition = batchingMatrix * mvPosition;\n#endif\n#ifdef USE_INSTANCING\n\tmvPosition = instanceMatrix * mvPosition;\n#endif\nmvPosition = modelViewMatrix * mvPosition;\ngl_Position = projectionMatrix * mvPosition;";

var dithering_fragment = "#ifdef DITHERING\n\tgl_FragColor.rgb = dithering( gl_FragColor.rgb );\n#endif";

var dithering_pars_fragment = "#ifdef DITHERING\n\tvec3 dithering( vec3 color ) {\n\t\tfloat grid_position = rand( gl_FragCoord.xy );\n\t\tvec3 dither_shift_RGB = vec3( 0.25 / 255.0, -0.25 / 255.0, 0.25 / 255.0 );\n\t\tdither_shift_RGB = mix( 2.0 * dither_shift_RGB, -2.0 * dither_shift_RGB, grid_position );\n\t\treturn color + dither_shift_RGB;\n\t}\n#endif";

var roughnessmap_fragment = "float roughnessFactor = roughness;\n#ifdef USE_ROUGHNESSMAP\n\tvec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );\n\troughnessFactor *= texelRoughness.g;\n#endif";

var roughnessmap_pars_fragment = "#ifdef USE_ROUGHNESSMAP\n\tuniform sampler2D roughnessMap;\n#endif";

var shadowmap_pars_fragment = "#if NUM_SPOT_LIGHT_COORDS > 0\n\tvarying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];\n#endif\n#if NUM_SPOT_LIGHT_MAPS > 0\n\tuniform sampler2D spotLightMap[ NUM_SPOT_LIGHT_MAPS ];\n#endif\n#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tvarying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tstruct DirectionalLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D spotShadowMap[ NUM_SPOT_LIGHT_SHADOWS ];\n\t\tstruct SpotLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\tuniform sampler2D pointShadowMap[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tvarying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tstruct PointLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t\tfloat shadowCameraNear;\n\t\t\tfloat shadowCameraFar;\n\t\t};\n\t\tuniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];\n\t#endif\n\tfloat texture2DCompare( sampler2D depths, vec2 uv, float compare ) {\n\t\treturn step( compare, unpackRGBAToDepth( texture2D( depths, uv ) ) );\n\t}\n\tvec2 texture2DDistribution( sampler2D shadow, vec2 uv ) {\n\t\treturn unpackRGBATo2Half( texture2D( shadow, uv ) );\n\t}\n\tfloat VSMShadow (sampler2D shadow, vec2 uv, float compare ){\n\t\tfloat occlusion = 1.0;\n\t\tvec2 distribution = texture2DDistribution( shadow, uv );\n\t\tfloat hard_shadow = step( compare , distribution.x );\n\t\tif (hard_shadow != 1.0 ) {\n\t\t\tfloat distance = compare - distribution.x ;\n\t\t\tfloat variance = max( 0.00000, distribution.y * distribution.y );\n\t\t\tfloat softness_probability = variance / (variance + distance * distance );\t\t\tsoftness_probability = clamp( ( softness_probability - 0.3 ) / ( 0.95 - 0.3 ), 0.0, 1.0 );\t\t\tocclusion = clamp( max( hard_shadow, softness_probability ), 0.0, 1.0 );\n\t\t}\n\t\treturn occlusion;\n\t}\n\tfloat getShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord ) {\n\t\tfloat shadow = 1.0;\n\t\tshadowCoord.xyz /= shadowCoord.w;\n\t\tshadowCoord.z += shadowBias;\n\t\tbool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;\n\t\tbool frustumTest = inFrustum && shadowCoord.z <= 1.0;\n\t\tif ( frustumTest ) {\n\t\t#if defined( SHADOWMAP_TYPE_PCF )\n\t\t\tvec2 texelSize = vec2( 1.0 ) / shadowMapSize;\n\t\t\tfloat dx0 = - texelSize.x * shadowRadius;\n\t\t\tfloat dy0 = - texelSize.y * shadowRadius;\n\t\t\tfloat dx1 = + texelSize.x * shadowRadius;\n\t\t\tfloat dy1 = + texelSize.y * shadowRadius;\n\t\t\tfloat dx2 = dx0 / 2.0;\n\t\t\tfloat dy2 = dy0 / 2.0;\n\t\t\tfloat dx3 = dx1 / 2.0;\n\t\t\tfloat dy3 = dy1 / 2.0;\n\t\t\tshadow = (\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy2 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx2, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx3, dy3 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx0, dy1 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.0, dy1 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, shadowCoord.xy + vec2( dx1, dy1 ), shadowCoord.z )\n\t\t\t) * ( 1.0 / 17.0 );\n\t\t#elif defined( SHADOWMAP_TYPE_PCF_SOFT )\n\t\t\tvec2 texelSize = vec2( 1.0 ) / shadowMapSize;\n\t\t\tfloat dx = texelSize.x;\n\t\t\tfloat dy = texelSize.y;\n\t\t\tvec2 uv = shadowCoord.xy;\n\t\t\tvec2 f = fract( uv * shadowMapSize + 0.5 );\n\t\t\tuv -= f * texelSize;\n\t\t\tshadow = (\n\t\t\t\ttexture2DCompare( shadowMap, uv, shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + vec2( dx, 0.0 ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + vec2( 0.0, dy ), shadowCoord.z ) +\n\t\t\t\ttexture2DCompare( shadowMap, uv + texelSize, shadowCoord.z ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( -dx, 0.0 ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 0.0 ), shadowCoord.z ),\n\t\t\t\t\t f.x ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( -dx, dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, dy ), shadowCoord.z ),\n\t\t\t\t\t f.x ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( 0.0, -dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( 0.0, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t f.y ) +\n\t\t\t\tmix( texture2DCompare( shadowMap, uv + vec2( dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t texture2DCompare( shadowMap, uv + vec2( dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t f.y ) +\n\t\t\t\tmix( mix( texture2DCompare( shadowMap, uv + vec2( -dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t\t  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, -dy ), shadowCoord.z ),\n\t\t\t\t\t\t  f.x ),\n\t\t\t\t\t mix( texture2DCompare( shadowMap, uv + vec2( -dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t\t  texture2DCompare( shadowMap, uv + vec2( 2.0 * dx, 2.0 * dy ), shadowCoord.z ),\n\t\t\t\t\t\t  f.x ),\n\t\t\t\t\t f.y )\n\t\t\t) * ( 1.0 / 9.0 );\n\t\t#elif defined( SHADOWMAP_TYPE_VSM )\n\t\t\tshadow = VSMShadow( shadowMap, shadowCoord.xy, shadowCoord.z );\n\t\t#else\n\t\t\tshadow = texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z );\n\t\t#endif\n\t\t}\n\t\treturn mix( 1.0, shadow, shadowIntensity );\n\t}\n\tvec2 cubeToUV( vec3 v, float texelSizeY ) {\n\t\tvec3 absV = abs( v );\n\t\tfloat scaleToCube = 1.0 / max( absV.x, max( absV.y, absV.z ) );\n\t\tabsV *= scaleToCube;\n\t\tv *= scaleToCube * ( 1.0 - 2.0 * texelSizeY );\n\t\tvec2 planar = v.xy;\n\t\tfloat almostATexel = 1.5 * texelSizeY;\n\t\tfloat almostOne = 1.0 - almostATexel;\n\t\tif ( absV.z >= almostOne ) {\n\t\t\tif ( v.z > 0.0 )\n\t\t\t\tplanar.x = 4.0 - v.x;\n\t\t} else if ( absV.x >= almostOne ) {\n\t\t\tfloat signX = sign( v.x );\n\t\t\tplanar.x = v.z * signX + 2.0 * signX;\n\t\t} else if ( absV.y >= almostOne ) {\n\t\t\tfloat signY = sign( v.y );\n\t\t\tplanar.x = v.x + 2.0 * signY + 2.0;\n\t\t\tplanar.y = v.z * signY - 2.0;\n\t\t}\n\t\treturn vec2( 0.125, 0.25 ) * planar + vec2( 0.375, 0.75 );\n\t}\n\tfloat getPointShadow( sampler2D shadowMap, vec2 shadowMapSize, float shadowIntensity, float shadowBias, float shadowRadius, vec4 shadowCoord, float shadowCameraNear, float shadowCameraFar ) {\n\t\tfloat shadow = 1.0;\n\t\tvec3 lightToPosition = shadowCoord.xyz;\n\t\t\n\t\tfloat lightToPositionLength = length( lightToPosition );\n\t\tif ( lightToPositionLength - shadowCameraFar <= 0.0 && lightToPositionLength - shadowCameraNear >= 0.0 ) {\n\t\t\tfloat dp = ( lightToPositionLength - shadowCameraNear ) / ( shadowCameraFar - shadowCameraNear );\t\t\tdp += shadowBias;\n\t\t\tvec3 bd3D = normalize( lightToPosition );\n\t\t\tvec2 texelSize = vec2( 1.0 ) / ( shadowMapSize * vec2( 4.0, 2.0 ) );\n\t\t\t#if defined( SHADOWMAP_TYPE_PCF ) || defined( SHADOWMAP_TYPE_PCF_SOFT ) || defined( SHADOWMAP_TYPE_VSM )\n\t\t\t\tvec2 offset = vec2( - 1, 1 ) * shadowRadius * texelSize.y;\n\t\t\t\tshadow = (\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xyx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yyx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxy, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.xxx, texelSize.y ), dp ) +\n\t\t\t\t\ttexture2DCompare( shadowMap, cubeToUV( bd3D + offset.yxx, texelSize.y ), dp )\n\t\t\t\t) * ( 1.0 / 9.0 );\n\t\t\t#else\n\t\t\t\tshadow = texture2DCompare( shadowMap, cubeToUV( bd3D, texelSize.y ), dp );\n\t\t\t#endif\n\t\t}\n\t\treturn mix( 1.0, shadow, shadowIntensity );\n\t}\n#endif";

var shadowmap_pars_vertex = "#if NUM_SPOT_LIGHT_COORDS > 0\n\tuniform mat4 spotLightMatrix[ NUM_SPOT_LIGHT_COORDS ];\n\tvarying vec4 vSpotLightCoord[ NUM_SPOT_LIGHT_COORDS ];\n#endif\n#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\tuniform mat4 directionalShadowMatrix[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tvarying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];\n\t\tstruct DirectionalLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform DirectionalLightShadow directionalLightShadows[ NUM_DIR_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\t\tstruct SpotLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t};\n\t\tuniform SpotLightShadow spotLightShadows[ NUM_SPOT_LIGHT_SHADOWS ];\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\tuniform mat4 pointShadowMatrix[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tvarying vec4 vPointShadowCoord[ NUM_POINT_LIGHT_SHADOWS ];\n\t\tstruct PointLightShadow {\n\t\t\tfloat shadowIntensity;\n\t\t\tfloat shadowBias;\n\t\t\tfloat shadowNormalBias;\n\t\t\tfloat shadowRadius;\n\t\t\tvec2 shadowMapSize;\n\t\t\tfloat shadowCameraNear;\n\t\t\tfloat shadowCameraFar;\n\t\t};\n\t\tuniform PointLightShadow pointLightShadows[ NUM_POINT_LIGHT_SHADOWS ];\n\t#endif\n#endif";

var shadowmap_vertex = "#if ( defined( USE_SHADOWMAP ) && ( NUM_DIR_LIGHT_SHADOWS > 0 || NUM_POINT_LIGHT_SHADOWS > 0 ) ) || ( NUM_SPOT_LIGHT_COORDS > 0 )\n\tvec3 shadowWorldNormal = inverseTransformDirection( transformedNormal, viewMatrix );\n\tvec4 shadowWorldPosition;\n#endif\n#if defined( USE_SHADOWMAP )\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {\n\t\t\tshadowWorldPosition = worldPosition + vec4( shadowWorldNormal * directionalLightShadows[ i ].shadowNormalBias, 0 );\n\t\t\tvDirectionalShadowCoord[ i ] = directionalShadowMatrix[ i ] * shadowWorldPosition;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\t\t#pragma unroll_loop_start\n\t\tfor ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {\n\t\t\tshadowWorldPosition = worldPosition + vec4( shadowWorldNormal * pointLightShadows[ i ].shadowNormalBias, 0 );\n\t\t\tvPointShadowCoord[ i ] = pointShadowMatrix[ i ] * shadowWorldPosition;\n\t\t}\n\t\t#pragma unroll_loop_end\n\t#endif\n#endif\n#if NUM_SPOT_LIGHT_COORDS > 0\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHT_COORDS; i ++ ) {\n\t\tshadowWorldPosition = worldPosition;\n\t\t#if ( defined( USE_SHADOWMAP ) && UNROLLED_LOOP_INDEX < NUM_SPOT_LIGHT_SHADOWS )\n\t\t\tshadowWorldPosition.xyz += shadowWorldNormal * spotLightShadows[ i ].shadowNormalBias;\n\t\t#endif\n\t\tvSpotLightCoord[ i ] = spotLightMatrix[ i ] * shadowWorldPosition;\n\t}\n\t#pragma unroll_loop_end\n#endif";

var shadowmask_pars_fragment = "float getShadowMask() {\n\tfloat shadow = 1.0;\n\t#ifdef USE_SHADOWMAP\n\t#if NUM_DIR_LIGHT_SHADOWS > 0\n\tDirectionalLightShadow directionalLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {\n\t\tdirectionalLight = directionalLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getShadow( directionalShadowMap[ i ], directionalLight.shadowMapSize, directionalLight.shadowIntensity, directionalLight.shadowBias, directionalLight.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_SPOT_LIGHT_SHADOWS > 0\n\tSpotLightShadow spotLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_SPOT_LIGHT_SHADOWS; i ++ ) {\n\t\tspotLight = spotLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getShadow( spotShadowMap[ i ], spotLight.shadowMapSize, spotLight.shadowIntensity, spotLight.shadowBias, spotLight.shadowRadius, vSpotLightCoord[ i ] ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#if NUM_POINT_LIGHT_SHADOWS > 0\n\tPointLightShadow pointLight;\n\t#pragma unroll_loop_start\n\tfor ( int i = 0; i < NUM_POINT_LIGHT_SHADOWS; i ++ ) {\n\t\tpointLight = pointLightShadows[ i ];\n\t\tshadow *= receiveShadow ? getPointShadow( pointShadowMap[ i ], pointLight.shadowMapSize, pointLight.shadowIntensity, pointLight.shadowBias, pointLight.shadowRadius, vPointShadowCoord[ i ], pointLight.shadowCameraNear, pointLight.shadowCameraFar ) : 1.0;\n\t}\n\t#pragma unroll_loop_end\n\t#endif\n\t#endif\n\treturn shadow;\n}";

var skinbase_vertex = "#ifdef USE_SKINNING\n\tmat4 boneMatX = getBoneMatrix( skinIndex.x );\n\tmat4 boneMatY = getBoneMatrix( skinIndex.y );\n\tmat4 boneMatZ = getBoneMatrix( skinIndex.z );\n\tmat4 boneMatW = getBoneMatrix( skinIndex.w );\n#endif";

var skinning_pars_vertex = "#ifdef USE_SKINNING\n\tuniform mat4 bindMatrix;\n\tuniform mat4 bindMatrixInverse;\n\tuniform highp sampler2D boneTexture;\n\tmat4 getBoneMatrix( const in float i ) {\n\t\tint size = textureSize( boneTexture, 0 ).x;\n\t\tint j = int( i ) * 4;\n\t\tint x = j % size;\n\t\tint y = j / size;\n\t\tvec4 v1 = texelFetch( boneTexture, ivec2( x, y ), 0 );\n\t\tvec4 v2 = texelFetch( boneTexture, ivec2( x + 1, y ), 0 );\n\t\tvec4 v3 = texelFetch( boneTexture, ivec2( x + 2, y ), 0 );\n\t\tvec4 v4 = texelFetch( boneTexture, ivec2( x + 3, y ), 0 );\n\t\treturn mat4( v1, v2, v3, v4 );\n\t}\n#endif";

var skinning_vertex = "#ifdef USE_SKINNING\n\tvec4 skinVertex = bindMatrix * vec4( transformed, 1.0 );\n\tvec4 skinned = vec4( 0.0 );\n\tskinned += boneMatX * skinVertex * skinWeight.x;\n\tskinned += boneMatY * skinVertex * skinWeight.y;\n\tskinned += boneMatZ * skinVertex * skinWeight.z;\n\tskinned += boneMatW * skinVertex * skinWeight.w;\n\ttransformed = ( bindMatrixInverse * skinned ).xyz;\n#endif";

var skinnormal_vertex = "#ifdef USE_SKINNING\n\tmat4 skinMatrix = mat4( 0.0 );\n\tskinMatrix += skinWeight.x * boneMatX;\n\tskinMatrix += skinWeight.y * boneMatY;\n\tskinMatrix += skinWeight.z * boneMatZ;\n\tskinMatrix += skinWeight.w * boneMatW;\n\tskinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;\n\tobjectNormal = vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz;\n\t#ifdef USE_TANGENT\n\t\tobjectTangent = vec4( skinMatrix * vec4( objectTangent, 0.0 ) ).xyz;\n\t#endif\n#endif";

var specularmap_fragment = "float specularStrength;\n#ifdef USE_SPECULARMAP\n\tvec4 texelSpecular = texture2D( specularMap, vSpecularMapUv );\n\tspecularStrength = texelSpecular.r;\n#else\n\tspecularStrength = 1.0;\n#endif";

var specularmap_pars_fragment = "#ifdef USE_SPECULARMAP\n\tuniform sampler2D specularMap;\n#endif";

var tonemapping_fragment = "#if defined( TONE_MAPPING )\n\tgl_FragColor.rgb = toneMapping( gl_FragColor.rgb );\n#endif";

var tonemapping_pars_fragment = "#ifndef saturate\n#define saturate( a ) clamp( a, 0.0, 1.0 )\n#endif\nuniform float toneMappingExposure;\nvec3 LinearToneMapping( vec3 color ) {\n\treturn saturate( toneMappingExposure * color );\n}\nvec3 ReinhardToneMapping( vec3 color ) {\n\tcolor *= toneMappingExposure;\n\treturn saturate( color / ( vec3( 1.0 ) + color ) );\n}\nvec3 CineonToneMapping( vec3 color ) {\n\tcolor *= toneMappingExposure;\n\tcolor = max( vec3( 0.0 ), color - 0.004 );\n\treturn pow( ( color * ( 6.2 * color + 0.5 ) ) / ( color * ( 6.2 * color + 1.7 ) + 0.06 ), vec3( 2.2 ) );\n}\nvec3 RRTAndODTFit( vec3 v ) {\n\tvec3 a = v * ( v + 0.0245786 ) - 0.000090537;\n\tvec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;\n\treturn a / b;\n}\nvec3 ACESFilmicToneMapping( vec3 color ) {\n\tconst mat3 ACESInputMat = mat3(\n\t\tvec3( 0.59719, 0.07600, 0.02840 ),\t\tvec3( 0.35458, 0.90834, 0.13383 ),\n\t\tvec3( 0.04823, 0.01566, 0.83777 )\n\t);\n\tconst mat3 ACESOutputMat = mat3(\n\t\tvec3(  1.60475, -0.10208, -0.00327 ),\t\tvec3( -0.53108,  1.10813, -0.07276 ),\n\t\tvec3( -0.07367, -0.00605,  1.07602 )\n\t);\n\tcolor *= toneMappingExposure / 0.6;\n\tcolor = ACESInputMat * color;\n\tcolor = RRTAndODTFit( color );\n\tcolor = ACESOutputMat * color;\n\treturn saturate( color );\n}\nconst mat3 LINEAR_REC2020_TO_LINEAR_SRGB = mat3(\n\tvec3( 1.6605, - 0.1246, - 0.0182 ),\n\tvec3( - 0.5876, 1.1329, - 0.1006 ),\n\tvec3( - 0.0728, - 0.0083, 1.1187 )\n);\nconst mat3 LINEAR_SRGB_TO_LINEAR_REC2020 = mat3(\n\tvec3( 0.6274, 0.0691, 0.0164 ),\n\tvec3( 0.3293, 0.9195, 0.0880 ),\n\tvec3( 0.0433, 0.0113, 0.8956 )\n);\nvec3 agxDefaultContrastApprox( vec3 x ) {\n\tvec3 x2 = x * x;\n\tvec3 x4 = x2 * x2;\n\treturn + 15.5 * x4 * x2\n\t\t- 40.14 * x4 * x\n\t\t+ 31.96 * x4\n\t\t- 6.868 * x2 * x\n\t\t+ 0.4298 * x2\n\t\t+ 0.1191 * x\n\t\t- 0.00232;\n}\nvec3 AgXToneMapping( vec3 color ) {\n\tconst mat3 AgXInsetMatrix = mat3(\n\t\tvec3( 0.856627153315983, 0.137318972929847, 0.11189821299995 ),\n\t\tvec3( 0.0951212405381588, 0.761241990602591, 0.0767994186031903 ),\n\t\tvec3( 0.0482516061458583, 0.101439036467562, 0.811302368396859 )\n\t);\n\tconst mat3 AgXOutsetMatrix = mat3(\n\t\tvec3( 1.1271005818144368, - 0.1413297634984383, - 0.14132976349843826 ),\n\t\tvec3( - 0.11060664309660323, 1.157823702216272, - 0.11060664309660294 ),\n\t\tvec3( - 0.016493938717834573, - 0.016493938717834257, 1.2519364065950405 )\n\t);\n\tconst float AgxMinEv = - 12.47393;\tconst float AgxMaxEv = 4.026069;\n\tcolor *= toneMappingExposure;\n\tcolor = LINEAR_SRGB_TO_LINEAR_REC2020 * color;\n\tcolor = AgXInsetMatrix * color;\n\tcolor = max( color, 1e-10 );\tcolor = log2( color );\n\tcolor = ( color - AgxMinEv ) / ( AgxMaxEv - AgxMinEv );\n\tcolor = clamp( color, 0.0, 1.0 );\n\tcolor = agxDefaultContrastApprox( color );\n\tcolor = AgXOutsetMatrix * color;\n\tcolor = pow( max( vec3( 0.0 ), color ), vec3( 2.2 ) );\n\tcolor = LINEAR_REC2020_TO_LINEAR_SRGB * color;\n\tcolor = clamp( color, 0.0, 1.0 );\n\treturn color;\n}\nvec3 NeutralToneMapping( vec3 color ) {\n\tconst float StartCompression = 0.8 - 0.04;\n\tconst float Desaturation = 0.15;\n\tcolor *= toneMappingExposure;\n\tfloat x = min( color.r, min( color.g, color.b ) );\n\tfloat offset = x < 0.08 ? x - 6.25 * x * x : 0.04;\n\tcolor -= offset;\n\tfloat peak = max( color.r, max( color.g, color.b ) );\n\tif ( peak < StartCompression ) return color;\n\tfloat d = 1. - StartCompression;\n\tfloat newPeak = 1. - d * d / ( peak + d - StartCompression );\n\tcolor *= newPeak / peak;\n\tfloat g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. );\n\treturn mix( color, vec3( newPeak ), g );\n}\nvec3 CustomToneMapping( vec3 color ) { return color; }";

var transmission_fragment = "#ifdef USE_TRANSMISSION\n\tmaterial.transmission = transmission;\n\tmaterial.transmissionAlpha = 1.0;\n\tmaterial.thickness = thickness;\n\tmaterial.attenuationDistance = attenuationDistance;\n\tmaterial.attenuationColor = attenuationColor;\n\t#ifdef USE_TRANSMISSIONMAP\n\t\tmaterial.transmission *= texture2D( transmissionMap, vTransmissionMapUv ).r;\n\t#endif\n\t#ifdef USE_THICKNESSMAP\n\t\tmaterial.thickness *= texture2D( thicknessMap, vThicknessMapUv ).g;\n\t#endif\n\tvec3 pos = vWorldPosition;\n\tvec3 v = normalize( cameraPosition - pos );\n\tvec3 n = inverseTransformDirection( normal, viewMatrix );\n\tvec4 transmitted = getIBLVolumeRefraction(\n\t\tn, v, material.roughness, material.diffuseColor, material.specularColor, material.specularF90,\n\t\tpos, modelMatrix, viewMatrix, projectionMatrix, material.dispersion, material.ior, material.thickness,\n\t\tmaterial.attenuationColor, material.attenuationDistance );\n\tmaterial.transmissionAlpha = mix( material.transmissionAlpha, transmitted.a, material.transmission );\n\ttotalDiffuse = mix( totalDiffuse, transmitted.rgb, material.transmission );\n#endif";

var transmission_pars_fragment = "#ifdef USE_TRANSMISSION\n\tuniform float transmission;\n\tuniform float thickness;\n\tuniform float attenuationDistance;\n\tuniform vec3 attenuationColor;\n\t#ifdef USE_TRANSMISSIONMAP\n\t\tuniform sampler2D transmissionMap;\n\t#endif\n\t#ifdef USE_THICKNESSMAP\n\t\tuniform sampler2D thicknessMap;\n\t#endif\n\tuniform vec2 transmissionSamplerSize;\n\tuniform sampler2D transmissionSamplerMap;\n\tuniform mat4 modelMatrix;\n\tuniform mat4 projectionMatrix;\n\tvarying vec3 vWorldPosition;\n\tfloat w0( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a * ( a * ( - a + 3.0 ) - 3.0 ) + 1.0 );\n\t}\n\tfloat w1( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a *  a * ( 3.0 * a - 6.0 ) + 4.0 );\n\t}\n\tfloat w2( float a ){\n\t\treturn ( 1.0 / 6.0 ) * ( a * ( a * ( - 3.0 * a + 3.0 ) + 3.0 ) + 1.0 );\n\t}\n\tfloat w3( float a ) {\n\t\treturn ( 1.0 / 6.0 ) * ( a * a * a );\n\t}\n\tfloat g0( float a ) {\n\t\treturn w0( a ) + w1( a );\n\t}\n\tfloat g1( float a ) {\n\t\treturn w2( a ) + w3( a );\n\t}\n\tfloat h0( float a ) {\n\t\treturn - 1.0 + w1( a ) / ( w0( a ) + w1( a ) );\n\t}\n\tfloat h1( float a ) {\n\t\treturn 1.0 + w3( a ) / ( w2( a ) + w3( a ) );\n\t}\n\tvec4 bicubic( sampler2D tex, vec2 uv, vec4 texelSize, float lod ) {\n\t\tuv = uv * texelSize.zw + 0.5;\n\t\tvec2 iuv = floor( uv );\n\t\tvec2 fuv = fract( uv );\n\t\tfloat g0x = g0( fuv.x );\n\t\tfloat g1x = g1( fuv.x );\n\t\tfloat h0x = h0( fuv.x );\n\t\tfloat h1x = h1( fuv.x );\n\t\tfloat h0y = h0( fuv.y );\n\t\tfloat h1y = h1( fuv.y );\n\t\tvec2 p0 = ( vec2( iuv.x + h0x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p1 = ( vec2( iuv.x + h1x, iuv.y + h0y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p2 = ( vec2( iuv.x + h0x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;\n\t\tvec2 p3 = ( vec2( iuv.x + h1x, iuv.y + h1y ) - 0.5 ) * texelSize.xy;\n\t\treturn g0( fuv.y ) * ( g0x * textureLod( tex, p0, lod ) + g1x * textureLod( tex, p1, lod ) ) +\n\t\t\tg1( fuv.y ) * ( g0x * textureLod( tex, p2, lod ) + g1x * textureLod( tex, p3, lod ) );\n\t}\n\tvec4 textureBicubic( sampler2D sampler, vec2 uv, float lod ) {\n\t\tvec2 fLodSize = vec2( textureSize( sampler, int( lod ) ) );\n\t\tvec2 cLodSize = vec2( textureSize( sampler, int( lod + 1.0 ) ) );\n\t\tvec2 fLodSizeInv = 1.0 / fLodSize;\n\t\tvec2 cLodSizeInv = 1.0 / cLodSize;\n\t\tvec4 fSample = bicubic( sampler, uv, vec4( fLodSizeInv, fLodSize ), floor( lod ) );\n\t\tvec4 cSample = bicubic( sampler, uv, vec4( cLodSizeInv, cLodSize ), ceil( lod ) );\n\t\treturn mix( fSample, cSample, fract( lod ) );\n\t}\n\tvec3 getVolumeTransmissionRay( const in vec3 n, const in vec3 v, const in float thickness, const in float ior, const in mat4 modelMatrix ) {\n\t\tvec3 refractionVector = refract( - v, normalize( n ), 1.0 / ior );\n\t\tvec3 modelScale;\n\t\tmodelScale.x = length( vec3( modelMatrix[ 0 ].xyz ) );\n\t\tmodelScale.y = length( vec3( modelMatrix[ 1 ].xyz ) );\n\t\tmodelScale.z = length( vec3( modelMatrix[ 2 ].xyz ) );\n\t\treturn normalize( refractionVector ) * thickness * modelScale;\n\t}\n\tfloat applyIorToRoughness( const in float roughness, const in float ior ) {\n\t\treturn roughness * clamp( ior * 2.0 - 2.0, 0.0, 1.0 );\n\t}\n\tvec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {\n\t\tfloat lod = log2( transmissionSamplerSize.x ) * applyIorToRoughness( roughness, ior );\n\t\treturn textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );\n\t}\n\tvec3 volumeAttenuation( const in float transmissionDistance, const in vec3 attenuationColor, const in float attenuationDistance ) {\n\t\tif ( isinf( attenuationDistance ) ) {\n\t\t\treturn vec3( 1.0 );\n\t\t} else {\n\t\t\tvec3 attenuationCoefficient = -log( attenuationColor ) / attenuationDistance;\n\t\t\tvec3 transmittance = exp( - attenuationCoefficient * transmissionDistance );\t\t\treturn transmittance;\n\t\t}\n\t}\n\tvec4 getIBLVolumeRefraction( const in vec3 n, const in vec3 v, const in float roughness, const in vec3 diffuseColor,\n\t\tconst in vec3 specularColor, const in float specularF90, const in vec3 position, const in mat4 modelMatrix,\n\t\tconst in mat4 viewMatrix, const in mat4 projMatrix, const in float dispersion, const in float ior, const in float thickness,\n\t\tconst in vec3 attenuationColor, const in float attenuationDistance ) {\n\t\tvec4 transmittedLight;\n\t\tvec3 transmittance;\n\t\t#ifdef USE_DISPERSION\n\t\t\tfloat halfSpread = ( ior - 1.0 ) * 0.025 * dispersion;\n\t\t\tvec3 iors = vec3( ior - halfSpread, ior, ior + halfSpread );\n\t\t\tfor ( int i = 0; i < 3; i ++ ) {\n\t\t\t\tvec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, iors[ i ], modelMatrix );\n\t\t\t\tvec3 refractedRayExit = position + transmissionRay;\n\t\t\n\t\t\t\tvec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );\n\t\t\t\tvec2 refractionCoords = ndcPos.xy / ndcPos.w;\n\t\t\t\trefractionCoords += 1.0;\n\t\t\t\trefractionCoords /= 2.0;\n\t\t\n\t\t\t\tvec4 transmissionSample = getTransmissionSample( refractionCoords, roughness, iors[ i ] );\n\t\t\t\ttransmittedLight[ i ] = transmissionSample[ i ];\n\t\t\t\ttransmittedLight.a += transmissionSample.a;\n\t\t\t\ttransmittance[ i ] = diffuseColor[ i ] * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance )[ i ];\n\t\t\t}\n\t\t\ttransmittedLight.a /= 3.0;\n\t\t\n\t\t#else\n\t\t\n\t\t\tvec3 transmissionRay = getVolumeTransmissionRay( n, v, thickness, ior, modelMatrix );\n\t\t\tvec3 refractedRayExit = position + transmissionRay;\n\t\t\tvec4 ndcPos = projMatrix * viewMatrix * vec4( refractedRayExit, 1.0 );\n\t\t\tvec2 refractionCoords = ndcPos.xy / ndcPos.w;\n\t\t\trefractionCoords += 1.0;\n\t\t\trefractionCoords /= 2.0;\n\t\t\ttransmittedLight = getTransmissionSample( refractionCoords, roughness, ior );\n\t\t\ttransmittance = diffuseColor * volumeAttenuation( length( transmissionRay ), attenuationColor, attenuationDistance );\n\t\t\n\t\t#endif\n\t\tvec3 attenuatedColor = transmittance * transmittedLight.rgb;\n\t\tvec3 F = EnvironmentBRDF( n, v, specularColor, specularF90, roughness );\n\t\tfloat transmittanceFactor = ( transmittance.r + transmittance.g + transmittance.b ) / 3.0;\n\t\treturn vec4( ( 1.0 - F ) * attenuatedColor, 1.0 - ( 1.0 - transmittedLight.a ) * transmittanceFactor );\n\t}\n#endif";

var uv_pars_fragment = "#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvarying vec2 vUv;\n#endif\n#ifdef USE_MAP\n\tvarying vec2 vMapUv;\n#endif\n#ifdef USE_ALPHAMAP\n\tvarying vec2 vAlphaMapUv;\n#endif\n#ifdef USE_LIGHTMAP\n\tvarying vec2 vLightMapUv;\n#endif\n#ifdef USE_AOMAP\n\tvarying vec2 vAoMapUv;\n#endif\n#ifdef USE_BUMPMAP\n\tvarying vec2 vBumpMapUv;\n#endif\n#ifdef USE_NORMALMAP\n\tvarying vec2 vNormalMapUv;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tvarying vec2 vEmissiveMapUv;\n#endif\n#ifdef USE_METALNESSMAP\n\tvarying vec2 vMetalnessMapUv;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tvarying vec2 vRoughnessMapUv;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tvarying vec2 vAnisotropyMapUv;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tvarying vec2 vClearcoatMapUv;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tvarying vec2 vClearcoatNormalMapUv;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tvarying vec2 vClearcoatRoughnessMapUv;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tvarying vec2 vIridescenceMapUv;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tvarying vec2 vIridescenceThicknessMapUv;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tvarying vec2 vSheenColorMapUv;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tvarying vec2 vSheenRoughnessMapUv;\n#endif\n#ifdef USE_SPECULARMAP\n\tvarying vec2 vSpecularMapUv;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tvarying vec2 vSpecularColorMapUv;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tvarying vec2 vSpecularIntensityMapUv;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tuniform mat3 transmissionMapTransform;\n\tvarying vec2 vTransmissionMapUv;\n#endif\n#ifdef USE_THICKNESSMAP\n\tuniform mat3 thicknessMapTransform;\n\tvarying vec2 vThicknessMapUv;\n#endif";

var uv_pars_vertex = "#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvarying vec2 vUv;\n#endif\n#ifdef USE_MAP\n\tuniform mat3 mapTransform;\n\tvarying vec2 vMapUv;\n#endif\n#ifdef USE_ALPHAMAP\n\tuniform mat3 alphaMapTransform;\n\tvarying vec2 vAlphaMapUv;\n#endif\n#ifdef USE_LIGHTMAP\n\tuniform mat3 lightMapTransform;\n\tvarying vec2 vLightMapUv;\n#endif\n#ifdef USE_AOMAP\n\tuniform mat3 aoMapTransform;\n\tvarying vec2 vAoMapUv;\n#endif\n#ifdef USE_BUMPMAP\n\tuniform mat3 bumpMapTransform;\n\tvarying vec2 vBumpMapUv;\n#endif\n#ifdef USE_NORMALMAP\n\tuniform mat3 normalMapTransform;\n\tvarying vec2 vNormalMapUv;\n#endif\n#ifdef USE_DISPLACEMENTMAP\n\tuniform mat3 displacementMapTransform;\n\tvarying vec2 vDisplacementMapUv;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tuniform mat3 emissiveMapTransform;\n\tvarying vec2 vEmissiveMapUv;\n#endif\n#ifdef USE_METALNESSMAP\n\tuniform mat3 metalnessMapTransform;\n\tvarying vec2 vMetalnessMapUv;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tuniform mat3 roughnessMapTransform;\n\tvarying vec2 vRoughnessMapUv;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tuniform mat3 anisotropyMapTransform;\n\tvarying vec2 vAnisotropyMapUv;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tuniform mat3 clearcoatMapTransform;\n\tvarying vec2 vClearcoatMapUv;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tuniform mat3 clearcoatNormalMapTransform;\n\tvarying vec2 vClearcoatNormalMapUv;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tuniform mat3 clearcoatRoughnessMapTransform;\n\tvarying vec2 vClearcoatRoughnessMapUv;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tuniform mat3 sheenColorMapTransform;\n\tvarying vec2 vSheenColorMapUv;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tuniform mat3 sheenRoughnessMapTransform;\n\tvarying vec2 vSheenRoughnessMapUv;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tuniform mat3 iridescenceMapTransform;\n\tvarying vec2 vIridescenceMapUv;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tuniform mat3 iridescenceThicknessMapTransform;\n\tvarying vec2 vIridescenceThicknessMapUv;\n#endif\n#ifdef USE_SPECULARMAP\n\tuniform mat3 specularMapTransform;\n\tvarying vec2 vSpecularMapUv;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tuniform mat3 specularColorMapTransform;\n\tvarying vec2 vSpecularColorMapUv;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tuniform mat3 specularIntensityMapTransform;\n\tvarying vec2 vSpecularIntensityMapUv;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tuniform mat3 transmissionMapTransform;\n\tvarying vec2 vTransmissionMapUv;\n#endif\n#ifdef USE_THICKNESSMAP\n\tuniform mat3 thicknessMapTransform;\n\tvarying vec2 vThicknessMapUv;\n#endif";

var uv_vertex = "#if defined( USE_UV ) || defined( USE_ANISOTROPY )\n\tvUv = vec3( uv, 1 ).xy;\n#endif\n#ifdef USE_MAP\n\tvMapUv = ( mapTransform * vec3( MAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ALPHAMAP\n\tvAlphaMapUv = ( alphaMapTransform * vec3( ALPHAMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_LIGHTMAP\n\tvLightMapUv = ( lightMapTransform * vec3( LIGHTMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_AOMAP\n\tvAoMapUv = ( aoMapTransform * vec3( AOMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_BUMPMAP\n\tvBumpMapUv = ( bumpMapTransform * vec3( BUMPMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_NORMALMAP\n\tvNormalMapUv = ( normalMapTransform * vec3( NORMALMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_DISPLACEMENTMAP\n\tvDisplacementMapUv = ( displacementMapTransform * vec3( DISPLACEMENTMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_EMISSIVEMAP\n\tvEmissiveMapUv = ( emissiveMapTransform * vec3( EMISSIVEMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_METALNESSMAP\n\tvMetalnessMapUv = ( metalnessMapTransform * vec3( METALNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ROUGHNESSMAP\n\tvRoughnessMapUv = ( roughnessMapTransform * vec3( ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_ANISOTROPYMAP\n\tvAnisotropyMapUv = ( anisotropyMapTransform * vec3( ANISOTROPYMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOATMAP\n\tvClearcoatMapUv = ( clearcoatMapTransform * vec3( CLEARCOATMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOAT_NORMALMAP\n\tvClearcoatNormalMapUv = ( clearcoatNormalMapTransform * vec3( CLEARCOAT_NORMALMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_CLEARCOAT_ROUGHNESSMAP\n\tvClearcoatRoughnessMapUv = ( clearcoatRoughnessMapTransform * vec3( CLEARCOAT_ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_IRIDESCENCEMAP\n\tvIridescenceMapUv = ( iridescenceMapTransform * vec3( IRIDESCENCEMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_IRIDESCENCE_THICKNESSMAP\n\tvIridescenceThicknessMapUv = ( iridescenceThicknessMapTransform * vec3( IRIDESCENCE_THICKNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SHEEN_COLORMAP\n\tvSheenColorMapUv = ( sheenColorMapTransform * vec3( SHEEN_COLORMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SHEEN_ROUGHNESSMAP\n\tvSheenRoughnessMapUv = ( sheenRoughnessMapTransform * vec3( SHEEN_ROUGHNESSMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULARMAP\n\tvSpecularMapUv = ( specularMapTransform * vec3( SPECULARMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULAR_COLORMAP\n\tvSpecularColorMapUv = ( specularColorMapTransform * vec3( SPECULAR_COLORMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_SPECULAR_INTENSITYMAP\n\tvSpecularIntensityMapUv = ( specularIntensityMapTransform * vec3( SPECULAR_INTENSITYMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_TRANSMISSIONMAP\n\tvTransmissionMapUv = ( transmissionMapTransform * vec3( TRANSMISSIONMAP_UV, 1 ) ).xy;\n#endif\n#ifdef USE_THICKNESSMAP\n\tvThicknessMapUv = ( thicknessMapTransform * vec3( THICKNESSMAP_UV, 1 ) ).xy;\n#endif";

var worldpos_vertex = "#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0\n\tvec4 worldPosition = vec4( transformed, 1.0 );\n\t#ifdef USE_BATCHING\n\t\tworldPosition = batchingMatrix * worldPosition;\n\t#endif\n\t#ifdef USE_INSTANCING\n\t\tworldPosition = instanceMatrix * worldPosition;\n\t#endif\n\tworldPosition = modelMatrix * worldPosition;\n#endif";

const vertex$h = "varying vec2 vUv;\nuniform mat3 uvTransform;\nvoid main() {\n\tvUv = ( uvTransform * vec3( uv, 1 ) ).xy;\n\tgl_Position = vec4( position.xy, 1.0, 1.0 );\n}";

const fragment$h = "uniform sampler2D t2D;\nuniform float backgroundIntensity;\nvarying vec2 vUv;\nvoid main() {\n\tvec4 texColor = texture2D( t2D, vUv );\n\t#ifdef DECODE_VIDEO_TEXTURE\n\t\ttexColor = vec4( mix( pow( texColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), texColor.rgb * 0.0773993808, vec3( lessThanEqual( texColor.rgb, vec3( 0.04045 ) ) ) ), texColor.w );\n\t#endif\n\ttexColor.rgb *= backgroundIntensity;\n\tgl_FragColor = texColor;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";

const vertex$g = "varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n\tgl_Position.z = gl_Position.w;\n}";

const fragment$g = "#ifdef ENVMAP_TYPE_CUBE\n\tuniform samplerCube envMap;\n#elif defined( ENVMAP_TYPE_CUBE_UV )\n\tuniform sampler2D envMap;\n#endif\nuniform float flipEnvMap;\nuniform float backgroundBlurriness;\nuniform float backgroundIntensity;\nuniform mat3 backgroundRotation;\nvarying vec3 vWorldDirection;\n#include <cube_uv_reflection_fragment>\nvoid main() {\n\t#ifdef ENVMAP_TYPE_CUBE\n\t\tvec4 texColor = textureCube( envMap, backgroundRotation * vec3( flipEnvMap * vWorldDirection.x, vWorldDirection.yz ) );\n\t#elif defined( ENVMAP_TYPE_CUBE_UV )\n\t\tvec4 texColor = textureCubeUV( envMap, backgroundRotation * vWorldDirection, backgroundBlurriness );\n\t#else\n\t\tvec4 texColor = vec4( 0.0, 0.0, 0.0, 1.0 );\n\t#endif\n\ttexColor.rgb *= backgroundIntensity;\n\tgl_FragColor = texColor;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";

const vertex$f = "varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n\tgl_Position.z = gl_Position.w;\n}";

const fragment$f = "uniform samplerCube tCube;\nuniform float tFlip;\nuniform float opacity;\nvarying vec3 vWorldDirection;\nvoid main() {\n\tvec4 texColor = textureCube( tCube, vec3( tFlip * vWorldDirection.x, vWorldDirection.yz ) );\n\tgl_FragColor = texColor;\n\tgl_FragColor.a *= opacity;\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";

const vertex$e = "#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvarying vec2 vHighPrecisionZW;\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <skinbase_vertex>\n\t#include <morphinstance_vertex>\n\t#ifdef USE_DISPLACEMENTMAP\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvHighPrecisionZW = gl_Position.zw;\n}";

const fragment$e = "#if DEPTH_PACKING == 3200\n\tuniform float opacity;\n#endif\n#include <common>\n#include <packing>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvarying vec2 vHighPrecisionZW;\nvoid main() {\n\tvec4 diffuseColor = vec4( 1.0 );\n\t#include <clipping_planes_fragment>\n\t#if DEPTH_PACKING == 3200\n\t\tdiffuseColor.a = opacity;\n\t#endif\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <logdepthbuf_fragment>\n\tfloat fragCoordZ = 0.5 * vHighPrecisionZW[0] / vHighPrecisionZW[1] + 0.5;\n\t#if DEPTH_PACKING == 3200\n\t\tgl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );\n\t#elif DEPTH_PACKING == 3201\n\t\tgl_FragColor = packDepthToRGBA( fragCoordZ );\n\t#elif DEPTH_PACKING == 3202\n\t\tgl_FragColor = vec4( packDepthToRGB( fragCoordZ ), 1.0 );\n\t#elif DEPTH_PACKING == 3203\n\t\tgl_FragColor = vec4( packDepthToRG( fragCoordZ ), 0.0, 1.0 );\n\t#endif\n}";

const vertex$d = "#define DISTANCE\nvarying vec3 vWorldPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <skinbase_vertex>\n\t#include <morphinstance_vertex>\n\t#ifdef USE_DISPLACEMENTMAP\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <worldpos_vertex>\n\t#include <clipping_planes_vertex>\n\tvWorldPosition = worldPosition.xyz;\n}";

const fragment$d = "#define DISTANCE\nuniform vec3 referencePosition;\nuniform float nearDistance;\nuniform float farDistance;\nvarying vec3 vWorldPosition;\n#include <common>\n#include <packing>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main () {\n\tvec4 diffuseColor = vec4( 1.0 );\n\t#include <clipping_planes_fragment>\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\tfloat dist = length( vWorldPosition - referencePosition );\n\tdist = ( dist - nearDistance ) / ( farDistance - nearDistance );\n\tdist = saturate( dist );\n\tgl_FragColor = packDepthToRGBA( dist );\n}";

const vertex$c = "varying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvWorldDirection = transformDirection( position, modelMatrix );\n\t#include <begin_vertex>\n\t#include <project_vertex>\n}";

const fragment$c = "uniform sampler2D tEquirect;\nvarying vec3 vWorldDirection;\n#include <common>\nvoid main() {\n\tvec3 direction = normalize( vWorldDirection );\n\tvec2 sampleUV = equirectUv( direction );\n\tgl_FragColor = texture2D( tEquirect, sampleUV );\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n}";

const vertex$b = "uniform float scale;\nattribute float lineDistance;\nvarying float vLineDistance;\n#include <common>\n#include <uv_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\tvLineDistance = scale * lineDistance;\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n}";

const fragment$b = "uniform vec3 diffuse;\nuniform float opacity;\nuniform float dashSize;\nuniform float totalSize;\nvarying float vLineDistance;\n#include <common>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tif ( mod( vLineDistance, totalSize ) > dashSize ) {\n\t\tdiscard;\n\t}\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n}";

const vertex$a = "#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#if defined ( USE_ENVMAP ) || defined ( USE_SKINNING )\n\t\t#include <beginnormal_vertex>\n\t\t#include <morphnormal_vertex>\n\t\t#include <skinbase_vertex>\n\t\t#include <skinnormal_vertex>\n\t\t#include <defaultnormal_vertex>\n\t#endif\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <fog_vertex>\n}";

const fragment$a = "uniform vec3 diffuse;\nuniform float opacity;\n#ifndef FLAT_SHADED\n\tvarying vec3 vNormal;\n#endif\n#include <common>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\t#ifdef USE_LIGHTMAP\n\t\tvec4 lightMapTexel = texture2D( lightMap, vLightMapUv );\n\t\treflectedLight.indirectDiffuse += lightMapTexel.rgb * lightMapIntensity * RECIPROCAL_PI;\n\t#else\n\t\treflectedLight.indirectDiffuse += vec3( 1.0 );\n\t#endif\n\t#include <aomap_fragment>\n\treflectedLight.indirectDiffuse *= diffuseColor.rgb;\n\tvec3 outgoingLight = reflectedLight.indirectDiffuse;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$9 = "#define LAMBERT\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";

const fragment$9 = "#define LAMBERT\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_lambert_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_lambert_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$8 = "#define MATCAP\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <color_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n\tvViewPosition = - mvPosition.xyz;\n}";

const fragment$8 = "#define MATCAP\nuniform vec3 diffuse;\nuniform float opacity;\nuniform sampler2D matcap;\nvarying vec3 vViewPosition;\n#include <common>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <normal_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\tvec3 viewDir = normalize( vViewPosition );\n\tvec3 x = normalize( vec3( viewDir.z, 0.0, - viewDir.x ) );\n\tvec3 y = cross( viewDir, x );\n\tvec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5;\n\t#ifdef USE_MATCAP\n\t\tvec4 matcapColor = texture2D( matcap, uv );\n\t#else\n\t\tvec4 matcapColor = vec4( vec3( mix( 0.2, 0.8, uv.y ) ), 1.0 );\n\t#endif\n\tvec3 outgoingLight = diffuseColor.rgb * matcapColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$7 = "#define NORMAL\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvarying vec3 vViewPosition;\n#endif\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvViewPosition = - mvPosition.xyz;\n#endif\n}";

const fragment$7 = "#define NORMAL\nuniform float opacity;\n#if defined( FLAT_SHADED ) || defined( USE_BUMPMAP ) || defined( USE_NORMALMAP_TANGENTSPACE )\n\tvarying vec3 vViewPosition;\n#endif\n#include <packing>\n#include <uv_pars_fragment>\n#include <normal_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( 0.0, 0.0, 0.0, opacity );\n\t#include <clipping_planes_fragment>\n\t#include <logdepthbuf_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\tgl_FragColor = vec4( packNormalToRGB( normal ), diffuseColor.a );\n\t#ifdef OPAQUE\n\t\tgl_FragColor.a = 1.0;\n\t#endif\n}";

const vertex$6 = "#define PHONG\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <envmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <envmap_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";

const fragment$6 = "#define PHONG\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform vec3 specular;\nuniform float shininess;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_phong_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <specularmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <specularmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_phong_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;\n\t#include <envmap_fragment>\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$5 = "#define STANDARD\nvarying vec3 vViewPosition;\n#ifdef USE_TRANSMISSION\n\tvarying vec3 vWorldPosition;\n#endif\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n#ifdef USE_TRANSMISSION\n\tvWorldPosition = worldPosition.xyz;\n#endif\n}";

const fragment$5 = "#define STANDARD\n#ifdef PHYSICAL\n\t#define IOR\n\t#define USE_SPECULAR\n#endif\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float roughness;\nuniform float metalness;\nuniform float opacity;\n#ifdef IOR\n\tuniform float ior;\n#endif\n#ifdef USE_SPECULAR\n\tuniform float specularIntensity;\n\tuniform vec3 specularColor;\n\t#ifdef USE_SPECULAR_COLORMAP\n\t\tuniform sampler2D specularColorMap;\n\t#endif\n\t#ifdef USE_SPECULAR_INTENSITYMAP\n\t\tuniform sampler2D specularIntensityMap;\n\t#endif\n#endif\n#ifdef USE_CLEARCOAT\n\tuniform float clearcoat;\n\tuniform float clearcoatRoughness;\n#endif\n#ifdef USE_DISPERSION\n\tuniform float dispersion;\n#endif\n#ifdef USE_IRIDESCENCE\n\tuniform float iridescence;\n\tuniform float iridescenceIOR;\n\tuniform float iridescenceThicknessMinimum;\n\tuniform float iridescenceThicknessMaximum;\n#endif\n#ifdef USE_SHEEN\n\tuniform vec3 sheenColor;\n\tuniform float sheenRoughness;\n\t#ifdef USE_SHEEN_COLORMAP\n\t\tuniform sampler2D sheenColorMap;\n\t#endif\n\t#ifdef USE_SHEEN_ROUGHNESSMAP\n\t\tuniform sampler2D sheenRoughnessMap;\n\t#endif\n#endif\n#ifdef USE_ANISOTROPY\n\tuniform vec2 anisotropyVector;\n\t#ifdef USE_ANISOTROPYMAP\n\t\tuniform sampler2D anisotropyMap;\n\t#endif\n#endif\nvarying vec3 vViewPosition;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <iridescence_fragment>\n#include <cube_uv_reflection_fragment>\n#include <envmap_common_pars_fragment>\n#include <envmap_physical_pars_fragment>\n#include <fog_pars_fragment>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_physical_pars_fragment>\n#include <transmission_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <clearcoat_pars_fragment>\n#include <iridescence_pars_fragment>\n#include <roughnessmap_pars_fragment>\n#include <metalnessmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <roughnessmap_fragment>\n\t#include <metalnessmap_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <clearcoat_normal_fragment_begin>\n\t#include <clearcoat_normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_physical_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;\n\tvec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;\n\t#include <transmission_fragment>\n\tvec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;\n\t#ifdef USE_SHEEN\n\t\tfloat sheenEnergyComp = 1.0 - 0.157 * max3( material.sheenColor );\n\t\toutgoingLight = outgoingLight * sheenEnergyComp + sheenSpecularDirect + sheenSpecularIndirect;\n\t#endif\n\t#ifdef USE_CLEARCOAT\n\t\tfloat dotNVcc = saturate( dot( geometryClearcoatNormal, geometryViewDir ) );\n\t\tvec3 Fcc = F_Schlick( material.clearcoatF0, material.clearcoatF90, dotNVcc );\n\t\toutgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * material.clearcoat;\n\t#endif\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$4 = "#define TOON\nvarying vec3 vViewPosition;\n#include <common>\n#include <batching_pars_vertex>\n#include <uv_pars_vertex>\n#include <displacementmap_pars_vertex>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <normal_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <shadowmap_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <normal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <displacementmap_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\tvViewPosition = - mvPosition.xyz;\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";

const fragment$4 = "#define TOON\nuniform vec3 diffuse;\nuniform vec3 emissive;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <dithering_pars_fragment>\n#include <color_pars_fragment>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <aomap_pars_fragment>\n#include <lightmap_pars_fragment>\n#include <emissivemap_pars_fragment>\n#include <gradientmap_pars_fragment>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <normal_pars_fragment>\n#include <lights_toon_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <bumpmap_pars_fragment>\n#include <normalmap_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );\n\tvec3 totalEmissiveRadiance = emissive;\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <color_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\t#include <normal_fragment_begin>\n\t#include <normal_fragment_maps>\n\t#include <emissivemap_fragment>\n\t#include <lights_toon_fragment>\n\t#include <lights_fragment_begin>\n\t#include <lights_fragment_maps>\n\t#include <lights_fragment_end>\n\t#include <aomap_fragment>\n\tvec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n\t#include <dithering_fragment>\n}";

const vertex$3 = "uniform float size;\nuniform float scale;\n#include <common>\n#include <color_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\n#ifdef USE_POINTS_UV\n\tvarying vec2 vUv;\n\tuniform mat3 uvTransform;\n#endif\nvoid main() {\n\t#ifdef USE_POINTS_UV\n\t\tvUv = ( uvTransform * vec3( uv, 1 ) ).xy;\n\t#endif\n\t#include <color_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphcolor_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <project_vertex>\n\tgl_PointSize = size;\n\t#ifdef USE_SIZEATTENUATION\n\t\tbool isPerspective = isPerspectiveMatrix( projectionMatrix );\n\t\tif ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );\n\t#endif\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <worldpos_vertex>\n\t#include <fog_vertex>\n}";

const fragment$3 = "uniform vec3 diffuse;\nuniform float opacity;\n#include <common>\n#include <color_pars_fragment>\n#include <map_particle_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_particle_fragment>\n\t#include <color_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n\t#include <premultiplied_alpha_fragment>\n}";

const vertex$2 = "#include <common>\n#include <batching_pars_vertex>\n#include <fog_pars_vertex>\n#include <morphtarget_pars_vertex>\n#include <skinning_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <shadowmap_pars_vertex>\nvoid main() {\n\t#include <batching_vertex>\n\t#include <beginnormal_vertex>\n\t#include <morphinstance_vertex>\n\t#include <morphnormal_vertex>\n\t#include <skinbase_vertex>\n\t#include <skinnormal_vertex>\n\t#include <defaultnormal_vertex>\n\t#include <begin_vertex>\n\t#include <morphtarget_vertex>\n\t#include <skinning_vertex>\n\t#include <project_vertex>\n\t#include <logdepthbuf_vertex>\n\t#include <worldpos_vertex>\n\t#include <shadowmap_vertex>\n\t#include <fog_vertex>\n}";

const fragment$2 = "uniform vec3 color;\nuniform float opacity;\n#include <common>\n#include <packing>\n#include <fog_pars_fragment>\n#include <bsdfs>\n#include <lights_pars_begin>\n#include <logdepthbuf_pars_fragment>\n#include <shadowmap_pars_fragment>\n#include <shadowmask_pars_fragment>\nvoid main() {\n\t#include <logdepthbuf_fragment>\n\tgl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n}";

const vertex$1 = "uniform float rotation;\nuniform vec2 center;\n#include <common>\n#include <uv_pars_vertex>\n#include <fog_pars_vertex>\n#include <logdepthbuf_pars_vertex>\n#include <clipping_planes_pars_vertex>\nvoid main() {\n\t#include <uv_vertex>\n\tvec4 mvPosition = modelViewMatrix[ 3 ];\n\tvec2 scale = vec2( length( modelMatrix[ 0 ].xyz ), length( modelMatrix[ 1 ].xyz ) );\n\t#ifndef USE_SIZEATTENUATION\n\t\tbool isPerspective = isPerspectiveMatrix( projectionMatrix );\n\t\tif ( isPerspective ) scale *= - mvPosition.z;\n\t#endif\n\tvec2 alignedPosition = ( position.xy - ( center - vec2( 0.5 ) ) ) * scale;\n\tvec2 rotatedPosition;\n\trotatedPosition.x = cos( rotation ) * alignedPosition.x - sin( rotation ) * alignedPosition.y;\n\trotatedPosition.y = sin( rotation ) * alignedPosition.x + cos( rotation ) * alignedPosition.y;\n\tmvPosition.xy += rotatedPosition;\n\tgl_Position = projectionMatrix * mvPosition;\n\t#include <logdepthbuf_vertex>\n\t#include <clipping_planes_vertex>\n\t#include <fog_vertex>\n}";

const fragment$1 = "uniform vec3 diffuse;\nuniform float opacity;\n#include <common>\n#include <uv_pars_fragment>\n#include <map_pars_fragment>\n#include <alphamap_pars_fragment>\n#include <alphatest_pars_fragment>\n#include <alphahash_pars_fragment>\n#include <fog_pars_fragment>\n#include <logdepthbuf_pars_fragment>\n#include <clipping_planes_pars_fragment>\nvoid main() {\n\tvec4 diffuseColor = vec4( diffuse, opacity );\n\t#include <clipping_planes_fragment>\n\tvec3 outgoingLight = vec3( 0.0 );\n\t#include <logdepthbuf_fragment>\n\t#include <map_fragment>\n\t#include <alphamap_fragment>\n\t#include <alphatest_fragment>\n\t#include <alphahash_fragment>\n\toutgoingLight = diffuseColor.rgb;\n\t#include <opaque_fragment>\n\t#include <tonemapping_fragment>\n\t#include <colorspace_fragment>\n\t#include <fog_fragment>\n}";

const ShaderChunk = {
	alphahash_fragment: alphahash_fragment,
	alphahash_pars_fragment: alphahash_pars_fragment,
	alphamap_fragment: alphamap_fragment,
	alphamap_pars_fragment: alphamap_pars_fragment,
	alphatest_fragment: alphatest_fragment,
	alphatest_pars_fragment: alphatest_pars_fragment,
	aomap_fragment: aomap_fragment,
	aomap_pars_fragment: aomap_pars_fragment,
	batching_pars_vertex: batching_pars_vertex,
	batching_vertex: batching_vertex,
	begin_vertex: begin_vertex,
	beginnormal_vertex: beginnormal_vertex,
	bsdfs: bsdfs,
	iridescence_fragment: iridescence_fragment,
	bumpmap_pars_fragment: bumpmap_pars_fragment,
	clipping_planes_fragment: clipping_planes_fragment,
	clipping_planes_pars_fragment: clipping_planes_pars_fragment,
	clipping_planes_pars_vertex: clipping_planes_pars_vertex,
	clipping_planes_vertex: clipping_planes_vertex,
	color_fragment: color_fragment,
	color_pars_fragment: color_pars_fragment,
	color_pars_vertex: color_pars_vertex,
	color_vertex: color_vertex,
	common: common,
	cube_uv_reflection_fragment: cube_uv_reflection_fragment,
	defaultnormal_vertex: defaultnormal_vertex,
	displacementmap_pars_vertex: displacementmap_pars_vertex,
	displacementmap_vertex: displacementmap_vertex,
	emissivemap_fragment: emissivemap_fragment,
	emissivemap_pars_fragment: emissivemap_pars_fragment,
	colorspace_fragment: colorspace_fragment,
	colorspace_pars_fragment: colorspace_pars_fragment,
	envmap_fragment: envmap_fragment,
	envmap_common_pars_fragment: envmap_common_pars_fragment,
	envmap_pars_fragment: envmap_pars_fragment,
	envmap_pars_vertex: envmap_pars_vertex,
	envmap_physical_pars_fragment: envmap_physical_pars_fragment,
	envmap_vertex: envmap_vertex,
	fog_vertex: fog_vertex,
	fog_pars_vertex: fog_pars_vertex,
	fog_fragment: fog_fragment,
	fog_pars_fragment: fog_pars_fragment,
	gradientmap_pars_fragment: gradientmap_pars_fragment,
	lightmap_pars_fragment: lightmap_pars_fragment,
	lights_lambert_fragment: lights_lambert_fragment,
	lights_lambert_pars_fragment: lights_lambert_pars_fragment,
	lights_pars_begin: lights_pars_begin,
	lights_toon_fragment: lights_toon_fragment,
	lights_toon_pars_fragment: lights_toon_pars_fragment,
	lights_phong_fragment: lights_phong_fragment,
	lights_phong_pars_fragment: lights_phong_pars_fragment,
	lights_physical_fragment: lights_physical_fragment,
	lights_physical_pars_fragment: lights_physical_pars_fragment,
	lights_fragment_begin: lights_fragment_begin,
	lights_fragment_maps: lights_fragment_maps,
	lights_fragment_end: lights_fragment_end,
	logdepthbuf_fragment: logdepthbuf_fragment,
	logdepthbuf_pars_fragment: logdepthbuf_pars_fragment,
	logdepthbuf_pars_vertex: logdepthbuf_pars_vertex,
	logdepthbuf_vertex: logdepthbuf_vertex,
	map_fragment: map_fragment,
	map_pars_fragment: map_pars_fragment,
	map_particle_fragment: map_particle_fragment,
	map_particle_pars_fragment: map_particle_pars_fragment,
	metalnessmap_fragment: metalnessmap_fragment,
	metalnessmap_pars_fragment: metalnessmap_pars_fragment,
	morphinstance_vertex: morphinstance_vertex,
	morphcolor_vertex: morphcolor_vertex,
	morphnormal_vertex: morphnormal_vertex,
	morphtarget_pars_vertex: morphtarget_pars_vertex,
	morphtarget_vertex: morphtarget_vertex,
	normal_fragment_begin: normal_fragment_begin,
	normal_fragment_maps: normal_fragment_maps,
	normal_pars_fragment: normal_pars_fragment,
	normal_pars_vertex: normal_pars_vertex,
	normal_vertex: normal_vertex,
	normalmap_pars_fragment: normalmap_pars_fragment,
	clearcoat_normal_fragment_begin: clearcoat_normal_fragment_begin,
	clearcoat_normal_fragment_maps: clearcoat_normal_fragment_maps,
	clearcoat_pars_fragment: clearcoat_pars_fragment,
	iridescence_pars_fragment: iridescence_pars_fragment,
	opaque_fragment: opaque_fragment,
	packing: packing,
	premultiplied_alpha_fragment: premultiplied_alpha_fragment,
	project_vertex: project_vertex,
	dithering_fragment: dithering_fragment,
	dithering_pars_fragment: dithering_pars_fragment,
	roughnessmap_fragment: roughnessmap_fragment,
	roughnessmap_pars_fragment: roughnessmap_pars_fragment,
	shadowmap_pars_fragment: shadowmap_pars_fragment,
	shadowmap_pars_vertex: shadowmap_pars_vertex,
	shadowmap_vertex: shadowmap_vertex,
	shadowmask_pars_fragment: shadowmask_pars_fragment,
	skinbase_vertex: skinbase_vertex,
	skinning_pars_vertex: skinning_pars_vertex,
	skinning_vertex: skinning_vertex,
	skinnormal_vertex: skinnormal_vertex,
	specularmap_fragment: specularmap_fragment,
	specularmap_pars_fragment: specularmap_pars_fragment,
	tonemapping_fragment: tonemapping_fragment,
	tonemapping_pars_fragment: tonemapping_pars_fragment,
	transmission_fragment: transmission_fragment,
	transmission_pars_fragment: transmission_pars_fragment,
	uv_pars_fragment: uv_pars_fragment,
	uv_pars_vertex: uv_pars_vertex,
	uv_vertex: uv_vertex,
	worldpos_vertex: worldpos_vertex,

	background_vert: vertex$h,
	background_frag: fragment$h,
	backgroundCube_vert: vertex$g,
	backgroundCube_frag: fragment$g,
	cube_vert: vertex$f,
	cube_frag: fragment$f,
	depth_vert: vertex$e,
	depth_frag: fragment$e,
	distanceRGBA_vert: vertex$d,
	distanceRGBA_frag: fragment$d,
	equirect_vert: vertex$c,
	equirect_frag: fragment$c,
	linedashed_vert: vertex$b,
	linedashed_frag: fragment$b,
	meshbasic_vert: vertex$a,
	meshbasic_frag: fragment$a,
	meshlambert_vert: vertex$9,
	meshlambert_frag: fragment$9,
	meshmatcap_vert: vertex$8,
	meshmatcap_frag: fragment$8,
	meshnormal_vert: vertex$7,
	meshnormal_frag: fragment$7,
	meshphong_vert: vertex$6,
	meshphong_frag: fragment$6,
	meshphysical_vert: vertex$5,
	meshphysical_frag: fragment$5,
	meshtoon_vert: vertex$4,
	meshtoon_frag: fragment$4,
	points_vert: vertex$3,
	points_frag: fragment$3,
	shadow_vert: vertex$2,
	shadow_frag: fragment$2,
	sprite_vert: vertex$1,
	sprite_frag: fragment$1
};

/**
 * Uniforms library for shared webgl shaders
 */

const UniformsLib = {

	common: {

		diffuse: { value: /*@__PURE__*/ new Color( 0xffffff ) },
		opacity: { value: 1.0 },

		map: { value: null },
		mapTransform: { value: /*@__PURE__*/ new Matrix3() },

		alphaMap: { value: null },
		alphaMapTransform: { value: /*@__PURE__*/ new Matrix3() },

		alphaTest: { value: 0 }

	},

	specularmap: {

		specularMap: { value: null },
		specularMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	envmap: {

		envMap: { value: null },
		envMapRotation: { value: /*@__PURE__*/ new Matrix3() },
		flipEnvMap: { value: - 1 },
		reflectivity: { value: 1.0 }, // basic, lambert, phong
		ior: { value: 1.5 }, // physical
		refractionRatio: { value: 0.98 }, // basic, lambert, phong

	},

	aomap: {

		aoMap: { value: null },
		aoMapIntensity: { value: 1 },
		aoMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	lightmap: {

		lightMap: { value: null },
		lightMapIntensity: { value: 1 },
		lightMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	bumpmap: {

		bumpMap: { value: null },
		bumpMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		bumpScale: { value: 1 }

	},

	normalmap: {

		normalMap: { value: null },
		normalMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		normalScale: { value: /*@__PURE__*/ new Vector2( 1, 1 ) }

	},

	displacementmap: {

		displacementMap: { value: null },
		displacementMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		displacementScale: { value: 1 },
		displacementBias: { value: 0 }

	},

	emissivemap: {

		emissiveMap: { value: null },
		emissiveMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	metalnessmap: {

		metalnessMap: { value: null },
		metalnessMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	roughnessmap: {

		roughnessMap: { value: null },
		roughnessMapTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	gradientmap: {

		gradientMap: { value: null }

	},

	fog: {

		fogDensity: { value: 0.00025 },
		fogNear: { value: 1 },
		fogFar: { value: 2000 },
		fogColor: { value: /*@__PURE__*/ new Color( 0xffffff ) }

	},

	lights: {

		ambientLightColor: { value: [] },

		lightProbe: { value: [] },

		directionalLights: { value: [], properties: {
			direction: {},
			color: {}
		} },

		directionalLightShadows: { value: [], properties: {
			shadowIntensity: 1,
			shadowBias: {},
			shadowNormalBias: {},
			shadowRadius: {},
			shadowMapSize: {}
		} },

		directionalShadowMap: { value: [] },
		directionalShadowMatrix: { value: [] },

		spotLights: { value: [], properties: {
			color: {},
			position: {},
			direction: {},
			distance: {},
			coneCos: {},
			penumbraCos: {},
			decay: {}
		} },

		spotLightShadows: { value: [], properties: {
			shadowIntensity: 1,
			shadowBias: {},
			shadowNormalBias: {},
			shadowRadius: {},
			shadowMapSize: {}
		} },

		spotLightMap: { value: [] },
		spotShadowMap: { value: [] },
		spotLightMatrix: { value: [] },

		pointLights: { value: [], properties: {
			color: {},
			position: {},
			decay: {},
			distance: {}
		} },

		pointLightShadows: { value: [], properties: {
			shadowIntensity: 1,
			shadowBias: {},
			shadowNormalBias: {},
			shadowRadius: {},
			shadowMapSize: {},
			shadowCameraNear: {},
			shadowCameraFar: {}
		} },

		pointShadowMap: { value: [] },
		pointShadowMatrix: { value: [] },

		hemisphereLights: { value: [], properties: {
			direction: {},
			skyColor: {},
			groundColor: {}
		} },

		// TODO (abelnation): RectAreaLight BRDF data needs to be moved from example to main src
		rectAreaLights: { value: [], properties: {
			color: {},
			position: {},
			width: {},
			height: {}
		} },

		ltc_1: { value: null },
		ltc_2: { value: null }

	},

	points: {

		diffuse: { value: /*@__PURE__*/ new Color( 0xffffff ) },
		opacity: { value: 1.0 },
		size: { value: 1.0 },
		scale: { value: 1.0 },
		map: { value: null },
		alphaMap: { value: null },
		alphaMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		alphaTest: { value: 0 },
		uvTransform: { value: /*@__PURE__*/ new Matrix3() }

	},

	sprite: {

		diffuse: { value: /*@__PURE__*/ new Color( 0xffffff ) },
		opacity: { value: 1.0 },
		center: { value: /*@__PURE__*/ new Vector2( 0.5, 0.5 ) },
		rotation: { value: 0.0 },
		map: { value: null },
		mapTransform: { value: /*@__PURE__*/ new Matrix3() },
		alphaMap: { value: null },
		alphaMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		alphaTest: { value: 0 }

	}

};

const ShaderLib = {

	basic: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.specularmap,
			UniformsLib.envmap,
			UniformsLib.aomap,
			UniformsLib.lightmap,
			UniformsLib.fog
		] ),

		vertexShader: ShaderChunk.meshbasic_vert,
		fragmentShader: ShaderChunk.meshbasic_frag

	},

	lambert: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.specularmap,
			UniformsLib.envmap,
			UniformsLib.aomap,
			UniformsLib.lightmap,
			UniformsLib.emissivemap,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			UniformsLib.fog,
			UniformsLib.lights,
			{
				emissive: { value: /*@__PURE__*/ new Color( 0x000000 ) }
			}
		] ),

		vertexShader: ShaderChunk.meshlambert_vert,
		fragmentShader: ShaderChunk.meshlambert_frag

	},

	phong: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.specularmap,
			UniformsLib.envmap,
			UniformsLib.aomap,
			UniformsLib.lightmap,
			UniformsLib.emissivemap,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			UniformsLib.fog,
			UniformsLib.lights,
			{
				emissive: { value: /*@__PURE__*/ new Color( 0x000000 ) },
				specular: { value: /*@__PURE__*/ new Color( 0x111111 ) },
				shininess: { value: 30 }
			}
		] ),

		vertexShader: ShaderChunk.meshphong_vert,
		fragmentShader: ShaderChunk.meshphong_frag

	},

	standard: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.envmap,
			UniformsLib.aomap,
			UniformsLib.lightmap,
			UniformsLib.emissivemap,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			UniformsLib.roughnessmap,
			UniformsLib.metalnessmap,
			UniformsLib.fog,
			UniformsLib.lights,
			{
				emissive: { value: /*@__PURE__*/ new Color( 0x000000 ) },
				roughness: { value: 1.0 },
				metalness: { value: 0.0 },
				envMapIntensity: { value: 1 }
			}
		] ),

		vertexShader: ShaderChunk.meshphysical_vert,
		fragmentShader: ShaderChunk.meshphysical_frag

	},

	toon: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.aomap,
			UniformsLib.lightmap,
			UniformsLib.emissivemap,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			UniformsLib.gradientmap,
			UniformsLib.fog,
			UniformsLib.lights,
			{
				emissive: { value: /*@__PURE__*/ new Color( 0x000000 ) }
			}
		] ),

		vertexShader: ShaderChunk.meshtoon_vert,
		fragmentShader: ShaderChunk.meshtoon_frag

	},

	matcap: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			UniformsLib.fog,
			{
				matcap: { value: null }
			}
		] ),

		vertexShader: ShaderChunk.meshmatcap_vert,
		fragmentShader: ShaderChunk.meshmatcap_frag

	},

	points: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.points,
			UniformsLib.fog
		] ),

		vertexShader: ShaderChunk.points_vert,
		fragmentShader: ShaderChunk.points_frag

	},

	dashed: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.fog,
			{
				scale: { value: 1 },
				dashSize: { value: 1 },
				totalSize: { value: 2 }
			}
		] ),

		vertexShader: ShaderChunk.linedashed_vert,
		fragmentShader: ShaderChunk.linedashed_frag

	},

	depth: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.displacementmap
		] ),

		vertexShader: ShaderChunk.depth_vert,
		fragmentShader: ShaderChunk.depth_frag

	},

	normal: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.bumpmap,
			UniformsLib.normalmap,
			UniformsLib.displacementmap,
			{
				opacity: { value: 1.0 }
			}
		] ),

		vertexShader: ShaderChunk.meshnormal_vert,
		fragmentShader: ShaderChunk.meshnormal_frag

	},

	sprite: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.sprite,
			UniformsLib.fog
		] ),

		vertexShader: ShaderChunk.sprite_vert,
		fragmentShader: ShaderChunk.sprite_frag

	},

	background: {

		uniforms: {
			uvTransform: { value: /*@__PURE__*/ new Matrix3() },
			t2D: { value: null },
			backgroundIntensity: { value: 1 }
		},

		vertexShader: ShaderChunk.background_vert,
		fragmentShader: ShaderChunk.background_frag

	},

	backgroundCube: {

		uniforms: {
			envMap: { value: null },
			flipEnvMap: { value: - 1 },
			backgroundBlurriness: { value: 0 },
			backgroundIntensity: { value: 1 },
			backgroundRotation: { value: /*@__PURE__*/ new Matrix3() }
		},

		vertexShader: ShaderChunk.backgroundCube_vert,
		fragmentShader: ShaderChunk.backgroundCube_frag

	},

	cube: {

		uniforms: {
			tCube: { value: null },
			tFlip: { value: - 1 },
			opacity: { value: 1.0 }
		},

		vertexShader: ShaderChunk.cube_vert,
		fragmentShader: ShaderChunk.cube_frag

	},

	equirect: {

		uniforms: {
			tEquirect: { value: null },
		},

		vertexShader: ShaderChunk.equirect_vert,
		fragmentShader: ShaderChunk.equirect_frag

	},

	distanceRGBA: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.common,
			UniformsLib.displacementmap,
			{
				referencePosition: { value: /*@__PURE__*/ new Vector3() },
				nearDistance: { value: 1 },
				farDistance: { value: 1000 }
			}
		] ),

		vertexShader: ShaderChunk.distanceRGBA_vert,
		fragmentShader: ShaderChunk.distanceRGBA_frag

	},

	shadow: {

		uniforms: /*@__PURE__*/ mergeUniforms( [
			UniformsLib.lights,
			UniformsLib.fog,
			{
				color: { value: /*@__PURE__*/ new Color( 0x00000 ) },
				opacity: { value: 1.0 }
			},
		] ),

		vertexShader: ShaderChunk.shadow_vert,
		fragmentShader: ShaderChunk.shadow_frag

	}

};

ShaderLib.physical = {

	uniforms: /*@__PURE__*/ mergeUniforms( [
		ShaderLib.standard.uniforms,
		{
			clearcoat: { value: 0 },
			clearcoatMap: { value: null },
			clearcoatMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			clearcoatNormalMap: { value: null },
			clearcoatNormalMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			clearcoatNormalScale: { value: /*@__PURE__*/ new Vector2( 1, 1 ) },
			clearcoatRoughness: { value: 0 },
			clearcoatRoughnessMap: { value: null },
			clearcoatRoughnessMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			dispersion: { value: 0 },
			iridescence: { value: 0 },
			iridescenceMap: { value: null },
			iridescenceMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			iridescenceIOR: { value: 1.3 },
			iridescenceThicknessMinimum: { value: 100 },
			iridescenceThicknessMaximum: { value: 400 },
			iridescenceThicknessMap: { value: null },
			iridescenceThicknessMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			sheen: { value: 0 },
			sheenColor: { value: /*@__PURE__*/ new Color( 0x000000 ) },
			sheenColorMap: { value: null },
			sheenColorMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			sheenRoughness: { value: 1 },
			sheenRoughnessMap: { value: null },
			sheenRoughnessMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			transmission: { value: 0 },
			transmissionMap: { value: null },
			transmissionMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			transmissionSamplerSize: { value: /*@__PURE__*/ new Vector2() },
			transmissionSamplerMap: { value: null },
			thickness: { value: 0 },
			thicknessMap: { value: null },
			thicknessMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			attenuationDistance: { value: 0 },
			attenuationColor: { value: /*@__PURE__*/ new Color( 0x000000 ) },
			specularColor: { value: /*@__PURE__*/ new Color( 1, 1, 1 ) },
			specularColorMap: { value: null },
			specularColorMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			specularIntensity: { value: 1 },
			specularIntensityMap: { value: null },
			specularIntensityMapTransform: { value: /*@__PURE__*/ new Matrix3() },
			anisotropyVector: { value: /*@__PURE__*/ new Vector2() },
			anisotropyMap: { value: null },
			anisotropyMapTransform: { value: /*@__PURE__*/ new Matrix3() },
		}
	] ),

	vertexShader: ShaderChunk.meshphysical_vert,
	fragmentShader: ShaderChunk.meshphysical_frag

};

const _rgb = { r: 0, b: 0, g: 0 };
const _e1$1 = /*@__PURE__*/ new Euler();
const _m1$1 = /*@__PURE__*/ new Matrix4();

function WebGLBackground( renderer, cubemaps, cubeuvmaps, state, objects, alpha, premultipliedAlpha ) {

	const clearColor = new Color( 0x000000 );
	let clearAlpha = alpha === true ? 0 : 1;

	let planeMesh;
	let boxMesh;

	let currentBackground = null;
	let currentBackgroundVersion = 0;
	let currentTonemapping = null;

	function getBackground( scene ) {

		let background = scene.isScene === true ? scene.background : null;

		if ( background && background.isTexture ) {

			const usePMREM = scene.backgroundBlurriness > 0; // use PMREM if the user wants to blur the background
			background = ( usePMREM ? cubeuvmaps : cubemaps ).get( background );

		}

		return background;

	}

	function render( scene ) {

		let forceClear = false;
		const background = getBackground( scene );

		if ( background === null ) {

			setClear( clearColor, clearAlpha );

		} else if ( background && background.isColor ) {

			setClear( background, 1 );
			forceClear = true;

		}

		const environmentBlendMode = renderer.xr.getEnvironmentBlendMode();

		if ( environmentBlendMode === 'additive' ) {

			state.buffers.color.setClear( 0, 0, 0, 1, premultipliedAlpha );

		} else if ( environmentBlendMode === 'alpha-blend' ) {

			state.buffers.color.setClear( 0, 0, 0, 0, premultipliedAlpha );

		}

		if ( renderer.autoClear || forceClear ) {

			// buffers might not be writable which is required to ensure a correct clear

			state.buffers.depth.setTest( true );
			state.buffers.depth.setMask( true );
			state.buffers.color.setMask( true );

			renderer.clear( renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil );

		}

	}

	function addToRenderList( renderList, scene ) {

		const background = getBackground( scene );

		if ( background && ( background.isCubeTexture || background.mapping === CubeUVReflectionMapping ) ) {

			if ( boxMesh === undefined ) {

				boxMesh = new Mesh(
					new BoxGeometry( 1, 1, 1 ),
					new ShaderMaterial( {
						name: 'BackgroundCubeMaterial',
						uniforms: cloneUniforms( ShaderLib.backgroundCube.uniforms ),
						vertexShader: ShaderLib.backgroundCube.vertexShader,
						fragmentShader: ShaderLib.backgroundCube.fragmentShader,
						side: BackSide,
						depthTest: false,
						depthWrite: false,
						fog: false
					} )
				);

				boxMesh.geometry.deleteAttribute( 'normal' );
				boxMesh.geometry.deleteAttribute( 'uv' );

				boxMesh.onBeforeRender = function ( renderer, scene, camera ) {

					this.matrixWorld.copyPosition( camera.matrixWorld );

				};

				// add "envMap" material property so the renderer can evaluate it like for built-in materials
				Object.defineProperty( boxMesh.material, 'envMap', {

					get: function () {

						return this.uniforms.envMap.value;

					}

				} );

				objects.update( boxMesh );

			}

			_e1$1.copy( scene.backgroundRotation );

			// accommodate left-handed frame
			_e1$1.x *= - 1; _e1$1.y *= - 1; _e1$1.z *= - 1;

			if ( background.isCubeTexture && background.isRenderTargetTexture === false ) {

				// environment maps which are not cube render targets or PMREMs follow a different convention
				_e1$1.y *= - 1;
				_e1$1.z *= - 1;

			}

			boxMesh.material.uniforms.envMap.value = background;
			boxMesh.material.uniforms.flipEnvMap.value = ( background.isCubeTexture && background.isRenderTargetTexture === false ) ? - 1 : 1;
			boxMesh.material.uniforms.backgroundBlurriness.value = scene.backgroundBlurriness;
			boxMesh.material.uniforms.backgroundIntensity.value = scene.backgroundIntensity;
			boxMesh.material.uniforms.backgroundRotation.value.setFromMatrix4( _m1$1.makeRotationFromEuler( _e1$1 ) );
			boxMesh.material.toneMapped = ColorManagement.getTransfer( background.colorSpace ) !== SRGBTransfer;

			if ( currentBackground !== background ||
				currentBackgroundVersion !== background.version ||
				currentTonemapping !== renderer.toneMapping ) {

				boxMesh.material.needsUpdate = true;

				currentBackground = background;
				currentBackgroundVersion = background.version;
				currentTonemapping = renderer.toneMapping;

			}

			boxMesh.layers.enableAll();

			// push to the pre-sorted opaque render list
			renderList.unshift( boxMesh, boxMesh.geometry, boxMesh.material, 0, 0, null );

		} else if ( background && background.isTexture ) {

			if ( planeMesh === undefined ) {

				planeMesh = new Mesh(
					new PlaneGeometry( 2, 2 ),
					new ShaderMaterial( {
						name: 'BackgroundMaterial',
						uniforms: cloneUniforms( ShaderLib.background.uniforms ),
						vertexShader: ShaderLib.background.vertexShader,
						fragmentShader: ShaderLib.background.fragmentShader,
						side: FrontSide,
						depthTest: false,
						depthWrite: false,
						fog: false
					} )
				);

				planeMesh.geometry.deleteAttribute( 'normal' );

				// add "map" material property so the renderer can evaluate it like for built-in materials
				Object.defineProperty( planeMesh.material, 'map', {

					get: function () {

						return this.uniforms.t2D.value;

					}

				} );

				objects.update( planeMesh );

			}

			planeMesh.material.uniforms.t2D.value = background;
			planeMesh.material.uniforms.backgroundIntensity.value = scene.backgroundIntensity;
			planeMesh.material.toneMapped = ColorManagement.getTransfer( background.colorSpace ) !== SRGBTransfer;

			if ( background.matrixAutoUpdate === true ) {

				background.updateMatrix();

			}

			planeMesh.material.uniforms.uvTransform.value.copy( background.matrix );

			if ( currentBackground !== background ||
				currentBackgroundVersion !== background.version ||
				currentTonemapping !== renderer.toneMapping ) {

				planeMesh.material.needsUpdate = true;

				currentBackground = background;
				currentBackgroundVersion = background.version;
				currentTonemapping = renderer.toneMapping;

			}

			planeMesh.layers.enableAll();

			// push to the pre-sorted opaque render list
			renderList.unshift( planeMesh, planeMesh.geometry, planeMesh.material, 0, 0, null );

		}

	}

	function setClear( color, alpha ) {

		color.getRGB( _rgb, getUnlitUniformColorSpace( renderer ) );

		state.buffers.color.setClear( _rgb.r, _rgb.g, _rgb.b, alpha, premultipliedAlpha );

	}

	return {

		getClearColor: function () {

			return clearColor;

		},
		setClearColor: function ( color, alpha = 1 ) {

			clearColor.set( color );
			clearAlpha = alpha;
			setClear( clearColor, clearAlpha );

		},
		getClearAlpha: function () {

			return clearAlpha;

		},
		setClearAlpha: function ( alpha ) {

			clearAlpha = alpha;
			setClear( clearColor, clearAlpha );

		},
		render: render,
		addToRenderList: addToRenderList

	};

}

function WebGLBindingStates( gl, attributes ) {

	const maxVertexAttributes = gl.getParameter( gl.MAX_VERTEX_ATTRIBS );

	const bindingStates = {};

	const defaultState = createBindingState( null );
	let currentState = defaultState;
	let forceUpdate = false;

	function setup( object, material, program, geometry, index ) {

		let updateBuffers = false;

		const state = getBindingState( geometry, program, material );

		if ( currentState !== state ) {

			currentState = state;
			bindVertexArrayObject( currentState.object );

		}

		updateBuffers = needsUpdate( object, geometry, program, index );

		if ( updateBuffers ) saveCache( object, geometry, program, index );

		if ( index !== null ) {

			attributes.update( index, gl.ELEMENT_ARRAY_BUFFER );

		}

		if ( updateBuffers || forceUpdate ) {

			forceUpdate = false;

			setupVertexAttributes( object, material, program, geometry );

			if ( index !== null ) {

				gl.bindBuffer( gl.ELEMENT_ARRAY_BUFFER, attributes.get( index ).buffer );

			}

		}

	}

	function createVertexArrayObject() {

		return gl.createVertexArray();

	}

	function bindVertexArrayObject( vao ) {

		return gl.bindVertexArray( vao );

	}

	function deleteVertexArrayObject( vao ) {

		return gl.deleteVertexArray( vao );

	}

	function getBindingState( geometry, program, material ) {

		const wireframe = ( material.wireframe === true );

		let programMap = bindingStates[ geometry.id ];

		if ( programMap === undefined ) {

			programMap = {};
			bindingStates[ geometry.id ] = programMap;

		}

		let stateMap = programMap[ program.id ];

		if ( stateMap === undefined ) {

			stateMap = {};
			programMap[ program.id ] = stateMap;

		}

		let state = stateMap[ wireframe ];

		if ( state === undefined ) {

			state = createBindingState( createVertexArrayObject() );
			stateMap[ wireframe ] = state;

		}

		return state;

	}

	function createBindingState( vao ) {

		const newAttributes = [];
		const enabledAttributes = [];
		const attributeDivisors = [];

		for ( let i = 0; i < maxVertexAttributes; i ++ ) {

			newAttributes[ i ] = 0;
			enabledAttributes[ i ] = 0;
			attributeDivisors[ i ] = 0;

		}

		return {

			// for backward compatibility on non-VAO support browser
			geometry: null,
			program: null,
			wireframe: false,

			newAttributes: newAttributes,
			enabledAttributes: enabledAttributes,
			attributeDivisors: attributeDivisors,
			object: vao,
			attributes: {},
			index: null

		};

	}

	function needsUpdate( object, geometry, program, index ) {

		const cachedAttributes = currentState.attributes;
		const geometryAttributes = geometry.attributes;

		let attributesNum = 0;

		const programAttributes = program.getAttributes();

		for ( const name in programAttributes ) {

			const programAttribute = programAttributes[ name ];

			if ( programAttribute.location >= 0 ) {

				const cachedAttribute = cachedAttributes[ name ];
				let geometryAttribute = geometryAttributes[ name ];

				if ( geometryAttribute === undefined ) {

					if ( name === 'instanceMatrix' && object.instanceMatrix ) geometryAttribute = object.instanceMatrix;
					if ( name === 'instanceColor' && object.instanceColor ) geometryAttribute = object.instanceColor;

				}

				if ( cachedAttribute === undefined ) return true;

				if ( cachedAttribute.attribute !== geometryAttribute ) return true;

				if ( geometryAttribute && cachedAttribute.data !== geometryAttribute.data ) return true;

				attributesNum ++;

			}

		}

		if ( currentState.attributesNum !== attributesNum ) return true;

		if ( currentState.index !== index ) return true;

		return false;

	}

	function saveCache( object, geometry, program, index ) {

		const cache = {};
		const attributes = geometry.attributes;
		let attributesNum = 0;

		const programAttributes = program.getAttributes();

		for ( const name in programAttributes ) {

			const programAttribute = programAttributes[ name ];

			if ( programAttribute.location >= 0 ) {

				let attribute = attributes[ name ];

				if ( attribute === undefined ) {

					if ( name === 'instanceMatrix' && object.instanceMatrix ) attribute = object.instanceMatrix;
					if ( name === 'instanceColor' && object.instanceColor ) attribute = object.instanceColor;

				}

				const data = {};
				data.attribute = attribute;

				if ( attribute && attribute.data ) {

					data.data = attribute.data;

				}

				cache[ name ] = data;

				attributesNum ++;

			}

		}

		currentState.attributes = cache;
		currentState.attributesNum = attributesNum;

		currentState.index = index;

	}

	function initAttributes() {

		const newAttributes = currentState.newAttributes;

		for ( let i = 0, il = newAttributes.length; i < il; i ++ ) {

			newAttributes[ i ] = 0;

		}

	}

	function enableAttribute( attribute ) {

		enableAttributeAndDivisor( attribute, 0 );

	}

	function enableAttributeAndDivisor( attribute, meshPerAttribute ) {

		const newAttributes = currentState.newAttributes;
		const enabledAttributes = currentState.enabledAttributes;
		const attributeDivisors = currentState.attributeDivisors;

		newAttributes[ attribute ] = 1;

		if ( enabledAttributes[ attribute ] === 0 ) {

			gl.enableVertexAttribArray( attribute );
			enabledAttributes[ attribute ] = 1;

		}

		if ( attributeDivisors[ attribute ] !== meshPerAttribute ) {

			gl.vertexAttribDivisor( attribute, meshPerAttribute );
			attributeDivisors[ attribute ] = meshPerAttribute;

		}

	}

	function disableUnusedAttributes() {

		const newAttributes = currentState.newAttributes;
		const enabledAttributes = currentState.enabledAttributes;

		for ( let i = 0, il = enabledAttributes.length; i < il; i ++ ) {

			if ( enabledAttributes[ i ] !== newAttributes[ i ] ) {

				gl.disableVertexAttribArray( i );
				enabledAttributes[ i ] = 0;

			}

		}

	}

	function vertexAttribPointer( index, size, type, normalized, stride, offset, integer ) {

		if ( integer === true ) {

			gl.vertexAttribIPointer( index, size, type, stride, offset );

		} else {

			gl.vertexAttribPointer( index, size, type, normalized, stride, offset );

		}

	}

	function setupVertexAttributes( object, material, program, geometry ) {

		initAttributes();

		const geometryAttributes = geometry.attributes;

		const programAttributes = program.getAttributes();

		const materialDefaultAttributeValues = material.defaultAttributeValues;

		for ( const name in programAttributes ) {

			const programAttribute = programAttributes[ name ];

			if ( programAttribute.location >= 0 ) {

				let geometryAttribute = geometryAttributes[ name ];

				if ( geometryAttribute === undefined ) {

					if ( name === 'instanceMatrix' && object.instanceMatrix ) geometryAttribute = object.instanceMatrix;
					if ( name === 'instanceColor' && object.instanceColor ) geometryAttribute = object.instanceColor;

				}

				if ( geometryAttribute !== undefined ) {

					const normalized = geometryAttribute.normalized;
					const size = geometryAttribute.itemSize;

					const attribute = attributes.get( geometryAttribute );

					// TODO Attribute may not be available on context restore

					if ( attribute === undefined ) continue;

					const buffer = attribute.buffer;
					const type = attribute.type;
					const bytesPerElement = attribute.bytesPerElement;

					// check for integer attributes

					const integer = ( type === gl.INT || type === gl.UNSIGNED_INT || geometryAttribute.gpuType === IntType );

					if ( geometryAttribute.isInterleavedBufferAttribute ) {

						const data = geometryAttribute.data;
						const stride = data.stride;
						const offset = geometryAttribute.offset;

						if ( data.isInstancedInterleavedBuffer ) {

							for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

								enableAttributeAndDivisor( programAttribute.location + i, data.meshPerAttribute );

							}

							if ( object.isInstancedMesh !== true && geometry._maxInstanceCount === undefined ) {

								geometry._maxInstanceCount = data.meshPerAttribute * data.count;

							}

						} else {

							for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

								enableAttribute( programAttribute.location + i );

							}

						}

						gl.bindBuffer( gl.ARRAY_BUFFER, buffer );

						for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

							vertexAttribPointer(
								programAttribute.location + i,
								size / programAttribute.locationSize,
								type,
								normalized,
								stride * bytesPerElement,
								( offset + ( size / programAttribute.locationSize ) * i ) * bytesPerElement,
								integer
							);

						}

					} else {

						if ( geometryAttribute.isInstancedBufferAttribute ) {

							for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

								enableAttributeAndDivisor( programAttribute.location + i, geometryAttribute.meshPerAttribute );

							}

							if ( object.isInstancedMesh !== true && geometry._maxInstanceCount === undefined ) {

								geometry._maxInstanceCount = geometryAttribute.meshPerAttribute * geometryAttribute.count;

							}

						} else {

							for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

								enableAttribute( programAttribute.location + i );

							}

						}

						gl.bindBuffer( gl.ARRAY_BUFFER, buffer );

						for ( let i = 0; i < programAttribute.locationSize; i ++ ) {

							vertexAttribPointer(
								programAttribute.location + i,
								size / programAttribute.locationSize,
								type,
								normalized,
								size * bytesPerElement,
								( size / programAttribute.locationSize ) * i * bytesPerElement,
								integer
							);

						}

					}

				} else if ( materialDefaultAttributeValues !== undefined ) {

					const value = materialDefaultAttributeValues[ name ];

					if ( value !== undefined ) {

						switch ( value.length ) {

							case 2:
								gl.vertexAttrib2fv( programAttribute.location, value );
								break;

							case 3:
								gl.vertexAttrib3fv( programAttribute.location, value );
								break;

							case 4:
								gl.vertexAttrib4fv( programAttribute.location, value );
								break;

							default:
								gl.vertexAttrib1fv( programAttribute.location, value );

						}

					}

				}

			}

		}

		disableUnusedAttributes();

	}

	function dispose() {

		reset();

		for ( const geometryId in bindingStates ) {

			const programMap = bindingStates[ geometryId ];

			for ( const programId in programMap ) {

				const stateMap = programMap[ programId ];

				for ( const wireframe in stateMap ) {

					deleteVertexArrayObject( stateMap[ wireframe ].object );

					delete stateMap[ wireframe ];

				}

				delete programMap[ programId ];

			}

			delete bindingStates[ geometryId ];

		}

	}

	function releaseStatesOfGeometry( geometry ) {

		if ( bindingStates[ geometry.id ] === undefined ) return;

		const programMap = bindingStates[ geometry.id ];

		for ( const programId in programMap ) {

			const stateMap = programMap[ programId ];

			for ( const wireframe in stateMap ) {

				deleteVertexArrayObject( stateMap[ wireframe ].object );

				delete stateMap[ wireframe ];

			}

			delete programMap[ programId ];

		}

		delete bindingStates[ geometry.id ];

	}

	function releaseStatesOfProgram( program ) {

		for ( const geometryId in bindingStates ) {

			const programMap = bindingStates[ geometryId ];

			if ( programMap[ program.id ] === undefined ) continue;

			const stateMap = programMap[ program.id ];

			for ( const wireframe in stateMap ) {

				deleteVertexArrayObject( stateMap[ wireframe ].object );

				delete stateMap[ wireframe ];

			}

			delete programMap[ program.id ];

		}

	}

	function reset() {

		resetDefaultState();
		forceUpdate = true;

		if ( currentState === defaultState ) return;

		currentState = defaultState;
		bindVertexArrayObject( currentState.object );

	}

	// for backward-compatibility

	function resetDefaultState() {

		defaultState.geometry = null;
		defaultState.program = null;
		defaultState.wireframe = false;

	}

	return {

		setup: setup,
		reset: reset,
		resetDefaultState: resetDefaultState,
		dispose: dispose,
		releaseStatesOfGeometry: releaseStatesOfGeometry,
		releaseStatesOfProgram: releaseStatesOfProgram,

		initAttributes: initAttributes,
		enableAttribute: enableAttribute,
		disableUnusedAttributes: disableUnusedAttributes

	};

}

function WebGLBufferRenderer( gl, extensions, info ) {

	let mode;

	function setMode( value ) {

		mode = value;

	}

	function render( start, count ) {

		gl.drawArrays( mode, start, count );

		info.update( count, mode, 1 );

	}

	function renderInstances( start, count, primcount ) {

		if ( primcount === 0 ) return;

		gl.drawArraysInstanced( mode, start, count, primcount );

		info.update( count, mode, primcount );

	}

	function renderMultiDraw( starts, counts, drawCount ) {

		if ( drawCount === 0 ) return;

		const extension = extensions.get( 'WEBGL_multi_draw' );
		extension.multiDrawArraysWEBGL( mode, starts, 0, counts, 0, drawCount );

		let elementCount = 0;
		for ( let i = 0; i < drawCount; i ++ ) {

			elementCount += counts[ i ];

		}

		info.update( elementCount, mode, 1 );

	}

	function renderMultiDrawInstances( starts, counts, drawCount, primcount ) {

		if ( drawCount === 0 ) return;

		const extension = extensions.get( 'WEBGL_multi_draw' );

		if ( extension === null ) {

			for ( let i = 0; i < starts.length; i ++ ) {

				renderInstances( starts[ i ], counts[ i ], primcount[ i ] );

			}

		} else {

			extension.multiDrawArraysInstancedWEBGL( mode, starts, 0, counts, 0, primcount, 0, drawCount );

			let elementCount = 0;
			for ( let i = 0; i < drawCount; i ++ ) {

				elementCount += counts[ i ];

			}

			for ( let i = 0; i < primcount.length; i ++ ) {

				info.update( elementCount, mode, primcount[ i ] );

			}

		}

	}

	//

	this.setMode = setMode;
	this.render = render;
	this.renderInstances = renderInstances;
	this.renderMultiDraw = renderMultiDraw;
	this.renderMultiDrawInstances = renderMultiDrawInstances;

}

function WebGLCapabilities( gl, extensions, parameters, utils ) {

	let maxAnisotropy;

	function getMaxAnisotropy() {

		if ( maxAnisotropy !== undefined ) return maxAnisotropy;

		if ( extensions.has( 'EXT_texture_filter_anisotropic' ) === true ) {

			const extension = extensions.get( 'EXT_texture_filter_anisotropic' );

			maxAnisotropy = gl.getParameter( extension.MAX_TEXTURE_MAX_ANISOTROPY_EXT );

		} else {

			maxAnisotropy = 0;

		}

		return maxAnisotropy;

	}

	function textureFormatReadable( textureFormat ) {

		if ( textureFormat !== RGBAFormat && utils.convert( textureFormat ) !== gl.getParameter( gl.IMPLEMENTATION_COLOR_READ_FORMAT ) ) {

			return false;

		}

		return true;

	}

	function textureTypeReadable( textureType ) {

		const halfFloatSupportedByExt = ( textureType === HalfFloatType ) && ( extensions.has( 'EXT_color_buffer_half_float' ) || extensions.has( 'EXT_color_buffer_float' ) );

		if ( textureType !== UnsignedByteType && utils.convert( textureType ) !== gl.getParameter( gl.IMPLEMENTATION_COLOR_READ_TYPE ) && // Edge and Chrome Mac < 52 (#9513)
			textureType !== FloatType && ! halfFloatSupportedByExt ) {

			return false;

		}

		return true;

	}

	function getMaxPrecision( precision ) {

		if ( precision === 'highp' ) {

			if ( gl.getShaderPrecisionFormat( gl.VERTEX_SHADER, gl.HIGH_FLOAT ).precision > 0 &&
				gl.getShaderPrecisionFormat( gl.FRAGMENT_SHADER, gl.HIGH_FLOAT ).precision > 0 ) {

				return 'highp';

			}

			precision = 'mediump';

		}

		if ( precision === 'mediump' ) {

			if ( gl.getShaderPrecisionFormat( gl.VERTEX_SHADER, gl.MEDIUM_FLOAT ).precision > 0 &&
				gl.getShaderPrecisionFormat( gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT ).precision > 0 ) {

				return 'mediump';

			}

		}

		return 'lowp';

	}

	let precision = parameters.precision !== undefined ? parameters.precision : 'highp';
	const maxPrecision = getMaxPrecision( precision );

	if ( maxPrecision !== precision ) {

		console.warn( 'THREE.WebGLRenderer:', precision, 'not supported, using', maxPrecision, 'instead.' );
		precision = maxPrecision;

	}

	const logarithmicDepthBuffer = parameters.logarithmicDepthBuffer === true;
	const reverseDepthBuffer = parameters.reverseDepthBuffer === true && extensions.has( 'EXT_clip_control' );

	if ( reverseDepthBuffer === true ) {

		const ext = extensions.get( 'EXT_clip_control' );
		ext.clipControlEXT( ext.LOWER_LEFT_EXT, ext.ZERO_TO_ONE_EXT );

	}

	const maxTextures = gl.getParameter( gl.MAX_TEXTURE_IMAGE_UNITS );
	const maxVertexTextures = gl.getParameter( gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS );
	const maxTextureSize = gl.getParameter( gl.MAX_TEXTURE_SIZE );
	const maxCubemapSize = gl.getParameter( gl.MAX_CUBE_MAP_TEXTURE_SIZE );

	const maxAttributes = gl.getParameter( gl.MAX_VERTEX_ATTRIBS );
	const maxVertexUniforms = gl.getParameter( gl.MAX_VERTEX_UNIFORM_VECTORS );
	const maxVaryings = gl.getParameter( gl.MAX_VARYING_VECTORS );
	const maxFragmentUniforms = gl.getParameter( gl.MAX_FRAGMENT_UNIFORM_VECTORS );

	const vertexTextures = maxVertexTextures > 0;

	const maxSamples = gl.getParameter( gl.MAX_SAMPLES );

	return {

		isWebGL2: true, // keeping this for backwards compatibility

		getMaxAnisotropy: getMaxAnisotropy,
		getMaxPrecision: getMaxPrecision,

		textureFormatReadable: textureFormatReadable,
		textureTypeReadable: textureTypeReadable,

		precision: precision,
		logarithmicDepthBuffer: logarithmicDepthBuffer,
		reverseDepthBuffer: reverseDepthBuffer,

		maxTextures: maxTextures,
		maxVertexTextures: maxVertexTextures,
		maxTextureSize: maxTextureSize,
		maxCubemapSize: maxCubemapSize,

		maxAttributes: maxAttributes,
		maxVertexUniforms: maxVertexUniforms,
		maxVaryings: maxVaryings,
		maxFragmentUniforms: maxFragmentUniforms,

		vertexTextures: vertexTextures,

		maxSamples: maxSamples

	};

}

function WebGLClipping( properties ) {

	const scope = this;

	let globalState = null,
		numGlobalPlanes = 0,
		localClippingEnabled = false,
		renderingShadows = false;

	const plane = new Plane(),
		viewNormalMatrix = new Matrix3(),

		uniform = { value: null, needsUpdate: false };

	this.uniform = uniform;
	this.numPlanes = 0;
	this.numIntersection = 0;

	this.init = function ( planes, enableLocalClipping ) {

		const enabled =
			planes.length !== 0 ||
			enableLocalClipping ||
			// enable state of previous frame - the clipping code has to
			// run another frame in order to reset the state:
			numGlobalPlanes !== 0 ||
			localClippingEnabled;

		localClippingEnabled = enableLocalClipping;

		numGlobalPlanes = planes.length;

		return enabled;

	};

	this.beginShadows = function () {

		renderingShadows = true;
		projectPlanes( null );

	};

	this.endShadows = function () {

		renderingShadows = false;

	};

	this.setGlobalState = function ( planes, camera ) {

		globalState = projectPlanes( planes, camera, 0 );

	};

	this.setState = function ( material, camera, useCache ) {

		const planes = material.clippingPlanes,
			clipIntersection = material.clipIntersection,
			clipShadows = material.clipShadows;

		const materialProperties = properties.get( material );

		if ( ! localClippingEnabled || planes === null || planes.length === 0 || renderingShadows && ! clipShadows ) {

			// there's no local clipping

			if ( renderingShadows ) {

				// there's no global clipping

				projectPlanes( null );

			} else {

				resetGlobalState();

			}

		} else {

			const nGlobal = renderingShadows ? 0 : numGlobalPlanes,
				lGlobal = nGlobal * 4;

			let dstArray = materialProperties.clippingState || null;

			uniform.value = dstArray; // ensure unique state

			dstArray = projectPlanes( planes, camera, lGlobal, useCache );

			for ( let i = 0; i !== lGlobal; ++ i ) {

				dstArray[ i ] = globalState[ i ];

			}

			materialProperties.clippingState = dstArray;
			this.numIntersection = clipIntersection ? this.numPlanes : 0;
			this.numPlanes += nGlobal;

		}


	};

	function resetGlobalState() {

		if ( uniform.value !== globalState ) {

			uniform.value = globalState;
			uniform.needsUpdate = numGlobalPlanes > 0;

		}

		scope.numPlanes = numGlobalPlanes;
		scope.numIntersection = 0;

	}

	function projectPlanes( planes, camera, dstOffset, skipTransform ) {

		const nPlanes = planes !== null ? planes.length : 0;
		let dstArray = null;

		if ( nPlanes !== 0 ) {

			dstArray = uniform.value;

			if ( skipTransform !== true || dstArray === null ) {

				const flatSize = dstOffset + nPlanes * 4,
					viewMatrix = camera.matrixWorldInverse;

				viewNormalMatrix.getNormalMatrix( viewMatrix );

				if ( dstArray === null || dstArray.length < flatSize ) {

					dstArray = new Float32Array( flatSize );

				}

				for ( let i = 0, i4 = dstOffset; i !== nPlanes; ++ i, i4 += 4 ) {

					plane.copy( planes[ i ] ).applyMatrix4( viewMatrix, viewNormalMatrix );

					plane.normal.toArray( dstArray, i4 );
					dstArray[ i4 + 3 ] = plane.constant;

				}

			}

			uniform.value = dstArray;
			uniform.needsUpdate = true;

		}

		scope.numPlanes = nPlanes;
		scope.numIntersection = 0;

		return dstArray;

	}

}

function WebGLCubeMaps( renderer ) {

	let cubemaps = new WeakMap();

	function mapTextureMapping( texture, mapping ) {

		if ( mapping === EquirectangularReflectionMapping ) {

			texture.mapping = CubeReflectionMapping;

		} else if ( mapping === EquirectangularRefractionMapping ) {

			texture.mapping = CubeRefractionMapping;

		}

		return texture;

	}

	function get( texture ) {

		if ( texture && texture.isTexture ) {

			const mapping = texture.mapping;

			if ( mapping === EquirectangularReflectionMapping || mapping === EquirectangularRefractionMapping ) {

				if ( cubemaps.has( texture ) ) {

					const cubemap = cubemaps.get( texture ).texture;
					return mapTextureMapping( cubemap, texture.mapping );

				} else {

					const image = texture.image;

					if ( image && image.height > 0 ) {

						const renderTarget = new WebGLCubeRenderTarget( image.height );
						renderTarget.fromEquirectangularTexture( renderer, texture );
						cubemaps.set( texture, renderTarget );

						texture.addEventListener( 'dispose', onTextureDispose );

						return mapTextureMapping( renderTarget.texture, texture.mapping );

					} else {

						// image not yet ready. try the conversion next frame

						return null;

					}

				}

			}

		}

		return texture;

	}

	function onTextureDispose( event ) {

		const texture = event.target;

		texture.removeEventListener( 'dispose', onTextureDispose );

		const cubemap = cubemaps.get( texture );

		if ( cubemap !== undefined ) {

			cubemaps.delete( texture );
			cubemap.dispose();

		}

	}

	function dispose() {

		cubemaps = new WeakMap();

	}

	return {
		get: get,
		dispose: dispose
	};

}

class OrthographicCamera extends Camera {

	constructor( left = - 1, right = 1, top = 1, bottom = - 1, near = 0.1, far = 2000 ) {

		super();

		this.isOrthographicCamera = true;

		this.type = 'OrthographicCamera';

		this.zoom = 1;
		this.view = null;

		this.left = left;
		this.right = right;
		this.top = top;
		this.bottom = bottom;

		this.near = near;
		this.far = far;

		this.updateProjectionMatrix();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.left = source.left;
		this.right = source.right;
		this.top = source.top;
		this.bottom = source.bottom;
		this.near = source.near;
		this.far = source.far;

		this.zoom = source.zoom;
		this.view = source.view === null ? null : Object.assign( {}, source.view );

		return this;

	}

	setViewOffset( fullWidth, fullHeight, x, y, width, height ) {

		if ( this.view === null ) {

			this.view = {
				enabled: true,
				fullWidth: 1,
				fullHeight: 1,
				offsetX: 0,
				offsetY: 0,
				width: 1,
				height: 1
			};

		}

		this.view.enabled = true;
		this.view.fullWidth = fullWidth;
		this.view.fullHeight = fullHeight;
		this.view.offsetX = x;
		this.view.offsetY = y;
		this.view.width = width;
		this.view.height = height;

		this.updateProjectionMatrix();

	}

	clearViewOffset() {

		if ( this.view !== null ) {

			this.view.enabled = false;

		}

		this.updateProjectionMatrix();

	}

	updateProjectionMatrix() {

		const dx = ( this.right - this.left ) / ( 2 * this.zoom );
		const dy = ( this.top - this.bottom ) / ( 2 * this.zoom );
		const cx = ( this.right + this.left ) / 2;
		const cy = ( this.top + this.bottom ) / 2;

		let left = cx - dx;
		let right = cx + dx;
		let top = cy + dy;
		let bottom = cy - dy;

		if ( this.view !== null && this.view.enabled ) {

			const scaleW = ( this.right - this.left ) / this.view.fullWidth / this.zoom;
			const scaleH = ( this.top - this.bottom ) / this.view.fullHeight / this.zoom;

			left += scaleW * this.view.offsetX;
			right = left + scaleW * this.view.width;
			top -= scaleH * this.view.offsetY;
			bottom = top - scaleH * this.view.height;

		}

		this.projectionMatrix.makeOrthographic( left, right, top, bottom, this.near, this.far, this.coordinateSystem );

		this.projectionMatrixInverse.copy( this.projectionMatrix ).invert();

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.object.zoom = this.zoom;
		data.object.left = this.left;
		data.object.right = this.right;
		data.object.top = this.top;
		data.object.bottom = this.bottom;
		data.object.near = this.near;
		data.object.far = this.far;

		if ( this.view !== null ) data.object.view = Object.assign( {}, this.view );

		return data;

	}

}

const LOD_MIN = 4;

// The standard deviations (radians) associated with the extra mips. These are
// chosen to approximate a Trowbridge-Reitz distribution function times the
// geometric shadowing function. These sigma values squared must match the
// variance #defines in cube_uv_reflection_fragment.glsl.js.
const EXTRA_LOD_SIGMA = [ 0.125, 0.215, 0.35, 0.446, 0.526, 0.582 ];

// The maximum length of the blur for loop. Smaller sigmas will use fewer
// samples and exit early, but not recompile the shader.
const MAX_SAMPLES = 20;

const _flatCamera = /*@__PURE__*/ new OrthographicCamera();
const _clearColor = /*@__PURE__*/ new Color();
let _oldTarget = null;
let _oldActiveCubeFace = 0;
let _oldActiveMipmapLevel = 0;
let _oldXrEnabled = false;

// Golden Ratio
const PHI = ( 1 + Math.sqrt( 5 ) ) / 2;
const INV_PHI = 1 / PHI;

// Vertices of a dodecahedron (except the opposites, which represent the
// same axis), used as axis directions evenly spread on a sphere.
const _axisDirections = [
	/*@__PURE__*/ new Vector3( - PHI, INV_PHI, 0 ),
	/*@__PURE__*/ new Vector3( PHI, INV_PHI, 0 ),
	/*@__PURE__*/ new Vector3( - INV_PHI, 0, PHI ),
	/*@__PURE__*/ new Vector3( INV_PHI, 0, PHI ),
	/*@__PURE__*/ new Vector3( 0, PHI, - INV_PHI ),
	/*@__PURE__*/ new Vector3( 0, PHI, INV_PHI ),
	/*@__PURE__*/ new Vector3( - 1, 1, - 1 ),
	/*@__PURE__*/ new Vector3( 1, 1, - 1 ),
	/*@__PURE__*/ new Vector3( - 1, 1, 1 ),
	/*@__PURE__*/ new Vector3( 1, 1, 1 ) ];

/**
 * This class generates a Prefiltered, Mipmapped Radiance Environment Map
 * (PMREM) from a cubeMap environment texture. This allows different levels of
 * blur to be quickly accessed based on material roughness. It is packed into a
 * special CubeUV format that allows us to perform custom interpolation so that
 * we can support nonlinear formats such as RGBE. Unlike a traditional mipmap
 * chain, it only goes down to the LOD_MIN level (above), and then creates extra
 * even more filtered 'mips' at the same LOD_MIN resolution, associated with
 * higher roughness levels. In this way we maintain resolution to smoothly
 * interpolate diffuse lighting while limiting sampling computation.
 *
 * Paper: Fast, Accurate Image-Based Lighting
 * https://drive.google.com/file/d/15y8r_UpKlU9SvV4ILb0C3qCPecS8pvLz/view
*/

class PMREMGenerator {

	constructor( renderer ) {

		this._renderer = renderer;
		this._pingPongRenderTarget = null;

		this._lodMax = 0;
		this._cubeSize = 0;
		this._lodPlanes = [];
		this._sizeLods = [];
		this._sigmas = [];

		this._blurMaterial = null;
		this._cubemapMaterial = null;
		this._equirectMaterial = null;

		this._compileMaterial( this._blurMaterial );

	}

	/**
	 * Generates a PMREM from a supplied Scene, which can be faster than using an
	 * image if networking bandwidth is low. Optional sigma specifies a blur radius
	 * in radians to be applied to the scene before PMREM generation. Optional near
	 * and far planes ensure the scene is rendered in its entirety (the cubeCamera
	 * is placed at the origin).
	 */
	fromScene( scene, sigma = 0, near = 0.1, far = 100 ) {

		_oldTarget = this._renderer.getRenderTarget();
		_oldActiveCubeFace = this._renderer.getActiveCubeFace();
		_oldActiveMipmapLevel = this._renderer.getActiveMipmapLevel();
		_oldXrEnabled = this._renderer.xr.enabled;

		this._renderer.xr.enabled = false;

		this._setSize( 256 );

		const cubeUVRenderTarget = this._allocateTargets();
		cubeUVRenderTarget.depthBuffer = true;

		this._sceneToCubeUV( scene, near, far, cubeUVRenderTarget );

		if ( sigma > 0 ) {

			this._blur( cubeUVRenderTarget, 0, 0, sigma );

		}

		this._applyPMREM( cubeUVRenderTarget );
		this._cleanup( cubeUVRenderTarget );

		return cubeUVRenderTarget;

	}

	/**
	 * Generates a PMREM from an equirectangular texture, which can be either LDR
	 * or HDR. The ideal input image size is 1k (1024 x 512),
	 * as this matches best with the 256 x 256 cubemap output.
	 * The smallest supported equirectangular image size is 64 x 32.
	 */
	fromEquirectangular( equirectangular, renderTarget = null ) {

		return this._fromTexture( equirectangular, renderTarget );

	}

	/**
	 * Generates a PMREM from an cubemap texture, which can be either LDR
	 * or HDR. The ideal input cube size is 256 x 256,
	 * as this matches best with the 256 x 256 cubemap output.
	 * The smallest supported cube size is 16 x 16.
	 */
	fromCubemap( cubemap, renderTarget = null ) {

		return this._fromTexture( cubemap, renderTarget );

	}

	/**
	 * Pre-compiles the cubemap shader. You can get faster start-up by invoking this method during
	 * your texture's network fetch for increased concurrency.
	 */
	compileCubemapShader() {

		if ( this._cubemapMaterial === null ) {

			this._cubemapMaterial = _getCubemapMaterial();
			this._compileMaterial( this._cubemapMaterial );

		}

	}

	/**
	 * Pre-compiles the equirectangular shader. You can get faster start-up by invoking this method during
	 * your texture's network fetch for increased concurrency.
	 */
	compileEquirectangularShader() {

		if ( this._equirectMaterial === null ) {

			this._equirectMaterial = _getEquirectMaterial();
			this._compileMaterial( this._equirectMaterial );

		}

	}

	/**
	 * Disposes of the PMREMGenerator's internal memory. Note that PMREMGenerator is a static class,
	 * so you should not need more than one PMREMGenerator object. If you do, calling dispose() on
	 * one of them will cause any others to also become unusable.
	 */
	dispose() {

		this._dispose();

		if ( this._cubemapMaterial !== null ) this._cubemapMaterial.dispose();
		if ( this._equirectMaterial !== null ) this._equirectMaterial.dispose();

	}

	// private interface

	_setSize( cubeSize ) {

		this._lodMax = Math.floor( Math.log2( cubeSize ) );
		this._cubeSize = Math.pow( 2, this._lodMax );

	}

	_dispose() {

		if ( this._blurMaterial !== null ) this._blurMaterial.dispose();

		if ( this._pingPongRenderTarget !== null ) this._pingPongRenderTarget.dispose();

		for ( let i = 0; i < this._lodPlanes.length; i ++ ) {

			this._lodPlanes[ i ].dispose();

		}

	}

	_cleanup( outputTarget ) {

		this._renderer.setRenderTarget( _oldTarget, _oldActiveCubeFace, _oldActiveMipmapLevel );
		this._renderer.xr.enabled = _oldXrEnabled;

		outputTarget.scissorTest = false;
		_setViewport( outputTarget, 0, 0, outputTarget.width, outputTarget.height );

	}

	_fromTexture( texture, renderTarget ) {

		if ( texture.mapping === CubeReflectionMapping || texture.mapping === CubeRefractionMapping ) {

			this._setSize( texture.image.length === 0 ? 16 : ( texture.image[ 0 ].width || texture.image[ 0 ].image.width ) );

		} else { // Equirectangular

			this._setSize( texture.image.width / 4 );

		}

		_oldTarget = this._renderer.getRenderTarget();
		_oldActiveCubeFace = this._renderer.getActiveCubeFace();
		_oldActiveMipmapLevel = this._renderer.getActiveMipmapLevel();
		_oldXrEnabled = this._renderer.xr.enabled;

		this._renderer.xr.enabled = false;

		const cubeUVRenderTarget = renderTarget || this._allocateTargets();
		this._textureToCubeUV( texture, cubeUVRenderTarget );
		this._applyPMREM( cubeUVRenderTarget );
		this._cleanup( cubeUVRenderTarget );

		return cubeUVRenderTarget;

	}

	_allocateTargets() {

		const width = 3 * Math.max( this._cubeSize, 16 * 7 );
		const height = 4 * this._cubeSize;

		const params = {
			magFilter: LinearFilter,
			minFilter: LinearFilter,
			generateMipmaps: false,
			type: HalfFloatType,
			format: RGBAFormat,
			colorSpace: LinearSRGBColorSpace,
			depthBuffer: false
		};

		const cubeUVRenderTarget = _createRenderTarget( width, height, params );

		if ( this._pingPongRenderTarget === null || this._pingPongRenderTarget.width !== width || this._pingPongRenderTarget.height !== height ) {

			if ( this._pingPongRenderTarget !== null ) {

				this._dispose();

			}

			this._pingPongRenderTarget = _createRenderTarget( width, height, params );

			const { _lodMax } = this;
			( { sizeLods: this._sizeLods, lodPlanes: this._lodPlanes, sigmas: this._sigmas } = _createPlanes( _lodMax ) );

			this._blurMaterial = _getBlurShader( _lodMax, width, height );

		}

		return cubeUVRenderTarget;

	}

	_compileMaterial( material ) {

		const tmpMesh = new Mesh( this._lodPlanes[ 0 ], material );
		this._renderer.compile( tmpMesh, _flatCamera );

	}

	_sceneToCubeUV( scene, near, far, cubeUVRenderTarget ) {

		const fov = 90;
		const aspect = 1;
		const cubeCamera = new PerspectiveCamera( fov, aspect, near, far );
		const upSign = [ 1, - 1, 1, 1, 1, 1 ];
		const forwardSign = [ 1, 1, 1, - 1, - 1, - 1 ];
		const renderer = this._renderer;

		const originalAutoClear = renderer.autoClear;
		const toneMapping = renderer.toneMapping;
		renderer.getClearColor( _clearColor );

		renderer.toneMapping = NoToneMapping;
		renderer.autoClear = false;

		const backgroundMaterial = new MeshBasicMaterial( {
			name: 'PMREM.Background',
			side: BackSide,
			depthWrite: false,
			depthTest: false,
		} );

		const backgroundBox = new Mesh( new BoxGeometry(), backgroundMaterial );

		let useSolidColor = false;
		const background = scene.background;

		if ( background ) {

			if ( background.isColor ) {

				backgroundMaterial.color.copy( background );
				scene.background = null;
				useSolidColor = true;

			}

		} else {

			backgroundMaterial.color.copy( _clearColor );
			useSolidColor = true;

		}

		for ( let i = 0; i < 6; i ++ ) {

			const col = i % 3;

			if ( col === 0 ) {

				cubeCamera.up.set( 0, upSign[ i ], 0 );
				cubeCamera.lookAt( forwardSign[ i ], 0, 0 );

			} else if ( col === 1 ) {

				cubeCamera.up.set( 0, 0, upSign[ i ] );
				cubeCamera.lookAt( 0, forwardSign[ i ], 0 );

			} else {

				cubeCamera.up.set( 0, upSign[ i ], 0 );
				cubeCamera.lookAt( 0, 0, forwardSign[ i ] );

			}

			const size = this._cubeSize;

			_setViewport( cubeUVRenderTarget, col * size, i > 2 ? size : 0, size, size );

			renderer.setRenderTarget( cubeUVRenderTarget );

			if ( useSolidColor ) {

				renderer.render( backgroundBox, cubeCamera );

			}

			renderer.render( scene, cubeCamera );

		}

		backgroundBox.geometry.dispose();
		backgroundBox.material.dispose();

		renderer.toneMapping = toneMapping;
		renderer.autoClear = originalAutoClear;
		scene.background = background;

	}

	_textureToCubeUV( texture, cubeUVRenderTarget ) {

		const renderer = this._renderer;

		const isCubeTexture = ( texture.mapping === CubeReflectionMapping || texture.mapping === CubeRefractionMapping );

		if ( isCubeTexture ) {

			if ( this._cubemapMaterial === null ) {

				this._cubemapMaterial = _getCubemapMaterial();

			}

			this._cubemapMaterial.uniforms.flipEnvMap.value = ( texture.isRenderTargetTexture === false ) ? - 1 : 1;

		} else {

			if ( this._equirectMaterial === null ) {

				this._equirectMaterial = _getEquirectMaterial();

			}

		}

		const material = isCubeTexture ? this._cubemapMaterial : this._equirectMaterial;
		const mesh = new Mesh( this._lodPlanes[ 0 ], material );

		const uniforms = material.uniforms;

		uniforms[ 'envMap' ].value = texture;

		const size = this._cubeSize;

		_setViewport( cubeUVRenderTarget, 0, 0, 3 * size, 2 * size );

		renderer.setRenderTarget( cubeUVRenderTarget );
		renderer.render( mesh, _flatCamera );

	}

	_applyPMREM( cubeUVRenderTarget ) {

		const renderer = this._renderer;
		const autoClear = renderer.autoClear;
		renderer.autoClear = false;
		const n = this._lodPlanes.length;

		for ( let i = 1; i < n; i ++ ) {

			const sigma = Math.sqrt( this._sigmas[ i ] * this._sigmas[ i ] - this._sigmas[ i - 1 ] * this._sigmas[ i - 1 ] );

			const poleAxis = _axisDirections[ ( n - i - 1 ) % _axisDirections.length ];

			this._blur( cubeUVRenderTarget, i - 1, i, sigma, poleAxis );

		}

		renderer.autoClear = autoClear;

	}

	/**
	 * This is a two-pass Gaussian blur for a cubemap. Normally this is done
	 * vertically and horizontally, but this breaks down on a cube. Here we apply
	 * the blur latitudinally (around the poles), and then longitudinally (towards
	 * the poles) to approximate the orthogonally-separable blur. It is least
	 * accurate at the poles, but still does a decent job.
	 */
	_blur( cubeUVRenderTarget, lodIn, lodOut, sigma, poleAxis ) {

		const pingPongRenderTarget = this._pingPongRenderTarget;

		this._halfBlur(
			cubeUVRenderTarget,
			pingPongRenderTarget,
			lodIn,
			lodOut,
			sigma,
			'latitudinal',
			poleAxis );

		this._halfBlur(
			pingPongRenderTarget,
			cubeUVRenderTarget,
			lodOut,
			lodOut,
			sigma,
			'longitudinal',
			poleAxis );

	}

	_halfBlur( targetIn, targetOut, lodIn, lodOut, sigmaRadians, direction, poleAxis ) {

		const renderer = this._renderer;
		const blurMaterial = this._blurMaterial;

		if ( direction !== 'latitudinal' && direction !== 'longitudinal' ) {

			console.error(
				'blur direction must be either latitudinal or longitudinal!' );

		}

		// Number of standard deviations at which to cut off the discrete approximation.
		const STANDARD_DEVIATIONS = 3;

		const blurMesh = new Mesh( this._lodPlanes[ lodOut ], blurMaterial );
		const blurUniforms = blurMaterial.uniforms;

		const pixels = this._sizeLods[ lodIn ] - 1;
		const radiansPerPixel = isFinite( sigmaRadians ) ? Math.PI / ( 2 * pixels ) : 2 * Math.PI / ( 2 * MAX_SAMPLES - 1 );
		const sigmaPixels = sigmaRadians / radiansPerPixel;
		const samples = isFinite( sigmaRadians ) ? 1 + Math.floor( STANDARD_DEVIATIONS * sigmaPixels ) : MAX_SAMPLES;

		if ( samples > MAX_SAMPLES ) {

			console.warn( `sigmaRadians, ${
				sigmaRadians}, is too large and will clip, as it requested ${
				samples} samples when the maximum is set to ${MAX_SAMPLES}` );

		}

		const weights = [];
		let sum = 0;

		for ( let i = 0; i < MAX_SAMPLES; ++ i ) {

			const x = i / sigmaPixels;
			const weight = Math.exp( - x * x / 2 );
			weights.push( weight );

			if ( i === 0 ) {

				sum += weight;

			} else if ( i < samples ) {

				sum += 2 * weight;

			}

		}

		for ( let i = 0; i < weights.length; i ++ ) {

			weights[ i ] = weights[ i ] / sum;

		}

		blurUniforms[ 'envMap' ].value = targetIn.texture;
		blurUniforms[ 'samples' ].value = samples;
		blurUniforms[ 'weights' ].value = weights;
		blurUniforms[ 'latitudinal' ].value = direction === 'latitudinal';

		if ( poleAxis ) {

			blurUniforms[ 'poleAxis' ].value = poleAxis;

		}

		const { _lodMax } = this;
		blurUniforms[ 'dTheta' ].value = radiansPerPixel;
		blurUniforms[ 'mipInt' ].value = _lodMax - lodIn;

		const outputSize = this._sizeLods[ lodOut ];
		const x = 3 * outputSize * ( lodOut > _lodMax - LOD_MIN ? lodOut - _lodMax + LOD_MIN : 0 );
		const y = 4 * ( this._cubeSize - outputSize );

		_setViewport( targetOut, x, y, 3 * outputSize, 2 * outputSize );
		renderer.setRenderTarget( targetOut );
		renderer.render( blurMesh, _flatCamera );

	}

}



function _createPlanes( lodMax ) {

	const lodPlanes = [];
	const sizeLods = [];
	const sigmas = [];

	let lod = lodMax;

	const totalLods = lodMax - LOD_MIN + 1 + EXTRA_LOD_SIGMA.length;

	for ( let i = 0; i < totalLods; i ++ ) {

		const sizeLod = Math.pow( 2, lod );
		sizeLods.push( sizeLod );
		let sigma = 1.0 / sizeLod;

		if ( i > lodMax - LOD_MIN ) {

			sigma = EXTRA_LOD_SIGMA[ i - lodMax + LOD_MIN - 1 ];

		} else if ( i === 0 ) {

			sigma = 0;

		}

		sigmas.push( sigma );

		const texelSize = 1.0 / ( sizeLod - 2 );
		const min = - texelSize;
		const max = 1 + texelSize;
		const uv1 = [ min, min, max, min, max, max, min, min, max, max, min, max ];

		const cubeFaces = 6;
		const vertices = 6;
		const positionSize = 3;
		const uvSize = 2;
		const faceIndexSize = 1;

		const position = new Float32Array( positionSize * vertices * cubeFaces );
		const uv = new Float32Array( uvSize * vertices * cubeFaces );
		const faceIndex = new Float32Array( faceIndexSize * vertices * cubeFaces );

		for ( let face = 0; face < cubeFaces; face ++ ) {

			const x = ( face % 3 ) * 2 / 3 - 1;
			const y = face > 2 ? 0 : - 1;
			const coordinates = [
				x, y, 0,
				x + 2 / 3, y, 0,
				x + 2 / 3, y + 1, 0,
				x, y, 0,
				x + 2 / 3, y + 1, 0,
				x, y + 1, 0
			];
			position.set( coordinates, positionSize * vertices * face );
			uv.set( uv1, uvSize * vertices * face );
			const fill = [ face, face, face, face, face, face ];
			faceIndex.set( fill, faceIndexSize * vertices * face );

		}

		const planes = new BufferGeometry();
		planes.setAttribute( 'position', new BufferAttribute( position, positionSize ) );
		planes.setAttribute( 'uv', new BufferAttribute( uv, uvSize ) );
		planes.setAttribute( 'faceIndex', new BufferAttribute( faceIndex, faceIndexSize ) );
		lodPlanes.push( planes );

		if ( lod > LOD_MIN ) {

			lod --;

		}

	}

	return { lodPlanes, sizeLods, sigmas };

}

function _createRenderTarget( width, height, params ) {

	const cubeUVRenderTarget = new WebGLRenderTarget( width, height, params );
	cubeUVRenderTarget.texture.mapping = CubeUVReflectionMapping;
	cubeUVRenderTarget.texture.name = 'PMREM.cubeUv';
	cubeUVRenderTarget.scissorTest = true;
	return cubeUVRenderTarget;

}

function _setViewport( target, x, y, width, height ) {

	target.viewport.set( x, y, width, height );
	target.scissor.set( x, y, width, height );

}

function _getBlurShader( lodMax, width, height ) {

	const weights = new Float32Array( MAX_SAMPLES );
	const poleAxis = new Vector3( 0, 1, 0 );
	const shaderMaterial = new ShaderMaterial( {

		name: 'SphericalGaussianBlur',

		defines: {
			'n': MAX_SAMPLES,
			'CUBEUV_TEXEL_WIDTH': 1.0 / width,
			'CUBEUV_TEXEL_HEIGHT': 1.0 / height,
			'CUBEUV_MAX_MIP': `${lodMax}.0`,
		},

		uniforms: {
			'envMap': { value: null },
			'samples': { value: 1 },
			'weights': { value: weights },
			'latitudinal': { value: false },
			'dTheta': { value: 0 },
			'mipInt': { value: 0 },
			'poleAxis': { value: poleAxis }
		},

		vertexShader: _getCommonVertexShader(),

		fragmentShader: /* glsl */`

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;
			uniform int samples;
			uniform float weights[ n ];
			uniform bool latitudinal;
			uniform float dTheta;
			uniform float mipInt;
			uniform vec3 poleAxis;

			#define ENVMAP_TYPE_CUBE_UV
			#include <cube_uv_reflection_fragment>

			vec3 getSample( float theta, vec3 axis ) {

				float cosTheta = cos( theta );
				// Rodrigues' axis-angle rotation
				vec3 sampleDirection = vOutputDirection * cosTheta
					+ cross( axis, vOutputDirection ) * sin( theta )
					+ axis * dot( axis, vOutputDirection ) * ( 1.0 - cosTheta );

				return bilinearCubeUV( envMap, sampleDirection, mipInt );

			}

			void main() {

				vec3 axis = latitudinal ? poleAxis : cross( poleAxis, vOutputDirection );

				if ( all( equal( axis, vec3( 0.0 ) ) ) ) {

					axis = vec3( vOutputDirection.z, 0.0, - vOutputDirection.x );

				}

				axis = normalize( axis );

				gl_FragColor = vec4( 0.0, 0.0, 0.0, 1.0 );
				gl_FragColor.rgb += weights[ 0 ] * getSample( 0.0, axis );

				for ( int i = 1; i < n; i++ ) {

					if ( i >= samples ) {

						break;

					}

					float theta = dTheta * float( i );
					gl_FragColor.rgb += weights[ i ] * getSample( -1.0 * theta, axis );
					gl_FragColor.rgb += weights[ i ] * getSample( theta, axis );

				}

			}
		`,

		blending: NoBlending,
		depthTest: false,
		depthWrite: false

	} );

	return shaderMaterial;

}

function _getEquirectMaterial() {

	return new ShaderMaterial( {

		name: 'EquirectangularToCubeUV',

		uniforms: {
			'envMap': { value: null }
		},

		vertexShader: _getCommonVertexShader(),

		fragmentShader: /* glsl */`

			precision mediump float;
			precision mediump int;

			varying vec3 vOutputDirection;

			uniform sampler2D envMap;

			#include <common>

			void main() {

				vec3 outputDirection = normalize( vOutputDirection );
				vec2 uv = equirectUv( outputDirection );

				gl_FragColor = vec4( texture2D ( envMap, uv ).rgb, 1.0 );

			}
		`,

		blending: NoBlending,
		depthTest: false,
		depthWrite: false

	} );

}

function _getCubemapMaterial() {

	return new ShaderMaterial( {

		name: 'CubemapToCubeUV',

		uniforms: {
			'envMap': { value: null },
			'flipEnvMap': { value: - 1 }
		},

		vertexShader: _getCommonVertexShader(),

		fragmentShader: /* glsl */`

			precision mediump float;
			precision mediump int;

			uniform float flipEnvMap;

			varying vec3 vOutputDirection;

			uniform samplerCube envMap;

			void main() {

				gl_FragColor = textureCube( envMap, vec3( flipEnvMap * vOutputDirection.x, vOutputDirection.yz ) );

			}
		`,

		blending: NoBlending,
		depthTest: false,
		depthWrite: false

	} );

}

function _getCommonVertexShader() {

	return /* glsl */`

		precision mediump float;
		precision mediump int;

		attribute float faceIndex;

		varying vec3 vOutputDirection;

		// RH coordinate system; PMREM face-indexing convention
		vec3 getDirection( vec2 uv, float face ) {

			uv = 2.0 * uv - 1.0;

			vec3 direction = vec3( uv, 1.0 );

			if ( face == 0.0 ) {

				direction = direction.zyx; // ( 1, v, u ) pos x

			} else if ( face == 1.0 ) {

				direction = direction.xzy;
				direction.xz *= -1.0; // ( -u, 1, -v ) pos y

			} else if ( face == 2.0 ) {

				direction.x *= -1.0; // ( -u, v, 1 ) pos z

			} else if ( face == 3.0 ) {

				direction = direction.zyx;
				direction.xz *= -1.0; // ( -1, v, -u ) neg x

			} else if ( face == 4.0 ) {

				direction = direction.xzy;
				direction.xy *= -1.0; // ( -u, -1, v ) neg y

			} else if ( face == 5.0 ) {

				direction.z *= -1.0; // ( u, v, -1 ) neg z

			}

			return direction;

		}

		void main() {

			vOutputDirection = getDirection( uv, faceIndex );
			gl_Position = vec4( position, 1.0 );

		}
	`;

}

function WebGLCubeUVMaps( renderer ) {

	let cubeUVmaps = new WeakMap();

	let pmremGenerator = null;

	function get( texture ) {

		if ( texture && texture.isTexture ) {

			const mapping = texture.mapping;

			const isEquirectMap = ( mapping === EquirectangularReflectionMapping || mapping === EquirectangularRefractionMapping );
			const isCubeMap = ( mapping === CubeReflectionMapping || mapping === CubeRefractionMapping );

			// equirect/cube map to cubeUV conversion

			if ( isEquirectMap || isCubeMap ) {

				let renderTarget = cubeUVmaps.get( texture );

				const currentPMREMVersion = renderTarget !== undefined ? renderTarget.texture.pmremVersion : 0;

				if ( texture.isRenderTargetTexture && texture.pmremVersion !== currentPMREMVersion ) {

					if ( pmremGenerator === null ) pmremGenerator = new PMREMGenerator( renderer );

					renderTarget = isEquirectMap ? pmremGenerator.fromEquirectangular( texture, renderTarget ) : pmremGenerator.fromCubemap( texture, renderTarget );
					renderTarget.texture.pmremVersion = texture.pmremVersion;

					cubeUVmaps.set( texture, renderTarget );

					return renderTarget.texture;

				} else {

					if ( renderTarget !== undefined ) {

						return renderTarget.texture;

					} else {

						const image = texture.image;

						if ( ( isEquirectMap && image && image.height > 0 ) || ( isCubeMap && image && isCubeTextureComplete( image ) ) ) {

							if ( pmremGenerator === null ) pmremGenerator = new PMREMGenerator( renderer );

							renderTarget = isEquirectMap ? pmremGenerator.fromEquirectangular( texture ) : pmremGenerator.fromCubemap( texture );
							renderTarget.texture.pmremVersion = texture.pmremVersion;

							cubeUVmaps.set( texture, renderTarget );

							texture.addEventListener( 'dispose', onTextureDispose );

							return renderTarget.texture;

						} else {

							// image not yet ready. try the conversion next frame

							return null;

						}

					}

				}

			}

		}

		return texture;

	}

	function isCubeTextureComplete( image ) {

		let count = 0;
		const length = 6;

		for ( let i = 0; i < length; i ++ ) {

			if ( image[ i ] !== undefined ) count ++;

		}

		return count === length;


	}

	function onTextureDispose( event ) {

		const texture = event.target;

		texture.removeEventListener( 'dispose', onTextureDispose );

		const cubemapUV = cubeUVmaps.get( texture );

		if ( cubemapUV !== undefined ) {

			cubeUVmaps.delete( texture );
			cubemapUV.dispose();

		}

	}

	function dispose() {

		cubeUVmaps = new WeakMap();

		if ( pmremGenerator !== null ) {

			pmremGenerator.dispose();
			pmremGenerator = null;

		}

	}

	return {
		get: get,
		dispose: dispose
	};

}

function WebGLExtensions( gl ) {

	const extensions = {};

	function getExtension( name ) {

		if ( extensions[ name ] !== undefined ) {

			return extensions[ name ];

		}

		let extension;

		switch ( name ) {

			case 'WEBGL_depth_texture':
				extension = gl.getExtension( 'WEBGL_depth_texture' ) || gl.getExtension( 'MOZ_WEBGL_depth_texture' ) || gl.getExtension( 'WEBKIT_WEBGL_depth_texture' );
				break;

			case 'EXT_texture_filter_anisotropic':
				extension = gl.getExtension( 'EXT_texture_filter_anisotropic' ) || gl.getExtension( 'MOZ_EXT_texture_filter_anisotropic' ) || gl.getExtension( 'WEBKIT_EXT_texture_filter_anisotropic' );
				break;

			case 'WEBGL_compressed_texture_s3tc':
				extension = gl.getExtension( 'WEBGL_compressed_texture_s3tc' ) || gl.getExtension( 'MOZ_WEBGL_compressed_texture_s3tc' ) || gl.getExtension( 'WEBKIT_WEBGL_compressed_texture_s3tc' );
				break;

			case 'WEBGL_compressed_texture_pvrtc':
				extension = gl.getExtension( 'WEBGL_compressed_texture_pvrtc' ) || gl.getExtension( 'WEBKIT_WEBGL_compressed_texture_pvrtc' );
				break;

			default:
				extension = gl.getExtension( name );

		}

		extensions[ name ] = extension;

		return extension;

	}

	return {

		has: function ( name ) {

			return getExtension( name ) !== null;

		},

		init: function () {

			getExtension( 'EXT_color_buffer_float' );
			getExtension( 'WEBGL_clip_cull_distance' );
			getExtension( 'OES_texture_float_linear' );
			getExtension( 'EXT_color_buffer_half_float' );
			getExtension( 'WEBGL_multisampled_render_to_texture' );
			getExtension( 'WEBGL_render_shared_exponent' );

		},

		get: function ( name ) {

			const extension = getExtension( name );

			if ( extension === null ) {

				warnOnce( 'THREE.WebGLRenderer: ' + name + ' extension not supported.' );

			}

			return extension;

		}

	};

}

function WebGLGeometries( gl, attributes, info, bindingStates ) {

	const geometries = {};
	const wireframeAttributes = new WeakMap();

	function onGeometryDispose( event ) {

		const geometry = event.target;

		if ( geometry.index !== null ) {

			attributes.remove( geometry.index );

		}

		for ( const name in geometry.attributes ) {

			attributes.remove( geometry.attributes[ name ] );

		}

		for ( const name in geometry.morphAttributes ) {

			const array = geometry.morphAttributes[ name ];

			for ( let i = 0, l = array.length; i < l; i ++ ) {

				attributes.remove( array[ i ] );

			}

		}

		geometry.removeEventListener( 'dispose', onGeometryDispose );

		delete geometries[ geometry.id ];

		const attribute = wireframeAttributes.get( geometry );

		if ( attribute ) {

			attributes.remove( attribute );
			wireframeAttributes.delete( geometry );

		}

		bindingStates.releaseStatesOfGeometry( geometry );

		if ( geometry.isInstancedBufferGeometry === true ) {

			delete geometry._maxInstanceCount;

		}

		//

		info.memory.geometries --;

	}

	function get( object, geometry ) {

		if ( geometries[ geometry.id ] === true ) return geometry;

		geometry.addEventListener( 'dispose', onGeometryDispose );

		geometries[ geometry.id ] = true;

		info.memory.geometries ++;

		return geometry;

	}

	function update( geometry ) {

		const geometryAttributes = geometry.attributes;

		// Updating index buffer in VAO now. See WebGLBindingStates.

		for ( const name in geometryAttributes ) {

			attributes.update( geometryAttributes[ name ], gl.ARRAY_BUFFER );

		}

		// morph targets

		const morphAttributes = geometry.morphAttributes;

		for ( const name in morphAttributes ) {

			const array = morphAttributes[ name ];

			for ( let i = 0, l = array.length; i < l; i ++ ) {

				attributes.update( array[ i ], gl.ARRAY_BUFFER );

			}

		}

	}

	function updateWireframeAttribute( geometry ) {

		const indices = [];

		const geometryIndex = geometry.index;
		const geometryPosition = geometry.attributes.position;
		let version = 0;

		if ( geometryIndex !== null ) {

			const array = geometryIndex.array;
			version = geometryIndex.version;

			for ( let i = 0, l = array.length; i < l; i += 3 ) {

				const a = array[ i + 0 ];
				const b = array[ i + 1 ];
				const c = array[ i + 2 ];

				indices.push( a, b, b, c, c, a );

			}

		} else if ( geometryPosition !== undefined ) {

			const array = geometryPosition.array;
			version = geometryPosition.version;

			for ( let i = 0, l = ( array.length / 3 ) - 1; i < l; i += 3 ) {

				const a = i + 0;
				const b = i + 1;
				const c = i + 2;

				indices.push( a, b, b, c, c, a );

			}

		} else {

			return;

		}

		const attribute = new ( arrayNeedsUint32( indices ) ? Uint32BufferAttribute : Uint16BufferAttribute )( indices, 1 );
		attribute.version = version;

		// Updating index buffer in VAO now. See WebGLBindingStates

		//

		const previousAttribute = wireframeAttributes.get( geometry );

		if ( previousAttribute ) attributes.remove( previousAttribute );

		//

		wireframeAttributes.set( geometry, attribute );

	}

	function getWireframeAttribute( geometry ) {

		const currentAttribute = wireframeAttributes.get( geometry );

		if ( currentAttribute ) {

			const geometryIndex = geometry.index;

			if ( geometryIndex !== null ) {

				// if the attribute is obsolete, create a new one

				if ( currentAttribute.version < geometryIndex.version ) {

					updateWireframeAttribute( geometry );

				}

			}

		} else {

			updateWireframeAttribute( geometry );

		}

		return wireframeAttributes.get( geometry );

	}

	return {

		get: get,
		update: update,

		getWireframeAttribute: getWireframeAttribute

	};

}

function WebGLIndexedBufferRenderer( gl, extensions, info ) {

	let mode;

	function setMode( value ) {

		mode = value;

	}

	let type, bytesPerElement;

	function setIndex( value ) {

		type = value.type;
		bytesPerElement = value.bytesPerElement;

	}

	function render( start, count ) {

		gl.drawElements( mode, count, type, start * bytesPerElement );

		info.update( count, mode, 1 );

	}

	function renderInstances( start, count, primcount ) {

		if ( primcount === 0 ) return;

		gl.drawElementsInstanced( mode, count, type, start * bytesPerElement, primcount );

		info.update( count, mode, primcount );

	}

	function renderMultiDraw( starts, counts, drawCount ) {

		if ( drawCount === 0 ) return;

		const extension = extensions.get( 'WEBGL_multi_draw' );
		extension.multiDrawElementsWEBGL( mode, counts, 0, type, starts, 0, drawCount );

		let elementCount = 0;
		for ( let i = 0; i < drawCount; i ++ ) {

			elementCount += counts[ i ];

		}

		info.update( elementCount, mode, 1 );


	}

	function renderMultiDrawInstances( starts, counts, drawCount, primcount ) {

		if ( drawCount === 0 ) return;

		const extension = extensions.get( 'WEBGL_multi_draw' );

		if ( extension === null ) {

			for ( let i = 0; i < starts.length; i ++ ) {

				renderInstances( starts[ i ] / bytesPerElement, counts[ i ], primcount[ i ] );

			}

		} else {

			extension.multiDrawElementsInstancedWEBGL( mode, counts, 0, type, starts, 0, primcount, 0, drawCount );

			let elementCount = 0;
			for ( let i = 0; i < drawCount; i ++ ) {

				elementCount += counts[ i ];

			}

			for ( let i = 0; i < primcount.length; i ++ ) {

				info.update( elementCount, mode, primcount[ i ] );

			}

		}

	}

	//

	this.setMode = setMode;
	this.setIndex = setIndex;
	this.render = render;
	this.renderInstances = renderInstances;
	this.renderMultiDraw = renderMultiDraw;
	this.renderMultiDrawInstances = renderMultiDrawInstances;

}

function WebGLInfo( gl ) {

	const memory = {
		geometries: 0,
		textures: 0
	};

	const render = {
		frame: 0,
		calls: 0,
		triangles: 0,
		points: 0,
		lines: 0
	};

	function update( count, mode, instanceCount ) {

		render.calls ++;

		switch ( mode ) {

			case gl.TRIANGLES:
				render.triangles += instanceCount * ( count / 3 );
				break;

			case gl.LINES:
				render.lines += instanceCount * ( count / 2 );
				break;

			case gl.LINE_STRIP:
				render.lines += instanceCount * ( count - 1 );
				break;

			case gl.LINE_LOOP:
				render.lines += instanceCount * count;
				break;

			case gl.POINTS:
				render.points += instanceCount * count;
				break;

			default:
				console.error( 'THREE.WebGLInfo: Unknown draw mode:', mode );
				break;

		}

	}

	function reset() {

		render.calls = 0;
		render.triangles = 0;
		render.points = 0;
		render.lines = 0;

	}

	return {
		memory: memory,
		render: render,
		programs: null,
		autoReset: true,
		reset: reset,
		update: update
	};

}

function WebGLMorphtargets( gl, capabilities, textures ) {

	const morphTextures = new WeakMap();
	const morph = new Vector4();

	function update( object, geometry, program ) {

		const objectInfluences = object.morphTargetInfluences;

		// the following encodes morph targets into an array of data textures. Each layer represents a single morph target.

		const morphAttribute = geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color;
		const morphTargetsCount = ( morphAttribute !== undefined ) ? morphAttribute.length : 0;

		let entry = morphTextures.get( geometry );

		if ( entry === undefined || entry.count !== morphTargetsCount ) {

			if ( entry !== undefined ) entry.texture.dispose();

			const hasMorphPosition = geometry.morphAttributes.position !== undefined;
			const hasMorphNormals = geometry.morphAttributes.normal !== undefined;
			const hasMorphColors = geometry.morphAttributes.color !== undefined;

			const morphTargets = geometry.morphAttributes.position || [];
			const morphNormals = geometry.morphAttributes.normal || [];
			const morphColors = geometry.morphAttributes.color || [];

			let vertexDataCount = 0;

			if ( hasMorphPosition === true ) vertexDataCount = 1;
			if ( hasMorphNormals === true ) vertexDataCount = 2;
			if ( hasMorphColors === true ) vertexDataCount = 3;

			let width = geometry.attributes.position.count * vertexDataCount;
			let height = 1;

			if ( width > capabilities.maxTextureSize ) {

				height = Math.ceil( width / capabilities.maxTextureSize );
				width = capabilities.maxTextureSize;

			}

			const buffer = new Float32Array( width * height * 4 * morphTargetsCount );

			const texture = new DataArrayTexture( buffer, width, height, morphTargetsCount );
			texture.type = FloatType;
			texture.needsUpdate = true;

			// fill buffer

			const vertexDataStride = vertexDataCount * 4;

			for ( let i = 0; i < morphTargetsCount; i ++ ) {

				const morphTarget = morphTargets[ i ];
				const morphNormal = morphNormals[ i ];
				const morphColor = morphColors[ i ];

				const offset = width * height * 4 * i;

				for ( let j = 0; j < morphTarget.count; j ++ ) {

					const stride = j * vertexDataStride;

					if ( hasMorphPosition === true ) {

						morph.fromBufferAttribute( morphTarget, j );

						buffer[ offset + stride + 0 ] = morph.x;
						buffer[ offset + stride + 1 ] = morph.y;
						buffer[ offset + stride + 2 ] = morph.z;
						buffer[ offset + stride + 3 ] = 0;

					}

					if ( hasMorphNormals === true ) {

						morph.fromBufferAttribute( morphNormal, j );

						buffer[ offset + stride + 4 ] = morph.x;
						buffer[ offset + stride + 5 ] = morph.y;
						buffer[ offset + stride + 6 ] = morph.z;
						buffer[ offset + stride + 7 ] = 0;

					}

					if ( hasMorphColors === true ) {

						morph.fromBufferAttribute( morphColor, j );

						buffer[ offset + stride + 8 ] = morph.x;
						buffer[ offset + stride + 9 ] = morph.y;
						buffer[ offset + stride + 10 ] = morph.z;
						buffer[ offset + stride + 11 ] = ( morphColor.itemSize === 4 ) ? morph.w : 1;

					}

				}

			}

			entry = {
				count: morphTargetsCount,
				texture: texture,
				size: new Vector2( width, height )
			};

			morphTextures.set( geometry, entry );

			function disposeTexture() {

				texture.dispose();

				morphTextures.delete( geometry );

				geometry.removeEventListener( 'dispose', disposeTexture );

			}

			geometry.addEventListener( 'dispose', disposeTexture );

		}

		//
		if ( object.isInstancedMesh === true && object.morphTexture !== null ) {

			program.getUniforms().setValue( gl, 'morphTexture', object.morphTexture, textures );

		} else {

			let morphInfluencesSum = 0;

			for ( let i = 0; i < objectInfluences.length; i ++ ) {

				morphInfluencesSum += objectInfluences[ i ];

			}

			const morphBaseInfluence = geometry.morphTargetsRelative ? 1 : 1 - morphInfluencesSum;


			program.getUniforms().setValue( gl, 'morphTargetBaseInfluence', morphBaseInfluence );
			program.getUniforms().setValue( gl, 'morphTargetInfluences', objectInfluences );

		}

		program.getUniforms().setValue( gl, 'morphTargetsTexture', entry.texture, textures );
		program.getUniforms().setValue( gl, 'morphTargetsTextureSize', entry.size );

	}

	return {

		update: update

	};

}

function WebGLObjects( gl, geometries, attributes, info ) {

	let updateMap = new WeakMap();

	function update( object ) {

		const frame = info.render.frame;

		const geometry = object.geometry;
		const buffergeometry = geometries.get( object, geometry );

		// Update once per frame

		if ( updateMap.get( buffergeometry ) !== frame ) {

			geometries.update( buffergeometry );

			updateMap.set( buffergeometry, frame );

		}

		if ( object.isInstancedMesh ) {

			if ( object.hasEventListener( 'dispose', onInstancedMeshDispose ) === false ) {

				object.addEventListener( 'dispose', onInstancedMeshDispose );

			}

			if ( updateMap.get( object ) !== frame ) {

				attributes.update( object.instanceMatrix, gl.ARRAY_BUFFER );

				if ( object.instanceColor !== null ) {

					attributes.update( object.instanceColor, gl.ARRAY_BUFFER );

				}

				updateMap.set( object, frame );

			}

		}

		if ( object.isSkinnedMesh ) {

			const skeleton = object.skeleton;

			if ( updateMap.get( skeleton ) !== frame ) {

				skeleton.update();

				updateMap.set( skeleton, frame );

			}

		}

		return buffergeometry;

	}

	function dispose() {

		updateMap = new WeakMap();

	}

	function onInstancedMeshDispose( event ) {

		const instancedMesh = event.target;

		instancedMesh.removeEventListener( 'dispose', onInstancedMeshDispose );

		attributes.remove( instancedMesh.instanceMatrix );

		if ( instancedMesh.instanceColor !== null ) attributes.remove( instancedMesh.instanceColor );

	}

	return {

		update: update,
		dispose: dispose

	};

}

class DepthTexture extends Texture {

	constructor( width, height, type, mapping, wrapS, wrapT, magFilter, minFilter, anisotropy, format = DepthFormat ) {

		if ( format !== DepthFormat && format !== DepthStencilFormat ) {

			throw new Error( 'DepthTexture format must be either THREE.DepthFormat or THREE.DepthStencilFormat' );

		}

		if ( type === undefined && format === DepthFormat ) type = UnsignedIntType;
		if ( type === undefined && format === DepthStencilFormat ) type = UnsignedInt248Type;

		super( null, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy );

		this.isDepthTexture = true;

		this.image = { width: width, height: height };

		this.magFilter = magFilter !== undefined ? magFilter : NearestFilter;
		this.minFilter = minFilter !== undefined ? minFilter : NearestFilter;

		this.flipY = false;
		this.generateMipmaps = false;

		this.compareFunction = null;

	}


	copy( source ) {

		super.copy( source );

		this.compareFunction = source.compareFunction;

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		if ( this.compareFunction !== null ) data.compareFunction = this.compareFunction;

		return data;

	}

}

/**
 * Uniforms of a program.
 * Those form a tree structure with a special top-level container for the root,
 * which you get by calling 'new WebGLUniforms( gl, program )'.
 *
 *
 * Properties of inner nodes including the top-level container:
 *
 * .seq - array of nested uniforms
 * .map - nested uniforms by name
 *
 *
 * Methods of all nodes except the top-level container:
 *
 * .setValue( gl, value, [textures] )
 *
 * 		uploads a uniform value(s)
 *  	the 'textures' parameter is needed for sampler uniforms
 *
 *
 * Static methods of the top-level container (textures factorizations):
 *
 * .upload( gl, seq, values, textures )
 *
 * 		sets uniforms in 'seq' to 'values[id].value'
 *
 * .seqWithValue( seq, values ) : filteredSeq
 *
 * 		filters 'seq' entries with corresponding entry in values
 *
 *
 * Methods of the top-level container (textures factorizations):
 *
 * .setValue( gl, name, value, textures )
 *
 * 		sets uniform with  name 'name' to 'value'
 *
 * .setOptional( gl, obj, prop )
 *
 * 		like .set for an optional property of the object
 *
 */


const emptyTexture = /*@__PURE__*/ new Texture();

const emptyShadowTexture = /*@__PURE__*/ new DepthTexture( 1, 1 );

const emptyArrayTexture = /*@__PURE__*/ new DataArrayTexture();
const empty3dTexture = /*@__PURE__*/ new Data3DTexture();
const emptyCubeTexture = /*@__PURE__*/ new CubeTexture();

// --- Utilities ---

// Array Caches (provide typed arrays for temporary by size)

const arrayCacheF32 = [];
const arrayCacheI32 = [];

// Float32Array caches used for uploading Matrix uniforms

const mat4array = new Float32Array( 16 );
const mat3array = new Float32Array( 9 );
const mat2array = new Float32Array( 4 );

// Flattening for arrays of vectors and matrices

function flatten( array, nBlocks, blockSize ) {

	const firstElem = array[ 0 ];

	if ( firstElem <= 0 || firstElem > 0 ) return array;
	// unoptimized: ! isNaN( firstElem )
	// see http://jacksondunstan.com/articles/983

	const n = nBlocks * blockSize;
	let r = arrayCacheF32[ n ];

	if ( r === undefined ) {

		r = new Float32Array( n );
		arrayCacheF32[ n ] = r;

	}

	if ( nBlocks !== 0 ) {

		firstElem.toArray( r, 0 );

		for ( let i = 1, offset = 0; i !== nBlocks; ++ i ) {

			offset += blockSize;
			array[ i ].toArray( r, offset );

		}

	}

	return r;

}

function arraysEqual( a, b ) {

	if ( a.length !== b.length ) return false;

	for ( let i = 0, l = a.length; i < l; i ++ ) {

		if ( a[ i ] !== b[ i ] ) return false;

	}

	return true;

}

function copyArray( a, b ) {

	for ( let i = 0, l = b.length; i < l; i ++ ) {

		a[ i ] = b[ i ];

	}

}

// Texture unit allocation

function allocTexUnits( textures, n ) {

	let r = arrayCacheI32[ n ];

	if ( r === undefined ) {

		r = new Int32Array( n );
		arrayCacheI32[ n ] = r;

	}

	for ( let i = 0; i !== n; ++ i ) {

		r[ i ] = textures.allocateTextureUnit();

	}

	return r;

}

// --- Setters ---

// Note: Defining these methods externally, because they come in a bunch
// and this way their names minify.

// Single scalar

function setValueV1f( gl, v ) {

	const cache = this.cache;

	if ( cache[ 0 ] === v ) return;

	gl.uniform1f( this.addr, v );

	cache[ 0 ] = v;

}

// Single float vector (from flat array or THREE.VectorN)

function setValueV2f( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y ) {

			gl.uniform2f( this.addr, v.x, v.y );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform2fv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV3f( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z ) {

			gl.uniform3f( this.addr, v.x, v.y, v.z );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;

		}

	} else if ( v.r !== undefined ) {

		if ( cache[ 0 ] !== v.r || cache[ 1 ] !== v.g || cache[ 2 ] !== v.b ) {

			gl.uniform3f( this.addr, v.r, v.g, v.b );

			cache[ 0 ] = v.r;
			cache[ 1 ] = v.g;
			cache[ 2 ] = v.b;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform3fv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV4f( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z || cache[ 3 ] !== v.w ) {

			gl.uniform4f( this.addr, v.x, v.y, v.z, v.w );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;
			cache[ 3 ] = v.w;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform4fv( this.addr, v );

		copyArray( cache, v );

	}

}

// Single matrix (from flat array or THREE.MatrixN)

function setValueM2( gl, v ) {

	const cache = this.cache;
	const elements = v.elements;

	if ( elements === undefined ) {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniformMatrix2fv( this.addr, false, v );

		copyArray( cache, v );

	} else {

		if ( arraysEqual( cache, elements ) ) return;

		mat2array.set( elements );

		gl.uniformMatrix2fv( this.addr, false, mat2array );

		copyArray( cache, elements );

	}

}

function setValueM3( gl, v ) {

	const cache = this.cache;
	const elements = v.elements;

	if ( elements === undefined ) {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniformMatrix3fv( this.addr, false, v );

		copyArray( cache, v );

	} else {

		if ( arraysEqual( cache, elements ) ) return;

		mat3array.set( elements );

		gl.uniformMatrix3fv( this.addr, false, mat3array );

		copyArray( cache, elements );

	}

}

function setValueM4( gl, v ) {

	const cache = this.cache;
	const elements = v.elements;

	if ( elements === undefined ) {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniformMatrix4fv( this.addr, false, v );

		copyArray( cache, v );

	} else {

		if ( arraysEqual( cache, elements ) ) return;

		mat4array.set( elements );

		gl.uniformMatrix4fv( this.addr, false, mat4array );

		copyArray( cache, elements );

	}

}

// Single integer / boolean

function setValueV1i( gl, v ) {

	const cache = this.cache;

	if ( cache[ 0 ] === v ) return;

	gl.uniform1i( this.addr, v );

	cache[ 0 ] = v;

}

// Single integer / boolean vector (from flat array or THREE.VectorN)

function setValueV2i( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y ) {

			gl.uniform2i( this.addr, v.x, v.y );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform2iv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV3i( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z ) {

			gl.uniform3i( this.addr, v.x, v.y, v.z );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform3iv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV4i( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z || cache[ 3 ] !== v.w ) {

			gl.uniform4i( this.addr, v.x, v.y, v.z, v.w );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;
			cache[ 3 ] = v.w;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform4iv( this.addr, v );

		copyArray( cache, v );

	}

}

// Single unsigned integer

function setValueV1ui( gl, v ) {

	const cache = this.cache;

	if ( cache[ 0 ] === v ) return;

	gl.uniform1ui( this.addr, v );

	cache[ 0 ] = v;

}

// Single unsigned integer vector (from flat array or THREE.VectorN)

function setValueV2ui( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y ) {

			gl.uniform2ui( this.addr, v.x, v.y );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform2uiv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV3ui( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z ) {

			gl.uniform3ui( this.addr, v.x, v.y, v.z );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform3uiv( this.addr, v );

		copyArray( cache, v );

	}

}

function setValueV4ui( gl, v ) {

	const cache = this.cache;

	if ( v.x !== undefined ) {

		if ( cache[ 0 ] !== v.x || cache[ 1 ] !== v.y || cache[ 2 ] !== v.z || cache[ 3 ] !== v.w ) {

			gl.uniform4ui( this.addr, v.x, v.y, v.z, v.w );

			cache[ 0 ] = v.x;
			cache[ 1 ] = v.y;
			cache[ 2 ] = v.z;
			cache[ 3 ] = v.w;

		}

	} else {

		if ( arraysEqual( cache, v ) ) return;

		gl.uniform4uiv( this.addr, v );

		copyArray( cache, v );

	}

}


// Single texture (2D / Cube)

function setValueT1( gl, v, textures ) {

	const cache = this.cache;
	const unit = textures.allocateTextureUnit();

	if ( cache[ 0 ] !== unit ) {

		gl.uniform1i( this.addr, unit );
		cache[ 0 ] = unit;

	}

	let emptyTexture2D;

	if ( this.type === gl.SAMPLER_2D_SHADOW ) {

		emptyShadowTexture.compareFunction = LessEqualCompare; // #28670
		emptyTexture2D = emptyShadowTexture;

	} else {

		emptyTexture2D = emptyTexture;

	}

	textures.setTexture2D( v || emptyTexture2D, unit );

}

function setValueT3D1( gl, v, textures ) {

	const cache = this.cache;
	const unit = textures.allocateTextureUnit();

	if ( cache[ 0 ] !== unit ) {

		gl.uniform1i( this.addr, unit );
		cache[ 0 ] = unit;

	}

	textures.setTexture3D( v || empty3dTexture, unit );

}

function setValueT6( gl, v, textures ) {

	const cache = this.cache;
	const unit = textures.allocateTextureUnit();

	if ( cache[ 0 ] !== unit ) {

		gl.uniform1i( this.addr, unit );
		cache[ 0 ] = unit;

	}

	textures.setTextureCube( v || emptyCubeTexture, unit );

}

function setValueT2DArray1( gl, v, textures ) {

	const cache = this.cache;
	const unit = textures.allocateTextureUnit();

	if ( cache[ 0 ] !== unit ) {

		gl.uniform1i( this.addr, unit );
		cache[ 0 ] = unit;

	}

	textures.setTexture2DArray( v || emptyArrayTexture, unit );

}

// Helper to pick the right setter for the singular case

function getSingularSetter( type ) {

	switch ( type ) {

		case 0x1406: return setValueV1f; // FLOAT
		case 0x8b50: return setValueV2f; // _VEC2
		case 0x8b51: return setValueV3f; // _VEC3
		case 0x8b52: return setValueV4f; // _VEC4

		case 0x8b5a: return setValueM2; // _MAT2
		case 0x8b5b: return setValueM3; // _MAT3
		case 0x8b5c: return setValueM4; // _MAT4

		case 0x1404: case 0x8b56: return setValueV1i; // INT, BOOL
		case 0x8b53: case 0x8b57: return setValueV2i; // _VEC2
		case 0x8b54: case 0x8b58: return setValueV3i; // _VEC3
		case 0x8b55: case 0x8b59: return setValueV4i; // _VEC4

		case 0x1405: return setValueV1ui; // UINT
		case 0x8dc6: return setValueV2ui; // _VEC2
		case 0x8dc7: return setValueV3ui; // _VEC3
		case 0x8dc8: return setValueV4ui; // _VEC4

		case 0x8b5e: // SAMPLER_2D
		case 0x8d66: // SAMPLER_EXTERNAL_OES
		case 0x8dca: // INT_SAMPLER_2D
		case 0x8dd2: // UNSIGNED_INT_SAMPLER_2D
		case 0x8b62: // SAMPLER_2D_SHADOW
			return setValueT1;

		case 0x8b5f: // SAMPLER_3D
		case 0x8dcb: // INT_SAMPLER_3D
		case 0x8dd3: // UNSIGNED_INT_SAMPLER_3D
			return setValueT3D1;

		case 0x8b60: // SAMPLER_CUBE
		case 0x8dcc: // INT_SAMPLER_CUBE
		case 0x8dd4: // UNSIGNED_INT_SAMPLER_CUBE
		case 0x8dc5: // SAMPLER_CUBE_SHADOW
			return setValueT6;

		case 0x8dc1: // SAMPLER_2D_ARRAY
		case 0x8dcf: // INT_SAMPLER_2D_ARRAY
		case 0x8dd7: // UNSIGNED_INT_SAMPLER_2D_ARRAY
		case 0x8dc4: // SAMPLER_2D_ARRAY_SHADOW
			return setValueT2DArray1;

	}

}


// Array of scalars

function setValueV1fArray( gl, v ) {

	gl.uniform1fv( this.addr, v );

}

// Array of vectors (from flat array or array of THREE.VectorN)

function setValueV2fArray( gl, v ) {

	const data = flatten( v, this.size, 2 );

	gl.uniform2fv( this.addr, data );

}

function setValueV3fArray( gl, v ) {

	const data = flatten( v, this.size, 3 );

	gl.uniform3fv( this.addr, data );

}

function setValueV4fArray( gl, v ) {

	const data = flatten( v, this.size, 4 );

	gl.uniform4fv( this.addr, data );

}

// Array of matrices (from flat array or array of THREE.MatrixN)

function setValueM2Array( gl, v ) {

	const data = flatten( v, this.size, 4 );

	gl.uniformMatrix2fv( this.addr, false, data );

}

function setValueM3Array( gl, v ) {

	const data = flatten( v, this.size, 9 );

	gl.uniformMatrix3fv( this.addr, false, data );

}

function setValueM4Array( gl, v ) {

	const data = flatten( v, this.size, 16 );

	gl.uniformMatrix4fv( this.addr, false, data );

}

// Array of integer / boolean

function setValueV1iArray( gl, v ) {

	gl.uniform1iv( this.addr, v );

}

// Array of integer / boolean vectors (from flat array)

function setValueV2iArray( gl, v ) {

	gl.uniform2iv( this.addr, v );

}

function setValueV3iArray( gl, v ) {

	gl.uniform3iv( this.addr, v );

}

function setValueV4iArray( gl, v ) {

	gl.uniform4iv( this.addr, v );

}

// Array of unsigned integer

function setValueV1uiArray( gl, v ) {

	gl.uniform1uiv( this.addr, v );

}

// Array of unsigned integer vectors (from flat array)

function setValueV2uiArray( gl, v ) {

	gl.uniform2uiv( this.addr, v );

}

function setValueV3uiArray( gl, v ) {

	gl.uniform3uiv( this.addr, v );

}

function setValueV4uiArray( gl, v ) {

	gl.uniform4uiv( this.addr, v );

}


// Array of textures (2D / 3D / Cube / 2DArray)

function setValueT1Array( gl, v, textures ) {

	const cache = this.cache;

	const n = v.length;

	const units = allocTexUnits( textures, n );

	if ( ! arraysEqual( cache, units ) ) {

		gl.uniform1iv( this.addr, units );

		copyArray( cache, units );

	}

	for ( let i = 0; i !== n; ++ i ) {

		textures.setTexture2D( v[ i ] || emptyTexture, units[ i ] );

	}

}

function setValueT3DArray( gl, v, textures ) {

	const cache = this.cache;

	const n = v.length;

	const units = allocTexUnits( textures, n );

	if ( ! arraysEqual( cache, units ) ) {

		gl.uniform1iv( this.addr, units );

		copyArray( cache, units );

	}

	for ( let i = 0; i !== n; ++ i ) {

		textures.setTexture3D( v[ i ] || empty3dTexture, units[ i ] );

	}

}

function setValueT6Array( gl, v, textures ) {

	const cache = this.cache;

	const n = v.length;

	const units = allocTexUnits( textures, n );

	if ( ! arraysEqual( cache, units ) ) {

		gl.uniform1iv( this.addr, units );

		copyArray( cache, units );

	}

	for ( let i = 0; i !== n; ++ i ) {

		textures.setTextureCube( v[ i ] || emptyCubeTexture, units[ i ] );

	}

}

function setValueT2DArrayArray( gl, v, textures ) {

	const cache = this.cache;

	const n = v.length;

	const units = allocTexUnits( textures, n );

	if ( ! arraysEqual( cache, units ) ) {

		gl.uniform1iv( this.addr, units );

		copyArray( cache, units );

	}

	for ( let i = 0; i !== n; ++ i ) {

		textures.setTexture2DArray( v[ i ] || emptyArrayTexture, units[ i ] );

	}

}


// Helper to pick the right setter for a pure (bottom-level) array

function getPureArraySetter( type ) {

	switch ( type ) {

		case 0x1406: return setValueV1fArray; // FLOAT
		case 0x8b50: return setValueV2fArray; // _VEC2
		case 0x8b51: return setValueV3fArray; // _VEC3
		case 0x8b52: return setValueV4fArray; // _VEC4

		case 0x8b5a: return setValueM2Array; // _MAT2
		case 0x8b5b: return setValueM3Array; // _MAT3
		case 0x8b5c: return setValueM4Array; // _MAT4

		case 0x1404: case 0x8b56: return setValueV1iArray; // INT, BOOL
		case 0x8b53: case 0x8b57: return setValueV2iArray; // _VEC2
		case 0x8b54: case 0x8b58: return setValueV3iArray; // _VEC3
		case 0x8b55: case 0x8b59: return setValueV4iArray; // _VEC4

		case 0x1405: return setValueV1uiArray; // UINT
		case 0x8dc6: return setValueV2uiArray; // _VEC2
		case 0x8dc7: return setValueV3uiArray; // _VEC3
		case 0x8dc8: return setValueV4uiArray; // _VEC4

		case 0x8b5e: // SAMPLER_2D
		case 0x8d66: // SAMPLER_EXTERNAL_OES
		case 0x8dca: // INT_SAMPLER_2D
		case 0x8dd2: // UNSIGNED_INT_SAMPLER_2D
		case 0x8b62: // SAMPLER_2D_SHADOW
			return setValueT1Array;

		case 0x8b5f: // SAMPLER_3D
		case 0x8dcb: // INT_SAMPLER_3D
		case 0x8dd3: // UNSIGNED_INT_SAMPLER_3D
			return setValueT3DArray;

		case 0x8b60: // SAMPLER_CUBE
		case 0x8dcc: // INT_SAMPLER_CUBE
		case 0x8dd4: // UNSIGNED_INT_SAMPLER_CUBE
		case 0x8dc5: // SAMPLER_CUBE_SHADOW
			return setValueT6Array;

		case 0x8dc1: // SAMPLER_2D_ARRAY
		case 0x8dcf: // INT_SAMPLER_2D_ARRAY
		case 0x8dd7: // UNSIGNED_INT_SAMPLER_2D_ARRAY
		case 0x8dc4: // SAMPLER_2D_ARRAY_SHADOW
			return setValueT2DArrayArray;

	}

}

// --- Uniform Classes ---

class SingleUniform {

	constructor( id, activeInfo, addr ) {

		this.id = id;
		this.addr = addr;
		this.cache = [];
		this.type = activeInfo.type;
		this.setValue = getSingularSetter( activeInfo.type );

		// this.path = activeInfo.name; // DEBUG

	}

}

class PureArrayUniform {

	constructor( id, activeInfo, addr ) {

		this.id = id;
		this.addr = addr;
		this.cache = [];
		this.type = activeInfo.type;
		this.size = activeInfo.size;
		this.setValue = getPureArraySetter( activeInfo.type );

		// this.path = activeInfo.name; // DEBUG

	}

}

class StructuredUniform {

	constructor( id ) {

		this.id = id;

		this.seq = [];
		this.map = {};

	}

	setValue( gl, value, textures ) {

		const seq = this.seq;

		for ( let i = 0, n = seq.length; i !== n; ++ i ) {

			const u = seq[ i ];
			u.setValue( gl, value[ u.id ], textures );

		}

	}

}

// --- Top-level ---

// Parser - builds up the property tree from the path strings

const RePathPart = /(\w+)(\])?(\[|\.)?/g;

// extracts
// 	- the identifier (member name or array index)
//  - followed by an optional right bracket (found when array index)
//  - followed by an optional left bracket or dot (type of subscript)
//
// Note: These portions can be read in a non-overlapping fashion and
// allow straightforward parsing of the hierarchy that WebGL encodes
// in the uniform names.

function addUniform( container, uniformObject ) {

	container.seq.push( uniformObject );
	container.map[ uniformObject.id ] = uniformObject;

}

function parseUniform( activeInfo, addr, container ) {

	const path = activeInfo.name,
		pathLength = path.length;

	// reset RegExp object, because of the early exit of a previous run
	RePathPart.lastIndex = 0;

	while ( true ) {

		const match = RePathPart.exec( path ),
			matchEnd = RePathPart.lastIndex;

		let id = match[ 1 ];
		const idIsIndex = match[ 2 ] === ']',
			subscript = match[ 3 ];

		if ( idIsIndex ) id = id | 0; // convert to integer

		if ( subscript === undefined || subscript === '[' && matchEnd + 2 === pathLength ) {

			// bare name or "pure" bottom-level array "[0]" suffix

			addUniform( container, subscript === undefined ?
				new SingleUniform( id, activeInfo, addr ) :
				new PureArrayUniform( id, activeInfo, addr ) );

			break;

		} else {

			// step into inner node / create it in case it doesn't exist

			const map = container.map;
			let next = map[ id ];

			if ( next === undefined ) {

				next = new StructuredUniform( id );
				addUniform( container, next );

			}

			container = next;

		}

	}

}

// Root Container

class WebGLUniforms {

	constructor( gl, program ) {

		this.seq = [];
		this.map = {};

		const n = gl.getProgramParameter( program, gl.ACTIVE_UNIFORMS );

		for ( let i = 0; i < n; ++ i ) {

			const info = gl.getActiveUniform( program, i ),
				addr = gl.getUniformLocation( program, info.name );

			parseUniform( info, addr, this );

		}

	}

	setValue( gl, name, value, textures ) {

		const u = this.map[ name ];

		if ( u !== undefined ) u.setValue( gl, value, textures );

	}

	setOptional( gl, object, name ) {

		const v = object[ name ];

		if ( v !== undefined ) this.setValue( gl, name, v );

	}

	static upload( gl, seq, values, textures ) {

		for ( let i = 0, n = seq.length; i !== n; ++ i ) {

			const u = seq[ i ],
				v = values[ u.id ];

			if ( v.needsUpdate !== false ) {

				// note: always updating when .needsUpdate is undefined
				u.setValue( gl, v.value, textures );

			}

		}

	}

	static seqWithValue( seq, values ) {

		const r = [];

		for ( let i = 0, n = seq.length; i !== n; ++ i ) {

			const u = seq[ i ];
			if ( u.id in values ) r.push( u );

		}

		return r;

	}

}

function WebGLShader( gl, type, string ) {

	const shader = gl.createShader( type );

	gl.shaderSource( shader, string );
	gl.compileShader( shader );

	return shader;

}

// From https://www.khronos.org/registry/webgl/extensions/KHR_parallel_shader_compile/
const COMPLETION_STATUS_KHR = 0x91B1;

let programIdCount = 0;

function handleSource( string, errorLine ) {

	const lines = string.split( '\n' );
	const lines2 = [];

	const from = Math.max( errorLine - 6, 0 );
	const to = Math.min( errorLine + 6, lines.length );

	for ( let i = from; i < to; i ++ ) {

		const line = i + 1;
		lines2.push( `${line === errorLine ? '>' : ' '} ${line}: ${lines[ i ]}` );

	}

	return lines2.join( '\n' );

}

function getEncodingComponents( colorSpace ) {

	const workingPrimaries = ColorManagement.getPrimaries( ColorManagement.workingColorSpace );
	const encodingPrimaries = ColorManagement.getPrimaries( colorSpace );

	let gamutMapping;

	if ( workingPrimaries === encodingPrimaries ) {

		gamutMapping = '';

	} else if ( workingPrimaries === P3Primaries && encodingPrimaries === Rec709Primaries ) {

		gamutMapping = 'LinearDisplayP3ToLinearSRGB';

	} else if ( workingPrimaries === Rec709Primaries && encodingPrimaries === P3Primaries ) {

		gamutMapping = 'LinearSRGBToLinearDisplayP3';

	}

	switch ( colorSpace ) {

		case LinearSRGBColorSpace:
		case LinearDisplayP3ColorSpace:
			return [ gamutMapping, 'LinearTransferOETF' ];

		case SRGBColorSpace:
		case DisplayP3ColorSpace:
			return [ gamutMapping, 'sRGBTransferOETF' ];

		default:
			console.warn( 'THREE.WebGLProgram: Unsupported color space:', colorSpace );
			return [ gamutMapping, 'LinearTransferOETF' ];

	}

}

function getShaderErrors( gl, shader, type ) {

	const status = gl.getShaderParameter( shader, gl.COMPILE_STATUS );
	const errors = gl.getShaderInfoLog( shader ).trim();

	if ( status && errors === '' ) return '';

	const errorMatches = /ERROR: 0:(\d+)/.exec( errors );
	if ( errorMatches ) {

		// --enable-privileged-webgl-extension
		// console.log( '**' + type + '**', gl.getExtension( 'WEBGL_debug_shaders' ).getTranslatedShaderSource( shader ) );

		const errorLine = parseInt( errorMatches[ 1 ] );
		return type.toUpperCase() + '\n\n' + errors + '\n\n' + handleSource( gl.getShaderSource( shader ), errorLine );

	} else {

		return errors;

	}

}

function getTexelEncodingFunction( functionName, colorSpace ) {

	const components = getEncodingComponents( colorSpace );
	return `vec4 ${functionName}( vec4 value ) { return ${components[ 0 ]}( ${components[ 1 ]}( value ) ); }`;

}

function getToneMappingFunction( functionName, toneMapping ) {

	let toneMappingName;

	switch ( toneMapping ) {

		case LinearToneMapping:
			toneMappingName = 'Linear';
			break;

		case ReinhardToneMapping:
			toneMappingName = 'Reinhard';
			break;

		case CineonToneMapping:
			toneMappingName = 'Cineon';
			break;

		case ACESFilmicToneMapping:
			toneMappingName = 'ACESFilmic';
			break;

		case AgXToneMapping:
			toneMappingName = 'AgX';
			break;

		case NeutralToneMapping:
			toneMappingName = 'Neutral';
			break;

		case CustomToneMapping:
			toneMappingName = 'Custom';
			break;

		default:
			console.warn( 'THREE.WebGLProgram: Unsupported toneMapping:', toneMapping );
			toneMappingName = 'Linear';

	}

	return 'vec3 ' + functionName + '( vec3 color ) { return ' + toneMappingName + 'ToneMapping( color ); }';

}

const _v0$1 = /*@__PURE__*/ new Vector3();

function getLuminanceFunction() {

	ColorManagement.getLuminanceCoefficients( _v0$1 );

	const r = _v0$1.x.toFixed( 4 );
	const g = _v0$1.y.toFixed( 4 );
	const b = _v0$1.z.toFixed( 4 );

	return [

		'float luminance( const in vec3 rgb ) {',

		`	const vec3 weights = vec3( ${ r }, ${ g }, ${ b } );`,

		'	return dot( weights, rgb );',

		'}'

	].join( '\n' );

}

function generateVertexExtensions( parameters ) {

	const chunks = [
		parameters.extensionClipCullDistance ? '#extension GL_ANGLE_clip_cull_distance : require' : '',
		parameters.extensionMultiDraw ? '#extension GL_ANGLE_multi_draw : require' : '',
	];

	return chunks.filter( filterEmptyLine ).join( '\n' );

}

function generateDefines( defines ) {

	const chunks = [];

	for ( const name in defines ) {

		const value = defines[ name ];

		if ( value === false ) continue;

		chunks.push( '#define ' + name + ' ' + value );

	}

	return chunks.join( '\n' );

}

function fetchAttributeLocations( gl, program ) {

	const attributes = {};

	const n = gl.getProgramParameter( program, gl.ACTIVE_ATTRIBUTES );

	for ( let i = 0; i < n; i ++ ) {

		const info = gl.getActiveAttrib( program, i );
		const name = info.name;

		let locationSize = 1;
		if ( info.type === gl.FLOAT_MAT2 ) locationSize = 2;
		if ( info.type === gl.FLOAT_MAT3 ) locationSize = 3;
		if ( info.type === gl.FLOAT_MAT4 ) locationSize = 4;

		// console.log( 'THREE.WebGLProgram: ACTIVE VERTEX ATTRIBUTE:', name, i );

		attributes[ name ] = {
			type: info.type,
			location: gl.getAttribLocation( program, name ),
			locationSize: locationSize
		};

	}

	return attributes;

}

function filterEmptyLine( string ) {

	return string !== '';

}

function replaceLightNums( string, parameters ) {

	const numSpotLightCoords = parameters.numSpotLightShadows + parameters.numSpotLightMaps - parameters.numSpotLightShadowsWithMaps;

	return string
		.replace( /NUM_DIR_LIGHTS/g, parameters.numDirLights )
		.replace( /NUM_SPOT_LIGHTS/g, parameters.numSpotLights )
		.replace( /NUM_SPOT_LIGHT_MAPS/g, parameters.numSpotLightMaps )
		.replace( /NUM_SPOT_LIGHT_COORDS/g, numSpotLightCoords )
		.replace( /NUM_RECT_AREA_LIGHTS/g, parameters.numRectAreaLights )
		.replace( /NUM_POINT_LIGHTS/g, parameters.numPointLights )
		.replace( /NUM_HEMI_LIGHTS/g, parameters.numHemiLights )
		.replace( /NUM_DIR_LIGHT_SHADOWS/g, parameters.numDirLightShadows )
		.replace( /NUM_SPOT_LIGHT_SHADOWS_WITH_MAPS/g, parameters.numSpotLightShadowsWithMaps )
		.replace( /NUM_SPOT_LIGHT_SHADOWS/g, parameters.numSpotLightShadows )
		.replace( /NUM_POINT_LIGHT_SHADOWS/g, parameters.numPointLightShadows );

}

function replaceClippingPlaneNums( string, parameters ) {

	return string
		.replace( /NUM_CLIPPING_PLANES/g, parameters.numClippingPlanes )
		.replace( /UNION_CLIPPING_PLANES/g, ( parameters.numClippingPlanes - parameters.numClipIntersection ) );

}

// Resolve Includes

const includePattern = /^[ \t]*#include +<([\w\d./]+)>/gm;

function resolveIncludes( string ) {

	return string.replace( includePattern, includeReplacer );

}

const shaderChunkMap = new Map();

function includeReplacer( match, include ) {

	let string = ShaderChunk[ include ];

	if ( string === undefined ) {

		const newInclude = shaderChunkMap.get( include );

		if ( newInclude !== undefined ) {

			string = ShaderChunk[ newInclude ];
			console.warn( 'THREE.WebGLRenderer: Shader chunk "%s" has been deprecated. Use "%s" instead.', include, newInclude );

		} else {

			throw new Error( 'Can not resolve #include <' + include + '>' );

		}

	}

	return resolveIncludes( string );

}

// Unroll Loops

const unrollLoopPattern = /#pragma unroll_loop_start\s+for\s*\(\s*int\s+i\s*=\s*(\d+)\s*;\s*i\s*<\s*(\d+)\s*;\s*i\s*\+\+\s*\)\s*{([\s\S]+?)}\s+#pragma unroll_loop_end/g;

function unrollLoops( string ) {

	return string.replace( unrollLoopPattern, loopReplacer );

}

function loopReplacer( match, start, end, snippet ) {

	let string = '';

	for ( let i = parseInt( start ); i < parseInt( end ); i ++ ) {

		string += snippet
			.replace( /\[\s*i\s*\]/g, '[ ' + i + ' ]' )
			.replace( /UNROLLED_LOOP_INDEX/g, i );

	}

	return string;

}

//

function generatePrecision( parameters ) {

	let precisionstring = `precision ${parameters.precision} float;
	precision ${parameters.precision} int;
	precision ${parameters.precision} sampler2D;
	precision ${parameters.precision} samplerCube;
	precision ${parameters.precision} sampler3D;
	precision ${parameters.precision} sampler2DArray;
	precision ${parameters.precision} sampler2DShadow;
	precision ${parameters.precision} samplerCubeShadow;
	precision ${parameters.precision} sampler2DArrayShadow;
	precision ${parameters.precision} isampler2D;
	precision ${parameters.precision} isampler3D;
	precision ${parameters.precision} isamplerCube;
	precision ${parameters.precision} isampler2DArray;
	precision ${parameters.precision} usampler2D;
	precision ${parameters.precision} usampler3D;
	precision ${parameters.precision} usamplerCube;
	precision ${parameters.precision} usampler2DArray;
	`;

	if ( parameters.precision === 'highp' ) {

		precisionstring += '\n#define HIGH_PRECISION';

	} else if ( parameters.precision === 'mediump' ) {

		precisionstring += '\n#define MEDIUM_PRECISION';

	} else if ( parameters.precision === 'lowp' ) {

		precisionstring += '\n#define LOW_PRECISION';

	}

	return precisionstring;

}

function generateShadowMapTypeDefine( parameters ) {

	let shadowMapTypeDefine = 'SHADOWMAP_TYPE_BASIC';

	if ( parameters.shadowMapType === PCFShadowMap ) {

		shadowMapTypeDefine = 'SHADOWMAP_TYPE_PCF';

	} else if ( parameters.shadowMapType === PCFSoftShadowMap ) {

		shadowMapTypeDefine = 'SHADOWMAP_TYPE_PCF_SOFT';

	} else if ( parameters.shadowMapType === VSMShadowMap ) {

		shadowMapTypeDefine = 'SHADOWMAP_TYPE_VSM';

	}

	return shadowMapTypeDefine;

}

function generateEnvMapTypeDefine( parameters ) {

	let envMapTypeDefine = 'ENVMAP_TYPE_CUBE';

	if ( parameters.envMap ) {

		switch ( parameters.envMapMode ) {

			case CubeReflectionMapping:
			case CubeRefractionMapping:
				envMapTypeDefine = 'ENVMAP_TYPE_CUBE';
				break;

			case CubeUVReflectionMapping:
				envMapTypeDefine = 'ENVMAP_TYPE_CUBE_UV';
				break;

		}

	}

	return envMapTypeDefine;

}

function generateEnvMapModeDefine( parameters ) {

	let envMapModeDefine = 'ENVMAP_MODE_REFLECTION';

	if ( parameters.envMap ) {

		switch ( parameters.envMapMode ) {

			case CubeRefractionMapping:

				envMapModeDefine = 'ENVMAP_MODE_REFRACTION';
				break;

		}

	}

	return envMapModeDefine;

}

function generateEnvMapBlendingDefine( parameters ) {

	let envMapBlendingDefine = 'ENVMAP_BLENDING_NONE';

	if ( parameters.envMap ) {

		switch ( parameters.combine ) {

			case MultiplyOperation:
				envMapBlendingDefine = 'ENVMAP_BLENDING_MULTIPLY';
				break;

			case MixOperation:
				envMapBlendingDefine = 'ENVMAP_BLENDING_MIX';
				break;

			case AddOperation:
				envMapBlendingDefine = 'ENVMAP_BLENDING_ADD';
				break;

		}

	}

	return envMapBlendingDefine;

}

function generateCubeUVSize( parameters ) {

	const imageHeight = parameters.envMapCubeUVHeight;

	if ( imageHeight === null ) return null;

	const maxMip = Math.log2( imageHeight ) - 2;

	const texelHeight = 1.0 / imageHeight;

	const texelWidth = 1.0 / ( 3 * Math.max( Math.pow( 2, maxMip ), 7 * 16 ) );

	return { texelWidth, texelHeight, maxMip };

}

function WebGLProgram( renderer, cacheKey, parameters, bindingStates ) {

	// TODO Send this event to Three.js DevTools
	// console.log( 'WebGLProgram', cacheKey );

	const gl = renderer.getContext();

	const defines = parameters.defines;

	let vertexShader = parameters.vertexShader;
	let fragmentShader = parameters.fragmentShader;

	const shadowMapTypeDefine = generateShadowMapTypeDefine( parameters );
	const envMapTypeDefine = generateEnvMapTypeDefine( parameters );
	const envMapModeDefine = generateEnvMapModeDefine( parameters );
	const envMapBlendingDefine = generateEnvMapBlendingDefine( parameters );
	const envMapCubeUVSize = generateCubeUVSize( parameters );

	const customVertexExtensions = generateVertexExtensions( parameters );

	const customDefines = generateDefines( defines );

	const program = gl.createProgram();

	let prefixVertex, prefixFragment;
	let versionString = parameters.glslVersion ? '#version ' + parameters.glslVersion + '\n' : '';

	if ( parameters.isRawShaderMaterial ) {

		prefixVertex = [

			'#define SHADER_TYPE ' + parameters.shaderType,
			'#define SHADER_NAME ' + parameters.shaderName,

			customDefines

		].filter( filterEmptyLine ).join( '\n' );

		if ( prefixVertex.length > 0 ) {

			prefixVertex += '\n';

		}

		prefixFragment = [

			'#define SHADER_TYPE ' + parameters.shaderType,
			'#define SHADER_NAME ' + parameters.shaderName,

			customDefines

		].filter( filterEmptyLine ).join( '\n' );

		if ( prefixFragment.length > 0 ) {

			prefixFragment += '\n';

		}

	} else {

		prefixVertex = [

			generatePrecision( parameters ),

			'#define SHADER_TYPE ' + parameters.shaderType,
			'#define SHADER_NAME ' + parameters.shaderName,

			customDefines,

			parameters.extensionClipCullDistance ? '#define USE_CLIP_DISTANCE' : '',
			parameters.batching ? '#define USE_BATCHING' : '',
			parameters.batchingColor ? '#define USE_BATCHING_COLOR' : '',
			parameters.instancing ? '#define USE_INSTANCING' : '',
			parameters.instancingColor ? '#define USE_INSTANCING_COLOR' : '',
			parameters.instancingMorph ? '#define USE_INSTANCING_MORPH' : '',

			parameters.useFog && parameters.fog ? '#define USE_FOG' : '',
			parameters.useFog && parameters.fogExp2 ? '#define FOG_EXP2' : '',

			parameters.map ? '#define USE_MAP' : '',
			parameters.envMap ? '#define USE_ENVMAP' : '',
			parameters.envMap ? '#define ' + envMapModeDefine : '',
			parameters.lightMap ? '#define USE_LIGHTMAP' : '',
			parameters.aoMap ? '#define USE_AOMAP' : '',
			parameters.bumpMap ? '#define USE_BUMPMAP' : '',
			parameters.normalMap ? '#define USE_NORMALMAP' : '',
			parameters.normalMapObjectSpace ? '#define USE_NORMALMAP_OBJECTSPACE' : '',
			parameters.normalMapTangentSpace ? '#define USE_NORMALMAP_TANGENTSPACE' : '',
			parameters.displacementMap ? '#define USE_DISPLACEMENTMAP' : '',
			parameters.emissiveMap ? '#define USE_EMISSIVEMAP' : '',

			parameters.anisotropy ? '#define USE_ANISOTROPY' : '',
			parameters.anisotropyMap ? '#define USE_ANISOTROPYMAP' : '',

			parameters.clearcoatMap ? '#define USE_CLEARCOATMAP' : '',
			parameters.clearcoatRoughnessMap ? '#define USE_CLEARCOAT_ROUGHNESSMAP' : '',
			parameters.clearcoatNormalMap ? '#define USE_CLEARCOAT_NORMALMAP' : '',

			parameters.iridescenceMap ? '#define USE_IRIDESCENCEMAP' : '',
			parameters.iridescenceThicknessMap ? '#define USE_IRIDESCENCE_THICKNESSMAP' : '',

			parameters.specularMap ? '#define USE_SPECULARMAP' : '',
			parameters.specularColorMap ? '#define USE_SPECULAR_COLORMAP' : '',
			parameters.specularIntensityMap ? '#define USE_SPECULAR_INTENSITYMAP' : '',

			parameters.roughnessMap ? '#define USE_ROUGHNESSMAP' : '',
			parameters.metalnessMap ? '#define USE_METALNESSMAP' : '',
			parameters.alphaMap ? '#define USE_ALPHAMAP' : '',
			parameters.alphaHash ? '#define USE_ALPHAHASH' : '',

			parameters.transmission ? '#define USE_TRANSMISSION' : '',
			parameters.transmissionMap ? '#define USE_TRANSMISSIONMAP' : '',
			parameters.thicknessMap ? '#define USE_THICKNESSMAP' : '',

			parameters.sheenColorMap ? '#define USE_SHEEN_COLORMAP' : '',
			parameters.sheenRoughnessMap ? '#define USE_SHEEN_ROUGHNESSMAP' : '',

			//

			parameters.mapUv ? '#define MAP_UV ' + parameters.mapUv : '',
			parameters.alphaMapUv ? '#define ALPHAMAP_UV ' + parameters.alphaMapUv : '',
			parameters.lightMapUv ? '#define LIGHTMAP_UV ' + parameters.lightMapUv : '',
			parameters.aoMapUv ? '#define AOMAP_UV ' + parameters.aoMapUv : '',
			parameters.emissiveMapUv ? '#define EMISSIVEMAP_UV ' + parameters.emissiveMapUv : '',
			parameters.bumpMapUv ? '#define BUMPMAP_UV ' + parameters.bumpMapUv : '',
			parameters.normalMapUv ? '#define NORMALMAP_UV ' + parameters.normalMapUv : '',
			parameters.displacementMapUv ? '#define DISPLACEMENTMAP_UV ' + parameters.displacementMapUv : '',

			parameters.metalnessMapUv ? '#define METALNESSMAP_UV ' + parameters.metalnessMapUv : '',
			parameters.roughnessMapUv ? '#define ROUGHNESSMAP_UV ' + parameters.roughnessMapUv : '',

			parameters.anisotropyMapUv ? '#define ANISOTROPYMAP_UV ' + parameters.anisotropyMapUv : '',

			parameters.clearcoatMapUv ? '#define CLEARCOATMAP_UV ' + parameters.clearcoatMapUv : '',
			parameters.clearcoatNormalMapUv ? '#define CLEARCOAT_NORMALMAP_UV ' + parameters.clearcoatNormalMapUv : '',
			parameters.clearcoatRoughnessMapUv ? '#define CLEARCOAT_ROUGHNESSMAP_UV ' + parameters.clearcoatRoughnessMapUv : '',

			parameters.iridescenceMapUv ? '#define IRIDESCENCEMAP_UV ' + parameters.iridescenceMapUv : '',
			parameters.iridescenceThicknessMapUv ? '#define IRIDESCENCE_THICKNESSMAP_UV ' + parameters.iridescenceThicknessMapUv : '',

			parameters.sheenColorMapUv ? '#define SHEEN_COLORMAP_UV ' + parameters.sheenColorMapUv : '',
			parameters.sheenRoughnessMapUv ? '#define SHEEN_ROUGHNESSMAP_UV ' + parameters.sheenRoughnessMapUv : '',

			parameters.specularMapUv ? '#define SPECULARMAP_UV ' + parameters.specularMapUv : '',
			parameters.specularColorMapUv ? '#define SPECULAR_COLORMAP_UV ' + parameters.specularColorMapUv : '',
			parameters.specularIntensityMapUv ? '#define SPECULAR_INTENSITYMAP_UV ' + parameters.specularIntensityMapUv : '',

			parameters.transmissionMapUv ? '#define TRANSMISSIONMAP_UV ' + parameters.transmissionMapUv : '',
			parameters.thicknessMapUv ? '#define THICKNESSMAP_UV ' + parameters.thicknessMapUv : '',

			//

			parameters.vertexTangents && parameters.flatShading === false ? '#define USE_TANGENT' : '',
			parameters.vertexColors ? '#define USE_COLOR' : '',
			parameters.vertexAlphas ? '#define USE_COLOR_ALPHA' : '',
			parameters.vertexUv1s ? '#define USE_UV1' : '',
			parameters.vertexUv2s ? '#define USE_UV2' : '',
			parameters.vertexUv3s ? '#define USE_UV3' : '',

			parameters.pointsUvs ? '#define USE_POINTS_UV' : '',

			parameters.flatShading ? '#define FLAT_SHADED' : '',

			parameters.skinning ? '#define USE_SKINNING' : '',

			parameters.morphTargets ? '#define USE_MORPHTARGETS' : '',
			parameters.morphNormals && parameters.flatShading === false ? '#define USE_MORPHNORMALS' : '',
			( parameters.morphColors ) ? '#define USE_MORPHCOLORS' : '',
			( parameters.morphTargetsCount > 0 ) ? '#define MORPHTARGETS_TEXTURE_STRIDE ' + parameters.morphTextureStride : '',
			( parameters.morphTargetsCount > 0 ) ? '#define MORPHTARGETS_COUNT ' + parameters.morphTargetsCount : '',
			parameters.doubleSided ? '#define DOUBLE_SIDED' : '',
			parameters.flipSided ? '#define FLIP_SIDED' : '',

			parameters.shadowMapEnabled ? '#define USE_SHADOWMAP' : '',
			parameters.shadowMapEnabled ? '#define ' + shadowMapTypeDefine : '',

			parameters.sizeAttenuation ? '#define USE_SIZEATTENUATION' : '',

			parameters.numLightProbes > 0 ? '#define USE_LIGHT_PROBES' : '',

			parameters.logarithmicDepthBuffer ? '#define USE_LOGDEPTHBUF' : '',
			parameters.reverseDepthBuffer ? '#define USE_REVERSEDEPTHBUF' : '',

			'uniform mat4 modelMatrix;',
			'uniform mat4 modelViewMatrix;',
			'uniform mat4 projectionMatrix;',
			'uniform mat4 viewMatrix;',
			'uniform mat3 normalMatrix;',
			'uniform vec3 cameraPosition;',
			'uniform bool isOrthographic;',

			'#ifdef USE_INSTANCING',

			'	attribute mat4 instanceMatrix;',

			'#endif',

			'#ifdef USE_INSTANCING_COLOR',

			'	attribute vec3 instanceColor;',

			'#endif',

			'#ifdef USE_INSTANCING_MORPH',

			'	uniform sampler2D morphTexture;',

			'#endif',

			'attribute vec3 position;',
			'attribute vec3 normal;',
			'attribute vec2 uv;',

			'#ifdef USE_UV1',

			'	attribute vec2 uv1;',

			'#endif',

			'#ifdef USE_UV2',

			'	attribute vec2 uv2;',

			'#endif',

			'#ifdef USE_UV3',

			'	attribute vec2 uv3;',

			'#endif',

			'#ifdef USE_TANGENT',

			'	attribute vec4 tangent;',

			'#endif',

			'#if defined( USE_COLOR_ALPHA )',

			'	attribute vec4 color;',

			'#elif defined( USE_COLOR )',

			'	attribute vec3 color;',

			'#endif',

			'#ifdef USE_SKINNING',

			'	attribute vec4 skinIndex;',
			'	attribute vec4 skinWeight;',

			'#endif',

			'\n'

		].filter( filterEmptyLine ).join( '\n' );

		prefixFragment = [

			generatePrecision( parameters ),

			'#define SHADER_TYPE ' + parameters.shaderType,
			'#define SHADER_NAME ' + parameters.shaderName,

			customDefines,

			parameters.useFog && parameters.fog ? '#define USE_FOG' : '',
			parameters.useFog && parameters.fogExp2 ? '#define FOG_EXP2' : '',

			parameters.alphaToCoverage ? '#define ALPHA_TO_COVERAGE' : '',
			parameters.map ? '#define USE_MAP' : '',
			parameters.matcap ? '#define USE_MATCAP' : '',
			parameters.envMap ? '#define USE_ENVMAP' : '',
			parameters.envMap ? '#define ' + envMapTypeDefine : '',
			parameters.envMap ? '#define ' + envMapModeDefine : '',
			parameters.envMap ? '#define ' + envMapBlendingDefine : '',
			envMapCubeUVSize ? '#define CUBEUV_TEXEL_WIDTH ' + envMapCubeUVSize.texelWidth : '',
			envMapCubeUVSize ? '#define CUBEUV_TEXEL_HEIGHT ' + envMapCubeUVSize.texelHeight : '',
			envMapCubeUVSize ? '#define CUBEUV_MAX_MIP ' + envMapCubeUVSize.maxMip + '.0' : '',
			parameters.lightMap ? '#define USE_LIGHTMAP' : '',
			parameters.aoMap ? '#define USE_AOMAP' : '',
			parameters.bumpMap ? '#define USE_BUMPMAP' : '',
			parameters.normalMap ? '#define USE_NORMALMAP' : '',
			parameters.normalMapObjectSpace ? '#define USE_NORMALMAP_OBJECTSPACE' : '',
			parameters.normalMapTangentSpace ? '#define USE_NORMALMAP_TANGENTSPACE' : '',
			parameters.emissiveMap ? '#define USE_EMISSIVEMAP' : '',

			parameters.anisotropy ? '#define USE_ANISOTROPY' : '',
			parameters.anisotropyMap ? '#define USE_ANISOTROPYMAP' : '',

			parameters.clearcoat ? '#define USE_CLEARCOAT' : '',
			parameters.clearcoatMap ? '#define USE_CLEARCOATMAP' : '',
			parameters.clearcoatRoughnessMap ? '#define USE_CLEARCOAT_ROUGHNESSMAP' : '',
			parameters.clearcoatNormalMap ? '#define USE_CLEARCOAT_NORMALMAP' : '',

			parameters.dispersion ? '#define USE_DISPERSION' : '',

			parameters.iridescence ? '#define USE_IRIDESCENCE' : '',
			parameters.iridescenceMap ? '#define USE_IRIDESCENCEMAP' : '',
			parameters.iridescenceThicknessMap ? '#define USE_IRIDESCENCE_THICKNESSMAP' : '',

			parameters.specularMap ? '#define USE_SPECULARMAP' : '',
			parameters.specularColorMap ? '#define USE_SPECULAR_COLORMAP' : '',
			parameters.specularIntensityMap ? '#define USE_SPECULAR_INTENSITYMAP' : '',

			parameters.roughnessMap ? '#define USE_ROUGHNESSMAP' : '',
			parameters.metalnessMap ? '#define USE_METALNESSMAP' : '',

			parameters.alphaMap ? '#define USE_ALPHAMAP' : '',
			parameters.alphaTest ? '#define USE_ALPHATEST' : '',
			parameters.alphaHash ? '#define USE_ALPHAHASH' : '',

			parameters.sheen ? '#define USE_SHEEN' : '',
			parameters.sheenColorMap ? '#define USE_SHEEN_COLORMAP' : '',
			parameters.sheenRoughnessMap ? '#define USE_SHEEN_ROUGHNESSMAP' : '',

			parameters.transmission ? '#define USE_TRANSMISSION' : '',
			parameters.transmissionMap ? '#define USE_TRANSMISSIONMAP' : '',
			parameters.thicknessMap ? '#define USE_THICKNESSMAP' : '',

			parameters.vertexTangents && parameters.flatShading === false ? '#define USE_TANGENT' : '',
			parameters.vertexColors || parameters.instancingColor || parameters.batchingColor ? '#define USE_COLOR' : '',
			parameters.vertexAlphas ? '#define USE_COLOR_ALPHA' : '',
			parameters.vertexUv1s ? '#define USE_UV1' : '',
			parameters.vertexUv2s ? '#define USE_UV2' : '',
			parameters.vertexUv3s ? '#define USE_UV3' : '',

			parameters.pointsUvs ? '#define USE_POINTS_UV' : '',

			parameters.gradientMap ? '#define USE_GRADIENTMAP' : '',

			parameters.flatShading ? '#define FLAT_SHADED' : '',

			parameters.doubleSided ? '#define DOUBLE_SIDED' : '',
			parameters.flipSided ? '#define FLIP_SIDED' : '',

			parameters.shadowMapEnabled ? '#define USE_SHADOWMAP' : '',
			parameters.shadowMapEnabled ? '#define ' + shadowMapTypeDefine : '',

			parameters.premultipliedAlpha ? '#define PREMULTIPLIED_ALPHA' : '',

			parameters.numLightProbes > 0 ? '#define USE_LIGHT_PROBES' : '',

			parameters.decodeVideoTexture ? '#define DECODE_VIDEO_TEXTURE' : '',

			parameters.logarithmicDepthBuffer ? '#define USE_LOGDEPTHBUF' : '',
			parameters.reverseDepthBuffer ? '#define USE_REVERSEDEPTHBUF' : '',

			'uniform mat4 viewMatrix;',
			'uniform vec3 cameraPosition;',
			'uniform bool isOrthographic;',

			( parameters.toneMapping !== NoToneMapping ) ? '#define TONE_MAPPING' : '',
			( parameters.toneMapping !== NoToneMapping ) ? ShaderChunk[ 'tonemapping_pars_fragment' ] : '', // this code is required here because it is used by the toneMapping() function defined below
			( parameters.toneMapping !== NoToneMapping ) ? getToneMappingFunction( 'toneMapping', parameters.toneMapping ) : '',

			parameters.dithering ? '#define DITHERING' : '',
			parameters.opaque ? '#define OPAQUE' : '',

			ShaderChunk[ 'colorspace_pars_fragment' ], // this code is required here because it is used by the various encoding/decoding function defined below
			getTexelEncodingFunction( 'linearToOutputTexel', parameters.outputColorSpace ),
			getLuminanceFunction(),

			parameters.useDepthPacking ? '#define DEPTH_PACKING ' + parameters.depthPacking : '',

			'\n'

		].filter( filterEmptyLine ).join( '\n' );

	}

	vertexShader = resolveIncludes( vertexShader );
	vertexShader = replaceLightNums( vertexShader, parameters );
	vertexShader = replaceClippingPlaneNums( vertexShader, parameters );

	fragmentShader = resolveIncludes( fragmentShader );
	fragmentShader = replaceLightNums( fragmentShader, parameters );
	fragmentShader = replaceClippingPlaneNums( fragmentShader, parameters );

	vertexShader = unrollLoops( vertexShader );
	fragmentShader = unrollLoops( fragmentShader );

	if ( parameters.isRawShaderMaterial !== true ) {

		// GLSL 3.0 conversion for built-in materials and ShaderMaterial

		versionString = '#version 300 es\n';

		prefixVertex = [
			customVertexExtensions,
			'#define attribute in',
			'#define varying out',
			'#define texture2D texture'
		].join( '\n' ) + '\n' + prefixVertex;

		prefixFragment = [
			'#define varying in',
			( parameters.glslVersion === GLSL3 ) ? '' : 'layout(location = 0) out highp vec4 pc_fragColor;',
			( parameters.glslVersion === GLSL3 ) ? '' : '#define gl_FragColor pc_fragColor',
			'#define gl_FragDepthEXT gl_FragDepth',
			'#define texture2D texture',
			'#define textureCube texture',
			'#define texture2DProj textureProj',
			'#define texture2DLodEXT textureLod',
			'#define texture2DProjLodEXT textureProjLod',
			'#define textureCubeLodEXT textureLod',
			'#define texture2DGradEXT textureGrad',
			'#define texture2DProjGradEXT textureProjGrad',
			'#define textureCubeGradEXT textureGrad'
		].join( '\n' ) + '\n' + prefixFragment;

	}

	const vertexGlsl = versionString + prefixVertex + vertexShader;
	const fragmentGlsl = versionString + prefixFragment + fragmentShader;

	// console.log( '*VERTEX*', vertexGlsl );
	// console.log( '*FRAGMENT*', fragmentGlsl );

	const glVertexShader = WebGLShader( gl, gl.VERTEX_SHADER, vertexGlsl );
	const glFragmentShader = WebGLShader( gl, gl.FRAGMENT_SHADER, fragmentGlsl );

	gl.attachShader( program, glVertexShader );
	gl.attachShader( program, glFragmentShader );

	// Force a particular attribute to index 0.

	if ( parameters.index0AttributeName !== undefined ) {

		gl.bindAttribLocation( program, 0, parameters.index0AttributeName );

	} else if ( parameters.morphTargets === true ) {

		// programs with morphTargets displace position out of attribute 0
		gl.bindAttribLocation( program, 0, 'position' );

	}

	gl.linkProgram( program );

	function onFirstUse( self ) {

		// check for link errors
		if ( renderer.debug.checkShaderErrors ) {

			const programLog = gl.getProgramInfoLog( program ).trim();
			const vertexLog = gl.getShaderInfoLog( glVertexShader ).trim();
			const fragmentLog = gl.getShaderInfoLog( glFragmentShader ).trim();

			let runnable = true;
			let haveDiagnostics = true;

			if ( gl.getProgramParameter( program, gl.LINK_STATUS ) === false ) {

				runnable = false;

				if ( typeof renderer.debug.onShaderError === 'function' ) {

					renderer.debug.onShaderError( gl, program, glVertexShader, glFragmentShader );

				} else {

					// default error reporting

					const vertexErrors = getShaderErrors( gl, glVertexShader, 'vertex' );
					const fragmentErrors = getShaderErrors( gl, glFragmentShader, 'fragment' );

					console.error(
						'THREE.WebGLProgram: Shader Error ' + gl.getError() + ' - ' +
						'VALIDATE_STATUS ' + gl.getProgramParameter( program, gl.VALIDATE_STATUS ) + '\n\n' +
						'Material Name: ' + self.name + '\n' +
						'Material Type: ' + self.type + '\n\n' +
						'Program Info Log: ' + programLog + '\n' +
						vertexErrors + '\n' +
						fragmentErrors
					);

				}

			} else if ( programLog !== '' ) {

				console.warn( 'THREE.WebGLProgram: Program Info Log:', programLog );

			} else if ( vertexLog === '' || fragmentLog === '' ) {

				haveDiagnostics = false;

			}

			if ( haveDiagnostics ) {

				self.diagnostics = {

					runnable: runnable,

					programLog: programLog,

					vertexShader: {

						log: vertexLog,
						prefix: prefixVertex

					},

					fragmentShader: {

						log: fragmentLog,
						prefix: prefixFragment

					}

				};

			}

		}

		// Clean up

		// Crashes in iOS9 and iOS10. #18402
		// gl.detachShader( program, glVertexShader );
		// gl.detachShader( program, glFragmentShader );

		gl.deleteShader( glVertexShader );
		gl.deleteShader( glFragmentShader );

		cachedUniforms = new WebGLUniforms( gl, program );
		cachedAttributes = fetchAttributeLocations( gl, program );

	}

	// set up caching for uniform locations

	let cachedUniforms;

	this.getUniforms = function () {

		if ( cachedUniforms === undefined ) {

			// Populates cachedUniforms and cachedAttributes
			onFirstUse( this );

		}

		return cachedUniforms;

	};

	// set up caching for attribute locations

	let cachedAttributes;

	this.getAttributes = function () {

		if ( cachedAttributes === undefined ) {

			// Populates cachedAttributes and cachedUniforms
			onFirstUse( this );

		}

		return cachedAttributes;

	};

	// indicate when the program is ready to be used. if the KHR_parallel_shader_compile extension isn't supported,
	// flag the program as ready immediately. It may cause a stall when it's first used.

	let programReady = ( parameters.rendererExtensionParallelShaderCompile === false );

	this.isReady = function () {

		if ( programReady === false ) {

			programReady = gl.getProgramParameter( program, COMPLETION_STATUS_KHR );

		}

		return programReady;

	};

	// free resource

	this.destroy = function () {

		bindingStates.releaseStatesOfProgram( this );

		gl.deleteProgram( program );
		this.program = undefined;

	};

	//

	this.type = parameters.shaderType;
	this.name = parameters.shaderName;
	this.id = programIdCount ++;
	this.cacheKey = cacheKey;
	this.usedTimes = 1;
	this.program = program;
	this.vertexShader = glVertexShader;
	this.fragmentShader = glFragmentShader;

	return this;

}

let _id$1 = 0;

class WebGLShaderCache {

	constructor() {

		this.shaderCache = new Map();
		this.materialCache = new Map();

	}

	update( material ) {

		const vertexShader = material.vertexShader;
		const fragmentShader = material.fragmentShader;

		const vertexShaderStage = this._getShaderStage( vertexShader );
		const fragmentShaderStage = this._getShaderStage( fragmentShader );

		const materialShaders = this._getShaderCacheForMaterial( material );

		if ( materialShaders.has( vertexShaderStage ) === false ) {

			materialShaders.add( vertexShaderStage );
			vertexShaderStage.usedTimes ++;

		}

		if ( materialShaders.has( fragmentShaderStage ) === false ) {

			materialShaders.add( fragmentShaderStage );
			fragmentShaderStage.usedTimes ++;

		}

		return this;

	}

	remove( material ) {

		const materialShaders = this.materialCache.get( material );

		for ( const shaderStage of materialShaders ) {

			shaderStage.usedTimes --;

			if ( shaderStage.usedTimes === 0 ) this.shaderCache.delete( shaderStage.code );

		}

		this.materialCache.delete( material );

		return this;

	}

	getVertexShaderID( material ) {

		return this._getShaderStage( material.vertexShader ).id;

	}

	getFragmentShaderID( material ) {

		return this._getShaderStage( material.fragmentShader ).id;

	}

	dispose() {

		this.shaderCache.clear();
		this.materialCache.clear();

	}

	_getShaderCacheForMaterial( material ) {

		const cache = this.materialCache;
		let set = cache.get( material );

		if ( set === undefined ) {

			set = new Set();
			cache.set( material, set );

		}

		return set;

	}

	_getShaderStage( code ) {

		const cache = this.shaderCache;
		let stage = cache.get( code );

		if ( stage === undefined ) {

			stage = new WebGLShaderStage( code );
			cache.set( code, stage );

		}

		return stage;

	}

}

class WebGLShaderStage {

	constructor( code ) {

		this.id = _id$1 ++;

		this.code = code;
		this.usedTimes = 0;

	}

}

function WebGLPrograms( renderer, cubemaps, cubeuvmaps, extensions, capabilities, bindingStates, clipping ) {

	const _programLayers = new Layers();
	const _customShaders = new WebGLShaderCache();
	const _activeChannels = new Set();
	const programs = [];

	const logarithmicDepthBuffer = capabilities.logarithmicDepthBuffer;
	const reverseDepthBuffer = capabilities.reverseDepthBuffer;
	const SUPPORTS_VERTEX_TEXTURES = capabilities.vertexTextures;

	let precision = capabilities.precision;

	const shaderIDs = {
		MeshDepthMaterial: 'depth',
		MeshDistanceMaterial: 'distanceRGBA',
		MeshNormalMaterial: 'normal',
		MeshBasicMaterial: 'basic',
		MeshLambertMaterial: 'lambert',
		MeshPhongMaterial: 'phong',
		MeshToonMaterial: 'toon',
		MeshStandardMaterial: 'physical',
		MeshPhysicalMaterial: 'physical',
		MeshMatcapMaterial: 'matcap',
		LineBasicMaterial: 'basic',
		LineDashedMaterial: 'dashed',
		PointsMaterial: 'points',
		ShadowMaterial: 'shadow',
		SpriteMaterial: 'sprite'
	};

	function getChannel( value ) {

		_activeChannels.add( value );

		if ( value === 0 ) return 'uv';

		return `uv${ value }`;

	}

	function getParameters( material, lights, shadows, scene, object ) {

		const fog = scene.fog;
		const geometry = object.geometry;
		const environment = material.isMeshStandardMaterial ? scene.environment : null;

		const envMap = ( material.isMeshStandardMaterial ? cubeuvmaps : cubemaps ).get( material.envMap || environment );
		const envMapCubeUVHeight = ( !! envMap ) && ( envMap.mapping === CubeUVReflectionMapping ) ? envMap.image.height : null;

		const shaderID = shaderIDs[ material.type ];

		// heuristics to create shader parameters according to lights in the scene
		// (not to blow over maxLights budget)

		if ( material.precision !== null ) {

			precision = capabilities.getMaxPrecision( material.precision );

			if ( precision !== material.precision ) {

				console.warn( 'THREE.WebGLProgram.getParameters:', material.precision, 'not supported, using', precision, 'instead.' );

			}

		}

		//

		const morphAttribute = geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color;
		const morphTargetsCount = ( morphAttribute !== undefined ) ? morphAttribute.length : 0;

		let morphTextureStride = 0;

		if ( geometry.morphAttributes.position !== undefined ) morphTextureStride = 1;
		if ( geometry.morphAttributes.normal !== undefined ) morphTextureStride = 2;
		if ( geometry.morphAttributes.color !== undefined ) morphTextureStride = 3;

		//

		let vertexShader, fragmentShader;
		let customVertexShaderID, customFragmentShaderID;

		if ( shaderID ) {

			const shader = ShaderLib[ shaderID ];

			vertexShader = shader.vertexShader;
			fragmentShader = shader.fragmentShader;

		} else {

			vertexShader = material.vertexShader;
			fragmentShader = material.fragmentShader;

			_customShaders.update( material );

			customVertexShaderID = _customShaders.getVertexShaderID( material );
			customFragmentShaderID = _customShaders.getFragmentShaderID( material );

		}

		const currentRenderTarget = renderer.getRenderTarget();

		const IS_INSTANCEDMESH = object.isInstancedMesh === true;
		const IS_BATCHEDMESH = object.isBatchedMesh === true;

		const HAS_MAP = !! material.map;
		const HAS_MATCAP = !! material.matcap;
		const HAS_ENVMAP = !! envMap;
		const HAS_AOMAP = !! material.aoMap;
		const HAS_LIGHTMAP = !! material.lightMap;
		const HAS_BUMPMAP = !! material.bumpMap;
		const HAS_NORMALMAP = !! material.normalMap;
		const HAS_DISPLACEMENTMAP = !! material.displacementMap;
		const HAS_EMISSIVEMAP = !! material.emissiveMap;

		const HAS_METALNESSMAP = !! material.metalnessMap;
		const HAS_ROUGHNESSMAP = !! material.roughnessMap;

		const HAS_ANISOTROPY = material.anisotropy > 0;
		const HAS_CLEARCOAT = material.clearcoat > 0;
		const HAS_DISPERSION = material.dispersion > 0;
		const HAS_IRIDESCENCE = material.iridescence > 0;
		const HAS_SHEEN = material.sheen > 0;
		const HAS_TRANSMISSION = material.transmission > 0;

		const HAS_ANISOTROPYMAP = HAS_ANISOTROPY && !! material.anisotropyMap;

		const HAS_CLEARCOATMAP = HAS_CLEARCOAT && !! material.clearcoatMap;
		const HAS_CLEARCOAT_NORMALMAP = HAS_CLEARCOAT && !! material.clearcoatNormalMap;
		const HAS_CLEARCOAT_ROUGHNESSMAP = HAS_CLEARCOAT && !! material.clearcoatRoughnessMap;

		const HAS_IRIDESCENCEMAP = HAS_IRIDESCENCE && !! material.iridescenceMap;
		const HAS_IRIDESCENCE_THICKNESSMAP = HAS_IRIDESCENCE && !! material.iridescenceThicknessMap;

		const HAS_SHEEN_COLORMAP = HAS_SHEEN && !! material.sheenColorMap;
		const HAS_SHEEN_ROUGHNESSMAP = HAS_SHEEN && !! material.sheenRoughnessMap;

		const HAS_SPECULARMAP = !! material.specularMap;
		const HAS_SPECULAR_COLORMAP = !! material.specularColorMap;
		const HAS_SPECULAR_INTENSITYMAP = !! material.specularIntensityMap;

		const HAS_TRANSMISSIONMAP = HAS_TRANSMISSION && !! material.transmissionMap;
		const HAS_THICKNESSMAP = HAS_TRANSMISSION && !! material.thicknessMap;

		const HAS_GRADIENTMAP = !! material.gradientMap;

		const HAS_ALPHAMAP = !! material.alphaMap;

		const HAS_ALPHATEST = material.alphaTest > 0;

		const HAS_ALPHAHASH = !! material.alphaHash;

		const HAS_EXTENSIONS = !! material.extensions;

		let toneMapping = NoToneMapping;

		if ( material.toneMapped ) {

			if ( currentRenderTarget === null || currentRenderTarget.isXRRenderTarget === true ) {

				toneMapping = renderer.toneMapping;

			}

		}

		const parameters = {

			shaderID: shaderID,
			shaderType: material.type,
			shaderName: material.name,

			vertexShader: vertexShader,
			fragmentShader: fragmentShader,
			defines: material.defines,

			customVertexShaderID: customVertexShaderID,
			customFragmentShaderID: customFragmentShaderID,

			isRawShaderMaterial: material.isRawShaderMaterial === true,
			glslVersion: material.glslVersion,

			precision: precision,

			batching: IS_BATCHEDMESH,
			batchingColor: IS_BATCHEDMESH && object._colorsTexture !== null,
			instancing: IS_INSTANCEDMESH,
			instancingColor: IS_INSTANCEDMESH && object.instanceColor !== null,
			instancingMorph: IS_INSTANCEDMESH && object.morphTexture !== null,

			supportsVertexTextures: SUPPORTS_VERTEX_TEXTURES,
			outputColorSpace: ( currentRenderTarget === null ) ? renderer.outputColorSpace : ( currentRenderTarget.isXRRenderTarget === true ? currentRenderTarget.texture.colorSpace : LinearSRGBColorSpace ),
			alphaToCoverage: !! material.alphaToCoverage,

			map: HAS_MAP,
			matcap: HAS_MATCAP,
			envMap: HAS_ENVMAP,
			envMapMode: HAS_ENVMAP && envMap.mapping,
			envMapCubeUVHeight: envMapCubeUVHeight,
			aoMap: HAS_AOMAP,
			lightMap: HAS_LIGHTMAP,
			bumpMap: HAS_BUMPMAP,
			normalMap: HAS_NORMALMAP,
			displacementMap: SUPPORTS_VERTEX_TEXTURES && HAS_DISPLACEMENTMAP,
			emissiveMap: HAS_EMISSIVEMAP,

			normalMapObjectSpace: HAS_NORMALMAP && material.normalMapType === ObjectSpaceNormalMap,
			normalMapTangentSpace: HAS_NORMALMAP && material.normalMapType === TangentSpaceNormalMap,

			metalnessMap: HAS_METALNESSMAP,
			roughnessMap: HAS_ROUGHNESSMAP,

			anisotropy: HAS_ANISOTROPY,
			anisotropyMap: HAS_ANISOTROPYMAP,

			clearcoat: HAS_CLEARCOAT,
			clearcoatMap: HAS_CLEARCOATMAP,
			clearcoatNormalMap: HAS_CLEARCOAT_NORMALMAP,
			clearcoatRoughnessMap: HAS_CLEARCOAT_ROUGHNESSMAP,

			dispersion: HAS_DISPERSION,

			iridescence: HAS_IRIDESCENCE,
			iridescenceMap: HAS_IRIDESCENCEMAP,
			iridescenceThicknessMap: HAS_IRIDESCENCE_THICKNESSMAP,

			sheen: HAS_SHEEN,
			sheenColorMap: HAS_SHEEN_COLORMAP,
			sheenRoughnessMap: HAS_SHEEN_ROUGHNESSMAP,

			specularMap: HAS_SPECULARMAP,
			specularColorMap: HAS_SPECULAR_COLORMAP,
			specularIntensityMap: HAS_SPECULAR_INTENSITYMAP,

			transmission: HAS_TRANSMISSION,
			transmissionMap: HAS_TRANSMISSIONMAP,
			thicknessMap: HAS_THICKNESSMAP,

			gradientMap: HAS_GRADIENTMAP,

			opaque: material.transparent === false && material.blending === NormalBlending && material.alphaToCoverage === false,

			alphaMap: HAS_ALPHAMAP,
			alphaTest: HAS_ALPHATEST,
			alphaHash: HAS_ALPHAHASH,

			combine: material.combine,

			//

			mapUv: HAS_MAP && getChannel( material.map.channel ),
			aoMapUv: HAS_AOMAP && getChannel( material.aoMap.channel ),
			lightMapUv: HAS_LIGHTMAP && getChannel( material.lightMap.channel ),
			bumpMapUv: HAS_BUMPMAP && getChannel( material.bumpMap.channel ),
			normalMapUv: HAS_NORMALMAP && getChannel( material.normalMap.channel ),
			displacementMapUv: HAS_DISPLACEMENTMAP && getChannel( material.displacementMap.channel ),
			emissiveMapUv: HAS_EMISSIVEMAP && getChannel( material.emissiveMap.channel ),

			metalnessMapUv: HAS_METALNESSMAP && getChannel( material.metalnessMap.channel ),
			roughnessMapUv: HAS_ROUGHNESSMAP && getChannel( material.roughnessMap.channel ),

			anisotropyMapUv: HAS_ANISOTROPYMAP && getChannel( material.anisotropyMap.channel ),

			clearcoatMapUv: HAS_CLEARCOATMAP && getChannel( material.clearcoatMap.channel ),
			clearcoatNormalMapUv: HAS_CLEARCOAT_NORMALMAP && getChannel( material.clearcoatNormalMap.channel ),
			clearcoatRoughnessMapUv: HAS_CLEARCOAT_ROUGHNESSMAP && getChannel( material.clearcoatRoughnessMap.channel ),

			iridescenceMapUv: HAS_IRIDESCENCEMAP && getChannel( material.iridescenceMap.channel ),
			iridescenceThicknessMapUv: HAS_IRIDESCENCE_THICKNESSMAP && getChannel( material.iridescenceThicknessMap.channel ),

			sheenColorMapUv: HAS_SHEEN_COLORMAP && getChannel( material.sheenColorMap.channel ),
			sheenRoughnessMapUv: HAS_SHEEN_ROUGHNESSMAP && getChannel( material.sheenRoughnessMap.channel ),

			specularMapUv: HAS_SPECULARMAP && getChannel( material.specularMap.channel ),
			specularColorMapUv: HAS_SPECULAR_COLORMAP && getChannel( material.specularColorMap.channel ),
			specularIntensityMapUv: HAS_SPECULAR_INTENSITYMAP && getChannel( material.specularIntensityMap.channel ),

			transmissionMapUv: HAS_TRANSMISSIONMAP && getChannel( material.transmissionMap.channel ),
			thicknessMapUv: HAS_THICKNESSMAP && getChannel( material.thicknessMap.channel ),

			alphaMapUv: HAS_ALPHAMAP && getChannel( material.alphaMap.channel ),

			//

			vertexTangents: !! geometry.attributes.tangent && ( HAS_NORMALMAP || HAS_ANISOTROPY ),
			vertexColors: material.vertexColors,
			vertexAlphas: material.vertexColors === true && !! geometry.attributes.color && geometry.attributes.color.itemSize === 4,

			pointsUvs: object.isPoints === true && !! geometry.attributes.uv && ( HAS_MAP || HAS_ALPHAMAP ),

			fog: !! fog,
			useFog: material.fog === true,
			fogExp2: ( !! fog && fog.isFogExp2 ),

			flatShading: material.flatShading === true,

			sizeAttenuation: material.sizeAttenuation === true,
			logarithmicDepthBuffer: logarithmicDepthBuffer,
			reverseDepthBuffer: reverseDepthBuffer,

			skinning: object.isSkinnedMesh === true,

			morphTargets: geometry.morphAttributes.position !== undefined,
			morphNormals: geometry.morphAttributes.normal !== undefined,
			morphColors: geometry.morphAttributes.color !== undefined,
			morphTargetsCount: morphTargetsCount,
			morphTextureStride: morphTextureStride,

			numDirLights: lights.directional.length,
			numPointLights: lights.point.length,
			numSpotLights: lights.spot.length,
			numSpotLightMaps: lights.spotLightMap.length,
			numRectAreaLights: lights.rectArea.length,
			numHemiLights: lights.hemi.length,

			numDirLightShadows: lights.directionalShadowMap.length,
			numPointLightShadows: lights.pointShadowMap.length,
			numSpotLightShadows: lights.spotShadowMap.length,
			numSpotLightShadowsWithMaps: lights.numSpotLightShadowsWithMaps,

			numLightProbes: lights.numLightProbes,

			numClippingPlanes: clipping.numPlanes,
			numClipIntersection: clipping.numIntersection,

			dithering: material.dithering,

			shadowMapEnabled: renderer.shadowMap.enabled && shadows.length > 0,
			shadowMapType: renderer.shadowMap.type,

			toneMapping: toneMapping,

			decodeVideoTexture: HAS_MAP && ( material.map.isVideoTexture === true ) && ( ColorManagement.getTransfer( material.map.colorSpace ) === SRGBTransfer ),

			premultipliedAlpha: material.premultipliedAlpha,

			doubleSided: material.side === DoubleSide,
			flipSided: material.side === BackSide,

			useDepthPacking: material.depthPacking >= 0,
			depthPacking: material.depthPacking || 0,

			index0AttributeName: material.index0AttributeName,

			extensionClipCullDistance: HAS_EXTENSIONS && material.extensions.clipCullDistance === true && extensions.has( 'WEBGL_clip_cull_distance' ),
			extensionMultiDraw: ( HAS_EXTENSIONS && material.extensions.multiDraw === true || IS_BATCHEDMESH ) && extensions.has( 'WEBGL_multi_draw' ),

			rendererExtensionParallelShaderCompile: extensions.has( 'KHR_parallel_shader_compile' ),

			customProgramCacheKey: material.customProgramCacheKey()

		};

		// the usage of getChannel() determines the active texture channels for this shader

		parameters.vertexUv1s = _activeChannels.has( 1 );
		parameters.vertexUv2s = _activeChannels.has( 2 );
		parameters.vertexUv3s = _activeChannels.has( 3 );

		_activeChannels.clear();

		return parameters;

	}

	function getProgramCacheKey( parameters ) {

		const array = [];

		if ( parameters.shaderID ) {

			array.push( parameters.shaderID );

		} else {

			array.push( parameters.customVertexShaderID );
			array.push( parameters.customFragmentShaderID );

		}

		if ( parameters.defines !== undefined ) {

			for ( const name in parameters.defines ) {

				array.push( name );
				array.push( parameters.defines[ name ] );

			}

		}

		if ( parameters.isRawShaderMaterial === false ) {

			getProgramCacheKeyParameters( array, parameters );
			getProgramCacheKeyBooleans( array, parameters );
			array.push( renderer.outputColorSpace );

		}

		array.push( parameters.customProgramCacheKey );

		return array.join();

	}

	function getProgramCacheKeyParameters( array, parameters ) {

		array.push( parameters.precision );
		array.push( parameters.outputColorSpace );
		array.push( parameters.envMapMode );
		array.push( parameters.envMapCubeUVHeight );
		array.push( parameters.mapUv );
		array.push( parameters.alphaMapUv );
		array.push( parameters.lightMapUv );
		array.push( parameters.aoMapUv );
		array.push( parameters.bumpMapUv );
		array.push( parameters.normalMapUv );
		array.push( parameters.displacementMapUv );
		array.push( parameters.emissiveMapUv );
		array.push( parameters.metalnessMapUv );
		array.push( parameters.roughnessMapUv );
		array.push( parameters.anisotropyMapUv );
		array.push( parameters.clearcoatMapUv );
		array.push( parameters.clearcoatNormalMapUv );
		array.push( parameters.clearcoatRoughnessMapUv );
		array.push( parameters.iridescenceMapUv );
		array.push( parameters.iridescenceThicknessMapUv );
		array.push( parameters.sheenColorMapUv );
		array.push( parameters.sheenRoughnessMapUv );
		array.push( parameters.specularMapUv );
		array.push( parameters.specularColorMapUv );
		array.push( parameters.specularIntensityMapUv );
		array.push( parameters.transmissionMapUv );
		array.push( parameters.thicknessMapUv );
		array.push( parameters.combine );
		array.push( parameters.fogExp2 );
		array.push( parameters.sizeAttenuation );
		array.push( parameters.morphTargetsCount );
		array.push( parameters.morphAttributeCount );
		array.push( parameters.numDirLights );
		array.push( parameters.numPointLights );
		array.push( parameters.numSpotLights );
		array.push( parameters.numSpotLightMaps );
		array.push( parameters.numHemiLights );
		array.push( parameters.numRectAreaLights );
		array.push( parameters.numDirLightShadows );
		array.push( parameters.numPointLightShadows );
		array.push( parameters.numSpotLightShadows );
		array.push( parameters.numSpotLightShadowsWithMaps );
		array.push( parameters.numLightProbes );
		array.push( parameters.shadowMapType );
		array.push( parameters.toneMapping );
		array.push( parameters.numClippingPlanes );
		array.push( parameters.numClipIntersection );
		array.push( parameters.depthPacking );

	}

	function getProgramCacheKeyBooleans( array, parameters ) {

		_programLayers.disableAll();

		if ( parameters.supportsVertexTextures )
			_programLayers.enable( 0 );
		if ( parameters.instancing )
			_programLayers.enable( 1 );
		if ( parameters.instancingColor )
			_programLayers.enable( 2 );
		if ( parameters.instancingMorph )
			_programLayers.enable( 3 );
		if ( parameters.matcap )
			_programLayers.enable( 4 );
		if ( parameters.envMap )
			_programLayers.enable( 5 );
		if ( parameters.normalMapObjectSpace )
			_programLayers.enable( 6 );
		if ( parameters.normalMapTangentSpace )
			_programLayers.enable( 7 );
		if ( parameters.clearcoat )
			_programLayers.enable( 8 );
		if ( parameters.iridescence )
			_programLayers.enable( 9 );
		if ( parameters.alphaTest )
			_programLayers.enable( 10 );
		if ( parameters.vertexColors )
			_programLayers.enable( 11 );
		if ( parameters.vertexAlphas )
			_programLayers.enable( 12 );
		if ( parameters.vertexUv1s )
			_programLayers.enable( 13 );
		if ( parameters.vertexUv2s )
			_programLayers.enable( 14 );
		if ( parameters.vertexUv3s )
			_programLayers.enable( 15 );
		if ( parameters.vertexTangents )
			_programLayers.enable( 16 );
		if ( parameters.anisotropy )
			_programLayers.enable( 17 );
		if ( parameters.alphaHash )
			_programLayers.enable( 18 );
		if ( parameters.batching )
			_programLayers.enable( 19 );
		if ( parameters.dispersion )
			_programLayers.enable( 20 );
		if ( parameters.batchingColor )
			_programLayers.enable( 21 );

		array.push( _programLayers.mask );
		_programLayers.disableAll();

		if ( parameters.fog )
			_programLayers.enable( 0 );
		if ( parameters.useFog )
			_programLayers.enable( 1 );
		if ( parameters.flatShading )
			_programLayers.enable( 2 );
		if ( parameters.logarithmicDepthBuffer )
			_programLayers.enable( 3 );
		if ( parameters.reverseDepthBuffer )
			_programLayers.enable( 4 );
		if ( parameters.skinning )
			_programLayers.enable( 5 );
		if ( parameters.morphTargets )
			_programLayers.enable( 6 );
		if ( parameters.morphNormals )
			_programLayers.enable( 7 );
		if ( parameters.morphColors )
			_programLayers.enable( 8 );
		if ( parameters.premultipliedAlpha )
			_programLayers.enable( 9 );
		if ( parameters.shadowMapEnabled )
			_programLayers.enable( 10 );
		if ( parameters.doubleSided )
			_programLayers.enable( 11 );
		if ( parameters.flipSided )
			_programLayers.enable( 12 );
		if ( parameters.useDepthPacking )
			_programLayers.enable( 13 );
		if ( parameters.dithering )
			_programLayers.enable( 14 );
		if ( parameters.transmission )
			_programLayers.enable( 15 );
		if ( parameters.sheen )
			_programLayers.enable( 16 );
		if ( parameters.opaque )
			_programLayers.enable( 17 );
		if ( parameters.pointsUvs )
			_programLayers.enable( 18 );
		if ( parameters.decodeVideoTexture )
			_programLayers.enable( 19 );
		if ( parameters.alphaToCoverage )
			_programLayers.enable( 20 );

		array.push( _programLayers.mask );

	}

	function getUniforms( material ) {

		const shaderID = shaderIDs[ material.type ];
		let uniforms;

		if ( shaderID ) {

			const shader = ShaderLib[ shaderID ];
			uniforms = UniformsUtils.clone( shader.uniforms );

		} else {

			uniforms = material.uniforms;

		}

		return uniforms;

	}

	function acquireProgram( parameters, cacheKey ) {

		let program;

		// Check if code has been already compiled
		for ( let p = 0, pl = programs.length; p < pl; p ++ ) {

			const preexistingProgram = programs[ p ];

			if ( preexistingProgram.cacheKey === cacheKey ) {

				program = preexistingProgram;
				++ program.usedTimes;

				break;

			}

		}

		if ( program === undefined ) {

			program = new WebGLProgram( renderer, cacheKey, parameters, bindingStates );
			programs.push( program );

		}

		return program;

	}

	function releaseProgram( program ) {

		if ( -- program.usedTimes === 0 ) {

			// Remove from unordered set
			const i = programs.indexOf( program );
			programs[ i ] = programs[ programs.length - 1 ];
			programs.pop();

			// Free WebGL resources
			program.destroy();

		}

	}

	function releaseShaderCache( material ) {

		_customShaders.remove( material );

	}

	function dispose() {

		_customShaders.dispose();

	}

	return {
		getParameters: getParameters,
		getProgramCacheKey: getProgramCacheKey,
		getUniforms: getUniforms,
		acquireProgram: acquireProgram,
		releaseProgram: releaseProgram,
		releaseShaderCache: releaseShaderCache,
		// Exposed for resource monitoring & error feedback via renderer.info:
		programs: programs,
		dispose: dispose
	};

}

function WebGLProperties() {

	let properties = new WeakMap();

	function has( object ) {

		return properties.has( object );

	}

	function get( object ) {

		let map = properties.get( object );

		if ( map === undefined ) {

			map = {};
			properties.set( object, map );

		}

		return map;

	}

	function remove( object ) {

		properties.delete( object );

	}

	function update( object, key, value ) {

		properties.get( object )[ key ] = value;

	}

	function dispose() {

		properties = new WeakMap();

	}

	return {
		has: has,
		get: get,
		remove: remove,
		update: update,
		dispose: dispose
	};

}

function painterSortStable( a, b ) {

	if ( a.groupOrder !== b.groupOrder ) {

		return a.groupOrder - b.groupOrder;

	} else if ( a.renderOrder !== b.renderOrder ) {

		return a.renderOrder - b.renderOrder;

	} else if ( a.material.id !== b.material.id ) {

		return a.material.id - b.material.id;

	} else if ( a.z !== b.z ) {

		return a.z - b.z;

	} else {

		return a.id - b.id;

	}

}

function reversePainterSortStable( a, b ) {

	if ( a.groupOrder !== b.groupOrder ) {

		return a.groupOrder - b.groupOrder;

	} else if ( a.renderOrder !== b.renderOrder ) {

		return a.renderOrder - b.renderOrder;

	} else if ( a.z !== b.z ) {

		return b.z - a.z;

	} else {

		return a.id - b.id;

	}

}


function WebGLRenderList() {

	const renderItems = [];
	let renderItemsIndex = 0;

	const opaque = [];
	const transmissive = [];
	const transparent = [];

	function init() {

		renderItemsIndex = 0;

		opaque.length = 0;
		transmissive.length = 0;
		transparent.length = 0;

	}

	function getNextRenderItem( object, geometry, material, groupOrder, z, group ) {

		let renderItem = renderItems[ renderItemsIndex ];

		if ( renderItem === undefined ) {

			renderItem = {
				id: object.id,
				object: object,
				geometry: geometry,
				material: material,
				groupOrder: groupOrder,
				renderOrder: object.renderOrder,
				z: z,
				group: group
			};

			renderItems[ renderItemsIndex ] = renderItem;

		} else {

			renderItem.id = object.id;
			renderItem.object = object;
			renderItem.geometry = geometry;
			renderItem.material = material;
			renderItem.groupOrder = groupOrder;
			renderItem.renderOrder = object.renderOrder;
			renderItem.z = z;
			renderItem.group = group;

		}

		renderItemsIndex ++;

		return renderItem;

	}

	function push( object, geometry, material, groupOrder, z, group ) {

		const renderItem = getNextRenderItem( object, geometry, material, groupOrder, z, group );

		if ( material.transmission > 0.0 ) {

			transmissive.push( renderItem );

		} else if ( material.transparent === true ) {

			transparent.push( renderItem );

		} else {

			opaque.push( renderItem );

		}

	}

	function unshift( object, geometry, material, groupOrder, z, group ) {

		const renderItem = getNextRenderItem( object, geometry, material, groupOrder, z, group );

		if ( material.transmission > 0.0 ) {

			transmissive.unshift( renderItem );

		} else if ( material.transparent === true ) {

			transparent.unshift( renderItem );

		} else {

			opaque.unshift( renderItem );

		}

	}

	function sort( customOpaqueSort, customTransparentSort ) {

		if ( opaque.length > 1 ) opaque.sort( customOpaqueSort || painterSortStable );
		if ( transmissive.length > 1 ) transmissive.sort( customTransparentSort || reversePainterSortStable );
		if ( transparent.length > 1 ) transparent.sort( customTransparentSort || reversePainterSortStable );

	}

	function finish() {

		// Clear references from inactive renderItems in the list

		for ( let i = renderItemsIndex, il = renderItems.length; i < il; i ++ ) {

			const renderItem = renderItems[ i ];

			if ( renderItem.id === null ) break;

			renderItem.id = null;
			renderItem.object = null;
			renderItem.geometry = null;
			renderItem.material = null;
			renderItem.group = null;

		}

	}

	return {

		opaque: opaque,
		transmissive: transmissive,
		transparent: transparent,

		init: init,
		push: push,
		unshift: unshift,
		finish: finish,

		sort: sort
	};

}

function WebGLRenderLists() {

	let lists = new WeakMap();

	function get( scene, renderCallDepth ) {

		const listArray = lists.get( scene );
		let list;

		if ( listArray === undefined ) {

			list = new WebGLRenderList();
			lists.set( scene, [ list ] );

		} else {

			if ( renderCallDepth >= listArray.length ) {

				list = new WebGLRenderList();
				listArray.push( list );

			} else {

				list = listArray[ renderCallDepth ];

			}

		}

		return list;

	}

	function dispose() {

		lists = new WeakMap();

	}

	return {
		get: get,
		dispose: dispose
	};

}

function UniformsCache() {

	const lights = {};

	return {

		get: function ( light ) {

			if ( lights[ light.id ] !== undefined ) {

				return lights[ light.id ];

			}

			let uniforms;

			switch ( light.type ) {

				case 'DirectionalLight':
					uniforms = {
						direction: new Vector3(),
						color: new Color()
					};
					break;

				case 'SpotLight':
					uniforms = {
						position: new Vector3(),
						direction: new Vector3(),
						color: new Color(),
						distance: 0,
						coneCos: 0,
						penumbraCos: 0,
						decay: 0
					};
					break;

				case 'PointLight':
					uniforms = {
						position: new Vector3(),
						color: new Color(),
						distance: 0,
						decay: 0
					};
					break;

				case 'HemisphereLight':
					uniforms = {
						direction: new Vector3(),
						skyColor: new Color(),
						groundColor: new Color()
					};
					break;

				case 'RectAreaLight':
					uniforms = {
						color: new Color(),
						position: new Vector3(),
						halfWidth: new Vector3(),
						halfHeight: new Vector3()
					};
					break;

			}

			lights[ light.id ] = uniforms;

			return uniforms;

		}

	};

}

function ShadowUniformsCache() {

	const lights = {};

	return {

		get: function ( light ) {

			if ( lights[ light.id ] !== undefined ) {

				return lights[ light.id ];

			}

			let uniforms;

			switch ( light.type ) {

				case 'DirectionalLight':
					uniforms = {
						shadowIntensity: 1,
						shadowBias: 0,
						shadowNormalBias: 0,
						shadowRadius: 1,
						shadowMapSize: new Vector2()
					};
					break;

				case 'SpotLight':
					uniforms = {
						shadowIntensity: 1,
						shadowBias: 0,
						shadowNormalBias: 0,
						shadowRadius: 1,
						shadowMapSize: new Vector2()
					};
					break;

				case 'PointLight':
					uniforms = {
						shadowIntensity: 1,
						shadowBias: 0,
						shadowNormalBias: 0,
						shadowRadius: 1,
						shadowMapSize: new Vector2(),
						shadowCameraNear: 1,
						shadowCameraFar: 1000
					};
					break;

				// TODO (abelnation): set RectAreaLight shadow uniforms

			}

			lights[ light.id ] = uniforms;

			return uniforms;

		}

	};

}



let nextVersion = 0;

function shadowCastingAndTexturingLightsFirst( lightA, lightB ) {

	return ( lightB.castShadow ? 2 : 0 ) - ( lightA.castShadow ? 2 : 0 ) + ( lightB.map ? 1 : 0 ) - ( lightA.map ? 1 : 0 );

}

function WebGLLights( extensions ) {

	const cache = new UniformsCache();

	const shadowCache = ShadowUniformsCache();

	const state = {

		version: 0,

		hash: {
			directionalLength: - 1,
			pointLength: - 1,
			spotLength: - 1,
			rectAreaLength: - 1,
			hemiLength: - 1,

			numDirectionalShadows: - 1,
			numPointShadows: - 1,
			numSpotShadows: - 1,
			numSpotMaps: - 1,

			numLightProbes: - 1
		},

		ambient: [ 0, 0, 0 ],
		probe: [],
		directional: [],
		directionalShadow: [],
		directionalShadowMap: [],
		directionalShadowMatrix: [],
		spot: [],
		spotLightMap: [],
		spotShadow: [],
		spotShadowMap: [],
		spotLightMatrix: [],
		rectArea: [],
		rectAreaLTC1: null,
		rectAreaLTC2: null,
		point: [],
		pointShadow: [],
		pointShadowMap: [],
		pointShadowMatrix: [],
		hemi: [],
		numSpotLightShadowsWithMaps: 0,
		numLightProbes: 0

	};

	for ( let i = 0; i < 9; i ++ ) state.probe.push( new Vector3() );

	const vector3 = new Vector3();
	const matrix4 = new Matrix4();
	const matrix42 = new Matrix4();

	function setup( lights ) {

		let r = 0, g = 0, b = 0;

		for ( let i = 0; i < 9; i ++ ) state.probe[ i ].set( 0, 0, 0 );

		let directionalLength = 0;
		let pointLength = 0;
		let spotLength = 0;
		let rectAreaLength = 0;
		let hemiLength = 0;

		let numDirectionalShadows = 0;
		let numPointShadows = 0;
		let numSpotShadows = 0;
		let numSpotMaps = 0;
		let numSpotShadowsWithMaps = 0;

		let numLightProbes = 0;

		// ordering : [shadow casting + map texturing, map texturing, shadow casting, none ]
		lights.sort( shadowCastingAndTexturingLightsFirst );

		for ( let i = 0, l = lights.length; i < l; i ++ ) {

			const light = lights[ i ];

			const color = light.color;
			const intensity = light.intensity;
			const distance = light.distance;

			const shadowMap = ( light.shadow && light.shadow.map ) ? light.shadow.map.texture : null;

			if ( light.isAmbientLight ) {

				r += color.r * intensity;
				g += color.g * intensity;
				b += color.b * intensity;

			} else if ( light.isLightProbe ) {

				for ( let j = 0; j < 9; j ++ ) {

					state.probe[ j ].addScaledVector( light.sh.coefficients[ j ], intensity );

				}

				numLightProbes ++;

			} else if ( light.isDirectionalLight ) {

				const uniforms = cache.get( light );

				uniforms.color.copy( light.color ).multiplyScalar( light.intensity );

				if ( light.castShadow ) {

					const shadow = light.shadow;

					const shadowUniforms = shadowCache.get( light );

					shadowUniforms.shadowIntensity = shadow.intensity;
					shadowUniforms.shadowBias = shadow.bias;
					shadowUniforms.shadowNormalBias = shadow.normalBias;
					shadowUniforms.shadowRadius = shadow.radius;
					shadowUniforms.shadowMapSize = shadow.mapSize;

					state.directionalShadow[ directionalLength ] = shadowUniforms;
					state.directionalShadowMap[ directionalLength ] = shadowMap;
					state.directionalShadowMatrix[ directionalLength ] = light.shadow.matrix;

					numDirectionalShadows ++;

				}

				state.directional[ directionalLength ] = uniforms;

				directionalLength ++;

			} else if ( light.isSpotLight ) {

				const uniforms = cache.get( light );

				uniforms.position.setFromMatrixPosition( light.matrixWorld );

				uniforms.color.copy( color ).multiplyScalar( intensity );
				uniforms.distance = distance;

				uniforms.coneCos = Math.cos( light.angle );
				uniforms.penumbraCos = Math.cos( light.angle * ( 1 - light.penumbra ) );
				uniforms.decay = light.decay;

				state.spot[ spotLength ] = uniforms;

				const shadow = light.shadow;

				if ( light.map ) {

					state.spotLightMap[ numSpotMaps ] = light.map;
					numSpotMaps ++;

					// make sure the lightMatrix is up to date
					// TODO : do it if required only
					shadow.updateMatrices( light );

					if ( light.castShadow ) numSpotShadowsWithMaps ++;

				}

				state.spotLightMatrix[ spotLength ] = shadow.matrix;

				if ( light.castShadow ) {

					const shadowUniforms = shadowCache.get( light );

					shadowUniforms.shadowIntensity = shadow.intensity;
					shadowUniforms.shadowBias = shadow.bias;
					shadowUniforms.shadowNormalBias = shadow.normalBias;
					shadowUniforms.shadowRadius = shadow.radius;
					shadowUniforms.shadowMapSize = shadow.mapSize;

					state.spotShadow[ spotLength ] = shadowUniforms;
					state.spotShadowMap[ spotLength ] = shadowMap;

					numSpotShadows ++;

				}

				spotLength ++;

			} else if ( light.isRectAreaLight ) {

				const uniforms = cache.get( light );

				uniforms.color.copy( color ).multiplyScalar( intensity );

				uniforms.halfWidth.set( light.width * 0.5, 0.0, 0.0 );
				uniforms.halfHeight.set( 0.0, light.height * 0.5, 0.0 );

				state.rectArea[ rectAreaLength ] = uniforms;

				rectAreaLength ++;

			} else if ( light.isPointLight ) {

				const uniforms = cache.get( light );

				uniforms.color.copy( light.color ).multiplyScalar( light.intensity );
				uniforms.distance = light.distance;
				uniforms.decay = light.decay;

				if ( light.castShadow ) {

					const shadow = light.shadow;

					const shadowUniforms = shadowCache.get( light );

					shadowUniforms.shadowIntensity = shadow.intensity;
					shadowUniforms.shadowBias = shadow.bias;
					shadowUniforms.shadowNormalBias = shadow.normalBias;
					shadowUniforms.shadowRadius = shadow.radius;
					shadowUniforms.shadowMapSize = shadow.mapSize;
					shadowUniforms.shadowCameraNear = shadow.camera.near;
					shadowUniforms.shadowCameraFar = shadow.camera.far;

					state.pointShadow[ pointLength ] = shadowUniforms;
					state.pointShadowMap[ pointLength ] = shadowMap;
					state.pointShadowMatrix[ pointLength ] = light.shadow.matrix;

					numPointShadows ++;

				}

				state.point[ pointLength ] = uniforms;

				pointLength ++;

			} else if ( light.isHemisphereLight ) {

				const uniforms = cache.get( light );

				uniforms.skyColor.copy( light.color ).multiplyScalar( intensity );
				uniforms.groundColor.copy( light.groundColor ).multiplyScalar( intensity );

				state.hemi[ hemiLength ] = uniforms;

				hemiLength ++;

			}

		}

		if ( rectAreaLength > 0 ) {

			if ( extensions.has( 'OES_texture_float_linear' ) === true ) {

				state.rectAreaLTC1 = UniformsLib.LTC_FLOAT_1;
				state.rectAreaLTC2 = UniformsLib.LTC_FLOAT_2;

			} else {

				state.rectAreaLTC1 = UniformsLib.LTC_HALF_1;
				state.rectAreaLTC2 = UniformsLib.LTC_HALF_2;

			}

		}

		state.ambient[ 0 ] = r;
		state.ambient[ 1 ] = g;
		state.ambient[ 2 ] = b;

		const hash = state.hash;

		if ( hash.directionalLength !== directionalLength ||
			hash.pointLength !== pointLength ||
			hash.spotLength !== spotLength ||
			hash.rectAreaLength !== rectAreaLength ||
			hash.hemiLength !== hemiLength ||
			hash.numDirectionalShadows !== numDirectionalShadows ||
			hash.numPointShadows !== numPointShadows ||
			hash.numSpotShadows !== numSpotShadows ||
			hash.numSpotMaps !== numSpotMaps ||
			hash.numLightProbes !== numLightProbes ) {

			state.directional.length = directionalLength;
			state.spot.length = spotLength;
			state.rectArea.length = rectAreaLength;
			state.point.length = pointLength;
			state.hemi.length = hemiLength;

			state.directionalShadow.length = numDirectionalShadows;
			state.directionalShadowMap.length = numDirectionalShadows;
			state.pointShadow.length = numPointShadows;
			state.pointShadowMap.length = numPointShadows;
			state.spotShadow.length = numSpotShadows;
			state.spotShadowMap.length = numSpotShadows;
			state.directionalShadowMatrix.length = numDirectionalShadows;
			state.pointShadowMatrix.length = numPointShadows;
			state.spotLightMatrix.length = numSpotShadows + numSpotMaps - numSpotShadowsWithMaps;
			state.spotLightMap.length = numSpotMaps;
			state.numSpotLightShadowsWithMaps = numSpotShadowsWithMaps;
			state.numLightProbes = numLightProbes;

			hash.directionalLength = directionalLength;
			hash.pointLength = pointLength;
			hash.spotLength = spotLength;
			hash.rectAreaLength = rectAreaLength;
			hash.hemiLength = hemiLength;

			hash.numDirectionalShadows = numDirectionalShadows;
			hash.numPointShadows = numPointShadows;
			hash.numSpotShadows = numSpotShadows;
			hash.numSpotMaps = numSpotMaps;

			hash.numLightProbes = numLightProbes;

			state.version = nextVersion ++;

		}

	}

	function setupView( lights, camera ) {

		let directionalLength = 0;
		let pointLength = 0;
		let spotLength = 0;
		let rectAreaLength = 0;
		let hemiLength = 0;

		const viewMatrix = camera.matrixWorldInverse;

		for ( let i = 0, l = lights.length; i < l; i ++ ) {

			const light = lights[ i ];

			if ( light.isDirectionalLight ) {

				const uniforms = state.directional[ directionalLength ];

				uniforms.direction.setFromMatrixPosition( light.matrixWorld );
				vector3.setFromMatrixPosition( light.target.matrixWorld );
				uniforms.direction.sub( vector3 );
				uniforms.direction.transformDirection( viewMatrix );

				directionalLength ++;

			} else if ( light.isSpotLight ) {

				const uniforms = state.spot[ spotLength ];

				uniforms.position.setFromMatrixPosition( light.matrixWorld );
				uniforms.position.applyMatrix4( viewMatrix );

				uniforms.direction.setFromMatrixPosition( light.matrixWorld );
				vector3.setFromMatrixPosition( light.target.matrixWorld );
				uniforms.direction.sub( vector3 );
				uniforms.direction.transformDirection( viewMatrix );

				spotLength ++;

			} else if ( light.isRectAreaLight ) {

				const uniforms = state.rectArea[ rectAreaLength ];

				uniforms.position.setFromMatrixPosition( light.matrixWorld );
				uniforms.position.applyMatrix4( viewMatrix );

				// extract local rotation of light to derive width/height half vectors
				matrix42.identity();
				matrix4.copy( light.matrixWorld );
				matrix4.premultiply( viewMatrix );
				matrix42.extractRotation( matrix4 );

				uniforms.halfWidth.set( light.width * 0.5, 0.0, 0.0 );
				uniforms.halfHeight.set( 0.0, light.height * 0.5, 0.0 );

				uniforms.halfWidth.applyMatrix4( matrix42 );
				uniforms.halfHeight.applyMatrix4( matrix42 );

				rectAreaLength ++;

			} else if ( light.isPointLight ) {

				const uniforms = state.point[ pointLength ];

				uniforms.position.setFromMatrixPosition( light.matrixWorld );
				uniforms.position.applyMatrix4( viewMatrix );

				pointLength ++;

			} else if ( light.isHemisphereLight ) {

				const uniforms = state.hemi[ hemiLength ];

				uniforms.direction.setFromMatrixPosition( light.matrixWorld );
				uniforms.direction.transformDirection( viewMatrix );

				hemiLength ++;

			}

		}

	}

	return {
		setup: setup,
		setupView: setupView,
		state: state
	};

}

function WebGLRenderState( extensions ) {

	const lights = new WebGLLights( extensions );

	const lightsArray = [];
	const shadowsArray = [];

	function init( camera ) {

		state.camera = camera;

		lightsArray.length = 0;
		shadowsArray.length = 0;

	}

	function pushLight( light ) {

		lightsArray.push( light );

	}

	function pushShadow( shadowLight ) {

		shadowsArray.push( shadowLight );

	}

	function setupLights() {

		lights.setup( lightsArray );

	}

	function setupLightsView( camera ) {

		lights.setupView( lightsArray, camera );

	}

	const state = {
		lightsArray: lightsArray,
		shadowsArray: shadowsArray,

		camera: null,

		lights: lights,

		transmissionRenderTarget: {}
	};

	return {
		init: init,
		state: state,
		setupLights: setupLights,
		setupLightsView: setupLightsView,

		pushLight: pushLight,
		pushShadow: pushShadow
	};

}

function WebGLRenderStates( extensions ) {

	let renderStates = new WeakMap();

	function get( scene, renderCallDepth = 0 ) {

		const renderStateArray = renderStates.get( scene );
		let renderState;

		if ( renderStateArray === undefined ) {

			renderState = new WebGLRenderState( extensions );
			renderStates.set( scene, [ renderState ] );

		} else {

			if ( renderCallDepth >= renderStateArray.length ) {

				renderState = new WebGLRenderState( extensions );
				renderStateArray.push( renderState );

			} else {

				renderState = renderStateArray[ renderCallDepth ];

			}

		}

		return renderState;

	}

	function dispose() {

		renderStates = new WeakMap();

	}

	return {
		get: get,
		dispose: dispose
	};

}

class MeshDepthMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshDepthMaterial = true;

		this.type = 'MeshDepthMaterial';

		this.depthPacking = BasicDepthPacking;

		this.map = null;

		this.alphaMap = null;

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.wireframe = false;
		this.wireframeLinewidth = 1;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.depthPacking = source.depthPacking;

		this.map = source.map;

		this.alphaMap = source.alphaMap;

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;

		return this;

	}

}

class MeshDistanceMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshDistanceMaterial = true;

		this.type = 'MeshDistanceMaterial';

		this.map = null;

		this.alphaMap = null;

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.map = source.map;

		this.alphaMap = source.alphaMap;

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		return this;

	}

}

const vertex = "void main() {\n\tgl_Position = vec4( position, 1.0 );\n}";

const fragment = "uniform sampler2D shadow_pass;\nuniform vec2 resolution;\nuniform float radius;\n#include <packing>\nvoid main() {\n\tconst float samples = float( VSM_SAMPLES );\n\tfloat mean = 0.0;\n\tfloat squared_mean = 0.0;\n\tfloat uvStride = samples <= 1.0 ? 0.0 : 2.0 / ( samples - 1.0 );\n\tfloat uvStart = samples <= 1.0 ? 0.0 : - 1.0;\n\tfor ( float i = 0.0; i < samples; i ++ ) {\n\t\tfloat uvOffset = uvStart + i * uvStride;\n\t\t#ifdef HORIZONTAL_PASS\n\t\t\tvec2 distribution = unpackRGBATo2Half( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( uvOffset, 0.0 ) * radius ) / resolution ) );\n\t\t\tmean += distribution.x;\n\t\t\tsquared_mean += distribution.y * distribution.y + distribution.x * distribution.x;\n\t\t#else\n\t\t\tfloat depth = unpackRGBAToDepth( texture2D( shadow_pass, ( gl_FragCoord.xy + vec2( 0.0, uvOffset ) * radius ) / resolution ) );\n\t\t\tmean += depth;\n\t\t\tsquared_mean += depth * depth;\n\t\t#endif\n\t}\n\tmean = mean / samples;\n\tsquared_mean = squared_mean / samples;\n\tfloat std_dev = sqrt( squared_mean - mean * mean );\n\tgl_FragColor = pack2HalfToRGBA( vec2( mean, std_dev ) );\n}";

function WebGLShadowMap( renderer, objects, capabilities ) {

	let _frustum = new Frustum();

	const _shadowMapSize = new Vector2(),
		_viewportSize = new Vector2(),

		_viewport = new Vector4(),

		_depthMaterial = new MeshDepthMaterial( { depthPacking: RGBADepthPacking } ),
		_distanceMaterial = new MeshDistanceMaterial(),

		_materialCache = {},

		_maxTextureSize = capabilities.maxTextureSize;

	const shadowSide = { [ FrontSide ]: BackSide, [ BackSide ]: FrontSide, [ DoubleSide ]: DoubleSide };

	const shadowMaterialVertical = new ShaderMaterial( {
		defines: {
			VSM_SAMPLES: 8
		},
		uniforms: {
			shadow_pass: { value: null },
			resolution: { value: new Vector2() },
			radius: { value: 4.0 }
		},

		vertexShader: vertex,
		fragmentShader: fragment

	} );

	const shadowMaterialHorizontal = shadowMaterialVertical.clone();
	shadowMaterialHorizontal.defines.HORIZONTAL_PASS = 1;

	const fullScreenTri = new BufferGeometry();
	fullScreenTri.setAttribute(
		'position',
		new BufferAttribute(
			new Float32Array( [ - 1, - 1, 0.5, 3, - 1, 0.5, - 1, 3, 0.5 ] ),
			3
		)
	);

	const fullScreenMesh = new Mesh( fullScreenTri, shadowMaterialVertical );

	const scope = this;

	this.enabled = false;

	this.autoUpdate = true;
	this.needsUpdate = false;

	this.type = PCFShadowMap;
	let _previousType = this.type;

	this.render = function ( lights, scene, camera ) {

		if ( scope.enabled === false ) return;
		if ( scope.autoUpdate === false && scope.needsUpdate === false ) return;

		if ( lights.length === 0 ) return;

		const currentRenderTarget = renderer.getRenderTarget();
		const activeCubeFace = renderer.getActiveCubeFace();
		const activeMipmapLevel = renderer.getActiveMipmapLevel();

		const _state = renderer.state;

		// Set GL state for depth map.
		_state.setBlending( NoBlending );
		_state.buffers.color.setClear( 1, 1, 1, 1 );
		_state.buffers.depth.setTest( true );
		_state.setScissorTest( false );

		// check for shadow map type changes

		const toVSM = ( _previousType !== VSMShadowMap && this.type === VSMShadowMap );
		const fromVSM = ( _previousType === VSMShadowMap && this.type !== VSMShadowMap );

		// render depth map

		for ( let i = 0, il = lights.length; i < il; i ++ ) {

			const light = lights[ i ];
			const shadow = light.shadow;

			if ( shadow === undefined ) {

				console.warn( 'THREE.WebGLShadowMap:', light, 'has no shadow.' );
				continue;

			}

			if ( shadow.autoUpdate === false && shadow.needsUpdate === false ) continue;

			_shadowMapSize.copy( shadow.mapSize );

			const shadowFrameExtents = shadow.getFrameExtents();

			_shadowMapSize.multiply( shadowFrameExtents );

			_viewportSize.copy( shadow.mapSize );

			if ( _shadowMapSize.x > _maxTextureSize || _shadowMapSize.y > _maxTextureSize ) {

				if ( _shadowMapSize.x > _maxTextureSize ) {

					_viewportSize.x = Math.floor( _maxTextureSize / shadowFrameExtents.x );
					_shadowMapSize.x = _viewportSize.x * shadowFrameExtents.x;
					shadow.mapSize.x = _viewportSize.x;

				}

				if ( _shadowMapSize.y > _maxTextureSize ) {

					_viewportSize.y = Math.floor( _maxTextureSize / shadowFrameExtents.y );
					_shadowMapSize.y = _viewportSize.y * shadowFrameExtents.y;
					shadow.mapSize.y = _viewportSize.y;

				}

			}

			if ( shadow.map === null || toVSM === true || fromVSM === true ) {

				const pars = ( this.type !== VSMShadowMap ) ? { minFilter: NearestFilter, magFilter: NearestFilter } : {};

				if ( shadow.map !== null ) {

					shadow.map.dispose();

				}

				shadow.map = new WebGLRenderTarget( _shadowMapSize.x, _shadowMapSize.y, pars );
				shadow.map.texture.name = light.name + '.shadowMap';

				shadow.camera.updateProjectionMatrix();

			}

			renderer.setRenderTarget( shadow.map );
			renderer.clear();

			const viewportCount = shadow.getViewportCount();

			for ( let vp = 0; vp < viewportCount; vp ++ ) {

				const viewport = shadow.getViewport( vp );

				_viewport.set(
					_viewportSize.x * viewport.x,
					_viewportSize.y * viewport.y,
					_viewportSize.x * viewport.z,
					_viewportSize.y * viewport.w
				);

				_state.viewport( _viewport );

				shadow.updateMatrices( light, vp );

				_frustum = shadow.getFrustum();

				renderObject( scene, camera, shadow.camera, light, this.type );

			}

			// do blur pass for VSM

			if ( shadow.isPointLightShadow !== true && this.type === VSMShadowMap ) {

				VSMPass( shadow, camera );

			}

			shadow.needsUpdate = false;

		}

		_previousType = this.type;

		scope.needsUpdate = false;

		renderer.setRenderTarget( currentRenderTarget, activeCubeFace, activeMipmapLevel );

	};

	function VSMPass( shadow, camera ) {

		const geometry = objects.update( fullScreenMesh );

		if ( shadowMaterialVertical.defines.VSM_SAMPLES !== shadow.blurSamples ) {

			shadowMaterialVertical.defines.VSM_SAMPLES = shadow.blurSamples;
			shadowMaterialHorizontal.defines.VSM_SAMPLES = shadow.blurSamples;

			shadowMaterialVertical.needsUpdate = true;
			shadowMaterialHorizontal.needsUpdate = true;

		}

		if ( shadow.mapPass === null ) {

			shadow.mapPass = new WebGLRenderTarget( _shadowMapSize.x, _shadowMapSize.y );

		}

		// vertical pass

		shadowMaterialVertical.uniforms.shadow_pass.value = shadow.map.texture;
		shadowMaterialVertical.uniforms.resolution.value = shadow.mapSize;
		shadowMaterialVertical.uniforms.radius.value = shadow.radius;
		renderer.setRenderTarget( shadow.mapPass );
		renderer.clear();
		renderer.renderBufferDirect( camera, null, geometry, shadowMaterialVertical, fullScreenMesh, null );

		// horizontal pass

		shadowMaterialHorizontal.uniforms.shadow_pass.value = shadow.mapPass.texture;
		shadowMaterialHorizontal.uniforms.resolution.value = shadow.mapSize;
		shadowMaterialHorizontal.uniforms.radius.value = shadow.radius;
		renderer.setRenderTarget( shadow.map );
		renderer.clear();
		renderer.renderBufferDirect( camera, null, geometry, shadowMaterialHorizontal, fullScreenMesh, null );

	}

	function getDepthMaterial( object, material, light, type ) {

		let result = null;

		const customMaterial = ( light.isPointLight === true ) ? object.customDistanceMaterial : object.customDepthMaterial;

		if ( customMaterial !== undefined ) {

			result = customMaterial;

		} else {

			result = ( light.isPointLight === true ) ? _distanceMaterial : _depthMaterial;

			if ( ( renderer.localClippingEnabled && material.clipShadows === true && Array.isArray( material.clippingPlanes ) && material.clippingPlanes.length !== 0 ) ||
				( material.displacementMap && material.displacementScale !== 0 ) ||
				( material.alphaMap && material.alphaTest > 0 ) ||
				( material.map && material.alphaTest > 0 ) ) {

				// in this case we need a unique material instance reflecting the
				// appropriate state

				const keyA = result.uuid, keyB = material.uuid;

				let materialsForVariant = _materialCache[ keyA ];

				if ( materialsForVariant === undefined ) {

					materialsForVariant = {};
					_materialCache[ keyA ] = materialsForVariant;

				}

				let cachedMaterial = materialsForVariant[ keyB ];

				if ( cachedMaterial === undefined ) {

					cachedMaterial = result.clone();
					materialsForVariant[ keyB ] = cachedMaterial;
					material.addEventListener( 'dispose', onMaterialDispose );

				}

				result = cachedMaterial;

			}

		}

		result.visible = material.visible;
		result.wireframe = material.wireframe;

		if ( type === VSMShadowMap ) {

			result.side = ( material.shadowSide !== null ) ? material.shadowSide : material.side;

		} else {

			result.side = ( material.shadowSide !== null ) ? material.shadowSide : shadowSide[ material.side ];

		}

		result.alphaMap = material.alphaMap;
		result.alphaTest = material.alphaTest;
		result.map = material.map;

		result.clipShadows = material.clipShadows;
		result.clippingPlanes = material.clippingPlanes;
		result.clipIntersection = material.clipIntersection;

		result.displacementMap = material.displacementMap;
		result.displacementScale = material.displacementScale;
		result.displacementBias = material.displacementBias;

		result.wireframeLinewidth = material.wireframeLinewidth;
		result.linewidth = material.linewidth;

		if ( light.isPointLight === true && result.isMeshDistanceMaterial === true ) {

			const materialProperties = renderer.properties.get( result );
			materialProperties.light = light;

		}

		return result;

	}

	function renderObject( object, camera, shadowCamera, light, type ) {

		if ( object.visible === false ) return;

		const visible = object.layers.test( camera.layers );

		if ( visible && ( object.isMesh || object.isLine || object.isPoints ) ) {

			if ( ( object.castShadow || ( object.receiveShadow && type === VSMShadowMap ) ) && ( ! object.frustumCulled || _frustum.intersectsObject( object ) ) ) {

				object.modelViewMatrix.multiplyMatrices( shadowCamera.matrixWorldInverse, object.matrixWorld );

				const geometry = objects.update( object );
				const material = object.material;

				if ( Array.isArray( material ) ) {

					const groups = geometry.groups;

					for ( let k = 0, kl = groups.length; k < kl; k ++ ) {

						const group = groups[ k ];
						const groupMaterial = material[ group.materialIndex ];

						if ( groupMaterial && groupMaterial.visible ) {

							const depthMaterial = getDepthMaterial( object, groupMaterial, light, type );

							object.onBeforeShadow( renderer, object, camera, shadowCamera, geometry, depthMaterial, group );

							renderer.renderBufferDirect( shadowCamera, null, geometry, depthMaterial, object, group );

							object.onAfterShadow( renderer, object, camera, shadowCamera, geometry, depthMaterial, group );

						}

					}

				} else if ( material.visible ) {

					const depthMaterial = getDepthMaterial( object, material, light, type );

					object.onBeforeShadow( renderer, object, camera, shadowCamera, geometry, depthMaterial, null );

					renderer.renderBufferDirect( shadowCamera, null, geometry, depthMaterial, object, null );

					object.onAfterShadow( renderer, object, camera, shadowCamera, geometry, depthMaterial, null );

				}

			}

		}

		const children = object.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			renderObject( children[ i ], camera, shadowCamera, light, type );

		}

	}

	function onMaterialDispose( event ) {

		const material = event.target;

		material.removeEventListener( 'dispose', onMaterialDispose );

		// make sure to remove the unique distance/depth materials used for shadow map rendering

		for ( const id in _materialCache ) {

			const cache = _materialCache[ id ];

			const uuid = event.target.uuid;

			if ( uuid in cache ) {

				const shadowMaterial = cache[ uuid ];
				shadowMaterial.dispose();
				delete cache[ uuid ];

			}

		}

	}

}

const reversedFuncs = {
	[ NeverDepth ]: AlwaysDepth,
	[ LessDepth ]: GreaterDepth,
	[ EqualDepth ]: NotEqualDepth,
	[ LessEqualDepth ]: GreaterEqualDepth,

	[ AlwaysDepth ]: NeverDepth,
	[ GreaterDepth ]: LessDepth,
	[ NotEqualDepth ]: EqualDepth,
	[ GreaterEqualDepth ]: LessEqualDepth,
};

function WebGLState( gl ) {

	function ColorBuffer() {

		let locked = false;

		const color = new Vector4();
		let currentColorMask = null;
		const currentColorClear = new Vector4( 0, 0, 0, 0 );

		return {

			setMask: function ( colorMask ) {

				if ( currentColorMask !== colorMask && ! locked ) {

					gl.colorMask( colorMask, colorMask, colorMask, colorMask );
					currentColorMask = colorMask;

				}

			},

			setLocked: function ( lock ) {

				locked = lock;

			},

			setClear: function ( r, g, b, a, premultipliedAlpha ) {

				if ( premultipliedAlpha === true ) {

					r *= a; g *= a; b *= a;

				}

				color.set( r, g, b, a );

				if ( currentColorClear.equals( color ) === false ) {

					gl.clearColor( r, g, b, a );
					currentColorClear.copy( color );

				}

			},

			reset: function () {

				locked = false;

				currentColorMask = null;
				currentColorClear.set( - 1, 0, 0, 0 ); // set to invalid state

			}

		};

	}

	function DepthBuffer() {

		let locked = false;
		let reversed = false;

		let currentDepthMask = null;
		let currentDepthFunc = null;
		let currentDepthClear = null;

		return {

			setReversed: function ( value ) {

				reversed = value;

			},

			setTest: function ( depthTest ) {

				if ( depthTest ) {

					enable( gl.DEPTH_TEST );

				} else {

					disable( gl.DEPTH_TEST );

				}

			},

			setMask: function ( depthMask ) {

				if ( currentDepthMask !== depthMask && ! locked ) {

					gl.depthMask( depthMask );
					currentDepthMask = depthMask;

				}

			},

			setFunc: function ( depthFunc ) {

				if ( reversed ) depthFunc = reversedFuncs[ depthFunc ];

				if ( currentDepthFunc !== depthFunc ) {

					switch ( depthFunc ) {

						case NeverDepth:

							gl.depthFunc( gl.NEVER );
							break;

						case AlwaysDepth:

							gl.depthFunc( gl.ALWAYS );
							break;

						case LessDepth:

							gl.depthFunc( gl.LESS );
							break;

						case LessEqualDepth:

							gl.depthFunc( gl.LEQUAL );
							break;

						case EqualDepth:

							gl.depthFunc( gl.EQUAL );
							break;

						case GreaterEqualDepth:

							gl.depthFunc( gl.GEQUAL );
							break;

						case GreaterDepth:

							gl.depthFunc( gl.GREATER );
							break;

						case NotEqualDepth:

							gl.depthFunc( gl.NOTEQUAL );
							break;

						default:

							gl.depthFunc( gl.LEQUAL );

					}

					currentDepthFunc = depthFunc;

				}

			},

			setLocked: function ( lock ) {

				locked = lock;

			},

			setClear: function ( depth ) {

				if ( currentDepthClear !== depth ) {

					gl.clearDepth( depth );
					currentDepthClear = depth;

				}

			},

			reset: function () {

				locked = false;

				currentDepthMask = null;
				currentDepthFunc = null;
				currentDepthClear = null;

			}

		};

	}

	function StencilBuffer() {

		let locked = false;

		let currentStencilMask = null;
		let currentStencilFunc = null;
		let currentStencilRef = null;
		let currentStencilFuncMask = null;
		let currentStencilFail = null;
		let currentStencilZFail = null;
		let currentStencilZPass = null;
		let currentStencilClear = null;

		return {

			setTest: function ( stencilTest ) {

				if ( ! locked ) {

					if ( stencilTest ) {

						enable( gl.STENCIL_TEST );

					} else {

						disable( gl.STENCIL_TEST );

					}

				}

			},

			setMask: function ( stencilMask ) {

				if ( currentStencilMask !== stencilMask && ! locked ) {

					gl.stencilMask( stencilMask );
					currentStencilMask = stencilMask;

				}

			},

			setFunc: function ( stencilFunc, stencilRef, stencilMask ) {

				if ( currentStencilFunc !== stencilFunc ||
				     currentStencilRef !== stencilRef ||
				     currentStencilFuncMask !== stencilMask ) {

					gl.stencilFunc( stencilFunc, stencilRef, stencilMask );

					currentStencilFunc = stencilFunc;
					currentStencilRef = stencilRef;
					currentStencilFuncMask = stencilMask;

				}

			},

			setOp: function ( stencilFail, stencilZFail, stencilZPass ) {

				if ( currentStencilFail !== stencilFail ||
				     currentStencilZFail !== stencilZFail ||
				     currentStencilZPass !== stencilZPass ) {

					gl.stencilOp( stencilFail, stencilZFail, stencilZPass );

					currentStencilFail = stencilFail;
					currentStencilZFail = stencilZFail;
					currentStencilZPass = stencilZPass;

				}

			},

			setLocked: function ( lock ) {

				locked = lock;

			},

			setClear: function ( stencil ) {

				if ( currentStencilClear !== stencil ) {

					gl.clearStencil( stencil );
					currentStencilClear = stencil;

				}

			},

			reset: function () {

				locked = false;

				currentStencilMask = null;
				currentStencilFunc = null;
				currentStencilRef = null;
				currentStencilFuncMask = null;
				currentStencilFail = null;
				currentStencilZFail = null;
				currentStencilZPass = null;
				currentStencilClear = null;

			}

		};

	}

	//

	const colorBuffer = new ColorBuffer();
	const depthBuffer = new DepthBuffer();
	const stencilBuffer = new StencilBuffer();

	const uboBindings = new WeakMap();
	const uboProgramMap = new WeakMap();

	let enabledCapabilities = {};

	let currentBoundFramebuffers = {};
	let currentDrawbuffers = new WeakMap();
	let defaultDrawbuffers = [];

	let currentProgram = null;

	let currentBlendingEnabled = false;
	let currentBlending = null;
	let currentBlendEquation = null;
	let currentBlendSrc = null;
	let currentBlendDst = null;
	let currentBlendEquationAlpha = null;
	let currentBlendSrcAlpha = null;
	let currentBlendDstAlpha = null;
	let currentBlendColor = new Color( 0, 0, 0 );
	let currentBlendAlpha = 0;
	let currentPremultipledAlpha = false;

	let currentFlipSided = null;
	let currentCullFace = null;

	let currentLineWidth = null;

	let currentPolygonOffsetFactor = null;
	let currentPolygonOffsetUnits = null;

	const maxTextures = gl.getParameter( gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS );

	let lineWidthAvailable = false;
	let version = 0;
	const glVersion = gl.getParameter( gl.VERSION );

	if ( glVersion.indexOf( 'WebGL' ) !== - 1 ) {

		version = parseFloat( /^WebGL (\d)/.exec( glVersion )[ 1 ] );
		lineWidthAvailable = ( version >= 1.0 );

	} else if ( glVersion.indexOf( 'OpenGL ES' ) !== - 1 ) {

		version = parseFloat( /^OpenGL ES (\d)/.exec( glVersion )[ 1 ] );
		lineWidthAvailable = ( version >= 2.0 );

	}

	let currentTextureSlot = null;
	let currentBoundTextures = {};

	const scissorParam = gl.getParameter( gl.SCISSOR_BOX );
	const viewportParam = gl.getParameter( gl.VIEWPORT );

	const currentScissor = new Vector4().fromArray( scissorParam );
	const currentViewport = new Vector4().fromArray( viewportParam );

	function createTexture( type, target, count, dimensions ) {

		const data = new Uint8Array( 4 ); // 4 is required to match default unpack alignment of 4.
		const texture = gl.createTexture();

		gl.bindTexture( type, texture );
		gl.texParameteri( type, gl.TEXTURE_MIN_FILTER, gl.NEAREST );
		gl.texParameteri( type, gl.TEXTURE_MAG_FILTER, gl.NEAREST );

		for ( let i = 0; i < count; i ++ ) {

			if ( type === gl.TEXTURE_3D || type === gl.TEXTURE_2D_ARRAY ) {

				gl.texImage3D( target, 0, gl.RGBA, 1, 1, dimensions, 0, gl.RGBA, gl.UNSIGNED_BYTE, data );

			} else {

				gl.texImage2D( target + i, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, data );

			}

		}

		return texture;

	}

	const emptyTextures = {};
	emptyTextures[ gl.TEXTURE_2D ] = createTexture( gl.TEXTURE_2D, gl.TEXTURE_2D, 1 );
	emptyTextures[ gl.TEXTURE_CUBE_MAP ] = createTexture( gl.TEXTURE_CUBE_MAP, gl.TEXTURE_CUBE_MAP_POSITIVE_X, 6 );
	emptyTextures[ gl.TEXTURE_2D_ARRAY ] = createTexture( gl.TEXTURE_2D_ARRAY, gl.TEXTURE_2D_ARRAY, 1, 1 );
	emptyTextures[ gl.TEXTURE_3D ] = createTexture( gl.TEXTURE_3D, gl.TEXTURE_3D, 1, 1 );

	// init

	colorBuffer.setClear( 0, 0, 0, 1 );
	depthBuffer.setClear( 1 );
	stencilBuffer.setClear( 0 );

	enable( gl.DEPTH_TEST );
	depthBuffer.setFunc( LessEqualDepth );

	setFlipSided( false );
	setCullFace( CullFaceBack );
	enable( gl.CULL_FACE );

	setBlending( NoBlending );

	//

	function enable( id ) {

		if ( enabledCapabilities[ id ] !== true ) {

			gl.enable( id );
			enabledCapabilities[ id ] = true;

		}

	}

	function disable( id ) {

		if ( enabledCapabilities[ id ] !== false ) {

			gl.disable( id );
			enabledCapabilities[ id ] = false;

		}

	}

	function bindFramebuffer( target, framebuffer ) {

		if ( currentBoundFramebuffers[ target ] !== framebuffer ) {

			gl.bindFramebuffer( target, framebuffer );

			currentBoundFramebuffers[ target ] = framebuffer;

			// gl.DRAW_FRAMEBUFFER is equivalent to gl.FRAMEBUFFER

			if ( target === gl.DRAW_FRAMEBUFFER ) {

				currentBoundFramebuffers[ gl.FRAMEBUFFER ] = framebuffer;

			}

			if ( target === gl.FRAMEBUFFER ) {

				currentBoundFramebuffers[ gl.DRAW_FRAMEBUFFER ] = framebuffer;

			}

			return true;

		}

		return false;

	}

	function drawBuffers( renderTarget, framebuffer ) {

		let drawBuffers = defaultDrawbuffers;

		let needsUpdate = false;

		if ( renderTarget ) {

			drawBuffers = currentDrawbuffers.get( framebuffer );

			if ( drawBuffers === undefined ) {

				drawBuffers = [];
				currentDrawbuffers.set( framebuffer, drawBuffers );

			}

			const textures = renderTarget.textures;

			if ( drawBuffers.length !== textures.length || drawBuffers[ 0 ] !== gl.COLOR_ATTACHMENT0 ) {

				for ( let i = 0, il = textures.length; i < il; i ++ ) {

					drawBuffers[ i ] = gl.COLOR_ATTACHMENT0 + i;

				}

				drawBuffers.length = textures.length;

				needsUpdate = true;

			}

		} else {

			if ( drawBuffers[ 0 ] !== gl.BACK ) {

				drawBuffers[ 0 ] = gl.BACK;

				needsUpdate = true;

			}

		}

		if ( needsUpdate ) {

			gl.drawBuffers( drawBuffers );

		}

	}

	function useProgram( program ) {

		if ( currentProgram !== program ) {

			gl.useProgram( program );

			currentProgram = program;

			return true;

		}

		return false;

	}

	const equationToGL = {
		[ AddEquation ]: gl.FUNC_ADD,
		[ SubtractEquation ]: gl.FUNC_SUBTRACT,
		[ ReverseSubtractEquation ]: gl.FUNC_REVERSE_SUBTRACT
	};

	equationToGL[ MinEquation ] = gl.MIN;
	equationToGL[ MaxEquation ] = gl.MAX;

	const factorToGL = {
		[ ZeroFactor ]: gl.ZERO,
		[ OneFactor ]: gl.ONE,
		[ SrcColorFactor ]: gl.SRC_COLOR,
		[ SrcAlphaFactor ]: gl.SRC_ALPHA,
		[ SrcAlphaSaturateFactor ]: gl.SRC_ALPHA_SATURATE,
		[ DstColorFactor ]: gl.DST_COLOR,
		[ DstAlphaFactor ]: gl.DST_ALPHA,
		[ OneMinusSrcColorFactor ]: gl.ONE_MINUS_SRC_COLOR,
		[ OneMinusSrcAlphaFactor ]: gl.ONE_MINUS_SRC_ALPHA,
		[ OneMinusDstColorFactor ]: gl.ONE_MINUS_DST_COLOR,
		[ OneMinusDstAlphaFactor ]: gl.ONE_MINUS_DST_ALPHA,
		[ ConstantColorFactor ]: gl.CONSTANT_COLOR,
		[ OneMinusConstantColorFactor ]: gl.ONE_MINUS_CONSTANT_COLOR,
		[ ConstantAlphaFactor ]: gl.CONSTANT_ALPHA,
		[ OneMinusConstantAlphaFactor ]: gl.ONE_MINUS_CONSTANT_ALPHA
	};

	function setBlending( blending, blendEquation, blendSrc, blendDst, blendEquationAlpha, blendSrcAlpha, blendDstAlpha, blendColor, blendAlpha, premultipliedAlpha ) {

		if ( blending === NoBlending ) {

			if ( currentBlendingEnabled === true ) {

				disable( gl.BLEND );
				currentBlendingEnabled = false;

			}

			return;

		}

		if ( currentBlendingEnabled === false ) {

			enable( gl.BLEND );
			currentBlendingEnabled = true;

		}

		if ( blending !== CustomBlending ) {

			if ( blending !== currentBlending || premultipliedAlpha !== currentPremultipledAlpha ) {

				if ( currentBlendEquation !== AddEquation || currentBlendEquationAlpha !== AddEquation ) {

					gl.blendEquation( gl.FUNC_ADD );

					currentBlendEquation = AddEquation;
					currentBlendEquationAlpha = AddEquation;

				}

				if ( premultipliedAlpha ) {

					switch ( blending ) {

						case NormalBlending:
							gl.blendFuncSeparate( gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA );
							break;

						case AdditiveBlending:
							gl.blendFunc( gl.ONE, gl.ONE );
							break;

						case SubtractiveBlending:
							gl.blendFuncSeparate( gl.ZERO, gl.ONE_MINUS_SRC_COLOR, gl.ZERO, gl.ONE );
							break;

						case MultiplyBlending:
							gl.blendFuncSeparate( gl.ZERO, gl.SRC_COLOR, gl.ZERO, gl.SRC_ALPHA );
							break;

						default:
							console.error( 'THREE.WebGLState: Invalid blending: ', blending );
							break;

					}

				} else {

					switch ( blending ) {

						case NormalBlending:
							gl.blendFuncSeparate( gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA );
							break;

						case AdditiveBlending:
							gl.blendFunc( gl.SRC_ALPHA, gl.ONE );
							break;

						case SubtractiveBlending:
							gl.blendFuncSeparate( gl.ZERO, gl.ONE_MINUS_SRC_COLOR, gl.ZERO, gl.ONE );
							break;

						case MultiplyBlending:
							gl.blendFunc( gl.ZERO, gl.SRC_COLOR );
							break;

						default:
							console.error( 'THREE.WebGLState: Invalid blending: ', blending );
							break;

					}

				}

				currentBlendSrc = null;
				currentBlendDst = null;
				currentBlendSrcAlpha = null;
				currentBlendDstAlpha = null;
				currentBlendColor.set( 0, 0, 0 );
				currentBlendAlpha = 0;

				currentBlending = blending;
				currentPremultipledAlpha = premultipliedAlpha;

			}

			return;

		}

		// custom blending

		blendEquationAlpha = blendEquationAlpha || blendEquation;
		blendSrcAlpha = blendSrcAlpha || blendSrc;
		blendDstAlpha = blendDstAlpha || blendDst;

		if ( blendEquation !== currentBlendEquation || blendEquationAlpha !== currentBlendEquationAlpha ) {

			gl.blendEquationSeparate( equationToGL[ blendEquation ], equationToGL[ blendEquationAlpha ] );

			currentBlendEquation = blendEquation;
			currentBlendEquationAlpha = blendEquationAlpha;

		}

		if ( blendSrc !== currentBlendSrc || blendDst !== currentBlendDst || blendSrcAlpha !== currentBlendSrcAlpha || blendDstAlpha !== currentBlendDstAlpha ) {

			gl.blendFuncSeparate( factorToGL[ blendSrc ], factorToGL[ blendDst ], factorToGL[ blendSrcAlpha ], factorToGL[ blendDstAlpha ] );

			currentBlendSrc = blendSrc;
			currentBlendDst = blendDst;
			currentBlendSrcAlpha = blendSrcAlpha;
			currentBlendDstAlpha = blendDstAlpha;

		}

		if ( blendColor.equals( currentBlendColor ) === false || blendAlpha !== currentBlendAlpha ) {

			gl.blendColor( blendColor.r, blendColor.g, blendColor.b, blendAlpha );

			currentBlendColor.copy( blendColor );
			currentBlendAlpha = blendAlpha;

		}

		currentBlending = blending;
		currentPremultipledAlpha = false;

	}

	function setMaterial( material, frontFaceCW ) {

		material.side === DoubleSide
			? disable( gl.CULL_FACE )
			: enable( gl.CULL_FACE );

		let flipSided = ( material.side === BackSide );
		if ( frontFaceCW ) flipSided = ! flipSided;

		setFlipSided( flipSided );

		( material.blending === NormalBlending && material.transparent === false )
			? setBlending( NoBlending )
			: setBlending( material.blending, material.blendEquation, material.blendSrc, material.blendDst, material.blendEquationAlpha, material.blendSrcAlpha, material.blendDstAlpha, material.blendColor, material.blendAlpha, material.premultipliedAlpha );

		depthBuffer.setFunc( material.depthFunc );
		depthBuffer.setTest( material.depthTest );
		depthBuffer.setMask( material.depthWrite );
		colorBuffer.setMask( material.colorWrite );

		const stencilWrite = material.stencilWrite;
		stencilBuffer.setTest( stencilWrite );
		if ( stencilWrite ) {

			stencilBuffer.setMask( material.stencilWriteMask );
			stencilBuffer.setFunc( material.stencilFunc, material.stencilRef, material.stencilFuncMask );
			stencilBuffer.setOp( material.stencilFail, material.stencilZFail, material.stencilZPass );

		}

		setPolygonOffset( material.polygonOffset, material.polygonOffsetFactor, material.polygonOffsetUnits );

		material.alphaToCoverage === true
			? enable( gl.SAMPLE_ALPHA_TO_COVERAGE )
			: disable( gl.SAMPLE_ALPHA_TO_COVERAGE );

	}

	//

	function setFlipSided( flipSided ) {

		if ( currentFlipSided !== flipSided ) {

			if ( flipSided ) {

				gl.frontFace( gl.CW );

			} else {

				gl.frontFace( gl.CCW );

			}

			currentFlipSided = flipSided;

		}

	}

	function setCullFace( cullFace ) {

		if ( cullFace !== CullFaceNone ) {

			enable( gl.CULL_FACE );

			if ( cullFace !== currentCullFace ) {

				if ( cullFace === CullFaceBack ) {

					gl.cullFace( gl.BACK );

				} else if ( cullFace === CullFaceFront ) {

					gl.cullFace( gl.FRONT );

				} else {

					gl.cullFace( gl.FRONT_AND_BACK );

				}

			}

		} else {

			disable( gl.CULL_FACE );

		}

		currentCullFace = cullFace;

	}

	function setLineWidth( width ) {

		if ( width !== currentLineWidth ) {

			if ( lineWidthAvailable ) gl.lineWidth( width );

			currentLineWidth = width;

		}

	}

	function setPolygonOffset( polygonOffset, factor, units ) {

		if ( polygonOffset ) {

			enable( gl.POLYGON_OFFSET_FILL );

			if ( currentPolygonOffsetFactor !== factor || currentPolygonOffsetUnits !== units ) {

				gl.polygonOffset( factor, units );

				currentPolygonOffsetFactor = factor;
				currentPolygonOffsetUnits = units;

			}

		} else {

			disable( gl.POLYGON_OFFSET_FILL );

		}

	}

	function setScissorTest( scissorTest ) {

		if ( scissorTest ) {

			enable( gl.SCISSOR_TEST );

		} else {

			disable( gl.SCISSOR_TEST );

		}

	}

	// texture

	function activeTexture( webglSlot ) {

		if ( webglSlot === undefined ) webglSlot = gl.TEXTURE0 + maxTextures - 1;

		if ( currentTextureSlot !== webglSlot ) {

			gl.activeTexture( webglSlot );
			currentTextureSlot = webglSlot;

		}

	}

	function bindTexture( webglType, webglTexture, webglSlot ) {

		if ( webglSlot === undefined ) {

			if ( currentTextureSlot === null ) {

				webglSlot = gl.TEXTURE0 + maxTextures - 1;

			} else {

				webglSlot = currentTextureSlot;

			}

		}

		let boundTexture = currentBoundTextures[ webglSlot ];

		if ( boundTexture === undefined ) {

			boundTexture = { type: undefined, texture: undefined };
			currentBoundTextures[ webglSlot ] = boundTexture;

		}

		if ( boundTexture.type !== webglType || boundTexture.texture !== webglTexture ) {

			if ( currentTextureSlot !== webglSlot ) {

				gl.activeTexture( webglSlot );
				currentTextureSlot = webglSlot;

			}

			gl.bindTexture( webglType, webglTexture || emptyTextures[ webglType ] );

			boundTexture.type = webglType;
			boundTexture.texture = webglTexture;

		}

	}

	function unbindTexture() {

		const boundTexture = currentBoundTextures[ currentTextureSlot ];

		if ( boundTexture !== undefined && boundTexture.type !== undefined ) {

			gl.bindTexture( boundTexture.type, null );

			boundTexture.type = undefined;
			boundTexture.texture = undefined;

		}

	}

	function compressedTexImage2D() {

		try {

			gl.compressedTexImage2D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function compressedTexImage3D() {

		try {

			gl.compressedTexImage3D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texSubImage2D() {

		try {

			gl.texSubImage2D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texSubImage3D() {

		try {

			gl.texSubImage3D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function compressedTexSubImage2D() {

		try {

			gl.compressedTexSubImage2D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function compressedTexSubImage3D() {

		try {

			gl.compressedTexSubImage3D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texStorage2D() {

		try {

			gl.texStorage2D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texStorage3D() {

		try {

			gl.texStorage3D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texImage2D() {

		try {

			gl.texImage2D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	function texImage3D() {

		try {

			gl.texImage3D.apply( gl, arguments );

		} catch ( error ) {

			console.error( 'THREE.WebGLState:', error );

		}

	}

	//

	function scissor( scissor ) {

		if ( currentScissor.equals( scissor ) === false ) {

			gl.scissor( scissor.x, scissor.y, scissor.z, scissor.w );
			currentScissor.copy( scissor );

		}

	}

	function viewport( viewport ) {

		if ( currentViewport.equals( viewport ) === false ) {

			gl.viewport( viewport.x, viewport.y, viewport.z, viewport.w );
			currentViewport.copy( viewport );

		}

	}

	function updateUBOMapping( uniformsGroup, program ) {

		let mapping = uboProgramMap.get( program );

		if ( mapping === undefined ) {

			mapping = new WeakMap();

			uboProgramMap.set( program, mapping );

		}

		let blockIndex = mapping.get( uniformsGroup );

		if ( blockIndex === undefined ) {

			blockIndex = gl.getUniformBlockIndex( program, uniformsGroup.name );

			mapping.set( uniformsGroup, blockIndex );

		}

	}

	function uniformBlockBinding( uniformsGroup, program ) {

		const mapping = uboProgramMap.get( program );
		const blockIndex = mapping.get( uniformsGroup );

		if ( uboBindings.get( program ) !== blockIndex ) {

			// bind shader specific block index to global block point
			gl.uniformBlockBinding( program, blockIndex, uniformsGroup.__bindingPointIndex );

			uboBindings.set( program, blockIndex );

		}

	}

	//

	function reset() {

		// reset state

		gl.disable( gl.BLEND );
		gl.disable( gl.CULL_FACE );
		gl.disable( gl.DEPTH_TEST );
		gl.disable( gl.POLYGON_OFFSET_FILL );
		gl.disable( gl.SCISSOR_TEST );
		gl.disable( gl.STENCIL_TEST );
		gl.disable( gl.SAMPLE_ALPHA_TO_COVERAGE );

		gl.blendEquation( gl.FUNC_ADD );
		gl.blendFunc( gl.ONE, gl.ZERO );
		gl.blendFuncSeparate( gl.ONE, gl.ZERO, gl.ONE, gl.ZERO );
		gl.blendColor( 0, 0, 0, 0 );

		gl.colorMask( true, true, true, true );
		gl.clearColor( 0, 0, 0, 0 );

		gl.depthMask( true );
		gl.depthFunc( gl.LESS );
		gl.clearDepth( 1 );

		gl.stencilMask( 0xffffffff );
		gl.stencilFunc( gl.ALWAYS, 0, 0xffffffff );
		gl.stencilOp( gl.KEEP, gl.KEEP, gl.KEEP );
		gl.clearStencil( 0 );

		gl.cullFace( gl.BACK );
		gl.frontFace( gl.CCW );

		gl.polygonOffset( 0, 0 );

		gl.activeTexture( gl.TEXTURE0 );

		gl.bindFramebuffer( gl.FRAMEBUFFER, null );
		gl.bindFramebuffer( gl.DRAW_FRAMEBUFFER, null );
		gl.bindFramebuffer( gl.READ_FRAMEBUFFER, null );

		gl.useProgram( null );

		gl.lineWidth( 1 );

		gl.scissor( 0, 0, gl.canvas.width, gl.canvas.height );
		gl.viewport( 0, 0, gl.canvas.width, gl.canvas.height );

		// reset internals

		enabledCapabilities = {};

		currentTextureSlot = null;
		currentBoundTextures = {};

		currentBoundFramebuffers = {};
		currentDrawbuffers = new WeakMap();
		defaultDrawbuffers = [];

		currentProgram = null;

		currentBlendingEnabled = false;
		currentBlending = null;
		currentBlendEquation = null;
		currentBlendSrc = null;
		currentBlendDst = null;
		currentBlendEquationAlpha = null;
		currentBlendSrcAlpha = null;
		currentBlendDstAlpha = null;
		currentBlendColor = new Color( 0, 0, 0 );
		currentBlendAlpha = 0;
		currentPremultipledAlpha = false;

		currentFlipSided = null;
		currentCullFace = null;

		currentLineWidth = null;

		currentPolygonOffsetFactor = null;
		currentPolygonOffsetUnits = null;

		currentScissor.set( 0, 0, gl.canvas.width, gl.canvas.height );
		currentViewport.set( 0, 0, gl.canvas.width, gl.canvas.height );

		colorBuffer.reset();
		depthBuffer.reset();
		stencilBuffer.reset();

	}

	return {

		buffers: {
			color: colorBuffer,
			depth: depthBuffer,
			stencil: stencilBuffer
		},

		enable: enable,
		disable: disable,

		bindFramebuffer: bindFramebuffer,
		drawBuffers: drawBuffers,

		useProgram: useProgram,

		setBlending: setBlending,
		setMaterial: setMaterial,

		setFlipSided: setFlipSided,
		setCullFace: setCullFace,

		setLineWidth: setLineWidth,
		setPolygonOffset: setPolygonOffset,

		setScissorTest: setScissorTest,

		activeTexture: activeTexture,
		bindTexture: bindTexture,
		unbindTexture: unbindTexture,
		compressedTexImage2D: compressedTexImage2D,
		compressedTexImage3D: compressedTexImage3D,
		texImage2D: texImage2D,
		texImage3D: texImage3D,

		updateUBOMapping: updateUBOMapping,
		uniformBlockBinding: uniformBlockBinding,

		texStorage2D: texStorage2D,
		texStorage3D: texStorage3D,
		texSubImage2D: texSubImage2D,
		texSubImage3D: texSubImage3D,
		compressedTexSubImage2D: compressedTexSubImage2D,
		compressedTexSubImage3D: compressedTexSubImage3D,

		scissor: scissor,
		viewport: viewport,

		reset: reset

	};

}

function contain( texture, aspect ) {

	const imageAspect = ( texture.image && texture.image.width ) ? texture.image.width / texture.image.height : 1;

	if ( imageAspect > aspect ) {

		texture.repeat.x = 1;
		texture.repeat.y = imageAspect / aspect;

		texture.offset.x = 0;
		texture.offset.y = ( 1 - texture.repeat.y ) / 2;

	} else {

		texture.repeat.x = aspect / imageAspect;
		texture.repeat.y = 1;

		texture.offset.x = ( 1 - texture.repeat.x ) / 2;
		texture.offset.y = 0;

	}

	return texture;

}

function cover( texture, aspect ) {

	const imageAspect = ( texture.image && texture.image.width ) ? texture.image.width / texture.image.height : 1;

	if ( imageAspect > aspect ) {

		texture.repeat.x = aspect / imageAspect;
		texture.repeat.y = 1;

		texture.offset.x = ( 1 - texture.repeat.x ) / 2;
		texture.offset.y = 0;

	} else {

		texture.repeat.x = 1;
		texture.repeat.y = imageAspect / aspect;

		texture.offset.x = 0;
		texture.offset.y = ( 1 - texture.repeat.y ) / 2;

	}

	return texture;

}

function fill( texture ) {

	texture.repeat.x = 1;
	texture.repeat.y = 1;

	texture.offset.x = 0;
	texture.offset.y = 0;

	return texture;

}



/**
 * Given the width, height, format, and type of a texture. Determines how many
 * bytes must be used to represent the texture.
 */
function getByteLength( width, height, format, type ) {

	const typeByteLength = getTextureTypeByteLength( type );

	switch ( format ) {

		// https://registry.khronos.org/OpenGL-Refpages/es3.0/html/glTexImage2D.xhtml
		case AlphaFormat:
			return width * height;
		case LuminanceFormat:
			return width * height;
		case LuminanceAlphaFormat:
			return width * height * 2;
		case RedFormat:
			return ( ( width * height ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RedIntegerFormat:
			return ( ( width * height ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RGFormat:
			return ( ( width * height * 2 ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RGIntegerFormat:
			return ( ( width * height * 2 ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RGBFormat:
			return ( ( width * height * 3 ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RGBAFormat:
			return ( ( width * height * 4 ) / typeByteLength.components ) * typeByteLength.byteLength;
		case RGBAIntegerFormat:
			return ( ( width * height * 4 ) / typeByteLength.components ) * typeByteLength.byteLength;

		// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_s3tc_srgb/
		case RGB_S3TC_DXT1_Format:
		case RGBA_S3TC_DXT1_Format:
			return Math.floor( ( width + 3 ) / 4 ) * Math.floor( ( height + 3 ) / 4 ) * 8;
		case RGBA_S3TC_DXT3_Format:
		case RGBA_S3TC_DXT5_Format:
			return Math.floor( ( width + 3 ) / 4 ) * Math.floor( ( height + 3 ) / 4 ) * 16;

		// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_pvrtc/
		case RGB_PVRTC_2BPPV1_Format:
		case RGBA_PVRTC_2BPPV1_Format:
			return ( Math.max( width, 16 ) * Math.max( height, 8 ) ) / 4;
		case RGB_PVRTC_4BPPV1_Format:
		case RGBA_PVRTC_4BPPV1_Format:
			return ( Math.max( width, 8 ) * Math.max( height, 8 ) ) / 2;

		// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_etc/
		case RGB_ETC1_Format:
		case RGB_ETC2_Format:
			return Math.floor( ( width + 3 ) / 4 ) * Math.floor( ( height + 3 ) / 4 ) * 8;
		case RGBA_ETC2_EAC_Format:
			return Math.floor( ( width + 3 ) / 4 ) * Math.floor( ( height + 3 ) / 4 ) * 16;

		// https://registry.khronos.org/webgl/extensions/WEBGL_compressed_texture_astc/
		case RGBA_ASTC_4x4_Format:
			return Math.floor( ( width + 3 ) / 4 ) * Math.floor( ( height + 3 ) / 4 ) * 16;
		case RGBA_ASTC_5x4_Format:
			return Math.floor( ( width + 4 ) / 5 ) * Math.floor( ( height + 3 ) / 4 ) * 16;
		case RGBA_ASTC_5x5_Format:
			return Math.floor( ( width + 4 ) / 5 ) * Math.floor( ( height + 4 ) / 5 ) * 16;
		case RGBA_ASTC_6x5_Format:
			return Math.floor( ( width + 5 ) / 6 ) * Math.floor( ( height + 4 ) / 5 ) * 16;
		case RGBA_ASTC_6x6_Format:
			return Math.floor( ( width + 5 ) / 6 ) * Math.floor( ( height + 5 ) / 6 ) * 16;
		case RGBA_ASTC_8x5_Format:
			return Math.floor( ( width + 7 ) / 8 ) * Math.floor( ( height + 4 ) / 5 ) * 16;
		case RGBA_ASTC_8x6_Format:
			return Math.floor( ( width + 7 ) / 8 ) * Math.floor( ( height + 5 ) / 6 ) * 16;
		case RGBA_ASTC_8x8_Format:
			return Math.floor( ( width + 7 ) / 8 ) * Math.floor( ( height + 7 ) / 8 ) * 16;
		case RGBA_ASTC_10x5_Format:
			return Math.floor( ( width + 9 ) / 10 ) * Math.floor( ( height + 4 ) / 5 ) * 16;
		case RGBA_ASTC_10x6_Format:
			return Math.floor( ( width + 9 ) / 10 ) * Math.floor( ( height + 5 ) / 6 ) * 16;
		case RGBA_ASTC_10x8_Format:
			return Math.floor( ( width + 9 ) / 10 ) * Math.floor( ( height + 7 ) / 8 ) * 16;
		case RGBA_ASTC_10x10_Format:
			return Math.floor( ( width + 9 ) / 10 ) * Math.floor( ( height + 9 ) / 10 ) * 16;
		case RGBA_ASTC_12x10_Format:
			return Math.floor( ( width + 11 ) / 12 ) * Math.floor( ( height + 9 ) / 10 ) * 16;
		case RGBA_ASTC_12x12_Format:
			return Math.floor( ( width + 11 ) / 12 ) * Math.floor( ( height + 11 ) / 12 ) * 16;

		// https://registry.khronos.org/webgl/extensions/EXT_texture_compression_bptc/
		case RGBA_BPTC_Format:
		case RGB_BPTC_SIGNED_Format:
		case RGB_BPTC_UNSIGNED_Format:
			return Math.ceil( width / 4 ) * Math.ceil( height / 4 ) * 16;

		// https://registry.khronos.org/webgl/extensions/EXT_texture_compression_rgtc/
		case RED_RGTC1_Format:
		case SIGNED_RED_RGTC1_Format:
			return Math.ceil( width / 4 ) * Math.ceil( height / 4 ) * 8;
		case RED_GREEN_RGTC2_Format:
		case SIGNED_RED_GREEN_RGTC2_Format:
			return Math.ceil( width / 4 ) * Math.ceil( height / 4 ) * 16;

	}

	throw new Error(
		`Unable to determine texture byte length for ${format} format.`,
	);

}

function getTextureTypeByteLength( type ) {

	switch ( type ) {

		case UnsignedByteType:
		case ByteType:
			return { byteLength: 1, components: 1 };
		case UnsignedShortType:
		case ShortType:
		case HalfFloatType:
			return { byteLength: 2, components: 1 };
		case UnsignedShort4444Type:
		case UnsignedShort5551Type:
			return { byteLength: 2, components: 4 };
		case UnsignedIntType:
		case IntType:
		case FloatType:
			return { byteLength: 4, components: 1 };
		case UnsignedInt5999Type:
			return { byteLength: 4, components: 3 };

	}

	throw new Error( `Unknown texture type ${type}.` );

}

const TextureUtils = {
	contain,
	cover,
	fill,
	getByteLength
};

function WebGLTextures( _gl, extensions, state, properties, capabilities, utils, info ) {

	const multisampledRTTExt = extensions.has( 'WEBGL_multisampled_render_to_texture' ) ? extensions.get( 'WEBGL_multisampled_render_to_texture' ) : null;
	const supportsInvalidateFramebuffer = typeof navigator === 'undefined' ? false : /OculusBrowser/g.test( navigator.userAgent );

	const _imageDimensions = new Vector2();
	const _videoTextures = new WeakMap();
	let _canvas;

	const _sources = new WeakMap(); // maps WebglTexture objects to instances of Source

	// cordova iOS (as of 5.0) still uses UIWebView, which provides OffscreenCanvas,
	// also OffscreenCanvas.getContext("webgl"), but not OffscreenCanvas.getContext("2d")!
	// Some implementations may only implement OffscreenCanvas partially (e.g. lacking 2d).

	let useOffscreenCanvas = false;

	try {

		useOffscreenCanvas = typeof OffscreenCanvas !== 'undefined'
			// eslint-disable-next-line compat/compat
			&& ( new OffscreenCanvas( 1, 1 ).getContext( '2d' ) ) !== null;

	} catch ( err ) {

		// Ignore any errors

	}

	function createCanvas( width, height ) {

		// Use OffscreenCanvas when available. Specially needed in web workers

		return useOffscreenCanvas ?
			// eslint-disable-next-line compat/compat
			new OffscreenCanvas( width, height ) : createElementNS( 'canvas' );

	}

	function resizeImage( image, needsNewCanvas, maxSize ) {

		let scale = 1;

		const dimensions = getDimensions( image );

		// handle case if texture exceeds max size

		if ( dimensions.width > maxSize || dimensions.height > maxSize ) {

			scale = maxSize / Math.max( dimensions.width, dimensions.height );

		}

		// only perform resize if necessary

		if ( scale < 1 ) {

			// only perform resize for certain image types

			if ( ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) ||
				( typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ) ||
				( typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap ) ||
				( typeof VideoFrame !== 'undefined' && image instanceof VideoFrame ) ) {

				const width = Math.floor( scale * dimensions.width );
				const height = Math.floor( scale * dimensions.height );

				if ( _canvas === undefined ) _canvas = createCanvas( width, height );

				// cube textures can't reuse the same canvas

				const canvas = needsNewCanvas ? createCanvas( width, height ) : _canvas;

				canvas.width = width;
				canvas.height = height;

				const context = canvas.getContext( '2d' );
				context.drawImage( image, 0, 0, width, height );

				console.warn( 'THREE.WebGLRenderer: Texture has been resized from (' + dimensions.width + 'x' + dimensions.height + ') to (' + width + 'x' + height + ').' );

				return canvas;

			} else {

				if ( 'data' in image ) {

					console.warn( 'THREE.WebGLRenderer: Image in DataTexture is too big (' + dimensions.width + 'x' + dimensions.height + ').' );

				}

				return image;

			}

		}

		return image;

	}

	function textureNeedsGenerateMipmaps( texture ) {

		return texture.generateMipmaps && texture.minFilter !== NearestFilter && texture.minFilter !== LinearFilter;

	}

	function generateMipmap( target ) {

		_gl.generateMipmap( target );

	}

	function getInternalFormat( internalFormatName, glFormat, glType, colorSpace, forceLinearTransfer = false ) {

		if ( internalFormatName !== null ) {

			if ( _gl[ internalFormatName ] !== undefined ) return _gl[ internalFormatName ];

			console.warn( 'THREE.WebGLRenderer: Attempt to use non-existing WebGL internal format \'' + internalFormatName + '\'' );

		}

		let internalFormat = glFormat;

		if ( glFormat === _gl.RED ) {

			if ( glType === _gl.FLOAT ) internalFormat = _gl.R32F;
			if ( glType === _gl.HALF_FLOAT ) internalFormat = _gl.R16F;
			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.R8;

		}

		if ( glFormat === _gl.RED_INTEGER ) {

			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.R8UI;
			if ( glType === _gl.UNSIGNED_SHORT ) internalFormat = _gl.R16UI;
			if ( glType === _gl.UNSIGNED_INT ) internalFormat = _gl.R32UI;
			if ( glType === _gl.BYTE ) internalFormat = _gl.R8I;
			if ( glType === _gl.SHORT ) internalFormat = _gl.R16I;
			if ( glType === _gl.INT ) internalFormat = _gl.R32I;

		}

		if ( glFormat === _gl.RG ) {

			if ( glType === _gl.FLOAT ) internalFormat = _gl.RG32F;
			if ( glType === _gl.HALF_FLOAT ) internalFormat = _gl.RG16F;
			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.RG8;

		}

		if ( glFormat === _gl.RG_INTEGER ) {

			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.RG8UI;
			if ( glType === _gl.UNSIGNED_SHORT ) internalFormat = _gl.RG16UI;
			if ( glType === _gl.UNSIGNED_INT ) internalFormat = _gl.RG32UI;
			if ( glType === _gl.BYTE ) internalFormat = _gl.RG8I;
			if ( glType === _gl.SHORT ) internalFormat = _gl.RG16I;
			if ( glType === _gl.INT ) internalFormat = _gl.RG32I;

		}

		if ( glFormat === _gl.RGB_INTEGER ) {

			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.RGB8UI;
			if ( glType === _gl.UNSIGNED_SHORT ) internalFormat = _gl.RGB16UI;
			if ( glType === _gl.UNSIGNED_INT ) internalFormat = _gl.RGB32UI;
			if ( glType === _gl.BYTE ) internalFormat = _gl.RGB8I;
			if ( glType === _gl.SHORT ) internalFormat = _gl.RGB16I;
			if ( glType === _gl.INT ) internalFormat = _gl.RGB32I;

		}

		if ( glFormat === _gl.RGBA_INTEGER ) {

			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = _gl.RGBA8UI;
			if ( glType === _gl.UNSIGNED_SHORT ) internalFormat = _gl.RGBA16UI;
			if ( glType === _gl.UNSIGNED_INT ) internalFormat = _gl.RGBA32UI;
			if ( glType === _gl.BYTE ) internalFormat = _gl.RGBA8I;
			if ( glType === _gl.SHORT ) internalFormat = _gl.RGBA16I;
			if ( glType === _gl.INT ) internalFormat = _gl.RGBA32I;

		}

		if ( glFormat === _gl.RGB ) {

			if ( glType === _gl.UNSIGNED_INT_5_9_9_9_REV ) internalFormat = _gl.RGB9_E5;

		}

		if ( glFormat === _gl.RGBA ) {

			const transfer = forceLinearTransfer ? LinearTransfer : ColorManagement.getTransfer( colorSpace );

			if ( glType === _gl.FLOAT ) internalFormat = _gl.RGBA32F;
			if ( glType === _gl.HALF_FLOAT ) internalFormat = _gl.RGBA16F;
			if ( glType === _gl.UNSIGNED_BYTE ) internalFormat = ( transfer === SRGBTransfer ) ? _gl.SRGB8_ALPHA8 : _gl.RGBA8;
			if ( glType === _gl.UNSIGNED_SHORT_4_4_4_4 ) internalFormat = _gl.RGBA4;
			if ( glType === _gl.UNSIGNED_SHORT_5_5_5_1 ) internalFormat = _gl.RGB5_A1;

		}

		if ( internalFormat === _gl.R16F || internalFormat === _gl.R32F ||
			internalFormat === _gl.RG16F || internalFormat === _gl.RG32F ||
			internalFormat === _gl.RGBA16F || internalFormat === _gl.RGBA32F ) {

			extensions.get( 'EXT_color_buffer_float' );

		}

		return internalFormat;

	}

	function getInternalDepthFormat( useStencil, depthType ) {

		let glInternalFormat;
		if ( useStencil ) {

			if ( depthType === null || depthType === UnsignedIntType || depthType === UnsignedInt248Type ) {

				glInternalFormat = _gl.DEPTH24_STENCIL8;

			} else if ( depthType === FloatType ) {

				glInternalFormat = _gl.DEPTH32F_STENCIL8;

			} else if ( depthType === UnsignedShortType ) {

				glInternalFormat = _gl.DEPTH24_STENCIL8;
				console.warn( 'DepthTexture: 16 bit depth attachment is not supported with stencil. Using 24-bit attachment.' );

			}

		} else {

			if ( depthType === null || depthType === UnsignedIntType || depthType === UnsignedInt248Type ) {

				glInternalFormat = _gl.DEPTH_COMPONENT24;

			} else if ( depthType === FloatType ) {

				glInternalFormat = _gl.DEPTH_COMPONENT32F;

			} else if ( depthType === UnsignedShortType ) {

				glInternalFormat = _gl.DEPTH_COMPONENT16;

			}

		}

		return glInternalFormat;

	}

	function getMipLevels( texture, image ) {

		if ( textureNeedsGenerateMipmaps( texture ) === true || ( texture.isFramebufferTexture && texture.minFilter !== NearestFilter && texture.minFilter !== LinearFilter ) ) {

			return Math.log2( Math.max( image.width, image.height ) ) + 1;

		} else if ( texture.mipmaps !== undefined && texture.mipmaps.length > 0 ) {

			// user-defined mipmaps

			return texture.mipmaps.length;

		} else if ( texture.isCompressedTexture && Array.isArray( texture.image ) ) {

			return image.mipmaps.length;

		} else {

			// texture without mipmaps (only base level)

			return 1;

		}

	}

	//

	function onTextureDispose( event ) {

		const texture = event.target;

		texture.removeEventListener( 'dispose', onTextureDispose );

		deallocateTexture( texture );

		if ( texture.isVideoTexture ) {

			_videoTextures.delete( texture );

		}

	}

	function onRenderTargetDispose( event ) {

		const renderTarget = event.target;

		renderTarget.removeEventListener( 'dispose', onRenderTargetDispose );

		deallocateRenderTarget( renderTarget );

	}

	//

	function deallocateTexture( texture ) {

		const textureProperties = properties.get( texture );

		if ( textureProperties.__webglInit === undefined ) return;

		// check if it's necessary to remove the WebGLTexture object

		const source = texture.source;
		const webglTextures = _sources.get( source );

		if ( webglTextures ) {

			const webglTexture = webglTextures[ textureProperties.__cacheKey ];
			webglTexture.usedTimes --;

			// the WebGLTexture object is not used anymore, remove it

			if ( webglTexture.usedTimes === 0 ) {

				deleteTexture( texture );

			}

			// remove the weak map entry if no WebGLTexture uses the source anymore

			if ( Object.keys( webglTextures ).length === 0 ) {

				_sources.delete( source );

			}

		}

		properties.remove( texture );

	}

	function deleteTexture( texture ) {

		const textureProperties = properties.get( texture );
		_gl.deleteTexture( textureProperties.__webglTexture );

		const source = texture.source;
		const webglTextures = _sources.get( source );
		delete webglTextures[ textureProperties.__cacheKey ];

		info.memory.textures --;

	}

	function deallocateRenderTarget( renderTarget ) {

		const renderTargetProperties = properties.get( renderTarget );

		if ( renderTarget.depthTexture ) {

			renderTarget.depthTexture.dispose();

		}

		if ( renderTarget.isWebGLCubeRenderTarget ) {

			for ( let i = 0; i < 6; i ++ ) {

				if ( Array.isArray( renderTargetProperties.__webglFramebuffer[ i ] ) ) {

					for ( let level = 0; level < renderTargetProperties.__webglFramebuffer[ i ].length; level ++ ) _gl.deleteFramebuffer( renderTargetProperties.__webglFramebuffer[ i ][ level ] );

				} else {

					_gl.deleteFramebuffer( renderTargetProperties.__webglFramebuffer[ i ] );

				}

				if ( renderTargetProperties.__webglDepthbuffer ) _gl.deleteRenderbuffer( renderTargetProperties.__webglDepthbuffer[ i ] );

			}

		} else {

			if ( Array.isArray( renderTargetProperties.__webglFramebuffer ) ) {

				for ( let level = 0; level < renderTargetProperties.__webglFramebuffer.length; level ++ ) _gl.deleteFramebuffer( renderTargetProperties.__webglFramebuffer[ level ] );

			} else {

				_gl.deleteFramebuffer( renderTargetProperties.__webglFramebuffer );

			}

			if ( renderTargetProperties.__webglDepthbuffer ) _gl.deleteRenderbuffer( renderTargetProperties.__webglDepthbuffer );
			if ( renderTargetProperties.__webglMultisampledFramebuffer ) _gl.deleteFramebuffer( renderTargetProperties.__webglMultisampledFramebuffer );

			if ( renderTargetProperties.__webglColorRenderbuffer ) {

				for ( let i = 0; i < renderTargetProperties.__webglColorRenderbuffer.length; i ++ ) {

					if ( renderTargetProperties.__webglColorRenderbuffer[ i ] ) _gl.deleteRenderbuffer( renderTargetProperties.__webglColorRenderbuffer[ i ] );

				}

			}

			if ( renderTargetProperties.__webglDepthRenderbuffer ) _gl.deleteRenderbuffer( renderTargetProperties.__webglDepthRenderbuffer );

		}

		const textures = renderTarget.textures;

		for ( let i = 0, il = textures.length; i < il; i ++ ) {

			const attachmentProperties = properties.get( textures[ i ] );

			if ( attachmentProperties.__webglTexture ) {

				_gl.deleteTexture( attachmentProperties.__webglTexture );

				info.memory.textures --;

			}

			properties.remove( textures[ i ] );

		}

		properties.remove( renderTarget );

	}

	//

	let textureUnits = 0;

	function resetTextureUnits() {

		textureUnits = 0;

	}

	function allocateTextureUnit() {

		const textureUnit = textureUnits;

		if ( textureUnit >= capabilities.maxTextures ) {

			console.warn( 'THREE.WebGLTextures: Trying to use ' + textureUnit + ' texture units while this GPU supports only ' + capabilities.maxTextures );

		}

		textureUnits += 1;

		return textureUnit;

	}

	function getTextureCacheKey( texture ) {

		const array = [];

		array.push( texture.wrapS );
		array.push( texture.wrapT );
		array.push( texture.wrapR || 0 );
		array.push( texture.magFilter );
		array.push( texture.minFilter );
		array.push( texture.anisotropy );
		array.push( texture.internalFormat );
		array.push( texture.format );
		array.push( texture.type );
		array.push( texture.generateMipmaps );
		array.push( texture.premultiplyAlpha );
		array.push( texture.flipY );
		array.push( texture.unpackAlignment );
		array.push( texture.colorSpace );

		return array.join();

	}

	//

	function setTexture2D( texture, slot ) {

		const textureProperties = properties.get( texture );

		if ( texture.isVideoTexture ) updateVideoTexture( texture );

		if ( texture.isRenderTargetTexture === false && texture.version > 0 && textureProperties.__version !== texture.version ) {

			const image = texture.image;

			if ( image === null ) {

				console.warn( 'THREE.WebGLRenderer: Texture marked for update but no image data found.' );

			} else if ( image.complete === false ) {

				console.warn( 'THREE.WebGLRenderer: Texture marked for update but image is incomplete' );

			} else {

				uploadTexture( textureProperties, texture, slot );
				return;

			}

		}

		state.bindTexture( _gl.TEXTURE_2D, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

	}

	function setTexture2DArray( texture, slot ) {

		const textureProperties = properties.get( texture );

		if ( texture.version > 0 && textureProperties.__version !== texture.version ) {

			uploadTexture( textureProperties, texture, slot );
			return;

		}

		state.bindTexture( _gl.TEXTURE_2D_ARRAY, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

	}

	function setTexture3D( texture, slot ) {

		const textureProperties = properties.get( texture );

		if ( texture.version > 0 && textureProperties.__version !== texture.version ) {

			uploadTexture( textureProperties, texture, slot );
			return;

		}

		state.bindTexture( _gl.TEXTURE_3D, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

	}

	function setTextureCube( texture, slot ) {

		const textureProperties = properties.get( texture );

		if ( texture.version > 0 && textureProperties.__version !== texture.version ) {

			uploadCubeTexture( textureProperties, texture, slot );
			return;

		}

		state.bindTexture( _gl.TEXTURE_CUBE_MAP, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

	}

	const wrappingToGL = {
		[ RepeatWrapping ]: _gl.REPEAT,
		[ ClampToEdgeWrapping ]: _gl.CLAMP_TO_EDGE,
		[ MirroredRepeatWrapping ]: _gl.MIRRORED_REPEAT
	};

	const filterToGL = {
		[ NearestFilter ]: _gl.NEAREST,
		[ NearestMipmapNearestFilter ]: _gl.NEAREST_MIPMAP_NEAREST,
		[ NearestMipmapLinearFilter ]: _gl.NEAREST_MIPMAP_LINEAR,

		[ LinearFilter ]: _gl.LINEAR,
		[ LinearMipmapNearestFilter ]: _gl.LINEAR_MIPMAP_NEAREST,
		[ LinearMipmapLinearFilter ]: _gl.LINEAR_MIPMAP_LINEAR
	};

	const compareToGL = {
		[ NeverCompare ]: _gl.NEVER,
		[ AlwaysCompare ]: _gl.ALWAYS,
		[ LessCompare ]: _gl.LESS,
		[ LessEqualCompare ]: _gl.LEQUAL,
		[ EqualCompare ]: _gl.EQUAL,
		[ GreaterEqualCompare ]: _gl.GEQUAL,
		[ GreaterCompare ]: _gl.GREATER,
		[ NotEqualCompare ]: _gl.NOTEQUAL
	};

	function setTextureParameters( textureType, texture ) {

		if ( texture.type === FloatType && extensions.has( 'OES_texture_float_linear' ) === false &&
			( texture.magFilter === LinearFilter || texture.magFilter === LinearMipmapNearestFilter || texture.magFilter === NearestMipmapLinearFilter || texture.magFilter === LinearMipmapLinearFilter ||
			texture.minFilter === LinearFilter || texture.minFilter === LinearMipmapNearestFilter || texture.minFilter === NearestMipmapLinearFilter || texture.minFilter === LinearMipmapLinearFilter ) ) {

			console.warn( 'THREE.WebGLRenderer: Unable to use linear filtering with floating point textures. OES_texture_float_linear not supported on this device.' );

		}

		_gl.texParameteri( textureType, _gl.TEXTURE_WRAP_S, wrappingToGL[ texture.wrapS ] );
		_gl.texParameteri( textureType, _gl.TEXTURE_WRAP_T, wrappingToGL[ texture.wrapT ] );

		if ( textureType === _gl.TEXTURE_3D || textureType === _gl.TEXTURE_2D_ARRAY ) {

			_gl.texParameteri( textureType, _gl.TEXTURE_WRAP_R, wrappingToGL[ texture.wrapR ] );

		}

		_gl.texParameteri( textureType, _gl.TEXTURE_MAG_FILTER, filterToGL[ texture.magFilter ] );
		_gl.texParameteri( textureType, _gl.TEXTURE_MIN_FILTER, filterToGL[ texture.minFilter ] );

		if ( texture.compareFunction ) {

			_gl.texParameteri( textureType, _gl.TEXTURE_COMPARE_MODE, _gl.COMPARE_REF_TO_TEXTURE );
			_gl.texParameteri( textureType, _gl.TEXTURE_COMPARE_FUNC, compareToGL[ texture.compareFunction ] );

		}

		if ( extensions.has( 'EXT_texture_filter_anisotropic' ) === true ) {

			if ( texture.magFilter === NearestFilter ) return;
			if ( texture.minFilter !== NearestMipmapLinearFilter && texture.minFilter !== LinearMipmapLinearFilter ) return;
			if ( texture.type === FloatType && extensions.has( 'OES_texture_float_linear' ) === false ) return; // verify extension

			if ( texture.anisotropy > 1 || properties.get( texture ).__currentAnisotropy ) {

				const extension = extensions.get( 'EXT_texture_filter_anisotropic' );
				_gl.texParameterf( textureType, extension.TEXTURE_MAX_ANISOTROPY_EXT, Math.min( texture.anisotropy, capabilities.getMaxAnisotropy() ) );
				properties.get( texture ).__currentAnisotropy = texture.anisotropy;

			}

		}

	}

	function initTexture( textureProperties, texture ) {

		let forceUpload = false;

		if ( textureProperties.__webglInit === undefined ) {

			textureProperties.__webglInit = true;

			texture.addEventListener( 'dispose', onTextureDispose );

		}

		// create Source <-> WebGLTextures mapping if necessary

		const source = texture.source;
		let webglTextures = _sources.get( source );

		if ( webglTextures === undefined ) {

			webglTextures = {};
			_sources.set( source, webglTextures );

		}

		// check if there is already a WebGLTexture object for the given texture parameters

		const textureCacheKey = getTextureCacheKey( texture );

		if ( textureCacheKey !== textureProperties.__cacheKey ) {

			// if not, create a new instance of WebGLTexture

			if ( webglTextures[ textureCacheKey ] === undefined ) {

				// create new entry

				webglTextures[ textureCacheKey ] = {
					texture: _gl.createTexture(),
					usedTimes: 0
				};

				info.memory.textures ++;

				// when a new instance of WebGLTexture was created, a texture upload is required
				// even if the image contents are identical

				forceUpload = true;

			}

			webglTextures[ textureCacheKey ].usedTimes ++;

			// every time the texture cache key changes, it's necessary to check if an instance of
			// WebGLTexture can be deleted in order to avoid a memory leak.

			const webglTexture = webglTextures[ textureProperties.__cacheKey ];

			if ( webglTexture !== undefined ) {

				webglTextures[ textureProperties.__cacheKey ].usedTimes --;

				if ( webglTexture.usedTimes === 0 ) {

					deleteTexture( texture );

				}

			}

			// store references to cache key and WebGLTexture object

			textureProperties.__cacheKey = textureCacheKey;
			textureProperties.__webglTexture = webglTextures[ textureCacheKey ].texture;

		}

		return forceUpload;

	}

	function uploadTexture( textureProperties, texture, slot ) {

		let textureType = _gl.TEXTURE_2D;

		if ( texture.isDataArrayTexture || texture.isCompressedArrayTexture ) textureType = _gl.TEXTURE_2D_ARRAY;
		if ( texture.isData3DTexture ) textureType = _gl.TEXTURE_3D;

		const forceUpload = initTexture( textureProperties, texture );
		const source = texture.source;

		state.bindTexture( textureType, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

		const sourceProperties = properties.get( source );

		if ( source.version !== sourceProperties.__version || forceUpload === true ) {

			state.activeTexture( _gl.TEXTURE0 + slot );

			const workingPrimaries = ColorManagement.getPrimaries( ColorManagement.workingColorSpace );
			const texturePrimaries = texture.colorSpace === NoColorSpace ? null : ColorManagement.getPrimaries( texture.colorSpace );
			const unpackConversion = texture.colorSpace === NoColorSpace || workingPrimaries === texturePrimaries ? _gl.NONE : _gl.BROWSER_DEFAULT_WEBGL;

			_gl.pixelStorei( _gl.UNPACK_FLIP_Y_WEBGL, texture.flipY );
			_gl.pixelStorei( _gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, texture.premultiplyAlpha );
			_gl.pixelStorei( _gl.UNPACK_ALIGNMENT, texture.unpackAlignment );
			_gl.pixelStorei( _gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, unpackConversion );

			let image = resizeImage( texture.image, false, capabilities.maxTextureSize );
			image = verifyColorSpace( texture, image );

			const glFormat = utils.convert( texture.format, texture.colorSpace );

			const glType = utils.convert( texture.type );
			let glInternalFormat = getInternalFormat( texture.internalFormat, glFormat, glType, texture.colorSpace, texture.isVideoTexture );

			setTextureParameters( textureType, texture );

			let mipmap;
			const mipmaps = texture.mipmaps;

			const useTexStorage = ( texture.isVideoTexture !== true );
			const allocateMemory = ( sourceProperties.__version === undefined ) || ( forceUpload === true );
			const dataReady = source.dataReady;
			const levels = getMipLevels( texture, image );

			if ( texture.isDepthTexture ) {

				glInternalFormat = getInternalDepthFormat( texture.format === DepthStencilFormat, texture.type );

				//

				if ( allocateMemory ) {

					if ( useTexStorage ) {

						state.texStorage2D( _gl.TEXTURE_2D, 1, glInternalFormat, image.width, image.height );

					} else {

						state.texImage2D( _gl.TEXTURE_2D, 0, glInternalFormat, image.width, image.height, 0, glFormat, glType, null );

					}

				}

			} else if ( texture.isDataTexture ) {

				// use manually created mipmaps if available
				// if there are no manual mipmaps
				// set 0 level mipmap and then use GL to generate other mipmap levels

				if ( mipmaps.length > 0 ) {

					if ( useTexStorage && allocateMemory ) {

						state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, mipmaps[ 0 ].width, mipmaps[ 0 ].height );

					}

					for ( let i = 0, il = mipmaps.length; i < il; i ++ ) {

						mipmap = mipmaps[ i ];

						if ( useTexStorage ) {

							if ( dataReady ) {

								state.texSubImage2D( _gl.TEXTURE_2D, i, 0, 0, mipmap.width, mipmap.height, glFormat, glType, mipmap.data );

							}

						} else {

							state.texImage2D( _gl.TEXTURE_2D, i, glInternalFormat, mipmap.width, mipmap.height, 0, glFormat, glType, mipmap.data );

						}

					}

					texture.generateMipmaps = false;

				} else {

					if ( useTexStorage ) {

						if ( allocateMemory ) {

							state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, image.width, image.height );

						}

						if ( dataReady ) {

							state.texSubImage2D( _gl.TEXTURE_2D, 0, 0, 0, image.width, image.height, glFormat, glType, image.data );

						}

					} else {

						state.texImage2D( _gl.TEXTURE_2D, 0, glInternalFormat, image.width, image.height, 0, glFormat, glType, image.data );

					}

				}

			} else if ( texture.isCompressedTexture ) {

				if ( texture.isCompressedArrayTexture ) {

					if ( useTexStorage && allocateMemory ) {

						state.texStorage3D( _gl.TEXTURE_2D_ARRAY, levels, glInternalFormat, mipmaps[ 0 ].width, mipmaps[ 0 ].height, image.depth );

					}

					for ( let i = 0, il = mipmaps.length; i < il; i ++ ) {

						mipmap = mipmaps[ i ];

						if ( texture.format !== RGBAFormat ) {

							if ( glFormat !== null ) {

								if ( useTexStorage ) {

									if ( dataReady ) {

										if ( texture.layerUpdates.size > 0 ) {

											const layerByteLength = getByteLength( mipmap.width, mipmap.height, texture.format, texture.type );

											for ( const layerIndex of texture.layerUpdates ) {

												const layerData = mipmap.data.subarray(
													layerIndex * layerByteLength / mipmap.data.BYTES_PER_ELEMENT,
													( layerIndex + 1 ) * layerByteLength / mipmap.data.BYTES_PER_ELEMENT
												);
												state.compressedTexSubImage3D( _gl.TEXTURE_2D_ARRAY, i, 0, 0, layerIndex, mipmap.width, mipmap.height, 1, glFormat, layerData, 0, 0 );

											}

											texture.clearLayerUpdates();

										} else {

											state.compressedTexSubImage3D( _gl.TEXTURE_2D_ARRAY, i, 0, 0, 0, mipmap.width, mipmap.height, image.depth, glFormat, mipmap.data, 0, 0 );

										}

									}

								} else {

									state.compressedTexImage3D( _gl.TEXTURE_2D_ARRAY, i, glInternalFormat, mipmap.width, mipmap.height, image.depth, 0, mipmap.data, 0, 0 );

								}

							} else {

								console.warn( 'THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()' );

							}

						} else {

							if ( useTexStorage ) {

								if ( dataReady ) {

									state.texSubImage3D( _gl.TEXTURE_2D_ARRAY, i, 0, 0, 0, mipmap.width, mipmap.height, image.depth, glFormat, glType, mipmap.data );

								}

							} else {

								state.texImage3D( _gl.TEXTURE_2D_ARRAY, i, glInternalFormat, mipmap.width, mipmap.height, image.depth, 0, glFormat, glType, mipmap.data );

							}

						}

					}

				} else {

					if ( useTexStorage && allocateMemory ) {

						state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, mipmaps[ 0 ].width, mipmaps[ 0 ].height );

					}

					for ( let i = 0, il = mipmaps.length; i < il; i ++ ) {

						mipmap = mipmaps[ i ];

						if ( texture.format !== RGBAFormat ) {

							if ( glFormat !== null ) {

								if ( useTexStorage ) {

									if ( dataReady ) {

										state.compressedTexSubImage2D( _gl.TEXTURE_2D, i, 0, 0, mipmap.width, mipmap.height, glFormat, mipmap.data );

									}

								} else {

									state.compressedTexImage2D( _gl.TEXTURE_2D, i, glInternalFormat, mipmap.width, mipmap.height, 0, mipmap.data );

								}

							} else {

								console.warn( 'THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .uploadTexture()' );

							}

						} else {

							if ( useTexStorage ) {

								if ( dataReady ) {

									state.texSubImage2D( _gl.TEXTURE_2D, i, 0, 0, mipmap.width, mipmap.height, glFormat, glType, mipmap.data );

								}

							} else {

								state.texImage2D( _gl.TEXTURE_2D, i, glInternalFormat, mipmap.width, mipmap.height, 0, glFormat, glType, mipmap.data );

							}

						}

					}

				}

			} else if ( texture.isDataArrayTexture ) {

				if ( useTexStorage ) {

					if ( allocateMemory ) {

						state.texStorage3D( _gl.TEXTURE_2D_ARRAY, levels, glInternalFormat, image.width, image.height, image.depth );

					}

					if ( dataReady ) {

						if ( texture.layerUpdates.size > 0 ) {

							const layerByteLength = getByteLength( image.width, image.height, texture.format, texture.type );

							for ( const layerIndex of texture.layerUpdates ) {

								const layerData = image.data.subarray(
									layerIndex * layerByteLength / image.data.BYTES_PER_ELEMENT,
									( layerIndex + 1 ) * layerByteLength / image.data.BYTES_PER_ELEMENT
								);
								state.texSubImage3D( _gl.TEXTURE_2D_ARRAY, 0, 0, 0, layerIndex, image.width, image.height, 1, glFormat, glType, layerData );

							}

							texture.clearLayerUpdates();

						} else {

							state.texSubImage3D( _gl.TEXTURE_2D_ARRAY, 0, 0, 0, 0, image.width, image.height, image.depth, glFormat, glType, image.data );

						}

					}

				} else {

					state.texImage3D( _gl.TEXTURE_2D_ARRAY, 0, glInternalFormat, image.width, image.height, image.depth, 0, glFormat, glType, image.data );

				}

			} else if ( texture.isData3DTexture ) {

				if ( useTexStorage ) {

					if ( allocateMemory ) {

						state.texStorage3D( _gl.TEXTURE_3D, levels, glInternalFormat, image.width, image.height, image.depth );

					}

					if ( dataReady ) {

						state.texSubImage3D( _gl.TEXTURE_3D, 0, 0, 0, 0, image.width, image.height, image.depth, glFormat, glType, image.data );

					}

				} else {

					state.texImage3D( _gl.TEXTURE_3D, 0, glInternalFormat, image.width, image.height, image.depth, 0, glFormat, glType, image.data );

				}

			} else if ( texture.isFramebufferTexture ) {

				if ( allocateMemory ) {

					if ( useTexStorage ) {

						state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, image.width, image.height );

					} else {

						let width = image.width, height = image.height;

						for ( let i = 0; i < levels; i ++ ) {

							state.texImage2D( _gl.TEXTURE_2D, i, glInternalFormat, width, height, 0, glFormat, glType, null );

							width >>= 1;
							height >>= 1;

						}

					}

				}

			} else {

				// regular Texture (image, video, canvas)

				// use manually created mipmaps if available
				// if there are no manual mipmaps
				// set 0 level mipmap and then use GL to generate other mipmap levels

				if ( mipmaps.length > 0 ) {

					if ( useTexStorage && allocateMemory ) {

						const dimensions = getDimensions( mipmaps[ 0 ] );

						state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, dimensions.width, dimensions.height );

					}

					for ( let i = 0, il = mipmaps.length; i < il; i ++ ) {

						mipmap = mipmaps[ i ];

						if ( useTexStorage ) {

							if ( dataReady ) {

								state.texSubImage2D( _gl.TEXTURE_2D, i, 0, 0, glFormat, glType, mipmap );

							}

						} else {

							state.texImage2D( _gl.TEXTURE_2D, i, glInternalFormat, glFormat, glType, mipmap );

						}

					}

					texture.generateMipmaps = false;

				} else {

					if ( useTexStorage ) {

						if ( allocateMemory ) {

							const dimensions = getDimensions( image );

							state.texStorage2D( _gl.TEXTURE_2D, levels, glInternalFormat, dimensions.width, dimensions.height );

						}

						if ( dataReady ) {

							state.texSubImage2D( _gl.TEXTURE_2D, 0, 0, 0, glFormat, glType, image );

						}

					} else {

						state.texImage2D( _gl.TEXTURE_2D, 0, glInternalFormat, glFormat, glType, image );

					}

				}

			}

			if ( textureNeedsGenerateMipmaps( texture ) ) {

				generateMipmap( textureType );

			}

			sourceProperties.__version = source.version;

			if ( texture.onUpdate ) texture.onUpdate( texture );

		}

		textureProperties.__version = texture.version;

	}

	function uploadCubeTexture( textureProperties, texture, slot ) {

		if ( texture.image.length !== 6 ) return;

		const forceUpload = initTexture( textureProperties, texture );
		const source = texture.source;

		state.bindTexture( _gl.TEXTURE_CUBE_MAP, textureProperties.__webglTexture, _gl.TEXTURE0 + slot );

		const sourceProperties = properties.get( source );

		if ( source.version !== sourceProperties.__version || forceUpload === true ) {

			state.activeTexture( _gl.TEXTURE0 + slot );

			const workingPrimaries = ColorManagement.getPrimaries( ColorManagement.workingColorSpace );
			const texturePrimaries = texture.colorSpace === NoColorSpace ? null : ColorManagement.getPrimaries( texture.colorSpace );
			const unpackConversion = texture.colorSpace === NoColorSpace || workingPrimaries === texturePrimaries ? _gl.NONE : _gl.BROWSER_DEFAULT_WEBGL;

			_gl.pixelStorei( _gl.UNPACK_FLIP_Y_WEBGL, texture.flipY );
			_gl.pixelStorei( _gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, texture.premultiplyAlpha );
			_gl.pixelStorei( _gl.UNPACK_ALIGNMENT, texture.unpackAlignment );
			_gl.pixelStorei( _gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, unpackConversion );

			const isCompressed = ( texture.isCompressedTexture || texture.image[ 0 ].isCompressedTexture );
			const isDataTexture = ( texture.image[ 0 ] && texture.image[ 0 ].isDataTexture );

			const cubeImage = [];

			for ( let i = 0; i < 6; i ++ ) {

				if ( ! isCompressed && ! isDataTexture ) {

					cubeImage[ i ] = resizeImage( texture.image[ i ], true, capabilities.maxCubemapSize );

				} else {

					cubeImage[ i ] = isDataTexture ? texture.image[ i ].image : texture.image[ i ];

				}

				cubeImage[ i ] = verifyColorSpace( texture, cubeImage[ i ] );

			}

			const image = cubeImage[ 0 ],
				glFormat = utils.convert( texture.format, texture.colorSpace ),
				glType = utils.convert( texture.type ),
				glInternalFormat = getInternalFormat( texture.internalFormat, glFormat, glType, texture.colorSpace );

			const useTexStorage = ( texture.isVideoTexture !== true );
			const allocateMemory = ( sourceProperties.__version === undefined ) || ( forceUpload === true );
			const dataReady = source.dataReady;
			let levels = getMipLevels( texture, image );

			setTextureParameters( _gl.TEXTURE_CUBE_MAP, texture );

			let mipmaps;

			if ( isCompressed ) {

				if ( useTexStorage && allocateMemory ) {

					state.texStorage2D( _gl.TEXTURE_CUBE_MAP, levels, glInternalFormat, image.width, image.height );

				}

				for ( let i = 0; i < 6; i ++ ) {

					mipmaps = cubeImage[ i ].mipmaps;

					for ( let j = 0; j < mipmaps.length; j ++ ) {

						const mipmap = mipmaps[ j ];

						if ( texture.format !== RGBAFormat ) {

							if ( glFormat !== null ) {

								if ( useTexStorage ) {

									if ( dataReady ) {

										state.compressedTexSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j, 0, 0, mipmap.width, mipmap.height, glFormat, mipmap.data );

									}

								} else {

									state.compressedTexImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j, glInternalFormat, mipmap.width, mipmap.height, 0, mipmap.data );

								}

							} else {

								console.warn( 'THREE.WebGLRenderer: Attempt to load unsupported compressed texture format in .setTextureCube()' );

							}

						} else {

							if ( useTexStorage ) {

								if ( dataReady ) {

									state.texSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j, 0, 0, mipmap.width, mipmap.height, glFormat, glType, mipmap.data );

								}

							} else {

								state.texImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j, glInternalFormat, mipmap.width, mipmap.height, 0, glFormat, glType, mipmap.data );

							}

						}

					}

				}

			} else {

				mipmaps = texture.mipmaps;

				if ( useTexStorage && allocateMemory ) {

					// TODO: Uniformly handle mipmap definitions
					// Normal textures and compressed cube textures define base level + mips with their mipmap array
					// Uncompressed cube textures use their mipmap array only for mips (no base level)

					if ( mipmaps.length > 0 ) levels ++;

					const dimensions = getDimensions( cubeImage[ 0 ] );

					state.texStorage2D( _gl.TEXTURE_CUBE_MAP, levels, glInternalFormat, dimensions.width, dimensions.height );

				}

				for ( let i = 0; i < 6; i ++ ) {

					if ( isDataTexture ) {

						if ( useTexStorage ) {

							if ( dataReady ) {

								state.texSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0, 0, 0, cubeImage[ i ].width, cubeImage[ i ].height, glFormat, glType, cubeImage[ i ].data );

							}

						} else {

							state.texImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0, glInternalFormat, cubeImage[ i ].width, cubeImage[ i ].height, 0, glFormat, glType, cubeImage[ i ].data );

						}

						for ( let j = 0; j < mipmaps.length; j ++ ) {

							const mipmap = mipmaps[ j ];
							const mipmapImage = mipmap.image[ i ].image;

							if ( useTexStorage ) {

								if ( dataReady ) {

									state.texSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j + 1, 0, 0, mipmapImage.width, mipmapImage.height, glFormat, glType, mipmapImage.data );

								}

							} else {

								state.texImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j + 1, glInternalFormat, mipmapImage.width, mipmapImage.height, 0, glFormat, glType, mipmapImage.data );

							}

						}

					} else {

						if ( useTexStorage ) {

							if ( dataReady ) {

								state.texSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0, 0, 0, glFormat, glType, cubeImage[ i ] );

							}

						} else {

							state.texImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0, glInternalFormat, glFormat, glType, cubeImage[ i ] );

						}

						for ( let j = 0; j < mipmaps.length; j ++ ) {

							const mipmap = mipmaps[ j ];

							if ( useTexStorage ) {

								if ( dataReady ) {

									state.texSubImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j + 1, 0, 0, glFormat, glType, mipmap.image[ i ] );

								}

							} else {

								state.texImage2D( _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, j + 1, glInternalFormat, glFormat, glType, mipmap.image[ i ] );

							}

						}

					}

				}

			}

			if ( textureNeedsGenerateMipmaps( texture ) ) {

				// We assume images for cube map have the same size.
				generateMipmap( _gl.TEXTURE_CUBE_MAP );

			}

			sourceProperties.__version = source.version;

			if ( texture.onUpdate ) texture.onUpdate( texture );

		}

		textureProperties.__version = texture.version;

	}

	// Render targets

	// Setup storage for target texture and bind it to correct framebuffer
	function setupFrameBufferTexture( framebuffer, renderTarget, texture, attachment, textureTarget, level ) {

		const glFormat = utils.convert( texture.format, texture.colorSpace );
		const glType = utils.convert( texture.type );
		const glInternalFormat = getInternalFormat( texture.internalFormat, glFormat, glType, texture.colorSpace );
		const renderTargetProperties = properties.get( renderTarget );

		if ( ! renderTargetProperties.__hasExternalTextures ) {

			const width = Math.max( 1, renderTarget.width >> level );
			const height = Math.max( 1, renderTarget.height >> level );

			if ( textureTarget === _gl.TEXTURE_3D || textureTarget === _gl.TEXTURE_2D_ARRAY ) {

				state.texImage3D( textureTarget, level, glInternalFormat, width, height, renderTarget.depth, 0, glFormat, glType, null );

			} else {

				state.texImage2D( textureTarget, level, glInternalFormat, width, height, 0, glFormat, glType, null );

			}

		}

		state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

		if ( useMultisampledRTT( renderTarget ) ) {

			multisampledRTTExt.framebufferTexture2DMultisampleEXT( _gl.FRAMEBUFFER, attachment, textureTarget, properties.get( texture ).__webglTexture, 0, getRenderTargetSamples( renderTarget ) );

		} else if ( textureTarget === _gl.TEXTURE_2D || ( textureTarget >= _gl.TEXTURE_CUBE_MAP_POSITIVE_X && textureTarget <= _gl.TEXTURE_CUBE_MAP_NEGATIVE_Z ) ) { // see #24753

			_gl.framebufferTexture2D( _gl.FRAMEBUFFER, attachment, textureTarget, properties.get( texture ).__webglTexture, level );

		}

		state.bindFramebuffer( _gl.FRAMEBUFFER, null );

	}

	// Setup storage for internal depth/stencil buffers and bind to correct framebuffer
	function setupRenderBufferStorage( renderbuffer, renderTarget, isMultisample ) {

		_gl.bindRenderbuffer( _gl.RENDERBUFFER, renderbuffer );

		if ( renderTarget.depthBuffer ) {

			// retrieve the depth attachment types
			const depthTexture = renderTarget.depthTexture;
			const depthType = depthTexture && depthTexture.isDepthTexture ? depthTexture.type : null;
			const glInternalFormat = getInternalDepthFormat( renderTarget.stencilBuffer, depthType );
			const glAttachmentType = renderTarget.stencilBuffer ? _gl.DEPTH_STENCIL_ATTACHMENT : _gl.DEPTH_ATTACHMENT;

			// set up the attachment
			const samples = getRenderTargetSamples( renderTarget );
			const isUseMultisampledRTT = useMultisampledRTT( renderTarget );
			if ( isUseMultisampledRTT ) {

				multisampledRTTExt.renderbufferStorageMultisampleEXT( _gl.RENDERBUFFER, samples, glInternalFormat, renderTarget.width, renderTarget.height );

			} else if ( isMultisample ) {

				_gl.renderbufferStorageMultisample( _gl.RENDERBUFFER, samples, glInternalFormat, renderTarget.width, renderTarget.height );

			} else {

				_gl.renderbufferStorage( _gl.RENDERBUFFER, glInternalFormat, renderTarget.width, renderTarget.height );

			}

			_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, glAttachmentType, _gl.RENDERBUFFER, renderbuffer );

		} else {

			const textures = renderTarget.textures;

			for ( let i = 0; i < textures.length; i ++ ) {

				const texture = textures[ i ];

				const glFormat = utils.convert( texture.format, texture.colorSpace );
				const glType = utils.convert( texture.type );
				const glInternalFormat = getInternalFormat( texture.internalFormat, glFormat, glType, texture.colorSpace );
				const samples = getRenderTargetSamples( renderTarget );

				if ( isMultisample && useMultisampledRTT( renderTarget ) === false ) {

					_gl.renderbufferStorageMultisample( _gl.RENDERBUFFER, samples, glInternalFormat, renderTarget.width, renderTarget.height );

				} else if ( useMultisampledRTT( renderTarget ) ) {

					multisampledRTTExt.renderbufferStorageMultisampleEXT( _gl.RENDERBUFFER, samples, glInternalFormat, renderTarget.width, renderTarget.height );

				} else {

					_gl.renderbufferStorage( _gl.RENDERBUFFER, glInternalFormat, renderTarget.width, renderTarget.height );

				}

			}

		}

		_gl.bindRenderbuffer( _gl.RENDERBUFFER, null );

	}

	// Setup resources for a Depth Texture for a FBO (needs an extension)
	function setupDepthTexture( framebuffer, renderTarget ) {

		const isCube = ( renderTarget && renderTarget.isWebGLCubeRenderTarget );
		if ( isCube ) throw new Error( 'Depth Texture with cube render targets is not supported' );

		state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

		if ( ! ( renderTarget.depthTexture && renderTarget.depthTexture.isDepthTexture ) ) {

			throw new Error( 'renderTarget.depthTexture must be an instance of THREE.DepthTexture' );

		}

		// upload an empty depth texture with framebuffer size
		if ( ! properties.get( renderTarget.depthTexture ).__webglTexture ||
				renderTarget.depthTexture.image.width !== renderTarget.width ||
				renderTarget.depthTexture.image.height !== renderTarget.height ) {

			renderTarget.depthTexture.image.width = renderTarget.width;
			renderTarget.depthTexture.image.height = renderTarget.height;
			renderTarget.depthTexture.needsUpdate = true;

		}

		setTexture2D( renderTarget.depthTexture, 0 );

		const webglDepthTexture = properties.get( renderTarget.depthTexture ).__webglTexture;
		const samples = getRenderTargetSamples( renderTarget );

		if ( renderTarget.depthTexture.format === DepthFormat ) {

			if ( useMultisampledRTT( renderTarget ) ) {

				multisampledRTTExt.framebufferTexture2DMultisampleEXT( _gl.FRAMEBUFFER, _gl.DEPTH_ATTACHMENT, _gl.TEXTURE_2D, webglDepthTexture, 0, samples );

			} else {

				_gl.framebufferTexture2D( _gl.FRAMEBUFFER, _gl.DEPTH_ATTACHMENT, _gl.TEXTURE_2D, webglDepthTexture, 0 );

			}

		} else if ( renderTarget.depthTexture.format === DepthStencilFormat ) {

			if ( useMultisampledRTT( renderTarget ) ) {

				multisampledRTTExt.framebufferTexture2DMultisampleEXT( _gl.FRAMEBUFFER, _gl.DEPTH_STENCIL_ATTACHMENT, _gl.TEXTURE_2D, webglDepthTexture, 0, samples );

			} else {

				_gl.framebufferTexture2D( _gl.FRAMEBUFFER, _gl.DEPTH_STENCIL_ATTACHMENT, _gl.TEXTURE_2D, webglDepthTexture, 0 );

			}

		} else {

			throw new Error( 'Unknown depthTexture format' );

		}

	}

	// Setup GL resources for a non-texture depth buffer
	function setupDepthRenderbuffer( renderTarget ) {

		const renderTargetProperties = properties.get( renderTarget );
		const isCube = ( renderTarget.isWebGLCubeRenderTarget === true );

		// if the bound depth texture has changed
		if ( renderTargetProperties.__boundDepthTexture !== renderTarget.depthTexture ) {

			// fire the dispose event to get rid of stored state associated with the previously bound depth buffer
			const depthTexture = renderTarget.depthTexture;
			if ( renderTargetProperties.__depthDisposeCallback ) {

				renderTargetProperties.__depthDisposeCallback();

			}

			// set up dispose listeners to track when the currently attached buffer is implicitly unbound
			if ( depthTexture ) {

				const disposeEvent = () => {

					delete renderTargetProperties.__boundDepthTexture;
					delete renderTargetProperties.__depthDisposeCallback;
					depthTexture.removeEventListener( 'dispose', disposeEvent );

				};

				depthTexture.addEventListener( 'dispose', disposeEvent );
				renderTargetProperties.__depthDisposeCallback = disposeEvent;

			}

			renderTargetProperties.__boundDepthTexture = depthTexture;

		}

		if ( renderTarget.depthTexture && ! renderTargetProperties.__autoAllocateDepthBuffer ) {

			if ( isCube ) throw new Error( 'target.depthTexture not supported in Cube render targets' );

			setupDepthTexture( renderTargetProperties.__webglFramebuffer, renderTarget );

		} else {

			if ( isCube ) {

				renderTargetProperties.__webglDepthbuffer = [];

				for ( let i = 0; i < 6; i ++ ) {

					state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglFramebuffer[ i ] );

					if ( renderTargetProperties.__webglDepthbuffer[ i ] === undefined ) {

						renderTargetProperties.__webglDepthbuffer[ i ] = _gl.createRenderbuffer();
						setupRenderBufferStorage( renderTargetProperties.__webglDepthbuffer[ i ], renderTarget, false );

					} else {

						// attach buffer if it's been created already
						const glAttachmentType = renderTarget.stencilBuffer ? _gl.DEPTH_STENCIL_ATTACHMENT : _gl.DEPTH_ATTACHMENT;
						const renderbuffer = renderTargetProperties.__webglDepthbuffer[ i ];
						_gl.bindRenderbuffer( _gl.RENDERBUFFER, renderbuffer );
						_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, glAttachmentType, _gl.RENDERBUFFER, renderbuffer );

					}

				}

			} else {

				state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglFramebuffer );

				if ( renderTargetProperties.__webglDepthbuffer === undefined ) {

					renderTargetProperties.__webglDepthbuffer = _gl.createRenderbuffer();
					setupRenderBufferStorage( renderTargetProperties.__webglDepthbuffer, renderTarget, false );

				} else {

					// attach buffer if it's been created already
					const glAttachmentType = renderTarget.stencilBuffer ? _gl.DEPTH_STENCIL_ATTACHMENT : _gl.DEPTH_ATTACHMENT;
					const renderbuffer = renderTargetProperties.__webglDepthbuffer;
					_gl.bindRenderbuffer( _gl.RENDERBUFFER, renderbuffer );
					_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, glAttachmentType, _gl.RENDERBUFFER, renderbuffer );

				}

			}

		}

		state.bindFramebuffer( _gl.FRAMEBUFFER, null );

	}

	// rebind framebuffer with external textures
	function rebindTextures( renderTarget, colorTexture, depthTexture ) {

		const renderTargetProperties = properties.get( renderTarget );

		if ( colorTexture !== undefined ) {

			setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer, renderTarget, renderTarget.texture, _gl.COLOR_ATTACHMENT0, _gl.TEXTURE_2D, 0 );

		}

		if ( depthTexture !== undefined ) {

			setupDepthRenderbuffer( renderTarget );

		}

	}

	// Set up GL resources for the render target
	function setupRenderTarget( renderTarget ) {

		const texture = renderTarget.texture;

		const renderTargetProperties = properties.get( renderTarget );
		const textureProperties = properties.get( texture );

		renderTarget.addEventListener( 'dispose', onRenderTargetDispose );

		const textures = renderTarget.textures;

		const isCube = ( renderTarget.isWebGLCubeRenderTarget === true );
		const isMultipleRenderTargets = ( textures.length > 1 );

		if ( ! isMultipleRenderTargets ) {

			if ( textureProperties.__webglTexture === undefined ) {

				textureProperties.__webglTexture = _gl.createTexture();

			}

			textureProperties.__version = texture.version;
			info.memory.textures ++;

		}

		// Setup framebuffer

		if ( isCube ) {

			renderTargetProperties.__webglFramebuffer = [];

			for ( let i = 0; i < 6; i ++ ) {

				if ( texture.mipmaps && texture.mipmaps.length > 0 ) {

					renderTargetProperties.__webglFramebuffer[ i ] = [];

					for ( let level = 0; level < texture.mipmaps.length; level ++ ) {

						renderTargetProperties.__webglFramebuffer[ i ][ level ] = _gl.createFramebuffer();

					}

				} else {

					renderTargetProperties.__webglFramebuffer[ i ] = _gl.createFramebuffer();

				}

			}

		} else {

			if ( texture.mipmaps && texture.mipmaps.length > 0 ) {

				renderTargetProperties.__webglFramebuffer = [];

				for ( let level = 0; level < texture.mipmaps.length; level ++ ) {

					renderTargetProperties.__webglFramebuffer[ level ] = _gl.createFramebuffer();

				}

			} else {

				renderTargetProperties.__webglFramebuffer = _gl.createFramebuffer();

			}

			if ( isMultipleRenderTargets ) {

				for ( let i = 0, il = textures.length; i < il; i ++ ) {

					const attachmentProperties = properties.get( textures[ i ] );

					if ( attachmentProperties.__webglTexture === undefined ) {

						attachmentProperties.__webglTexture = _gl.createTexture();

						info.memory.textures ++;

					}

				}

			}

			if ( ( renderTarget.samples > 0 ) && useMultisampledRTT( renderTarget ) === false ) {

				renderTargetProperties.__webglMultisampledFramebuffer = _gl.createFramebuffer();
				renderTargetProperties.__webglColorRenderbuffer = [];

				state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglMultisampledFramebuffer );

				for ( let i = 0; i < textures.length; i ++ ) {

					const texture = textures[ i ];
					renderTargetProperties.__webglColorRenderbuffer[ i ] = _gl.createRenderbuffer();

					_gl.bindRenderbuffer( _gl.RENDERBUFFER, renderTargetProperties.__webglColorRenderbuffer[ i ] );

					const glFormat = utils.convert( texture.format, texture.colorSpace );
					const glType = utils.convert( texture.type );
					const glInternalFormat = getInternalFormat( texture.internalFormat, glFormat, glType, texture.colorSpace, renderTarget.isXRRenderTarget === true );
					const samples = getRenderTargetSamples( renderTarget );
					_gl.renderbufferStorageMultisample( _gl.RENDERBUFFER, samples, glInternalFormat, renderTarget.width, renderTarget.height );

					_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, _gl.COLOR_ATTACHMENT0 + i, _gl.RENDERBUFFER, renderTargetProperties.__webglColorRenderbuffer[ i ] );

				}

				_gl.bindRenderbuffer( _gl.RENDERBUFFER, null );

				if ( renderTarget.depthBuffer ) {

					renderTargetProperties.__webglDepthRenderbuffer = _gl.createRenderbuffer();
					setupRenderBufferStorage( renderTargetProperties.__webglDepthRenderbuffer, renderTarget, true );

				}

				state.bindFramebuffer( _gl.FRAMEBUFFER, null );

			}

		}

		// Setup color buffer

		if ( isCube ) {

			state.bindTexture( _gl.TEXTURE_CUBE_MAP, textureProperties.__webglTexture );
			setTextureParameters( _gl.TEXTURE_CUBE_MAP, texture );

			for ( let i = 0; i < 6; i ++ ) {

				if ( texture.mipmaps && texture.mipmaps.length > 0 ) {

					for ( let level = 0; level < texture.mipmaps.length; level ++ ) {

						setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer[ i ][ level ], renderTarget, texture, _gl.COLOR_ATTACHMENT0, _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, level );

					}

				} else {

					setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer[ i ], renderTarget, texture, _gl.COLOR_ATTACHMENT0, _gl.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0 );

				}

			}

			if ( textureNeedsGenerateMipmaps( texture ) ) {

				generateMipmap( _gl.TEXTURE_CUBE_MAP );

			}

			state.unbindTexture();

		} else if ( isMultipleRenderTargets ) {

			for ( let i = 0, il = textures.length; i < il; i ++ ) {

				const attachment = textures[ i ];
				const attachmentProperties = properties.get( attachment );

				state.bindTexture( _gl.TEXTURE_2D, attachmentProperties.__webglTexture );
				setTextureParameters( _gl.TEXTURE_2D, attachment );
				setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer, renderTarget, attachment, _gl.COLOR_ATTACHMENT0 + i, _gl.TEXTURE_2D, 0 );

				if ( textureNeedsGenerateMipmaps( attachment ) ) {

					generateMipmap( _gl.TEXTURE_2D );

				}

			}

			state.unbindTexture();

		} else {

			let glTextureType = _gl.TEXTURE_2D;

			if ( renderTarget.isWebGL3DRenderTarget || renderTarget.isWebGLArrayRenderTarget ) {

				glTextureType = renderTarget.isWebGL3DRenderTarget ? _gl.TEXTURE_3D : _gl.TEXTURE_2D_ARRAY;

			}

			state.bindTexture( glTextureType, textureProperties.__webglTexture );
			setTextureParameters( glTextureType, texture );

			if ( texture.mipmaps && texture.mipmaps.length > 0 ) {

				for ( let level = 0; level < texture.mipmaps.length; level ++ ) {

					setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer[ level ], renderTarget, texture, _gl.COLOR_ATTACHMENT0, glTextureType, level );

				}

			} else {

				setupFrameBufferTexture( renderTargetProperties.__webglFramebuffer, renderTarget, texture, _gl.COLOR_ATTACHMENT0, glTextureType, 0 );

			}

			if ( textureNeedsGenerateMipmaps( texture ) ) {

				generateMipmap( glTextureType );

			}

			state.unbindTexture();

		}

		// Setup depth and stencil buffers

		if ( renderTarget.depthBuffer ) {

			setupDepthRenderbuffer( renderTarget );

		}

	}

	function updateRenderTargetMipmap( renderTarget ) {

		const textures = renderTarget.textures;

		for ( let i = 0, il = textures.length; i < il; i ++ ) {

			const texture = textures[ i ];

			if ( textureNeedsGenerateMipmaps( texture ) ) {

				const target = renderTarget.isWebGLCubeRenderTarget ? _gl.TEXTURE_CUBE_MAP : _gl.TEXTURE_2D;
				const webglTexture = properties.get( texture ).__webglTexture;

				state.bindTexture( target, webglTexture );
				generateMipmap( target );
				state.unbindTexture();

			}

		}

	}

	const invalidationArrayRead = [];
	const invalidationArrayDraw = [];

	function updateMultisampleRenderTarget( renderTarget ) {

		if ( renderTarget.samples > 0 ) {

			if ( useMultisampledRTT( renderTarget ) === false ) {

				const textures = renderTarget.textures;
				const width = renderTarget.width;
				const height = renderTarget.height;
				let mask = _gl.COLOR_BUFFER_BIT;
				const depthStyle = renderTarget.stencilBuffer ? _gl.DEPTH_STENCIL_ATTACHMENT : _gl.DEPTH_ATTACHMENT;
				const renderTargetProperties = properties.get( renderTarget );
				const isMultipleRenderTargets = ( textures.length > 1 );

				// If MRT we need to remove FBO attachments
				if ( isMultipleRenderTargets ) {

					for ( let i = 0; i < textures.length; i ++ ) {

						state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglMultisampledFramebuffer );
						_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, _gl.COLOR_ATTACHMENT0 + i, _gl.RENDERBUFFER, null );

						state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglFramebuffer );
						_gl.framebufferTexture2D( _gl.DRAW_FRAMEBUFFER, _gl.COLOR_ATTACHMENT0 + i, _gl.TEXTURE_2D, null, 0 );

					}

				}

				state.bindFramebuffer( _gl.READ_FRAMEBUFFER, renderTargetProperties.__webglMultisampledFramebuffer );
				state.bindFramebuffer( _gl.DRAW_FRAMEBUFFER, renderTargetProperties.__webglFramebuffer );

				for ( let i = 0; i < textures.length; i ++ ) {

					if ( renderTarget.resolveDepthBuffer ) {

						if ( renderTarget.depthBuffer ) mask |= _gl.DEPTH_BUFFER_BIT;

						// resolving stencil is slow with a D3D backend. disable it for all transmission render targets (see #27799)

						if ( renderTarget.stencilBuffer && renderTarget.resolveStencilBuffer ) mask |= _gl.STENCIL_BUFFER_BIT;

					}

					if ( isMultipleRenderTargets ) {

						_gl.framebufferRenderbuffer( _gl.READ_FRAMEBUFFER, _gl.COLOR_ATTACHMENT0, _gl.RENDERBUFFER, renderTargetProperties.__webglColorRenderbuffer[ i ] );

						const webglTexture = properties.get( textures[ i ] ).__webglTexture;
						_gl.framebufferTexture2D( _gl.DRAW_FRAMEBUFFER, _gl.COLOR_ATTACHMENT0, _gl.TEXTURE_2D, webglTexture, 0 );

					}

					_gl.blitFramebuffer( 0, 0, width, height, 0, 0, width, height, mask, _gl.NEAREST );

					if ( supportsInvalidateFramebuffer === true ) {

						invalidationArrayRead.length = 0;
						invalidationArrayDraw.length = 0;

						invalidationArrayRead.push( _gl.COLOR_ATTACHMENT0 + i );

						if ( renderTarget.depthBuffer && renderTarget.resolveDepthBuffer === false ) {

							invalidationArrayRead.push( depthStyle );
							invalidationArrayDraw.push( depthStyle );

							_gl.invalidateFramebuffer( _gl.DRAW_FRAMEBUFFER, invalidationArrayDraw );

						}

						_gl.invalidateFramebuffer( _gl.READ_FRAMEBUFFER, invalidationArrayRead );

					}

				}

				state.bindFramebuffer( _gl.READ_FRAMEBUFFER, null );
				state.bindFramebuffer( _gl.DRAW_FRAMEBUFFER, null );

				// If MRT since pre-blit we removed the FBO we need to reconstruct the attachments
				if ( isMultipleRenderTargets ) {

					for ( let i = 0; i < textures.length; i ++ ) {

						state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglMultisampledFramebuffer );
						_gl.framebufferRenderbuffer( _gl.FRAMEBUFFER, _gl.COLOR_ATTACHMENT0 + i, _gl.RENDERBUFFER, renderTargetProperties.__webglColorRenderbuffer[ i ] );

						const webglTexture = properties.get( textures[ i ] ).__webglTexture;

						state.bindFramebuffer( _gl.FRAMEBUFFER, renderTargetProperties.__webglFramebuffer );
						_gl.framebufferTexture2D( _gl.DRAW_FRAMEBUFFER, _gl.COLOR_ATTACHMENT0 + i, _gl.TEXTURE_2D, webglTexture, 0 );

					}

				}

				state.bindFramebuffer( _gl.DRAW_FRAMEBUFFER, renderTargetProperties.__webglMultisampledFramebuffer );

			} else {

				if ( renderTarget.depthBuffer && renderTarget.resolveDepthBuffer === false && supportsInvalidateFramebuffer ) {

					const depthStyle = renderTarget.stencilBuffer ? _gl.DEPTH_STENCIL_ATTACHMENT : _gl.DEPTH_ATTACHMENT;

					_gl.invalidateFramebuffer( _gl.DRAW_FRAMEBUFFER, [ depthStyle ] );

				}

			}

		}

	}

	function getRenderTargetSamples( renderTarget ) {

		return Math.min( capabilities.maxSamples, renderTarget.samples );

	}

	function useMultisampledRTT( renderTarget ) {

		const renderTargetProperties = properties.get( renderTarget );

		return renderTarget.samples > 0 && extensions.has( 'WEBGL_multisampled_render_to_texture' ) === true && renderTargetProperties.__useRenderToTexture !== false;

	}

	function updateVideoTexture( texture ) {

		const frame = info.render.frame;

		// Check the last frame we updated the VideoTexture

		if ( _videoTextures.get( texture ) !== frame ) {

			_videoTextures.set( texture, frame );
			texture.update();

		}

	}

	function verifyColorSpace( texture, image ) {

		const colorSpace = texture.colorSpace;
		const format = texture.format;
		const type = texture.type;

		if ( texture.isCompressedTexture === true || texture.isVideoTexture === true ) return image;

		if ( colorSpace !== LinearSRGBColorSpace && colorSpace !== NoColorSpace ) {

			// sRGB

			if ( ColorManagement.getTransfer( colorSpace ) === SRGBTransfer ) {

				// in WebGL 2 uncompressed textures can only be sRGB encoded if they have the RGBA8 format

				if ( format !== RGBAFormat || type !== UnsignedByteType ) {

					console.warn( 'THREE.WebGLTextures: sRGB encoded textures have to use RGBAFormat and UnsignedByteType.' );

				}

			} else {

				console.error( 'THREE.WebGLTextures: Unsupported texture color space:', colorSpace );

			}

		}

		return image;

	}

	function getDimensions( image ) {

		if ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) {

			// if intrinsic data are not available, fallback to width/height

			_imageDimensions.width = image.naturalWidth || image.width;
			_imageDimensions.height = image.naturalHeight || image.height;

		} else if ( typeof VideoFrame !== 'undefined' && image instanceof VideoFrame ) {

			_imageDimensions.width = image.displayWidth;
			_imageDimensions.height = image.displayHeight;

		} else {

			_imageDimensions.width = image.width;
			_imageDimensions.height = image.height;

		}

		return _imageDimensions;

	}

	//

	this.allocateTextureUnit = allocateTextureUnit;
	this.resetTextureUnits = resetTextureUnits;

	this.setTexture2D = setTexture2D;
	this.setTexture2DArray = setTexture2DArray;
	this.setTexture3D = setTexture3D;
	this.setTextureCube = setTextureCube;
	this.rebindTextures = rebindTextures;
	this.setupRenderTarget = setupRenderTarget;
	this.updateRenderTargetMipmap = updateRenderTargetMipmap;
	this.updateMultisampleRenderTarget = updateMultisampleRenderTarget;
	this.setupDepthRenderbuffer = setupDepthRenderbuffer;
	this.setupFrameBufferTexture = setupFrameBufferTexture;
	this.useMultisampledRTT = useMultisampledRTT;

}

function WebGLUtils( gl, extensions ) {

	function convert( p, colorSpace = NoColorSpace ) {

		let extension;

		const transfer = ColorManagement.getTransfer( colorSpace );

		if ( p === UnsignedByteType ) return gl.UNSIGNED_BYTE;
		if ( p === UnsignedShort4444Type ) return gl.UNSIGNED_SHORT_4_4_4_4;
		if ( p === UnsignedShort5551Type ) return gl.UNSIGNED_SHORT_5_5_5_1;
		if ( p === UnsignedInt5999Type ) return gl.UNSIGNED_INT_5_9_9_9_REV;

		if ( p === ByteType ) return gl.BYTE;
		if ( p === ShortType ) return gl.SHORT;
		if ( p === UnsignedShortType ) return gl.UNSIGNED_SHORT;
		if ( p === IntType ) return gl.INT;
		if ( p === UnsignedIntType ) return gl.UNSIGNED_INT;
		if ( p === FloatType ) return gl.FLOAT;
		if ( p === HalfFloatType ) return gl.HALF_FLOAT;

		if ( p === AlphaFormat ) return gl.ALPHA;
		if ( p === RGBFormat ) return gl.RGB;
		if ( p === RGBAFormat ) return gl.RGBA;
		if ( p === LuminanceFormat ) return gl.LUMINANCE;
		if ( p === LuminanceAlphaFormat ) return gl.LUMINANCE_ALPHA;
		if ( p === DepthFormat ) return gl.DEPTH_COMPONENT;
		if ( p === DepthStencilFormat ) return gl.DEPTH_STENCIL;

		// WebGL2 formats.

		if ( p === RedFormat ) return gl.RED;
		if ( p === RedIntegerFormat ) return gl.RED_INTEGER;
		if ( p === RGFormat ) return gl.RG;
		if ( p === RGIntegerFormat ) return gl.RG_INTEGER;
		if ( p === RGBAIntegerFormat ) return gl.RGBA_INTEGER;

		// S3TC

		if ( p === RGB_S3TC_DXT1_Format || p === RGBA_S3TC_DXT1_Format || p === RGBA_S3TC_DXT3_Format || p === RGBA_S3TC_DXT5_Format ) {

			if ( transfer === SRGBTransfer ) {

				extension = extensions.get( 'WEBGL_compressed_texture_s3tc_srgb' );

				if ( extension !== null ) {

					if ( p === RGB_S3TC_DXT1_Format ) return extension.COMPRESSED_SRGB_S3TC_DXT1_EXT;
					if ( p === RGBA_S3TC_DXT1_Format ) return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT1_EXT;
					if ( p === RGBA_S3TC_DXT3_Format ) return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT3_EXT;
					if ( p === RGBA_S3TC_DXT5_Format ) return extension.COMPRESSED_SRGB_ALPHA_S3TC_DXT5_EXT;

				} else {

					return null;

				}

			} else {

				extension = extensions.get( 'WEBGL_compressed_texture_s3tc' );

				if ( extension !== null ) {

					if ( p === RGB_S3TC_DXT1_Format ) return extension.COMPRESSED_RGB_S3TC_DXT1_EXT;
					if ( p === RGBA_S3TC_DXT1_Format ) return extension.COMPRESSED_RGBA_S3TC_DXT1_EXT;
					if ( p === RGBA_S3TC_DXT3_Format ) return extension.COMPRESSED_RGBA_S3TC_DXT3_EXT;
					if ( p === RGBA_S3TC_DXT5_Format ) return extension.COMPRESSED_RGBA_S3TC_DXT5_EXT;

				} else {

					return null;

				}

			}

		}

		// PVRTC

		if ( p === RGB_PVRTC_4BPPV1_Format || p === RGB_PVRTC_2BPPV1_Format || p === RGBA_PVRTC_4BPPV1_Format || p === RGBA_PVRTC_2BPPV1_Format ) {

			extension = extensions.get( 'WEBGL_compressed_texture_pvrtc' );

			if ( extension !== null ) {

				if ( p === RGB_PVRTC_4BPPV1_Format ) return extension.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;
				if ( p === RGB_PVRTC_2BPPV1_Format ) return extension.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;
				if ( p === RGBA_PVRTC_4BPPV1_Format ) return extension.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG;
				if ( p === RGBA_PVRTC_2BPPV1_Format ) return extension.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG;

			} else {

				return null;

			}

		}

		// ETC

		if ( p === RGB_ETC1_Format || p === RGB_ETC2_Format || p === RGBA_ETC2_EAC_Format ) {

			extension = extensions.get( 'WEBGL_compressed_texture_etc' );

			if ( extension !== null ) {

				if ( p === RGB_ETC1_Format || p === RGB_ETC2_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ETC2 : extension.COMPRESSED_RGB8_ETC2;
				if ( p === RGBA_ETC2_EAC_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ETC2_EAC : extension.COMPRESSED_RGBA8_ETC2_EAC;

			} else {

				return null;

			}

		}

		// ASTC

		if ( p === RGBA_ASTC_4x4_Format || p === RGBA_ASTC_5x4_Format || p === RGBA_ASTC_5x5_Format ||
			p === RGBA_ASTC_6x5_Format || p === RGBA_ASTC_6x6_Format || p === RGBA_ASTC_8x5_Format ||
			p === RGBA_ASTC_8x6_Format || p === RGBA_ASTC_8x8_Format || p === RGBA_ASTC_10x5_Format ||
			p === RGBA_ASTC_10x6_Format || p === RGBA_ASTC_10x8_Format || p === RGBA_ASTC_10x10_Format ||
			p === RGBA_ASTC_12x10_Format || p === RGBA_ASTC_12x12_Format ) {

			extension = extensions.get( 'WEBGL_compressed_texture_astc' );

			if ( extension !== null ) {

				if ( p === RGBA_ASTC_4x4_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_4x4_KHR : extension.COMPRESSED_RGBA_ASTC_4x4_KHR;
				if ( p === RGBA_ASTC_5x4_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_5x4_KHR : extension.COMPRESSED_RGBA_ASTC_5x4_KHR;
				if ( p === RGBA_ASTC_5x5_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_5x5_KHR : extension.COMPRESSED_RGBA_ASTC_5x5_KHR;
				if ( p === RGBA_ASTC_6x5_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_6x5_KHR : extension.COMPRESSED_RGBA_ASTC_6x5_KHR;
				if ( p === RGBA_ASTC_6x6_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_6x6_KHR : extension.COMPRESSED_RGBA_ASTC_6x6_KHR;
				if ( p === RGBA_ASTC_8x5_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x5_KHR : extension.COMPRESSED_RGBA_ASTC_8x5_KHR;
				if ( p === RGBA_ASTC_8x6_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x6_KHR : extension.COMPRESSED_RGBA_ASTC_8x6_KHR;
				if ( p === RGBA_ASTC_8x8_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_8x8_KHR : extension.COMPRESSED_RGBA_ASTC_8x8_KHR;
				if ( p === RGBA_ASTC_10x5_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x5_KHR : extension.COMPRESSED_RGBA_ASTC_10x5_KHR;
				if ( p === RGBA_ASTC_10x6_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x6_KHR : extension.COMPRESSED_RGBA_ASTC_10x6_KHR;
				if ( p === RGBA_ASTC_10x8_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x8_KHR : extension.COMPRESSED_RGBA_ASTC_10x8_KHR;
				if ( p === RGBA_ASTC_10x10_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_10x10_KHR : extension.COMPRESSED_RGBA_ASTC_10x10_KHR;
				if ( p === RGBA_ASTC_12x10_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_12x10_KHR : extension.COMPRESSED_RGBA_ASTC_12x10_KHR;
				if ( p === RGBA_ASTC_12x12_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB8_ALPHA8_ASTC_12x12_KHR : extension.COMPRESSED_RGBA_ASTC_12x12_KHR;

			} else {

				return null;

			}

		}

		// BPTC

		if ( p === RGBA_BPTC_Format || p === RGB_BPTC_SIGNED_Format || p === RGB_BPTC_UNSIGNED_Format ) {

			extension = extensions.get( 'EXT_texture_compression_bptc' );

			if ( extension !== null ) {

				if ( p === RGBA_BPTC_Format ) return ( transfer === SRGBTransfer ) ? extension.COMPRESSED_SRGB_ALPHA_BPTC_UNORM_EXT : extension.COMPRESSED_RGBA_BPTC_UNORM_EXT;
				if ( p === RGB_BPTC_SIGNED_Format ) return extension.COMPRESSED_RGB_BPTC_SIGNED_FLOAT_EXT;
				if ( p === RGB_BPTC_UNSIGNED_Format ) return extension.COMPRESSED_RGB_BPTC_UNSIGNED_FLOAT_EXT;

			} else {

				return null;

			}

		}

		// RGTC

		if ( p === RED_RGTC1_Format || p === SIGNED_RED_RGTC1_Format || p === RED_GREEN_RGTC2_Format || p === SIGNED_RED_GREEN_RGTC2_Format ) {

			extension = extensions.get( 'EXT_texture_compression_rgtc' );

			if ( extension !== null ) {

				if ( p === RGBA_BPTC_Format ) return extension.COMPRESSED_RED_RGTC1_EXT;
				if ( p === SIGNED_RED_RGTC1_Format ) return extension.COMPRESSED_SIGNED_RED_RGTC1_EXT;
				if ( p === RED_GREEN_RGTC2_Format ) return extension.COMPRESSED_RED_GREEN_RGTC2_EXT;
				if ( p === SIGNED_RED_GREEN_RGTC2_Format ) return extension.COMPRESSED_SIGNED_RED_GREEN_RGTC2_EXT;

			} else {

				return null;

			}

		}

		//

		if ( p === UnsignedInt248Type ) return gl.UNSIGNED_INT_24_8;

		// if "p" can't be resolved, assume the user defines a WebGL constant as a string (fallback/workaround for packed RGB formats)

		return ( gl[ p ] !== undefined ) ? gl[ p ] : null;

	}

	return { convert: convert };

}

class ArrayCamera extends PerspectiveCamera {

	constructor( array = [] ) {

		super();

		this.isArrayCamera = true;

		this.cameras = array;

	}

}

class Group extends Object3D {

	constructor() {

		super();

		this.isGroup = true;

		this.type = 'Group';

	}

}

const _moveEvent = { type: 'move' };

class WebXRController {

	constructor() {

		this._targetRay = null;
		this._grip = null;
		this._hand = null;

	}

	getHandSpace() {

		if ( this._hand === null ) {

			this._hand = new Group();
			this._hand.matrixAutoUpdate = false;
			this._hand.visible = false;

			this._hand.joints = {};
			this._hand.inputState = { pinching: false };

		}

		return this._hand;

	}

	getTargetRaySpace() {

		if ( this._targetRay === null ) {

			this._targetRay = new Group();
			this._targetRay.matrixAutoUpdate = false;
			this._targetRay.visible = false;
			this._targetRay.hasLinearVelocity = false;
			this._targetRay.linearVelocity = new Vector3();
			this._targetRay.hasAngularVelocity = false;
			this._targetRay.angularVelocity = new Vector3();

		}

		return this._targetRay;

	}

	getGripSpace() {

		if ( this._grip === null ) {

			this._grip = new Group();
			this._grip.matrixAutoUpdate = false;
			this._grip.visible = false;
			this._grip.hasLinearVelocity = false;
			this._grip.linearVelocity = new Vector3();
			this._grip.hasAngularVelocity = false;
			this._grip.angularVelocity = new Vector3();

		}

		return this._grip;

	}

	dispatchEvent( event ) {

		if ( this._targetRay !== null ) {

			this._targetRay.dispatchEvent( event );

		}

		if ( this._grip !== null ) {

			this._grip.dispatchEvent( event );

		}

		if ( this._hand !== null ) {

			this._hand.dispatchEvent( event );

		}

		return this;

	}

	connect( inputSource ) {

		if ( inputSource && inputSource.hand ) {

			const hand = this._hand;

			if ( hand ) {

				for ( const inputjoint of inputSource.hand.values() ) {

					// Initialize hand with joints when connected
					this._getHandJoint( hand, inputjoint );

				}

			}

		}

		this.dispatchEvent( { type: 'connected', data: inputSource } );

		return this;

	}

	disconnect( inputSource ) {

		this.dispatchEvent( { type: 'disconnected', data: inputSource } );

		if ( this._targetRay !== null ) {

			this._targetRay.visible = false;

		}

		if ( this._grip !== null ) {

			this._grip.visible = false;

		}

		if ( this._hand !== null ) {

			this._hand.visible = false;

		}

		return this;

	}

	update( inputSource, frame, referenceSpace ) {

		let inputPose = null;
		let gripPose = null;
		let handPose = null;

		const targetRay = this._targetRay;
		const grip = this._grip;
		const hand = this._hand;

		if ( inputSource && frame.session.visibilityState !== 'visible-blurred' ) {

			if ( hand && inputSource.hand ) {

				handPose = true;

				for ( const inputjoint of inputSource.hand.values() ) {

					// Update the joints groups with the XRJoint poses
					const jointPose = frame.getJointPose( inputjoint, referenceSpace );

					// The transform of this joint will be updated with the joint pose on each frame
					const joint = this._getHandJoint( hand, inputjoint );

					if ( jointPose !== null ) {

						joint.matrix.fromArray( jointPose.transform.matrix );
						joint.matrix.decompose( joint.position, joint.rotation, joint.scale );
						joint.matrixWorldNeedsUpdate = true;
						joint.jointRadius = jointPose.radius;

					}

					joint.visible = jointPose !== null;

				}

				// Custom events

				// Check pinchz
				const indexTip = hand.joints[ 'index-finger-tip' ];
				const thumbTip = hand.joints[ 'thumb-tip' ];
				const distance = indexTip.position.distanceTo( thumbTip.position );

				const distanceToPinch = 0.02;
				const threshold = 0.005;

				if ( hand.inputState.pinching && distance > distanceToPinch + threshold ) {

					hand.inputState.pinching = false;
					this.dispatchEvent( {
						type: 'pinchend',
						handedness: inputSource.handedness,
						target: this
					} );

				} else if ( ! hand.inputState.pinching && distance <= distanceToPinch - threshold ) {

					hand.inputState.pinching = true;
					this.dispatchEvent( {
						type: 'pinchstart',
						handedness: inputSource.handedness,
						target: this
					} );

				}

			} else {

				if ( grip !== null && inputSource.gripSpace ) {

					gripPose = frame.getPose( inputSource.gripSpace, referenceSpace );

					if ( gripPose !== null ) {

						grip.matrix.fromArray( gripPose.transform.matrix );
						grip.matrix.decompose( grip.position, grip.rotation, grip.scale );
						grip.matrixWorldNeedsUpdate = true;

						if ( gripPose.linearVelocity ) {

							grip.hasLinearVelocity = true;
							grip.linearVelocity.copy( gripPose.linearVelocity );

						} else {

							grip.hasLinearVelocity = false;

						}

						if ( gripPose.angularVelocity ) {

							grip.hasAngularVelocity = true;
							grip.angularVelocity.copy( gripPose.angularVelocity );

						} else {

							grip.hasAngularVelocity = false;

						}

					}

				}

			}

			if ( targetRay !== null ) {

				inputPose = frame.getPose( inputSource.targetRaySpace, referenceSpace );

				// Some runtimes (namely Vive Cosmos with Vive OpenXR Runtime) have only grip space and ray space is equal to it
				if ( inputPose === null && gripPose !== null ) {

					inputPose = gripPose;

				}

				if ( inputPose !== null ) {

					targetRay.matrix.fromArray( inputPose.transform.matrix );
					targetRay.matrix.decompose( targetRay.position, targetRay.rotation, targetRay.scale );
					targetRay.matrixWorldNeedsUpdate = true;

					if ( inputPose.linearVelocity ) {

						targetRay.hasLinearVelocity = true;
						targetRay.linearVelocity.copy( inputPose.linearVelocity );

					} else {

						targetRay.hasLinearVelocity = false;

					}

					if ( inputPose.angularVelocity ) {

						targetRay.hasAngularVelocity = true;
						targetRay.angularVelocity.copy( inputPose.angularVelocity );

					} else {

						targetRay.hasAngularVelocity = false;

					}

					this.dispatchEvent( _moveEvent );

				}

			}


		}

		if ( targetRay !== null ) {

			targetRay.visible = ( inputPose !== null );

		}

		if ( grip !== null ) {

			grip.visible = ( gripPose !== null );

		}

		if ( hand !== null ) {

			hand.visible = ( handPose !== null );

		}

		return this;

	}

	// private method

	_getHandJoint( hand, inputjoint ) {

		if ( hand.joints[ inputjoint.jointName ] === undefined ) {

			const joint = new Group();
			joint.matrixAutoUpdate = false;
			joint.visible = false;
			hand.joints[ inputjoint.jointName ] = joint;

			hand.add( joint );

		}

		return hand.joints[ inputjoint.jointName ];

	}

}

const _occlusion_vertex = `
void main() {

	gl_Position = vec4( position, 1.0 );

}`;

const _occlusion_fragment = `
uniform sampler2DArray depthColor;
uniform float depthWidth;
uniform float depthHeight;

void main() {

	vec2 coord = vec2( gl_FragCoord.x / depthWidth, gl_FragCoord.y / depthHeight );

	if ( coord.x >= 1.0 ) {

		gl_FragDepth = texture( depthColor, vec3( coord.x - 1.0, coord.y, 1 ) ).r;

	} else {

		gl_FragDepth = texture( depthColor, vec3( coord.x, coord.y, 0 ) ).r;

	}

}`;

class WebXRDepthSensing {

	constructor() {

		this.texture = null;
		this.mesh = null;

		this.depthNear = 0;
		this.depthFar = 0;

	}

	init( renderer, depthData, renderState ) {

		if ( this.texture === null ) {

			const texture = new Texture();

			const texProps = renderer.properties.get( texture );
			texProps.__webglTexture = depthData.texture;

			if ( ( depthData.depthNear != renderState.depthNear ) || ( depthData.depthFar != renderState.depthFar ) ) {

				this.depthNear = depthData.depthNear;
				this.depthFar = depthData.depthFar;

			}

			this.texture = texture;

		}

	}

	getMesh( cameraXR ) {

		if ( this.texture !== null ) {

			if ( this.mesh === null ) {

				const viewport = cameraXR.cameras[ 0 ].viewport;
				const material = new ShaderMaterial( {
					vertexShader: _occlusion_vertex,
					fragmentShader: _occlusion_fragment,
					uniforms: {
						depthColor: { value: this.texture },
						depthWidth: { value: viewport.z },
						depthHeight: { value: viewport.w }
					}
				} );

				this.mesh = new Mesh( new PlaneGeometry( 20, 20 ), material );

			}

		}

		return this.mesh;

	}

	reset() {

		this.texture = null;
		this.mesh = null;

	}

	getDepthTexture() {

		return this.texture;

	}

}

class WebXRManager extends EventDispatcher {

	constructor( renderer, gl ) {

		super();

		const scope = this;

		let session = null;

		let framebufferScaleFactor = 1.0;

		let referenceSpace = null;
		let referenceSpaceType = 'local-floor';
		// Set default foveation to maximum.
		let foveation = 1.0;
		let customReferenceSpace = null;

		let pose = null;
		let glBinding = null;
		let glProjLayer = null;
		let glBaseLayer = null;
		let xrFrame = null;

		const depthSensing = new WebXRDepthSensing();
		const attributes = gl.getContextAttributes();

		let initialRenderTarget = null;
		let newRenderTarget = null;

		const controllers = [];
		const controllerInputSources = [];

		const currentSize = new Vector2();
		let currentPixelRatio = null;

		//

		const cameraL = new PerspectiveCamera();
		cameraL.layers.enable( 1 );
		cameraL.viewport = new Vector4();

		const cameraR = new PerspectiveCamera();
		cameraR.layers.enable( 2 );
		cameraR.viewport = new Vector4();

		const cameras = [ cameraL, cameraR ];

		const cameraXR = new ArrayCamera();
		cameraXR.layers.enable( 1 );
		cameraXR.layers.enable( 2 );

		let _currentDepthNear = null;
		let _currentDepthFar = null;

		//

		this.cameraAutoUpdate = true;
		this.enabled = false;

		this.isPresenting = false;

		this.getController = function ( index ) {

			let controller = controllers[ index ];

			if ( controller === undefined ) {

				controller = new WebXRController();
				controllers[ index ] = controller;

			}

			return controller.getTargetRaySpace();

		};

		this.getControllerGrip = function ( index ) {

			let controller = controllers[ index ];

			if ( controller === undefined ) {

				controller = new WebXRController();
				controllers[ index ] = controller;

			}

			return controller.getGripSpace();

		};

		this.getHand = function ( index ) {

			let controller = controllers[ index ];

			if ( controller === undefined ) {

				controller = new WebXRController();
				controllers[ index ] = controller;

			}

			return controller.getHandSpace();

		};

		//

		function onSessionEvent( event ) {

			const controllerIndex = controllerInputSources.indexOf( event.inputSource );

			if ( controllerIndex === - 1 ) {

				return;

			}

			const controller = controllers[ controllerIndex ];

			if ( controller !== undefined ) {

				controller.update( event.inputSource, event.frame, customReferenceSpace || referenceSpace );
				controller.dispatchEvent( { type: event.type, data: event.inputSource } );

			}

		}

		function onSessionEnd() {

			session.removeEventListener( 'select', onSessionEvent );
			session.removeEventListener( 'selectstart', onSessionEvent );
			session.removeEventListener( 'selectend', onSessionEvent );
			session.removeEventListener( 'squeeze', onSessionEvent );
			session.removeEventListener( 'squeezestart', onSessionEvent );
			session.removeEventListener( 'squeezeend', onSessionEvent );
			session.removeEventListener( 'end', onSessionEnd );
			session.removeEventListener( 'inputsourceschange', onInputSourcesChange );

			for ( let i = 0; i < controllers.length; i ++ ) {

				const inputSource = controllerInputSources[ i ];

				if ( inputSource === null ) continue;

				controllerInputSources[ i ] = null;

				controllers[ i ].disconnect( inputSource );

			}

			_currentDepthNear = null;
			_currentDepthFar = null;

			depthSensing.reset();

			// restore framebuffer/rendering state

			renderer.setRenderTarget( initialRenderTarget );

			glBaseLayer = null;
			glProjLayer = null;
			glBinding = null;
			session = null;
			newRenderTarget = null;

			//

			animation.stop();

			scope.isPresenting = false;

			renderer.setPixelRatio( currentPixelRatio );
			renderer.setSize( currentSize.width, currentSize.height, false );

			scope.dispatchEvent( { type: 'sessionend' } );

		}

		this.setFramebufferScaleFactor = function ( value ) {

			framebufferScaleFactor = value;

			if ( scope.isPresenting === true ) {

				console.warn( 'THREE.WebXRManager: Cannot change framebuffer scale while presenting.' );

			}

		};

		this.setReferenceSpaceType = function ( value ) {

			referenceSpaceType = value;

			if ( scope.isPresenting === true ) {

				console.warn( 'THREE.WebXRManager: Cannot change reference space type while presenting.' );

			}

		};

		this.getReferenceSpace = function () {

			return customReferenceSpace || referenceSpace;

		};

		this.setReferenceSpace = function ( space ) {

			customReferenceSpace = space;

		};

		this.getBaseLayer = function () {

			return glProjLayer !== null ? glProjLayer : glBaseLayer;

		};

		this.getBinding = function () {

			return glBinding;

		};

		this.getFrame = function () {

			return xrFrame;

		};

		this.getSession = function () {

			return session;

		};

		this.setSession = async function ( value ) {

			session = value;

			if ( session !== null ) {

				initialRenderTarget = renderer.getRenderTarget();

				session.addEventListener( 'select', onSessionEvent );
				session.addEventListener( 'selectstart', onSessionEvent );
				session.addEventListener( 'selectend', onSessionEvent );
				session.addEventListener( 'squeeze', onSessionEvent );
				session.addEventListener( 'squeezestart', onSessionEvent );
				session.addEventListener( 'squeezeend', onSessionEvent );
				session.addEventListener( 'end', onSessionEnd );
				session.addEventListener( 'inputsourceschange', onInputSourcesChange );

				if ( attributes.xrCompatible !== true ) {

					await gl.makeXRCompatible();

				}

				currentPixelRatio = renderer.getPixelRatio();
				renderer.getSize( currentSize );

				if ( session.renderState.layers === undefined ) {

					const layerInit = {
						antialias: attributes.antialias,
						alpha: true,
						depth: attributes.depth,
						stencil: attributes.stencil,
						framebufferScaleFactor: framebufferScaleFactor
					};

					glBaseLayer = new XRWebGLLayer( session, gl, layerInit );

					session.updateRenderState( { baseLayer: glBaseLayer } );

					renderer.setPixelRatio( 1 );
					renderer.setSize( glBaseLayer.framebufferWidth, glBaseLayer.framebufferHeight, false );

					newRenderTarget = new WebGLRenderTarget(
						glBaseLayer.framebufferWidth,
						glBaseLayer.framebufferHeight,
						{
							format: RGBAFormat,
							type: UnsignedByteType,
							colorSpace: renderer.outputColorSpace,
							stencilBuffer: attributes.stencil
						}
					);

				} else {

					let depthFormat = null;
					let depthType = null;
					let glDepthFormat = null;

					if ( attributes.depth ) {

						glDepthFormat = attributes.stencil ? gl.DEPTH24_STENCIL8 : gl.DEPTH_COMPONENT24;
						depthFormat = attributes.stencil ? DepthStencilFormat : DepthFormat;
						depthType = attributes.stencil ? UnsignedInt248Type : UnsignedIntType;

					}

					const projectionlayerInit = {
						colorFormat: gl.RGBA8,
						depthFormat: glDepthFormat,
						scaleFactor: framebufferScaleFactor
					};

					glBinding = new XRWebGLBinding( session, gl );

					glProjLayer = glBinding.createProjectionLayer( projectionlayerInit );

					session.updateRenderState( { layers: [ glProjLayer ] } );

					renderer.setPixelRatio( 1 );
					renderer.setSize( glProjLayer.textureWidth, glProjLayer.textureHeight, false );

					newRenderTarget = new WebGLRenderTarget(
						glProjLayer.textureWidth,
						glProjLayer.textureHeight,
						{
							format: RGBAFormat,
							type: UnsignedByteType,
							depthTexture: new DepthTexture( glProjLayer.textureWidth, glProjLayer.textureHeight, depthType, undefined, undefined, undefined, undefined, undefined, undefined, depthFormat ),
							stencilBuffer: attributes.stencil,
							colorSpace: renderer.outputColorSpace,
							samples: attributes.antialias ? 4 : 0,
							resolveDepthBuffer: ( glProjLayer.ignoreDepthValues === false )
						} );

				}

				newRenderTarget.isXRRenderTarget = true; // TODO Remove this when possible, see #23278

				this.setFoveation( foveation );

				customReferenceSpace = null;
				referenceSpace = await session.requestReferenceSpace( referenceSpaceType );

				animation.setContext( session );
				animation.start();

				scope.isPresenting = true;

				scope.dispatchEvent( { type: 'sessionstart' } );

			}

		};

		this.getEnvironmentBlendMode = function () {

			if ( session !== null ) {

				return session.environmentBlendMode;

			}

		};

		this.getDepthTexture = function () {

			return depthSensing.getDepthTexture();

		};

		function onInputSourcesChange( event ) {

			// Notify disconnected

			for ( let i = 0; i < event.removed.length; i ++ ) {

				const inputSource = event.removed[ i ];
				const index = controllerInputSources.indexOf( inputSource );

				if ( index >= 0 ) {

					controllerInputSources[ index ] = null;
					controllers[ index ].disconnect( inputSource );

				}

			}

			// Notify connected

			for ( let i = 0; i < event.added.length; i ++ ) {

				const inputSource = event.added[ i ];

				let controllerIndex = controllerInputSources.indexOf( inputSource );

				if ( controllerIndex === - 1 ) {

					// Assign input source a controller that currently has no input source

					for ( let i = 0; i < controllers.length; i ++ ) {

						if ( i >= controllerInputSources.length ) {

							controllerInputSources.push( inputSource );
							controllerIndex = i;
							break;

						} else if ( controllerInputSources[ i ] === null ) {

							controllerInputSources[ i ] = inputSource;
							controllerIndex = i;
							break;

						}

					}

					// If all controllers do currently receive input we ignore new ones

					if ( controllerIndex === - 1 ) break;

				}

				const controller = controllers[ controllerIndex ];

				if ( controller ) {

					controller.connect( inputSource );

				}

			}

		}

		//

		const cameraLPos = new Vector3();
		const cameraRPos = new Vector3();

		/**
		 * Assumes 2 cameras that are parallel and share an X-axis, and that
		 * the cameras' projection and world matrices have already been set.
		 * And that near and far planes are identical for both cameras.
		 * Visualization of this technique: https://computergraphics.stackexchange.com/a/4765
		 */
		function setProjectionFromUnion( camera, cameraL, cameraR ) {

			cameraLPos.setFromMatrixPosition( cameraL.matrixWorld );
			cameraRPos.setFromMatrixPosition( cameraR.matrixWorld );

			const ipd = cameraLPos.distanceTo( cameraRPos );

			const projL = cameraL.projectionMatrix.elements;
			const projR = cameraR.projectionMatrix.elements;

			// VR systems will have identical far and near planes, and
			// most likely identical top and bottom frustum extents.
			// Use the left camera for these values.
			const near = projL[ 14 ] / ( projL[ 10 ] - 1 );
			const far = projL[ 14 ] / ( projL[ 10 ] + 1 );
			const topFov = ( projL[ 9 ] + 1 ) / projL[ 5 ];
			const bottomFov = ( projL[ 9 ] - 1 ) / projL[ 5 ];

			const leftFov = ( projL[ 8 ] - 1 ) / projL[ 0 ];
			const rightFov = ( projR[ 8 ] + 1 ) / projR[ 0 ];
			const left = near * leftFov;
			const right = near * rightFov;

			// Calculate the new camera's position offset from the
			// left camera. xOffset should be roughly half `ipd`.
			const zOffset = ipd / ( - leftFov + rightFov );
			const xOffset = zOffset * - leftFov;

			// TODO: Better way to apply this offset?
			cameraL.matrixWorld.decompose( camera.position, camera.quaternion, camera.scale );
			camera.translateX( xOffset );
			camera.translateZ( zOffset );
			camera.matrixWorld.compose( camera.position, camera.quaternion, camera.scale );
			camera.matrixWorldInverse.copy( camera.matrixWorld ).invert();

			// Check if the projection uses an infinite far plane.
			if ( projL[ 10 ] === - 1.0 ) {

				// Use the projection matrix from the left eye.
				// The camera offset is sufficient to include the view volumes
				// of both eyes (assuming symmetric projections).
				camera.projectionMatrix.copy( cameraL.projectionMatrix );
				camera.projectionMatrixInverse.copy( cameraL.projectionMatrixInverse );

			} else {

				// Find the union of the frustum values of the cameras and scale
				// the values so that the near plane's position does not change in world space,
				// although must now be relative to the new union camera.
				const near2 = near + zOffset;
				const far2 = far + zOffset;
				const left2 = left - xOffset;
				const right2 = right + ( ipd - xOffset );
				const top2 = topFov * far / far2 * near2;
				const bottom2 = bottomFov * far / far2 * near2;

				camera.projectionMatrix.makePerspective( left2, right2, top2, bottom2, near2, far2 );
				camera.projectionMatrixInverse.copy( camera.projectionMatrix ).invert();

			}

		}

		function updateCamera( camera, parent ) {

			if ( parent === null ) {

				camera.matrixWorld.copy( camera.matrix );

			} else {

				camera.matrixWorld.multiplyMatrices( parent.matrixWorld, camera.matrix );

			}

			camera.matrixWorldInverse.copy( camera.matrixWorld ).invert();

		}

		this.updateCamera = function ( camera ) {

			if ( session === null ) return;

			let depthNear = camera.near;
			let depthFar = camera.far;

			if ( depthSensing.texture !== null ) {

				if ( depthSensing.depthNear > 0 ) depthNear = depthSensing.depthNear;
				if ( depthSensing.depthFar > 0 ) depthFar = depthSensing.depthFar;

			}

			cameraXR.near = cameraR.near = cameraL.near = depthNear;
			cameraXR.far = cameraR.far = cameraL.far = depthFar;

			if ( _currentDepthNear !== cameraXR.near || _currentDepthFar !== cameraXR.far ) {

				// Note that the new renderState won't apply until the next frame. See #18320

				session.updateRenderState( {
					depthNear: cameraXR.near,
					depthFar: cameraXR.far
				} );

				_currentDepthNear = cameraXR.near;
				_currentDepthFar = cameraXR.far;

			}

			const parent = camera.parent;
			const cameras = cameraXR.cameras;

			updateCamera( cameraXR, parent );

			for ( let i = 0; i < cameras.length; i ++ ) {

				updateCamera( cameras[ i ], parent );

			}

			// update projection matrix for proper view frustum culling

			if ( cameras.length === 2 ) {

				setProjectionFromUnion( cameraXR, cameraL, cameraR );

			} else {

				// assume single camera setup (AR)

				cameraXR.projectionMatrix.copy( cameraL.projectionMatrix );

			}

			// update user camera and its children

			updateUserCamera( camera, cameraXR, parent );

		};

		function updateUserCamera( camera, cameraXR, parent ) {

			if ( parent === null ) {

				camera.matrix.copy( cameraXR.matrixWorld );

			} else {

				camera.matrix.copy( parent.matrixWorld );
				camera.matrix.invert();
				camera.matrix.multiply( cameraXR.matrixWorld );

			}

			camera.matrix.decompose( camera.position, camera.quaternion, camera.scale );
			camera.updateMatrixWorld( true );

			camera.projectionMatrix.copy( cameraXR.projectionMatrix );
			camera.projectionMatrixInverse.copy( cameraXR.projectionMatrixInverse );

			if ( camera.isPerspectiveCamera ) {

				camera.fov = RAD2DEG * 2 * Math.atan( 1 / camera.projectionMatrix.elements[ 5 ] );
				camera.zoom = 1;

			}

		}

		this.getCamera = function () {

			return cameraXR;

		};

		this.getFoveation = function () {

			if ( glProjLayer === null && glBaseLayer === null ) {

				return undefined;

			}

			return foveation;

		};

		this.setFoveation = function ( value ) {

			// 0 = no foveation = full resolution
			// 1 = maximum foveation = the edges render at lower resolution

			foveation = value;

			if ( glProjLayer !== null ) {

				glProjLayer.fixedFoveation = value;

			}

			if ( glBaseLayer !== null && glBaseLayer.fixedFoveation !== undefined ) {

				glBaseLayer.fixedFoveation = value;

			}

		};

		this.hasDepthSensing = function () {

			return depthSensing.texture !== null;

		};

		this.getDepthSensingMesh = function () {

			return depthSensing.getMesh( cameraXR );

		};

		// Animation Loop

		let onAnimationFrameCallback = null;

		function onAnimationFrame( time, frame ) {

			pose = frame.getViewerPose( customReferenceSpace || referenceSpace );
			xrFrame = frame;

			if ( pose !== null ) {

				const views = pose.views;

				if ( glBaseLayer !== null ) {

					renderer.setRenderTargetFramebuffer( newRenderTarget, glBaseLayer.framebuffer );
					renderer.setRenderTarget( newRenderTarget );

				}

				let cameraXRNeedsUpdate = false;

				// check if it's necessary to rebuild cameraXR's camera list

				if ( views.length !== cameraXR.cameras.length ) {

					cameraXR.cameras.length = 0;
					cameraXRNeedsUpdate = true;

				}

				for ( let i = 0; i < views.length; i ++ ) {

					const view = views[ i ];

					let viewport = null;

					if ( glBaseLayer !== null ) {

						viewport = glBaseLayer.getViewport( view );

					} else {

						const glSubImage = glBinding.getViewSubImage( glProjLayer, view );
						viewport = glSubImage.viewport;

						// For side-by-side projection, we only produce a single texture for both eyes.
						if ( i === 0 ) {

							renderer.setRenderTargetTextures(
								newRenderTarget,
								glSubImage.colorTexture,
								glProjLayer.ignoreDepthValues ? undefined : glSubImage.depthStencilTexture );

							renderer.setRenderTarget( newRenderTarget );

						}

					}

					let camera = cameras[ i ];

					if ( camera === undefined ) {

						camera = new PerspectiveCamera();
						camera.layers.enable( i );
						camera.viewport = new Vector4();
						cameras[ i ] = camera;

					}

					camera.matrix.fromArray( view.transform.matrix );
					camera.matrix.decompose( camera.position, camera.quaternion, camera.scale );
					camera.projectionMatrix.fromArray( view.projectionMatrix );
					camera.projectionMatrixInverse.copy( camera.projectionMatrix ).invert();
					camera.viewport.set( viewport.x, viewport.y, viewport.width, viewport.height );

					if ( i === 0 ) {

						cameraXR.matrix.copy( camera.matrix );
						cameraXR.matrix.decompose( cameraXR.position, cameraXR.quaternion, cameraXR.scale );

					}

					if ( cameraXRNeedsUpdate === true ) {

						cameraXR.cameras.push( camera );

					}

				}

				//

				const enabledFeatures = session.enabledFeatures;

				if ( enabledFeatures && enabledFeatures.includes( 'depth-sensing' ) ) {

					const depthData = glBinding.getDepthInformation( views[ 0 ] );

					if ( depthData && depthData.isValid && depthData.texture ) {

						depthSensing.init( renderer, depthData, session.renderState );

					}

				}

			}

			//

			for ( let i = 0; i < controllers.length; i ++ ) {

				const inputSource = controllerInputSources[ i ];
				const controller = controllers[ i ];

				if ( inputSource !== null && controller !== undefined ) {

					controller.update( inputSource, frame, customReferenceSpace || referenceSpace );

				}

			}

			if ( onAnimationFrameCallback ) onAnimationFrameCallback( time, frame );

			if ( frame.detectedPlanes ) {

				scope.dispatchEvent( { type: 'planesdetected', data: frame } );

			}

			xrFrame = null;

		}

		const animation = new WebGLAnimation();

		animation.setAnimationLoop( onAnimationFrame );

		this.setAnimationLoop = function ( callback ) {

			onAnimationFrameCallback = callback;

		};

		this.dispose = function () {};

	}

}

const _e1 = /*@__PURE__*/ new Euler();
const _m1 = /*@__PURE__*/ new Matrix4();

function WebGLMaterials( renderer, properties ) {

	function refreshTransformUniform( map, uniform ) {

		if ( map.matrixAutoUpdate === true ) {

			map.updateMatrix();

		}

		uniform.value.copy( map.matrix );

	}

	function refreshFogUniforms( uniforms, fog ) {

		fog.color.getRGB( uniforms.fogColor.value, getUnlitUniformColorSpace( renderer ) );

		if ( fog.isFog ) {

			uniforms.fogNear.value = fog.near;
			uniforms.fogFar.value = fog.far;

		} else if ( fog.isFogExp2 ) {

			uniforms.fogDensity.value = fog.density;

		}

	}

	function refreshMaterialUniforms( uniforms, material, pixelRatio, height, transmissionRenderTarget ) {

		if ( material.isMeshBasicMaterial ) {

			refreshUniformsCommon( uniforms, material );

		} else if ( material.isMeshLambertMaterial ) {

			refreshUniformsCommon( uniforms, material );

		} else if ( material.isMeshToonMaterial ) {

			refreshUniformsCommon( uniforms, material );
			refreshUniformsToon( uniforms, material );

		} else if ( material.isMeshPhongMaterial ) {

			refreshUniformsCommon( uniforms, material );
			refreshUniformsPhong( uniforms, material );

		} else if ( material.isMeshStandardMaterial ) {

			refreshUniformsCommon( uniforms, material );
			refreshUniformsStandard( uniforms, material );

			if ( material.isMeshPhysicalMaterial ) {

				refreshUniformsPhysical( uniforms, material, transmissionRenderTarget );

			}

		} else if ( material.isMeshMatcapMaterial ) {

			refreshUniformsCommon( uniforms, material );
			refreshUniformsMatcap( uniforms, material );

		} else if ( material.isMeshDepthMaterial ) {

			refreshUniformsCommon( uniforms, material );

		} else if ( material.isMeshDistanceMaterial ) {

			refreshUniformsCommon( uniforms, material );
			refreshUniformsDistance( uniforms, material );

		} else if ( material.isMeshNormalMaterial ) {

			refreshUniformsCommon( uniforms, material );

		} else if ( material.isLineBasicMaterial ) {

			refreshUniformsLine( uniforms, material );

			if ( material.isLineDashedMaterial ) {

				refreshUniformsDash( uniforms, material );

			}

		} else if ( material.isPointsMaterial ) {

			refreshUniformsPoints( uniforms, material, pixelRatio, height );

		} else if ( material.isSpriteMaterial ) {

			refreshUniformsSprites( uniforms, material );

		} else if ( material.isShadowMaterial ) {

			uniforms.color.value.copy( material.color );
			uniforms.opacity.value = material.opacity;

		} else if ( material.isShaderMaterial ) {

			material.uniformsNeedUpdate = false; // #15581

		}

	}

	function refreshUniformsCommon( uniforms, material ) {

		uniforms.opacity.value = material.opacity;

		if ( material.color ) {

			uniforms.diffuse.value.copy( material.color );

		}

		if ( material.emissive ) {

			uniforms.emissive.value.copy( material.emissive ).multiplyScalar( material.emissiveIntensity );

		}

		if ( material.map ) {

			uniforms.map.value = material.map;

			refreshTransformUniform( material.map, uniforms.mapTransform );

		}

		if ( material.alphaMap ) {

			uniforms.alphaMap.value = material.alphaMap;

			refreshTransformUniform( material.alphaMap, uniforms.alphaMapTransform );

		}

		if ( material.bumpMap ) {

			uniforms.bumpMap.value = material.bumpMap;

			refreshTransformUniform( material.bumpMap, uniforms.bumpMapTransform );

			uniforms.bumpScale.value = material.bumpScale;

			if ( material.side === BackSide ) {

				uniforms.bumpScale.value *= - 1;

			}

		}

		if ( material.normalMap ) {

			uniforms.normalMap.value = material.normalMap;

			refreshTransformUniform( material.normalMap, uniforms.normalMapTransform );

			uniforms.normalScale.value.copy( material.normalScale );

			if ( material.side === BackSide ) {

				uniforms.normalScale.value.negate();

			}

		}

		if ( material.displacementMap ) {

			uniforms.displacementMap.value = material.displacementMap;

			refreshTransformUniform( material.displacementMap, uniforms.displacementMapTransform );

			uniforms.displacementScale.value = material.displacementScale;
			uniforms.displacementBias.value = material.displacementBias;

		}

		if ( material.emissiveMap ) {

			uniforms.emissiveMap.value = material.emissiveMap;

			refreshTransformUniform( material.emissiveMap, uniforms.emissiveMapTransform );

		}

		if ( material.specularMap ) {

			uniforms.specularMap.value = material.specularMap;

			refreshTransformUniform( material.specularMap, uniforms.specularMapTransform );

		}

		if ( material.alphaTest > 0 ) {

			uniforms.alphaTest.value = material.alphaTest;

		}

		const materialProperties = properties.get( material );

		const envMap = materialProperties.envMap;
		const envMapRotation = materialProperties.envMapRotation;

		if ( envMap ) {

			uniforms.envMap.value = envMap;

			_e1.copy( envMapRotation );

			// accommodate left-handed frame
			_e1.x *= - 1; _e1.y *= - 1; _e1.z *= - 1;

			if ( envMap.isCubeTexture && envMap.isRenderTargetTexture === false ) {

				// environment maps which are not cube render targets or PMREMs follow a different convention
				_e1.y *= - 1;
				_e1.z *= - 1;

			}

			uniforms.envMapRotation.value.setFromMatrix4( _m1.makeRotationFromEuler( _e1 ) );

			uniforms.flipEnvMap.value = ( envMap.isCubeTexture && envMap.isRenderTargetTexture === false ) ? - 1 : 1;

			uniforms.reflectivity.value = material.reflectivity;
			uniforms.ior.value = material.ior;
			uniforms.refractionRatio.value = material.refractionRatio;

		}

		if ( material.lightMap ) {

			uniforms.lightMap.value = material.lightMap;
			uniforms.lightMapIntensity.value = material.lightMapIntensity;

			refreshTransformUniform( material.lightMap, uniforms.lightMapTransform );

		}

		if ( material.aoMap ) {

			uniforms.aoMap.value = material.aoMap;
			uniforms.aoMapIntensity.value = material.aoMapIntensity;

			refreshTransformUniform( material.aoMap, uniforms.aoMapTransform );

		}

	}

	function refreshUniformsLine( uniforms, material ) {

		uniforms.diffuse.value.copy( material.color );
		uniforms.opacity.value = material.opacity;

		if ( material.map ) {

			uniforms.map.value = material.map;

			refreshTransformUniform( material.map, uniforms.mapTransform );

		}

	}

	function refreshUniformsDash( uniforms, material ) {

		uniforms.dashSize.value = material.dashSize;
		uniforms.totalSize.value = material.dashSize + material.gapSize;
		uniforms.scale.value = material.scale;

	}

	function refreshUniformsPoints( uniforms, material, pixelRatio, height ) {

		uniforms.diffuse.value.copy( material.color );
		uniforms.opacity.value = material.opacity;
		uniforms.size.value = material.size * pixelRatio;
		uniforms.scale.value = height * 0.5;

		if ( material.map ) {

			uniforms.map.value = material.map;

			refreshTransformUniform( material.map, uniforms.uvTransform );

		}

		if ( material.alphaMap ) {

			uniforms.alphaMap.value = material.alphaMap;

			refreshTransformUniform( material.alphaMap, uniforms.alphaMapTransform );

		}

		if ( material.alphaTest > 0 ) {

			uniforms.alphaTest.value = material.alphaTest;

		}

	}

	function refreshUniformsSprites( uniforms, material ) {

		uniforms.diffuse.value.copy( material.color );
		uniforms.opacity.value = material.opacity;
		uniforms.rotation.value = material.rotation;

		if ( material.map ) {

			uniforms.map.value = material.map;

			refreshTransformUniform( material.map, uniforms.mapTransform );

		}

		if ( material.alphaMap ) {

			uniforms.alphaMap.value = material.alphaMap;

			refreshTransformUniform( material.alphaMap, uniforms.alphaMapTransform );

		}

		if ( material.alphaTest > 0 ) {

			uniforms.alphaTest.value = material.alphaTest;

		}

	}

	function refreshUniformsPhong( uniforms, material ) {

		uniforms.specular.value.copy( material.specular );
		uniforms.shininess.value = Math.max( material.shininess, 1e-4 ); // to prevent pow( 0.0, 0.0 )

	}

	function refreshUniformsToon( uniforms, material ) {

		if ( material.gradientMap ) {

			uniforms.gradientMap.value = material.gradientMap;

		}

	}

	function refreshUniformsStandard( uniforms, material ) {

		uniforms.metalness.value = material.metalness;

		if ( material.metalnessMap ) {

			uniforms.metalnessMap.value = material.metalnessMap;

			refreshTransformUniform( material.metalnessMap, uniforms.metalnessMapTransform );

		}

		uniforms.roughness.value = material.roughness;

		if ( material.roughnessMap ) {

			uniforms.roughnessMap.value = material.roughnessMap;

			refreshTransformUniform( material.roughnessMap, uniforms.roughnessMapTransform );

		}

		if ( material.envMap ) {

			//uniforms.envMap.value = material.envMap; // part of uniforms common

			uniforms.envMapIntensity.value = material.envMapIntensity;

		}

	}

	function refreshUniformsPhysical( uniforms, material, transmissionRenderTarget ) {

		uniforms.ior.value = material.ior; // also part of uniforms common

		if ( material.sheen > 0 ) {

			uniforms.sheenColor.value.copy( material.sheenColor ).multiplyScalar( material.sheen );

			uniforms.sheenRoughness.value = material.sheenRoughness;

			if ( material.sheenColorMap ) {

				uniforms.sheenColorMap.value = material.sheenColorMap;

				refreshTransformUniform( material.sheenColorMap, uniforms.sheenColorMapTransform );

			}

			if ( material.sheenRoughnessMap ) {

				uniforms.sheenRoughnessMap.value = material.sheenRoughnessMap;

				refreshTransformUniform( material.sheenRoughnessMap, uniforms.sheenRoughnessMapTransform );

			}

		}

		if ( material.clearcoat > 0 ) {

			uniforms.clearcoat.value = material.clearcoat;
			uniforms.clearcoatRoughness.value = material.clearcoatRoughness;

			if ( material.clearcoatMap ) {

				uniforms.clearcoatMap.value = material.clearcoatMap;

				refreshTransformUniform( material.clearcoatMap, uniforms.clearcoatMapTransform );

			}

			if ( material.clearcoatRoughnessMap ) {

				uniforms.clearcoatRoughnessMap.value = material.clearcoatRoughnessMap;

				refreshTransformUniform( material.clearcoatRoughnessMap, uniforms.clearcoatRoughnessMapTransform );

			}

			if ( material.clearcoatNormalMap ) {

				uniforms.clearcoatNormalMap.value = material.clearcoatNormalMap;

				refreshTransformUniform( material.clearcoatNormalMap, uniforms.clearcoatNormalMapTransform );

				uniforms.clearcoatNormalScale.value.copy( material.clearcoatNormalScale );

				if ( material.side === BackSide ) {

					uniforms.clearcoatNormalScale.value.negate();

				}

			}

		}

		if ( material.dispersion > 0 ) {

			uniforms.dispersion.value = material.dispersion;

		}

		if ( material.iridescence > 0 ) {

			uniforms.iridescence.value = material.iridescence;
			uniforms.iridescenceIOR.value = material.iridescenceIOR;
			uniforms.iridescenceThicknessMinimum.value = material.iridescenceThicknessRange[ 0 ];
			uniforms.iridescenceThicknessMaximum.value = material.iridescenceThicknessRange[ 1 ];

			if ( material.iridescenceMap ) {

				uniforms.iridescenceMap.value = material.iridescenceMap;

				refreshTransformUniform( material.iridescenceMap, uniforms.iridescenceMapTransform );

			}

			if ( material.iridescenceThicknessMap ) {

				uniforms.iridescenceThicknessMap.value = material.iridescenceThicknessMap;

				refreshTransformUniform( material.iridescenceThicknessMap, uniforms.iridescenceThicknessMapTransform );

			}

		}

		if ( material.transmission > 0 ) {

			uniforms.transmission.value = material.transmission;
			uniforms.transmissionSamplerMap.value = transmissionRenderTarget.texture;
			uniforms.transmissionSamplerSize.value.set( transmissionRenderTarget.width, transmissionRenderTarget.height );

			if ( material.transmissionMap ) {

				uniforms.transmissionMap.value = material.transmissionMap;

				refreshTransformUniform( material.transmissionMap, uniforms.transmissionMapTransform );

			}

			uniforms.thickness.value = material.thickness;

			if ( material.thicknessMap ) {

				uniforms.thicknessMap.value = material.thicknessMap;

				refreshTransformUniform( material.thicknessMap, uniforms.thicknessMapTransform );

			}

			uniforms.attenuationDistance.value = material.attenuationDistance;
			uniforms.attenuationColor.value.copy( material.attenuationColor );

		}

		if ( material.anisotropy > 0 ) {

			uniforms.anisotropyVector.value.set( material.anisotropy * Math.cos( material.anisotropyRotation ), material.anisotropy * Math.sin( material.anisotropyRotation ) );

			if ( material.anisotropyMap ) {

				uniforms.anisotropyMap.value = material.anisotropyMap;

				refreshTransformUniform( material.anisotropyMap, uniforms.anisotropyMapTransform );

			}

		}

		uniforms.specularIntensity.value = material.specularIntensity;
		uniforms.specularColor.value.copy( material.specularColor );

		if ( material.specularColorMap ) {

			uniforms.specularColorMap.value = material.specularColorMap;

			refreshTransformUniform( material.specularColorMap, uniforms.specularColorMapTransform );

		}

		if ( material.specularIntensityMap ) {

			uniforms.specularIntensityMap.value = material.specularIntensityMap;

			refreshTransformUniform( material.specularIntensityMap, uniforms.specularIntensityMapTransform );

		}

	}

	function refreshUniformsMatcap( uniforms, material ) {

		if ( material.matcap ) {

			uniforms.matcap.value = material.matcap;

		}

	}

	function refreshUniformsDistance( uniforms, material ) {

		const light = properties.get( material ).light;

		uniforms.referencePosition.value.setFromMatrixPosition( light.matrixWorld );
		uniforms.nearDistance.value = light.shadow.camera.near;
		uniforms.farDistance.value = light.shadow.camera.far;

	}

	return {
		refreshFogUniforms: refreshFogUniforms,
		refreshMaterialUniforms: refreshMaterialUniforms
	};

}

function WebGLUniformsGroups( gl, info, capabilities, state ) {

	let buffers = {};
	let updateList = {};
	let allocatedBindingPoints = [];

	const maxBindingPoints = gl.getParameter( gl.MAX_UNIFORM_BUFFER_BINDINGS ); // binding points are global whereas block indices are per shader program

	function bind( uniformsGroup, program ) {

		const webglProgram = program.program;
		state.uniformBlockBinding( uniformsGroup, webglProgram );

	}

	function update( uniformsGroup, program ) {

		let buffer = buffers[ uniformsGroup.id ];

		if ( buffer === undefined ) {

			prepareUniformsGroup( uniformsGroup );

			buffer = createBuffer( uniformsGroup );
			buffers[ uniformsGroup.id ] = buffer;

			uniformsGroup.addEventListener( 'dispose', onUniformsGroupsDispose );

		}

		// ensure to update the binding points/block indices mapping for this program

		const webglProgram = program.program;
		state.updateUBOMapping( uniformsGroup, webglProgram );

		// update UBO once per frame

		const frame = info.render.frame;

		if ( updateList[ uniformsGroup.id ] !== frame ) {

			updateBufferData( uniformsGroup );

			updateList[ uniformsGroup.id ] = frame;

		}

	}

	function createBuffer( uniformsGroup ) {

		// the setup of an UBO is independent of a particular shader program but global

		const bindingPointIndex = allocateBindingPointIndex();
		uniformsGroup.__bindingPointIndex = bindingPointIndex;

		const buffer = gl.createBuffer();
		const size = uniformsGroup.__size;
		const usage = uniformsGroup.usage;

		gl.bindBuffer( gl.UNIFORM_BUFFER, buffer );
		gl.bufferData( gl.UNIFORM_BUFFER, size, usage );
		gl.bindBuffer( gl.UNIFORM_BUFFER, null );
		gl.bindBufferBase( gl.UNIFORM_BUFFER, bindingPointIndex, buffer );

		return buffer;

	}

	function allocateBindingPointIndex() {

		for ( let i = 0; i < maxBindingPoints; i ++ ) {

			if ( allocatedBindingPoints.indexOf( i ) === - 1 ) {

				allocatedBindingPoints.push( i );
				return i;

			}

		}

		console.error( 'THREE.WebGLRenderer: Maximum number of simultaneously usable uniforms groups reached.' );

		return 0;

	}

	function updateBufferData( uniformsGroup ) {

		const buffer = buffers[ uniformsGroup.id ];
		const uniforms = uniformsGroup.uniforms;
		const cache = uniformsGroup.__cache;

		gl.bindBuffer( gl.UNIFORM_BUFFER, buffer );

		for ( let i = 0, il = uniforms.length; i < il; i ++ ) {

			const uniformArray = Array.isArray( uniforms[ i ] ) ? uniforms[ i ] : [ uniforms[ i ] ];

			for ( let j = 0, jl = uniformArray.length; j < jl; j ++ ) {

				const uniform = uniformArray[ j ];

				if ( hasUniformChanged( uniform, i, j, cache ) === true ) {

					const offset = uniform.__offset;

					const values = Array.isArray( uniform.value ) ? uniform.value : [ uniform.value ];

					let arrayOffset = 0;

					for ( let k = 0; k < values.length; k ++ ) {

						const value = values[ k ];

						const info = getUniformSize( value );

						// TODO add integer and struct support
						if ( typeof value === 'number' || typeof value === 'boolean' ) {

							uniform.__data[ 0 ] = value;
							gl.bufferSubData( gl.UNIFORM_BUFFER, offset + arrayOffset, uniform.__data );

						} else if ( value.isMatrix3 ) {

							// manually converting 3x3 to 3x4

							uniform.__data[ 0 ] = value.elements[ 0 ];
							uniform.__data[ 1 ] = value.elements[ 1 ];
							uniform.__data[ 2 ] = value.elements[ 2 ];
							uniform.__data[ 3 ] = 0;
							uniform.__data[ 4 ] = value.elements[ 3 ];
							uniform.__data[ 5 ] = value.elements[ 4 ];
							uniform.__data[ 6 ] = value.elements[ 5 ];
							uniform.__data[ 7 ] = 0;
							uniform.__data[ 8 ] = value.elements[ 6 ];
							uniform.__data[ 9 ] = value.elements[ 7 ];
							uniform.__data[ 10 ] = value.elements[ 8 ];
							uniform.__data[ 11 ] = 0;

						} else {

							value.toArray( uniform.__data, arrayOffset );

							arrayOffset += info.storage / Float32Array.BYTES_PER_ELEMENT;

						}

					}

					gl.bufferSubData( gl.UNIFORM_BUFFER, offset, uniform.__data );

				}

			}

		}

		gl.bindBuffer( gl.UNIFORM_BUFFER, null );

	}

	function hasUniformChanged( uniform, index, indexArray, cache ) {

		const value = uniform.value;
		const indexString = index + '_' + indexArray;

		if ( cache[ indexString ] === undefined ) {

			// cache entry does not exist so far

			if ( typeof value === 'number' || typeof value === 'boolean' ) {

				cache[ indexString ] = value;

			} else {

				cache[ indexString ] = value.clone();

			}

			return true;

		} else {

			const cachedObject = cache[ indexString ];

			// compare current value with cached entry

			if ( typeof value === 'number' || typeof value === 'boolean' ) {

				if ( cachedObject !== value ) {

					cache[ indexString ] = value;
					return true;

				}

			} else {

				if ( cachedObject.equals( value ) === false ) {

					cachedObject.copy( value );
					return true;

				}

			}

		}

		return false;

	}

	function prepareUniformsGroup( uniformsGroup ) {

		// determine total buffer size according to the STD140 layout
		// Hint: STD140 is the only supported layout in WebGL 2

		const uniforms = uniformsGroup.uniforms;

		let offset = 0; // global buffer offset in bytes
		const chunkSize = 16; // size of a chunk in bytes

		for ( let i = 0, l = uniforms.length; i < l; i ++ ) {

			const uniformArray = Array.isArray( uniforms[ i ] ) ? uniforms[ i ] : [ uniforms[ i ] ];

			for ( let j = 0, jl = uniformArray.length; j < jl; j ++ ) {

				const uniform = uniformArray[ j ];

				const values = Array.isArray( uniform.value ) ? uniform.value : [ uniform.value ];

				for ( let k = 0, kl = values.length; k < kl; k ++ ) {

					const value = values[ k ];

					const info = getUniformSize( value );

					const chunkOffset = offset % chunkSize; // offset in the current chunk
					const chunkPadding = chunkOffset % info.boundary; // required padding to match boundary
					const chunkStart = chunkOffset + chunkPadding; // the start position in the current chunk for the data

					offset += chunkPadding;

					// Check for chunk overflow
					if ( chunkStart !== 0 && ( chunkSize - chunkStart ) < info.storage ) {

						// Add padding and adjust offset
						offset += ( chunkSize - chunkStart );

					}

					// the following two properties will be used for partial buffer updates
					uniform.__data = new Float32Array( info.storage / Float32Array.BYTES_PER_ELEMENT );
					uniform.__offset = offset;

					// Update the global offset
					offset += info.storage;

				}

			}

		}

		// ensure correct final padding

		const chunkOffset = offset % chunkSize;

		if ( chunkOffset > 0 ) offset += ( chunkSize - chunkOffset );

		//

		uniformsGroup.__size = offset;
		uniformsGroup.__cache = {};

		return this;

	}

	function getUniformSize( value ) {

		const info = {
			boundary: 0, // bytes
			storage: 0 // bytes
		};

		// determine sizes according to STD140

		if ( typeof value === 'number' || typeof value === 'boolean' ) {

			// float/int/bool

			info.boundary = 4;
			info.storage = 4;

		} else if ( value.isVector2 ) {

			// vec2

			info.boundary = 8;
			info.storage = 8;

		} else if ( value.isVector3 || value.isColor ) {

			// vec3

			info.boundary = 16;
			info.storage = 12; // evil: vec3 must start on a 16-byte boundary but it only consumes 12 bytes

		} else if ( value.isVector4 ) {

			// vec4

			info.boundary = 16;
			info.storage = 16;

		} else if ( value.isMatrix3 ) {

			// mat3 (in STD140 a 3x3 matrix is represented as 3x4)

			info.boundary = 48;
			info.storage = 48;

		} else if ( value.isMatrix4 ) {

			// mat4

			info.boundary = 64;
			info.storage = 64;

		} else if ( value.isTexture ) {

			console.warn( 'THREE.WebGLRenderer: Texture samplers can not be part of an uniforms group.' );

		} else {

			console.warn( 'THREE.WebGLRenderer: Unsupported uniform value type.', value );

		}

		return info;

	}

	function onUniformsGroupsDispose( event ) {

		const uniformsGroup = event.target;

		uniformsGroup.removeEventListener( 'dispose', onUniformsGroupsDispose );

		const index = allocatedBindingPoints.indexOf( uniformsGroup.__bindingPointIndex );
		allocatedBindingPoints.splice( index, 1 );

		gl.deleteBuffer( buffers[ uniformsGroup.id ] );

		delete buffers[ uniformsGroup.id ];
		delete updateList[ uniformsGroup.id ];

	}

	function dispose() {

		for ( const id in buffers ) {

			gl.deleteBuffer( buffers[ id ] );

		}

		allocatedBindingPoints = [];
		buffers = {};
		updateList = {};

	}

	return {

		bind: bind,
		update: update,

		dispose: dispose

	};

}

class WebGLRenderer {

	constructor( parameters = {} ) {

		const {
			canvas = createCanvasElement(),
			context = null,
			depth = true,
			stencil = false,
			alpha = false,
			antialias = false,
			premultipliedAlpha = true,
			preserveDrawingBuffer = false,
			powerPreference = 'default',
			failIfMajorPerformanceCaveat = false,
		} = parameters;

		this.isWebGLRenderer = true;

		let _alpha;

		if ( context !== null ) {

			if ( typeof WebGLRenderingContext !== 'undefined' && context instanceof WebGLRenderingContext ) {

				throw new Error( 'THREE.WebGLRenderer: WebGL 1 is not supported since r163.' );

			}

			_alpha = context.getContextAttributes().alpha;

		} else {

			_alpha = alpha;

		}

		const uintClearColor = new Uint32Array( 4 );
		const intClearColor = new Int32Array( 4 );

		let currentRenderList = null;
		let currentRenderState = null;

		// render() can be called from within a callback triggered by another render.
		// We track this so that the nested render call gets its list and state isolated from the parent render call.

		const renderListStack = [];
		const renderStateStack = [];

		// public properties

		this.domElement = canvas;

		// Debug configuration container
		this.debug = {

			/**
			 * Enables error checking and reporting when shader programs are being compiled
			 * @type {boolean}
			 */
			checkShaderErrors: true,
			/**
			 * Callback for custom error reporting.
			 * @type {?Function}
			 */
			onShaderError: null
		};

		// clearing

		this.autoClear = true;
		this.autoClearColor = true;
		this.autoClearDepth = true;
		this.autoClearStencil = true;

		// scene graph

		this.sortObjects = true;

		// user-defined clipping

		this.clippingPlanes = [];
		this.localClippingEnabled = false;

		// physically based shading

		this._outputColorSpace = SRGBColorSpace;

		// tone mapping

		this.toneMapping = NoToneMapping;
		this.toneMappingExposure = 1.0;

		// internal properties

		const _this = this;

		let _isContextLost = false;

		// internal state cache

		let _currentActiveCubeFace = 0;
		let _currentActiveMipmapLevel = 0;
		let _currentRenderTarget = null;
		let _currentMaterialId = - 1;

		let _currentCamera = null;

		const _currentViewport = new Vector4();
		const _currentScissor = new Vector4();
		let _currentScissorTest = null;

		const _currentClearColor = new Color( 0x000000 );
		let _currentClearAlpha = 0;

		//

		let _width = canvas.width;
		let _height = canvas.height;

		let _pixelRatio = 1;
		let _opaqueSort = null;
		let _transparentSort = null;

		const _viewport = new Vector4( 0, 0, _width, _height );
		const _scissor = new Vector4( 0, 0, _width, _height );
		let _scissorTest = false;

		// frustum

		const _frustum = new Frustum();

		// clipping

		let _clippingEnabled = false;
		let _localClippingEnabled = false;

		// camera matrices cache

		const _currentProjectionMatrix = new Matrix4();
		const _projScreenMatrix = new Matrix4();

		const _vector3 = new Vector3();

		const _vector4 = new Vector4();

		const _emptyScene = { background: null, fog: null, environment: null, overrideMaterial: null, isScene: true };

		let _renderBackground = false;

		function getTargetPixelRatio() {

			return _currentRenderTarget === null ? _pixelRatio : 1;

		}

		// initialize

		let _gl = context;

		function getContext( contextName, contextAttributes ) {

			return canvas.getContext( contextName, contextAttributes );

		}

		try {

			const contextAttributes = {
				alpha: true,
				depth,
				stencil,
				antialias,
				premultipliedAlpha,
				preserveDrawingBuffer,
				powerPreference,
				failIfMajorPerformanceCaveat,
			};

			// OffscreenCanvas does not have setAttribute, see #22811
			if ( 'setAttribute' in canvas ) canvas.setAttribute( 'data-engine', `three.js r${REVISION}` );

			// event listeners must be registered before WebGL context is created, see #12753
			canvas.addEventListener( 'webglcontextlost', onContextLost, false );
			canvas.addEventListener( 'webglcontextrestored', onContextRestore, false );
			canvas.addEventListener( 'webglcontextcreationerror', onContextCreationError, false );

			if ( _gl === null ) {

				const contextName = 'webgl2';

				_gl = getContext( contextName, contextAttributes );

				if ( _gl === null ) {

					if ( getContext( contextName ) ) {

						throw new Error( 'Error creating WebGL context with your selected attributes.' );

					} else {

						throw new Error( 'Error creating WebGL context.' );

					}

				}

			}

		} catch ( error ) {

			console.error( 'THREE.WebGLRenderer: ' + error.message );
			throw error;

		}

		let extensions, capabilities, state, info;
		let properties, textures, cubemaps, cubeuvmaps, attributes, geometries, objects;
		let programCache, materials, renderLists, renderStates, clipping, shadowMap;

		let background, morphtargets, bufferRenderer, indexedBufferRenderer;

		let utils, bindingStates, uniformsGroups;

		function initGLContext() {

			extensions = new WebGLExtensions( _gl );
			extensions.init();

			utils = new WebGLUtils( _gl, extensions );

			capabilities = new WebGLCapabilities( _gl, extensions, parameters, utils );

			state = new WebGLState( _gl );

			if ( capabilities.reverseDepthBuffer ) state.buffers.depth.setReversed( true );

			info = new WebGLInfo( _gl );
			properties = new WebGLProperties();
			textures = new WebGLTextures( _gl, extensions, state, properties, capabilities, utils, info );
			cubemaps = new WebGLCubeMaps( _this );
			cubeuvmaps = new WebGLCubeUVMaps( _this );
			attributes = new WebGLAttributes( _gl );
			bindingStates = new WebGLBindingStates( _gl, attributes );
			geometries = new WebGLGeometries( _gl, attributes, info, bindingStates );
			objects = new WebGLObjects( _gl, geometries, attributes, info );
			morphtargets = new WebGLMorphtargets( _gl, capabilities, textures );
			clipping = new WebGLClipping( properties );
			programCache = new WebGLPrograms( _this, cubemaps, cubeuvmaps, extensions, capabilities, bindingStates, clipping );
			materials = new WebGLMaterials( _this, properties );
			renderLists = new WebGLRenderLists();
			renderStates = new WebGLRenderStates( extensions );
			background = new WebGLBackground( _this, cubemaps, cubeuvmaps, state, objects, _alpha, premultipliedAlpha );
			shadowMap = new WebGLShadowMap( _this, objects, capabilities );
			uniformsGroups = new WebGLUniformsGroups( _gl, info, capabilities, state );

			bufferRenderer = new WebGLBufferRenderer( _gl, extensions, info );
			indexedBufferRenderer = new WebGLIndexedBufferRenderer( _gl, extensions, info );

			info.programs = programCache.programs;

			_this.capabilities = capabilities;
			_this.extensions = extensions;
			_this.properties = properties;
			_this.renderLists = renderLists;
			_this.shadowMap = shadowMap;
			_this.state = state;
			_this.info = info;

		}

		initGLContext();

		// xr

		const xr = new WebXRManager( _this, _gl );

		this.xr = xr;

		// API

		this.getContext = function () {

			return _gl;

		};

		this.getContextAttributes = function () {

			return _gl.getContextAttributes();

		};

		this.forceContextLoss = function () {

			const extension = extensions.get( 'WEBGL_lose_context' );
			if ( extension ) extension.loseContext();

		};

		this.forceContextRestore = function () {

			const extension = extensions.get( 'WEBGL_lose_context' );
			if ( extension ) extension.restoreContext();

		};

		this.getPixelRatio = function () {

			return _pixelRatio;

		};

		this.setPixelRatio = function ( value ) {

			if ( value === undefined ) return;

			_pixelRatio = value;

			this.setSize( _width, _height, false );

		};

		this.getSize = function ( target ) {

			return target.set( _width, _height );

		};

		this.setSize = function ( width, height, updateStyle = true ) {

			if ( xr.isPresenting ) {

				console.warn( 'THREE.WebGLRenderer: Can\'t change size while VR device is presenting.' );
				return;

			}

			_width = width;
			_height = height;

			canvas.width = Math.floor( width * _pixelRatio );
			canvas.height = Math.floor( height * _pixelRatio );

			if ( updateStyle === true ) {

				canvas.style.width = width + 'px';
				canvas.style.height = height + 'px';

			}

			this.setViewport( 0, 0, width, height );

		};

		this.getDrawingBufferSize = function ( target ) {

			return target.set( _width * _pixelRatio, _height * _pixelRatio ).floor();

		};

		this.setDrawingBufferSize = function ( width, height, pixelRatio ) {

			_width = width;
			_height = height;

			_pixelRatio = pixelRatio;

			canvas.width = Math.floor( width * pixelRatio );
			canvas.height = Math.floor( height * pixelRatio );

			this.setViewport( 0, 0, width, height );

		};

		this.getCurrentViewport = function ( target ) {

			return target.copy( _currentViewport );

		};

		this.getViewport = function ( target ) {

			return target.copy( _viewport );

		};

		this.setViewport = function ( x, y, width, height ) {

			if ( x.isVector4 ) {

				_viewport.set( x.x, x.y, x.z, x.w );

			} else {

				_viewport.set( x, y, width, height );

			}

			state.viewport( _currentViewport.copy( _viewport ).multiplyScalar( _pixelRatio ).round() );

		};

		this.getScissor = function ( target ) {

			return target.copy( _scissor );

		};

		this.setScissor = function ( x, y, width, height ) {

			if ( x.isVector4 ) {

				_scissor.set( x.x, x.y, x.z, x.w );

			} else {

				_scissor.set( x, y, width, height );

			}

			state.scissor( _currentScissor.copy( _scissor ).multiplyScalar( _pixelRatio ).round() );

		};

		this.getScissorTest = function () {

			return _scissorTest;

		};

		this.setScissorTest = function ( boolean ) {

			state.setScissorTest( _scissorTest = boolean );

		};

		this.setOpaqueSort = function ( method ) {

			_opaqueSort = method;

		};

		this.setTransparentSort = function ( method ) {

			_transparentSort = method;

		};

		// Clearing

		this.getClearColor = function ( target ) {

			return target.copy( background.getClearColor() );

		};

		this.setClearColor = function () {

			background.setClearColor.apply( background, arguments );

		};

		this.getClearAlpha = function () {

			return background.getClearAlpha();

		};

		this.setClearAlpha = function () {

			background.setClearAlpha.apply( background, arguments );

		};

		this.clear = function ( color = true, depth = true, stencil = true ) {

			let bits = 0;

			if ( color ) {

				// check if we're trying to clear an integer target
				let isIntegerFormat = false;
				if ( _currentRenderTarget !== null ) {

					const targetFormat = _currentRenderTarget.texture.format;
					isIntegerFormat = targetFormat === RGBAIntegerFormat ||
						targetFormat === RGIntegerFormat ||
						targetFormat === RedIntegerFormat;

				}

				// use the appropriate clear functions to clear the target if it's a signed
				// or unsigned integer target
				if ( isIntegerFormat ) {

					const targetType = _currentRenderTarget.texture.type;
					const isUnsignedType = targetType === UnsignedByteType ||
						targetType === UnsignedIntType ||
						targetType === UnsignedShortType ||
						targetType === UnsignedInt248Type ||
						targetType === UnsignedShort4444Type ||
						targetType === UnsignedShort5551Type;

					const clearColor = background.getClearColor();
					const a = background.getClearAlpha();
					const r = clearColor.r;
					const g = clearColor.g;
					const b = clearColor.b;

					if ( isUnsignedType ) {

						uintClearColor[ 0 ] = r;
						uintClearColor[ 1 ] = g;
						uintClearColor[ 2 ] = b;
						uintClearColor[ 3 ] = a;
						_gl.clearBufferuiv( _gl.COLOR, 0, uintClearColor );

					} else {

						intClearColor[ 0 ] = r;
						intClearColor[ 1 ] = g;
						intClearColor[ 2 ] = b;
						intClearColor[ 3 ] = a;
						_gl.clearBufferiv( _gl.COLOR, 0, intClearColor );

					}

				} else {

					bits |= _gl.COLOR_BUFFER_BIT;

				}

			}

			if ( depth ) {

				bits |= _gl.DEPTH_BUFFER_BIT;
				_gl.clearDepth( this.capabilities.reverseDepthBuffer ? 0 : 1 );

			}

			if ( stencil ) {

				bits |= _gl.STENCIL_BUFFER_BIT;
				this.state.buffers.stencil.setMask( 0xffffffff );

			}

			_gl.clear( bits );

		};

		this.clearColor = function () {

			this.clear( true, false, false );

		};

		this.clearDepth = function () {

			this.clear( false, true, false );

		};

		this.clearStencil = function () {

			this.clear( false, false, true );

		};

		//

		this.dispose = function () {

			canvas.removeEventListener( 'webglcontextlost', onContextLost, false );
			canvas.removeEventListener( 'webglcontextrestored', onContextRestore, false );
			canvas.removeEventListener( 'webglcontextcreationerror', onContextCreationError, false );

			renderLists.dispose();
			renderStates.dispose();
			properties.dispose();
			cubemaps.dispose();
			cubeuvmaps.dispose();
			objects.dispose();
			bindingStates.dispose();
			uniformsGroups.dispose();
			programCache.dispose();

			xr.dispose();

			xr.removeEventListener( 'sessionstart', onXRSessionStart );
			xr.removeEventListener( 'sessionend', onXRSessionEnd );

			animation.stop();

		};

		// Events

		function onContextLost( event ) {

			event.preventDefault();

			console.log( 'THREE.WebGLRenderer: Context Lost.' );

			_isContextLost = true;

		}

		function onContextRestore( /* event */ ) {

			console.log( 'THREE.WebGLRenderer: Context Restored.' );

			_isContextLost = false;

			const infoAutoReset = info.autoReset;
			const shadowMapEnabled = shadowMap.enabled;
			const shadowMapAutoUpdate = shadowMap.autoUpdate;
			const shadowMapNeedsUpdate = shadowMap.needsUpdate;
			const shadowMapType = shadowMap.type;

			initGLContext();

			info.autoReset = infoAutoReset;
			shadowMap.enabled = shadowMapEnabled;
			shadowMap.autoUpdate = shadowMapAutoUpdate;
			shadowMap.needsUpdate = shadowMapNeedsUpdate;
			shadowMap.type = shadowMapType;

		}

		function onContextCreationError( event ) {

			console.error( 'THREE.WebGLRenderer: A WebGL context could not be created. Reason: ', event.statusMessage );

		}

		function onMaterialDispose( event ) {

			const material = event.target;

			material.removeEventListener( 'dispose', onMaterialDispose );

			deallocateMaterial( material );

		}

		// Buffer deallocation

		function deallocateMaterial( material ) {

			releaseMaterialProgramReferences( material );

			properties.remove( material );

		}


		function releaseMaterialProgramReferences( material ) {

			const programs = properties.get( material ).programs;

			if ( programs !== undefined ) {

				programs.forEach( function ( program ) {

					programCache.releaseProgram( program );

				} );

				if ( material.isShaderMaterial ) {

					programCache.releaseShaderCache( material );

				}

			}

		}

		// Buffer rendering

		this.renderBufferDirect = function ( camera, scene, geometry, material, object, group ) {

			if ( scene === null ) scene = _emptyScene; // renderBufferDirect second parameter used to be fog (could be null)

			const frontFaceCW = ( object.isMesh && object.matrixWorld.determinant() < 0 );

			const program = setProgram( camera, scene, geometry, material, object );

			state.setMaterial( material, frontFaceCW );

			//

			let index = geometry.index;
			let rangeFactor = 1;

			if ( material.wireframe === true ) {

				index = geometries.getWireframeAttribute( geometry );

				if ( index === undefined ) return;

				rangeFactor = 2;

			}

			//

			const drawRange = geometry.drawRange;
			const position = geometry.attributes.position;

			let drawStart = drawRange.start * rangeFactor;
			let drawEnd = ( drawRange.start + drawRange.count ) * rangeFactor;

			if ( group !== null ) {

				drawStart = Math.max( drawStart, group.start * rangeFactor );
				drawEnd = Math.min( drawEnd, ( group.start + group.count ) * rangeFactor );

			}

			if ( index !== null ) {

				drawStart = Math.max( drawStart, 0 );
				drawEnd = Math.min( drawEnd, index.count );

			} else if ( position !== undefined && position !== null ) {

				drawStart = Math.max( drawStart, 0 );
				drawEnd = Math.min( drawEnd, position.count );

			}

			const drawCount = drawEnd - drawStart;

			if ( drawCount < 0 || drawCount === Infinity ) return;

			//

			bindingStates.setup( object, material, program, geometry, index );

			let attribute;
			let renderer = bufferRenderer;

			if ( index !== null ) {

				attribute = attributes.get( index );

				renderer = indexedBufferRenderer;
				renderer.setIndex( attribute );

			}

			//

			if ( object.isMesh ) {

				if ( material.wireframe === true ) {

					state.setLineWidth( material.wireframeLinewidth * getTargetPixelRatio() );
					renderer.setMode( _gl.LINES );

				} else {

					renderer.setMode( _gl.TRIANGLES );

				}

			} else if ( object.isLine ) {

				let lineWidth = material.linewidth;

				if ( lineWidth === undefined ) lineWidth = 1; // Not using Line*Material

				state.setLineWidth( lineWidth * getTargetPixelRatio() );

				if ( object.isLineSegments ) {

					renderer.setMode( _gl.LINES );

				} else if ( object.isLineLoop ) {

					renderer.setMode( _gl.LINE_LOOP );

				} else {

					renderer.setMode( _gl.LINE_STRIP );

				}

			} else if ( object.isPoints ) {

				renderer.setMode( _gl.POINTS );

			} else if ( object.isSprite ) {

				renderer.setMode( _gl.TRIANGLES );

			}

			if ( object.isBatchedMesh ) {

				if ( object._multiDrawInstances !== null ) {

					renderer.renderMultiDrawInstances( object._multiDrawStarts, object._multiDrawCounts, object._multiDrawCount, object._multiDrawInstances );

				} else {

					if ( ! extensions.get( 'WEBGL_multi_draw' ) ) {

						const starts = object._multiDrawStarts;
						const counts = object._multiDrawCounts;
						const drawCount = object._multiDrawCount;
						const bytesPerElement = index ? attributes.get( index ).bytesPerElement : 1;
						const uniforms = properties.get( material ).currentProgram.getUniforms();
						for ( let i = 0; i < drawCount; i ++ ) {

							uniforms.setValue( _gl, '_gl_DrawID', i );
							renderer.render( starts[ i ] / bytesPerElement, counts[ i ] );

						}

					} else {

						renderer.renderMultiDraw( object._multiDrawStarts, object._multiDrawCounts, object._multiDrawCount );

					}

				}

			} else if ( object.isInstancedMesh ) {

				renderer.renderInstances( drawStart, drawCount, object.count );

			} else if ( geometry.isInstancedBufferGeometry ) {

				const maxInstanceCount = geometry._maxInstanceCount !== undefined ? geometry._maxInstanceCount : Infinity;
				const instanceCount = Math.min( geometry.instanceCount, maxInstanceCount );

				renderer.renderInstances( drawStart, drawCount, instanceCount );

			} else {

				renderer.render( drawStart, drawCount );

			}

		};

		// Compile

		function prepareMaterial( material, scene, object ) {

			if ( material.transparent === true && material.side === DoubleSide && material.forceSinglePass === false ) {

				material.side = BackSide;
				material.needsUpdate = true;
				getProgram( material, scene, object );

				material.side = FrontSide;
				material.needsUpdate = true;
				getProgram( material, scene, object );

				material.side = DoubleSide;

			} else {

				getProgram( material, scene, object );

			}

		}

		this.compile = function ( scene, camera, targetScene = null ) {

			if ( targetScene === null ) targetScene = scene;

			currentRenderState = renderStates.get( targetScene );
			currentRenderState.init( camera );

			renderStateStack.push( currentRenderState );

			// gather lights from both the target scene and the new object that will be added to the scene.

			targetScene.traverseVisible( function ( object ) {

				if ( object.isLight && object.layers.test( camera.layers ) ) {

					currentRenderState.pushLight( object );

					if ( object.castShadow ) {

						currentRenderState.pushShadow( object );

					}

				}

			} );

			if ( scene !== targetScene ) {

				scene.traverseVisible( function ( object ) {

					if ( object.isLight && object.layers.test( camera.layers ) ) {

						currentRenderState.pushLight( object );

						if ( object.castShadow ) {

							currentRenderState.pushShadow( object );

						}

					}

				} );

			}

			currentRenderState.setupLights();

			// Only initialize materials in the new scene, not the targetScene.

			const materials = new Set();

			scene.traverse( function ( object ) {

				if ( ! ( object.isMesh || object.isPoints || object.isLine || object.isSprite ) ) {

					return;

				}

				const material = object.material;

				if ( material ) {

					if ( Array.isArray( material ) ) {

						for ( let i = 0; i < material.length; i ++ ) {

							const material2 = material[ i ];

							prepareMaterial( material2, targetScene, object );
							materials.add( material2 );

						}

					} else {

						prepareMaterial( material, targetScene, object );
						materials.add( material );

					}

				}

			} );

			renderStateStack.pop();
			currentRenderState = null;

			return materials;

		};

		// compileAsync

		this.compileAsync = function ( scene, camera, targetScene = null ) {

			const materials = this.compile( scene, camera, targetScene );

			// Wait for all the materials in the new object to indicate that they're
			// ready to be used before resolving the promise.

			return new Promise( ( resolve ) => {

				function checkMaterialsReady() {

					materials.forEach( function ( material ) {

						const materialProperties = properties.get( material );
						const program = materialProperties.currentProgram;

						if ( program.isReady() ) {

							// remove any programs that report they're ready to use from the list
							materials.delete( material );

						}

					} );

					// once the list of compiling materials is empty, call the callback

					if ( materials.size === 0 ) {

						resolve( scene );
						return;

					}

					// if some materials are still not ready, wait a bit and check again

					setTimeout( checkMaterialsReady, 10 );

				}

				if ( extensions.get( 'KHR_parallel_shader_compile' ) !== null ) {

					// If we can check the compilation status of the materials without
					// blocking then do so right away.

					checkMaterialsReady();

				} else {

					// Otherwise start by waiting a bit to give the materials we just
					// initialized a chance to finish.

					setTimeout( checkMaterialsReady, 10 );

				}

			} );

		};

		// Animation Loop

		let onAnimationFrameCallback = null;

		function onAnimationFrame( time ) {

			if ( onAnimationFrameCallback ) onAnimationFrameCallback( time );

		}

		function onXRSessionStart() {

			animation.stop();

		}

		function onXRSessionEnd() {

			animation.start();

		}

		const animation = new WebGLAnimation();
		animation.setAnimationLoop( onAnimationFrame );

		if ( typeof self !== 'undefined' ) animation.setContext( self );

		this.setAnimationLoop = function ( callback ) {

			onAnimationFrameCallback = callback;
			xr.setAnimationLoop( callback );

			( callback === null ) ? animation.stop() : animation.start();

		};

		xr.addEventListener( 'sessionstart', onXRSessionStart );
		xr.addEventListener( 'sessionend', onXRSessionEnd );

		// Rendering

		this.render = function ( scene, camera ) {

			if ( camera !== undefined && camera.isCamera !== true ) {

				console.error( 'THREE.WebGLRenderer.render: camera is not an instance of THREE.Camera.' );
				return;

			}

			if ( _isContextLost === true ) return;

			// update scene graph

			if ( scene.matrixWorldAutoUpdate === true ) scene.updateMatrixWorld();

			// update camera matrices and frustum

			if ( camera.parent === null && camera.matrixWorldAutoUpdate === true ) camera.updateMatrixWorld();

			if ( xr.enabled === true && xr.isPresenting === true ) {

				if ( xr.cameraAutoUpdate === true ) xr.updateCamera( camera );

				camera = xr.getCamera(); // use XR camera for rendering

			}

			//
			if ( scene.isScene === true ) scene.onBeforeRender( _this, scene, camera, _currentRenderTarget );

			currentRenderState = renderStates.get( scene, renderStateStack.length );
			currentRenderState.init( camera );

			renderStateStack.push( currentRenderState );

			_projScreenMatrix.multiplyMatrices( camera.projectionMatrix, camera.matrixWorldInverse );
			_frustum.setFromProjectionMatrix( _projScreenMatrix );

			_localClippingEnabled = this.localClippingEnabled;
			_clippingEnabled = clipping.init( this.clippingPlanes, _localClippingEnabled );

			currentRenderList = renderLists.get( scene, renderListStack.length );
			currentRenderList.init();

			renderListStack.push( currentRenderList );

			if ( xr.enabled === true && xr.isPresenting === true ) {

				const depthSensingMesh = _this.xr.getDepthSensingMesh();

				if ( depthSensingMesh !== null ) {

					projectObject( depthSensingMesh, camera, - Infinity, _this.sortObjects );

				}

			}

			projectObject( scene, camera, 0, _this.sortObjects );

			currentRenderList.finish();

			if ( _this.sortObjects === true ) {

				currentRenderList.sort( _opaqueSort, _transparentSort );

			}

			_renderBackground = xr.enabled === false || xr.isPresenting === false || xr.hasDepthSensing() === false;
			if ( _renderBackground ) {

				background.addToRenderList( currentRenderList, scene );

			}

			//

			this.info.render.frame ++;

			if ( _clippingEnabled === true ) clipping.beginShadows();

			const shadowsArray = currentRenderState.state.shadowsArray;

			shadowMap.render( shadowsArray, scene, camera );

			if ( _clippingEnabled === true ) clipping.endShadows();

			//

			if ( this.info.autoReset === true ) this.info.reset();

			// render scene

			const opaqueObjects = currentRenderList.opaque;
			const transmissiveObjects = currentRenderList.transmissive;

			currentRenderState.setupLights();

			if ( camera.isArrayCamera ) {

				const cameras = camera.cameras;

				if ( transmissiveObjects.length > 0 ) {

					for ( let i = 0, l = cameras.length; i < l; i ++ ) {

						const camera2 = cameras[ i ];

						renderTransmissionPass( opaqueObjects, transmissiveObjects, scene, camera2 );

					}

				}

				if ( _renderBackground ) background.render( scene );

				for ( let i = 0, l = cameras.length; i < l; i ++ ) {

					const camera2 = cameras[ i ];

					renderScene( currentRenderList, scene, camera2, camera2.viewport );

				}

			} else {

				if ( transmissiveObjects.length > 0 ) renderTransmissionPass( opaqueObjects, transmissiveObjects, scene, camera );

				if ( _renderBackground ) background.render( scene );

				renderScene( currentRenderList, scene, camera );

			}

			//

			if ( _currentRenderTarget !== null ) {

				// resolve multisample renderbuffers to a single-sample texture if necessary

				textures.updateMultisampleRenderTarget( _currentRenderTarget );

				// Generate mipmap if we're using any kind of mipmap filtering

				textures.updateRenderTargetMipmap( _currentRenderTarget );

			}

			//

			if ( scene.isScene === true ) scene.onAfterRender( _this, scene, camera );

			// _gl.finish();

			bindingStates.resetDefaultState();
			_currentMaterialId = - 1;
			_currentCamera = null;

			renderStateStack.pop();

			if ( renderStateStack.length > 0 ) {

				currentRenderState = renderStateStack[ renderStateStack.length - 1 ];

				if ( _clippingEnabled === true ) clipping.setGlobalState( _this.clippingPlanes, currentRenderState.state.camera );

			} else {

				currentRenderState = null;

			}

			renderListStack.pop();

			if ( renderListStack.length > 0 ) {

				currentRenderList = renderListStack[ renderListStack.length - 1 ];

			} else {

				currentRenderList = null;

			}

		};

		function projectObject( object, camera, groupOrder, sortObjects ) {

			if ( object.visible === false ) return;

			const visible = object.layers.test( camera.layers );

			if ( visible ) {

				if ( object.isGroup ) {

					groupOrder = object.renderOrder;

				} else if ( object.isLOD ) {

					if ( object.autoUpdate === true ) object.update( camera );

				} else if ( object.isLight ) {

					currentRenderState.pushLight( object );

					if ( object.castShadow ) {

						currentRenderState.pushShadow( object );

					}

				} else if ( object.isSprite ) {

					if ( ! object.frustumCulled || _frustum.intersectsSprite( object ) ) {

						if ( sortObjects ) {

							_vector4.setFromMatrixPosition( object.matrixWorld )
								.applyMatrix4( _projScreenMatrix );

						}

						const geometry = objects.update( object );
						const material = object.material;

						if ( material.visible ) {

							currentRenderList.push( object, geometry, material, groupOrder, _vector4.z, null );

						}

					}

				} else if ( object.isMesh || object.isLine || object.isPoints ) {

					if ( ! object.frustumCulled || _frustum.intersectsObject( object ) ) {

						const geometry = objects.update( object );
						const material = object.material;

						if ( sortObjects ) {

							if ( object.boundingSphere !== undefined ) {

								if ( object.boundingSphere === null ) object.computeBoundingSphere();
								_vector4.copy( object.boundingSphere.center );

							} else {

								if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();
								_vector4.copy( geometry.boundingSphere.center );

							}

							_vector4
								.applyMatrix4( object.matrixWorld )
								.applyMatrix4( _projScreenMatrix );

						}

						if ( Array.isArray( material ) ) {

							const groups = geometry.groups;

							for ( let i = 0, l = groups.length; i < l; i ++ ) {

								const group = groups[ i ];
								const groupMaterial = material[ group.materialIndex ];

								if ( groupMaterial && groupMaterial.visible ) {

									currentRenderList.push( object, geometry, groupMaterial, groupOrder, _vector4.z, group );

								}

							}

						} else if ( material.visible ) {

							currentRenderList.push( object, geometry, material, groupOrder, _vector4.z, null );

						}

					}

				}

			}

			const children = object.children;

			for ( let i = 0, l = children.length; i < l; i ++ ) {

				projectObject( children[ i ], camera, groupOrder, sortObjects );

			}

		}

		function renderScene( currentRenderList, scene, camera, viewport ) {

			const opaqueObjects = currentRenderList.opaque;
			const transmissiveObjects = currentRenderList.transmissive;
			const transparentObjects = currentRenderList.transparent;

			currentRenderState.setupLightsView( camera );

			if ( _clippingEnabled === true ) clipping.setGlobalState( _this.clippingPlanes, camera );

			if ( viewport ) state.viewport( _currentViewport.copy( viewport ) );

			if ( opaqueObjects.length > 0 ) renderObjects( opaqueObjects, scene, camera );
			if ( transmissiveObjects.length > 0 ) renderObjects( transmissiveObjects, scene, camera );
			if ( transparentObjects.length > 0 ) renderObjects( transparentObjects, scene, camera );

			// Ensure depth buffer writing is enabled so it can be cleared on next render

			state.buffers.depth.setTest( true );
			state.buffers.depth.setMask( true );
			state.buffers.color.setMask( true );

			state.setPolygonOffset( false );

		}

		function renderTransmissionPass( opaqueObjects, transmissiveObjects, scene, camera ) {

			const overrideMaterial = scene.isScene === true ? scene.overrideMaterial : null;

			if ( overrideMaterial !== null ) {

				return;

			}

			if ( currentRenderState.state.transmissionRenderTarget[ camera.id ] === undefined ) {

				currentRenderState.state.transmissionRenderTarget[ camera.id ] = new WebGLRenderTarget( 1, 1, {
					generateMipmaps: true,
					type: ( extensions.has( 'EXT_color_buffer_half_float' ) || extensions.has( 'EXT_color_buffer_float' ) ) ? HalfFloatType : UnsignedByteType,
					minFilter: LinearMipmapLinearFilter,
					samples: 4,
					stencilBuffer: stencil,
					resolveDepthBuffer: false,
					resolveStencilBuffer: false,
					colorSpace: ColorManagement.workingColorSpace,
				} );

				// debug

				/*
				const geometry = new PlaneGeometry();
				const material = new MeshBasicMaterial( { map: _transmissionRenderTarget.texture } );

				const mesh = new Mesh( geometry, material );
				scene.add( mesh );
				*/

			}

			const transmissionRenderTarget = currentRenderState.state.transmissionRenderTarget[ camera.id ];

			const activeViewport = camera.viewport || _currentViewport;
			transmissionRenderTarget.setSize( activeViewport.z, activeViewport.w );

			//

			const currentRenderTarget = _this.getRenderTarget();
			_this.setRenderTarget( transmissionRenderTarget );

			_this.getClearColor( _currentClearColor );
			_currentClearAlpha = _this.getClearAlpha();
			if ( _currentClearAlpha < 1 ) _this.setClearColor( 0xffffff, 0.5 );

			_this.clear();

			if ( _renderBackground ) background.render( scene );

			// Turn off the features which can affect the frag color for opaque objects pass.
			// Otherwise they are applied twice in opaque objects pass and transmission objects pass.
			const currentToneMapping = _this.toneMapping;
			_this.toneMapping = NoToneMapping;

			// Remove viewport from camera to avoid nested render calls resetting viewport to it (e.g Reflector).
			// Transmission render pass requires viewport to match the transmissionRenderTarget.
			const currentCameraViewport = camera.viewport;
			if ( camera.viewport !== undefined ) camera.viewport = undefined;

			currentRenderState.setupLightsView( camera );

			if ( _clippingEnabled === true ) clipping.setGlobalState( _this.clippingPlanes, camera );

			renderObjects( opaqueObjects, scene, camera );

			textures.updateMultisampleRenderTarget( transmissionRenderTarget );
			textures.updateRenderTargetMipmap( transmissionRenderTarget );

			if ( extensions.has( 'WEBGL_multisampled_render_to_texture' ) === false ) { // see #28131

				let renderTargetNeedsUpdate = false;

				for ( let i = 0, l = transmissiveObjects.length; i < l; i ++ ) {

					const renderItem = transmissiveObjects[ i ];

					const object = renderItem.object;
					const geometry = renderItem.geometry;
					const material = renderItem.material;
					const group = renderItem.group;

					if ( material.side === DoubleSide && object.layers.test( camera.layers ) ) {

						const currentSide = material.side;

						material.side = BackSide;
						material.needsUpdate = true;

						renderObject( object, scene, camera, geometry, material, group );

						material.side = currentSide;
						material.needsUpdate = true;

						renderTargetNeedsUpdate = true;

					}

				}

				if ( renderTargetNeedsUpdate === true ) {

					textures.updateMultisampleRenderTarget( transmissionRenderTarget );
					textures.updateRenderTargetMipmap( transmissionRenderTarget );

				}

			}

			_this.setRenderTarget( currentRenderTarget );

			_this.setClearColor( _currentClearColor, _currentClearAlpha );

			if ( currentCameraViewport !== undefined ) camera.viewport = currentCameraViewport;

			_this.toneMapping = currentToneMapping;

		}

		function renderObjects( renderList, scene, camera ) {

			const overrideMaterial = scene.isScene === true ? scene.overrideMaterial : null;

			for ( let i = 0, l = renderList.length; i < l; i ++ ) {

				const renderItem = renderList[ i ];

				const object = renderItem.object;
				const geometry = renderItem.geometry;
				const material = overrideMaterial === null ? renderItem.material : overrideMaterial;
				const group = renderItem.group;

				if ( object.layers.test( camera.layers ) ) {

					renderObject( object, scene, camera, geometry, material, group );

				}

			}

		}

		function renderObject( object, scene, camera, geometry, material, group ) {

			object.onBeforeRender( _this, scene, camera, geometry, material, group );

			object.modelViewMatrix.multiplyMatrices( camera.matrixWorldInverse, object.matrixWorld );
			object.normalMatrix.getNormalMatrix( object.modelViewMatrix );

			material.onBeforeRender( _this, scene, camera, geometry, object, group );

			if ( material.transparent === true && material.side === DoubleSide && material.forceSinglePass === false ) {

				material.side = BackSide;
				material.needsUpdate = true;
				_this.renderBufferDirect( camera, scene, geometry, material, object, group );

				material.side = FrontSide;
				material.needsUpdate = true;
				_this.renderBufferDirect( camera, scene, geometry, material, object, group );

				material.side = DoubleSide;

			} else {

				_this.renderBufferDirect( camera, scene, geometry, material, object, group );

			}

			object.onAfterRender( _this, scene, camera, geometry, material, group );

		}

		function getProgram( material, scene, object ) {

			if ( scene.isScene !== true ) scene = _emptyScene; // scene could be a Mesh, Line, Points, ...

			const materialProperties = properties.get( material );

			const lights = currentRenderState.state.lights;
			const shadowsArray = currentRenderState.state.shadowsArray;

			const lightsStateVersion = lights.state.version;

			const parameters = programCache.getParameters( material, lights.state, shadowsArray, scene, object );
			const programCacheKey = programCache.getProgramCacheKey( parameters );

			let programs = materialProperties.programs;

			// always update environment and fog - changing these trigger an getProgram call, but it's possible that the program doesn't change

			materialProperties.environment = material.isMeshStandardMaterial ? scene.environment : null;
			materialProperties.fog = scene.fog;
			materialProperties.envMap = ( material.isMeshStandardMaterial ? cubeuvmaps : cubemaps ).get( material.envMap || materialProperties.environment );
			materialProperties.envMapRotation = ( materialProperties.environment !== null && material.envMap === null ) ? scene.environmentRotation : material.envMapRotation;

			if ( programs === undefined ) {

				// new material

				material.addEventListener( 'dispose', onMaterialDispose );

				programs = new Map();
				materialProperties.programs = programs;

			}

			let program = programs.get( programCacheKey );

			if ( program !== undefined ) {

				// early out if program and light state is identical

				if ( materialProperties.currentProgram === program && materialProperties.lightsStateVersion === lightsStateVersion ) {

					updateCommonMaterialProperties( material, parameters );

					return program;

				}

			} else {

				parameters.uniforms = programCache.getUniforms( material );

				material.onBeforeCompile( parameters, _this );

				program = programCache.acquireProgram( parameters, programCacheKey );
				programs.set( programCacheKey, program );

				materialProperties.uniforms = parameters.uniforms;

			}

			const uniforms = materialProperties.uniforms;

			if ( ( ! material.isShaderMaterial && ! material.isRawShaderMaterial ) || material.clipping === true ) {

				uniforms.clippingPlanes = clipping.uniform;

			}

			updateCommonMaterialProperties( material, parameters );

			// store the light setup it was created for

			materialProperties.needsLights = materialNeedsLights( material );
			materialProperties.lightsStateVersion = lightsStateVersion;

			if ( materialProperties.needsLights ) {

				// wire up the material to this renderer's lighting state

				uniforms.ambientLightColor.value = lights.state.ambient;
				uniforms.lightProbe.value = lights.state.probe;
				uniforms.directionalLights.value = lights.state.directional;
				uniforms.directionalLightShadows.value = lights.state.directionalShadow;
				uniforms.spotLights.value = lights.state.spot;
				uniforms.spotLightShadows.value = lights.state.spotShadow;
				uniforms.rectAreaLights.value = lights.state.rectArea;
				uniforms.ltc_1.value = lights.state.rectAreaLTC1;
				uniforms.ltc_2.value = lights.state.rectAreaLTC2;
				uniforms.pointLights.value = lights.state.point;
				uniforms.pointLightShadows.value = lights.state.pointShadow;
				uniforms.hemisphereLights.value = lights.state.hemi;

				uniforms.directionalShadowMap.value = lights.state.directionalShadowMap;
				uniforms.directionalShadowMatrix.value = lights.state.directionalShadowMatrix;
				uniforms.spotShadowMap.value = lights.state.spotShadowMap;
				uniforms.spotLightMatrix.value = lights.state.spotLightMatrix;
				uniforms.spotLightMap.value = lights.state.spotLightMap;
				uniforms.pointShadowMap.value = lights.state.pointShadowMap;
				uniforms.pointShadowMatrix.value = lights.state.pointShadowMatrix;
				// TODO (abelnation): add area lights shadow info to uniforms

			}

			materialProperties.currentProgram = program;
			materialProperties.uniformsList = null;

			return program;

		}

		function getUniformList( materialProperties ) {

			if ( materialProperties.uniformsList === null ) {

				const progUniforms = materialProperties.currentProgram.getUniforms();
				materialProperties.uniformsList = WebGLUniforms.seqWithValue( progUniforms.seq, materialProperties.uniforms );

			}

			return materialProperties.uniformsList;

		}

		function updateCommonMaterialProperties( material, parameters ) {

			const materialProperties = properties.get( material );

			materialProperties.outputColorSpace = parameters.outputColorSpace;
			materialProperties.batching = parameters.batching;
			materialProperties.batchingColor = parameters.batchingColor;
			materialProperties.instancing = parameters.instancing;
			materialProperties.instancingColor = parameters.instancingColor;
			materialProperties.instancingMorph = parameters.instancingMorph;
			materialProperties.skinning = parameters.skinning;
			materialProperties.morphTargets = parameters.morphTargets;
			materialProperties.morphNormals = parameters.morphNormals;
			materialProperties.morphColors = parameters.morphColors;
			materialProperties.morphTargetsCount = parameters.morphTargetsCount;
			materialProperties.numClippingPlanes = parameters.numClippingPlanes;
			materialProperties.numIntersection = parameters.numClipIntersection;
			materialProperties.vertexAlphas = parameters.vertexAlphas;
			materialProperties.vertexTangents = parameters.vertexTangents;
			materialProperties.toneMapping = parameters.toneMapping;

		}

		function setProgram( camera, scene, geometry, material, object ) {

			if ( scene.isScene !== true ) scene = _emptyScene; // scene could be a Mesh, Line, Points, ...

			textures.resetTextureUnits();

			const fog = scene.fog;
			const environment = material.isMeshStandardMaterial ? scene.environment : null;
			const colorSpace = ( _currentRenderTarget === null ) ? _this.outputColorSpace : ( _currentRenderTarget.isXRRenderTarget === true ? _currentRenderTarget.texture.colorSpace : LinearSRGBColorSpace );
			const envMap = ( material.isMeshStandardMaterial ? cubeuvmaps : cubemaps ).get( material.envMap || environment );
			const vertexAlphas = material.vertexColors === true && !! geometry.attributes.color && geometry.attributes.color.itemSize === 4;
			const vertexTangents = !! geometry.attributes.tangent && ( !! material.normalMap || material.anisotropy > 0 );
			const morphTargets = !! geometry.morphAttributes.position;
			const morphNormals = !! geometry.morphAttributes.normal;
			const morphColors = !! geometry.morphAttributes.color;

			let toneMapping = NoToneMapping;

			if ( material.toneMapped ) {

				if ( _currentRenderTarget === null || _currentRenderTarget.isXRRenderTarget === true ) {

					toneMapping = _this.toneMapping;

				}

			}

			const morphAttribute = geometry.morphAttributes.position || geometry.morphAttributes.normal || geometry.morphAttributes.color;
			const morphTargetsCount = ( morphAttribute !== undefined ) ? morphAttribute.length : 0;

			const materialProperties = properties.get( material );
			const lights = currentRenderState.state.lights;

			if ( _clippingEnabled === true ) {

				if ( _localClippingEnabled === true || camera !== _currentCamera ) {

					const useCache =
						camera === _currentCamera &&
						material.id === _currentMaterialId;

					// we might want to call this function with some ClippingGroup
					// object instead of the material, once it becomes feasible
					// (#8465, #8379)
					clipping.setState( material, camera, useCache );

				}

			}

			//

			let needsProgramChange = false;

			if ( material.version === materialProperties.__version ) {

				if ( materialProperties.needsLights && ( materialProperties.lightsStateVersion !== lights.state.version ) ) {

					needsProgramChange = true;

				} else if ( materialProperties.outputColorSpace !== colorSpace ) {

					needsProgramChange = true;

				} else if ( object.isBatchedMesh && materialProperties.batching === false ) {

					needsProgramChange = true;

				} else if ( ! object.isBatchedMesh && materialProperties.batching === true ) {

					needsProgramChange = true;

				} else if ( object.isBatchedMesh && materialProperties.batchingColor === true && object.colorTexture === null ) {

					needsProgramChange = true;

				} else if ( object.isBatchedMesh && materialProperties.batchingColor === false && object.colorTexture !== null ) {

					needsProgramChange = true;

				} else if ( object.isInstancedMesh && materialProperties.instancing === false ) {

					needsProgramChange = true;

				} else if ( ! object.isInstancedMesh && materialProperties.instancing === true ) {

					needsProgramChange = true;

				} else if ( object.isSkinnedMesh && materialProperties.skinning === false ) {

					needsProgramChange = true;

				} else if ( ! object.isSkinnedMesh && materialProperties.skinning === true ) {

					needsProgramChange = true;

				} else if ( object.isInstancedMesh && materialProperties.instancingColor === true && object.instanceColor === null ) {

					needsProgramChange = true;

				} else if ( object.isInstancedMesh && materialProperties.instancingColor === false && object.instanceColor !== null ) {

					needsProgramChange = true;

				} else if ( object.isInstancedMesh && materialProperties.instancingMorph === true && object.morphTexture === null ) {

					needsProgramChange = true;

				} else if ( object.isInstancedMesh && materialProperties.instancingMorph === false && object.morphTexture !== null ) {

					needsProgramChange = true;

				} else if ( materialProperties.envMap !== envMap ) {

					needsProgramChange = true;

				} else if ( material.fog === true && materialProperties.fog !== fog ) {

					needsProgramChange = true;

				} else if ( materialProperties.numClippingPlanes !== undefined &&
					( materialProperties.numClippingPlanes !== clipping.numPlanes ||
					materialProperties.numIntersection !== clipping.numIntersection ) ) {

					needsProgramChange = true;

				} else if ( materialProperties.vertexAlphas !== vertexAlphas ) {

					needsProgramChange = true;

				} else if ( materialProperties.vertexTangents !== vertexTangents ) {

					needsProgramChange = true;

				} else if ( materialProperties.morphTargets !== morphTargets ) {

					needsProgramChange = true;

				} else if ( materialProperties.morphNormals !== morphNormals ) {

					needsProgramChange = true;

				} else if ( materialProperties.morphColors !== morphColors ) {

					needsProgramChange = true;

				} else if ( materialProperties.toneMapping !== toneMapping ) {

					needsProgramChange = true;

				} else if ( materialProperties.morphTargetsCount !== morphTargetsCount ) {

					needsProgramChange = true;

				}

			} else {

				needsProgramChange = true;
				materialProperties.__version = material.version;

			}

			//

			let program = materialProperties.currentProgram;

			if ( needsProgramChange === true ) {

				program = getProgram( material, scene, object );

			}

			let refreshProgram = false;
			let refreshMaterial = false;
			let refreshLights = false;

			const p_uniforms = program.getUniforms(),
				m_uniforms = materialProperties.uniforms;

			if ( state.useProgram( program.program ) ) {

				refreshProgram = true;
				refreshMaterial = true;
				refreshLights = true;

			}

			if ( material.id !== _currentMaterialId ) {

				_currentMaterialId = material.id;

				refreshMaterial = true;

			}

			if ( refreshProgram || _currentCamera !== camera ) {

				// common camera uniforms

				if ( capabilities.reverseDepthBuffer ) {

					_currentProjectionMatrix.copy( camera.projectionMatrix );

					toNormalizedProjectionMatrix( _currentProjectionMatrix );
					toReversedProjectionMatrix( _currentProjectionMatrix );

					p_uniforms.setValue( _gl, 'projectionMatrix', _currentProjectionMatrix );

				} else {

					p_uniforms.setValue( _gl, 'projectionMatrix', camera.projectionMatrix );

				}

				p_uniforms.setValue( _gl, 'viewMatrix', camera.matrixWorldInverse );

				const uCamPos = p_uniforms.map.cameraPosition;

				if ( uCamPos !== undefined ) {

					uCamPos.setValue( _gl, _vector3.setFromMatrixPosition( camera.matrixWorld ) );

				}

				if ( capabilities.logarithmicDepthBuffer ) {

					p_uniforms.setValue( _gl, 'logDepthBufFC',
						2.0 / ( Math.log( camera.far + 1.0 ) / Math.LN2 ) );

				}

				// consider moving isOrthographic to UniformLib and WebGLMaterials, see https://github.com/mrdoob/three.js/pull/26467#issuecomment-1645185067

				if ( material.isMeshPhongMaterial ||
					material.isMeshToonMaterial ||
					material.isMeshLambertMaterial ||
					material.isMeshBasicMaterial ||
					material.isMeshStandardMaterial ||
					material.isShaderMaterial ) {

					p_uniforms.setValue( _gl, 'isOrthographic', camera.isOrthographicCamera === true );

				}

				if ( _currentCamera !== camera ) {

					_currentCamera = camera;

					// lighting uniforms depend on the camera so enforce an update
					// now, in case this material supports lights - or later, when
					// the next material that does gets activated:

					refreshMaterial = true;		// set to true on material change
					refreshLights = true;		// remains set until update done

				}

			}

			// skinning and morph target uniforms must be set even if material didn't change
			// auto-setting of texture unit for bone and morph texture must go before other textures
			// otherwise textures used for skinning and morphing can take over texture units reserved for other material textures

			if ( object.isSkinnedMesh ) {

				p_uniforms.setOptional( _gl, object, 'bindMatrix' );
				p_uniforms.setOptional( _gl, object, 'bindMatrixInverse' );

				const skeleton = object.skeleton;

				if ( skeleton ) {

					if ( skeleton.boneTexture === null ) skeleton.computeBoneTexture();

					p_uniforms.setValue( _gl, 'boneTexture', skeleton.boneTexture, textures );

				}

			}

			if ( object.isBatchedMesh ) {

				p_uniforms.setOptional( _gl, object, 'batchingTexture' );
				p_uniforms.setValue( _gl, 'batchingTexture', object._matricesTexture, textures );

				p_uniforms.setOptional( _gl, object, 'batchingIdTexture' );
				p_uniforms.setValue( _gl, 'batchingIdTexture', object._indirectTexture, textures );

				p_uniforms.setOptional( _gl, object, 'batchingColorTexture' );
				if ( object._colorsTexture !== null ) {

					p_uniforms.setValue( _gl, 'batchingColorTexture', object._colorsTexture, textures );

				}

			}

			const morphAttributes = geometry.morphAttributes;

			if ( morphAttributes.position !== undefined || morphAttributes.normal !== undefined || ( morphAttributes.color !== undefined ) ) {

				morphtargets.update( object, geometry, program );

			}

			if ( refreshMaterial || materialProperties.receiveShadow !== object.receiveShadow ) {

				materialProperties.receiveShadow = object.receiveShadow;
				p_uniforms.setValue( _gl, 'receiveShadow', object.receiveShadow );

			}

			// https://github.com/mrdoob/three.js/pull/24467#issuecomment-1209031512

			if ( material.isMeshGouraudMaterial && material.envMap !== null ) {

				m_uniforms.envMap.value = envMap;

				m_uniforms.flipEnvMap.value = ( envMap.isCubeTexture && envMap.isRenderTargetTexture === false ) ? - 1 : 1;

			}

			if ( material.isMeshStandardMaterial && material.envMap === null && scene.environment !== null ) {

				m_uniforms.envMapIntensity.value = scene.environmentIntensity;

			}

			if ( refreshMaterial ) {

				p_uniforms.setValue( _gl, 'toneMappingExposure', _this.toneMappingExposure );

				if ( materialProperties.needsLights ) {

					// the current material requires lighting info

					// note: all lighting uniforms are always set correctly
					// they simply reference the renderer's state for their
					// values
					//
					// use the current material's .needsUpdate flags to set
					// the GL state when required

					markUniformsLightsNeedsUpdate( m_uniforms, refreshLights );

				}

				// refresh uniforms common to several materials

				if ( fog && material.fog === true ) {

					materials.refreshFogUniforms( m_uniforms, fog );

				}

				materials.refreshMaterialUniforms( m_uniforms, material, _pixelRatio, _height, currentRenderState.state.transmissionRenderTarget[ camera.id ] );

				WebGLUniforms.upload( _gl, getUniformList( materialProperties ), m_uniforms, textures );

			}

			if ( material.isShaderMaterial && material.uniformsNeedUpdate === true ) {

				WebGLUniforms.upload( _gl, getUniformList( materialProperties ), m_uniforms, textures );
				material.uniformsNeedUpdate = false;

			}

			if ( material.isSpriteMaterial ) {

				p_uniforms.setValue( _gl, 'center', object.center );

			}

			// common matrices

			p_uniforms.setValue( _gl, 'modelViewMatrix', object.modelViewMatrix );
			p_uniforms.setValue( _gl, 'normalMatrix', object.normalMatrix );
			p_uniforms.setValue( _gl, 'modelMatrix', object.matrixWorld );

			// UBOs

			if ( material.isShaderMaterial || material.isRawShaderMaterial ) {

				const groups = material.uniformsGroups;

				for ( let i = 0, l = groups.length; i < l; i ++ ) {

					const group = groups[ i ];

					uniformsGroups.update( group, program );
					uniformsGroups.bind( group, program );

				}

			}

			return program;

		}

		// If uniforms are marked as clean, they don't need to be loaded to the GPU.

		function markUniformsLightsNeedsUpdate( uniforms, value ) {

			uniforms.ambientLightColor.needsUpdate = value;
			uniforms.lightProbe.needsUpdate = value;

			uniforms.directionalLights.needsUpdate = value;
			uniforms.directionalLightShadows.needsUpdate = value;
			uniforms.pointLights.needsUpdate = value;
			uniforms.pointLightShadows.needsUpdate = value;
			uniforms.spotLights.needsUpdate = value;
			uniforms.spotLightShadows.needsUpdate = value;
			uniforms.rectAreaLights.needsUpdate = value;
			uniforms.hemisphereLights.needsUpdate = value;

		}

		function materialNeedsLights( material ) {

			return material.isMeshLambertMaterial || material.isMeshToonMaterial || material.isMeshPhongMaterial ||
				material.isMeshStandardMaterial || material.isShadowMaterial ||
				( material.isShaderMaterial && material.lights === true );

		}

		this.getActiveCubeFace = function () {

			return _currentActiveCubeFace;

		};

		this.getActiveMipmapLevel = function () {

			return _currentActiveMipmapLevel;

		};

		this.getRenderTarget = function () {

			return _currentRenderTarget;

		};

		this.setRenderTargetTextures = function ( renderTarget, colorTexture, depthTexture ) {

			properties.get( renderTarget.texture ).__webglTexture = colorTexture;
			properties.get( renderTarget.depthTexture ).__webglTexture = depthTexture;

			const renderTargetProperties = properties.get( renderTarget );
			renderTargetProperties.__hasExternalTextures = true;

			renderTargetProperties.__autoAllocateDepthBuffer = depthTexture === undefined;

			if ( ! renderTargetProperties.__autoAllocateDepthBuffer ) {

				// The multisample_render_to_texture extension doesn't work properly if there
				// are midframe flushes and an external depth buffer. Disable use of the extension.
				if ( extensions.has( 'WEBGL_multisampled_render_to_texture' ) === true ) {

					console.warn( 'THREE.WebGLRenderer: Render-to-texture extension was disabled because an external texture was provided' );
					renderTargetProperties.__useRenderToTexture = false;

				}

			}

		};

		this.setRenderTargetFramebuffer = function ( renderTarget, defaultFramebuffer ) {

			const renderTargetProperties = properties.get( renderTarget );
			renderTargetProperties.__webglFramebuffer = defaultFramebuffer;
			renderTargetProperties.__useDefaultFramebuffer = defaultFramebuffer === undefined;

		};

		this.setRenderTarget = function ( renderTarget, activeCubeFace = 0, activeMipmapLevel = 0 ) {

			_currentRenderTarget = renderTarget;
			_currentActiveCubeFace = activeCubeFace;
			_currentActiveMipmapLevel = activeMipmapLevel;

			let useDefaultFramebuffer = true;
			let framebuffer = null;
			let isCube = false;
			let isRenderTarget3D = false;

			if ( renderTarget ) {

				const renderTargetProperties = properties.get( renderTarget );

				if ( renderTargetProperties.__useDefaultFramebuffer !== undefined ) {

					// We need to make sure to rebind the framebuffer.
					state.bindFramebuffer( _gl.FRAMEBUFFER, null );
					useDefaultFramebuffer = false;

				} else if ( renderTargetProperties.__webglFramebuffer === undefined ) {

					textures.setupRenderTarget( renderTarget );

				} else if ( renderTargetProperties.__hasExternalTextures ) {

					// Color and depth texture must be rebound in order for the swapchain to update.
					textures.rebindTextures( renderTarget, properties.get( renderTarget.texture ).__webglTexture, properties.get( renderTarget.depthTexture ).__webglTexture );

				} else if ( renderTarget.depthBuffer ) {

					// check if the depth texture is already bound to the frame buffer and that it's been initialized
					const depthTexture = renderTarget.depthTexture;
					if ( renderTargetProperties.__boundDepthTexture !== depthTexture ) {

						// check if the depth texture is compatible
						if (
							depthTexture !== null &&
							properties.has( depthTexture ) &&
							( renderTarget.width !== depthTexture.image.width || renderTarget.height !== depthTexture.image.height )
						) {

							throw new Error( 'WebGLRenderTarget: Attached DepthTexture is initialized to the incorrect size.' );

						}

						// Swap the depth buffer to the currently attached one
						textures.setupDepthRenderbuffer( renderTarget );

					}

				}

				const texture = renderTarget.texture;

				if ( texture.isData3DTexture || texture.isDataArrayTexture || texture.isCompressedArrayTexture ) {

					isRenderTarget3D = true;

				}

				const __webglFramebuffer = properties.get( renderTarget ).__webglFramebuffer;

				if ( renderTarget.isWebGLCubeRenderTarget ) {

					if ( Array.isArray( __webglFramebuffer[ activeCubeFace ] ) ) {

						framebuffer = __webglFramebuffer[ activeCubeFace ][ activeMipmapLevel ];

					} else {

						framebuffer = __webglFramebuffer[ activeCubeFace ];

					}

					isCube = true;

				} else if ( ( renderTarget.samples > 0 ) && textures.useMultisampledRTT( renderTarget ) === false ) {

					framebuffer = properties.get( renderTarget ).__webglMultisampledFramebuffer;

				} else {

					if ( Array.isArray( __webglFramebuffer ) ) {

						framebuffer = __webglFramebuffer[ activeMipmapLevel ];

					} else {

						framebuffer = __webglFramebuffer;

					}

				}

				_currentViewport.copy( renderTarget.viewport );
				_currentScissor.copy( renderTarget.scissor );
				_currentScissorTest = renderTarget.scissorTest;

			} else {

				_currentViewport.copy( _viewport ).multiplyScalar( _pixelRatio ).floor();
				_currentScissor.copy( _scissor ).multiplyScalar( _pixelRatio ).floor();
				_currentScissorTest = _scissorTest;

			}

			const framebufferBound = state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

			if ( framebufferBound && useDefaultFramebuffer ) {

				state.drawBuffers( renderTarget, framebuffer );

			}

			state.viewport( _currentViewport );
			state.scissor( _currentScissor );
			state.setScissorTest( _currentScissorTest );

			if ( isCube ) {

				const textureProperties = properties.get( renderTarget.texture );
				_gl.framebufferTexture2D( _gl.FRAMEBUFFER, _gl.COLOR_ATTACHMENT0, _gl.TEXTURE_CUBE_MAP_POSITIVE_X + activeCubeFace, textureProperties.__webglTexture, activeMipmapLevel );

			} else if ( isRenderTarget3D ) {

				const textureProperties = properties.get( renderTarget.texture );
				const layer = activeCubeFace || 0;
				_gl.framebufferTextureLayer( _gl.FRAMEBUFFER, _gl.COLOR_ATTACHMENT0, textureProperties.__webglTexture, activeMipmapLevel || 0, layer );

			}

			_currentMaterialId = - 1; // reset current material to ensure correct uniform bindings

		};

		this.readRenderTargetPixels = function ( renderTarget, x, y, width, height, buffer, activeCubeFaceIndex ) {

			if ( ! ( renderTarget && renderTarget.isWebGLRenderTarget ) ) {

				console.error( 'THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.' );
				return;

			}

			let framebuffer = properties.get( renderTarget ).__webglFramebuffer;

			if ( renderTarget.isWebGLCubeRenderTarget && activeCubeFaceIndex !== undefined ) {

				framebuffer = framebuffer[ activeCubeFaceIndex ];

			}

			if ( framebuffer ) {

				state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

				try {

					const texture = renderTarget.texture;
					const textureFormat = texture.format;
					const textureType = texture.type;

					if ( ! capabilities.textureFormatReadable( textureFormat ) ) {

						console.error( 'THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in RGBA or implementation defined format.' );
						return;

					}

					if ( ! capabilities.textureTypeReadable( textureType ) ) {

						console.error( 'THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not in UnsignedByteType or implementation defined type.' );
						return;

					}

					// the following if statement ensures valid read requests (no out-of-bounds pixels, see #8604)

					if ( ( x >= 0 && x <= ( renderTarget.width - width ) ) && ( y >= 0 && y <= ( renderTarget.height - height ) ) ) {

						_gl.readPixels( x, y, width, height, utils.convert( textureFormat ), utils.convert( textureType ), buffer );

					}

				} finally {

					// restore framebuffer of current render target if necessary

					const framebuffer = ( _currentRenderTarget !== null ) ? properties.get( _currentRenderTarget ).__webglFramebuffer : null;
					state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

				}

			}

		};

		this.readRenderTargetPixelsAsync = async function ( renderTarget, x, y, width, height, buffer, activeCubeFaceIndex ) {

			if ( ! ( renderTarget && renderTarget.isWebGLRenderTarget ) ) {

				throw new Error( 'THREE.WebGLRenderer.readRenderTargetPixels: renderTarget is not THREE.WebGLRenderTarget.' );

			}

			let framebuffer = properties.get( renderTarget ).__webglFramebuffer;
			if ( renderTarget.isWebGLCubeRenderTarget && activeCubeFaceIndex !== undefined ) {

				framebuffer = framebuffer[ activeCubeFaceIndex ];

			}

			if ( framebuffer ) {

				const texture = renderTarget.texture;
				const textureFormat = texture.format;
				const textureType = texture.type;

				if ( ! capabilities.textureFormatReadable( textureFormat ) ) {

					throw new Error( 'THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in RGBA or implementation defined format.' );

				}

				if ( ! capabilities.textureTypeReadable( textureType ) ) {

					throw new Error( 'THREE.WebGLRenderer.readRenderTargetPixelsAsync: renderTarget is not in UnsignedByteType or implementation defined type.' );

				}

				// the following if statement ensures valid read requests (no out-of-bounds pixels, see #8604)
				if ( ( x >= 0 && x <= ( renderTarget.width - width ) ) && ( y >= 0 && y <= ( renderTarget.height - height ) ) ) {

					// set the active frame buffer to the one we want to read
					state.bindFramebuffer( _gl.FRAMEBUFFER, framebuffer );

					const glBuffer = _gl.createBuffer();
					_gl.bindBuffer( _gl.PIXEL_PACK_BUFFER, glBuffer );
					_gl.bufferData( _gl.PIXEL_PACK_BUFFER, buffer.byteLength, _gl.STREAM_READ );
					_gl.readPixels( x, y, width, height, utils.convert( textureFormat ), utils.convert( textureType ), 0 );

					// reset the frame buffer to the currently set buffer before waiting
					const currFramebuffer = _currentRenderTarget !== null ? properties.get( _currentRenderTarget ).__webglFramebuffer : null;
					state.bindFramebuffer( _gl.FRAMEBUFFER, currFramebuffer );

					// check if the commands have finished every 8 ms
					const sync = _gl.fenceSync( _gl.SYNC_GPU_COMMANDS_COMPLETE, 0 );

					_gl.flush();

					await probeAsync( _gl, sync, 4 );

					// read the data and delete the buffer
					_gl.bindBuffer( _gl.PIXEL_PACK_BUFFER, glBuffer );
					_gl.getBufferSubData( _gl.PIXEL_PACK_BUFFER, 0, buffer );
					_gl.deleteBuffer( glBuffer );
					_gl.deleteSync( sync );

					return buffer;

				} else {

					throw new Error( 'THREE.WebGLRenderer.readRenderTargetPixelsAsync: requested read bounds are out of range.' );

				}

			}

		};

		this.copyFramebufferToTexture = function ( texture, position = null, level = 0 ) {

			// support previous signature with position first
			if ( texture.isTexture !== true ) {

				// @deprecated, r165
				warnOnce( 'WebGLRenderer: copyFramebufferToTexture function signature has changed.' );

				position = arguments[ 0 ] || null;
				texture = arguments[ 1 ];

			}

			const levelScale = Math.pow( 2, - level );
			const width = Math.floor( texture.image.width * levelScale );
			const height = Math.floor( texture.image.height * levelScale );

			const x = position !== null ? position.x : 0;
			const y = position !== null ? position.y : 0;

			textures.setTexture2D( texture, 0 );

			_gl.copyTexSubImage2D( _gl.TEXTURE_2D, level, 0, 0, x, y, width, height );

			state.unbindTexture();

		};

		this.copyTextureToTexture = function ( srcTexture, dstTexture, srcRegion = null, dstPosition = null, level = 0 ) {

			// support previous signature with dstPosition first
			if ( srcTexture.isTexture !== true ) {

				// @deprecated, r165
				warnOnce( 'WebGLRenderer: copyTextureToTexture function signature has changed.' );

				dstPosition = arguments[ 0 ] || null;
				srcTexture = arguments[ 1 ];
				dstTexture = arguments[ 2 ];
				level = arguments[ 3 ] || 0;
				srcRegion = null;

			}

			let width, height, minX, minY;
			let dstX, dstY;
			if ( srcRegion !== null ) {

				width = srcRegion.max.x - srcRegion.min.x;
				height = srcRegion.max.y - srcRegion.min.y;
				minX = srcRegion.min.x;
				minY = srcRegion.min.y;

			} else {

				width = srcTexture.image.width;
				height = srcTexture.image.height;
				minX = 0;
				minY = 0;

			}

			if ( dstPosition !== null ) {

				dstX = dstPosition.x;
				dstY = dstPosition.y;

			} else {

				dstX = 0;
				dstY = 0;

			}

			const glFormat = utils.convert( dstTexture.format );
			const glType = utils.convert( dstTexture.type );

			textures.setTexture2D( dstTexture, 0 );

			// As another texture upload may have changed pixelStorei
			// parameters, make sure they are correct for the dstTexture
			_gl.pixelStorei( _gl.UNPACK_FLIP_Y_WEBGL, dstTexture.flipY );
			_gl.pixelStorei( _gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, dstTexture.premultiplyAlpha );
			_gl.pixelStorei( _gl.UNPACK_ALIGNMENT, dstTexture.unpackAlignment );

			const currentUnpackRowLen = _gl.getParameter( _gl.UNPACK_ROW_LENGTH );
			const currentUnpackImageHeight = _gl.getParameter( _gl.UNPACK_IMAGE_HEIGHT );
			const currentUnpackSkipPixels = _gl.getParameter( _gl.UNPACK_SKIP_PIXELS );
			const currentUnpackSkipRows = _gl.getParameter( _gl.UNPACK_SKIP_ROWS );
			const currentUnpackSkipImages = _gl.getParameter( _gl.UNPACK_SKIP_IMAGES );

			const image = srcTexture.isCompressedTexture ? srcTexture.mipmaps[ level ] : srcTexture.image;

			_gl.pixelStorei( _gl.UNPACK_ROW_LENGTH, image.width );
			_gl.pixelStorei( _gl.UNPACK_IMAGE_HEIGHT, image.height );
			_gl.pixelStorei( _gl.UNPACK_SKIP_PIXELS, minX );
			_gl.pixelStorei( _gl.UNPACK_SKIP_ROWS, minY );

			if ( srcTexture.isDataTexture ) {

				_gl.texSubImage2D( _gl.TEXTURE_2D, level, dstX, dstY, width, height, glFormat, glType, image.data );

			} else {

				if ( srcTexture.isCompressedTexture ) {

					_gl.compressedTexSubImage2D( _gl.TEXTURE_2D, level, dstX, dstY, image.width, image.height, glFormat, image.data );

				} else {

					_gl.texSubImage2D( _gl.TEXTURE_2D, level, dstX, dstY, width, height, glFormat, glType, image );

				}

			}

			_gl.pixelStorei( _gl.UNPACK_ROW_LENGTH, currentUnpackRowLen );
			_gl.pixelStorei( _gl.UNPACK_IMAGE_HEIGHT, currentUnpackImageHeight );
			_gl.pixelStorei( _gl.UNPACK_SKIP_PIXELS, currentUnpackSkipPixels );
			_gl.pixelStorei( _gl.UNPACK_SKIP_ROWS, currentUnpackSkipRows );
			_gl.pixelStorei( _gl.UNPACK_SKIP_IMAGES, currentUnpackSkipImages );

			// Generate mipmaps only when copying level 0
			if ( level === 0 && dstTexture.generateMipmaps ) _gl.generateMipmap( _gl.TEXTURE_2D );

			state.unbindTexture();

		};

		this.copyTextureToTexture3D = function ( srcTexture, dstTexture, srcRegion = null, dstPosition = null, level = 0 ) {

			// support previous signature with source box first
			if ( srcTexture.isTexture !== true ) {

				// @deprecated, r165
				warnOnce( 'WebGLRenderer: copyTextureToTexture3D function signature has changed.' );

				srcRegion = arguments[ 0 ] || null;
				dstPosition = arguments[ 1 ] || null;
				srcTexture = arguments[ 2 ];
				dstTexture = arguments[ 3 ];
				level = arguments[ 4 ] || 0;

			}

			let width, height, depth, minX, minY, minZ;
			let dstX, dstY, dstZ;
			const image = srcTexture.isCompressedTexture ? srcTexture.mipmaps[ level ] : srcTexture.image;
			if ( srcRegion !== null ) {

				width = srcRegion.max.x - srcRegion.min.x;
				height = srcRegion.max.y - srcRegion.min.y;
				depth = srcRegion.max.z - srcRegion.min.z;
				minX = srcRegion.min.x;
				minY = srcRegion.min.y;
				minZ = srcRegion.min.z;

			} else {

				width = image.width;
				height = image.height;
				depth = image.depth;
				minX = 0;
				minY = 0;
				minZ = 0;

			}

			if ( dstPosition !== null ) {

				dstX = dstPosition.x;
				dstY = dstPosition.y;
				dstZ = dstPosition.z;

			} else {

				dstX = 0;
				dstY = 0;
				dstZ = 0;

			}

			const glFormat = utils.convert( dstTexture.format );
			const glType = utils.convert( dstTexture.type );
			let glTarget;

			if ( dstTexture.isData3DTexture ) {

				textures.setTexture3D( dstTexture, 0 );
				glTarget = _gl.TEXTURE_3D;

			} else if ( dstTexture.isDataArrayTexture || dstTexture.isCompressedArrayTexture ) {

				textures.setTexture2DArray( dstTexture, 0 );
				glTarget = _gl.TEXTURE_2D_ARRAY;

			} else {

				console.warn( 'THREE.WebGLRenderer.copyTextureToTexture3D: only supports THREE.DataTexture3D and THREE.DataTexture2DArray.' );
				return;

			}

			_gl.pixelStorei( _gl.UNPACK_FLIP_Y_WEBGL, dstTexture.flipY );
			_gl.pixelStorei( _gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, dstTexture.premultiplyAlpha );
			_gl.pixelStorei( _gl.UNPACK_ALIGNMENT, dstTexture.unpackAlignment );

			const currentUnpackRowLen = _gl.getParameter( _gl.UNPACK_ROW_LENGTH );
			const currentUnpackImageHeight = _gl.getParameter( _gl.UNPACK_IMAGE_HEIGHT );
			const currentUnpackSkipPixels = _gl.getParameter( _gl.UNPACK_SKIP_PIXELS );
			const currentUnpackSkipRows = _gl.getParameter( _gl.UNPACK_SKIP_ROWS );
			const currentUnpackSkipImages = _gl.getParameter( _gl.UNPACK_SKIP_IMAGES );

			_gl.pixelStorei( _gl.UNPACK_ROW_LENGTH, image.width );
			_gl.pixelStorei( _gl.UNPACK_IMAGE_HEIGHT, image.height );
			_gl.pixelStorei( _gl.UNPACK_SKIP_PIXELS, minX );
			_gl.pixelStorei( _gl.UNPACK_SKIP_ROWS, minY );
			_gl.pixelStorei( _gl.UNPACK_SKIP_IMAGES, minZ );

			if ( srcTexture.isDataTexture || srcTexture.isData3DTexture ) {

				_gl.texSubImage3D( glTarget, level, dstX, dstY, dstZ, width, height, depth, glFormat, glType, image.data );

			} else {

				if ( dstTexture.isCompressedArrayTexture ) {

					_gl.compressedTexSubImage3D( glTarget, level, dstX, dstY, dstZ, width, height, depth, glFormat, image.data );

				} else {

					_gl.texSubImage3D( glTarget, level, dstX, dstY, dstZ, width, height, depth, glFormat, glType, image );

				}

			}

			_gl.pixelStorei( _gl.UNPACK_ROW_LENGTH, currentUnpackRowLen );
			_gl.pixelStorei( _gl.UNPACK_IMAGE_HEIGHT, currentUnpackImageHeight );
			_gl.pixelStorei( _gl.UNPACK_SKIP_PIXELS, currentUnpackSkipPixels );
			_gl.pixelStorei( _gl.UNPACK_SKIP_ROWS, currentUnpackSkipRows );
			_gl.pixelStorei( _gl.UNPACK_SKIP_IMAGES, currentUnpackSkipImages );

			// Generate mipmaps only when copying level 0
			if ( level === 0 && dstTexture.generateMipmaps ) _gl.generateMipmap( glTarget );

			state.unbindTexture();

		};

		this.initRenderTarget = function ( target ) {

			if ( properties.get( target ).__webglFramebuffer === undefined ) {

				textures.setupRenderTarget( target );

			}

		};

		this.initTexture = function ( texture ) {

			if ( texture.isCubeTexture ) {

				textures.setTextureCube( texture, 0 );

			} else if ( texture.isData3DTexture ) {

				textures.setTexture3D( texture, 0 );

			} else if ( texture.isDataArrayTexture || texture.isCompressedArrayTexture ) {

				textures.setTexture2DArray( texture, 0 );

			} else {

				textures.setTexture2D( texture, 0 );

			}

			state.unbindTexture();

		};

		this.resetState = function () {

			_currentActiveCubeFace = 0;
			_currentActiveMipmapLevel = 0;
			_currentRenderTarget = null;

			state.reset();
			bindingStates.reset();

		};

		if ( typeof __THREE_DEVTOOLS__ !== 'undefined' ) {

			__THREE_DEVTOOLS__.dispatchEvent( new CustomEvent( 'observe', { detail: this } ) );

		}

	}

	get coordinateSystem() {

		return WebGLCoordinateSystem;

	}

	get outputColorSpace() {

		return this._outputColorSpace;

	}

	set outputColorSpace( colorSpace ) {

		this._outputColorSpace = colorSpace;

		const gl = this.getContext();
		gl.drawingBufferColorSpace = colorSpace === DisplayP3ColorSpace ? 'display-p3' : 'srgb';
		gl.unpackColorSpace = ColorManagement.workingColorSpace === LinearDisplayP3ColorSpace ? 'display-p3' : 'srgb';

	}

}

class FogExp2 {

	constructor( color, density = 0.00025 ) {

		this.isFogExp2 = true;

		this.name = '';

		this.color = new Color( color );
		this.density = density;

	}

	clone() {

		return new FogExp2( this.color, this.density );

	}

	toJSON( /* meta */ ) {

		return {
			type: 'FogExp2',
			name: this.name,
			color: this.color.getHex(),
			density: this.density
		};

	}

}

class Fog {

	constructor( color, near = 1, far = 1000 ) {

		this.isFog = true;

		this.name = '';

		this.color = new Color( color );

		this.near = near;
		this.far = far;

	}

	clone() {

		return new Fog( this.color, this.near, this.far );

	}

	toJSON( /* meta */ ) {

		return {
			type: 'Fog',
			name: this.name,
			color: this.color.getHex(),
			near: this.near,
			far: this.far
		};

	}

}

class Scene extends Object3D {

	constructor() {

		super();

		this.isScene = true;

		this.type = 'Scene';

		this.background = null;
		this.environment = null;
		this.fog = null;

		this.backgroundBlurriness = 0;
		this.backgroundIntensity = 1;
		this.backgroundRotation = new Euler();

		this.environmentIntensity = 1;
		this.environmentRotation = new Euler();

		this.overrideMaterial = null;

		if ( typeof __THREE_DEVTOOLS__ !== 'undefined' ) {

			__THREE_DEVTOOLS__.dispatchEvent( new CustomEvent( 'observe', { detail: this } ) );

		}

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		if ( source.background !== null ) this.background = source.background.clone();
		if ( source.environment !== null ) this.environment = source.environment.clone();
		if ( source.fog !== null ) this.fog = source.fog.clone();

		this.backgroundBlurriness = source.backgroundBlurriness;
		this.backgroundIntensity = source.backgroundIntensity;
		this.backgroundRotation.copy( source.backgroundRotation );

		this.environmentIntensity = source.environmentIntensity;
		this.environmentRotation.copy( source.environmentRotation );

		if ( source.overrideMaterial !== null ) this.overrideMaterial = source.overrideMaterial.clone();

		this.matrixAutoUpdate = source.matrixAutoUpdate;

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		if ( this.fog !== null ) data.object.fog = this.fog.toJSON();

		if ( this.backgroundBlurriness > 0 ) data.object.backgroundBlurriness = this.backgroundBlurriness;
		if ( this.backgroundIntensity !== 1 ) data.object.backgroundIntensity = this.backgroundIntensity;
		data.object.backgroundRotation = this.backgroundRotation.toArray();

		if ( this.environmentIntensity !== 1 ) data.object.environmentIntensity = this.environmentIntensity;
		data.object.environmentRotation = this.environmentRotation.toArray();

		return data;

	}

}

class InterleavedBuffer {

	constructor( array, stride ) {

		this.isInterleavedBuffer = true;

		this.array = array;
		this.stride = stride;
		this.count = array !== undefined ? array.length / stride : 0;

		this.usage = StaticDrawUsage;
		this.updateRanges = [];

		this.version = 0;

		this.uuid = generateUUID();

	}

	onUploadCallback() {}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	setUsage( value ) {

		this.usage = value;

		return this;

	}

	addUpdateRange( start, count ) {

		this.updateRanges.push( { start, count } );

	}

	clearUpdateRanges() {

		this.updateRanges.length = 0;

	}

	copy( source ) {

		this.array = new source.array.constructor( source.array );
		this.count = source.count;
		this.stride = source.stride;
		this.usage = source.usage;

		return this;

	}

	copyAt( index1, attribute, index2 ) {

		index1 *= this.stride;
		index2 *= attribute.stride;

		for ( let i = 0, l = this.stride; i < l; i ++ ) {

			this.array[ index1 + i ] = attribute.array[ index2 + i ];

		}

		return this;

	}

	set( value, offset = 0 ) {

		this.array.set( value, offset );

		return this;

	}

	clone( data ) {

		if ( data.arrayBuffers === undefined ) {

			data.arrayBuffers = {};

		}

		if ( this.array.buffer._uuid === undefined ) {

			this.array.buffer._uuid = generateUUID();

		}

		if ( data.arrayBuffers[ this.array.buffer._uuid ] === undefined ) {

			data.arrayBuffers[ this.array.buffer._uuid ] = this.array.slice( 0 ).buffer;

		}

		const array = new this.array.constructor( data.arrayBuffers[ this.array.buffer._uuid ] );

		const ib = new this.constructor( array, this.stride );
		ib.setUsage( this.usage );

		return ib;

	}

	onUpload( callback ) {

		this.onUploadCallback = callback;

		return this;

	}

	toJSON( data ) {

		if ( data.arrayBuffers === undefined ) {

			data.arrayBuffers = {};

		}

		// generate UUID for array buffer if necessary

		if ( this.array.buffer._uuid === undefined ) {

			this.array.buffer._uuid = generateUUID();

		}

		if ( data.arrayBuffers[ this.array.buffer._uuid ] === undefined ) {

			data.arrayBuffers[ this.array.buffer._uuid ] = Array.from( new Uint32Array( this.array.buffer ) );

		}

		//

		return {
			uuid: this.uuid,
			buffer: this.array.buffer._uuid,
			type: this.array.constructor.name,
			stride: this.stride
		};

	}

}

const _vector$6 = /*@__PURE__*/ new Vector3();

class InterleavedBufferAttribute {

	constructor( interleavedBuffer, itemSize, offset, normalized = false ) {

		this.isInterleavedBufferAttribute = true;

		this.name = '';

		this.data = interleavedBuffer;
		this.itemSize = itemSize;
		this.offset = offset;

		this.normalized = normalized;

	}

	get count() {

		return this.data.count;

	}

	get array() {

		return this.data.array;

	}

	set needsUpdate( value ) {

		this.data.needsUpdate = value;

	}

	applyMatrix4( m ) {

		for ( let i = 0, l = this.data.count; i < l; i ++ ) {

			_vector$6.fromBufferAttribute( this, i );

			_vector$6.applyMatrix4( m );

			this.setXYZ( i, _vector$6.x, _vector$6.y, _vector$6.z );

		}

		return this;

	}

	applyNormalMatrix( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$6.fromBufferAttribute( this, i );

			_vector$6.applyNormalMatrix( m );

			this.setXYZ( i, _vector$6.x, _vector$6.y, _vector$6.z );

		}

		return this;

	}

	transformDirection( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$6.fromBufferAttribute( this, i );

			_vector$6.transformDirection( m );

			this.setXYZ( i, _vector$6.x, _vector$6.y, _vector$6.z );

		}

		return this;

	}

	getComponent( index, component ) {

		let value = this.array[ index * this.data.stride + this.offset + component ];

		if ( this.normalized ) value = denormalize( value, this.array );

		return value;

	}

	setComponent( index, component, value ) {

		if ( this.normalized ) value = normalize( value, this.array );

		this.data.array[ index * this.data.stride + this.offset + component ] = value;

		return this;

	}

	setX( index, x ) {

		if ( this.normalized ) x = normalize( x, this.array );

		this.data.array[ index * this.data.stride + this.offset ] = x;

		return this;

	}

	setY( index, y ) {

		if ( this.normalized ) y = normalize( y, this.array );

		this.data.array[ index * this.data.stride + this.offset + 1 ] = y;

		return this;

	}

	setZ( index, z ) {

		if ( this.normalized ) z = normalize( z, this.array );

		this.data.array[ index * this.data.stride + this.offset + 2 ] = z;

		return this;

	}

	setW( index, w ) {

		if ( this.normalized ) w = normalize( w, this.array );

		this.data.array[ index * this.data.stride + this.offset + 3 ] = w;

		return this;

	}

	getX( index ) {

		let x = this.data.array[ index * this.data.stride + this.offset ];

		if ( this.normalized ) x = denormalize( x, this.array );

		return x;

	}

	getY( index ) {

		let y = this.data.array[ index * this.data.stride + this.offset + 1 ];

		if ( this.normalized ) y = denormalize( y, this.array );

		return y;

	}

	getZ( index ) {

		let z = this.data.array[ index * this.data.stride + this.offset + 2 ];

		if ( this.normalized ) z = denormalize( z, this.array );

		return z;

	}

	getW( index ) {

		let w = this.data.array[ index * this.data.stride + this.offset + 3 ];

		if ( this.normalized ) w = denormalize( w, this.array );

		return w;

	}

	setXY( index, x, y ) {

		index = index * this.data.stride + this.offset;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );

		}

		this.data.array[ index + 0 ] = x;
		this.data.array[ index + 1 ] = y;

		return this;

	}

	setXYZ( index, x, y, z ) {

		index = index * this.data.stride + this.offset;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );

		}

		this.data.array[ index + 0 ] = x;
		this.data.array[ index + 1 ] = y;
		this.data.array[ index + 2 ] = z;

		return this;

	}

	setXYZW( index, x, y, z, w ) {

		index = index * this.data.stride + this.offset;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );
			w = normalize( w, this.array );

		}

		this.data.array[ index + 0 ] = x;
		this.data.array[ index + 1 ] = y;
		this.data.array[ index + 2 ] = z;
		this.data.array[ index + 3 ] = w;

		return this;

	}

	clone( data ) {

		if ( data === undefined ) {

			console.log( 'THREE.InterleavedBufferAttribute.clone(): Cloning an interleaved buffer attribute will de-interleave buffer data.' );

			const array = [];

			for ( let i = 0; i < this.count; i ++ ) {

				const index = i * this.data.stride + this.offset;

				for ( let j = 0; j < this.itemSize; j ++ ) {

					array.push( this.data.array[ index + j ] );

				}

			}

			return new BufferAttribute( new this.array.constructor( array ), this.itemSize, this.normalized );

		} else {

			if ( data.interleavedBuffers === undefined ) {

				data.interleavedBuffers = {};

			}

			if ( data.interleavedBuffers[ this.data.uuid ] === undefined ) {

				data.interleavedBuffers[ this.data.uuid ] = this.data.clone( data );

			}

			return new InterleavedBufferAttribute( data.interleavedBuffers[ this.data.uuid ], this.itemSize, this.offset, this.normalized );

		}

	}

	toJSON( data ) {

		if ( data === undefined ) {

			console.log( 'THREE.InterleavedBufferAttribute.toJSON(): Serializing an interleaved buffer attribute will de-interleave buffer data.' );

			const array = [];

			for ( let i = 0; i < this.count; i ++ ) {

				const index = i * this.data.stride + this.offset;

				for ( let j = 0; j < this.itemSize; j ++ ) {

					array.push( this.data.array[ index + j ] );

				}

			}

			// de-interleave data and save it as an ordinary buffer attribute for now

			return {
				itemSize: this.itemSize,
				type: this.array.constructor.name,
				array: array,
				normalized: this.normalized
			};

		} else {

			// save as true interleaved attribute

			if ( data.interleavedBuffers === undefined ) {

				data.interleavedBuffers = {};

			}

			if ( data.interleavedBuffers[ this.data.uuid ] === undefined ) {

				data.interleavedBuffers[ this.data.uuid ] = this.data.toJSON( data );

			}

			return {
				isInterleavedBufferAttribute: true,
				itemSize: this.itemSize,
				data: this.data.uuid,
				offset: this.offset,
				normalized: this.normalized
			};

		}

	}

}

class SpriteMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isSpriteMaterial = true;

		this.type = 'SpriteMaterial';

		this.color = new Color( 0xffffff );

		this.map = null;

		this.alphaMap = null;

		this.rotation = 0;

		this.sizeAttenuation = true;

		this.transparent = true;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.alphaMap = source.alphaMap;

		this.rotation = source.rotation;

		this.sizeAttenuation = source.sizeAttenuation;

		this.fog = source.fog;

		return this;

	}

}

let _geometry;

const _intersectPoint = /*@__PURE__*/ new Vector3();
const _worldScale = /*@__PURE__*/ new Vector3();
const _mvPosition = /*@__PURE__*/ new Vector3();

const _alignedPosition = /*@__PURE__*/ new Vector2();
const _rotatedPosition = /*@__PURE__*/ new Vector2();
const _viewWorldMatrix = /*@__PURE__*/ new Matrix4();

const _vA = /*@__PURE__*/ new Vector3();
const _vB = /*@__PURE__*/ new Vector3();
const _vC = /*@__PURE__*/ new Vector3();

const _uvA = /*@__PURE__*/ new Vector2();
const _uvB = /*@__PURE__*/ new Vector2();
const _uvC = /*@__PURE__*/ new Vector2();

class Sprite extends Object3D {

	constructor( material = new SpriteMaterial() ) {

		super();

		this.isSprite = true;

		this.type = 'Sprite';

		if ( _geometry === undefined ) {

			_geometry = new BufferGeometry();

			const float32Array = new Float32Array( [
				- 0.5, - 0.5, 0, 0, 0,
				0.5, - 0.5, 0, 1, 0,
				0.5, 0.5, 0, 1, 1,
				- 0.5, 0.5, 0, 0, 1
			] );

			const interleavedBuffer = new InterleavedBuffer( float32Array, 5 );

			_geometry.setIndex( [ 0, 1, 2,	0, 2, 3 ] );
			_geometry.setAttribute( 'position', new InterleavedBufferAttribute( interleavedBuffer, 3, 0, false ) );
			_geometry.setAttribute( 'uv', new InterleavedBufferAttribute( interleavedBuffer, 2, 3, false ) );

		}

		this.geometry = _geometry;
		this.material = material;

		this.center = new Vector2( 0.5, 0.5 );

	}

	raycast( raycaster, intersects ) {

		if ( raycaster.camera === null ) {

			console.error( 'THREE.Sprite: "Raycaster.camera" needs to be set in order to raycast against sprites.' );

		}

		_worldScale.setFromMatrixScale( this.matrixWorld );

		_viewWorldMatrix.copy( raycaster.camera.matrixWorld );
		this.modelViewMatrix.multiplyMatrices( raycaster.camera.matrixWorldInverse, this.matrixWorld );

		_mvPosition.setFromMatrixPosition( this.modelViewMatrix );

		if ( raycaster.camera.isPerspectiveCamera && this.material.sizeAttenuation === false ) {

			_worldScale.multiplyScalar( - _mvPosition.z );

		}

		const rotation = this.material.rotation;
		let sin, cos;

		if ( rotation !== 0 ) {

			cos = Math.cos( rotation );
			sin = Math.sin( rotation );

		}

		const center = this.center;

		transformVertex( _vA.set( - 0.5, - 0.5, 0 ), _mvPosition, center, _worldScale, sin, cos );
		transformVertex( _vB.set( 0.5, - 0.5, 0 ), _mvPosition, center, _worldScale, sin, cos );
		transformVertex( _vC.set( 0.5, 0.5, 0 ), _mvPosition, center, _worldScale, sin, cos );

		_uvA.set( 0, 0 );
		_uvB.set( 1, 0 );
		_uvC.set( 1, 1 );

		// check first triangle
		let intersect = raycaster.ray.intersectTriangle( _vA, _vB, _vC, false, _intersectPoint );

		if ( intersect === null ) {

			// check second triangle
			transformVertex( _vB.set( - 0.5, 0.5, 0 ), _mvPosition, center, _worldScale, sin, cos );
			_uvB.set( 0, 1 );

			intersect = raycaster.ray.intersectTriangle( _vA, _vC, _vB, false, _intersectPoint );
			if ( intersect === null ) {

				return;

			}

		}

		const distance = raycaster.ray.origin.distanceTo( _intersectPoint );

		if ( distance < raycaster.near || distance > raycaster.far ) return;

		intersects.push( {

			distance: distance,
			point: _intersectPoint.clone(),
			uv: Triangle.getInterpolation( _intersectPoint, _vA, _vB, _vC, _uvA, _uvB, _uvC, new Vector2() ),
			face: null,
			object: this

		} );

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		if ( source.center !== undefined ) this.center.copy( source.center );

		this.material = source.material;

		return this;

	}

}

function transformVertex( vertexPosition, mvPosition, center, scale, sin, cos ) {

	// compute position in camera space
	_alignedPosition.subVectors( vertexPosition, center ).addScalar( 0.5 ).multiply( scale );

	// to check if rotation is not zero
	if ( sin !== undefined ) {

		_rotatedPosition.x = ( cos * _alignedPosition.x ) - ( sin * _alignedPosition.y );
		_rotatedPosition.y = ( sin * _alignedPosition.x ) + ( cos * _alignedPosition.y );

	} else {

		_rotatedPosition.copy( _alignedPosition );

	}


	vertexPosition.copy( mvPosition );
	vertexPosition.x += _rotatedPosition.x;
	vertexPosition.y += _rotatedPosition.y;

	// transform to world space
	vertexPosition.applyMatrix4( _viewWorldMatrix );

}

const _v1$2 = /*@__PURE__*/ new Vector3();
const _v2$1 = /*@__PURE__*/ new Vector3();

class LOD extends Object3D {

	constructor() {

		super();

		this._currentLevel = 0;

		this.type = 'LOD';

		Object.defineProperties( this, {
			levels: {
				enumerable: true,
				value: []
			},
			isLOD: {
				value: true,
			}
		} );

		this.autoUpdate = true;

	}

	copy( source ) {

		super.copy( source, false );

		const levels = source.levels;

		for ( let i = 0, l = levels.length; i < l; i ++ ) {

			const level = levels[ i ];

			this.addLevel( level.object.clone(), level.distance, level.hysteresis );

		}

		this.autoUpdate = source.autoUpdate;

		return this;

	}

	addLevel( object, distance = 0, hysteresis = 0 ) {

		distance = Math.abs( distance );

		const levels = this.levels;

		let l;

		for ( l = 0; l < levels.length; l ++ ) {

			if ( distance < levels[ l ].distance ) {

				break;

			}

		}

		levels.splice( l, 0, { distance: distance, hysteresis: hysteresis, object: object } );

		this.add( object );

		return this;

	}

	removeLevel( distance ) {

		const levels = this.levels;

		for ( let i = 0; i < levels.length; i ++ ) {

			if ( levels[ i ].distance === distance ) {

				const removedElements = levels.splice( i, 1 );
				this.remove( removedElements[ 0 ].object );

				return true;

			}

		}

		return false;

	}

	getCurrentLevel() {

		return this._currentLevel;

	}



	getObjectForDistance( distance ) {

		const levels = this.levels;

		if ( levels.length > 0 ) {

			let i, l;

			for ( i = 1, l = levels.length; i < l; i ++ ) {

				let levelDistance = levels[ i ].distance;

				if ( levels[ i ].object.visible ) {

					levelDistance -= levelDistance * levels[ i ].hysteresis;

				}

				if ( distance < levelDistance ) {

					break;

				}

			}

			return levels[ i - 1 ].object;

		}

		return null;

	}

	raycast( raycaster, intersects ) {

		const levels = this.levels;

		if ( levels.length > 0 ) {

			_v1$2.setFromMatrixPosition( this.matrixWorld );

			const distance = raycaster.ray.origin.distanceTo( _v1$2 );

			this.getObjectForDistance( distance ).raycast( raycaster, intersects );

		}

	}

	update( camera ) {

		const levels = this.levels;

		if ( levels.length > 1 ) {

			_v1$2.setFromMatrixPosition( camera.matrixWorld );
			_v2$1.setFromMatrixPosition( this.matrixWorld );

			const distance = _v1$2.distanceTo( _v2$1 ) / camera.zoom;

			levels[ 0 ].object.visible = true;

			let i, l;

			for ( i = 1, l = levels.length; i < l; i ++ ) {

				let levelDistance = levels[ i ].distance;

				if ( levels[ i ].object.visible ) {

					levelDistance -= levelDistance * levels[ i ].hysteresis;

				}

				if ( distance >= levelDistance ) {

					levels[ i - 1 ].object.visible = false;
					levels[ i ].object.visible = true;

				} else {

					break;

				}

			}

			this._currentLevel = i - 1;

			for ( ; i < l; i ++ ) {

				levels[ i ].object.visible = false;

			}

		}

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		if ( this.autoUpdate === false ) data.object.autoUpdate = false;

		data.object.levels = [];

		const levels = this.levels;

		for ( let i = 0, l = levels.length; i < l; i ++ ) {

			const level = levels[ i ];

			data.object.levels.push( {
				object: level.object.uuid,
				distance: level.distance,
				hysteresis: level.hysteresis
			} );

		}

		return data;

	}

}

const _basePosition = /*@__PURE__*/ new Vector3();

const _skinIndex = /*@__PURE__*/ new Vector4();
const _skinWeight = /*@__PURE__*/ new Vector4();

const _vector3 = /*@__PURE__*/ new Vector3();
const _matrix4 = /*@__PURE__*/ new Matrix4();
const _vertex = /*@__PURE__*/ new Vector3();

const _sphere$4 = /*@__PURE__*/ new Sphere();
const _inverseMatrix$2 = /*@__PURE__*/ new Matrix4();
const _ray$2 = /*@__PURE__*/ new Ray();

class SkinnedMesh extends Mesh {

	constructor( geometry, material ) {

		super( geometry, material );

		this.isSkinnedMesh = true;

		this.type = 'SkinnedMesh';

		this.bindMode = AttachedBindMode;
		this.bindMatrix = new Matrix4();
		this.bindMatrixInverse = new Matrix4();

		this.boundingBox = null;
		this.boundingSphere = null;

	}

	computeBoundingBox() {

		const geometry = this.geometry;

		if ( this.boundingBox === null ) {

			this.boundingBox = new Box3();

		}

		this.boundingBox.makeEmpty();

		const positionAttribute = geometry.getAttribute( 'position' );

		for ( let i = 0; i < positionAttribute.count; i ++ ) {

			this.getVertexPosition( i, _vertex );
			this.boundingBox.expandByPoint( _vertex );

		}

	}

	computeBoundingSphere() {

		const geometry = this.geometry;

		if ( this.boundingSphere === null ) {

			this.boundingSphere = new Sphere();

		}

		this.boundingSphere.makeEmpty();

		const positionAttribute = geometry.getAttribute( 'position' );

		for ( let i = 0; i < positionAttribute.count; i ++ ) {

			this.getVertexPosition( i, _vertex );
			this.boundingSphere.expandByPoint( _vertex );

		}

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.bindMode = source.bindMode;
		this.bindMatrix.copy( source.bindMatrix );
		this.bindMatrixInverse.copy( source.bindMatrixInverse );

		this.skeleton = source.skeleton;

		if ( source.boundingBox !== null ) this.boundingBox = source.boundingBox.clone();
		if ( source.boundingSphere !== null ) this.boundingSphere = source.boundingSphere.clone();

		return this;

	}

	raycast( raycaster, intersects ) {

		const material = this.material;
		const matrixWorld = this.matrixWorld;

		if ( material === undefined ) return;

		// test with bounding sphere in world space

		if ( this.boundingSphere === null ) this.computeBoundingSphere();

		_sphere$4.copy( this.boundingSphere );
		_sphere$4.applyMatrix4( matrixWorld );

		if ( raycaster.ray.intersectsSphere( _sphere$4 ) === false ) return;

		// convert ray to local space of skinned mesh

		_inverseMatrix$2.copy( matrixWorld ).invert();
		_ray$2.copy( raycaster.ray ).applyMatrix4( _inverseMatrix$2 );

		// test with bounding box in local space

		if ( this.boundingBox !== null ) {

			if ( _ray$2.intersectsBox( this.boundingBox ) === false ) return;

		}

		// test for intersections with geometry

		this._computeIntersections( raycaster, intersects, _ray$2 );

	}

	getVertexPosition( index, target ) {

		super.getVertexPosition( index, target );

		this.applyBoneTransform( index, target );

		return target;

	}

	bind( skeleton, bindMatrix ) {

		this.skeleton = skeleton;

		if ( bindMatrix === undefined ) {

			this.updateMatrixWorld( true );

			this.skeleton.calculateInverses();

			bindMatrix = this.matrixWorld;

		}

		this.bindMatrix.copy( bindMatrix );
		this.bindMatrixInverse.copy( bindMatrix ).invert();

	}

	pose() {

		this.skeleton.pose();

	}

	normalizeSkinWeights() {

		const vector = new Vector4();

		const skinWeight = this.geometry.attributes.skinWeight;

		for ( let i = 0, l = skinWeight.count; i < l; i ++ ) {

			vector.fromBufferAttribute( skinWeight, i );

			const scale = 1.0 / vector.manhattanLength();

			if ( scale !== Infinity ) {

				vector.multiplyScalar( scale );

			} else {

				vector.set( 1, 0, 0, 0 ); // do something reasonable

			}

			skinWeight.setXYZW( i, vector.x, vector.y, vector.z, vector.w );

		}

	}

	updateMatrixWorld( force ) {

		super.updateMatrixWorld( force );

		if ( this.bindMode === AttachedBindMode ) {

			this.bindMatrixInverse.copy( this.matrixWorld ).invert();

		} else if ( this.bindMode === DetachedBindMode ) {

			this.bindMatrixInverse.copy( this.bindMatrix ).invert();

		} else {

			console.warn( 'THREE.SkinnedMesh: Unrecognized bindMode: ' + this.bindMode );

		}

	}

	applyBoneTransform( index, vector ) {

		const skeleton = this.skeleton;
		const geometry = this.geometry;

		_skinIndex.fromBufferAttribute( geometry.attributes.skinIndex, index );
		_skinWeight.fromBufferAttribute( geometry.attributes.skinWeight, index );

		_basePosition.copy( vector ).applyMatrix4( this.bindMatrix );

		vector.set( 0, 0, 0 );

		for ( let i = 0; i < 4; i ++ ) {

			const weight = _skinWeight.getComponent( i );

			if ( weight !== 0 ) {

				const boneIndex = _skinIndex.getComponent( i );

				_matrix4.multiplyMatrices( skeleton.bones[ boneIndex ].matrixWorld, skeleton.boneInverses[ boneIndex ] );

				vector.addScaledVector( _vector3.copy( _basePosition ).applyMatrix4( _matrix4 ), weight );

			}

		}

		return vector.applyMatrix4( this.bindMatrixInverse );

	}

}

class Bone extends Object3D {

	constructor() {

		super();

		this.isBone = true;

		this.type = 'Bone';

	}

}

class DataTexture extends Texture {

	constructor( data = null, width = 1, height = 1, format, type, mapping, wrapS, wrapT, magFilter = NearestFilter, minFilter = NearestFilter, anisotropy, colorSpace ) {

		super( null, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy, colorSpace );

		this.isDataTexture = true;

		this.image = { data: data, width: width, height: height };

		this.generateMipmaps = false;
		this.flipY = false;
		this.unpackAlignment = 1;

	}

}

const _offsetMatrix = /*@__PURE__*/ new Matrix4();
const _identityMatrix$1 = /*@__PURE__*/ new Matrix4();

class Skeleton {

	constructor( bones = [], boneInverses = [] ) {

		this.uuid = generateUUID();

		this.bones = bones.slice( 0 );
		this.boneInverses = boneInverses;
		this.boneMatrices = null;

		this.boneTexture = null;

		this.init();

	}

	init() {

		const bones = this.bones;
		const boneInverses = this.boneInverses;

		this.boneMatrices = new Float32Array( bones.length * 16 );

		// calculate inverse bone matrices if necessary

		if ( boneInverses.length === 0 ) {

			this.calculateInverses();

		} else {

			// handle special case

			if ( bones.length !== boneInverses.length ) {

				console.warn( 'THREE.Skeleton: Number of inverse bone matrices does not match amount of bones.' );

				this.boneInverses = [];

				for ( let i = 0, il = this.bones.length; i < il; i ++ ) {

					this.boneInverses.push( new Matrix4() );

				}

			}

		}

	}

	calculateInverses() {

		this.boneInverses.length = 0;

		for ( let i = 0, il = this.bones.length; i < il; i ++ ) {

			const inverse = new Matrix4();

			if ( this.bones[ i ] ) {

				inverse.copy( this.bones[ i ].matrixWorld ).invert();

			}

			this.boneInverses.push( inverse );

		}

	}

	pose() {

		// recover the bind-time world matrices

		for ( let i = 0, il = this.bones.length; i < il; i ++ ) {

			const bone = this.bones[ i ];

			if ( bone ) {

				bone.matrixWorld.copy( this.boneInverses[ i ] ).invert();

			}

		}

		// compute the local matrices, positions, rotations and scales

		for ( let i = 0, il = this.bones.length; i < il; i ++ ) {

			const bone = this.bones[ i ];

			if ( bone ) {

				if ( bone.parent && bone.parent.isBone ) {

					bone.matrix.copy( bone.parent.matrixWorld ).invert();
					bone.matrix.multiply( bone.matrixWorld );

				} else {

					bone.matrix.copy( bone.matrixWorld );

				}

				bone.matrix.decompose( bone.position, bone.quaternion, bone.scale );

			}

		}

	}

	update() {

		const bones = this.bones;
		const boneInverses = this.boneInverses;
		const boneMatrices = this.boneMatrices;
		const boneTexture = this.boneTexture;

		// flatten bone matrices to array

		for ( let i = 0, il = bones.length; i < il; i ++ ) {

			// compute the offset between the current and the original transform

			const matrix = bones[ i ] ? bones[ i ].matrixWorld : _identityMatrix$1;

			_offsetMatrix.multiplyMatrices( matrix, boneInverses[ i ] );
			_offsetMatrix.toArray( boneMatrices, i * 16 );

		}

		if ( boneTexture !== null ) {

			boneTexture.needsUpdate = true;

		}

	}

	clone() {

		return new Skeleton( this.bones, this.boneInverses );

	}

	computeBoneTexture() {

		// layout (1 matrix = 4 pixels)
		//      RGBA RGBA RGBA RGBA (=> column1, column2, column3, column4)
		//  with  8x8  pixel texture max   16 bones * 4 pixels =  (8 * 8)
		//       16x16 pixel texture max   64 bones * 4 pixels = (16 * 16)
		//       32x32 pixel texture max  256 bones * 4 pixels = (32 * 32)
		//       64x64 pixel texture max 1024 bones * 4 pixels = (64 * 64)

		let size = Math.sqrt( this.bones.length * 4 ); // 4 pixels needed for 1 matrix
		size = Math.ceil( size / 4 ) * 4;
		size = Math.max( size, 4 );

		const boneMatrices = new Float32Array( size * size * 4 ); // 4 floats per RGBA pixel
		boneMatrices.set( this.boneMatrices ); // copy current values

		const boneTexture = new DataTexture( boneMatrices, size, size, RGBAFormat, FloatType );
		boneTexture.needsUpdate = true;

		this.boneMatrices = boneMatrices;
		this.boneTexture = boneTexture;

		return this;

	}

	getBoneByName( name ) {

		for ( let i = 0, il = this.bones.length; i < il; i ++ ) {

			const bone = this.bones[ i ];

			if ( bone.name === name ) {

				return bone;

			}

		}

		return undefined;

	}

	dispose( ) {

		if ( this.boneTexture !== null ) {

			this.boneTexture.dispose();

			this.boneTexture = null;

		}

	}

	fromJSON( json, bones ) {

		this.uuid = json.uuid;

		for ( let i = 0, l = json.bones.length; i < l; i ++ ) {

			const uuid = json.bones[ i ];
			let bone = bones[ uuid ];

			if ( bone === undefined ) {

				console.warn( 'THREE.Skeleton: No bone found with UUID:', uuid );
				bone = new Bone();

			}

			this.bones.push( bone );
			this.boneInverses.push( new Matrix4().fromArray( json.boneInverses[ i ] ) );

		}

		this.init();

		return this;

	}

	toJSON() {

		const data = {
			metadata: {
				version: 4.6,
				type: 'Skeleton',
				generator: 'Skeleton.toJSON'
			},
			bones: [],
			boneInverses: []
		};

		data.uuid = this.uuid;

		const bones = this.bones;
		const boneInverses = this.boneInverses;

		for ( let i = 0, l = bones.length; i < l; i ++ ) {

			const bone = bones[ i ];
			data.bones.push( bone.uuid );

			const boneInverse = boneInverses[ i ];
			data.boneInverses.push( boneInverse.toArray() );

		}

		return data;

	}

}

class InstancedBufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized, meshPerAttribute = 1 ) {

		super( array, itemSize, normalized );

		this.isInstancedBufferAttribute = true;

		this.meshPerAttribute = meshPerAttribute;

	}

	copy( source ) {

		super.copy( source );

		this.meshPerAttribute = source.meshPerAttribute;

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.meshPerAttribute = this.meshPerAttribute;

		data.isInstancedBufferAttribute = true;

		return data;

	}

}

const _instanceLocalMatrix = /*@__PURE__*/ new Matrix4();
const _instanceWorldMatrix = /*@__PURE__*/ new Matrix4();

const _instanceIntersects = [];

const _box3 = /*@__PURE__*/ new Box3();
const _identity = /*@__PURE__*/ new Matrix4();
const _mesh$1 = /*@__PURE__*/ new Mesh();
const _sphere$3 = /*@__PURE__*/ new Sphere();

class InstancedMesh extends Mesh {

	constructor( geometry, material, count ) {

		super( geometry, material );

		this.isInstancedMesh = true;

		this.instanceMatrix = new InstancedBufferAttribute( new Float32Array( count * 16 ), 16 );
		this.instanceColor = null;
		this.morphTexture = null;

		this.count = count;

		this.boundingBox = null;
		this.boundingSphere = null;

		for ( let i = 0; i < count; i ++ ) {

			this.setMatrixAt( i, _identity );

		}

	}

	computeBoundingBox() {

		const geometry = this.geometry;
		const count = this.count;

		if ( this.boundingBox === null ) {

			this.boundingBox = new Box3();

		}

		if ( geometry.boundingBox === null ) {

			geometry.computeBoundingBox();

		}

		this.boundingBox.makeEmpty();

		for ( let i = 0; i < count; i ++ ) {

			this.getMatrixAt( i, _instanceLocalMatrix );

			_box3.copy( geometry.boundingBox ).applyMatrix4( _instanceLocalMatrix );

			this.boundingBox.union( _box3 );

		}

	}

	computeBoundingSphere() {

		const geometry = this.geometry;
		const count = this.count;

		if ( this.boundingSphere === null ) {

			this.boundingSphere = new Sphere();

		}

		if ( geometry.boundingSphere === null ) {

			geometry.computeBoundingSphere();

		}

		this.boundingSphere.makeEmpty();

		for ( let i = 0; i < count; i ++ ) {

			this.getMatrixAt( i, _instanceLocalMatrix );

			_sphere$3.copy( geometry.boundingSphere ).applyMatrix4( _instanceLocalMatrix );

			this.boundingSphere.union( _sphere$3 );

		}

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.instanceMatrix.copy( source.instanceMatrix );

		if ( source.morphTexture !== null ) this.morphTexture = source.morphTexture.clone();
		if ( source.instanceColor !== null ) this.instanceColor = source.instanceColor.clone();

		this.count = source.count;

		if ( source.boundingBox !== null ) this.boundingBox = source.boundingBox.clone();
		if ( source.boundingSphere !== null ) this.boundingSphere = source.boundingSphere.clone();

		return this;

	}

	getColorAt( index, color ) {

		color.fromArray( this.instanceColor.array, index * 3 );

	}

	getMatrixAt( index, matrix ) {

		matrix.fromArray( this.instanceMatrix.array, index * 16 );

	}

	getMorphAt( index, object ) {

		const objectInfluences = object.morphTargetInfluences;

		const array = this.morphTexture.source.data.data;

		const len = objectInfluences.length + 1; // All influences + the baseInfluenceSum

		const dataIndex = index * len + 1; // Skip the baseInfluenceSum at the beginning

		for ( let i = 0; i < objectInfluences.length; i ++ ) {

			objectInfluences[ i ] = array[ dataIndex + i ];

		}

	}

	raycast( raycaster, intersects ) {

		const matrixWorld = this.matrixWorld;
		const raycastTimes = this.count;

		_mesh$1.geometry = this.geometry;
		_mesh$1.material = this.material;

		if ( _mesh$1.material === undefined ) return;

		// test with bounding sphere first

		if ( this.boundingSphere === null ) this.computeBoundingSphere();

		_sphere$3.copy( this.boundingSphere );
		_sphere$3.applyMatrix4( matrixWorld );

		if ( raycaster.ray.intersectsSphere( _sphere$3 ) === false ) return;

		// now test each instance

		for ( let instanceId = 0; instanceId < raycastTimes; instanceId ++ ) {

			// calculate the world matrix for each instance

			this.getMatrixAt( instanceId, _instanceLocalMatrix );

			_instanceWorldMatrix.multiplyMatrices( matrixWorld, _instanceLocalMatrix );

			// the mesh represents this single instance

			_mesh$1.matrixWorld = _instanceWorldMatrix;

			_mesh$1.raycast( raycaster, _instanceIntersects );

			// process the result of raycast

			for ( let i = 0, l = _instanceIntersects.length; i < l; i ++ ) {

				const intersect = _instanceIntersects[ i ];
				intersect.instanceId = instanceId;
				intersect.object = this;
				intersects.push( intersect );

			}

			_instanceIntersects.length = 0;

		}

	}

	setColorAt( index, color ) {

		if ( this.instanceColor === null ) {

			this.instanceColor = new InstancedBufferAttribute( new Float32Array( this.instanceMatrix.count * 3 ).fill( 1 ), 3 );

		}

		color.toArray( this.instanceColor.array, index * 3 );

	}

	setMatrixAt( index, matrix ) {

		matrix.toArray( this.instanceMatrix.array, index * 16 );

	}

	setMorphAt( index, object ) {

		const objectInfluences = object.morphTargetInfluences;

		const len = objectInfluences.length + 1; // morphBaseInfluence + all influences

		if ( this.morphTexture === null ) {

			this.morphTexture = new DataTexture( new Float32Array( len * this.count ), len, this.count, RedFormat, FloatType );

		}

		const array = this.morphTexture.source.data.data;

		let morphInfluencesSum = 0;

		for ( let i = 0; i < objectInfluences.length; i ++ ) {

			morphInfluencesSum += objectInfluences[ i ];

		}

		const morphBaseInfluence = this.geometry.morphTargetsRelative ? 1 : 1 - morphInfluencesSum;

		const dataIndex = len * index;

		array[ dataIndex ] = morphBaseInfluence;

		array.set( objectInfluences, dataIndex + 1 );

	}

	updateMorphTargets() {

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

		if ( this.morphTexture !== null ) {

			this.morphTexture.dispose();
			this.morphTexture = null;

		}

		return this;

	}

}

function sortOpaque( a, b ) {

	return a.z - b.z;

}

function sortTransparent( a, b ) {

	return b.z - a.z;

}

class MultiDrawRenderList {

	constructor() {

		this.index = 0;
		this.pool = [];
		this.list = [];

	}

	push( drawRange, z, index ) {

		const pool = this.pool;
		const list = this.list;
		if ( this.index >= pool.length ) {

			pool.push( {

				start: - 1,
				count: - 1,
				z: - 1,
				index: - 1,

			} );

		}

		const item = pool[ this.index ];
		list.push( item );
		this.index ++;

		item.start = drawRange.start;
		item.count = drawRange.count;
		item.z = z;
		item.index = index;

	}

	reset() {

		this.list.length = 0;
		this.index = 0;

	}

}

const _matrix$1 = /*@__PURE__*/ new Matrix4();
const _invMatrixWorld = /*@__PURE__*/ new Matrix4();
const _identityMatrix = /*@__PURE__*/ new Matrix4();
const _whiteColor = /*@__PURE__*/ new Color( 1, 1, 1 );
const _projScreenMatrix$2 = /*@__PURE__*/ new Matrix4();
const _frustum = /*@__PURE__*/ new Frustum();
const _box$1 = /*@__PURE__*/ new Box3();
const _sphere$2 = /*@__PURE__*/ new Sphere();
const _vector$5 = /*@__PURE__*/ new Vector3();
const _forward = /*@__PURE__*/ new Vector3();
const _temp = /*@__PURE__*/ new Vector3();
const _renderList = /*@__PURE__*/ new MultiDrawRenderList();
const _mesh = /*@__PURE__*/ new Mesh();
const _batchIntersects = [];

// @TODO: SkinnedMesh support?
// @TODO: geometry.groups support?
// @TODO: geometry.drawRange support?
// @TODO: geometry.morphAttributes support?
// @TODO: Support uniform parameter per geometry
// @TODO: Add an "optimize" function to pack geometry and remove data gaps

// copies data from attribute "src" into "target" starting at "targetOffset"
function copyAttributeData( src, target, targetOffset = 0 ) {

	const itemSize = target.itemSize;
	if ( src.isInterleavedBufferAttribute || src.array.constructor !== target.array.constructor ) {

		// use the component getters and setters if the array data cannot
		// be copied directly
		const vertexCount = src.count;
		for ( let i = 0; i < vertexCount; i ++ ) {

			for ( let c = 0; c < itemSize; c ++ ) {

				target.setComponent( i + targetOffset, c, src.getComponent( i, c ) );

			}

		}

	} else {

		// faster copy approach using typed array set function
		target.array.set( src.array, targetOffset * itemSize );

	}

	target.needsUpdate = true;

}

class BatchedMesh extends Mesh {

	get maxInstanceCount() {

		return this._maxInstanceCount;

	}

	constructor( maxInstanceCount, maxVertexCount, maxIndexCount = maxVertexCount * 2, material ) {

		super( new BufferGeometry(), material );

		this.isBatchedMesh = true;
		this.perObjectFrustumCulled = true;
		this.sortObjects = true;
		this.boundingBox = null;
		this.boundingSphere = null;
		this.customSort = null;

		// stores visible, active, and geometry id per object
		this._drawInfo = [];

		// instance ids that have been set as inactive, and are available to be overwritten
		this._availableInstanceIds = [];

		// geometry information
		this._drawRanges = [];
		this._reservedRanges = [];
		this._bounds = [];

		this._maxInstanceCount = maxInstanceCount;
		this._maxVertexCount = maxVertexCount;
		this._maxIndexCount = maxIndexCount;

		this._geometryInitialized = false;
		this._geometryCount = 0;
		this._multiDrawCounts = new Int32Array( maxInstanceCount );
		this._multiDrawStarts = new Int32Array( maxInstanceCount );
		this._multiDrawCount = 0;
		this._multiDrawInstances = null;
		this._visibilityChanged = true;

		// Local matrix per geometry by using data texture
		this._matricesTexture = null;
		this._indirectTexture = null;
		this._colorsTexture = null;

		this._initMatricesTexture();
		this._initIndirectTexture();

	}

	_initMatricesTexture() {

		// layout (1 matrix = 4 pixels)
		//      RGBA RGBA RGBA RGBA (=> column1, column2, column3, column4)
		//  with  8x8  pixel texture max   16 matrices * 4 pixels =  (8 * 8)
		//       16x16 pixel texture max   64 matrices * 4 pixels = (16 * 16)
		//       32x32 pixel texture max  256 matrices * 4 pixels = (32 * 32)
		//       64x64 pixel texture max 1024 matrices * 4 pixels = (64 * 64)

		let size = Math.sqrt( this._maxInstanceCount * 4 ); // 4 pixels needed for 1 matrix
		size = Math.ceil( size / 4 ) * 4;
		size = Math.max( size, 4 );

		const matricesArray = new Float32Array( size * size * 4 ); // 4 floats per RGBA pixel
		const matricesTexture = new DataTexture( matricesArray, size, size, RGBAFormat, FloatType );

		this._matricesTexture = matricesTexture;

	}

	_initIndirectTexture() {

		let size = Math.sqrt( this._maxInstanceCount );
		size = Math.ceil( size );

		const indirectArray = new Uint32Array( size * size );
		const indirectTexture = new DataTexture( indirectArray, size, size, RedIntegerFormat, UnsignedIntType );

		this._indirectTexture = indirectTexture;

	}

	_initColorsTexture() {

		let size = Math.sqrt( this._maxInstanceCount );
		size = Math.ceil( size );

		// 4 floats per RGBA pixel initialized to white
		const colorsArray = new Float32Array( size * size * 4 ).fill( 1 );
		const colorsTexture = new DataTexture( colorsArray, size, size, RGBAFormat, FloatType );
		colorsTexture.colorSpace = ColorManagement.workingColorSpace;

		this._colorsTexture = colorsTexture;

	}

	_initializeGeometry( reference ) {

		const geometry = this.geometry;
		const maxVertexCount = this._maxVertexCount;
		const maxIndexCount = this._maxIndexCount;
		if ( this._geometryInitialized === false ) {

			for ( const attributeName in reference.attributes ) {

				const srcAttribute = reference.getAttribute( attributeName );
				const { array, itemSize, normalized } = srcAttribute;

				const dstArray = new array.constructor( maxVertexCount * itemSize );
				const dstAttribute = new BufferAttribute( dstArray, itemSize, normalized );

				geometry.setAttribute( attributeName, dstAttribute );

			}

			if ( reference.getIndex() !== null ) {

				// Reserve last u16 index for primitive restart.
				const indexArray = maxVertexCount > 65535
					? new Uint32Array( maxIndexCount )
					: new Uint16Array( maxIndexCount );

				geometry.setIndex( new BufferAttribute( indexArray, 1 ) );

			}

			this._geometryInitialized = true;

		}

	}

	// Make sure the geometry is compatible with the existing combined geometry attributes
	_validateGeometry( geometry ) {

		// check to ensure the geometries are using consistent attributes and indices
		const batchGeometry = this.geometry;
		if ( Boolean( geometry.getIndex() ) !== Boolean( batchGeometry.getIndex() ) ) {

			throw new Error( 'BatchedMesh: All geometries must consistently have "index".' );

		}

		for ( const attributeName in batchGeometry.attributes ) {

			if ( ! geometry.hasAttribute( attributeName ) ) {

				throw new Error( `BatchedMesh: Added geometry missing "${ attributeName }". All geometries must have consistent attributes.` );

			}

			const srcAttribute = geometry.getAttribute( attributeName );
			const dstAttribute = batchGeometry.getAttribute( attributeName );
			if ( srcAttribute.itemSize !== dstAttribute.itemSize || srcAttribute.normalized !== dstAttribute.normalized ) {

				throw new Error( 'BatchedMesh: All attributes must have a consistent itemSize and normalized value.' );

			}

		}

	}

	setCustomSort( func ) {

		this.customSort = func;
		return this;

	}

	computeBoundingBox() {

		if ( this.boundingBox === null ) {

			this.boundingBox = new Box3();

		}

		const boundingBox = this.boundingBox;
		const drawInfo = this._drawInfo;

		boundingBox.makeEmpty();
		for ( let i = 0, l = drawInfo.length; i < l; i ++ ) {

			if ( drawInfo[ i ].active === false ) continue;

			const geometryId = drawInfo[ i ].geometryIndex;
			this.getMatrixAt( i, _matrix$1 );
			this.getBoundingBoxAt( geometryId, _box$1 ).applyMatrix4( _matrix$1 );
			boundingBox.union( _box$1 );

		}

	}

	computeBoundingSphere() {

		if ( this.boundingSphere === null ) {

			this.boundingSphere = new Sphere();

		}

		const boundingSphere = this.boundingSphere;
		const drawInfo = this._drawInfo;

		boundingSphere.makeEmpty();
		for ( let i = 0, l = drawInfo.length; i < l; i ++ ) {

			if ( drawInfo[ i ].active === false ) continue;

			const geometryId = drawInfo[ i ].geometryIndex;
			this.getMatrixAt( i, _matrix$1 );
			this.getBoundingSphereAt( geometryId, _sphere$2 ).applyMatrix4( _matrix$1 );
			boundingSphere.union( _sphere$2 );

		}

	}

	addInstance( geometryId ) {

		const atCapacity = this._drawInfo.length >= this.maxInstanceCount;

		// ensure we're not over geometry
		if ( atCapacity && this._availableInstanceIds.length === 0 ) {

			throw new Error( 'BatchedMesh: Maximum item count reached.' );

		}

		const instanceDrawInfo = {
			visible: true,
			active: true,
			geometryIndex: geometryId,
		};

		let drawId = null;

		// Prioritize using previously freed instance ids
		if ( this._availableInstanceIds.length > 0 ) {

			drawId = this._availableInstanceIds.pop();
			this._drawInfo[ drawId ] = instanceDrawInfo;

		} else {

			drawId = this._drawInfo.length;
			this._drawInfo.push( instanceDrawInfo );

		}

		const matricesTexture = this._matricesTexture;
		const matricesArray = matricesTexture.image.data;
		_identityMatrix.toArray( matricesArray, drawId * 16 );
		matricesTexture.needsUpdate = true;

		const colorsTexture = this._colorsTexture;
		if ( colorsTexture ) {

			_whiteColor.toArray( colorsTexture.image.data, drawId * 4 );
			colorsTexture.needsUpdate = true;

		}

		return drawId;

	}

	addGeometry( geometry, vertexCount = - 1, indexCount = - 1 ) {

		this._initializeGeometry( geometry );

		this._validateGeometry( geometry );

		// ensure we're not over geometry
		if ( this._drawInfo.length >= this._maxInstanceCount ) {

			throw new Error( 'BatchedMesh: Maximum item count reached.' );

		}

		// get the necessary range fo the geometry
		const reservedRange = {
			vertexStart: - 1,
			vertexCount: - 1,
			indexStart: - 1,
			indexCount: - 1,
		};

		let lastRange = null;
		const reservedRanges = this._reservedRanges;
		const drawRanges = this._drawRanges;
		const bounds = this._bounds;
		if ( this._geometryCount !== 0 ) {

			lastRange = reservedRanges[ reservedRanges.length - 1 ];

		}

		if ( vertexCount === - 1 ) {

			reservedRange.vertexCount = geometry.getAttribute( 'position' ).count;

		} else {

			reservedRange.vertexCount = vertexCount;

		}

		if ( lastRange === null ) {

			reservedRange.vertexStart = 0;

		} else {

			reservedRange.vertexStart = lastRange.vertexStart + lastRange.vertexCount;

		}

		const index = geometry.getIndex();
		const hasIndex = index !== null;
		if ( hasIndex ) {

			if ( indexCount	=== - 1 ) {

				reservedRange.indexCount = index.count;

			} else {

				reservedRange.indexCount = indexCount;

			}

			if ( lastRange === null ) {

				reservedRange.indexStart = 0;

			} else {

				reservedRange.indexStart = lastRange.indexStart + lastRange.indexCount;

			}

		}

		if (
			reservedRange.indexStart !== - 1 &&
			reservedRange.indexStart + reservedRange.indexCount > this._maxIndexCount ||
			reservedRange.vertexStart + reservedRange.vertexCount > this._maxVertexCount
		) {

			throw new Error( 'BatchedMesh: Reserved space request exceeds the maximum buffer size.' );

		}

		// update id
		const geometryId = this._geometryCount;
		this._geometryCount ++;

		// add the reserved range and draw range objects
		reservedRanges.push( reservedRange );
		drawRanges.push( {
			start: hasIndex ? reservedRange.indexStart : reservedRange.vertexStart,
			count: - 1
		} );
		bounds.push( {
			boxInitialized: false,
			box: new Box3(),

			sphereInitialized: false,
			sphere: new Sphere()
		} );

		// update the geometry
		this.setGeometryAt( geometryId, geometry );

		return geometryId;

	}

	setGeometryAt( geometryId, geometry ) {

		if ( geometryId >= this._geometryCount ) {

			throw new Error( 'BatchedMesh: Maximum geometry count reached.' );

		}

		this._validateGeometry( geometry );

		const batchGeometry = this.geometry;
		const hasIndex = batchGeometry.getIndex() !== null;
		const dstIndex = batchGeometry.getIndex();
		const srcIndex = geometry.getIndex();
		const reservedRange = this._reservedRanges[ geometryId ];
		if (
			hasIndex &&
			srcIndex.count > reservedRange.indexCount ||
			geometry.attributes.position.count > reservedRange.vertexCount
		) {

			throw new Error( 'BatchedMesh: Reserved space not large enough for provided geometry.' );

		}

		// copy geometry over
		const vertexStart = reservedRange.vertexStart;
		const vertexCount = reservedRange.vertexCount;
		for ( const attributeName in batchGeometry.attributes ) {

			// copy attribute data
			const srcAttribute = geometry.getAttribute( attributeName );
			const dstAttribute = batchGeometry.getAttribute( attributeName );
			copyAttributeData( srcAttribute, dstAttribute, vertexStart );

			// fill the rest in with zeroes
			const itemSize = srcAttribute.itemSize;
			for ( let i = srcAttribute.count, l = vertexCount; i < l; i ++ ) {

				const index = vertexStart + i;
				for ( let c = 0; c < itemSize; c ++ ) {

					dstAttribute.setComponent( index, c, 0 );

				}

			}

			dstAttribute.needsUpdate = true;
			dstAttribute.addUpdateRange( vertexStart * itemSize, vertexCount * itemSize );

		}

		// copy index
		if ( hasIndex ) {

			const indexStart = reservedRange.indexStart;

			// copy index data over
			for ( let i = 0; i < srcIndex.count; i ++ ) {

				dstIndex.setX( indexStart + i, vertexStart + srcIndex.getX( i ) );

			}

			// fill the rest in with zeroes
			for ( let i = srcIndex.count, l = reservedRange.indexCount; i < l; i ++ ) {

				dstIndex.setX( indexStart + i, vertexStart );

			}

			dstIndex.needsUpdate = true;
			dstIndex.addUpdateRange( indexStart, reservedRange.indexCount );

		}

		// store the bounding boxes
		const bound = this._bounds[ geometryId ];
		if ( geometry.boundingBox !== null ) {

			bound.box.copy( geometry.boundingBox );
			bound.boxInitialized = true;

		} else {

			bound.boxInitialized = false;

		}

		if ( geometry.boundingSphere !== null ) {

			bound.sphere.copy( geometry.boundingSphere );
			bound.sphereInitialized = true;

		} else {

			bound.sphereInitialized = false;

		}

		// set drawRange count
		const drawRange = this._drawRanges[ geometryId ];
		const posAttr = geometry.getAttribute( 'position' );
		drawRange.count = hasIndex ? srcIndex.count : posAttr.count;
		this._visibilityChanged = true;

		return geometryId;

	}

	/*
	deleteGeometry( geometryId ) {

		// TODO: delete geometry and associated instances

	}
	*/

	deleteInstance( instanceId ) {

		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return this;

		}

		drawInfo[ instanceId ].active = false;
		this._availableInstanceIds.push( instanceId );
		this._visibilityChanged = true;

		return this;

	}

	// get bounding box and compute it if it doesn't exist
	getBoundingBoxAt( geometryId, target ) {

		if ( geometryId >= this._geometryCount ) {

			return null;

		}

		// compute bounding box
		const bound = this._bounds[ geometryId ];
		const box = bound.box;
		const geometry = this.geometry;
		if ( bound.boxInitialized === false ) {

			box.makeEmpty();

			const index = geometry.index;
			const position = geometry.attributes.position;
			const drawRange = this._drawRanges[ geometryId ];
			for ( let i = drawRange.start, l = drawRange.start + drawRange.count; i < l; i ++ ) {

				let iv = i;
				if ( index ) {

					iv = index.getX( iv );

				}

				box.expandByPoint( _vector$5.fromBufferAttribute( position, iv ) );

			}

			bound.boxInitialized = true;

		}

		target.copy( box );
		return target;

	}

	// get bounding sphere and compute it if it doesn't exist
	getBoundingSphereAt( geometryId, target ) {

		if ( geometryId >= this._geometryCount ) {

			return null;

		}

		// compute bounding sphere
		const bound = this._bounds[ geometryId ];
		const sphere = bound.sphere;
		const geometry = this.geometry;
		if ( bound.sphereInitialized === false ) {

			sphere.makeEmpty();

			this.getBoundingBoxAt( geometryId, _box$1 );
			_box$1.getCenter( sphere.center );

			const index = geometry.index;
			const position = geometry.attributes.position;
			const drawRange = this._drawRanges[ geometryId ];

			let maxRadiusSq = 0;
			for ( let i = drawRange.start, l = drawRange.start + drawRange.count; i < l; i ++ ) {

				let iv = i;
				if ( index ) {

					iv = index.getX( iv );

				}

				_vector$5.fromBufferAttribute( position, iv );
				maxRadiusSq = Math.max( maxRadiusSq, sphere.center.distanceToSquared( _vector$5 ) );

			}

			sphere.radius = Math.sqrt( maxRadiusSq );
			bound.sphereInitialized = true;

		}

		target.copy( sphere );
		return target;

	}

	setMatrixAt( instanceId, matrix ) {

		// @TODO: Map geometryId to index of the arrays because
		//        optimize() can make geometryId mismatch the index

		const drawInfo = this._drawInfo;
		const matricesTexture = this._matricesTexture;
		const matricesArray = this._matricesTexture.image.data;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return this;

		}

		matrix.toArray( matricesArray, instanceId * 16 );
		matricesTexture.needsUpdate = true;

		return this;

	}

	getMatrixAt( instanceId, matrix ) {

		const drawInfo = this._drawInfo;
		const matricesArray = this._matricesTexture.image.data;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return null;

		}

		return matrix.fromArray( matricesArray, instanceId * 16 );

	}

	setColorAt( instanceId, color ) {

		if ( this._colorsTexture === null ) {

			this._initColorsTexture();

		}

		// @TODO: Map id to index of the arrays because
		//        optimize() can make id mismatch the index

		const colorsTexture = this._colorsTexture;
		const colorsArray = this._colorsTexture.image.data;
		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return this;

		}

		color.toArray( colorsArray, instanceId * 4 );
		colorsTexture.needsUpdate = true;

		return this;

	}

	getColorAt( instanceId, color ) {

		const colorsArray = this._colorsTexture.image.data;
		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return null;

		}

		return color.fromArray( colorsArray, instanceId * 4 );

	}

	setVisibleAt( instanceId, value ) {

		// if the geometry is out of range, not active, or visibility state
		// does not change then return early
		const drawInfo = this._drawInfo;
		if (
			instanceId >= drawInfo.length ||
			drawInfo[ instanceId ].active === false ||
			drawInfo[ instanceId ].visible === value
		) {

			return this;

		}

		drawInfo[ instanceId ].visible = value;
		this._visibilityChanged = true;

		return this;

	}

	getVisibleAt( instanceId ) {

		// return early if the geometry is out of range or not active
		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return false;

		}

		return drawInfo[ instanceId ].visible;

	}

	setGeometryIdAt( instanceId, geometryId ) {

		// return early if the geometry is out of range or not active
		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return null;

		}

		// check if the provided geometryId is within the valid range
		if ( geometryId < 0 || geometryId >= this._geometryCount ) {

			return null;

		}

		drawInfo[ instanceId ].geometryIndex = geometryId;

		return this;

	}

	getGeometryIdAt( instanceId ) {

		const drawInfo = this._drawInfo;
		if ( instanceId >= drawInfo.length || drawInfo[ instanceId ].active === false ) {

			return - 1;

		}

		return drawInfo[ instanceId ].geometryIndex;

	}

	getGeometryRangeAt( geometryId, target = {} ) {

		if ( geometryId < 0 || geometryId >= this._geometryCount ) {

			return null;

		}

		const drawRange = this._drawRanges[ geometryId ];

		target.start = drawRange.start;
		target.count = drawRange.count;

		return target;

	}

	raycast( raycaster, intersects ) {

		const drawInfo = this._drawInfo;
		const drawRanges = this._drawRanges;
		const matrixWorld = this.matrixWorld;
		const batchGeometry = this.geometry;

		// iterate over each geometry
		_mesh.material = this.material;
		_mesh.geometry.index = batchGeometry.index;
		_mesh.geometry.attributes = batchGeometry.attributes;
		if ( _mesh.geometry.boundingBox === null ) {

			_mesh.geometry.boundingBox = new Box3();

		}

		if ( _mesh.geometry.boundingSphere === null ) {

			_mesh.geometry.boundingSphere = new Sphere();

		}

		for ( let i = 0, l = drawInfo.length; i < l; i ++ ) {

			if ( ! drawInfo[ i ].visible || ! drawInfo[ i ].active ) {

				continue;

			}

			const geometryId = drawInfo[ i ].geometryIndex;
			const drawRange = drawRanges[ geometryId ];
			_mesh.geometry.setDrawRange( drawRange.start, drawRange.count );

			// ge the intersects
			this.getMatrixAt( i, _mesh.matrixWorld ).premultiply( matrixWorld );
			this.getBoundingBoxAt( geometryId, _mesh.geometry.boundingBox );
			this.getBoundingSphereAt( geometryId, _mesh.geometry.boundingSphere );
			_mesh.raycast( raycaster, _batchIntersects );

			// add batch id to the intersects
			for ( let j = 0, l = _batchIntersects.length; j < l; j ++ ) {

				const intersect = _batchIntersects[ j ];
				intersect.object = this;
				intersect.batchId = i;
				intersects.push( intersect );

			}

			_batchIntersects.length = 0;

		}

		_mesh.material = null;
		_mesh.geometry.index = null;
		_mesh.geometry.attributes = {};
		_mesh.geometry.setDrawRange( 0, Infinity );

	}

	copy( source ) {

		super.copy( source );

		this.geometry = source.geometry.clone();
		this.perObjectFrustumCulled = source.perObjectFrustumCulled;
		this.sortObjects = source.sortObjects;
		this.boundingBox = source.boundingBox !== null ? source.boundingBox.clone() : null;
		this.boundingSphere = source.boundingSphere !== null ? source.boundingSphere.clone() : null;

		this._drawRanges = source._drawRanges.map( range => ( { ...range } ) );
		this._reservedRanges = source._reservedRanges.map( range => ( { ...range } ) );

		this._drawInfo = source._drawInfo.map( inf => ( { ...inf } ) );
		this._bounds = source._bounds.map( bound => ( {
			boxInitialized: bound.boxInitialized,
			box: bound.box.clone(),

			sphereInitialized: bound.sphereInitialized,
			sphere: bound.sphere.clone()
		} ) );

		this._maxInstanceCount = source._maxInstanceCount;
		this._maxVertexCount = source._maxVertexCount;
		this._maxIndexCount = source._maxIndexCount;

		this._geometryInitialized = source._geometryInitialized;
		this._geometryCount = source._geometryCount;
		this._multiDrawCounts = source._multiDrawCounts.slice();
		this._multiDrawStarts = source._multiDrawStarts.slice();

		this._matricesTexture = source._matricesTexture.clone();
		this._matricesTexture.image.data = this._matricesTexture.image.data.slice();

		if ( this._colorsTexture !== null ) {

			this._colorsTexture = source._colorsTexture.clone();
			this._colorsTexture.image.data = this._colorsTexture.image.data.slice();

		}

		return this;

	}

	dispose() {

		// Assuming the geometry is not shared with other meshes
		this.geometry.dispose();

		this._matricesTexture.dispose();
		this._matricesTexture = null;

		this._indirectTexture.dispose();
		this._indirectTexture = null;

		if ( this._colorsTexture !== null ) {

			this._colorsTexture.dispose();
			this._colorsTexture = null;

		}

		return this;

	}

	onBeforeRender( renderer, scene, camera, geometry, material/*, _group*/ ) {

		// if visibility has not changed and frustum culling and object sorting is not required
		// then skip iterating over all items
		if ( ! this._visibilityChanged && ! this.perObjectFrustumCulled && ! this.sortObjects ) {

			return;

		}

		// the indexed version of the multi draw function requires specifying the start
		// offset in bytes.
		const index = geometry.getIndex();
		const bytesPerElement = index === null ? 1 : index.array.BYTES_PER_ELEMENT;

		const drawInfo = this._drawInfo;
		const multiDrawStarts = this._multiDrawStarts;
		const multiDrawCounts = this._multiDrawCounts;
		const drawRanges = this._drawRanges;
		const perObjectFrustumCulled = this.perObjectFrustumCulled;
		const indirectTexture = this._indirectTexture;
		const indirectArray = indirectTexture.image.data;

		// prepare the frustum in the local frame
		if ( perObjectFrustumCulled ) {

			_projScreenMatrix$2
				.multiplyMatrices( camera.projectionMatrix, camera.matrixWorldInverse )
				.multiply( this.matrixWorld );
			_frustum.setFromProjectionMatrix(
				_projScreenMatrix$2,
				renderer.coordinateSystem
			);

		}

		let count = 0;
		if ( this.sortObjects ) {

			// get the camera position in the local frame
			_invMatrixWorld.copy( this.matrixWorld ).invert();
			_vector$5.setFromMatrixPosition( camera.matrixWorld ).applyMatrix4( _invMatrixWorld );
			_forward.set( 0, 0, - 1 ).transformDirection( camera.matrixWorld ).transformDirection( _invMatrixWorld );

			for ( let i = 0, l = drawInfo.length; i < l; i ++ ) {

				if ( drawInfo[ i ].visible && drawInfo[ i ].active ) {

					const geometryId = drawInfo[ i ].geometryIndex;

					// get the bounds in world space
					this.getMatrixAt( i, _matrix$1 );
					this.getBoundingSphereAt( geometryId, _sphere$2 ).applyMatrix4( _matrix$1 );

					// determine whether the batched geometry is within the frustum
					let culled = false;
					if ( perObjectFrustumCulled ) {

						culled = ! _frustum.intersectsSphere( _sphere$2 );

					}

					if ( ! culled ) {

						// get the distance from camera used for sorting
						const z = _temp.subVectors( _sphere$2.center, _vector$5 ).dot( _forward );
						_renderList.push( drawRanges[ geometryId ], z, i );

					}

				}

			}

			// Sort the draw ranges and prep for rendering
			const list = _renderList.list;
			const customSort = this.customSort;
			if ( customSort === null ) {

				list.sort( material.transparent ? sortTransparent : sortOpaque );

			} else {

				customSort.call( this, list, camera );

			}

			for ( let i = 0, l = list.length; i < l; i ++ ) {

				const item = list[ i ];
				multiDrawStarts[ count ] = item.start * bytesPerElement;
				multiDrawCounts[ count ] = item.count;
				indirectArray[ count ] = item.index;
				count ++;

			}

			_renderList.reset();

		} else {

			for ( let i = 0, l = drawInfo.length; i < l; i ++ ) {

				if ( drawInfo[ i ].visible && drawInfo[ i ].active ) {

					const geometryId = drawInfo[ i ].geometryIndex;

					// determine whether the batched geometry is within the frustum
					let culled = false;
					if ( perObjectFrustumCulled ) {

						// get the bounds in world space
						this.getMatrixAt( i, _matrix$1 );
						this.getBoundingSphereAt( geometryId, _sphere$2 ).applyMatrix4( _matrix$1 );
						culled = ! _frustum.intersectsSphere( _sphere$2 );

					}

					if ( ! culled ) {

						const range = drawRanges[ geometryId ];
						multiDrawStarts[ count ] = range.start * bytesPerElement;
						multiDrawCounts[ count ] = range.count;
						indirectArray[ count ] = i;
						count ++;

					}

				}

			}

		}

		indirectTexture.needsUpdate = true;
		this._multiDrawCount = count;
		this._visibilityChanged = false;

	}

	onBeforeShadow( renderer, object, camera, shadowCamera, geometry, depthMaterial/* , group */ ) {

		this.onBeforeRender( renderer, null, shadowCamera, geometry, depthMaterial );

	}

}

class LineBasicMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isLineBasicMaterial = true;

		this.type = 'LineBasicMaterial';

		this.color = new Color( 0xffffff );

		this.map = null;

		this.linewidth = 1;
		this.linecap = 'round';
		this.linejoin = 'round';

		this.fog = true;

		this.setValues( parameters );

	}


	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.linewidth = source.linewidth;
		this.linecap = source.linecap;
		this.linejoin = source.linejoin;

		this.fog = source.fog;

		return this;

	}

}

const _vStart = /*@__PURE__*/ new Vector3();
const _vEnd = /*@__PURE__*/ new Vector3();

const _inverseMatrix$1 = /*@__PURE__*/ new Matrix4();
const _ray$1 = /*@__PURE__*/ new Ray();
const _sphere$1 = /*@__PURE__*/ new Sphere();

const _intersectPointOnRay = /*@__PURE__*/ new Vector3();
const _intersectPointOnSegment = /*@__PURE__*/ new Vector3();

class Line extends Object3D {

	constructor( geometry = new BufferGeometry(), material = new LineBasicMaterial() ) {

		super();

		this.isLine = true;

		this.type = 'Line';

		this.geometry = geometry;
		this.material = material;

		this.updateMorphTargets();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.material = Array.isArray( source.material ) ? source.material.slice() : source.material;
		this.geometry = source.geometry;

		return this;

	}

	computeLineDistances() {

		const geometry = this.geometry;

		// we assume non-indexed geometry

		if ( geometry.index === null ) {

			const positionAttribute = geometry.attributes.position;
			const lineDistances = [ 0 ];

			for ( let i = 1, l = positionAttribute.count; i < l; i ++ ) {

				_vStart.fromBufferAttribute( positionAttribute, i - 1 );
				_vEnd.fromBufferAttribute( positionAttribute, i );

				lineDistances[ i ] = lineDistances[ i - 1 ];
				lineDistances[ i ] += _vStart.distanceTo( _vEnd );

			}

			geometry.setAttribute( 'lineDistance', new Float32BufferAttribute( lineDistances, 1 ) );

		} else {

			console.warn( 'THREE.Line.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.' );

		}

		return this;

	}

	raycast( raycaster, intersects ) {

		const geometry = this.geometry;
		const matrixWorld = this.matrixWorld;
		const threshold = raycaster.params.Line.threshold;
		const drawRange = geometry.drawRange;

		// Checking boundingSphere distance to ray

		if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();

		_sphere$1.copy( geometry.boundingSphere );
		_sphere$1.applyMatrix4( matrixWorld );
		_sphere$1.radius += threshold;

		if ( raycaster.ray.intersectsSphere( _sphere$1 ) === false ) return;

		//

		_inverseMatrix$1.copy( matrixWorld ).invert();
		_ray$1.copy( raycaster.ray ).applyMatrix4( _inverseMatrix$1 );

		const localThreshold = threshold / ( ( this.scale.x + this.scale.y + this.scale.z ) / 3 );
		const localThresholdSq = localThreshold * localThreshold;

		const step = this.isLineSegments ? 2 : 1;

		const index = geometry.index;
		const attributes = geometry.attributes;
		const positionAttribute = attributes.position;

		if ( index !== null ) {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( index.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, l = end - 1; i < l; i += step ) {

				const a = index.getX( i );
				const b = index.getX( i + 1 );

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, a, b );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

			if ( this.isLineLoop ) {

				const a = index.getX( end - 1 );
				const b = index.getX( start );

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, a, b );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

		} else {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( positionAttribute.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, l = end - 1; i < l; i += step ) {

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, i, i + 1 );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

			if ( this.isLineLoop ) {

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, end - 1, start );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

		}

	}

	updateMorphTargets() {

		const geometry = this.geometry;

		const morphAttributes = geometry.morphAttributes;
		const keys = Object.keys( morphAttributes );

		if ( keys.length > 0 ) {

			const morphAttribute = morphAttributes[ keys[ 0 ] ];

			if ( morphAttribute !== undefined ) {

				this.morphTargetInfluences = [];
				this.morphTargetDictionary = {};

				for ( let m = 0, ml = morphAttribute.length; m < ml; m ++ ) {

					const name = morphAttribute[ m ].name || String( m );

					this.morphTargetInfluences.push( 0 );
					this.morphTargetDictionary[ name ] = m;

				}

			}

		}

	}

}

function checkIntersection( object, raycaster, ray, thresholdSq, a, b ) {

	const positionAttribute = object.geometry.attributes.position;

	_vStart.fromBufferAttribute( positionAttribute, a );
	_vEnd.fromBufferAttribute( positionAttribute, b );

	const distSq = ray.distanceSqToSegment( _vStart, _vEnd, _intersectPointOnRay, _intersectPointOnSegment );

	if ( distSq > thresholdSq ) return;

	_intersectPointOnRay.applyMatrix4( object.matrixWorld ); // Move back to world space for distance calculation

	const distance = raycaster.ray.origin.distanceTo( _intersectPointOnRay );

	if ( distance < raycaster.near || distance > raycaster.far ) return;

	return {

		distance: distance,
		// What do we want? intersection point on the ray or on the segment??
		// point: raycaster.ray.at( distance ),
		point: _intersectPointOnSegment.clone().applyMatrix4( object.matrixWorld ),
		index: a,
		face: null,
		faceIndex: null,
		barycoord: null,
		object: object

	};

}

const _start = /*@__PURE__*/ new Vector3();
const _end = /*@__PURE__*/ new Vector3();

class LineSegments extends Line {

	constructor( geometry, material ) {

		super( geometry, material );

		this.isLineSegments = true;

		this.type = 'LineSegments';

	}

	computeLineDistances() {

		const geometry = this.geometry;

		// we assume non-indexed geometry

		if ( geometry.index === null ) {

			const positionAttribute = geometry.attributes.position;
			const lineDistances = [];

			for ( let i = 0, l = positionAttribute.count; i < l; i += 2 ) {

				_start.fromBufferAttribute( positionAttribute, i );
				_end.fromBufferAttribute( positionAttribute, i + 1 );

				lineDistances[ i ] = ( i === 0 ) ? 0 : lineDistances[ i - 1 ];
				lineDistances[ i + 1 ] = lineDistances[ i ] + _start.distanceTo( _end );

			}

			geometry.setAttribute( 'lineDistance', new Float32BufferAttribute( lineDistances, 1 ) );

		} else {

			console.warn( 'THREE.LineSegments.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.' );

		}

		return this;

	}

}

class LineLoop extends Line {

	constructor( geometry, material ) {

		super( geometry, material );

		this.isLineLoop = true;

		this.type = 'LineLoop';

	}

}

class PointsMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isPointsMaterial = true;

		this.type = 'PointsMaterial';

		this.color = new Color( 0xffffff );

		this.map = null;

		this.alphaMap = null;

		this.size = 1;
		this.sizeAttenuation = true;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.alphaMap = source.alphaMap;

		this.size = source.size;
		this.sizeAttenuation = source.sizeAttenuation;

		this.fog = source.fog;

		return this;

	}

}

const _inverseMatrix = /*@__PURE__*/ new Matrix4();
const _ray = /*@__PURE__*/ new Ray();
const _sphere = /*@__PURE__*/ new Sphere();
const _position$2 = /*@__PURE__*/ new Vector3();

class Points extends Object3D {

	constructor( geometry = new BufferGeometry(), material = new PointsMaterial() ) {

		super();

		this.isPoints = true;

		this.type = 'Points';

		this.geometry = geometry;
		this.material = material;

		this.updateMorphTargets();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.material = Array.isArray( source.material ) ? source.material.slice() : source.material;
		this.geometry = source.geometry;

		return this;

	}

	raycast( raycaster, intersects ) {

		const geometry = this.geometry;
		const matrixWorld = this.matrixWorld;
		const threshold = raycaster.params.Points.threshold;
		const drawRange = geometry.drawRange;

		// Checking boundingSphere distance to ray

		if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();

		_sphere.copy( geometry.boundingSphere );
		_sphere.applyMatrix4( matrixWorld );
		_sphere.radius += threshold;

		if ( raycaster.ray.intersectsSphere( _sphere ) === false ) return;

		//

		_inverseMatrix.copy( matrixWorld ).invert();
		_ray.copy( raycaster.ray ).applyMatrix4( _inverseMatrix );

		const localThreshold = threshold / ( ( this.scale.x + this.scale.y + this.scale.z ) / 3 );
		const localThresholdSq = localThreshold * localThreshold;

		const index = geometry.index;
		const attributes = geometry.attributes;
		const positionAttribute = attributes.position;

		if ( index !== null ) {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( index.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, il = end; i < il; i ++ ) {

				const a = index.getX( i );

				_position$2.fromBufferAttribute( positionAttribute, a );

				testPoint( _position$2, a, localThresholdSq, matrixWorld, raycaster, intersects, this );

			}

		} else {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( positionAttribute.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, l = end; i < l; i ++ ) {

				_position$2.fromBufferAttribute( positionAttribute, i );

				testPoint( _position$2, i, localThresholdSq, matrixWorld, raycaster, intersects, this );

			}

		}

	}

	updateMorphTargets() {

		const geometry = this.geometry;

		const morphAttributes = geometry.morphAttributes;
		const keys = Object.keys( morphAttributes );

		if ( keys.length > 0 ) {

			const morphAttribute = morphAttributes[ keys[ 0 ] ];

			if ( morphAttribute !== undefined ) {

				this.morphTargetInfluences = [];
				this.morphTargetDictionary = {};

				for ( let m = 0, ml = morphAttribute.length; m < ml; m ++ ) {

					const name = morphAttribute[ m ].name || String( m );

					this.morphTargetInfluences.push( 0 );
					this.morphTargetDictionary[ name ] = m;

				}

			}

		}

	}

}

function testPoint( point, index, localThresholdSq, matrixWorld, raycaster, intersects, object ) {

	const rayPointDistanceSq = _ray.distanceSqToPoint( point );

	if ( rayPointDistanceSq < localThresholdSq ) {

		const intersectPoint = new Vector3();

		_ray.closestPointToPoint( point, intersectPoint );
		intersectPoint.applyMatrix4( matrixWorld );

		const distance = raycaster.ray.origin.distanceTo( intersectPoint );

		if ( distance < raycaster.near || distance > raycaster.far ) return;

		intersects.push( {

			distance: distance,
			distanceToRay: Math.sqrt( rayPointDistanceSq ),
			point: intersectPoint,
			index: index,
			face: null,
			faceIndex: null,
			barycoord: null,
			object: object

		} );

	}

}

class VideoTexture extends Texture {

	constructor( video, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy ) {

		super( video, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy );

		this.isVideoTexture = true;

		this.minFilter = minFilter !== undefined ? minFilter : LinearFilter;
		this.magFilter = magFilter !== undefined ? magFilter : LinearFilter;

		this.generateMipmaps = false;

		const scope = this;

		function updateVideo() {

			scope.needsUpdate = true;
			video.requestVideoFrameCallback( updateVideo );

		}

		if ( 'requestVideoFrameCallback' in video ) {

			video.requestVideoFrameCallback( updateVideo );

		}

	}

	clone() {

		return new this.constructor( this.image ).copy( this );

	}

	update() {

		const video = this.image;
		const hasVideoFrameCallback = 'requestVideoFrameCallback' in video;

		if ( hasVideoFrameCallback === false && video.readyState >= video.HAVE_CURRENT_DATA ) {

			this.needsUpdate = true;

		}

	}

}

class FramebufferTexture extends Texture {

	constructor( width, height ) {

		super( { width, height } );

		this.isFramebufferTexture = true;

		this.magFilter = NearestFilter;
		this.minFilter = NearestFilter;

		this.generateMipmaps = false;

		this.needsUpdate = true;

	}

}

class CompressedTexture extends Texture {

	constructor( mipmaps, width, height, format, type, mapping, wrapS, wrapT, magFilter, minFilter, anisotropy, colorSpace ) {

		super( null, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy, colorSpace );

		this.isCompressedTexture = true;

		this.image = { width: width, height: height };
		this.mipmaps = mipmaps;

		// no flipping for cube textures
		// (also flipping doesn't work for compressed textures )

		this.flipY = false;

		// can't generate mipmaps for compressed textures
		// mips must be embedded in DDS files

		this.generateMipmaps = false;

	}

}

class CompressedArrayTexture extends CompressedTexture {

	constructor( mipmaps, width, height, depth, format, type ) {

		super( mipmaps, width, height, format, type );

		this.isCompressedArrayTexture = true;
		this.image.depth = depth;
		this.wrapR = ClampToEdgeWrapping;

		this.layerUpdates = new Set();

	}

	addLayerUpdate( layerIndex ) {

		this.layerUpdates.add( layerIndex );

	}

	clearLayerUpdates() {

		this.layerUpdates.clear();

	}

}

class CompressedCubeTexture extends CompressedTexture {

	constructor( images, format, type ) {

		super( undefined, images[ 0 ].width, images[ 0 ].height, format, type, CubeReflectionMapping );

		this.isCompressedCubeTexture = true;
		this.isCubeTexture = true;

		this.image = images;

	}

}

class CanvasTexture extends Texture {

	constructor( canvas, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy ) {

		super( canvas, mapping, wrapS, wrapT, magFilter, minFilter, format, type, anisotropy );

		this.isCanvasTexture = true;

		this.needsUpdate = true;

	}

}

/**
 * Extensible curve object.
 *
 * Some common of curve methods:
 * .getPoint( t, optionalTarget ), .getTangent( t, optionalTarget )
 * .getPointAt( u, optionalTarget ), .getTangentAt( u, optionalTarget )
 * .getPoints(), .getSpacedPoints()
 * .getLength()
 * .updateArcLengths()
 *
 * This following curves inherit from THREE.Curve:
 *
 * -- 2D curves --
 * THREE.ArcCurve
 * THREE.CubicBezierCurve
 * THREE.EllipseCurve
 * THREE.LineCurve
 * THREE.QuadraticBezierCurve
 * THREE.SplineCurve
 *
 * -- 3D curves --
 * THREE.CatmullRomCurve3
 * THREE.CubicBezierCurve3
 * THREE.LineCurve3
 * THREE.QuadraticBezierCurve3
 *
 * A series of curves can be represented as a THREE.CurvePath.
 *
 **/

class Curve {

	constructor() {

		this.type = 'Curve';

		this.arcLengthDivisions = 200;

	}

	// Virtual base class method to overwrite and implement in subclasses
	//	- t [0 .. 1]

	getPoint( /* t, optionalTarget */ ) {

		console.warn( 'THREE.Curve: .getPoint() not implemented.' );
		return null;

	}

	// Get point at relative position in curve according to arc length
	// - u [0 .. 1]

	getPointAt( u, optionalTarget ) {

		const t = this.getUtoTmapping( u );
		return this.getPoint( t, optionalTarget );

	}

	// Get sequence of points using getPoint( t )

	getPoints( divisions = 5 ) {

		const points = [];

		for ( let d = 0; d <= divisions; d ++ ) {

			points.push( this.getPoint( d / divisions ) );

		}

		return points;

	}

	// Get sequence of points using getPointAt( u )

	getSpacedPoints( divisions = 5 ) {

		const points = [];

		for ( let d = 0; d <= divisions; d ++ ) {

			points.push( this.getPointAt( d / divisions ) );

		}

		return points;

	}

	// Get total curve arc length

	getLength() {

		const lengths = this.getLengths();
		return lengths[ lengths.length - 1 ];

	}

	// Get list of cumulative segment lengths

	getLengths( divisions = this.arcLengthDivisions ) {

		if ( this.cacheArcLengths &&
			( this.cacheArcLengths.length === divisions + 1 ) &&
			! this.needsUpdate ) {

			return this.cacheArcLengths;

		}

		this.needsUpdate = false;

		const cache = [];
		let current, last = this.getPoint( 0 );
		let sum = 0;

		cache.push( 0 );

		for ( let p = 1; p <= divisions; p ++ ) {

			current = this.getPoint( p / divisions );
			sum += current.distanceTo( last );
			cache.push( sum );
			last = current;

		}

		this.cacheArcLengths = cache;

		return cache; // { sums: cache, sum: sum }; Sum is in the last element.

	}

	updateArcLengths() {

		this.needsUpdate = true;
		this.getLengths();

	}

	// Given u ( 0 .. 1 ), get a t to find p. This gives you points which are equidistant

	getUtoTmapping( u, distance ) {

		const arcLengths = this.getLengths();

		let i = 0;
		const il = arcLengths.length;

		let targetArcLength; // The targeted u distance value to get

		if ( distance ) {

			targetArcLength = distance;

		} else {

			targetArcLength = u * arcLengths[ il - 1 ];

		}

		// binary search for the index with largest value smaller than target u distance

		let low = 0, high = il - 1, comparison;

		while ( low <= high ) {

			i = Math.floor( low + ( high - low ) / 2 ); // less likely to overflow, though probably not issue here, JS doesn't really have integers, all numbers are floats

			comparison = arcLengths[ i ] - targetArcLength;

			if ( comparison < 0 ) {

				low = i + 1;

			} else if ( comparison > 0 ) {

				high = i - 1;

			} else {

				high = i;
				break;

				// DONE

			}

		}

		i = high;

		if ( arcLengths[ i ] === targetArcLength ) {

			return i / ( il - 1 );

		}

		// we could get finer grain at lengths, or use simple interpolation between two points

		const lengthBefore = arcLengths[ i ];
		const lengthAfter = arcLengths[ i + 1 ];

		const segmentLength = lengthAfter - lengthBefore;

		// determine where we are between the 'before' and 'after' points

		const segmentFraction = ( targetArcLength - lengthBefore ) / segmentLength;

		// add that fractional amount to t

		const t = ( i + segmentFraction ) / ( il - 1 );

		return t;

	}

	// Returns a unit vector tangent at t
	// In case any sub curve does not implement its tangent derivation,
	// 2 points a small delta apart will be used to find its gradient
	// which seems to give a reasonable approximation

	getTangent( t, optionalTarget ) {

		const delta = 0.0001;
		let t1 = t - delta;
		let t2 = t + delta;

		// Capping in case of danger

		if ( t1 < 0 ) t1 = 0;
		if ( t2 > 1 ) t2 = 1;

		const pt1 = this.getPoint( t1 );
		const pt2 = this.getPoint( t2 );

		const tangent = optionalTarget || ( ( pt1.isVector2 ) ? new Vector2() : new Vector3() );

		tangent.copy( pt2 ).sub( pt1 ).normalize();

		return tangent;

	}

	getTangentAt( u, optionalTarget ) {

		const t = this.getUtoTmapping( u );
		return this.getTangent( t, optionalTarget );

	}

	computeFrenetFrames( segments, closed ) {

		// see http://www.cs.indiana.edu/pub/techreports/TR425.pdf

		const normal = new Vector3();

		const tangents = [];
		const normals = [];
		const binormals = [];

		const vec = new Vector3();
		const mat = new Matrix4();

		// compute the tangent vectors for each segment on the curve

		for ( let i = 0; i <= segments; i ++ ) {

			const u = i / segments;

			tangents[ i ] = this.getTangentAt( u, new Vector3() );

		}

		// select an initial normal vector perpendicular to the first tangent vector,
		// and in the direction of the minimum tangent xyz component

		normals[ 0 ] = new Vector3();
		binormals[ 0 ] = new Vector3();
		let min = Number.MAX_VALUE;
		const tx = Math.abs( tangents[ 0 ].x );
		const ty = Math.abs( tangents[ 0 ].y );
		const tz = Math.abs( tangents[ 0 ].z );

		if ( tx <= min ) {

			min = tx;
			normal.set( 1, 0, 0 );

		}

		if ( ty <= min ) {

			min = ty;
			normal.set( 0, 1, 0 );

		}

		if ( tz <= min ) {

			normal.set( 0, 0, 1 );

		}

		vec.crossVectors( tangents[ 0 ], normal ).normalize();

		normals[ 0 ].crossVectors( tangents[ 0 ], vec );
		binormals[ 0 ].crossVectors( tangents[ 0 ], normals[ 0 ] );


		// compute the slowly-varying normal and binormal vectors for each segment on the curve

		for ( let i = 1; i <= segments; i ++ ) {

			normals[ i ] = normals[ i - 1 ].clone();

			binormals[ i ] = binormals[ i - 1 ].clone();

			vec.crossVectors( tangents[ i - 1 ], tangents[ i ] );

			if ( vec.length() > Number.EPSILON ) {

				vec.normalize();

				const theta = Math.acos( clamp( tangents[ i - 1 ].dot( tangents[ i ] ), - 1, 1 ) ); // clamp for floating pt errors

				normals[ i ].applyMatrix4( mat.makeRotationAxis( vec, theta ) );

			}

			binormals[ i ].crossVectors( tangents[ i ], normals[ i ] );

		}

		// if the curve is closed, postprocess the vectors so the first and last normal vectors are the same

		if ( closed === true ) {

			let theta = Math.acos( clamp( normals[ 0 ].dot( normals[ segments ] ), - 1, 1 ) );
			theta /= segments;

			if ( tangents[ 0 ].dot( vec.crossVectors( normals[ 0 ], normals[ segments ] ) ) > 0 ) {

				theta = - theta;

			}

			for ( let i = 1; i <= segments; i ++ ) {

				// twist a little...
				normals[ i ].applyMatrix4( mat.makeRotationAxis( tangents[ i ], theta * i ) );
				binormals[ i ].crossVectors( tangents[ i ], normals[ i ] );

			}

		}

		return {
			tangents: tangents,
			normals: normals,
			binormals: binormals
		};

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.arcLengthDivisions = source.arcLengthDivisions;

		return this;

	}

	toJSON() {

		const data = {
			metadata: {
				version: 4.6,
				type: 'Curve',
				generator: 'Curve.toJSON'
			}
		};

		data.arcLengthDivisions = this.arcLengthDivisions;
		data.type = this.type;

		return data;

	}

	fromJSON( json ) {

		this.arcLengthDivisions = json.arcLengthDivisions;

		return this;

	}

}

class EllipseCurve extends Curve {

	constructor( aX = 0, aY = 0, xRadius = 1, yRadius = 1, aStartAngle = 0, aEndAngle = Math.PI * 2, aClockwise = false, aRotation = 0 ) {

		super();

		this.isEllipseCurve = true;

		this.type = 'EllipseCurve';

		this.aX = aX;
		this.aY = aY;

		this.xRadius = xRadius;
		this.yRadius = yRadius;

		this.aStartAngle = aStartAngle;
		this.aEndAngle = aEndAngle;

		this.aClockwise = aClockwise;

		this.aRotation = aRotation;

	}

	getPoint( t, optionalTarget = new Vector2() ) {

		const point = optionalTarget;

		const twoPi = Math.PI * 2;
		let deltaAngle = this.aEndAngle - this.aStartAngle;
		const samePoints = Math.abs( deltaAngle ) < Number.EPSILON;

		// ensures that deltaAngle is 0 .. 2 PI
		while ( deltaAngle < 0 ) deltaAngle += twoPi;
		while ( deltaAngle > twoPi ) deltaAngle -= twoPi;

		if ( deltaAngle < Number.EPSILON ) {

			if ( samePoints ) {

				deltaAngle = 0;

			} else {

				deltaAngle = twoPi;

			}

		}

		if ( this.aClockwise === true && ! samePoints ) {

			if ( deltaAngle === twoPi ) {

				deltaAngle = - twoPi;

			} else {

				deltaAngle = deltaAngle - twoPi;

			}

		}

		const angle = this.aStartAngle + t * deltaAngle;
		let x = this.aX + this.xRadius * Math.cos( angle );
		let y = this.aY + this.yRadius * Math.sin( angle );

		if ( this.aRotation !== 0 ) {

			const cos = Math.cos( this.aRotation );
			const sin = Math.sin( this.aRotation );

			const tx = x - this.aX;
			const ty = y - this.aY;

			// Rotate the point about the center of the ellipse.
			x = tx * cos - ty * sin + this.aX;
			y = tx * sin + ty * cos + this.aY;

		}

		return point.set( x, y );

	}

	copy( source ) {

		super.copy( source );

		this.aX = source.aX;
		this.aY = source.aY;

		this.xRadius = source.xRadius;
		this.yRadius = source.yRadius;

		this.aStartAngle = source.aStartAngle;
		this.aEndAngle = source.aEndAngle;

		this.aClockwise = source.aClockwise;

		this.aRotation = source.aRotation;

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.aX = this.aX;
		data.aY = this.aY;

		data.xRadius = this.xRadius;
		data.yRadius = this.yRadius;

		data.aStartAngle = this.aStartAngle;
		data.aEndAngle = this.aEndAngle;

		data.aClockwise = this.aClockwise;

		data.aRotation = this.aRotation;

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.aX = json.aX;
		this.aY = json.aY;

		this.xRadius = json.xRadius;
		this.yRadius = json.yRadius;

		this.aStartAngle = json.aStartAngle;
		this.aEndAngle = json.aEndAngle;

		this.aClockwise = json.aClockwise;

		this.aRotation = json.aRotation;

		return this;

	}

}

class ArcCurve extends EllipseCurve {

	constructor( aX, aY, aRadius, aStartAngle, aEndAngle, aClockwise ) {

		super( aX, aY, aRadius, aRadius, aStartAngle, aEndAngle, aClockwise );

		this.isArcCurve = true;

		this.type = 'ArcCurve';

	}

}

/**
 * Centripetal CatmullRom Curve - which is useful for avoiding
 * cusps and self-intersections in non-uniform catmull rom curves.
 * http://www.cemyuksel.com/research/catmullrom_param/catmullrom.pdf
 *
 * curve.type accepts centripetal(default), chordal and catmullrom
 * curve.tension is used for catmullrom which defaults to 0.5
 */


/*
Based on an optimized c++ solution in
 - http://stackoverflow.com/questions/9489736/catmull-rom-curve-with-no-cusps-and-no-self-intersections/
 - http://ideone.com/NoEbVM

This CubicPoly class could be used for reusing some variables and calculations,
but for three.js curve use, it could be possible inlined and flatten into a single function call
which can be placed in CurveUtils.
*/

function CubicPoly() {

	let c0 = 0, c1 = 0, c2 = 0, c3 = 0;

	/*
	 * Compute coefficients for a cubic polynomial
	 *   p(s) = c0 + c1*s + c2*s^2 + c3*s^3
	 * such that
	 *   p(0) = x0, p(1) = x1
	 *  and
	 *   p'(0) = t0, p'(1) = t1.
	 */
	function init( x0, x1, t0, t1 ) {

		c0 = x0;
		c1 = t0;
		c2 = - 3 * x0 + 3 * x1 - 2 * t0 - t1;
		c3 = 2 * x0 - 2 * x1 + t0 + t1;

	}

	return {

		initCatmullRom: function ( x0, x1, x2, x3, tension ) {

			init( x1, x2, tension * ( x2 - x0 ), tension * ( x3 - x1 ) );

		},

		initNonuniformCatmullRom: function ( x0, x1, x2, x3, dt0, dt1, dt2 ) {

			// compute tangents when parameterized in [t1,t2]
			let t1 = ( x1 - x0 ) / dt0 - ( x2 - x0 ) / ( dt0 + dt1 ) + ( x2 - x1 ) / dt1;
			let t2 = ( x2 - x1 ) / dt1 - ( x3 - x1 ) / ( dt1 + dt2 ) + ( x3 - x2 ) / dt2;

			// rescale tangents for parametrization in [0,1]
			t1 *= dt1;
			t2 *= dt1;

			init( x1, x2, t1, t2 );

		},

		calc: function ( t ) {

			const t2 = t * t;
			const t3 = t2 * t;
			return c0 + c1 * t + c2 * t2 + c3 * t3;

		}

	};

}

//

const tmp = /*@__PURE__*/ new Vector3();
const px = /*@__PURE__*/ new CubicPoly();
const py = /*@__PURE__*/ new CubicPoly();
const pz = /*@__PURE__*/ new CubicPoly();

class CatmullRomCurve3 extends Curve {

	constructor( points = [], closed = false, curveType = 'centripetal', tension = 0.5 ) {

		super();

		this.isCatmullRomCurve3 = true;

		this.type = 'CatmullRomCurve3';

		this.points = points;
		this.closed = closed;
		this.curveType = curveType;
		this.tension = tension;

	}

	getPoint( t, optionalTarget = new Vector3() ) {

		const point = optionalTarget;

		const points = this.points;
		const l = points.length;

		const p = ( l - ( this.closed ? 0 : 1 ) ) * t;
		let intPoint = Math.floor( p );
		let weight = p - intPoint;

		if ( this.closed ) {

			intPoint += intPoint > 0 ? 0 : ( Math.floor( Math.abs( intPoint ) / l ) + 1 ) * l;

		} else if ( weight === 0 && intPoint === l - 1 ) {

			intPoint = l - 2;
			weight = 1;

		}

		let p0, p3; // 4 points (p1 & p2 defined below)

		if ( this.closed || intPoint > 0 ) {

			p0 = points[ ( intPoint - 1 ) % l ];

		} else {

			// extrapolate first point
			tmp.subVectors( points[ 0 ], points[ 1 ] ).add( points[ 0 ] );
			p0 = tmp;

		}

		const p1 = points[ intPoint % l ];
		const p2 = points[ ( intPoint + 1 ) % l ];

		if ( this.closed || intPoint + 2 < l ) {

			p3 = points[ ( intPoint + 2 ) % l ];

		} else {

			// extrapolate last point
			tmp.subVectors( points[ l - 1 ], points[ l - 2 ] ).add( points[ l - 1 ] );
			p3 = tmp;

		}

		if ( this.curveType === 'centripetal' || this.curveType === 'chordal' ) {

			// init Centripetal / Chordal Catmull-Rom
			const pow = this.curveType === 'chordal' ? 0.5 : 0.25;
			let dt0 = Math.pow( p0.distanceToSquared( p1 ), pow );
			let dt1 = Math.pow( p1.distanceToSquared( p2 ), pow );
			let dt2 = Math.pow( p2.distanceToSquared( p3 ), pow );

			// safety check for repeated points
			if ( dt1 < 1e-4 ) dt1 = 1.0;
			if ( dt0 < 1e-4 ) dt0 = dt1;
			if ( dt2 < 1e-4 ) dt2 = dt1;

			px.initNonuniformCatmullRom( p0.x, p1.x, p2.x, p3.x, dt0, dt1, dt2 );
			py.initNonuniformCatmullRom( p0.y, p1.y, p2.y, p3.y, dt0, dt1, dt2 );
			pz.initNonuniformCatmullRom( p0.z, p1.z, p2.z, p3.z, dt0, dt1, dt2 );

		} else if ( this.curveType === 'catmullrom' ) {

			px.initCatmullRom( p0.x, p1.x, p2.x, p3.x, this.tension );
			py.initCatmullRom( p0.y, p1.y, p2.y, p3.y, this.tension );
			pz.initCatmullRom( p0.z, p1.z, p2.z, p3.z, this.tension );

		}

		point.set(
			px.calc( weight ),
			py.calc( weight ),
			pz.calc( weight )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.points = [];

		for ( let i = 0, l = source.points.length; i < l; i ++ ) {

			const point = source.points[ i ];

			this.points.push( point.clone() );

		}

		this.closed = source.closed;
		this.curveType = source.curveType;
		this.tension = source.tension;

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.points = [];

		for ( let i = 0, l = this.points.length; i < l; i ++ ) {

			const point = this.points[ i ];
			data.points.push( point.toArray() );

		}

		data.closed = this.closed;
		data.curveType = this.curveType;
		data.tension = this.tension;

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.points = [];

		for ( let i = 0, l = json.points.length; i < l; i ++ ) {

			const point = json.points[ i ];
			this.points.push( new Vector3().fromArray( point ) );

		}

		this.closed = json.closed;
		this.curveType = json.curveType;
		this.tension = json.tension;

		return this;

	}

}

/**
 * Bezier Curves formulas obtained from
 * https://en.wikipedia.org/wiki/B%C3%A9zier_curve
 */

function CatmullRom( t, p0, p1, p2, p3 ) {

	const v0 = ( p2 - p0 ) * 0.5;
	const v1 = ( p3 - p1 ) * 0.5;
	const t2 = t * t;
	const t3 = t * t2;
	return ( 2 * p1 - 2 * p2 + v0 + v1 ) * t3 + ( - 3 * p1 + 3 * p2 - 2 * v0 - v1 ) * t2 + v0 * t + p1;

}

//

function QuadraticBezierP0( t, p ) {

	const k = 1 - t;
	return k * k * p;

}

function QuadraticBezierP1( t, p ) {

	return 2 * ( 1 - t ) * t * p;

}

function QuadraticBezierP2( t, p ) {

	return t * t * p;

}

function QuadraticBezier( t, p0, p1, p2 ) {

	return QuadraticBezierP0( t, p0 ) + QuadraticBezierP1( t, p1 ) +
		QuadraticBezierP2( t, p2 );

}

//

function CubicBezierP0( t, p ) {

	const k = 1 - t;
	return k * k * k * p;

}

function CubicBezierP1( t, p ) {

	const k = 1 - t;
	return 3 * k * k * t * p;

}

function CubicBezierP2( t, p ) {

	return 3 * ( 1 - t ) * t * t * p;

}

function CubicBezierP3( t, p ) {

	return t * t * t * p;

}

function CubicBezier( t, p0, p1, p2, p3 ) {

	return CubicBezierP0( t, p0 ) + CubicBezierP1( t, p1 ) + CubicBezierP2( t, p2 ) +
		CubicBezierP3( t, p3 );

}

class CubicBezierCurve extends Curve {

	constructor( v0 = new Vector2(), v1 = new Vector2(), v2 = new Vector2(), v3 = new Vector2() ) {

		super();

		this.isCubicBezierCurve = true;

		this.type = 'CubicBezierCurve';

		this.v0 = v0;
		this.v1 = v1;
		this.v2 = v2;
		this.v3 = v3;

	}

	getPoint( t, optionalTarget = new Vector2() ) {

		const point = optionalTarget;

		const v0 = this.v0, v1 = this.v1, v2 = this.v2, v3 = this.v3;

		point.set(
			CubicBezier( t, v0.x, v1.x, v2.x, v3.x ),
			CubicBezier( t, v0.y, v1.y, v2.y, v3.y )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.v0.copy( source.v0 );
		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );
		this.v3.copy( source.v3 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v0 = this.v0.toArray();
		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();
		data.v3 = this.v3.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v0.fromArray( json.v0 );
		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );
		this.v3.fromArray( json.v3 );

		return this;

	}

}

class CubicBezierCurve3 extends Curve {

	constructor( v0 = new Vector3(), v1 = new Vector3(), v2 = new Vector3(), v3 = new Vector3() ) {

		super();

		this.isCubicBezierCurve3 = true;

		this.type = 'CubicBezierCurve3';

		this.v0 = v0;
		this.v1 = v1;
		this.v2 = v2;
		this.v3 = v3;

	}

	getPoint( t, optionalTarget = new Vector3() ) {

		const point = optionalTarget;

		const v0 = this.v0, v1 = this.v1, v2 = this.v2, v3 = this.v3;

		point.set(
			CubicBezier( t, v0.x, v1.x, v2.x, v3.x ),
			CubicBezier( t, v0.y, v1.y, v2.y, v3.y ),
			CubicBezier( t, v0.z, v1.z, v2.z, v3.z )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.v0.copy( source.v0 );
		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );
		this.v3.copy( source.v3 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v0 = this.v0.toArray();
		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();
		data.v3 = this.v3.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v0.fromArray( json.v0 );
		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );
		this.v3.fromArray( json.v3 );

		return this;

	}

}

class LineCurve extends Curve {

	constructor( v1 = new Vector2(), v2 = new Vector2() ) {

		super();

		this.isLineCurve = true;

		this.type = 'LineCurve';

		this.v1 = v1;
		this.v2 = v2;

	}

	getPoint( t, optionalTarget = new Vector2() ) {

		const point = optionalTarget;

		if ( t === 1 ) {

			point.copy( this.v2 );

		} else {

			point.copy( this.v2 ).sub( this.v1 );
			point.multiplyScalar( t ).add( this.v1 );

		}

		return point;

	}

	// Line curve is linear, so we can overwrite default getPointAt
	getPointAt( u, optionalTarget ) {

		return this.getPoint( u, optionalTarget );

	}

	getTangent( t, optionalTarget = new Vector2() ) {

		return optionalTarget.subVectors( this.v2, this.v1 ).normalize();

	}

	getTangentAt( u, optionalTarget ) {

		return this.getTangent( u, optionalTarget );

	}

	copy( source ) {

		super.copy( source );

		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );

		return this;

	}

}

class LineCurve3 extends Curve {

	constructor( v1 = new Vector3(), v2 = new Vector3() ) {

		super();

		this.isLineCurve3 = true;

		this.type = 'LineCurve3';

		this.v1 = v1;
		this.v2 = v2;

	}

	getPoint( t, optionalTarget = new Vector3() ) {

		const point = optionalTarget;

		if ( t === 1 ) {

			point.copy( this.v2 );

		} else {

			point.copy( this.v2 ).sub( this.v1 );
			point.multiplyScalar( t ).add( this.v1 );

		}

		return point;

	}

	// Line curve is linear, so we can overwrite default getPointAt
	getPointAt( u, optionalTarget ) {

		return this.getPoint( u, optionalTarget );

	}

	getTangent( t, optionalTarget = new Vector3() ) {

		return optionalTarget.subVectors( this.v2, this.v1 ).normalize();

	}

	getTangentAt( u, optionalTarget ) {

		return this.getTangent( u, optionalTarget );

	}

	copy( source ) {

		super.copy( source );

		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );

		return this;

	}

}

class QuadraticBezierCurve extends Curve {

	constructor( v0 = new Vector2(), v1 = new Vector2(), v2 = new Vector2() ) {

		super();

		this.isQuadraticBezierCurve = true;

		this.type = 'QuadraticBezierCurve';

		this.v0 = v0;
		this.v1 = v1;
		this.v2 = v2;

	}

	getPoint( t, optionalTarget = new Vector2() ) {

		const point = optionalTarget;

		const v0 = this.v0, v1 = this.v1, v2 = this.v2;

		point.set(
			QuadraticBezier( t, v0.x, v1.x, v2.x ),
			QuadraticBezier( t, v0.y, v1.y, v2.y )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.v0.copy( source.v0 );
		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v0 = this.v0.toArray();
		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v0.fromArray( json.v0 );
		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );

		return this;

	}

}

class QuadraticBezierCurve3 extends Curve {

	constructor( v0 = new Vector3(), v1 = new Vector3(), v2 = new Vector3() ) {

		super();

		this.isQuadraticBezierCurve3 = true;

		this.type = 'QuadraticBezierCurve3';

		this.v0 = v0;
		this.v1 = v1;
		this.v2 = v2;

	}

	getPoint( t, optionalTarget = new Vector3() ) {

		const point = optionalTarget;

		const v0 = this.v0, v1 = this.v1, v2 = this.v2;

		point.set(
			QuadraticBezier( t, v0.x, v1.x, v2.x ),
			QuadraticBezier( t, v0.y, v1.y, v2.y ),
			QuadraticBezier( t, v0.z, v1.z, v2.z )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.v0.copy( source.v0 );
		this.v1.copy( source.v1 );
		this.v2.copy( source.v2 );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.v0 = this.v0.toArray();
		data.v1 = this.v1.toArray();
		data.v2 = this.v2.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.v0.fromArray( json.v0 );
		this.v1.fromArray( json.v1 );
		this.v2.fromArray( json.v2 );

		return this;

	}

}

class SplineCurve extends Curve {

	constructor( points = [] ) {

		super();

		this.isSplineCurve = true;

		this.type = 'SplineCurve';

		this.points = points;

	}

	getPoint( t, optionalTarget = new Vector2() ) {

		const point = optionalTarget;

		const points = this.points;
		const p = ( points.length - 1 ) * t;

		const intPoint = Math.floor( p );
		const weight = p - intPoint;

		const p0 = points[ intPoint === 0 ? intPoint : intPoint - 1 ];
		const p1 = points[ intPoint ];
		const p2 = points[ intPoint > points.length - 2 ? points.length - 1 : intPoint + 1 ];
		const p3 = points[ intPoint > points.length - 3 ? points.length - 1 : intPoint + 2 ];

		point.set(
			CatmullRom( weight, p0.x, p1.x, p2.x, p3.x ),
			CatmullRom( weight, p0.y, p1.y, p2.y, p3.y )
		);

		return point;

	}

	copy( source ) {

		super.copy( source );

		this.points = [];

		for ( let i = 0, l = source.points.length; i < l; i ++ ) {

			const point = source.points[ i ];

			this.points.push( point.clone() );

		}

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.points = [];

		for ( let i = 0, l = this.points.length; i < l; i ++ ) {

			const point = this.points[ i ];
			data.points.push( point.toArray() );

		}

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.points = [];

		for ( let i = 0, l = json.points.length; i < l; i ++ ) {

			const point = json.points[ i ];
			this.points.push( new Vector2().fromArray( point ) );

		}

		return this;

	}

}

var Curves = /*#__PURE__*/Object.freeze({
	__proto__: null,
	ArcCurve: ArcCurve,
	CatmullRomCurve3: CatmullRomCurve3,
	CubicBezierCurve: CubicBezierCurve,
	CubicBezierCurve3: CubicBezierCurve3,
	EllipseCurve: EllipseCurve,
	LineCurve: LineCurve,
	LineCurve3: LineCurve3,
	QuadraticBezierCurve: QuadraticBezierCurve,
	QuadraticBezierCurve3: QuadraticBezierCurve3,
	SplineCurve: SplineCurve
});

/**************************************************************
 *	Curved Path - a curve path is simply a array of connected
 *  curves, but retains the api of a curve
 **************************************************************/

class CurvePath extends Curve {

	constructor() {

		super();

		this.type = 'CurvePath';

		this.curves = [];
		this.autoClose = false; // Automatically closes the path

	}

	add( curve ) {

		this.curves.push( curve );

	}

	closePath() {

		// Add a line curve if start and end of lines are not connected
		const startPoint = this.curves[ 0 ].getPoint( 0 );
		const endPoint = this.curves[ this.curves.length - 1 ].getPoint( 1 );

		if ( ! startPoint.equals( endPoint ) ) {

			const lineType = ( startPoint.isVector2 === true ) ? 'LineCurve' : 'LineCurve3';
			this.curves.push( new Curves[ lineType ]( endPoint, startPoint ) );

		}

		return this;

	}

	// To get accurate point with reference to
	// entire path distance at time t,
	// following has to be done:

	// 1. Length of each sub path have to be known
	// 2. Locate and identify type of curve
	// 3. Get t for the curve
	// 4. Return curve.getPointAt(t')

	getPoint( t, optionalTarget ) {

		const d = t * this.getLength();
		const curveLengths = this.getCurveLengths();
		let i = 0;

		// To think about boundaries points.

		while ( i < curveLengths.length ) {

			if ( curveLengths[ i ] >= d ) {

				const diff = curveLengths[ i ] - d;
				const curve = this.curves[ i ];

				const segmentLength = curve.getLength();
				const u = segmentLength === 0 ? 0 : 1 - diff / segmentLength;

				return curve.getPointAt( u, optionalTarget );

			}

			i ++;

		}

		return null;

		// loop where sum != 0, sum > d , sum+1 <d

	}

	// We cannot use the default THREE.Curve getPoint() with getLength() because in
	// THREE.Curve, getLength() depends on getPoint() but in THREE.CurvePath
	// getPoint() depends on getLength

	getLength() {

		const lens = this.getCurveLengths();
		return lens[ lens.length - 1 ];

	}

	// cacheLengths must be recalculated.
	updateArcLengths() {

		this.needsUpdate = true;
		this.cacheLengths = null;
		this.getCurveLengths();

	}

	// Compute lengths and cache them
	// We cannot overwrite getLengths() because UtoT mapping uses it.

	getCurveLengths() {

		// We use cache values if curves and cache array are same length

		if ( this.cacheLengths && this.cacheLengths.length === this.curves.length ) {

			return this.cacheLengths;

		}

		// Get length of sub-curve
		// Push sums into cached array

		const lengths = [];
		let sums = 0;

		for ( let i = 0, l = this.curves.length; i < l; i ++ ) {

			sums += this.curves[ i ].getLength();
			lengths.push( sums );

		}

		this.cacheLengths = lengths;

		return lengths;

	}

	getSpacedPoints( divisions = 40 ) {

		const points = [];

		for ( let i = 0; i <= divisions; i ++ ) {

			points.push( this.getPoint( i / divisions ) );

		}

		if ( this.autoClose ) {

			points.push( points[ 0 ] );

		}

		return points;

	}

	getPoints( divisions = 12 ) {

		const points = [];
		let last;

		for ( let i = 0, curves = this.curves; i < curves.length; i ++ ) {

			const curve = curves[ i ];
			const resolution = curve.isEllipseCurve ? divisions * 2
				: ( curve.isLineCurve || curve.isLineCurve3 ) ? 1
					: curve.isSplineCurve ? divisions * curve.points.length
						: divisions;

			const pts = curve.getPoints( resolution );

			for ( let j = 0; j < pts.length; j ++ ) {

				const point = pts[ j ];

				if ( last && last.equals( point ) ) continue; // ensures no consecutive points are duplicates

				points.push( point );
				last = point;

			}

		}

		if ( this.autoClose && points.length > 1 && ! points[ points.length - 1 ].equals( points[ 0 ] ) ) {

			points.push( points[ 0 ] );

		}

		return points;

	}

	copy( source ) {

		super.copy( source );

		this.curves = [];

		for ( let i = 0, l = source.curves.length; i < l; i ++ ) {

			const curve = source.curves[ i ];

			this.curves.push( curve.clone() );

		}

		this.autoClose = source.autoClose;

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.autoClose = this.autoClose;
		data.curves = [];

		for ( let i = 0, l = this.curves.length; i < l; i ++ ) {

			const curve = this.curves[ i ];
			data.curves.push( curve.toJSON() );

		}

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.autoClose = json.autoClose;
		this.curves = [];

		for ( let i = 0, l = json.curves.length; i < l; i ++ ) {

			const curve = json.curves[ i ];
			this.curves.push( new Curves[ curve.type ]().fromJSON( curve ) );

		}

		return this;

	}

}

class Path extends CurvePath {

	constructor( points ) {

		super();

		this.type = 'Path';

		this.currentPoint = new Vector2();

		if ( points ) {

			this.setFromPoints( points );

		}

	}

	setFromPoints( points ) {

		this.moveTo( points[ 0 ].x, points[ 0 ].y );

		for ( let i = 1, l = points.length; i < l; i ++ ) {

			this.lineTo( points[ i ].x, points[ i ].y );

		}

		return this;

	}

	moveTo( x, y ) {

		this.currentPoint.set( x, y ); // TODO consider referencing vectors instead of copying?

		return this;

	}

	lineTo( x, y ) {

		const curve = new LineCurve( this.currentPoint.clone(), new Vector2( x, y ) );
		this.curves.push( curve );

		this.currentPoint.set( x, y );

		return this;

	}

	quadraticCurveTo( aCPx, aCPy, aX, aY ) {

		const curve = new QuadraticBezierCurve(
			this.currentPoint.clone(),
			new Vector2( aCPx, aCPy ),
			new Vector2( aX, aY )
		);

		this.curves.push( curve );

		this.currentPoint.set( aX, aY );

		return this;

	}

	bezierCurveTo( aCP1x, aCP1y, aCP2x, aCP2y, aX, aY ) {

		const curve = new CubicBezierCurve(
			this.currentPoint.clone(),
			new Vector2( aCP1x, aCP1y ),
			new Vector2( aCP2x, aCP2y ),
			new Vector2( aX, aY )
		);

		this.curves.push( curve );

		this.currentPoint.set( aX, aY );

		return this;

	}

	splineThru( pts /*Array of Vector*/ ) {

		const npts = [ this.currentPoint.clone() ].concat( pts );

		const curve = new SplineCurve( npts );
		this.curves.push( curve );

		this.currentPoint.copy( pts[ pts.length - 1 ] );

		return this;

	}

	arc( aX, aY, aRadius, aStartAngle, aEndAngle, aClockwise ) {

		const x0 = this.currentPoint.x;
		const y0 = this.currentPoint.y;

		this.absarc( aX + x0, aY + y0, aRadius,
			aStartAngle, aEndAngle, aClockwise );

		return this;

	}

	absarc( aX, aY, aRadius, aStartAngle, aEndAngle, aClockwise ) {

		this.absellipse( aX, aY, aRadius, aRadius, aStartAngle, aEndAngle, aClockwise );

		return this;

	}

	ellipse( aX, aY, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation ) {

		const x0 = this.currentPoint.x;
		const y0 = this.currentPoint.y;

		this.absellipse( aX + x0, aY + y0, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation );

		return this;

	}

	absellipse( aX, aY, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation ) {

		const curve = new EllipseCurve( aX, aY, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation );

		if ( this.curves.length > 0 ) {

			// if a previous curve is present, attempt to join
			const firstPoint = curve.getPoint( 0 );

			if ( ! firstPoint.equals( this.currentPoint ) ) {

				this.lineTo( firstPoint.x, firstPoint.y );

			}

		}

		this.curves.push( curve );

		const lastPoint = curve.getPoint( 1 );
		this.currentPoint.copy( lastPoint );

		return this;

	}

	copy( source ) {

		super.copy( source );

		this.currentPoint.copy( source.currentPoint );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.currentPoint = this.currentPoint.toArray();

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.currentPoint.fromArray( json.currentPoint );

		return this;

	}

}

class LatheGeometry extends BufferGeometry {

	constructor( points = [ new Vector2( 0, - 0.5 ), new Vector2( 0.5, 0 ), new Vector2( 0, 0.5 ) ], segments = 12, phiStart = 0, phiLength = Math.PI * 2 ) {

		super();

		this.type = 'LatheGeometry';

		this.parameters = {
			points: points,
			segments: segments,
			phiStart: phiStart,
			phiLength: phiLength
		};

		segments = Math.floor( segments );

		// clamp phiLength so it's in range of [ 0, 2PI ]

		phiLength = clamp( phiLength, 0, Math.PI * 2 );

		// buffers

		const indices = [];
		const vertices = [];
		const uvs = [];
		const initNormals = [];
		const normals = [];

		// helper variables

		const inverseSegments = 1.0 / segments;
		const vertex = new Vector3();
		const uv = new Vector2();
		const normal = new Vector3();
		const curNormal = new Vector3();
		const prevNormal = new Vector3();
		let dx = 0;
		let dy = 0;

		// pre-compute normals for initial "meridian"

		for ( let j = 0; j <= ( points.length - 1 ); j ++ ) {

			switch ( j ) {

				case 0:				// special handling for 1st vertex on path

					dx = points[ j + 1 ].x - points[ j ].x;
					dy = points[ j + 1 ].y - points[ j ].y;

					normal.x = dy * 1.0;
					normal.y = - dx;
					normal.z = dy * 0.0;

					prevNormal.copy( normal );

					normal.normalize();

					initNormals.push( normal.x, normal.y, normal.z );

					break;

				case ( points.length - 1 ):	// special handling for last Vertex on path

					initNormals.push( prevNormal.x, prevNormal.y, prevNormal.z );

					break;

				default:			// default handling for all vertices in between

					dx = points[ j + 1 ].x - points[ j ].x;
					dy = points[ j + 1 ].y - points[ j ].y;

					normal.x = dy * 1.0;
					normal.y = - dx;
					normal.z = dy * 0.0;

					curNormal.copy( normal );

					normal.x += prevNormal.x;
					normal.y += prevNormal.y;
					normal.z += prevNormal.z;

					normal.normalize();

					initNormals.push( normal.x, normal.y, normal.z );

					prevNormal.copy( curNormal );

			}

		}

		// generate vertices, uvs and normals

		for ( let i = 0; i <= segments; i ++ ) {

			const phi = phiStart + i * inverseSegments * phiLength;

			const sin = Math.sin( phi );
			const cos = Math.cos( phi );

			for ( let j = 0; j <= ( points.length - 1 ); j ++ ) {

				// vertex

				vertex.x = points[ j ].x * sin;
				vertex.y = points[ j ].y;
				vertex.z = points[ j ].x * cos;

				vertices.push( vertex.x, vertex.y, vertex.z );

				// uv

				uv.x = i / segments;
				uv.y = j / ( points.length - 1 );

				uvs.push( uv.x, uv.y );

				// normal

				const x = initNormals[ 3 * j + 0 ] * sin;
				const y = initNormals[ 3 * j + 1 ];
				const z = initNormals[ 3 * j + 0 ] * cos;

				normals.push( x, y, z );

			}

		}

		// indices

		for ( let i = 0; i < segments; i ++ ) {

			for ( let j = 0; j < ( points.length - 1 ); j ++ ) {

				const base = j + i * points.length;

				const a = base;
				const b = base + points.length;
				const c = base + points.length + 1;
				const d = base + 1;

				// faces

				indices.push( a, b, d );
				indices.push( c, d, b );

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new LatheGeometry( data.points, data.segments, data.phiStart, data.phiLength );

	}

}

class CapsuleGeometry extends LatheGeometry {

	constructor( radius = 1, length = 1, capSegments = 4, radialSegments = 8 ) {

		const path = new Path();
		path.absarc( 0, - length / 2, radius, Math.PI * 1.5, 0 );
		path.absarc( 0, length / 2, radius, 0, Math.PI * 0.5 );

		super( path.getPoints( capSegments ), radialSegments );

		this.type = 'CapsuleGeometry';

		this.parameters = {
			radius: radius,
			length: length,
			capSegments: capSegments,
			radialSegments: radialSegments,
		};

	}

	static fromJSON( data ) {

		return new CapsuleGeometry( data.radius, data.length, data.capSegments, data.radialSegments );

	}

}

class CircleGeometry extends BufferGeometry {

	constructor( radius = 1, segments = 32, thetaStart = 0, thetaLength = Math.PI * 2 ) {

		super();

		this.type = 'CircleGeometry';

		this.parameters = {
			radius: radius,
			segments: segments,
			thetaStart: thetaStart,
			thetaLength: thetaLength
		};

		segments = Math.max( 3, segments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		const vertex = new Vector3();
		const uv = new Vector2();

		// center point

		vertices.push( 0, 0, 0 );
		normals.push( 0, 0, 1 );
		uvs.push( 0.5, 0.5 );

		for ( let s = 0, i = 3; s <= segments; s ++, i += 3 ) {

			const segment = thetaStart + s / segments * thetaLength;

			// vertex

			vertex.x = radius * Math.cos( segment );
			vertex.y = radius * Math.sin( segment );

			vertices.push( vertex.x, vertex.y, vertex.z );

			// normal

			normals.push( 0, 0, 1 );

			// uvs

			uv.x = ( vertices[ i ] / radius + 1 ) / 2;
			uv.y = ( vertices[ i + 1 ] / radius + 1 ) / 2;

			uvs.push( uv.x, uv.y );

		}

		// indices

		for ( let i = 1; i <= segments; i ++ ) {

			indices.push( i, i + 1, 0 );

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new CircleGeometry( data.radius, data.segments, data.thetaStart, data.thetaLength );

	}

}

class CylinderGeometry extends BufferGeometry {

	constructor( radiusTop = 1, radiusBottom = 1, height = 1, radialSegments = 32, heightSegments = 1, openEnded = false, thetaStart = 0, thetaLength = Math.PI * 2 ) {

		super();

		this.type = 'CylinderGeometry';

		this.parameters = {
			radiusTop: radiusTop,
			radiusBottom: radiusBottom,
			height: height,
			radialSegments: radialSegments,
			heightSegments: heightSegments,
			openEnded: openEnded,
			thetaStart: thetaStart,
			thetaLength: thetaLength
		};

		const scope = this;

		radialSegments = Math.floor( radialSegments );
		heightSegments = Math.floor( heightSegments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		let index = 0;
		const indexArray = [];
		const halfHeight = height / 2;
		let groupStart = 0;

		// generate geometry

		generateTorso();

		if ( openEnded === false ) {

			if ( radiusTop > 0 ) generateCap( true );
			if ( radiusBottom > 0 ) generateCap( false );

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

		function generateTorso() {

			const normal = new Vector3();
			const vertex = new Vector3();

			let groupCount = 0;

			// this will be used to calculate the normal
			const slope = ( radiusBottom - radiusTop ) / height;

			// generate vertices, normals and uvs

			for ( let y = 0; y <= heightSegments; y ++ ) {

				const indexRow = [];

				const v = y / heightSegments;

				// calculate the radius of the current row

				const radius = v * ( radiusBottom - radiusTop ) + radiusTop;

				for ( let x = 0; x <= radialSegments; x ++ ) {

					const u = x / radialSegments;

					const theta = u * thetaLength + thetaStart;

					const sinTheta = Math.sin( theta );
					const cosTheta = Math.cos( theta );

					// vertex

					vertex.x = radius * sinTheta;
					vertex.y = - v * height + halfHeight;
					vertex.z = radius * cosTheta;
					vertices.push( vertex.x, vertex.y, vertex.z );

					// normal

					normal.set( sinTheta, slope, cosTheta ).normalize();
					normals.push( normal.x, normal.y, normal.z );

					// uv

					uvs.push( u, 1 - v );

					// save index of vertex in respective row

					indexRow.push( index ++ );

				}

				// now save vertices of the row in our index array

				indexArray.push( indexRow );

			}

			// generate indices

			for ( let x = 0; x < radialSegments; x ++ ) {

				for ( let y = 0; y < heightSegments; y ++ ) {

					// we use the index array to access the correct indices

					const a = indexArray[ y ][ x ];
					const b = indexArray[ y + 1 ][ x ];
					const c = indexArray[ y + 1 ][ x + 1 ];
					const d = indexArray[ y ][ x + 1 ];

					// faces

					if ( radiusTop > 0 ) {

						indices.push( a, b, d );
						groupCount += 3;

					}

					if ( radiusBottom > 0 ) {

						indices.push( b, c, d );
						groupCount += 3;

					}

				}

			}

			// add a group to the geometry. this will ensure multi material support

			scope.addGroup( groupStart, groupCount, 0 );

			// calculate new start value for groups

			groupStart += groupCount;

		}

		function generateCap( top ) {

			// save the index of the first center vertex
			const centerIndexStart = index;

			const uv = new Vector2();
			const vertex = new Vector3();

			let groupCount = 0;

			const radius = ( top === true ) ? radiusTop : radiusBottom;
			const sign = ( top === true ) ? 1 : - 1;

			// first we generate the center vertex data of the cap.
			// because the geometry needs one set of uvs per face,
			// we must generate a center vertex per face/segment

			for ( let x = 1; x <= radialSegments; x ++ ) {

				// vertex

				vertices.push( 0, halfHeight * sign, 0 );

				// normal

				normals.push( 0, sign, 0 );

				// uv

				uvs.push( 0.5, 0.5 );

				// increase index

				index ++;

			}

			// save the index of the last center vertex
			const centerIndexEnd = index;

			// now we generate the surrounding vertices, normals and uvs

			for ( let x = 0; x <= radialSegments; x ++ ) {

				const u = x / radialSegments;
				const theta = u * thetaLength + thetaStart;

				const cosTheta = Math.cos( theta );
				const sinTheta = Math.sin( theta );

				// vertex

				vertex.x = radius * sinTheta;
				vertex.y = halfHeight * sign;
				vertex.z = radius * cosTheta;
				vertices.push( vertex.x, vertex.y, vertex.z );

				// normal

				normals.push( 0, sign, 0 );

				// uv

				uv.x = ( cosTheta * 0.5 ) + 0.5;
				uv.y = ( sinTheta * 0.5 * sign ) + 0.5;
				uvs.push( uv.x, uv.y );

				// increase index

				index ++;

			}

			// generate indices

			for ( let x = 0; x < radialSegments; x ++ ) {

				const c = centerIndexStart + x;
				const i = centerIndexEnd + x;

				if ( top === true ) {

					// face top

					indices.push( i, i + 1, c );

				} else {

					// face bottom

					indices.push( i + 1, i, c );

				}

				groupCount += 3;

			}

			// add a group to the geometry. this will ensure multi material support

			scope.addGroup( groupStart, groupCount, top === true ? 1 : 2 );

			// calculate new start value for groups

			groupStart += groupCount;

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new CylinderGeometry( data.radiusTop, data.radiusBottom, data.height, data.radialSegments, data.heightSegments, data.openEnded, data.thetaStart, data.thetaLength );

	}

}

class ConeGeometry extends CylinderGeometry {

	constructor( radius = 1, height = 1, radialSegments = 32, heightSegments = 1, openEnded = false, thetaStart = 0, thetaLength = Math.PI * 2 ) {

		super( 0, radius, height, radialSegments, heightSegments, openEnded, thetaStart, thetaLength );

		this.type = 'ConeGeometry';

		this.parameters = {
			radius: radius,
			height: height,
			radialSegments: radialSegments,
			heightSegments: heightSegments,
			openEnded: openEnded,
			thetaStart: thetaStart,
			thetaLength: thetaLength
		};

	}

	static fromJSON( data ) {

		return new ConeGeometry( data.radius, data.height, data.radialSegments, data.heightSegments, data.openEnded, data.thetaStart, data.thetaLength );

	}

}

class PolyhedronGeometry extends BufferGeometry {

	constructor( vertices = [], indices = [], radius = 1, detail = 0 ) {

		super();

		this.type = 'PolyhedronGeometry';

		this.parameters = {
			vertices: vertices,
			indices: indices,
			radius: radius,
			detail: detail
		};

		// default buffer data

		const vertexBuffer = [];
		const uvBuffer = [];

		// the subdivision creates the vertex buffer data

		subdivide( detail );

		// all vertices should lie on a conceptual sphere with a given radius

		applyRadius( radius );

		// finally, create the uv data

		generateUVs();

		// build non-indexed geometry

		this.setAttribute( 'position', new Float32BufferAttribute( vertexBuffer, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( vertexBuffer.slice(), 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvBuffer, 2 ) );

		if ( detail === 0 ) {

			this.computeVertexNormals(); // flat normals

		} else {

			this.normalizeNormals(); // smooth normals

		}

		// helper functions

		function subdivide( detail ) {

			const a = new Vector3();
			const b = new Vector3();
			const c = new Vector3();

			// iterate over all faces and apply a subdivision with the given detail value

			for ( let i = 0; i < indices.length; i += 3 ) {

				// get the vertices of the face

				getVertexByIndex( indices[ i + 0 ], a );
				getVertexByIndex( indices[ i + 1 ], b );
				getVertexByIndex( indices[ i + 2 ], c );

				// perform subdivision

				subdivideFace( a, b, c, detail );

			}

		}

		function subdivideFace( a, b, c, detail ) {

			const cols = detail + 1;

			// we use this multidimensional array as a data structure for creating the subdivision

			const v = [];

			// construct all of the vertices for this subdivision

			for ( let i = 0; i <= cols; i ++ ) {

				v[ i ] = [];

				const aj = a.clone().lerp( c, i / cols );
				const bj = b.clone().lerp( c, i / cols );

				const rows = cols - i;

				for ( let j = 0; j <= rows; j ++ ) {

					if ( j === 0 && i === cols ) {

						v[ i ][ j ] = aj;

					} else {

						v[ i ][ j ] = aj.clone().lerp( bj, j / rows );

					}

				}

			}

			// construct all of the faces

			for ( let i = 0; i < cols; i ++ ) {

				for ( let j = 0; j < 2 * ( cols - i ) - 1; j ++ ) {

					const k = Math.floor( j / 2 );

					if ( j % 2 === 0 ) {

						pushVertex( v[ i ][ k + 1 ] );
						pushVertex( v[ i + 1 ][ k ] );
						pushVertex( v[ i ][ k ] );

					} else {

						pushVertex( v[ i ][ k + 1 ] );
						pushVertex( v[ i + 1 ][ k + 1 ] );
						pushVertex( v[ i + 1 ][ k ] );

					}

				}

			}

		}

		function applyRadius( radius ) {

			const vertex = new Vector3();

			// iterate over the entire buffer and apply the radius to each vertex

			for ( let i = 0; i < vertexBuffer.length; i += 3 ) {

				vertex.x = vertexBuffer[ i + 0 ];
				vertex.y = vertexBuffer[ i + 1 ];
				vertex.z = vertexBuffer[ i + 2 ];

				vertex.normalize().multiplyScalar( radius );

				vertexBuffer[ i + 0 ] = vertex.x;
				vertexBuffer[ i + 1 ] = vertex.y;
				vertexBuffer[ i + 2 ] = vertex.z;

			}

		}

		function generateUVs() {

			const vertex = new Vector3();

			for ( let i = 0; i < vertexBuffer.length; i += 3 ) {

				vertex.x = vertexBuffer[ i + 0 ];
				vertex.y = vertexBuffer[ i + 1 ];
				vertex.z = vertexBuffer[ i + 2 ];

				const u = azimuth( vertex ) / 2 / Math.PI + 0.5;
				const v = inclination( vertex ) / Math.PI + 0.5;
				uvBuffer.push( u, 1 - v );

			}

			correctUVs();

			correctSeam();

		}

		function correctSeam() {

			// handle case when face straddles the seam, see #3269

			for ( let i = 0; i < uvBuffer.length; i += 6 ) {

				// uv data of a single face

				const x0 = uvBuffer[ i + 0 ];
				const x1 = uvBuffer[ i + 2 ];
				const x2 = uvBuffer[ i + 4 ];

				const max = Math.max( x0, x1, x2 );
				const min = Math.min( x0, x1, x2 );

				// 0.9 is somewhat arbitrary

				if ( max > 0.9 && min < 0.1 ) {

					if ( x0 < 0.2 ) uvBuffer[ i + 0 ] += 1;
					if ( x1 < 0.2 ) uvBuffer[ i + 2 ] += 1;
					if ( x2 < 0.2 ) uvBuffer[ i + 4 ] += 1;

				}

			}

		}

		function pushVertex( vertex ) {

			vertexBuffer.push( vertex.x, vertex.y, vertex.z );

		}

		function getVertexByIndex( index, vertex ) {

			const stride = index * 3;

			vertex.x = vertices[ stride + 0 ];
			vertex.y = vertices[ stride + 1 ];
			vertex.z = vertices[ stride + 2 ];

		}

		function correctUVs() {

			const a = new Vector3();
			const b = new Vector3();
			const c = new Vector3();

			const centroid = new Vector3();

			const uvA = new Vector2();
			const uvB = new Vector2();
			const uvC = new Vector2();

			for ( let i = 0, j = 0; i < vertexBuffer.length; i += 9, j += 6 ) {

				a.set( vertexBuffer[ i + 0 ], vertexBuffer[ i + 1 ], vertexBuffer[ i + 2 ] );
				b.set( vertexBuffer[ i + 3 ], vertexBuffer[ i + 4 ], vertexBuffer[ i + 5 ] );
				c.set( vertexBuffer[ i + 6 ], vertexBuffer[ i + 7 ], vertexBuffer[ i + 8 ] );

				uvA.set( uvBuffer[ j + 0 ], uvBuffer[ j + 1 ] );
				uvB.set( uvBuffer[ j + 2 ], uvBuffer[ j + 3 ] );
				uvC.set( uvBuffer[ j + 4 ], uvBuffer[ j + 5 ] );

				centroid.copy( a ).add( b ).add( c ).divideScalar( 3 );

				const azi = azimuth( centroid );

				correctUV( uvA, j + 0, a, azi );
				correctUV( uvB, j + 2, b, azi );
				correctUV( uvC, j + 4, c, azi );

			}

		}

		function correctUV( uv, stride, vector, azimuth ) {

			if ( ( azimuth < 0 ) && ( uv.x === 1 ) ) {

				uvBuffer[ stride ] = uv.x - 1;

			}

			if ( ( vector.x === 0 ) && ( vector.z === 0 ) ) {

				uvBuffer[ stride ] = azimuth / 2 / Math.PI + 0.5;

			}

		}

		// Angle around the Y axis, counter-clockwise when looking from above.

		function azimuth( vector ) {

			return Math.atan2( vector.z, - vector.x );

		}


		// Angle above the XZ plane.

		function inclination( vector ) {

			return Math.atan2( - vector.y, Math.sqrt( ( vector.x * vector.x ) + ( vector.z * vector.z ) ) );

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new PolyhedronGeometry( data.vertices, data.indices, data.radius, data.details );

	}

}

class DodecahedronGeometry extends PolyhedronGeometry {

	constructor( radius = 1, detail = 0 ) {

		const t = ( 1 + Math.sqrt( 5 ) ) / 2;
		const r = 1 / t;

		const vertices = [

			// (±1, ±1, ±1)
			- 1, - 1, - 1,	- 1, - 1, 1,
			- 1, 1, - 1, - 1, 1, 1,
			1, - 1, - 1, 1, - 1, 1,
			1, 1, - 1, 1, 1, 1,

			// (0, ±1/φ, ±φ)
			0, - r, - t, 0, - r, t,
			0, r, - t, 0, r, t,

			// (±1/φ, ±φ, 0)
			- r, - t, 0, - r, t, 0,
			r, - t, 0, r, t, 0,

			// (±φ, 0, ±1/φ)
			- t, 0, - r, t, 0, - r,
			- t, 0, r, t, 0, r
		];

		const indices = [
			3, 11, 7, 	3, 7, 15, 	3, 15, 13,
			7, 19, 17, 	7, 17, 6, 	7, 6, 15,
			17, 4, 8, 	17, 8, 10, 	17, 10, 6,
			8, 0, 16, 	8, 16, 2, 	8, 2, 10,
			0, 12, 1, 	0, 1, 18, 	0, 18, 16,
			6, 10, 2, 	6, 2, 13, 	6, 13, 15,
			2, 16, 18, 	2, 18, 3, 	2, 3, 13,
			18, 1, 9, 	18, 9, 11, 	18, 11, 3,
			4, 14, 12, 	4, 12, 0, 	4, 0, 8,
			11, 9, 5, 	11, 5, 19, 	11, 19, 7,
			19, 5, 14, 	19, 14, 4, 	19, 4, 17,
			1, 12, 14, 	1, 14, 5, 	1, 5, 9
		];

		super( vertices, indices, radius, detail );

		this.type = 'DodecahedronGeometry';

		this.parameters = {
			radius: radius,
			detail: detail
		};

	}

	static fromJSON( data ) {

		return new DodecahedronGeometry( data.radius, data.detail );

	}

}

const _v0 = /*@__PURE__*/ new Vector3();
const _v1$1 = /*@__PURE__*/ new Vector3();
const _normal = /*@__PURE__*/ new Vector3();
const _triangle = /*@__PURE__*/ new Triangle();

class EdgesGeometry extends BufferGeometry {

	constructor( geometry = null, thresholdAngle = 1 ) {

		super();

		this.type = 'EdgesGeometry';

		this.parameters = {
			geometry: geometry,
			thresholdAngle: thresholdAngle
		};

		if ( geometry !== null ) {

			const precisionPoints = 4;
			const precision = Math.pow( 10, precisionPoints );
			const thresholdDot = Math.cos( DEG2RAD * thresholdAngle );

			const indexAttr = geometry.getIndex();
			const positionAttr = geometry.getAttribute( 'position' );
			const indexCount = indexAttr ? indexAttr.count : positionAttr.count;

			const indexArr = [ 0, 0, 0 ];
			const vertKeys = [ 'a', 'b', 'c' ];
			const hashes = new Array( 3 );

			const edgeData = {};
			const vertices = [];
			for ( let i = 0; i < indexCount; i += 3 ) {

				if ( indexAttr ) {

					indexArr[ 0 ] = indexAttr.getX( i );
					indexArr[ 1 ] = indexAttr.getX( i + 1 );
					indexArr[ 2 ] = indexAttr.getX( i + 2 );

				} else {

					indexArr[ 0 ] = i;
					indexArr[ 1 ] = i + 1;
					indexArr[ 2 ] = i + 2;

				}

				const { a, b, c } = _triangle;
				a.fromBufferAttribute( positionAttr, indexArr[ 0 ] );
				b.fromBufferAttribute( positionAttr, indexArr[ 1 ] );
				c.fromBufferAttribute( positionAttr, indexArr[ 2 ] );
				_triangle.getNormal( _normal );

				// create hashes for the edge from the vertices
				hashes[ 0 ] = `${ Math.round( a.x * precision ) },${ Math.round( a.y * precision ) },${ Math.round( a.z * precision ) }`;
				hashes[ 1 ] = `${ Math.round( b.x * precision ) },${ Math.round( b.y * precision ) },${ Math.round( b.z * precision ) }`;
				hashes[ 2 ] = `${ Math.round( c.x * precision ) },${ Math.round( c.y * precision ) },${ Math.round( c.z * precision ) }`;

				// skip degenerate triangles
				if ( hashes[ 0 ] === hashes[ 1 ] || hashes[ 1 ] === hashes[ 2 ] || hashes[ 2 ] === hashes[ 0 ] ) {

					continue;

				}

				// iterate over every edge
				for ( let j = 0; j < 3; j ++ ) {

					// get the first and next vertex making up the edge
					const jNext = ( j + 1 ) % 3;
					const vecHash0 = hashes[ j ];
					const vecHash1 = hashes[ jNext ];
					const v0 = _triangle[ vertKeys[ j ] ];
					const v1 = _triangle[ vertKeys[ jNext ] ];

					const hash = `${ vecHash0 }_${ vecHash1 }`;
					const reverseHash = `${ vecHash1 }_${ vecHash0 }`;

					if ( reverseHash in edgeData && edgeData[ reverseHash ] ) {

						// if we found a sibling edge add it into the vertex array if
						// it meets the angle threshold and delete the edge from the map.
						if ( _normal.dot( edgeData[ reverseHash ].normal ) <= thresholdDot ) {

							vertices.push( v0.x, v0.y, v0.z );
							vertices.push( v1.x, v1.y, v1.z );

						}

						edgeData[ reverseHash ] = null;

					} else if ( ! ( hash in edgeData ) ) {

						// if we've already got an edge here then skip adding a new one
						edgeData[ hash ] = {

							index0: indexArr[ j ],
							index1: indexArr[ jNext ],
							normal: _normal.clone(),

						};

					}

				}

			}

			// iterate over all remaining, unmatched edges and add them to the vertex array
			for ( const key in edgeData ) {

				if ( edgeData[ key ] ) {

					const { index0, index1 } = edgeData[ key ];
					_v0.fromBufferAttribute( positionAttr, index0 );
					_v1$1.fromBufferAttribute( positionAttr, index1 );

					vertices.push( _v0.x, _v0.y, _v0.z );
					vertices.push( _v1$1.x, _v1$1.y, _v1$1.z );

				}

			}

			this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

}

class Shape extends Path {

	constructor( points ) {

		super( points );

		this.uuid = generateUUID();

		this.type = 'Shape';

		this.holes = [];

	}

	getPointsHoles( divisions ) {

		const holesPts = [];

		for ( let i = 0, l = this.holes.length; i < l; i ++ ) {

			holesPts[ i ] = this.holes[ i ].getPoints( divisions );

		}

		return holesPts;

	}

	// get points of shape and holes (keypoints based on segments parameter)

	extractPoints( divisions ) {

		return {

			shape: this.getPoints( divisions ),
			holes: this.getPointsHoles( divisions )

		};

	}

	copy( source ) {

		super.copy( source );

		this.holes = [];

		for ( let i = 0, l = source.holes.length; i < l; i ++ ) {

			const hole = source.holes[ i ];

			this.holes.push( hole.clone() );

		}

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.uuid = this.uuid;
		data.holes = [];

		for ( let i = 0, l = this.holes.length; i < l; i ++ ) {

			const hole = this.holes[ i ];
			data.holes.push( hole.toJSON() );

		}

		return data;

	}

	fromJSON( json ) {

		super.fromJSON( json );

		this.uuid = json.uuid;
		this.holes = [];

		for ( let i = 0, l = json.holes.length; i < l; i ++ ) {

			const hole = json.holes[ i ];
			this.holes.push( new Path().fromJSON( hole ) );

		}

		return this;

	}

}

/**
 * Port from https://github.com/mapbox/earcut (v2.2.4)
 */

const Earcut = {

	triangulate: function ( data, holeIndices, dim = 2 ) {

		const hasHoles = holeIndices && holeIndices.length;
		const outerLen = hasHoles ? holeIndices[ 0 ] * dim : data.length;
		let outerNode = linkedList( data, 0, outerLen, dim, true );
		const triangles = [];

		if ( ! outerNode || outerNode.next === outerNode.prev ) return triangles;

		let minX, minY, maxX, maxY, x, y, invSize;

		if ( hasHoles ) outerNode = eliminateHoles( data, holeIndices, outerNode, dim );

		// if the shape is not too simple, we'll use z-order curve hash later; calculate polygon bbox
		if ( data.length > 80 * dim ) {

			minX = maxX = data[ 0 ];
			minY = maxY = data[ 1 ];

			for ( let i = dim; i < outerLen; i += dim ) {

				x = data[ i ];
				y = data[ i + 1 ];
				if ( x < minX ) minX = x;
				if ( y < minY ) minY = y;
				if ( x > maxX ) maxX = x;
				if ( y > maxY ) maxY = y;

			}

			// minX, minY and invSize are later used to transform coords into integers for z-order calculation
			invSize = Math.max( maxX - minX, maxY - minY );
			invSize = invSize !== 0 ? 32767 / invSize : 0;

		}

		earcutLinked( outerNode, triangles, dim, minX, minY, invSize, 0 );

		return triangles;

	}

};

// create a circular doubly linked list from polygon points in the specified winding order
function linkedList( data, start, end, dim, clockwise ) {

	let i, last;

	if ( clockwise === ( signedArea( data, start, end, dim ) > 0 ) ) {

		for ( i = start; i < end; i += dim ) last = insertNode( i, data[ i ], data[ i + 1 ], last );

	} else {

		for ( i = end - dim; i >= start; i -= dim ) last = insertNode( i, data[ i ], data[ i + 1 ], last );

	}

	if ( last && equals( last, last.next ) ) {

		removeNode( last );
		last = last.next;

	}

	return last;

}

// eliminate colinear or duplicate points
function filterPoints( start, end ) {

	if ( ! start ) return start;
	if ( ! end ) end = start;

	let p = start,
		again;
	do {

		again = false;

		if ( ! p.steiner && ( equals( p, p.next ) || area( p.prev, p, p.next ) === 0 ) ) {

			removeNode( p );
			p = end = p.prev;
			if ( p === p.next ) break;
			again = true;

		} else {

			p = p.next;

		}

	} while ( again || p !== end );

	return end;

}

// main ear slicing loop which triangulates a polygon (given as a linked list)
function earcutLinked( ear, triangles, dim, minX, minY, invSize, pass ) {

	if ( ! ear ) return;

	// interlink polygon nodes in z-order
	if ( ! pass && invSize ) indexCurve( ear, minX, minY, invSize );

	let stop = ear,
		prev, next;

	// iterate through ears, slicing them one by one
	while ( ear.prev !== ear.next ) {

		prev = ear.prev;
		next = ear.next;

		if ( invSize ? isEarHashed( ear, minX, minY, invSize ) : isEar( ear ) ) {

			// cut off the triangle
			triangles.push( prev.i / dim | 0 );
			triangles.push( ear.i / dim | 0 );
			triangles.push( next.i / dim | 0 );

			removeNode( ear );

			// skipping the next vertex leads to less sliver triangles
			ear = next.next;
			stop = next.next;

			continue;

		}

		ear = next;

		// if we looped through the whole remaining polygon and can't find any more ears
		if ( ear === stop ) {

			// try filtering points and slicing again
			if ( ! pass ) {

				earcutLinked( filterPoints( ear ), triangles, dim, minX, minY, invSize, 1 );

				// if this didn't work, try curing all small self-intersections locally

			} else if ( pass === 1 ) {

				ear = cureLocalIntersections( filterPoints( ear ), triangles, dim );
				earcutLinked( ear, triangles, dim, minX, minY, invSize, 2 );

				// as a last resort, try splitting the remaining polygon into two

			} else if ( pass === 2 ) {

				splitEarcut( ear, triangles, dim, minX, minY, invSize );

			}

			break;

		}

	}

}

// check whether a polygon node forms a valid ear with adjacent nodes
function isEar( ear ) {

	const a = ear.prev,
		b = ear,
		c = ear.next;

	if ( area( a, b, c ) >= 0 ) return false; // reflex, can't be an ear

	// now make sure we don't have other points inside the potential ear
	const ax = a.x, bx = b.x, cx = c.x, ay = a.y, by = b.y, cy = c.y;

	// triangle bbox; min & max are calculated like this for speed
	const x0 = ax < bx ? ( ax < cx ? ax : cx ) : ( bx < cx ? bx : cx ),
		y0 = ay < by ? ( ay < cy ? ay : cy ) : ( by < cy ? by : cy ),
		x1 = ax > bx ? ( ax > cx ? ax : cx ) : ( bx > cx ? bx : cx ),
		y1 = ay > by ? ( ay > cy ? ay : cy ) : ( by > cy ? by : cy );

	let p = c.next;
	while ( p !== a ) {

		if ( p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1 &&
			pointInTriangle( ax, ay, bx, by, cx, cy, p.x, p.y ) &&
			area( p.prev, p, p.next ) >= 0 ) return false;
		p = p.next;

	}

	return true;

}

function isEarHashed( ear, minX, minY, invSize ) {

	const a = ear.prev,
		b = ear,
		c = ear.next;

	if ( area( a, b, c ) >= 0 ) return false; // reflex, can't be an ear

	const ax = a.x, bx = b.x, cx = c.x, ay = a.y, by = b.y, cy = c.y;

	// triangle bbox; min & max are calculated like this for speed
	const x0 = ax < bx ? ( ax < cx ? ax : cx ) : ( bx < cx ? bx : cx ),
		y0 = ay < by ? ( ay < cy ? ay : cy ) : ( by < cy ? by : cy ),
		x1 = ax > bx ? ( ax > cx ? ax : cx ) : ( bx > cx ? bx : cx ),
		y1 = ay > by ? ( ay > cy ? ay : cy ) : ( by > cy ? by : cy );

	// z-order range for the current triangle bbox;
	const minZ = zOrder( x0, y0, minX, minY, invSize ),
		maxZ = zOrder( x1, y1, minX, minY, invSize );

	let p = ear.prevZ,
		n = ear.nextZ;

	// look for points inside the triangle in both directions
	while ( p && p.z >= minZ && n && n.z <= maxZ ) {

		if ( p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1 && p !== a && p !== c &&
			pointInTriangle( ax, ay, bx, by, cx, cy, p.x, p.y ) && area( p.prev, p, p.next ) >= 0 ) return false;
		p = p.prevZ;

		if ( n.x >= x0 && n.x <= x1 && n.y >= y0 && n.y <= y1 && n !== a && n !== c &&
			pointInTriangle( ax, ay, bx, by, cx, cy, n.x, n.y ) && area( n.prev, n, n.next ) >= 0 ) return false;
		n = n.nextZ;

	}

	// look for remaining points in decreasing z-order
	while ( p && p.z >= minZ ) {

		if ( p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1 && p !== a && p !== c &&
			pointInTriangle( ax, ay, bx, by, cx, cy, p.x, p.y ) && area( p.prev, p, p.next ) >= 0 ) return false;
		p = p.prevZ;

	}

	// look for remaining points in increasing z-order
	while ( n && n.z <= maxZ ) {

		if ( n.x >= x0 && n.x <= x1 && n.y >= y0 && n.y <= y1 && n !== a && n !== c &&
			pointInTriangle( ax, ay, bx, by, cx, cy, n.x, n.y ) && area( n.prev, n, n.next ) >= 0 ) return false;
		n = n.nextZ;

	}

	return true;

}

// go through all polygon nodes and cure small local self-intersections
function cureLocalIntersections( start, triangles, dim ) {

	let p = start;
	do {

		const a = p.prev,
			b = p.next.next;

		if ( ! equals( a, b ) && intersects( a, p, p.next, b ) && locallyInside( a, b ) && locallyInside( b, a ) ) {

			triangles.push( a.i / dim | 0 );
			triangles.push( p.i / dim | 0 );
			triangles.push( b.i / dim | 0 );

			// remove two nodes involved
			removeNode( p );
			removeNode( p.next );

			p = start = b;

		}

		p = p.next;

	} while ( p !== start );

	return filterPoints( p );

}

// try splitting polygon into two and triangulate them independently
function splitEarcut( start, triangles, dim, minX, minY, invSize ) {

	// look for a valid diagonal that divides the polygon into two
	let a = start;
	do {

		let b = a.next.next;
		while ( b !== a.prev ) {

			if ( a.i !== b.i && isValidDiagonal( a, b ) ) {

				// split the polygon in two by the diagonal
				let c = splitPolygon( a, b );

				// filter colinear points around the cuts
				a = filterPoints( a, a.next );
				c = filterPoints( c, c.next );

				// run earcut on each half
				earcutLinked( a, triangles, dim, minX, minY, invSize, 0 );
				earcutLinked( c, triangles, dim, minX, minY, invSize, 0 );
				return;

			}

			b = b.next;

		}

		a = a.next;

	} while ( a !== start );

}

// link every hole into the outer loop, producing a single-ring polygon without holes
function eliminateHoles( data, holeIndices, outerNode, dim ) {

	const queue = [];
	let i, len, start, end, list;

	for ( i = 0, len = holeIndices.length; i < len; i ++ ) {

		start = holeIndices[ i ] * dim;
		end = i < len - 1 ? holeIndices[ i + 1 ] * dim : data.length;
		list = linkedList( data, start, end, dim, false );
		if ( list === list.next ) list.steiner = true;
		queue.push( getLeftmost( list ) );

	}

	queue.sort( compareX );

	// process holes from left to right
	for ( i = 0; i < queue.length; i ++ ) {

		outerNode = eliminateHole( queue[ i ], outerNode );

	}

	return outerNode;

}

function compareX( a, b ) {

	return a.x - b.x;

}

// find a bridge between vertices that connects hole with an outer ring and link it
function eliminateHole( hole, outerNode ) {

	const bridge = findHoleBridge( hole, outerNode );
	if ( ! bridge ) {

		return outerNode;

	}

	const bridgeReverse = splitPolygon( bridge, hole );

	// filter collinear points around the cuts
	filterPoints( bridgeReverse, bridgeReverse.next );
	return filterPoints( bridge, bridge.next );

}

// David Eberly's algorithm for finding a bridge between hole and outer polygon
function findHoleBridge( hole, outerNode ) {

	let p = outerNode,
		qx = - Infinity,
		m;

	const hx = hole.x, hy = hole.y;

	// find a segment intersected by a ray from the hole's leftmost point to the left;
	// segment's endpoint with lesser x will be potential connection point
	do {

		if ( hy <= p.y && hy >= p.next.y && p.next.y !== p.y ) {

			const x = p.x + ( hy - p.y ) * ( p.next.x - p.x ) / ( p.next.y - p.y );
			if ( x <= hx && x > qx ) {

				qx = x;
				m = p.x < p.next.x ? p : p.next;
				if ( x === hx ) return m; // hole touches outer segment; pick leftmost endpoint

			}

		}

		p = p.next;

	} while ( p !== outerNode );

	if ( ! m ) return null;

	// look for points inside the triangle of hole point, segment intersection and endpoint;
	// if there are no points found, we have a valid connection;
	// otherwise choose the point of the minimum angle with the ray as connection point

	const stop = m,
		mx = m.x,
		my = m.y;
	let tanMin = Infinity, tan;

	p = m;

	do {

		if ( hx >= p.x && p.x >= mx && hx !== p.x &&
				pointInTriangle( hy < my ? hx : qx, hy, mx, my, hy < my ? qx : hx, hy, p.x, p.y ) ) {

			tan = Math.abs( hy - p.y ) / ( hx - p.x ); // tangential

			if ( locallyInside( p, hole ) && ( tan < tanMin || ( tan === tanMin && ( p.x > m.x || ( p.x === m.x && sectorContainsSector( m, p ) ) ) ) ) ) {

				m = p;
				tanMin = tan;

			}

		}

		p = p.next;

	} while ( p !== stop );

	return m;

}

// whether sector in vertex m contains sector in vertex p in the same coordinates
function sectorContainsSector( m, p ) {

	return area( m.prev, m, p.prev ) < 0 && area( p.next, m, m.next ) < 0;

}

// interlink polygon nodes in z-order
function indexCurve( start, minX, minY, invSize ) {

	let p = start;
	do {

		if ( p.z === 0 ) p.z = zOrder( p.x, p.y, minX, minY, invSize );
		p.prevZ = p.prev;
		p.nextZ = p.next;
		p = p.next;

	} while ( p !== start );

	p.prevZ.nextZ = null;
	p.prevZ = null;

	sortLinked( p );

}

// Simon Tatham's linked list merge sort algorithm
// http://www.chiark.greenend.org.uk/~sgtatham/algorithms/listsort.html
function sortLinked( list ) {

	let i, p, q, e, tail, numMerges, pSize, qSize,
		inSize = 1;

	do {

		p = list;
		list = null;
		tail = null;
		numMerges = 0;

		while ( p ) {

			numMerges ++;
			q = p;
			pSize = 0;
			for ( i = 0; i < inSize; i ++ ) {

				pSize ++;
				q = q.nextZ;
				if ( ! q ) break;

			}

			qSize = inSize;

			while ( pSize > 0 || ( qSize > 0 && q ) ) {

				if ( pSize !== 0 && ( qSize === 0 || ! q || p.z <= q.z ) ) {

					e = p;
					p = p.nextZ;
					pSize --;

				} else {

					e = q;
					q = q.nextZ;
					qSize --;

				}

				if ( tail ) tail.nextZ = e;
				else list = e;

				e.prevZ = tail;
				tail = e;

			}

			p = q;

		}

		tail.nextZ = null;
		inSize *= 2;

	} while ( numMerges > 1 );

	return list;

}

// z-order of a point given coords and inverse of the longer side of data bbox
function zOrder( x, y, minX, minY, invSize ) {

	// coords are transformed into non-negative 15-bit integer range
	x = ( x - minX ) * invSize | 0;
	y = ( y - minY ) * invSize | 0;

	x = ( x | ( x << 8 ) ) & 0x00FF00FF;
	x = ( x | ( x << 4 ) ) & 0x0F0F0F0F;
	x = ( x | ( x << 2 ) ) & 0x33333333;
	x = ( x | ( x << 1 ) ) & 0x55555555;

	y = ( y | ( y << 8 ) ) & 0x00FF00FF;
	y = ( y | ( y << 4 ) ) & 0x0F0F0F0F;
	y = ( y | ( y << 2 ) ) & 0x33333333;
	y = ( y | ( y << 1 ) ) & 0x55555555;

	return x | ( y << 1 );

}

// find the leftmost node of a polygon ring
function getLeftmost( start ) {

	let p = start,
		leftmost = start;
	do {

		if ( p.x < leftmost.x || ( p.x === leftmost.x && p.y < leftmost.y ) ) leftmost = p;
		p = p.next;

	} while ( p !== start );

	return leftmost;

}

// check if a point lies within a convex triangle
function pointInTriangle( ax, ay, bx, by, cx, cy, px, py ) {

	return ( cx - px ) * ( ay - py ) >= ( ax - px ) * ( cy - py ) &&
           ( ax - px ) * ( by - py ) >= ( bx - px ) * ( ay - py ) &&
           ( bx - px ) * ( cy - py ) >= ( cx - px ) * ( by - py );

}

// check if a diagonal between two polygon nodes is valid (lies in polygon interior)
function isValidDiagonal( a, b ) {

	return a.next.i !== b.i && a.prev.i !== b.i && ! intersectsPolygon( a, b ) && // dones't intersect other edges
           ( locallyInside( a, b ) && locallyInside( b, a ) && middleInside( a, b ) && // locally visible
            ( area( a.prev, a, b.prev ) || area( a, b.prev, b ) ) || // does not create opposite-facing sectors
            equals( a, b ) && area( a.prev, a, a.next ) > 0 && area( b.prev, b, b.next ) > 0 ); // special zero-length case

}

// signed area of a triangle
function area( p, q, r ) {

	return ( q.y - p.y ) * ( r.x - q.x ) - ( q.x - p.x ) * ( r.y - q.y );

}

// check if two points are equal
function equals( p1, p2 ) {

	return p1.x === p2.x && p1.y === p2.y;

}

// check if two segments intersect
function intersects( p1, q1, p2, q2 ) {

	const o1 = sign( area( p1, q1, p2 ) );
	const o2 = sign( area( p1, q1, q2 ) );
	const o3 = sign( area( p2, q2, p1 ) );
	const o4 = sign( area( p2, q2, q1 ) );

	if ( o1 !== o2 && o3 !== o4 ) return true; // general case

	if ( o1 === 0 && onSegment( p1, p2, q1 ) ) return true; // p1, q1 and p2 are collinear and p2 lies on p1q1
	if ( o2 === 0 && onSegment( p1, q2, q1 ) ) return true; // p1, q1 and q2 are collinear and q2 lies on p1q1
	if ( o3 === 0 && onSegment( p2, p1, q2 ) ) return true; // p2, q2 and p1 are collinear and p1 lies on p2q2
	if ( o4 === 0 && onSegment( p2, q1, q2 ) ) return true; // p2, q2 and q1 are collinear and q1 lies on p2q2

	return false;

}

// for collinear points p, q, r, check if point q lies on segment pr
function onSegment( p, q, r ) {

	return q.x <= Math.max( p.x, r.x ) && q.x >= Math.min( p.x, r.x ) && q.y <= Math.max( p.y, r.y ) && q.y >= Math.min( p.y, r.y );

}

function sign( num ) {

	return num > 0 ? 1 : num < 0 ? - 1 : 0;

}

// check if a polygon diagonal intersects any polygon segments
function intersectsPolygon( a, b ) {

	let p = a;
	do {

		if ( p.i !== a.i && p.next.i !== a.i && p.i !== b.i && p.next.i !== b.i &&
			intersects( p, p.next, a, b ) ) return true;
		p = p.next;

	} while ( p !== a );

	return false;

}

// check if a polygon diagonal is locally inside the polygon
function locallyInside( a, b ) {

	return area( a.prev, a, a.next ) < 0 ?
		area( a, b, a.next ) >= 0 && area( a, a.prev, b ) >= 0 :
		area( a, b, a.prev ) < 0 || area( a, a.next, b ) < 0;

}

// check if the middle point of a polygon diagonal is inside the polygon
function middleInside( a, b ) {

	let p = a,
		inside = false;
	const px = ( a.x + b.x ) / 2,
		py = ( a.y + b.y ) / 2;
	do {

		if ( ( ( p.y > py ) !== ( p.next.y > py ) ) && p.next.y !== p.y &&
			( px < ( p.next.x - p.x ) * ( py - p.y ) / ( p.next.y - p.y ) + p.x ) )
			inside = ! inside;
		p = p.next;

	} while ( p !== a );

	return inside;

}

// link two polygon vertices with a bridge; if the vertices belong to the same ring, it splits polygon into two;
// if one belongs to the outer ring and another to a hole, it merges it into a single ring
function splitPolygon( a, b ) {

	const a2 = new Node( a.i, a.x, a.y ),
		b2 = new Node( b.i, b.x, b.y ),
		an = a.next,
		bp = b.prev;

	a.next = b;
	b.prev = a;

	a2.next = an;
	an.prev = a2;

	b2.next = a2;
	a2.prev = b2;

	bp.next = b2;
	b2.prev = bp;

	return b2;

}

// create a node and optionally link it with previous one (in a circular doubly linked list)
function insertNode( i, x, y, last ) {

	const p = new Node( i, x, y );

	if ( ! last ) {

		p.prev = p;
		p.next = p;

	} else {

		p.next = last.next;
		p.prev = last;
		last.next.prev = p;
		last.next = p;

	}

	return p;

}

function removeNode( p ) {

	p.next.prev = p.prev;
	p.prev.next = p.next;

	if ( p.prevZ ) p.prevZ.nextZ = p.nextZ;
	if ( p.nextZ ) p.nextZ.prevZ = p.prevZ;

}

function Node( i, x, y ) {

	// vertex index in coordinates array
	this.i = i;

	// vertex coordinates
	this.x = x;
	this.y = y;

	// previous and next vertex nodes in a polygon ring
	this.prev = null;
	this.next = null;

	// z-order curve value
	this.z = 0;

	// previous and next nodes in z-order
	this.prevZ = null;
	this.nextZ = null;

	// indicates whether this is a steiner point
	this.steiner = false;

}

function signedArea( data, start, end, dim ) {

	let sum = 0;
	for ( let i = start, j = end - dim; i < end; i += dim ) {

		sum += ( data[ j ] - data[ i ] ) * ( data[ i + 1 ] + data[ j + 1 ] );
		j = i;

	}

	return sum;

}

class ShapeUtils {

	// calculate area of the contour polygon

	static area( contour ) {

		const n = contour.length;
		let a = 0.0;

		for ( let p = n - 1, q = 0; q < n; p = q ++ ) {

			a += contour[ p ].x * contour[ q ].y - contour[ q ].x * contour[ p ].y;

		}

		return a * 0.5;

	}

	static isClockWise( pts ) {

		return ShapeUtils.area( pts ) < 0;

	}

	static triangulateShape( contour, holes ) {

		const vertices = []; // flat array of vertices like [ x0,y0, x1,y1, x2,y2, ... ]
		const holeIndices = []; // array of hole indices
		const faces = []; // final array of vertex indices like [ [ a,b,d ], [ b,c,d ] ]

		removeDupEndPts( contour );
		addContour( vertices, contour );

		//

		let holeIndex = contour.length;

		holes.forEach( removeDupEndPts );

		for ( let i = 0; i < holes.length; i ++ ) {

			holeIndices.push( holeIndex );
			holeIndex += holes[ i ].length;
			addContour( vertices, holes[ i ] );

		}

		//

		const triangles = Earcut.triangulate( vertices, holeIndices );

		//

		for ( let i = 0; i < triangles.length; i += 3 ) {

			faces.push( triangles.slice( i, i + 3 ) );

		}

		return faces;

	}

}

function removeDupEndPts( points ) {

	const l = points.length;

	if ( l > 2 && points[ l - 1 ].equals( points[ 0 ] ) ) {

		points.pop();

	}

}

function addContour( vertices, contour ) {

	for ( let i = 0; i < contour.length; i ++ ) {

		vertices.push( contour[ i ].x );
		vertices.push( contour[ i ].y );

	}

}

/**
 * Creates extruded geometry from a path shape.
 *
 * parameters = {
 *
 *  curveSegments: <int>, // number of points on the curves
 *  steps: <int>, // number of points for z-side extrusions / used for subdividing segments of extrude spline too
 *  depth: <float>, // Depth to extrude the shape
 *
 *  bevelEnabled: <bool>, // turn on bevel
 *  bevelThickness: <float>, // how deep into the original shape bevel goes
 *  bevelSize: <float>, // how far from shape outline (including bevelOffset) is bevel
 *  bevelOffset: <float>, // how far from shape outline does bevel start
 *  bevelSegments: <int>, // number of bevel layers
 *
 *  extrudePath: <THREE.Curve> // curve to extrude shape along
 *
 *  UVGenerator: <Object> // object that provides UV generator functions
 *
 * }
 */


class ExtrudeGeometry extends BufferGeometry {

	constructor( shapes = new Shape( [ new Vector2( 0.5, 0.5 ), new Vector2( - 0.5, 0.5 ), new Vector2( - 0.5, - 0.5 ), new Vector2( 0.5, - 0.5 ) ] ), options = {} ) {

		super();

		this.type = 'ExtrudeGeometry';

		this.parameters = {
			shapes: shapes,
			options: options
		};

		shapes = Array.isArray( shapes ) ? shapes : [ shapes ];

		const scope = this;

		const verticesArray = [];
		const uvArray = [];

		for ( let i = 0, l = shapes.length; i < l; i ++ ) {

			const shape = shapes[ i ];
			addShape( shape );

		}

		// build geometry

		this.setAttribute( 'position', new Float32BufferAttribute( verticesArray, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvArray, 2 ) );

		this.computeVertexNormals();

		// functions

		function addShape( shape ) {

			const placeholder = [];

			// options

			const curveSegments = options.curveSegments !== undefined ? options.curveSegments : 12;
			const steps = options.steps !== undefined ? options.steps : 1;
			const depth = options.depth !== undefined ? options.depth : 1;

			let bevelEnabled = options.bevelEnabled !== undefined ? options.bevelEnabled : true;
			let bevelThickness = options.bevelThickness !== undefined ? options.bevelThickness : 0.2;
			let bevelSize = options.bevelSize !== undefined ? options.bevelSize : bevelThickness - 0.1;
			let bevelOffset = options.bevelOffset !== undefined ? options.bevelOffset : 0;
			let bevelSegments = options.bevelSegments !== undefined ? options.bevelSegments : 3;

			const extrudePath = options.extrudePath;

			const uvgen = options.UVGenerator !== undefined ? options.UVGenerator : WorldUVGenerator;

			//

			let extrudePts, extrudeByPath = false;
			let splineTube, binormal, normal, position2;

			if ( extrudePath ) {

				extrudePts = extrudePath.getSpacedPoints( steps );

				extrudeByPath = true;
				bevelEnabled = false; // bevels not supported for path extrusion

				// SETUP TNB variables

				// TODO1 - have a .isClosed in spline?

				splineTube = extrudePath.computeFrenetFrames( steps, false );

				// console.log(splineTube, 'splineTube', splineTube.normals.length, 'steps', steps, 'extrudePts', extrudePts.length);

				binormal = new Vector3();
				normal = new Vector3();
				position2 = new Vector3();

			}

			// Safeguards if bevels are not enabled

			if ( ! bevelEnabled ) {

				bevelSegments = 0;
				bevelThickness = 0;
				bevelSize = 0;
				bevelOffset = 0;

			}

			// Variables initialization

			const shapePoints = shape.extractPoints( curveSegments );

			let vertices = shapePoints.shape;
			const holes = shapePoints.holes;

			const reverse = ! ShapeUtils.isClockWise( vertices );

			if ( reverse ) {

				vertices = vertices.reverse();

				// Maybe we should also check if holes are in the opposite direction, just to be safe ...

				for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

					const ahole = holes[ h ];

					if ( ShapeUtils.isClockWise( ahole ) ) {

						holes[ h ] = ahole.reverse();

					}

				}

			}


			const faces = ShapeUtils.triangulateShape( vertices, holes );

			/* Vertices */

			const contour = vertices; // vertices has all points but contour has only points of circumference

			for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

				const ahole = holes[ h ];

				vertices = vertices.concat( ahole );

			}


			function scalePt2( pt, vec, size ) {

				if ( ! vec ) console.error( 'THREE.ExtrudeGeometry: vec does not exist' );

				return pt.clone().addScaledVector( vec, size );

			}

			const vlen = vertices.length, flen = faces.length;


			// Find directions for point movement


			function getBevelVec( inPt, inPrev, inNext ) {

				// computes for inPt the corresponding point inPt' on a new contour
				//   shifted by 1 unit (length of normalized vector) to the left
				// if we walk along contour clockwise, this new contour is outside the old one
				//
				// inPt' is the intersection of the two lines parallel to the two
				//  adjacent edges of inPt at a distance of 1 unit on the left side.

				let v_trans_x, v_trans_y, shrink_by; // resulting translation vector for inPt

				// good reading for geometry algorithms (here: line-line intersection)
				// http://geomalgorithms.com/a05-_intersect-1.html

				const v_prev_x = inPt.x - inPrev.x,
					v_prev_y = inPt.y - inPrev.y;
				const v_next_x = inNext.x - inPt.x,
					v_next_y = inNext.y - inPt.y;

				const v_prev_lensq = ( v_prev_x * v_prev_x + v_prev_y * v_prev_y );

				// check for collinear edges
				const collinear0 = ( v_prev_x * v_next_y - v_prev_y * v_next_x );

				if ( Math.abs( collinear0 ) > Number.EPSILON ) {

					// not collinear

					// length of vectors for normalizing

					const v_prev_len = Math.sqrt( v_prev_lensq );
					const v_next_len = Math.sqrt( v_next_x * v_next_x + v_next_y * v_next_y );

					// shift adjacent points by unit vectors to the left

					const ptPrevShift_x = ( inPrev.x - v_prev_y / v_prev_len );
					const ptPrevShift_y = ( inPrev.y + v_prev_x / v_prev_len );

					const ptNextShift_x = ( inNext.x - v_next_y / v_next_len );
					const ptNextShift_y = ( inNext.y + v_next_x / v_next_len );

					// scaling factor for v_prev to intersection point

					const sf = ( ( ptNextShift_x - ptPrevShift_x ) * v_next_y -
							( ptNextShift_y - ptPrevShift_y ) * v_next_x ) /
						( v_prev_x * v_next_y - v_prev_y * v_next_x );

					// vector from inPt to intersection point

					v_trans_x = ( ptPrevShift_x + v_prev_x * sf - inPt.x );
					v_trans_y = ( ptPrevShift_y + v_prev_y * sf - inPt.y );

					// Don't normalize!, otherwise sharp corners become ugly
					//  but prevent crazy spikes
					const v_trans_lensq = ( v_trans_x * v_trans_x + v_trans_y * v_trans_y );
					if ( v_trans_lensq <= 2 ) {

						return new Vector2( v_trans_x, v_trans_y );

					} else {

						shrink_by = Math.sqrt( v_trans_lensq / 2 );

					}

				} else {

					// handle special case of collinear edges

					let direction_eq = false; // assumes: opposite

					if ( v_prev_x > Number.EPSILON ) {

						if ( v_next_x > Number.EPSILON ) {

							direction_eq = true;

						}

					} else {

						if ( v_prev_x < - Number.EPSILON ) {

							if ( v_next_x < - Number.EPSILON ) {

								direction_eq = true;

							}

						} else {

							if ( Math.sign( v_prev_y ) === Math.sign( v_next_y ) ) {

								direction_eq = true;

							}

						}

					}

					if ( direction_eq ) {

						// console.log("Warning: lines are a straight sequence");
						v_trans_x = - v_prev_y;
						v_trans_y = v_prev_x;
						shrink_by = Math.sqrt( v_prev_lensq );

					} else {

						// console.log("Warning: lines are a straight spike");
						v_trans_x = v_prev_x;
						v_trans_y = v_prev_y;
						shrink_by = Math.sqrt( v_prev_lensq / 2 );

					}

				}

				return new Vector2( v_trans_x / shrink_by, v_trans_y / shrink_by );

			}


			const contourMovements = [];

			for ( let i = 0, il = contour.length, j = il - 1, k = i + 1; i < il; i ++, j ++, k ++ ) {

				if ( j === il ) j = 0;
				if ( k === il ) k = 0;

				//  (j)---(i)---(k)
				// console.log('i,j,k', i, j , k)

				contourMovements[ i ] = getBevelVec( contour[ i ], contour[ j ], contour[ k ] );

			}

			const holesMovements = [];
			let oneHoleMovements, verticesMovements = contourMovements.concat();

			for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

				const ahole = holes[ h ];

				oneHoleMovements = [];

				for ( let i = 0, il = ahole.length, j = il - 1, k = i + 1; i < il; i ++, j ++, k ++ ) {

					if ( j === il ) j = 0;
					if ( k === il ) k = 0;

					//  (j)---(i)---(k)
					oneHoleMovements[ i ] = getBevelVec( ahole[ i ], ahole[ j ], ahole[ k ] );

				}

				holesMovements.push( oneHoleMovements );
				verticesMovements = verticesMovements.concat( oneHoleMovements );

			}


			// Loop bevelSegments, 1 for the front, 1 for the back

			for ( let b = 0; b < bevelSegments; b ++ ) {

				//for ( b = bevelSegments; b > 0; b -- ) {

				const t = b / bevelSegments;
				const z = bevelThickness * Math.cos( t * Math.PI / 2 );
				const bs = bevelSize * Math.sin( t * Math.PI / 2 ) + bevelOffset;

				// contract shape

				for ( let i = 0, il = contour.length; i < il; i ++ ) {

					const vert = scalePt2( contour[ i ], contourMovements[ i ], bs );

					v( vert.x, vert.y, - z );

				}

				// expand holes

				for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

					const ahole = holes[ h ];
					oneHoleMovements = holesMovements[ h ];

					for ( let i = 0, il = ahole.length; i < il; i ++ ) {

						const vert = scalePt2( ahole[ i ], oneHoleMovements[ i ], bs );

						v( vert.x, vert.y, - z );

					}

				}

			}

			const bs = bevelSize + bevelOffset;

			// Back facing vertices

			for ( let i = 0; i < vlen; i ++ ) {

				const vert = bevelEnabled ? scalePt2( vertices[ i ], verticesMovements[ i ], bs ) : vertices[ i ];

				if ( ! extrudeByPath ) {

					v( vert.x, vert.y, 0 );

				} else {

					// v( vert.x, vert.y + extrudePts[ 0 ].y, extrudePts[ 0 ].x );

					normal.copy( splineTube.normals[ 0 ] ).multiplyScalar( vert.x );
					binormal.copy( splineTube.binormals[ 0 ] ).multiplyScalar( vert.y );

					position2.copy( extrudePts[ 0 ] ).add( normal ).add( binormal );

					v( position2.x, position2.y, position2.z );

				}

			}

			// Add stepped vertices...
			// Including front facing vertices

			for ( let s = 1; s <= steps; s ++ ) {

				for ( let i = 0; i < vlen; i ++ ) {

					const vert = bevelEnabled ? scalePt2( vertices[ i ], verticesMovements[ i ], bs ) : vertices[ i ];

					if ( ! extrudeByPath ) {

						v( vert.x, vert.y, depth / steps * s );

					} else {

						// v( vert.x, vert.y + extrudePts[ s - 1 ].y, extrudePts[ s - 1 ].x );

						normal.copy( splineTube.normals[ s ] ).multiplyScalar( vert.x );
						binormal.copy( splineTube.binormals[ s ] ).multiplyScalar( vert.y );

						position2.copy( extrudePts[ s ] ).add( normal ).add( binormal );

						v( position2.x, position2.y, position2.z );

					}

				}

			}


			// Add bevel segments planes

			//for ( b = 1; b <= bevelSegments; b ++ ) {
			for ( let b = bevelSegments - 1; b >= 0; b -- ) {

				const t = b / bevelSegments;
				const z = bevelThickness * Math.cos( t * Math.PI / 2 );
				const bs = bevelSize * Math.sin( t * Math.PI / 2 ) + bevelOffset;

				// contract shape

				for ( let i = 0, il = contour.length; i < il; i ++ ) {

					const vert = scalePt2( contour[ i ], contourMovements[ i ], bs );
					v( vert.x, vert.y, depth + z );

				}

				// expand holes

				for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

					const ahole = holes[ h ];
					oneHoleMovements = holesMovements[ h ];

					for ( let i = 0, il = ahole.length; i < il; i ++ ) {

						const vert = scalePt2( ahole[ i ], oneHoleMovements[ i ], bs );

						if ( ! extrudeByPath ) {

							v( vert.x, vert.y, depth + z );

						} else {

							v( vert.x, vert.y + extrudePts[ steps - 1 ].y, extrudePts[ steps - 1 ].x + z );

						}

					}

				}

			}

			/* Faces */

			// Top and bottom faces

			buildLidFaces();

			// Sides faces

			buildSideFaces();


			/////  Internal functions

			function buildLidFaces() {

				const start = verticesArray.length / 3;

				if ( bevelEnabled ) {

					let layer = 0; // steps + 1
					let offset = vlen * layer;

					// Bottom faces

					for ( let i = 0; i < flen; i ++ ) {

						const face = faces[ i ];
						f3( face[ 2 ] + offset, face[ 1 ] + offset, face[ 0 ] + offset );

					}

					layer = steps + bevelSegments * 2;
					offset = vlen * layer;

					// Top faces

					for ( let i = 0; i < flen; i ++ ) {

						const face = faces[ i ];
						f3( face[ 0 ] + offset, face[ 1 ] + offset, face[ 2 ] + offset );

					}

				} else {

					// Bottom faces

					for ( let i = 0; i < flen; i ++ ) {

						const face = faces[ i ];
						f3( face[ 2 ], face[ 1 ], face[ 0 ] );

					}

					// Top faces

					for ( let i = 0; i < flen; i ++ ) {

						const face = faces[ i ];
						f3( face[ 0 ] + vlen * steps, face[ 1 ] + vlen * steps, face[ 2 ] + vlen * steps );

					}

				}

				scope.addGroup( start, verticesArray.length / 3 - start, 0 );

			}

			// Create faces for the z-sides of the shape

			function buildSideFaces() {

				const start = verticesArray.length / 3;
				let layeroffset = 0;
				sidewalls( contour, layeroffset );
				layeroffset += contour.length;

				for ( let h = 0, hl = holes.length; h < hl; h ++ ) {

					const ahole = holes[ h ];
					sidewalls( ahole, layeroffset );

					//, true
					layeroffset += ahole.length;

				}


				scope.addGroup( start, verticesArray.length / 3 - start, 1 );


			}

			function sidewalls( contour, layeroffset ) {

				let i = contour.length;

				while ( -- i >= 0 ) {

					const j = i;
					let k = i - 1;
					if ( k < 0 ) k = contour.length - 1;

					//console.log('b', i,j, i-1, k,vertices.length);

					for ( let s = 0, sl = ( steps + bevelSegments * 2 ); s < sl; s ++ ) {

						const slen1 = vlen * s;
						const slen2 = vlen * ( s + 1 );

						const a = layeroffset + j + slen1,
							b = layeroffset + k + slen1,
							c = layeroffset + k + slen2,
							d = layeroffset + j + slen2;

						f4( a, b, c, d );

					}

				}

			}

			function v( x, y, z ) {

				placeholder.push( x );
				placeholder.push( y );
				placeholder.push( z );

			}


			function f3( a, b, c ) {

				addVertex( a );
				addVertex( b );
				addVertex( c );

				const nextIndex = verticesArray.length / 3;
				const uvs = uvgen.generateTopUV( scope, verticesArray, nextIndex - 3, nextIndex - 2, nextIndex - 1 );

				addUV( uvs[ 0 ] );
				addUV( uvs[ 1 ] );
				addUV( uvs[ 2 ] );

			}

			function f4( a, b, c, d ) {

				addVertex( a );
				addVertex( b );
				addVertex( d );

				addVertex( b );
				addVertex( c );
				addVertex( d );


				const nextIndex = verticesArray.length / 3;
				const uvs = uvgen.generateSideWallUV( scope, verticesArray, nextIndex - 6, nextIndex - 3, nextIndex - 2, nextIndex - 1 );

				addUV( uvs[ 0 ] );
				addUV( uvs[ 1 ] );
				addUV( uvs[ 3 ] );

				addUV( uvs[ 1 ] );
				addUV( uvs[ 2 ] );
				addUV( uvs[ 3 ] );

			}

			function addVertex( index ) {

				verticesArray.push( placeholder[ index * 3 + 0 ] );
				verticesArray.push( placeholder[ index * 3 + 1 ] );
				verticesArray.push( placeholder[ index * 3 + 2 ] );

			}


			function addUV( vector2 ) {

				uvArray.push( vector2.x );
				uvArray.push( vector2.y );

			}

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		const shapes = this.parameters.shapes;
		const options = this.parameters.options;

		return toJSON$1( shapes, options, data );

	}

	static fromJSON( data, shapes ) {

		const geometryShapes = [];

		for ( let j = 0, jl = data.shapes.length; j < jl; j ++ ) {

			const shape = shapes[ data.shapes[ j ] ];

			geometryShapes.push( shape );

		}

		const extrudePath = data.options.extrudePath;

		if ( extrudePath !== undefined ) {

			data.options.extrudePath = new Curves[ extrudePath.type ]().fromJSON( extrudePath );

		}

		return new ExtrudeGeometry( geometryShapes, data.options );

	}

}

const WorldUVGenerator = {

	generateTopUV: function ( geometry, vertices, indexA, indexB, indexC ) {

		const a_x = vertices[ indexA * 3 ];
		const a_y = vertices[ indexA * 3 + 1 ];
		const b_x = vertices[ indexB * 3 ];
		const b_y = vertices[ indexB * 3 + 1 ];
		const c_x = vertices[ indexC * 3 ];
		const c_y = vertices[ indexC * 3 + 1 ];

		return [
			new Vector2( a_x, a_y ),
			new Vector2( b_x, b_y ),
			new Vector2( c_x, c_y )
		];

	},

	generateSideWallUV: function ( geometry, vertices, indexA, indexB, indexC, indexD ) {

		const a_x = vertices[ indexA * 3 ];
		const a_y = vertices[ indexA * 3 + 1 ];
		const a_z = vertices[ indexA * 3 + 2 ];
		const b_x = vertices[ indexB * 3 ];
		const b_y = vertices[ indexB * 3 + 1 ];
		const b_z = vertices[ indexB * 3 + 2 ];
		const c_x = vertices[ indexC * 3 ];
		const c_y = vertices[ indexC * 3 + 1 ];
		const c_z = vertices[ indexC * 3 + 2 ];
		const d_x = vertices[ indexD * 3 ];
		const d_y = vertices[ indexD * 3 + 1 ];
		const d_z = vertices[ indexD * 3 + 2 ];

		if ( Math.abs( a_y - b_y ) < Math.abs( a_x - b_x ) ) {

			return [
				new Vector2( a_x, 1 - a_z ),
				new Vector2( b_x, 1 - b_z ),
				new Vector2( c_x, 1 - c_z ),
				new Vector2( d_x, 1 - d_z )
			];

		} else {

			return [
				new Vector2( a_y, 1 - a_z ),
				new Vector2( b_y, 1 - b_z ),
				new Vector2( c_y, 1 - c_z ),
				new Vector2( d_y, 1 - d_z )
			];

		}

	}

};

function toJSON$1( shapes, options, data ) {

	data.shapes = [];

	if ( Array.isArray( shapes ) ) {

		for ( let i = 0, l = shapes.length; i < l; i ++ ) {

			const shape = shapes[ i ];

			data.shapes.push( shape.uuid );

		}

	} else {

		data.shapes.push( shapes.uuid );

	}

	data.options = Object.assign( {}, options );

	if ( options.extrudePath !== undefined ) data.options.extrudePath = options.extrudePath.toJSON();

	return data;

}

class IcosahedronGeometry extends PolyhedronGeometry {

	constructor( radius = 1, detail = 0 ) {

		const t = ( 1 + Math.sqrt( 5 ) ) / 2;

		const vertices = [
			- 1, t, 0, 	1, t, 0, 	- 1, - t, 0, 	1, - t, 0,
			0, - 1, t, 	0, 1, t,	0, - 1, - t, 	0, 1, - t,
			t, 0, - 1, 	t, 0, 1, 	- t, 0, - 1, 	- t, 0, 1
		];

		const indices = [
			0, 11, 5, 	0, 5, 1, 	0, 1, 7, 	0, 7, 10, 	0, 10, 11,
			1, 5, 9, 	5, 11, 4,	11, 10, 2,	10, 7, 6,	7, 1, 8,
			3, 9, 4, 	3, 4, 2,	3, 2, 6,	3, 6, 8,	3, 8, 9,
			4, 9, 5, 	2, 4, 11,	6, 2, 10,	8, 6, 7,	9, 8, 1
		];

		super( vertices, indices, radius, detail );

		this.type = 'IcosahedronGeometry';

		this.parameters = {
			radius: radius,
			detail: detail
		};

	}

	static fromJSON( data ) {

		return new IcosahedronGeometry( data.radius, data.detail );

	}

}

class OctahedronGeometry extends PolyhedronGeometry {

	constructor( radius = 1, detail = 0 ) {

		const vertices = [
			1, 0, 0, 	- 1, 0, 0,	0, 1, 0,
			0, - 1, 0, 	0, 0, 1,	0, 0, - 1
		];

		const indices = [
			0, 2, 4,	0, 4, 3,	0, 3, 5,
			0, 5, 2,	1, 2, 5,	1, 5, 3,
			1, 3, 4,	1, 4, 2
		];

		super( vertices, indices, radius, detail );

		this.type = 'OctahedronGeometry';

		this.parameters = {
			radius: radius,
			detail: detail
		};

	}

	static fromJSON( data ) {

		return new OctahedronGeometry( data.radius, data.detail );

	}

}

class RingGeometry extends BufferGeometry {

	constructor( innerRadius = 0.5, outerRadius = 1, thetaSegments = 32, phiSegments = 1, thetaStart = 0, thetaLength = Math.PI * 2 ) {

		super();

		this.type = 'RingGeometry';

		this.parameters = {
			innerRadius: innerRadius,
			outerRadius: outerRadius,
			thetaSegments: thetaSegments,
			phiSegments: phiSegments,
			thetaStart: thetaStart,
			thetaLength: thetaLength
		};

		thetaSegments = Math.max( 3, thetaSegments );
		phiSegments = Math.max( 1, phiSegments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// some helper variables

		let radius = innerRadius;
		const radiusStep = ( ( outerRadius - innerRadius ) / phiSegments );
		const vertex = new Vector3();
		const uv = new Vector2();

		// generate vertices, normals and uvs

		for ( let j = 0; j <= phiSegments; j ++ ) {

			for ( let i = 0; i <= thetaSegments; i ++ ) {

				// values are generate from the inside of the ring to the outside

				const segment = thetaStart + i / thetaSegments * thetaLength;

				// vertex

				vertex.x = radius * Math.cos( segment );
				vertex.y = radius * Math.sin( segment );

				vertices.push( vertex.x, vertex.y, vertex.z );

				// normal

				normals.push( 0, 0, 1 );

				// uv

				uv.x = ( vertex.x / outerRadius + 1 ) / 2;
				uv.y = ( vertex.y / outerRadius + 1 ) / 2;

				uvs.push( uv.x, uv.y );

			}

			// increase the radius for next row of vertices

			radius += radiusStep;

		}

		// indices

		for ( let j = 0; j < phiSegments; j ++ ) {

			const thetaSegmentLevel = j * ( thetaSegments + 1 );

			for ( let i = 0; i < thetaSegments; i ++ ) {

				const segment = i + thetaSegmentLevel;

				const a = segment;
				const b = segment + thetaSegments + 1;
				const c = segment + thetaSegments + 2;
				const d = segment + 1;

				// faces

				indices.push( a, b, d );
				indices.push( b, c, d );

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new RingGeometry( data.innerRadius, data.outerRadius, data.thetaSegments, data.phiSegments, data.thetaStart, data.thetaLength );

	}

}

class ShapeGeometry extends BufferGeometry {

	constructor( shapes = new Shape( [ new Vector2( 0, 0.5 ), new Vector2( - 0.5, - 0.5 ), new Vector2( 0.5, - 0.5 ) ] ), curveSegments = 12 ) {

		super();

		this.type = 'ShapeGeometry';

		this.parameters = {
			shapes: shapes,
			curveSegments: curveSegments
		};

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		let groupStart = 0;
		let groupCount = 0;

		// allow single and array values for "shapes" parameter

		if ( Array.isArray( shapes ) === false ) {

			addShape( shapes );

		} else {

			for ( let i = 0; i < shapes.length; i ++ ) {

				addShape( shapes[ i ] );

				this.addGroup( groupStart, groupCount, i ); // enables MultiMaterial support

				groupStart += groupCount;
				groupCount = 0;

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );


		// helper functions

		function addShape( shape ) {

			const indexOffset = vertices.length / 3;
			const points = shape.extractPoints( curveSegments );

			let shapeVertices = points.shape;
			const shapeHoles = points.holes;

			// check direction of vertices

			if ( ShapeUtils.isClockWise( shapeVertices ) === false ) {

				shapeVertices = shapeVertices.reverse();

			}

			for ( let i = 0, l = shapeHoles.length; i < l; i ++ ) {

				const shapeHole = shapeHoles[ i ];

				if ( ShapeUtils.isClockWise( shapeHole ) === true ) {

					shapeHoles[ i ] = shapeHole.reverse();

				}

			}

			const faces = ShapeUtils.triangulateShape( shapeVertices, shapeHoles );

			// join vertices of inner and outer paths to a single array

			for ( let i = 0, l = shapeHoles.length; i < l; i ++ ) {

				const shapeHole = shapeHoles[ i ];
				shapeVertices = shapeVertices.concat( shapeHole );

			}

			// vertices, normals, uvs

			for ( let i = 0, l = shapeVertices.length; i < l; i ++ ) {

				const vertex = shapeVertices[ i ];

				vertices.push( vertex.x, vertex.y, 0 );
				normals.push( 0, 0, 1 );
				uvs.push( vertex.x, vertex.y ); // world uvs

			}

			// indices

			for ( let i = 0, l = faces.length; i < l; i ++ ) {

				const face = faces[ i ];

				const a = face[ 0 ] + indexOffset;
				const b = face[ 1 ] + indexOffset;
				const c = face[ 2 ] + indexOffset;

				indices.push( a, b, c );
				groupCount += 3;

			}

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		const shapes = this.parameters.shapes;

		return toJSON( shapes, data );

	}

	static fromJSON( data, shapes ) {

		const geometryShapes = [];

		for ( let j = 0, jl = data.shapes.length; j < jl; j ++ ) {

			const shape = shapes[ data.shapes[ j ] ];

			geometryShapes.push( shape );

		}

		return new ShapeGeometry( geometryShapes, data.curveSegments );

	}

}

function toJSON( shapes, data ) {

	data.shapes = [];

	if ( Array.isArray( shapes ) ) {

		for ( let i = 0, l = shapes.length; i < l; i ++ ) {

			const shape = shapes[ i ];

			data.shapes.push( shape.uuid );

		}

	} else {

		data.shapes.push( shapes.uuid );

	}

	return data;

}

class SphereGeometry extends BufferGeometry {

	constructor( radius = 1, widthSegments = 32, heightSegments = 16, phiStart = 0, phiLength = Math.PI * 2, thetaStart = 0, thetaLength = Math.PI ) {

		super();

		this.type = 'SphereGeometry';

		this.parameters = {
			radius: radius,
			widthSegments: widthSegments,
			heightSegments: heightSegments,
			phiStart: phiStart,
			phiLength: phiLength,
			thetaStart: thetaStart,
			thetaLength: thetaLength
		};

		widthSegments = Math.max( 3, Math.floor( widthSegments ) );
		heightSegments = Math.max( 2, Math.floor( heightSegments ) );

		const thetaEnd = Math.min( thetaStart + thetaLength, Math.PI );

		let index = 0;
		const grid = [];

		const vertex = new Vector3();
		const normal = new Vector3();

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// generate vertices, normals and uvs

		for ( let iy = 0; iy <= heightSegments; iy ++ ) {

			const verticesRow = [];

			const v = iy / heightSegments;

			// special case for the poles

			let uOffset = 0;

			if ( iy === 0 && thetaStart === 0 ) {

				uOffset = 0.5 / widthSegments;

			} else if ( iy === heightSegments && thetaEnd === Math.PI ) {

				uOffset = - 0.5 / widthSegments;

			}

			for ( let ix = 0; ix <= widthSegments; ix ++ ) {

				const u = ix / widthSegments;

				// vertex

				vertex.x = - radius * Math.cos( phiStart + u * phiLength ) * Math.sin( thetaStart + v * thetaLength );
				vertex.y = radius * Math.cos( thetaStart + v * thetaLength );
				vertex.z = radius * Math.sin( phiStart + u * phiLength ) * Math.sin( thetaStart + v * thetaLength );

				vertices.push( vertex.x, vertex.y, vertex.z );

				// normal

				normal.copy( vertex ).normalize();
				normals.push( normal.x, normal.y, normal.z );

				// uv

				uvs.push( u + uOffset, 1 - v );

				verticesRow.push( index ++ );

			}

			grid.push( verticesRow );

		}

		// indices

		for ( let iy = 0; iy < heightSegments; iy ++ ) {

			for ( let ix = 0; ix < widthSegments; ix ++ ) {

				const a = grid[ iy ][ ix + 1 ];
				const b = grid[ iy ][ ix ];
				const c = grid[ iy + 1 ][ ix ];
				const d = grid[ iy + 1 ][ ix + 1 ];

				if ( iy !== 0 || thetaStart > 0 ) indices.push( a, b, d );
				if ( iy !== heightSegments - 1 || thetaEnd < Math.PI ) indices.push( b, c, d );

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new SphereGeometry( data.radius, data.widthSegments, data.heightSegments, data.phiStart, data.phiLength, data.thetaStart, data.thetaLength );

	}

}

class TetrahedronGeometry extends PolyhedronGeometry {

	constructor( radius = 1, detail = 0 ) {

		const vertices = [
			1, 1, 1, 	- 1, - 1, 1, 	- 1, 1, - 1, 	1, - 1, - 1
		];

		const indices = [
			2, 1, 0, 	0, 3, 2,	1, 3, 0,	2, 3, 1
		];

		super( vertices, indices, radius, detail );

		this.type = 'TetrahedronGeometry';

		this.parameters = {
			radius: radius,
			detail: detail
		};

	}

	static fromJSON( data ) {

		return new TetrahedronGeometry( data.radius, data.detail );

	}

}

class TorusGeometry extends BufferGeometry {

	constructor( radius = 1, tube = 0.4, radialSegments = 12, tubularSegments = 48, arc = Math.PI * 2 ) {

		super();

		this.type = 'TorusGeometry';

		this.parameters = {
			radius: radius,
			tube: tube,
			radialSegments: radialSegments,
			tubularSegments: tubularSegments,
			arc: arc
		};

		radialSegments = Math.floor( radialSegments );
		tubularSegments = Math.floor( tubularSegments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		const center = new Vector3();
		const vertex = new Vector3();
		const normal = new Vector3();

		// generate vertices, normals and uvs

		for ( let j = 0; j <= radialSegments; j ++ ) {

			for ( let i = 0; i <= tubularSegments; i ++ ) {

				const u = i / tubularSegments * arc;
				const v = j / radialSegments * Math.PI * 2;

				// vertex

				vertex.x = ( radius + tube * Math.cos( v ) ) * Math.cos( u );
				vertex.y = ( radius + tube * Math.cos( v ) ) * Math.sin( u );
				vertex.z = tube * Math.sin( v );

				vertices.push( vertex.x, vertex.y, vertex.z );

				// normal

				center.x = radius * Math.cos( u );
				center.y = radius * Math.sin( u );
				normal.subVectors( vertex, center ).normalize();

				normals.push( normal.x, normal.y, normal.z );

				// uv

				uvs.push( i / tubularSegments );
				uvs.push( j / radialSegments );

			}

		}

		// generate indices

		for ( let j = 1; j <= radialSegments; j ++ ) {

			for ( let i = 1; i <= tubularSegments; i ++ ) {

				// indices

				const a = ( tubularSegments + 1 ) * j + i - 1;
				const b = ( tubularSegments + 1 ) * ( j - 1 ) + i - 1;
				const c = ( tubularSegments + 1 ) * ( j - 1 ) + i;
				const d = ( tubularSegments + 1 ) * j + i;

				// faces

				indices.push( a, b, d );
				indices.push( b, c, d );

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new TorusGeometry( data.radius, data.tube, data.radialSegments, data.tubularSegments, data.arc );

	}

}

class TorusKnotGeometry extends BufferGeometry {

	constructor( radius = 1, tube = 0.4, tubularSegments = 64, radialSegments = 8, p = 2, q = 3 ) {

		super();

		this.type = 'TorusKnotGeometry';

		this.parameters = {
			radius: radius,
			tube: tube,
			tubularSegments: tubularSegments,
			radialSegments: radialSegments,
			p: p,
			q: q
		};

		tubularSegments = Math.floor( tubularSegments );
		radialSegments = Math.floor( radialSegments );

		// buffers

		const indices = [];
		const vertices = [];
		const normals = [];
		const uvs = [];

		// helper variables

		const vertex = new Vector3();
		const normal = new Vector3();

		const P1 = new Vector3();
		const P2 = new Vector3();

		const B = new Vector3();
		const T = new Vector3();
		const N = new Vector3();

		// generate vertices, normals and uvs

		for ( let i = 0; i <= tubularSegments; ++ i ) {

			// the radian "u" is used to calculate the position on the torus curve of the current tubular segment

			const u = i / tubularSegments * p * Math.PI * 2;

			// now we calculate two points. P1 is our current position on the curve, P2 is a little farther ahead.
			// these points are used to create a special "coordinate space", which is necessary to calculate the correct vertex positions

			calculatePositionOnCurve( u, p, q, radius, P1 );
			calculatePositionOnCurve( u + 0.01, p, q, radius, P2 );

			// calculate orthonormal basis

			T.subVectors( P2, P1 );
			N.addVectors( P2, P1 );
			B.crossVectors( T, N );
			N.crossVectors( B, T );

			// normalize B, N. T can be ignored, we don't use it

			B.normalize();
			N.normalize();

			for ( let j = 0; j <= radialSegments; ++ j ) {

				// now calculate the vertices. they are nothing more than an extrusion of the torus curve.
				// because we extrude a shape in the xy-plane, there is no need to calculate a z-value.

				const v = j / radialSegments * Math.PI * 2;
				const cx = - tube * Math.cos( v );
				const cy = tube * Math.sin( v );

				// now calculate the final vertex position.
				// first we orient the extrusion with our basis vectors, then we add it to the current position on the curve

				vertex.x = P1.x + ( cx * N.x + cy * B.x );
				vertex.y = P1.y + ( cx * N.y + cy * B.y );
				vertex.z = P1.z + ( cx * N.z + cy * B.z );

				vertices.push( vertex.x, vertex.y, vertex.z );

				// normal (P1 is always the center/origin of the extrusion, thus we can use it to calculate the normal)

				normal.subVectors( vertex, P1 ).normalize();

				normals.push( normal.x, normal.y, normal.z );

				// uv

				uvs.push( i / tubularSegments );
				uvs.push( j / radialSegments );

			}

		}

		// generate indices

		for ( let j = 1; j <= tubularSegments; j ++ ) {

			for ( let i = 1; i <= radialSegments; i ++ ) {

				// indices

				const a = ( radialSegments + 1 ) * ( j - 1 ) + ( i - 1 );
				const b = ( radialSegments + 1 ) * j + ( i - 1 );
				const c = ( radialSegments + 1 ) * j + i;
				const d = ( radialSegments + 1 ) * ( j - 1 ) + i;

				// faces

				indices.push( a, b, d );
				indices.push( b, c, d );

			}

		}

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

		// this function calculates the current position on the torus curve

		function calculatePositionOnCurve( u, p, q, radius, position ) {

			const cu = Math.cos( u );
			const su = Math.sin( u );
			const quOverP = q / p * u;
			const cs = Math.cos( quOverP );

			position.x = radius * ( 2 + cs ) * 0.5 * cu;
			position.y = radius * ( 2 + cs ) * su * 0.5;
			position.z = radius * Math.sin( quOverP ) * 0.5;

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	static fromJSON( data ) {

		return new TorusKnotGeometry( data.radius, data.tube, data.tubularSegments, data.radialSegments, data.p, data.q );

	}

}

class TubeGeometry extends BufferGeometry {

	constructor( path = new QuadraticBezierCurve3( new Vector3( - 1, - 1, 0 ), new Vector3( - 1, 1, 0 ), new Vector3( 1, 1, 0 ) ), tubularSegments = 64, radius = 1, radialSegments = 8, closed = false ) {

		super();

		this.type = 'TubeGeometry';

		this.parameters = {
			path: path,
			tubularSegments: tubularSegments,
			radius: radius,
			radialSegments: radialSegments,
			closed: closed
		};

		const frames = path.computeFrenetFrames( tubularSegments, closed );

		// expose internals

		this.tangents = frames.tangents;
		this.normals = frames.normals;
		this.binormals = frames.binormals;

		// helper variables

		const vertex = new Vector3();
		const normal = new Vector3();
		const uv = new Vector2();
		let P = new Vector3();

		// buffer

		const vertices = [];
		const normals = [];
		const uvs = [];
		const indices = [];

		// create buffer data

		generateBufferData();

		// build geometry

		this.setIndex( indices );
		this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		this.setAttribute( 'normal', new Float32BufferAttribute( normals, 3 ) );
		this.setAttribute( 'uv', new Float32BufferAttribute( uvs, 2 ) );

		// functions

		function generateBufferData() {

			for ( let i = 0; i < tubularSegments; i ++ ) {

				generateSegment( i );

			}

			// if the geometry is not closed, generate the last row of vertices and normals
			// at the regular position on the given path
			//
			// if the geometry is closed, duplicate the first row of vertices and normals (uvs will differ)

			generateSegment( ( closed === false ) ? tubularSegments : 0 );

			// uvs are generated in a separate function.
			// this makes it easy compute correct values for closed geometries

			generateUVs();

			// finally create faces

			generateIndices();

		}

		function generateSegment( i ) {

			// we use getPointAt to sample evenly distributed points from the given path

			P = path.getPointAt( i / tubularSegments, P );

			// retrieve corresponding normal and binormal

			const N = frames.normals[ i ];
			const B = frames.binormals[ i ];

			// generate normals and vertices for the current segment

			for ( let j = 0; j <= radialSegments; j ++ ) {

				const v = j / radialSegments * Math.PI * 2;

				const sin = Math.sin( v );
				const cos = - Math.cos( v );

				// normal

				normal.x = ( cos * N.x + sin * B.x );
				normal.y = ( cos * N.y + sin * B.y );
				normal.z = ( cos * N.z + sin * B.z );
				normal.normalize();

				normals.push( normal.x, normal.y, normal.z );

				// vertex

				vertex.x = P.x + radius * normal.x;
				vertex.y = P.y + radius * normal.y;
				vertex.z = P.z + radius * normal.z;

				vertices.push( vertex.x, vertex.y, vertex.z );

			}

		}

		function generateIndices() {

			for ( let j = 1; j <= tubularSegments; j ++ ) {

				for ( let i = 1; i <= radialSegments; i ++ ) {

					const a = ( radialSegments + 1 ) * ( j - 1 ) + ( i - 1 );
					const b = ( radialSegments + 1 ) * j + ( i - 1 );
					const c = ( radialSegments + 1 ) * j + i;
					const d = ( radialSegments + 1 ) * ( j - 1 ) + i;

					// faces

					indices.push( a, b, d );
					indices.push( b, c, d );

				}

			}

		}

		function generateUVs() {

			for ( let i = 0; i <= tubularSegments; i ++ ) {

				for ( let j = 0; j <= radialSegments; j ++ ) {

					uv.x = i / tubularSegments;
					uv.y = j / radialSegments;

					uvs.push( uv.x, uv.y );

				}

			}

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.path = this.parameters.path.toJSON();

		return data;

	}

	static fromJSON( data ) {

		// This only works for built-in curves (e.g. CatmullRomCurve3).
		// User defined curves or instances of CurvePath will not be deserialized.
		return new TubeGeometry(
			new Curves[ data.path.type ]().fromJSON( data.path ),
			data.tubularSegments,
			data.radius,
			data.radialSegments,
			data.closed
		);

	}

}

class WireframeGeometry extends BufferGeometry {

	constructor( geometry = null ) {

		super();

		this.type = 'WireframeGeometry';

		this.parameters = {
			geometry: geometry
		};

		if ( geometry !== null ) {

			// buffer

			const vertices = [];
			const edges = new Set();

			// helper variables

			const start = new Vector3();
			const end = new Vector3();

			if ( geometry.index !== null ) {

				// indexed BufferGeometry

				const position = geometry.attributes.position;
				const indices = geometry.index;
				let groups = geometry.groups;

				if ( groups.length === 0 ) {

					groups = [ { start: 0, count: indices.count, materialIndex: 0 } ];

				}

				// create a data structure that contains all edges without duplicates

				for ( let o = 0, ol = groups.length; o < ol; ++ o ) {

					const group = groups[ o ];

					const groupStart = group.start;
					const groupCount = group.count;

					for ( let i = groupStart, l = ( groupStart + groupCount ); i < l; i += 3 ) {

						for ( let j = 0; j < 3; j ++ ) {

							const index1 = indices.getX( i + j );
							const index2 = indices.getX( i + ( j + 1 ) % 3 );

							start.fromBufferAttribute( position, index1 );
							end.fromBufferAttribute( position, index2 );

							if ( isUniqueEdge( start, end, edges ) === true ) {

								vertices.push( start.x, start.y, start.z );
								vertices.push( end.x, end.y, end.z );

							}

						}

					}

				}

			} else {

				// non-indexed BufferGeometry

				const position = geometry.attributes.position;

				for ( let i = 0, l = ( position.count / 3 ); i < l; i ++ ) {

					for ( let j = 0; j < 3; j ++ ) {

						// three edges per triangle, an edge is represented as (index1, index2)
						// e.g. the first triangle has the following edges: (0,1),(1,2),(2,0)

						const index1 = 3 * i + j;
						const index2 = 3 * i + ( ( j + 1 ) % 3 );

						start.fromBufferAttribute( position, index1 );
						end.fromBufferAttribute( position, index2 );

						if ( isUniqueEdge( start, end, edges ) === true ) {

							vertices.push( start.x, start.y, start.z );
							vertices.push( end.x, end.y, end.z );

						}

					}

				}

			}

			// build geometry

			this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );

		}

	}

	copy( source ) {

		super.copy( source );

		this.parameters = Object.assign( {}, source.parameters );

		return this;

	}

}

function isUniqueEdge( start, end, edges ) {

	const hash1 = `${start.x},${start.y},${start.z}-${end.x},${end.y},${end.z}`;
	const hash2 = `${end.x},${end.y},${end.z}-${start.x},${start.y},${start.z}`; // coincident edge

	if ( edges.has( hash1 ) === true || edges.has( hash2 ) === true ) {

		return false;

	} else {

		edges.add( hash1 );
		edges.add( hash2 );
		return true;

	}

}

var Geometries = /*#__PURE__*/Object.freeze({
	__proto__: null,
	BoxGeometry: BoxGeometry,
	CapsuleGeometry: CapsuleGeometry,
	CircleGeometry: CircleGeometry,
	ConeGeometry: ConeGeometry,
	CylinderGeometry: CylinderGeometry,
	DodecahedronGeometry: DodecahedronGeometry,
	EdgesGeometry: EdgesGeometry,
	ExtrudeGeometry: ExtrudeGeometry,
	IcosahedronGeometry: IcosahedronGeometry,
	LatheGeometry: LatheGeometry,
	OctahedronGeometry: OctahedronGeometry,
	PlaneGeometry: PlaneGeometry,
	PolyhedronGeometry: PolyhedronGeometry,
	RingGeometry: RingGeometry,
	ShapeGeometry: ShapeGeometry,
	SphereGeometry: SphereGeometry,
	TetrahedronGeometry: TetrahedronGeometry,
	TorusGeometry: TorusGeometry,
	TorusKnotGeometry: TorusKnotGeometry,
	TubeGeometry: TubeGeometry,
	WireframeGeometry: WireframeGeometry
});

class ShadowMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isShadowMaterial = true;

		this.type = 'ShadowMaterial';

		this.color = new Color( 0x000000 );
		this.transparent = true;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.fog = source.fog;

		return this;

	}

}

class RawShaderMaterial extends ShaderMaterial {

	constructor( parameters ) {

		super( parameters );

		this.isRawShaderMaterial = true;

		this.type = 'RawShaderMaterial';

	}

}

class MeshStandardMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshStandardMaterial = true;

		this.defines = { 'STANDARD': '' };

		this.type = 'MeshStandardMaterial';

		this.color = new Color( 0xffffff ); // diffuse
		this.roughness = 1.0;
		this.metalness = 0.0;

		this.map = null;

		this.lightMap = null;
		this.lightMapIntensity = 1.0;

		this.aoMap = null;
		this.aoMapIntensity = 1.0;

		this.emissive = new Color( 0x000000 );
		this.emissiveIntensity = 1.0;
		this.emissiveMap = null;

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.roughnessMap = null;

		this.metalnessMap = null;

		this.alphaMap = null;

		this.envMap = null;
		this.envMapRotation = new Euler();
		this.envMapIntensity = 1.0;

		this.wireframe = false;
		this.wireframeLinewidth = 1;
		this.wireframeLinecap = 'round';
		this.wireframeLinejoin = 'round';

		this.flatShading = false;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.defines = { 'STANDARD': '' };

		this.color.copy( source.color );
		this.roughness = source.roughness;
		this.metalness = source.metalness;

		this.map = source.map;

		this.lightMap = source.lightMap;
		this.lightMapIntensity = source.lightMapIntensity;

		this.aoMap = source.aoMap;
		this.aoMapIntensity = source.aoMapIntensity;

		this.emissive.copy( source.emissive );
		this.emissiveMap = source.emissiveMap;
		this.emissiveIntensity = source.emissiveIntensity;

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.roughnessMap = source.roughnessMap;

		this.metalnessMap = source.metalnessMap;

		this.alphaMap = source.alphaMap;

		this.envMap = source.envMap;
		this.envMapRotation.copy( source.envMapRotation );
		this.envMapIntensity = source.envMapIntensity;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;
		this.wireframeLinecap = source.wireframeLinecap;
		this.wireframeLinejoin = source.wireframeLinejoin;

		this.flatShading = source.flatShading;

		this.fog = source.fog;

		return this;

	}

}

class MeshPhysicalMaterial extends MeshStandardMaterial {

	constructor( parameters ) {

		super();

		this.isMeshPhysicalMaterial = true;

		this.defines = {

			'STANDARD': '',
			'PHYSICAL': ''

		};

		this.type = 'MeshPhysicalMaterial';

		this.anisotropyRotation = 0;
		this.anisotropyMap = null;

		this.clearcoatMap = null;
		this.clearcoatRoughness = 0.0;
		this.clearcoatRoughnessMap = null;
		this.clearcoatNormalScale = new Vector2( 1, 1 );
		this.clearcoatNormalMap = null;

		this.ior = 1.5;

		Object.defineProperty( this, 'reflectivity', {
			get: function () {

				return ( clamp( 2.5 * ( this.ior - 1 ) / ( this.ior + 1 ), 0, 1 ) );

			},
			set: function ( reflectivity ) {

				this.ior = ( 1 + 0.4 * reflectivity ) / ( 1 - 0.4 * reflectivity );

			}
		} );

		this.iridescenceMap = null;
		this.iridescenceIOR = 1.3;
		this.iridescenceThicknessRange = [ 100, 400 ];
		this.iridescenceThicknessMap = null;

		this.sheenColor = new Color( 0x000000 );
		this.sheenColorMap = null;
		this.sheenRoughness = 1.0;
		this.sheenRoughnessMap = null;

		this.transmissionMap = null;

		this.thickness = 0;
		this.thicknessMap = null;
		this.attenuationDistance = Infinity;
		this.attenuationColor = new Color( 1, 1, 1 );

		this.specularIntensity = 1.0;
		this.specularIntensityMap = null;
		this.specularColor = new Color( 1, 1, 1 );
		this.specularColorMap = null;

		this._anisotropy = 0;
		this._clearcoat = 0;
		this._dispersion = 0;
		this._iridescence = 0;
		this._sheen = 0.0;
		this._transmission = 0;

		this.setValues( parameters );

	}

	get anisotropy() {

		return this._anisotropy;

	}

	set anisotropy( value ) {

		if ( this._anisotropy > 0 !== value > 0 ) {

			this.version ++;

		}

		this._anisotropy = value;

	}

	get clearcoat() {

		return this._clearcoat;

	}

	set clearcoat( value ) {

		if ( this._clearcoat > 0 !== value > 0 ) {

			this.version ++;

		}

		this._clearcoat = value;

	}

	get iridescence() {

		return this._iridescence;

	}

	set iridescence( value ) {

		if ( this._iridescence > 0 !== value > 0 ) {

			this.version ++;

		}

		this._iridescence = value;

	}

	get dispersion() {

		return this._dispersion;

	}

	set dispersion( value ) {

		if ( this._dispersion > 0 !== value > 0 ) {

			this.version ++;

		}

		this._dispersion = value;

	}

	get sheen() {

		return this._sheen;

	}

	set sheen( value ) {

		if ( this._sheen > 0 !== value > 0 ) {

			this.version ++;

		}

		this._sheen = value;

	}

	get transmission() {

		return this._transmission;

	}

	set transmission( value ) {

		if ( this._transmission > 0 !== value > 0 ) {

			this.version ++;

		}

		this._transmission = value;

	}

	copy( source ) {

		super.copy( source );

		this.defines = {

			'STANDARD': '',
			'PHYSICAL': ''

		};

		this.anisotropy = source.anisotropy;
		this.anisotropyRotation = source.anisotropyRotation;
		this.anisotropyMap = source.anisotropyMap;

		this.clearcoat = source.clearcoat;
		this.clearcoatMap = source.clearcoatMap;
		this.clearcoatRoughness = source.clearcoatRoughness;
		this.clearcoatRoughnessMap = source.clearcoatRoughnessMap;
		this.clearcoatNormalMap = source.clearcoatNormalMap;
		this.clearcoatNormalScale.copy( source.clearcoatNormalScale );

		this.dispersion = source.dispersion;
		this.ior = source.ior;

		this.iridescence = source.iridescence;
		this.iridescenceMap = source.iridescenceMap;
		this.iridescenceIOR = source.iridescenceIOR;
		this.iridescenceThicknessRange = [ ...source.iridescenceThicknessRange ];
		this.iridescenceThicknessMap = source.iridescenceThicknessMap;

		this.sheen = source.sheen;
		this.sheenColor.copy( source.sheenColor );
		this.sheenColorMap = source.sheenColorMap;
		this.sheenRoughness = source.sheenRoughness;
		this.sheenRoughnessMap = source.sheenRoughnessMap;

		this.transmission = source.transmission;
		this.transmissionMap = source.transmissionMap;

		this.thickness = source.thickness;
		this.thicknessMap = source.thicknessMap;
		this.attenuationDistance = source.attenuationDistance;
		this.attenuationColor.copy( source.attenuationColor );

		this.specularIntensity = source.specularIntensity;
		this.specularIntensityMap = source.specularIntensityMap;
		this.specularColor.copy( source.specularColor );
		this.specularColorMap = source.specularColorMap;

		return this;

	}

}

class MeshPhongMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshPhongMaterial = true;

		this.type = 'MeshPhongMaterial';

		this.color = new Color( 0xffffff ); // diffuse
		this.specular = new Color( 0x111111 );
		this.shininess = 30;

		this.map = null;

		this.lightMap = null;
		this.lightMapIntensity = 1.0;

		this.aoMap = null;
		this.aoMapIntensity = 1.0;

		this.emissive = new Color( 0x000000 );
		this.emissiveIntensity = 1.0;
		this.emissiveMap = null;

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.specularMap = null;

		this.alphaMap = null;

		this.envMap = null;
		this.envMapRotation = new Euler();
		this.combine = MultiplyOperation;
		this.reflectivity = 1;
		this.refractionRatio = 0.98;

		this.wireframe = false;
		this.wireframeLinewidth = 1;
		this.wireframeLinecap = 'round';
		this.wireframeLinejoin = 'round';

		this.flatShading = false;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );
		this.specular.copy( source.specular );
		this.shininess = source.shininess;

		this.map = source.map;

		this.lightMap = source.lightMap;
		this.lightMapIntensity = source.lightMapIntensity;

		this.aoMap = source.aoMap;
		this.aoMapIntensity = source.aoMapIntensity;

		this.emissive.copy( source.emissive );
		this.emissiveMap = source.emissiveMap;
		this.emissiveIntensity = source.emissiveIntensity;

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.specularMap = source.specularMap;

		this.alphaMap = source.alphaMap;

		this.envMap = source.envMap;
		this.envMapRotation.copy( source.envMapRotation );
		this.combine = source.combine;
		this.reflectivity = source.reflectivity;
		this.refractionRatio = source.refractionRatio;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;
		this.wireframeLinecap = source.wireframeLinecap;
		this.wireframeLinejoin = source.wireframeLinejoin;

		this.flatShading = source.flatShading;

		this.fog = source.fog;

		return this;

	}

}

class MeshToonMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshToonMaterial = true;

		this.defines = { 'TOON': '' };

		this.type = 'MeshToonMaterial';

		this.color = new Color( 0xffffff );

		this.map = null;
		this.gradientMap = null;

		this.lightMap = null;
		this.lightMapIntensity = 1.0;

		this.aoMap = null;
		this.aoMapIntensity = 1.0;

		this.emissive = new Color( 0x000000 );
		this.emissiveIntensity = 1.0;
		this.emissiveMap = null;

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.alphaMap = null;

		this.wireframe = false;
		this.wireframeLinewidth = 1;
		this.wireframeLinecap = 'round';
		this.wireframeLinejoin = 'round';

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;
		this.gradientMap = source.gradientMap;

		this.lightMap = source.lightMap;
		this.lightMapIntensity = source.lightMapIntensity;

		this.aoMap = source.aoMap;
		this.aoMapIntensity = source.aoMapIntensity;

		this.emissive.copy( source.emissive );
		this.emissiveMap = source.emissiveMap;
		this.emissiveIntensity = source.emissiveIntensity;

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.alphaMap = source.alphaMap;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;
		this.wireframeLinecap = source.wireframeLinecap;
		this.wireframeLinejoin = source.wireframeLinejoin;

		this.fog = source.fog;

		return this;

	}

}

class MeshNormalMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshNormalMaterial = true;

		this.type = 'MeshNormalMaterial';

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.wireframe = false;
		this.wireframeLinewidth = 1;

		this.flatShading = false;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;

		this.flatShading = source.flatShading;

		return this;

	}

}

class MeshLambertMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshLambertMaterial = true;

		this.type = 'MeshLambertMaterial';

		this.color = new Color( 0xffffff ); // diffuse

		this.map = null;

		this.lightMap = null;
		this.lightMapIntensity = 1.0;

		this.aoMap = null;
		this.aoMapIntensity = 1.0;

		this.emissive = new Color( 0x000000 );
		this.emissiveIntensity = 1.0;
		this.emissiveMap = null;

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.specularMap = null;

		this.alphaMap = null;

		this.envMap = null;
		this.envMapRotation = new Euler();
		this.combine = MultiplyOperation;
		this.reflectivity = 1;
		this.refractionRatio = 0.98;

		this.wireframe = false;
		this.wireframeLinewidth = 1;
		this.wireframeLinecap = 'round';
		this.wireframeLinejoin = 'round';

		this.flatShading = false;

		this.fog = true;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.lightMap = source.lightMap;
		this.lightMapIntensity = source.lightMapIntensity;

		this.aoMap = source.aoMap;
		this.aoMapIntensity = source.aoMapIntensity;

		this.emissive.copy( source.emissive );
		this.emissiveMap = source.emissiveMap;
		this.emissiveIntensity = source.emissiveIntensity;

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.specularMap = source.specularMap;

		this.alphaMap = source.alphaMap;

		this.envMap = source.envMap;
		this.envMapRotation.copy( source.envMapRotation );
		this.combine = source.combine;
		this.reflectivity = source.reflectivity;
		this.refractionRatio = source.refractionRatio;

		this.wireframe = source.wireframe;
		this.wireframeLinewidth = source.wireframeLinewidth;
		this.wireframeLinecap = source.wireframeLinecap;
		this.wireframeLinejoin = source.wireframeLinejoin;

		this.flatShading = source.flatShading;

		this.fog = source.fog;

		return this;

	}

}

class MeshMatcapMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isMeshMatcapMaterial = true;

		this.defines = { 'MATCAP': '' };

		this.type = 'MeshMatcapMaterial';

		this.color = new Color( 0xffffff ); // diffuse

		this.matcap = null;

		this.map = null;

		this.bumpMap = null;
		this.bumpScale = 1;

		this.normalMap = null;
		this.normalMapType = TangentSpaceNormalMap;
		this.normalScale = new Vector2( 1, 1 );

		this.displacementMap = null;
		this.displacementScale = 1;
		this.displacementBias = 0;

		this.alphaMap = null;

		this.flatShading = false;

		this.fog = true;

		this.setValues( parameters );

	}


	copy( source ) {

		super.copy( source );

		this.defines = { 'MATCAP': '' };

		this.color.copy( source.color );

		this.matcap = source.matcap;

		this.map = source.map;

		this.bumpMap = source.bumpMap;
		this.bumpScale = source.bumpScale;

		this.normalMap = source.normalMap;
		this.normalMapType = source.normalMapType;
		this.normalScale.copy( source.normalScale );

		this.displacementMap = source.displacementMap;
		this.displacementScale = source.displacementScale;
		this.displacementBias = source.displacementBias;

		this.alphaMap = source.alphaMap;

		this.flatShading = source.flatShading;

		this.fog = source.fog;

		return this;

	}

}

class LineDashedMaterial extends LineBasicMaterial {

	constructor( parameters ) {

		super();

		this.isLineDashedMaterial = true;

		this.type = 'LineDashedMaterial';

		this.scale = 1;
		this.dashSize = 3;
		this.gapSize = 1;

		this.setValues( parameters );

	}

	copy( source ) {

		super.copy( source );

		this.scale = source.scale;
		this.dashSize = source.dashSize;
		this.gapSize = source.gapSize;

		return this;

	}

}

// converts an array to a specific type
function convertArray( array, type, forceClone ) {

	if ( ! array || // let 'undefined' and 'null' pass
		! forceClone && array.constructor === type ) return array;

	if ( typeof type.BYTES_PER_ELEMENT === 'number' ) {

		return new type( array ); // create typed array

	}

	return Array.prototype.slice.call( array ); // create Array

}

function isTypedArray( object ) {

	return ArrayBuffer.isView( object ) &&
		! ( object instanceof DataView );

}

// returns an array by which times and values can be sorted
function getKeyframeOrder( times ) {

	function compareTime( i, j ) {

		return times[ i ] - times[ j ];

	}

	const n = times.length;
	const result = new Array( n );
	for ( let i = 0; i !== n; ++ i ) result[ i ] = i;

	result.sort( compareTime );

	return result;

}

// uses the array previously returned by 'getKeyframeOrder' to sort data
function sortedArray( values, stride, order ) {

	const nValues = values.length;
	const result = new values.constructor( nValues );

	for ( let i = 0, dstOffset = 0; dstOffset !== nValues; ++ i ) {

		const srcOffset = order[ i ] * stride;

		for ( let j = 0; j !== stride; ++ j ) {

			result[ dstOffset ++ ] = values[ srcOffset + j ];

		}

	}

	return result;

}

// function for parsing AOS keyframe formats
function flattenJSON( jsonKeys, times, values, valuePropertyName ) {

	let i = 1, key = jsonKeys[ 0 ];

	while ( key !== undefined && key[ valuePropertyName ] === undefined ) {

		key = jsonKeys[ i ++ ];

	}

	if ( key === undefined ) return; // no data

	let value = key[ valuePropertyName ];
	if ( value === undefined ) return; // no data

	if ( Array.isArray( value ) ) {

		do {

			value = key[ valuePropertyName ];

			if ( value !== undefined ) {

				times.push( key.time );
				values.push.apply( values, value ); // push all elements

			}

			key = jsonKeys[ i ++ ];

		} while ( key !== undefined );

	} else if ( value.toArray !== undefined ) {

		// ...assume THREE.Math-ish

		do {

			value = key[ valuePropertyName ];

			if ( value !== undefined ) {

				times.push( key.time );
				value.toArray( values, values.length );

			}

			key = jsonKeys[ i ++ ];

		} while ( key !== undefined );

	} else {

		// otherwise push as-is

		do {

			value = key[ valuePropertyName ];

			if ( value !== undefined ) {

				times.push( key.time );
				values.push( value );

			}

			key = jsonKeys[ i ++ ];

		} while ( key !== undefined );

	}

}

function subclip( sourceClip, name, startFrame, endFrame, fps = 30 ) {

	const clip = sourceClip.clone();

	clip.name = name;

	const tracks = [];

	for ( let i = 0; i < clip.tracks.length; ++ i ) {

		const track = clip.tracks[ i ];
		const valueSize = track.getValueSize();

		const times = [];
		const values = [];

		for ( let j = 0; j < track.times.length; ++ j ) {

			const frame = track.times[ j ] * fps;

			if ( frame < startFrame || frame >= endFrame ) continue;

			times.push( track.times[ j ] );

			for ( let k = 0; k < valueSize; ++ k ) {

				values.push( track.values[ j * valueSize + k ] );

			}

		}

		if ( times.length === 0 ) continue;

		track.times = convertArray( times, track.times.constructor );
		track.values = convertArray( values, track.values.constructor );

		tracks.push( track );

	}

	clip.tracks = tracks;

	// find minimum .times value across all tracks in the trimmed clip

	let minStartTime = Infinity;

	for ( let i = 0; i < clip.tracks.length; ++ i ) {

		if ( minStartTime > clip.tracks[ i ].times[ 0 ] ) {

			minStartTime = clip.tracks[ i ].times[ 0 ];

		}

	}

	// shift all tracks such that clip begins at t=0

	for ( let i = 0; i < clip.tracks.length; ++ i ) {

		clip.tracks[ i ].shift( - 1 * minStartTime );

	}

	clip.resetDuration();

	return clip;

}

function makeClipAdditive( targetClip, referenceFrame = 0, referenceClip = targetClip, fps = 30 ) {

	if ( fps <= 0 ) fps = 30;

	const numTracks = referenceClip.tracks.length;
	const referenceTime = referenceFrame / fps;

	// Make each track's values relative to the values at the reference frame
	for ( let i = 0; i < numTracks; ++ i ) {

		const referenceTrack = referenceClip.tracks[ i ];
		const referenceTrackType = referenceTrack.ValueTypeName;

		// Skip this track if it's non-numeric
		if ( referenceTrackType === 'bool' || referenceTrackType === 'string' ) continue;

		// Find the track in the target clip whose name and type matches the reference track
		const targetTrack = targetClip.tracks.find( function ( track ) {

			return track.name === referenceTrack.name
				&& track.ValueTypeName === referenceTrackType;

		} );

		if ( targetTrack === undefined ) continue;

		let referenceOffset = 0;
		const referenceValueSize = referenceTrack.getValueSize();

		if ( referenceTrack.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline ) {

			referenceOffset = referenceValueSize / 3;

		}

		let targetOffset = 0;
		const targetValueSize = targetTrack.getValueSize();

		if ( targetTrack.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline ) {

			targetOffset = targetValueSize / 3;

		}

		const lastIndex = referenceTrack.times.length - 1;
		let referenceValue;

		// Find the value to subtract out of the track
		if ( referenceTime <= referenceTrack.times[ 0 ] ) {

			// Reference frame is earlier than the first keyframe, so just use the first keyframe
			const startIndex = referenceOffset;
			const endIndex = referenceValueSize - referenceOffset;
			referenceValue = referenceTrack.values.slice( startIndex, endIndex );

		} else if ( referenceTime >= referenceTrack.times[ lastIndex ] ) {

			// Reference frame is after the last keyframe, so just use the last keyframe
			const startIndex = lastIndex * referenceValueSize + referenceOffset;
			const endIndex = startIndex + referenceValueSize - referenceOffset;
			referenceValue = referenceTrack.values.slice( startIndex, endIndex );

		} else {

			// Interpolate to the reference value
			const interpolant = referenceTrack.createInterpolant();
			const startIndex = referenceOffset;
			const endIndex = referenceValueSize - referenceOffset;
			interpolant.evaluate( referenceTime );
			referenceValue = interpolant.resultBuffer.slice( startIndex, endIndex );

		}

		// Conjugate the quaternion
		if ( referenceTrackType === 'quaternion' ) {

			const referenceQuat = new Quaternion().fromArray( referenceValue ).normalize().conjugate();
			referenceQuat.toArray( referenceValue );

		}

		// Subtract the reference value from all of the track values

		const numTimes = targetTrack.times.length;
		for ( let j = 0; j < numTimes; ++ j ) {

			const valueStart = j * targetValueSize + targetOffset;

			if ( referenceTrackType === 'quaternion' ) {

				// Multiply the conjugate for quaternion track types
				Quaternion.multiplyQuaternionsFlat(
					targetTrack.values,
					valueStart,
					referenceValue,
					0,
					targetTrack.values,
					valueStart
				);

			} else {

				const valueEnd = targetValueSize - targetOffset * 2;

				// Subtract each value for all other numeric track types
				for ( let k = 0; k < valueEnd; ++ k ) {

					targetTrack.values[ valueStart + k ] -= referenceValue[ k ];

				}

			}

		}

	}

	targetClip.blendMode = AdditiveAnimationBlendMode;

	return targetClip;

}

const AnimationUtils = {
	convertArray: convertArray,
	isTypedArray: isTypedArray,
	getKeyframeOrder: getKeyframeOrder,
	sortedArray: sortedArray,
	flattenJSON: flattenJSON,
	subclip: subclip,
	makeClipAdditive: makeClipAdditive
};

/**
 * Abstract base class of interpolants over parametric samples.
 *
 * The parameter domain is one dimensional, typically the time or a path
 * along a curve defined by the data.
 *
 * The sample values can have any dimensionality and derived classes may
 * apply special interpretations to the data.
 *
 * This class provides the interval seek in a Template Method, deferring
 * the actual interpolation to derived classes.
 *
 * Time complexity is O(1) for linear access crossing at most two points
 * and O(log N) for random access, where N is the number of positions.
 *
 * References:
 *
 * 		http://www.oodesign.com/template-method-pattern.html
 *
 */

class Interpolant {

	constructor( parameterPositions, sampleValues, sampleSize, resultBuffer ) {

		this.parameterPositions = parameterPositions;
		this._cachedIndex = 0;

		this.resultBuffer = resultBuffer !== undefined ?
			resultBuffer : new sampleValues.constructor( sampleSize );
		this.sampleValues = sampleValues;
		this.valueSize = sampleSize;

		this.settings = null;
		this.DefaultSettings_ = {};

	}

	evaluate( t ) {

		const pp = this.parameterPositions;
		let i1 = this._cachedIndex,
			t1 = pp[ i1 ],
			t0 = pp[ i1 - 1 ];

		validate_interval: {

			seek: {

				let right;

				linear_scan: {

					//- See http://jsperf.com/comparison-to-undefined/3
					//- slower code:
					//-
					//- 				if ( t >= t1 || t1 === undefined ) {
					forward_scan: if ( ! ( t < t1 ) ) {

						for ( let giveUpAt = i1 + 2; ; ) {

							if ( t1 === undefined ) {

								if ( t < t0 ) break forward_scan;

								// after end

								i1 = pp.length;
								this._cachedIndex = i1;
								return this.copySampleValue_( i1 - 1 );

							}

							if ( i1 === giveUpAt ) break; // this loop

							t0 = t1;
							t1 = pp[ ++ i1 ];

							if ( t < t1 ) {

								// we have arrived at the sought interval
								break seek;

							}

						}

						// prepare binary search on the right side of the index
						right = pp.length;
						break linear_scan;

					}

					//- slower code:
					//-					if ( t < t0 || t0 === undefined ) {
					if ( ! ( t >= t0 ) ) {

						// looping?

						const t1global = pp[ 1 ];

						if ( t < t1global ) {

							i1 = 2; // + 1, using the scan for the details
							t0 = t1global;

						}

						// linear reverse scan

						for ( let giveUpAt = i1 - 2; ; ) {

							if ( t0 === undefined ) {

								// before start

								this._cachedIndex = 0;
								return this.copySampleValue_( 0 );

							}

							if ( i1 === giveUpAt ) break; // this loop

							t1 = t0;
							t0 = pp[ -- i1 - 1 ];

							if ( t >= t0 ) {

								// we have arrived at the sought interval
								break seek;

							}

						}

						// prepare binary search on the left side of the index
						right = i1;
						i1 = 0;
						break linear_scan;

					}

					// the interval is valid

					break validate_interval;

				} // linear scan

				// binary search

				while ( i1 < right ) {

					const mid = ( i1 + right ) >>> 1;

					if ( t < pp[ mid ] ) {

						right = mid;

					} else {

						i1 = mid + 1;

					}

				}

				t1 = pp[ i1 ];
				t0 = pp[ i1 - 1 ];

				// check boundary cases, again

				if ( t0 === undefined ) {

					this._cachedIndex = 0;
					return this.copySampleValue_( 0 );

				}

				if ( t1 === undefined ) {

					i1 = pp.length;
					this._cachedIndex = i1;
					return this.copySampleValue_( i1 - 1 );

				}

			} // seek

			this._cachedIndex = i1;

			this.intervalChanged_( i1, t0, t1 );

		} // validate_interval

		return this.interpolate_( i1, t0, t, t1 );

	}

	getSettings_() {

		return this.settings || this.DefaultSettings_;

	}

	copySampleValue_( index ) {

		// copies a sample value to the result buffer

		const result = this.resultBuffer,
			values = this.sampleValues,
			stride = this.valueSize,
			offset = index * stride;

		for ( let i = 0; i !== stride; ++ i ) {

			result[ i ] = values[ offset + i ];

		}

		return result;

	}

	// Template methods for derived classes:

	interpolate_( /* i1, t0, t, t1 */ ) {

		throw new Error( 'call to abstract method' );
		// implementations shall return this.resultBuffer

	}

	intervalChanged_( /* i1, t0, t1 */ ) {

		// empty

	}

}

/**
 * Fast and simple cubic spline interpolant.
 *
 * It was derived from a Hermitian construction setting the first derivative
 * at each sample position to the linear slope between neighboring positions
 * over their parameter interval.
 */

class CubicInterpolant extends Interpolant {

	constructor( parameterPositions, sampleValues, sampleSize, resultBuffer ) {

		super( parameterPositions, sampleValues, sampleSize, resultBuffer );

		this._weightPrev = - 0;
		this._offsetPrev = - 0;
		this._weightNext = - 0;
		this._offsetNext = - 0;

		this.DefaultSettings_ = {

			endingStart: ZeroCurvatureEnding,
			endingEnd: ZeroCurvatureEnding

		};

	}

	intervalChanged_( i1, t0, t1 ) {

		const pp = this.parameterPositions;
		let iPrev = i1 - 2,
			iNext = i1 + 1,

			tPrev = pp[ iPrev ],
			tNext = pp[ iNext ];

		if ( tPrev === undefined ) {

			switch ( this.getSettings_().endingStart ) {

				case ZeroSlopeEnding:

					// f'(t0) = 0
					iPrev = i1;
					tPrev = 2 * t0 - t1;

					break;

				case WrapAroundEnding:

					// use the other end of the curve
					iPrev = pp.length - 2;
					tPrev = t0 + pp[ iPrev ] - pp[ iPrev + 1 ];

					break;

				default: // ZeroCurvatureEnding

					// f''(t0) = 0 a.k.a. Natural Spline
					iPrev = i1;
					tPrev = t1;

			}

		}

		if ( tNext === undefined ) {

			switch ( this.getSettings_().endingEnd ) {

				case ZeroSlopeEnding:

					// f'(tN) = 0
					iNext = i1;
					tNext = 2 * t1 - t0;

					break;

				case WrapAroundEnding:

					// use the other end of the curve
					iNext = 1;
					tNext = t1 + pp[ 1 ] - pp[ 0 ];

					break;

				default: // ZeroCurvatureEnding

					// f''(tN) = 0, a.k.a. Natural Spline
					iNext = i1 - 1;
					tNext = t0;

			}

		}

		const halfDt = ( t1 - t0 ) * 0.5,
			stride = this.valueSize;

		this._weightPrev = halfDt / ( t0 - tPrev );
		this._weightNext = halfDt / ( tNext - t1 );
		this._offsetPrev = iPrev * stride;
		this._offsetNext = iNext * stride;

	}

	interpolate_( i1, t0, t, t1 ) {

		const result = this.resultBuffer,
			values = this.sampleValues,
			stride = this.valueSize,

			o1 = i1 * stride,		o0 = o1 - stride,
			oP = this._offsetPrev, 	oN = this._offsetNext,
			wP = this._weightPrev,	wN = this._weightNext,

			p = ( t - t0 ) / ( t1 - t0 ),
			pp = p * p,
			ppp = pp * p;

		// evaluate polynomials

		const sP = - wP * ppp + 2 * wP * pp - wP * p;
		const s0 = ( 1 + wP ) * ppp + ( - 1.5 - 2 * wP ) * pp + ( - 0.5 + wP ) * p + 1;
		const s1 = ( - 1 - wN ) * ppp + ( 1.5 + wN ) * pp + 0.5 * p;
		const sN = wN * ppp - wN * pp;

		// combine data linearly

		for ( let i = 0; i !== stride; ++ i ) {

			result[ i ] =
					sP * values[ oP + i ] +
					s0 * values[ o0 + i ] +
					s1 * values[ o1 + i ] +
					sN * values[ oN + i ];

		}

		return result;

	}

}

class LinearInterpolant extends Interpolant {

	constructor( parameterPositions, sampleValues, sampleSize, resultBuffer ) {

		super( parameterPositions, sampleValues, sampleSize, resultBuffer );

	}

	interpolate_( i1, t0, t, t1 ) {

		const result = this.resultBuffer,
			values = this.sampleValues,
			stride = this.valueSize,

			offset1 = i1 * stride,
			offset0 = offset1 - stride,

			weight1 = ( t - t0 ) / ( t1 - t0 ),
			weight0 = 1 - weight1;

		for ( let i = 0; i !== stride; ++ i ) {

			result[ i ] =
					values[ offset0 + i ] * weight0 +
					values[ offset1 + i ] * weight1;

		}

		return result;

	}

}

/**
 *
 * Interpolant that evaluates to the sample value at the position preceding
 * the parameter.
 */

class DiscreteInterpolant extends Interpolant {

	constructor( parameterPositions, sampleValues, sampleSize, resultBuffer ) {

		super( parameterPositions, sampleValues, sampleSize, resultBuffer );

	}

	interpolate_( i1 /*, t0, t, t1 */ ) {

		return this.copySampleValue_( i1 - 1 );

	}

}

class KeyframeTrack {

	constructor( name, times, values, interpolation ) {

		if ( name === undefined ) throw new Error( 'THREE.KeyframeTrack: track name is undefined' );
		if ( times === undefined || times.length === 0 ) throw new Error( 'THREE.KeyframeTrack: no keyframes in track named ' + name );

		this.name = name;

		this.times = convertArray( times, this.TimeBufferType );
		this.values = convertArray( values, this.ValueBufferType );

		this.setInterpolation( interpolation || this.DefaultInterpolation );

	}

	// Serialization (in static context, because of constructor invocation
	// and automatic invocation of .toJSON):

	static toJSON( track ) {

		const trackType = track.constructor;

		let json;

		// derived classes can define a static toJSON method
		if ( trackType.toJSON !== this.toJSON ) {

			json = trackType.toJSON( track );

		} else {

			// by default, we assume the data can be serialized as-is
			json = {

				'name': track.name,
				'times': convertArray( track.times, Array ),
				'values': convertArray( track.values, Array )

			};

			const interpolation = track.getInterpolation();

			if ( interpolation !== track.DefaultInterpolation ) {

				json.interpolation = interpolation;

			}

		}

		json.type = track.ValueTypeName; // mandatory

		return json;

	}

	InterpolantFactoryMethodDiscrete( result ) {

		return new DiscreteInterpolant( this.times, this.values, this.getValueSize(), result );

	}

	InterpolantFactoryMethodLinear( result ) {

		return new LinearInterpolant( this.times, this.values, this.getValueSize(), result );

	}

	InterpolantFactoryMethodSmooth( result ) {

		return new CubicInterpolant( this.times, this.values, this.getValueSize(), result );

	}

	setInterpolation( interpolation ) {

		let factoryMethod;

		switch ( interpolation ) {

			case InterpolateDiscrete:

				factoryMethod = this.InterpolantFactoryMethodDiscrete;

				break;

			case InterpolateLinear:

				factoryMethod = this.InterpolantFactoryMethodLinear;

				break;

			case InterpolateSmooth:

				factoryMethod = this.InterpolantFactoryMethodSmooth;

				break;

		}

		if ( factoryMethod === undefined ) {

			const message = 'unsupported interpolation for ' +
				this.ValueTypeName + ' keyframe track named ' + this.name;

			if ( this.createInterpolant === undefined ) {

				// fall back to default, unless the default itself is messed up
				if ( interpolation !== this.DefaultInterpolation ) {

					this.setInterpolation( this.DefaultInterpolation );

				} else {

					throw new Error( message ); // fatal, in this case

				}

			}

			console.warn( 'THREE.KeyframeTrack:', message );
			return this;

		}

		this.createInterpolant = factoryMethod;

		return this;

	}

	getInterpolation() {

		switch ( this.createInterpolant ) {

			case this.InterpolantFactoryMethodDiscrete:

				return InterpolateDiscrete;

			case this.InterpolantFactoryMethodLinear:

				return InterpolateLinear;

			case this.InterpolantFactoryMethodSmooth:

				return InterpolateSmooth;

		}

	}

	getValueSize() {

		return this.values.length / this.times.length;

	}

	// move all keyframes either forwards or backwards in time
	shift( timeOffset ) {

		if ( timeOffset !== 0.0 ) {

			const times = this.times;

			for ( let i = 0, n = times.length; i !== n; ++ i ) {

				times[ i ] += timeOffset;

			}

		}

		return this;

	}

	// scale all keyframe times by a factor (useful for frame <-> seconds conversions)
	scale( timeScale ) {

		if ( timeScale !== 1.0 ) {

			const times = this.times;

			for ( let i = 0, n = times.length; i !== n; ++ i ) {

				times[ i ] *= timeScale;

			}

		}

		return this;

	}

	// removes keyframes before and after animation without changing any values within the range [startTime, endTime].
	// IMPORTANT: We do not shift around keys to the start of the track time, because for interpolated keys this will change their values
	trim( startTime, endTime ) {

		const times = this.times,
			nKeys = times.length;

		let from = 0,
			to = nKeys - 1;

		while ( from !== nKeys && times[ from ] < startTime ) {

			++ from;

		}

		while ( to !== - 1 && times[ to ] > endTime ) {

			-- to;

		}

		++ to; // inclusive -> exclusive bound

		if ( from !== 0 || to !== nKeys ) {

			// empty tracks are forbidden, so keep at least one keyframe
			if ( from >= to ) {

				to = Math.max( to, 1 );
				from = to - 1;

			}

			const stride = this.getValueSize();
			this.times = times.slice( from, to );
			this.values = this.values.slice( from * stride, to * stride );

		}

		return this;

	}

	// ensure we do not get a GarbageInGarbageOut situation, make sure tracks are at least minimally viable
	validate() {

		let valid = true;

		const valueSize = this.getValueSize();
		if ( valueSize - Math.floor( valueSize ) !== 0 ) {

			console.error( 'THREE.KeyframeTrack: Invalid value size in track.', this );
			valid = false;

		}

		const times = this.times,
			values = this.values,

			nKeys = times.length;

		if ( nKeys === 0 ) {

			console.error( 'THREE.KeyframeTrack: Track is empty.', this );
			valid = false;

		}

		let prevTime = null;

		for ( let i = 0; i !== nKeys; i ++ ) {

			const currTime = times[ i ];

			if ( typeof currTime === 'number' && isNaN( currTime ) ) {

				console.error( 'THREE.KeyframeTrack: Time is not a valid number.', this, i, currTime );
				valid = false;
				break;

			}

			if ( prevTime !== null && prevTime > currTime ) {

				console.error( 'THREE.KeyframeTrack: Out of order keys.', this, i, currTime, prevTime );
				valid = false;
				break;

			}

			prevTime = currTime;

		}

		if ( values !== undefined ) {

			if ( isTypedArray( values ) ) {

				for ( let i = 0, n = values.length; i !== n; ++ i ) {

					const value = values[ i ];

					if ( isNaN( value ) ) {

						console.error( 'THREE.KeyframeTrack: Value is not a valid number.', this, i, value );
						valid = false;
						break;

					}

				}

			}

		}

		return valid;

	}

	// removes equivalent sequential keys as common in morph target sequences
	// (0,0,0,0,1,1,1,0,0,0,0,0,0,0) --> (0,0,1,1,0,0)
	optimize() {

		// times or values may be shared with other tracks, so overwriting is unsafe
		const times = this.times.slice(),
			values = this.values.slice(),
			stride = this.getValueSize(),

			smoothInterpolation = this.getInterpolation() === InterpolateSmooth,

			lastIndex = times.length - 1;

		let writeIndex = 1;

		for ( let i = 1; i < lastIndex; ++ i ) {

			let keep = false;

			const time = times[ i ];
			const timeNext = times[ i + 1 ];

			// remove adjacent keyframes scheduled at the same time

			if ( time !== timeNext && ( i !== 1 || time !== times[ 0 ] ) ) {

				if ( ! smoothInterpolation ) {

					// remove unnecessary keyframes same as their neighbors

					const offset = i * stride,
						offsetP = offset - stride,
						offsetN = offset + stride;

					for ( let j = 0; j !== stride; ++ j ) {

						const value = values[ offset + j ];

						if ( value !== values[ offsetP + j ] ||
							value !== values[ offsetN + j ] ) {

							keep = true;
							break;

						}

					}

				} else {

					keep = true;

				}

			}

			// in-place compaction

			if ( keep ) {

				if ( i !== writeIndex ) {

					times[ writeIndex ] = times[ i ];

					const readOffset = i * stride,
						writeOffset = writeIndex * stride;

					for ( let j = 0; j !== stride; ++ j ) {

						values[ writeOffset + j ] = values[ readOffset + j ];

					}

				}

				++ writeIndex;

			}

		}

		// flush last keyframe (compaction looks ahead)

		if ( lastIndex > 0 ) {

			times[ writeIndex ] = times[ lastIndex ];

			for ( let readOffset = lastIndex * stride, writeOffset = writeIndex * stride, j = 0; j !== stride; ++ j ) {

				values[ writeOffset + j ] = values[ readOffset + j ];

			}

			++ writeIndex;

		}

		if ( writeIndex !== times.length ) {

			this.times = times.slice( 0, writeIndex );
			this.values = values.slice( 0, writeIndex * stride );

		} else {

			this.times = times;
			this.values = values;

		}

		return this;

	}

	clone() {

		const times = this.times.slice();
		const values = this.values.slice();

		const TypedKeyframeTrack = this.constructor;
		const track = new TypedKeyframeTrack( this.name, times, values );

		// Interpolant argument to constructor is not saved, so copy the factory method directly.
		track.createInterpolant = this.createInterpolant;

		return track;

	}

}

KeyframeTrack.prototype.TimeBufferType = Float32Array;
KeyframeTrack.prototype.ValueBufferType = Float32Array;
KeyframeTrack.prototype.DefaultInterpolation = InterpolateLinear;

/**
 * A Track of Boolean keyframe values.
 */
class BooleanKeyframeTrack extends KeyframeTrack {

	// No interpolation parameter because only InterpolateDiscrete is valid.
	constructor( name, times, values ) {

		super( name, times, values );

	}

}

BooleanKeyframeTrack.prototype.ValueTypeName = 'bool';
BooleanKeyframeTrack.prototype.ValueBufferType = Array;
BooleanKeyframeTrack.prototype.DefaultInterpolation = InterpolateDiscrete;
BooleanKeyframeTrack.prototype.InterpolantFactoryMethodLinear = undefined;
BooleanKeyframeTrack.prototype.InterpolantFactoryMethodSmooth = undefined;

/**
 * A Track of keyframe values that represent color.
 */
class ColorKeyframeTrack extends KeyframeTrack {}

ColorKeyframeTrack.prototype.ValueTypeName = 'color';

/**
 * A Track of numeric keyframe values.
 */
class NumberKeyframeTrack extends KeyframeTrack {}

NumberKeyframeTrack.prototype.ValueTypeName = 'number';

/**
 * Spherical linear unit quaternion interpolant.
 */

class QuaternionLinearInterpolant extends Interpolant {

	constructor( parameterPositions, sampleValues, sampleSize, resultBuffer ) {

		super( parameterPositions, sampleValues, sampleSize, resultBuffer );

	}

	interpolate_( i1, t0, t, t1 ) {

		const result = this.resultBuffer,
			values = this.sampleValues,
			stride = this.valueSize,

			alpha = ( t - t0 ) / ( t1 - t0 );

		let offset = i1 * stride;

		for ( let end = offset + stride; offset !== end; offset += 4 ) {

			Quaternion.slerpFlat( result, 0, values, offset - stride, values, offset, alpha );

		}

		return result;

	}

}

/**
 * A Track of quaternion keyframe values.
 */
class QuaternionKeyframeTrack extends KeyframeTrack {

	InterpolantFactoryMethodLinear( result ) {

		return new QuaternionLinearInterpolant( this.times, this.values, this.getValueSize(), result );

	}

}

QuaternionKeyframeTrack.prototype.ValueTypeName = 'quaternion';
// ValueBufferType is inherited
// DefaultInterpolation is inherited;
QuaternionKeyframeTrack.prototype.InterpolantFactoryMethodSmooth = undefined;

/**
 * A Track that interpolates Strings
 */
class StringKeyframeTrack extends KeyframeTrack {

	// No interpolation parameter because only InterpolateDiscrete is valid.
	constructor( name, times, values ) {

		super( name, times, values );

	}

}

StringKeyframeTrack.prototype.ValueTypeName = 'string';
StringKeyframeTrack.prototype.ValueBufferType = Array;
StringKeyframeTrack.prototype.DefaultInterpolation = InterpolateDiscrete;
StringKeyframeTrack.prototype.InterpolantFactoryMethodLinear = undefined;
StringKeyframeTrack.prototype.InterpolantFactoryMethodSmooth = undefined;

/**
 * A Track of vectored keyframe values.
 */
class VectorKeyframeTrack extends KeyframeTrack {}

VectorKeyframeTrack.prototype.ValueTypeName = 'vector';

class AnimationClip {

	constructor( name = '', duration = - 1, tracks = [], blendMode = NormalAnimationBlendMode ) {

		this.name = name;
		this.tracks = tracks;
		this.duration = duration;
		this.blendMode = blendMode;

		this.uuid = generateUUID();

		// this means it should figure out its duration by scanning the tracks
		if ( this.duration < 0 ) {

			this.resetDuration();

		}

	}


	static parse( json ) {

		const tracks = [],
			jsonTracks = json.tracks,
			frameTime = 1.0 / ( json.fps || 1.0 );

		for ( let i = 0, n = jsonTracks.length; i !== n; ++ i ) {

			tracks.push( parseKeyframeTrack( jsonTracks[ i ] ).scale( frameTime ) );

		}

		const clip = new this( json.name, json.duration, tracks, json.blendMode );
		clip.uuid = json.uuid;

		return clip;

	}

	static toJSON( clip ) {

		const tracks = [],
			clipTracks = clip.tracks;

		const json = {

			'name': clip.name,
			'duration': clip.duration,
			'tracks': tracks,
			'uuid': clip.uuid,
			'blendMode': clip.blendMode

		};

		for ( let i = 0, n = clipTracks.length; i !== n; ++ i ) {

			tracks.push( KeyframeTrack.toJSON( clipTracks[ i ] ) );

		}

		return json;

	}

	static CreateFromMorphTargetSequence( name, morphTargetSequence, fps, noLoop ) {

		const numMorphTargets = morphTargetSequence.length;
		const tracks = [];

		for ( let i = 0; i < numMorphTargets; i ++ ) {

			let times = [];
			let values = [];

			times.push(
				( i + numMorphTargets - 1 ) % numMorphTargets,
				i,
				( i + 1 ) % numMorphTargets );

			values.push( 0, 1, 0 );

			const order = getKeyframeOrder( times );
			times = sortedArray( times, 1, order );
			values = sortedArray( values, 1, order );

			// if there is a key at the first frame, duplicate it as the
			// last frame as well for perfect loop.
			if ( ! noLoop && times[ 0 ] === 0 ) {

				times.push( numMorphTargets );
				values.push( values[ 0 ] );

			}

			tracks.push(
				new NumberKeyframeTrack(
					'.morphTargetInfluences[' + morphTargetSequence[ i ].name + ']',
					times, values
				).scale( 1.0 / fps ) );

		}

		return new this( name, - 1, tracks );

	}

	static findByName( objectOrClipArray, name ) {

		let clipArray = objectOrClipArray;

		if ( ! Array.isArray( objectOrClipArray ) ) {

			const o = objectOrClipArray;
			clipArray = o.geometry && o.geometry.animations || o.animations;

		}

		for ( let i = 0; i < clipArray.length; i ++ ) {

			if ( clipArray[ i ].name === name ) {

				return clipArray[ i ];

			}

		}

		return null;

	}

	static CreateClipsFromMorphTargetSequences( morphTargets, fps, noLoop ) {

		const animationToMorphTargets = {};

		// tested with https://regex101.com/ on trick sequences
		// such flamingo_flyA_003, flamingo_run1_003, crdeath0059
		const pattern = /^([\w-]*?)([\d]+)$/;

		// sort morph target names into animation groups based
		// patterns like Walk_001, Walk_002, Run_001, Run_002
		for ( let i = 0, il = morphTargets.length; i < il; i ++ ) {

			const morphTarget = morphTargets[ i ];
			const parts = morphTarget.name.match( pattern );

			if ( parts && parts.length > 1 ) {

				const name = parts[ 1 ];

				let animationMorphTargets = animationToMorphTargets[ name ];

				if ( ! animationMorphTargets ) {

					animationToMorphTargets[ name ] = animationMorphTargets = [];

				}

				animationMorphTargets.push( morphTarget );

			}

		}

		const clips = [];

		for ( const name in animationToMorphTargets ) {

			clips.push( this.CreateFromMorphTargetSequence( name, animationToMorphTargets[ name ], fps, noLoop ) );

		}

		return clips;

	}

	// parse the animation.hierarchy format
	static parseAnimation( animation, bones ) {

		if ( ! animation ) {

			console.error( 'THREE.AnimationClip: No animation in JSONLoader data.' );
			return null;

		}

		const addNonemptyTrack = function ( trackType, trackName, animationKeys, propertyName, destTracks ) {

			// only return track if there are actually keys.
			if ( animationKeys.length !== 0 ) {

				const times = [];
				const values = [];

				flattenJSON( animationKeys, times, values, propertyName );

				// empty keys are filtered out, so check again
				if ( times.length !== 0 ) {

					destTracks.push( new trackType( trackName, times, values ) );

				}

			}

		};

		const tracks = [];

		const clipName = animation.name || 'default';
		const fps = animation.fps || 30;
		const blendMode = animation.blendMode;

		// automatic length determination in AnimationClip.
		let duration = animation.length || - 1;

		const hierarchyTracks = animation.hierarchy || [];

		for ( let h = 0; h < hierarchyTracks.length; h ++ ) {

			const animationKeys = hierarchyTracks[ h ].keys;

			// skip empty tracks
			if ( ! animationKeys || animationKeys.length === 0 ) continue;

			// process morph targets
			if ( animationKeys[ 0 ].morphTargets ) {

				// figure out all morph targets used in this track
				const morphTargetNames = {};

				let k;

				for ( k = 0; k < animationKeys.length; k ++ ) {

					if ( animationKeys[ k ].morphTargets ) {

						for ( let m = 0; m < animationKeys[ k ].morphTargets.length; m ++ ) {

							morphTargetNames[ animationKeys[ k ].morphTargets[ m ] ] = - 1;

						}

					}

				}

				// create a track for each morph target with all zero
				// morphTargetInfluences except for the keys in which
				// the morphTarget is named.
				for ( const morphTargetName in morphTargetNames ) {

					const times = [];
					const values = [];

					for ( let m = 0; m !== animationKeys[ k ].morphTargets.length; ++ m ) {

						const animationKey = animationKeys[ k ];

						times.push( animationKey.time );
						values.push( ( animationKey.morphTarget === morphTargetName ) ? 1 : 0 );

					}

					tracks.push( new NumberKeyframeTrack( '.morphTargetInfluence[' + morphTargetName + ']', times, values ) );

				}

				duration = morphTargetNames.length * fps;

			} else {

				// ...assume skeletal animation

				const boneName = '.bones[' + bones[ h ].name + ']';

				addNonemptyTrack(
					VectorKeyframeTrack, boneName + '.position',
					animationKeys, 'pos', tracks );

				addNonemptyTrack(
					QuaternionKeyframeTrack, boneName + '.quaternion',
					animationKeys, 'rot', tracks );

				addNonemptyTrack(
					VectorKeyframeTrack, boneName + '.scale',
					animationKeys, 'scl', tracks );

			}

		}

		if ( tracks.length === 0 ) {

			return null;

		}

		const clip = new this( clipName, duration, tracks, blendMode );

		return clip;

	}

	resetDuration() {

		const tracks = this.tracks;
		let duration = 0;

		for ( let i = 0, n = tracks.length; i !== n; ++ i ) {

			const track = this.tracks[ i ];

			duration = Math.max( duration, track.times[ track.times.length - 1 ] );

		}

		this.duration = duration;

		return this;

	}

	trim() {

		for ( let i = 0; i < this.tracks.length; i ++ ) {

			this.tracks[ i ].trim( 0, this.duration );

		}

		return this;

	}

	validate() {

		let valid = true;

		for ( let i = 0; i < this.tracks.length; i ++ ) {

			valid = valid && this.tracks[ i ].validate();

		}

		return valid;

	}

	optimize() {

		for ( let i = 0; i < this.tracks.length; i ++ ) {

			this.tracks[ i ].optimize();

		}

		return this;

	}

	clone() {

		const tracks = [];

		for ( let i = 0; i < this.tracks.length; i ++ ) {

			tracks.push( this.tracks[ i ].clone() );

		}

		return new this.constructor( this.name, this.duration, tracks, this.blendMode );

	}

	toJSON() {

		return this.constructor.toJSON( this );

	}

}

function getTrackTypeForValueTypeName( typeName ) {

	switch ( typeName.toLowerCase() ) {

		case 'scalar':
		case 'double':
		case 'float':
		case 'number':
		case 'integer':

			return NumberKeyframeTrack;

		case 'vector':
		case 'vector2':
		case 'vector3':
		case 'vector4':

			return VectorKeyframeTrack;

		case 'color':

			return ColorKeyframeTrack;

		case 'quaternion':

			return QuaternionKeyframeTrack;

		case 'bool':
		case 'boolean':

			return BooleanKeyframeTrack;

		case 'string':

			return StringKeyframeTrack;

	}

	throw new Error( 'THREE.KeyframeTrack: Unsupported typeName: ' + typeName );

}

function parseKeyframeTrack( json ) {

	if ( json.type === undefined ) {

		throw new Error( 'THREE.KeyframeTrack: track type undefined, can not parse' );

	}

	const trackType = getTrackTypeForValueTypeName( json.type );

	if ( json.times === undefined ) {

		const times = [], values = [];

		flattenJSON( json.keys, times, values, 'value' );

		json.times = times;
		json.values = values;

	}

	// derived classes can define a static parse method
	if ( trackType.parse !== undefined ) {

		return trackType.parse( json );

	} else {

		// by default, we assume a constructor compatible with the base
		return new trackType( json.name, json.times, json.values, json.interpolation );

	}

}

const Cache = {

	enabled: false,

	files: {},

	add: function ( key, file ) {

		if ( this.enabled === false ) return;

		// console.log( 'THREE.Cache', 'Adding key:', key );

		this.files[ key ] = file;

	},

	get: function ( key ) {

		if ( this.enabled === false ) return;

		// console.log( 'THREE.Cache', 'Checking key:', key );

		return this.files[ key ];

	},

	remove: function ( key ) {

		delete this.files[ key ];

	},

	clear: function () {

		this.files = {};

	}

};

class LoadingManager {

	constructor( onLoad, onProgress, onError ) {

		const scope = this;

		let isLoading = false;
		let itemsLoaded = 0;
		let itemsTotal = 0;
		let urlModifier = undefined;
		const handlers = [];

		// Refer to #5689 for the reason why we don't set .onStart
		// in the constructor

		this.onStart = undefined;
		this.onLoad = onLoad;
		this.onProgress = onProgress;
		this.onError = onError;

		this.itemStart = function ( url ) {

			itemsTotal ++;

			if ( isLoading === false ) {

				if ( scope.onStart !== undefined ) {

					scope.onStart( url, itemsLoaded, itemsTotal );

				}

			}

			isLoading = true;

		};

		this.itemEnd = function ( url ) {

			itemsLoaded ++;

			if ( scope.onProgress !== undefined ) {

				scope.onProgress( url, itemsLoaded, itemsTotal );

			}

			if ( itemsLoaded === itemsTotal ) {

				isLoading = false;

				if ( scope.onLoad !== undefined ) {

					scope.onLoad();

				}

			}

		};

		this.itemError = function ( url ) {

			if ( scope.onError !== undefined ) {

				scope.onError( url );

			}

		};

		this.resolveURL = function ( url ) {

			if ( urlModifier ) {

				return urlModifier( url );

			}

			return url;

		};

		this.setURLModifier = function ( transform ) {

			urlModifier = transform;

			return this;

		};

		this.addHandler = function ( regex, loader ) {

			handlers.push( regex, loader );

			return this;

		};

		this.removeHandler = function ( regex ) {

			const index = handlers.indexOf( regex );

			if ( index !== - 1 ) {

				handlers.splice( index, 2 );

			}

			return this;

		};

		this.getHandler = function ( file ) {

			for ( let i = 0, l = handlers.length; i < l; i += 2 ) {

				const regex = handlers[ i ];
				const loader = handlers[ i + 1 ];

				if ( regex.global ) regex.lastIndex = 0; // see #17920

				if ( regex.test( file ) ) {

					return loader;

				}

			}

			return null;

		};

	}

}

const DefaultLoadingManager = /*@__PURE__*/ new LoadingManager();

class Loader {

	constructor( manager ) {

		this.manager = ( manager !== undefined ) ? manager : DefaultLoadingManager;

		this.crossOrigin = 'anonymous';
		this.withCredentials = false;
		this.path = '';
		this.resourcePath = '';
		this.requestHeader = {};

	}

	load( /* url, onLoad, onProgress, onError */ ) {}

	loadAsync( url, onProgress ) {

		const scope = this;

		return new Promise( function ( resolve, reject ) {

			scope.load( url, resolve, onProgress, reject );

		} );

	}

	parse( /* data */ ) {}

	setCrossOrigin( crossOrigin ) {

		this.crossOrigin = crossOrigin;
		return this;

	}

	setWithCredentials( value ) {

		this.withCredentials = value;
		return this;

	}

	setPath( path ) {

		this.path = path;
		return this;

	}

	setResourcePath( resourcePath ) {

		this.resourcePath = resourcePath;
		return this;

	}

	setRequestHeader( requestHeader ) {

		this.requestHeader = requestHeader;
		return this;

	}

}

Loader.DEFAULT_MATERIAL_NAME = '__DEFAULT';

const loading = {};

class HttpError extends Error {

	constructor( message, response ) {

		super( message );
		this.response = response;

	}

}

class FileLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		if ( url === undefined ) url = '';

		if ( this.path !== undefined ) url = this.path + url;

		url = this.manager.resolveURL( url );

		const cached = Cache.get( url );

		if ( cached !== undefined ) {

			this.manager.itemStart( url );

			setTimeout( () => {

				if ( onLoad ) onLoad( cached );

				this.manager.itemEnd( url );

			}, 0 );

			return cached;

		}

		// Check if request is duplicate

		if ( loading[ url ] !== undefined ) {

			loading[ url ].push( {

				onLoad: onLoad,
				onProgress: onProgress,
				onError: onError

			} );

			return;

		}

		// Initialise array for duplicate requests
		loading[ url ] = [];

		loading[ url ].push( {
			onLoad: onLoad,
			onProgress: onProgress,
			onError: onError,
		} );

		// create request
		const req = new Request( url, {
			headers: new Headers( this.requestHeader ),
			credentials: this.withCredentials ? 'include' : 'same-origin',
			// An abort controller could be added within a future PR
		} );

		// record states ( avoid data race )
		const mimeType = this.mimeType;
		const responseType = this.responseType;

		// start the fetch
		fetch( req )
			.then( response => {

				if ( response.status === 200 || response.status === 0 ) {

					// Some browsers return HTTP Status 0 when using non-http protocol
					// e.g. 'file://' or 'data://'. Handle as success.

					if ( response.status === 0 ) {

						console.warn( 'THREE.FileLoader: HTTP Status 0 received.' );

					}

					// Workaround: Checking if response.body === undefined for Alipay browser #23548

					if ( typeof ReadableStream === 'undefined' || response.body === undefined || response.body.getReader === undefined ) {

						return response;

					}

					const callbacks = loading[ url ];
					const reader = response.body.getReader();

					// Nginx needs X-File-Size check
					// https://serverfault.com/questions/482875/why-does-nginx-remove-content-length-header-for-chunked-content
					const contentLength = response.headers.get( 'X-File-Size' ) || response.headers.get( 'Content-Length' );
					const total = contentLength ? parseInt( contentLength ) : 0;
					const lengthComputable = total !== 0;
					let loaded = 0;

					// periodically read data into the new stream tracking while download progress
					const stream = new ReadableStream( {
						start( controller ) {

							readData();

							function readData() {

								reader.read().then( ( { done, value } ) => {

									if ( done ) {

										controller.close();

									} else {

										loaded += value.byteLength;

										const event = new ProgressEvent( 'progress', { lengthComputable, loaded, total } );
										for ( let i = 0, il = callbacks.length; i < il; i ++ ) {

											const callback = callbacks[ i ];
											if ( callback.onProgress ) callback.onProgress( event );

										}

										controller.enqueue( value );
										readData();

									}

								}, ( e ) => {

									controller.error( e );

								} );

							}

						}

					} );

					return new Response( stream );

				} else {

					throw new HttpError( `fetch for "${response.url}" responded with ${response.status}: ${response.statusText}`, response );

				}

			} )
			.then( response => {

				switch ( responseType ) {

					case 'arraybuffer':

						return response.arrayBuffer();

					case 'blob':

						return response.blob();

					case 'document':

						return response.text()
							.then( text => {

								const parser = new DOMParser();
								return parser.parseFromString( text, mimeType );

							} );

					case 'json':

						return response.json();

					default:

						if ( mimeType === undefined ) {

							return response.text();

						} else {

							// sniff encoding
							const re = /charset="?([^;"\s]*)"?/i;
							const exec = re.exec( mimeType );
							const label = exec && exec[ 1 ] ? exec[ 1 ].toLowerCase() : undefined;
							const decoder = new TextDecoder( label );
							return response.arrayBuffer().then( ab => decoder.decode( ab ) );

						}

				}

			} )
			.then( data => {

				// Add to cache only on HTTP success, so that we do not cache
				// error response bodies as proper responses to requests.
				Cache.add( url, data );

				const callbacks = loading[ url ];
				delete loading[ url ];

				for ( let i = 0, il = callbacks.length; i < il; i ++ ) {

					const callback = callbacks[ i ];
					if ( callback.onLoad ) callback.onLoad( data );

				}

			} )
			.catch( err => {

				// Abort errors and other errors are handled the same

				const callbacks = loading[ url ];

				if ( callbacks === undefined ) {

					// When onLoad was called and url was deleted in `loading`
					this.manager.itemError( url );
					throw err;

				}

				delete loading[ url ];

				for ( let i = 0, il = callbacks.length; i < il; i ++ ) {

					const callback = callbacks[ i ];
					if ( callback.onError ) callback.onError( err );

				}

				this.manager.itemError( url );

			} )
			.finally( () => {

				this.manager.itemEnd( url );

			} );

		this.manager.itemStart( url );

	}

	setResponseType( value ) {

		this.responseType = value;
		return this;

	}

	setMimeType( value ) {

		this.mimeType = value;
		return this;

	}

}

class AnimationLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const loader = new FileLoader( this.manager );
		loader.setPath( this.path );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( this.withCredentials );
		loader.load( url, function ( text ) {

			try {

				onLoad( scope.parse( JSON.parse( text ) ) );

			} catch ( e ) {

				if ( onError ) {

					onError( e );

				} else {

					console.error( e );

				}

				scope.manager.itemError( url );

			}

		}, onProgress, onError );

	}

	parse( json ) {

		const animations = [];

		for ( let i = 0; i < json.length; i ++ ) {

			const clip = AnimationClip.parse( json[ i ] );

			animations.push( clip );

		}

		return animations;

	}

}

/**
 * Abstract Base class to block based textures loader (dds, pvr, ...)
 *
 * Sub classes have to implement the parse() method which will be used in load().
 */

class CompressedTextureLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const images = [];

		const texture = new CompressedTexture();

		const loader = new FileLoader( this.manager );
		loader.setPath( this.path );
		loader.setResponseType( 'arraybuffer' );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( scope.withCredentials );

		let loaded = 0;

		function loadTexture( i ) {

			loader.load( url[ i ], function ( buffer ) {

				const texDatas = scope.parse( buffer, true );

				images[ i ] = {
					width: texDatas.width,
					height: texDatas.height,
					format: texDatas.format,
					mipmaps: texDatas.mipmaps
				};

				loaded += 1;

				if ( loaded === 6 ) {

					if ( texDatas.mipmapCount === 1 ) texture.minFilter = LinearFilter;

					texture.image = images;
					texture.format = texDatas.format;
					texture.needsUpdate = true;

					if ( onLoad ) onLoad( texture );

				}

			}, onProgress, onError );

		}

		if ( Array.isArray( url ) ) {

			for ( let i = 0, il = url.length; i < il; ++ i ) {

				loadTexture( i );

			}

		} else {

			// compressed cubemap texture stored in a single DDS file

			loader.load( url, function ( buffer ) {

				const texDatas = scope.parse( buffer, true );

				if ( texDatas.isCubemap ) {

					const faces = texDatas.mipmaps.length / texDatas.mipmapCount;

					for ( let f = 0; f < faces; f ++ ) {

						images[ f ] = { mipmaps: [] };

						for ( let i = 0; i < texDatas.mipmapCount; i ++ ) {

							images[ f ].mipmaps.push( texDatas.mipmaps[ f * texDatas.mipmapCount + i ] );
							images[ f ].format = texDatas.format;
							images[ f ].width = texDatas.width;
							images[ f ].height = texDatas.height;

						}

					}

					texture.image = images;

				} else {

					texture.image.width = texDatas.width;
					texture.image.height = texDatas.height;
					texture.mipmaps = texDatas.mipmaps;

				}

				if ( texDatas.mipmapCount === 1 ) {

					texture.minFilter = LinearFilter;

				}

				texture.format = texDatas.format;
				texture.needsUpdate = true;

				if ( onLoad ) onLoad( texture );

			}, onProgress, onError );

		}

		return texture;

	}

}

class ImageLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		if ( this.path !== undefined ) url = this.path + url;

		url = this.manager.resolveURL( url );

		const scope = this;

		const cached = Cache.get( url );

		if ( cached !== undefined ) {

			scope.manager.itemStart( url );

			setTimeout( function () {

				if ( onLoad ) onLoad( cached );

				scope.manager.itemEnd( url );

			}, 0 );

			return cached;

		}

		const image = createElementNS( 'img' );

		function onImageLoad() {

			removeEventListeners();

			Cache.add( url, this );

			if ( onLoad ) onLoad( this );

			scope.manager.itemEnd( url );

		}

		function onImageError( event ) {

			removeEventListeners();

			if ( onError ) onError( event );

			scope.manager.itemError( url );
			scope.manager.itemEnd( url );

		}

		function removeEventListeners() {

			image.removeEventListener( 'load', onImageLoad, false );
			image.removeEventListener( 'error', onImageError, false );

		}

		image.addEventListener( 'load', onImageLoad, false );
		image.addEventListener( 'error', onImageError, false );

		if ( url.slice( 0, 5 ) !== 'data:' ) {

			if ( this.crossOrigin !== undefined ) image.crossOrigin = this.crossOrigin;

		}

		scope.manager.itemStart( url );

		image.src = url;

		return image;

	}

}

class CubeTextureLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( urls, onLoad, onProgress, onError ) {

		const texture = new CubeTexture();
		texture.colorSpace = SRGBColorSpace;

		const loader = new ImageLoader( this.manager );
		loader.setCrossOrigin( this.crossOrigin );
		loader.setPath( this.path );

		let loaded = 0;

		function loadTexture( i ) {

			loader.load( urls[ i ], function ( image ) {

				texture.images[ i ] = image;

				loaded ++;

				if ( loaded === 6 ) {

					texture.needsUpdate = true;

					if ( onLoad ) onLoad( texture );

				}

			}, undefined, onError );

		}

		for ( let i = 0; i < urls.length; ++ i ) {

			loadTexture( i );

		}

		return texture;

	}

}

/**
 * Abstract Base class to load generic binary textures formats (rgbe, hdr, ...)
 *
 * Sub classes have to implement the parse() method which will be used in load().
 */

class DataTextureLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const texture = new DataTexture();

		const loader = new FileLoader( this.manager );
		loader.setResponseType( 'arraybuffer' );
		loader.setRequestHeader( this.requestHeader );
		loader.setPath( this.path );
		loader.setWithCredentials( scope.withCredentials );
		loader.load( url, function ( buffer ) {

			let texData;

			try {

				texData = scope.parse( buffer );

			} catch ( error ) {

				if ( onError !== undefined ) {

					onError( error );

				} else {

					console.error( error );
					return;

				}

			}

			if ( texData.image !== undefined ) {

				texture.image = texData.image;

			} else if ( texData.data !== undefined ) {

				texture.image.width = texData.width;
				texture.image.height = texData.height;
				texture.image.data = texData.data;

			}

			texture.wrapS = texData.wrapS !== undefined ? texData.wrapS : ClampToEdgeWrapping;
			texture.wrapT = texData.wrapT !== undefined ? texData.wrapT : ClampToEdgeWrapping;

			texture.magFilter = texData.magFilter !== undefined ? texData.magFilter : LinearFilter;
			texture.minFilter = texData.minFilter !== undefined ? texData.minFilter : LinearFilter;

			texture.anisotropy = texData.anisotropy !== undefined ? texData.anisotropy : 1;

			if ( texData.colorSpace !== undefined ) {

				texture.colorSpace = texData.colorSpace;

			}

			if ( texData.flipY !== undefined ) {

				texture.flipY = texData.flipY;

			}

			if ( texData.format !== undefined ) {

				texture.format = texData.format;

			}

			if ( texData.type !== undefined ) {

				texture.type = texData.type;

			}

			if ( texData.mipmaps !== undefined ) {

				texture.mipmaps = texData.mipmaps;
				texture.minFilter = LinearMipmapLinearFilter; // presumably...

			}

			if ( texData.mipmapCount === 1 ) {

				texture.minFilter = LinearFilter;

			}

			if ( texData.generateMipmaps !== undefined ) {

				texture.generateMipmaps = texData.generateMipmaps;

			}

			texture.needsUpdate = true;

			if ( onLoad ) onLoad( texture, texData );

		}, onProgress, onError );


		return texture;

	}

}

class TextureLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const texture = new Texture();

		const loader = new ImageLoader( this.manager );
		loader.setCrossOrigin( this.crossOrigin );
		loader.setPath( this.path );

		loader.load( url, function ( image ) {

			texture.image = image;
			texture.needsUpdate = true;

			if ( onLoad !== undefined ) {

				onLoad( texture );

			}

		}, onProgress, onError );

		return texture;

	}

}

class Light extends Object3D {

	constructor( color, intensity = 1 ) {

		super();

		this.isLight = true;

		this.type = 'Light';

		this.color = new Color( color );
		this.intensity = intensity;

	}

	dispose() {

		// Empty here in base class; some subclasses override.

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.color.copy( source.color );
		this.intensity = source.intensity;

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.object.color = this.color.getHex();
		data.object.intensity = this.intensity;

		if ( this.groundColor !== undefined ) data.object.groundColor = this.groundColor.getHex();

		if ( this.distance !== undefined ) data.object.distance = this.distance;
		if ( this.angle !== undefined ) data.object.angle = this.angle;
		if ( this.decay !== undefined ) data.object.decay = this.decay;
		if ( this.penumbra !== undefined ) data.object.penumbra = this.penumbra;

		if ( this.shadow !== undefined ) data.object.shadow = this.shadow.toJSON();
		if ( this.target !== undefined ) data.object.target = this.target.uuid;

		return data;

	}

}

class HemisphereLight extends Light {

	constructor( skyColor, groundColor, intensity ) {

		super( skyColor, intensity );

		this.isHemisphereLight = true;

		this.type = 'HemisphereLight';

		this.position.copy( Object3D.DEFAULT_UP );
		this.updateMatrix();

		this.groundColor = new Color( groundColor );

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.groundColor.copy( source.groundColor );

		return this;

	}

}

const _projScreenMatrix$1 = /*@__PURE__*/ new Matrix4();
const _lightPositionWorld$1 = /*@__PURE__*/ new Vector3();
const _lookTarget$1 = /*@__PURE__*/ new Vector3();

class LightShadow {

	constructor( camera ) {

		this.camera = camera;

		this.intensity = 1;

		this.bias = 0;
		this.normalBias = 0;
		this.radius = 1;
		this.blurSamples = 8;

		this.mapSize = new Vector2( 512, 512 );

		this.map = null;
		this.mapPass = null;
		this.matrix = new Matrix4();

		this.autoUpdate = true;
		this.needsUpdate = false;

		this._frustum = new Frustum();
		this._frameExtents = new Vector2( 1, 1 );

		this._viewportCount = 1;

		this._viewports = [

			new Vector4( 0, 0, 1, 1 )

		];

	}

	getViewportCount() {

		return this._viewportCount;

	}

	getFrustum() {

		return this._frustum;

	}

	updateMatrices( light ) {

		const shadowCamera = this.camera;
		const shadowMatrix = this.matrix;

		_lightPositionWorld$1.setFromMatrixPosition( light.matrixWorld );
		shadowCamera.position.copy( _lightPositionWorld$1 );

		_lookTarget$1.setFromMatrixPosition( light.target.matrixWorld );
		shadowCamera.lookAt( _lookTarget$1 );
		shadowCamera.updateMatrixWorld();

		_projScreenMatrix$1.multiplyMatrices( shadowCamera.projectionMatrix, shadowCamera.matrixWorldInverse );
		this._frustum.setFromProjectionMatrix( _projScreenMatrix$1 );

		shadowMatrix.set(
			0.5, 0.0, 0.0, 0.5,
			0.0, 0.5, 0.0, 0.5,
			0.0, 0.0, 0.5, 0.5,
			0.0, 0.0, 0.0, 1.0
		);

		shadowMatrix.multiply( _projScreenMatrix$1 );

	}

	getViewport( viewportIndex ) {

		return this._viewports[ viewportIndex ];

	}

	getFrameExtents() {

		return this._frameExtents;

	}

	dispose() {

		if ( this.map ) {

			this.map.dispose();

		}

		if ( this.mapPass ) {

			this.mapPass.dispose();

		}

	}

	copy( source ) {

		this.camera = source.camera.clone();

		this.intensity = source.intensity;

		this.bias = source.bias;
		this.radius = source.radius;

		this.mapSize.copy( source.mapSize );

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	toJSON() {

		const object = {};

		if ( this.intensity !== 1 ) object.intensity = this.intensity;
		if ( this.bias !== 0 ) object.bias = this.bias;
		if ( this.normalBias !== 0 ) object.normalBias = this.normalBias;
		if ( this.radius !== 1 ) object.radius = this.radius;
		if ( this.mapSize.x !== 512 || this.mapSize.y !== 512 ) object.mapSize = this.mapSize.toArray();

		object.camera = this.camera.toJSON( false ).object;
		delete object.camera.matrix;

		return object;

	}

}

class SpotLightShadow extends LightShadow {

	constructor() {

		super( new PerspectiveCamera( 50, 1, 0.5, 500 ) );

		this.isSpotLightShadow = true;

		this.focus = 1;

	}

	updateMatrices( light ) {

		const camera = this.camera;

		const fov = RAD2DEG * 2 * light.angle * this.focus;
		const aspect = this.mapSize.width / this.mapSize.height;
		const far = light.distance || camera.far;

		if ( fov !== camera.fov || aspect !== camera.aspect || far !== camera.far ) {

			camera.fov = fov;
			camera.aspect = aspect;
			camera.far = far;
			camera.updateProjectionMatrix();

		}

		super.updateMatrices( light );

	}

	copy( source ) {

		super.copy( source );

		this.focus = source.focus;

		return this;

	}

}

class SpotLight extends Light {

	constructor( color, intensity, distance = 0, angle = Math.PI / 3, penumbra = 0, decay = 2 ) {

		super( color, intensity );

		this.isSpotLight = true;

		this.type = 'SpotLight';

		this.position.copy( Object3D.DEFAULT_UP );
		this.updateMatrix();

		this.target = new Object3D();

		this.distance = distance;
		this.angle = angle;
		this.penumbra = penumbra;
		this.decay = decay;

		this.map = null;

		this.shadow = new SpotLightShadow();

	}

	get power() {

		// compute the light's luminous power (in lumens) from its intensity (in candela)
		// by convention for a spotlight, luminous power (lm) = π * luminous intensity (cd)
		return this.intensity * Math.PI;

	}

	set power( power ) {

		// set the light's intensity (in candela) from the desired luminous power (in lumens)
		this.intensity = power / Math.PI;

	}

	dispose() {

		this.shadow.dispose();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.distance = source.distance;
		this.angle = source.angle;
		this.penumbra = source.penumbra;
		this.decay = source.decay;

		this.target = source.target.clone();

		this.shadow = source.shadow.clone();

		return this;

	}

}

const _projScreenMatrix = /*@__PURE__*/ new Matrix4();
const _lightPositionWorld = /*@__PURE__*/ new Vector3();
const _lookTarget = /*@__PURE__*/ new Vector3();

class PointLightShadow extends LightShadow {

	constructor() {

		super( new PerspectiveCamera( 90, 1, 0.5, 500 ) );

		this.isPointLightShadow = true;

		this._frameExtents = new Vector2( 4, 2 );

		this._viewportCount = 6;

		this._viewports = [
			// These viewports map a cube-map onto a 2D texture with the
			// following orientation:
			//
			//  xzXZ
			//   y Y
			//
			// X - Positive x direction
			// x - Negative x direction
			// Y - Positive y direction
			// y - Negative y direction
			// Z - Positive z direction
			// z - Negative z direction

			// positive X
			new Vector4( 2, 1, 1, 1 ),
			// negative X
			new Vector4( 0, 1, 1, 1 ),
			// positive Z
			new Vector4( 3, 1, 1, 1 ),
			// negative Z
			new Vector4( 1, 1, 1, 1 ),
			// positive Y
			new Vector4( 3, 0, 1, 1 ),
			// negative Y
			new Vector4( 1, 0, 1, 1 )
		];

		this._cubeDirections = [
			new Vector3( 1, 0, 0 ), new Vector3( - 1, 0, 0 ), new Vector3( 0, 0, 1 ),
			new Vector3( 0, 0, - 1 ), new Vector3( 0, 1, 0 ), new Vector3( 0, - 1, 0 )
		];

		this._cubeUps = [
			new Vector3( 0, 1, 0 ), new Vector3( 0, 1, 0 ), new Vector3( 0, 1, 0 ),
			new Vector3( 0, 1, 0 ), new Vector3( 0, 0, 1 ),	new Vector3( 0, 0, - 1 )
		];

	}

	updateMatrices( light, viewportIndex = 0 ) {

		const camera = this.camera;
		const shadowMatrix = this.matrix;

		const far = light.distance || camera.far;

		if ( far !== camera.far ) {

			camera.far = far;
			camera.updateProjectionMatrix();

		}

		_lightPositionWorld.setFromMatrixPosition( light.matrixWorld );
		camera.position.copy( _lightPositionWorld );

		_lookTarget.copy( camera.position );
		_lookTarget.add( this._cubeDirections[ viewportIndex ] );
		camera.up.copy( this._cubeUps[ viewportIndex ] );
		camera.lookAt( _lookTarget );
		camera.updateMatrixWorld();

		shadowMatrix.makeTranslation( - _lightPositionWorld.x, - _lightPositionWorld.y, - _lightPositionWorld.z );

		_projScreenMatrix.multiplyMatrices( camera.projectionMatrix, camera.matrixWorldInverse );
		this._frustum.setFromProjectionMatrix( _projScreenMatrix );

	}

}

class PointLight extends Light {

	constructor( color, intensity, distance = 0, decay = 2 ) {

		super( color, intensity );

		this.isPointLight = true;

		this.type = 'PointLight';

		this.distance = distance;
		this.decay = decay;

		this.shadow = new PointLightShadow();

	}

	get power() {

		// compute the light's luminous power (in lumens) from its intensity (in candela)
		// for an isotropic light source, luminous power (lm) = 4 π luminous intensity (cd)
		return this.intensity * 4 * Math.PI;

	}

	set power( power ) {

		// set the light's intensity (in candela) from the desired luminous power (in lumens)
		this.intensity = power / ( 4 * Math.PI );

	}

	dispose() {

		this.shadow.dispose();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.distance = source.distance;
		this.decay = source.decay;

		this.shadow = source.shadow.clone();

		return this;

	}

}

class DirectionalLightShadow extends LightShadow {

	constructor() {

		super( new OrthographicCamera( - 5, 5, 5, - 5, 0.5, 500 ) );

		this.isDirectionalLightShadow = true;

	}

}

class DirectionalLight extends Light {

	constructor( color, intensity ) {

		super( color, intensity );

		this.isDirectionalLight = true;

		this.type = 'DirectionalLight';

		this.position.copy( Object3D.DEFAULT_UP );
		this.updateMatrix();

		this.target = new Object3D();

		this.shadow = new DirectionalLightShadow();

	}

	dispose() {

		this.shadow.dispose();

	}

	copy( source ) {

		super.copy( source );

		this.target = source.target.clone();
		this.shadow = source.shadow.clone();

		return this;

	}

}

class AmbientLight extends Light {

	constructor( color, intensity ) {

		super( color, intensity );

		this.isAmbientLight = true;

		this.type = 'AmbientLight';

	}

}

class RectAreaLight extends Light {

	constructor( color, intensity, width = 10, height = 10 ) {

		super( color, intensity );

		this.isRectAreaLight = true;

		this.type = 'RectAreaLight';

		this.width = width;
		this.height = height;

	}

	get power() {

		// compute the light's luminous power (in lumens) from its intensity (in nits)
		return this.intensity * this.width * this.height * Math.PI;

	}

	set power( power ) {

		// set the light's intensity (in nits) from the desired luminous power (in lumens)
		this.intensity = power / ( this.width * this.height * Math.PI );

	}

	copy( source ) {

		super.copy( source );

		this.width = source.width;
		this.height = source.height;

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.object.width = this.width;
		data.object.height = this.height;

		return data;

	}

}

/**
 * Primary reference:
 *   https://graphics.stanford.edu/papers/envmap/envmap.pdf
 *
 * Secondary reference:
 *   https://www.ppsloan.org/publications/StupidSH36.pdf
 */

// 3-band SH defined by 9 coefficients

class SphericalHarmonics3 {

	constructor() {

		this.isSphericalHarmonics3 = true;

		this.coefficients = [];

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients.push( new Vector3() );

		}

	}

	set( coefficients ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].copy( coefficients[ i ] );

		}

		return this;

	}

	zero() {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].set( 0, 0, 0 );

		}

		return this;

	}

	// get the radiance in the direction of the normal
	// target is a Vector3
	getAt( normal, target ) {

		// normal is assumed to be unit length

		const x = normal.x, y = normal.y, z = normal.z;

		const coeff = this.coefficients;

		// band 0
		target.copy( coeff[ 0 ] ).multiplyScalar( 0.282095 );

		// band 1
		target.addScaledVector( coeff[ 1 ], 0.488603 * y );
		target.addScaledVector( coeff[ 2 ], 0.488603 * z );
		target.addScaledVector( coeff[ 3 ], 0.488603 * x );

		// band 2
		target.addScaledVector( coeff[ 4 ], 1.092548 * ( x * y ) );
		target.addScaledVector( coeff[ 5 ], 1.092548 * ( y * z ) );
		target.addScaledVector( coeff[ 6 ], 0.315392 * ( 3.0 * z * z - 1.0 ) );
		target.addScaledVector( coeff[ 7 ], 1.092548 * ( x * z ) );
		target.addScaledVector( coeff[ 8 ], 0.546274 * ( x * x - y * y ) );

		return target;

	}

	// get the irradiance (radiance convolved with cosine lobe) in the direction of the normal
	// target is a Vector3
	// https://graphics.stanford.edu/papers/envmap/envmap.pdf
	getIrradianceAt( normal, target ) {

		// normal is assumed to be unit length

		const x = normal.x, y = normal.y, z = normal.z;

		const coeff = this.coefficients;

		// band 0
		target.copy( coeff[ 0 ] ).multiplyScalar( 0.886227 ); // π * 0.282095

		// band 1
		target.addScaledVector( coeff[ 1 ], 2.0 * 0.511664 * y ); // ( 2 * π / 3 ) * 0.488603
		target.addScaledVector( coeff[ 2 ], 2.0 * 0.511664 * z );
		target.addScaledVector( coeff[ 3 ], 2.0 * 0.511664 * x );

		// band 2
		target.addScaledVector( coeff[ 4 ], 2.0 * 0.429043 * x * y ); // ( π / 4 ) * 1.092548
		target.addScaledVector( coeff[ 5 ], 2.0 * 0.429043 * y * z );
		target.addScaledVector( coeff[ 6 ], 0.743125 * z * z - 0.247708 ); // ( π / 4 ) * 0.315392 * 3
		target.addScaledVector( coeff[ 7 ], 2.0 * 0.429043 * x * z );
		target.addScaledVector( coeff[ 8 ], 0.429043 * ( x * x - y * y ) ); // ( π / 4 ) * 0.546274

		return target;

	}

	add( sh ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].add( sh.coefficients[ i ] );

		}

		return this;

	}

	addScaledSH( sh, s ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].addScaledVector( sh.coefficients[ i ], s );

		}

		return this;

	}

	scale( s ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].multiplyScalar( s );

		}

		return this;

	}

	lerp( sh, alpha ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.coefficients[ i ].lerp( sh.coefficients[ i ], alpha );

		}

		return this;

	}

	equals( sh ) {

		for ( let i = 0; i < 9; i ++ ) {

			if ( ! this.coefficients[ i ].equals( sh.coefficients[ i ] ) ) {

				return false;

			}

		}

		return true;

	}

	copy( sh ) {

		return this.set( sh.coefficients );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	fromArray( array, offset = 0 ) {

		const coefficients = this.coefficients;

		for ( let i = 0; i < 9; i ++ ) {

			coefficients[ i ].fromArray( array, offset + ( i * 3 ) );

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const coefficients = this.coefficients;

		for ( let i = 0; i < 9; i ++ ) {

			coefficients[ i ].toArray( array, offset + ( i * 3 ) );

		}

		return array;

	}

	// evaluate the basis functions
	// shBasis is an Array[ 9 ]
	static getBasisAt( normal, shBasis ) {

		// normal is assumed to be unit length

		const x = normal.x, y = normal.y, z = normal.z;

		// band 0
		shBasis[ 0 ] = 0.282095;

		// band 1
		shBasis[ 1 ] = 0.488603 * y;
		shBasis[ 2 ] = 0.488603 * z;
		shBasis[ 3 ] = 0.488603 * x;

		// band 2
		shBasis[ 4 ] = 1.092548 * x * y;
		shBasis[ 5 ] = 1.092548 * y * z;
		shBasis[ 6 ] = 0.315392 * ( 3 * z * z - 1 );
		shBasis[ 7 ] = 1.092548 * x * z;
		shBasis[ 8 ] = 0.546274 * ( x * x - y * y );

	}

}

class LightProbe extends Light {

	constructor( sh = new SphericalHarmonics3(), intensity = 1 ) {

		super( undefined, intensity );

		this.isLightProbe = true;

		this.sh = sh;

	}

	copy( source ) {

		super.copy( source );

		this.sh.copy( source.sh );

		return this;

	}

	fromJSON( json ) {

		this.intensity = json.intensity; // TODO: Move this bit to Light.fromJSON();
		this.sh.fromArray( json.sh );

		return this;

	}

	toJSON( meta ) {

		const data = super.toJSON( meta );

		data.object.sh = this.sh.toArray();

		return data;

	}

}

class MaterialLoader extends Loader {

	constructor( manager ) {

		super( manager );
		this.textures = {};

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const loader = new FileLoader( scope.manager );
		loader.setPath( scope.path );
		loader.setRequestHeader( scope.requestHeader );
		loader.setWithCredentials( scope.withCredentials );
		loader.load( url, function ( text ) {

			try {

				onLoad( scope.parse( JSON.parse( text ) ) );

			} catch ( e ) {

				if ( onError ) {

					onError( e );

				} else {

					console.error( e );

				}

				scope.manager.itemError( url );

			}

		}, onProgress, onError );

	}

	parse( json ) {

		const textures = this.textures;

		function getTexture( name ) {

			if ( textures[ name ] === undefined ) {

				console.warn( 'THREE.MaterialLoader: Undefined texture', name );

			}

			return textures[ name ];

		}

		const material = this.createMaterialFromType( json.type );

		if ( json.uuid !== undefined ) material.uuid = json.uuid;
		if ( json.name !== undefined ) material.name = json.name;
		if ( json.color !== undefined && material.color !== undefined ) material.color.setHex( json.color );
		if ( json.roughness !== undefined ) material.roughness = json.roughness;
		if ( json.metalness !== undefined ) material.metalness = json.metalness;
		if ( json.sheen !== undefined ) material.sheen = json.sheen;
		if ( json.sheenColor !== undefined ) material.sheenColor = new Color().setHex( json.sheenColor );
		if ( json.sheenRoughness !== undefined ) material.sheenRoughness = json.sheenRoughness;
		if ( json.emissive !== undefined && material.emissive !== undefined ) material.emissive.setHex( json.emissive );
		if ( json.specular !== undefined && material.specular !== undefined ) material.specular.setHex( json.specular );
		if ( json.specularIntensity !== undefined ) material.specularIntensity = json.specularIntensity;
		if ( json.specularColor !== undefined && material.specularColor !== undefined ) material.specularColor.setHex( json.specularColor );
		if ( json.shininess !== undefined ) material.shininess = json.shininess;
		if ( json.clearcoat !== undefined ) material.clearcoat = json.clearcoat;
		if ( json.clearcoatRoughness !== undefined ) material.clearcoatRoughness = json.clearcoatRoughness;
		if ( json.dispersion !== undefined ) material.dispersion = json.dispersion;
		if ( json.iridescence !== undefined ) material.iridescence = json.iridescence;
		if ( json.iridescenceIOR !== undefined ) material.iridescenceIOR = json.iridescenceIOR;
		if ( json.iridescenceThicknessRange !== undefined ) material.iridescenceThicknessRange = json.iridescenceThicknessRange;
		if ( json.transmission !== undefined ) material.transmission = json.transmission;
		if ( json.thickness !== undefined ) material.thickness = json.thickness;
		if ( json.attenuationDistance !== undefined ) material.attenuationDistance = json.attenuationDistance;
		if ( json.attenuationColor !== undefined && material.attenuationColor !== undefined ) material.attenuationColor.setHex( json.attenuationColor );
		if ( json.anisotropy !== undefined ) material.anisotropy = json.anisotropy;
		if ( json.anisotropyRotation !== undefined ) material.anisotropyRotation = json.anisotropyRotation;
		if ( json.fog !== undefined ) material.fog = json.fog;
		if ( json.flatShading !== undefined ) material.flatShading = json.flatShading;
		if ( json.blending !== undefined ) material.blending = json.blending;
		if ( json.combine !== undefined ) material.combine = json.combine;
		if ( json.side !== undefined ) material.side = json.side;
		if ( json.shadowSide !== undefined ) material.shadowSide = json.shadowSide;
		if ( json.opacity !== undefined ) material.opacity = json.opacity;
		if ( json.transparent !== undefined ) material.transparent = json.transparent;
		if ( json.alphaTest !== undefined ) material.alphaTest = json.alphaTest;
		if ( json.alphaHash !== undefined ) material.alphaHash = json.alphaHash;
		if ( json.depthFunc !== undefined ) material.depthFunc = json.depthFunc;
		if ( json.depthTest !== undefined ) material.depthTest = json.depthTest;
		if ( json.depthWrite !== undefined ) material.depthWrite = json.depthWrite;
		if ( json.colorWrite !== undefined ) material.colorWrite = json.colorWrite;
		if ( json.blendSrc !== undefined ) material.blendSrc = json.blendSrc;
		if ( json.blendDst !== undefined ) material.blendDst = json.blendDst;
		if ( json.blendEquation !== undefined ) material.blendEquation = json.blendEquation;
		if ( json.blendSrcAlpha !== undefined ) material.blendSrcAlpha = json.blendSrcAlpha;
		if ( json.blendDstAlpha !== undefined ) material.blendDstAlpha = json.blendDstAlpha;
		if ( json.blendEquationAlpha !== undefined ) material.blendEquationAlpha = json.blendEquationAlpha;
		if ( json.blendColor !== undefined && material.blendColor !== undefined ) material.blendColor.setHex( json.blendColor );
		if ( json.blendAlpha !== undefined ) material.blendAlpha = json.blendAlpha;
		if ( json.stencilWriteMask !== undefined ) material.stencilWriteMask = json.stencilWriteMask;
		if ( json.stencilFunc !== undefined ) material.stencilFunc = json.stencilFunc;
		if ( json.stencilRef !== undefined ) material.stencilRef = json.stencilRef;
		if ( json.stencilFuncMask !== undefined ) material.stencilFuncMask = json.stencilFuncMask;
		if ( json.stencilFail !== undefined ) material.stencilFail = json.stencilFail;
		if ( json.stencilZFail !== undefined ) material.stencilZFail = json.stencilZFail;
		if ( json.stencilZPass !== undefined ) material.stencilZPass = json.stencilZPass;
		if ( json.stencilWrite !== undefined ) material.stencilWrite = json.stencilWrite;

		if ( json.wireframe !== undefined ) material.wireframe = json.wireframe;
		if ( json.wireframeLinewidth !== undefined ) material.wireframeLinewidth = json.wireframeLinewidth;
		if ( json.wireframeLinecap !== undefined ) material.wireframeLinecap = json.wireframeLinecap;
		if ( json.wireframeLinejoin !== undefined ) material.wireframeLinejoin = json.wireframeLinejoin;

		if ( json.rotation !== undefined ) material.rotation = json.rotation;

		if ( json.linewidth !== undefined ) material.linewidth = json.linewidth;
		if ( json.dashSize !== undefined ) material.dashSize = json.dashSize;
		if ( json.gapSize !== undefined ) material.gapSize = json.gapSize;
		if ( json.scale !== undefined ) material.scale = json.scale;

		if ( json.polygonOffset !== undefined ) material.polygonOffset = json.polygonOffset;
		if ( json.polygonOffsetFactor !== undefined ) material.polygonOffsetFactor = json.polygonOffsetFactor;
		if ( json.polygonOffsetUnits !== undefined ) material.polygonOffsetUnits = json.polygonOffsetUnits;

		if ( json.dithering !== undefined ) material.dithering = json.dithering;

		if ( json.alphaToCoverage !== undefined ) material.alphaToCoverage = json.alphaToCoverage;
		if ( json.premultipliedAlpha !== undefined ) material.premultipliedAlpha = json.premultipliedAlpha;
		if ( json.forceSinglePass !== undefined ) material.forceSinglePass = json.forceSinglePass;

		if ( json.visible !== undefined ) material.visible = json.visible;

		if ( json.toneMapped !== undefined ) material.toneMapped = json.toneMapped;

		if ( json.userData !== undefined ) material.userData = json.userData;

		if ( json.vertexColors !== undefined ) {

			if ( typeof json.vertexColors === 'number' ) {

				material.vertexColors = ( json.vertexColors > 0 ) ? true : false;

			} else {

				material.vertexColors = json.vertexColors;

			}

		}

		// Shader Material

		if ( json.uniforms !== undefined ) {

			for ( const name in json.uniforms ) {

				const uniform = json.uniforms[ name ];

				material.uniforms[ name ] = {};

				switch ( uniform.type ) {

					case 't':
						material.uniforms[ name ].value = getTexture( uniform.value );
						break;

					case 'c':
						material.uniforms[ name ].value = new Color().setHex( uniform.value );
						break;

					case 'v2':
						material.uniforms[ name ].value = new Vector2().fromArray( uniform.value );
						break;

					case 'v3':
						material.uniforms[ name ].value = new Vector3().fromArray( uniform.value );
						break;

					case 'v4':
						material.uniforms[ name ].value = new Vector4().fromArray( uniform.value );
						break;

					case 'm3':
						material.uniforms[ name ].value = new Matrix3().fromArray( uniform.value );
						break;

					case 'm4':
						material.uniforms[ name ].value = new Matrix4().fromArray( uniform.value );
						break;

					default:
						material.uniforms[ name ].value = uniform.value;

				}

			}

		}

		if ( json.defines !== undefined ) material.defines = json.defines;
		if ( json.vertexShader !== undefined ) material.vertexShader = json.vertexShader;
		if ( json.fragmentShader !== undefined ) material.fragmentShader = json.fragmentShader;
		if ( json.glslVersion !== undefined ) material.glslVersion = json.glslVersion;

		if ( json.extensions !== undefined ) {

			for ( const key in json.extensions ) {

				material.extensions[ key ] = json.extensions[ key ];

			}

		}

		if ( json.lights !== undefined ) material.lights = json.lights;
		if ( json.clipping !== undefined ) material.clipping = json.clipping;

		// for PointsMaterial

		if ( json.size !== undefined ) material.size = json.size;
		if ( json.sizeAttenuation !== undefined ) material.sizeAttenuation = json.sizeAttenuation;

		// maps

		if ( json.map !== undefined ) material.map = getTexture( json.map );
		if ( json.matcap !== undefined ) material.matcap = getTexture( json.matcap );

		if ( json.alphaMap !== undefined ) material.alphaMap = getTexture( json.alphaMap );

		if ( json.bumpMap !== undefined ) material.bumpMap = getTexture( json.bumpMap );
		if ( json.bumpScale !== undefined ) material.bumpScale = json.bumpScale;

		if ( json.normalMap !== undefined ) material.normalMap = getTexture( json.normalMap );
		if ( json.normalMapType !== undefined ) material.normalMapType = json.normalMapType;
		if ( json.normalScale !== undefined ) {

			let normalScale = json.normalScale;

			if ( Array.isArray( normalScale ) === false ) {

				// Blender exporter used to export a scalar. See #7459

				normalScale = [ normalScale, normalScale ];

			}

			material.normalScale = new Vector2().fromArray( normalScale );

		}

		if ( json.displacementMap !== undefined ) material.displacementMap = getTexture( json.displacementMap );
		if ( json.displacementScale !== undefined ) material.displacementScale = json.displacementScale;
		if ( json.displacementBias !== undefined ) material.displacementBias = json.displacementBias;

		if ( json.roughnessMap !== undefined ) material.roughnessMap = getTexture( json.roughnessMap );
		if ( json.metalnessMap !== undefined ) material.metalnessMap = getTexture( json.metalnessMap );

		if ( json.emissiveMap !== undefined ) material.emissiveMap = getTexture( json.emissiveMap );
		if ( json.emissiveIntensity !== undefined ) material.emissiveIntensity = json.emissiveIntensity;

		if ( json.specularMap !== undefined ) material.specularMap = getTexture( json.specularMap );
		if ( json.specularIntensityMap !== undefined ) material.specularIntensityMap = getTexture( json.specularIntensityMap );
		if ( json.specularColorMap !== undefined ) material.specularColorMap = getTexture( json.specularColorMap );

		if ( json.envMap !== undefined ) material.envMap = getTexture( json.envMap );
		if ( json.envMapRotation !== undefined ) material.envMapRotation.fromArray( json.envMapRotation );
		if ( json.envMapIntensity !== undefined ) material.envMapIntensity = json.envMapIntensity;

		if ( json.reflectivity !== undefined ) material.reflectivity = json.reflectivity;
		if ( json.refractionRatio !== undefined ) material.refractionRatio = json.refractionRatio;

		if ( json.lightMap !== undefined ) material.lightMap = getTexture( json.lightMap );
		if ( json.lightMapIntensity !== undefined ) material.lightMapIntensity = json.lightMapIntensity;

		if ( json.aoMap !== undefined ) material.aoMap = getTexture( json.aoMap );
		if ( json.aoMapIntensity !== undefined ) material.aoMapIntensity = json.aoMapIntensity;

		if ( json.gradientMap !== undefined ) material.gradientMap = getTexture( json.gradientMap );

		if ( json.clearcoatMap !== undefined ) material.clearcoatMap = getTexture( json.clearcoatMap );
		if ( json.clearcoatRoughnessMap !== undefined ) material.clearcoatRoughnessMap = getTexture( json.clearcoatRoughnessMap );
		if ( json.clearcoatNormalMap !== undefined ) material.clearcoatNormalMap = getTexture( json.clearcoatNormalMap );
		if ( json.clearcoatNormalScale !== undefined ) material.clearcoatNormalScale = new Vector2().fromArray( json.clearcoatNormalScale );

		if ( json.iridescenceMap !== undefined ) material.iridescenceMap = getTexture( json.iridescenceMap );
		if ( json.iridescenceThicknessMap !== undefined ) material.iridescenceThicknessMap = getTexture( json.iridescenceThicknessMap );

		if ( json.transmissionMap !== undefined ) material.transmissionMap = getTexture( json.transmissionMap );
		if ( json.thicknessMap !== undefined ) material.thicknessMap = getTexture( json.thicknessMap );

		if ( json.anisotropyMap !== undefined ) material.anisotropyMap = getTexture( json.anisotropyMap );

		if ( json.sheenColorMap !== undefined ) material.sheenColorMap = getTexture( json.sheenColorMap );
		if ( json.sheenRoughnessMap !== undefined ) material.sheenRoughnessMap = getTexture( json.sheenRoughnessMap );

		return material;

	}

	setTextures( value ) {

		this.textures = value;
		return this;

	}

	createMaterialFromType( type ) {

		return MaterialLoader.createMaterialFromType( type );

	}

	static createMaterialFromType( type ) {

		const materialLib = {
			ShadowMaterial,
			SpriteMaterial,
			RawShaderMaterial,
			ShaderMaterial,
			PointsMaterial,
			MeshPhysicalMaterial,
			MeshStandardMaterial,
			MeshPhongMaterial,
			MeshToonMaterial,
			MeshNormalMaterial,
			MeshLambertMaterial,
			MeshDepthMaterial,
			MeshDistanceMaterial,
			MeshBasicMaterial,
			MeshMatcapMaterial,
			LineDashedMaterial,
			LineBasicMaterial,
			Material
		};

		return new materialLib[ type ]();

	}

}

class LoaderUtils {

	static decodeText( array ) { // @deprecated, r165

		console.warn( 'THREE.LoaderUtils: decodeText() has been deprecated with r165 and will be removed with r175. Use TextDecoder instead.' );

		if ( typeof TextDecoder !== 'undefined' ) {

			return new TextDecoder().decode( array );

		}

		// Avoid the String.fromCharCode.apply(null, array) shortcut, which
		// throws a "maximum call stack size exceeded" error for large arrays.

		let s = '';

		for ( let i = 0, il = array.length; i < il; i ++ ) {

			// Implicitly assumes little-endian.
			s += String.fromCharCode( array[ i ] );

		}

		try {

			// merges multi-byte utf-8 characters.

			return decodeURIComponent( escape( s ) );

		} catch ( e ) { // see #16358

			return s;

		}

	}

	static extractUrlBase( url ) {

		const index = url.lastIndexOf( '/' );

		if ( index === - 1 ) return './';

		return url.slice( 0, index + 1 );

	}

	static resolveURL( url, path ) {

		// Invalid URL
		if ( typeof url !== 'string' || url === '' ) return '';

		// Host Relative URL
		if ( /^https?:\/\//i.test( path ) && /^\//.test( url ) ) {

			path = path.replace( /(^https?:\/\/[^\/]+).*/i, '$1' );

		}

		// Absolute URL http://,https://,//
		if ( /^(https?:)?\/\//i.test( url ) ) return url;

		// Data URI
		if ( /^data:.*,.*$/i.test( url ) ) return url;

		// Blob URL
		if ( /^blob:.*$/i.test( url ) ) return url;

		// Relative URL
		return path + url;

	}

}

class InstancedBufferGeometry extends BufferGeometry {

	constructor() {

		super();

		this.isInstancedBufferGeometry = true;

		this.type = 'InstancedBufferGeometry';
		this.instanceCount = Infinity;

	}

	copy( source ) {

		super.copy( source );

		this.instanceCount = source.instanceCount;

		return this;

	}

	toJSON() {

		const data = super.toJSON();

		data.instanceCount = this.instanceCount;

		data.isInstancedBufferGeometry = true;

		return data;

	}

}

class BufferGeometryLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const loader = new FileLoader( scope.manager );
		loader.setPath( scope.path );
		loader.setRequestHeader( scope.requestHeader );
		loader.setWithCredentials( scope.withCredentials );
		loader.load( url, function ( text ) {

			try {

				onLoad( scope.parse( JSON.parse( text ) ) );

			} catch ( e ) {

				if ( onError ) {

					onError( e );

				} else {

					console.error( e );

				}

				scope.manager.itemError( url );

			}

		}, onProgress, onError );

	}

	parse( json ) {

		const interleavedBufferMap = {};
		const arrayBufferMap = {};

		function getInterleavedBuffer( json, uuid ) {

			if ( interleavedBufferMap[ uuid ] !== undefined ) return interleavedBufferMap[ uuid ];

			const interleavedBuffers = json.interleavedBuffers;
			const interleavedBuffer = interleavedBuffers[ uuid ];

			const buffer = getArrayBuffer( json, interleavedBuffer.buffer );

			const array = getTypedArray( interleavedBuffer.type, buffer );
			const ib = new InterleavedBuffer( array, interleavedBuffer.stride );
			ib.uuid = interleavedBuffer.uuid;

			interleavedBufferMap[ uuid ] = ib;

			return ib;

		}

		function getArrayBuffer( json, uuid ) {

			if ( arrayBufferMap[ uuid ] !== undefined ) return arrayBufferMap[ uuid ];

			const arrayBuffers = json.arrayBuffers;
			const arrayBuffer = arrayBuffers[ uuid ];

			const ab = new Uint32Array( arrayBuffer ).buffer;

			arrayBufferMap[ uuid ] = ab;

			return ab;

		}

		const geometry = json.isInstancedBufferGeometry ? new InstancedBufferGeometry() : new BufferGeometry();

		const index = json.data.index;

		if ( index !== undefined ) {

			const typedArray = getTypedArray( index.type, index.array );
			geometry.setIndex( new BufferAttribute( typedArray, 1 ) );

		}

		const attributes = json.data.attributes;

		for ( const key in attributes ) {

			const attribute = attributes[ key ];
			let bufferAttribute;

			if ( attribute.isInterleavedBufferAttribute ) {

				const interleavedBuffer = getInterleavedBuffer( json.data, attribute.data );
				bufferAttribute = new InterleavedBufferAttribute( interleavedBuffer, attribute.itemSize, attribute.offset, attribute.normalized );

			} else {

				const typedArray = getTypedArray( attribute.type, attribute.array );
				const bufferAttributeConstr = attribute.isInstancedBufferAttribute ? InstancedBufferAttribute : BufferAttribute;
				bufferAttribute = new bufferAttributeConstr( typedArray, attribute.itemSize, attribute.normalized );

			}

			if ( attribute.name !== undefined ) bufferAttribute.name = attribute.name;
			if ( attribute.usage !== undefined ) bufferAttribute.setUsage( attribute.usage );

			geometry.setAttribute( key, bufferAttribute );

		}

		const morphAttributes = json.data.morphAttributes;

		if ( morphAttributes ) {

			for ( const key in morphAttributes ) {

				const attributeArray = morphAttributes[ key ];

				const array = [];

				for ( let i = 0, il = attributeArray.length; i < il; i ++ ) {

					const attribute = attributeArray[ i ];
					let bufferAttribute;

					if ( attribute.isInterleavedBufferAttribute ) {

						const interleavedBuffer = getInterleavedBuffer( json.data, attribute.data );
						bufferAttribute = new InterleavedBufferAttribute( interleavedBuffer, attribute.itemSize, attribute.offset, attribute.normalized );

					} else {

						const typedArray = getTypedArray( attribute.type, attribute.array );
						bufferAttribute = new BufferAttribute( typedArray, attribute.itemSize, attribute.normalized );

					}

					if ( attribute.name !== undefined ) bufferAttribute.name = attribute.name;
					array.push( bufferAttribute );

				}

				geometry.morphAttributes[ key ] = array;

			}

		}

		const morphTargetsRelative = json.data.morphTargetsRelative;

		if ( morphTargetsRelative ) {

			geometry.morphTargetsRelative = true;

		}

		const groups = json.data.groups || json.data.drawcalls || json.data.offsets;

		if ( groups !== undefined ) {

			for ( let i = 0, n = groups.length; i !== n; ++ i ) {

				const group = groups[ i ];

				geometry.addGroup( group.start, group.count, group.materialIndex );

			}

		}

		const boundingSphere = json.data.boundingSphere;

		if ( boundingSphere !== undefined ) {

			const center = new Vector3();

			if ( boundingSphere.center !== undefined ) {

				center.fromArray( boundingSphere.center );

			}

			geometry.boundingSphere = new Sphere( center, boundingSphere.radius );

		}

		if ( json.name ) geometry.name = json.name;
		if ( json.userData ) geometry.userData = json.userData;

		return geometry;

	}

}

class ObjectLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const path = ( this.path === '' ) ? LoaderUtils.extractUrlBase( url ) : this.path;
		this.resourcePath = this.resourcePath || path;

		const loader = new FileLoader( this.manager );
		loader.setPath( this.path );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( this.withCredentials );
		loader.load( url, function ( text ) {

			let json = null;

			try {

				json = JSON.parse( text );

			} catch ( error ) {

				if ( onError !== undefined ) onError( error );

				console.error( 'THREE:ObjectLoader: Can\'t parse ' + url + '.', error.message );

				return;

			}

			const metadata = json.metadata;

			if ( metadata === undefined || metadata.type === undefined || metadata.type.toLowerCase() === 'geometry' ) {

				if ( onError !== undefined ) onError( new Error( 'THREE.ObjectLoader: Can\'t load ' + url ) );

				console.error( 'THREE.ObjectLoader: Can\'t load ' + url );
				return;

			}

			scope.parse( json, onLoad );

		}, onProgress, onError );

	}

	async loadAsync( url, onProgress ) {

		const scope = this;

		const path = ( this.path === '' ) ? LoaderUtils.extractUrlBase( url ) : this.path;
		this.resourcePath = this.resourcePath || path;

		const loader = new FileLoader( this.manager );
		loader.setPath( this.path );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( this.withCredentials );

		const text = await loader.loadAsync( url, onProgress );

		const json = JSON.parse( text );

		const metadata = json.metadata;

		if ( metadata === undefined || metadata.type === undefined || metadata.type.toLowerCase() === 'geometry' ) {

			throw new Error( 'THREE.ObjectLoader: Can\'t load ' + url );

		}

		return await scope.parseAsync( json );

	}

	parse( json, onLoad ) {

		const animations = this.parseAnimations( json.animations );
		const shapes = this.parseShapes( json.shapes );
		const geometries = this.parseGeometries( json.geometries, shapes );

		const images = this.parseImages( json.images, function () {

			if ( onLoad !== undefined ) onLoad( object );

		} );

		const textures = this.parseTextures( json.textures, images );
		const materials = this.parseMaterials( json.materials, textures );

		const object = this.parseObject( json.object, geometries, materials, textures, animations );
		const skeletons = this.parseSkeletons( json.skeletons, object );

		this.bindSkeletons( object, skeletons );
		this.bindLightTargets( object );

		//

		if ( onLoad !== undefined ) {

			let hasImages = false;

			for ( const uuid in images ) {

				if ( images[ uuid ].data instanceof HTMLImageElement ) {

					hasImages = true;
					break;

				}

			}

			if ( hasImages === false ) onLoad( object );

		}

		return object;

	}

	async parseAsync( json ) {

		const animations = this.parseAnimations( json.animations );
		const shapes = this.parseShapes( json.shapes );
		const geometries = this.parseGeometries( json.geometries, shapes );

		const images = await this.parseImagesAsync( json.images );

		const textures = this.parseTextures( json.textures, images );
		const materials = this.parseMaterials( json.materials, textures );

		const object = this.parseObject( json.object, geometries, materials, textures, animations );
		const skeletons = this.parseSkeletons( json.skeletons, object );

		this.bindSkeletons( object, skeletons );
		this.bindLightTargets( object );

		return object;

	}

	parseShapes( json ) {

		const shapes = {};

		if ( json !== undefined ) {

			for ( let i = 0, l = json.length; i < l; i ++ ) {

				const shape = new Shape().fromJSON( json[ i ] );

				shapes[ shape.uuid ] = shape;

			}

		}

		return shapes;

	}

	parseSkeletons( json, object ) {

		const skeletons = {};
		const bones = {};

		// generate bone lookup table

		object.traverse( function ( child ) {

			if ( child.isBone ) bones[ child.uuid ] = child;

		} );

		// create skeletons

		if ( json !== undefined ) {

			for ( let i = 0, l = json.length; i < l; i ++ ) {

				const skeleton = new Skeleton().fromJSON( json[ i ], bones );

				skeletons[ skeleton.uuid ] = skeleton;

			}

		}

		return skeletons;

	}

	parseGeometries( json, shapes ) {

		const geometries = {};

		if ( json !== undefined ) {

			const bufferGeometryLoader = new BufferGeometryLoader();

			for ( let i = 0, l = json.length; i < l; i ++ ) {

				let geometry;
				const data = json[ i ];

				switch ( data.type ) {

					case 'BufferGeometry':
					case 'InstancedBufferGeometry':

						geometry = bufferGeometryLoader.parse( data );
						break;

					default:

						if ( data.type in Geometries ) {

							geometry = Geometries[ data.type ].fromJSON( data, shapes );

						} else {

							console.warn( `THREE.ObjectLoader: Unsupported geometry type "${ data.type }"` );

						}

				}

				geometry.uuid = data.uuid;

				if ( data.name !== undefined ) geometry.name = data.name;
				if ( data.userData !== undefined ) geometry.userData = data.userData;

				geometries[ data.uuid ] = geometry;

			}

		}

		return geometries;

	}

	parseMaterials( json, textures ) {

		const cache = {}; // MultiMaterial
		const materials = {};

		if ( json !== undefined ) {

			const loader = new MaterialLoader();
			loader.setTextures( textures );

			for ( let i = 0, l = json.length; i < l; i ++ ) {

				const data = json[ i ];

				if ( cache[ data.uuid ] === undefined ) {

					cache[ data.uuid ] = loader.parse( data );

				}

				materials[ data.uuid ] = cache[ data.uuid ];

			}

		}

		return materials;

	}

	parseAnimations( json ) {

		const animations = {};

		if ( json !== undefined ) {

			for ( let i = 0; i < json.length; i ++ ) {

				const data = json[ i ];

				const clip = AnimationClip.parse( data );

				animations[ clip.uuid ] = clip;

			}

		}

		return animations;

	}

	parseImages( json, onLoad ) {

		const scope = this;
		const images = {};

		let loader;

		function loadImage( url ) {

			scope.manager.itemStart( url );

			return loader.load( url, function () {

				scope.manager.itemEnd( url );

			}, undefined, function () {

				scope.manager.itemError( url );
				scope.manager.itemEnd( url );

			} );

		}

		function deserializeImage( image ) {

			if ( typeof image === 'string' ) {

				const url = image;

				const path = /^(\/\/)|([a-z]+:(\/\/)?)/i.test( url ) ? url : scope.resourcePath + url;

				return loadImage( path );

			} else {

				if ( image.data ) {

					return {
						data: getTypedArray( image.type, image.data ),
						width: image.width,
						height: image.height
					};

				} else {

					return null;

				}

			}

		}

		if ( json !== undefined && json.length > 0 ) {

			const manager = new LoadingManager( onLoad );

			loader = new ImageLoader( manager );
			loader.setCrossOrigin( this.crossOrigin );

			for ( let i = 0, il = json.length; i < il; i ++ ) {

				const image = json[ i ];
				const url = image.url;

				if ( Array.isArray( url ) ) {

					// load array of images e.g CubeTexture

					const imageArray = [];

					for ( let j = 0, jl = url.length; j < jl; j ++ ) {

						const currentUrl = url[ j ];

						const deserializedImage = deserializeImage( currentUrl );

						if ( deserializedImage !== null ) {

							if ( deserializedImage instanceof HTMLImageElement ) {

								imageArray.push( deserializedImage );

							} else {

								// special case: handle array of data textures for cube textures

								imageArray.push( new DataTexture( deserializedImage.data, deserializedImage.width, deserializedImage.height ) );

							}

						}

					}

					images[ image.uuid ] = new Source( imageArray );

				} else {

					// load single image

					const deserializedImage = deserializeImage( image.url );
					images[ image.uuid ] = new Source( deserializedImage );


				}

			}

		}

		return images;

	}

	async parseImagesAsync( json ) {

		const scope = this;
		const images = {};

		let loader;

		async function deserializeImage( image ) {

			if ( typeof image === 'string' ) {

				const url = image;

				const path = /^(\/\/)|([a-z]+:(\/\/)?)/i.test( url ) ? url : scope.resourcePath + url;

				return await loader.loadAsync( path );

			} else {

				if ( image.data ) {

					return {
						data: getTypedArray( image.type, image.data ),
						width: image.width,
						height: image.height
					};

				} else {

					return null;

				}

			}

		}

		if ( json !== undefined && json.length > 0 ) {

			loader = new ImageLoader( this.manager );
			loader.setCrossOrigin( this.crossOrigin );

			for ( let i = 0, il = json.length; i < il; i ++ ) {

				const image = json[ i ];
				const url = image.url;

				if ( Array.isArray( url ) ) {

					// load array of images e.g CubeTexture

					const imageArray = [];

					for ( let j = 0, jl = url.length; j < jl; j ++ ) {

						const currentUrl = url[ j ];

						const deserializedImage = await deserializeImage( currentUrl );

						if ( deserializedImage !== null ) {

							if ( deserializedImage instanceof HTMLImageElement ) {

								imageArray.push( deserializedImage );

							} else {

								// special case: handle array of data textures for cube textures

								imageArray.push( new DataTexture( deserializedImage.data, deserializedImage.width, deserializedImage.height ) );

							}

						}

					}

					images[ image.uuid ] = new Source( imageArray );

				} else {

					// load single image

					const deserializedImage = await deserializeImage( image.url );
					images[ image.uuid ] = new Source( deserializedImage );

				}

			}

		}

		return images;

	}

	parseTextures( json, images ) {

		function parseConstant( value, type ) {

			if ( typeof value === 'number' ) return value;

			console.warn( 'THREE.ObjectLoader.parseTexture: Constant should be in numeric form.', value );

			return type[ value ];

		}

		const textures = {};

		if ( json !== undefined ) {

			for ( let i = 0, l = json.length; i < l; i ++ ) {

				const data = json[ i ];

				if ( data.image === undefined ) {

					console.warn( 'THREE.ObjectLoader: No "image" specified for', data.uuid );

				}

				if ( images[ data.image ] === undefined ) {

					console.warn( 'THREE.ObjectLoader: Undefined image', data.image );

				}

				const source = images[ data.image ];
				const image = source.data;

				let texture;

				if ( Array.isArray( image ) ) {

					texture = new CubeTexture();

					if ( image.length === 6 ) texture.needsUpdate = true;

				} else {

					if ( image && image.data ) {

						texture = new DataTexture();

					} else {

						texture = new Texture();

					}

					if ( image ) texture.needsUpdate = true; // textures can have undefined image data

				}

				texture.source = source;

				texture.uuid = data.uuid;

				if ( data.name !== undefined ) texture.name = data.name;

				if ( data.mapping !== undefined ) texture.mapping = parseConstant( data.mapping, TEXTURE_MAPPING );
				if ( data.channel !== undefined ) texture.channel = data.channel;

				if ( data.offset !== undefined ) texture.offset.fromArray( data.offset );
				if ( data.repeat !== undefined ) texture.repeat.fromArray( data.repeat );
				if ( data.center !== undefined ) texture.center.fromArray( data.center );
				if ( data.rotation !== undefined ) texture.rotation = data.rotation;

				if ( data.wrap !== undefined ) {

					texture.wrapS = parseConstant( data.wrap[ 0 ], TEXTURE_WRAPPING );
					texture.wrapT = parseConstant( data.wrap[ 1 ], TEXTURE_WRAPPING );

				}

				if ( data.format !== undefined ) texture.format = data.format;
				if ( data.internalFormat !== undefined ) texture.internalFormat = data.internalFormat;
				if ( data.type !== undefined ) texture.type = data.type;
				if ( data.colorSpace !== undefined ) texture.colorSpace = data.colorSpace;

				if ( data.minFilter !== undefined ) texture.minFilter = parseConstant( data.minFilter, TEXTURE_FILTER );
				if ( data.magFilter !== undefined ) texture.magFilter = parseConstant( data.magFilter, TEXTURE_FILTER );
				if ( data.anisotropy !== undefined ) texture.anisotropy = data.anisotropy;

				if ( data.flipY !== undefined ) texture.flipY = data.flipY;

				if ( data.generateMipmaps !== undefined ) texture.generateMipmaps = data.generateMipmaps;
				if ( data.premultiplyAlpha !== undefined ) texture.premultiplyAlpha = data.premultiplyAlpha;
				if ( data.unpackAlignment !== undefined ) texture.unpackAlignment = data.unpackAlignment;
				if ( data.compareFunction !== undefined ) texture.compareFunction = data.compareFunction;

				if ( data.userData !== undefined ) texture.userData = data.userData;

				textures[ data.uuid ] = texture;

			}

		}

		return textures;

	}

	parseObject( data, geometries, materials, textures, animations ) {

		let object;

		function getGeometry( name ) {

			if ( geometries[ name ] === undefined ) {

				console.warn( 'THREE.ObjectLoader: Undefined geometry', name );

			}

			return geometries[ name ];

		}

		function getMaterial( name ) {

			if ( name === undefined ) return undefined;

			if ( Array.isArray( name ) ) {

				const array = [];

				for ( let i = 0, l = name.length; i < l; i ++ ) {

					const uuid = name[ i ];

					if ( materials[ uuid ] === undefined ) {

						console.warn( 'THREE.ObjectLoader: Undefined material', uuid );

					}

					array.push( materials[ uuid ] );

				}

				return array;

			}

			if ( materials[ name ] === undefined ) {

				console.warn( 'THREE.ObjectLoader: Undefined material', name );

			}

			return materials[ name ];

		}

		function getTexture( uuid ) {

			if ( textures[ uuid ] === undefined ) {

				console.warn( 'THREE.ObjectLoader: Undefined texture', uuid );

			}

			return textures[ uuid ];

		}

		let geometry, material;

		switch ( data.type ) {

			case 'Scene':

				object = new Scene();

				if ( data.background !== undefined ) {

					if ( Number.isInteger( data.background ) ) {

						object.background = new Color( data.background );

					} else {

						object.background = getTexture( data.background );

					}

				}

				if ( data.environment !== undefined ) {

					object.environment = getTexture( data.environment );

				}

				if ( data.fog !== undefined ) {

					if ( data.fog.type === 'Fog' ) {

						object.fog = new Fog( data.fog.color, data.fog.near, data.fog.far );

					} else if ( data.fog.type === 'FogExp2' ) {

						object.fog = new FogExp2( data.fog.color, data.fog.density );

					}

					if ( data.fog.name !== '' ) {

						object.fog.name = data.fog.name;

					}

				}

				if ( data.backgroundBlurriness !== undefined ) object.backgroundBlurriness = data.backgroundBlurriness;
				if ( data.backgroundIntensity !== undefined ) object.backgroundIntensity = data.backgroundIntensity;
				if ( data.backgroundRotation !== undefined ) object.backgroundRotation.fromArray( data.backgroundRotation );

				if ( data.environmentIntensity !== undefined ) object.environmentIntensity = data.environmentIntensity;
				if ( data.environmentRotation !== undefined ) object.environmentRotation.fromArray( data.environmentRotation );

				break;

			case 'PerspectiveCamera':

				object = new PerspectiveCamera( data.fov, data.aspect, data.near, data.far );

				if ( data.focus !== undefined ) object.focus = data.focus;
				if ( data.zoom !== undefined ) object.zoom = data.zoom;
				if ( data.filmGauge !== undefined ) object.filmGauge = data.filmGauge;
				if ( data.filmOffset !== undefined ) object.filmOffset = data.filmOffset;
				if ( data.view !== undefined ) object.view = Object.assign( {}, data.view );

				break;

			case 'OrthographicCamera':

				object = new OrthographicCamera( data.left, data.right, data.top, data.bottom, data.near, data.far );

				if ( data.zoom !== undefined ) object.zoom = data.zoom;
				if ( data.view !== undefined ) object.view = Object.assign( {}, data.view );

				break;

			case 'AmbientLight':

				object = new AmbientLight( data.color, data.intensity );

				break;

			case 'DirectionalLight':

				object = new DirectionalLight( data.color, data.intensity );
				object.target = data.target || '';

				break;

			case 'PointLight':

				object = new PointLight( data.color, data.intensity, data.distance, data.decay );

				break;

			case 'RectAreaLight':

				object = new RectAreaLight( data.color, data.intensity, data.width, data.height );

				break;

			case 'SpotLight':

				object = new SpotLight( data.color, data.intensity, data.distance, data.angle, data.penumbra, data.decay );
				object.target = data.target || '';

				break;

			case 'HemisphereLight':

				object = new HemisphereLight( data.color, data.groundColor, data.intensity );

				break;

			case 'LightProbe':

				object = new LightProbe().fromJSON( data );

				break;

			case 'SkinnedMesh':

				geometry = getGeometry( data.geometry );
			 	material = getMaterial( data.material );

				object = new SkinnedMesh( geometry, material );

				if ( data.bindMode !== undefined ) object.bindMode = data.bindMode;
				if ( data.bindMatrix !== undefined ) object.bindMatrix.fromArray( data.bindMatrix );
				if ( data.skeleton !== undefined ) object.skeleton = data.skeleton;

				break;

			case 'Mesh':

				geometry = getGeometry( data.geometry );
				material = getMaterial( data.material );

				object = new Mesh( geometry, material );

				break;

			case 'InstancedMesh':

				geometry = getGeometry( data.geometry );
				material = getMaterial( data.material );
				const count = data.count;
				const instanceMatrix = data.instanceMatrix;
				const instanceColor = data.instanceColor;

				object = new InstancedMesh( geometry, material, count );
				object.instanceMatrix = new InstancedBufferAttribute( new Float32Array( instanceMatrix.array ), 16 );
				if ( instanceColor !== undefined ) object.instanceColor = new InstancedBufferAttribute( new Float32Array( instanceColor.array ), instanceColor.itemSize );

				break;

			case 'BatchedMesh':

				geometry = getGeometry( data.geometry );
				material = getMaterial( data.material );

				object = new BatchedMesh( data.maxInstanceCount, data.maxVertexCount, data.maxIndexCount, material );
				object.geometry = geometry;
				object.perObjectFrustumCulled = data.perObjectFrustumCulled;
				object.sortObjects = data.sortObjects;

				object._drawRanges = data.drawRanges;
				object._reservedRanges = data.reservedRanges;

				object._visibility = data.visibility;
				object._active = data.active;
				object._bounds = data.bounds.map( bound => {

					const box = new Box3();
					box.min.fromArray( bound.boxMin );
					box.max.fromArray( bound.boxMax );

					const sphere = new Sphere();
					sphere.radius = bound.sphereRadius;
					sphere.center.fromArray( bound.sphereCenter );

					return {
						boxInitialized: bound.boxInitialized,
						box: box,

						sphereInitialized: bound.sphereInitialized,
						sphere: sphere
					};

				} );

				object._maxInstanceCount = data.maxInstanceCount;
				object._maxVertexCount = data.maxVertexCount;
				object._maxIndexCount = data.maxIndexCount;

				object._geometryInitialized = data.geometryInitialized;
				object._geometryCount = data.geometryCount;

				object._matricesTexture = getTexture( data.matricesTexture.uuid );
				if ( data.colorsTexture !== undefined ) object._colorsTexture = getTexture( data.colorsTexture.uuid );

				break;

			case 'LOD':

				object = new LOD();

				break;

			case 'Line':

				object = new Line( getGeometry( data.geometry ), getMaterial( data.material ) );

				break;

			case 'LineLoop':

				object = new LineLoop( getGeometry( data.geometry ), getMaterial( data.material ) );

				break;

			case 'LineSegments':

				object = new LineSegments( getGeometry( data.geometry ), getMaterial( data.material ) );

				break;

			case 'PointCloud':
			case 'Points':

				object = new Points( getGeometry( data.geometry ), getMaterial( data.material ) );

				break;

			case 'Sprite':

				object = new Sprite( getMaterial( data.material ) );

				break;

			case 'Group':

				object = new Group();

				break;

			case 'Bone':

				object = new Bone();

				break;

			default:

				object = new Object3D();

		}

		object.uuid = data.uuid;

		if ( data.name !== undefined ) object.name = data.name;

		if ( data.matrix !== undefined ) {

			object.matrix.fromArray( data.matrix );

			if ( data.matrixAutoUpdate !== undefined ) object.matrixAutoUpdate = data.matrixAutoUpdate;
			if ( object.matrixAutoUpdate ) object.matrix.decompose( object.position, object.quaternion, object.scale );

		} else {

			if ( data.position !== undefined ) object.position.fromArray( data.position );
			if ( data.rotation !== undefined ) object.rotation.fromArray( data.rotation );
			if ( data.quaternion !== undefined ) object.quaternion.fromArray( data.quaternion );
			if ( data.scale !== undefined ) object.scale.fromArray( data.scale );

		}

		if ( data.up !== undefined ) object.up.fromArray( data.up );

		if ( data.castShadow !== undefined ) object.castShadow = data.castShadow;
		if ( data.receiveShadow !== undefined ) object.receiveShadow = data.receiveShadow;

		if ( data.shadow ) {

			if ( data.shadow.intensity !== undefined ) object.shadow.intensity = data.shadow.intensity;
			if ( data.shadow.bias !== undefined ) object.shadow.bias = data.shadow.bias;
			if ( data.shadow.normalBias !== undefined ) object.shadow.normalBias = data.shadow.normalBias;
			if ( data.shadow.radius !== undefined ) object.shadow.radius = data.shadow.radius;
			if ( data.shadow.mapSize !== undefined ) object.shadow.mapSize.fromArray( data.shadow.mapSize );
			if ( data.shadow.camera !== undefined ) object.shadow.camera = this.parseObject( data.shadow.camera );

		}

		if ( data.visible !== undefined ) object.visible = data.visible;
		if ( data.frustumCulled !== undefined ) object.frustumCulled = data.frustumCulled;
		if ( data.renderOrder !== undefined ) object.renderOrder = data.renderOrder;
		if ( data.userData !== undefined ) object.userData = data.userData;
		if ( data.layers !== undefined ) object.layers.mask = data.layers;

		if ( data.children !== undefined ) {

			const children = data.children;

			for ( let i = 0; i < children.length; i ++ ) {

				object.add( this.parseObject( children[ i ], geometries, materials, textures, animations ) );

			}

		}

		if ( data.animations !== undefined ) {

			const objectAnimations = data.animations;

			for ( let i = 0; i < objectAnimations.length; i ++ ) {

				const uuid = objectAnimations[ i ];

				object.animations.push( animations[ uuid ] );

			}

		}

		if ( data.type === 'LOD' ) {

			if ( data.autoUpdate !== undefined ) object.autoUpdate = data.autoUpdate;

			const levels = data.levels;

			for ( let l = 0; l < levels.length; l ++ ) {

				const level = levels[ l ];
				const child = object.getObjectByProperty( 'uuid', level.object );

				if ( child !== undefined ) {

					object.addLevel( child, level.distance, level.hysteresis );

				}

			}

		}

		return object;

	}

	bindSkeletons( object, skeletons ) {

		if ( Object.keys( skeletons ).length === 0 ) return;

		object.traverse( function ( child ) {

			if ( child.isSkinnedMesh === true && child.skeleton !== undefined ) {

				const skeleton = skeletons[ child.skeleton ];

				if ( skeleton === undefined ) {

					console.warn( 'THREE.ObjectLoader: No skeleton found with UUID:', child.skeleton );

				} else {

					child.bind( skeleton, child.bindMatrix );

				}

			}

		} );

	}

	bindLightTargets( object ) {

		object.traverse( function ( child ) {

			if ( child.isDirectionalLight || child.isSpotLight ) {

				const uuid = child.target;

				const target = object.getObjectByProperty( 'uuid', uuid );

				if ( target !== undefined ) {

					child.target = target;

				} else {

					child.target = new Object3D();

				}

			}

		} );

	}

}

const TEXTURE_MAPPING = {
	UVMapping: UVMapping,
	CubeReflectionMapping: CubeReflectionMapping,
	CubeRefractionMapping: CubeRefractionMapping,
	EquirectangularReflectionMapping: EquirectangularReflectionMapping,
	EquirectangularRefractionMapping: EquirectangularRefractionMapping,
	CubeUVReflectionMapping: CubeUVReflectionMapping
};

const TEXTURE_WRAPPING = {
	RepeatWrapping: RepeatWrapping,
	ClampToEdgeWrapping: ClampToEdgeWrapping,
	MirroredRepeatWrapping: MirroredRepeatWrapping
};

const TEXTURE_FILTER = {
	NearestFilter: NearestFilter,
	NearestMipmapNearestFilter: NearestMipmapNearestFilter,
	NearestMipmapLinearFilter: NearestMipmapLinearFilter,
	LinearFilter: LinearFilter,
	LinearMipmapNearestFilter: LinearMipmapNearestFilter,
	LinearMipmapLinearFilter: LinearMipmapLinearFilter
};

class ImageBitmapLoader extends Loader {

	constructor( manager ) {

		super( manager );

		this.isImageBitmapLoader = true;

		if ( typeof createImageBitmap === 'undefined' ) {

			console.warn( 'THREE.ImageBitmapLoader: createImageBitmap() not supported.' );

		}

		if ( typeof fetch === 'undefined' ) {

			console.warn( 'THREE.ImageBitmapLoader: fetch() not supported.' );

		}

		this.options = { premultiplyAlpha: 'none' };

	}

	setOptions( options ) {

		this.options = options;

		return this;

	}

	load( url, onLoad, onProgress, onError ) {

		if ( url === undefined ) url = '';

		if ( this.path !== undefined ) url = this.path + url;

		url = this.manager.resolveURL( url );

		const scope = this;

		const cached = Cache.get( url );

		if ( cached !== undefined ) {

			scope.manager.itemStart( url );

			// If cached is a promise, wait for it to resolve
			if ( cached.then ) {

				cached.then( imageBitmap => {

					if ( onLoad ) onLoad( imageBitmap );

					scope.manager.itemEnd( url );

				} ).catch( e => {

					if ( onError ) onError( e );

				} );
				return;

			}

			// If cached is not a promise (i.e., it's already an imageBitmap)
			setTimeout( function () {

				if ( onLoad ) onLoad( cached );

				scope.manager.itemEnd( url );

			}, 0 );

			return cached;

		}

		const fetchOptions = {};
		fetchOptions.credentials = ( this.crossOrigin === 'anonymous' ) ? 'same-origin' : 'include';
		fetchOptions.headers = this.requestHeader;

		const promise = fetch( url, fetchOptions ).then( function ( res ) {

			return res.blob();

		} ).then( function ( blob ) {

			return createImageBitmap( blob, Object.assign( scope.options, { colorSpaceConversion: 'none' } ) );

		} ).then( function ( imageBitmap ) {

			Cache.add( url, imageBitmap );

			if ( onLoad ) onLoad( imageBitmap );

			scope.manager.itemEnd( url );

			return imageBitmap;

		} ).catch( function ( e ) {

			if ( onError ) onError( e );

			Cache.remove( url );

			scope.manager.itemError( url );
			scope.manager.itemEnd( url );

		} );

		Cache.add( url, promise );
		scope.manager.itemStart( url );

	}

}

let _context;

class AudioContext {

	static getContext() {

		if ( _context === undefined ) {

			_context = new ( window.AudioContext || window.webkitAudioContext )();

		}

		return _context;

	}

	static setContext( value ) {

		_context = value;

	}

}

class AudioLoader extends Loader {

	constructor( manager ) {

		super( manager );

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const loader = new FileLoader( this.manager );
		loader.setResponseType( 'arraybuffer' );
		loader.setPath( this.path );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( this.withCredentials );
		loader.load( url, function ( buffer ) {

			try {

				// Create a copy of the buffer. The `decodeAudioData` method
				// detaches the buffer when complete, preventing reuse.
				const bufferCopy = buffer.slice( 0 );

				const context = AudioContext.getContext();
				context.decodeAudioData( bufferCopy, function ( audioBuffer ) {

					onLoad( audioBuffer );

				} ).catch( handleError );

			} catch ( e ) {

				handleError( e );

			}

		}, onProgress, onError );

		function handleError( e ) {

			if ( onError ) {

				onError( e );

			} else {

				console.error( e );

			}

			scope.manager.itemError( url );

		}

	}

}

const _eyeRight = /*@__PURE__*/ new Matrix4();
const _eyeLeft = /*@__PURE__*/ new Matrix4();
const _projectionMatrix = /*@__PURE__*/ new Matrix4();

class StereoCamera {

	constructor() {

		this.type = 'StereoCamera';

		this.aspect = 1;

		this.eyeSep = 0.064;

		this.cameraL = new PerspectiveCamera();
		this.cameraL.layers.enable( 1 );
		this.cameraL.matrixAutoUpdate = false;

		this.cameraR = new PerspectiveCamera();
		this.cameraR.layers.enable( 2 );
		this.cameraR.matrixAutoUpdate = false;

		this._cache = {
			focus: null,
			fov: null,
			aspect: null,
			near: null,
			far: null,
			zoom: null,
			eyeSep: null
		};

	}

	update( camera ) {

		const cache = this._cache;

		const needsUpdate = cache.focus !== camera.focus || cache.fov !== camera.fov ||
			cache.aspect !== camera.aspect * this.aspect || cache.near !== camera.near ||
			cache.far !== camera.far || cache.zoom !== camera.zoom || cache.eyeSep !== this.eyeSep;

		if ( needsUpdate ) {

			cache.focus = camera.focus;
			cache.fov = camera.fov;
			cache.aspect = camera.aspect * this.aspect;
			cache.near = camera.near;
			cache.far = camera.far;
			cache.zoom = camera.zoom;
			cache.eyeSep = this.eyeSep;

			// Off-axis stereoscopic effect based on
			// http://paulbourke.net/stereographics/stereorender/

			_projectionMatrix.copy( camera.projectionMatrix );
			const eyeSepHalf = cache.eyeSep / 2;
			const eyeSepOnProjection = eyeSepHalf * cache.near / cache.focus;
			const ymax = ( cache.near * Math.tan( DEG2RAD * cache.fov * 0.5 ) ) / cache.zoom;
			let xmin, xmax;

			// translate xOffset

			_eyeLeft.elements[ 12 ] = - eyeSepHalf;
			_eyeRight.elements[ 12 ] = eyeSepHalf;

			// for left eye

			xmin = - ymax * cache.aspect + eyeSepOnProjection;
			xmax = ymax * cache.aspect + eyeSepOnProjection;

			_projectionMatrix.elements[ 0 ] = 2 * cache.near / ( xmax - xmin );
			_projectionMatrix.elements[ 8 ] = ( xmax + xmin ) / ( xmax - xmin );

			this.cameraL.projectionMatrix.copy( _projectionMatrix );

			// for right eye

			xmin = - ymax * cache.aspect - eyeSepOnProjection;
			xmax = ymax * cache.aspect - eyeSepOnProjection;

			_projectionMatrix.elements[ 0 ] = 2 * cache.near / ( xmax - xmin );
			_projectionMatrix.elements[ 8 ] = ( xmax + xmin ) / ( xmax - xmin );

			this.cameraR.projectionMatrix.copy( _projectionMatrix );

		}

		this.cameraL.matrixWorld.copy( camera.matrixWorld ).multiply( _eyeLeft );
		this.cameraR.matrixWorld.copy( camera.matrixWorld ).multiply( _eyeRight );

	}

}

class Clock {

	constructor( autoStart = true ) {

		this.autoStart = autoStart;

		this.startTime = 0;
		this.oldTime = 0;
		this.elapsedTime = 0;

		this.running = false;

	}

	start() {

		this.startTime = now();

		this.oldTime = this.startTime;
		this.elapsedTime = 0;
		this.running = true;

	}

	stop() {

		this.getElapsedTime();
		this.running = false;
		this.autoStart = false;

	}

	getElapsedTime() {

		this.getDelta();
		return this.elapsedTime;

	}

	getDelta() {

		let diff = 0;

		if ( this.autoStart && ! this.running ) {

			this.start();
			return 0;

		}

		if ( this.running ) {

			const newTime = now();

			diff = ( newTime - this.oldTime ) / 1000;
			this.oldTime = newTime;

			this.elapsedTime += diff;

		}

		return diff;

	}

}

function now() {

	return performance.now();

}

const _position$1 = /*@__PURE__*/ new Vector3();
const _quaternion$1 = /*@__PURE__*/ new Quaternion();
const _scale$1 = /*@__PURE__*/ new Vector3();
const _orientation$1 = /*@__PURE__*/ new Vector3();

class AudioListener extends Object3D {

	constructor() {

		super();

		this.type = 'AudioListener';

		this.context = AudioContext.getContext();

		this.gain = this.context.createGain();
		this.gain.connect( this.context.destination );

		this.filter = null;

		this.timeDelta = 0;

		// private

		this._clock = new Clock();

	}

	getInput() {

		return this.gain;

	}

	removeFilter() {

		if ( this.filter !== null ) {

			this.gain.disconnect( this.filter );
			this.filter.disconnect( this.context.destination );
			this.gain.connect( this.context.destination );
			this.filter = null;

		}

		return this;

	}

	getFilter() {

		return this.filter;

	}

	setFilter( value ) {

		if ( this.filter !== null ) {

			this.gain.disconnect( this.filter );
			this.filter.disconnect( this.context.destination );

		} else {

			this.gain.disconnect( this.context.destination );

		}

		this.filter = value;
		this.gain.connect( this.filter );
		this.filter.connect( this.context.destination );

		return this;

	}

	getMasterVolume() {

		return this.gain.gain.value;

	}

	setMasterVolume( value ) {

		this.gain.gain.setTargetAtTime( value, this.context.currentTime, 0.01 );

		return this;

	}

	updateMatrixWorld( force ) {

		super.updateMatrixWorld( force );

		const listener = this.context.listener;
		const up = this.up;

		this.timeDelta = this._clock.getDelta();

		this.matrixWorld.decompose( _position$1, _quaternion$1, _scale$1 );

		_orientation$1.set( 0, 0, - 1 ).applyQuaternion( _quaternion$1 );

		if ( listener.positionX ) {

			// code path for Chrome (see #14393)

			const endTime = this.context.currentTime + this.timeDelta;

			listener.positionX.linearRampToValueAtTime( _position$1.x, endTime );
			listener.positionY.linearRampToValueAtTime( _position$1.y, endTime );
			listener.positionZ.linearRampToValueAtTime( _position$1.z, endTime );
			listener.forwardX.linearRampToValueAtTime( _orientation$1.x, endTime );
			listener.forwardY.linearRampToValueAtTime( _orientation$1.y, endTime );
			listener.forwardZ.linearRampToValueAtTime( _orientation$1.z, endTime );
			listener.upX.linearRampToValueAtTime( up.x, endTime );
			listener.upY.linearRampToValueAtTime( up.y, endTime );
			listener.upZ.linearRampToValueAtTime( up.z, endTime );

		} else {

			listener.setPosition( _position$1.x, _position$1.y, _position$1.z );
			listener.setOrientation( _orientation$1.x, _orientation$1.y, _orientation$1.z, up.x, up.y, up.z );

		}

	}

}

class Audio extends Object3D {

	constructor( listener ) {

		super();

		this.type = 'Audio';

		this.listener = listener;
		this.context = listener.context;

		this.gain = this.context.createGain();
		this.gain.connect( listener.getInput() );

		this.autoplay = false;

		this.buffer = null;
		this.detune = 0;
		this.loop = false;
		this.loopStart = 0;
		this.loopEnd = 0;
		this.offset = 0;
		this.duration = undefined;
		this.playbackRate = 1;
		this.isPlaying = false;
		this.hasPlaybackControl = true;
		this.source = null;
		this.sourceType = 'empty';

		this._startedAt = 0;
		this._progress = 0;
		this._connected = false;

		this.filters = [];

	}

	getOutput() {

		return this.gain;

	}

	setNodeSource( audioNode ) {

		this.hasPlaybackControl = false;
		this.sourceType = 'audioNode';
		this.source = audioNode;
		this.connect();

		return this;

	}

	setMediaElementSource( mediaElement ) {

		this.hasPlaybackControl = false;
		this.sourceType = 'mediaNode';
		this.source = this.context.createMediaElementSource( mediaElement );
		this.connect();

		return this;

	}

	setMediaStreamSource( mediaStream ) {

		this.hasPlaybackControl = false;
		this.sourceType = 'mediaStreamNode';
		this.source = this.context.createMediaStreamSource( mediaStream );
		this.connect();

		return this;

	}

	setBuffer( audioBuffer ) {

		this.buffer = audioBuffer;
		this.sourceType = 'buffer';

		if ( this.autoplay ) this.play();

		return this;

	}

	play( delay = 0 ) {

		if ( this.isPlaying === true ) {

			console.warn( 'THREE.Audio: Audio is already playing.' );
			return;

		}

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return;

		}

		this._startedAt = this.context.currentTime + delay;

		const source = this.context.createBufferSource();
		source.buffer = this.buffer;
		source.loop = this.loop;
		source.loopStart = this.loopStart;
		source.loopEnd = this.loopEnd;
		source.onended = this.onEnded.bind( this );
		source.start( this._startedAt, this._progress + this.offset, this.duration );

		this.isPlaying = true;

		this.source = source;

		this.setDetune( this.detune );
		this.setPlaybackRate( this.playbackRate );

		return this.connect();

	}

	pause() {

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return;

		}

		if ( this.isPlaying === true ) {

			// update current progress

			this._progress += Math.max( this.context.currentTime - this._startedAt, 0 ) * this.playbackRate;

			if ( this.loop === true ) {

				// ensure _progress does not exceed duration with looped audios

				this._progress = this._progress % ( this.duration || this.buffer.duration );

			}

			this.source.stop();
			this.source.onended = null;

			this.isPlaying = false;

		}

		return this;

	}

	stop( delay = 0 ) {

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return;

		}

		this._progress = 0;

		if ( this.source !== null ) {

			this.source.stop( this.context.currentTime + delay );
			this.source.onended = null;

		}

		this.isPlaying = false;

		return this;

	}

	connect() {

		if ( this.filters.length > 0 ) {

			this.source.connect( this.filters[ 0 ] );

			for ( let i = 1, l = this.filters.length; i < l; i ++ ) {

				this.filters[ i - 1 ].connect( this.filters[ i ] );

			}

			this.filters[ this.filters.length - 1 ].connect( this.getOutput() );

		} else {

			this.source.connect( this.getOutput() );

		}

		this._connected = true;

		return this;

	}

	disconnect() {

		if ( this._connected === false ) {

			return;

		}

		if ( this.filters.length > 0 ) {

			this.source.disconnect( this.filters[ 0 ] );

			for ( let i = 1, l = this.filters.length; i < l; i ++ ) {

				this.filters[ i - 1 ].disconnect( this.filters[ i ] );

			}

			this.filters[ this.filters.length - 1 ].disconnect( this.getOutput() );

		} else {

			this.source.disconnect( this.getOutput() );

		}

		this._connected = false;

		return this;

	}

	getFilters() {

		return this.filters;

	}

	setFilters( value ) {

		if ( ! value ) value = [];

		if ( this._connected === true ) {

			this.disconnect();
			this.filters = value.slice();
			this.connect();

		} else {

			this.filters = value.slice();

		}

		return this;

	}

	setDetune( value ) {

		this.detune = value;

		if ( this.isPlaying === true && this.source.detune !== undefined ) {

			this.source.detune.setTargetAtTime( this.detune, this.context.currentTime, 0.01 );

		}

		return this;

	}

	getDetune() {

		return this.detune;

	}

	getFilter() {

		return this.getFilters()[ 0 ];

	}

	setFilter( filter ) {

		return this.setFilters( filter ? [ filter ] : [] );

	}

	setPlaybackRate( value ) {

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return;

		}

		this.playbackRate = value;

		if ( this.isPlaying === true ) {

			this.source.playbackRate.setTargetAtTime( this.playbackRate, this.context.currentTime, 0.01 );

		}

		return this;

	}

	getPlaybackRate() {

		return this.playbackRate;

	}

	onEnded() {

		this.isPlaying = false;

	}

	getLoop() {

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return false;

		}

		return this.loop;

	}

	setLoop( value ) {

		if ( this.hasPlaybackControl === false ) {

			console.warn( 'THREE.Audio: this Audio has no playback control.' );
			return;

		}

		this.loop = value;

		if ( this.isPlaying === true ) {

			this.source.loop = this.loop;

		}

		return this;

	}

	setLoopStart( value ) {

		this.loopStart = value;

		return this;

	}

	setLoopEnd( value ) {

		this.loopEnd = value;

		return this;

	}

	getVolume() {

		return this.gain.gain.value;

	}

	setVolume( value ) {

		this.gain.gain.setTargetAtTime( value, this.context.currentTime, 0.01 );

		return this;

	}

}

const _position = /*@__PURE__*/ new Vector3();
const _quaternion = /*@__PURE__*/ new Quaternion();
const _scale = /*@__PURE__*/ new Vector3();
const _orientation = /*@__PURE__*/ new Vector3();

class PositionalAudio extends Audio {

	constructor( listener ) {

		super( listener );

		this.panner = this.context.createPanner();
		this.panner.panningModel = 'HRTF';
		this.panner.connect( this.gain );

	}

	connect() {

		super.connect();

		this.panner.connect( this.gain );

	}

	disconnect() {

		super.disconnect();

		this.panner.disconnect( this.gain );

	}

	getOutput() {

		return this.panner;

	}

	getRefDistance() {

		return this.panner.refDistance;

	}

	setRefDistance( value ) {

		this.panner.refDistance = value;

		return this;

	}

	getRolloffFactor() {

		return this.panner.rolloffFactor;

	}

	setRolloffFactor( value ) {

		this.panner.rolloffFactor = value;

		return this;

	}

	getDistanceModel() {

		return this.panner.distanceModel;

	}

	setDistanceModel( value ) {

		this.panner.distanceModel = value;

		return this;

	}

	getMaxDistance() {

		return this.panner.maxDistance;

	}

	setMaxDistance( value ) {

		this.panner.maxDistance = value;

		return this;

	}

	setDirectionalCone( coneInnerAngle, coneOuterAngle, coneOuterGain ) {

		this.panner.coneInnerAngle = coneInnerAngle;
		this.panner.coneOuterAngle = coneOuterAngle;
		this.panner.coneOuterGain = coneOuterGain;

		return this;

	}

	updateMatrixWorld( force ) {

		super.updateMatrixWorld( force );

		if ( this.hasPlaybackControl === true && this.isPlaying === false ) return;

		this.matrixWorld.decompose( _position, _quaternion, _scale );

		_orientation.set( 0, 0, 1 ).applyQuaternion( _quaternion );

		const panner = this.panner;

		if ( panner.positionX ) {

			// code path for Chrome and Firefox (see #14393)

			const endTime = this.context.currentTime + this.listener.timeDelta;

			panner.positionX.linearRampToValueAtTime( _position.x, endTime );
			panner.positionY.linearRampToValueAtTime( _position.y, endTime );
			panner.positionZ.linearRampToValueAtTime( _position.z, endTime );
			panner.orientationX.linearRampToValueAtTime( _orientation.x, endTime );
			panner.orientationY.linearRampToValueAtTime( _orientation.y, endTime );
			panner.orientationZ.linearRampToValueAtTime( _orientation.z, endTime );

		} else {

			panner.setPosition( _position.x, _position.y, _position.z );
			panner.setOrientation( _orientation.x, _orientation.y, _orientation.z );

		}

	}

}

class AudioAnalyser {

	constructor( audio, fftSize = 2048 ) {

		this.analyser = audio.context.createAnalyser();
		this.analyser.fftSize = fftSize;

		this.data = new Uint8Array( this.analyser.frequencyBinCount );

		audio.getOutput().connect( this.analyser );

	}


	getFrequencyData() {

		this.analyser.getByteFrequencyData( this.data );

		return this.data;

	}

	getAverageFrequency() {

		let value = 0;
		const data = this.getFrequencyData();

		for ( let i = 0; i < data.length; i ++ ) {

			value += data[ i ];

		}

		return value / data.length;

	}

}

class PropertyMixer {

	constructor( binding, typeName, valueSize ) {

		this.binding = binding;
		this.valueSize = valueSize;

		let mixFunction,
			mixFunctionAdditive,
			setIdentity;

		// buffer layout: [ incoming | accu0 | accu1 | orig | addAccu | (optional work) ]
		//
		// interpolators can use .buffer as their .result
		// the data then goes to 'incoming'
		//
		// 'accu0' and 'accu1' are used frame-interleaved for
		// the cumulative result and are compared to detect
		// changes
		//
		// 'orig' stores the original state of the property
		//
		// 'add' is used for additive cumulative results
		//
		// 'work' is optional and is only present for quaternion types. It is used
		// to store intermediate quaternion multiplication results

		switch ( typeName ) {

			case 'quaternion':
				mixFunction = this._slerp;
				mixFunctionAdditive = this._slerpAdditive;
				setIdentity = this._setAdditiveIdentityQuaternion;

				this.buffer = new Float64Array( valueSize * 6 );
				this._workIndex = 5;
				break;

			case 'string':
			case 'bool':
				mixFunction = this._select;

				// Use the regular mix function and for additive on these types,
				// additive is not relevant for non-numeric types
				mixFunctionAdditive = this._select;

				setIdentity = this._setAdditiveIdentityOther;

				this.buffer = new Array( valueSize * 5 );
				break;

			default:
				mixFunction = this._lerp;
				mixFunctionAdditive = this._lerpAdditive;
				setIdentity = this._setAdditiveIdentityNumeric;

				this.buffer = new Float64Array( valueSize * 5 );

		}

		this._mixBufferRegion = mixFunction;
		this._mixBufferRegionAdditive = mixFunctionAdditive;
		this._setIdentity = setIdentity;
		this._origIndex = 3;
		this._addIndex = 4;

		this.cumulativeWeight = 0;
		this.cumulativeWeightAdditive = 0;

		this.useCount = 0;
		this.referenceCount = 0;

	}

	// accumulate data in the 'incoming' region into 'accu<i>'
	accumulate( accuIndex, weight ) {

		// note: happily accumulating nothing when weight = 0, the caller knows
		// the weight and shouldn't have made the call in the first place

		const buffer = this.buffer,
			stride = this.valueSize,
			offset = accuIndex * stride + stride;

		let currentWeight = this.cumulativeWeight;

		if ( currentWeight === 0 ) {

			// accuN := incoming * weight

			for ( let i = 0; i !== stride; ++ i ) {

				buffer[ offset + i ] = buffer[ i ];

			}

			currentWeight = weight;

		} else {

			// accuN := accuN + incoming * weight

			currentWeight += weight;
			const mix = weight / currentWeight;
			this._mixBufferRegion( buffer, offset, 0, mix, stride );

		}

		this.cumulativeWeight = currentWeight;

	}

	// accumulate data in the 'incoming' region into 'add'
	accumulateAdditive( weight ) {

		const buffer = this.buffer,
			stride = this.valueSize,
			offset = stride * this._addIndex;

		if ( this.cumulativeWeightAdditive === 0 ) {

			// add = identity

			this._setIdentity();

		}

		// add := add + incoming * weight

		this._mixBufferRegionAdditive( buffer, offset, 0, weight, stride );
		this.cumulativeWeightAdditive += weight;

	}

	// apply the state of 'accu<i>' to the binding when accus differ
	apply( accuIndex ) {

		const stride = this.valueSize,
			buffer = this.buffer,
			offset = accuIndex * stride + stride,

			weight = this.cumulativeWeight,
			weightAdditive = this.cumulativeWeightAdditive,

			binding = this.binding;

		this.cumulativeWeight = 0;
		this.cumulativeWeightAdditive = 0;

		if ( weight < 1 ) {

			// accuN := accuN + original * ( 1 - cumulativeWeight )

			const originalValueOffset = stride * this._origIndex;

			this._mixBufferRegion(
				buffer, offset, originalValueOffset, 1 - weight, stride );

		}

		if ( weightAdditive > 0 ) {

			// accuN := accuN + additive accuN

			this._mixBufferRegionAdditive( buffer, offset, this._addIndex * stride, 1, stride );

		}

		for ( let i = stride, e = stride + stride; i !== e; ++ i ) {

			if ( buffer[ i ] !== buffer[ i + stride ] ) {

				// value has changed -> update scene graph

				binding.setValue( buffer, offset );
				break;

			}

		}

	}

	// remember the state of the bound property and copy it to both accus
	saveOriginalState() {

		const binding = this.binding;

		const buffer = this.buffer,
			stride = this.valueSize,

			originalValueOffset = stride * this._origIndex;

		binding.getValue( buffer, originalValueOffset );

		// accu[0..1] := orig -- initially detect changes against the original
		for ( let i = stride, e = originalValueOffset; i !== e; ++ i ) {

			buffer[ i ] = buffer[ originalValueOffset + ( i % stride ) ];

		}

		// Add to identity for additive
		this._setIdentity();

		this.cumulativeWeight = 0;
		this.cumulativeWeightAdditive = 0;

	}

	// apply the state previously taken via 'saveOriginalState' to the binding
	restoreOriginalState() {

		const originalValueOffset = this.valueSize * 3;
		this.binding.setValue( this.buffer, originalValueOffset );

	}

	_setAdditiveIdentityNumeric() {

		const startIndex = this._addIndex * this.valueSize;
		const endIndex = startIndex + this.valueSize;

		for ( let i = startIndex; i < endIndex; i ++ ) {

			this.buffer[ i ] = 0;

		}

	}

	_setAdditiveIdentityQuaternion() {

		this._setAdditiveIdentityNumeric();
		this.buffer[ this._addIndex * this.valueSize + 3 ] = 1;

	}

	_setAdditiveIdentityOther() {

		const startIndex = this._origIndex * this.valueSize;
		const targetIndex = this._addIndex * this.valueSize;

		for ( let i = 0; i < this.valueSize; i ++ ) {

			this.buffer[ targetIndex + i ] = this.buffer[ startIndex + i ];

		}

	}


	// mix functions

	_select( buffer, dstOffset, srcOffset, t, stride ) {

		if ( t >= 0.5 ) {

			for ( let i = 0; i !== stride; ++ i ) {

				buffer[ dstOffset + i ] = buffer[ srcOffset + i ];

			}

		}

	}

	_slerp( buffer, dstOffset, srcOffset, t ) {

		Quaternion.slerpFlat( buffer, dstOffset, buffer, dstOffset, buffer, srcOffset, t );

	}

	_slerpAdditive( buffer, dstOffset, srcOffset, t, stride ) {

		const workOffset = this._workIndex * stride;

		// Store result in intermediate buffer offset
		Quaternion.multiplyQuaternionsFlat( buffer, workOffset, buffer, dstOffset, buffer, srcOffset );

		// Slerp to the intermediate result
		Quaternion.slerpFlat( buffer, dstOffset, buffer, dstOffset, buffer, workOffset, t );

	}

	_lerp( buffer, dstOffset, srcOffset, t, stride ) {

		const s = 1 - t;

		for ( let i = 0; i !== stride; ++ i ) {

			const j = dstOffset + i;

			buffer[ j ] = buffer[ j ] * s + buffer[ srcOffset + i ] * t;

		}

	}

	_lerpAdditive( buffer, dstOffset, srcOffset, t, stride ) {

		for ( let i = 0; i !== stride; ++ i ) {

			const j = dstOffset + i;

			buffer[ j ] = buffer[ j ] + buffer[ srcOffset + i ] * t;

		}

	}

}

// Characters [].:/ are reserved for track binding syntax.
const _RESERVED_CHARS_RE = '\\[\\]\\.:\\/';
const _reservedRe = new RegExp( '[' + _RESERVED_CHARS_RE + ']', 'g' );

// Attempts to allow node names from any language. ES5's `\w` regexp matches
// only latin characters, and the unicode \p{L} is not yet supported. So
// instead, we exclude reserved characters and match everything else.
const _wordChar = '[^' + _RESERVED_CHARS_RE + ']';
const _wordCharOrDot = '[^' + _RESERVED_CHARS_RE.replace( '\\.', '' ) + ']';

// Parent directories, delimited by '/' or ':'. Currently unused, but must
// be matched to parse the rest of the track name.
const _directoryRe = /*@__PURE__*/ /((?:WC+[\/:])*)/.source.replace( 'WC', _wordChar );

// Target node. May contain word characters (a-zA-Z0-9_) and '.' or '-'.
const _nodeRe = /*@__PURE__*/ /(WCOD+)?/.source.replace( 'WCOD', _wordCharOrDot );

// Object on target node, and accessor. May not contain reserved
// characters. Accessor may contain any character except closing bracket.
const _objectRe = /*@__PURE__*/ /(?:\.(WC+)(?:\[(.+)\])?)?/.source.replace( 'WC', _wordChar );

// Property and accessor. May not contain reserved characters. Accessor may
// contain any non-bracket characters.
const _propertyRe = /*@__PURE__*/ /\.(WC+)(?:\[(.+)\])?/.source.replace( 'WC', _wordChar );

const _trackRe = new RegExp( ''
	+ '^'
	+ _directoryRe
	+ _nodeRe
	+ _objectRe
	+ _propertyRe
	+ '$'
);

const _supportedObjectNames = [ 'material', 'materials', 'bones', 'map' ];

class Composite {

	constructor( targetGroup, path, optionalParsedPath ) {

		const parsedPath = optionalParsedPath || PropertyBinding.parseTrackName( path );

		this._targetGroup = targetGroup;
		this._bindings = targetGroup.subscribe_( path, parsedPath );

	}

	getValue( array, offset ) {

		this.bind(); // bind all binding

		const firstValidIndex = this._targetGroup.nCachedObjects_,
			binding = this._bindings[ firstValidIndex ];

		// and only call .getValue on the first
		if ( binding !== undefined ) binding.getValue( array, offset );

	}

	setValue( array, offset ) {

		const bindings = this._bindings;

		for ( let i = this._targetGroup.nCachedObjects_, n = bindings.length; i !== n; ++ i ) {

			bindings[ i ].setValue( array, offset );

		}

	}

	bind() {

		const bindings = this._bindings;

		for ( let i = this._targetGroup.nCachedObjects_, n = bindings.length; i !== n; ++ i ) {

			bindings[ i ].bind();

		}

	}

	unbind() {

		const bindings = this._bindings;

		for ( let i = this._targetGroup.nCachedObjects_, n = bindings.length; i !== n; ++ i ) {

			bindings[ i ].unbind();

		}

	}

}

// Note: This class uses a State pattern on a per-method basis:
// 'bind' sets 'this.getValue' / 'setValue' and shadows the
// prototype version of these methods with one that represents
// the bound state. When the property is not found, the methods
// become no-ops.
class PropertyBinding {

	constructor( rootNode, path, parsedPath ) {

		this.path = path;
		this.parsedPath = parsedPath || PropertyBinding.parseTrackName( path );

		this.node = PropertyBinding.findNode( rootNode, this.parsedPath.nodeName );

		this.rootNode = rootNode;

		// initial state of these methods that calls 'bind'
		this.getValue = this._getValue_unbound;
		this.setValue = this._setValue_unbound;

	}


	static create( root, path, parsedPath ) {

		if ( ! ( root && root.isAnimationObjectGroup ) ) {

			return new PropertyBinding( root, path, parsedPath );

		} else {

			return new PropertyBinding.Composite( root, path, parsedPath );

		}

	}

	/**
	 * Replaces spaces with underscores and removes unsupported characters from
	 * node names, to ensure compatibility with parseTrackName().
	 *
	 * @param {string} name Node name to be sanitized.
	 * @return {string}
	 */
	static sanitizeNodeName( name ) {

		return name.replace( /\s/g, '_' ).replace( _reservedRe, '' );

	}

	static parseTrackName( trackName ) {

		const matches = _trackRe.exec( trackName );

		if ( matches === null ) {

			throw new Error( 'PropertyBinding: Cannot parse trackName: ' + trackName );

		}

		const results = {
			// directoryName: matches[ 1 ], // (tschw) currently unused
			nodeName: matches[ 2 ],
			objectName: matches[ 3 ],
			objectIndex: matches[ 4 ],
			propertyName: matches[ 5 ], // required
			propertyIndex: matches[ 6 ]
		};

		const lastDot = results.nodeName && results.nodeName.lastIndexOf( '.' );

		if ( lastDot !== undefined && lastDot !== - 1 ) {

			const objectName = results.nodeName.substring( lastDot + 1 );

			// Object names must be checked against an allowlist. Otherwise, there
			// is no way to parse 'foo.bar.baz': 'baz' must be a property, but
			// 'bar' could be the objectName, or part of a nodeName (which can
			// include '.' characters).
			if ( _supportedObjectNames.indexOf( objectName ) !== - 1 ) {

				results.nodeName = results.nodeName.substring( 0, lastDot );
				results.objectName = objectName;

			}

		}

		if ( results.propertyName === null || results.propertyName.length === 0 ) {

			throw new Error( 'PropertyBinding: can not parse propertyName from trackName: ' + trackName );

		}

		return results;

	}

	static findNode( root, nodeName ) {

		if ( nodeName === undefined || nodeName === '' || nodeName === '.' || nodeName === - 1 || nodeName === root.name || nodeName === root.uuid ) {

			return root;

		}

		// search into skeleton bones.
		if ( root.skeleton ) {

			const bone = root.skeleton.getBoneByName( nodeName );

			if ( bone !== undefined ) {

				return bone;

			}

		}

		// search into node subtree.
		if ( root.children ) {

			const searchNodeSubtree = function ( children ) {

				for ( let i = 0; i < children.length; i ++ ) {

					const childNode = children[ i ];

					if ( childNode.name === nodeName || childNode.uuid === nodeName ) {

						return childNode;

					}

					const result = searchNodeSubtree( childNode.children );

					if ( result ) return result;

				}

				return null;

			};

			const subTreeNode = searchNodeSubtree( root.children );

			if ( subTreeNode ) {

				return subTreeNode;

			}

		}

		return null;

	}

	// these are used to "bind" a nonexistent property
	_getValue_unavailable() {}
	_setValue_unavailable() {}

	// Getters

	_getValue_direct( buffer, offset ) {

		buffer[ offset ] = this.targetObject[ this.propertyName ];

	}

	_getValue_array( buffer, offset ) {

		const source = this.resolvedProperty;

		for ( let i = 0, n = source.length; i !== n; ++ i ) {

			buffer[ offset ++ ] = source[ i ];

		}

	}

	_getValue_arrayElement( buffer, offset ) {

		buffer[ offset ] = this.resolvedProperty[ this.propertyIndex ];

	}

	_getValue_toArray( buffer, offset ) {

		this.resolvedProperty.toArray( buffer, offset );

	}

	// Direct

	_setValue_direct( buffer, offset ) {

		this.targetObject[ this.propertyName ] = buffer[ offset ];

	}

	_setValue_direct_setNeedsUpdate( buffer, offset ) {

		this.targetObject[ this.propertyName ] = buffer[ offset ];
		this.targetObject.needsUpdate = true;

	}

	_setValue_direct_setMatrixWorldNeedsUpdate( buffer, offset ) {

		this.targetObject[ this.propertyName ] = buffer[ offset ];
		this.targetObject.matrixWorldNeedsUpdate = true;

	}

	// EntireArray

	_setValue_array( buffer, offset ) {

		const dest = this.resolvedProperty;

		for ( let i = 0, n = dest.length; i !== n; ++ i ) {

			dest[ i ] = buffer[ offset ++ ];

		}

	}

	_setValue_array_setNeedsUpdate( buffer, offset ) {

		const dest = this.resolvedProperty;

		for ( let i = 0, n = dest.length; i !== n; ++ i ) {

			dest[ i ] = buffer[ offset ++ ];

		}

		this.targetObject.needsUpdate = true;

	}

	_setValue_array_setMatrixWorldNeedsUpdate( buffer, offset ) {

		const dest = this.resolvedProperty;

		for ( let i = 0, n = dest.length; i !== n; ++ i ) {

			dest[ i ] = buffer[ offset ++ ];

		}

		this.targetObject.matrixWorldNeedsUpdate = true;

	}

	// ArrayElement

	_setValue_arrayElement( buffer, offset ) {

		this.resolvedProperty[ this.propertyIndex ] = buffer[ offset ];

	}

	_setValue_arrayElement_setNeedsUpdate( buffer, offset ) {

		this.resolvedProperty[ this.propertyIndex ] = buffer[ offset ];
		this.targetObject.needsUpdate = true;

	}

	_setValue_arrayElement_setMatrixWorldNeedsUpdate( buffer, offset ) {

		this.resolvedProperty[ this.propertyIndex ] = buffer[ offset ];
		this.targetObject.matrixWorldNeedsUpdate = true;

	}

	// HasToFromArray

	_setValue_fromArray( buffer, offset ) {

		this.resolvedProperty.fromArray( buffer, offset );

	}

	_setValue_fromArray_setNeedsUpdate( buffer, offset ) {

		this.resolvedProperty.fromArray( buffer, offset );
		this.targetObject.needsUpdate = true;

	}

	_setValue_fromArray_setMatrixWorldNeedsUpdate( buffer, offset ) {

		this.resolvedProperty.fromArray( buffer, offset );
		this.targetObject.matrixWorldNeedsUpdate = true;

	}

	_getValue_unbound( targetArray, offset ) {

		this.bind();
		this.getValue( targetArray, offset );

	}

	_setValue_unbound( sourceArray, offset ) {

		this.bind();
		this.setValue( sourceArray, offset );

	}

	// create getter / setter pair for a property in the scene graph
	bind() {

		let targetObject = this.node;
		const parsedPath = this.parsedPath;

		const objectName = parsedPath.objectName;
		const propertyName = parsedPath.propertyName;
		let propertyIndex = parsedPath.propertyIndex;

		if ( ! targetObject ) {

			targetObject = PropertyBinding.findNode( this.rootNode, parsedPath.nodeName );

			this.node = targetObject;

		}

		// set fail state so we can just 'return' on error
		this.getValue = this._getValue_unavailable;
		this.setValue = this._setValue_unavailable;

		// ensure there is a value node
		if ( ! targetObject ) {

			console.warn( 'THREE.PropertyBinding: No target node found for track: ' + this.path + '.' );
			return;

		}

		if ( objectName ) {

			let objectIndex = parsedPath.objectIndex;

			// special cases were we need to reach deeper into the hierarchy to get the face materials....
			switch ( objectName ) {

				case 'materials':

					if ( ! targetObject.material ) {

						console.error( 'THREE.PropertyBinding: Can not bind to material as node does not have a material.', this );
						return;

					}

					if ( ! targetObject.material.materials ) {

						console.error( 'THREE.PropertyBinding: Can not bind to material.materials as node.material does not have a materials array.', this );
						return;

					}

					targetObject = targetObject.material.materials;

					break;

				case 'bones':

					if ( ! targetObject.skeleton ) {

						console.error( 'THREE.PropertyBinding: Can not bind to bones as node does not have a skeleton.', this );
						return;

					}

					// potential future optimization: skip this if propertyIndex is already an integer
					// and convert the integer string to a true integer.

					targetObject = targetObject.skeleton.bones;

					// support resolving morphTarget names into indices.
					for ( let i = 0; i < targetObject.length; i ++ ) {

						if ( targetObject[ i ].name === objectIndex ) {

							objectIndex = i;
							break;

						}

					}

					break;

				case 'map':

					if ( 'map' in targetObject ) {

						targetObject = targetObject.map;
						break;

					}

					if ( ! targetObject.material ) {

						console.error( 'THREE.PropertyBinding: Can not bind to material as node does not have a material.', this );
						return;

					}

					if ( ! targetObject.material.map ) {

						console.error( 'THREE.PropertyBinding: Can not bind to material.map as node.material does not have a map.', this );
						return;

					}

					targetObject = targetObject.material.map;
					break;

				default:

					if ( targetObject[ objectName ] === undefined ) {

						console.error( 'THREE.PropertyBinding: Can not bind to objectName of node undefined.', this );
						return;

					}

					targetObject = targetObject[ objectName ];

			}


			if ( objectIndex !== undefined ) {

				if ( targetObject[ objectIndex ] === undefined ) {

					console.error( 'THREE.PropertyBinding: Trying to bind to objectIndex of objectName, but is undefined.', this, targetObject );
					return;

				}

				targetObject = targetObject[ objectIndex ];

			}

		}

		// resolve property
		const nodeProperty = targetObject[ propertyName ];

		if ( nodeProperty === undefined ) {

			const nodeName = parsedPath.nodeName;

			console.error( 'THREE.PropertyBinding: Trying to update property for track: ' + nodeName +
				'.' + propertyName + ' but it wasn\'t found.', targetObject );
			return;

		}

		// determine versioning scheme
		let versioning = this.Versioning.None;

		this.targetObject = targetObject;

		if ( targetObject.needsUpdate !== undefined ) { // material

			versioning = this.Versioning.NeedsUpdate;

		} else if ( targetObject.matrixWorldNeedsUpdate !== undefined ) { // node transform

			versioning = this.Versioning.MatrixWorldNeedsUpdate;

		}

		// determine how the property gets bound
		let bindingType = this.BindingType.Direct;

		if ( propertyIndex !== undefined ) {

			// access a sub element of the property array (only primitives are supported right now)

			if ( propertyName === 'morphTargetInfluences' ) {

				// potential optimization, skip this if propertyIndex is already an integer, and convert the integer string to a true integer.

				// support resolving morphTarget names into indices.
				if ( ! targetObject.geometry ) {

					console.error( 'THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.', this );
					return;

				}

				if ( ! targetObject.geometry.morphAttributes ) {

					console.error( 'THREE.PropertyBinding: Can not bind to morphTargetInfluences because node does not have a geometry.morphAttributes.', this );
					return;

				}

				if ( targetObject.morphTargetDictionary[ propertyIndex ] !== undefined ) {

					propertyIndex = targetObject.morphTargetDictionary[ propertyIndex ];

				}

			}

			bindingType = this.BindingType.ArrayElement;

			this.resolvedProperty = nodeProperty;
			this.propertyIndex = propertyIndex;

		} else if ( nodeProperty.fromArray !== undefined && nodeProperty.toArray !== undefined ) {

			// must use copy for Object3D.Euler/Quaternion

			bindingType = this.BindingType.HasFromToArray;

			this.resolvedProperty = nodeProperty;

		} else if ( Array.isArray( nodeProperty ) ) {

			bindingType = this.BindingType.EntireArray;

			this.resolvedProperty = nodeProperty;

		} else {

			this.propertyName = propertyName;

		}

		// select getter / setter
		this.getValue = this.GetterByBindingType[ bindingType ];
		this.setValue = this.SetterByBindingTypeAndVersioning[ bindingType ][ versioning ];

	}

	unbind() {

		this.node = null;

		// back to the prototype version of getValue / setValue
		// note: avoiding to mutate the shape of 'this' via 'delete'
		this.getValue = this._getValue_unbound;
		this.setValue = this._setValue_unbound;

	}

}

PropertyBinding.Composite = Composite;

PropertyBinding.prototype.BindingType = {
	Direct: 0,
	EntireArray: 1,
	ArrayElement: 2,
	HasFromToArray: 3
};

PropertyBinding.prototype.Versioning = {
	None: 0,
	NeedsUpdate: 1,
	MatrixWorldNeedsUpdate: 2
};

PropertyBinding.prototype.GetterByBindingType = [

	PropertyBinding.prototype._getValue_direct,
	PropertyBinding.prototype._getValue_array,
	PropertyBinding.prototype._getValue_arrayElement,
	PropertyBinding.prototype._getValue_toArray,

];

PropertyBinding.prototype.SetterByBindingTypeAndVersioning = [

	[
		// Direct
		PropertyBinding.prototype._setValue_direct,
		PropertyBinding.prototype._setValue_direct_setNeedsUpdate,
		PropertyBinding.prototype._setValue_direct_setMatrixWorldNeedsUpdate,

	], [

		// EntireArray

		PropertyBinding.prototype._setValue_array,
		PropertyBinding.prototype._setValue_array_setNeedsUpdate,
		PropertyBinding.prototype._setValue_array_setMatrixWorldNeedsUpdate,

	], [

		// ArrayElement
		PropertyBinding.prototype._setValue_arrayElement,
		PropertyBinding.prototype._setValue_arrayElement_setNeedsUpdate,
		PropertyBinding.prototype._setValue_arrayElement_setMatrixWorldNeedsUpdate,

	], [

		// HasToFromArray
		PropertyBinding.prototype._setValue_fromArray,
		PropertyBinding.prototype._setValue_fromArray_setNeedsUpdate,
		PropertyBinding.prototype._setValue_fromArray_setMatrixWorldNeedsUpdate,

	]

];

/**
 *
 * A group of objects that receives a shared animation state.
 *
 * Usage:
 *
 *  - Add objects you would otherwise pass as 'root' to the
 *    constructor or the .clipAction method of AnimationMixer.
 *
 *  - Instead pass this object as 'root'.
 *
 *  - You can also add and remove objects later when the mixer
 *    is running.
 *
 * Note:
 *
 *    Objects of this class appear as one object to the mixer,
 *    so cache control of the individual objects must be done
 *    on the group.
 *
 * Limitation:
 *
 *  - The animated properties must be compatible among the
 *    all objects in the group.
 *
 *  - A single property can either be controlled through a
 *    target group or directly, but not both.
 */

class AnimationObjectGroup {

	constructor() {

		this.isAnimationObjectGroup = true;

		this.uuid = generateUUID();

		// cached objects followed by the active ones
		this._objects = Array.prototype.slice.call( arguments );

		this.nCachedObjects_ = 0; // threshold
		// note: read by PropertyBinding.Composite

		const indices = {};
		this._indicesByUUID = indices; // for bookkeeping

		for ( let i = 0, n = arguments.length; i !== n; ++ i ) {

			indices[ arguments[ i ].uuid ] = i;

		}

		this._paths = []; // inside: string
		this._parsedPaths = []; // inside: { we don't care, here }
		this._bindings = []; // inside: Array< PropertyBinding >
		this._bindingsIndicesByPath = {}; // inside: indices in these arrays

		const scope = this;

		this.stats = {

			objects: {
				get total() {

					return scope._objects.length;

				},
				get inUse() {

					return this.total - scope.nCachedObjects_;

				}
			},
			get bindingsPerObject() {

				return scope._bindings.length;

			}

		};

	}

	add() {

		const objects = this._objects,
			indicesByUUID = this._indicesByUUID,
			paths = this._paths,
			parsedPaths = this._parsedPaths,
			bindings = this._bindings,
			nBindings = bindings.length;

		let knownObject = undefined,
			nObjects = objects.length,
			nCachedObjects = this.nCachedObjects_;

		for ( let i = 0, n = arguments.length; i !== n; ++ i ) {

			const object = arguments[ i ],
				uuid = object.uuid;
			let index = indicesByUUID[ uuid ];

			if ( index === undefined ) {

				// unknown object -> add it to the ACTIVE region

				index = nObjects ++;
				indicesByUUID[ uuid ] = index;
				objects.push( object );

				// accounting is done, now do the same for all bindings

				for ( let j = 0, m = nBindings; j !== m; ++ j ) {

					bindings[ j ].push( new PropertyBinding( object, paths[ j ], parsedPaths[ j ] ) );

				}

			} else if ( index < nCachedObjects ) {

				knownObject = objects[ index ];

				// move existing object to the ACTIVE region

				const firstActiveIndex = -- nCachedObjects,
					lastCachedObject = objects[ firstActiveIndex ];

				indicesByUUID[ lastCachedObject.uuid ] = index;
				objects[ index ] = lastCachedObject;

				indicesByUUID[ uuid ] = firstActiveIndex;
				objects[ firstActiveIndex ] = object;

				// accounting is done, now do the same for all bindings

				for ( let j = 0, m = nBindings; j !== m; ++ j ) {

					const bindingsForPath = bindings[ j ],
						lastCached = bindingsForPath[ firstActiveIndex ];

					let binding = bindingsForPath[ index ];

					bindingsForPath[ index ] = lastCached;

					if ( binding === undefined ) {

						// since we do not bother to create new bindings
						// for objects that are cached, the binding may
						// or may not exist

						binding = new PropertyBinding( object, paths[ j ], parsedPaths[ j ] );

					}

					bindingsForPath[ firstActiveIndex ] = binding;

				}

			} else if ( objects[ index ] !== knownObject ) {

				console.error( 'THREE.AnimationObjectGroup: Different objects with the same UUID ' +
					'detected. Clean the caches or recreate your infrastructure when reloading scenes.' );

			} // else the object is already where we want it to be

		} // for arguments

		this.nCachedObjects_ = nCachedObjects;

	}

	remove() {

		const objects = this._objects,
			indicesByUUID = this._indicesByUUID,
			bindings = this._bindings,
			nBindings = bindings.length;

		let nCachedObjects = this.nCachedObjects_;

		for ( let i = 0, n = arguments.length; i !== n; ++ i ) {

			const object = arguments[ i ],
				uuid = object.uuid,
				index = indicesByUUID[ uuid ];

			if ( index !== undefined && index >= nCachedObjects ) {

				// move existing object into the CACHED region

				const lastCachedIndex = nCachedObjects ++,
					firstActiveObject = objects[ lastCachedIndex ];

				indicesByUUID[ firstActiveObject.uuid ] = index;
				objects[ index ] = firstActiveObject;

				indicesByUUID[ uuid ] = lastCachedIndex;
				objects[ lastCachedIndex ] = object;

				// accounting is done, now do the same for all bindings

				for ( let j = 0, m = nBindings; j !== m; ++ j ) {

					const bindingsForPath = bindings[ j ],
						firstActive = bindingsForPath[ lastCachedIndex ],
						binding = bindingsForPath[ index ];

					bindingsForPath[ index ] = firstActive;
					bindingsForPath[ lastCachedIndex ] = binding;

				}

			}

		} // for arguments

		this.nCachedObjects_ = nCachedObjects;

	}

	// remove & forget
	uncache() {

		const objects = this._objects,
			indicesByUUID = this._indicesByUUID,
			bindings = this._bindings,
			nBindings = bindings.length;

		let nCachedObjects = this.nCachedObjects_,
			nObjects = objects.length;

		for ( let i = 0, n = arguments.length; i !== n; ++ i ) {

			const object = arguments[ i ],
				uuid = object.uuid,
				index = indicesByUUID[ uuid ];

			if ( index !== undefined ) {

				delete indicesByUUID[ uuid ];

				if ( index < nCachedObjects ) {

					// object is cached, shrink the CACHED region

					const firstActiveIndex = -- nCachedObjects,
						lastCachedObject = objects[ firstActiveIndex ],
						lastIndex = -- nObjects,
						lastObject = objects[ lastIndex ];

					// last cached object takes this object's place
					indicesByUUID[ lastCachedObject.uuid ] = index;
					objects[ index ] = lastCachedObject;

					// last object goes to the activated slot and pop
					indicesByUUID[ lastObject.uuid ] = firstActiveIndex;
					objects[ firstActiveIndex ] = lastObject;
					objects.pop();

					// accounting is done, now do the same for all bindings

					for ( let j = 0, m = nBindings; j !== m; ++ j ) {

						const bindingsForPath = bindings[ j ],
							lastCached = bindingsForPath[ firstActiveIndex ],
							last = bindingsForPath[ lastIndex ];

						bindingsForPath[ index ] = lastCached;
						bindingsForPath[ firstActiveIndex ] = last;
						bindingsForPath.pop();

					}

				} else {

					// object is active, just swap with the last and pop

					const lastIndex = -- nObjects,
						lastObject = objects[ lastIndex ];

					if ( lastIndex > 0 ) {

						indicesByUUID[ lastObject.uuid ] = index;

					}

					objects[ index ] = lastObject;
					objects.pop();

					// accounting is done, now do the same for all bindings

					for ( let j = 0, m = nBindings; j !== m; ++ j ) {

						const bindingsForPath = bindings[ j ];

						bindingsForPath[ index ] = bindingsForPath[ lastIndex ];
						bindingsForPath.pop();

					}

				} // cached or active

			} // if object is known

		} // for arguments

		this.nCachedObjects_ = nCachedObjects;

	}

	// Internal interface used by befriended PropertyBinding.Composite:

	subscribe_( path, parsedPath ) {

		// returns an array of bindings for the given path that is changed
		// according to the contained objects in the group

		const indicesByPath = this._bindingsIndicesByPath;
		let index = indicesByPath[ path ];
		const bindings = this._bindings;

		if ( index !== undefined ) return bindings[ index ];

		const paths = this._paths,
			parsedPaths = this._parsedPaths,
			objects = this._objects,
			nObjects = objects.length,
			nCachedObjects = this.nCachedObjects_,
			bindingsForPath = new Array( nObjects );

		index = bindings.length;

		indicesByPath[ path ] = index;

		paths.push( path );
		parsedPaths.push( parsedPath );
		bindings.push( bindingsForPath );

		for ( let i = nCachedObjects, n = objects.length; i !== n; ++ i ) {

			const object = objects[ i ];
			bindingsForPath[ i ] = new PropertyBinding( object, path, parsedPath );

		}

		return bindingsForPath;

	}

	unsubscribe_( path ) {

		// tells the group to forget about a property path and no longer
		// update the array previously obtained with 'subscribe_'

		const indicesByPath = this._bindingsIndicesByPath,
			index = indicesByPath[ path ];

		if ( index !== undefined ) {

			const paths = this._paths,
				parsedPaths = this._parsedPaths,
				bindings = this._bindings,
				lastBindingsIndex = bindings.length - 1,
				lastBindings = bindings[ lastBindingsIndex ],
				lastBindingsPath = path[ lastBindingsIndex ];

			indicesByPath[ lastBindingsPath ] = index;

			bindings[ index ] = lastBindings;
			bindings.pop();

			parsedPaths[ index ] = parsedPaths[ lastBindingsIndex ];
			parsedPaths.pop();

			paths[ index ] = paths[ lastBindingsIndex ];
			paths.pop();

		}

	}

}

class AnimationAction {

	constructor( mixer, clip, localRoot = null, blendMode = clip.blendMode ) {

		this._mixer = mixer;
		this._clip = clip;
		this._localRoot = localRoot;
		this.blendMode = blendMode;

		const tracks = clip.tracks,
			nTracks = tracks.length,
			interpolants = new Array( nTracks );

		const interpolantSettings = {
			endingStart: ZeroCurvatureEnding,
			endingEnd: ZeroCurvatureEnding
		};

		for ( let i = 0; i !== nTracks; ++ i ) {

			const interpolant = tracks[ i ].createInterpolant( null );
			interpolants[ i ] = interpolant;
			interpolant.settings = interpolantSettings;

		}

		this._interpolantSettings = interpolantSettings;

		this._interpolants = interpolants; // bound by the mixer

		// inside: PropertyMixer (managed by the mixer)
		this._propertyBindings = new Array( nTracks );

		this._cacheIndex = null; // for the memory manager
		this._byClipCacheIndex = null; // for the memory manager

		this._timeScaleInterpolant = null;
		this._weightInterpolant = null;

		this.loop = LoopRepeat;
		this._loopCount = - 1;

		// global mixer time when the action is to be started
		// it's set back to 'null' upon start of the action
		this._startTime = null;

		// scaled local time of the action
		// gets clamped or wrapped to 0..clip.duration according to loop
		this.time = 0;

		this.timeScale = 1;
		this._effectiveTimeScale = 1;

		this.weight = 1;
		this._effectiveWeight = 1;

		this.repetitions = Infinity; // no. of repetitions when looping

		this.paused = false; // true -> zero effective time scale
		this.enabled = true; // false -> zero effective weight

		this.clampWhenFinished = false;// keep feeding the last frame?

		this.zeroSlopeAtStart = true;// for smooth interpolation w/o separate
		this.zeroSlopeAtEnd = true;// clips for start, loop and end

	}

	// State & Scheduling

	play() {

		this._mixer._activateAction( this );

		return this;

	}

	stop() {

		this._mixer._deactivateAction( this );

		return this.reset();

	}

	reset() {

		this.paused = false;
		this.enabled = true;

		this.time = 0; // restart clip
		this._loopCount = - 1;// forget previous loops
		this._startTime = null;// forget scheduling

		return this.stopFading().stopWarping();

	}

	isRunning() {

		return this.enabled && ! this.paused && this.timeScale !== 0 &&
			this._startTime === null && this._mixer._isActiveAction( this );

	}

	// return true when play has been called
	isScheduled() {

		return this._mixer._isActiveAction( this );

	}

	startAt( time ) {

		this._startTime = time;

		return this;

	}

	setLoop( mode, repetitions ) {

		this.loop = mode;
		this.repetitions = repetitions;

		return this;

	}

	// Weight

	// set the weight stopping any scheduled fading
	// although .enabled = false yields an effective weight of zero, this
	// method does *not* change .enabled, because it would be confusing
	setEffectiveWeight( weight ) {

		this.weight = weight;

		// note: same logic as when updated at runtime
		this._effectiveWeight = this.enabled ? weight : 0;

		return this.stopFading();

	}

	// return the weight considering fading and .enabled
	getEffectiveWeight() {

		return this._effectiveWeight;

	}

	fadeIn( duration ) {

		return this._scheduleFading( duration, 0, 1 );

	}

	fadeOut( duration ) {

		return this._scheduleFading( duration, 1, 0 );

	}

	crossFadeFrom( fadeOutAction, duration, warp ) {

		fadeOutAction.fadeOut( duration );
		this.fadeIn( duration );

		if ( warp ) {

			const fadeInDuration = this._clip.duration,
				fadeOutDuration = fadeOutAction._clip.duration,

				startEndRatio = fadeOutDuration / fadeInDuration,
				endStartRatio = fadeInDuration / fadeOutDuration;

			fadeOutAction.warp( 1.0, startEndRatio, duration );
			this.warp( endStartRatio, 1.0, duration );

		}

		return this;

	}

	crossFadeTo( fadeInAction, duration, warp ) {

		return fadeInAction.crossFadeFrom( this, duration, warp );

	}

	stopFading() {

		const weightInterpolant = this._weightInterpolant;

		if ( weightInterpolant !== null ) {

			this._weightInterpolant = null;
			this._mixer._takeBackControlInterpolant( weightInterpolant );

		}

		return this;

	}

	// Time Scale Control

	// set the time scale stopping any scheduled warping
	// although .paused = true yields an effective time scale of zero, this
	// method does *not* change .paused, because it would be confusing
	setEffectiveTimeScale( timeScale ) {

		this.timeScale = timeScale;
		this._effectiveTimeScale = this.paused ? 0 : timeScale;

		return this.stopWarping();

	}

	// return the time scale considering warping and .paused
	getEffectiveTimeScale() {

		return this._effectiveTimeScale;

	}

	setDuration( duration ) {

		this.timeScale = this._clip.duration / duration;

		return this.stopWarping();

	}

	syncWith( action ) {

		this.time = action.time;
		this.timeScale = action.timeScale;

		return this.stopWarping();

	}

	halt( duration ) {

		return this.warp( this._effectiveTimeScale, 0, duration );

	}

	warp( startTimeScale, endTimeScale, duration ) {

		const mixer = this._mixer,
			now = mixer.time,
			timeScale = this.timeScale;

		let interpolant = this._timeScaleInterpolant;

		if ( interpolant === null ) {

			interpolant = mixer._lendControlInterpolant();
			this._timeScaleInterpolant = interpolant;

		}

		const times = interpolant.parameterPositions,
			values = interpolant.sampleValues;

		times[ 0 ] = now;
		times[ 1 ] = now + duration;

		values[ 0 ] = startTimeScale / timeScale;
		values[ 1 ] = endTimeScale / timeScale;

		return this;

	}

	stopWarping() {

		const timeScaleInterpolant = this._timeScaleInterpolant;

		if ( timeScaleInterpolant !== null ) {

			this._timeScaleInterpolant = null;
			this._mixer._takeBackControlInterpolant( timeScaleInterpolant );

		}

		return this;

	}

	// Object Accessors

	getMixer() {

		return this._mixer;

	}

	getClip() {

		return this._clip;

	}

	getRoot() {

		return this._localRoot || this._mixer._root;

	}

	// Interna

	_update( time, deltaTime, timeDirection, accuIndex ) {

		// called by the mixer

		if ( ! this.enabled ) {

			// call ._updateWeight() to update ._effectiveWeight

			this._updateWeight( time );
			return;

		}

		const startTime = this._startTime;

		if ( startTime !== null ) {

			// check for scheduled start of action

			const timeRunning = ( time - startTime ) * timeDirection;
			if ( timeRunning < 0 || timeDirection === 0 ) {

				deltaTime = 0;

			} else {


				this._startTime = null; // unschedule
				deltaTime = timeDirection * timeRunning;

			}

		}

		// apply time scale and advance time

		deltaTime *= this._updateTimeScale( time );
		const clipTime = this._updateTime( deltaTime );

		// note: _updateTime may disable the action resulting in
		// an effective weight of 0

		const weight = this._updateWeight( time );

		if ( weight > 0 ) {

			const interpolants = this._interpolants;
			const propertyMixers = this._propertyBindings;

			switch ( this.blendMode ) {

				case AdditiveAnimationBlendMode:

					for ( let j = 0, m = interpolants.length; j !== m; ++ j ) {

						interpolants[ j ].evaluate( clipTime );
						propertyMixers[ j ].accumulateAdditive( weight );

					}

					break;

				case NormalAnimationBlendMode:
				default:

					for ( let j = 0, m = interpolants.length; j !== m; ++ j ) {

						interpolants[ j ].evaluate( clipTime );
						propertyMixers[ j ].accumulate( accuIndex, weight );

					}

			}

		}

	}

	_updateWeight( time ) {

		let weight = 0;

		if ( this.enabled ) {

			weight = this.weight;
			const interpolant = this._weightInterpolant;

			if ( interpolant !== null ) {

				const interpolantValue = interpolant.evaluate( time )[ 0 ];

				weight *= interpolantValue;

				if ( time > interpolant.parameterPositions[ 1 ] ) {

					this.stopFading();

					if ( interpolantValue === 0 ) {

						// faded out, disable
						this.enabled = false;

					}

				}

			}

		}

		this._effectiveWeight = weight;
		return weight;

	}

	_updateTimeScale( time ) {

		let timeScale = 0;

		if ( ! this.paused ) {

			timeScale = this.timeScale;

			const interpolant = this._timeScaleInterpolant;

			if ( interpolant !== null ) {

				const interpolantValue = interpolant.evaluate( time )[ 0 ];

				timeScale *= interpolantValue;

				if ( time > interpolant.parameterPositions[ 1 ] ) {

					this.stopWarping();

					if ( timeScale === 0 ) {

						// motion has halted, pause
						this.paused = true;

					} else {

						// warp done - apply final time scale
						this.timeScale = timeScale;

					}

				}

			}

		}

		this._effectiveTimeScale = timeScale;
		return timeScale;

	}

	_updateTime( deltaTime ) {

		const duration = this._clip.duration;
		const loop = this.loop;

		let time = this.time + deltaTime;
		let loopCount = this._loopCount;

		const pingPong = ( loop === LoopPingPong );

		if ( deltaTime === 0 ) {

			if ( loopCount === - 1 ) return time;

			return ( pingPong && ( loopCount & 1 ) === 1 ) ? duration - time : time;

		}

		if ( loop === LoopOnce ) {

			if ( loopCount === - 1 ) {

				// just started

				this._loopCount = 0;
				this._setEndings( true, true, false );

			}

			handle_stop: {

				if ( time >= duration ) {

					time = duration;

				} else if ( time < 0 ) {

					time = 0;

				} else {

					this.time = time;

					break handle_stop;

				}

				if ( this.clampWhenFinished ) this.paused = true;
				else this.enabled = false;

				this.time = time;

				this._mixer.dispatchEvent( {
					type: 'finished', action: this,
					direction: deltaTime < 0 ? - 1 : 1
				} );

			}

		} else { // repetitive Repeat or PingPong

			if ( loopCount === - 1 ) {

				// just started

				if ( deltaTime >= 0 ) {

					loopCount = 0;

					this._setEndings( true, this.repetitions === 0, pingPong );

				} else {

					// when looping in reverse direction, the initial
					// transition through zero counts as a repetition,
					// so leave loopCount at -1

					this._setEndings( this.repetitions === 0, true, pingPong );

				}

			}

			if ( time >= duration || time < 0 ) {

				// wrap around

				const loopDelta = Math.floor( time / duration ); // signed
				time -= duration * loopDelta;

				loopCount += Math.abs( loopDelta );

				const pending = this.repetitions - loopCount;

				if ( pending <= 0 ) {

					// have to stop (switch state, clamp time, fire event)

					if ( this.clampWhenFinished ) this.paused = true;
					else this.enabled = false;

					time = deltaTime > 0 ? duration : 0;

					this.time = time;

					this._mixer.dispatchEvent( {
						type: 'finished', action: this,
						direction: deltaTime > 0 ? 1 : - 1
					} );

				} else {

					// keep running

					if ( pending === 1 ) {

						// entering the last round

						const atStart = deltaTime < 0;
						this._setEndings( atStart, ! atStart, pingPong );

					} else {

						this._setEndings( false, false, pingPong );

					}

					this._loopCount = loopCount;

					this.time = time;

					this._mixer.dispatchEvent( {
						type: 'loop', action: this, loopDelta: loopDelta
					} );

				}

			} else {

				this.time = time;

			}

			if ( pingPong && ( loopCount & 1 ) === 1 ) {

				// invert time for the "pong round"

				return duration - time;

			}

		}

		return time;

	}

	_setEndings( atStart, atEnd, pingPong ) {

		const settings = this._interpolantSettings;

		if ( pingPong ) {

			settings.endingStart = ZeroSlopeEnding;
			settings.endingEnd = ZeroSlopeEnding;

		} else {

			// assuming for LoopOnce atStart == atEnd == true

			if ( atStart ) {

				settings.endingStart = this.zeroSlopeAtStart ? ZeroSlopeEnding : ZeroCurvatureEnding;

			} else {

				settings.endingStart = WrapAroundEnding;

			}

			if ( atEnd ) {

				settings.endingEnd = this.zeroSlopeAtEnd ? ZeroSlopeEnding : ZeroCurvatureEnding;

			} else {

				settings.endingEnd 	 = WrapAroundEnding;

			}

		}

	}

	_scheduleFading( duration, weightNow, weightThen ) {

		const mixer = this._mixer, now = mixer.time;
		let interpolant = this._weightInterpolant;

		if ( interpolant === null ) {

			interpolant = mixer._lendControlInterpolant();
			this._weightInterpolant = interpolant;

		}

		const times = interpolant.parameterPositions,
			values = interpolant.sampleValues;

		times[ 0 ] = now;
		values[ 0 ] = weightNow;
		times[ 1 ] = now + duration;
		values[ 1 ] = weightThen;

		return this;

	}

}

const _controlInterpolantsResultBuffer = new Float32Array( 1 );


class AnimationMixer extends EventDispatcher {

	constructor( root ) {

		super();

		this._root = root;
		this._initMemoryManager();
		this._accuIndex = 0;
		this.time = 0;
		this.timeScale = 1.0;

	}

	_bindAction( action, prototypeAction ) {

		const root = action._localRoot || this._root,
			tracks = action._clip.tracks,
			nTracks = tracks.length,
			bindings = action._propertyBindings,
			interpolants = action._interpolants,
			rootUuid = root.uuid,
			bindingsByRoot = this._bindingsByRootAndName;

		let bindingsByName = bindingsByRoot[ rootUuid ];

		if ( bindingsByName === undefined ) {

			bindingsByName = {};
			bindingsByRoot[ rootUuid ] = bindingsByName;

		}

		for ( let i = 0; i !== nTracks; ++ i ) {

			const track = tracks[ i ],
				trackName = track.name;

			let binding = bindingsByName[ trackName ];

			if ( binding !== undefined ) {

				++ binding.referenceCount;
				bindings[ i ] = binding;

			} else {

				binding = bindings[ i ];

				if ( binding !== undefined ) {

					// existing binding, make sure the cache knows

					if ( binding._cacheIndex === null ) {

						++ binding.referenceCount;
						this._addInactiveBinding( binding, rootUuid, trackName );

					}

					continue;

				}

				const path = prototypeAction && prototypeAction.
					_propertyBindings[ i ].binding.parsedPath;

				binding = new PropertyMixer(
					PropertyBinding.create( root, trackName, path ),
					track.ValueTypeName, track.getValueSize() );

				++ binding.referenceCount;
				this._addInactiveBinding( binding, rootUuid, trackName );

				bindings[ i ] = binding;

			}

			interpolants[ i ].resultBuffer = binding.buffer;

		}

	}

	_activateAction( action ) {

		if ( ! this._isActiveAction( action ) ) {

			if ( action._cacheIndex === null ) {

				// this action has been forgotten by the cache, but the user
				// appears to be still using it -> rebind

				const rootUuid = ( action._localRoot || this._root ).uuid,
					clipUuid = action._clip.uuid,
					actionsForClip = this._actionsByClip[ clipUuid ];

				this._bindAction( action,
					actionsForClip && actionsForClip.knownActions[ 0 ] );

				this._addInactiveAction( action, clipUuid, rootUuid );

			}

			const bindings = action._propertyBindings;

			// increment reference counts / sort out state
			for ( let i = 0, n = bindings.length; i !== n; ++ i ) {

				const binding = bindings[ i ];

				if ( binding.useCount ++ === 0 ) {

					this._lendBinding( binding );
					binding.saveOriginalState();

				}

			}

			this._lendAction( action );

		}

	}

	_deactivateAction( action ) {

		if ( this._isActiveAction( action ) ) {

			const bindings = action._propertyBindings;

			// decrement reference counts / sort out state
			for ( let i = 0, n = bindings.length; i !== n; ++ i ) {

				const binding = bindings[ i ];

				if ( -- binding.useCount === 0 ) {

					binding.restoreOriginalState();
					this._takeBackBinding( binding );

				}

			}

			this._takeBackAction( action );

		}

	}

	// Memory manager

	_initMemoryManager() {

		this._actions = []; // 'nActiveActions' followed by inactive ones
		this._nActiveActions = 0;

		this._actionsByClip = {};
		// inside:
		// {
		// 	knownActions: Array< AnimationAction > - used as prototypes
		// 	actionByRoot: AnimationAction - lookup
		// }


		this._bindings = []; // 'nActiveBindings' followed by inactive ones
		this._nActiveBindings = 0;

		this._bindingsByRootAndName = {}; // inside: Map< name, PropertyMixer >


		this._controlInterpolants = []; // same game as above
		this._nActiveControlInterpolants = 0;

		const scope = this;

		this.stats = {

			actions: {
				get total() {

					return scope._actions.length;

				},
				get inUse() {

					return scope._nActiveActions;

				}
			},
			bindings: {
				get total() {

					return scope._bindings.length;

				},
				get inUse() {

					return scope._nActiveBindings;

				}
			},
			controlInterpolants: {
				get total() {

					return scope._controlInterpolants.length;

				},
				get inUse() {

					return scope._nActiveControlInterpolants;

				}
			}

		};

	}

	// Memory management for AnimationAction objects

	_isActiveAction( action ) {

		const index = action._cacheIndex;
		return index !== null && index < this._nActiveActions;

	}

	_addInactiveAction( action, clipUuid, rootUuid ) {

		const actions = this._actions,
			actionsByClip = this._actionsByClip;

		let actionsForClip = actionsByClip[ clipUuid ];

		if ( actionsForClip === undefined ) {

			actionsForClip = {

				knownActions: [ action ],
				actionByRoot: {}

			};

			action._byClipCacheIndex = 0;

			actionsByClip[ clipUuid ] = actionsForClip;

		} else {

			const knownActions = actionsForClip.knownActions;

			action._byClipCacheIndex = knownActions.length;
			knownActions.push( action );

		}

		action._cacheIndex = actions.length;
		actions.push( action );

		actionsForClip.actionByRoot[ rootUuid ] = action;

	}

	_removeInactiveAction( action ) {

		const actions = this._actions,
			lastInactiveAction = actions[ actions.length - 1 ],
			cacheIndex = action._cacheIndex;

		lastInactiveAction._cacheIndex = cacheIndex;
		actions[ cacheIndex ] = lastInactiveAction;
		actions.pop();

		action._cacheIndex = null;


		const clipUuid = action._clip.uuid,
			actionsByClip = this._actionsByClip,
			actionsForClip = actionsByClip[ clipUuid ],
			knownActionsForClip = actionsForClip.knownActions,

			lastKnownAction =
				knownActionsForClip[ knownActionsForClip.length - 1 ],

			byClipCacheIndex = action._byClipCacheIndex;

		lastKnownAction._byClipCacheIndex = byClipCacheIndex;
		knownActionsForClip[ byClipCacheIndex ] = lastKnownAction;
		knownActionsForClip.pop();

		action._byClipCacheIndex = null;


		const actionByRoot = actionsForClip.actionByRoot,
			rootUuid = ( action._localRoot || this._root ).uuid;

		delete actionByRoot[ rootUuid ];

		if ( knownActionsForClip.length === 0 ) {

			delete actionsByClip[ clipUuid ];

		}

		this._removeInactiveBindingsForAction( action );

	}

	_removeInactiveBindingsForAction( action ) {

		const bindings = action._propertyBindings;

		for ( let i = 0, n = bindings.length; i !== n; ++ i ) {

			const binding = bindings[ i ];

			if ( -- binding.referenceCount === 0 ) {

				this._removeInactiveBinding( binding );

			}

		}

	}

	_lendAction( action ) {

		// [ active actions |  inactive actions  ]
		// [  active actions >| inactive actions ]
		//                 s        a
		//                  <-swap->
		//                 a        s

		const actions = this._actions,
			prevIndex = action._cacheIndex,

			lastActiveIndex = this._nActiveActions ++,

			firstInactiveAction = actions[ lastActiveIndex ];

		action._cacheIndex = lastActiveIndex;
		actions[ lastActiveIndex ] = action;

		firstInactiveAction._cacheIndex = prevIndex;
		actions[ prevIndex ] = firstInactiveAction;

	}

	_takeBackAction( action ) {

		// [  active actions  | inactive actions ]
		// [ active actions |< inactive actions  ]
		//        a        s
		//         <-swap->
		//        s        a

		const actions = this._actions,
			prevIndex = action._cacheIndex,

			firstInactiveIndex = -- this._nActiveActions,

			lastActiveAction = actions[ firstInactiveIndex ];

		action._cacheIndex = firstInactiveIndex;
		actions[ firstInactiveIndex ] = action;

		lastActiveAction._cacheIndex = prevIndex;
		actions[ prevIndex ] = lastActiveAction;

	}

	// Memory management for PropertyMixer objects

	_addInactiveBinding( binding, rootUuid, trackName ) {

		const bindingsByRoot = this._bindingsByRootAndName,
			bindings = this._bindings;

		let bindingByName = bindingsByRoot[ rootUuid ];

		if ( bindingByName === undefined ) {

			bindingByName = {};
			bindingsByRoot[ rootUuid ] = bindingByName;

		}

		bindingByName[ trackName ] = binding;

		binding._cacheIndex = bindings.length;
		bindings.push( binding );

	}

	_removeInactiveBinding( binding ) {

		const bindings = this._bindings,
			propBinding = binding.binding,
			rootUuid = propBinding.rootNode.uuid,
			trackName = propBinding.path,
			bindingsByRoot = this._bindingsByRootAndName,
			bindingByName = bindingsByRoot[ rootUuid ],

			lastInactiveBinding = bindings[ bindings.length - 1 ],
			cacheIndex = binding._cacheIndex;

		lastInactiveBinding._cacheIndex = cacheIndex;
		bindings[ cacheIndex ] = lastInactiveBinding;
		bindings.pop();

		delete bindingByName[ trackName ];

		if ( Object.keys( bindingByName ).length === 0 ) {

			delete bindingsByRoot[ rootUuid ];

		}

	}

	_lendBinding( binding ) {

		const bindings = this._bindings,
			prevIndex = binding._cacheIndex,

			lastActiveIndex = this._nActiveBindings ++,

			firstInactiveBinding = bindings[ lastActiveIndex ];

		binding._cacheIndex = lastActiveIndex;
		bindings[ lastActiveIndex ] = binding;

		firstInactiveBinding._cacheIndex = prevIndex;
		bindings[ prevIndex ] = firstInactiveBinding;

	}

	_takeBackBinding( binding ) {

		const bindings = this._bindings,
			prevIndex = binding._cacheIndex,

			firstInactiveIndex = -- this._nActiveBindings,

			lastActiveBinding = bindings[ firstInactiveIndex ];

		binding._cacheIndex = firstInactiveIndex;
		bindings[ firstInactiveIndex ] = binding;

		lastActiveBinding._cacheIndex = prevIndex;
		bindings[ prevIndex ] = lastActiveBinding;

	}


	// Memory management of Interpolants for weight and time scale

	_lendControlInterpolant() {

		const interpolants = this._controlInterpolants,
			lastActiveIndex = this._nActiveControlInterpolants ++;

		let interpolant = interpolants[ lastActiveIndex ];

		if ( interpolant === undefined ) {

			interpolant = new LinearInterpolant(
				new Float32Array( 2 ), new Float32Array( 2 ),
				1, _controlInterpolantsResultBuffer );

			interpolant.__cacheIndex = lastActiveIndex;
			interpolants[ lastActiveIndex ] = interpolant;

		}

		return interpolant;

	}

	_takeBackControlInterpolant( interpolant ) {

		const interpolants = this._controlInterpolants,
			prevIndex = interpolant.__cacheIndex,

			firstInactiveIndex = -- this._nActiveControlInterpolants,

			lastActiveInterpolant = interpolants[ firstInactiveIndex ];

		interpolant.__cacheIndex = firstInactiveIndex;
		interpolants[ firstInactiveIndex ] = interpolant;

		lastActiveInterpolant.__cacheIndex = prevIndex;
		interpolants[ prevIndex ] = lastActiveInterpolant;

	}

	// return an action for a clip optionally using a custom root target
	// object (this method allocates a lot of dynamic memory in case a
	// previously unknown clip/root combination is specified)
	clipAction( clip, optionalRoot, blendMode ) {

		const root = optionalRoot || this._root,
			rootUuid = root.uuid;

		let clipObject = typeof clip === 'string' ? AnimationClip.findByName( root, clip ) : clip;

		const clipUuid = clipObject !== null ? clipObject.uuid : clip;

		const actionsForClip = this._actionsByClip[ clipUuid ];
		let prototypeAction = null;

		if ( blendMode === undefined ) {

			if ( clipObject !== null ) {

				blendMode = clipObject.blendMode;

			} else {

				blendMode = NormalAnimationBlendMode;

			}

		}

		if ( actionsForClip !== undefined ) {

			const existingAction = actionsForClip.actionByRoot[ rootUuid ];

			if ( existingAction !== undefined && existingAction.blendMode === blendMode ) {

				return existingAction;

			}

			// we know the clip, so we don't have to parse all
			// the bindings again but can just copy
			prototypeAction = actionsForClip.knownActions[ 0 ];

			// also, take the clip from the prototype action
			if ( clipObject === null )
				clipObject = prototypeAction._clip;

		}

		// clip must be known when specified via string
		if ( clipObject === null ) return null;

		// allocate all resources required to run it
		const newAction = new AnimationAction( this, clipObject, optionalRoot, blendMode );

		this._bindAction( newAction, prototypeAction );

		// and make the action known to the memory manager
		this._addInactiveAction( newAction, clipUuid, rootUuid );

		return newAction;

	}

	// get an existing action
	existingAction( clip, optionalRoot ) {

		const root = optionalRoot || this._root,
			rootUuid = root.uuid,

			clipObject = typeof clip === 'string' ?
				AnimationClip.findByName( root, clip ) : clip,

			clipUuid = clipObject ? clipObject.uuid : clip,

			actionsForClip = this._actionsByClip[ clipUuid ];

		if ( actionsForClip !== undefined ) {

			return actionsForClip.actionByRoot[ rootUuid ] || null;

		}

		return null;

	}

	// deactivates all previously scheduled actions
	stopAllAction() {

		const actions = this._actions,
			nActions = this._nActiveActions;

		for ( let i = nActions - 1; i >= 0; -- i ) {

			actions[ i ].stop();

		}

		return this;

	}

	// advance the time and update apply the animation
	update( deltaTime ) {

		deltaTime *= this.timeScale;

		const actions = this._actions,
			nActions = this._nActiveActions,

			time = this.time += deltaTime,
			timeDirection = Math.sign( deltaTime ),

			accuIndex = this._accuIndex ^= 1;

		// run active actions

		for ( let i = 0; i !== nActions; ++ i ) {

			const action = actions[ i ];

			action._update( time, deltaTime, timeDirection, accuIndex );

		}

		// update scene graph

		const bindings = this._bindings,
			nBindings = this._nActiveBindings;

		for ( let i = 0; i !== nBindings; ++ i ) {

			bindings[ i ].apply( accuIndex );

		}

		return this;

	}

	// Allows you to seek to a specific time in an animation.
	setTime( timeInSeconds ) {

		this.time = 0; // Zero out time attribute for AnimationMixer object;
		for ( let i = 0; i < this._actions.length; i ++ ) {

			this._actions[ i ].time = 0; // Zero out time attribute for all associated AnimationAction objects.

		}

		return this.update( timeInSeconds ); // Update used to set exact time. Returns "this" AnimationMixer object.

	}

	// return this mixer's root target object
	getRoot() {

		return this._root;

	}

	// free all resources specific to a particular clip
	uncacheClip( clip ) {

		const actions = this._actions,
			clipUuid = clip.uuid,
			actionsByClip = this._actionsByClip,
			actionsForClip = actionsByClip[ clipUuid ];

		if ( actionsForClip !== undefined ) {

			// note: just calling _removeInactiveAction would mess up the
			// iteration state and also require updating the state we can
			// just throw away

			const actionsToRemove = actionsForClip.knownActions;

			for ( let i = 0, n = actionsToRemove.length; i !== n; ++ i ) {

				const action = actionsToRemove[ i ];

				this._deactivateAction( action );

				const cacheIndex = action._cacheIndex,
					lastInactiveAction = actions[ actions.length - 1 ];

				action._cacheIndex = null;
				action._byClipCacheIndex = null;

				lastInactiveAction._cacheIndex = cacheIndex;
				actions[ cacheIndex ] = lastInactiveAction;
				actions.pop();

				this._removeInactiveBindingsForAction( action );

			}

			delete actionsByClip[ clipUuid ];

		}

	}

	// free all resources specific to a particular root target object
	uncacheRoot( root ) {

		const rootUuid = root.uuid,
			actionsByClip = this._actionsByClip;

		for ( const clipUuid in actionsByClip ) {

			const actionByRoot = actionsByClip[ clipUuid ].actionByRoot,
				action = actionByRoot[ rootUuid ];

			if ( action !== undefined ) {

				this._deactivateAction( action );
				this._removeInactiveAction( action );

			}

		}

		const bindingsByRoot = this._bindingsByRootAndName,
			bindingByName = bindingsByRoot[ rootUuid ];

		if ( bindingByName !== undefined ) {

			for ( const trackName in bindingByName ) {

				const binding = bindingByName[ trackName ];
				binding.restoreOriginalState();
				this._removeInactiveBinding( binding );

			}

		}

	}

	// remove a targeted clip from the cache
	uncacheAction( clip, optionalRoot ) {

		const action = this.existingAction( clip, optionalRoot );

		if ( action !== null ) {

			this._deactivateAction( action );
			this._removeInactiveAction( action );

		}

	}

}

class Uniform {

	constructor( value ) {

		this.value = value;

	}

	clone() {

		return new Uniform( this.value.clone === undefined ? this.value : this.value.clone() );

	}

}

let _id = 0;

class UniformsGroup extends EventDispatcher {

	constructor() {

		super();

		this.isUniformsGroup = true;

		Object.defineProperty( this, 'id', { value: _id ++ } );

		this.name = '';

		this.usage = StaticDrawUsage;
		this.uniforms = [];

	}

	add( uniform ) {

		this.uniforms.push( uniform );

		return this;

	}

	remove( uniform ) {

		const index = this.uniforms.indexOf( uniform );

		if ( index !== - 1 ) this.uniforms.splice( index, 1 );

		return this;

	}

	setName( name ) {

		this.name = name;

		return this;

	}

	setUsage( value ) {

		this.usage = value;

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

		return this;

	}

	copy( source ) {

		this.name = source.name;
		this.usage = source.usage;

		const uniformsSource = source.uniforms;

		this.uniforms.length = 0;

		for ( let i = 0, l = uniformsSource.length; i < l; i ++ ) {

			const uniforms = Array.isArray( uniformsSource[ i ] ) ? uniformsSource[ i ] : [ uniformsSource[ i ] ];

			for ( let j = 0; j < uniforms.length; j ++ ) {

				this.uniforms.push( uniforms[ j ].clone() );

			}

		}

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

class InstancedInterleavedBuffer extends InterleavedBuffer {

	constructor( array, stride, meshPerAttribute = 1 ) {

		super( array, stride );

		this.isInstancedInterleavedBuffer = true;

		this.meshPerAttribute = meshPerAttribute;

	}

	copy( source ) {

		super.copy( source );

		this.meshPerAttribute = source.meshPerAttribute;

		return this;

	}

	clone( data ) {

		const ib = super.clone( data );

		ib.meshPerAttribute = this.meshPerAttribute;

		return ib;

	}

	toJSON( data ) {

		const json = super.toJSON( data );

		json.isInstancedInterleavedBuffer = true;
		json.meshPerAttribute = this.meshPerAttribute;

		return json;

	}

}

class GLBufferAttribute {

	constructor( buffer, type, itemSize, elementSize, count ) {

		this.isGLBufferAttribute = true;

		this.name = '';

		this.buffer = buffer;
		this.type = type;
		this.itemSize = itemSize;
		this.elementSize = elementSize;
		this.count = count;

		this.version = 0;

	}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	setBuffer( buffer ) {

		this.buffer = buffer;

		return this;

	}

	setType( type, elementSize ) {

		this.type = type;
		this.elementSize = elementSize;

		return this;

	}

	setItemSize( itemSize ) {

		this.itemSize = itemSize;

		return this;

	}

	setCount( count ) {

		this.count = count;

		return this;

	}

}

const _matrix = /*@__PURE__*/ new Matrix4();

class Raycaster {

	constructor( origin, direction, near = 0, far = Infinity ) {

		this.ray = new Ray( origin, direction );
		// direction is assumed to be normalized (for accurate distance calculations)

		this.near = near;
		this.far = far;
		this.camera = null;
		this.layers = new Layers();

		this.params = {
			Mesh: {},
			Line: { threshold: 1 },
			LOD: {},
			Points: { threshold: 1 },
			Sprite: {}
		};

	}

	set( origin, direction ) {

		// direction is assumed to be normalized (for accurate distance calculations)

		this.ray.set( origin, direction );

	}

	setFromCamera( coords, camera ) {

		if ( camera.isPerspectiveCamera ) {

			this.ray.origin.setFromMatrixPosition( camera.matrixWorld );
			this.ray.direction.set( coords.x, coords.y, 0.5 ).unproject( camera ).sub( this.ray.origin ).normalize();
			this.camera = camera;

		} else if ( camera.isOrthographicCamera ) {

			this.ray.origin.set( coords.x, coords.y, ( camera.near + camera.far ) / ( camera.near - camera.far ) ).unproject( camera ); // set origin in plane of camera
			this.ray.direction.set( 0, 0, - 1 ).transformDirection( camera.matrixWorld );
			this.camera = camera;

		} else {

			console.error( 'THREE.Raycaster: Unsupported camera type: ' + camera.type );

		}

	}

	setFromXRController( controller ) {

		_matrix.identity().extractRotation( controller.matrixWorld );

		this.ray.origin.setFromMatrixPosition( controller.matrixWorld );
		this.ray.direction.set( 0, 0, - 1 ).applyMatrix4( _matrix );

		return this;

	}

	intersectObject( object, recursive = true, intersects = [] ) {

		intersect( object, this, intersects, recursive );

		intersects.sort( ascSort );

		return intersects;

	}

	intersectObjects( objects, recursive = true, intersects = [] ) {

		for ( let i = 0, l = objects.length; i < l; i ++ ) {

			intersect( objects[ i ], this, intersects, recursive );

		}

		intersects.sort( ascSort );

		return intersects;

	}

}

function ascSort( a, b ) {

	return a.distance - b.distance;

}

function intersect( object, raycaster, intersects, recursive ) {

	let propagate = true;

	if ( object.layers.test( raycaster.layers ) ) {

		const result = object.raycast( raycaster, intersects );

		if ( result === false ) propagate = false;

	}

	if ( propagate === true && recursive === true ) {

		const children = object.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			intersect( children[ i ], raycaster, intersects, true );

		}

	}

}

/**
 * Ref: https://en.wikipedia.org/wiki/Spherical_coordinate_system
 *
 * phi (the polar angle) is measured from the positive y-axis. The positive y-axis is up.
 * theta (the azimuthal angle) is measured from the positive z-axis.
 */
class Spherical {

	constructor( radius = 1, phi = 0, theta = 0 ) {

		this.radius = radius;
		this.phi = phi; // polar angle
		this.theta = theta; // azimuthal angle

		return this;

	}

	set( radius, phi, theta ) {

		this.radius = radius;
		this.phi = phi;
		this.theta = theta;

		return this;

	}

	copy( other ) {

		this.radius = other.radius;
		this.phi = other.phi;
		this.theta = other.theta;

		return this;

	}

	// restrict phi to be between EPS and PI-EPS
	makeSafe() {

		const EPS = 0.000001;
		this.phi = Math.max( EPS, Math.min( Math.PI - EPS, this.phi ) );

		return this;

	}

	setFromVector3( v ) {

		return this.setFromCartesianCoords( v.x, v.y, v.z );

	}

	setFromCartesianCoords( x, y, z ) {

		this.radius = Math.sqrt( x * x + y * y + z * z );

		if ( this.radius === 0 ) {

			this.theta = 0;
			this.phi = 0;

		} else {

			this.theta = Math.atan2( x, z );
			this.phi = Math.acos( clamp( y / this.radius, - 1, 1 ) );

		}

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

/**
 * Ref: https://en.wikipedia.org/wiki/Cylindrical_coordinate_system
 */

class Cylindrical {

	constructor( radius = 1, theta = 0, y = 0 ) {

		this.radius = radius; // distance from the origin to a point in the x-z plane
		this.theta = theta; // counterclockwise angle in the x-z plane measured in radians from the positive z-axis
		this.y = y; // height above the x-z plane

		return this;

	}

	set( radius, theta, y ) {

		this.radius = radius;
		this.theta = theta;
		this.y = y;

		return this;

	}

	copy( other ) {

		this.radius = other.radius;
		this.theta = other.theta;
		this.y = other.y;

		return this;

	}

	setFromVector3( v ) {

		return this.setFromCartesianCoords( v.x, v.y, v.z );

	}

	setFromCartesianCoords( x, y, z ) {

		this.radius = Math.sqrt( x * x + z * z );
		this.theta = Math.atan2( x, z );
		this.y = y;

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

class Matrix2 {

	constructor( n11, n12, n21, n22 ) {

		Matrix2.prototype.isMatrix2 = true;

		this.elements = [
			1, 0,
			0, 1,
		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n21, n22 );

		}

	}

	identity() {

		this.set(
			1, 0,
			0, 1,
		);

		return this;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 4; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	set( n11, n12, n21, n22 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 2 ] = n12;
		te[ 1 ] = n21; te[ 3 ] = n22;

		return this;

	}

}

const _vector$4 = /*@__PURE__*/ new Vector2();

class Box2 {

	constructor( min = new Vector2( + Infinity, + Infinity ), max = new Vector2( - Infinity, - Infinity ) ) {

		this.isBox2 = true;

		this.min = min;
		this.max = max;

	}

	set( min, max ) {

		this.min.copy( min );
		this.max.copy( max );

		return this;

	}

	setFromPoints( points ) {

		this.makeEmpty();

		for ( let i = 0, il = points.length; i < il; i ++ ) {

			this.expandByPoint( points[ i ] );

		}

		return this;

	}

	setFromCenterAndSize( center, size ) {

		const halfSize = _vector$4.copy( size ).multiplyScalar( 0.5 );
		this.min.copy( center ).sub( halfSize );
		this.max.copy( center ).add( halfSize );

		return this;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( box ) {

		this.min.copy( box.min );
		this.max.copy( box.max );

		return this;

	}

	makeEmpty() {

		this.min.x = this.min.y = + Infinity;
		this.max.x = this.max.y = - Infinity;

		return this;

	}

	isEmpty() {

		// this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

		return ( this.max.x < this.min.x ) || ( this.max.y < this.min.y );

	}

	getCenter( target ) {

		return this.isEmpty() ? target.set( 0, 0 ) : target.addVectors( this.min, this.max ).multiplyScalar( 0.5 );

	}

	getSize( target ) {

		return this.isEmpty() ? target.set( 0, 0 ) : target.subVectors( this.max, this.min );

	}

	expandByPoint( point ) {

		this.min.min( point );
		this.max.max( point );

		return this;

	}

	expandByVector( vector ) {

		this.min.sub( vector );
		this.max.add( vector );

		return this;

	}

	expandByScalar( scalar ) {

		this.min.addScalar( - scalar );
		this.max.addScalar( scalar );

		return this;

	}

	containsPoint( point ) {

		return point.x >= this.min.x && point.x <= this.max.x &&
			point.y >= this.min.y && point.y <= this.max.y;

	}

	containsBox( box ) {

		return this.min.x <= box.min.x && box.max.x <= this.max.x &&
			this.min.y <= box.min.y && box.max.y <= this.max.y;

	}

	getParameter( point, target ) {

		// This can potentially have a divide by zero if the box
		// has a size dimension of 0.

		return target.set(
			( point.x - this.min.x ) / ( this.max.x - this.min.x ),
			( point.y - this.min.y ) / ( this.max.y - this.min.y )
		);

	}

	intersectsBox( box ) {

		// using 4 splitting planes to rule out intersections

		return box.max.x >= this.min.x && box.min.x <= this.max.x &&
			box.max.y >= this.min.y && box.min.y <= this.max.y;

	}

	clampPoint( point, target ) {

		return target.copy( point ).clamp( this.min, this.max );

	}

	distanceToPoint( point ) {

		return this.clampPoint( point, _vector$4 ).distanceTo( point );

	}

	intersect( box ) {

		this.min.max( box.min );
		this.max.min( box.max );

		if ( this.isEmpty() ) this.makeEmpty();

		return this;

	}

	union( box ) {

		this.min.min( box.min );
		this.max.max( box.max );

		return this;

	}

	translate( offset ) {

		this.min.add( offset );
		this.max.add( offset );

		return this;

	}

	equals( box ) {

		return box.min.equals( this.min ) && box.max.equals( this.max );

	}

}

const _startP = /*@__PURE__*/ new Vector3();
const _startEnd = /*@__PURE__*/ new Vector3();

class Line3 {

	constructor( start = new Vector3(), end = new Vector3() ) {

		this.start = start;
		this.end = end;

	}

	set( start, end ) {

		this.start.copy( start );
		this.end.copy( end );

		return this;

	}

	copy( line ) {

		this.start.copy( line.start );
		this.end.copy( line.end );

		return this;

	}

	getCenter( target ) {

		return target.addVectors( this.start, this.end ).multiplyScalar( 0.5 );

	}

	delta( target ) {

		return target.subVectors( this.end, this.start );

	}

	distanceSq() {

		return this.start.distanceToSquared( this.end );

	}

	distance() {

		return this.start.distanceTo( this.end );

	}

	at( t, target ) {

		return this.delta( target ).multiplyScalar( t ).add( this.start );

	}

	closestPointToPointParameter( point, clampToLine ) {

		_startP.subVectors( point, this.start );
		_startEnd.subVectors( this.end, this.start );

		const startEnd2 = _startEnd.dot( _startEnd );
		const startEnd_startP = _startEnd.dot( _startP );

		let t = startEnd_startP / startEnd2;

		if ( clampToLine ) {

			t = clamp( t, 0, 1 );

		}

		return t;

	}

	closestPointToPoint( point, clampToLine, target ) {

		const t = this.closestPointToPointParameter( point, clampToLine );

		return this.delta( target ).multiplyScalar( t ).add( this.start );

	}

	applyMatrix4( matrix ) {

		this.start.applyMatrix4( matrix );
		this.end.applyMatrix4( matrix );

		return this;

	}

	equals( line ) {

		return line.start.equals( this.start ) && line.end.equals( this.end );

	}

	clone() {

		return new this.constructor().copy( this );

	}

}

const _vector$3 = /*@__PURE__*/ new Vector3();

class SpotLightHelper extends Object3D {

	constructor( light, color ) {

		super();

		this.light = light;

		this.matrixAutoUpdate = false;

		this.color = color;

		this.type = 'SpotLightHelper';

		const geometry = new BufferGeometry();

		const positions = [
			0, 0, 0, 	0, 0, 1,
			0, 0, 0, 	1, 0, 1,
			0, 0, 0,	- 1, 0, 1,
			0, 0, 0, 	0, 1, 1,
			0, 0, 0, 	0, - 1, 1
		];

		for ( let i = 0, j = 1, l = 32; i < l; i ++, j ++ ) {

			const p1 = ( i / l ) * Math.PI * 2;
			const p2 = ( j / l ) * Math.PI * 2;

			positions.push(
				Math.cos( p1 ), Math.sin( p1 ), 1,
				Math.cos( p2 ), Math.sin( p2 ), 1
			);

		}

		geometry.setAttribute( 'position', new Float32BufferAttribute( positions, 3 ) );

		const material = new LineBasicMaterial( { fog: false, toneMapped: false } );

		this.cone = new LineSegments( geometry, material );
		this.add( this.cone );

		this.update();

	}

	dispose() {

		this.cone.geometry.dispose();
		this.cone.material.dispose();

	}

	update() {

		this.light.updateWorldMatrix( true, false );
		this.light.target.updateWorldMatrix( true, false );

		// update the local matrix based on the parent and light target transforms
		if ( this.parent ) {

			this.parent.updateWorldMatrix( true );

			this.matrix
				.copy( this.parent.matrixWorld )
				.invert()
				.multiply( this.light.matrixWorld );

		} else {

			this.matrix.copy( this.light.matrixWorld );

		}

		this.matrixWorld.copy( this.light.matrixWorld );

		const coneLength = this.light.distance ? this.light.distance : 1000;
		const coneWidth = coneLength * Math.tan( this.light.angle );

		this.cone.scale.set( coneWidth, coneWidth, coneLength );

		_vector$3.setFromMatrixPosition( this.light.target.matrixWorld );

		this.cone.lookAt( _vector$3 );

		if ( this.color !== undefined ) {

			this.cone.material.color.set( this.color );

		} else {

			this.cone.material.color.copy( this.light.color );

		}

	}

}

const _vector$2 = /*@__PURE__*/ new Vector3();
const _boneMatrix = /*@__PURE__*/ new Matrix4();
const _matrixWorldInv = /*@__PURE__*/ new Matrix4();


class SkeletonHelper extends LineSegments {

	constructor( object ) {

		const bones = getBoneList( object );

		const geometry = new BufferGeometry();

		const vertices = [];
		const colors = [];

		const color1 = new Color( 0, 0, 1 );
		const color2 = new Color( 0, 1, 0 );

		for ( let i = 0; i < bones.length; i ++ ) {

			const bone = bones[ i ];

			if ( bone.parent && bone.parent.isBone ) {

				vertices.push( 0, 0, 0 );
				vertices.push( 0, 0, 0 );
				colors.push( color1.r, color1.g, color1.b );
				colors.push( color2.r, color2.g, color2.b );

			}

		}

		geometry.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		geometry.setAttribute( 'color', new Float32BufferAttribute( colors, 3 ) );

		const material = new LineBasicMaterial( { vertexColors: true, depthTest: false, depthWrite: false, toneMapped: false, transparent: true } );

		super( geometry, material );

		this.isSkeletonHelper = true;

		this.type = 'SkeletonHelper';

		this.root = object;
		this.bones = bones;

		this.matrix = object.matrixWorld;
		this.matrixAutoUpdate = false;

	}

	updateMatrixWorld( force ) {

		const bones = this.bones;

		const geometry = this.geometry;
		const position = geometry.getAttribute( 'position' );

		_matrixWorldInv.copy( this.root.matrixWorld ).invert();

		for ( let i = 0, j = 0; i < bones.length; i ++ ) {

			const bone = bones[ i ];

			if ( bone.parent && bone.parent.isBone ) {

				_boneMatrix.multiplyMatrices( _matrixWorldInv, bone.matrixWorld );
				_vector$2.setFromMatrixPosition( _boneMatrix );
				position.setXYZ( j, _vector$2.x, _vector$2.y, _vector$2.z );

				_boneMatrix.multiplyMatrices( _matrixWorldInv, bone.parent.matrixWorld );
				_vector$2.setFromMatrixPosition( _boneMatrix );
				position.setXYZ( j + 1, _vector$2.x, _vector$2.y, _vector$2.z );

				j += 2;

			}

		}

		geometry.getAttribute( 'position' ).needsUpdate = true;

		super.updateMatrixWorld( force );

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}


function getBoneList( object ) {

	const boneList = [];

	if ( object.isBone === true ) {

		boneList.push( object );

	}

	for ( let i = 0; i < object.children.length; i ++ ) {

		boneList.push.apply( boneList, getBoneList( object.children[ i ] ) );

	}

	return boneList;

}

class PointLightHelper extends Mesh {

	constructor( light, sphereSize, color ) {

		const geometry = new SphereGeometry( sphereSize, 4, 2 );
		const material = new MeshBasicMaterial( { wireframe: true, fog: false, toneMapped: false } );

		super( geometry, material );

		this.light = light;

		this.color = color;

		this.type = 'PointLightHelper';

		this.matrix = this.light.matrixWorld;
		this.matrixAutoUpdate = false;

		this.update();


		/*
	// TODO: delete this comment?
	const distanceGeometry = new THREE.IcosahedronGeometry( 1, 2 );
	const distanceMaterial = new THREE.MeshBasicMaterial( { color: hexColor, fog: false, wireframe: true, opacity: 0.1, transparent: true } );

	this.lightSphere = new THREE.Mesh( bulbGeometry, bulbMaterial );
	this.lightDistance = new THREE.Mesh( distanceGeometry, distanceMaterial );

	const d = light.distance;

	if ( d === 0.0 ) {

		this.lightDistance.visible = false;

	} else {

		this.lightDistance.scale.set( d, d, d );

	}

	this.add( this.lightDistance );
	*/

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

	update() {

		this.light.updateWorldMatrix( true, false );

		if ( this.color !== undefined ) {

			this.material.color.set( this.color );

		} else {

			this.material.color.copy( this.light.color );

		}

		/*
		const d = this.light.distance;

		if ( d === 0.0 ) {

			this.lightDistance.visible = false;

		} else {

			this.lightDistance.visible = true;
			this.lightDistance.scale.set( d, d, d );

		}
		*/

	}

}

const _vector$1 = /*@__PURE__*/ new Vector3();
const _color1 = /*@__PURE__*/ new Color();
const _color2 = /*@__PURE__*/ new Color();

class HemisphereLightHelper extends Object3D {

	constructor( light, size, color ) {

		super();

		this.light = light;

		this.matrix = light.matrixWorld;
		this.matrixAutoUpdate = false;

		this.color = color;

		this.type = 'HemisphereLightHelper';

		const geometry = new OctahedronGeometry( size );
		geometry.rotateY( Math.PI * 0.5 );

		this.material = new MeshBasicMaterial( { wireframe: true, fog: false, toneMapped: false } );
		if ( this.color === undefined ) this.material.vertexColors = true;

		const position = geometry.getAttribute( 'position' );
		const colors = new Float32Array( position.count * 3 );

		geometry.setAttribute( 'color', new BufferAttribute( colors, 3 ) );

		this.add( new Mesh( geometry, this.material ) );

		this.update();

	}

	dispose() {

		this.children[ 0 ].geometry.dispose();
		this.children[ 0 ].material.dispose();

	}

	update() {

		const mesh = this.children[ 0 ];

		if ( this.color !== undefined ) {

			this.material.color.set( this.color );

		} else {

			const colors = mesh.geometry.getAttribute( 'color' );

			_color1.copy( this.light.color );
			_color2.copy( this.light.groundColor );

			for ( let i = 0, l = colors.count; i < l; i ++ ) {

				const color = ( i < ( l / 2 ) ) ? _color1 : _color2;

				colors.setXYZ( i, color.r, color.g, color.b );

			}

			colors.needsUpdate = true;

		}

		this.light.updateWorldMatrix( true, false );

		mesh.lookAt( _vector$1.setFromMatrixPosition( this.light.matrixWorld ).negate() );

	}

}

class GridHelper extends LineSegments {

	constructor( size = 10, divisions = 10, color1 = 0x444444, color2 = 0x888888 ) {

		color1 = new Color( color1 );
		color2 = new Color( color2 );

		const center = divisions / 2;
		const step = size / divisions;
		const halfSize = size / 2;

		const vertices = [], colors = [];

		for ( let i = 0, j = 0, k = - halfSize; i <= divisions; i ++, k += step ) {

			vertices.push( - halfSize, 0, k, halfSize, 0, k );
			vertices.push( k, 0, - halfSize, k, 0, halfSize );

			const color = i === center ? color1 : color2;

			color.toArray( colors, j ); j += 3;
			color.toArray( colors, j ); j += 3;
			color.toArray( colors, j ); j += 3;
			color.toArray( colors, j ); j += 3;

		}

		const geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		geometry.setAttribute( 'color', new Float32BufferAttribute( colors, 3 ) );

		const material = new LineBasicMaterial( { vertexColors: true, toneMapped: false } );

		super( geometry, material );

		this.type = 'GridHelper';

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}

class PolarGridHelper extends LineSegments {

	constructor( radius = 10, sectors = 16, rings = 8, divisions = 64, color1 = 0x444444, color2 = 0x888888 ) {

		color1 = new Color( color1 );
		color2 = new Color( color2 );

		const vertices = [];
		const colors = [];

		// create the sectors

		if ( sectors > 1 ) {

			for ( let i = 0; i < sectors; i ++ ) {

				const v = ( i / sectors ) * ( Math.PI * 2 );

				const x = Math.sin( v ) * radius;
				const z = Math.cos( v ) * radius;

				vertices.push( 0, 0, 0 );
				vertices.push( x, 0, z );

				const color = ( i & 1 ) ? color1 : color2;

				colors.push( color.r, color.g, color.b );
				colors.push( color.r, color.g, color.b );

			}

		}

		// create the rings

		for ( let i = 0; i < rings; i ++ ) {

			const color = ( i & 1 ) ? color1 : color2;

			const r = radius - ( radius / rings * i );

			for ( let j = 0; j < divisions; j ++ ) {

				// first vertex

				let v = ( j / divisions ) * ( Math.PI * 2 );

				let x = Math.sin( v ) * r;
				let z = Math.cos( v ) * r;

				vertices.push( x, 0, z );
				colors.push( color.r, color.g, color.b );

				// second vertex

				v = ( ( j + 1 ) / divisions ) * ( Math.PI * 2 );

				x = Math.sin( v ) * r;
				z = Math.cos( v ) * r;

				vertices.push( x, 0, z );
				colors.push( color.r, color.g, color.b );

			}

		}

		const geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		geometry.setAttribute( 'color', new Float32BufferAttribute( colors, 3 ) );

		const material = new LineBasicMaterial( { vertexColors: true, toneMapped: false } );

		super( geometry, material );

		this.type = 'PolarGridHelper';

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}

const _v1 = /*@__PURE__*/ new Vector3();
const _v2 = /*@__PURE__*/ new Vector3();
const _v3 = /*@__PURE__*/ new Vector3();

class DirectionalLightHelper extends Object3D {

	constructor( light, size, color ) {

		super();

		this.light = light;

		this.matrix = light.matrixWorld;
		this.matrixAutoUpdate = false;

		this.color = color;

		this.type = 'DirectionalLightHelper';

		if ( size === undefined ) size = 1;

		let geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( [
			- size, size, 0,
			size, size, 0,
			size, - size, 0,
			- size, - size, 0,
			- size, size, 0
		], 3 ) );

		const material = new LineBasicMaterial( { fog: false, toneMapped: false } );

		this.lightPlane = new Line( geometry, material );
		this.add( this.lightPlane );

		geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( [ 0, 0, 0, 0, 0, 1 ], 3 ) );

		this.targetLine = new Line( geometry, material );
		this.add( this.targetLine );

		this.update();

	}

	dispose() {

		this.lightPlane.geometry.dispose();
		this.lightPlane.material.dispose();
		this.targetLine.geometry.dispose();
		this.targetLine.material.dispose();

	}

	update() {

		this.light.updateWorldMatrix( true, false );
		this.light.target.updateWorldMatrix( true, false );

		_v1.setFromMatrixPosition( this.light.matrixWorld );
		_v2.setFromMatrixPosition( this.light.target.matrixWorld );
		_v3.subVectors( _v2, _v1 );

		this.lightPlane.lookAt( _v2 );

		if ( this.color !== undefined ) {

			this.lightPlane.material.color.set( this.color );
			this.targetLine.material.color.set( this.color );

		} else {

			this.lightPlane.material.color.copy( this.light.color );
			this.targetLine.material.color.copy( this.light.color );

		}

		this.targetLine.lookAt( _v2 );
		this.targetLine.scale.z = _v3.length();

	}

}

const _vector = /*@__PURE__*/ new Vector3();
const _camera = /*@__PURE__*/ new Camera();

/**
 *	- shows frustum, line of sight and up of the camera
 *	- suitable for fast updates
 * 	- based on frustum visualization in lightgl.js shadowmap example
 *		https://github.com/evanw/lightgl.js/blob/master/tests/shadowmap.html
 */

class CameraHelper extends LineSegments {

	constructor( camera ) {

		const geometry = new BufferGeometry();
		const material = new LineBasicMaterial( { color: 0xffffff, vertexColors: true, toneMapped: false } );

		const vertices = [];
		const colors = [];

		const pointMap = {};

		// near

		addLine( 'n1', 'n2' );
		addLine( 'n2', 'n4' );
		addLine( 'n4', 'n3' );
		addLine( 'n3', 'n1' );

		// far

		addLine( 'f1', 'f2' );
		addLine( 'f2', 'f4' );
		addLine( 'f4', 'f3' );
		addLine( 'f3', 'f1' );

		// sides

		addLine( 'n1', 'f1' );
		addLine( 'n2', 'f2' );
		addLine( 'n3', 'f3' );
		addLine( 'n4', 'f4' );

		// cone

		addLine( 'p', 'n1' );
		addLine( 'p', 'n2' );
		addLine( 'p', 'n3' );
		addLine( 'p', 'n4' );

		// up

		addLine( 'u1', 'u2' );
		addLine( 'u2', 'u3' );
		addLine( 'u3', 'u1' );

		// target

		addLine( 'c', 't' );
		addLine( 'p', 'c' );

		// cross

		addLine( 'cn1', 'cn2' );
		addLine( 'cn3', 'cn4' );

		addLine( 'cf1', 'cf2' );
		addLine( 'cf3', 'cf4' );

		function addLine( a, b ) {

			addPoint( a );
			addPoint( b );

		}

		function addPoint( id ) {

			vertices.push( 0, 0, 0 );
			colors.push( 0, 0, 0 );

			if ( pointMap[ id ] === undefined ) {

				pointMap[ id ] = [];

			}

			pointMap[ id ].push( ( vertices.length / 3 ) - 1 );

		}

		geometry.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		geometry.setAttribute( 'color', new Float32BufferAttribute( colors, 3 ) );

		super( geometry, material );

		this.type = 'CameraHelper';

		this.camera = camera;
		if ( this.camera.updateProjectionMatrix ) this.camera.updateProjectionMatrix();

		this.matrix = camera.matrixWorld;
		this.matrixAutoUpdate = false;

		this.pointMap = pointMap;

		this.update();

		// colors

		const colorFrustum = new Color( 0xffaa00 );
		const colorCone = new Color( 0xff0000 );
		const colorUp = new Color( 0x00aaff );
		const colorTarget = new Color( 0xffffff );
		const colorCross = new Color( 0x333333 );

		this.setColors( colorFrustum, colorCone, colorUp, colorTarget, colorCross );

	}

	setColors( frustum, cone, up, target, cross ) {

		const geometry = this.geometry;

		const colorAttribute = geometry.getAttribute( 'color' );

		// near

		colorAttribute.setXYZ( 0, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 1, frustum.r, frustum.g, frustum.b ); // n1, n2
		colorAttribute.setXYZ( 2, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 3, frustum.r, frustum.g, frustum.b ); // n2, n4
		colorAttribute.setXYZ( 4, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 5, frustum.r, frustum.g, frustum.b ); // n4, n3
		colorAttribute.setXYZ( 6, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 7, frustum.r, frustum.g, frustum.b ); // n3, n1

		// far

		colorAttribute.setXYZ( 8, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 9, frustum.r, frustum.g, frustum.b ); // f1, f2
		colorAttribute.setXYZ( 10, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 11, frustum.r, frustum.g, frustum.b ); // f2, f4
		colorAttribute.setXYZ( 12, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 13, frustum.r, frustum.g, frustum.b ); // f4, f3
		colorAttribute.setXYZ( 14, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 15, frustum.r, frustum.g, frustum.b ); // f3, f1

		// sides

		colorAttribute.setXYZ( 16, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 17, frustum.r, frustum.g, frustum.b ); // n1, f1
		colorAttribute.setXYZ( 18, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 19, frustum.r, frustum.g, frustum.b ); // n2, f2
		colorAttribute.setXYZ( 20, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 21, frustum.r, frustum.g, frustum.b ); // n3, f3
		colorAttribute.setXYZ( 22, frustum.r, frustum.g, frustum.b ); colorAttribute.setXYZ( 23, frustum.r, frustum.g, frustum.b ); // n4, f4

		// cone

		colorAttribute.setXYZ( 24, cone.r, cone.g, cone.b ); colorAttribute.setXYZ( 25, cone.r, cone.g, cone.b ); // p, n1
		colorAttribute.setXYZ( 26, cone.r, cone.g, cone.b ); colorAttribute.setXYZ( 27, cone.r, cone.g, cone.b ); // p, n2
		colorAttribute.setXYZ( 28, cone.r, cone.g, cone.b ); colorAttribute.setXYZ( 29, cone.r, cone.g, cone.b ); // p, n3
		colorAttribute.setXYZ( 30, cone.r, cone.g, cone.b ); colorAttribute.setXYZ( 31, cone.r, cone.g, cone.b ); // p, n4

		// up

		colorAttribute.setXYZ( 32, up.r, up.g, up.b ); colorAttribute.setXYZ( 33, up.r, up.g, up.b ); // u1, u2
		colorAttribute.setXYZ( 34, up.r, up.g, up.b ); colorAttribute.setXYZ( 35, up.r, up.g, up.b ); // u2, u3
		colorAttribute.setXYZ( 36, up.r, up.g, up.b ); colorAttribute.setXYZ( 37, up.r, up.g, up.b ); // u3, u1

		// target

		colorAttribute.setXYZ( 38, target.r, target.g, target.b ); colorAttribute.setXYZ( 39, target.r, target.g, target.b ); // c, t
		colorAttribute.setXYZ( 40, cross.r, cross.g, cross.b ); colorAttribute.setXYZ( 41, cross.r, cross.g, cross.b ); // p, c

		// cross

		colorAttribute.setXYZ( 42, cross.r, cross.g, cross.b ); colorAttribute.setXYZ( 43, cross.r, cross.g, cross.b ); // cn1, cn2
		colorAttribute.setXYZ( 44, cross.r, cross.g, cross.b ); colorAttribute.setXYZ( 45, cross.r, cross.g, cross.b ); // cn3, cn4

		colorAttribute.setXYZ( 46, cross.r, cross.g, cross.b ); colorAttribute.setXYZ( 47, cross.r, cross.g, cross.b ); // cf1, cf2
		colorAttribute.setXYZ( 48, cross.r, cross.g, cross.b ); colorAttribute.setXYZ( 49, cross.r, cross.g, cross.b ); // cf3, cf4

		colorAttribute.needsUpdate = true;

	}

	update() {

		const geometry = this.geometry;
		const pointMap = this.pointMap;

		const w = 1, h = 1;

		// we need just camera projection matrix inverse
		// world matrix must be identity

		_camera.projectionMatrixInverse.copy( this.camera.projectionMatrixInverse );

		// center / target

		setPoint( 'c', pointMap, geometry, _camera, 0, 0, - 1 );
		setPoint( 't', pointMap, geometry, _camera, 0, 0, 1 );

		// near

		setPoint( 'n1', pointMap, geometry, _camera, - w, - h, - 1 );
		setPoint( 'n2', pointMap, geometry, _camera, w, - h, - 1 );
		setPoint( 'n3', pointMap, geometry, _camera, - w, h, - 1 );
		setPoint( 'n4', pointMap, geometry, _camera, w, h, - 1 );

		// far

		setPoint( 'f1', pointMap, geometry, _camera, - w, - h, 1 );
		setPoint( 'f2', pointMap, geometry, _camera, w, - h, 1 );
		setPoint( 'f3', pointMap, geometry, _camera, - w, h, 1 );
		setPoint( 'f4', pointMap, geometry, _camera, w, h, 1 );

		// up

		setPoint( 'u1', pointMap, geometry, _camera, w * 0.7, h * 1.1, - 1 );
		setPoint( 'u2', pointMap, geometry, _camera, - w * 0.7, h * 1.1, - 1 );
		setPoint( 'u3', pointMap, geometry, _camera, 0, h * 2, - 1 );

		// cross

		setPoint( 'cf1', pointMap, geometry, _camera, - w, 0, 1 );
		setPoint( 'cf2', pointMap, geometry, _camera, w, 0, 1 );
		setPoint( 'cf3', pointMap, geometry, _camera, 0, - h, 1 );
		setPoint( 'cf4', pointMap, geometry, _camera, 0, h, 1 );

		setPoint( 'cn1', pointMap, geometry, _camera, - w, 0, - 1 );
		setPoint( 'cn2', pointMap, geometry, _camera, w, 0, - 1 );
		setPoint( 'cn3', pointMap, geometry, _camera, 0, - h, - 1 );
		setPoint( 'cn4', pointMap, geometry, _camera, 0, h, - 1 );

		geometry.getAttribute( 'position' ).needsUpdate = true;

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}


function setPoint( point, pointMap, geometry, camera, x, y, z ) {

	_vector.set( x, y, z ).unproject( camera );

	const points = pointMap[ point ];

	if ( points !== undefined ) {

		const position = geometry.getAttribute( 'position' );

		for ( let i = 0, l = points.length; i < l; i ++ ) {

			position.setXYZ( points[ i ], _vector.x, _vector.y, _vector.z );

		}

	}

}

const _box = /*@__PURE__*/ new Box3();

class BoxHelper extends LineSegments {

	constructor( object, color = 0xffff00 ) {

		const indices = new Uint16Array( [ 0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7 ] );
		const positions = new Float32Array( 8 * 3 );

		const geometry = new BufferGeometry();
		geometry.setIndex( new BufferAttribute( indices, 1 ) );
		geometry.setAttribute( 'position', new BufferAttribute( positions, 3 ) );

		super( geometry, new LineBasicMaterial( { color: color, toneMapped: false } ) );

		this.object = object;
		this.type = 'BoxHelper';

		this.matrixAutoUpdate = false;

		this.update();

	}

	update( object ) {

		if ( object !== undefined ) {

			console.warn( 'THREE.BoxHelper: .update() has no longer arguments.' );

		}

		if ( this.object !== undefined ) {

			_box.setFromObject( this.object );

		}

		if ( _box.isEmpty() ) return;

		const min = _box.min;
		const max = _box.max;

		/*
			5____4
		1/___0/|
		| 6__|_7
		2/___3/

		0: max.x, max.y, max.z
		1: min.x, max.y, max.z
		2: min.x, min.y, max.z
		3: max.x, min.y, max.z
		4: max.x, max.y, min.z
		5: min.x, max.y, min.z
		6: min.x, min.y, min.z
		7: max.x, min.y, min.z
		*/

		const position = this.geometry.attributes.position;
		const array = position.array;

		array[ 0 ] = max.x; array[ 1 ] = max.y; array[ 2 ] = max.z;
		array[ 3 ] = min.x; array[ 4 ] = max.y; array[ 5 ] = max.z;
		array[ 6 ] = min.x; array[ 7 ] = min.y; array[ 8 ] = max.z;
		array[ 9 ] = max.x; array[ 10 ] = min.y; array[ 11 ] = max.z;
		array[ 12 ] = max.x; array[ 13 ] = max.y; array[ 14 ] = min.z;
		array[ 15 ] = min.x; array[ 16 ] = max.y; array[ 17 ] = min.z;
		array[ 18 ] = min.x; array[ 19 ] = min.y; array[ 20 ] = min.z;
		array[ 21 ] = max.x; array[ 22 ] = min.y; array[ 23 ] = min.z;

		position.needsUpdate = true;

		this.geometry.computeBoundingSphere();

	}

	setFromObject( object ) {

		this.object = object;
		this.update();

		return this;

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.object = source.object;

		return this;

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}

class Box3Helper extends LineSegments {

	constructor( box, color = 0xffff00 ) {

		const indices = new Uint16Array( [ 0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7 ] );

		const positions = [ 1, 1, 1, - 1, 1, 1, - 1, - 1, 1, 1, - 1, 1, 1, 1, - 1, - 1, 1, - 1, - 1, - 1, - 1, 1, - 1, - 1 ];

		const geometry = new BufferGeometry();

		geometry.setIndex( new BufferAttribute( indices, 1 ) );

		geometry.setAttribute( 'position', new Float32BufferAttribute( positions, 3 ) );

		super( geometry, new LineBasicMaterial( { color: color, toneMapped: false } ) );

		this.box = box;

		this.type = 'Box3Helper';

		this.geometry.computeBoundingSphere();

	}

	updateMatrixWorld( force ) {

		const box = this.box;

		if ( box.isEmpty() ) return;

		box.getCenter( this.position );

		box.getSize( this.scale );

		this.scale.multiplyScalar( 0.5 );

		super.updateMatrixWorld( force );

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}

class PlaneHelper extends Line {

	constructor( plane, size = 1, hex = 0xffff00 ) {

		const color = hex;

		const positions = [ 1, - 1, 0, - 1, 1, 0, - 1, - 1, 0, 1, 1, 0, - 1, 1, 0, - 1, - 1, 0, 1, - 1, 0, 1, 1, 0 ];

		const geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( positions, 3 ) );
		geometry.computeBoundingSphere();

		super( geometry, new LineBasicMaterial( { color: color, toneMapped: false } ) );

		this.type = 'PlaneHelper';

		this.plane = plane;

		this.size = size;

		const positions2 = [ 1, 1, 0, - 1, 1, 0, - 1, - 1, 0, 1, 1, 0, - 1, - 1, 0, 1, - 1, 0 ];

		const geometry2 = new BufferGeometry();
		geometry2.setAttribute( 'position', new Float32BufferAttribute( positions2, 3 ) );
		geometry2.computeBoundingSphere();

		this.add( new Mesh( geometry2, new MeshBasicMaterial( { color: color, opacity: 0.2, transparent: true, depthWrite: false, toneMapped: false } ) ) );

	}

	updateMatrixWorld( force ) {

		this.position.set( 0, 0, 0 );

		this.scale.set( 0.5 * this.size, 0.5 * this.size, 1 );

		this.lookAt( this.plane.normal );

		this.translateZ( - this.plane.constant );

		super.updateMatrixWorld( force );

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();
		this.children[ 0 ].geometry.dispose();
		this.children[ 0 ].material.dispose();

	}

}

const _axis = /*@__PURE__*/ new Vector3();
let _lineGeometry, _coneGeometry;

class ArrowHelper extends Object3D {

	// dir is assumed to be normalized

	constructor( dir = new Vector3( 0, 0, 1 ), origin = new Vector3( 0, 0, 0 ), length = 1, color = 0xffff00, headLength = length * 0.2, headWidth = headLength * 0.2 ) {

		super();

		this.type = 'ArrowHelper';

		if ( _lineGeometry === undefined ) {

			_lineGeometry = new BufferGeometry();
			_lineGeometry.setAttribute( 'position', new Float32BufferAttribute( [ 0, 0, 0, 0, 1, 0 ], 3 ) );

			_coneGeometry = new CylinderGeometry( 0, 0.5, 1, 5, 1 );
			_coneGeometry.translate( 0, - 0.5, 0 );

		}

		this.position.copy( origin );

		this.line = new Line( _lineGeometry, new LineBasicMaterial( { color: color, toneMapped: false } ) );
		this.line.matrixAutoUpdate = false;
		this.add( this.line );

		this.cone = new Mesh( _coneGeometry, new MeshBasicMaterial( { color: color, toneMapped: false } ) );
		this.cone.matrixAutoUpdate = false;
		this.add( this.cone );

		this.setDirection( dir );
		this.setLength( length, headLength, headWidth );

	}

	setDirection( dir ) {

		// dir is assumed to be normalized

		if ( dir.y > 0.99999 ) {

			this.quaternion.set( 0, 0, 0, 1 );

		} else if ( dir.y < - 0.99999 ) {

			this.quaternion.set( 1, 0, 0, 0 );

		} else {

			_axis.set( dir.z, 0, - dir.x ).normalize();

			const radians = Math.acos( dir.y );

			this.quaternion.setFromAxisAngle( _axis, radians );

		}

	}

	setLength( length, headLength = length * 0.2, headWidth = headLength * 0.2 ) {

		this.line.scale.set( 1, Math.max( 0.0001, length - headLength ), 1 ); // see #17458
		this.line.updateMatrix();

		this.cone.scale.set( headWidth, headLength, headWidth );
		this.cone.position.y = length;
		this.cone.updateMatrix();

	}

	setColor( color ) {

		this.line.material.color.set( color );
		this.cone.material.color.set( color );

	}

	copy( source ) {

		super.copy( source, false );

		this.line.copy( source.line );
		this.cone.copy( source.cone );

		return this;

	}

	dispose() {

		this.line.geometry.dispose();
		this.line.material.dispose();
		this.cone.geometry.dispose();
		this.cone.material.dispose();

	}

}

class AxesHelper extends LineSegments {

	constructor( size = 1 ) {

		const vertices = [
			0, 0, 0,	size, 0, 0,
			0, 0, 0,	0, size, 0,
			0, 0, 0,	0, 0, size
		];

		const colors = [
			1, 0, 0,	1, 0.6, 0,
			0, 1, 0,	0.6, 1, 0,
			0, 0, 1,	0, 0.6, 1
		];

		const geometry = new BufferGeometry();
		geometry.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
		geometry.setAttribute( 'color', new Float32BufferAttribute( colors, 3 ) );

		const material = new LineBasicMaterial( { vertexColors: true, toneMapped: false } );

		super( geometry, material );

		this.type = 'AxesHelper';

	}

	setColors( xAxisColor, yAxisColor, zAxisColor ) {

		const color = new Color();
		const array = this.geometry.attributes.color.array;

		color.set( xAxisColor );
		color.toArray( array, 0 );
		color.toArray( array, 3 );

		color.set( yAxisColor );
		color.toArray( array, 6 );
		color.toArray( array, 9 );

		color.set( zAxisColor );
		color.toArray( array, 12 );
		color.toArray( array, 15 );

		this.geometry.attributes.color.needsUpdate = true;

		return this;

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();

	}

}

class ShapePath {

	constructor() {

		this.type = 'ShapePath';

		this.color = new Color();

		this.subPaths = [];
		this.currentPath = null;

	}

	moveTo( x, y ) {

		this.currentPath = new Path();
		this.subPaths.push( this.currentPath );
		this.currentPath.moveTo( x, y );

		return this;

	}

	lineTo( x, y ) {

		this.currentPath.lineTo( x, y );

		return this;

	}

	quadraticCurveTo( aCPx, aCPy, aX, aY ) {

		this.currentPath.quadraticCurveTo( aCPx, aCPy, aX, aY );

		return this;

	}

	bezierCurveTo( aCP1x, aCP1y, aCP2x, aCP2y, aX, aY ) {

		this.currentPath.bezierCurveTo( aCP1x, aCP1y, aCP2x, aCP2y, aX, aY );

		return this;

	}

	splineThru( pts ) {

		this.currentPath.splineThru( pts );

		return this;

	}

	toShapes( isCCW ) {

		function toShapesNoHoles( inSubpaths ) {

			const shapes = [];

			for ( let i = 0, l = inSubpaths.length; i < l; i ++ ) {

				const tmpPath = inSubpaths[ i ];

				const tmpShape = new Shape();
				tmpShape.curves = tmpPath.curves;

				shapes.push( tmpShape );

			}

			return shapes;

		}

		function isPointInsidePolygon( inPt, inPolygon ) {

			const polyLen = inPolygon.length;

			// inPt on polygon contour => immediate success    or
			// toggling of inside/outside at every single! intersection point of an edge
			//  with the horizontal line through inPt, left of inPt
			//  not counting lowerY endpoints of edges and whole edges on that line
			let inside = false;
			for ( let p = polyLen - 1, q = 0; q < polyLen; p = q ++ ) {

				let edgeLowPt = inPolygon[ p ];
				let edgeHighPt = inPolygon[ q ];

				let edgeDx = edgeHighPt.x - edgeLowPt.x;
				let edgeDy = edgeHighPt.y - edgeLowPt.y;

				if ( Math.abs( edgeDy ) > Number.EPSILON ) {

					// not parallel
					if ( edgeDy < 0 ) {

						edgeLowPt = inPolygon[ q ]; edgeDx = - edgeDx;
						edgeHighPt = inPolygon[ p ]; edgeDy = - edgeDy;

					}

					if ( ( inPt.y < edgeLowPt.y ) || ( inPt.y > edgeHighPt.y ) ) 		continue;

					if ( inPt.y === edgeLowPt.y ) {

						if ( inPt.x === edgeLowPt.x )		return	true;		// inPt is on contour ?
						// continue;				// no intersection or edgeLowPt => doesn't count !!!

					} else {

						const perpEdge = edgeDy * ( inPt.x - edgeLowPt.x ) - edgeDx * ( inPt.y - edgeLowPt.y );
						if ( perpEdge === 0 )				return	true;		// inPt is on contour ?
						if ( perpEdge < 0 ) 				continue;
						inside = ! inside;		// true intersection left of inPt

					}

				} else {

					// parallel or collinear
					if ( inPt.y !== edgeLowPt.y ) 		continue;			// parallel
					// edge lies on the same horizontal line as inPt
					if ( ( ( edgeHighPt.x <= inPt.x ) && ( inPt.x <= edgeLowPt.x ) ) ||
						 ( ( edgeLowPt.x <= inPt.x ) && ( inPt.x <= edgeHighPt.x ) ) )		return	true;	// inPt: Point on contour !
					// continue;

				}

			}

			return	inside;

		}

		const isClockWise = ShapeUtils.isClockWise;

		const subPaths = this.subPaths;
		if ( subPaths.length === 0 ) return [];

		let solid, tmpPath, tmpShape;
		const shapes = [];

		if ( subPaths.length === 1 ) {

			tmpPath = subPaths[ 0 ];
			tmpShape = new Shape();
			tmpShape.curves = tmpPath.curves;
			shapes.push( tmpShape );
			return shapes;

		}

		let holesFirst = ! isClockWise( subPaths[ 0 ].getPoints() );
		holesFirst = isCCW ? ! holesFirst : holesFirst;

		// console.log("Holes first", holesFirst);

		const betterShapeHoles = [];
		const newShapes = [];
		let newShapeHoles = [];
		let mainIdx = 0;
		let tmpPoints;

		newShapes[ mainIdx ] = undefined;
		newShapeHoles[ mainIdx ] = [];

		for ( let i = 0, l = subPaths.length; i < l; i ++ ) {

			tmpPath = subPaths[ i ];
			tmpPoints = tmpPath.getPoints();
			solid = isClockWise( tmpPoints );
			solid = isCCW ? ! solid : solid;

			if ( solid ) {

				if ( ( ! holesFirst ) && ( newShapes[ mainIdx ] ) )	mainIdx ++;

				newShapes[ mainIdx ] = { s: new Shape(), p: tmpPoints };
				newShapes[ mainIdx ].s.curves = tmpPath.curves;

				if ( holesFirst )	mainIdx ++;
				newShapeHoles[ mainIdx ] = [];

				//console.log('cw', i);

			} else {

				newShapeHoles[ mainIdx ].push( { h: tmpPath, p: tmpPoints[ 0 ] } );

				//console.log('ccw', i);

			}

		}

		// only Holes? -> probably all Shapes with wrong orientation
		if ( ! newShapes[ 0 ] )	return	toShapesNoHoles( subPaths );


		if ( newShapes.length > 1 ) {

			let ambiguous = false;
			let toChange = 0;

			for ( let sIdx = 0, sLen = newShapes.length; sIdx < sLen; sIdx ++ ) {

				betterShapeHoles[ sIdx ] = [];

			}

			for ( let sIdx = 0, sLen = newShapes.length; sIdx < sLen; sIdx ++ ) {

				const sho = newShapeHoles[ sIdx ];

				for ( let hIdx = 0; hIdx < sho.length; hIdx ++ ) {

					const ho = sho[ hIdx ];
					let hole_unassigned = true;

					for ( let s2Idx = 0; s2Idx < newShapes.length; s2Idx ++ ) {

						if ( isPointInsidePolygon( ho.p, newShapes[ s2Idx ].p ) ) {

							if ( sIdx !== s2Idx )	toChange ++;

							if ( hole_unassigned ) {

								hole_unassigned = false;
								betterShapeHoles[ s2Idx ].push( ho );

							} else {

								ambiguous = true;

							}

						}

					}

					if ( hole_unassigned ) {

						betterShapeHoles[ sIdx ].push( ho );

					}

				}

			}

			if ( toChange > 0 && ambiguous === false ) {

				newShapeHoles = betterShapeHoles;

			}

		}

		let tmpHoles;

		for ( let i = 0, il = newShapes.length; i < il; i ++ ) {

			tmpShape = newShapes[ i ].s;
			shapes.push( tmpShape );
			tmpHoles = newShapeHoles[ i ];

			for ( let j = 0, jl = tmpHoles.length; j < jl; j ++ ) {

				tmpShape.holes.push( tmpHoles[ j ].h );

			}

		}

		//console.log("shape", shapes);

		return shapes;

	}

}

class Controls extends EventDispatcher {

	constructor( object, domElement = null ) {

		super();

		this.object = object;
		this.domElement = domElement;

		this.enabled = true;

		this.state = - 1;

		this.keys = {};
		this.mouseButtons = { LEFT: null, MIDDLE: null, RIGHT: null };
		this.touches = { ONE: null, TWO: null };

	}

	connect() {}

	disconnect() {}

	dispose() {}

	update( /* delta */ ) {}

}

class WebGLMultipleRenderTargets extends WebGLRenderTarget { // @deprecated, r162

	constructor( width = 1, height = 1, count = 1, options = {} ) {

		console.warn( 'THREE.WebGLMultipleRenderTargets has been deprecated and will be removed in r172. Use THREE.WebGLRenderTarget and set the "count" parameter to enable MRT.' );

		super( width, height, { ...options, count } );

		this.isWebGLMultipleRenderTargets = true;

	}

	get texture() {

		return this.textures;

	}

}

if ( typeof __THREE_DEVTOOLS__ !== 'undefined' ) {

	__THREE_DEVTOOLS__.dispatchEvent( new CustomEvent( 'register', { detail: {
		revision: REVISION,
	} } ) );

}

if ( typeof window !== 'undefined' ) {

	if ( window.__THREE__ ) {

		console.warn( 'WARNING: Multiple instances of Three.js being imported.' );

	} else {

		window.__THREE__ = REVISION;

	}

}

return {ACESFilmicToneMapping,AddEquation,AddOperation,AdditiveAnimationBlendMode,AdditiveBlending,AgXToneMapping,AlphaFormat,AlwaysCompare,AlwaysDepth,AlwaysStencilFunc,AmbientLight,AnimationAction,AnimationClip,AnimationLoader,AnimationMixer,AnimationObjectGroup,AnimationUtils,ArcCurve,ArrayCamera,ArrowHelper,AttachedBindMode,Audio,AudioAnalyser,AudioContext,AudioListener,AudioLoader,AxesHelper,BackSide,BasicDepthPacking,BasicShadowMap,BatchedMesh,Bone,BooleanKeyframeTrack,Box2,Box3,Box3Helper,BoxGeometry,BoxHelper,BufferAttribute,BufferGeometry,BufferGeometryLoader,ByteType,Cache,Camera,CameraHelper,CanvasTexture,CapsuleGeometry,CatmullRomCurve3,CineonToneMapping,CircleGeometry,ClampToEdgeWrapping,Clock,Color,ColorKeyframeTrack,ColorManagement,CompressedArrayTexture,CompressedCubeTexture,CompressedTexture,CompressedTextureLoader,ConeGeometry,ConstantAlphaFactor,ConstantColorFactor,Controls,CubeCamera,CubeReflectionMapping,CubeRefractionMapping,CubeTexture,CubeTextureLoader,CubeUVReflectionMapping,CubicBezierCurve,CubicBezierCurve3,CubicInterpolant,CullFaceBack,CullFaceFront,CullFaceFrontBack,CullFaceNone,Curve,CurvePath,CustomBlending,CustomToneMapping,CylinderGeometry,Cylindrical,Data3DTexture,DataArrayTexture,DataTexture,DataTextureLoader,DataUtils,DecrementStencilOp,DecrementWrapStencilOp,DefaultLoadingManager,DepthFormat,DepthStencilFormat,DepthTexture,DetachedBindMode,DirectionalLight,DirectionalLightHelper,DiscreteInterpolant,DisplayP3ColorSpace,DodecahedronGeometry,DoubleSide,DstAlphaFactor,DstColorFactor,DynamicCopyUsage,DynamicDrawUsage,DynamicReadUsage,EdgesGeometry,EllipseCurve,EqualCompare,EqualDepth,EqualStencilFunc,EquirectangularReflectionMapping,EquirectangularRefractionMapping,Euler,EventDispatcher,ExtrudeGeometry,FileLoader,Float16BufferAttribute,Float32BufferAttribute,FloatType,Fog,FogExp2,FramebufferTexture,FrontSide,Frustum,GLBufferAttribute,GLSL1,GLSL3,GreaterCompare,GreaterDepth,GreaterEqualCompare,GreaterEqualDepth,GreaterEqualStencilFunc,GreaterStencilFunc,GridHelper,Group,HalfFloatType,HemisphereLight,HemisphereLightHelper,IcosahedronGeometry,ImageBitmapLoader,ImageLoader,ImageUtils,IncrementStencilOp,IncrementWrapStencilOp,InstancedBufferAttribute,InstancedBufferGeometry,InstancedInterleavedBuffer,InstancedMesh,Int16BufferAttribute,Int32BufferAttribute,Int8BufferAttribute,IntType,InterleavedBuffer,InterleavedBufferAttribute,Interpolant,InterpolateDiscrete,InterpolateLinear,InterpolateSmooth,InvertStencilOp,KeepStencilOp,KeyframeTrack,LOD,LatheGeometry,Layers,LessCompare,LessDepth,LessEqualCompare,LessEqualDepth,LessEqualStencilFunc,LessStencilFunc,Light,LightProbe,Line,Line3,LineBasicMaterial,LineCurve,LineCurve3,LineDashedMaterial,LineLoop,LineSegments,LinearDisplayP3ColorSpace,LinearFilter,LinearInterpolant,LinearMipMapLinearFilter,LinearMipMapNearestFilter,LinearMipmapLinearFilter,LinearMipmapNearestFilter,LinearSRGBColorSpace,LinearToneMapping,LinearTransfer,Loader,LoaderUtils,LoadingManager,LoopOnce,LoopPingPong,LoopRepeat,LuminanceAlphaFormat,LuminanceFormat,MOUSE,Material,MaterialLoader,MathUtils,Matrix2,Matrix3,Matrix4,MaxEquation,Mesh,MeshBasicMaterial,MeshDepthMaterial,MeshDistanceMaterial,MeshLambertMaterial,MeshMatcapMaterial,MeshNormalMaterial,MeshPhongMaterial,MeshPhysicalMaterial,MeshStandardMaterial,MeshToonMaterial,MinEquation,MirroredRepeatWrapping,MixOperation,MultiplyBlending,MultiplyOperation,NearestFilter,NearestMipMapLinearFilter,NearestMipMapNearestFilter,NearestMipmapLinearFilter,NearestMipmapNearestFilter,NeutralToneMapping,NeverCompare,NeverDepth,NeverStencilFunc,NoBlending,NoColorSpace,NoToneMapping,NormalAnimationBlendMode,NormalBlending,NotEqualCompare,NotEqualDepth,NotEqualStencilFunc,NumberKeyframeTrack,Object3D,ObjectLoader,ObjectSpaceNormalMap,OctahedronGeometry,OneFactor,OneMinusConstantAlphaFactor,OneMinusConstantColorFactor,OneMinusDstAlphaFactor,OneMinusDstColorFactor,OneMinusSrcAlphaFactor,OneMinusSrcColorFactor,OrthographicCamera,P3Primaries,PCFShadowMap,PCFSoftShadowMap,PMREMGenerator,Path,PerspectiveCamera,Plane,PlaneGeometry,PlaneHelper,PointLight,PointLightHelper,Points,PointsMaterial,PolarGridHelper,PolyhedronGeometry,PositionalAudio,PropertyBinding,PropertyMixer,QuadraticBezierCurve,QuadraticBezierCurve3,Quaternion,QuaternionKeyframeTrack,QuaternionLinearInterpolant,RED_GREEN_RGTC2_Format,RED_RGTC1_Format,REVISION,RGBADepthPacking,RGBAFormat,RGBAIntegerFormat,RGBA_ASTC_10x10_Format,RGBA_ASTC_10x5_Format,RGBA_ASTC_10x6_Format,RGBA_ASTC_10x8_Format,RGBA_ASTC_12x10_Format,RGBA_ASTC_12x12_Format,RGBA_ASTC_4x4_Format,RGBA_ASTC_5x4_Format,RGBA_ASTC_5x5_Format,RGBA_ASTC_6x5_Format,RGBA_ASTC_6x6_Format,RGBA_ASTC_8x5_Format,RGBA_ASTC_8x6_Format,RGBA_ASTC_8x8_Format,RGBA_BPTC_Format,RGBA_ETC2_EAC_Format,RGBA_PVRTC_2BPPV1_Format,RGBA_PVRTC_4BPPV1_Format,RGBA_S3TC_DXT1_Format,RGBA_S3TC_DXT3_Format,RGBA_S3TC_DXT5_Format,RGBDepthPacking,RGBFormat,RGBIntegerFormat,RGB_BPTC_SIGNED_Format,RGB_BPTC_UNSIGNED_Format,RGB_ETC1_Format,RGB_ETC2_Format,RGB_PVRTC_2BPPV1_Format,RGB_PVRTC_4BPPV1_Format,RGB_S3TC_DXT1_Format,RGDepthPacking,RGFormat,RGIntegerFormat,RawShaderMaterial,Ray,Raycaster,Rec709Primaries,RectAreaLight,RedFormat,RedIntegerFormat,ReinhardToneMapping,RenderTarget,RepeatWrapping,ReplaceStencilOp,ReverseSubtractEquation,RingGeometry,SIGNED_RED_GREEN_RGTC2_Format,SIGNED_RED_RGTC1_Format,SRGBColorSpace,SRGBTransfer,Scene,ShaderChunk,ShaderLib,ShaderMaterial,ShadowMaterial,Shape,ShapeGeometry,ShapePath,ShapeUtils,ShortType,Skeleton,SkeletonHelper,SkinnedMesh,Source,Sphere,SphereGeometry,Spherical,SphericalHarmonics3,SplineCurve,SpotLight,SpotLightHelper,Sprite,SpriteMaterial,SrcAlphaFactor,SrcAlphaSaturateFactor,SrcColorFactor,StaticCopyUsage,StaticDrawUsage,StaticReadUsage,StereoCamera,StreamCopyUsage,StreamDrawUsage,StreamReadUsage,StringKeyframeTrack,SubtractEquation,SubtractiveBlending,TOUCH,TangentSpaceNormalMap,TetrahedronGeometry,Texture,TextureLoader,TextureUtils,TorusGeometry,TorusKnotGeometry,Triangle,TriangleFanDrawMode,TriangleStripDrawMode,TrianglesDrawMode,TubeGeometry,UVMapping,Uint16BufferAttribute,Uint32BufferAttribute,Uint8BufferAttribute,Uint8ClampedBufferAttribute,Uniform,UniformsGroup,UniformsLib,UniformsUtils,UnsignedByteType,UnsignedInt248Type,UnsignedInt5999Type,UnsignedIntType,UnsignedShort4444Type,UnsignedShort5551Type,UnsignedShortType,VSMShadowMap,Vector2,Vector3,Vector4,VectorKeyframeTrack,VideoTexture,WebGL3DRenderTarget,WebGLArrayRenderTarget,WebGLCoordinateSystem,WebGLCubeRenderTarget,WebGLMultipleRenderTargets,WebGLRenderTarget,WebGLRenderer,WebGLUtils,WebGPUCoordinateSystem,WireframeGeometry,WrapAroundEnding,ZeroCurvatureEnding,ZeroFactor,ZeroSlopeEnding,ZeroStencilOp,createCanvasElement};})();const OBJLoader=(()=>{const {
	BufferGeometry,
	FileLoader,
	Float32BufferAttribute,
	Group,
	LineBasicMaterial,
	LineSegments,
	Loader,
	Material,
	Mesh,
	MeshPhongMaterial,
	Points,
	PointsMaterial,
	Vector3,
	Color,
	SRGBColorSpace
}=THREE;

// o object_name | g group_name
const _object_pattern = /^[og]\s*(.+)?/;
// mtllib file_reference
const _material_library_pattern = /^mtllib /;
// usemtl material_name
const _material_use_pattern = /^usemtl /;
// usemap map_name
const _map_use_pattern = /^usemap /;
const _face_vertex_data_separator_pattern = /\s+/;

const _vA = new Vector3();
const _vB = new Vector3();
const _vC = new Vector3();

const _ab = new Vector3();
const _cb = new Vector3();

const _color = new Color();

function ParserState() {

	const state = {
		objects: [],
		object: {},

		vertices: [],
		normals: [],
		colors: [],
		uvs: [],

		materials: {},
		materialLibraries: [],

		startObject: function ( name, fromDeclaration ) {

			// If the current object (initial from reset) is not from a g/o declaration in the parsed
			// file. We need to use it for the first parsed g/o to keep things in sync.
			if ( this.object && this.object.fromDeclaration === false ) {

				this.object.name = name;
				this.object.fromDeclaration = ( fromDeclaration !== false );
				return;

			}

			const previousMaterial = ( this.object && typeof this.object.currentMaterial === 'function' ? this.object.currentMaterial() : undefined );

			if ( this.object && typeof this.object._finalize === 'function' ) {

				this.object._finalize( true );

			}

			this.object = {
				name: name || '',
				fromDeclaration: ( fromDeclaration !== false ),

				geometry: {
					vertices: [],
					normals: [],
					colors: [],
					uvs: [],
					hasUVIndices: false
				},
				materials: [],
				smooth: true,

				startMaterial: function ( name, libraries ) {

					const previous = this._finalize( false );

					// New usemtl declaration overwrites an inherited material, except if faces were declared
					// after the material, then it must be preserved for proper MultiMaterial continuation.
					if ( previous && ( previous.inherited || previous.groupCount <= 0 ) ) {

						this.materials.splice( previous.index, 1 );

					}

					const material = {
						index: this.materials.length,
						name: name || '',
						mtllib: ( Array.isArray( libraries ) && libraries.length > 0 ? libraries[ libraries.length - 1 ] : '' ),
						smooth: ( previous !== undefined ? previous.smooth : this.smooth ),
						groupStart: ( previous !== undefined ? previous.groupEnd : 0 ),
						groupEnd: - 1,
						groupCount: - 1,
						inherited: false,

						clone: function ( index ) {

							const cloned = {
								index: ( typeof index === 'number' ? index : this.index ),
								name: this.name,
								mtllib: this.mtllib,
								smooth: this.smooth,
								groupStart: 0,
								groupEnd: - 1,
								groupCount: - 1,
								inherited: false
							};
							cloned.clone = this.clone.bind( cloned );
							return cloned;

						}
					};

					this.materials.push( material );

					return material;

				},

				currentMaterial: function () {

					if ( this.materials.length > 0 ) {

						return this.materials[ this.materials.length - 1 ];

					}

					return undefined;

				},

				_finalize: function ( end ) {

					const lastMultiMaterial = this.currentMaterial();
					if ( lastMultiMaterial && lastMultiMaterial.groupEnd === - 1 ) {

						lastMultiMaterial.groupEnd = this.geometry.vertices.length / 3;
						lastMultiMaterial.groupCount = lastMultiMaterial.groupEnd - lastMultiMaterial.groupStart;
						lastMultiMaterial.inherited = false;

					}

					// Ignore objects tail materials if no face declarations followed them before a new o/g started.
					if ( end && this.materials.length > 1 ) {

						for ( let mi = this.materials.length - 1; mi >= 0; mi -- ) {

							if ( this.materials[ mi ].groupCount <= 0 ) {

								this.materials.splice( mi, 1 );

							}

						}

					}

					// Guarantee at least one empty material, this makes the creation later more straight forward.
					if ( end && this.materials.length === 0 ) {

						this.materials.push( {
							name: '',
							smooth: this.smooth
						} );

					}

					return lastMultiMaterial;

				}
			};

			// Inherit previous objects material.
			// Spec tells us that a declared material must be set to all objects until a new material is declared.
			// If a usemtl declaration is encountered while this new object is being parsed, it will
			// overwrite the inherited material. Exception being that there was already face declarations
			// to the inherited material, then it will be preserved for proper MultiMaterial continuation.

			if ( previousMaterial && previousMaterial.name && typeof previousMaterial.clone === 'function' ) {

				const declared = previousMaterial.clone( 0 );
				declared.inherited = true;
				this.object.materials.push( declared );

			}

			this.objects.push( this.object );

		},

		finalize: function () {

			if ( this.object && typeof this.object._finalize === 'function' ) {

				this.object._finalize( true );

			}

		},

		parseVertexIndex: function ( value, len ) {

			const index = parseInt( value, 10 );
			return ( index >= 0 ? index - 1 : index + len / 3 ) * 3;

		},

		parseNormalIndex: function ( value, len ) {

			const index = parseInt( value, 10 );
			return ( index >= 0 ? index - 1 : index + len / 3 ) * 3;

		},

		parseUVIndex: function ( value, len ) {

			const index = parseInt( value, 10 );
			return ( index >= 0 ? index - 1 : index + len / 2 ) * 2;

		},

		addVertex: function ( a, b, c ) {

			const src = this.vertices;
			const dst = this.object.geometry.vertices;

			dst.push( src[ a + 0 ], src[ a + 1 ], src[ a + 2 ] );
			dst.push( src[ b + 0 ], src[ b + 1 ], src[ b + 2 ] );
			dst.push( src[ c + 0 ], src[ c + 1 ], src[ c + 2 ] );

		},

		addVertexPoint: function ( a ) {

			const src = this.vertices;
			const dst = this.object.geometry.vertices;

			dst.push( src[ a + 0 ], src[ a + 1 ], src[ a + 2 ] );

		},

		addVertexLine: function ( a ) {

			const src = this.vertices;
			const dst = this.object.geometry.vertices;

			dst.push( src[ a + 0 ], src[ a + 1 ], src[ a + 2 ] );

		},

		addNormal: function ( a, b, c ) {

			const src = this.normals;
			const dst = this.object.geometry.normals;

			dst.push( src[ a + 0 ], src[ a + 1 ], src[ a + 2 ] );
			dst.push( src[ b + 0 ], src[ b + 1 ], src[ b + 2 ] );
			dst.push( src[ c + 0 ], src[ c + 1 ], src[ c + 2 ] );

		},

		addFaceNormal: function ( a, b, c ) {

			const src = this.vertices;
			const dst = this.object.geometry.normals;

			_vA.fromArray( src, a );
			_vB.fromArray( src, b );
			_vC.fromArray( src, c );

			_cb.subVectors( _vC, _vB );
			_ab.subVectors( _vA, _vB );
			_cb.cross( _ab );

			_cb.normalize();

			dst.push( _cb.x, _cb.y, _cb.z );
			dst.push( _cb.x, _cb.y, _cb.z );
			dst.push( _cb.x, _cb.y, _cb.z );

		},

		addColor: function ( a, b, c ) {

			const src = this.colors;
			const dst = this.object.geometry.colors;

			if ( src[ a ] !== undefined ) dst.push( src[ a + 0 ], src[ a + 1 ], src[ a + 2 ] );
			if ( src[ b ] !== undefined ) dst.push( src[ b + 0 ], src[ b + 1 ], src[ b + 2 ] );
			if ( src[ c ] !== undefined ) dst.push( src[ c + 0 ], src[ c + 1 ], src[ c + 2 ] );

		},

		addUV: function ( a, b, c ) {

			const src = this.uvs;
			const dst = this.object.geometry.uvs;

			dst.push( src[ a + 0 ], src[ a + 1 ] );
			dst.push( src[ b + 0 ], src[ b + 1 ] );
			dst.push( src[ c + 0 ], src[ c + 1 ] );

		},

		addDefaultUV: function () {

			const dst = this.object.geometry.uvs;

			dst.push( 0, 0 );
			dst.push( 0, 0 );
			dst.push( 0, 0 );

		},

		addUVLine: function ( a ) {

			const src = this.uvs;
			const dst = this.object.geometry.uvs;

			dst.push( src[ a + 0 ], src[ a + 1 ] );

		},

		addFace: function ( a, b, c, ua, ub, uc, na, nb, nc ) {

			const vLen = this.vertices.length;

			let ia = this.parseVertexIndex( a, vLen );
			let ib = this.parseVertexIndex( b, vLen );
			let ic = this.parseVertexIndex( c, vLen );

			this.addVertex( ia, ib, ic );
			this.addColor( ia, ib, ic );

			// normals

			if ( na !== undefined && na !== '' ) {

				const nLen = this.normals.length;

				ia = this.parseNormalIndex( na, nLen );
				ib = this.parseNormalIndex( nb, nLen );
				ic = this.parseNormalIndex( nc, nLen );

				this.addNormal( ia, ib, ic );

			} else {

				this.addFaceNormal( ia, ib, ic );

			}

			// uvs

			if ( ua !== undefined && ua !== '' ) {

				const uvLen = this.uvs.length;

				ia = this.parseUVIndex( ua, uvLen );
				ib = this.parseUVIndex( ub, uvLen );
				ic = this.parseUVIndex( uc, uvLen );

				this.addUV( ia, ib, ic );

				this.object.geometry.hasUVIndices = true;

			} else {

				// add placeholder values (for inconsistent face definitions)

				this.addDefaultUV();

			}

		},

		addPointGeometry: function ( vertices ) {

			this.object.geometry.type = 'Points';

			const vLen = this.vertices.length;

			for ( let vi = 0, l = vertices.length; vi < l; vi ++ ) {

				const index = this.parseVertexIndex( vertices[ vi ], vLen );

				this.addVertexPoint( index );
				this.addColor( index );

			}

		},

		addLineGeometry: function ( vertices, uvs ) {

			this.object.geometry.type = 'Line';

			const vLen = this.vertices.length;
			const uvLen = this.uvs.length;

			for ( let vi = 0, l = vertices.length; vi < l; vi ++ ) {

				this.addVertexLine( this.parseVertexIndex( vertices[ vi ], vLen ) );

			}

			for ( let uvi = 0, l = uvs.length; uvi < l; uvi ++ ) {

				this.addUVLine( this.parseUVIndex( uvs[ uvi ], uvLen ) );

			}

		}

	};

	state.startObject( '', false );

	return state;

}

//

class OBJLoader extends Loader {

	constructor( manager ) {

		super( manager );

		this.materials = null;

	}

	load( url, onLoad, onProgress, onError ) {

		const scope = this;

		const loader = new FileLoader( this.manager );
		loader.setPath( this.path );
		loader.setRequestHeader( this.requestHeader );
		loader.setWithCredentials( this.withCredentials );
		loader.load( url, function ( text ) {

			try {

				onLoad( scope.parse( text ) );

			} catch ( e ) {

				if ( onError ) {

					onError( e );

				} else {

					console.error( e );

				}

				scope.manager.itemError( url );

			}

		}, onProgress, onError );

	}

	setMaterials( materials ) {

		this.materials = materials;

		return this;

	}

	parse( text ) {

		const state = new ParserState();

		if ( text.indexOf( '\r\n' ) !== - 1 ) {

			// This is faster than String.split with regex that splits on both
			text = text.replace( /\r\n/g, '\n' );

		}

		if ( text.indexOf( '\\\n' ) !== - 1 ) {

			// join lines separated by a line continuation character (\)
			text = text.replace( /\\\n/g, '' );

		}

		const lines = text.split( '\n' );
		let result = [];

		for ( let i = 0, l = lines.length; i < l; i ++ ) {

			const line = lines[ i ].trimStart();

			if ( line.length === 0 ) continue;

			const lineFirstChar = line.charAt( 0 );

			// @todo invoke passed in handler if any
			if ( lineFirstChar === '#' ) continue; // skip comments

			if ( lineFirstChar === 'v' ) {

				const data = line.split( _face_vertex_data_separator_pattern );

				switch ( data[ 0 ] ) {

					case 'v':
						state.vertices.push(
							parseFloat( data[ 1 ] ),
							parseFloat( data[ 2 ] ),
							parseFloat( data[ 3 ] )
						);
						if ( data.length >= 7 ) {

							_color.setRGB(
								parseFloat( data[ 4 ] ),
								parseFloat( data[ 5 ] ),
								parseFloat( data[ 6 ] ),
								SRGBColorSpace
							);

							state.colors.push( _color.r, _color.g, _color.b );

						} else {

							// if no colors are defined, add placeholders so color and vertex indices match

							state.colors.push( undefined, undefined, undefined );

						}

						break;
					case 'vn':
						state.normals.push(
							parseFloat( data[ 1 ] ),
							parseFloat( data[ 2 ] ),
							parseFloat( data[ 3 ] )
						);
						break;
					case 'vt':
						state.uvs.push(
							parseFloat( data[ 1 ] ),
							parseFloat( data[ 2 ] )
						);
						break;

				}

			} else if ( lineFirstChar === 'f' ) {

				const lineData = line.slice( 1 ).trim();
				const vertexData = lineData.split( _face_vertex_data_separator_pattern );
				const faceVertices = [];

				// Parse the face vertex data into an easy to work with format

				for ( let j = 0, jl = vertexData.length; j < jl; j ++ ) {

					const vertex = vertexData[ j ];

					if ( vertex.length > 0 ) {

						const vertexParts = vertex.split( '/' );
						faceVertices.push( vertexParts );

					}

				}

				// Draw an edge between the first vertex and all subsequent vertices to form an n-gon

				const v1 = faceVertices[ 0 ];

				for ( let j = 1, jl = faceVertices.length - 1; j < jl; j ++ ) {

					const v2 = faceVertices[ j ];
					const v3 = faceVertices[ j + 1 ];

					state.addFace(
						v1[ 0 ], v2[ 0 ], v3[ 0 ],
						v1[ 1 ], v2[ 1 ], v3[ 1 ],
						v1[ 2 ], v2[ 2 ], v3[ 2 ]
					);

				}

			} else if ( lineFirstChar === 'l' ) {

				const lineParts = line.substring( 1 ).trim().split( ' ' );
				let lineVertices = [];
				const lineUVs = [];

				if ( line.indexOf( '/' ) === - 1 ) {

					lineVertices = lineParts;

				} else {

					for ( let li = 0, llen = lineParts.length; li < llen; li ++ ) {

						const parts = lineParts[ li ].split( '/' );

						if ( parts[ 0 ] !== '' ) lineVertices.push( parts[ 0 ] );
						if ( parts[ 1 ] !== '' ) lineUVs.push( parts[ 1 ] );

					}

				}

				state.addLineGeometry( lineVertices, lineUVs );

			} else if ( lineFirstChar === 'p' ) {

				const lineData = line.slice( 1 ).trim();
				const pointData = lineData.split( ' ' );

				state.addPointGeometry( pointData );

			} else if ( ( result = _object_pattern.exec( line ) ) !== null ) {

				// o object_name
				// or
				// g group_name

				// WORKAROUND: https://bugs.chromium.org/p/v8/issues/detail?id=2869
				// let name = result[ 0 ].slice( 1 ).trim();
				const name = ( ' ' + result[ 0 ].slice( 1 ).trim() ).slice( 1 );

				state.startObject( name );

			} else if ( _material_use_pattern.test( line ) ) {

				// material

				state.object.startMaterial( line.substring( 7 ).trim(), state.materialLibraries );

			} else if ( _material_library_pattern.test( line ) ) {

				// mtl file

				state.materialLibraries.push( line.substring( 7 ).trim() );

			} else if ( _map_use_pattern.test( line ) ) {

				// the line is parsed but ignored since the loader assumes textures are defined MTL files
				// (according to https://www.okino.com/conv/imp_wave.htm, 'usemap' is the old-style Wavefront texture reference method)

				console.warn( 'THREE.OBJLoader: Rendering identifier "usemap" not supported. Textures must be defined in MTL files.' );

			} else if ( lineFirstChar === 's' ) {

				result = line.split( ' ' );

				// smooth shading

				// @todo Handle files that have varying smooth values for a set of faces inside one geometry,
				// but does not define a usemtl for each face set.
				// This should be detected and a dummy material created (later MultiMaterial and geometry groups).
				// This requires some care to not create extra material on each smooth value for "normal" obj files.
				// where explicit usemtl defines geometry groups.
				// Example asset: examples/models/obj/cerberus/Cerberus.obj

				/*
					 * http://paulbourke.net/dataformats/obj/
					 *
					 * From chapter "Grouping" Syntax explanation "s group_number":
					 * "group_number is the smoothing group number. To turn off smoothing groups, use a value of 0 or off.
					 * Polygonal elements use group numbers to put elements in different smoothing groups. For free-form
					 * surfaces, smoothing groups are either turned on or off; there is no difference between values greater
					 * than 0."
					 */
				if ( result.length > 1 ) {

					const value = result[ 1 ].trim().toLowerCase();
					state.object.smooth = ( value !== '0' && value !== 'off' );

				} else {

					// ZBrush can produce "s" lines #11707
					state.object.smooth = true;

				}

				const material = state.object.currentMaterial();
				if ( material ) material.smooth = state.object.smooth;

			} else {

				// Handle null terminated files without exception
				if ( line === '\0' ) continue;

				console.warn( 'THREE.OBJLoader: Unexpected line: "' + line + '"' );

			}

		}

		state.finalize();

		const container = new Group();
		container.materialLibraries = [].concat( state.materialLibraries );

		const hasPrimitives = ! ( state.objects.length === 1 && state.objects[ 0 ].geometry.vertices.length === 0 );

		if ( hasPrimitives === true ) {

			for ( let i = 0, l = state.objects.length; i < l; i ++ ) {

				const object = state.objects[ i ];
				const geometry = object.geometry;
				const materials = object.materials;
				const isLine = ( geometry.type === 'Line' );
				const isPoints = ( geometry.type === 'Points' );
				let hasVertexColors = false;

				// Skip o/g line declarations that did not follow with any faces
				if ( geometry.vertices.length === 0 ) continue;

				const buffergeometry = new BufferGeometry();

				buffergeometry.setAttribute( 'position', new Float32BufferAttribute( geometry.vertices, 3 ) );

				if ( geometry.normals.length > 0 ) {

					buffergeometry.setAttribute( 'normal', new Float32BufferAttribute( geometry.normals, 3 ) );

				}

				if ( geometry.colors.length > 0 ) {

					hasVertexColors = true;
					buffergeometry.setAttribute( 'color', new Float32BufferAttribute( geometry.colors, 3 ) );

				}

				if ( geometry.hasUVIndices === true ) {

					buffergeometry.setAttribute( 'uv', new Float32BufferAttribute( geometry.uvs, 2 ) );

				}

				// Create materials

				const createdMaterials = [];

				for ( let mi = 0, miLen = materials.length; mi < miLen; mi ++ ) {

					const sourceMaterial = materials[ mi ];
					const materialHash = sourceMaterial.name + '_' + sourceMaterial.smooth + '_' + hasVertexColors;
					let material = state.materials[ materialHash ];

					if ( this.materials !== null ) {

						material = this.materials.create( sourceMaterial.name );

						// mtl etc. loaders probably can't create line materials correctly, copy properties to a line material.
						if ( isLine && material && ! ( material instanceof LineBasicMaterial ) ) {

							const materialLine = new LineBasicMaterial();
							Material.prototype.copy.call( materialLine, material );
							materialLine.color.copy( material.color );
							material = materialLine;

						} else if ( isPoints && material && ! ( material instanceof PointsMaterial ) ) {

							const materialPoints = new PointsMaterial( { size: 10, sizeAttenuation: false } );
							Material.prototype.copy.call( materialPoints, material );
							materialPoints.color.copy( material.color );
							materialPoints.map = material.map;
							material = materialPoints;

						}

					}

					if ( material === undefined ) {

						if ( isLine ) {

							material = new LineBasicMaterial();

						} else if ( isPoints ) {

							material = new PointsMaterial( { size: 1, sizeAttenuation: false } );

						} else {

							material = new MeshPhongMaterial();

						}

						material.name = sourceMaterial.name;
						material.flatShading = sourceMaterial.smooth ? false : true;
						material.vertexColors = hasVertexColors;

						state.materials[ materialHash ] = material;

					}

					createdMaterials.push( material );

				}

				// Create mesh

				let mesh;

				if ( createdMaterials.length > 1 ) {

					for ( let mi = 0, miLen = materials.length; mi < miLen; mi ++ ) {

						const sourceMaterial = materials[ mi ];
						buffergeometry.addGroup( sourceMaterial.groupStart, sourceMaterial.groupCount, mi );

					}

					if ( isLine ) {

						mesh = new LineSegments( buffergeometry, createdMaterials );

					} else if ( isPoints ) {

						mesh = new Points( buffergeometry, createdMaterials );

					} else {

						mesh = new Mesh( buffergeometry, createdMaterials );

					}

				} else {

					if ( isLine ) {

						mesh = new LineSegments( buffergeometry, createdMaterials[ 0 ] );

					} else if ( isPoints ) {

						mesh = new Points( buffergeometry, createdMaterials[ 0 ] );

					} else {

						mesh = new Mesh( buffergeometry, createdMaterials[ 0 ] );

					}

				}

				mesh.name = object.name;

				container.add( mesh );

			}

		} else {

			// if there is only the default parser state object with no geometry data, interpret data as point cloud

			if ( state.vertices.length > 0 ) {

				const material = new PointsMaterial( { size: 1, sizeAttenuation: false } );

				const buffergeometry = new BufferGeometry();

				buffergeometry.setAttribute( 'position', new Float32BufferAttribute( state.vertices, 3 ) );

				if ( state.colors.length > 0 && state.colors[ 0 ] !== undefined ) {

					buffergeometry.setAttribute( 'color', new Float32BufferAttribute( state.colors, 3 ) );
					material.vertexColors = true;

				}

				const points = new Points( buffergeometry, material );
				container.add( points );

			}

		}

		return container;

	}

}

return OBJLoader;
})();const helmetData="v 278.47 24.96 -64.75\r\nv 272.56 26.92 -66.62\r\nv 274.16 27.03 -69.17\r\nv 280.74 25.21 -67.23\r\nv 268.62 28.97 -67.82\r\nv 269.67 28.87 -70.34\r\nv 271.54 28.95 -71.34\r\nv 275.31 27.76 -70.95\r\nv 282.33 26.09 -68.96\r\nv 285.67 23.24 -62.02\r\nv 282.2 23.8 -59.23\r\nv 275.75 25.44 -61.81\r\nv 293.52 21.58 -58.38\r\nv 289.44 22.27 -55.78\r\nv 285.79 23.83 -52.82\r\nv 279.13 25.23 -56.11\r\nv 273.38 26.79 -58.56\r\nv 301.4 19.8 -53.76\r\nv 296.77 20.63 -51.36\r\nv 309.12 17.8 -48.18\r\nv 304.05 18.76 -45.98\r\nv 299.47 20.58 -43.42\r\nv 292.62 22.32 -48.59\r\nv 316.5 15.47 -41.66\r\nv 311.08 16.57 -39.65\r\nv 323.56 12.81 -34.12\r\nv 317.92 14.06 -32.32\r\nv 312.69 16.24 -30.2\r\nv 306.15 18.55 -37.3\r\nv 324.61 11.26 -23.93\r\nv 319.14 13.69 -22.05\r\nv 330.36 9.79 -25.52\r\nv 336.79 6.42 -15.77\r\nv 330.98 8.26 -14.43\r\nv 325.3 11.11 -12.83\r\nv 336.89 5.16 -3.77\r\nv 330.99 8.67 -2.51\r\nv 342.74 2.73 -4.83\r\nv 347.81 -0.52 7.27\r\nv 341.92 2.49 8\r\nv 335.82 6.61 8.85\r\nv 351.6 -2.51 20.5\r\nv 345.66 0.76 20.84\r\nv 353.75 -3.61 34.63\r\nv 347.77 -0.26 34.53\r\nv 341.39 4.3 34.35\r\nv 339.38 5.19 21.2\r\nv 347.94 -0.77 48.88\r\nv 341.54 3.87 48.12\r\nv 353.93 -4.18 49.45\r\nv 352.2 -4 64.4\r\nv 346.24 -0.65 63.35\r\nv 339.9 3.93 62.01\r\nv 342.74 0.24 77.45\r\nv 336.56 4.54 75.54\r\nv 348.62 -2.86 78.95\r\nv 343.55 -1.22 92.51\r\nv 337.81 1.57 90.58\r\nv 331.87 5.52 88.17\r\nv 331.83 3.01 102.21\r\nv 326.18 6.68 99.37\r\nv 337.34 0.46 104.5\r\nv 330.34 2.29 114.95\r\nv 325.11 4.57 112.26\r\nv 319.8 7.92 109\r\nv 317.97 6.25 120.71\r\nv 313.01 9.14 116.94\r\nv 322.91 4.38 123.89\r\nv 315.1 6.43 131.19\r\nv 310.47 7.87 127.54\r\nv 305.86 10.27 123.33\r\nv 302.67 9.23 132.77\r\nv 298.42 11.24 128.33\r\nv 307 8.14 136.7\r\nv 298.66 9.57 140.98\r\nv 294.7 10.37 136.86\r\nv 290.86 12.04 132.27\r\nv 286.69 11.31 140.27\r\nv 283.4 12.67 135.5\r\nv 290.15 10.77 144.6\r\nv 281.99 11.77 147.48\r\nv 279.21 12.12 143.01\r\nv 276.65 13.25 138.13\r\nv 272.83 12.88 145.12\r\nv 271.22 13.9 140.29\r\nv 274.68 12.61 149.5\r\nv 269.52 13.51 150.73\r\nv 268.45 13.8 146.58\r\nv 267.65 14.79 141.94\r\nv 266.88 30.89 -61.48\r\nv 269.29 28.75 -60.27\r\nv 268.61 31.06 -56.82\r\nv 266.62 33.14 -58.08\r\nv 272.17 29.15 -55.14\r\nv 271.73 32.45 -51.68\r\nv 268.44 34.25 -53.35\r\nv 266.64 36.22 -54.68\r\nv 283.68 26.39 -49.67\r\nv 277.45 27.68 -52.8\r\nv 282.59 29.89 -46.41\r\nv 276.7 31.08 -49.41\r\nv 290.11 24.98 -45.62\r\nv 296.58 23.38 -40.65\r\nv 294.77 27.12 -37.76\r\nv 288.67 28.6 -42.53\r\nv 302.91 21.49 -34.74\r\nv 309.13 19.37 -27.87\r\nv 306.66 23.4 -25.44\r\nv 300.77 25.36 -32.07\r\nv 321.12 14.77 -11.08\r\nv 315.27 17.06 -19.99\r\nv 317.98 19.19 -9.26\r\nv 312.46 21.28 -17.83\r\nv 331.05 11.02 9.76\r\nv 326.51 12.71 -1.15\r\nv 327.27 15.84 10.71\r\nv 323.03 17.34 0.28\r\nv 334.4 9.83 21.58\r\nv 336.27 9.1 34.15\r\nv 332.12 14.26 33.94\r\nv 330.38 14.85 21.98\r\nv 334.85 8.81 60.53\r\nv 336.4 8.76 47.27\r\nv 330.77 14.07 59.01\r\nv 332.23 14.02 46.42\r\nv 331.69 9.25 73.46\r\nv 327.25 9.97 85.56\r\nv 323.6 14.93 82.89\r\nv 327.79 14.41 71.33\r\nv 321.87 10.86 96.32\r\nv 315.82 11.8 105.59\r\nv 312.76 16.24 102.15\r\nv 318.51 15.57 93.22\r\nv 302.58 13.45 119.31\r\nv 309.38 12.67 113.19\r\nv 300.18 17.37 115.45\r\nv 306.64 16.85 109.51\r\nv 288.34 14.64 127.96\r\nv 295.5 14.11 124.14\r\nv 286.68 18.11 123.88\r\nv 293.45 17.79 120.15\r\nv 275.12 15.44 133.64\r\nv 281.34 15.03 131.07\r\nv 274.28 18.59 129.46\r\nv 280.08 18.33 126.92\r\nv 267.32 16.81 137.62\r\nv 270.35 15.97 135.84\r\nv 267.24 19.76 133.55\r\nv 269.93 19.02 131.68\r\nv 271.93 40.98 -45.27\r\nv 272.45 44.96 -43.06\r\nv 269.18 46.08 -44.73\r\nv 268.73 42.42 -46.96\r\nv 273.12 48.17 -41.93\r\nv 269.73 48.96 -43.62\r\nv 268.13 49.04 -45.05\r\nv 267.28 47.29 -46.16\r\nv 266.95 43.96 -48.42\r\nv 281.83 38.97 -40.28\r\nv 282.08 43.25 -38.12\r\nv 276.95 44.08 -40.9\r\nv 276.52 39.92 -43.09\r\nv 282.64 46.71 -36.98\r\nv 277.6 47.46 -39.76\r\nv 292.64 36.65 -32.24\r\nv 292.39 41.09 -30.27\r\nv 287.24 42.28 -34.59\r\nv 287.23 37.91 -36.67\r\nv 292.67 44.64 -29.19\r\nv 287.65 45.79 -33.46\r\nv 302.64 41.48 -18.2\r\nv 297.67 43.17 -24.11\r\nv 297.53 39.59 -25.12\r\nv 302.63 37.86 -19.12\r\nv 298.01 35.1 -26.95\r\nv 303.31 33.32 -20.79\r\nv 312.21 37.87 -3.9\r\nv 307.55 39.65 -11.46\r\nv 307.65 36 -12.27\r\nv 312.42 34.18 -4.58\r\nv 308.53 31.41 -13.73\r\nv 313.49 29.54 -5.81\r\nv 319.94 35.18 13.51\r\nv 316.42 36.34 4.45\r\nv 316.75 32.6 3.92\r\nv 320.36 31.4 13.16\r\nv 317.99 27.9 2.97\r\nv 321.76 26.64 12.52\r\nv 323.88 34.24 33.48\r\nv 322.48 34.53 23.24\r\nv 322.98 30.71 23.08\r\nv 324.43 30.39 33.54\r\nv 324.5 25.88 22.79\r\nv 326.02 25.51 33.63\r\nv 322.67 34.19 54.83\r\nv 323.93 34.15 44.11\r\nv 324.49 30.29 44.42\r\nv 323.21 30.33 55.39\r\nv 326.1 25.38 44.92\r\nv 324.77 25.42 56.33\r\nv 316.43 34.37 75.28\r\nv 320.09 34.27 65.34\r\nv 320.59 30.43 66.16\r\nv 316.88 30.57 76.33\r\nv 322.08 25.57 67.5\r\nv 318.26 25.8 78.05\r\nv 306.82 34.58 92.25\r\nv 311.92 34.48 84.31\r\nv 312.31 30.73 85.55\r\nv 307.14 30.89 93.62\r\nv 313.57 26.06 87.57\r\nv 308.27 26.32 95.88\r\nv 296.72 26.75 108.43\r\nv 295.91 31.13 105.91\r\nv 301.64 31.03 100.37\r\nv 302.63 26.56 102.79\r\nv 295.73 34.72 104.42\r\nv 301.37 34.67 98.92\r\nv 284.6 26.95 116.54\r\nv 284.26 31.17 113.93\r\nv 290.06 31.18 110.36\r\nv 290.66 26.88 112.93\r\nv 284.35 34.66 112.45\r\nv 290.01 34.73 108.86\r\nv 273.57 26.96 122.01\r\nv 273.69 30.94 119.33\r\nv 278.64 31.07 116.87\r\nv 278.73 26.95 119.51\r\nv 274.05 34.2 117.82\r\nv 278.89 34.49 115.38\r\nv 268.41 33.1 121.73\r\nv 270.26 33.78 119.92\r\nv 269.89 30.8 121.46\r\nv 267.52 30.97 123.29\r\nv 269.66 27.08 124.22\r\nv 267.25 27.49 126.15\r\nv 270.78 27.43 -63.59\r\nv 267.62 29.58 -64.8\r\nv 290.51 24.27 -65.95\r\nv 288.52 23.42 -64.34\r\nv 299.3 22.35 -61.94\r\nv 296.91 21.61 -60.49\r\nv 308.13 20.35 -56.95\r\nv 305.33 19.7 -55.67\r\nv 316.65 18.17 -51.01\r\nv 313.51 17.6 -49.89\r\nv 321.18 15.19 -43.18\r\nv 324.52 15.75 -44.16\r\nv 331.73 13.02 -36.32\r\nv 328.36 12.44 -35.47\r\nv 335.08 9.33 -26.69\r\nv 338.27 9.93 -27.42\r\nv 344.21 6.44 -17.38\r\nv 341.32 5.82 -16.76\r\nv 347.06 1.89 -5.62\r\nv 349.61 2.55 -6.13\r\nv 354.16 -0.87 6.32\r\nv 351.93 -1.56 6.71\r\nv 357.58 -2.93 19.93\r\nv 355.58 -3.65 20.2\r\nv 359.53 -4.04 34.47\r\nv 357.66 -4.79 34.62\r\nv 357.85 -5.39 49.73\r\nv 359.72 -4.64 49.71\r\nv 358.1 -4.45 65.09\r\nv 356.15 -5.2 64.99\r\nv 352.6 -3.97 79.84\r\nv 354.66 -3.21 80.04\r\nv 349.68 -1.43 93.99\r\nv 347.51 -2.19 93.67\r\nv 341.21 -0.38 105.9\r\nv 343.41 0.41 106.35\r\nv 336.25 2.42 117.2\r\nv 334.06 1.6 116.62\r\nv 326.44 3.88 125.87\r\nv 328.55 4.72 126.63\r\nv 320.45 6.97 134.47\r\nv 318.43 6.13 133.5\r\nv 310.14 8.01 139.33\r\nv 312.08 8.85 140.58\r\nv 303.4 10.39 145.43\r\nv 301.57 9.58 143.9\r\nv 292.74 10.9 147.75\r\nv 294.4 11.68 149.49\r\nv 285.57 12.66 152.54\r\nv 284.14 11.95 150.71\r\nv 276.28 12.74 152.62\r\nv 277.41 13.32 154.37\r\nv 272.83 13.8 154.55\r\nv 270.65 13.54 153.61\r\nv 268.48 38.26 -49.95\r\nv 271.67 36.6 -48.26\r\nv 266.74 40.05 -51.36\r\nv 281.99 34.3 -43.16\r\nv 276.41 35.37 -46.05\r\nv 287.7 33.13 -39.43\r\nv 293.44 31.77 -34.84\r\nv 299.11 30.13 -29.37\r\nv 304.69 28.27 -22.98\r\nv 315.39 24.33 -7.43\r\nv 310.17 26.28 -15.67\r\nv 324.12 21.25 11.66\r\nv 320.14 22.61 1.7\r\nv 327.03 20.4 22.4\r\nv 328.64 19.93 33.77\r\nv 328.73 19.75 45.61\r\nv 327.35 19.81 57.57\r\nv 324.53 20.04 69.28\r\nv 320.55 20.4 80.31\r\nv 315.69 20.82 90.22\r\nv 310.2 21.26 98.82\r\nv 298.19 21.99 111.72\r\nv 304.34 21.65 105.94\r\nv 285.42 22.4 119.96\r\nv 291.81 22.23 116.31\r\nv 273.77 22.64 125.48\r\nv 279.21 22.5 122.96\r\nv 269.69 22.94 127.71\r\nv 267.19 23.54 129.65\r\nv 268.16 30.68 -70.91\r\nv 270.5 30.21 -72.17\r\nv 267.34 30.99 -68.52\r\nv 267.38 32.98 -68.98\r\nv 268.13 32.47 -71.18\r\nv 270.46 31.83 -72.31\r\nv 275.77 29.08 -71.65\r\nv 283.05 27.51 -69.7\r\nv 275.87 30.71 -71.82\r\nv 283.33 29.2 -69.92\r\nv 267 15.08 147.4\r\nv 266.47 16.1 143.08\r\nv 267.81 14.7 151.22\r\nv 267.83 16.04 151.34\r\nv 267.02 16.52 147.9\r\nv 266.48 17.62 143.95\r\nv 266.03 35.11 -59.19\r\nv 266.15 32.96 -62.45\r\nv 266.14 38.05 -55.94\r\nv 266.28 39.81 -57.16\r\nv 266.14 37.04 -60.22\r\nv 266.26 34.98 -63.28\r\nv 266.36 18.1 138.98\r\nv 266.45 20.97 135.06\r\nv 266.44 19.62 140.12\r\nv 266.61 22.41 136.39\r\nv 270.25 50.75 -43.98\r\nv 267.87 50.4 -45.66\r\nv 273.83 50.24 -42.18\r\nv 274.56 51.75 -43.12\r\nv 270.77 51.99 -45.07\r\nv 268.21 51.43 -46.92\r\nv 266.89 48.42 -47.58\r\nv 266.62 45.34 -49.9\r\nv 267.25 49.51 -49\r\nv 267.02 46.64 -51.38\r\nv 278.37 49.65 -39.94\r\nv 283.4 48.92 -37.11\r\nv 284.27 50.51 -37.88\r\nv 279.2 51.24 -40.78\r\nv 288.41 48.01 -33.56\r\nv 293.4 46.86 -29.26\r\nv 294.38 48.42 -29.91\r\nv 289.33 49.59 -34.27\r\nv 298.39 45.41 -24.16\r\nv 303.34 43.74 -18.25\r\nv 304.4 45.32 -18.78\r\nv 299.41 46.97 -24.75\r\nv 308.23 41.94 -11.5\r\nv 312.85 40.21 -3.94\r\nv 313.93 41.88 -4.34\r\nv 309.3 43.56 -11.97\r\nv 317.03 38.73 4.41\r\nv 320.5 37.61 13.47\r\nv 321.56 39.36 13.24\r\nv 318.1 40.44 4.09\r\nv 323.01 36.98 23.19\r\nv 324.38 36.7 33.42\r\nv 325.41 38.48 33.36\r\nv 324.06 38.75 23.05\r\nv 324.42 36.63 44.01\r\nv 323.16 36.67 54.68\r\nv 324.16 38.47 54.75\r\nv 325.44 38.41 44.02\r\nv 320.59 36.75 65.14\r\nv 316.94 36.84 75.03\r\nv 317.92 38.66 75.19\r\nv 321.59 38.56 65.25\r\nv 312.43 36.94 84.04\r\nv 307.31 37.03 91.97\r\nv 308.22 38.86 92.25\r\nv 313.38 38.77 84.24\r\nv 301.85 37.1 98.68\r\nv 296.18 37.14 104.25\r\nv 296.96 38.97 104.72\r\nv 302.69 38.93 99.05\r\nv 290.47 37.13 108.75\r\nv 284.83 37.04 112.39\r\nv 285.52 38.85 113.05\r\nv 291.19 38.96 109.32\r\nv 279.41 36.82 115.37\r\nv 274.56 36.4 117.86\r\nv 275.16 38.05 118.69\r\nv 280.07 38.6 116.1\r\nv 270.67 35.7 120.06\r\nv 268.01 34.29 122.27\r\nv 268.29 35.42 123.46\r\nv 271.1 37.08 121.04\r\nv 266.88 31.74 124.83\r\nv 266.6 28.4 127.8\r\nv 266.84 29.54 129.32\r\nv 267.11 32.81 126.22\r\nv 266.63 31.66 -65.63\r\nv 266.72 33.72 -66.28\r\nv 291.42 25.7 -66.64\r\nv 300.37 23.75 -62.54\r\nv 291.78 27.42 -66.86\r\nv 300.76 25.48 -62.72\r\nv 309.36 21.73 -57.47\r\nv 318.01 19.57 -51.46\r\nv 309.8 23.48 -57.62\r\nv 318.47 21.38 -51.57\r\nv 325.93 17.2 -44.54\r\nv 333.07 14.58 -36.63\r\nv 326.36 19.1 -44.61\r\nv 333.41 16.62 -36.68\r\nv 339.42 11.63 -27.69\r\nv 345.08 8.32 -17.62\r\nv 339.56 13.88 -27.72\r\nv 344.94 10.82 -17.66\r\nv 350.14 4.6 -6.35\r\nv 354.38 1.34 6.1\r\nv 349.67 7.35 -6.42\r\nv 353.58 4.32 5.96\r\nv 357.55 -0.59 19.68\r\nv 359.38 -1.61 34.18\r\nv 356.51 2.56 19.45\r\nv 358.2 1.66 33.81\r\nv 359.57 -2.14 49.34\r\nv 358.07 -1.94 64.64\r\nv 358.41 1.22 48.81\r\nv 357.04 1.45 63.92\r\nv 354.81 -0.74 79.51\r\nv 350.02 0.97 93.4\r\nv 354 2.58 78.62\r\nv 349.45 4.18 92.36\r\nv 343.92 2.71 105.75\r\nv 336.87 4.62 116.62\r\nv 343.58 5.78 104.62\r\nv 336.71 7.53 115.47\r\nv 329.24 6.82 126.13\r\nv 321.16 8.95 134.09\r\nv 329.21 9.56 125\r\nv 321.22 11.49 133.03\r\nv 312.8 10.66 140.37\r\nv 304.11 12.02 145.4\r\nv 312.91 12.96 139.42\r\nv 304.26 14.06 144.59\r\nv 295.07 13.12 149.63\r\nv 286.15 13.92 152.8\r\nv 295.26 14.89 148.96\r\nv 286.32 15.44 152.26\r\nv 277.81 14.4 154.64\r\nv 271.51 14.62 155.02\r\nv 277.84 15.72 154.17\r\nv 271.44 15.83 154.65\r\nv 268.75 14.64 153.82\r\nv 268.71 15.87 153.64\r\nv 266.35 41.67 -52.76\r\nv 266.63 43.21 -54.16\r\nv 266.49 24.61 131.27\r\nv 266.71 25.9 132.75\r\nv 252.2 24.96 -64.75\r\nv 249.94 25.21 -67.23\r\nv 256.51 27.03 -69.17\r\nv 258.11 26.92 -66.62\r\nv 248.34 26.09 -68.96\r\nv 255.36 27.76 -70.95\r\nv 259.13 28.95 -71.34\r\nv 261 28.87 -70.34\r\nv 262.05 28.97 -67.82\r\nv 254.92 25.44 -61.81\r\nv 248.47 23.8 -59.23\r\nv 245 23.24 -62.02\r\nv 257.29 26.79 -58.56\r\nv 251.54 25.23 -56.11\r\nv 244.88 23.83 -52.82\r\nv 241.23 22.27 -55.78\r\nv 237.15 21.58 -58.38\r\nv 233.9 20.63 -51.36\r\nv 229.27 19.8 -53.76\r\nv 238.05 22.32 -48.59\r\nv 231.2 20.58 -43.42\r\nv 226.63 18.76 -45.98\r\nv 221.55 17.8 -48.18\r\nv 219.59 16.57 -39.65\r\nv 214.17 15.47 -41.66\r\nv 224.52 18.55 -37.3\r\nv 217.98 16.24 -30.2\r\nv 212.75 14.06 -32.32\r\nv 207.11 12.81 -34.12\r\nv 211.53 13.69 -22.05\r\nv 206.06 11.26 -23.93\r\nv 205.37 11.11 -12.83\r\nv 199.69 8.26 -14.43\r\nv 193.88 6.42 -15.77\r\nv 200.31 9.79 -25.52\r\nv 199.68 8.67 -2.51\r\nv 193.78 5.16 -3.77\r\nv 194.85 6.61 8.85\r\nv 188.75 2.49 8\r\nv 182.86 -0.52 7.27\r\nv 187.93 2.73 -4.83\r\nv 185.01 0.76 20.84\r\nv 179.08 -2.51 20.5\r\nv 191.29 5.19 21.2\r\nv 189.28 4.3 34.35\r\nv 182.9 -0.26 34.53\r\nv 176.92 -3.61 34.63\r\nv 189.13 3.87 48.12\r\nv 182.73 -0.77 48.88\r\nv 190.77 3.93 62.01\r\nv 184.43 -0.65 63.35\r\nv 178.47 -4 64.4\r\nv 176.74 -4.18 49.45\r\nv 194.11 4.54 75.54\r\nv 187.93 0.24 77.45\r\nv 198.8 5.52 88.17\r\nv 192.86 1.57 90.58\r\nv 187.13 -1.22 92.51\r\nv 182.05 -2.86 78.95\r\nv 204.49 6.68 99.37\r\nv 198.84 3.01 102.21\r\nv 210.87 7.92 109\r\nv 205.56 4.57 112.26\r\nv 200.33 2.29 114.95\r\nv 193.33 0.46 104.5\r\nv 217.66 9.14 116.94\r\nv 212.7 6.25 120.71\r\nv 224.81 10.27 123.33\r\nv 220.2 7.87 127.54\r\nv 215.57 6.43 131.19\r\nv 207.76 4.38 123.89\r\nv 232.25 11.24 128.33\r\nv 228 9.23 132.77\r\nv 239.81 12.04 132.27\r\nv 235.97 10.37 136.86\r\nv 232.01 9.57 140.98\r\nv 223.67 8.14 136.7\r\nv 247.27 12.67 135.5\r\nv 243.98 11.31 140.27\r\nv 254.02 13.25 138.13\r\nv 251.46 12.12 143.01\r\nv 248.68 11.77 147.48\r\nv 240.52 10.77 144.6\r\nv 259.45 13.9 140.29\r\nv 257.84 12.88 145.12\r\nv 263.02 14.79 141.94\r\nv 262.22 13.8 146.58\r\nv 261.15 13.51 150.73\r\nv 256 12.61 149.5\r\nv 263.79 30.89 -61.48\r\nv 264.05 33.14 -58.08\r\nv 262.06 31.06 -56.82\r\nv 261.38 28.75 -60.27\r\nv 264.03 36.22 -54.68\r\nv 262.23 34.25 -53.35\r\nv 258.94 32.45 -51.68\r\nv 258.5 29.15 -55.14\r\nv 253.22 27.68 -52.8\r\nv 246.99 26.39 -49.67\r\nv 253.97 31.08 -49.41\r\nv 248.08 29.89 -46.41\r\nv 240.56 24.98 -45.62\r\nv 242.01 28.6 -42.53\r\nv 235.9 27.12 -37.76\r\nv 234.09 23.38 -40.65\r\nv 227.76 21.49 -34.74\r\nv 229.9 25.36 -32.07\r\nv 224.01 23.4 -25.44\r\nv 221.54 19.37 -27.87\r\nv 215.4 17.06 -19.99\r\nv 209.55 14.77 -11.08\r\nv 218.21 21.28 -17.83\r\nv 212.7 19.19 -9.26\r\nv 204.17 12.71 -1.15\r\nv 199.62 11.02 9.76\r\nv 207.65 17.34 0.28\r\nv 203.4 15.84 10.71\r\nv 196.27 9.83 21.58\r\nv 200.29 14.85 21.98\r\nv 198.55 14.26 33.94\r\nv 194.4 9.1 34.15\r\nv 194.27 8.76 47.27\r\nv 195.82 8.81 60.53\r\nv 198.44 14.02 46.42\r\nv 199.9 14.07 59.01\r\nv 198.98 9.25 73.46\r\nv 202.88 14.41 71.33\r\nv 207.07 14.93 82.89\r\nv 203.42 9.97 85.56\r\nv 208.8 10.86 96.32\r\nv 212.16 15.57 93.22\r\nv 217.91 16.24 102.15\r\nv 214.85 11.8 105.59\r\nv 221.29 12.67 113.19\r\nv 228.09 13.45 119.31\r\nv 224.03 16.85 109.51\r\nv 230.49 17.37 115.45\r\nv 235.17 14.11 124.14\r\nv 242.33 14.64 127.96\r\nv 237.22 17.79 120.15\r\nv 243.99 18.11 123.88\r\nv 249.34 15.03 131.07\r\nv 255.55 15.44 133.64\r\nv 250.6 18.33 126.92\r\nv 256.39 18.59 129.46\r\nv 260.32 15.97 135.84\r\nv 263.35 16.81 137.62\r\nv 260.74 19.02 131.68\r\nv 263.43 19.76 133.55\r\nv 258.74 40.98 -45.27\r\nv 261.94 42.42 -46.96\r\nv 261.49 46.08 -44.73\r\nv 258.22 44.96 -43.06\r\nv 263.73 43.96 -48.42\r\nv 263.39 47.29 -46.16\r\nv 262.54 49.04 -45.05\r\nv 260.94 48.96 -43.62\r\nv 257.55 48.17 -41.93\r\nv 248.84 38.97 -40.28\r\nv 254.15 39.92 -43.09\r\nv 253.72 44.08 -40.9\r\nv 248.59 43.25 -38.12\r\nv 253.07 47.46 -39.76\r\nv 248.04 46.71 -36.98\r\nv 238.03 36.65 -32.24\r\nv 243.44 37.91 -36.67\r\nv 243.43 42.28 -34.59\r\nv 238.28 41.09 -30.27\r\nv 243.02 45.79 -33.46\r\nv 238.01 44.64 -29.19\r\nv 228.03 41.48 -18.2\r\nv 228.04 37.86 -19.12\r\nv 233.14 39.59 -25.12\r\nv 233 43.17 -24.11\r\nv 227.36 33.32 -20.79\r\nv 232.66 35.1 -26.95\r\nv 218.46 37.87 -3.9\r\nv 218.25 34.18 -4.58\r\nv 223.02 36 -12.27\r\nv 223.12 39.65 -11.46\r\nv 217.18 29.54 -5.81\r\nv 222.14 31.41 -13.73\r\nv 210.74 35.18 13.51\r\nv 210.31 31.4 13.16\r\nv 213.92 32.6 3.92\r\nv 214.25 36.34 4.45\r\nv 208.91 26.64 12.52\r\nv 212.68 27.9 2.97\r\nv 206.79 34.24 33.48\r\nv 206.24 30.39 33.54\r\nv 207.69 30.71 23.08\r\nv 208.19 34.53 23.24\r\nv 204.66 25.51 33.63\r\nv 206.17 25.88 22.79\r\nv 208 34.19 54.83\r\nv 207.46 30.33 55.39\r\nv 206.18 30.29 44.42\r\nv 206.74 34.15 44.11\r\nv 205.9 25.42 56.33\r\nv 204.57 25.38 44.92\r\nv 214.24 34.37 75.28\r\nv 213.79 30.57 76.33\r\nv 210.08 30.43 66.16\r\nv 210.58 34.27 65.34\r\nv 212.42 25.8 78.05\r\nv 208.59 25.57 67.5\r\nv 223.85 34.58 92.25\r\nv 223.53 30.89 93.62\r\nv 218.36 30.73 85.55\r\nv 218.75 34.48 84.31\r\nv 222.4 26.32 95.88\r\nv 217.1 26.06 87.57\r\nv 233.95 26.75 108.43\r\nv 228.04 26.56 102.79\r\nv 229.03 31.03 100.37\r\nv 234.77 31.13 105.91\r\nv 229.3 34.67 98.92\r\nv 234.94 34.72 104.42\r\nv 246.07 26.95 116.54\r\nv 240.02 26.88 112.93\r\nv 240.61 31.18 110.36\r\nv 246.42 31.17 113.93\r\nv 240.66 34.73 108.86\r\nv 246.32 34.66 112.45\r\nv 257.1 26.96 122.01\r\nv 251.94 26.95 119.51\r\nv 252.03 31.07 116.87\r\nv 256.98 30.94 119.33\r\nv 251.78 34.49 115.38\r\nv 256.62 34.2 117.82\r\nv 262.26 33.1 121.73\r\nv 263.15 30.97 123.29\r\nv 260.79 30.8 121.46\r\nv 260.41 33.78 119.92\r\nv 263.42 27.49 126.15\r\nv 261.01 27.08 124.22\r\nv 259.9 27.43 -63.59\r\nv 263.05 29.58 -64.8\r\nv 242.15 23.42 -64.34\r\nv 240.16 24.27 -65.95\r\nv 233.76 21.61 -60.49\r\nv 231.37 22.35 -61.94\r\nv 225.34 19.7 -55.67\r\nv 222.54 20.35 -56.95\r\nv 217.16 17.6 -49.89\r\nv 214.02 18.17 -51.01\r\nv 209.49 15.19 -43.18\r\nv 202.31 12.44 -35.47\r\nv 198.94 13.02 -36.32\r\nv 206.15 15.75 -44.16\r\nv 195.59 9.33 -26.69\r\nv 189.35 5.82 -16.76\r\nv 186.46 6.44 -17.38\r\nv 192.4 9.93 -27.42\r\nv 183.61 1.89 -5.62\r\nv 178.74 -1.56 6.71\r\nv 176.51 -0.87 6.32\r\nv 181.06 2.55 -6.13\r\nv 175.09 -3.65 20.2\r\nv 173.1 -2.93 19.93\r\nv 173.01 -4.79 34.62\r\nv 171.14 -4.04 34.47\r\nv 172.82 -5.39 49.73\r\nv 174.52 -5.2 64.99\r\nv 172.57 -4.45 65.09\r\nv 170.95 -4.64 49.71\r\nv 178.07 -3.97 79.84\r\nv 183.16 -2.19 93.67\r\nv 180.99 -1.43 93.99\r\nv 176.01 -3.21 80.04\r\nv 189.46 -0.38 105.9\r\nv 196.61 1.6 116.62\r\nv 194.42 2.42 117.2\r\nv 187.26 0.41 106.35\r\nv 204.23 3.88 125.87\r\nv 212.24 6.13 133.5\r\nv 210.22 6.97 134.47\r\nv 202.12 4.72 126.63\r\nv 220.53 8.01 139.33\r\nv 229.1 9.58 143.9\r\nv 227.27 10.39 145.43\r\nv 218.59 8.85 140.58\r\nv 237.93 10.9 147.75\r\nv 246.53 11.95 150.71\r\nv 245.1 12.66 152.54\r\nv 236.27 11.68 149.49\r\nv 254.39 12.74 152.62\r\nv 260.02 13.54 153.61\r\nv 257.84 13.8 154.55\r\nv 253.26 13.32 154.37\r\nv 259 36.6 -48.26\r\nv 262.19 38.26 -49.95\r\nv 263.93 40.05 -51.36\r\nv 254.26 35.37 -46.05\r\nv 248.68 34.3 -43.16\r\nv 237.23 31.77 -34.84\r\nv 242.97 33.13 -39.43\r\nv 225.98 28.27 -22.98\r\nv 231.56 30.13 -29.37\r\nv 220.5 26.28 -15.67\r\nv 215.29 24.33 -7.43\r\nv 210.53 22.61 1.7\r\nv 206.55 21.25 11.66\r\nv 202.03 19.93 33.77\r\nv 203.64 20.4 22.4\r\nv 203.32 19.81 57.57\r\nv 201.94 19.75 45.61\r\nv 210.12 20.4 80.31\r\nv 206.14 20.04 69.28\r\nv 220.47 21.26 98.82\r\nv 214.98 20.82 90.22\r\nv 226.33 21.65 105.94\r\nv 232.48 21.99 111.72\r\nv 238.86 22.23 116.31\r\nv 245.25 22.4 119.96\r\nv 251.46 22.5 122.96\r\nv 256.9 22.64 125.48\r\nv 263.48 23.54 129.65\r\nv 260.99 22.94 127.71\r\nv 260.17 30.21 -72.17\r\nv 262.51 30.68 -70.91\r\nv 260.22 31.83 -72.31\r\nv 262.54 32.47 -71.18\r\nv 263.29 32.98 -68.98\r\nv 263.33 30.99 -68.52\r\nv 247.62 27.51 -69.7\r\nv 254.9 29.08 -71.65\r\nv 247.34 29.2 -69.92\r\nv 254.8 30.71 -71.82\r\nv 264.2 16.1 143.08\r\nv 263.67 15.08 147.4\r\nv 264.19 17.62 143.95\r\nv 263.65 16.52 147.9\r\nv 262.84 16.04 151.34\r\nv 262.86 14.7 151.22\r\nv 264.52 32.96 -62.45\r\nv 264.64 35.11 -59.19\r\nv 264.41 34.98 -63.28\r\nv 264.53 37.04 -60.22\r\nv 264.39 39.81 -57.16\r\nv 264.53 38.05 -55.94\r\nv 264.22 20.97 135.06\r\nv 264.31 18.1 138.98\r\nv 264.06 22.41 136.39\r\nv 264.23 19.62 140.12\r\nv 262.8 50.4 -45.66\r\nv 260.42 50.75 -43.98\r\nv 262.46 51.43 -46.92\r\nv 259.9 51.99 -45.07\r\nv 256.11 51.75 -43.12\r\nv 256.84 50.24 -42.18\r\nv 264.05 45.34 -49.9\r\nv 263.78 48.42 -47.58\r\nv 263.66 46.64 -51.38\r\nv 263.42 49.51 -49\r\nv 252.3 49.65 -39.94\r\nv 251.47 51.24 -40.78\r\nv 246.4 50.51 -37.88\r\nv 247.27 48.92 -37.11\r\nv 242.26 48.01 -33.56\r\nv 241.34 49.59 -34.27\r\nv 236.29 48.42 -29.91\r\nv 237.27 46.86 -29.26\r\nv 232.28 45.41 -24.16\r\nv 231.26 46.97 -24.75\r\nv 226.28 45.32 -18.78\r\nv 227.33 43.74 -18.25\r\nv 222.44 41.94 -11.5\r\nv 221.37 43.56 -11.97\r\nv 216.74 41.88 -4.34\r\nv 217.82 40.21 -3.94\r\nv 213.64 38.73 4.41\r\nv 212.57 40.44 4.09\r\nv 209.11 39.36 13.24\r\nv 210.17 37.61 13.47\r\nv 207.66 36.98 23.19\r\nv 206.62 38.75 23.05\r\nv 205.26 38.48 33.36\r\nv 206.29 36.7 33.42\r\nv 206.25 36.63 44.01\r\nv 205.23 38.41 44.02\r\nv 206.51 38.47 54.75\r\nv 207.51 36.67 54.68\r\nv 210.08 36.75 65.14\r\nv 209.08 38.56 65.25\r\nv 212.75 38.66 75.19\r\nv 213.73 36.84 75.03\r\nv 218.24 36.94 84.04\r\nv 217.29 38.77 84.24\r\nv 222.45 38.86 92.25\r\nv 223.36 37.03 91.97\r\nv 228.82 37.1 98.68\r\nv 227.98 38.93 99.05\r\nv 233.71 38.97 104.72\r\nv 234.49 37.14 104.25\r\nv 240.2 37.13 108.75\r\nv 239.48 38.96 109.32\r\nv 245.15 38.85 113.05\r\nv 245.84 37.04 112.39\r\nv 251.26 36.82 115.37\r\nv 250.6 38.6 116.1\r\nv 255.52 38.05 118.69\r\nv 256.11 36.4 117.86\r\nv 260 35.7 120.06\r\nv 259.57 37.08 121.04\r\nv 262.39 35.42 123.46\r\nv 262.66 34.29 122.27\r\nv 263.79 31.74 124.83\r\nv 263.56 32.81 126.22\r\nv 263.83 29.54 129.32\r\nv 264.07 28.4 127.8\r\nv 264.05 31.66 -65.63\r\nv 263.95 33.72 -66.28\r\nv 230.31 23.75 -62.54\r\nv 239.25 25.7 -66.64\r\nv 229.91 25.48 -62.72\r\nv 238.89 27.42 -66.86\r\nv 212.66 19.57 -51.46\r\nv 221.31 21.73 -57.47\r\nv 212.2 21.38 -51.57\r\nv 220.87 23.48 -57.62\r\nv 197.6 14.58 -36.63\r\nv 204.75 17.2 -44.54\r\nv 197.26 16.62 -36.68\r\nv 204.31 19.1 -44.61\r\nv 185.59 8.32 -17.62\r\nv 191.25 11.63 -27.69\r\nv 185.73 10.82 -17.66\r\nv 191.11 13.88 -27.72\r\nv 176.3 1.34 6.1\r\nv 180.53 4.6 -6.35\r\nv 177.09 4.32 5.96\r\nv 181 7.35 -6.42\r\nv 171.29 -1.61 34.18\r\nv 173.12 -0.59 19.68\r\nv 172.47 1.66 33.81\r\nv 174.16 2.56 19.45\r\nv 172.6 -1.94 64.64\r\nv 171.1 -2.14 49.34\r\nv 173.63 1.45 63.92\r\nv 172.26 1.22 48.81\r\nv 180.65 0.97 93.4\r\nv 175.86 -0.74 79.51\r\nv 181.22 4.18 92.36\r\nv 176.67 2.58 78.62\r\nv 193.8 4.62 116.62\r\nv 186.75 2.71 105.75\r\nv 193.96 7.53 115.47\r\nv 187.09 5.78 104.62\r\nv 209.51 8.95 134.09\r\nv 201.43 6.82 126.13\r\nv 209.45 11.49 133.03\r\nv 201.46 9.56 125\r\nv 226.56 12.02 145.4\r\nv 217.87 10.66 140.37\r\nv 226.41 14.06 144.59\r\nv 217.76 12.96 139.42\r\nv 244.52 13.92 152.8\r\nv 235.6 13.12 149.63\r\nv 244.36 15.44 152.26\r\nv 235.41 14.89 148.96\r\nv 259.16 14.62 155.02\r\nv 252.86 14.4 154.64\r\nv 259.23 15.83 154.65\r\nv 252.83 15.72 154.17\r\nv 261.92 14.64 153.82\r\nv 261.96 15.87 153.64\r\nv 264.32 41.67 -52.76\r\nv 264.04 43.21 -54.16\r\nv 264.18 24.61 131.27\r\nv 263.97 25.9 132.75\r\nvn -0.21 -0.96 -0.17\r\nvn -0.34 -0.93 -0.15\r\nvn -0.21 -0.92 -0.34\r\nvn -0.11 -0.92 -0.37\r\nvn -0.62 -0.77 -0.14\r\nvn -0.45 -0.78 -0.44\r\nvn -0.19 -0.73 -0.65\r\nvn -0.05 -0.71 -0.7\r\nvn 0.06 -0.72 -0.69\r\nvn -0.16 -0.98 -0.14\r\nvn -0.24 -0.97 0.04\r\nvn -0.31 -0.95 0.01\r\nvn -0.15 -0.98 -0.12\r\nvn -0.23 -0.97 0.05\r\nvn -0.34 -0.9 0.28\r\nvn -0.34 -0.9 0.28\r\nvn -0.42 -0.87 0.24\r\nvn -0.15 -0.98 -0.12\r\nvn -0.25 -0.97 0.03\r\nvn -0.16 -0.98 -0.14\r\nvn -0.27 -0.96 0\r\nvn -0.4 -0.89 0.2\r\nvn -0.37 -0.9 0.24\r\nvn -0.18 -0.97 -0.16\r\nvn -0.29 -0.96 -0.03\r\nvn -0.19 -0.97 -0.18\r\nvn -0.31 -0.95 -0.05\r\nvn -0.46 -0.88 0.12\r\nvn -0.43 -0.89 0.16\r\nvn -0.33 -0.94 -0.06\r\nvn -0.49 -0.87 0.11\r\nvn -0.21 -0.96 -0.18\r\nvn -0.24 -0.95 -0.18\r\nvn -0.37 -0.93 -0.05\r\nvn -0.52 -0.85 0.1\r\nvn -0.44 -0.9 -0.01\r\nvn -0.56 -0.82 0.11\r\nvn -0.3 -0.94 -0.14\r\nvn -0.35 -0.94 -0.07\r\nvn -0.49 -0.87 0.02\r\nvn -0.59 -0.8 0.1\r\nvn -0.37 -0.93 -0.02\r\nvn -0.52 -0.86 0.03\r\nvn -0.38 -0.93 -0.02\r\nvn -0.52 -0.85 -0\r\nvn -0.62 -0.78 0.01\r\nvn -0.61 -0.79 0.07\r\nvn -0.53 -0.85 -0.04\r\nvn -0.62 -0.78 -0.04\r\nvn -0.38 -0.92 -0.03\r\nvn -0.38 -0.93 -0.03\r\nvn -0.52 -0.85 -0.07\r\nvn -0.61 -0.78 -0.1\r\nvn -0.49 -0.87 -0.08\r\nvn -0.59 -0.8 -0.14\r\nvn -0.35 -0.94 -0.02\r\nvn -0.32 -0.95 -0.02\r\nvn -0.45 -0.89 -0.11\r\nvn -0.55 -0.82 -0.18\r\nvn -0.42 -0.9 -0.13\r\nvn -0.52 -0.83 -0.21\r\nvn -0.3 -0.95 -0.03\r\nvn -0.27 -0.96 -0.01\r\nvn -0.39 -0.91 -0.14\r\nvn -0.47 -0.85 -0.25\r\nvn -0.34 -0.93 -0.13\r\nvn -0.41 -0.87 -0.27\r\nvn -0.24 -0.97 0.01\r\nvn -0.2 -0.98 0.03\r\nvn -0.27 -0.95 -0.12\r\nvn -0.34 -0.9 -0.28\r\nvn -0.22 -0.97 -0.12\r\nvn -0.27 -0.92 -0.29\r\nvn -0.16 -0.99 0.03\r\nvn -0.13 -0.99 0.04\r\nvn -0.17 -0.98 -0.11\r\nvn -0.22 -0.93 -0.29\r\nvn -0.14 -0.99 -0.09\r\nvn -0.19 -0.94 -0.27\r\nvn -0.11 -0.99 0.05\r\nvn -0.1 -0.99 0.05\r\nvn -0.13 -0.99 -0.07\r\nvn -0.19 -0.95 -0.25\r\nvn -0.18 -0.98 -0.06\r\nvn -0.27 -0.93 -0.24\r\nvn -0.13 -0.99 0.05\r\nvn -0.36 -0.93 0.09\r\nvn -0.45 -0.89 -0.03\r\nvn -0.57 -0.8 -0.19\r\nvn -0.84 -0.52 0.14\r\nvn -0.6 -0.77 0.21\r\nvn -0.67 -0.62 0.4\r\nvn -0.88 -0.39 0.27\r\nvn -0.5 -0.73 0.47\r\nvn -0.52 -0.6 0.6\r\nvn -0.69 -0.51 0.51\r\nvn -0.9 -0.3 0.33\r\nvn -0.43 -0.76 0.49\r\nvn -0.41 -0.76 0.5\r\nvn -0.47 -0.62 0.62\r\nvn -0.45 -0.63 0.64\r\nvn -0.48 -0.75 0.45\r\nvn -0.53 -0.75 0.4\r\nvn -0.6 -0.6 0.53\r\nvn -0.54 -0.61 0.58\r\nvn -0.57 -0.74 0.35\r\nvn -0.61 -0.73 0.3\r\nvn -0.69 -0.59 0.42\r\nvn -0.65 -0.59 0.47\r\nvn -0.66 -0.72 0.24\r\nvn -0.63 -0.73 0.27\r\nvn -0.74 -0.59 0.32\r\nvn -0.72 -0.59 0.37\r\nvn -0.7 -0.7 0.16\r\nvn -0.68 -0.71 0.2\r\nvn -0.78 -0.59 0.21\r\nvn -0.76 -0.59 0.27\r\nvn -0.71 -0.69 0.1\r\nvn -0.72 -0.69 0.03\r\nvn -0.81 -0.59 0.04\r\nvn -0.8 -0.59 0.13\r\nvn -0.71 -0.69 -0.12\r\nvn -0.72 -0.69 -0.05\r\nvn -0.79 -0.59 -0.14\r\nvn -0.81 -0.59 -0.05\r\nvn -0.69 -0.7 -0.18\r\nvn -0.65 -0.72 -0.24\r\nvn -0.73 -0.63 -0.28\r\nvn -0.77 -0.61 -0.21\r\nvn -0.61 -0.74 -0.29\r\nvn -0.55 -0.76 -0.34\r\nvn -0.61 -0.67 -0.41\r\nvn -0.68 -0.65 -0.35\r\nvn -0.41 -0.81 -0.43\r\nvn -0.48 -0.79 -0.39\r\nvn -0.46 -0.72 -0.53\r\nvn -0.54 -0.7 -0.47\r\nvn -0.27 -0.84 -0.47\r\nvn -0.33 -0.83 -0.45\r\nvn -0.32 -0.74 -0.59\r\nvn -0.38 -0.73 -0.57\r\nvn -0.27 -0.85 -0.45\r\nvn -0.24 -0.85 -0.46\r\nvn -0.32 -0.74 -0.59\r\nvn -0.29 -0.74 -0.6\r\nvn -0.69 -0.64 -0.34\r\nvn -0.37 -0.82 -0.43\r\nvn -0.76 -0.51 -0.41\r\nvn -0.45 -0.7 -0.56\r\nvn -0.5 -0.42 0.75\r\nvn -0.48 -0.29 0.83\r\nvn -0.59 -0.28 0.76\r\nvn -0.65 -0.38 0.66\r\nvn -0.43 0.01 0.9\r\nvn -0.54 0.07 0.84\r\nvn -0.72 0.04 0.69\r\nvn -0.85 -0.1 0.52\r\nvn -0.9 -0.21 0.39\r\nvn -0.51 -0.41 0.75\r\nvn -0.53 -0.28 0.81\r\nvn -0.47 -0.29 0.84\r\nvn -0.46 -0.43 0.78\r\nvn -0.52 0.02 0.86\r\nvn -0.45 0.01 0.89\r\nvn -0.66 -0.39 0.64\r\nvn -0.68 -0.25 0.69\r\nvn -0.61 -0.26 0.75\r\nvn -0.59 -0.4 0.7\r\nvn -0.67 0.04 0.75\r\nvn -0.6 0.03 0.8\r\nvn -0.77 0.05 0.63\r\nvn -0.72 0.05 0.69\r\nvn -0.74 -0.24 0.63\r\nvn -0.79 -0.24 0.57\r\nvn -0.72 -0.38 0.58\r\nvn -0.77 -0.38 0.52\r\nvn -0.86 0.05 0.51\r\nvn -0.82 0.05 0.58\r\nvn -0.83 -0.23 0.51\r\nvn -0.86 -0.23 0.45\r\nvn -0.8 -0.37 0.47\r\nvn -0.83 -0.37 0.41\r\nvn -0.95 0.04 0.32\r\nvn -0.91 0.05 0.42\r\nvn -0.9 -0.23 0.38\r\nvn -0.93 -0.23 0.29\r\nvn -0.86 -0.37 0.34\r\nvn -0.89 -0.37 0.26\r\nvn -1 0.03 0.07\r\nvn -0.98 0.04 0.2\r\nvn -0.96 -0.23 0.19\r\nvn -0.97 -0.23 0.07\r\nvn -0.91 -0.37 0.17\r\nvn -0.92 -0.38 0.06\r\nvn -0.98 0.01 -0.18\r\nvn -1 0.02 -0.06\r\nvn -0.97 -0.24 -0.06\r\nvn -0.95 -0.25 -0.17\r\nvn -0.92 -0.38 -0.05\r\nvn -0.9 -0.39 -0.16\r\nvn -0.91 -0.03 -0.41\r\nvn -0.95 -0.01 -0.3\r\nvn -0.92 -0.27 -0.28\r\nvn -0.87 -0.3 -0.38\r\nvn -0.87 -0.41 -0.26\r\nvn -0.83 -0.44 -0.35\r\nvn -0.8 -0.09 -0.59\r\nvn -0.86 -0.06 -0.5\r\nvn -0.82 -0.33 -0.47\r\nvn -0.75 -0.37 -0.55\r\nvn -0.77 -0.47 -0.43\r\nvn -0.7 -0.5 -0.51\r\nvn -0.54 -0.54 -0.64\r\nvn -0.59 -0.41 -0.7\r\nvn -0.67 -0.39 -0.63\r\nvn -0.63 -0.52 -0.58\r\nvn -0.64 -0.13 -0.76\r\nvn -0.72 -0.12 -0.68\r\nvn -0.39 -0.55 -0.73\r\nvn -0.44 -0.42 -0.79\r\nvn -0.5 -0.42 -0.75\r\nvn -0.46 -0.55 -0.7\r\nvn -0.49 -0.13 -0.86\r\nvn -0.56 -0.13 -0.82\r\nvn -0.39 -0.55 -0.74\r\nvn -0.4 -0.43 -0.81\r\nvn -0.39 -0.42 -0.82\r\nvn -0.36 -0.55 -0.75\r\nvn -0.45 -0.11 -0.89\r\nvn -0.44 -0.12 -0.89\r\nvn -0.72 -0.1 -0.69\r\nvn -0.58 -0.07 -0.81\r\nvn -0.49 -0.43 -0.76\r\nvn -0.79 -0.24 -0.56\r\nvn -0.5 -0.53 -0.69\r\nvn -0.8 -0.37 -0.48\r\nvn -0.46 -0.89 0.01\r\nvn -0.74 -0.67 -0.01\r\nvn 0.11 -0.73 -0.67\r\nvn -0.06 -0.93 -0.35\r\nvn 0.15 -0.75 -0.64\r\nvn -0.05 -0.95 -0.32\r\nvn 0.19 -0.78 -0.6\r\nvn -0.04 -0.95 -0.3\r\nvn 0.22 -0.79 -0.57\r\nvn -0.04 -0.95 -0.29\r\nvn -0.04 -0.95 -0.3\r\nvn 0.26 -0.79 -0.55\r\nvn 0.31 -0.79 -0.54\r\nvn -0.03 -0.95 -0.31\r\nvn -0.03 -0.95 -0.32\r\nvn 0.38 -0.76 -0.52\r\nvn 0.47 -0.72 -0.51\r\nvn -0.03 -0.95 -0.32\r\nvn -0.02 -0.96 -0.29\r\nvn 0.58 -0.68 -0.45\r\nvn 0.7 -0.63 -0.34\r\nvn -0 -0.98 -0.21\r\nvn 0.79 -0.58 -0.22\r\nvn 0.03 -0.99 -0.12\r\nvn 0.84 -0.53 -0.09\r\nvn 0.05 -1 -0.06\r\nvn 0.06 -1 -0.01\r\nvn 0.85 -0.53 0.04\r\nvn 0.82 -0.55 0.17\r\nvn 0.05 -1 0.06\r\nvn 0.04 -0.99 0.12\r\nvn 0.78 -0.56 0.29\r\nvn 0.72 -0.57 0.4\r\nvn 0.03 -0.99 0.15\r\nvn 0.03 -0.98 0.18\r\nvn 0.66 -0.57 0.49\r\nvn 0.6 -0.56 0.57\r\nvn 0.02 -0.97 0.23\r\nvn 0.01 -0.96 0.27\r\nvn 0.53 -0.55 0.65\r\nvn 0.45 -0.54 0.71\r\nvn -0 -0.95 0.3\r\nvn -0.02 -0.96 0.29\r\nvn 0.35 -0.56 0.75\r\nvn 0.27 -0.6 0.75\r\nvn -0.03 -0.96 0.28\r\nvn -0.03 -0.97 0.26\r\nvn 0.2 -0.65 0.74\r\nvn 0.13 -0.68 0.73\r\nvn -0.04 -0.97 0.24\r\nvn -0.07 -0.97 0.22\r\nvn 0.03 -0.67 0.74\r\nvn -0.07 -0.74 0.67\r\nvn -0.26 -0.87 0.43\r\nvn -0.68 -0.43 0.59\r\nvn -0.52 -0.51 0.69\r\nvn -0.9 -0.25 0.35\r\nvn -0.5 -0.52 0.7\r\nvn -0.46 -0.52 0.72\r\nvn -0.57 -0.5 0.65\r\nvn -0.64 -0.49 0.6\r\nvn -0.69 -0.48 0.54\r\nvn -0.74 -0.48 0.48\r\nvn -0.8 -0.48 0.37\r\nvn -0.77 -0.48 0.42\r\nvn -0.84 -0.48 0.24\r\nvn -0.82 -0.48 0.31\r\nvn -0.86 -0.48 0.15\r\nvn -0.87 -0.48 0.05\r\nvn -0.87 -0.49 -0.05\r\nvn -0.85 -0.5 -0.15\r\nvn -0.82 -0.51 -0.24\r\nvn -0.78 -0.54 -0.32\r\nvn -0.73 -0.56 -0.39\r\nvn -0.66 -0.59 -0.46\r\nvn -0.5 -0.63 -0.59\r\nvn -0.59 -0.61 -0.53\r\nvn -0.36 -0.65 -0.67\r\nvn -0.42 -0.64 -0.64\r\nvn -0.36 -0.64 -0.68\r\nvn -0.33 -0.65 -0.69\r\nvn -0.49 -0.6 -0.63\r\nvn -0.79 -0.43 -0.44\r\nvn -0.74 -0.38 -0.55\r\nvn -0.26 -0.4 -0.88\r\nvn -0.92 -0.33 -0.22\r\nvn -0.96 -0.04 -0.28\r\nvn -0.78 -0.11 -0.62\r\nvn -0.23 -0.09 -0.97\r\nvn 0.11 -0.3 -0.95\r\nvn 0.21 -0.35 -0.91\r\nvn 0.14 -0.11 -0.98\r\nvn 0.25 -0.17 -0.95\r\nvn -0.89 -0.45 0.08\r\nvn -0.93 -0.35 -0.04\r\nvn -0.85 -0.48 0.22\r\nvn -0.95 -0.01 0.3\r\nvn -0.98 -0.05 0.18\r\nvn -1 -0.04 0.07\r\nvn -0.99 -0.09 0.07\r\nvn -0.99 -0.16 -0.01\r\nvn -0.99 -0.04 0.09\r\nvn -1 0.08 0\r\nvn -1 0.04 -0.03\r\nvn -0.99 0.01 -0.11\r\nvn -0.97 -0.21 -0.12\r\nvn -0.99 -0.12 -0.12\r\nvn -1 0.04 0.01\r\nvn -1 0.08 0.03\r\nvn -0.42 0.52 0.74\r\nvn -0.77 0.42 0.48\r\nvn -0.35 0.43 0.83\r\nvn -0.31 0.61 0.73\r\nvn -0.4 0.69 0.6\r\nvn -0.76 0.59 0.27\r\nvn -0.98 0.16 0.13\r\nvn -1 0.06 0.03\r\nvn -0.96 0.29 -0.03\r\nvn -0.98 0.18 -0.11\r\nvn -0.38 0.41 0.83\r\nvn -0.43 0.4 0.81\r\nvn -0.37 0.56 0.74\r\nvn -0.33 0.57 0.75\r\nvn -0.5 0.41 0.76\r\nvn -0.55 0.42 0.72\r\nvn -0.47 0.58 0.67\r\nvn -0.42 0.57 0.7\r\nvn -0.6 0.42 0.68\r\nvn -0.65 0.42 0.63\r\nvn -0.56 0.58 0.6\r\nvn -0.51 0.58 0.63\r\nvn -0.7 0.42 0.58\r\nvn -0.76 0.41 0.51\r\nvn -0.67 0.56 0.49\r\nvn -0.61 0.57 0.55\r\nvn -0.81 0.4 0.43\r\nvn -0.87 0.38 0.32\r\nvn -0.79 0.53 0.3\r\nvn -0.73 0.55 0.41\r\nvn -0.91 0.37 0.2\r\nvn -0.93 0.37 0.07\r\nvn -0.86 0.51 0.07\r\nvn -0.83 0.52 0.19\r\nvn -0.93 0.36 -0.05\r\nvn -0.92 0.35 -0.17\r\nvn -0.85 0.5 -0.16\r\nvn -0.86 0.5 -0.05\r\nvn -0.9 0.34 -0.28\r\nvn -0.86 0.32 -0.39\r\nvn -0.8 0.48 -0.36\r\nvn -0.83 0.49 -0.26\r\nvn -0.82 0.3 -0.48\r\nvn -0.77 0.28 -0.58\r\nvn -0.72 0.45 -0.53\r\nvn -0.77 0.46 -0.45\r\nvn -0.7 0.26 -0.66\r\nvn -0.63 0.26 -0.73\r\nvn -0.59 0.43 -0.68\r\nvn -0.66 0.44 -0.61\r\nvn -0.55 0.26 -0.79\r\nvn -0.49 0.28 -0.83\r\nvn -0.47 0.46 -0.76\r\nvn -0.52 0.44 -0.73\r\nvn -0.45 0.3 -0.84\r\nvn -0.47 0.34 -0.81\r\nvn -0.47 0.53 -0.71\r\nvn -0.44 0.48 -0.76\r\nvn -0.59 0.41 -0.7\r\nvn -0.84 0.28 -0.46\r\nvn -0.84 0.48 -0.26\r\nvn -0.59 0.58 -0.55\r\nvn -0.98 0.07 -0.18\r\nvn -0.99 -0.04 -0.11\r\nvn -0.99 0.12 0.07\r\nvn -0.97 0.23 -0.01\r\nvn -0.96 -0.25 -0.12\r\nvn -0.98 -0.02 -0.2\r\nvn 0.28 -0.37 -0.89\r\nvn 0.35 -0.38 -0.86\r\nvn 0.33 -0.19 -0.92\r\nvn 0.4 -0.19 -0.89\r\nvn 0.41 -0.39 -0.82\r\nvn 0.48 -0.41 -0.77\r\nvn 0.48 -0.19 -0.86\r\nvn 0.56 -0.2 -0.8\r\nvn 0.56 -0.4 -0.72\r\nvn 0.66 -0.37 -0.66\r\nvn 0.65 -0.18 -0.74\r\nvn 0.75 -0.14 -0.65\r\nvn 0.75 -0.3 -0.58\r\nvn 0.85 -0.19 -0.5\r\nvn 0.83 -0.06 -0.55\r\nvn 0.9 0.05 -0.44\r\nvn 0.92 -0.06 -0.39\r\nvn 0.96 0.06 -0.27\r\nvn 0.93 0.16 -0.32\r\nvn 0.94 0.25 -0.22\r\nvn 0.98 0.15 -0.16\r\nvn 0.98 0.2 -0.06\r\nvn 0.94 0.31 -0.14\r\nvn 0.94 0.34 -0.05\r\nvn 0.98 0.2 0.04\r\nvn 0.97 0.18 0.15\r\nvn 0.94 0.34 0.04\r\nvn 0.94 0.32 0.13\r\nvn 0.95 0.15 0.26\r\nvn 0.92 0.12 0.38\r\nvn 0.93 0.29 0.22\r\nvn 0.9 0.27 0.34\r\nvn 0.87 0.1 0.49\r\nvn 0.81 0.09 0.58\r\nvn 0.86 0.26 0.44\r\nvn 0.81 0.26 0.53\r\nvn 0.74 0.09 0.66\r\nvn 0.65 0.1 0.75\r\nvn 0.75 0.26 0.61\r\nvn 0.66 0.28 0.7\r\nvn 0.55 0.09 0.83\r\nvn 0.46 0.06 0.89\r\nvn 0.56 0.29 0.77\r\nvn 0.47 0.29 0.83\r\nvn 0.37 0.02 0.93\r\nvn 0.27 -0 0.96\r\nvn 0.39 0.29 0.88\r\nvn 0.28 0.29 0.91\r\nvn 0.14 0.02 0.99\r\nvn -0.2 -0.23 0.95\r\nvn 0.15 0.33 0.93\r\nvn -0.14 0.29 0.95\r\nvn -0.67 -0.4 0.62\r\nvn -0.72 0.07 0.68\r\nvn -1 0.02 0.06\r\nvn -0.99 0.15 -0.04\r\nvn -0.99 -0.08 -0.1\r\nvn -0.99 0.1 0.06\r\nvn 0.21 -0.96 -0.17\r\nvn 0.11 -0.92 -0.37\r\nvn 0.21 -0.92 -0.34\r\nvn 0.34 -0.93 -0.15\r\nvn -0.06 -0.72 -0.69\r\nvn 0.05 -0.71 -0.7\r\nvn 0.19 -0.73 -0.65\r\nvn 0.45 -0.78 -0.44\r\nvn 0.62 -0.77 -0.14\r\nvn 0.31 -0.95 0.01\r\nvn 0.24 -0.97 0.04\r\nvn 0.16 -0.98 -0.14\r\nvn 0.42 -0.87 0.24\r\nvn 0.34 -0.9 0.28\r\nvn 0.34 -0.9 0.28\r\nvn 0.23 -0.97 0.05\r\nvn 0.15 -0.98 -0.12\r\nvn 0.25 -0.97 0.03\r\nvn 0.15 -0.98 -0.12\r\nvn 0.37 -0.9 0.24\r\nvn 0.4 -0.89 0.2\r\nvn 0.27 -0.96 0\r\nvn 0.16 -0.98 -0.14\r\nvn 0.29 -0.96 -0.03\r\nvn 0.18 -0.97 -0.16\r\nvn 0.43 -0.89 0.16\r\nvn 0.46 -0.88 0.12\r\nvn 0.31 -0.95 -0.05\r\nvn 0.19 -0.97 -0.18\r\nvn 0.49 -0.87 0.11\r\nvn 0.33 -0.94 -0.06\r\nvn 0.52 -0.85 0.1\r\nvn 0.37 -0.93 -0.05\r\nvn 0.24 -0.95 -0.18\r\nvn 0.21 -0.96 -0.18\r\nvn 0.56 -0.82 0.11\r\nvn 0.44 -0.9 -0.01\r\nvn 0.59 -0.8 0.1\r\nvn 0.49 -0.87 0.02\r\nvn 0.35 -0.94 -0.07\r\nvn 0.3 -0.94 -0.14\r\nvn 0.52 -0.86 0.03\r\nvn 0.37 -0.93 -0.02\r\nvn 0.61 -0.79 0.07\r\nvn 0.62 -0.78 0.01\r\nvn 0.52 -0.85 -0\r\nvn 0.38 -0.93 -0.02\r\nvn 0.62 -0.78 -0.04\r\nvn 0.53 -0.85 -0.04\r\nvn 0.61 -0.78 -0.1\r\nvn 0.52 -0.85 -0.07\r\nvn 0.38 -0.93 -0.03\r\nvn 0.38 -0.92 -0.03\r\nvn 0.59 -0.8 -0.14\r\nvn 0.49 -0.87 -0.08\r\nvn 0.55 -0.82 -0.18\r\nvn 0.45 -0.89 -0.11\r\nvn 0.32 -0.95 -0.02\r\nvn 0.35 -0.94 -0.02\r\nvn 0.52 -0.83 -0.21\r\nvn 0.42 -0.9 -0.13\r\nvn 0.47 -0.85 -0.25\r\nvn 0.39 -0.91 -0.14\r\nvn 0.27 -0.96 -0.01\r\nvn 0.3 -0.95 -0.03\r\nvn 0.41 -0.87 -0.27\r\nvn 0.34 -0.93 -0.13\r\nvn 0.34 -0.9 -0.28\r\nvn 0.27 -0.95 -0.12\r\nvn 0.2 -0.98 0.03\r\nvn 0.24 -0.97 0.01\r\nvn 0.27 -0.92 -0.29\r\nvn 0.22 -0.97 -0.12\r\nvn 0.22 -0.93 -0.29\r\nvn 0.17 -0.98 -0.11\r\nvn 0.13 -0.99 0.04\r\nvn 0.16 -0.99 0.03\r\nvn 0.19 -0.94 -0.27\r\nvn 0.14 -0.99 -0.09\r\nvn 0.19 -0.95 -0.25\r\nvn 0.13 -0.99 -0.07\r\nvn 0.1 -0.99 0.05\r\nvn 0.11 -0.99 0.05\r\nvn 0.27 -0.93 -0.24\r\nvn 0.18 -0.98 -0.06\r\nvn 0.57 -0.8 -0.19\r\nvn 0.45 -0.89 -0.03\r\nvn 0.36 -0.93 0.09\r\nvn 0.13 -0.99 0.05\r\nvn 0.84 -0.52 0.14\r\nvn 0.88 -0.39 0.27\r\nvn 0.67 -0.62 0.4\r\nvn 0.6 -0.77 0.21\r\nvn 0.9 -0.3 0.33\r\nvn 0.69 -0.51 0.51\r\nvn 0.52 -0.6 0.6\r\nvn 0.5 -0.73 0.47\r\nvn 0.41 -0.76 0.5\r\nvn 0.43 -0.76 0.49\r\nvn 0.45 -0.63 0.64\r\nvn 0.47 -0.62 0.62\r\nvn 0.48 -0.75 0.45\r\nvn 0.54 -0.61 0.58\r\nvn 0.6 -0.6 0.53\r\nvn 0.53 -0.75 0.4\r\nvn 0.57 -0.74 0.35\r\nvn 0.65 -0.59 0.47\r\nvn 0.69 -0.59 0.42\r\nvn 0.61 -0.73 0.3\r\nvn 0.63 -0.73 0.27\r\nvn 0.66 -0.72 0.24\r\nvn 0.72 -0.59 0.37\r\nvn 0.74 -0.59 0.32\r\nvn 0.68 -0.71 0.2\r\nvn 0.7 -0.7 0.16\r\nvn 0.76 -0.59 0.27\r\nvn 0.78 -0.59 0.21\r\nvn 0.71 -0.69 0.1\r\nvn 0.8 -0.59 0.13\r\nvn 0.81 -0.59 0.04\r\nvn 0.72 -0.69 0.03\r\nvn 0.72 -0.69 -0.05\r\nvn 0.71 -0.69 -0.12\r\nvn 0.81 -0.59 -0.05\r\nvn 0.79 -0.59 -0.14\r\nvn 0.69 -0.7 -0.18\r\nvn 0.77 -0.61 -0.21\r\nvn 0.73 -0.63 -0.28\r\nvn 0.65 -0.72 -0.24\r\nvn 0.61 -0.74 -0.29\r\nvn 0.68 -0.65 -0.35\r\nvn 0.61 -0.67 -0.41\r\nvn 0.55 -0.76 -0.34\r\nvn 0.48 -0.79 -0.39\r\nvn 0.41 -0.81 -0.43\r\nvn 0.54 -0.7 -0.47\r\nvn 0.46 -0.72 -0.53\r\nvn 0.33 -0.83 -0.45\r\nvn 0.27 -0.84 -0.47\r\nvn 0.38 -0.73 -0.57\r\nvn 0.32 -0.74 -0.59\r\nvn 0.24 -0.85 -0.46\r\nvn 0.27 -0.85 -0.45\r\nvn 0.29 -0.74 -0.6\r\nvn 0.32 -0.74 -0.59\r\nvn 0.37 -0.82 -0.43\r\nvn 0.69 -0.64 -0.34\r\nvn 0.45 -0.7 -0.56\r\nvn 0.76 -0.51 -0.41\r\nvn 0.5 -0.42 0.75\r\nvn 0.65 -0.38 0.66\r\nvn 0.59 -0.28 0.76\r\nvn 0.48 -0.29 0.83\r\nvn 0.9 -0.21 0.39\r\nvn 0.85 -0.1 0.52\r\nvn 0.72 0.04 0.69\r\nvn 0.54 0.07 0.84\r\nvn 0.43 0.01 0.9\r\nvn 0.51 -0.41 0.75\r\nvn 0.46 -0.43 0.78\r\nvn 0.47 -0.29 0.84\r\nvn 0.53 -0.28 0.81\r\nvn 0.45 0.01 0.89\r\nvn 0.52 0.02 0.86\r\nvn 0.66 -0.39 0.64\r\nvn 0.59 -0.4 0.7\r\nvn 0.61 -0.26 0.75\r\nvn 0.68 -0.25 0.69\r\nvn 0.6 0.03 0.8\r\nvn 0.67 0.04 0.75\r\nvn 0.77 0.05 0.63\r\nvn 0.79 -0.24 0.57\r\nvn 0.74 -0.24 0.63\r\nvn 0.72 0.05 0.69\r\nvn 0.77 -0.38 0.52\r\nvn 0.72 -0.38 0.58\r\nvn 0.86 0.05 0.51\r\nvn 0.86 -0.23 0.45\r\nvn 0.83 -0.23 0.51\r\nvn 0.82 0.05 0.58\r\nvn 0.83 -0.37 0.41\r\nvn 0.8 -0.37 0.47\r\nvn 0.95 0.04 0.32\r\nvn 0.93 -0.23 0.29\r\nvn 0.9 -0.23 0.38\r\nvn 0.91 0.05 0.42\r\nvn 0.89 -0.37 0.26\r\nvn 0.86 -0.37 0.34\r\nvn 1 0.03 0.07\r\nvn 0.97 -0.23 0.07\r\nvn 0.96 -0.23 0.19\r\nvn 0.98 0.04 0.2\r\nvn 0.92 -0.38 0.06\r\nvn 0.91 -0.37 0.17\r\nvn 0.98 0.01 -0.18\r\nvn 0.95 -0.25 -0.17\r\nvn 0.97 -0.24 -0.06\r\nvn 1 0.02 -0.06\r\nvn 0.9 -0.39 -0.16\r\nvn 0.92 -0.38 -0.05\r\nvn 0.91 -0.03 -0.41\r\nvn 0.87 -0.3 -0.38\r\nvn 0.92 -0.27 -0.28\r\nvn 0.95 -0.01 -0.3\r\nvn 0.83 -0.44 -0.35\r\nvn 0.87 -0.41 -0.26\r\nvn 0.8 -0.09 -0.59\r\nvn 0.75 -0.37 -0.55\r\nvn 0.82 -0.33 -0.47\r\nvn 0.86 -0.06 -0.5\r\nvn 0.7 -0.5 -0.51\r\nvn 0.77 -0.47 -0.43\r\nvn 0.54 -0.54 -0.64\r\nvn 0.63 -0.52 -0.58\r\nvn 0.67 -0.39 -0.63\r\nvn 0.59 -0.41 -0.7\r\nvn 0.72 -0.12 -0.68\r\nvn 0.64 -0.13 -0.76\r\nvn 0.39 -0.55 -0.73\r\nvn 0.46 -0.55 -0.7\r\nvn 0.5 -0.42 -0.75\r\nvn 0.44 -0.42 -0.79\r\nvn 0.56 -0.13 -0.82\r\nvn 0.49 -0.13 -0.86\r\nvn 0.39 -0.55 -0.74\r\nvn 0.36 -0.55 -0.75\r\nvn 0.39 -0.42 -0.82\r\nvn 0.4 -0.43 -0.81\r\nvn 0.44 -0.12 -0.89\r\nvn 0.45 -0.11 -0.89\r\nvn 0.72 -0.1 -0.69\r\nvn 0.79 -0.24 -0.56\r\nvn 0.49 -0.43 -0.76\r\nvn 0.58 -0.07 -0.81\r\nvn 0.8 -0.37 -0.48\r\nvn 0.5 -0.53 -0.69\r\nvn 0.46 -0.89 0.01\r\nvn 0.74 -0.67 -0.01\r\nvn 0.06 -0.93 -0.35\r\nvn -0.11 -0.73 -0.67\r\nvn 0.05 -0.95 -0.32\r\nvn -0.15 -0.75 -0.64\r\nvn 0.04 -0.95 -0.3\r\nvn -0.19 -0.78 -0.6\r\nvn 0.04 -0.95 -0.29\r\nvn -0.22 -0.79 -0.57\r\nvn 0.04 -0.95 -0.3\r\nvn 0.03 -0.95 -0.31\r\nvn -0.31 -0.79 -0.54\r\nvn -0.26 -0.79 -0.55\r\nvn 0.03 -0.95 -0.32\r\nvn 0.03 -0.95 -0.32\r\nvn -0.47 -0.72 -0.51\r\nvn -0.38 -0.76 -0.52\r\nvn 0.02 -0.96 -0.29\r\nvn 0 -0.98 -0.21\r\nvn -0.7 -0.63 -0.34\r\nvn -0.58 -0.68 -0.45\r\nvn -0.03 -0.99 -0.12\r\nvn -0.79 -0.58 -0.22\r\nvn -0.05 -1 -0.06\r\nvn -0.84 -0.53 -0.09\r\nvn -0.06 -1 -0.01\r\nvn -0.05 -1 0.06\r\nvn -0.82 -0.55 0.17\r\nvn -0.85 -0.53 0.04\r\nvn -0.04 -0.99 0.12\r\nvn -0.03 -0.99 0.15\r\nvn -0.72 -0.57 0.4\r\nvn -0.78 -0.56 0.29\r\nvn -0.03 -0.98 0.18\r\nvn -0.02 -0.97 0.23\r\nvn -0.6 -0.56 0.57\r\nvn -0.66 -0.57 0.49\r\nvn -0.01 -0.96 0.27\r\nvn 0 -0.95 0.3\r\nvn -0.45 -0.54 0.71\r\nvn -0.53 -0.55 0.65\r\nvn 0.02 -0.96 0.29\r\nvn 0.03 -0.96 0.28\r\nvn -0.27 -0.6 0.75\r\nvn -0.35 -0.56 0.75\r\nvn 0.03 -0.97 0.26\r\nvn 0.04 -0.97 0.24\r\nvn -0.13 -0.68 0.73\r\nvn -0.2 -0.65 0.74\r\nvn 0.07 -0.97 0.22\r\nvn 0.26 -0.87 0.43\r\nvn 0.07 -0.74 0.67\r\nvn -0.03 -0.67 0.74\r\nvn 0.52 -0.51 0.69\r\nvn 0.68 -0.43 0.59\r\nvn 0.9 -0.25 0.35\r\nvn 0.46 -0.52 0.72\r\nvn 0.5 -0.52 0.7\r\nvn 0.64 -0.49 0.6\r\nvn 0.57 -0.5 0.65\r\nvn 0.74 -0.48 0.48\r\nvn 0.69 -0.48 0.54\r\nvn 0.77 -0.48 0.42\r\nvn 0.8 -0.48 0.37\r\nvn 0.82 -0.48 0.31\r\nvn 0.84 -0.48 0.24\r\nvn 0.87 -0.48 0.05\r\nvn 0.86 -0.48 0.15\r\nvn 0.85 -0.5 -0.15\r\nvn 0.87 -0.49 -0.05\r\nvn 0.78 -0.54 -0.32\r\nvn 0.82 -0.51 -0.24\r\nvn 0.66 -0.59 -0.46\r\nvn 0.73 -0.56 -0.39\r\nvn 0.59 -0.61 -0.53\r\nvn 0.5 -0.63 -0.59\r\nvn 0.42 -0.64 -0.64\r\nvn 0.36 -0.65 -0.67\r\nvn 0.33 -0.65 -0.69\r\nvn 0.36 -0.64 -0.68\r\nvn 0.79 -0.43 -0.44\r\nvn 0.49 -0.6 -0.63\r\nvn 0.26 -0.4 -0.88\r\nvn 0.74 -0.38 -0.55\r\nvn 0.23 -0.09 -0.97\r\nvn 0.78 -0.11 -0.62\r\nvn 0.96 -0.04 -0.28\r\nvn 0.92 -0.33 -0.22\r\nvn -0.21 -0.35 -0.91\r\nvn -0.11 -0.3 -0.95\r\nvn -0.25 -0.17 -0.95\r\nvn -0.14 -0.11 -0.98\r\nvn 0.93 -0.35 -0.04\r\nvn 0.89 -0.45 0.08\r\nvn 1 -0.04 0.07\r\nvn 0.98 -0.05 0.18\r\nvn 0.95 -0.01 0.3\r\nvn 0.85 -0.48 0.22\r\nvn 0.99 -0.16 -0.01\r\nvn 0.99 -0.09 0.07\r\nvn 0.99 0.01 -0.11\r\nvn 1 0.04 -0.03\r\nvn 1 0.08 0\r\nvn 0.99 -0.04 0.09\r\nvn 0.99 -0.12 -0.12\r\nvn 0.97 -0.21 -0.12\r\nvn 1 0.08 0.03\r\nvn 1 0.04 0.01\r\nvn 0.77 0.42 0.48\r\nvn 0.42 0.52 0.74\r\nvn 0.76 0.59 0.27\r\nvn 0.4 0.69 0.6\r\nvn 0.31 0.61 0.73\r\nvn 0.35 0.43 0.83\r\nvn 1 0.06 0.03\r\nvn 0.98 0.16 0.13\r\nvn 0.98 0.18 -0.11\r\nvn 0.96 0.29 -0.03\r\nvn 0.38 0.41 0.83\r\nvn 0.33 0.57 0.75\r\nvn 0.37 0.56 0.74\r\nvn 0.43 0.4 0.81\r\nvn 0.5 0.41 0.76\r\nvn 0.42 0.57 0.7\r\nvn 0.47 0.58 0.67\r\nvn 0.55 0.42 0.72\r\nvn 0.6 0.42 0.68\r\nvn 0.51 0.58 0.63\r\nvn 0.56 0.58 0.6\r\nvn 0.65 0.42 0.63\r\nvn 0.7 0.42 0.58\r\nvn 0.61 0.57 0.55\r\nvn 0.67 0.56 0.49\r\nvn 0.76 0.41 0.51\r\nvn 0.81 0.4 0.43\r\nvn 0.73 0.55 0.41\r\nvn 0.79 0.53 0.3\r\nvn 0.87 0.38 0.32\r\nvn 0.91 0.37 0.2\r\nvn 0.83 0.52 0.19\r\nvn 0.86 0.51 0.07\r\nvn 0.93 0.37 0.07\r\nvn 0.93 0.36 -0.05\r\nvn 0.86 0.5 -0.05\r\nvn 0.85 0.5 -0.16\r\nvn 0.92 0.35 -0.17\r\nvn 0.9 0.34 -0.28\r\nvn 0.83 0.49 -0.26\r\nvn 0.8 0.48 -0.36\r\nvn 0.86 0.32 -0.39\r\nvn 0.82 0.3 -0.48\r\nvn 0.77 0.46 -0.45\r\nvn 0.72 0.45 -0.53\r\nvn 0.77 0.28 -0.58\r\nvn 0.7 0.26 -0.66\r\nvn 0.66 0.44 -0.61\r\nvn 0.59 0.43 -0.68\r\nvn 0.63 0.26 -0.73\r\nvn 0.55 0.26 -0.79\r\nvn 0.52 0.44 -0.73\r\nvn 0.47 0.46 -0.76\r\nvn 0.49 0.28 -0.83\r\nvn 0.45 0.3 -0.84\r\nvn 0.44 0.48 -0.76\r\nvn 0.47 0.53 -0.71\r\nvn 0.47 0.34 -0.81\r\nvn 0.59 0.41 -0.7\r\nvn 0.59 0.58 -0.55\r\nvn 0.84 0.48 -0.26\r\nvn 0.84 0.28 -0.46\r\nvn 0.98 0.07 -0.18\r\nvn 0.97 0.23 -0.01\r\nvn 0.99 0.12 0.07\r\nvn 0.99 -0.04 -0.11\r\nvn 0.96 -0.25 -0.12\r\nvn 0.98 -0.02 -0.2\r\nvn -0.35 -0.38 -0.86\r\nvn -0.28 -0.37 -0.89\r\nvn -0.4 -0.19 -0.89\r\nvn -0.33 -0.19 -0.92\r\nvn -0.48 -0.41 -0.77\r\nvn -0.41 -0.39 -0.82\r\nvn -0.56 -0.2 -0.8\r\nvn -0.48 -0.19 -0.86\r\nvn -0.66 -0.37 -0.66\r\nvn -0.56 -0.4 -0.72\r\nvn -0.75 -0.14 -0.65\r\nvn -0.65 -0.18 -0.74\r\nvn -0.85 -0.19 -0.5\r\nvn -0.75 -0.3 -0.58\r\nvn -0.9 0.05 -0.44\r\nvn -0.83 -0.06 -0.55\r\nvn -0.96 0.06 -0.27\r\nvn -0.92 -0.06 -0.39\r\nvn -0.94 0.25 -0.22\r\nvn -0.93 0.16 -0.32\r\nvn -0.98 0.2 -0.06\r\nvn -0.98 0.15 -0.16\r\nvn -0.94 0.34 -0.05\r\nvn -0.94 0.31 -0.14\r\nvn -0.97 0.18 0.15\r\nvn -0.98 0.2 0.04\r\nvn -0.94 0.32 0.13\r\nvn -0.94 0.34 0.04\r\nvn -0.92 0.12 0.38\r\nvn -0.95 0.15 0.26\r\nvn -0.9 0.27 0.34\r\nvn -0.93 0.29 0.22\r\nvn -0.81 0.09 0.58\r\nvn -0.87 0.1 0.49\r\nvn -0.81 0.26 0.53\r\nvn -0.86 0.26 0.44\r\nvn -0.65 0.1 0.75\r\nvn -0.74 0.09 0.66\r\nvn -0.66 0.28 0.7\r\nvn -0.75 0.26 0.61\r\nvn -0.46 0.06 0.89\r\nvn -0.55 0.09 0.83\r\nvn -0.47 0.29 0.83\r\nvn -0.56 0.29 0.77\r\nvn -0.27 -0 0.96\r\nvn -0.37 0.02 0.93\r\nvn -0.28 0.29 0.91\r\nvn -0.39 0.29 0.88\r\nvn 0.2 -0.23 0.95\r\nvn -0.14 0.02 0.99\r\nvn 0.14 0.29 0.95\r\nvn -0.15 0.33 0.93\r\nvn 0.67 -0.4 0.62\r\nvn 0.72 0.07 0.68\r\nvn 1 0.02 0.06\r\nvn 0.99 0.15 -0.04\r\nvn 0.99 -0.08 -0.1\r\nvn 0.99 0.1 0.06\r\nvt 0.0443 0.7248\r\nvt 0.0309 0.7455\r\nvt 0.0231 0.7374\r\nvt 0.0375 0.7142\r\nvt 0.022 0.7607\r\nvt 0.0144 0.7542\r\nvt 0.0116 0.7467\r\nvt 0.0167 0.7318\r\nvt 0.0317 0.7066\r\nvt 0.0628 0.7014\r\nvt 0.0688 0.7171\r\nvt 0.0517 0.7379\r\nvt 0.0854 0.6768\r\nvt 0.0902 0.6948\r\nvt 0.0965 0.7125\r\nvt 0.0761 0.7328\r\nvt 0.06 0.751\r\nvt 0.1107 0.6526\r\nvt 0.1148 0.6724\r\nvt 0.1385 0.6287\r\nvt 0.1421 0.65\r\nvt 0.1469 0.6709\r\nvt 0.1202 0.6918\r\nvt 0.1683 0.6054\r\nvt 0.1717 0.6279\r\nvt 0.2009 0.5824\r\nvt 0.2041 0.6057\r\nvt 0.2082 0.6289\r\nvt 0.1761 0.65\r\nvt 0.2397 0.5834\r\nvt 0.2433 0.6075\r\nvt 0.2368 0.5598\r\nvt 0.276 0.5375\r\nvt 0.2785 0.5616\r\nvt 0.2816 0.5868\r\nvt 0.3208 0.5408\r\nvt 0.3232 0.5676\r\nvt 0.3188 0.5161\r\nvt 0.3645 0.4974\r\nvt 0.3659 0.5228\r\nvt 0.3676 0.5511\r\nvt 0.4128 0.4828\r\nvt 0.4139 0.5089\r\nvt 0.4639 0.4731\r\nvt 0.4643 0.4997\r\nvt 0.4644 0.5301\r\nvt 0.4149 0.5385\r\nvt 0.5172 0.4959\r\nvt 0.5161 0.5266\r\nvt 0.5176 0.4692\r\nvt 0.5723 0.4708\r\nvt 0.5711 0.4974\r\nvt 0.5689 0.5279\r\nvt 0.6246 0.504\r\nvt 0.6213 0.5338\r\nvt 0.6266 0.4778\r\nvt 0.6787 0.4892\r\nvt 0.6759 0.5148\r\nvt 0.6716 0.5435\r\nvt 0.7231 0.5286\r\nvt 0.7179 0.5563\r\nvt 0.7265 0.5038\r\nvt 0.7702 0.5208\r\nvt 0.766 0.5448\r\nvt 0.7598 0.5713\r\nvt 0.8043 0.5626\r\nvt 0.7966 0.5879\r\nvt 0.8098 0.5395\r\nvt 0.8449 0.5597\r\nvt 0.8381 0.582\r\nvt 0.8292 0.6062\r\nvt 0.8675 0.6031\r\nvt 0.858 0.626\r\nvt 0.8751 0.5816\r\nvt 0.9023 0.605\r\nvt 0.8939 0.6254\r\nvt 0.8838 0.6468\r\nvt 0.9185 0.6483\r\nvt 0.9073 0.6678\r\nvt 0.928 0.6294\r\nvt 0.951 0.6536\r\nvt 0.9401 0.6702\r\nvt 0.9276 0.6871\r\nvt 0.9575 0.6895\r\nvt 0.9437 0.7028\r\nvt 0.9695 0.6763\r\nvt 0.9819 0.6929\r\nvt 0.9695 0.7031\r\nvt 0.9548 0.7135\r\nvt 0.0421 0.7764\r\nvt 0.0492 0.7656\r\nvt 0.0607 0.7756\r\nvt 0.0543 0.7857\r\nvt 0.0708 0.7625\r\nvt 0.0838 0.7741\r\nvt 0.0741 0.7865\r\nvt 0.0681 0.7962\r\nvt 0.1059 0.727\r\nvt 0.0862 0.7458\r\nvt 0.1182 0.741\r\nvt 0.0989 0.7585\r\nvt 0.1288 0.7078\r\nvt 0.1546 0.6881\r\nvt 0.165 0.7045\r\nvt 0.1402 0.723\r\nvt 0.183 0.6683\r\nvt 0.2144 0.6483\r\nvt 0.2226 0.6669\r\nvt 0.1924 0.6857\r\nvt 0.2861 0.6087\r\nvt 0.2487 0.6281\r\nvt 0.2921 0.6298\r\nvt 0.2558 0.6479\r\nvt 0.37 0.5761\r\nvt 0.3267 0.5911\r\nvt 0.3732 0.5995\r\nvt 0.3313 0.6134\r\nvt 0.416 0.5647\r\nvt 0.4642 0.5572\r\nvt 0.464 0.5826\r\nvt 0.4176 0.5892\r\nvt 0.5654 0.5551\r\nvt 0.5143 0.554\r\nvt 0.5612 0.5809\r\nvt 0.5122 0.5799\r\nvt 0.6163 0.5605\r\nvt 0.6651 0.5693\r\nvt 0.6573 0.5937\r\nvt 0.6102 0.5858\r\nvt 0.7103 0.581\r\nvt 0.7511 0.5948\r\nvt 0.7406 0.6169\r\nvt 0.701 0.6043\r\nvt 0.8186 0.627\r\nvt 0.7869 0.6101\r\nvt 0.8064 0.6465\r\nvt 0.7755 0.631\r\nvt 0.8719 0.6647\r\nvt 0.8467 0.6454\r\nvt 0.8584 0.6812\r\nvt 0.8338 0.6635\r\nvt 0.9142 0.7011\r\nvt 0.8947 0.6839\r\nvt 0.8996 0.7143\r\nvt 0.8807 0.6987\r\nvt 0.9401 0.7234\r\nvt 0.9295 0.7145\r\nvt 0.9246 0.7342\r\nvt 0.9144 0.7262\r\nvt 0.1132 0.7997\r\nvt 0.126 0.8108\r\nvt 0.1155 0.8211\r\nvt 0.1033 0.8111\r\nvt 0.1354 0.8198\r\nvt 0.1238 0.829\r\nvt 0.1165 0.8326\r\nvt 0.1092 0.8289\r\nvt 0.0973 0.8198\r\nvt 0.1467 0.7713\r\nvt 0.1594 0.7843\r\nvt 0.1412 0.7984\r\nvt 0.1283 0.7863\r\nvt 0.1691 0.7943\r\nvt 0.151 0.8079\r\nvt 0.1898 0.7396\r\nvt 0.2009 0.7543\r\nvt 0.1792 0.7696\r\nvt 0.1671 0.7558\r\nvt 0.2096 0.7655\r\nvt 0.1885 0.7802\r\nvt 0.2583 0.7341\r\nvt 0.2327 0.75\r\nvt 0.2249 0.7383\r\nvt 0.2514 0.7219\r\nvt 0.2147 0.7228\r\nvt 0.2424 0.7057\r\nvt 0.318 0.703\r\nvt 0.2868 0.7182\r\nvt 0.2807 0.7054\r\nvt 0.3128 0.6897\r\nvt 0.2729 0.6886\r\nvt 0.3063 0.6723\r\nvt 0.3877 0.6783\r\nvt 0.3517 0.6894\r\nvt 0.3476 0.6757\r\nvt 0.3847 0.6641\r\nvt 0.3425 0.6577\r\nvt 0.381 0.6455\r\nvt 0.465 0.6657\r\nvt 0.4256 0.6704\r\nvt 0.4238 0.656\r\nvt 0.4645 0.651\r\nvt 0.4217 0.6369\r\nvt 0.4641 0.6315\r\nvt 0.547 0.6645\r\nvt 0.5057 0.6637\r\nvt 0.5067 0.649\r\nvt 0.5494 0.6498\r\nvt 0.5081 0.6293\r\nvt 0.5528 0.6302\r\nvt 0.6284 0.6732\r\nvt 0.5883 0.6677\r\nvt 0.5922 0.6533\r\nvt 0.6337 0.6592\r\nvt 0.5974 0.6341\r\nvt 0.6407 0.6405\r\nvt 0.7009 0.6899\r\nvt 0.6662 0.6807\r\nvt 0.6726 0.6672\r\nvt 0.7084 0.677\r\nvt 0.6811 0.6492\r\nvt 0.7181 0.6598\r\nvt 0.78 0.6852\r\nvt 0.7685 0.7009\r\nvt 0.7402 0.6883\r\nvt 0.7509 0.6718\r\nvt 0.7597 0.7126\r\nvt 0.732 0.7007\r\nvt 0.8291 0.7144\r\nvt 0.8163 0.7282\r\nvt 0.7937 0.7144\r\nvt 0.8059 0.6996\r\nvt 0.8065 0.7386\r\nvt 0.7843 0.7254\r\nvt 0.8684 0.7417\r\nvt 0.8548 0.7533\r\nvt 0.8369 0.7416\r\nvt 0.8502 0.7289\r\nvt 0.8447 0.7622\r\nvt 0.8268 0.7513\r\nvt 0.8693 0.7721\r\nvt 0.8595 0.7697\r\nvt 0.8691 0.7618\r\nvt 0.8787 0.7678\r\nvt 0.8827 0.7515\r\nvt 0.8925 0.7584\r\nvt 0.0395 0.7554\r\nvt 0.0313 0.7682\r\nvt 0.0526 0.6795\r\nvt 0.0574 0.6886\r\nvt 0.0774 0.6519\r\nvt 0.0811 0.6622\r\nvt 0.1042 0.6246\r\nvt 0.1073 0.6362\r\nvt 0.1325 0.5981\r\nvt 0.1353 0.6108\r\nvt 0.1655 0.5865\r\nvt 0.1628 0.5732\r\nvt 0.1956 0.5499\r\nvt 0.1982 0.5633\r\nvt 0.2342 0.541\r\nvt 0.2315 0.5284\r\nvt 0.2709 0.508\r\nvt 0.2736 0.5194\r\nvt 0.3169 0.4989\r\nvt 0.3143 0.4888\r\nvt 0.3608 0.4722\r\nvt 0.363 0.481\r\nvt 0.41 0.4586\r\nvt 0.4116 0.4668\r\nvt 0.4621 0.4495\r\nvt 0.4632 0.4572\r\nvt 0.5176 0.4533\r\nvt 0.5169 0.4455\r\nvt 0.5725 0.4466\r\nvt 0.5727 0.4547\r\nvt 0.6275 0.4614\r\nvt 0.6276 0.4529\r\nvt 0.6805 0.4638\r\nvt 0.6802 0.4728\r\nvt 0.7284 0.4877\r\nvt 0.729 0.4785\r\nvt 0.7736 0.4959\r\nvt 0.7726 0.5051\r\nvt 0.813 0.5241\r\nvt 0.8145 0.515\r\nvt 0.8511 0.5356\r\nvt 0.849 0.5446\r\nvt 0.8801 0.5667\r\nvt 0.8829 0.5578\r\nvt 0.9118 0.5815\r\nvt 0.9082 0.5905\r\nvt 0.935 0.6156\r\nvt 0.9393 0.607\r\nvt 0.9636 0.6332\r\nvt 0.9589 0.6411\r\nvt 0.9778 0.666\r\nvt 0.9826 0.6594\r\nvt 0.9902 0.6756\r\nvt 0.9897 0.6845\r\nvt 0.0888 0.7989\r\nvt 0.0985 0.787\r\nvt 0.083 0.8082\r\nvt 0.1323 0.7563\r\nvt 0.1135 0.7725\r\nvt 0.1536 0.7395\r\nvt 0.1773 0.7223\r\nvt 0.2034 0.7046\r\nvt 0.2324 0.6867\r\nvt 0.2991 0.6516\r\nvt 0.2643 0.6687\r\nvt 0.377 0.6233\r\nvt 0.3368 0.6362\r\nvt 0.4195 0.614\r\nvt 0.4639 0.608\r\nvt 0.51 0.6056\r\nvt 0.5569 0.6066\r\nvt 0.6036 0.6109\r\nvt 0.6488 0.6181\r\nvt 0.6909 0.6276\r\nvt 0.7293 0.6392\r\nvt 0.7931 0.6665\r\nvt 0.7631 0.6521\r\nvt 0.8437 0.6982\r\nvt 0.8197 0.6821\r\nvt 0.8839 0.7282\r\nvt 0.8653 0.7141\r\nvt 0.8984 0.7391\r\nvt 0.9084 0.7465\r\nvt 0.0088 0.7612\r\nvt 0.006 0.7508\r\nvt 0.0161 0.7681\r\nvt 0.0106 0.7735\r\nvt 0.003 0.7645\r\nvt 0 0.7517\r\nvt 0.0114 0.7294\r\nvt 0.0265 0.7024\r\nvt 0.0054 0.7279\r\nvt 0.0208 0.6992\r\nvt 0.9752 0.7088\r\nvt 0.9608 0.7187\r\nvt 0.9874 0.6993\r\nvt 0.9909 0.7029\r\nvt 0.9794 0.7129\r\nvt 0.9655 0.7234\r\nvt 0.0499 0.7933\r\nvt 0.0372 0.7839\r\nvt 0.0639 0.8037\r\nvt 0.0603 0.8109\r\nvt 0.0462 0.8006\r\nvt 0.033 0.791\r\nvt 0.946 0.7287\r\nvt 0.9305 0.7396\r\nvt 0.9507 0.7341\r\nvt 0.9352 0.7453\r\nvt 0.1275 0.8349\r\nvt 0.1164 0.8381\r\nvt 0.1409 0.826\r\nvt 0.1452 0.8319\r\nvt 0.1298 0.8411\r\nvt 0.1154 0.8443\r\nvt 0.1056 0.8349\r\nvt 0.0932 0.8265\r\nvt 0.1025 0.8409\r\nvt 0.0893 0.833\r\nvt 0.1571 0.8143\r\nvt 0.1751 0.8009\r\nvt 0.1799 0.8067\r\nvt 0.162 0.82\r\nvt 0.1942 0.787\r\nvt 0.2148 0.7726\r\nvt 0.219 0.7788\r\nvt 0.1986 0.793\r\nvt 0.2375 0.7575\r\nvt 0.2626 0.742\r\nvt 0.2662 0.7486\r\nvt 0.2413 0.7639\r\nvt 0.2906 0.7264\r\nvt 0.3212 0.7115\r\nvt 0.324 0.7187\r\nvt 0.2938 0.7333\r\nvt 0.3544 0.6983\r\nvt 0.3897 0.6875\r\nvt 0.3915 0.6951\r\nvt 0.3567 0.7058\r\nvt 0.4269 0.6798\r\nvt 0.4656 0.6752\r\nvt 0.4661 0.683\r\nvt 0.4281 0.6876\r\nvt 0.5054 0.6733\r\nvt 0.5458 0.674\r\nvt 0.545 0.6818\r\nvt 0.5053 0.6811\r\nvt 0.5861 0.6771\r\nvt 0.6253 0.6823\r\nvt 0.6232 0.6899\r\nvt 0.5846 0.6848\r\nvt 0.6623 0.6895\r\nvt 0.6965 0.6983\r\nvt 0.6932 0.7054\r\nvt 0.6596 0.6968\r\nvt 0.727 0.7087\r\nvt 0.7542 0.7202\r\nvt 0.7501 0.7267\r\nvt 0.7233 0.7155\r\nvt 0.7784 0.7326\r\nvt 0.8003 0.7454\r\nvt 0.7955 0.7514\r\nvt 0.7739 0.7389\r\nvt 0.8205 0.7578\r\nvt 0.8389 0.7683\r\nvt 0.8345 0.7742\r\nvt 0.8156 0.7636\r\nvt 0.8551 0.7755\r\nvt 0.8696 0.7772\r\nvt 0.8702 0.7834\r\nvt 0.8527 0.7816\r\nvt 0.8835 0.7727\r\nvt 0.8982 0.7635\r\nvt 0.9027 0.7692\r\nvt 0.8868 0.7785\r\nvt 0.0258 0.7757\r\nvt 0.0209 0.7821\r\nvt 0.0481 0.6743\r\nvt 0.0734 0.6461\r\nvt 0.0428 0.6702\r\nvt 0.0684 0.6416\r\nvt 0.1006 0.6183\r\nvt 0.1291 0.5912\r\nvt 0.0957 0.6135\r\nvt 0.1242 0.5861\r\nvt 0.1594 0.5661\r\nvt 0.192 0.5428\r\nvt 0.1545 0.5606\r\nvt 0.1868 0.537\r\nvt 0.2276 0.5215\r\nvt 0.2665 0.5014\r\nvt 0.2218 0.5152\r\nvt 0.2601 0.4945\r\nvt 0.3097 0.4823\r\nvt 0.3564 0.465\r\nvt 0.3028 0.4744\r\nvt 0.3498 0.4555\r\nvt 0.4063 0.4506\r\nvt 0.4594 0.4406\r\nvt 0.4008 0.4393\r\nvt 0.4554 0.428\r\nvt 0.5152 0.4361\r\nvt 0.572 0.437\r\nvt 0.5129 0.4227\r\nvt 0.5714 0.4234\r\nvt 0.628 0.4434\r\nvt 0.6814 0.4544\r\nvt 0.6289 0.43\r\nvt 0.6834 0.4416\r\nvt 0.7304 0.4694\r\nvt 0.7754 0.4872\r\nvt 0.7331 0.4573\r\nvt 0.7788 0.4758\r\nvt 0.8169 0.5067\r\nvt 0.854 0.5281\r\nvt 0.821 0.4963\r\nvt 0.8586 0.5188\r\nvt 0.886 0.551\r\nvt 0.9149 0.5755\r\nvt 0.8909 0.543\r\nvt 0.9197 0.5687\r\nvt 0.9423 0.6017\r\nvt 0.9666 0.6288\r\nvt 0.9467 0.5961\r\nvt 0.9709 0.6244\r\nvt 0.9857 0.6562\r\nvt 0.9952 0.6795\r\nvt 0.9903 0.6534\r\nvt 1 0.679\r\nvt 0.9944 0.6912\r\nvt 0.9983 0.6938\r\nvt 0.0788 0.8154\r\nvt 0.0749 0.8223\r\nvt 0.9142 0.7518\r\nvt 0.919 0.7575\r\nvt 0.0443 0.1195\r\nvt 0.0375 0.1301\r\nvt 0.0231 0.1069\r\nvt 0.0309 0.0988\r\nvt 0.0317 0.1376\r\nvt 0.0167 0.1125\r\nvt 0.0116 0.0976\r\nvt 0.0144 0.0901\r\nvt 0.022 0.0836\r\nvt 0.0517 0.1064\r\nvt 0.0688 0.1272\r\nvt 0.0628 0.1429\r\nvt 0.06 0.0933\r\nvt 0.0761 0.1115\r\nvt 0.0965 0.1318\r\nvt 0.0902 0.1495\r\nvt 0.0854 0.1674\r\nvt 0.1148 0.1719\r\nvt 0.1107 0.1917\r\nvt 0.1202 0.1525\r\nvt 0.1469 0.1734\r\nvt 0.1421 0.1943\r\nvt 0.1385 0.2156\r\nvt 0.1717 0.2164\r\nvt 0.1683 0.2389\r\nvt 0.1761 0.1943\r\nvt 0.2082 0.2154\r\nvt 0.2041 0.2386\r\nvt 0.2009 0.2618\r\nvt 0.2433 0.2368\r\nvt 0.2397 0.2609\r\nvt 0.2816 0.2575\r\nvt 0.2785 0.2827\r\nvt 0.276 0.3068\r\nvt 0.2368 0.2845\r\nvt 0.3232 0.2767\r\nvt 0.3208 0.3035\r\nvt 0.3676 0.2932\r\nvt 0.3659 0.3215\r\nvt 0.3645 0.3469\r\nvt 0.3188 0.3282\r\nvt 0.4139 0.3353\r\nvt 0.4128 0.3615\r\nvt 0.4149 0.3058\r\nvt 0.4644 0.3142\r\nvt 0.4643 0.3446\r\nvt 0.4639 0.3712\r\nvt 0.5161 0.3177\r\nvt 0.5172 0.3483\r\nvt 0.5689 0.3164\r\nvt 0.5711 0.3469\r\nvt 0.5723 0.3735\r\nvt 0.5176 0.3751\r\nvt 0.6213 0.3105\r\nvt 0.6246 0.3403\r\nvt 0.6716 0.3008\r\nvt 0.6759 0.3295\r\nvt 0.6787 0.3551\r\nvt 0.6266 0.3665\r\nvt 0.7179 0.288\r\nvt 0.7231 0.3157\r\nvt 0.7598 0.273\r\nvt 0.766 0.2995\r\nvt 0.7702 0.3234\r\nvt 0.7265 0.3405\r\nvt 0.7966 0.2564\r\nvt 0.8043 0.2817\r\nvt 0.8292 0.2381\r\nvt 0.8381 0.2623\r\nvt 0.8449 0.2846\r\nvt 0.8098 0.3048\r\nvt 0.858 0.2183\r\nvt 0.8675 0.2412\r\nvt 0.8838 0.1975\r\nvt 0.8939 0.2189\r\nvt 0.9023 0.2393\r\nvt 0.8751 0.2627\r\nvt 0.9073 0.1765\r\nvt 0.9185 0.196\r\nvt 0.9276 0.1572\r\nvt 0.9401 0.1741\r\nvt 0.951 0.1907\r\nvt 0.928 0.2149\r\nvt 0.9437 0.1415\r\nvt 0.9575 0.1548\r\nvt 0.9548 0.1308\r\nvt 0.9695 0.1412\r\nvt 0.9819 0.1513\r\nvt 0.9695 0.168\r\nvt 0.0421 0.0678\r\nvt 0.0543 0.0586\r\nvt 0.0607 0.0686\r\nvt 0.0492 0.0787\r\nvt 0.0681 0.0481\r\nvt 0.0741 0.0578\r\nvt 0.0838 0.0702\r\nvt 0.0708 0.0818\r\nvt 0.0862 0.0985\r\nvt 0.1059 0.1173\r\nvt 0.0989 0.0858\r\nvt 0.1182 0.1033\r\nvt 0.1288 0.1365\r\nvt 0.1402 0.1213\r\nvt 0.165 0.1398\r\nvt 0.1546 0.1562\r\nvt 0.183 0.176\r\nvt 0.1924 0.1585\r\nvt 0.2226 0.1774\r\nvt 0.2144 0.196\r\nvt 0.2487 0.2162\r\nvt 0.2861 0.2356\r\nvt 0.2558 0.1964\r\nvt 0.2921 0.2145\r\nvt 0.3267 0.2532\r\nvt 0.37 0.2682\r\nvt 0.3313 0.2309\r\nvt 0.3732 0.2448\r\nvt 0.416 0.2796\r\nvt 0.4176 0.255\r\nvt 0.464 0.2616\r\nvt 0.4642 0.2871\r\nvt 0.5143 0.2903\r\nvt 0.5654 0.2891\r\nvt 0.5122 0.2644\r\nvt 0.5612 0.2634\r\nvt 0.6163 0.2838\r\nvt 0.6102 0.2585\r\nvt 0.6573 0.2506\r\nvt 0.6651 0.275\r\nvt 0.7103 0.2633\r\nvt 0.701 0.24\r\nvt 0.7406 0.2274\r\nvt 0.7511 0.2495\r\nvt 0.7869 0.2342\r\nvt 0.8186 0.2173\r\nvt 0.7755 0.2133\r\nvt 0.8064 0.1977\r\nvt 0.8467 0.1988\r\nvt 0.8719 0.1796\r\nvt 0.8338 0.1808\r\nvt 0.8584 0.1631\r\nvt 0.8947 0.1604\r\nvt 0.9142 0.1432\r\nvt 0.8807 0.1456\r\nvt 0.8996 0.13\r\nvt 0.9295 0.1298\r\nvt 0.9401 0.1209\r\nvt 0.9144 0.1181\r\nvt 0.9246 0.1101\r\nvt 0.1132 0.0446\r\nvt 0.1033 0.0332\r\nvt 0.1155 0.0232\r\nvt 0.126 0.0334\r\nvt 0.0973 0.0244\r\nvt 0.1092 0.0154\r\nvt 0.1165 0.0117\r\nvt 0.1238 0.0153\r\nvt 0.1354 0.0245\r\nvt 0.1467 0.0729\r\nvt 0.1283 0.058\r\nvt 0.1412 0.0459\r\nvt 0.1594 0.0599\r\nvt 0.151 0.0364\r\nvt 0.1691 0.05\r\nvt 0.1898 0.1047\r\nvt 0.1671 0.0885\r\nvt 0.1792 0.0747\r\nvt 0.2009 0.09\r\nvt 0.1885 0.0641\r\nvt 0.2096 0.0788\r\nvt 0.2583 0.1102\r\nvt 0.2514 0.1224\r\nvt 0.2249 0.106\r\nvt 0.2327 0.0943\r\nvt 0.2424 0.1386\r\nvt 0.2147 0.1215\r\nvt 0.318 0.1413\r\nvt 0.3128 0.1546\r\nvt 0.2807 0.1389\r\nvt 0.2868 0.1261\r\nvt 0.3063 0.172\r\nvt 0.2729 0.1557\r\nvt 0.3877 0.166\r\nvt 0.3847 0.1801\r\nvt 0.3476 0.1686\r\nvt 0.3517 0.1549\r\nvt 0.381 0.1987\r\nvt 0.3425 0.1866\r\nvt 0.465 0.1786\r\nvt 0.4645 0.1933\r\nvt 0.4238 0.1883\r\nvt 0.4256 0.1739\r\nvt 0.4641 0.2128\r\nvt 0.4217 0.2074\r\nvt 0.547 0.1798\r\nvt 0.5494 0.1945\r\nvt 0.5067 0.1953\r\nvt 0.5057 0.1805\r\nvt 0.5528 0.214\r\nvt 0.5081 0.215\r\nvt 0.6284 0.1711\r\nvt 0.6337 0.1851\r\nvt 0.5922 0.191\r\nvt 0.5883 0.1765\r\nvt 0.6407 0.2038\r\nvt 0.5974 0.2102\r\nvt 0.7009 0.1544\r\nvt 0.7084 0.1673\r\nvt 0.6726 0.1771\r\nvt 0.6662 0.1636\r\nvt 0.7181 0.1845\r\nvt 0.6811 0.1951\r\nvt 0.78 0.1591\r\nvt 0.7509 0.1724\r\nvt 0.7402 0.156\r\nvt 0.7685 0.1434\r\nvt 0.732 0.1436\r\nvt 0.7597 0.1317\r\nvt 0.8291 0.1299\r\nvt 0.8059 0.1447\r\nvt 0.7937 0.1299\r\nvt 0.8163 0.1161\r\nvt 0.7843 0.1188\r\nvt 0.8065 0.1057\r\nvt 0.8684 0.1026\r\nvt 0.8502 0.1153\r\nvt 0.8369 0.1027\r\nvt 0.8548 0.091\r\nvt 0.8268 0.0929\r\nvt 0.8447 0.0821\r\nvt 0.8693 0.0722\r\nvt 0.8787 0.0765\r\nvt 0.8691 0.0825\r\nvt 0.8595 0.0746\r\nvt 0.8925 0.0859\r\nvt 0.8827 0.0928\r\nvt 0.0395 0.0889\r\nvt 0.0313 0.0761\r\nvt 0.0574 0.1557\r\nvt 0.0526 0.1648\r\nvt 0.0811 0.182\r\nvt 0.0774 0.1924\r\nvt 0.1073 0.208\r\nvt 0.1042 0.2196\r\nvt 0.1353 0.2335\r\nvt 0.1325 0.2462\r\nvt 0.1655 0.2578\r\nvt 0.1982 0.281\r\nvt 0.1956 0.2943\r\nvt 0.1628 0.2711\r\nvt 0.2342 0.3033\r\nvt 0.2736 0.3248\r\nvt 0.2709 0.3363\r\nvt 0.2315 0.3159\r\nvt 0.3169 0.3454\r\nvt 0.363 0.3633\r\nvt 0.3608 0.3721\r\nvt 0.3143 0.3554\r\nvt 0.4116 0.3775\r\nvt 0.41 0.3856\r\nvt 0.4632 0.3871\r\nvt 0.4621 0.3948\r\nvt 0.5176 0.391\r\nvt 0.5727 0.3896\r\nvt 0.5725 0.3977\r\nvt 0.5169 0.3988\r\nvt 0.6275 0.3828\r\nvt 0.6802 0.3715\r\nvt 0.6805 0.3805\r\nvt 0.6276 0.3914\r\nvt 0.7284 0.3566\r\nvt 0.7726 0.3392\r\nvt 0.7736 0.3484\r\nvt 0.729 0.3658\r\nvt 0.813 0.3202\r\nvt 0.849 0.2997\r\nvt 0.8511 0.3087\r\nvt 0.8145 0.3293\r\nvt 0.8801 0.2776\r\nvt 0.9082 0.2538\r\nvt 0.9118 0.2628\r\nvt 0.8829 0.2865\r\nvt 0.935 0.2287\r\nvt 0.9589 0.2031\r\nvt 0.9636 0.2111\r\nvt 0.9393 0.2373\r\nvt 0.9778 0.1783\r\nvt 0.9897 0.1598\r\nvt 0.9902 0.1687\r\nvt 0.9826 0.1849\r\nvt 0.0985 0.0573\r\nvt 0.0888 0.0454\r\nvt 0.083 0.036\r\nvt 0.1135 0.0718\r\nvt 0.1323 0.088\r\nvt 0.1773 0.122\r\nvt 0.1536 0.1047\r\nvt 0.2324 0.1576\r\nvt 0.2034 0.1397\r\nvt 0.2643 0.1756\r\nvt 0.2991 0.1927\r\nvt 0.3368 0.2081\r\nvt 0.377 0.221\r\nvt 0.4639 0.2362\r\nvt 0.4195 0.2303\r\nvt 0.5569 0.2377\r\nvt 0.51 0.2387\r\nvt 0.6488 0.2262\r\nvt 0.6036 0.2334\r\nvt 0.7293 0.2051\r\nvt 0.6909 0.2166\r\nvt 0.7631 0.1921\r\nvt 0.7931 0.1778\r\nvt 0.8197 0.1622\r\nvt 0.8437 0.1461\r\nvt 0.8653 0.1301\r\nvt 0.8839 0.116\r\nvt 0.9084 0.0978\r\nvt 0.8984 0.1052\r\nvt 0.006 0.0935\r\nvt 0.0088 0.0831\r\nvt 0 0.0926\r\nvt 0.003 0.0797\r\nvt 0.0106 0.0708\r\nvt 0.0161 0.0762\r\nvt 0.0265 0.1419\r\nvt 0.0114 0.1149\r\nvt 0.0208 0.1451\r\nvt 0.0054 0.1164\r\nvt 0.9608 0.1256\r\nvt 0.9752 0.1355\r\nvt 0.9655 0.1209\r\nvt 0.9794 0.1314\r\nvt 0.9909 0.1414\r\nvt 0.9874 0.145\r\nvt 0.0372 0.0604\r\nvt 0.0499 0.051\r\nvt 0.033 0.0533\r\nvt 0.0462 0.0437\r\nvt 0.0603 0.0333\r\nvt 0.0639 0.0406\r\nvt 0.9305 0.1047\r\nvt 0.946 0.1156\r\nvt 0.9352 0.099\r\nvt 0.9507 0.1102\r\nvt 0.1164 0.0062\r\nvt 0.1275 0.0094\r\nvt 0.1154 -0\r\nvt 0.1298 0.0032\r\nvt 0.1452 0.0123\r\nvt 0.1409 0.0182\r\nvt 0.0932 0.0178\r\nvt 0.1056 0.0094\r\nvt 0.0893 0.0113\r\nvt 0.1025 0.0033\r\nvt 0.1571 0.03\r\nvt 0.162 0.0243\r\nvt 0.1799 0.0376\r\nvt 0.1751 0.0434\r\nvt 0.1942 0.0573\r\nvt 0.1986 0.0513\r\nvt 0.219 0.0655\r\nvt 0.2148 0.0717\r\nvt 0.2375 0.0868\r\nvt 0.2413 0.0804\r\nvt 0.2662 0.0957\r\nvt 0.2626 0.1023\r\nvt 0.2906 0.1179\r\nvt 0.2938 0.111\r\nvt 0.324 0.1256\r\nvt 0.3212 0.1327\r\nvt 0.3544 0.146\r\nvt 0.3567 0.1385\r\nvt 0.3915 0.1492\r\nvt 0.3897 0.1568\r\nvt 0.4269 0.1645\r\nvt 0.4281 0.1567\r\nvt 0.4661 0.1613\r\nvt 0.4656 0.1691\r\nvt 0.5054 0.171\r\nvt 0.5053 0.1632\r\nvt 0.545 0.1625\r\nvt 0.5458 0.1703\r\nvt 0.5861 0.1672\r\nvt 0.5846 0.1595\r\nvt 0.6232 0.1544\r\nvt 0.6253 0.162\r\nvt 0.6623 0.1548\r\nvt 0.6596 0.1475\r\nvt 0.6932 0.1389\r\nvt 0.6965 0.146\r\nvt 0.727 0.1356\r\nvt 0.7233 0.1288\r\nvt 0.7501 0.1175\r\nvt 0.7542 0.1241\r\nvt 0.7784 0.1117\r\nvt 0.7739 0.1054\r\nvt 0.7955 0.0929\r\nvt 0.8003 0.0989\r\nvt 0.8205 0.0865\r\nvt 0.8156 0.0807\r\nvt 0.8345 0.0701\r\nvt 0.8389 0.0759\r\nvt 0.8551 0.0688\r\nvt 0.8527 0.0627\r\nvt 0.8702 0.0609\r\nvt 0.8696 0.0671\r\nvt 0.8835 0.0716\r\nvt 0.8868 0.0657\r\nvt 0.9027 0.0751\r\nvt 0.8982 0.0808\r\nvt 0.0258 0.0686\r\nvt 0.0209 0.0622\r\nvt 0.0734 0.1982\r\nvt 0.0481 0.17\r\nvt 0.0684 0.2027\r\nvt 0.0428 0.1741\r\nvt 0.1291 0.253\r\nvt 0.1006 0.226\r\nvt 0.1242 0.2582\r\nvt 0.0957 0.2308\r\nvt 0.192 0.3014\r\nvt 0.1594 0.2782\r\nvt 0.1868 0.3073\r\nvt 0.1545 0.2837\r\nvt 0.2665 0.3429\r\nvt 0.2276 0.3228\r\nvt 0.2601 0.3497\r\nvt 0.2218 0.329\r\nvt 0.3564 0.3793\r\nvt 0.3097 0.362\r\nvt 0.3498 0.3888\r\nvt 0.3028 0.3699\r\nvt 0.4594 0.4037\r\nvt 0.4063 0.3937\r\nvt 0.4554 0.4163\r\nvt 0.4008 0.405\r\nvt 0.572 0.4073\r\nvt 0.5152 0.4082\r\nvt 0.5714 0.4209\r\nvt 0.5129 0.4216\r\nvt 0.6814 0.3899\r\nvt 0.628 0.4009\r\nvt 0.6834 0.4026\r\nvt 0.6289 0.4143\r\nvt 0.7754 0.3571\r\nvt 0.7304 0.3749\r\nvt 0.7788 0.3684\r\nvt 0.7331 0.387\r\nvt 0.854 0.3162\r\nvt 0.8169 0.3375\r\nvt 0.8586 0.3255\r\nvt 0.821 0.3479\r\nvt 0.9149 0.2688\r\nvt 0.886 0.2933\r\nvt 0.9197 0.2756\r\nvt 0.8909 0.3013\r\nvt 0.9666 0.2155\r\nvt 0.9423 0.2426\r\nvt 0.9709 0.2199\r\nvt 0.9467 0.2482\r\nvt 0.9952 0.1648\r\nvt 0.9857 0.1881\r\nvt 1 0.1653\r\nvt 0.9903 0.1908\r\nvt 0.9944 0.153\r\nvt 0.9983 0.1505\r\nvt 0.0788 0.0289\r\nvt 0.0749 0.022\r\nvt 0.9142 0.0925\r\nvt 0.919 0.0868\r\no Padding\r\nusemtl Padding\r\nf 1/1/1 2/2/2 3/3/3 4/4/4\r\nf 2/2/2 5/5/5 6/6/6 3/3/3\r\nf 7/7/7 8/8/8 3/3/3 6/6/6\r\nf 8/8/8 9/9/9 4/4/4 3/3/3\r\nf 1/1/1 10/10/10 11/11/11 12/12/12\r\nf 10/10/10 13/13/13 14/14/14 11/11/11\r\nf 15/15/15 16/16/16 11/11/11 14/14/14\r\nf 16/16/16 17/17/17 12/12/12 11/11/11\r\nf 13/13/13 18/18/18 19/19/19 14/14/14\r\nf 18/18/18 20/20/20 21/21/21 19/19/19\r\nf 22/22/22 23/23/23 19/19/19 21/21/21\r\nf 23/23/23 15/15/15 14/14/14 19/19/19\r\nf 20/20/20 24/24/24 25/25/25 21/21/21\r\nf 24/24/24 26/26/26 27/27/27 25/25/25\r\nf 28/28/28 29/29/29 25/25/25 27/27/27\r\nf 29/29/29 22/22/22 21/21/21 25/25/25\r\nf 28/28/28 27/27/27 30/30/30 31/31/31\r\nf 27/27/27 26/26/26 32/32/32 30/30/30\r\nf 33/33/33 34/34/34 30/30/30 32/32/32\r\nf 34/34/34 35/35/35 31/31/31 30/30/30\r\nf 35/35/35 34/34/34 36/36/36 37/37/37\r\nf 34/34/34 33/33/33 38/38/38 36/36/36\r\nf 39/39/39 40/40/40 36/36/36 38/38/38\r\nf 40/40/40 41/41/41 37/37/37 36/36/36\r\nf 39/39/39 42/42/42 43/43/43 40/40/40\r\nf 42/42/42 44/44/44 45/45/45 43/43/43\r\nf 46/46/46 47/47/47 43/43/43 45/45/45\r\nf 47/47/47 41/41/41 40/40/40 43/43/43\r\nf 46/46/46 45/45/45 48/48/48 49/49/49\r\nf 45/45/45 44/44/44 50/50/50 48/48/48\r\nf 51/51/51 52/52/52 48/48/48 50/50/50\r\nf 52/52/52 53/53/53 49/49/49 48/48/48\r\nf 53/53/53 52/52/52 54/54/54 55/55/55\r\nf 52/52/52 51/51/51 56/56/56 54/54/54\r\nf 57/57/57 58/58/58 54/54/54 56/56/56\r\nf 58/58/58 59/59/59 55/55/55 54/54/54\r\nf 59/59/59 58/58/58 60/60/60 61/61/61\r\nf 58/58/58 57/57/57 62/62/62 60/60/60\r\nf 63/63/63 64/64/64 60/60/60 62/62/62\r\nf 64/64/64 65/65/65 61/61/61 60/60/60\r\nf 65/65/65 64/64/64 66/66/66 67/67/67\r\nf 64/64/64 63/63/63 68/68/68 66/66/66\r\nf 69/69/69 70/70/70 66/66/66 68/68/68\r\nf 70/70/70 71/71/71 67/67/67 66/66/66\r\nf 71/71/71 70/70/70 72/72/72 73/73/73\r\nf 70/70/70 69/69/69 74/74/74 72/72/72\r\nf 75/75/75 76/76/76 72/72/72 74/74/74\r\nf 76/76/76 77/77/77 73/73/73 72/72/72\r\nf 77/77/77 76/76/76 78/78/78 79/79/79\r\nf 76/76/76 75/75/75 80/80/80 78/78/78\r\nf 81/81/81 82/82/82 78/78/78 80/80/80\r\nf 82/82/82 83/83/83 79/79/79 78/78/78\r\nf 83/83/83 82/82/82 84/84/84 85/85/85\r\nf 82/82/82 81/81/81 86/86/86 84/84/84\r\nf 87/87/87 88/88/88 84/84/84 86/86/86\r\nf 88/88/88 89/89/89 85/85/85 84/84/84\r\nf 90/90/90 91/91/91 92/92/92 93/93/93\r\nf 91/91/91 17/17/17 94/94/94 92/92/92\r\nf 95/95/95 96/96/96 92/92/92 94/94/94\r\nf 96/96/96 97/97/97 93/93/93 92/92/92\r\nf 15/15/15 98/98/98 99/99/99 16/16/16\r\nf 98/98/98 100/100/100 101/101/101 99/99/99\r\nf 95/95/95 94/94/94 99/99/99 101/101/101\r\nf 94/94/94 17/17/17 16/16/16 99/99/99\r\nf 15/15/15 23/23/23 102/102/102 98/98/98\r\nf 23/23/23 22/22/22 103/103/103 102/102/102\r\nf 104/104/104 105/105/105 102/102/102 103/103/103\r\nf 105/105/105 100/100/100 98/98/98 102/102/102\r\nf 22/22/22 29/29/29 106/106/106 103/103/103\r\nf 29/29/29 28/28/28 107/107/107 106/106/106\r\nf 108/108/108 109/109/109 106/106/106 107/107/107\r\nf 109/109/109 104/104/104 103/103/103 106/106/106\r\nf 35/35/35 110/110/110 111/111/111 31/31/31\r\nf 110/110/110 112/112/112 113/113/113 111/111/111\r\nf 108/108/108 107/107/107 111/111/111 113/113/113\r\nf 107/107/107 28/28/28 31/31/31 111/111/111\r\nf 41/41/41 114/114/114 115/115/115 37/37/37\r\nf 114/114/114 116/116/116 117/117/117 115/115/115\r\nf 112/112/112 110/110/110 115/115/115 117/117/117\r\nf 110/110/110 35/35/35 37/37/37 115/115/115\r\nf 41/41/41 47/47/47 118/118/118 114/114/114\r\nf 47/47/47 46/46/46 119/119/119 118/118/118\r\nf 120/120/120 121/121/121 118/118/118 119/119/119\r\nf 121/121/121 116/116/116 114/114/114 118/118/118\r\nf 53/53/53 122/122/122 123/123/123 49/49/49\r\nf 122/122/122 124/124/124 125/125/125 123/123/123\r\nf 120/120/120 119/119/119 123/123/123 125/125/125\r\nf 119/119/119 46/46/46 49/49/49 123/123/123\r\nf 53/53/53 55/55/55 126/126/126 122/122/122\r\nf 55/55/55 59/59/59 127/127/127 126/126/126\r\nf 128/128/128 129/129/129 126/126/126 127/127/127\r\nf 129/129/129 124/124/124 122/122/122 126/126/126\r\nf 59/59/59 61/61/61 130/130/130 127/127/127\r\nf 61/61/61 65/65/65 131/131/131 130/130/130\r\nf 132/132/132 133/133/133 130/130/130 131/131/131\r\nf 133/133/133 128/128/128 127/127/127 130/130/130\r\nf 71/71/71 134/134/134 135/135/135 67/67/67\r\nf 134/134/134 136/136/136 137/137/137 135/135/135\r\nf 132/132/132 131/131/131 135/135/135 137/137/137\r\nf 131/131/131 65/65/65 67/67/67 135/135/135\r\nf 77/77/77 138/138/138 139/139/139 73/73/73\r\nf 138/138/138 140/140/140 141/141/141 139/139/139\r\nf 136/136/136 134/134/134 139/139/139 141/141/141\r\nf 134/134/134 71/71/71 73/73/73 139/139/139\r\nf 83/83/83 142/142/142 143/143/143 79/79/79\r\nf 142/142/142 144/144/144 145/145/145 143/143/143\r\nf 140/140/140 138/138/138 143/143/143 145/145/145\r\nf 138/138/138 77/77/77 79/79/79 143/143/143\r\nf 89/89/89 146/146/146 147/147/147 85/85/85\r\nf 146/146/146 148/148/148 149/149/149 147/147/147\r\nf 144/144/144 142/142/142 147/147/147 149/149/149\r\nf 142/142/142 83/83/83 85/85/85 147/147/147\r\nf 150/150/150 151/151/151 152/152/152 153/153/153\r\nf 151/151/151 154/154/154 155/155/155 152/152/152\r\nf 156/156/156 157/157/157 152/152/152 155/155/155\r\nf 157/157/157 158/158/158 153/153/153 152/152/152\r\nf 159/159/159 160/160/160 161/161/161 162/162/162\r\nf 160/160/160 163/163/163 164/164/164 161/161/161\r\nf 154/154/154 151/151/151 161/161/161 164/164/164\r\nf 151/151/151 150/150/150 162/162/162 161/161/161\r\nf 165/165/165 166/166/166 167/167/167 168/168/168\r\nf 166/166/166 169/169/169 170/170/170 167/167/167\r\nf 163/163/163 160/160/160 167/167/167 170/170/170\r\nf 160/160/160 159/159/159 168/168/168 167/167/167\r\nf 171/171/171 172/172/172 173/173/173 174/174/174\r\nf 172/172/172 169/169/169 166/166/166 173/173/173\r\nf 165/165/165 175/175/175 173/173/173 166/166/166\r\nf 175/175/175 176/176/176 174/174/174 173/173/173\r\nf 177/177/177 178/178/178 179/179/179 180/180/180\r\nf 178/178/178 171/171/171 174/174/174 179/179/179\r\nf 176/176/176 181/181/181 179/179/179 174/174/174\r\nf 181/181/181 182/182/182 180/180/180 179/179/179\r\nf 183/183/183 184/184/184 185/185/185 186/186/186\r\nf 184/184/184 177/177/177 180/180/180 185/185/185\r\nf 182/182/182 187/187/187 185/185/185 180/180/180\r\nf 187/187/187 188/188/188 186/186/186 185/185/185\r\nf 189/189/189 190/190/190 191/191/191 192/192/192\r\nf 190/190/190 183/183/183 186/186/186 191/191/191\r\nf 188/188/188 193/193/193 191/191/191 186/186/186\r\nf 193/193/193 194/194/194 192/192/192 191/191/191\r\nf 195/195/195 196/196/196 197/197/197 198/198/198\r\nf 196/196/196 189/189/189 192/192/192 197/197/197\r\nf 194/194/194 199/199/199 197/197/197 192/192/192\r\nf 199/199/199 200/200/200 198/198/198 197/197/197\r\nf 201/201/201 202/202/202 203/203/203 204/204/204\r\nf 202/202/202 195/195/195 198/198/198 203/203/203\r\nf 200/200/200 205/205/205 203/203/203 198/198/198\r\nf 205/205/205 206/206/206 204/204/204 203/203/203\r\nf 207/207/207 208/208/208 209/209/209 210/210/210\r\nf 208/208/208 201/201/201 204/204/204 209/209/209\r\nf 206/206/206 211/211/211 209/209/209 204/204/204\r\nf 211/211/211 212/212/212 210/210/210 209/209/209\r\nf 213/213/213 214/214/214 215/215/215 216/216/216\r\nf 214/214/214 217/217/217 218/218/218 215/215/215\r\nf 207/207/207 210/210/210 215/215/215 218/218/218\r\nf 210/210/210 212/212/212 216/216/216 215/215/215\r\nf 219/219/219 220/220/220 221/221/221 222/222/222\r\nf 220/220/220 223/223/223 224/224/224 221/221/221\r\nf 217/217/217 214/214/214 221/221/221 224/224/224\r\nf 214/214/214 213/213/213 222/222/222 221/221/221\r\nf 225/225/225 226/226/226 227/227/227 228/228/228\r\nf 226/226/226 229/229/229 230/230/230 227/227/227\r\nf 223/223/223 220/220/220 227/227/227 230/230/230\r\nf 220/220/220 219/219/219 228/228/228 227/227/227\r\nf 231/231/231 232/232/232 233/233/233 234/234/234\r\nf 232/232/232 229/229/229 226/226/226 233/233/233\r\nf 225/225/225 235/235/235 233/233/233 226/226/226\r\nf 235/235/235 236/236/236 234/234/234 233/233/233\r\nf 17/17/17 91/91/91 237/237/237 12/12/12\r\nf 91/91/91 90/90/90 238/238/238 237/237/237\r\nf 5/5/5 2/2/2 237/237/237 238/238/238\r\nf 2/2/2 1/1/1 12/12/12 237/237/237\r\nf 9/9/9 239/239/239 240/240/240 4/4/4\r\nf 239/239/239 241/241/241 242/242/242 240/240/240\r\nf 13/13/13 10/10/10 240/240/240 242/242/242\r\nf 10/10/10 1/1/1 4/4/4 240/240/240\r\nf 241/241/241 243/243/243 244/244/244 242/242/242\r\nf 243/243/243 245/245/245 246/246/246 244/244/244\r\nf 20/20/20 18/18/18 244/244/244 246/246/246\r\nf 18/18/18 13/13/13 242/242/242 244/244/244\r\nf 20/20/20 246/246/246 247/247/247 24/24/24\r\nf 246/246/246 245/245/245 248/248/248 247/247/247\r\nf 249/249/249 250/250/250 247/247/247 248/248/248\r\nf 250/250/250 26/26/26 24/24/24 247/247/247\r\nf 26/26/26 250/250/250 251/251/251 32/32/32\r\nf 250/250/250 249/249/249 252/252/252 251/251/251\r\nf 253/253/253 254/254/254 251/251/251 252/252/252\r\nf 254/254/254 33/33/33 32/32/32 251/251/251\r\nf 33/33/33 254/254/254 255/255/255 38/38/38\r\nf 254/254/254 253/253/253 256/256/256 255/255/255\r\nf 257/257/257 258/258/258 255/255/255 256/256/256\r\nf 258/258/258 39/39/39 38/38/38 255/255/255\r\nf 257/257/257 259/259/259 260/260/260 258/258/258\r\nf 259/259/259 261/261/261 262/262/262 260/260/260\r\nf 44/44/44 42/42/42 260/260/260 262/262/262\r\nf 42/42/42 39/39/39 258/258/258 260/260/260\r\nf 44/44/44 262/262/262 263/263/263 50/50/50\r\nf 262/262/262 261/261/261 264/264/264 263/263/263\r\nf 265/265/265 266/266/266 263/263/263 264/264/264\r\nf 266/266/266 51/51/51 50/50/50 263/263/263\r\nf 51/51/51 266/266/266 267/267/267 56/56/56\r\nf 266/266/266 265/265/265 268/268/268 267/267/267\r\nf 269/269/269 270/270/270 267/267/267 268/268/268\r\nf 270/270/270 57/57/57 56/56/56 267/267/267\r\nf 57/57/57 270/270/270 271/271/271 62/62/62\r\nf 270/270/270 269/269/269 272/272/272 271/271/271\r\nf 273/273/273 274/274/274 271/271/271 272/272/272\r\nf 274/274/274 63/63/63 62/62/62 271/271/271\r\nf 63/63/63 274/274/274 275/275/275 68/68/68\r\nf 274/274/274 273/273/273 276/276/276 275/275/275\r\nf 277/277/277 278/278/278 275/275/275 276/276/276\r\nf 278/278/278 69/69/69 68/68/68 275/275/275\r\nf 69/69/69 278/278/278 279/279/279 74/74/74\r\nf 278/278/278 277/277/277 280/280/280 279/279/279\r\nf 281/281/281 282/282/282 279/279/279 280/280/280\r\nf 282/282/282 75/75/75 74/74/74 279/279/279\r\nf 75/75/75 282/282/282 283/283/283 80/80/80\r\nf 282/282/282 281/281/281 284/284/284 283/283/283\r\nf 285/285/285 286/286/286 283/283/283 284/284/284\r\nf 286/286/286 81/81/81 80/80/80 283/283/283\r\nf 81/81/81 286/286/286 287/287/287 86/86/86\r\nf 286/286/286 285/285/285 288/288/288 287/287/287\r\nf 289/289/289 290/290/290 287/287/287 288/288/288\r\nf 290/290/290 87/87/87 86/86/86 287/287/287\r\nf 150/150/150 153/153/153 291/291/291 292/292/292\r\nf 153/153/153 158/158/158 293/293/293 291/291/291\r\nf 97/97/97 96/96/96 291/291/291 293/293/293\r\nf 96/96/96 95/95/95 292/292/292 291/291/291\r\nf 100/100/100 294/294/294 295/295/295 101/101/101\r\nf 294/294/294 159/159/159 162/162/162 295/295/295\r\nf 150/150/150 292/292/292 295/295/295 162/162/162\r\nf 292/292/292 95/95/95 101/101/101 295/295/295\r\nf 165/165/165 168/168/168 296/296/296 297/297/297\r\nf 168/168/168 159/159/159 294/294/294 296/296/296\r\nf 100/100/100 105/105/105 296/296/296 294/294/294\r\nf 105/105/105 104/104/104 297/297/297 296/296/296\r\nf 176/176/176 175/175/175 298/298/298 299/299/299\r\nf 175/175/175 165/165/165 297/297/297 298/298/298\r\nf 104/104/104 109/109/109 298/298/298 297/297/297\r\nf 109/109/109 108/108/108 299/299/299 298/298/298\r\nf 112/112/112 300/300/300 301/301/301 113/113/113\r\nf 300/300/300 182/182/182 181/181/181 301/301/301\r\nf 176/176/176 299/299/299 301/301/301 181/181/181\r\nf 299/299/299 108/108/108 113/113/113 301/301/301\r\nf 116/116/116 302/302/302 303/303/303 117/117/117\r\nf 302/302/302 188/188/188 187/187/187 303/303/303\r\nf 182/182/182 300/300/300 303/303/303 187/187/187\r\nf 300/300/300 112/112/112 117/117/117 303/303/303\r\nf 194/194/194 193/193/193 304/304/304 305/305/305\r\nf 193/193/193 188/188/188 302/302/302 304/304/304\r\nf 116/116/116 121/121/121 304/304/304 302/302/302\r\nf 121/121/121 120/120/120 305/305/305 304/304/304\r\nf 200/200/200 199/199/199 306/306/306 307/307/307\r\nf 199/199/199 194/194/194 305/305/305 306/306/306\r\nf 120/120/120 125/125/125 306/306/306 305/305/305\r\nf 125/125/125 124/124/124 307/307/307 306/306/306\r\nf 206/206/206 205/205/205 308/308/308 309/309/309\r\nf 205/205/205 200/200/200 307/307/307 308/308/308\r\nf 124/124/124 129/129/129 308/308/308 307/307/307\r\nf 129/129/129 128/128/128 309/309/309 308/308/308\r\nf 212/212/212 211/211/211 310/310/310 311/311/311\r\nf 211/211/211 206/206/206 309/309/309 310/310/310\r\nf 128/128/128 133/133/133 310/310/310 309/309/309\r\nf 133/133/133 132/132/132 311/311/311 310/310/310\r\nf 136/136/136 312/312/312 313/313/313 137/137/137\r\nf 312/312/312 213/213/213 216/216/216 313/313/313\r\nf 212/212/212 311/311/311 313/313/313 216/216/216\r\nf 311/311/311 132/132/132 137/137/137 313/313/313\r\nf 140/140/140 314/314/314 315/315/315 141/141/141\r\nf 314/314/314 219/219/219 222/222/222 315/315/315\r\nf 213/213/213 312/312/312 315/315/315 222/222/222\r\nf 312/312/312 136/136/136 141/141/141 315/315/315\r\nf 144/144/144 316/316/316 317/317/317 145/145/145\r\nf 316/316/316 225/225/225 228/228/228 317/317/317\r\nf 219/219/219 314/314/314 317/317/317 228/228/228\r\nf 314/314/314 140/140/140 145/145/145 317/317/317\r\nf 236/236/236 235/235/235 318/318/318 319/319/319\r\nf 235/235/235 225/225/225 316/316/316 318/318/318\r\nf 144/144/144 149/149/149 318/318/318 316/316/316\r\nf 149/149/149 148/148/148 319/319/319 318/318/318\r\nf 7/7/7 6/6/6 320/320/320 321/321/321\r\nf 6/6/6 5/5/5 322/322/322 320/320/320\r\nf 323/323/323 324/324/324 320/320/320 322/322/322\r\nf 324/324/324 325/325/325 321/321/321 320/320/320\r\nf 9/9/9 8/8/8 326/326/326 327/327/327\r\nf 8/8/8 7/7/7 321/321/321 326/326/326\r\nf 325/325/325 328/328/328 326/326/326 321/321/321\r\nf 328/328/328 329/329/329 327/327/327 326/326/326\r\nf 89/89/89 88/88/88 330/330/330 331/331/331\r\nf 88/88/88 87/87/87 332/332/332 330/330/330\r\nf 333/333/333 334/334/334 330/330/330 332/332/332\r\nf 334/334/334 335/335/335 331/331/331 330/330/330\r\nf 90/90/90 93/93/93 336/336/336 337/337/337\r\nf 93/93/93 97/97/97 338/338/338 336/336/336\r\nf 339/339/339 340/340/340 336/336/336 338/338/338\r\nf 340/340/340 341/341/341 337/337/337 336/336/336\r\nf 148/148/148 146/146/146 342/342/342 343/343/343\r\nf 146/146/146 89/89/89 331/331/331 342/342/342\r\nf 335/335/335 344/344/344 342/342/342 331/331/331\r\nf 344/344/344 345/345/345 343/343/343 342/342/342\r\nf 156/156/156 155/155/155 346/346/346 347/347/347\r\nf 155/155/155 154/154/154 348/348/348 346/346/346\r\nf 349/349/349 350/350/350 346/346/346 348/348/348\r\nf 350/350/350 351/351/351 347/347/347 346/346/346\r\nf 158/158/158 157/157/157 352/352/352 353/353/353\r\nf 157/157/157 156/156/156 347/347/347 352/352/352\r\nf 351/351/351 354/354/354 352/352/352 347/347/347\r\nf 354/354/354 355/355/355 353/353/353 352/352/352\r\nf 154/154/154 164/164/164 356/356/356 348/348/348\r\nf 164/164/164 163/163/163 357/357/357 356/356/356\r\nf 358/358/358 359/359/359 356/356/356 357/357/357\r\nf 359/359/359 349/349/349 348/348/348 356/356/356\r\nf 163/163/163 170/170/170 360/360/360 357/357/357\r\nf 170/170/170 169/169/169 361/361/361 360/360/360\r\nf 362/362/362 363/363/363 360/360/360 361/361/361\r\nf 363/363/363 358/358/358 357/357/357 360/360/360\r\nf 169/169/169 172/172/172 364/364/364 361/361/361\r\nf 172/172/172 171/171/171 365/365/365 364/364/364\r\nf 366/366/366 367/367/367 364/364/364 365/365/365\r\nf 367/367/367 362/362/362 361/361/361 364/364/364\r\nf 171/171/171 178/178/178 368/368/368 365/365/365\r\nf 178/178/178 177/177/177 369/369/369 368/368/368\r\nf 370/370/370 371/371/371 368/368/368 369/369/369\r\nf 371/371/371 366/366/366 365/365/365 368/368/368\r\nf 177/177/177 184/184/184 372/372/372 369/369/369\r\nf 184/184/184 183/183/183 373/373/373 372/372/372\r\nf 374/374/374 375/375/375 372/372/372 373/373/373\r\nf 375/375/375 370/370/370 369/369/369 372/372/372\r\nf 183/183/183 190/190/190 376/376/376 373/373/373\r\nf 190/190/190 189/189/189 377/377/377 376/376/376\r\nf 378/378/378 379/379/379 376/376/376 377/377/377\r\nf 379/379/379 374/374/374 373/373/373 376/376/376\r\nf 189/189/189 196/196/196 380/380/380 377/377/377\r\nf 196/196/196 195/195/195 381/381/381 380/380/380\r\nf 382/382/382 383/383/383 380/380/380 381/381/381\r\nf 383/383/383 378/378/378 377/377/377 380/380/380\r\nf 195/195/195 202/202/202 384/384/384 381/381/381\r\nf 202/202/202 201/201/201 385/385/385 384/384/384\r\nf 386/386/386 387/387/387 384/384/384 385/385/385\r\nf 387/387/387 382/382/382 381/381/381 384/384/384\r\nf 201/201/201 208/208/208 388/388/388 385/385/385\r\nf 208/208/208 207/207/207 389/389/389 388/388/388\r\nf 390/390/390 391/391/391 388/388/388 389/389/389\r\nf 391/391/391 386/386/386 385/385/385 388/388/388\r\nf 207/207/207 218/218/218 392/392/392 389/389/389\r\nf 218/218/218 217/217/217 393/393/393 392/392/392\r\nf 394/394/394 395/395/395 392/392/392 393/393/393\r\nf 395/395/395 390/390/390 389/389/389 392/392/392\r\nf 217/217/217 224/224/224 396/396/396 393/393/393\r\nf 224/224/224 223/223/223 397/397/397 396/396/396\r\nf 398/398/398 399/399/399 396/396/396 397/397/397\r\nf 399/399/399 394/394/394 393/393/393 396/396/396\r\nf 223/223/223 230/230/230 400/400/400 397/397/397\r\nf 230/230/230 229/229/229 401/401/401 400/400/400\r\nf 402/402/402 403/403/403 400/400/400 401/401/401\r\nf 403/403/403 398/398/398 397/397/397 400/400/400\r\nf 229/229/229 232/232/232 404/404/404 401/401/401\r\nf 232/232/232 231/231/231 405/405/405 404/404/404\r\nf 406/406/406 407/407/407 404/404/404 405/405/405\r\nf 407/407/407 402/402/402 401/401/401 404/404/404\r\nf 231/231/231 234/234/234 408/408/408 405/405/405\r\nf 234/234/234 236/236/236 409/409/409 408/408/408\r\nf 410/410/410 411/411/411 408/408/408 409/409/409\r\nf 411/411/411 406/406/406 405/405/405 408/408/408\r\nf 5/5/5 238/238/238 412/412/412 322/322/322\r\nf 238/238/238 90/90/90 337/337/337 412/412/412\r\nf 341/341/341 413/413/413 412/412/412 337/337/337\r\nf 413/413/413 323/323/323 322/322/322 412/412/412\r\nf 241/241/241 239/239/239 414/414/414 415/415/415\r\nf 239/239/239 9/9/9 327/327/327 414/414/414\r\nf 329/329/329 416/416/416 414/414/414 327/327/327\r\nf 416/416/416 417/417/417 415/415/415 414/414/414\r\nf 245/245/245 243/243/243 418/418/418 419/419/419\r\nf 243/243/243 241/241/241 415/415/415 418/418/418\r\nf 417/417/417 420/420/420 418/418/418 415/415/415\r\nf 420/420/420 421/421/421 419/419/419 418/418/418\r\nf 249/249/249 248/248/248 422/422/422 423/423/423\r\nf 248/248/248 245/245/245 419/419/419 422/422/422\r\nf 421/421/421 424/424/424 422/422/422 419/419/419\r\nf 424/424/424 425/425/425 423/423/423 422/422/422\r\nf 253/253/253 252/252/252 426/426/426 427/427/427\r\nf 252/252/252 249/249/249 423/423/423 426/426/426\r\nf 425/425/425 428/428/428 426/426/426 423/423/423\r\nf 428/428/428 429/429/429 427/427/427 426/426/426\r\nf 257/257/257 256/256/256 430/430/430 431/431/431\r\nf 256/256/256 253/253/253 427/427/427 430/430/430\r\nf 429/429/429 432/432/432 430/430/430 427/427/427\r\nf 432/432/432 433/433/433 431/431/431 430/430/430\r\nf 261/261/261 259/259/259 434/434/434 435/435/435\r\nf 259/259/259 257/257/257 431/431/431 434/434/434\r\nf 433/433/433 436/436/436 434/434/434 431/431/431\r\nf 436/436/436 437/437/437 435/435/435 434/434/434\r\nf 265/265/265 264/264/264 438/438/438 439/439/439\r\nf 264/264/264 261/261/261 435/435/435 438/438/438\r\nf 437/437/437 440/440/440 438/438/438 435/435/435\r\nf 440/440/440 441/441/441 439/439/439 438/438/438\r\nf 269/269/269 268/268/268 442/442/442 443/443/443\r\nf 268/268/268 265/265/265 439/439/439 442/442/442\r\nf 441/441/441 444/444/444 442/442/442 439/439/439\r\nf 444/444/444 445/445/445 443/443/443 442/442/442\r\nf 273/273/273 272/272/272 446/446/446 447/447/447\r\nf 272/272/272 269/269/269 443/443/443 446/446/446\r\nf 445/445/445 448/448/448 446/446/446 443/443/443\r\nf 448/448/448 449/449/449 447/447/447 446/446/446\r\nf 277/277/277 276/276/276 450/450/450 451/451/451\r\nf 276/276/276 273/273/273 447/447/447 450/450/450\r\nf 449/449/449 452/452/452 450/450/450 447/447/447\r\nf 452/452/452 453/453/453 451/451/451 450/450/450\r\nf 281/281/281 280/280/280 454/454/454 455/455/455\r\nf 280/280/280 277/277/277 451/451/451 454/454/454\r\nf 453/453/453 456/456/456 454/454/454 451/451/451\r\nf 456/456/456 457/457/457 455/455/455 454/454/454\r\nf 285/285/285 284/284/284 458/458/458 459/459/459\r\nf 284/284/284 281/281/281 455/455/455 458/458/458\r\nf 457/457/457 460/460/460 458/458/458 455/455/455\r\nf 460/460/460 461/461/461 459/459/459 458/458/458\r\nf 289/289/289 288/288/288 462/462/462 463/463/463\r\nf 288/288/288 285/285/285 459/459/459 462/462/462\r\nf 461/461/461 464/464/464 462/462/462 459/459/459\r\nf 464/464/464 465/465/465 463/463/463 462/462/462\r\nf 87/87/87 290/290/290 466/466/466 332/332/332\r\nf 290/290/290 289/289/289 463/463/463 466/466/466\r\nf 465/465/465 467/467/467 466/466/466 463/463/463\r\nf 467/467/467 333/333/333 332/332/332 466/466/466\r\nf 97/97/97 293/293/293 468/468/468 338/338/338\r\nf 293/293/293 158/158/158 353/353/353 468/468/468\r\nf 355/355/355 469/469/469 468/468/468 353/353/353\r\nf 469/469/469 339/339/339 338/338/338 468/468/468\r\nf 236/236/236 319/319/319 470/470/470 409/409/409\r\nf 319/319/319 148/148/148 343/343/343 470/470/470\r\nf 345/345/345 471/471/471 470/470/470 343/343/343\r\nf 471/471/471 410/410/410 409/409/409 470/470/470\r\nf 472/472/472 473/473/473 474/474/474 475/475/475\r\nf 473/473/473 476/476/476 477/477/477 474/474/474\r\nf 478/478/478 479/479/479 474/474/474 477/477/477\r\nf 479/479/479 480/480/480 475/475/475 474/474/474\r\nf 472/472/472 481/481/481 482/482/482 483/483/483\r\nf 481/481/481 484/484/484 485/485/485 482/482/482\r\nf 486/486/486 487/487/487 482/482/482 485/485/485\r\nf 487/487/487 488/488/488 483/483/483 482/482/482\r\nf 488/488/488 487/487/487 489/489/489 490/490/490\r\nf 487/487/487 486/486/486 491/491/491 489/489/489\r\nf 492/492/492 493/493/493 489/489/489 491/491/491\r\nf 493/493/493 494/494/494 490/490/490 489/489/489\r\nf 494/494/494 493/493/493 495/495/495 496/496/496\r\nf 493/493/493 492/492/492 497/497/497 495/495/495\r\nf 498/498/498 499/499/499 495/495/495 497/497/497\r\nf 499/499/499 500/500/500 496/496/496 495/495/495\r\nf 498/498/498 501/501/501 502/502/502 499/499/499\r\nf 501/501/501 503/503/503 504/504/504 502/502/502\r\nf 505/505/505 506/506/506 502/502/502 504/504/504\r\nf 506/506/506 500/500/500 499/499/499 502/502/502\r\nf 503/503/503 507/507/507 508/508/508 504/504/504\r\nf 507/507/507 509/509/509 510/510/510 508/508/508\r\nf 511/511/511 512/512/512 508/508/508 510/510/510\r\nf 512/512/512 505/505/505 504/504/504 508/508/508\r\nf 511/511/511 510/510/510 513/513/513 514/514/514\r\nf 510/510/510 509/509/509 515/515/515 513/513/513\r\nf 516/516/516 517/517/517 513/513/513 515/515/515\r\nf 517/517/517 518/518/518 514/514/514 513/513/513\r\nf 516/516/516 519/519/519 520/520/520 517/517/517\r\nf 519/519/519 521/521/521 522/522/522 520/520/520\r\nf 523/523/523 524/524/524 520/520/520 522/522/522\r\nf 524/524/524 518/518/518 517/517/517 520/520/520\r\nf 521/521/521 525/525/525 526/526/526 522/522/522\r\nf 525/525/525 527/527/527 528/528/528 526/526/526\r\nf 529/529/529 530/530/530 526/526/526 528/528/528\r\nf 530/530/530 523/523/523 522/522/522 526/526/526\r\nf 527/527/527 531/531/531 532/532/532 528/528/528\r\nf 531/531/531 533/533/533 534/534/534 532/532/532\r\nf 535/535/535 536/536/536 532/532/532 534/534/534\r\nf 536/536/536 529/529/529 528/528/528 532/532/532\r\nf 533/533/533 537/537/537 538/538/538 534/534/534\r\nf 537/537/537 539/539/539 540/540/540 538/538/538\r\nf 541/541/541 542/542/542 538/538/538 540/540/540\r\nf 542/542/542 535/535/535 534/534/534 538/538/538\r\nf 539/539/539 543/543/543 544/544/544 540/540/540\r\nf 543/543/543 545/545/545 546/546/546 544/544/544\r\nf 547/547/547 548/548/548 544/544/544 546/546/546\r\nf 548/548/548 541/541/541 540/540/540 544/544/544\r\nf 545/545/545 549/549/549 550/550/550 546/546/546\r\nf 549/549/549 551/551/551 552/552/552 550/550/550\r\nf 553/553/553 554/554/554 550/550/550 552/552/552\r\nf 554/554/554 547/547/547 546/546/546 550/550/550\r\nf 551/551/551 555/555/555 556/556/556 552/552/552\r\nf 555/555/555 557/557/557 558/558/558 556/556/556\r\nf 559/559/559 560/560/560 556/556/556 558/558/558\r\nf 560/560/560 553/553/553 552/552/552 556/556/556\r\nf 561/561/561 562/562/562 563/563/563 564/564/564\r\nf 562/562/562 565/565/565 566/566/566 563/563/563\r\nf 567/567/567 568/568/568 563/563/563 566/566/566\r\nf 568/568/568 484/484/484 564/564/564 563/563/563\r\nf 486/486/486 485/485/485 569/569/569 570/570/570\r\nf 485/485/485 484/484/484 568/568/568 569/569/569\r\nf 567/567/567 571/571/571 569/569/569 568/568/568\r\nf 571/571/571 572/572/572 570/570/570 569/569/569\r\nf 486/486/486 570/570/570 573/573/573 491/491/491\r\nf 570/570/570 572/572/572 574/574/574 573/573/573\r\nf 575/575/575 576/576/576 573/573/573 574/574/574\r\nf 576/576/576 492/492/492 491/491/491 573/573/573\r\nf 492/492/492 576/576/576 577/577/577 497/497/497\r\nf 576/576/576 575/575/575 578/578/578 577/577/577\r\nf 579/579/579 580/580/580 577/577/577 578/578/578\r\nf 580/580/580 498/498/498 497/497/497 577/577/577\r\nf 503/503/503 501/501/501 581/581/581 582/582/582\r\nf 501/501/501 498/498/498 580/580/580 581/581/581\r\nf 579/579/579 583/583/583 581/581/581 580/580/580\r\nf 583/583/583 584/584/584 582/582/582 581/581/581\r\nf 509/509/509 507/507/507 585/585/585 586/586/586\r\nf 507/507/507 503/503/503 582/582/582 585/585/585\r\nf 584/584/584 587/587/587 585/585/585 582/582/582\r\nf 587/587/587 588/588/588 586/586/586 585/585/585\r\nf 509/509/509 586/586/586 589/589/589 515/515/515\r\nf 586/586/586 588/588/588 590/590/590 589/589/589\r\nf 591/591/591 592/592/592 589/589/589 590/590/590\r\nf 592/592/592 516/516/516 515/515/515 589/589/589\r\nf 521/521/521 519/519/519 593/593/593 594/594/594\r\nf 519/519/519 516/516/516 592/592/592 593/593/593\r\nf 591/591/591 595/595/595 593/593/593 592/592/592\r\nf 595/595/595 596/596/596 594/594/594 593/593/593\r\nf 521/521/521 594/594/594 597/597/597 525/525/525\r\nf 594/594/594 596/596/596 598/598/598 597/597/597\r\nf 599/599/599 600/600/600 597/597/597 598/598/598\r\nf 600/600/600 527/527/527 525/525/525 597/597/597\r\nf 527/527/527 600/600/600 601/601/601 531/531/531\r\nf 600/600/600 599/599/599 602/602/602 601/601/601\r\nf 603/603/603 604/604/604 601/601/601 602/602/602\r\nf 604/604/604 533/533/533 531/531/531 601/601/601\r\nf 539/539/539 537/537/537 605/605/605 606/606/606\r\nf 537/537/537 533/533/533 604/604/604 605/605/605\r\nf 603/603/603 607/607/607 605/605/605 604/604/604\r\nf 607/607/607 608/608/608 606/606/606 605/605/605\r\nf 545/545/545 543/543/543 609/609/609 610/610/610\r\nf 543/543/543 539/539/539 606/606/606 609/609/609\r\nf 608/608/608 611/611/611 609/609/609 606/606/606\r\nf 611/611/611 612/612/612 610/610/610 609/609/609\r\nf 551/551/551 549/549/549 613/613/613 614/614/614\r\nf 549/549/549 545/545/545 610/610/610 613/613/613\r\nf 612/612/612 615/615/615 613/613/613 610/610/610\r\nf 615/615/615 616/616/616 614/614/614 613/613/613\r\nf 557/557/557 555/555/555 617/617/617 618/618/618\r\nf 555/555/555 551/551/551 614/614/614 617/617/617\r\nf 616/616/616 619/619/619 617/617/617 614/614/614\r\nf 619/619/619 620/620/620 618/618/618 617/617/617\r\nf 621/621/621 622/622/622 623/623/623 624/624/624\r\nf 622/622/622 625/625/625 626/626/626 623/623/623\r\nf 627/627/627 628/628/628 623/623/623 626/626/626\r\nf 628/628/628 629/629/629 624/624/624 623/623/623\r\nf 630/630/630 631/631/631 632/632/632 633/633/633\r\nf 631/631/631 621/621/621 624/624/624 632/632/632\r\nf 629/629/629 634/634/634 632/632/632 624/624/624\r\nf 634/634/634 635/635/635 633/633/633 632/632/632\r\nf 636/636/636 637/637/637 638/638/638 639/639/639\r\nf 637/637/637 630/630/630 633/633/633 638/638/638\r\nf 635/635/635 640/640/640 638/638/638 633/633/633\r\nf 640/640/640 641/641/641 639/639/639 638/638/638\r\nf 642/642/642 643/643/643 644/644/644 645/645/645\r\nf 643/643/643 646/646/646 647/647/647 644/644/644\r\nf 636/636/636 639/639/639 644/644/644 647/647/647\r\nf 639/639/639 641/641/641 645/645/645 644/644/644\r\nf 648/648/648 649/649/649 650/650/650 651/651/651\r\nf 649/649/649 652/652/652 653/653/653 650/650/650\r\nf 646/646/646 643/643/643 650/650/650 653/653/653\r\nf 643/643/643 642/642/642 651/651/651 650/650/650\r\nf 654/654/654 655/655/655 656/656/656 657/657/657\r\nf 655/655/655 658/658/658 659/659/659 656/656/656\r\nf 652/652/652 649/649/649 656/656/656 659/659/659\r\nf 649/649/649 648/648/648 657/657/657 656/656/656\r\nf 660/660/660 661/661/661 662/662/662 663/663/663\r\nf 661/661/661 664/664/664 665/665/665 662/662/662\r\nf 658/658/658 655/655/655 662/662/662 665/665/665\r\nf 655/655/655 654/654/654 663/663/663 662/662/662\r\nf 666/666/666 667/667/667 668/668/668 669/669/669\r\nf 667/667/667 670/670/670 671/671/671 668/668/668\r\nf 664/664/664 661/661/661 668/668/668 671/671/671\r\nf 661/661/661 660/660/660 669/669/669 668/668/668\r\nf 672/672/672 673/673/673 674/674/674 675/675/675\r\nf 673/673/673 676/676/676 677/677/677 674/674/674\r\nf 670/670/670 667/667/667 674/674/674 677/677/677\r\nf 667/667/667 666/666/666 675/675/675 674/674/674\r\nf 678/678/678 679/679/679 680/680/680 681/681/681\r\nf 679/679/679 682/682/682 683/683/683 680/680/680\r\nf 676/676/676 673/673/673 680/680/680 683/683/683\r\nf 673/673/673 672/672/672 681/681/681 680/680/680\r\nf 684/684/684 685/685/685 686/686/686 687/687/687\r\nf 685/685/685 682/682/682 679/679/679 686/686/686\r\nf 678/678/678 688/688/688 686/686/686 679/679/679\r\nf 688/688/688 689/689/689 687/687/687 686/686/686\r\nf 690/690/690 691/691/691 692/692/692 693/693/693\r\nf 691/691/691 684/684/684 687/687/687 692/692/692\r\nf 689/689/689 694/694/694 692/692/692 687/687/687\r\nf 694/694/694 695/695/695 693/693/693 692/692/692\r\nf 696/696/696 697/697/697 698/698/698 699/699/699\r\nf 697/697/697 690/690/690 693/693/693 698/698/698\r\nf 695/695/695 700/700/700 698/698/698 693/693/693\r\nf 700/700/700 701/701/701 699/699/699 698/698/698\r\nf 702/702/702 703/703/703 704/704/704 705/705/705\r\nf 703/703/703 706/706/706 707/707/707 704/704/704\r\nf 696/696/696 699/699/699 704/704/704 707/707/707\r\nf 699/699/699 701/701/701 705/705/705 704/704/704\r\nf 484/484/484 481/481/481 708/708/708 564/564/564\r\nf 481/481/481 472/472/472 475/475/475 708/708/708\r\nf 480/480/480 709/709/709 708/708/708 475/475/475\r\nf 709/709/709 561/561/561 564/564/564 708/708/708\r\nf 476/476/476 473/473/473 710/710/710 711/711/711\r\nf 473/473/473 472/472/472 483/483/483 710/710/710\r\nf 488/488/488 712/712/712 710/710/710 483/483/483\r\nf 712/712/712 713/713/713 711/711/711 710/710/710\r\nf 713/713/713 712/712/712 714/714/714 715/715/715\r\nf 712/712/712 488/488/488 490/490/490 714/714/714\r\nf 494/494/494 716/716/716 714/714/714 490/490/490\r\nf 716/716/716 717/717/717 715/715/715 714/714/714\r\nf 494/494/494 496/496/496 718/718/718 716/716/716\r\nf 496/496/496 500/500/500 719/719/719 718/718/718\r\nf 720/720/720 721/721/721 718/718/718 719/719/719\r\nf 721/721/721 717/717/717 716/716/716 718/718/718\r\nf 500/500/500 506/506/506 722/722/722 719/719/719\r\nf 506/506/506 505/505/505 723/723/723 722/722/722\r\nf 724/724/724 725/725/725 722/722/722 723/723/723\r\nf 725/725/725 720/720/720 719/719/719 722/722/722\r\nf 505/505/505 512/512/512 726/726/726 723/723/723\r\nf 512/512/512 511/511/511 727/727/727 726/726/726\r\nf 728/728/728 729/729/729 726/726/726 727/727/727\r\nf 729/729/729 724/724/724 723/723/723 726/726/726\r\nf 728/728/728 727/727/727 730/730/730 731/731/731\r\nf 727/727/727 511/511/511 514/514/514 730/730/730\r\nf 518/518/518 732/732/732 730/730/730 514/514/514\r\nf 732/732/732 733/733/733 731/731/731 730/730/730\r\nf 518/518/518 524/524/524 734/734/734 732/732/732\r\nf 524/524/524 523/523/523 735/735/735 734/734/734\r\nf 736/736/736 737/737/737 734/734/734 735/735/735\r\nf 737/737/737 733/733/733 732/732/732 734/734/734\r\nf 523/523/523 530/530/530 738/738/738 735/735/735\r\nf 530/530/530 529/529/529 739/739/739 738/738/738\r\nf 740/740/740 741/741/741 738/738/738 739/739/739\r\nf 741/741/741 736/736/736 735/735/735 738/738/738\r\nf 529/529/529 536/536/536 742/742/742 739/739/739\r\nf 536/536/536 535/535/535 743/743/743 742/742/742\r\nf 744/744/744 745/745/745 742/742/742 743/743/743\r\nf 745/745/745 740/740/740 739/739/739 742/742/742\r\nf 535/535/535 542/542/542 746/746/746 743/743/743\r\nf 542/542/542 541/541/541 747/747/747 746/746/746\r\nf 748/748/748 749/749/749 746/746/746 747/747/747\r\nf 749/749/749 744/744/744 743/743/743 746/746/746\r\nf 541/541/541 548/548/548 750/750/750 747/747/747\r\nf 548/548/548 547/547/547 751/751/751 750/750/750\r\nf 752/752/752 753/753/753 750/750/750 751/751/751\r\nf 753/753/753 748/748/748 747/747/747 750/750/750\r\nf 547/547/547 554/554/554 754/754/754 751/751/751\r\nf 554/554/554 553/553/553 755/755/755 754/754/754\r\nf 756/756/756 757/757/757 754/754/754 755/755/755\r\nf 757/757/757 752/752/752 751/751/751 754/754/754\r\nf 553/553/553 560/560/560 758/758/758 755/755/755\r\nf 560/560/560 559/559/559 759/759/759 758/758/758\r\nf 760/760/760 761/761/761 758/758/758 759/759/759\r\nf 761/761/761 756/756/756 755/755/755 758/758/758\r\nf 621/621/621 762/762/762 763/763/763 622/622/622\r\nf 762/762/762 567/567/567 566/566/566 763/763/763\r\nf 565/565/565 764/764/764 763/763/763 566/566/566\r\nf 764/764/764 625/625/625 622/622/622 763/763/763\r\nf 572/572/572 571/571/571 765/765/765 766/766/766\r\nf 571/571/571 567/567/567 762/762/762 765/765/765\r\nf 621/621/621 631/631/631 765/765/765 762/762/762\r\nf 631/631/631 630/630/630 766/766/766 765/765/765\r\nf 636/636/636 767/767/767 768/768/768 637/637/637\r\nf 767/767/767 575/575/575 574/574/574 768/768/768\r\nf 572/572/572 766/766/766 768/768/768 574/574/574\r\nf 766/766/766 630/630/630 637/637/637 768/768/768\r\nf 646/646/646 769/769/769 770/770/770 647/647/647\r\nf 769/769/769 579/579/579 578/578/578 770/770/770\r\nf 575/575/575 767/767/767 770/770/770 578/578/578\r\nf 767/767/767 636/636/636 647/647/647 770/770/770\r\nf 584/584/584 583/583/583 771/771/771 772/772/772\r\nf 583/583/583 579/579/579 769/769/769 771/771/771\r\nf 646/646/646 653/653/653 771/771/771 769/769/769\r\nf 653/653/653 652/652/652 772/772/772 771/771/771\r\nf 588/588/588 587/587/587 773/773/773 774/774/774\r\nf 587/587/587 584/584/584 772/772/772 773/773/773\r\nf 652/652/652 659/659/659 773/773/773 772/772/772\r\nf 659/659/659 658/658/658 774/774/774 773/773/773\r\nf 664/664/664 775/775/775 776/776/776 665/665/665\r\nf 775/775/775 591/591/591 590/590/590 776/776/776\r\nf 588/588/588 774/774/774 776/776/776 590/590/590\r\nf 774/774/774 658/658/658 665/665/665 776/776/776\r\nf 670/670/670 777/777/777 778/778/778 671/671/671\r\nf 777/777/777 596/596/596 595/595/595 778/778/778\r\nf 591/591/591 775/775/775 778/778/778 595/595/595\r\nf 775/775/775 664/664/664 671/671/671 778/778/778\r\nf 676/676/676 779/779/779 780/780/780 677/677/677\r\nf 779/779/779 599/599/599 598/598/598 780/780/780\r\nf 596/596/596 777/777/777 780/780/780 598/598/598\r\nf 777/777/777 670/670/670 677/677/677 780/780/780\r\nf 682/682/682 781/781/781 782/782/782 683/683/683\r\nf 781/781/781 603/603/603 602/602/602 782/782/782\r\nf 599/599/599 779/779/779 782/782/782 602/602/602\r\nf 779/779/779 676/676/676 683/683/683 782/782/782\r\nf 608/608/608 607/607/607 783/783/783 784/784/784\r\nf 607/607/607 603/603/603 781/781/781 783/783/783\r\nf 682/682/682 685/685/685 783/783/783 781/781/781\r\nf 685/685/685 684/684/684 784/784/784 783/783/783\r\nf 612/612/612 611/611/611 785/785/785 786/786/786\r\nf 611/611/611 608/608/608 784/784/784 785/785/785\r\nf 684/684/684 691/691/691 785/785/785 784/784/784\r\nf 691/691/691 690/690/690 786/786/786 785/785/785\r\nf 616/616/616 615/615/615 787/787/787 788/788/788\r\nf 615/615/615 612/612/612 786/786/786 787/787/787\r\nf 690/690/690 697/697/697 787/787/787 786/786/786\r\nf 697/697/697 696/696/696 788/788/788 787/787/787\r\nf 706/706/706 789/789/789 790/790/790 707/707/707\r\nf 789/789/789 620/620/620 619/619/619 790/790/790\r\nf 616/616/616 788/788/788 790/790/790 619/619/619\r\nf 788/788/788 696/696/696 707/707/707 790/790/790\r\nf 478/478/478 791/791/791 792/792/792 479/479/479\r\nf 791/791/791 793/793/793 794/794/794 792/792/792\r\nf 795/795/795 796/796/796 792/792/792 794/794/794\r\nf 796/796/796 480/480/480 479/479/479 792/792/792\r\nf 476/476/476 797/797/797 798/798/798 477/477/477\r\nf 797/797/797 799/799/799 800/800/800 798/798/798\r\nf 793/793/793 791/791/791 798/798/798 800/800/800\r\nf 791/791/791 478/478/478 477/477/477 798/798/798\r\nf 557/557/557 801/801/801 802/802/802 558/558/558\r\nf 801/801/801 803/803/803 804/804/804 802/802/802\r\nf 805/805/805 806/806/806 802/802/802 804/804/804\r\nf 806/806/806 559/559/559 558/558/558 802/802/802\r\nf 561/561/561 807/807/807 808/808/808 562/562/562\r\nf 807/807/807 809/809/809 810/810/810 808/808/808\r\nf 811/811/811 812/812/812 808/808/808 810/810/810\r\nf 812/812/812 565/565/565 562/562/562 808/808/808\r\nf 620/620/620 813/813/813 814/814/814 618/618/618\r\nf 813/813/813 815/815/815 816/816/816 814/814/814\r\nf 803/803/803 801/801/801 814/814/814 816/816/816\r\nf 801/801/801 557/557/557 618/618/618 814/814/814\r\nf 627/627/627 817/817/817 818/818/818 628/628/628\r\nf 817/817/817 819/819/819 820/820/820 818/818/818\r\nf 821/821/821 822/822/822 818/818/818 820/820/820\r\nf 822/822/822 629/629/629 628/628/628 818/818/818\r\nf 625/625/625 823/823/823 824/824/824 626/626/626\r\nf 823/823/823 825/825/825 826/826/826 824/824/824\r\nf 819/819/819 817/817/817 824/824/824 826/826/826\r\nf 817/817/817 627/627/627 626/626/626 824/824/824\r\nf 629/629/629 822/822/822 827/827/827 634/634/634\r\nf 822/822/822 821/821/821 828/828/828 827/827/827\r\nf 829/829/829 830/830/830 827/827/827 828/828/828\r\nf 830/830/830 635/635/635 634/634/634 827/827/827\r\nf 635/635/635 830/830/830 831/831/831 640/640/640\r\nf 830/830/830 829/829/829 832/832/832 831/831/831\r\nf 833/833/833 834/834/834 831/831/831 832/832/832\r\nf 834/834/834 641/641/641 640/640/640 831/831/831\r\nf 641/641/641 834/834/834 835/835/835 645/645/645\r\nf 834/834/834 833/833/833 836/836/836 835/835/835\r\nf 837/837/837 838/838/838 835/835/835 836/836/836\r\nf 838/838/838 642/642/642 645/645/645 835/835/835\r\nf 642/642/642 838/838/838 839/839/839 651/651/651\r\nf 838/838/838 837/837/837 840/840/840 839/839/839\r\nf 841/841/841 842/842/842 839/839/839 840/840/840\r\nf 842/842/842 648/648/648 651/651/651 839/839/839\r\nf 648/648/648 842/842/842 843/843/843 657/657/657\r\nf 842/842/842 841/841/841 844/844/844 843/843/843\r\nf 845/845/845 846/846/846 843/843/843 844/844/844\r\nf 846/846/846 654/654/654 657/657/657 843/843/843\r\nf 654/654/654 846/846/846 847/847/847 663/663/663\r\nf 846/846/846 845/845/845 848/848/848 847/847/847\r\nf 849/849/849 850/850/850 847/847/847 848/848/848\r\nf 850/850/850 660/660/660 663/663/663 847/847/847\r\nf 660/660/660 850/850/850 851/851/851 669/669/669\r\nf 850/850/850 849/849/849 852/852/852 851/851/851\r\nf 853/853/853 854/854/854 851/851/851 852/852/852\r\nf 854/854/854 666/666/666 669/669/669 851/851/851\r\nf 666/666/666 854/854/854 855/855/855 675/675/675\r\nf 854/854/854 853/853/853 856/856/856 855/855/855\r\nf 857/857/857 858/858/858 855/855/855 856/856/856\r\nf 858/858/858 672/672/672 675/675/675 855/855/855\r\nf 672/672/672 858/858/858 859/859/859 681/681/681\r\nf 858/858/858 857/857/857 860/860/860 859/859/859\r\nf 861/861/861 862/862/862 859/859/859 860/860/860\r\nf 862/862/862 678/678/678 681/681/681 859/859/859\r\nf 678/678/678 862/862/862 863/863/863 688/688/688\r\nf 862/862/862 861/861/861 864/864/864 863/863/863\r\nf 865/865/865 866/866/866 863/863/863 864/864/864\r\nf 866/866/866 689/689/689 688/688/688 863/863/863\r\nf 689/689/689 866/866/866 867/867/867 694/694/694\r\nf 866/866/866 865/865/865 868/868/868 867/867/867\r\nf 869/869/869 870/870/870 867/867/867 868/868/868\r\nf 870/870/870 695/695/695 694/694/694 867/867/867\r\nf 695/695/695 870/870/870 871/871/871 700/700/700\r\nf 870/870/870 869/869/869 872/872/872 871/871/871\r\nf 873/873/873 874/874/874 871/871/871 872/872/872\r\nf 874/874/874 701/701/701 700/700/700 871/871/871\r\nf 701/701/701 874/874/874 875/875/875 705/705/705\r\nf 874/874/874 873/873/873 876/876/876 875/875/875\r\nf 877/877/877 878/878/878 875/875/875 876/876/876\r\nf 878/878/878 702/702/702 705/705/705 875/875/875\r\nf 702/702/702 878/878/878 879/879/879 703/703/703\r\nf 878/878/878 877/877/877 880/880/880 879/879/879\r\nf 881/881/881 882/882/882 879/879/879 880/880/880\r\nf 882/882/882 706/706/706 703/703/703 879/879/879\r\nf 480/480/480 796/796/796 883/883/883 709/709/709\r\nf 796/796/796 795/795/795 884/884/884 883/883/883\r\nf 809/809/809 807/807/807 883/883/883 884/884/884\r\nf 807/807/807 561/561/561 709/709/709 883/883/883\r\nf 713/713/713 885/885/885 886/886/886 711/711/711\r\nf 885/885/885 887/887/887 888/888/888 886/886/886\r\nf 799/799/799 797/797/797 886/886/886 888/888/888\r\nf 797/797/797 476/476/476 711/711/711 886/886/886\r\nf 717/717/717 889/889/889 890/890/890 715/715/715\r\nf 889/889/889 891/891/891 892/892/892 890/890/890\r\nf 887/887/887 885/885/885 890/890/890 892/892/892\r\nf 885/885/885 713/713/713 715/715/715 890/890/890\r\nf 720/720/720 893/893/893 894/894/894 721/721/721\r\nf 893/893/893 895/895/895 896/896/896 894/894/894\r\nf 891/891/891 889/889/889 894/894/894 896/896/896\r\nf 889/889/889 717/717/717 721/721/721 894/894/894\r\nf 724/724/724 897/897/897 898/898/898 725/725/725\r\nf 897/897/897 899/899/899 900/900/900 898/898/898\r\nf 895/895/895 893/893/893 898/898/898 900/900/900\r\nf 893/893/893 720/720/720 725/725/725 898/898/898\r\nf 728/728/728 901/901/901 902/902/902 729/729/729\r\nf 901/901/901 903/903/903 904/904/904 902/902/902\r\nf 899/899/899 897/897/897 902/902/902 904/904/904\r\nf 897/897/897 724/724/724 729/729/729 902/902/902\r\nf 733/733/733 905/905/905 906/906/906 731/731/731\r\nf 905/905/905 907/907/907 908/908/908 906/906/906\r\nf 903/903/903 901/901/901 906/906/906 908/908/908\r\nf 901/901/901 728/728/728 731/731/731 906/906/906\r\nf 736/736/736 909/909/909 910/910/910 737/737/737\r\nf 909/909/909 911/911/911 912/912/912 910/910/910\r\nf 907/907/907 905/905/905 910/910/910 912/912/912\r\nf 905/905/905 733/733/733 737/737/737 910/910/910\r\nf 740/740/740 913/913/913 914/914/914 741/741/741\r\nf 913/913/913 915/915/915 916/916/916 914/914/914\r\nf 911/911/911 909/909/909 914/914/914 916/916/916\r\nf 909/909/909 736/736/736 741/741/741 914/914/914\r\nf 744/744/744 917/917/917 918/918/918 745/745/745\r\nf 917/917/917 919/919/919 920/920/920 918/918/918\r\nf 915/915/915 913/913/913 918/918/918 920/920/920\r\nf 913/913/913 740/740/740 745/745/745 918/918/918\r\nf 748/748/748 921/921/921 922/922/922 749/749/749\r\nf 921/921/921 923/923/923 924/924/924 922/922/922\r\nf 919/919/919 917/917/917 922/922/922 924/924/924\r\nf 917/917/917 744/744/744 749/749/749 922/922/922\r\nf 752/752/752 925/925/925 926/926/926 753/753/753\r\nf 925/925/925 927/927/927 928/928/928 926/926/926\r\nf 923/923/923 921/921/921 926/926/926 928/928/928\r\nf 921/921/921 748/748/748 753/753/753 926/926/926\r\nf 756/756/756 929/929/929 930/930/930 757/757/757\r\nf 929/929/929 931/931/931 932/932/932 930/930/930\r\nf 927/927/927 925/925/925 930/930/930 932/932/932\r\nf 925/925/925 752/752/752 757/757/757 930/930/930\r\nf 760/760/760 933/933/933 934/934/934 761/761/761\r\nf 933/933/933 935/935/935 936/936/936 934/934/934\r\nf 931/931/931 929/929/929 934/934/934 936/936/936\r\nf 929/929/929 756/756/756 761/761/761 934/934/934\r\nf 559/559/559 806/806/806 937/937/937 759/759/759\r\nf 806/806/806 805/805/805 938/938/938 937/937/937\r\nf 935/935/935 933/933/933 937/937/937 938/938/938\r\nf 933/933/933 760/760/760 759/759/759 937/937/937\r\nf 565/565/565 812/812/812 939/939/939 764/764/764\r\nf 812/812/812 811/811/811 940/940/940 939/939/939\r\nf 825/825/825 823/823/823 939/939/939 940/940/940\r\nf 823/823/823 625/625/625 764/764/764 939/939/939\r\nf 706/706/706 882/882/882 941/941/941 789/789/789\r\nf 882/882/882 881/881/881 942/942/942 941/941/941\r\nf 815/815/815 813/813/813 941/941/941 942/942/942\r\nf 813/813/813 620/620/620 789/789/789 941/941/941\r\nv 352.17 -8.51 106.92\r\nv 351.88 -6.61 106.62\r\nv 348.6 -5.96 112.14\r\nv 348.86 -7.88 112.47\r\nv 351.35 -4.37 106.12\r\nv 348.09 -3.71 111.61\r\nv 344.76 -3.03 116.75\r\nv 345.24 -5.3 117.32\r\nv 345.48 -7.23 117.66\r\nv 350.12 -0.34 105.05\r\nv 346.94 0.36 110.45\r\nv 347.49 -1.48 111\r\nv 350.71 -2.16 105.56\r\nv 343.66 1.08 115.51\r\nv 344.19 -0.78 116.1\r\nv 337.88 2.35 123.67\r\nv 338.37 0.47 124.31\r\nv 341.06 -0.11 120.62\r\nv 340.54 1.77 120\r\nv 338.91 -1.82 125\r\nv 341.61 -2.38 121.3\r\nv 339.59 -6.08 125.95\r\nv 342.3 -6.61 122.24\r\nv 342.07 -4.67 121.89\r\nv 339.36 -4.12 125.6\r\nv 355.14 -9.06 101.29\r\nv 354.83 -7.17 101\r\nv 357.78 -9.54 95.62\r\nv 357.43 -7.66 95.35\r\nv 356.81 -5.45 94.92\r\nv 354.25 -4.95 100.54\r\nv 362.07 -10.3 84.48\r\nv 361.66 -8.43 84.21\r\nv 359.71 -8.08 89.73\r\nv 360.09 -9.96 90\r\nv 360.96 -6.24 83.8\r\nv 359.04 -5.88 89.32\r\nv 359.4 -2.3 82.95\r\nv 357.55 -1.92 88.44\r\nv 358.26 -3.71 88.86\r\nv 360.14 -4.08 83.35\r\nv 355.4 -1.47 93.99\r\nv 356.07 -3.27 94.43\r\nv 353.56 -2.76 100.01\r\nv 352.93 -0.95 99.54\r\nv 363.72 -10.59 79.14\r\nv 363.3 -8.72 78.84\r\nv 365.07 -10.81 73.98\r\nv 364.65 -8.95 73.65\r\nv 363.93 -6.77 73.21\r\nv 362.58 -6.54 78.42\r\nv 367.05 -11.05 64.17\r\nv 366.63 -9.21 63.84\r\nv 365.75 -9.11 68.65\r\nv 366.17 -10.96 68.99\r\nv 365.89 -7.05 63.4\r\nv 365.02 -6.94 68.19\r\nv 364.26 -3.16 62.55\r\nv 363.4 -3.03 67.3\r\nv 364.17 -4.79 67.71\r\nv 365.03 -4.91 62.94\r\nv 362.32 -2.84 72.31\r\nv 363.08 -4.62 72.73\r\nv 361.75 -4.38 77.96\r\nv 360.99 -2.6 77.55\r\nv 365.68 -4.98 58.47\r\nv 364.89 -3.24 58.14\r\nv 366.55 -7.1 58.86\r\nv 367.03 -7.09 54.45\r\nv 366.14 -5 54.16\r\nv 365.33 -3.27 53.92\r\nv 367.75 -11.08 59.51\r\nv 367.31 -9.24 59.23\r\nv 368.26 -11.04 54.92\r\nv 367.81 -9.22 54.72\r\nv 368.78 -10.73 45.55\r\nv 368.29 -8.96 45.56\r\nv 368.13 -9.12 50.2\r\nv 368.6 -10.92 50.3\r\nv 367.48 -6.88 45.56\r\nv 367.34 -7.02 50.05\r\nv 365.71 -3.15 45.54\r\nv 365.59 -3.24 49.77\r\nv 366.42 -4.95 49.9\r\nv 366.55 -4.84 45.55\r\nv 347.83 8.72 -24.1\r\nv 348.16 7.6 -25.49\r\nv 344.17 9.83 -31.96\r\nv 343.85 10.91 -30.54\r\nv 348.49 6.22 -27.14\r\nv 344.49 8.49 -33.64\r\nv 340.51 10.69 -39.45\r\nv 340.19 11.99 -37.73\r\nv 339.87 13.04 -36.28\r\nv 348.7 3.51 -30.05\r\nv 344.7 5.85 -36.59\r\nv 344.7 7.1 -35.28\r\nv 348.7 4.78 -28.75\r\nv 340.74 8.1 -42.45\r\nv 340.73 9.33 -41.12\r\nv 333.98 11.78 -51.36\r\nv 333.95 12.99 -49.99\r\nv 337.05 11.34 -46.09\r\nv 337.08 10.12 -47.44\r\nv 333.71 14.31 -48.25\r\nv 336.82 12.67 -44.38\r\nv 333.05 16.57 -44.93\r\nv 336.17 14.97 -41.14\r\nv 336.49 13.95 -42.63\r\nv 333.38 15.56 -46.46\r\nv 285.65 31.28 -80.91\r\nv 285.86 31.91 -78.75\r\nv 291.37 30.07 -76.95\r\nv 291.14 29.42 -79.06\r\nv 286.04 32.47 -76.05\r\nv 291.57 30.66 -74.33\r\nv 297.12 28.69 -72.28\r\nv 296.92 28.07 -74.83\r\nv 296.67 27.39 -76.87\r\nv 286.28 33.24 -71.01\r\nv 291.84 31.49 -69.4\r\nv 291.73 31.14 -71.65\r\nv 286.18 32.92 -73.31\r\nv 297.39 29.61 -67.48\r\nv 297.28 29.22 -69.68\r\nv 307.82 26.05 -63\r\nv 307.76 25.52 -65.05\r\nv 302.68 27.31 -67.46\r\nv 302.78 27.75 -65.33\r\nv 307.64 24.83 -67.48\r\nv 302.54 26.71 -69.98\r\nv 307.25 23.24 -71.72\r\nv 302.1 25.29 -74.4\r\nv 302.34 26.02 -72.44\r\nv 307.48 24.04 -69.85\r\nv 331.72 12.95 -54.02\r\nv 331.68 14.16 -52.63\r\nv 330.08 13.75 -55.72\r\nv 330.06 14.95 -54.31\r\nv 329.84 16.25 -52.51\r\nv 331.45 15.46 -50.85\r\nv 327.95 14.71 -57.48\r\nv 327.95 15.93 -56.02\r\nv 328.88 15.5 -55.34\r\nv 328.89 14.29 -56.77\r\nv 327.78 17.24 -54.17\r\nv 328.68 16.8 -53.51\r\nv 327.25 19.45 -50.64\r\nv 328.11 19 -50.03\r\nv 328.4 18.03 -51.63\r\nv 327.51 18.47 -52.26\r\nv 329.23 18.46 -49.07\r\nv 329.53 17.48 -50.65\r\nv 331.12 16.7 -49.03\r\nv 330.8 17.69 -47.48\r\nv 364.8 -1.99 26.68\r\nv 365.58 -3.55 26.11\r\nv 364.85 -2.96 20.78\r\nv 364.13 -1.45 21.52\r\nv 366.43 -5.45 25.43\r\nv 365.65 -4.81 19.9\r\nv 364.55 -3.99 14.1\r\nv 363.83 -2.2 15.18\r\nv 363.17 -0.73 16.09\r\nv 367.58 -9.05 24.2\r\nv 366.69 -8.31 18.3\r\nv 366.32 -6.69 19.02\r\nv 367.16 -7.39 24.76\r\nv 365.46 -7.38 12.14\r\nv 365.15 -5.81 13.02\r\nv 361.73 -4.78 -1.14\r\nv 361.55 -3.31 0\r\nv 363.59 -4.7 6.7\r\nv 363.83 -6.22 5.67\r\nv 361.11 -1.62 1.41\r\nv 363.07 -2.94 7.96\r\nv 360.02 1.38 4.02\r\nv 361.82 0.19 10.3\r\nv 362.42 -1.22 9.23\r\nv 360.55 0.03 2.83\r\nv 359.09 -3.01 -8.34\r\nv 358.97 -1.6 -7.12\r\nv 355.98 -1 -15.7\r\nv 355.91 0.37 -14.44\r\nv 355.61 1.92 -12.88\r\nv 358.6 0.02 -5.62\r\nv 352.47 2.52 -21.73\r\nv 352.49 1.2 -23.01\r\nv 352.22 4 -20.14\r\nv 351.49 6.62 -17.16\r\nv 351.85 5.45 -18.52\r\nv 354.79 4.66 -9.97\r\nv 355.2 3.43 -11.3\r\nv 358.12 1.6 -4.1\r\nv 357.65 2.89 -2.82\r\nv 368.78 -10.47 40.56\r\nv 368.3 -8.72 40.69\r\nv 368.6 -10.11 35.33\r\nv 368.13 -8.38 35.6\r\nv 367.33 -6.37 35.91\r\nv 367.49 -6.67 40.84\r\nv 367.76 -7.95 30.28\r\nv 368.21 -9.64 29.88\r\nv 366.98 -5.97 30.77\r\nv 365.28 -2.43 31.67\r\nv 366.09 -4.02 31.26\r\nv 365.58 -2.76 36.48\r\nv 366.41 -4.39 36.22\r\nv 366.56 -4.65 40.99\r\nv 365.72 -2.99 41.11\r\nv 315.08 -1 148.8\r\nv 319.64 -1.85 145.56\r\nv 319.46 0.09 144.95\r\nv 314.92 0.93 148.14\r\nv 323.5 -2.62 142.43\r\nv 323.31 -0.68 141.9\r\nv 322.96 1.6 141.04\r\nv 319.13 2.37 143.98\r\nv 314.62 3.22 147.08\r\nv 329.19 -3.85 137.2\r\nv 328.99 -1.9 136.83\r\nv 326.49 -1.35 139.14\r\nv 326.69 -3.3 139.59\r\nv 328.6 0.4 136.18\r\nv 326.12 0.94 138.39\r\nv 327.71 4.55 134.71\r\nv 325.27 5.08 136.73\r\nv 325.68 3.21 137.52\r\nv 328.14 2.67 135.41\r\nv 322.16 5.73 139.16\r\nv 322.55 3.86 140.05\r\nv 313.94 7.34 144.83\r\nv 314.26 5.47 145.89\r\nv 318.75 4.62 142.88\r\nv 318.39 6.49 141.89\r\nv 291.45 2.48 160.27\r\nv 297.85 1.67 157.8\r\nv 297.75 3.63 157.12\r\nv 291.37 4.46 159.59\r\nv 304.03 0.79 155.01\r\nv 303.9 2.75 154.32\r\nv 303.67 5.05 153.21\r\nv 297.56 5.95 156\r\nv 291.21 6.79 158.48\r\nv 309.68 1.83 151.29\r\nv 309.83 -0.11 151.98\r\nv 309.41 4.13 150.19\r\nv 308.81 8.26 147.85\r\nv 309.1 6.39 148.96\r\nv 303.15 9.22 150.83\r\nv 303.4 7.33 151.95\r\nv 290.87 11.01 156.07\r\nv 291.03 9.1 157.21\r\nv 297.33 8.25 154.75\r\nv 297.12 10.15 153.62\r\nv 280.39 32.85 -82.36\r\nv 280.55 33.47 -80.14\r\nv 275.28 34.06 -83.39\r\nv 275.39 34.67 -81.14\r\nv 275.5 35.19 -78.35\r\nv 280.7 34 -77.39\r\nv 265.34 35.13 -84.22\r\nv 265.34 35.73 -81.94\r\nv 270.34 35.45 -81.74\r\nv 270.28 34.84 -84.01\r\nv 265.34 36.24 -79.11\r\nv 270.39 35.96 -78.92\r\nv 265.34 36.92 -73.82\r\nv 270.47 36.65 -73.65\r\nv 270.44 36.37 -76.06\r\nv 265.34 36.65 -76.24\r\nv 275.64 35.89 -73.13\r\nv 275.58 35.61 -75.51\r\nv 280.81 34.43 -74.6\r\nv 280.9 34.73 -72.25\r\nv 347.01 -1.71 103.01\r\nv 347.59 -3.42 103.5\r\nv 344.41 -2.72 108.72\r\nv 343.84 -0.99 108.19\r\nv 348.36 -5.46 104.15\r\nv 345.15 -4.77 109.42\r\nv 341.86 -4.06 114.34\r\nv 341.13 -1.99 113.6\r\nv 340.57 -0.25 113.03\r\nv 350.11 -9.1 105.56\r\nv 346.85 -8.45 110.95\r\nv 345.99 -6.8 110.19\r\nv 349.22 -7.46 104.86\r\nv 343.53 -7.78 116\r\nv 342.69 -6.11 115.17\r\nv 337.75 -6.6 124.03\r\nv 336.94 -4.9 123.11\r\nv 339.58 -5.46 119.56\r\nv 340.41 -7.15 120.44\r\nv 336.14 -2.82 122.19\r\nv 338.76 -3.39 118.68\r\nv 334.87 1.04 120.75\r\nv 337.48 0.45 117.29\r\nv 338.04 -1.31 117.9\r\nv 335.42 -0.72 121.37\r\nv 356.09 -3.76 81.74\r\nv 356.8 -5.43 82.12\r\nv 354.98 -5.04 87.44\r\nv 354.29 -3.36 87.04\r\nv 357.73 -7.42 82.62\r\nv 355.87 -7.04 87.95\r\nv 353.7 -6.59 93.35\r\nv 352.85 -4.58 92.81\r\nv 352.21 -2.89 92.39\r\nv 359.82 -10.96 83.66\r\nv 357.88 -10.6 89.05\r\nv 356.87 -9.01 88.51\r\nv 358.78 -9.37 83.15\r\nv 355.62 -10.17 94.54\r\nv 354.65 -8.57 93.95\r\nv 352.1 -8.05 99.42\r\nv 353.03 -9.67 100.07\r\nv 351.2 -6.06 98.77\r\nv 349.79 -2.34 97.72\r\nv 350.4 -4.04 98.18\r\nv 360.81 -4.67 61.98\r\nv 361.56 -6.31 62.36\r\nv 360.72 -6.18 66.99\r\nv 359.98 -4.53 66.59\r\nv 362.53 -8.26 62.83\r\nv 361.68 -8.14 67.49\r\nv 360.62 -7.97 72.36\r\nv 359.66 -6 71.85\r\nv 358.93 -4.34 71.45\r\nv 364.71 -11.73 63.77\r\nv 363.85 -11.64 68.49\r\nv 362.76 -10.08 68.02\r\nv 363.61 -10.18 63.33\r\nv 362.77 -11.48 73.39\r\nv 361.69 -9.91 72.89\r\nv 360.37 -9.67 77.94\r\nv 361.44 -11.25 78.44\r\nv 359.31 -7.73 77.41\r\nv 357.63 -4.08 76.52\r\nv 358.36 -5.75 76.91\r\nv 368.55 -12.08 50.34\r\nv 368.73 -11.88 45.53\r\nv 368.2 -12.2 55.01\r\nv 367.71 -12.72 55\r\nv 368.06 -12.59 50.32\r\nv 368.24 -12.39 45.5\r\nv 366.97 -12.24 64.31\r\nv 367.67 -12.26 59.64\r\nv 366.49 -12.75 64.28\r\nv 367.19 -12.77 59.62\r\nv 365.39 -11.77 59.2\r\nv 366.39 -12.61 59.47\r\nv 365.7 -12.59 64.09\r\nv 365.9 -11.73 54.69\r\nv 366.91 -12.56 54.89\r\nv 366.4 -11.44 45.45\r\nv 367.43 -12.24 45.47\r\nv 367.26 -12.44 50.25\r\nv 366.23 -11.62 50.14\r\nv 348.39 2.6 -30.75\r\nv 344.39 4.95 -37.28\r\nv 347.85 2.1 -30.86\r\nv 343.86 4.44 -37.38\r\nv 339.93 6.67 -43.21\r\nv 340.44 7.2 -43.14\r\nv 346.49 2.44 -29.44\r\nv 342.54 4.7 -35.88\r\nv 343.21 4.34 -36.9\r\nv 347.18 2.04 -30.42\r\nv 338.64 6.87 -41.63\r\nv 339.3 6.54 -42.7\r\nv 332 10.37 -50.35\r\nv 332.64 10.11 -51.49\r\nv 335.69 8.51 -47.62\r\nv 335.04 8.8 -46.51\r\nv 333.25 10.3 -52.07\r\nv 336.31 8.67 -48.17\r\nv 336.8 9.22 -48.12\r\nv 333.72 10.87 -52.04\r\nv 284.95 29.08 -79.99\r\nv 290.32 27.28 -78.08\r\nv 290.3 27.22 -75.74\r\nv 284.94 28.97 -77.61\r\nv 295.75 25.33 -75.82\r\nv 295.72 25.33 -73.52\r\nv 295.76 25.52 -70.87\r\nv 290.35 27.34 -73.02\r\nv 285 29.05 -74.83\r\nv 306.16 21.33 -70.56\r\nv 306.05 21.5 -68.38\r\nv 301.03 23.39 -71.04\r\nv 301.09 23.31 -73.28\r\nv 306.03 21.88 -65.88\r\nv 301.05 23.67 -68.46\r\nv 306.11 22.79 -61.43\r\nv 301.2 24.4 -63.86\r\nv 301.11 24.04 -65.94\r\nv 306.06 22.35 -63.44\r\nv 295.95 26.1 -66.13\r\nv 295.85 25.8 -68.27\r\nv 285.18 29.43 -69.86\r\nv 285.08 29.23 -72.11\r\nv 290.44 27.56 -70.36\r\nv 290.55 27.8 -68.16\r\nv 311.97 21.36 -68.91\r\nv 311.69 20.58 -69.81\r\nv 306.96 22.51 -72.68\r\nv 316.16 19.69 -66.15\r\nv 315.89 18.85 -67\r\nv 315.55 18.23 -67.05\r\nv 311.37 19.97 -69.88\r\nv 306.66 21.9 -72.76\r\nv 322.57 17.09 -61.5\r\nv 322.32 16.15 -62.28\r\nv 319.47 17.36 -64.42\r\nv 319.73 18.25 -63.61\r\nv 321.95 15.51 -62.31\r\nv 319.11 16.73 -64.47\r\nv 321.08 15.28 -60.35\r\nv 318.33 16.44 -62.45\r\nv 318.72 16.41 -63.79\r\nv 321.52 15.2 -61.65\r\nv 314.87 17.86 -64.96\r\nv 315.19 17.88 -66.35\r\nv 306.37 21.49 -72.04\r\nv 311.05 19.59 -69.16\r\nv 310.78 19.51 -67.73\r\nv 367.49 -10.13 23.88\r\nv 366.57 -9.37 17.89\r\nv 366.99 -10.63 23.79\r\nv 366.06 -9.87 17.78\r\nv 364.79 -8.91 11.5\r\nv 365.31 -8.42 11.63\r\nv 365.19 -9.8 24.3\r\nv 364.3 -9.08 18.44\r\nv 365.27 -9.78 17.97\r\nv 366.19 -10.52 23.93\r\nv 363.08 -8.18 12.33\r\nv 364.01 -8.84 11.74\r\nv 359.38 -5.64 -0.83\r\nv 360.22 -6.22 -1.62\r\nv 362.35 -7.67 5.22\r\nv 361.46 -7.05 5.92\r\nv 360.96 -6.26 -1.95\r\nv 363.11 -7.72 4.94\r\nv 363.64 -7.24 5.08\r\nv 361.5 -5.77 -1.79\r\nv 352.18 0.28 -23.72\r\nv 355.69 -1.94 -16.4\r\nv 355.14 -2.42 -16.55\r\nv 351.63 -0.21 -23.85\r\nv 358.82 -3.98 -9.03\r\nv 358.28 -4.46 -9.18\r\nv 356.76 -3.92 -7.96\r\nv 357.56 -4.45 -8.82\r\nv 353.68 -1.95 -15.26\r\nv 354.44 -2.44 -16.17\r\nv 350.95 -0.25 -23.44\r\nv 350.23 0.19 -22.49\r\nv 365.82 -10.38 29.93\r\nv 364.69 -8.99 30.35\r\nv 364.1 -8.45 24.88\r\nv 366.22 -10.84 35.33\r\nv 365.07 -9.41 35.6\r\nv 363.91 -7.65 35.92\r\nv 363.57 -7.26 30.85\r\nv 363.02 -6.76 25.58\r\nv 365.24 -9.96 45.43\r\nv 365.24 -9.73 40.63\r\nv 366.4 -11.18 40.51\r\nv 364.08 -8.14 45.42\r\nv 364.08 -7.93 40.78\r\nv 362.23 -4.71 45.41\r\nv 362.23 -4.56 41.06\r\nv 363.03 -6.1 40.93\r\nv 363.03 -6.28 45.41\r\nv 362.09 -4.33 36.5\r\nv 362.88 -5.85 36.23\r\nv 361.31 -3.59 26.84\r\nv 362.05 -5.05 26.27\r\nv 362.56 -5.5 31.34\r\nv 361.79 -4.01 31.75\r\nv 333.56 -0.3 123.79\r\nv 333.03 1.47 123.16\r\nv 334.26 -2.4 124.63\r\nv 332.94 -2.1 126.26\r\nv 332.27 0 125.42\r\nv 331.76 1.78 124.8\r\nv 335.83 -6.2 126.53\r\nv 335.04 -4.49 125.58\r\nv 334.45 -5.9 128.19\r\nv 333.69 -4.19 127.22\r\nv 332.62 -5.49 130.13\r\nv 331.91 -3.78 129.16\r\nv 332.71 -3.97 128.33\r\nv 333.44 -5.68 129.3\r\nv 331.22 -1.7 128.2\r\nv 331.99 -1.88 127.36\r\nv 330.15 2.17 126.76\r\nv 330.87 2 125.91\r\nv 331.35 0.23 126.52\r\nv 330.61 0.4 127.36\r\nv 312.34 5.95 140.99\r\nv 312.66 4.21 142.01\r\nv 307.67 5.14 144.91\r\nv 307.39 6.89 143.85\r\nv 313.07 2.13 143.31\r\nv 308.04 3.06 146.27\r\nv 302.48 4.01 149.12\r\nv 302.16 6.1 147.74\r\nv 301.92 7.86 146.67\r\nv 314.01 -1.59 146.17\r\nv 308.87 -0.68 149.23\r\nv 308.45 1 147.76\r\nv 313.53 0.08 144.75\r\nv 303.21 0.24 152.15\r\nv 302.84 1.93 150.65\r\nv 290.9 1.96 157.21\r\nv 290.65 3.68 155.67\r\nv 296.86 2.84 153.31\r\nv 297.16 1.14 154.83\r\nv 290.41 5.78 154.12\r\nv 296.55 4.93 151.78\r\nv 290.04 9.69 151.64\r\nv 296.09 8.81 149.31\r\nv 296.29 7.04 150.39\r\nv 290.2 7.9 152.73\r\nv 284.04 8.64 154.63\r\nv 283.92 10.43 153.52\r\nv 284.2 6.51 156.05\r\nv 277.94 7.08 157.5\r\nv 277.83 9.21 156.05\r\nv 277.75 11 154.92\r\nv 284.56 2.68 159.2\r\nv 284.38 4.4 157.63\r\nv 278.18 3.24 160.72\r\nv 278.06 4.97 159.12\r\nv 265.34 3.75 162.04\r\nv 265.34 5.47 160.4\r\nv 271.71 5.34 160.06\r\nv 271.77 3.61 161.69\r\nv 265.34 7.58 158.75\r\nv 271.64 7.45 158.42\r\nv 265.34 11.51 156.09\r\nv 271.55 11.37 155.79\r\nv 271.59 9.58 156.94\r\nv 265.34 9.72 157.25\r\nv 350.93 -10.02 106.17\r\nv 347.65 -9.38 111.63\r\nv 351.6 -10.22 106.64\r\nv 348.3 -9.59 112.15\r\nv 344.94 -8.95 117.29\r\nv 344.31 -8.73 116.73\r\nv 348.73 -9.09 112.45\r\nv 352.04 -9.71 106.91\r\nv 345.36 -8.45 117.63\r\nv 339.48 -7.3 125.89\r\nv 342.19 -7.83 122.19\r\nv 339.09 -7.8 125.5\r\nv 341.78 -8.33 121.83\r\nv 341.17 -8.1 121.22\r\nv 338.49 -7.57 124.86\r\nv 360.01 -11.15 90.02\r\nv 361.99 -11.5 84.53\r\nv 357.68 -10.74 95.63\r\nv 357.23 -11.25 95.42\r\nv 359.55 -11.67 89.85\r\nv 361.53 -12.01 84.38\r\nv 355.03 -10.26 101.28\r\nv 354.58 -10.77 101.05\r\nv 353.88 -10.58 100.62\r\nv 356.51 -11.07 95.05\r\nv 360.77 -11.84 84.08\r\nv 358.81 -11.49 89.51\r\nv 366.09 -12.15 69.12\r\nv 365 -12 74.09\r\nv 364.52 -12.52 74.01\r\nv 365.61 -12.67 69.07\r\nv 363.64 -11.78 79.22\r\nv 363.17 -12.3 79.11\r\nv 362.41 -12.13 78.84\r\nv 363.75 -12.35 73.77\r\nv 364.83 -12.5 68.85\r\nv 361.43 -4.76 57.7\r\nv 362.19 -6.39 58.02\r\nv 361.86 -4.81 53.6\r\nv 362.63 -6.42 53.83\r\nv 363.64 -8.33 54.12\r\nv 363.18 -8.32 58.42\r\nv 362.91 -6.38 49.67\r\nv 362.12 -4.79 49.55\r\nv 363.94 -8.27 49.83\r\nv 365.08 -10.12 49.99\r\nv 364.77 -10.21 54.42\r\nv 364.28 -10.23 58.83\r\nv 344.62 6.79 -23.08\r\nv 340.74 8.88 -29.38\r\nv 341.01 7.87 -30.81\r\nv 344.91 5.73 -24.49\r\nv 336.85 10.91 -34.97\r\nv 337.12 9.92 -36.43\r\nv 337.53 8.77 -38.22\r\nv 341.42 6.68 -32.56\r\nv 345.33 4.51 -26.2\r\nv 330.2 14.18 -43.39\r\nv 330.48 13.22 -44.91\r\nv 333.52 11.75 -41.19\r\nv 333.24 12.72 -39.7\r\nv 330.89 12.13 -46.77\r\nv 333.93 10.63 -43.02\r\nv 334.44 9.58 -44.88\r\nv 331.41 11.11 -48.68\r\nv 338.04 7.68 -40.05\r\nv 345.86 3.33 -27.94\r\nv 341.93 5.56 -34.34\r\nv 285.06 29.43 -81.56\r\nv 290.44 27.61 -79.64\r\nv 285.23 29.97 -82.27\r\nv 290.64 28.12 -80.35\r\nv 296.13 26.08 -78.08\r\nv 295.9 25.6 -77.36\r\nv 290.88 28.75 -80.18\r\nv 285.44 30.61 -82.07\r\nv 296.4 26.7 -77.93\r\nv 301.81 24.58 -75.41\r\nv 301.52 23.97 -75.52\r\nv 301.27 23.53 -74.8\r\nv 328.02 15.14 -45.87\r\nv 328.29 14.21 -47.42\r\nv 326.5 15.74 -47.44\r\nv 326.76 14.82 -49\r\nv 327.15 13.78 -50.92\r\nv 328.7 13.14 -49.31\r\nv 324.63 16.37 -48.99\r\nv 324.86 15.49 -50.6\r\nv 325.68 15.21 -49.97\r\nv 325.44 16.11 -48.38\r\nv 325.21 14.49 -52.57\r\nv 326.05 14.19 -51.91\r\nv 326.16 12.97 -56.36\r\nv 327.05 12.61 -55.67\r\nv 326.51 13.26 -53.91\r\nv 325.65 13.59 -54.59\r\nv 328.2 12.14 -54.63\r\nv 327.64 12.82 -52.9\r\nv 329.21 12.15 -51.25\r\nv 329.79 11.44 -52.95\r\nv 360.65 -3.06 21.77\r\nv 361.33 -4.48 21.02\r\nv 359.69 -2.37 16.43\r\nv 360.32 -3.74 15.52\r\nv 361.15 -5.35 14.4\r\nv 362.24 -6.14 20.12\r\nv 356.59 -0.32 4.57\r\nv 357.08 -1.58 3.36\r\nv 358.93 -2.79 9.66\r\nv 358.37 -1.48 10.74\r\nv 357.75 -3.06 1.9\r\nv 359.68 -4.34 8.36\r\nv 360.55 -5.85 7.04\r\nv 358.53 -4.5 0.43\r\nv 362.1 -6.92 13.28\r\nv 363.26 -7.78 19.21\r\nv 354.26 1.15 -2.17\r\nv 354.68 -0.06 -3.46\r\nv 351.44 2.86 -9.21\r\nv 351.81 1.71 -10.55\r\nv 352.33 0.37 -12.19\r\nv 355.27 -1.47 -5.03\r\nv 348.53 3.66 -17.65\r\nv 348.21 4.76 -16.27\r\nv 348.99 2.38 -19.33\r\nv 349.57 1.15 -21.03\r\nv 352.96 -0.93 -13.85\r\nv 355.98 -2.84 -6.61\r\nv 368.14 -10.75 29.65\r\nv 368.55 -11.23 35.18\r\nv 368.06 -11.73 35.12\r\nv 367.65 -11.25 29.58\r\nv 368.73 -11.6 40.47\r\nv 368.25 -12.11 40.43\r\nv 367.44 -11.97 40.45\r\nv 367.25 -11.61 35.18\r\nv 366.84 -11.13 29.67\r\nv 336.55 -7.17 127.37\r\nv 337.13 -7.41 128.04\r\nv 335.71 -7.11 129.74\r\nv 335.15 -6.87 129.06\r\nv 337.61 -5.68 128.54\r\nv 337.5 -6.91 128.46\r\nv 336.18 -5.38 130.28\r\nv 336.07 -6.61 130.18\r\nv 334.25 -4.97 132.31\r\nv 334.15 -6.2 132.18\r\nv 335.02 -6.39 131.32\r\nv 335.12 -5.16 131.44\r\nv 333.81 -6.69 131.72\r\nv 334.66 -6.88 130.88\r\nv 334.11 -6.65 130.18\r\nv 333.27 -6.46 131.01\r\nv 314.44 -2.53 147.39\r\nv 309.26 -1.63 150.51\r\nv 314.8 -2.74 148.32\r\nv 309.58 -1.85 151.47\r\nv 303.82 -0.94 154.46\r\nv 303.54 -0.72 153.46\r\nv 309.78 -1.34 152\r\nv 315.03 -2.23 148.83\r\nv 303.99 -0.44 155.01\r\nv 291.42 1.24 160.24\r\nv 297.82 0.44 157.79\r\nv 291.3 0.75 159.63\r\nv 297.67 -0.07 157.2\r\nv 297.44 0.17 156.17\r\nv 291.12 0.98 158.58\r\nv 284.98 3.19 162.33\r\nv 284.96 1.95 162.3\r\nv 278.46 3.76 163.91\r\nv 278.44 2.52 163.87\r\nv 278.39 2.02 163.24\r\nv 284.87 1.45 161.67\r\nv 265.34 4.28 165.27\r\nv 265.34 3.03 165.25\r\nv 271.9 2.89 164.89\r\nv 271.91 4.13 164.91\r\nv 265.34 2.53 164.61\r\nv 271.87 2.39 164.25\r\nv 271.82 2.63 163.14\r\nv 265.34 2.76 163.5\r\nv 278.29 2.26 162.14\r\nv 284.73 1.69 160.59\r\nv 271.89 6.12 164.18\r\nv 265.34 6.26 164.53\r\nv 278.42 5.74 163.19\r\nv 278.34 8.09 162.03\r\nv 271.85 8.46 162.99\r\nv 265.34 8.6 163.33\r\nv 284.92 5.17 161.64\r\nv 284.8 7.51 160.5\r\nv 284.54 11.75 158.05\r\nv 284.67 9.83 159.21\r\nv 278.17 12.33 159.51\r\nv 278.25 10.41 160.7\r\nv 265.34 12.85 160.74\r\nv 265.34 10.93 161.96\r\nv 271.8 10.79 161.63\r\nv 271.76 12.71 160.42\r\nv 279.83 30.49 -79.04\r\nv 279.84 30.61 -81.46\r\nv 279.88 30.55 -76.22\r\nv 274.93 31.74 -77.19\r\nv 274.9 31.68 -80.05\r\nv 274.9 31.8 -82.51\r\nv 280.02 30.91 -71.15\r\nv 279.95 30.71 -73.44\r\nv 275.04 32.11 -72.05\r\nv 274.98 31.91 -74.37\r\nv 265.34 33.24 -72.74\r\nv 265.34 33.02 -75.09\r\nv 270.13 32.71 -74.92\r\nv 270.16 32.93 -72.57\r\nv 265.34 32.83 -77.95\r\nv 270.1 32.53 -77.76\r\nv 265.34 32.86 -83.33\r\nv 270.09 32.58 -83.13\r\nv 270.09 32.46 -80.65\r\nv 265.34 32.75 -80.84\r\nv 280.21 32.18 -83.55\r\nv 280.05 31.53 -83.77\r\nv 275.05 32.74 -84.83\r\nv 275.16 33.39 -84.61\r\nv 279.92 30.98 -83.06\r\nv 274.95 32.18 -84.12\r\nv 265.34 33.24 -84.96\r\nv 270.11 32.96 -84.75\r\nv 265.34 33.8 -85.69\r\nv 270.16 33.51 -85.47\r\nv 270.22 34.17 -85.24\r\nv 265.34 34.45 -85.45\r\nv 178.67 -8.51 106.92\r\nv 181.98 -7.88 112.47\r\nv 182.4 -5.96 112.14\r\nv 179.13 -6.61 106.62\r\nv 185.34 -7.23 117.66\r\nv 185.74 -5.3 117.31\r\nv 186.4 -3.03 116.74\r\nv 183.11 -3.71 111.61\r\nv 179.87 -4.37 106.12\r\nv 191.2 -6.07 125.94\r\nv 191.53 -4.12 125.57\r\nv 188.87 -4.67 121.87\r\nv 188.51 -6.61 122.23\r\nv 192.11 -1.81 124.94\r\nv 189.49 -2.38 121.27\r\nv 193.36 2.36 123.56\r\nv 190.84 1.77 119.95\r\nv 190.2 -0.11 120.58\r\nv 192.76 0.47 124.21\r\nv 187.84 1.08 115.5\r\nv 187.16 -0.78 116.09\r\nv 181.47 -0.34 105.05\r\nv 180.71 -2.16 105.56\r\nv 183.9 -1.48 111\r\nv 184.63 0.36 110.45\r\nv 176.17 -7.17 101\r\nv 175.69 -9.06 101.29\r\nv 176.95 -4.95 100.54\r\nv 174.36 -5.45 94.92\r\nv 173.55 -7.66 95.35\r\nv 173.04 -9.54 95.62\r\nv 178.63 -0.95 99.54\r\nv 177.83 -2.76 100.01\r\nv 176.11 -1.47 93.99\r\nv 175.28 -3.27 94.43\r\nv 171.99 -2.3 82.95\r\nv 171.11 -4.08 83.35\r\nv 173.04 -3.71 88.86\r\nv 173.89 -1.92 88.44\r\nv 170.14 -6.24 83.8\r\nv 172.09 -5.88 89.32\r\nv 168.73 -10.3 84.48\r\nv 170.72 -9.96 90\r\nv 171.25 -8.08 89.73\r\nv 169.27 -8.43 84.21\r\nv 167.63 -8.72 78.84\r\nv 167.08 -10.59 79.14\r\nv 168.51 -6.54 78.42\r\nv 167.17 -6.77 73.21\r\nv 166.28 -8.95 73.65\r\nv 165.73 -10.81 73.98\r\nv 170.39 -2.6 77.55\r\nv 169.5 -4.38 77.96\r\nv 169.07 -2.84 72.31\r\nv 168.17 -4.62 72.73\r\nv 167.16 -3.16 62.55\r\nv 166.25 -4.91 62.94\r\nv 167.09 -4.79 67.71\r\nv 168 -3.03 67.3\r\nv 165.22 -7.05 63.4\r\nv 166.08 -6.94 68.19\r\nv 163.76 -11.05 64.17\r\nv 164.63 -10.96 68.99\r\nv 165.19 -9.11 68.65\r\nv 164.32 -9.21 63.84\r\nv 166.53 -3.24 58.14\r\nv 165.6 -4.98 58.47\r\nv 166.08 -3.27 53.92\r\nv 165.13 -5 54.16\r\nv 164.08 -7.09 54.45\r\nv 164.56 -7.1 58.86\r\nv 165.65 -3.15 45.54\r\nv 164.68 -4.84 45.55\r\nv 164.83 -4.95 49.9\r\nv 165.79 -3.24 49.77\r\nv 163.6 -6.88 45.56\r\nv 163.76 -7.02 50.05\r\nv 162.02 -10.73 45.55\r\nv 162.2 -10.92 50.3\r\nv 162.8 -9.12 50.2\r\nv 162.64 -8.96 45.56\r\nv 162.54 -11.04 54.92\r\nv 163.14 -9.22 54.72\r\nv 163.64 -9.24 59.23\r\nv 163.06 -11.08 59.51\r\nv 183.77 8.72 -24.1\r\nv 187.59 10.91 -30.54\r\nv 187.12 9.83 -31.96\r\nv 183.26 7.6 -25.49\r\nv 191.36 13.05 -36.28\r\nv 190.93 12 -37.74\r\nv 190.49 10.69 -39.45\r\nv 186.63 8.49 -33.64\r\nv 182.73 6.22 -27.14\r\nv 197.79 16.66 -44.97\r\nv 197.43 15.64 -46.49\r\nv 194.45 13.98 -42.64\r\nv 194.84 15.01 -41.15\r\nv 197.07 14.37 -48.28\r\nv 194.05 12.69 -44.4\r\nv 196.71 11.76 -51.4\r\nv 193.63 10.07 -47.45\r\nv 193.74 11.33 -46.1\r\nv 196.8 13.02 -50.02\r\nv 190.01 8.05 -42.46\r\nv 190.14 9.31 -41.13\r\nv 182.13 3.49 -30.05\r\nv 182.31 4.78 -28.75\r\nv 186.25 7.09 -35.28\r\nv 186.1 5.82 -36.59\r\nv 245.02 31.28 -80.91\r\nv 239.53 29.42 -79.06\r\nv 239.3 30.07 -76.95\r\nv 244.81 31.91 -78.75\r\nv 234 27.38 -76.87\r\nv 233.76 28.06 -74.83\r\nv 233.55 28.69 -72.28\r\nv 239.1 30.66 -74.33\r\nv 244.63 32.47 -76.05\r\nv 223.45 23.22 -71.75\r\nv 223.22 24.02 -69.87\r\nv 228.34 26 -72.45\r\nv 228.59 25.28 -74.41\r\nv 223.06 24.79 -67.5\r\nv 228.15 26.69 -69.98\r\nv 222.88 25.98 -63.02\r\nv 227.91 27.73 -65.34\r\nv 228 27.29 -67.47\r\nv 222.94 25.47 -65.07\r\nv 233.28 29.61 -67.49\r\nv 233.39 29.22 -69.68\r\nv 244.39 33.24 -71.01\r\nv 244.49 32.92 -73.31\r\nv 238.94 31.14 -71.65\r\nv 238.83 31.49 -69.4\r\nv 199.07 14.24 -52.7\r\nv 199 12.98 -54.09\r\nv 199.32 15.59 -50.92\r\nv 200.96 16.45 -52.62\r\nv 200.74 15.1 -54.42\r\nv 200.68 13.85 -55.84\r\nv 199.97 17.87 -47.54\r\nv 199.65 16.86 -49.1\r\nv 201.56 18.73 -49.17\r\nv 201.26 17.72 -50.76\r\nv 203.63 19.78 -50.8\r\nv 203.37 18.76 -52.43\r\nv 202.44 18.32 -51.77\r\nv 202.72 19.34 -50.16\r\nv 203.12 17.48 -54.34\r\nv 202.16 17.04 -53.66\r\nv 202.92 14.9 -57.66\r\nv 201.93 14.46 -56.93\r\nv 201.97 15.7 -55.49\r\nv 202.94 16.14 -56.2\r\nv 166.51 -1.99 26.68\r\nv 167.17 -1.44 21.52\r\nv 166.33 -2.96 20.78\r\nv 165.61 -3.55 26.11\r\nv 168.14 -0.73 16.09\r\nv 167.36 -2.2 15.18\r\nv 166.5 -3.99 14.1\r\nv 165.4 -4.81 19.9\r\nv 164.62 -5.45 25.43\r\nv 171.37 1.38 4.02\r\nv 170.71 0.03 2.83\r\nv 168.78 -1.22 9.23\r\nv 169.51 0.19 10.3\r\nv 169.99 -1.62 1.41\r\nv 168 -2.94 7.96\r\nv 169.07 -4.78 -1.14\r\nv 166.96 -6.22 5.67\r\nv 167.33 -4.7 6.7\r\nv 169.38 -3.31 0\r\nv 165.33 -7.38 12.14\r\nv 165.75 -5.81 13.02\r\nv 163.21 -9.05 24.2\r\nv 163.75 -7.39 24.76\r\nv 164.59 -6.69 19.02\r\nv 164.1 -8.31 18.3\r\nv 172 -1.6 -7.12\r\nv 171.73 -3.01 -8.34\r\nv 172.55 0.02 -5.62\r\nv 175.59 1.92 -12.88\r\nv 175.09 0.36 -14.44\r\nv 174.86 -1 -15.7\r\nv 173.83 2.89 -2.82\r\nv 173.21 1.6 -4.1\r\nv 176.78 4.66 -9.97\r\nv 176.21 3.43 -11.3\r\nv 179.59 5.45 -18.52\r\nv 180.13 6.62 -17.16\r\nv 179.02 4 -20.14\r\nv 178.35 1.19 -23.01\r\nv 178.56 2.51 -21.73\r\nv 162.62 -8.72 40.69\r\nv 162.01 -10.47 40.56\r\nv 163.58 -6.67 40.84\r\nv 163.73 -6.37 35.91\r\nv 162.79 -8.38 35.6\r\nv 162.19 -10.11 35.33\r\nv 165.63 -2.99 41.11\r\nv 164.67 -4.65 40.99\r\nv 165.76 -2.76 36.48\r\nv 164.8 -4.39 36.22\r\nv 165.11 -4.02 31.26\r\nv 166.04 -2.43 31.67\r\nv 164.07 -5.97 30.77\r\nv 162.58 -9.64 29.88\r\nv 163.15 -7.95 30.28\r\nv 215.72 -0.97 148.91\r\nv 215.87 0.96 148.22\r\nv 211.44 0.15 145.12\r\nv 211.28 -1.79 145.78\r\nv 216.15 3.25 147.13\r\nv 211.74 2.43 144.08\r\nv 208.01 1.69 141.18\r\nv 207.71 -0.59 142.15\r\nv 207.56 -2.53 142.77\r\nv 216.81 7.37 144.81\r\nv 212.43 6.54 141.87\r\nv 212.1 4.68 142.91\r\nv 216.49 5.5 145.9\r\nv 208.73 5.82 139.11\r\nv 208.39 3.95 140.09\r\nv 203.24 4.68 134.54\r\nv 202.88 2.8 135.38\r\nv 205.33 3.32 137.54\r\nv 205.68 5.2 136.63\r\nv 202.49 0.53 136.33\r\nv 204.95 1.06 138.56\r\nv 202.04 -3.71 137.66\r\nv 204.49 -3.17 140.01\r\nv 204.64 -1.23 139.45\r\nv 202.18 -1.77 137.15\r\nv 239.22 2.48 160.27\r\nv 239.3 4.46 159.59\r\nv 232.92 3.63 157.12\r\nv 232.82 1.67 157.8\r\nv 239.46 6.79 158.48\r\nv 233.11 5.95 156\r\nv 227.01 5.05 153.21\r\nv 226.78 2.75 154.32\r\nv 226.65 0.8 155.02\r\nv 239.8 11.01 156.07\r\nv 233.55 10.15 153.62\r\nv 233.34 8.25 154.75\r\nv 239.64 9.1 157.21\r\nv 227.53 9.22 150.83\r\nv 227.28 7.34 151.95\r\nv 221.61 6.4 148.96\r\nv 221.89 8.28 147.85\r\nv 221.3 4.14 150.21\r\nv 220.9 -0.1 152.03\r\nv 221.04 1.85 151.32\r\nv 250.12 33.47 -80.14\r\nv 250.28 32.85 -82.36\r\nv 249.97 34 -77.39\r\nv 255.17 35.19 -78.35\r\nv 255.28 34.67 -81.14\r\nv 255.39 34.06 -83.39\r\nv 249.78 34.73 -72.25\r\nv 249.86 34.43 -74.6\r\nv 255.03 35.89 -73.13\r\nv 255.09 35.61 -75.51\r\nv 260.24 36.37 -76.06\r\nv 260.21 36.65 -73.65\r\nv 260.28 35.96 -78.92\r\nv 260.39 34.84 -84.01\r\nv 260.33 35.45 -81.74\r\nv 184.47 -1.71 103.01\r\nv 187.52 -0.99 108.19\r\nv 186.83 -2.72 108.72\r\nv 183.74 -3.42 103.5\r\nv 190.61 -0.25 113.02\r\nv 189.96 -1.99 113.6\r\nv 189.11 -4.06 114.35\r\nv 185.93 -4.77 109.42\r\nv 182.8 -5.46 104.15\r\nv 195.92 1.05 120.73\r\nv 195.36 -0.72 121.37\r\nv 192.89 -1.31 117.89\r\nv 193.5 0.45 117.28\r\nv 194.62 -2.82 122.2\r\nv 192.09 -3.39 118.69\r\nv 192.95 -6.6 124.05\r\nv 190.32 -7.15 120.45\r\nv 191.21 -5.46 119.57\r\nv 193.79 -4.9 123.13\r\nv 187.24 -7.78 116\r\nv 188.17 -6.11 115.18\r\nv 180.72 -9.1 105.56\r\nv 181.75 -7.46 104.86\r\nv 184.93 -6.8 110.19\r\nv 183.95 -8.45 110.95\r\nv 175.3 -3.76 81.74\r\nv 177.15 -3.36 87.04\r\nv 176.32 -5.04 87.44\r\nv 174.45 -5.43 82.12\r\nv 179.29 -2.89 92.39\r\nv 178.49 -4.58 92.81\r\nv 177.46 -6.59 93.35\r\nv 175.26 -7.04 87.95\r\nv 173.36 -7.42 82.62\r\nv 180.96 -4.04 98.18\r\nv 181.73 -2.34 97.72\r\nv 179.98 -6.06 98.77\r\nv 177.8 -9.67 100.07\r\nv 178.88 -8.05 99.42\r\nv 175.2 -10.17 94.54\r\nv 176.32 -8.57 93.95\r\nv 170.98 -10.96 83.66\r\nv 172.16 -9.37 83.15\r\nv 174.08 -9.01 88.51\r\nv 172.93 -10.6 89.05\r\nv 170.6 -4.67 61.98\r\nv 171.42 -4.53 66.59\r\nv 170.55 -6.18 66.99\r\nv 169.72 -6.31 62.36\r\nv 172.46 -4.34 71.45\r\nv 171.59 -6 71.85\r\nv 170.48 -7.97 72.36\r\nv 169.42 -8.14 67.49\r\nv 168.59 -8.26 62.83\r\nv 172.89 -5.75 76.91\r\nv 173.74 -4.08 76.52\r\nv 171.78 -7.73 77.41\r\nv 169.36 -11.25 78.44\r\nv 170.56 -9.67 77.94\r\nv 168.03 -11.48 73.39\r\nv 169.25 -9.91 72.89\r\nv 166.1 -11.73 63.77\r\nv 167.33 -10.18 63.33\r\nv 168.18 -10.08 68.02\r\nv 166.96 -11.64 68.49\r\nv 162 -11.88 45.53\r\nv 162.18 -12.08 50.34\r\nv 162.45 -12.39 45.5\r\nv 162.63 -12.59 50.32\r\nv 162.99 -12.72 55\r\nv 162.53 -12.2 55.01\r\nv 164.4 -11.44 45.45\r\nv 164.57 -11.62 50.14\r\nv 163.47 -12.44 50.25\r\nv 163.29 -12.24 45.47\r\nv 164.91 -11.73 54.69\r\nv 163.81 -12.56 54.89\r\nv 165.03 -12.59 64.09\r\nv 164.33 -12.61 59.47\r\nv 165.42 -11.76 59.2\r\nv 164.21 -12.75 64.28\r\nv 163.51 -12.77 59.62\r\nv 163.05 -12.26 59.64\r\nv 163.75 -12.24 64.31\r\nv 186.3 4.89 -37.28\r\nv 182.33 2.57 -30.75\r\nv 190.22 7.1 -43.14\r\nv 190.67 6.51 -43.21\r\nv 186.78 4.33 -37.38\r\nv 182.83 2.05 -30.86\r\nv 196.91 10.78 -52.08\r\nv 193.83 9.1 -48.14\r\nv 197.32 10.12 -52.11\r\nv 194.26 8.47 -48.18\r\nv 198.48 10.06 -50.38\r\nv 195.5 8.47 -46.53\r\nv 194.85 8.24 -47.64\r\nv 197.87 9.87 -51.53\r\nv 191.99 6.59 -41.64\r\nv 191.29 6.32 -42.7\r\nv 184.31 2.34 -29.44\r\nv 183.52 1.96 -30.42\r\nv 187.43 4.19 -36.9\r\nv 188.18 4.52 -35.88\r\nv 245.72 29.08 -79.99\r\nv 245.73 28.97 -77.61\r\nv 240.37 27.22 -75.74\r\nv 240.35 27.28 -78.08\r\nv 245.67 29.05 -74.83\r\nv 240.32 27.34 -73.02\r\nv 234.91 25.51 -70.87\r\nv 234.96 25.32 -73.53\r\nv 234.92 25.32 -75.82\r\nv 245.5 29.43 -69.86\r\nv 240.13 27.8 -68.16\r\nv 240.23 27.56 -70.36\r\nv 245.59 29.23 -72.11\r\nv 234.73 26.1 -66.13\r\nv 234.82 25.8 -68.27\r\nv 224.58 22.83 -61.45\r\nv 224.64 22.35 -63.46\r\nv 229.57 24.04 -65.95\r\nv 229.48 24.42 -63.87\r\nv 224.67 21.86 -65.9\r\nv 229.64 23.66 -68.47\r\nv 224.54 21.28 -70.58\r\nv 229.59 23.29 -73.29\r\nv 229.65 23.37 -71.05\r\nv 224.65 21.46 -68.4\r\nv 223.74 22.48 -72.7\r\nv 219.04 20.53 -69.86\r\nv 218.76 21.33 -68.95\r\nv 224.04 21.86 -72.79\r\nv 219.36 19.89 -69.92\r\nv 215.21 18.11 -67.12\r\nv 214.87 18.77 -67.07\r\nv 214.6 19.63 -66.22\r\nv 219.95 19.39 -67.77\r\nv 219.68 19.49 -69.21\r\nv 224.33 21.44 -72.06\r\nv 215.89 17.7 -65.03\r\nv 215.57 17.73 -66.43\r\nv 209.72 15.14 -60.48\r\nv 209.3 15.06 -61.79\r\nv 212.08 16.23 -63.89\r\nv 212.45 16.26 -62.55\r\nv 208.87 15.4 -62.45\r\nv 211.68 16.59 -64.57\r\nv 208.27 17.05 -61.63\r\nv 211.08 18.19 -63.72\r\nv 211.33 17.27 -64.53\r\nv 208.51 16.09 -62.41\r\nv 164.15 -9.37 17.89\r\nv 163.23 -10.13 23.88\r\nv 165.41 -8.42 11.63\r\nv 165.91 -8.91 11.5\r\nv 164.63 -9.87 17.78\r\nv 163.7 -10.63 23.79\r\nv 169.22 -5.77 -1.79\r\nv 167.08 -7.24 5.08\r\nv 169.73 -6.26 -1.95\r\nv 167.58 -7.72 4.94\r\nv 171.43 -5.64 -0.83\r\nv 169.33 -7.05 5.92\r\nv 168.37 -7.67 5.22\r\nv 170.5 -6.22 -1.62\r\nv 167.71 -8.18 12.33\r\nv 166.71 -8.84 11.74\r\nv 165.6 -9.8 24.3\r\nv 164.53 -10.52 23.93\r\nv 165.45 -9.78 17.97\r\nv 166.49 -9.08 18.44\r\nv 178.55 0.27 -23.72\r\nv 179.06 -0.23 -23.85\r\nv 175.56 -2.43 -16.55\r\nv 175.05 -1.94 -16.4\r\nv 180.6 0.16 -22.49\r\nv 179.77 -0.28 -23.44\r\nv 177.15 -1.96 -15.26\r\nv 176.3 -2.44 -16.17\r\nv 173.17 -4.45 -8.82\r\nv 174.06 -3.92 -7.96\r\nv 172.42 -4.46 -9.18\r\nv 171.91 -3.98 -9.03\r\nv 166.81 -8.45 24.88\r\nv 166.22 -8.99 30.35\r\nv 164.97 -10.38 29.93\r\nv 168.03 -6.76 25.58\r\nv 167.49 -7.26 30.85\r\nv 167.15 -7.65 35.92\r\nv 165.85 -9.41 35.6\r\nv 164.58 -10.84 35.33\r\nv 170 -3.59 26.84\r\nv 169.53 -4.01 31.75\r\nv 168.64 -5.5 31.34\r\nv 169.14 -5.05 26.27\r\nv 169.25 -4.33 36.5\r\nv 168.33 -5.85 36.23\r\nv 169.14 -4.71 45.41\r\nv 168.2 -6.28 45.41\r\nv 168.19 -6.1 40.93\r\nv 169.12 -4.56 41.06\r\nv 167.01 -8.14 45.42\r\nv 167 -7.93 40.78\r\nv 164.39 -11.18 40.51\r\nv 165.68 -9.73 40.63\r\nv 165.69 -9.96 45.43\r\nv 197.63 1.48 123.12\r\nv 197.11 -0.29 123.79\r\nv 198.81 1.79 124.73\r\nv 198.33 0.02 125.41\r\nv 197.7 -2.09 126.29\r\nv 196.42 -2.4 124.64\r\nv 200.42 2.22 126.66\r\nv 200.01 0.45 127.36\r\nv 199.24 0.25 126.51\r\nv 199.68 2.02 125.82\r\nv 199.47 -1.66 128.27\r\nv 198.66 -1.86 127.41\r\nv 198.21 -5.44 130.3\r\nv 197.31 -5.65 129.4\r\nv 197.99 -3.94 128.4\r\nv 198.85 -3.74 129.29\r\nv 196.26 -5.89 128.24\r\nv 196.99 -4.18 127.27\r\nv 195.65 -4.48 125.6\r\nv 194.87 -6.19 126.55\r\nv 218.39 5.98 140.97\r\nv 223.31 6.9 143.85\r\nv 223.03 5.15 144.91\r\nv 218.09 4.24 142.01\r\nv 228.75 7.87 146.67\r\nv 228.51 6.1 147.75\r\nv 228.2 4.01 149.13\r\nv 222.67 3.07 146.28\r\nv 217.7 2.16 143.36\r\nv 240.64 9.69 151.64\r\nv 240.47 7.9 152.73\r\nv 234.38 7.04 150.39\r\nv 234.58 8.81 149.31\r\nv 240.26 5.78 154.12\r\nv 234.12 4.93 151.78\r\nv 239.77 1.96 157.21\r\nv 233.51 1.14 154.83\r\nv 233.81 2.84 153.31\r\nv 240.02 3.68 155.67\r\nv 227.48 0.24 152.16\r\nv 227.84 1.94 150.65\r\nv 216.79 -1.56 146.27\r\nv 217.25 0.11 144.83\r\nv 222.26 1.01 147.79\r\nv 221.85 -0.67 149.27\r\nv 246.75 10.43 153.52\r\nv 246.63 8.64 154.63\r\nv 252.92 11 154.92\r\nv 252.84 9.21 156.05\r\nv 252.73 7.08 157.5\r\nv 246.47 6.51 156.05\r\nv 259.08 9.58 156.94\r\nv 259.12 11.37 155.79\r\nv 259.03 7.45 158.42\r\nv 258.9 3.61 161.69\r\nv 258.97 5.34 160.06\r\nv 252.49 3.24 160.72\r\nv 252.61 4.97 159.12\r\nv 246.29 4.4 157.63\r\nv 246.11 2.68 159.2\r\nv 183.07 -9.38 111.63\r\nv 179.8 -10.02 106.17\r\nv 186.4 -8.73 116.73\r\nv 185.76 -8.95 117.29\r\nv 182.4 -9.59 112.15\r\nv 179.1 -10.22 106.64\r\nv 192.2 -7.56 124.87\r\nv 189.53 -8.1 121.23\r\nv 191.61 -7.8 125.51\r\nv 188.91 -8.33 121.84\r\nv 188.55 -7.83 122.2\r\nv 191.25 -7.3 125.9\r\nv 185.38 -8.45 117.63\r\nv 178.69 -9.71 106.91\r\nv 182 -9.09 112.45\r\nv 168.73 -11.5 84.53\r\nv 170.72 -11.15 90.02\r\nv 169.17 -12.01 84.38\r\nv 171.15 -11.67 89.85\r\nv 173.47 -11.25 95.42\r\nv 173.05 -10.74 95.63\r\nv 171.92 -11.49 89.51\r\nv 169.95 -11.84 84.08\r\nv 174.22 -11.07 95.05\r\nv 176.85 -10.58 100.62\r\nv 176.12 -10.77 101.05\r\nv 175.71 -10.26 101.28\r\nv 164.63 -12.15 69.12\r\nv 165.09 -12.67 69.07\r\nv 166.18 -12.52 74.01\r\nv 165.73 -12 74.09\r\nv 165.89 -12.5 68.85\r\nv 166.98 -12.35 73.77\r\nv 168.32 -12.13 78.84\r\nv 167.52 -12.3 79.11\r\nv 167.08 -11.78 79.22\r\nv 169.09 -6.39 58.02\r\nv 169.99 -4.76 57.7\r\nv 167.94 -8.32 58.42\r\nv 167.47 -8.33 54.12\r\nv 168.64 -6.42 53.83\r\nv 169.55 -4.81 53.6\r\nv 166.67 -10.23 58.83\r\nv 166.18 -10.21 54.42\r\nv 165.85 -10.12 49.99\r\nv 167.16 -8.27 49.83\r\nv 169.27 -4.79 49.55\r\nv 168.35 -6.38 49.67\r\nv 186.94 6.69 -23.08\r\nv 186.47 5.63 -24.49\r\nv 190.2 7.66 -30.81\r\nv 190.63 8.69 -29.38\r\nv 185.84 4.4 -26.2\r\nv 189.61 6.47 -32.56\r\nv 193.32 8.45 -38.22\r\nv 193.86 9.61 -36.44\r\nv 194.26 10.63 -34.97\r\nv 188.92 5.35 -34.34\r\nv 185.1 3.23 -27.94\r\nv 192.68 7.37 -40.05\r\nv 199.08 10.77 -48.71\r\nv 196.14 9.21 -44.9\r\nv 199.63 11.78 -46.8\r\nv 196.73 10.25 -43.03\r\nv 200.42 13.89 -43.42\r\nv 197.59 12.39 -39.71\r\nv 197.23 11.38 -41.21\r\nv 200.09 12.89 -44.94\r\nv 240.23 27.61 -79.64\r\nv 245.61 29.43 -81.56\r\nv 234.77 25.6 -77.36\r\nv 234.54 26.08 -78.08\r\nv 240.03 28.12 -80.35\r\nv 245.44 29.97 -82.27\r\nv 229.41 23.51 -74.8\r\nv 229.16 23.96 -75.53\r\nv 228.87 24.57 -75.42\r\nv 234.28 26.7 -77.93\r\nv 245.24 30.61 -82.07\r\nv 239.79 28.75 -80.18\r\nv 202.2 14.03 -47.48\r\nv 202.51 15.02 -45.94\r\nv 201.78 12.95 -49.37\r\nv 203.35 13.82 -51.03\r\nv 203.75 14.88 -49.11\r\nv 204.03 15.84 -47.54\r\nv 200.71 11.28 -53.03\r\nv 201.27 11.96 -51.32\r\nv 202.34 12.18 -54.74\r\nv 202.87 12.85 -53.01\r\nv 204.52 13.29 -56.55\r\nv 205 13.95 -54.77\r\nv 204.06 13.5 -54.06\r\nv 203.55 12.83 -55.82\r\nv 205.43 14.89 -52.74\r\nv 204.51 14.45 -52.05\r\nv 206.03 16.87 -49.15\r\nv 205.15 16.44 -48.51\r\nv 204.89 15.49 -50.11\r\nv 205.78 15.93 -50.76\r\nv 169.85 -4.48 21.02\r\nv 170.66 -3.06 21.77\r\nv 168.81 -6.14 20.12\r\nv 169.9 -5.35 14.4\r\nv 170.87 -3.74 15.52\r\nv 171.61 -2.37 16.43\r\nv 167.65 -7.78 19.21\r\nv 168.81 -6.92 13.28\r\nv 172.4 -4.5 0.43\r\nv 170.37 -5.85 7.04\r\nv 173.35 -3.06 1.9\r\nv 171.38 -4.34 8.36\r\nv 174.8 -0.32 4.57\r\nv 172.96 -1.48 10.74\r\nv 172.28 -2.79 9.66\r\nv 174.17 -1.58 3.36\r\nv 176.64 -0.06 -3.46\r\nv 177.22 1.15 -2.17\r\nv 175.88 -1.47 -5.03\r\nv 178.87 0.35 -12.19\r\nv 179.59 1.7 -10.55\r\nv 180.12 2.85 -9.21\r\nv 174.99 -2.84 -6.61\r\nv 178.03 -0.94 -13.85\r\nv 181.44 1.11 -21.03\r\nv 182.22 2.34 -19.33\r\nv 183.4 4.72 -16.27\r\nv 182.9 3.62 -17.65\r\nv 162.58 -10.75 29.65\r\nv 163.04 -11.25 29.58\r\nv 162.63 -11.73 35.12\r\nv 162.17 -11.23 35.18\r\nv 163.88 -11.13 29.67\r\nv 163.47 -11.61 35.18\r\nv 163.28 -11.97 40.45\r\nv 162.44 -12.11 40.43\r\nv 161.99 -11.6 40.47\r\nv 194.16 -7.16 127.41\r\nv 195.58 -6.86 129.12\r\nv 195.03 -7.1 129.8\r\nv 193.59 -7.4 128.07\r\nv 197.59 -6.41 131.2\r\nv 196.66 -6.62 130.29\r\nv 197.07 -6.65 131.91\r\nv 196.13 -6.86 130.99\r\nv 196.6 -4.92 132.39\r\nv 195.67 -5.13 131.46\r\nv 195.77 -6.36 131.41\r\nv 196.72 -6.15 132.34\r\nv 194.59 -5.37 130.27\r\nv 194.68 -6.6 130.21\r\nv 193.23 -6.9 128.47\r\nv 193.17 -5.67 128.52\r\nv 221.46 -1.62 150.55\r\nv 216.36 -2.5 147.51\r\nv 227.14 -0.71 153.47\r\nv 226.87 -0.94 154.47\r\nv 221.15 -1.83 151.52\r\nv 216.01 -2.71 148.45\r\nv 239.55 0.98 158.58\r\nv 233.23 0.17 156.17\r\nv 239.37 0.75 159.63\r\nv 233 -0.07 157.2\r\nv 232.85 0.44 157.79\r\nv 239.25 1.24 160.24\r\nv 226.69 -0.43 155.03\r\nv 215.78 -2.2 148.95\r\nv 220.95 -1.33 152.05\r\nv 245.71 1.95 162.3\r\nv 245.69 3.19 162.33\r\nv 245.8 1.45 161.67\r\nv 252.28 2.02 163.24\r\nv 252.23 2.52 163.87\r\nv 252.21 3.76 163.91\r\nv 245.94 1.69 160.59\r\nv 252.38 2.26 162.14\r\nv 258.85 2.63 163.14\r\nv 258.8 2.39 164.25\r\nv 258.77 4.13 164.91\r\nv 258.77 2.89 164.89\r\nv 258.79 6.12 164.18\r\nv 258.82 8.46 162.99\r\nv 252.33 8.09 162.03\r\nv 252.25 5.74 163.19\r\nv 258.91 12.71 160.42\r\nv 258.87 10.79 161.63\r\nv 252.5 12.33 159.51\r\nv 252.42 10.41 160.7\r\nv 246 9.83 159.21\r\nv 246.13 11.75 158.05\r\nv 245.87 7.51 160.5\r\nv 245.75 5.17 161.64\r\nv 250.83 30.61 -81.46\r\nv 250.84 30.49 -79.04\r\nv 255.77 31.8 -82.51\r\nv 255.77 31.68 -80.05\r\nv 255.74 31.74 -77.19\r\nv 250.79 30.55 -76.22\r\nv 260.58 32.46 -80.65\r\nv 260.58 32.58 -83.13\r\nv 260.57 32.53 -77.76\r\nv 260.51 32.93 -72.57\r\nv 260.54 32.71 -74.92\r\nv 255.63 32.11 -72.05\r\nv 255.69 31.91 -74.37\r\nv 250.72 30.71 -73.44\r\nv 250.65 30.91 -71.15\r\nv 250.46 32.18 -83.55\r\nv 255.51 33.39 -84.61\r\nv 255.63 32.74 -84.83\r\nv 250.62 31.53 -83.77\r\nv 260.45 34.17 -85.24\r\nv 260.51 33.51 -85.47\r\nv 260.56 32.96 -84.75\r\nv 255.72 32.18 -84.12\r\nv 250.76 30.98 -83.06\r\nv 340.21 2.88 119.6\r\nv 337.55 3.47 123.26\r\nv 343.31 2.18 115.13\r\nv 343.09 2.71 114.9\r\nv 340 3.41 119.35\r\nv 337.34 4.01 123.01\r\nv 349.73 0.74 104.71\r\nv 346.57 1.45 110.09\r\nv 349.49 1.26 104.51\r\nv 346.34 1.97 109.87\r\nv 349.26 1.36 104.33\r\nv 346.12 2.08 109.67\r\nv 346.21 2.12 109.75\r\nv 349.35 1.41 104.4\r\nv 342.87 2.82 114.68\r\nv 342.96 2.86 114.77\r\nv 337.14 4.13 122.78\r\nv 337.22 4.17 122.87\r\nv 339.87 3.57 119.22\r\nv 339.79 3.53 119.13\r\nv 352.52 0.12 99.24\r\nv 354.96 -0.41 93.71\r\nv 354.69 0.1 93.54\r\nv 352.27 0.64 99.05\r\nv 358.92 -1.25 82.69\r\nv 357.08 -0.87 88.18\r\nv 358.63 -0.74 82.54\r\nv 356.8 -0.36 88.02\r\nv 358.36 -0.65 82.41\r\nv 356.54 -0.26 87.88\r\nv 356.64 -0.22 87.93\r\nv 358.46 -0.6 82.46\r\nv 354.44 0.2 93.39\r\nv 354.54 0.24 93.45\r\nv 352.12 0.78 98.95\r\nv 352.02 0.74 98.89\r\nv 360.49 -1.55 77.29\r\nv 361.82 -1.8 72.05\r\nv 361.52 -1.3 71.91\r\nv 360.2 -1.05 77.14\r\nv 363.75 -2.12 62.31\r\nv 362.9 -1.98 67.04\r\nv 363.45 -1.62 62.17\r\nv 362.6 -1.49 66.89\r\nv 363.17 -1.54 62.09\r\nv 362.33 -1.4 66.8\r\nv 362.43 -1.35 66.82\r\nv 363.28 -1.49 62.11\r\nv 361.25 -1.21 71.8\r\nv 361.35 -1.16 71.83\r\nv 360.03 -0.91 77.07\r\nv 359.93 -0.96 77.03\r\nv 364.38 -2.21 57.93\r\nv 364.8 -2.25 53.77\r\nv 364.49 -1.77 53.69\r\nv 364.07 -1.72 57.82\r\nv 365.17 -2.16 45.54\r\nv 365.06 -2.24 49.69\r\nv 364.85 -1.69 45.53\r\nv 364.74 -1.76 49.64\r\nv 364.57 -1.62 45.52\r\nv 364.46 -1.68 49.61\r\nv 364.56 -1.63 49.62\r\nv 364.67 -1.56 45.52\r\nv 364.21 -1.69 53.64\r\nv 364.32 -1.64 53.65\r\nv 363.9 -1.59 57.77\r\nv 363.79 -1.64 57.75\r\nv 343.63 11.54 -29.67\r\nv 347.59 9.37 -23.25\r\nv 339.64 13.65 -35.39\r\nv 339.49 13.92 -34.92\r\nv 343.47 11.82 -29.21\r\nv 347.43 9.67 -22.81\r\nv 332.82 17.14 -44\r\nv 335.94 15.56 -40.23\r\nv 332.66 17.4 -43.51\r\nv 335.79 15.83 -39.74\r\nv 332.48 17.37 -43.25\r\nv 335.6 15.8 -39.49\r\nv 335.68 15.87 -39.55\r\nv 332.56 17.44 -43.3\r\nv 339.29 13.9 -34.68\r\nv 339.38 13.97 -34.73\r\nv 347.22 9.67 -22.59\r\nv 347.32 9.73 -22.63\r\nv 343.36 11.87 -29.03\r\nv 343.27 11.81 -28.98\r\nv 302.81 27.99 -64.04\r\nv 307.83 26.34 -61.75\r\nv 297.44 29.81 -66.16\r\nv 297.44 29.87 -65.48\r\nv 302.8 28.06 -63.38\r\nv 307.81 26.44 -61.11\r\nv 286.33 33.4 -69.62\r\nv 291.89 31.66 -68.04\r\nv 286.33 33.43 -68.91\r\nv 291.89 31.7 -67.35\r\nv 286.28 33.27 -68.65\r\nv 291.82 31.55 -67.08\r\nv 291.86 31.65 -67.1\r\nv 286.31 33.37 -68.67\r\nv 297.36 29.73 -65.21\r\nv 297.41 29.82 -65.24\r\nv 307.71 26.33 -60.83\r\nv 307.77 26.42 -60.87\r\nv 302.76 28.03 -63.14\r\nv 302.71 27.94 -63.1\r\nv 330.58 18.25 -46.52\r\nv 329.01 19.02 -48.1\r\nv 328.87 19.27 -47.59\r\nv 330.43 18.51 -46.02\r\nv 327.06 20.01 -49.64\r\nv 327.91 19.56 -49.04\r\nv 326.93 20.26 -49.12\r\nv 327.77 19.81 -48.52\r\nv 326.76 20.22 -48.85\r\nv 327.6 19.77 -48.25\r\nv 327.68 19.84 -48.31\r\nv 326.84 20.29 -48.9\r\nv 328.7 19.23 -47.32\r\nv 328.77 19.3 -47.38\r\nv 330.33 18.54 -45.81\r\nv 330.25 18.47 -45.76\r\nv 363.65 -0.55 21.97\r\nv 364.3 -1.07 27.02\r\nv 362.73 0.13 16.65\r\nv 362.46 0.53 16.93\r\nv 363.37 -0.13 22.2\r\nv 364 -0.64 27.2\r\nv 359.66 2.18 4.75\r\nv 361.43 1.02 10.94\r\nv 359.43 2.55 5.13\r\nv 361.18 1.41 11.28\r\nv 359.19 2.58 5.29\r\nv 360.92 1.45 11.43\r\nv 361.03 1.51 11.4\r\nv 359.29 2.64 5.27\r\nv 362.2 0.58 17.05\r\nv 362.3 0.64 17.04\r\nv 363.72 -0.58 27.27\r\nv 363.83 -0.53 27.26\r\nv 363.2 -0.03 22.29\r\nv 363.1 -0.08 22.3\r\nv 357.33 3.65 -2.04\r\nv 354.5 5.37 -9.16\r\nv 354.32 5.71 -8.74\r\nv 357.12 4 -1.64\r\nv 351.24 7.3 -16.32\r\nv 351.07 7.62 -15.89\r\nv 350.85 7.62 -15.68\r\nv 350.95 7.68 -15.72\r\nv 354.09 5.72 -8.54\r\nv 354.19 5.78 -8.58\r\nv 356.99 4.08 -1.49\r\nv 356.89 4.02 -1.46\r\nv 365.17 -2.02 41.18\r\nv 365.04 -1.8 36.64\r\nv 364.72 -1.34 36.72\r\nv 364.85 -1.55 41.22\r\nv 364.76 -1.48 31.92\r\nv 364.44 -1.04 32.04\r\nv 364.16 -0.97 32.09\r\nv 364.27 -0.92 32.09\r\nv 364.44 -1.27 36.75\r\nv 364.54 -1.22 36.75\r\nv 364.67 -1.43 41.23\r\nv 364.57 -1.48 41.23\r\nv 318.15 7.59 141.26\r\nv 313.72 8.44 144.15\r\nv 321.91 6.83 138.59\r\nv 321.75 7.37 138.25\r\nv 318 8.12 140.89\r\nv 313.58 8.97 143.75\r\nv 327.43 5.67 134.25\r\nv 325 6.19 136.22\r\nv 327.25 6.2 133.97\r\nv 324.83 6.72 135.91\r\nv 327.07 6.31 133.71\r\nv 324.66 6.83 135.64\r\nv 324.73 6.87 135.74\r\nv 327.15 6.35 133.81\r\nv 321.58 7.47 137.96\r\nv 321.65 7.51 138.07\r\nv 313.44 9.07 143.43\r\nv 313.5 9.12 143.55\r\nv 317.91 8.27 140.7\r\nv 317.85 8.22 140.59\r\nv 296.98 11.27 152.89\r\nv 290.75 12.14 155.34\r\nv 302.98 10.33 150.11\r\nv 302.88 10.87 149.7\r\nv 296.89 11.81 152.47\r\nv 290.68 12.68 154.92\r\nv 308.61 9.37 147.15\r\nv 308.49 9.91 146.74\r\nv 308.36 10.01 146.41\r\nv 308.42 10.05 146.53\r\nv 302.76 10.98 149.36\r\nv 302.81 11.02 149.48\r\nv 290.6 12.79 154.56\r\nv 290.63 12.84 154.69\r\nv 296.83 11.97 152.25\r\nv 296.79 11.92 152.13\r\nv 280.93 34.88 -70.83\r\nv 275.67 36.03 -71.69\r\nv 275.67 36.05 -70.97\r\nv 280.94 34.9 -70.12\r\nv 265.34 37.05 -72.37\r\nv 270.48 36.78 -72.2\r\nv 265.34 37.07 -71.63\r\nv 270.48 36.79 -71.47\r\nv 265.34 36.9 -71.36\r\nv 270.47 36.62 -71.2\r\nv 270.48 36.73 -71.21\r\nv 265.34 37 -71.38\r\nv 275.64 35.88 -70.7\r\nv 275.66 35.98 -70.71\r\nv 280.93 34.84 -69.87\r\nv 280.9 34.74 -69.85\r\nv 343.54 0.06 107.89\r\nv 346.7 -0.67 102.74\r\nv 340.27 0.81 112.71\r\nv 340.16 1.36 112.6\r\nv 343.43 0.6 107.78\r\nv 346.59 -0.13 102.63\r\nv 334.57 2.12 120.42\r\nv 337.18 1.52 116.97\r\nv 334.46 2.67 120.32\r\nv 337.07 2.07 116.85\r\nv 334.56 2.93 120.47\r\nv 337.18 2.32 116.97\r\nv 337.09 2.28 116.88\r\nv 334.48 2.89 120.36\r\nv 340.27 1.61 112.7\r\nv 340.18 1.57 112.61\r\nv 346.71 0.12 102.72\r\nv 346.61 0.08 102.65\r\nv 343.45 0.81 107.79\r\nv 343.54 0.86 107.87\r\nv 353.92 -2.34 86.82\r\nv 355.7 -2.74 81.52\r\nv 351.85 -1.86 92.15\r\nv 351.73 -1.33 92.07\r\nv 353.78 -1.81 86.74\r\nv 355.56 -2.21 81.44\r\nv 349.46 -1.31 97.47\r\nv 349.34 -0.77 97.38\r\nv 349.46 -0.52 97.46\r\nv 349.36 -0.56 97.39\r\nv 351.85 -1.08 92.13\r\nv 351.75 -1.12 92.07\r\nv 355.68 -1.95 81.48\r\nv 355.57 -2 81.44\r\nv 353.8 -1.6 86.74\r\nv 353.9 -1.55 86.79\r\nv 359.58 -3.52 66.36\r\nv 360.41 -3.67 61.77\r\nv 358.52 -3.32 71.22\r\nv 358.37 -2.79 71.13\r\nv 359.43 -2.99 66.26\r\nv 360.25 -3.14 61.67\r\nv 357.23 -3.06 76.3\r\nv 357.08 -2.53 76.21\r\nv 357.2 -2.28 76.25\r\nv 357.1 -2.33 76.21\r\nv 358.49 -2.54 71.15\r\nv 358.39 -2.59 71.12\r\nv 360.38 -2.89 61.67\r\nv 360.27 -2.94 61.65\r\nv 359.44 -2.79 66.24\r\nv 359.55 -2.74 66.27\r\nv 290.62 27.98 -66.85\r\nv 285.24 29.59 -68.52\r\nv 296.02 26.31 -64.86\r\nv 296.08 26.46 -64.23\r\nv 290.68 28.12 -66.2\r\nv 285.29 29.72 -67.85\r\nv 306.17 23.08 -60.24\r\nv 301.27 24.65 -62.63\r\nv 306.22 23.28 -59.67\r\nv 301.33 24.82 -62.03\r\nv 306.33 23.51 -59.53\r\nv 301.43 25.04 -61.88\r\nv 301.38 24.94 -61.85\r\nv 306.27 23.41 -59.5\r\nv 296.18 26.67 -64.07\r\nv 296.13 26.57 -64.04\r\nv 285.37 29.93 -67.66\r\nv 285.33 29.83 -67.64\r\nv 290.72 28.23 -66\r\nv 290.76 28.33 -66.03\r\nv 361.36 -3.1 32\r\nv 360.91 -2.7 27.18\r\nv 361.65 -3.4 36.66\r\nv 361.48 -2.91 36.74\r\nv 361.2 -2.62 32.12\r\nv 360.76 -2.23 27.34\r\nv 361.79 -3.75 45.41\r\nv 361.78 -3.61 41.13\r\nv 361.62 -3.24 45.42\r\nv 361.61 -3.12 41.17\r\nv 361.74 -2.99 45.43\r\nv 361.73 -2.87 41.19\r\nv 361.62 -2.92 41.19\r\nv 361.63 -3.04 45.42\r\nv 361.6 -2.66 36.76\r\nv 361.49 -2.71 36.76\r\nv 360.88 -1.99 27.38\r\nv 360.78 -2.04 27.39\r\nv 361.22 -2.43 32.16\r\nv 361.32 -2.37 32.15\r\nv 332.74 2.55 122.85\r\nv 331.48 2.86 124.5\r\nv 331.38 3.41 124.45\r\nv 332.63 3.11 122.77\r\nv 329.9 3.25 126.5\r\nv 330.6 3.08 125.63\r\nv 329.81 3.81 126.48\r\nv 330.51 3.63 125.6\r\nv 329.91 4.06 126.81\r\nv 330.61 3.89 125.91\r\nv 330.53 3.85 125.72\r\nv 329.84 4.02 126.61\r\nv 331.48 3.67 124.71\r\nv 331.4 3.63 124.54\r\nv 332.65 3.32 122.83\r\nv 332.73 3.36 122.97\r\nv 307.25 7.96 143.26\r\nv 312.18 7.02 140.42\r\nv 301.8 8.94 146.07\r\nv 301.76 9.49 145.83\r\nv 307.2 8.51 143.02\r\nv 312.13 7.57 140.19\r\nv 289.95 10.77 151.04\r\nv 295.98 9.89 148.71\r\nv 289.93 11.34 150.8\r\nv 295.95 10.46 148.47\r\nv 289.97 11.6 150.93\r\nv 296.01 10.71 148.6\r\nv 295.97 10.67 148.47\r\nv 289.94 11.55 150.8\r\nv 301.82 9.75 145.95\r\nv 301.77 9.71 145.83\r\nv 312.2 7.83 140.3\r\nv 312.15 7.78 140.18\r\nv 307.22 8.73 143.02\r\nv 307.27 8.77 143.14\r\nv 283.86 11.52 152.91\r\nv 277.71 12.09 154.29\r\nv 277.7 12.66 154.04\r\nv 283.84 12.08 152.66\r\nv 265.34 12.6 155.44\r\nv 271.53 12.47 155.15\r\nv 265.34 13.17 155.18\r\nv 271.52 13.03 154.89\r\nv 265.34 13.43 155.31\r\nv 271.54 13.3 155.02\r\nv 271.53 13.25 154.89\r\nv 265.34 13.39 155.18\r\nv 277.72 12.92 154.17\r\nv 277.7 12.88 154.04\r\nv 283.85 12.3 152.67\r\nv 283.87 12.35 152.8\r\nv 361.68 -3.81 49.48\r\nv 361.43 -3.82 53.46\r\nv 361.27 -3.3 53.4\r\nv 361.51 -3.3 49.45\r\nv 361.02 -3.77 57.51\r\nv 360.86 -3.25 57.43\r\nv 360.98 -2.99 57.43\r\nv 360.87 -3.04 57.42\r\nv 361.39 -3.05 53.41\r\nv 361.28 -3.1 53.39\r\nv 361.52 -3.1 49.44\r\nv 361.63 -3.05 49.45\r\nv 333.12 13.33 -38.82\r\nv 330.09 14.78 -42.5\r\nv 336.73 11.52 -34.11\r\nv 336.72 11.87 -33.7\r\nv 333.13 13.67 -38.41\r\nv 330.1 15.12 -42.08\r\nv 344.48 7.44 -22.25\r\nv 340.61 9.52 -28.53\r\nv 344.46 7.79 -21.86\r\nv 340.6 9.86 -28.13\r\nv 344.62 8.01 -21.79\r\nv 340.77 10.08 -28.07\r\nv 340.67 10.02 -28.02\r\nv 344.52 7.95 -21.75\r\nv 336.9 12.09 -33.65\r\nv 336.8 12.02 -33.6\r\nv 330.3 15.39 -42.02\r\nv 330.18 15.29 -41.97\r\nv 333.2 13.83 -38.3\r\nv 333.32 13.91 -38.35\r\nv 310.51 21.34 -58.93\r\nv 310.53 21.69 -57.79\r\nv 314.32 20.07 -56.5\r\nv 314.32 20.47 -55.41\r\nv 314.35 20.71 -54.88\r\nv 310.58 21.9 -57.24\r\nv 320.01 18.13 -52.47\r\nv 319.95 18.61 -51.45\r\nv 317.48 19.44 -53.25\r\nv 317.51 19 -54.3\r\nv 319.96 18.91 -50.97\r\nv 317.49 19.71 -52.74\r\nv 320.1 19.28 -50.89\r\nv 317.62 20.04 -52.65\r\nv 317.55 19.89 -52.61\r\nv 320.02 19.11 -50.84\r\nv 314.47 21 -54.78\r\nv 314.4 20.87 -54.73\r\nv 310.63 22.05 -57.08\r\nv 310.69 22.16 -57.12\r\nv 358.07 -0.67 11.38\r\nv 356.33 0.46 5.29\r\nv 359.36 -1.54 16.98\r\nv 359.24 -1.09 17.24\r\nv 357.97 -0.24 11.69\r\nv 356.25 0.87 5.63\r\nv 360.27 -2.2 22.21\r\nv 360.13 -1.74 22.42\r\nv 360.26 -1.5 22.48\r\nv 360.16 -1.55 22.49\r\nv 359.37 -0.86 17.3\r\nv 359.26 -0.91 17.32\r\nv 356.39 1.09 5.71\r\nv 356.29 1.04 5.73\r\nv 358.01 -0.06 11.78\r\nv 358.11 -0.01 11.75\r\nv 348.05 5.44 -15.45\r\nv 351.26 3.57 -8.41\r\nv 351.21 3.95 -8.03\r\nv 348.02 5.8 -15.06\r\nv 354.04 1.89 -1.4\r\nv 353.98 2.28 -1.03\r\nv 354.12 2.51 -0.96\r\nv 354.02 2.45 -0.93\r\nv 351.36 4.17 -7.95\r\nv 351.26 4.11 -7.92\r\nv 348.08 5.96 -14.96\r\nv 348.17 6.02 -14.99\r\nv 271.73 13.84 159.64\r\nv 265.34 13.98 159.95\r\nv 278.11 13.46 158.75\r\nv 278.08 14.01 158.31\r\nv 271.71 14.39 159.19\r\nv 265.34 14.53 159.5\r\nv 284.46 12.88 157.3\r\nv 284.4 13.43 156.87\r\nv 284.34 13.54 156.51\r\nv 284.37 13.58 156.64\r\nv 278.03 14.12 157.94\r\nv 278.05 14.16 158.07\r\nv 265.34 14.63 159.13\r\nv 265.34 14.68 159.26\r\nv 271.7 14.54 158.96\r\nv 271.69 14.49 158.82\r\nv 270.18 33.09 -71.17\r\nv 265.34 33.41 -71.33\r\nv 275.08 32.27 -70.66\r\nv 275.1 32.4 -69.97\r\nv 270.19 33.22 -70.47\r\nv 265.34 33.54 -70.63\r\nv 280.08 31.06 -69.78\r\nv 280.12 31.19 -69.1\r\nv 280.18 31.4 -68.9\r\nv 280.15 31.3 -68.89\r\nv 275.15 32.61 -69.76\r\nv 275.13 32.51 -69.75\r\nv 265.34 33.78 -70.42\r\nv 265.34 33.66 -70.41\r\nv 270.2 33.34 -70.25\r\nv 270.21 33.45 -70.26\r\nv 185.09 1.45 110.09\r\nv 181.96 0.74 104.71\r\nv 188.28 2.18 115.11\r\nv 188.54 2.71 114.88\r\nv 185.37 1.97 109.87\r\nv 182.25 1.26 104.51\r\nv 193.75 3.48 123.13\r\nv 191.26 2.88 119.54\r\nv 193.99 4.02 122.87\r\nv 191.5 3.42 119.3\r\nv 194.2 4.13 122.64\r\nv 191.73 3.53 119.07\r\nv 191.64 3.57 119.16\r\nv 194.12 4.17 122.73\r\nv 188.78 2.82 114.67\r\nv 188.69 2.86 114.75\r\nv 182.5 1.36 104.33\r\nv 182.4 1.41 104.4\r\nv 185.52 2.12 109.75\r\nv 185.61 2.08 109.67\r\nv 174.44 -0.87 88.18\r\nv 172.55 -1.25 82.69\r\nv 176.64 -0.41 93.71\r\nv 176.95 0.1 93.54\r\nv 174.77 -0.36 88.02\r\nv 172.88 -0.74 82.54\r\nv 179.14 0.12 99.24\r\nv 179.44 0.64 99.05\r\nv 179.7 0.74 98.89\r\nv 179.61 0.78 98.95\r\nv 177.22 0.2 93.39\r\nv 177.12 0.24 93.45\r\nv 173.16 -0.65 82.41\r\nv 173.06 -0.6 82.46\r\nv 174.94 -0.22 87.93\r\nv 175.04 -0.26 87.88\r\nv 168.58 -1.98 67.04\r\nv 167.75 -2.12 62.31\r\nv 169.64 -1.8 72.05\r\nv 169.98 -1.3 71.91\r\nv 168.92 -1.49 66.89\r\nv 168.09 -1.62 62.17\r\nv 170.96 -1.55 77.29\r\nv 171.3 -1.05 77.14\r\nv 171.58 -0.96 77.03\r\nv 171.48 -0.91 77.07\r\nv 170.27 -1.21 71.8\r\nv 170.16 -1.16 71.83\r\nv 168.38 -1.54 62.09\r\nv 168.28 -1.49 62.11\r\nv 169.1 -1.35 66.82\r\nv 169.21 -1.4 66.8\r\nv 166.41 -2.24 49.69\r\nv 166.27 -2.16 45.54\r\nv 166.68 -2.25 53.77\r\nv 167.04 -1.77 53.69\r\nv 166.77 -1.76 49.64\r\nv 166.63 -1.69 45.53\r\nv 167.12 -2.21 57.93\r\nv 167.47 -1.72 57.82\r\nv 167.76 -1.64 57.75\r\nv 167.66 -1.59 57.77\r\nv 167.33 -1.69 53.64\r\nv 167.23 -1.64 53.65\r\nv 166.93 -1.62 45.52\r\nv 166.82 -1.56 45.52\r\nv 166.95 -1.63 49.62\r\nv 167.06 -1.68 49.61\r\nv 195.1 15.6 -40.24\r\nv 198.03 17.24 -44.03\r\nv 191.64 13.66 -35.39\r\nv 191.83 13.93 -34.92\r\nv 195.27 15.87 -39.76\r\nv 198.18 17.5 -43.54\r\nv 184.1 9.37 -23.25\r\nv 187.9 11.54 -29.67\r\nv 184.32 9.67 -22.81\r\nv 188.1 11.82 -29.21\r\nv 184.54 9.67 -22.59\r\nv 188.31 11.81 -28.98\r\nv 188.22 11.87 -29.03\r\nv 184.45 9.73 -22.63\r\nv 192.03 13.91 -34.68\r\nv 191.95 13.98 -34.73\r\nv 198.37 17.48 -43.28\r\nv 198.29 17.55 -43.33\r\nv 195.38 15.91 -39.56\r\nv 195.46 15.84 -39.5\r\nv 238.78 31.66 -68.04\r\nv 244.34 33.4 -69.62\r\nv 233.24 29.8 -66.16\r\nv 233.24 29.86 -65.48\r\nv 238.78 31.7 -67.35\r\nv 244.34 33.43 -68.91\r\nv 222.86 26.25 -61.77\r\nv 227.87 27.96 -64.05\r\nv 222.89 26.35 -61.13\r\nv 227.88 28.03 -63.39\r\nv 222.99 26.24 -60.85\r\nv 227.97 27.9 -63.11\r\nv 227.92 27.99 -63.15\r\nv 222.93 26.33 -60.89\r\nv 233.32 29.72 -65.21\r\nv 233.27 29.81 -65.24\r\nv 244.39 33.27 -68.65\r\nv 244.36 33.37 -68.67\r\nv 238.81 31.65 -67.1\r\nv 238.85 31.55 -67.08\r\nv 218.38 24.49 -60.59\r\nv 218.39 24.83 -59.39\r\nv 214.46 23.26 -58.21\r\nv 214.51 23.66 -57.06\r\nv 214.56 23.82 -56.46\r\nv 218.43 24.96 -58.77\r\nv 208.58 21.49 -54.24\r\nv 208.7 22 -53.17\r\nv 211.26 22.72 -54.94\r\nv 211.17 22.26 -56.04\r\nv 208.79 22.22 -52.61\r\nv 211.34 22.91 -54.36\r\nv 208.94 22.17 -52.33\r\nv 211.48 22.85 -54.08\r\nv 211.41 22.93 -54.13\r\nv 208.87 22.24 -52.39\r\nv 214.69 23.74 -56.18\r\nv 214.63 23.82 -56.23\r\nv 218.48 24.94 -58.53\r\nv 218.55 24.86 -58.49\r\nv 169.98 1.02 10.94\r\nv 171.81 2.18 4.75\r\nv 168.65 0.13 16.65\r\nv 168.95 0.53 16.93\r\nv 170.26 1.41 11.28\r\nv 172.07 2.55 5.13\r\nv 167.08 -1.07 27.02\r\nv 167.72 -0.55 21.97\r\nv 167.42 -0.64 27.2\r\nv 168.04 -0.13 22.2\r\nv 167.71 -0.58 27.27\r\nv 168.32 -0.08 22.3\r\nv 168.22 -0.03 22.29\r\nv 167.6 -0.53 27.26\r\nv 169.23 0.58 17.05\r\nv 169.12 0.64 17.04\r\nv 172.33 2.58 5.29\r\nv 172.23 2.64 5.27\r\nv 170.42 1.51 11.4\r\nv 170.53 1.45 11.43\r\nv 180.49 7.3 -16.32\r\nv 177.17 5.37 -9.16\r\nv 177.4 5.71 -8.74\r\nv 180.72 7.62 -15.89\r\nv 174.24 3.65 -2.04\r\nv 174.49 4 -1.64\r\nv 174.74 4.02 -1.46\r\nv 174.64 4.08 -1.49\r\nv 177.65 5.72 -8.54\r\nv 177.55 5.78 -8.58\r\nv 180.85 7.68 -15.72\r\nv 180.95 7.62 -15.68\r\nv 166.64 -1.48 31.92\r\nv 166.37 -1.8 36.64\r\nv 166.72 -1.34 36.72\r\nv 166.99 -1.04 32.04\r\nv 166.25 -2.02 41.18\r\nv 166.61 -1.55 41.22\r\nv 166.91 -1.48 41.23\r\nv 166.8 -1.43 41.23\r\nv 167.02 -1.27 36.75\r\nv 166.91 -1.22 36.75\r\nv 167.17 -0.92 32.09\r\nv 167.28 -0.97 32.09\r\nv 195.14 2.79 126.06\r\nv 195.5 3.91 125.62\r\nv 196.38 3.1 127.75\r\nv 196.72 4.22 127.3\r\nv 196.92 4.77 127.02\r\nv 195.72 4.45 125.36\r\nv 198.07 3.54 129.76\r\nv 198.36 4.66 129.29\r\nv 197.6 4.46 128.43\r\nv 197.29 3.34 128.89\r\nv 198.54 5.2 129.01\r\nv 197.79 5 128.15\r\nv 198.73 5.31 128.76\r\nv 197.99 5.11 127.9\r\nv 197.91 5.15 128\r\nv 198.65 5.35 128.85\r\nv 197.12 4.88 126.78\r\nv 197.04 4.92 126.87\r\nv 195.85 4.61 125.21\r\nv 195.93 4.57 125.11\r\nv 222.08 9.38 147.13\r\nv 217.02 8.47 144.12\r\nv 227.69 10.34 150.11\r\nv 227.8 10.87 149.69\r\nv 222.2 9.92 146.72\r\nv 217.15 9 143.72\r\nv 239.92 12.14 155.34\r\nv 233.69 11.27 152.89\r\nv 239.99 12.68 154.92\r\nv 233.78 11.81 152.47\r\nv 240.07 12.79 154.56\r\nv 233.88 11.92 152.13\r\nv 233.84 11.97 152.25\r\nv 240.04 12.84 154.69\r\nv 227.92 10.98 149.35\r\nv 227.87 11.02 149.47\r\nv 217.29 9.1 143.39\r\nv 217.23 9.15 143.51\r\nv 222.28 10.07 146.51\r\nv 222.33 10.02 146.39\r\nv 260.19 36.78 -72.2\r\nv 255 36.03 -71.69\r\nv 255 36.05 -70.97\r\nv 260.19 36.79 -71.47\r\nv 249.74 34.88 -70.83\r\nv 249.73 34.9 -70.12\r\nv 249.77 34.74 -69.85\r\nv 249.75 34.84 -69.87\r\nv 255.03 35.88 -70.7\r\nv 255.01 35.98 -70.71\r\nv 260.19 36.73 -71.21\r\nv 260.2 36.62 -71.2\r\nv 193.84 1.52 116.95\r\nv 196.23 2.12 120.38\r\nv 190.97 0.81 112.71\r\nv 191.11 1.36 112.59\r\nv 193.96 2.07 116.82\r\nv 196.35 2.68 120.25\r\nv 184.88 -0.67 102.74\r\nv 187.9 0.06 107.89\r\nv 185.03 -0.13 102.63\r\nv 188.05 0.6 107.78\r\nv 184.93 0.12 102.72\r\nv 187.95 0.86 107.87\r\nv 188.04 0.81 107.79\r\nv 185.02 0.08 102.65\r\nv 191.01 1.61 112.69\r\nv 191.1 1.57 112.6\r\nv 196.25 2.93 120.35\r\nv 196.33 2.89 120.26\r\nv 193.95 2.28 116.84\r\nv 193.86 2.32 116.92\r\nv 182.15 -1.31 97.47\r\nv 179.73 -1.86 92.15\r\nv 179.91 -1.33 92.07\r\nv 182.32 -0.77 97.38\r\nv 175.77 -2.74 81.52\r\nv 177.6 -2.34 86.82\r\nv 175.96 -2.21 81.44\r\nv 177.79 -1.81 86.74\r\nv 175.85 -1.95 81.48\r\nv 177.68 -1.55 86.79\r\nv 177.78 -1.6 86.74\r\nv 175.95 -2 81.44\r\nv 179.8 -1.08 92.13\r\nv 179.9 -1.12 92.07\r\nv 182.31 -0.56 97.39\r\nv 182.22 -0.52 97.46\r\nv 174.22 -3.06 76.3\r\nv 172.94 -3.32 71.22\r\nv 173.13 -2.79 71.13\r\nv 174.41 -2.53 76.21\r\nv 171.09 -3.67 61.77\r\nv 171.9 -3.52 66.36\r\nv 171.29 -3.14 61.67\r\nv 172.1 -2.99 66.26\r\nv 171.18 -2.89 61.67\r\nv 171.99 -2.74 66.27\r\nv 172.09 -2.79 66.24\r\nv 171.28 -2.94 61.65\r\nv 173.02 -2.54 71.15\r\nv 173.13 -2.59 71.12\r\nv 174.41 -2.33 76.21\r\nv 174.3 -2.28 76.25\r\nv 229.41 24.68 -62.64\r\nv 224.53 23.15 -60.26\r\nv 234.65 26.32 -64.86\r\nv 234.59 26.48 -64.23\r\nv 229.35 24.86 -62.03\r\nv 224.48 23.37 -59.68\r\nv 245.43 29.59 -68.52\r\nv 240.05 27.98 -66.85\r\nv 245.38 29.73 -67.85\r\nv 239.99 28.13 -66.2\r\nv 245.3 30.02 -67.66\r\nv 239.91 28.41 -66.03\r\nv 239.95 28.26 -66\r\nv 245.34 29.87 -67.64\r\nv 234.49 26.75 -64.07\r\nv 234.54 26.61 -64.04\r\nv 224.37 23.65 -59.55\r\nv 224.42 23.53 -59.52\r\nv 229.3 25 -61.85\r\nv 229.25 25.13 -61.89\r\nv 169.64 -3.61 41.13\r\nv 169.66 -3.75 45.41\r\nv 169.76 -3.4 36.66\r\nv 169.97 -2.91 36.74\r\nv 169.85 -3.12 41.17\r\nv 169.87 -3.24 45.42\r\nv 170.47 -2.7 27.18\r\nv 170.03 -3.1 32\r\nv 170.66 -2.23 27.34\r\nv 170.23 -2.62 32.12\r\nv 170.55 -1.99 27.38\r\nv 170.12 -2.37 32.15\r\nv 170.23 -2.43 32.16\r\nv 170.65 -2.04 27.39\r\nv 169.86 -2.66 36.76\r\nv 169.97 -2.71 36.76\r\nv 169.76 -2.99 45.43\r\nv 169.87 -3.04 45.42\r\nv 169.85 -2.92 41.19\r\nv 169.74 -2.87 41.19\r\nv 214.18 5.16 138.17\r\nv 214.34 6.22 137.61\r\nv 218.55 7.04 140.38\r\nv 210.62 4.44 135.55\r\nv 210.79 5.51 135.03\r\nv 210.85 6.06 134.81\r\nv 214.4 6.77 137.38\r\nv 218.6 7.6 140.14\r\nv 205.36 3.33 131.2\r\nv 205.54 4.4 130.75\r\nv 207.87 4.9 132.71\r\nv 207.69 3.83 133.2\r\nv 205.59 4.96 130.57\r\nv 207.92 5.45 132.51\r\nv 205.49 5.21 130.68\r\nv 207.82 5.71 132.62\r\nv 207.89 5.66 132.52\r\nv 205.56 5.17 130.58\r\nv 210.75 6.31 134.92\r\nv 210.82 6.27 134.81\r\nv 218.52 7.85 140.25\r\nv 218.58 7.81 140.14\r\nv 214.37 6.99 137.38\r\nv 214.31 7.03 137.49\r\nv 234.69 9.89 148.71\r\nv 240.72 10.77 151.04\r\nv 228.88 8.94 146.07\r\nv 228.92 9.5 145.83\r\nv 234.72 10.46 148.47\r\nv 240.74 11.34 150.8\r\nv 223.45 7.97 143.25\r\nv 223.5 8.52 143\r\nv 223.42 8.78 143.12\r\nv 223.48 8.74 143\r\nv 228.85 9.76 145.95\r\nv 228.9 9.71 145.83\r\nv 240.7 11.6 150.93\r\nv 240.73 11.55 150.8\r\nv 234.71 10.67 148.47\r\nv 234.66 10.71 148.6\r\nv 259.14 12.47 155.15\r\nv 252.96 12.09 154.29\r\nv 252.97 12.66 154.04\r\nv 259.15 13.03 154.89\r\nv 246.82 11.52 152.91\r\nv 246.83 12.08 152.66\r\nv 246.8 12.35 152.8\r\nv 246.82 12.3 152.67\r\nv 252.95 12.92 154.17\r\nv 252.97 12.88 154.04\r\nv 259.14 13.25 154.89\r\nv 259.13 13.3 155.02\r\nv 170.48 -3.77 57.51\r\nv 170.06 -3.82 53.46\r\nv 170.26 -3.3 53.4\r\nv 170.68 -3.25 57.43\r\nv 169.79 -3.81 49.48\r\nv 169.99 -3.3 49.45\r\nv 169.89 -3.05 49.45\r\nv 169.99 -3.1 49.44\r\nv 170.15 -3.05 53.41\r\nv 170.26 -3.1 53.39\r\nv 170.68 -3.04 57.42\r\nv 170.57 -2.99 57.43\r\nv 190.86 9.36 -28.53\r\nv 187.18 7.36 -22.25\r\nv 194.46 11.3 -34.11\r\nv 194.53 11.71 -33.71\r\nv 190.93 9.75 -28.13\r\nv 187.26 7.74 -21.86\r\nv 200.59 14.57 -42.53\r\nv 197.78 13.07 -38.84\r\nv 200.64 15.01 -42.11\r\nv 197.84 13.5 -38.42\r\nv 200.52 15.44 -42.05\r\nv 197.72 13.89 -38.37\r\nv 197.8 13.74 -38.32\r\nv 200.6 15.28 -42\r\nv 194.4 12.05 -33.65\r\nv 194.49 11.93 -33.6\r\nv 187.13 8 -21.79\r\nv 187.23 7.92 -21.75\r\nv 190.89 9.95 -28.02\r\nv 190.8 10.05 -28.07\r\nv 202.67 15.7 -45.03\r\nv 204.17 16.52 -46.61\r\nv 204.21 16.96 -46.18\r\nv 202.71 16.14 -44.6\r\nv 206.15 17.52 -48.2\r\nv 205.28 17.1 -47.57\r\nv 206.17 17.96 -47.75\r\nv 205.31 17.54 -47.13\r\nv 206.06 18.41 -47.68\r\nv 205.19 17.99 -47.07\r\nv 205.27 17.82 -47.02\r\nv 206.13 18.23 -47.63\r\nv 204.09 17.41 -46.12\r\nv 204.17 17.24 -46.07\r\nv 202.67 16.42 -44.49\r\nv 202.59 16.59 -44.54\r\nv 171.1 -2.2 22.21\r\nv 172.02 -1.54 16.98\r\nv 172.18 -1.09 17.24\r\nv 171.27 -1.74 22.42\r\nv 175.13 0.46 5.29\r\nv 173.33 -0.67 11.38\r\nv 175.25 0.87 5.63\r\nv 173.47 -0.24 11.69\r\nv 175.12 1.09 5.71\r\nv 173.34 -0.01 11.76\r\nv 173.45 -0.06 11.78\r\nv 175.23 1.04 5.73\r\nv 172.05 -0.86 17.3\r\nv 172.16 -0.91 17.32\r\nv 171.26 -1.55 22.49\r\nv 171.16 -1.5 22.48\r\nv 177.53 1.89 -1.4\r\nv 180.41 3.56 -8.41\r\nv 180.51 3.94 -8.03\r\nv 177.64 2.28 -1.03\r\nv 183.66 5.41 -15.45\r\nv 183.75 5.78 -15.06\r\nv 183.62 6.02 -14.99\r\nv 183.72 5.95 -14.96\r\nv 180.38 4.17 -7.95\r\nv 180.47 4.11 -7.92\r\nv 177.61 2.45 -0.93\r\nv 177.51 2.51 -0.96\r\nv 246.21 12.88 157.3\r\nv 252.56 13.46 158.75\r\nv 252.59 14.01 158.31\r\nv 246.27 13.43 156.87\r\nv 258.94 13.84 159.64\r\nv 258.96 14.39 159.19\r\nv 258.98 14.49 158.82\r\nv 258.97 14.54 158.96\r\nv 252.64 14.12 157.94\r\nv 252.62 14.16 158.07\r\nv 246.3 13.58 156.64\r\nv 246.33 13.54 156.51\r\nv 250.59 31.06 -69.78\r\nv 255.59 32.27 -70.66\r\nv 255.57 32.4 -69.97\r\nv 250.55 31.2 -69.1\r\nv 260.49 33.09 -71.17\r\nv 260.48 33.23 -70.47\r\nv 260.46 33.48 -70.26\r\nv 260.47 33.35 -70.25\r\nv 255.53 32.68 -69.76\r\nv 255.54 32.53 -69.75\r\nv 250.52 31.33 -68.89\r\nv 250.49 31.49 -68.9\r\nv 349.16 1.32 104.26\r\nv 346.02 2.04 109.59\r\nv 349.05 1.27 104.18\r\nv 345.92 1.99 109.51\r\nv 342.68 2.73 114.51\r\nv 342.77 2.78 114.6\r\nv 348.83 1.17 104.01\r\nv 345.71 1.89 109.33\r\nv 345.81 1.94 109.42\r\nv 348.94 1.22 104.1\r\nv 342.47 2.64 114.32\r\nv 342.57 2.69 114.41\r\nv 336.76 3.94 122.37\r\nv 336.86 3.99 122.47\r\nv 339.5 3.39 118.84\r\nv 339.4 3.34 118.74\r\nv 336.96 4.04 122.58\r\nv 339.6 3.44 118.94\r\nv 339.69 3.48 119.04\r\nv 337.05 4.08 122.68\r\nv 358.25 -0.7 82.36\r\nv 356.43 -0.31 87.83\r\nv 358.14 -0.75 82.31\r\nv 356.32 -0.36 87.77\r\nv 354.22 0.1 93.26\r\nv 354.33 0.15 93.33\r\nv 357.9 -0.85 82.21\r\nv 356.09 -0.46 87.65\r\nv 356.21 -0.41 87.71\r\nv 358.02 -0.8 82.26\r\nv 354 0 93.13\r\nv 354.11 0.05 93.19\r\nv 351.7 0.59 98.67\r\nv 351.59 0.54 98.6\r\nv 351.81 0.64 98.75\r\nv 351.92 0.69 98.82\r\nv 363.06 -1.59 62.06\r\nv 362.21 -1.45 66.76\r\nv 362.94 -1.64 62.04\r\nv 362.1 -1.5 66.73\r\nv 361.02 -1.31 71.72\r\nv 361.14 -1.26 71.76\r\nv 362.7 -1.75 61.98\r\nv 361.85 -1.61 66.66\r\nv 361.98 -1.55 66.7\r\nv 362.82 -1.7 62.01\r\nv 360.78 -1.42 71.64\r\nv 360.9 -1.36 71.68\r\nv 359.58 -1.11 76.89\r\nv 359.46 -1.17 76.85\r\nv 359.7 -1.06 76.94\r\nv 359.82 -1.01 76.98\r\nv 364.45 -1.67 45.51\r\nv 364.34 -1.73 49.59\r\nv 364.33 -1.72 45.5\r\nv 364.22 -1.79 49.58\r\nv 363.98 -1.79 53.6\r\nv 364.09 -1.74 53.62\r\nv 364.09 -1.84 45.49\r\nv 363.98 -1.9 49.56\r\nv 364.1 -1.85 49.57\r\nv 364.21 -1.78 45.5\r\nv 363.73 -1.91 53.57\r\nv 363.85 -1.85 53.59\r\nv 363.44 -1.8 57.69\r\nv 363.31 -1.85 57.66\r\nv 363.56 -1.74 57.71\r\nv 363.68 -1.69 57.73\r\nv 332.4 17.3 -43.18\r\nv 335.51 15.73 -39.43\r\nv 332.31 17.22 -43.12\r\nv 335.42 15.66 -39.37\r\nv 339.11 13.76 -34.57\r\nv 339.2 13.83 -34.62\r\nv 332.12 17.07 -42.99\r\nv 335.23 15.5 -39.25\r\nv 335.32 15.58 -39.31\r\nv 332.22 17.15 -43.06\r\nv 338.91 13.61 -34.45\r\nv 339.01 13.69 -34.51\r\nv 346.81 9.4 -22.4\r\nv 346.91 9.47 -22.45\r\nv 342.97 11.6 -28.83\r\nv 342.87 11.53 -28.77\r\nv 347.02 9.54 -22.5\r\nv 343.08 11.67 -28.88\r\nv 343.18 11.74 -28.93\r\nv 347.12 9.61 -22.55\r\nv 286.24 33.16 -68.63\r\nv 291.77 31.45 -67.05\r\nv 286.2 33.05 -68.61\r\nv 291.73 31.34 -67.03\r\nv 297.25 29.52 -65.14\r\nv 297.3 29.63 -65.18\r\nv 286.11 32.82 -68.56\r\nv 291.63 31.12 -66.97\r\nv 291.68 31.23 -67\r\nv 286.16 32.94 -68.58\r\nv 297.13 29.31 -65.08\r\nv 297.19 29.42 -65.11\r\nv 307.44 25.94 -60.65\r\nv 307.51 26.04 -60.7\r\nv 302.53 27.64 -62.99\r\nv 302.46 27.53 -62.95\r\nv 307.58 26.14 -60.74\r\nv 302.59 27.74 -63.03\r\nv 302.65 27.84 -63.07\r\nv 307.64 26.23 -60.79\r\nv 312.11 24.95 -58.4\r\nv 312.18 25.04 -58.45\r\nv 312.04 24.85 -58.35\r\nv 315.92 23.83 -56.02\r\nv 315.99 23.92 -56.07\r\nv 316.06 24 -56.12\r\nv 311.89 24.66 -58.25\r\nv 311.96 24.76 -58.3\r\nv 315.76 23.64 -55.91\r\nv 315.84 23.73 -55.96\r\nv 321.57 22.11 -51.98\r\nv 321.65 22.2 -52.04\r\nv 319.09 22.9 -53.82\r\nv 319.01 22.81 -53.76\r\nv 321.74 22.29 -52.1\r\nv 319.17 22.99 -53.88\r\nv 321.89 22.45 -52.22\r\nv 319.32 23.16 -53.99\r\nv 319.25 23.08 -53.94\r\nv 321.82 22.37 -52.16\r\nv 359.08 2.53 5.32\r\nv 360.81 1.4 11.45\r\nv 358.96 2.47 5.35\r\nv 360.7 1.34 11.47\r\nv 361.97 0.47 17.09\r\nv 362.08 0.53 17.07\r\nv 358.73 2.34 5.41\r\nv 360.46 1.22 11.52\r\nv 360.58 1.28 11.5\r\nv 358.84 2.41 5.38\r\nv 361.73 0.35 17.13\r\nv 361.85 0.41 17.11\r\nv 363.25 -0.81 27.31\r\nv 363.37 -0.75 27.3\r\nv 362.75 -0.25 22.34\r\nv 362.62 -0.31 22.36\r\nv 363.49 -0.69 27.29\r\nv 362.87 -0.19 22.33\r\nv 362.99 -0.13 22.31\r\nv 363.61 -0.63 27.28\r\nv 350.75 7.56 -15.64\r\nv 350.64 7.5 -15.6\r\nv 353.88 5.6 -8.47\r\nv 353.99 5.66 -8.51\r\nv 350.42 7.36 -15.52\r\nv 350.53 7.43 -15.56\r\nv 353.65 5.47 -8.4\r\nv 353.76 5.54 -8.43\r\nv 356.55 3.84 -1.36\r\nv 356.44 3.78 -1.33\r\nv 356.67 3.91 -1.39\r\nv 356.78 3.97 -1.43\r\nv 364.05 -1.03 32.1\r\nv 363.93 -1.08 32.1\r\nv 364.21 -1.38 36.75\r\nv 364.32 -1.33 36.75\r\nv 363.69 -1.2 32.12\r\nv 363.81 -1.14 32.11\r\nv 363.96 -1.5 36.75\r\nv 364.08 -1.44 36.75\r\nv 364.21 -1.65 41.22\r\nv 364.09 -1.71 41.22\r\nv 364.33 -1.59 41.22\r\nv 364.45 -1.54 41.22\r\nv 335.11 4.52 125.29\r\nv 335.2 4.56 125.39\r\nv 335.02 4.47 125.19\r\nv 333.62 4.78 127.01\r\nv 333.7 4.82 127.11\r\nv 333.79 4.87 127.22\r\nv 334.83 4.38 124.97\r\nv 334.92 4.42 125.08\r\nv 333.43 4.68 126.79\r\nv 333.52 4.73 126.9\r\nv 331.55 5.08 128.98\r\nv 331.64 5.13 129.09\r\nv 332.49 4.95 128.16\r\nv 332.39 4.91 128.04\r\nv 331.73 5.18 129.2\r\nv 332.58 5 128.27\r\nv 331.9 5.27 129.42\r\nv 332.75 5.09 128.48\r\nv 332.67 5.04 128.37\r\nv 331.82 5.22 129.31\r\nv 313.38 9.03 143.31\r\nv 308.3 9.96 146.28\r\nv 313.31 8.98 143.19\r\nv 308.24 9.91 146.15\r\nv 302.66 10.88 149.09\r\nv 302.71 10.93 149.23\r\nv 313.18 8.88 142.92\r\nv 308.12 9.81 145.88\r\nv 308.18 9.86 146.02\r\nv 313.24 8.93 143.06\r\nv 302.54 10.78 148.81\r\nv 302.6 10.83 148.95\r\nv 290.44 12.6 153.99\r\nv 290.48 12.65 154.14\r\nv 296.65 11.78 151.71\r\nv 296.6 11.73 151.57\r\nv 290.52 12.7 154.28\r\nv 296.7 11.83 151.85\r\nv 296.74 11.88 151.99\r\nv 290.56 12.75 154.43\r\nv 265.34 36.79 -71.35\r\nv 270.46 36.51 -71.19\r\nv 265.34 36.67 -71.33\r\nv 270.45 36.4 -71.17\r\nv 275.6 35.65 -70.67\r\nv 275.62 35.77 -70.68\r\nv 265.34 36.43 -71.3\r\nv 270.42 36.16 -71.14\r\nv 270.44 36.28 -71.15\r\nv 265.34 36.55 -71.32\r\nv 275.56 35.42 -70.63\r\nv 275.58 35.54 -70.65\r\nv 280.8 34.4 -69.8\r\nv 280.77 34.28 -69.78\r\nv 280.84 34.52 -69.82\r\nv 280.87 34.63 -69.83\r\nv 284.31 13.49 156.37\r\nv 284.28 13.44 156.23\r\nv 277.99 14.02 157.65\r\nv 278.01 14.07 157.8\r\nv 284.22 13.34 155.93\r\nv 284.25 13.39 156.08\r\nv 277.95 13.92 157.35\r\nv 277.97 13.97 157.5\r\nv 265.34 14.43 158.53\r\nv 265.34 14.48 158.69\r\nv 271.66 14.34 158.38\r\nv 271.65 14.29 158.23\r\nv 265.34 14.53 158.84\r\nv 271.67 14.4 158.53\r\nv 271.68 14.45 158.68\r\nv 265.34 14.58 158.99\r\nv 194.29 4.09 122.54\r\nv 191.82 3.48 118.98\r\nv 194.38 4.04 122.44\r\nv 191.92 3.44 118.88\r\nv 188.97 2.73 114.49\r\nv 188.87 2.78 114.58\r\nv 194.58 3.95 122.23\r\nv 192.11 3.34 118.68\r\nv 192.01 3.39 118.78\r\nv 194.48 3.99 122.33\r\nv 189.18 2.64 114.3\r\nv 189.07 2.69 114.4\r\nv 182.92 1.17 104.01\r\nv 182.81 1.22 104.1\r\nv 185.92 1.94 109.42\r\nv 186.02 1.89 109.33\r\nv 182.71 1.27 104.18\r\nv 185.81 1.99 109.51\r\nv 185.71 2.04 109.59\r\nv 182.6 1.32 104.26\r\nv 179.81 0.69 98.82\r\nv 179.92 0.64 98.75\r\nv 177.44 0.1 93.26\r\nv 177.33 0.15 93.33\r\nv 180.14 0.54 98.6\r\nv 180.03 0.59 98.67\r\nv 177.66 0 93.13\r\nv 177.55 0.05 93.19\r\nv 173.62 -0.85 82.21\r\nv 173.5 -0.8 82.26\r\nv 175.38 -0.41 87.71\r\nv 175.49 -0.46 87.65\r\nv 173.39 -0.75 82.31\r\nv 175.26 -0.36 87.77\r\nv 175.15 -0.31 87.83\r\nv 173.27 -0.7 82.36\r\nv 171.69 -1.01 76.98\r\nv 171.81 -1.06 76.94\r\nv 170.5 -1.31 71.72\r\nv 170.38 -1.26 71.76\r\nv 172.04 -1.17 76.85\r\nv 171.92 -1.11 76.89\r\nv 170.74 -1.42 71.64\r\nv 170.62 -1.36 71.68\r\nv 168.86 -1.75 61.98\r\nv 168.73 -1.7 62.01\r\nv 169.56 -1.55 66.7\r\nv 169.68 -1.61 66.66\r\nv 168.61 -1.64 62.04\r\nv 169.44 -1.5 66.73\r\nv 169.32 -1.45 66.76\r\nv 168.5 -1.59 62.06\r\nv 167.88 -1.69 57.73\r\nv 168 -1.74 57.71\r\nv 167.56 -1.79 53.6\r\nv 167.45 -1.74 53.62\r\nv 168.24 -1.85 57.66\r\nv 168.12 -1.8 57.69\r\nv 167.81 -1.91 53.57\r\nv 167.69 -1.85 53.59\r\nv 167.41 -1.84 45.49\r\nv 167.28 -1.78 45.5\r\nv 167.42 -1.85 49.57\r\nv 167.54 -1.9 49.56\r\nv 167.16 -1.72 45.5\r\nv 167.29 -1.79 49.58\r\nv 167.18 -1.73 49.59\r\nv 167.04 -1.67 45.51\r\nv 184.64 9.61 -22.55\r\nv 188.41 11.74 -28.93\r\nv 184.74 9.54 -22.5\r\nv 188.51 11.67 -28.88\r\nv 192.22 13.77 -34.57\r\nv 192.12 13.84 -34.63\r\nv 184.96 9.4 -22.4\r\nv 188.71 11.53 -28.77\r\nv 188.61 11.6 -28.83\r\nv 184.85 9.47 -22.45\r\nv 192.42 13.62 -34.45\r\nv 192.32 13.7 -34.51\r\nv 198.73 17.17 -43.02\r\nv 198.64 17.25 -43.09\r\nv 195.74 15.62 -39.32\r\nv 195.83 15.55 -39.26\r\nv 198.54 17.33 -43.15\r\nv 195.64 15.7 -39.39\r\nv 195.55 15.77 -39.45\r\nv 198.46 17.41 -43.22\r\nv 223.05 26.15 -60.81\r\nv 228.03 27.81 -63.07\r\nv 223.12 26.05 -60.76\r\nv 228.09 27.71 -63.04\r\nv 233.43 29.52 -65.15\r\nv 233.37 29.62 -65.18\r\nv 223.26 25.85 -60.67\r\nv 228.22 27.5 -62.96\r\nv 228.16 27.6 -63\r\nv 223.19 25.95 -60.72\r\nv 233.54 29.3 -65.08\r\nv 233.48 29.41 -65.11\r\nv 244.56 32.82 -68.56\r\nv 244.51 32.94 -68.58\r\nv 238.99 31.23 -67\r\nv 239.04 31.12 -66.97\r\nv 244.47 33.05 -68.61\r\nv 238.94 31.34 -67.03\r\nv 238.9 31.45 -67.05\r\nv 244.43 33.16 -68.63\r\nv 200.6 18.61 -45.75\r\nv 200.52 18.68 -45.82\r\nv 200.69 18.53 -45.69\r\nv 202.24 19.39 -47.29\r\nv 202.16 19.47 -47.36\r\nv 202.08 19.55 -47.42\r\nv 200.87 18.37 -45.55\r\nv 200.78 18.45 -45.62\r\nv 202.42 19.23 -47.16\r\nv 202.33 19.31 -47.23\r\nv 204.44 20.27 -48.74\r\nv 204.36 20.35 -48.81\r\nv 203.47 19.91 -48.19\r\nv 203.55 19.83 -48.12\r\nv 204.27 20.43 -48.88\r\nv 203.38 20 -48.26\r\nv 204.11 20.59 -49\r\nv 203.22 20.15 -48.38\r\nv 203.3 20.08 -48.32\r\nv 204.19 20.51 -48.94\r\nv 167.82 -0.63 27.28\r\nv 168.44 -0.13 22.31\r\nv 167.94 -0.69 27.29\r\nv 168.55 -0.19 22.33\r\nv 169.46 0.47 17.09\r\nv 169.34 0.53 17.07\r\nv 168.18 -0.81 27.31\r\nv 168.8 -0.31 22.36\r\nv 168.67 -0.25 22.34\r\nv 168.06 -0.75 27.3\r\nv 169.7 0.35 17.13\r\nv 169.58 0.41 17.11\r\nv 172.79 2.34 5.41\r\nv 172.67 2.41 5.38\r\nv 170.87 1.28 11.5\r\nv 170.99 1.22 11.52\r\nv 172.55 2.47 5.35\r\nv 170.76 1.34 11.47\r\nv 170.64 1.4 11.45\r\nv 172.44 2.53 5.32\r\nv 174.85 3.97 -1.43\r\nv 174.96 3.91 -1.39\r\nv 177.86 5.6 -8.47\r\nv 177.75 5.66 -8.51\r\nv 175.19 3.78 -1.33\r\nv 175.07 3.84 -1.36\r\nv 178.09 5.47 -8.4\r\nv 177.97 5.54 -8.43\r\nv 181.27 7.43 -15.56\r\nv 181.38 7.36 -15.52\r\nv 181.16 7.5 -15.6\r\nv 181.05 7.56 -15.64\r\nv 167.02 -1.54 41.22\r\nv 167.14 -1.59 41.22\r\nv 167.25 -1.38 36.75\r\nv 167.13 -1.32 36.75\r\nv 167.39 -1.71 41.22\r\nv 167.26 -1.65 41.22\r\nv 167.5 -1.5 36.75\r\nv 167.37 -1.44 36.75\r\nv 167.63 -1.14 32.11\r\nv 167.76 -1.2 32.12\r\nv 167.51 -1.08 32.1\r\nv 167.39 -1.03 32.1\r\nv 217.35 9.06 143.27\r\nv 213.01 8.23 140.39\r\nv 212.95 8.28 140.51\r\nv 217.42 9.01 143.14\r\nv 213.09 8.19 140.27\r\nv 209.42 7.46 137.59\r\nv 209.34 7.51 137.71\r\nv 209.27 7.56 137.82\r\nv 217.56 8.91 142.88\r\nv 213.23 8.08 140.01\r\nv 213.16 8.14 140.14\r\nv 217.49 8.96 143.01\r\nv 209.57 7.37 137.34\r\nv 209.5 7.42 137.46\r\nv 204.14 6.25 132.93\r\nv 204.06 6.3 133.05\r\nv 206.47 6.8 135.07\r\nv 206.55 6.75 134.95\r\nv 203.98 6.35 133.17\r\nv 206.39 6.85 135.19\r\nv 203.82 6.44 133.39\r\nv 206.24 6.94 135.42\r\nv 206.31 6.9 135.31\r\nv 203.89 6.39 133.28\r\nv 240.11 12.75 154.43\r\nv 233.93 11.88 151.99\r\nv 240.15 12.7 154.28\r\nv 233.97 11.83 151.85\r\nv 228.02 10.88 149.09\r\nv 227.97 10.93 149.22\r\nv 240.23 12.6 153.99\r\nv 234.07 11.73 151.57\r\nv 234.02 11.78 151.71\r\nv 240.19 12.65 154.14\r\nv 228.13 10.78 148.81\r\nv 228.08 10.84 148.95\r\nv 222.51 9.88 146\r\nv 222.58 9.83 145.87\r\nv 222.45 9.93 146.14\r\nv 222.39 9.97 146.27\r\nv 249.8 34.63 -69.83\r\nv 249.83 34.52 -69.82\r\nv 255.07 35.65 -70.67\r\nv 255.05 35.77 -70.68\r\nv 249.9 34.28 -69.78\r\nv 249.87 34.4 -69.8\r\nv 255.11 35.42 -70.63\r\nv 255.09 35.54 -70.65\r\nv 260.24 36.28 -71.15\r\nv 260.25 36.16 -71.14\r\nv 260.22 36.4 -71.17\r\nv 260.21 36.51 -71.19\r\nv 258.99 14.45 158.68\r\nv 259 14.4 158.53\r\nv 252.68 14.02 157.65\r\nv 252.66 14.07 157.8\r\nv 259.02 14.29 158.23\r\nv 259.01 14.34 158.38\r\nv 252.72 13.92 157.35\r\nv 252.7 13.97 157.5\r\nv 246.42 13.39 156.08\r\nv 246.45 13.34 155.93\r\nv 246.39 13.44 156.23\r\nv 246.36 13.49 156.37\r\nv 334.65 2.97 120.58\r\nv 337.27 2.36 117.07\r\nv 334.75 3.01 120.69\r\nv 337.36 2.41 117.17\r\nv 340.46 1.7 112.88\r\nv 340.36 1.65 112.79\r\nv 334.94 3.11 120.9\r\nv 337.56 2.5 117.37\r\nv 337.46 2.46 117.27\r\nv 334.84 3.06 120.8\r\nv 340.67 1.79 113.07\r\nv 340.56 1.75 112.97\r\nv 347.13 0.31 103.04\r\nv 347.02 0.27 102.95\r\nv 343.85 0.99 108.13\r\nv 343.96 1.04 108.22\r\nv 346.91 0.22 102.87\r\nv 343.74 0.95 108.04\r\nv 343.64 0.9 107.95\r\nv 346.81 0.17 102.79\r\nv 349.57 -0.47 97.52\r\nv 349.67 -0.42 97.6\r\nv 352.06 -0.98 92.26\r\nv 351.95 -1.03 92.2\r\nv 349.9 -0.32 97.75\r\nv 349.78 -0.37 97.67\r\nv 352.29 -0.88 92.4\r\nv 352.18 -0.93 92.33\r\nv 356.13 -1.75 81.69\r\nv 356.02 -1.8 81.64\r\nv 354.24 -1.4 86.97\r\nv 354.36 -1.35 87.03\r\nv 355.9 -1.85 81.58\r\nv 354.12 -1.46 86.91\r\nv 354.01 -1.51 86.85\r\nv 355.78 -1.91 81.53\r\nv 357.31 -2.23 76.29\r\nv 357.43 -2.18 76.33\r\nv 358.72 -2.44 71.23\r\nv 358.61 -2.49 71.19\r\nv 357.67 -2.07 76.43\r\nv 357.55 -2.12 76.38\r\nv 358.96 -2.33 71.31\r\nv 358.84 -2.38 71.27\r\nv 360.85 -2.67 61.78\r\nv 360.73 -2.73 61.75\r\nv 359.9 -2.58 66.37\r\nv 360.02 -2.52 66.4\r\nv 360.61 -2.78 61.73\r\nv 359.78 -2.63 66.33\r\nv 359.66 -2.69 66.3\r\nv 360.49 -2.84 61.7\r\nv 306.39 23.63 -59.57\r\nv 301.49 25.15 -61.91\r\nv 306.45 23.77 -59.61\r\nv 301.54 25.27 -61.95\r\nv 296.28 26.89 -64.13\r\nv 296.23 26.78 -64.1\r\nv 306.58 24.05 -59.7\r\nv 301.66 25.51 -62.02\r\nv 301.6 25.39 -61.99\r\nv 306.51 23.91 -59.66\r\nv 296.39 27.12 -64.19\r\nv 296.34 27 -64.16\r\nv 285.53 30.38 -67.75\r\nv 285.49 30.26 -67.73\r\nv 290.9 28.65 -66.1\r\nv 290.95 28.77 -66.13\r\nv 285.45 30.15 -67.7\r\nv 290.85 28.54 -66.07\r\nv 290.81 28.43 -66.05\r\nv 285.41 30.04 -67.68\r\nv 361.85 -2.94 45.43\r\nv 361.84 -2.81 41.19\r\nv 361.97 -2.88 45.44\r\nv 361.96 -2.75 41.2\r\nv 361.83 -2.55 36.76\r\nv 361.71 -2.61 36.76\r\nv 362.21 -2.77 45.45\r\nv 362.21 -2.64 41.2\r\nv 362.08 -2.7 41.2\r\nv 362.09 -2.82 45.45\r\nv 362.07 -2.43 36.75\r\nv 361.95 -2.49 36.75\r\nv 361.36 -1.76 27.34\r\nv 361.23 -1.82 27.35\r\nv 361.67 -2.2 32.13\r\nv 361.8 -2.14 32.13\r\nv 361.11 -1.88 27.36\r\nv 361.55 -2.26 32.14\r\nv 361.44 -2.32 32.15\r\nv 361 -1.94 27.37\r\nv 312.27 7.87 140.42\r\nv 316.54 7.02 137.7\r\nv 316.47 6.97 137.58\r\nv 312.33 7.92 140.54\r\nv 316.61 7.07 137.82\r\nv 320.23 6.32 135.31\r\nv 320.16 6.27 135.19\r\nv 320.09 6.23 135.07\r\nv 312.47 8.02 140.81\r\nv 316.76 7.17 138.07\r\nv 316.68 7.12 137.95\r\nv 312.4 7.97 140.67\r\nv 320.39 6.42 135.55\r\nv 320.31 6.37 135.43\r\nv 325.71 5.28 131.48\r\nv 325.62 5.23 131.36\r\nv 323.29 5.74 133.21\r\nv 323.37 5.79 133.33\r\nv 325.54 5.18 131.24\r\nv 323.21 5.69 133.09\r\nv 325.38 5.09 131\r\nv 323.05 5.6 132.85\r\nv 323.13 5.64 132.97\r\nv 325.46 5.13 131.12\r\nv 290.01 11.64 151.07\r\nv 296.05 10.76 148.73\r\nv 290.05 11.69 151.21\r\nv 296.1 10.81 148.87\r\nv 301.93 9.85 146.22\r\nv 301.87 9.8 146.08\r\nv 290.13 11.79 151.5\r\nv 296.19 10.91 149.16\r\nv 296.15 10.86 149.01\r\nv 290.09 11.74 151.35\r\nv 302.04 9.95 146.5\r\nv 301.98 9.9 146.35\r\nv 307.45 8.92 143.53\r\nv 307.51 8.97 143.66\r\nv 307.39 8.87 143.39\r\nv 307.33 8.82 143.26\r\nv 265.34 13.48 155.45\r\nv 271.55 13.35 155.16\r\nv 265.34 13.53 155.6\r\nv 271.56 13.4 155.31\r\nv 277.76 13.02 154.46\r\nv 277.74 12.97 154.31\r\nv 265.34 13.64 155.91\r\nv 271.58 13.5 155.61\r\nv 271.57 13.45 155.46\r\nv 265.34 13.59 155.75\r\nv 277.8 13.12 154.76\r\nv 277.78 13.07 154.61\r\nv 283.96 12.49 153.23\r\nv 283.99 12.54 153.38\r\nv 283.93 12.44 153.08\r\nv 283.9 12.39 152.94\r\nv 361.09 -2.94 57.45\r\nv 361.21 -2.89 57.48\r\nv 361.62 -2.94 53.44\r\nv 361.5 -3 53.42\r\nv 361.46 -2.77 57.52\r\nv 361.33 -2.83 57.5\r\nv 361.87 -2.83 53.47\r\nv 361.74 -2.88 53.46\r\nv 361.98 -2.88 49.49\r\nv 362.11 -2.83 49.5\r\nv 361.86 -2.94 49.47\r\nv 361.74 -3 49.46\r\nv 344.73 8.08 -21.84\r\nv 340.87 10.15 -28.12\r\nv 344.83 8.15 -21.88\r\nv 340.98 10.22 -28.17\r\nv 337.11 12.24 -33.75\r\nv 337.01 12.16 -33.7\r\nv 345.05 8.29 -21.98\r\nv 341.18 10.37 -28.27\r\nv 341.08 10.29 -28.22\r\nv 344.94 8.22 -21.93\r\nv 337.31 12.4 -33.86\r\nv 337.21 12.32 -33.8\r\nv 330.7 15.84 -42.26\r\nv 330.6 15.72 -42.2\r\nv 333.62 14.17 -38.52\r\nv 333.72 14.27 -38.58\r\nv 330.51 15.6 -42.14\r\nv 333.53 14.08 -38.46\r\nv 333.42 13.99 -38.41\r\nv 330.41 15.49 -42.08\r\nv 328.25 16.54 -44.54\r\nv 328.14 16.4 -44.48\r\nv 328.35 16.69 -44.6\r\nv 326.84 17.42 -46.14\r\nv 326.74 17.23 -46.08\r\nv 326.63 17.06 -46.02\r\nv 328.54 17 -44.72\r\nv 328.44 16.84 -44.66\r\nv 327.03 17.84 -46.26\r\nv 326.93 17.63 -46.2\r\nv 325.15 18.85 -47.77\r\nv 325.06 18.55 -47.7\r\nv 325.88 18.17 -47.12\r\nv 325.97 18.43 -47.18\r\nv 324.97 18.26 -47.64\r\nv 325.78 17.92 -47.06\r\nv 324.77 17.75 -47.53\r\nv 325.58 17.47 -46.94\r\nv 325.69 17.68 -47\r\nv 324.88 17.99 -47.59\r\nv 360.38 -1.45 22.46\r\nv 360.49 -1.39 22.45\r\nv 359.6 -0.75 17.27\r\nv 359.48 -0.8 17.29\r\nv 360.74 -1.27 22.42\r\nv 360.61 -1.33 22.43\r\nv 359.84 -0.63 17.23\r\nv 359.72 -0.69 17.25\r\nv 356.85 1.33 5.6\r\nv 356.73 1.27 5.62\r\nv 358.46 0.16 11.69\r\nv 358.58 0.22 11.66\r\nv 356.62 1.21 5.65\r\nv 358.34 0.1 11.71\r\nv 358.22 0.04 11.73\r\nv 356.5 1.15 5.68\r\nv 354.23 2.57 -0.99\r\nv 354.34 2.63 -1.02\r\nv 351.58 4.29 -8.03\r\nv 351.47 4.23 -7.99\r\nv 354.57 2.75 -1.09\r\nv 354.46 2.69 -1.05\r\nv 351.8 4.42 -8.1\r\nv 351.69 4.36 -8.06\r\nv 348.5 6.22 -15.12\r\nv 348.61 6.29 -15.16\r\nv 348.39 6.15 -15.07\r\nv 348.28 6.09 -15.03\r\nv 280.21 31.51 -68.92\r\nv 280.24 31.62 -68.94\r\nv 275.19 32.84 -69.79\r\nv 275.17 32.73 -69.78\r\nv 280.3 31.86 -68.97\r\nv 280.27 31.74 -68.96\r\nv 275.23 33.09 -69.83\r\nv 275.21 32.97 -69.81\r\nv 265.34 34.37 -70.48\r\nv 265.34 34.21 -70.47\r\nv 270.25 33.83 -70.31\r\nv 270.26 33.96 -70.32\r\nv 265.34 34.06 -70.45\r\nv 270.24 33.7 -70.29\r\nv 270.22 33.57 -70.28\r\nv 265.34 33.91 -70.44\r\nv 184.83 0.17 102.79\r\nv 187.85 0.9 107.95\r\nv 184.72 0.22 102.87\r\nv 187.75 0.95 108.04\r\nv 190.82 1.7 112.87\r\nv 190.91 1.65 112.77\r\nv 184.51 0.31 103.04\r\nv 187.54 1.04 108.22\r\nv 187.64 0.99 108.13\r\nv 184.62 0.27 102.95\r\nv 190.61 1.79 113.06\r\nv 190.71 1.75 112.96\r\nv 195.87 3.11 120.76\r\nv 195.97 3.06 120.66\r\nv 193.58 2.46 117.22\r\nv 193.48 2.5 117.32\r\nv 196.07 3.02 120.55\r\nv 193.67 2.41 117.12\r\nv 193.77 2.37 117.02\r\nv 196.16 2.97 120.45\r\nv 175.74 -1.91 81.53\r\nv 177.57 -1.51 86.85\r\nv 175.63 -1.85 81.58\r\nv 177.46 -1.46 86.91\r\nv 179.59 -0.98 92.26\r\nv 179.7 -1.03 92.2\r\nv 175.39 -1.75 81.69\r\nv 177.23 -1.35 87.03\r\nv 177.35 -1.4 86.97\r\nv 175.51 -1.8 81.64\r\nv 179.36 -0.88 92.4\r\nv 179.47 -0.93 92.33\r\nv 181.89 -0.37 97.67\r\nv 181.78 -0.32 97.75\r\nv 182 -0.42 97.6\r\nv 182.11 -0.47 97.53\r\nv 171.06 -2.84 61.7\r\nv 171.88 -2.69 66.3\r\nv 170.95 -2.78 61.73\r\nv 171.76 -2.63 66.33\r\nv 172.79 -2.44 71.23\r\nv 172.91 -2.49 71.19\r\nv 170.7 -2.67 61.78\r\nv 171.52 -2.52 66.4\r\nv 171.64 -2.58 66.37\r\nv 170.83 -2.73 61.75\r\nv 172.55 -2.33 71.31\r\nv 172.68 -2.38 71.27\r\nv 173.96 -2.12 76.38\r\nv 173.84 -2.07 76.43\r\nv 174.08 -2.18 76.33\r\nv 174.19 -2.23 76.29\r\nv 245.26 30.23 -67.68\r\nv 239.86 28.61 -66.05\r\nv 245.23 30.46 -67.7\r\nv 239.82 28.83 -66.07\r\nv 234.39 27.12 -64.13\r\nv 234.44 26.92 -64.1\r\nv 245.14 30.99 -67.75\r\nv 239.72 29.33 -66.13\r\nv 239.77 29.07 -66.1\r\nv 245.19 30.72 -67.73\r\nv 234.28 27.55 -64.19\r\nv 234.34 27.33 -64.16\r\nv 224.12 24.19 -59.72\r\nv 224.18 24.05 -59.67\r\nv 229.08 25.61 -61.99\r\nv 229.02 25.79 -62.03\r\nv 224.25 23.91 -59.63\r\nv 229.14 25.44 -61.96\r\nv 229.2 25.28 -61.92\r\nv 224.31 23.78 -59.59\r\nv 170.43 -1.94 27.37\r\nv 170.01 -2.32 32.15\r\nv 170.32 -1.88 27.36\r\nv 169.89 -2.26 32.14\r\nv 169.63 -2.55 36.76\r\nv 169.75 -2.61 36.76\r\nv 170.07 -1.76 27.34\r\nv 169.64 -2.14 32.13\r\nv 169.77 -2.2 32.13\r\nv 170.2 -1.82 27.35\r\nv 169.38 -2.43 36.75\r\nv 169.51 -2.49 36.75\r\nv 169.28 -2.77 45.46\r\nv 169.41 -2.82 45.45\r\nv 169.39 -2.7 41.2\r\nv 169.27 -2.64 41.2\r\nv 169.53 -2.88 45.44\r\nv 169.51 -2.75 41.2\r\nv 169.63 -2.81 41.19\r\nv 169.65 -2.94 45.43\r\nv 197.83 3.41 122.84\r\nv 197.91 3.37 122.74\r\nv 197.73 3.46 122.94\r\nv 198.88 3.77 124.54\r\nv 198.97 3.72 124.44\r\nv 199.06 3.68 124.34\r\nv 197.55 3.55 123.16\r\nv 197.64 3.5 123.05\r\nv 198.7 3.86 124.76\r\nv 198.79 3.82 124.65\r\nv 200.24 4.29 126.68\r\nv 200.33 4.24 126.56\r\nv 199.62 4.05 125.74\r\nv 199.53 4.09 125.85\r\nv 200.42 4.2 126.45\r\nv 199.71 4 125.63\r\nv 200.59 4.11 126.24\r\nv 199.88 3.91 125.42\r\nv 199.8 3.96 125.52\r\nv 200.51 4.15 126.34\r\nv 218.46 7.9 140.37\r\nv 223.36 8.83 143.24\r\nv 218.39 7.95 140.5\r\nv 223.3 8.88 143.37\r\nv 228.75 9.85 146.21\r\nv 228.8 9.8 146.08\r\nv 218.26 8.05 140.76\r\nv 223.18 8.98 143.64\r\nv 223.24 8.93 143.51\r\nv 218.33 8 140.63\r\nv 228.64 9.95 146.49\r\nv 228.69 9.9 146.35\r\nv 240.54 11.79 151.5\r\nv 240.58 11.74 151.35\r\nv 234.53 10.86 149.01\r\nv 234.48 10.91 149.16\r\nv 240.62 11.69 151.21\r\nv 234.57 10.81 148.87\r\nv 234.62 10.76 148.73\r\nv 240.66 11.64 151.07\r\nv 246.77 12.39 152.94\r\nv 246.74 12.44 153.08\r\nv 252.91 13.02 154.46\r\nv 252.93 12.97 154.31\r\nv 246.68 12.54 153.38\r\nv 246.71 12.49 153.23\r\nv 252.87 13.12 154.76\r\nv 252.89 13.07 154.61\r\nv 259.1 13.45 155.46\r\nv 259.09 13.5 155.61\r\nv 259.11 13.4 155.31\r\nv 259.12 13.35 155.16\r\nv 169.77 -3 49.46\r\nv 169.66 -2.94 49.47\r\nv 169.92 -2.94 53.44\r\nv 170.04 -3 53.42\r\nv 169.41 -2.83 49.5\r\nv 169.53 -2.88 49.49\r\nv 169.67 -2.83 53.47\r\nv 169.8 -2.88 53.46\r\nv 170.22 -2.83 57.5\r\nv 170.1 -2.77 57.52\r\nv 170.34 -2.89 57.48\r\nv 170.46 -2.94 57.45\r\nv 200.43 15.57 -42.11\r\nv 197.63 14.01 -38.42\r\nv 200.34 15.67 -42.17\r\nv 197.53 14.1 -38.48\r\nv 194.21 12.24 -33.75\r\nv 194.31 12.15 -33.7\r\nv 200.15 15.84 -42.29\r\nv 197.34 14.27 -38.59\r\nv 197.44 14.19 -38.53\r\nv 200.25 15.76 -42.23\r\nv 194.01 12.4 -33.86\r\nv 194.11 12.32 -33.81\r\nv 186.72 8.29 -21.98\r\nv 186.83 8.22 -21.93\r\nv 190.5 10.29 -28.22\r\nv 190.4 10.37 -28.27\r\nv 186.93 8.15 -21.88\r\nv 190.61 10.22 -28.17\r\nv 190.71 10.14 -28.12\r\nv 187.04 8.07 -21.84\r\nv 219.97 22.52 -57.2\r\nv 220.03 22.4 -57.16\r\nv 219.9 22.64 -57.24\r\nv 216.15 21.6 -54.93\r\nv 216.22 21.49 -54.88\r\nv 216.29 21.37 -54.84\r\nv 219.77 22.87 -57.34\r\nv 219.84 22.75 -57.29\r\nv 216 21.81 -55.03\r\nv 216.08 21.71 -54.98\r\nv 210.41 20.34 -51.22\r\nv 210.49 20.25 -51.16\r\nv 212.95 20.88 -52.89\r\nv 212.87 20.97 -52.95\r\nv 210.57 20.15 -51.1\r\nv 213.03 20.78 -52.84\r\nv 210.72 19.89 -51\r\nv 213.17 20.54 -52.74\r\nv 213.1 20.67 -52.79\r\nv 210.65 20.03 -51.05\r\nv 175.02 1.15 5.68\r\nv 173.23 0.05 11.73\r\nv 174.9 1.21 5.65\r\nv 173.12 0.1 11.71\r\nv 171.83 -0.75 17.27\r\nv 171.94 -0.8 17.29\r\nv 174.66 1.33 5.6\r\nv 172.88 0.22 11.66\r\nv 173 0.16 11.69\r\nv 174.78 1.27 5.62\r\nv 171.58 -0.63 17.23\r\nv 171.71 -0.69 17.25\r\nv 170.81 -1.33 22.43\r\nv 170.68 -1.27 22.42\r\nv 170.93 -1.39 22.45\r\nv 171.04 -1.45 22.46\r\nv 183.52 6.08 -15.03\r\nv 183.41 6.15 -15.07\r\nv 180.16 4.29 -8.03\r\nv 180.27 4.23 -7.99\r\nv 183.19 6.29 -15.16\r\nv 183.3 6.22 -15.12\r\nv 179.93 4.42 -8.1\r\nv 180.05 4.36 -8.06\r\nv 177.17 2.69 -1.05\r\nv 177.05 2.75 -1.09\r\nv 177.29 2.63 -1.02\r\nv 177.4 2.57 -0.99\r\nv 260.45 33.64 -70.28\r\nv 260.44 33.81 -70.29\r\nv 255.48 33.06 -69.79\r\nv 255.5 32.86 -69.78\r\nv 260.41 34.19 -70.32\r\nv 260.42 34 -70.31\r\nv 255.44 33.51 -69.83\r\nv 255.46 33.28 -69.81\r\nv 250.4 32.16 -68.96\r\nv 250.37 32.42 -68.97\r\nv 250.43 31.91 -68.94\r\nv 250.46 31.68 -68.92\r\nv 211.07 21.45 -57.86\r\nv 208.42 20.59 -55.99\r\nv 214.41 22.55 -60.1\r\nv 214.4 21.63 -62.33\r\nv 210.99 20.41 -60\r\nv 208.28 19.45 -58.05\r\nv 218.39 23.89 -62.56\r\nv 218.45 23.1 -64.89\r\nv 218.57 22.21 -67.16\r\nv 214.46 20.62 -64.5\r\nv 208.21 18.22 -60.06\r\nv 210.98 19.28 -62.08\r\nv 207.78 14.42 -59.02\r\nv 207.32 14.29 -60.29\r\nv 206.41 13.95 -57.98\r\nv 205.93 13.8 -59.24\r\nv 205.47 14.07 -59.87\r\nv 206.88 14.6 -60.94\r\nv 204 13.1 -57.77\r\nv 204.9 13.44 -58.45\r\nv 205.4 13.62 -57.21\r\nv 203.52 13.33 -58.4\r\nv 204.42 13.69 -59.08\r\nv 203.83 15.28 -58.33\r\nv 204.04 14.32 -59.06\r\nv 203.14 13.95 -58.38\r\nv 204.87 15.69 -59.11\r\nv 205.09 14.72 -59.85\r\nv 206.51 15.28 -60.91\r\nv 206.28 16.25 -60.15\r\nv 212.76 16.74 -60.59\r\nv 210.09 15.71 -58.58\r\nv 216.13 18.09 -63\r\nv 216.29 18.74 -60.68\r\nv 213 17.52 -58.35\r\nv 210.39 16.57 -56.42\r\nv 220.11 19.68 -65.66\r\nv 220.2 20.21 -63.25\r\nv 220.22 21.42 -58.97\r\nv 220.23 20.83 -60.9\r\nv 216.43 20.21 -56.56\r\nv 216.38 19.51 -58.42\r\nv 210.78 18.43 -52.58\r\nv 210.62 17.54 -54.31\r\nv 213.16 18.4 -56.17\r\nv 213.26 19.21 -54.39\r\nv 206.73 20.93 -52.96\r\nv 206.88 21.47 -51.91\r\nv 205.43 20.5 -52.05\r\nv 205.59 21.07 -51.03\r\nv 205.71 21.32 -50.49\r\nv 206.98 21.71 -51.36\r\nv 203.82 20.36 -49.8\r\nv 204.64 20.72 -50.37\r\nv 204.47 20.14 -51.38\r\nv 203.94 20.62 -49.27\r\nv 204.76 20.98 -49.84\r\nv 204.93 20.94 -49.56\r\nv 204.85 21.02 -49.62\r\nv 204.03 20.66 -49.06\r\nv 205.87 21.28 -50.21\r\nv 205.79 21.36 -50.27\r\nv 207.07 21.74 -51.15\r\nv 207.14 21.67 -51.09\r\nv 213.3 19.77 -53.33\r\nv 210.84 19.06 -51.57\r\nv 216.43 20.7 -55.47\r\nv 216.4 21.02 -54.94\r\nv 213.29 20.15 -52.83\r\nv 210.84 19.47 -51.08\r\nv 220.19 21.83 -57.82\r\nv 220.15 22.09 -57.27\r\nv 220.09 22.27 -57.11\r\nv 216.35 21.23 -54.79\r\nv 210.79 19.73 -50.95\r\nv 213.24 20.38 -52.69\r\nv 209.26 21.82 -52.09\r\nv 211.79 22.5 -53.85\r\nv 211.71 22.59 -53.91\r\nv 209.18 21.91 -52.16\r\nv 214.99 23.37 -55.97\r\nv 214.92 23.47 -56.02\r\nv 214.84 23.56 -56.08\r\nv 211.63 22.68 -53.97\r\nv 209.1 22 -52.22\r\nv 218.76 24.58 -58.34\r\nv 218.83 24.48 -58.29\r\nv 218.69 24.67 -58.39\r\nv 218.62 24.77 -58.44\r\nv 214.77 23.65 -56.13\r\nv 209.02 22.09 -52.28\r\nv 211.55 22.76 -54.03\r\nv 208.66 19.87 -49.99\r\nv 208.74 19.78 -49.93\r\nv 207.42 19.5 -49.12\r\nv 207.51 19.42 -49.06\r\nv 207.59 19.32 -49\r\nv 208.83 19.68 -49.87\r\nv 205.72 18.84 -47.92\r\nv 205.8 18.76 -47.86\r\nv 206.6 19.1 -48.42\r\nv 206.51 19.18 -48.47\r\nv 205.89 18.66 -47.8\r\nv 206.68 19 -48.36\r\nv 206.84 18.75 -48.24\r\nv 206.76 18.89 -48.3\r\nv 205.97 18.55 -47.74\r\nv 207.76 19.06 -48.89\r\nv 207.68 19.21 -48.95\r\nv 208.91 19.57 -49.82\r\nv 208.98 19.43 -49.76\r\nv 204.23 19.13 -53.03\r\nv 205.21 19.5 -53.72\r\nv 205 18.26 -55.69\r\nv 203.99 17.86 -54.97\r\nv 206.54 19.97 -54.66\r\nv 206.36 18.76 -56.67\r\nv 206.25 17.47 -58.62\r\nv 204.86 16.93 -57.61\r\nv 203.83 16.52 -56.85\r\nv 205.86 14.28 -55.41\r\nv 206.86 14.59 -56.16\r\nv 207.24 15.53 -54.08\r\nv 206.27 15.22 -53.36\r\nv 208.19 15.03 -57.16\r\nv 208.54 15.94 -55.05\r\nv 209.01 17.9 -51.3\r\nv 208.82 16.96 -52.99\r\nv 207.76 17.51 -50.4\r\nv 207.54 16.57 -52.05\r\nv 206.6 16.25 -51.36\r\nv 206.83 17.2 -49.73\r\nv 206.94 17.85 -48.77\r\nv 207.86 18.17 -49.42\r\nv 207.87 18.61 -48.96\r\nv 206.96 18.29 -48.31\r\nv 209.09 18.55 -50.31\r\nv 209.1 18.98 -49.84\r\nv 209.06 19.25 -49.71\r\nv 207.83 18.89 -48.84\r\nv 206.92 18.57 -48.19\r\nv 205.26 20.62 -49.31\r\nv 205.17 20.7 -49.38\r\nv 206.2 20.95 -49.96\r\nv 206.11 21.04 -50.03\r\nv 206.03 21.12 -50.09\r\nv 205.09 20.78 -49.44\r\nv 207.38 21.42 -50.91\r\nv 207.47 21.33 -50.84\r\nv 207.3 21.5 -50.97\r\nv 207.22 21.59 -51.03\r\nv 205.95 21.2 -50.15\r\nv 205 20.87 -49.5\r\nv 203.02 12.64 -57.03\r\nv 201.79 12 -55.94\r\nv 201.28 12.25 -56.55\r\nv 202.53 12.88 -57.65\r\nv 200.13 11.09 -54.2\r\nv 199.6 11.36 -54.8\r\nv 199.2 12.01 -54.78\r\nv 200.89 12.88 -56.53\r\nv 202.14 13.5 -57.63\r\nv 202.91 19.93 -49.17\r\nv 201.76 19.32 -48.2\r\nv 201.9 19.58 -47.69\r\nv 203.05 20.19 -48.65\r\nv 200.19 18.45 -46.59\r\nv 200.34 18.71 -46.08\r\nv 200.44 18.75 -45.88\r\nv 202 19.62 -47.48\r\nv 203.14 20.23 -48.44\r\nv 204.85 18.42 -47.31\r\nv 204.94 18.34 -47.25\r\nv 203.74 17.84 -46.36\r\nv 203.83 17.75 -46.3\r\nv 203.92 17.66 -46.24\r\nv 205.02 18.25 -47.19\r\nv 202.32 16.92 -44.72\r\nv 202.23 17 -44.78\r\nv 202.41 16.83 -44.66\r\nv 202.5 16.72 -44.6\r\nv 204.01 17.55 -46.18\r\nv 205.11 18.13 -47.13\r\nv 319.83 19.36 -61.98\r\nv 322.63 18.28 -59.93\r\nv 316.31 20.7 -64.44\r\nv 316.36 21.75 -62.27\r\nv 319.82 20.55 -59.9\r\nv 322.56 19.56 -57.93\r\nv 312.16 22.26 -67.12\r\nv 312.28 23.17 -64.85\r\nv 312.35 24.64 -60.55\r\nv 312.33 24 -62.52\r\nv 316.3 23.48 -58.15\r\nv 316.35 22.72 -60.04\r\nv 322.26 21.72 -54.12\r\nv 322.42 20.76 -55.87\r\nv 319.73 21.65 -57.77\r\nv 319.63 22.52 -55.95\r\nv 324.6 16.23 -59.99\r\nv 324.35 15.27 -60.75\r\nv 326.02 15.6 -58.93\r\nv 325.77 14.63 -59.66\r\nv 325.37 13.97 -59.69\r\nv 323.96 14.62 -60.77\r\nv 327.7 13.75 -58.19\r\nv 326.82 14.15 -58.87\r\nv 327.07 15.12 -58.14\r\nv 327.26 13.1 -58.21\r\nv 326.4 13.5 -58.89\r\nv 325.34 13.34 -57.02\r\nv 325.88 13.21 -58.26\r\nv 326.73 12.83 -57.59\r\nv 324.37 13.81 -57.8\r\nv 324.88 13.68 -59.05\r\nv 323.5 14.32 -60.13\r\nv 323.02 14.43 -58.86\r\nv 317.61 18.32 -56.08\r\nv 320.16 17.39 -54.19\r\nv 314.37 19.47 -58.35\r\nv 314.47 18.8 -60.61\r\nv 317.78 17.56 -58.26\r\nv 320.4 16.55 -56.3\r\nv 310.5 20.82 -60.86\r\nv 310.53 20.25 -63.21\r\nv 310.61 19.77 -65.62\r\nv 314.63 18.22 -62.93\r\nv 320.71 15.79 -58.46\r\nv 318.02 16.88 -60.49\r\nv 319.54 23.01 -54.85\r\nv 322.14 22.26 -53.06\r\nv 316.25 23.9 -57\r\nv 316.19 24.08 -56.4\r\nv 319.46 23.22 -54.27\r\nv 322.04 22.5 -52.5\r\nv 312.33 24.99 -59.35\r\nv 312.29 25.13 -58.73\r\nv 312.24 25.12 -58.5\r\nv 316.13 24.08 -56.17\r\nv 321.96 22.53 -52.27\r\nv 319.39 23.24 -54.05\r\nv 321.78 17.48 -51.16\r\nv 321.7 18 -50.17\r\nv 323 17.01 -50.24\r\nv 322.92 17.55 -49.27\r\nv 322.93 17.9 -48.81\r\nv 321.71 18.33 -49.7\r\nv 324.55 16.95 -48.04\r\nv 323.8 17.22 -48.61\r\nv 323.88 16.65 -49.56\r\nv 324.57 17.32 -47.59\r\nv 323.82 17.58 -48.15\r\nv 324.01 18.02 -48.08\r\nv 323.9 17.82 -48.03\r\nv 324.66 17.56 -47.48\r\nv 323.1 18.32 -48.74\r\nv 323 18.13 -48.69\r\nv 321.77 18.54 -49.58\r\nv 321.86 18.72 -49.63\r\nv 323.4 21.48 -50.71\r\nv 323.49 21.56 -50.77\r\nv 324.69 20.9 -49.81\r\nv 324.78 20.99 -49.88\r\nv 324.86 21.07 -49.94\r\nv 323.57 21.65 -50.84\r\nv 326.43 19.89 -48.59\r\nv 326.51 19.98 -48.66\r\nv 325.72 20.46 -49.21\r\nv 325.63 20.38 -49.15\r\nv 326.6 20.06 -48.72\r\nv 325.81 20.54 -49.28\r\nv 325.97 20.7 -49.4\r\nv 325.89 20.63 -49.34\r\nv 326.68 20.14 -48.79\r\nv 325.02 21.23 -50.06\r\nv 324.94 21.15 -50\r\nv 323.65 21.73 -50.9\r\nv 323.73 21.81 -50.96\r\nv 320.42 20.36 -51.11\r\nv 317.92 21 -52.86\r\nv 317.84 20.73 -52.81\r\nv 320.34 20.06 -51.05\r\nv 314.75 21.81 -54.97\r\nv 314.68 21.59 -54.92\r\nv 314.6 21.37 -54.87\r\nv 317.76 20.47 -52.75\r\nv 320.26 19.76 -50.99\r\nv 310.89 22.64 -57.25\r\nv 310.95 22.83 -57.3\r\nv 310.82 22.47 -57.21\r\nv 310.75 22.31 -57.16\r\nv 314.53 21.17 -54.82\r\nv 320.18 19.5 -50.94\r\nv 317.69 20.24 -52.7\r\nv 327.07 16.35 -56.67\r\nv 326.04 16.84 -57.43\r\nv 325.9 18.18 -55.52\r\nv 326.92 17.68 -54.79\r\nv 324.63 17.46 -58.46\r\nv 324.52 18.79 -56.52\r\nv 324.14 21.03 -52.82\r\nv 324.34 20.04 -54.52\r\nv 325.46 20.44 -51.89\r\nv 325.69 19.44 -53.56\r\nv 326.68 18.93 -52.86\r\nv 326.43 19.92 -51.21\r\nv 324.1 15.79 -51.19\r\nv 323.2 16.17 -51.89\r\nv 323.51 15.24 -53.91\r\nv 324.43 14.82 -53.18\r\nv 321.96 16.69 -52.84\r\nv 322.24 15.79 -54.9\r\nv 322.59 14.98 -57.01\r\nv 323.9 14.39 -55.98\r\nv 324.85 13.94 -55.23\r\nv 326.26 20.49 -50.21\r\nv 325.3 21.02 -50.87\r\nv 325.18 21.27 -50.33\r\nv 326.13 20.74 -49.68\r\nv 323.99 21.6 -51.77\r\nv 323.88 21.85 -51.23\r\nv 323.8 21.89 -51.01\r\nv 325.1 21.31 -50.12\r\nv 326.04 20.78 -49.46\r\nv 324.38 19.2 -48.32\r\nv 324.29 18.87 -48.26\r\nv 323.46 19.52 -48.97\r\nv 323.37 19.19 -48.91\r\nv 323.29 18.87 -48.85\r\nv 324.2 18.56 -48.2\r\nv 322.12 19.56 -49.79\r\nv 322.2 19.89 -49.85\r\nv 322.03 19.25 -49.74\r\nv 321.95 18.96 -49.68\r\nv 323.2 18.57 -48.79\r\nv 324.11 18.27 -48.14\r\nv 328.64 13.35 -57.48\r\nv 329.83 12.81 -56.41\r\nv 329.38 12.19 -56.43\r\nv 328.2 12.71 -57.49\r\nv 331.46 12.03 -54.7\r\nv 331 11.43 -54.72\r\nv 330.41 11.22 -54.13\r\nv 328.81 11.95 -55.82\r\nv 327.64 12.45 -56.88\r\nv 325.35 16.7 -47.44\r\nv 326.4 16.34 -46.51\r\nv 326.42 16.7 -46.08\r\nv 325.37 17.07 -47\r\nv 327.92 15.74 -44.96\r\nv 327.93 16.09 -44.54\r\nv 328.02 16.28 -44.43\r\nv 326.51 16.91 -45.97\r\nv 325.46 17.3 -46.89\r\nv 327.26 19.45 -47.99\r\nv 327.35 19.53 -48.06\r\nv 328.35 18.91 -47.06\r\nv 328.44 19 -47.13\r\nv 328.53 19.08 -47.2\r\nv 327.44 19.61 -48.13\r\nv 329.99 18.24 -45.56\r\nv 329.9 18.16 -45.49\r\nv 330.08 18.32 -45.63\r\nv 330.17 18.4 -45.69\r\nv 328.61 19.16 -47.26\r\nv 327.52 19.69 -48.19\r\nv 201.39 4.29 132.92\r\nv 201.02 2.4 133.72\r\nv 200 3.98 131.66\r\nv 199.61 2.1 132.43\r\nv 199.2 -0.19 133.27\r\nv 200.62 0.13 134.61\r\nv 197.64 1.64 130.49\r\nv 198.53 1.85 131.39\r\nv 198.93 3.74 130.65\r\nv 197.17 -0.65 131.28\r\nv 198.09 -0.43 132.21\r\nv 197.57 -4.7 133.34\r\nv 197.74 -2.74 132.92\r\nv 196.79 -2.96 131.98\r\nv 198.71 -4.45 134.46\r\nv 198.86 -2.49 134.01\r\nv 200.3 -2.18 135.38\r\nv 200.15 -4.13 135.85\r\nv 207.36 2.08 134.07\r\nv 205.02 1.58 132.02\r\nv 210.3 2.7 136.49\r\nv 209.86 0.62 137.7\r\nv 206.92 -0 135.2\r\nv 204.57 -0.51 133.08\r\nv 213.86 3.42 139.17\r\nv 213.44 1.34 140.46\r\nv 212.47 -2.37 143.25\r\nv 212.96 -0.7 141.87\r\nv 208.84 -3.1 140.34\r\nv 209.36 -1.43 139.03\r\nv 203.48 -4.27 135.4\r\nv 204.04 -2.58 134.25\r\nv 206.4 -2.06 136.45\r\nv 205.86 -3.74 137.68\r\nv 205.34 -4.69 138.75\r\nv 202.94 -5.22 136.42\r\nv 208.34 -4.05 141.47\r\nv 207.93 -4.26 142.32\r\nv 204.9 -4.91 139.56\r\nv 202.48 -5.44 137.2\r\nv 212 -3.31 144.44\r\nv 211.62 -3.52 145.33\r\nv 211.36 -3.01 145.81\r\nv 207.65 -3.75 142.79\r\nv 202.16 -4.94 137.64\r\nv 204.6 -4.4 140.01\r\nv 205.92 6.3 136.05\r\nv 203.49 5.79 133.99\r\nv 208.96 6.92 138.48\r\nv 209.11 7.45 138.12\r\nv 206.07 6.84 135.71\r\nv 203.64 6.33 133.67\r\nv 212.65 7.65 141.2\r\nv 212.8 8.18 140.82\r\nv 212.88 8.32 140.62\r\nv 209.21 7.6 137.93\r\nv 203.74 6.48 133.5\r\nv 206.17 6.99 135.53\r\nv 203.59 2.95 129.66\r\nv 203.77 4.02 129.24\r\nv 202.25 2.65 128.47\r\nv 202.44 3.73 128.06\r\nv 202.5 4.28 127.9\r\nv 203.82 4.58 129.07\r\nv 200.63 3.29 126.27\r\nv 201.43 3.49 127.11\r\nv 201.23 2.42 127.5\r\nv 200.7 3.85 126.13\r\nv 201.5 4.05 126.96\r\nv 201.39 4.31 127.07\r\nv 201.47 4.26 126.97\r\nv 200.67 4.06 126.14\r\nv 202.39 4.54 128.01\r\nv 202.47 4.5 127.91\r\nv 203.79 4.79 129.08\r\nv 203.72 4.83 129.18\r\nv 202.31 5.86 131.37\r\nv 202.23 5.91 131.49\r\nv 200.94 5.57 130.16\r\nv 200.86 5.61 130.28\r\nv 200.77 5.66 130.39\r\nv 202.14 5.96 131.61\r\nv 199.08 5.13 128.32\r\nv 198.99 5.18 128.43\r\nv 199.82 5.38 129.29\r\nv 199.91 5.33 129.18\r\nv 198.9 5.22 128.54\r\nv 199.73 5.43 129.41\r\nv 199.56 5.51 129.62\r\nv 199.64 5.47 129.52\r\nv 198.81 5.27 128.65\r\nv 200.6 5.75 130.61\r\nv 200.68 5.71 130.5\r\nv 202.06 6.01 131.72\r\nv 201.98 6.05 131.83\r\nv 205.16 5.4 131.14\r\nv 207.51 5.9 133.09\r\nv 207.59 5.85 132.97\r\nv 205.25 5.35 131.02\r\nv 210.45 6.51 135.4\r\nv 210.53 6.46 135.28\r\nv 210.61 6.41 135.15\r\nv 207.67 5.8 132.85\r\nv 205.33 5.3 130.91\r\nv 214.1 7.18 137.86\r\nv 214.03 7.23 137.99\r\nv 214.17 7.13 137.73\r\nv 214.24 7.08 137.61\r\nv 210.68 6.36 135.03\r\nv 205.41 5.26 130.79\r\nv 207.75 5.75 132.73\r\nv 200.86 0.65 128.22\r\nv 201.89 0.89 129.21\r\nv 201.41 -1.21 130.17\r\nv 200.35 -1.45 129.15\r\nv 203.24 1.19 130.44\r\nv 202.77 -0.91 131.44\r\nv 201.65 -4.67 133.65\r\nv 202.23 -2.98 132.55\r\nv 200.25 -4.98 132.3\r\nv 200.84 -3.28 131.24\r\nv 199.76 -3.53 130.19\r\nv 199.15 -5.23 131.23\r\nv 198.55 -6.2 132.15\r\nv 199.67 -5.95 133.25\r\nv 199.18 -6.18 133.98\r\nv 198.04 -6.43 132.87\r\nv 201.09 -5.63 134.63\r\nv 200.62 -5.86 135.38\r\nv 200.28 -5.36 135.82\r\nv 198.84 -5.68 134.42\r\nv 197.69 -5.93 133.3\r\nv 199.2 4.86 130.17\r\nv 200.26 5.1 131.17\r\nv 200.42 5.64 130.87\r\nv 199.38 5.4 129.88\r\nv 201.64 5.4 132.4\r\nv 201.8 5.94 132.1\r\nv 201.91 6.09 131.93\r\nv 200.53 5.79 130.71\r\nv 199.48 5.56 129.72\r\nv 201.04 4.49 127.51\r\nv 201.13 4.44 127.4\r\nv 202.05 4.72 128.46\r\nv 202.14 4.68 128.34\r\nv 202.22 4.63 128.23\r\nv 201.22 4.4 127.28\r\nv 203.47 4.97 129.52\r\nv 203.38 5.02 129.63\r\nv 203.55 4.92 129.4\r\nv 203.64 4.88 129.29\r\nv 202.31 4.58 128.12\r\nv 201.31 4.35 127.17\r\nv 196.82 1.44 129.6\r\nv 195.87 1.2 128.44\r\nv 195.32 -1.09 129.21\r\nv 196.32 -0.85 130.38\r\nv 194.59 0.89 126.74\r\nv 193.98 -1.4 127.49\r\nv 193.46 -3.71 128.14\r\nv 194.85 -3.41 129.87\r\nv 195.89 -3.17 131.06\r\nv 199.91 3.1 125.45\r\nv 199.07 2.87 124.36\r\nv 199.16 3.43 124.22\r\nv 199.99 3.66 125.3\r\nv 197.91 2.56 122.76\r\nv 198.02 3.12 122.63\r\nv 198 3.33 122.64\r\nv 199.14 3.64 124.24\r\nv 199.96 3.87 125.32\r\nv 198.34 4.93 127.46\r\nv 198.25 4.98 127.58\r\nv 197.48 4.7 126.35\r\nv 197.39 4.75 126.46\r\nv 197.3 4.79 126.57\r\nv 198.16 5.03 127.69\r\nv 196.2 4.43 124.8\r\nv 196.3 4.38 124.69\r\nv 196.11 4.48 124.91\r\nv 196.02 4.52 125.01\r\nv 197.21 4.84 126.67\r\nv 198.07 5.07 127.8\r\nv 331.03 -4.26 135.44\r\nv 330.82 -2.3 135.12\r\nv 332.38 -4.55 134.15\r\nv 332.16 -2.6 133.86\r\nv 331.74 -0.29 133.32\r\nv 330.42 0 134.54\r\nv 334.02 -3.01 132.04\r\nv 333.17 -2.82 132.89\r\nv 333.39 -4.78 133.16\r\nv 333.59 -0.69 131.5\r\nv 332.74 -0.51 132.36\r\nv 332.6 3.49 130.28\r\nv 331.77 3.67 131.14\r\nv 332.24 1.78 131.72\r\nv 333.08 1.6 130.87\r\nv 330.79 3.88 132.06\r\nv 331.25 2 132.66\r\nv 329.94 2.28 133.84\r\nv 329.5 4.17 133.2\r\nv 327.73 -4.4 134.93\r\nv 325.3 -3.86 137.26\r\nv 324.7 -2.18 136.14\r\nv 327.11 -2.71 133.9\r\nv 322.21 -3.2 140.01\r\nv 321.65 -1.52 138.8\r\nv 321.1 0.53 137.57\r\nv 324.13 -0.12 135.03\r\nv 326.5 -0.64 132.89\r\nv 317.93 -0.76 141.72\r\nv 318.45 -2.43 143.04\r\nv 317.42 1.29 140.37\r\nv 316.63 5.1 138.21\r\nv 316.97 3.36 139.15\r\nv 320.25 4.35 135.61\r\nv 320.62 2.61 136.46\r\nv 325.57 3.21 131.3\r\nv 325.97 1.45 131.98\r\nv 323.62 1.97 134.04\r\nv 323.23 3.72 133.28\r\nv 326.61 -4.52 139.52\r\nv 329.11 -5.08 137.1\r\nv 323.43 -3.85 142.4\r\nv 323.15 -4.35 141.92\r\nv 326.31 -5.03 139.04\r\nv 328.79 -5.58 136.62\r\nv 319.57 -3.07 145.57\r\nv 319.32 -3.58 145.07\r\nv 318.93 -3.37 144.2\r\nv 322.73 -4.14 141.09\r\nv 328.31 -5.36 135.87\r\nv 325.85 -4.81 138.26\r\nv 329.2 5.28 132.78\r\nv 330.49 5 131.66\r\nv 330.31 5.54 131.41\r\nv 329.02 5.82 132.52\r\nv 332.29 4.61 129.9\r\nv 331.46 4.79 130.75\r\nv 332.1 5.15 129.66\r\nv 331.27 5.33 130.51\r\nv 331.08 5.44 130.26\r\nv 331.16 5.49 130.36\r\nv 331.98 5.31 129.51\r\nv 330.12 5.65 131.17\r\nv 330.19 5.7 131.27\r\nv 328.91 5.97 132.37\r\nv 328.84 5.93 132.27\r\nv 323.03 4.79 132.86\r\nv 325.36 4.28 130.94\r\nv 320.06 5.42 135.14\r\nv 320 5.97 134.95\r\nv 322.96 5.34 132.71\r\nv 325.28 4.83 130.83\r\nv 316.45 6.17 137.68\r\nv 316.39 6.72 137.47\r\nv 316.41 6.93 137.47\r\nv 320.02 6.18 134.96\r\nv 325.31 5.04 130.88\r\nv 322.98 5.55 132.74\r\nv 326.74 6.12 133.25\r\nv 324.34 6.64 135.17\r\nv 324.42 6.69 135.29\r\nv 326.83 6.17 133.37\r\nv 321.28 7.28 137.48\r\nv 321.36 7.33 137.6\r\nv 321.44 7.38 137.73\r\nv 324.5 6.74 135.41\r\nv 326.91 6.22 133.49\r\nv 317.63 8.08 140.22\r\nv 317.56 8.03 140.09\r\nv 317.71 8.13 140.35\r\nv 317.78 8.18 140.47\r\nv 321.51 7.43 137.84\r\nv 326.99 6.27 133.6\r\nv 324.58 6.78 135.53\r\nv 327.42 4.9 130.09\r\nv 327.33 4.85 129.98\r\nv 328.65 4.63 129.03\r\nv 328.56 4.58 128.92\r\nv 328.47 4.53 128.8\r\nv 327.24 4.8 129.86\r\nv 330.27 4.25 127.34\r\nv 330.18 4.2 127.23\r\nv 329.45 4.37 128.05\r\nv 329.54 4.42 128.16\r\nv 330.09 4.15 127.11\r\nv 329.37 4.33 127.93\r\nv 329.2 4.24 127.64\r\nv 329.28 4.28 127.79\r\nv 330 4.11 126.97\r\nv 328.3 4.44 128.52\r\nv 328.38 4.49 128.67\r\nv 327.16 4.76 129.73\r\nv 327.08 4.71 129.6\r\nv 331.8 -5.31 130.96\r\nv 331.11 -3.6 129.99\r\nv 330.82 -5.09 131.94\r\nv 330.15 -3.39 130.97\r\nv 329.51 -1.31 130.02\r\nv 330.45 -1.52 129.04\r\nv 328.87 -3.1 132.22\r\nv 329.52 -4.8 133.21\r\nv 328.24 -1.03 131.26\r\nv 327.28 2.83 129.77\r\nv 327.69 1.07 130.41\r\nv 328.51 2.56 128.58\r\nv 328.94 0.79 129.19\r\nv 329.86 0.58 128.21\r\nv 329.41 2.35 127.61\r\nv 333.3 -6.01 133.02\r\nv 332.29 -5.78 134.01\r\nv 331.95 -6.28 133.54\r\nv 332.96 -6.5 132.55\r\nv 330.94 -5.48 135.31\r\nv 330.62 -5.99 134.84\r\nv 330.12 -5.76 134.11\r\nv 331.44 -6.05 132.82\r\nv 332.43 -6.27 131.84\r\nv 329.18 3.42 127.35\r\nv 328.28 3.63 128.3\r\nv 328.2 4.19 128.25\r\nv 329.09 3.98 127.32\r\nv 327.06 3.9 129.46\r\nv 326.98 4.46 129.38\r\nv 327 4.67 129.46\r\nv 328.22 4.4 128.35\r\nv 329.12 4.19 127.45\r\nv 330.74 5.26 129.82\r\nv 330.83 5.31 129.94\r\nv 329.77 5.47 130.72\r\nv 329.86 5.52 130.84\r\nv 329.95 5.56 130.95\r\nv 330.91 5.35 130.05\r\nv 328.59 5.79 131.93\r\nv 328.5 5.74 131.82\r\nv 328.67 5.84 132.05\r\nv 328.76 5.89 132.16\r\nv 330.03 5.61 131.06\r\nv 331 5.4 130.16\r\nv 334.89 -3.2 131.15\r\nv 335.95 -3.42 129.96\r\nv 335.51 -1.11 129.39\r\nv 334.45 -0.88 130.6\r\nv 337.38 -3.72 128.2\r\nv 336.94 -1.41 127.61\r\nv 335.92 2.78 126.29\r\nv 336.41 0.89 126.92\r\nv 334.5 3.09 128.11\r\nv 334.99 1.19 128.72\r\nv 333.93 1.41 129.95\r\nv 333.46 3.31 129.36\r\nv 333.14 4.43 128.96\r\nv 334.19 4.21 127.7\r\nv 333.99 4.75 127.46\r\nv 332.95 4.97 128.72\r\nv 335.6 3.9 125.88\r\nv 335.4 4.44 125.63\r\nv 335.28 4.6 125.49\r\nv 333.87 4.91 127.31\r\nv 332.83 5.13 128.57\r\nv 330.97 4.07 126.43\r\nv 330.88 4.02 126.32\r\nv 331.85 3.85 125.21\r\nv 331.75 3.8 125.09\r\nv 331.66 3.75 124.98\r\nv 330.78 3.98 126.2\r\nv 333.01 3.5 123.33\r\nv 333.1 3.54 123.44\r\nv 332.91 3.45 123.22\r\nv 332.82 3.4 123.1\r\nv 331.57 3.71 124.85\r\nv 330.7 3.93 126.07\r\nvn 0.82 0.05 0.56\r\nvn 0.82 0.21 0.53\r\nvn 0.79 0.21 0.57\r\nvn 0.8 0.04 0.6\r\nvn 0.81 0.28 0.51\r\nvn 0.79 0.27 0.55\r\nvn 0.77 0.27 0.58\r\nvn 0.77 0.21 0.6\r\nvn 0.77 0.04 0.63\r\nvn 0.8 0.34 0.49\r\nvn 0.78 0.34 0.53\r\nvn 0.78 0.31 0.54\r\nvn 0.81 0.31 0.5\r\nvn 0.76 0.34 0.56\r\nvn 0.76 0.31 0.57\r\nvn 0.73 0.33 0.59\r\nvn 0.74 0.3 0.61\r\nvn 0.75 0.3 0.59\r\nvn 0.74 0.33 0.58\r\nvn 0.74 0.27 0.62\r\nvn 0.75 0.27 0.6\r\nvn 0.74 0.04 0.67\r\nvn 0.75 0.04 0.66\r\nvn 0.76 0.2 0.62\r\nvn 0.74 0.2 0.64\r\nvn 0.85 0.05 0.52\r\nvn 0.85 0.22 0.48\r\nvn 0.88 0.06 0.47\r\nvn 0.87 0.23 0.44\r\nvn 0.86 0.3 0.42\r\nvn 0.84 0.29 0.46\r\nvn 0.92 0.09 0.37\r\nvn 0.9 0.26 0.35\r\nvn 0.89 0.25 0.39\r\nvn 0.9 0.08 0.42\r\nvn 0.89 0.32 0.33\r\nvn 0.87 0.31 0.37\r\nvn 0.87 0.39 0.3\r\nvn 0.86 0.38 0.34\r\nvn 0.87 0.35 0.36\r\nvn 0.88 0.36 0.32\r\nvn 0.85 0.37 0.39\r\nvn 0.85 0.33 0.4\r\nvn 0.83 0.32 0.45\r\nvn 0.83 0.35 0.44\r\nvn 0.94 0.09 0.33\r\nvn 0.92 0.26 0.3\r\nvn 0.96 0.09 0.28\r\nvn 0.93 0.26 0.26\r\nvn 0.91 0.33 0.25\r\nvn 0.9 0.33 0.29\r\nvn 0.98 0.09 0.2\r\nvn 0.95 0.26 0.19\r\nvn 0.94 0.26 0.22\r\nvn 0.97 0.09 0.24\r\nvn 0.93 0.33 0.18\r\nvn 0.92 0.33 0.21\r\nvn 0.9 0.4 0.17\r\nvn 0.9 0.4 0.2\r\nvn 0.91 0.37 0.21\r\nvn 0.91 0.37 0.17\r\nvn 0.89 0.4 0.23\r\nvn 0.9 0.37 0.24\r\nvn 0.89 0.36 0.28\r\nvn 0.88 0.39 0.27\r\nvn 0.92 0.37 0.14\r\nvn 0.91 0.4 0.13\r\nvn 0.93 0.33 0.14\r\nvn 0.94 0.34 0.11\r\nvn 0.92 0.37 0.1\r\nvn 0.91 0.41 0.09\r\nvn 0.98 0.09 0.16\r\nvn 0.95 0.27 0.15\r\nvn 0.99 0.09 0.12\r\nvn 0.96 0.27 0.11\r\nvn 0.99 0.1 0.03\r\nvn 0.96 0.28 0.04\r\nvn 0.96 0.27 0.07\r\nvn 0.99 0.09 0.07\r\nvn 0.94 0.35 0.04\r\nvn 0.94 0.34 0.07\r\nvn 0.91 0.42 0.03\r\nvn 0.91 0.41 0.06\r\nvn 0.92 0.38 0.07\r\nvn 0.92 0.39 0.04\r\nvn 0.79 0.52 -0.32\r\nvn 0.8 0.49 -0.34\r\nvn 0.77 0.52 -0.36\r\nvn 0.76 0.55 -0.34\r\nvn 0.82 0.45 -0.37\r\nvn 0.79 0.47 -0.39\r\nvn 0.77 0.5 -0.4\r\nvn 0.75 0.55 -0.37\r\nvn 0.74 0.58 -0.35\r\nvn 0.84 0.19 -0.51\r\nvn 0.82 0.22 -0.54\r\nvn 0.81 0.4 -0.44\r\nvn 0.83 0.37 -0.41\r\nvn 0.79 0.25 -0.55\r\nvn 0.78 0.43 -0.45\r\nvn 0.75 0.3 -0.58\r\nvn 0.74 0.48 -0.47\r\nvn 0.76 0.45 -0.46\r\nvn 0.78 0.27 -0.57\r\nvn 0.72 0.55 -0.41\r\nvn 0.74 0.53 -0.41\r\nvn 0.7 0.62 -0.36\r\nvn 0.71 0.6 -0.35\r\nvn 0.73 0.57 -0.38\r\nvn 0.71 0.59 -0.38\r\nvn 0.4 0.76 -0.51\r\nvn 0.38 0.86 -0.34\r\nvn 0.43 0.83 -0.36\r\nvn 0.45 0.72 -0.53\r\nvn 0.37 0.89 -0.26\r\nvn 0.41 0.87 -0.28\r\nvn 0.44 0.84 -0.3\r\nvn 0.46 0.8 -0.38\r\nvn 0.48 0.68 -0.55\r\nvn 0.35 0.92 -0.18\r\nvn 0.38 0.9 -0.2\r\nvn 0.4 0.89 -0.23\r\nvn 0.36 0.91 -0.21\r\nvn 0.41 0.88 -0.22\r\nvn 0.42 0.87 -0.26\r\nvn 0.43 0.85 -0.3\r\nvn 0.46 0.82 -0.33\r\nvn 0.44 0.85 -0.29\r\nvn 0.42 0.87 -0.26\r\nvn 0.48 0.79 -0.38\r\nvn 0.47 0.82 -0.34\r\nvn 0.54 0.59 -0.61\r\nvn 0.51 0.64 -0.58\r\nvn 0.49 0.77 -0.41\r\nvn 0.51 0.73 -0.45\r\nvn 0.73 0.32 -0.61\r\nvn 0.72 0.5 -0.49\r\nvn 0.68 0.35 -0.64\r\nvn 0.68 0.52 -0.51\r\nvn 0.67 0.59 -0.45\r\nvn 0.7 0.57 -0.43\r\nvn 0.62 0.39 -0.68\r\nvn 0.62 0.55 -0.55\r\nvn 0.64 0.54 -0.54\r\nvn 0.64 0.38 -0.67\r\nvn 0.63 0.61 -0.48\r\nvn 0.64 0.61 -0.48\r\nvn 0.64 0.66 -0.4\r\nvn 0.63 0.67 -0.4\r\nvn 0.63 0.64 -0.43\r\nvn 0.63 0.64 -0.44\r\nvn 0.65 0.66 -0.38\r\nvn 0.66 0.63 -0.41\r\nvn 0.69 0.61 -0.39\r\nvn 0.68 0.64 -0.37\r\nvn 0.9 0.43 -0.07\r\nvn 0.91 0.4 -0.08\r\nvn 0.91 0.4 -0.11\r\nvn 0.9 0.43 -0.1\r\nvn 0.93 0.36 -0.09\r\nvn 0.92 0.36 -0.12\r\nvn 0.92 0.37 -0.16\r\nvn 0.9 0.41 -0.15\r\nvn 0.89 0.44 -0.13\r\nvn 0.98 0.1 -0.14\r\nvn 0.98 0.1 -0.19\r\nvn 0.95 0.29 -0.14\r\nvn 0.95 0.29 -0.1\r\nvn 0.96 0.11 -0.24\r\nvn 0.94 0.29 -0.19\r\nvn 0.93 0.13 -0.35\r\nvn 0.91 0.31 -0.28\r\nvn 0.92 0.3 -0.23\r\nvn 0.95 0.12 -0.3\r\nvn 0.89 0.38 -0.24\r\nvn 0.9 0.37 -0.2\r\nvn 0.87 0.46 -0.21\r\nvn 0.88 0.45 -0.17\r\nvn 0.89 0.41 -0.19\r\nvn 0.88 0.42 -0.22\r\nvn 0.91 0.14 -0.39\r\nvn 0.89 0.32 -0.31\r\nvn 0.89 0.15 -0.43\r\nvn 0.88 0.33 -0.35\r\nvn 0.86 0.41 -0.31\r\nvn 0.88 0.39 -0.28\r\nvn 0.86 0.35 -0.38\r\nvn 0.87 0.17 -0.47\r\nvn 0.84 0.42 -0.34\r\nvn 0.82 0.5 -0.3\r\nvn 0.83 0.46 -0.32\r\nvn 0.84 0.48 -0.27\r\nvn 0.85 0.45 -0.29\r\nvn 0.86 0.43 -0.26\r\nvn 0.85 0.47 -0.24\r\nvn 0.99 0.1 -0.01\r\nvn 0.96 0.28 0\r\nvn 0.99 0.1 -0.05\r\nvn 0.96 0.29 -0.03\r\nvn 0.93 0.36 -0.02\r\nvn 0.94 0.35 0.01\r\nvn 0.96 0.29 -0.07\r\nvn 0.99 0.1 -0.1\r\nvn 0.93 0.36 -0.05\r\nvn 0.9 0.43 -0.04\r\nvn 0.92 0.4 -0.05\r\nvn 0.9 0.43 -0.02\r\nvn 0.92 0.4 -0.02\r\nvn 0.92 0.39 0.01\r\nvn 0.91 0.42 0.01\r\nvn 0.48 0.12 0.87\r\nvn 0.54 0.1 0.84\r\nvn 0.54 0.27 0.8\r\nvn 0.49 0.28 0.82\r\nvn 0.58 0.08 0.81\r\nvn 0.58 0.25 0.78\r\nvn 0.57 0.31 0.76\r\nvn 0.53 0.33 0.78\r\nvn 0.49 0.35 0.8\r\nvn 0.62 0.04 0.79\r\nvn 0.62 0.2 0.76\r\nvn 0.61 0.22 0.76\r\nvn 0.61 0.06 0.79\r\nvn 0.61 0.27 0.75\r\nvn 0.6 0.29 0.75\r\nvn 0.58 0.33 0.74\r\nvn 0.57 0.35 0.74\r\nvn 0.58 0.32 0.75\r\nvn 0.6 0.3 0.74\r\nvn 0.54 0.37 0.75\r\nvn 0.56 0.34 0.76\r\nvn 0.48 0.41 0.78\r\nvn 0.48 0.38 0.79\r\nvn 0.52 0.36 0.77\r\nvn 0.51 0.39 0.76\r\nvn 0.29 0.11 0.95\r\nvn 0.33 0.11 0.94\r\nvn 0.35 0.29 0.89\r\nvn 0.3 0.28 0.91\r\nvn 0.38 0.12 0.92\r\nvn 0.39 0.29 0.87\r\nvn 0.4 0.35 0.85\r\nvn 0.35 0.35 0.87\r\nvn 0.3 0.35 0.89\r\nvn 0.44 0.29 0.85\r\nvn 0.43 0.12 0.89\r\nvn 0.44 0.35 0.82\r\nvn 0.44 0.41 0.8\r\nvn 0.44 0.39 0.81\r\nvn 0.4 0.41 0.82\r\nvn 0.4 0.39 0.83\r\nvn 0.3 0.41 0.86\r\nvn 0.3 0.38 0.87\r\nvn 0.36 0.38 0.85\r\nvn 0.36 0.41 0.84\r\nvn 0.34 0.8 -0.5\r\nvn 0.32 0.89 -0.32\r\nvn 0.25 0.84 -0.49\r\nvn 0.24 0.92 -0.31\r\nvn 0.23 0.95 -0.23\r\nvn 0.31 0.92 -0.24\r\nvn 0 0.88 -0.48\r\nvn -0 0.95 -0.3\r\nvn 0.13 0.95 -0.3\r\nvn 0.14 0.87 -0.48\r\nvn 0 0.98 -0.22\r\nvn 0.13 0.97 -0.22\r\nvn 0 0.99 -0.14\r\nvn 0.12 0.98 -0.14\r\nvn 0.12 0.98 -0.18\r\nvn 0 0.98 -0.18\r\nvn 0.22 0.96 -0.15\r\nvn 0.22 0.96 -0.19\r\nvn 0.3 0.93 -0.2\r\nvn 0.29 0.94 -0.16\r\nvn -0.8 -0.33 -0.5\r\nvn -0.79 -0.36 -0.49\r\nvn -0.77 -0.36 -0.53\r\nvn -0.77 -0.33 -0.55\r\nvn -0.78 -0.39 -0.48\r\nvn -0.76 -0.39 -0.52\r\nvn -0.74 -0.4 -0.55\r\nvn -0.74 -0.36 -0.56\r\nvn -0.75 -0.33 -0.57\r\nvn -0.7 -0.59 -0.4\r\nvn -0.69 -0.59 -0.43\r\nvn -0.74 -0.45 -0.5\r\nvn -0.76 -0.46 -0.46\r\nvn -0.67 -0.59 -0.45\r\nvn -0.72 -0.46 -0.52\r\nvn -0.65 -0.59 -0.48\r\nvn -0.7 -0.46 -0.55\r\nvn -0.71 -0.46 -0.54\r\nvn -0.66 -0.59 -0.47\r\nvn -0.71 -0.4 -0.57\r\nvn -0.72 -0.4 -0.56\r\nvn -0.73 -0.33 -0.6\r\nvn -0.73 -0.33 -0.59\r\nvn -0.73 -0.36 -0.58\r\nvn -0.72 -0.36 -0.59\r\nvn -0.88 -0.36 -0.31\r\nvn -0.87 -0.39 -0.31\r\nvn -0.86 -0.38 -0.35\r\nvn -0.87 -0.35 -0.35\r\nvn -0.85 -0.43 -0.3\r\nvn -0.84 -0.42 -0.34\r\nvn -0.83 -0.41 -0.39\r\nvn -0.84 -0.37 -0.39\r\nvn -0.85 -0.34 -0.4\r\nvn -0.74 -0.63 -0.25\r\nvn -0.73 -0.62 -0.29\r\nvn -0.81 -0.48 -0.33\r\nvn -0.82 -0.49 -0.29\r\nvn -0.72 -0.61 -0.32\r\nvn -0.8 -0.47 -0.37\r\nvn -0.78 -0.46 -0.42\r\nvn -0.72 -0.6 -0.36\r\nvn -0.81 -0.4 -0.43\r\nvn -0.83 -0.33 -0.45\r\nvn -0.82 -0.36 -0.44\r\nvn -0.91 -0.37 -0.17\r\nvn -0.9 -0.41 -0.17\r\nvn -0.89 -0.41 -0.2\r\nvn -0.91 -0.37 -0.2\r\nvn -0.88 -0.44 -0.17\r\nvn -0.87 -0.44 -0.2\r\nvn -0.87 -0.44 -0.23\r\nvn -0.88 -0.4 -0.23\r\nvn -0.9 -0.37 -0.24\r\nvn -0.75 -0.64 -0.14\r\nvn -0.75 -0.64 -0.16\r\nvn -0.84 -0.5 -0.19\r\nvn -0.85 -0.51 -0.16\r\nvn -0.75 -0.64 -0.19\r\nvn -0.84 -0.5 -0.22\r\nvn -0.83 -0.5 -0.25\r\nvn -0.74 -0.63 -0.22\r\nvn -0.86 -0.44 -0.26\r\nvn -0.89 -0.37 -0.27\r\nvn -0.88 -0.4 -0.27\r\nvn 0.93 -0.36 0.05\r\nvn 0.93 -0.36 -0\r\nvn 0.93 -0.36 0.1\r\nvn 0.33 -0.94 0.01\r\nvn 0.33 -0.94 -0.02\r\nvn 0.34 -0.94 -0.06\r\nvn 0.91 -0.36 0.19\r\nvn 0.92 -0.36 0.15\r\nvn 0.32 -0.94 0.08\r\nvn 0.32 -0.94 0.05\r\nvn -0.75 -0.65 -0.12\r\nvn -0.46 -0.89 -0.08\r\nvn -0.46 -0.88 -0.08\r\nvn -0.75 -0.65 -0.1\r\nvn -0.45 -0.89 -0.08\r\nvn -0.74 -0.67 -0.06\r\nvn -0.44 -0.9 -0.07\r\nvn -0.44 -0.89 -0.07\r\nvn -0.75 -0.66 -0.08\r\nvn 0.74 -0.21 -0.63\r\nvn 0.72 -0.18 -0.67\r\nvn 0.28 -0.77 -0.57\r\nvn 0.28 -0.75 -0.61\r\nvn 0.27 -0.72 -0.63\r\nvn 0.7 -0.16 -0.69\r\nvn -0.64 -0.76 0.09\r\nvn -0.62 -0.78 0.09\r\nvn -0.32 -0.92 -0.22\r\nvn -0.33 -0.92 -0.2\r\nvn -0.59 -0.8 0.09\r\nvn -0.3 -0.92 -0.24\r\nvn -0.55 -0.83 0.08\r\nvn -0.27 -0.93 -0.27\r\nvn -0.29 -0.92 -0.25\r\nvn -0.57 -0.82 0.08\r\nvn 0.27 -0.69 -0.68\r\nvn 0.27 -0.71 -0.66\r\nvn 0.69 -0.13 -0.72\r\nvn 0.67 -0.11 -0.74\r\nvn -0.29 -0.94 -0.19\r\nvn -0.31 -0.93 -0.17\r\nvn -0.36 -0.93 0.01\r\nvn -0.33 -0.94 -0.01\r\nvn -0.34 -0.93 -0.14\r\nvn -0.39 -0.92 0.05\r\nvn -0.4 -0.91 0.13\r\nvn -0.37 -0.92 0.09\r\nvn -0.34 -0.94 0.07\r\nvn -0.38 -0.92 -0.06\r\nvn -0.44 -0.89 0.12\r\nvn -0.41 -0.91 0.08\r\nvn -0.36 -0.93 -0.1\r\nvn -0.45 -0.87 0.21\r\nvn -0.42 -0.89 0.17\r\nvn -0.44 -0.85 0.3\r\nvn -0.42 -0.87 0.26\r\nvn -0.42 -0.88 0.22\r\nvn -0.45 -0.86 0.26\r\nvn -0.39 -0.89 0.21\r\nvn -0.4 -0.9 0.18\r\nvn -0.34 -0.93 0.16\r\nvn -0.34 -0.93 0.12\r\nvn -0.37 -0.92 0.14\r\nvn -0.37 -0.91 0.18\r\nvn 0.56 0.54 -0.63\r\nvn 0.51 0.15 -0.85\r\nvn 0.48 0.2 -0.85\r\nvn 0.58 0.49 -0.65\r\nvn 0.53 0.11 -0.84\r\nvn 0.21 -0.53 -0.82\r\nvn 0.2 -0.5 -0.84\r\nvn 0.19 -0.47 -0.86\r\nvn 0.61 0.42 -0.67\r\nvn 0.54 0.06 -0.84\r\nvn 0.54 0.08 -0.84\r\nvn 0.6 0.45 -0.66\r\nvn 0.22 -0.56 -0.8\r\nvn 0.22 -0.55 -0.81\r\nvn -0.46 -0.89 0.05\r\nvn -0.44 -0.9 0.03\r\nvn -0.22 -0.91 -0.35\r\nvn -0.24 -0.91 -0.34\r\nvn -0.42 -0.91 0.01\r\nvn -0.21 -0.9 -0.37\r\nvn -0.2 -0.88 -0.43\r\nvn -0.2 -0.89 -0.4\r\nvn -0.4 -0.92 -0.02\r\nvn 0.91 -0.35 -0.21\r\nvn 0.9 -0.35 -0.26\r\nvn 0.34 -0.92 -0.21\r\nvn 0.33 -0.91 -0.25\r\nvn 0.33 -0.89 -0.3\r\nvn 0.88 -0.34 -0.33\r\nvn -0.73 -0.69 0.01\r\nvn -0.72 -0.69 0.03\r\nvn -0.4 -0.91 -0.09\r\nvn -0.41 -0.91 -0.08\r\nvn -0.72 -0.7 0.04\r\nvn -0.4 -0.91 -0.1\r\nvn -0.7 -0.71 0.07\r\nvn -0.39 -0.91 -0.13\r\nvn -0.39 -0.91 -0.11\r\nvn -0.71 -0.7 0.06\r\nvn 0.31 -0.86 -0.41\r\nvn 0.32 -0.88 -0.36\r\nvn 0.86 -0.32 -0.39\r\nvn 0.84 -0.3 -0.45\r\nvn 0.77 -0.24 -0.59\r\nvn 0.79 -0.26 -0.55\r\nvn 0.3 -0.81 -0.5\r\nvn 0.29 -0.79 -0.54\r\nvn 0.82 -0.28 -0.5\r\nvn 0.3 -0.84 -0.46\r\nvn -0.69 -0.72 0.08\r\nvn -0.38 -0.91 -0.15\r\nvn -0.68 -0.73 0.09\r\nvn -0.36 -0.92 -0.16\r\nvn -0.35 -0.92 -0.18\r\nvn -0.66 -0.75 0.09\r\nvn -0.73 -0.68 -0.01\r\nvn -0.84 -0.55 0.02\r\nvn -0.83 -0.55 0.05\r\nvn -0.73 -0.68 -0.02\r\nvn -0.84 -0.54 0\r\nvn -0.88 -0.48 0.01\r\nvn -0.88 -0.48 0.04\r\nvn -0.87 -0.48 0.06\r\nvn -0.85 -0.53 -0.05\r\nvn -0.84 -0.54 -0.02\r\nvn -0.74 -0.67 -0.04\r\nvn -0.88 -0.47 -0.04\r\nvn -0.88 -0.47 -0.02\r\nvn -0.92 -0.4 -0.03\r\nvn -0.92 -0.4 -0.01\r\nvn -0.9 -0.43 -0.01\r\nvn -0.9 -0.43 -0.04\r\nvn -0.91 -0.4 0.02\r\nvn -0.9 -0.44 0.02\r\nvn -0.91 -0.41 0.07\r\nvn -0.89 -0.44 0.07\r\nvn -0.9 -0.44 0.04\r\nvn -0.91 -0.41 0.05\r\nvn -0.72 -0.36 -0.6\r\nvn -0.73 -0.32 -0.61\r\nvn -0.7 -0.4 -0.59\r\nvn -0.69 -0.39 -0.61\r\nvn -0.71 -0.35 -0.61\r\nvn -0.72 -0.31 -0.62\r\nvn -0.64 -0.58 -0.5\r\nvn -0.69 -0.46 -0.56\r\nvn -0.63 -0.58 -0.52\r\nvn -0.67 -0.45 -0.59\r\nvn -0.6 -0.57 -0.56\r\nvn -0.64 -0.44 -0.62\r\nvn -0.65 -0.45 -0.61\r\nvn -0.61 -0.58 -0.54\r\nvn -0.67 -0.38 -0.64\r\nvn -0.67 -0.39 -0.63\r\nvn -0.7 -0.29 -0.65\r\nvn -0.71 -0.3 -0.64\r\nvn -0.69 -0.35 -0.63\r\nvn -0.69 -0.34 -0.65\r\nvn -0.48 -0.39 -0.79\r\nvn -0.48 -0.42 -0.77\r\nvn -0.44 -0.42 -0.79\r\nvn -0.44 -0.39 -0.81\r\nvn -0.48 -0.45 -0.75\r\nvn -0.44 -0.46 -0.78\r\nvn -0.4 -0.45 -0.8\r\nvn -0.4 -0.42 -0.82\r\nvn -0.4 -0.39 -0.83\r\nvn -0.46 -0.63 -0.63\r\nvn -0.42 -0.63 -0.65\r\nvn -0.43 -0.51 -0.74\r\nvn -0.48 -0.51 -0.72\r\nvn -0.37 -0.63 -0.68\r\nvn -0.39 -0.51 -0.77\r\nvn -0.29 -0.63 -0.72\r\nvn -0.3 -0.51 -0.81\r\nvn -0.35 -0.51 -0.79\r\nvn -0.33 -0.63 -0.7\r\nvn -0.3 -0.45 -0.84\r\nvn -0.35 -0.45 -0.82\r\nvn -0.3 -0.39 -0.87\r\nvn -0.36 -0.39 -0.85\r\nvn -0.35 -0.42 -0.84\r\nvn -0.3 -0.41 -0.86\r\nvn -0.24 -0.42 -0.88\r\nvn -0.24 -0.39 -0.89\r\nvn -0.24 -0.45 -0.86\r\nvn -0.17 -0.46 -0.87\r\nvn -0.17 -0.42 -0.89\r\nvn -0.17 -0.4 -0.9\r\nvn -0.24 -0.63 -0.74\r\nvn -0.24 -0.51 -0.83\r\nvn -0.17 -0.64 -0.75\r\nvn -0.17 -0.52 -0.84\r\nvn -0 -0.65 -0.76\r\nvn -0 -0.53 -0.85\r\nvn -0.09 -0.52 -0.85\r\nvn -0.09 -0.65 -0.76\r\nvn 0 -0.47 -0.88\r\nvn -0.09 -0.47 -0.88\r\nvn -0 -0.41 -0.91\r\nvn -0.09 -0.4 -0.91\r\nvn -0.09 -0.43 -0.9\r\nvn -0 -0.43 -0.9\r\nvn -0.48 -0.85 -0.21\r\nvn -0.48 -0.84 -0.23\r\nvn 0.18 -0.95 0.27\r\nvn 0.15 -0.95 0.28\r\nvn 0.13 -0.95 0.28\r\nvn -0.48 -0.84 -0.25\r\nvn 0.7 -0.38 0.6\r\nvn 0.73 -0.38 0.57\r\nvn 0.68 -0.38 0.63\r\nvn 0.63 -0.39 0.67\r\nvn 0.65 -0.39 0.65\r\nvn 0.09 -0.95 0.29\r\nvn 0.11 -0.95 0.28\r\nvn -0.48 -0.84 -0.26\r\nvn -0.48 -0.83 -0.27\r\nvn 0.83 -0.36 0.43\r\nvn 0.85 -0.35 0.38\r\nvn 0.8 -0.36 0.48\r\nvn 0.23 -0.94 0.23\r\nvn 0.26 -0.94 0.21\r\nvn 0.27 -0.94 0.19\r\nvn 0.77 -0.37 0.52\r\nvn 0.21 -0.95 0.25\r\nvn -0.48 -0.86 -0.18\r\nvn -0.48 -0.86 -0.16\r\nvn -0.47 -0.87 -0.12\r\nvn -0.47 -0.87 -0.14\r\nvn 0.91 -0.35 0.24\r\nvn 0.89 -0.35 0.28\r\nvn 0.3 -0.94 0.14\r\nvn 0.31 -0.94 0.11\r\nvn 0.88 -0.35 0.33\r\nvn 0.29 -0.94 0.17\r\nvn -0.47 -0.88 -0.11\r\nvn -0.47 -0.88 -0.09\r\nvn -0.47 -0.88 -0.09\r\nvn -0.92 -0.38 -0.13\r\nvn -0.9 -0.41 -0.13\r\nvn -0.92 -0.38 -0.09\r\nvn -0.91 -0.41 -0.1\r\nvn -0.89 -0.45 -0.1\r\nvn -0.88 -0.45 -0.13\r\nvn -0.9 -0.42 -0.06\r\nvn -0.92 -0.39 -0.06\r\nvn -0.89 -0.46 -0.07\r\nvn -0.85 -0.52 -0.07\r\nvn -0.85 -0.52 -0.1\r\nvn -0.85 -0.51 -0.13\r\nvn -0.8 -0.49 0.35\r\nvn -0.77 -0.52 0.37\r\nvn -0.76 -0.56 0.34\r\nvn -0.79 -0.53 0.32\r\nvn -0.74 -0.55 0.38\r\nvn -0.73 -0.58 0.35\r\nvn -0.72 -0.62 0.31\r\nvn -0.74 -0.6 0.3\r\nvn -0.77 -0.57 0.29\r\nvn -0.7 -0.59 0.41\r\nvn -0.68 -0.63 0.36\r\nvn -0.71 -0.61 0.36\r\nvn -0.72 -0.57 0.39\r\nvn -0.67 -0.67 0.32\r\nvn -0.7 -0.65 0.31\r\nvn -0.67 -0.7 0.24\r\nvn -0.64 -0.73 0.25\r\nvn -0.69 -0.68 0.24\r\nvn -0.74 -0.63 0.23\r\nvn -0.71 -0.66 0.24\r\nvn -0.15 -0.82 -0.54\r\nvn -0.17 -0.83 -0.53\r\nvn 0.14 -0.3 -0.94\r\nvn 0.15 -0.34 -0.93\r\nvn 0.16 -0.38 -0.91\r\nvn -0.18 -0.85 -0.5\r\nvn 0.4 0.37 -0.84\r\nvn 0.36 0.42 -0.83\r\nvn 0.43 0.32 -0.84\r\nvn 0.46 0.26 -0.85\r\nvn 0.17 -0.43 -0.89\r\nvn -0.19 -0.86 -0.47\r\nvn -0.66 -0.62 0.42\r\nvn -0.65 -0.66 0.38\r\nvn -0.62 -0.65 0.45\r\nvn -0.61 -0.69 0.4\r\nvn -0.6 -0.72 0.35\r\nvn -0.64 -0.69 0.33\r\nvn -0.56 -0.68 0.47\r\nvn -0.56 -0.72 0.42\r\nvn -0.56 -0.71 0.42\r\nvn -0.57 -0.67 0.47\r\nvn -0.56 -0.75 0.36\r\nvn -0.56 -0.74 0.37\r\nvn -0.47 -0.88 0.07\r\nvn -0.46 -0.88 0.08\r\nvn -0.54 -0.79 0.28\r\nvn -0.54 -0.8 0.27\r\nvn -0.48 -0.87 0.08\r\nvn -0.57 -0.78 0.27\r\nvn -0.61 -0.75 0.26\r\nvn -0.52 -0.85 0.08\r\nvn -0.91 -0.41 0.11\r\nvn -0.89 -0.44 0.1\r\nvn -0.9 -0.41 0.15\r\nvn -0.88 -0.45 0.14\r\nvn -0.86 -0.49 0.13\r\nvn -0.87 -0.49 0.09\r\nvn -0.88 -0.43 0.22\r\nvn -0.86 -0.47 0.21\r\nvn -0.87 -0.46 0.17\r\nvn -0.89 -0.42 0.18\r\nvn -0.84 -0.51 0.19\r\nvn -0.85 -0.5 0.16\r\nvn -0.82 -0.56 0.13\r\nvn -0.8 -0.57 0.16\r\nvn -0.82 -0.56 0.1\r\nvn -0.83 -0.55 0.07\r\nvn -0.86 -0.44 0.25\r\nvn -0.85 -0.48 0.24\r\nvn -0.84 -0.45 0.28\r\nvn -0.83 -0.49 0.27\r\nvn -0.81 -0.53 0.24\r\nvn -0.83 -0.52 0.22\r\nvn -0.81 -0.5 0.29\r\nvn -0.82 -0.47 0.32\r\nvn -0.8 -0.55 0.27\r\nvn -0.76 -0.61 0.22\r\nvn -0.78 -0.6 0.2\r\nvn -0.79 -0.58 0.18\r\nvn 0.92 -0.36 -0.15\r\nvn 0.93 -0.36 -0.1\r\nvn 0.34 -0.93 -0.13\r\nvn 0.34 -0.93 -0.17\r\nvn 0.93 -0.36 -0.05\r\nvn 0.34 -0.94 -0.09\r\nvn -0.43 -0.9 -0.07\r\nvn -0.42 -0.9 -0.07\r\nvn -0.42 -0.91 -0.07\r\nvn -0.48 -0.83 -0.28\r\nvn 0.08 -0.95 0.29\r\nvn 0.06 -0.95 0.31\r\nvn -0.47 -0.83 -0.29\r\nvn 0.72 0.04 0.7\r\nvn 0.61 -0.39 0.69\r\nvn 0.69 0.03 0.73\r\nvn 0.57 -0.4 0.72\r\nvn 0.63 0.02 0.78\r\nvn 0.51 -0.4 0.76\r\nvn 0.53 -0.4 0.75\r\nvn 0.65 0.03 0.76\r\nvn 0.02 -0.95 0.32\r\nvn 0.03 -0.95 0.32\r\nvn -0.47 -0.83 -0.31\r\nvn -0.47 -0.83 -0.32\r\nvn -0.37 -0.85 -0.37\r\nvn -0.34 -0.85 -0.4\r\nvn -0 -0.94 0.35\r\nvn -0.02 -0.94 0.34\r\nvn -0.03 -0.95 0.32\r\nvn -0.31 -0.85 -0.42\r\nvn 0.34 -0.34 0.88\r\nvn 0.39 -0.33 0.86\r\nvn 0.29 -0.35 0.89\r\nvn 0.21 -0.37 0.91\r\nvn 0.25 -0.36 0.9\r\nvn -0.04 -0.96 0.29\r\nvn -0.04 -0.95 0.31\r\nvn -0.28 -0.85 -0.44\r\nvn -0.25 -0.85 -0.46\r\nvn 0.23 0.11 0.97\r\nvn 0.17 -0.37 0.92\r\nvn 0.17 0.12 0.98\r\nvn 0.12 -0.36 0.92\r\nvn -0.03 -0.96 0.28\r\nvn -0.04 -0.96 0.28\r\nvn 0 0.13 0.99\r\nvn 0 -0.36 0.93\r\nvn 0.06 -0.36 0.93\r\nvn 0.09 0.12 0.99\r\nvn -0 -0.96 0.28\r\nvn -0.02 -0.96 0.28\r\nvn -0.08 -0.87 -0.49\r\nvn -0 -0.87 -0.5\r\nvn -0.15 -0.86 -0.49\r\nvn -0.2 -0.85 -0.48\r\nvn 0.09 0.3 0.95\r\nvn 0 0.3 0.95\r\nvn 0.17 0.29 0.94\r\nvn 0.17 0.36 0.92\r\nvn 0.09 0.36 0.93\r\nvn 0 0.37 0.93\r\nvn 0.24 0.29 0.93\r\nvn 0.24 0.35 0.91\r\nvn 0.24 0.41 0.88\r\nvn 0.24 0.38 0.89\r\nvn 0.17 0.42 0.89\r\nvn 0.17 0.39 0.9\r\nvn 0 0.43 0.9\r\nvn 0 0.4 0.92\r\nvn 0.09 0.4 0.91\r\nvn 0.09 0.43 0.9\r\nvn -0.29 -0.96 -0.02\r\nvn -0.25 -0.95 -0.2\r\nvn -0.3 -0.95 0.06\r\nvn -0.23 -0.97 0.06\r\nvn -0.22 -0.98 -0.02\r\nvn -0.19 -0.96 -0.2\r\nvn -0.31 -0.94 0.14\r\nvn -0.3 -0.95 0.11\r\nvn -0.24 -0.96 0.14\r\nvn -0.23 -0.97 0.1\r\nvn -0 -0.99 0.15\r\nvn -0 -0.99 0.11\r\nvn -0.13 -0.99 0.11\r\nvn -0.14 -0.98 0.14\r\nvn -0 -1 0.06\r\nvn -0.13 -0.99 0.06\r\nvn -0 -0.98 -0.19\r\nvn -0.11 -0.98 -0.19\r\nvn -0.12 -0.99 -0.02\r\nvn -0 -1 -0.01\r\nvn 0.31 0.47 -0.83\r\nvn 0.11 -0.27 -0.96\r\nvn 0.08 -0.25 -0.97\r\nvn 0.23 0.51 -0.83\r\nvn -0.14 -0.82 -0.55\r\nvn -0.11 -0.83 -0.55\r\nvn -0 -0.84 -0.55\r\nvn -0.06 -0.83 -0.55\r\nvn -0 -0.23 -0.97\r\nvn 0.04 -0.23 -0.97\r\nvn 0.12 0.54 -0.83\r\nvn 0 0.56 -0.83\r\nvn -0.82 0.11 0.56\r\nvn -0.8 0.1 0.6\r\nvn -0.79 0.27 0.56\r\nvn -0.81 0.27 0.52\r\nvn -0.78 0.09 0.62\r\nvn -0.77 0.26 0.58\r\nvn -0.76 0.32 0.56\r\nvn -0.78 0.33 0.53\r\nvn -0.8 0.34 0.5\r\nvn -0.74 0.08 0.67\r\nvn -0.74 0.24 0.62\r\nvn -0.76 0.25 0.61\r\nvn -0.76 0.09 0.65\r\nvn -0.74 0.3 0.6\r\nvn -0.75 0.31 0.58\r\nvn -0.74 0.36 0.56\r\nvn -0.75 0.37 0.55\r\nvn -0.75 0.35 0.57\r\nvn -0.74 0.34 0.58\r\nvn -0.76 0.38 0.53\r\nvn -0.76 0.35 0.55\r\nvn -0.79 0.4 0.47\r\nvn -0.79 0.37 0.48\r\nvn -0.77 0.36 0.52\r\nvn -0.77 0.39 0.5\r\nvn -0.83 0.28 0.47\r\nvn -0.85 0.12 0.51\r\nvn -0.82 0.35 0.45\r\nvn -0.84 0.35 0.41\r\nvn -0.86 0.29 0.43\r\nvn -0.88 0.12 0.46\r\nvn -0.81 0.41 0.42\r\nvn -0.81 0.38 0.44\r\nvn -0.82 0.42 0.38\r\nvn -0.83 0.39 0.39\r\nvn -0.85 0.44 0.3\r\nvn -0.86 0.41 0.31\r\nvn -0.85 0.4 0.35\r\nvn -0.84 0.43 0.34\r\nvn -0.87 0.37 0.32\r\nvn -0.86 0.36 0.37\r\nvn -0.92 0.14 0.37\r\nvn -0.9 0.13 0.42\r\nvn -0.87 0.3 0.38\r\nvn -0.89 0.31 0.34\r\nvn -0.9 0.31 0.29\r\nvn -0.94 0.15 0.32\r\nvn -0.88 0.38 0.28\r\nvn -0.89 0.38 0.24\r\nvn -0.91 0.32 0.25\r\nvn -0.95 0.15 0.27\r\nvn -0.86 0.44 0.26\r\nvn -0.87 0.41 0.27\r\nvn -0.87 0.44 0.22\r\nvn -0.88 0.42 0.23\r\nvn -0.88 0.45 0.16\r\nvn -0.89 0.42 0.17\r\nvn -0.89 0.42 0.2\r\nvn -0.87 0.45 0.19\r\nvn -0.91 0.38 0.17\r\nvn -0.9 0.38 0.2\r\nvn -0.97 0.15 0.2\r\nvn -0.96 0.15 0.23\r\nvn -0.92 0.32 0.21\r\nvn -0.93 0.32 0.18\r\nvn -0.88 0.45 0.13\r\nvn -0.9 0.42 0.13\r\nvn -0.88 0.46 0.1\r\nvn -0.9 0.43 0.1\r\nvn -0.91 0.39 0.11\r\nvn -0.91 0.39 0.14\r\nvn -0.88 0.47 0.04\r\nvn -0.9 0.44 0.04\r\nvn -0.9 0.43 0.07\r\nvn -0.88 0.46 0.06\r\nvn -0.91 0.4 0.04\r\nvn -0.92 0.4 0.07\r\nvn -0.99 0.16 0.03\r\nvn -0.99 0.15 0.07\r\nvn -0.94 0.33 0.08\r\nvn -0.94 0.34 0.04\r\nvn -0.98 0.15 0.12\r\nvn -0.94 0.33 0.11\r\nvn -0.93 0.32 0.15\r\nvn -0.98 0.15 0.16\r\nvn -0.77 0.58 -0.27\r\nvn -0.75 0.59 -0.29\r\nvn -0.77 0.56 -0.31\r\nvn -0.78 0.54 -0.3\r\nvn -0.74 0.6 -0.3\r\nvn -0.75 0.57 -0.33\r\nvn -0.77 0.53 -0.36\r\nvn -0.78 0.52 -0.34\r\nvn -0.8 0.51 -0.32\r\nvn -0.72 0.61 -0.33\r\nvn -0.73 0.58 -0.36\r\nvn -0.74 0.57 -0.35\r\nvn -0.73 0.6 -0.32\r\nvn -0.74 0.54 -0.4\r\nvn -0.75 0.54 -0.38\r\nvn -0.76 0.31 -0.57\r\nvn -0.78 0.3 -0.55\r\nvn -0.77 0.47 -0.44\r\nvn -0.75 0.48 -0.46\r\nvn -0.8 0.29 -0.53\r\nvn -0.78 0.46 -0.42\r\nvn -0.84 0.26 -0.47\r\nvn -0.82 0.44 -0.37\r\nvn -0.8 0.45 -0.4\r\nvn -0.82 0.27 -0.5\r\nvn -0.4 0.76 -0.51\r\nvn -0.45 0.72 -0.53\r\nvn -0.43 0.83 -0.36\r\nvn -0.38 0.86 -0.34\r\nvn -0.48 0.68 -0.55\r\nvn -0.46 0.8 -0.38\r\nvn -0.44 0.84 -0.3\r\nvn -0.41 0.87 -0.28\r\nvn -0.37 0.89 -0.26\r\nvn -0.54 0.59 -0.6\r\nvn -0.52 0.73 -0.44\r\nvn -0.49 0.77 -0.41\r\nvn -0.51 0.63 -0.58\r\nvn -0.49 0.79 -0.37\r\nvn -0.47 0.82 -0.33\r\nvn -0.44 0.85 -0.29\r\nvn -0.43 0.87 -0.25\r\nvn -0.45 0.85 -0.29\r\nvn -0.46 0.82 -0.33\r\nvn -0.41 0.88 -0.22\r\nvn -0.43 0.87 -0.26\r\nvn -0.35 0.92 -0.18\r\nvn -0.36 0.91 -0.21\r\nvn -0.4 0.89 -0.23\r\nvn -0.38 0.9 -0.2\r\nvn -0.72 0.49 -0.48\r\nvn -0.73 0.33 -0.6\r\nvn -0.71 0.56 -0.42\r\nvn -0.68 0.58 -0.46\r\nvn -0.69 0.51 -0.51\r\nvn -0.69 0.36 -0.63\r\nvn -0.7 0.62 -0.35\r\nvn -0.71 0.59 -0.39\r\nvn -0.67 0.63 -0.38\r\nvn -0.68 0.61 -0.42\r\nvn -0.6 0.67 -0.44\r\nvn -0.6 0.65 -0.47\r\nvn -0.63 0.63 -0.45\r\nvn -0.63 0.65 -0.42\r\nvn -0.6 0.61 -0.51\r\nvn -0.63 0.6 -0.49\r\nvn -0.61 0.41 -0.68\r\nvn -0.64 0.39 -0.67\r\nvn -0.64 0.54 -0.55\r\nvn -0.61 0.56 -0.56\r\nvn -0.88 0.48 -0.06\r\nvn -0.87 0.48 -0.09\r\nvn -0.89 0.45 -0.1\r\nvn -0.89 0.45 -0.06\r\nvn -0.87 0.49 -0.12\r\nvn -0.88 0.45 -0.13\r\nvn -0.9 0.42 -0.15\r\nvn -0.9 0.41 -0.11\r\nvn -0.91 0.41 -0.08\r\nvn -0.84 0.51 -0.19\r\nvn -0.85 0.48 -0.21\r\nvn -0.87 0.46 -0.17\r\nvn -0.85 0.5 -0.16\r\nvn -0.87 0.44 -0.22\r\nvn -0.89 0.43 -0.19\r\nvn -0.93 0.19 -0.33\r\nvn -0.94 0.17 -0.28\r\nvn -0.91 0.36 -0.22\r\nvn -0.89 0.37 -0.26\r\nvn -0.96 0.16 -0.23\r\nvn -0.92 0.35 -0.17\r\nvn -0.98 0.16 -0.13\r\nvn -0.94 0.34 -0.09\r\nvn -0.93 0.34 -0.13\r\nvn -0.97 0.16 -0.18\r\nvn -0.88 0.39 -0.29\r\nvn -0.91 0.2 -0.37\r\nvn -0.85 0.46 -0.25\r\nvn -0.84 0.47 -0.28\r\nvn -0.86 0.4 -0.32\r\nvn -0.89 0.22 -0.4\r\nvn -0.82 0.53 -0.22\r\nvn -0.84 0.5 -0.23\r\nvn -0.8 0.54 -0.24\r\nvn -0.82 0.51 -0.26\r\nvn -0.8 0.53 -0.28\r\nvn -0.79 0.56 -0.26\r\nvn -0.82 0.49 -0.3\r\nvn -0.87 0.24 -0.44\r\nvn -0.84 0.42 -0.34\r\nvn -0.94 0.34 0.01\r\nvn -0.99 0.16 -0.01\r\nvn -0.91 0.41 0.01\r\nvn -0.91 0.41 -0.01\r\nvn -0.94 0.34 -0.02\r\nvn -0.99 0.16 -0.05\r\nvn -0.88 0.47 0.02\r\nvn -0.9 0.44 0.02\r\nvn -0.88 0.48 -0.01\r\nvn -0.9 0.45 -0.01\r\nvn -0.89 0.45 -0.04\r\nvn -0.88 0.48 -0.03\r\nvn -0.91 0.41 -0.04\r\nvn -0.98 0.16 -0.09\r\nvn -0.94 0.34 -0.06\r\nvn -0.48 0.12 0.87\r\nvn -0.49 0.29 0.82\r\nvn -0.54 0.28 0.79\r\nvn -0.54 0.11 0.84\r\nvn -0.49 0.35 0.8\r\nvn -0.53 0.34 0.78\r\nvn -0.57 0.32 0.75\r\nvn -0.58 0.26 0.77\r\nvn -0.58 0.1 0.81\r\nvn -0.48 0.41 0.77\r\nvn -0.52 0.4 0.75\r\nvn -0.53 0.37 0.76\r\nvn -0.49 0.39 0.79\r\nvn -0.55 0.39 0.74\r\nvn -0.56 0.36 0.75\r\nvn -0.59 0.35 0.72\r\nvn -0.6 0.32 0.73\r\nvn -0.59 0.34 0.73\r\nvn -0.58 0.37 0.73\r\nvn -0.61 0.29 0.73\r\nvn -0.6 0.3 0.74\r\nvn -0.62 0.06 0.78\r\nvn -0.61 0.08 0.79\r\nvn -0.61 0.24 0.75\r\nvn -0.62 0.22 0.75\r\nvn -0.29 0.11 0.95\r\nvn -0.3 0.28 0.91\r\nvn -0.35 0.29 0.89\r\nvn -0.33 0.11 0.94\r\nvn -0.3 0.35 0.89\r\nvn -0.35 0.35 0.87\r\nvn -0.4 0.35 0.85\r\nvn -0.39 0.29 0.87\r\nvn -0.38 0.12 0.92\r\nvn -0.3 0.41 0.86\r\nvn -0.36 0.41 0.84\r\nvn -0.36 0.38 0.85\r\nvn -0.3 0.38 0.87\r\nvn -0.4 0.41 0.82\r\nvn -0.4 0.39 0.83\r\nvn -0.44 0.39 0.81\r\nvn -0.44 0.42 0.79\r\nvn -0.44 0.35 0.82\r\nvn -0.43 0.13 0.89\r\nvn -0.44 0.29 0.85\r\nvn -0.32 0.89 -0.32\r\nvn -0.34 0.8 -0.5\r\nvn -0.31 0.92 -0.24\r\nvn -0.23 0.95 -0.23\r\nvn -0.24 0.92 -0.31\r\nvn -0.25 0.84 -0.49\r\nvn -0.29 0.94 -0.16\r\nvn -0.3 0.93 -0.2\r\nvn -0.22 0.96 -0.15\r\nvn -0.22 0.96 -0.19\r\nvn -0.12 0.98 -0.18\r\nvn -0.12 0.98 -0.14\r\nvn -0.13 0.97 -0.22\r\nvn -0.14 0.87 -0.48\r\nvn -0.13 0.95 -0.3\r\nvn 0.79 -0.38 -0.47\r\nvn 0.78 -0.37 -0.51\r\nvn 0.77 -0.4 -0.5\r\nvn 0.78 -0.41 -0.47\r\nvn 0.76 -0.36 -0.54\r\nvn 0.75 -0.39 -0.53\r\nvn 0.74 -0.43 -0.52\r\nvn 0.75 -0.44 -0.49\r\nvn 0.77 -0.45 -0.46\r\nvn 0.75 -0.34 -0.57\r\nvn 0.74 -0.37 -0.56\r\nvn 0.74 -0.38 -0.55\r\nvn 0.75 -0.35 -0.56\r\nvn 0.72 -0.41 -0.56\r\nvn 0.73 -0.42 -0.54\r\nvn 0.65 -0.59 -0.47\r\nvn 0.66 -0.6 -0.45\r\nvn 0.71 -0.47 -0.52\r\nvn 0.7 -0.47 -0.54\r\nvn 0.66 -0.61 -0.43\r\nvn 0.72 -0.48 -0.5\r\nvn 0.68 -0.63 -0.38\r\nvn 0.75 -0.5 -0.44\r\nvn 0.73 -0.49 -0.47\r\nvn 0.67 -0.62 -0.41\r\nvn 0.86 -0.41 -0.3\r\nvn 0.85 -0.41 -0.34\r\nvn 0.83 -0.44 -0.34\r\nvn 0.84 -0.44 -0.3\r\nvn 0.83 -0.4 -0.39\r\nvn 0.82 -0.43 -0.38\r\nvn 0.8 -0.46 -0.37\r\nvn 0.82 -0.47 -0.33\r\nvn 0.83 -0.48 -0.29\r\nvn 0.8 -0.42 -0.42\r\nvn 0.81 -0.39 -0.43\r\nvn 0.79 -0.45 -0.41\r\nvn 0.69 -0.64 -0.34\r\nvn 0.76 -0.51 -0.4\r\nvn 0.7 -0.65 -0.31\r\nvn 0.78 -0.52 -0.36\r\nvn 0.71 -0.66 -0.24\r\nvn 0.8 -0.54 -0.28\r\nvn 0.79 -0.53 -0.32\r\nvn 0.71 -0.65 -0.27\r\nvn 0.89 -0.43 -0.16\r\nvn 0.88 -0.43 -0.19\r\nvn 0.87 -0.46 -0.19\r\nvn 0.87 -0.46 -0.16\r\nvn 0.88 -0.42 -0.23\r\nvn 0.86 -0.45 -0.22\r\nvn 0.84 -0.49 -0.22\r\nvn 0.85 -0.49 -0.19\r\nvn 0.85 -0.49 -0.16\r\nvn 0.85 -0.45 -0.26\r\nvn 0.87 -0.42 -0.26\r\nvn 0.84 -0.49 -0.25\r\nvn 0.72 -0.67 -0.21\r\nvn 0.8 -0.54 -0.24\r\nvn 0.72 -0.67 -0.18\r\nvn 0.81 -0.55 -0.21\r\nvn 0.72 -0.68 -0.14\r\nvn 0.82 -0.55 -0.15\r\nvn 0.82 -0.55 -0.18\r\nvn 0.72 -0.67 -0.15\r\nvn -0.95 -0.32 0\r\nvn -0.95 -0.32 0.05\r\nvn -0.35 -0.93 -0.05\r\nvn -0.35 -0.94 -0.02\r\nvn -0.35 -0.94 0.02\r\nvn -0.94 -0.32 0.1\r\nvn 0.71 -0.7 -0.06\r\nvn 0.72 -0.69 -0.08\r\nvn 0.43 -0.9 -0.07\r\nvn 0.42 -0.91 -0.07\r\nvn 0.72 -0.69 -0.1\r\nvn 0.43 -0.9 -0.07\r\nvn 0.44 -0.89 -0.08\r\nvn 0.44 -0.9 -0.08\r\nvn 0.72 -0.68 -0.12\r\nvn -0.33 -0.94 0.08\r\nvn -0.34 -0.94 0.05\r\nvn -0.94 -0.32 0.15\r\nvn -0.93 -0.32 0.2\r\nvn -0.75 -0.13 -0.65\r\nvn -0.77 -0.16 -0.62\r\nvn -0.73 -0.1 -0.68\r\nvn -0.33 -0.68 -0.65\r\nvn -0.33 -0.71 -0.62\r\nvn -0.32 -0.75 -0.58\r\nvn -0.69 -0.06 -0.73\r\nvn -0.71 -0.08 -0.7\r\nvn -0.32 -0.63 -0.71\r\nvn -0.33 -0.65 -0.68\r\nvn 0.55 -0.84 0.04\r\nvn 0.55 -0.83 0.04\r\nvn 0.23 -0.92 -0.31\r\nvn 0.23 -0.92 -0.33\r\nvn 0.56 -0.83 0.05\r\nvn 0.25 -0.93 -0.28\r\nvn 0.6 -0.8 0.06\r\nvn 0.3 -0.93 -0.22\r\nvn 0.27 -0.93 -0.25\r\nvn 0.58 -0.81 0.06\r\nvn 0.29 -0.94 -0.19\r\nvn 0.33 -0.94 -0.01\r\nvn 0.36 -0.93 0.01\r\nvn 0.31 -0.93 -0.17\r\nvn 0.34 -0.94 0.07\r\nvn 0.37 -0.92 0.09\r\nvn 0.4 -0.91 0.13\r\nvn 0.39 -0.92 0.05\r\nvn 0.34 -0.93 -0.14\r\nvn 0.34 -0.93 0.16\r\nvn 0.37 -0.91 0.18\r\nvn 0.37 -0.92 0.14\r\nvn 0.34 -0.93 0.12\r\nvn 0.39 -0.89 0.22\r\nvn 0.4 -0.9 0.18\r\nvn 0.44 -0.84 0.32\r\nvn 0.45 -0.85 0.27\r\nvn 0.42 -0.88 0.22\r\nvn 0.42 -0.87 0.26\r\nvn 0.46 -0.86 0.22\r\nvn 0.43 -0.89 0.17\r\nvn 0.39 -0.92 -0.06\r\nvn 0.37 -0.93 -0.1\r\nvn 0.42 -0.9 0.09\r\nvn 0.45 -0.88 0.13\r\nvn -0.48 0.2 -0.85\r\nvn -0.51 0.15 -0.85\r\nvn -0.56 0.53 -0.63\r\nvn -0.19 -0.46 -0.87\r\nvn -0.21 -0.49 -0.85\r\nvn -0.22 -0.51 -0.83\r\nvn -0.53 0.11 -0.84\r\nvn -0.58 0.49 -0.65\r\nvn 0.41 -0.91 -0.02\r\nvn 0.2 -0.88 -0.42\r\nvn 0.2 -0.87 -0.44\r\nvn 0.43 -0.9 0.01\r\nvn 0.21 -0.89 -0.4\r\nvn 0.44 -0.89 0.08\r\nvn 0.2 -0.91 -0.35\r\nvn 0.21 -0.9 -0.38\r\nvn 0.45 -0.89 0.05\r\nvn -0.24 -0.54 -0.8\r\nvn -0.23 -0.53 -0.82\r\nvn -0.6 0.43 -0.68\r\nvn -0.6 0.45 -0.66\r\nvn -0.54 0.08 -0.84\r\nvn -0.54 0.06 -0.84\r\nvn -0.91 -0.31 -0.26\r\nvn -0.93 -0.32 -0.2\r\nvn -0.9 -0.3 -0.32\r\nvn -0.34 -0.89 -0.3\r\nvn -0.35 -0.9 -0.25\r\nvn -0.35 -0.91 -0.21\r\nvn -0.86 -0.26 -0.44\r\nvn -0.88 -0.28 -0.38\r\nvn -0.33 -0.85 -0.41\r\nvn -0.33 -0.87 -0.36\r\nvn 0.67 -0.74 0.05\r\nvn 0.68 -0.73 0.04\r\nvn 0.38 -0.92 -0.12\r\nvn 0.37 -0.92 -0.14\r\nvn 0.69 -0.72 0.03\r\nvn 0.38 -0.92 -0.1\r\nvn 0.7 -0.72 0\r\nvn 0.39 -0.92 -0.08\r\nvn 0.39 -0.92 -0.09\r\nvn 0.69 -0.72 0.01\r\nvn -0.79 -0.19 -0.58\r\nvn -0.32 -0.78 -0.54\r\nvn -0.31 -0.81 -0.5\r\nvn -0.81 -0.21 -0.54\r\nvn 0.62 -0.78 0.06\r\nvn 0.32 -0.93 -0.2\r\nvn 0.64 -0.77 0.06\r\nvn 0.34 -0.92 -0.18\r\nvn 0.36 -0.92 -0.16\r\nvn 0.66 -0.75 0.05\r\nvn -0.32 -0.83 -0.46\r\nvn -0.84 -0.24 -0.5\r\nvn 0.81 -0.59 0.04\r\nvn 0.81 -0.59 0.02\r\nvn 0.7 -0.71 -0.01\r\nvn 0.85 -0.53 0.05\r\nvn 0.85 -0.53 0.03\r\nvn 0.85 -0.52 0\r\nvn 0.81 -0.58 -0.01\r\nvn 0.71 -0.71 -0.03\r\nvn 0.89 -0.46 0.06\r\nvn 0.89 -0.46 0.04\r\nvn 0.87 -0.49 0.03\r\nvn 0.87 -0.49 0.06\r\nvn 0.89 -0.46 0.01\r\nvn 0.87 -0.49 0.01\r\nvn 0.89 -0.45 -0.04\r\nvn 0.88 -0.48 -0.04\r\nvn 0.87 -0.48 -0.02\r\nvn 0.89 -0.45 -0.01\r\nvn 0.86 -0.52 -0.05\r\nvn 0.85 -0.52 -0.02\r\nvn 0.71 -0.7 -0.04\r\nvn 0.81 -0.58 -0.03\r\nvn 0.82 -0.57 -0.05\r\nvn 0.75 -0.33 -0.58\r\nvn 0.73 -0.36 -0.58\r\nvn 0.73 -0.32 -0.6\r\nvn 0.72 -0.35 -0.6\r\nvn 0.7 -0.39 -0.6\r\nvn 0.72 -0.4 -0.57\r\nvn 0.67 -0.31 -0.67\r\nvn 0.66 -0.34 -0.67\r\nvn 0.69 -0.34 -0.63\r\nvn 0.71 -0.31 -0.63\r\nvn 0.65 -0.38 -0.66\r\nvn 0.68 -0.38 -0.63\r\nvn 0.6 -0.57 -0.56\r\nvn 0.61 -0.57 -0.55\r\nvn 0.65 -0.44 -0.61\r\nvn 0.64 -0.44 -0.64\r\nvn 0.63 -0.58 -0.52\r\nvn 0.68 -0.45 -0.58\r\nvn 0.7 -0.46 -0.56\r\nvn 0.65 -0.58 -0.49\r\nvn 0.48 -0.39 -0.78\r\nvn 0.44 -0.39 -0.81\r\nvn 0.44 -0.42 -0.79\r\nvn 0.48 -0.42 -0.77\r\nvn 0.4 -0.39 -0.83\r\nvn 0.4 -0.42 -0.81\r\nvn 0.39 -0.46 -0.8\r\nvn 0.44 -0.46 -0.77\r\nvn 0.48 -0.45 -0.75\r\nvn 0.3 -0.39 -0.87\r\nvn 0.3 -0.41 -0.86\r\nvn 0.35 -0.42 -0.84\r\nvn 0.36 -0.39 -0.85\r\nvn 0.3 -0.45 -0.84\r\nvn 0.35 -0.45 -0.82\r\nvn 0.29 -0.63 -0.72\r\nvn 0.33 -0.63 -0.7\r\nvn 0.35 -0.51 -0.79\r\nvn 0.3 -0.51 -0.81\r\nvn 0.37 -0.63 -0.68\r\nvn 0.39 -0.51 -0.77\r\nvn 0.46 -0.63 -0.63\r\nvn 0.48 -0.51 -0.72\r\nvn 0.43 -0.51 -0.74\r\nvn 0.41 -0.63 -0.65\r\nvn 0.24 -0.39 -0.89\r\nvn 0.24 -0.42 -0.88\r\nvn 0.17 -0.4 -0.9\r\nvn 0.17 -0.42 -0.89\r\nvn 0.17 -0.46 -0.87\r\nvn 0.24 -0.45 -0.86\r\nvn 0.09 -0.43 -0.9\r\nvn 0.09 -0.4 -0.91\r\nvn 0.09 -0.47 -0.88\r\nvn 0.09 -0.65 -0.76\r\nvn 0.09 -0.52 -0.85\r\nvn 0.17 -0.64 -0.75\r\nvn 0.17 -0.52 -0.84\r\nvn 0.24 -0.51 -0.83\r\nvn 0.24 -0.63 -0.74\r\nvn 0.47 -0.86 -0.22\r\nvn 0.47 -0.86 -0.19\r\nvn 0.47 -0.85 -0.24\r\nvn -0.14 -0.95 0.29\r\nvn -0.17 -0.94 0.29\r\nvn -0.2 -0.94 0.28\r\nvn 0.48 -0.83 -0.27\r\nvn 0.48 -0.84 -0.25\r\nvn -0.1 -0.95 0.29\r\nvn -0.12 -0.95 0.29\r\nvn -0.67 -0.35 0.66\r\nvn -0.65 -0.36 0.67\r\nvn -0.69 -0.35 0.63\r\nvn -0.75 -0.33 0.57\r\nvn -0.72 -0.34 0.61\r\nvn -0.87 -0.31 0.39\r\nvn -0.84 -0.32 0.43\r\nvn -0.29 -0.94 0.2\r\nvn -0.27 -0.94 0.22\r\nvn -0.25 -0.94 0.24\r\nvn -0.82 -0.32 0.48\r\nvn 0.46 -0.88 -0.13\r\nvn 0.45 -0.88 -0.11\r\nvn 0.46 -0.88 -0.15\r\nvn 0.46 -0.87 -0.17\r\nvn -0.22 -0.94 0.26\r\nvn -0.78 -0.33 0.53\r\nvn -0.92 -0.31 0.24\r\nvn -0.33 -0.94 0.11\r\nvn -0.32 -0.94 0.14\r\nvn -0.91 -0.31 0.29\r\nvn 0.45 -0.89 -0.08\r\nvn 0.45 -0.89 -0.09\r\nvn 0.45 -0.89 -0.1\r\nvn -0.31 -0.94 0.17\r\nvn -0.89 -0.31 0.34\r\nvn 0.88 -0.46 -0.13\r\nvn 0.89 -0.43 -0.13\r\nvn 0.86 -0.5 -0.13\r\nvn 0.86 -0.5 -0.1\r\nvn 0.88 -0.47 -0.1\r\nvn 0.89 -0.44 -0.09\r\nvn 0.82 -0.56 -0.13\r\nvn 0.82 -0.56 -0.1\r\nvn 0.82 -0.57 -0.08\r\nvn 0.86 -0.51 -0.07\r\nvn 0.89 -0.44 -0.06\r\nvn 0.88 -0.47 -0.07\r\nvn 0.78 -0.55 0.3\r\nvn 0.76 -0.59 0.27\r\nvn 0.75 -0.6 0.29\r\nvn 0.76 -0.56 0.32\r\nvn 0.74 -0.62 0.24\r\nvn 0.73 -0.64 0.26\r\nvn 0.71 -0.65 0.27\r\nvn 0.73 -0.61 0.31\r\nvn 0.75 -0.56 0.34\r\nvn 0.69 -0.7 0.2\r\nvn 0.71 -0.68 0.19\r\nvn 0.67 -0.71 0.21\r\nvn 0.66 -0.72 0.22\r\nvn 0.66 -0.72 0.21\r\nvn 0.69 -0.66 0.29\r\nvn 0.7 -0.65 0.28\r\nvn 0.73 -0.57 0.38\r\nvn 0.74 -0.57 0.36\r\nvn 0.72 -0.61 0.32\r\nvn 0.71 -0.62 0.33\r\nvn 0.17 -0.83 -0.53\r\nvn 0.15 -0.82 -0.54\r\nvn 0.18 -0.85 -0.5\r\nvn -0.16 -0.38 -0.91\r\nvn -0.15 -0.34 -0.93\r\nvn -0.14 -0.3 -0.94\r\nvn 0.19 -0.86 -0.47\r\nvn -0.17 -0.42 -0.89\r\nvn -0.46 0.26 -0.85\r\nvn -0.43 0.32 -0.84\r\nvn -0.36 0.42 -0.83\r\nvn -0.4 0.37 -0.84\r\nvn 0.7 -0.63 0.34\r\nvn 0.71 -0.58 0.39\r\nvn 0.68 -0.67 0.3\r\nvn 0.66 -0.68 0.31\r\nvn 0.68 -0.64 0.36\r\nvn 0.69 -0.6 0.41\r\nvn 0.55 -0.83 0.04\r\nvn 0.65 -0.73 0.22\r\nvn 0.55 -0.83 0.05\r\nvn 0.64 -0.74 0.23\r\nvn 0.48 -0.87 0.11\r\nvn 0.55 -0.78 0.29\r\nvn 0.6 -0.75 0.25\r\nvn 0.53 -0.84 0.07\r\nvn 0.58 -0.73 0.36\r\nvn 0.63 -0.7 0.33\r\nvn 0.6 -0.65 0.47\r\nvn 0.64 -0.62 0.44\r\nvn 0.64 -0.67 0.39\r\nvn 0.59 -0.69 0.41\r\nvn 0.87 -0.49 0.09\r\nvn 0.88 -0.46 0.09\r\nvn 0.84 -0.53 0.08\r\nvn 0.84 -0.54 0.11\r\nvn 0.86 -0.5 0.12\r\nvn 0.88 -0.46 0.13\r\nvn 0.8 -0.59 0.06\r\nvn 0.8 -0.6 0.09\r\nvn 0.78 -0.62 0.13\r\nvn 0.79 -0.61 0.11\r\nvn 0.81 -0.56 0.17\r\nvn 0.83 -0.54 0.14\r\nvn 0.85 -0.49 0.21\r\nvn 0.86 -0.47 0.17\r\nvn 0.85 -0.51 0.16\r\nvn 0.83 -0.52 0.19\r\nvn 0.82 -0.54 0.21\r\nvn 0.83 -0.51 0.23\r\nvn 0.8 -0.57 0.19\r\nvn 0.78 -0.59 0.21\r\nvn 0.8 -0.55 0.23\r\nvn 0.81 -0.52 0.25\r\nvn 0.76 -0.63 0.15\r\nvn 0.74 -0.65 0.16\r\nvn 0.73 -0.67 0.17\r\nvn 0.76 -0.61 0.22\r\nvn 0.8 -0.54 0.28\r\nvn 0.78 -0.57 0.25\r\nvn -0.94 -0.32 -0.15\r\nvn -0.35 -0.92 -0.17\r\nvn -0.35 -0.93 -0.13\r\nvn -0.94 -0.32 -0.1\r\nvn 0.4 -0.91 -0.08\r\nvn 0.41 -0.91 -0.07\r\nvn 0.41 -0.91 -0.07\r\nvn -0.35 -0.93 -0.09\r\nvn -0.95 -0.32 -0.05\r\nvn 0.48 -0.83 -0.28\r\nvn 0.48 -0.83 -0.3\r\nvn -0.06 -0.95 0.31\r\nvn -0.08 -0.95 0.3\r\nvn 0.47 -0.82 -0.32\r\nvn 0.47 -0.83 -0.31\r\nvn -0.02 -0.94 0.33\r\nvn -0.04 -0.94 0.33\r\nvn -0.63 0.05 0.77\r\nvn -0.66 0.05 0.75\r\nvn -0.54 -0.37 0.75\r\nvn -0.52 -0.38 0.77\r\nvn -0.69 0.06 0.72\r\nvn -0.58 -0.37 0.72\r\nvn -0.62 -0.37 0.69\r\nvn -0.72 0.07 0.69\r\nvn 0.34 -0.85 -0.4\r\nvn 0.37 -0.85 -0.37\r\nvn 0.31 -0.85 -0.42\r\nvn 0.03 -0.95 0.32\r\nvn 0.02 -0.94 0.34\r\nvn 0 -0.94 0.35\r\nvn 0.25 -0.85 -0.46\r\nvn 0.28 -0.85 -0.44\r\nvn 0.04 -0.96 0.29\r\nvn 0.04 -0.95 0.31\r\nvn -0.25 -0.36 0.9\r\nvn -0.21 -0.37 0.91\r\nvn -0.29 -0.34 0.89\r\nvn -0.39 -0.33 0.86\r\nvn -0.34 -0.33 0.88\r\nvn -0.17 -0.37 0.92\r\nvn -0.23 0.11 0.97\r\nvn 0.04 -0.96 0.28\r\nvn 0.03 -0.96 0.28\r\nvn -0.12 -0.36 0.92\r\nvn -0.17 0.12 0.98\r\nvn 0.2 -0.85 -0.48\r\nvn 0.15 -0.86 -0.49\r\nvn 0.08 -0.87 -0.49\r\nvn 0.02 -0.96 0.28\r\nvn -0.09 0.12 0.99\r\nvn -0.06 -0.36 0.93\r\nvn -0.09 0.3 0.95\r\nvn -0.09 0.36 0.93\r\nvn -0.17 0.36 0.92\r\nvn -0.17 0.29 0.94\r\nvn -0.09 0.43 0.9\r\nvn -0.09 0.4 0.91\r\nvn -0.17 0.42 0.89\r\nvn -0.17 0.39 0.9\r\nvn -0.24 0.38 0.89\r\nvn -0.24 0.41 0.88\r\nvn -0.24 0.35 0.91\r\nvn -0.24 0.29 0.93\r\nvn 0.25 -0.95 -0.2\r\nvn 0.29 -0.96 -0.02\r\nvn 0.19 -0.96 -0.2\r\nvn 0.22 -0.98 -0.02\r\nvn 0.23 -0.97 0.06\r\nvn 0.3 -0.95 0.06\r\nvn 0.12 -0.99 -0.02\r\nvn 0.11 -0.98 -0.19\r\nvn 0.13 -0.99 0.06\r\nvn 0.14 -0.98 0.14\r\nvn 0.13 -0.99 0.11\r\nvn 0.24 -0.96 0.14\r\nvn 0.23 -0.97 0.1\r\nvn 0.3 -0.95 0.11\r\nvn 0.31 -0.94 0.14\r\nvn -0.31 0.47 -0.83\r\nvn -0.23 0.51 -0.83\r\nvn -0.08 -0.25 -0.97\r\nvn -0.11 -0.27 -0.96\r\nvn -0.12 0.54 -0.83\r\nvn -0.04 -0.23 -0.97\r\nvn 0.06 -0.83 -0.55\r\nvn 0.11 -0.83 -0.55\r\nvn 0.14 -0.82 -0.55\r\nvn 0.73 0.39 0.56\r\nvn 0.73 0.38 0.57\r\nvn 0.75 0.39 0.54\r\nvn 0.69 0.57 0.45\r\nvn 0.68 0.57 0.47\r\nvn 0.67 0.56 0.48\r\nvn 0.79 0.4 0.47\r\nvn 0.77 0.39 0.51\r\nvn 0.72 0.58 0.38\r\nvn 0.7 0.58 0.42\r\nvn -0.26 0.91 -0.33\r\nvn -0.23 0.91 -0.35\r\nvn 0.24 0.97 -0\r\nvn 0.23 0.97 -0.01\r\nvn -0.2 0.91 -0.36\r\nvn 0.25 0.97 0\r\nvn -0.17 0.91 -0.37\r\nvn 0.26 0.96 0.02\r\nvn 0.26 0.97 0.01\r\nvn -0.19 0.91 -0.37\r\nvn 0.81 0.41 0.42\r\nvn 0.83 0.42 0.37\r\nvn 0.74 0.6 0.3\r\nvn 0.73 0.59 0.34\r\nvn 0.85 0.44 0.29\r\nvn 0.84 0.43 0.33\r\nvn 0.75 0.62 0.23\r\nvn 0.75 0.61 0.26\r\nvn -0.37 0.9 -0.22\r\nvn -0.35 0.9 -0.25\r\nvn 0.19 0.98 -0.02\r\nvn 0.17 0.98 -0.02\r\nvn -0.32 0.91 -0.28\r\nvn 0.2 0.98 -0.02\r\nvn 0.22 0.98 -0.02\r\nvn -0.29 0.91 -0.31\r\nvn 0.86 0.45 0.26\r\nvn 0.86 0.45 0.22\r\nvn 0.76 0.63 0.18\r\nvn 0.75 0.63 0.21\r\nvn 0.88 0.45 0.16\r\nvn 0.87 0.45 0.19\r\nvn 0.76 0.63 0.13\r\nvn 0.76 0.63 0.16\r\nvn -0.43 0.89 -0.12\r\nvn -0.42 0.9 -0.14\r\nvn 0.14 0.99 -0.01\r\nvn 0.14 0.99 -0.01\r\nvn -0.41 0.9 -0.17\r\nvn 0.15 0.99 -0.02\r\nvn 0.16 0.99 -0.02\r\nvn -0.39 0.9 -0.2\r\nvn 0.88 0.46 0.12\r\nvn 0.88 0.46 0.09\r\nvn 0.76 0.64 0.07\r\nvn 0.77 0.64 0.1\r\nvn 0.88 0.48 0.03\r\nvn 0.88 0.47 0.06\r\nvn 0.75 0.66 0.03\r\nvn 0.76 0.65 0.05\r\nvn -0.47 0.88 0.02\r\nvn -0.46 0.89 -0.01\r\nvn 0.11 0.99 0.02\r\nvn 0.1 0.99 0.03\r\nvn -0.46 0.89 -0.05\r\nvn 0.12 0.99 0\r\nvn 0.13 0.99 -0\r\nvn -0.45 0.89 -0.09\r\nvn 0.74 0.61 -0.3\r\nvn 0.76 0.58 -0.28\r\nvn 0.71 0.63 -0.3\r\nvn 0.6 0.79 -0.12\r\nvn 0.62 0.77 -0.13\r\nvn 0.65 0.75 -0.12\r\nvn 0.67 0.67 -0.31\r\nvn 0.69 0.66 -0.3\r\nvn 0.57 0.81 -0.12\r\nvn 0.58 0.8 -0.12\r\nvn -0.26 0.68 0.68\r\nvn -0.28 0.69 0.67\r\nvn 0.12 0.91 0.4\r\nvn 0.12 0.9 0.41\r\nvn -0.3 0.71 0.64\r\nvn 0.12 0.92 0.38\r\nvn -0.34 0.75 0.57\r\nvn 0.11 0.94 0.33\r\nvn 0.11 0.93 0.36\r\nvn -0.32 0.72 0.61\r\nvn 0.4 0.9 -0.19\r\nvn 0.41 0.88 -0.23\r\nvn 0.39 0.91 -0.15\r\nvn 0.33 0.94 0.08\r\nvn 0.34 0.94 0.05\r\nvn 0.33 0.94 -0\r\nvn 0.33 0.94 -0.11\r\nvn 0.37 0.92 -0.13\r\nvn 0.29 0.95 0.12\r\nvn 0.32 0.94 0.11\r\nvn -0.14 0.18 0.97\r\nvn -0.16 0.23 0.96\r\nvn 0.06 0.68 0.73\r\nvn 0.06 0.66 0.75\r\nvn -0.17 0.29 0.94\r\nvn 0.06 0.71 0.7\r\nvn -0.21 0.42 0.88\r\nvn 0.03 0.79 0.61\r\nvn 0.05 0.75 0.66\r\nvn -0.19 0.35 0.92\r\nvn 0.65 0.69 -0.31\r\nvn 0.63 0.7 -0.33\r\nvn 0.55 0.83 -0.12\r\nvn 0.56 0.82 -0.12\r\nvn 0.63 0.7 -0.33\r\nvn 0.62 0.71 -0.34\r\nvn 0.58 0.81 -0.11\r\nvn 0.56 0.82 -0.12\r\nvn -0.1 0.56 0.82\r\nvn -0.15 0.6 0.79\r\nvn 0.2 0.85 0.48\r\nvn 0.24 0.82 0.52\r\nvn -0.2 0.64 0.74\r\nvn 0.15 0.88 0.45\r\nvn 0.13 0.9 0.43\r\nvn -0.24 0.67 0.71\r\nvn 0.87 0.49 -0.08\r\nvn 0.87 0.49 -0.05\r\nvn 0.86 0.5 -0.11\r\nvn 0.73 0.68 -0.05\r\nvn 0.74 0.68 -0.03\r\nvn 0.74 0.67 -0.01\r\nvn 0.84 0.51 -0.18\r\nvn 0.85 0.5 -0.15\r\nvn 0.71 0.7 -0.08\r\nvn 0.72 0.69 -0.06\r\nvn -0.41 0.83 0.39\r\nvn -0.42 0.84 0.33\r\nvn 0.11 0.97 0.2\r\nvn 0.11 0.97 0.23\r\nvn -0.44 0.86 0.27\r\nvn 0.1 0.98 0.17\r\nvn -0.46 0.87 0.17\r\nvn 0.09 0.99 0.11\r\nvn 0.1 0.99 0.14\r\nvn -0.45 0.86 0.22\r\nvn 0.82 0.53 -0.21\r\nvn 0.81 0.54 -0.23\r\nvn 0.69 0.72 -0.1\r\nvn 0.7 0.71 -0.09\r\nvn 0.79 0.56 -0.26\r\nvn 0.67 0.73 -0.11\r\nvn -0.37 0.77 0.52\r\nvn 0.11 0.95 0.31\r\nvn -0.38 0.79 0.48\r\nvn 0.11 0.95 0.28\r\nvn 0.11 0.96 0.25\r\nvn -0.39 0.81 0.43\r\nvn 0.88 0.48 0.01\r\nvn 0.87 0.49 -0.01\r\nvn 0.74 0.67 0.01\r\nvn 0.75 0.66 0.02\r\nvn 0.87 0.49 -0.03\r\nvn 0.74 0.67 -0\r\nvn -0.47 0.87 0.13\r\nvn 0.09 0.99 0.09\r\nvn -0.47 0.88 0.09\r\nvn 0.09 0.99 0.07\r\nvn 0.09 0.99 0.05\r\nvn -0.47 0.88 0.06\r\nvn 0.51 0.45 0.74\r\nvn 0.47 0.46 0.75\r\nvn 0.54 0.43 0.73\r\nvn 0.51 0.6 0.62\r\nvn 0.48 0.61 0.63\r\nvn 0.45 0.62 0.64\r\nvn 0.57 0.39 0.72\r\nvn 0.56 0.41 0.72\r\nvn 0.54 0.57 0.62\r\nvn 0.53 0.58 0.62\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn 0.26 0.97 0.04\r\nvn 0.26 0.96 0.04\r\nvn -0.06 0.89 -0.45\r\nvn 0.25 0.97 0.04\r\nvn -0.04 0.89 -0.46\r\nvn 0.23 0.97 0.05\r\nvn 0.24 0.97 0.04\r\nvn -0.05 0.89 -0.46\r\nvn 0.36 0.46 0.81\r\nvn 0.3 0.46 0.84\r\nvn 0.4 0.46 0.79\r\nvn 0.39 0.62 0.68\r\nvn 0.35 0.62 0.7\r\nvn 0.3 0.62 0.73\r\nvn 0.44 0.46 0.77\r\nvn 0.42 0.63 0.66\r\nvn -0.02 0.89 -0.45\r\nvn 0.22 0.97 0.06\r\nvn -0.01 0.9 -0.44\r\nvn 0.21 0.97 0.08\r\nvn 0.01 0.9 -0.43\r\nvn 0.18 0.98 0.12\r\nvn 0.2 0.97 0.1\r\nvn 0 0.9 -0.44\r\nvn 0.28 0.95 -0.1\r\nvn 0.21 0.97 -0.08\r\nvn 0.18 0.97 0.14\r\nvn 0.25 0.96 0.14\r\nvn 0 1 -0.07\r\nvn 0.11 0.99 -0.08\r\nvn 0 0.99 0.15\r\nvn 0.1 0.98 0.15\r\nvn 0 0.09 1\r\nvn -0.04 0.1 0.99\r\nvn 0.03 0.63 0.77\r\nvn -0 0.63 0.77\r\nvn -0.08 0.11 0.99\r\nvn 0.05 0.63 0.77\r\nvn 0.06 0.64 0.77\r\nvn -0.11 0.14 0.98\r\nvn -0.78 -0.27 -0.57\r\nvn -0.81 -0.27 -0.52\r\nvn -0.76 -0.27 -0.6\r\nvn -0.76 -0.06 -0.65\r\nvn -0.79 -0.06 -0.61\r\nvn -0.82 -0.06 -0.57\r\nvn -0.74 -0.26 -0.62\r\nvn -0.74 -0.27 -0.61\r\nvn -0.74 -0.03 -0.67\r\nvn -0.74 -0.06 -0.67\r\nvn -0.14 0.93 -0.35\r\nvn -0.17 0.92 -0.37\r\nvn -0.51 0.6 -0.61\r\nvn -0.49 0.63 -0.6\r\nvn -0.19 0.91 -0.37\r\nvn -0.54 0.59 -0.6\r\nvn -0.25 0.91 -0.34\r\nvn -0.61 0.59 -0.54\r\nvn -0.57 0.59 -0.58\r\nvn -0.22 0.91 -0.36\r\nvn -0.89 -0.29 -0.36\r\nvn -0.9 -0.3 -0.32\r\nvn -0.87 -0.28 -0.41\r\nvn -0.89 -0.07 -0.45\r\nvn -0.91 -0.09 -0.4\r\nvn -0.93 -0.1 -0.35\r\nvn -0.84 -0.27 -0.46\r\nvn -0.86 -0.06 -0.51\r\nvn -0.29 0.91 -0.31\r\nvn -0.65 0.59 -0.48\r\nvn -0.32 0.9 -0.29\r\nvn -0.69 0.58 -0.43\r\nvn -0.37 0.9 -0.23\r\nvn -0.75 0.56 -0.34\r\nvn -0.72 0.57 -0.39\r\nvn -0.35 0.9 -0.26\r\nvn -0.93 -0.31 -0.21\r\nvn -0.93 -0.31 -0.17\r\nvn -0.92 -0.31 -0.25\r\nvn -0.96 -0.11 -0.27\r\nvn -0.97 -0.11 -0.23\r\nvn -0.98 -0.11 -0.19\r\nvn -0.91 -0.31 -0.28\r\nvn -0.95 -0.1 -0.31\r\nvn -0.39 0.9 -0.2\r\nvn -0.77 0.56 -0.3\r\nvn -0.41 0.9 -0.18\r\nvn -0.79 0.55 -0.26\r\nvn -0.44 0.89 -0.12\r\nvn -0.81 0.55 -0.18\r\nvn -0.8 0.55 -0.22\r\nvn -0.42 0.89 -0.15\r\nvn -0.38 -0.89 0.25\r\nvn -0.35 -0.91 0.22\r\nvn -0.4 -0.87 0.29\r\nvn -0.42 -0.75 0.5\r\nvn -0.4 -0.79 0.47\r\nvn -0.37 -0.82 0.44\r\nvn -0.45 -0.81 0.37\r\nvn -0.42 -0.84 0.33\r\nvn -0.48 -0.66 0.58\r\nvn -0.45 -0.71 0.54\r\nvn -0.26 0.33 0.91\r\nvn -0.23 0.31 0.92\r\nvn -0.38 -0.14 0.91\r\nvn -0.41 -0.09 0.91\r\nvn -0.2 0.27 0.94\r\nvn -0.36 -0.2 0.91\r\nvn -0.16 0.18 0.97\r\nvn -0.3 -0.31 0.9\r\nvn -0.33 -0.26 0.91\r\nvn -0.18 0.22 0.96\r\nvn -0.94 -0.34 0.05\r\nvn -0.94 -0.34 0.08\r\nvn -0.94 -0.34 0.03\r\nvn -0.99 -0.13 0.05\r\nvn -0.99 -0.13 0.08\r\nvn -0.99 -0.12 0.12\r\nvn -0.94 -0.33 -0.03\r\nvn -0.94 -0.34 -0\r\nvn -0.99 -0.12 -0.02\r\nvn -0.99 -0.12 0.01\r\nvn -0.47 0.88 0.02\r\nvn -0.48 0.88 0.06\r\nvn -0.85 0.53 0.04\r\nvn -0.85 0.53 0\r\nvn -0.48 0.87 0.09\r\nvn -0.84 0.53 0.09\r\nvn -0.47 0.87 0.17\r\nvn -0.83 0.53 0.18\r\nvn -0.84 0.53 0.13\r\nvn -0.47 0.87 0.13\r\nvn -0.74 -0.25 -0.63\r\nvn -0.74 -0.23 -0.64\r\nvn -0.72 0.06 -0.69\r\nvn -0.73 0.01 -0.68\r\nvn -0.71 -0.19 -0.68\r\nvn -0.73 -0.2 -0.66\r\nvn -0.68 0.14 -0.72\r\nvn -0.7 0.11 -0.7\r\nvn -0.03 0.95 -0.3\r\nvn -0.05 0.95 -0.3\r\nvn -0.38 0.74 -0.55\r\nvn -0.33 0.76 -0.56\r\nvn -0.08 0.95 -0.31\r\nvn -0.42 0.71 -0.56\r\nvn -0.46 0.67 -0.58\r\nvn -0.11 0.94 -0.33\r\nvn -0.44 -0.34 -0.83\r\nvn -0.47 -0.33 -0.82\r\nvn -0.4 -0.34 -0.85\r\nvn -0.39 -0.14 -0.91\r\nvn -0.42 -0.14 -0.89\r\nvn -0.46 -0.13 -0.88\r\nvn -0.3 -0.33 -0.89\r\nvn -0.36 -0.33 -0.87\r\nvn -0.29 -0.13 -0.95\r\nvn -0.34 -0.14 -0.93\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn -0.18 0.55 -0.82\r\nvn -0.15 0.55 -0.82\r\nvn -0 0.89 -0.45\r\nvn -0.21 0.54 -0.81\r\nvn -0.03 0.89 -0.46\r\nvn -0.27 0.54 -0.8\r\nvn -0.24 0.54 -0.81\r\nvn -0.02 0.89 -0.46\r\nvn -0.24 -0.33 -0.91\r\nvn -0.17 -0.34 -0.93\r\nvn -0.16 -0.15 -0.98\r\nvn -0.22 -0.14 -0.96\r\nvn -0 -0.35 -0.94\r\nvn -0.09 -0.35 -0.93\r\nvn -0 -0.16 -0.99\r\nvn -0.08 -0.15 -0.98\r\nvn 0 0.9 -0.43\r\nvn 0.01 0.9 -0.43\r\nvn -0.04 0.54 -0.84\r\nvn -0 0.54 -0.84\r\nvn 0.02 0.9 -0.43\r\nvn -0.07 0.55 -0.83\r\nvn -0.11 0.55 -0.83\r\nvn 0.02 0.9 -0.43\r\nvn -0.94 -0.33 -0.06\r\nvn -0.94 -0.32 -0.09\r\nvn -0.99 -0.11 -0.1\r\nvn -0.99 -0.12 -0.05\r\nvn -0.94 -0.32 -0.13\r\nvn -0.98 -0.11 -0.15\r\nvn -0.45 0.89 -0.09\r\nvn -0.82 0.55 -0.14\r\nvn -0.46 0.89 -0.06\r\nvn -0.83 0.54 -0.09\r\nvn -0.84 0.54 -0.04\r\nvn -0.47 0.88 -0.02\r\nvn -0.74 -0.49 0.46\r\nvn -0.72 -0.51 0.48\r\nvn -0.76 -0.47 0.44\r\nvn -0.78 -0.22 0.59\r\nvn -0.75 -0.23 0.61\r\nvn -0.73 -0.24 0.64\r\nvn -0.82 -0.43 0.39\r\nvn -0.79 -0.45 0.42\r\nvn -0.84 -0.19 0.51\r\nvn -0.81 -0.21 0.55\r\nvn -0.34 0.75 0.56\r\nvn -0.31 0.73 0.6\r\nvn -0.61 0.41 0.68\r\nvn -0.65 0.42 0.63\r\nvn -0.3 0.71 0.64\r\nvn -0.57 0.39 0.72\r\nvn -0.34 0.62 0.71\r\nvn -0.55 0.34 0.76\r\nvn -0.56 0.37 0.74\r\nvn -0.3 0.68 0.67\r\nvn -0.47 -0.82 0.33\r\nvn -0.48 -0.78 0.41\r\nvn -0.5 -0.79 0.37\r\nvn -0.51 -0.74 0.44\r\nvn -0.54 -0.55 0.64\r\nvn -0.51 -0.6 0.62\r\nvn -0.54 -0.73 0.41\r\nvn -0.55 -0.67 0.49\r\nvn -0.53 -0.71 0.47\r\nvn -0.52 -0.76 0.39\r\nvn -0.57 -0.45 0.69\r\nvn -0.56 -0.49 0.67\r\nvn -0.39 0.3 0.87\r\nvn -0.38 0.31 0.87\r\nvn -0.48 0.02 0.87\r\nvn -0.49 0.05 0.87\r\nvn -0.35 0.33 0.88\r\nvn -0.47 -0 0.88\r\nvn -0.44 -0.04 0.9\r\nvn -0.31 0.34 0.89\r\nvn -0.91 -0.35 0.21\r\nvn -0.9 -0.36 0.25\r\nvn -0.92 -0.35 0.16\r\nvn -0.97 -0.13 0.22\r\nvn -0.95 -0.13 0.27\r\nvn -0.94 -0.14 0.32\r\nvn -0.93 -0.34 0.12\r\nvn -0.98 -0.12 0.16\r\nvn -0.46 0.86 0.22\r\nvn -0.82 0.53 0.23\r\nvn -0.44 0.85 0.27\r\nvn -0.8 0.52 0.3\r\nvn -0.41 0.83 0.39\r\nvn -0.76 0.49 0.43\r\nvn -0.78 0.51 0.36\r\nvn -0.43 0.84 0.33\r\nvn -0.84 -0.4 0.35\r\nvn -0.87 -0.39 0.32\r\nvn -0.9 -0.17 0.41\r\nvn -0.87 -0.18 0.46\r\nvn -0.88 -0.38 0.28\r\nvn -0.92 -0.15 0.37\r\nvn -0.4 0.81 0.43\r\nvn -0.74 0.47 0.48\r\nvn -0.39 0.79 0.48\r\nvn -0.71 0.46 0.53\r\nvn -0.69 0.44 0.58\r\nvn -0.37 0.77 0.52\r\nvn 0.09 0.47 0.88\r\nvn 0 0.48 0.88\r\nvn 0.17 0.47 0.87\r\nvn 0.17 0.63 0.76\r\nvn 0.09 0.63 0.77\r\nvn 0 0.64 0.77\r\nvn 0.24 0.46 0.85\r\nvn 0.24 0.62 0.75\r\nvn 0.01 0.9 -0.43\r\nvn 0.15 0.98 0.14\r\nvn 0.01 0.9 -0.43\r\nvn 0.11 0.98 0.15\r\nvn 0 0.9 -0.43\r\nvn 0 0.99 0.15\r\nvn 0.06 0.99 0.15\r\nvn 0.01 0.9 -0.43\r\nvn -0.14 -0.97 0.21\r\nvn -0 -0.98 0.21\r\nvn -0.24 -0.95 0.21\r\nvn -0.25 -0.87 0.42\r\nvn -0.14 -0.89 0.43\r\nvn -0 -0.9 0.44\r\nvn -0.31 -0.93 0.21\r\nvn -0.32 -0.85 0.43\r\nvn -0.12 0.14 0.98\r\nvn -0.25 -0.35 0.9\r\nvn -0.08 0.11 0.99\r\nvn -0.19 -0.38 0.91\r\nvn 0 0.08 1\r\nvn -0 -0.39 0.92\r\nvn -0.1 -0.4 0.91\r\nvn -0.04 0.09 0.99\r\nvn -0.76 0.44 0.48\r\nvn -0.77 0.45 0.45\r\nvn -0.74 0.44 0.51\r\nvn -0.68 0.61 0.42\r\nvn -0.68 0.61 0.39\r\nvn -0.69 0.62 0.36\r\nvn -0.73 0.42 0.54\r\nvn -0.74 0.43 0.53\r\nvn -0.68 0.59 0.44\r\nvn -0.68 0.6 0.43\r\nvn 0.17 0.91 -0.37\r\nvn 0.19 0.91 -0.37\r\nvn -0.25 0.97 -0.01\r\nvn -0.26 0.97 -0.01\r\nvn 0.21 0.91 -0.36\r\nvn -0.24 0.97 -0.01\r\nvn 0.26 0.91 -0.33\r\nvn -0.22 0.98 -0.02\r\nvn -0.23 0.97 -0.02\r\nvn 0.23 0.91 -0.35\r\nvn -0.82 0.48 0.32\r\nvn -0.83 0.49 0.28\r\nvn -0.8 0.47 0.36\r\nvn -0.71 0.64 0.29\r\nvn -0.72 0.65 0.26\r\nvn -0.72 0.65 0.23\r\nvn -0.79 0.46 0.4\r\nvn -0.7 0.63 0.33\r\nvn 0.29 0.91 -0.31\r\nvn -0.2 0.98 -0.03\r\nvn 0.32 0.91 -0.28\r\nvn -0.18 0.98 -0.03\r\nvn 0.37 0.9 -0.23\r\nvn -0.16 0.99 -0.03\r\nvn -0.17 0.99 -0.03\r\nvn 0.35 0.9 -0.25\r\nvn -0.85 0.5 0.18\r\nvn -0.85 0.5 0.15\r\nvn -0.84 0.5 0.21\r\nvn -0.73 0.66 0.17\r\nvn -0.73 0.66 0.14\r\nvn -0.74 0.67 0.12\r\nvn -0.83 0.49 0.25\r\nvn -0.73 0.66 0.2\r\nvn 0.39 0.9 -0.2\r\nvn -0.14 0.99 -0.02\r\nvn 0.41 0.9 -0.17\r\nvn -0.13 0.99 -0.02\r\nvn 0.43 0.89 -0.12\r\nvn -0.12 0.99 -0.01\r\nvn -0.13 0.99 -0.02\r\nvn 0.42 0.9 -0.14\r\nvn -0.85 0.52 0.06\r\nvn -0.85 0.52 0.04\r\nvn -0.86 0.51 0.09\r\nvn -0.73 0.68 0.07\r\nvn -0.73 0.68 0.05\r\nvn -0.72 0.69 0.04\r\nvn -0.86 0.5 0.12\r\nvn -0.74 0.67 0.1\r\nvn 0.45 0.89 -0.09\r\nvn -0.11 0.99 -0.01\r\nvn 0.46 0.89 -0.06\r\nvn -0.1 0.99 0\r\nvn 0.47 0.88 0.02\r\nvn -0.08 1 0.03\r\nvn -0.09 1 0.02\r\nvn 0.46 0.89 -0.02\r\nvn -0.71 0.65 -0.27\r\nvn -0.7 0.66 -0.28\r\nvn -0.72 0.65 -0.26\r\nvn -0.6 0.79 -0.08\r\nvn -0.6 0.79 -0.09\r\nvn -0.6 0.8 -0.09\r\nvn -0.74 0.63 -0.23\r\nvn -0.73 0.64 -0.24\r\nvn -0.62 0.78 -0.08\r\nvn -0.61 0.79 -0.08\r\nvn 0.35 0.75 0.57\r\nvn 0.32 0.73 0.6\r\nvn -0.1 0.92 0.38\r\nvn -0.09 0.93 0.35\r\nvn 0.3 0.71 0.64\r\nvn -0.11 0.91 0.4\r\nvn 0.26 0.68 0.69\r\nvn -0.14 0.89 0.43\r\nvn -0.12 0.9 0.41\r\nvn 0.28 0.7 0.66\r\nvn -0.37 0.92 -0.13\r\nvn -0.33 0.94 -0.11\r\nvn -0.39 0.91 -0.15\r\nvn -0.33 0.94 0.08\r\nvn -0.32 0.94 0.11\r\nvn -0.29 0.95 0.12\r\nvn -0.42 0.88 -0.22\r\nvn -0.41 0.89 -0.18\r\nvn -0.34 0.94 0.01\r\nvn -0.34 0.94 0.05\r\nvn 0.2 0.41 0.89\r\nvn 0.18 0.35 0.92\r\nvn -0.05 0.74 0.67\r\nvn -0.04 0.78 0.62\r\nvn 0.17 0.29 0.94\r\nvn -0.06 0.71 0.7\r\nvn 0.14 0.18 0.97\r\nvn -0.06 0.66 0.75\r\nvn -0.06 0.68 0.73\r\nvn 0.16 0.23 0.96\r\nvn -0.46 0.82 -0.34\r\nvn -0.43 0.86 -0.27\r\nvn -0.47 0.79 -0.38\r\nvn -0.44 0.84 -0.32\r\nvn -0.35 0.93 -0.1\r\nvn -0.34 0.94 -0.04\r\nvn -0.5 0.74 -0.45\r\nvn -0.47 0.79 -0.39\r\nvn -0.46 0.81 -0.36\r\nvn -0.49 0.76 -0.42\r\nvn -0.39 0.9 -0.19\r\nvn -0.37 0.92 -0.15\r\nvn 0.25 0.59 0.77\r\nvn 0.25 0.56 0.79\r\nvn -0.02 0.88 0.47\r\nvn -0.03 0.9 0.44\r\nvn 0.24 0.52 0.82\r\nvn -0.01 0.86 0.52\r\nvn -0.02 0.82 0.57\r\nvn 0.23 0.47 0.85\r\nvn -0.82 0.55 -0.14\r\nvn -0.81 0.56 -0.17\r\nvn -0.84 0.54 -0.1\r\nvn -0.7 0.71 -0.04\r\nvn -0.69 0.72 -0.05\r\nvn -0.68 0.73 -0.07\r\nvn -0.84 0.53 -0.04\r\nvn -0.84 0.54 -0.07\r\nvn -0.71 0.7 -0\r\nvn -0.71 0.71 -0.02\r\nvn 0.46 0.87 0.17\r\nvn 0.45 0.86 0.22\r\nvn -0.08 0.99 0.14\r\nvn -0.08 0.99 0.12\r\nvn 0.44 0.86 0.28\r\nvn -0.09 0.98 0.17\r\nvn 0.41 0.83 0.39\r\nvn -0.09 0.97 0.23\r\nvn -0.09 0.98 0.2\r\nvn 0.42 0.84 0.34\r\nvn -0.76 0.61 -0.22\r\nvn -0.77 0.6 -0.2\r\nvn -0.65 0.76 -0.08\r\nvn -0.63 0.77 -0.08\r\nvn -0.79 0.58 -0.19\r\nvn -0.66 0.74 -0.08\r\nvn 0.39 0.81 0.44\r\nvn -0.09 0.96 0.26\r\nvn 0.38 0.79 0.48\r\nvn -0.09 0.95 0.29\r\nvn -0.09 0.94 0.32\r\nvn 0.37 0.77 0.52\r\nvn -0.85 0.53 -0.02\r\nvn -0.85 0.53 -0\r\nvn -0.72 0.7 0.02\r\nvn -0.71 0.7 0.01\r\nvn -0.85 0.53 0.02\r\nvn -0.72 0.69 0.03\r\nvn 0.47 0.88 0.05\r\nvn -0.08 1 0.05\r\nvn 0.47 0.88 0.09\r\nvn -0.07 0.99 0.07\r\nvn -0.07 0.99 0.09\r\nvn 0.47 0.87 0.13\r\nvn -0.74 0.35 0.57\r\nvn -0.73 0.41 0.54\r\nvn -0.73 0.34 0.59\r\nvn -0.72 0.4 0.56\r\nvn -0.68 0.57 0.47\r\nvn -0.68 0.58 0.45\r\nvn -0.67 0.33 0.66\r\nvn -0.67 0.39 0.63\r\nvn -0.7 0.39 0.6\r\nvn -0.7 0.34 0.63\r\nvn -0.63 0.56 0.53\r\nvn -0.66 0.56 0.5\r\nvn 0.11 0.9 -0.42\r\nvn 0.13 0.91 -0.4\r\nvn -0.29 0.96 0.01\r\nvn -0.29 0.96 0.02\r\nvn 0.15 0.91 -0.39\r\nvn -0.29 0.96 -0\r\nvn -0.27 0.96 -0.01\r\nvn 0.16 0.91 -0.37\r\nvn -0.44 0.47 0.77\r\nvn -0.48 0.46 0.75\r\nvn -0.4 0.46 0.79\r\nvn -0.39 0.62 0.68\r\nvn -0.42 0.63 0.65\r\nvn -0.45 0.63 0.63\r\nvn -0.3 0.46 0.84\r\nvn -0.36 0.46 0.81\r\nvn -0.3 0.62 0.73\r\nvn -0.35 0.62 0.7\r\nvn -0.01 0.9 -0.43\r\nvn 0 0.9 -0.44\r\nvn -0.2 0.97 0.1\r\nvn -0.18 0.98 0.12\r\nvn 0.01 0.9 -0.44\r\nvn -0.21 0.97 0.08\r\nvn 0.04 0.89 -0.45\r\nvn -0.23 0.97 0.05\r\nvn -0.22 0.97 0.06\r\nvn 0.03 0.89 -0.45\r\nvn -0.11 0.99 -0.08\r\nvn -0.21 0.97 -0.08\r\nvn -0.18 0.97 0.14\r\nvn -0.1 0.98 0.15\r\nvn -0.28 0.95 -0.1\r\nvn -0.25 0.96 0.14\r\nvn 0.11 0.14 0.98\r\nvn -0.06 0.64 0.77\r\nvn 0.08 0.11 0.99\r\nvn -0.05 0.63 0.77\r\nvn -0.03 0.63 0.77\r\nvn 0.04 0.1 0.99\r\nvn 0.76 -0.29 -0.58\r\nvn 0.76 -0.28 -0.59\r\nvn 0.77 -0.3 -0.56\r\nvn 0.78 -0.1 -0.61\r\nvn 0.77 -0.09 -0.63\r\nvn 0.76 -0.08 -0.64\r\nvn 0.81 -0.32 -0.49\r\nvn 0.79 -0.31 -0.53\r\nvn 0.83 -0.11 -0.54\r\nvn 0.8 -0.11 -0.58\r\nvn 0.25 0.91 -0.34\r\nvn 0.22 0.91 -0.36\r\nvn 0.59 0.57 -0.57\r\nvn 0.63 0.57 -0.53\r\nvn 0.2 0.91 -0.37\r\nvn 0.57 0.57 -0.59\r\nvn 0.17 0.91 -0.37\r\nvn 0.54 0.58 -0.61\r\nvn 0.55 0.58 -0.6\r\nvn 0.18 0.91 -0.37\r\nvn 0.83 -0.33 -0.45\r\nvn 0.85 -0.34 -0.4\r\nvn 0.88 -0.13 -0.45\r\nvn 0.86 -0.12 -0.49\r\nvn 0.88 -0.36 -0.31\r\nvn 0.87 -0.35 -0.36\r\nvn 0.92 -0.15 -0.35\r\nvn 0.91 -0.14 -0.4\r\nvn 0.37 0.9 -0.23\r\nvn 0.34 0.9 -0.26\r\nvn 0.73 0.55 -0.4\r\nvn 0.76 0.54 -0.35\r\nvn 0.32 0.9 -0.29\r\nvn 0.7 0.55 -0.45\r\nvn 0.67 0.56 -0.49\r\nvn 0.29 0.91 -0.31\r\nvn 0.89 -0.36 -0.27\r\nvn 0.9 -0.37 -0.23\r\nvn 0.95 -0.16 -0.26\r\nvn 0.94 -0.16 -0.3\r\nvn 0.91 -0.37 -0.17\r\nvn 0.91 -0.37 -0.2\r\nvn 0.97 -0.17 -0.18\r\nvn 0.96 -0.16 -0.22\r\nvn 0.44 0.89 -0.12\r\nvn 0.42 0.89 -0.15\r\nvn 0.82 0.53 -0.22\r\nvn 0.83 0.53 -0.18\r\nvn 0.41 0.9 -0.17\r\nvn 0.8 0.53 -0.26\r\nvn 0.79 0.54 -0.31\r\nvn 0.39 0.9 -0.2\r\nvn 0.42 -0.84 0.34\r\nvn 0.45 -0.8 0.4\r\nvn 0.4 -0.87 0.29\r\nvn 0.42 -0.73 0.54\r\nvn 0.45 -0.68 0.58\r\nvn 0.47 -0.63 0.62\r\nvn 0.35 -0.91 0.23\r\nvn 0.38 -0.89 0.26\r\nvn 0.37 -0.79 0.49\r\nvn 0.4 -0.76 0.51\r\nvn 0.18 0.11 0.98\r\nvn 0.21 0.14 0.97\r\nvn 0.33 -0.25 0.91\r\nvn 0.3 -0.29 0.91\r\nvn 0.23 0.19 0.95\r\nvn 0.36 -0.2 0.91\r\nvn 0.28 0.31 0.91\r\nvn 0.41 -0.08 0.91\r\nvn 0.38 -0.15 0.91\r\nvn 0.25 0.25 0.94\r\nvn 0.92 -0.39 -0.01\r\nvn 0.92 -0.39 -0.03\r\nvn 0.92 -0.4 0.02\r\nvn 0.98 -0.18 0.04\r\nvn 0.98 -0.18 0\r\nvn 0.98 -0.18 -0.03\r\nvn 0.92 -0.4 0.07\r\nvn 0.92 -0.4 0.04\r\nvn 0.98 -0.18 0.11\r\nvn 0.98 -0.18 0.07\r\nvn 0.47 0.87 0.17\r\nvn 0.47 0.87 0.13\r\nvn 0.85 0.51 0.13\r\nvn 0.84 0.51 0.17\r\nvn 0.48 0.87 0.09\r\nvn 0.86 0.51 0.08\r\nvn 0.47 0.88 0.02\r\nvn 0.86 0.51 -0\r\nvn 0.86 0.51 0.04\r\nvn 0.48 0.88 0.05\r\nvn 0.52 -0.38 -0.77\r\nvn 0.52 -0.32 -0.79\r\nvn 0.48 -0.34 -0.81\r\nvn 0.55 -0.36 -0.75\r\nvn 0.55 -0.31 -0.78\r\nvn 0.54 -0.11 -0.84\r\nvn 0.51 -0.13 -0.85\r\nvn 0.47 -0.14 -0.87\r\nvn 0.59 -0.33 -0.74\r\nvn 0.59 -0.27 -0.76\r\nvn 0.57 -0.29 -0.77\r\nvn 0.58 -0.34 -0.74\r\nvn 0.57 -0.07 -0.82\r\nvn 0.56 -0.09 -0.82\r\nvn 0.08 0.89 -0.44\r\nvn 0.08 0.89 -0.44\r\nvn 0.36 0.56 -0.75\r\nvn 0.36 0.57 -0.74\r\nvn 0.07 0.89 -0.45\r\nvn 0.34 0.55 -0.76\r\nvn 0.04 0.89 -0.46\r\nvn 0.28 0.54 -0.8\r\nvn 0.31 0.54 -0.78\r\nvn 0.06 0.89 -0.46\r\nvn 0.36 -0.33 -0.87\r\nvn 0.3 -0.33 -0.89\r\nvn 0.4 -0.34 -0.85\r\nvn 0.39 -0.14 -0.91\r\nvn 0.34 -0.14 -0.93\r\nvn 0.29 -0.13 -0.95\r\nvn 0.44 -0.34 -0.83\r\nvn 0.43 -0.14 -0.89\r\nvn 0.02 0.89 -0.46\r\nvn 0.25 0.54 -0.81\r\nvn 0 0.89 -0.45\r\nvn 0.21 0.54 -0.81\r\nvn -0.01 0.9 -0.44\r\nvn 0.15 0.55 -0.82\r\nvn 0.18 0.55 -0.82\r\nvn -0.01 0.9 -0.44\r\nvn 0.09 -0.35 -0.93\r\nvn 0.17 -0.34 -0.93\r\nvn 0.16 -0.15 -0.98\r\nvn 0.08 -0.15 -0.98\r\nvn 0.24 -0.33 -0.91\r\nvn 0.22 -0.14 -0.96\r\nvn -0.02 0.9 -0.43\r\nvn 0.11 0.55 -0.83\r\nvn -0.02 0.9 -0.43\r\nvn 0.07 0.55 -0.83\r\nvn 0.04 0.54 -0.84\r\nvn -0.01 0.9 -0.43\r\nvn 0.92 -0.37 -0.13\r\nvn 0.92 -0.38 -0.1\r\nvn 0.98 -0.17 -0.1\r\nvn 0.98 -0.17 -0.14\r\nvn 0.92 -0.38 -0.06\r\nvn 0.98 -0.18 -0.06\r\nvn 0.47 0.88 -0.02\r\nvn 0.86 0.51 -0.05\r\nvn 0.46 0.89 -0.06\r\nvn 0.85 0.52 -0.1\r\nvn 0.84 0.52 -0.14\r\nvn 0.45 0.89 -0.09\r\nvn 0.79 -0.49 0.37\r\nvn 0.81 -0.48 0.34\r\nvn 0.78 -0.48 0.4\r\nvn 0.8 -0.26 0.53\r\nvn 0.82 -0.27 0.5\r\nvn 0.85 -0.27 0.46\r\nvn 0.75 -0.48 0.45\r\nvn 0.77 -0.48 0.43\r\nvn 0.76 -0.26 0.59\r\nvn 0.78 -0.26 0.56\r\nvn 0.48 0.45 0.75\r\nvn 0.49 0.5 0.72\r\nvn 0.68 0.19 0.71\r\nvn 0.66 0.16 0.74\r\nvn 0.47 0.55 0.68\r\nvn 0.69 0.23 0.69\r\nvn 0.42 0.69 0.58\r\nvn 0.71 0.33 0.62\r\nvn 0.7 0.27 0.66\r\nvn 0.45 0.62 0.64\r\nvn 0.73 -0.49 0.47\r\nvn 0.7 -0.51 0.5\r\nvn 0.69 -0.29 0.66\r\nvn 0.74 -0.27 0.62\r\nvn 0.61 -0.55 0.56\r\nvn 0.65 -0.54 0.54\r\nvn 0.59 -0.33 0.73\r\nvn 0.63 -0.32 0.7\r\nvn 0.32 0.36 0.88\r\nvn 0.34 0.37 0.87\r\nvn 0.5 0.09 0.86\r\nvn 0.47 0.08 0.88\r\nvn 0.4 0.39 0.83\r\nvn 0.56 0.11 0.82\r\nvn 0.62 0.14 0.77\r\nvn 0.45 0.42 0.79\r\nvn 0.91 -0.4 0.11\r\nvn 0.9 -0.4 0.15\r\nvn 0.96 -0.18 0.21\r\nvn 0.97 -0.18 0.15\r\nvn 0.87 -0.43 0.23\r\nvn 0.89 -0.41 0.19\r\nvn 0.93 -0.21 0.31\r\nvn 0.95 -0.19 0.26\r\nvn 0.41 0.82 0.39\r\nvn 0.43 0.84 0.33\r\nvn 0.79 0.48 0.37\r\nvn 0.77 0.46 0.44\r\nvn 0.44 0.85 0.28\r\nvn 0.82 0.5 0.3\r\nvn 0.83 0.5 0.23\r\nvn 0.46 0.86 0.22\r\nvn 0.86 -0.45 0.27\r\nvn 0.84 -0.46 0.29\r\nvn 0.89 -0.24 0.39\r\nvn 0.91 -0.23 0.36\r\nvn 0.82 -0.47 0.31\r\nvn 0.87 -0.26 0.42\r\nvn 0.4 0.75 0.53\r\nvn 0.72 0.38 0.58\r\nvn 0.39 0.78 0.48\r\nvn 0.73 0.41 0.54\r\nvn 0.75 0.44 0.49\r\nvn 0.4 0.81 0.44\r\nvn -0.24 0.46 0.85\r\nvn -0.17 0.47 0.87\r\nvn -0.17 0.63 0.76\r\nvn -0.24 0.62 0.75\r\nvn -0.09 0.47 0.88\r\nvn -0.09 0.63 0.77\r\nvn -0.01 0.9 -0.43\r\nvn -0.06 0.99 0.15\r\nvn -0.01 0.9 -0.43\r\nvn -0.11 0.98 0.15\r\nvn -0.15 0.98 0.14\r\nvn -0.01 0.9 -0.43\r\nvn 0.31 -0.92 0.22\r\nvn 0.24 -0.95 0.21\r\nvn 0.25 -0.85 0.46\r\nvn 0.32 -0.82 0.48\r\nvn 0.14 -0.97 0.21\r\nvn 0.14 -0.88 0.45\r\nvn 0.04 0.07 1\r\nvn 0.1 -0.38 0.92\r\nvn 0.09 0.08 0.99\r\nvn 0.18 -0.36 0.92\r\nvn 0.25 -0.33 0.91\r\nvn 0.14 0.09 0.99\r\nvn -0.26 0.91 -0.33\r\nvn -0.23 0.91 -0.35\r\nvn -0.26 0.91 -0.33\r\nvn -0.23 0.91 -0.35\r\nvn -0.2 0.91 -0.36\r\nvn -0.2 0.91 -0.36\r\nvn -0.26 0.91 -0.33\r\nvn -0.23 0.91 -0.35\r\nvn -0.23 0.91 -0.35\r\nvn -0.26 0.91 -0.33\r\nvn -0.2 0.91 -0.36\r\nvn -0.2 0.91 -0.36\r\nvn -0.17 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.37 0.9 -0.22\r\nvn -0.35 0.9 -0.25\r\nvn -0.37 0.9 -0.22\r\nvn -0.35 0.9 -0.25\r\nvn -0.32 0.91 -0.28\r\nvn -0.32 0.91 -0.28\r\nvn -0.37 0.9 -0.22\r\nvn -0.35 0.9 -0.25\r\nvn -0.35 0.9 -0.25\r\nvn -0.37 0.9 -0.22\r\nvn -0.32 0.91 -0.28\r\nvn -0.29 0.91 -0.31\r\nvn -0.29 0.91 -0.31\r\nvn -0.29 0.91 -0.31\r\nvn -0.29 0.91 -0.31\r\nvn -0.43 0.89 -0.12\r\nvn -0.42 0.9 -0.15\r\nvn -0.43 0.89 -0.12\r\nvn -0.42 0.9 -0.15\r\nvn -0.41 0.9 -0.17\r\nvn -0.41 0.9 -0.17\r\nvn -0.43 0.89 -0.12\r\nvn -0.42 0.9 -0.15\r\nvn -0.42 0.9 -0.15\r\nvn -0.43 0.89 -0.12\r\nvn -0.41 0.9 -0.17\r\nvn -0.41 0.9 -0.17\r\nvn -0.39 0.9 -0.2\r\nvn -0.39 0.9 -0.2\r\nvn -0.39 0.9 -0.2\r\nvn -0.39 0.9 -0.2\r\nvn -0.47 0.88 0.02\r\nvn -0.46 0.89 -0.01\r\nvn -0.47 0.88 0.02\r\nvn -0.46 0.89 -0.01\r\nvn -0.46 0.89 -0.05\r\nvn -0.46 0.89 -0.05\r\nvn -0.47 0.88 0.02\r\nvn -0.46 0.89 -0.02\r\nvn -0.46 0.89 -0.02\r\nvn -0.47 0.88 0.02\r\nvn -0.46 0.89 -0.05\r\nvn -0.46 0.89 -0.05\r\nvn -0.45 0.89 -0.09\r\nvn -0.45 0.89 -0.09\r\nvn -0.45 0.89 -0.09\r\nvn -0.45 0.89 -0.09\r\nvn -0.26 0.68 0.68\r\nvn -0.28 0.69 0.67\r\nvn -0.26 0.68 0.68\r\nvn -0.3 0.71 0.64\r\nvn -0.3 0.71 0.64\r\nvn -0.26 0.68 0.68\r\nvn -0.28 0.69 0.67\r\nvn -0.28 0.69 0.67\r\nvn -0.26 0.68 0.68\r\nvn -0.3 0.71 0.64\r\nvn -0.3 0.71 0.64\r\nvn -0.34 0.75 0.57\r\nvn -0.32 0.72 0.61\r\nvn -0.32 0.72 0.61\r\nvn -0.34 0.75 0.57\r\nvn -0.32 0.72 0.61\r\nvn -0.32 0.72 0.61\r\nvn -0.34 0.75 0.57\r\nvn -0.14 0.18 0.97\r\nvn -0.16 0.23 0.96\r\nvn -0.14 0.18 0.97\r\nvn -0.16 0.23 0.96\r\nvn -0.17 0.29 0.94\r\nvn -0.17 0.29 0.94\r\nvn -0.14 0.18 0.97\r\nvn -0.16 0.23 0.96\r\nvn -0.16 0.23 0.96\r\nvn -0.14 0.18 0.97\r\nvn -0.17 0.29 0.94\r\nvn -0.17 0.29 0.94\r\nvn -0.21 0.42 0.88\r\nvn -0.21 0.42 0.88\r\nvn -0.19 0.35 0.92\r\nvn -0.19 0.35 0.92\r\nvn -0.21 0.42 0.88\r\nvn -0.19 0.35 0.92\r\nvn -0.19 0.35 0.92\r\nvn -0.21 0.42 0.88\r\nvn -0.24 0.48 0.84\r\nvn -0.24 0.48 0.85\r\nvn -0.24 0.48 0.84\r\nvn -0.26 0.53 0.81\r\nvn -0.25 0.53 0.81\r\nvn -0.25 0.53 0.81\r\nvn -0.24 0.48 0.84\r\nvn -0.24 0.48 0.84\r\nvn -0.26 0.53 0.81\r\nvn -0.26 0.53 0.81\r\nvn -0.24 0.58 0.78\r\nvn -0.23 0.58 0.78\r\nvn -0.26 0.57 0.78\r\nvn -0.26 0.57 0.78\r\nvn -0.23 0.58 0.78\r\nvn -0.26 0.57 0.78\r\nvn -0.23 0.58 0.78\r\nvn -0.26 0.57 0.78\r\nvn -0.26 0.57 0.78\r\nvn -0.23 0.58 0.78\r\nvn -0.41 0.83 0.39\r\nvn -0.42 0.84 0.33\r\nvn -0.44 0.86 0.27\r\nvn -0.42 0.84 0.33\r\nvn -0.42 0.84 0.33\r\nvn -0.41 0.83 0.39\r\nvn -0.44 0.86 0.28\r\nvn -0.46 0.87 0.17\r\nvn -0.45 0.86 0.22\r\nvn -0.45 0.86 0.22\r\nvn -0.37 0.77 0.52\r\nvn -0.37 0.77 0.52\r\nvn -0.38 0.79 0.48\r\nvn -0.38 0.79 0.48\r\nvn -0.37 0.77 0.52\r\nvn -0.37 0.77 0.52\r\nvn -0.38 0.79 0.48\r\nvn -0.38 0.79 0.48\r\nvn -0.39 0.81 0.43\r\nvn -0.39 0.81 0.43\r\nvn -0.39 0.81 0.43\r\nvn -0.39 0.81 0.43\r\nvn -0.47 0.87 0.13\r\nvn -0.47 0.88 0.09\r\nvn -0.47 0.87 0.13\r\nvn -0.47 0.88 0.09\r\nvn -0.47 0.88 0.06\r\nvn -0.47 0.88 0.06\r\nvn -0.17 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.16 0.91 -0.38\r\nvn -0.16 0.91 -0.38\r\nvn -0.16 0.91 -0.38\r\nvn -0.17 0.91 -0.37\r\nvn -0.16 0.91 -0.38\r\nvn -0.16 0.91 -0.38\r\nvn -0.13 0.91 -0.4\r\nvn -0.13 0.91 -0.4\r\nvn -0.15 0.91 -0.39\r\nvn -0.15 0.91 -0.39\r\nvn -0.15 0.91 -0.39\r\nvn -0.13 0.91 -0.4\r\nvn -0.14 0.91 -0.39\r\nvn -0.04 0.89 -0.46\r\nvn -0.02 0.89 -0.45\r\nvn -0.04 0.89 -0.46\r\nvn -0.02 0.89 -0.45\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.04 0.89 -0.46\r\nvn -0.02 0.89 -0.45\r\nvn -0.02 0.89 -0.45\r\nvn -0.04 0.89 -0.46\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.43\r\nvn 0.01 0.9 -0.43\r\nvn 0 0.9 -0.44\r\nvn 0 0.9 -0.44\r\nvn 0.01 0.9 -0.43\r\nvn 0 0.9 -0.44\r\nvn 0 0.9 -0.44\r\nvn 0.01 0.9 -0.43\r\nvn -0.04 0.1 0.99\r\nvn -0.04 0.1 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.04 0.1 0.99\r\nvn -0.04 0.1 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.12 0.14 0.98\r\nvn -0.12 0.14 0.98\r\nvn -0.12 0.14 0.98\r\nvn -0.12 0.14 0.98\r\nvn 0.01 0.9 -0.43\r\nvn 0.01 0.9 -0.43\r\nvn 0 0.9 -0.43\r\nvn 0.01 0.9 -0.43\r\nvn 0.01 0.9 -0.43\r\nvn -0 0.9 -0.43\r\nvn 0.17 0.91 -0.37\r\nvn 0.19 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.19 0.91 -0.37\r\nvn 0.21 0.91 -0.36\r\nvn 0.21 0.91 -0.36\r\nvn 0.17 0.91 -0.37\r\nvn 0.19 0.91 -0.37\r\nvn 0.19 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.21 0.91 -0.36\r\nvn 0.21 0.91 -0.36\r\nvn 0.26 0.91 -0.33\r\nvn 0.26 0.91 -0.33\r\nvn 0.23 0.91 -0.35\r\nvn 0.23 0.91 -0.35\r\nvn 0.26 0.91 -0.33\r\nvn 0.23 0.91 -0.35\r\nvn 0.23 0.91 -0.35\r\nvn 0.26 0.91 -0.33\r\nvn 0.29 0.91 -0.31\r\nvn 0.29 0.91 -0.31\r\nvn 0.32 0.9 -0.28\r\nvn 0.32 0.9 -0.28\r\nvn 0.29 0.91 -0.31\r\nvn 0.29 0.91 -0.31\r\nvn 0.32 0.9 -0.28\r\nvn 0.32 0.9 -0.28\r\nvn 0.37 0.9 -0.23\r\nvn 0.37 0.9 -0.23\r\nvn 0.34 0.9 -0.26\r\nvn 0.34 0.9 -0.26\r\nvn 0.37 0.9 -0.23\r\nvn 0.34 0.9 -0.25\r\nvn 0.35 0.9 -0.25\r\nvn 0.37 0.9 -0.23\r\nvn 0.39 0.9 -0.2\r\nvn 0.39 0.9 -0.2\r\nvn 0.41 0.9 -0.17\r\nvn 0.41 0.9 -0.17\r\nvn 0.39 0.9 -0.2\r\nvn 0.39 0.9 -0.2\r\nvn 0.41 0.9 -0.17\r\nvn 0.41 0.9 -0.17\r\nvn 0.43 0.89 -0.12\r\nvn 0.43 0.89 -0.12\r\nvn 0.42 0.9 -0.14\r\nvn 0.42 0.9 -0.14\r\nvn 0.43 0.89 -0.12\r\nvn 0.42 0.9 -0.14\r\nvn 0.42 0.9 -0.14\r\nvn 0.43 0.89 -0.12\r\nvn 0.45 0.89 -0.09\r\nvn 0.45 0.89 -0.09\r\nvn 0.46 0.89 -0.06\r\nvn 0.46 0.89 -0.06\r\nvn 0.45 0.89 -0.09\r\nvn 0.45 0.89 -0.09\r\nvn 0.46 0.89 -0.06\r\nvn 0.46 0.89 -0.06\r\nvn 0.47 0.88 0.02\r\nvn 0.46 0.89 -0.02\r\nvn 0.46 0.89 -0.02\r\nvn 0.47 0.88 0.02\r\nvn 0.46 0.89 -0.02\r\nvn 0.46 0.89 -0.02\r\nvn 0.47 0.88 0.02\r\nvn 0.35 0.75 0.57\r\nvn 0.32 0.73 0.6\r\nvn 0.35 0.75 0.57\r\nvn 0.3 0.71 0.64\r\nvn 0.3 0.71 0.64\r\nvn 0.35 0.75 0.57\r\nvn 0.32 0.73 0.6\r\nvn 0.32 0.73 0.6\r\nvn 0.35 0.75 0.57\r\nvn 0.3 0.71 0.63\r\nvn 0.3 0.71 0.64\r\nvn 0.26 0.68 0.69\r\nvn 0.26 0.68 0.69\r\nvn 0.28 0.7 0.66\r\nvn 0.28 0.7 0.66\r\nvn 0.26 0.68 0.69\r\nvn 0.28 0.7 0.66\r\nvn 0.28 0.7 0.66\r\nvn 0.26 0.68 0.69\r\nvn 0.2 0.41 0.89\r\nvn 0.18 0.35 0.92\r\nvn 0.21 0.41 0.89\r\nvn 0.18 0.35 0.92\r\nvn 0.17 0.29 0.94\r\nvn 0.17 0.29 0.94\r\nvn 0.21 0.41 0.89\r\nvn 0.19 0.35 0.92\r\nvn 0.19 0.35 0.92\r\nvn 0.21 0.41 0.89\r\nvn 0.17 0.29 0.94\r\nvn 0.17 0.29 0.94\r\nvn 0.14 0.18 0.97\r\nvn 0.14 0.18 0.97\r\nvn 0.16 0.23 0.96\r\nvn 0.16 0.23 0.96\r\nvn 0.14 0.18 0.97\r\nvn 0.16 0.23 0.96\r\nvn 0.16 0.23 0.96\r\nvn 0.14 0.18 0.97\r\nvn 0.23 0.66 0.72\r\nvn 0.23 0.66 0.72\r\nvn 0.23 0.66 0.72\r\nvn 0.19 0.63 0.75\r\nvn 0.19 0.63 0.76\r\nvn 0.19 0.63 0.76\r\nvn 0.23 0.66 0.72\r\nvn 0.23 0.66 0.72\r\nvn 0.19 0.63 0.75\r\nvn 0.19 0.63 0.75\r\nvn 0.15 0.59 0.8\r\nvn 0.14 0.59 0.8\r\nvn 0.15 0.6 0.79\r\nvn 0.15 0.6 0.79\r\nvn 0.14 0.59 0.8\r\nvn 0.15 0.6 0.79\r\nvn 0.14 0.58 0.8\r\nvn 0.15 0.6 0.79\r\nvn 0.15 0.6 0.79\r\nvn 0.14 0.58 0.8\r\nvn 0.46 0.87 0.17\r\nvn 0.45 0.86 0.22\r\nvn 0.45 0.86 0.22\r\nvn 0.44 0.86 0.28\r\nvn 0.46 0.87 0.17\r\nvn 0.45 0.86 0.22\r\nvn 0.45 0.86 0.22\r\nvn 0.46 0.87 0.17\r\nvn 0.44 0.86 0.28\r\nvn 0.41 0.83 0.39\r\nvn 0.41 0.83 0.39\r\nvn 0.42 0.84 0.34\r\nvn 0.42 0.84 0.34\r\nvn 0.41 0.83 0.39\r\nvn 0.41 0.83 0.39\r\nvn 0.39 0.81 0.44\r\nvn 0.39 0.81 0.44\r\nvn 0.38 0.79 0.48\r\nvn 0.38 0.79 0.48\r\nvn 0.39 0.81 0.44\r\nvn 0.39 0.81 0.44\r\nvn 0.38 0.79 0.48\r\nvn 0.37 0.77 0.52\r\nvn 0.37 0.77 0.52\r\nvn 0.37 0.77 0.52\r\nvn 0.47 0.88 0.05\r\nvn 0.47 0.88 0.05\r\nvn 0.47 0.88 0.05\r\nvn 0.47 0.87 0.13\r\nvn 0.47 0.87 0.13\r\nvn 0.04 0.89 -0.45\r\nvn 0.06 0.89 -0.45\r\nvn 0.06 0.89 -0.45\r\nvn 0.04 0.89 -0.45\r\nvn 0.06 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.04 0.89 -0.45\r\nvn 0.06 0.89 -0.45\r\nvn 0.06 0.89 -0.45\r\nvn 0.04 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.09 0.9 -0.44\r\nvn 0.08 0.89 -0.44\r\nvn 0.08 0.89 -0.44\r\nvn -0.01 0.9 -0.43\r\nvn -0 0.9 -0.44\r\nvn -0.01 0.9 -0.43\r\nvn -0 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.43\r\nvn -0 0.9 -0.44\r\nvn -0 0.9 -0.44\r\nvn -0.01 0.9 -0.43\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.03 0.89 -0.45\r\nvn 0.03 0.89 -0.45\r\nvn 0.03 0.89 -0.45\r\nvn 0.03 0.89 -0.45\r\nvn 0.12 0.14 0.98\r\nvn 0.12 0.14 0.98\r\nvn 0.08 0.11 0.99\r\nvn 0.08 0.11 0.99\r\nvn 0.12 0.14 0.98\r\nvn 0.12 0.14 0.98\r\nvn 0.08 0.11 0.99\r\nvn 0.08 0.11 0.99\r\nvn 0.04 0.1 0.99\r\nvn 0.04 0.1 0.99\r\nvn 0.04 0.1 0.99\r\nvn 0.04 0.1 0.99\r\nvn -0.01 0.9 -0.43\r\nvn -0.01 0.9 -0.43\r\nvn -0.01 0.9 -0.43\r\nvn -0.01 0.9 -0.43\r\nvn -0.15 0.92 -0.36\r\nvn -0.17 0.91 -0.37\r\nvn -0.16 0.92 -0.37\r\nvn -0.18 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.19 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.18 0.91 -0.38\r\nvn -0.18 0.91 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.2 0.91 -0.37\r\nvn -0.2 0.91 -0.37\r\nvn -0.25 0.91 -0.34\r\nvn -0.25 0.91 -0.34\r\nvn -0.22 0.91 -0.36\r\nvn -0.22 0.91 -0.36\r\nvn -0.25 0.91 -0.34\r\nvn -0.22 0.91 -0.36\r\nvn -0.22 0.91 -0.36\r\nvn -0.25 0.91 -0.34\r\nvn -0.29 0.91 -0.31\r\nvn -0.29 0.91 -0.31\r\nvn -0.32 0.9 -0.29\r\nvn -0.32 0.9 -0.29\r\nvn -0.29 0.91 -0.31\r\nvn -0.29 0.91 -0.31\r\nvn -0.32 0.9 -0.28\r\nvn -0.32 0.9 -0.28\r\nvn -0.37 0.9 -0.23\r\nvn -0.37 0.9 -0.23\r\nvn -0.35 0.9 -0.26\r\nvn -0.37 0.9 -0.23\r\nvn -0.35 0.9 -0.26\r\nvn -0.35 0.9 -0.26\r\nvn -0.37 0.9 -0.23\r\nvn -0.39 0.9 -0.2\r\nvn -0.39 0.9 -0.2\r\nvn -0.41 0.9 -0.18\r\nvn -0.41 0.9 -0.18\r\nvn -0.39 0.9 -0.2\r\nvn -0.39 0.9 -0.2\r\nvn -0.41 0.9 -0.17\r\nvn -0.41 0.9 -0.17\r\nvn -0.44 0.89 -0.12\r\nvn -0.44 0.89 -0.12\r\nvn -0.42 0.89 -0.15\r\nvn -0.42 0.89 -0.15\r\nvn -0.44 0.89 -0.12\r\nvn -0.42 0.89 -0.15\r\nvn -0.42 0.89 -0.15\r\nvn -0.44 0.89 -0.12\r\nvn -0.27 0.32 0.91\r\nvn -0.23 0.3 0.93\r\nvn -0.27 0.31 0.91\r\nvn -0.23 0.3 0.93\r\nvn -0.2 0.27 0.94\r\nvn -0.2 0.27 0.94\r\nvn -0.28 0.3 0.91\r\nvn -0.23 0.3 0.93\r\nvn -0.23 0.3 0.93\r\nvn -0.28 0.3 0.91\r\nvn -0.2 0.27 0.94\r\nvn -0.2 0.27 0.94\r\nvn -0.15 0.18 0.97\r\nvn -0.15 0.18 0.97\r\nvn -0.18 0.22 0.96\r\nvn -0.18 0.22 0.96\r\nvn -0.15 0.18 0.97\r\nvn -0.18 0.22 0.96\r\nvn -0.18 0.22 0.96\r\nvn -0.15 0.18 0.97\r\nvn -0.47 0.88 0.02\r\nvn -0.47 0.88 0.02\r\nvn -0.48 0.88 0.02\r\nvn -0.48 0.88 0.06\r\nvn -0.48 0.88 0.06\r\nvn -0.48 0.87 0.09\r\nvn -0.47 0.87 0.17\r\nvn -0.47 0.87 0.17\r\nvn -0.47 0.87 0.13\r\nvn -0.47 0.87 0.13\r\nvn -0.47 0.87 0.17\r\nvn -0.47 0.87 0.17\r\nvn -0.03 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.03 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.06 0.89 -0.46\r\nvn -0.06 0.89 -0.46\r\nvn -0.05 0.89 -0.45\r\nvn -0.03 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.03 0.89 -0.46\r\nvn -0.06 0.89 -0.46\r\nvn -0.06 0.89 -0.46\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.44\r\nvn -0.06 0.89 -0.45\r\nvn -0.05 0.91 -0.41\r\nvn -0.06 0.9 -0.44\r\nvn -0.06 0.89 -0.45\r\nvn -0.06 0.9 -0.43\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn -0 0.89 -0.45\r\nvn -0 0.89 -0.45\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn 0.01 0.9 -0.44\r\nvn -0 0.89 -0.45\r\nvn -0 0.89 -0.45\r\nvn -0.02 0.89 -0.46\r\nvn -0.02 0.89 -0.46\r\nvn -0.02 0.89 -0.46\r\nvn -0.02 0.89 -0.46\r\nvn -0 0.9 -0.43\r\nvn 0.02 0.9 -0.43\r\nvn 0.02 0.9 -0.43\r\nvn 0.02 0.9 -0.43\r\nvn -0.45 0.89 -0.09\r\nvn -0.45 0.89 -0.09\r\nvn -0.46 0.89 -0.05\r\nvn -0.46 0.89 -0.06\r\nvn -0.45 0.89 -0.09\r\nvn -0.45 0.89 -0.09\r\nvn -0.46 0.89 -0.05\r\nvn -0.46 0.89 -0.05\r\nvn -0.47 0.88 -0.02\r\nvn -0.47 0.88 -0.02\r\nvn -0.47 0.88 -0.02\r\nvn -0.35 0.75 0.57\r\nvn -0.32 0.73 0.61\r\nvn -0.35 0.74 0.57\r\nvn -0.33 0.72 0.61\r\nvn -0.32 0.69 0.65\r\nvn -0.31 0.7 0.64\r\nvn -0.35 0.74 0.57\r\nvn -0.33 0.72 0.61\r\nvn -0.33 0.72 0.61\r\nvn -0.35 0.74 0.57\r\nvn -0.32 0.68 0.65\r\nvn -0.32 0.69 0.65\r\nvn -0.39 0.54 0.75\r\nvn -0.39 0.55 0.74\r\nvn -0.34 0.63 0.69\r\nvn -0.34 0.63 0.7\r\nvn -0.38 0.56 0.73\r\nvn -0.34 0.64 0.69\r\nvn -0.32 0.66 0.68\r\nvn -0.36 0.59 0.72\r\nvn -0.41 0.49 0.77\r\nvn -0.38 0.54 0.75\r\nvn -0.42 0.46 0.78\r\nvn -0.43 0.37 0.82\r\nvn -0.42 0.4 0.81\r\nvn -0.4 0.45 0.8\r\nvn -0.42 0.43 0.8\r\nvn -0.43 0.44 0.79\r\nvn -0.42 0.34 0.84\r\nvn -0.43 0.35 0.83\r\nvn -0.39 0.23 0.89\r\nvn -0.4 0.24 0.89\r\nvn -0.41 0.28 0.87\r\nvn -0.39 0.27 0.88\r\nvn -0.4 0.25 0.88\r\nvn -0.41 0.29 0.86\r\nvn -0.38 0.33 0.86\r\nvn -0.39 0.38 0.84\r\nvn -0.4 0.33 0.85\r\nvn -0.4 0.28 0.87\r\nvn -0.46 0.86 0.22\r\nvn -0.46 0.86 0.22\r\nvn -0.44 0.85 0.27\r\nvn -0.46 0.86 0.22\r\nvn -0.44 0.85 0.27\r\nvn -0.44 0.85 0.27\r\nvn -0.41 0.83 0.39\r\nvn -0.41 0.83 0.39\r\nvn -0.43 0.84 0.33\r\nvn -0.43 0.84 0.33\r\nvn -0.41 0.83 0.39\r\nvn -0.41 0.83 0.39\r\nvn -0.4 0.81 0.43\r\nvn -0.4 0.81 0.43\r\nvn -0.39 0.79 0.48\r\nvn -0.39 0.79 0.48\r\nvn -0.4 0.81 0.43\r\nvn -0.39 0.79 0.48\r\nvn -0.39 0.79 0.48\r\nvn -0.37 0.77 0.52\r\nvn -0.37 0.77 0.52\r\nvn -0.37 0.77 0.52\r\nvn -0.37 0.77 0.52\r\nvn -0.12 0.14 0.98\r\nvn -0.12 0.14 0.98\r\nvn -0.08 0.11 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.12 0.14 0.98\r\nvn -0.12 0.14 0.98\r\nvn -0.08 0.11 0.99\r\nvn -0.08 0.11 0.99\r\nvn 0 0.07 1\r\nvn 0 0.07 1\r\nvn -0.04 0.09 1\r\nvn -0.04 0.09 1\r\nvn 0 0.07 1\r\nvn -0.04 0.09 1\r\nvn -0.04 0.09 0.99\r\nvn 0 0.08 1\r\nvn 0.25 0.91 -0.34\r\nvn 0.22 0.91 -0.36\r\nvn 0.25 0.91 -0.34\r\nvn 0.22 0.91 -0.36\r\nvn 0.2 0.91 -0.37\r\nvn 0.2 0.91 -0.37\r\nvn 0.25 0.91 -0.34\r\nvn 0.22 0.91 -0.36\r\nvn 0.22 0.91 -0.36\r\nvn 0.25 0.91 -0.34\r\nvn 0.2 0.91 -0.37\r\nvn 0.2 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.18 0.91 -0.37\r\nvn 0.18 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.18 0.91 -0.37\r\nvn 0.18 0.91 -0.37\r\nvn 0.17 0.91 -0.37\r\nvn 0.37 0.9 -0.23\r\nvn 0.34 0.9 -0.26\r\nvn 0.37 0.9 -0.23\r\nvn 0.34 0.9 -0.26\r\nvn 0.32 0.9 -0.29\r\nvn 0.32 0.9 -0.29\r\nvn 0.37 0.9 -0.23\r\nvn 0.35 0.9 -0.26\r\nvn 0.35 0.9 -0.26\r\nvn 0.37 0.9 -0.23\r\nvn 0.32 0.9 -0.29\r\nvn 0.32 0.9 -0.29\r\nvn 0.29 0.91 -0.31\r\nvn 0.29 0.91 -0.31\r\nvn 0.29 0.91 -0.31\r\nvn 0.29 0.91 -0.31\r\nvn 0.44 0.89 -0.12\r\nvn 0.42 0.89 -0.15\r\nvn 0.44 0.89 -0.12\r\nvn 0.42 0.89 -0.15\r\nvn 0.41 0.9 -0.17\r\nvn 0.41 0.9 -0.17\r\nvn 0.44 0.89 -0.12\r\nvn 0.42 0.89 -0.15\r\nvn 0.42 0.89 -0.15\r\nvn 0.44 0.89 -0.12\r\nvn 0.41 0.9 -0.17\r\nvn 0.41 0.9 -0.17\r\nvn 0.39 0.9 -0.2\r\nvn 0.39 0.9 -0.2\r\nvn 0.39 0.9 -0.2\r\nvn 0.39 0.9 -0.2\r\nvn 0.18 0.09 0.98\r\nvn 0.22 0.12 0.97\r\nvn 0.19 0.09 0.98\r\nvn 0.22 0.11 0.97\r\nvn 0.24 0.16 0.96\r\nvn 0.24 0.17 0.96\r\nvn 0.18 0.08 0.98\r\nvn 0.22 0.11 0.97\r\nvn 0.22 0.11 0.97\r\nvn 0.19 0.08 0.98\r\nvn 0.24 0.15 0.96\r\nvn 0.24 0.15 0.96\r\nvn 0.27 0.31 0.91\r\nvn 0.27 0.31 0.91\r\nvn 0.26 0.22 0.94\r\nvn 0.25 0.22 0.94\r\nvn 0.27 0.31 0.91\r\nvn 0.26 0.22 0.94\r\nvn 0.26 0.23 0.94\r\nvn 0.27 0.31 0.91\r\nvn 0.47 0.87 0.17\r\nvn 0.47 0.87 0.13\r\nvn 0.47 0.87 0.17\r\nvn 0.48 0.87 0.09\r\nvn 0.47 0.87 0.17\r\nvn 0.47 0.87 0.13\r\nvn 0.47 0.87 0.17\r\nvn 0.47 0.88 0.02\r\nvn 0.47 0.88 0.02\r\nvn 0.48 0.88 0.05\r\nvn 0.47 0.88 0.02\r\nvn 0.47 0.88 0.02\r\nvn 0.16 0.91 -0.38\r\nvn 0.16 0.91 -0.38\r\nvn 0.16 0.91 -0.38\r\nvn 0.14 0.91 -0.39\r\nvn 0.14 0.91 -0.39\r\nvn 0.14 0.91 -0.39\r\nvn 0.16 0.91 -0.38\r\nvn 0.14 0.91 -0.39\r\nvn 0.11 0.9 -0.42\r\nvn 0.11 0.9 -0.42\r\nvn 0.12 0.91 -0.41\r\nvn 0.12 0.9 -0.41\r\nvn 0.11 0.9 -0.42\r\nvn 0.11 0.9 -0.42\r\nvn 0.11 0.9 -0.42\r\nvn 0.04 0.89 -0.46\r\nvn 0.02 0.89 -0.46\r\nvn 0.04 0.89 -0.46\r\nvn 0.02 0.89 -0.46\r\nvn 0 0.89 -0.45\r\nvn 0 0.89 -0.45\r\nvn 0.04 0.89 -0.46\r\nvn 0.02 0.89 -0.46\r\nvn 0.02 0.89 -0.46\r\nvn 0.04 0.89 -0.46\r\nvn 0.01 0.89 -0.45\r\nvn 0.01 0.89 -0.45\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.01 0.9 -0.44\r\nvn -0.02 0.9 -0.43\r\nvn -0.02 0.9 -0.43\r\nvn -0.02 0.9 -0.43\r\nvn -0.01 0.9 -0.43\r\nvn 0.47 0.88 -0.02\r\nvn 0.47 0.88 -0.02\r\nvn 0.46 0.89 -0.06\r\nvn 0.46 0.89 -0.06\r\nvn 0.47 0.88 -0.02\r\nvn 0.47 0.88 -0.02\r\nvn 0.46 0.89 -0.06\r\nvn 0.46 0.89 -0.06\r\nvn 0.45 0.89 -0.09\r\nvn 0.45 0.89 -0.09\r\nvn 0.45 0.89 -0.09\r\nvn 0.45 0.89 -0.09\r\nvn 0.41 0.54 0.74\r\nvn 0.42 0.58 0.7\r\nvn 0.34 0.61 0.72\r\nvn 0.36 0.64 0.68\r\nvn 0.36 0.67 0.65\r\nvn 0.41 0.62 0.67\r\nvn 0.28 0.67 0.69\r\nvn 0.3 0.69 0.66\r\nvn 0.31 0.67 0.67\r\nvn 0.3 0.65 0.7\r\nvn 0.32 0.7 0.64\r\nvn 0.33 0.69 0.64\r\nvn 0.36 0.74 0.56\r\nvn 0.36 0.74 0.57\r\nvn 0.34 0.72 0.61\r\nvn 0.34 0.72 0.6\r\nvn 0.37 0.73 0.57\r\nvn 0.37 0.7 0.61\r\nvn 0.4 0.67 0.63\r\nvn 0.39 0.72 0.58\r\nvn 0.3 0.37 0.88\r\nvn 0.31 0.35 0.88\r\nvn 0.29 0.39 0.87\r\nvn 0.3 0.45 0.84\r\nvn 0.32 0.41 0.85\r\nvn 0.34 0.37 0.87\r\nvn 0.28 0.4 0.87\r\nvn 0.28 0.4 0.87\r\nvn 0.28 0.48 0.83\r\nvn 0.29 0.47 0.83\r\nvn 0.28 0.56 0.78\r\nvn 0.29 0.55 0.79\r\nvn 0.29 0.52 0.8\r\nvn 0.28 0.53 0.8\r\nvn 0.31 0.5 0.81\r\nvn 0.31 0.49 0.82\r\nvn 0.38 0.36 0.85\r\nvn 0.37 0.37 0.85\r\nvn 0.34 0.43 0.84\r\nvn 0.35 0.44 0.83\r\nvn 0.41 0.82 0.39\r\nvn 0.43 0.84 0.33\r\nvn 0.41 0.82 0.39\r\nvn 0.44 0.85 0.28\r\nvn 0.44 0.85 0.28\r\nvn 0.41 0.82 0.39\r\nvn 0.43 0.84 0.33\r\nvn 0.41 0.82 0.39\r\nvn 0.44 0.85 0.28\r\nvn 0.44 0.85 0.28\r\nvn 0.46 0.86 0.22\r\nvn 0.46 0.86 0.22\r\nvn 0.46 0.86 0.22\r\nvn 0.39 0.76 0.53\r\nvn 0.38 0.76 0.52\r\nvn 0.39 0.79 0.48\r\nvn 0.39 0.78 0.48\r\nvn 0.37 0.77 0.52\r\nvn 0.37 0.77 0.52\r\nvn 0.38 0.79 0.48\r\nvn 0.38 0.79 0.48\r\nvn 0.4 0.81 0.44\r\nvn 0.4 0.81 0.44\r\nvn 0.4 0.81 0.44\r\nvn 0.4 0.81 0.44\r\nvn 0.05 0.07 1\r\nvn 0.05 0.07 1\r\nvn 0.09 0.06 0.99\r\nvn 0.09 0.07 0.99\r\nvn 0.05 0.06 1\r\nvn 0.05 0.06 1\r\nvn 0.09 0.06 0.99\r\nvn 0.1 0.06 0.99\r\nvn 0.14 0.07 0.99\r\nvn 0.14 0.07 0.99\r\nvn 0.14 0.07 0.99\r\nvn 0.14 0.08 0.99\r\nvn -0.52 0.73 -0.45\r\nvn -0.53 0.7 -0.47\r\nvn -0.5 0.76 -0.41\r\nvn -0.53 0.72 -0.45\r\nvn -0.55 0.69 -0.48\r\nvn -0.56 0.66 -0.5\r\nvn -0.48 0.79 -0.37\r\nvn -0.51 0.76 -0.41\r\nvn -0.54 0.69 -0.48\r\nvn -0.56 0.65 -0.51\r\nvn -0.59 0.59 -0.56\r\nvn -0.58 0.62 -0.54\r\nvn 0.43 -0.9 0.11\r\nvn 0.2 -0.93 -0.31\r\nvn 0.42 -0.9 0.13\r\nvn 0.2 -0.94 -0.28\r\nvn -0.23 -0.58 -0.78\r\nvn -0.24 -0.56 -0.79\r\nvn 0.27 -0.92 -0.29\r\nvn 0.22 -0.94 -0.27\r\nvn 0.44 -0.89 0.14\r\nvn -0.2 -0.59 -0.78\r\nvn -0.22 -0.59 -0.77\r\nvn -0.6 0.41 -0.69\r\nvn -0.53 0.04 -0.84\r\nvn -0.53 0.04 -0.84\r\nvn -0.6 0.41 -0.69\r\nvn -0.53 0.05 -0.84\r\nvn -0.54 0.05 -0.84\r\nvn -0.6 0.42 -0.68\r\nvn 0.52 -0.82 0.25\r\nvn 0.51 -0.81 0.29\r\nvn 0.5 -0.84 0.22\r\nvn 0.51 -0.8 0.31\r\nvn 0.53 -0.78 0.35\r\nvn 0.53 -0.76 0.38\r\nvn 0.48 -0.86 0.18\r\nvn 0.48 -0.83 0.27\r\nvn 0.47 -0.8 0.37\r\nvn 0.48 -0.82 0.32\r\nvn 0.5 -0.76 0.43\r\nvn 0.5 -0.78 0.37\r\nvn 0.53 -0.69 0.49\r\nvn 0.53 -0.73 0.43\r\nvn 0.52 -0.75 0.41\r\nvn 0.52 -0.72 0.46\r\nvn -0.52 0.72 -0.46\r\nvn -0.49 0.77 -0.41\r\nvn -0.54 0.7 -0.47\r\nvn -0.52 0.75 -0.41\r\nvn -0.44 0.87 -0.21\r\nvn -0.41 0.89 -0.2\r\nvn -0.59 0.71 -0.38\r\nvn -0.55 0.73 -0.4\r\nvn -0.57 0.68 -0.46\r\nvn -0.53 0.83 -0.17\r\nvn -0.49 0.85 -0.19\r\nvn 0.17 0.59 0.79\r\nvn -0.14 0.88 0.45\r\nvn -0.19 0.86 0.47\r\nvn 0.21 0.6 0.77\r\nvn -0.1 0.9 0.43\r\nvn -0.06 0.9 0.43\r\nvn 0.23 0.6 0.77\r\nvn 0.53 -0.64 0.56\r\nvn 0.54 -0.61 0.58\r\nvn 0.51 -0.69 0.52\r\nvn 0.52 -0.48 0.7\r\nvn 0.54 -0.42 0.73\r\nvn 0.55 -0.38 0.74\r\nvn 0.48 -0.75 0.46\r\nvn 0.5 -0.55 0.67\r\nvn 0.44 -0.02 0.9\r\nvn 0.46 0.02 0.89\r\nvn 0.48 0.06 0.87\r\nvn 0.48 0.05 0.88\r\nvn 0.25 0.59 0.77\r\nvn 0.26 0.57 0.78\r\nvn 0.26 0.57 0.78\r\nvn 0.25 0.59 0.77\r\nvn 0.25 0.53 0.81\r\nvn 0.25 0.53 0.81\r\nvn 0.25 0.53 0.81\r\nvn 0.26 0.56 0.78\r\nvn 0.25 0.59 0.77\r\nvn 0.23 0.48 0.85\r\nvn 0.23 0.48 0.85\r\nvn 0.23 0.47 0.85\r\nvn 0.23 0.47 0.85\r\nvn 0.25 0.53 0.81\r\nvn 0.25 0.59 0.77\r\nvn 0.25 0.56 0.79\r\nvn 0.26 0.58 0.78\r\nvn 0.27 0.56 0.78\r\nvn 0.23 0.58 0.78\r\nvn 0.25 0.56 0.79\r\nvn 0.28 0.51 0.81\r\nvn 0.3 0.51 0.81\r\nvn 0.17 0.56 0.81\r\nvn 0.18 0.55 0.82\r\nvn 0.21 0.56 0.8\r\nvn 0.2 0.57 0.8\r\nvn 0.22 0.5 0.84\r\nvn 0.25 0.51 0.82\r\nvn 0.34 0.36 0.87\r\nvn 0.3 0.44 0.85\r\nvn 0.27 0.44 0.86\r\nvn 0.36 0.36 0.86\r\nvn 0.32 0.44 0.84\r\nvn 0.34 0.44 0.83\r\nvn 0.38 0.36 0.85\r\nvn -0.58 0.66 -0.48\r\nvn -0.56 0.67 -0.49\r\nvn -0.58 0.63 -0.52\r\nvn -0.59 0.62 -0.52\r\nvn -0.54 0.69 -0.49\r\nvn -0.57 0.64 -0.52\r\nvn -0.59 0.58 -0.57\r\nvn -0.59 0.57 -0.57\r\nvn -0.6 0.56 -0.57\r\nvn 0.51 -0.8 0.32\r\nvn 0.49 -0.81 0.32\r\nvn 0.52 -0.76 0.4\r\nvn 0.54 -0.75 0.39\r\nvn 0.5 -0.81 0.31\r\nvn 0.52 -0.76 0.39\r\nvn 0.53 -0.68 0.51\r\nvn 0.53 -0.72 0.45\r\nvn 0.54 -0.67 0.51\r\nvn 0.53 -0.72 0.45\r\nvn 0.55 -0.71 0.44\r\nvn 0.57 -0.66 0.49\r\nvn 0.58 -0.57 0.58\r\nvn 0.56 -0.58 0.6\r\nvn 0.56 -0.35 0.75\r\nvn 0.58 -0.34 0.74\r\nvn 0.55 -0.59 0.6\r\nvn 0.55 -0.36 0.75\r\nvn 0.48 0.07 0.87\r\nvn 0.48 0.07 0.88\r\nvn 0.47 0.08 0.88\r\nvn 0.18 0.6 0.78\r\nvn 0.18 0.6 0.78\r\nvn 0.21 0.6 0.77\r\nvn 0.21 0.6 0.77\r\nvn 0.21 0.6 0.77\r\nvn 0.18 0.59 0.78\r\nvn 0.23 0.6 0.76\r\nvn 0.23 0.6 0.76\r\nvn 0.23 0.6 0.76\r\nvn 0.23 0.6 0.77\r\nvn 0.21 0.6 0.77\r\nvn 0.17 0.59 0.79\r\nvn 0.29 -0.9 -0.32\r\nvn 0.28 -0.9 -0.34\r\nvn -0.24 -0.6 -0.76\r\nvn -0.2 -0.59 -0.79\r\nvn 0.25 -0.91 -0.34\r\nvn -0.29 -0.61 -0.73\r\nvn -0.65 -0.04 -0.76\r\nvn -0.61 -0.01 -0.79\r\nvn -0.56 0.02 -0.83\r\nvn -0.62 0.7 -0.36\r\nvn -0.66 0.68 -0.33\r\nvn -0.58 0.8 -0.12\r\nvn -0.56 0.82 -0.15\r\nvn -0.68 0.67 -0.3\r\nvn -0.6 0.8 -0.11\r\nvn -0.16 0.88 0.44\r\nvn -0.19 0.87 0.46\r\nvn -0.21 0.86 0.47\r\nvn 0.17 0.58 0.8\r\nvn 0.19 0.56 0.81\r\nvn 0.21 0.61 0.76\r\nvn 0.23 0.59 0.77\r\nvn 0.27 0.55 0.79\r\nvn 0.23 0.52 0.83\r\nvn 0.27 0.63 0.73\r\nvn 0.25 0.64 0.72\r\nvn 0.32 0.58 0.75\r\nvn 0.38 0.51 0.77\r\nvn 0.33 0.48 0.81\r\nvn 0.28 0.45 0.85\r\nvn 0.58 0.61 -0.55\r\nvn 0.59 0.58 -0.56\r\nvn 0.56 0.65 -0.52\r\nvn 0.53 0.71 -0.46\r\nvn 0.55 0.67 -0.49\r\nvn 0.57 0.64 -0.51\r\nvn 0.54 0.69 -0.49\r\nvn 0.51 0.75 -0.42\r\nvn 0.45 0.82 -0.35\r\nvn 0.47 0.79 -0.38\r\nvn 0.47 0.79 -0.4\r\nvn 0.5 0.75 -0.43\r\nvn 0.53 0.71 -0.46\r\nvn 0.55 0.68 -0.48\r\nvn 0.52 0.71 -0.47\r\nvn 0.49 0.75 -0.44\r\nvn 0.61 0.41 -0.68\r\nvn 0.54 0.04 -0.84\r\nvn 0.62 0.4 -0.68\r\nvn 0.54 0.03 -0.84\r\nvn 0.21 -0.57 -0.8\r\nvn 0.21 -0.56 -0.8\r\nvn 0.53 0.01 -0.85\r\nvn 0.54 0.02 -0.84\r\nvn 0.62 0.39 -0.68\r\nvn 0.21 -0.58 -0.79\r\nvn 0.21 -0.57 -0.79\r\nvn -0.49 -0.87 0.05\r\nvn -0.25 -0.9 -0.35\r\nvn -0.23 -0.91 -0.34\r\nvn -0.49 -0.87 0.05\r\nvn -0.25 -0.9 -0.35\r\nvn -0.25 -0.91 -0.35\r\nvn -0.48 -0.88 0.05\r\nvn -0.52 -0.78 0.35\r\nvn -0.54 -0.75 0.37\r\nvn -0.5 -0.8 0.33\r\nvn -0.5 -0.82 0.28\r\nvn -0.52 -0.8 0.3\r\nvn -0.55 -0.77 0.32\r\nvn -0.47 -0.83 0.29\r\nvn -0.47 -0.85 0.25\r\nvn -0.46 -0.87 0.16\r\nvn -0.49 -0.85 0.2\r\nvn -0.53 -0.81 0.24\r\nvn -0.51 -0.83 0.22\r\nvn 0.46 0.8 -0.39\r\nvn 0.5 0.76 -0.41\r\nvn 0.43 0.83 -0.35\r\nvn 0.34 0.93 -0.13\r\nvn 0.38 0.91 -0.18\r\nvn 0.43 0.88 -0.2\r\nvn 0.41 0.86 -0.29\r\nvn 0.33 0.94 -0.06\r\nvn 0.01 0.83 0.55\r\nvn 0 0.87 0.5\r\nvn 0.07 0.89 0.44\r\nvn 0.02 0.89 0.45\r\nvn -0.56 -0.71 0.43\r\nvn -0.57 -0.64 0.52\r\nvn -0.58 -0.69 0.44\r\nvn -0.58 -0.61 0.53\r\nvn -0.58 -0.37 0.73\r\nvn -0.58 -0.4 0.71\r\nvn -0.57 -0.59 0.57\r\nvn -0.59 -0.6 0.55\r\nvn -0.58 -0.68 0.45\r\nvn -0.57 -0.33 0.75\r\nvn -0.58 -0.34 0.74\r\nvn -0.39 0.31 0.87\r\nvn -0.48 0.11 0.87\r\nvn -0.47 0.13 0.87\r\nvn -0.4 0.3 0.87\r\nvn -0.48 0.09 0.87\r\nvn -0.48 0.07 0.87\r\nvn -0.4 0.3 0.87\r\nvn -0.19 0.58 0.79\r\nvn -0.19 0.58 0.79\r\nvn -0.14 0.57 0.81\r\nvn -0.14 0.57 0.81\r\nvn -0.14 0.57 0.81\r\nvn -0.19 0.58 0.79\r\nvn -0.11 0.57 0.82\r\nvn -0.1 0.57 0.82\r\nvn -0.1 0.56 0.82\r\nvn -0.1 0.56 0.82\r\nvn -0.1 0.56 0.82\r\nvn -0.1 0.56 0.83\r\nvn -0.1 0.55 0.83\r\nvn -0.1 0.55 0.83\r\nvn -0.1 0.56 0.82\r\nvn -0.14 0.56 0.81\r\nvn -0.14 0.57 0.81\r\nvn -0.19 0.58 0.79\r\nvn -0.18 0.58 0.8\r\nvn -0.43 0.22 0.87\r\nvn -0.42 0.23 0.88\r\nvn -0.42 0.24 0.88\r\nvn -0.43 0.22 0.87\r\nvn -0.38 0.26 0.89\r\nvn -0.38 0.26 0.89\r\nvn -0.38 0.27 0.89\r\nvn -0.41 0.25 0.88\r\nvn -0.43 0.23 0.87\r\nvn -0.33 0.28 0.9\r\nvn -0.33 0.28 0.9\r\nvn -0.33 0.29 0.9\r\nvn -0.32 0.31 0.9\r\nvn -0.37 0.29 0.89\r\nvn -0.41 0.26 0.87\r\nvn -0.4 0.27 0.88\r\nvn 0.63 0.55 -0.56\r\nvn 0.62 0.55 -0.56\r\nvn 0.61 0.61 -0.5\r\nvn 0.63 0.6 -0.49\r\nvn 0.6 0.56 -0.57\r\nvn 0.59 0.62 -0.51\r\nvn 0.57 0.69 -0.45\r\nvn 0.58 0.66 -0.48\r\nvn 0.61 0.66 -0.43\r\nvn 0.61 0.64 -0.46\r\nvn 0.64 0.63 -0.44\r\nvn 0.64 0.65 -0.4\r\nvn -0.58 -0.71 0.4\r\nvn -0.58 -0.72 0.39\r\nvn -0.58 -0.74 0.33\r\nvn -0.58 -0.74 0.34\r\nvn -0.57 -0.73 0.38\r\nvn -0.57 -0.76 0.33\r\nvn -0.55 -0.8 0.24\r\nvn -0.57 -0.79 0.24\r\nvn -0.57 -0.79 0.25\r\nvn 0.64 0.69 -0.34\r\nvn 0.6 0.71 -0.37\r\nvn 0.55 0.82 -0.15\r\nvn 0.59 0.8 -0.11\r\nvn 0.55 0.73 -0.4\r\nvn 0.49 0.85 -0.18\r\nvn 0.14 0.88 0.46\r\nvn 0.2 0.85 0.49\r\nvn 0.25 0.82 0.52\r\nvn -0.41 0.21 0.88\r\nvn -0.42 0.22 0.88\r\nvn -0.43 0.21 0.88\r\nvn -0.43 0.21 0.88\r\nvn -0.42 0.23 0.88\r\nvn -0.41 0.23 0.88\r\nvn -0.43 0.21 0.88\r\nvn -0.43 0.21 0.88\r\nvn -0.43 0.23 0.88\r\nvn -0.42 0.25 0.87\r\nvn -0.41 0.25 0.87\r\nvn -0.41 0.26 0.88\r\nvn 0.55 -0.01 -0.83\r\nvn 0.6 -0.04 -0.8\r\nvn 0.25 -0.63 -0.73\r\nvn 0.23 -0.6 -0.77\r\nvn 0.64 -0.08 -0.76\r\nvn 0.27 -0.66 -0.7\r\nvn -0.24 -0.93 -0.28\r\nvn -0.23 -0.93 -0.3\r\nvn -0.22 -0.92 -0.32\r\nvn -0.59 -0.58 0.56\r\nvn -0.64 -0.55 0.53\r\nvn -0.66 -0.28 0.7\r\nvn -0.6 -0.31 0.74\r\nvn -0.69 -0.53 0.5\r\nvn -0.7 -0.25 0.67\r\nvn -0.55 0.29 0.79\r\nvn -0.53 0.23 0.82\r\nvn -0.49 0.17 0.85\r\nvn -0.15 0.6 0.79\r\nvn -0.15 0.6 0.79\r\nvn -0.21 0.64 0.74\r\nvn -0.21 0.64 0.74\r\nvn -0.2 0.64 0.74\r\nvn -0.15 0.6 0.79\r\nvn -0.24 0.67 0.71\r\nvn -0.24 0.67 0.71\r\nvn -0.24 0.67 0.71\r\nvn -0.24 0.67 0.71\r\nvn -0.2 0.64 0.74\r\nvn -0.15 0.6 0.79\r\nvn -0.6 0.34 0.72\r\nvn -0.61 0.31 0.73\r\nvn -0.62 0.34 0.71\r\nvn -0.62 0.31 0.72\r\nvn -0.63 0.27 0.73\r\nvn -0.62 0.28 0.73\r\nvn -0.67 0.3 0.68\r\nvn -0.64 0.3 0.7\r\nvn -0.64 0.33 0.69\r\nvn -0.66 0.27 0.7\r\nvn -0.64 0.27 0.72\r\nvn -0.63 0.04 0.78\r\nvn -0.64 0.21 0.74\r\nvn -0.65 0.21 0.73\r\nvn -0.62 0.05 0.78\r\nvn -0.63 0.21 0.75\r\nvn -0.63 0.21 0.75\r\nvn -0.62 0.05 0.78\r\nvn 0.59 -0.37 -0.72\r\nvn 0.6 -0.36 -0.72\r\nvn 0.56 -0.39 -0.73\r\nvn 0.56 -0.43 -0.71\r\nvn 0.59 -0.41 -0.7\r\nvn 0.61 -0.39 -0.69\r\nvn 0.52 -0.41 -0.75\r\nvn 0.53 -0.44 -0.73\r\nvn 0.5 -0.62 -0.6\r\nvn 0.52 -0.5 -0.69\r\nvn 0.54 -0.6 -0.58\r\nvn 0.56 -0.48 -0.67\r\nvn 0.59 -0.58 -0.57\r\nvn 0.61 -0.45 -0.65\r\nvn 0.59 -0.46 -0.66\r\nvn 0.57 -0.59 -0.57\r\nvn 0.45 -0.83 -0.33\r\nvn 0.46 -0.82 -0.33\r\nvn 0.43 -0.84 -0.33\r\nvn -0.02 -0.94 0.34\r\nvn -0.02 -0.94 0.34\r\nvn -0.02 -0.94 0.33\r\nvn 0.4 -0.85 -0.35\r\nvn -0.01 -0.94 0.35\r\nvn -0.44 -0.33 0.83\r\nvn -0.48 -0.35 0.81\r\nvn -0.52 -0.37 0.77\r\nvn -0.51 -0.36 0.78\r\nvn -0.57 0.42 0.71\r\nvn -0.58 0.41 0.7\r\nvn -0.54 0.44 0.72\r\nvn -0.51 0.61 0.61\r\nvn -0.54 0.59 0.6\r\nvn -0.55 0.58 0.6\r\nvn -0.51 0.45 0.73\r\nvn -0.48 0.62 0.62\r\nvn -0.24 0.97 0.04\r\nvn -0.24 0.97 0.04\r\nvn -0.25 0.97 0.04\r\nvn -0.25 0.97 0.04\r\nvn 0.6 -0.32 -0.73\r\nvn 0.6 -0.26 -0.76\r\nvn 0.62 -0.31 -0.72\r\nvn 0.62 -0.25 -0.74\r\nvn 0.6 -0.05 -0.8\r\nvn 0.58 -0.06 -0.81\r\nvn 0.68 -0.25 -0.69\r\nvn 0.65 -0.25 -0.72\r\nvn 0.64 -0.31 -0.7\r\nvn 0.67 -0.05 -0.74\r\nvn 0.63 -0.05 -0.77\r\nvn 0.1 0.9 -0.43\r\nvn 0.4 0.59 -0.7\r\nvn 0.43 0.59 -0.68\r\nvn 0.09 0.9 -0.44\r\nvn 0.38 0.58 -0.72\r\nvn 0.37 0.57 -0.73\r\nvn 0.09 0.89 -0.44\r\nvn 0.09 0.9 -0.44\r\nvn 0.09 0.9 -0.44\r\nvn 0.09 0.9 -0.43\r\nvn 0.09 0.9 -0.43\r\nvn 0.09 0.9 -0.43\r\nvn 0.11 0.9 -0.42\r\nvn 0.11 0.9 -0.42\r\nvn 0.1 0.9 -0.43\r\nvn 0.1 0.9 -0.43\r\nvn 0.11 0.9 -0.42\r\nvn 0.09 0.9 -0.43\r\nvn 0.08 0.89 -0.44\r\nvn 0.07 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.07 0.89 -0.45\r\nvn 0.06 0.89 -0.46\r\nvn 0.06 0.89 -0.46\r\nvn 0.06 0.89 -0.46\r\nvn 0.06 0.89 -0.46\r\nvn 0.64 -0.34 -0.69\r\nvn 0.62 -0.34 -0.71\r\nvn 0.62 -0.38 -0.69\r\nvn 0.64 -0.37 -0.67\r\nvn 0.61 -0.35 -0.71\r\nvn 0.61 -0.38 -0.69\r\nvn 0.59 -0.57 -0.57\r\nvn 0.61 -0.44 -0.66\r\nvn 0.59 -0.57 -0.57\r\nvn 0.62 -0.44 -0.65\r\nvn 0.63 -0.43 -0.65\r\nvn 0.6 -0.57 -0.57\r\nvn 0.47 -0.82 -0.33\r\nvn 0.47 -0.82 -0.33\r\nvn -0.02 -0.95 0.33\r\nvn -0.02 -0.95 0.33\r\nvn 0.46 -0.82 -0.33\r\nvn -0.02 -0.95 0.33\r\nvn -0.51 -0.37 0.77\r\nvn -0.51 -0.38 0.77\r\nvn -0.52 -0.38 0.77\r\nvn -0.64 0.39 0.67\r\nvn -0.61 0.39 0.69\r\nvn -0.58 0.57 0.59\r\nvn -0.6 0.56 0.56\r\nvn -0.59 0.4 0.7\r\nvn -0.56 0.57 0.6\r\nvn -0.26 0.96 0.04\r\nvn -0.27 0.96 0.04\r\nvn -0.28 0.96 0.03\r\nvn 0.1 0.9 -0.43\r\nvn 0.09 0.9 -0.44\r\nvn 0.09 0.89 -0.44\r\nvn 0.09 0.89 -0.44\r\nvn 0.1 0.9 -0.43\r\nvn -0.69 0.31 0.65\r\nvn -0.72 0.32 0.62\r\nvn -0.71 0.28 0.64\r\nvn -0.68 0.28 0.68\r\nvn -0.74 0.33 0.59\r\nvn -0.73 0.29 0.61\r\nvn -0.73 0.23 0.64\r\nvn -0.71 0.22 0.67\r\nvn -0.67 0.21 0.71\r\nvn 0.72 -0.25 -0.65\r\nvn 0.74 -0.26 -0.61\r\nvn 0.75 -0.06 -0.66\r\nvn 0.71 -0.05 -0.7\r\nvn 0.76 -0.27 -0.59\r\nvn 0.76 -0.07 -0.65\r\nvn 0.53 0.59 -0.61\r\nvn 0.5 0.59 -0.63\r\nvn 0.47 0.59 -0.66\r\nvn 0.13 0.91 -0.4\r\nvn 0.13 0.91 -0.4\r\nvn 0.15 0.91 -0.39\r\nvn 0.15 0.91 -0.39\r\nvn 0.15 0.91 -0.39\r\nvn 0.16 0.91 -0.37\r\nvn 0.16 0.91 -0.37\r\nvn 0.16 0.91 -0.37\r\nvn 0.16 0.91 -0.37\r\nvn 0.15 0.91 -0.39\r\nvn 0.62 0.03 0.79\r\nvn 0.62 0.19 0.76\r\nvn 0.62 0.02 0.78\r\nvn 0.63 0.18 0.75\r\nvn 0.63 0.25 0.74\r\nvn 0.61 0.25 0.75\r\nvn 0.65 0.18 0.74\r\nvn 0.64 0.18 0.75\r\nvn 0.62 0.02 0.78\r\nvn 0.65 0.25 0.71\r\nvn 0.64 0.25 0.73\r\nvn 0.66 0.31 0.68\r\nvn 0.64 0.31 0.7\r\nvn 0.64 0.28 0.72\r\nvn 0.66 0.28 0.7\r\nvn 0.61 0.31 0.73\r\nvn 0.62 0.28 0.73\r\nvn 0.6 0.29 0.74\r\nvn 0.59 0.32 0.74\r\nvn -0.59 -0.57 -0.57\r\nvn -0.57 -0.58 -0.58\r\nvn -0.6 -0.45 -0.66\r\nvn -0.61 -0.44 -0.66\r\nvn -0.55 -0.6 -0.59\r\nvn -0.56 -0.47 -0.68\r\nvn -0.56 -0.41 -0.72\r\nvn -0.59 -0.39 -0.7\r\nvn -0.61 -0.38 -0.69\r\nvn -0.53 -0.49 -0.7\r\nvn -0.51 -0.61 -0.61\r\nvn -0.52 -0.43 -0.73\r\nvn -0.51 -0.37 -0.77\r\nvn -0.52 -0.4 -0.76\r\nvn -0.55 -0.35 -0.76\r\nvn -0.55 -0.38 -0.74\r\nvn -0.6 -0.3 -0.74\r\nvn -0.61 -0.34 -0.72\r\nvn -0.58 -0.36 -0.73\r\nvn -0.57 -0.33 -0.75\r\nvn 0.5 -0.37 0.78\r\nvn 0.51 -0.39 0.77\r\nvn 0.48 -0.36 0.8\r\nvn 0.02 -0.94 0.34\r\nvn 0.02 -0.94 0.33\r\nvn 0.02 -0.95 0.32\r\nvn 0.44 -0.34 0.83\r\nvn 0.01 -0.94 0.34\r\nvn -0.4 -0.84 -0.35\r\nvn -0.43 -0.84 -0.34\r\nvn -0.46 -0.82 -0.33\r\nvn -0.45 -0.83 -0.33\r\nvn 0.59 0.38 0.72\r\nvn 0.61 0.37 0.7\r\nvn 0.57 0.55 0.6\r\nvn 0.56 0.56 0.61\r\nvn 0.66 0.37 0.66\r\nvn 0.63 0.37 0.68\r\nvn 0.62 0.55 0.56\r\nvn 0.6 0.55 0.58\r\nvn -0.11 0.9 -0.42\r\nvn 0.27 0.96 0.05\r\nvn 0.27 0.96 0.04\r\nvn -0.09 0.9 -0.43\r\nvn 0.27 0.96 0.05\r\nvn 0.27 0.96 0.05\r\nvn -0.08 0.89 -0.44\r\nvn -0.57 -0.26 -0.78\r\nvn -0.59 -0.24 -0.77\r\nvn -0.54 -0.29 -0.79\r\nvn -0.52 -0.09 -0.85\r\nvn -0.54 -0.05 -0.84\r\nvn -0.56 -0 -0.83\r\nvn -0.51 -0.31 -0.8\r\nvn -0.49 -0.12 -0.86\r\nvn -0.3 0.55 -0.78\r\nvn -0.31 0.56 -0.77\r\nvn -0.32 0.63 -0.71\r\nvn -0.32 0.59 -0.74\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn -0.07 0.89 -0.45\r\nvn -0.06 0.89 -0.45\r\nvn -0.06 0.89 -0.45\r\nvn -0.06 0.89 -0.45\r\nvn -0.05 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.05 0.89 -0.46\r\nvn -0.06 0.89 -0.45\r\nvn -0.08 0.89 -0.45\r\nvn -0.08 0.89 -0.44\r\nvn -0.09 0.9 -0.43\r\nvn -0.09 0.9 -0.43\r\nvn -0.07 0.91 -0.41\r\nvn -0.07 0.9 -0.43\r\nvn -0.14 0.91 -0.4\r\nvn -0.13 0.91 -0.39\r\nvn -0.11 0.91 -0.41\r\nvn -0.11 0.9 -0.42\r\nvn -0.11 0.92 -0.37\r\nvn -0.09 0.92 -0.39\r\nvn -0.02 0.95 -0.32\r\nvn -0.06 0.93 -0.36\r\nvn -0.07 0.94 -0.34\r\nvn -0.02 0.94 -0.34\r\nvn -0.05 0.92 -0.38\r\nvn -0.05 0.91 -0.41\r\nvn -0.03 0.92 -0.38\r\nvn -0.6 -0.57 -0.56\r\nvn -0.64 -0.44 -0.63\r\nvn -0.6 -0.57 -0.57\r\nvn -0.63 -0.43 -0.64\r\nvn -0.64 -0.37 -0.67\r\nvn -0.66 -0.37 -0.65\r\nvn -0.62 -0.43 -0.65\r\nvn -0.59 -0.57 -0.57\r\nvn -0.63 -0.37 -0.69\r\nvn -0.62 -0.29 -0.73\r\nvn -0.63 -0.33 -0.71\r\nvn -0.65 -0.28 -0.7\r\nvn -0.65 -0.33 -0.69\r\nvn -0.67 -0.33 -0.66\r\nvn -0.68 -0.28 -0.67\r\nvn 0.51 -0.4 0.76\r\nvn 0.5 -0.4 0.77\r\nvn 0.02 -0.95 0.32\r\nvn 0.02 -0.95 0.32\r\nvn 0.51 -0.39 0.77\r\nvn 0.02 -0.95 0.32\r\nvn -0.47 -0.82 -0.33\r\nvn -0.47 -0.82 -0.33\r\nvn -0.47 -0.82 -0.32\r\nvn -0.69 -0.18 -0.7\r\nvn -0.65 -0.19 -0.74\r\nvn -0.6 0.1 -0.79\r\nvn -0.64 0.13 -0.76\r\nvn -0.61 -0.21 -0.76\r\nvn -0.58 0.05 -0.82\r\nvn -0.3 0.68 -0.67\r\nvn -0.3 0.72 -0.62\r\nvn -0.31 0.75 -0.58\r\nvn -0.11 0.9 -0.42\r\nvn -0.09 0.9 -0.43\r\nvn -0.09 0.9 -0.43\r\nvn -0.08 0.89 -0.44\r\nvn 0.67 0.19 0.72\r\nvn 0.7 0.19 0.69\r\nvn 0.7 0.26 0.66\r\nvn 0.68 0.25 0.69\r\nvn 0.73 0.2 0.66\r\nvn 0.73 0.26 0.64\r\nvn 0.72 0.33 0.61\r\nvn 0.73 0.3 0.62\r\nvn 0.71 0.32 0.63\r\nvn 0.71 0.29 0.64\r\nvn 0.68 0.29 0.67\r\nvn 0.68 0.32 0.66\r\nvn 0.68 0.37 0.63\r\nvn 0.7 0.38 0.6\r\nvn 0.65 0.56 0.51\r\nvn 0.64 0.55 0.54\r\nvn 0.72 0.38 0.58\r\nvn 0.67 0.56 0.49\r\nvn 0.27 0.96 0.02\r\nvn 0.27 0.96 0.03\r\nvn 0.27 0.96 0.04\r\nvn -0.15 0.91 -0.38\r\nvn -0.15 0.92 -0.37\r\nvn -0.16 0.91 -0.37\r\nvn -0.16 0.92 -0.37\r\nvn -0.14 0.92 -0.35\r\nvn -0.13 0.93 -0.36\r\nvn -0.16 0.92 -0.37\r\nvn -0.17 0.91 -0.37\r\nvn -0.15 0.92 -0.36\r\nvn -0.14 0.93 -0.35\r\nvn -0.12 0.94 -0.33\r\nvn -0.09 0.94 -0.33\r\nvt 0.8 0.0201\r\nvt 0.8 0.0173\r\nvt 0.8081 0.0173\r\nvt 0.8081 0.0201\r\nvt 0.8 0.0135\r\nvt 0.8081 0.0135\r\nvt 0.8156 0.0135\r\nvt 0.8156 0.0173\r\nvt 0.8156 0.0201\r\nvt 0.8 0.0054\r\nvt 0.8081 0.0054\r\nvt 0.8081 0.0093\r\nvt 0.8 0.0093\r\nvt 0.8156 0.0054\r\nvt 0.8156 0.0093\r\nvt 0.8274 0.0054\r\nvt 0.8274 0.0093\r\nvt 0.8222 0.0093\r\nvt 0.8222 0.0054\r\nvt 0.8274 0.0135\r\nvt 0.8222 0.0135\r\nvt 0.8274 0.0201\r\nvt 0.8222 0.0201\r\nvt 0.8222 0.0173\r\nvt 0.8274 0.0173\r\nvt 0.7919 0.0201\r\nvt 0.7919 0.0173\r\nvt 0.7841 0.0201\r\nvt 0.7841 0.0173\r\nvt 0.7841 0.0135\r\nvt 0.7919 0.0135\r\nvt 0.769 0.0201\r\nvt 0.769 0.0173\r\nvt 0.7764 0.0173\r\nvt 0.7764 0.0201\r\nvt 0.769 0.0135\r\nvt 0.7764 0.0135\r\nvt 0.769 0.0054\r\nvt 0.7764 0.0054\r\nvt 0.7764 0.0093\r\nvt 0.769 0.0093\r\nvt 0.7841 0.0054\r\nvt 0.7841 0.0093\r\nvt 0.7919 0.0093\r\nvt 0.7919 0.0054\r\nvt 0.7619 0.0201\r\nvt 0.7619 0.0173\r\nvt 0.7552 0.0201\r\nvt 0.7552 0.0173\r\nvt 0.7552 0.0135\r\nvt 0.7619 0.0135\r\nvt 0.7428 0.0201\r\nvt 0.7428 0.0173\r\nvt 0.7488 0.0173\r\nvt 0.7488 0.0201\r\nvt 0.7428 0.0135\r\nvt 0.7488 0.0135\r\nvt 0.7428 0.0054\r\nvt 0.7488 0.0054\r\nvt 0.7488 0.0093\r\nvt 0.7428 0.0093\r\nvt 0.7552 0.0054\r\nvt 0.7552 0.0093\r\nvt 0.7619 0.0093\r\nvt 0.7619 0.0054\r\nvt 0.7372 0.0093\r\nvt 0.7372 0.0054\r\nvt 0.7372 0.0135\r\nvt 0.7318 0.0135\r\nvt 0.7318 0.0093\r\nvt 0.7318 0.0054\r\nvt 0.7372 0.0201\r\nvt 0.7372 0.0173\r\nvt 0.7318 0.0201\r\nvt 0.7318 0.0173\r\nvt 0.7212 0.0201\r\nvt 0.7212 0.0173\r\nvt 0.7265 0.0173\r\nvt 0.7265 0.0201\r\nvt 0.7212 0.0135\r\nvt 0.7265 0.0135\r\nvt 0.7212 0.0054\r\nvt 0.7265 0.0054\r\nvt 0.7265 0.0093\r\nvt 0.7212 0.0093\r\nvt 0.6276 0.0054\r\nvt 0.6276 0.0093\r\nvt 0.617 0.0093\r\nvt 0.617 0.0054\r\nvt 0.6276 0.0135\r\nvt 0.617 0.0135\r\nvt 0.6069 0.0135\r\nvt 0.6069 0.0093\r\nvt 0.6069 0.0054\r\nvt 0.6276 0.0201\r\nvt 0.617 0.0201\r\nvt 0.617 0.0173\r\nvt 0.6276 0.0173\r\nvt 0.6069 0.0201\r\nvt 0.6069 0.0173\r\nvt 0.5898 0.0201\r\nvt 0.5898 0.0173\r\nvt 0.5977 0.0173\r\nvt 0.5977 0.0201\r\nvt 0.5898 0.0135\r\nvt 0.5977 0.0135\r\nvt 0.5898 0.0054\r\nvt 0.5977 0.0054\r\nvt 0.5977 0.0093\r\nvt 0.5898 0.0093\r\nvt 0.5068 0.0201\r\nvt 0.5068 0.0173\r\nvt 0.5159 0.0173\r\nvt 0.5159 0.0201\r\nvt 0.5068 0.0135\r\nvt 0.5159 0.0135\r\nvt 0.525 0.0135\r\nvt 0.525 0.0173\r\nvt 0.525 0.0201\r\nvt 0.5068 0.0054\r\nvt 0.5159 0.0054\r\nvt 0.5159 0.0093\r\nvt 0.5068 0.0093\r\nvt 0.525 0.0054\r\nvt 0.525 0.0093\r\nvt 0.5423 0.0054\r\nvt 0.5423 0.0093\r\nvt 0.5339 0.0093\r\nvt 0.5339 0.0054\r\nvt 0.5423 0.0135\r\nvt 0.5339 0.0135\r\nvt 0.5423 0.0201\r\nvt 0.5339 0.0201\r\nvt 0.5339 0.0173\r\nvt 0.5423 0.0173\r\nvt 0.5839 0.0201\r\nvt 0.5839 0.0173\r\nvt 0.5797 0.0201\r\nvt 0.5797 0.0173\r\nvt 0.5797 0.0135\r\nvt 0.5839 0.0135\r\nvt 0.5749 0.0201\r\nvt 0.5749 0.0173\r\nvt 0.5769 0.0173\r\nvt 0.5769 0.0201\r\nvt 0.5749 0.0135\r\nvt 0.5769 0.0135\r\nvt 0.5749 0.0054\r\nvt 0.5769 0.0054\r\nvt 0.5769 0.0093\r\nvt 0.5749 0.0093\r\nvt 0.5797 0.0054\r\nvt 0.5797 0.0093\r\nvt 0.5839 0.0093\r\nvt 0.5839 0.0054\r\nvt 0.6975 0.0054\r\nvt 0.6975 0.0093\r\nvt 0.6909 0.0093\r\nvt 0.6909 0.0054\r\nvt 0.6975 0.0135\r\nvt 0.6909 0.0135\r\nvt 0.6839 0.0135\r\nvt 0.6839 0.0093\r\nvt 0.6839 0.0054\r\nvt 0.6975 0.0201\r\nvt 0.6909 0.0201\r\nvt 0.6909 0.0173\r\nvt 0.6975 0.0173\r\nvt 0.6839 0.0201\r\nvt 0.6839 0.0173\r\nvt 0.668 0.0201\r\nvt 0.668 0.0173\r\nvt 0.6763 0.0173\r\nvt 0.6763 0.0201\r\nvt 0.668 0.0135\r\nvt 0.6763 0.0135\r\nvt 0.668 0.0054\r\nvt 0.6763 0.0054\r\nvt 0.6763 0.0093\r\nvt 0.668 0.0093\r\nvt 0.6588 0.0201\r\nvt 0.6588 0.0173\r\nvt 0.6488 0.0201\r\nvt 0.6488 0.0173\r\nvt 0.6488 0.0135\r\nvt 0.6588 0.0135\r\nvt 0.6383 0.0173\r\nvt 0.6383 0.0201\r\nvt 0.6383 0.0135\r\nvt 0.6383 0.0054\r\nvt 0.6383 0.0093\r\nvt 0.6488 0.0054\r\nvt 0.6488 0.0093\r\nvt 0.6588 0.0093\r\nvt 0.6588 0.0054\r\nvt 0.7157 0.0201\r\nvt 0.7157 0.0173\r\nvt 0.7099 0.0201\r\nvt 0.7099 0.0173\r\nvt 0.7099 0.0135\r\nvt 0.7157 0.0135\r\nvt 0.7039 0.0173\r\nvt 0.7039 0.0201\r\nvt 0.7039 0.0135\r\nvt 0.7039 0.0054\r\nvt 0.7039 0.0093\r\nvt 0.7099 0.0054\r\nvt 0.7099 0.0093\r\nvt 0.7157 0.0093\r\nvt 0.7157 0.0054\r\nvt 0.875 0.0201\r\nvt 0.8668 0.0201\r\nvt 0.8668 0.0173\r\nvt 0.875 0.0173\r\nvt 0.8596 0.0201\r\nvt 0.8596 0.0173\r\nvt 0.8596 0.0135\r\nvt 0.8668 0.0135\r\nvt 0.875 0.0135\r\nvt 0.8482 0.0201\r\nvt 0.8482 0.0173\r\nvt 0.8534 0.0173\r\nvt 0.8534 0.0201\r\nvt 0.8482 0.0135\r\nvt 0.8534 0.0135\r\nvt 0.8482 0.0054\r\nvt 0.8534 0.0054\r\nvt 0.8534 0.0093\r\nvt 0.8482 0.0093\r\nvt 0.8596 0.0054\r\nvt 0.8596 0.0093\r\nvt 0.875 0.0054\r\nvt 0.875 0.0093\r\nvt 0.8668 0.0093\r\nvt 0.8668 0.0054\r\nvt 0.9132 0.0201\r\nvt 0.9033 0.0201\r\nvt 0.9033 0.0173\r\nvt 0.9132 0.0173\r\nvt 0.8935 0.0201\r\nvt 0.8935 0.0173\r\nvt 0.8935 0.0135\r\nvt 0.9033 0.0135\r\nvt 0.9132 0.0135\r\nvt 0.884 0.0173\r\nvt 0.884 0.0201\r\nvt 0.884 0.0135\r\nvt 0.884 0.0054\r\nvt 0.884 0.0093\r\nvt 0.8935 0.0054\r\nvt 0.8935 0.0093\r\nvt 0.9132 0.0054\r\nvt 0.9132 0.0093\r\nvt 0.9033 0.0093\r\nvt 0.9033 0.0054\r\nvt 0.498 0.0201\r\nvt 0.498 0.0173\r\nvt 0.4897 0.0201\r\nvt 0.4897 0.0173\r\nvt 0.4897 0.0135\r\nvt 0.498 0.0135\r\nvt 0.4742 0.0201\r\nvt 0.4742 0.0173\r\nvt 0.4818 0.0173\r\nvt 0.4818 0.0201\r\nvt 0.4742 0.0135\r\nvt 0.4818 0.0135\r\nvt 0.4742 0.0054\r\nvt 0.4818 0.0054\r\nvt 0.4818 0.0093\r\nvt 0.4742 0.0093\r\nvt 0.4897 0.0054\r\nvt 0.4897 0.0093\r\nvt 0.498 0.0093\r\nvt 0.498 0.0054\r\nvt 0.8015 0.0423\r\nvt 0.8015 0.0396\r\nvt 0.8096 0.0396\r\nvt 0.8096 0.0423\r\nvt 0.8015 0.0359\r\nvt 0.8096 0.0359\r\nvt 0.8173 0.0359\r\nvt 0.8173 0.0396\r\nvt 0.8173 0.0423\r\nvt 0.8015 0.0279\r\nvt 0.8096 0.0279\r\nvt 0.8096 0.0318\r\nvt 0.8015 0.0318\r\nvt 0.8173 0.0279\r\nvt 0.8173 0.0318\r\nvt 0.8291 0.0279\r\nvt 0.8291 0.0318\r\nvt 0.8239 0.0318\r\nvt 0.8239 0.0279\r\nvt 0.8291 0.0359\r\nvt 0.8239 0.0359\r\nvt 0.8291 0.0423\r\nvt 0.8239 0.0423\r\nvt 0.8239 0.0396\r\nvt 0.8291 0.0396\r\nvt 0.7706 0.0423\r\nvt 0.7706 0.0396\r\nvt 0.7779 0.0396\r\nvt 0.7779 0.0423\r\nvt 0.7706 0.0359\r\nvt 0.7779 0.0359\r\nvt 0.7855 0.0359\r\nvt 0.7855 0.0396\r\nvt 0.7855 0.0423\r\nvt 0.7706 0.0279\r\nvt 0.7779 0.0279\r\nvt 0.7779 0.0318\r\nvt 0.7706 0.0318\r\nvt 0.7855 0.0279\r\nvt 0.7855 0.0318\r\nvt 0.7934 0.0318\r\nvt 0.7934 0.0279\r\nvt 0.7934 0.0359\r\nvt 0.7934 0.0423\r\nvt 0.7934 0.0396\r\nvt 0.7444 0.0423\r\nvt 0.7444 0.0396\r\nvt 0.7505 0.0396\r\nvt 0.7505 0.0423\r\nvt 0.7444 0.0359\r\nvt 0.7505 0.0359\r\nvt 0.7569 0.0359\r\nvt 0.7569 0.0396\r\nvt 0.7569 0.0423\r\nvt 0.7444 0.0279\r\nvt 0.7505 0.0279\r\nvt 0.7505 0.0318\r\nvt 0.7444 0.0318\r\nvt 0.7569 0.0279\r\nvt 0.7569 0.0318\r\nvt 0.7636 0.0318\r\nvt 0.7636 0.0279\r\nvt 0.7636 0.0359\r\nvt 0.7636 0.0423\r\nvt 0.7636 0.0396\r\nvt 0.7265 0.0217\r\nvt 0.7212 0.0217\r\nvt 0.7318 0.0217\r\nvt 0.7318 0.0228\r\nvt 0.7265 0.0228\r\nvt 0.7212 0.0228\r\nvt 0.7428 0.0217\r\nvt 0.7372 0.0217\r\nvt 0.7428 0.0228\r\nvt 0.7372 0.0228\r\nvt 0.7386 0.0279\r\nvt 0.7386 0.0249\r\nvt 0.7444 0.0249\r\nvt 0.7331 0.0279\r\nvt 0.7331 0.0249\r\nvt 0.7331 0.0231\r\nvt 0.7386 0.0231\r\nvt 0.7444 0.0231\r\nvt 0.7224 0.0279\r\nvt 0.7224 0.0249\r\nvt 0.7278 0.0249\r\nvt 0.7278 0.0279\r\nvt 0.7224 0.0231\r\nvt 0.7278 0.0231\r\nvt 0.6276 0.0217\r\nvt 0.617 0.0217\r\nvt 0.6276 0.0228\r\nvt 0.617 0.0228\r\nvt 0.6069 0.0228\r\nvt 0.6069 0.0217\r\nvt 0.6285 0.0279\r\nvt 0.6179 0.0279\r\nvt 0.6179 0.0249\r\nvt 0.6285 0.0249\r\nvt 0.6077 0.0279\r\nvt 0.6077 0.0249\r\nvt 0.6077 0.0231\r\nvt 0.6179 0.0231\r\nvt 0.6285 0.0231\r\nvt 0.5906 0.0279\r\nvt 0.5906 0.0249\r\nvt 0.5984 0.0249\r\nvt 0.5984 0.0279\r\nvt 0.5906 0.0231\r\nvt 0.5984 0.0231\r\nvt 0.5977 0.0217\r\nvt 0.5898 0.0217\r\nvt 0.5977 0.0228\r\nvt 0.5898 0.0228\r\nvt 0.5064 0.0279\r\nvt 0.5155 0.0279\r\nvt 0.5155 0.0318\r\nvt 0.5064 0.0318\r\nvt 0.5246 0.0279\r\nvt 0.5246 0.0318\r\nvt 0.5246 0.0359\r\nvt 0.5155 0.0359\r\nvt 0.5064 0.0359\r\nvt 0.5419 0.0279\r\nvt 0.5419 0.0318\r\nvt 0.5335 0.0318\r\nvt 0.5335 0.0279\r\nvt 0.5419 0.0359\r\nvt 0.5335 0.0359\r\nvt 0.5419 0.0423\r\nvt 0.5335 0.0423\r\nvt 0.5335 0.0396\r\nvt 0.5419 0.0396\r\nvt 0.5246 0.0423\r\nvt 0.5246 0.0396\r\nvt 0.5064 0.0423\r\nvt 0.5064 0.0396\r\nvt 0.5155 0.0396\r\nvt 0.5155 0.0423\r\nvt 0.5499 0.0201\r\nvt 0.5499 0.0217\r\nvt 0.5423 0.0217\r\nvt 0.5566 0.0201\r\nvt 0.5566 0.0217\r\nvt 0.5566 0.0228\r\nvt 0.5499 0.0228\r\nvt 0.5423 0.0228\r\nvt 0.5662 0.0201\r\nvt 0.5662 0.0217\r\nvt 0.562 0.0217\r\nvt 0.562 0.0201\r\nvt 0.5662 0.0228\r\nvt 0.562 0.0228\r\nvt 0.566 0.0279\r\nvt 0.5617 0.0279\r\nvt 0.5617 0.0249\r\nvt 0.566 0.0249\r\nvt 0.5562 0.0279\r\nvt 0.5562 0.0249\r\nvt 0.5562 0.0231\r\nvt 0.5617 0.0231\r\nvt 0.566 0.0231\r\nvt 0.5419 0.0249\r\nvt 0.5495 0.0249\r\nvt 0.5495 0.0279\r\nvt 0.5419 0.0231\r\nvt 0.5495 0.0231\r\nvt 0.6975 0.0217\r\nvt 0.6909 0.0217\r\nvt 0.6975 0.0228\r\nvt 0.6909 0.0228\r\nvt 0.6839 0.0228\r\nvt 0.6839 0.0217\r\nvt 0.6985 0.0279\r\nvt 0.6917 0.0279\r\nvt 0.6917 0.0249\r\nvt 0.6985 0.0249\r\nvt 0.6847 0.0279\r\nvt 0.6847 0.0249\r\nvt 0.6847 0.0231\r\nvt 0.6917 0.0231\r\nvt 0.6985 0.0231\r\nvt 0.6688 0.0279\r\nvt 0.6688 0.0249\r\nvt 0.6771 0.0249\r\nvt 0.6771 0.0279\r\nvt 0.6688 0.0231\r\nvt 0.6771 0.0231\r\nvt 0.6763 0.0217\r\nvt 0.668 0.0217\r\nvt 0.6763 0.0228\r\nvt 0.668 0.0228\r\nvt 0.6383 0.0217\r\nvt 0.6488 0.0217\r\nvt 0.6488 0.0228\r\nvt 0.6383 0.0228\r\nvt 0.6588 0.0217\r\nvt 0.6588 0.0228\r\nvt 0.6597 0.0279\r\nvt 0.6597 0.0249\r\nvt 0.6497 0.0279\r\nvt 0.6497 0.0249\r\nvt 0.6497 0.0231\r\nvt 0.6597 0.0231\r\nvt 0.6392 0.0249\r\nvt 0.6392 0.0279\r\nvt 0.6392 0.0231\r\nvt 0.7049 0.0279\r\nvt 0.7049 0.0318\r\nvt 0.6985 0.0318\r\nvt 0.711 0.0279\r\nvt 0.711 0.0318\r\nvt 0.711 0.0359\r\nvt 0.7049 0.0359\r\nvt 0.6985 0.0359\r\nvt 0.7224 0.0318\r\nvt 0.7169 0.0318\r\nvt 0.7169 0.0279\r\nvt 0.7224 0.0359\r\nvt 0.7169 0.0359\r\nvt 0.7224 0.0423\r\nvt 0.7169 0.0423\r\nvt 0.7169 0.0396\r\nvt 0.7224 0.0396\r\nvt 0.711 0.0423\r\nvt 0.711 0.0396\r\nvt 0.6985 0.0423\r\nvt 0.6985 0.0396\r\nvt 0.7049 0.0396\r\nvt 0.7049 0.0423\r\nvt 0.8327 0.0396\r\nvt 0.8327 0.0423\r\nvt 0.8327 0.0359\r\nvt 0.8352 0.0359\r\nvt 0.8352 0.0396\r\nvt 0.8352 0.0423\r\nvt 0.8327 0.0279\r\nvt 0.8327 0.0318\r\nvt 0.8352 0.0279\r\nvt 0.8352 0.0318\r\nvt 0.8386 0.0279\r\nvt 0.8386 0.0318\r\nvt 0.837 0.0318\r\nvt 0.837 0.0279\r\nvt 0.8386 0.0359\r\nvt 0.837 0.0359\r\nvt 0.8386 0.0423\r\nvt 0.837 0.0423\r\nvt 0.837 0.0396\r\nvt 0.8386 0.0396\r\nvt 0.8759 0.0423\r\nvt 0.8759 0.0396\r\nvt 0.8848 0.0396\r\nvt 0.8848 0.0423\r\nvt 0.8759 0.0359\r\nvt 0.8848 0.0359\r\nvt 0.8944 0.0359\r\nvt 0.8944 0.0396\r\nvt 0.8944 0.0423\r\nvt 0.8759 0.0279\r\nvt 0.8848 0.0279\r\nvt 0.8848 0.0318\r\nvt 0.8759 0.0318\r\nvt 0.8944 0.0279\r\nvt 0.8944 0.0318\r\nvt 0.914 0.0279\r\nvt 0.914 0.0318\r\nvt 0.9042 0.0318\r\nvt 0.9042 0.0279\r\nvt 0.914 0.0359\r\nvt 0.9042 0.0359\r\nvt 0.914 0.0423\r\nvt 0.9042 0.0423\r\nvt 0.9042 0.0396\r\nvt 0.914 0.0396\r\nvt 0.9238 0.0396\r\nvt 0.9238 0.0423\r\nvt 0.9238 0.0359\r\nvt 0.9335 0.0359\r\nvt 0.9335 0.0396\r\nvt 0.9335 0.0423\r\nvt 0.9238 0.0279\r\nvt 0.9238 0.0318\r\nvt 0.9335 0.0279\r\nvt 0.9335 0.0318\r\nvt 0.953 0.0279\r\nvt 0.953 0.0318\r\nvt 0.9431 0.0318\r\nvt 0.9431 0.0279\r\nvt 0.953 0.0359\r\nvt 0.9431 0.0359\r\nvt 0.953 0.0423\r\nvt 0.9431 0.0423\r\nvt 0.9431 0.0396\r\nvt 0.953 0.0396\r\nvt 0.8015 0.0249\r\nvt 0.8096 0.0249\r\nvt 0.8015 0.0231\r\nvt 0.8096 0.0231\r\nvt 0.8173 0.0231\r\nvt 0.8173 0.0249\r\nvt 0.8081 0.0217\r\nvt 0.8 0.0217\r\nvt 0.8156 0.0217\r\nvt 0.8156 0.0228\r\nvt 0.8081 0.0228\r\nvt 0.8 0.0228\r\nvt 0.8274 0.0217\r\nvt 0.8222 0.0217\r\nvt 0.8274 0.0228\r\nvt 0.8222 0.0228\r\nvt 0.8239 0.0249\r\nvt 0.8291 0.0249\r\nvt 0.8239 0.0231\r\nvt 0.8291 0.0231\r\nvt 0.7764 0.0217\r\nvt 0.769 0.0217\r\nvt 0.7841 0.0217\r\nvt 0.7841 0.0228\r\nvt 0.7764 0.0228\r\nvt 0.769 0.0228\r\nvt 0.7919 0.0217\r\nvt 0.7919 0.0228\r\nvt 0.7934 0.0249\r\nvt 0.7855 0.0249\r\nvt 0.7855 0.0231\r\nvt 0.7934 0.0231\r\nvt 0.7706 0.0249\r\nvt 0.7779 0.0249\r\nvt 0.7706 0.0231\r\nvt 0.7779 0.0231\r\nvt 0.7488 0.0217\r\nvt 0.7552 0.0217\r\nvt 0.7552 0.0228\r\nvt 0.7488 0.0228\r\nvt 0.7619 0.0217\r\nvt 0.7619 0.0228\r\nvt 0.7636 0.0249\r\nvt 0.7569 0.0249\r\nvt 0.7569 0.0231\r\nvt 0.7636 0.0231\r\nvt 0.7505 0.0249\r\nvt 0.7505 0.0231\r\nvt 0.7386 0.0423\r\nvt 0.7386 0.0396\r\nvt 0.7331 0.0423\r\nvt 0.7331 0.0396\r\nvt 0.7331 0.0359\r\nvt 0.7386 0.0359\r\nvt 0.7278 0.0396\r\nvt 0.7278 0.0423\r\nvt 0.7278 0.0359\r\nvt 0.7278 0.0318\r\nvt 0.7331 0.0318\r\nvt 0.7386 0.0318\r\nvt 0.6285 0.0423\r\nvt 0.6179 0.0423\r\nvt 0.6179 0.0396\r\nvt 0.6285 0.0396\r\nvt 0.6077 0.0423\r\nvt 0.6077 0.0396\r\nvt 0.6077 0.0359\r\nvt 0.6179 0.0359\r\nvt 0.6285 0.0359\r\nvt 0.5906 0.0423\r\nvt 0.5906 0.0396\r\nvt 0.5984 0.0396\r\nvt 0.5984 0.0423\r\nvt 0.5906 0.0359\r\nvt 0.5984 0.0359\r\nvt 0.5984 0.0318\r\nvt 0.5906 0.0318\r\nvt 0.6077 0.0318\r\nvt 0.6285 0.0318\r\nvt 0.6179 0.0318\r\nvt 0.5064 0.0249\r\nvt 0.5155 0.0249\r\nvt 0.5064 0.0231\r\nvt 0.5155 0.0231\r\nvt 0.5246 0.0231\r\nvt 0.5246 0.0249\r\nvt 0.5159 0.0217\r\nvt 0.5068 0.0217\r\nvt 0.525 0.0217\r\nvt 0.525 0.0228\r\nvt 0.5159 0.0228\r\nvt 0.5068 0.0228\r\nvt 0.5339 0.0217\r\nvt 0.5339 0.0228\r\nvt 0.5335 0.0249\r\nvt 0.5335 0.0231\r\nvt 0.5835 0.0423\r\nvt 0.5835 0.0396\r\nvt 0.5793 0.0423\r\nvt 0.5793 0.0396\r\nvt 0.5793 0.0359\r\nvt 0.5835 0.0359\r\nvt 0.5745 0.0423\r\nvt 0.5745 0.0396\r\nvt 0.5765 0.0396\r\nvt 0.5765 0.0423\r\nvt 0.5745 0.0359\r\nvt 0.5765 0.0359\r\nvt 0.5745 0.0279\r\nvt 0.5765 0.0279\r\nvt 0.5765 0.0318\r\nvt 0.5745 0.0318\r\nvt 0.5793 0.0279\r\nvt 0.5793 0.0318\r\nvt 0.5835 0.0318\r\nvt 0.5835 0.0279\r\nvt 0.6917 0.0423\r\nvt 0.6917 0.0396\r\nvt 0.6847 0.0423\r\nvt 0.6847 0.0396\r\nvt 0.6847 0.0359\r\nvt 0.6917 0.0359\r\nvt 0.6688 0.0423\r\nvt 0.6688 0.0396\r\nvt 0.6771 0.0396\r\nvt 0.6771 0.0423\r\nvt 0.6688 0.0359\r\nvt 0.6771 0.0359\r\nvt 0.6771 0.0318\r\nvt 0.6688 0.0318\r\nvt 0.6847 0.0318\r\nvt 0.6917 0.0318\r\nvt 0.6597 0.0423\r\nvt 0.6597 0.0396\r\nvt 0.6497 0.0423\r\nvt 0.6497 0.0396\r\nvt 0.6497 0.0359\r\nvt 0.6597 0.0359\r\nvt 0.6392 0.0396\r\nvt 0.6392 0.0423\r\nvt 0.6392 0.0359\r\nvt 0.6392 0.0318\r\nvt 0.6497 0.0318\r\nvt 0.6597 0.0318\r\nvt 0.7039 0.0217\r\nvt 0.7099 0.0217\r\nvt 0.7099 0.0228\r\nvt 0.7039 0.0228\r\nvt 0.7157 0.0217\r\nvt 0.7157 0.0228\r\nvt 0.7169 0.0249\r\nvt 0.711 0.0249\r\nvt 0.711 0.0231\r\nvt 0.7169 0.0231\r\nvt 0.7049 0.0249\r\nvt 0.7049 0.0231\r\nvt 0.8327 0.0249\r\nvt 0.8327 0.0231\r\nvt 0.8352 0.0231\r\nvt 0.8352 0.0249\r\nvt 0.8311 0.0201\r\nvt 0.8311 0.0217\r\nvt 0.8337 0.0201\r\nvt 0.8337 0.0217\r\nvt 0.8337 0.0228\r\nvt 0.8311 0.0228\r\nvt 0.8373 0.0201\r\nvt 0.8373 0.0217\r\nvt 0.8356 0.0217\r\nvt 0.8356 0.0201\r\nvt 0.8373 0.0228\r\nvt 0.8356 0.0228\r\nvt 0.837 0.0249\r\nvt 0.8386 0.0249\r\nvt 0.837 0.0231\r\nvt 0.8386 0.0231\r\nvt 0.8759 0.0249\r\nvt 0.8848 0.0249\r\nvt 0.8759 0.0231\r\nvt 0.8848 0.0231\r\nvt 0.8944 0.0231\r\nvt 0.8944 0.0249\r\nvt 0.884 0.0217\r\nvt 0.875 0.0217\r\nvt 0.8935 0.0217\r\nvt 0.8935 0.0228\r\nvt 0.884 0.0228\r\nvt 0.875 0.0228\r\nvt 0.9132 0.0217\r\nvt 0.9033 0.0217\r\nvt 0.9132 0.0228\r\nvt 0.9033 0.0228\r\nvt 0.9042 0.0249\r\nvt 0.914 0.0249\r\nvt 0.9042 0.0231\r\nvt 0.914 0.0231\r\nvt 0.923 0.0201\r\nvt 0.923 0.0217\r\nvt 0.9327 0.0201\r\nvt 0.9327 0.0217\r\nvt 0.9327 0.0228\r\nvt 0.923 0.0228\r\nvt 0.9518 0.0201\r\nvt 0.9518 0.0217\r\nvt 0.9424 0.0217\r\nvt 0.9424 0.0201\r\nvt 0.9518 0.0228\r\nvt 0.9424 0.0228\r\nvt 0.9431 0.0249\r\nvt 0.953 0.0249\r\nvt 0.9335 0.0249\r\nvt 0.9335 0.0231\r\nvt 0.9431 0.0231\r\nvt 0.953 0.0231\r\nvt 0.9238 0.0249\r\nvt 0.9238 0.0231\r\nvt 0.9424 0.0173\r\nvt 0.9518 0.0173\r\nvt 0.9327 0.0173\r\nvt 0.9327 0.0135\r\nvt 0.9424 0.0135\r\nvt 0.9518 0.0135\r\nvt 0.923 0.0173\r\nvt 0.923 0.0135\r\nvt 0.923 0.0054\r\nvt 0.923 0.0093\r\nvt 0.9327 0.0054\r\nvt 0.9327 0.0093\r\nvt 0.9518 0.0054\r\nvt 0.9518 0.0093\r\nvt 0.9424 0.0093\r\nvt 0.9424 0.0054\r\nvt 0.4977 0.0318\r\nvt 0.4977 0.0279\r\nvt 0.4977 0.0359\r\nvt 0.4896 0.0359\r\nvt 0.4896 0.0318\r\nvt 0.4896 0.0279\r\nvt 0.4977 0.0423\r\nvt 0.4977 0.0396\r\nvt 0.4896 0.0423\r\nvt 0.4896 0.0396\r\nvt 0.4745 0.0423\r\nvt 0.4745 0.0396\r\nvt 0.4819 0.0396\r\nvt 0.4819 0.0423\r\nvt 0.4745 0.0359\r\nvt 0.4819 0.0359\r\nvt 0.4745 0.0279\r\nvt 0.4819 0.0279\r\nvt 0.4819 0.0318\r\nvt 0.4745 0.0318\r\nvt 0.498 0.0217\r\nvt 0.498 0.0228\r\nvt 0.4897 0.0228\r\nvt 0.4897 0.0217\r\nvt 0.4977 0.0249\r\nvt 0.4896 0.0249\r\nvt 0.4896 0.0231\r\nvt 0.4977 0.0231\r\nvt 0.4745 0.0249\r\nvt 0.4819 0.0249\r\nvt 0.4745 0.0231\r\nvt 0.4819 0.0231\r\nvt 0.4818 0.0217\r\nvt 0.4742 0.0217\r\nvt 0.4818 0.0228\r\nvt 0.4742 0.0228\r\nvt 0.1497 0.0201\r\nvt 0.1416 0.0201\r\nvt 0.1416 0.0173\r\nvt 0.1497 0.0173\r\nvt 0.1336 0.0201\r\nvt 0.1336 0.0173\r\nvt 0.1336 0.0135\r\nvt 0.1416 0.0135\r\nvt 0.1497 0.0135\r\nvt 0.1196 0.0201\r\nvt 0.1196 0.0173\r\nvt 0.1261 0.0173\r\nvt 0.1261 0.0201\r\nvt 0.1196 0.0135\r\nvt 0.1261 0.0135\r\nvt 0.1196 0.0054\r\nvt 0.1261 0.0054\r\nvt 0.1261 0.0093\r\nvt 0.1196 0.0093\r\nvt 0.1336 0.0054\r\nvt 0.1336 0.0093\r\nvt 0.1497 0.0054\r\nvt 0.1497 0.0093\r\nvt 0.1416 0.0093\r\nvt 0.1416 0.0054\r\nvt 0.1576 0.0173\r\nvt 0.1576 0.0201\r\nvt 0.1576 0.0135\r\nvt 0.1652 0.0135\r\nvt 0.1652 0.0173\r\nvt 0.1652 0.0201\r\nvt 0.1576 0.0054\r\nvt 0.1576 0.0093\r\nvt 0.1652 0.0054\r\nvt 0.1652 0.0093\r\nvt 0.1796 0.0054\r\nvt 0.1796 0.0093\r\nvt 0.1725 0.0093\r\nvt 0.1725 0.0054\r\nvt 0.1796 0.0135\r\nvt 0.1725 0.0135\r\nvt 0.1796 0.0201\r\nvt 0.1725 0.0201\r\nvt 0.1725 0.0173\r\nvt 0.1796 0.0173\r\nvt 0.1863 0.0173\r\nvt 0.1863 0.0201\r\nvt 0.1863 0.0135\r\nvt 0.1927 0.0135\r\nvt 0.1927 0.0173\r\nvt 0.1927 0.0201\r\nvt 0.1863 0.0054\r\nvt 0.1863 0.0093\r\nvt 0.1927 0.0054\r\nvt 0.1927 0.0093\r\nvt 0.2046 0.0054\r\nvt 0.2046 0.0093\r\nvt 0.1988 0.0093\r\nvt 0.1988 0.0054\r\nvt 0.2046 0.0135\r\nvt 0.1988 0.0135\r\nvt 0.2046 0.0201\r\nvt 0.1988 0.0201\r\nvt 0.1988 0.0173\r\nvt 0.2046 0.0173\r\nvt 0.2101 0.0054\r\nvt 0.2101 0.0093\r\nvt 0.2154 0.0054\r\nvt 0.2154 0.0093\r\nvt 0.2154 0.0135\r\nvt 0.2101 0.0135\r\nvt 0.2263 0.0054\r\nvt 0.2263 0.0093\r\nvt 0.2208 0.0093\r\nvt 0.2208 0.0054\r\nvt 0.2263 0.0135\r\nvt 0.2208 0.0135\r\nvt 0.2263 0.0201\r\nvt 0.2208 0.0201\r\nvt 0.2208 0.0173\r\nvt 0.2263 0.0173\r\nvt 0.2154 0.0201\r\nvt 0.2154 0.0173\r\nvt 0.2101 0.0173\r\nvt 0.2101 0.0201\r\nvt 0.3244 0.0054\r\nvt 0.3344 0.0054\r\nvt 0.3344 0.0093\r\nvt 0.3244 0.0093\r\nvt 0.3435 0.0054\r\nvt 0.3435 0.0093\r\nvt 0.3435 0.0135\r\nvt 0.3344 0.0135\r\nvt 0.3244 0.0135\r\nvt 0.3573 0.0054\r\nvt 0.3573 0.0093\r\nvt 0.3513 0.0093\r\nvt 0.3513 0.0054\r\nvt 0.3573 0.0135\r\nvt 0.3513 0.0135\r\nvt 0.3573 0.0201\r\nvt 0.3513 0.0201\r\nvt 0.3513 0.0173\r\nvt 0.3573 0.0173\r\nvt 0.3435 0.0201\r\nvt 0.3435 0.0173\r\nvt 0.3244 0.0201\r\nvt 0.3244 0.0173\r\nvt 0.3344 0.0173\r\nvt 0.3344 0.0201\r\nvt 0.4429 0.0201\r\nvt 0.4342 0.0201\r\nvt 0.4342 0.0173\r\nvt 0.4429 0.0173\r\nvt 0.4252 0.0201\r\nvt 0.4252 0.0173\r\nvt 0.4252 0.0135\r\nvt 0.4342 0.0135\r\nvt 0.4429 0.0135\r\nvt 0.4073 0.0201\r\nvt 0.4073 0.0173\r\nvt 0.4161 0.0173\r\nvt 0.4161 0.0201\r\nvt 0.4073 0.0135\r\nvt 0.4161 0.0135\r\nvt 0.4073 0.0054\r\nvt 0.4161 0.0054\r\nvt 0.4161 0.0093\r\nvt 0.4073 0.0093\r\nvt 0.4252 0.0054\r\nvt 0.4252 0.0093\r\nvt 0.4429 0.0054\r\nvt 0.4429 0.0093\r\nvt 0.4342 0.0093\r\nvt 0.4342 0.0054\r\nvt 0.3616 0.0173\r\nvt 0.3616 0.0201\r\nvt 0.3616 0.0135\r\nvt 0.3646 0.0135\r\nvt 0.3646 0.0173\r\nvt 0.3646 0.0201\r\nvt 0.3616 0.0054\r\nvt 0.3616 0.0093\r\nvt 0.3646 0.0054\r\nvt 0.3646 0.0093\r\nvt 0.3685 0.0054\r\nvt 0.3685 0.0093\r\nvt 0.3668 0.0093\r\nvt 0.3668 0.0054\r\nvt 0.3685 0.0135\r\nvt 0.3668 0.0135\r\nvt 0.3685 0.0201\r\nvt 0.3668 0.0201\r\nvt 0.3668 0.0173\r\nvt 0.3685 0.0173\r\nvt 0.2512 0.0054\r\nvt 0.2583 0.0054\r\nvt 0.2583 0.0093\r\nvt 0.2512 0.0093\r\nvt 0.2658 0.0054\r\nvt 0.2658 0.0093\r\nvt 0.2658 0.0135\r\nvt 0.2583 0.0135\r\nvt 0.2512 0.0135\r\nvt 0.2831 0.0054\r\nvt 0.2831 0.0093\r\nvt 0.274 0.0093\r\nvt 0.274 0.0054\r\nvt 0.2831 0.0135\r\nvt 0.274 0.0135\r\nvt 0.2831 0.0201\r\nvt 0.274 0.0201\r\nvt 0.274 0.0173\r\nvt 0.2831 0.0173\r\nvt 0.2658 0.0201\r\nvt 0.2658 0.0173\r\nvt 0.2512 0.0201\r\nvt 0.2512 0.0173\r\nvt 0.2583 0.0173\r\nvt 0.2583 0.0201\r\nvt 0.2929 0.0173\r\nvt 0.2929 0.0201\r\nvt 0.2929 0.0135\r\nvt 0.3034 0.0135\r\nvt 0.3034 0.0173\r\nvt 0.3034 0.0201\r\nvt 0.2929 0.0054\r\nvt 0.2929 0.0093\r\nvt 0.3034 0.0054\r\nvt 0.3034 0.0093\r\nvt 0.314 0.0093\r\nvt 0.314 0.0054\r\nvt 0.314 0.0135\r\nvt 0.314 0.0201\r\nvt 0.314 0.0173\r\nvt 0.2321 0.0173\r\nvt 0.2321 0.0201\r\nvt 0.2321 0.0135\r\nvt 0.2382 0.0135\r\nvt 0.2382 0.0173\r\nvt 0.2382 0.0201\r\nvt 0.2321 0.0054\r\nvt 0.2321 0.0093\r\nvt 0.2382 0.0054\r\nvt 0.2382 0.0093\r\nvt 0.2445 0.0093\r\nvt 0.2445 0.0054\r\nvt 0.2445 0.0135\r\nvt 0.2445 0.0201\r\nvt 0.2445 0.0173\r\nvt 0.0752 0.0201\r\nvt 0.0752 0.0173\r\nvt 0.0823 0.0173\r\nvt 0.0823 0.0201\r\nvt 0.0752 0.0135\r\nvt 0.0823 0.0135\r\nvt 0.0884 0.0135\r\nvt 0.0884 0.0173\r\nvt 0.0884 0.0201\r\nvt 0.0752 0.0054\r\nvt 0.0823 0.0054\r\nvt 0.0823 0.0093\r\nvt 0.0752 0.0093\r\nvt 0.0884 0.0054\r\nvt 0.0884 0.0093\r\nvt 0.0975 0.0054\r\nvt 0.0975 0.0093\r\nvt 0.0935 0.0093\r\nvt 0.0935 0.0054\r\nvt 0.0975 0.0135\r\nvt 0.0935 0.0135\r\nvt 0.0975 0.0201\r\nvt 0.0935 0.0201\r\nvt 0.0935 0.0173\r\nvt 0.0975 0.0173\r\nvt 0.039 0.0201\r\nvt 0.039 0.0173\r\nvt 0.0488 0.0173\r\nvt 0.0488 0.0201\r\nvt 0.039 0.0135\r\nvt 0.0488 0.0135\r\nvt 0.0583 0.0135\r\nvt 0.0583 0.0173\r\nvt 0.0583 0.0201\r\nvt 0.039 0.0054\r\nvt 0.0488 0.0054\r\nvt 0.0488 0.0093\r\nvt 0.039 0.0093\r\nvt 0.0583 0.0054\r\nvt 0.0583 0.0093\r\nvt 0.0672 0.0093\r\nvt 0.0672 0.0054\r\nvt 0.0672 0.0135\r\nvt 0.0672 0.0201\r\nvt 0.0672 0.0173\r\nvt 0.4511 0.0173\r\nvt 0.4511 0.0201\r\nvt 0.4511 0.0135\r\nvt 0.459 0.0135\r\nvt 0.459 0.0173\r\nvt 0.459 0.0201\r\nvt 0.4511 0.0054\r\nvt 0.4511 0.0093\r\nvt 0.459 0.0054\r\nvt 0.459 0.0093\r\nvt 0.4666 0.0093\r\nvt 0.4666 0.0054\r\nvt 0.4666 0.0135\r\nvt 0.4666 0.0201\r\nvt 0.4666 0.0173\r\nvt 0.1494 0.0423\r\nvt 0.1413 0.0423\r\nvt 0.1413 0.0396\r\nvt 0.1494 0.0396\r\nvt 0.1333 0.0423\r\nvt 0.1333 0.0396\r\nvt 0.1333 0.0359\r\nvt 0.1413 0.0359\r\nvt 0.1494 0.0359\r\nvt 0.1192 0.0423\r\nvt 0.1192 0.0396\r\nvt 0.1257 0.0396\r\nvt 0.1257 0.0423\r\nvt 0.1192 0.0359\r\nvt 0.1257 0.0359\r\nvt 0.1192 0.0279\r\nvt 0.1257 0.0279\r\nvt 0.1257 0.0318\r\nvt 0.1192 0.0318\r\nvt 0.1333 0.0279\r\nvt 0.1333 0.0318\r\nvt 0.1494 0.0279\r\nvt 0.1494 0.0318\r\nvt 0.1413 0.0318\r\nvt 0.1413 0.0279\r\nvt 0.1794 0.0423\r\nvt 0.1723 0.0423\r\nvt 0.1723 0.0396\r\nvt 0.1794 0.0396\r\nvt 0.1649 0.0423\r\nvt 0.1649 0.0396\r\nvt 0.1649 0.0359\r\nvt 0.1723 0.0359\r\nvt 0.1794 0.0359\r\nvt 0.1572 0.0396\r\nvt 0.1572 0.0423\r\nvt 0.1572 0.0359\r\nvt 0.1572 0.0279\r\nvt 0.1572 0.0318\r\nvt 0.1649 0.0279\r\nvt 0.1649 0.0318\r\nvt 0.1794 0.0279\r\nvt 0.1794 0.0318\r\nvt 0.1723 0.0318\r\nvt 0.1723 0.0279\r\nvt 0.2042 0.0423\r\nvt 0.1985 0.0423\r\nvt 0.1985 0.0396\r\nvt 0.2042 0.0396\r\nvt 0.1925 0.0423\r\nvt 0.1925 0.0396\r\nvt 0.1925 0.0359\r\nvt 0.1985 0.0359\r\nvt 0.2042 0.0359\r\nvt 0.1861 0.0396\r\nvt 0.1861 0.0423\r\nvt 0.1861 0.0359\r\nvt 0.1861 0.0279\r\nvt 0.1861 0.0318\r\nvt 0.1925 0.0279\r\nvt 0.1925 0.0318\r\nvt 0.2042 0.0279\r\nvt 0.2042 0.0318\r\nvt 0.1985 0.0318\r\nvt 0.1985 0.0279\r\nvt 0.2263 0.0217\r\nvt 0.2208 0.0217\r\nvt 0.2263 0.0228\r\nvt 0.2208 0.0228\r\nvt 0.2154 0.0228\r\nvt 0.2154 0.0217\r\nvt 0.2257 0.0279\r\nvt 0.2201 0.0279\r\nvt 0.2201 0.0249\r\nvt 0.2257 0.0249\r\nvt 0.2148 0.0279\r\nvt 0.2148 0.0249\r\nvt 0.2148 0.0231\r\nvt 0.2201 0.0231\r\nvt 0.2257 0.0231\r\nvt 0.2042 0.0249\r\nvt 0.2095 0.0249\r\nvt 0.2095 0.0279\r\nvt 0.2042 0.0231\r\nvt 0.2095 0.0231\r\nvt 0.2101 0.0217\r\nvt 0.2046 0.0217\r\nvt 0.2101 0.0228\r\nvt 0.2046 0.0228\r\nvt 0.3344 0.0217\r\nvt 0.3244 0.0217\r\nvt 0.3435 0.0217\r\nvt 0.3435 0.0228\r\nvt 0.3344 0.0228\r\nvt 0.3244 0.0228\r\nvt 0.3573 0.0217\r\nvt 0.3513 0.0217\r\nvt 0.3573 0.0228\r\nvt 0.3513 0.0228\r\nvt 0.3581 0.0279\r\nvt 0.3522 0.0279\r\nvt 0.3522 0.0249\r\nvt 0.3581 0.0249\r\nvt 0.3444 0.0279\r\nvt 0.3444 0.0249\r\nvt 0.3444 0.0231\r\nvt 0.3522 0.0231\r\nvt 0.3581 0.0231\r\nvt 0.3252 0.0279\r\nvt 0.3252 0.0249\r\nvt 0.3353 0.0249\r\nvt 0.3353 0.0279\r\nvt 0.3252 0.0231\r\nvt 0.3353 0.0231\r\nvt 0.4441 0.0279\r\nvt 0.4441 0.0318\r\nvt 0.4354 0.0318\r\nvt 0.4354 0.0279\r\nvt 0.4441 0.0359\r\nvt 0.4354 0.0359\r\nvt 0.4263 0.0359\r\nvt 0.4263 0.0318\r\nvt 0.4263 0.0279\r\nvt 0.4441 0.0423\r\nvt 0.4354 0.0423\r\nvt 0.4354 0.0396\r\nvt 0.4441 0.0396\r\nvt 0.4263 0.0423\r\nvt 0.4263 0.0396\r\nvt 0.408 0.0423\r\nvt 0.408 0.0396\r\nvt 0.417 0.0396\r\nvt 0.417 0.0423\r\nvt 0.408 0.0359\r\nvt 0.417 0.0359\r\nvt 0.408 0.0279\r\nvt 0.417 0.0279\r\nvt 0.417 0.0318\r\nvt 0.408 0.0318\r\nvt 0.4073 0.0217\r\nvt 0.3991 0.0217\r\nvt 0.3991 0.0201\r\nvt 0.4073 0.0228\r\nvt 0.3991 0.0228\r\nvt 0.3916 0.0228\r\nvt 0.3916 0.0217\r\nvt 0.3916 0.0201\r\nvt 0.3995 0.0279\r\nvt 0.3995 0.0249\r\nvt 0.408 0.0249\r\nvt 0.3919 0.0279\r\nvt 0.3919 0.0249\r\nvt 0.3919 0.0231\r\nvt 0.3995 0.0231\r\nvt 0.408 0.0231\r\nvt 0.3797 0.0279\r\nvt 0.3797 0.0249\r\nvt 0.3852 0.0249\r\nvt 0.3852 0.0279\r\nvt 0.3797 0.0231\r\nvt 0.3852 0.0231\r\nvt 0.3795 0.0201\r\nvt 0.385 0.0201\r\nvt 0.385 0.0217\r\nvt 0.3795 0.0217\r\nvt 0.385 0.0228\r\nvt 0.3795 0.0228\r\nvt 0.2583 0.0217\r\nvt 0.2512 0.0217\r\nvt 0.2658 0.0217\r\nvt 0.2658 0.0228\r\nvt 0.2583 0.0228\r\nvt 0.2512 0.0228\r\nvt 0.2831 0.0217\r\nvt 0.274 0.0217\r\nvt 0.2831 0.0228\r\nvt 0.274 0.0228\r\nvt 0.2831 0.0279\r\nvt 0.2738 0.0279\r\nvt 0.2738 0.0249\r\nvt 0.2831 0.0249\r\nvt 0.2654 0.0279\r\nvt 0.2654 0.0249\r\nvt 0.2654 0.0231\r\nvt 0.2738 0.0231\r\nvt 0.2831 0.0231\r\nvt 0.2507 0.0279\r\nvt 0.2507 0.0249\r\nvt 0.2577 0.0249\r\nvt 0.2577 0.0279\r\nvt 0.2507 0.0231\r\nvt 0.2577 0.0231\r\nvt 0.314 0.0217\r\nvt 0.314 0.0228\r\nvt 0.3034 0.0228\r\nvt 0.3034 0.0217\r\nvt 0.3146 0.0279\r\nvt 0.3146 0.0249\r\nvt 0.3038 0.0279\r\nvt 0.3038 0.0249\r\nvt 0.3038 0.0231\r\nvt 0.3146 0.0231\r\nvt 0.2932 0.0249\r\nvt 0.2932 0.0279\r\nvt 0.2932 0.0231\r\nvt 0.2929 0.0217\r\nvt 0.2929 0.0228\r\nvt 0.2507 0.0318\r\nvt 0.244 0.0318\r\nvt 0.244 0.0279\r\nvt 0.2507 0.0359\r\nvt 0.244 0.0359\r\nvt 0.2376 0.0359\r\nvt 0.2376 0.0318\r\nvt 0.2376 0.0279\r\nvt 0.2507 0.0423\r\nvt 0.244 0.0423\r\nvt 0.244 0.0396\r\nvt 0.2507 0.0396\r\nvt 0.2376 0.0423\r\nvt 0.2376 0.0396\r\nvt 0.2257 0.0423\r\nvt 0.2257 0.0396\r\nvt 0.2315 0.0396\r\nvt 0.2315 0.0423\r\nvt 0.2257 0.0359\r\nvt 0.2315 0.0359\r\nvt 0.2315 0.0279\r\nvt 0.2315 0.0318\r\nvt 0.2257 0.0318\r\nvt 0.1142 0.0423\r\nvt 0.1142 0.0396\r\nvt 0.1107 0.0423\r\nvt 0.1107 0.0396\r\nvt 0.1107 0.0359\r\nvt 0.1142 0.0359\r\nvt 0.1064 0.0423\r\nvt 0.1064 0.0396\r\nvt 0.1082 0.0396\r\nvt 0.1082 0.0423\r\nvt 0.1064 0.0359\r\nvt 0.1082 0.0359\r\nvt 0.1064 0.0279\r\nvt 0.1082 0.0279\r\nvt 0.1082 0.0318\r\nvt 0.1064 0.0318\r\nvt 0.1107 0.0279\r\nvt 0.1107 0.0318\r\nvt 0.1142 0.0318\r\nvt 0.1142 0.0279\r\nvt 0.0753 0.0423\r\nvt 0.0672 0.0423\r\nvt 0.0672 0.0396\r\nvt 0.0753 0.0396\r\nvt 0.0583 0.0423\r\nvt 0.0583 0.0396\r\nvt 0.0583 0.0359\r\nvt 0.0672 0.0359\r\nvt 0.0753 0.0359\r\nvt 0.039 0.0423\r\nvt 0.039 0.0396\r\nvt 0.0488 0.0396\r\nvt 0.0488 0.0423\r\nvt 0.039 0.0359\r\nvt 0.0488 0.0359\r\nvt 0.039 0.0279\r\nvt 0.0488 0.0279\r\nvt 0.0488 0.0318\r\nvt 0.039 0.0318\r\nvt 0.0583 0.0279\r\nvt 0.0583 0.0318\r\nvt 0.0753 0.0279\r\nvt 0.0753 0.0318\r\nvt 0.0672 0.0318\r\nvt 0.0672 0.0279\r\nvt 0.0291 0.0423\r\nvt 0.0291 0.0396\r\nvt 0.0193 0.0423\r\nvt 0.0193 0.0396\r\nvt 0.0193 0.0359\r\nvt 0.0291 0.0359\r\nvt 0 0.0423\r\nvt 0 0.0396\r\nvt 0.0096 0.0396\r\nvt 0.0096 0.0423\r\nvt 0 0.0359\r\nvt 0.0096 0.0359\r\nvt 0 0.0279\r\nvt 0.0096 0.0279\r\nvt 0.0096 0.0318\r\nvt 0 0.0318\r\nvt 0.0193 0.0279\r\nvt 0.0193 0.0318\r\nvt 0.0291 0.0318\r\nvt 0.0291 0.0279\r\nvt 0.1413 0.0249\r\nvt 0.1494 0.0249\r\nvt 0.1333 0.0249\r\nvt 0.1333 0.0231\r\nvt 0.1413 0.0231\r\nvt 0.1494 0.0231\r\nvt 0.1192 0.0249\r\nvt 0.1257 0.0249\r\nvt 0.1192 0.0231\r\nvt 0.1257 0.0231\r\nvt 0.1261 0.0217\r\nvt 0.1196 0.0217\r\nvt 0.1336 0.0217\r\nvt 0.1336 0.0228\r\nvt 0.1261 0.0228\r\nvt 0.1196 0.0228\r\nvt 0.1497 0.0217\r\nvt 0.1416 0.0217\r\nvt 0.1497 0.0228\r\nvt 0.1416 0.0228\r\nvt 0.1796 0.0217\r\nvt 0.1725 0.0217\r\nvt 0.1796 0.0228\r\nvt 0.1725 0.0228\r\nvt 0.1652 0.0228\r\nvt 0.1652 0.0217\r\nvt 0.1723 0.0249\r\nvt 0.1794 0.0249\r\nvt 0.1649 0.0249\r\nvt 0.1649 0.0231\r\nvt 0.1723 0.0231\r\nvt 0.1794 0.0231\r\nvt 0.1572 0.0249\r\nvt 0.1572 0.0231\r\nvt 0.1576 0.0217\r\nvt 0.1576 0.0228\r\nvt 0.1988 0.0217\r\nvt 0.1988 0.0228\r\nvt 0.1927 0.0228\r\nvt 0.1927 0.0217\r\nvt 0.1985 0.0249\r\nvt 0.1925 0.0249\r\nvt 0.1925 0.0231\r\nvt 0.1985 0.0231\r\nvt 0.1861 0.0249\r\nvt 0.1861 0.0231\r\nvt 0.1863 0.0217\r\nvt 0.1863 0.0228\r\nvt 0.2095 0.0396\r\nvt 0.2095 0.0423\r\nvt 0.2095 0.0359\r\nvt 0.2148 0.0359\r\nvt 0.2148 0.0396\r\nvt 0.2148 0.0423\r\nvt 0.2095 0.0318\r\nvt 0.2148 0.0318\r\nvt 0.2201 0.0318\r\nvt 0.2201 0.0359\r\nvt 0.2201 0.0423\r\nvt 0.2201 0.0396\r\nvt 0.3252 0.0423\r\nvt 0.3252 0.0396\r\nvt 0.3353 0.0396\r\nvt 0.3353 0.0423\r\nvt 0.3252 0.0359\r\nvt 0.3353 0.0359\r\nvt 0.3444 0.0359\r\nvt 0.3444 0.0396\r\nvt 0.3444 0.0423\r\nvt 0.3353 0.0318\r\nvt 0.3252 0.0318\r\nvt 0.3444 0.0318\r\nvt 0.3581 0.0318\r\nvt 0.3522 0.0318\r\nvt 0.3581 0.0359\r\nvt 0.3522 0.0359\r\nvt 0.3581 0.0423\r\nvt 0.3522 0.0423\r\nvt 0.3522 0.0396\r\nvt 0.3581 0.0396\r\nvt 0.4354 0.0249\r\nvt 0.4441 0.0249\r\nvt 0.4263 0.0249\r\nvt 0.4263 0.0231\r\nvt 0.4354 0.0231\r\nvt 0.4441 0.0231\r\nvt 0.417 0.0249\r\nvt 0.417 0.0231\r\nvt 0.4161 0.0217\r\nvt 0.4252 0.0217\r\nvt 0.4252 0.0228\r\nvt 0.4161 0.0228\r\nvt 0.4429 0.0217\r\nvt 0.4342 0.0217\r\nvt 0.4429 0.0228\r\nvt 0.4342 0.0228\r\nvt 0.3623 0.0396\r\nvt 0.3623 0.0423\r\nvt 0.3623 0.0359\r\nvt 0.3652 0.0359\r\nvt 0.3652 0.0396\r\nvt 0.3652 0.0423\r\nvt 0.3623 0.0279\r\nvt 0.3623 0.0318\r\nvt 0.3652 0.0279\r\nvt 0.3652 0.0318\r\nvt 0.3689 0.0279\r\nvt 0.3689 0.0318\r\nvt 0.3672 0.0318\r\nvt 0.3672 0.0279\r\nvt 0.3689 0.0359\r\nvt 0.3672 0.0359\r\nvt 0.3689 0.0423\r\nvt 0.3672 0.0423\r\nvt 0.3672 0.0396\r\nvt 0.3689 0.0396\r\nvt 0.2577 0.0396\r\nvt 0.2577 0.0423\r\nvt 0.2577 0.0359\r\nvt 0.2654 0.0359\r\nvt 0.2654 0.0396\r\nvt 0.2654 0.0423\r\nvt 0.2577 0.0318\r\nvt 0.2654 0.0318\r\nvt 0.2831 0.0318\r\nvt 0.2738 0.0318\r\nvt 0.2831 0.0359\r\nvt 0.2738 0.0359\r\nvt 0.2831 0.0423\r\nvt 0.2738 0.0423\r\nvt 0.2738 0.0396\r\nvt 0.2831 0.0396\r\nvt 0.2932 0.0396\r\nvt 0.2932 0.0423\r\nvt 0.2932 0.0359\r\nvt 0.3038 0.0359\r\nvt 0.3038 0.0396\r\nvt 0.3038 0.0423\r\nvt 0.2932 0.0318\r\nvt 0.3038 0.0318\r\nvt 0.3146 0.0318\r\nvt 0.3146 0.0359\r\nvt 0.3146 0.0423\r\nvt 0.3146 0.0396\r\nvt 0.2445 0.0217\r\nvt 0.2445 0.0228\r\nvt 0.2382 0.0228\r\nvt 0.2382 0.0217\r\nvt 0.244 0.0249\r\nvt 0.2376 0.0249\r\nvt 0.2376 0.0231\r\nvt 0.244 0.0231\r\nvt 0.2315 0.0249\r\nvt 0.2315 0.0231\r\nvt 0.2321 0.0217\r\nvt 0.2321 0.0228\r\nvt 0.1142 0.0249\r\nvt 0.1107 0.0249\r\nvt 0.1107 0.0231\r\nvt 0.1142 0.0231\r\nvt 0.1064 0.0249\r\nvt 0.1082 0.0249\r\nvt 0.1064 0.0231\r\nvt 0.1082 0.0231\r\nvt 0.1067 0.0201\r\nvt 0.1086 0.0201\r\nvt 0.1086 0.0217\r\nvt 0.1067 0.0217\r\nvt 0.1111 0.0201\r\nvt 0.1111 0.0217\r\nvt 0.1111 0.0228\r\nvt 0.1086 0.0228\r\nvt 0.1067 0.0228\r\nvt 0.1146 0.0217\r\nvt 0.1146 0.0201\r\nvt 0.1146 0.0228\r\nvt 0.0672 0.0249\r\nvt 0.0753 0.0249\r\nvt 0.0583 0.0249\r\nvt 0.0583 0.0231\r\nvt 0.0672 0.0231\r\nvt 0.0753 0.0231\r\nvt 0.039 0.0249\r\nvt 0.0488 0.0249\r\nvt 0.039 0.0231\r\nvt 0.0488 0.0231\r\nvt 0.0488 0.0217\r\nvt 0.039 0.0217\r\nvt 0.0583 0.0217\r\nvt 0.0583 0.0228\r\nvt 0.0488 0.0228\r\nvt 0.039 0.0228\r\nvt 0.0752 0.0217\r\nvt 0.0672 0.0217\r\nvt 0.0752 0.0228\r\nvt 0.0672 0.0228\r\nvt 0.0292 0.0217\r\nvt 0.0292 0.0201\r\nvt 0.0292 0.0228\r\nvt 0.0194 0.0228\r\nvt 0.0194 0.0217\r\nvt 0.0194 0.0201\r\nvt 0.0291 0.0249\r\nvt 0.0193 0.0249\r\nvt 0.0193 0.0231\r\nvt 0.0291 0.0231\r\nvt 0 0.0249\r\nvt 0.0096 0.0249\r\nvt 0 0.0231\r\nvt 0.0096 0.0231\r\nvt 0 0.0201\r\nvt 0.0097 0.0201\r\nvt 0.0097 0.0217\r\nvt 0 0.0217\r\nvt 0.0097 0.0228\r\nvt 0 0.0228\r\nvt 0 0.0173\r\nvt 0.0097 0.0173\r\nvt 0 0.0135\r\nvt 0.0097 0.0135\r\nvt 0.0194 0.0135\r\nvt 0.0194 0.0173\r\nvt 0 0.0054\r\nvt 0.0097 0.0054\r\nvt 0.0097 0.0093\r\nvt 0 0.0093\r\nvt 0.0194 0.0054\r\nvt 0.0194 0.0093\r\nvt 0.0292 0.0093\r\nvt 0.0292 0.0054\r\nvt 0.0292 0.0135\r\nvt 0.0292 0.0173\r\nvt 0.4522 0.0279\r\nvt 0.4522 0.0318\r\nvt 0.4599 0.0279\r\nvt 0.4599 0.0318\r\nvt 0.4599 0.0359\r\nvt 0.4522 0.0359\r\nvt 0.4672 0.0318\r\nvt 0.4672 0.0279\r\nvt 0.4672 0.0359\r\nvt 0.4672 0.0423\r\nvt 0.4672 0.0396\r\nvt 0.4599 0.0423\r\nvt 0.4599 0.0396\r\nvt 0.4522 0.0396\r\nvt 0.4522 0.0423\r\nvt 0.4511 0.0217\r\nvt 0.459 0.0217\r\nvt 0.459 0.0228\r\nvt 0.4511 0.0228\r\nvt 0.4666 0.0217\r\nvt 0.4666 0.0228\r\nvt 0.4672 0.0249\r\nvt 0.4599 0.0249\r\nvt 0.4599 0.0231\r\nvt 0.4672 0.0231\r\nvt 0.4522 0.0249\r\nvt 0.4522 0.0231\r\nvt 0.8222 0.0026\r\nvt 0.8274 0.0026\r\nvt 0.8156 0.0026\r\nvt 0.8156 0.001\r\nvt 0.8222 0.001\r\nvt 0.8274 0.001\r\nvt 0.8 0.0026\r\nvt 0.8081 0.0026\r\nvt 0.8 0.001\r\nvt 0.8081 0.001\r\nvt 0.8 0\r\nvt 0.8081 0\r\nvt 0.8081 0.0002\r\nvt 0.8 0.0002\r\nvt 0.8156 0\r\nvt 0.8156 0.0002\r\nvt 0.8274 0\r\nvt 0.8274 0.0002\r\nvt 0.8222 0.0002\r\nvt 0.8222 0\r\nvt 0.7919 0.0026\r\nvt 0.7841 0.0026\r\nvt 0.7841 0.001\r\nvt 0.7919 0.001\r\nvt 0.769 0.0026\r\nvt 0.7764 0.0026\r\nvt 0.769 0.001\r\nvt 0.7764 0.001\r\nvt 0.769 0\r\nvt 0.7764 0\r\nvt 0.7764 0.0002\r\nvt 0.769 0.0002\r\nvt 0.7841 0\r\nvt 0.7841 0.0002\r\nvt 0.7919 0.0002\r\nvt 0.7919 0\r\nvt 0.7619 0.0026\r\nvt 0.7552 0.0026\r\nvt 0.7552 0.001\r\nvt 0.7619 0.001\r\nvt 0.7428 0.0026\r\nvt 0.7488 0.0026\r\nvt 0.7428 0.001\r\nvt 0.7488 0.001\r\nvt 0.7428 0\r\nvt 0.7488 0\r\nvt 0.7488 0.0002\r\nvt 0.7428 0.0002\r\nvt 0.7552 0\r\nvt 0.7552 0.0002\r\nvt 0.7619 0.0002\r\nvt 0.7619 0\r\nvt 0.7372 0.0026\r\nvt 0.7318 0.0026\r\nvt 0.7318 0.001\r\nvt 0.7372 0.001\r\nvt 0.7212 0.0026\r\nvt 0.7265 0.0026\r\nvt 0.7212 0.001\r\nvt 0.7265 0.001\r\nvt 0.7212 0\r\nvt 0.7265 0\r\nvt 0.7265 0.0002\r\nvt 0.7212 0.0002\r\nvt 0.7318 0\r\nvt 0.7318 0.0002\r\nvt 0.7372 0.0002\r\nvt 0.7372 0\r\nvt 0.617 0.0026\r\nvt 0.6276 0.0026\r\nvt 0.6069 0.0026\r\nvt 0.6069 0.001\r\nvt 0.617 0.001\r\nvt 0.6276 0.001\r\nvt 0.5898 0.0026\r\nvt 0.5977 0.0026\r\nvt 0.5898 0.001\r\nvt 0.5977 0.001\r\nvt 0.5898 0\r\nvt 0.5977 0\r\nvt 0.5977 0.0002\r\nvt 0.5898 0.0002\r\nvt 0.6069 0\r\nvt 0.6069 0.0002\r\nvt 0.6276 0\r\nvt 0.6276 0.0002\r\nvt 0.617 0.0002\r\nvt 0.617 0\r\nvt 0.5339 0.0026\r\nvt 0.5423 0.0026\r\nvt 0.525 0.0026\r\nvt 0.525 0.001\r\nvt 0.5339 0.001\r\nvt 0.5423 0.001\r\nvt 0.5068 0.0026\r\nvt 0.5159 0.0026\r\nvt 0.5068 0.001\r\nvt 0.5159 0.001\r\nvt 0.5068 0\r\nvt 0.5159 0\r\nvt 0.5159 0.0002\r\nvt 0.5068 0.0002\r\nvt 0.525 0\r\nvt 0.525 0.0002\r\nvt 0.5423 0\r\nvt 0.5423 0.0002\r\nvt 0.5339 0.0002\r\nvt 0.5339 0\r\nvt 0.5839 0.0026\r\nvt 0.5797 0.0026\r\nvt 0.5797 0.001\r\nvt 0.5839 0.001\r\nvt 0.5749 0.0026\r\nvt 0.5769 0.0026\r\nvt 0.5749 0.001\r\nvt 0.5769 0.001\r\nvt 0.5749 0\r\nvt 0.5769 0\r\nvt 0.5769 0.0002\r\nvt 0.5749 0.0002\r\nvt 0.5797 0\r\nvt 0.5797 0.0002\r\nvt 0.5839 0.0002\r\nvt 0.5839 0\r\nvt 0.6909 0.0026\r\nvt 0.6975 0.0026\r\nvt 0.6839 0.0026\r\nvt 0.6839 0.001\r\nvt 0.6909 0.001\r\nvt 0.6975 0.001\r\nvt 0.668 0.0026\r\nvt 0.6763 0.0026\r\nvt 0.668 0.001\r\nvt 0.6763 0.001\r\nvt 0.668 0\r\nvt 0.6763 0\r\nvt 0.6763 0.0002\r\nvt 0.668 0.0002\r\nvt 0.6839 0\r\nvt 0.6839 0.0002\r\nvt 0.6975 0\r\nvt 0.6975 0.0002\r\nvt 0.6909 0.0002\r\nvt 0.6909 0\r\nvt 0.6588 0.0026\r\nvt 0.6488 0.0026\r\nvt 0.6488 0.001\r\nvt 0.6588 0.001\r\nvt 0.6383 0.0026\r\nvt 0.6383 0.001\r\nvt 0.6383 0\r\nvt 0.6383 0.0002\r\nvt 0.6488 0\r\nvt 0.6488 0.0002\r\nvt 0.6588 0.0002\r\nvt 0.6588 0\r\nvt 0.7157 0.0026\r\nvt 0.7099 0.0026\r\nvt 0.7099 0.001\r\nvt 0.7157 0.001\r\nvt 0.7039 0.0026\r\nvt 0.7039 0.001\r\nvt 0.7039 0\r\nvt 0.7039 0.0002\r\nvt 0.7099 0\r\nvt 0.7099 0.0002\r\nvt 0.7157 0.0002\r\nvt 0.7157 0\r\nvt 0.8668 0.0026\r\nvt 0.875 0.0026\r\nvt 0.8596 0.0026\r\nvt 0.8596 0.001\r\nvt 0.8668 0.001\r\nvt 0.875 0.001\r\nvt 0.8482 0.0026\r\nvt 0.8534 0.0026\r\nvt 0.8482 0.001\r\nvt 0.8534 0.001\r\nvt 0.8482 0\r\nvt 0.8534 0\r\nvt 0.8534 0.0002\r\nvt 0.8482 0.0002\r\nvt 0.8596 0\r\nvt 0.8596 0.0002\r\nvt 0.875 0\r\nvt 0.875 0.0002\r\nvt 0.8668 0.0002\r\nvt 0.8668 0\r\nvt 0.9033 0.0026\r\nvt 0.9132 0.0026\r\nvt 0.8935 0.0026\r\nvt 0.8935 0.001\r\nvt 0.9033 0.001\r\nvt 0.9132 0.001\r\nvt 0.884 0.0026\r\nvt 0.884 0.001\r\nvt 0.884 0\r\nvt 0.884 0.0002\r\nvt 0.8935 0\r\nvt 0.8935 0.0002\r\nvt 0.9132 0\r\nvt 0.9132 0.0002\r\nvt 0.9033 0.0002\r\nvt 0.9033 0\r\nvt 0.498 0.0026\r\nvt 0.4897 0.0026\r\nvt 0.4897 0.001\r\nvt 0.498 0.001\r\nvt 0.4742 0.0026\r\nvt 0.4818 0.0026\r\nvt 0.4742 0.001\r\nvt 0.4818 0.001\r\nvt 0.4742 0\r\nvt 0.4818 0\r\nvt 0.4818 0.0002\r\nvt 0.4742 0.0002\r\nvt 0.4897 0\r\nvt 0.4897 0.0002\r\nvt 0.498 0.0002\r\nvt 0.498 0\r\nvt 0.8096 0.0438\r\nvt 0.8015 0.0438\r\nvt 0.8173 0.0438\r\nvt 0.8173 0.0445\r\nvt 0.8096 0.0445\r\nvt 0.8015 0.0445\r\nvt 0.8291 0.0438\r\nvt 0.8239 0.0438\r\nvt 0.8291 0.0445\r\nvt 0.8239 0.0445\r\nvt 0.8291 0.045\r\nvt 0.8239 0.045\r\nvt 0.8239 0.0448\r\nvt 0.8291 0.0448\r\nvt 0.8173 0.045\r\nvt 0.8173 0.0448\r\nvt 0.8015 0.045\r\nvt 0.8015 0.0448\r\nvt 0.8096 0.0448\r\nvt 0.8096 0.045\r\nvt 0.7779 0.0438\r\nvt 0.7706 0.0438\r\nvt 0.7855 0.0438\r\nvt 0.7855 0.0445\r\nvt 0.7779 0.0445\r\nvt 0.7706 0.0445\r\nvt 0.7934 0.0438\r\nvt 0.7934 0.0445\r\nvt 0.7934 0.045\r\nvt 0.7934 0.0448\r\nvt 0.7855 0.045\r\nvt 0.7855 0.0448\r\nvt 0.7706 0.045\r\nvt 0.7706 0.0448\r\nvt 0.7779 0.0448\r\nvt 0.7779 0.045\r\nvt 0.7505 0.0438\r\nvt 0.7444 0.0438\r\nvt 0.7569 0.0438\r\nvt 0.7569 0.0445\r\nvt 0.7505 0.0445\r\nvt 0.7444 0.0445\r\nvt 0.7636 0.0438\r\nvt 0.7636 0.0445\r\nvt 0.7636 0.045\r\nvt 0.7636 0.0448\r\nvt 0.7569 0.045\r\nvt 0.7569 0.0448\r\nvt 0.7444 0.045\r\nvt 0.7444 0.0448\r\nvt 0.7505 0.0448\r\nvt 0.7505 0.045\r\nvt 0.5155 0.0438\r\nvt 0.5064 0.0438\r\nvt 0.5246 0.0438\r\nvt 0.5246 0.0445\r\nvt 0.5155 0.0445\r\nvt 0.5064 0.0445\r\nvt 0.5419 0.0438\r\nvt 0.5335 0.0438\r\nvt 0.5419 0.0445\r\nvt 0.5335 0.0445\r\nvt 0.5419 0.045\r\nvt 0.5335 0.045\r\nvt 0.5335 0.0448\r\nvt 0.5419 0.0448\r\nvt 0.5246 0.045\r\nvt 0.5246 0.0448\r\nvt 0.5064 0.045\r\nvt 0.5064 0.0448\r\nvt 0.5155 0.0448\r\nvt 0.5155 0.045\r\nvt 0.7049 0.0438\r\nvt 0.6985 0.0438\r\nvt 0.711 0.0438\r\nvt 0.711 0.0445\r\nvt 0.7049 0.0445\r\nvt 0.6985 0.0445\r\nvt 0.7224 0.0438\r\nvt 0.7169 0.0438\r\nvt 0.7224 0.0445\r\nvt 0.7169 0.0445\r\nvt 0.7224 0.045\r\nvt 0.7169 0.045\r\nvt 0.7169 0.0448\r\nvt 0.7224 0.0448\r\nvt 0.711 0.045\r\nvt 0.711 0.0448\r\nvt 0.6985 0.045\r\nvt 0.6985 0.0448\r\nvt 0.7049 0.0448\r\nvt 0.7049 0.045\r\nvt 0.8327 0.0438\r\nvt 0.8352 0.0438\r\nvt 0.8352 0.0445\r\nvt 0.8327 0.0445\r\nvt 0.8386 0.0438\r\nvt 0.837 0.0438\r\nvt 0.8386 0.0445\r\nvt 0.837 0.0445\r\nvt 0.8386 0.045\r\nvt 0.837 0.045\r\nvt 0.837 0.0448\r\nvt 0.8386 0.0448\r\nvt 0.8352 0.045\r\nvt 0.8352 0.0448\r\nvt 0.8327 0.0448\r\nvt 0.8327 0.045\r\nvt 0.8848 0.0438\r\nvt 0.8759 0.0438\r\nvt 0.8944 0.0438\r\nvt 0.8944 0.0445\r\nvt 0.8848 0.0445\r\nvt 0.8759 0.0445\r\nvt 0.914 0.0438\r\nvt 0.9042 0.0438\r\nvt 0.914 0.0445\r\nvt 0.9042 0.0445\r\nvt 0.914 0.045\r\nvt 0.9042 0.045\r\nvt 0.9042 0.0448\r\nvt 0.914 0.0448\r\nvt 0.8944 0.045\r\nvt 0.8944 0.0448\r\nvt 0.8759 0.045\r\nvt 0.8759 0.0448\r\nvt 0.8848 0.0448\r\nvt 0.8848 0.045\r\nvt 0.9238 0.0438\r\nvt 0.9335 0.0438\r\nvt 0.9335 0.0445\r\nvt 0.9238 0.0445\r\nvt 0.953 0.0438\r\nvt 0.9431 0.0438\r\nvt 0.953 0.0445\r\nvt 0.9431 0.0445\r\nvt 0.953 0.045\r\nvt 0.9431 0.045\r\nvt 0.9431 0.0448\r\nvt 0.953 0.0448\r\nvt 0.9335 0.045\r\nvt 0.9335 0.0448\r\nvt 0.9238 0.0448\r\nvt 0.9238 0.045\r\nvt 0.7278 0.0438\r\nvt 0.7331 0.0438\r\nvt 0.7331 0.0445\r\nvt 0.7278 0.0445\r\nvt 0.7386 0.0438\r\nvt 0.7386 0.0445\r\nvt 0.7386 0.045\r\nvt 0.7386 0.0448\r\nvt 0.7331 0.045\r\nvt 0.7331 0.0448\r\nvt 0.7278 0.0448\r\nvt 0.7278 0.045\r\nvt 0.5984 0.0438\r\nvt 0.5906 0.0438\r\nvt 0.6077 0.0438\r\nvt 0.6077 0.0445\r\nvt 0.5984 0.0445\r\nvt 0.5906 0.0445\r\nvt 0.6285 0.0438\r\nvt 0.6179 0.0438\r\nvt 0.6285 0.0445\r\nvt 0.6179 0.0445\r\nvt 0.6285 0.045\r\nvt 0.6179 0.045\r\nvt 0.6179 0.0448\r\nvt 0.6285 0.0448\r\nvt 0.6077 0.045\r\nvt 0.6077 0.0448\r\nvt 0.5906 0.045\r\nvt 0.5906 0.0448\r\nvt 0.5984 0.0448\r\nvt 0.5984 0.045\r\nvt 0.5495 0.0423\r\nvt 0.5495 0.0438\r\nvt 0.5562 0.0423\r\nvt 0.5562 0.0438\r\nvt 0.5562 0.0445\r\nvt 0.5495 0.0445\r\nvt 0.566 0.0423\r\nvt 0.566 0.0438\r\nvt 0.5617 0.0438\r\nvt 0.5617 0.0423\r\nvt 0.566 0.0445\r\nvt 0.5617 0.0445\r\nvt 0.566 0.045\r\nvt 0.5617 0.045\r\nvt 0.5617 0.0448\r\nvt 0.566 0.0448\r\nvt 0.5562 0.045\r\nvt 0.5562 0.0448\r\nvt 0.5495 0.0448\r\nvt 0.5495 0.045\r\nvt 0.6771 0.0438\r\nvt 0.6688 0.0438\r\nvt 0.6847 0.0438\r\nvt 0.6847 0.0445\r\nvt 0.6771 0.0445\r\nvt 0.6688 0.0445\r\nvt 0.6917 0.0438\r\nvt 0.6917 0.0445\r\nvt 0.6917 0.045\r\nvt 0.6917 0.0448\r\nvt 0.6847 0.045\r\nvt 0.6847 0.0448\r\nvt 0.6688 0.045\r\nvt 0.6688 0.0448\r\nvt 0.6771 0.0448\r\nvt 0.6771 0.045\r\nvt 0.6392 0.0438\r\nvt 0.6497 0.0438\r\nvt 0.6497 0.0445\r\nvt 0.6392 0.0445\r\nvt 0.6597 0.0438\r\nvt 0.6597 0.0445\r\nvt 0.6597 0.045\r\nvt 0.6597 0.0448\r\nvt 0.6497 0.045\r\nvt 0.6497 0.0448\r\nvt 0.6392 0.0448\r\nvt 0.6392 0.045\r\nvt 0.9424 0.0026\r\nvt 0.9518 0.0026\r\nvt 0.9327 0.0026\r\nvt 0.9327 0.001\r\nvt 0.9424 0.001\r\nvt 0.9518 0.001\r\nvt 0.923 0.0026\r\nvt 0.923 0.001\r\nvt 0.923 0\r\nvt 0.923 0.0002\r\nvt 0.9327 0\r\nvt 0.9327 0.0002\r\nvt 0.9518 0\r\nvt 0.9518 0.0002\r\nvt 0.9424 0.0002\r\nvt 0.9424 0\r\nvt 0.4819 0.0438\r\nvt 0.4745 0.0438\r\nvt 0.4896 0.0438\r\nvt 0.4896 0.0445\r\nvt 0.4819 0.0445\r\nvt 0.4745 0.0445\r\nvt 0.4977 0.0438\r\nvt 0.4977 0.0445\r\nvt 0.4977 0.045\r\nvt 0.4977 0.0448\r\nvt 0.4896 0.045\r\nvt 0.4896 0.0448\r\nvt 0.4745 0.045\r\nvt 0.4745 0.0448\r\nvt 0.4819 0.0448\r\nvt 0.4819 0.045\r\nvt 0.1416 0.0026\r\nvt 0.1497 0.0026\r\nvt 0.1336 0.0026\r\nvt 0.1336 0.001\r\nvt 0.1416 0.001\r\nvt 0.1497 0.001\r\nvt 0.1196 0.0026\r\nvt 0.1261 0.0026\r\nvt 0.1196 0.001\r\nvt 0.1261 0.001\r\nvt 0.1196 0\r\nvt 0.1261 0\r\nvt 0.1261 0.0002\r\nvt 0.1196 0.0002\r\nvt 0.1336 0\r\nvt 0.1336 0.0002\r\nvt 0.1497 0\r\nvt 0.1497 0.0002\r\nvt 0.1416 0.0002\r\nvt 0.1416 0\r\nvt 0.1725 0.0026\r\nvt 0.1796 0.0026\r\nvt 0.1652 0.0026\r\nvt 0.1652 0.001\r\nvt 0.1725 0.001\r\nvt 0.1796 0.001\r\nvt 0.1576 0.0026\r\nvt 0.1576 0.001\r\nvt 0.1576 0\r\nvt 0.1576 0.0002\r\nvt 0.1652 0\r\nvt 0.1652 0.0002\r\nvt 0.1796 0\r\nvt 0.1796 0.0002\r\nvt 0.1725 0.0002\r\nvt 0.1725 0\r\nvt 0.1988 0.0026\r\nvt 0.2046 0.0026\r\nvt 0.1927 0.0026\r\nvt 0.1927 0.001\r\nvt 0.1988 0.001\r\nvt 0.2046 0.001\r\nvt 0.1863 0.0026\r\nvt 0.1863 0.001\r\nvt 0.1863 0\r\nvt 0.1863 0.0002\r\nvt 0.1927 0\r\nvt 0.1927 0.0002\r\nvt 0.2046 0\r\nvt 0.2046 0.0002\r\nvt 0.1988 0.0002\r\nvt 0.1988 0\r\nvt 0.2208 0.0026\r\nvt 0.2263 0.0026\r\nvt 0.2154 0.0026\r\nvt 0.2154 0.001\r\nvt 0.2208 0.001\r\nvt 0.2263 0.001\r\nvt 0.2101 0.0026\r\nvt 0.2101 0.001\r\nvt 0.2101 0\r\nvt 0.2101 0.0002\r\nvt 0.2154 0\r\nvt 0.2154 0.0002\r\nvt 0.2263 0\r\nvt 0.2263 0.0002\r\nvt 0.2208 0.0002\r\nvt 0.2208 0\r\nvt 0.3513 0.0026\r\nvt 0.3573 0.0026\r\nvt 0.3435 0.0026\r\nvt 0.3435 0.001\r\nvt 0.3513 0.001\r\nvt 0.3573 0.001\r\nvt 0.3244 0.0026\r\nvt 0.3344 0.0026\r\nvt 0.3244 0.001\r\nvt 0.3344 0.001\r\nvt 0.3244 0\r\nvt 0.3344 0\r\nvt 0.3344 0.0002\r\nvt 0.3244 0.0002\r\nvt 0.3435 0\r\nvt 0.3435 0.0002\r\nvt 0.3573 0\r\nvt 0.3573 0.0002\r\nvt 0.3513 0.0002\r\nvt 0.3513 0\r\nvt 0.4342 0.0026\r\nvt 0.4429 0.0026\r\nvt 0.4252 0.0026\r\nvt 0.4252 0.001\r\nvt 0.4342 0.001\r\nvt 0.4429 0.001\r\nvt 0.4073 0.0026\r\nvt 0.4161 0.0026\r\nvt 0.4073 0.001\r\nvt 0.4161 0.001\r\nvt 0.4073 0\r\nvt 0.4161 0\r\nvt 0.4161 0.0002\r\nvt 0.4073 0.0002\r\nvt 0.4252 0\r\nvt 0.4252 0.0002\r\nvt 0.4429 0\r\nvt 0.4429 0.0002\r\nvt 0.4342 0.0002\r\nvt 0.4342 0\r\nvt 0.3991 0.0054\r\nvt 0.3991 0.0026\r\nvt 0.3916 0.0054\r\nvt 0.3916 0.0026\r\nvt 0.3916 0.001\r\nvt 0.3991 0.001\r\nvt 0.3795 0.0054\r\nvt 0.3795 0.0026\r\nvt 0.385 0.0026\r\nvt 0.385 0.0054\r\nvt 0.3795 0.001\r\nvt 0.385 0.001\r\nvt 0.3795 0\r\nvt 0.385 0\r\nvt 0.385 0.0002\r\nvt 0.3795 0.0002\r\nvt 0.3916 0\r\nvt 0.3916 0.0002\r\nvt 0.3991 0.0002\r\nvt 0.3991 0\r\nvt 0.274 0.0026\r\nvt 0.2831 0.0026\r\nvt 0.2658 0.0026\r\nvt 0.2658 0.001\r\nvt 0.274 0.001\r\nvt 0.2831 0.001\r\nvt 0.2512 0.0026\r\nvt 0.2583 0.0026\r\nvt 0.2512 0.001\r\nvt 0.2583 0.001\r\nvt 0.2512 0\r\nvt 0.2583 0\r\nvt 0.2583 0.0002\r\nvt 0.2512 0.0002\r\nvt 0.2658 0\r\nvt 0.2658 0.0002\r\nvt 0.2831 0\r\nvt 0.2831 0.0002\r\nvt 0.274 0.0002\r\nvt 0.274 0\r\nvt 0.314 0.0026\r\nvt 0.3034 0.0026\r\nvt 0.3034 0.001\r\nvt 0.314 0.001\r\nvt 0.2929 0.0026\r\nvt 0.2929 0.001\r\nvt 0.2929 0\r\nvt 0.2929 0.0002\r\nvt 0.3034 0\r\nvt 0.3034 0.0002\r\nvt 0.314 0.0002\r\nvt 0.314 0\r\nvt 0.2445 0.0026\r\nvt 0.2382 0.0026\r\nvt 0.2382 0.001\r\nvt 0.2445 0.001\r\nvt 0.2321 0.0026\r\nvt 0.2321 0.001\r\nvt 0.2321 0\r\nvt 0.2321 0.0002\r\nvt 0.2382 0\r\nvt 0.2382 0.0002\r\nvt 0.2445 0.0002\r\nvt 0.2445 0\r\nvt 0.1146 0.0054\r\nvt 0.1146 0.0026\r\nvt 0.1111 0.0054\r\nvt 0.1111 0.0026\r\nvt 0.1111 0.001\r\nvt 0.1146 0.001\r\nvt 0.1067 0.0054\r\nvt 0.1067 0.0026\r\nvt 0.1086 0.0026\r\nvt 0.1086 0.0054\r\nvt 0.1067 0.001\r\nvt 0.1086 0.001\r\nvt 0.1067 0\r\nvt 0.1086 0\r\nvt 0.1086 0.0002\r\nvt 0.1067 0.0002\r\nvt 0.1111 0\r\nvt 0.1111 0.0002\r\nvt 0.1146 0.0002\r\nvt 0.1146 0\r\nvt 0.0672 0.0026\r\nvt 0.0752 0.0026\r\nvt 0.0583 0.0026\r\nvt 0.0583 0.001\r\nvt 0.0672 0.001\r\nvt 0.0752 0.001\r\nvt 0.039 0.0026\r\nvt 0.0488 0.0026\r\nvt 0.039 0.001\r\nvt 0.0488 0.001\r\nvt 0.039 0\r\nvt 0.0488 0\r\nvt 0.0488 0.0002\r\nvt 0.039 0.0002\r\nvt 0.0583 0\r\nvt 0.0583 0.0002\r\nvt 0.0752 0\r\nvt 0.0752 0.0002\r\nvt 0.0672 0.0002\r\nvt 0.0672 0\r\nvt 0.4666 0.0026\r\nvt 0.459 0.0026\r\nvt 0.459 0.001\r\nvt 0.4666 0.001\r\nvt 0.4511 0.0026\r\nvt 0.4511 0.001\r\nvt 0.4511 0\r\nvt 0.4511 0.0002\r\nvt 0.459 0\r\nvt 0.459 0.0002\r\nvt 0.4666 0.0002\r\nvt 0.4666 0\r\nvt 0.1257 0.0438\r\nvt 0.1192 0.0438\r\nvt 0.1333 0.0438\r\nvt 0.1333 0.0445\r\nvt 0.1257 0.0445\r\nvt 0.1192 0.0445\r\nvt 0.1494 0.0438\r\nvt 0.1413 0.0438\r\nvt 0.1494 0.0445\r\nvt 0.1413 0.0445\r\nvt 0.1494 0.045\r\nvt 0.1413 0.045\r\nvt 0.1413 0.0448\r\nvt 0.1494 0.0448\r\nvt 0.1333 0.045\r\nvt 0.1333 0.0448\r\nvt 0.1192 0.045\r\nvt 0.1192 0.0448\r\nvt 0.1257 0.0448\r\nvt 0.1257 0.045\r\nvt 0.1572 0.0438\r\nvt 0.1649 0.0438\r\nvt 0.1649 0.0445\r\nvt 0.1572 0.0445\r\nvt 0.1794 0.0438\r\nvt 0.1723 0.0438\r\nvt 0.1794 0.0445\r\nvt 0.1723 0.0445\r\nvt 0.1794 0.045\r\nvt 0.1723 0.045\r\nvt 0.1723 0.0448\r\nvt 0.1794 0.0448\r\nvt 0.1649 0.045\r\nvt 0.1649 0.0448\r\nvt 0.1572 0.0448\r\nvt 0.1572 0.045\r\nvt 0.1861 0.0438\r\nvt 0.1925 0.0438\r\nvt 0.1925 0.0445\r\nvt 0.1861 0.0445\r\nvt 0.2042 0.0438\r\nvt 0.1985 0.0438\r\nvt 0.2042 0.0445\r\nvt 0.1985 0.0445\r\nvt 0.2042 0.045\r\nvt 0.1985 0.045\r\nvt 0.1985 0.0448\r\nvt 0.2042 0.0448\r\nvt 0.1925 0.045\r\nvt 0.1925 0.0448\r\nvt 0.1861 0.0448\r\nvt 0.1861 0.045\r\nvt 0.417 0.0438\r\nvt 0.408 0.0438\r\nvt 0.4263 0.0438\r\nvt 0.4263 0.0445\r\nvt 0.417 0.0445\r\nvt 0.408 0.0445\r\nvt 0.4441 0.0438\r\nvt 0.4354 0.0438\r\nvt 0.4441 0.0445\r\nvt 0.4354 0.0445\r\nvt 0.4441 0.045\r\nvt 0.4354 0.045\r\nvt 0.4354 0.0448\r\nvt 0.4441 0.0448\r\nvt 0.4263 0.045\r\nvt 0.4263 0.0448\r\nvt 0.408 0.045\r\nvt 0.408 0.0448\r\nvt 0.417 0.0448\r\nvt 0.417 0.045\r\nvt 0.2315 0.0438\r\nvt 0.2257 0.0438\r\nvt 0.2376 0.0438\r\nvt 0.2376 0.0445\r\nvt 0.2315 0.0445\r\nvt 0.2257 0.0445\r\nvt 0.2507 0.0438\r\nvt 0.244 0.0438\r\nvt 0.2507 0.0445\r\nvt 0.244 0.0445\r\nvt 0.2507 0.045\r\nvt 0.244 0.045\r\nvt 0.244 0.0448\r\nvt 0.2507 0.0448\r\nvt 0.2376 0.045\r\nvt 0.2376 0.0448\r\nvt 0.2257 0.045\r\nvt 0.2257 0.0448\r\nvt 0.2315 0.0448\r\nvt 0.2315 0.045\r\nvt 0.0823 0.0423\r\nvt 0.0823 0.0438\r\nvt 0.0753 0.0438\r\nvt 0.0883 0.0423\r\nvt 0.0883 0.0438\r\nvt 0.0883 0.0445\r\nvt 0.0823 0.0445\r\nvt 0.0753 0.0445\r\nvt 0.0974 0.0423\r\nvt 0.0974 0.0438\r\nvt 0.0934 0.0438\r\nvt 0.0934 0.0423\r\nvt 0.0974 0.0445\r\nvt 0.0934 0.0445\r\nvt 0.0974 0.045\r\nvt 0.0934 0.045\r\nvt 0.0934 0.0448\r\nvt 0.0974 0.0448\r\nvt 0.0883 0.045\r\nvt 0.0883 0.0448\r\nvt 0.0753 0.045\r\nvt 0.0753 0.0448\r\nvt 0.0823 0.0448\r\nvt 0.0823 0.045\r\nvt 0.0488 0.0438\r\nvt 0.039 0.0438\r\nvt 0.0583 0.0438\r\nvt 0.0583 0.0445\r\nvt 0.0488 0.0445\r\nvt 0.039 0.0445\r\nvt 0.0672 0.0438\r\nvt 0.0672 0.0445\r\nvt 0.0672 0.045\r\nvt 0.0672 0.0448\r\nvt 0.0583 0.045\r\nvt 0.0583 0.0448\r\nvt 0.039 0.045\r\nvt 0.039 0.0448\r\nvt 0.0488 0.0448\r\nvt 0.0488 0.045\r\nvt 0.0096 0.0438\r\nvt 0 0.0438\r\nvt 0.0193 0.0438\r\nvt 0.0193 0.0445\r\nvt 0.0096 0.0445\r\nvt 0 0.0445\r\nvt 0.0291 0.0438\r\nvt 0.0291 0.0445\r\nvt 0.0291 0.045\r\nvt 0.0291 0.0448\r\nvt 0.0193 0.045\r\nvt 0.0193 0.0448\r\nvt 0 0.045\r\nvt 0 0.0448\r\nvt 0.0096 0.0448\r\nvt 0.0096 0.045\r\nvt 0.2095 0.0438\r\nvt 0.2148 0.0438\r\nvt 0.2148 0.0445\r\nvt 0.2095 0.0445\r\nvt 0.2201 0.0438\r\nvt 0.2201 0.0445\r\nvt 0.2201 0.045\r\nvt 0.2201 0.0448\r\nvt 0.2148 0.045\r\nvt 0.2148 0.0448\r\nvt 0.2095 0.0448\r\nvt 0.2095 0.045\r\nvt 0.3353 0.0438\r\nvt 0.3252 0.0438\r\nvt 0.3444 0.0438\r\nvt 0.3444 0.0445\r\nvt 0.3353 0.0445\r\nvt 0.3252 0.0445\r\nvt 0.3581 0.0438\r\nvt 0.3522 0.0438\r\nvt 0.3581 0.0445\r\nvt 0.3522 0.0445\r\nvt 0.3581 0.045\r\nvt 0.3522 0.045\r\nvt 0.3522 0.0448\r\nvt 0.3581 0.0448\r\nvt 0.3444 0.045\r\nvt 0.3444 0.0448\r\nvt 0.3252 0.045\r\nvt 0.3252 0.0448\r\nvt 0.3353 0.0448\r\nvt 0.3353 0.045\r\nvt 0.3623 0.0438\r\nvt 0.3652 0.0438\r\nvt 0.3652 0.0445\r\nvt 0.3623 0.0445\r\nvt 0.3689 0.0438\r\nvt 0.3672 0.0438\r\nvt 0.3689 0.0445\r\nvt 0.3672 0.0445\r\nvt 0.3689 0.045\r\nvt 0.3672 0.045\r\nvt 0.3672 0.0448\r\nvt 0.3689 0.0448\r\nvt 0.3652 0.045\r\nvt 0.3652 0.0448\r\nvt 0.3623 0.0448\r\nvt 0.3623 0.045\r\nvt 0.2577 0.0438\r\nvt 0.2654 0.0438\r\nvt 0.2654 0.0445\r\nvt 0.2577 0.0445\r\nvt 0.2831 0.0438\r\nvt 0.2738 0.0438\r\nvt 0.2831 0.0445\r\nvt 0.2738 0.0445\r\nvt 0.2831 0.045\r\nvt 0.2738 0.045\r\nvt 0.2738 0.0448\r\nvt 0.2831 0.0448\r\nvt 0.2654 0.045\r\nvt 0.2654 0.0448\r\nvt 0.2577 0.0448\r\nvt 0.2577 0.045\r\nvt 0.2932 0.0438\r\nvt 0.3038 0.0438\r\nvt 0.3038 0.0445\r\nvt 0.2932 0.0445\r\nvt 0.3146 0.0438\r\nvt 0.3146 0.0445\r\nvt 0.3146 0.045\r\nvt 0.3146 0.0448\r\nvt 0.3038 0.045\r\nvt 0.3038 0.0448\r\nvt 0.2932 0.0448\r\nvt 0.2932 0.045\r\nvt 0.0292 0.0026\r\nvt 0.0194 0.0026\r\nvt 0.0194 0.001\r\nvt 0.0292 0.001\r\nvt 0 0.0026\r\nvt 0.0097 0.0026\r\nvt 0 0.001\r\nvt 0.0097 0.001\r\nvt 0 0\r\nvt 0.0097 0\r\nvt 0.0097 0.0002\r\nvt 0 0.0002\r\nvt 0.0194 0\r\nvt 0.0194 0.0002\r\nvt 0.0292 0.0002\r\nvt 0.0292 0\r\nvt 0.4522 0.0438\r\nvt 0.4599 0.0438\r\nvt 0.4599 0.0445\r\nvt 0.4522 0.0445\r\nvt 0.4672 0.0438\r\nvt 0.4672 0.0445\r\nvt 0.4672 0.045\r\nvt 0.4672 0.0448\r\nvt 0.4599 0.045\r\nvt 0.4599 0.0448\r\nvt 0.4522 0.0448\r\nvt 0.4522 0.045\r\nvt 0.9544 0.1601\r\nvt 0.9542 0.1601\r\nvt 0.9542 0.1514\r\nvt 0.9544 0.1514\r\nvt 0.9539 0.1601\r\nvt 0.9539 0.1514\r\nvt 0.9539 0.1428\r\nvt 0.9542 0.1428\r\nvt 0.9544 0.1428\r\nvt 0.9535 0.1601\r\nvt 0.9535 0.1514\r\nvt 0.9537 0.1514\r\nvt 0.9537 0.1601\r\nvt 0.9535 0.1428\r\nvt 0.9537 0.1428\r\nvt 0.9535 0.1276\r\nvt 0.9537 0.1276\r\nvt 0.9537 0.1347\r\nvt 0.9535 0.1347\r\nvt 0.9539 0.1276\r\nvt 0.9539 0.1347\r\nvt 0.9544 0.1276\r\nvt 0.9544 0.1347\r\nvt 0.9542 0.1347\r\nvt 0.9542 0.1276\r\nvt 0.9544 0.192\r\nvt 0.9542 0.192\r\nvt 0.9542 0.1844\r\nvt 0.9544 0.1844\r\nvt 0.9539 0.192\r\nvt 0.9539 0.1844\r\nvt 0.9539 0.1765\r\nvt 0.9542 0.1765\r\nvt 0.9544 0.1765\r\nvt 0.9535 0.192\r\nvt 0.9535 0.1844\r\nvt 0.9537 0.1844\r\nvt 0.9537 0.192\r\nvt 0.9535 0.1765\r\nvt 0.9537 0.1765\r\nvt 0.9537 0.1684\r\nvt 0.9535 0.1684\r\nvt 0.9539 0.1684\r\nvt 0.9544 0.1684\r\nvt 0.9542 0.1684\r\nvt 0.9544 0.2188\r\nvt 0.9542 0.2188\r\nvt 0.9542 0.2128\r\nvt 0.9544 0.2128\r\nvt 0.9539 0.2188\r\nvt 0.9539 0.2128\r\nvt 0.9539 0.2063\r\nvt 0.9542 0.2063\r\nvt 0.9544 0.2063\r\nvt 0.9535 0.2188\r\nvt 0.9535 0.2128\r\nvt 0.9537 0.2128\r\nvt 0.9537 0.2188\r\nvt 0.9535 0.2063\r\nvt 0.9537 0.2063\r\nvt 0.9537 0.1993\r\nvt 0.9535 0.1993\r\nvt 0.9539 0.1993\r\nvt 0.9544 0.1993\r\nvt 0.9542 0.1993\r\nvt 0.9544 0.2405\r\nvt 0.9542 0.2405\r\nvt 0.9542 0.2351\r\nvt 0.9544 0.2351\r\nvt 0.9539 0.2405\r\nvt 0.9539 0.2351\r\nvt 0.9539 0.2298\r\nvt 0.9542 0.2298\r\nvt 0.9544 0.2298\r\nvt 0.9535 0.2405\r\nvt 0.9535 0.2351\r\nvt 0.9537 0.2351\r\nvt 0.9537 0.2405\r\nvt 0.9535 0.2298\r\nvt 0.9537 0.2298\r\nvt 0.9537 0.2244\r\nvt 0.9535 0.2244\r\nvt 0.9539 0.2244\r\nvt 0.9544 0.2244\r\nvt 0.9542 0.2244\r\nvt 0.9544 0.3765\r\nvt 0.9542 0.3765\r\nvt 0.9542 0.37\r\nvt 0.9544 0.37\r\nvt 0.9539 0.3765\r\nvt 0.9539 0.37\r\nvt 0.9539 0.3616\r\nvt 0.9542 0.3616\r\nvt 0.9544 0.3616\r\nvt 0.9535 0.3765\r\nvt 0.9535 0.37\r\nvt 0.9537 0.37\r\nvt 0.9537 0.3765\r\nvt 0.9535 0.3616\r\nvt 0.9537 0.3616\r\nvt 0.9535 0.3408\r\nvt 0.9537 0.3408\r\nvt 0.9537 0.3517\r\nvt 0.9535 0.3517\r\nvt 0.9539 0.3408\r\nvt 0.9539 0.3517\r\nvt 0.9544 0.3408\r\nvt 0.9544 0.3517\r\nvt 0.9542 0.3517\r\nvt 0.9542 0.3408\r\nvt 0.9544 0.4659\r\nvt 0.9542 0.4659\r\nvt 0.9542 0.4564\r\nvt 0.9544 0.4564\r\nvt 0.9539 0.4659\r\nvt 0.9539 0.4564\r\nvt 0.9539 0.4467\r\nvt 0.9542 0.4467\r\nvt 0.9544 0.4467\r\nvt 0.9535 0.4659\r\nvt 0.9535 0.4564\r\nvt 0.9537 0.4564\r\nvt 0.9537 0.4659\r\nvt 0.9535 0.4467\r\nvt 0.9537 0.4467\r\nvt 0.9535 0.4276\r\nvt 0.9537 0.4276\r\nvt 0.9537 0.437\r\nvt 0.9535 0.437\r\nvt 0.9539 0.4276\r\nvt 0.9539 0.437\r\nvt 0.9544 0.4276\r\nvt 0.9544 0.437\r\nvt 0.9542 0.437\r\nvt 0.9542 0.4276\r\nvt 0.9542 0.419\r\nvt 0.9544 0.419\r\nvt 0.9539 0.419\r\nvt 0.9539 0.4113\r\nvt 0.9542 0.4113\r\nvt 0.9544 0.4113\r\nvt 0.9535 0.419\r\nvt 0.9537 0.419\r\nvt 0.9535 0.4113\r\nvt 0.9537 0.4113\r\nvt 0.9535 0.3991\r\nvt 0.9537 0.3991\r\nvt 0.9537 0.4046\r\nvt 0.9535 0.4046\r\nvt 0.9539 0.3991\r\nvt 0.9539 0.4046\r\nvt 0.9544 0.3991\r\nvt 0.9544 0.4046\r\nvt 0.9542 0.4046\r\nvt 0.9542 0.3991\r\nvt 0.9544 0.2969\r\nvt 0.9542 0.2969\r\nvt 0.9542 0.2876\r\nvt 0.9544 0.2876\r\nvt 0.9539 0.2969\r\nvt 0.9539 0.2876\r\nvt 0.9539 0.2793\r\nvt 0.9542 0.2793\r\nvt 0.9544 0.2793\r\nvt 0.9535 0.2969\r\nvt 0.9535 0.2876\r\nvt 0.9537 0.2876\r\nvt 0.9537 0.2969\r\nvt 0.9535 0.2793\r\nvt 0.9537 0.2793\r\nvt 0.9535 0.265\r\nvt 0.9537 0.265\r\nvt 0.9537 0.2719\r\nvt 0.9535 0.2719\r\nvt 0.9539 0.265\r\nvt 0.9539 0.2719\r\nvt 0.9544 0.265\r\nvt 0.9544 0.2719\r\nvt 0.9542 0.2719\r\nvt 0.9542 0.265\r\nvt 0.9542 0.3295\r\nvt 0.9544 0.3295\r\nvt 0.9539 0.3295\r\nvt 0.9539 0.3182\r\nvt 0.9542 0.3182\r\nvt 0.9544 0.3182\r\nvt 0.9535 0.3295\r\nvt 0.9537 0.3295\r\nvt 0.9535 0.3182\r\nvt 0.9537 0.3182\r\nvt 0.9537 0.3072\r\nvt 0.9535 0.3072\r\nvt 0.9539 0.3072\r\nvt 0.9544 0.3072\r\nvt 0.9542 0.3072\r\nvt 0.9542 0.2585\r\nvt 0.9544 0.2585\r\nvt 0.9539 0.2585\r\nvt 0.9539 0.2523\r\nvt 0.9542 0.2523\r\nvt 0.9544 0.2523\r\nvt 0.9535 0.2585\r\nvt 0.9537 0.2585\r\nvt 0.9535 0.2523\r\nvt 0.9537 0.2523\r\nvt 0.9537 0.2463\r\nvt 0.9535 0.2463\r\nvt 0.9539 0.2463\r\nvt 0.9544 0.2463\r\nvt 0.9542 0.2463\r\nvt 0.9542 0.122\r\nvt 0.9544 0.122\r\nvt 0.9539 0.122\r\nvt 0.9539 0.118\r\nvt 0.9542 0.118\r\nvt 0.9544 0.118\r\nvt 0.9535 0.122\r\nvt 0.9537 0.122\r\nvt 0.9535 0.118\r\nvt 0.9537 0.118\r\nvt 0.9535 0.113\r\nvt 0.9537 0.113\r\nvt 0.9537 0.1151\r\nvt 0.9535 0.1151\r\nvt 0.9539 0.113\r\nvt 0.9539 0.1151\r\nvt 0.9544 0.113\r\nvt 0.9544 0.1151\r\nvt 0.9542 0.1151\r\nvt 0.9542 0.113\r\nvt 0.9544 0.0805\r\nvt 0.9542 0.0805\r\nvt 0.9542 0.0718\r\nvt 0.9544 0.0718\r\nvt 0.9539 0.0805\r\nvt 0.9539 0.0718\r\nvt 0.9539 0.0622\r\nvt 0.9542 0.0622\r\nvt 0.9544 0.0622\r\nvt 0.9535 0.0805\r\nvt 0.9535 0.0718\r\nvt 0.9537 0.0718\r\nvt 0.9537 0.0805\r\nvt 0.9535 0.0622\r\nvt 0.9537 0.0622\r\nvt 0.9535 0.0415\r\nvt 0.9537 0.0415\r\nvt 0.9537 0.052\r\nvt 0.9535 0.052\r\nvt 0.9539 0.0415\r\nvt 0.9539 0.052\r\nvt 0.9544 0.0415\r\nvt 0.9544 0.052\r\nvt 0.9542 0.052\r\nvt 0.9542 0.0415\r\nvt 0.9544 0.5001\r\nvt 0.9542 0.5001\r\nvt 0.9542 0.4918\r\nvt 0.9544 0.4918\r\nvt 0.9539 0.5001\r\nvt 0.9539 0.4918\r\nvt 0.9539 0.4835\r\nvt 0.9542 0.4835\r\nvt 0.9544 0.4835\r\nvt 0.9535 0.5001\r\nvt 0.9535 0.4918\r\nvt 0.9537 0.4918\r\nvt 0.9537 0.5001\r\nvt 0.9535 0.4835\r\nvt 0.9537 0.4835\r\nvt 0.9537 0.4749\r\nvt 0.9535 0.4749\r\nvt 0.9539 0.4749\r\nvt 0.9544 0.4749\r\nvt 0.9542 0.4749\r\nvt 0.9542 0.031\r\nvt 0.9544 0.031\r\nvt 0.9539 0.031\r\nvt 0.9539 0.0206\r\nvt 0.9542 0.0206\r\nvt 0.9544 0.0206\r\nvt 0.9535 0.031\r\nvt 0.9537 0.031\r\nvt 0.9535 0.0206\r\nvt 0.9537 0.0206\r\nvt 0.9535 0\r\nvt 0.9537 0\r\nvt 0.9537 0.0102\r\nvt 0.9535 0.0102\r\nvt 0.9539 0\r\nvt 0.9539 0.0102\r\nvt 0.9544 0\r\nvt 0.9544 0.0102\r\nvt 0.9542 0.0102\r\nvt 0.9542 0\r\nvt 0.9544 0.8683\r\nvt 0.9542 0.8683\r\nvt 0.9542 0.8631\r\nvt 0.9544 0.8631\r\nvt 0.9539 0.8683\r\nvt 0.9539 0.8631\r\nvt 0.9539 0.8562\r\nvt 0.9542 0.8562\r\nvt 0.9544 0.8562\r\nvt 0.9535 0.8683\r\nvt 0.9535 0.8631\r\nvt 0.9537 0.8631\r\nvt 0.9537 0.8683\r\nvt 0.9535 0.8562\r\nvt 0.9537 0.8562\r\nvt 0.9535 0.8397\r\nvt 0.9537 0.8397\r\nvt 0.9537 0.8482\r\nvt 0.9535 0.8482\r\nvt 0.9539 0.8397\r\nvt 0.9539 0.8482\r\nvt 0.9544 0.8397\r\nvt 0.9544 0.8482\r\nvt 0.9542 0.8482\r\nvt 0.9542 0.8397\r\nvt 0.9542 0.8311\r\nvt 0.9544 0.8311\r\nvt 0.9539 0.8311\r\nvt 0.9539 0.8227\r\nvt 0.9542 0.8227\r\nvt 0.9544 0.8227\r\nvt 0.9535 0.8311\r\nvt 0.9537 0.8311\r\nvt 0.9535 0.8227\r\nvt 0.9537 0.8227\r\nvt 0.9535 0.8066\r\nvt 0.9537 0.8066\r\nvt 0.9537 0.8145\r\nvt 0.9535 0.8145\r\nvt 0.9539 0.8066\r\nvt 0.9539 0.8145\r\nvt 0.9544 0.8066\r\nvt 0.9544 0.8145\r\nvt 0.9542 0.8145\r\nvt 0.9542 0.8066\r\nvt 0.9542 0.799\r\nvt 0.9544 0.799\r\nvt 0.9539 0.799\r\nvt 0.9539 0.7917\r\nvt 0.9542 0.7917\r\nvt 0.9544 0.7917\r\nvt 0.9535 0.799\r\nvt 0.9537 0.799\r\nvt 0.9535 0.7917\r\nvt 0.9537 0.7917\r\nvt 0.9535 0.7782\r\nvt 0.9537 0.7782\r\nvt 0.9537 0.7847\r\nvt 0.9535 0.7847\r\nvt 0.9539 0.7782\r\nvt 0.9539 0.7847\r\nvt 0.9544 0.7782\r\nvt 0.9544 0.7847\r\nvt 0.9542 0.7847\r\nvt 0.9542 0.7782\r\nvt 0.9542 0.7722\r\nvt 0.9544 0.7722\r\nvt 0.9539 0.7722\r\nvt 0.9539 0.7666\r\nvt 0.9542 0.7666\r\nvt 0.9544 0.7666\r\nvt 0.9535 0.7722\r\nvt 0.9537 0.7722\r\nvt 0.9535 0.7666\r\nvt 0.9537 0.7666\r\nvt 0.9535 0.756\r\nvt 0.9537 0.756\r\nvt 0.9537 0.7613\r\nvt 0.9535 0.7613\r\nvt 0.9539 0.756\r\nvt 0.9539 0.7613\r\nvt 0.9544 0.756\r\nvt 0.9544 0.7613\r\nvt 0.9542 0.7613\r\nvt 0.9542 0.756\r\nvt 0.9544 0.6613\r\nvt 0.9542 0.6613\r\nvt 0.9542 0.6501\r\nvt 0.9544 0.6501\r\nvt 0.9539 0.6613\r\nvt 0.9539 0.6501\r\nvt 0.9539 0.6394\r\nvt 0.9542 0.6394\r\nvt 0.9544 0.6394\r\nvt 0.9535 0.6613\r\nvt 0.9535 0.6501\r\nvt 0.9537 0.6501\r\nvt 0.9537 0.6613\r\nvt 0.9535 0.6394\r\nvt 0.9537 0.6394\r\nvt 0.9535 0.6215\r\nvt 0.9537 0.6215\r\nvt 0.9537 0.6297\r\nvt 0.9535 0.6297\r\nvt 0.9539 0.6215\r\nvt 0.9539 0.6297\r\nvt 0.9544 0.6215\r\nvt 0.9544 0.6297\r\nvt 0.9542 0.6297\r\nvt 0.9542 0.6215\r\nvt 0.9544 0.573\r\nvt 0.9542 0.573\r\nvt 0.9542 0.5643\r\nvt 0.9544 0.5643\r\nvt 0.9539 0.573\r\nvt 0.9539 0.5643\r\nvt 0.9539 0.555\r\nvt 0.9542 0.555\r\nvt 0.9544 0.555\r\nvt 0.9535 0.573\r\nvt 0.9535 0.5643\r\nvt 0.9537 0.5643\r\nvt 0.9537 0.573\r\nvt 0.9535 0.555\r\nvt 0.9537 0.555\r\nvt 0.9535 0.5355\r\nvt 0.9537 0.5355\r\nvt 0.9537 0.5452\r\nvt 0.9535 0.5452\r\nvt 0.9539 0.5355\r\nvt 0.9539 0.5452\r\nvt 0.9544 0.5355\r\nvt 0.9544 0.5452\r\nvt 0.9542 0.5452\r\nvt 0.9542 0.5355\r\nvt 0.9542 0.6151\r\nvt 0.9544 0.6151\r\nvt 0.9539 0.6151\r\nvt 0.9539 0.6107\r\nvt 0.9542 0.6107\r\nvt 0.9544 0.6107\r\nvt 0.9535 0.6151\r\nvt 0.9537 0.6151\r\nvt 0.9535 0.6107\r\nvt 0.9537 0.6107\r\nvt 0.9535 0.6055\r\nvt 0.9537 0.6055\r\nvt 0.9537 0.6076\r\nvt 0.9535 0.6076\r\nvt 0.9539 0.6055\r\nvt 0.9539 0.6076\r\nvt 0.9544 0.6055\r\nvt 0.9544 0.6076\r\nvt 0.9542 0.6076\r\nvt 0.9542 0.6055\r\nvt 0.9544 0.7325\r\nvt 0.9542 0.7325\r\nvt 0.9542 0.726\r\nvt 0.9544 0.726\r\nvt 0.9539 0.7325\r\nvt 0.9539 0.726\r\nvt 0.9539 0.7192\r\nvt 0.9542 0.7192\r\nvt 0.9544 0.7192\r\nvt 0.9535 0.7325\r\nvt 0.9535 0.726\r\nvt 0.9537 0.726\r\nvt 0.9537 0.7325\r\nvt 0.9535 0.7192\r\nvt 0.9537 0.7192\r\nvt 0.9535 0.7034\r\nvt 0.9537 0.7034\r\nvt 0.9537 0.7117\r\nvt 0.9535 0.7117\r\nvt 0.9539 0.7034\r\nvt 0.9539 0.7117\r\nvt 0.9544 0.7034\r\nvt 0.9544 0.7117\r\nvt 0.9542 0.7117\r\nvt 0.9542 0.7034\r\nvt 0.9542 0.694\r\nvt 0.9544 0.694\r\nvt 0.9539 0.694\r\nvt 0.9539 0.6836\r\nvt 0.9542 0.6836\r\nvt 0.9544 0.6836\r\nvt 0.9535 0.694\r\nvt 0.9537 0.694\r\nvt 0.9535 0.6836\r\nvt 0.9537 0.6836\r\nvt 0.9537 0.6726\r\nvt 0.9535 0.6726\r\nvt 0.9539 0.6726\r\nvt 0.9544 0.6726\r\nvt 0.9542 0.6726\r\nvt 0.9542 0.7505\r\nvt 0.9544 0.7505\r\nvt 0.9539 0.7505\r\nvt 0.9539 0.7448\r\nvt 0.9542 0.7448\r\nvt 0.9544 0.7448\r\nvt 0.9535 0.7505\r\nvt 0.9537 0.7505\r\nvt 0.9535 0.7448\r\nvt 0.9537 0.7448\r\nvt 0.9537 0.7388\r\nvt 0.9535 0.7388\r\nvt 0.9539 0.7388\r\nvt 0.9544 0.7388\r\nvt 0.9542 0.7388\r\nvt 0.9544 0.918\r\nvt 0.9542 0.918\r\nvt 0.9542 0.9094\r\nvt 0.9544 0.9094\r\nvt 0.9539 0.918\r\nvt 0.9539 0.9094\r\nvt 0.9539 0.9018\r\nvt 0.9542 0.9018\r\nvt 0.9544 0.9018\r\nvt 0.9535 0.918\r\nvt 0.9535 0.9094\r\nvt 0.9537 0.9094\r\nvt 0.9537 0.918\r\nvt 0.9535 0.9018\r\nvt 0.9537 0.9018\r\nvt 0.9535 0.89\r\nvt 0.9537 0.89\r\nvt 0.9537 0.8954\r\nvt 0.9535 0.8954\r\nvt 0.9539 0.89\r\nvt 0.9539 0.8954\r\nvt 0.9544 0.89\r\nvt 0.9544 0.8954\r\nvt 0.9542 0.8954\r\nvt 0.9542 0.89\r\nvt 0.9544 0.9587\r\nvt 0.9542 0.9587\r\nvt 0.9542 0.9482\r\nvt 0.9544 0.9482\r\nvt 0.9539 0.9587\r\nvt 0.9539 0.9482\r\nvt 0.9539 0.9377\r\nvt 0.9542 0.9377\r\nvt 0.9544 0.9377\r\nvt 0.9535 0.9587\r\nvt 0.9535 0.9482\r\nvt 0.9537 0.9482\r\nvt 0.9537 0.9587\r\nvt 0.9535 0.9377\r\nvt 0.9537 0.9377\r\nvt 0.9537 0.9275\r\nvt 0.9535 0.9275\r\nvt 0.9539 0.9275\r\nvt 0.9544 0.9275\r\nvt 0.9542 0.9275\r\nvt 0.9542 0.5261\r\nvt 0.9544 0.5261\r\nvt 0.9539 0.5261\r\nvt 0.9539 0.5171\r\nvt 0.9542 0.5171\r\nvt 0.9544 0.5171\r\nvt 0.9535 0.5261\r\nvt 0.9537 0.5261\r\nvt 0.9535 0.5171\r\nvt 0.9537 0.5171\r\nvt 0.9537 0.5085\r\nvt 0.9535 0.5085\r\nvt 0.9539 0.5085\r\nvt 0.9544 0.5085\r\nvt 0.9542 0.5085\r\nvt 0.9544 1\r\nvt 0.9542 1\r\nvt 0.9542 0.9897\r\nvt 0.9544 0.9897\r\nvt 0.9539 1\r\nvt 0.9539 0.9897\r\nvt 0.9539 0.9795\r\nvt 0.9542 0.9795\r\nvt 0.9544 0.9795\r\nvt 0.9535 1\r\nvt 0.9535 0.9897\r\nvt 0.9537 0.9897\r\nvt 0.9537 1\r\nvt 0.9535 0.9795\r\nvt 0.9537 0.9795\r\nvt 0.9537 0.9691\r\nvt 0.9535 0.9691\r\nvt 0.9539 0.9691\r\nvt 0.9544 0.9691\r\nvt 0.9542 0.9691\r\nvt 0.8291 0.0452\r\nvt 0.8239 0.0452\r\nvt 0.8291 0.0454\r\nvt 0.8239 0.0454\r\nvt 0.8173 0.0454\r\nvt 0.8173 0.0452\r\nvt 0.8291 0.0458\r\nvt 0.8239 0.0458\r\nvt 0.8239 0.0456\r\nvt 0.8291 0.0456\r\nvt 0.8173 0.0458\r\nvt 0.8173 0.0456\r\nvt 0.8015 0.0458\r\nvt 0.8015 0.0456\r\nvt 0.8096 0.0456\r\nvt 0.8096 0.0458\r\nvt 0.8015 0.0454\r\nvt 0.8096 0.0454\r\nvt 0.8096 0.0452\r\nvt 0.8015 0.0452\r\nvt 0.7934 0.0452\r\nvt 0.7934 0.0454\r\nvt 0.7855 0.0454\r\nvt 0.7855 0.0452\r\nvt 0.7934 0.0458\r\nvt 0.7934 0.0456\r\nvt 0.7855 0.0458\r\nvt 0.7855 0.0456\r\nvt 0.7706 0.0458\r\nvt 0.7706 0.0456\r\nvt 0.7779 0.0456\r\nvt 0.7779 0.0458\r\nvt 0.7706 0.0454\r\nvt 0.7779 0.0454\r\nvt 0.7779 0.0452\r\nvt 0.7706 0.0452\r\nvt 0.7636 0.0452\r\nvt 0.7636 0.0454\r\nvt 0.7569 0.0454\r\nvt 0.7569 0.0452\r\nvt 0.7636 0.0458\r\nvt 0.7636 0.0456\r\nvt 0.7569 0.0458\r\nvt 0.7569 0.0456\r\nvt 0.7444 0.0458\r\nvt 0.7444 0.0456\r\nvt 0.7505 0.0456\r\nvt 0.7505 0.0458\r\nvt 0.7444 0.0454\r\nvt 0.7505 0.0454\r\nvt 0.7505 0.0452\r\nvt 0.7444 0.0452\r\nvt 0.5419 0.0452\r\nvt 0.5335 0.0452\r\nvt 0.5419 0.0454\r\nvt 0.5335 0.0454\r\nvt 0.5246 0.0454\r\nvt 0.5246 0.0452\r\nvt 0.5419 0.0458\r\nvt 0.5335 0.0458\r\nvt 0.5335 0.0456\r\nvt 0.5419 0.0456\r\nvt 0.5246 0.0458\r\nvt 0.5246 0.0456\r\nvt 0.5064 0.0458\r\nvt 0.5064 0.0456\r\nvt 0.5155 0.0456\r\nvt 0.5155 0.0458\r\nvt 0.5064 0.0454\r\nvt 0.5155 0.0454\r\nvt 0.5155 0.0452\r\nvt 0.5064 0.0452\r\nvt 0.7224 0.0452\r\nvt 0.7169 0.0452\r\nvt 0.7224 0.0454\r\nvt 0.7169 0.0454\r\nvt 0.711 0.0454\r\nvt 0.711 0.0452\r\nvt 0.7224 0.0458\r\nvt 0.7169 0.0458\r\nvt 0.7169 0.0456\r\nvt 0.7224 0.0456\r\nvt 0.711 0.0458\r\nvt 0.711 0.0456\r\nvt 0.6985 0.0458\r\nvt 0.6985 0.0456\r\nvt 0.7049 0.0456\r\nvt 0.7049 0.0458\r\nvt 0.6985 0.0454\r\nvt 0.7049 0.0454\r\nvt 0.7049 0.0452\r\nvt 0.6985 0.0452\r\nvt 0.8759 0.0452\r\nvt 0.8677 0.0452\r\nvt 0.8677 0.045\r\nvt 0.8759 0.0454\r\nvt 0.8677 0.0454\r\nvt 0.8605 0.0454\r\nvt 0.8605 0.0452\r\nvt 0.8605 0.045\r\nvt 0.8759 0.0458\r\nvt 0.8677 0.0458\r\nvt 0.8677 0.0456\r\nvt 0.8759 0.0456\r\nvt 0.8605 0.0458\r\nvt 0.8605 0.0456\r\nvt 0.8492 0.0458\r\nvt 0.8492 0.0456\r\nvt 0.8543 0.0456\r\nvt 0.8543 0.0458\r\nvt 0.8492 0.0454\r\nvt 0.8543 0.0454\r\nvt 0.8492 0.045\r\nvt 0.8543 0.045\r\nvt 0.8543 0.0452\r\nvt 0.8492 0.0452\r\nvt 0.914 0.0452\r\nvt 0.9042 0.0452\r\nvt 0.914 0.0454\r\nvt 0.9042 0.0454\r\nvt 0.8944 0.0454\r\nvt 0.8944 0.0452\r\nvt 0.914 0.0458\r\nvt 0.9042 0.0458\r\nvt 0.9042 0.0456\r\nvt 0.914 0.0456\r\nvt 0.8944 0.0458\r\nvt 0.8944 0.0456\r\nvt 0.8848 0.0456\r\nvt 0.8848 0.0458\r\nvt 0.8848 0.0454\r\nvt 0.8848 0.0452\r\nvt 0.953 0.0452\r\nvt 0.9431 0.0452\r\nvt 0.953 0.0454\r\nvt 0.9431 0.0454\r\nvt 0.9335 0.0454\r\nvt 0.9335 0.0452\r\nvt 0.953 0.0458\r\nvt 0.9431 0.0458\r\nvt 0.9431 0.0456\r\nvt 0.953 0.0456\r\nvt 0.9335 0.0458\r\nvt 0.9335 0.0456\r\nvt 0.9238 0.0456\r\nvt 0.9238 0.0458\r\nvt 0.9238 0.0454\r\nvt 0.9238 0.0452\r\nvt 0.7386 0.0452\r\nvt 0.7386 0.0454\r\nvt 0.7331 0.0454\r\nvt 0.7331 0.0452\r\nvt 0.7386 0.0458\r\nvt 0.7386 0.0456\r\nvt 0.7331 0.0458\r\nvt 0.7331 0.0456\r\nvt 0.7278 0.0456\r\nvt 0.7278 0.0458\r\nvt 0.7278 0.0454\r\nvt 0.7278 0.0452\r\nvt 0.6285 0.0452\r\nvt 0.6179 0.0452\r\nvt 0.6285 0.0454\r\nvt 0.6179 0.0454\r\nvt 0.6077 0.0454\r\nvt 0.6077 0.0452\r\nvt 0.6285 0.0458\r\nvt 0.6179 0.0458\r\nvt 0.6179 0.0456\r\nvt 0.6285 0.0456\r\nvt 0.6077 0.0458\r\nvt 0.6077 0.0456\r\nvt 0.5906 0.0458\r\nvt 0.5906 0.0456\r\nvt 0.5984 0.0456\r\nvt 0.5984 0.0458\r\nvt 0.5906 0.0454\r\nvt 0.5984 0.0454\r\nvt 0.5984 0.0452\r\nvt 0.5906 0.0452\r\nvt 0.5835 0.0452\r\nvt 0.5835 0.045\r\nvt 0.5835 0.0454\r\nvt 0.5793 0.0454\r\nvt 0.5793 0.0452\r\nvt 0.5793 0.045\r\nvt 0.5835 0.0458\r\nvt 0.5835 0.0456\r\nvt 0.5793 0.0458\r\nvt 0.5793 0.0456\r\nvt 0.5745 0.0458\r\nvt 0.5745 0.0456\r\nvt 0.5765 0.0456\r\nvt 0.5765 0.0458\r\nvt 0.5745 0.0454\r\nvt 0.5765 0.0454\r\nvt 0.5745 0.045\r\nvt 0.5765 0.045\r\nvt 0.5765 0.0452\r\nvt 0.5745 0.0452\r\nvt 0.6917 0.0452\r\nvt 0.6917 0.0454\r\nvt 0.6847 0.0454\r\nvt 0.6847 0.0452\r\nvt 0.6917 0.0458\r\nvt 0.6917 0.0456\r\nvt 0.6847 0.0458\r\nvt 0.6847 0.0456\r\nvt 0.6688 0.0458\r\nvt 0.6688 0.0456\r\nvt 0.6771 0.0456\r\nvt 0.6771 0.0458\r\nvt 0.6688 0.0454\r\nvt 0.6771 0.0454\r\nvt 0.6771 0.0452\r\nvt 0.6688 0.0452\r\nvt 0.6597 0.0452\r\nvt 0.6597 0.0454\r\nvt 0.6497 0.0454\r\nvt 0.6497 0.0452\r\nvt 0.6597 0.0458\r\nvt 0.6597 0.0456\r\nvt 0.6497 0.0458\r\nvt 0.6497 0.0456\r\nvt 0.6392 0.0456\r\nvt 0.6392 0.0458\r\nvt 0.6392 0.0454\r\nvt 0.6392 0.0452\r\nvt 0.4977 0.0452\r\nvt 0.4977 0.0454\r\nvt 0.4896 0.0454\r\nvt 0.4896 0.0452\r\nvt 0.4977 0.0458\r\nvt 0.4977 0.0456\r\nvt 0.4896 0.0458\r\nvt 0.4896 0.0456\r\nvt 0.4745 0.0458\r\nvt 0.4745 0.0456\r\nvt 0.4819 0.0456\r\nvt 0.4819 0.0458\r\nvt 0.4745 0.0454\r\nvt 0.4819 0.0454\r\nvt 0.4819 0.0452\r\nvt 0.4745 0.0452\r\nvt 0.1494 0.0452\r\nvt 0.1413 0.0452\r\nvt 0.1494 0.0454\r\nvt 0.1413 0.0454\r\nvt 0.1333 0.0454\r\nvt 0.1333 0.0452\r\nvt 0.1494 0.0458\r\nvt 0.1413 0.0458\r\nvt 0.1413 0.0456\r\nvt 0.1494 0.0456\r\nvt 0.1333 0.0458\r\nvt 0.1333 0.0456\r\nvt 0.1192 0.0458\r\nvt 0.1192 0.0456\r\nvt 0.1257 0.0456\r\nvt 0.1257 0.0458\r\nvt 0.1192 0.0454\r\nvt 0.1257 0.0454\r\nvt 0.1257 0.0452\r\nvt 0.1192 0.0452\r\nvt 0.1794 0.0452\r\nvt 0.1723 0.0452\r\nvt 0.1794 0.0454\r\nvt 0.1723 0.0454\r\nvt 0.1649 0.0454\r\nvt 0.1649 0.0452\r\nvt 0.1794 0.0458\r\nvt 0.1723 0.0458\r\nvt 0.1723 0.0456\r\nvt 0.1794 0.0456\r\nvt 0.1649 0.0458\r\nvt 0.1649 0.0456\r\nvt 0.1572 0.0456\r\nvt 0.1572 0.0458\r\nvt 0.1572 0.0454\r\nvt 0.1572 0.0452\r\nvt 0.2042 0.0452\r\nvt 0.1985 0.0452\r\nvt 0.2042 0.0454\r\nvt 0.1985 0.0454\r\nvt 0.1925 0.0454\r\nvt 0.1925 0.0452\r\nvt 0.2042 0.0458\r\nvt 0.1985 0.0458\r\nvt 0.1985 0.0456\r\nvt 0.2042 0.0456\r\nvt 0.1925 0.0458\r\nvt 0.1925 0.0456\r\nvt 0.1861 0.0456\r\nvt 0.1861 0.0458\r\nvt 0.1861 0.0454\r\nvt 0.1861 0.0452\r\nvt 0.4441 0.0452\r\nvt 0.4354 0.0452\r\nvt 0.4441 0.0454\r\nvt 0.4354 0.0454\r\nvt 0.4263 0.0454\r\nvt 0.4263 0.0452\r\nvt 0.4441 0.0458\r\nvt 0.4354 0.0458\r\nvt 0.4354 0.0456\r\nvt 0.4441 0.0456\r\nvt 0.4263 0.0458\r\nvt 0.4263 0.0456\r\nvt 0.408 0.0458\r\nvt 0.408 0.0456\r\nvt 0.417 0.0456\r\nvt 0.417 0.0458\r\nvt 0.408 0.0454\r\nvt 0.417 0.0454\r\nvt 0.417 0.0452\r\nvt 0.408 0.0452\r\nvt 0.2507 0.0452\r\nvt 0.244 0.0452\r\nvt 0.2507 0.0454\r\nvt 0.244 0.0454\r\nvt 0.2376 0.0454\r\nvt 0.2376 0.0452\r\nvt 0.2507 0.0458\r\nvt 0.244 0.0458\r\nvt 0.244 0.0456\r\nvt 0.2507 0.0456\r\nvt 0.2376 0.0458\r\nvt 0.2376 0.0456\r\nvt 0.2257 0.0458\r\nvt 0.2257 0.0456\r\nvt 0.2315 0.0456\r\nvt 0.2315 0.0458\r\nvt 0.2257 0.0454\r\nvt 0.2315 0.0454\r\nvt 0.2315 0.0452\r\nvt 0.2257 0.0452\r\nvt 0.1142 0.0452\r\nvt 0.1142 0.045\r\nvt 0.1142 0.0454\r\nvt 0.1107 0.0454\r\nvt 0.1107 0.0452\r\nvt 0.1107 0.045\r\nvt 0.1142 0.0458\r\nvt 0.1142 0.0456\r\nvt 0.1107 0.0458\r\nvt 0.1107 0.0456\r\nvt 0.1064 0.0458\r\nvt 0.1064 0.0456\r\nvt 0.1082 0.0456\r\nvt 0.1082 0.0458\r\nvt 0.1064 0.0454\r\nvt 0.1082 0.0454\r\nvt 0.1064 0.045\r\nvt 0.1082 0.045\r\nvt 0.1082 0.0452\r\nvt 0.1064 0.0452\r\nvt 0.0753 0.0452\r\nvt 0.0672 0.0452\r\nvt 0.0753 0.0454\r\nvt 0.0672 0.0454\r\nvt 0.0583 0.0454\r\nvt 0.0583 0.0452\r\nvt 0.0753 0.0458\r\nvt 0.0672 0.0458\r\nvt 0.0672 0.0456\r\nvt 0.0753 0.0456\r\nvt 0.0583 0.0458\r\nvt 0.0583 0.0456\r\nvt 0.039 0.0458\r\nvt 0.039 0.0456\r\nvt 0.0488 0.0456\r\nvt 0.0488 0.0458\r\nvt 0.039 0.0454\r\nvt 0.0488 0.0454\r\nvt 0.0488 0.0452\r\nvt 0.039 0.0452\r\nvt 0.0291 0.0452\r\nvt 0.0291 0.0454\r\nvt 0.0193 0.0454\r\nvt 0.0193 0.0452\r\nvt 0.0291 0.0458\r\nvt 0.0291 0.0456\r\nvt 0.0193 0.0458\r\nvt 0.0193 0.0456\r\nvt 0 0.0458\r\nvt 0 0.0456\r\nvt 0.0096 0.0456\r\nvt 0.0096 0.0458\r\nvt 0 0.0454\r\nvt 0.0096 0.0454\r\nvt 0.0096 0.0452\r\nvt 0 0.0452\r\nvt 0.2201 0.0452\r\nvt 0.2201 0.0454\r\nvt 0.2148 0.0454\r\nvt 0.2148 0.0452\r\nvt 0.2201 0.0458\r\nvt 0.2201 0.0456\r\nvt 0.2148 0.0458\r\nvt 0.2148 0.0456\r\nvt 0.2095 0.0456\r\nvt 0.2095 0.0458\r\nvt 0.2095 0.0454\r\nvt 0.2095 0.0452\r\nvt 0.3581 0.0452\r\nvt 0.3522 0.0452\r\nvt 0.3581 0.0454\r\nvt 0.3522 0.0454\r\nvt 0.3444 0.0454\r\nvt 0.3444 0.0452\r\nvt 0.3581 0.0458\r\nvt 0.3522 0.0458\r\nvt 0.3522 0.0456\r\nvt 0.3581 0.0456\r\nvt 0.3444 0.0458\r\nvt 0.3444 0.0456\r\nvt 0.3252 0.0458\r\nvt 0.3252 0.0456\r\nvt 0.3353 0.0456\r\nvt 0.3353 0.0458\r\nvt 0.3252 0.0454\r\nvt 0.3353 0.0454\r\nvt 0.3353 0.0452\r\nvt 0.3252 0.0452\r\nvt 0.3995 0.0452\r\nvt 0.3995 0.045\r\nvt 0.3995 0.0454\r\nvt 0.3919 0.0454\r\nvt 0.3919 0.0452\r\nvt 0.3919 0.045\r\nvt 0.3995 0.0458\r\nvt 0.3995 0.0456\r\nvt 0.3919 0.0458\r\nvt 0.3919 0.0456\r\nvt 0.3797 0.0458\r\nvt 0.3797 0.0456\r\nvt 0.3852 0.0456\r\nvt 0.3852 0.0458\r\nvt 0.3797 0.0454\r\nvt 0.3852 0.0454\r\nvt 0.3797 0.045\r\nvt 0.3852 0.045\r\nvt 0.3852 0.0452\r\nvt 0.3797 0.0452\r\nvt 0.2831 0.0452\r\nvt 0.2738 0.0452\r\nvt 0.2831 0.0454\r\nvt 0.2738 0.0454\r\nvt 0.2654 0.0454\r\nvt 0.2654 0.0452\r\nvt 0.2831 0.0458\r\nvt 0.2738 0.0458\r\nvt 0.2738 0.0456\r\nvt 0.2831 0.0456\r\nvt 0.2654 0.0458\r\nvt 0.2654 0.0456\r\nvt 0.2577 0.0456\r\nvt 0.2577 0.0458\r\nvt 0.2577 0.0454\r\nvt 0.2577 0.0452\r\nvt 0.3146 0.0452\r\nvt 0.3146 0.0454\r\nvt 0.3038 0.0454\r\nvt 0.3038 0.0452\r\nvt 0.3146 0.0458\r\nvt 0.3146 0.0456\r\nvt 0.3038 0.0458\r\nvt 0.3038 0.0456\r\nvt 0.2932 0.0456\r\nvt 0.2932 0.0458\r\nvt 0.2932 0.0454\r\nvt 0.2932 0.0452\r\nvt 0.4672 0.0452\r\nvt 0.4672 0.0454\r\nvt 0.4599 0.0454\r\nvt 0.4599 0.0452\r\nvt 0.4672 0.0458\r\nvt 0.4672 0.0456\r\nvt 0.4599 0.0458\r\nvt 0.4599 0.0456\r\nvt 0.4522 0.0456\r\nvt 0.4522 0.0458\r\nvt 0.4522 0.0454\r\nvt 0.4522 0.0452\r\nvt 0.385 0.0093\r\nvt 0.3795 0.0093\r\nvt 0.3916 0.0093\r\nvt 0.3916 0.0135\r\nvt 0.385 0.0135\r\nvt 0.3795 0.0135\r\nvt 0.3991 0.0093\r\nvt 0.3991 0.0135\r\nvt 0.3991 0.0173\r\nvt 0.3916 0.0173\r\nvt 0.3795 0.0173\r\nvt 0.385 0.0173\r\nvt 0.3755 0.0279\r\nvt 0.3755 0.0249\r\nvt 0.3726 0.0279\r\nvt 0.3726 0.0249\r\nvt 0.3726 0.0231\r\nvt 0.3755 0.0231\r\nvt 0.3689 0.0249\r\nvt 0.3705 0.0249\r\nvt 0.3705 0.0279\r\nvt 0.3689 0.0231\r\nvt 0.3705 0.0231\r\nvt 0.3702 0.0201\r\nvt 0.3702 0.0217\r\nvt 0.3685 0.0217\r\nvt 0.3723 0.0201\r\nvt 0.3723 0.0217\r\nvt 0.3723 0.0228\r\nvt 0.3702 0.0228\r\nvt 0.3685 0.0228\r\nvt 0.3753 0.0217\r\nvt 0.3753 0.0201\r\nvt 0.3753 0.0228\r\nvt 0.3852 0.0318\r\nvt 0.3797 0.0318\r\nvt 0.3919 0.0318\r\nvt 0.3919 0.0359\r\nvt 0.3852 0.0359\r\nvt 0.3797 0.0359\r\nvt 0.3995 0.0318\r\nvt 0.3995 0.0359\r\nvt 0.3995 0.0423\r\nvt 0.3995 0.0396\r\nvt 0.3919 0.0423\r\nvt 0.3919 0.0396\r\nvt 0.3797 0.0423\r\nvt 0.3797 0.0396\r\nvt 0.3852 0.0396\r\nvt 0.3852 0.0423\r\nvt 0.3753 0.0054\r\nvt 0.3753 0.0026\r\nvt 0.3723 0.0054\r\nvt 0.3723 0.0026\r\nvt 0.3723 0.001\r\nvt 0.3753 0.001\r\nvt 0.3685 0.0026\r\nvt 0.3702 0.0026\r\nvt 0.3702 0.0054\r\nvt 0.3685 0.001\r\nvt 0.3702 0.001\r\nvt 0.3685 0\r\nvt 0.3702 0\r\nvt 0.3702 0.0002\r\nvt 0.3685 0.0002\r\nvt 0.3723 0\r\nvt 0.3723 0.0002\r\nvt 0.3753 0.0002\r\nvt 0.3753 0\r\nvt 0.3852 0.0438\r\nvt 0.3797 0.0438\r\nvt 0.3919 0.0438\r\nvt 0.3919 0.0445\r\nvt 0.3852 0.0445\r\nvt 0.3797 0.0445\r\nvt 0.3995 0.0438\r\nvt 0.3995 0.0445\r\nvt 0.3995 0.0448\r\nvt 0.3919 0.0448\r\nvt 0.3797 0.0448\r\nvt 0.3852 0.0448\r\nvt 0.9535 0.5969\r\nvt 0.9535 0.5928\r\nvt 0.9537 0.5928\r\nvt 0.9537 0.5969\r\nvt 0.9535 0.5873\r\nvt 0.9537 0.5873\r\nvt 0.9539 0.5873\r\nvt 0.9539 0.5928\r\nvt 0.9539 0.5969\r\nvt 0.9537 0.5806\r\nvt 0.9535 0.5806\r\nvt 0.9539 0.5806\r\nvt 0.9544 0.5806\r\nvt 0.9542 0.5806\r\nvt 0.9544 0.5873\r\nvt 0.9542 0.5873\r\nvt 0.9544 0.5969\r\nvt 0.9542 0.5969\r\nvt 0.9542 0.5928\r\nvt 0.9544 0.5928\r\nvt 0.3755 0.0458\r\nvt 0.3755 0.0456\r\nvt 0.3726 0.0458\r\nvt 0.3726 0.0456\r\nvt 0.3726 0.0454\r\nvt 0.3755 0.0454\r\nvt 0.3689 0.0458\r\nvt 0.3689 0.0456\r\nvt 0.3705 0.0456\r\nvt 0.3705 0.0458\r\nvt 0.3689 0.0454\r\nvt 0.3705 0.0454\r\nvt 0.3705 0.045\r\nvt 0.3705 0.0452\r\nvt 0.3689 0.0452\r\nvt 0.3726 0.045\r\nvt 0.3726 0.0452\r\nvt 0.3755 0.0452\r\nvt 0.3755 0.045\r\nvt 0.3702 0.0093\r\nvt 0.3723 0.0093\r\nvt 0.3723 0.0135\r\nvt 0.3702 0.0135\r\nvt 0.3753 0.0093\r\nvt 0.3753 0.0135\r\nvt 0.3753 0.0173\r\nvt 0.3723 0.0173\r\nvt 0.3702 0.0173\r\nvt 0.3705 0.0318\r\nvt 0.3726 0.0318\r\nvt 0.3726 0.0359\r\nvt 0.3705 0.0359\r\nvt 0.3755 0.0318\r\nvt 0.3755 0.0359\r\nvt 0.3755 0.0423\r\nvt 0.3755 0.0396\r\nvt 0.3726 0.0423\r\nvt 0.3726 0.0396\r\nvt 0.3705 0.0396\r\nvt 0.3705 0.0423\r\nvt 0.3705 0.0438\r\nvt 0.3726 0.0438\r\nvt 0.3726 0.0445\r\nvt 0.3705 0.0445\r\nvt 0.3755 0.0438\r\nvt 0.3755 0.0445\r\nvt 0.3755 0.0448\r\nvt 0.3726 0.0448\r\nvt 0.3705 0.0448\r\nvt 0.9535 0.6037\r\nvt 0.9537 0.6037\r\nvt 0.9535 0.602\r\nvt 0.9537 0.602\r\nvt 0.9539 0.602\r\nvt 0.9539 0.6037\r\nvt 0.9537 0.5999\r\nvt 0.9535 0.5999\r\nvt 0.9539 0.5999\r\nvt 0.9544 0.5999\r\nvt 0.9542 0.5999\r\nvt 0.9544 0.602\r\nvt 0.9542 0.602\r\nvt 0.9542 0.6037\r\nvt 0.9544 0.6037\r\nvt 0.3672 0.0249\r\nvt 0.3652 0.0249\r\nvt 0.3652 0.0231\r\nvt 0.3672 0.0231\r\nvt 0.3623 0.0249\r\nvt 0.3623 0.0231\r\nvt 0.3616 0.0217\r\nvt 0.3646 0.0217\r\nvt 0.3646 0.0228\r\nvt 0.3616 0.0228\r\nvt 0.3668 0.0217\r\nvt 0.3668 0.0228\r\nvt 0.3668 0.0026\r\nvt 0.3646 0.0026\r\nvt 0.3646 0.001\r\nvt 0.3668 0.001\r\nvt 0.3616 0.0026\r\nvt 0.3616 0.001\r\nvt 0.3616 0\r\nvt 0.3616 0.0002\r\nvt 0.3646 0\r\nvt 0.3646 0.0002\r\nvt 0.3668 0.0002\r\nvt 0.3668 0\r\nvt 0.3672 0.0458\r\nvt 0.3672 0.0456\r\nvt 0.3652 0.0458\r\nvt 0.3652 0.0456\r\nvt 0.3652 0.0454\r\nvt 0.3672 0.0454\r\nvt 0.3623 0.0456\r\nvt 0.3623 0.0458\r\nvt 0.3623 0.0454\r\nvt 0.3623 0.0452\r\nvt 0.3652 0.0452\r\nvt 0.3672 0.0452\r\nvt 0.562 0.0173\r\nvt 0.5662 0.0173\r\nvt 0.5566 0.0173\r\nvt 0.5566 0.0135\r\nvt 0.562 0.0135\r\nvt 0.5662 0.0135\r\nvt 0.5499 0.0173\r\nvt 0.5499 0.0135\r\nvt 0.5499 0.0054\r\nvt 0.5499 0.0093\r\nvt 0.5566 0.0054\r\nvt 0.5566 0.0093\r\nvt 0.5662 0.0054\r\nvt 0.5662 0.0093\r\nvt 0.562 0.0093\r\nvt 0.562 0.0054\r\nvt 0.5693 0.0201\r\nvt 0.5693 0.0217\r\nvt 0.5715 0.0201\r\nvt 0.5715 0.0217\r\nvt 0.5715 0.0228\r\nvt 0.5693 0.0228\r\nvt 0.5749 0.0217\r\nvt 0.5732 0.0217\r\nvt 0.5732 0.0201\r\nvt 0.5749 0.0228\r\nvt 0.5732 0.0228\r\nvt 0.5729 0.0279\r\nvt 0.5729 0.0249\r\nvt 0.5745 0.0249\r\nvt 0.5712 0.0279\r\nvt 0.5712 0.0249\r\nvt 0.5712 0.0231\r\nvt 0.5729 0.0231\r\nvt 0.5745 0.0231\r\nvt 0.569 0.0249\r\nvt 0.569 0.0279\r\nvt 0.569 0.0231\r\nvt 0.5617 0.0396\r\nvt 0.566 0.0396\r\nvt 0.5562 0.0396\r\nvt 0.5562 0.0359\r\nvt 0.5617 0.0359\r\nvt 0.566 0.0359\r\nvt 0.5495 0.0396\r\nvt 0.5495 0.0359\r\nvt 0.5495 0.0318\r\nvt 0.5562 0.0318\r\nvt 0.566 0.0318\r\nvt 0.5617 0.0318\r\nvt 0.562 0.0026\r\nvt 0.5662 0.0026\r\nvt 0.5566 0.0026\r\nvt 0.5566 0.001\r\nvt 0.562 0.001\r\nvt 0.5662 0.001\r\nvt 0.5499 0.0026\r\nvt 0.5499 0.001\r\nvt 0.5499 0\r\nvt 0.5499 0.0002\r\nvt 0.5566 0\r\nvt 0.5566 0.0002\r\nvt 0.5662 0\r\nvt 0.5662 0.0002\r\nvt 0.562 0.0002\r\nvt 0.562 0\r\nvt 0.569 0.0423\r\nvt 0.569 0.0438\r\nvt 0.5712 0.0423\r\nvt 0.5712 0.0438\r\nvt 0.5712 0.0445\r\nvt 0.569 0.0445\r\nvt 0.5745 0.0438\r\nvt 0.5729 0.0438\r\nvt 0.5729 0.0423\r\nvt 0.5745 0.0445\r\nvt 0.5729 0.0445\r\nvt 0.5729 0.045\r\nvt 0.5729 0.0448\r\nvt 0.5745 0.0448\r\nvt 0.5712 0.045\r\nvt 0.5712 0.0448\r\nvt 0.569 0.0448\r\nvt 0.569 0.045\r\nvt 0.9535 0.3949\r\nvt 0.9537 0.3949\r\nvt 0.9535 0.3918\r\nvt 0.9537 0.3918\r\nvt 0.9539 0.3918\r\nvt 0.9539 0.3949\r\nvt 0.9535 0.3876\r\nvt 0.9537 0.3876\r\nvt 0.9537 0.3894\r\nvt 0.9535 0.3894\r\nvt 0.9539 0.3876\r\nvt 0.9539 0.3894\r\nvt 0.9544 0.3876\r\nvt 0.9544 0.3894\r\nvt 0.9542 0.3894\r\nvt 0.9542 0.3876\r\nvt 0.9544 0.3918\r\nvt 0.9542 0.3918\r\nvt 0.9542 0.3949\r\nvt 0.9544 0.3949\r\nvt 0.566 0.0458\r\nvt 0.5617 0.0458\r\nvt 0.5617 0.0456\r\nvt 0.566 0.0456\r\nvt 0.5562 0.0458\r\nvt 0.5562 0.0456\r\nvt 0.5562 0.0454\r\nvt 0.5617 0.0454\r\nvt 0.566 0.0454\r\nvt 0.5495 0.0456\r\nvt 0.5495 0.0458\r\nvt 0.5495 0.0454\r\nvt 0.5495 0.0452\r\nvt 0.5562 0.0452\r\nvt 0.566 0.0452\r\nvt 0.5617 0.0452\r\nvt 0.5732 0.0173\r\nvt 0.5715 0.0173\r\nvt 0.5715 0.0135\r\nvt 0.5732 0.0135\r\nvt 0.5693 0.0173\r\nvt 0.5693 0.0135\r\nvt 0.5693 0.0054\r\nvt 0.5693 0.0093\r\nvt 0.5715 0.0054\r\nvt 0.5715 0.0093\r\nvt 0.5732 0.0093\r\nvt 0.5732 0.0054\r\nvt 0.5729 0.0396\r\nvt 0.5712 0.0396\r\nvt 0.5712 0.0359\r\nvt 0.5729 0.0359\r\nvt 0.569 0.0396\r\nvt 0.569 0.0359\r\nvt 0.569 0.0318\r\nvt 0.5712 0.0318\r\nvt 0.5729 0.0318\r\nvt 0.5732 0.0026\r\nvt 0.5715 0.0026\r\nvt 0.5715 0.001\r\nvt 0.5732 0.001\r\nvt 0.5693 0.0026\r\nvt 0.5693 0.001\r\nvt 0.5693 0\r\nvt 0.5693 0.0002\r\nvt 0.5715 0\r\nvt 0.5715 0.0002\r\nvt 0.5732 0.0002\r\nvt 0.5732 0\r\nvt 0.5729 0.0458\r\nvt 0.5729 0.0456\r\nvt 0.5712 0.0458\r\nvt 0.5712 0.0456\r\nvt 0.5712 0.0454\r\nvt 0.5729 0.0454\r\nvt 0.569 0.0456\r\nvt 0.569 0.0458\r\nvt 0.569 0.0454\r\nvt 0.569 0.0452\r\nvt 0.5712 0.0452\r\nvt 0.5729 0.0452\r\nvt 0.5769 0.0217\r\nvt 0.5797 0.0217\r\nvt 0.5797 0.0228\r\nvt 0.5769 0.0228\r\nvt 0.5839 0.0217\r\nvt 0.5839 0.0228\r\nvt 0.5835 0.0249\r\nvt 0.5793 0.0249\r\nvt 0.5793 0.0231\r\nvt 0.5835 0.0231\r\nvt 0.5765 0.0249\r\nvt 0.5765 0.0231\r\nvt 0.5765 0.0438\r\nvt 0.5793 0.0438\r\nvt 0.5793 0.0445\r\nvt 0.5765 0.0445\r\nvt 0.5835 0.0438\r\nvt 0.5835 0.0445\r\nvt 0.5835 0.0448\r\nvt 0.5793 0.0448\r\nvt 0.5765 0.0448\r\nvt 0.9535 0.3858\r\nvt 0.9537 0.3858\r\nvt 0.9535 0.3838\r\nvt 0.9537 0.3838\r\nvt 0.9539 0.3838\r\nvt 0.9539 0.3858\r\nvt 0.9537 0.3809\r\nvt 0.9535 0.3809\r\nvt 0.9539 0.3809\r\nvt 0.9544 0.3809\r\nvt 0.9542 0.3809\r\nvt 0.9544 0.3838\r\nvt 0.9542 0.3838\r\nvt 0.9542 0.3858\r\nvt 0.9544 0.3858\r\nvt 0.1006 0.0054\r\nvt 0.1006 0.0093\r\nvt 0.103 0.0054\r\nvt 0.103 0.0093\r\nvt 0.103 0.0135\r\nvt 0.1006 0.0135\r\nvt 0.1067 0.0093\r\nvt 0.1049 0.0093\r\nvt 0.1049 0.0054\r\nvt 0.1067 0.0135\r\nvt 0.1049 0.0135\r\nvt 0.1049 0.0201\r\nvt 0.1049 0.0173\r\nvt 0.1067 0.0173\r\nvt 0.103 0.0201\r\nvt 0.103 0.0173\r\nvt 0.1006 0.0173\r\nvt 0.1006 0.0201\r\nvt 0.0934 0.0396\r\nvt 0.0974 0.0396\r\nvt 0.0883 0.0396\r\nvt 0.0883 0.0359\r\nvt 0.0934 0.0359\r\nvt 0.0974 0.0359\r\nvt 0.0823 0.0396\r\nvt 0.0823 0.0359\r\nvt 0.0823 0.0279\r\nvt 0.0823 0.0318\r\nvt 0.0883 0.0279\r\nvt 0.0883 0.0318\r\nvt 0.0974 0.0279\r\nvt 0.0974 0.0318\r\nvt 0.0934 0.0318\r\nvt 0.0934 0.0279\r\nvt 0.0934 0.0249\r\nvt 0.0974 0.0249\r\nvt 0.0883 0.0249\r\nvt 0.0883 0.0231\r\nvt 0.0934 0.0231\r\nvt 0.0974 0.0231\r\nvt 0.0823 0.0249\r\nvt 0.0823 0.0231\r\nvt 0.0823 0.0217\r\nvt 0.0884 0.0217\r\nvt 0.0884 0.0228\r\nvt 0.0823 0.0228\r\nvt 0.0975 0.0217\r\nvt 0.0935 0.0217\r\nvt 0.0975 0.0228\r\nvt 0.0935 0.0228\r\nvt 0.0935 0.0026\r\nvt 0.0975 0.0026\r\nvt 0.0884 0.0026\r\nvt 0.0884 0.001\r\nvt 0.0935 0.001\r\nvt 0.0975 0.001\r\nvt 0.0823 0.0026\r\nvt 0.0823 0.001\r\nvt 0.0823 0\r\nvt 0.0823 0.0002\r\nvt 0.0884 0\r\nvt 0.0884 0.0002\r\nvt 0.0975 0\r\nvt 0.0975 0.0002\r\nvt 0.0935 0.0002\r\nvt 0.0935 0\r\nvt 0.1005 0.0423\r\nvt 0.1005 0.0438\r\nvt 0.1028 0.0423\r\nvt 0.1028 0.0438\r\nvt 0.1028 0.0445\r\nvt 0.1005 0.0445\r\nvt 0.1064 0.0438\r\nvt 0.1047 0.0438\r\nvt 0.1047 0.0423\r\nvt 0.1064 0.0445\r\nvt 0.1047 0.0445\r\nvt 0.1047 0.045\r\nvt 0.1047 0.0448\r\nvt 0.1064 0.0448\r\nvt 0.1028 0.045\r\nvt 0.1028 0.0448\r\nvt 0.1005 0.0448\r\nvt 0.1005 0.045\r\nvt 0.9535 0.8858\r\nvt 0.9537 0.8858\r\nvt 0.9535 0.8825\r\nvt 0.9537 0.8825\r\nvt 0.9539 0.8825\r\nvt 0.9539 0.8858\r\nvt 0.9535 0.8781\r\nvt 0.9537 0.8781\r\nvt 0.9537 0.8801\r\nvt 0.9535 0.8801\r\nvt 0.9539 0.8781\r\nvt 0.9539 0.8801\r\nvt 0.9544 0.8781\r\nvt 0.9544 0.8801\r\nvt 0.9542 0.8801\r\nvt 0.9542 0.8781\r\nvt 0.9544 0.8825\r\nvt 0.9542 0.8825\r\nvt 0.9542 0.8858\r\nvt 0.9544 0.8858\r\nvt 0.0974 0.0458\r\nvt 0.0934 0.0458\r\nvt 0.0934 0.0456\r\nvt 0.0974 0.0456\r\nvt 0.0883 0.0458\r\nvt 0.0883 0.0456\r\nvt 0.0883 0.0454\r\nvt 0.0934 0.0454\r\nvt 0.0974 0.0454\r\nvt 0.0823 0.0456\r\nvt 0.0823 0.0458\r\nvt 0.0823 0.0454\r\nvt 0.0823 0.0452\r\nvt 0.0883 0.0452\r\nvt 0.0974 0.0452\r\nvt 0.0934 0.0452\r\nvt 0.1047 0.0396\r\nvt 0.1028 0.0396\r\nvt 0.1028 0.0359\r\nvt 0.1047 0.0359\r\nvt 0.1005 0.0396\r\nvt 0.1005 0.0359\r\nvt 0.1005 0.0279\r\nvt 0.1005 0.0318\r\nvt 0.1028 0.0279\r\nvt 0.1028 0.0318\r\nvt 0.1047 0.0318\r\nvt 0.1047 0.0279\r\nvt 0.1047 0.0249\r\nvt 0.1028 0.0249\r\nvt 0.1028 0.0231\r\nvt 0.1047 0.0231\r\nvt 0.1005 0.0249\r\nvt 0.1005 0.0231\r\nvt 0.1006 0.0217\r\nvt 0.103 0.0217\r\nvt 0.103 0.0228\r\nvt 0.1006 0.0228\r\nvt 0.1049 0.0217\r\nvt 0.1049 0.0228\r\nvt 0.1049 0.0026\r\nvt 0.103 0.0026\r\nvt 0.103 0.001\r\nvt 0.1049 0.001\r\nvt 0.1006 0.0026\r\nvt 0.1006 0.001\r\nvt 0.1006 0\r\nvt 0.1006 0.0002\r\nvt 0.103 0\r\nvt 0.103 0.0002\r\nvt 0.1049 0.0002\r\nvt 0.1049 0\r\nvt 0.1047 0.0458\r\nvt 0.1047 0.0456\r\nvt 0.1028 0.0458\r\nvt 0.1028 0.0456\r\nvt 0.1028 0.0454\r\nvt 0.1047 0.0454\r\nvt 0.1005 0.0456\r\nvt 0.1005 0.0458\r\nvt 0.1005 0.0454\r\nvt 0.1005 0.0452\r\nvt 0.1028 0.0452\r\nvt 0.1047 0.0452\r\nvt 0.1086 0.0093\r\nvt 0.1111 0.0093\r\nvt 0.1111 0.0135\r\nvt 0.1086 0.0135\r\nvt 0.1146 0.0093\r\nvt 0.1146 0.0135\r\nvt 0.1146 0.0173\r\nvt 0.1111 0.0173\r\nvt 0.1086 0.0173\r\nvt 0.1082 0.0438\r\nvt 0.1107 0.0438\r\nvt 0.1107 0.0445\r\nvt 0.1082 0.0445\r\nvt 0.1142 0.0438\r\nvt 0.1142 0.0445\r\nvt 0.1142 0.0448\r\nvt 0.1107 0.0448\r\nvt 0.1082 0.0448\r\nvt 0.9535 0.8764\r\nvt 0.9537 0.8764\r\nvt 0.9535 0.8745\r\nvt 0.9537 0.8745\r\nvt 0.9539 0.8745\r\nvt 0.9539 0.8764\r\nvt 0.9537 0.872\r\nvt 0.9535 0.872\r\nvt 0.9539 0.872\r\nvt 0.9544 0.872\r\nvt 0.9542 0.872\r\nvt 0.9544 0.8745\r\nvt 0.9542 0.8745\r\nvt 0.9542 0.8764\r\nvt 0.9544 0.8764\r\nvt 0.8443 0.0201\r\nvt 0.8443 0.0173\r\nvt 0.8413 0.0201\r\nvt 0.8413 0.0173\r\nvt 0.8413 0.0135\r\nvt 0.8443 0.0135\r\nvt 0.8373 0.0173\r\nvt 0.8391 0.0173\r\nvt 0.8391 0.0201\r\nvt 0.8373 0.0135\r\nvt 0.8391 0.0135\r\nvt 0.8373 0.0054\r\nvt 0.8391 0.0054\r\nvt 0.8391 0.0093\r\nvt 0.8373 0.0093\r\nvt 0.8413 0.0054\r\nvt 0.8413 0.0093\r\nvt 0.8443 0.0093\r\nvt 0.8443 0.0054\r\nvt 0.8492 0.0279\r\nvt 0.8543 0.0279\r\nvt 0.8543 0.0318\r\nvt 0.8492 0.0318\r\nvt 0.8605 0.0279\r\nvt 0.8605 0.0318\r\nvt 0.8605 0.0359\r\nvt 0.8543 0.0359\r\nvt 0.8492 0.0359\r\nvt 0.8677 0.0318\r\nvt 0.8677 0.0279\r\nvt 0.8677 0.0359\r\nvt 0.8677 0.0423\r\nvt 0.8677 0.0396\r\nvt 0.8605 0.0423\r\nvt 0.8605 0.0396\r\nvt 0.8492 0.0423\r\nvt 0.8492 0.0396\r\nvt 0.8543 0.0396\r\nvt 0.8543 0.0423\r\nvt 0.8534 0.0217\r\nvt 0.8482 0.0217\r\nvt 0.8596 0.0217\r\nvt 0.8596 0.0228\r\nvt 0.8534 0.0228\r\nvt 0.8482 0.0228\r\nvt 0.8668 0.0217\r\nvt 0.8668 0.0228\r\nvt 0.8677 0.0249\r\nvt 0.8605 0.0249\r\nvt 0.8605 0.0231\r\nvt 0.8677 0.0231\r\nvt 0.8492 0.0249\r\nvt 0.8543 0.0249\r\nvt 0.8492 0.0231\r\nvt 0.8543 0.0231\r\nvt 0.8443 0.0026\r\nvt 0.8413 0.0026\r\nvt 0.8413 0.001\r\nvt 0.8443 0.001\r\nvt 0.8373 0.0026\r\nvt 0.8391 0.0026\r\nvt 0.8373 0.001\r\nvt 0.8391 0.001\r\nvt 0.8373 0\r\nvt 0.8391 0\r\nvt 0.8391 0.0002\r\nvt 0.8373 0.0002\r\nvt 0.8413 0\r\nvt 0.8413 0.0002\r\nvt 0.8443 0.0002\r\nvt 0.8443 0\r\nvt 0.8543 0.0438\r\nvt 0.8492 0.0438\r\nvt 0.8605 0.0438\r\nvt 0.8605 0.0445\r\nvt 0.8543 0.0445\r\nvt 0.8492 0.0445\r\nvt 0.8677 0.0438\r\nvt 0.8677 0.0445\r\nvt 0.8677 0.0448\r\nvt 0.8605 0.0448\r\nvt 0.8492 0.0448\r\nvt 0.8543 0.0448\r\nvt 0.9535 0.104\r\nvt 0.9535 0.0999\r\nvt 0.9537 0.0999\r\nvt 0.9537 0.104\r\nvt 0.9535 0.0946\r\nvt 0.9537 0.0946\r\nvt 0.9539 0.0946\r\nvt 0.9539 0.0999\r\nvt 0.9539 0.104\r\nvt 0.9537 0.0881\r\nvt 0.9535 0.0881\r\nvt 0.9539 0.0881\r\nvt 0.9544 0.0881\r\nvt 0.9542 0.0881\r\nvt 0.9544 0.0946\r\nvt 0.9542 0.0946\r\nvt 0.9544 0.104\r\nvt 0.9542 0.104\r\nvt 0.9542 0.0999\r\nvt 0.9544 0.0999\r\nvt 0.8452 0.0458\r\nvt 0.8452 0.0456\r\nvt 0.8424 0.0458\r\nvt 0.8424 0.0456\r\nvt 0.8424 0.0454\r\nvt 0.8452 0.0454\r\nvt 0.8386 0.0458\r\nvt 0.8386 0.0456\r\nvt 0.8403 0.0456\r\nvt 0.8403 0.0458\r\nvt 0.8386 0.0454\r\nvt 0.8403 0.0454\r\nvt 0.8403 0.045\r\nvt 0.8403 0.0452\r\nvt 0.8386 0.0452\r\nvt 0.8424 0.045\r\nvt 0.8424 0.0452\r\nvt 0.8452 0.0452\r\nvt 0.8452 0.045\r\nvt 0.8403 0.0279\r\nvt 0.8403 0.0318\r\nvt 0.8424 0.0279\r\nvt 0.8424 0.0318\r\nvt 0.8424 0.0359\r\nvt 0.8403 0.0359\r\nvt 0.8452 0.0318\r\nvt 0.8452 0.0279\r\nvt 0.8452 0.0359\r\nvt 0.8452 0.0423\r\nvt 0.8452 0.0396\r\nvt 0.8424 0.0423\r\nvt 0.8424 0.0396\r\nvt 0.8403 0.0396\r\nvt 0.8403 0.0423\r\nvt 0.8391 0.0217\r\nvt 0.8413 0.0217\r\nvt 0.8413 0.0228\r\nvt 0.8391 0.0228\r\nvt 0.8443 0.0217\r\nvt 0.8443 0.0228\r\nvt 0.8452 0.0249\r\nvt 0.8424 0.0249\r\nvt 0.8424 0.0231\r\nvt 0.8452 0.0231\r\nvt 0.8403 0.0249\r\nvt 0.8403 0.0231\r\nvt 0.8403 0.0438\r\nvt 0.8424 0.0438\r\nvt 0.8424 0.0445\r\nvt 0.8403 0.0445\r\nvt 0.8452 0.0438\r\nvt 0.8452 0.0445\r\nvt 0.8452 0.0448\r\nvt 0.8424 0.0448\r\nvt 0.8403 0.0448\r\nvt 0.9535 0.1112\r\nvt 0.9537 0.1112\r\nvt 0.9535 0.1093\r\nvt 0.9537 0.1093\r\nvt 0.9539 0.1093\r\nvt 0.9539 0.1112\r\nvt 0.9537 0.107\r\nvt 0.9535 0.107\r\nvt 0.9539 0.107\r\nvt 0.9544 0.107\r\nvt 0.9542 0.107\r\nvt 0.9544 0.1093\r\nvt 0.9542 0.1093\r\nvt 0.9542 0.1112\r\nvt 0.9544 0.1112\r\nvt 0.8356 0.0173\r\nvt 0.8337 0.0173\r\nvt 0.8337 0.0135\r\nvt 0.8356 0.0135\r\nvt 0.8311 0.0173\r\nvt 0.8311 0.0135\r\nvt 0.8311 0.0054\r\nvt 0.8311 0.0093\r\nvt 0.8337 0.0054\r\nvt 0.8337 0.0093\r\nvt 0.8356 0.0093\r\nvt 0.8356 0.0054\r\nvt 0.8356 0.0026\r\nvt 0.8337 0.0026\r\nvt 0.8337 0.001\r\nvt 0.8356 0.001\r\nvt 0.8311 0.0026\r\nvt 0.8311 0.001\r\nvt 0.8311 0\r\nvt 0.8311 0.0002\r\nvt 0.8337 0\r\nvt 0.8337 0.0002\r\nvt 0.8356 0.0002\r\nvt 0.8356 0\r\nvt 0.837 0.0458\r\nvt 0.837 0.0456\r\nvt 0.8352 0.0458\r\nvt 0.8352 0.0456\r\nvt 0.8352 0.0454\r\nvt 0.837 0.0454\r\nvt 0.8327 0.0456\r\nvt 0.8327 0.0458\r\nvt 0.8327 0.0454\r\nvt 0.8327 0.0452\r\nvt 0.8352 0.0452\r\nvt 0.837 0.0452\r\no trim\r\nusemtl Trim\r\nf 943/943/943 944/944/944 945/945/945 946/946/946\r\nf 947/947/947 948/948/948 945/945/945 944/944/944\r\nf 949/949/949 950/950/950 945/945/945 948/948/948\r\nf 951/951/951 946/946/946 945/945/945 950/950/950\r\nf 952/952/952 953/953/953 954/954/954 955/955/955\r\nf 956/956/956 957/957/957 954/954/954 953/953/953\r\nf 949/949/949 948/948/948 954/954/954 957/957/957\r\nf 947/947/947 955/955/955 954/954/954 948/948/948\r\nf 958/958/958 959/959/959 960/960/960 961/961/961\r\nf 962/962/962 963/963/963 960/960/960 959/959/959\r\nf 949/949/949 957/957/957 960/960/960 963/963/963\r\nf 956/956/956 961/961/961 960/960/960 957/957/957\r\nf 964/964/964 965/965/965 966/966/966 967/967/967\r\nf 951/951/951 950/950/950 966/966/966 965/965/965\r\nf 949/949/949 963/963/963 966/966/966 950/950/950\r\nf 962/962/962 967/967/967 966/966/966 963/963/963\r\nf 943/943/943 968/968/968 969/969/969 944/944/944\r\nf 970/970/970 971/971/971 969/969/969 968/968/968\r\nf 972/972/972 973/973/973 969/969/969 971/971/971\r\nf 947/947/947 944/944/944 969/969/969 973/973/973\r\nf 974/974/974 975/975/975 976/976/976 977/977/977\r\nf 978/978/978 979/979/979 976/976/976 975/975/975\r\nf 972/972/972 971/971/971 976/976/976 979/979/979\r\nf 970/970/970 977/977/977 976/976/976 971/971/971\r\nf 980/980/980 981/981/981 982/982/982 983/983/983\r\nf 984/984/984 985/985/985 982/982/982 981/981/981\r\nf 972/972/972 979/979/979 982/982/982 985/985/985\r\nf 978/978/978 983/983/983 982/982/982 979/979/979\r\nf 952/952/952 955/955/955 986/986/986 987/987/987\r\nf 947/947/947 973/973/973 986/986/986 955/955/955\r\nf 972/972/972 985/985/985 986/986/986 973/973/973\r\nf 984/984/984 987/987/987 986/986/986 985/985/985\r\nf 974/974/974 988/988/988 989/989/989 975/975/975\r\nf 990/990/990 991/991/991 989/989/989 988/988/988\r\nf 992/992/992 993/993/993 989/989/989 991/991/991\r\nf 978/978/978 975/975/975 989/989/989 993/993/993\r\nf 994/994/994 995/995/995 996/996/996 997/997/997\r\nf 998/998/998 999/999/999 996/996/996 995/995/995\r\nf 992/992/992 991/991/991 996/996/996 999/999/999\r\nf 990/990/990 997/997/997 996/996/996 991/991/991\r\nf 1000/1000/1000 1001/1001/1001 1002/1002/1002 1003/1003/1003\r\nf 1004/1004/1004 1005/1005/1005 1002/1002/1002 1001/1001/1001\r\nf 992/992/992 999/999/999 1002/1002/1002 1005/1005/1005\r\nf 998/998/998 1003/1003/1003 1002/1002/1002 999/999/999\r\nf 980/980/980 983/983/983 1006/1006/1006 1007/1007/1007\r\nf 978/978/978 993/993/993 1006/1006/1006 983/983/983\r\nf 992/992/992 1005/1005/1005 1006/1006/1006 993/993/993\r\nf 1004/1004/1004 1007/1007/1007 1006/1006/1006 1005/1005/1005\r\nf 1000/1000/1000 1003/1003/1003 1008/1008/1008 1009/1009/1009\r\nf 998/998/998 1010/1010/1010 1008/1008/1008 1003/1003/1003\r\nf 1011/1011/1011 1012/1012/1012 1008/1008/1008 1010/1010/1010\r\nf 1013/1013/1013 1009/1009/1009 1008/1008/1008 1012/1012/1012\r\nf 994/994/994 1014/1014/1014 1015/1015/1015 995/995/995\r\nf 1016/1016/1016 1017/1017/1017 1015/1015/1015 1014/1014/1014\r\nf 1011/1011/1011 1010/1010/1010 1015/1015/1015 1017/1017/1017\r\nf 998/998/998 995/995/995 1015/1015/1015 1010/1010/1010\r\nf 1018/1018/1018 1019/1019/1019 1020/1020/1020 1021/1021/1021\r\nf 1022/1022/1022 1023/1023/1023 1020/1020/1020 1019/1019/1019\r\nf 1011/1011/1011 1017/1017/1017 1020/1020/1020 1023/1023/1023\r\nf 1016/1016/1016 1021/1021/1021 1020/1020/1020 1017/1017/1017\r\nf 1024/1024/1024 1025/1025/1025 1026/1026/1026 1027/1027/1027\r\nf 1013/1013/1013 1012/1012/1012 1026/1026/1026 1025/1025/1025\r\nf 1011/1011/1011 1023/1023/1023 1026/1026/1026 1012/1012/1012\r\nf 1022/1022/1022 1027/1027/1027 1026/1026/1026 1023/1023/1023\r\nf 1028/1028/1028 1029/1029/1029 1030/1030/1030 1031/1031/1031\r\nf 1032/1032/1032 1033/1033/1033 1030/1030/1030 1029/1029/1029\r\nf 1034/1034/1034 1035/1035/1035 1030/1030/1030 1033/1033/1033\r\nf 1036/1036/1036 1031/1031/1031 1030/1030/1030 1035/1035/1035\r\nf 1037/1037/1037 1038/1038/1038 1039/1039/1039 1040/1040/1040\r\nf 1041/1041/1041 1042/1042/1042 1039/1039/1039 1038/1038/1038\r\nf 1034/1034/1034 1033/1033/1033 1039/1039/1039 1042/1042/1042\r\nf 1032/1032/1032 1040/1040/1040 1039/1039/1039 1033/1033/1033\r\nf 1043/1043/1043 1044/1044/1044 1045/1045/1045 1046/1046/1046\r\nf 1047/1047/1047 1048/1048/1048 1045/1045/1045 1044/1044/1044\r\nf 1034/1034/1034 1042/1042/1042 1045/1045/1045 1048/1048/1048\r\nf 1041/1041/1041 1046/1046/1046 1045/1045/1045 1042/1042/1042\r\nf 1049/1049/1049 1050/1050/1050 1051/1051/1051 1052/1052/1052\r\nf 1036/1036/1036 1035/1035/1035 1051/1051/1051 1050/1050/1050\r\nf 1034/1034/1034 1048/1048/1048 1051/1051/1051 1035/1035/1035\r\nf 1047/1047/1047 1052/1052/1052 1051/1051/1051 1048/1048/1048\r\nf 1053/1053/1053 1054/1054/1054 1055/1055/1055 1056/1056/1056\r\nf 1057/1057/1057 1058/1058/1058 1055/1055/1055 1054/1054/1054\r\nf 1059/1059/1059 1060/1060/1060 1055/1055/1055 1058/1058/1058\r\nf 1061/1061/1061 1056/1056/1056 1055/1055/1055 1060/1060/1060\r\nf 1062/1062/1062 1063/1063/1063 1064/1064/1064 1065/1065/1065\r\nf 1066/1066/1066 1067/1067/1067 1064/1064/1064 1063/1063/1063\r\nf 1059/1059/1059 1058/1058/1058 1064/1064/1064 1067/1067/1067\r\nf 1057/1057/1057 1065/1065/1065 1064/1064/1064 1058/1058/1058\r\nf 1068/1068/1068 1069/1069/1069 1070/1070/1070 1071/1071/1071\r\nf 1072/1072/1072 1073/1073/1073 1070/1070/1070 1069/1069/1069\r\nf 1059/1059/1059 1067/1067/1067 1070/1070/1070 1073/1073/1073\r\nf 1066/1066/1066 1071/1071/1071 1070/1070/1070 1067/1067/1067\r\nf 1074/1074/1074 1075/1075/1075 1076/1076/1076 1077/1077/1077\r\nf 1061/1061/1061 1060/1060/1060 1076/1076/1076 1075/1075/1075\r\nf 1059/1059/1059 1073/1073/1073 1076/1076/1076 1060/1060/1060\r\nf 1072/1072/1072 1077/1077/1077 1076/1076/1076 1073/1073/1073\r\nf 1043/1043/1043 1078/1078/1078 1079/1079/1079 1044/1044/1044\r\nf 1080/1080/1080 1081/1081/1081 1079/1079/1079 1078/1078/1078\r\nf 1082/1082/1082 1083/1083/1083 1079/1079/1079 1081/1081/1081\r\nf 1047/1047/1047 1044/1044/1044 1079/1079/1079 1083/1083/1083\r\nf 1084/1084/1084 1085/1085/1085 1086/1086/1086 1087/1087/1087\r\nf 1088/1088/1088 1089/1089/1089 1086/1086/1086 1085/1085/1085\r\nf 1082/1082/1082 1081/1081/1081 1086/1086/1086 1089/1089/1089\r\nf 1080/1080/1080 1087/1087/1087 1086/1086/1086 1081/1081/1081\r\nf 1090/1090/1090 1091/1091/1091 1092/1092/1092 1093/1093/1093\r\nf 1094/1094/1094 1095/1095/1095 1092/1092/1092 1091/1091/1091\r\nf 1082/1082/1082 1089/1089/1089 1092/1092/1092 1095/1095/1095\r\nf 1088/1088/1088 1093/1093/1093 1092/1092/1092 1089/1089/1089\r\nf 1049/1049/1049 1052/1052/1052 1096/1096/1096 1097/1097/1097\r\nf 1047/1047/1047 1083/1083/1083 1096/1096/1096 1052/1052/1052\r\nf 1082/1082/1082 1095/1095/1095 1096/1096/1096 1083/1083/1083\r\nf 1094/1094/1094 1097/1097/1097 1096/1096/1096 1095/1095/1095\r\nf 1098/1098/1098 1099/1099/1099 1100/1100/1100 1101/1101/1101\r\nf 1102/1102/1102 1103/1103/1103 1100/1100/1100 1099/1099/1099\r\nf 1104/1104/1104 1105/1105/1105 1100/1100/1100 1103/1103/1103\r\nf 1106/1106/1106 1101/1101/1101 1100/1100/1100 1105/1105/1105\r\nf 1107/1107/1107 1108/1108/1108 1109/1109/1109 1110/1110/1110\r\nf 1111/1111/1111 1112/1112/1112 1109/1109/1109 1108/1108/1108\r\nf 1104/1104/1104 1103/1103/1103 1109/1109/1109 1112/1112/1112\r\nf 1102/1102/1102 1110/1110/1110 1109/1109/1109 1103/1103/1103\r\nf 1113/1113/1113 1114/1114/1114 1115/1115/1115 1116/1116/1116\r\nf 1117/1117/1117 1118/1118/1118 1115/1115/1115 1114/1114/1114\r\nf 1104/1104/1104 1112/1112/1112 1115/1115/1115 1118/1118/1118\r\nf 1111/1111/1111 1116/1116/1116 1115/1115/1115 1112/1112/1112\r\nf 1119/1119/1119 1120/1120/1120 1121/1121/1121 1122/1122/1122\r\nf 1106/1106/1106 1105/1105/1105 1121/1121/1121 1120/1120/1120\r\nf 1104/1104/1104 1118/1118/1118 1121/1121/1121 1105/1105/1105\r\nf 1117/1117/1117 1122/1122/1122 1121/1121/1121 1118/1118/1118\r\nf 1113/1113/1113 1123/1123/1123 1124/1124/1124 1114/1114/1114\r\nf 1125/1125/1125 1126/1126/1126 1124/1124/1124 1123/1123/1123\r\nf 1127/1127/1127 1128/1128/1128 1124/1124/1124 1126/1126/1126\r\nf 1117/1117/1117 1114/1114/1114 1124/1124/1124 1128/1128/1128\r\nf 1037/1037/1037 1040/1040/1040 1129/1129/1129 1130/1130/1130\r\nf 1032/1032/1032 1131/1131/1131 1129/1129/1129 1040/1040/1040\r\nf 1127/1127/1127 1126/1126/1126 1129/1129/1129 1131/1131/1131\r\nf 1125/1125/1125 1130/1130/1130 1129/1129/1129 1126/1126/1126\r\nf 1028/1028/1028 1132/1132/1132 1133/1133/1133 1029/1029/1029\r\nf 1134/1134/1134 1135/1135/1135 1133/1133/1133 1132/1132/1132\r\nf 1127/1127/1127 1131/1131/1131 1133/1133/1133 1135/1135/1135\r\nf 1032/1032/1032 1029/1029/1029 1133/1133/1133 1131/1131/1131\r\nf 1119/1119/1119 1122/1122/1122 1136/1136/1136 1137/1137/1137\r\nf 1117/1117/1117 1128/1128/1128 1136/1136/1136 1122/1122/1122\r\nf 1127/1127/1127 1135/1135/1135 1136/1136/1136 1128/1128/1128\r\nf 1134/1134/1134 1137/1137/1137 1136/1136/1136 1135/1135/1135\r\nf 1018/1018/1018 1138/1138/1138 1139/1139/1139 1019/1019/1019\r\nf 1140/1140/1140 1141/1141/1141 1139/1139/1139 1138/1138/1138\r\nf 1142/1142/1142 1143/1143/1143 1139/1139/1139 1141/1141/1141\r\nf 1022/1022/1022 1019/1019/1019 1139/1139/1139 1143/1143/1143\r\nf 1107/1107/1107 1110/1110/1110 1144/1144/1144 1145/1145/1145\r\nf 1102/1102/1102 1146/1146/1146 1144/1144/1144 1110/1110/1110\r\nf 1142/1142/1142 1141/1141/1141 1144/1144/1144 1146/1146/1146\r\nf 1140/1140/1140 1145/1145/1145 1144/1144/1144 1141/1141/1141\r\nf 1098/1098/1098 1147/1147/1147 1148/1148/1148 1099/1099/1099\r\nf 1149/1149/1149 1150/1150/1150 1148/1148/1148 1147/1147/1147\r\nf 1142/1142/1142 1146/1146/1146 1148/1148/1148 1150/1150/1150\r\nf 1102/1102/1102 1099/1099/1099 1148/1148/1148 1146/1146/1146\r\nf 1024/1024/1024 1027/1027/1027 1151/1151/1151 1152/1152/1152\r\nf 1022/1022/1022 1143/1143/1143 1151/1151/1151 1027/1027/1027\r\nf 1142/1142/1142 1150/1150/1150 1151/1151/1151 1143/1143/1143\r\nf 1149/1149/1149 1152/1152/1152 1151/1151/1151 1150/1150/1150\r\nf 1153/1153/1153 1154/1154/1154 1155/1155/1155 1156/1156/1156\r\nf 1157/1157/1157 1158/1158/1158 1155/1155/1155 1154/1154/1154\r\nf 1159/1159/1159 1160/1160/1160 1155/1155/1155 1158/1158/1158\r\nf 1161/1161/1161 1156/1156/1156 1155/1155/1155 1160/1160/1160\r\nf 1162/1162/1162 1163/1163/1163 1164/1164/1164 1165/1165/1165\r\nf 1166/1166/1166 1167/1167/1167 1164/1164/1164 1163/1163/1163\r\nf 1159/1159/1159 1158/1158/1158 1164/1164/1164 1167/1167/1167\r\nf 1157/1157/1157 1165/1165/1165 1164/1164/1164 1158/1158/1158\r\nf 1168/1168/1168 1169/1169/1169 1170/1170/1170 1171/1171/1171\r\nf 1172/1172/1172 1173/1173/1173 1170/1170/1170 1169/1169/1169\r\nf 1159/1159/1159 1167/1167/1167 1170/1170/1170 1173/1173/1173\r\nf 1166/1166/1166 1171/1171/1171 1170/1170/1170 1167/1167/1167\r\nf 1174/1174/1174 1175/1175/1175 1176/1176/1176 1177/1177/1177\r\nf 1161/1161/1161 1160/1160/1160 1176/1176/1176 1175/1175/1175\r\nf 1159/1159/1159 1173/1173/1173 1176/1176/1176 1160/1160/1160\r\nf 1172/1172/1172 1177/1177/1177 1176/1176/1176 1173/1173/1173\r\nf 1178/1178/1178 1179/1179/1179 1180/1180/1180 1181/1181/1181\r\nf 1182/1182/1182 1183/1183/1183 1180/1180/1180 1179/1179/1179\r\nf 1184/1184/1184 1185/1185/1185 1180/1180/1180 1183/1183/1183\r\nf 1186/1186/1186 1181/1181/1181 1180/1180/1180 1185/1185/1185\r\nf 1153/1153/1153 1156/1156/1156 1187/1187/1187 1188/1188/1188\r\nf 1161/1161/1161 1189/1189/1189 1187/1187/1187 1156/1156/1156\r\nf 1184/1184/1184 1183/1183/1183 1187/1187/1187 1189/1189/1189\r\nf 1182/1182/1182 1188/1188/1188 1187/1187/1187 1183/1183/1183\r\nf 1174/1174/1174 1190/1190/1190 1191/1191/1191 1175/1175/1175\r\nf 1192/1192/1192 1193/1193/1193 1191/1191/1191 1190/1190/1190\r\nf 1184/1184/1184 1189/1189/1189 1191/1191/1191 1193/1193/1193\r\nf 1161/1161/1161 1175/1175/1175 1191/1191/1191 1189/1189/1189\r\nf 1194/1194/1194 1195/1195/1195 1196/1196/1196 1197/1197/1197\r\nf 1186/1186/1186 1185/1185/1185 1196/1196/1196 1195/1195/1195\r\nf 1184/1184/1184 1193/1193/1193 1196/1196/1196 1185/1185/1185\r\nf 1192/1192/1192 1197/1197/1197 1196/1196/1196 1193/1193/1193\r\nf 1053/1053/1053 1198/1198/1198 1199/1199/1199 1054/1054/1054\r\nf 1200/1200/1200 1201/1201/1201 1199/1199/1199 1198/1198/1198\r\nf 1202/1202/1202 1203/1203/1203 1199/1199/1199 1201/1201/1201\r\nf 1057/1057/1057 1054/1054/1054 1199/1199/1199 1203/1203/1203\r\nf 1204/1204/1204 1205/1205/1205 1206/1206/1206 1207/1207/1207\r\nf 1208/1208/1208 1209/1209/1209 1206/1206/1206 1205/1205/1205\r\nf 1202/1202/1202 1201/1201/1201 1206/1206/1206 1209/1209/1209\r\nf 1200/1200/1200 1207/1207/1207 1206/1206/1206 1201/1201/1201\r\nf 1210/1210/1210 1211/1211/1211 1212/1212/1212 1213/1213/1213\r\nf 1214/1214/1214 1215/1215/1215 1212/1212/1212 1211/1211/1211\r\nf 1202/1202/1202 1209/1209/1209 1212/1212/1212 1215/1215/1215\r\nf 1208/1208/1208 1213/1213/1213 1212/1212/1212 1209/1209/1209\r\nf 1062/1062/1062 1065/1065/1065 1216/1216/1216 1217/1217/1217\r\nf 1057/1057/1057 1203/1203/1203 1216/1216/1216 1065/1065/1065\r\nf 1202/1202/1202 1215/1215/1215 1216/1216/1216 1203/1203/1203\r\nf 1214/1214/1214 1217/1217/1217 1216/1216/1216 1215/1215/1215\r\nf 1218/1218/1218 1219/1219/1219 1220/1220/1220 1221/1221/1221\r\nf 1222/1222/1222 1223/1223/1223 1220/1220/1220 1219/1219/1219\r\nf 1224/1224/1224 1225/1225/1225 1220/1220/1220 1223/1223/1223\r\nf 1226/1226/1226 1221/1221/1221 1220/1220/1220 1225/1225/1225\r\nf 1227/1227/1227 1228/1228/1228 1229/1229/1229 1230/1230/1230\r\nf 1231/1231/1231 1232/1232/1232 1229/1229/1229 1228/1228/1228\r\nf 1224/1224/1224 1223/1223/1223 1229/1229/1229 1232/1232/1232\r\nf 1222/1222/1222 1230/1230/1230 1229/1229/1229 1223/1223/1223\r\nf 1233/1233/1233 1234/1234/1234 1235/1235/1235 1236/1236/1236\r\nf 1237/1237/1237 1238/1238/1238 1235/1235/1235 1234/1234/1234\r\nf 1224/1224/1224 1232/1232/1232 1235/1235/1235 1238/1238/1238\r\nf 1231/1231/1231 1236/1236/1236 1235/1235/1235 1232/1232/1232\r\nf 1239/1239/1239 1240/1240/1240 1241/1241/1241 1242/1242/1242\r\nf 1226/1226/1226 1225/1225/1225 1241/1241/1241 1240/1240/1240\r\nf 1224/1224/1224 1238/1238/1238 1241/1241/1241 1225/1225/1225\r\nf 1237/1237/1237 1242/1242/1242 1241/1241/1241 1238/1238/1238\r\nf 1243/1243/1243 1244/1244/1244 1245/1245/1245 1246/1246/1246\r\nf 1247/1247/1247 1248/1248/1248 1245/1245/1245 1244/1244/1244\r\nf 1249/1249/1249 1250/1250/1250 1245/1245/1245 1248/1248/1248\r\nf 1251/1251/1251 1246/1246/1246 1245/1245/1245 1250/1250/1250\r\nf 1252/1252/1252 1253/1253/1253 1254/1254/1254 1255/1255/1255\r\nf 1256/1256/1256 1257/1257/1257 1254/1254/1254 1253/1253/1253\r\nf 1249/1249/1249 1248/1248/1248 1254/1254/1254 1257/1257/1257\r\nf 1247/1247/1247 1255/1255/1255 1254/1254/1254 1248/1248/1248\r\nf 1227/1227/1227 1230/1230/1230 1258/1258/1258 1259/1259/1259\r\nf 1222/1222/1222 1260/1260/1260 1258/1258/1258 1230/1230/1230\r\nf 1249/1249/1249 1257/1257/1257 1258/1258/1258 1260/1260/1260\r\nf 1256/1256/1256 1259/1259/1259 1258/1258/1258 1257/1257/1257\r\nf 1218/1218/1218 1261/1261/1261 1262/1262/1262 1219/1219/1219\r\nf 1251/1251/1251 1250/1250/1250 1262/1262/1262 1261/1261/1261\r\nf 1249/1249/1249 1260/1260/1260 1262/1262/1262 1250/1250/1250\r\nf 1222/1222/1222 1219/1219/1219 1262/1262/1262 1260/1260/1260\r\nf 1263/1263/1263 1264/1264/1264 1265/1265/1265 1266/1266/1266\r\nf 1267/1267/1267 1268/1268/1268 1265/1265/1265 1264/1264/1264\r\nf 1269/1269/1269 1270/1270/1270 1265/1265/1265 1268/1268/1268\r\nf 1271/1271/1271 1266/1266/1266 1265/1265/1265 1270/1270/1270\r\nf 1272/1272/1272 1273/1273/1273 1274/1274/1274 1275/1275/1275\r\nf 1276/1276/1276 1277/1277/1277 1274/1274/1274 1273/1273/1273\r\nf 1269/1269/1269 1268/1268/1268 1274/1274/1274 1277/1277/1277\r\nf 1267/1267/1267 1275/1275/1275 1274/1274/1274 1268/1268/1268\r\nf 1252/1252/1252 1255/1255/1255 1278/1278/1278 1279/1279/1279\r\nf 1247/1247/1247 1280/1280/1280 1278/1278/1278 1255/1255/1255\r\nf 1269/1269/1269 1277/1277/1277 1278/1278/1278 1280/1280/1280\r\nf 1276/1276/1276 1279/1279/1279 1278/1278/1278 1277/1277/1277\r\nf 1243/1243/1243 1281/1281/1281 1282/1282/1282 1244/1244/1244\r\nf 1271/1271/1271 1270/1270/1270 1282/1282/1282 1281/1281/1281\r\nf 1269/1269/1269 1280/1280/1280 1282/1282/1282 1270/1270/1270\r\nf 1247/1247/1247 1244/1244/1244 1282/1282/1282 1280/1280/1280\r\nf 1018/1018/1018 1021/1021/1021 1283/1283/1283 1284/1284/1284\r\nf 1016/1016/1016 1285/1285/1285 1283/1283/1283 1021/1021/1021\r\nf 1286/1286/1286 1287/1287/1287 1283/1283/1283 1285/1285/1285\r\nf 1288/1288/1288 1284/1284/1284 1283/1283/1283 1287/1287/1287\r\nf 994/994/994 1289/1289/1289 1290/1290/1290 1014/1014/1014\r\nf 1291/1291/1291 1292/1292/1292 1290/1290/1290 1289/1289/1289\r\nf 1286/1286/1286 1285/1285/1285 1290/1290/1290 1292/1292/1292\r\nf 1016/1016/1016 1014/1014/1014 1290/1290/1290 1285/1285/1285\r\nf 1272/1272/1272 1293/1293/1293 1294/1294/1294 1295/1295/1295\r\nf 1296/1296/1296 1297/1297/1297 1294/1294/1294 1293/1293/1293\r\nf 1286/1298/1286 1292/1299/1292 1294/1294/1294 1297/1297/1297\r\nf 1291/1300/1291 1295/1295/1295 1294/1294/1294 1292/1299/1292\r\nf 1298/1301/1298 1299/1302/1299 1300/1303/1300 1301/1304/1301\r\nf 1288/1305/1288 1287/1306/1287 1300/1303/1300 1299/1302/1299\r\nf 1286/1298/1286 1297/1297/1297 1300/1303/1300 1287/1306/1287\r\nf 1296/1296/1296 1301/1304/1301 1300/1303/1300 1297/1297/1297\r\nf 1037/1037/1037 1302/1307/1302 1303/1308/1303 1038/1038/1038\r\nf 1304/1309/1304 1305/1310/1305 1303/1308/1303 1302/1307/1302\r\nf 1306/1311/1306 1307/1312/1307 1303/1308/1303 1305/1310/1305\r\nf 1041/1041/1041 1038/1038/1038 1303/1308/1303 1307/1312/1307\r\nf 1308/1313/1308 1309/1314/1309 1310/1315/1310 1311/1316/1311\r\nf 1312/1317/1312 1313/1318/1313 1310/1315/1310 1309/1314/1309\r\nf 1306/1319/1306 1305/1320/1305 1310/1315/1310 1313/1318/1313\r\nf 1304/1321/1304 1311/1316/1311 1310/1315/1310 1305/1320/1305\r\nf 1314/1322/1314 1315/1323/1315 1316/1324/1316 1317/1325/1317\r\nf 1318/1326/1318 1319/1327/1319 1316/1324/1316 1315/1323/1315\r\nf 1306/1319/1306 1313/1318/1313 1316/1324/1316 1319/1327/1319\r\nf 1312/1317/1312 1317/1325/1317 1316/1324/1316 1313/1318/1313\r\nf 1043/1043/1043 1046/1046/1046 1320/1328/1320 1321/1329/1321\r\nf 1041/1041/1041 1307/1312/1307 1320/1328/1320 1046/1046/1046\r\nf 1306/1311/1306 1319/1330/1319 1320/1328/1320 1307/1312/1307\r\nf 1318/1331/1318 1321/1329/1321 1320/1328/1320 1319/1330/1319\r\nf 1322/1332/1322 1323/1333/1323 1324/1334/1324 1325/1335/1325\r\nf 1326/1336/1326 1327/1337/1327 1324/1334/1324 1323/1333/1323\r\nf 1328/1338/1328 1329/1339/1329 1324/1334/1324 1327/1337/1327\r\nf 1330/1340/1330 1325/1335/1325 1324/1334/1324 1329/1339/1329\r\nf 1331/1341/1331 1332/1342/1332 1333/1343/1333 1334/1344/1334\r\nf 1335/1345/1335 1336/1346/1336 1333/1343/1333 1332/1342/1332\r\nf 1328/1338/1328 1327/1337/1327 1333/1343/1333 1336/1346/1336\r\nf 1326/1336/1326 1334/1344/1334 1333/1343/1333 1327/1337/1327\r\nf 1337/1347/1337 1338/1348/1338 1339/1349/1339 1340/1350/1340\r\nf 1341/1351/1341 1342/1352/1342 1339/1349/1339 1338/1348/1338\r\nf 1328/1338/1328 1336/1346/1336 1339/1349/1339 1342/1352/1342\r\nf 1335/1345/1335 1340/1350/1340 1339/1349/1339 1336/1346/1336\r\nf 1343/1353/1343 1344/1354/1344 1345/1355/1345 1346/1356/1346\r\nf 1330/1340/1330 1329/1339/1329 1345/1355/1345 1344/1354/1344\r\nf 1328/1338/1328 1342/1352/1342 1345/1355/1345 1329/1339/1329\r\nf 1341/1351/1341 1346/1356/1346 1345/1355/1345 1342/1352/1342\r\nf 1074/1074/1074 1347/1357/1347 1348/1358/1348 1349/1359/1349\r\nf 1350/1360/1350 1351/1361/1351 1348/1358/1348 1347/1357/1347\r\nf 1352/1362/1352 1353/1363/1353 1348/1358/1348 1351/1361/1351\r\nf 1354/1364/1354 1349/1359/1349 1348/1358/1348 1353/1363/1353\r\nf 1355/1365/1355 1356/1366/1356 1357/1367/1357 1358/1368/1358\r\nf 1359/1369/1359 1360/1370/1360 1357/1367/1357 1356/1366/1356\r\nf 1352/1362/1352 1351/1361/1351 1357/1367/1357 1360/1370/1360\r\nf 1350/1360/1350 1358/1368/1358 1357/1367/1357 1351/1361/1351\r\nf 1361/1371/1361 1362/1372/1362 1363/1373/1363 1364/1374/1364\r\nf 1365/1375/1365 1366/1376/1366 1363/1373/1363 1362/1372/1362\r\nf 1352/1377/1352 1360/1378/1360 1363/1373/1363 1366/1376/1366\r\nf 1359/1379/1359 1364/1374/1364 1363/1373/1363 1360/1378/1360\r\nf 1331/1341/1331 1367/1380/1367 1368/1381/1368 1369/1382/1369\r\nf 1354/1383/1354 1353/1384/1353 1368/1381/1368 1367/1380/1367\r\nf 1352/1377/1352 1366/1376/1366 1368/1381/1368 1353/1384/1353\r\nf 1365/1375/1365 1369/1382/1369 1368/1381/1368 1366/1376/1366\r\nf 1107/1107/1107 1370/1385/1370 1371/1386/1371 1108/1108/1108\r\nf 1372/1387/1372 1373/1388/1373 1371/1386/1371 1370/1385/1370\r\nf 1374/1389/1374 1375/1390/1375 1371/1386/1371 1373/1388/1373\r\nf 1111/1111/1111 1108/1108/1108 1371/1386/1371 1375/1390/1375\r\nf 1376/1391/1376 1377/1392/1377 1378/1393/1378 1379/1394/1379\r\nf 1380/1395/1380 1381/1396/1381 1378/1393/1378 1377/1392/1377\r\nf 1374/1397/1374 1373/1398/1373 1378/1393/1378 1381/1396/1381\r\nf 1372/1399/1372 1379/1394/1379 1378/1393/1378 1373/1398/1373\r\nf 1382/1400/1382 1383/1401/1383 1384/1402/1384 1385/1403/1385\r\nf 1386/1404/1386 1387/1405/1387 1384/1402/1384 1383/1401/1383\r\nf 1374/1397/1374 1381/1396/1381 1384/1402/1384 1387/1405/1387\r\nf 1380/1395/1380 1385/1403/1385 1384/1402/1384 1381/1396/1381\r\nf 1113/1113/1113 1116/1116/1116 1388/1406/1388 1389/1407/1389\r\nf 1111/1111/1111 1375/1390/1375 1388/1406/1388 1116/1116/1116\r\nf 1374/1389/1374 1387/1408/1387 1388/1406/1388 1375/1390/1375\r\nf 1386/1409/1386 1389/1407/1389 1388/1406/1388 1387/1408/1387\r\nf 1037/1037/1037 1130/1130/1130 1390/1410/1390 1302/1307/1302\r\nf 1125/1125/1125 1391/1411/1391 1390/1410/1390 1130/1130/1130\r\nf 1392/1412/1392 1393/1413/1393 1390/1410/1390 1391/1411/1391\r\nf 1304/1309/1304 1302/1307/1302 1390/1410/1390 1393/1413/1393\r\nf 1113/1113/1113 1389/1407/1389 1394/1414/1394 1123/1123/1123\r\nf 1386/1409/1386 1395/1415/1395 1394/1414/1394 1389/1407/1389\r\nf 1392/1412/1392 1391/1411/1391 1394/1414/1394 1395/1415/1395\r\nf 1125/1125/1125 1123/1123/1123 1394/1414/1394 1391/1411/1391\r\nf 1382/1400/1382 1396/1416/1396 1397/1417/1397 1383/1401/1383\r\nf 1398/1418/1398 1399/1419/1399 1397/1417/1397 1396/1416/1396\r\nf 1392/1420/1392 1395/1421/1395 1397/1417/1397 1399/1419/1399\r\nf 1386/1404/1386 1383/1401/1383 1397/1417/1397 1395/1421/1395\r\nf 1308/1313/1308 1311/1316/1311 1400/1422/1400 1401/1423/1401\r\nf 1304/1321/1304 1393/1424/1393 1400/1422/1400 1311/1316/1311\r\nf 1392/1420/1392 1399/1419/1399 1400/1422/1400 1393/1424/1393\r\nf 1398/1418/1398 1401/1423/1401 1400/1422/1400 1399/1419/1399\r\nf 1376/1391/1376 1402/1425/1402 1403/1426/1403 1404/1427/1404\r\nf 1405/1428/1405 1406/1429/1406 1403/1426/1403 1402/1425/1402\r\nf 1407/1430/1407 1408/1431/1408 1403/1426/1403 1406/1429/1406\r\nf 1409/1432/1409 1404/1427/1404 1403/1426/1403 1408/1431/1408\r\nf 1298/1301/1298 1410/1433/1410 1411/1434/1411 1412/1435/1412\r\nf 1413/1436/1413 1414/1437/1414 1411/1434/1411 1410/1433/1410\r\nf 1407/1430/1407 1406/1429/1406 1411/1434/1411 1414/1437/1414\r\nf 1405/1428/1405 1412/1435/1412 1411/1434/1411 1406/1429/1406\r\nf 1415/1438/1415 1416/1439/1416 1417/1440/1417 1418/1441/1418\r\nf 1419/1442/1419 1420/1443/1420 1417/1440/1417 1416/1439/1416\r\nf 1407/1430/1407 1414/1437/1414 1417/1440/1417 1420/1443/1420\r\nf 1413/1436/1413 1418/1441/1418 1417/1440/1417 1414/1437/1414\r\nf 1421/1444/1421 1422/1445/1422 1423/1446/1423 1424/1447/1424\r\nf 1409/1432/1409 1408/1431/1408 1423/1446/1423 1422/1445/1422\r\nf 1407/1430/1407 1420/1443/1420 1423/1446/1423 1408/1431/1408\r\nf 1419/1442/1419 1424/1447/1424 1423/1446/1423 1420/1443/1420\r\nf 1239/1239/1239 1242/1242/1242 1425/1448/1425 1426/1449/1426\r\nf 1237/1237/1237 1427/1450/1427 1425/1448/1425 1242/1242/1242\r\nf 1428/1451/1428 1429/1452/1429 1425/1448/1425 1427/1450/1427\r\nf 1430/1453/1430 1426/1449/1426 1425/1448/1425 1429/1452/1429\r\nf 1233/1233/1233 1431/1454/1431 1432/1455/1432 1234/1234/1234\r\nf 1433/1456/1433 1434/1457/1434 1432/1455/1432 1431/1454/1431\r\nf 1428/1451/1428 1427/1450/1427 1432/1455/1432 1434/1457/1434\r\nf 1237/1237/1237 1234/1234/1234 1432/1455/1432 1427/1450/1427\r\nf 1435/1458/1435 1436/1459/1436 1437/1460/1437 1438/1461/1438\r\nf 1439/1462/1439 1440/1463/1440 1437/1460/1437 1436/1459/1436\r\nf 1428/1451/1428 1434/1457/1434 1437/1460/1437 1440/1463/1440\r\nf 1433/1456/1433 1438/1461/1438 1437/1460/1437 1434/1457/1434\r\nf 1441/1464/1441 1442/1465/1442 1443/1466/1443 1444/1467/1444\r\nf 1430/1453/1430 1429/1452/1429 1443/1466/1443 1442/1465/1442\r\nf 1428/1451/1428 1440/1463/1440 1443/1466/1443 1429/1452/1429\r\nf 1439/1462/1439 1444/1467/1444 1443/1466/1443 1440/1463/1440\r\nf 1445/1468/1445 1446/1469/1446 1447/1470/1447 1448/1471/1448\r\nf 1449/1472/1449 1450/1473/1450 1447/1470/1447 1446/1469/1446\r\nf 1451/1474/1451 1452/1475/1452 1447/1470/1447 1450/1473/1450\r\nf 1453/1476/1453 1448/1471/1448 1447/1470/1447 1452/1475/1452\r\nf 1454/1477/1454 1455/1478/1455 1456/1479/1456 1457/1480/1457\r\nf 1458/1481/1458 1459/1482/1459 1456/1479/1456 1455/1478/1455\r\nf 1451/1474/1451 1450/1473/1450 1456/1479/1456 1459/1482/1459\r\nf 1449/1472/1449 1457/1480/1457 1456/1479/1456 1450/1473/1450\r\nf 1460/1483/1460 1461/1484/1461 1462/1485/1462 1463/1486/1463\r\nf 1464/1487/1464 1465/1488/1465 1462/1485/1462 1461/1484/1461\r\nf 1451/1474/1451 1459/1482/1459 1462/1485/1462 1465/1488/1465\r\nf 1458/1481/1458 1463/1486/1463 1462/1485/1462 1459/1482/1459\r\nf 1466/1489/1466 1467/1490/1467 1468/1491/1468 1469/1492/1469\r\nf 1453/1476/1453 1452/1475/1452 1468/1491/1468 1467/1490/1467\r\nf 1451/1474/1451 1465/1488/1465 1468/1491/1468 1452/1475/1452\r\nf 1464/1487/1464 1469/1492/1469 1468/1491/1468 1465/1488/1465\r\nf 1466/1489/1466 1469/1492/1469 1470/1493/1470 1471/1494/1471\r\nf 1464/1487/1464 1472/1495/1472 1470/1493/1470 1469/1492/1469\r\nf 1473/1496/1473 1474/1497/1474 1470/1493/1470 1472/1495/1472\r\nf 1475/1498/1475 1471/1494/1471 1470/1493/1470 1474/1497/1474\r\nf 1460/1483/1460 1476/1499/1476 1477/1500/1477 1461/1484/1461\r\nf 1478/1501/1478 1479/1502/1479 1477/1500/1477 1476/1499/1476\r\nf 1473/1496/1473 1472/1495/1472 1477/1500/1477 1479/1502/1479\r\nf 1464/1487/1464 1461/1484/1461 1477/1500/1477 1472/1495/1472\r\nf 1480/1503/1480 1481/1504/1481 1482/1505/1482 1483/1506/1483\r\nf 1484/1507/1484 1485/1508/1485 1482/1505/1482 1481/1504/1481\r\nf 1473/1496/1473 1479/1502/1479 1482/1505/1482 1485/1508/1485\r\nf 1478/1501/1478 1483/1506/1483 1482/1505/1482 1479/1502/1479\r\nf 1486/1509/1486 1487/1510/1487 1488/1511/1488 1489/1512/1489\r\nf 1475/1498/1475 1474/1497/1474 1488/1511/1488 1487/1510/1487\r\nf 1473/1496/1473 1485/1508/1485 1488/1511/1488 1474/1497/1474\r\nf 1484/1507/1484 1489/1512/1489 1488/1511/1488 1485/1508/1485\r\nf 1227/1227/1227 1490/1513/1490 1491/1514/1491 1228/1228/1228\r\nf 1492/1515/1492 1493/1516/1493 1491/1514/1491 1490/1513/1490\r\nf 1494/1517/1494 1495/1518/1495 1491/1514/1491 1493/1516/1493\r\nf 1231/1231/1231 1228/1228/1228 1491/1514/1491 1495/1518/1495\r\nf 943/943/943 946/946/946 1496/1519/1496 1497/1520/1497\r\nf 951/951/951 1498/1521/1498 1496/1519/1496 946/946/946\r\nf 1494/1522/1494 1493/1523/1493 1496/1519/1496 1498/1521/1498\r\nf 1492/1524/1492 1497/1520/1497 1496/1519/1496 1493/1523/1493\r\nf 964/964/964 1499/1525/1499 1500/1526/1500 965/965/965\r\nf 1501/1527/1501 1502/1528/1502 1500/1526/1500 1499/1525/1499\r\nf 1494/1522/1494 1498/1521/1498 1500/1526/1500 1502/1528/1502\r\nf 951/951/951 965/965/965 1500/1526/1500 1498/1521/1498\r\nf 1233/1233/1233 1236/1236/1236 1503/1529/1503 1504/1530/1504\r\nf 1231/1231/1231 1495/1518/1495 1503/1529/1503 1236/1236/1236\r\nf 1494/1517/1494 1502/1531/1502 1503/1529/1503 1495/1518/1495\r\nf 1501/1532/1501 1504/1530/1504 1503/1529/1503 1502/1531/1502\r\nf 974/974/974 977/977/977 1505/1533/1505 1506/1534/1506\r\nf 970/970/970 1507/1535/1507 1505/1533/1505 977/977/977\r\nf 1508/1536/1508 1509/1537/1509 1505/1533/1505 1507/1535/1507\r\nf 1510/1538/1510 1506/1534/1506 1505/1533/1505 1509/1537/1509\r\nf 943/943/943 1497/1520/1497 1511/1539/1511 968/968/968\r\nf 1492/1524/1492 1512/1540/1512 1511/1539/1511 1497/1520/1497\r\nf 1508/1536/1508 1507/1535/1507 1511/1539/1511 1512/1540/1512\r\nf 970/970/970 968/968/968 1511/1539/1511 1507/1535/1507\r\nf 1227/1227/1227 1259/1259/1259 1513/1541/1513 1490/1513/1490\r\nf 1256/1256/1256 1514/1542/1514 1513/1541/1513 1259/1259/1259\r\nf 1508/1543/1508 1512/1544/1512 1513/1541/1513 1514/1542/1514\r\nf 1492/1515/1492 1490/1513/1490 1513/1541/1513 1512/1544/1512\r\nf 1252/1252/1252 1515/1545/1515 1516/1546/1516 1253/1253/1253\r\nf 1510/1547/1510 1509/1548/1509 1516/1546/1516 1515/1545/1515\r\nf 1508/1543/1508 1514/1542/1514 1516/1546/1516 1509/1548/1509\r\nf 1256/1256/1256 1253/1253/1253 1516/1546/1516 1514/1542/1514\r\nf 994/994/994 997/997/997 1517/1549/1517 1289/1289/1289\r\nf 990/990/990 1518/1550/1518 1517/1549/1517 997/997/997\r\nf 1519/1551/1519 1520/1552/1520 1517/1549/1517 1518/1550/1518\r\nf 1291/1291/1291 1289/1289/1289 1517/1549/1517 1520/1552/1520\r\nf 974/974/974 1506/1534/1506 1521/1553/1521 988/988/988\r\nf 1510/1538/1510 1522/1554/1522 1521/1553/1521 1506/1534/1506\r\nf 1519/1551/1519 1518/1550/1518 1521/1553/1521 1522/1554/1522\r\nf 990/990/990 988/988/988 1521/1553/1521 1518/1550/1518\r\nf 1252/1252/1252 1279/1279/1279 1523/1555/1523 1515/1545/1515\r\nf 1276/1276/1276 1524/1556/1524 1523/1555/1523 1279/1279/1279\r\nf 1519/1557/1519 1522/1558/1522 1523/1555/1523 1524/1556/1524\r\nf 1510/1547/1510 1515/1545/1515 1523/1555/1523 1522/1558/1522\r\nf 1272/1272/1272 1295/1295/1295 1525/1559/1525 1273/1273/1273\r\nf 1291/1300/1291 1520/1560/1520 1525/1559/1525 1295/1295/1295\r\nf 1519/1557/1519 1524/1556/1524 1525/1559/1525 1520/1560/1520\r\nf 1276/1276/1276 1273/1273/1273 1525/1559/1525 1524/1556/1524\r\nf 1263/1263/1263 1526/1561/1526 1527/1562/1527 1264/1264/1264\r\nf 1528/1563/1528 1529/1564/1529 1527/1562/1527 1526/1561/1526\r\nf 1530/1565/1530 1531/1566/1531 1527/1562/1527 1529/1564/1529\r\nf 1267/1267/1267 1264/1264/1264 1527/1562/1527 1531/1566/1531\r\nf 1415/1438/1415 1418/1441/1418 1532/1567/1532 1533/1568/1533\r\nf 1413/1436/1413 1534/1569/1534 1532/1567/1532 1418/1441/1418\r\nf 1530/1565/1530 1529/1564/1529 1532/1567/1532 1534/1569/1534\r\nf 1528/1563/1528 1533/1568/1533 1532/1567/1532 1529/1564/1529\r\nf 1298/1301/1298 1301/1304/1301 1535/1570/1535 1410/1433/1410\r\nf 1296/1296/1296 1536/1571/1536 1535/1570/1535 1301/1304/1301\r\nf 1530/1565/1530 1534/1569/1534 1535/1570/1535 1536/1571/1536\r\nf 1413/1436/1413 1410/1433/1410 1535/1570/1535 1534/1569/1534\r\nf 1272/1272/1272 1275/1275/1275 1537/1572/1537 1293/1293/1293\r\nf 1267/1267/1267 1531/1566/1531 1537/1572/1537 1275/1275/1275\r\nf 1530/1565/1530 1536/1571/1536 1537/1572/1537 1531/1566/1531\r\nf 1296/1296/1296 1293/1293/1293 1537/1572/1537 1536/1571/1536\r\nf 1538/1573/1538 1539/1574/1539 1540/1575/1540 1541/1576/1541\r\nf 1542/1577/1542 1543/1578/1543 1540/1575/1540 1539/1574/1539\r\nf 1544/1579/1544 1545/1580/1545 1540/1575/1540 1543/1578/1543\r\nf 1546/1581/1546 1541/1576/1541 1540/1575/1540 1545/1580/1545\r\nf 1547/1582/1547 1548/1583/1548 1549/1584/1549 1550/1585/1550\r\nf 1551/1586/1551 1552/1587/1552 1549/1584/1549 1548/1583/1548\r\nf 1544/1579/1544 1543/1578/1543 1549/1584/1549 1552/1587/1552\r\nf 1542/1577/1542 1550/1585/1550 1549/1584/1549 1543/1578/1543\r\nf 1314/1322/1314 1317/1325/1317 1553/1588/1553 1554/1589/1554\r\nf 1312/1317/1312 1555/1590/1555 1553/1588/1553 1317/1325/1317\r\nf 1544/1579/1544 1552/1587/1552 1553/1588/1553 1555/1590/1555\r\nf 1551/1586/1551 1554/1589/1554 1553/1588/1553 1552/1587/1552\r\nf 1308/1313/1308 1556/1591/1556 1557/1592/1557 1309/1314/1309\r\nf 1546/1581/1546 1545/1580/1545 1557/1592/1557 1556/1591/1556\r\nf 1544/1579/1544 1555/1590/1555 1557/1592/1557 1545/1580/1545\r\nf 1312/1317/1312 1309/1314/1309 1557/1592/1557 1555/1590/1555\r\nf 1322/1332/1322 1558/1593/1558 1559/1594/1559 1323/1333/1323\r\nf 1560/1595/1560 1561/1596/1561 1559/1594/1559 1558/1593/1558\r\nf 1562/1597/1562 1563/1598/1563 1559/1594/1559 1561/1596/1561\r\nf 1326/1336/1326 1323/1333/1323 1559/1594/1559 1563/1598/1563\r\nf 1053/1053/1053 1056/1056/1056 1564/1599/1564 1565/1600/1565\r\nf 1061/1061/1061 1566/1601/1566 1564/1599/1564 1056/1056/1056\r\nf 1562/1602/1562 1561/1603/1561 1564/1599/1564 1566/1601/1566\r\nf 1560/1604/1560 1565/1600/1565 1564/1599/1564 1561/1603/1561\r\nf 1074/1074/1074 1349/1359/1349 1567/1605/1567 1075/1075/1075\r\nf 1354/1364/1354 1568/1606/1568 1567/1605/1567 1349/1359/1349\r\nf 1562/1602/1562 1566/1601/1566 1567/1605/1567 1568/1606/1568\r\nf 1061/1061/1061 1075/1075/1075 1567/1605/1567 1566/1601/1566\r\nf 1331/1341/1331 1334/1344/1334 1569/1607/1569 1367/1380/1367\r\nf 1326/1336/1326 1563/1598/1563 1569/1607/1569 1334/1344/1334\r\nf 1562/1597/1562 1568/1608/1568 1569/1607/1569 1563/1598/1563\r\nf 1354/1383/1354 1367/1380/1367 1569/1607/1569 1568/1608/1568\r\nf 1547/1582/1547 1570/1609/1570 1571/1610/1571 1548/1583/1548\r\nf 1572/1611/1572 1573/1612/1573 1571/1610/1571 1570/1609/1570\r\nf 1574/1613/1574 1575/1614/1575 1571/1610/1571 1573/1612/1573\r\nf 1551/1586/1551 1548/1583/1548 1571/1610/1571 1575/1614/1575\r\nf 1576/1615/1576 1577/1616/1577 1578/1617/1578 1579/1618/1579\r\nf 1580/1619/1580 1581/1620/1581 1578/1617/1578 1577/1616/1577\r\nf 1574/1613/1574 1573/1612/1573 1578/1617/1578 1581/1620/1581\r\nf 1572/1611/1572 1579/1618/1579 1578/1617/1578 1573/1612/1573\r\nf 1582/1621/1582 1583/1622/1583 1584/1623/1584 1585/1624/1585\r\nf 1586/1625/1586 1587/1626/1587 1584/1623/1584 1583/1622/1583\r\nf 1574/1613/1574 1581/1620/1581 1584/1623/1584 1587/1626/1587\r\nf 1580/1619/1580 1585/1624/1585 1584/1623/1584 1581/1620/1581\r\nf 1314/1322/1314 1554/1589/1554 1588/1627/1588 1589/1628/1589\r\nf 1551/1586/1551 1575/1614/1575 1588/1627/1588 1554/1589/1554\r\nf 1574/1613/1574 1587/1626/1587 1588/1627/1588 1575/1614/1575\r\nf 1586/1625/1586 1589/1628/1589 1588/1627/1588 1587/1626/1587\r\nf 1421/1444/1421 1590/1629/1590 1591/1630/1591 1422/1445/1422\r\nf 1592/1631/1592 1593/1632/1593 1591/1630/1591 1590/1629/1590\r\nf 1594/1633/1594 1595/1634/1595 1591/1630/1591 1593/1632/1593\r\nf 1409/1432/1409 1422/1445/1422 1591/1630/1591 1595/1634/1595\r\nf 1596/1635/1596 1597/1636/1597 1598/1637/1598 1599/1638/1599\r\nf 1600/1639/1600 1601/1640/1601 1598/1637/1598 1597/1636/1597\r\nf 1594/1633/1594 1593/1632/1593 1598/1637/1598 1601/1640/1601\r\nf 1592/1631/1592 1599/1638/1599 1598/1637/1598 1593/1632/1593\r\nf 1382/1400/1382 1385/1403/1385 1602/1641/1602 1603/1642/1603\r\nf 1380/1395/1380 1604/1643/1604 1602/1641/1602 1385/1403/1385\r\nf 1594/1633/1594 1601/1640/1601 1602/1641/1602 1604/1643/1604\r\nf 1600/1639/1600 1603/1642/1603 1602/1641/1602 1601/1640/1601\r\nf 1376/1391/1376 1404/1427/1404 1605/1644/1605 1377/1392/1377\r\nf 1409/1432/1409 1595/1634/1595 1605/1644/1605 1404/1427/1404\r\nf 1594/1633/1594 1604/1643/1604 1605/1644/1605 1595/1634/1595\r\nf 1380/1395/1380 1377/1392/1377 1605/1644/1605 1604/1643/1604\r\nf 1596/1635/1596 1606/1645/1606 1607/1646/1607 1597/1636/1597\r\nf 1608/1647/1608 1609/1648/1609 1607/1646/1607 1606/1645/1606\r\nf 1610/1649/1610 1611/1650/1611 1607/1646/1607 1609/1648/1609\r\nf 1600/1639/1600 1597/1636/1597 1607/1646/1607 1611/1650/1611\r\nf 1538/1573/1538 1541/1576/1541 1612/1651/1612 1613/1652/1613\r\nf 1546/1581/1546 1614/1653/1614 1612/1651/1612 1541/1576/1541\r\nf 1610/1649/1610 1609/1648/1609 1612/1651/1612 1614/1653/1614\r\nf 1608/1647/1608 1613/1652/1613 1612/1651/1612 1609/1648/1609\r\nf 1308/1313/1308 1401/1423/1401 1615/1654/1615 1556/1591/1556\r\nf 1398/1418/1398 1616/1655/1616 1615/1654/1615 1401/1423/1401\r\nf 1610/1649/1610 1614/1653/1614 1615/1654/1615 1616/1655/1616\r\nf 1546/1581/1546 1556/1591/1556 1615/1654/1615 1614/1653/1614\r\nf 1382/1400/1382 1603/1642/1603 1617/1656/1617 1396/1416/1396\r\nf 1600/1639/1600 1611/1650/1611 1617/1656/1617 1603/1642/1603\r\nf 1610/1649/1610 1616/1655/1616 1617/1656/1617 1611/1650/1611\r\nf 1398/1418/1398 1396/1416/1396 1617/1656/1617 1616/1655/1616\r\nf 1107/1107/1107 1145/1145/1145 1618/1657/1618 1370/1385/1370\r\nf 1140/1140/1140 1619/1658/1619 1618/1657/1618 1145/1145/1145\r\nf 1620/1659/1620 1621/1660/1621 1618/1657/1618 1619/1658/1619\r\nf 1372/1387/1372 1370/1385/1370 1618/1657/1618 1621/1660/1621\r\nf 1018/1018/1018 1284/1284/1284 1622/1661/1622 1138/1138/1138\r\nf 1288/1288/1288 1623/1662/1623 1622/1661/1622 1284/1284/1284\r\nf 1620/1659/1620 1619/1658/1619 1622/1661/1622 1623/1662/1623\r\nf 1140/1140/1140 1138/1138/1138 1622/1661/1622 1619/1658/1619\r\nf 1298/1301/1298 1412/1435/1412 1624/1663/1624 1299/1302/1299\r\nf 1405/1428/1405 1625/1664/1625 1624/1663/1624 1412/1435/1412\r\nf 1620/1665/1620 1623/1666/1623 1624/1663/1624 1625/1664/1625\r\nf 1288/1305/1288 1299/1302/1299 1624/1663/1624 1623/1666/1623\r\nf 1376/1391/1376 1379/1394/1379 1626/1667/1626 1402/1425/1402\r\nf 1372/1399/1372 1621/1668/1621 1626/1667/1626 1379/1394/1379\r\nf 1620/1665/1620 1625/1664/1625 1626/1667/1626 1621/1668/1621\r\nf 1405/1428/1405 1402/1425/1402 1626/1667/1626 1625/1664/1625\r\nf 1233/1233/1233 1504/1530/1504 1627/1669/1627 1431/1454/1431\r\nf 1501/1532/1501 1628/1670/1628 1627/1669/1627 1504/1530/1504\r\nf 1629/1671/1629 1630/1672/1630 1627/1669/1627 1628/1670/1628\r\nf 1433/1456/1433 1431/1454/1431 1627/1669/1627 1630/1672/1630\r\nf 964/964/964 1631/1673/1631 1632/1674/1632 1499/1525/1499\r\nf 1633/1675/1633 1634/1676/1634 1632/1674/1632 1631/1673/1631\r\nf 1629/1677/1629 1628/1678/1628 1632/1674/1632 1634/1676/1634\r\nf 1501/1527/1501 1499/1525/1499 1632/1674/1632 1628/1678/1628\r\nf 1635/1679/1635 1636/1680/1636 1637/1681/1637 1638/1682/1638\r\nf 1639/1683/1639 1640/1684/1640 1637/1681/1637 1636/1680/1636\r\nf 1629/1677/1629 1634/1676/1634 1637/1681/1637 1640/1684/1640\r\nf 1633/1675/1633 1638/1682/1638 1637/1681/1637 1634/1676/1634\r\nf 1435/1458/1435 1438/1461/1438 1641/1685/1641 1642/1686/1642\r\nf 1433/1456/1433 1630/1672/1630 1641/1685/1641 1438/1461/1438\r\nf 1629/1671/1629 1640/1687/1640 1641/1685/1641 1630/1672/1630\r\nf 1639/1688/1639 1642/1686/1642 1641/1685/1641 1640/1687/1640\r\nf 1454/1477/1454 1643/1689/1643 1644/1690/1644 1455/1478/1455\r\nf 1645/1691/1645 1646/1692/1646 1644/1690/1644 1643/1689/1643\r\nf 1647/1693/1647 1648/1694/1648 1644/1690/1644 1646/1692/1646\r\nf 1458/1481/1458 1455/1478/1455 1644/1690/1644 1648/1694/1648\r\nf 1153/1153/1153 1188/1188/1188 1649/1695/1649 1650/1696/1650\r\nf 1182/1182/1182 1651/1697/1651 1649/1695/1649 1188/1188/1188\r\nf 1647/1698/1647 1646/1699/1646 1649/1695/1649 1651/1697/1651\r\nf 1645/1700/1645 1650/1696/1650 1649/1695/1649 1646/1699/1646\r\nf 1178/1178/1178 1652/1701/1652 1653/1702/1653 1179/1179/1179\r\nf 1654/1703/1654 1655/1704/1655 1653/1702/1653 1652/1701/1652\r\nf 1647/1698/1647 1651/1697/1651 1653/1702/1653 1655/1704/1655\r\nf 1182/1182/1182 1179/1179/1179 1653/1702/1653 1651/1697/1651\r\nf 1460/1483/1460 1463/1486/1463 1656/1705/1656 1657/1706/1657\r\nf 1458/1481/1458 1648/1694/1648 1656/1705/1656 1463/1486/1463\r\nf 1647/1693/1647 1655/1707/1655 1656/1705/1656 1648/1694/1648\r\nf 1654/1708/1654 1657/1706/1657 1656/1705/1656 1655/1707/1655\r\nf 1178/1178/1178 1658/1709/1658 1659/1710/1659 1652/1701/1652\r\nf 1660/1711/1660 1661/1712/1661 1659/1710/1659 1658/1709/1658\r\nf 1662/1713/1662 1663/1714/1663 1659/1710/1659 1661/1712/1661\r\nf 1654/1703/1654 1652/1701/1652 1659/1710/1659 1663/1714/1663\r\nf 1664/1715/1664 1665/1716/1665 1666/1717/1666 1667/1718/1667\r\nf 1668/1719/1668 1669/1720/1669 1666/1717/1666 1665/1716/1665\r\nf 1662/1713/1662 1661/1712/1661 1666/1717/1666 1669/1720/1669\r\nf 1660/1711/1660 1667/1718/1667 1666/1717/1666 1661/1712/1661\r\nf 1480/1503/1480 1483/1506/1483 1670/1721/1670 1671/1722/1671\r\nf 1478/1501/1478 1672/1723/1672 1670/1721/1670 1483/1506/1483\r\nf 1662/1724/1662 1669/1725/1669 1670/1721/1670 1672/1723/1672\r\nf 1668/1726/1668 1671/1722/1671 1670/1721/1670 1669/1725/1669\r\nf 1460/1483/1460 1657/1706/1657 1673/1727/1673 1476/1499/1476\r\nf 1654/1708/1654 1663/1728/1663 1673/1727/1673 1657/1706/1657\r\nf 1662/1724/1662 1672/1723/1672 1673/1727/1673 1663/1728/1663\r\nf 1478/1501/1478 1476/1499/1476 1673/1727/1673 1672/1723/1672\r\nf 1664/1715/1664 1667/1718/1667 1674/1729/1674 1675/1730/1675\r\nf 1660/1711/1660 1676/1731/1676 1674/1729/1674 1667/1718/1667\r\nf 1677/1732/1677 1678/1733/1678 1674/1729/1674 1676/1731/1676\r\nf 1679/1734/1679 1675/1730/1675 1674/1729/1674 1678/1733/1678\r\nf 1178/1178/1178 1181/1181/1181 1680/1735/1680 1658/1709/1658\r\nf 1186/1186/1186 1681/1736/1681 1680/1735/1680 1181/1181/1181\r\nf 1677/1732/1677 1676/1731/1676 1680/1735/1680 1681/1736/1681\r\nf 1660/1711/1660 1658/1709/1658 1680/1735/1680 1676/1731/1676\r\nf 1194/1194/1194 1682/1737/1682 1683/1738/1683 1195/1195/1195\r\nf 1684/1739/1684 1685/1740/1685 1683/1738/1683 1682/1737/1682\r\nf 1677/1732/1677 1681/1736/1681 1683/1738/1683 1685/1740/1685\r\nf 1186/1186/1186 1195/1195/1195 1683/1738/1683 1681/1736/1681\r\nf 1686/1741/1686 1687/1742/1687 1688/1743/1688 1689/1744/1689\r\nf 1679/1734/1679 1678/1733/1678 1688/1743/1688 1687/1742/1687\r\nf 1677/1732/1677 1685/1740/1685 1688/1743/1688 1678/1733/1678\r\nf 1684/1739/1684 1689/1744/1689 1688/1743/1688 1685/1740/1685\r\nf 1322/1332/1322 1325/1335/1325 1690/1745/1690 1691/1746/1691\r\nf 1330/1340/1330 1692/1747/1692 1690/1745/1690 1325/1335/1325\r\nf 1693/1748/1693 1694/1749/1694 1690/1745/1690 1692/1747/1692\r\nf 1695/1750/1695 1691/1746/1691 1690/1745/1690 1694/1749/1694\r\nf 1343/1353/1343 1696/1751/1696 1697/1752/1697 1344/1354/1344\r\nf 1698/1753/1698 1699/1754/1699 1697/1752/1697 1696/1751/1696\r\nf 1693/1748/1693 1692/1747/1692 1697/1752/1697 1699/1754/1699\r\nf 1330/1340/1330 1344/1354/1344 1697/1752/1697 1692/1747/1692\r\nf 1700/1755/1700 1701/1756/1701 1702/1757/1702 1703/1758/1703\r\nf 1704/1759/1704 1705/1760/1705 1702/1757/1702 1701/1756/1701\r\nf 1693/1748/1693 1699/1754/1699 1702/1757/1702 1705/1760/1705\r\nf 1698/1753/1698 1703/1758/1703 1702/1757/1702 1699/1754/1699\r\nf 1706/1761/1706 1707/1762/1707 1708/1763/1708 1709/1764/1709\r\nf 1695/1750/1695 1694/1749/1694 1708/1763/1708 1707/1762/1707\r\nf 1693/1748/1693 1705/1760/1705 1708/1763/1708 1694/1749/1694\r\nf 1704/1759/1704 1709/1764/1709 1708/1763/1708 1705/1760/1705\r\nf 1053/1053/1053 1565/1600/1565 1710/1765/1710 1198/1198/1198\r\nf 1560/1604/1560 1711/1766/1711 1710/1765/1710 1565/1600/1565\r\nf 1712/1767/1712 1713/1768/1713 1710/1765/1710 1711/1766/1711\r\nf 1200/1200/1200 1198/1198/1198 1710/1765/1710 1713/1768/1713\r\nf 1322/1332/1322 1691/1746/1691 1714/1769/1714 1558/1593/1558\r\nf 1695/1750/1695 1715/1770/1715 1714/1769/1714 1691/1746/1691\r\nf 1712/1771/1712 1711/1772/1711 1714/1769/1714 1715/1770/1715\r\nf 1560/1595/1560 1558/1593/1558 1714/1769/1714 1711/1772/1711\r\nf 1706/1761/1706 1716/1773/1716 1717/1774/1717 1707/1762/1707\r\nf 1718/1775/1718 1719/1776/1719 1717/1774/1717 1716/1773/1716\r\nf 1712/1771/1712 1715/1770/1715 1717/1774/1717 1719/1776/1719\r\nf 1695/1750/1695 1707/1762/1707 1717/1774/1717 1715/1770/1715\r\nf 1204/1204/1204 1207/1207/1207 1720/1777/1720 1721/1778/1721\r\nf 1200/1200/1200 1713/1768/1713 1720/1777/1720 1207/1207/1207\r\nf 1712/1767/1712 1719/1779/1719 1720/1777/1720 1713/1768/1713\r\nf 1718/1780/1718 1721/1778/1721 1720/1777/1720 1719/1779/1719\r\nf 1722/1781/1722 1723/1782/1723 1724/1783/1724 1725/1784/1725\r\nf 1726/1785/1726 1727/1786/1727 1724/1783/1724 1723/1782/1723\r\nf 1728/1787/1728 1729/1788/1729 1724/1783/1724 1727/1786/1727\r\nf 1730/1789/1730 1725/1784/1725 1724/1783/1724 1729/1788/1729\r\nf 1731/1790/1731 1732/1791/1732 1733/1792/1733 1734/1793/1734\r\nf 1735/1794/1735 1736/1795/1736 1733/1792/1733 1732/1791/1732\r\nf 1728/1787/1728 1727/1786/1727 1733/1792/1733 1736/1795/1736\r\nf 1726/1785/1726 1734/1793/1734 1733/1792/1733 1727/1786/1727\r\nf 1737/1796/1737 1738/1797/1738 1739/1798/1739 1740/1799/1740\r\nf 1741/1800/1741 1742/1801/1742 1739/1798/1739 1738/1797/1738\r\nf 1728/1787/1728 1736/1795/1736 1739/1798/1739 1742/1801/1742\r\nf 1735/1794/1735 1740/1799/1740 1739/1798/1739 1736/1795/1736\r\nf 1743/1802/1743 1744/1803/1744 1745/1804/1745 1746/1805/1746\r\nf 1730/1789/1730 1729/1788/1729 1745/1804/1745 1744/1803/1744\r\nf 1728/1787/1728 1742/1801/1742 1745/1804/1745 1729/1788/1729\r\nf 1741/1800/1741 1746/1805/1746 1745/1804/1745 1742/1801/1742\r\nf 1722/1781/1722 1725/1784/1725 1747/1806/1747 1748/1807/1748\r\nf 1730/1789/1730 1749/1808/1749 1747/1806/1747 1725/1784/1725\r\nf 1750/1809/1750 1751/1810/1751 1747/1806/1747 1749/1808/1749\r\nf 1752/1811/1752 1748/1807/1748 1747/1806/1747 1751/1810/1751\r\nf 1743/1802/1743 1753/1812/1753 1754/1813/1754 1744/1803/1744\r\nf 1755/1814/1755 1756/1815/1756 1754/1813/1754 1753/1812/1753\r\nf 1750/1809/1750 1749/1808/1749 1754/1813/1754 1756/1815/1756\r\nf 1730/1789/1730 1744/1803/1744 1754/1813/1754 1749/1808/1749\r\nf 1757/1816/1757 1758/1817/1758 1759/1818/1759 1760/1819/1760\r\nf 1761/1820/1761 1762/1821/1762 1759/1818/1759 1758/1817/1758\r\nf 1750/1809/1750 1756/1815/1756 1759/1818/1759 1762/1821/1762\r\nf 1755/1814/1755 1760/1819/1760 1759/1818/1759 1756/1815/1756\r\nf 1763/1822/1763 1764/1823/1764 1765/1824/1765 1766/1825/1766\r\nf 1752/1811/1752 1751/1810/1751 1765/1824/1765 1764/1823/1764\r\nf 1750/1809/1750 1762/1821/1762 1765/1824/1765 1751/1810/1751\r\nf 1761/1820/1761 1766/1825/1766 1765/1824/1765 1762/1821/1762\r\nf 1763/1822/1763 1766/1825/1766 1767/1826/1767 1768/1827/1768\r\nf 1761/1820/1761 1769/1828/1769 1767/1826/1767 1766/1825/1766\r\nf 1770/1829/1770 1771/1830/1771 1767/1826/1767 1769/1828/1769\r\nf 1772/1831/1772 1768/1827/1768 1767/1826/1767 1771/1830/1771\r\nf 1757/1816/1757 1773/1832/1773 1774/1833/1774 1758/1817/1758\r\nf 1775/1834/1775 1776/1835/1776 1774/1833/1774 1773/1832/1773\r\nf 1770/1829/1770 1769/1828/1769 1774/1833/1774 1776/1835/1776\r\nf 1761/1820/1761 1758/1817/1758 1774/1833/1774 1769/1828/1769\r\nf 1777/1836/1777 1778/1837/1778 1779/1838/1779 1780/1839/1780\r\nf 1781/1840/1781 1782/1841/1782 1779/1838/1779 1778/1837/1778\r\nf 1770/1829/1770 1776/1835/1776 1779/1838/1779 1782/1841/1782\r\nf 1775/1834/1775 1780/1839/1780 1779/1838/1779 1776/1835/1776\r\nf 1783/1842/1783 1784/1843/1784 1785/1844/1785 1786/1845/1786\r\nf 1772/1831/1772 1771/1830/1771 1785/1844/1785 1784/1843/1784\r\nf 1770/1829/1770 1782/1841/1782 1785/1844/1785 1771/1830/1771\r\nf 1781/1840/1781 1786/1845/1786 1785/1844/1785 1782/1841/1782\r\nf 1777/1836/1777 1787/1846/1787 1788/1847/1788 1778/1837/1778\r\nf 1789/1848/1789 1790/1849/1790 1788/1847/1788 1787/1846/1787\r\nf 1791/1850/1791 1792/1851/1792 1788/1847/1788 1790/1849/1790\r\nf 1781/1840/1781 1778/1837/1778 1788/1847/1788 1792/1851/1792\r\nf 1793/1852/1793 1794/1853/1794 1795/1854/1795 1796/1855/1796\r\nf 1797/1856/1797 1798/1857/1798 1795/1854/1795 1794/1853/1794\r\nf 1791/1850/1791 1790/1849/1790 1795/1854/1795 1798/1857/1798\r\nf 1789/1848/1789 1796/1855/1796 1795/1854/1795 1790/1849/1790\r\nf 1799/1858/1799 1800/1859/1800 1801/1860/1801 1802/1861/1802\r\nf 1803/1862/1803 1804/1863/1804 1801/1860/1801 1800/1859/1800\r\nf 1791/1850/1791 1798/1857/1798 1801/1860/1801 1804/1863/1804\r\nf 1797/1856/1797 1802/1861/1802 1801/1860/1801 1798/1857/1798\r\nf 1783/1842/1783 1786/1845/1786 1805/1864/1805 1806/1865/1806\r\nf 1781/1840/1781 1792/1851/1792 1805/1864/1805 1786/1845/1786\r\nf 1791/1850/1791 1804/1863/1804 1805/1864/1805 1792/1851/1792\r\nf 1803/1862/1803 1806/1865/1806 1805/1864/1805 1804/1863/1804\r\nf 1807/1866/1807 1808/1867/1808 1809/1868/1809 1810/1869/1810\r\nf 1811/1870/1811 1812/1871/1812 1809/1868/1809 1808/1867/1808\r\nf 1813/1872/1813 1814/1873/1814 1809/1868/1809 1812/1871/1812\r\nf 1815/1874/1815 1810/1869/1810 1809/1868/1809 1814/1873/1814\r\nf 1816/1875/1816 1817/1876/1817 1818/1877/1818 1819/1878/1819\r\nf 1820/1879/1820 1821/1880/1821 1818/1877/1818 1817/1876/1817\r\nf 1813/1872/1813 1812/1871/1812 1818/1877/1818 1821/1880/1821\r\nf 1811/1870/1811 1819/1878/1819 1818/1877/1818 1812/1871/1812\r\nf 1822/1881/1822 1823/1882/1823 1824/1883/1824 1825/1884/1825\r\nf 1826/1885/1826 1827/1886/1827 1824/1883/1824 1823/1882/1823\r\nf 1813/1872/1813 1821/1880/1821 1824/1883/1824 1827/1886/1827\r\nf 1820/1879/1820 1825/1884/1825 1824/1883/1824 1821/1880/1821\r\nf 1828/1887/1828 1829/1888/1829 1830/1889/1830 1831/1890/1831\r\nf 1815/1874/1815 1814/1873/1814 1830/1889/1830 1829/1888/1829\r\nf 1813/1872/1813 1827/1886/1827 1830/1889/1830 1814/1873/1814\r\nf 1826/1885/1826 1831/1890/1831 1830/1889/1830 1827/1886/1827\r\nf 1832/1891/1832 1833/1892/1833 1834/1893/1834 1835/1894/1835\r\nf 1836/1895/1836 1837/1896/1837 1834/1893/1834 1833/1892/1833\r\nf 1838/1897/1838 1839/1898/1839 1834/1893/1834 1837/1896/1837\r\nf 1840/1899/1840 1835/1894/1835 1834/1893/1834 1839/1898/1839\r\nf 1841/1900/1841 1842/1901/1842 1843/1902/1843 1844/1903/1844\r\nf 1845/1904/1845 1846/1905/1846 1843/1902/1843 1842/1901/1842\r\nf 1838/1897/1838 1837/1896/1837 1843/1902/1843 1846/1905/1846\r\nf 1836/1895/1836 1844/1903/1844 1843/1902/1843 1837/1896/1837\r\nf 1847/1906/1847 1848/1907/1848 1849/1908/1849 1850/1909/1850\r\nf 1851/1910/1851 1852/1911/1852 1849/1908/1849 1848/1907/1848\r\nf 1838/1897/1838 1846/1905/1846 1849/1908/1849 1852/1911/1852\r\nf 1845/1904/1845 1850/1909/1850 1849/1908/1849 1846/1905/1846\r\nf 1853/1912/1853 1854/1913/1854 1855/1914/1855 1856/1915/1856\r\nf 1840/1899/1840 1839/1898/1839 1855/1914/1855 1854/1913/1854\r\nf 1838/1897/1838 1852/1911/1852 1855/1914/1855 1839/1898/1839\r\nf 1851/1910/1851 1856/1915/1856 1855/1914/1855 1852/1911/1852\r\nf 1822/1881/1822 1825/1884/1825 1857/1916/1857 1858/1917/1858\r\nf 1820/1879/1820 1859/1918/1859 1857/1916/1857 1825/1884/1825\r\nf 1860/1919/1860 1861/1920/1861 1857/1916/1857 1859/1918/1859\r\nf 1862/1921/1862 1858/1917/1858 1857/1916/1857 1861/1920/1861\r\nf 1816/1875/1816 1863/1922/1863 1864/1923/1864 1817/1876/1817\r\nf 1865/1924/1865 1866/1925/1866 1864/1923/1864 1863/1922/1863\r\nf 1860/1919/1860 1859/1918/1859 1864/1923/1864 1866/1925/1866\r\nf 1820/1879/1820 1817/1876/1817 1864/1923/1864 1859/1918/1859\r\nf 1867/1926/1867 1868/1927/1868 1869/1928/1869 1870/1929/1870\r\nf 1871/1930/1871 1872/1931/1872 1869/1928/1869 1868/1927/1868\r\nf 1860/1919/1860 1866/1925/1866 1869/1928/1869 1872/1931/1872\r\nf 1865/1924/1865 1870/1929/1870 1869/1928/1869 1866/1925/1866\r\nf 1873/1932/1873 1874/1933/1874 1875/1934/1875 1876/1935/1876\r\nf 1862/1921/1862 1861/1920/1861 1875/1934/1875 1874/1933/1874\r\nf 1860/1919/1860 1872/1931/1872 1875/1934/1875 1861/1920/1861\r\nf 1871/1930/1871 1876/1935/1876 1875/1934/1875 1872/1931/1872\r\nf 1877/1936/1877 1878/1937/1878 1879/1938/1879 1880/1939/1880\r\nf 1881/1940/1881 1882/1941/1882 1879/1938/1879 1878/1937/1878\r\nf 1883/1942/1883 1884/1943/1884 1879/1938/1879 1882/1941/1882\r\nf 1885/1944/1885 1880/1939/1880 1879/1938/1879 1884/1943/1884\r\nf 1886/1945/1886 1887/1946/1887 1888/1947/1888 1889/1948/1889\r\nf 1890/1949/1890 1891/1950/1891 1888/1947/1888 1887/1946/1887\r\nf 1883/1942/1883 1882/1941/1882 1888/1947/1888 1891/1950/1891\r\nf 1881/1940/1881 1889/1948/1889 1888/1947/1888 1882/1941/1882\r\nf 1892/1951/1892 1893/1952/1893 1894/1953/1894 1895/1954/1895\r\nf 1896/1955/1896 1897/1956/1897 1894/1953/1894 1893/1952/1893\r\nf 1883/1942/1883 1891/1950/1891 1894/1953/1894 1897/1956/1897\r\nf 1890/1949/1890 1895/1954/1895 1894/1953/1894 1891/1950/1891\r\nf 1898/1957/1898 1899/1958/1899 1900/1959/1900 1901/1960/1901\r\nf 1885/1944/1885 1884/1943/1884 1900/1959/1900 1899/1958/1899\r\nf 1883/1942/1883 1897/1956/1897 1900/1959/1900 1884/1943/1884\r\nf 1896/1955/1896 1901/1960/1901 1900/1959/1900 1897/1956/1897\r\nf 1892/1951/1892 1895/1954/1895 1902/1961/1902 1903/1962/1903\r\nf 1890/1949/1890 1904/1963/1904 1902/1961/1902 1895/1954/1895\r\nf 1905/1964/1905 1906/1965/1906 1902/1961/1902 1904/1963/1904\r\nf 1907/1966/1907 1903/1962/1903 1902/1961/1902 1906/1965/1906\r\nf 1886/1945/1886 1908/1967/1908 1909/1968/1909 1887/1946/1887\r\nf 1910/1969/1910 1911/1970/1911 1909/1968/1909 1908/1967/1908\r\nf 1905/1964/1905 1904/1963/1904 1909/1968/1909 1911/1970/1911\r\nf 1890/1949/1890 1887/1946/1887 1909/1968/1909 1904/1963/1904\r\nf 1807/1866/1807 1810/1869/1810 1912/1971/1912 1913/1972/1913\r\nf 1815/1874/1815 1914/1973/1914 1912/1971/1912 1810/1869/1810\r\nf 1905/1964/1905 1911/1970/1911 1912/1971/1912 1914/1973/1914\r\nf 1910/1969/1910 1913/1972/1913 1912/1971/1912 1911/1970/1911\r\nf 1828/1887/1828 1915/1974/1915 1916/1975/1916 1829/1888/1829\r\nf 1907/1966/1907 1906/1965/1906 1916/1975/1916 1915/1974/1915\r\nf 1905/1964/1905 1914/1973/1914 1916/1975/1916 1906/1965/1906\r\nf 1815/1874/1815 1829/1888/1829 1916/1975/1916 1914/1973/1914\r\nf 1799/1858/1799 1802/1861/1802 1917/1976/1917 1918/1977/1918\r\nf 1797/1856/1797 1919/1978/1919 1917/1976/1917 1802/1861/1802\r\nf 1920/1979/1920 1921/1980/1921 1917/1976/1917 1919/1978/1919\r\nf 1922/1981/1922 1918/1977/1918 1917/1976/1917 1921/1980/1921\r\nf 1793/1852/1793 1923/1982/1923 1924/1983/1924 1794/1853/1794\r\nf 1925/1984/1925 1926/1985/1926 1924/1983/1924 1923/1982/1923\r\nf 1920/1979/1920 1919/1978/1919 1924/1983/1924 1926/1985/1926\r\nf 1797/1856/1797 1794/1853/1794 1924/1983/1924 1919/1978/1919\r\nf 1877/1936/1877 1880/1939/1880 1927/1986/1927 1928/1987/1928\r\nf 1885/1944/1885 1929/1988/1929 1927/1986/1927 1880/1939/1880\r\nf 1920/1979/1920 1926/1985/1926 1927/1986/1927 1929/1988/1929\r\nf 1925/1984/1925 1928/1987/1928 1927/1986/1927 1926/1985/1926\r\nf 1898/1957/1898 1930/1989/1930 1931/1990/1931 1899/1958/1899\r\nf 1922/1981/1922 1921/1980/1921 1931/1990/1931 1930/1989/1930\r\nf 1920/1979/1920 1929/1988/1929 1931/1990/1931 1921/1980/1921\r\nf 1885/1944/1885 1899/1958/1899 1931/1990/1931 1929/1988/1929\r\nf 1932/1991/1932 1933/1992/1933 1934/1993/1934 1935/1994/1935\r\nf 1936/1995/1936 1937/1996/1937 1934/1993/1934 1933/1992/1933\r\nf 1938/1997/1938 1939/1998/1939 1934/1993/1934 1937/1996/1937\r\nf 1940/1999/1940 1935/1994/1935 1934/1993/1934 1939/1998/1939\r\nf 1941/2000/1941 1942/2001/1942 1943/2002/1943 1944/2003/1944\r\nf 1945/2004/1945 1946/2005/1946 1943/2002/1943 1942/2001/1942\r\nf 1938/1997/1938 1937/1996/1937 1943/2002/1943 1946/2005/1946\r\nf 1936/1995/1936 1944/2003/1944 1943/2002/1943 1937/1996/1937\r\nf 1947/2006/1947 1948/2007/1948 1949/2008/1949 1950/2009/1950\r\nf 1951/2010/1951 1952/2011/1952 1949/2008/1949 1948/2007/1948\r\nf 1938/1997/1938 1946/2005/1946 1949/2008/1949 1952/2011/1952\r\nf 1945/2004/1945 1950/2009/1950 1949/2008/1949 1946/2005/1946\r\nf 1953/2012/1953 1954/2013/1954 1955/2014/1955 1956/2015/1956\r\nf 1940/1999/1940 1939/1998/1939 1955/2014/1955 1954/2013/1954\r\nf 1938/1997/1938 1952/2011/1952 1955/2014/1955 1939/1998/1939\r\nf 1951/2010/1951 1956/2015/1956 1955/2014/1955 1952/2011/1952\r\nf 1957/2016/1957 1958/2017/1958 1959/2018/1959 1960/2019/1960\r\nf 1961/2020/1961 1962/2021/1962 1959/2018/1959 1958/2017/1958\r\nf 1963/2022/1963 1964/2023/1964 1959/2018/1959 1962/2021/1962\r\nf 1965/2024/1965 1960/2019/1960 1959/2018/1959 1964/2023/1964\r\nf 1966/2025/1966 1967/2026/1967 1968/2027/1968 1969/2028/1969\r\nf 1970/2029/1970 1971/2030/1971 1968/2027/1968 1967/2026/1967\r\nf 1963/2022/1963 1962/2021/1962 1968/2027/1968 1971/2030/1971\r\nf 1961/2020/1961 1969/2028/1969 1968/2027/1968 1962/2021/1962\r\nf 1941/2000/1941 1944/2003/1944 1972/2031/1972 1973/2032/1973\r\nf 1936/1995/1936 1974/2033/1974 1972/2031/1972 1944/2003/1944\r\nf 1963/2022/1963 1971/2030/1971 1972/2031/1972 1974/2033/1974\r\nf 1970/2029/1970 1973/2032/1973 1972/2031/1972 1971/2030/1971\r\nf 1932/1991/1932 1975/2034/1975 1976/2035/1976 1933/1992/1933\r\nf 1965/2024/1965 1964/2023/1964 1976/2035/1976 1975/2034/1975\r\nf 1963/2022/1963 1974/2033/1974 1976/2035/1976 1964/2023/1964\r\nf 1936/1995/1936 1933/1992/1933 1976/2035/1976 1974/2033/1974\r\nf 1832/1891/1832 1835/1894/1835 1977/2036/1977 1978/2037/1978\r\nf 1840/1899/1840 1979/2038/1979 1977/2036/1977 1835/1894/1835\r\nf 1980/2039/1980 1981/2040/1981 1977/2036/1977 1979/2038/1979\r\nf 1982/2041/1982 1978/2037/1978 1977/2036/1977 1981/2040/1981\r\nf 1853/1912/1853 1983/2042/1983 1984/2043/1984 1854/1913/1854\r\nf 1985/2044/1985 1986/2045/1986 1984/2043/1984 1983/2042/1983\r\nf 1980/2039/1980 1979/2038/1979 1984/2043/1984 1986/2045/1986\r\nf 1840/1899/1840 1854/1913/1854 1984/2043/1984 1979/2038/1979\r\nf 1210/1210/1210 1213/1213/1213 1987/2046/1987 1988/2047/1988\r\nf 1208/1208/1208 1989/2048/1989 1987/2046/1987 1213/1213/1213\r\nf 1980/2039/1980 1986/2045/1986 1987/2046/1987 1989/2048/1989\r\nf 1985/2044/1985 1988/2047/1988 1987/2046/1987 1986/2045/1986\r\nf 1204/1204/1204 1990/2049/1990 1991/2050/1991 1205/1205/1205\r\nf 1982/2041/1982 1981/2040/1981 1991/2050/1991 1990/2049/1990\r\nf 1980/2039/1980 1989/2048/1989 1991/2050/1991 1981/2040/1981\r\nf 1208/1208/1208 1205/1205/1205 1991/2050/1991 1989/2048/1989\r\nf 1992/2051/1992 1993/2052/1993 1994/2053/1994 1995/2054/1995\r\nf 1996/2055/1996 1997/2056/1997 1994/2053/1994 1993/2052/1993\r\nf 1998/2057/1998 1999/2058/1999 1994/2053/1994 1997/2056/1997\r\nf 2000/2059/2000 1995/2054/1995 1994/2053/1994 1999/2058/1999\r\nf 2001/2060/2001 2002/2061/2002 2003/2062/2003 2004/2063/2004\r\nf 2005/2064/2005 2006/2065/2006 2003/2062/2003 2002/2061/2002\r\nf 1998/2057/1998 1997/2056/1997 2003/2062/2003 2006/2065/2006\r\nf 1996/2055/1996 2004/2063/2004 2003/2062/2003 1997/2056/1997\r\nf 2007/2066/2007 2008/2067/2008 2009/2068/2009 2010/2069/2010\r\nf 2011/2070/2011 2012/2071/2012 2009/2068/2009 2008/2067/2008\r\nf 1998/2057/1998 2006/2065/2006 2009/2068/2009 2012/2071/2012\r\nf 2005/2064/2005 2010/2069/2010 2009/2068/2009 2006/2065/2006\r\nf 2013/2072/2013 2014/2073/2014 2015/2074/2015 2016/2075/2016\r\nf 2000/2059/2000 1999/2058/1999 2015/2074/2015 2014/2073/2014\r\nf 1998/2057/1998 2012/2071/2012 2015/2074/2015 1999/2058/1999\r\nf 2011/2070/2011 2016/2075/2016 2015/2074/2015 2012/2071/2012\r\nf 2017/2076/2017 2018/2077/2018 2019/2078/2019 2020/2079/2020\r\nf 2021/2080/2021 2022/2081/2022 2019/2078/2019 2018/2077/2018\r\nf 2023/2082/2023 2024/2083/2024 2019/2078/2019 2022/2081/2022\r\nf 2025/2084/2025 2020/2079/2020 2019/2078/2019 2024/2083/2024\r\nf 1992/2051/1992 1995/2054/1995 2026/2085/2026 2027/2086/2027\r\nf 2000/2059/2000 2028/2087/2028 2026/2085/2026 1995/2054/1995\r\nf 2023/2082/2023 2022/2081/2022 2026/2085/2026 2028/2087/2028\r\nf 2021/2080/2021 2027/2086/2027 2026/2085/2026 2022/2081/2022\r\nf 2013/2072/2013 2029/2088/2029 2030/2089/2030 2014/2073/2014\r\nf 2031/2090/2031 2032/2091/2032 2030/2089/2030 2029/2088/2029\r\nf 2023/2082/2023 2028/2087/2028 2030/2089/2030 2032/2091/2032\r\nf 2000/2059/2000 2014/2073/2014 2030/2089/2030 2028/2087/2028\r\nf 2033/2092/2033 2034/2093/2034 2035/2094/2035 2036/2095/2036\r\nf 2025/2084/2025 2024/2083/2024 2035/2094/2035 2034/2093/2034\r\nf 2023/2082/2023 2032/2091/2032 2035/2094/2035 2024/2083/2024\r\nf 2031/2090/2031 2036/2095/2036 2035/2094/2035 2032/2091/2032\r\nf 2037/2096/2037 2038/2097/2038 2039/2098/2039 2040/2099/2040\r\nf 2041/2100/2041 2042/2101/2042 2039/2098/2039 2038/2097/2038\r\nf 2043/2102/2043 2044/2103/2044 2039/2098/2039 2042/2101/2042\r\nf 2045/2104/2045 2040/2099/2040 2039/2098/2039 2044/2103/2044\r\nf 2017/2076/2017 2020/2079/2020 2046/2105/2046 2047/2106/2047\r\nf 2025/2084/2025 2048/2107/2048 2046/2105/2046 2020/2079/2020\r\nf 2043/2102/2043 2042/2101/2042 2046/2105/2046 2048/2107/2048\r\nf 2041/2100/2041 2047/2106/2047 2046/2105/2046 2042/2101/2042\r\nf 2033/2092/2033 2049/2108/2049 2050/2109/2050 2034/2093/2034\r\nf 2051/2110/2051 2052/2111/2052 2050/2109/2050 2049/2108/2049\r\nf 2043/2102/2043 2048/2107/2048 2050/2109/2050 2052/2111/2052\r\nf 2025/2084/2025 2034/2093/2034 2050/2109/2050 2048/2107/2048\r\nf 2053/2112/2053 2054/2113/2054 2055/2114/2055 2056/2115/2056\r\nf 2045/2104/2045 2044/2103/2044 2055/2114/2055 2054/2113/2054\r\nf 2043/2102/2043 2052/2111/2052 2055/2114/2055 2044/2103/2044\r\nf 2051/2110/2051 2056/2115/2056 2055/2114/2055 2052/2111/2052\r\nf 1799/1858/1799 2057/2116/2057 2058/2117/2058 1800/1859/1800\r\nf 2059/2118/2059 2060/2119/2060 2058/2117/2058 2057/2116/2057\r\nf 2061/2120/2061 2062/2121/2062 2058/2117/2058 2060/2119/2060\r\nf 1803/1862/1803 1800/1859/1800 2058/2117/2058 2062/2121/2062\r\nf 2063/2122/2063 2064/2123/2064 2065/2124/2065 2066/2125/2066\r\nf 2067/2126/2067 2068/2127/2068 2065/2124/2065 2064/2123/2064\r\nf 2061/2128/2061 2060/2129/2060 2065/2124/2065 2068/2127/2068\r\nf 2059/2130/2059 2066/2125/2066 2065/2124/2065 2060/2129/2060\r\nf 2053/2112/2053 2069/2131/2069 2070/2132/2070 2071/2133/2071\r\nf 2072/2134/2072 2073/2135/2073 2070/2132/2070 2069/2131/2069\r\nf 2061/2128/2061 2068/2127/2068 2070/2132/2070 2073/2135/2073\r\nf 2067/2126/2067 2071/2133/2071 2070/2132/2070 2068/2127/2068\r\nf 1783/1842/1783 1806/1865/1806 2074/2136/2074 2075/2137/2075\r\nf 1803/1862/1803 2062/2121/2062 2074/2136/2074 1806/1865/1806\r\nf 2061/2120/2061 2073/2138/2073 2074/2136/2074 2062/2121/2062\r\nf 2072/2139/2072 2075/2137/2075 2074/2136/2074 2073/2138/2073\r\nf 1828/1887/1828 1831/1890/1831 2076/2140/2076 2077/2141/2077\r\nf 1826/1885/1826 2078/2142/2078 2076/2140/2076 1831/1890/1831\r\nf 2079/2143/2079 2080/2144/2080 2076/2140/2076 2078/2142/2078\r\nf 2081/2145/2081 2077/2141/2077 2076/2140/2076 2080/2144/2080\r\nf 1822/1881/1822 2082/2146/2082 2083/2147/2083 1823/1882/1823\r\nf 2084/2148/2084 2085/2149/2085 2083/2147/2083 2082/2146/2082\r\nf 2079/2143/2079 2078/2142/2078 2083/2147/2083 2085/2149/2085\r\nf 1826/1885/1826 1823/1882/1823 2083/2147/2083 2078/2142/2078\r\nf 2086/2150/2086 2087/2151/2087 2088/2152/2088 2089/2153/2089\r\nf 2090/2154/2090 2091/2155/2091 2088/2152/2088 2087/2151/2087\r\nf 2079/2156/2079 2085/2157/2085 2088/2152/2088 2091/2155/2091\r\nf 2084/2158/2084 2089/2153/2089 2088/2152/2088 2085/2157/2085\r\nf 2092/2159/2092 2093/2160/2093 2094/2161/2094 2095/2162/2095\r\nf 2081/2163/2081 2080/2164/2080 2094/2161/2094 2093/2160/2093\r\nf 2079/2156/2079 2091/2155/2091 2094/2161/2094 2080/2164/2080\r\nf 2090/2154/2090 2095/2162/2095 2094/2161/2094 2091/2155/2091\r\nf 2096/2165/2096 2097/2166/2097 2098/2167/2098 2099/2168/2099\r\nf 2100/2169/2100 2101/2170/2101 2098/2167/2098 2097/2166/2097\r\nf 2102/2171/2102 2103/2172/2103 2098/2167/2098 2101/2170/2101\r\nf 2104/2173/2104 2099/2168/2099 2098/2167/2098 2103/2172/2103\r\nf 2105/2174/2105 2106/2175/2106 2107/2176/2107 2108/2177/2108\r\nf 2109/2178/2109 2110/2179/2110 2107/2176/2107 2106/2175/2106\r\nf 2102/2171/2102 2101/2170/2101 2107/2176/2107 2110/2179/2110\r\nf 2100/2169/2100 2108/2177/2108 2107/2176/2107 2101/2170/2101\r\nf 2111/2180/2111 2112/2181/2112 2113/2182/2113 2114/2183/2114\r\nf 2115/2184/2115 2116/2185/2116 2113/2182/2113 2112/2181/2112\r\nf 2102/2171/2102 2110/2179/2110 2113/2182/2113 2116/2185/2116\r\nf 2109/2178/2109 2114/2183/2114 2113/2182/2113 2110/2179/2110\r\nf 2117/2186/2117 2118/2187/2118 2119/2188/2119 2120/2189/2120\r\nf 2104/2173/2104 2103/2172/2103 2119/2188/2119 2118/2187/2118\r\nf 2102/2171/2102 2116/2185/2116 2119/2188/2119 2103/2172/2103\r\nf 2115/2184/2115 2120/2189/2120 2119/2188/2119 2116/2185/2116\r\nf 1841/1900/1841 2121/2190/2121 2122/2191/2122 2123/2192/2123\r\nf 2124/2193/2124 2125/2194/2125 2122/2191/2122 2121/2190/2121\r\nf 2126/2195/2126 2127/2196/2127 2122/2191/2122 2125/2194/2125\r\nf 2128/2197/2128 2123/2192/2123 2122/2191/2122 2127/2196/2127\r\nf 2117/2186/2117 2129/2198/2129 2130/2199/2130 2131/2200/2131\r\nf 2132/2201/2132 2133/2202/2133 2130/2199/2130 2129/2198/2129\r\nf 2126/2203/2126 2125/2204/2125 2130/2199/2130 2133/2202/2133\r\nf 2124/2205/2124 2131/2200/2131 2130/2199/2130 2125/2204/2125\r\nf 2134/2206/2134 2135/2207/2135 2136/2208/2136 2137/2209/2137\r\nf 2138/2210/2138 2139/2211/2139 2136/2208/2136 2135/2207/2135\r\nf 2126/2203/2126 2133/2202/2133 2136/2208/2136 2139/2211/2139\r\nf 2132/2201/2132 2137/2209/2137 2136/2208/2136 2133/2202/2133\r\nf 2140/2212/2140 2141/2213/2141 2142/2214/2142 2143/2215/2143\r\nf 2128/2197/2128 2127/2196/2127 2142/2214/2142 2141/2213/2141\r\nf 2126/2195/2126 2139/2216/2139 2142/2214/2142 2127/2196/2127\r\nf 2138/2217/2138 2143/2215/2143 2142/2214/2142 2139/2216/2139\r\nf 1898/1957/1898 1901/1960/1901 2144/2218/2144 2145/2219/2145\r\nf 1896/1955/1896 2146/2220/2146 2144/2218/2144 1901/1960/1901\r\nf 2147/2221/2147 2148/2222/2148 2144/2218/2144 2146/2220/2146\r\nf 2149/2223/2149 2145/2219/2145 2144/2218/2144 2148/2222/2148\r\nf 1892/1951/1892 2150/2224/2150 2151/2225/2151 1893/1952/1893\r\nf 2152/2226/2152 2153/2227/2153 2151/2225/2151 2150/2224/2150\r\nf 2147/2221/2147 2146/2220/2146 2151/2225/2151 2153/2227/2153\r\nf 1896/1955/1896 1893/1952/1893 2151/2225/2151 2146/2220/2146\r\nf 2154/2228/2154 2155/2229/2155 2156/2230/2156 2157/2231/2157\r\nf 2158/2232/2158 2159/2233/2159 2156/2230/2156 2155/2229/2155\r\nf 2147/2234/2147 2153/2235/2153 2156/2230/2156 2159/2233/2159\r\nf 2152/2236/2152 2157/2231/2157 2156/2230/2156 2153/2235/2153\r\nf 2160/2237/2160 2161/2238/2161 2162/2239/2162 2163/2240/2163\r\nf 2149/2241/2149 2148/2242/2148 2162/2239/2162 2161/2238/2161\r\nf 2147/2234/2147 2159/2233/2159 2162/2239/2162 2148/2242/2148\r\nf 2158/2232/2158 2163/2240/2163 2162/2239/2162 2159/2233/2159\r\nf 1828/1887/1828 2077/2141/2077 2164/2243/2164 1915/1974/1915\r\nf 2081/2145/2081 2165/2244/2165 2164/2243/2164 2077/2141/2077\r\nf 2166/2245/2166 2167/2246/2167 2164/2243/2164 2165/2244/2165\r\nf 1907/1966/1907 1915/1974/1915 2164/2243/2164 2167/2246/2167\r\nf 2092/2159/2092 2168/2247/2168 2169/2248/2169 2093/2160/2093\r\nf 2170/2249/2170 2171/2250/2171 2169/2248/2169 2168/2247/2168\r\nf 2166/2251/2166 2165/2252/2165 2169/2248/2169 2171/2250/2171\r\nf 2081/2163/2081 2093/2160/2093 2169/2248/2169 2165/2252/2165\r\nf 2154/2228/2154 2157/2231/2157 2172/2253/2172 2173/2254/2173\r\nf 2152/2236/2152 2174/2255/2174 2172/2253/2172 2157/2231/2157\r\nf 2166/2251/2166 2171/2250/2171 2172/2253/2172 2174/2255/2174\r\nf 2170/2249/2170 2173/2254/2173 2172/2253/2172 2171/2250/2171\r\nf 1892/1951/1892 1903/1962/1903 2175/2256/2175 2150/2224/2150\r\nf 1907/1966/1907 2167/2246/2167 2175/2256/2175 1903/1962/1903\r\nf 2166/2245/2166 2174/2257/2174 2175/2256/2175 2167/2246/2167\r\nf 2152/2226/2152 2150/2224/2150 2175/2256/2175 2174/2257/2174\r\nf 2160/2237/2160 2176/2258/2176 2177/2259/2177 2178/2260/2178\r\nf 2179/2261/2179 2180/2262/2180 2177/2259/2177 2176/2258/2176\r\nf 2181/2263/2181 2182/2264/2182 2177/2259/2177 2180/2262/2180\r\nf 2183/2265/2183 2178/2260/2178 2177/2259/2177 2182/2264/2182\r\nf 2184/2266/2184 2185/2267/2185 2186/2268/2186 2187/2269/2187\r\nf 2188/2270/2188 2189/2271/2189 2186/2268/2186 2185/2267/2185\r\nf 2181/2263/2181 2180/2262/2180 2186/2268/2186 2189/2271/2189\r\nf 2179/2261/2179 2187/2269/2187 2186/2268/2186 2180/2262/2180\r\nf 2190/2272/2190 2191/2273/2191 2192/2274/2192 2193/2275/2193\r\nf 2194/2276/2194 2195/2277/2195 2192/2274/2192 2191/2273/2191\r\nf 2181/2263/2181 2189/2271/2189 2192/2274/2192 2195/2277/2195\r\nf 2188/2270/2188 2193/2275/2193 2192/2274/2192 2189/2271/2189\r\nf 2063/2122/2063 2196/2278/2196 2197/2279/2197 2198/2280/2198\r\nf 2183/2265/2183 2182/2264/2182 2197/2279/2197 2196/2278/2196\r\nf 2181/2263/2181 2195/2277/2195 2197/2279/2197 2182/2264/2182\r\nf 2194/2276/2194 2198/2280/2198 2197/2279/2197 2195/2277/2195\r\nf 2001/2060/2001 2199/2281/2199 2200/2282/2200 2002/2061/2002\r\nf 2201/2283/2201 2202/2284/2202 2200/2282/2200 2199/2281/2199\r\nf 2203/2285/2203 2204/2286/2204 2200/2282/2200 2202/2284/2202\r\nf 2005/2064/2005 2002/2061/2002 2200/2282/2200 2204/2286/2204\r\nf 2205/2287/2205 2206/2288/2206 2207/2289/2207 2208/2290/2208\r\nf 2209/2291/2209 2210/2292/2210 2207/2289/2207 2206/2288/2206\r\nf 2203/2285/2203 2202/2284/2202 2207/2289/2207 2210/2292/2210\r\nf 2201/2283/2201 2208/2290/2208 2207/2289/2207 2202/2284/2202\r\nf 2211/2293/2211 2212/2294/2212 2213/2295/2213 2214/2296/2214\r\nf 2215/2297/2215 2216/2298/2216 2213/2295/2213 2212/2294/2212\r\nf 2203/2285/2203 2210/2292/2210 2213/2295/2213 2216/2298/2216\r\nf 2209/2291/2209 2214/2296/2214 2213/2295/2213 2210/2292/2210\r\nf 2007/2066/2007 2010/2069/2010 2217/2299/2217 2218/2300/2218\r\nf 2005/2064/2005 2204/2286/2204 2217/2299/2217 2010/2069/2010\r\nf 2203/2285/2203 2216/2298/2216 2217/2299/2217 2204/2286/2204\r\nf 2215/2297/2215 2218/2300/2218 2217/2299/2217 2216/2298/2216\r\nf 2219/2301/2219 2220/2302/2220 2221/2303/2221 2222/2304/2222\r\nf 2223/2305/2223 2224/2306/2224 2221/2303/2221 2220/2302/2220\r\nf 2225/2307/2225 2226/2308/2226 2221/2303/2221 2224/2306/2224\r\nf 2227/2309/2227 2222/2304/2222 2221/2303/2221 2226/2308/2226\r\nf 2228/2310/2228 2229/2311/2229 2230/2312/2230 2231/2313/2231\r\nf 2232/2314/2232 2233/2315/2233 2230/2312/2230 2229/2311/2229\r\nf 2225/2307/2225 2224/2306/2224 2230/2312/2230 2233/2315/2233\r\nf 2223/2305/2223 2231/2313/2231 2230/2312/2230 2224/2306/2224\r\nf 2234/2316/2234 2235/2317/2235 2236/2318/2236 2237/2319/2237\r\nf 2238/2320/2238 2239/2321/2239 2236/2318/2236 2235/2317/2235\r\nf 2225/2307/2225 2233/2315/2233 2236/2318/2236 2239/2321/2239\r\nf 2232/2314/2232 2237/2319/2237 2236/2318/2236 2233/2315/2233\r\nf 2240/2322/2240 2241/2323/2241 2242/2324/2242 2243/2325/2243\r\nf 2227/2309/2227 2226/2308/2226 2242/2324/2242 2241/2323/2241\r\nf 2225/2307/2225 2239/2321/2239 2242/2324/2242 2226/2308/2226\r\nf 2238/2320/2238 2243/2325/2243 2242/2324/2242 2239/2321/2239\r\nf 2228/2310/2228 2244/2326/2244 2245/2327/2245 2229/2311/2229\r\nf 2246/2328/2246 2247/2329/2247 2245/2327/2245 2244/2326/2244\r\nf 2248/2330/2248 2249/2331/2249 2245/2327/2245 2247/2329/2247\r\nf 2232/2314/2232 2229/2311/2229 2245/2327/2245 2249/2331/2249\r\nf 1486/2332/1486 1489/2333/1489 2250/2334/2250 2251/2335/2251\r\nf 1484/2336/1484 2252/2337/2252 2250/2334/2250 1489/2333/1489\r\nf 2248/2330/2248 2247/2329/2247 2250/2334/2250 2252/2337/2252\r\nf 2246/2328/2246 2251/2335/2251 2250/2334/2250 2247/2329/2247\r\nf 1480/2338/1480 2253/2339/2253 2254/2340/2254 1481/2341/1481\r\nf 2255/2342/2255 2256/2343/2256 2254/2340/2254 2253/2339/2253\r\nf 2248/2330/2248 2252/2337/2252 2254/2340/2254 2256/2343/2256\r\nf 1484/2336/1484 1481/2341/1481 2254/2340/2254 2252/2337/2252\r\nf 2234/2316/2234 2237/2319/2237 2257/2344/2257 2258/2345/2258\r\nf 2232/2314/2232 2249/2331/2249 2257/2344/2257 2237/2319/2237\r\nf 2248/2330/2248 2256/2343/2256 2257/2344/2257 2249/2331/2249\r\nf 2255/2342/2255 2258/2345/2258 2257/2344/2257 2256/2343/2256\r\nf 2013/2072/2013 2016/2075/2016 2259/2346/2259 2260/2347/2260\r\nf 2011/2070/2011 2261/2348/2261 2259/2346/2259 2016/2075/2016\r\nf 2262/2349/2262 2263/2350/2263 2259/2346/2259 2261/2348/2261\r\nf 2264/2351/2264 2260/2347/2260 2259/2346/2259 2263/2350/2263\r\nf 2007/2066/2007 2265/2352/2265 2266/2353/2266 2008/2067/2008\r\nf 2267/2354/2267 2268/2355/2268 2266/2353/2266 2265/2352/2265\r\nf 2262/2349/2262 2261/2348/2261 2266/2353/2266 2268/2355/2268\r\nf 2011/2070/2011 2008/2067/2008 2266/2353/2266 2261/2348/2261\r\nf 1731/1790/1731 1734/1793/1734 2269/2356/2269 2270/2357/2270\r\nf 1726/1785/1726 2271/2358/2271 2269/2356/2269 1734/1793/1734\r\nf 2262/2359/2262 2268/2360/2268 2269/2356/2269 2271/2358/2271\r\nf 2267/2361/2267 2270/2357/2270 2269/2356/2269 2268/2360/2268\r\nf 1722/1781/1722 2272/2362/2272 2273/2363/2273 1723/1782/1723\r\nf 2264/2364/2264 2263/2365/2263 2273/2363/2273 2272/2362/2272\r\nf 2262/2359/2262 2271/2358/2271 2273/2363/2273 2263/2365/2263\r\nf 1726/1785/1726 1723/1782/1723 2273/2363/2273 2271/2358/2271\r\nf 1763/1822/1763 2274/2366/2274 2275/2367/2275 1764/1823/1764\r\nf 2276/2368/2276 2277/2369/2277 2275/2367/2275 2274/2366/2274\r\nf 2278/2370/2278 2279/2371/2279 2275/2367/2275 2277/2369/2277\r\nf 1752/1811/1752 1764/1823/1764 2275/2367/2275 2279/2371/2279\r\nf 2033/2092/2033 2036/2095/2036 2280/2372/2280 2281/2373/2281\r\nf 2031/2090/2031 2282/2374/2282 2280/2372/2280 2036/2095/2036\r\nf 2278/2375/2278 2277/2376/2277 2280/2372/2280 2282/2374/2282\r\nf 2276/2377/2276 2281/2373/2281 2280/2372/2280 2277/2376/2277\r\nf 2013/2072/2013 2260/2347/2260 2283/2378/2283 2029/2088/2029\r\nf 2264/2351/2264 2284/2379/2284 2283/2378/2283 2260/2347/2260\r\nf 2278/2375/2278 2282/2374/2282 2283/2378/2283 2284/2379/2284\r\nf 2031/2090/2031 2029/2088/2029 2283/2378/2283 2282/2374/2282\r\nf 1722/1781/1722 1748/1807/1748 2285/2380/2285 2272/2362/2272\r\nf 1752/1811/1752 2279/2371/2279 2285/2380/2285 1748/1807/1748\r\nf 2278/2370/2278 2284/2381/2284 2285/2380/2285 2279/2371/2279\r\nf 2264/2364/2264 2272/2362/2272 2285/2380/2285 2284/2381/2284\r\nf 1783/1842/1783 2075/2137/2075 2286/2382/2286 1784/1843/1784\r\nf 2072/2139/2072 2287/2383/2287 2286/2382/2286 2075/2137/2075\r\nf 2288/2384/2288 2289/2385/2289 2286/2382/2286 2287/2383/2287\r\nf 1772/1831/1772 1784/1843/1784 2286/2382/2286 2289/2385/2289\r\nf 2053/2112/2053 2056/2115/2056 2290/2386/2290 2069/2131/2069\r\nf 2051/2110/2051 2291/2387/2291 2290/2386/2290 2056/2115/2056\r\nf 2288/2388/2288 2287/2389/2287 2290/2386/2290 2291/2387/2291\r\nf 2072/2134/2072 2069/2131/2069 2290/2386/2290 2287/2389/2287\r\nf 2033/2092/2033 2281/2373/2281 2292/2390/2292 2049/2108/2049\r\nf 2276/2377/2276 2293/2391/2293 2292/2390/2292 2281/2373/2281\r\nf 2288/2388/2288 2291/2387/2291 2292/2390/2292 2293/2391/2293\r\nf 2051/2110/2051 2049/2108/2049 2292/2390/2292 2291/2387/2291\r\nf 1763/1822/1763 1768/1827/1768 2294/2392/2294 2274/2366/2274\r\nf 1772/1831/1772 2289/2385/2289 2294/2392/2294 1768/1827/1768\r\nf 2288/2384/2288 2293/2393/2293 2294/2392/2294 2289/2385/2289\r\nf 2276/2368/2276 2274/2366/2274 2294/2392/2294 2293/2393/2293\r\nf 2037/2096/2037 2040/2099/2040 2295/2394/2295 2296/2395/2296\r\nf 2045/2104/2045 2297/2396/2297 2295/2394/2295 2040/2099/2040\r\nf 2298/2397/2298 2299/2398/2299 2295/2394/2295 2297/2396/2297\r\nf 2300/2399/2300 2296/2395/2296 2295/2394/2295 2299/2398/2299\r\nf 2053/2112/2053 2071/2133/2071 2301/2400/2301 2054/2113/2054\r\nf 2067/2126/2067 2302/2401/2302 2301/2400/2301 2071/2133/2071\r\nf 2298/2397/2298 2297/2396/2297 2301/2400/2301 2302/2401/2302\r\nf 2045/2104/2045 2054/2113/2054 2301/2400/2301 2297/2396/2297\r\nf 2063/2122/2063 2198/2280/2198 2303/2402/2303 2064/2123/2064\r\nf 2194/2276/2194 2304/2403/2304 2303/2402/2303 2198/2280/2198\r\nf 2298/2397/2298 2302/2401/2302 2303/2402/2303 2304/2403/2304\r\nf 2067/2126/2067 2064/2123/2064 2303/2402/2303 2302/2401/2302\r\nf 2190/2272/2190 2305/2404/2305 2306/2405/2306 2191/2273/2191\r\nf 2300/2399/2300 2299/2398/2299 2306/2405/2306 2305/2404/2305\r\nf 2298/2397/2298 2304/2403/2304 2306/2405/2306 2299/2398/2299\r\nf 2194/2276/2194 2191/2273/2191 2306/2405/2306 2304/2403/2304\r\nf 2307/2406/2307 2308/2407/2308 2309/2408/2309 2310/2409/2310\r\nf 2311/2410/2311 2312/2411/2312 2309/2408/2309 2308/2407/2308\r\nf 2313/2412/2313 2314/2413/2314 2309/2408/2309 2312/2411/2312\r\nf 2315/2414/2315 2310/2409/2310 2309/2408/2309 2314/2413/2314\r\nf 2092/2159/2092 2095/2162/2095 2316/2415/2316 2317/2416/2317\r\nf 2090/2154/2090 2318/2417/2318 2316/2415/2316 2095/2162/2095\r\nf 2313/2412/2313 2312/2411/2312 2316/2415/2316 2318/2417/2318\r\nf 2311/2410/2311 2317/2416/2317 2316/2415/2316 2312/2411/2312\r\nf 2086/2150/2086 2319/2418/2319 2320/2419/2320 2087/2151/2087\r\nf 2321/2420/2321 2322/2421/2322 2320/2419/2320 2319/2418/2319\r\nf 2313/2412/2313 2318/2417/2318 2320/2419/2320 2322/2421/2322\r\nf 2090/2154/2090 2087/2151/2087 2320/2419/2320 2318/2417/2318\r\nf 2323/2422/2323 2324/2423/2324 2325/2424/2325 2326/2425/2326\r\nf 2315/2414/2315 2314/2413/2314 2325/2424/2325 2324/2423/2324\r\nf 2313/2412/2313 2322/2421/2322 2325/2424/2325 2314/2413/2314\r\nf 2321/2420/2321 2326/2425/2326 2325/2424/2325 2322/2421/2322\r\nf 2096/2165/2096 2099/2168/2099 2327/2426/2327 2328/2427/2328\r\nf 2104/2173/2104 2329/2428/2329 2327/2426/2327 2099/2168/2099\r\nf 2330/2429/2330 2331/2430/2331 2327/2426/2327 2329/2428/2329\r\nf 2332/2431/2332 2328/2427/2328 2327/2426/2327 2331/2430/2331\r\nf 2117/2186/2117 2131/2200/2131 2333/2432/2333 2118/2187/2118\r\nf 2124/2205/2124 2334/2433/2334 2333/2432/2333 2131/2200/2131\r\nf 2330/2429/2330 2329/2428/2329 2333/2432/2333 2334/2433/2334\r\nf 2104/2173/2104 2118/2187/2118 2333/2432/2333 2329/2428/2329\r\nf 1841/1900/1841 1844/1903/1844 2335/2434/2335 2121/2190/2121\r\nf 1836/1895/1836 2336/2435/2336 2335/2434/2335 1844/1903/1844\r\nf 2330/2436/2330 2334/2437/2334 2335/2434/2335 2336/2435/2336\r\nf 2124/2193/2124 2121/2190/2121 2335/2434/2335 2334/2437/2334\r\nf 1832/1891/1832 2337/2438/2337 2338/2439/2338 1833/1892/1833\r\nf 2332/2440/2332 2331/2441/2331 2338/2439/2338 2337/2438/2337\r\nf 2330/2436/2330 2336/2435/2336 2338/2439/2338 2331/2441/2331\r\nf 1836/1895/1836 1833/1892/1833 2338/2439/2338 2336/2435/2336\r\nf 2323/2422/2323 2326/2425/2326 2339/2442/2339 2340/2443/2340\r\nf 2321/2420/2321 2341/2444/2341 2339/2442/2339 2326/2425/2326\r\nf 2342/2445/2342 2343/2446/2343 2339/2442/2339 2341/2444/2341\r\nf 2344/2447/2344 2340/2443/2340 2339/2442/2339 2343/2446/2343\r\nf 2086/2150/2086 2345/2448/2345 2346/2449/2346 2319/2418/2319\r\nf 2347/2450/2347 2348/2451/2348 2346/2449/2346 2345/2448/2345\r\nf 2342/2445/2342 2341/2444/2341 2346/2449/2346 2348/2451/2348\r\nf 2321/2420/2321 2319/2418/2319 2346/2449/2346 2341/2444/2341\r\nf 2349/2452/2349 2350/2453/2350 2351/2454/2351 2352/2455/2352\r\nf 2353/2456/2353 2354/2457/2354 2351/2454/2351 2350/2453/2350\r\nf 2342/2445/2342 2348/2451/2348 2351/2454/2351 2354/2457/2354\r\nf 2347/2450/2347 2352/2455/2352 2351/2454/2351 2348/2451/2348\r\nf 2355/2458/2355 2356/2459/2356 2357/2460/2357 2358/2461/2358\r\nf 2344/2447/2344 2343/2446/2343 2357/2460/2357 2356/2459/2356\r\nf 2342/2445/2342 2354/2457/2354 2357/2460/2357 2343/2446/2343\r\nf 2353/2456/2353 2358/2461/2358 2357/2460/2357 2354/2457/2354\r\nf 2184/2266/2184 2187/2269/2187 2359/2462/2359 2360/2463/2360\r\nf 2179/2261/2179 2361/2464/2361 2359/2462/2359 2187/2269/2187\r\nf 2362/2465/2362 2363/2466/2363 2359/2462/2359 2361/2464/2361\r\nf 2364/2467/2364 2360/2463/2360 2359/2462/2359 2363/2466/2363\r\nf 2160/2237/2160 2163/2240/2163 2365/2468/2365 2176/2258/2176\r\nf 2158/2232/2158 2366/2469/2366 2365/2468/2365 2163/2240/2163\r\nf 2362/2465/2362 2361/2464/2361 2365/2468/2365 2366/2469/2366\r\nf 2179/2261/2179 2176/2258/2176 2365/2468/2365 2361/2464/2361\r\nf 2154/2228/2154 2367/2470/2367 2368/2471/2368 2155/2229/2155\r\nf 2369/2472/2369 2370/2473/2370 2368/2471/2368 2367/2470/2367\r\nf 2362/2465/2362 2366/2469/2366 2368/2471/2368 2370/2473/2370\r\nf 2158/2232/2158 2155/2229/2155 2368/2471/2368 2366/2469/2366\r\nf 2371/2474/2371 2372/2475/2372 2373/2476/2373 2374/2477/2374\r\nf 2364/2467/2364 2363/2466/2363 2373/2476/2373 2372/2475/2372\r\nf 2362/2465/2362 2370/2473/2370 2373/2476/2373 2363/2466/2363\r\nf 2369/2472/2369 2374/2477/2374 2373/2476/2373 2370/2473/2370\r\nf 2371/2474/2371 2374/2477/2374 2375/2478/2375 2376/2479/2376\r\nf 2369/2472/2369 2377/2480/2377 2375/2478/2375 2374/2477/2374\r\nf 2378/2481/2378 2379/2482/2379 2375/2478/2375 2377/2480/2377\r\nf 2380/2483/2380 2376/2479/2376 2375/2478/2375 2379/2482/2379\r\nf 2154/2228/2154 2173/2254/2173 2381/2484/2381 2367/2470/2367\r\nf 2170/2249/2170 2382/2485/2382 2381/2484/2381 2173/2254/2173\r\nf 2378/2481/2378 2377/2480/2377 2381/2484/2381 2382/2485/2382\r\nf 2369/2472/2369 2367/2470/2367 2381/2484/2381 2377/2480/2377\r\nf 2092/2159/2092 2317/2416/2317 2383/2486/2383 2168/2247/2168\r\nf 2311/2410/2311 2384/2487/2384 2383/2486/2383 2317/2416/2317\r\nf 2378/2481/2378 2382/2485/2382 2383/2486/2383 2384/2487/2384\r\nf 2170/2249/2170 2168/2247/2168 2383/2486/2383 2382/2485/2382\r\nf 2307/2406/2307 2385/2488/2385 2386/2489/2386 2308/2407/2308\r\nf 2380/2483/2380 2379/2482/2379 2386/2489/2386 2385/2488/2385\r\nf 2378/2481/2378 2384/2487/2384 2386/2489/2386 2379/2482/2379\r\nf 2311/2410/2311 2308/2407/2308 2386/2489/2386 2384/2487/2384\r\nf 1898/1957/1898 2145/2219/2145 2387/2490/2387 1930/1989/1930\r\nf 2149/2223/2149 2388/2491/2388 2387/2490/2387 2145/2219/2145\r\nf 2389/2492/2389 2390/2493/2390 2387/2490/2387 2388/2491/2388\r\nf 1922/1981/1922 1930/1989/1930 2387/2490/2387 2390/2493/2390\r\nf 2160/2237/2160 2178/2260/2178 2391/2494/2391 2161/2238/2161\r\nf 2183/2265/2183 2392/2495/2392 2391/2494/2391 2178/2260/2178\r\nf 2389/2496/2389 2388/2497/2388 2391/2494/2391 2392/2495/2392\r\nf 2149/2241/2149 2161/2238/2161 2391/2494/2391 2388/2497/2388\r\nf 2063/2122/2063 2066/2125/2066 2393/2498/2393 2196/2278/2196\r\nf 2059/2130/2059 2394/2499/2394 2393/2498/2393 2066/2125/2066\r\nf 2389/2496/2389 2392/2495/2392 2393/2498/2393 2394/2499/2394\r\nf 2183/2265/2183 2196/2278/2196 2393/2498/2393 2392/2495/2392\r\nf 1799/1858/1799 1918/1977/1918 2395/2500/2395 2057/2116/2057\r\nf 1922/1981/1922 2390/2493/2390 2395/2500/2395 1918/1977/1918\r\nf 2389/2492/2389 2394/2501/2394 2395/2500/2395 2390/2493/2390\r\nf 2059/2118/2059 2057/2116/2057 2395/2500/2395 2394/2501/2394\r\nf 2007/2066/2007 2218/2300/2218 2396/2502/2396 2265/2352/2265\r\nf 2215/2297/2215 2397/2503/2397 2396/2502/2396 2218/2300/2218\r\nf 2398/2504/2398 2399/2505/2399 2396/2502/2396 2397/2503/2397\r\nf 2267/2354/2267 2265/2352/2265 2396/2502/2396 2399/2505/2399\r\nf 2211/2293/2211 2400/2506/2400 2401/2507/2401 2212/2294/2212\r\nf 2402/2508/2402 2403/2509/2403 2401/2507/2401 2400/2506/2400\r\nf 2398/2504/2398 2397/2503/2397 2401/2507/2401 2403/2509/2403\r\nf 2215/2297/2215 2212/2294/2212 2401/2507/2401 2397/2503/2397\r\nf 2404/2510/2404 2405/2511/2405 2406/2512/2406 2407/2513/2407\r\nf 2408/2514/2408 2409/2515/2409 2406/2512/2406 2405/2511/2405\r\nf 2398/2516/2398 2403/2517/2403 2406/2512/2406 2409/2515/2409\r\nf 2402/2518/2402 2407/2513/2407 2406/2512/2406 2403/2517/2403\r\nf 1731/1790/1731 2270/2357/2270 2410/2519/2410 2411/2520/2411\r\nf 2267/2361/2267 2399/2521/2399 2410/2519/2410 2270/2357/2270\r\nf 2398/2516/2398 2409/2515/2409 2410/2519/2410 2399/2521/2399\r\nf 2408/2514/2408 2411/2520/2411 2410/2519/2410 2409/2515/2409\r\nf 2240/2322/2240 2243/2325/2243 2412/2522/2412 2413/2523/2413\r\nf 2238/2320/2238 2414/2524/2414 2412/2522/2412 2243/2325/2243\r\nf 2415/2525/2415 2416/2526/2416 2412/2522/2412 2414/2524/2414\r\nf 2417/2527/2417 2413/2523/2413 2412/2522/2412 2416/2526/2416\r\nf 2234/2316/2234 2418/2528/2418 2419/2529/2419 2235/2317/2235\r\nf 2420/2530/2420 2421/2531/2421 2419/2529/2419 2418/2528/2418\r\nf 2415/2525/2415 2414/2524/2414 2419/2529/2419 2421/2531/2421\r\nf 2238/2320/2238 2235/2317/2235 2419/2529/2419 2414/2524/2414\r\nf 1957/2016/1957 1960/2019/1960 2422/2532/2422 2423/2533/2423\r\nf 1965/2024/1965 2424/2534/2424 2422/2532/2422 1960/2019/1960\r\nf 2415/2535/2415 2421/2536/2421 2422/2532/2422 2424/2534/2424\r\nf 2420/2537/2420 2423/2533/2423 2422/2532/2422 2421/2536/2421\r\nf 1932/1991/1932 2425/2538/2425 2426/2539/2426 1975/2034/1975\r\nf 2417/2540/2417 2416/2541/2416 2426/2539/2426 2425/2538/2425\r\nf 2415/2535/2415 2424/2534/2424 2426/2539/2426 2416/2541/2416\r\nf 1965/2024/1965 1975/2034/1975 2426/2539/2426 2424/2534/2424\r\nf 1957/2016/1957 2423/2533/2423 2427/2542/2427 2428/2543/2428\r\nf 2420/2537/2420 2429/2544/2429 2427/2542/2427 2423/2533/2423\r\nf 2430/2545/2430 2431/2546/2431 2427/2542/2427 2429/2544/2429\r\nf 2432/2547/2432 2428/2543/2428 2427/2542/2427 2431/2546/2431\r\nf 2234/2316/2234 2258/2345/2258 2433/2548/2433 2418/2528/2418\r\nf 2255/2342/2255 2434/2549/2434 2433/2548/2433 2258/2345/2258\r\nf 2430/2550/2430 2429/2551/2429 2433/2548/2433 2434/2549/2434\r\nf 2420/2530/2420 2418/2528/2418 2433/2548/2433 2429/2551/2429\r\nf 1480/2338/1480 1671/2552/1671 2435/2553/2435 2253/2339/2253\r\nf 1668/2554/1668 2436/2555/2436 2435/2553/2435 1671/2552/1671\r\nf 2430/2550/2430 2434/2549/2434 2435/2553/2435 2436/2555/2436\r\nf 2255/2342/2255 2253/2339/2253 2435/2553/2435 2434/2549/2434\r\nf 1664/2556/1664 2437/2557/2437 2438/2558/2438 1665/2559/1665\r\nf 2432/2547/2432 2431/2546/2431 2438/2558/2438 2437/2557/2437\r\nf 2430/2545/2430 2436/2560/2436 2438/2558/2438 2431/2546/2431\r\nf 1668/2561/1668 1665/2559/1665 2438/2558/2438 2436/2560/2436\r\nf 1664/2556/1664 1675/2562/1675 2439/2563/2439 2437/2557/2437\r\nf 1679/2564/1679 2440/2565/2440 2439/2563/2439 1675/2562/1675\r\nf 2441/2566/2441 2442/2567/2442 2439/2563/2439 2440/2565/2440\r\nf 2432/2547/2432 2437/2557/2437 2439/2563/2439 2442/2567/2442\r\nf 1686/2568/1686 2443/2569/2443 2444/2570/2444 1687/2571/1687\r\nf 2445/2572/2445 2446/2573/2446 2444/2570/2444 2443/2569/2443\r\nf 2441/2566/2441 2440/2565/2440 2444/2570/2444 2446/2573/2446\r\nf 1679/2564/1679 1687/2571/1687 2444/2570/2444 2440/2565/2440\r\nf 1966/2025/1966 1969/2028/1969 2447/2574/2447 2448/2575/2448\r\nf 1961/2020/1961 2449/2576/2449 2447/2574/2447 1969/2028/1969\r\nf 2441/2566/2441 2446/2573/2446 2447/2574/2447 2449/2576/2449\r\nf 2445/2572/2445 2448/2575/2448 2447/2574/2447 2446/2573/2446\r\nf 1957/2016/1957 2428/2543/2428 2450/2577/2450 1958/2017/1958\r\nf 2432/2547/2432 2442/2567/2442 2450/2577/2450 2428/2543/2428\r\nf 2441/2566/2441 2449/2576/2449 2450/2577/2450 2442/2567/2442\r\nf 1961/2020/1961 1958/2017/1958 2450/2577/2450 2449/2576/2449\r\nf 2096/2165/2096 2451/2578/2451 2452/2579/2452 2097/2166/2097\r\nf 2453/2580/2453 2454/2581/2454 2452/2579/2452 2451/2578/2451\r\nf 2455/2582/2455 2456/2583/2456 2452/2579/2452 2454/2581/2454\r\nf 2100/2169/2100 2097/2166/2097 2452/2579/2452 2456/2583/2456\r\nf 1706/1761/1706 1709/1764/1709 2457/2584/2457 2458/2585/2458\r\nf 1704/1759/1704 2459/2586/2459 2457/2584/2457 1709/1764/1709\r\nf 2455/2582/2455 2454/2581/2454 2457/2584/2457 2459/2586/2459\r\nf 2453/2580/2453 2458/2585/2458 2457/2584/2457 2454/2581/2454\r\nf 1700/1755/1700 2460/2587/2460 2461/2588/2461 1701/1756/1701\r\nf 2462/2589/2462 2463/2590/2463 2461/2588/2461 2460/2587/2460\r\nf 2455/2582/2455 2459/2586/2459 2461/2588/2461 2463/2590/2463\r\nf 1704/1759/1704 1701/1756/1701 2461/2588/2461 2459/2586/2459\r\nf 2105/2174/2105 2108/2177/2108 2464/2591/2464 2465/2592/2465\r\nf 2100/2169/2100 2456/2583/2456 2464/2591/2464 2108/2177/2108\r\nf 2455/2582/2455 2463/2590/2463 2464/2591/2464 2456/2583/2456\r\nf 2462/2589/2462 2465/2592/2465 2464/2591/2464 2463/2590/2463\r\nf 1832/1891/1832 1978/2037/1978 2466/2593/2466 2337/2438/2337\r\nf 1982/2041/1982 2467/2594/2467 2466/2593/2466 1978/2037/1978\r\nf 2468/2595/2468 2469/2596/2469 2466/2593/2466 2467/2594/2467\r\nf 2332/2440/2332 2337/2438/2337 2466/2593/2466 2469/2596/2469\r\nf 1204/1204/1204 1721/1778/1721 2470/2597/2470 1990/2049/1990\r\nf 1718/1780/1718 2471/2598/2471 2470/2597/2470 1721/1778/1721\r\nf 2468/2595/2468 2467/2594/2467 2470/2597/2470 2471/2598/2471\r\nf 1982/2041/1982 1990/2049/1990 2470/2597/2470 2467/2594/2467\r\nf 1706/1761/1706 2458/2585/2458 2472/2599/2472 1716/1773/1716\r\nf 2453/2580/2453 2473/2600/2473 2472/2599/2472 2458/2585/2458\r\nf 2468/2601/2468 2471/2602/2471 2472/2599/2472 2473/2600/2473\r\nf 1718/1775/1718 1716/1773/1716 2472/2599/2472 2471/2602/2471\r\nf 2096/2165/2096 2328/2427/2328 2474/2603/2474 2451/2578/2451\r\nf 2332/2431/2332 2469/2604/2469 2474/2603/2474 2328/2427/2328\r\nf 2468/2601/2468 2473/2600/2473 2474/2603/2474 2469/2604/2469\r\nf 2453/2580/2453 2451/2578/2451 2474/2603/2474 2473/2600/2473\r\nf 958/958/958 961/961/961 2475/2605/2475 2476/2606/2476\r\nf 956/956/956 2477/2607/2477 2475/2605/2475 961/961/961\r\nf 2478/2608/2478 2479/2609/2479 2475/2605/2475 2477/2607/2477\r\nf 2480/2610/2480 2476/2606/2476 2475/2605/2475 2479/2609/2479\r\nf 952/952/952 2481/2611/2481 2482/2612/2482 953/953/953\r\nf 2483/2613/2483 2484/2614/2484 2482/2612/2482 2481/2611/2481\r\nf 2478/2608/2478 2477/2607/2477 2482/2612/2482 2484/2614/2484\r\nf 956/956/956 953/953/953 2482/2612/2482 2477/2607/2477\r\nf 2485/2615/2485 2486/2616/2486 2487/2617/2487 2488/2618/2488\r\nf 2489/2619/2489 2490/2620/2490 2487/2617/2487 2486/2616/2486\r\nf 2478/2608/2478 2484/2614/2484 2487/2617/2487 2490/2620/2490\r\nf 2483/2613/2483 2488/2618/2488 2487/2617/2487 2484/2614/2484\r\nf 2491/2621/2491 2492/2622/2492 2493/2623/2493 2494/2624/2494\r\nf 2480/2610/2480 2479/2609/2479 2493/2623/2493 2492/2622/2492\r\nf 2478/2608/2478 2490/2620/2490 2493/2623/2493 2479/2609/2479\r\nf 2489/2619/2489 2494/2624/2494 2493/2623/2493 2490/2620/2490\r\nf 952/952/952 987/987/987 2495/2625/2495 2481/2611/2481\r\nf 984/984/984 2496/2626/2496 2495/2625/2495 987/987/987\r\nf 2497/2627/2497 2498/2628/2498 2495/2625/2495 2496/2626/2496\r\nf 2483/2613/2483 2481/2611/2481 2495/2625/2495 2498/2628/2498\r\nf 980/980/980 2499/2629/2499 2500/2630/2500 981/981/981\r\nf 2501/2631/2501 2502/2632/2502 2500/2630/2500 2499/2629/2499\r\nf 2497/2627/2497 2496/2626/2496 2500/2630/2500 2502/2632/2502\r\nf 984/984/984 981/981/981 2500/2630/2500 2496/2626/2496\r\nf 2503/2633/2503 2504/2634/2504 2505/2635/2505 2506/2636/2506\r\nf 2507/2637/2507 2508/2638/2508 2505/2635/2505 2504/2634/2504\r\nf 2497/2627/2497 2502/2632/2502 2505/2635/2505 2508/2638/2508\r\nf 2501/2631/2501 2506/2636/2506 2505/2635/2505 2502/2632/2502\r\nf 2485/2615/2485 2488/2618/2488 2509/2639/2509 2510/2640/2510\r\nf 2483/2613/2483 2498/2628/2498 2509/2639/2509 2488/2618/2488\r\nf 2497/2627/2497 2508/2638/2508 2509/2639/2509 2498/2628/2498\r\nf 2507/2637/2507 2510/2640/2510 2509/2639/2509 2508/2638/2508\r\nf 980/980/980 1007/1007/1007 2511/2641/2511 2499/2629/2499\r\nf 1004/1004/1004 2512/2642/2512 2511/2641/2511 1007/1007/1007\r\nf 2513/2643/2513 2514/2644/2514 2511/2641/2511 2512/2642/2512\r\nf 2501/2631/2501 2499/2629/2499 2511/2641/2511 2514/2644/2514\r\nf 1000/1000/1000 2515/2645/2515 2516/2646/2516 1001/1001/1001\r\nf 2517/2647/2517 2518/2648/2518 2516/2646/2516 2515/2645/2515\r\nf 2513/2643/2513 2512/2642/2512 2516/2646/2516 2518/2648/2518\r\nf 1004/1004/1004 1001/1001/1001 2516/2646/2516 2512/2642/2512\r\nf 2519/2649/2519 2520/2650/2520 2521/2651/2521 2522/2652/2522\r\nf 2523/2653/2523 2524/2654/2524 2521/2651/2521 2520/2650/2520\r\nf 2513/2643/2513 2518/2648/2518 2521/2651/2521 2524/2654/2524\r\nf 2517/2647/2517 2522/2652/2522 2521/2651/2521 2518/2648/2518\r\nf 2503/2633/2503 2506/2636/2506 2525/2655/2525 2526/2656/2526\r\nf 2501/2631/2501 2514/2644/2514 2525/2655/2525 2506/2636/2506\r\nf 2513/2643/2513 2524/2654/2524 2525/2655/2525 2514/2644/2514\r\nf 2523/2653/2523 2526/2656/2526 2525/2655/2525 2524/2654/2524\r\nf 1000/1000/1000 1009/1009/1009 2527/2657/2527 2515/2645/2515\r\nf 1013/1013/1013 2528/2658/2528 2527/2657/2527 1009/1009/1009\r\nf 2529/2659/2529 2530/2660/2530 2527/2657/2527 2528/2658/2528\r\nf 2517/2647/2517 2515/2645/2515 2527/2657/2527 2530/2660/2530\r\nf 1024/1024/1024 2531/2661/2531 2532/2662/2532 1025/1025/1025\r\nf 2533/2663/2533 2534/2664/2534 2532/2662/2532 2531/2661/2531\r\nf 2529/2659/2529 2528/2658/2528 2532/2662/2532 2534/2664/2534\r\nf 1013/1013/1013 1025/1025/1025 2532/2662/2532 2528/2658/2528\r\nf 2535/2665/2535 2536/2666/2536 2537/2667/2537 2538/2668/2538\r\nf 2539/2669/2539 2540/2670/2540 2537/2667/2537 2536/2666/2536\r\nf 2529/2659/2529 2534/2664/2534 2537/2667/2537 2540/2670/2540\r\nf 2533/2663/2533 2538/2668/2538 2537/2667/2537 2534/2664/2534\r\nf 2519/2649/2519 2522/2652/2522 2541/2671/2541 2542/2672/2542\r\nf 2517/2647/2517 2530/2660/2530 2541/2671/2541 2522/2652/2522\r\nf 2529/2659/2529 2540/2670/2540 2541/2671/2541 2530/2660/2530\r\nf 2539/2669/2539 2542/2672/2542 2541/2671/2541 2540/2670/2540\r\nf 1028/1028/1028 1031/1031/1031 2543/2673/2543 2544/2674/2544\r\nf 1036/1036/1036 2545/2675/2545 2543/2673/2543 1031/1031/1031\r\nf 2546/2676/2546 2547/2677/2547 2543/2673/2543 2545/2675/2545\r\nf 2548/2678/2548 2544/2674/2544 2543/2673/2543 2547/2677/2547\r\nf 1049/1049/1049 2549/2679/2549 2550/2680/2550 1050/1050/1050\r\nf 2551/2681/2551 2552/2682/2552 2550/2680/2550 2549/2679/2549\r\nf 2546/2676/2546 2545/2675/2545 2550/2680/2550 2552/2682/2552\r\nf 1036/1036/1036 1050/1050/1050 2550/2680/2550 2545/2675/2545\r\nf 2553/2683/2553 2554/2684/2554 2555/2685/2555 2556/2686/2556\r\nf 2557/2687/2557 2558/2688/2558 2555/2685/2555 2554/2684/2554\r\nf 2546/2676/2546 2552/2682/2552 2555/2685/2555 2558/2688/2558\r\nf 2551/2681/2551 2556/2686/2556 2555/2685/2555 2552/2682/2552\r\nf 2559/2689/2559 2560/2690/2560 2561/2691/2561 2562/2692/2562\r\nf 2548/2678/2548 2547/2677/2547 2561/2691/2561 2560/2690/2560\r\nf 2546/2676/2546 2558/2688/2558 2561/2691/2561 2547/2677/2547\r\nf 2557/2687/2557 2562/2692/2562 2561/2691/2561 2558/2688/2558\r\nf 1068/1068/1068 1071/1071/1071 2563/2693/2563 2564/2694/2564\r\nf 1066/1066/1066 2565/2695/2565 2563/2693/2563 1071/1071/1071\r\nf 2566/2696/2566 2567/2697/2567 2563/2693/2563 2565/2695/2565\r\nf 2568/2698/2568 2564/2694/2564 2563/2693/2563 2567/2697/2567\r\nf 1062/1062/1062 2569/2699/2569 2570/2700/2570 1063/1063/1063\r\nf 2571/2701/2571 2572/2702/2572 2570/2700/2570 2569/2699/2569\r\nf 2566/2696/2566 2565/2695/2565 2570/2700/2570 2572/2702/2572\r\nf 1066/1066/1066 1063/1063/1063 2570/2700/2570 2565/2695/2565\r\nf 2573/2703/2573 2574/2704/2574 2575/2705/2575 2576/2706/2576\r\nf 2577/2707/2577 2578/2708/2578 2575/2705/2575 2574/2704/2574\r\nf 2566/2696/2566 2572/2702/2572 2575/2705/2575 2578/2708/2578\r\nf 2571/2701/2571 2576/2706/2576 2575/2705/2575 2572/2702/2572\r\nf 2579/2709/2579 2580/2710/2580 2581/2711/2581 2582/2712/2582\r\nf 2568/2698/2568 2567/2697/2567 2581/2711/2581 2580/2710/2580\r\nf 2566/2696/2566 2578/2708/2578 2581/2711/2581 2567/2697/2567\r\nf 2577/2707/2577 2582/2712/2582 2581/2711/2581 2578/2708/2578\r\nf 1049/1049/1049 1097/1097/1097 2583/2713/2583 2549/2679/2549\r\nf 1094/1094/1094 2584/2714/2584 2583/2713/2583 1097/1097/1097\r\nf 2585/2715/2585 2586/2716/2586 2583/2713/2583 2584/2714/2584\r\nf 2551/2681/2551 2549/2679/2549 2583/2713/2583 2586/2716/2586\r\nf 1090/1090/1090 2587/2717/2587 2588/2718/2588 1091/1091/1091\r\nf 2589/2719/2589 2590/2720/2590 2588/2718/2588 2587/2717/2587\r\nf 2585/2715/2585 2584/2714/2584 2588/2718/2588 2590/2720/2590\r\nf 1094/1094/1094 1091/1091/1091 2588/2718/2588 2584/2714/2584\r\nf 2591/2721/2591 2592/2722/2592 2593/2723/2593 2594/2724/2594\r\nf 2595/2725/2595 2596/2726/2596 2593/2723/2593 2592/2722/2592\r\nf 2585/2715/2585 2590/2720/2590 2593/2723/2593 2596/2726/2596\r\nf 2589/2719/2589 2594/2724/2594 2593/2723/2593 2590/2720/2590\r\nf 2553/2683/2553 2556/2686/2556 2597/2727/2597 2598/2728/2598\r\nf 2551/2681/2551 2586/2716/2586 2597/2727/2597 2556/2686/2556\r\nf 2585/2715/2585 2596/2726/2596 2597/2727/2597 2586/2716/2586\r\nf 2595/2725/2595 2598/2728/2598 2597/2727/2597 2596/2726/2596\r\nf 1098/1098/1098 1101/1101/1101 2599/2729/2599 2600/2730/2600\r\nf 1106/1106/1106 2601/2731/2601 2599/2729/2599 1101/1101/1101\r\nf 2602/2732/2602 2603/2733/2603 2599/2729/2599 2601/2731/2601\r\nf 2604/2734/2604 2600/2730/2600 2599/2729/2599 2603/2733/2603\r\nf 1119/1119/1119 2605/2735/2605 2606/2736/2606 1120/1120/1120\r\nf 2607/2737/2607 2608/2738/2608 2606/2736/2606 2605/2735/2605\r\nf 2602/2732/2602 2601/2731/2601 2606/2736/2606 2608/2738/2608\r\nf 1106/1106/1106 1120/1120/1120 2606/2736/2606 2601/2731/2601\r\nf 2609/2739/2609 2610/2740/2610 2611/2741/2611 2612/2742/2612\r\nf 2613/2743/2613 2614/2744/2614 2611/2741/2611 2610/2740/2610\r\nf 2602/2732/2602 2608/2738/2608 2611/2741/2611 2614/2744/2614\r\nf 2607/2737/2607 2612/2742/2612 2611/2741/2611 2608/2738/2608\r\nf 2615/2745/2615 2616/2746/2616 2617/2747/2617 2618/2748/2618\r\nf 2604/2734/2604 2603/2733/2603 2617/2747/2617 2616/2746/2616\r\nf 2602/2732/2602 2614/2744/2614 2617/2747/2617 2603/2733/2603\r\nf 2613/2743/2613 2618/2748/2618 2617/2747/2617 2614/2744/2614\r\nf 1119/1119/1119 1137/1137/1137 2619/2749/2619 2605/2735/2605\r\nf 1134/1134/1134 2620/2750/2620 2619/2749/2619 1137/1137/1137\r\nf 2621/2751/2621 2622/2752/2622 2619/2749/2619 2620/2750/2620\r\nf 2607/2737/2607 2605/2735/2605 2619/2749/2619 2622/2752/2622\r\nf 1028/1028/1028 2544/2674/2544 2623/2753/2623 1132/1132/1132\r\nf 2548/2678/2548 2624/2754/2624 2623/2753/2623 2544/2674/2544\r\nf 2621/2751/2621 2620/2750/2620 2623/2753/2623 2624/2754/2624\r\nf 1134/1134/1134 1132/1132/1132 2623/2753/2623 2620/2750/2620\r\nf 2559/2689/2559 2625/2755/2625 2626/2756/2626 2560/2690/2560\r\nf 2627/2757/2627 2628/2758/2628 2626/2756/2626 2625/2755/2625\r\nf 2621/2751/2621 2624/2754/2624 2626/2756/2626 2628/2758/2628\r\nf 2548/2678/2548 2560/2690/2560 2626/2756/2626 2624/2754/2624\r\nf 2609/2739/2609 2612/2742/2612 2629/2759/2629 2630/2760/2630\r\nf 2607/2737/2607 2622/2752/2622 2629/2759/2629 2612/2742/2612\r\nf 2621/2751/2621 2628/2758/2628 2629/2759/2629 2622/2752/2622\r\nf 2627/2757/2627 2630/2760/2630 2629/2759/2629 2628/2758/2628\r\nf 1024/1024/1024 1152/1152/1152 2631/2761/2631 2531/2661/2531\r\nf 1149/1149/1149 2632/2762/2632 2631/2761/2631 1152/1152/1152\r\nf 2633/2763/2633 2634/2764/2634 2631/2761/2631 2632/2762/2632\r\nf 2533/2663/2533 2531/2661/2531 2631/2761/2631 2634/2764/2634\r\nf 1098/1098/1098 2600/2730/2600 2635/2765/2635 1147/1147/1147\r\nf 2604/2734/2604 2636/2766/2636 2635/2765/2635 2600/2730/2600\r\nf 2633/2763/2633 2632/2762/2632 2635/2765/2635 2636/2766/2636\r\nf 1149/1149/1149 1147/1147/1147 2635/2765/2635 2632/2762/2632\r\nf 2615/2745/2615 2637/2767/2637 2638/2768/2638 2616/2746/2616\r\nf 2639/2769/2639 2640/2770/2640 2638/2768/2638 2637/2767/2637\r\nf 2633/2763/2633 2636/2766/2636 2638/2768/2638 2640/2770/2640\r\nf 2604/2734/2604 2616/2746/2616 2638/2768/2638 2636/2766/2636\r\nf 2535/2665/2535 2538/2668/2538 2641/2771/2641 2642/2772/2642\r\nf 2533/2663/2533 2634/2764/2634 2641/2771/2641 2538/2668/2538\r\nf 2633/2763/2633 2640/2770/2640 2641/2771/2641 2634/2764/2634\r\nf 2639/2769/2639 2642/2772/2642 2641/2771/2641 2640/2770/2640\r\nf 1174/1174/1174 1177/1177/1177 2643/2773/2643 2644/2774/2644\r\nf 1172/1172/1172 2645/2775/2645 2643/2773/2643 1177/1177/1177\r\nf 2646/2776/2646 2647/2777/2647 2643/2773/2643 2645/2775/2645\r\nf 2648/2778/2648 2644/2774/2644 2643/2773/2643 2647/2777/2647\r\nf 1168/1168/1168 2649/2779/2649 2650/2780/2650 1169/1169/1169\r\nf 2651/2781/2651 2652/2782/2652 2650/2780/2650 2649/2779/2649\r\nf 2646/2776/2646 2645/2775/2645 2650/2780/2650 2652/2782/2652\r\nf 1172/1172/1172 1169/1169/1169 2650/2780/2650 2645/2775/2645\r\nf 2653/2783/2653 2654/2784/2654 2655/2785/2655 2656/2786/2656\r\nf 2657/2787/2657 2658/2788/2658 2655/2785/2655 2654/2784/2654\r\nf 2646/2776/2646 2652/2782/2652 2655/2785/2655 2658/2788/2658\r\nf 2651/2781/2651 2656/2786/2656 2655/2785/2655 2652/2782/2652\r\nf 2659/2789/2659 2660/2790/2660 2661/2791/2661 2662/2792/2662\r\nf 2648/2778/2648 2647/2777/2647 2661/2791/2661 2660/2790/2660\r\nf 2646/2776/2646 2658/2788/2658 2661/2791/2661 2647/2777/2647\r\nf 2657/2787/2657 2662/2792/2662 2661/2791/2661 2658/2788/2658\r\nf 1194/1194/1194 1197/1197/1197 2663/2793/2663 2664/2794/2664\r\nf 1192/1192/1192 2665/2795/2665 2663/2793/2663 1197/1197/1197\r\nf 2666/2796/2666 2667/2797/2667 2663/2793/2663 2665/2795/2665\r\nf 2668/2798/2668 2664/2794/2664 2663/2793/2663 2667/2797/2667\r\nf 1174/1174/1174 2644/2774/2644 2669/2799/2669 1190/1190/1190\r\nf 2648/2778/2648 2670/2800/2670 2669/2799/2669 2644/2774/2644\r\nf 2666/2796/2666 2665/2795/2665 2669/2799/2669 2670/2800/2670\r\nf 1192/1192/1192 1190/1190/1190 2669/2799/2669 2665/2795/2665\r\nf 2659/2789/2659 2671/2801/2671 2672/2802/2672 2660/2790/2660\r\nf 2673/2803/2673 2674/2804/2674 2672/2802/2672 2671/2801/2671\r\nf 2666/2796/2666 2670/2800/2670 2672/2802/2672 2674/2804/2674\r\nf 2648/2778/2648 2660/2790/2660 2672/2802/2672 2670/2800/2670\r\nf 2675/2805/2675 2676/2806/2676 2677/2807/2677 2678/2808/2678\r\nf 2668/2798/2668 2667/2797/2667 2677/2807/2677 2676/2806/2676\r\nf 2666/2796/2666 2674/2804/2674 2677/2807/2677 2667/2797/2667\r\nf 2673/2803/2673 2678/2808/2678 2677/2807/2677 2674/2804/2674\r\nf 1062/1062/1062 1217/1217/1217 2679/2809/2679 2569/2699/2569\r\nf 1214/1214/1214 2680/2810/2680 2679/2809/2679 1217/1217/1217\r\nf 2681/2811/2681 2682/2812/2682 2679/2809/2679 2680/2810/2680\r\nf 2571/2701/2571 2569/2699/2569 2679/2809/2679 2682/2812/2682\r\nf 1210/1210/1210 2683/2813/2683 2684/2814/2684 1211/1211/1211\r\nf 2685/2815/2685 2686/2816/2686 2684/2814/2684 2683/2813/2683\r\nf 2681/2811/2681 2680/2810/2680 2684/2814/2684 2686/2816/2686\r\nf 1214/1214/1214 1211/1211/1211 2684/2814/2684 2680/2810/2680\r\nf 2687/2817/2687 2688/2818/2688 2689/2819/2689 2690/2820/2690\r\nf 2691/2821/2691 2692/2822/2692 2689/2819/2689 2688/2818/2688\r\nf 2681/2811/2681 2686/2816/2686 2689/2819/2689 2692/2822/2692\r\nf 2685/2815/2685 2690/2820/2690 2689/2819/2689 2686/2816/2686\r\nf 2573/2703/2573 2576/2706/2576 2693/2823/2693 2694/2824/2694\r\nf 2571/2701/2571 2682/2812/2682 2693/2823/2693 2576/2706/2576\r\nf 2681/2811/2681 2692/2822/2692 2693/2823/2693 2682/2812/2682\r\nf 2691/2821/2691 2694/2824/2694 2693/2823/2693 2692/2822/2692\r\nf 1218/1218/1218 1221/1221/1221 2695/2825/2695 2696/2826/2696\r\nf 1226/1226/1226 2697/2827/2697 2695/2825/2695 1221/1221/1221\r\nf 2698/2828/2698 2699/2829/2699 2695/2825/2695 2697/2827/2697\r\nf 2700/2830/2700 2696/2826/2696 2695/2825/2695 2699/2829/2699\r\nf 1239/1239/1239 2701/2831/2701 2702/2832/2702 1240/1240/1240\r\nf 2703/2833/2703 2704/2834/2704 2702/2832/2702 2701/2831/2701\r\nf 2698/2828/2698 2697/2827/2697 2702/2832/2702 2704/2834/2704\r\nf 1226/1226/1226 1240/1240/1240 2702/2832/2702 2697/2827/2697\r\nf 2705/2835/2705 2706/2836/2706 2707/2837/2707 2708/2838/2708\r\nf 2709/2839/2709 2710/2840/2710 2707/2837/2707 2706/2836/2706\r\nf 2698/2828/2698 2704/2834/2704 2707/2837/2707 2710/2840/2710\r\nf 2703/2833/2703 2708/2838/2708 2707/2837/2707 2704/2834/2704\r\nf 2711/2841/2711 2712/2842/2712 2713/2843/2713 2714/2844/2714\r\nf 2700/2830/2700 2699/2829/2699 2713/2843/2713 2712/2842/2712\r\nf 2698/2828/2698 2710/2840/2710 2713/2843/2713 2699/2829/2699\r\nf 2709/2839/2709 2714/2844/2714 2713/2843/2713 2710/2840/2710\r\nf 1243/1243/1243 1246/1246/1246 2715/2845/2715 2716/2846/2716\r\nf 1251/1251/1251 2717/2847/2717 2715/2845/2715 1246/1246/1246\r\nf 2718/2848/2718 2719/2849/2719 2715/2845/2715 2717/2847/2717\r\nf 2720/2850/2720 2716/2846/2716 2715/2845/2715 2719/2849/2719\r\nf 1218/1218/1218 2696/2826/2696 2721/2851/2721 1261/1261/1261\r\nf 2700/2830/2700 2722/2852/2722 2721/2851/2721 2696/2826/2696\r\nf 2718/2848/2718 2717/2847/2717 2721/2851/2721 2722/2852/2722\r\nf 1251/1251/1251 1261/1261/1261 2721/2851/2721 2717/2847/2717\r\nf 2711/2841/2711 2723/2853/2723 2724/2854/2724 2712/2842/2712\r\nf 2725/2855/2725 2726/2856/2726 2724/2854/2724 2723/2853/2723\r\nf 2718/2848/2718 2722/2852/2722 2724/2854/2724 2726/2856/2726\r\nf 2700/2830/2700 2712/2842/2712 2724/2854/2724 2722/2852/2722\r\nf 2727/2857/2727 2728/2858/2728 2729/2859/2729 2730/2860/2730\r\nf 2720/2850/2720 2719/2849/2719 2729/2859/2729 2728/2858/2728\r\nf 2718/2848/2718 2726/2856/2726 2729/2859/2729 2719/2849/2719\r\nf 2725/2855/2725 2730/2860/2730 2729/2859/2729 2726/2856/2726\r\nf 1263/1263/1263 1266/1266/1266 2731/2861/2731 2732/2862/2732\r\nf 1271/1271/1271 2733/2863/2733 2731/2861/2731 1266/1266/1266\r\nf 2734/2864/2734 2735/2865/2735 2731/2861/2731 2733/2863/2733\r\nf 2736/2866/2736 2732/2862/2732 2731/2861/2731 2735/2865/2735\r\nf 1243/1243/1243 2716/2846/2716 2737/2867/2737 1281/1281/1281\r\nf 2720/2850/2720 2738/2868/2738 2737/2867/2737 2716/2846/2716\r\nf 2734/2864/2734 2733/2863/2733 2737/2867/2737 2738/2868/2738\r\nf 1271/1271/1271 1281/1281/1281 2737/2867/2737 2733/2863/2733\r\nf 2727/2857/2727 2739/2869/2739 2740/2870/2740 2728/2858/2728\r\nf 2741/2871/2741 2742/2872/2742 2740/2870/2740 2739/2869/2739\r\nf 2734/2864/2734 2738/2868/2738 2740/2870/2740 2742/2872/2742\r\nf 2720/2850/2720 2728/2858/2728 2740/2870/2740 2738/2868/2738\r\nf 2743/2873/2743 2744/2874/2744 2745/2875/2745 2746/2876/2746\r\nf 2736/2866/2736 2735/2865/2735 2745/2875/2745 2744/2874/2744\r\nf 2734/2864/2734 2742/2872/2742 2745/2875/2745 2735/2865/2735\r\nf 2741/2871/2741 2746/2876/2746 2745/2875/2745 2742/2872/2742\r\nf 1343/1353/1343 1346/1356/1346 2747/2877/2747 2748/2878/2748\r\nf 1341/1351/1341 2749/2879/2749 2747/2877/2747 1346/1356/1346\r\nf 2750/2880/2750 2751/2881/2751 2747/2877/2747 2749/2879/2749\r\nf 2752/2882/2752 2748/2878/2748 2747/2877/2747 2751/2881/2751\r\nf 1337/1347/1337 2753/2883/2753 2754/2884/2754 1338/1348/1338\r\nf 2755/2885/2755 2756/2886/2756 2754/2884/2754 2753/2883/2753\r\nf 2750/2880/2750 2749/2879/2749 2754/2884/2754 2756/2886/2756\r\nf 1341/1351/1341 1338/1348/1338 2754/2884/2754 2749/2879/2749\r\nf 2757/2887/2757 2758/2888/2758 2759/2889/2759 2760/2890/2760\r\nf 2761/2891/2761 2762/2892/2762 2759/2889/2759 2758/2888/2758\r\nf 2750/2880/2750 2756/2886/2756 2759/2889/2759 2762/2892/2762\r\nf 2755/2885/2755 2760/2890/2760 2759/2889/2759 2756/2886/2756\r\nf 2763/2893/2763 2764/2894/2764 2765/2895/2765 2766/2896/2766\r\nf 2752/2882/2752 2751/2881/2751 2765/2895/2765 2764/2894/2764\r\nf 2750/2880/2750 2762/2892/2762 2765/2895/2765 2751/2881/2751\r\nf 2761/2891/2761 2766/2896/2766 2765/2895/2765 2762/2892/2762\r\nf 1421/1444/1421 1424/1447/1424 2767/2897/2767 2768/2898/2768\r\nf 1419/1442/1419 2769/2899/2769 2767/2897/2767 1424/1447/1424\r\nf 2770/2900/2770 2771/2901/2771 2767/2897/2767 2769/2899/2769\r\nf 2772/2902/2772 2768/2898/2768 2767/2897/2767 2771/2901/2771\r\nf 1415/1438/1415 2773/2903/2773 2774/2904/2774 1416/1439/1416\r\nf 2775/2905/2775 2776/2906/2776 2774/2904/2774 2773/2903/2773\r\nf 2770/2900/2770 2769/2899/2769 2774/2904/2774 2776/2906/2776\r\nf 1419/1442/1419 1416/1439/1416 2774/2904/2774 2769/2899/2769\r\nf 2777/2907/2777 2778/2908/2778 2779/2909/2779 2780/2910/2780\r\nf 2781/2911/2781 2782/2912/2782 2779/2909/2779 2778/2908/2778\r\nf 2770/2900/2770 2776/2906/2776 2779/2909/2779 2782/2912/2782\r\nf 2775/2905/2775 2780/2910/2780 2779/2909/2779 2776/2906/2776\r\nf 2783/2913/2783 2784/2914/2784 2785/2915/2785 2786/2916/2786\r\nf 2772/2902/2772 2771/2901/2771 2785/2915/2785 2784/2914/2784\r\nf 2770/2900/2770 2782/2912/2782 2785/2915/2785 2771/2901/2771\r\nf 2781/2911/2781 2786/2916/2786 2785/2915/2785 2782/2912/2782\r\nf 1239/1239/1239 1426/1449/1426 2787/2917/2787 2701/2831/2701\r\nf 1430/1453/1430 2788/2918/2788 2787/2917/2787 1426/1449/1426\r\nf 2789/2919/2789 2790/2920/2790 2787/2917/2787 2788/2918/2788\r\nf 2703/2833/2703 2701/2831/2701 2787/2917/2787 2790/2920/2790\r\nf 1441/1464/1441 2791/2921/2791 2792/2922/2792 1442/1465/1442\r\nf 2793/2923/2793 2794/2924/2794 2792/2922/2792 2791/2921/2791\r\nf 2789/2919/2789 2788/2918/2788 2792/2922/2792 2794/2924/2794\r\nf 1430/1453/1430 1442/1465/1442 2792/2922/2792 2788/2918/2788\r\nf 2795/2925/2795 2796/2926/2796 2797/2927/2797 2798/2928/2798\r\nf 2799/2929/2799 2800/2930/2800 2797/2927/2797 2796/2926/2796\r\nf 2789/2919/2789 2794/2924/2794 2797/2927/2797 2800/2930/2800\r\nf 2793/2923/2793 2798/2928/2798 2797/2927/2797 2794/2924/2794\r\nf 2705/2835/2705 2708/2838/2708 2801/2931/2801 2802/2932/2802\r\nf 2703/2833/2703 2790/2920/2790 2801/2931/2801 2708/2838/2708\r\nf 2789/2919/2789 2800/2930/2800 2801/2931/2801 2790/2920/2790\r\nf 2799/2929/2799 2802/2932/2802 2801/2931/2801 2800/2930/2800\r\nf 1445/1468/1445 1448/1471/1448 2803/2933/2803 2804/2934/2804\r\nf 1453/1476/1453 2805/2935/2805 2803/2933/2803 1448/1471/1448\r\nf 2806/2936/2806 2807/2937/2807 2803/2933/2803 2805/2935/2805\r\nf 2808/2938/2808 2804/2934/2804 2803/2933/2803 2807/2937/2807\r\nf 1466/1489/1466 2809/2939/2809 2810/2940/2810 1467/1490/1467\r\nf 2811/2941/2811 2812/2942/2812 2810/2940/2810 2809/2939/2809\r\nf 2806/2936/2806 2805/2935/2805 2810/2940/2810 2812/2942/2812\r\nf 1453/1476/1453 1467/1490/1467 2810/2940/2810 2805/2935/2805\r\nf 2813/2943/2813 2814/2944/2814 2815/2945/2815 2816/2946/2816\r\nf 2817/2947/2817 2818/2948/2818 2815/2945/2815 2814/2944/2814\r\nf 2806/2936/2806 2812/2942/2812 2815/2945/2815 2818/2948/2818\r\nf 2811/2941/2811 2816/2946/2816 2815/2945/2815 2812/2942/2812\r\nf 2819/2949/2819 2820/2950/2820 2821/2951/2821 2822/2952/2822\r\nf 2808/2938/2808 2807/2937/2807 2821/2951/2821 2820/2950/2820\r\nf 2806/2936/2806 2818/2948/2818 2821/2951/2821 2807/2937/2807\r\nf 2817/2947/2817 2822/2952/2822 2821/2951/2821 2818/2948/2818\r\nf 1466/1489/1466 1471/1494/1471 2823/2953/2823 2809/2939/2809\r\nf 1475/1498/1475 2824/2954/2824 2823/2953/2823 1471/1494/1471\r\nf 2825/2955/2825 2826/2956/2826 2823/2953/2823 2824/2954/2824\r\nf 2811/2941/2811 2809/2939/2809 2823/2953/2823 2826/2956/2826\r\nf 1486/1509/1486 2827/2957/2827 2828/2958/2828 1487/1510/1487\r\nf 2829/2959/2829 2830/2960/2830 2828/2958/2828 2827/2957/2827\r\nf 2825/2955/2825 2824/2954/2824 2828/2958/2828 2830/2960/2830\r\nf 1475/1498/1475 1487/1510/1487 2828/2958/2828 2824/2954/2824\r\nf 2831/2961/2831 2832/2962/2832 2833/2963/2833 2834/2964/2834\r\nf 2835/2965/2835 2836/2966/2836 2833/2963/2833 2832/2962/2832\r\nf 2825/2955/2825 2830/2960/2830 2833/2963/2833 2836/2966/2836\r\nf 2829/2959/2829 2834/2964/2834 2833/2963/2833 2830/2960/2830\r\nf 2813/2943/2813 2816/2946/2816 2837/2967/2837 2838/2968/2838\r\nf 2811/2941/2811 2826/2956/2826 2837/2967/2837 2816/2946/2816\r\nf 2825/2955/2825 2836/2966/2836 2837/2967/2837 2826/2956/2826\r\nf 2835/2965/2835 2838/2968/2838 2837/2967/2837 2836/2966/2836\r\nf 1415/1438/1415 1533/1568/1533 2839/2969/2839 2773/2903/2773\r\nf 1528/1563/1528 2840/2970/2840 2839/2969/2839 1533/1568/1533\r\nf 2841/2971/2841 2842/2972/2842 2839/2969/2839 2840/2970/2840\r\nf 2775/2905/2775 2773/2903/2773 2839/2969/2839 2842/2972/2842\r\nf 1263/1263/1263 2732/2862/2732 2843/2973/2843 1526/1561/1526\r\nf 2736/2866/2736 2844/2974/2844 2843/2973/2843 2732/2862/2732\r\nf 2841/2971/2841 2840/2970/2840 2843/2973/2843 2844/2974/2844\r\nf 1528/1563/1528 1526/1561/1526 2843/2973/2843 2840/2970/2840\r\nf 2743/2873/2743 2845/2975/2845 2846/2976/2846 2744/2874/2744\r\nf 2847/2977/2847 2848/2978/2848 2846/2976/2846 2845/2975/2845\r\nf 2841/2971/2841 2844/2974/2844 2846/2976/2846 2848/2978/2848\r\nf 2736/2866/2736 2744/2874/2744 2846/2976/2846 2844/2974/2844\r\nf 2777/2907/2777 2780/2910/2780 2849/2979/2849 2850/2980/2850\r\nf 2775/2905/2775 2842/2972/2842 2849/2979/2849 2780/2910/2780\r\nf 2841/2971/2841 2848/2978/2848 2849/2979/2849 2842/2972/2842\r\nf 2847/2977/2847 2850/2980/2850 2849/2979/2849 2848/2978/2848\r\nf 1547/1582/1547 1550/1585/1550 2851/2981/2851 2852/2982/2852\r\nf 1542/1577/1542 2853/2983/2853 2851/2981/2851 1550/1585/1550\r\nf 2854/2984/2854 2855/2985/2855 2851/2981/2851 2853/2983/2853\r\nf 2856/2986/2856 2852/2982/2852 2851/2981/2851 2855/2985/2855\r\nf 1538/1573/1538 2857/2987/2857 2858/2988/2858 1539/1574/1539\r\nf 2859/2989/2859 2860/2990/2860 2858/2988/2858 2857/2987/2857\r\nf 2854/2984/2854 2853/2983/2853 2858/2988/2858 2860/2990/2860\r\nf 1542/1577/1542 1539/1574/1539 2858/2988/2858 2853/2983/2853\r\nf 2861/2991/2861 2862/2992/2862 2863/2993/2863 2864/2994/2864\r\nf 2865/2995/2865 2866/2996/2866 2863/2993/2863 2862/2992/2862\r\nf 2854/2984/2854 2860/2990/2860 2863/2993/2863 2866/2996/2866\r\nf 2859/2989/2859 2864/2994/2864 2863/2993/2863 2860/2990/2860\r\nf 2867/2997/2867 2868/2998/2868 2869/2999/2869 2870/3000/2870\r\nf 2856/2986/2856 2855/2985/2855 2869/2999/2869 2868/2998/2868\r\nf 2854/2984/2854 2866/2996/2866 2869/2999/2869 2855/2985/2855\r\nf 2865/2995/2865 2870/3000/2870 2869/2999/2869 2866/2996/2866\r\nf 1337/1347/1337 2871/3001/2871 2872/3002/2872 2753/2883/2753\r\nf 2873/3003/2873 2874/3004/2874 2872/3002/2872 2871/3001/2871\r\nf 2875/3005/2875 2876/3006/2876 2872/3002/2872 2874/3004/2874\r\nf 2755/2885/2755 2753/2883/2753 2872/3002/2872 2876/3006/2876\r\nf 2877/3007/2877 2878/3008/2878 2879/3009/2879 2880/3010/2880\r\nf 2881/3011/2881 2882/3012/2882 2879/3009/2879 2878/3008/2878\r\nf 2875/3005/2875 2874/3004/2874 2879/3009/2879 2882/3012/2882\r\nf 2873/3003/2873 2880/3010/2880 2879/3009/2879 2874/3004/2874\r\nf 2883/3013/2883 2884/3014/2884 2885/3015/2885 2886/3016/2886\r\nf 2887/3017/2887 2888/3018/2888 2885/3015/2885 2884/3014/2884\r\nf 2875/3005/2875 2882/3012/2882 2885/3015/2885 2888/3018/2888\r\nf 2881/3011/2881 2886/3016/2886 2885/3015/2885 2882/3012/2882\r\nf 2757/2887/2757 2760/2890/2760 2889/3019/2889 2890/3020/2890\r\nf 2755/2885/2755 2876/3006/2876 2889/3019/2889 2760/2890/2760\r\nf 2875/3005/2875 2888/3018/2888 2889/3019/2889 2876/3006/2876\r\nf 2887/3017/2887 2890/3020/2890 2889/3019/2889 2888/3018/2888\r\nf 1596/1635/1596 1599/1638/1599 2891/3021/2891 2892/3022/2892\r\nf 1592/1631/1592 2893/3023/2893 2891/3021/2891 1599/1638/1599\r\nf 2894/3024/2894 2895/3025/2895 2891/3021/2891 2893/3023/2893\r\nf 2896/3026/2896 2892/3022/2892 2891/3021/2891 2895/3025/2895\r\nf 1421/1444/1421 2768/2898/2768 2897/3027/2897 1590/1629/1590\r\nf 2772/2902/2772 2898/3028/2898 2897/3027/2897 2768/2898/2768\r\nf 2894/3024/2894 2893/3023/2893 2897/3027/2897 2898/3028/2898\r\nf 1592/1631/1592 1590/1629/1590 2897/3027/2897 2893/3023/2893\r\nf 2783/2913/2783 2899/3029/2899 2900/3030/2900 2784/2914/2784\r\nf 2901/3031/2901 2902/3032/2902 2900/3030/2900 2899/3029/2899\r\nf 2894/3024/2894 2898/3028/2898 2900/3030/2900 2902/3032/2902\r\nf 2772/2902/2772 2784/2914/2784 2900/3030/2900 2898/3028/2898\r\nf 2903/3033/2903 2904/3034/2904 2905/3035/2905 2906/3036/2906\r\nf 2896/3026/2896 2895/3025/2895 2905/3035/2905 2904/3034/2904\r\nf 2894/3024/2894 2902/3032/2902 2905/3035/2905 2895/3025/2895\r\nf 2901/3031/2901 2906/3036/2906 2905/3035/2905 2902/3032/2902\r\nf 1538/1573/1538 1613/1652/1613 2907/3037/2907 2857/2987/2857\r\nf 1608/1647/1608 2908/3038/2908 2907/3037/2907 1613/1652/1613\r\nf 2909/3039/2909 2910/3040/2910 2907/3037/2907 2908/3038/2908\r\nf 2859/2989/2859 2857/2987/2857 2907/3037/2907 2910/3040/2910\r\nf 1596/1635/1596 2892/3022/2892 2911/3041/2911 1606/1645/1606\r\nf 2896/3026/2896 2912/3042/2912 2911/3041/2911 2892/3022/2892\r\nf 2909/3039/2909 2908/3038/2908 2911/3041/2911 2912/3042/2912\r\nf 1608/1647/1608 1606/1645/1606 2911/3041/2911 2908/3038/2908\r\nf 2903/3033/2903 2913/3043/2913 2914/3044/2914 2904/3034/2904\r\nf 2915/3045/2915 2916/3046/2916 2914/3044/2914 2913/3043/2913\r\nf 2909/3039/2909 2912/3042/2912 2914/3044/2914 2916/3046/2916\r\nf 2896/3026/2896 2904/3034/2904 2914/3044/2914 2912/3042/2912\r\nf 2861/2991/2861 2864/2994/2864 2917/3047/2917 2918/3048/2918\r\nf 2859/2989/2859 2910/3040/2910 2917/3047/2917 2864/2994/2864\r\nf 2909/3039/2909 2916/3046/2916 2917/3047/2917 2910/3040/2910\r\nf 2915/3045/2915 2918/3048/2918 2917/3047/2917 2916/3046/2916\r\nf 1686/1741/1686 1689/1744/1689 2919/3049/2919 2920/3050/2920\r\nf 1684/1739/1684 2921/3051/2921 2919/3049/2919 1689/1744/1689\r\nf 2922/3052/2922 2923/3053/2923 2919/3049/2919 2921/3051/2921\r\nf 2924/3054/2924 2920/3050/2920 2919/3049/2919 2923/3053/2923\r\nf 1194/1194/1194 2664/2794/2664 2925/3055/2925 1682/1737/1682\r\nf 2668/2798/2668 2926/3056/2926 2925/3055/2925 2664/2794/2664\r\nf 2922/3052/2922 2921/3051/2921 2925/3055/2925 2926/3056/2926\r\nf 1684/1739/1684 1682/1737/1682 2925/3055/2925 2921/3051/2921\r\nf 2675/2805/2675 2927/3057/2927 2928/3058/2928 2676/2806/2676\r\nf 2929/3059/2929 2930/3060/2930 2928/3058/2928 2927/3057/2927\r\nf 2922/3052/2922 2926/3056/2926 2928/3058/2928 2930/3060/2930\r\nf 2668/2798/2668 2676/2806/2676 2928/3058/2928 2926/3056/2926\r\nf 2931/3061/2931 2932/3062/2932 2933/3063/2933 2934/3064/2934\r\nf 2924/3054/2924 2923/3053/2923 2933/3063/2933 2932/3062/2932\r\nf 2922/3052/2922 2930/3060/2930 2933/3063/2933 2923/3053/2923\r\nf 2929/3059/2929 2934/3064/2934 2933/3063/2933 2930/3060/2930\r\nf 1700/1755/1700 1703/1758/1703 2935/3065/2935 2936/3066/2936\r\nf 1698/1753/1698 2937/3067/2937 2935/3065/2935 1703/1758/1703\r\nf 2938/3068/2938 2939/3069/2939 2935/3065/2935 2937/3067/2937\r\nf 2940/3070/2940 2936/3066/2936 2935/3065/2935 2939/3069/2939\r\nf 1343/1353/1343 2748/2878/2748 2941/3071/2941 1696/1751/1696\r\nf 2752/2882/2752 2942/3072/2942 2941/3071/2941 2748/2878/2748\r\nf 2938/3068/2938 2937/3067/2937 2941/3071/2941 2942/3072/2942\r\nf 1698/1753/1698 1696/1751/1696 2941/3071/2941 2937/3067/2937\r\nf 2763/2893/2763 2943/3073/2943 2944/3074/2944 2764/2894/2764\r\nf 2945/3075/2945 2946/3076/2946 2944/3074/2944 2943/3073/2943\r\nf 2938/3068/2938 2942/3072/2942 2944/3074/2944 2946/3076/2946\r\nf 2752/2882/2752 2764/2894/2764 2944/3074/2944 2942/3072/2942\r\nf 2947/3077/2947 2948/3078/2948 2949/3079/2949 2950/3080/2950\r\nf 2940/3070/2940 2939/3069/2939 2949/3079/2949 2948/3078/2948\r\nf 2938/3068/2938 2946/3076/2946 2949/3079/2949 2939/3069/2939\r\nf 2945/3075/2945 2950/3080/2950 2949/3079/2949 2946/3076/2946\r\nf 1743/1802/1743 1746/1805/1746 2951/3081/2951 2952/3082/2952\r\nf 1741/1800/1741 2953/3083/2953 2951/3081/2951 1746/1805/1746\r\nf 2954/3084/2954 2955/3085/2955 2951/3081/2951 2953/3083/2953\r\nf 2956/3086/2956 2952/3082/2952 2951/3081/2951 2955/3085/2955\r\nf 1737/1796/1737 2957/3087/2957 2958/3088/2958 1738/1797/1738\r\nf 2959/3089/2959 2960/3090/2960 2958/3088/2958 2957/3087/2957\r\nf 2954/3084/2954 2953/3083/2953 2958/3088/2958 2960/3090/2960\r\nf 1741/1800/1741 1738/1797/1738 2958/3088/2958 2953/3083/2953\r\nf 2961/3091/2961 2962/3092/2962 2963/3093/2963 2964/3094/2964\r\nf 2965/3095/2965 2966/3096/2966 2963/3093/2963 2962/3092/2962\r\nf 2954/3084/2954 2960/3090/2960 2963/3093/2963 2966/3096/2966\r\nf 2959/3089/2959 2964/3094/2964 2963/3093/2963 2960/3090/2960\r\nf 2967/3097/2967 2968/3098/2968 2969/3099/2969 2970/3100/2970\r\nf 2956/3086/2956 2955/3085/2955 2969/3099/2969 2968/3098/2968\r\nf 2954/3084/2954 2966/3096/2966 2969/3099/2969 2955/3085/2955\r\nf 2965/3095/2965 2970/3100/2970 2969/3099/2969 2966/3096/2966\r\nf 1757/1816/1757 1760/1819/1760 2971/3101/2971 2972/3102/2972\r\nf 1755/1814/1755 2973/3103/2973 2971/3101/2971 1760/1819/1760\r\nf 2974/3104/2974 2975/3105/2975 2971/3101/2971 2973/3103/2973\r\nf 2976/3106/2976 2972/3102/2972 2971/3101/2971 2975/3105/2975\r\nf 1743/1802/1743 2952/3082/2952 2977/3107/2977 1753/1812/1753\r\nf 2956/3086/2956 2978/3108/2978 2977/3107/2977 2952/3082/2952\r\nf 2974/3104/2974 2973/3103/2973 2977/3107/2977 2978/3108/2978\r\nf 1755/1814/1755 1753/1812/1753 2977/3107/2977 2973/3103/2973\r\nf 2967/3097/2967 2979/3109/2979 2980/3110/2980 2968/3098/2968\r\nf 2981/3111/2981 2982/3112/2982 2980/3110/2980 2979/3109/2979\r\nf 2974/3104/2974 2978/3108/2978 2980/3110/2980 2982/3112/2982\r\nf 2956/3086/2956 2968/3098/2968 2980/3110/2980 2978/3108/2978\r\nf 2983/3113/2983 2984/3114/2984 2985/3115/2985 2986/3116/2986\r\nf 2976/3106/2976 2975/3105/2975 2985/3115/2985 2984/3114/2984\r\nf 2974/3104/2974 2982/3112/2982 2985/3115/2985 2975/3105/2975\r\nf 2981/3111/2981 2986/3116/2986 2985/3115/2985 2982/3112/2982\r\nf 1777/1836/1777 1780/1839/1780 2987/3117/2987 2988/3118/2988\r\nf 1775/1834/1775 2989/3119/2989 2987/3117/2987 1780/1839/1780\r\nf 2990/3120/2990 2991/3121/2991 2987/3117/2987 2989/3119/2989\r\nf 2992/3122/2992 2988/3118/2988 2987/3117/2987 2991/3121/2991\r\nf 1757/1816/1757 2972/3102/2972 2993/3123/2993 1773/1832/1773\r\nf 2976/3106/2976 2994/3124/2994 2993/3123/2993 2972/3102/2972\r\nf 2990/3120/2990 2989/3119/2989 2993/3123/2993 2994/3124/2994\r\nf 1775/1834/1775 1773/1832/1773 2993/3123/2993 2989/3119/2989\r\nf 2983/3113/2983 2995/3125/2995 2996/3126/2996 2984/3114/2984\r\nf 2997/3127/2997 2998/3128/2998 2996/3126/2996 2995/3125/2995\r\nf 2990/3120/2990 2994/3124/2994 2996/3126/2996 2998/3128/2998\r\nf 2976/3106/2976 2984/3114/2984 2996/3126/2996 2994/3124/2994\r\nf 2999/3129/2999 3000/3130/3000 3001/3131/3001 3002/3132/3002\r\nf 2992/3122/2992 2991/3121/2991 3001/3131/3001 3000/3130/3000\r\nf 2990/3120/2990 2998/3128/2998 3001/3131/3001 2991/3121/2991\r\nf 2997/3127/2997 3002/3132/3002 3001/3131/3001 2998/3128/2998\r\nf 1793/1852/1793 1796/1855/1796 3003/3133/3003 3004/3134/3004\r\nf 1789/1848/1789 3005/3135/3005 3003/3133/3003 1796/1855/1796\r\nf 3006/3136/3006 3007/3137/3007 3003/3133/3003 3005/3135/3005\r\nf 3008/3138/3008 3004/3134/3004 3003/3133/3003 3007/3137/3007\r\nf 1777/1836/1777 2988/3118/2988 3009/3139/3009 1787/1846/1787\r\nf 2992/3122/2992 3010/3140/3010 3009/3139/3009 2988/3118/2988\r\nf 3006/3136/3006 3005/3135/3005 3009/3139/3009 3010/3140/3010\r\nf 1789/1848/1789 1787/1846/1787 3009/3139/3009 3005/3135/3005\r\nf 2999/3129/2999 3011/3141/3011 3012/3142/3012 3000/3130/3000\r\nf 3013/3143/3013 3014/3144/3014 3012/3142/3012 3011/3141/3011\r\nf 3006/3136/3006 3010/3140/3010 3012/3142/3012 3014/3144/3014\r\nf 2992/3122/2992 3000/3130/3000 3012/3142/3012 3010/3140/3010\r\nf 3015/3145/3015 3016/3146/3016 3017/3147/3017 3018/3148/3018\r\nf 3008/3138/3008 3007/3137/3007 3017/3147/3017 3016/3146/3016\r\nf 3006/3136/3006 3014/3144/3014 3017/3147/3017 3007/3137/3007\r\nf 3013/3143/3013 3018/3148/3018 3017/3147/3017 3014/3144/3014\r\nf 1816/1875/1816 1819/1878/1819 3019/3149/3019 3020/3150/3020\r\nf 1811/1870/1811 3021/3151/3021 3019/3149/3019 1819/1878/1819\r\nf 3022/3152/3022 3023/3153/3023 3019/3149/3019 3021/3151/3021\r\nf 3024/3154/3024 3020/3150/3020 3019/3149/3019 3023/3153/3023\r\nf 1807/1866/1807 3025/3155/3025 3026/3156/3026 1808/1867/1808\r\nf 3027/3157/3027 3028/3158/3028 3026/3156/3026 3025/3155/3025\r\nf 3022/3152/3022 3021/3151/3021 3026/3156/3026 3028/3158/3028\r\nf 1811/1870/1811 1808/1867/1808 3026/3156/3026 3021/3151/3021\r\nf 3029/3159/3029 3030/3160/3030 3031/3161/3031 3032/3162/3032\r\nf 3033/3163/3033 3034/3164/3034 3031/3161/3031 3030/3160/3030\r\nf 3022/3152/3022 3028/3158/3028 3031/3161/3031 3034/3164/3034\r\nf 3027/3157/3027 3032/3162/3032 3031/3161/3031 3028/3158/3028\r\nf 3035/3165/3035 3036/3166/3036 3037/3167/3037 3038/3168/3038\r\nf 3024/3154/3024 3023/3153/3023 3037/3167/3037 3036/3166/3036\r\nf 3022/3152/3022 3034/3164/3034 3037/3167/3037 3023/3153/3023\r\nf 3033/3163/3033 3038/3168/3038 3037/3167/3037 3034/3164/3034\r\nf 1853/1912/1853 1856/1915/1856 3039/3169/3039 3040/3170/3040\r\nf 1851/1910/1851 3041/3171/3041 3039/3169/3039 1856/1915/1856\r\nf 3042/3172/3042 3043/3173/3043 3039/3169/3039 3041/3171/3041\r\nf 3044/3174/3044 3040/3170/3040 3039/3169/3039 3043/3173/3043\r\nf 1847/1906/1847 3045/3175/3045 3046/3176/3046 1848/1907/1848\r\nf 3047/3177/3047 3048/3178/3048 3046/3176/3046 3045/3175/3045\r\nf 3042/3172/3042 3041/3171/3041 3046/3176/3046 3048/3178/3048\r\nf 1851/1910/1851 1848/1907/1848 3046/3176/3046 3041/3171/3041\r\nf 3049/3179/3049 3050/3180/3050 3051/3181/3051 3052/3182/3052\r\nf 3053/3183/3053 3054/3184/3054 3051/3181/3051 3050/3180/3050\r\nf 3042/3172/3042 3048/3178/3048 3051/3181/3051 3054/3184/3054\r\nf 3047/3177/3047 3052/3182/3052 3051/3181/3051 3048/3178/3048\r\nf 3055/3185/3055 3056/3186/3056 3057/3187/3057 3058/3188/3058\r\nf 3044/3174/3044 3043/3173/3043 3057/3187/3057 3056/3186/3056\r\nf 3042/3172/3042 3054/3184/3054 3057/3187/3057 3043/3173/3043\r\nf 3053/3183/3053 3058/3188/3058 3057/3187/3057 3054/3184/3054\r\nf 1847/1906/1847 3059/3189/3059 3060/3190/3060 3045/3175/3045\r\nf 3061/3191/3061 3062/3192/3062 3060/3190/3060 3059/3189/3059\r\nf 3063/3193/3063 3064/3194/3064 3060/3190/3060 3062/3192/3062\r\nf 3047/3177/3047 3045/3175/3045 3060/3190/3060 3064/3194/3064\r\nf 3065/3195/3065 3066/3196/3066 3067/3197/3067 3068/3198/3068\r\nf 3069/3199/3069 3070/3200/3070 3067/3197/3067 3066/3196/3066\r\nf 3063/3193/3063 3062/3192/3062 3067/3197/3067 3070/3200/3070\r\nf 3061/3191/3061 3068/3198/3068 3067/3197/3067 3062/3192/3062\r\nf 3071/3201/3071 3072/3202/3072 3073/3203/3073 3074/3204/3074\r\nf 3075/3205/3075 3076/3206/3076 3073/3203/3073 3072/3202/3072\r\nf 3063/3193/3063 3070/3200/3070 3073/3203/3073 3076/3206/3076\r\nf 3069/3199/3069 3074/3204/3074 3073/3203/3073 3070/3200/3070\r\nf 3049/3179/3049 3052/3182/3052 3077/3207/3077 3078/3208/3078\r\nf 3047/3177/3047 3064/3194/3064 3077/3207/3077 3052/3182/3052\r\nf 3063/3193/3063 3076/3206/3076 3077/3207/3077 3064/3194/3064\r\nf 3075/3205/3075 3078/3208/3078 3077/3207/3077 3076/3206/3076\r\nf 1886/1945/1886 1889/1948/1889 3079/3209/3079 3080/3210/3080\r\nf 1881/1940/1881 3081/3211/3081 3079/3209/3079 1889/1948/1889\r\nf 3082/3212/3082 3083/3213/3083 3079/3209/3079 3081/3211/3081\r\nf 3084/3214/3084 3080/3210/3080 3079/3209/3079 3083/3213/3083\r\nf 1877/1936/1877 3085/3215/3085 3086/3216/3086 1878/1937/1878\r\nf 3087/3217/3087 3088/3218/3088 3086/3216/3086 3085/3215/3085\r\nf 3082/3212/3082 3081/3211/3081 3086/3216/3086 3088/3218/3088\r\nf 1881/1940/1881 1878/1937/1878 3086/3216/3086 3081/3211/3081\r\nf 3089/3219/3089 3090/3220/3090 3091/3221/3091 3092/3222/3092\r\nf 3093/3223/3093 3094/3224/3094 3091/3221/3091 3090/3220/3090\r\nf 3082/3212/3082 3088/3218/3088 3091/3221/3091 3094/3224/3094\r\nf 3087/3217/3087 3092/3222/3092 3091/3221/3091 3088/3218/3088\r\nf 3095/3225/3095 3096/3226/3096 3097/3227/3097 3098/3228/3098\r\nf 3084/3214/3084 3083/3213/3083 3097/3227/3097 3096/3226/3096\r\nf 3082/3212/3082 3094/3224/3094 3097/3227/3097 3083/3213/3083\r\nf 3093/3223/3093 3098/3228/3098 3097/3227/3097 3094/3224/3094\r\nf 1807/1866/1807 1913/1972/1913 3099/3229/3099 3025/3155/3025\r\nf 1910/1969/1910 3100/3230/3100 3099/3229/3099 1913/1972/1913\r\nf 3101/3231/3101 3102/3232/3102 3099/3229/3099 3100/3230/3100\r\nf 3027/3157/3027 3025/3155/3025 3099/3229/3099 3102/3232/3102\r\nf 1886/1945/1886 3080/3210/3080 3103/3233/3103 1908/1967/1908\r\nf 3084/3214/3084 3104/3234/3104 3103/3233/3103 3080/3210/3080\r\nf 3101/3231/3101 3100/3230/3100 3103/3233/3103 3104/3234/3104\r\nf 1910/1969/1910 1908/1967/1908 3103/3233/3103 3100/3230/3100\r\nf 3095/3225/3095 3105/3235/3105 3106/3236/3106 3096/3226/3096\r\nf 3107/3237/3107 3108/3238/3108 3106/3236/3106 3105/3235/3105\r\nf 3101/3231/3101 3104/3234/3104 3106/3236/3106 3108/3238/3108\r\nf 3084/3214/3084 3096/3226/3096 3106/3236/3106 3104/3234/3104\r\nf 3029/3159/3029 3032/3162/3032 3109/3239/3109 3110/3240/3110\r\nf 3027/3157/3027 3102/3232/3102 3109/3239/3109 3032/3162/3032\r\nf 3101/3231/3101 3108/3238/3108 3109/3239/3109 3102/3232/3102\r\nf 3107/3237/3107 3110/3240/3110 3109/3239/3109 3108/3238/3108\r\nf 1877/1936/1877 1928/1987/1928 3111/3241/3111 3085/3215/3085\r\nf 1925/1984/1925 3112/3242/3112 3111/3241/3111 1928/1987/1928\r\nf 3113/3243/3113 3114/3244/3114 3111/3241/3111 3112/3242/3112\r\nf 3087/3217/3087 3085/3215/3085 3111/3241/3111 3114/3244/3114\r\nf 1793/1852/1793 3004/3134/3004 3115/3245/3115 1923/1982/1923\r\nf 3008/3138/3008 3116/3246/3116 3115/3245/3115 3004/3134/3004\r\nf 3113/3243/3113 3112/3242/3112 3115/3245/3115 3116/3246/3116\r\nf 1925/1984/1925 1923/1982/1923 3115/3245/3115 3112/3242/3112\r\nf 3015/3145/3015 3117/3247/3117 3118/3248/3118 3016/3146/3016\r\nf 3119/3249/3119 3120/3250/3120 3118/3248/3118 3117/3247/3117\r\nf 3113/3243/3113 3116/3246/3116 3118/3248/3118 3120/3250/3120\r\nf 3008/3138/3008 3016/3146/3016 3118/3248/3118 3116/3246/3116\r\nf 3089/3219/3089 3092/3222/3092 3121/3251/3121 3122/3252/3122\r\nf 3087/3217/3087 3114/3244/3114 3121/3251/3121 3092/3222/3092\r\nf 3113/3243/3113 3120/3250/3120 3121/3251/3121 3114/3244/3114\r\nf 3119/3249/3119 3122/3252/3122 3121/3251/3121 3120/3250/3120\r\nf 1737/1796/1737 3123/3253/3123 3124/3254/3124 2957/3087/2957\r\nf 3125/3255/3125 3126/3256/3126 3124/3254/3124 3123/3253/3123\r\nf 3127/3257/3127 3128/3258/3128 3124/3254/3124 3126/3256/3126\r\nf 2959/3089/2959 2957/3087/2957 3124/3254/3124 3128/3258/3128\r\nf 3129/3259/3129 3130/3260/3130 3131/3261/3131 3132/3262/3132\r\nf 3133/3263/3133 3134/3264/3134 3131/3261/3131 3130/3260/3130\r\nf 3127/3257/3127 3126/3256/3126 3131/3261/3131 3134/3264/3134\r\nf 3125/3255/3125 3132/3262/3132 3131/3261/3131 3126/3256/3126\r\nf 3135/3265/3135 3136/3266/3136 3137/3267/3137 3138/3268/3138\r\nf 3139/3269/3139 3140/3270/3140 3137/3267/3137 3136/3266/3136\r\nf 3127/3257/3127 3134/3264/3134 3137/3267/3137 3140/3270/3140\r\nf 3133/3263/3133 3138/3268/3138 3137/3267/3137 3134/3264/3134\r\nf 2961/3091/2961 2964/3094/2964 3141/3271/3141 3142/3272/3142\r\nf 2959/3089/2959 3128/3258/3128 3141/3271/3141 2964/3094/2964\r\nf 3127/3257/3127 3140/3270/3140 3141/3271/3141 3128/3258/3128\r\nf 3139/3269/3139 3142/3272/3142 3141/3271/3141 3140/3270/3140\r\nf 1941/2000/1941 1973/2032/1973 3143/3273/3143 3144/3274/3144\r\nf 1970/2029/1970 3145/3275/3145 3143/3273/3143 1973/2032/1973\r\nf 3146/3276/3146 3147/3277/3147 3143/3273/3143 3145/3275/3145\r\nf 3148/3278/3148 3144/3274/3144 3143/3273/3143 3147/3277/3147\r\nf 1966/2025/1966 3149/3279/3149 3150/3280/3150 1967/2026/1967\r\nf 3151/3281/3151 3152/3282/3152 3150/3280/3150 3149/3279/3149\r\nf 3146/3276/3146 3145/3275/3145 3150/3280/3150 3152/3282/3152\r\nf 1970/2029/1970 1967/2026/1967 3150/3280/3150 3145/3275/3145\r\nf 3153/3283/3153 3154/3284/3154 3155/3285/3155 3156/3286/3156\r\nf 3157/3287/3157 3158/3288/3158 3155/3285/3155 3154/3284/3154\r\nf 3146/3276/3146 3152/3282/3152 3155/3285/3155 3158/3288/3158\r\nf 3151/3281/3151 3156/3286/3156 3155/3285/3155 3152/3282/3152\r\nf 3159/3289/3159 3160/3290/3160 3161/3291/3161 3162/3292/3162\r\nf 3148/3278/3148 3147/3277/3147 3161/3291/3161 3160/3290/3160\r\nf 3146/3276/3146 3158/3288/3158 3161/3291/3161 3147/3277/3147\r\nf 3157/3287/3157 3162/3292/3162 3161/3291/3161 3158/3288/3158\r\nf 1210/1210/1210 1988/2047/1988 3163/3293/3163 2683/2813/2683\r\nf 1985/2044/1985 3164/3294/3164 3163/3293/3163 1988/2047/1988\r\nf 3165/3295/3165 3166/3296/3166 3163/3293/3163 3164/3294/3164\r\nf 2685/2815/2685 2683/2813/2683 3163/3293/3163 3166/3296/3166\r\nf 1853/1912/1853 3040/3170/3040 3167/3297/3167 1983/2042/1983\r\nf 3044/3174/3044 3168/3298/3168 3167/3297/3167 3040/3170/3040\r\nf 3165/3295/3165 3164/3294/3164 3167/3297/3167 3168/3298/3168\r\nf 1985/2044/1985 1983/2042/1983 3167/3297/3167 3164/3294/3164\r\nf 3055/3185/3055 3169/3299/3169 3170/3300/3170 3056/3186/3056\r\nf 3171/3301/3171 3172/3302/3172 3170/3300/3170 3169/3299/3169\r\nf 3165/3295/3165 3168/3298/3168 3170/3300/3170 3172/3302/3172\r\nf 3044/3174/3044 3056/3186/3056 3170/3300/3170 3168/3298/3168\r\nf 2687/2817/2687 2690/2820/2690 3173/3303/3173 3174/3304/3174\r\nf 2685/2815/2685 3166/3296/3166 3173/3303/3173 2690/2820/2690\r\nf 3165/3295/3165 3172/3302/3172 3173/3303/3173 3166/3296/3166\r\nf 3171/3301/3171 3174/3304/3174 3173/3303/3173 3172/3302/3172\r\nf 2001/2060/2001 2004/2063/2004 3175/3305/3175 3176/3306/3176\r\nf 1996/2055/1996 3177/3307/3177 3175/3305/3175 2004/2063/2004\r\nf 3178/3308/3178 3179/3309/3179 3175/3305/3175 3177/3307/3177\r\nf 3180/3310/3180 3176/3306/3176 3175/3305/3175 3179/3309/3179\r\nf 1992/2051/1992 3181/3311/3181 3182/3312/3182 1993/2052/1993\r\nf 3183/3313/3183 3184/3314/3184 3182/3312/3182 3181/3311/3181\r\nf 3178/3308/3178 3177/3307/3177 3182/3312/3182 3184/3314/3184\r\nf 1996/2055/1996 1993/2052/1993 3182/3312/3182 3177/3307/3177\r\nf 3185/3315/3185 3186/3316/3186 3187/3317/3187 3188/3318/3188\r\nf 3189/3319/3189 3190/3320/3190 3187/3317/3187 3186/3316/3186\r\nf 3178/3308/3178 3184/3314/3184 3187/3317/3187 3190/3320/3190\r\nf 3183/3313/3183 3188/3318/3188 3187/3317/3187 3184/3314/3184\r\nf 3191/3321/3191 3192/3322/3192 3193/3323/3193 3194/3324/3194\r\nf 3180/3310/3180 3179/3309/3179 3193/3323/3193 3192/3322/3192\r\nf 3178/3308/3178 3190/3320/3190 3193/3323/3193 3179/3309/3179\r\nf 3189/3319/3189 3194/3324/3194 3193/3323/3193 3190/3320/3190\r\nf 1992/2051/1992 2027/2086/2027 3195/3325/3195 3181/3311/3181\r\nf 2021/2080/2021 3196/3326/3196 3195/3325/3195 2027/2086/2027\r\nf 3197/3327/3197 3198/3328/3198 3195/3325/3195 3196/3326/3196\r\nf 3183/3313/3183 3181/3311/3181 3195/3325/3195 3198/3328/3198\r\nf 2017/2076/2017 3199/3329/3199 3200/3330/3200 2018/2077/2018\r\nf 3201/3331/3201 3202/3332/3202 3200/3330/3200 3199/3329/3199\r\nf 3197/3327/3197 3196/3326/3196 3200/3330/3200 3202/3332/3202\r\nf 2021/2080/2021 2018/2077/2018 3200/3330/3200 3196/3326/3196\r\nf 3203/3333/3203 3204/3334/3204 3205/3335/3205 3206/3336/3206\r\nf 3207/3337/3207 3208/3338/3208 3205/3335/3205 3204/3334/3204\r\nf 3197/3327/3197 3202/3332/3202 3205/3335/3205 3208/3338/3208\r\nf 3201/3331/3201 3206/3336/3206 3205/3335/3205 3202/3332/3202\r\nf 3185/3315/3185 3188/3318/3188 3209/3339/3209 3210/3340/3210\r\nf 3183/3313/3183 3198/3328/3198 3209/3339/3209 3188/3318/3188\r\nf 3197/3327/3197 3208/3338/3208 3209/3339/3209 3198/3328/3198\r\nf 3207/3337/3207 3210/3340/3210 3209/3339/3209 3208/3338/3208\r\nf 2017/2076/2017 2047/2106/2047 3211/3341/3211 3199/3329/3199\r\nf 2041/2100/2041 3212/3342/3212 3211/3341/3211 2047/2106/2047\r\nf 3213/3343/3213 3214/3344/3214 3211/3341/3211 3212/3342/3212\r\nf 3201/3331/3201 3199/3329/3199 3211/3341/3211 3214/3344/3214\r\nf 2037/2096/2037 3215/3345/3215 3216/3346/3216 2038/2097/2038\r\nf 3217/3347/3217 3218/3348/3218 3216/3346/3216 3215/3345/3215\r\nf 3213/3343/3213 3212/3342/3212 3216/3346/3216 3218/3348/3218\r\nf 2041/2100/2041 2038/2097/2038 3216/3346/3216 3212/3342/3212\r\nf 3219/3349/3219 3220/3350/3220 3221/3351/3221 3222/3352/3222\r\nf 3223/3353/3223 3224/3354/3224 3221/3351/3221 3220/3350/3220\r\nf 3213/3343/3213 3218/3348/3218 3221/3351/3221 3224/3354/3224\r\nf 3217/3347/3217 3222/3352/3222 3221/3351/3221 3218/3348/3218\r\nf 3203/3333/3203 3206/3336/3206 3225/3355/3225 3226/3356/3226\r\nf 3201/3331/3201 3214/3344/3214 3225/3355/3225 3206/3336/3206\r\nf 3213/3343/3213 3224/3354/3224 3225/3355/3225 3214/3344/3214\r\nf 3223/3353/3223 3226/3356/3226 3225/3355/3225 3224/3354/3224\r\nf 2111/2180/2111 2114/2183/2114 3227/3357/3227 3228/3358/3228\r\nf 2109/2178/2109 3229/3359/3229 3227/3357/3227 2114/2183/2114\r\nf 3230/3360/3230 3231/3361/3231 3227/3357/3227 3229/3359/3229\r\nf 3232/3362/3232 3228/3358/3228 3227/3357/3227 3231/3361/3231\r\nf 2105/2174/2105 3233/3363/3233 3234/3364/3234 2106/2175/2106\r\nf 3235/3365/3235 3236/3366/3236 3234/3364/3234 3233/3363/3233\r\nf 3230/3360/3230 3229/3359/3229 3234/3364/3234 3236/3366/3236\r\nf 2109/2178/2109 2106/2175/2106 3234/3364/3234 3229/3359/3229\r\nf 3237/3367/3237 3238/3368/3238 3239/3369/3239 3240/3370/3240\r\nf 3241/3371/3241 3242/3372/3242 3239/3369/3239 3238/3368/3238\r\nf 3230/3360/3230 3236/3366/3236 3239/3369/3239 3242/3372/3242\r\nf 3235/3365/3235 3240/3370/3240 3239/3369/3239 3236/3366/3236\r\nf 3243/3373/3243 3244/3374/3244 3245/3375/3245 3246/3376/3246\r\nf 3232/3362/3232 3231/3361/3231 3245/3375/3245 3244/3374/3244\r\nf 3230/3360/3230 3242/3372/3242 3245/3375/3245 3231/3361/3231\r\nf 3241/3371/3241 3246/3376/3246 3245/3375/3245 3242/3372/3242\r\nf 2190/2272/2190 2193/2275/2193 3247/3377/3247 3248/3378/3248\r\nf 2188/2270/2188 3249/3379/3249 3247/3377/3247 2193/2275/2193\r\nf 3250/3380/3250 3251/3381/3251 3247/3377/3247 3249/3379/3249\r\nf 3252/3382/3252 3248/3378/3248 3247/3377/3247 3251/3381/3251\r\nf 2184/2266/2184 3253/3383/3253 3254/3384/3254 2185/2267/2185\r\nf 3255/3385/3255 3256/3386/3256 3254/3384/3254 3253/3383/3253\r\nf 3250/3380/3250 3249/3379/3249 3254/3384/3254 3256/3386/3256\r\nf 2188/2270/2188 2185/2267/2185 3254/3384/3254 3249/3379/3249\r\nf 3257/3387/3257 3258/3388/3258 3259/3389/3259 3260/3390/3260\r\nf 3261/3391/3261 3262/3392/3262 3259/3389/3259 3258/3388/3258\r\nf 3250/3380/3250 3256/3386/3256 3259/3389/3259 3262/3392/3262\r\nf 3255/3385/3255 3260/3390/3260 3259/3389/3259 3256/3386/3256\r\nf 3263/3393/3263 3264/3394/3264 3265/3395/3265 3266/3396/3266\r\nf 3252/3382/3252 3251/3381/3251 3265/3395/3265 3264/3394/3264\r\nf 3250/3380/3250 3262/3392/3262 3265/3395/3265 3251/3381/3251\r\nf 3261/3391/3261 3266/3396/3266 3265/3395/3265 3262/3392/3262\r\nf 2219/2301/2219 3267/3397/3267 3268/3398/3268 3269/3399/3269\r\nf 3270/3400/3270 3271/3401/3271 3268/3398/3268 3267/3397/3267\r\nf 3272/3402/3272 3273/3403/3273 3268/3398/3268 3271/3401/3271\r\nf 3274/3404/3274 3269/3399/3269 3268/3398/3268 3273/3403/3273\r\nf 3275/3405/3275 3276/3406/3276 3277/3407/3277 3278/3408/3278\r\nf 3279/3409/3279 3280/3410/3280 3277/3407/3277 3276/3406/3276\r\nf 3272/3402/3272 3271/3401/3271 3277/3407/3277 3280/3410/3280\r\nf 3270/3400/3270 3278/3408/3278 3277/3407/3277 3271/3401/3271\r\nf 3281/3411/3281 3282/3412/3282 3283/3413/3283 3284/3414/3284\r\nf 3285/3415/3285 3286/3416/3286 3283/3413/3283 3282/3412/3282\r\nf 3272/3402/3272 3280/3410/3280 3283/3413/3283 3286/3416/3286\r\nf 3279/3409/3279 3284/3414/3284 3283/3413/3283 3280/3410/3280\r\nf 3287/3417/3287 3288/3418/3288 3289/3419/3289 3290/3420/3290\r\nf 3274/3404/3274 3273/3403/3273 3289/3419/3289 3288/3418/3288\r\nf 3272/3402/3272 3286/3416/3286 3289/3419/3289 3273/3403/3273\r\nf 3285/3415/3285 3290/3420/3290 3289/3419/3289 3286/3416/3286\r\nf 2228/2310/2228 2231/2313/2231 3291/3421/3291 3292/3422/3292\r\nf 2223/2305/2223 3293/3423/3293 3291/3421/3291 2231/2313/2231\r\nf 3294/3424/3294 3295/3425/3295 3291/3421/3291 3293/3423/3293\r\nf 3296/3426/3296 3292/3422/3292 3291/3421/3291 3295/3425/3295\r\nf 2219/2301/2219 3269/3399/3269 3297/3427/3297 2220/2302/2220\r\nf 3274/3404/3274 3298/3428/3298 3297/3427/3297 3269/3399/3269\r\nf 3294/3424/3294 3293/3423/3293 3297/3427/3297 3298/3428/3298\r\nf 2223/2305/2223 2220/2302/2220 3297/3427/3297 3293/3423/3293\r\nf 3287/3417/3287 3299/3429/3299 3300/3430/3300 3288/3418/3288\r\nf 3301/3431/3301 3302/3432/3302 3300/3430/3300 3299/3429/3299\r\nf 3294/3424/3294 3298/3428/3298 3300/3430/3300 3302/3432/3302\r\nf 3274/3404/3274 3288/3418/3288 3300/3430/3300 3298/3428/3298\r\nf 3303/3433/3303 3304/3434/3304 3305/3435/3305 3306/3436/3306\r\nf 3296/3426/3296 3295/3425/3295 3305/3435/3305 3304/3434/3304\r\nf 3294/3424/3294 3302/3432/3302 3305/3435/3305 3295/3425/3295\r\nf 3301/3431/3301 3306/3436/3306 3305/3435/3305 3302/3432/3302\r\nf 1486/2332/1486 2251/2335/2251 3307/3437/3307 2827/3438/2827\r\nf 2246/2328/2246 3308/3439/3308 3307/3437/3307 2251/2335/2251\r\nf 3309/3440/3309 3310/3441/3310 3307/3437/3307 3308/3439/3308\r\nf 2829/3442/2829 2827/3438/2827 3307/3437/3307 3310/3441/3310\r\nf 2228/2310/2228 3292/3422/3292 3311/3443/3311 2244/2326/2244\r\nf 3296/3426/3296 3312/3444/3312 3311/3443/3311 3292/3422/3292\r\nf 3309/3440/3309 3308/3439/3308 3311/3443/3311 3312/3444/3312\r\nf 2246/2328/2246 2244/2326/2244 3311/3443/3311 3308/3439/3308\r\nf 3303/3433/3303 3313/3445/3313 3314/3446/3314 3304/3434/3304\r\nf 3315/3447/3315 3316/3448/3316 3314/3446/3314 3313/3445/3313\r\nf 3309/3440/3309 3312/3444/3312 3314/3446/3314 3316/3448/3316\r\nf 3296/3426/3296 3304/3434/3304 3314/3446/3314 3312/3444/3312\r\nf 2831/3449/2831 2834/3450/2834 3317/3451/3317 3318/3452/3318\r\nf 2829/3442/2829 3310/3441/3310 3317/3451/3317 2834/3450/2834\r\nf 3309/3440/3309 3316/3448/3316 3317/3451/3317 3310/3441/3310\r\nf 3315/3447/3315 3318/3452/3318 3317/3451/3317 3316/3448/3316\r\nf 2037/2096/2037 2296/2395/2296 3319/3453/3319 3215/3345/3215\r\nf 2300/2399/2300 3320/3454/3320 3319/3453/3319 2296/2395/2296\r\nf 3321/3455/3321 3322/3456/3322 3319/3453/3319 3320/3454/3320\r\nf 3217/3347/3217 3215/3345/3215 3319/3453/3319 3322/3456/3322\r\nf 2190/2272/2190 3248/3378/3248 3323/3457/3323 2305/2404/2305\r\nf 3252/3382/3252 3324/3458/3324 3323/3457/3323 3248/3378/3248\r\nf 3321/3455/3321 3320/3454/3320 3323/3457/3323 3324/3458/3324\r\nf 2300/2399/2300 2305/2404/2305 3323/3457/3323 3320/3454/3320\r\nf 3263/3393/3263 3325/3459/3325 3326/3460/3326 3264/3394/3264\r\nf 3327/3461/3327 3328/3462/3328 3326/3460/3326 3325/3459/3325\r\nf 3321/3455/3321 3324/3458/3324 3326/3460/3326 3328/3462/3328\r\nf 3252/3382/3252 3264/3394/3264 3326/3460/3326 3324/3458/3324\r\nf 3219/3349/3219 3222/3352/3222 3329/3463/3329 3330/3464/3330\r\nf 3217/3347/3217 3322/3456/3322 3329/3463/3329 3222/3352/3222\r\nf 3321/3455/3321 3328/3462/3328 3329/3463/3329 3322/3456/3322\r\nf 3327/3461/3327 3330/3464/3330 3329/3463/3329 3328/3462/3328\r\nf 2307/2406/2307 2310/2409/2310 3331/3465/3331 3332/3466/3332\r\nf 2315/2414/2315 3333/3467/3333 3331/3465/3331 2310/2409/2310\r\nf 3334/3468/3334 3335/3469/3335 3331/3465/3331 3333/3467/3333\r\nf 3336/3470/3336 3332/3466/3332 3331/3465/3331 3335/3469/3335\r\nf 2323/2422/2323 3337/3471/3337 3338/3472/3338 2324/2423/2324\r\nf 3339/3473/3339 3340/3474/3340 3338/3472/3338 3337/3471/3337\r\nf 3334/3468/3334 3333/3467/3333 3338/3472/3338 3340/3474/3340\r\nf 2315/2414/2315 2324/2423/2324 3338/3472/3338 3333/3467/3333\r\nf 3341/3475/3341 3342/3476/3342 3343/3477/3343 3344/3478/3344\r\nf 3345/3479/3345 3346/3480/3346 3343/3477/3343 3342/3476/3342\r\nf 3334/3468/3334 3340/3474/3340 3343/3477/3343 3346/3480/3346\r\nf 3339/3473/3339 3344/3478/3344 3343/3477/3343 3340/3474/3340\r\nf 3347/3481/3347 3348/3482/3348 3349/3483/3349 3350/3484/3350\r\nf 3336/3470/3336 3335/3469/3335 3349/3483/3349 3348/3482/3348\r\nf 3334/3468/3334 3346/3480/3346 3349/3483/3349 3335/3469/3335\r\nf 3345/3479/3345 3350/3484/3350 3349/3483/3349 3346/3480/3346\r\nf 2323/2422/2323 2340/2443/2340 3351/3485/3351 3337/3471/3337\r\nf 2344/2447/2344 3352/3486/3352 3351/3485/3351 2340/2443/2340\r\nf 3353/3487/3353 3354/3488/3354 3351/3485/3351 3352/3486/3352\r\nf 3339/3473/3339 3337/3471/3337 3351/3485/3351 3354/3488/3354\r\nf 2355/2458/2355 3355/3489/3355 3356/3490/3356 2356/2459/2356\r\nf 3357/3491/3357 3358/3492/3358 3356/3490/3356 3355/3489/3355\r\nf 3353/3487/3353 3352/3486/3352 3356/3490/3356 3358/3492/3358\r\nf 2344/2447/2344 2356/2459/2356 3356/3490/3356 3352/3486/3352\r\nf 3359/3493/3359 3360/3494/3360 3361/3495/3361 3362/3496/3362\r\nf 3363/3497/3363 3364/3498/3364 3361/3495/3361 3360/3494/3360\r\nf 3353/3487/3353 3358/3492/3358 3361/3495/3361 3364/3498/3364\r\nf 3357/3491/3357 3362/3496/3362 3361/3495/3361 3358/3492/3358\r\nf 3341/3475/3341 3344/3478/3344 3365/3499/3365 3366/3500/3366\r\nf 3339/3473/3339 3354/3488/3354 3365/3499/3365 3344/3478/3344\r\nf 3353/3487/3353 3364/3498/3364 3365/3499/3365 3354/3488/3354\r\nf 3363/3497/3363 3366/3500/3366 3365/3499/3365 3364/3498/3364\r\nf 2184/2266/2184 2360/2463/2360 3367/3501/3367 3253/3383/3253\r\nf 2364/2467/2364 3368/3502/3368 3367/3501/3367 2360/2463/2360\r\nf 3369/3503/3369 3370/3504/3370 3367/3501/3367 3368/3502/3368\r\nf 3255/3385/3255 3253/3383/3253 3367/3501/3367 3370/3504/3370\r\nf 2371/2474/2371 3371/3505/3371 3372/3506/3372 2372/2475/2372\r\nf 3373/3507/3373 3374/3508/3374 3372/3506/3372 3371/3505/3371\r\nf 3369/3503/3369 3368/3502/3368 3372/3506/3372 3374/3508/3374\r\nf 2364/2467/2364 2372/2475/2372 3372/3506/3372 3368/3502/3368\r\nf 3375/3509/3375 3376/3510/3376 3377/3511/3377 3378/3512/3378\r\nf 3379/3513/3379 3380/3514/3380 3377/3511/3377 3376/3510/3376\r\nf 3369/3503/3369 3374/3508/3374 3377/3511/3377 3380/3514/3380\r\nf 3373/3507/3373 3378/3512/3378 3377/3511/3377 3374/3508/3374\r\nf 3257/3387/3257 3260/3390/3260 3381/3515/3381 3382/3516/3382\r\nf 3255/3385/3255 3370/3504/3370 3381/3515/3381 3260/3390/3260\r\nf 3369/3503/3369 3380/3514/3380 3381/3515/3381 3370/3504/3370\r\nf 3379/3513/3379 3382/3516/3382 3381/3515/3381 3380/3514/3380\r\nf 2371/2474/2371 2376/2479/2376 3383/3517/3383 3371/3505/3371\r\nf 2380/2483/2380 3384/3518/3384 3383/3517/3383 2376/2479/2376\r\nf 3385/3519/3385 3386/3520/3386 3383/3517/3383 3384/3518/3384\r\nf 3373/3507/3373 3371/3505/3371 3383/3517/3383 3386/3520/3386\r\nf 2307/2406/2307 3332/3466/3332 3387/3521/3387 2385/2488/2385\r\nf 3336/3470/3336 3388/3522/3388 3387/3521/3387 3332/3466/3332\r\nf 3385/3519/3385 3384/3518/3384 3387/3521/3387 3388/3522/3388\r\nf 2380/2483/2380 2385/2488/2385 3387/3521/3387 3384/3518/3384\r\nf 3347/3481/3347 3389/3523/3389 3390/3524/3390 3348/3482/3348\r\nf 3391/3525/3391 3392/3526/3392 3390/3524/3390 3389/3523/3389\r\nf 3385/3519/3385 3388/3522/3388 3390/3524/3390 3392/3526/3392\r\nf 3336/3470/3336 3348/3482/3348 3390/3524/3390 3388/3522/3388\r\nf 3375/3509/3375 3378/3512/3378 3393/3527/3393 3394/3528/3394\r\nf 3373/3507/3373 3386/3520/3386 3393/3527/3393 3378/3512/3378\r\nf 3385/3519/3385 3392/3526/3392 3393/3527/3393 3386/3520/3386\r\nf 3391/3525/3391 3394/3528/3394 3393/3527/3393 3392/3526/3392\r\nf 1966/2025/1966 2448/2575/2448 3395/3529/3395 3149/3279/3149\r\nf 2445/2572/2445 3396/3530/3396 3395/3529/3395 2448/2575/2448\r\nf 3397/3531/3397 3398/3532/3398 3395/3529/3395 3396/3530/3396\r\nf 3151/3281/3151 3149/3279/3149 3395/3529/3395 3398/3532/3398\r\nf 1686/2568/1686 2920/3533/2920 3399/3534/3399 2443/2569/2443\r\nf 2924/3535/2924 3400/3536/3400 3399/3534/3399 2920/3533/2920\r\nf 3397/3531/3397 3396/3530/3396 3399/3534/3399 3400/3536/3400\r\nf 2445/2572/2445 2443/2569/2443 3399/3534/3399 3396/3530/3396\r\nf 2931/3537/2931 3401/3538/3401 3402/3539/3402 2932/3540/2932\r\nf 3403/3541/3403 3404/3542/3404 3402/3539/3402 3401/3538/3401\r\nf 3397/3531/3397 3400/3536/3400 3402/3539/3402 3404/3542/3404\r\nf 2924/3535/2924 2932/3540/2932 3402/3539/3402 3400/3536/3400\r\nf 3153/3283/3153 3156/3286/3156 3405/3543/3405 3406/3544/3406\r\nf 3151/3281/3151 3398/3532/3398 3405/3543/3405 3156/3286/3156\r\nf 3397/3531/3397 3404/3542/3404 3405/3543/3405 3398/3532/3398\r\nf 3403/3541/3403 3406/3544/3406 3405/3543/3405 3404/3542/3404\r\nf 2105/2174/2105 2465/2592/2465 3407/3545/3407 3233/3363/3233\r\nf 2462/2589/2462 3408/3546/3408 3407/3545/3407 2465/2592/2465\r\nf 3409/3547/3409 3410/3548/3410 3407/3545/3407 3408/3546/3408\r\nf 3235/3365/3235 3233/3363/3233 3407/3545/3407 3410/3548/3410\r\nf 1700/1755/1700 2936/3066/2936 3411/3549/3411 2460/2587/2460\r\nf 2940/3070/2940 3412/3550/3412 3411/3549/3411 2936/3066/2936\r\nf 3409/3547/3409 3408/3546/3408 3411/3549/3411 3412/3550/3412\r\nf 2462/2589/2462 2460/2587/2460 3411/3549/3411 3408/3546/3408\r\nf 2947/3077/2947 3413/3551/3413 3414/3552/3414 2948/3078/2948\r\nf 3415/3553/3415 3416/3554/3416 3414/3552/3414 3413/3551/3413\r\nf 3409/3547/3409 3412/3550/3412 3414/3552/3414 3416/3554/3416\r\nf 2940/3070/2940 2948/3078/2948 3414/3552/3414 3412/3550/3412\r\nf 3237/3367/3237 3240/3370/3240 3417/3555/3417 3418/3556/3418\r\nf 3235/3365/3235 3410/3548/3410 3417/3555/3417 3240/3370/3240\r\nf 3409/3547/3409 3416/3554/3416 3417/3555/3417 3410/3548/3410\r\nf 3415/3553/3415 3418/3556/3418 3417/3555/3417 3416/3554/3416\r\nf 2485/3557/2485 3419/3558/3419 3420/3559/3420 2486/3560/2486\r\nf 3421/3561/3421 3422/3562/3422 3420/3559/3420 3419/3558/3419\r\nf 3423/3563/3423 3424/3564/3424 3420/3559/3420 3422/3562/3422\r\nf 2489/3565/2489 2486/3560/2486 3420/3559/3420 3424/3564/3424\r\nf 3425/3566/3425 3426/3567/3426 3427/3568/3427 3428/3569/3428\r\nf 3429/3570/3429 3430/3571/3430 3427/3568/3427 3426/3567/3426\r\nf 3423/3563/3423 3422/3562/3422 3427/3568/3427 3430/3571/3430\r\nf 3421/3561/3421 3428/3569/3428 3427/3568/3427 3422/3562/3422\r\nf 3431/3572/3431 3432/3573/3432 3433/3574/3433 3434/3575/3434\r\nf 3435/3576/3435 3436/3577/3436 3433/3574/3433 3432/3573/3432\r\nf 3423/3563/3423 3430/3571/3430 3433/3574/3433 3436/3577/3436\r\nf 3429/3570/3429 3434/3575/3434 3433/3574/3433 3430/3571/3430\r\nf 2491/3578/2491 2494/3579/2494 3437/3580/3437 3438/3581/3438\r\nf 2489/3565/2489 3424/3564/3424 3437/3580/3437 2494/3579/2494\r\nf 3423/3563/3423 3436/3577/3436 3437/3580/3437 3424/3564/3424\r\nf 3435/3576/3435 3438/3581/3438 3437/3580/3437 3436/3577/3436\r\nf 2503/3582/2503 3439/3583/3439 3440/3584/3440 2504/3585/2504\r\nf 3441/3586/3441 3442/3587/3442 3440/3584/3440 3439/3583/3439\r\nf 3443/3588/3443 3444/3589/3444 3440/3584/3440 3442/3587/3442\r\nf 2507/3590/2507 2504/3585/2504 3440/3584/3440 3444/3589/3444\r\nf 3445/3591/3445 3446/3592/3446 3447/3593/3447 3448/3594/3448\r\nf 3449/3595/3449 3450/3596/3449 3447/3593/3447 3446/3592/3446\r\nf 3443/3588/3443 3442/3587/3442 3447/3593/3447 3450/3596/3449\r\nf 3441/3586/3441 3448/3594/3448 3447/3593/3447 3442/3587/3442\r\nf 3425/3566/3425 3428/3569/3428 3451/3597/3450 3452/3598/3451\r\nf 3421/3561/3421 3453/3599/3452 3451/3597/3450 3428/3569/3428\r\nf 3443/3588/3443 3450/3596/3449 3451/3597/3450 3453/3599/3452\r\nf 3449/3595/3449 3452/3598/3451 3451/3597/3450 3450/3596/3449\r\nf 2485/3557/2485 2510/3600/2510 3454/3601/3453 3419/3558/3419\r\nf 2507/3590/2507 3444/3589/3444 3454/3601/3453 2510/3600/2510\r\nf 3443/3588/3443 3453/3599/3452 3454/3601/3453 3444/3589/3444\r\nf 3421/3561/3421 3419/3558/3419 3454/3601/3453 3453/3599/3452\r\nf 2519/3602/2519 3455/3603/3454 3456/3604/3455 2520/3605/2520\r\nf 3457/3606/3456 3458/3607/3457 3456/3604/3455 3455/3603/3454\r\nf 3459/3608/3458 3460/3609/3459 3456/3604/3455 3458/3607/3457\r\nf 2523/3610/2523 2520/3605/2520 3456/3604/3455 3460/3609/3459\r\nf 3461/3611/3460 3462/3612/3461 3463/3613/3462 3464/3614/3463\r\nf 3465/3615/3464 3466/3616/3465 3463/3613/3462 3462/3612/3461\r\nf 3459/3608/3458 3458/3607/3457 3463/3613/3462 3466/3616/3465\r\nf 3457/3606/3456 3464/3614/3463 3463/3613/3462 3458/3607/3457\r\nf 3445/3591/3445 3448/3594/3448 3467/3617/3466 3468/3618/3467\r\nf 3441/3586/3441 3469/3619/3468 3467/3617/3466 3448/3594/3448\r\nf 3459/3608/3458 3466/3616/3465 3467/3617/3466 3469/3619/3468\r\nf 3465/3615/3464 3468/3618/3467 3467/3617/3466 3466/3616/3465\r\nf 2503/3582/2503 2526/3620/2526 3470/3621/3469 3439/3583/3439\r\nf 2523/3610/2523 3460/3609/3459 3470/3621/3469 2526/3620/2526\r\nf 3459/3608/3458 3469/3619/3468 3470/3621/3469 3460/3609/3459\r\nf 3441/3586/3441 3439/3583/3439 3470/3621/3469 3469/3619/3468\r\nf 2535/3622/2535 3471/3623/3470 3472/3624/3471 2536/3625/2536\r\nf 3473/3626/3472 3474/3627/3473 3472/3624/3471 3471/3623/3470\r\nf 3475/3628/3474 3476/3629/3475 3472/3624/3471 3474/3627/3473\r\nf 2539/3630/2539 2536/3625/2536 3472/3624/3471 3476/3629/3475\r\nf 3477/3631/3476 3478/3632/3477 3479/3633/3478 3480/3634/3479\r\nf 3481/3635/3480 3482/3636/3481 3479/3633/3478 3478/3632/3477\r\nf 3475/3628/3474 3474/3627/3473 3479/3633/3478 3482/3636/3481\r\nf 3473/3626/3472 3480/3634/3479 3479/3633/3478 3474/3627/3473\r\nf 3461/3611/3460 3464/3614/3463 3483/3637/3482 3484/3638/3483\r\nf 3457/3606/3456 3485/3639/3484 3483/3637/3482 3464/3614/3463\r\nf 3475/3628/3474 3482/3636/3481 3483/3637/3482 3485/3639/3484\r\nf 3481/3635/3480 3484/3638/3483 3483/3637/3482 3482/3636/3481\r\nf 2519/3602/2519 2542/3640/2542 3486/3641/3485 3455/3603/3454\r\nf 2539/3630/2539 3476/3629/3475 3486/3641/3485 2542/3640/2542\r\nf 3475/3628/3474 3485/3639/3484 3486/3641/3485 3476/3629/3475\r\nf 3457/3606/3456 3455/3603/3454 3486/3641/3485 3485/3639/3484\r\nf 2553/3642/2553 3487/3643/3486 3488/3644/3487 2554/3645/2554\r\nf 3489/3646/3488 3490/3647/3487 3488/3644/3487 3487/3643/3486\r\nf 3491/3648/3489 3492/3649/3490 3488/3644/3487 3490/3647/3487\r\nf 2557/3650/2557 2554/3645/2554 3488/3644/3487 3492/3649/3490\r\nf 3493/3651/3491 3494/3652/3492 3495/3653/3493 3496/3654/3494\r\nf 3497/3655/3495 3498/3656/3496 3495/3653/3493 3494/3652/3492\r\nf 3491/3648/3489 3490/3647/3487 3495/3653/3493 3498/3656/3496\r\nf 3489/3646/3488 3496/3654/3494 3495/3653/3493 3490/3647/3487\r\nf 3499/3657/3497 3500/3658/3497 3501/3659/3498 3502/3660/3499\r\nf 3503/3661/3500 3504/3662/3501 3501/3659/3498 3500/3658/3497\r\nf 3491/3648/3489 3498/3656/3496 3501/3659/3498 3504/3662/3501\r\nf 3497/3655/3495 3502/3660/3499 3501/3659/3498 3498/3656/3496\r\nf 2559/3663/2559 2562/3664/2562 3505/3665/3502 3506/3666/3503\r\nf 2557/3650/2557 3492/3649/3490 3505/3665/3502 2562/3664/2562\r\nf 3491/3648/3489 3504/3662/3501 3505/3665/3502 3492/3649/3490\r\nf 3503/3661/3500 3506/3666/3503 3505/3665/3502 3504/3662/3501\r\nf 2573/3667/2573 3507/3668/3504 3508/3669/3505 2574/3670/2574\r\nf 3509/3671/3506 3510/3672/3507 3508/3669/3505 3507/3668/3504\r\nf 3511/3673/3508 3512/3674/3509 3508/3669/3505 3510/3672/3507\r\nf 2577/3675/2577 2574/3670/2574 3508/3669/3505 3512/3674/3509\r\nf 3513/3676/3510 3514/3677/3511 3515/3678/3512 3516/3679/3513\r\nf 3517/3680/3514 3518/3681/3515 3515/3678/3512 3514/3677/3511\r\nf 3511/3673/3508 3510/3672/3507 3515/3678/3512 3518/3681/3515\r\nf 3509/3671/3506 3516/3679/3513 3515/3678/3512 3510/3672/3507\r\nf 3519/3682/3516 3520/3683/3517 3521/3684/3518 3522/3685/3519\r\nf 3523/3686/3520 3524/3687/3521 3521/3684/3518 3520/3683/3517\r\nf 3511/3673/3508 3518/3681/3515 3521/3684/3518 3524/3687/3521\r\nf 3517/3680/3514 3522/3685/3519 3521/3684/3518 3518/3681/3515\r\nf 2579/3688/2579 2582/3689/2582 3525/3690/3522 3526/3691/3523\r\nf 2577/3675/2577 3512/3674/3509 3525/3690/3522 2582/3689/2582\r\nf 3511/3673/3508 3524/3687/3521 3525/3690/3522 3512/3674/3509\r\nf 3523/3686/3520 3526/3691/3523 3525/3690/3522 3524/3687/3521\r\nf 2579/3688/2579 3526/3691/3523 3527/3692/3524 3528/3693/3525\r\nf 3523/3686/3520 3529/3694/3526 3527/3692/3524 3526/3691/3523\r\nf 3530/3695/3527 3531/3696/3528 3527/3692/3524 3529/3694/3526\r\nf 3532/3697/3529 3528/3693/3525 3527/3692/3524 3531/3696/3528\r\nf 3519/3682/3516 3533/3698/3530 3534/3699/3531 3520/3683/3517\r\nf 3535/3700/3532 3536/3701/3533 3534/3699/3531 3533/3698/3530\r\nf 3530/3695/3527 3529/3694/3526 3534/3699/3531 3536/3701/3533\r\nf 3523/3686/3520 3520/3683/3517 3534/3699/3531 3529/3694/3526\r\nf 3537/3702/3534 3538/3703/3535 3539/3704/3536 3540/3705/3537\r\nf 3541/3706/3538 3542/3707/3539 3539/3704/3536 3538/3703/3535\r\nf 3530/3695/3527 3536/3701/3533 3539/3704/3536 3542/3707/3539\r\nf 3535/3700/3532 3540/3705/3537 3539/3704/3536 3536/3701/3533\r\nf 3543/3708/3540 3544/3709/3541 3545/3710/3542 3546/3711/3543\r\nf 3532/3697/3529 3531/3696/3528 3545/3710/3542 3544/3709/3541\r\nf 3530/3695/3527 3542/3707/3539 3545/3710/3542 3531/3696/3528\r\nf 3541/3706/3538 3546/3711/3543 3545/3710/3542 3542/3707/3539\r\nf 2609/3712/2609 3547/3713/2609 3548/3714/2610 2610/3715/2610\r\nf 3549/3716/3544 3550/3717/3545 3548/3714/2610 3547/3713/2609\r\nf 3551/3718/3546 3552/3719/2613 3548/3714/2610 3550/3717/3545\r\nf 2613/3720/2613 2610/3715/2610 3548/3714/2610 3552/3719/2613\r\nf 3553/3721/2609 3554/3722/3547 3555/3723/3548 3556/3724/3549\r\nf 3557/3725/3550 3558/3726/3546 3555/3723/3548 3554/3722/3547\r\nf 3551/3718/3546 3550/3717/3545 3555/3723/3548 3558/3726/3546\r\nf 3549/3716/3544 3556/3724/3549 3555/3723/3548 3550/3717/3545\r\nf 3559/3727/3551 3560/3728/3551 3561/3729/3552 3562/3730/3553\r\nf 3563/3731/3551 3564/3732/3552 3561/3729/3552 3560/3728/3551\r\nf 3551/3718/3546 3558/3726/3546 3561/3729/3552 3564/3732/3552\r\nf 3557/3725/3550 3562/3730/3553 3561/3729/3552 3558/3726/3546\r\nf 2615/3733/2615 2618/3734/2618 3565/3735/2618 3566/3736/2615\r\nf 2613/3720/2613 3552/3719/2613 3565/3735/2618 2618/3734/2618\r\nf 3551/3718/3546 3564/3732/3552 3565/3735/2618 3552/3719/2613\r\nf 3563/3731/3551 3566/3736/2615 3565/3735/2618 3564/3732/3552\r\nf 2559/3663/2559 3506/3666/3503 3567/3737/3554 2625/3738/2625\r\nf 3503/3661/3500 3568/3739/3555 3567/3737/3554 3506/3666/3503\r\nf 3569/3740/3556 3570/3741/3557 3567/3737/3554 3568/3739/3555\r\nf 2627/3742/2627 2625/3738/2625 3567/3737/3554 3570/3741/3557\r\nf 3499/3657/3497 3571/3743/3558 3572/3744/3559 3500/3658/3497\r\nf 3573/3745/3560 3574/3746/3561 3572/3744/3559 3571/3743/3558\r\nf 3569/3740/3556 3568/3739/3555 3572/3744/3559 3574/3746/3561\r\nf 3503/3661/3500 3500/3658/3497 3572/3744/3559 3568/3739/3555\r\nf 3553/3721/2609 3556/3724/3549 3575/3747/3562 3576/3748/3563\r\nf 3549/3716/3544 3577/3749/3564 3575/3747/3562 3556/3724/3549\r\nf 3569/3740/3556 3574/3746/3561 3575/3747/3562 3577/3749/3564\r\nf 3573/3745/3560 3576/3748/3563 3575/3747/3562 3574/3746/3561\r\nf 2609/3712/2609 2630/3750/2630 3578/3751/3565 3547/3713/2609\r\nf 2627/3742/2627 3570/3741/3557 3578/3751/3565 2630/3750/2630\r\nf 3569/3740/3556 3577/3749/3564 3578/3751/3565 3570/3741/3557\r\nf 3549/3716/3544 3547/3713/2609 3578/3751/3565 3577/3749/3564\r\nf 2615/3733/2615 3566/3736/2615 3579/3752/3566 2637/3753/2637\r\nf 3563/3731/3551 3580/3754/3566 3579/3752/3566 3566/3736/2615\r\nf 3581/3755/3567 3582/3756/3567 3579/3752/3566 3580/3754/3566\r\nf 2639/3757/2639 2637/3753/2637 3579/3752/3566 3582/3756/3567\r\nf 3559/3727/3551 3583/3758/3568 3584/3759/3566 3560/3728/3551\r\nf 3585/3760/3569 3586/3761/2639 3584/3759/3566 3583/3758/3568\r\nf 3581/3755/3567 3580/3754/3566 3584/3759/3566 3586/3761/2639\r\nf 3563/3731/3551 3560/3728/3551 3584/3759/3566 3580/3754/3566\r\nf 3477/3631/3476 3480/3634/3479 3587/3762/3570 3588/3763/3571\r\nf 3473/3626/3472 3589/3764/3571 3587/3762/3570 3480/3634/3479\r\nf 3581/3755/3567 3586/3761/2639 3587/3762/3570 3589/3764/3571\r\nf 3585/3760/3569 3588/3763/3571 3587/3762/3570 3586/3761/2639\r\nf 2535/3622/2535 2642/3765/2642 3590/3766/3571 3471/3623/3470\r\nf 2639/3757/2639 3582/3756/3567 3590/3766/3571 2642/3765/2642\r\nf 3581/3755/3567 3589/3764/3571 3590/3766/3571 3582/3756/3567\r\nf 3473/3626/3472 3471/3623/3470 3590/3766/3571 3589/3764/3571\r\nf 2491/3578/2491 3438/3581/3438 3591/3767/3572 3592/3768/3573\r\nf 3435/3576/3435 3593/3769/3574 3591/3767/3572 3438/3581/3438\r\nf 3594/3770/3575 3595/3771/3576 3591/3767/3572 3593/3769/3574\r\nf 3596/3772/3577 3592/3768/3573 3591/3767/3572 3595/3771/3576\r\nf 3431/3572/3431 3597/3773/3578 3598/3774/3578 3432/3573/3432\r\nf 3599/3775/3579 3600/3776/3580 3598/3774/3578 3597/3773/3578\r\nf 3594/3770/3575 3593/3769/3574 3598/3774/3578 3600/3776/3580\r\nf 3435/3576/3435 3432/3573/3432 3598/3774/3578 3593/3769/3574\r\nf 3601/3777/3581 3602/3778/3582 3603/3779/3583 3604/3780/3584\r\nf 3605/3781/3582 3606/3782/3585 3603/3779/3583 3602/3778/3582\r\nf 3594/3770/3575 3600/3776/3580 3603/3779/3583 3606/3782/3585\r\nf 3599/3775/3579 3604/3780/3584 3603/3779/3583 3600/3776/3580\r\nf 3607/3783/3586 3608/3784/3587 3609/3785/3587 3610/3786/3586\r\nf 3596/3772/3577 3595/3771/3576 3609/3785/3587 3608/3784/3587\r\nf 3594/3770/3575 3606/3782/3585 3609/3785/3587 3595/3771/3576\r\nf 3605/3781/3582 3610/3786/3586 3609/3785/3587 3606/3782/3585\r\nf 2659/3787/2659 3611/3788/3588 3612/3789/3589 2671/3790/2671\r\nf 3613/3791/3590 3614/3792/3591 3612/3789/3589 3611/3788/3588\r\nf 3615/3793/3592 3616/3794/3593 3612/3789/3589 3614/3792/3591\r\nf 2673/3795/2673 2671/3790/2671 3612/3789/3589 3616/3794/3593\r\nf 3617/3796/3594 3618/3797/3595 3619/3798/3596 3620/3799/3597\r\nf 3621/3800/3598 3622/3801/3599 3619/3798/3596 3618/3797/3595\r\nf 3615/3793/3592 3614/3792/3591 3619/3798/3596 3622/3801/3599\r\nf 3613/3791/3590 3620/3799/3597 3619/3798/3596 3614/3792/3591\r\nf 3623/3802/3600 3624/3803/3601 3625/3804/3602 3626/3805/3603\r\nf 3627/3806/3604 3628/3807/3605 3625/3804/3602 3624/3803/3601\r\nf 3615/3793/3592 3622/3801/3599 3625/3804/3602 3628/3807/3605\r\nf 3621/3800/3598 3626/3805/3603 3625/3804/3602 3622/3801/3599\r\nf 2675/3808/2675 2678/3809/2678 3629/3810/3606 3630/3811/3607\r\nf 2673/3795/2673 3616/3794/3593 3629/3810/3606 2678/3809/2678\r\nf 3615/3793/3592 3628/3807/3605 3629/3810/3606 3616/3794/3593\r\nf 3627/3806/3604 3630/3811/3607 3629/3810/3606 3628/3807/3605\r\nf 2687/3812/2687 3631/3813/2687 3632/3814/3608 2688/3815/2688\r\nf 3633/3816/2687 3634/3817/3609 3632/3814/3608 3631/3813/2687\r\nf 3635/3818/3610 3636/3819/3611 3632/3814/3608 3634/3817/3609\r\nf 2691/3820/2691 2688/3815/2688 3632/3814/3608 3636/3819/3611\r\nf 3637/3821/2687 3638/3822/3612 3639/3823/3613 3640/3824/2687\r\nf 3641/3825/3614 3642/3826/3615 3639/3823/3613 3638/3822/3612\r\nf 3635/3818/3610 3634/3817/3609 3639/3823/3613 3642/3826/3615\r\nf 3633/3816/2687 3640/3824/2687 3639/3823/3613 3634/3817/3609\r\nf 3513/3676/3510 3516/3679/3513 3643/3827/3616 3644/3828/3617\r\nf 3509/3671/3506 3645/3829/3618 3643/3827/3616 3516/3679/3513\r\nf 3635/3818/3610 3642/3826/3615 3643/3827/3616 3645/3829/3618\r\nf 3641/3825/3614 3644/3828/3617 3643/3827/3616 3642/3826/3615\r\nf 2573/3667/2573 2694/3830/2694 3646/3831/3619 3507/3668/3504\r\nf 2691/3820/2691 3636/3819/3611 3646/3831/3619 2694/3830/2694\r\nf 3635/3818/3610 3645/3829/3618 3646/3831/3619 3636/3819/3611\r\nf 3509/3671/3506 3507/3668/3504 3646/3831/3619 3645/3829/3618\r\nf 2675/3808/2675 3630/3811/3607 3647/3832/2927 2927/3833/2927\r\nf 3627/3806/3604 3648/3834/2927 3647/3832/2927 3630/3811/3607\r\nf 3649/3835/2929 3650/3836/2929 3647/3832/2927 3648/3834/2927\r\nf 2929/3837/2929 2927/3833/2927 3647/3832/2927 3650/3836/2929\r\nf 3623/3802/3600 3651/3838/3620 3652/3839/3620 3624/3803/3601\r\nf 3653/3840/2929 3654/3841/3621 3652/3839/3620 3651/3838/3620\r\nf 3649/3835/2929 3648/3834/2927 3652/3839/3620 3654/3841/3621\r\nf 3627/3806/3604 3624/3803/3601 3652/3839/3620 3648/3834/2927\r\nf 3655/3842/2931 3656/3843/3622 3657/3844/3623 3658/3845/3624\r\nf 3659/3846/3625 3660/3847/3623 3657/3844/3623 3656/3843/3622\r\nf 3649/3835/2929 3654/3841/3621 3657/3844/3623 3660/3847/3623\r\nf 3653/3840/2929 3658/3845/3624 3657/3844/3623 3654/3841/3621\r\nf 2931/3848/2931 2934/3849/2934 3661/3850/3624 3662/3851/2931\r\nf 2929/3837/2929 3650/3836/2929 3661/3850/3624 2934/3849/2934\r\nf 3649/3835/2929 3660/3847/3623 3661/3850/3624 3650/3836/2929\r\nf 3659/3846/3625 3662/3851/2931 3661/3850/3624 3660/3847/3623\r\nf 2961/3852/2961 3663/3853/3626 3664/3854/3627 2962/3855/2962\r\nf 3665/3856/3628 3666/3857/3629 3664/3854/3627 3663/3853/3626\r\nf 3667/3858/3630 3668/3859/3631 3664/3854/3627 3666/3857/3629\r\nf 2965/3860/2965 2962/3855/2962 3664/3854/3627 3668/3859/3631\r\nf 3669/3861/3632 3670/3862/3633 3671/3863/3634 3672/3864/3635\r\nf 3673/3865/3636 3674/3866/3637 3671/3863/3634 3670/3862/3633\r\nf 3667/3858/3630 3666/3857/3629 3671/3863/3634 3674/3866/3637\r\nf 3665/3856/3628 3672/3864/3635 3671/3863/3634 3666/3857/3629\r\nf 3675/3867/3638 3676/3868/3639 3677/3869/3640 3678/3870/3641\r\nf 3679/3871/3642 3680/3872/3643 3677/3869/3640 3676/3868/3639\r\nf 3667/3858/3630 3674/3866/3637 3677/3869/3640 3680/3872/3643\r\nf 3673/3865/3636 3678/3870/3641 3677/3869/3640 3674/3866/3637\r\nf 2967/3873/2967 2970/3874/2970 3681/3875/3644 3682/3876/3645\r\nf 2965/3860/2965 3668/3859/3631 3681/3875/3644 2970/3874/2970\r\nf 3667/3858/3630 3680/3872/3643 3681/3875/3644 3668/3859/3631\r\nf 3679/3871/3642 3682/3876/3645 3681/3875/3644 3680/3872/3643\r\nf 2967/3873/2967 3682/3876/3645 3683/3877/3646 2979/3878/2979\r\nf 3679/3871/3642 3684/3879/3647 3683/3877/3646 3682/3876/3645\r\nf 3685/3880/3648 3686/3881/3649 3683/3877/3646 3684/3879/3647\r\nf 2981/3882/2981 2979/3878/2979 3683/3877/3646 3686/3881/3649\r\nf 3675/3867/3638 3687/3883/3650 3688/3884/3651 3676/3868/3639\r\nf 3689/3885/3652 3690/3886/3653 3688/3884/3651 3687/3883/3650\r\nf 3685/3880/3648 3684/3879/3647 3688/3884/3651 3690/3886/3653\r\nf 3679/3871/3642 3676/3868/3639 3688/3884/3651 3684/3879/3647\r\nf 3691/3887/3654 3692/3888/3655 3693/3889/3656 3694/3890/3657\r\nf 3695/3891/3658 3696/3892/3659 3693/3889/3656 3692/3888/3655\r\nf 3685/3880/3648 3690/3886/3653 3693/3889/3656 3696/3892/3659\r\nf 3689/3885/3652 3694/3890/3657 3693/3889/3656 3690/3886/3653\r\nf 2983/3893/2983 2986/3894/2986 3697/3895/3660 3698/3896/3661\r\nf 2981/3882/2981 3686/3881/3649 3697/3895/3660 2986/3894/2986\r\nf 3685/3880/3648 3696/3892/3659 3697/3895/3660 3686/3881/3649\r\nf 3695/3891/3658 3698/3896/3661 3697/3895/3660 3696/3892/3659\r\nf 2983/3893/2983 3698/3896/3661 3699/3897/3662 2995/3898/2995\r\nf 3695/3891/3658 3700/3899/3663 3699/3897/3662 3698/3896/3661\r\nf 3701/3900/3664 3702/3901/3665 3699/3897/3662 3700/3899/3663\r\nf 2997/3902/2997 2995/3898/2995 3699/3897/3662 3702/3901/3665\r\nf 3691/3887/3654 3703/3903/3666 3704/3904/3667 3692/3888/3655\r\nf 3705/3905/3668 3706/3906/3669 3704/3904/3667 3703/3903/3666\r\nf 3701/3900/3664 3700/3899/3663 3704/3904/3667 3706/3906/3669\r\nf 3695/3891/3658 3692/3888/3655 3704/3904/3667 3700/3899/3663\r\nf 3707/3907/3670 3708/3908/3671 3709/3909/3672 3710/3910/3673\r\nf 3711/3911/3674 3712/3912/3675 3709/3909/3672 3708/3908/3671\r\nf 3701/3900/3664 3706/3906/3669 3709/3909/3672 3712/3912/3675\r\nf 3705/3905/3668 3710/3910/3673 3709/3909/3672 3706/3906/3669\r\nf 2999/3913/2999 3002/3914/3002 3713/3915/3676 3714/3916/3677\r\nf 2997/3902/2997 3702/3901/3665 3713/3915/3676 3002/3914/3002\r\nf 3701/3900/3664 3712/3912/3675 3713/3915/3676 3702/3901/3665\r\nf 3711/3911/3674 3714/3916/3677 3713/3915/3676 3712/3912/3675\r\nf 2999/3913/2999 3714/3916/3677 3715/3917/3678 3011/3918/3011\r\nf 3711/3911/3674 3716/3919/3679 3715/3917/3678 3714/3916/3677\r\nf 3717/3920/3680 3718/3921/3681 3715/3917/3678 3716/3919/3679\r\nf 3013/3922/3013 3011/3918/3011 3715/3917/3678 3718/3921/3681\r\nf 3707/3907/3670 3719/3923/3682 3720/3924/3683 3708/3908/3671\r\nf 3721/3925/3684 3722/3926/3685 3720/3924/3683 3719/3923/3682\r\nf 3717/3920/3680 3716/3919/3679 3720/3924/3683 3722/3926/3685\r\nf 3711/3911/3674 3708/3908/3671 3720/3924/3683 3716/3919/3679\r\nf 3723/3927/3686 3724/3928/3686 3725/3929/3687 3726/3930/3688\r\nf 3727/3931/3689 3728/3932/3690 3725/3929/3687 3724/3928/3686\r\nf 3717/3920/3680 3722/3926/3685 3725/3929/3687 3728/3932/3690\r\nf 3721/3925/3684 3726/3930/3688 3725/3929/3687 3722/3926/3685\r\nf 3015/3933/3015 3018/3934/3018 3729/3935/3691 3730/3936/3692\r\nf 3013/3922/3013 3718/3921/3681 3729/3935/3691 3018/3934/3018\r\nf 3717/3920/3680 3728/3932/3690 3729/3935/3691 3718/3921/3681\r\nf 3727/3931/3689 3730/3936/3692 3729/3935/3691 3728/3932/3690\r\nf 3029/3937/3029 3731/3938/3693 3732/3939/3694 3030/3940/3030\r\nf 3733/3941/3695 3734/3942/3694 3732/3939/3694 3731/3938/3693\r\nf 3735/3943/3696 3736/3944/3697 3732/3939/3694 3734/3942/3694\r\nf 3033/3945/3033 3030/3940/3030 3732/3939/3694 3736/3944/3697\r\nf 3737/3946/3698 3738/3947/3699 3739/3948/3700 3740/3949/3701\r\nf 3741/3950/3702 3742/3951/3703 3739/3948/3700 3738/3947/3699\r\nf 3735/3943/3696 3734/3942/3694 3739/3948/3700 3742/3951/3703\r\nf 3733/3941/3695 3740/3949/3701 3739/3948/3700 3734/3942/3694\r\nf 3743/3952/3704 3744/3953/3705 3745/3954/3706 3746/3955/3707\r\nf 3747/3956/3708 3748/3957/3709 3745/3954/3706 3744/3953/3705\r\nf 3735/3943/3696 3742/3951/3703 3745/3954/3706 3748/3957/3709\r\nf 3741/3950/3702 3746/3955/3707 3745/3954/3706 3742/3951/3703\r\nf 3035/3958/3035 3038/3959/3038 3749/3960/3710 3750/3961/3711\r\nf 3033/3945/3033 3736/3944/3697 3749/3960/3710 3038/3959/3038\r\nf 3735/3943/3696 3748/3957/3709 3749/3960/3710 3736/3944/3697\r\nf 3747/3956/3708 3750/3961/3711 3749/3960/3710 3748/3957/3709\r\nf 3049/3962/3049 3751/3963/3712 3752/3964/3713 3050/3965/3050\r\nf 3753/3966/3714 3754/3967/3715 3752/3964/3713 3751/3963/3712\r\nf 3755/3968/3716 3756/3969/3717 3752/3964/3713 3754/3967/3715\r\nf 3053/3970/3053 3050/3965/3050 3752/3964/3713 3756/3969/3717\r\nf 3757/3971/3718 3758/3972/3719 3759/3973/3720 3760/3974/3721\r\nf 3761/3975/3722 3762/3976/3723 3759/3973/3720 3758/3972/3719\r\nf 3755/3968/3716 3754/3967/3715 3759/3973/3720 3762/3976/3723\r\nf 3753/3966/3714 3760/3974/3721 3759/3973/3720 3754/3967/3715\r\nf 3763/3977/3724 3764/3978/3725 3765/3979/3726 3766/3980/3727\r\nf 3767/3981/3728 3768/3982/3729 3765/3979/3726 3764/3978/3725\r\nf 3755/3968/3716 3762/3976/3723 3765/3979/3726 3768/3982/3729\r\nf 3761/3975/3722 3766/3980/3727 3765/3979/3726 3762/3976/3723\r\nf 3055/3983/3055 3058/3984/3058 3769/3985/3730 3770/3986/3731\r\nf 3053/3970/3053 3756/3969/3717 3769/3985/3730 3058/3984/3058\r\nf 3755/3968/3716 3768/3982/3729 3769/3985/3730 3756/3969/3717\r\nf 3767/3981/3728 3770/3986/3731 3769/3985/3730 3768/3982/3729\r\nf 3035/3958/3035 3750/3961/3711 3771/3987/3732 3772/3988/3733\r\nf 3747/3956/3708 3773/3989/3734 3771/3987/3732 3750/3961/3711\r\nf 3774/3990/3735 3775/3991/3736 3771/3987/3732 3773/3989/3734\r\nf 3776/3992/3737 3772/3988/3733 3771/3987/3732 3775/3991/3736\r\nf 3743/3952/3704 3777/3993/3738 3778/3994/3739 3744/3953/3705\r\nf 3779/3995/3740 3780/3996/3741 3778/3994/3739 3777/3993/3738\r\nf 3774/3990/3735 3773/3989/3734 3778/3994/3739 3780/3996/3741\r\nf 3747/3956/3708 3744/3953/3705 3778/3994/3739 3773/3989/3734\r\nf 3781/3997/3742 3782/3998/3743 3783/3999/3744 3784/4000/3745\r\nf 3785/4001/3746 3786/4002/3747 3783/3999/3744 3782/3998/3743\r\nf 3774/3990/3735 3780/3996/3741 3783/3999/3744 3786/4002/3747\r\nf 3779/3995/3740 3784/4000/3745 3783/3999/3744 3780/3996/3741\r\nf 3787/4003/3748 3788/4004/3749 3789/4005/3750 3790/4006/3751\r\nf 3776/3992/3737 3775/3991/3736 3789/4005/3750 3788/4004/3749\r\nf 3774/3990/3735 3786/4002/3747 3789/4005/3750 3775/3991/3736\r\nf 3785/4001/3746 3790/4006/3751 3789/4005/3750 3786/4002/3747\r\nf 3089/4007/3089 3791/4008/3752 3792/4009/3753 3090/4010/3090\r\nf 3793/4011/3752 3794/4012/3754 3792/4009/3753 3791/4008/3752\r\nf 3795/4013/3755 3796/4014/3093 3792/4009/3753 3794/4012/3754\r\nf 3093/4015/3093 3090/4010/3090 3792/4009/3753 3796/4014/3093\r\nf 3797/4016/3756 3798/4017/3757 3799/4018/3758 3800/4019/3759\r\nf 3801/4020/3760 3802/4021/3755 3799/4018/3758 3798/4017/3757\r\nf 3795/4013/3755 3794/4012/3754 3799/4018/3758 3802/4021/3755\r\nf 3793/4011/3752 3800/4019/3759 3799/4018/3758 3794/4012/3754\r\nf 3803/4022/3761 3804/4023/3762 3805/4024/3763 3806/4025/3764\r\nf 3807/4026/3765 3808/4027/3763 3805/4024/3763 3804/4023/3762\r\nf 3795/4013/3755 3802/4021/3755 3805/4024/3763 3808/4027/3763\r\nf 3801/4020/3760 3806/4025/3764 3805/4024/3763 3802/4021/3755\r\nf 3095/4028/3095 3098/4029/3098 3809/4030/3763 3810/4031/3766\r\nf 3093/4015/3093 3796/4014/3093 3809/4030/3763 3098/4029/3098\r\nf 3795/4013/3755 3808/4027/3763 3809/4030/3763 3796/4014/3093\r\nf 3807/4026/3765 3810/4031/3766 3809/4030/3763 3808/4027/3763\r\nf 3095/4028/3095 3810/4031/3766 3811/4032/3767 3105/4033/3105\r\nf 3807/4026/3765 3812/4034/3768 3811/4032/3767 3810/4031/3766\r\nf 3813/4035/3769 3814/4036/3770 3811/4032/3767 3812/4034/3768\r\nf 3107/4037/3107 3105/4033/3105 3811/4032/3767 3814/4036/3770\r\nf 3803/4022/3761 3815/4038/3771 3816/4039/3772 3804/4023/3762\r\nf 3817/4040/3769 3818/4041/3773 3816/4039/3772 3815/4038/3771\r\nf 3813/4035/3769 3812/4034/3768 3816/4039/3772 3818/4041/3773\r\nf 3807/4026/3765 3804/4023/3762 3816/4039/3772 3812/4034/3768\r\nf 3737/3946/3698 3740/3949/3701 3819/4042/3774 3820/4043/3774\r\nf 3733/3941/3695 3821/4044/3775 3819/4042/3774 3740/3949/3701\r\nf 3813/4035/3769 3818/4041/3773 3819/4042/3774 3821/4044/3775\r\nf 3817/4040/3769 3820/4043/3774 3819/4042/3774 3818/4041/3773\r\nf 3029/3937/3029 3110/4045/3110 3822/4046/3776 3731/3938/3693\r\nf 3107/4037/3107 3814/4036/3770 3822/4046/3776 3110/4045/3110\r\nf 3813/4035/3769 3821/4044/3775 3822/4046/3776 3814/4036/3770\r\nf 3733/3941/3695 3731/3938/3693 3822/4046/3776 3821/4044/3775\r\nf 3015/3933/3015 3730/3936/3692 3823/4047/3777 3117/4048/3117\r\nf 3727/3931/3689 3824/4049/3777 3823/4047/3777 3730/3936/3692\r\nf 3825/4050/3119 3826/4051/3119 3823/4047/3777 3824/4049/3777\r\nf 3119/4052/3119 3117/4048/3117 3823/4047/3777 3826/4051/3119\r\nf 3723/3927/3686 3827/4053/3778 3828/4054/3779 3724/3928/3686\r\nf 3829/4055/3119 3830/4056/3119 3828/4054/3779 3827/4053/3778\r\nf 3825/4050/3119 3824/4049/3777 3828/4054/3779 3830/4056/3119\r\nf 3727/3931/3689 3724/3928/3686 3828/4054/3779 3824/4049/3777\r\nf 3797/4016/3756 3800/4019/3759 3831/4057/3780 3832/4058/3781\r\nf 3793/4011/3752 3833/4059/3780 3831/4057/3780 3800/4019/3759\r\nf 3825/4050/3119 3830/4056/3119 3831/4057/3780 3833/4059/3780\r\nf 3829/4055/3119 3832/4058/3781 3831/4057/3780 3830/4056/3119\r\nf 3089/4007/3089 3122/4060/3122 3834/4061/3780 3791/4008/3752\r\nf 3119/4052/3119 3826/4051/3119 3834/4061/3780 3122/4060/3122\r\nf 3825/4050/3119 3833/4059/3780 3834/4061/3780 3826/4051/3119\r\nf 3793/4011/3752 3791/4008/3752 3834/4061/3780 3833/4059/3780\r\nf 3159/4062/3159 3835/4063/3782 3836/4064/3783 3837/4065/3784\r\nf 3838/4066/3785 3839/4067/3786 3836/4064/3783 3835/4063/3782\r\nf 3840/4068/3787 3841/4069/3788 3836/4064/3783 3839/4067/3786\r\nf 3842/4070/3789 3837/4065/3784 3836/4064/3783 3841/4069/3788\r\nf 3843/4071/3790 3844/4072/3791 3845/4073/3792 3846/4074/3793\r\nf 3847/4075/3794 3848/4076/3795 3845/4073/3792 3844/4072/3791\r\nf 3840/4068/3787 3839/4067/3786 3845/4073/3792 3848/4076/3795\r\nf 3838/4066/3785 3846/4074/3793 3845/4073/3792 3839/4067/3786\r\nf 3849/4077/3796 3850/4078/3796 3851/4079/3797 3852/4080/3797\r\nf 3853/4081/3796 3854/4082/3797 3851/4079/3797 3850/4078/3796\r\nf 3840/4068/3787 3848/4076/3795 3851/4079/3797 3854/4082/3797\r\nf 3847/4075/3794 3852/4080/3797 3851/4079/3797 3848/4076/3795\r\nf 3855/4083/3796 3856/4084/3798 3857/4085/3798 3858/4086/3796\r\nf 3842/4070/3789 3841/4069/3788 3857/4085/3798 3856/4084/3798\r\nf 3840/4068/3787 3854/4082/3797 3857/4085/3798 3841/4069/3788\r\nf 3853/4081/3796 3858/4086/3796 3857/4085/3798 3854/4082/3797\r\nf 3153/4087/3153 3859/4088/3799 3860/4089/3800 3154/4090/3154\r\nf 3861/4091/3801 3862/4092/3802 3860/4089/3800 3859/4088/3799\r\nf 3863/4093/3803 3864/4094/3804 3860/4089/3800 3862/4092/3802\r\nf 3157/4095/3157 3154/4090/3154 3860/4089/3800 3864/4094/3804\r\nf 3865/4096/3805 3866/4097/3806 3867/4098/3807 3868/4099/3808\r\nf 3869/4100/3809 3870/4101/3810 3867/4098/3807 3866/4097/3806\r\nf 3863/4093/3803 3862/4092/3802 3867/4098/3807 3870/4101/3810\r\nf 3861/4091/3801 3868/4099/3808 3867/4098/3807 3862/4092/3802\r\nf 3843/4071/3790 3846/4074/3793 3871/4102/3811 3872/4103/3812\r\nf 3838/4066/3785 3873/4104/3813 3871/4102/3811 3846/4074/3793\r\nf 3863/4093/3803 3870/4101/3810 3871/4102/3811 3873/4104/3813\r\nf 3869/4100/3809 3872/4103/3812 3871/4102/3811 3870/4101/3810\r\nf 3159/4062/3159 3162/4105/3162 3874/4106/3814 3835/4063/3782\r\nf 3157/4095/3157 3864/4094/3804 3874/4106/3814 3162/4105/3162\r\nf 3863/4093/3803 3873/4104/3813 3874/4106/3814 3864/4094/3804\r\nf 3838/4066/3785 3835/4063/3782 3874/4106/3814 3873/4104/3813\r\nf 3055/3983/3055 3770/3986/3731 3875/4107/3815 3169/4108/3169\r\nf 3767/3981/3728 3876/4109/3816 3875/4107/3815 3770/3986/3731\r\nf 3877/4110/3817 3878/4111/3818 3875/4107/3815 3876/4109/3816\r\nf 3171/4112/3171 3169/4108/3169 3875/4107/3815 3878/4111/3818\r\nf 3763/3977/3724 3879/4113/3819 3880/4114/3820 3764/3978/3725\r\nf 3881/4115/3821 3882/4116/3822 3880/4114/3820 3879/4113/3819\r\nf 3877/4110/3817 3876/4109/3816 3880/4114/3820 3882/4116/3822\r\nf 3767/3981/3728 3764/3978/3725 3880/4114/3820 3876/4109/3816\r\nf 3637/3821/2687 3640/3824/2687 3883/4117/3823 3884/4118/3824\r\nf 3633/3816/2687 3885/4119/3825 3883/4117/3823 3640/3824/2687\r\nf 3877/4110/3817 3882/4116/3822 3883/4117/3823 3885/4119/3825\r\nf 3881/4115/3821 3884/4118/3824 3883/4117/3823 3882/4116/3822\r\nf 2687/3812/2687 3174/4120/3174 3886/4121/3826 3631/3813/2687\r\nf 3171/4112/3171 3878/4111/3818 3886/4121/3826 3174/4120/3174\r\nf 3877/4110/3817 3885/4119/3825 3886/4121/3826 3878/4111/3818\r\nf 3633/3816/2687 3631/3813/2687 3886/4121/3826 3885/4119/3825\r\nf 2931/4122/2931 3662/4123/2931 3887/4124/3827 3401/4125/3401\r\nf 3659/4126/3625 3888/4127/3828 3887/4124/3827 3662/4123/2931\r\nf 3889/4128/3403 3890/4129/3403 3887/4124/3827 3888/4127/3828\r\nf 3403/4130/3403 3401/4125/3401 3887/4124/3827 3890/4129/3403\r\nf 3655/4131/2931 3891/4132/3828 3892/4133/3828 3656/4134/3622\r\nf 3893/4135/3403 3894/4136/3403 3892/4133/3828 3891/4132/3828\r\nf 3889/4128/3403 3888/4127/3828 3892/4133/3828 3894/4136/3403\r\nf 3659/4126/3625 3656/4134/3622 3892/4133/3828 3888/4127/3828\r\nf 3865/4096/3805 3868/4099/3808 3895/4137/3829 3896/4138/3829\r\nf 3861/4091/3801 3897/4139/3830 3895/4137/3829 3868/4099/3808\r\nf 3889/4128/3403 3894/4136/3403 3895/4137/3829 3897/4139/3830\r\nf 3893/4135/3403 3896/4138/3829 3895/4137/3829 3894/4136/3403\r\nf 3153/4087/3153 3406/4140/3406 3898/4141/3830 3859/4088/3799\r\nf 3403/4130/3403 3890/4129/3403 3898/4141/3830 3406/4140/3406\r\nf 3889/4128/3403 3897/4139/3830 3898/4141/3830 3890/4129/3403\r\nf 3861/4091/3801 3859/4088/3799 3898/4141/3830 3897/4139/3830\r\nf 2705/2835/2705 3899/4142/3831 3900/4143/3832 2706/2836/2706\r\nf 3901/4144/3833 3902/4145/3834 3900/4143/3832 3899/4142/3831\r\nf 3903/4146/3835 3904/4147/3836 3900/4143/3832 3902/4145/3834\r\nf 2709/2839/2709 2706/2836/2706 3900/4143/3832 3904/4147/3836\r\nf 3905/4148/3837 3906/4149/3838 3907/4150/3839 3908/4151/3840\r\nf 3909/4152/3841 3910/4153/3842 3907/4150/3839 3906/4149/3838\r\nf 3903/4146/3835 3902/4145/3834 3907/4150/3839 3910/4153/3842\r\nf 3901/4144/3833 3908/4151/3840 3907/4150/3839 3902/4145/3834\r\nf 3911/4154/3843 3912/4155/3844 3913/4156/3845 3914/4157/3846\r\nf 3915/4158/3847 3916/4159/3848 3913/4156/3845 3912/4155/3844\r\nf 3903/4146/3835 3910/4153/3842 3913/4156/3845 3916/4159/3848\r\nf 3909/4152/3841 3914/4157/3846 3913/4156/3845 3910/4153/3842\r\nf 2711/2841/2711 2714/2844/2714 3917/4160/3849 3918/4161/3850\r\nf 2709/2839/2709 3904/4147/3836 3917/4160/3849 2714/2844/2714\r\nf 3903/4146/3835 3916/4159/3848 3917/4160/3849 3904/4147/3836\r\nf 3915/4158/3847 3918/4161/3850 3917/4160/3849 3916/4159/3848\r\nf 2711/2841/2711 3918/4161/3850 3919/4162/3851 2723/2853/2723\r\nf 3915/4158/3847 3920/4163/3852 3919/4162/3851 3918/4161/3850\r\nf 3921/4164/3853 3922/4165/3854 3919/4162/3851 3920/4163/3852\r\nf 2725/2855/2725 2723/2853/2723 3919/4162/3851 3922/4165/3854\r\nf 3911/4154/3843 3923/4166/3855 3924/4167/3856 3912/4155/3844\r\nf 3925/4168/3857 3926/4169/3858 3924/4167/3856 3923/4166/3855\r\nf 3921/4164/3853 3920/4163/3852 3924/4167/3856 3926/4169/3858\r\nf 3915/4158/3847 3912/4155/3844 3924/4167/3856 3920/4163/3852\r\nf 3927/4170/3859 3928/4171/3860 3929/4172/3861 3930/4173/3861\r\nf 3931/4174/3862 3932/4175/3863 3929/4172/3861 3928/4171/3860\r\nf 3921/4164/3853 3926/4169/3858 3929/4172/3861 3932/4175/3863\r\nf 3925/4168/3857 3930/4173/3861 3929/4172/3861 3926/4169/3858\r\nf 2727/2857/2727 2730/2860/2730 3933/4176/3864 3934/4177/3865\r\nf 2725/2855/2725 3922/4165/3854 3933/4176/3864 2730/2860/2730\r\nf 3921/4164/3853 3932/4175/3863 3933/4176/3864 3922/4165/3854\r\nf 3931/4174/3862 3934/4177/3865 3933/4176/3864 3932/4175/3863\r\nf 2727/2857/2727 3934/4177/3865 3935/4178/3866 2739/2869/2739\r\nf 3931/4174/3862 3936/4179/3867 3935/4178/3866 3934/4177/3865\r\nf 3937/4180/3868 3938/4181/3869 3935/4178/3866 3936/4179/3867\r\nf 2741/2871/2741 2739/2869/2739 3935/4178/3866 3938/4181/3869\r\nf 3927/4170/3859 3939/4182/3870 3940/4183/3871 3928/4171/3860\r\nf 3941/4184/3872 3942/4185/3873 3940/4183/3871 3939/4182/3870\r\nf 3937/4180/3868 3936/4179/3867 3940/4183/3871 3942/4185/3873\r\nf 3931/4174/3862 3928/4171/3860 3940/4183/3871 3936/4179/3867\r\nf 3943/4186/3874 3944/4187/3875 3945/4188/3876 3946/4189/3877\r\nf 3947/4190/3878 3948/4191/3879 3945/4188/3876 3944/4187/3875\r\nf 3937/4180/3868 3942/4185/3873 3945/4188/3876 3948/4191/3879\r\nf 3941/4184/3872 3946/4189/3877 3945/4188/3876 3942/4185/3873\r\nf 2743/2873/2743 2746/2876/2746 3949/4192/3880 3950/4193/3881\r\nf 2741/2871/2741 3938/4181/3869 3949/4192/3880 2746/2876/2746\r\nf 3937/4180/3868 3948/4191/3879 3949/4192/3880 3938/4181/3869\r\nf 3947/4190/3878 3950/4193/3881 3949/4192/3880 3948/4191/3879\r\nf 2757/2887/2757 3951/4194/3882 3952/4195/3883 2758/2888/2758\r\nf 3953/4196/3884 3954/4197/3885 3952/4195/3883 3951/4194/3882\r\nf 3955/4198/3886 3956/4199/3887 3952/4195/3883 3954/4197/3885\r\nf 2761/2891/2761 2758/2888/2758 3952/4195/3883 3956/4199/3887\r\nf 3957/4200/3888 3958/4201/3889 3959/4202/3890 3960/4203/3891\r\nf 3961/4204/3892 3962/4205/3893 3959/4202/3890 3958/4201/3889\r\nf 3955/4198/3886 3954/4197/3885 3959/4202/3890 3962/4205/3893\r\nf 3953/4196/3884 3960/4203/3891 3959/4202/3890 3954/4197/3885\r\nf 3963/4206/3894 3964/4207/3895 3965/4208/3896 3966/4209/3897\r\nf 3967/4210/3898 3968/4211/3899 3965/4208/3896 3964/4207/3895\r\nf 3955/4198/3886 3962/4205/3893 3965/4208/3896 3968/4211/3899\r\nf 3961/4204/3892 3966/4209/3897 3965/4208/3896 3962/4205/3893\r\nf 2763/2893/2763 2766/2896/2766 3969/4212/3900 3970/4213/3901\r\nf 2761/2891/2761 3956/4199/3887 3969/4212/3900 2766/2896/2766\r\nf 3955/4198/3886 3968/4211/3899 3969/4212/3900 3956/4199/3887\r\nf 3967/4210/3898 3970/4213/3901 3969/4212/3900 3968/4211/3899\r\nf 2777/2907/2777 3971/4214/3902 3972/4215/2778 2778/2908/2778\r\nf 3973/4216/3903 3974/4217/2778 3972/4215/2778 3971/4214/3902\r\nf 3975/4218/2781 3976/4219/2781 3972/4215/2778 3974/4217/2778\r\nf 2781/2911/2781 2778/2908/2778 3972/4215/2778 3976/4219/2781\r\nf 3977/4220/3904 3978/4221/3905 3979/4222/3906 3980/4223/3904\r\nf 3981/4224/3907 3982/4225/2781 3979/4222/3906 3978/4221/3905\r\nf 3975/4218/2781 3974/4217/2778 3979/4222/3906 3982/4225/2781\r\nf 3973/4216/3903 3980/4223/3904 3979/4222/3906 3974/4217/2778\r\nf 3983/4226/3908 3984/4227/3909 3985/4228/3910 3986/4229/3911\r\nf 3987/4230/3912 3988/4231/3910 3985/4228/3910 3984/4227/3909\r\nf 3975/4218/2781 3982/4225/2781 3985/4228/3910 3988/4231/3910\r\nf 3981/4224/3907 3986/4229/3911 3985/4228/3910 3982/4225/2781\r\nf 2783/2913/2783 2786/2916/2786 3989/4232/2786 3990/4233/3913\r\nf 2781/2911/2781 3976/4219/2781 3989/4232/2786 2786/2916/2786\r\nf 3975/4218/2781 3988/4231/3910 3989/4232/2786 3976/4219/2781\r\nf 3987/4230/3912 3990/4233/3913 3989/4232/2786 3988/4231/3910\r\nf 2819/2949/2819 3991/4234/3914 3992/4235/3915 3993/4236/3916\r\nf 3994/4237/3917 3995/4238/3918 3992/4235/3915 3991/4234/3914\r\nf 3996/4239/3919 3997/4240/3920 3992/4235/3915 3995/4238/3918\r\nf 3998/4241/3921 3993/4236/3916 3992/4235/3915 3997/4240/3920\r\nf 3999/4242/3922 4000/4243/3923 4001/4244/3924 4002/4245/3925\r\nf 4003/4246/3926 4004/4247/3927 4001/4244/3924 4000/4243/3923\r\nf 3996/4239/3919 3995/4238/3918 4001/4244/3924 4004/4247/3927\r\nf 3994/4237/3917 4002/4245/3925 4001/4244/3924 3995/4238/3918\r\nf 4005/4248/3928 4006/4249/3929 4007/4250/3930 4008/4251/3931\r\nf 4009/4252/3932 4010/4253/3933 4007/4250/3930 4006/4249/3929\r\nf 3996/4239/3919 4004/4247/3927 4007/4250/3930 4010/4253/3933\r\nf 4003/4246/3926 4008/4251/3931 4007/4250/3930 4004/4247/3927\r\nf 4011/4254/3934 4012/4255/3935 4013/4256/3936 4014/4257/3937\r\nf 3998/4241/3921 3997/4240/3920 4013/4256/3936 4012/4255/3935\r\nf 3996/4239/3919 4010/4253/3933 4013/4256/3936 3997/4240/3920\r\nf 4009/4252/3932 4014/4257/3937 4013/4256/3936 4010/4253/3933\r\nf 2813/2943/2813 4015/4258/3938 4016/4259/3939 2814/2944/2814\r\nf 4017/4260/3940 4018/4261/3941 4016/4259/3939 4015/4258/3938\r\nf 4019/4262/3942 4020/4263/3943 4016/4259/3939 4018/4261/3941\r\nf 2817/2947/2817 2814/2944/2814 4016/4259/3939 4020/4263/3943\r\nf 4021/4264/3944 4022/4265/3945 4023/4266/3946 4024/4267/3947\r\nf 4025/4268/3948 4026/4269/3949 4023/4266/3946 4022/4265/3945\r\nf 4019/4262/3942 4018/4261/3941 4023/4266/3946 4026/4269/3949\r\nf 4017/4260/3940 4024/4267/3947 4023/4266/3946 4018/4261/3941\r\nf 3999/4242/3922 4002/4245/3925 4027/4270/3950 4028/4271/3951\r\nf 3994/4237/3917 4029/4272/3952 4027/4270/3950 4002/4245/3925\r\nf 4019/4262/3942 4026/4269/3949 4027/4270/3950 4029/4272/3952\r\nf 4025/4268/3948 4028/4271/3951 4027/4270/3950 4026/4269/3949\r\nf 2819/2949/2819 2822/2952/2822 4030/4273/3953 3991/4234/3914\r\nf 2817/2947/2817 4020/4263/3943 4030/4273/3953 2822/2952/2822\r\nf 4019/4262/3942 4029/4272/3952 4030/4273/3953 4020/4263/3943\r\nf 3994/4237/3917 3991/4234/3914 4030/4273/3953 4029/4272/3952\r\nf 2831/2961/2831 4031/4274/2831 4032/4275/2832 2832/2962/2832\r\nf 4033/4276/3954 4034/4277/2832 4032/4275/2832 4031/4274/2831\r\nf 4035/4278/3955 4036/4279/3955 4032/4275/2832 4034/4277/2832\r\nf 2835/2965/2835 2832/2962/2832 4032/4275/2832 4036/4279/3955\r\nf 4037/4280/2831 4038/4281/2832 4039/4282/2832 4040/4283/2831\r\nf 4041/4284/3955 4042/4285/3955 4039/4282/2832 4038/4281/2832\r\nf 4035/4278/3955 4034/4277/2832 4039/4282/2832 4042/4285/3955\r\nf 4033/4276/3954 4040/4283/2831 4039/4282/2832 4034/4277/2832\r\nf 4021/4264/3944 4024/4267/3947 4043/4286/3956 4044/4287/3956\r\nf 4017/4260/3940 4045/4288/3957 4043/4286/3956 4024/4267/3947\r\nf 4035/4278/3955 4042/4285/3955 4043/4286/3956 4045/4288/3957\r\nf 4041/4284/3955 4044/4287/3956 4043/4286/3956 4042/4285/3955\r\nf 2813/2943/2813 2838/2968/2838 4046/4289/3957 4015/4258/3938\r\nf 2835/2965/2835 4036/4279/3955 4046/4289/3957 2838/2968/2838\r\nf 4035/4278/3955 4045/4288/3957 4046/4289/3957 4036/4279/3955\r\nf 4017/4260/3940 4015/4258/3938 4046/4289/3957 4045/4288/3957\r\nf 2743/2873/2743 3950/4193/3881 4047/4290/3958 2845/2975/2845\r\nf 3947/4190/3878 4048/4291/3959 4047/4290/3958 3950/4193/3881\r\nf 4049/4292/3960 4050/4293/3961 4047/4290/3958 4048/4291/3959\r\nf 2847/2977/2847 2845/2975/2845 4047/4290/3958 4050/4293/3961\r\nf 3943/4186/3874 4051/4294/3962 4052/4295/3963 3944/4187/3875\r\nf 4053/4296/3964 4054/4297/3965 4052/4295/3963 4051/4294/3962\r\nf 4049/4292/3960 4048/4291/3959 4052/4295/3963 4054/4297/3965\r\nf 3947/4190/3878 3944/4187/3875 4052/4295/3963 4048/4291/3959\r\nf 3977/4220/3904 3980/4223/3904 4055/4298/3966 4056/4299/3966\r\nf 3973/4216/3903 4057/4300/3967 4055/4298/3966 3980/4223/3904\r\nf 4049/4292/3960 4054/4297/3965 4055/4298/3966 4057/4300/3967\r\nf 4053/4296/3964 4056/4299/3966 4055/4298/3966 4054/4297/3965\r\nf 2777/2907/2777 2850/2980/2850 4058/4301/3968 3971/4214/3902\r\nf 2847/2977/2847 4050/4293/3961 4058/4301/3968 2850/2980/2850\r\nf 4049/4292/3960 4057/4300/3967 4058/4301/3968 4050/4293/3961\r\nf 3973/4216/3903 3971/4214/3902 4058/4301/3968 4057/4300/3967\r\nf 2861/2991/2861 4059/4302/3969 4060/4303/3970 2862/2992/2862\r\nf 4061/4304/3971 4062/4305/3972 4060/4303/3970 4059/4302/3969\r\nf 4063/4306/3973 4064/4307/3974 4060/4303/3970 4062/4305/3972\r\nf 2865/2995/2865 2862/2992/2862 4060/4303/3970 4064/4307/3974\r\nf 4065/4308/3975 4066/4309/3976 4067/4310/3977 4068/4311/3978\r\nf 4069/4312/3979 4070/4313/3980 4067/4310/3977 4066/4309/3976\r\nf 4063/4306/3973 4062/4305/3972 4067/4310/3977 4070/4313/3980\r\nf 4061/4304/3971 4068/4311/3978 4067/4310/3977 4062/4305/3972\r\nf 4071/4314/3981 4072/4315/3982 4073/4316/3983 4074/4317/3984\r\nf 4075/4318/3985 4076/4319/3986 4073/4316/3983 4072/4315/3982\r\nf 4063/4306/3973 4070/4313/3980 4073/4316/3983 4076/4319/3986\r\nf 4069/4312/3979 4074/4317/3984 4073/4316/3983 4070/4313/3980\r\nf 2867/2997/2867 2870/3000/2870 4077/4320/3987 4078/4321/3988\r\nf 2865/2995/2865 4064/4307/3974 4077/4320/3987 2870/3000/2870\r\nf 4063/4306/3973 4076/4319/3986 4077/4320/3987 4064/4307/3974\r\nf 4075/4318/3985 4078/4321/3988 4077/4320/3987 4076/4319/3986\r\nf 2867/2997/2867 4078/4321/3988 4079/4322/3989 4080/4323/3990\r\nf 4075/4318/3985 4081/4324/3991 4079/4322/3989 4078/4321/3988\r\nf 4082/4325/3992 4083/4326/3993 4079/4322/3989 4081/4324/3991\r\nf 4084/4327/3994 4080/4323/3990 4079/4322/3989 4083/4326/3993\r\nf 4071/4314/3981 4085/4328/3995 4086/4329/3996 4072/4315/3982\r\nf 4087/4330/3997 4088/4331/3998 4086/4329/3996 4085/4328/3995\r\nf 4082/4325/3992 4081/4324/3991 4086/4329/3996 4088/4331/3998\r\nf 4075/4318/3985 4072/4315/3982 4086/4329/3996 4081/4324/3991\r\nf 4089/4332/3999 4090/4333/4000 4091/4334/4001 4092/4335/4002\r\nf 4093/4336/4003 4094/4337/4004 4091/4334/4001 4090/4333/4000\r\nf 4082/4325/3992 4088/4331/3998 4091/4334/4001 4094/4337/4004\r\nf 4087/4330/3997 4092/4335/4002 4091/4334/4001 4088/4331/3998\r\nf 4095/4338/4005 4096/4339/4006 4097/4340/4007 4098/4341/4008\r\nf 4084/4327/3994 4083/4326/3993 4097/4340/4007 4096/4339/4006\r\nf 4082/4325/3992 4094/4337/4004 4097/4340/4007 4083/4326/3993\r\nf 4093/4336/4003 4098/4341/4008 4097/4340/4007 4094/4337/4004\r\nf 2783/2913/2783 3990/4233/3913 4099/4342/4009 2899/3029/2899\r\nf 3987/4230/3912 4100/4343/4010 4099/4342/4009 3990/4233/3913\r\nf 4101/4344/4011 4102/4345/4011 4099/4342/4009 4100/4343/4010\r\nf 2901/3031/2901 2899/3029/2899 4099/4342/4009 4102/4345/4011\r\nf 3983/4226/3908 4103/4346/4012 4104/4347/4010 3984/4227/3909\r\nf 4105/4348/4013 4106/4349/4014 4104/4347/4010 4103/4346/4012\r\nf 4101/4344/4011 4100/4343/4010 4104/4347/4010 4106/4349/4014\r\nf 3987/4230/3912 3984/4227/3909 4104/4347/4010 4100/4343/4010\r\nf 4107/4350/4015 4108/4351/4016 4109/4352/4017 4110/4353/4018\r\nf 4111/4354/4019 4112/4355/2906 4109/4352/4017 4108/4351/4016\r\nf 4101/4344/4011 4106/4349/4014 4109/4352/4017 4112/4355/2906\r\nf 4105/4348/4013 4110/4353/4018 4109/4352/4017 4106/4349/4014\r\nf 2903/3033/2903 2906/3036/2906 4113/4356/2906 4114/4357/4020\r\nf 2901/3031/2901 4102/4345/4011 4113/4356/2906 2906/3036/2906\r\nf 4101/4344/4011 4112/4355/2906 4113/4356/2906 4102/4345/4011\r\nf 4111/4354/4019 4114/4357/4020 4113/4356/2906 4112/4355/2906\r\nf 2903/3033/2903 4114/4357/4020 4115/4358/4021 2913/3043/2913\r\nf 4111/4354/4019 4116/4359/4022 4115/4358/4021 4114/4357/4020\r\nf 4117/4360/4023 4118/4361/4024 4115/4358/4021 4116/4359/4022\r\nf 2915/3045/2915 2913/3043/2913 4115/4358/4021 4118/4361/4024\r\nf 4107/4350/4015 4119/4362/4022 4120/4363/4025 4108/4351/4016\r\nf 4121/4364/4026 4122/4365/4027 4120/4363/4025 4119/4362/4022\r\nf 4117/4360/4023 4116/4359/4022 4120/4363/4025 4122/4365/4027\r\nf 4111/4354/4019 4108/4351/4016 4120/4363/4025 4116/4359/4022\r\nf 4065/4308/3975 4068/4311/3978 4123/4366/4028 4124/4367/4029\r\nf 4061/4304/3971 4125/4368/4030 4123/4366/4028 4068/4311/3978\r\nf 4117/4360/4023 4122/4365/4027 4123/4366/4028 4125/4368/4030\r\nf 4121/4364/4026 4124/4367/4029 4123/4366/4028 4122/4365/4027\r\nf 2861/2991/2861 2918/3048/2918 4126/4369/4031 4059/4302/3969\r\nf 2915/3045/2915 4118/4361/4024 4126/4369/4031 2918/3048/2918\r\nf 4117/4360/4023 4125/4368/4030 4126/4369/4031 4118/4361/4024\r\nf 4061/4304/3971 4059/4302/3969 4126/4369/4031 4125/4368/4030\r\nf 2763/2893/2763 3970/4213/3901 4127/4370/4032 2943/3073/2943\r\nf 3967/4210/3898 4128/4371/4033 4127/4370/4032 3970/4213/3901\r\nf 4129/4372/4034 4130/4373/4035 4127/4370/4032 4128/4371/4033\r\nf 2945/3075/2945 2943/3073/2943 4127/4370/4032 4130/4373/4035\r\nf 3963/4206/3894 4131/4374/4036 4132/4375/4037 3964/4207/3895\r\nf 4133/4376/4038 4134/4377/4039 4132/4375/4037 4131/4374/4036\r\nf 4129/4372/4034 4128/4371/4033 4132/4375/4037 4134/4377/4039\r\nf 3967/4210/3898 3964/4207/3895 4132/4375/4037 4128/4371/4033\r\nf 4135/4378/4040 4136/4379/4041 4137/4380/4042 4138/4381/4043\r\nf 4139/4382/4044 4140/4383/4045 4137/4380/4042 4136/4379/4041\r\nf 4129/4372/4034 4134/4377/4039 4137/4380/4042 4140/4383/4045\r\nf 4133/4376/4038 4138/4381/4043 4137/4380/4042 4134/4377/4039\r\nf 2947/3077/2947 2950/3080/2950 4141/4384/4046 4142/4385/4047\r\nf 2945/3075/2945 4130/4373/4035 4141/4384/4046 2950/3080/2950\r\nf 4129/4372/4034 4140/4383/4045 4141/4384/4046 4130/4373/4035\r\nf 4139/4382/4044 4142/4385/4047 4141/4384/4046 4140/4383/4045\r\nf 3185/3315/3185 4143/4386/4048 4144/4387/4049 3186/3316/3186\r\nf 4145/4388/4050 4146/4389/4051 4144/4387/4049 4143/4386/4048\r\nf 4147/4390/4052 4148/4391/4053 4144/4387/4049 4146/4389/4051\r\nf 3189/3319/3189 3186/3316/3186 4144/4387/4049 4148/4391/4053\r\nf 4149/4392/4054 4150/4393/4055 4151/4394/4056 4152/4395/4057\r\nf 4153/4396/4058 4154/4397/4059 4151/4394/4056 4150/4393/4055\r\nf 4147/4390/4052 4146/4389/4051 4151/4394/4056 4154/4397/4059\r\nf 4145/4388/4050 4152/4395/4057 4151/4394/4056 4146/4389/4051\r\nf 4155/4398/4060 4156/4399/4061 4157/4400/4062 4158/4401/4063\r\nf 4159/4402/4064 4160/4403/4065 4157/4400/4062 4156/4399/4061\r\nf 4147/4390/4052 4154/4397/4059 4157/4400/4062 4160/4403/4065\r\nf 4153/4396/4058 4158/4401/4063 4157/4400/4062 4154/4397/4059\r\nf 3191/3321/3191 3194/3324/3194 4161/4404/4066 4162/4405/4067\r\nf 3189/3319/3189 4148/4391/4053 4161/4404/4066 3194/3324/3194\r\nf 4147/4390/4052 4160/4403/4065 4161/4404/4066 4148/4391/4053\r\nf 4159/4402/4064 4162/4405/4067 4161/4404/4066 4160/4403/4065\r\nf 3203/3333/3203 4163/4406/4068 4164/4407/4069 3204/3334/3204\r\nf 4165/4408/4070 4166/4409/4071 4164/4407/4069 4163/4406/4068\r\nf 4167/4410/4072 4168/4411/4073 4164/4407/4069 4166/4409/4071\r\nf 3207/3337/3207 3204/3334/3204 4164/4407/4069 4168/4411/4073\r\nf 4169/4412/4074 4170/4413/4075 4171/4414/4076 4172/4415/4077\r\nf 4173/4416/4078 4174/4417/4079 4171/4414/4076 4170/4413/4075\r\nf 4167/4410/4072 4166/4409/4071 4171/4414/4076 4174/4417/4079\r\nf 4165/4408/4070 4172/4415/4077 4171/4414/4076 4166/4409/4071\r\nf 4149/4392/4054 4152/4395/4057 4175/4418/4080 4176/4419/4081\r\nf 4145/4388/4050 4177/4420/4082 4175/4418/4080 4152/4395/4057\r\nf 4167/4410/4072 4174/4417/4079 4175/4418/4080 4177/4420/4082\r\nf 4173/4416/4078 4176/4419/4081 4175/4418/4080 4174/4417/4079\r\nf 3185/3315/3185 3210/3340/3210 4178/4421/4083 4143/4386/4048\r\nf 3207/3337/3207 4168/4411/4073 4178/4421/4083 3210/3340/3210\r\nf 4167/4410/4072 4177/4420/4082 4178/4421/4083 4168/4411/4073\r\nf 4145/4388/4050 4143/4386/4048 4178/4421/4083 4177/4420/4082\r\nf 3219/3349/3219 4179/4422/4084 4180/4423/4085 3220/3350/3220\r\nf 4181/4424/4086 4182/4425/4087 4180/4423/4085 4179/4422/4084\r\nf 4183/4426/4088 4184/4427/4089 4180/4423/4085 4182/4425/4087\r\nf 3223/3353/3223 3220/3350/3220 4180/4423/4085 4184/4427/4089\r\nf 4185/4428/4090 4186/4429/4091 4187/4430/4092 4188/4431/4093\r\nf 4189/4432/4094 4190/4433/4095 4187/4430/4092 4186/4429/4091\r\nf 4183/4426/4088 4182/4425/4087 4187/4430/4092 4190/4433/4095\r\nf 4181/4424/4086 4188/4431/4093 4187/4430/4092 4182/4425/4087\r\nf 4169/4412/4074 4172/4415/4077 4191/4434/4096 4192/4435/4097\r\nf 4165/4408/4070 4193/4436/4098 4191/4434/4096 4172/4415/4077\r\nf 4183/4426/4088 4190/4433/4095 4191/4434/4096 4193/4436/4098\r\nf 4189/4432/4094 4192/4435/4097 4191/4434/4096 4190/4433/4095\r\nf 3203/3333/3203 3226/3356/3226 4194/4437/4099 4163/4406/4068\r\nf 3223/3353/3223 4184/4427/4089 4194/4437/4099 3226/3356/3226\r\nf 4183/4426/4088 4193/4436/4098 4194/4437/4099 4184/4427/4089\r\nf 4165/4408/4070 4163/4406/4068 4194/4437/4099 4193/4436/4098\r\nf 3237/3367/3237 4195/4438/4100 4196/4439/4101 3238/3368/3238\r\nf 4197/4440/4102 4198/4441/4103 4196/4439/4101 4195/4438/4100\r\nf 4199/4442/4104 4200/4443/4105 4196/4439/4101 4198/4441/4103\r\nf 3241/3371/3241 3238/3368/3238 4196/4439/4101 4200/4443/4105\r\nf 4201/4444/4106 4202/4445/4107 4203/4446/4108 4204/4447/4109\r\nf 4205/4448/4110 4206/4449/4111 4203/4446/4108 4202/4445/4107\r\nf 4199/4442/4104 4198/4441/4103 4203/4446/4108 4206/4449/4111\r\nf 4197/4440/4102 4204/4447/4109 4203/4446/4108 4198/4441/4103\r\nf 4207/4450/4112 4208/4451/4113 4209/4452/4114 4210/4453/4115\r\nf 4211/4454/4116 4212/4455/4117 4209/4452/4114 4208/4451/4113\r\nf 4199/4442/4104 4206/4449/4111 4209/4452/4114 4212/4455/4117\r\nf 4205/4448/4110 4210/4453/4115 4209/4452/4114 4206/4449/4111\r\nf 3243/3373/3243 3246/3376/3246 4213/4456/4118 4214/4457/4119\r\nf 3241/3371/3241 4200/4443/4105 4213/4456/4118 3246/3376/3246\r\nf 4199/4442/4104 4212/4455/4117 4213/4456/4118 4200/4443/4105\r\nf 4211/4454/4116 4214/4457/4119 4213/4456/4118 4212/4455/4117\r\nf 3257/3387/3257 4215/4458/4120 4216/4459/4121 3258/3388/3258\r\nf 4217/4460/4122 4218/4461/4121 4216/4459/4121 4215/4458/4120\r\nf 4219/4462/4123 4220/4463/4123 4216/4459/4121 4218/4461/4121\r\nf 3261/3391/3261 3258/3388/3258 4216/4459/4121 4220/4463/4123\r\nf 4221/4464/4124 4222/4465/4125 4223/4466/4125 4224/4467/4126\r\nf 4225/4468/3261 4226/4469/3261 4223/4466/4125 4222/4465/4125\r\nf 4219/4462/4123 4218/4461/4121 4223/4466/4125 4226/4469/3261\r\nf 4217/4460/4122 4224/4467/4126 4223/4466/4125 4218/4461/4121\r\nf 4227/4470/4127 4228/4471/4128 4229/4472/4129 4230/4473/4129\r\nf 4231/4474/4130 4232/4475/4129 4229/4472/4129 4228/4471/4128\r\nf 4219/4462/4123 4226/4469/3261 4229/4472/4129 4232/4475/4129\r\nf 4225/4468/3261 4230/4473/4129 4229/4472/4129 4226/4469/3261\r\nf 3263/3393/3263 3266/3396/3266 4233/4476/3266 4234/4477/4131\r\nf 3261/3391/3261 4220/4463/4123 4233/4476/3266 3266/3396/3266\r\nf 4219/4462/4123 4232/4475/4129 4233/4476/3266 4220/4463/4123\r\nf 4231/4474/4130 4234/4477/4131 4233/4476/3266 4232/4475/4129\r\nf 3191/3321/3191 4162/4405/4067 4235/4478/4132 4236/4479/4133\r\nf 4159/4402/4064 4237/4480/4134 4235/4478/4132 4162/4405/4067\r\nf 4238/4481/4135 4239/4482/4136 4235/4478/4132 4237/4480/4134\r\nf 4240/4483/4137 4236/4479/4133 4235/4478/4132 4239/4482/4136\r\nf 4155/4398/4060 4241/4484/4134 4242/4485/4138 4156/4399/4061\r\nf 4243/4486/4139 4244/4487/4135 4242/4485/4138 4241/4484/4134\r\nf 4238/4481/4135 4237/4480/4134 4242/4485/4138 4244/4487/4135\r\nf 4159/4402/4064 4156/4399/4061 4242/4485/4138 4237/4480/4134\r\nf 4245/4488/4140 4246/4489/4141 4247/4490/4142 4248/4491/4143\r\nf 4249/4492/4144 4250/4493/4142 4247/4490/4142 4246/4489/4141\r\nf 4238/4481/4135 4244/4487/4135 4247/4490/4142 4250/4493/4142\r\nf 4243/4486/4139 4248/4491/4143 4247/4490/4142 4244/4487/4135\r\nf 4251/4494/4145 4252/4495/4142 4253/4496/4142 4254/4497/4146\r\nf 4240/4483/4137 4239/4482/4136 4253/4496/4142 4252/4495/4142\r\nf 4238/4481/4135 4250/4493/4142 4253/4496/4142 4239/4482/4136\r\nf 4249/4492/4144 4254/4497/4146 4253/4496/4142 4250/4493/4142\r\nf 3287/3417/3287 4255/4498/4147 4256/4499/4148 3299/3429/3299\r\nf 4257/4500/4149 4258/4501/4150 4256/4499/4148 4255/4498/4147\r\nf 4259/4502/4151 4260/4503/4152 4256/4499/4148 4258/4501/4150\r\nf 3301/3431/3301 3299/3429/3299 4256/4499/4148 4260/4503/4152\r\nf 4261/4504/4153 4262/4505/4154 4263/4506/4155 4264/4507/4156\r\nf 4265/4508/4157 4266/4509/4158 4263/4506/4155 4262/4505/4154\r\nf 4259/4502/4151 4258/4501/4150 4263/4506/4155 4266/4509/4158\r\nf 4257/4500/4149 4264/4507/4156 4263/4506/4155 4258/4501/4150\r\nf 4267/4510/4159 4268/4511/4160 4269/4512/4161 4270/4513/4162\r\nf 4271/4514/4163 4272/4515/4164 4269/4512/4161 4268/4511/4160\r\nf 4259/4502/4151 4266/4509/4158 4269/4512/4161 4272/4515/4164\r\nf 4265/4508/4157 4270/4513/4162 4269/4512/4161 4266/4509/4158\r\nf 3303/3433/3303 3306/3436/3306 4273/4516/4165 4274/4517/4166\r\nf 3301/3431/3301 4260/4503/4152 4273/4516/4165 3306/3436/3306\r\nf 4259/4502/4151 4272/4515/4164 4273/4516/4165 4260/4503/4152\r\nf 4271/4514/4163 4274/4517/4166 4273/4516/4165 4272/4515/4164\r\nf 3303/3433/3303 4274/4517/4166 4275/4518/4167 3313/3445/3313\r\nf 4271/4514/4163 4276/4519/4167 4275/4518/4167 4274/4517/4166\r\nf 4277/4520/4168 4278/4521/4168 4275/4518/4167 4276/4519/4167\r\nf 3315/3447/3315 3313/3445/3313 4275/4518/4167 4278/4521/4168\r\nf 4267/4510/4159 4279/4522/4169 4280/4523/4169 4268/4511/4160\r\nf 4281/4524/4168 4282/4525/4168 4280/4523/4169 4279/4522/4169\r\nf 4277/4520/4168 4276/4519/4167 4280/4523/4169 4282/4525/4168\r\nf 4271/4514/4163 4268/4511/4160 4280/4523/4169 4276/4519/4167\r\nf 4037/4526/2831 4040/4527/2831 4283/4528/3318 4284/4529/4170\r\nf 4033/4530/3954 4285/4531/3318 4283/4528/3318 4040/4527/2831\r\nf 4277/4520/4168 4282/4525/4168 4283/4528/3318 4285/4531/3318\r\nf 4281/4524/4168 4284/4529/4170 4283/4528/3318 4282/4525/4168\r\nf 2831/3449/2831 3318/3452/3318 4286/4532/3318 4031/4533/2831\r\nf 3315/3447/3315 4278/4521/4168 4286/4532/3318 3318/3452/3318\r\nf 4277/4520/4168 4285/4531/3318 4286/4532/3318 4278/4521/4168\r\nf 4033/4530/3954 4031/4533/2831 4286/4532/3318 4285/4531/3318\r\nf 3263/3393/3263 4234/4477/4131 4287/4534/4171 3325/3459/3325\r\nf 4231/4474/4130 4288/4535/4172 4287/4534/4171 4234/4477/4131\r\nf 4289/4536/4173 4290/4537/4174 4287/4534/4171 4288/4535/4172\r\nf 3327/3461/3327 3325/3459/3325 4287/4534/4171 4290/4537/4174\r\nf 4227/4470/4127 4291/4538/4175 4292/4539/4176 4228/4471/4128\r\nf 4293/4540/4177 4294/4541/4178 4292/4539/4176 4291/4538/4175\r\nf 4289/4536/4173 4288/4535/4172 4292/4539/4176 4294/4541/4178\r\nf 4231/4474/4130 4228/4471/4128 4292/4539/4176 4288/4535/4172\r\nf 4185/4428/4090 4188/4431/4093 4295/4542/4179 4296/4543/4180\r\nf 4181/4424/4086 4297/4544/4181 4295/4542/4179 4188/4431/4093\r\nf 4289/4536/4173 4294/4541/4178 4295/4542/4179 4297/4544/4181\r\nf 4293/4540/4177 4296/4543/4180 4295/4542/4179 4294/4541/4178\r\nf 3219/3349/3219 3330/3464/3330 4298/4545/4182 4179/4422/4084\r\nf 3327/3461/3327 4290/4537/4174 4298/4545/4182 3330/3464/3330\r\nf 4289/4536/4173 4297/4544/4181 4298/4545/4182 4290/4537/4174\r\nf 4181/4424/4086 4179/4422/4084 4298/4545/4182 4297/4544/4181\r\nf 3341/3475/3341 4299/4546/4183 4300/4547/4184 3342/3476/3342\r\nf 4301/4548/4185 4302/4549/4186 4300/4547/4184 4299/4546/4183\r\nf 4303/4550/4187 4304/4551/4188 4300/4547/4184 4302/4549/4186\r\nf 3345/3479/3345 3342/3476/3342 4300/4547/4184 4304/4551/4188\r\nf 4305/4552/4189 4306/4553/4190 4307/4554/4191 4308/4555/4192\r\nf 4309/4556/4193 4310/4557/4194 4307/4554/4191 4306/4553/4190\r\nf 4303/4550/4187 4302/4549/4186 4307/4554/4191 4310/4557/4194\r\nf 4301/4548/4185 4308/4555/4192 4307/4554/4191 4302/4549/4186\r\nf 4311/4558/4195 4312/4559/4196 4313/4560/4197 4314/4561/4198\r\nf 4315/4562/4199 4316/4563/4200 4313/4560/4197 4312/4559/4196\r\nf 4303/4550/4187 4310/4557/4194 4313/4560/4197 4316/4563/4200\r\nf 4309/4556/4193 4314/4561/4198 4313/4560/4197 4310/4557/4194\r\nf 3347/3481/3347 3350/3484/3350 4317/4564/4201 4318/4565/4202\r\nf 3345/3479/3345 4304/4551/4188 4317/4564/4201 3350/3484/3350\r\nf 4303/4550/4187 4316/4563/4200 4317/4564/4201 4304/4551/4188\r\nf 4315/4562/4199 4318/4565/4202 4317/4564/4201 4316/4563/4200\r\nf 3243/3373/3243 4214/4457/4119 4319/4566/4203 4320/4567/4204\r\nf 4211/4454/4116 4321/4568/4205 4319/4566/4203 4214/4457/4119\r\nf 4322/4569/4206 4323/4570/4207 4319/4566/4203 4321/4568/4205\r\nf 4324/4571/4208 4320/4567/4204 4319/4566/4203 4323/4570/4207\r\nf 4207/4450/4112 4325/4572/4209 4326/4573/4210 4208/4451/4113\r\nf 4327/4574/4211 4328/4575/4212 4326/4573/4210 4325/4572/4209\r\nf 4322/4569/4206 4321/4568/4205 4326/4573/4210 4328/4575/4212\r\nf 4211/4454/4116 4208/4451/4113 4326/4573/4210 4321/4568/4205\r\nf 4329/4576/4213 4330/4577/4214 4331/4578/4215 4332/4579/4216\r\nf 4333/4580/4217 4334/4581/4218 4331/4578/4215 4330/4577/4214\r\nf 4322/4569/4206 4328/4575/4212 4331/4578/4215 4334/4581/4218\r\nf 4327/4574/4211 4332/4579/4216 4331/4578/4215 4328/4575/4212\r\nf 4335/4582/4219 4336/4583/4220 4337/4584/4221 4338/4585/4222\r\nf 4324/4571/4208 4323/4570/4207 4337/4584/4221 4336/4583/4220\r\nf 4322/4569/4206 4334/4581/4218 4337/4584/4221 4323/4570/4207\r\nf 4333/4580/4217 4338/4585/4222 4337/4584/4221 4334/4581/4218\r\nf 3375/3509/3375 4339/4586/4223 4340/4587/4224 3376/3510/3376\r\nf 4341/4588/4225 4342/4589/4224 4340/4587/4224 4339/4586/4223\r\nf 4343/4590/4226 4344/4591/4227 4340/4587/4224 4342/4589/4224\r\nf 3379/3513/3379 3376/3510/3376 4340/4587/4224 4344/4591/4227\r\nf 4345/4592/4228 4346/4593/4229 4347/4594/4224 4348/4595/4230\r\nf 4349/4596/4231 4350/4597/4232 4347/4594/4224 4346/4593/4229\r\nf 4343/4590/4226 4342/4589/4224 4347/4594/4224 4350/4597/4232\r\nf 4341/4588/4225 4348/4595/4230 4347/4594/4224 4342/4589/4224\r\nf 4221/4464/4124 4224/4467/4126 4351/4598/4233 4352/4599/4233\r\nf 4217/4460/4122 4353/4600/4234 4351/4598/4233 4224/4467/4126\r\nf 4343/4590/4226 4350/4597/4232 4351/4598/4233 4353/4600/4234\r\nf 4349/4596/4231 4352/4599/4233 4351/4598/4233 4350/4597/4232\r\nf 3257/3387/3257 3382/3516/3382 4354/4601/4235 4215/4458/4120\r\nf 3379/3513/3379 4344/4591/4227 4354/4601/4235 3382/3516/3382\r\nf 4343/4590/4226 4353/4600/4234 4354/4601/4235 4344/4591/4227\r\nf 4217/4460/4122 4215/4458/4120 4354/4601/4235 4353/4600/4234\r\nf 3347/3481/3347 4318/4565/4202 4355/4602/4236 3389/3523/3389\r\nf 4315/4562/4199 4356/4603/4237 4355/4602/4236 4318/4565/4202\r\nf 4357/4604/4238 4358/4605/4239 4355/4602/4236 4356/4603/4237\r\nf 3391/3525/3391 3389/3523/3389 4355/4602/4236 4358/4605/4239\r\nf 4311/4558/4195 4359/4606/4240 4360/4607/4241 4312/4559/4196\r\nf 4361/4608/4242 4362/4609/4243 4360/4607/4241 4359/4606/4240\r\nf 4357/4604/4238 4356/4603/4237 4360/4607/4241 4362/4609/4243\r\nf 4315/4562/4199 4312/4559/4196 4360/4607/4241 4356/4603/4237\r\nf 4345/4592/4228 4348/4595/4230 4363/4610/4244 4364/4611/4245\r\nf 4341/4588/4225 4365/4612/4246 4363/4610/4244 4348/4595/4230\r\nf 4357/4604/4238 4362/4609/4243 4363/4610/4244 4365/4612/4246\r\nf 4361/4608/4242 4364/4611/4245 4363/4610/4244 4362/4609/4243\r\nf 3375/3509/3375 3394/3528/3394 4366/4613/4247 4339/4586/4223\r\nf 3391/3525/3391 4358/4605/4239 4366/4613/4247 3394/3528/3394\r\nf 4357/4604/4238 4365/4612/4246 4366/4613/4247 4358/4605/4239\r\nf 4341/4588/4225 4339/4586/4223 4366/4613/4247 4365/4612/4246\r\nf 2947/3077/2947 4142/4385/4047 4367/4614/4248 3413/3551/3413\r\nf 4139/4382/4044 4368/4615/4249 4367/4614/4248 4142/4385/4047\r\nf 4369/4616/4250 4370/4617/4251 4367/4614/4248 4368/4615/4249\r\nf 3415/3553/3415 3413/3551/3413 4367/4614/4248 4370/4617/4251\r\nf 4135/4378/4040 4371/4618/4252 4372/4619/4253 4136/4379/4041\r\nf 4373/4620/4254 4374/4621/4255 4372/4619/4253 4371/4618/4252\r\nf 4369/4616/4250 4368/4615/4249 4372/4619/4253 4374/4621/4255\r\nf 4139/4382/4044 4136/4379/4041 4372/4619/4253 4368/4615/4249\r\nf 4201/4444/4106 4204/4447/4109 4375/4622/4256 4376/4623/4257\r\nf 4197/4440/4102 4377/4624/4258 4375/4622/4256 4204/4447/4109\r\nf 4369/4616/4250 4374/4621/4255 4375/4622/4256 4377/4624/4258\r\nf 4373/4620/4254 4376/4623/4257 4375/4622/4256 4374/4621/4255\r\nf 3237/3367/3237 3418/3556/3418 4378/4625/4259 4195/4438/4100\r\nf 3415/3553/3415 4370/4617/4251 4378/4625/4259 3418/3556/3418\r\nf 4369/4616/4250 4377/4624/4258 4378/4625/4259 4370/4617/4251\r\nf 4197/4440/4102 4195/4438/4100 4378/4625/4259 4377/4624/4258\r\nf 3065/3195/3065 3068/3198/3068 4379/4626/4260 4380/4627/4261\r\nf 3061/3191/3061 4381/4628/4262 4379/4626/4260 3068/3198/3068\r\nf 4382/4629/4263 4383/4630/4264 4379/4626/4260 4381/4628/4262\r\nf 4384/4631/4265 4380/4627/4261 4379/4626/4260 4383/4630/4264\r\nf 1847/1906/1847 1850/1909/1850 4385/4632/4266 3059/3189/3059\r\nf 1845/1904/1845 4386/4633/4267 4385/4632/4266 1850/1909/1850\r\nf 4382/4629/4263 4381/4628/4262 4385/4632/4266 4386/4633/4267\r\nf 3061/3191/3061 3059/3189/3059 4385/4632/4266 4381/4628/4262\r\nf 1841/1900/1841 2123/2192/2123 4387/4634/4268 1842/1901/1842\r\nf 2128/2197/2128 4388/4635/4269 4387/4634/4268 2123/2192/2123\r\nf 4382/4629/4263 4386/4633/4267 4387/4634/4268 4388/4635/4269\r\nf 1845/1904/1845 1842/1901/1842 4387/4634/4268 4386/4633/4267\r\nf 2140/2212/2140 4389/4636/4270 4390/4637/4271 2141/2213/2141\r\nf 4384/4631/4265 4383/4630/4264 4390/4637/4271 4389/4636/4270\r\nf 4382/4629/4263 4388/4635/4269 4390/4637/4271 4383/4630/4264\r\nf 2128/2197/2128 2141/2213/2141 4390/4637/4271 4388/4635/4269\r\nf 2134/2206/2134 4391/4638/4272 4392/4639/4273 2135/2207/2135\r\nf 4393/4640/4274 4394/4641/4275 4392/4639/4273 4391/4638/4272\r\nf 4395/4642/4276 4396/4643/4277 4392/4639/4273 4394/4641/4275\r\nf 2138/2210/2138 2135/2207/2135 4392/4639/4273 4396/4643/4277\r\nf 2349/2452/2349 4397/4644/4278 4398/4645/4279 4399/4646/4280\r\nf 4400/4647/4281 4401/4648/4282 4398/4645/4279 4397/4644/4278\r\nf 4395/4642/4276 4394/4641/4275 4398/4645/4279 4401/4648/4282\r\nf 4393/4640/4274 4399/4646/4280 4398/4645/4279 4394/4641/4275\r\nf 1873/1932/1873 4402/4649/4283 4403/4650/4284 4404/4651/4285\r\nf 4405/4652/4286 4406/4653/4287 4403/4650/4284 4402/4649/4283\r\nf 4395/4654/4276 4401/4655/4282 4403/4650/4284 4406/4653/4287\r\nf 4400/4656/4281 4404/4651/4285 4403/4650/4284 4401/4655/4282\r\nf 2140/2212/2140 2143/2215/2143 4407/4657/4288 4408/4658/4289\r\nf 2138/2217/2138 4396/4659/4277 4407/4657/4288 2143/2215/2143\r\nf 4395/4654/4276 4406/4653/4287 4407/4657/4288 4396/4659/4277\r\nf 4405/4652/4286 4408/4658/4289 4407/4657/4288 4406/4653/4287\r\nf 2134/2206/2134 2137/2209/2137 4409/4660/4290 4410/4661/4291\r\nf 2132/2201/2132 4411/4662/4292 4409/4660/4290 2137/2209/2137\r\nf 4412/4663/4293 4413/4664/4294 4409/4660/4290 4411/4662/4292\r\nf 4414/4665/4295 4410/4661/4291 4409/4660/4290 4413/4664/4294\r\nf 2117/2186/2117 2120/2189/2120 4415/4666/4296 2129/2198/2129\r\nf 2115/2184/2115 4416/4667/4297 4415/4666/4296 2120/2189/2120\r\nf 4412/4663/4293 4411/4662/4292 4415/4666/4296 4416/4667/4297\r\nf 2132/2201/2132 2129/2198/2129 4415/4666/4296 4411/4662/4292\r\nf 2111/2180/2111 4417/4668/4298 4418/4669/4299 2112/2181/2112\r\nf 4419/4670/4300 4420/4671/4301 4418/4669/4299 4417/4668/4298\r\nf 4412/4663/4293 4416/4667/4297 4418/4669/4299 4420/4671/4301\r\nf 2115/2184/2115 2112/2181/2112 4418/4669/4299 4416/4667/4297\r\nf 4421/4672/4302 4422/4673/4303 4423/4674/4304 4424/4675/4305\r\nf 4414/4665/4295 4413/4664/4294 4423/4674/4304 4422/4673/4303\r\nf 4412/4663/4293 4420/4671/4301 4423/4674/4304 4413/4664/4294\r\nf 4419/4670/4300 4424/4675/4305 4423/4674/4304 4420/4671/4301\r\nf 3065/3195/3065 4425/4676/4306 4426/4677/4307 3066/3196/3066\r\nf 4427/4678/4308 4428/4679/4309 4426/4677/4307 4425/4676/4306\r\nf 4429/4680/4310 4430/4681/4311 4426/4677/4307 4428/4679/4309\r\nf 3069/3199/3069 3066/3196/3066 4426/4677/4307 4430/4681/4311\r\nf 1867/1926/1867 4431/4682/4312 4432/4683/4313 4433/4684/4314\r\nf 4434/4685/4315 4435/4686/4316 4432/4683/4313 4431/4682/4312\r\nf 4429/4680/4310 4428/4679/4309 4432/4683/4313 4435/4686/4316\r\nf 4427/4678/4308 4433/4684/4314 4432/4683/4313 4428/4679/4309\r\nf 3787/4687/3748 4436/4688/4317 4437/4689/4318 4438/4690/4319\r\nf 4439/4691/4320 4440/4692/4321 4437/4689/4318 4436/4688/4317\r\nf 4429/4680/4310 4435/4686/4316 4437/4689/4318 4440/4692/4321\r\nf 4434/4685/4315 4438/4690/4319 4437/4689/4318 4435/4686/4316\r\nf 3071/3201/3071 3074/3204/3074 4441/4693/4322 4442/4694/4323\r\nf 3069/3199/3069 4430/4681/4311 4441/4693/4322 3074/3204/3074\r\nf 4429/4680/4310 4440/4692/4321 4441/4693/4322 4430/4681/4311\r\nf 4439/4691/4320 4442/4694/4323 4441/4693/4322 4440/4692/4321\r\nf 4421/4672/4302 4424/4675/4305 4443/4695/4324 4444/4696/4325\r\nf 4419/4670/4300 4445/4697/4326 4443/4695/4324 4424/4675/4305\r\nf 4446/4698/4327 4447/4699/4328 4443/4695/4324 4445/4697/4326\r\nf 4448/4700/4329 4444/4696/4325 4443/4695/4324 4447/4699/4328\r\nf 2111/2180/2111 3228/3358/3228 4449/4701/4330 4417/4668/4298\r\nf 3232/3362/3232 4450/4702/4331 4449/4701/4330 3228/3358/3228\r\nf 4446/4698/4327 4445/4697/4326 4449/4701/4330 4450/4702/4331\r\nf 4419/4670/4300 4417/4668/4298 4449/4701/4330 4445/4697/4326\r\nf 3243/3373/3243 4320/4567/4204 4451/4703/4332 3244/3374/3244\r\nf 4324/4571/4208 4452/4704/4333 4451/4703/4332 4320/4567/4204\r\nf 4446/4698/4327 4450/4702/4331 4451/4703/4332 4452/4704/4333\r\nf 3232/3362/3232 3244/3374/3244 4451/4703/4332 4450/4702/4331\r\nf 4335/4582/4219 4453/4705/4334 4454/4706/4335 4336/4583/4220\r\nf 4448/4700/4329 4447/4699/4328 4454/4706/4335 4453/4705/4334\r\nf 4446/4698/4327 4452/4704/4333 4454/4706/4335 4447/4699/4328\r\nf 4324/4571/4208 4336/4583/4220 4454/4706/4335 4452/4704/4333\r\nf 4455/4707/4336 4456/4708/4337 4457/4709/4338 4458/4710/4339\r\nf 4459/4711/4340 4460/4712/4341 4457/4709/4338 4456/4708/4337\r\nf 4461/4713/4342 4462/4714/4343 4457/4709/4338 4460/4712/4341\r\nf 4463/4715/4344 4458/4710/4339 4457/4709/4338 4462/4714/4343\r\nf 3757/3971/3718 3760/3974/3721 4464/4716/4345 4465/4717/4346\r\nf 3753/3966/3714 4466/4718/4347 4464/4716/4345 3760/3974/3721\r\nf 4461/4713/4342 4460/4712/4341 4464/4716/4345 4466/4718/4347\r\nf 4459/4711/4340 4465/4717/4346 4464/4716/4345 4460/4712/4341\r\nf 3049/3962/3049 3078/4719/3078 4467/4720/4348 3751/3963/3712\r\nf 3075/4721/3075 4468/4722/4349 4467/4720/4348 3078/4719/3078\r\nf 4461/4713/4342 4466/4718/4347 4467/4720/4348 4468/4722/4349\r\nf 3753/3966/3714 3751/3963/3712 4467/4720/4348 4466/4718/4347\r\nf 3071/4723/3071 4469/4724/4350 4470/4725/4351 3072/4726/3072\r\nf 4463/4715/4344 4462/4714/4343 4470/4725/4351 4469/4724/4350\r\nf 4461/4713/4342 4468/4722/4349 4470/4725/4351 4462/4714/4343\r\nf 3075/4721/3075 3072/4726/3072 4470/4725/4351 4468/4722/4349\r\nf 4329/4576/4213 4471/4727/4352 4472/4728/4353 4330/4577/4214\r\nf 4473/4729/4354 4474/4730/4355 4472/4728/4353 4471/4727/4352\r\nf 4475/4731/4356 4476/4732/4357 4472/4728/4353 4474/4730/4355\r\nf 4333/4580/4217 4330/4577/4214 4472/4728/4353 4476/4732/4357\r\nf 4477/4733/4358 4478/4734/4359 4479/4735/4360 4480/4736/4361\r\nf 4481/4737/4362 4482/4738/4363 4479/4735/4360 4478/4734/4359\r\nf 4475/4731/4356 4474/4730/4355 4479/4735/4360 4482/4738/4363\r\nf 4473/4729/4354 4480/4736/4361 4479/4735/4360 4474/4730/4355\r\nf 3359/3493/3359 4483/4739/4364 4484/4740/4365 4485/4741/4366\r\nf 4486/4742/4367 4487/4743/4368 4484/4740/4365 4483/4739/4364\r\nf 4475/4731/4356 4482/4738/4363 4484/4740/4365 4487/4743/4368\r\nf 4481/4737/4362 4485/4741/4366 4484/4740/4365 4482/4738/4363\r\nf 4335/4582/4219 4338/4585/4222 4488/4744/4369 4489/4745/4370\r\nf 4333/4580/4217 4476/4732/4357 4488/4744/4369 4338/4585/4222\r\nf 4475/4731/4356 4487/4743/4368 4488/4744/4369 4476/4732/4357\r\nf 4486/4742/4367 4489/4745/4370 4488/4744/4369 4487/4743/4368\r\nf 1867/1926/1867 4433/4684/4314 4490/4746/4371 1868/1927/1868\r\nf 4427/4678/4308 4491/4747/4372 4490/4746/4371 4433/4684/4314\r\nf 4492/4748/4373 4493/4749/4374 4490/4746/4371 4491/4747/4372\r\nf 1871/1930/1871 1868/1927/1868 4490/4746/4371 4493/4749/4374\r\nf 3065/3195/3065 4380/4627/4261 4494/4750/4375 4425/4676/4306\r\nf 4384/4631/4265 4495/4751/4376 4494/4750/4375 4380/4627/4261\r\nf 4492/4748/4373 4491/4747/4372 4494/4750/4375 4495/4751/4376\r\nf 4427/4678/4308 4425/4676/4306 4494/4750/4375 4491/4747/4372\r\nf 2140/2212/2140 4408/4658/4289 4496/4752/4377 4389/4636/4270\r\nf 4405/4652/4286 4497/4753/4378 4496/4752/4377 4408/4658/4289\r\nf 4492/4748/4373 4495/4751/4376 4496/4752/4377 4497/4753/4378\r\nf 4384/4631/4265 4389/4636/4270 4496/4752/4377 4495/4751/4376\r\nf 1873/1932/1873 1876/1935/1876 4498/4754/4379 4402/4649/4283\r\nf 1871/1930/1871 4493/4749/4374 4498/4754/4379 1876/1935/1876\r\nf 4492/4748/4373 4497/4753/4378 4498/4754/4379 4493/4749/4374\r\nf 4405/4652/4286 4402/4649/4283 4498/4754/4379 4497/4753/4378\r\nf 2349/2452/2349 4399/4646/4280 4499/4755/4380 2350/2453/2350\r\nf 4393/4640/4274 4500/4756/4381 4499/4755/4380 4399/4646/4280\r\nf 4501/4757/4382 4502/4758/4383 4499/4755/4380 4500/4756/4381\r\nf 2353/2456/2353 2350/2453/2350 4499/4755/4380 4502/4758/4383\r\nf 2134/2206/2134 4410/4661/4291 4503/4759/4384 4391/4638/4272\r\nf 4414/4665/4295 4504/4760/4385 4503/4759/4384 4410/4661/4291\r\nf 4501/4757/4382 4500/4756/4381 4503/4759/4384 4504/4760/4385\r\nf 4393/4640/4274 4391/4638/4272 4503/4759/4384 4500/4756/4381\r\nf 4421/4672/4302 4505/4761/4386 4506/4762/4387 4422/4673/4303\r\nf 4507/4763/4388 4508/4764/4389 4506/4762/4387 4505/4761/4386\r\nf 4501/4757/4382 4504/4760/4385 4506/4762/4387 4508/4764/4389\r\nf 4414/4665/4295 4422/4673/4303 4506/4762/4387 4504/4760/4385\r\nf 2355/2458/2355 2358/2461/2358 4509/4765/4390 4510/4766/4391\r\nf 2353/2456/2353 4502/4758/4383 4509/4765/4390 2358/2461/2358\r\nf 4501/4757/4382 4508/4764/4389 4509/4765/4390 4502/4758/4383\r\nf 4507/4763/4388 4510/4766/4391 4509/4765/4390 4508/4764/4389\r\nf 2355/2458/2355 4510/4766/4391 4511/4767/4392 3355/3489/3355\r\nf 4507/4763/4388 4512/4768/4393 4511/4767/4392 4510/4766/4391\r\nf 4513/4769/4394 4514/4770/4395 4511/4767/4392 4512/4768/4393\r\nf 3357/3491/3357 3355/3489/3355 4511/4767/4392 4514/4770/4395\r\nf 4421/4672/4302 4444/4696/4325 4515/4771/4396 4505/4761/4386\r\nf 4448/4700/4329 4516/4772/4397 4515/4771/4396 4444/4696/4325\r\nf 4513/4769/4394 4512/4768/4393 4515/4771/4396 4516/4772/4397\r\nf 4507/4763/4388 4505/4761/4386 4515/4771/4396 4512/4768/4393\r\nf 4335/4582/4219 4489/4745/4370 4517/4773/4398 4453/4705/4334\r\nf 4486/4742/4367 4518/4774/4399 4517/4773/4398 4489/4745/4370\r\nf 4513/4769/4394 4516/4772/4397 4517/4773/4398 4518/4774/4399\r\nf 4448/4700/4329 4453/4705/4334 4517/4773/4398 4516/4772/4397\r\nf 3359/3493/3359 3362/3496/3362 4519/4775/4400 4483/4739/4364\r\nf 3357/3491/3357 4514/4770/4395 4519/4775/4400 3362/3496/3362\r\nf 4513/4769/4394 4518/4774/4399 4519/4775/4400 4514/4770/4395\r\nf 4486/4742/4367 4483/4739/4364 4519/4775/4400 4518/4774/4399\r\nf 3781/3997/3742 4520/4776/4401 4521/4777/4402 3782/3998/3743\r\nf 4522/4778/4403 4523/4779/4404 4521/4777/4402 4520/4776/4401\r\nf 4524/4780/4405 4525/4781/4406 4521/4777/4402 4523/4779/4404\r\nf 3785/4001/3746 3782/3998/3743 4521/4777/4402 4525/4781/4406\r\nf 4455/4707/4336 4458/4710/4339 4526/4782/4407 4527/4783/4408\r\nf 4463/4715/4344 4528/4784/4409 4526/4782/4407 4458/4710/4339\r\nf 4524/4780/4405 4523/4779/4404 4526/4782/4407 4528/4784/4409\r\nf 4522/4778/4403 4527/4783/4408 4526/4782/4407 4523/4779/4404\r\nf 3071/4723/3071 4442/4785/4323 4529/4786/4410 4469/4724/4350\r\nf 4439/4787/4320 4530/4788/4411 4529/4786/4410 4442/4785/4323\r\nf 4524/4780/4405 4528/4784/4409 4529/4786/4410 4530/4788/4411\r\nf 4463/4715/4344 4469/4724/4350 4529/4786/4410 4528/4784/4409\r\nf 3787/4003/3748 3790/4006/3751 4531/4789/4412 4436/4790/4317\r\nf 3785/4001/3746 4525/4781/4406 4531/4789/4412 3790/4006/3751\r\nf 4524/4780/4405 4530/4788/4411 4531/4789/4412 4525/4781/4406\r\nf 4439/4787/4320 4436/4790/4317 4531/4789/4412 4530/4788/4411\r\nf 2349/2452/2349 2352/2455/2352 4532/4791/4413 4397/4644/4278\r\nf 2347/2450/2347 4533/4792/4414 4532/4791/4413 2352/2455/2352\r\nf 4534/4793/4415 4535/4794/4416 4532/4791/4413 4533/4792/4414\r\nf 4400/4647/4281 4397/4644/4278 4532/4791/4413 4535/4794/4416\r\nf 2086/2150/2086 2089/2153/2089 4536/4795/4417 2345/2448/2345\r\nf 2084/2158/2084 4537/4796/4418 4536/4795/4417 2089/2153/2089\r\nf 4534/4793/4415 4533/4792/4414 4536/4795/4417 4537/4796/4418\r\nf 2347/2450/2347 2345/2448/2345 4536/4795/4417 4533/4792/4414\r\nf 1822/1881/1822 1858/1917/1858 4538/4797/4419 2082/2146/2082\r\nf 1862/1921/1862 4539/4798/4420 4538/4797/4419 1858/1917/1858\r\nf 4534/4799/4415 4537/4800/4418 4538/4797/4419 4539/4798/4420\r\nf 2084/2148/2084 2082/2146/2082 4538/4797/4419 4537/4800/4418\r\nf 1873/1932/1873 4404/4651/4285 4540/4801/4421 1874/1933/1874\r\nf 4400/4656/4281 4535/4802/4416 4540/4801/4421 4404/4651/4285\r\nf 4534/4799/4415 4539/4798/4420 4540/4801/4421 4535/4802/4416\r\nf 1862/1921/1862 1874/1933/1874 4540/4801/4421 4539/4798/4420\r\nf 1867/1926/1867 1870/1929/1870 4541/4803/4422 4431/4682/4312\r\nf 1865/1924/1865 4542/4804/4423 4541/4803/4422 1870/1929/1870\r\nf 4543/4805/4424 4544/4806/4425 4541/4803/4422 4542/4804/4423\r\nf 4434/4685/4315 4431/4682/4312 4541/4803/4422 4544/4806/4425\r\nf 1816/1875/1816 3020/3150/3020 4545/4807/4426 1863/1922/1863\r\nf 3024/3154/3024 4546/4808/4427 4545/4807/4426 3020/3150/3020\r\nf 4543/4805/4424 4542/4804/4423 4545/4807/4426 4546/4808/4427\r\nf 1865/1924/1865 1863/1922/1863 4545/4807/4426 4542/4804/4423\r\nf 3035/3165/3035 3772/4809/3733 4547/4810/4428 3036/3166/3036\r\nf 3776/4811/3737 4548/4812/4429 4547/4810/4428 3772/4809/3733\r\nf 4543/4805/4424 4546/4808/4427 4547/4810/4428 4548/4812/4429\r\nf 3024/3154/3024 3036/3166/3036 4547/4810/4428 4546/4808/4427\r\nf 3787/4687/3748 4438/4690/4319 4549/4813/4430 3788/4814/3749\r\nf 4434/4685/4315 4544/4806/4425 4549/4813/4430 4438/4690/4319\r\nf 4543/4805/4424 4548/4812/4429 4549/4813/4430 4544/4806/4425\r\nf 3776/4811/3737 3788/4814/3749 4549/4813/4430 4548/4812/4429\r\nf 4477/4733/4358 4550/4815/4431 4551/4816/4432 4478/4734/4359\r\nf 4552/4817/4433 4553/4818/4434 4551/4816/4432 4550/4815/4431\r\nf 4554/4819/4435 4555/4820/4436 4551/4816/4432 4553/4818/4434\r\nf 4481/4737/4362 4478/4734/4359 4551/4816/4432 4555/4820/4436\r\nf 4305/4552/4189 4308/4555/4192 4556/4821/4437 4557/4822/4438\r\nf 4301/4548/4185 4558/4823/4439 4556/4821/4437 4308/4555/4192\r\nf 4554/4819/4435 4553/4818/4434 4556/4821/4437 4558/4823/4439\r\nf 4552/4817/4433 4557/4822/4438 4556/4821/4437 4553/4818/4434\r\nf 3341/3475/3341 3366/3500/3366 4559/4824/4440 4299/4546/4183\r\nf 3363/3497/3363 4560/4825/4441 4559/4824/4440 3366/3500/3366\r\nf 4554/4819/4435 4558/4823/4439 4559/4824/4440 4560/4825/4441\r\nf 4301/4548/4185 4299/4546/4183 4559/4824/4440 4558/4823/4439\r\nf 3359/3493/3359 4485/4741/4366 4561/4826/4442 3360/3494/3360\r\nf 4481/4737/4362 4555/4820/4436 4561/4826/4442 4485/4741/4366\r\nf 4554/4819/4435 4560/4825/4441 4561/4826/4442 4555/4820/4436\r\nf 3363/3497/3363 3360/3494/3360 4561/4826/4442 4560/4825/4441\r\nf 1355/1365/1355 1358/1368/1358 4562/4827/4443 4563/4828/4444\r\nf 1350/1360/1350 4564/4829/4445 4562/4827/4443 1358/1368/1358\r\nf 4565/4830/4446 4566/4831/4447 4562/4827/4443 4564/4829/4445\r\nf 4567/4832/4448 4563/4828/4444 4562/4827/4443 4566/4831/4447\r\nf 1074/1074/1074 1077/1077/1077 4568/4833/4449 1347/1357/1347\r\nf 1072/1072/1072 4569/4834/4450 4568/4833/4449 1077/1077/1077\r\nf 4565/4830/4446 4564/4829/4445 4568/4833/4449 4569/4834/4450\r\nf 1350/1360/1350 1347/1357/1347 4568/4833/4449 4564/4829/4445\r\nf 1068/1068/1068 4570/4835/4451 4571/4836/4452 1069/1069/1069\r\nf 4572/4837/4453 4573/4838/4454 4571/4836/4452 4570/4835/4451\r\nf 4565/4830/4446 4569/4834/4450 4571/4836/4452 4573/4838/4454\r\nf 1072/1072/1072 1069/1069/1069 4571/4836/4452 4569/4834/4450\r\nf 4574/4839/4455 4575/4840/4456 4576/4841/4457 4577/4842/4458\r\nf 4567/4832/4448 4566/4831/4447 4576/4841/4457 4575/4840/4456\r\nf 4565/4830/4446 4573/4838/4454 4576/4841/4457 4566/4831/4447\r\nf 4572/4837/4453 4577/4842/4458 4576/4841/4457 4573/4838/4454\r\nf 1355/1365/1355 4578/4843/4459 4579/4844/4460 1356/1366/1356\r\nf 4580/4845/4461 4581/4846/4462 4579/4844/4460 4578/4843/4459\r\nf 4582/4847/4463 4583/4848/4464 4579/4844/4460 4581/4846/4462\r\nf 1359/1369/1359 1356/1366/1356 4579/4844/4460 4583/4848/4464\r\nf 1084/1084/1084 4584/4849/4465 4585/4850/4466 4586/4851/4467\r\nf 4587/4852/4468 4588/4853/4469 4585/4850/4466 4584/4849/4465\r\nf 4582/4847/4463 4581/4846/4462 4585/4850/4466 4588/4853/4469\r\nf 4580/4845/4461 4586/4851/4467 4585/4850/4466 4581/4846/4462\r\nf 1582/1621/1582 4589/4854/4470 4590/4855/4471 4591/4856/4472\r\nf 4592/4857/4473 4593/4858/4474 4590/4855/4471 4589/4854/4470\r\nf 4582/4859/4463 4588/4860/4469 4590/4855/4471 4593/4858/4474\r\nf 4587/4861/4468 4591/4856/4472 4590/4855/4471 4588/4860/4469\r\nf 1361/1371/1361 1364/1374/1364 4594/4862/4475 4595/4863/4476\r\nf 1359/1379/1359 4583/4864/4464 4594/4862/4475 1364/1374/1364\r\nf 4582/4859/4463 4593/4858/4474 4594/4862/4475 4583/4864/4464\r\nf 4592/4857/4473 4595/4863/4476 4594/4862/4475 4593/4858/4474\r\nf 2877/3007/2877 2880/3010/2880 4596/4865/4477 4597/4866/4478\r\nf 2873/3003/2873 4598/4867/4479 4596/4865/4477 2880/3010/2880\r\nf 4599/4868/4480 4600/4869/4481 4596/4865/4477 4598/4867/4479\r\nf 4601/4870/4482 4597/4866/4478 4596/4865/4477 4600/4869/4481\r\nf 1337/1347/1337 1340/1350/1340 4602/4871/4483 2871/3001/2871\r\nf 1335/1345/1335 4603/4872/4484 4602/4871/4483 1340/1350/1340\r\nf 4599/4868/4480 4598/4867/4479 4602/4871/4483 4603/4872/4484\r\nf 2873/3003/2873 2871/3001/2871 4602/4871/4483 4598/4867/4479\r\nf 1331/1341/1331 1369/1382/1369 4604/4873/4485 1332/1342/1332\r\nf 1365/1375/1365 4605/4874/4486 4604/4873/4485 1369/1382/1369\r\nf 4599/4868/4480 4603/4872/4484 4604/4873/4485 4605/4874/4486\r\nf 1335/1345/1335 1332/1342/1332 4604/4873/4485 4603/4872/4484\r\nf 1361/1371/1361 4606/4875/4487 4607/4876/4488 1362/1372/1362\r\nf 4601/4870/4482 4600/4869/4481 4607/4876/4488 4606/4875/4487\r\nf 4599/4868/4480 4605/4874/4486 4607/4876/4488 4600/4869/4481\r\nf 1365/1375/1365 1362/1372/1362 4607/4876/4488 4605/4874/4486\r\nf 4574/4839/4455 4577/4842/4458 4608/4877/4489 4609/4878/4490\r\nf 4572/4837/4453 4610/4879/4491 4608/4877/4489 4577/4842/4458\r\nf 4611/4880/4492 4612/4881/4493 4608/4877/4489 4610/4879/4491\r\nf 4613/4882/4494 4609/4878/4490 4608/4877/4489 4612/4881/4493\r\nf 1068/1068/1068 2564/2694/2564 4614/4883/4495 4570/4835/4451\r\nf 2568/2698/2568 4615/4884/4496 4614/4883/4495 2564/2694/2564\r\nf 4611/4880/4492 4610/4879/4491 4614/4883/4495 4615/4884/4496\r\nf 4572/4837/4453 4570/4835/4451 4614/4883/4495 4610/4879/4491\r\nf 2579/2709/2579 3528/4885/3525 4616/4886/4497 2580/2710/2580\r\nf 3532/4887/3529 4617/4888/4498 4616/4886/4497 3528/4885/3525\r\nf 4611/4880/4492 4615/4884/4496 4616/4886/4497 4617/4888/4498\r\nf 2568/2698/2568 2580/2710/2580 4616/4886/4497 4615/4884/4496\r\nf 3543/4889/3540 4618/4890/4499 4619/4891/4500 3544/4892/3541\r\nf 4613/4882/4494 4612/4881/4493 4619/4891/4500 4618/4890/4499\r\nf 4611/4880/4492 4617/4888/4498 4619/4891/4500 4612/4881/4493\r\nf 3532/4887/3529 3544/4892/3541 4619/4891/4500 4617/4888/4498\r\nf 2877/3007/2877 4620/4893/4501 4621/4894/4502 2878/3008/2878\r\nf 4622/4895/4503 4623/4896/4504 4621/4894/4502 4620/4893/4501\r\nf 4624/4897/4505 4625/4898/4506 4621/4894/4502 4623/4896/4504\r\nf 2881/3011/2881 2878/3008/2878 4621/4894/4502 4625/4898/4506\r\nf 1576/1615/1576 4626/4899/4507 4627/4900/4508 4628/4901/4509\r\nf 4629/4902/4510 4630/4903/4511 4627/4900/4508 4626/4899/4507\r\nf 4624/4897/4505 4623/4896/4504 4627/4900/4508 4630/4903/4511\r\nf 4622/4895/4503 4628/4901/4509 4627/4900/4508 4623/4896/4504\r\nf 4095/4338/4005 4631/4904/4512 4632/4905/4513 4633/4906/4514\r\nf 4634/4907/4515 4635/4908/4516 4632/4905/4513 4631/4904/4512\r\nf 4624/4897/4505 4630/4903/4511 4632/4905/4513 4635/4908/4516\r\nf 4629/4902/4510 4633/4906/4514 4632/4905/4513 4630/4903/4511\r\nf 2883/3013/2883 2886/3016/2886 4636/4909/4517 4637/4910/4518\r\nf 2881/3011/2881 4625/4898/4506 4636/4909/4517 2886/3016/2886\r\nf 4624/4897/4505 4635/4908/4516 4636/4909/4517 4625/4898/4506\r\nf 4634/4907/4515 4637/4910/4518 4636/4909/4517 4635/4908/4516\r\nf 3537/3702/3534 4638/4911/4519 4639/4912/4520 3538/3703/3535\r\nf 4640/4913/4521 4641/4914/4522 4639/4912/4520 4638/4911/4519\r\nf 4642/4915/4523 4643/4916/4524 4639/4912/4520 4641/4914/4522\r\nf 3541/3706/3538 3538/3703/3535 4639/4912/4520 4643/4916/4524\r\nf 4644/4917/4525 4645/4918/4526 4646/4919/4527 4647/4920/4528\r\nf 4648/4921/4529 4649/4922/4530 4646/4919/4527 4645/4918/4526\r\nf 4642/4915/4523 4641/4914/4522 4646/4919/4527 4649/4922/4530\r\nf 4640/4913/4521 4647/4920/4528 4646/4919/4527 4641/4914/4522\r\nf 2591/4923/2591 4650/4924/4531 4651/4925/4532 4652/4926/4533\r\nf 4653/4927/4534 4654/4928/4535 4651/4925/4532 4650/4924/4531\r\nf 4642/4915/4523 4649/4922/4530 4651/4925/4532 4654/4928/4535\r\nf 4648/4921/4529 4652/4926/4533 4651/4925/4532 4649/4922/4530\r\nf 3543/3708/3540 3546/3711/3543 4655/4929/4536 4656/4930/4537\r\nf 3541/3706/3538 4643/4916/4524 4655/4929/4536 3546/3711/3543\r\nf 4642/4915/4523 4654/4928/4535 4655/4929/4536 4643/4916/4524\r\nf 4653/4927/4534 4656/4930/4537 4655/4929/4536 4654/4928/4535\r\nf 4657/4931/4538 4658/4932/4539 4659/4933/4540 4660/4934/4541\r\nf 4661/4935/4542 4662/4936/4543 4659/4933/4540 4658/4932/4539\r\nf 4663/4937/4544 4664/4938/4545 4659/4933/4540 4662/4936/4543\r\nf 4665/4939/4546 4660/4934/4541 4659/4933/4540 4664/4938/4545\r\nf 3957/4200/3888 3960/4203/3891 4666/4940/4547 4667/4941/4548\r\nf 3953/4196/3884 4668/4942/4549 4666/4940/4547 3960/4203/3891\r\nf 4663/4937/4544 4662/4936/4543 4666/4940/4547 4668/4942/4549\r\nf 4661/4935/4542 4667/4941/4548 4666/4940/4547 4662/4936/4543\r\nf 2757/2887/2757 2890/3020/2890 4669/4943/4550 3951/4194/3882\r\nf 2887/3017/2887 4670/4944/4551 4669/4943/4550 2890/3020/2890\r\nf 4663/4937/4544 4668/4942/4549 4669/4943/4550 4670/4944/4551\r\nf 3953/4196/3884 3951/4194/3882 4669/4943/4550 4668/4942/4549\r\nf 2883/3013/2883 4671/4945/4552 4672/4946/4553 2884/3014/2884\r\nf 4665/4939/4546 4664/4938/4545 4672/4946/4553 4671/4945/4552\r\nf 4663/4937/4544 4670/4944/4551 4672/4946/4553 4664/4938/4545\r\nf 2887/3017/2887 2884/3014/2884 4672/4946/4553 4670/4944/4551\r\nf 1084/1084/1084 4586/4851/4467 4673/4947/4554 1085/1085/1085\r\nf 4580/4845/4461 4674/4948/4555 4673/4947/4554 4586/4851/4467\r\nf 4675/4949/4556 4676/4950/4557 4673/4947/4554 4674/4948/4555\r\nf 1088/1088/1088 1085/1085/1085 4673/4947/4554 4676/4950/4557\r\nf 1355/1365/1355 4563/4828/4444 4677/4951/4558 4578/4843/4459\r\nf 4567/4832/4448 4678/4952/4559 4677/4951/4558 4563/4828/4444\r\nf 4675/4949/4556 4674/4948/4555 4677/4951/4558 4678/4952/4559\r\nf 4580/4845/4461 4578/4843/4459 4677/4951/4558 4674/4948/4555\r\nf 4574/4839/4455 4679/4953/4560 4680/4954/4561 4575/4840/4456\r\nf 4681/4955/4562 4682/4956/4563 4680/4954/4561 4679/4953/4560\r\nf 4675/4949/4556 4678/4952/4559 4680/4954/4561 4682/4956/4563\r\nf 4567/4832/4448 4575/4840/4456 4680/4954/4561 4678/4952/4559\r\nf 1090/1090/1090 1093/1093/1093 4683/4957/4564 4684/4958/4565\r\nf 1088/1088/1088 4676/4950/4557 4683/4957/4564 1093/1093/1093\r\nf 4675/4949/4556 4682/4956/4563 4683/4957/4564 4676/4950/4557\r\nf 4681/4955/4562 4684/4958/4565 4683/4957/4564 4682/4956/4563\r\nf 1576/1615/1576 4628/4901/4509 4685/4959/4566 1577/1616/1577\r\nf 4622/4895/4503 4686/4960/4567 4685/4959/4566 4628/4901/4509\r\nf 4687/4961/4568 4688/4962/4569 4685/4959/4566 4686/4960/4567\r\nf 1580/1619/1580 1577/1616/1577 4685/4959/4566 4688/4962/4569\r\nf 2877/3007/2877 4597/4866/4478 4689/4963/4570 4620/4893/4501\r\nf 4601/4870/4482 4690/4964/4571 4689/4963/4570 4597/4866/4478\r\nf 4687/4961/4568 4686/4960/4567 4689/4963/4570 4690/4964/4571\r\nf 4622/4895/4503 4620/4893/4501 4689/4963/4570 4686/4960/4567\r\nf 1361/1371/1361 4595/4863/4476 4691/4965/4572 4606/4875/4487\r\nf 4592/4857/4473 4692/4966/4573 4691/4965/4572 4595/4863/4476\r\nf 4687/4961/4568 4690/4964/4571 4691/4965/4572 4692/4966/4573\r\nf 4601/4870/4482 4606/4875/4487 4691/4965/4572 4690/4964/4571\r\nf 1582/1621/1582 1585/1624/1585 4693/4967/4574 4589/4854/4470\r\nf 1580/1619/1580 4688/4962/4569 4693/4967/4574 1585/1624/1585\r\nf 4687/4961/4568 4692/4966/4573 4693/4967/4574 4688/4962/4569\r\nf 4592/4857/4473 4589/4854/4470 4693/4967/4574 4692/4966/4573\r\nf 1090/1090/1090 4684/4958/4565 4694/4968/4575 2587/2717/2587\r\nf 4681/4955/4562 4695/4969/4576 4694/4968/4575 4684/4958/4565\r\nf 4696/4970/4577 4697/4971/4578 4694/4968/4575 4695/4969/4576\r\nf 2589/2719/2589 2587/2717/2587 4694/4968/4575 4697/4971/4578\r\nf 4574/4839/4455 4609/4878/4490 4698/4972/4579 4679/4953/4560\r\nf 4613/4882/4494 4699/4973/4580 4698/4972/4579 4609/4878/4490\r\nf 4696/4970/4577 4695/4969/4576 4698/4972/4579 4699/4973/4580\r\nf 4681/4955/4562 4679/4953/4560 4698/4972/4579 4695/4969/4576\r\nf 3543/4889/3540 4656/4974/4537 4700/4975/4581 4618/4890/4499\r\nf 4653/4976/4534 4701/4977/4582 4700/4975/4581 4656/4974/4537\r\nf 4696/4970/4577 4699/4973/4580 4700/4975/4581 4701/4977/4582\r\nf 4613/4882/4494 4618/4890/4499 4700/4975/4581 4699/4973/4580\r\nf 2591/2721/2591 2594/2724/2594 4702/4978/4583 4650/4979/4531\r\nf 2589/2719/2589 4697/4971/4578 4702/4978/4583 2594/2724/2594\r\nf 4696/4970/4577 4701/4977/4582 4702/4978/4583 4697/4971/4578\r\nf 4653/4976/4534 4650/4979/4531 4702/4978/4583 4701/4977/4582\r\nf 4089/4332/3999 4703/4980/4584 4704/4981/4585 4090/4333/4000\r\nf 4705/4982/4586 4706/4983/4587 4704/4981/4585 4703/4980/4584\r\nf 4707/4984/4588 4708/4985/4589 4704/4981/4585 4706/4983/4587\r\nf 4093/4336/4003 4090/4333/4000 4704/4981/4585 4708/4985/4589\r\nf 4657/4931/4538 4660/4934/4541 4709/4986/4590 4710/4987/4591\r\nf 4665/4939/4546 4711/4988/4592 4709/4986/4590 4660/4934/4541\r\nf 4707/4984/4588 4706/4983/4587 4709/4986/4590 4711/4988/4592\r\nf 4705/4982/4586 4710/4987/4591 4709/4986/4590 4706/4983/4587\r\nf 2883/3013/2883 4637/4910/4518 4712/4989/4593 4671/4945/4552\r\nf 4634/4907/4515 4713/4990/4594 4712/4989/4593 4637/4910/4518\r\nf 4707/4984/4588 4711/4988/4592 4712/4989/4593 4713/4990/4594\r\nf 4665/4939/4546 4671/4945/4552 4712/4989/4593 4711/4988/4592\r\nf 4095/4338/4005 4098/4341/4008 4714/4991/4595 4631/4904/4512\r\nf 4093/4336/4003 4708/4985/4589 4714/4991/4595 4098/4341/4008\r\nf 4707/4984/4588 4713/4990/4594 4714/4991/4595 4708/4985/4589\r\nf 4634/4907/4515 4631/4904/4512 4714/4991/4595 4713/4990/4594\r\nf 1084/1084/1084 1087/1087/1087 4715/4992/4596 4584/4849/4465\r\nf 1080/1080/1080 4716/4993/4597 4715/4992/4596 1087/1087/1087\r\nf 4717/4994/4598 4718/4995/4599 4715/4992/4596 4716/4993/4597\r\nf 4587/4852/4468 4584/4849/4465 4715/4992/4596 4718/4995/4599\r\nf 1043/1043/1043 1321/1329/1321 4719/4996/4600 1078/1078/1078\r\nf 1318/1331/1318 4720/4997/4601 4719/4996/4600 1321/1329/1321\r\nf 4717/4994/4598 4716/4993/4597 4719/4996/4600 4720/4997/4601\r\nf 1080/1080/1080 1078/1078/1078 4719/4996/4600 4716/4993/4597\r\nf 1314/1322/1314 1589/1628/1589 4721/4998/4602 1315/1323/1315\r\nf 1586/1625/1586 4722/4999/4603 4721/4998/4602 1589/1628/1589\r\nf 4717/5000/4598 4720/5001/4601 4721/4998/4602 4722/4999/4603\r\nf 1318/1326/1318 1315/1323/1315 4721/4998/4602 4720/5001/4601\r\nf 1582/1621/1582 4591/4856/4472 4723/5002/4604 1583/1622/1583\r\nf 4587/4861/4468 4718/5003/4599 4723/5002/4604 4591/4856/4472\r\nf 4717/5000/4598 4722/4999/4603 4723/5002/4604 4718/5003/4599\r\nf 1586/1625/1586 1583/1622/1583 4723/5002/4604 4722/4999/4603\r\nf 1576/1615/1576 1579/1618/1579 4724/5004/4605 4626/4899/4507\r\nf 1572/1611/1572 4725/5005/4606 4724/5004/4605 1579/1618/1579\r\nf 4726/5006/4607 4727/5007/4608 4724/5004/4605 4725/5005/4606\r\nf 4629/4902/4510 4626/4899/4507 4724/5004/4605 4727/5007/4608\r\nf 1547/1582/1547 2852/2982/2852 4728/5008/4609 1570/1609/1570\r\nf 2856/2986/2856 4729/5009/4610 4728/5008/4609 2852/2982/2852\r\nf 4726/5006/4607 4725/5005/4606 4728/5008/4609 4729/5009/4610\r\nf 1572/1611/1572 1570/1609/1570 4728/5008/4609 4725/5005/4606\r\nf 2867/2997/2867 4080/4323/3990 4730/5010/4611 2868/2998/2868\r\nf 4084/4327/3994 4731/5011/4612 4730/5010/4611 4080/4323/3990\r\nf 4726/5006/4607 4729/5009/4610 4730/5010/4611 4731/5011/4612\r\nf 2856/2986/2856 2868/2998/2868 4730/5010/4611 4729/5009/4610\r\nf 4095/4338/4005 4633/4906/4514 4732/5012/4613 4096/4339/4006\r\nf 4629/4902/4510 4727/5007/4608 4732/5012/4613 4633/4906/4514\r\nf 4726/5006/4607 4731/5011/4612 4732/5012/4613 4727/5007/4608\r\nf 4084/4327/3994 4096/4339/4006 4732/5012/4613 4731/5011/4612\r\nf 4644/4917/4525 4733/5013/4614 4734/5014/4615 4645/4918/4526\r\nf 4735/5015/4616 4736/5016/4617 4734/5014/4615 4733/5013/4614\r\nf 4737/5017/4618 4738/5018/4619 4734/5014/4615 4736/5016/4617\r\nf 4648/4921/4529 4645/4918/4526 4734/5014/4615 4738/5018/4619\r\nf 3493/3651/3491 3496/3654/3494 4739/5019/4620 4740/5020/4621\r\nf 3489/3646/3488 4741/5021/4622 4739/5019/4620 3496/3654/3494\r\nf 4737/5017/4618 4736/5016/4617 4739/5019/4620 4741/5021/4622\r\nf 4735/5015/4616 4740/5020/4621 4739/5019/4620 4736/5016/4617\r\nf 2553/3642/2553 2598/5022/2598 4742/5023/4623 3487/3643/3486\r\nf 2595/5024/2595 4743/5025/4624 4742/5023/4623 2598/5022/2598\r\nf 4737/5017/4618 4741/5021/4622 4742/5023/4623 4743/5025/4624\r\nf 3489/3646/3488 3487/3643/3486 4742/5023/4623 4741/5021/4622\r\nf 2591/4923/2591 4652/4926/4533 4744/5026/4625 2592/5027/2592\r\nf 4648/4921/4529 4738/5018/4619 4744/5026/4625 4652/4926/4533\r\nf 4737/5017/4618 4743/5025/4624 4744/5026/4625 4738/5018/4619\r\nf 2595/5024/2595 2592/5027/2592 4744/5026/4625 4743/5025/4624\r\nf 1947/2006/1947 4745/5028/4626 4746/5029/4627 1948/2007/1948\r\nf 4747/5030/4628 4748/5031/4629 4746/5029/4627 4745/5028/4626\r\nf 4749/5032/4630 4750/5033/4631 4746/5029/4627 4748/5031/4629\r\nf 1951/2010/1951 1948/2007/1948 4746/5029/4627 4750/5033/4631\r\nf 3129/3259/3129 4751/5034/4632 4752/5035/4633 4753/5036/4634\r\nf 4754/5037/4635 4755/5038/4636 4752/5035/4633 4751/5034/4632\r\nf 4749/5032/4630 4748/5031/4629 4752/5035/4633 4755/5038/4636\r\nf 4747/5030/4628 4753/5036/4634 4752/5035/4633 4748/5031/4629\r\nf 2404/2510/2404 4756/5039/4637 4757/5040/4638 4758/5041/4639\r\nf 4759/5042/4640 4760/5043/4641 4757/5040/4638 4756/5039/4637\r\nf 4749/5032/4630 4755/5038/4636 4757/5040/4638 4760/5043/4641\r\nf 4754/5037/4635 4758/5041/4639 4757/5040/4638 4755/5038/4636\r\nf 1953/2012/1953 1956/2015/1956 4761/5044/4642 4762/5045/4643\r\nf 1951/2010/1951 4750/5033/4631 4761/5044/4642 1956/2015/1956\r\nf 4749/5032/4630 4760/5043/4641 4761/5044/4642 4750/5033/4631\r\nf 4759/5042/4640 4762/5045/4643 4761/5044/4642 4760/5043/4641\r\nf 3275/3405/3275 3278/3408/3278 4763/5046/4644 4764/5047/4645\r\nf 3270/3400/3270 4765/5048/4646 4763/5046/4644 3278/3408/3278\r\nf 4766/5049/4647 4767/5050/4648 4763/5046/4644 4765/5048/4646\r\nf 4768/5051/4649 4764/5047/4645 4763/5046/4644 4767/5050/4648\r\nf 2219/2301/2219 2222/2304/2222 4769/5052/4650 3267/3397/3267\r\nf 2227/2309/2227 4770/5053/4651 4769/5052/4650 2222/2304/2222\r\nf 4766/5049/4647 4765/5048/4646 4769/5052/4650 4770/5053/4651\r\nf 3270/3400/3270 3267/3397/3267 4769/5052/4650 4765/5048/4646\r\nf 2240/2322/2240 4771/5054/4652 4772/5055/4653 2241/2323/2241\r\nf 4773/5056/4654 4774/5057/4655 4772/5055/4653 4771/5054/4652\r\nf 4766/5049/4647 4770/5053/4651 4772/5055/4653 4774/5057/4655\r\nf 2227/2309/2227 2241/2323/2241 4772/5055/4653 4770/5053/4651\r\nf 4775/5058/4656 4776/5059/4657 4777/5060/4658 4778/5061/4659\r\nf 4768/5051/4649 4767/5050/4648 4777/5060/4658 4776/5059/4657\r\nf 4766/5049/4647 4774/5057/4655 4777/5060/4658 4767/5050/4648\r\nf 4773/5056/4654 4778/5061/4659 4777/5060/4658 4774/5057/4655\r\nf 4775/5058/4656 4778/5061/4659 4779/5062/4660 4780/5063/4661\r\nf 4773/5056/4654 4781/5064/4662 4779/5062/4660 4778/5061/4659\r\nf 4782/5065/4663 4783/5066/4664 4779/5062/4660 4781/5064/4662\r\nf 4784/5067/4665 4780/5063/4661 4779/5062/4660 4783/5066/4664\r\nf 2240/2322/2240 2413/2523/2413 4785/5068/4666 4771/5054/4652\r\nf 2417/2527/2417 4786/5069/4667 4785/5068/4666 2413/2523/2413\r\nf 4782/5065/4663 4781/5064/4662 4785/5068/4666 4786/5069/4667\r\nf 4773/5056/4654 4771/5054/4652 4785/5068/4666 4781/5064/4662\r\nf 1932/1991/1932 1935/1994/1935 4787/5070/4668 2425/2538/2425\r\nf 1940/1999/1940 4788/5071/4669 4787/5070/4668 1935/1994/1935\r\nf 4782/5072/4663 4786/5073/4667 4787/5070/4668 4788/5071/4669\r\nf 2417/2540/2417 2425/2538/2425 4787/5070/4668 4786/5073/4667\r\nf 1953/2012/1953 4789/5074/4670 4790/5075/4671 1954/2013/1954\r\nf 4784/5076/4665 4783/5077/4664 4790/5075/4671 4789/5074/4670\r\nf 4782/5072/4663 4788/5071/4669 4790/5075/4671 4783/5077/4664\r\nf 1940/1999/1940 1954/2013/1954 4790/5075/4671 4788/5071/4669\r\nf 1947/2006/1947 1950/2009/1950 4791/5078/4672 4792/5079/4673\r\nf 1945/2004/1945 4793/5080/4674 4791/5078/4672 1950/2009/1950\r\nf 4794/5081/4675 4795/5082/4676 4791/5078/4672 4793/5080/4674\r\nf 4796/5083/4677 4792/5079/4673 4791/5078/4672 4795/5082/4676\r\nf 1941/2000/1941 3144/3274/3144 4797/5084/4678 1942/2001/1942\r\nf 3148/3278/3148 4798/5085/4679 4797/5084/4678 3144/3274/3144\r\nf 4794/5081/4675 4793/5080/4674 4797/5084/4678 4798/5085/4679\r\nf 1945/2004/1945 1942/2001/1942 4797/5084/4678 4793/5080/4674\r\nf 3159/3289/3159 3837/5086/3784 4799/5087/4680 3160/3290/3160\r\nf 3842/5088/3789 4800/5089/4681 4799/5087/4680 3837/5086/3784\r\nf 4794/5081/4675 4798/5085/4679 4799/5087/4680 4800/5089/4681\r\nf 3148/3278/3148 3160/3290/3160 4799/5087/4680 4798/5085/4679\r\nf 3855/5090/3796 4801/5091/4682 4802/5092/4683 3856/5093/3798\r\nf 4796/5083/4677 4795/5082/4676 4802/5092/4683 4801/5091/4682\r\nf 4794/5081/4675 4800/5089/4681 4802/5092/4683 4795/5082/4676\r\nf 3842/5088/3789 3856/5093/3798 4802/5092/4683 4800/5089/4681\r\nf 3275/3405/3275 4803/5094/4684 4804/5095/4685 3276/3406/3276\r\nf 4805/5096/4686 4806/5097/4687 4804/5095/4685 4803/5094/4684\r\nf 4807/5098/4688 4808/5099/4689 4804/5095/4685 4806/5097/4687\r\nf 3279/3409/3279 3276/3406/3276 4804/5095/4685 4808/5099/4689\r\nf 2205/2287/2205 4809/5100/4690 4810/5101/4691 4811/5102/4692\r\nf 4812/5103/4693 4813/5104/4694 4810/5101/4691 4809/5100/4690\r\nf 4807/5098/4688 4806/5097/4687 4810/5101/4691 4813/5104/4694\r\nf 4805/5096/4686 4811/5102/4692 4810/5101/4691 4806/5097/4687\r\nf 4251/4494/4145 4814/5105/4695 4815/5106/4696 4816/5107/4697\r\nf 4817/5108/4698 4818/5109/4699 4815/5106/4696 4814/5105/4695\r\nf 4807/5098/4688 4813/5104/4694 4815/5106/4696 4818/5109/4699\r\nf 4812/5103/4693 4816/5107/4697 4815/5106/4696 4813/5104/4694\r\nf 3281/3411/3281 3284/3414/3284 4819/5110/4700 4820/5111/4701\r\nf 3279/3409/3279 4808/5099/4689 4819/5110/4700 3284/3414/3284\r\nf 4807/5098/4688 4818/5109/4699 4819/5110/4700 4808/5099/4689\r\nf 4817/5108/4698 4820/5111/4701 4819/5110/4700 4818/5109/4699\r\nf 3849/4077/3796 4821/5112/4702 4822/5113/4703 3850/4078/3796\r\nf 4823/5114/4704 4824/5115/4705 4822/5113/4703 4821/5112/4702\r\nf 4825/5116/4706 4826/5117/4703 4822/5113/4703 4824/5115/4705\r\nf 3853/4081/3796 3850/4078/3796 4822/5113/4703 4826/5117/4703\r\nf 4827/5118/4707 4828/5119/4708 4829/5120/4709 4830/5121/4709\r\nf 4831/5122/4708 4832/5123/4710 4829/5120/4709 4828/5119/4708\r\nf 4825/5116/4706 4824/5115/4705 4829/5120/4709 4832/5123/4710\r\nf 4823/5114/4704 4830/5121/4709 4829/5120/4709 4824/5115/4705\r\nf 3135/5124/3135 4833/5125/4710 4834/5126/4710 4835/5127/4711\r\nf 4836/5128/4712 4837/5129/4712 4834/5126/4710 4833/5125/4710\r\nf 4825/5116/4706 4832/5123/4710 4834/5126/4710 4837/5129/4712\r\nf 4831/5122/4708 4835/5127/4711 4834/5126/4710 4832/5123/4710\r\nf 3855/4083/3796 3858/4086/3796 4838/5130/4703 4839/5131/4703\r\nf 3853/4081/3796 4826/5117/4703 4838/5130/4703 3858/4086/3796\r\nf 4825/5116/4706 4837/5129/4712 4838/5130/4703 4826/5117/4703\r\nf 4836/5128/4712 4839/5131/4703 4838/5130/4703 4837/5129/4712\r\nf 4840/5132/3281 4841/5133/4713 4842/5134/3282 4843/5135/3281\r\nf 4844/5136/4714 4845/5137/4715 4842/5134/3282 4841/5133/4713\r\nf 4846/5138/4716 4847/5139/3282 4842/5134/3282 4845/5137/4715\r\nf 4848/5140/3281 4843/5135/3281 4842/5134/3282 4847/5139/3282\r\nf 4261/4504/4153 4264/4507/4156 4849/5141/4717 4850/5142/4718\r\nf 4257/4500/4149 4851/5143/4719 4849/5141/4717 4264/4507/4156\r\nf 4846/5138/4716 4845/5137/4715 4849/5141/4717 4851/5143/4719\r\nf 4844/5136/4714 4850/5142/4718 4849/5141/4717 4845/5137/4715\r\nf 3287/3417/3287 3290/3420/3290 4852/5144/4720 4255/4498/4147\r\nf 3285/3415/3285 4853/5145/4716 4852/5144/4720 3290/3420/3290\r\nf 4846/5138/4716 4851/5143/4719 4852/5144/4720 4853/5145/4716\r\nf 4257/4500/4149 4255/4498/4147 4852/5144/4720 4851/5143/4719\r\nf 3281/3411/3281 4854/5146/3281 4855/5147/3282 3282/3412/3282\r\nf 4848/5140/3281 4847/5139/3282 4855/5147/3282 4854/5146/3281\r\nf 4846/5138/4716 4853/5145/4716 4855/5147/3282 4847/5139/3282\r\nf 3285/3415/3285 3282/3412/3282 4855/5147/3282 4853/5145/4716\r\nf 2205/2287/2205 4811/5102/4692 4856/5148/4721 2206/2288/2206\r\nf 4805/5096/4686 4857/5149/4722 4856/5148/4721 4811/5102/4692\r\nf 4858/5150/4723 4859/5151/4724 4856/5148/4721 4857/5149/4722\r\nf 2209/2291/2209 2206/2288/2206 4856/5148/4721 4859/5151/4724\r\nf 3275/3405/3275 4764/5047/4645 4860/5152/4725 4803/5094/4684\r\nf 4768/5051/4649 4861/5153/4726 4860/5152/4725 4764/5047/4645\r\nf 4858/5150/4723 4857/5149/4722 4860/5152/4725 4861/5153/4726\r\nf 4805/5096/4686 4803/5094/4684 4860/5152/4725 4857/5149/4722\r\nf 4775/5058/4656 4862/5154/4727 4863/5155/4728 4776/5059/4657\r\nf 4864/5156/4729 4865/5157/4730 4863/5155/4728 4862/5154/4727\r\nf 4858/5150/4723 4861/5153/4726 4863/5155/4728 4865/5157/4730\r\nf 4768/5051/4649 4776/5059/4657 4863/5155/4728 4861/5153/4726\r\nf 2211/2293/2211 2214/2296/2214 4866/5158/4731 4867/5159/4732\r\nf 2209/2291/2209 4859/5151/4724 4866/5158/4731 2214/2296/2214\r\nf 4858/5150/4723 4865/5157/4730 4866/5158/4731 4859/5151/4724\r\nf 4864/5156/4729 4867/5159/4732 4866/5158/4731 4865/5157/4730\r\nf 2211/2293/2211 4867/5159/4732 4868/5160/4733 2400/2506/2400\r\nf 4864/5156/4729 4869/5161/4734 4868/5160/4733 4867/5159/4732\r\nf 4870/5162/4735 4871/5163/4736 4868/5160/4733 4869/5161/4734\r\nf 2402/2508/2402 2400/2506/2400 4868/5160/4733 4871/5163/4736\r\nf 4775/5058/4656 4780/5063/4661 4872/5164/4737 4862/5154/4727\r\nf 4784/5067/4665 4873/5165/4738 4872/5164/4737 4780/5063/4661\r\nf 4870/5162/4735 4869/5161/4734 4872/5164/4737 4873/5165/4738\r\nf 4864/5156/4729 4862/5154/4727 4872/5164/4737 4869/5161/4734\r\nf 1953/2012/1953 4762/5045/4643 4874/5166/4739 4789/5074/4670\r\nf 4759/5042/4640 4875/5167/4740 4874/5166/4739 4762/5045/4643\r\nf 4870/5168/4735 4873/5169/4738 4874/5166/4739 4875/5167/4740\r\nf 4784/5076/4665 4789/5074/4670 4874/5166/4739 4873/5169/4738\r\nf 2404/2510/2404 2407/2513/2407 4876/5170/4741 4756/5039/4637\r\nf 2402/2518/2402 4871/5171/4736 4876/5170/4741 2407/2513/2407\r\nf 4870/5168/4735 4875/5167/4740 4876/5170/4741 4871/5171/4736\r\nf 4759/5042/4640 4756/5039/4637 4876/5170/4741 4875/5167/4740\r\nf 3129/3259/3129 4753/5036/4634 4877/5172/4742 3130/3260/3130\r\nf 4747/5030/4628 4878/5173/4743 4877/5172/4742 4753/5036/4634\r\nf 4879/5174/4744 4880/5175/4745 4877/5172/4742 4878/5173/4743\r\nf 3133/3263/3133 3130/3260/3130 4877/5172/4742 4880/5175/4745\r\nf 1947/2006/1947 4792/5079/4673 4881/5176/4746 4745/5028/4626\r\nf 4796/5083/4677 4882/5177/4747 4881/5176/4746 4792/5079/4673\r\nf 4879/5174/4744 4878/5173/4743 4881/5176/4746 4882/5177/4747\r\nf 4747/5030/4628 4745/5028/4626 4881/5176/4746 4878/5173/4743\r\nf 3855/5090/3796 4839/5178/4703 4883/5179/4748 4801/5091/4682\r\nf 4836/5180/4712 4884/5181/4749 4883/5179/4748 4839/5178/4703\r\nf 4879/5174/4744 4882/5177/4747 4883/5179/4748 4884/5181/4749\r\nf 4796/5083/4677 4801/5091/4682 4883/5179/4748 4882/5177/4747\r\nf 3135/3265/3135 3138/3268/3138 4885/5182/4750 4833/5183/4710\r\nf 3133/3263/3133 4880/5175/4745 4885/5182/4750 3138/3268/3138\r\nf 4879/5174/4744 4884/5181/4749 4885/5182/4750 4880/5175/4745\r\nf 4836/5180/4712 4833/5183/4710 4885/5182/4750 4884/5181/4749\r\nf 4245/4488/4140 4886/5184/4751 4887/5185/4751 4246/4489/4141\r\nf 4888/5186/4698 4889/5187/4752 4887/5185/4751 4886/5184/4751\r\nf 4890/5188/4752 4891/5189/4751 4887/5185/4751 4889/5187/4752\r\nf 4249/4492/4144 4246/4489/4141 4887/5185/4751 4891/5189/4751\r\nf 4840/5132/3281 4843/5135/3281 4892/5190/4753 4893/5191/4701\r\nf 4848/5140/3281 4894/5192/4754 4892/5190/4753 4843/5135/3281\r\nf 4890/5188/4752 4889/5187/4752 4892/5190/4753 4894/5192/4754\r\nf 4888/5186/4698 4893/5191/4701 4892/5190/4753 4889/5187/4752\r\nf 3281/3411/3281 4820/5111/4701 4895/5193/4701 4854/5146/3281\r\nf 4817/5108/4698 4896/5194/4698 4895/5193/4701 4820/5111/4701\r\nf 4890/5188/4752 4894/5192/4754 4895/5193/4701 4896/5194/4698\r\nf 4848/5140/3281 4854/5146/3281 4895/5193/4701 4894/5192/4754\r\nf 4251/4494/4145 4254/4497/4146 4897/5195/4755 4814/5105/4695\r\nf 4249/4492/4144 4891/5189/4751 4897/5195/4755 4254/4497/4146\r\nf 4890/5188/4752 4896/5194/4698 4897/5195/4755 4891/5189/4751\r\nf 4817/5108/4698 4814/5105/4695 4897/5195/4755 4896/5194/4698\r\nf 3129/3259/3129 3132/3262/3132 4898/5196/4756 4751/5034/4632\r\nf 3125/3255/3125 4899/5197/4757 4898/5196/4756 3132/3262/3132\r\nf 4900/5198/4758 4901/5199/4759 4898/5196/4756 4899/5197/4757\r\nf 4754/5037/4635 4751/5034/4632 4898/5196/4756 4901/5199/4759\r\nf 1737/1796/1737 1740/1799/1740 4902/5200/4760 3123/3253/3123\r\nf 1735/1794/1735 4903/5201/4761 4902/5200/4760 1740/1799/1740\r\nf 4900/5198/4758 4899/5197/4757 4902/5200/4760 4903/5201/4761\r\nf 3125/3255/3125 3123/3253/3123 4902/5200/4760 4899/5197/4757\r\nf 1731/1790/1731 2411/2520/2411 4904/5202/4762 1732/1791/1732\r\nf 2408/2514/2408 4905/5203/4763 4904/5202/4762 2411/2520/2411\r\nf 4900/5198/4758 4903/5201/4761 4904/5202/4762 4905/5203/4763\r\nf 1735/1794/1735 1732/1791/1732 4904/5202/4762 4903/5201/4761\r\nf 2404/2510/2404 4758/5041/4639 4906/5204/4764 2405/2511/2405\r\nf 4754/5037/4635 4901/5199/4759 4906/5204/4764 4758/5041/4639\r\nf 4900/5198/4758 4905/5203/4763 4906/5204/4764 4901/5199/4759\r\nf 2408/2514/2408 2405/2511/2405 4906/5204/4764 4905/5203/4763\r\nf 2205/2287/2205 2208/2290/2208 4907/5205/4765 4809/5100/4690\r\nf 2201/2283/2201 4908/5206/4766 4907/5205/4765 2208/2290/2208\r\nf 4909/5207/4767 4910/5208/4768 4907/5205/4765 4908/5206/4766\r\nf 4812/5103/4693 4809/5100/4690 4907/5205/4765 4910/5208/4768\r\nf 2001/2060/2001 3176/3306/3176 4911/5209/4769 2199/2281/2199\r\nf 3180/3310/3180 4912/5210/4770 4911/5209/4769 3176/3306/3176\r\nf 4909/5207/4767 4908/5206/4766 4911/5209/4769 4912/5210/4770\r\nf 2201/2283/2201 2199/2281/2199 4911/5209/4769 4908/5206/4766\r\nf 3191/3321/3191 4236/4479/4133 4913/5211/4771 3192/3322/3192\r\nf 4240/4483/4137 4914/5212/4772 4913/5211/4771 4236/4479/4133\r\nf 4909/5207/4767 4912/5210/4770 4913/5211/4771 4914/5212/4772\r\nf 3180/3310/3180 3192/3322/3192 4913/5211/4771 4912/5210/4770\r\nf 4251/4494/4145 4816/5107/4697 4915/5213/4773 4252/4495/4142\r\nf 4812/5103/4693 4910/5208/4768 4915/5213/4773 4816/5107/4697\r\nf 4909/5207/4767 4914/5212/4772 4915/5213/4773 4910/5208/4768\r\nf 4240/4483/4137 4252/4495/4142 4915/5213/4773 4914/5212/4772\r\nf 4827/5118/4707 4916/5214/4774 4917/5215/4775 4828/5119/4708\r\nf 4918/5216/4776 4919/5217/4777 4917/5215/4775 4916/5214/4774\r\nf 4920/5218/4778 4921/5219/4775 4917/5215/4775 4919/5217/4777\r\nf 4831/5122/4708 4828/5119/4708 4917/5215/4775 4921/5219/4775\r\nf 3669/3861/3632 3672/3864/3635 4922/5220/4779 4923/5221/4780\r\nf 3665/3856/3628 4924/5222/4781 4922/5220/4779 3672/3864/3635\r\nf 4920/5218/4778 4919/5217/4777 4922/5220/4779 4924/5222/4781\r\nf 4918/5216/4776 4923/5221/4780 4922/5220/4779 4919/5217/4777\r\nf 2961/3852/2961 3142/5223/3142 4925/5224/4782 3663/3853/3626\r\nf 3139/5225/3139 4926/5226/4783 4925/5224/4782 3142/5223/3142\r\nf 4920/5218/4778 4924/5222/4781 4925/5224/4782 4926/5226/4783\r\nf 3665/3856/3628 3663/3853/3626 4925/5224/4782 4924/5222/4781\r\nf 3135/5124/3135 4835/5127/4711 4927/5227/4775 3136/5228/3136\r\nf 4831/5122/4708 4921/5219/4775 4927/5227/4775 4835/5127/4711\r\nf 4920/5218/4778 4926/5226/4783 4927/5227/4775 4921/5219/4775\r\nf 3139/5225/3139 3136/5228/3136 4927/5227/4775 4926/5226/4783\r\nf 1162/1162/1162 4928/5229/4784 4929/5230/4785 1163/1163/1163\r\nf 4930/5231/4786 4931/5232/4787 4929/5230/4785 4928/5229/4784\r\nf 4932/5233/4788 4933/5234/4789 4929/5230/4785 4931/5232/4787\r\nf 1166/1166/1166 1163/1163/1163 4929/5230/4785 4933/5234/4789\r\nf 1635/1679/1635 4934/5235/4790 4935/5236/4791 4936/5237/4792\r\nf 4937/5238/4793 4938/5239/4794 4935/5236/4791 4934/5235/4790\r\nf 4932/5233/4788 4931/5232/4787 4935/5236/4791 4938/5239/4794\r\nf 4930/5231/4786 4936/5237/4792 4935/5236/4791 4931/5232/4787\r\nf 4939/5240/4795 4940/5241/4796 4941/5242/4797 4942/5243/4798\r\nf 4943/5244/4799 4944/5245/4800 4941/5242/4797 4940/5241/4796\r\nf 4932/5233/4788 4938/5239/4794 4941/5242/4797 4944/5245/4800\r\nf 4937/5238/4793 4942/5243/4798 4941/5242/4797 4938/5239/4794\r\nf 1168/1168/1168 1171/1171/1171 4945/5246/4801 4946/5247/4802\r\nf 1166/1166/1166 4933/5234/4789 4945/5246/4801 1171/1171/1171\r\nf 4932/5233/4788 4944/5245/4800 4945/5246/4801 4933/5234/4789\r\nf 4943/5244/4799 4946/5247/4802 4945/5246/4801 4944/5245/4800\r\nf 4947/5248/4803 4948/5249/4804 4949/5250/4805 4950/5251/4806\r\nf 4951/5252/4807 4952/5253/4808 4949/5250/4805 4948/5249/4804\r\nf 4953/5254/4809 4954/5255/4810 4949/5250/4805 4952/5253/4808\r\nf 4955/5256/4811 4950/5251/4806 4949/5250/4805 4954/5255/4810\r\nf 1454/1477/1454 1457/1480/1457 4956/5257/4812 4957/5258/4813\r\nf 1449/1472/1449 4958/5259/4814 4956/5257/4812 1457/1480/1457\r\nf 4953/5254/4809 4952/5253/4808 4956/5257/4812 4958/5259/4814\r\nf 4951/5252/4807 4957/5258/4813 4956/5257/4812 4952/5253/4808\r\nf 1445/1468/1445 4959/5260/4815 4960/5261/4816 1446/1469/1446\r\nf 4961/5262/4817 4962/5263/4818 4960/5261/4816 4959/5260/4815\r\nf 4953/5254/4809 4958/5259/4814 4960/5261/4816 4962/5263/4818\r\nf 1449/1472/1449 1446/1469/1446 4960/5261/4816 4958/5259/4814\r\nf 4963/5264/4819 4964/5265/4820 4965/5266/4821 4966/5267/4822\r\nf 4955/5256/4811 4954/5255/4810 4965/5266/4821 4964/5265/4820\r\nf 4953/5254/4809 4962/5263/4818 4965/5266/4821 4954/5255/4810\r\nf 4961/5262/4817 4966/5267/4822 4965/5266/4821 4962/5263/4818\r\nf 1162/1162/1162 1165/1165/1165 4967/5268/4823 4968/5269/4824\r\nf 1157/1157/1157 4969/5270/4825 4967/5268/4823 1165/1165/1165\r\nf 4970/5271/4826 4971/5272/4827 4967/5268/4823 4969/5270/4825\r\nf 4972/5273/4828 4968/5269/4824 4967/5268/4823 4971/5272/4827\r\nf 1153/1153/1153 1650/1696/1650 4973/5274/4829 1154/1154/1154\r\nf 1645/1700/1645 4974/5275/4830 4973/5274/4829 1650/1696/1650\r\nf 4970/5271/4826 4969/5270/4825 4973/5274/4829 4974/5275/4830\r\nf 1157/1157/1157 1154/1154/1154 4973/5274/4829 4969/5270/4825\r\nf 1454/1477/1454 4957/5258/4813 4975/5276/4831 1643/1689/1643\r\nf 4951/5252/4807 4976/5277/4832 4975/5276/4831 4957/5258/4813\r\nf 4970/5278/4826 4974/5279/4830 4975/5276/4831 4976/5277/4832\r\nf 1645/1691/1645 1643/1689/1643 4975/5276/4831 4974/5279/4830\r\nf 4947/5248/4803 4977/5280/4833 4978/5281/4834 4948/5249/4804\r\nf 4972/5282/4828 4971/5283/4827 4978/5281/4834 4977/5280/4833\r\nf 4970/5278/4826 4976/5277/4832 4978/5281/4834 4971/5283/4827\r\nf 4951/5252/4807 4948/5249/4804 4978/5281/4834 4976/5277/4832\r\nf 1168/1168/1168 4946/5247/4802 4979/5284/4835 2649/2779/2649\r\nf 4943/5244/4799 4980/5285/4836 4979/5284/4835 4946/5247/4802\r\nf 4981/5286/4837 4982/5287/4838 4979/5284/4835 4980/5285/4836\r\nf 2651/2781/2651 2649/2779/2649 4979/5284/4835 4982/5287/4838\r\nf 4939/5240/4795 4983/5288/4839 4984/5289/4840 4940/5241/4796\r\nf 4985/5290/4841 4986/5291/4842 4984/5289/4840 4983/5288/4839\r\nf 4981/5286/4837 4980/5285/4836 4984/5289/4840 4986/5291/4842\r\nf 4943/5244/4799 4940/5241/4796 4984/5289/4840 4980/5285/4836\r\nf 3607/5292/3586 4987/5293/4843 4988/5294/4844 4989/5295/4845\r\nf 4990/5296/4846 4991/5297/4847 4988/5294/4844 4987/5293/4843\r\nf 4981/5286/4837 4986/5291/4842 4988/5294/4844 4991/5297/4847\r\nf 4985/5290/4841 4989/5295/4845 4988/5294/4844 4986/5291/4842\r\nf 2653/2783/2653 2656/2786/2656 4992/5298/4848 4993/5299/4849\r\nf 2651/2781/2651 4982/5287/4838 4992/5298/4848 2656/2786/2656\r\nf 4981/5286/4837 4991/5297/4847 4992/5298/4848 4982/5287/4838\r\nf 4990/5296/4846 4993/5299/4849 4992/5298/4848 4991/5297/4847\r\nf 4963/5264/4819 4966/5267/4822 4994/5300/4850 4995/5301/4851\r\nf 4961/5262/4817 4996/5302/4852 4994/5300/4850 4966/5267/4822\r\nf 4997/5303/4853 4998/5304/4854 4994/5300/4850 4996/5302/4852\r\nf 4999/5305/4855 4995/5301/4851 4994/5300/4850 4998/5304/4854\r\nf 1445/1468/1445 2804/2934/2804 5000/5306/4856 4959/5260/4815\r\nf 2808/2938/2808 5001/5307/4857 5000/5306/4856 2804/2934/2804\r\nf 4997/5303/4853 4996/5302/4852 5000/5306/4856 5001/5307/4857\r\nf 4961/5262/4817 4959/5260/4815 5000/5306/4856 4996/5302/4852\r\nf 2819/2949/2819 3993/4236/3916 5002/5308/4858 2820/2950/2820\r\nf 3998/4241/3921 5003/5309/4859 5002/5308/4858 3993/4236/3916\r\nf 4997/5303/4853 5001/5307/4857 5002/5308/4858 5003/5309/4859\r\nf 2808/2938/2808 2820/2950/2820 5002/5308/4858 5001/5307/4857\r\nf 4011/4254/3934 5004/5310/4860 5005/5311/4861 4012/4255/3935\r\nf 4999/5305/4855 4998/5304/4854 5005/5311/4861 5004/5310/4860\r\nf 4997/5303/4853 5003/5309/4859 5005/5311/4861 4998/5304/4854\r\nf 3998/4241/3921 4012/4255/3935 5005/5311/4861 5003/5309/4859\r\nf 5006/5312/4862 5007/5313/4863 5008/5314/4864 5009/5315/2653\r\nf 5010/5316/4865 5011/5317/4866 5008/5314/4864 5007/5313/4863\r\nf 5012/5318/4867 5013/5319/2654 5008/5314/4864 5011/5317/4866\r\nf 5014/5320/2653 5009/5315/2653 5008/5314/4864 5013/5319/2654\r\nf 3617/3796/3594 3620/3799/3597 5015/5321/4868 5016/5322/4869\r\nf 3613/3791/3590 5017/5323/4870 5015/5321/4868 3620/3799/3597\r\nf 5012/5318/4867 5011/5317/4866 5015/5321/4868 5017/5323/4870\r\nf 5010/5316/4865 5016/5322/4869 5015/5321/4868 5011/5317/4866\r\nf 2659/3787/2659 2662/5324/2662 5018/5325/4871 3611/3788/3588\r\nf 2657/5326/2657 5019/5327/4872 5018/5325/4871 2662/5324/2662\r\nf 5012/5318/4867 5017/5323/4870 5018/5325/4871 5019/5327/4872\r\nf 3613/3791/3590 3611/3788/3588 5018/5325/4871 5017/5323/4870\r\nf 2653/5328/2653 5020/5329/2653 5021/5330/2654 2654/5331/2654\r\nf 5014/5320/2653 5013/5319/2654 5021/5330/2654 5020/5329/2653\r\nf 5012/5318/4867 5019/5327/4872 5021/5330/2654 5013/5319/2654\r\nf 2657/5326/2657 2654/5331/2654 5021/5330/2654 5019/5327/4872\r\nf 4005/4248/3928 5022/5332/4873 5023/5333/4874 4006/4249/3929\r\nf 5024/5334/4875 5025/5335/4876 5023/5333/4874 5022/5332/4873\r\nf 5026/5336/4877 5027/5337/4878 5023/5333/4874 5025/5335/4876\r\nf 4009/4252/3932 4006/4249/3929 5023/5333/4874 5027/5337/4878\r\nf 5028/5338/4879 5029/5339/4880 5030/5340/4881 5031/5341/4882\r\nf 5032/5342/4883 5033/5343/4884 5030/5340/4881 5029/5339/4880\r\nf 5026/5336/4877 5025/5335/4876 5030/5340/4881 5033/5343/4884\r\nf 5024/5334/4875 5031/5341/4882 5030/5340/4881 5025/5335/4876\r\nf 2795/2925/2795 5034/5344/4885 5035/5345/4886 5036/5346/4887\r\nf 5037/5347/4888 5038/5348/4889 5035/5345/4886 5034/5344/4885\r\nf 5026/5336/4877 5033/5343/4884 5035/5345/4886 5038/5348/4889\r\nf 5032/5342/4883 5036/5346/4887 5035/5345/4886 5033/5343/4884\r\nf 4011/4254/3934 4014/4257/3937 5039/5349/4890 5040/5350/4891\r\nf 4009/4252/3932 5027/5337/4878 5039/5349/4890 4014/4257/3937\r\nf 5026/5336/4877 5038/5348/4889 5039/5349/4890 5027/5337/4878\r\nf 5037/5347/4888 5040/5350/4891 5039/5349/4890 5038/5348/4889\r\nf 1435/1458/1435 5041/5351/4892 5042/5352/4893 1436/1459/1436\r\nf 5043/5353/4894 5044/5354/4895 5042/5352/4893 5041/5351/4892\r\nf 5045/5355/4896 5046/5356/4897 5042/5352/4893 5044/5354/4895\r\nf 1439/1462/1439 1436/1459/1436 5042/5352/4893 5046/5356/4897\r\nf 4947/5248/4803 4950/5251/4806 5047/5357/4898 5048/5358/4899\r\nf 4955/5256/4811 5049/5359/4900 5047/5357/4898 4950/5251/4806\r\nf 5045/5355/4896 5044/5354/4895 5047/5357/4898 5049/5359/4900\r\nf 5043/5353/4894 5048/5358/4899 5047/5357/4898 5044/5354/4895\r\nf 4963/5264/4819 5050/5360/4901 5051/5361/4902 4964/5265/4820\r\nf 5052/5362/4903 5053/5363/4904 5051/5361/4902 5050/5360/4901\r\nf 5045/5355/4896 5049/5359/4900 5051/5361/4902 5053/5363/4904\r\nf 4955/5256/4811 4964/5265/4820 5051/5361/4902 5049/5359/4900\r\nf 1441/1464/1441 1444/1467/1444 5054/5364/4905 5055/5365/4906\r\nf 1439/1462/1439 5046/5356/4897 5054/5364/4905 1444/1467/1444\r\nf 5045/5355/4896 5053/5363/4904 5054/5364/4905 5046/5356/4897\r\nf 5052/5362/4903 5055/5365/4906 5054/5364/4905 5053/5363/4904\r\nf 1635/1679/1635 4936/5237/4792 5056/5366/4907 1636/1680/1636\r\nf 4930/5231/4786 5057/5367/4908 5056/5366/4907 4936/5237/4792\r\nf 5058/5368/4909 5059/5369/4910 5056/5366/4907 5057/5367/4908\r\nf 1639/1683/1639 1636/1680/1636 5056/5366/4907 5059/5369/4910\r\nf 1162/1162/1162 4968/5269/4824 5060/5370/4911 4928/5229/4784\r\nf 4972/5273/4828 5061/5371/4912 5060/5370/4911 4968/5269/4824\r\nf 5058/5368/4909 5057/5367/4908 5060/5370/4911 5061/5371/4912\r\nf 4930/5231/4786 4928/5229/4784 5060/5370/4911 5057/5367/4908\r\nf 4947/5248/4803 5048/5358/4899 5062/5372/4913 4977/5280/4833\r\nf 5043/5353/4894 5063/5373/4914 5062/5372/4913 5048/5358/4899\r\nf 5058/5374/4909 5061/5375/4912 5062/5372/4913 5063/5373/4914\r\nf 4972/5282/4828 4977/5280/4833 5062/5372/4913 5061/5375/4912\r\nf 1435/1458/1435 1642/1686/1642 5064/5376/4915 5041/5351/4892\r\nf 1639/1688/1639 5059/5377/4910 5064/5376/4915 1642/1686/1642\r\nf 5058/5374/4909 5063/5373/4914 5064/5376/4915 5059/5377/4910\r\nf 5043/5353/4894 5041/5351/4892 5064/5376/4915 5063/5373/4914\r\nf 1441/1464/1441 5055/5365/4906 5065/5378/4916 2791/2921/2791\r\nf 5052/5362/4903 5066/5379/4917 5065/5378/4916 5055/5365/4906\r\nf 5067/5380/4918 5068/5381/4919 5065/5378/4916 5066/5379/4917\r\nf 2793/2923/2793 2791/2921/2791 5065/5378/4916 5068/5381/4919\r\nf 4963/5264/4819 4995/5301/4851 5069/5382/4920 5050/5360/4901\r\nf 4999/5305/4855 5070/5383/4921 5069/5382/4920 4995/5301/4851\r\nf 5067/5380/4918 5066/5379/4917 5069/5382/4920 5070/5383/4921\r\nf 5052/5362/4903 5050/5360/4901 5069/5382/4920 5066/5379/4917\r\nf 4011/4254/3934 5040/5350/4891 5071/5384/4922 5004/5310/4860\r\nf 5037/5347/4888 5072/5385/4923 5071/5384/4922 5040/5350/4891\r\nf 5067/5380/4918 5070/5383/4921 5071/5384/4922 5072/5385/4923\r\nf 4999/5305/4855 5004/5310/4860 5071/5384/4922 5070/5383/4921\r\nf 2795/2925/2795 2798/2928/2798 5073/5386/4924 5034/5344/4885\r\nf 2793/2923/2793 5068/5381/4919 5073/5386/4924 2798/2928/2798\r\nf 5067/5380/4918 5072/5385/4923 5073/5386/4924 5068/5381/4919\r\nf 5037/5347/4888 5034/5344/4885 5073/5386/4924 5072/5385/4923\r\nf 3601/3777/3581 5074/5387/4843 5075/5388/4925 3602/3778/3582\r\nf 5076/5389/4926 5077/5390/4927 5075/5388/4925 5074/5387/4843\r\nf 5078/5391/4927 5079/5392/4925 5075/5388/4925 5077/5390/4927\r\nf 3605/3781/3582 3602/3778/3582 5075/5388/4925 5079/5392/4925\r\nf 5006/5312/4862 5009/5315/2653 5080/5393/4849 5081/5394/4928\r\nf 5014/5320/2653 5082/5395/4849 5080/5393/4849 5009/5315/2653\r\nf 5078/5391/4927 5077/5390/4927 5080/5393/4849 5082/5395/4849\r\nf 5076/5389/4926 5081/5394/4928 5080/5393/4849 5077/5390/4927\r\nf 2653/5328/2653 4993/5396/4849 5083/5397/4849 5020/5329/2653\r\nf 4990/5398/4846 5084/5399/4846 5083/5397/4849 4993/5396/4849\r\nf 5078/5391/4927 5082/5395/4849 5083/5397/4849 5084/5399/4846\r\nf 5014/5320/2653 5020/5329/2653 5083/5397/4849 5082/5395/4849\r\nf 3607/3783/3586 3610/3786/3586 5085/5400/4843 4987/5401/4843\r\nf 3605/3781/3582 5079/5392/4925 5085/5400/4843 3610/3786/3586\r\nf 5078/5391/4927 5084/5399/4846 5085/5400/4843 5079/5392/4925\r\nf 4990/5398/4846 4987/5401/4843 5085/5400/4843 5084/5399/4846\r\nf 1635/1679/1635 1638/1682/1638 5086/5402/4929 4934/5235/4790\r\nf 1633/1675/1633 5087/5403/4930 5086/5402/4929 1638/1682/1638\r\nf 5088/5404/4931 5089/5405/4932 5086/5402/4929 5087/5403/4930\r\nf 4937/5238/4793 4934/5235/4790 5086/5402/4929 5089/5405/4932\r\nf 964/964/964 967/967/967 5090/5406/4933 1631/1673/1631\r\nf 962/962/962 5091/5407/4934 5090/5406/4933 967/967/967\r\nf 5088/5404/4931 5087/5403/4930 5090/5406/4933 5091/5407/4934\r\nf 1633/1675/1633 1631/1673/1631 5090/5406/4933 5087/5403/4930\r\nf 958/958/958 5092/5408/4935 5093/5409/4936 959/959/959\r\nf 5094/5410/4937 5095/5411/4938 5093/5409/4936 5092/5408/4935\r\nf 5088/5404/4931 5091/5407/4934 5093/5409/4936 5095/5411/4938\r\nf 962/962/962 959/959/959 5093/5409/4936 5091/5407/4934\r\nf 4939/5240/4795 4942/5243/4798 5096/5412/4939 5097/5413/4940\r\nf 4937/5238/4793 5089/5405/4932 5096/5412/4939 4942/5243/4798\r\nf 5088/5404/4931 5095/5411/4938 5096/5412/4939 5089/5405/4932\r\nf 5094/5410/4937 5097/5413/4940 5096/5412/4939 5095/5411/4938\r\nf 4939/5240/4795 5097/5413/4940 5098/5414/4941 4983/5288/4839\r\nf 5094/5410/4937 5099/5415/4942 5098/5414/4941 5097/5413/4940\r\nf 5100/5416/4943 5101/5417/4944 5098/5414/4941 5099/5415/4942\r\nf 4985/5290/4841 4983/5288/4839 5098/5414/4941 5101/5417/4944\r\nf 958/958/958 2476/2606/2476 5102/5418/4945 5092/5408/4935\r\nf 2480/2610/2480 5103/5419/4946 5102/5418/4945 2476/2606/2476\r\nf 5100/5416/4943 5099/5415/4942 5102/5418/4945 5103/5419/4946\r\nf 5094/5410/4937 5092/5408/4935 5102/5418/4945 5099/5415/4942\r\nf 2491/2621/2491 3592/5420/3573 5104/5421/4947 2492/2622/2492\r\nf 3596/5422/3577 5105/5423/4948 5104/5421/4947 3592/5420/3573\r\nf 5100/5416/4943 5103/5419/4946 5104/5421/4947 5105/5423/4948\r\nf 2480/2610/2480 2492/2622/2492 5104/5421/4947 5103/5419/4946\r\nf 3607/5292/3586 4989/5295/4845 5106/5424/4949 3608/5425/3587\r\nf 4985/5290/4841 5101/5417/4944 5106/5424/4949 4989/5295/4845\r\nf 5100/5416/4943 5105/5423/4948 5106/5424/4949 5101/5417/4944\r\nf 3596/5422/3577 3608/5425/3587 5106/5424/4949 5105/5423/4948\r\nf 5028/5338/4879 5107/5426/4950 5108/5427/4951 5029/5339/4880\r\nf 5109/5428/4952 5110/5429/4953 5108/5427/4951 5107/5426/4950\r\nf 5111/5430/4954 5112/5431/4955 5108/5427/4951 5110/5429/4953\r\nf 5032/5342/4883 5029/5339/4880 5108/5427/4951 5112/5431/4955\r\nf 3905/4148/3837 3908/4151/3840 5113/5432/4956 5114/5433/4957\r\nf 3901/4144/3833 5115/5434/4958 5113/5432/4956 3908/4151/3840\r\nf 5111/5430/4954 5110/5429/4953 5113/5432/4956 5115/5434/4958\r\nf 5109/5428/4952 5114/5433/4957 5113/5432/4956 5110/5429/4953\r\nf 2705/2835/2705 2802/2932/2802 5116/5435/4959 3899/4142/3831\r\nf 2799/2929/2799 5117/5436/4960 5116/5435/4959 2802/2932/2802\r\nf 5111/5430/4954 5115/5434/4958 5116/5435/4959 5117/5436/4960\r\nf 3901/4144/3833 3899/4142/3831 5116/5435/4959 5115/5434/4958\r\nf 2795/2925/2795 5036/5346/4887 5118/5437/4961 2796/2926/2796\r\nf 5032/5342/4883 5112/5431/4955 5118/5437/4961 5036/5346/4887\r\nf 5111/5430/4954 5117/5436/4960 5118/5437/4961 5112/5431/4955\r\nf 2799/2929/2799 2796/2926/2796 5118/5437/4961 5117/5436/4960\r\nv 332.79 45.05 94.49\r\nv 333.93 35.47 95.02\r\nv 328.55 36.47 103.73\r\nv 327.41 45.75 103.15\r\nv 334.92 27.67 95.47\r\nv 329.55 28.92 104.24\r\nv 322.96 30.42 112.47\r\nv 321.98 37.68 111.92\r\nv 320.88 46.63 111.3\r\nv 327.57 78.79 91.42\r\nv 329.4 71.14 92.56\r\nv 324.27 71.36 101.01\r\nv 322.68 78.94 99.64\r\nv 330.78 62.95 93.41\r\nv 325.52 63.26 102\r\nv 319.14 63.75 110.05\r\nv 318.04 71.79 108.9\r\nv 316.71 79.37 107.21\r\nv 322.28 92.25 88.34\r\nv 318.31 92.19 95.71\r\nv 315.61 97.43 93.59\r\nv 318.72 97.83 86.76\r\nv 313.37 92.54 102.16\r\nv 311.59 97.57 99.23\r\nv 309.89 100.7 96.65\r\nv 311.97 101.7 92.18\r\nv 314.19 102.6 85.59\r\nv 304.65 48.79 125.23\r\nv 303.96 57.04 124.59\r\nv 312.44 56.15 118.15\r\nv 313.24 47.67 118.72\r\nv 303.31 65.24 123.65\r\nv 311.67 64.44 117.33\r\nv 320.01 55.34 110.77\r\nv 284.06 83.02 127.48\r\nv 283.7 90.68 123.03\r\nv 292.54 89.61 120.08\r\nv 293.12 82.12 124.12\r\nv 283.43 97.64 117.37\r\nv 292.07 96.37 114.92\r\nv 300.12 94.93 111.57\r\nv 300.89 88.4 116.03\r\nv 301.75 81.1 119.56\r\nv 283.32 103.56 110.56\r\nv 291.81 102.1 108.72\r\nv 283.41 108.36 102.89\r\nv 291.89 106.71 101.65\r\nv 299.52 104.7 100.08\r\nv 299.58 100.4 106.25\r\nv 285.48 50.74 134.75\r\nv 285.13 58.65 133.89\r\nv 294.76 57.92 129.9\r\nv 295.3 49.86 130.63\r\nv 284.8 66.76 132.67\r\nv 294.26 66.06 128.82\r\nv 284.28 114.45 86.1\r\nv 293.19 112.46 85.74\r\nv 292.41 110.08 93.89\r\nv 283.77 111.92 94.63\r\nv 301.35 109.79 85.41\r\nv 300.23 107.72 93.04\r\nv 322.39 46.47 -23.84\r\nv 329.52 45.51 -14.36\r\nv 328.63 54.51 -13.16\r\nv 321.78 55.17 -22.14\r\nv 335.25 44.73 -4.38\r\nv 334.11 54.04 -3.78\r\nv 332.95 62.84 -3.05\r\nv 327.67 63.11 -12.07\r\nv 321.03 63.58 -20.73\r\nv 329.48 78.79 -1.02\r\nv 326.96 85.88 0.37\r\nv 322.49 85.86 -7.53\r\nv 324.65 78.85 -9.37\r\nv 323.84 92.26 1.89\r\nv 319.94 92.08 -5.48\r\nv 314.88 92.33 -12.14\r\nv 316.79 86.18 -14.89\r\nv 318.53 79.18 -17.29\r\nv 330.99 93.48 31.24\r\nv 326.2 99.52 31.57\r\nv 325.49 99.26 24.97\r\nv 330.19 93.26 24.39\r\nv 320.38 104.82 31.84\r\nv 319.75 104.52 25.44\r\nv 318.73 104.05 18.67\r\nv 324.3 98.87 17.99\r\nv 328.84 92.96 17.16\r\nv 339.18 44.24 5.1\r\nv 338.01 53.77 5.38\r\nv 341.77 43.93 14.03\r\nv 340.66 53.62 14.16\r\nv 339.37 62.74 14.47\r\nv 336.76 62.75 5.86\r\nv 335.44 79.1 15.55\r\nv 332.52 86.36 16.32\r\nv 330.24 86.08 8.41\r\nv 333 78.91 7.36\r\nv 326.77 92.59 9.55\r\nv 317.05 97.31 -3.4\r\nv 312.93 97.35 -9.18\r\nv 320.1 97.81 3.38\r\nv 315.37 102.54 4.45\r\nv 313.2 101.55 -2.06\r\nv 311.09 100.49 -6.61\r\nv 314.29 47.53 -31.84\r\nv 314.86 38.91 -34.22\r\nv 323.05 37.58 -26.24\r\nv 305.26 48.73 -38.51\r\nv 305.84 40.37 -40.53\r\nv 306.32 33.63 -42.49\r\nv 315.33 31.95 -36.5\r\nv 323.59 30.39 -28.51\r\nv 344.44 43.61 30.24\r\nv 343.45 43.73 22.37\r\nv 344.51 33.43 22.47\r\nv 345.45 33.25 30.38\r\nv 342.93 33.75 14.12\r\nv 343.92 25.44 14.18\r\nv 345.38 25.05 22.54\r\nv 346.28 24.82 30.47\r\nv 342.02 62.78 30.27\r\nv 340.24 71.42 30.4\r\nv 339.3 71.34 22.88\r\nv 341.05 62.76 22.59\r\nv 337.89 79.41 30.62\r\nv 336.99 79.28 23.29\r\nv 337.68 71.23 14.93\r\nv 322.51 98.36 10.66\r\nv 317.24 103.38 11.51\r\nv 306.2 98.72 103.27\r\nv 305.86 102.6 98.32\r\nv 307.27 93.55 107.38\r\nv 310.74 72.48 115.96\r\nv 309.67 80.13 113.89\r\nv 302.57 73.32 122.03\r\nv 308.38 106.54 85.25\r\nv 306.8 105.03 92.23\r\nv 317.97 104.02 70.41\r\nv 316.24 103.38 78.14\r\nv 309.95 107.68 77.63\r\nv 311.34 108.49 69.89\r\nv 302.53 111.23 77.47\r\nv 303.62 112.19 69.63\r\nv 300.87 94.59 -22.18\r\nv 308.46 93.26 -17.66\r\nv 307.27 98.42 -13.45\r\nv 300.23 100.05 -16.69\r\nv 306.84 102.33 -8.4\r\nv 300.12 104.38 -10.34\r\nv 302.72 80.87 -30.49\r\nv 311.14 79.9 -24.37\r\nv 309.81 86.99 -21.33\r\nv 301.76 88.1 -26.79\r\nv 307.75 104.83 -2.22\r\nv 300.83 107.46 -3.17\r\nv 309.3 106.42 4.68\r\nv 301.96 109.59 4.4\r\nv 295.37 50.11 -44.03\r\nv 295.8 42.15 -45.85\r\nv 285.2 51.41 -48.15\r\nv 285.41 43.92 -49.88\r\nv 285.53 37.97 -51.58\r\nv 296.11 35.77 -47.63\r\nv 310.76 107.6 11.89\r\nv 311.98 108.44 19.04\r\nv 304.08 112.05 19.17\r\nv 303.09 111.08 11.93\r\nv 274.56 109.42 103.67\r\nv 274.74 113.08 95.1\r\nv 265.52 109.8 103.93\r\nv 265.52 113.48 95.27\r\nv 265.52 116.08 86.46\r\nv 274.99 115.67 86.36\r\nv 294.27 113.85 11.76\r\nv 293.4 112.21 3.94\r\nv 295.06 114.89 19.12\r\nv 285.38 116.93 18.98\r\nv 284.81 115.87 11.51\r\nv 284.19 114.16 3.46\r\nv 284 82.98 -39.14\r\nv 293.5 81.97 -35.47\r\nv 292.83 89.36 -31.22\r\nv 283.57 90.48 -34.48\r\nv 292.27 96.04 -25.87\r\nv 283.24 97.32 -28.57\r\nv 291.95 101.74 -19.42\r\nv 283.09 103.19 -21.48\r\nv 292.01 106.36 -12.11\r\nv 283.19 107.99 -13.52\r\nv 292.57 109.78 -4.17\r\nv 283.6 111.59 -5.05\r\nv 285.36 117.18 69.59\r\nv 294.84 115.09 69.56\r\nv 294.04 114.07 77.52\r\nv 284.84 116.14 77.67\r\nv 275.29 52.38 -50.63\r\nv 275.37 45.35 -52.33\r\nv 265.52 52.79 -51.46\r\nv 265.52 45.99 -53.16\r\nv 265.52 40.67 -54.82\r\nv 275.41 39.82 -53.99\r\nv 275.2 117.05 11.31\r\nv 274.88 115.32 3.13\r\nv 275.5 118.12 18.85\r\nv 265.52 118.51 18.8\r\nv 265.52 117.44 11.23\r\nv 265.52 115.7 3\r\nv 323.68 24.87 113.08\r\nv 323.44 26.75 112.83\r\nv 330.05 25.15 104.55\r\nv 330.31 23.28 104.77\r\nv 335.44 23.81 95.74\r\nv 335.72 21.94 95.91\r\nv 306.96 28.57 127.39\r\nv 306.77 30.45 127.03\r\nv 315.65 28.56 120.36\r\nv 315.87 26.68 120.67\r\nv 306.38 33.92 126.57\r\nv 315.2 32.13 119.96\r\nv 323.86 26.8 -29.82\r\nv 332.04 24.88 -18.22\r\nv 331.56 28.61 -17.34\r\nv 324 25.01 -30.66\r\nv 332.29 23.06 -18.81\r\nv 339.03 21.3 -6.17\r\nv 338.69 23.15 -5.79\r\nv 338.03 27.03 -5.38\r\nv 315.5 28.44 -37.93\r\nv 315.53 26.66 -38.94\r\nv 306.43 30.2 -43.88\r\nv 306.37 28.43 -44.99\r\nv 347.18 18.82 30.12\r\nv 346.8 20.73 30.35\r\nv 345.91 20.98 22.38\r\nv 346.29 19.07 22.11\r\nv 344.49 21.41 14\r\nv 344.86 19.51 13.69\r\nv 342.47 22.06 4.78\r\nv 342.83 20.19 4.46\r\nv 341.81 26.04 5\r\nv 286.56 31.89 137.69\r\nv 286.49 33.79 137.2\r\nv 296.9 32.31 132.7\r\nv 297.02 30.41 133.13\r\nv 286.31 37.08 136.59\r\nv 296.61 35.67 132.17\r\nv 296.13 32.47 -49\r\nv 296 30.71 -50.17\r\nv 285.48 34.81 -52.93\r\nv 285.33 33.06 -54.12\r\nv 275.36 36.82 -55.33\r\nv 275.27 35.08 -56.5\r\nv 265.52 37.74 -56.14\r\nv 265.52 36 -57.29\r\nv 320.68 85.96 97.8\r\nv 315.12 86.38 104.89\r\nv 325.22 85.86 89.96\r\nv 334.84 86.77 30.91\r\nv 333.99 86.6 23.82\r\nv 293.71 74.2 126.96\r\nv 284.43 74.97 130.61\r\nv 319.94 71.62 -19.17\r\nv 326.36 71.24 -10.85\r\nv 331.44 71.1 -2.15\r\nv 335.14 71.13 6.52\r\nv 308.47 87.26 110.97\r\nv 304.29 65.16 -35.28\r\nv 313.16 64.28 -28.52\r\nv 312.27 72.29 -26.69\r\nv 303.58 73.17 -33.23\r\nv 275.01 75.47 132.84\r\nv 274.83 83.62 129.52\r\nv 275.19 67.21 135.05\r\nv 265.52 67.38 135.87\r\nv 265.52 75.66 133.6\r\nv 265.52 83.85 130.21\r\nv 284.75 67.08 -44.96\r\nv 294.65 66.16 -40.83\r\nv 294.13 74.18 -38.58\r\nv 284.4 75.09 -42.54\r\nv 326.49 54.7 102.64\r\nv 331.83 54.23 94.01\r\nv 342.37 53.53 22.41\r\nv 343.36 53.48 30.21\r\nv 314.28 39.08 119.37\r\nv 305.57 40.56 125.93\r\nv 295.99 41.99 131.43\r\nv 285.93 43.14 135.71\r\nv 330.61 36.18 -15.9\r\nv 336.72 34.98 -4.92\r\nv 340.58 34.21 5.04\r\nv 313.77 56.01 -30.08\r\nv 304.79 57 -36.9\r\nv 295.03 58.12 -42.5\r\nv 285 59.15 -46.66\r\nv 275.75 43.76 138.58\r\nv 275.53 51.24 137.38\r\nv 275.94 37.82 139.67\r\nv 265.52 38.03 140.88\r\nv 265.52 43.95 139.66\r\nv 265.52 51.4 138.33\r\nv 275.08 67.71 -47.42\r\nv 275.2 59.88 -49.16\r\nv 265.52 67.95 -48.24\r\nv 265.52 60.17 -49.99\r\nv 330.65 93.36 57.11\r\nv 329.57 93.12 64.45\r\nv 324.92 99.16 63.67\r\nv 325.89 99.44 56.54\r\nv 327.88 92.82 72.26\r\nv 323.44 98.78 71.25\r\nv 319.23 104.49 63\r\nv 320.09 104.8 56.05\r\nv 342.06 25.99 76.9\r\nv 341.17 34.14 76.75\r\nv 343.36 33.74 67.36\r\nv 344.22 25.46 67.07\r\nv 340.12 44.16 76.51\r\nv 342.33 43.9 67.59\r\nv 343.78 43.73 59.25\r\nv 344.79 33.46 58.75\r\nv 345.62 25.11 58.19\r\nv 341.47 62.76 59.11\r\nv 340.09 62.74 67.16\r\nv 338.44 71.25 66.67\r\nv 339.76 71.34 58.74\r\nv 337.96 62.74 75.7\r\nv 336.38 71.15 75.07\r\nv 334.25 78.97 74.27\r\nv 336.22 79.14 66.02\r\nv 337.47 79.3 58.26\r\nv 305.07 113.12 55.47\r\nv 305.43 113.29 49.33\r\nv 313.56 109.54 49.49\r\nv 313.13 109.37 55.7\r\nv 305.58 113.31 43.56\r\nv 313.74 109.59 43.65\r\nv 320.77 105.04 43.75\r\nv 320.58 104.98 49.72\r\nv 286.33 118.22 43.43\r\nv 296.31 116.19 43.49\r\nv 296.2 116.18 49.24\r\nv 286.26 118.23 49.19\r\nv 295.93 116.03 55.37\r\nv 286.08 118.1 55.33\r\nv 342.91 20.1 77.05\r\nv 342.58 22 76.99\r\nv 344.74 21.43 66.96\r\nv 345.08 19.52 66.96\r\nv 346.14 21.04 57.97\r\nv 346.51 19.13 57.9\r\nv 333.3 86.44 65.26\r\nv 334.48 86.64 57.7\r\nv 331.46 86.2 73.3\r\nv 339.12 53.72 76.17\r\nv 341.32 53.6 67.5\r\nv 342.74 53.52 59.32\r\nv 312.4 109.04 62.54\r\nv 304.46 112.79 62.27\r\nv 295.47 115.71 62.15\r\nv 285.78 117.79 62.14\r\nv 325.46 92.49 80.37\r\nv 321.35 98.31 79.13\r\nv 338.08 34.71 85.99\r\nv 339.03 26.7 86.36\r\nv 336.98 44.53 85.54\r\nv 334.89 62.79 84.55\r\nv 333.4 71.1 83.81\r\nv 331.4 78.82 82.85\r\nv 339.55 22.78 86.57\r\nv 339.86 20.89 86.69\r\nv 328.79 85.98 81.66\r\nv 336 53.92 85.1\r\nv 305.54 113.23 37.89\r\nv 313.69 109.52 37.9\r\nv 305.29 113.03 32.07\r\nv 313.4 109.33 32.01\r\nv 320.71 104.99 37.87\r\nv 286.28 118.12 37.77\r\nv 296.27 116.1 37.84\r\nv 286.12 117.91 31.95\r\nv 296.05 115.89 32.03\r\nv 331.47 93.6 44.01\r\nv 331.26 93.53 50.39\r\nv 326.44 99.62 50.03\r\nv 326.64 99.69 43.87\r\nv 345.61 33.28 51.83\r\nv 346.42 24.88 51.62\r\nv 344.63 43.62 51.97\r\nv 344.97 43.56 45.05\r\nv 345.95 33.19 45.36\r\nv 346.74 24.76 45.6\r\nv 342.57 62.78 44.64\r\nv 342.26 62.77 51.71\r\nv 340.51 71.41 51.44\r\nv 340.79 71.45 44.47\r\nv 338.17 79.41 51.12\r\nv 338.43 79.47 44.31\r\nv 346.94 20.8 51.49\r\nv 347.31 18.88 51.39\r\nv 347.25 20.67 45.58\r\nv 347.63 18.75 45.45\r\nv 335.13 86.79 50.76\r\nv 335.36 86.86 44.15\r\nv 343.57 53.48 51.91\r\nv 343.91 53.45 44.82\r\nv 331.4 93.59 37.7\r\nv 326.58 99.66 37.79\r\nv 346.71 24.73 38.35\r\nv 345.91 33.17 38.11\r\nv 344.93 43.55 37.81\r\nv 342.51 62.79 37.56\r\nv 340.72 71.46 37.53\r\nv 338.35 79.48 37.55\r\nv 347.23 20.64 38.31\r\nv 347.61 18.72 38.13\r\nv 335.29 86.86 37.61\r\nv 343.85 53.45 37.63\r\nv 312.85 109 25.73\r\nv 304.81 112.67 25.86\r\nv 285.82 117.56 25.74\r\nv 295.65 115.53 25.83\r\nv 265.52 98.78 119.35\r\nv 274.54 98.47 118.85\r\nv 274.66 91.4 124.82\r\nv 265.52 91.66 125.42\r\nv 274.5 104.51 111.69\r\nv 265.52 104.86 112.07\r\nv 275.36 59.1 136.39\r\nv 265.52 59.25 137.26\r\nv 265.52 109.35 -14.67\r\nv 274.36 109 -14.38\r\nv 274.58 112.68 -5.6\r\nv 265.52 113.05 -5.8\r\nv 265.52 83.89 -42.04\r\nv 274.7 83.64 -41.33\r\nv 274.5 91.22 -36.4\r\nv 265.52 91.5 -37.03\r\nv 274.34 98.15 -30.17\r\nv 265.52 98.45 -30.7\r\nv 274.29 104.11 -22.7\r\nv 265.52 104.44 -23.11\r\nv 265.52 118.84 69.67\r\nv 275.53 118.43 69.64\r\nv 275.27 117.38 77.79\r\nv 265.52 117.8 77.84\r\nv 276.02 34.58 140.38\r\nv 265.52 34.8 141.63\r\nv 276.05 32.69 140.89\r\nv 265.52 32.91 142.16\r\nv 274.9 75.71 -44.9\r\nv 265.52 75.95 -45.68\r\nv 265.52 119.79 43.39\r\nv 276.01 119.41 43.4\r\nv 275.98 119.43 49.17\r\nv 265.52 119.81 49.17\r\nv 275.89 119.32 55.33\r\nv 265.52 119.71 55.34\r\nv 275.74 119.03 62.15\r\nv 265.52 119.43 62.17\r\nv 265.52 119.48 31.86\r\nv 275.89 119.1 31.89\r\nv 275.98 119.31 37.72\r\nv 265.52 119.69 37.7\r\nv 275.73 118.75 25.65\r\nv 265.52 119.14 25.61\r\nv 198.24 45.05 94.49\r\nv 203.62 45.75 103.15\r\nv 202.48 36.47 103.73\r\nv 197.1 35.47 95.02\r\nv 210.15 46.63 111.3\r\nv 209.05 37.68 111.92\r\nv 208.08 30.42 112.47\r\nv 201.49 28.92 104.24\r\nv 196.11 27.67 95.47\r\nv 203.46 78.79 91.42\r\nv 208.36 78.94 99.64\r\nv 206.76 71.36 101.01\r\nv 201.63 71.14 92.56\r\nv 214.32 79.37 107.21\r\nv 212.99 71.79 108.9\r\nv 211.89 63.75 110.05\r\nv 205.51 63.26 102\r\nv 200.25 62.95 93.41\r\nv 208.75 92.25 88.34\r\nv 212.32 97.83 86.76\r\nv 215.42 97.43 93.59\r\nv 212.72 92.19 95.71\r\nv 216.84 102.6 85.59\r\nv 219.06 101.7 92.18\r\nv 221.14 100.7 96.65\r\nv 219.45 97.57 99.23\r\nv 217.66 92.54 102.16\r\nv 226.38 48.79 125.23\r\nv 217.79 47.67 118.72\r\nv 218.6 56.15 118.15\r\nv 227.08 57.04 124.59\r\nv 211.02 55.34 110.77\r\nv 219.36 64.44 117.33\r\nv 227.72 65.24 123.65\r\nv 246.97 83.02 127.48\r\nv 237.91 82.12 124.12\r\nv 238.49 89.61 120.08\r\nv 247.33 90.68 123.03\r\nv 229.28 81.1 119.56\r\nv 230.14 88.4 116.03\r\nv 230.91 94.93 111.57\r\nv 238.96 96.37 114.92\r\nv 247.6 97.64 117.37\r\nv 239.22 102.1 108.72\r\nv 247.72 103.56 110.56\r\nv 231.46 100.4 106.25\r\nv 231.51 104.7 100.08\r\nv 239.15 106.71 101.65\r\nv 247.62 108.36 102.89\r\nv 245.55 50.74 134.75\r\nv 235.73 49.86 130.63\r\nv 236.27 57.92 129.9\r\nv 245.9 58.65 133.89\r\nv 236.77 66.06 128.82\r\nv 246.23 66.76 132.67\r\nv 246.75 114.45 86.1\r\nv 247.26 111.92 94.63\r\nv 238.62 110.08 93.89\r\nv 237.84 112.46 85.74\r\nv 230.8 107.72 93.04\r\nv 229.68 109.79 85.41\r\nv 208.64 46.47 -23.84\r\nv 209.25 55.17 -22.14\r\nv 202.4 54.51 -13.16\r\nv 201.51 45.51 -14.36\r\nv 210 63.58 -20.73\r\nv 203.37 63.11 -12.07\r\nv 198.08 62.85 -3.05\r\nv 196.92 54.04 -3.78\r\nv 195.78 44.73 -4.38\r\nv 201.55 78.79 -1.02\r\nv 206.38 78.85 -9.37\r\nv 208.54 85.86 -7.53\r\nv 204.07 85.88 0.37\r\nv 212.5 79.18 -17.29\r\nv 214.24 86.18 -14.89\r\nv 216.15 92.33 -12.14\r\nv 211.09 92.08 -5.48\r\nv 207.19 92.26 1.89\r\nv 200.05 93.48 31.24\r\nv 200.84 93.26 24.39\r\nv 205.54 99.26 24.97\r\nv 204.83 99.52 31.57\r\nv 202.19 92.96 17.16\r\nv 206.73 98.87 17.99\r\nv 212.3 104.05 18.67\r\nv 211.28 104.52 25.44\r\nv 210.65 104.82 31.84\r\nv 193.02 53.77 5.38\r\nv 191.85 44.24 5.1\r\nv 194.27 62.75 5.86\r\nv 191.66 62.74 14.47\r\nv 190.37 53.62 14.16\r\nv 189.27 43.93 14.03\r\nv 195.6 79.1 15.55\r\nv 198.03 78.91 7.36\r\nv 200.79 86.08 8.41\r\nv 198.51 86.36 16.32\r\nv 204.26 92.59 9.55\r\nv 218.1 97.35 -9.18\r\nv 213.98 97.31 -3.4\r\nv 219.94 100.49 -6.61\r\nv 217.83 101.55 -2.06\r\nv 215.66 102.54 4.45\r\nv 210.94 97.81 3.38\r\nv 207.98 37.58 -26.24\r\nv 216.17 38.91 -34.22\r\nv 216.75 47.53 -31.84\r\nv 207.44 30.39 -28.51\r\nv 215.7 31.95 -36.5\r\nv 224.72 33.63 -42.49\r\nv 225.19 40.37 -40.53\r\nv 225.77 48.73 -38.51\r\nv 186.59 43.61 30.24\r\nv 185.58 33.25 30.38\r\nv 186.53 33.43 22.47\r\nv 187.58 43.73 22.37\r\nv 184.76 24.82 30.47\r\nv 185.66 25.05 22.54\r\nv 187.11 25.44 14.18\r\nv 188.1 33.75 14.12\r\nv 189.01 62.78 30.27\r\nv 189.98 62.76 22.59\r\nv 191.73 71.34 22.88\r\nv 190.79 71.42 30.4\r\nv 193.36 71.23 14.93\r\nv 194.04 79.28 23.29\r\nv 193.14 79.41 30.62\r\nv 208.53 98.36 10.66\r\nv 213.79 103.38 11.51\r\nv 225.17 102.6 98.32\r\nv 224.83 98.72 103.27\r\nv 223.77 93.55 107.38\r\nv 221.36 80.13 113.89\r\nv 220.29 72.48 115.96\r\nv 228.46 73.32 122.03\r\nv 224.23 105.03 92.23\r\nv 222.65 106.54 85.25\r\nv 213.06 104.02 70.41\r\nv 219.69 108.49 69.89\r\nv 221.08 107.68 77.63\r\nv 214.79 103.38 78.14\r\nv 227.41 112.19 69.63\r\nv 228.5 111.23 77.47\r\nv 230.16 94.59 -22.18\r\nv 230.8 100.05 -16.69\r\nv 223.76 98.42 -13.45\r\nv 222.57 93.26 -17.66\r\nv 230.91 104.38 -10.34\r\nv 224.2 102.33 -8.4\r\nv 228.31 80.87 -30.49\r\nv 229.28 88.1 -26.79\r\nv 221.22 86.99 -21.33\r\nv 219.89 79.9 -24.37\r\nv 230.2 107.46 -3.17\r\nv 223.28 104.83 -2.22\r\nv 229.07 109.59 4.4\r\nv 221.73 106.42 4.68\r\nv 235.24 42.15 -45.85\r\nv 235.66 50.11 -44.03\r\nv 234.92 35.77 -47.63\r\nv 245.5 37.97 -51.58\r\nv 245.62 43.92 -49.88\r\nv 245.83 51.41 -48.15\r\nv 220.28 107.6 11.89\r\nv 227.94 111.08 11.93\r\nv 226.95 112.05 19.17\r\nv 219.05 108.44 19.04\r\nv 256.29 113.08 95.1\r\nv 256.47 109.42 103.67\r\nv 256.04 115.67 86.36\r\nv 237.63 112.21 3.94\r\nv 236.76 113.85 11.76\r\nv 246.84 114.16 3.46\r\nv 246.22 115.87 11.51\r\nv 245.65 116.93 18.98\r\nv 235.97 114.89 19.12\r\nv 247.04 82.98 -39.14\r\nv 247.46 90.48 -34.48\r\nv 238.2 89.36 -31.22\r\nv 237.53 81.97 -35.47\r\nv 247.79 97.32 -28.57\r\nv 238.76 96.04 -25.87\r\nv 247.94 103.19 -21.48\r\nv 239.08 101.74 -19.42\r\nv 247.84 107.99 -13.52\r\nv 239.02 106.36 -12.11\r\nv 238.46 109.78 -4.17\r\nv 247.43 111.59 -5.05\r\nv 245.67 117.18 69.59\r\nv 246.19 116.14 77.67\r\nv 236.99 114.07 77.52\r\nv 236.19 115.09 69.56\r\nv 255.66 45.35 -52.33\r\nv 255.74 52.38 -50.63\r\nv 255.62 39.82 -53.99\r\nv 256.15 115.32 3.13\r\nv 255.83 117.05 11.31\r\nv 255.53 118.12 18.85\r\nv 207.35 24.87 113.08\r\nv 200.72 23.28 104.77\r\nv 200.98 25.15 104.55\r\nv 207.59 26.75 112.83\r\nv 195.31 21.94 95.91\r\nv 195.59 23.81 95.74\r\nv 224.07 28.57 127.39\r\nv 215.16 26.68 120.67\r\nv 215.38 28.56 120.36\r\nv 224.26 30.45 127.03\r\nv 215.83 32.13 119.96\r\nv 224.65 33.92 126.57\r\nv 199.47 28.61 -17.34\r\nv 198.99 24.88 -18.22\r\nv 207.17 26.8 -29.82\r\nv 193 27.03 -5.38\r\nv 192.34 23.15 -5.79\r\nv 192 21.3 -6.17\r\nv 198.74 23.06 -18.81\r\nv 207.03 25.01 -30.66\r\nv 215.5 26.66 -38.94\r\nv 215.53 28.44 -37.93\r\nv 224.66 28.43 -44.99\r\nv 224.6 30.2 -43.88\r\nv 183.85 18.82 30.12\r\nv 184.74 19.07 22.11\r\nv 185.12 20.98 22.38\r\nv 184.23 20.73 30.35\r\nv 186.17 19.51 13.69\r\nv 186.54 21.41 14\r\nv 188.2 20.19 4.46\r\nv 188.57 22.06 4.78\r\nv 189.22 26.04 5\r\nv 244.47 31.89 137.69\r\nv 234.01 30.41 133.13\r\nv 234.14 32.31 132.7\r\nv 244.54 33.79 137.2\r\nv 234.43 35.67 132.17\r\nv 244.72 37.08 136.59\r\nv 235.03 30.71 -50.17\r\nv 234.9 32.47 -49\r\nv 245.7 33.06 -54.12\r\nv 245.55 34.81 -52.93\r\nv 255.76 35.08 -56.5\r\nv 255.67 36.82 -55.33\r\nv 215.92 86.38 104.89\r\nv 210.35 85.96 97.8\r\nv 205.82 85.86 89.96\r\nv 197.04 86.6 23.82\r\nv 196.19 86.77 30.91\r\nv 237.32 74.2 126.96\r\nv 246.6 74.97 130.61\r\nv 204.68 71.24 -10.85\r\nv 211.09 71.62 -19.17\r\nv 199.59 71.1 -2.15\r\nv 195.9 71.13 6.52\r\nv 222.56 87.26 110.97\r\nv 226.74 65.16 -35.28\r\nv 227.45 73.17 -33.23\r\nv 218.76 72.29 -26.69\r\nv 217.87 64.28 -28.52\r\nv 256.2 83.62 129.52\r\nv 256.02 75.47 132.84\r\nv 255.84 67.21 135.05\r\nv 246.28 67.08 -44.96\r\nv 246.63 75.09 -42.54\r\nv 236.9 74.18 -38.58\r\nv 236.38 66.16 -40.83\r\nv 204.55 54.7 102.64\r\nv 199.2 54.23 94.01\r\nv 187.67 53.48 30.21\r\nv 188.66 53.53 22.41\r\nv 216.75 39.08 119.37\r\nv 225.46 40.56 125.93\r\nv 235.04 41.99 131.43\r\nv 245.1 43.14 135.71\r\nv 200.42 36.18 -15.9\r\nv 194.31 34.98 -4.92\r\nv 190.45 34.21 5.04\r\nv 217.26 56.01 -30.08\r\nv 226.24 57 -36.9\r\nv 236 58.12 -42.5\r\nv 246.03 59.15 -46.66\r\nv 255.5 51.24 137.38\r\nv 255.28 43.76 138.58\r\nv 255.1 37.82 139.67\r\nv 255.83 59.88 -49.16\r\nv 255.95 67.71 -47.42\r\nv 200.38 93.36 57.11\r\nv 205.14 99.44 56.54\r\nv 206.11 99.16 63.67\r\nv 201.46 93.12 64.45\r\nv 210.94 104.8 56.05\r\nv 211.8 104.49 63\r\nv 207.6 98.78 71.25\r\nv 203.15 92.82 72.26\r\nv 188.97 25.99 76.9\r\nv 186.82 25.46 67.07\r\nv 187.68 33.74 67.36\r\nv 189.86 34.14 76.75\r\nv 185.41 25.11 58.19\r\nv 186.24 33.46 58.75\r\nv 187.25 43.73 59.25\r\nv 188.7 43.9 67.59\r\nv 190.92 44.16 76.51\r\nv 189.56 62.76 59.11\r\nv 191.27 71.34 58.74\r\nv 192.59 71.25 66.67\r\nv 190.94 62.74 67.16\r\nv 193.56 79.3 58.26\r\nv 194.81 79.14 66.02\r\nv 196.78 78.97 74.27\r\nv 194.65 71.15 75.07\r\nv 193.07 62.74 75.7\r\nv 225.97 113.12 55.47\r\nv 217.9 109.37 55.7\r\nv 217.47 109.54 49.49\r\nv 225.6 113.29 49.33\r\nv 210.45 104.98 49.72\r\nv 210.27 105.04 43.75\r\nv 217.3 109.59 43.65\r\nv 225.46 113.31 43.56\r\nv 244.7 118.22 43.43\r\nv 244.77 118.23 49.19\r\nv 234.83 116.18 49.24\r\nv 234.72 116.19 43.49\r\nv 244.95 118.1 55.33\r\nv 235.1 116.03 55.37\r\nv 188.12 20.1 77.05\r\nv 185.95 19.52 66.96\r\nv 186.3 21.43 66.96\r\nv 188.45 22 76.99\r\nv 184.53 19.13 57.9\r\nv 184.89 21.04 57.97\r\nv 196.56 86.64 57.7\r\nv 197.73 86.44 65.26\r\nv 199.57 86.2 73.3\r\nv 189.72 53.6 67.5\r\nv 191.91 53.72 76.17\r\nv 188.29 53.52 59.32\r\nv 226.57 112.79 62.27\r\nv 218.63 109.04 62.54\r\nv 245.25 117.79 62.14\r\nv 235.56 115.71 62.15\r\nv 209.69 98.31 79.13\r\nv 205.57 92.49 80.37\r\nv 192 26.7 86.36\r\nv 192.95 34.71 85.99\r\nv 194.05 44.53 85.54\r\nv 197.63 71.1 83.81\r\nv 196.14 62.79 84.55\r\nv 199.63 78.82 82.85\r\nv 191.18 20.89 86.69\r\nv 191.48 22.78 86.57\r\nv 202.25 85.98 81.66\r\nv 195.03 53.92 85.1\r\nv 217.34 109.52 37.9\r\nv 225.5 113.23 37.89\r\nv 210.32 104.99 37.87\r\nv 217.63 109.33 32.01\r\nv 225.75 113.03 32.07\r\nv 234.76 116.1 37.84\r\nv 244.75 118.12 37.77\r\nv 234.98 115.89 32.03\r\nv 244.91 117.91 31.95\r\nv 199.56 93.6 44.01\r\nv 204.39 99.69 43.87\r\nv 204.59 99.62 50.03\r\nv 199.78 93.53 50.39\r\nv 184.61 24.88 51.62\r\nv 185.42 33.28 51.83\r\nv 184.29 24.76 45.6\r\nv 185.08 33.19 45.36\r\nv 186.06 43.56 45.05\r\nv 186.41 43.62 51.97\r\nv 188.46 62.78 44.64\r\nv 190.24 71.45 44.47\r\nv 190.52 71.41 51.44\r\nv 188.77 62.77 51.71\r\nv 192.6 79.47 44.31\r\nv 192.86 79.41 51.12\r\nv 183.72 18.88 51.39\r\nv 184.1 20.8 51.49\r\nv 183.4 18.75 45.45\r\nv 183.78 20.67 45.58\r\nv 195.67 86.86 44.15\r\nv 195.9 86.79 50.76\r\nv 187.12 53.45 44.82\r\nv 187.46 53.48 51.91\r\nv 204.45 99.66 37.79\r\nv 199.63 93.59 37.7\r\nv 185.12 33.17 38.11\r\nv 184.32 24.73 38.35\r\nv 186.1 43.55 37.81\r\nv 190.31 71.46 37.53\r\nv 188.52 62.79 37.56\r\nv 192.68 79.48 37.55\r\nv 183.43 18.72 38.13\r\nv 183.8 20.64 38.31\r\nv 195.75 86.86 37.61\r\nv 187.18 53.45 37.63\r\nv 226.22 112.67 25.86\r\nv 218.18 109 25.73\r\nv 235.38 115.53 25.83\r\nv 245.21 117.56 25.74\r\nv 256.37 91.4 124.82\r\nv 256.49 98.47 118.85\r\nv 256.53 104.51 111.69\r\nv 255.67 59.1 136.39\r\nv 256.46 112.68 -5.6\r\nv 256.68 109 -14.38\r\nv 256.53 91.22 -36.4\r\nv 256.33 83.64 -41.33\r\nv 256.69 98.15 -30.17\r\nv 256.75 104.11 -22.7\r\nv 255.76 117.38 77.79\r\nv 255.5 118.43 69.64\r\nv 255.01 34.58 140.38\r\nv 254.98 32.69 140.89\r\nv 256.13 75.71 -44.9\r\nv 255.06 119.43 49.17\r\nv 255.03 119.41 43.4\r\nv 255.14 119.32 55.33\r\nv 255.29 119.03 62.15\r\nv 255.05 119.31 37.72\r\nv 255.14 119.1 31.89\r\nv 255.3 118.75 25.65\r\nvn -0.83 -0.1 -0.55\r\nvn -0.84 -0.11 -0.54\r\nvn -0.76 -0.12 -0.64\r\nvn -0.76 -0.1 -0.65\r\nvn -0.84 -0.12 -0.53\r\nvn -0.76 -0.12 -0.63\r\nvn -0.68 -0.12 -0.73\r\nvn -0.67 -0.11 -0.73\r\nvn -0.67 -0.1 -0.73\r\nvn -0.81 -0.27 -0.52\r\nvn -0.82 -0.2 -0.53\r\nvn -0.75 -0.19 -0.63\r\nvn -0.74 -0.26 -0.62\r\nvn -0.83 -0.14 -0.54\r\nvn -0.76 -0.13 -0.64\r\nvn -0.67 -0.13 -0.73\r\nvn -0.67 -0.19 -0.72\r\nvn -0.66 -0.26 -0.71\r\nvn -0.77 -0.46 -0.45\r\nvn -0.71 -0.43 -0.56\r\nvn -0.68 -0.53 -0.5\r\nvn -0.72 -0.57 -0.4\r\nvn -0.62 -0.42 -0.66\r\nvn -0.59 -0.53 -0.61\r\nvn -0.55 -0.63 -0.55\r\nvn -0.59 -0.67 -0.45\r\nvn -0.62 -0.7 -0.35\r\nvn -0.48 -0.09 -0.87\r\nvn -0.48 -0.09 -0.87\r\nvn -0.58 -0.1 -0.81\r\nvn -0.58 -0.1 -0.81\r\nvn -0.48 -0.13 -0.87\r\nvn -0.58 -0.13 -0.81\r\nvn -0.67 -0.1 -0.74\r\nvn -0.25 -0.33 -0.91\r\nvn -0.23 -0.44 -0.87\r\nvn -0.34 -0.41 -0.84\r\nvn -0.36 -0.31 -0.88\r\nvn -0.22 -0.56 -0.8\r\nvn -0.32 -0.53 -0.79\r\nvn -0.41 -0.49 -0.77\r\nvn -0.44 -0.38 -0.81\r\nvn -0.46 -0.28 -0.84\r\nvn -0.2 -0.68 -0.7\r\nvn -0.29 -0.65 -0.7\r\nvn -0.19 -0.79 -0.58\r\nvn -0.28 -0.76 -0.58\r\nvn -0.36 -0.73 -0.58\r\nvn -0.38 -0.61 -0.7\r\nvn -0.27 -0.09 -0.96\r\nvn -0.27 -0.1 -0.96\r\nvn -0.38 -0.1 -0.92\r\nvn -0.38 -0.09 -0.92\r\nvn -0.26 -0.15 -0.95\r\nvn -0.37 -0.14 -0.92\r\nvn -0.19 -0.92 -0.33\r\nvn -0.29 -0.9 -0.34\r\nvn -0.28 -0.85 -0.45\r\nvn -0.19 -0.87 -0.45\r\nvn -0.39 -0.86 -0.34\r\nvn -0.37 -0.81 -0.45\r\nvn -0.69 -0.16 0.71\r\nvn -0.78 -0.14 0.61\r\nvn -0.77 -0.13 0.62\r\nvn -0.68 -0.14 0.72\r\nvn -0.85 -0.13 0.51\r\nvn -0.85 -0.12 0.52\r\nvn -0.84 -0.15 0.52\r\nvn -0.77 -0.15 0.62\r\nvn -0.68 -0.16 0.72\r\nvn -0.82 -0.28 0.5\r\nvn -0.8 -0.37 0.48\r\nvn -0.73 -0.35 0.59\r\nvn -0.75 -0.27 0.61\r\nvn -0.76 -0.47 0.45\r\nvn -0.71 -0.44 0.55\r\nvn -0.62 -0.43 0.66\r\nvn -0.64 -0.35 0.68\r\nvn -0.66 -0.27 0.7\r\nvn -0.85 -0.51 0.11\r\nvn -0.76 -0.64 0.11\r\nvn -0.76 -0.63 0.17\r\nvn -0.84 -0.51 0.18\r\nvn -0.65 -0.75 0.1\r\nvn -0.65 -0.75 0.16\r\nvn -0.64 -0.74 0.22\r\nvn -0.74 -0.62 0.24\r\nvn -0.82 -0.5 0.26\r\nvn -0.91 -0.11 0.39\r\nvn -0.91 -0.12 0.4\r\nvn -0.95 -0.1 0.28\r\nvn -0.95 -0.11 0.29\r\nvn -0.94 -0.15 0.3\r\nvn -0.9 -0.15 0.4\r\nvn -0.91 -0.29 0.29\r\nvn -0.88 -0.39 0.28\r\nvn -0.84 -0.38 0.38\r\nvn -0.87 -0.29 0.39\r\nvn -0.8 -0.49 0.35\r\nvn -0.67 -0.55 0.5\r\nvn -0.58 -0.54 0.61\r\nvn -0.71 -0.59 0.4\r\nvn -0.61 -0.71 0.36\r\nvn -0.58 -0.68 0.45\r\nvn -0.53 -0.64 0.55\r\nvn -0.58 -0.17 0.8\r\nvn -0.57 -0.21 0.79\r\nvn -0.69 -0.19 0.7\r\nvn -0.48 -0.16 0.86\r\nvn -0.47 -0.19 0.86\r\nvn -0.46 -0.23 0.86\r\nvn -0.57 -0.24 0.79\r\nvn -0.7 -0.21 0.68\r\nvn -0.99 -0.09 0.11\r\nvn -0.98 -0.09 0.19\r\nvn -0.98 -0.09 0.18\r\nvn -0.99 -0.08 0.11\r\nvn -0.96 -0.1 0.26\r\nvn -0.97 -0.12 0.23\r\nvn -0.98 -0.1 0.17\r\nvn -0.99 -0.1 0.1\r\nvn -0.98 -0.15 0.12\r\nvn -0.97 -0.22 0.12\r\nvn -0.96 -0.21 0.2\r\nvn -0.97 -0.15 0.2\r\nvn -0.95 -0.3 0.12\r\nvn -0.93 -0.3 0.19\r\nvn -0.93 -0.21 0.29\r\nvn -0.73 -0.61 0.32\r\nvn -0.62 -0.73 0.29\r\nvn -0.47 -0.56 -0.68\r\nvn -0.46 -0.68 -0.57\r\nvn -0.51 -0.44 -0.73\r\nvn -0.57 -0.19 -0.8\r\nvn -0.56 -0.27 -0.78\r\nvn -0.47 -0.2 -0.86\r\nvn -0.5 -0.8 -0.34\r\nvn -0.47 -0.77 -0.43\r\nvn -0.64 -0.73 -0.23\r\nvn -0.64 -0.72 -0.29\r\nvn -0.52 -0.81 -0.27\r\nvn -0.52 -0.83 -0.21\r\nvn -0.4 -0.88 -0.26\r\nvn -0.4 -0.9 -0.19\r\nvn -0.41 -0.51 0.76\r\nvn -0.51 -0.46 0.73\r\nvn -0.46 -0.57 0.68\r\nvn -0.37 -0.62 0.69\r\nvn -0.44 -0.69 0.57\r\nvn -0.34 -0.74 0.58\r\nvn -0.46 -0.31 0.83\r\nvn -0.56 -0.29 0.78\r\nvn -0.54 -0.37 0.76\r\nvn -0.44 -0.4 0.8\r\nvn -0.45 -0.78 0.44\r\nvn -0.35 -0.82 0.46\r\nvn -0.48 -0.8 0.35\r\nvn -0.37 -0.86 0.35\r\nvn -0.38 -0.15 0.91\r\nvn -0.37 -0.18 0.91\r\nvn -0.27 -0.15 0.95\r\nvn -0.27 -0.18 0.94\r\nvn -0.28 -0.24 0.93\r\nvn -0.37 -0.23 0.9\r\nvn -0.5 -0.82 0.28\r\nvn -0.51 -0.83 0.21\r\nvn -0.39 -0.9 0.2\r\nvn -0.38 -0.88 0.27\r\nvn -0.1 -0.81 -0.58\r\nvn -0.1 -0.89 -0.45\r\nvn -0 -0.81 -0.58\r\nvn -0 -0.89 -0.45\r\nvn -0 -0.95 -0.32\r\nvn -0.1 -0.94 -0.33\r\nvn -0.28 -0.92 0.26\r\nvn -0.28 -0.89 0.35\r\nvn -0.28 -0.94 0.19\r\nvn -0.18 -0.97 0.17\r\nvn -0.18 -0.95 0.25\r\nvn -0.19 -0.92 0.35\r\nvn -0.26 -0.35 0.9\r\nvn -0.37 -0.33 0.87\r\nvn -0.35 -0.43 0.83\r\nvn -0.25 -0.46 0.85\r\nvn -0.32 -0.55 0.77\r\nvn -0.23 -0.58 0.78\r\nvn -0.29 -0.66 0.69\r\nvn -0.21 -0.7 0.69\r\nvn -0.27 -0.77 0.58\r\nvn -0.19 -0.8 0.57\r\nvn -0.27 -0.85 0.46\r\nvn -0.19 -0.87 0.45\r\nvn -0.19 -0.97 -0.16\r\nvn -0.29 -0.94 -0.17\r\nvn -0.29 -0.92 -0.25\r\nvn -0.19 -0.95 -0.23\r\nvn -0.14 -0.15 0.98\r\nvn -0.15 -0.19 0.97\r\nvn 0 -0.16 0.99\r\nvn 0 -0.2 0.98\r\nvn 0 -0.26 0.97\r\nvn -0.16 -0.25 0.95\r\nvn -0.09 -0.97 0.24\r\nvn -0.09 -0.94 0.34\r\nvn -0.09 -0.98 0.16\r\nvn 0 -0.99 0.16\r\nvn -0 -0.97 0.24\r\nvn -0 -0.94 0.34\r\nvn -0.69 -0.15 -0.71\r\nvn -0.68 -0.14 -0.72\r\nvn -0.77 -0.14 -0.63\r\nvn -0.77 -0.15 -0.62\r\nvn -0.84 -0.14 -0.52\r\nvn -0.85 -0.15 -0.51\r\nvn -0.5 -0.16 -0.85\r\nvn -0.49 -0.15 -0.86\r\nvn -0.59 -0.14 -0.8\r\nvn -0.6 -0.15 -0.79\r\nvn -0.49 -0.12 -0.86\r\nvn -0.58 -0.12 -0.8\r\nvn -0.7 -0.25 0.67\r\nvn -0.8 -0.21 0.56\r\nvn -0.8 -0.18 0.58\r\nvn -0.7 -0.27 0.66\r\nvn -0.8 -0.23 0.55\r\nvn -0.88 -0.2 0.44\r\nvn -0.88 -0.19 0.45\r\nvn -0.87 -0.16 0.46\r\nvn -0.57 -0.29 0.77\r\nvn -0.57 -0.32 0.76\r\nvn -0.46 -0.31 0.83\r\nvn -0.46 -0.35 0.81\r\nvn -0.98 -0.18 0.09\r\nvn -0.98 -0.15 0.1\r\nvn -0.98 -0.15 0.16\r\nvn -0.97 -0.18 0.16\r\nvn -0.96 -0.16 0.22\r\nvn -0.96 -0.19 0.21\r\nvn -0.94 -0.17 0.31\r\nvn -0.93 -0.2 0.3\r\nvn -0.94 -0.14 0.32\r\nvn -0.31 -0.18 -0.94\r\nvn -0.3 -0.16 -0.94\r\nvn -0.4 -0.15 -0.91\r\nvn -0.4 -0.17 -0.9\r\nvn -0.29 -0.13 -0.95\r\nvn -0.39 -0.12 -0.91\r\nvn -0.38 -0.32 0.87\r\nvn -0.39 -0.37 0.84\r\nvn -0.3 -0.34 0.89\r\nvn -0.31 -0.4 0.86\r\nvn -0.18 -0.36 0.92\r\nvn -0.19 -0.42 0.89\r\nvn 0 -0.37 0.93\r\nvn 0 -0.43 0.9\r\nvn -0.73 -0.34 -0.59\r\nvn -0.64 -0.33 -0.69\r\nvn -0.79 -0.36 -0.49\r\nvn -0.91 -0.4 0.11\r\nvn -0.9 -0.4 0.19\r\nvn -0.37 -0.21 -0.91\r\nvn -0.26 -0.23 -0.94\r\nvn -0.67 -0.21 0.71\r\nvn -0.76 -0.2 0.62\r\nvn -0.83 -0.21 0.51\r\nvn -0.89 -0.21 0.4\r\nvn -0.54 -0.35 -0.76\r\nvn -0.48 -0.17 0.86\r\nvn -0.58 -0.17 0.8\r\nvn -0.57 -0.22 0.79\r\nvn -0.48 -0.23 0.85\r\nvn -0.13 -0.23 -0.96\r\nvn -0.13 -0.34 -0.93\r\nvn -0.14 -0.15 -0.98\r\nvn -0 -0.16 -0.99\r\nvn -0 -0.24 -0.97\r\nvn -0 -0.34 -0.94\r\nvn -0.27 -0.18 0.94\r\nvn -0.38 -0.18 0.91\r\nvn -0.38 -0.24 0.89\r\nvn -0.27 -0.25 0.93\r\nvn -0.76 -0.1 -0.65\r\nvn -0.83 -0.11 -0.55\r\nvn -0.97 -0.11 0.2\r\nvn -0.99 -0.11 0.12\r\nvn -0.58 -0.11 -0.81\r\nvn -0.48 -0.11 -0.87\r\nvn -0.39 -0.1 -0.92\r\nvn -0.28 -0.11 -0.95\r\nvn -0.79 -0.16 0.59\r\nvn -0.86 -0.14 0.48\r\nvn -0.93 -0.12 0.36\r\nvn -0.58 -0.15 0.8\r\nvn -0.48 -0.15 0.86\r\nvn -0.38 -0.15 0.91\r\nvn -0.27 -0.15 0.95\r\nvn -0.16 -0.12 -0.98\r\nvn -0.15 -0.1 -0.98\r\nvn -0.17 -0.14 -0.97\r\nvn -0 -0.16 -0.99\r\nvn -0 -0.14 -0.99\r\nvn -0 -0.11 -0.99\r\nvn -0.14 -0.19 0.97\r\nvn -0.14 -0.15 0.98\r\nvn 0 -0.19 0.98\r\nvn 0 -0.15 0.99\r\nvn -0.85 -0.51 -0.14\r\nvn -0.84 -0.51 -0.21\r\nvn -0.76 -0.62 -0.19\r\nvn -0.76 -0.63 -0.13\r\nvn -0.82 -0.5 -0.29\r\nvn -0.75 -0.61 -0.26\r\nvn -0.65 -0.74 -0.17\r\nvn -0.65 -0.75 -0.12\r\nvn -0.95 -0.11 -0.3\r\nvn -0.94 -0.09 -0.32\r\nvn -0.97 -0.08 -0.23\r\nvn -0.97 -0.1 -0.22\r\nvn -0.94 -0.09 -0.34\r\nvn -0.96 -0.09 -0.25\r\nvn -0.98 -0.08 -0.17\r\nvn -0.98 -0.08 -0.17\r\nvn -0.98 -0.09 -0.16\r\nvn -0.97 -0.15 -0.17\r\nvn -0.96 -0.15 -0.25\r\nvn -0.95 -0.21 -0.24\r\nvn -0.96 -0.21 -0.16\r\nvn -0.93 -0.15 -0.34\r\nvn -0.92 -0.21 -0.33\r\nvn -0.9 -0.29 -0.32\r\nvn -0.93 -0.29 -0.24\r\nvn -0.94 -0.3 -0.16\r\nvn -0.4 -0.91 -0.08\r\nvn -0.4 -0.92 -0.04\r\nvn -0.53 -0.85 -0.05\r\nvn -0.53 -0.84 -0.1\r\nvn -0.4 -0.92 0\r\nvn -0.53 -0.85 -0\r\nvn -0.66 -0.75 -0.01\r\nvn -0.66 -0.75 -0.06\r\nvn -0.18 -0.98 0.01\r\nvn -0.28 -0.96 0.01\r\nvn -0.28 -0.96 -0.03\r\nvn -0.18 -0.98 -0.02\r\nvn -0.28 -0.96 -0.07\r\nvn -0.18 -0.98 -0.05\r\nvn -0.94 -0.15 -0.29\r\nvn -0.95 -0.13 -0.3\r\nvn -0.97 -0.13 -0.21\r\nvn -0.97 -0.16 -0.21\r\nvn -0.98 -0.13 -0.16\r\nvn -0.97 -0.16 -0.16\r\nvn -0.89 -0.39 -0.23\r\nvn -0.9 -0.4 -0.15\r\nvn -0.87 -0.39 -0.31\r\nvn -0.93 -0.11 -0.34\r\nvn -0.96 -0.11 -0.25\r\nvn -0.98 -0.11 -0.17\r\nvn -0.53 -0.84 -0.15\r\nvn -0.4 -0.91 -0.13\r\nvn -0.29 -0.95 -0.11\r\nvn -0.19 -0.98 -0.1\r\nvn -0.8 -0.48 -0.37\r\nvn -0.73 -0.6 -0.33\r\nvn -0.9 -0.11 -0.43\r\nvn -0.9 -0.11 -0.41\r\nvn -0.89 -0.1 -0.44\r\nvn -0.89 -0.14 -0.44\r\nvn -0.88 -0.2 -0.43\r\nvn -0.86 -0.28 -0.42\r\nvn -0.9 -0.13 -0.41\r\nvn -0.9 -0.15 -0.4\r\nvn -0.84 -0.37 -0.4\r\nvn -0.89 -0.11 -0.44\r\nvn -0.4 -0.92 0.04\r\nvn -0.53 -0.85 0.04\r\nvn -0.39 -0.91 0.09\r\nvn -0.52 -0.85 0.09\r\nvn -0.66 -0.75 0.04\r\nvn -0.18 -0.98 0.04\r\nvn -0.28 -0.96 0.04\r\nvn -0.18 -0.98 0.07\r\nvn -0.28 -0.96 0.08\r\nvn -0.86 -0.52 -0.02\r\nvn -0.85 -0.51 -0.08\r\nvn -0.77 -0.64 -0.07\r\nvn -0.77 -0.64 -0.01\r\nvn -0.99 -0.08 -0.1\r\nvn -0.99 -0.1 -0.1\r\nvn -0.99 -0.09 -0.1\r\nvn -1 -0.09 -0.03\r\nvn -1 -0.08 -0.03\r\nvn -0.99 -0.1 -0.03\r\nvn -0.99 -0.15 -0.02\r\nvn -0.98 -0.15 -0.09\r\nvn -0.97 -0.21 -0.09\r\nvn -0.98 -0.22 -0.02\r\nvn -0.95 -0.3 -0.08\r\nvn -0.95 -0.3 -0.02\r\nvn -0.99 -0.14 -0.1\r\nvn -0.98 -0.16 -0.1\r\nvn -0.99 -0.14 -0.03\r\nvn -0.99 -0.17 -0.03\r\nvn -0.91 -0.4 -0.08\r\nvn -0.92 -0.4 -0.02\r\nvn -0.99 -0.11 -0.1\r\nvn -0.99 -0.11 -0.02\r\nvn -0.86 -0.52 0.05\r\nvn -0.77 -0.64 0.05\r\nvn -0.99 -0.1 0.04\r\nvn -1 -0.08 0.04\r\nvn -1 -0.09 0.04\r\nvn -0.99 -0.15 0.05\r\nvn -0.98 -0.22 0.05\r\nvn -0.95 -0.3 0.05\r\nvn -0.99 -0.14 0.03\r\nvn -0.98 -0.17 0.03\r\nvn -0.92 -0.4 0.05\r\nvn -0.99 -0.11 0.04\r\nvn -0.52 -0.84 0.15\r\nvn -0.39 -0.91 0.14\r\nvn -0.18 -0.98 0.12\r\nvn -0.28 -0.95 0.13\r\nvn 0 -0.59 -0.81\r\nvn -0.11 -0.58 -0.81\r\nvn -0.12 -0.46 -0.88\r\nvn 0 -0.46 -0.89\r\nvn -0.11 -0.7 -0.7\r\nvn -0 -0.71 -0.7\r\nvn -0.14 -0.11 -0.98\r\nvn 0 -0.11 -0.99\r\nvn -0 -0.82 0.57\r\nvn -0.1 -0.82 0.57\r\nvn -0.1 -0.89 0.45\r\nvn -0 -0.89 0.45\r\nvn 0 -0.37 0.93\r\nvn -0.14 -0.36 0.92\r\nvn -0.13 -0.48 0.87\r\nvn 0 -0.49 0.87\r\nvn -0.12 -0.6 0.79\r\nvn -0 -0.61 0.79\r\nvn -0.11 -0.72 0.69\r\nvn -0 -0.73 0.69\r\nvn -0 -0.99 -0.14\r\nvn -0.09 -0.98 -0.15\r\nvn -0.1 -0.97 -0.23\r\nvn -0 -0.98 -0.22\r\nvn -0.17 -0.17 -0.97\r\nvn -0 -0.18 -0.98\r\nvn -0.18 -0.19 -0.97\r\nvn -0 -0.19 -0.98\r\nvn -0.14 -0.26 0.95\r\nvn 0 -0.27 0.96\r\nvn 0 -1 0.02\r\nvn -0.09 -1 0.01\r\nvn -0.09 -1 -0.01\r\nvn -0 -1 -0.01\r\nvn -0.09 -1 -0.04\r\nvn -0 -1 -0.04\r\nvn -0.09 -0.99 -0.09\r\nvn -0 -1 -0.08\r\nvn -0 -1 0.06\r\nvn -0.09 -0.99 0.07\r\nvn -0.09 -1 0.04\r\nvn -0 -1 0.04\r\nvn -0.09 -0.99 0.11\r\nvn -0 -0.99 0.1\r\nvn 0.83 -0.1 -0.55\r\nvn 0.76 -0.1 -0.65\r\nvn 0.76 -0.12 -0.64\r\nvn 0.84 -0.11 -0.54\r\nvn 0.67 -0.1 -0.73\r\nvn 0.67 -0.11 -0.73\r\nvn 0.68 -0.12 -0.73\r\nvn 0.76 -0.12 -0.63\r\nvn 0.84 -0.12 -0.53\r\nvn 0.81 -0.27 -0.52\r\nvn 0.74 -0.26 -0.62\r\nvn 0.75 -0.19 -0.63\r\nvn 0.82 -0.2 -0.53\r\nvn 0.66 -0.26 -0.71\r\nvn 0.67 -0.19 -0.72\r\nvn 0.67 -0.13 -0.73\r\nvn 0.76 -0.13 -0.64\r\nvn 0.83 -0.14 -0.54\r\nvn 0.77 -0.46 -0.45\r\nvn 0.72 -0.57 -0.4\r\nvn 0.68 -0.53 -0.5\r\nvn 0.71 -0.43 -0.56\r\nvn 0.62 -0.7 -0.35\r\nvn 0.59 -0.67 -0.45\r\nvn 0.55 -0.63 -0.55\r\nvn 0.59 -0.53 -0.61\r\nvn 0.62 -0.42 -0.66\r\nvn 0.48 -0.09 -0.87\r\nvn 0.58 -0.1 -0.81\r\nvn 0.58 -0.1 -0.81\r\nvn 0.48 -0.09 -0.87\r\nvn 0.67 -0.1 -0.74\r\nvn 0.58 -0.13 -0.81\r\nvn 0.48 -0.13 -0.87\r\nvn 0.25 -0.33 -0.91\r\nvn 0.36 -0.31 -0.88\r\nvn 0.34 -0.41 -0.84\r\nvn 0.23 -0.44 -0.87\r\nvn 0.46 -0.28 -0.84\r\nvn 0.44 -0.38 -0.81\r\nvn 0.41 -0.49 -0.77\r\nvn 0.32 -0.53 -0.79\r\nvn 0.22 -0.56 -0.8\r\nvn 0.29 -0.65 -0.7\r\nvn 0.2 -0.68 -0.7\r\nvn 0.38 -0.61 -0.7\r\nvn 0.36 -0.73 -0.58\r\nvn 0.28 -0.76 -0.58\r\nvn 0.19 -0.79 -0.58\r\nvn 0.27 -0.09 -0.96\r\nvn 0.38 -0.09 -0.92\r\nvn 0.38 -0.1 -0.92\r\nvn 0.27 -0.1 -0.96\r\nvn 0.37 -0.14 -0.92\r\nvn 0.26 -0.15 -0.95\r\nvn 0.19 -0.92 -0.33\r\nvn 0.19 -0.87 -0.45\r\nvn 0.28 -0.85 -0.45\r\nvn 0.29 -0.9 -0.34\r\nvn 0.37 -0.81 -0.45\r\nvn 0.39 -0.86 -0.34\r\nvn 0.69 -0.16 0.71\r\nvn 0.68 -0.14 0.72\r\nvn 0.77 -0.13 0.62\r\nvn 0.78 -0.14 0.61\r\nvn 0.68 -0.16 0.72\r\nvn 0.77 -0.15 0.62\r\nvn 0.84 -0.15 0.52\r\nvn 0.85 -0.12 0.52\r\nvn 0.85 -0.13 0.51\r\nvn 0.82 -0.28 0.5\r\nvn 0.75 -0.27 0.61\r\nvn 0.73 -0.35 0.59\r\nvn 0.8 -0.37 0.48\r\nvn 0.66 -0.27 0.7\r\nvn 0.64 -0.35 0.68\r\nvn 0.62 -0.43 0.66\r\nvn 0.71 -0.44 0.55\r\nvn 0.76 -0.47 0.45\r\nvn 0.85 -0.51 0.11\r\nvn 0.84 -0.51 0.18\r\nvn 0.76 -0.63 0.17\r\nvn 0.76 -0.64 0.11\r\nvn 0.82 -0.5 0.26\r\nvn 0.74 -0.62 0.24\r\nvn 0.64 -0.74 0.22\r\nvn 0.65 -0.75 0.16\r\nvn 0.65 -0.75 0.1\r\nvn 0.91 -0.12 0.4\r\nvn 0.91 -0.11 0.39\r\nvn 0.9 -0.15 0.4\r\nvn 0.94 -0.15 0.3\r\nvn 0.95 -0.11 0.29\r\nvn 0.95 -0.1 0.28\r\nvn 0.91 -0.29 0.29\r\nvn 0.87 -0.29 0.39\r\nvn 0.84 -0.38 0.38\r\nvn 0.88 -0.39 0.28\r\nvn 0.8 -0.49 0.35\r\nvn 0.58 -0.54 0.61\r\nvn 0.67 -0.55 0.5\r\nvn 0.53 -0.64 0.55\r\nvn 0.58 -0.68 0.45\r\nvn 0.61 -0.71 0.36\r\nvn 0.71 -0.59 0.4\r\nvn 0.69 -0.19 0.7\r\nvn 0.57 -0.21 0.79\r\nvn 0.58 -0.17 0.8\r\nvn 0.7 -0.21 0.68\r\nvn 0.57 -0.24 0.79\r\nvn 0.46 -0.23 0.86\r\nvn 0.47 -0.19 0.86\r\nvn 0.48 -0.16 0.86\r\nvn 0.99 -0.09 0.11\r\nvn 0.99 -0.08 0.11\r\nvn 0.98 -0.09 0.18\r\nvn 0.98 -0.09 0.19\r\nvn 0.99 -0.1 0.1\r\nvn 0.98 -0.1 0.17\r\nvn 0.97 -0.12 0.23\r\nvn 0.96 -0.1 0.26\r\nvn 0.98 -0.15 0.12\r\nvn 0.97 -0.15 0.2\r\nvn 0.96 -0.21 0.2\r\nvn 0.97 -0.22 0.12\r\nvn 0.93 -0.21 0.29\r\nvn 0.93 -0.3 0.19\r\nvn 0.95 -0.3 0.12\r\nvn 0.73 -0.61 0.32\r\nvn 0.62 -0.73 0.29\r\nvn 0.46 -0.68 -0.57\r\nvn 0.47 -0.56 -0.68\r\nvn 0.51 -0.44 -0.73\r\nvn 0.56 -0.27 -0.78\r\nvn 0.57 -0.19 -0.8\r\nvn 0.47 -0.2 -0.86\r\nvn 0.47 -0.77 -0.43\r\nvn 0.5 -0.8 -0.34\r\nvn 0.64 -0.73 -0.23\r\nvn 0.52 -0.83 -0.21\r\nvn 0.52 -0.81 -0.27\r\nvn 0.64 -0.72 -0.29\r\nvn 0.4 -0.9 -0.19\r\nvn 0.4 -0.88 -0.26\r\nvn 0.41 -0.51 0.76\r\nvn 0.37 -0.62 0.69\r\nvn 0.46 -0.57 0.68\r\nvn 0.51 -0.46 0.73\r\nvn 0.34 -0.74 0.58\r\nvn 0.44 -0.69 0.57\r\nvn 0.46 -0.31 0.83\r\nvn 0.44 -0.4 0.8\r\nvn 0.54 -0.37 0.76\r\nvn 0.56 -0.29 0.78\r\nvn 0.35 -0.82 0.46\r\nvn 0.45 -0.78 0.44\r\nvn 0.37 -0.86 0.35\r\nvn 0.48 -0.8 0.35\r\nvn 0.37 -0.18 0.91\r\nvn 0.38 -0.15 0.91\r\nvn 0.37 -0.23 0.9\r\nvn 0.28 -0.24 0.93\r\nvn 0.27 -0.18 0.94\r\nvn 0.27 -0.15 0.95\r\nvn 0.5 -0.82 0.28\r\nvn 0.38 -0.88 0.27\r\nvn 0.39 -0.9 0.2\r\nvn 0.51 -0.83 0.21\r\nvn 0.1 -0.89 -0.45\r\nvn 0.1 -0.81 -0.58\r\nvn 0.1 -0.94 -0.33\r\nvn 0.28 -0.89 0.35\r\nvn 0.28 -0.92 0.26\r\nvn 0.19 -0.92 0.35\r\nvn 0.18 -0.95 0.25\r\nvn 0.18 -0.97 0.17\r\nvn 0.28 -0.94 0.19\r\nvn 0.26 -0.35 0.9\r\nvn 0.25 -0.46 0.85\r\nvn 0.35 -0.43 0.83\r\nvn 0.37 -0.33 0.87\r\nvn 0.23 -0.58 0.78\r\nvn 0.32 -0.55 0.77\r\nvn 0.21 -0.7 0.69\r\nvn 0.29 -0.66 0.69\r\nvn 0.19 -0.8 0.57\r\nvn 0.27 -0.77 0.58\r\nvn 0.27 -0.85 0.46\r\nvn 0.19 -0.87 0.45\r\nvn 0.19 -0.97 -0.16\r\nvn 0.19 -0.95 -0.23\r\nvn 0.29 -0.92 -0.25\r\nvn 0.29 -0.94 -0.17\r\nvn 0.15 -0.19 0.97\r\nvn 0.14 -0.15 0.98\r\nvn 0.16 -0.25 0.95\r\nvn 0.09 -0.94 0.34\r\nvn 0.09 -0.97 0.24\r\nvn 0.09 -0.98 0.16\r\nvn 0.69 -0.15 -0.71\r\nvn 0.77 -0.15 -0.62\r\nvn 0.77 -0.14 -0.63\r\nvn 0.68 -0.14 -0.72\r\nvn 0.85 -0.15 -0.51\r\nvn 0.84 -0.14 -0.52\r\nvn 0.5 -0.16 -0.85\r\nvn 0.6 -0.15 -0.79\r\nvn 0.59 -0.14 -0.8\r\nvn 0.49 -0.15 -0.86\r\nvn 0.58 -0.12 -0.8\r\nvn 0.49 -0.12 -0.86\r\nvn 0.8 -0.18 0.58\r\nvn 0.8 -0.21 0.56\r\nvn 0.7 -0.25 0.67\r\nvn 0.87 -0.16 0.46\r\nvn 0.88 -0.19 0.45\r\nvn 0.88 -0.2 0.44\r\nvn 0.8 -0.23 0.55\r\nvn 0.7 -0.27 0.66\r\nvn 0.57 -0.32 0.76\r\nvn 0.57 -0.29 0.77\r\nvn 0.46 -0.35 0.81\r\nvn 0.46 -0.31 0.83\r\nvn 0.98 -0.18 0.09\r\nvn 0.97 -0.18 0.16\r\nvn 0.98 -0.15 0.16\r\nvn 0.98 -0.15 0.1\r\nvn 0.96 -0.19 0.21\r\nvn 0.96 -0.16 0.22\r\nvn 0.93 -0.2 0.3\r\nvn 0.94 -0.17 0.31\r\nvn 0.94 -0.14 0.32\r\nvn 0.31 -0.18 -0.94\r\nvn 0.4 -0.17 -0.9\r\nvn 0.4 -0.15 -0.91\r\nvn 0.3 -0.16 -0.94\r\nvn 0.39 -0.12 -0.91\r\nvn 0.29 -0.13 -0.95\r\nvn 0.39 -0.37 0.84\r\nvn 0.38 -0.32 0.87\r\nvn 0.31 -0.4 0.86\r\nvn 0.3 -0.34 0.89\r\nvn 0.19 -0.42 0.89\r\nvn 0.18 -0.36 0.92\r\nvn 0.64 -0.33 -0.69\r\nvn 0.73 -0.34 -0.59\r\nvn 0.79 -0.36 -0.49\r\nvn 0.9 -0.4 0.19\r\nvn 0.91 -0.4 0.11\r\nvn 0.37 -0.21 -0.91\r\nvn 0.26 -0.23 -0.94\r\nvn 0.76 -0.2 0.62\r\nvn 0.67 -0.21 0.71\r\nvn 0.83 -0.21 0.51\r\nvn 0.89 -0.21 0.4\r\nvn 0.54 -0.35 -0.76\r\nvn 0.48 -0.17 0.86\r\nvn 0.48 -0.23 0.85\r\nvn 0.57 -0.22 0.79\r\nvn 0.58 -0.17 0.8\r\nvn 0.13 -0.34 -0.93\r\nvn 0.13 -0.23 -0.96\r\nvn 0.14 -0.15 -0.98\r\nvn 0.27 -0.18 0.94\r\nvn 0.27 -0.25 0.93\r\nvn 0.38 -0.24 0.89\r\nvn 0.38 -0.18 0.91\r\nvn 0.76 -0.1 -0.65\r\nvn 0.83 -0.11 -0.55\r\nvn 0.99 -0.11 0.12\r\nvn 0.97 -0.11 0.2\r\nvn 0.58 -0.11 -0.81\r\nvn 0.48 -0.11 -0.87\r\nvn 0.39 -0.1 -0.92\r\nvn 0.28 -0.11 -0.95\r\nvn 0.79 -0.16 0.59\r\nvn 0.86 -0.14 0.48\r\nvn 0.93 -0.12 0.36\r\nvn 0.58 -0.15 0.8\r\nvn 0.48 -0.15 0.86\r\nvn 0.38 -0.15 0.91\r\nvn 0.27 -0.15 0.95\r\nvn 0.15 -0.1 -0.98\r\nvn 0.16 -0.12 -0.98\r\nvn 0.17 -0.14 -0.97\r\nvn 0.14 -0.15 0.98\r\nvn 0.14 -0.19 0.97\r\nvn 0.85 -0.51 -0.14\r\nvn 0.76 -0.63 -0.13\r\nvn 0.76 -0.62 -0.19\r\nvn 0.84 -0.51 -0.21\r\nvn 0.65 -0.75 -0.12\r\nvn 0.65 -0.74 -0.17\r\nvn 0.75 -0.61 -0.26\r\nvn 0.82 -0.5 -0.29\r\nvn 0.95 -0.11 -0.3\r\nvn 0.97 -0.1 -0.22\r\nvn 0.97 -0.08 -0.23\r\nvn 0.94 -0.09 -0.32\r\nvn 0.98 -0.09 -0.16\r\nvn 0.98 -0.08 -0.17\r\nvn 0.98 -0.08 -0.17\r\nvn 0.96 -0.09 -0.25\r\nvn 0.94 -0.09 -0.34\r\nvn 0.97 -0.15 -0.17\r\nvn 0.96 -0.21 -0.16\r\nvn 0.95 -0.21 -0.24\r\nvn 0.96 -0.15 -0.25\r\nvn 0.94 -0.3 -0.16\r\nvn 0.93 -0.29 -0.24\r\nvn 0.9 -0.29 -0.32\r\nvn 0.92 -0.21 -0.33\r\nvn 0.93 -0.15 -0.34\r\nvn 0.4 -0.91 -0.08\r\nvn 0.53 -0.84 -0.1\r\nvn 0.53 -0.85 -0.05\r\nvn 0.4 -0.92 -0.04\r\nvn 0.66 -0.75 -0.06\r\nvn 0.66 -0.75 -0.01\r\nvn 0.53 -0.85 -0\r\nvn 0.4 -0.92 0\r\nvn 0.18 -0.98 0.01\r\nvn 0.18 -0.98 -0.02\r\nvn 0.28 -0.96 -0.03\r\nvn 0.28 -0.96 0.01\r\nvn 0.18 -0.98 -0.05\r\nvn 0.28 -0.96 -0.07\r\nvn 0.94 -0.15 -0.29\r\nvn 0.97 -0.16 -0.21\r\nvn 0.97 -0.13 -0.21\r\nvn 0.95 -0.13 -0.3\r\nvn 0.97 -0.16 -0.16\r\nvn 0.98 -0.13 -0.16\r\nvn 0.9 -0.4 -0.15\r\nvn 0.89 -0.39 -0.23\r\nvn 0.87 -0.39 -0.31\r\nvn 0.96 -0.11 -0.25\r\nvn 0.93 -0.11 -0.34\r\nvn 0.98 -0.11 -0.17\r\nvn 0.4 -0.91 -0.13\r\nvn 0.53 -0.84 -0.15\r\nvn 0.19 -0.98 -0.1\r\nvn 0.29 -0.95 -0.11\r\nvn 0.73 -0.6 -0.33\r\nvn 0.8 -0.48 -0.37\r\nvn 0.9 -0.11 -0.41\r\nvn 0.9 -0.11 -0.43\r\nvn 0.89 -0.1 -0.44\r\nvn 0.88 -0.2 -0.43\r\nvn 0.89 -0.14 -0.44\r\nvn 0.86 -0.28 -0.42\r\nvn 0.9 -0.15 -0.4\r\nvn 0.9 -0.13 -0.41\r\nvn 0.84 -0.37 -0.4\r\nvn 0.89 -0.11 -0.44\r\nvn 0.53 -0.85 0.04\r\nvn 0.4 -0.92 0.04\r\nvn 0.66 -0.75 0.04\r\nvn 0.52 -0.85 0.09\r\nvn 0.39 -0.91 0.09\r\nvn 0.28 -0.96 0.04\r\nvn 0.18 -0.98 0.04\r\nvn 0.28 -0.96 0.08\r\nvn 0.18 -0.98 0.07\r\nvn 0.86 -0.52 -0.02\r\nvn 0.77 -0.64 -0.01\r\nvn 0.77 -0.64 -0.07\r\nvn 0.85 -0.51 -0.08\r\nvn 0.99 -0.1 -0.1\r\nvn 0.99 -0.08 -0.1\r\nvn 0.99 -0.1 -0.03\r\nvn 1 -0.08 -0.03\r\nvn 1 -0.09 -0.03\r\nvn 0.99 -0.09 -0.1\r\nvn 0.99 -0.15 -0.02\r\nvn 0.98 -0.22 -0.02\r\nvn 0.97 -0.21 -0.09\r\nvn 0.98 -0.15 -0.09\r\nvn 0.95 -0.3 -0.02\r\nvn 0.95 -0.3 -0.08\r\nvn 0.98 -0.16 -0.1\r\nvn 0.99 -0.14 -0.1\r\nvn 0.99 -0.17 -0.03\r\nvn 0.99 -0.14 -0.03\r\nvn 0.92 -0.4 -0.02\r\nvn 0.91 -0.4 -0.08\r\nvn 0.99 -0.11 -0.02\r\nvn 0.99 -0.11 -0.1\r\nvn 0.77 -0.64 0.05\r\nvn 0.86 -0.52 0.05\r\nvn 1 -0.08 0.04\r\nvn 0.99 -0.1 0.04\r\nvn 1 -0.09 0.04\r\nvn 0.98 -0.22 0.05\r\nvn 0.99 -0.15 0.05\r\nvn 0.95 -0.3 0.05\r\nvn 0.98 -0.17 0.03\r\nvn 0.99 -0.14 0.03\r\nvn 0.92 -0.4 0.05\r\nvn 0.99 -0.11 0.04\r\nvn 0.39 -0.91 0.14\r\nvn 0.52 -0.84 0.15\r\nvn 0.28 -0.95 0.13\r\nvn 0.18 -0.98 0.12\r\nvn 0.12 -0.46 -0.88\r\nvn 0.11 -0.58 -0.81\r\nvn 0.11 -0.7 -0.7\r\nvn 0.14 -0.11 -0.98\r\nvn 0.1 -0.89 0.45\r\nvn 0.1 -0.82 0.57\r\nvn 0.13 -0.48 0.87\r\nvn 0.14 -0.36 0.92\r\nvn 0.12 -0.6 0.79\r\nvn 0.11 -0.72 0.69\r\nvn 0.1 -0.97 -0.23\r\nvn 0.09 -0.98 -0.15\r\nvn 0.17 -0.17 -0.97\r\nvn 0.18 -0.19 -0.97\r\nvn 0.14 -0.26 0.95\r\nvn 0.09 -1 -0.01\r\nvn 0.09 -1 0.01\r\nvn 0.09 -1 -0.04\r\nvn 0.09 -0.99 -0.09\r\nvn 0.09 -1 0.04\r\nvn 0.09 -0.99 0.07\r\nvn 0.09 -0.99 0.11\r\nvt 0.1447 0.4214\r\nvt 0.1447 0.3783\r\nvt 0.0798 0.3783\r\nvt 0.0798 0.4214\r\nvt 0.1447 0.3446\r\nvt 0.0798 0.3446\r\nvt 0 0.3333\r\nvt 0 0.3783\r\nvt 0 0.4232\r\nvt 0.1447 0.6905\r\nvt 0.1447 0.5971\r\nvt 0.0798 0.5971\r\nvt 0.0798 0.6905\r\nvt 0.1447 0.514\r\nvt 0.0798 0.514\r\nvt 0 0.4988\r\nvt 0 0.5971\r\nvt 0 0.6954\r\nvt 0.1447 0.8515\r\nvt 0.0798 0.8515\r\nvt 0.0798 0.9264\r\nvt 0.1447 0.9264\r\nvt 0 0.8527\r\nvt 0 0.9264\r\nvt 0 1\r\nvt 0.0798 1\r\nvt 0.1596 1\r\nvt 0.5786 0.8801\r\nvt 0.539 0.8801\r\nvt 0.539 0.9381\r\nvt 0.5786 0.9381\r\nvt 0.486 0.8801\r\nvt 0.486 0.9381\r\nvt 0.5012 1\r\nvt 0.539 1\r\nvt 0.5768 1\r\nvt 0.3095 0.7805\r\nvt 0.2259 0.7805\r\nvt 0.2259 0.8296\r\nvt 0.3095 0.8296\r\nvt 0.1485 0.7805\r\nvt 0.1485 0.8296\r\nvt 0.1485 0.8801\r\nvt 0.2259 0.8801\r\nvt 0.3095 0.8801\r\nvt 0.0736 0.7805\r\nvt 0.0736 0.8296\r\nvt 0 0.783\r\nvt 0 0.8296\r\nvt 0 0.8762\r\nvt 0.0736 0.8801\r\nvt 0.5786 0.7805\r\nvt 0.539 0.7805\r\nvt 0.539 0.8296\r\nvt 0.5786 0.8296\r\nvt 0.486 0.7805\r\nvt 0.486 0.8296\r\nvt 0.8553 0.7806\r\nvt 0.8553 0.8296\r\nvt 0.9202 0.8296\r\nvt 0.9202 0.7806\r\nvt 0.8553 0.8801\r\nvt 0.9202 0.8801\r\nvt 1 0.8762\r\nvt 1 0.8296\r\nvt 1 0.783\r\nvt 0.0798 0.461\r\nvt 0 0.461\r\nvt 0.1508 0.4214\r\nvt 0.1508 0.461\r\nvt 0.1508 0.514\r\nvt 0.1508 0.6905\r\nvt 0.1508 0.7741\r\nvt 0.0798 0.7741\r\nvt 0.1508 0.8515\r\nvt 0 0.7741\r\nvt 0.2379 0.8515\r\nvt 0.2379 0.9264\r\nvt 0.2432 0.9264\r\nvt 0.2432 0.8515\r\nvt 0.2371 1\r\nvt 0.2432 1\r\nvt 0.2493 1\r\nvt 0.2366 0.9264\r\nvt 0.2366 0.8515\r\nvt 0.2044 0.4214\r\nvt 0.2044 0.461\r\nvt 0.2366 0.4214\r\nvt 0.2366 0.461\r\nvt 0.2366 0.514\r\nvt 0.2044 0.514\r\nvt 0.2366 0.6905\r\nvt 0.2366 0.7741\r\nvt 0.2044 0.7741\r\nvt 0.2044 0.6905\r\nvt 0.2044 0.8515\r\nvt 0.1508 0.9264\r\nvt 0.6217 0.9381\r\nvt 0.6217 1\r\nvt 0.6217 0.8801\r\nvt 0.6554 0.8801\r\nvt 0.6554 0.9381\r\nvt 0.6667 1\r\nvt 0.2379 0.4214\r\nvt 0.2432 0.4214\r\nvt 0.2432 0.3783\r\nvt 0.2379 0.3783\r\nvt 0.2366 0.3783\r\nvt 0.2366 0.3446\r\nvt 0.2432 0.3446\r\nvt 0.2379 0.3446\r\nvt 0.2379 0.514\r\nvt 0.2379 0.5971\r\nvt 0.2432 0.5971\r\nvt 0.2432 0.514\r\nvt 0.2379 0.6905\r\nvt 0.2432 0.6905\r\nvt 0.2366 0.5971\r\nvt 0.2044 0.9264\r\nvt 0.2044 1\r\nvt 0.0736 1\r\nvt 0.0736 0.9381\r\nvt 0 0.9381\r\nvt 0.1473 1\r\nvt 0.1485 0.9381\r\nvt 0.3046 1\r\nvt 0.4029 1\r\nvt 0.4029 0.9381\r\nvt 0.3095 0.9381\r\nvt 0.4029 0.8801\r\nvt 0.8553 0.9381\r\nvt 0.9202 0.9381\r\nvt 0.8404 1\r\nvt 0.9202 1\r\nvt 1 1\r\nvt 1 0.9381\r\nvt 0.7999 1\r\nvt 0.8202 1\r\nvt 0.8202 0.9381\r\nvt 0.8021 0.9381\r\nvt 0.8202 0.8801\r\nvt 0.8021 0.8801\r\nvt 0.2259 0.9381\r\nvt 0.2259 1\r\nvt 0.8492 0.9381\r\nvt 0.8492 0.8801\r\nvt 0.6217 0.8296\r\nvt 0.5786 0.7813\r\nvt 0.6217 0.7813\r\nvt 0.6554 0.7813\r\nvt 0.6554 0.8296\r\nvt 0.7956 1\r\nvt 0.7956 0.9381\r\nvt 0.7507 1\r\nvt 0.7634 0.9381\r\nvt 0.7634 0.8801\r\nvt 0.7956 0.8801\r\nvt 1 0.7266\r\nvt 0.9202 0.7268\r\nvt 1 0.6703\r\nvt 0.9202 0.6707\r\nvt 0.8404 0.6711\r\nvt 0.8553 0.7271\r\nvt 0.7956 0.8296\r\nvt 0.8492 0.8296\r\nvt 0.7634 0.8296\r\nvt 0.7634 0.7813\r\nvt 0.7956 0.7814\r\nvt 0.8492 0.7815\r\nvt 0.3095 0.7815\r\nvt 0.2259 0.7816\r\nvt 0.1485 0.7816\r\nvt 0.0736 0.7817\r\nvt 0.9202 0.7816\r\nvt 0.8021 0.7808\r\nvt 0.8021 0.8296\r\nvt 0.8202 0.8296\r\nvt 0.8202 0.7807\r\nvt 0.5786 0.7299\r\nvt 0.6217 0.7298\r\nvt 0.5768 0.6768\r\nvt 0.6217 0.6766\r\nvt 0.6667 0.6763\r\nvt 0.6554 0.7297\r\nvt 0.7956 0.73\r\nvt 0.8492 0.7304\r\nvt 0.7634 0.7297\r\nvt 0.7507 0.6763\r\nvt 0.7956 0.6771\r\nvt 0.8404 0.6779\r\nvt 0.0798 0.3333\r\nvt 0.1447 0.3333\r\nvt 0.6667 0.8801\r\nvt 0.6667 0.9381\r\nvt 0.1508 0.3333\r\nvt 0.1508 0.3446\r\nvt 0.2379 0.3333\r\nvt 0.2432 0.3333\r\nvt 0.2366 0.3333\r\nvt 0.2044 0.3333\r\nvt 0.2044 0.3446\r\nvt 0.6667 0.7805\r\nvt 0.6667 0.8296\r\nvt 0.6554 0.7805\r\nvt 0.6667 0.7813\r\nvt 0.6667 0.7296\r\nvt 0.1447 0.7741\r\nvt 0.2379 0.7741\r\nvt 0.2432 0.7741\r\nvt 0.4029 0.8296\r\nvt 0.4029 0.7805\r\nvt 0.1508 0.5971\r\nvt 0.2044 0.5971\r\nvt 0.4029 0.7264\r\nvt 0.3095 0.7264\r\nvt 0.486 0.7264\r\nvt 0.5012 0.6698\r\nvt 0.4029 0.6698\r\nvt 0.3046 0.6697\r\nvt 0.486 0.7814\r\nvt 0.4029 0.7815\r\nvt 0.1447 0.461\r\nvt 0.2432 0.461\r\nvt 0.2379 0.461\r\nvt 0.6217 0.7805\r\nvt 0.1508 0.3783\r\nvt 0.2044 0.3783\r\nvt 0.539 0.7814\r\nvt 0.6217 0.7264\r\nvt 0.5786 0.7264\r\nvt 0.6554 0.7264\r\nvt 0.6667 0.6699\r\nvt 0.6217 0.6699\r\nvt 0.5768 0.6698\r\nvt 0.486 0.7301\r\nvt 0.539 0.73\r\nvt 0.5012 0.6772\r\nvt 0.539 0.677\r\nvt 0.2208 0.8515\r\nvt 0.2114 0.8515\r\nvt 0.2114 0.9264\r\nvt 0.2208 0.9264\r\nvt 0.1979 0.8515\r\nvt 0.1979 0.9264\r\nvt 0.2001 1\r\nvt 0.2114 1\r\nvt 0.2226 1\r\nvt 0.1979 0.3446\r\nvt 0.1979 0.3783\r\nvt 0.2114 0.3783\r\nvt 0.2114 0.3446\r\nvt 0.1979 0.4214\r\nvt 0.2114 0.4214\r\nvt 0.2208 0.4214\r\nvt 0.2208 0.3783\r\nvt 0.2208 0.3446\r\nvt 0.2208 0.514\r\nvt 0.2114 0.514\r\nvt 0.2114 0.5971\r\nvt 0.2208 0.5971\r\nvt 0.1979 0.514\r\nvt 0.1979 0.5971\r\nvt 0.1979 0.6905\r\nvt 0.2114 0.6905\r\nvt 0.2208 0.6905\r\nvt 0.7792 0.8801\r\nvt 0.7733 0.8801\r\nvt 0.7733 0.9381\r\nvt 0.7792 0.9381\r\nvt 0.7695 0.8801\r\nvt 0.7695 0.9381\r\nvt 0.7693 1\r\nvt 0.7733 1\r\nvt 0.7774 1\r\nvt 0.7695 0.781\r\nvt 0.7695 0.8296\r\nvt 0.7733 0.8296\r\nvt 0.7733 0.781\r\nvt 0.7792 0.8296\r\nvt 0.7792 0.7809\r\nvt 0.1979 0.3333\r\nvt 0.2114 0.3333\r\nvt 0.2208 0.3333\r\nvt 0.2114 0.7741\r\nvt 0.2208 0.7741\r\nvt 0.1979 0.7741\r\nvt 0.1979 0.461\r\nvt 0.2114 0.461\r\nvt 0.2208 0.461\r\nvt 0.7886 0.9381\r\nvt 0.7886 0.8801\r\nvt 0.7886 1\r\nvt 0.7886 0.8296\r\nvt 0.7886 0.7808\r\nvt 0.1798 0.8515\r\nvt 0.1798 0.9264\r\nvt 0.1798 1\r\nvt 0.1798 0.3783\r\nvt 0.1798 0.3446\r\nvt 0.1798 0.4214\r\nvt 0.1798 0.514\r\nvt 0.1798 0.5971\r\nvt 0.1798 0.6905\r\nvt 0.1798 0.3333\r\nvt 0.1798 0.7741\r\nvt 0.1798 0.461\r\nvt 0.7661 0.8801\r\nvt 0.7661 0.9381\r\nvt 0.7621 0.8801\r\nvt 0.7621 0.9381\r\nvt 0.7629 1\r\nvt 0.7661 1\r\nvt 0.7661 0.7811\r\nvt 0.7661 0.8296\r\nvt 0.7621 0.7811\r\nvt 0.7621 0.8296\r\nvt 0.2305 0.8515\r\nvt 0.2267 0.8515\r\nvt 0.2267 0.9264\r\nvt 0.2305 0.9264\r\nvt 0.2267 1\r\nvt 0.2307 1\r\nvt 0.2267 0.3783\r\nvt 0.2267 0.3446\r\nvt 0.2267 0.4214\r\nvt 0.2305 0.4214\r\nvt 0.2305 0.3783\r\nvt 0.2305 0.3446\r\nvt 0.2305 0.514\r\nvt 0.2267 0.514\r\nvt 0.2267 0.5971\r\nvt 0.2305 0.5971\r\nvt 0.2267 0.6905\r\nvt 0.2305 0.6905\r\nvt 0.2267 0.3333\r\nvt 0.2305 0.3333\r\nvt 0.2267 0.7741\r\nvt 0.2305 0.7741\r\nvt 0.2267 0.461\r\nvt 0.2305 0.461\r\nvt 0.2339 0.8515\r\nvt 0.2339 0.9264\r\nvt 0.2339 1\r\nvt 0.2339 0.3446\r\nvt 0.2339 0.3783\r\nvt 0.2339 0.4214\r\nvt 0.2339 0.514\r\nvt 0.2339 0.5971\r\nvt 0.2339 0.6905\r\nvt 0.2339 0.3333\r\nvt 0.2339 0.7741\r\nvt 0.2339 0.461\r\nvt 0.7568 0.9381\r\nvt 0.7568 0.8801\r\nvt 0.7568 1\r\nvt 0.7568 0.7812\r\nvt 0.7568 0.8296\r\nvt 0.1473 0.6697\r\nvt 0.1485 0.7264\r\nvt 0.2259 0.7263\r\nvt 0.2259 0.6697\r\nvt 0 0.6703\r\nvt 0 0.7266\r\nvt 0.0736 0.7265\r\nvt 0.0736 0.67\r\nvt 0.539 0.7264\r\nvt 0.539 0.6698\r\nvt 1 0.6794\r\nvt 1 0.7312\r\nvt 0.9202 0.7308\r\nvt 0.9202 0.6787\r\nvt 0.3046 0.6781\r\nvt 0.3095 0.7305\r\nvt 0.2259 0.7308\r\nvt 0.2259 0.6786\r\nvt 0.1485 0.731\r\nvt 0.1473 0.6791\r\nvt 0.0736 0.7311\r\nvt 0.0736 0.6793\r\nvt 0 0.7312\r\nvt 0 0.6794\r\nvt 0.7999 0.6723\r\nvt 0.8021 0.7276\r\nvt 0.8202 0.7273\r\nvt 0.8202 0.6717\r\nvt 0.6667 0.7264\r\nvt 0.6667 0.7265\r\nvt 0.6667 0.67\r\nvt 0.4029 0.7303\r\nvt 0.4029 0.6776\r\nvt 0.7693 0.6742\r\nvt 0.7695 0.7286\r\nvt 0.7733 0.7284\r\nvt 0.7733 0.6738\r\nvt 0.7792 0.7281\r\nvt 0.7774 0.6733\r\nvt 0.7886 0.7279\r\nvt 0.7886 0.6728\r\nvt 0.7629 0.6752\r\nvt 0.7621 0.7291\r\nvt 0.7661 0.7288\r\nvt 0.7661 0.6747\r\nvt 0.7568 0.7294\r\nvt 0.7568 0.6757\r\no Object002\r\nusemtl Panel_Rear\r\nf 5119/5438/4962 5120/5439/4963 5121/5440/4964 5122/5441/4965\r\nf 5123/5442/4966 5124/5443/4967 5121/5440/4964 5120/5439/4963\r\nf 5125/5444/4968 5126/5445/4969 5121/5440/4964 5124/5443/4967\r\nf 5127/5446/4970 5122/5441/4965 5121/5440/4964 5126/5445/4969\r\nf 5128/5447/4971 5129/5448/4972 5130/5449/4973 5131/5450/4974\r\nf 5132/5451/4975 5133/5452/4976 5130/5449/4973 5129/5448/4972\r\nf 5134/5453/4977 5135/5454/4978 5130/5449/4973 5133/5452/4976\r\nf 5136/5455/4979 5131/5450/4974 5130/5449/4973 5135/5454/4978\r\nf 5137/5456/4980 5138/5457/4981 5139/5458/4982 5140/5459/4983\r\nf 5141/5460/4984 5142/5461/4985 5139/5458/4982 5138/5457/4981\r\nf 5143/5462/4986 5144/5463/4987 5139/5458/4982 5142/5461/4985\r\nf 5145/5464/4988 5140/5459/4983 5139/5458/4982 5144/5463/4987\r\nf 5146/5465/4989 5147/5466/4990 5148/5467/4991 5149/5468/4992\r\nf 5150/5469/4993 5151/5470/4994 5148/5467/4991 5147/5466/4990\r\nf 5134/5471/4977 5152/5472/4995 5148/5467/4991 5151/5470/4994\r\nf 5127/5473/4970 5149/5468/4992 5148/5467/4991 5152/5472/4995\r\nf 5153/5474/4996 5154/5475/4997 5155/5476/4998 5156/5477/4999\r\nf 5157/5478/5000 5158/5479/5001 5155/5476/4998 5154/5475/4997\r\nf 5159/5480/5002 5160/5481/5003 5155/5476/4998 5158/5479/5001\r\nf 5161/5482/5004 5156/5477/4999 5155/5476/4998 5160/5481/5003\r\nf 5157/5478/5000 5162/5483/5005 5163/5484/5006 5158/5479/5001\r\nf 5164/5485/5007 5165/5486/5008 5163/5484/5006 5162/5483/5005\r\nf 5166/5487/5009 5167/5488/5010 5163/5484/5006 5165/5486/5008\r\nf 5159/5480/5002 5158/5479/5001 5163/5484/5006 5167/5488/5010\r\nf 5168/5489/5011 5169/5490/5012 5170/5491/5013 5171/5492/5014\r\nf 5172/5493/5015 5173/5494/5016 5170/5491/5013 5169/5490/5012\r\nf 5150/5469/4993 5147/5466/4990 5170/5491/5013 5173/5494/5016\r\nf 5146/5465/4989 5171/5492/5014 5170/5491/5013 5147/5466/4990\r\nf 5174/5495/5017 5175/5496/5018 5176/5497/5019 5177/5498/5020\r\nf 5178/5499/5021 5179/5500/5022 5176/5497/5019 5175/5496/5018\r\nf 5166/5501/5009 5165/5502/5008 5176/5497/5019 5179/5500/5022\r\nf 5164/5503/5007 5177/5498/5020 5176/5497/5019 5165/5502/5008\r\nf 5180/5446/5023 5181/5441/5024 5182/5504/5025 5183/5505/5026\r\nf 5184/5506/5027 5185/5507/5028 5182/5504/5025 5181/5441/5024\r\nf 5186/5508/5029 5187/5452/5030 5182/5504/5025 5185/5507/5028\r\nf 5188/5453/5031 5183/5505/5026 5182/5504/5025 5187/5452/5030\r\nf 5189/5509/5032 5190/5510/5033 5191/5511/5034 5192/5450/5035\r\nf 5193/5512/5036 5194/5457/5037 5191/5511/5034 5190/5510/5033\r\nf 5195/5460/5038 5196/5513/5039 5191/5511/5034 5194/5457/5037\r\nf 5197/5455/5040 5192/5450/5035 5191/5511/5034 5196/5513/5039\r\nf 5198/5514/5041 5199/5515/5042 5200/5516/5043 5201/5517/5044\r\nf 5202/5518/5045 5203/5519/5046 5200/5516/5043 5199/5515/5042\r\nf 5204/5520/5047 5205/5521/5048 5200/5516/5043 5203/5519/5046\r\nf 5206/5522/5049 5201/5517/5044 5200/5516/5043 5205/5521/5048\r\nf 5184/5506/5027 5207/5523/5050 5208/5524/5051 5185/5507/5028\r\nf 5209/5525/5052 5210/5526/5053 5208/5524/5051 5207/5523/5050\r\nf 5211/5527/5054 5212/5528/5055 5208/5524/5051 5210/5526/5053\r\nf 5186/5508/5029 5185/5507/5028 5208/5524/5051 5212/5528/5055\r\nf 5213/5529/5056 5214/5530/5057 5215/5531/5058 5216/5532/5059\r\nf 5206/5522/5049 5217/5533/5060 5215/5531/5058 5214/5530/5057\r\nf 5193/5512/5036 5190/5510/5033 5215/5531/5058 5217/5533/5060\r\nf 5189/5509/5032 5216/5532/5059 5215/5531/5058 5190/5510/5033\r\nf 5195/5460/5038 5194/5457/5037 5218/5458/5061 5219/5461/5062\r\nf 5193/5512/5036 5220/5534/5063 5218/5458/5061 5194/5457/5037\r\nf 5221/5464/5064 5222/5463/5065 5218/5458/5061 5220/5534/5063\r\nf 5223/5462/5066 5219/5461/5062 5218/5458/5061 5222/5463/5065\r\nf 5180/5473/5023 5224/5468/5067 5225/5535/5068 5226/5536/5069\r\nf 5227/5465/5070 5228/5537/5071 5225/5535/5068 5224/5468/5067\r\nf 5229/5538/5072 5230/5539/5073 5225/5535/5068 5228/5537/5071\r\nf 5231/5540/5074 5226/5536/5069 5225/5535/5068 5230/5539/5073\r\nf 5232/5541/5075 5233/5542/5076 5234/5543/5077 5235/5544/5078\r\nf 5209/5525/5052 5236/5545/5079 5234/5543/5077 5233/5542/5076\r\nf 5237/5546/5080 5238/5547/5081 5234/5543/5077 5236/5545/5079\r\nf 5239/5548/5082 5235/5544/5078 5234/5543/5077 5238/5547/5081\r\nf 5240/5549/5083 5241/5550/5084 5242/5551/5085 5243/5552/5086\r\nf 5244/5553/5087 5245/5554/5088 5242/5551/5085 5241/5550/5084\r\nf 5213/5529/5056 5246/5555/5089 5242/5551/5085 5245/5554/5088\r\nf 5211/5527/5054 5243/5552/5086 5242/5551/5085 5246/5555/5089\r\nf 5206/5522/5049 5205/5521/5048 5247/5556/5090 5217/5533/5060\r\nf 5204/5520/5047 5248/5557/5091 5247/5556/5090 5205/5521/5048\r\nf 5221/5464/5064 5220/5534/5063 5247/5556/5090 5248/5557/5091\r\nf 5193/5512/5036 5217/5533/5060 5247/5556/5090 5220/5534/5063\r\nf 5143/5462/4986 5142/5558/4985 5249/5559/5092 5250/5560/5093\r\nf 5141/5561/4984 5251/5562/5094 5249/5559/5092 5142/5558/4985\r\nf 5159/5480/5002 5167/5488/5010 5249/5559/5092 5251/5562/5094\r\nf 5166/5487/5009 5250/5560/5093 5249/5559/5092 5167/5488/5010\r\nf 5136/5563/4979 5135/5564/4978 5252/5565/5095 5253/5566/5096\r\nf 5134/5471/4977 5151/5470/4994 5252/5565/5095 5135/5564/4978\r\nf 5150/5469/4993 5254/5567/5097 5252/5565/5095 5151/5470/4994\r\nf 5161/5482/5004 5253/5566/5096 5252/5565/5095 5254/5567/5097\r\nf 5178/5499/5021 5255/5568/5098 5256/5569/5099 5179/5500/5022\r\nf 5145/5570/4988 5144/5571/4987 5256/5569/5099 5255/5568/5098\r\nf 5143/5572/4986 5250/5573/5093 5256/5569/5099 5144/5571/4987\r\nf 5166/5501/5009 5179/5500/5022 5256/5569/5099 5250/5573/5093\r\nf 5257/5574/5100 5258/5575/5101 5259/5576/5102 5260/5577/5103\r\nf 5145/5570/4988 5255/5568/5098 5259/5576/5102 5258/5575/5101\r\nf 5178/5499/5021 5261/5578/5104 5259/5576/5102 5255/5568/5098\r\nf 5262/5579/5105 5260/5577/5103 5259/5576/5102 5261/5578/5104\r\nf 5263/5480/5106 5264/5562/5107 5265/5559/5108 5266/5488/5109\r\nf 5195/5561/5038 5219/5558/5062 5265/5559/5108 5264/5562/5107\r\nf 5223/5462/5066 5267/5560/5110 5265/5559/5108 5219/5558/5062\r\nf 5268/5487/5111 5266/5488/5109 5265/5559/5108 5267/5560/5110\r\nf 5269/5482/5112 5270/5566/5113 5271/5580/5114 5272/5481/5115\r\nf 5197/5563/5040 5196/5581/5039 5271/5580/5114 5270/5566/5113\r\nf 5195/5561/5038 5264/5562/5107 5271/5580/5114 5196/5581/5039\r\nf 5263/5480/5106 5272/5481/5115 5271/5580/5114 5264/5562/5107\r\nf 5268/5501/5111 5267/5573/5110 5273/5569/5116 5274/5500/5117\r\nf 5223/5572/5066 5222/5571/5065 5273/5569/5116 5267/5573/5110\r\nf 5221/5570/5064 5275/5582/5118 5273/5569/5116 5222/5571/5065\r\nf 5276/5583/5119 5274/5500/5117 5273/5569/5116 5275/5582/5118\r\nf 5227/5465/5070 5277/5492/5120 5278/5584/5121 5228/5537/5071\r\nf 5279/5585/5122 5280/5586/5123 5278/5584/5121 5277/5492/5120\r\nf 5281/5587/5124 5282/5588/5125 5278/5584/5121 5280/5586/5123\r\nf 5229/5538/5072 5228/5537/5071 5278/5584/5121 5282/5588/5125\r\nf 5221/5570/5064 5248/5589/5091 5283/5590/5126 5275/5582/5118\r\nf 5204/5591/5047 5284/5592/5127 5283/5590/5126 5248/5589/5091\r\nf 5285/5593/5128 5286/5594/5129 5283/5590/5126 5284/5592/5127\r\nf 5276/5583/5119 5275/5582/5118 5283/5590/5126 5286/5594/5129\r\nf 5164/5503/5007 5287/5595/5130 5288/5596/5131 5177/5498/5020\r\nf 5289/5597/5132 5290/5598/5133 5288/5596/5131 5287/5595/5130\r\nf 5291/5599/5134 5292/5600/5135 5288/5596/5131 5290/5598/5133\r\nf 5174/5495/5017 5177/5498/5020 5288/5596/5131 5292/5600/5135\r\nf 5276/5583/5119 5286/5594/5129 5293/5601/5136 5294/5602/5137\r\nf 5285/5593/5128 5295/5603/5138 5293/5601/5136 5286/5594/5129\r\nf 5296/5604/5139 5297/5605/5140 5293/5601/5136 5295/5603/5138\r\nf 5298/5606/5141 5294/5602/5137 5293/5601/5136 5297/5605/5140\r\nf 5299/5607/5142 5300/5477/5143 5301/5476/5144 5302/5608/5145\r\nf 5269/5482/5112 5272/5481/5115 5301/5476/5144 5300/5477/5143\r\nf 5263/5480/5106 5303/5479/5146 5301/5476/5144 5272/5481/5115\r\nf 5304/5609/5147 5302/5608/5145 5301/5476/5144 5303/5479/5146\r\nf 5304/5609/5147 5303/5479/5146 5305/5484/5148 5306/5610/5149\r\nf 5263/5480/5106 5266/5488/5109 5305/5484/5148 5303/5479/5146\r\nf 5268/5487/5111 5307/5486/5150 5305/5484/5148 5266/5488/5109\r\nf 5308/5485/5151 5306/5610/5149 5305/5484/5148 5307/5486/5150\r\nf 5268/5501/5111 5274/5500/5117 5309/5497/5152 5307/5502/5150\r\nf 5276/5583/5119 5294/5602/5137 5309/5497/5152 5274/5500/5117\r\nf 5298/5606/5141 5310/5611/5153 5309/5497/5152 5294/5602/5137\r\nf 5308/5503/5151 5307/5502/5150 5309/5497/5152 5310/5611/5153\r\nf 5311/5612/5154 5312/5613/5155 5313/5614/5156 5314/5615/5157\r\nf 5262/5579/5105 5261/5578/5104 5313/5614/5156 5312/5613/5155\r\nf 5178/5499/5021 5175/5496/5018 5313/5614/5156 5261/5578/5104\r\nf 5174/5495/5017 5314/5615/5157 5313/5614/5156 5175/5496/5018\r\nf 5279/5585/5122 5315/5616/5158 5316/5617/5159 5280/5586/5123\r\nf 5317/5618/5160 5318/5619/5161 5316/5617/5159 5315/5616/5158\r\nf 5319/5620/5162 5320/5621/5163 5316/5617/5159 5318/5619/5161\r\nf 5281/5587/5124 5280/5586/5123 5316/5617/5159 5320/5621/5163\r\nf 5298/5606/5141 5297/5605/5140 5321/5622/5164 5322/5623/5165\r\nf 5296/5604/5139 5323/5624/5166 5321/5622/5164 5297/5605/5140\r\nf 5324/5625/5167 5325/5626/5168 5321/5622/5164 5323/5624/5166\r\nf 5326/5627/5169 5322/5623/5165 5321/5622/5164 5325/5626/5168\r\nf 5327/5444/5170 5328/5444/5171 5329/5628/5172 5330/5628/5173\r\nf 5125/5444/4968 5124/5443/4967 5329/5628/5172 5328/5444/5171\r\nf 5123/5442/4966 5331/5629/5174 5329/5628/5172 5124/5443/4967\r\nf 5332/5629/5175 5330/5628/5173 5329/5628/5172 5331/5629/5174\r\nf 5333/5630/5176 5334/5630/5177 5335/5631/5178 5336/5631/5179\r\nf 5337/5538/5180 5338/5539/5181 5335/5631/5178 5334/5630/5177\r\nf 5125/5540/4968 5328/5540/5171 5335/5631/5178 5338/5539/5181\r\nf 5327/5540/5170 5336/5631/5179 5335/5631/5178 5328/5540/5171\r\nf 5231/5444/5074 5339/5444/5182 5340/5628/5183 5341/5443/5184\r\nf 5342/5444/5185 5343/5628/5186 5340/5628/5183 5339/5444/5182\r\nf 5344/5632/5187 5345/5632/5188 5340/5628/5183 5343/5628/5186\r\nf 5346/5633/5189 5341/5443/5184 5340/5628/5183 5345/5632/5188\r\nf 5342/5540/5185 5339/5540/5182 5347/5631/5190 5348/5631/5191\r\nf 5231/5540/5074 5230/5539/5073 5347/5631/5190 5339/5540/5182\r\nf 5229/5538/5072 5349/5630/5192 5347/5631/5190 5230/5539/5073\r\nf 5350/5630/5193 5348/5631/5191 5347/5631/5190 5349/5630/5192\r\nf 5351/5634/5194 5352/5634/5195 5353/5635/5196 5354/5635/5197\r\nf 5239/5548/5082 5238/5547/5081 5353/5635/5196 5352/5634/5195\r\nf 5237/5546/5080 5355/5636/5198 5353/5635/5196 5238/5547/5081\r\nf 5356/5636/5199 5354/5635/5197 5353/5635/5196 5355/5636/5198\r\nf 5356/5636/5199 5355/5636/5198 5357/5637/5200 5358/5637/5201\r\nf 5237/5546/5080 5359/5638/5202 5357/5637/5200 5355/5636/5198\r\nf 5346/5633/5189 5345/5632/5188 5357/5637/5200 5359/5638/5202\r\nf 5344/5632/5187 5358/5637/5201 5357/5637/5200 5345/5632/5188\r\nf 5360/5639/5203 5361/5639/5204 5362/5640/5205 5363/5640/5206\r\nf 5364/5641/5207 5365/5588/5208 5362/5640/5205 5361/5639/5204\r\nf 5337/5538/5180 5334/5630/5177 5362/5640/5205 5365/5588/5208\r\nf 5333/5630/5176 5363/5640/5206 5362/5640/5205 5334/5630/5177\r\nf 5350/5630/5193 5349/5630/5192 5366/5640/5209 5367/5640/5210\r\nf 5229/5538/5072 5282/5588/5125 5366/5640/5209 5349/5630/5192\r\nf 5281/5587/5124 5368/5642/5211 5366/5640/5209 5282/5588/5125\r\nf 5369/5642/5212 5367/5640/5210 5366/5640/5209 5368/5642/5211\r\nf 5369/5642/5212 5368/5642/5211 5370/5643/5213 5371/5643/5214\r\nf 5281/5587/5124 5320/5621/5163 5370/5643/5213 5368/5642/5211\r\nf 5319/5620/5162 5372/5620/5215 5370/5643/5213 5320/5621/5163\r\nf 5373/5620/5216 5371/5643/5214 5370/5643/5213 5372/5620/5215\r\nf 5141/5460/4984 5138/5457/4981 5374/5511/5217 5375/5513/5218\r\nf 5137/5456/4980 5376/5644/5219 5374/5511/5217 5138/5457/4981\r\nf 5128/5447/4971 5131/5450/4974 5374/5511/5217 5376/5644/5219\r\nf 5136/5455/4979 5375/5513/5218 5374/5511/5217 5131/5450/4974\r\nf 5244/5553/5087 5377/5645/5220 5378/5646/5221 5245/5554/5088\r\nf 5198/5514/5041 5201/5517/5044 5378/5646/5221 5377/5645/5220\r\nf 5206/5522/5049 5214/5530/5057 5378/5646/5221 5201/5517/5044\r\nf 5213/5529/5056 5245/5554/5088 5378/5646/5221 5214/5530/5057\r\nf 5161/5482/5004 5254/5567/5097 5379/5647/5222 5156/5477/4999\r\nf 5150/5469/4993 5173/5494/5016 5379/5647/5222 5254/5567/5097\r\nf 5172/5493/5015 5380/5648/5223 5379/5647/5222 5173/5494/5016\r\nf 5153/5474/4996 5156/5477/4999 5379/5647/5222 5380/5648/5223\r\nf 5197/5455/5040 5381/5454/5224 5382/5449/5225 5192/5450/5035\r\nf 5188/5453/5031 5187/5452/5030 5382/5449/5225 5381/5454/5224\r\nf 5186/5508/5029 5383/5649/5226 5382/5449/5225 5187/5452/5030\r\nf 5189/5509/5032 5192/5450/5035 5382/5449/5225 5383/5649/5226\r\nf 5189/5509/5032 5383/5649/5226 5384/5650/5227 5216/5532/5059\r\nf 5186/5508/5029 5212/5528/5055 5384/5650/5227 5383/5649/5226\r\nf 5211/5527/5054 5246/5555/5089 5384/5650/5227 5212/5528/5055\r\nf 5213/5529/5056 5216/5532/5059 5384/5650/5227 5246/5555/5089\r\nf 5161/5482/5004 5160/5481/5003 5385/5580/5228 5253/5566/5096\r\nf 5159/5480/5002 5251/5562/5094 5385/5580/5228 5160/5481/5003\r\nf 5141/5561/4984 5375/5581/5218 5385/5580/5228 5251/5562/5094\r\nf 5136/5563/4979 5253/5566/5096 5385/5580/5228 5375/5581/5218\r\nf 5386/5469/5229 5387/5470/5230 5388/5565/5231 5389/5567/5232\r\nf 5188/5471/5031 5381/5564/5224 5388/5565/5231 5387/5470/5230\r\nf 5197/5563/5040 5270/5566/5113 5388/5565/5231 5381/5564/5224\r\nf 5269/5482/5112 5389/5567/5232 5388/5565/5231 5270/5566/5113\r\nf 5153/5474/4996 5380/5648/5223 5390/5651/5233 5391/5652/5234\r\nf 5172/5493/5015 5392/5653/5235 5390/5651/5233 5380/5648/5223\r\nf 5393/5654/5236 5394/5655/5237 5390/5651/5233 5392/5653/5235\r\nf 5395/5656/5238 5391/5652/5234 5390/5651/5233 5394/5655/5237\r\nf 5396/5657/5239 5397/5494/5240 5398/5647/5241 5399/5658/5242\r\nf 5386/5469/5229 5389/5567/5232 5398/5647/5241 5397/5494/5240\r\nf 5269/5482/5112 5300/5477/5143 5398/5647/5241 5389/5567/5232\r\nf 5299/5607/5142 5399/5658/5242 5398/5647/5241 5300/5477/5143\r\nf 5127/5446/4970 5152/5505/4995 5400/5504/5243 5122/5441/4965\r\nf 5134/5453/4977 5133/5452/4976 5400/5504/5243 5152/5505/4995\r\nf 5132/5451/4975 5401/5659/5244 5400/5504/5243 5133/5452/4976\r\nf 5119/5438/4962 5122/5441/4965 5400/5504/5243 5401/5659/5244\r\nf 5240/5549/5083 5243/5552/5086 5402/5660/5245 5403/5661/5246\r\nf 5211/5527/5054 5210/5526/5053 5402/5660/5245 5243/5552/5086\r\nf 5209/5525/5052 5233/5542/5076 5402/5660/5245 5210/5526/5053\r\nf 5232/5541/5075 5403/5661/5246 5402/5660/5245 5233/5542/5076\r\nf 5127/5473/4970 5126/5536/4969 5404/5535/5247 5149/5468/4992\r\nf 5125/5540/4968 5338/5539/5181 5404/5535/5247 5126/5536/4969\r\nf 5337/5538/5180 5405/5537/5248 5404/5535/5247 5338/5539/5181\r\nf 5146/5465/4989 5149/5468/4992 5404/5535/5247 5405/5537/5248\r\nf 5146/5465/4989 5405/5537/5248 5406/5584/5249 5171/5492/5014\r\nf 5337/5538/5180 5365/5588/5208 5406/5584/5249 5405/5537/5248\r\nf 5364/5641/5207 5407/5662/5250 5406/5584/5249 5365/5588/5208\r\nf 5168/5489/5011 5171/5492/5014 5406/5584/5249 5407/5662/5250\r\nf 5231/5444/5074 5341/5443/5184 5408/5440/5251 5226/5445/5069\r\nf 5346/5633/5189 5409/5663/5252 5408/5440/5251 5341/5443/5184\r\nf 5184/5506/5027 5181/5441/5024 5408/5440/5251 5409/5663/5252\r\nf 5180/5446/5023 5226/5445/5069 5408/5440/5251 5181/5441/5024\r\nf 5346/5633/5189 5359/5638/5202 5410/5664/5253 5409/5663/5252\r\nf 5237/5546/5080 5236/5545/5079 5410/5664/5253 5359/5638/5202\r\nf 5209/5525/5052 5207/5523/5050 5410/5664/5253 5236/5545/5079\r\nf 5184/5506/5027 5409/5663/5252 5410/5664/5253 5207/5523/5050\r\nf 5188/5471/5031 5387/5470/5230 5411/5467/5254 5183/5472/5026\r\nf 5386/5469/5229 5412/5466/5255 5411/5467/5254 5387/5470/5230\r\nf 5227/5465/5070 5224/5468/5067 5411/5467/5254 5412/5466/5255\r\nf 5180/5473/5023 5183/5472/5026 5411/5467/5254 5224/5468/5067\r\nf 5386/5469/5229 5397/5494/5240 5413/5491/5256 5412/5466/5255\r\nf 5396/5657/5239 5414/5665/5257 5413/5491/5256 5397/5494/5240\r\nf 5279/5585/5122 5277/5492/5120 5413/5491/5256 5414/5665/5257\r\nf 5227/5465/5070 5412/5466/5255 5413/5491/5256 5277/5492/5120\r\nf 5168/5489/5011 5407/5662/5250 5415/5666/5258 5416/5667/5259\r\nf 5364/5641/5207 5417/5668/5260 5415/5666/5258 5407/5662/5250\r\nf 5418/5669/5261 5419/5670/5262 5415/5666/5258 5417/5668/5260\r\nf 5420/5671/5263 5416/5667/5259 5415/5666/5258 5419/5670/5262\r\nf 5396/5657/5239 5421/5672/5264 5422/5673/5265 5414/5665/5257\r\nf 5423/5674/5266 5424/5675/5267 5422/5673/5265 5421/5672/5264\r\nf 5317/5618/5160 5315/5616/5158 5422/5673/5265 5424/5675/5267\r\nf 5279/5585/5122 5414/5665/5257 5422/5673/5265 5315/5616/5158\r\nf 5425/5676/5268 5426/5677/5269 5427/5678/5270 5428/5679/5271\r\nf 5429/5680/5272 5430/5681/5273 5427/5678/5270 5426/5677/5269\r\nf 5257/5682/5100 5431/5683/5274 5427/5678/5270 5430/5681/5273\r\nf 5432/5684/5275 5428/5679/5271 5427/5678/5270 5431/5683/5274\r\nf 5433/5685/5276 5434/5686/5277 5435/5687/5278 5436/5688/5279\r\nf 5437/5689/5280 5438/5690/5281 5435/5687/5278 5434/5686/5277\r\nf 5439/5691/5282 5440/5692/5283 5435/5687/5278 5438/5690/5281\r\nf 5441/5693/5284 5436/5688/5279 5435/5687/5278 5440/5692/5283\r\nf 5442/5694/5285 5443/5695/5286 5444/5696/5287 5445/5697/5288\r\nf 5446/5698/5289 5447/5699/5290 5444/5696/5287 5443/5695/5286\r\nf 5448/5700/5291 5449/5701/5292 5444/5696/5287 5447/5699/5290\r\nf 5450/5702/5293 5445/5697/5288 5444/5696/5287 5449/5701/5292\r\nf 5451/5703/5294 5452/5704/5295 5453/5705/5296 5454/5706/5297\r\nf 5455/5707/5298 5456/5708/5299 5453/5705/5296 5452/5704/5295\r\nf 5457/5709/5300 5458/5710/5301 5453/5705/5296 5456/5708/5299\r\nf 5432/5711/5275 5454/5706/5297 5453/5705/5296 5458/5710/5301\r\nf 5459/5712/5302 5460/5713/5303 5461/5714/5304 5462/5715/5305\r\nf 5455/5707/5298 5452/5704/5295 5461/5714/5304 5460/5713/5303\r\nf 5451/5703/5294 5463/5716/5306 5461/5714/5304 5452/5704/5295\r\nf 5464/5717/5307 5462/5715/5305 5461/5714/5304 5463/5716/5306\r\nf 5465/5718/5308 5466/5718/5309 5467/5719/5310 5468/5719/5311\r\nf 5433/5685/5276 5436/5688/5279 5467/5719/5310 5466/5718/5309\r\nf 5441/5693/5284 5469/5720/5312 5467/5719/5310 5436/5688/5279\r\nf 5470/5720/5313 5468/5719/5311 5467/5719/5310 5469/5720/5312\r\nf 5450/5702/5293 5449/5701/5292 5471/5721/5314 5472/5722/5315\r\nf 5448/5700/5291 5473/5723/5316 5471/5721/5314 5449/5701/5292\r\nf 5429/5680/5272 5426/5677/5269 5471/5721/5314 5473/5723/5316\r\nf 5425/5676/5268 5472/5722/5315 5471/5721/5314 5426/5677/5269\r\nf 5437/5689/5280 5474/5724/5317 5475/5725/5318 5438/5690/5281\r\nf 5446/5698/5289 5443/5695/5286 5475/5725/5318 5474/5724/5317\r\nf 5442/5694/5285 5476/5726/5319 5475/5725/5318 5443/5695/5286\r\nf 5439/5691/5282 5438/5690/5281 5475/5725/5318 5476/5726/5319\r\nf 5451/5703/5294 5454/5706/5297 5477/5727/5320 5478/5728/5321\r\nf 5432/5711/5275 5431/5729/5274 5477/5727/5320 5454/5706/5297\r\nf 5257/5574/5100 5260/5577/5103 5477/5727/5320 5431/5729/5274\r\nf 5262/5579/5105 5478/5728/5321 5477/5727/5320 5260/5577/5103\r\nf 5464/5717/5307 5463/5716/5306 5479/5730/5322 5480/5731/5323\r\nf 5451/5703/5294 5478/5728/5321 5479/5730/5322 5463/5716/5306\r\nf 5262/5579/5105 5312/5613/5155 5479/5730/5322 5478/5728/5321\r\nf 5311/5612/5154 5480/5731/5323 5479/5730/5322 5312/5613/5155\r\nf 5429/5680/5272 5481/5732/5324 5482/5733/5325 5430/5681/5273\r\nf 5137/5456/4980 5140/5459/4983 5482/5733/5325 5481/5732/5324\r\nf 5145/5464/4988 5258/5734/5101 5482/5733/5325 5140/5459/4983\r\nf 5257/5682/5100 5430/5681/5273 5482/5733/5325 5258/5734/5101\r\nf 5123/5442/4966 5120/5439/4963 5483/5735/5326 5484/5736/5327\r\nf 5119/5438/4962 5485/5737/5328 5483/5735/5326 5120/5439/4963\r\nf 5437/5689/5280 5434/5686/5277 5483/5735/5326 5485/5737/5328\r\nf 5433/5685/5276 5484/5736/5327 5483/5735/5326 5434/5686/5277\r\nf 5446/5698/5289 5486/5738/5329 5487/5739/5330 5447/5699/5290\r\nf 5132/5451/4975 5129/5448/4972 5487/5739/5330 5486/5738/5329\r\nf 5128/5447/4971 5488/5740/5331 5487/5739/5330 5129/5448/4972\r\nf 5448/5700/5291 5447/5699/5290 5487/5739/5330 5488/5740/5331\r\nf 5332/5629/5175 5331/5629/5174 5489/5741/5332 5490/5741/5333\r\nf 5123/5442/4966 5484/5736/5327 5489/5741/5332 5331/5629/5174\r\nf 5433/5685/5276 5466/5718/5309 5489/5741/5332 5484/5736/5327\r\nf 5465/5718/5308 5490/5741/5333 5489/5741/5332 5466/5718/5309\r\nf 5448/5700/5291 5488/5740/5331 5491/5742/5334 5473/5723/5316\r\nf 5128/5447/4971 5376/5644/5219 5491/5742/5334 5488/5740/5331\r\nf 5137/5456/4980 5481/5732/5324 5491/5742/5334 5376/5644/5219\r\nf 5429/5680/5272 5473/5723/5316 5491/5742/5334 5481/5732/5324\r\nf 5119/5438/4962 5401/5659/5244 5492/5743/5335 5485/5737/5328\r\nf 5132/5451/4975 5486/5738/5329 5492/5743/5335 5401/5659/5244\r\nf 5446/5698/5289 5474/5724/5317 5492/5743/5335 5486/5738/5329\r\nf 5437/5689/5280 5485/5737/5328 5492/5743/5335 5474/5724/5317\r\nf 5455/5707/5298 5493/5744/5336 5494/5745/5337 5456/5708/5299\r\nf 5495/5746/5338 5496/5747/5339 5494/5745/5337 5493/5744/5336\r\nf 5202/5748/5045 5497/5749/5340 5494/5745/5337 5496/5747/5339\r\nf 5457/5709/5300 5456/5708/5299 5494/5745/5337 5497/5749/5340\r\nf 5459/5712/5302 5498/5750/5341 5499/5751/5342 5460/5713/5303\r\nf 5500/5752/5343 5501/5753/5344 5499/5751/5342 5498/5750/5341\r\nf 5495/5746/5338 5493/5744/5336 5499/5751/5342 5501/5753/5344\r\nf 5455/5707/5298 5460/5713/5303 5499/5751/5342 5493/5744/5336\r\nf 5502/5754/5345 5503/5755/5346 5504/5756/5347 5505/5757/5348\r\nf 5425/5676/5268 5428/5679/5271 5504/5756/5347 5503/5755/5346\r\nf 5432/5684/5275 5458/5758/5301 5504/5756/5347 5428/5679/5271\r\nf 5457/5759/5300 5505/5757/5348 5504/5756/5347 5458/5758/5301\r\nf 5441/5693/5284 5440/5692/5283 5506/5760/5349 5507/5761/5350\r\nf 5439/5691/5282 5508/5762/5351 5506/5760/5349 5440/5692/5283\r\nf 5509/5763/5352 5510/5764/5353 5506/5760/5349 5508/5762/5351\r\nf 5511/5765/5354 5507/5761/5350 5506/5760/5349 5510/5764/5353\r\nf 5512/5766/5355 5513/5767/5356 5514/5768/5357 5515/5769/5358\r\nf 5442/5694/5285 5445/5697/5288 5514/5768/5357 5513/5767/5356\r\nf 5450/5702/5293 5516/5770/5359 5514/5768/5357 5445/5697/5288\r\nf 5517/5771/5360 5515/5769/5358 5514/5768/5357 5516/5770/5359\r\nf 5470/5720/5313 5469/5720/5312 5518/5772/5361 5519/5772/5362\r\nf 5441/5693/5284 5507/5761/5350 5518/5772/5361 5469/5720/5312\r\nf 5511/5765/5354 5520/5773/5363 5518/5772/5361 5507/5761/5350\r\nf 5521/5773/5364 5519/5772/5362 5518/5772/5361 5520/5773/5363\r\nf 5517/5771/5360 5516/5770/5359 5522/5774/5365 5523/5775/5366\r\nf 5450/5702/5293 5472/5722/5315 5522/5774/5365 5516/5770/5359\r\nf 5425/5676/5268 5503/5755/5346 5522/5774/5365 5472/5722/5315\r\nf 5502/5754/5345 5523/5775/5366 5522/5774/5365 5503/5755/5346\r\nf 5509/5763/5352 5508/5762/5351 5524/5776/5367 5525/5777/5368\r\nf 5439/5691/5282 5476/5726/5319 5524/5776/5367 5508/5762/5351\r\nf 5442/5694/5285 5513/5767/5356 5524/5776/5367 5476/5726/5319\r\nf 5512/5766/5355 5525/5777/5368 5524/5776/5367 5513/5767/5356\r\nf 5198/5514/5041 5526/5778/5369 5527/5779/5370 5199/5515/5042\r\nf 5502/5754/5345 5505/5757/5348 5527/5779/5370 5526/5778/5369\r\nf 5457/5759/5300 5497/5780/5340 5527/5779/5370 5505/5757/5348\r\nf 5202/5518/5045 5199/5515/5042 5527/5779/5370 5497/5780/5340\r\nf 5239/5548/5082 5528/5781/5371 5529/5782/5372 5235/5544/5078\r\nf 5511/5765/5354 5510/5764/5353 5529/5782/5372 5528/5781/5371\r\nf 5509/5763/5352 5530/5783/5373 5529/5782/5372 5510/5764/5353\r\nf 5232/5541/5075 5235/5544/5078 5529/5782/5372 5530/5783/5373\r\nf 5240/5549/5083 5531/5784/5374 5532/5785/5375 5241/5550/5084\r\nf 5512/5766/5355 5515/5769/5358 5532/5785/5375 5531/5784/5374\r\nf 5517/5771/5360 5533/5786/5376 5532/5785/5375 5515/5769/5358\r\nf 5244/5553/5087 5241/5550/5084 5532/5785/5375 5533/5786/5376\r\nf 5521/5773/5364 5520/5773/5363 5534/5787/5377 5535/5787/5378\r\nf 5511/5765/5354 5528/5781/5371 5534/5787/5377 5520/5773/5363\r\nf 5239/5548/5082 5352/5634/5195 5534/5787/5377 5528/5781/5371\r\nf 5351/5634/5194 5535/5787/5378 5534/5787/5377 5352/5634/5195\r\nf 5244/5553/5087 5533/5786/5376 5536/5788/5379 5377/5645/5220\r\nf 5517/5771/5360 5523/5775/5366 5536/5788/5379 5533/5786/5376\r\nf 5502/5754/5345 5526/5778/5369 5536/5788/5379 5523/5775/5366\r\nf 5198/5514/5041 5377/5645/5220 5536/5788/5379 5526/5778/5369\r\nf 5232/5541/5075 5530/5783/5373 5537/5789/5380 5403/5661/5246\r\nf 5509/5763/5352 5525/5777/5368 5537/5789/5380 5530/5783/5373\r\nf 5512/5766/5355 5531/5784/5374 5537/5789/5380 5525/5777/5368\r\nf 5240/5549/5083 5403/5661/5246 5537/5789/5380 5531/5784/5374\r\nf 5285/5593/5128 5284/5592/5127 5538/5790/5381 5539/5791/5382\r\nf 5204/5591/5047 5203/5792/5046 5538/5790/5381 5284/5592/5127\r\nf 5202/5748/5045 5496/5747/5339 5538/5790/5381 5203/5792/5046\r\nf 5495/5746/5338 5539/5791/5382 5538/5790/5381 5496/5747/5339\r\nf 5500/5752/5343 5540/5793/5383 5541/5794/5384 5501/5753/5344\r\nf 5296/5604/5139 5295/5603/5138 5541/5794/5384 5540/5793/5383\r\nf 5285/5593/5128 5539/5791/5382 5541/5794/5384 5295/5603/5138\r\nf 5495/5746/5338 5501/5753/5344 5541/5794/5384 5539/5791/5382\r\nf 5542/5795/5385 5543/5796/5386 5544/5797/5387 5545/5798/5388\r\nf 5157/5478/5000 5154/5475/4997 5544/5797/5387 5543/5796/5386\r\nf 5153/5474/4996 5391/5652/5234 5544/5797/5387 5154/5475/4997\r\nf 5395/5656/5238 5545/5798/5388 5544/5797/5387 5391/5652/5234\r\nf 5289/5799/5132 5287/5800/5130 5546/5801/5389 5547/5802/5390\r\nf 5164/5485/5007 5162/5483/5005 5546/5801/5389 5287/5800/5130\r\nf 5157/5478/5000 5543/5796/5386 5546/5801/5389 5162/5483/5005\r\nf 5542/5795/5385 5547/5802/5390 5546/5801/5389 5543/5796/5386\r\nf 5393/5654/5236 5392/5653/5235 5548/5803/5391 5549/5804/5392\r\nf 5172/5493/5015 5169/5490/5012 5548/5803/5391 5392/5653/5235\r\nf 5168/5489/5011 5416/5667/5259 5548/5803/5391 5169/5490/5012\r\nf 5420/5671/5263 5549/5804/5392 5548/5803/5391 5416/5667/5259\r\nf 5550/5805/5393 5551/5806/5394 5552/5807/5395 5553/5808/5396\r\nf 5308/5503/5151 5310/5611/5153 5552/5807/5395 5551/5806/5394\r\nf 5298/5606/5141 5322/5623/5165 5552/5807/5395 5310/5611/5153\r\nf 5326/5627/5169 5553/5808/5396 5552/5807/5395 5322/5623/5165\r\nf 5554/5809/5397 5555/5810/5398 5556/5811/5399 5557/5812/5400\r\nf 5299/5607/5142 5302/5608/5145 5556/5811/5399 5555/5810/5398\r\nf 5304/5609/5147 5558/5813/5401 5556/5811/5399 5302/5608/5145\r\nf 5559/5814/5402 5557/5812/5400 5556/5811/5399 5558/5813/5401\r\nf 5559/5814/5402 5558/5813/5401 5560/5815/5403 5561/5816/5404\r\nf 5304/5609/5147 5306/5610/5149 5560/5815/5403 5558/5813/5401\r\nf 5308/5485/5151 5551/5817/5394 5560/5815/5403 5306/5610/5149\r\nf 5550/5818/5393 5561/5816/5404 5560/5815/5403 5551/5817/5394\r\nf 5562/5819/5405 5563/5820/5406 5564/5821/5407 5565/5822/5408\r\nf 5311/5612/5154 5314/5615/5157 5564/5821/5407 5563/5820/5406\r\nf 5174/5495/5017 5292/5600/5135 5564/5821/5407 5314/5615/5157\r\nf 5291/5599/5134 5565/5822/5408 5564/5821/5407 5292/5600/5135\r\nf 5418/5669/5261 5417/5668/5260 5566/5823/5409 5567/5669/5410\r\nf 5364/5641/5207 5361/5639/5204 5566/5823/5409 5417/5668/5260\r\nf 5360/5639/5203 5568/5824/5411 5566/5823/5409 5361/5639/5204\r\nf 5569/5825/5412 5567/5669/5410 5566/5823/5409 5568/5824/5411\r\nf 5423/5674/5266 5421/5672/5264 5570/5826/5413 5571/5827/5414\r\nf 5396/5657/5239 5399/5658/5242 5570/5826/5413 5421/5672/5264\r\nf 5299/5607/5142 5555/5810/5398 5570/5826/5413 5399/5658/5242\r\nf 5554/5809/5397 5571/5827/5414 5570/5826/5413 5555/5810/5398\r\nf 5572/5828/5415 5573/5829/5416 5574/5830/5417 5575/5831/5418\r\nf 5459/5712/5302 5462/5715/5305 5574/5830/5417 5573/5829/5416\r\nf 5464/5717/5307 5576/5832/5419 5574/5830/5417 5462/5715/5305\r\nf 5577/5833/5420 5575/5831/5418 5574/5830/5417 5576/5832/5419\r\nf 5577/5833/5420 5576/5832/5419 5578/5834/5421 5579/5835/5422\r\nf 5464/5717/5307 5480/5731/5323 5578/5834/5421 5576/5832/5419\r\nf 5311/5612/5154 5563/5820/5406 5578/5834/5421 5480/5731/5323\r\nf 5562/5819/5405 5579/5835/5422 5578/5834/5421 5563/5820/5406\r\nf 5580/5836/5423 5581/5837/5424 5582/5838/5425 5583/5839/5426\r\nf 5500/5752/5343 5498/5750/5341 5582/5838/5425 5581/5837/5424\r\nf 5459/5712/5302 5573/5829/5416 5582/5838/5425 5498/5750/5341\r\nf 5572/5828/5415 5583/5839/5426 5582/5838/5425 5573/5829/5416\r\nf 5324/5625/5167 5323/5624/5166 5584/5840/5427 5585/5841/5428\r\nf 5296/5604/5139 5540/5793/5383 5584/5840/5427 5323/5624/5166\r\nf 5500/5752/5343 5581/5837/5424 5584/5840/5427 5540/5793/5383\r\nf 5580/5836/5423 5585/5841/5428 5584/5840/5427 5581/5837/5424\r\nf 5586/5438/5429 5587/5441/5430 5588/5440/5431 5589/5439/5432\r\nf 5590/5446/5433 5591/5445/5434 5588/5440/5431 5587/5441/5430\r\nf 5592/5444/5435 5593/5443/5436 5588/5440/5431 5591/5445/5434\r\nf 5594/5442/5437 5589/5439/5432 5588/5440/5431 5593/5443/5436\r\nf 5595/5447/5438 5596/5450/5439 5597/5449/5440 5598/5448/5441\r\nf 5599/5455/5442 5600/5454/5443 5597/5449/5440 5596/5450/5439\r\nf 5601/5453/5444 5602/5452/5445 5597/5449/5440 5600/5454/5443\r\nf 5603/5451/5446 5598/5448/5441 5597/5449/5440 5602/5452/5445\r\nf 5604/5456/5447 5605/5459/5448 5606/5458/5449 5607/5457/5450\r\nf 5608/5464/5451 5609/5463/5452 5606/5458/5449 5605/5459/5448\r\nf 5610/5462/5453 5611/5461/5454 5606/5458/5449 5609/5463/5452\r\nf 5612/5460/5455 5607/5457/5450 5606/5458/5449 5611/5461/5454\r\nf 5613/5465/5456 5614/5468/5457 5615/5467/5458 5616/5466/5459\r\nf 5590/5473/5433 5617/5472/5460 5615/5467/5458 5614/5468/5457\r\nf 5601/5471/5444 5618/5470/5461 5615/5467/5458 5617/5472/5460\r\nf 5619/5469/5462 5616/5466/5459 5615/5467/5458 5618/5470/5461\r\nf 5620/5474/5463 5621/5477/5464 5622/5476/5465 5623/5475/5466\r\nf 5624/5482/5467 5625/5481/5468 5622/5476/5465 5621/5477/5464\r\nf 5626/5480/5469 5627/5479/5470 5622/5476/5465 5625/5481/5468\r\nf 5628/5478/5471 5623/5475/5466 5622/5476/5465 5627/5479/5470\r\nf 5628/5478/5471 5627/5479/5470 5629/5484/5472 5630/5483/5473\r\nf 5626/5480/5469 5631/5488/5474 5629/5484/5472 5627/5479/5470\r\nf 5632/5487/5475 5633/5486/5476 5629/5484/5472 5631/5488/5474\r\nf 5634/5485/5477 5630/5483/5473 5629/5484/5472 5633/5486/5476\r\nf 5635/5489/5478 5636/5492/5479 5637/5491/5480 5638/5490/5481\r\nf 5613/5465/5456 5616/5466/5459 5637/5491/5480 5636/5492/5479\r\nf 5619/5469/5462 5639/5494/5482 5637/5491/5480 5616/5466/5459\r\nf 5640/5493/5483 5638/5490/5481 5637/5491/5480 5639/5494/5482\r\nf 5641/5495/5484 5642/5498/5485 5643/5497/5486 5644/5496/5487\r\nf 5634/5503/5477 5633/5502/5476 5643/5497/5486 5642/5498/5485\r\nf 5632/5501/5475 5645/5500/5488 5643/5497/5486 5633/5502/5476\r\nf 5646/5499/5489 5644/5496/5487 5643/5497/5486 5645/5500/5488\r\nf 5647/5446/5490 5648/5505/5491 5649/5504/5492 5650/5441/5493\r\nf 5651/5453/5494 5652/5452/5495 5649/5504/5492 5648/5505/5491\r\nf 5653/5508/5496 5654/5507/5497 5649/5504/5492 5652/5452/5495\r\nf 5655/5506/5498 5650/5441/5493 5649/5504/5492 5654/5507/5497\r\nf 5656/5509/5499 5657/5450/5500 5658/5511/5501 5659/5510/5502\r\nf 5660/5455/5503 5661/5513/5504 5658/5511/5501 5657/5450/5500\r\nf 5662/5460/5505 5663/5457/5506 5658/5511/5501 5661/5513/5504\r\nf 5664/5512/5507 5659/5510/5502 5658/5511/5501 5663/5457/5506\r\nf 5665/5514/5508 5666/5517/5509 5667/5516/5510 5668/5515/5511\r\nf 5669/5522/5512 5670/5521/5513 5667/5516/5510 5666/5517/5509\r\nf 5671/5520/5514 5672/5519/5515 5667/5516/5510 5670/5521/5513\r\nf 5673/5518/5516 5668/5515/5511 5667/5516/5510 5672/5519/5515\r\nf 5655/5506/5498 5654/5507/5497 5674/5524/5517 5675/5523/5518\r\nf 5653/5508/5496 5676/5528/5519 5674/5524/5517 5654/5507/5497\r\nf 5677/5527/5520 5678/5526/5521 5674/5524/5517 5676/5528/5519\r\nf 5679/5525/5522 5675/5523/5518 5674/5524/5517 5678/5526/5521\r\nf 5680/5529/5523 5681/5532/5524 5682/5531/5525 5683/5530/5526\r\nf 5656/5509/5499 5659/5510/5502 5682/5531/5525 5681/5532/5524\r\nf 5664/5512/5507 5684/5533/5527 5682/5531/5525 5659/5510/5502\r\nf 5669/5522/5512 5683/5530/5526 5682/5531/5525 5684/5533/5527\r\nf 5662/5460/5505 5685/5461/5528 5686/5458/5529 5663/5457/5506\r\nf 5687/5462/5530 5688/5463/5531 5686/5458/5529 5685/5461/5528\r\nf 5689/5464/5532 5690/5534/5533 5686/5458/5529 5688/5463/5531\r\nf 5664/5512/5507 5663/5457/5506 5686/5458/5529 5690/5534/5533\r\nf 5647/5473/5490 5691/5536/5534 5692/5535/5535 5693/5468/5536\r\nf 5694/5540/5537 5695/5539/5538 5692/5535/5535 5691/5536/5534\r\nf 5696/5538/5539 5697/5537/5540 5692/5535/5535 5695/5539/5538\r\nf 5698/5465/5541 5693/5468/5536 5692/5535/5535 5697/5537/5540\r\nf 5699/5541/5542 5700/5544/5543 5701/5543/5544 5702/5542/5545\r\nf 5703/5548/5546 5704/5547/5547 5701/5543/5544 5700/5544/5543\r\nf 5705/5546/5548 5706/5545/5549 5701/5543/5544 5704/5547/5547\r\nf 5679/5525/5522 5702/5542/5545 5701/5543/5544 5706/5545/5549\r\nf 5707/5549/5550 5708/5552/5551 5709/5551/5552 5710/5550/5553\r\nf 5677/5527/5520 5711/5555/5554 5709/5551/5552 5708/5552/5551\r\nf 5680/5529/5523 5712/5554/5555 5709/5551/5552 5711/5555/5554\r\nf 5713/5553/5556 5710/5550/5553 5709/5551/5552 5712/5554/5555\r\nf 5669/5522/5512 5684/5533/5527 5714/5556/5557 5670/5521/5513\r\nf 5664/5512/5507 5690/5534/5533 5714/5556/5557 5684/5533/5527\r\nf 5689/5464/5532 5715/5557/5558 5714/5556/5557 5690/5534/5533\r\nf 5671/5520/5514 5670/5521/5513 5714/5556/5557 5715/5557/5558\r\nf 5610/5462/5453 5716/5560/5559 5717/5559/5560 5611/5558/5454\r\nf 5632/5487/5475 5631/5488/5474 5717/5559/5560 5716/5560/5559\r\nf 5626/5480/5469 5718/5562/5561 5717/5559/5560 5631/5488/5474\r\nf 5612/5561/5455 5611/5558/5454 5717/5559/5560 5718/5562/5561\r\nf 5599/5563/5442 5719/5566/5562 5720/5565/5563 5600/5564/5443\r\nf 5624/5482/5467 5721/5567/5564 5720/5565/5563 5719/5566/5562\r\nf 5619/5469/5462 5618/5470/5461 5720/5565/5563 5721/5567/5564\r\nf 5601/5471/5444 5600/5564/5443 5720/5565/5563 5618/5470/5461\r\nf 5646/5499/5489 5645/5500/5488 5722/5569/5565 5723/5568/5566\r\nf 5632/5501/5475 5716/5573/5559 5722/5569/5565 5645/5500/5488\r\nf 5610/5572/5453 5609/5571/5452 5722/5569/5565 5716/5573/5559\r\nf 5608/5570/5451 5723/5568/5566 5722/5569/5565 5609/5571/5452\r\nf 5724/5574/5567 5725/5577/5568 5726/5576/5569 5727/5575/5570\r\nf 5728/5579/5571 5729/5578/5572 5726/5576/5569 5725/5577/5568\r\nf 5646/5499/5489 5723/5568/5566 5726/5576/5569 5729/5578/5572\r\nf 5608/5570/5451 5727/5575/5570 5726/5576/5569 5723/5568/5566\r\nf 5730/5480/5573 5731/5488/5574 5732/5559/5575 5733/5562/5576\r\nf 5734/5487/5577 5735/5560/5578 5732/5559/5575 5731/5488/5574\r\nf 5687/5462/5530 5685/5558/5528 5732/5559/5575 5735/5560/5578\r\nf 5662/5561/5505 5733/5562/5576 5732/5559/5575 5685/5558/5528\r\nf 5736/5482/5579 5737/5481/5580 5738/5580/5581 5739/5566/5582\r\nf 5730/5480/5573 5733/5562/5576 5738/5580/5581 5737/5481/5580\r\nf 5662/5561/5505 5661/5581/5504 5738/5580/5581 5733/5562/5576\r\nf 5660/5563/5503 5739/5566/5582 5738/5580/5581 5661/5581/5504\r\nf 5734/5501/5577 5740/5500/5583 5741/5569/5584 5735/5573/5578\r\nf 5742/5583/5585 5743/5582/5586 5741/5569/5584 5740/5500/5583\r\nf 5689/5570/5532 5688/5571/5531 5741/5569/5584 5743/5582/5586\r\nf 5687/5572/5530 5735/5573/5578 5741/5569/5584 5688/5571/5531\r\nf 5698/5465/5541 5697/5537/5540 5744/5584/5587 5745/5492/5588\r\nf 5696/5538/5539 5746/5588/5589 5744/5584/5587 5697/5537/5540\r\nf 5747/5587/5590 5748/5586/5591 5744/5584/5587 5746/5588/5589\r\nf 5749/5585/5592 5745/5492/5588 5744/5584/5587 5748/5586/5591\r\nf 5689/5570/5532 5743/5582/5586 5750/5590/5593 5715/5589/5558\r\nf 5742/5583/5585 5751/5594/5594 5750/5590/5593 5743/5582/5586\r\nf 5752/5593/5595 5753/5592/5596 5750/5590/5593 5751/5594/5594\r\nf 5671/5591/5514 5715/5589/5558 5750/5590/5593 5753/5592/5596\r\nf 5634/5503/5477 5642/5498/5485 5754/5596/5597 5755/5595/5598\r\nf 5641/5495/5484 5756/5600/5599 5754/5596/5597 5642/5498/5485\r\nf 5291/5599/5134 5290/5598/5133 5754/5596/5597 5756/5600/5599\r\nf 5289/5597/5132 5755/5595/5598 5754/5596/5597 5290/5598/5133\r\nf 5742/5583/5585 5757/5602/5600 5758/5601/5601 5751/5594/5594\r\nf 5759/5606/5602 5760/5605/5603 5758/5601/5601 5757/5602/5600\r\nf 5761/5604/5604 5762/5603/5605 5758/5601/5601 5760/5605/5603\r\nf 5752/5593/5595 5751/5594/5594 5758/5601/5601 5762/5603/5605\r\nf 5763/5607/5606 5764/5608/5607 5765/5476/5608 5766/5477/5609\r\nf 5767/5609/5610 5768/5479/5611 5765/5476/5608 5764/5608/5607\r\nf 5730/5480/5573 5737/5481/5580 5765/5476/5608 5768/5479/5611\r\nf 5736/5482/5579 5766/5477/5609 5765/5476/5608 5737/5481/5580\r\nf 5767/5609/5610 5769/5610/5612 5770/5484/5613 5768/5479/5611\r\nf 5771/5485/5614 5772/5486/5615 5770/5484/5613 5769/5610/5612\r\nf 5734/5487/5577 5731/5488/5574 5770/5484/5613 5772/5486/5615\r\nf 5730/5480/5573 5768/5479/5611 5770/5484/5613 5731/5488/5574\r\nf 5734/5501/5577 5772/5502/5615 5773/5497/5616 5740/5500/5583\r\nf 5771/5503/5614 5774/5611/5617 5773/5497/5616 5772/5502/5615\r\nf 5759/5606/5602 5757/5602/5600 5773/5497/5616 5774/5611/5617\r\nf 5742/5583/5585 5740/5500/5583 5773/5497/5616 5757/5602/5600\r\nf 5775/5612/5618 5776/5615/5619 5777/5614/5620 5778/5613/5621\r\nf 5641/5495/5484 5644/5496/5487 5777/5614/5620 5776/5615/5619\r\nf 5646/5499/5489 5729/5578/5572 5777/5614/5620 5644/5496/5487\r\nf 5728/5579/5571 5778/5613/5621 5777/5614/5620 5729/5578/5572\r\nf 5749/5585/5592 5748/5586/5591 5779/5617/5622 5780/5616/5623\r\nf 5747/5587/5590 5781/5621/5624 5779/5617/5622 5748/5586/5591\r\nf 5319/5620/5162 5318/5619/5161 5779/5617/5622 5781/5621/5624\r\nf 5317/5618/5160 5780/5616/5623 5779/5617/5622 5318/5619/5161\r\nf 5759/5606/5602 5782/5623/5625 5783/5622/5626 5760/5605/5603\r\nf 5326/5627/5169 5325/5626/5168 5783/5622/5626 5782/5623/5625\r\nf 5324/5625/5167 5784/5624/5627 5783/5622/5626 5325/5626/5168\r\nf 5761/5604/5604 5760/5605/5603 5783/5622/5626 5784/5624/5627\r\nf 5785/5444/5628 5786/5628/5629 5787/5628/5630 5788/5444/5631\r\nf 5789/5629/5632 5790/5629/5633 5787/5628/5630 5786/5628/5629\r\nf 5594/5442/5437 5593/5443/5436 5787/5628/5630 5790/5629/5633\r\nf 5592/5444/5435 5788/5444/5631 5787/5628/5630 5593/5443/5436\r\nf 5791/5630/5634 5792/5631/5635 5793/5631/5636 5794/5630/5637\r\nf 5785/5540/5628 5788/5540/5631 5793/5631/5636 5792/5631/5635\r\nf 5592/5540/5435 5795/5539/5638 5793/5631/5636 5788/5540/5631\r\nf 5796/5538/5639 5794/5630/5637 5793/5631/5636 5795/5539/5638\r\nf 5694/5444/5537 5797/5443/5640 5798/5628/5641 5799/5444/5642\r\nf 5800/5633/5643 5801/5632/5644 5798/5628/5641 5797/5443/5640\r\nf 5802/5632/5645 5803/5628/5646 5798/5628/5641 5801/5632/5644\r\nf 5804/5444/5647 5799/5444/5642 5798/5628/5641 5803/5628/5646\r\nf 5804/5540/5647 5805/5631/5648 5806/5631/5649 5799/5540/5642\r\nf 5807/5630/5650 5808/5630/5651 5806/5631/5649 5805/5631/5648\r\nf 5696/5538/5539 5695/5539/5538 5806/5631/5649 5808/5630/5651\r\nf 5694/5540/5537 5799/5540/5642 5806/5631/5649 5695/5539/5538\r\nf 5809/5634/5652 5810/5635/5653 5811/5635/5654 5812/5634/5655\r\nf 5813/5636/5656 5814/5636/5657 5811/5635/5654 5810/5635/5653\r\nf 5705/5546/5548 5704/5547/5547 5811/5635/5654 5814/5636/5657\r\nf 5703/5548/5546 5812/5634/5655 5811/5635/5654 5704/5547/5547\r\nf 5813/5636/5656 5815/5637/5658 5816/5637/5659 5814/5636/5657\r\nf 5802/5632/5645 5801/5632/5644 5816/5637/5659 5815/5637/5658\r\nf 5800/5633/5643 5817/5638/5660 5816/5637/5659 5801/5632/5644\r\nf 5705/5546/5548 5814/5636/5657 5816/5637/5659 5817/5638/5660\r\nf 5818/5639/5661 5819/5640/5662 5820/5640/5663 5821/5639/5664\r\nf 5791/5630/5634 5794/5630/5637 5820/5640/5663 5819/5640/5662\r\nf 5796/5538/5639 5822/5588/5665 5820/5640/5663 5794/5630/5637\r\nf 5823/5641/5666 5821/5639/5664 5820/5640/5663 5822/5588/5665\r\nf 5807/5630/5650 5824/5640/5667 5825/5640/5668 5808/5630/5651\r\nf 5826/5642/5669 5827/5642/5670 5825/5640/5668 5824/5640/5667\r\nf 5747/5587/5590 5746/5588/5589 5825/5640/5668 5827/5642/5670\r\nf 5696/5538/5539 5808/5630/5651 5825/5640/5668 5746/5588/5589\r\nf 5826/5642/5669 5828/5643/5671 5829/5643/5672 5827/5642/5670\r\nf 5373/5620/5216 5372/5620/5215 5829/5643/5672 5828/5643/5671\r\nf 5319/5620/5162 5781/5621/5624 5829/5643/5672 5372/5620/5215\r\nf 5747/5587/5590 5827/5642/5670 5829/5643/5672 5781/5621/5624\r\nf 5612/5460/5455 5830/5513/5673 5831/5511/5674 5607/5457/5450\r\nf 5599/5455/5442 5596/5450/5439 5831/5511/5674 5830/5513/5673\r\nf 5595/5447/5438 5832/5644/5675 5831/5511/5674 5596/5450/5439\r\nf 5604/5456/5447 5607/5457/5450 5831/5511/5674 5832/5644/5675\r\nf 5713/5553/5556 5712/5554/5555 5833/5646/5676 5834/5645/5677\r\nf 5680/5529/5523 5683/5530/5526 5833/5646/5676 5712/5554/5555\r\nf 5669/5522/5512 5666/5517/5509 5833/5646/5676 5683/5530/5526\r\nf 5665/5514/5508 5834/5645/5677 5833/5646/5676 5666/5517/5509\r\nf 5624/5482/5467 5621/5477/5464 5835/5647/5678 5721/5567/5564\r\nf 5620/5474/5463 5836/5648/5679 5835/5647/5678 5621/5477/5464\r\nf 5640/5493/5483 5639/5494/5482 5835/5647/5678 5836/5648/5679\r\nf 5619/5469/5462 5721/5567/5564 5835/5647/5678 5639/5494/5482\r\nf 5660/5455/5503 5657/5450/5500 5837/5449/5680 5838/5454/5681\r\nf 5656/5509/5499 5839/5649/5682 5837/5449/5680 5657/5450/5500\r\nf 5653/5508/5496 5652/5452/5495 5837/5449/5680 5839/5649/5682\r\nf 5651/5453/5494 5838/5454/5681 5837/5449/5680 5652/5452/5495\r\nf 5656/5509/5499 5681/5532/5524 5840/5650/5683 5839/5649/5682\r\nf 5680/5529/5523 5711/5555/5554 5840/5650/5683 5681/5532/5524\r\nf 5677/5527/5520 5676/5528/5519 5840/5650/5683 5711/5555/5554\r\nf 5653/5508/5496 5839/5649/5682 5840/5650/5683 5676/5528/5519\r\nf 5624/5482/5467 5719/5566/5562 5841/5580/5684 5625/5481/5468\r\nf 5599/5563/5442 5830/5581/5673 5841/5580/5684 5719/5566/5562\r\nf 5612/5561/5455 5718/5562/5561 5841/5580/5684 5830/5581/5673\r\nf 5626/5480/5469 5625/5481/5468 5841/5580/5684 5718/5562/5561\r\nf 5842/5469/5685 5843/5567/5686 5844/5565/5687 5845/5470/5688\r\nf 5736/5482/5579 5739/5566/5582 5844/5565/5687 5843/5567/5686\r\nf 5660/5563/5503 5838/5564/5681 5844/5565/5687 5739/5566/5582\r\nf 5651/5471/5494 5845/5470/5688 5844/5565/5687 5838/5564/5681\r\nf 5620/5474/5463 5846/5652/5689 5847/5651/5690 5836/5648/5679\r\nf 5395/5656/5238 5394/5655/5237 5847/5651/5690 5846/5652/5689\r\nf 5393/5654/5236 5848/5653/5691 5847/5651/5690 5394/5655/5237\r\nf 5640/5493/5483 5836/5648/5679 5847/5651/5690 5848/5653/5691\r\nf 5849/5657/5692 5850/5658/5693 5851/5647/5694 5852/5494/5695\r\nf 5763/5607/5606 5766/5477/5609 5851/5647/5694 5850/5658/5693\r\nf 5736/5482/5579 5843/5567/5686 5851/5647/5694 5766/5477/5609\r\nf 5842/5469/5685 5852/5494/5695 5851/5647/5694 5843/5567/5686\r\nf 5590/5446/5433 5587/5441/5430 5853/5504/5696 5617/5505/5460\r\nf 5586/5438/5429 5854/5659/5697 5853/5504/5696 5587/5441/5430\r\nf 5603/5451/5446 5602/5452/5445 5853/5504/5696 5854/5659/5697\r\nf 5601/5453/5444 5617/5505/5460 5853/5504/5696 5602/5452/5445\r\nf 5707/5549/5550 5855/5661/5698 5856/5660/5699 5708/5552/5551\r\nf 5699/5541/5542 5702/5542/5545 5856/5660/5699 5855/5661/5698\r\nf 5679/5525/5522 5678/5526/5521 5856/5660/5699 5702/5542/5545\r\nf 5677/5527/5520 5708/5552/5551 5856/5660/5699 5678/5526/5521\r\nf 5590/5473/5433 5614/5468/5457 5857/5535/5700 5591/5536/5434\r\nf 5613/5465/5456 5858/5537/5701 5857/5535/5700 5614/5468/5457\r\nf 5796/5538/5639 5795/5539/5638 5857/5535/5700 5858/5537/5701\r\nf 5592/5540/5435 5591/5536/5434 5857/5535/5700 5795/5539/5638\r\nf 5613/5465/5456 5636/5492/5479 5859/5584/5702 5858/5537/5701\r\nf 5635/5489/5478 5860/5662/5703 5859/5584/5702 5636/5492/5479\r\nf 5823/5641/5666 5822/5588/5665 5859/5584/5702 5860/5662/5703\r\nf 5796/5538/5639 5858/5537/5701 5859/5584/5702 5822/5588/5665\r\nf 5694/5444/5537 5691/5445/5534 5861/5440/5704 5797/5443/5640\r\nf 5647/5446/5490 5650/5441/5493 5861/5440/5704 5691/5445/5534\r\nf 5655/5506/5498 5862/5663/5705 5861/5440/5704 5650/5441/5493\r\nf 5800/5633/5643 5797/5443/5640 5861/5440/5704 5862/5663/5705\r\nf 5800/5633/5643 5862/5663/5705 5863/5664/5706 5817/5638/5660\r\nf 5655/5506/5498 5675/5523/5518 5863/5664/5706 5862/5663/5705\r\nf 5679/5525/5522 5706/5545/5549 5863/5664/5706 5675/5523/5518\r\nf 5705/5546/5548 5817/5638/5660 5863/5664/5706 5706/5545/5549\r\nf 5651/5471/5494 5648/5472/5491 5864/5467/5707 5845/5470/5688\r\nf 5647/5473/5490 5693/5468/5536 5864/5467/5707 5648/5472/5491\r\nf 5698/5465/5541 5865/5466/5708 5864/5467/5707 5693/5468/5536\r\nf 5842/5469/5685 5845/5470/5688 5864/5467/5707 5865/5466/5708\r\nf 5842/5469/5685 5865/5466/5708 5866/5491/5709 5852/5494/5695\r\nf 5698/5465/5541 5745/5492/5588 5866/5491/5709 5865/5466/5708\r\nf 5749/5585/5592 5867/5665/5710 5866/5491/5709 5745/5492/5588\r\nf 5849/5657/5692 5852/5494/5695 5866/5491/5709 5867/5665/5710\r\nf 5635/5489/5478 5868/5667/5711 5869/5666/5712 5860/5662/5703\r\nf 5420/5671/5263 5419/5670/5262 5869/5666/5712 5868/5667/5711\r\nf 5418/5669/5261 5870/5668/5713 5869/5666/5712 5419/5670/5262\r\nf 5823/5641/5666 5860/5662/5703 5869/5666/5712 5870/5668/5713\r\nf 5849/5657/5692 5867/5665/5710 5871/5673/5714 5872/5672/5715\r\nf 5749/5585/5592 5780/5616/5623 5871/5673/5714 5867/5665/5710\r\nf 5317/5618/5160 5424/5675/5267 5871/5673/5714 5780/5616/5623\r\nf 5423/5674/5266 5872/5672/5715 5871/5673/5714 5424/5675/5267\r\nf 5873/5676/5716 5874/5679/5717 5875/5678/5718 5876/5677/5719\r\nf 5877/5684/5720 5878/5683/5721 5875/5678/5718 5874/5679/5717\r\nf 5724/5682/5567 5879/5681/5722 5875/5678/5718 5878/5683/5721\r\nf 5880/5680/5723 5876/5677/5719 5875/5678/5718 5879/5681/5722\r\nf 5881/5685/5724 5882/5688/5725 5883/5687/5726 5884/5686/5727\r\nf 5885/5693/5728 5886/5692/5729 5883/5687/5726 5882/5688/5725\r\nf 5887/5691/5730 5888/5690/5731 5883/5687/5726 5886/5692/5729\r\nf 5889/5689/5732 5884/5686/5727 5883/5687/5726 5888/5690/5731\r\nf 5890/5694/5733 5891/5697/5734 5892/5696/5735 5893/5695/5736\r\nf 5894/5702/5737 5895/5701/5738 5892/5696/5735 5891/5697/5734\r\nf 5896/5700/5739 5897/5699/5740 5892/5696/5735 5895/5701/5738\r\nf 5898/5698/5741 5893/5695/5736 5892/5696/5735 5897/5699/5740\r\nf 5899/5703/5742 5900/5706/5743 5901/5705/5744 5902/5704/5745\r\nf 5877/5711/5720 5903/5710/5746 5901/5705/5744 5900/5706/5743\r\nf 5904/5709/5747 5905/5708/5748 5901/5705/5744 5903/5710/5746\r\nf 5906/5707/5749 5902/5704/5745 5901/5705/5744 5905/5708/5748\r\nf 5907/5712/5750 5908/5715/5751 5909/5714/5752 5910/5713/5753\r\nf 5911/5717/5754 5912/5716/5755 5909/5714/5752 5908/5715/5751\r\nf 5899/5703/5742 5902/5704/5745 5909/5714/5752 5912/5716/5755\r\nf 5906/5707/5749 5910/5713/5753 5909/5714/5752 5902/5704/5745\r\nf 5913/5718/5756 5914/5719/5757 5915/5719/5758 5916/5718/5759\r\nf 5917/5720/5760 5918/5720/5761 5915/5719/5758 5914/5719/5757\r\nf 5885/5693/5728 5882/5688/5725 5915/5719/5758 5918/5720/5761\r\nf 5881/5685/5724 5916/5718/5759 5915/5719/5758 5882/5688/5725\r\nf 5894/5702/5737 5919/5722/5762 5920/5721/5763 5895/5701/5738\r\nf 5873/5676/5716 5876/5677/5719 5920/5721/5763 5919/5722/5762\r\nf 5880/5680/5723 5921/5723/5764 5920/5721/5763 5876/5677/5719\r\nf 5896/5700/5739 5895/5701/5738 5920/5721/5763 5921/5723/5764\r\nf 5889/5689/5732 5888/5690/5731 5922/5725/5765 5923/5724/5766\r\nf 5887/5691/5730 5924/5726/5767 5922/5725/5765 5888/5690/5731\r\nf 5890/5694/5733 5893/5695/5736 5922/5725/5765 5924/5726/5767\r\nf 5898/5698/5741 5923/5724/5766 5922/5725/5765 5893/5695/5736\r\nf 5899/5703/5742 5925/5728/5768 5926/5727/5769 5900/5706/5743\r\nf 5728/5579/5571 5725/5577/5568 5926/5727/5769 5925/5728/5768\r\nf 5724/5574/5567 5878/5729/5721 5926/5727/5769 5725/5577/5568\r\nf 5877/5711/5720 5900/5706/5743 5926/5727/5769 5878/5729/5721\r\nf 5911/5717/5754 5927/5731/5770 5928/5730/5771 5912/5716/5755\r\nf 5775/5612/5618 5778/5613/5621 5928/5730/5771 5927/5731/5770\r\nf 5728/5579/5571 5925/5728/5768 5928/5730/5771 5778/5613/5621\r\nf 5899/5703/5742 5912/5716/5755 5928/5730/5771 5925/5728/5768\r\nf 5880/5680/5723 5879/5681/5722 5929/5733/5772 5930/5732/5773\r\nf 5724/5682/5567 5727/5734/5570 5929/5733/5772 5879/5681/5722\r\nf 5608/5464/5451 5605/5459/5448 5929/5733/5772 5727/5734/5570\r\nf 5604/5456/5447 5930/5732/5773 5929/5733/5772 5605/5459/5448\r\nf 5594/5442/5437 5931/5736/5774 5932/5735/5775 5589/5439/5432\r\nf 5881/5685/5724 5884/5686/5727 5932/5735/5775 5931/5736/5774\r\nf 5889/5689/5732 5933/5737/5776 5932/5735/5775 5884/5686/5727\r\nf 5586/5438/5429 5589/5439/5432 5932/5735/5775 5933/5737/5776\r\nf 5898/5698/5741 5897/5699/5740 5934/5739/5777 5935/5738/5778\r\nf 5896/5700/5739 5936/5740/5779 5934/5739/5777 5897/5699/5740\r\nf 5595/5447/5438 5598/5448/5441 5934/5739/5777 5936/5740/5779\r\nf 5603/5451/5446 5935/5738/5778 5934/5739/5777 5598/5448/5441\r\nf 5789/5629/5632 5937/5741/5780 5938/5741/5781 5790/5629/5633\r\nf 5913/5718/5756 5916/5718/5759 5938/5741/5781 5937/5741/5780\r\nf 5881/5685/5724 5931/5736/5774 5938/5741/5781 5916/5718/5759\r\nf 5594/5442/5437 5790/5629/5633 5938/5741/5781 5931/5736/5774\r\nf 5896/5700/5739 5921/5723/5764 5939/5742/5782 5936/5740/5779\r\nf 5880/5680/5723 5930/5732/5773 5939/5742/5782 5921/5723/5764\r\nf 5604/5456/5447 5832/5644/5675 5939/5742/5782 5930/5732/5773\r\nf 5595/5447/5438 5936/5740/5779 5939/5742/5782 5832/5644/5675\r\nf 5586/5438/5429 5933/5737/5776 5940/5743/5783 5854/5659/5697\r\nf 5889/5689/5732 5923/5724/5766 5940/5743/5783 5933/5737/5776\r\nf 5898/5698/5741 5935/5738/5778 5940/5743/5783 5923/5724/5766\r\nf 5603/5451/5446 5854/5659/5697 5940/5743/5783 5935/5738/5778\r\nf 5906/5707/5749 5905/5708/5748 5941/5745/5784 5942/5744/5785\r\nf 5904/5709/5747 5943/5749/5786 5941/5745/5784 5905/5708/5748\r\nf 5673/5748/5516 5944/5747/5787 5941/5745/5784 5943/5749/5786\r\nf 5945/5746/5788 5942/5744/5785 5941/5745/5784 5944/5747/5787\r\nf 5907/5712/5750 5910/5713/5753 5946/5751/5789 5947/5750/5790\r\nf 5906/5707/5749 5942/5744/5785 5946/5751/5789 5910/5713/5753\r\nf 5945/5746/5788 5948/5753/5791 5946/5751/5789 5942/5744/5785\r\nf 5949/5752/5792 5947/5750/5790 5946/5751/5789 5948/5753/5791\r\nf 5950/5754/5793 5951/5757/5794 5952/5756/5795 5953/5755/5796\r\nf 5904/5759/5747 5903/5758/5746 5952/5756/5795 5951/5757/5794\r\nf 5877/5684/5720 5874/5679/5717 5952/5756/5795 5903/5758/5746\r\nf 5873/5676/5716 5953/5755/5796 5952/5756/5795 5874/5679/5717\r\nf 5885/5693/5728 5954/5761/5797 5955/5760/5798 5886/5692/5729\r\nf 5956/5765/5799 5957/5764/5800 5955/5760/5798 5954/5761/5797\r\nf 5958/5763/5801 5959/5762/5802 5955/5760/5798 5957/5764/5800\r\nf 5887/5691/5730 5886/5692/5729 5955/5760/5798 5959/5762/5802\r\nf 5960/5766/5803 5961/5769/5804 5962/5768/5805 5963/5767/5806\r\nf 5964/5771/5807 5965/5770/5808 5962/5768/5805 5961/5769/5804\r\nf 5894/5702/5737 5891/5697/5734 5962/5768/5805 5965/5770/5808\r\nf 5890/5694/5733 5963/5767/5806 5962/5768/5805 5891/5697/5734\r\nf 5917/5720/5760 5966/5772/5809 5967/5772/5810 5918/5720/5761\r\nf 5968/5773/5811 5969/5773/5812 5967/5772/5810 5966/5772/5809\r\nf 5956/5765/5799 5954/5761/5797 5967/5772/5810 5969/5773/5812\r\nf 5885/5693/5728 5918/5720/5761 5967/5772/5810 5954/5761/5797\r\nf 5964/5771/5807 5970/5775/5813 5971/5774/5814 5965/5770/5808\r\nf 5950/5754/5793 5953/5755/5796 5971/5774/5814 5970/5775/5813\r\nf 5873/5676/5716 5919/5722/5762 5971/5774/5814 5953/5755/5796\r\nf 5894/5702/5737 5965/5770/5808 5971/5774/5814 5919/5722/5762\r\nf 5958/5763/5801 5972/5777/5815 5973/5776/5816 5959/5762/5802\r\nf 5960/5766/5803 5963/5767/5806 5973/5776/5816 5972/5777/5815\r\nf 5890/5694/5733 5924/5726/5767 5973/5776/5816 5963/5767/5806\r\nf 5887/5691/5730 5959/5762/5802 5973/5776/5816 5924/5726/5767\r\nf 5665/5514/5508 5668/5515/5511 5974/5779/5817 5975/5778/5818\r\nf 5673/5518/5516 5943/5780/5786 5974/5779/5817 5668/5515/5511\r\nf 5904/5759/5747 5951/5757/5794 5974/5779/5817 5943/5780/5786\r\nf 5950/5754/5793 5975/5778/5818 5974/5779/5817 5951/5757/5794\r\nf 5703/5548/5546 5700/5544/5543 5976/5782/5819 5977/5781/5820\r\nf 5699/5541/5542 5978/5783/5821 5976/5782/5819 5700/5544/5543\r\nf 5958/5763/5801 5957/5764/5800 5976/5782/5819 5978/5783/5821\r\nf 5956/5765/5799 5977/5781/5820 5976/5782/5819 5957/5764/5800\r\nf 5707/5549/5550 5710/5550/5553 5979/5785/5822 5980/5784/5823\r\nf 5713/5553/5556 5981/5786/5824 5979/5785/5822 5710/5550/5553\r\nf 5964/5771/5807 5961/5769/5804 5979/5785/5822 5981/5786/5824\r\nf 5960/5766/5803 5980/5784/5823 5979/5785/5822 5961/5769/5804\r\nf 5968/5773/5811 5982/5787/5825 5983/5787/5826 5969/5773/5812\r\nf 5809/5634/5652 5812/5634/5655 5983/5787/5826 5982/5787/5825\r\nf 5703/5548/5546 5977/5781/5820 5983/5787/5826 5812/5634/5655\r\nf 5956/5765/5799 5969/5773/5812 5983/5787/5826 5977/5781/5820\r\nf 5713/5553/5556 5834/5645/5677 5984/5788/5827 5981/5786/5824\r\nf 5665/5514/5508 5975/5778/5818 5984/5788/5827 5834/5645/5677\r\nf 5950/5754/5793 5970/5775/5813 5984/5788/5827 5975/5778/5818\r\nf 5964/5771/5807 5981/5786/5824 5984/5788/5827 5970/5775/5813\r\nf 5699/5541/5542 5855/5661/5698 5985/5789/5828 5978/5783/5821\r\nf 5707/5549/5550 5980/5784/5823 5985/5789/5828 5855/5661/5698\r\nf 5960/5766/5803 5972/5777/5815 5985/5789/5828 5980/5784/5823\r\nf 5958/5763/5801 5978/5783/5821 5985/5789/5828 5972/5777/5815\r\nf 5752/5593/5595 5986/5791/5829 5987/5790/5830 5753/5592/5596\r\nf 5945/5746/5788 5944/5747/5787 5987/5790/5830 5986/5791/5829\r\nf 5673/5748/5516 5672/5792/5515 5987/5790/5830 5944/5747/5787\r\nf 5671/5591/5514 5753/5592/5596 5987/5790/5830 5672/5792/5515\r\nf 5949/5752/5792 5948/5753/5791 5988/5794/5831 5989/5793/5832\r\nf 5945/5746/5788 5986/5791/5829 5988/5794/5831 5948/5753/5791\r\nf 5752/5593/5595 5762/5603/5605 5988/5794/5831 5986/5791/5829\r\nf 5761/5604/5604 5989/5793/5832 5988/5794/5831 5762/5603/5605\r\nf 5542/5795/5385 5545/5798/5388 5990/5797/5833 5991/5796/5834\r\nf 5395/5656/5238 5846/5652/5689 5990/5797/5833 5545/5798/5388\r\nf 5620/5474/5463 5623/5475/5466 5990/5797/5833 5846/5652/5689\r\nf 5628/5478/5471 5991/5796/5834 5990/5797/5833 5623/5475/5466\r\nf 5289/5799/5132 5547/5802/5390 5992/5801/5835 5755/5800/5598\r\nf 5542/5795/5385 5991/5796/5834 5992/5801/5835 5547/5802/5390\r\nf 5628/5478/5471 5630/5483/5473 5992/5801/5835 5991/5796/5834\r\nf 5634/5485/5477 5755/5800/5598 5992/5801/5835 5630/5483/5473\r\nf 5393/5654/5236 5549/5804/5392 5993/5803/5836 5848/5653/5691\r\nf 5420/5671/5263 5868/5667/5711 5993/5803/5836 5549/5804/5392\r\nf 5635/5489/5478 5638/5490/5481 5993/5803/5836 5868/5667/5711\r\nf 5640/5493/5483 5848/5653/5691 5993/5803/5836 5638/5490/5481\r\nf 5550/5805/5393 5553/5808/5396 5994/5807/5837 5995/5806/5838\r\nf 5326/5627/5169 5782/5623/5625 5994/5807/5837 5553/5808/5396\r\nf 5759/5606/5602 5774/5611/5617 5994/5807/5837 5782/5623/5625\r\nf 5771/5503/5614 5995/5806/5838 5994/5807/5837 5774/5611/5617\r\nf 5554/5809/5397 5557/5812/5400 5996/5811/5839 5997/5810/5840\r\nf 5559/5814/5402 5998/5813/5841 5996/5811/5839 5557/5812/5400\r\nf 5767/5609/5610 5764/5608/5607 5996/5811/5839 5998/5813/5841\r\nf 5763/5607/5606 5997/5810/5840 5996/5811/5839 5764/5608/5607\r\nf 5559/5814/5402 5561/5816/5404 5999/5815/5842 5998/5813/5841\r\nf 5550/5818/5393 5995/5817/5838 5999/5815/5842 5561/5816/5404\r\nf 5771/5485/5614 5769/5610/5612 5999/5815/5842 5995/5817/5838\r\nf 5767/5609/5610 5998/5813/5841 5999/5815/5842 5769/5610/5612\r\nf 5562/5819/5405 5565/5822/5408 6000/5821/5843 6001/5820/5844\r\nf 5291/5599/5134 5756/5600/5599 6000/5821/5843 5565/5822/5408\r\nf 5641/5495/5484 5776/5615/5619 6000/5821/5843 5756/5600/5599\r\nf 5775/5612/5618 6001/5820/5844 6000/5821/5843 5776/5615/5619\r\nf 5418/5669/5261 5567/5669/5410 6002/5823/5845 5870/5668/5713\r\nf 5569/5825/5412 6003/5824/5846 6002/5823/5845 5567/5669/5410\r\nf 5818/5639/5661 5821/5639/5664 6002/5823/5845 6003/5824/5846\r\nf 5823/5641/5666 5870/5668/5713 6002/5823/5845 5821/5639/5664\r\nf 5423/5674/5266 5571/5827/5414 6004/5826/5847 5872/5672/5715\r\nf 5554/5809/5397 5997/5810/5840 6004/5826/5847 5571/5827/5414\r\nf 5763/5607/5606 5850/5658/5693 6004/5826/5847 5997/5810/5840\r\nf 5849/5657/5692 5872/5672/5715 6004/5826/5847 5850/5658/5693\r\nf 5572/5828/5415 5575/5831/5418 6005/5830/5848 6006/5829/5849\r\nf 5577/5833/5420 6007/5832/5850 6005/5830/5848 5575/5831/5418\r\nf 5911/5717/5754 5908/5715/5751 6005/5830/5848 6007/5832/5850\r\nf 5907/5712/5750 6006/5829/5849 6005/5830/5848 5908/5715/5751\r\nf 5577/5833/5420 5579/5835/5422 6008/5834/5851 6007/5832/5850\r\nf 5562/5819/5405 6001/5820/5844 6008/5834/5851 5579/5835/5422\r\nf 5775/5612/5618 5927/5731/5770 6008/5834/5851 6001/5820/5844\r\nf 5911/5717/5754 6007/5832/5850 6008/5834/5851 5927/5731/5770\r\nf 5580/5836/5423 5583/5839/5426 6009/5838/5852 6010/5837/5853\r\nf 5572/5828/5415 6006/5829/5849 6009/5838/5852 5583/5839/5426\r\nf 5907/5712/5750 5947/5750/5790 6009/5838/5852 6006/5829/5849\r\nf 5949/5752/5792 6010/5837/5853 6009/5838/5852 5947/5750/5790\r\nf 5324/5625/5167 5585/5841/5428 6011/5840/5854 5784/5624/5627\r\nf 5580/5836/5423 6010/5837/5853 6011/5840/5854 5585/5841/5428\r\nf 5949/5752/5792 5989/5793/5832 6011/5840/5854 6010/5837/5853\r\nf 5761/5604/5604 5784/5624/5627 6011/5840/5854 5989/5793/5832\r\nv 200.62 0.32 129.42\r\nv 200.65 1.13 128.78\r\nv 201.53 1.16 129.85\r\nv 201.19 0.05 130.33\r\nv 201.11 2.68 128.16\r\nv 201.98 2.7 129.23\r\nv 202.81 2.74 130.35\r\nv 202.36 1.2 130.97\r\nv 201.89 0.37 131.04\r\nv 202.59 1.24 131.41\r\nv 201.98 0.11 131.49\r\nv 203.04 2.78 130.78\r\nv 202.81 2.88 131.04\r\nv 202.36 1.34 131.67\r\nv 201.76 0.22 131.75\r\nv 200.2 1.16 133\r\nv 200.63 2.01 132.99\r\nv 199.73 2 131.94\r\nv 199.45 0.86 132.35\r\nv 201.08 3.55 132.37\r\nv 200.18 3.54 131.32\r\nv 199.37 3.49 130.18\r\nv 198.92 1.95 130.8\r\nv 198.93 1.11 131.38\r\nv 198.76 1.88 130.3\r\nv 198.72 0.77 131.11\r\nv 199.21 3.43 129.67\r\nv 199.49 3.29 129.34\r\nv 199.04 1.75 129.96\r\nv 199 0.64 130.78\r\nv 201.6 4.35 127.49\r\nv 202.47 4.37 128.56\r\nv 202.13 6.13 126.78\r\nv 203 6.14 127.86\r\nv 203.83 6.18 128.98\r\nv 203.3 4.4 129.68\r\nv 203.53 4.44 130.12\r\nv 204.06 6.21 129.41\r\nv 203.83 6.32 129.68\r\nv 203.3 4.55 130.38\r\nv 201.57 5.22 131.7\r\nv 200.68 5.21 130.65\r\nv 202.1 6.99 131\r\nv 201.2 6.98 129.95\r\nv 200.39 6.94 128.8\r\nv 199.86 5.16 129.51\r\nv 199.7 5.1 129\r\nv 200.23 6.88 128.3\r\nv 200.52 6.74 127.96\r\nv 199.99 4.96 128.67\r\nv 202.69 8.02 126.05\r\nv 203.56 8.03 127.13\r\nv 203.27 9.96 125.31\r\nv 204.14 9.97 126.39\r\nv 204.97 9.99 127.51\r\nv 204.39 8.05 128.25\r\nv 204.61 8.09 128.69\r\nv 205.19 10.03 127.95\r\nv 204.97 10.13 128.21\r\nv 204.39 8.19 128.95\r\nv 202.65 8.86 130.27\r\nv 201.76 8.86 129.22\r\nv 203.23 10.79 129.54\r\nv 202.34 10.79 128.48\r\nv 201.53 10.76 127.34\r\nv 200.95 8.82 128.08\r\nv 200.79 8.76 127.57\r\nv 201.37 10.7 126.83\r\nv 201.65 10.57 126.5\r\nv 201.08 8.63 127.24\r\nv 203.86 11.92 124.58\r\nv 204.72 11.93 125.65\r\nv 204.43 13.87 123.86\r\nv 205.3 13.87 124.93\r\nv 206.13 13.89 126.06\r\nv 205.55 11.95 126.78\r\nv 205.78 11.98 127.22\r\nv 206.35 13.92 126.5\r\nv 206.13 14.02 126.76\r\nv 205.55 12.09 127.48\r\nv 203.81 12.74 128.8\r\nv 202.92 12.75 127.75\r\nv 204.39 14.68 128.09\r\nv 203.5 14.68 127.03\r\nv 202.69 14.65 125.88\r\nv 202.11 12.72 126.6\r\nv 201.95 12.66 126.09\r\nv 202.53 14.6 125.38\r\nv 202.82 14.47 125.04\r\nv 202.24 12.53 125.76\r\nv 204.99 15.74 123.17\r\nv 205.86 15.74 124.25\r\nv 205.52 17.52 122.53\r\nv 206.39 17.52 123.61\r\nv 207.21 17.53 124.73\r\nv 206.69 15.76 125.37\r\nv 206.91 15.79 125.81\r\nv 207.44 17.56 125.17\r\nv 207.21 17.66 125.44\r\nv 206.68 15.89 126.08\r\nv 204.94 16.55 127.4\r\nv 204.06 16.56 126.35\r\nv 205.47 18.31 126.77\r\nv 204.58 18.32 125.71\r\nv 203.77 18.3 124.56\r\nv 203.25 16.53 125.2\r\nv 203.09 16.47 124.69\r\nv 203.61 18.24 124.05\r\nv 203.9 18.11 123.71\r\nv 203.38 16.34 124.36\r\nv 206 19.15 121.94\r\nv 206.87 19.14 123.03\r\nv 206.43 20.59 121.44\r\nv 207.3 20.59 122.52\r\nv 208.12 20.6 123.65\r\nv 207.7 19.16 124.15\r\nv 207.92 19.19 124.59\r\nv 208.34 20.63 124.08\r\nv 208.12 20.73 124.35\r\nv 207.69 19.29 124.85\r\nv 205.95 19.93 126.19\r\nv 205.06 19.94 125.13\r\nv 206.37 21.36 125.68\r\nv 205.49 21.38 124.62\r\nv 204.68 21.36 123.47\r\nv 204.26 19.92 123.98\r\nv 204.1 19.87 123.47\r\nv 204.52 21.3 122.96\r\nv 204.81 21.18 122.63\r\nv 204.38 19.74 123.13\r\nv 206.79 21.81 121.01\r\nv 207.65 21.81 122.1\r\nv 207.09 22.84 120.67\r\nv 207.95 22.83 121.75\r\nv 208.77 22.83 122.88\r\nv 208.48 21.81 123.22\r\nv 208.7 21.84 123.66\r\nv 208.99 22.86 123.32\r\nv 208.77 22.95 123.59\r\nv 208.47 21.94 123.93\r\nv 206.72 22.57 125.27\r\nv 205.84 22.58 124.21\r\nv 207.02 23.57 124.93\r\nv 206.13 23.59 123.87\r\nv 205.33 23.57 122.71\r\nv 205.03 22.57 123.05\r\nv 204.88 22.51 122.54\r\nv 205.17 23.52 122.2\r\nv 205.46 23.4 121.87\r\nv 205.16 22.39 122.21\r\nv 207.33 23.7 120.39\r\nv 208.19 23.68 121.47\r\nv 207.53 24.42 120.17\r\nv 208.39 24.4 121.25\r\nv 209.21 24.4 122.39\r\nv 209.02 23.68 122.61\r\nv 209.24 23.71 123.05\r\nv 209.43 24.42 122.83\r\nv 209.2 24.5 123.09\r\nv 209.01 23.8 123.31\r\nv 207.25 24.4 124.66\r\nv 206.37 24.42 123.6\r\nv 207.44 25.08 124.45\r\nv 206.56 25.1 123.39\r\nv 205.76 25.1 122.23\r\nv 205.57 24.41 122.44\r\nv 205.41 24.36 121.93\r\nv 205.6 25.06 121.72\r\nv 205.89 24.94 121.38\r\nv 205.7 24.24 121.59\r\nv 207.69 25.03 119.99\r\nv 208.56 25.01 121.08\r\nv 207.83 25.57 119.86\r\nv 208.69 25.54 120.94\r\nv 209.51 25.52 122.08\r\nv 209.38 25 122.21\r\nv 209.59 25.02 122.66\r\nv 209.72 25.54 122.52\r\nv 209.49 25.61 122.79\r\nv 209.36 25.1 122.93\r\nv 207.59 25.64 124.29\r\nv 206.71 25.67 123.23\r\nv 207.71 26.12 124.17\r\nv 206.83 26.15 123.1\r\nv 206.03 26.16 121.94\r\nv 205.91 25.67 122.07\r\nv 205.76 25.64 121.55\r\nv 205.88 26.13 121.42\r\nv 206.18 26.03 121.08\r\nv 206.05 25.53 121.21\r\nv 207.94 26.05 119.75\r\nv 208.8 26.01 120.84\r\nv 208.04 26.5 119.66\r\nv 208.9 26.46 120.75\r\nv 209.71 26.43 121.89\r\nv 209.62 25.99 121.97\r\nv 209.83 26 122.42\r\nv 209.93 26.44 122.33\r\nv 209.7 26.51 122.6\r\nv 209.6 26.07 122.69\r\nv 207.81 26.54 124.07\r\nv 206.93 26.58 123.01\r\nv 207.9 26.94 123.99\r\nv 207.02 26.98 122.93\r\nv 206.22 27 121.76\r\nv 206.14 26.59 121.84\r\nv 205.98 26.57 121.33\r\nv 206.07 26.97 121.25\r\nv 206.37 26.89 120.9\r\nv 206.28 26.47 120.98\r\nv 208.13 26.95 119.58\r\nv 208.99 26.91 120.67\r\nv 208.22 27.43 119.51\r\nv 209.08 27.39 120.6\r\nv 209.89 27.36 121.73\r\nv 209.81 26.88 121.81\r\nv 210.02 26.89 122.25\r\nv 210.11 27.36 122.18\r\nv 209.87 27.42 122.45\r\nv 209.78 26.95 122.53\r\nv 207.98 27.34 123.92\r\nv 207.1 27.38 122.86\r\nv 208.06 27.77 123.86\r\nv 207.18 27.81 122.79\r\nv 206.39 27.84 121.63\r\nv 206.31 27.4 121.69\r\nv 206.16 27.38 121.17\r\nv 206.24 27.82 121.11\r\nv 206.54 27.75 120.76\r\nv 206.46 27.31 120.83\r\nv 208.31 27.96 119.44\r\nv 209.17 27.92 120.53\r\nv 208.4 28.58 119.37\r\nv 209.26 28.54 120.46\r\nv 210.07 28.51 121.6\r\nv 209.98 27.89 121.67\r\nv 210.2 27.89 122.11\r\nv 210.29 28.51 122.05\r\nv 210.05 28.55 122.32\r\nv 209.96 27.94 122.39\r\nv 208.14 28.26 123.79\r\nv 207.26 28.3 122.73\r\nv 208.22 28.83 123.73\r\nv 207.35 28.87 122.67\r\nv 206.55 28.9 121.5\r\nv 206.47 28.33 121.56\r\nv 206.32 28.31 121.04\r\nv 206.4 28.89 120.98\r\nv 206.71 28.84 120.63\r\nv 206.62 28.25 120.69\r\nv 208.5 29.3 119.3\r\nv 209.36 29.26 120.39\r\nv 208.6 30.16 119.24\r\nv 209.46 30.12 120.33\r\nv 210.28 30.09 121.47\r\nv 210.17 29.23 121.53\r\nv 210.38 29.23 121.98\r\nv 210.49 30.09 121.91\r\nv 210.25 30.12 122.19\r\nv 210.15 29.27 122.26\r\nv 208.32 29.52 123.67\r\nv 207.44 29.56 122.6\r\nv 208.42 30.35 123.6\r\nv 207.54 30.39 122.54\r\nv 206.75 30.42 121.37\r\nv 206.64 29.59 121.44\r\nv 206.5 29.58 120.92\r\nv 206.6 30.41 120.85\r\nv 206.9 30.37 120.5\r\nv 206.8 29.53 120.57\r\nv 208.72 31.18 119.17\r\nv 209.58 31.14 120.26\r\nv 208.85 32.39 119.09\r\nv 209.71 32.35 120.18\r\nv 210.52 32.32 121.32\r\nv 210.39 31.11 121.4\r\nv 210.61 31.11 121.84\r\nv 210.74 32.31 121.76\r\nv 210.5 32.34 122.04\r\nv 210.37 31.14 122.12\r\nv 208.53 31.35 123.53\r\nv 207.65 31.39 122.47\r\nv 208.66 32.54 123.46\r\nv 207.78 32.58 122.39\r\nv 206.99 32.61 121.23\r\nv 206.86 31.42 121.3\r\nv 206.71 31.41 120.78\r\nv 206.84 32.61 120.71\r\nv 207.15 32.57 120.35\r\nv 207.02 31.37 120.43\r\nv 209 33.81 119\r\nv 209.86 33.77 120.09\r\nv 209.17 35.42 118.91\r\nv 210.02 35.39 120\r\nv 210.84 35.36 121.14\r\nv 210.67 33.74 121.23\r\nv 210.89 33.74 121.68\r\nv 211.05 35.36 121.58\r\nv 210.81 35.38 121.86\r\nv 210.65 33.77 121.95\r\nv 208.81 33.96 123.37\r\nv 207.93 34 122.31\r\nv 208.98 35.57 123.28\r\nv 208.1 35.61 122.21\r\nv 207.3 35.64 121.05\r\nv 207.14 34.03 121.14\r\nv 206.99 34.02 120.62\r\nv 207.16 35.63 120.53\r\nv 207.46 35.6 120.17\r\nv 207.3 33.99 120.27\r\nv 209.34 37.2 118.81\r\nv 210.2 37.17 119.9\r\nv 209.53 39.11 118.7\r\nv 210.39 39.08 119.79\r\nv 211.2 39.05 120.93\r\nv 211.02 37.14 121.03\r\nv 211.23 37.13 121.48\r\nv 211.42 39.05 121.38\r\nv 211.17 39.07 121.65\r\nv 210.99 37.16 121.76\r\nv 209.15 37.34 123.18\r\nv 208.27 37.38 122.11\r\nv 209.34 39.25 123.07\r\nv 208.46 39.29 122.01\r\nv 207.67 39.31 120.84\r\nv 207.48 37.41 120.95\r\nv 207.33 37.4 120.43\r\nv 207.52 39.31 120.32\r\nv 207.82 39.28 119.97\r\nv 207.64 37.37 120.07\r\nv 209.72 41.13 118.59\r\nv 210.58 41.1 119.68\r\nv 209.92 43.23 118.48\r\nv 210.77 43.2 119.57\r\nv 211.59 43.17 120.71\r\nv 211.39 41.07 120.82\r\nv 211.61 41.07 121.27\r\nv 211.8 43.17 121.15\r\nv 211.56 43.19 121.43\r\nv 211.37 41.09 121.54\r\nv 209.53 41.27 122.96\r\nv 208.65 41.3 121.9\r\nv 209.73 43.36 122.85\r\nv 208.84 43.4 121.78\r\nv 208.05 43.42 120.62\r\nv 207.86 41.33 120.73\r\nv 207.71 41.32 120.21\r\nv 207.9 43.42 120.1\r\nv 208.21 43.39 119.75\r\nv 208.01 41.29 119.86\r\nv 210.11 45.38 118.36\r\nv 210.97 45.35 119.45\r\nv 210.3 47.55 118.25\r\nv 211.16 47.52 119.34\r\nv 211.98 47.49 120.48\r\nv 211.78 45.32 120.59\r\nv 212 45.32 121.04\r\nv 212.19 47.49 120.93\r\nv 211.95 47.51 121.2\r\nv 211.76 45.34 121.32\r\nv 209.92 45.51 122.74\r\nv 209.04 45.54 121.67\r\nv 210.11 47.68 122.62\r\nv 209.23 47.71 121.56\r\nv 208.44 47.73 120.39\r\nv 208.25 45.57 120.51\r\nv 208.1 45.56 119.99\r\nv 208.29 47.73 119.87\r\nv 208.6 47.7 119.52\r\nv 208.4 45.53 119.63\r\nv 210.49 49.71 118.14\r\nv 211.35 49.68 119.23\r\nv 210.67 51.87 118.02\r\nv 211.53 51.84 119.11\r\nv 212.35 51.82 120.25\r\nv 212.16 49.66 120.37\r\nv 212.38 49.66 120.81\r\nv 212.56 51.82 120.7\r\nv 212.32 51.84 120.98\r\nv 212.14 49.68 121.09\r\nv 210.3 49.84 122.51\r\nv 209.42 49.87 121.45\r\nv 210.48 52.01 122.39\r\nv 209.6 52.03 121.33\r\nv 208.81 52.05 120.17\r\nv 208.63 49.9 120.28\r\nv 208.48 49.89 119.76\r\nv 208.66 52.05 119.64\r\nv 208.97 52.02 119.29\r\nv 208.78 49.86 119.41\r\nv 210.86 54.01 117.9\r\nv 211.72 53.99 118.99\r\nv 211.04 56.15 117.77\r\nv 211.9 56.12 118.86\r\nv 212.72 56.11 120\r\nv 212.53 53.97 120.13\r\nv 212.75 53.97 120.58\r\nv 212.93 56.11 120.44\r\nv 212.69 56.13 120.72\r\nv 212.51 53.99 120.85\r\nv 210.67 54.16 122.27\r\nv 209.79 54.19 121.21\r\nv 210.86 56.31 122.14\r\nv 209.97 56.33 121.07\r\nv 209.18 56.35 119.91\r\nv 208.99 54.21 120.04\r\nv 208.84 54.2 119.52\r\nv 209.03 56.34 119.39\r\nv 209.34 56.31 119.04\r\nv 209.15 54.17 119.17\r\nv 211.24 58.27 117.62\r\nv 212.1 58.25 118.71\r\nv 211.44 60.37 117.46\r\nv 212.3 60.35 118.55\r\nv 213.11 60.34 119.69\r\nv 212.91 58.23 119.85\r\nv 213.13 58.23 120.3\r\nv 213.33 60.34 120.13\r\nv 213.09 60.37 120.41\r\nv 212.89 58.26 120.57\r\nv 211.05 58.45 121.99\r\nv 210.17 58.47 120.93\r\nv 211.25 60.58 121.82\r\nv 210.37 60.6 120.76\r\nv 209.58 60.61 119.6\r\nv 209.37 58.49 119.76\r\nv 209.23 58.48 119.24\r\nv 209.43 60.6 119.08\r\nv 209.73 60.56 118.72\r\nv 209.53 58.44 118.89\r\nv 211.65 62.47 117.27\r\nv 212.51 62.45 118.36\r\nv 211.88 64.54 117.06\r\nv 212.74 64.52 118.15\r\nv 213.56 64.52 119.29\r\nv 213.33 62.44 119.5\r\nv 213.54 62.44 119.94\r\nv 213.77 64.52 119.73\r\nv 213.53 64.56 120.01\r\nv 213.3 62.47 120.22\r\nv 211.47 62.7 121.64\r\nv 210.59 62.72 120.57\r\nv 211.7 64.8 121.42\r\nv 210.82 64.82 120.36\r\nv 210.02 64.83 119.19\r\nv 209.79 62.73 119.41\r\nv 209.64 62.71 118.89\r\nv 209.87 64.81 118.67\r\nv 210.18 64.76 118.32\r\nv 209.95 62.67 118.54\r\nv 212.12 66.59 116.82\r\nv 212.98 66.58 117.9\r\nv 212.39 68.63 116.54\r\nv 213.25 68.62 117.63\r\nv 214.06 68.62 118.77\r\nv 213.8 66.58 119.04\r\nv 214.02 66.59 119.49\r\nv 214.28 68.63 119.21\r\nv 214.04 68.68 119.49\r\nv 213.78 66.63 119.76\r\nv 211.95 66.9 121.17\r\nv 211.07 66.91 120.11\r\nv 212.22 68.97 120.89\r\nv 211.34 68.99 119.83\r\nv 210.54 68.98 118.67\r\nv 210.27 66.91 118.95\r\nv 210.12 66.89 118.43\r\nv 210.39 68.96 118.15\r\nv 210.69 68.9 117.8\r\nv 210.43 66.84 118.08\r\nv 212.67 70.65 116.23\r\nv 213.53 70.64 117.32\r\nv 212.96 72.64 115.88\r\nv 213.82 72.64 116.97\r\nv 214.64 72.65 118.1\r\nv 214.35 70.65 118.45\r\nv 214.56 70.66 118.9\r\nv 214.86 72.67 118.55\r\nv 214.62 72.73 118.82\r\nv 214.32 70.71 119.17\r\nv 212.5 71.03 120.57\r\nv 211.62 71.04 119.51\r\nv 212.81 73.08 120.22\r\nv 211.92 73.08 119.16\r\nv 211.13 73.07 118\r\nv 210.82 71.03 118.35\r\nv 210.67 71.01 117.83\r\nv 210.97 73.03 117.48\r\nv 211.28 72.96 117.13\r\nv 210.98 70.94 117.48\r\nv 213.27 74.61 115.49\r\nv 214.14 74.62 116.58\r\nv 213.6 76.56 115.06\r\nv 214.47 76.58 116.14\r\nv 215.29 76.6 117.27\r\nv 214.96 74.64 117.71\r\nv 215.17 74.66 118.15\r\nv 215.51 76.63 117.72\r\nv 215.27 76.69 117.99\r\nv 214.94 74.72 118.43\r\nv 213.13 75.1 119.82\r\nv 212.24 75.1 118.76\r\nv 213.46 77.11 119.37\r\nv 212.58 77.1 118.31\r\nv 211.78 77.07 117.16\r\nv 211.44 75.08 117.6\r\nv 211.29 75.04 117.08\r\nv 211.63 77.02 116.64\r\nv 211.93 76.94 116.3\r\nv 211.59 74.96 116.74\r\nv 213.95 78.49 114.58\r\nv 214.81 78.51 115.66\r\nv 214.3 80.39 114.05\r\nv 215.17 80.42 115.13\r\nv 216 80.46 116.26\r\nv 215.63 78.55 116.79\r\nv 215.85 78.57 117.23\r\nv 216.22 80.5 116.7\r\nv 215.98 80.57 116.96\r\nv 215.62 78.64 117.5\r\nv 213.82 79.09 118.88\r\nv 212.93 79.07 117.82\r\nv 214.19 81.05 118.33\r\nv 213.3 81.03 117.28\r\nv 212.5 80.97 116.12\r\nv 212.13 79.03 116.67\r\nv 211.98 78.98 116.15\r\nv 212.34 80.92 115.61\r\nv 212.64 80.82 115.27\r\nv 212.27 78.89 115.81\r\nv 214.68 82.26 113.47\r\nv 215.55 82.3 114.54\r\nv 215.06 84.1 112.84\r\nv 215.93 84.15 113.91\r\nv 216.76 84.22 115.03\r\nv 216.37 82.36 115.67\r\nv 216.59 82.4 116.11\r\nv 216.99 84.27 115.46\r\nv 216.75 84.35 115.73\r\nv 216.36 82.48 116.37\r\nv 214.58 82.99 117.73\r\nv 213.68 82.96 116.68\r\nv 214.97 84.9 117.07\r\nv 214.08 84.86 116.03\r\nv 213.27 84.78 114.88\r\nv 212.88 82.89 115.53\r\nv 212.72 82.83 115.02\r\nv 213.11 84.71 114.37\r\nv 213.41 84.6 114.04\r\nv 213.02 82.73 114.68\r\nv 215.46 85.91 112.16\r\nv 216.33 85.97 113.22\r\nv 215.86 87.68 111.43\r\nv 216.74 87.75 112.49\r\nv 217.57 87.85 113.6\r\nv 217.17 86.05 114.34\r\nv 217.39 86.1 114.77\r\nv 217.8 87.9 114.03\r\nv 217.57 88 114.29\r\nv 217.16 86.2 115.03\r\nv 215.39 86.78 116.37\r\nv 214.49 86.72 115.32\r\nv 215.8 88.61 115.61\r\nv 214.91 88.55 114.57\r\nv 214.09 88.44 113.44\r\nv 213.68 86.63 114.18\r\nv 213.52 86.56 113.68\r\nv 213.93 88.36 112.93\r\nv 214.22 88.24 112.6\r\nv 213.81 86.44 113.35\r\nv 216.27 89.4 110.65\r\nv 217.15 89.49 111.71\r\nv 216.68 91.07 109.84\r\nv 217.57 91.18 110.89\r\nv 218.41 91.3 111.98\r\nv 217.99 89.6 112.81\r\nv 218.22 89.66 113.24\r\nv 218.64 91.37 112.41\r\nv 218.41 91.48 112.66\r\nv 217.99 89.76 113.5\r\nv 216.23 90.41 114.8\r\nv 215.33 90.33 113.77\r\nv 216.66 92.16 113.95\r\nv 215.76 92.06 112.92\r\nv 214.93 91.93 111.8\r\nv 214.51 90.21 112.64\r\nv 214.35 90.12 112.14\r\nv 214.77 91.83 111.31\r\nv 215.06 91.69 110.98\r\nv 214.64 89.99 111.82\r\nv 217.09 92.69 108.98\r\nv 217.98 92.81 110.02\r\nv 217.5 94.25 108.08\r\nv 218.4 94.39 109.11\r\nv 219.25 94.54 110.19\r\nv 218.83 92.95 111.11\r\nv 219.06 93.02 111.53\r\nv 219.48 94.62 110.61\r\nv 219.26 94.75 110.85\r\nv 218.83 93.14 111.78\r\nv 217.09 93.85 113.05\r\nv 216.19 93.75 112.03\r\nv 217.53 95.5 112.1\r\nv 216.61 95.37 111.09\r\nv 215.78 95.2 109.99\r\nv 215.36 93.6 110.92\r\nv 215.19 93.49 110.43\r\nv 215.61 95.09 109.51\r\nv 215.9 94.93 109.19\r\nv 215.48 93.34 110.11\r\nv 217.91 95.74 107.14\r\nv 218.81 95.9 108.16\r\nv 218.3 97.16 106.17\r\nv 219.21 97.34 107.18\r\nv 220.07 97.54 108.23\r\nv 219.66 96.07 109.23\r\nv 219.9 96.16 109.64\r\nv 220.31 97.63 108.63\r\nv 220.09 97.77 108.87\r\nv 219.68 96.29 109.88\r\nv 217.95 97.08 111.11\r\nv 217.04 96.93 110.11\r\nv 218.38 98.59 110.07\r\nv 217.45 98.43 109.09\r\nv 216.61 98.21 108.02\r\nv 216.2 96.75 109.02\r\nv 216.03 96.62 108.54\r\nv 216.43 98.08 107.55\r\nv 216.71 97.9 107.25\r\nv 216.31 96.46 108.24\r\nv 218.68 98.49 105.18\r\nv 219.59 98.69 106.17\r\nv 219.02 99.71 104.17\r\nv 219.94 99.95 105.13\r\nv 220.83 100.23 106.11\r\nv 220.46 98.92 107.19\r\nv 220.7 99.03 107.59\r\nv 221.08 100.4 106.45\r\nv 220.86 100.51 106.72\r\nv 220.48 99.17 107.82\r\nv 218.78 100.02 109\r\nv 217.85 99.83 108.04\r\nv 219.16 101.37 107.89\r\nv 218.22 101.15 106.95\r\nv 217.36 100.87 105.94\r\nv 217 99.59 106.99\r\nv 216.82 99.44 106.53\r\nv 217.17 100.7 105.49\r\nv 217.45 100.51 105.2\r\nv 217.09 99.26 106.23\r\nv 219.33 100.82 103.16\r\nv 220.27 101.11 104.06\r\nv 219.57 101.8 102.13\r\nv 220.52 102.16 102.95\r\nv 221.44 102.57 103.8\r\nv 221.18 101.49 104.95\r\nv 221.47 101.82 105.15\r\nv 221.72 102.78 104.08\r\nv 221.68 103.16 103.81\r\nv 221.53 102.87 104.34\r\nv 221.29 102.6 104.91\r\nv 221.21 101.79 105.54\r\nv 219.51 102.63 106.73\r\nv 218.56 102.36 105.84\r\nv 219.79 103.8 105.49\r\nv 218.82 103.45 104.69\r\nv 217.93 103.04 103.81\r\nv 217.68 102.02 104.88\r\nv 217.49 101.83 104.45\r\nv 217.73 102.82 103.42\r\nv 218 102.62 103.14\r\nv 217.76 101.64 104.17\r\nv 219.71 102.64 101.09\r\nv 220.67 103.1 101.78\r\nv 219.71 103.36 99.98\r\nv 220.67 103.93 100.51\r\nv 221.6 104.53 101.09\r\nv 221.6 103.6 102.51\r\nv 219.95 104.87 104.16\r\nv 218.97 104.42 103.48\r\nv 219.95 105.81 102.7\r\nv 218.97 105.26 102.19\r\nv 218.07 104.64 101.6\r\nv 218.07 103.91 102.73\r\nv 217.87 103.66 102.38\r\nv 217.87 104.36 101.3\r\nv 218.14 104.15 101.03\r\nv 218.14 103.45 102.11\r\nv 219.55 103.96 98.79\r\nv 220.49 104.62 99.15\r\nv 219.26 104.48 97.48\r\nv 220.16 105.2 97.68\r\nv 221.03 105.96 97.91\r\nv 221.39 105.31 99.54\r\nv 219.74 106.61 101.13\r\nv 218.79 105.97 100.79\r\nv 219.38 107.28 99.46\r\nv 218.45 106.57 99.28\r\nv 217.6 105.8 99.02\r\nv 217.91 105.26 100.37\r\nv 217.71 104.95 100.14\r\nv 217.42 105.46 98.84\r\nv 217.69 105.24 98.59\r\nv 217.98 104.73 99.87\r\nv 218.85 104.94 96.03\r\nv 219.73 105.7 96.11\r\nv 218.38 105.36 94.44\r\nv 219.23 106.14 94.45\r\nv 220.05 106.96 94.5\r\nv 220.57 106.49 96.23\r\nv 220.8 106.8 96.32\r\nv 221.27 106.25 98.04\r\nv 220.27 107.27 94.56\r\nv 220.05 107.45 94.74\r\nv 220.58 106.97 96.51\r\nv 221.05 106.42 98.24\r\nv 218.9 107.83 97.73\r\nv 218 107.08 97.66\r\nv 218.36 108.31 95.93\r\nv 217.49 107.54 95.93\r\nv 216.69 106.71 95.87\r\nv 217.18 106.28 97.52\r\nv 217.01 105.92 97.38\r\nv 216.53 106.35 95.76\r\nv 216.81 106.13 95.53\r\nv 217.29 105.7 97.14\r\nv 217.86 105.75 92.73\r\nv 218.71 106.55 92.7\r\nv 217.33 106.12 90.92\r\nv 218.16 106.92 90.86\r\nv 218.96 107.76 90.83\r\nv 219.51 107.37 92.69\r\nv 219.72 107.69 92.73\r\nv 219.17 108.08 90.86\r\nv 218.95 108.26 91.02\r\nv 219.5 107.87 92.91\r\nv 217.8 108.75 94.06\r\nv 216.94 107.97 94.11\r\nv 217.23 109.14 92.12\r\nv 216.38 108.36 92.2\r\nv 215.6 107.51 92.22\r\nv 216.16 107.12 94.1\r\nv 216 106.76 94.01\r\nv 215.45 107.14 92.14\r\nv 215.74 106.91 91.93\r\nv 216.28 106.53 93.78\r\nv 216.78 106.47 89.03\r\nv 217.61 107.27 88.95\r\nv 216.24 106.79 87.08\r\nv 217.07 107.6 86.99\r\nv 217.86 108.44 86.93\r\nv 218.4 108.11 88.9\r\nv 218.61 108.43 88.92\r\nv 218.06 108.76 86.95\r\nv 217.84 108.94 87.1\r\nv 218.39 108.61 89.08\r\nv 216.66 109.51 90.13\r\nv 215.81 108.72 90.23\r\nv 216.09 109.84 88.1\r\nv 215.25 109.06 88.2\r\nv 214.48 108.2 88.25\r\nv 215.04 107.87 90.26\r\nv 214.89 107.5 90.2\r\nv 214.34 107.83 88.19\r\nv 214.62 107.6 88\r\nv 215.18 107.27 89.99\r\nv 215.72 107.1 85.1\r\nv 216.54 107.9 85\r\nv 215.21 107.38 83.08\r\nv 216.03 108.19 82.98\r\nv 216.82 109.03 82.91\r\nv 217.33 108.74 84.93\r\nv 217.53 109.07 84.95\r\nv 217.02 109.35 82.92\r\nv 216.79 109.54 83.05\r\nv 217.3 109.25 85.09\r\nv 215.55 110.16 86.04\r\nv 214.7 109.37 86.15\r\nv 215.02 110.45 83.96\r\nv 214.18 109.66 84.07\r\nv 213.41 108.81 84.13\r\nv 213.94 108.52 86.2\r\nv 213.79 108.14 86.15\r\nv 213.27 108.43 84.08\r\nv 213.57 108.2 83.91\r\nv 214.09 107.91 85.97\r\nv 214.72 107.65 81.04\r\nv 215.54 108.46 80.94\r\nv 214.25 107.9 78.98\r\nv 215.07 108.7 78.89\r\nv 215.86 109.54 78.81\r\nv 216.33 109.29 80.87\r\nv 216.53 109.62 80.87\r\nv 216.06 109.87 78.82\r\nv 215.83 110.05 78.94\r\nv 216.3 109.8 81\r\nv 214.52 110.73 81.86\r\nv 213.67 109.94 81.97\r\nv 214.04 110.98 79.75\r\nv 213.19 110.19 79.86\r\nv 212.43 109.33 79.92\r\nv 212.91 109.08 82.03\r\nv 212.77 108.71 81.99\r\nv 212.29 108.96 79.88\r\nv 212.59 108.72 79.73\r\nv 213.07 108.47 81.83\r\nv 213.81 108.12 76.92\r\nv 214.63 108.93 76.83\r\nv 213.39 108.33 74.86\r\nv 214.21 109.14 74.77\r\nv 214.99 109.98 74.69\r\nv 215.41 109.77 76.75\r\nv 215.62 110.09 76.75\r\nv 215.2 110.3 74.69\r\nv 214.96 110.49 74.8\r\nv 215.38 110.28 76.87\r\nv 213.58 111.21 77.63\r\nv 212.74 110.43 77.74\r\nv 213.15 111.43 75.52\r\nv 212.3 110.64 75.63\r\nv 211.54 109.78 75.69\r\nv 211.97 109.57 77.81\r\nv 211.83 109.19 77.77\r\nv 211.4 109.41 75.66\r\nv 211.71 109.17 75.52\r\nv 212.14 108.96 77.63\r\nv 212.99 108.53 72.81\r\nv 213.82 109.33 72.72\r\nv 212.63 108.7 70.77\r\nv 213.45 109.51 70.68\r\nv 214.24 110.35 70.61\r\nv 214.6 110.17 72.64\r\nv 214.8 110.5 72.64\r\nv 214.44 110.67 70.61\r\nv 214.2 110.86 70.7\r\nv 214.57 110.68 72.74\r\nv 212.75 111.63 73.42\r\nv 211.9 110.84 73.52\r\nv 212.38 111.81 71.33\r\nv 211.53 111.02 71.42\r\nv 210.77 110.16 71.49\r\nv 211.14 109.98 73.58\r\nv 211 109.61 73.55\r\nv 210.63 109.79 71.46\r\nv 210.93 109.55 71.34\r\nv 211.31 109.37 73.43\r\nv 212.29 108.86 68.75\r\nv 213.12 109.66 68.66\r\nv 211.99 109 66.75\r\nv 212.82 109.81 66.67\r\nv 213.6 110.65 66.61\r\nv 213.91 110.5 68.6\r\nv 214.11 110.83 68.59\r\nv 213.8 110.97 66.61\r\nv 213.56 111.16 66.69\r\nv 213.87 111.02 68.68\r\nv 212.03 111.97 69.26\r\nv 211.19 111.18 69.35\r\nv 211.72 112.11 67.23\r\nv 210.87 111.33 67.31\r\nv 210.11 110.47 67.37\r\nv 210.42 110.32 69.41\r\nv 210.29 109.95 69.39\r\nv 209.97 110.09 67.35\r\nv 210.28 109.85 67.25\r\nv 210.59 109.71 69.28\r\nv 211.71 109.13 64.79\r\nv 212.54 109.93 64.72\r\nv 211.46 109.24 62.87\r\nv 212.29 110.05 62.8\r\nv 213.08 110.89 62.75\r\nv 213.33 110.77 64.66\r\nv 213.53 111.1 64.66\r\nv 213.28 111.21 62.74\r\nv 213.03 111.4 62.81\r\nv 213.29 111.29 64.73\r\nv 211.44 112.24 65.23\r\nv 210.59 111.46 65.31\r\nv 211.19 112.36 63.27\r\nv 210.33 111.57 63.35\r\nv 209.57 110.72 63.4\r\nv 209.83 110.6 65.36\r\nv 209.69 110.22 65.35\r\nv 209.43 110.34 63.38\r\nv 209.75 110.1 63.3\r\nv 210 109.98 65.25\r\nv 211.24 109.35 61\r\nv 212.07 110.15 60.93\r\nv 211.04 109.43 59.18\r\nv 211.87 110.24 59.12\r\nv 212.65 111.08 59.07\r\nv 212.85 110.99 60.88\r\nv 213.06 111.31 60.88\r\nv 212.85 111.4 59.06\r\nv 212.61 111.59 59.12\r\nv 212.81 111.5 60.94\r\nv 210.96 112.46 61.36\r\nv 210.1 111.68 61.43\r\nv 210.75 112.55 59.51\r\nv 209.9 111.77 59.57\r\nv 209.14 110.91 59.62\r\nv 209.34 110.82 61.48\r\nv 209.2 110.44 61.47\r\nv 209 110.53 59.61\r\nv 209.31 110.29 59.53\r\nv 209.52 110.2 61.39\r\nv 210.86 109.51 57.42\r\nv 211.69 110.31 57.36\r\nv 210.7 109.58 55.72\r\nv 211.53 110.38 55.67\r\nv 212.32 111.22 55.62\r\nv 212.47 111.15 57.31\r\nv 212.68 111.48 57.31\r\nv 212.52 111.54 55.62\r\nv 212.27 111.73 55.66\r\nv 212.43 111.67 57.36\r\nv 210.57 112.63 57.71\r\nv 209.72 111.85 57.77\r\nv 210.41 112.7 55.98\r\nv 209.56 111.91 56.04\r\nv 208.79 111.06 56.08\r\nv 208.95 110.99 57.82\r\nv 208.82 110.61 57.81\r\nv 208.66 110.68 56.07\r\nv 208.97 110.44 56.01\r\nv 209.13 110.37 57.74\r\nv 210.56 109.63 54.1\r\nv 211.4 110.44 54.05\r\nv 210.45 109.68 52.53\r\nv 211.28 110.49 52.49\r\nv 212.07 111.32 52.45\r\nv 212.18 111.28 54.01\r\nv 212.39 111.6 54\r\nv 212.27 111.65 52.45\r\nv 212.02 111.84 52.48\r\nv 212.14 111.79 54.04\r\nv 210.27 112.76 54.33\r\nv 209.42 111.97 54.38\r\nv 210.15 112.81 52.74\r\nv 209.3 112.02 52.78\r\nv 208.53 111.17 52.82\r\nv 208.65 111.12 54.42\r\nv 208.52 110.74 54.41\r\nv 208.4 110.79 52.81\r\nv 208.71 110.55 52.76\r\nv 208.83 110.5 54.36\r\nv 210.35 109.72 51.02\r\nv 211.18 110.53 50.98\r\nv 210.27 109.75 49.55\r\nv 211.1 110.56 49.52\r\nv 211.89 111.39 49.49\r\nv 211.97 111.36 50.95\r\nv 212.17 111.69 50.95\r\nv 212.09 111.72 49.48\r\nv 211.84 111.91 49.51\r\nv 211.92 111.88 50.98\r\nv 210.05 112.85 51.19\r\nv 209.2 112.06 51.23\r\nv 209.97 112.88 49.68\r\nv 209.11 112.09 49.72\r\nv 208.35 111.24 49.75\r\nv 208.43 111.21 51.26\r\nv 208.3 110.83 51.26\r\nv 208.21 110.86 49.75\r\nv 208.53 110.62 49.71\r\nv 208.61 110.59 51.22\r\nv 210.2 109.78 48.11\r\nv 211.04 110.58 48.08\r\nv 210.15 109.79 46.68\r\nv 210.99 110.59 46.65\r\nv 211.77 111.43 46.63\r\nv 211.82 111.42 48.05\r\nv 212.03 111.74 48.05\r\nv 211.98 111.76 46.63\r\nv 211.73 111.95 46.64\r\nv 211.78 111.93 48.07\r\nv 209.9 112.9 48.2\r\nv 209.05 112.12 48.24\r\nv 209.85 112.92 46.74\r\nv 209 112.13 46.77\r\nv 208.23 111.28 46.79\r\nv 208.28 111.26 48.26\r\nv 208.15 110.89 48.26\r\nv 208.1 110.9 46.79\r\nv 208.41 110.66 46.77\r\nv 208.46 110.64 48.23\r\nv 210.12 109.8 45.26\r\nv 210.95 110.6 45.24\r\nv 210.1 109.8 43.82\r\nv 210.93 110.6 43.81\r\nv 211.72 111.44 43.79\r\nv 211.74 111.44 45.22\r\nv 211.94 111.76 45.21\r\nv 211.92 111.76 43.79\r\nv 211.67 111.96 43.79\r\nv 211.7 111.95 45.22\r\nv 209.82 112.92 45.28\r\nv 208.96 112.14 45.3\r\nv 209.8 112.93 43.82\r\nv 208.94 112.14 43.83\r\nv 208.18 111.29 43.85\r\nv 208.2 111.29 45.32\r\nv 208.06 110.91 45.32\r\nv 208.04 110.91 43.85\r\nv 208.36 110.66 43.85\r\nv 208.38 110.66 45.31\r\nv 210.09 109.79 42.38\r\nv 210.92 110.59 42.36\r\nv 210.1 109.78 40.91\r\nv 210.93 110.58 40.9\r\nv 211.72 111.42 40.89\r\nv 211.71 111.43 42.35\r\nv 211.92 111.76 42.35\r\nv 211.92 111.74 40.89\r\nv 211.67 111.94 40.88\r\nv 211.67 111.95 42.35\r\nv 209.79 112.92 42.34\r\nv 208.93 112.13 42.35\r\nv 209.79 112.91 40.84\r\nv 208.94 112.12 40.84\r\nv 208.17 111.27 40.85\r\nv 208.17 111.28 42.36\r\nv 208.03 110.9 42.36\r\nv 208.04 110.89 40.86\r\nv 208.35 110.64 40.87\r\nv 208.35 110.66 42.37\r\nv 210.12 109.76 39.42\r\nv 210.95 110.56 39.42\r\nv 210.15 109.73 37.92\r\nv 210.98 110.53 37.92\r\nv 211.77 111.37 37.92\r\nv 211.74 111.4 39.41\r\nv 211.94 111.72 39.41\r\nv 211.97 111.7 37.91\r\nv 211.72 111.89 37.89\r\nv 211.69 111.92 39.4\r\nv 209.81 112.88 39.32\r\nv 208.96 112.1 39.32\r\nv 209.85 112.86 37.78\r\nv 208.99 112.07 37.78\r\nv 208.23 111.22 37.78\r\nv 208.19 111.24 39.32\r\nv 208.06 110.87 39.33\r\nv 208.09 110.84 37.79\r\nv 208.41 110.59 37.81\r\nv 208.37 110.62 39.35\r\nv 210.2 109.7 36.39\r\nv 211.03 110.5 36.4\r\nv 210.26 109.65 34.85\r\nv 211.09 110.46 34.86\r\nv 211.88 111.29 34.86\r\nv 211.82 111.34 36.4\r\nv 212.02 111.66 36.39\r\nv 212.08 111.62 34.86\r\nv 211.83 111.81 34.83\r\nv 211.77 111.85 36.37\r\nv 209.9 112.82 36.22\r\nv 209.04 112.03 36.21\r\nv 209.96 112.78 34.65\r\nv 209.1 111.99 34.63\r\nv 208.34 111.14 34.63\r\nv 208.28 111.18 36.21\r\nv 208.14 110.8 36.23\r\nv 208.2 110.76 34.64\r\nv 208.52 110.52 34.68\r\nv 208.46 110.56 36.26\r\nv 210.34 109.6 33.28\r\nv 211.17 110.41 33.3\r\nv 210.43 109.55 31.7\r\nv 211.26 110.35 31.72\r\nv 212.05 111.19 31.73\r\nv 211.96 111.24 33.31\r\nv 212.16 111.57 33.3\r\nv 212.25 111.51 31.73\r\nv 212 111.7 31.69\r\nv 211.91 111.76 33.27\r\nv 210.04 112.73 33.05\r\nv 209.18 111.94 33.03\r\nv 210.13 112.67 31.44\r\nv 209.28 111.88 31.41\r\nv 208.51 111.03 31.4\r\nv 208.42 111.09 33.03\r\nv 208.28 110.71 33.04\r\nv 208.38 110.65 31.42\r\nv 208.69 110.41 31.47\r\nv 208.6 110.46 33.08\r\nv 210.54 109.48 30.1\r\nv 211.37 110.28 30.12\r\nv 210.67 109.4 28.48\r\nv 211.5 110.21 28.51\r\nv 212.28 111.05 28.53\r\nv 212.16 111.12 30.14\r\nv 212.36 111.45 30.13\r\nv 212.49 111.37 28.52\r\nv 212.24 111.56 28.47\r\nv 212.11 111.64 30.09\r\nv 210.24 112.6 29.8\r\nv 209.39 111.81 29.77\r\nv 210.37 112.52 28.14\r\nv 209.52 111.74 28.11\r\nv 208.76 110.88 28.09\r\nv 208.63 110.96 29.76\r\nv 208.49 110.58 29.78\r\nv 208.62 110.5 28.11\r\nv 208.93 110.26 28.17\r\nv 208.8 110.34 29.83\r\nv 210.81 109.32 26.83\r\nv 211.64 110.12 26.87\r\nv 210.98 109.22 25.17\r\nv 211.81 110.03 25.2\r\nv 212.59 110.87 25.23\r\nv 212.43 110.96 26.89\r\nv 212.63 111.29 26.88\r\nv 212.8 111.19 25.23\r\nv 212.55 111.38 25.16\r\nv 212.38 111.48 26.83\r\nv 210.52 112.44 26.46\r\nv 209.67 111.65 26.42\r\nv 210.69 112.34 24.75\r\nv 209.84 111.55 24.71\r\nv 209.08 110.7 24.69\r\nv 208.9 110.79 26.4\r\nv 208.77 110.42 26.42\r\nv 208.94 110.32 24.71\r\nv 209.25 110.08 24.79\r\nv 209.08 110.18 26.49\r\nv 211.16 109.12 23.47\r\nv 211.99 109.92 23.52\r\nv 211.38 109 21.76\r\nv 212.21 109.8 21.8\r\nv 212.99 110.64 21.84\r\nv 212.78 110.76 23.55\r\nv 212.98 111.08 23.54\r\nv 213.19 110.96 21.83\r\nv 212.95 111.15 21.76\r\nv 212.74 111.27 23.47\r\nv 210.89 112.23 23.02\r\nv 210.03 111.44 22.97\r\nv 211.1 112.1 21.25\r\nv 210.25 111.32 21.2\r\nv 209.49 110.46 21.17\r\nv 209.27 110.58 22.94\r\nv 209.13 110.21 22.97\r\nv 209.35 110.08 21.2\r\nv 209.66 109.84 21.29\r\nv 209.44 109.97 23.05\r\nv 211.62 108.86 20.01\r\nv 212.44 109.66 20.06\r\nv 211.88 108.71 18.24\r\nv 212.71 109.51 18.3\r\nv 213.49 110.35 18.34\r\nv 213.23 110.5 20.1\r\nv 213.43 110.83 20.1\r\nv 213.7 110.68 18.33\r\nv 213.45 110.87 18.24\r\nv 213.19 111.02 20.01\r\nv 211.35 111.96 19.46\r\nv 210.5 111.18 19.4\r\nv 211.62 111.81 17.64\r\nv 210.77 111.02 17.57\r\nv 210.01 110.16 17.54\r\nv 209.73 110.32 19.37\r\nv 209.6 109.94 19.4\r\nv 209.87 109.79 17.57\r\nv 210.18 109.55 17.69\r\nv 209.91 109.7 19.51\r\nv 212.17 108.54 16.44\r\nv 213 109.35 16.5\r\nv 212.5 108.36 14.61\r\nv 213.32 109.16 14.67\r\nv 214.11 110 14.72\r\nv 213.78 110.19 16.54\r\nv 213.99 110.51 16.53\r\nv 214.31 110.33 14.71\r\nv 214.07 110.51 14.6\r\nv 213.75 110.7 16.43\r\nv 211.92 111.64 15.79\r\nv 211.07 110.85 15.72\r\nv 212.25 111.45 13.9\r\nv 211.41 110.66 13.83\r\nv 210.64 109.8 13.79\r\nv 210.31 109.99 15.68\r\nv 210.17 109.62 15.72\r\nv 210.51 109.43 13.83\r\nv 210.81 109.19 13.96\r\nv 210.48 109.38 15.84\r\nv 212.85 108.15 12.75\r\nv 213.68 108.96 12.82\r\nv 213.23 107.93 10.87\r\nv 214.06 108.74 10.94\r\nv 214.84 109.58 10.99\r\nv 214.46 109.8 12.86\r\nv 214.66 110.12 12.85\r\nv 215.05 109.9 10.97\r\nv 214.81 110.08 10.85\r\nv 214.43 110.31 12.74\r\nv 212.62 111.24 11.99\r\nv 211.77 110.45 11.91\r\nv 213.01 111.01 10.05\r\nv 212.17 110.22 9.97\r\nv 211.4 109.36 9.94\r\nv 211.01 109.59 11.88\r\nv 210.87 109.22 11.92\r\nv 211.26 108.98 9.98\r\nv 211.56 108.75 10.14\r\nv 211.17 108.98 12.06\r\nv 213.65 107.68 8.97\r\nv 214.47 108.49 9.04\r\nv 214.09 107.41 7.06\r\nv 214.91 108.22 7.13\r\nv 215.7 109.06 7.17\r\nv 215.25 109.33 9.09\r\nv 215.46 109.65 9.07\r\nv 215.9 109.38 7.15\r\nv 215.67 109.57 7.01\r\nv 215.22 109.84 8.94\r\nv 213.44 110.75 8.09\r\nv 212.59 109.96 8.01\r\nv 213.9 110.47 6.11\r\nv 213.05 109.68 6.03\r\nv 212.29 108.83 6\r\nv 211.83 109.1 7.98\r\nv 211.69 108.73 8.03\r\nv 212.15 108.45 6.05\r\nv 212.44 108.22 6.23\r\nv 211.99 108.5 8.19\r\nv 214.56 107.12 5.12\r\nv 215.39 107.92 5.19\r\nv 215.07 106.8 3.18\r\nv 215.9 107.6 3.24\r\nv 216.68 108.44 3.28\r\nv 216.17 108.76 5.23\r\nv 216.38 109.09 5.21\r\nv 216.89 108.76 3.25\r\nv 216.66 108.94 3.1\r\nv 216.15 109.27 5.07\r\nv 214.39 110.17 4.11\r\nv 213.54 109.38 4.03\r\nv 214.91 109.83 2.09\r\nv 214.07 109.04 2.01\r\nv 213.3 108.19 1.99\r\nv 212.78 108.52 4\r\nv 212.63 108.15 4.06\r\nv 213.16 107.82 2.06\r\nv 213.45 107.59 2.25\r\nv 212.93 107.92 4.25\r\nv 215.61 106.45 1.22\r\nv 216.44 107.25 1.28\r\nv 216.16 106.07 -0.71\r\nv 216.99 106.87 -0.67\r\nv 217.79 107.71 -0.66\r\nv 217.22 108.09 1.31\r\nv 217.43 108.41 1.28\r\nv 218 108.03 -0.7\r\nv 217.77 108.2 -0.87\r\nv 217.21 108.59 1.12\r\nv 215.47 109.47 0.06\r\nv 214.62 108.68 -0.01\r\nv 216.05 109.08 -1.97\r\nv 215.2 108.29 -2.03\r\nv 214.43 107.44 -2.03\r\nv 213.86 107.83 -0.03\r\nv 213.71 107.46 0.05\r\nv 214.28 107.07 -1.94\r\nv 214.56 106.85 -1.73\r\nv 214 107.23 0.25\r\nv 216.72 105.67 -2.6\r\nv 217.56 106.46 -2.58\r\nv 217.33 105.19 -4.58\r\nv 218.18 105.93 -4.62\r\nv 219 106.69 -4.69\r\nv 218.36 107.29 -2.6\r\nv 218.57 107.61 -2.64\r\nv 219.22 106.99 -4.76\r\nv 219.01 107.17 -4.94\r\nv 218.35 107.79 -2.82\r\nv 216.64 108.65 -3.97\r\nv 215.78 107.87 -4.01\r\nv 217.36 108.09 -6.1\r\nv 216.46 107.36 -6.07\r\nv 215.64 106.57 -5.97\r\nv 215 107.02 -3.98\r\nv 214.85 106.66 -3.88\r\nv 215.48 106.22 -5.85\r\nv 215.76 105.99 -5.62\r\nv 215.13 106.43 -3.66\r\nv 218.06 104.6 -6.78\r\nv 218.94 105.16 -6.93\r\nv 218.77 103.79 -9.27\r\nv 219.79 104.43 -9.31\r\nv 221.11 104.96 -9.79\r\nv 219.79 105.77 -7.12\r\nv 220.02 106.01 -7.23\r\nv 221.29 105.37 -9.4\r\nv 221.13 105.59 -9.58\r\nv 219.84 106.22 -7.41\r\nv 218.33 107.31 -8.5\r\nv 217.35 106.73 -8.33\r\nv 219.88 106.72 -11.13\r\nv 218.31 106.13 -10.63\r\nv 217.16 105.36 -10.53\r\nv 216.43 106.07 -8.1\r\nv 216.23 105.76 -7.94\r\nv 216.8 105.08 -10.01\r\nv 217.07 104.83 -9.81\r\nv 216.5 105.52 -7.72\r\nv 201.02 -0.29 130.55\r\nv 200.79 -0.19 130.81\r\nv 200.05 0.15 129.57\r\nv 200.27 0.05 129.3\r\nv 260.86 114.42 -20.88\r\nv 258.53 114.28 -20.79\r\nv 258.52 114.84 -19.48\r\nv 260.85 114.98 -19.57\r\nv 256.2 114.09 -20.66\r\nv 256.19 114.65 -19.35\r\nv 256.19 115.11 -17.97\r\nv 258.52 115.3 -18.1\r\nv 260.85 115.44 -18.19\r\nv 258.54 115.33 -17.55\r\nv 260.86 115.47 -17.64\r\nv 256.21 115.14 -17.42\r\nv 256.26 114.82 -17.18\r\nv 258.57 115 -17.31\r\nv 260.89 115.14 -17.4\r\nv 261 113.44 -16.72\r\nv 258.75 113.31 -16.63\r\nv 258.75 112.78 -17.96\r\nv 261.01 112.91 -18.05\r\nv 256.48 113.13 -16.51\r\nv 256.49 112.59 -17.83\r\nv 256.49 112.11 -19.2\r\nv 258.75 112.29 -19.32\r\nv 261.01 112.43 -19.41\r\nv 258.75 112.19 -19.82\r\nv 261 112.33 -19.91\r\nv 256.49 112.01 -19.69\r\nv 256.45 112.26 -19.88\r\nv 258.72 112.45 -20.01\r\nv 260.99 112.58 -20.1\r\nv 253.87 113.85 -20.5\r\nv 253.85 114.41 -19.19\r\nv 251.53 113.56 -20.31\r\nv 251.52 114.12 -19\r\nv 251.52 114.58 -17.62\r\nv 253.86 114.87 -17.81\r\nv 253.88 114.9 -17.26\r\nv 251.55 114.61 -17.07\r\nv 251.62 114.29 -16.83\r\nv 253.94 114.58 -17.02\r\nv 254.22 112.89 -16.35\r\nv 254.23 112.36 -17.68\r\nv 251.94 112.61 -16.16\r\nv 251.96 112.08 -17.49\r\nv 251.96 111.59 -18.85\r\nv 254.23 111.87 -19.04\r\nv 254.22 111.77 -19.54\r\nv 251.95 111.49 -19.35\r\nv 251.89 111.75 -19.54\r\nv 254.17 112.03 -19.73\r\nv 249.19 113.22 -20.08\r\nv 249.17 113.78 -18.77\r\nv 246.85 112.84 -19.82\r\nv 246.83 113.39 -18.51\r\nv 246.83 113.85 -17.13\r\nv 249.18 114.24 -17.39\r\nv 249.21 114.27 -16.84\r\nv 246.87 113.89 -16.58\r\nv 246.96 113.57 -16.34\r\nv 249.29 113.95 -16.6\r\nv 249.66 112.28 -15.94\r\nv 249.68 111.75 -17.27\r\nv 247.37 111.9 -15.69\r\nv 247.39 111.37 -17.01\r\nv 247.39 110.89 -18.37\r\nv 249.68 111.26 -18.63\r\nv 249.67 111.16 -19.13\r\nv 247.38 110.78 -18.87\r\nv 247.31 111.04 -19.06\r\nv 249.6 111.41 -19.31\r\nv 244.5 112.41 -19.53\r\nv 244.48 112.96 -18.22\r\nv 242.15 111.94 -19.21\r\nv 242.13 112.49 -17.9\r\nv 242.13 112.95 -16.52\r\nv 244.48 113.42 -16.84\r\nv 244.52 113.46 -16.29\r\nv 242.18 112.99 -15.97\r\nv 242.28 112.67 -15.73\r\nv 244.62 113.14 -16.05\r\nv 245.08 111.48 -15.4\r\nv 245.09 110.95 -16.73\r\nv 242.78 111.02 -15.09\r\nv 242.8 110.49 -16.42\r\nv 242.8 110 -17.78\r\nv 245.1 110.47 -18.09\r\nv 245.08 110.36 -18.59\r\nv 242.78 109.9 -18.27\r\nv 242.7 110.15 -18.46\r\nv 245 110.61 -18.77\r\nv 239.82 111.43 -18.86\r\nv 239.8 111.98 -17.55\r\nv 237.52 110.89 -18.48\r\nv 237.49 111.44 -17.17\r\nv 237.5 111.9 -15.8\r\nv 239.8 112.44 -16.17\r\nv 239.85 112.48 -15.62\r\nv 237.55 111.94 -15.25\r\nv 237.67 111.63 -15.02\r\nv 239.97 112.16 -15.39\r\nv 240.5 110.52 -14.75\r\nv 240.52 109.99 -16.07\r\nv 238.25 110 -14.38\r\nv 238.26 109.47 -15.71\r\nv 238.26 108.98 -17.07\r\nv 240.52 109.51 -17.43\r\nv 240.5 109.4 -17.93\r\nv 238.24 108.88 -17.57\r\nv 238.14 109.12 -17.75\r\nv 240.41 109.65 -18.12\r\nv 235.25 110.32 -18.09\r\nv 235.22 110.87 -16.77\r\nv 233.02 109.73 -17.67\r\nv 233 110.28 -16.35\r\nv 233.01 110.75 -14.98\r\nv 235.23 111.34 -15.4\r\nv 235.29 111.38 -14.85\r\nv 233.07 110.79 -14.43\r\nv 233.21 110.48 -14.2\r\nv 235.42 111.06 -14.62\r\nv 236.03 109.44 -13.99\r\nv 236.04 108.91 -15.32\r\nv 233.85 108.86 -13.58\r\nv 233.87 108.33 -14.91\r\nv 233.86 107.84 -16.27\r\nv 236.04 108.42 -16.68\r\nv 236.02 108.32 -17.18\r\nv 233.84 107.74 -16.76\r\nv 233.73 107.98 -16.94\r\nv 235.91 108.56 -17.36\r\nv 230.86 109.12 -17.23\r\nv 230.83 109.67 -15.92\r\nv 228.75 108.5 -16.77\r\nv 228.73 109.05 -15.46\r\nv 228.75 109.52 -14.08\r\nv 230.85 110.14 -14.54\r\nv 230.91 110.18 -14\r\nv 228.82 109.56 -13.54\r\nv 228.97 109.25 -13.31\r\nv 231.06 109.87 -13.77\r\nv 231.74 108.27 -13.15\r\nv 231.75 107.74 -14.48\r\nv 229.69 107.66 -12.71\r\nv 229.7 107.13 -14.03\r\nv 229.69 106.64 -15.39\r\nv 231.74 107.25 -15.84\r\nv 231.71 107.14 -16.33\r\nv 229.65 106.53 -15.88\r\nv 229.53 106.77 -16.06\r\nv 231.6 107.39 -16.51\r\nv 226.73 107.86 -16.29\r\nv 226.71 108.42 -14.98\r\nv 224.77 107.21 -15.8\r\nv 224.69 107.74 -14.46\r\nv 224.65 108.19 -13.07\r\nv 226.74 108.89 -13.61\r\nv 226.81 108.93 -13.07\r\nv 224.71 108.22 -12.52\r\nv 224.88 107.92 -12.3\r\nv 226.97 108.63 -12.84\r\nv 227.72 107.04 -12.24\r\nv 227.72 106.51 -13.57\r\nv 225.69 106.36 -11.71\r\nv 225.76 105.85 -13.06\r\nv 225.8 105.39 -14.44\r\nv 227.71 106.02 -14.93\r\nv 227.67 105.91 -15.42\r\nv 225.79 105.29 -14.94\r\nv 225.65 105.53 -15.12\r\nv 227.55 106.15 -15.6\r\nv 222.87 106.55 -15.27\r\nv 222.59 107 -13.87\r\nv 221.21 105.93 -14.77\r\nv 220.69 106.28 -13.28\r\nv 222.36 107.36 -12.4\r\nv 222.35 107.37 -11.83\r\nv 220.21 106.92 -10.61\r\nv 220.41 106.63 -10.39\r\nv 222.53 107.07 -11.61\r\nv 223.45 105.56 -11.06\r\nv 223.72 105.13 -12.48\r\nv 221.92 104.46 -11.93\r\nv 222.41 104.17 -13.46\r\nv 223.96 104.74 -13.93\r\nv 224.01 104.67 -14.45\r\nv 222.53 104.13 -14.01\r\nv 222.38 104.36 -14.18\r\nv 223.87 104.9 -14.63\r\nv 219.97 105.44 -14.36\r\nv 219.25 105.72 -12.81\r\nv 219.16 104.94 -14.34\r\nv 218.31 105.09 -12.83\r\nv 220.68 103.98 -11.51\r\nv 219.93 103.47 -11.54\r\nv 220.73 103.37 -13.08\r\nv 221.35 103.76 -13.11\r\nv 221.53 103.74 -13.68\r\nv 220.95 103.39 -13.64\r\nv 220.75 103.59 -13.8\r\nv 221.35 103.96 -13.84\r\nv 218.83 104.3 -15\r\nv 217.91 104.22 -13.77\r\nv 218.66 103.45 -16.05\r\nv 217.72 103.19 -15.09\r\nv 216.86 102.86 -14.06\r\nv 217.06 104.09 -12.47\r\nv 216.88 103.95 -11.95\r\nv 216.68 102.68 -13.61\r\nv 216.95 102.46 -13.36\r\nv 217.15 103.72 -11.72\r\nv 218.71 102.67 -11.08\r\nv 219.62 102.75 -12.33\r\nv 218.54 101.57 -12.5\r\nv 219.46 101.85 -13.48\r\nv 220.34 102.16 -14.5\r\nv 220.48 102.87 -13.61\r\nv 220.72 102.96 -14.08\r\nv 220.58 102.31 -14.88\r\nv 220.36 102.47 -15.09\r\nv 220.51 103.14 -14.26\r\nv 218.35 102.36 -17.21\r\nv 217.42 102.07 -16.29\r\nv 217.96 101.09 -18.39\r\nv 217.04 100.86 -17.42\r\nv 216.21 100.58 -16.37\r\nv 216.57 101.73 -15.3\r\nv 216.39 101.54 -14.87\r\nv 216.04 100.41 -15.92\r\nv 216.32 100.22 -15.64\r\nv 216.67 101.34 -14.6\r\nv 218.26 100.51 -13.67\r\nv 219.18 100.81 -14.6\r\nv 217.92 99.4 -14.69\r\nv 218.81 99.65 -15.68\r\nv 219.67 99.91 -16.71\r\nv 220.05 101.14 -15.57\r\nv 220.29 101.28 -15.95\r\nv 219.9 100.04 -17.1\r\nv 219.68 100.19 -17.32\r\nv 220.07 101.44 -16.16\r\nv 217.53 99.74 -19.51\r\nv 216.63 99.55 -18.51\r\nv 217.07 98.3 -20.58\r\nv 216.18 98.15 -19.56\r\nv 215.37 97.94 -18.45\r\nv 215.8 99.31 -17.42\r\nv 215.64 99.16 -16.96\r\nv 215.2 97.8 -17.97\r\nv 215.49 97.62 -17.69\r\nv 215.92 98.97 -16.67\r\nv 217.53 98.18 -15.71\r\nv 218.41 98.38 -16.73\r\nv 217.1 96.86 -16.7\r\nv 217.98 97.03 -17.74\r\nv 218.81 97.23 -18.83\r\nv 219.25 98.61 -17.79\r\nv 219.48 98.72 -18.2\r\nv 219.03 97.33 -19.25\r\nv 218.81 97.46 -19.47\r\nv 219.26 98.87 -18.42\r\nv 216.59 96.8 -21.61\r\nv 215.71 96.66 -20.57\r\nv 216.11 95.22 -22.59\r\nv 215.23 95.1 -21.54\r\nv 214.43 94.94 -20.4\r\nv 214.9 96.48 -19.45\r\nv 214.75 96.35 -18.96\r\nv 214.27 94.83 -19.91\r\nv 214.57 94.67 -19.61\r\nv 215.03 96.18 -18.67\r\nv 216.66 95.45 -17.66\r\nv 217.52 95.6 -18.72\r\nv 216.2 93.96 -18.58\r\nv 217.06 94.09 -19.66\r\nv 217.88 94.24 -20.77\r\nv 218.35 95.77 -19.82\r\nv 218.57 95.86 -20.25\r\nv 218.09 94.32 -21.2\r\nv 217.87 94.45 -21.44\r\nv 218.34 95.99 -20.48\r\nv 215.62 93.59 -23.53\r\nv 214.74 93.49 -22.46\r\nv 215.13 91.9 -24.41\r\nv 214.26 91.81 -23.34\r\nv 213.47 91.69 -22.18\r\nv 213.95 93.34 -21.31\r\nv 213.8 93.24 -20.82\r\nv 213.32 91.59 -21.68\r\nv 213.61 91.44 -21.37\r\nv 214.09 93.08 -20.51\r\nv 215.73 92.41 -19.47\r\nv 216.59 92.52 -20.55\r\nv 215.27 90.8 -20.31\r\nv 216.12 90.89 -21.41\r\nv 216.92 91.01 -22.54\r\nv 217.4 92.65 -21.68\r\nv 217.62 92.72 -22.11\r\nv 217.14 91.08 -22.98\r\nv 216.91 91.19 -23.22\r\nv 217.39 92.85 -22.35\r\nv 214.64 90.16 -25.26\r\nv 213.77 90.09 -24.18\r\nv 214.16 88.38 -26.05\r\nv 213.3 88.32 -24.97\r\nv 212.52 88.22 -23.8\r\nv 212.99 89.98 -23.01\r\nv 212.84 89.89 -22.51\r\nv 212.37 88.14 -23.29\r\nv 212.67 88 -22.97\r\nv 213.14 89.75 -22.2\r\nv 214.8 89.13 -21.12\r\nv 215.65 89.22 -22.22\r\nv 214.34 87.42 -21.88\r\nv 215.18 87.49 -22.99\r\nv 215.98 87.58 -24.14\r\nv 216.45 89.32 -23.36\r\nv 216.66 89.38 -23.8\r\nv 216.19 87.63 -24.58\r\nv 215.96 87.74 -24.83\r\nv 216.43 89.49 -24.05\r\nv 213.69 86.55 -26.8\r\nv 212.83 86.5 -25.72\r\nv 213.23 84.69 -27.51\r\nv 212.37 84.65 -26.42\r\nv 211.6 84.57 -25.24\r\nv 212.05 86.41 -24.54\r\nv 211.91 86.34 -24.03\r\nv 211.46 84.5 -24.73\r\nv 211.76 84.39 -24.41\r\nv 212.21 86.21 -23.71\r\nv 213.88 85.66 -22.61\r\nv 214.73 85.72 -23.72\r\nv 213.44 83.86 -23.29\r\nv 214.28 83.91 -24.41\r\nv 215.08 83.98 -25.56\r\nv 215.52 85.8 -24.87\r\nv 215.73 85.85 -25.32\r\nv 215.28 84.03 -26.01\r\nv 215.05 84.12 -26.26\r\nv 215.5 85.95 -25.57\r\nv 212.79 82.8 -28.17\r\nv 211.93 82.76 -27.08\r\nv 212.37 80.88 -28.79\r\nv 211.51 80.85 -27.7\r\nv 210.74 80.79 -26.51\r\nv 211.16 82.7 -25.9\r\nv 211.02 82.63 -25.38\r\nv 210.6 80.73 -26\r\nv 210.91 80.63 -25.67\r\nv 211.32 82.52 -25.06\r\nv 213.01 82.03 -23.94\r\nv 213.85 82.07 -25.05\r\nv 212.6 80.16 -24.54\r\nv 213.44 80.2 -25.65\r\nv 214.23 80.26 -26.82\r\nv 214.64 82.13 -26.21\r\nv 214.85 82.17 -26.66\r\nv 214.44 80.29 -27.26\r\nv 214.2 80.37 -27.52\r\nv 214.61 82.26 -26.91\r\nv 211.97 78.94 -29.37\r\nv 211.11 78.91 -28.27\r\nv 211.59 76.98 -29.9\r\nv 210.74 76.95 -28.81\r\nv 209.97 76.9 -27.62\r\nv 210.34 78.86 -27.09\r\nv 210.2 78.8 -26.57\r\nv 209.83 76.85 -27.1\r\nv 210.14 76.76 -26.77\r\nv 210.51 78.71 -26.24\r\nv 212.21 78.27 -25.1\r\nv 213.05 78.31 -26.22\r\nv 211.85 76.36 -25.62\r\nv 212.68 76.39 -26.74\r\nv 213.47 76.43 -27.9\r\nv 213.84 78.36 -27.38\r\nv 214.05 78.39 -27.83\r\nv 213.68 76.46 -28.35\r\nv 213.43 76.53 -28.61\r\nv 213.8 78.47 -28.09\r\nv 211.24 74.99 -30.4\r\nv 210.38 74.97 -29.31\r\nv 210.91 72.99 -30.87\r\nv 210.05 72.97 -29.77\r\nv 209.29 72.93 -28.58\r\nv 209.62 74.93 -28.12\r\nv 209.48 74.88 -27.6\r\nv 209.15 72.88 -28.06\r\nv 209.46 72.81 -27.73\r\nv 209.79 74.8 -27.27\r\nv 211.5 74.42 -26.11\r\nv 212.33 74.45 -27.23\r\nv 211.18 72.46 -26.57\r\nv 212.01 72.49 -27.69\r\nv 212.8 72.52 -28.85\r\nv 213.12 74.49 -28.39\r\nv 213.33 74.52 -28.84\r\nv 213 72.55 -29.3\r\nv 212.76 72.61 -29.56\r\nv 213.08 74.58 -29.1\r\nv 210.6 70.97 -31.31\r\nv 209.75 70.95 -30.21\r\nv 210.32 68.93 -31.72\r\nv 209.46 68.91 -30.63\r\nv 208.7 68.87 -29.43\r\nv 208.98 70.91 -29.02\r\nv 208.84 70.87 -28.5\r\nv 208.56 68.83 -28.91\r\nv 208.87 68.77 -28.58\r\nv 209.15 70.8 -28.17\r\nv 210.88 70.47 -27\r\nv 211.71 70.5 -28.12\r\nv 210.6 68.47 -27.4\r\nv 211.43 68.5 -28.52\r\nv 212.22 68.53 -29.69\r\nv 212.5 70.54 -29.28\r\nv 212.7 70.56 -29.73\r\nv 212.42 68.56 -30.14\r\nv 212.18 68.61 -30.4\r\nv 212.46 70.62 -30\r\nv 210.06 66.88 -32.11\r\nv 209.21 66.86 -31.02\r\nv 209.83 64.81 -32.48\r\nv 208.97 64.79 -31.39\r\nv 208.21 64.74 -30.2\r\nv 208.44 66.81 -29.83\r\nv 208.3 66.78 -29.3\r\nv 208.07 64.7 -29.68\r\nv 208.38 64.65 -29.34\r\nv 208.62 66.72 -28.97\r\nv 210.34 66.44 -27.79\r\nv 211.18 66.47 -28.91\r\nv 210.11 64.4 -28.16\r\nv 210.95 64.43 -29.28\r\nv 211.74 64.47 -30.44\r\nv 211.97 66.51 -30.07\r\nv 212.17 66.53 -30.53\r\nv 211.94 64.49 -30.89\r\nv 211.69 64.54 -31.16\r\nv 211.92 66.58 -30.79\r\nv 209.62 62.73 -32.84\r\nv 208.77 62.7 -31.75\r\nv 209.44 60.64 -33.19\r\nv 208.58 60.6 -32.1\r\nv 207.81 60.55 -30.91\r\nv 208 62.65 -30.56\r\nv 207.86 62.62 -30.04\r\nv 207.68 60.51 -30.39\r\nv 207.99 60.46 -30.05\r\nv 208.18 62.56 -29.7\r\nv 209.91 62.33 -28.51\r\nv 210.74 62.37 -29.63\r\nv 209.73 60.25 -28.86\r\nv 210.56 60.29 -29.98\r\nv 211.35 60.34 -31.14\r\nv 211.53 62.41 -30.8\r\nv 211.74 62.44 -31.25\r\nv 211.55 60.36 -31.59\r\nv 211.31 60.4 -31.86\r\nv 211.49 62.48 -31.51\r\nv 209.27 58.54 -33.54\r\nv 208.42 58.49 -32.45\r\nv 209.13 56.43 -33.9\r\nv 208.27 56.38 -32.81\r\nv 207.5 56.31 -31.62\r\nv 207.65 58.44 -31.26\r\nv 207.51 58.4 -30.74\r\nv 207.37 56.27 -31.1\r\nv 207.68 56.23 -30.76\r\nv 207.83 58.35 -30.4\r\nv 209.56 58.15 -29.21\r\nv 210.4 58.2 -30.33\r\nv 209.42 56.03 -29.57\r\nv 210.25 56.09 -30.68\r\nv 211.04 56.15 -31.84\r\nv 211.19 58.25 -31.49\r\nv 211.39 58.28 -31.94\r\nv 211.25 56.18 -32.29\r\nv 211 56.22 -32.56\r\nv 211.15 58.32 -32.21\r\nv 209 54.32 -34.26\r\nv 208.14 54.26 -33.17\r\nv 208.89 52.2 -34.65\r\nv 208.03 52.13 -33.56\r\nv 207.26 52.05 -32.38\r\nv 207.37 54.18 -31.99\r\nv 207.24 54.14 -31.47\r\nv 207.12 52 -31.85\r\nv 207.44 51.96 -31.52\r\nv 207.55 54.1 -31.13\r\nv 209.29 53.91 -29.93\r\nv 210.12 53.97 -31.05\r\nv 209.17 51.77 -30.32\r\nv 210.01 51.85 -31.43\r\nv 210.8 51.93 -32.59\r\nv 210.92 54.04 -32.21\r\nv 211.12 54.08 -32.66\r\nv 211.01 51.96 -33.04\r\nv 210.76 52 -33.31\r\nv 210.87 54.11 -32.92\r\nv 208.78 50.08 -35.06\r\nv 207.92 50 -33.97\r\nv 208.69 47.97 -35.5\r\nv 207.83 47.87 -34.41\r\nv 207.06 47.77 -33.23\r\nv 207.15 49.91 -32.79\r\nv 207.02 49.86 -32.27\r\nv 206.92 47.72 -32.71\r\nv 207.24 47.67 -32.38\r\nv 207.33 49.81 -31.93\r\nv 209.07 49.63 -30.74\r\nv 209.91 49.71 -31.85\r\nv 208.97 47.48 -31.18\r\nv 209.81 47.57 -32.29\r\nv 210.6 47.67 -33.45\r\nv 210.7 49.8 -33\r\nv 210.9 49.84 -33.45\r\nv 210.81 47.72 -33.89\r\nv 210.56 47.75 -34.16\r\nv 210.66 49.88 -33.72\r\nv 208.6 45.85 -35.98\r\nv 207.74 45.74 -34.89\r\nv 208.52 43.75 -36.48\r\nv 207.66 43.64 -35.4\r\nv 206.89 43.52 -34.23\r\nv 206.97 45.63 -33.72\r\nv 206.83 45.57 -33.2\r\nv 206.75 43.45 -33.71\r\nv 207.07 43.4 -33.37\r\nv 207.15 45.52 -32.86\r\nv 208.88 45.32 -31.67\r\nv 209.72 45.43 -32.77\r\nv 208.8 43.2 -32.18\r\nv 209.64 43.31 -33.29\r\nv 210.43 43.44 -34.44\r\nv 210.52 45.54 -33.93\r\nv 210.72 45.59 -34.37\r\nv 210.64 43.49 -34.88\r\nv 210.39 43.53 -35.15\r\nv 210.47 45.63 -34.64\r\nv 208.45 41.71 -37\r\nv 207.59 41.59 -35.93\r\nv 208.38 39.75 -37.53\r\nv 207.52 39.62 -36.45\r\nv 206.75 39.48 -35.28\r\nv 206.82 41.46 -34.75\r\nv 206.68 41.39 -34.24\r\nv 206.61 39.41 -34.77\r\nv 206.92 39.36 -34.43\r\nv 206.99 41.34 -33.9\r\nv 208.73 41.13 -32.71\r\nv 209.57 41.25 -33.81\r\nv 208.66 39.14 -33.24\r\nv 209.5 39.28 -34.34\r\nv 210.29 39.41 -35.49\r\nv 210.36 41.39 -34.96\r\nv 210.57 41.44 -35.41\r\nv 210.5 39.47 -35.93\r\nv 210.25 39.52 -36.2\r\nv 210.32 41.48 -35.67\r\nv 208.32 37.9 -38.05\r\nv 207.46 37.77 -36.97\r\nv 208.26 36.19 -38.54\r\nv 207.4 36.05 -37.47\r\nv 206.63 35.89 -36.31\r\nv 206.68 37.62 -35.81\r\nv 206.55 37.54 -35.29\r\nv 206.49 35.81 -35.79\r\nv 206.8 35.76 -35.46\r\nv 206.86 37.49 -34.95\r\nv 208.6 37.27 -33.77\r\nv 209.43 37.41 -34.86\r\nv 208.54 35.53 -34.27\r\nv 209.38 35.67 -35.37\r\nv 210.17 35.83 -36.51\r\nv 210.23 37.55 -36.01\r\nv 210.44 37.61 -36.45\r\nv 210.38 35.89 -36.95\r\nv 210.13 35.94 -37.22\r\nv 210.19 37.66 -36.72\r\nv 208.21 34.64 -39.01\r\nv 207.35 34.49 -37.94\r\nv 208.16 33.28 -39.43\r\nv 207.3 33.13 -38.36\r\nv 206.53 32.96 -37.2\r\nv 206.58 34.33 -36.78\r\nv 206.44 34.25 -36.26\r\nv 206.39 32.88 -36.69\r\nv 206.71 32.82 -36.36\r\nv 206.75 34.19 -35.93\r\nv 208.49 33.96 -34.75\r\nv 209.33 34.11 -35.84\r\nv 208.44 32.57 -35.18\r\nv 209.28 32.73 -36.27\r\nv 210.07 32.9 -37.41\r\nv 210.12 34.27 -36.98\r\nv 210.33 34.33 -37.42\r\nv 210.28 32.96 -37.85\r\nv 210.03 33.01 -38.11\r\nv 210.08 34.38 -37.68\r\nv 208.12 32.15 -39.8\r\nv 207.26 31.99 -38.73\r\nv 208.09 31.22 -40.11\r\nv 207.23 31.05 -39.05\r\nv 206.45 30.87 -37.9\r\nv 206.49 31.82 -37.57\r\nv 206.35 31.73 -37.06\r\nv 206.31 30.78 -37.39\r\nv 206.63 30.7 -37.06\r\nv 206.66 31.66 -36.73\r\nv 208.4 31.41 -35.55\r\nv 209.24 31.57 -36.64\r\nv 208.36 30.43 -35.89\r\nv 209.2 30.6 -36.97\r\nv 210 30.78 -38.11\r\nv 210.03 31.74 -37.78\r\nv 210.24 31.81 -38.22\r\nv 210.2 30.86 -38.55\r\nv 209.96 30.91 -38.8\r\nv 209.99 31.87 -38.48\r\nv 208.05 30.47 -40.39\r\nv 207.19 30.29 -39.33\r\nv 208.02 29.87 -40.63\r\nv 207.16 29.67 -39.58\r\nv 206.38 29.45 -38.44\r\nv 206.42 30.09 -38.18\r\nv 206.28 29.99 -37.68\r\nv 206.24 29.34 -37.94\r\nv 206.56 29.25 -37.61\r\nv 206.59 29.91 -37.35\r\nv 208.32 29.6 -36.19\r\nv 209.16 29.79 -37.27\r\nv 208.29 28.91 -36.47\r\nv 209.13 29.11 -37.55\r\nv 209.93 29.33 -38.66\r\nv 209.96 29.99 -38.4\r\nv 210.17 30.07 -38.84\r\nv 210.13 29.42 -39.1\r\nv 209.89 29.49 -39.35\r\nv 209.92 30.13 -39.09\r\nv 207.99 29.39 -40.85\r\nv 207.13 29.18 -39.81\r\nv 207.96 29.01 -41.05\r\nv 207.1 28.77 -40.02\r\nv 206.32 28.5 -38.9\r\nv 206.35 28.93 -38.68\r\nv 206.21 28.81 -38.18\r\nv 206.17 28.36 -38.41\r\nv 206.49 28.25 -38.1\r\nv 206.52 28.71 -37.86\r\nv 208.25 28.32 -36.74\r\nv 209.09 28.54 -37.81\r\nv 208.21 27.81 -37.01\r\nv 209.05 28.06 -38.06\r\nv 209.86 28.32 -39.16\r\nv 209.89 28.78 -38.91\r\nv 210.1 28.88 -39.34\r\nv 210.06 28.43 -39.58\r\nv 209.82 28.52 -39.82\r\nv 209.85 28.96 -39.59\r\nv 207.93 28.69 -41.24\r\nv 207.06 28.43 -40.23\r\nv 207.9 28.4 -41.44\r\nv 207.03 28.12 -40.44\r\nv 206.24 27.79 -39.36\r\nv 206.28 28.13 -39.13\r\nv 206.14 27.98 -38.65\r\nv 206.1 27.63 -38.89\r\nv 206.41 27.49 -38.59\r\nv 206.45 27.85 -38.34\r\nv 208.17 27.36 -37.29\r\nv 209.01 27.63 -38.32\r\nv 208.12 26.95 -37.57\r\nv 208.97 27.24 -38.59\r\nv 209.77 27.56 -39.65\r\nv 209.82 27.92 -39.4\r\nv 210.03 28.04 -39.82\r\nv 209.98 27.69 -40.06\r\nv 209.74 27.8 -40.29\r\nv 209.78 28.14 -40.05\r\nv 207.86 28.11 -41.66\r\nv 206.99 27.81 -40.68\r\nv 207.82 27.83 -41.9\r\nv 206.94 27.5 -40.93\r\nv 206.15 27.13 -39.89\r\nv 206.2 27.46 -39.61\r\nv 206.05 27.29 -39.15\r\nv 206 26.94 -39.43\r\nv 206.31 26.78 -39.16\r\nv 206.36 27.14 -38.86\r\nv 208.06 26.55 -37.87\r\nv 208.92 26.86 -38.88\r\nv 208 26.15 -38.2\r\nv 208.86 26.49 -39.19\r\nv 209.67 26.85 -40.22\r\nv 209.73 27.2 -39.92\r\nv 209.94 27.34 -40.32\r\nv 209.89 27 -40.61\r\nv 209.65 27.12 -40.83\r\nv 209.7 27.46 -40.55\r\nv 207.77 27.53 -42.17\r\nv 206.89 27.19 -41.22\r\nv 207.71 27.22 -42.48\r\nv 206.83 26.86 -41.55\r\nv 206.03 26.44 -40.55\r\nv 206.09 26.79 -40.2\r\nv 205.94 26.6 -39.75\r\nv 205.88 26.23 -40.12\r\nv 206.18 26.05 -39.86\r\nv 206.25 26.42 -39.49\r\nv 207.94 25.75 -38.57\r\nv 208.8 26.11 -39.54\r\nv 207.87 25.35 -38.97\r\nv 208.73 25.73 -39.92\r\nv 209.55 26.13 -40.9\r\nv 209.61 26.49 -40.54\r\nv 209.83 26.65 -40.93\r\nv 209.77 26.3 -41.28\r\nv 209.53 26.44 -41.48\r\nv 209.59 26.79 -41.14\r\nv 207.65 26.89 -42.84\r\nv 206.76 26.5 -41.93\r\nv 207.58 26.52 -43.26\r\nv 206.69 26.12 -42.37\r\nv 205.88 25.67 -41.4\r\nv 205.96 26.07 -40.95\r\nv 205.81 25.85 -40.52\r\nv 205.73 25.45 -40.99\r\nv 206.03 25.26 -40.74\r\nv 206.11 25.67 -40.27\r\nv 207.79 24.93 -39.42\r\nv 208.65 25.33 -40.35\r\nv 207.7 24.5 -39.91\r\nv 208.57 24.91 -40.83\r\nv 209.4 25.35 -41.77\r\nv 209.48 25.75 -41.31\r\nv 209.7 25.93 -41.68\r\nv 209.62 25.53 -42.13\r\nv 209.38 25.68 -42.32\r\nv 209.46 26.07 -41.88\r\nv 207.5 26.12 -43.74\r\nv 206.6 25.71 -42.87\r\nv 207.41 25.68 -44.3\r\nv 206.51 25.26 -43.44\r\nv 205.7 24.78 -42.51\r\nv 205.79 25.24 -41.92\r\nv 205.64 25.01 -41.51\r\nv 205.54 24.54 -42.11\r\nv 205.84 24.34 -41.87\r\nv 205.94 24.82 -41.28\r\nv 207.61 24.04 -40.47\r\nv 208.48 24.47 -41.36\r\nv 207.51 23.55 -41.08\r\nv 208.39 23.99 -41.96\r\nv 209.22 24.46 -42.88\r\nv 209.31 24.92 -42.29\r\nv 209.53 25.11 -42.64\r\nv 209.44 24.65 -43.22\r\nv 209.2 24.81 -43.41\r\nv 209.3 25.26 -42.83\r\nv 207.31 25.19 -44.94\r\nv 206.41 24.76 -44.09\r\nv 207.2 24.67 -45.64\r\nv 206.3 24.23 -44.81\r\nv 205.48 23.73 -43.9\r\nv 205.59 24.27 -43.17\r\nv 205.44 24.03 -42.78\r\nv 205.32 23.48 -43.52\r\nv 205.62 23.27 -43.29\r\nv 205.73 23.83 -42.55\r\nv 207.4 23.03 -41.77\r\nv 208.28 23.47 -42.64\r\nv 207.29 22.47 -42.53\r\nv 208.16 22.92 -43.38\r\nv 209 23.41 -44.27\r\nv 209.11 23.95 -43.54\r\nv 209.34 24.15 -43.88\r\nv 209.22 23.61 -44.61\r\nv 208.99 23.77 -44.78\r\nv 209.1 24.31 -44.06\r\nv 207.08 24.11 -46.42\r\nv 206.18 23.66 -45.59\r\nv 206.96 23.54 -47.26\r\nv 206.05 23.07 -46.44\r\nv 205.23 22.55 -45.57\r\nv 205.36 23.15 -44.71\r\nv 205.2 22.9 -44.33\r\nv 205.07 22.3 -45.2\r\nv 205.37 22.09 -44.98\r\nv 205.5 22.69 -44.1\r\nv 207.16 21.87 -43.35\r\nv 208.04 22.34 -44.2\r\nv 207.03 21.25 -44.25\r\nv 207.91 21.73 -45.07\r\nv 208.75 22.24 -45.93\r\nv 208.88 22.83 -45.07\r\nv 209.1 23.04 -45.4\r\nv 208.98 22.45 -46.26\r\nv 208.75 22.61 -46.43\r\nv 208.87 23.21 -45.57\r\nv 206.83 22.94 -48.15\r\nv 205.92 22.47 -47.35\r\nv 206.7 22.33 -49.1\r\nv 205.79 21.85 -48.32\r\nv 204.96 21.3 -47.47\r\nv 205.1 21.93 -46.5\r\nv 204.94 21.68 -46.13\r\nv 204.8 21.04 -47.12\r\nv 205.09 20.82 -46.91\r\nv 205.23 21.46 -45.92\r\nv 206.89 20.62 -45.2\r\nv 207.78 21.1 -46.01\r\nv 206.75 19.97 -46.21\r\nv 207.64 20.46 -47\r\nv 208.48 20.99 -47.83\r\nv 208.62 21.62 -46.85\r\nv 208.85 21.83 -47.17\r\nv 208.71 21.21 -48.14\r\nv 208.48 21.38 -48.31\r\nv 208.62 22 -47.34\r\nv 206.56 21.71 -50.09\r\nv 205.65 21.22 -49.33\r\nv 206.42 21.09 -51.14\r\nv 205.51 20.59 -50.39\r\nv 204.68 20.03 -49.58\r\nv 204.82 20.67 -48.51\r\nv 204.66 20.4 -48.15\r\nv 204.52 19.75 -49.24\r\nv 204.81 19.53 -49.04\r\nv 204.95 20.17 -47.95\r\nv 206.61 19.31 -47.27\r\nv 207.5 19.82 -48.05\r\nv 206.46 18.65 -48.38\r\nv 207.35 19.17 -49.14\r\nv 208.2 19.72 -49.93\r\nv 208.34 20.35 -48.86\r\nv 208.57 20.58 -49.17\r\nv 208.43 19.94 -50.24\r\nv 208.2 20.12 -50.39\r\nv 208.34 20.75 -49.33\r\nv 206.28 20.47 -52.23\r\nv 205.37 19.97 -51.5\r\nv 206.01 19.96 -52.64\r\nv 205.3 19.46 -52.32\r\nv 204.71 19.16 -51.51\r\nv 204.54 19.4 -50.7\r\nv 204.37 19.12 -50.37\r\nv 204.55 18.82 -51.48\r\nv 204.84 18.6 -51.29\r\nv 204.66 18.89 -50.17\r\nv 206.31 18.01 -49.52\r\nv 207.21 18.53 -50.27\r\nv 206.44 17.8 -50.37\r\nv 207.08 18.07 -51.15\r\nv 207.73 18.61 -51.5\r\nv 208.06 19.08 -51.05\r\nv 208.29 19.31 -51.35\r\nv 207.89 18.66 -51.95\r\nv 207.66 18.84 -52.1\r\nv 208.06 19.49 -51.5\r\nv 206.14 17.58 -50.43\r\nv 206.98 17.96 -51.48\r\nv 205.91 17.76 -50.58\r\nv 206.75 18.14 -51.63\r\nv 201.77 3.37 132.26\r\nv 201.48 3.51 132.59\r\nv 201.03 1.96 133.21\r\nv 201.32 1.83 132.88\r\nv 200.43 0.84 133.3\r\nv 200.71 0.71 132.97\r\nv 200.02 3.04 128.72\r\nv 199.57 1.5 129.35\r\nv 200.54 2.8 128.13\r\nv 200.08 1.26 128.75\r\nv 199.53 0.39 130.16\r\nv 202.79 6.8 130.89\r\nv 202.5 6.94 131.22\r\nv 201.97 5.17 131.92\r\nv 202.26 5.03 131.59\r\nv 201.05 6.5 127.35\r\nv 200.52 4.71 128.05\r\nv 201.56 6.26 126.75\r\nv 201.03 4.47 127.45\r\nv 203.92 10.61 129.43\r\nv 203.63 10.74 129.76\r\nv 203.06 8.81 130.5\r\nv 203.34 8.68 130.17\r\nv 202.19 10.32 125.88\r\nv 201.61 8.38 126.62\r\nv 202.7 10.09 125.28\r\nv 202.12 8.14 126.02\r\nv 205.08 14.49 127.98\r\nv 204.79 14.62 128.31\r\nv 204.22 12.69 129.03\r\nv 204.5 12.56 128.7\r\nv 203.35 14.22 124.42\r\nv 202.77 12.28 125.14\r\nv 203.87 13.99 123.82\r\nv 203.29 12.05 124.54\r\nv 206.16 18.13 126.66\r\nv 205.87 18.26 126.99\r\nv 205.35 16.49 127.63\r\nv 205.63 16.36 127.3\r\nv 204.44 17.87 123.09\r\nv 203.91 16.1 123.73\r\nv 204.95 17.64 122.49\r\nv 204.42 15.87 123.13\r\nv 207.06 21.19 125.57\r\nv 206.78 21.31 125.91\r\nv 206.35 19.88 126.41\r\nv 206.64 19.75 126.08\r\nv 205.35 20.94 122\r\nv 204.92 19.5 122.51\r\nv 205.86 20.72 121.4\r\nv 205.44 19.27 121.91\r\nv 207.71 23.39 124.82\r\nv 207.42 23.52 125.15\r\nv 207.13 22.51 125.49\r\nv 207.42 22.39 125.16\r\nv 206 23.18 121.24\r\nv 205.7 22.16 121.58\r\nv 206.52 22.96 120.63\r\nv 206.22 21.94 120.98\r\nv 208.14 24.91 124.34\r\nv 207.84 25.03 124.68\r\nv 207.66 24.34 124.89\r\nv 207.95 24.23 124.55\r\nv 206.44 24.74 120.74\r\nv 206.24 24.03 120.96\r\nv 206.96 24.53 120.14\r\nv 206.76 23.82 120.36\r\nv 208.41 25.97 124.05\r\nv 208.12 26.07 124.39\r\nv 208 25.59 124.51\r\nv 208.29 25.48 124.17\r\nv 206.73 25.85 120.44\r\nv 206.6 25.33 120.57\r\nv 207.26 25.67 119.83\r\nv 207.12 25.14 119.96\r\nv 208.6 26.81 123.87\r\nv 208.3 26.89 124.22\r\nv 208.22 26.49 124.3\r\nv 208.51 26.4 123.95\r\nv 206.93 26.74 120.25\r\nv 206.83 26.31 120.34\r\nv 207.46 26.59 119.63\r\nv 207.37 26.14 119.72\r\nv 208.77 27.66 123.73\r\nv 208.47 27.73 124.08\r\nv 208.39 27.3 124.15\r\nv 208.69 27.22 123.8\r\nv 207.1 27.63 120.11\r\nv 207.02 27.17 120.18\r\nv 207.64 27.51 119.48\r\nv 207.55 27.03 119.55\r\nv 208.94 28.74 123.6\r\nv 208.63 28.8 123.95\r\nv 208.55 28.22 124.02\r\nv 208.85 28.16 123.67\r\nv 207.27 28.74 119.98\r\nv 207.19 28.14 120.04\r\nv 207.82 28.64 119.35\r\nv 207.73 28.03 119.41\r\nv 209.13 30.28 123.48\r\nv 208.83 30.32 123.83\r\nv 208.73 29.49 123.89\r\nv 209.03 29.44 123.54\r\nv 207.47 30.29 119.85\r\nv 207.37 29.45 119.91\r\nv 208.02 30.21 119.22\r\nv 207.92 29.36 119.28\r\nv 209.38 32.48 123.33\r\nv 209.07 32.52 123.68\r\nv 208.94 31.32 123.76\r\nv 209.25 31.28 123.4\r\nv 207.72 32.5 119.7\r\nv 207.59 31.3 119.78\r\nv 208.27 32.43 119.07\r\nv 208.14 31.23 119.14\r\nv 209.69 35.51 123.15\r\nv 209.39 35.54 123.5\r\nv 209.22 33.93 123.59\r\nv 209.53 33.9 123.24\r\nv 208.03 35.53 119.52\r\nv 207.87 33.92 119.61\r\nv 208.58 35.47 118.89\r\nv 208.42 33.86 118.98\r\nv 210.05 39.19 122.94\r\nv 209.75 39.23 123.29\r\nv 209.56 37.32 123.4\r\nv 209.87 37.28 123.05\r\nv 208.39 39.22 119.31\r\nv 208.21 37.31 119.42\r\nv 208.94 39.16 118.68\r\nv 208.76 37.25 118.79\r\nv 210.44 43.31 122.72\r\nv 210.13 43.34 123.07\r\nv 209.94 41.24 123.18\r\nv 210.25 41.21 122.83\r\nv 208.78 43.33 119.09\r\nv 208.59 41.23 119.2\r\nv 209.33 43.27 118.46\r\nv 209.14 41.17 118.57\r\nv 210.83 47.63 122.49\r\nv 210.52 47.66 122.84\r\nv 210.33 45.49 122.96\r\nv 210.64 45.45 122.61\r\nv 209.17 47.64 118.86\r\nv 208.98 45.47 118.98\r\nv 209.72 47.59 118.23\r\nv 209.53 45.42 118.34\r\nv 211.2 51.95 122.26\r\nv 210.89 51.98 122.62\r\nv 210.71 49.82 122.73\r\nv 211.02 49.79 122.38\r\nv 209.54 51.96 118.64\r\nv 209.36 49.8 118.75\r\nv 210.09 51.91 118\r\nv 209.91 49.75 118.12\r\nv 211.57 56.25 122.01\r\nv 211.27 56.29 122.36\r\nv 211.08 54.14 122.49\r\nv 211.38 54.11 122.14\r\nv 209.91 56.25 118.38\r\nv 209.72 54.11 118.51\r\nv 210.46 56.19 117.75\r\nv 210.27 54.05 117.88\r\nv 211.97 60.52 121.69\r\nv 211.66 60.56 122.05\r\nv 211.46 58.43 122.21\r\nv 211.77 58.39 121.86\r\nv 210.3 60.49 118.07\r\nv 210.1 58.37 118.23\r\nv 210.85 60.42 117.44\r\nv 210.65 58.31 117.6\r\nv 212.41 64.73 121.29\r\nv 212.11 64.78 121.64\r\nv 211.88 62.67 121.86\r\nv 212.18 62.63 121.51\r\nv 210.75 64.68 117.67\r\nv 210.52 62.59 117.88\r\nv 211.29 64.59 117.04\r\nv 211.07 62.51 117.25\r\nv 212.93 68.89 120.76\r\nv 212.63 68.95 121.11\r\nv 212.36 66.87 121.39\r\nv 212.66 66.82 121.04\r\nv 211.26 68.79 117.15\r\nv 210.99 66.74 117.42\r\nv 211.8 68.69 116.52\r\nv 211.54 66.65 116.8\r\nv 213.52 72.98 120.09\r\nv 213.22 73.05 120.44\r\nv 212.91 71.01 120.8\r\nv 213.22 70.94 120.45\r\nv 211.84 72.83 116.48\r\nv 211.54 70.82 116.83\r\nv 212.38 72.71 115.86\r\nv 212.08 70.71 116.21\r\nv 214.17 77 119.25\r\nv 213.87 77.08 119.59\r\nv 213.54 75.07 120.04\r\nv 213.84 75 119.69\r\nv 212.48 76.79 115.65\r\nv 212.15 74.82 116.09\r\nv 213.02 76.64 115.03\r\nv 212.69 74.69 115.47\r\nv 214.89 80.93 118.21\r\nv 214.6 81.02 118.55\r\nv 214.23 79.06 119.1\r\nv 214.53 78.97 118.76\r\nv 213.19 80.64 114.63\r\nv 212.83 78.73 115.17\r\nv 213.73 80.47 114.02\r\nv 213.37 78.57 114.55\r\nv 215.68 84.76 116.96\r\nv 215.38 84.87 117.3\r\nv 214.98 82.96 117.95\r\nv 215.28 82.86 117.61\r\nv 213.96 84.39 113.41\r\nv 213.57 82.53 114.05\r\nv 214.49 84.19 112.81\r\nv 214.1 82.35 113.44\r\nv 216.5 88.46 115.5\r\nv 216.21 88.59 115.83\r\nv 215.79 86.75 116.59\r\nv 216.09 86.63 116.25\r\nv 214.76 88 111.99\r\nv 214.36 86.22 112.72\r\nv 215.29 87.78 111.4\r\nv 214.88 86 112.13\r\nv 217.36 91.99 113.85\r\nv 217.07 92.14 114.17\r\nv 216.64 90.39 115.02\r\nv 216.93 90.25 114.7\r\nv 215.59 91.43 110.38\r\nv 215.18 89.74 111.21\r\nv 216.11 91.18 109.8\r\nv 215.7 89.5 110.62\r\nv 218.22 95.32 112\r\nv 217.94 95.48 112.32\r\nv 217.5 93.83 113.27\r\nv 217.79 93.69 112.95\r\nv 216.42 94.64 108.6\r\nv 216.01 93.07 109.51\r\nv 216.93 94.36 108.04\r\nv 216.52 92.8 108.94\r\nv 219.07 98.4 109.98\r\nv 218.79 98.58 110.29\r\nv 218.37 97.06 111.32\r\nv 218.65 96.9 111.01\r\nv 217.23 97.58 106.68\r\nv 216.83 96.15 107.66\r\nv 217.73 97.27 106.13\r\nv 217.34 95.85 107.1\r\nv 219.85 101.19 107.8\r\nv 219.58 101.38 108.09\r\nv 219.19 100.02 109.21\r\nv 219.47 99.84 108.91\r\nv 217.96 100.16 104.66\r\nv 217.61 98.92 105.67\r\nv 218.45 99.82 104.14\r\nv 218.11 98.6 105.14\r\nv 220.49 103.62 105.39\r\nv 220.21 103.84 105.66\r\nv 219.93 102.65 106.91\r\nv 220.2 102.45 106.63\r\nv 218.51 102.24 102.62\r\nv 218.27 101.27 103.64\r\nv 218.99 101.87 102.13\r\nv 218.76 100.91 103.13\r\nv 218.64 103.75 100.54\r\nv 218.64 103.06 101.6\r\nv 219.13 103.37 100.06\r\nv 219.13 102.69 101.12\r\nv 218.2 104.84 98.12\r\nv 218.49 104.33 99.39\r\nv 218.69 104.45 97.66\r\nv 218.98 103.95 98.92\r\nv 219.04 108.26 95.6\r\nv 218.76 108.48 95.84\r\nv 219.3 108 97.66\r\nv 219.58 107.78 97.41\r\nv 219.79 107.43 99.42\r\nv 220.06 107.21 99.17\r\nv 217.32 105.71 95.09\r\nv 217.8 105.29 96.68\r\nv 217.82 105.31 94.67\r\nv 218.29 104.9 96.24\r\nv 217.91 109.09 91.8\r\nv 217.62 109.32 92.02\r\nv 218.19 108.92 93.95\r\nv 218.48 108.69 93.73\r\nv 216.26 106.49 91.53\r\nv 216.81 106.11 93.37\r\nv 216.77 106.08 91.15\r\nv 217.31 105.71 92.96\r\nv 216.78 109.79 87.8\r\nv 216.49 110.02 87.99\r\nv 217.05 109.68 90.02\r\nv 217.34 109.45 89.82\r\nv 215.17 107.17 87.64\r\nv 215.71 106.84 89.62\r\nv 215.69 106.75 87.3\r\nv 216.23 106.43 89.25\r\nv 215.71 110.39 83.69\r\nv 215.42 110.63 83.86\r\nv 215.94 110.33 85.94\r\nv 216.23 110.1 85.76\r\nv 214.12 107.76 83.59\r\nv 214.63 107.47 85.63\r\nv 214.65 107.34 83.28\r\nv 215.16 107.06 85.3\r\nv 214.73 110.92 79.5\r\nv 214.43 111.15 79.66\r\nv 214.91 110.9 81.76\r\nv 215.21 110.67 81.6\r\nv 213.15 108.28 79.44\r\nv 213.62 108.03 81.52\r\nv 213.68 107.86 79.17\r\nv 214.15 107.61 81.23\r\nv 213.85 111.36 75.3\r\nv 213.55 111.6 75.44\r\nv 213.98 111.39 77.54\r\nv 214.28 111.15 77.4\r\nv 212.27 108.73 75.27\r\nv 212.7 108.51 77.36\r\nv 212.82 108.3 75.02\r\nv 213.24 108.09 77.1\r\nv 213.08 111.74 71.13\r\nv 212.77 111.98 71.25\r\nv 213.15 111.8 73.34\r\nv 213.45 111.56 73.21\r\nv 211.51 109.1 71.12\r\nv 211.87 108.92 73.19\r\nv 212.05 108.67 70.91\r\nv 212.42 108.49 72.96\r\nv 212.43 112.04 67.06\r\nv 212.12 112.28 67.16\r\nv 212.43 112.14 69.19\r\nv 212.74 111.9 69.08\r\nv 210.86 109.4 67.06\r\nv 211.17 109.26 69.08\r\nv 211.41 108.97 66.88\r\nv 211.72 108.83 68.88\r\nv 211.9 112.29 63.13\r\nv 211.59 112.53 63.21\r\nv 211.84 112.42 65.17\r\nv 212.15 112.17 65.07\r\nv 210.32 109.65 63.13\r\nv 210.58 109.53 65.08\r\nv 210.88 109.21 62.98\r\nv 211.13 109.1 64.91\r\nv 211.47 112.48 59.38\r\nv 211.15 112.72 59.46\r\nv 211.36 112.63 61.31\r\nv 211.67 112.39 61.23\r\nv 209.89 109.84 59.4\r\nv 210.1 109.75 61.24\r\nv 210.45 109.4 59.27\r\nv 210.66 109.31 61.1\r\nv 211.13 112.63 55.88\r\nv 210.81 112.87 55.94\r\nv 210.97 112.8 57.67\r\nv 211.29 112.56 57.6\r\nv 209.56 109.98 55.9\r\nv 209.71 109.92 57.62\r\nv 210.12 109.55 55.79\r\nv 210.28 109.48 57.5\r\nv 210.87 112.73 52.66\r\nv 210.56 112.98 52.7\r\nv 210.67 112.93 54.29\r\nv 210.99 112.68 54.24\r\nv 209.3 110.09 52.68\r\nv 209.42 110.04 54.26\r\nv 209.86 109.65 52.59\r\nv 209.98 109.6 54.16\r\nv 210.69 112.8 49.63\r\nv 210.37 113.05 49.66\r\nv 210.46 113.02 51.16\r\nv 210.77 112.77 51.12\r\nv 209.12 110.16 49.65\r\nv 209.2 110.13 51.15\r\nv 209.68 109.72 49.59\r\nv 209.76 109.69 51.07\r\nv 210.57 112.84 46.71\r\nv 210.26 113.09 46.73\r\nv 210.31 113.07 48.18\r\nv 210.62 112.83 48.16\r\nv 209 110.2 46.74\r\nv 209.05 110.18 48.19\r\nv 209.57 109.76 46.71\r\nv 209.62 109.75 48.14\r\nv 210.52 112.85 43.81\r\nv 210.2 113.09 43.81\r\nv 210.22 113.09 45.27\r\nv 210.54 112.85 45.26\r\nv 208.95 110.21 43.84\r\nv 208.97 110.21 45.29\r\nv 209.51 109.77 43.83\r\nv 209.53 109.77 45.27\r\nv 210.51 112.83 40.85\r\nv 210.2 113.07 40.84\r\nv 210.19 113.09 42.33\r\nv 210.51 112.84 42.34\r\nv 208.94 110.19 40.88\r\nv 208.94 110.2 42.37\r\nv 209.51 109.75 40.9\r\nv 209.51 109.76 42.38\r\nv 210.57 112.78 37.81\r\nv 210.25 113.03 37.79\r\nv 210.22 113.05 39.32\r\nv 210.53 112.81 39.34\r\nv 209 110.14 37.85\r\nv 208.96 110.17 39.38\r\nv 209.56 109.7 37.89\r\nv 209.53 109.73 39.41\r\nv 210.68 112.7 34.7\r\nv 210.36 112.95 34.66\r\nv 210.3 112.99 36.24\r\nv 210.62 112.75 36.27\r\nv 209.11 110.06 34.75\r\nv 209.04 110.1 36.31\r\nv 209.67 109.62 34.81\r\nv 209.61 109.67 36.36\r\nv 210.85 112.59 31.51\r\nv 210.54 112.84 31.46\r\nv 210.44 112.9 33.07\r\nv 210.76 112.65 33.12\r\nv 209.28 109.95 31.56\r\nv 209.19 110.01 33.16\r\nv 209.85 109.51 31.65\r\nv 209.75 109.57 33.24\r\nv 211.09 112.45 28.24\r\nv 210.78 112.69 28.18\r\nv 210.65 112.77 29.83\r\nv 210.96 112.53 29.89\r\nv 209.52 109.81 28.29\r\nv 209.39 109.89 29.94\r\nv 210.08 109.37 28.41\r\nv 209.96 109.45 30.04\r\nv 211.41 112.27 24.87\r\nv 211.09 112.51 24.8\r\nv 210.92 112.61 26.5\r\nv 211.24 112.36 26.57\r\nv 209.83 109.63 24.93\r\nv 209.67 109.72 26.63\r\nv 210.4 109.19 25.08\r\nv 210.23 109.29 26.75\r\nv 211.81 112.03 21.4\r\nv 211.5 112.27 21.31\r\nv 211.29 112.4 23.07\r\nv 211.6 112.16 23.15\r\nv 210.24 109.39 21.47\r\nv 210.03 109.52 23.22\r\nv 210.8 108.96 21.65\r\nv 210.58 109.08 23.37\r\nv 212.33 111.74 17.82\r\nv 212.02 111.98 17.71\r\nv 211.75 112.14 19.52\r\nv 212.06 111.9 19.63\r\nv 210.75 109.1 17.9\r\nv 210.48 109.26 19.7\r\nv 211.3 108.67 18.11\r\nv 211.04 108.83 19.89\r\nv 212.96 111.38 14.11\r\nv 212.65 111.62 13.98\r\nv 212.32 111.81 15.86\r\nv 212.63 111.57 15.98\r\nv 211.38 108.75 14.21\r\nv 211.05 108.93 16.07\r\nv 211.92 108.32 14.45\r\nv 211.6 108.51 16.29\r\nv 213.71 110.95 10.29\r\nv 213.41 111.18 10.14\r\nv 213.01 111.41 12.07\r\nv 213.32 111.18 12.21\r\nv 212.12 108.31 10.42\r\nv 211.74 108.54 12.33\r\nv 212.66 107.89 10.7\r\nv 212.28 108.11 12.59\r\nv 214.59 110.42 6.38\r\nv 214.29 110.65 6.2\r\nv 213.83 110.93 8.18\r\nv 214.13 110.69 8.35\r\nv 212.99 107.79 6.55\r\nv 212.54 108.06 8.5\r\nv 213.53 107.37 6.86\r\nv 213.08 107.64 8.79\r\nv 215.6 109.78 2.39\r\nv 215.31 110.01 2.19\r\nv 214.78 110.34 4.21\r\nv 215.08 110.11 4.39\r\nv 213.99 107.16 2.62\r\nv 213.48 107.49 4.59\r\nv 214.51 106.75 2.96\r\nv 214 107.07 4.92\r\nv 216.73 109.03 -1.66\r\nv 216.45 109.25 -1.87\r\nv 215.87 109.65 0.16\r\nv 216.15 109.42 0.36\r\nv 215.09 106.43 -1.33\r\nv 214.53 106.81 0.63\r\nv 215.61 106.02 -0.94\r\nv 215.05 106.4 1\r\nv 218.03 108.02 -5.79\r\nv 217.76 108.25 -6.02\r\nv 217.03 108.82 -3.88\r\nv 217.32 108.6 -3.65\r\nv 216.27 105.57 -5.2\r\nv 215.66 106.02 -3.23\r\nv 216.77 105.17 -4.79\r\nv 216.16 105.62 -2.83\r\nv 218.75 107.41 -8.47\r\nv 218.98 107.15 -8.24\r\nv 217.57 104.37 -9.42\r\nv 217.01 105.08 -7.32\r\nv 218.05 103.92 -9.04\r\nv 217.5 104.64 -6.93\r\nv 199.46 0.44 132.36\r\nv 199.75 0.3 132.02\r\nv 260.93 114.53 -16.95\r\nv 263.22 114.61 -17\r\nv 263.19 115.22 -17.45\r\nv 260.98 113.94 -16.51\r\nv 263.24 114.02 -16.57\r\nv 265.5 114.05 -16.59\r\nv 265.5 114.64 -17.02\r\nv 265.5 115.25 -17.47\r\nv 260.9 113.78 -20.98\r\nv 260.87 114.11 -21.22\r\nv 263.19 114.2 -21.28\r\nv 263.2 113.87 -21.04\r\nv 263.18 114.5 -20.94\r\nv 265.5 114.53 -20.95\r\nv 265.5 114.22 -21.3\r\nv 265.5 113.89 -21.06\r\nv 256.35 114.21 -16.73\r\nv 258.64 114.39 -16.86\r\nv 256.43 113.62 -16.3\r\nv 258.71 113.81 -16.42\r\nv 256.27 113.46 -20.77\r\nv 256.22 113.78 -21.01\r\nv 258.55 113.97 -21.13\r\nv 258.59 113.65 -20.89\r\nv 251.75 113.68 -16.38\r\nv 254.05 113.97 -16.57\r\nv 251.87 113.1 -15.95\r\nv 254.15 113.39 -16.14\r\nv 251.64 112.93 -20.41\r\nv 251.57 113.25 -20.65\r\nv 253.9 113.54 -20.85\r\nv 253.96 113.22 -20.61\r\nv 247.12 112.97 -15.9\r\nv 249.44 113.35 -16.16\r\nv 247.28 112.39 -15.47\r\nv 249.58 112.77 -15.73\r\nv 246.98 112.21 -19.93\r\nv 246.89 112.53 -20.17\r\nv 249.23 112.92 -20.42\r\nv 249.31 112.59 -20.19\r\nv 242.48 112.08 -15.3\r\nv 244.8 112.54 -15.61\r\nv 242.67 111.51 -14.87\r\nv 244.98 111.97 -15.19\r\nv 242.31 111.31 -19.32\r\nv 242.2 111.63 -19.55\r\nv 244.54 112.1 -19.87\r\nv 244.64 111.78 -19.64\r\nv 237.9 111.04 -14.58\r\nv 240.18 111.58 -14.95\r\nv 238.12 110.48 -14.16\r\nv 240.39 111.01 -14.53\r\nv 237.69 110.27 -18.6\r\nv 237.57 110.58 -18.83\r\nv 239.87 111.12 -19.21\r\nv 239.99 110.81 -18.97\r\nv 233.47 109.9 -13.77\r\nv 235.66 110.48 -14.19\r\nv 233.71 109.34 -13.36\r\nv 235.89 109.92 -13.77\r\nv 233.22 109.12 -17.79\r\nv 233.08 109.43 -18.02\r\nv 235.3 110.02 -18.43\r\nv 235.44 109.71 -18.2\r\nv 229.26 108.68 -12.89\r\nv 231.33 109.3 -13.34\r\nv 229.53 108.14 -12.48\r\nv 231.59 108.75 -12.93\r\nv 228.97 107.89 -16.89\r\nv 228.82 108.2 -17.12\r\nv 230.92 108.82 -17.58\r\nv 231.06 108.51 -17.35\r\nv 225.19 107.36 -11.88\r\nv 227.27 108.06 -12.42\r\nv 225.5 106.82 -11.48\r\nv 227.56 107.52 -12.02\r\nv 225.03 106.62 -15.93\r\nv 224.86 106.92 -16.16\r\nv 226.79 107.56 -16.64\r\nv 226.96 107.26 -16.42\r\nv 220.78 106.1 -9.98\r\nv 222.87 106.52 -11.2\r\nv 223.19 105.99 -10.8\r\nv 221.66 105.42 -14.97\r\nv 221.46 105.71 -15.18\r\nv 223.03 106.29 -15.65\r\nv 223.21 105.99 -15.43\r\nv 219.81 104.53 -14.55\r\nv 219.55 104.79 -14.76\r\nv 220.29 105.25 -14.8\r\nv 220.52 104.97 -14.59\r\nv 217.47 102.07 -12.88\r\nv 217.66 103.28 -11.3\r\nv 217.96 101.69 -12.42\r\nv 218.14 102.86 -10.89\r\nv 219.35 103.25 -16.02\r\nv 219.08 103.46 -16.28\r\nv 219.24 104.23 -15.32\r\nv 219.51 104 -15.09\r\nv 216.84 99.86 -15.13\r\nv 217.19 100.97 -14.1\r\nv 217.34 99.51 -14.63\r\nv 217.69 100.61 -13.62\r\nv 218.65 100.9 -18.33\r\nv 218.37 101.09 -18.6\r\nv 218.77 102.38 -17.41\r\nv 219.05 102.18 -17.14\r\nv 216.02 97.29 -17.15\r\nv 216.45 98.63 -16.15\r\nv 216.53 96.97 -16.63\r\nv 216.96 98.3 -15.64\r\nv 217.76 98.11 -20.53\r\nv 217.48 98.29 -20.82\r\nv 217.94 99.73 -19.74\r\nv 218.22 99.55 -19.46\r\nv 215.11 94.37 -19.05\r\nv 215.57 95.87 -18.12\r\nv 215.63 94.08 -18.51\r\nv 216.09 95.56 -17.59\r\nv 216.8 95.04 -22.53\r\nv 216.51 95.2 -22.83\r\nv 217 96.78 -21.85\r\nv 217.29 96.61 -21.55\r\nv 214.17 91.17 -20.8\r\nv 214.64 92.8 -19.95\r\nv 214.7 90.91 -20.25\r\nv 215.16 92.52 -19.4\r\nv 215.82 91.72 -24.35\r\nv 215.53 91.87 -24.65\r\nv 216.02 93.56 -23.77\r\nv 216.31 93.41 -23.46\r\nv 213.23 87.76 -22.39\r\nv 213.69 89.49 -21.61\r\nv 213.77 87.52 -21.82\r\nv 214.23 89.24 -21.06\r\nv 214.86 88.22 -25.98\r\nv 214.56 88.35 -26.29\r\nv 215.04 90.13 -25.5\r\nv 215.34 89.99 -25.19\r\nv 212.32 84.17 -23.81\r\nv 212.77 85.98 -23.12\r\nv 212.87 83.96 -23.23\r\nv 213.31 85.76 -22.55\r\nv 213.94 84.55 -27.43\r\nv 213.63 84.66 -27.75\r\nv 214.09 86.52 -27.04\r\nv 214.39 86.4 -26.73\r\nv 211.48 80.44 -25.06\r\nv 211.89 82.32 -24.46\r\nv 212.03 80.25 -24.48\r\nv 212.44 82.12 -23.88\r\nv 213.08 80.75 -28.71\r\nv 212.77 80.85 -29.03\r\nv 213.19 82.77 -28.41\r\nv 213.5 82.66 -28.09\r\nv 210.71 76.6 -26.16\r\nv 211.08 78.53 -25.63\r\nv 211.27 76.44 -25.57\r\nv 211.64 78.36 -25.04\r\nv 212.3 76.86 -29.82\r\nv 211.99 76.95 -30.14\r\nv 212.37 78.91 -29.61\r\nv 212.68 78.82 -29.28\r\nv 210.04 72.66 -27.11\r\nv 210.36 74.64 -26.65\r\nv 210.6 72.52 -26.52\r\nv 210.92 74.49 -26.06\r\nv 211.62 72.89 -30.78\r\nv 211.31 72.97 -31.11\r\nv 211.64 74.97 -30.64\r\nv 211.95 74.89 -30.31\r\nv 209.46 68.64 -27.95\r\nv 209.74 70.66 -27.55\r\nv 210.02 68.52 -27.35\r\nv 210.29 70.53 -26.95\r\nv 211.03 68.85 -31.63\r\nv 210.72 68.92 -31.96\r\nv 211 70.95 -31.55\r\nv 211.32 70.88 -31.21\r\nv 208.97 64.54 -28.71\r\nv 209.2 66.6 -28.34\r\nv 209.53 64.44 -28.11\r\nv 209.76 66.49 -27.74\r\nv 210.55 64.75 -32.39\r\nv 210.23 64.8 -32.72\r\nv 210.47 66.87 -32.35\r\nv 210.78 66.81 -32.02\r\nv 208.58 60.37 -29.42\r\nv 208.76 62.46 -29.07\r\nv 209.14 60.28 -28.82\r\nv 209.33 62.37 -28.47\r\nv 210.16 60.58 -33.09\r\nv 209.84 60.63 -33.43\r\nv 210.03 62.72 -33.08\r\nv 210.34 62.67 -32.74\r\nv 208.27 56.14 -30.13\r\nv 208.41 58.26 -29.77\r\nv 208.83 56.06 -29.52\r\nv 208.98 58.18 -29.17\r\nv 209.85 56.39 -33.8\r\nv 209.54 56.43 -34.13\r\nv 209.68 58.54 -33.78\r\nv 209.99 58.49 -33.44\r\nv 208.02 51.87 -30.89\r\nv 208.14 54.01 -30.5\r\nv 208.59 51.79 -30.28\r\nv 208.7 53.93 -29.89\r\nv 209.61 52.16 -34.54\r\nv 209.29 52.21 -34.88\r\nv 209.41 54.32 -34.5\r\nv 209.72 54.28 -34.16\r\nv 207.82 47.58 -31.75\r\nv 207.92 49.73 -31.3\r\nv 208.39 47.49 -31.14\r\nv 208.48 49.64 -30.7\r\nv 209.41 47.93 -35.39\r\nv 209.1 47.98 -35.73\r\nv 209.19 50.09 -35.29\r\nv 209.5 50.05 -34.95\r\nv 207.65 43.3 -32.75\r\nv 207.73 45.43 -32.23\r\nv 208.22 43.21 -32.14\r\nv 208.3 45.34 -31.63\r\nv 209.24 43.72 -36.38\r\nv 208.93 43.77 -36.71\r\nv 209.01 45.86 -36.21\r\nv 209.32 45.81 -35.87\r\nv 207.51 39.25 -33.81\r\nv 207.58 41.24 -33.27\r\nv 208.07 39.15 -33.2\r\nv 208.14 41.14 -32.67\r\nv 209.1 39.72 -37.42\r\nv 208.79 39.77 -37.76\r\nv 208.85 41.73 -37.23\r\nv 209.17 41.68 -36.9\r\nv 207.39 35.65 -34.83\r\nv 207.45 37.38 -34.33\r\nv 207.95 35.54 -34.23\r\nv 208.01 37.28 -33.73\r\nv 208.98 36.15 -38.44\r\nv 208.67 36.21 -38.77\r\nv 208.72 37.92 -38.28\r\nv 209.04 37.87 -37.94\r\nv 207.29 32.7 -35.73\r\nv 207.34 34.08 -35.31\r\nv 207.85 32.58 -35.14\r\nv 207.9 33.97 -34.71\r\nv 208.88 33.25 -39.33\r\nv 208.57 33.31 -39.66\r\nv 208.62 34.66 -39.24\r\nv 208.93 34.6 -38.91\r\nv 207.21 30.57 -36.44\r\nv 207.25 31.54 -36.11\r\nv 207.77 30.44 -35.85\r\nv 207.81 31.42 -35.51\r\nv 208.81 31.18 -40.01\r\nv 208.49 31.25 -40.34\r\nv 208.53 32.18 -40.03\r\nv 208.84 32.11 -39.69\r\nv 207.14 29.09 -37.01\r\nv 207.18 29.76 -36.74\r\nv 207.7 28.93 -36.43\r\nv 207.74 29.62 -36.15\r\nv 208.74 29.81 -40.54\r\nv 208.43 29.9 -40.86\r\nv 208.46 30.5 -40.62\r\nv 208.77 30.42 -40.29\r\nv 207.07 28.04 -37.52\r\nv 207.1 28.52 -37.27\r\nv 207.62 27.83 -36.97\r\nv 207.66 28.34 -36.7\r\nv 208.68 28.94 -40.96\r\nv 208.37 29.05 -41.27\r\nv 208.4 29.43 -41.07\r\nv 208.71 29.33 -40.76\r\nv 206.98 27.23 -38.04\r\nv 207.03 27.62 -37.78\r\nv 207.53 26.98 -37.52\r\nv 207.58 27.39 -37.24\r\nv 208.61 28.3 -41.37\r\nv 208.31 28.44 -41.66\r\nv 208.34 28.73 -41.47\r\nv 208.65 28.6 -41.16\r\nv 206.87 26.48 -38.64\r\nv 206.93 26.86 -38.33\r\nv 207.42 26.19 -38.14\r\nv 207.48 26.59 -37.82\r\nv 208.53 27.71 -41.84\r\nv 208.22 27.87 -42.12\r\nv 208.27 28.16 -41.88\r\nv 208.57 28.01 -41.59\r\nv 206.74 25.72 -39.37\r\nv 206.81 26.11 -38.99\r\nv 207.28 25.4 -38.91\r\nv 207.36 25.8 -38.51\r\nv 208.42 27.09 -42.43\r\nv 208.12 27.27 -42.69\r\nv 208.18 27.58 -42.39\r\nv 208.48 27.41 -42.12\r\nv 206.58 24.9 -40.29\r\nv 206.67 25.32 -39.8\r\nv 207.12 24.55 -39.85\r\nv 207.21 24.98 -39.35\r\nv 208.29 26.39 -43.22\r\nv 207.99 26.58 -43.46\r\nv 208.06 26.94 -43.05\r\nv 208.36 26.76 -42.8\r\nv 206.39 23.97 -41.44\r\nv 206.49 24.45 -40.83\r\nv 206.93 23.61 -41.02\r\nv 207.03 24.09 -40.4\r\nv 208.12 25.54 -44.27\r\nv 207.82 25.75 -44.5\r\nv 207.91 26.18 -43.95\r\nv 208.21 25.99 -43.71\r\nv 206.17 22.89 -42.87\r\nv 206.29 23.44 -42.12\r\nv 206.7 22.52 -42.46\r\nv 206.82 23.08 -41.7\r\nv 207.91 24.53 -45.61\r\nv 207.61 24.74 -45.84\r\nv 207.72 25.26 -45.13\r\nv 208.01 25.06 -44.9\r\nv 205.92 21.69 -44.57\r\nv 206.05 22.3 -43.69\r\nv 206.45 21.3 -44.18\r\nv 206.58 21.92 -43.29\r\nv 207.67 23.39 -47.23\r\nv 207.37 23.61 -47.44\r\nv 207.49 24.19 -46.61\r\nv 207.79 23.98 -46.39\r\nv 205.64 20.41 -46.52\r\nv 205.78 21.06 -45.52\r\nv 206.17 20.02 -46.15\r\nv 206.31 20.67 -45.14\r\nv 207.4 22.18 -49.07\r\nv 207.11 22.4 -49.28\r\nv 207.24 23.01 -48.33\r\nv 207.54 22.8 -48.12\r\nv 205.35 19.11 -48.67\r\nv 205.5 19.76 -47.57\r\nv 205.88 18.7 -48.32\r\nv 206.03 19.36 -47.21\r\nv 207.13 20.95 -51.12\r\nv 206.84 21.17 -51.31\r\nv 206.98 21.79 -50.27\r\nv 207.27 21.57 -50.07\r\nv 205.39 18.17 -50.93\r\nv 205.21 18.46 -49.81\r\nv 205.73 18.06 -49.46\r\nv 206.59 19.68 -52.8\r\nv 206.3 19.91 -52.99\r\nv 206.7 20.56 -52.41\r\nv 206.99 20.33 -52.21\r\nv 205.39 19.21 -52.52\r\nv 205.68 18.98 -52.33\r\nv 202.3 3.12 131.64\r\nv 201.85 1.58 132.26\r\nv 201.24 0.46 132.35\r\nv 203.32 6.56 130.27\r\nv 202.79 4.78 130.97\r\nv 204.45 10.36 128.81\r\nv 203.88 8.43 129.55\r\nv 205.61 14.25 127.36\r\nv 205.04 12.32 128.08\r\nv 206.7 17.89 126.03\r\nv 206.17 16.12 126.67\r\nv 207.6 20.95 124.95\r\nv 207.18 19.51 125.45\r\nv 208.25 23.17 124.19\r\nv 207.95 22.16 124.53\r\nv 208.68 24.7 123.7\r\nv 208.49 24.01 123.92\r\nv 208.96 25.79 123.41\r\nv 208.84 25.29 123.54\r\nv 209.16 26.66 123.22\r\nv 209.07 26.23 123.31\r\nv 209.33 27.54 123.08\r\nv 209.25 27.08 123.15\r\nv 209.5 28.64 122.95\r\nv 209.42 28.05 123.01\r\nv 209.7 30.2 122.82\r\nv 209.6 29.35 122.89\r\nv 209.95 32.41 122.67\r\nv 209.82 31.21 122.75\r\nv 210.26 35.44 122.49\r\nv 210.1 33.83 122.58\r\nv 210.63 39.13 122.28\r\nv 210.44 37.22 122.39\r\nv 211.01 43.25 122.06\r\nv 210.82 41.15 122.17\r\nv 211.4 47.57 121.83\r\nv 211.21 45.4 121.95\r\nv 211.77 51.9 121.61\r\nv 211.59 49.74 121.72\r\nv 212.14 56.19 121.35\r\nv 211.96 54.05 121.48\r\nv 212.54 60.44 121.04\r\nv 212.34 58.32 121.2\r\nv 212.98 64.64 120.64\r\nv 212.75 62.55 120.85\r\nv 213.5 68.78 120.11\r\nv 213.23 66.72 120.39\r\nv 214.08 72.85 119.44\r\nv 213.78 70.82 119.8\r\nv 214.73 76.84 118.61\r\nv 214.4 74.86 119.05\r\nv 215.45 80.75 117.58\r\nv 215.08 78.81 118.12\r\nv 216.23 84.55 116.33\r\nv 215.83 82.66 116.98\r\nv 217.05 88.23 114.88\r\nv 216.63 86.41 115.63\r\nv 217.89 91.73 113.24\r\nv 217.47 90 114.09\r\nv 218.75 95.03 111.42\r\nv 218.32 93.41 112.35\r\nv 219.59 98.08 109.42\r\nv 219.17 96.59 110.44\r\nv 220.36 100.83 107.25\r\nv 219.99 99.5 108.36\r\nv 221.02 103.18 104.91\r\nv 220.71 102.08 106.1\r\nv 219.55 107.84 95.16\r\nv 220.09 107.37 96.95\r\nv 220.56 106.81 98.7\r\nv 218.44 108.66 91.41\r\nv 219 108.27 93.31\r\nv 217.32 109.36 87.44\r\nv 217.87 109.02 89.45\r\nv 216.26 109.96 83.36\r\nv 216.78 109.67 85.42\r\nv 215.29 110.48 79.21\r\nv 215.76 110.23 81.29\r\nv 214.42 110.92 75.04\r\nv 214.84 110.71 77.13\r\nv 213.65 111.29 70.91\r\nv 214.02 111.11 72.97\r\nv 213.01 111.59 66.87\r\nv 213.32 111.45 68.88\r\nv 212.48 111.84 62.97\r\nv 212.73 111.72 64.9\r\nv 212.05 112.03 59.25\r\nv 212.25 111.94 61.08\r\nv 211.71 112.17 55.77\r\nv 211.87 112.1 57.48\r\nv 211.46 112.28 52.57\r\nv 211.57 112.23 54.14\r\nv 211.28 112.35 49.57\r\nv 211.36 112.32 51.05\r\nv 211.16 112.39 46.67\r\nv 211.21 112.37 48.11\r\nv 211.11 112.39 43.8\r\nv 211.13 112.39 45.24\r\nv 211.1 112.37 40.86\r\nv 211.1 112.39 42.34\r\nv 211.16 112.33 37.85\r\nv 211.12 112.35 39.37\r\nv 211.27 112.25 34.77\r\nv 211.21 112.29 36.32\r\nv 211.44 112.14 31.6\r\nv 211.35 112.2 33.19\r\nv 211.68 112 28.36\r\nv 211.55 112.07 29.99\r\nv 211.99 111.82 25.02\r\nv 211.82 111.91 26.7\r\nv 212.39 111.58 21.58\r\nv 212.18 111.71 23.32\r\nv 212.9 111.3 18.03\r\nv 212.63 111.45 19.82\r\nv 213.53 110.94 14.36\r\nv 213.2 111.13 16.21\r\nv 214.27 110.51 10.58\r\nv 213.88 110.73 12.48\r\nv 215.14 109.98 6.7\r\nv 214.69 110.26 8.65\r\nv 216.14 109.35 2.75\r\nv 215.62 109.68 4.74\r\nv 217.26 108.61 -1.25\r\nv 216.69 109 0.75\r\nv 218.53 107.59 -5.35\r\nv 217.84 108.18 -3.23\r\nv 219.42 106.68 -7.82\r\nv 200.28 0.05 131.41\r\nv 260.94 113.17 -20.53\r\nv 263.22 113.25 -20.59\r\nv 263.24 112.67 -20.15\r\nv 265.5 113.28 -20.6\r\nv 265.5 112.69 -20.17\r\nv 256.36 112.85 -20.32\r\nv 258.65 113.04 -20.44\r\nv 251.77 112.33 -19.97\r\nv 254.07 112.61 -20.16\r\nv 247.15 111.61 -19.49\r\nv 249.46 111.99 -19.74\r\nv 242.51 110.72 -18.88\r\nv 244.83 111.19 -19.2\r\nv 237.92 109.69 -18.17\r\nv 240.2 110.22 -18.54\r\nv 233.48 108.54 -17.36\r\nv 235.68 109.12 -17.77\r\nv 229.26 107.32 -16.47\r\nv 231.34 107.94 -16.92\r\nv 225.35 106.06 -15.52\r\nv 227.26 106.69 -16\r\nv 222.02 104.88 -14.57\r\nv 223.55 105.44 -15.02\r\nv 220.29 104.05 -14.17\r\nv 220.95 104.45 -14.21\r\nv 219.87 102.85 -15.54\r\nv 220.02 103.56 -14.67\r\nv 219.17 100.54 -17.82\r\nv 219.57 101.8 -16.64\r\nv 218.3 97.78 -19.99\r\nv 218.75 99.2 -18.93\r\nv 217.34 94.73 -21.97\r\nv 217.83 96.29 -21.01\r\nv 216.38 91.45 -23.77\r\nv 216.86 93.12 -22.9\r\nv 215.42 87.97 -25.39\r\nv 215.89 89.74 -24.6\r\nv 214.5 84.33 -26.83\r\nv 214.95 86.17 -26.14\r\nv 213.65 80.56 -28.1\r\nv 214.06 82.46 -27.49\r\nv 212.88 76.69 -29.2\r\nv 213.25 78.64 -28.67\r\nv 212.2 72.75 -30.16\r\nv 212.53 74.73 -29.7\r\nv 211.62 68.73 -31\r\nv 211.9 70.75 -30.59\r\nv 211.13 64.64 -31.76\r\nv 211.36 66.69 -31.39\r\nv 210.74 60.49 -32.46\r\nv 210.93 62.57 -32.12\r\nv 210.44 56.3 -33.17\r\nv 210.58 58.4 -32.81\r\nv 210.19 52.08 -33.91\r\nv 210.31 54.19 -33.53\r\nv 210 47.84 -34.76\r\nv 210.09 49.96 -34.32\r\nv 209.83 43.62 -35.75\r\nv 209.91 45.72 -35.24\r\nv 209.69 39.62 -36.8\r\nv 209.75 41.58 -36.27\r\nv 209.57 36.04 -37.82\r\nv 209.62 37.76 -37.32\r\nv 209.47 33.13 -38.71\r\nv 209.52 34.49 -38.28\r\nv 209.39 31.04 -39.4\r\nv 209.43 31.99 -39.08\r\nv 209.33 29.65 -39.93\r\nv 209.36 30.27 -39.68\r\nv 209.26 28.73 -40.38\r\nv 209.29 29.14 -40.16\r\nv 209.19 28.05 -40.82\r\nv 209.23 28.37 -40.6\r\nv 209.1 27.41 -41.32\r\nv 209.15 27.73 -41.06\r\nv 208.99 26.76 -41.95\r\nv 209.05 27.09 -41.62\r\nv 208.85 26.03 -42.76\r\nv 208.92 26.41 -42.33\r\nv 208.67 25.17 -43.83\r\nv 208.76 25.62 -43.26\r\nv 208.46 24.14 -45.19\r\nv 208.57 24.68 -44.47\r\nv 208.22 23 -46.82\r\nv 208.34 23.58 -45.97\r\nv 207.95 21.77 -48.68\r\nv 208.09 22.39 -47.72\r\nv 207.68 20.53 -50.75\r\nv 207.82 21.15 -49.69\r\nv 207.13 19.25 -52.45\r\nv 207.53 19.9 -51.85\r\nv 206.22 18.55 -51.97\r\nv 200.76 2.7 127.86\r\nv 200.31 1.15 128.49\r\nv 201.78 6.15 126.49\r\nv 201.25 4.37 127.19\r\nv 202.93 9.98 125.02\r\nv 202.35 8.04 125.76\r\nv 204.09 13.89 123.56\r\nv 203.51 11.95 124.28\r\nv 205.18 17.54 122.23\r\nv 204.65 15.77 122.87\r\nv 206.09 20.62 121.14\r\nv 205.66 19.17 121.65\r\nv 206.74 22.86 120.37\r\nv 206.45 21.84 120.72\r\nv 207.19 24.45 119.87\r\nv 206.99 23.72 120.09\r\nv 207.49 25.6 119.56\r\nv 207.35 25.06 119.69\r\nv 207.7 26.52 119.36\r\nv 207.6 26.07 119.45\r\nv 207.88 27.45 119.21\r\nv 207.79 26.97 119.28\r\nv 208.06 28.6 119.07\r\nv 207.97 27.99 119.14\r\nv 208.26 30.18 118.94\r\nv 208.16 29.32 119.01\r\nv 208.51 32.4 118.79\r\nv 208.38 31.2 118.87\r\nv 208.82 35.44 118.61\r\nv 208.66 33.83 118.7\r\nv 209.19 39.13 118.4\r\nv 209 37.22 118.51\r\nv 209.57 43.25 118.18\r\nv 209.38 41.15 118.29\r\nv 209.96 47.56 117.95\r\nv 209.77 45.39 118.07\r\nv 210.33 51.88 117.73\r\nv 210.15 49.73 117.84\r\nv 210.7 56.16 117.47\r\nv 210.51 54.03 117.61\r\nv 211.09 60.39 117.16\r\nv 210.89 58.28 117.33\r\nv 211.53 64.55 116.76\r\nv 211.31 62.48 116.98\r\nv 212.04 68.64 116.24\r\nv 211.78 66.61 116.52\r\nv 212.62 72.65 115.59\r\nv 212.32 70.66 115.93\r\nv 213.26 76.57 114.76\r\nv 212.93 74.63 115.2\r\nv 213.96 80.39 113.75\r\nv 213.6 78.5 114.28\r\nv 214.72 84.11 112.54\r\nv 214.33 82.27 113.17\r\nv 215.51 87.68 111.14\r\nv 215.11 85.91 111.86\r\nv 216.33 91.07 109.55\r\nv 215.92 89.4 110.36\r\nv 217.15 94.24 107.79\r\nv 216.75 92.68 108.69\r\nv 217.95 97.13 105.9\r\nv 217.56 95.72 106.86\r\nv 218.67 99.67 103.91\r\nv 218.32 98.45 104.91\r\nv 219.21 101.71 101.91\r\nv 218.97 100.76 102.91\r\nv 219.34 103.21 99.85\r\nv 219.34 102.52 100.91\r\nv 218.9 104.28 97.46\r\nv 219.19 103.78 98.72\r\nv 218.04 105.14 94.49\r\nv 218.51 104.73 96.05\r\nv 217 105.9 90.98\r\nv 217.53 105.53 92.79\r\nv 215.91 106.57 87.15\r\nv 216.45 106.25 89.1\r\nv 214.88 107.16 83.14\r\nv 215.39 106.87 85.16\r\nv 213.92 107.67 79.04\r\nv 214.39 107.42 81.1\r\nv 213.05 108.11 74.92\r\nv 213.47 107.9 76.98\r\nv 212.3 108.48 70.82\r\nv 212.66 108.3 72.86\r\nv 211.66 108.78 66.8\r\nv 211.96 108.64 68.79\r\nv 211.13 109.02 62.91\r\nv 211.38 108.91 64.84\r\nv 210.7 109.21 59.21\r\nv 210.9 109.12 61.04\r\nv 210.36 109.36 55.75\r\nv 210.52 109.29 57.45\r\nv 210.11 109.46 52.56\r\nv 210.23 109.41 54.12\r\nv 209.93 109.53 49.57\r\nv 210.01 109.5 51.04\r\nv 209.82 109.57 46.69\r\nv 209.87 109.55 48.12\r\nv 209.76 109.58 43.83\r\nv 209.78 109.58 45.26\r\nv 209.76 109.56 40.91\r\nv 209.75 109.57 42.38\r\nv 209.81 109.51 37.91\r\nv 209.78 109.54 39.42\r\nv 209.92 109.43 34.84\r\nv 209.86 109.47 36.38\r\nv 210.09 109.32 31.69\r\nv 210 109.38 33.27\r\nv 210.33 109.18 28.45\r\nv 210.2 109.26 30.08\r\nv 210.64 109 25.14\r\nv 210.47 109.1 26.81\r\nv 211.04 108.77 21.72\r\nv 210.83 108.89 23.44\r\nv 211.55 108.49 18.2\r\nv 211.28 108.64 19.97\r\nv 212.16 108.13 14.56\r\nv 211.84 108.32 16.39\r\nv 212.9 107.7 10.82\r\nv 212.52 107.93 12.7\r\nv 213.76 107.19 7\r\nv 213.31 107.46 8.92\r\nv 214.74 106.57 3.12\r\nv 214.23 106.89 5.07\r\nv 215.83 105.85 -0.77\r\nv 215.28 106.22 1.16\r\nv 216.99 104.99 -4.61\r\nv 216.38 105.44 -2.65\r\nv 218.26 103.73 -8.88\r\nv 217.71 104.46 -6.76\r\nv 261 113.68 -16.32\r\nv 263.25 113.77 -16.38\r\nv 263.25 113.53 -16.77\r\nv 265.5 113.55 -16.79\r\nv 265.5 113.79 -16.4\r\nv 256.47 113.37 -16.11\r\nv 258.74 113.55 -16.23\r\nv 251.93 112.85 -15.76\r\nv 254.2 113.13 -15.95\r\nv 247.35 112.14 -15.29\r\nv 249.64 112.52 -15.54\r\nv 242.76 111.26 -14.69\r\nv 245.05 111.72 -15\r\nv 238.22 110.23 -13.98\r\nv 240.48 110.76 -14.35\r\nv 233.82 109.1 -13.18\r\nv 236 109.68 -13.59\r\nv 229.65 107.9 -12.31\r\nv 231.7 108.5 -12.75\r\nv 225.63 106.59 -11.31\r\nv 227.69 107.28 -11.84\r\nv 223.33 105.76 -10.63\r\nv 218.18 101.52 -12.22\r\nv 218.36 102.68 -10.71\r\nv 217.56 99.36 -14.42\r\nv 217.91 100.45 -13.4\r\nv 216.76 96.83 -16.4\r\nv 217.18 98.15 -15.42\r\nv 215.86 93.95 -18.28\r\nv 216.32 95.43 -17.36\r\nv 214.93 90.8 -20.01\r\nv 215.39 92.4 -19.16\r\nv 214 87.42 -21.58\r\nv 214.46 89.13 -20.81\r\nv 213.11 83.87 -22.98\r\nv 213.55 85.66 -22.3\r\nv 212.27 80.17 -24.23\r\nv 212.68 82.03 -23.63\r\nv 211.51 76.37 -25.31\r\nv 211.88 78.28 -24.79\r\nv 210.84 72.46 -26.26\r\nv 211.16 74.43 -25.8\r\nv 210.26 68.47 -27.09\r\nv 210.54 70.48 -26.69\r\nv 209.78 64.4 -27.85\r\nv 210.01 66.44 -27.48\r\nv 209.39 60.24 -28.55\r\nv 209.57 62.33 -28.2\r\nv 209.08 56.02 -29.26\r\nv 209.23 58.14 -28.9\r\nv 208.84 51.76 -30.01\r\nv 208.95 53.9 -29.63\r\nv 208.63 47.45 -30.88\r\nv 208.73 49.61 -30.43\r\nv 208.46 43.17 -31.88\r\nv 208.55 45.3 -31.36\r\nv 208.32 39.11 -32.94\r\nv 208.39 41.1 -32.41\r\nv 208.2 35.49 -33.97\r\nv 208.26 37.23 -33.47\r\nv 208.1 32.53 -34.88\r\nv 208.15 33.92 -34.44\r\nv 208.02 30.38 -35.59\r\nv 208.06 31.36 -35.25\r\nv 207.95 28.86 -36.18\r\nv 207.98 29.56 -35.89\r\nv 207.87 27.75 -36.72\r\nv 207.91 28.26 -36.45\r\nv 207.78 26.87 -37.29\r\nv 207.82 27.29 -37\r\nv 207.66 26.07 -37.93\r\nv 207.72 26.47 -37.59\r\nv 207.52 25.26 -38.7\r\nv 207.6 25.67 -38.29\r\nv 207.36 24.4 -39.66\r\nv 207.44 24.84 -39.16\r\nv 207.16 23.45 -40.83\r\nv 207.26 23.94 -40.21\r\nv 206.94 22.35 -42.29\r\nv 207.05 22.92 -41.52\r\nv 206.68 21.14 -44.01\r\nv 206.81 21.76 -43.12\r\nv 206.4 19.84 -45.98\r\nv 206.54 20.5 -44.97\r\nv 206.11 18.52 -48.16\r\nv 206.26 19.18 -47.05\r\nv 205.96 17.88 -49.31\r\nv 220.16 106.75 101.15\r\nv 220.38 105.93 102.78\r\nv 220.65 105.7 102.53\r\nv 220.43 106.54 100.89\r\nv 220.38 104.95 104.28\r\nv 220.67 104.66 104.05\r\nv 221.17 105.25 102.06\r\nv 220.93 106.14 100.4\r\nv 221.64 104.92 101.55\r\nv 221.42 105.75 99.93\r\nv 221.26 104.04 103.66\r\nv 221.68 103.89 103.05\r\nv 221.86 103.8 102.8\r\nv 221.85 104.77 101.33\r\nv 221.64 105.58 99.73\r\nv 263.18 115.06 -19.63\r\nv 265.5 115.09 -19.64\r\nv 263.18 115.52 -18.25\r\nv 265.5 115.55 -18.27\r\nv 263.18 115.55 -17.7\r\nv 265.5 115.58 -17.72\r\nv 263.26 112.99 -18.1\r\nv 265.5 113.02 -18.12\r\nv 263.26 112.51 -19.46\r\nv 265.5 112.53 -19.48\r\nv 263.25 112.41 -19.96\r\nv 265.5 112.43 -19.98\r\nv 330.38 0.32 129.42\r\nv 329.81 0.05 130.33\r\nv 329.47 1.16 129.85\r\nv 330.35 1.13 128.78\r\nv 329.11 0.37 131.04\r\nv 328.64 1.2 130.97\r\nv 328.19 2.74 130.35\r\nv 329.02 2.7 129.23\r\nv 329.89 2.68 128.16\r\nv 329.02 0.11 131.49\r\nv 328.41 1.24 131.41\r\nv 329.24 0.22 131.75\r\nv 328.64 1.34 131.67\r\nv 328.19 2.88 131.04\r\nv 327.96 2.78 130.78\r\nv 330.8 1.16 133\r\nv 331.55 0.86 132.35\r\nv 331.27 2 131.94\r\nv 330.37 2.01 132.99\r\nv 332.07 1.11 131.38\r\nv 332.08 1.95 130.8\r\nv 331.63 3.49 130.18\r\nv 330.82 3.54 131.32\r\nv 329.92 3.55 132.37\r\nv 332.28 0.77 131.11\r\nv 332.25 1.88 130.3\r\nv 332 0.64 130.78\r\nv 331.96 1.75 129.96\r\nv 331.51 3.29 129.34\r\nv 331.79 3.43 129.67\r\nv 328.53 4.37 128.56\r\nv 329.4 4.35 127.49\r\nv 327.7 4.4 129.68\r\nv 327.17 6.18 128.98\r\nv 328 6.14 127.86\r\nv 328.87 6.13 126.78\r\nv 327.47 4.44 130.12\r\nv 327.7 4.55 130.38\r\nv 327.17 6.32 129.68\r\nv 326.95 6.21 129.41\r\nv 330.33 5.21 130.65\r\nv 329.43 5.22 131.7\r\nv 331.14 5.16 129.51\r\nv 330.61 6.94 128.8\r\nv 329.8 6.98 129.95\r\nv 328.91 6.99 131\r\nv 331.3 5.1 129\r\nv 331.01 4.96 128.67\r\nv 330.49 6.74 127.96\r\nv 330.77 6.88 128.3\r\nv 327.44 8.03 127.13\r\nv 328.31 8.02 126.05\r\nv 326.61 8.05 128.25\r\nv 326.03 9.99 127.51\r\nv 326.86 9.97 126.39\r\nv 327.73 9.96 125.31\r\nv 326.39 8.09 128.69\r\nv 326.61 8.19 128.95\r\nv 326.03 10.13 128.21\r\nv 325.81 10.03 127.95\r\nv 329.24 8.86 129.22\r\nv 328.35 8.86 130.27\r\nv 330.05 8.82 128.08\r\nv 329.47 10.76 127.34\r\nv 328.66 10.79 128.48\r\nv 327.77 10.79 129.54\r\nv 330.21 8.76 127.57\r\nv 329.92 8.63 127.24\r\nv 329.35 10.57 126.5\r\nv 329.63 10.7 126.83\r\nv 326.28 11.93 125.65\r\nv 327.15 11.92 124.58\r\nv 325.45 11.95 126.78\r\nv 324.87 13.89 126.06\r\nv 325.7 13.87 124.93\r\nv 326.57 13.87 123.86\r\nv 325.23 11.98 127.22\r\nv 325.45 12.09 127.48\r\nv 324.87 14.02 126.76\r\nv 324.65 13.92 126.5\r\nv 328.08 12.75 127.75\r\nv 327.19 12.74 128.8\r\nv 328.89 12.72 126.6\r\nv 328.31 14.65 125.88\r\nv 327.5 14.68 127.03\r\nv 326.61 14.68 128.09\r\nv 329.05 12.66 126.09\r\nv 328.76 12.53 125.76\r\nv 328.18 14.47 125.04\r\nv 328.47 14.6 125.38\r\nv 325.14 15.74 124.25\r\nv 326.01 15.74 123.17\r\nv 324.31 15.76 125.37\r\nv 323.79 17.53 124.73\r\nv 324.61 17.52 123.61\r\nv 325.48 17.52 122.53\r\nv 324.09 15.79 125.81\r\nv 324.32 15.89 126.08\r\nv 323.79 17.66 125.44\r\nv 323.56 17.56 125.17\r\nv 326.94 16.56 126.35\r\nv 326.06 16.55 127.4\r\nv 327.75 16.53 125.2\r\nv 327.23 18.3 124.56\r\nv 326.42 18.32 125.71\r\nv 325.53 18.31 126.77\r\nv 327.91 16.47 124.69\r\nv 327.63 16.34 124.36\r\nv 327.1 18.11 123.71\r\nv 327.39 18.24 124.05\r\nv 324.13 19.14 123.03\r\nv 325 19.15 121.94\r\nv 323.3 19.16 124.15\r\nv 322.88 20.6 123.65\r\nv 323.7 20.59 122.52\r\nv 324.57 20.59 121.44\r\nv 323.08 19.19 124.59\r\nv 323.31 19.29 124.85\r\nv 322.88 20.73 124.35\r\nv 322.66 20.63 124.08\r\nv 325.94 19.94 125.13\r\nv 325.05 19.93 126.19\r\nv 326.74 19.92 123.98\r\nv 326.32 21.36 123.47\r\nv 325.52 21.38 124.62\r\nv 324.63 21.36 125.68\r\nv 326.9 19.87 123.47\r\nv 326.62 19.74 123.13\r\nv 326.19 21.18 122.63\r\nv 326.48 21.3 122.96\r\nv 323.35 21.81 122.1\r\nv 324.21 21.81 121.01\r\nv 322.52 21.81 123.22\r\nv 322.23 22.83 122.88\r\nv 323.05 22.83 121.75\r\nv 323.91 22.84 120.67\r\nv 322.3 21.84 123.66\r\nv 322.53 21.94 123.93\r\nv 322.23 22.95 123.59\r\nv 322.01 22.86 123.32\r\nv 325.16 22.58 124.21\r\nv 324.28 22.57 125.27\r\nv 325.97 22.57 123.05\r\nv 325.67 23.57 122.71\r\nv 324.87 23.59 123.87\r\nv 323.99 23.57 124.93\r\nv 326.12 22.51 122.54\r\nv 325.84 22.39 122.21\r\nv 325.54 23.4 121.87\r\nv 325.83 23.52 122.2\r\nv 322.81 23.68 121.47\r\nv 323.67 23.7 120.39\r\nv 321.98 23.68 122.61\r\nv 321.79 24.4 122.39\r\nv 322.61 24.4 121.25\r\nv 323.47 24.42 120.17\r\nv 321.76 23.71 123.05\r\nv 321.99 23.8 123.31\r\nv 321.8 24.5 123.09\r\nv 321.57 24.42 122.83\r\nv 324.63 24.42 123.6\r\nv 323.75 24.4 124.66\r\nv 325.44 24.41 122.44\r\nv 325.24 25.1 122.23\r\nv 324.44 25.1 123.39\r\nv 323.56 25.08 124.45\r\nv 325.59 24.36 121.93\r\nv 325.3 24.24 121.59\r\nv 325.11 24.94 121.38\r\nv 325.4 25.06 121.72\r\nv 322.44 25.01 121.08\r\nv 323.31 25.03 119.99\r\nv 321.63 25 122.21\r\nv 321.49 25.52 122.08\r\nv 322.31 25.54 120.94\r\nv 323.17 25.57 119.86\r\nv 321.41 25.02 122.66\r\nv 321.64 25.1 122.93\r\nv 321.51 25.61 122.79\r\nv 321.28 25.54 122.52\r\nv 324.29 25.67 123.23\r\nv 323.41 25.64 124.29\r\nv 325.09 25.67 122.07\r\nv 324.97 26.16 121.94\r\nv 324.17 26.15 123.1\r\nv 323.29 26.12 124.17\r\nv 325.25 25.64 121.55\r\nv 324.95 25.53 121.21\r\nv 324.83 26.03 121.08\r\nv 325.12 26.13 121.42\r\nv 322.2 26.01 120.84\r\nv 323.06 26.05 119.75\r\nv 321.38 25.99 121.97\r\nv 321.29 26.43 121.89\r\nv 322.1 26.46 120.75\r\nv 322.96 26.5 119.66\r\nv 321.17 26 122.42\r\nv 321.4 26.07 122.69\r\nv 321.31 26.51 122.6\r\nv 321.07 26.44 122.33\r\nv 324.07 26.58 123.01\r\nv 323.19 26.54 124.07\r\nv 324.86 26.59 121.84\r\nv 324.78 27 121.76\r\nv 323.98 26.98 122.93\r\nv 323.1 26.94 123.99\r\nv 325.02 26.57 121.33\r\nv 324.72 26.47 120.98\r\nv 324.63 26.89 120.9\r\nv 324.93 26.97 121.25\r\nv 322.01 26.91 120.67\r\nv 322.87 26.95 119.58\r\nv 321.19 26.88 121.81\r\nv 321.11 27.36 121.73\r\nv 321.92 27.39 120.6\r\nv 322.78 27.43 119.51\r\nv 320.98 26.89 122.25\r\nv 321.22 26.95 122.53\r\nv 321.13 27.42 122.45\r\nv 320.89 27.36 122.18\r\nv 323.9 27.38 122.86\r\nv 323.02 27.34 123.92\r\nv 324.69 27.4 121.69\r\nv 324.61 27.84 121.63\r\nv 323.82 27.81 122.79\r\nv 322.94 27.77 123.86\r\nv 324.85 27.38 121.17\r\nv 324.54 27.31 120.83\r\nv 324.46 27.75 120.76\r\nv 324.76 27.82 121.11\r\nv 321.83 27.92 120.53\r\nv 322.69 27.96 119.44\r\nv 321.02 27.89 121.67\r\nv 320.93 28.51 121.6\r\nv 321.74 28.54 120.46\r\nv 322.6 28.58 119.37\r\nv 320.8 27.89 122.11\r\nv 321.04 27.94 122.39\r\nv 320.95 28.55 122.32\r\nv 320.71 28.51 122.05\r\nv 323.74 28.3 122.73\r\nv 322.86 28.26 123.79\r\nv 324.53 28.33 121.56\r\nv 324.45 28.9 121.5\r\nv 323.65 28.87 122.67\r\nv 322.78 28.83 123.73\r\nv 324.68 28.31 121.04\r\nv 324.38 28.25 120.69\r\nv 324.29 28.84 120.63\r\nv 324.6 28.89 120.98\r\nv 321.64 29.26 120.39\r\nv 322.5 29.3 119.3\r\nv 320.83 29.23 121.53\r\nv 320.72 30.09 121.47\r\nv 321.54 30.12 120.33\r\nv 322.4 30.16 119.24\r\nv 320.62 29.23 121.98\r\nv 320.86 29.27 122.26\r\nv 320.75 30.12 122.19\r\nv 320.51 30.09 121.91\r\nv 323.56 29.56 122.6\r\nv 322.68 29.52 123.67\r\nv 324.36 29.59 121.44\r\nv 324.25 30.42 121.37\r\nv 323.46 30.39 122.54\r\nv 322.58 30.35 123.6\r\nv 324.5 29.58 120.92\r\nv 324.2 29.53 120.57\r\nv 324.1 30.37 120.5\r\nv 324.4 30.41 120.85\r\nv 321.42 31.14 120.26\r\nv 322.28 31.18 119.17\r\nv 320.61 31.11 121.4\r\nv 320.48 32.32 121.32\r\nv 321.29 32.35 120.18\r\nv 322.15 32.39 119.09\r\nv 320.39 31.11 121.84\r\nv 320.63 31.14 122.12\r\nv 320.5 32.34 122.04\r\nv 320.26 32.31 121.76\r\nv 323.35 31.39 122.47\r\nv 322.47 31.35 123.53\r\nv 324.14 31.42 121.3\r\nv 324.01 32.61 121.23\r\nv 323.22 32.58 122.39\r\nv 322.34 32.54 123.46\r\nv 324.29 31.41 120.78\r\nv 323.98 31.37 120.43\r\nv 323.85 32.57 120.35\r\nv 324.16 32.61 120.71\r\nv 321.14 33.77 120.09\r\nv 322 33.81 119\r\nv 320.33 33.74 121.23\r\nv 320.16 35.36 121.14\r\nv 320.98 35.39 120\r\nv 321.83 35.42 118.91\r\nv 320.11 33.74 121.68\r\nv 320.35 33.77 121.95\r\nv 320.19 35.38 121.86\r\nv 319.95 35.36 121.58\r\nv 323.07 34 122.31\r\nv 322.19 33.96 123.37\r\nv 323.86 34.03 121.14\r\nv 323.7 35.64 121.05\r\nv 322.9 35.61 122.21\r\nv 322.02 35.57 123.28\r\nv 324.01 34.02 120.62\r\nv 323.7 33.99 120.27\r\nv 323.54 35.6 120.17\r\nv 323.85 35.63 120.53\r\nv 320.8 37.17 119.9\r\nv 321.66 37.2 118.81\r\nv 319.98 37.14 121.03\r\nv 319.8 39.05 120.93\r\nv 320.61 39.08 119.79\r\nv 321.47 39.11 118.7\r\nv 319.77 37.13 121.48\r\nv 320.01 37.16 121.76\r\nv 319.83 39.07 121.65\r\nv 319.58 39.05 121.38\r\nv 322.73 37.38 122.11\r\nv 321.85 37.34 123.18\r\nv 323.52 37.41 120.95\r\nv 323.34 39.31 120.84\r\nv 322.54 39.29 122.01\r\nv 321.66 39.25 123.07\r\nv 323.67 37.4 120.43\r\nv 323.36 37.37 120.07\r\nv 323.18 39.28 119.97\r\nv 323.48 39.31 120.32\r\nv 320.42 41.1 119.68\r\nv 321.28 41.13 118.59\r\nv 319.61 41.07 120.82\r\nv 319.41 43.17 120.71\r\nv 320.23 43.2 119.57\r\nv 321.08 43.23 118.48\r\nv 319.39 41.07 121.27\r\nv 319.63 41.09 121.54\r\nv 319.44 43.19 121.43\r\nv 319.2 43.17 121.15\r\nv 322.35 41.3 121.9\r\nv 321.47 41.27 122.96\r\nv 323.14 41.33 120.73\r\nv 322.95 43.42 120.62\r\nv 322.16 43.4 121.78\r\nv 321.28 43.36 122.85\r\nv 323.29 41.32 120.21\r\nv 322.99 41.29 119.86\r\nv 322.79 43.39 119.75\r\nv 323.1 43.42 120.1\r\nv 320.03 45.35 119.45\r\nv 320.89 45.38 118.36\r\nv 319.22 45.32 120.59\r\nv 319.02 47.49 120.48\r\nv 319.84 47.52 119.34\r\nv 320.7 47.55 118.25\r\nv 319 45.32 121.04\r\nv 319.24 45.34 121.32\r\nv 319.05 47.51 121.2\r\nv 318.81 47.49 120.93\r\nv 321.96 45.54 121.67\r\nv 321.08 45.51 122.74\r\nv 322.75 45.57 120.51\r\nv 322.56 47.73 120.39\r\nv 321.77 47.71 121.56\r\nv 320.89 47.68 122.62\r\nv 322.9 45.56 119.99\r\nv 322.6 45.53 119.63\r\nv 322.4 47.7 119.52\r\nv 322.71 47.73 119.87\r\nv 319.65 49.68 119.23\r\nv 320.51 49.71 118.14\r\nv 318.84 49.66 120.37\r\nv 318.65 51.82 120.25\r\nv 319.47 51.84 119.11\r\nv 320.33 51.87 118.02\r\nv 318.62 49.66 120.81\r\nv 318.86 49.68 121.09\r\nv 318.68 51.84 120.98\r\nv 318.44 51.82 120.7\r\nv 321.58 49.87 121.45\r\nv 320.7 49.84 122.51\r\nv 322.38 49.9 120.28\r\nv 322.19 52.05 120.17\r\nv 321.4 52.03 121.33\r\nv 320.52 52.01 122.39\r\nv 322.52 49.89 119.76\r\nv 322.22 49.86 119.41\r\nv 322.03 52.02 119.29\r\nv 322.34 52.05 119.64\r\nv 319.28 53.99 118.99\r\nv 320.14 54.01 117.9\r\nv 318.47 53.97 120.13\r\nv 318.28 56.1 120\r\nv 319.1 56.12 118.86\r\nv 319.96 56.15 117.77\r\nv 318.25 53.97 120.58\r\nv 318.5 53.99 120.85\r\nv 318.31 56.13 120.72\r\nv 318.07 56.11 120.44\r\nv 321.21 54.19 121.21\r\nv 320.33 54.16 122.27\r\nv 322.01 54.21 120.04\r\nv 321.82 56.35 119.91\r\nv 321.03 56.33 121.07\r\nv 320.14 56.31 122.14\r\nv 322.16 54.2 119.52\r\nv 321.85 54.17 119.17\r\nv 321.66 56.31 119.04\r\nv 321.97 56.34 119.39\r\nv 318.9 58.25 118.71\r\nv 319.76 58.27 117.62\r\nv 318.09 58.23 119.85\r\nv 317.89 60.34 119.69\r\nv 318.7 60.35 118.55\r\nv 319.56 60.37 117.46\r\nv 317.87 58.23 120.3\r\nv 318.12 58.26 120.57\r\nv 317.91 60.37 120.41\r\nv 317.67 60.34 120.13\r\nv 320.83 58.47 120.93\r\nv 319.95 58.45 121.99\r\nv 321.63 58.49 119.76\r\nv 321.42 60.61 119.6\r\nv 320.63 60.6 120.76\r\nv 319.75 60.58 121.82\r\nv 321.77 58.48 119.24\r\nv 321.47 58.44 118.89\r\nv 321.27 60.56 118.72\r\nv 321.57 60.6 119.08\r\nv 318.49 62.45 118.36\r\nv 319.35 62.47 117.27\r\nv 317.67 62.44 119.5\r\nv 317.45 64.52 119.29\r\nv 318.26 64.52 118.15\r\nv 319.12 64.54 117.06\r\nv 317.46 62.44 119.94\r\nv 317.7 62.47 120.22\r\nv 317.47 64.56 120.01\r\nv 317.23 64.52 119.73\r\nv 320.41 62.72 120.57\r\nv 319.53 62.7 121.64\r\nv 321.21 62.73 119.41\r\nv 320.98 64.83 119.19\r\nv 320.18 64.82 120.36\r\nv 319.3 64.8 121.42\r\nv 321.36 62.71 118.89\r\nv 321.05 62.67 118.54\r\nv 320.82 64.76 118.32\r\nv 321.13 64.81 118.67\r\nv 318.02 66.58 117.9\r\nv 318.88 66.59 116.82\r\nv 317.2 66.58 119.04\r\nv 316.94 68.62 118.77\r\nv 317.75 68.62 117.63\r\nv 318.61 68.63 116.54\r\nv 316.98 66.59 119.49\r\nv 317.22 66.63 119.76\r\nv 316.96 68.68 119.49\r\nv 316.72 68.63 119.21\r\nv 319.93 66.91 120.11\r\nv 319.05 66.9 121.17\r\nv 320.73 66.91 118.95\r\nv 320.46 68.98 118.67\r\nv 319.66 68.99 119.83\r\nv 318.78 68.97 120.89\r\nv 320.88 66.89 118.43\r\nv 320.57 66.84 118.08\r\nv 320.31 68.9 117.8\r\nv 320.61 68.96 118.15\r\nv 317.47 70.64 117.32\r\nv 318.33 70.65 116.23\r\nv 316.65 70.65 118.45\r\nv 316.36 72.65 118.1\r\nv 317.18 72.64 116.97\r\nv 318.04 72.64 115.88\r\nv 316.44 70.66 118.9\r\nv 316.68 70.71 119.17\r\nv 316.38 72.73 118.82\r\nv 316.14 72.67 118.55\r\nv 319.38 71.04 119.51\r\nv 318.5 71.03 120.57\r\nv 320.18 71.03 118.35\r\nv 319.87 73.07 118\r\nv 319.08 73.08 119.16\r\nv 318.19 73.08 120.22\r\nv 320.33 71.01 117.83\r\nv 320.02 70.94 117.48\r\nv 319.72 72.96 117.13\r\nv 320.03 73.03 117.48\r\nv 316.86 74.62 116.58\r\nv 317.73 74.61 115.49\r\nv 316.04 74.64 117.71\r\nv 315.71 76.6 117.27\r\nv 316.53 76.58 116.14\r\nv 317.4 76.56 115.06\r\nv 315.83 74.66 118.15\r\nv 316.06 74.72 118.43\r\nv 315.73 76.69 117.99\r\nv 315.49 76.63 117.72\r\nv 318.76 75.1 118.76\r\nv 317.87 75.1 119.82\r\nv 319.56 75.08 117.6\r\nv 319.22 77.07 117.16\r\nv 318.42 77.1 118.31\r\nv 317.54 77.11 119.37\r\nv 319.71 75.04 117.08\r\nv 319.41 74.96 116.74\r\nv 319.08 76.94 116.3\r\nv 319.38 77.02 116.64\r\nv 316.19 78.51 115.66\r\nv 317.05 78.49 114.58\r\nv 315.37 78.55 116.79\r\nv 315 80.46 116.26\r\nv 315.83 80.42 115.13\r\nv 316.7 80.39 114.05\r\nv 315.15 78.57 117.23\r\nv 315.38 78.64 117.5\r\nv 315.02 80.57 116.96\r\nv 314.78 80.5 116.7\r\nv 318.07 79.07 117.82\r\nv 317.18 79.09 118.88\r\nv 318.87 79.03 116.67\r\nv 318.5 80.97 116.12\r\nv 317.7 81.03 117.28\r\nv 316.81 81.05 118.33\r\nv 319.03 78.98 116.15\r\nv 318.73 78.89 115.81\r\nv 318.36 80.82 115.27\r\nv 318.66 80.92 115.61\r\nv 315.45 82.3 114.54\r\nv 316.32 82.26 113.47\r\nv 314.63 82.36 115.67\r\nv 314.24 84.22 115.03\r\nv 315.07 84.15 113.91\r\nv 315.94 84.1 112.84\r\nv 314.41 82.4 116.11\r\nv 314.64 82.48 116.37\r\nv 314.25 84.35 115.73\r\nv 314.01 84.27 115.46\r\nv 317.32 82.96 116.68\r\nv 316.43 82.99 117.73\r\nv 318.12 82.89 115.53\r\nv 317.73 84.78 114.88\r\nv 316.92 84.86 116.03\r\nv 316.03 84.9 117.07\r\nv 318.28 82.83 115.02\r\nv 317.98 82.73 114.68\r\nv 317.59 84.6 114.04\r\nv 317.89 84.71 114.37\r\nv 314.67 85.97 113.22\r\nv 315.54 85.91 112.16\r\nv 313.84 86.05 114.34\r\nv 313.43 87.85 113.6\r\nv 314.26 87.75 112.49\r\nv 315.14 87.68 111.43\r\nv 313.61 86.1 114.77\r\nv 313.84 86.2 115.03\r\nv 313.43 88 114.29\r\nv 313.2 87.9 114.03\r\nv 316.51 86.72 115.32\r\nv 315.62 86.78 116.37\r\nv 317.32 86.63 114.18\r\nv 316.91 88.44 113.44\r\nv 316.09 88.55 114.57\r\nv 315.2 88.61 115.61\r\nv 317.48 86.56 113.68\r\nv 317.19 86.44 113.35\r\nv 316.78 88.24 112.6\r\nv 317.07 88.36 112.93\r\nv 313.85 89.49 111.71\r\nv 314.73 89.4 110.65\r\nv 313.01 89.6 112.81\r\nv 312.59 91.3 111.98\r\nv 313.43 91.18 110.89\r\nv 314.32 91.07 109.84\r\nv 312.79 89.66 113.24\r\nv 313.01 89.76 113.5\r\nv 312.59 91.48 112.66\r\nv 312.37 91.37 112.41\r\nv 315.67 90.33 113.77\r\nv 314.77 90.41 114.8\r\nv 316.49 90.21 112.64\r\nv 316.07 91.93 111.8\r\nv 315.24 92.06 112.92\r\nv 314.34 92.16 113.95\r\nv 316.65 90.12 112.14\r\nv 316.36 89.99 111.82\r\nv 315.94 91.69 110.98\r\nv 316.23 91.83 111.31\r\nv 313.02 92.81 110.02\r\nv 313.91 92.69 108.98\r\nv 312.17 92.95 111.11\r\nv 311.75 94.54 110.19\r\nv 312.6 94.39 109.11\r\nv 313.5 94.25 108.08\r\nv 311.94 93.02 111.53\r\nv 312.17 93.14 111.78\r\nv 311.74 94.75 110.85\r\nv 311.52 94.62 110.6\r\nv 314.81 93.75 112.03\r\nv 313.91 93.85 113.05\r\nv 315.64 93.6 110.92\r\nv 315.22 95.2 109.99\r\nv 314.39 95.37 111.09\r\nv 313.48 95.5 112.1\r\nv 315.81 93.49 110.43\r\nv 315.52 93.34 110.11\r\nv 315.11 94.93 109.19\r\nv 315.39 95.09 109.51\r\nv 312.19 95.9 108.16\r\nv 313.09 95.74 107.14\r\nv 311.34 96.07 109.23\r\nv 310.93 97.54 108.23\r\nv 311.79 97.34 107.18\r\nv 312.7 97.16 106.17\r\nv 311.1 96.16 109.64\r\nv 311.32 96.29 109.88\r\nv 310.91 97.77 108.87\r\nv 310.69 97.63 108.63\r\nv 313.96 96.93 110.11\r\nv 313.05 97.08 111.11\r\nv 314.8 96.75 109.02\r\nv 314.39 98.21 108.02\r\nv 313.55 98.43 109.09\r\nv 312.63 98.59 110.07\r\nv 314.97 96.62 108.54\r\nv 314.69 96.46 108.24\r\nv 314.29 97.9 107.25\r\nv 314.57 98.08 107.55\r\nv 311.41 98.69 106.17\r\nv 312.32 98.49 105.18\r\nv 310.54 98.92 107.19\r\nv 310.17 100.23 106.11\r\nv 311.06 99.95 105.13\r\nv 311.98 99.71 104.17\r\nv 310.3 99.03 107.59\r\nv 310.52 99.17 107.82\r\nv 310.14 100.51 106.72\r\nv 309.92 100.4 106.45\r\nv 313.15 99.83 108.04\r\nv 312.22 100.02 109\r\nv 314 99.59 106.99\r\nv 313.64 100.87 105.94\r\nv 312.78 101.15 106.95\r\nv 311.84 101.37 107.89\r\nv 314.18 99.44 106.53\r\nv 313.91 99.26 106.23\r\nv 313.55 100.51 105.2\r\nv 313.83 100.7 105.49\r\nv 310.73 101.11 104.06\r\nv 311.67 100.82 103.16\r\nv 309.82 101.49 104.95\r\nv 309.56 102.57 103.8\r\nv 310.48 102.16 102.95\r\nv 311.43 101.8 102.13\r\nv 309.53 101.82 105.15\r\nv 309.79 101.79 105.54\r\nv 309.71 102.6 104.91\r\nv 309.47 102.87 104.34\r\nv 309.32 103.16 103.81\r\nv 309.28 102.78 104.08\r\nv 312.44 102.36 105.84\r\nv 311.49 102.63 106.73\r\nv 313.32 102.02 104.88\r\nv 313.07 103.04 103.81\r\nv 312.18 103.45 104.69\r\nv 311.21 103.8 105.49\r\nv 313.51 101.83 104.45\r\nv 313.24 101.64 104.17\r\nv 313 102.62 103.14\r\nv 313.27 102.82 103.42\r\nv 310.33 103.1 101.78\r\nv 311.29 102.64 101.09\r\nv 309.4 103.6 102.51\r\nv 309.4 104.53 101.09\r\nv 310.33 103.93 100.51\r\nv 311.29 103.36 99.98\r\nv 312.03 104.42 103.48\r\nv 311.05 104.87 104.16\r\nv 312.93 103.91 102.73\r\nv 312.93 104.64 101.6\r\nv 312.03 105.26 102.19\r\nv 311.05 105.81 102.7\r\nv 313.13 103.66 102.38\r\nv 312.86 103.45 102.11\r\nv 312.86 104.15 101.03\r\nv 313.13 104.36 101.3\r\nv 310.51 104.62 99.15\r\nv 311.45 103.96 98.79\r\nv 309.61 105.3 99.54\r\nv 309.97 105.96 97.91\r\nv 310.84 105.2 97.68\r\nv 311.74 104.48 97.48\r\nv 312.21 105.97 100.79\r\nv 311.26 106.61 101.13\r\nv 313.1 105.26 100.37\r\nv 313.4 105.8 99.02\r\nv 312.55 106.57 99.28\r\nv 311.62 107.28 99.46\r\nv 313.29 104.95 100.14\r\nv 313.02 104.73 99.87\r\nv 313.31 105.24 98.59\r\nv 313.58 105.46 98.84\r\nv 311.27 105.7 96.11\r\nv 312.15 104.94 96.03\r\nv 310.43 106.49 96.23\r\nv 310.95 106.96 94.5\r\nv 311.77 106.14 94.45\r\nv 312.62 105.36 94.44\r\nv 309.73 106.25 98.04\r\nv 310.2 106.8 96.32\r\nv 309.95 106.42 98.24\r\nv 310.42 106.97 96.51\r\nv 310.95 107.45 94.74\r\nv 310.73 107.27 94.56\r\nv 313 107.08 97.66\r\nv 312.1 107.83 97.73\r\nv 313.82 106.28 97.52\r\nv 314.31 106.71 95.87\r\nv 313.51 107.54 95.93\r\nv 312.64 108.31 95.93\r\nv 313.99 105.92 97.38\r\nv 313.71 105.7 97.14\r\nv 314.19 106.13 95.53\r\nv 314.47 106.35 95.76\r\nv 312.29 106.55 92.7\r\nv 313.14 105.75 92.73\r\nv 311.49 107.37 92.69\r\nv 312.04 107.76 90.83\r\nv 312.84 106.92 90.86\r\nv 313.67 106.12 90.92\r\nv 311.28 107.69 92.73\r\nv 311.5 107.87 92.91\r\nv 312.06 108.26 91.02\r\nv 311.83 108.08 90.86\r\nv 314.06 107.97 94.11\r\nv 313.2 108.75 94.06\r\nv 314.84 107.12 94.1\r\nv 315.4 107.51 92.22\r\nv 314.62 108.36 92.2\r\nv 313.77 109.14 92.12\r\nv 315 106.76 94.01\r\nv 314.72 106.53 93.78\r\nv 315.27 106.91 91.93\r\nv 315.55 107.14 92.14\r\nv 313.39 107.27 88.95\r\nv 314.22 106.47 89.03\r\nv 312.6 108.11 88.9\r\nv 313.14 108.44 86.93\r\nv 313.93 107.6 86.99\r\nv 314.76 106.79 87.08\r\nv 312.39 108.43 88.92\r\nv 312.61 108.61 89.08\r\nv 313.17 108.94 87.1\r\nv 312.94 108.76 86.95\r\nv 315.19 108.72 90.23\r\nv 314.34 109.51 90.13\r\nv 315.96 107.87 90.26\r\nv 316.52 108.2 88.25\r\nv 315.75 109.06 88.2\r\nv 314.91 109.84 88.1\r\nv 316.11 107.5 90.2\r\nv 315.82 107.27 89.99\r\nv 316.38 107.6 88\r\nv 316.67 107.83 88.19\r\nv 314.46 107.9 85\r\nv 315.28 107.1 85.1\r\nv 313.67 108.74 84.93\r\nv 314.18 109.03 82.91\r\nv 314.97 108.19 82.98\r\nv 315.79 107.38 83.08\r\nv 313.47 109.07 84.95\r\nv 313.7 109.25 85.09\r\nv 314.21 109.54 83.05\r\nv 313.98 109.35 82.92\r\nv 316.3 109.37 86.15\r\nv 315.45 110.16 86.04\r\nv 317.06 108.52 86.2\r\nv 317.59 108.81 84.13\r\nv 316.82 109.66 84.07\r\nv 315.98 110.45 83.96\r\nv 317.21 108.14 86.15\r\nv 316.91 107.91 85.97\r\nv 317.43 108.2 83.91\r\nv 317.73 108.43 84.08\r\nv 315.46 108.46 80.94\r\nv 316.28 107.65 81.04\r\nv 314.68 109.29 80.87\r\nv 315.14 109.54 78.81\r\nv 315.93 108.7 78.89\r\nv 316.75 107.9 78.98\r\nv 314.47 109.62 80.87\r\nv 314.7 109.8 81\r\nv 315.17 110.05 78.94\r\nv 314.94 109.87 78.82\r\nv 317.33 109.94 81.97\r\nv 316.48 110.73 81.86\r\nv 318.09 109.08 82.03\r\nv 318.57 109.33 79.92\r\nv 317.81 110.19 79.86\r\nv 316.96 110.98 79.75\r\nv 318.23 108.71 81.99\r\nv 317.93 108.47 81.83\r\nv 318.41 108.72 79.73\r\nv 318.71 108.96 79.88\r\nv 316.37 108.93 76.83\r\nv 317.2 108.12 76.92\r\nv 315.59 109.77 76.75\r\nv 316.01 109.98 74.69\r\nv 316.79 109.14 74.77\r\nv 317.62 108.33 74.86\r\nv 315.38 110.09 76.75\r\nv 315.62 110.28 76.87\r\nv 316.04 110.49 74.8\r\nv 315.8 110.3 74.69\r\nv 318.27 110.43 77.74\r\nv 317.42 111.21 77.63\r\nv 319.03 109.57 77.81\r\nv 319.46 109.78 75.69\r\nv 318.7 110.64 75.63\r\nv 317.85 111.43 75.52\r\nv 319.17 109.19 77.77\r\nv 318.87 108.96 77.63\r\nv 319.29 109.17 75.52\r\nv 319.6 109.41 75.66\r\nv 317.18 109.33 72.72\r\nv 318.01 108.53 72.81\r\nv 316.4 110.17 72.64\r\nv 316.76 110.35 70.61\r\nv 317.55 109.51 70.68\r\nv 318.37 108.7 70.77\r\nv 316.2 110.5 72.64\r\nv 316.44 110.68 72.74\r\nv 316.8 110.86 70.7\r\nv 316.56 110.67 70.61\r\nv 319.1 110.84 73.52\r\nv 318.25 111.63 73.42\r\nv 319.86 109.98 73.58\r\nv 320.23 110.16 71.49\r\nv 319.47 111.02 71.42\r\nv 318.62 111.81 71.33\r\nv 320 109.61 73.55\r\nv 319.69 109.37 73.43\r\nv 320.07 109.55 71.34\r\nv 320.37 109.79 71.46\r\nv 317.88 109.66 68.66\r\nv 318.71 108.86 68.75\r\nv 317.1 110.5 68.6\r\nv 317.4 110.65 66.61\r\nv 318.18 109.81 66.67\r\nv 319.01 109 66.75\r\nv 316.89 110.83 68.59\r\nv 317.13 111.02 68.68\r\nv 317.44 111.16 66.69\r\nv 317.2 110.97 66.61\r\nv 319.81 111.18 69.35\r\nv 318.97 111.97 69.26\r\nv 320.58 110.32 69.41\r\nv 320.89 110.47 67.37\r\nv 320.13 111.33 67.31\r\nv 319.28 112.11 67.23\r\nv 320.71 109.95 69.39\r\nv 320.41 109.71 69.28\r\nv 320.72 109.85 67.25\r\nv 321.03 110.09 67.35\r\nv 318.46 109.93 64.72\r\nv 319.29 109.13 64.79\r\nv 317.67 110.77 64.66\r\nv 317.92 110.89 62.75\r\nv 318.71 110.05 62.8\r\nv 319.54 109.24 62.87\r\nv 317.47 111.1 64.66\r\nv 317.72 111.29 64.73\r\nv 317.97 111.4 62.81\r\nv 317.72 111.21 62.74\r\nv 320.41 111.46 65.31\r\nv 319.56 112.24 65.23\r\nv 321.17 110.6 65.36\r\nv 321.43 110.72 63.4\r\nv 320.67 111.57 63.35\r\nv 319.82 112.36 63.27\r\nv 321.31 110.22 65.35\r\nv 321 109.98 65.25\r\nv 321.26 110.1 63.3\r\nv 321.57 110.34 63.38\r\nv 318.93 110.15 60.93\r\nv 319.76 109.35 61\r\nv 318.15 110.99 60.88\r\nv 318.35 111.08 59.07\r\nv 319.13 110.24 59.12\r\nv 319.96 109.43 59.18\r\nv 317.95 111.31 60.88\r\nv 318.19 111.5 60.94\r\nv 318.39 111.59 59.12\r\nv 318.15 111.4 59.06\r\nv 320.9 111.68 61.43\r\nv 320.04 112.46 61.36\r\nv 321.66 110.82 61.48\r\nv 321.87 110.91 59.62\r\nv 321.1 111.77 59.57\r\nv 320.25 112.55 59.51\r\nv 321.8 110.44 61.47\r\nv 321.48 110.2 61.39\r\nv 321.69 110.29 59.53\r\nv 322 110.53 59.61\r\nv 319.31 110.31 57.36\r\nv 320.14 109.51 57.42\r\nv 318.53 111.15 57.31\r\nv 318.68 111.22 55.62\r\nv 319.47 110.38 55.67\r\nv 320.3 109.58 55.72\r\nv 318.32 111.48 57.31\r\nv 318.57 111.67 57.36\r\nv 318.73 111.73 55.66\r\nv 318.48 111.54 55.62\r\nv 321.28 111.85 57.77\r\nv 320.43 112.63 57.71\r\nv 322.05 110.99 57.82\r\nv 322.21 111.06 56.08\r\nv 321.44 111.91 56.04\r\nv 320.59 112.7 55.98\r\nv 322.18 110.61 57.81\r\nv 321.87 110.37 57.74\r\nv 322.03 110.44 56.01\r\nv 322.34 110.68 56.07\r\nv 319.61 110.44 54.05\r\nv 320.44 109.63 54.1\r\nv 318.82 111.28 54.01\r\nv 318.93 111.32 52.45\r\nv 319.72 110.49 52.49\r\nv 320.55 109.68 52.53\r\nv 318.62 111.6 54\r\nv 318.86 111.79 54.04\r\nv 318.98 111.84 52.48\r\nv 318.73 111.65 52.45\r\nv 321.58 111.97 54.38\r\nv 320.73 112.76 54.33\r\nv 322.35 111.12 54.42\r\nv 322.47 111.17 52.82\r\nv 321.7 112.02 52.78\r\nv 320.85 112.81 52.74\r\nv 322.48 110.74 54.41\r\nv 322.17 110.5 54.36\r\nv 322.29 110.55 52.76\r\nv 322.6 110.79 52.81\r\nv 319.82 110.53 50.98\r\nv 320.65 109.72 51.02\r\nv 319.03 111.36 50.95\r\nv 319.11 111.39 49.49\r\nv 319.9 110.56 49.52\r\nv 320.73 109.75 49.55\r\nv 318.83 111.69 50.95\r\nv 319.08 111.88 50.98\r\nv 319.16 111.91 49.51\r\nv 318.91 111.72 49.48\r\nv 321.8 112.06 51.23\r\nv 320.95 112.85 51.19\r\nv 322.57 111.21 51.26\r\nv 322.65 111.24 49.75\r\nv 321.89 112.09 49.72\r\nv 321.03 112.88 49.68\r\nv 322.7 110.83 51.26\r\nv 322.39 110.59 51.22\r\nv 322.47 110.62 49.71\r\nv 322.79 110.86 49.75\r\nv 319.96 110.58 48.08\r\nv 320.8 109.78 48.11\r\nv 319.18 111.42 48.05\r\nv 319.23 111.43 46.63\r\nv 320.01 110.59 46.65\r\nv 320.85 109.79 46.68\r\nv 318.97 111.74 48.05\r\nv 319.22 111.93 48.07\r\nv 319.27 111.95 46.64\r\nv 319.02 111.76 46.63\r\nv 321.95 112.12 48.24\r\nv 321.1 112.9 48.2\r\nv 322.72 111.26 48.26\r\nv 322.77 111.28 46.79\r\nv 322 112.13 46.77\r\nv 321.15 112.92 46.74\r\nv 322.85 110.88 48.26\r\nv 322.54 110.64 48.23\r\nv 322.59 110.66 46.77\r\nv 322.9 110.9 46.79\r\nv 320.05 110.6 45.24\r\nv 320.88 109.8 45.26\r\nv 319.26 111.44 45.22\r\nv 319.28 111.44 43.79\r\nv 320.07 110.6 43.81\r\nv 320.9 109.8 43.82\r\nv 319.06 111.76 45.21\r\nv 319.31 111.95 45.22\r\nv 319.33 111.96 43.79\r\nv 319.08 111.76 43.79\r\nv 322.04 112.14 45.3\r\nv 321.18 112.92 45.28\r\nv 322.8 111.29 45.32\r\nv 322.83 111.29 43.85\r\nv 322.06 112.14 43.83\r\nv 321.2 112.93 43.82\r\nv 322.94 110.91 45.32\r\nv 322.62 110.66 45.31\r\nv 322.64 110.66 43.85\r\nv 322.96 110.91 43.85\r\nv 320.08 110.59 42.36\r\nv 320.91 109.79 42.38\r\nv 319.29 111.43 42.35\r\nv 319.28 111.42 40.89\r\nv 320.07 110.58 40.9\r\nv 320.9 109.78 40.91\r\nv 319.08 111.76 42.35\r\nv 319.33 111.95 42.35\r\nv 319.33 111.94 40.88\r\nv 319.08 111.74 40.89\r\nv 322.07 112.13 42.35\r\nv 321.21 112.92 42.34\r\nv 322.83 111.28 42.36\r\nv 322.83 111.27 40.85\r\nv 322.06 112.12 40.84\r\nv 321.21 112.91 40.84\r\nv 322.97 110.9 42.36\r\nv 322.65 110.66 42.37\r\nv 322.65 110.64 40.87\r\nv 322.96 110.89 40.86\r\nv 320.05 110.56 39.42\r\nv 320.88 109.76 39.42\r\nv 319.26 111.4 39.41\r\nv 319.23 111.37 37.92\r\nv 320.02 110.53 37.92\r\nv 320.85 109.73 37.92\r\nv 319.06 111.72 39.41\r\nv 319.31 111.92 39.4\r\nv 319.28 111.89 37.89\r\nv 319.03 111.7 37.91\r\nv 322.04 112.1 39.32\r\nv 321.19 112.88 39.32\r\nv 322.81 111.24 39.32\r\nv 322.77 111.22 37.78\r\nv 322.01 112.07 37.78\r\nv 321.15 112.86 37.78\r\nv 322.94 110.87 39.33\r\nv 322.63 110.62 39.35\r\nv 322.59 110.59 37.81\r\nv 322.91 110.84 37.79\r\nv 319.97 110.5 36.4\r\nv 320.8 109.7 36.39\r\nv 319.18 111.34 36.4\r\nv 319.12 111.29 34.86\r\nv 319.91 110.46 34.86\r\nv 320.74 109.65 34.85\r\nv 318.98 111.66 36.39\r\nv 319.23 111.85 36.37\r\nv 319.17 111.81 34.83\r\nv 318.92 111.62 34.86\r\nv 321.96 112.03 36.21\r\nv 321.11 112.82 36.22\r\nv 322.73 111.18 36.21\r\nv 322.66 111.14 34.63\r\nv 321.9 111.99 34.63\r\nv 321.04 112.78 34.65\r\nv 322.86 110.8 36.23\r\nv 322.55 110.56 36.26\r\nv 322.48 110.52 34.68\r\nv 322.8 110.76 34.64\r\nv 319.83 110.41 33.3\r\nv 320.66 109.6 33.28\r\nv 319.04 111.24 33.31\r\nv 318.95 111.19 31.73\r\nv 319.74 110.35 31.72\r\nv 320.57 109.55 31.7\r\nv 318.84 111.57 33.3\r\nv 319.09 111.76 33.27\r\nv 319 111.7 31.69\r\nv 318.75 111.51 31.73\r\nv 321.82 111.94 33.03\r\nv 320.96 112.73 33.05\r\nv 322.58 111.09 33.03\r\nv 322.49 111.03 31.4\r\nv 321.72 111.88 31.41\r\nv 320.87 112.67 31.44\r\nv 322.72 110.71 33.04\r\nv 322.4 110.46 33.08\r\nv 322.31 110.41 31.47\r\nv 322.62 110.65 31.42\r\nv 319.63 110.28 30.12\r\nv 320.46 109.48 30.1\r\nv 318.84 111.12 30.14\r\nv 318.72 111.05 28.53\r\nv 319.5 110.21 28.51\r\nv 320.34 109.4 28.48\r\nv 318.64 111.45 30.13\r\nv 318.89 111.64 30.09\r\nv 318.76 111.56 28.47\r\nv 318.51 111.37 28.52\r\nv 321.61 111.81 29.77\r\nv 320.76 112.6 29.8\r\nv 322.38 110.96 29.76\r\nv 322.25 110.88 28.09\r\nv 321.48 111.74 28.11\r\nv 320.63 112.52 28.14\r\nv 322.51 110.58 29.78\r\nv 322.2 110.34 29.83\r\nv 322.07 110.26 28.17\r\nv 322.38 110.5 28.11\r\nv 319.36 110.12 26.87\r\nv 320.19 109.32 26.83\r\nv 318.57 110.96 26.89\r\nv 318.41 110.87 25.23\r\nv 319.19 110.03 25.2\r\nv 320.02 109.22 25.17\r\nv 318.37 111.29 26.88\r\nv 318.62 111.48 26.83\r\nv 318.45 111.38 25.16\r\nv 318.2 111.19 25.23\r\nv 321.33 111.65 26.42\r\nv 320.48 112.44 26.46\r\nv 322.1 110.79 26.4\r\nv 321.92 110.7 24.69\r\nv 321.16 111.55 24.71\r\nv 320.31 112.34 24.75\r\nv 322.23 110.42 26.42\r\nv 321.92 110.18 26.49\r\nv 321.75 110.08 24.79\r\nv 322.06 110.32 24.71\r\nv 319.01 109.92 23.52\r\nv 319.84 109.12 23.47\r\nv 318.22 110.76 23.55\r\nv 318.01 110.64 21.84\r\nv 318.79 109.8 21.8\r\nv 319.62 109 21.76\r\nv 318.02 111.08 23.54\r\nv 318.26 111.27 23.47\r\nv 318.05 111.15 21.76\r\nv 317.81 110.96 21.83\r\nv 320.97 111.44 22.97\r\nv 320.12 112.23 23.02\r\nv 321.73 110.58 22.94\r\nv 321.51 110.46 21.17\r\nv 320.75 111.32 21.2\r\nv 319.9 112.1 21.25\r\nv 321.87 110.21 22.97\r\nv 321.56 109.97 23.05\r\nv 321.34 109.84 21.29\r\nv 321.65 110.08 21.2\r\nv 318.56 109.66 20.06\r\nv 319.39 108.86 20.01\r\nv 317.77 110.5 20.1\r\nv 317.51 110.35 18.34\r\nv 318.29 109.51 18.3\r\nv 319.12 108.71 18.24\r\nv 317.57 110.83 20.1\r\nv 317.81 111.02 20.01\r\nv 317.55 110.87 18.24\r\nv 317.3 110.68 18.33\r\nv 320.5 111.18 19.4\r\nv 319.65 111.96 19.46\r\nv 321.27 110.32 19.37\r\nv 320.99 110.16 17.54\r\nv 320.23 111.02 17.57\r\nv 319.38 111.81 17.64\r\nv 321.4 109.94 19.4\r\nv 321.09 109.7 19.51\r\nv 320.82 109.55 17.69\r\nv 321.13 109.79 17.57\r\nv 318 109.35 16.5\r\nv 318.83 108.54 16.44\r\nv 317.22 110.19 16.54\r\nv 316.89 110 14.72\r\nv 317.68 109.16 14.67\r\nv 318.5 108.36 14.61\r\nv 317.01 110.51 16.53\r\nv 317.25 110.7 16.43\r\nv 316.93 110.51 14.6\r\nv 316.69 110.33 14.71\r\nv 319.93 110.85 15.72\r\nv 319.08 111.64 15.79\r\nv 320.69 109.99 15.68\r\nv 320.36 109.8 13.79\r\nv 319.59 110.66 13.83\r\nv 318.75 111.45 13.9\r\nv 320.83 109.62 15.72\r\nv 320.52 109.38 15.84\r\nv 320.19 109.19 13.96\r\nv 320.49 109.43 13.83\r\nv 317.32 108.96 12.82\r\nv 318.15 108.15 12.75\r\nv 316.54 109.8 12.86\r\nv 316.16 109.58 10.99\r\nv 316.94 108.74 10.94\r\nv 317.77 107.93 10.87\r\nv 316.34 110.12 12.85\r\nv 316.58 110.31 12.74\r\nv 316.19 110.08 10.85\r\nv 315.95 109.9 10.97\r\nv 319.23 110.45 11.91\r\nv 318.38 111.24 11.99\r\nv 319.99 109.59 11.88\r\nv 319.6 109.36 9.94\r\nv 318.84 110.22 9.97\r\nv 317.99 111.01 10.05\r\nv 320.13 109.22 11.92\r\nv 319.83 108.98 12.06\r\nv 319.44 108.75 10.14\r\nv 319.74 108.98 9.98\r\nv 316.53 108.49 9.04\r\nv 317.36 107.68 8.97\r\nv 315.75 109.33 9.09\r\nv 315.3 109.06 7.17\r\nv 316.09 108.22 7.13\r\nv 316.91 107.41 7.06\r\nv 315.54 109.65 9.07\r\nv 315.78 109.84 8.94\r\nv 315.33 109.57 7.01\r\nv 315.1 109.38 7.15\r\nv 318.41 109.96 8.01\r\nv 317.56 110.75 8.09\r\nv 319.17 109.1 7.98\r\nv 318.71 108.83 6\r\nv 317.95 109.68 6.03\r\nv 317.1 110.47 6.11\r\nv 319.31 108.73 8.03\r\nv 319.01 108.5 8.19\r\nv 318.56 108.22 6.23\r\nv 318.86 108.45 6.05\r\nv 315.61 107.92 5.19\r\nv 316.44 107.12 5.12\r\nv 314.83 108.76 5.23\r\nv 314.32 108.44 3.28\r\nv 315.1 107.6 3.24\r\nv 315.93 106.8 3.18\r\nv 314.62 109.09 5.21\r\nv 314.85 109.27 5.07\r\nv 314.34 108.94 3.1\r\nv 314.11 108.76 3.25\r\nv 317.46 109.38 4.03\r\nv 316.61 110.17 4.11\r\nv 318.22 108.52 4\r\nv 317.7 108.19 1.99\r\nv 316.93 109.04 2.01\r\nv 316.09 109.83 2.09\r\nv 318.37 108.15 4.06\r\nv 318.07 107.92 4.25\r\nv 317.55 107.59 2.25\r\nv 317.84 107.82 2.06\r\nv 314.56 107.25 1.28\r\nv 315.39 106.45 1.22\r\nv 313.78 108.09 1.31\r\nv 313.21 107.71 -0.66\r\nv 314.01 106.87 -0.67\r\nv 314.84 106.07 -0.71\r\nv 313.57 108.41 1.28\r\nv 313.79 108.59 1.12\r\nv 313.23 108.2 -0.87\r\nv 313 108.03 -0.7\r\nv 316.38 108.68 -0.01\r\nv 315.53 109.47 0.06\r\nv 317.14 107.83 -0.03\r\nv 316.57 107.44 -2.03\r\nv 315.8 108.29 -2.03\r\nv 314.95 109.08 -1.97\r\nv 317.29 107.46 0.05\r\nv 317 107.23 0.25\r\nv 316.44 106.85 -1.73\r\nv 316.72 107.07 -1.94\r\nv 313.45 106.46 -2.58\r\nv 314.29 105.67 -2.6\r\nv 312.65 107.29 -2.6\r\nv 312 106.69 -4.69\r\nv 312.82 105.93 -4.62\r\nv 313.67 105.19 -4.58\r\nv 312.43 107.61 -2.64\r\nv 312.65 107.79 -2.82\r\nv 311.99 107.17 -4.94\r\nv 311.78 106.99 -4.76\r\nv 315.22 107.87 -4.01\r\nv 314.36 108.65 -3.97\r\nv 316 107.02 -3.98\r\nv 315.36 106.57 -5.97\r\nv 314.54 107.36 -6.07\r\nv 313.64 108.09 -6.1\r\nv 316.15 106.66 -3.88\r\nv 315.87 106.43 -3.66\r\nv 315.25 105.99 -5.62\r\nv 315.52 106.22 -5.85\r\nv 312.06 105.16 -6.93\r\nv 312.94 104.6 -6.78\r\nv 311.21 105.77 -7.12\r\nv 309.89 104.96 -9.79\r\nv 311.22 104.43 -9.31\r\nv 312.23 103.79 -9.27\r\nv 310.98 106.01 -7.23\r\nv 311.16 106.22 -7.41\r\nv 309.87 105.59 -9.58\r\nv 309.71 105.37 -9.4\r\nv 313.66 106.73 -8.33\r\nv 312.67 107.31 -8.5\r\nv 314.57 106.07 -8.1\r\nv 313.84 105.36 -10.53\r\nv 312.69 106.13 -10.63\r\nv 311.12 106.72 -11.13\r\nv 314.77 105.76 -7.94\r\nv 314.5 105.52 -7.72\r\nv 313.93 104.83 -9.81\r\nv 314.2 105.08 -10.01\r\nv 329.98 -0.29 130.55\r\nv 330.73 0.05 129.3\r\nv 330.95 0.15 129.57\r\nv 330.21 -0.19 130.81\r\nv 270.14 114.42 -20.88\r\nv 270.15 114.98 -19.57\r\nv 272.48 114.84 -19.48\r\nv 272.47 114.28 -20.79\r\nv 270.15 115.44 -18.19\r\nv 272.48 115.3 -18.1\r\nv 274.81 115.11 -17.97\r\nv 274.81 114.65 -19.35\r\nv 274.8 114.09 -20.66\r\nv 270.14 115.47 -17.64\r\nv 272.46 115.33 -17.55\r\nv 270.11 115.14 -17.4\r\nv 272.43 115 -17.31\r\nv 274.74 114.82 -17.18\r\nv 274.79 115.14 -17.42\r\nv 270 113.44 -16.72\r\nv 269.99 112.91 -18.05\r\nv 272.25 112.78 -17.96\r\nv 272.25 113.31 -16.63\r\nv 269.99 112.43 -19.41\r\nv 272.25 112.29 -19.32\r\nv 274.51 112.11 -19.2\r\nv 274.51 112.59 -17.83\r\nv 274.52 113.13 -16.51\r\nv 270 112.33 -19.91\r\nv 272.25 112.19 -19.82\r\nv 270.01 112.58 -20.1\r\nv 272.28 112.45 -20.01\r\nv 274.55 112.26 -19.88\r\nv 274.51 112.01 -19.69\r\nv 277.15 114.41 -19.19\r\nv 277.13 113.85 -20.5\r\nv 277.14 114.87 -17.81\r\nv 279.48 114.58 -17.62\r\nv 279.48 114.12 -19\r\nv 279.47 113.56 -20.31\r\nv 277.12 114.9 -17.26\r\nv 277.06 114.58 -17.02\r\nv 279.39 114.29 -16.83\r\nv 279.46 114.61 -17.07\r\nv 276.77 112.36 -17.68\r\nv 276.78 112.89 -16.35\r\nv 276.77 111.87 -19.04\r\nv 279.04 111.59 -18.85\r\nv 279.05 112.08 -17.49\r\nv 279.06 112.61 -16.16\r\nv 276.78 111.77 -19.54\r\nv 276.83 112.03 -19.73\r\nv 279.11 111.75 -19.54\r\nv 279.05 111.49 -19.35\r\nv 281.83 113.78 -18.77\r\nv 281.81 113.22 -20.08\r\nv 281.82 114.24 -17.39\r\nv 284.17 113.85 -17.13\r\nv 284.17 113.39 -18.51\r\nv 284.15 112.84 -19.82\r\nv 281.79 114.27 -16.84\r\nv 281.71 113.95 -16.6\r\nv 284.05 113.57 -16.34\r\nv 284.13 113.89 -16.58\r\nv 281.32 111.75 -17.27\r\nv 281.34 112.28 -15.94\r\nv 281.32 111.26 -18.63\r\nv 283.61 110.89 -18.37\r\nv 283.61 111.37 -17.01\r\nv 283.63 111.9 -15.69\r\nv 281.34 111.16 -19.13\r\nv 281.4 111.41 -19.31\r\nv 283.69 111.04 -19.06\r\nv 283.62 110.78 -18.87\r\nv 286.52 112.96 -18.22\r\nv 286.5 112.41 -19.53\r\nv 286.52 113.42 -16.84\r\nv 288.87 112.95 -16.52\r\nv 288.87 112.49 -17.9\r\nv 288.85 111.94 -19.21\r\nv 286.48 113.46 -16.29\r\nv 286.38 113.14 -16.05\r\nv 288.72 112.67 -15.73\r\nv 288.82 112.99 -15.97\r\nv 285.91 110.95 -16.73\r\nv 285.92 111.48 -15.4\r\nv 285.9 110.47 -18.09\r\nv 288.2 110 -17.78\r\nv 288.2 110.49 -16.42\r\nv 288.22 111.02 -15.09\r\nv 285.92 110.36 -18.59\r\nv 286 110.61 -18.77\r\nv 288.3 110.15 -18.46\r\nv 288.22 109.9 -18.27\r\nv 291.2 111.98 -17.55\r\nv 291.18 111.43 -18.86\r\nv 291.2 112.44 -16.17\r\nv 293.5 111.9 -15.8\r\nv 293.51 111.44 -17.17\r\nv 293.48 110.89 -18.48\r\nv 291.15 112.48 -15.62\r\nv 291.04 112.16 -15.39\r\nv 293.33 111.63 -15.02\r\nv 293.45 111.94 -15.25\r\nv 290.48 109.99 -16.07\r\nv 290.5 110.52 -14.75\r\nv 290.48 109.51 -17.43\r\nv 292.74 108.98 -17.07\r\nv 292.74 109.47 -15.71\r\nv 292.75 110 -14.38\r\nv 290.5 109.4 -17.93\r\nv 290.59 109.65 -18.12\r\nv 292.86 109.12 -17.75\r\nv 292.76 108.88 -17.57\r\nv 295.78 110.87 -16.77\r\nv 295.75 110.32 -18.09\r\nv 295.77 111.34 -15.4\r\nv 297.99 110.75 -14.98\r\nv 298 110.28 -16.35\r\nv 297.98 109.73 -17.67\r\nv 295.71 111.38 -14.85\r\nv 295.58 111.06 -14.62\r\nv 297.79 110.48 -14.2\r\nv 297.93 110.79 -14.43\r\nv 294.96 108.91 -15.32\r\nv 294.97 109.44 -13.99\r\nv 294.96 108.42 -16.68\r\nv 297.14 107.84 -16.27\r\nv 297.13 108.33 -14.91\r\nv 297.15 108.86 -13.58\r\nv 294.99 108.32 -17.18\r\nv 295.09 108.56 -17.36\r\nv 297.27 107.98 -16.94\r\nv 297.17 107.74 -16.76\r\nv 300.17 109.67 -15.92\r\nv 300.15 109.12 -17.23\r\nv 300.15 110.14 -14.54\r\nv 302.25 109.52 -14.08\r\nv 302.27 109.05 -15.46\r\nv 302.25 108.5 -16.77\r\nv 300.09 110.18 -14\r\nv 299.94 109.87 -13.77\r\nv 302.03 109.25 -13.31\r\nv 302.18 109.56 -13.54\r\nv 299.25 107.74 -14.48\r\nv 299.27 108.27 -13.15\r\nv 299.26 107.25 -15.84\r\nv 301.31 106.64 -15.39\r\nv 301.3 107.13 -14.03\r\nv 301.31 107.66 -12.71\r\nv 299.29 107.14 -16.33\r\nv 299.4 107.39 -16.51\r\nv 301.47 106.77 -16.06\r\nv 301.35 106.53 -15.88\r\nv 304.29 108.42 -14.98\r\nv 304.27 107.86 -16.29\r\nv 304.26 108.89 -13.61\r\nv 306.35 108.19 -13.07\r\nv 306.31 107.74 -14.46\r\nv 306.23 107.21 -15.8\r\nv 304.19 108.93 -13.07\r\nv 304.03 108.63 -12.84\r\nv 306.12 107.92 -12.3\r\nv 306.29 108.22 -12.52\r\nv 303.28 106.51 -13.57\r\nv 303.28 107.04 -12.24\r\nv 303.29 106.02 -14.93\r\nv 305.2 105.39 -14.44\r\nv 305.24 105.85 -13.06\r\nv 305.31 106.36 -11.71\r\nv 303.33 105.91 -15.42\r\nv 303.45 106.15 -15.6\r\nv 305.35 105.53 -15.12\r\nv 305.21 105.29 -14.94\r\nv 308.41 107 -13.87\r\nv 308.13 106.55 -15.27\r\nv 308.64 107.36 -12.4\r\nv 310.31 106.28 -13.28\r\nv 309.79 105.93 -14.77\r\nv 308.65 107.37 -11.83\r\nv 308.47 107.07 -11.61\r\nv 310.59 106.63 -10.39\r\nv 310.79 106.92 -10.61\r\nv 307.28 105.13 -12.48\r\nv 307.55 105.56 -11.06\r\nv 307.04 104.74 -13.93\r\nv 308.59 104.17 -13.46\r\nv 309.08 104.46 -11.93\r\nv 306.99 104.67 -14.45\r\nv 307.13 104.9 -14.63\r\nv 308.62 104.36 -14.18\r\nv 308.47 104.13 -14.01\r\nv 311.75 105.72 -12.81\r\nv 311.03 105.44 -14.36\r\nv 312.7 105.09 -12.83\r\nv 311.84 104.94 -14.34\r\nv 310.32 103.98 -11.51\r\nv 309.65 103.76 -13.11\r\nv 310.27 103.37 -13.08\r\nv 311.07 103.47 -11.54\r\nv 309.47 103.74 -13.68\r\nv 309.65 103.96 -13.84\r\nv 310.25 103.59 -13.8\r\nv 310.05 103.39 -13.64\r\nv 313.09 104.22 -13.77\r\nv 312.17 104.3 -15\r\nv 313.94 104.09 -12.47\r\nv 314.14 102.86 -14.06\r\nv 313.28 103.19 -15.09\r\nv 312.34 103.45 -16.05\r\nv 314.12 103.95 -11.95\r\nv 313.85 103.72 -11.72\r\nv 314.05 102.46 -13.36\r\nv 314.32 102.68 -13.61\r\nv 311.39 102.75 -12.33\r\nv 312.29 102.67 -11.08\r\nv 310.52 102.87 -13.61\r\nv 310.66 102.16 -14.5\r\nv 311.54 101.85 -13.48\r\nv 312.46 101.57 -12.5\r\nv 310.28 102.96 -14.08\r\nv 310.5 103.14 -14.26\r\nv 310.64 102.47 -15.09\r\nv 310.42 102.31 -14.88\r\nv 313.58 102.07 -16.29\r\nv 312.65 102.36 -17.21\r\nv 314.43 101.73 -15.3\r\nv 314.79 100.58 -16.37\r\nv 313.96 100.86 -17.42\r\nv 313.04 101.09 -18.39\r\nv 314.61 101.54 -14.87\r\nv 314.33 101.34 -14.6\r\nv 314.68 100.22 -15.64\r\nv 314.96 100.41 -15.92\r\nv 311.83 100.81 -14.6\r\nv 312.74 100.51 -13.67\r\nv 310.95 101.14 -15.57\r\nv 311.34 99.91 -16.71\r\nv 312.19 99.65 -15.68\r\nv 313.08 99.4 -14.69\r\nv 310.71 101.28 -15.95\r\nv 310.93 101.44 -16.16\r\nv 311.32 100.19 -17.32\r\nv 311.1 100.04 -17.1\r\nv 314.38 99.55 -18.51\r\nv 313.47 99.74 -19.51\r\nv 315.2 99.31 -17.42\r\nv 315.63 97.94 -18.45\r\nv 314.82 98.15 -19.56\r\nv 313.93 98.3 -20.58\r\nv 315.36 99.16 -16.96\r\nv 315.08 98.97 -16.67\r\nv 315.51 97.62 -17.69\r\nv 315.8 97.8 -17.97\r\nv 312.59 98.38 -16.73\r\nv 313.47 98.18 -15.71\r\nv 311.75 98.61 -17.79\r\nv 312.19 97.23 -18.83\r\nv 313.02 97.03 -17.74\r\nv 313.9 96.86 -16.7\r\nv 311.52 98.72 -18.2\r\nv 311.75 98.87 -18.42\r\nv 312.19 97.46 -19.47\r\nv 311.97 97.33 -19.25\r\nv 315.29 96.66 -20.57\r\nv 314.41 96.8 -21.61\r\nv 316.1 96.48 -19.45\r\nv 316.57 94.94 -20.4\r\nv 315.77 95.1 -21.54\r\nv 314.89 95.22 -22.59\r\nv 316.25 96.35 -18.96\r\nv 315.97 96.18 -18.67\r\nv 316.44 94.67 -19.61\r\nv 316.73 94.83 -19.91\r\nv 313.48 95.6 -18.72\r\nv 314.34 95.45 -17.66\r\nv 312.65 95.77 -19.82\r\nv 313.12 94.24 -20.77\r\nv 313.94 94.09 -19.66\r\nv 314.8 93.96 -18.58\r\nv 312.43 95.86 -20.25\r\nv 312.66 95.99 -20.48\r\nv 313.13 94.45 -21.44\r\nv 312.91 94.32 -21.2\r\nv 316.26 93.49 -22.46\r\nv 315.38 93.59 -23.53\r\nv 317.05 93.34 -21.31\r\nv 317.53 91.69 -22.18\r\nv 316.74 91.81 -23.34\r\nv 315.87 91.9 -24.41\r\nv 317.2 93.24 -20.82\r\nv 316.91 93.08 -20.51\r\nv 317.39 91.44 -21.37\r\nv 317.68 91.59 -21.68\r\nv 314.41 92.52 -20.55\r\nv 315.27 92.41 -19.47\r\nv 313.6 92.65 -21.68\r\nv 314.08 91.01 -22.54\r\nv 314.88 90.89 -21.41\r\nv 315.73 90.8 -20.31\r\nv 313.38 92.72 -22.11\r\nv 313.61 92.85 -22.35\r\nv 314.1 91.19 -23.22\r\nv 313.86 91.08 -22.98\r\nv 317.23 90.09 -24.18\r\nv 316.36 90.16 -25.26\r\nv 318.01 89.98 -23.01\r\nv 318.48 88.22 -23.8\r\nv 317.7 88.32 -24.97\r\nv 316.84 88.38 -26.05\r\nv 318.16 89.89 -22.51\r\nv 317.86 89.75 -22.2\r\nv 318.33 88 -22.97\r\nv 318.63 88.13 -23.29\r\nv 315.35 89.22 -22.22\r\nv 316.2 89.13 -21.12\r\nv 314.55 89.32 -23.36\r\nv 315.02 87.58 -24.14\r\nv 315.82 87.49 -22.99\r\nv 316.66 87.42 -21.88\r\nv 314.34 89.38 -23.8\r\nv 314.57 89.49 -24.05\r\nv 315.04 87.74 -24.83\r\nv 314.81 87.63 -24.58\r\nv 318.17 86.5 -25.72\r\nv 317.31 86.55 -26.8\r\nv 318.95 86.41 -24.54\r\nv 319.4 84.57 -25.24\r\nv 318.63 84.65 -26.42\r\nv 317.77 84.69 -27.51\r\nv 319.09 86.34 -24.03\r\nv 318.79 86.21 -23.71\r\nv 319.24 84.39 -24.41\r\nv 319.54 84.5 -24.73\r\nv 316.28 85.72 -23.72\r\nv 317.12 85.66 -22.61\r\nv 315.48 85.8 -24.87\r\nv 315.93 83.98 -25.56\r\nv 316.72 83.91 -24.41\r\nv 317.56 83.86 -23.29\r\nv 315.27 85.85 -25.32\r\nv 315.51 85.95 -25.57\r\nv 315.96 84.12 -26.26\r\nv 315.72 84.03 -26.01\r\nv 319.07 82.76 -27.08\r\nv 318.21 82.8 -28.17\r\nv 319.84 82.7 -25.9\r\nv 320.26 80.79 -26.51\r\nv 319.49 80.85 -27.7\r\nv 318.63 80.88 -28.79\r\nv 319.98 82.63 -25.38\r\nv 319.68 82.52 -25.06\r\nv 320.09 80.63 -25.67\r\nv 320.4 80.73 -26\r\nv 317.15 82.07 -25.05\r\nv 317.99 82.03 -23.94\r\nv 316.36 82.13 -26.21\r\nv 316.77 80.26 -26.82\r\nv 317.56 80.2 -25.65\r\nv 318.4 80.16 -24.54\r\nv 316.15 82.17 -26.66\r\nv 316.39 82.26 -26.91\r\nv 316.8 80.37 -27.52\r\nv 316.56 80.29 -27.26\r\nv 319.89 78.91 -28.27\r\nv 319.03 78.94 -29.37\r\nv 320.66 78.86 -27.09\r\nv 321.03 76.9 -27.62\r\nv 320.26 76.95 -28.81\r\nv 319.41 76.98 -29.9\r\nv 320.8 78.8 -26.57\r\nv 320.49 78.71 -26.24\r\nv 320.86 76.76 -26.77\r\nv 321.17 76.85 -27.1\r\nv 317.95 78.31 -26.22\r\nv 318.79 78.27 -25.1\r\nv 317.16 78.36 -27.38\r\nv 317.53 76.43 -27.9\r\nv 318.32 76.39 -26.74\r\nv 319.15 76.36 -25.62\r\nv 316.96 78.39 -27.83\r\nv 317.2 78.47 -28.09\r\nv 317.57 76.53 -28.61\r\nv 317.33 76.46 -28.35\r\nv 320.62 74.97 -29.31\r\nv 319.76 74.99 -30.4\r\nv 321.39 74.93 -28.12\r\nv 321.72 72.93 -28.58\r\nv 320.95 72.97 -29.77\r\nv 320.09 72.99 -30.87\r\nv 321.52 74.88 -27.6\r\nv 321.21 74.8 -27.27\r\nv 321.54 72.81 -27.73\r\nv 321.85 72.88 -28.06\r\nv 318.67 74.45 -27.23\r\nv 319.5 74.42 -26.11\r\nv 317.88 74.49 -28.39\r\nv 318.2 72.52 -28.85\r\nv 318.99 72.49 -27.69\r\nv 319.82 72.46 -26.57\r\nv 317.67 74.52 -28.84\r\nv 317.92 74.58 -29.1\r\nv 318.24 72.61 -29.56\r\nv 318 72.55 -29.3\r\nv 321.25 70.95 -30.21\r\nv 320.4 70.97 -31.31\r\nv 322.02 70.91 -29.02\r\nv 322.3 68.87 -29.43\r\nv 321.54 68.91 -30.63\r\nv 320.68 68.93 -31.72\r\nv 322.16 70.87 -28.5\r\nv 321.85 70.8 -28.17\r\nv 322.13 68.77 -28.58\r\nv 322.44 68.83 -28.91\r\nv 319.29 70.5 -28.12\r\nv 320.12 70.47 -27\r\nv 318.5 70.54 -29.28\r\nv 318.78 68.53 -29.69\r\nv 319.57 68.5 -28.52\r\nv 320.4 68.47 -27.4\r\nv 318.3 70.56 -29.73\r\nv 318.54 70.62 -30\r\nv 318.82 68.61 -30.4\r\nv 318.58 68.56 -30.14\r\nv 321.79 66.86 -31.02\r\nv 320.94 66.88 -32.11\r\nv 322.56 66.81 -29.83\r\nv 322.79 64.74 -30.2\r\nv 322.03 64.79 -31.39\r\nv 321.17 64.81 -32.48\r\nv 322.7 66.78 -29.3\r\nv 322.38 66.72 -28.97\r\nv 322.62 64.65 -29.34\r\nv 322.93 64.7 -29.68\r\nv 319.82 66.47 -28.91\r\nv 320.66 66.44 -27.79\r\nv 319.03 66.51 -30.07\r\nv 319.26 64.47 -30.44\r\nv 320.05 64.43 -29.28\r\nv 320.89 64.4 -28.16\r\nv 318.83 66.53 -30.53\r\nv 319.08 66.58 -30.79\r\nv 319.31 64.54 -31.16\r\nv 319.06 64.49 -30.89\r\nv 322.23 62.7 -31.75\r\nv 321.38 62.73 -32.84\r\nv 323 62.65 -30.56\r\nv 323.19 60.55 -30.91\r\nv 322.42 60.6 -32.1\r\nv 321.56 60.64 -33.19\r\nv 323.14 62.62 -30.04\r\nv 322.82 62.56 -29.7\r\nv 323.01 60.46 -30.05\r\nv 323.32 60.51 -30.39\r\nv 320.26 62.37 -29.63\r\nv 321.09 62.33 -28.51\r\nv 319.47 62.41 -30.8\r\nv 319.65 60.34 -31.14\r\nv 320.44 60.29 -29.98\r\nv 321.27 60.25 -28.86\r\nv 319.26 62.44 -31.25\r\nv 319.51 62.48 -31.51\r\nv 319.69 60.4 -31.86\r\nv 319.45 60.36 -31.59\r\nv 322.58 58.49 -32.45\r\nv 321.73 58.54 -33.54\r\nv 323.35 58.44 -31.26\r\nv 323.5 56.31 -31.62\r\nv 322.73 56.38 -32.81\r\nv 321.87 56.43 -33.9\r\nv 323.49 58.4 -30.74\r\nv 323.17 58.35 -30.4\r\nv 323.32 56.23 -30.76\r\nv 323.63 56.27 -31.1\r\nv 320.6 58.2 -30.33\r\nv 321.44 58.15 -29.21\r\nv 319.81 58.25 -31.49\r\nv 319.96 56.15 -31.84\r\nv 320.75 56.09 -30.68\r\nv 321.58 56.03 -29.57\r\nv 319.61 58.28 -31.94\r\nv 319.86 58.32 -32.21\r\nv 320 56.22 -32.56\r\nv 319.75 56.18 -32.29\r\nv 322.86 54.26 -33.17\r\nv 322 54.32 -34.26\r\nv 323.63 54.18 -31.99\r\nv 323.74 52.05 -32.38\r\nv 322.97 52.13 -33.56\r\nv 322.11 52.2 -34.65\r\nv 323.76 54.14 -31.47\r\nv 323.45 54.1 -31.13\r\nv 323.56 51.96 -31.52\r\nv 323.88 52 -31.85\r\nv 320.88 53.97 -31.05\r\nv 321.71 53.91 -29.93\r\nv 320.08 54.04 -32.21\r\nv 320.2 51.93 -32.59\r\nv 320.99 51.85 -31.43\r\nv 321.83 51.77 -30.32\r\nv 319.88 54.08 -32.66\r\nv 320.13 54.11 -32.92\r\nv 320.24 52 -33.31\r\nv 319.99 51.96 -33.04\r\nv 323.08 50 -33.97\r\nv 322.22 50.08 -35.06\r\nv 323.85 49.91 -32.79\r\nv 323.94 47.77 -33.23\r\nv 323.17 47.87 -34.41\r\nv 322.31 47.97 -35.5\r\nv 323.98 49.86 -32.27\r\nv 323.67 49.81 -31.93\r\nv 323.76 47.67 -32.38\r\nv 324.08 47.72 -32.71\r\nv 321.1 49.71 -31.85\r\nv 321.93 49.63 -30.74\r\nv 320.3 49.8 -33\r\nv 320.4 47.67 -33.45\r\nv 321.19 47.57 -32.29\r\nv 322.03 47.48 -31.18\r\nv 320.1 49.84 -33.45\r\nv 320.35 49.88 -33.72\r\nv 320.44 47.75 -34.16\r\nv 320.19 47.72 -33.89\r\nv 323.26 45.74 -34.89\r\nv 322.4 45.85 -35.98\r\nv 324.03 45.63 -33.72\r\nv 324.11 43.52 -34.23\r\nv 323.34 43.64 -35.4\r\nv 322.48 43.75 -36.48\r\nv 324.17 45.57 -33.2\r\nv 323.85 45.52 -32.86\r\nv 323.93 43.4 -33.37\r\nv 324.25 43.45 -33.71\r\nv 321.28 45.43 -32.77\r\nv 322.12 45.32 -31.67\r\nv 320.48 45.54 -33.93\r\nv 320.57 43.44 -34.44\r\nv 321.36 43.31 -33.29\r\nv 322.2 43.2 -32.18\r\nv 320.28 45.59 -34.37\r\nv 320.53 45.63 -34.64\r\nv 320.61 43.53 -35.15\r\nv 320.36 43.49 -34.88\r\nv 323.41 41.59 -35.93\r\nv 322.55 41.71 -37\r\nv 324.19 41.46 -34.75\r\nv 324.25 39.48 -35.28\r\nv 323.48 39.62 -36.45\r\nv 322.62 39.75 -37.53\r\nv 324.32 41.39 -34.24\r\nv 324.01 41.34 -33.9\r\nv 324.08 39.36 -34.43\r\nv 324.39 39.41 -34.77\r\nv 321.43 41.25 -33.81\r\nv 322.27 41.13 -32.71\r\nv 320.64 41.39 -34.96\r\nv 320.71 39.41 -35.49\r\nv 321.5 39.28 -34.34\r\nv 322.34 39.14 -33.24\r\nv 320.43 41.44 -35.41\r\nv 320.68 41.48 -35.67\r\nv 320.75 39.52 -36.2\r\nv 320.5 39.47 -35.93\r\nv 323.54 37.77 -36.97\r\nv 322.68 37.9 -38.05\r\nv 324.32 37.62 -35.81\r\nv 324.37 35.89 -36.31\r\nv 323.6 36.05 -37.47\r\nv 322.74 36.19 -38.54\r\nv 324.45 37.54 -35.29\r\nv 324.14 37.49 -34.95\r\nv 324.2 35.76 -35.46\r\nv 324.51 35.81 -35.79\r\nv 321.57 37.41 -34.86\r\nv 322.4 37.27 -33.77\r\nv 320.77 37.55 -36.01\r\nv 320.83 35.83 -36.51\r\nv 321.62 35.67 -35.37\r\nv 322.46 35.53 -34.27\r\nv 320.57 37.61 -36.45\r\nv 320.81 37.66 -36.72\r\nv 320.87 35.94 -37.22\r\nv 320.62 35.89 -36.95\r\nv 323.65 34.49 -37.94\r\nv 322.79 34.64 -39.01\r\nv 324.42 34.33 -36.78\r\nv 324.47 32.96 -37.2\r\nv 323.7 33.13 -38.36\r\nv 322.84 33.28 -39.43\r\nv 324.56 34.25 -36.26\r\nv 324.25 34.19 -35.93\r\nv 324.3 32.82 -36.36\r\nv 324.61 32.88 -36.69\r\nv 321.67 34.11 -35.84\r\nv 322.51 33.96 -34.75\r\nv 320.88 34.27 -36.98\r\nv 320.93 32.9 -37.41\r\nv 321.72 32.73 -36.27\r\nv 322.56 32.57 -35.18\r\nv 320.67 34.33 -37.42\r\nv 320.92 34.38 -37.68\r\nv 320.97 33.01 -38.11\r\nv 320.72 32.96 -37.85\r\nv 323.74 31.99 -38.73\r\nv 322.88 32.15 -39.8\r\nv 324.51 31.82 -37.57\r\nv 324.55 30.87 -37.9\r\nv 323.78 31.05 -39.05\r\nv 322.91 31.22 -40.11\r\nv 324.65 31.73 -37.06\r\nv 324.34 31.66 -36.73\r\nv 324.37 30.7 -37.06\r\nv 324.69 30.78 -37.39\r\nv 321.76 31.57 -36.64\r\nv 322.6 31.41 -35.55\r\nv 320.97 31.74 -37.78\r\nv 321 30.78 -38.11\r\nv 321.8 30.6 -36.97\r\nv 322.64 30.43 -35.89\r\nv 320.76 31.81 -38.22\r\nv 321.01 31.87 -38.48\r\nv 321.05 30.91 -38.8\r\nv 320.8 30.86 -38.55\r\nv 323.81 30.29 -39.33\r\nv 322.95 30.47 -40.39\r\nv 324.58 30.09 -38.18\r\nv 324.62 29.45 -38.44\r\nv 323.84 29.67 -39.58\r\nv 322.98 29.87 -40.63\r\nv 324.72 29.99 -37.68\r\nv 324.41 29.91 -37.35\r\nv 324.44 29.25 -37.61\r\nv 324.76 29.34 -37.94\r\nv 321.84 29.79 -37.27\r\nv 322.68 29.6 -36.19\r\nv 321.04 29.99 -38.4\r\nv 321.07 29.33 -38.66\r\nv 321.87 29.11 -37.55\r\nv 322.71 28.91 -36.47\r\nv 320.83 30.07 -38.84\r\nv 321.08 30.13 -39.09\r\nv 321.11 29.49 -39.35\r\nv 320.87 29.42 -39.1\r\nv 323.87 29.17 -39.81\r\nv 323.01 29.39 -40.85\r\nv 324.65 28.93 -38.68\r\nv 324.68 28.5 -38.9\r\nv 323.9 28.77 -40.02\r\nv 323.04 29.01 -41.05\r\nv 324.79 28.81 -38.18\r\nv 324.48 28.71 -37.86\r\nv 324.51 28.25 -38.1\r\nv 324.83 28.36 -38.41\r\nv 321.91 28.54 -37.81\r\nv 322.75 28.32 -36.74\r\nv 321.11 28.78 -38.91\r\nv 321.15 28.32 -39.16\r\nv 321.95 28.06 -38.06\r\nv 322.79 27.81 -37.01\r\nv 320.9 28.88 -39.34\r\nv 321.15 28.96 -39.59\r\nv 321.18 28.52 -39.82\r\nv 320.94 28.43 -39.58\r\nv 323.94 28.43 -40.23\r\nv 323.07 28.69 -41.24\r\nv 324.72 28.13 -39.13\r\nv 324.76 27.79 -39.36\r\nv 323.97 28.12 -40.44\r\nv 323.1 28.4 -41.44\r\nv 324.86 27.98 -38.65\r\nv 324.55 27.85 -38.34\r\nv 324.6 27.49 -38.59\r\nv 324.9 27.63 -38.89\r\nv 321.99 27.63 -38.32\r\nv 322.84 27.36 -37.29\r\nv 321.18 27.92 -39.4\r\nv 321.23 27.56 -39.65\r\nv 322.03 27.24 -38.59\r\nv 322.88 26.95 -37.57\r\nv 320.97 28.04 -39.82\r\nv 321.22 28.14 -40.05\r\nv 321.26 27.8 -40.29\r\nv 321.02 27.69 -40.06\r\nv 324.01 27.81 -40.68\r\nv 323.14 28.11 -41.66\r\nv 324.8 27.46 -39.61\r\nv 324.85 27.13 -39.89\r\nv 324.06 27.5 -40.93\r\nv 323.18 27.83 -41.9\r\nv 324.95 27.29 -39.15\r\nv 324.64 27.14 -38.86\r\nv 324.7 26.78 -39.16\r\nv 325 26.94 -39.43\r\nv 322.08 26.86 -38.88\r\nv 322.94 26.55 -37.87\r\nv 321.27 27.2 -39.92\r\nv 321.33 26.85 -40.22\r\nv 322.14 26.49 -39.19\r\nv 323 26.15 -38.2\r\nv 321.06 27.34 -40.32\r\nv 321.3 27.46 -40.55\r\nv 321.35 27.12 -40.83\r\nv 321.11 27 -40.61\r\nv 324.11 27.19 -41.22\r\nv 323.23 27.53 -42.17\r\nv 324.91 26.79 -40.2\r\nv 324.97 26.44 -40.55\r\nv 324.17 26.86 -41.55\r\nv 323.29 27.22 -42.48\r\nv 325.06 26.6 -39.75\r\nv 324.75 26.42 -39.49\r\nv 324.82 26.05 -39.86\r\nv 325.12 26.23 -40.12\r\nv 322.2 26.11 -39.54\r\nv 323.06 25.75 -38.57\r\nv 321.39 26.49 -40.54\r\nv 321.45 26.13 -40.9\r\nv 322.27 25.73 -39.92\r\nv 323.13 25.35 -38.97\r\nv 321.17 26.65 -40.93\r\nv 321.41 26.79 -41.14\r\nv 321.47 26.44 -41.48\r\nv 321.23 26.3 -41.28\r\nv 324.24 26.5 -41.93\r\nv 323.35 26.89 -42.84\r\nv 325.04 26.07 -40.95\r\nv 325.12 25.67 -41.4\r\nv 324.31 26.12 -42.37\r\nv 323.42 26.52 -43.26\r\nv 325.19 25.85 -40.52\r\nv 324.89 25.67 -40.27\r\nv 324.97 25.26 -40.74\r\nv 325.27 25.45 -40.99\r\nv 322.35 25.33 -40.35\r\nv 323.21 24.93 -39.42\r\nv 321.52 25.75 -41.31\r\nv 321.6 25.35 -41.77\r\nv 322.43 24.91 -40.83\r\nv 323.3 24.5 -39.91\r\nv 321.3 25.93 -41.68\r\nv 321.54 26.07 -41.88\r\nv 321.62 25.68 -42.32\r\nv 321.38 25.53 -42.13\r\nv 324.4 25.71 -42.87\r\nv 323.5 26.12 -43.74\r\nv 325.21 25.24 -41.92\r\nv 325.3 24.78 -42.51\r\nv 324.49 25.26 -43.44\r\nv 323.59 25.68 -44.3\r\nv 325.36 25.01 -41.51\r\nv 325.06 24.82 -41.28\r\nv 325.16 24.34 -41.87\r\nv 325.46 24.54 -42.11\r\nv 322.52 24.47 -41.36\r\nv 323.39 24.04 -40.47\r\nv 321.69 24.92 -42.29\r\nv 321.78 24.46 -42.88\r\nv 322.62 23.99 -41.96\r\nv 323.49 23.55 -41.08\r\nv 321.47 25.11 -42.64\r\nv 321.7 25.26 -42.83\r\nv 321.8 24.81 -43.41\r\nv 321.56 24.65 -43.22\r\nv 324.59 24.76 -44.09\r\nv 323.69 25.19 -44.94\r\nv 325.41 24.27 -43.17\r\nv 325.52 23.73 -43.9\r\nv 324.7 24.23 -44.81\r\nv 323.8 24.67 -45.64\r\nv 325.56 24.03 -42.78\r\nv 325.27 23.83 -42.55\r\nv 325.38 23.27 -43.29\r\nv 325.68 23.48 -43.52\r\nv 322.72 23.47 -42.64\r\nv 323.6 23.03 -41.77\r\nv 321.89 23.95 -43.54\r\nv 322 23.41 -44.27\r\nv 322.84 22.92 -43.38\r\nv 323.72 22.46 -42.53\r\nv 321.67 24.15 -43.88\r\nv 321.9 24.31 -44.06\r\nv 322.01 23.77 -44.78\r\nv 321.78 23.61 -44.61\r\nv 324.82 23.66 -45.59\r\nv 323.92 24.11 -46.42\r\nv 325.64 23.15 -44.71\r\nv 325.77 22.55 -45.57\r\nv 324.95 23.07 -46.44\r\nv 324.04 23.53 -47.26\r\nv 325.8 22.9 -44.33\r\nv 325.5 22.69 -44.1\r\nv 325.63 22.09 -44.98\r\nv 325.93 22.3 -45.2\r\nv 322.96 22.34 -44.2\r\nv 323.84 21.87 -43.35\r\nv 322.12 22.83 -45.07\r\nv 322.25 22.24 -45.93\r\nv 323.09 21.73 -45.07\r\nv 323.97 21.25 -44.25\r\nv 321.9 23.04 -45.4\r\nv 322.13 23.21 -45.57\r\nv 322.25 22.61 -46.43\r\nv 322.02 22.45 -46.26\r\nv 325.08 22.47 -47.35\r\nv 324.17 22.94 -48.15\r\nv 325.9 21.93 -46.5\r\nv 326.04 21.3 -47.47\r\nv 325.21 21.85 -48.32\r\nv 324.3 22.33 -49.1\r\nv 326.06 21.68 -46.13\r\nv 325.77 21.46 -45.92\r\nv 325.91 20.82 -46.91\r\nv 326.2 21.04 -47.12\r\nv 323.22 21.1 -46.01\r\nv 324.11 20.62 -45.2\r\nv 322.38 21.62 -46.85\r\nv 322.52 20.99 -47.83\r\nv 323.36 20.46 -47\r\nv 324.25 19.97 -46.21\r\nv 322.15 21.83 -47.17\r\nv 322.39 22 -47.34\r\nv 322.52 21.38 -48.31\r\nv 322.29 21.21 -48.14\r\nv 325.35 21.22 -49.33\r\nv 324.44 21.71 -50.09\r\nv 326.18 20.67 -48.51\r\nv 326.32 20.03 -49.58\r\nv 325.49 20.59 -50.39\r\nv 324.58 21.09 -51.14\r\nv 326.34 20.4 -48.15\r\nv 326.05 20.17 -47.95\r\nv 326.19 19.53 -49.04\r\nv 326.49 19.75 -49.24\r\nv 323.5 19.82 -48.05\r\nv 324.39 19.31 -47.27\r\nv 322.66 20.35 -48.86\r\nv 322.8 19.72 -49.93\r\nv 323.65 19.17 -49.14\r\nv 324.54 18.65 -48.38\r\nv 322.43 20.58 -49.17\r\nv 322.66 20.75 -49.33\r\nv 322.8 20.12 -50.39\r\nv 322.57 19.94 -50.24\r\nv 325.63 19.97 -51.5\r\nv 324.72 20.47 -52.23\r\nv 326.46 19.39 -50.7\r\nv 326.29 19.16 -51.51\r\nv 325.7 19.46 -52.32\r\nv 324.99 19.96 -52.64\r\nv 326.63 19.12 -50.37\r\nv 326.34 18.89 -50.17\r\nv 326.16 18.6 -51.29\r\nv 326.45 18.82 -51.48\r\nv 323.79 18.53 -50.27\r\nv 324.69 18.01 -49.52\r\nv 322.94 19.08 -51.05\r\nv 323.27 18.61 -51.5\r\nv 323.93 18.07 -51.15\r\nv 324.56 17.8 -50.37\r\nv 322.71 19.31 -51.35\r\nv 322.94 19.49 -51.5\r\nv 323.34 18.84 -52.1\r\nv 323.11 18.66 -51.95\r\nv 324.02 17.96 -51.48\r\nv 324.86 17.58 -50.43\r\nv 324.25 18.14 -51.63\r\nv 325.09 17.76 -50.58\r\nv 329.23 3.37 132.26\r\nv 329.68 1.83 132.88\r\nv 329.97 1.96 133.21\r\nv 329.52 3.51 132.59\r\nv 330.29 0.71 132.97\r\nv 330.57 0.84 133.3\r\nv 331.43 1.5 129.35\r\nv 330.98 3.04 128.72\r\nv 331.47 0.39 130.16\r\nv 330.92 1.26 128.75\r\nv 330.46 2.8 128.13\r\nv 328.21 6.8 130.89\r\nv 328.74 5.03 131.59\r\nv 329.03 5.17 131.92\r\nv 328.5 6.94 131.22\r\nv 330.48 4.71 128.05\r\nv 329.95 6.5 127.35\r\nv 329.97 4.47 127.45\r\nv 329.44 6.26 126.75\r\nv 327.08 10.61 129.43\r\nv 327.66 8.68 130.17\r\nv 327.94 8.81 130.5\r\nv 327.37 10.74 129.76\r\nv 329.39 8.38 126.62\r\nv 328.81 10.32 125.87\r\nv 328.88 8.14 126.02\r\nv 328.3 10.09 125.28\r\nv 325.92 14.49 127.98\r\nv 326.5 12.56 128.7\r\nv 326.78 12.69 129.03\r\nv 326.21 14.62 128.31\r\nv 328.23 12.28 125.14\r\nv 327.65 14.22 124.42\r\nv 327.71 12.05 124.54\r\nv 327.14 13.99 123.82\r\nv 324.84 18.13 126.66\r\nv 325.37 16.36 127.3\r\nv 325.65 16.49 127.63\r\nv 325.13 18.26 126.99\r\nv 327.09 16.1 123.73\r\nv 326.56 17.87 123.09\r\nv 326.58 15.87 123.13\r\nv 326.05 17.64 122.49\r\nv 323.94 21.19 125.57\r\nv 324.36 19.75 126.08\r\nv 324.65 19.88 126.41\r\nv 324.22 21.31 125.91\r\nv 326.08 19.5 122.51\r\nv 325.66 20.94 122\r\nv 325.57 19.27 121.91\r\nv 325.14 20.72 121.4\r\nv 323.29 23.39 124.82\r\nv 323.58 22.39 125.16\r\nv 323.87 22.51 125.49\r\nv 323.58 23.52 125.15\r\nv 325.3 22.16 121.58\r\nv 325 23.18 121.24\r\nv 324.78 21.94 120.98\r\nv 324.48 22.96 120.63\r\nv 322.86 24.91 124.34\r\nv 323.06 24.23 124.55\r\nv 323.35 24.34 124.89\r\nv 323.16 25.03 124.68\r\nv 324.76 24.03 120.96\r\nv 324.56 24.74 120.74\r\nv 324.24 23.82 120.36\r\nv 324.04 24.53 120.14\r\nv 322.59 25.97 124.05\r\nv 322.71 25.48 124.17\r\nv 323 25.59 124.51\r\nv 322.88 26.07 124.39\r\nv 324.4 25.33 120.57\r\nv 324.27 25.85 120.44\r\nv 323.88 25.14 119.96\r\nv 323.75 25.67 119.83\r\nv 322.4 26.81 123.87\r\nv 322.49 26.4 123.95\r\nv 322.78 26.49 124.3\r\nv 322.7 26.89 124.22\r\nv 324.17 26.31 120.34\r\nv 324.07 26.74 120.25\r\nv 323.63 26.14 119.72\r\nv 323.54 26.59 119.63\r\nv 322.23 27.66 123.73\r\nv 322.31 27.22 123.8\r\nv 322.61 27.3 124.15\r\nv 322.53 27.73 124.08\r\nv 323.98 27.17 120.18\r\nv 323.9 27.63 120.11\r\nv 323.45 27.03 119.55\r\nv 323.36 27.51 119.48\r\nv 322.06 28.74 123.6\r\nv 322.15 28.16 123.67\r\nv 322.45 28.22 124.02\r\nv 322.37 28.8 123.95\r\nv 323.81 28.14 120.04\r\nv 323.73 28.74 119.98\r\nv 323.27 28.03 119.41\r\nv 323.18 28.64 119.35\r\nv 321.87 30.28 123.48\r\nv 321.97 29.44 123.54\r\nv 322.28 29.49 123.89\r\nv 322.17 30.32 123.83\r\nv 323.63 29.45 119.91\r\nv 323.53 30.29 119.85\r\nv 323.08 29.36 119.28\r\nv 322.98 30.21 119.22\r\nv 321.62 32.48 123.33\r\nv 321.75 31.28 123.4\r\nv 322.06 31.32 123.76\r\nv 321.93 32.52 123.68\r\nv 323.41 31.3 119.78\r\nv 323.28 32.5 119.7\r\nv 322.86 31.23 119.14\r\nv 322.73 32.43 119.07\r\nv 321.31 35.51 123.15\r\nv 321.47 33.9 123.24\r\nv 321.78 33.93 123.59\r\nv 321.62 35.54 123.5\r\nv 323.13 33.92 119.61\r\nv 322.97 35.53 119.52\r\nv 322.58 33.86 118.98\r\nv 322.42 35.47 118.89\r\nv 320.95 39.19 122.94\r\nv 321.13 37.28 123.05\r\nv 321.44 37.32 123.4\r\nv 321.25 39.23 123.29\r\nv 322.79 37.31 119.42\r\nv 322.61 39.22 119.31\r\nv 322.24 37.25 118.79\r\nv 322.06 39.16 118.68\r\nv 320.56 43.31 122.72\r\nv 320.75 41.21 122.83\r\nv 321.06 41.24 123.18\r\nv 320.87 43.34 123.07\r\nv 322.41 41.23 119.2\r\nv 322.22 43.33 119.09\r\nv 321.86 41.17 118.57\r\nv 321.67 43.27 118.46\r\nv 320.17 47.63 122.49\r\nv 320.36 45.45 122.61\r\nv 320.67 45.49 122.96\r\nv 320.48 47.66 122.84\r\nv 322.02 45.47 118.98\r\nv 321.83 47.64 118.86\r\nv 321.47 45.42 118.34\r\nv 321.28 47.59 118.23\r\nv 319.8 51.95 122.26\r\nv 319.99 49.79 122.38\r\nv 320.29 49.82 122.73\r\nv 320.11 51.98 122.62\r\nv 321.65 49.8 118.75\r\nv 321.46 51.96 118.64\r\nv 321.1 49.75 118.12\r\nv 320.91 51.91 118\r\nv 319.43 56.25 122.01\r\nv 319.62 54.11 122.14\r\nv 319.92 54.14 122.49\r\nv 319.74 56.29 122.36\r\nv 321.28 54.11 118.51\r\nv 321.09 56.25 118.38\r\nv 320.73 54.05 117.88\r\nv 320.54 56.19 117.75\r\nv 319.03 60.52 121.69\r\nv 319.24 58.39 121.86\r\nv 319.54 58.43 122.21\r\nv 319.34 60.56 122.05\r\nv 320.9 58.37 118.23\r\nv 320.7 60.49 118.07\r\nv 320.35 58.31 117.6\r\nv 320.15 60.42 117.44\r\nv 318.59 64.73 121.29\r\nv 318.82 62.63 121.51\r\nv 319.12 62.67 121.86\r\nv 318.89 64.78 121.64\r\nv 320.48 62.59 117.88\r\nv 320.25 64.68 117.67\r\nv 319.93 62.51 117.25\r\nv 319.71 64.59 117.04\r\nv 318.07 68.89 120.76\r\nv 318.34 66.82 121.04\r\nv 318.64 66.87 121.39\r\nv 318.37 68.95 121.11\r\nv 320.01 66.74 117.42\r\nv 319.74 68.79 117.15\r\nv 319.46 66.65 116.8\r\nv 319.2 68.69 116.52\r\nv 317.48 72.98 120.09\r\nv 317.79 70.94 120.45\r\nv 318.09 71.01 120.8\r\nv 317.78 73.05 120.44\r\nv 319.46 70.82 116.83\r\nv 319.16 72.83 116.48\r\nv 318.92 70.71 116.21\r\nv 318.62 72.71 115.86\r\nv 316.83 77 119.25\r\nv 317.16 75 119.69\r\nv 317.46 75.07 120.04\r\nv 317.13 77.08 119.59\r\nv 318.85 74.82 116.09\r\nv 318.52 76.79 115.65\r\nv 318.31 74.69 115.47\r\nv 317.98 76.64 115.03\r\nv 316.11 80.93 118.21\r\nv 316.47 78.97 118.76\r\nv 316.77 79.06 119.1\r\nv 316.4 81.02 118.55\r\nv 318.17 78.73 115.17\r\nv 317.81 80.64 114.63\r\nv 317.63 78.57 114.55\r\nv 317.28 80.47 114.02\r\nv 315.32 84.76 116.96\r\nv 315.72 82.86 117.61\r\nv 316.02 82.96 117.95\r\nv 315.62 84.87 117.3\r\nv 317.43 82.53 114.05\r\nv 317.04 84.39 113.41\r\nv 316.9 82.35 113.44\r\nv 316.52 84.19 112.81\r\nv 314.5 88.46 115.5\r\nv 314.91 86.63 116.25\r\nv 315.21 86.75 116.59\r\nv 314.79 88.59 115.83\r\nv 316.64 86.22 112.72\r\nv 316.24 88 111.99\r\nv 316.12 86 112.13\r\nv 315.71 87.78 111.4\r\nv 313.64 91.99 113.85\r\nv 314.07 90.25 114.7\r\nv 314.36 90.39 115.02\r\nv 313.93 92.14 114.17\r\nv 315.82 89.74 111.21\r\nv 315.41 91.43 110.38\r\nv 315.31 89.5 110.62\r\nv 314.89 91.18 109.8\r\nv 312.78 95.32 112\r\nv 313.21 93.69 112.95\r\nv 313.5 93.83 113.27\r\nv 313.06 95.48 112.32\r\nv 314.99 93.07 109.51\r\nv 314.58 94.64 108.6\r\nv 314.48 92.8 108.94\r\nv 314.07 94.36 108.04\r\nv 311.93 98.4 109.98\r\nv 312.35 96.9 111.01\r\nv 312.63 97.06 111.32\r\nv 312.21 98.58 110.29\r\nv 314.17 96.15 107.66\r\nv 313.77 97.58 106.68\r\nv 313.66 95.85 107.1\r\nv 313.27 97.27 106.13\r\nv 311.15 101.19 107.8\r\nv 311.53 99.83 108.91\r\nv 311.81 100.02 109.21\r\nv 311.42 101.38 108.09\r\nv 313.39 98.92 105.67\r\nv 313.04 100.16 104.66\r\nv 312.9 98.6 105.14\r\nv 312.55 99.82 104.14\r\nv 310.51 103.62 105.39\r\nv 310.8 102.45 106.63\r\nv 311.07 102.65 106.91\r\nv 310.79 103.84 105.66\r\nv 312.73 101.27 103.64\r\nv 312.49 102.24 102.62\r\nv 312.24 100.91 103.13\r\nv 312.01 101.87 102.13\r\nv 312.36 103.06 101.6\r\nv 312.36 103.75 100.54\r\nv 311.87 102.69 101.12\r\nv 311.87 103.37 100.06\r\nv 312.51 104.33 99.39\r\nv 312.8 104.84 98.12\r\nv 312.03 103.95 98.92\r\nv 312.31 104.45 97.66\r\nv 311.97 108.26 95.6\r\nv 311.42 107.78 97.41\r\nv 311.7 108 97.66\r\nv 312.24 108.48 95.84\r\nv 310.94 107.21 99.17\r\nv 311.21 107.43 99.42\r\nv 313.2 105.29 96.68\r\nv 313.68 105.71 95.09\r\nv 312.71 104.9 96.24\r\nv 313.18 105.31 94.67\r\nv 313.09 109.09 91.8\r\nv 312.53 108.69 93.73\r\nv 312.81 108.92 93.95\r\nv 313.38 109.32 92.02\r\nv 314.19 106.11 93.37\r\nv 314.74 106.49 91.53\r\nv 313.69 105.71 92.96\r\nv 314.23 106.08 91.15\r\nv 314.23 109.79 87.8\r\nv 313.66 109.45 89.82\r\nv 313.95 109.68 90.02\r\nv 314.51 110.02 87.99\r\nv 315.29 106.84 89.62\r\nv 315.84 107.17 87.64\r\nv 314.77 106.43 89.25\r\nv 315.32 106.75 87.3\r\nv 315.29 110.39 83.69\r\nv 314.77 110.1 85.76\r\nv 315.06 110.33 85.94\r\nv 315.58 110.63 83.86\r\nv 316.37 107.47 85.63\r\nv 316.88 107.76 83.59\r\nv 315.84 107.06 85.3\r\nv 316.36 107.34 83.28\r\nv 316.27 110.92 79.5\r\nv 315.79 110.67 81.6\r\nv 316.09 110.9 81.76\r\nv 316.57 111.15 79.66\r\nv 317.38 108.03 81.52\r\nv 317.85 108.28 79.44\r\nv 316.85 107.61 81.23\r\nv 317.32 107.86 79.17\r\nv 317.15 111.36 75.3\r\nv 316.72 111.15 77.4\r\nv 317.02 111.39 77.54\r\nv 317.45 111.6 75.44\r\nv 318.3 108.51 77.36\r\nv 318.73 108.73 75.27\r\nv 317.76 108.09 77.1\r\nv 318.19 108.3 75.02\r\nv 317.92 111.74 71.13\r\nv 317.55 111.56 73.21\r\nv 317.85 111.8 73.34\r\nv 318.23 111.98 71.25\r\nv 319.13 108.92 73.19\r\nv 319.5 109.1 71.12\r\nv 318.58 108.49 72.96\r\nv 318.95 108.67 70.91\r\nv 318.57 112.04 67.06\r\nv 318.26 111.9 69.08\r\nv 318.57 112.14 69.19\r\nv 318.88 112.28 67.16\r\nv 319.83 109.26 69.08\r\nv 320.14 109.4 67.06\r\nv 319.28 108.83 68.88\r\nv 319.59 108.97 66.88\r\nv 319.1 112.29 63.13\r\nv 318.85 112.17 65.07\r\nv 319.16 112.42 65.17\r\nv 319.41 112.53 63.21\r\nv 320.42 109.53 65.08\r\nv 320.68 109.65 63.13\r\nv 319.87 109.1 64.91\r\nv 320.12 109.21 62.98\r\nv 319.53 112.48 59.38\r\nv 319.33 112.39 61.23\r\nv 319.64 112.63 61.31\r\nv 319.85 112.72 59.46\r\nv 320.9 109.75 61.24\r\nv 321.11 109.84 59.4\r\nv 320.34 109.31 61.1\r\nv 320.55 109.4 59.27\r\nv 319.87 112.63 55.88\r\nv 319.72 112.56 57.6\r\nv 320.03 112.8 57.67\r\nv 320.19 112.87 55.94\r\nv 321.29 109.92 57.62\r\nv 321.45 109.98 55.9\r\nv 320.73 109.48 57.5\r\nv 320.88 109.55 55.79\r\nv 320.13 112.73 52.66\r\nv 320.01 112.68 54.24\r\nv 320.33 112.93 54.29\r\nv 320.45 112.98 52.7\r\nv 321.58 110.04 54.26\r\nv 321.7 110.09 52.68\r\nv 321.02 109.6 54.16\r\nv 321.14 109.65 52.59\r\nv 320.31 112.8 49.63\r\nv 320.23 112.77 51.12\r\nv 320.55 113.02 51.16\r\nv 320.63 113.05 49.66\r\nv 321.8 110.13 51.15\r\nv 321.88 110.16 49.65\r\nv 321.24 109.69 51.07\r\nv 321.32 109.72 49.59\r\nv 320.43 112.84 46.71\r\nv 320.38 112.83 48.16\r\nv 320.69 113.07 48.18\r\nv 320.74 113.09 46.73\r\nv 321.95 110.18 48.19\r\nv 322 110.2 46.74\r\nv 321.38 109.75 48.14\r\nv 321.43 109.76 46.71\r\nv 320.48 112.85 43.81\r\nv 320.46 112.85 45.26\r\nv 320.78 113.09 45.27\r\nv 320.8 113.09 43.81\r\nv 322.03 110.21 45.29\r\nv 322.05 110.21 43.84\r\nv 321.47 109.77 45.27\r\nv 321.49 109.77 43.83\r\nv 320.49 112.83 40.85\r\nv 320.49 112.84 42.34\r\nv 320.81 113.09 42.33\r\nv 320.8 113.07 40.84\r\nv 322.06 110.2 42.37\r\nv 322.06 110.19 40.88\r\nv 321.49 109.76 42.38\r\nv 321.49 109.75 40.9\r\nv 320.43 112.78 37.81\r\nv 320.47 112.81 39.34\r\nv 320.78 113.05 39.32\r\nv 320.75 113.03 37.79\r\nv 322.04 110.17 39.38\r\nv 322 110.14 37.85\r\nv 321.47 109.73 39.41\r\nv 321.44 109.7 37.89\r\nv 320.32 112.7 34.7\r\nv 320.38 112.75 36.27\r\nv 320.7 112.99 36.24\r\nv 320.64 112.95 34.66\r\nv 321.96 110.1 36.31\r\nv 321.89 110.06 34.75\r\nv 321.39 109.67 36.36\r\nv 321.33 109.62 34.81\r\nv 320.15 112.59 31.51\r\nv 320.24 112.65 33.12\r\nv 320.56 112.9 33.07\r\nv 320.46 112.84 31.46\r\nv 321.81 110.01 33.16\r\nv 321.72 109.95 31.56\r\nv 321.25 109.57 33.24\r\nv 321.15 109.51 31.65\r\nv 319.91 112.45 28.24\r\nv 320.04 112.53 29.89\r\nv 320.35 112.77 29.83\r\nv 320.22 112.69 28.18\r\nv 321.61 109.88 29.94\r\nv 321.48 109.81 28.29\r\nv 321.05 109.45 30.04\r\nv 320.92 109.37 28.41\r\nv 319.59 112.27 24.87\r\nv 319.76 112.36 26.57\r\nv 320.08 112.61 26.5\r\nv 319.91 112.51 24.8\r\nv 321.33 109.72 26.63\r\nv 321.17 109.63 24.93\r\nv 320.77 109.29 26.75\r\nv 320.61 109.19 25.08\r\nv 319.19 112.03 21.4\r\nv 319.4 112.16 23.15\r\nv 319.71 112.4 23.07\r\nv 319.5 112.27 21.31\r\nv 320.97 109.52 23.22\r\nv 320.76 109.39 21.47\r\nv 320.42 109.08 23.37\r\nv 320.2 108.96 21.65\r\nv 318.67 111.74 17.82\r\nv 318.94 111.9 19.63\r\nv 319.25 112.14 19.52\r\nv 318.98 111.98 17.71\r\nv 320.52 109.26 19.7\r\nv 320.25 109.1 17.9\r\nv 319.96 108.83 19.89\r\nv 319.7 108.67 18.11\r\nv 318.04 111.38 14.11\r\nv 318.37 111.57 15.98\r\nv 318.68 111.81 15.86\r\nv 318.35 111.62 13.98\r\nv 319.95 108.93 16.07\r\nv 319.62 108.75 14.21\r\nv 319.4 108.51 16.29\r\nv 319.08 108.32 14.45\r\nv 317.29 110.95 10.29\r\nv 317.68 111.18 12.21\r\nv 317.99 111.41 12.07\r\nv 317.59 111.18 10.14\r\nv 319.26 108.54 12.33\r\nv 318.88 108.31 10.42\r\nv 318.72 108.11 12.59\r\nv 318.34 107.89 10.7\r\nv 316.41 110.42 6.38\r\nv 316.87 110.69 8.35\r\nv 317.17 110.93 8.18\r\nv 316.71 110.65 6.2\r\nv 318.46 108.06 8.5\r\nv 318.01 107.79 6.55\r\nv 317.92 107.64 8.79\r\nv 317.48 107.37 6.86\r\nv 315.4 109.78 2.39\r\nv 315.93 110.11 4.39\r\nv 316.22 110.34 4.21\r\nv 315.69 110.01 2.19\r\nv 317.52 107.49 4.59\r\nv 317.01 107.16 2.62\r\nv 317 107.07 4.92\r\nv 316.49 106.75 2.96\r\nv 314.27 109.03 -1.66\r\nv 314.85 109.42 0.36\r\nv 315.14 109.65 0.16\r\nv 314.55 109.25 -1.87\r\nv 316.47 106.81 0.63\r\nv 315.91 106.43 -1.33\r\nv 315.95 106.4 1\r\nv 315.39 106.02 -0.94\r\nv 312.97 108.02 -5.79\r\nv 313.68 108.6 -3.65\r\nv 313.97 108.82 -3.88\r\nv 313.24 108.25 -6.02\r\nv 315.35 106.02 -3.23\r\nv 314.73 105.57 -5.2\r\nv 314.84 105.62 -2.83\r\nv 314.23 105.17 -4.79\r\nv 312.02 107.15 -8.24\r\nv 312.25 107.41 -8.47\r\nv 313.99 105.08 -7.32\r\nv 313.43 104.37 -9.42\r\nv 313.5 104.64 -6.93\r\nv 312.95 103.92 -9.04\r\nv 331.25 0.3 132.02\r\nv 331.54 0.44 132.36\r\nv 267.81 115.22 -17.45\r\nv 267.78 114.61 -17\r\nv 270.07 114.53 -16.95\r\nv 267.76 114.02 -16.57\r\nv 270.02 113.94 -16.51\r\nv 270.11 113.78 -20.98\r\nv 267.8 113.87 -21.04\r\nv 267.81 114.2 -21.28\r\nv 270.13 114.11 -21.22\r\nv 267.82 114.5 -20.94\r\nv 272.36 114.39 -16.86\r\nv 274.65 114.21 -16.73\r\nv 272.29 113.81 -16.42\r\nv 274.57 113.62 -16.3\r\nv 274.73 113.46 -20.77\r\nv 272.41 113.65 -20.89\r\nv 272.45 113.97 -21.13\r\nv 274.78 113.78 -21.01\r\nv 276.95 113.97 -16.57\r\nv 279.26 113.68 -16.38\r\nv 276.85 113.39 -16.14\r\nv 279.13 113.1 -15.95\r\nv 279.36 112.93 -20.41\r\nv 277.04 113.22 -20.61\r\nv 277.1 113.54 -20.85\r\nv 279.43 113.25 -20.65\r\nv 281.56 113.35 -16.16\r\nv 283.88 112.97 -15.9\r\nv 281.42 112.77 -15.73\r\nv 283.72 112.39 -15.47\r\nv 284.02 112.21 -19.93\r\nv 281.69 112.59 -20.19\r\nv 281.77 112.92 -20.42\r\nv 284.11 112.53 -20.17\r\nv 286.2 112.54 -15.61\r\nv 288.52 112.08 -15.3\r\nv 286.02 111.97 -15.19\r\nv 288.33 111.51 -14.87\r\nv 288.69 111.31 -19.32\r\nv 286.36 111.78 -19.64\r\nv 286.46 112.1 -19.87\r\nv 288.8 111.63 -19.55\r\nv 290.82 111.58 -14.95\r\nv 293.1 111.04 -14.58\r\nv 290.61 111.01 -14.53\r\nv 292.88 110.48 -14.16\r\nv 293.31 110.27 -18.6\r\nv 291.01 110.81 -18.97\r\nv 291.13 111.12 -19.21\r\nv 293.43 110.58 -18.83\r\nv 295.34 110.48 -14.19\r\nv 297.54 109.9 -13.77\r\nv 295.11 109.92 -13.77\r\nv 297.29 109.34 -13.36\r\nv 297.78 109.12 -17.79\r\nv 295.57 109.71 -18.2\r\nv 295.7 110.02 -18.43\r\nv 297.92 109.43 -18.02\r\nv 299.67 109.3 -13.34\r\nv 301.74 108.68 -12.89\r\nv 299.41 108.75 -12.93\r\nv 301.47 108.14 -12.48\r\nv 302.03 107.89 -16.89\r\nv 299.94 108.51 -17.35\r\nv 300.08 108.82 -17.58\r\nv 302.18 108.2 -17.12\r\nv 303.73 108.06 -12.42\r\nv 305.81 107.36 -11.88\r\nv 303.44 107.52 -12.02\r\nv 305.5 106.82 -11.48\r\nv 305.97 106.62 -15.93\r\nv 304.04 107.26 -16.42\r\nv 304.21 107.56 -16.64\r\nv 306.14 106.92 -16.16\r\nv 308.13 106.52 -11.2\r\nv 310.22 106.1 -9.98\r\nv 307.81 105.99 -10.8\r\nv 309.34 105.42 -14.97\r\nv 307.79 105.99 -15.43\r\nv 307.97 106.29 -15.65\r\nv 309.54 105.71 -15.18\r\nv 311.19 104.53 -14.55\r\nv 310.48 104.97 -14.59\r\nv 310.71 105.25 -14.8\r\nv 311.45 104.79 -14.76\r\nv 313.34 103.28 -11.3\r\nv 313.53 102.07 -12.88\r\nv 312.86 102.86 -10.89\r\nv 313.04 101.69 -12.42\r\nv 311.65 103.25 -16.02\r\nv 311.49 104 -15.09\r\nv 311.76 104.23 -15.32\r\nv 311.92 103.46 -16.28\r\nv 313.81 100.97 -14.1\r\nv 314.16 99.86 -15.13\r\nv 313.31 100.61 -13.62\r\nv 313.66 99.51 -14.63\r\nv 312.35 100.9 -18.33\r\nv 311.95 102.18 -17.14\r\nv 312.23 102.38 -17.41\r\nv 312.63 101.09 -18.6\r\nv 314.55 98.63 -16.15\r\nv 314.98 97.29 -17.15\r\nv 314.04 98.3 -15.64\r\nv 314.47 96.97 -16.63\r\nv 313.24 98.11 -20.53\r\nv 312.78 99.55 -19.46\r\nv 313.06 99.73 -19.74\r\nv 313.52 98.29 -20.82\r\nv 315.43 95.87 -18.12\r\nv 315.89 94.37 -19.05\r\nv 314.91 95.56 -17.59\r\nv 315.37 94.08 -18.51\r\nv 314.2 95.04 -22.53\r\nv 313.71 96.61 -21.55\r\nv 314 96.78 -21.85\r\nv 314.49 95.2 -22.83\r\nv 316.36 92.8 -19.95\r\nv 316.83 91.17 -20.8\r\nv 315.84 92.52 -19.4\r\nv 316.3 90.91 -20.25\r\nv 315.18 91.72 -24.35\r\nv 314.69 93.41 -23.46\r\nv 314.98 93.56 -23.77\r\nv 315.47 91.87 -24.65\r\nv 317.31 89.49 -21.61\r\nv 317.77 87.76 -22.39\r\nv 316.77 89.24 -21.06\r\nv 317.23 87.52 -21.82\r\nv 316.14 88.22 -25.98\r\nv 315.66 89.99 -25.19\r\nv 315.96 90.13 -25.5\r\nv 316.44 88.35 -26.29\r\nv 318.23 85.98 -23.12\r\nv 318.68 84.17 -23.81\r\nv 317.69 85.76 -22.55\r\nv 318.13 83.96 -23.23\r\nv 317.06 84.55 -27.43\r\nv 316.61 86.4 -26.73\r\nv 316.91 86.52 -27.04\r\nv 317.37 84.66 -27.75\r\nv 319.11 82.32 -24.46\r\nv 319.52 80.44 -25.06\r\nv 318.56 82.12 -23.88\r\nv 318.97 80.25 -24.48\r\nv 317.92 80.75 -28.71\r\nv 317.5 82.66 -28.09\r\nv 317.81 82.77 -28.41\r\nv 318.23 80.85 -29.03\r\nv 319.92 78.53 -25.63\r\nv 320.29 76.6 -26.16\r\nv 319.36 78.36 -25.04\r\nv 319.73 76.44 -25.57\r\nv 318.7 76.86 -29.82\r\nv 318.32 78.82 -29.28\r\nv 318.63 78.91 -29.61\r\nv 319.01 76.95 -30.14\r\nv 320.64 74.64 -26.65\r\nv 320.96 72.66 -27.11\r\nv 320.08 74.49 -26.06\r\nv 320.4 72.52 -26.52\r\nv 319.38 72.89 -30.78\r\nv 319.05 74.89 -30.31\r\nv 319.36 74.97 -30.64\r\nv 319.69 72.97 -31.11\r\nv 321.27 70.66 -27.55\r\nv 321.54 68.64 -27.95\r\nv 320.71 70.53 -26.95\r\nv 320.98 68.52 -27.35\r\nv 319.97 68.85 -31.63\r\nv 319.69 70.88 -31.21\r\nv 320 70.95 -31.55\r\nv 320.28 68.92 -31.96\r\nv 321.8 66.6 -28.34\r\nv 322.03 64.54 -28.71\r\nv 321.24 66.49 -27.74\r\nv 321.47 64.44 -28.11\r\nv 320.45 64.75 -32.39\r\nv 320.22 66.81 -32.02\r\nv 320.54 66.87 -32.35\r\nv 320.77 64.8 -32.72\r\nv 322.24 62.46 -29.07\r\nv 322.42 60.37 -29.42\r\nv 321.68 62.37 -28.47\r\nv 321.86 60.28 -28.82\r\nv 320.84 60.58 -33.09\r\nv 320.66 62.67 -32.74\r\nv 320.97 62.72 -33.08\r\nv 321.16 60.63 -33.43\r\nv 322.59 58.26 -29.77\r\nv 322.73 56.14 -30.13\r\nv 322.02 58.18 -29.17\r\nv 322.17 56.06 -29.52\r\nv 321.15 56.39 -33.8\r\nv 321.01 58.49 -33.44\r\nv 321.32 58.54 -33.78\r\nv 321.47 56.43 -34.13\r\nv 322.86 54.01 -30.5\r\nv 322.98 51.87 -30.89\r\nv 322.3 53.93 -29.89\r\nv 322.41 51.79 -30.28\r\nv 321.39 52.16 -34.54\r\nv 321.28 54.28 -34.16\r\nv 321.59 54.32 -34.5\r\nv 321.71 52.21 -34.88\r\nv 323.08 49.73 -31.3\r\nv 323.18 47.58 -31.75\r\nv 322.52 49.64 -30.7\r\nv 322.61 47.49 -31.14\r\nv 321.59 47.93 -35.39\r\nv 321.5 50.05 -34.95\r\nv 321.81 50.09 -35.29\r\nv 321.91 47.98 -35.73\r\nv 323.27 45.43 -32.23\r\nv 323.35 43.3 -32.75\r\nv 322.7 45.34 -31.63\r\nv 322.78 43.21 -32.14\r\nv 321.76 43.72 -36.38\r\nv 321.68 45.81 -35.87\r\nv 321.99 45.86 -36.21\r\nv 322.07 43.77 -36.71\r\nv 323.42 41.24 -33.27\r\nv 323.49 39.25 -33.81\r\nv 322.86 41.14 -32.67\r\nv 322.93 39.15 -33.2\r\nv 321.9 39.72 -37.42\r\nv 321.83 41.68 -36.9\r\nv 322.15 41.73 -37.23\r\nv 322.21 39.77 -37.76\r\nv 323.55 37.38 -34.33\r\nv 323.61 35.65 -34.83\r\nv 322.99 37.28 -33.73\r\nv 323.05 35.54 -34.23\r\nv 322.02 36.15 -38.44\r\nv 321.96 37.87 -37.94\r\nv 322.28 37.92 -38.28\r\nv 322.33 36.21 -38.77\r\nv 323.66 34.08 -35.31\r\nv 323.71 32.7 -35.73\r\nv 323.1 33.97 -34.71\r\nv 323.15 32.58 -35.14\r\nv 322.12 33.25 -39.33\r\nv 322.07 34.6 -38.91\r\nv 322.38 34.66 -39.24\r\nv 322.43 33.31 -39.66\r\nv 323.75 31.54 -36.11\r\nv 323.79 30.57 -36.44\r\nv 323.19 31.42 -35.51\r\nv 323.23 30.44 -35.85\r\nv 322.19 31.18 -40.01\r\nv 322.16 32.11 -39.69\r\nv 322.47 32.18 -40.03\r\nv 322.51 31.25 -40.34\r\nv 323.83 29.76 -36.74\r\nv 323.86 29.09 -37.01\r\nv 323.26 29.62 -36.15\r\nv 323.3 28.93 -36.43\r\nv 322.26 29.81 -40.54\r\nv 322.23 30.42 -40.29\r\nv 322.54 30.5 -40.62\r\nv 322.57 29.9 -40.86\r\nv 323.9 28.52 -37.27\r\nv 323.93 28.04 -37.52\r\nv 323.34 28.34 -36.7\r\nv 323.38 27.83 -36.97\r\nv 322.32 28.94 -40.96\r\nv 322.29 29.33 -40.76\r\nv 322.6 29.43 -41.07\r\nv 322.63 29.05 -41.27\r\nv 323.98 27.62 -37.78\r\nv 324.02 27.23 -38.04\r\nv 323.42 27.39 -37.24\r\nv 323.47 26.98 -37.52\r\nv 322.39 28.3 -41.37\r\nv 322.35 28.6 -41.16\r\nv 322.66 28.73 -41.47\r\nv 322.69 28.44 -41.66\r\nv 324.07 26.86 -38.33\r\nv 324.13 26.48 -38.64\r\nv 323.52 26.59 -37.82\r\nv 323.58 26.19 -38.14\r\nv 322.47 27.71 -41.84\r\nv 322.43 28.01 -41.59\r\nv 322.73 28.16 -41.88\r\nv 322.78 27.87 -42.12\r\nv 324.19 26.11 -38.99\r\nv 324.26 25.72 -39.37\r\nv 323.64 25.8 -38.51\r\nv 323.72 25.4 -38.91\r\nv 322.58 27.09 -42.43\r\nv 322.52 27.41 -42.12\r\nv 322.82 27.58 -42.39\r\nv 322.88 27.27 -42.69\r\nv 324.33 25.32 -39.8\r\nv 324.42 24.9 -40.29\r\nv 323.79 24.98 -39.35\r\nv 323.88 24.55 -39.85\r\nv 322.71 26.39 -43.22\r\nv 322.64 26.76 -42.8\r\nv 322.94 26.94 -43.05\r\nv 323.01 26.58 -43.46\r\nv 324.51 24.45 -40.83\r\nv 324.61 23.97 -41.44\r\nv 323.97 24.09 -40.4\r\nv 324.07 23.61 -41.02\r\nv 322.89 25.54 -44.27\r\nv 322.79 25.99 -43.71\r\nv 323.09 26.18 -43.95\r\nv 323.18 25.75 -44.5\r\nv 324.71 23.44 -42.12\r\nv 324.83 22.89 -42.87\r\nv 324.18 23.08 -41.7\r\nv 324.3 22.51 -42.46\r\nv 323.1 24.53 -45.61\r\nv 322.99 25.06 -44.9\r\nv 323.28 25.26 -45.13\r\nv 323.39 24.74 -45.84\r\nv 324.95 22.3 -43.69\r\nv 325.08 21.69 -44.57\r\nv 324.42 21.92 -43.29\r\nv 324.55 21.3 -44.18\r\nv 323.33 23.39 -47.23\r\nv 323.21 23.98 -46.39\r\nv 323.51 24.19 -46.61\r\nv 323.63 23.61 -47.44\r\nv 325.22 21.06 -45.52\r\nv 325.36 20.41 -46.52\r\nv 324.69 20.67 -45.14\r\nv 324.83 20.02 -46.15\r\nv 323.6 22.18 -49.07\r\nv 323.46 22.8 -48.12\r\nv 323.76 23.01 -48.33\r\nv 323.89 22.4 -49.28\r\nv 325.5 19.76 -47.57\r\nv 325.65 19.11 -48.67\r\nv 324.98 19.36 -47.21\r\nv 325.12 18.7 -48.32\r\nv 323.87 20.95 -51.12\r\nv 323.73 21.57 -50.07\r\nv 324.03 21.79 -50.27\r\nv 324.16 21.17 -51.31\r\nv 325.79 18.46 -49.81\r\nv 325.61 18.17 -50.93\r\nv 325.27 18.06 -49.46\r\nv 324.41 19.68 -52.8\r\nv 324.01 20.33 -52.21\r\nv 324.3 20.55 -52.41\r\nv 324.7 19.91 -52.99\r\nv 325.32 18.98 -52.33\r\nv 325.61 19.21 -52.52\r\nv 329.15 1.58 132.26\r\nv 328.7 3.12 131.64\r\nv 329.76 0.46 132.35\r\nv 328.21 4.78 130.97\r\nv 327.68 6.56 130.27\r\nv 327.12 8.43 129.55\r\nv 326.55 10.36 128.81\r\nv 325.96 12.32 128.08\r\nv 325.39 14.25 127.36\r\nv 324.83 16.12 126.67\r\nv 324.31 17.89 126.03\r\nv 323.82 19.51 125.45\r\nv 323.4 20.95 124.95\r\nv 323.05 22.16 124.53\r\nv 322.75 23.17 124.19\r\nv 322.51 24.01 123.92\r\nv 322.32 24.7 123.7\r\nv 322.16 25.29 123.54\r\nv 322.04 25.79 123.41\r\nv 321.93 26.23 123.31\r\nv 321.84 26.66 123.22\r\nv 321.75 27.08 123.15\r\nv 321.67 27.54 123.08\r\nv 321.59 28.05 123.01\r\nv 321.5 28.64 122.95\r\nv 321.4 29.35 122.89\r\nv 321.3 30.2 122.82\r\nv 321.18 31.21 122.75\r\nv 321.05 32.41 122.67\r\nv 320.9 33.83 122.58\r\nv 320.74 35.44 122.49\r\nv 320.56 37.22 122.39\r\nv 320.38 39.13 122.28\r\nv 320.18 41.15 122.17\r\nv 319.99 43.25 122.06\r\nv 319.79 45.4 121.95\r\nv 319.6 47.57 121.83\r\nv 319.41 49.74 121.72\r\nv 319.23 51.9 121.61\r\nv 319.05 54.05 121.48\r\nv 318.86 56.19 121.35\r\nv 318.66 58.32 121.2\r\nv 318.46 60.44 121.04\r\nv 318.25 62.55 120.85\r\nv 318.02 64.64 120.64\r\nv 317.77 66.72 120.39\r\nv 317.5 68.78 120.11\r\nv 317.22 70.82 119.8\r\nv 316.92 72.85 119.44\r\nv 316.6 74.86 119.05\r\nv 316.27 76.84 118.61\r\nv 315.92 78.81 118.12\r\nv 315.55 80.75 117.58\r\nv 315.17 82.66 116.98\r\nv 314.77 84.55 116.33\r\nv 314.37 86.41 115.63\r\nv 313.95 88.23 114.88\r\nv 313.53 90 114.09\r\nv 313.11 91.73 113.24\r\nv 312.68 93.41 112.35\r\nv 312.25 95.03 111.42\r\nv 311.83 96.59 110.44\r\nv 311.41 98.08 109.42\r\nv 311.02 99.5 108.36\r\nv 310.64 100.83 107.25\r\nv 310.29 102.08 106.1\r\nv 309.98 103.18 104.91\r\nv 310.91 107.37 96.95\r\nv 311.45 107.84 95.16\r\nv 310.44 106.81 98.7\r\nv 312 108.27 93.31\r\nv 312.56 108.66 91.41\r\nv 313.13 109.02 89.45\r\nv 313.69 109.36 87.44\r\nv 314.22 109.67 85.42\r\nv 314.74 109.96 83.36\r\nv 315.24 110.23 81.29\r\nv 315.71 110.48 79.21\r\nv 316.16 110.71 77.13\r\nv 316.58 110.92 75.04\r\nv 316.98 111.11 72.97\r\nv 317.35 111.29 70.91\r\nv 317.69 111.45 68.88\r\nv 317.99 111.59 66.87\r\nv 318.27 111.72 64.9\r\nv 318.52 111.84 62.97\r\nv 318.75 111.94 61.08\r\nv 318.95 112.03 59.25\r\nv 319.13 112.1 57.48\r\nv 319.29 112.17 55.77\r\nv 319.43 112.23 54.14\r\nv 319.54 112.28 52.57\r\nv 319.64 112.32 51.05\r\nv 319.72 112.35 49.57\r\nv 319.79 112.37 48.11\r\nv 319.84 112.39 46.67\r\nv 319.87 112.39 45.24\r\nv 319.89 112.39 43.8\r\nv 319.9 112.39 42.34\r\nv 319.9 112.37 40.86\r\nv 319.88 112.35 39.37\r\nv 319.84 112.33 37.85\r\nv 319.8 112.29 36.32\r\nv 319.73 112.25 34.77\r\nv 319.65 112.2 33.19\r\nv 319.56 112.14 31.6\r\nv 319.45 112.07 29.99\r\nv 319.32 112 28.36\r\nv 319.18 111.91 26.7\r\nv 319.01 111.82 25.02\r\nv 318.82 111.71 23.32\r\nv 318.61 111.58 21.58\r\nv 318.37 111.45 19.82\r\nv 318.1 111.3 18.03\r\nv 317.8 111.13 16.21\r\nv 317.48 110.94 14.36\r\nv 317.12 110.73 12.48\r\nv 316.73 110.51 10.58\r\nv 316.31 110.26 8.65\r\nv 315.86 109.98 6.7\r\nv 315.38 109.68 4.74\r\nv 314.86 109.35 2.75\r\nv 314.31 109 0.75\r\nv 313.74 108.61 -1.25\r\nv 313.16 108.18 -3.23\r\nv 312.47 107.59 -5.35\r\nv 311.58 106.68 -7.82\r\nv 330.72 0.05 131.41\r\nv 267.76 112.67 -20.15\r\nv 267.78 113.25 -20.59\r\nv 270.06 113.17 -20.53\r\nv 272.35 113.04 -20.44\r\nv 274.64 112.85 -20.32\r\nv 276.93 112.61 -20.16\r\nv 279.23 112.33 -19.97\r\nv 281.54 111.99 -19.74\r\nv 283.85 111.61 -19.49\r\nv 286.17 111.19 -19.2\r\nv 288.49 110.72 -18.88\r\nv 290.8 110.22 -18.54\r\nv 293.08 109.69 -18.17\r\nv 295.32 109.12 -17.77\r\nv 297.52 108.54 -17.36\r\nv 299.67 107.94 -16.92\r\nv 301.74 107.32 -16.47\r\nv 303.74 106.69 -16\r\nv 305.65 106.06 -15.52\r\nv 307.45 105.44 -15.02\r\nv 308.98 104.88 -14.57\r\nv 310.05 104.45 -14.21\r\nv 310.71 104.05 -14.17\r\nv 310.98 103.56 -14.67\r\nv 311.13 102.85 -15.54\r\nv 311.43 101.8 -16.64\r\nv 311.83 100.54 -17.82\r\nv 312.25 99.2 -18.93\r\nv 312.7 97.78 -19.99\r\nv 313.18 96.29 -21.01\r\nv 313.66 94.73 -21.97\r\nv 314.14 93.12 -22.9\r\nv 314.63 91.45 -23.77\r\nv 315.11 89.74 -24.6\r\nv 315.58 87.97 -25.39\r\nv 316.05 86.17 -26.14\r\nv 316.5 84.33 -26.83\r\nv 316.94 82.46 -27.49\r\nv 317.35 80.56 -28.1\r\nv 317.75 78.64 -28.67\r\nv 318.12 76.69 -29.2\r\nv 318.47 74.73 -29.7\r\nv 318.8 72.75 -30.16\r\nv 319.1 70.75 -30.59\r\nv 319.38 68.73 -31\r\nv 319.64 66.69 -31.39\r\nv 319.87 64.64 -31.76\r\nv 320.07 62.57 -32.12\r\nv 320.26 60.49 -32.46\r\nv 320.42 58.4 -32.81\r\nv 320.56 56.3 -33.17\r\nv 320.69 54.19 -33.53\r\nv 320.81 52.08 -33.91\r\nv 320.91 49.96 -34.32\r\nv 321 47.84 -34.76\r\nv 321.09 45.72 -35.24\r\nv 321.17 43.62 -35.75\r\nv 321.25 41.58 -36.27\r\nv 321.31 39.62 -36.8\r\nv 321.38 37.76 -37.32\r\nv 321.43 36.04 -37.82\r\nv 321.48 34.49 -38.28\r\nv 321.53 33.13 -38.71\r\nv 321.57 31.99 -39.08\r\nv 321.61 31.04 -39.4\r\nv 321.64 30.27 -39.68\r\nv 321.67 29.65 -39.93\r\nv 321.71 29.14 -40.16\r\nv 321.74 28.72 -40.38\r\nv 321.77 28.37 -40.6\r\nv 321.81 28.05 -40.82\r\nv 321.85 27.73 -41.06\r\nv 321.9 27.41 -41.32\r\nv 321.95 27.09 -41.62\r\nv 322.01 26.76 -41.95\r\nv 322.08 26.41 -42.33\r\nv 322.15 26.03 -42.76\r\nv 322.24 25.62 -43.26\r\nv 322.33 25.17 -43.83\r\nv 322.43 24.68 -44.47\r\nv 322.54 24.14 -45.19\r\nv 322.66 23.58 -45.97\r\nv 322.78 23 -46.82\r\nv 322.91 22.39 -47.72\r\nv 323.05 21.77 -48.68\r\nv 323.19 21.15 -49.69\r\nv 323.33 20.53 -50.75\r\nv 323.47 19.9 -51.85\r\nv 323.87 19.25 -52.45\r\nv 324.78 18.55 -51.97\r\nv 330.69 1.15 128.49\r\nv 330.24 2.7 127.86\r\nv 329.75 4.37 127.19\r\nv 329.22 6.15 126.49\r\nv 328.65 8.04 125.76\r\nv 328.07 9.98 125.02\r\nv 327.49 11.95 124.28\r\nv 326.91 13.89 123.56\r\nv 326.35 15.77 122.87\r\nv 325.82 17.54 122.23\r\nv 325.34 19.17 121.65\r\nv 324.91 20.62 121.14\r\nv 324.55 21.84 120.72\r\nv 324.26 22.86 120.37\r\nv 324.01 23.72 120.09\r\nv 323.81 24.45 119.87\r\nv 323.65 25.06 119.69\r\nv 323.51 25.6 119.56\r\nv 323.4 26.07 119.45\r\nv 323.3 26.52 119.36\r\nv 323.21 26.97 119.28\r\nv 323.12 27.45 119.21\r\nv 323.03 27.99 119.14\r\nv 322.94 28.6 119.07\r\nv 322.85 29.32 119.01\r\nv 322.74 30.18 118.94\r\nv 322.62 31.2 118.87\r\nv 322.49 32.4 118.79\r\nv 322.34 33.83 118.7\r\nv 322.18 35.44 118.61\r\nv 322 37.22 118.51\r\nv 321.82 39.13 118.4\r\nv 321.62 41.15 118.29\r\nv 321.43 43.25 118.18\r\nv 321.23 45.39 118.07\r\nv 321.04 47.56 117.95\r\nv 320.85 49.73 117.84\r\nv 320.67 51.88 117.73\r\nv 320.49 54.03 117.61\r\nv 320.3 56.16 117.47\r\nv 320.11 58.28 117.33\r\nv 319.91 60.39 117.16\r\nv 319.69 62.48 116.97\r\nv 319.47 64.55 116.76\r\nv 319.22 66.61 116.52\r\nv 318.96 68.64 116.24\r\nv 318.68 70.66 115.93\r\nv 318.38 72.65 115.59\r\nv 318.07 74.63 115.2\r\nv 317.74 76.57 114.76\r\nv 317.4 78.5 114.28\r\nv 317.04 80.39 113.75\r\nv 316.67 82.27 113.17\r\nv 316.28 84.11 112.54\r\nv 315.89 85.91 111.86\r\nv 315.49 87.68 111.14\r\nv 315.08 89.4 110.36\r\nv 314.67 91.07 109.55\r\nv 314.26 92.68 108.69\r\nv 313.85 94.24 107.79\r\nv 313.44 95.72 106.86\r\nv 313.05 97.13 105.9\r\nv 312.68 98.45 104.91\r\nv 312.33 99.67 103.91\r\nv 312.03 100.76 102.91\r\nv 311.79 101.71 101.91\r\nv 311.66 102.52 100.91\r\nv 311.66 103.21 99.85\r\nv 311.81 103.78 98.72\r\nv 312.1 104.28 97.46\r\nv 312.49 104.73 96.05\r\nv 312.96 105.14 94.49\r\nv 313.47 105.53 92.79\r\nv 314 105.9 90.98\r\nv 314.55 106.25 89.1\r\nv 315.09 106.57 87.15\r\nv 315.61 106.87 85.16\r\nv 316.12 107.16 83.14\r\nv 316.61 107.42 81.1\r\nv 317.08 107.67 79.04\r\nv 317.53 107.9 76.98\r\nv 317.95 108.11 74.92\r\nv 318.34 108.3 72.86\r\nv 318.71 108.48 70.82\r\nv 319.04 108.64 68.79\r\nv 319.35 108.78 66.8\r\nv 319.62 108.91 64.84\r\nv 319.87 109.02 62.91\r\nv 320.1 109.12 61.04\r\nv 320.3 109.21 59.21\r\nv 320.48 109.29 57.45\r\nv 320.64 109.36 55.75\r\nv 320.77 109.41 54.12\r\nv 320.89 109.46 52.56\r\nv 320.99 109.5 51.04\r\nv 321.07 109.53 49.57\r\nv 321.13 109.55 48.12\r\nv 321.18 109.57 46.69\r\nv 321.22 109.58 45.26\r\nv 321.24 109.58 43.83\r\nv 321.25 109.57 42.38\r\nv 321.24 109.56 40.91\r\nv 321.22 109.54 39.42\r\nv 321.19 109.51 37.91\r\nv 321.14 109.47 36.38\r\nv 321.08 109.43 34.84\r\nv 321 109.38 33.27\r\nv 320.91 109.32 31.69\r\nv 320.8 109.26 30.08\r\nv 320.67 109.18 28.45\r\nv 320.53 109.1 26.81\r\nv 320.36 109 25.14\r\nv 320.17 108.89 23.44\r\nv 319.96 108.77 21.72\r\nv 319.72 108.64 19.97\r\nv 319.45 108.49 18.2\r\nv 319.16 108.32 16.39\r\nv 318.84 108.13 14.56\r\nv 318.48 107.93 12.7\r\nv 318.1 107.7 10.82\r\nv 317.69 107.46 8.92\r\nv 317.24 107.19 7\r\nv 316.77 106.89 5.07\r\nv 316.26 106.57 3.12\r\nv 315.72 106.22 1.16\r\nv 315.17 105.85 -0.77\r\nv 314.62 105.44 -2.65\r\nv 314.01 104.99 -4.61\r\nv 313.29 104.46 -6.76\r\nv 312.74 103.73 -8.88\r\nv 267.75 113.77 -16.38\r\nv 270 113.68 -16.32\r\nv 267.75 113.53 -16.77\r\nv 272.26 113.55 -16.23\r\nv 274.53 113.37 -16.11\r\nv 276.8 113.13 -15.95\r\nv 279.08 112.85 -15.76\r\nv 281.36 112.52 -15.54\r\nv 283.65 112.14 -15.29\r\nv 285.95 111.72 -15\r\nv 288.24 111.26 -14.69\r\nv 290.52 110.76 -14.35\r\nv 292.78 110.23 -13.98\r\nv 295 109.68 -13.59\r\nv 297.18 109.1 -13.18\r\nv 299.3 108.5 -12.75\r\nv 301.35 107.9 -12.31\r\nv 303.31 107.28 -11.84\r\nv 305.37 106.59 -11.31\r\nv 307.67 105.76 -10.63\r\nv 312.64 102.68 -10.71\r\nv 312.82 101.52 -12.22\r\nv 313.09 100.45 -13.4\r\nv 313.44 99.36 -14.42\r\nv 313.82 98.15 -15.42\r\nv 314.24 96.83 -16.4\r\nv 314.68 95.43 -17.36\r\nv 315.14 93.95 -18.28\r\nv 315.61 92.4 -19.16\r\nv 316.07 90.8 -20.01\r\nv 316.54 89.13 -20.81\r\nv 317 87.42 -21.58\r\nv 317.45 85.66 -22.3\r\nv 317.89 83.87 -22.98\r\nv 318.32 82.03 -23.63\r\nv 318.73 80.17 -24.23\r\nv 319.12 78.28 -24.79\r\nv 319.49 76.37 -25.31\r\nv 319.84 74.43 -25.8\r\nv 320.16 72.46 -26.26\r\nv 320.46 70.48 -26.69\r\nv 320.74 68.47 -27.09\r\nv 320.99 66.44 -27.48\r\nv 321.22 64.4 -27.85\r\nv 321.43 62.33 -28.2\r\nv 321.61 60.24 -28.55\r\nv 321.77 58.14 -28.9\r\nv 321.92 56.02 -29.26\r\nv 322.05 53.9 -29.63\r\nv 322.17 51.76 -30.01\r\nv 322.27 49.61 -30.43\r\nv 322.37 47.45 -30.88\r\nv 322.45 45.3 -31.36\r\nv 322.54 43.17 -31.88\r\nv 322.61 41.1 -32.41\r\nv 322.68 39.11 -32.94\r\nv 322.74 37.23 -33.47\r\nv 322.8 35.49 -33.97\r\nv 322.85 33.92 -34.44\r\nv 322.9 32.53 -34.88\r\nv 322.94 31.36 -35.25\r\nv 322.98 30.38 -35.59\r\nv 323.02 29.56 -35.89\r\nv 323.05 28.86 -36.18\r\nv 323.09 28.26 -36.45\r\nv 323.13 27.75 -36.72\r\nv 323.18 27.29 -37\r\nv 323.22 26.87 -37.29\r\nv 323.28 26.47 -37.59\r\nv 323.34 26.07 -37.93\r\nv 323.41 25.67 -38.29\r\nv 323.48 25.26 -38.7\r\nv 323.56 24.83 -39.16\r\nv 323.64 24.4 -39.66\r\nv 323.74 23.94 -40.21\r\nv 323.84 23.45 -40.83\r\nv 323.95 22.92 -41.52\r\nv 324.06 22.35 -42.29\r\nv 324.19 21.76 -43.12\r\nv 324.32 21.14 -44.01\r\nv 324.46 20.5 -44.97\r\nv 324.6 19.84 -45.98\r\nv 324.74 19.18 -47.05\r\nv 324.89 18.52 -48.16\r\nv 325.04 17.88 -49.31\r\nv 310.84 106.75 101.15\r\nv 310.57 106.54 100.89\r\nv 310.35 105.7 102.53\r\nv 310.63 105.93 102.78\r\nv 310.33 104.66 104.05\r\nv 310.62 104.95 104.28\r\nv 310.07 106.14 100.4\r\nv 309.83 105.25 102.06\r\nv 309.58 105.75 99.93\r\nv 309.36 104.92 101.55\r\nv 309.74 104.04 103.66\r\nv 309.32 103.89 103.05\r\nv 309.14 103.8 102.8\r\nv 309.15 104.77 101.33\r\nv 309.36 105.58 99.73\r\nv 267.82 115.06 -19.63\r\nv 267.82 115.52 -18.25\r\nv 267.82 115.55 -17.7\r\nv 267.74 112.99 -18.1\r\nv 267.74 112.51 -19.46\r\nv 267.75 112.41 -19.96\r\nvn 0.6 -0.43 -0.67\r\nvn 0.58 -0.39 -0.72\r\nvn 0.68 -0.37 -0.64\r\nvn 0.64 -0.48 -0.6\r\nvn 0.59 -0.36 -0.72\r\nvn 0.68 -0.36 -0.64\r\nvn 0.76 -0.35 -0.55\r\nvn 0.75 -0.38 -0.53\r\nvn 0.71 -0.42 -0.56\r\nvn 0.93 -0.3 0.22\r\nvn 0.67 -0.71 0.21\r\nvn 0.97 -0.2 0.16\r\nvn 0.69 0.03 0.72\r\nvn 0.65 -0.11 0.75\r\nvn 0.34 -0.62 0.71\r\nvn -0.59 0.22 0.78\r\nvn -0.5 0.3 0.81\r\nvn -0.68 0.34 0.65\r\nvn -0.72 0.12 0.69\r\nvn -0.5 0.36 0.79\r\nvn -0.68 0.36 0.64\r\nvn -0.82 0.34 0.46\r\nvn -0.84 0.28 0.46\r\nvn -0.81 0.21 0.55\r\nvn -0.97 0.05 -0.24\r\nvn -0.93 -0.37 0.06\r\nvn -0.95 0.17 -0.25\r\nvn -0.69 -0.03 -0.72\r\nvn -0.72 -0.17 -0.67\r\nvn -0.67 -0.66 -0.34\r\nvn 0.59 -0.36 -0.72\r\nvn 0.59 -0.36 -0.72\r\nvn 0.68 -0.36 -0.64\r\nvn 0.76 -0.35 -0.55\r\nvn 0.76 -0.36 -0.55\r\nvn 0.97 -0.21 0.16\r\nvn 0.96 -0.21 0.16\r\nvn 0.69 0.02 0.72\r\nvn 0.69 0.02 0.72\r\nvn -0.5 0.36 0.79\r\nvn -0.5 0.35 0.79\r\nvn -0.68 0.36 0.64\r\nvn -0.82 0.34 0.46\r\nvn -0.82 0.35 0.46\r\nvn -0.95 0.18 -0.25\r\nvn -0.95 0.18 -0.25\r\nvn -0.69 -0.02 -0.72\r\nvn -0.69 -0.02 -0.72\r\nvn 0.59 -0.36 -0.72\r\nvn 0.68 -0.36 -0.64\r\nvn 0.59 -0.35 -0.72\r\nvn 0.68 -0.35 -0.64\r\nvn 0.76 -0.35 -0.55\r\nvn 0.76 -0.35 -0.55\r\nvn 0.96 -0.21 0.16\r\nvn 0.96 -0.21 0.16\r\nvn 0.69 0.01 0.72\r\nvn 0.69 0.02 0.72\r\nvn -0.51 0.35 0.79\r\nvn -0.68 0.36 0.64\r\nvn -0.51 0.35 0.79\r\nvn -0.68 0.35 0.64\r\nvn -0.82 0.34 0.46\r\nvn -0.82 0.34 0.46\r\nvn -0.95 0.18 -0.25\r\nvn -0.95 0.18 -0.25\r\nvn -0.69 -0.01 -0.72\r\nvn -0.69 -0.02 -0.72\r\nvn 0.59 -0.35 -0.72\r\nvn 0.68 -0.35 -0.64\r\nvn 0.6 -0.35 -0.72\r\nvn 0.68 -0.35 -0.64\r\nvn 0.76 -0.35 -0.55\r\nvn 0.76 -0.35 -0.55\r\nvn 0.96 -0.21 0.16\r\nvn 0.96 -0.21 0.16\r\nvn 0.69 0.01 0.73\r\nvn 0.69 0.01 0.72\r\nvn -0.51 0.34 0.79\r\nvn -0.68 0.35 0.64\r\nvn -0.51 0.34 0.79\r\nvn -0.68 0.35 0.64\r\nvn -0.82 0.34 0.46\r\nvn -0.82 0.34 0.46\r\nvn -0.95 0.18 -0.25\r\nvn -0.95 0.18 -0.25\r\nvn -0.69 -0.01 -0.73\r\nvn -0.69 -0.01 -0.72\r\nvn 0.6 -0.35 -0.72\r\nvn 0.68 -0.35 -0.64\r\nvn 0.6 -0.34 -0.73\r\nvn 0.68 -0.35 -0.64\r\nvn 0.76 -0.34 -0.55\r\nvn 0.76 -0.34 -0.55\r\nvn 0.96 -0.21 0.16\r\nvn 0.96 -0.21 0.16\r\nvn 0.69 0.01 0.73\r\nvn 0.69 0.01 0.73\r\nvn -0.51 0.34 0.79\r\nvn -0.68 0.35 0.64\r\nvn -0.51 0.34 0.79\r\nvn -0.68 0.35 0.64\r\nvn -0.82 0.33 0.46\r\nvn -0.82 0.34 0.46\r\nvn -0.95 0.18 -0.25\r\nvn -0.95 0.18 -0.25\r\nvn -0.69 -0.01 -0.73\r\nvn -0.69 -0.01 -0.73\r\nvn 0.6 -0.34 -0.73\r\nvn 0.68 -0.34 -0.64\r\nvn 0.6 -0.34 -0.73\r\nvn 0.68 -0.34 -0.65\r\nvn 0.76 -0.34 -0.55\r\nvn 0.76 -0.34 -0.55\r\nvn 0.96 -0.21 0.16\r\nvn 0.96 -0.21 0.16\r\nvn 0.69 0 0.73\r\nvn 0.69 0 0.73\r\nvn -0.51 0.33 0.79\r\nvn -0.68 0.34 0.64\r\nvn -0.51 0.33 0.79\r\nvn -0.68 0.34 0.65\r\nvn -0.82 0.33 0.46\r\nvn -0.82 0.33 0.46\r\nvn -0.95 0.18 -0.25\r\nvn -0.95 0.18 -0.26\r\nvn -0.69 -0 -0.73\r\nvn -0.69 -0 -0.73\r\nvn 0.6 -0.33 -0.73\r\nvn 0.69 -0.33 -0.65\r\nvn 0.6 -0.33 -0.73\r\nvn 0.69 -0.33 -0.65\r\nvn 0.76 -0.33 -0.56\r\nvn 0.76 -0.33 -0.56\r\nvn 0.96 -0.21 0.16\r\nvn 0.97 -0.21 0.16\r\nvn 0.69 -0 0.73\r\nvn 0.69 0 0.73\r\nvn -0.51 0.33 0.79\r\nvn -0.69 0.34 0.65\r\nvn -0.51 0.32 0.8\r\nvn -0.69 0.33 0.65\r\nvn -0.83 0.32 0.47\r\nvn -0.82 0.33 0.46\r\nvn -0.95 0.18 -0.26\r\nvn -0.95 0.18 -0.26\r\nvn -0.69 0 -0.73\r\nvn -0.69 -0 -0.73\r\nvn 0.6 -0.32 -0.73\r\nvn 0.69 -0.32 -0.65\r\nvn 0.61 -0.3 -0.74\r\nvn 0.69 -0.31 -0.65\r\nvn 0.77 -0.3 -0.56\r\nvn 0.77 -0.32 -0.56\r\nvn 0.97 -0.2 0.16\r\nvn 0.97 -0.2 0.15\r\nvn 0.68 -0.01 0.73\r\nvn 0.68 -0.01 0.73\r\nvn -0.52 0.31 0.8\r\nvn -0.69 0.32 0.65\r\nvn -0.52 0.3 0.8\r\nvn -0.69 0.31 0.65\r\nvn -0.83 0.3 0.47\r\nvn -0.83 0.31 0.47\r\nvn -0.95 0.18 -0.26\r\nvn -0.95 0.17 -0.26\r\nvn -0.68 0.01 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.61 -0.28 -0.74\r\nvn 0.7 -0.29 -0.66\r\nvn 0.62 -0.26 -0.74\r\nvn 0.7 -0.27 -0.66\r\nvn 0.78 -0.27 -0.57\r\nvn 0.77 -0.29 -0.57\r\nvn 0.97 -0.19 0.15\r\nvn 0.97 -0.18 0.15\r\nvn 0.68 -0.02 0.73\r\nvn 0.68 -0.01 0.73\r\nvn -0.52 0.28 0.81\r\nvn -0.7 0.29 0.66\r\nvn -0.53 0.25 0.81\r\nvn -0.7 0.27 0.66\r\nvn -0.84 0.26 0.48\r\nvn -0.83 0.28 0.47\r\nvn -0.95 0.17 -0.25\r\nvn -0.95 0.16 -0.25\r\nvn -0.68 0.02 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.62 -0.24 -0.75\r\nvn 0.71 -0.24 -0.66\r\nvn 0.63 -0.22 -0.75\r\nvn 0.71 -0.22 -0.67\r\nvn 0.79 -0.22 -0.58\r\nvn 0.78 -0.24 -0.57\r\nvn 0.97 -0.17 0.15\r\nvn 0.98 -0.16 0.15\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.53 0.23 0.81\r\nvn -0.71 0.24 0.66\r\nvn -0.54 0.21 0.82\r\nvn -0.71 0.22 0.67\r\nvn -0.85 0.22 0.48\r\nvn -0.84 0.24 0.48\r\nvn -0.96 0.15 -0.25\r\nvn -0.96 0.15 -0.25\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.63 -0.2 -0.75\r\nvn 0.71 -0.2 -0.67\r\nvn 0.63 -0.17 -0.75\r\nvn 0.72 -0.18 -0.67\r\nvn 0.79 -0.18 -0.58\r\nvn 0.79 -0.2 -0.58\r\nvn 0.98 -0.15 0.15\r\nvn 0.98 -0.14 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.54 0.19 0.82\r\nvn -0.71 0.2 0.67\r\nvn -0.54 0.17 0.82\r\nvn -0.72 0.18 0.67\r\nvn -0.85 0.18 0.49\r\nvn -0.85 0.2 0.49\r\nvn -0.96 0.14 -0.25\r\nvn -0.96 0.12 -0.25\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.15 -0.76\r\nvn 0.72 -0.16 -0.67\r\nvn 0.64 -0.13 -0.76\r\nvn 0.72 -0.14 -0.68\r\nvn 0.8 -0.14 -0.58\r\nvn 0.8 -0.16 -0.58\r\nvn 0.98 -0.12 0.14\r\nvn 0.98 -0.11 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.55 0.15 0.82\r\nvn -0.72 0.16 0.67\r\nvn -0.55 0.13 0.83\r\nvn -0.72 0.14 0.68\r\nvn -0.86 0.14 0.49\r\nvn -0.86 0.16 0.49\r\nvn -0.96 0.11 -0.25\r\nvn -0.96 0.1 -0.25\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.12 -0.76\r\nvn 0.73 -0.12 -0.68\r\nvn 0.64 -0.11 -0.76\r\nvn 0.73 -0.11 -0.68\r\nvn 0.8 -0.12 -0.59\r\nvn 0.8 -0.13 -0.59\r\nvn 0.98 -0.1 0.14\r\nvn 0.98 -0.1 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.55 0.11 0.83\r\nvn -0.73 0.12 0.68\r\nvn -0.55 0.1 0.83\r\nvn -0.73 0.11 0.68\r\nvn -0.86 0.12 0.49\r\nvn -0.86 0.13 0.49\r\nvn -0.97 0.09 -0.24\r\nvn -0.97 0.09 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.1 -0.76\r\nvn 0.73 -0.1 -0.68\r\nvn 0.64 -0.1 -0.76\r\nvn 0.73 -0.1 -0.68\r\nvn 0.8 -0.1 -0.59\r\nvn 0.8 -0.11 -0.59\r\nvn 0.99 -0.09 0.14\r\nvn 0.99 -0.09 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.55 0.09 0.83\r\nvn -0.73 0.1 0.68\r\nvn -0.55 0.09 0.83\r\nvn -0.73 0.1 0.68\r\nvn -0.86 0.1 0.49\r\nvn -0.86 0.11 0.49\r\nvn -0.97 0.08 -0.24\r\nvn -0.97 0.08 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.09 -0.76\r\nvn 0.73 -0.1 -0.68\r\nvn 0.64 -0.09 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.8 -0.1 -0.59\r\nvn 0.8 -0.1 -0.59\r\nvn 0.99 -0.08 0.14\r\nvn 0.99 -0.08 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.55 0.09 0.83\r\nvn -0.73 0.1 0.68\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.86 0.1 0.49\r\nvn -0.86 0.1 0.49\r\nvn -0.97 0.08 -0.24\r\nvn -0.97 0.08 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.09 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.8 -0.09 -0.59\r\nvn 0.8 -0.09 -0.59\r\nvn 0.99 -0.08 0.14\r\nvn 0.99 -0.08 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.86 0.09 0.49\r\nvn -0.86 0.09 0.49\r\nvn -0.97 0.07 -0.24\r\nvn -0.97 0.07 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.8 -0.09 -0.59\r\nvn 0.8 -0.09 -0.59\r\nvn 0.99 -0.08 0.14\r\nvn 0.99 -0.07 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.86 0.09 0.49\r\nvn -0.86 0.09 0.49\r\nvn -0.97 0.07 -0.24\r\nvn -0.97 0.07 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.08 -0.68\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.08 -0.68\r\nvn 0.81 -0.08 -0.59\r\nvn 0.8 -0.09 -0.59\r\nvn 0.99 -0.07 0.14\r\nvn 0.99 -0.07 0.14\r\nvn 0.68 -0.03 0.73\r\nvn 0.68 -0.03 0.73\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.08 0.68\r\nvn -0.56 0.07 0.83\r\nvn -0.73 0.08 0.68\r\nvn -0.86 0.09 0.49\r\nvn -0.86 0.09 0.49\r\nvn -0.97 0.07 -0.24\r\nvn -0.97 0.07 -0.24\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.08 -0.68\r\nvn 0.64 -0.08 -0.76\r\nvn 0.81 -0.08 -0.59\r\nvn 0.81 -0.08 -0.59\r\nvn 0.99 -0.07 0.14\r\nvn 0.99 -0.07 0.14\r\nvn 0.68 -0.02 0.73\r\nvn 0.68 -0.02 0.73\r\nvn -0.56 0.07 0.83\r\nvn -0.73 0.08 0.68\r\nvn -0.56 0.07 0.83\r\nvn -0.87 0.08 0.49\r\nvn -0.87 0.08 0.49\r\nvn -0.97 0.06 -0.24\r\nvn -0.97 0.06 -0.24\r\nvn -0.68 0.02 -0.73\r\nvn -0.68 0.02 -0.73\r\nvn 0.64 -0.08 -0.76\r\nvn 0.73 -0.08 -0.68\r\nvn 0.64 -0.09 -0.76\r\nvn 0.73 -0.09 -0.68\r\nvn 0.8 -0.09 -0.59\r\nvn 0.8 -0.09 -0.59\r\nvn 0.99 -0.07 0.15\r\nvn 0.99 -0.07 0.15\r\nvn 0.68 -0.02 0.73\r\nvn 0.68 -0.02 0.73\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.08 0.68\r\nvn -0.56 0.08 0.83\r\nvn -0.73 0.09 0.68\r\nvn -0.86 0.09 0.49\r\nvn -0.86 0.09 0.49\r\nvn -0.97 0.06 -0.24\r\nvn -0.97 0.07 -0.24\r\nvn -0.68 0.02 -0.73\r\nvn -0.68 0.02 -0.73\r\nvn 0.64 -0.09 -0.76\r\nvn 0.73 -0.1 -0.68\r\nvn 0.64 -0.1 -0.76\r\nvn 0.73 -0.1 -0.68\r\nvn 0.8 -0.11 -0.59\r\nvn 0.8 -0.1 -0.59\r\nvn 0.99 -0.07 0.15\r\nvn 0.99 -0.08 0.15\r\nvn 0.68 -0.02 0.73\r\nvn 0.68 -0.02 0.73\r\nvn -0.55 0.09 0.83\r\nvn -0.73 0.1 0.68\r\nvn -0.55 0.1 0.83\r\nvn -0.73 0.1 0.68\r\nvn -0.86 0.1 0.49\r\nvn -0.86 0.1 0.49\r\nvn -0.97 0.07 -0.24\r\nvn -0.97 0.07 -0.24\r\nvn -0.68 0.02 -0.73\r\nvn -0.68 0.02 -0.73\r\nvn 0.64 -0.11 -0.76\r\nvn 0.73 -0.11 -0.68\r\nvn 0.64 -0.12 -0.76\r\nvn 0.73 -0.13 -0.68\r\nvn 0.8 -0.13 -0.58\r\nvn 0.8 -0.12 -0.59\r\nvn 0.99 -0.08 0.15\r\nvn 0.98 -0.09 0.15\r\nvn 0.68 -0.01 0.73\r\nvn 0.68 -0.01 0.73\r\nvn -0.55 0.11 0.83\r\nvn -0.73 0.11 0.68\r\nvn -0.55 0.12 0.83\r\nvn -0.73 0.13 0.68\r\nvn -0.86 0.13 0.49\r\nvn -0.86 0.11 0.49\r\nvn -0.97 0.07 -0.24\r\nvn -0.97 0.08 -0.24\r\nvn -0.68 0.01 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.64 -0.14 -0.76\r\nvn 0.72 -0.14 -0.68\r\nvn 0.64 -0.15 -0.76\r\nvn 0.72 -0.16 -0.67\r\nvn 0.8 -0.15 -0.58\r\nvn 0.8 -0.14 -0.58\r\nvn 0.98 -0.09 0.15\r\nvn 0.98 -0.1 0.15\r\nvn 0.68 -0 0.73\r\nvn 0.68 -0.01 0.73\r\nvn -0.55 0.14 0.82\r\nvn -0.72 0.14 0.68\r\nvn -0.55 0.15 0.82\r\nvn -0.72 0.16 0.67\r\nvn -0.86 0.15 0.49\r\nvn -0.86 0.14 0.49\r\nvn -0.97 0.08 -0.24\r\nvn -0.97 0.09 -0.24\r\nvn -0.68 0 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.63 -0.17 -0.75\r\nvn 0.72 -0.17 -0.67\r\nvn 0.63 -0.19 -0.75\r\nvn 0.72 -0.19 -0.67\r\nvn 0.79 -0.18 -0.58\r\nvn 0.8 -0.17 -0.58\r\nvn 0.98 -0.11 0.15\r\nvn 0.98 -0.11 0.15\r\nvn 0.68 0 0.73\r\nvn 0.68 -0 0.73\r\nvn -0.55 0.17 0.82\r\nvn -0.72 0.17 0.67\r\nvn -0.54 0.18 0.82\r\nvn -0.72 0.19 0.67\r\nvn -0.85 0.18 0.49\r\nvn -0.86 0.17 0.49\r\nvn -0.97 0.09 -0.24\r\nvn -0.97 0.1 -0.24\r\nvn -0.68 -0 -0.73\r\nvn -0.68 0 -0.73\r\nvn 0.63 -0.2 -0.75\r\nvn 0.72 -0.2 -0.67\r\nvn 0.63 -0.22 -0.75\r\nvn 0.71 -0.22 -0.66\r\nvn 0.79 -0.22 -0.57\r\nvn 0.79 -0.2 -0.58\r\nvn 0.98 -0.12 0.15\r\nvn 0.98 -0.12 0.15\r\nvn 0.68 0.02 0.73\r\nvn 0.68 0.01 0.73\r\nvn -0.54 0.2 0.82\r\nvn -0.72 0.2 0.67\r\nvn -0.54 0.22 0.81\r\nvn -0.71 0.22 0.66\r\nvn -0.85 0.21 0.48\r\nvn -0.85 0.2 0.48\r\nvn -0.96 0.1 -0.24\r\nvn -0.96 0.11 -0.24\r\nvn -0.68 -0.02 -0.73\r\nvn -0.68 -0.01 -0.73\r\nvn 0.62 -0.24 -0.74\r\nvn 0.71 -0.24 -0.66\r\nvn 0.62 -0.26 -0.74\r\nvn 0.71 -0.26 -0.66\r\nvn 0.78 -0.25 -0.57\r\nvn 0.79 -0.24 -0.57\r\nvn 0.98 -0.13 0.16\r\nvn 0.98 -0.13 0.16\r\nvn 0.69 0.04 0.73\r\nvn 0.69 0.03 0.73\r\nvn -0.54 0.24 0.81\r\nvn -0.71 0.24 0.66\r\nvn -0.53 0.26 0.81\r\nvn -0.71 0.26 0.66\r\nvn -0.84 0.25 0.48\r\nvn -0.85 0.23 0.48\r\nvn -0.96 0.11 -0.24\r\nvn -0.96 0.11 -0.24\r\nvn -0.69 -0.04 -0.73\r\nvn -0.69 -0.03 -0.73\r\nvn 0.62 -0.28 -0.73\r\nvn 0.7 -0.28 -0.65\r\nvn 0.61 -0.3 -0.73\r\nvn 0.7 -0.3 -0.65\r\nvn 0.78 -0.29 -0.56\r\nvn 0.78 -0.27 -0.56\r\nvn 0.98 -0.14 0.16\r\nvn 0.98 -0.14 0.16\r\nvn 0.69 0.06 0.72\r\nvn 0.69 0.04 0.72\r\nvn -0.53 0.28 0.8\r\nvn -0.7 0.28 0.65\r\nvn -0.52 0.3 0.8\r\nvn -0.7 0.3 0.65\r\nvn -0.84 0.28 0.47\r\nvn -0.84 0.26 0.47\r\nvn -0.96 0.12 -0.24\r\nvn -0.96 0.12 -0.24\r\nvn -0.69 -0.06 -0.72\r\nvn -0.69 -0.04 -0.72\r\nvn 0.61 -0.32 -0.72\r\nvn 0.7 -0.32 -0.64\r\nvn 0.61 -0.35 -0.72\r\nvn 0.69 -0.34 -0.64\r\nvn 0.77 -0.33 -0.55\r\nvn 0.77 -0.31 -0.55\r\nvn 0.98 -0.15 0.16\r\nvn 0.98 -0.15 0.16\r\nvn 0.7 0.08 0.71\r\nvn 0.69 0.07 0.72\r\nvn -0.52 0.33 0.79\r\nvn -0.7 0.32 0.64\r\nvn -0.52 0.35 0.78\r\nvn -0.69 0.34 0.64\r\nvn -0.83 0.31 0.46\r\nvn -0.84 0.3 0.46\r\nvn -0.96 0.12 -0.24\r\nvn -0.96 0.12 -0.24\r\nvn -0.7 -0.08 -0.71\r\nvn -0.69 -0.07 -0.72\r\nvn 0.6 -0.37 -0.71\r\nvn 0.69 -0.36 -0.63\r\nvn 0.6 -0.39 -0.7\r\nvn 0.68 -0.38 -0.62\r\nvn 0.76 -0.36 -0.54\r\nvn 0.77 -0.35 -0.54\r\nvn 0.97 -0.15 0.16\r\nvn 0.97 -0.16 0.16\r\nvn 0.7 0.1 0.71\r\nvn 0.7 0.09 0.71\r\nvn -0.51 0.37 0.78\r\nvn -0.69 0.36 0.63\r\nvn -0.51 0.39 0.77\r\nvn -0.68 0.38 0.62\r\nvn -0.82 0.35 0.45\r\nvn -0.83 0.33 0.45\r\nvn -0.96 0.12 -0.24\r\nvn -0.96 0.12 -0.24\r\nvn -0.7 -0.1 -0.71\r\nvn -0.7 -0.09 -0.71\r\nvn 0.59 -0.41 -0.69\r\nvn 0.68 -0.4 -0.62\r\nvn 0.59 -0.43 -0.68\r\nvn 0.67 -0.42 -0.61\r\nvn 0.75 -0.4 -0.52\r\nvn 0.76 -0.38 -0.53\r\nvn 0.97 -0.16 0.16\r\nvn 0.97 -0.16 0.17\r\nvn 0.71 0.13 0.69\r\nvn 0.7 0.12 0.7\r\nvn -0.5 0.42 0.76\r\nvn -0.68 0.4 0.62\r\nvn -0.49 0.44 0.75\r\nvn -0.67 0.42 0.61\r\nvn -0.82 0.38 0.43\r\nvn -0.82 0.36 0.44\r\nvn -0.96 0.12 -0.24\r\nvn -0.96 0.12 -0.24\r\nvn -0.71 -0.13 -0.69\r\nvn -0.7 -0.12 -0.7\r\nvn 0.58 -0.46 -0.67\r\nvn 0.67 -0.44 -0.6\r\nvn 0.57 -0.48 -0.66\r\nvn 0.66 -0.46 -0.59\r\nvn 0.74 -0.44 -0.5\r\nvn 0.75 -0.42 -0.51\r\nvn 0.97 -0.16 0.17\r\nvn 0.97 -0.16 0.17\r\nvn 0.72 0.17 0.68\r\nvn 0.71 0.15 0.69\r\nvn -0.49 0.47 0.74\r\nvn -0.67 0.44 0.6\r\nvn -0.48 0.49 0.73\r\nvn -0.66 0.46 0.59\r\nvn -0.81 0.41 0.42\r\nvn -0.81 0.4 0.43\r\nvn -0.96 0.12 -0.24\r\nvn -0.96 0.12 -0.24\r\nvn -0.72 -0.17 -0.68\r\nvn -0.71 -0.15 -0.69\r\nvn 0.57 -0.51 -0.65\r\nvn 0.66 -0.49 -0.57\r\nvn 0.56 -0.53 -0.63\r\nvn 0.65 -0.51 -0.56\r\nvn 0.74 -0.48 -0.48\r\nvn 0.74 -0.46 -0.49\r\nvn 0.98 -0.15 0.16\r\nvn 0.98 -0.14 0.16\r\nvn 0.73 0.22 0.64\r\nvn 0.72 0.19 0.66\r\nvn -0.48 0.52 0.71\r\nvn -0.66 0.49 0.57\r\nvn -0.47 0.55 0.69\r\nvn -0.65 0.51 0.56\r\nvn -0.8 0.45 0.4\r\nvn -0.8 0.43 0.41\r\nvn -0.97 0.11 -0.23\r\nvn -0.97 0.1 -0.23\r\nvn -0.73 -0.22 -0.65\r\nvn -0.72 -0.19 -0.66\r\nvn 0.56 -0.56 -0.61\r\nvn 0.65 -0.53 -0.54\r\nvn 0.55 -0.59 -0.59\r\nvn 0.64 -0.55 -0.53\r\nvn 0.72 -0.51 -0.47\r\nvn 0.74 -0.5 -0.46\r\nvn 0.98 -0.11 0.17\r\nvn 0.98 -0.05 0.19\r\nvn 0.81 0.35 0.47\r\nvn 0.78 0.31 0.54\r\nvn 0.75 0.28 0.6\r\nvn 0.74 0.25 0.63\r\nvn -0.46 0.58 0.67\r\nvn -0.65 0.53 0.55\r\nvn -0.45 0.61 0.65\r\nvn -0.64 0.55 0.53\r\nvn -0.79 0.46 0.39\r\nvn -0.8 0.46 0.39\r\nvn -0.97 0.08 -0.21\r\nvn -0.98 0.03 -0.18\r\nvn -0.75 -0.33 -0.57\r\nvn -0.74 -0.26 -0.62\r\nvn 0.55 -0.61 -0.57\r\nvn 0.64 -0.56 -0.52\r\nvn 0.55 -0.63 -0.55\r\nvn 0.64 -0.57 -0.51\r\nvn 0.74 -0.5 -0.46\r\nvn 0.72 -0.51 -0.46\r\nvn -0.45 0.64 0.62\r\nvn -0.64 0.56 0.52\r\nvn -0.46 0.67 0.58\r\nvn -0.64 0.57 0.51\r\nvn -0.79 0.44 0.42\r\nvn -0.79 0.46 0.4\r\nvn -0.99 -0.03 -0.11\r\nvn -0.99 -0.12 -0.01\r\nvn -0.76 -0.53 -0.36\r\nvn -0.76 -0.42 -0.49\r\nvn 0.55 -0.64 -0.53\r\nvn 0.64 -0.57 -0.5\r\nvn 0.56 -0.66 -0.5\r\nvn 0.65 -0.58 -0.49\r\nvn 0.73 -0.49 -0.47\r\nvn 0.73 -0.49 -0.47\r\nvn -0.46 0.7 0.54\r\nvn -0.64 0.58 0.5\r\nvn -0.46 0.73 0.51\r\nvn -0.65 0.58 0.49\r\nvn -0.79 0.42 0.45\r\nvn -0.79 0.42 0.44\r\nvn -0.98 -0.2 0.09\r\nvn -0.95 -0.25 0.18\r\nvn -0.71 -0.69 -0.12\r\nvn -0.74 -0.63 -0.23\r\nvn 0.57 -0.67 -0.48\r\nvn 0.65 -0.59 -0.47\r\nvn 0.58 -0.68 -0.45\r\nvn 0.66 -0.6 -0.45\r\nvn 0.74 -0.51 -0.45\r\nvn 0.73 -0.5 -0.46\r\nvn 0.95 0.2 -0.25\r\nvn 0.96 0.18 -0.2\r\nvn 0.94 0.21 -0.28\r\nvn 0.68 0.74 0.01\r\nvn 0.69 0.72 0.05\r\nvn 0.72 0.69 0.13\r\nvn -0.48 0.74 0.47\r\nvn -0.65 0.59 0.47\r\nvn -0.49 0.75 0.44\r\nvn -0.66 0.6 0.45\r\nvn -0.8 0.42 0.44\r\nvn -0.79 0.41 0.45\r\nvn -0.94 -0.27 0.22\r\nvn -0.93 -0.28 0.25\r\nvn -0.67 -0.74 -0.01\r\nvn -0.69 -0.72 -0.05\r\nvn 0.59 -0.69 -0.42\r\nvn 0.67 -0.61 -0.43\r\nvn 0.59 -0.7 -0.4\r\nvn 0.68 -0.61 -0.41\r\nvn 0.75 -0.52 -0.41\r\nvn 0.74 -0.51 -0.43\r\nvn 0.94 0.21 -0.28\r\nvn 0.94 0.2 -0.28\r\nvn 0.66 0.75 -0.03\r\nvn 0.67 0.75 -0.02\r\nvn -0.5 0.76 0.41\r\nvn -0.67 0.61 0.43\r\nvn -0.51 0.77 0.39\r\nvn -0.68 0.61 0.41\r\nvn -0.81 0.43 0.41\r\nvn -0.8 0.42 0.42\r\nvn -0.92 -0.29 0.25\r\nvn -0.92 -0.29 0.25\r\nvn -0.66 -0.75 0.03\r\nvn -0.67 -0.75 0.02\r\nvn 0.6 -0.7 -0.38\r\nvn 0.68 -0.62 -0.39\r\nvn 0.61 -0.71 -0.36\r\nvn 0.69 -0.62 -0.37\r\nvn 0.76 -0.53 -0.37\r\nvn 0.76 -0.52 -0.39\r\nvn 0.94 0.2 -0.28\r\nvn 0.94 0.2 -0.27\r\nvn 0.65 0.75 -0.05\r\nvn 0.66 0.75 -0.05\r\nvn -0.52 0.77 0.37\r\nvn -0.68 0.62 0.39\r\nvn -0.52 0.78 0.35\r\nvn -0.69 0.62 0.37\r\nvn -0.82 0.44 0.37\r\nvn -0.81 0.43 0.39\r\nvn -0.92 -0.29 0.25\r\nvn -0.93 -0.29 0.25\r\nvn -0.65 -0.75 0.05\r\nvn -0.66 -0.75 0.05\r\nvn 0.61 -0.71 -0.34\r\nvn 0.7 -0.63 -0.35\r\nvn 0.62 -0.72 -0.32\r\nvn 0.7 -0.63 -0.33\r\nvn 0.77 -0.54 -0.34\r\nvn 0.77 -0.53 -0.35\r\nvn 0.94 0.2 -0.26\r\nvn 0.95 0.2 -0.25\r\nvn 0.65 0.76 -0.06\r\nvn 0.65 0.76 -0.05\r\nvn -0.53 0.78 0.33\r\nvn -0.7 0.63 0.35\r\nvn -0.53 0.79 0.31\r\nvn -0.7 0.63 0.33\r\nvn -0.83 0.44 0.34\r\nvn -0.83 0.44 0.35\r\nvn -0.93 -0.29 0.24\r\nvn -0.93 -0.28 0.23\r\nvn -0.65 -0.76 0.06\r\nvn -0.65 -0.76 0.05\r\nvn 0.62 -0.72 -0.31\r\nvn 0.71 -0.63 -0.31\r\nvn 0.63 -0.72 -0.29\r\nvn 0.71 -0.64 -0.3\r\nvn 0.78 -0.54 -0.3\r\nvn 0.78 -0.54 -0.32\r\nvn 0.95 0.19 -0.24\r\nvn 0.95 0.19 -0.23\r\nvn 0.65 0.76 -0.06\r\nvn 0.65 0.76 -0.06\r\nvn -0.54 0.79 0.29\r\nvn -0.71 0.63 0.31\r\nvn -0.54 0.79 0.28\r\nvn -0.71 0.64 0.3\r\nvn -0.84 0.45 0.3\r\nvn -0.84 0.45 0.32\r\nvn -0.93 -0.28 0.22\r\nvn -0.94 -0.28 0.21\r\nvn -0.65 -0.76 0.06\r\nvn -0.65 -0.76 0.06\r\nvn 0.63 -0.72 -0.27\r\nvn 0.72 -0.64 -0.28\r\nvn 0.64 -0.73 -0.25\r\nvn 0.72 -0.64 -0.26\r\nvn 0.79 -0.55 -0.27\r\nvn 0.79 -0.55 -0.28\r\nvn 0.96 0.19 -0.22\r\nvn 0.96 0.19 -0.21\r\nvn 0.65 0.76 -0.06\r\nvn 0.65 0.76 -0.06\r\nvn -0.55 0.79 0.26\r\nvn -0.72 0.64 0.28\r\nvn -0.55 0.8 0.24\r\nvn -0.72 0.64 0.26\r\nvn -0.85 0.45 0.27\r\nvn -0.85 0.45 0.29\r\nvn -0.94 -0.28 0.2\r\nvn -0.94 -0.28 0.19\r\nvn -0.65 -0.76 0.06\r\nvn -0.65 -0.76 0.06\r\nvn 0.64 -0.73 -0.24\r\nvn 0.72 -0.64 -0.25\r\nvn 0.65 -0.73 -0.22\r\nvn 0.73 -0.65 -0.23\r\nvn 0.8 -0.55 -0.23\r\nvn 0.8 -0.55 -0.25\r\nvn 0.96 0.19 -0.2\r\nvn 0.97 0.18 -0.18\r\nvn 0.65 0.76 -0.05\r\nvn 0.65 0.76 -0.05\r\nvn -0.56 0.8 0.23\r\nvn -0.72 0.64 0.25\r\nvn -0.56 0.8 0.21\r\nvn -0.73 0.65 0.23\r\nvn -0.86 0.46 0.23\r\nvn -0.85 0.46 0.25\r\nvn -0.94 -0.28 0.18\r\nvn -0.95 -0.28 0.17\r\nvn -0.65 -0.76 0.05\r\nvn -0.65 -0.76 0.05\r\nvn 0.65 -0.73 -0.2\r\nvn 0.73 -0.65 -0.21\r\nvn 0.65 -0.73 -0.19\r\nvn 0.73 -0.65 -0.2\r\nvn 0.81 -0.56 -0.2\r\nvn 0.8 -0.55 -0.22\r\nvn 0.97 0.18 -0.17\r\nvn 0.97 0.18 -0.16\r\nvn 0.65 0.75 -0.05\r\nvn 0.65 0.75 -0.05\r\nvn -0.56 0.8 0.19\r\nvn -0.73 0.65 0.21\r\nvn -0.57 0.8 0.18\r\nvn -0.73 0.65 0.2\r\nvn -0.86 0.46 0.2\r\nvn -0.86 0.46 0.22\r\nvn -0.95 -0.27 0.16\r\nvn -0.95 -0.27 0.15\r\nvn -0.65 -0.75 0.05\r\nvn -0.65 -0.75 0.05\r\nvn 0.65 -0.74 -0.17\r\nvn 0.74 -0.65 -0.18\r\nvn 0.66 -0.74 -0.16\r\nvn 0.74 -0.65 -0.17\r\nvn 0.81 -0.56 -0.17\r\nvn 0.81 -0.56 -0.18\r\nvn 0.97 0.18 -0.15\r\nvn 0.97 0.18 -0.14\r\nvn 0.66 0.75 -0.04\r\nvn 0.66 0.75 -0.04\r\nvn -0.57 0.81 0.17\r\nvn -0.74 0.65 0.18\r\nvn -0.57 0.81 0.15\r\nvn -0.74 0.65 0.17\r\nvn -0.87 0.46 0.17\r\nvn -0.87 0.46 0.19\r\nvn -0.95 -0.27 0.14\r\nvn -0.95 -0.27 0.13\r\nvn -0.66 -0.75 0.04\r\nvn -0.66 -0.75 0.04\r\nvn 0.66 -0.74 -0.15\r\nvn 0.74 -0.65 -0.15\r\nvn 0.66 -0.74 -0.13\r\nvn 0.74 -0.65 -0.14\r\nvn 0.82 -0.56 -0.14\r\nvn 0.81 -0.56 -0.16\r\nvn 0.98 0.18 -0.13\r\nvn 0.98 0.18 -0.12\r\nvn 0.66 0.75 -0.04\r\nvn 0.66 0.75 -0.04\r\nvn -0.57 0.81 0.14\r\nvn -0.74 0.65 0.15\r\nvn -0.57 0.81 0.13\r\nvn -0.74 0.65 0.14\r\nvn -0.87 0.47 0.14\r\nvn -0.87 0.46 0.16\r\nvn -0.96 -0.27 0.12\r\nvn -0.96 -0.27 0.11\r\nvn -0.66 -0.75 0.04\r\nvn -0.66 -0.75 0.04\r\nvn 0.66 -0.74 -0.12\r\nvn 0.75 -0.65 -0.13\r\nvn 0.66 -0.74 -0.11\r\nvn 0.75 -0.66 -0.12\r\nvn 0.82 -0.56 -0.12\r\nvn 0.82 -0.56 -0.13\r\nvn 0.98 0.18 -0.11\r\nvn 0.98 0.18 -0.1\r\nvn 0.66 0.75 -0.03\r\nvn 0.66 0.75 -0.03\r\nvn -0.58 0.81 0.12\r\nvn -0.75 0.65 0.13\r\nvn -0.58 0.81 0.1\r\nvn -0.75 0.66 0.12\r\nvn -0.88 0.47 0.12\r\nvn -0.87 0.47 0.13\r\nvn -0.96 -0.27 0.1\r\nvn -0.96 -0.27 0.09\r\nvn -0.66 -0.75 0.03\r\nvn -0.66 -0.75 0.03\r\nvn 0.66 -0.74 -0.1\r\nvn 0.75 -0.66 -0.1\r\nvn 0.67 -0.74 -0.09\r\nvn 0.75 -0.66 -0.09\r\nvn 0.82 -0.56 -0.09\r\nvn 0.82 -0.56 -0.11\r\nvn 0.98 0.18 -0.09\r\nvn 0.98 0.18 -0.08\r\nvn 0.66 0.75 -0.03\r\nvn 0.66 0.75 -0.03\r\nvn -0.58 0.81 0.09\r\nvn -0.75 0.66 0.1\r\nvn -0.58 0.81 0.08\r\nvn -0.75 0.66 0.09\r\nvn -0.88 0.47 0.09\r\nvn -0.88 0.47 0.11\r\nvn -0.96 -0.27 0.08\r\nvn -0.96 -0.27 0.07\r\nvn -0.66 -0.75 0.03\r\nvn -0.66 -0.75 0.03\r\nvn 0.67 -0.74 -0.07\r\nvn 0.75 -0.66 -0.08\r\nvn 0.67 -0.74 -0.06\r\nvn 0.75 -0.66 -0.06\r\nvn 0.82 -0.56 -0.07\r\nvn 0.82 -0.56 -0.08\r\nvn 0.98 0.18 -0.07\r\nvn 0.98 0.18 -0.06\r\nvn 0.66 0.75 -0.02\r\nvn 0.66 0.75 -0.02\r\nvn -0.58 0.81 0.07\r\nvn -0.75 0.66 0.08\r\nvn -0.58 0.81 0.06\r\nvn -0.75 0.66 0.06\r\nvn -0.88 0.47 0.07\r\nvn -0.88 0.47 0.08\r\nvn -0.96 -0.27 0.06\r\nvn -0.96 -0.27 0.05\r\nvn -0.66 -0.75 0.02\r\nvn -0.66 -0.75 0.02\r\nvn 0.67 -0.74 -0.05\r\nvn 0.75 -0.66 -0.05\r\nvn 0.67 -0.74 -0.03\r\nvn 0.75 -0.66 -0.03\r\nvn 0.82 -0.56 -0.04\r\nvn 0.82 -0.56 -0.05\r\nvn 0.98 0.18 -0.04\r\nvn 0.98 0.17 -0.03\r\nvn 0.66 0.75 -0.02\r\nvn 0.66 0.75 -0.02\r\nvn -0.58 0.81 0.04\r\nvn -0.75 0.66 0.05\r\nvn -0.58 0.81 0.03\r\nvn -0.75 0.66 0.03\r\nvn -0.88 0.47 0.04\r\nvn -0.88 0.47 0.05\r\nvn -0.96 -0.27 0.04\r\nvn -0.96 -0.27 0.03\r\nvn -0.66 -0.75 0.02\r\nvn -0.66 -0.75 0.02\r\nvn 0.67 -0.74 -0.02\r\nvn 0.75 -0.66 -0.02\r\nvn 0.67 -0.74 -0.01\r\nvn 0.75 -0.66 -0.01\r\nvn 0.83 -0.56 -0.01\r\nvn 0.83 -0.56 -0.02\r\nvn 0.98 0.17 -0.02\r\nvn 0.98 0.17 -0.01\r\nvn 0.66 0.75 -0.01\r\nvn 0.66 0.75 -0.01\r\nvn -0.58 0.81 0.02\r\nvn -0.75 0.66 0.02\r\nvn -0.58 0.81 0\r\nvn -0.75 0.66 0.01\r\nvn -0.88 0.47 0.01\r\nvn -0.88 0.47 0.02\r\nvn -0.96 -0.27 0.02\r\nvn -0.96 -0.27 0.01\r\nvn -0.66 -0.75 0.01\r\nvn -0.66 -0.75 0.01\r\nvn 0.67 -0.74 0.01\r\nvn 0.75 -0.66 0.01\r\nvn 0.67 -0.74 0.02\r\nvn 0.75 -0.66 0.02\r\nvn 0.83 -0.56 0.02\r\nvn 0.83 -0.56 0\r\nvn 0.98 0.17 -0\r\nvn 0.98 0.17 0.01\r\nvn 0.66 0.75 -0.01\r\nvn 0.66 0.75 -0.01\r\nvn -0.58 0.81 -0.01\r\nvn -0.75 0.66 -0.01\r\nvn -0.58 0.81 -0.02\r\nvn -0.75 0.66 -0.02\r\nvn -0.88 0.47 -0.02\r\nvn -0.88 0.47 -0\r\nvn -0.96 -0.27 0\r\nvn -0.96 -0.27 -0.01\r\nvn -0.66 -0.75 0.01\r\nvn -0.66 -0.75 0.01\r\nvn 0.67 -0.74 0.03\r\nvn 0.75 -0.66 0.03\r\nvn 0.67 -0.74 0.04\r\nvn 0.75 -0.66 0.04\r\nvn 0.83 -0.56 0.04\r\nvn 0.83 -0.56 0.03\r\nvn 0.98 0.17 0.02\r\nvn 0.98 0.18 0.03\r\nvn 0.66 0.75 -0\r\nvn 0.66 0.75 -0\r\nvn -0.58 0.81 -0.03\r\nvn -0.75 0.66 -0.03\r\nvn -0.58 0.81 -0.04\r\nvn -0.75 0.66 -0.04\r\nvn -0.88 0.47 -0.04\r\nvn -0.88 0.47 -0.03\r\nvn -0.96 -0.27 -0.01\r\nvn -0.96 -0.27 -0.02\r\nvn -0.66 -0.75 0\r\nvn -0.66 -0.75 0\r\nvn 0.67 -0.74 0.06\r\nvn 0.75 -0.66 0.06\r\nvn 0.67 -0.74 0.07\r\nvn 0.75 -0.66 0.07\r\nvn 0.82 -0.56 0.07\r\nvn 0.82 -0.56 0.06\r\nvn 0.98 0.18 0.04\r\nvn 0.98 0.18 0.05\r\nvn 0.66 0.75 0.01\r\nvn 0.66 0.75 0\r\nvn -0.58 0.81 -0.05\r\nvn -0.75 0.66 -0.06\r\nvn -0.58 0.81 -0.07\r\nvn -0.75 0.66 -0.07\r\nvn -0.88 0.47 -0.07\r\nvn -0.88 0.47 -0.06\r\nvn -0.96 -0.27 -0.03\r\nvn -0.96 -0.27 -0.04\r\nvn -0.66 -0.75 -0.01\r\nvn -0.66 -0.75 -0\r\nvn 0.67 -0.74 0.08\r\nvn 0.75 -0.66 0.08\r\nvn 0.67 -0.74 0.09\r\nvn 0.75 -0.66 0.09\r\nvn 0.82 -0.56 0.09\r\nvn 0.82 -0.56 0.08\r\nvn 0.98 0.18 0.06\r\nvn 0.98 0.18 0.07\r\nvn 0.66 0.75 0.01\r\nvn 0.66 0.75 0.01\r\nvn -0.58 0.81 -0.08\r\nvn -0.75 0.66 -0.08\r\nvn -0.58 0.81 -0.09\r\nvn -0.75 0.66 -0.09\r\nvn -0.88 0.47 -0.09\r\nvn -0.88 0.47 -0.08\r\nvn -0.96 -0.27 -0.05\r\nvn -0.96 -0.27 -0.06\r\nvn -0.66 -0.75 -0.01\r\nvn -0.66 -0.75 -0.01\r\nvn 0.67 -0.74 0.1\r\nvn 0.75 -0.65 0.11\r\nvn 0.66 -0.74 0.12\r\nvn 0.75 -0.65 0.12\r\nvn 0.82 -0.56 0.12\r\nvn 0.82 -0.56 0.11\r\nvn 0.98 0.18 0.08\r\nvn 0.98 0.18 0.09\r\nvn 0.66 0.75 0.02\r\nvn 0.66 0.75 0.01\r\nvn -0.58 0.81 -0.1\r\nvn -0.75 0.65 -0.11\r\nvn -0.58 0.81 -0.11\r\nvn -0.75 0.65 -0.12\r\nvn -0.88 0.46 -0.12\r\nvn -0.88 0.47 -0.11\r\nvn -0.96 -0.27 -0.07\r\nvn -0.96 -0.27 -0.08\r\nvn -0.66 -0.75 -0.02\r\nvn -0.66 -0.75 -0.01\r\nvn 0.66 -0.74 0.13\r\nvn 0.75 -0.65 0.14\r\nvn 0.66 -0.74 0.15\r\nvn 0.74 -0.65 0.15\r\nvn 0.82 -0.56 0.15\r\nvn 0.82 -0.56 0.14\r\nvn 0.98 0.18 0.1\r\nvn 0.98 0.18 0.11\r\nvn 0.66 0.75 0.02\r\nvn 0.66 0.75 0.02\r\nvn -0.58 0.81 -0.13\r\nvn -0.75 0.65 -0.14\r\nvn -0.58 0.81 -0.14\r\nvn -0.74 0.65 -0.15\r\nvn -0.87 0.46 -0.15\r\nvn -0.88 0.46 -0.14\r\nvn -0.96 -0.27 -0.09\r\nvn -0.96 -0.27 -0.1\r\nvn -0.66 -0.75 -0.02\r\nvn -0.66 -0.75 -0.02\r\nvn 0.66 -0.73 0.16\r\nvn 0.74 -0.65 0.17\r\nvn 0.66 -0.73 0.18\r\nvn 0.74 -0.65 0.19\r\nvn 0.81 -0.55 0.19\r\nvn 0.81 -0.56 0.17\r\nvn 0.98 0.18 0.12\r\nvn 0.97 0.18 0.14\r\nvn 0.66 0.75 0.03\r\nvn 0.66 0.75 0.02\r\nvn -0.57 0.8 -0.16\r\nvn -0.74 0.65 -0.17\r\nvn -0.57 0.8 -0.17\r\nvn -0.74 0.65 -0.19\r\nvn -0.87 0.46 -0.19\r\nvn -0.87 0.46 -0.17\r\nvn -0.96 -0.27 -0.11\r\nvn -0.95 -0.27 -0.12\r\nvn -0.66 -0.75 -0.03\r\nvn -0.66 -0.75 -0.02\r\nvn 0.65 -0.73 0.2\r\nvn 0.74 -0.65 0.2\r\nvn 0.65 -0.73 0.22\r\nvn 0.73 -0.64 0.22\r\nvn 0.8 -0.55 0.22\r\nvn 0.81 -0.55 0.21\r\nvn 0.97 0.18 0.15\r\nvn 0.97 0.19 0.16\r\nvn 0.65 0.76 0.03\r\nvn 0.65 0.76 0.03\r\nvn -0.57 0.8 -0.19\r\nvn -0.74 0.65 -0.2\r\nvn -0.56 0.8 -0.21\r\nvn -0.73 0.64 -0.22\r\nvn -0.86 0.46 -0.22\r\nvn -0.87 0.46 -0.2\r\nvn -0.95 -0.28 -0.14\r\nvn -0.95 -0.28 -0.15\r\nvn -0.65 -0.76 -0.03\r\nvn -0.65 -0.76 -0.03\r\nvn 0.65 -0.73 0.23\r\nvn 0.73 -0.64 0.24\r\nvn 0.64 -0.72 0.25\r\nvn 0.72 -0.64 0.26\r\nvn 0.8 -0.54 0.26\r\nvn 0.8 -0.55 0.24\r\nvn 0.97 0.19 0.18\r\nvn 0.96 0.19 0.19\r\nvn 0.65 0.76 0.04\r\nvn 0.65 0.76 0.03\r\nvn -0.56 0.8 -0.23\r\nvn -0.73 0.64 -0.24\r\nvn -0.56 0.79 -0.24\r\nvn -0.72 0.64 -0.26\r\nvn -0.85 0.45 -0.26\r\nvn -0.86 0.45 -0.24\r\nvn -0.95 -0.28 -0.16\r\nvn -0.94 -0.28 -0.17\r\nvn -0.65 -0.76 -0.04\r\nvn -0.65 -0.76 -0.03\r\nvn 0.64 -0.72 0.27\r\nvn 0.72 -0.64 0.28\r\nvn 0.63 -0.72 0.29\r\nvn 0.72 -0.63 0.3\r\nvn 0.79 -0.54 0.3\r\nvn 0.79 -0.54 0.28\r\nvn 0.96 0.19 0.2\r\nvn 0.96 0.19 0.21\r\nvn 0.65 0.76 0.04\r\nvn 0.65 0.76 0.04\r\nvn -0.55 0.79 -0.26\r\nvn -0.72 0.64 -0.28\r\nvn -0.55 0.79 -0.28\r\nvn -0.72 0.63 -0.3\r\nvn -0.85 0.44 -0.3\r\nvn -0.85 0.45 -0.28\r\nvn -0.94 -0.28 -0.18\r\nvn -0.94 -0.28 -0.19\r\nvn -0.65 -0.76 -0.04\r\nvn -0.65 -0.76 -0.04\r\nvn 0.63 -0.71 0.31\r\nvn 0.71 -0.63 0.32\r\nvn 0.62 -0.71 0.33\r\nvn 0.7 -0.63 0.34\r\nvn 0.78 -0.53 0.34\r\nvn 0.78 -0.54 0.32\r\nvn 0.95 0.2 0.23\r\nvn 0.95 0.2 0.24\r\nvn 0.65 0.76 0.04\r\nvn -0.54 0.78 -0.3\r\nvn -0.71 0.63 -0.32\r\nvn -0.54 0.78 -0.32\r\nvn -0.7 0.63 -0.34\r\nvn -0.83 0.44 -0.34\r\nvn -0.84 0.44 -0.32\r\nvn -0.94 -0.28 -0.21\r\nvn -0.93 -0.29 -0.22\r\nvn -0.65 -0.76 -0.04\r\nvn 0.62 -0.71 0.35\r\nvn 0.7 -0.62 0.36\r\nvn 0.61 -0.7 0.37\r\nvn 0.69 -0.62 0.38\r\nvn 0.76 -0.52 0.38\r\nvn 0.77 -0.53 0.36\r\nvn 0.95 0.2 0.25\r\nvn 0.94 0.2 0.26\r\nvn 0.65 0.76 0.04\r\nvn 0.65 0.76 0.04\r\nvn -0.53 0.78 -0.34\r\nvn -0.7 0.62 -0.36\r\nvn -0.53 0.77 -0.36\r\nvn -0.69 0.62 -0.38\r\nvn -0.82 0.43 -0.37\r\nvn -0.83 0.43 -0.35\r\nvn -0.93 -0.29 -0.23\r\nvn -0.93 -0.29 -0.24\r\nvn -0.65 -0.76 -0.04\r\nvn -0.65 -0.76 -0.04\r\nvn 0.6 -0.7 0.39\r\nvn 0.69 -0.61 0.4\r\nvn 0.6 -0.69 0.41\r\nvn 0.68 -0.61 0.42\r\nvn 0.75 -0.51 0.42\r\nvn 0.76 -0.52 0.4\r\nvn 0.94 0.21 0.27\r\nvn 0.94 0.21 0.28\r\nvn 0.66 0.75 0.02\r\nvn 0.66 0.75 0.03\r\nvn -0.52 0.77 -0.38\r\nvn -0.69 0.61 -0.4\r\nvn -0.51 0.76 -0.4\r\nvn -0.68 0.61 -0.42\r\nvn -0.81 0.42 -0.41\r\nvn -0.82 0.42 -0.39\r\nvn -0.92 -0.29 -0.24\r\nvn -0.92 -0.29 -0.25\r\nvn -0.66 -0.75 -0.02\r\nvn -0.66 -0.75 -0.03\r\nvn 0.59 -0.68 0.44\r\nvn 0.67 -0.6 0.45\r\nvn 0.55 -0.69 0.47\r\nvn 0.63 -0.6 0.49\r\nvn 0.71 -0.49 0.5\r\nvn 0.74 -0.5 0.45\r\nvn 0.94 0.22 0.27\r\nvn 0.94 0.2 0.27\r\nvn 0.71 0.7 -0.05\r\nvn 0.67 0.74 -0.01\r\nvn -0.5 0.75 -0.44\r\nvn -0.67 0.6 -0.45\r\nvn -0.45 0.74 -0.51\r\nvn -0.63 0.6 -0.5\r\nvn -0.78 0.42 -0.47\r\nvn -0.8 0.41 -0.43\r\nvn -0.92 -0.3 -0.26\r\nvn -0.91 -0.29 -0.29\r\nvn -0.66 -0.75 -0.04\r\nvn -0.66 -0.75 -0.02\r\nvn 0.48 -0.73 0.49\r\nvn 0.57 -0.63 0.52\r\nvn 0.5 -0.7 0.52\r\nvn 0.5 -0.71 0.49\r\nvn 0.48 -0.69 0.54\r\nvn 0.64 -0.51 0.57\r\nvn 0.89 0.14 0.44\r\nvn 0.62 -0.1 0.78\r\nvn 0.52 0.64 0.57\r\nvn 0.74 0.66 0.11\r\nvn -0.37 0.76 -0.53\r\nvn -0.56 0.64 -0.52\r\nvn -0.37 0.84 -0.4\r\nvn -0.5 0.72 -0.49\r\nvn -0.69 0.57 -0.45\r\nvn -0.73 0.49 -0.48\r\nvn -0.93 -0.22 -0.29\r\nvn -0.98 -0.1 -0.16\r\nvn -0.77 -0.6 0.22\r\nvn -0.71 -0.71 0\r\nvn 0.19 -0.97 -0.15\r\nvn -0.24 -0.93 0.27\r\nvn -0.67 -0.66 -0.34\r\nvn -0.15 -0.75 -0.64\r\nvn -0.06 0.75 -0.66\r\nvn -0.08 0.75 -0.65\r\nvn -0.09 0.88 -0.47\r\nvn -0.06 0.88 -0.47\r\nvn -0.11 0.75 -0.65\r\nvn -0.12 0.88 -0.46\r\nvn -0.11 0.96 -0.25\r\nvn -0.09 0.96 -0.25\r\nvn -0.06 0.97 -0.25\r\nvn -0.05 0.88 0.48\r\nvn -0.03 0.88 0.48\r\nvn -0.07 0.87 0.48\r\nvn -0 0.47 0.88\r\nvn -0 0.47 0.88\r\nvn -0 0.47 0.88\r\nvn 0.06 -0.82 0.57\r\nvn 0.09 -0.82 0.57\r\nvn 0.09 -0.88 0.47\r\nvn 0.06 -0.88 0.47\r\nvn 0.11 -0.82 0.57\r\nvn 0.12 -0.88 0.46\r\nvn 0.11 -0.93 0.36\r\nvn 0.09 -0.93 0.36\r\nvn 0.06 -0.93 0.36\r\nvn 0.06 -0.91 -0.4\r\nvn 0.04 -0.92 -0.4\r\nvn 0.08 -0.91 -0.4\r\nvn 0 -0.47 -0.88\r\nvn 0 -0.47 -0.88\r\nvn 0 -0.47 -0.88\r\nvn -0.14 0.75 -0.65\r\nvn -0.14 0.87 -0.46\r\nvn -0.16 0.74 -0.65\r\nvn -0.17 0.87 -0.46\r\nvn -0.16 0.95 -0.25\r\nvn -0.14 0.96 -0.25\r\nvn -0.08 0.87 0.48\r\nvn -0.1 0.87 0.48\r\nvn -0.01 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn 0.14 -0.81 0.56\r\nvn 0.14 -0.87 0.46\r\nvn 0.16 -0.81 0.56\r\nvn 0.17 -0.87 0.46\r\nvn 0.17 -0.92 0.35\r\nvn 0.14 -0.92 0.36\r\nvn 0.09 -0.91 -0.4\r\nvn 0.11 -0.91 -0.4\r\nvn 0.01 -0.47 -0.88\r\nvn 0.01 -0.47 -0.88\r\nvn -0.18 0.74 -0.65\r\nvn -0.19 0.87 -0.46\r\nvn -0.21 0.73 -0.65\r\nvn -0.21 0.86 -0.46\r\nvn -0.21 0.95 -0.24\r\nvn -0.19 0.95 -0.25\r\nvn -0.11 0.87 0.48\r\nvn -0.12 0.87 0.49\r\nvn -0.01 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn 0.19 -0.81 0.56\r\nvn 0.19 -0.87 0.46\r\nvn 0.21 -0.8 0.56\r\nvn 0.21 -0.86 0.46\r\nvn 0.21 -0.91 0.35\r\nvn 0.19 -0.92 0.35\r\nvn 0.12 -0.91 -0.4\r\nvn 0.14 -0.9 -0.4\r\nvn 0.01 -0.47 -0.88\r\nvn 0.01 -0.47 -0.88\r\nvn -0.23 0.73 -0.64\r\nvn -0.23 0.86 -0.46\r\nvn -0.25 0.73 -0.64\r\nvn -0.25 0.85 -0.45\r\nvn -0.25 0.94 -0.24\r\nvn -0.23 0.94 -0.24\r\nvn -0.14 0.86 0.49\r\nvn -0.15 0.86 0.49\r\nvn -0.01 0.47 0.88\r\nvn 0.23 -0.8 0.56\r\nvn 0.23 -0.86 0.46\r\nvn 0.25 -0.79 0.55\r\nvn 0.25 -0.85 0.45\r\nvn 0.25 -0.9 0.35\r\nvn 0.23 -0.91 0.35\r\nvn 0.15 -0.9 -0.41\r\nvn 0.17 -0.9 -0.41\r\nvn 0.01 -0.47 -0.88\r\nvn -0.27 0.72 -0.64\r\nvn -0.27 0.85 -0.45\r\nvn -0.28 0.72 -0.64\r\nvn -0.29 0.84 -0.45\r\nvn -0.29 0.93 -0.24\r\nvn -0.27 0.93 -0.24\r\nvn -0.16 0.86 0.49\r\nvn -0.17 0.85 0.49\r\nvn -0.01 0.47 0.88\r\nvn 0.27 -0.79 0.55\r\nvn 0.27 -0.85 0.45\r\nvn 0.29 -0.78 0.55\r\nvn 0.29 -0.84 0.45\r\nvn 0.29 -0.89 0.34\r\nvn 0.27 -0.9 0.34\r\nvn 0.18 -0.9 -0.41\r\nvn 0.19 -0.89 -0.41\r\nvn 0.01 -0.47 -0.88\r\nvn -0.3 0.71 -0.64\r\nvn -0.31 0.84 -0.45\r\nvn -0.32 0.71 -0.63\r\nvn -0.33 0.83 -0.44\r\nvn -0.32 0.92 -0.23\r\nvn -0.3 0.92 -0.23\r\nvn -0.18 0.85 0.49\r\nvn -0.18 0.85 0.49\r\nvn -0 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn 0.31 -0.78 0.55\r\nvn 0.31 -0.84 0.45\r\nvn 0.33 -0.77 0.54\r\nvn 0.33 -0.83 0.44\r\nvn 0.33 -0.88 0.34\r\nvn 0.31 -0.89 0.34\r\nvn 0.2 -0.89 -0.41\r\nvn 0.21 -0.89 -0.41\r\nvn 0 -0.47 -0.88\r\nvn 0.01 -0.47 -0.88\r\nvn -0.34 0.7 -0.63\r\nvn -0.35 0.83 -0.44\r\nvn -0.35 0.69 -0.63\r\nvn -0.36 0.82 -0.44\r\nvn -0.35 0.91 -0.23\r\nvn -0.34 0.91 -0.23\r\nvn -0.19 0.85 0.5\r\nvn -0.2 0.84 0.5\r\nvn -0 0.47 0.88\r\nvn -0 0.47 0.88\r\nvn 0.34 -0.77 0.54\r\nvn 0.35 -0.83 0.44\r\nvn 0.36 -0.76 0.54\r\nvn 0.36 -0.82 0.44\r\nvn 0.36 -0.87 0.33\r\nvn 0.34 -0.88 0.33\r\nvn 0.22 -0.88 -0.41\r\nvn 0.23 -0.88 -0.42\r\nvn 0 -0.47 -0.88\r\nvn 0 -0.47 -0.88\r\nvn -0.37 0.69 -0.62\r\nvn -0.38 0.82 -0.43\r\nvn -0.4 0.68 -0.61\r\nvn -0.41 0.81 -0.42\r\nvn -0.4 0.89 -0.21\r\nvn -0.37 0.9 -0.22\r\nvn -0.21 0.84 0.5\r\nvn -0.22 0.84 0.5\r\nvn 0.01 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0.38 -0.75 0.53\r\nvn 0.38 -0.82 0.43\r\nvn 0.41 -0.75 0.53\r\nvn 0.41 -0.81 0.42\r\nvn 0.4 -0.86 0.32\r\nvn 0.38 -0.87 0.33\r\nvn 0.24 -0.88 -0.42\r\nvn 0.24 -0.87 -0.42\r\nvn -0.01 -0.47 -0.88\r\nvn -0 -0.47 -0.88\r\nvn -0.42 0.68 -0.6\r\nvn -0.43 0.8 -0.41\r\nvn -0.44 0.67 -0.6\r\nvn -0.45 0.78 -0.44\r\nvn -0.39 0.9 -0.21\r\nvn -0.11 0.88 0.47\r\nvn 0.13 0.98 0.12\r\nvn 0.49 0.63 0.6\r\nvn 0.13 0.52 0.84\r\nvn 0.43 -0.75 0.51\r\nvn 0.43 -0.8 0.41\r\nvn 0.45 -0.79 0.42\r\nvn 0.44 -0.85 0.3\r\nvn 0.42 -0.86 0.31\r\nvn 0.25 -0.87 -0.43\r\nvn 0.26 -0.87 -0.43\r\nvn -0.02 -0.48 -0.88\r\nvn -0.01 -0.47 -0.88\r\nvn -0.46 0.66 -0.59\r\nvn -0.49 0.74 -0.46\r\nvn -0.49 0.66 -0.58\r\nvn -0.57 0.67 -0.47\r\nvn 0.5 -0.75 0.44\r\nvn 0.57 -0.67 0.47\r\nvn 0.61 -0.69 0.38\r\nvn 0.5 -0.8 0.33\r\nvn 0.37 -0.83 -0.42\r\nvn 0.76 -0.58 -0.3\r\nvn 0.47 -0.09 -0.88\r\nvn 0.08 -0.42 -0.9\r\nvn -0.48 0.64 -0.6\r\nvn -0.65 0.59 -0.49\r\nvn -0.47 0.62 -0.63\r\nvn -0.66 0.56 -0.51\r\nvn -0.81 0.47 -0.37\r\nvn -0.8 0.47 -0.37\r\nvn -0.99 -0.07 0.09\r\nvn -0.98 0.02 0.19\r\nvn -0.74 -0.36 0.57\r\nvn -0.76 -0.46 0.46\r\nvn 0.56 -0.62 0.55\r\nvn 0.64 -0.59 0.49\r\nvn 0.57 -0.59 0.57\r\nvn 0.66 -0.56 0.51\r\nvn 0.74 -0.51 0.43\r\nvn 0.71 -0.57 0.41\r\nvn 0.97 -0.17 -0.18\r\nvn 0.98 -0.09 -0.16\r\nvn 0.73 0.33 -0.6\r\nvn 0.71 0.28 -0.64\r\nvn -0.48 0.59 -0.65\r\nvn -0.66 0.54 -0.52\r\nvn -0.49 0.56 -0.67\r\nvn -0.67 0.52 -0.53\r\nvn -0.81 0.46 -0.37\r\nvn -0.81 0.47 -0.36\r\nvn -0.97 0.07 0.24\r\nvn -0.96 0.1 0.26\r\nvn -0.71 -0.22 0.67\r\nvn -0.72 -0.28 0.63\r\nvn 0.57 -0.57 0.59\r\nvn 0.66 -0.54 0.52\r\nvn 0.58 -0.54 0.61\r\nvn 0.67 -0.52 0.53\r\nvn 0.75 -0.49 0.45\r\nvn 0.74 -0.51 0.44\r\nvn 0.97 -0.13 -0.18\r\nvn 0.97 -0.15 -0.2\r\nvn 0.71 0.22 -0.67\r\nvn 0.72 0.26 -0.64\r\nvn -0.5 0.53 -0.69\r\nvn -0.67 0.5 -0.55\r\nvn -0.5 0.5 -0.7\r\nvn -0.68 0.48 -0.56\r\nvn -0.82 0.43 -0.38\r\nvn -0.81 0.44 -0.38\r\nvn -0.96 0.12 0.27\r\nvn -0.95 0.13 0.28\r\nvn -0.69 -0.17 0.7\r\nvn -0.7 -0.19 0.69\r\nvn 0.59 -0.52 0.62\r\nvn 0.67 -0.5 0.55\r\nvn 0.59 -0.49 0.64\r\nvn 0.68 -0.48 0.56\r\nvn 0.76 -0.45 0.47\r\nvn 0.75 -0.47 0.46\r\nvn 0.97 -0.16 -0.2\r\nvn 0.96 -0.17 -0.2\r\nvn 0.69 0.17 -0.7\r\nvn 0.7 0.19 -0.69\r\nvn -0.51 0.48 -0.72\r\nvn -0.69 0.45 -0.57\r\nvn -0.52 0.45 -0.73\r\nvn -0.69 0.43 -0.58\r\nvn -0.83 0.39 -0.4\r\nvn -0.82 0.41 -0.39\r\nvn -0.95 0.13 0.28\r\nvn -0.95 0.13 0.28\r\nvn -0.68 -0.13 0.72\r\nvn -0.69 -0.14 0.71\r\nvn 0.6 -0.47 0.65\r\nvn 0.69 -0.45 0.57\r\nvn 0.61 -0.44 0.66\r\nvn 0.69 -0.43 0.58\r\nvn 0.77 -0.42 0.49\r\nvn 0.76 -0.43 0.48\r\nvn 0.96 -0.17 -0.21\r\nvn 0.96 -0.17 -0.21\r\nvn 0.68 0.13 -0.72\r\nvn 0.69 0.14 -0.71\r\nvn -0.53 0.43 -0.74\r\nvn -0.7 0.41 -0.59\r\nvn -0.53 0.4 -0.75\r\nvn -0.7 0.39 -0.59\r\nvn -0.84 0.36 -0.41\r\nvn -0.83 0.38 -0.41\r\nvn -0.95 0.14 0.28\r\nvn -0.95 0.14 0.28\r\nvn -0.67 -0.1 0.73\r\nvn -0.68 -0.11 0.73\r\nvn 0.61 -0.42 0.67\r\nvn 0.7 -0.41 0.59\r\nvn 0.62 -0.4 0.68\r\nvn 0.7 -0.39 0.59\r\nvn 0.78 -0.38 0.5\r\nvn 0.77 -0.4 0.49\r\nvn 0.96 -0.17 -0.2\r\nvn 0.96 -0.17 -0.2\r\nvn 0.67 0.1 -0.73\r\nvn 0.68 0.11 -0.73\r\nvn -0.54 0.38 -0.75\r\nvn -0.71 0.37 -0.6\r\nvn -0.54 0.36 -0.76\r\nvn -0.71 0.35 -0.61\r\nvn -0.85 0.33 -0.42\r\nvn -0.84 0.34 -0.42\r\nvn -0.95 0.14 0.28\r\nvn -0.95 0.13 0.28\r\nvn -0.67 -0.07 0.74\r\nvn -0.67 -0.08 0.74\r\nvn 0.62 -0.38 0.68\r\nvn 0.71 -0.37 0.6\r\nvn 0.63 -0.36 0.69\r\nvn 0.71 -0.35 0.61\r\nvn 0.79 -0.34 0.51\r\nvn 0.78 -0.36 0.51\r\nvn 0.96 -0.17 -0.2\r\nvn 0.97 -0.16 -0.2\r\nvn 0.67 0.07 -0.74\r\nvn 0.67 0.08 -0.74\r\nvn -0.55 0.34 -0.77\r\nvn -0.72 0.33 -0.61\r\nvn -0.55 0.32 -0.77\r\nvn -0.72 0.31 -0.62\r\nvn -0.85 0.29 -0.43\r\nvn -0.85 0.31 -0.43\r\nvn -0.95 0.13 0.28\r\nvn -0.95 0.13 0.28\r\nvn -0.66 -0.05 0.75\r\nvn -0.66 -0.06 0.74\r\nvn 0.63 -0.34 0.7\r\nvn 0.72 -0.33 0.61\r\nvn 0.64 -0.32 0.7\r\nvn 0.72 -0.31 0.62\r\nvn 0.79 -0.31 0.52\r\nvn 0.79 -0.32 0.52\r\nvn 0.97 -0.16 -0.2\r\nvn 0.97 -0.15 -0.2\r\nvn 0.66 0.05 -0.75\r\nvn 0.66 0.06 -0.74\r\nvn -0.56 0.3 -0.78\r\nvn -0.72 0.3 -0.62\r\nvn -0.56 0.28 -0.78\r\nvn -0.73 0.28 -0.63\r\nvn -0.86 0.26 -0.44\r\nvn -0.86 0.28 -0.44\r\nvn -0.95 0.12 0.28\r\nvn -0.95 0.12 0.28\r\nvn -0.66 -0.04 0.75\r\nvn -0.66 -0.05 0.75\r\nvn 0.64 -0.3 0.71\r\nvn 0.73 -0.3 0.62\r\nvn 0.65 -0.28 0.71\r\nvn 0.73 -0.28 0.63\r\nvn 0.8 -0.27 0.53\r\nvn 0.8 -0.29 0.53\r\nvn 0.97 -0.15 -0.2\r\nvn 0.97 -0.14 -0.19\r\nvn 0.66 0.04 -0.75\r\nvn 0.66 0.05 -0.75\r\nvn -0.56 0.26 -0.78\r\nvn -0.73 0.26 -0.63\r\nvn -0.57 0.24 -0.79\r\nvn -0.74 0.24 -0.63\r\nvn -0.87 0.23 -0.45\r\nvn -0.86 0.24 -0.44\r\nvn -0.95 0.11 0.28\r\nvn -0.95 0.1 0.28\r\nvn -0.66 -0.03 0.75\r\nvn -0.66 -0.04 0.75\r\nvn 0.65 -0.26 0.72\r\nvn 0.73 -0.26 0.63\r\nvn 0.65 -0.24 0.72\r\nvn 0.74 -0.24 0.63\r\nvn 0.81 -0.23 0.54\r\nvn 0.81 -0.25 0.54\r\nvn 0.97 -0.13 -0.19\r\nvn 0.97 -0.12 -0.19\r\nvn 0.66 0.03 -0.75\r\nvn 0.66 0.04 -0.75\r\nvn -0.57 0.22 -0.79\r\nvn -0.74 0.22 -0.64\r\nvn -0.57 0.21 -0.79\r\nvn -0.74 0.21 -0.64\r\nvn -0.87 0.19 -0.45\r\nvn -0.87 0.21 -0.45\r\nvn -0.96 0.1 0.28\r\nvn -0.96 0.09 0.28\r\nvn -0.66 -0.03 0.75\r\nvn -0.66 -0.03 0.75\r\nvn 0.65 -0.22 0.72\r\nvn 0.74 -0.22 0.64\r\nvn 0.66 -0.21 0.72\r\nvn 0.74 -0.21 0.64\r\nvn 0.81 -0.2 0.55\r\nvn 0.81 -0.22 0.54\r\nvn 0.98 -0.12 -0.19\r\nvn 0.98 -0.11 -0.19\r\nvn 0.66 0.03 -0.75\r\nvn 0.66 0.03 -0.75\r\nvn -0.57 0.19 -0.8\r\nvn -0.74 0.19 -0.64\r\nvn -0.57 0.18 -0.8\r\nvn -0.74 0.18 -0.64\r\nvn -0.87 0.17 -0.46\r\nvn -0.87 0.18 -0.45\r\nvn -0.96 0.08 0.28\r\nvn -0.96 0.07 0.27\r\nvn -0.66 -0.03 0.75\r\nvn 0.66 -0.19 0.73\r\nvn 0.74 -0.19 0.64\r\nvn 0.66 -0.18 0.73\r\nvn 0.74 -0.18 0.64\r\nvn 0.82 -0.17 0.55\r\nvn 0.82 -0.19 0.55\r\nvn 0.98 -0.1 -0.18\r\nvn 0.98 -0.09 -0.18\r\nvn 0.66 0.03 -0.75\r\nvn -0.58 0.17 -0.8\r\nvn -0.75 0.16 -0.65\r\nvn -0.58 0.16 -0.8\r\nvn -0.75 0.15 -0.65\r\nvn -0.88 0.14 -0.46\r\nvn -0.88 0.15 -0.46\r\nvn -0.96 0.06 0.27\r\nvn -0.96 0.06 0.27\r\nvn -0.66 -0.03 0.75\r\nvn -0.66 -0.03 0.75\r\nvn 0.66 -0.17 0.73\r\nvn 0.75 -0.16 0.65\r\nvn 0.66 -0.15 0.73\r\nvn 0.75 -0.15 0.65\r\nvn 0.82 -0.15 0.55\r\nvn 0.82 -0.16 0.55\r\nvn 0.98 -0.08 -0.18\r\nvn 0.98 -0.07 -0.18\r\nvn 0.66 0.03 -0.75\r\nvn 0.66 0.03 -0.75\r\nvn -0.58 0.15 -0.8\r\nvn -0.75 0.14 -0.65\r\nvn -0.58 0.14 -0.8\r\nvn -0.75 0.13 -0.65\r\nvn -0.88 0.12 -0.46\r\nvn -0.88 0.13 -0.46\r\nvn -0.96 0.05 0.27\r\nvn -0.96 0.04 0.27\r\nvn -0.66 -0.04 0.75\r\nvn -0.66 -0.04 0.75\r\nvn 0.66 -0.14 0.73\r\nvn 0.75 -0.14 0.65\r\nvn 0.66 -0.14 0.73\r\nvn 0.75 -0.13 0.65\r\nvn 0.82 -0.13 0.56\r\nvn 0.82 -0.14 0.56\r\nvn 0.98 -0.06 -0.18\r\nvn 0.98 -0.05 -0.18\r\nvn 0.66 0.04 -0.75\r\nvn 0.66 0.04 -0.75\r\nvn -0.58 0.13 -0.8\r\nvn -0.75 0.13 -0.65\r\nvn -0.58 0.13 -0.8\r\nvn -0.75 0.12 -0.65\r\nvn -0.88 0.11 -0.46\r\nvn -0.88 0.11 -0.46\r\nvn -0.96 0.03 0.27\r\nvn -0.96 0.02 0.27\r\nvn -0.66 -0.05 0.75\r\nvn -0.66 -0.05 0.75\r\nvn 0.67 -0.13 0.74\r\nvn 0.75 -0.13 0.65\r\nvn 0.67 -0.13 0.74\r\nvn 0.75 -0.12 0.65\r\nvn 0.82 -0.12 0.56\r\nvn 0.82 -0.12 0.56\r\nvn 0.98 -0.04 -0.18\r\nvn 0.98 -0.04 -0.17\r\nvn 0.66 0.05 -0.75\r\nvn 0.66 0.05 -0.75\r\nvn -0.58 0.13 -0.8\r\nvn -0.75 0.12 -0.65\r\nvn -0.58 0.13 -0.8\r\nvn -0.75 0.12 -0.65\r\nvn -0.88 0.1 -0.46\r\nvn -0.88 0.1 -0.46\r\nvn -0.96 0.01 0.27\r\nvn -0.96 0.01 0.27\r\nvn -0.66 -0.07 0.75\r\nvn -0.66 -0.06 0.75\r\nvn 0.67 -0.13 0.74\r\nvn 0.75 -0.12 0.65\r\nvn 0.67 -0.13 0.74\r\nvn 0.75 -0.12 0.65\r\nvn 0.82 -0.11 0.56\r\nvn 0.82 -0.11 0.56\r\nvn 0.98 -0.03 -0.17\r\nvn 0.98 -0.02 -0.17\r\nvn 0.66 0.07 -0.75\r\nvn 0.66 0.06 -0.75\r\nvn -0.58 0.14 -0.8\r\nvn -0.75 0.12 -0.65\r\nvn -0.58 0.15 -0.8\r\nvn -0.75 0.13 -0.65\r\nvn -0.88 0.11 -0.46\r\nvn -0.88 0.1 -0.46\r\nvn -0.96 0 0.27\r\nvn -0.96 -0.01 0.27\r\nvn -0.66 -0.09 0.74\r\nvn -0.66 -0.08 0.75\r\nvn 0.66 -0.13 0.73\r\nvn 0.75 -0.12 0.65\r\nvn 0.66 -0.14 0.73\r\nvn 0.75 -0.13 0.65\r\nvn 0.82 -0.12 0.56\r\nvn 0.82 -0.11 0.56\r\nvn 0.99 -0.02 -0.17\r\nvn 0.99 -0.01 -0.17\r\nvn 0.66 0.09 -0.74\r\nvn 0.66 0.08 -0.75\r\nvn -0.58 0.15 -0.8\r\nvn -0.75 0.14 -0.65\r\nvn -0.58 0.16 -0.8\r\nvn -0.75 0.14 -0.65\r\nvn -0.88 0.11 -0.46\r\nvn -0.88 0.11 -0.46\r\nvn -0.96 -0.01 0.27\r\nvn -0.96 -0.02 0.27\r\nvn -0.66 -0.11 0.74\r\nvn -0.66 -0.1 0.74\r\nvn 0.66 -0.15 0.73\r\nvn 0.75 -0.14 0.65\r\nvn 0.66 -0.15 0.73\r\nvn 0.75 -0.14 0.65\r\nvn 0.82 -0.13 0.56\r\nvn 0.82 -0.12 0.56\r\nvn 0.99 -0.01 -0.17\r\nvn 0.99 -0 -0.17\r\nvn 0.66 0.11 -0.74\r\nvn 0.66 0.1 -0.74\r\nvn -0.58 0.17 -0.8\r\nvn -0.75 0.14 -0.65\r\nvn -0.58 0.17 -0.8\r\nvn -0.75 0.15 -0.65\r\nvn -0.88 0.12 -0.46\r\nvn -0.88 0.11 -0.46\r\nvn -0.96 -0.02 0.26\r\nvn -0.96 -0.02 0.26\r\nvn -0.66 -0.12 0.74\r\nvn -0.66 -0.12 0.74\r\nvn 0.66 -0.16 0.73\r\nvn 0.75 -0.14 0.65\r\nvn 0.66 -0.16 0.73\r\nvn 0.75 -0.15 0.65\r\nvn 0.82 -0.13 0.55\r\nvn 0.82 -0.13 0.56\r\nvn 0.99 0 -0.17\r\nvn 0.99 0 -0.17\r\nvn 0.66 0.12 -0.74\r\nvn 0.66 0.12 -0.74\r\nvn -0.58 0.18 -0.8\r\nvn -0.75 0.15 -0.65\r\nvn -0.58 0.19 -0.8\r\nvn -0.75 0.16 -0.64\r\nvn -0.88 0.12 -0.46\r\nvn -0.88 0.12 -0.46\r\nvn -0.96 -0.03 0.26\r\nvn -0.96 -0.03 0.26\r\nvn -0.66 -0.14 0.74\r\nvn -0.66 -0.13 0.74\r\nvn 0.66 -0.17 0.73\r\nvn 0.75 -0.15 0.65\r\nvn 0.66 -0.17 0.73\r\nvn 0.75 -0.16 0.64\r\nvn 0.82 -0.14 0.55\r\nvn 0.82 -0.14 0.55\r\nvn 0.99 0.01 -0.17\r\nvn 0.99 0.01 -0.17\r\nvn 0.66 0.14 -0.74\r\nvn 0.66 0.13 -0.74\r\nvn -0.58 0.19 -0.79\r\nvn -0.75 0.16 -0.64\r\nvn -0.58 0.2 -0.79\r\nvn -0.75 0.17 -0.64\r\nvn -0.88 0.13 -0.46\r\nvn -0.88 0.13 -0.46\r\nvn -0.96 -0.03 0.26\r\nvn -0.96 -0.03 0.26\r\nvn -0.66 -0.15 0.73\r\nvn -0.66 -0.14 0.73\r\nvn 0.66 -0.18 0.73\r\nvn 0.75 -0.16 0.64\r\nvn 0.66 -0.19 0.72\r\nvn 0.75 -0.17 0.64\r\nvn 0.82 -0.15 0.55\r\nvn 0.82 -0.15 0.55\r\nvn 0.99 0.01 -0.17\r\nvn 0.99 0.01 -0.17\r\nvn 0.66 0.15 -0.73\r\nvn 0.66 0.14 -0.73\r\nvn -0.58 0.21 -0.79\r\nvn -0.75 0.18 -0.64\r\nvn -0.58 0.22 -0.79\r\nvn -0.75 0.19 -0.64\r\nvn -0.88 0.15 -0.45\r\nvn -0.88 0.14 -0.46\r\nvn -0.96 -0.03 0.26\r\nvn -0.96 -0.03 0.26\r\nvn -0.66 -0.16 0.73\r\nvn -0.66 -0.15 0.73\r\nvn 0.66 -0.19 0.72\r\nvn 0.75 -0.18 0.64\r\nvn 0.66 -0.21 0.72\r\nvn 0.75 -0.19 0.64\r\nvn 0.82 -0.17 0.55\r\nvn 0.82 -0.16 0.55\r\nvn 0.99 0.01 -0.17\r\nvn 0.99 0.01 -0.16\r\nvn 0.66 0.16 -0.73\r\nvn 0.66 0.15 -0.73\r\nvn -0.58 0.24 -0.78\r\nvn -0.75 0.21 -0.63\r\nvn -0.57 0.27 -0.77\r\nvn -0.75 0.23 -0.63\r\nvn -0.88 0.18 -0.44\r\nvn -0.88 0.16 -0.45\r\nvn -0.96 -0.03 0.27\r\nvn -0.96 -0.03 0.27\r\nvn -0.67 -0.19 0.72\r\nvn -0.66 -0.17 0.73\r\nvn 0.66 -0.23 0.71\r\nvn 0.75 -0.21 0.63\r\nvn 0.66 -0.25 0.71\r\nvn 0.74 -0.23 0.63\r\nvn 0.82 -0.21 0.54\r\nvn 0.82 -0.19 0.54\r\nvn 0.99 0 -0.16\r\nvn 0.99 -0 -0.16\r\nvn 0.67 0.19 -0.72\r\nvn 0.66 0.17 -0.73\r\nvn -0.57 0.3 -0.76\r\nvn -0.74 0.26 -0.62\r\nvn -0.57 0.34 -0.75\r\nvn -0.74 0.3 -0.6\r\nvn -0.87 0.24 -0.42\r\nvn -0.88 0.21 -0.44\r\nvn -0.96 -0.03 0.27\r\nvn -0.96 -0.03 0.27\r\nvn -0.67 -0.23 0.71\r\nvn -0.67 -0.21 0.72\r\nvn 0.66 -0.29 0.7\r\nvn 0.74 -0.26 0.62\r\nvn 0.66 -0.33 0.68\r\nvn 0.74 -0.3 0.6\r\nvn 0.81 -0.27 0.51\r\nvn 0.82 -0.24 0.53\r\nvn 0.99 -0 -0.16\r\nvn 0.99 -0.01 -0.16\r\nvn 0.67 0.23 -0.71\r\nvn 0.67 0.21 -0.72\r\nvn -0.57 0.39 -0.73\r\nvn -0.74 0.34 -0.58\r\nvn -0.56 0.43 -0.71\r\nvn -0.73 0.38 -0.57\r\nvn -0.87 0.3 -0.4\r\nvn -0.87 0.27 -0.41\r\nvn -0.96 -0.03 0.27\r\nvn -0.96 -0.03 0.27\r\nvn -0.67 -0.28 0.68\r\nvn -0.67 -0.26 0.69\r\nvn 0.65 -0.37 0.66\r\nvn 0.74 -0.34 0.58\r\nvn 0.65 -0.41 0.64\r\nvn 0.73 -0.38 0.57\r\nvn 0.81 -0.34 0.48\r\nvn 0.81 -0.31 0.5\r\nvn 0.99 -0.02 -0.17\r\nvn 0.99 -0.02 -0.17\r\nvn 0.67 0.28 -0.68\r\nvn 0.67 0.26 -0.7\r\nvn -0.56 0.46 -0.69\r\nvn -0.73 0.41 -0.55\r\nvn -0.55 0.5 -0.67\r\nvn -0.73 0.43 -0.53\r\nvn -0.86 0.35 -0.37\r\nvn -0.87 0.33 -0.38\r\nvn -0.96 -0.03 0.27\r\nvn -0.96 -0.03 0.27\r\nvn -0.68 -0.32 0.66\r\nvn -0.67 -0.3 0.67\r\nvn 0.64 -0.44 0.63\r\nvn 0.73 -0.41 0.55\r\nvn 0.64 -0.47 0.61\r\nvn 0.73 -0.43 0.53\r\nvn 0.8 -0.39 0.45\r\nvn 0.8 -0.37 0.47\r\nvn 0.98 -0.03 -0.17\r\nvn 0.98 -0.03 -0.17\r\nvn 0.68 0.32 -0.66\r\nvn 0.67 0.3 -0.67\r\nvn -0.55 0.53 -0.65\r\nvn -0.72 0.46 -0.51\r\nvn -0.54 0.56 -0.63\r\nvn -0.72 0.49 -0.5\r\nvn -0.86 0.39 -0.34\r\nvn -0.86 0.37 -0.35\r\nvn -0.96 -0.04 0.27\r\nvn -0.96 -0.04 0.26\r\nvn -0.68 -0.36 0.63\r\nvn -0.68 -0.34 0.65\r\nvn 0.64 -0.5 0.59\r\nvn 0.72 -0.46 0.52\r\nvn 0.63 -0.53 0.57\r\nvn 0.72 -0.49 0.5\r\nvn 0.79 -0.44 0.42\r\nvn 0.8 -0.42 0.43\r\nvn 0.98 -0.03 -0.18\r\nvn 0.98 -0.03 -0.18\r\nvn 0.68 0.36 -0.63\r\nvn 0.68 0.34 -0.65\r\nvn -0.54 0.58 -0.61\r\nvn -0.72 0.51 -0.48\r\nvn -0.54 0.6 -0.6\r\nvn -0.71 0.52 -0.47\r\nvn -0.85 0.42 -0.32\r\nvn -0.85 0.41 -0.33\r\nvn -0.96 -0.04 0.26\r\nvn -0.97 -0.05 0.26\r\nvn -0.69 -0.39 0.61\r\nvn -0.69 -0.38 0.62\r\nvn 0.63 -0.55 0.55\r\nvn 0.71 -0.51 0.48\r\nvn 0.63 -0.56 0.54\r\nvn 0.71 -0.52 0.47\r\nvn 0.79 -0.47 0.39\r\nvn 0.79 -0.46 0.41\r\nvn 0.98 -0.03 -0.18\r\nvn 0.98 -0.03 -0.18\r\nvn 0.69 0.39 -0.61\r\nvn 0.69 0.38 -0.62\r\nvn -0.53 0.61 -0.58\r\nvn -0.71 0.53 -0.46\r\nvn -0.53 0.62 -0.57\r\nvn -0.71 0.54 -0.45\r\nvn -0.85 0.43 -0.3\r\nvn -0.85 0.43 -0.31\r\nvn -0.97 -0.05 0.25\r\nvn -0.97 -0.05 0.25\r\nvn -0.69 -0.42 0.59\r\nvn -0.69 -0.41 0.6\r\nvn 0.62 -0.58 0.53\r\nvn 0.71 -0.53 0.46\r\nvn 0.62 -0.59 0.52\r\nvn 0.71 -0.54 0.45\r\nvn 0.79 -0.49 0.38\r\nvn 0.79 -0.48 0.39\r\nvn 0.98 -0.02 -0.17\r\nvn 0.98 -0.02 -0.17\r\nvn 0.69 0.42 -0.59\r\nvn 0.69 0.41 -0.6\r\nvn -0.53 0.63 -0.56\r\nvn -0.71 0.55 -0.44\r\nvn -0.53 0.64 -0.55\r\nvn -0.71 0.56 -0.44\r\nvn -0.85 0.45 -0.29\r\nvn -0.85 0.44 -0.3\r\nvn -0.97 -0.06 0.24\r\nvn -0.97 -0.06 0.24\r\nvn -0.7 -0.43 0.57\r\nvn -0.69 -0.42 0.58\r\nvn 0.62 -0.6 0.51\r\nvn 0.71 -0.55 0.44\r\nvn 0.62 -0.61 0.5\r\nvn 0.71 -0.56 0.44\r\nvn 0.78 -0.5 0.37\r\nvn 0.78 -0.5 0.37\r\nvn 0.99 -0.02 -0.17\r\nvn 0.99 -0.02 -0.17\r\nvn 0.7 0.43 -0.57\r\nvn 0.69 0.42 -0.58\r\nvn -0.53 0.65 -0.54\r\nvn -0.7 0.57 -0.43\r\nvn -0.53 0.66 -0.53\r\nvn -0.7 0.57 -0.42\r\nvn -0.84 0.46 -0.28\r\nvn -0.84 0.45 -0.29\r\nvn -0.97 -0.06 0.24\r\nvn -0.97 -0.06 0.23\r\nvn -0.7 -0.45 0.55\r\nvn -0.7 -0.44 0.56\r\nvn 0.62 -0.61 0.49\r\nvn 0.7 -0.57 0.43\r\nvn 0.61 -0.62 0.48\r\nvn 0.7 -0.57 0.42\r\nvn 0.78 -0.52 0.35\r\nvn 0.78 -0.51 0.36\r\nvn 0.99 -0.01 -0.17\r\nvn 0.99 -0.01 -0.17\r\nvn 0.7 0.45 -0.55\r\nvn 0.7 0.44 -0.56\r\nvn -0.52 0.67 -0.52\r\nvn -0.7 0.58 -0.41\r\nvn -0.52 0.68 -0.51\r\nvn -0.7 0.59 -0.4\r\nvn -0.84 0.47 -0.27\r\nvn -0.84 0.46 -0.28\r\nvn -0.97 -0.07 0.23\r\nvn -0.97 -0.07 0.23\r\nvn -0.7 -0.47 0.53\r\nvn -0.7 -0.46 0.54\r\nvn 0.61 -0.63 0.47\r\nvn 0.7 -0.58 0.41\r\nvn 0.61 -0.64 0.46\r\nvn 0.7 -0.59 0.4\r\nvn 0.78 -0.53 0.34\r\nvn 0.78 -0.52 0.34\r\nvn 0.99 -0.01 -0.16\r\nvn 0.99 -0.01 -0.16\r\nvn 0.7 0.47 -0.53\r\nvn 0.7 0.46 -0.54\r\nvn -0.52 0.69 -0.5\r\nvn -0.7 0.6 -0.39\r\nvn -0.52 0.7 -0.49\r\nvn -0.7 0.61 -0.38\r\nvn -0.84 0.48 -0.25\r\nvn -0.84 0.47 -0.26\r\nvn -0.97 -0.07 0.22\r\nvn -0.97 -0.08 0.22\r\nvn -0.71 -0.49 0.51\r\nvn -0.71 -0.48 0.52\r\nvn 0.61 -0.65 0.45\r\nvn 0.7 -0.6 0.39\r\nvn 0.61 -0.66 0.44\r\nvn 0.7 -0.61 0.38\r\nvn 0.77 -0.55 0.32\r\nvn 0.78 -0.54 0.33\r\nvn 0.99 -0.01 -0.16\r\nvn 0.99 -0 -0.16\r\nvn 0.71 0.49 -0.51\r\nvn 0.71 0.48 -0.52\r\nvn -0.51 0.66 -0.56\r\nvn -0.7 0.6 -0.4\r\nvn -0.59 0.53 -0.6\r\nvn -0.7 0.38 -0.61\r\nvn -0.8 0.39 -0.45\r\nvn -0.86 0.41 -0.31\r\nvn -0.97 -0.21 0.09\r\nvn -0.83 -0.36 -0.43\r\nvn -0.54 -0.79 -0.29\r\nvn -0.72 -0.62 0.32\r\nvn 0.59 -0.71 0.39\r\nvn 0.69 -0.63 0.36\r\nvn 0.62 -0.72 0.32\r\nvn 0.66 -0.72 0.22\r\nvn 0.73 -0.64 0.24\r\nvn 0.77 -0.58 0.26\r\nvn 0.95 -0.07 -0.31\r\nvn 0.69 -0.47 -0.55\r\nvn 0.35 -0.16 -0.92\r\nvn 0.67 0.35 -0.66\r\nvn -0.06 -1 -0.02\r\nvn 0.28 -0.85 -0.45\r\nvn -0.55 -0.79 -0.29\r\nvn -0.13 -0.61 -0.78\r\nvn 0.21 0.22 0.95\r\nvn 0.2 0.1 0.97\r\nvn 0.65 -0.11 0.75\r\nvn -0.08 -0.33 0.94\r\nvn 0.34 -0.62 0.71\r\nvn -0.72 -0.17 -0.67\r\nvn -0.67 -0.66 -0.34\r\nvn 0.69 0.02 0.72\r\nvn 0.21 0.21 0.95\r\nvn 0.21 0.22 0.95\r\nvn -0.69 -0.02 -0.72\r\nvn 0.2 0.21 0.96\r\nvn 0.2 0.21 0.96\r\nvn 0.2 0.2 0.96\r\nvn 0.2 0.2 0.96\r\nvn 0.69 0.01 0.72\r\nvn -0.69 -0.01 -0.72\r\nvn 0.2 0.19 0.96\r\nvn 0.2 0.2 0.96\r\nvn 0.2 0.19 0.96\r\nvn 0.2 0.19 0.96\r\nvn 0.69 -0 0.73\r\nvn 0.2 0.18 0.96\r\nvn 0.2 0.18 0.96\r\nvn 0.68 -0.01 0.73\r\nvn 0.19 0.16 0.97\r\nvn 0.2 0.17 0.97\r\nvn -0.68 0.01 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.19 0.13 0.97\r\nvn 0.19 0.14 0.97\r\nvn 0.68 -0.01 0.73\r\nvn -0.68 0.01 -0.73\r\nvn 0.18 0.1 0.98\r\nvn 0.18 0.11 0.98\r\nvn -0.68 0.03 -0.73\r\nvn 0.18 0.07 0.98\r\nvn 0.18 0.08 0.98\r\nvn 0.68 -0.03 0.73\r\nvn -0.68 0.03 -0.73\r\nvn 0.17 0.05 0.98\r\nvn 0.18 0.06 0.98\r\nvn -0.68 0.03 -0.73\r\nvn 0.17 0.03 0.98\r\nvn 0.17 0.04 0.98\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.03 0.98\r\nvn -0.68 0.03 -0.73\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.02 0.99\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.03 0.99\r\nvn 0.17 0.04 0.98\r\nvn 0.17 0.04 0.98\r\nvn 0.17 0.06 0.98\r\nvn 0.17 0.05 0.98\r\nvn 0.17 0.08 0.98\r\nvn 0.17 0.07 0.98\r\nvn 0.18 0.11 0.98\r\nvn 0.17 0.09 0.98\r\nvn 0.18 0.14 0.97\r\nvn 0.18 0.12 0.98\r\nvn 0.19 0.17 0.97\r\nvn 0.18 0.15 0.97\r\nvn 0.19 0.21 0.96\r\nvn 0.19 0.19 0.96\r\nvn 0.2 0.25 0.95\r\nvn 0.2 0.23 0.95\r\nvn 0.21 0.29 0.93\r\nvn 0.2 0.27 0.94\r\nvn -0.7 -0.1 -0.71\r\nvn -0.7 -0.09 -0.71\r\nvn 0.22 0.34 0.91\r\nvn 0.21 0.32 0.92\r\nvn 0.7 0.12 0.7\r\nvn -0.71 -0.13 -0.69\r\nvn -0.7 -0.12 -0.7\r\nvn -0.71 -0.13 -0.69\r\nvn 0.72 0.17 0.68\r\nvn 0.23 0.4 0.89\r\nvn 0.23 0.37 0.9\r\nvn -0.72 -0.17 -0.68\r\nvn -0.72 -0.17 -0.68\r\nvn 0.73 0.22 0.65\r\nvn 0.25 0.47 0.85\r\nvn 0.24 0.43 0.87\r\nvn 0.72 0.19 0.66\r\nvn -0.73 -0.22 -0.65\r\nvn -0.72 -0.19 -0.66\r\nvn -0.73 -0.22 -0.65\r\nvn -0.72 -0.19 -0.66\r\nvn 0.75 0.34 0.57\r\nvn 0.28 0.58 0.76\r\nvn 0.27 0.51 0.81\r\nvn 0.74 0.27 0.62\r\nvn -0.75 -0.33 -0.57\r\nvn -0.74 -0.26 -0.62\r\nvn -0.75 -0.33 -0.57\r\nvn -0.74 -0.26 -0.62\r\nvn -0.76 -0.42 -0.49\r\nvn -0.76 -0.42 -0.49\r\nvn -0.71 -0.69 -0.12\r\nvn -0.74 -0.63 -0.23\r\nvn -0.71 -0.69 -0.12\r\nvn -0.74 -0.63 -0.23\r\nvn 0.68 0.74 0.01\r\nvn 0.2 0.95 0.25\r\nvn 0.22 0.93 0.3\r\nvn 0.69 0.72 0.05\r\nvn 0.25 0.89 0.37\r\nvn 0.72 0.69 0.13\r\nvn -0.67 -0.74 -0.01\r\nvn -0.69 -0.72 -0.05\r\nvn -0.67 -0.74 -0.01\r\nvn -0.69 -0.72 -0.05\r\nvn 0.66 0.75 -0.03\r\nvn 0.18 0.96 0.2\r\nvn 0.19 0.96 0.22\r\nvn 0.67 0.75 -0.02\r\nvn -0.66 -0.75 0.03\r\nvn -0.67 -0.75 0.02\r\nvn -0.67 -0.75 0.02\r\nvn 0.65 0.75 -0.05\r\nvn 0.17 0.97 0.16\r\nvn 0.17 0.97 0.18\r\nvn 0.66 0.75 -0.05\r\nvn -0.65 -0.75 0.05\r\nvn -0.66 -0.75 0.05\r\nvn 0.16 0.98 0.14\r\nvn 0.16 0.98 0.15\r\nvn 0.15 0.98 0.12\r\nvn 0.16 0.98 0.13\r\nvn 0.15 0.98 0.1\r\nvn 0.15 0.98 0.11\r\nvn 0.65 0.76 -0.05\r\nvn 0.14 0.99 0.08\r\nvn 0.15 0.99 0.09\r\nvn 0.14 0.99 0.07\r\nvn 0.14 0.99 0.08\r\nvn 0.14 0.99 0.06\r\nvn 0.14 0.99 0.06\r\nvn 0.14 0.99 0.05\r\nvn 0.14 0.99 0.05\r\nvn -0.66 -0.75 0.04\r\nvn 0.14 0.99 0.04\r\nvn 0.14 0.99 0.04\r\nvn 0.14 0.99 0.03\r\nvn 0.14 0.99 0.03\r\nvn 0.14 0.99 0.02\r\nvn 0.14 0.99 0.02\r\nvn 0.14 0.99 0.01\r\nvn 0.14 0.99 0.01\r\nvn 0.14 0.99 -0\r\nvn 0.14 0.99 0\r\nvn 0.66 0.75 -0.01\r\nvn -0.66 -0.75 0.01\r\nvn 0.14 0.99 -0.01\r\nvn 0.14 0.99 -0.01\r\nvn 0.14 0.99 -0.02\r\nvn 0.14 0.99 -0.02\r\nvn 0.14 0.99 -0.03\r\nvn 0.14 0.99 -0.03\r\nvn 0.66 0.75 0.01\r\nvn 0.14 0.99 -0.04\r\nvn 0.14 0.99 -0.04\r\nvn -0.66 -0.75 -0.01\r\nvn 0.14 0.99 -0.05\r\nvn 0.14 0.99 -0.05\r\nvn 0.14 0.99 -0.07\r\nvn 0.14 0.99 -0.06\r\nvn 0.14 0.99 -0.08\r\nvn 0.14 0.99 -0.07\r\nvn -0.66 -0.75 -0.03\r\nvn 0.14 0.99 -0.09\r\nvn 0.14 0.99 -0.09\r\nvn -0.65 -0.76 -0.03\r\nvn 0.15 0.98 -0.11\r\nvn 0.14 0.98 -0.1\r\nvn 0.15 0.98 -0.13\r\nvn 0.15 0.98 -0.12\r\nvn 0.16 0.98 -0.15\r\nvn 0.15 0.98 -0.14\r\nvn 0.65 0.76 0.04\r\nvn 0.17 0.97 -0.18\r\nvn 0.16 0.97 -0.17\r\nvn 0.65 0.76 0.04\r\nvn -0.65 -0.76 -0.04\r\nvn -0.65 -0.76 -0.04\r\nvn 0.66 0.75 0.02\r\nvn 0.18 0.96 -0.21\r\nvn 0.17 0.97 -0.19\r\nvn -0.66 -0.75 -0.02\r\nvn -0.66 -0.75 -0.03\r\nvn -0.66 -0.75 -0.03\r\nvn 0.7 0.72 -0\r\nvn 0.23 0.93 -0.29\r\nvn 0.18 0.95 -0.25\r\nvn 0.67 0.75 0\r\nvn -0.67 -0.75 -0.03\r\nvn -0.66 -0.75 -0.02\r\nvn -0.67 -0.74 -0.02\r\nvn -0.66 -0.75 -0.01\r\nvn 0.28 0.94 -0.2\r\nvn 0.71 0.68 0.17\r\nvn -0.77 -0.6 0.22\r\nvn -0.71 -0.7 0.01\r\nvn -0.77 -0.6 0.23\r\nvn -0.71 -0.7 0.02\r\nvn -0.55 -0.62 0.56\r\nvn -0 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn -0.03 0.1 -0.99\r\nvn -0.02 0.1 -1\r\nvn 0 -0.47 -0.88\r\nvn -0.03 0.75 -0.66\r\nvn 0 0.75 -0.66\r\nvn -0 0.1 -1\r\nvn -0 -0.47 -0.88\r\nvn -0.06 0.09 -0.99\r\nvn -0.05 0.1 -0.99\r\nvn -0.09 0.09 -0.99\r\nvn -0.07 0.09 -0.99\r\nvn -0.11 0.09 -0.99\r\nvn -0.1 0.09 -0.99\r\nvn -0.13 0.08 -0.99\r\nvn -0.12 0.08 -0.99\r\nvn -0.16 0.07 -0.99\r\nvn -0.14 0.08 -0.99\r\nvn -0.18 0.07 -0.98\r\nvn -0.17 0.07 -0.98\r\nvn -0 0.47 0.88\r\nvn 0 -0.47 -0.88\r\nvn -0.2 0.06 -0.98\r\nvn -0.19 0.06 -0.98\r\nvn 0.01 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0.01 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn -0.01 -0.47 -0.88\r\nvn -0.23 0.05 -0.97\r\nvn -0.21 0.06 -0.98\r\nvn 0.5 0.64 0.58\r\nvn 0.13 0.52 0.84\r\nvn 0.14 0.52 0.84\r\nvn -0.02 -0.48 -0.88\r\nvn -0.26 0.05 -0.96\r\nvn -0.24 0.05 -0.97\r\nvn -0.01 -0.47 -0.88\r\nvn 0.47 -0.09 -0.88\r\nvn -0.03 0.38 -0.93\r\nvn -0.22 0.11 -0.97\r\nvn 0.09 -0.42 -0.9\r\nvn -0.74 -0.36 0.57\r\nvn -0.76 -0.46 0.46\r\nvn -0.74 -0.36 0.57\r\nvn -0.76 -0.46 0.45\r\nvn 0.73 0.33 -0.6\r\nvn 0.25 0.58 -0.78\r\nvn 0.2 0.57 -0.8\r\nvn 0.71 0.28 -0.64\r\nvn -0.71 -0.22 0.67\r\nvn -0.72 -0.28 0.63\r\nvn -0.71 -0.22 0.67\r\nvn -0.72 -0.28 0.63\r\nvn 0.71 0.23 -0.67\r\nvn 0.23 0.47 -0.85\r\nvn 0.24 0.52 -0.82\r\nvn 0.72 0.26 -0.64\r\nvn -0.69 -0.17 0.7\r\nvn -0.7 -0.19 0.69\r\nvn 0.69 0.17 -0.7\r\nvn 0.2 0.4 -0.89\r\nvn 0.21 0.43 -0.87\r\nvn 0.7 0.19 -0.69\r\nvn -0.68 -0.13 0.72\r\nvn -0.69 -0.14 0.71\r\nvn 0.19 0.34 -0.92\r\nvn 0.2 0.37 -0.91\r\nvn 0.69 0.14 -0.71\r\nvn -0.68 -0.11 0.73\r\nvn 0.67 0.1 -0.73\r\nvn 0.18 0.3 -0.94\r\nvn 0.18 0.32 -0.93\r\nvn -0.67 -0.08 0.74\r\nvn -0.67 -0.07 0.74\r\nvn 0.17 0.25 -0.95\r\nvn 0.17 0.27 -0.95\r\nvn -0.66 -0.06 0.74\r\nvn 0.16 0.22 -0.96\r\nvn 0.16 0.24 -0.96\r\nvn -0.66 -0.05 0.75\r\nvn -0.66 -0.04 0.75\r\nvn 0.15 0.19 -0.97\r\nvn 0.15 0.2 -0.97\r\nvn 0.15 0.16 -0.98\r\nvn 0.15 0.17 -0.97\r\nvn 0.66 0.04 -0.75\r\nvn 0.14 0.14 -0.98\r\nvn 0.14 0.15 -0.98\r\nvn 0.14 0.12 -0.98\r\nvn 0.14 0.13 -0.98\r\nvn 0.14 0.11 -0.98\r\nvn 0.14 0.12 -0.98\r\nvn 0.14 0.11 -0.98\r\nvn 0.14 0.11 -0.98\r\nvn 0.14 0.11 -0.98\r\nvn 0.14 0.11 -0.98\r\nvn 0.14 0.13 -0.98\r\nvn 0.14 0.12 -0.98\r\nvn 0.14 0.15 -0.98\r\nvn 0.14 0.14 -0.98\r\nvn 0.14 0.17 -0.98\r\nvn 0.14 0.16 -0.98\r\nvn 0.14 0.19 -0.97\r\nvn 0.14 0.18 -0.97\r\nvn -0.66 -0.13 0.74\r\nvn 0.15 0.2 -0.97\r\nvn 0.14 0.19 -0.97\r\nvn 0.66 0.13 -0.74\r\nvn 0.15 0.22 -0.97\r\nvn 0.15 0.21 -0.97\r\nvn -0.66 -0.16 0.73\r\nvn -0.66 -0.15 0.73\r\nvn 0.66 0.16 -0.73\r\nvn 0.15 0.24 -0.96\r\nvn 0.15 0.22 -0.96\r\nvn 0.66 0.15 -0.73\r\nvn -0.67 -0.19 0.72\r\nvn -0.66 -0.17 0.73\r\nvn -0.67 -0.19 0.72\r\nvn -0.66 -0.17 0.73\r\nvn 0.67 0.19 -0.72\r\nvn 0.15 0.28 -0.95\r\nvn 0.15 0.26 -0.95\r\nvn 0.66 0.17 -0.73\r\nvn -0.67 -0.23 0.71\r\nvn -0.67 -0.21 0.72\r\nvn -0.67 -0.23 0.71\r\nvn -0.67 -0.21 0.72\r\nvn 0.67 0.23 -0.71\r\nvn 0.16 0.36 -0.92\r\nvn 0.15 0.32 -0.94\r\nvn 0.67 0.21 -0.72\r\nvn -0.67 -0.28 0.68\r\nvn -0.67 -0.26 0.69\r\nvn 0.67 0.28 -0.68\r\nvn 0.16 0.44 -0.88\r\nvn 0.16 0.4 -0.9\r\nvn 0.67 0.26 -0.7\r\nvn -0.68 -0.32 0.66\r\nvn -0.67 -0.3 0.67\r\nvn 0.17 0.51 -0.85\r\nvn 0.17 0.47 -0.87\r\nvn 0.67 0.3 -0.67\r\nvn -0.68 -0.36 0.63\r\nvn -0.68 -0.36 0.63\r\nvn -0.68 -0.34 0.65\r\nvn 0.68 0.36 -0.63\r\nvn 0.18 0.57 -0.8\r\nvn 0.18 0.54 -0.82\r\nvn 0.68 0.34 -0.65\r\nvn -0.69 -0.39 0.61\r\nvn -0.69 -0.38 0.62\r\nvn -0.69 -0.39 0.61\r\nvn -0.69 -0.38 0.62\r\nvn 0.69 0.39 -0.61\r\nvn 0.19 0.61 -0.77\r\nvn 0.18 0.59 -0.78\r\nvn 0.69 0.38 -0.62\r\nvn -0.69 -0.42 0.59\r\nvn -0.69 -0.41 0.6\r\nvn -0.69 -0.41 0.6\r\nvn 0.69 0.42 -0.59\r\nvn 0.19 0.65 -0.74\r\nvn 0.19 0.63 -0.75\r\nvn 0.69 0.41 -0.6\r\nvn 0.2 0.67 -0.72\r\nvn 0.19 0.66 -0.73\r\nvn 0.2 0.69 -0.69\r\nvn 0.2 0.68 -0.71\r\nvn 0.7 0.47 -0.53\r\nvn 0.21 0.72 -0.67\r\nvn 0.2 0.7 -0.68\r\nvn -0.71 -0.49 0.51\r\nvn 0.71 0.49 -0.51\r\nvn 0.21 0.74 -0.63\r\nvn 0.21 0.73 -0.65\r\nvn -0.55 -0.79 -0.29\r\nvn -0.72 -0.62 0.32\r\nvn -0.72 -0.62 0.32\r\nvn 0.35 -0.16 -0.92\r\nvn -0.05 0.19 -0.98\r\nvn 0.22 0.62 -0.75\r\nvn 0.67 0.35 -0.66\r\nvn -0.43 -0.25 -0.87\r\nvn 0.73 0.22 0.65\r\nvn 0.76 0.33 0.56\r\nvn 0.74 0.26 0.62\r\nvn 0.68 0.74 0.01\r\nvn 0.69 0.72 0.05\r\nvn 0.72 0.69 0.13\r\nvn 0.67 0.75 -0.02\r\nvn 0.66 0.75 -0.04\r\nvn 0.7 0.71 -0.02\r\nvn 0.67 0.74 -0\r\nvn 0.73 0.67 0.14\r\nvn -0.02 -0.48 -0.88\r\nvn -0.01 -0.47 -0.88\r\nvn 0.47 -0.09 -0.88\r\nvn 0.09 -0.42 -0.9\r\nvn 0.73 0.33 -0.6\r\nvn 0.71 0.28 -0.64\r\nvn 0.71 0.23 -0.67\r\nvn 0.72 0.26 -0.64\r\nvn 0.66 0.13 -0.74\r\nvn 0.66 0.15 -0.73\r\nvn 0.67 0.19 -0.72\r\nvn 0.66 0.17 -0.73\r\nvn 0.67 0.23 -0.71\r\nvn 0.67 0.21 -0.72\r\nvn 0.67 0.26 -0.7\r\nvn 0.68 0.36 -0.63\r\nvn 0.68 0.34 -0.65\r\nvn 0.69 0.38 -0.62\r\nvn 0.71 0.48 -0.52\r\nvn 0.35 -0.16 -0.92\r\nvn 0.67 0.35 -0.66\r\nvn -0.11 -0.25 -0.96\r\nvn -0.17 -0.35 -0.92\r\nvn -0.11 -0.24 -0.96\r\nvn -0.11 -0.25 -0.96\r\nvn -0.11 -0.23 -0.97\r\nvn -0.11 -0.24 -0.97\r\nvn -0.11 -0.23 -0.97\r\nvn -0.11 -0.23 -0.97\r\nvn -0.11 -0.22 -0.97\r\nvn -0.11 -0.22 -0.97\r\nvn -0.1 -0.22 -0.97\r\nvn -0.11 -0.22 -0.97\r\nvn -0.1 -0.2 -0.97\r\nvn -0.1 -0.21 -0.97\r\nvn -0.1 -0.18 -0.98\r\nvn -0.1 -0.2 -0.98\r\nvn -0.09 -0.15 -0.98\r\nvn -0.09 -0.17 -0.98\r\nvn -0.08 -0.12 -0.99\r\nvn -0.08 -0.13 -0.99\r\nvn -0.07 -0.09 -0.99\r\nvn -0.08 -0.1 -0.99\r\nvn -0.07 -0.06 -1\r\nvn -0.07 -0.07 -0.99\r\nvn -0.07 -0.05 -1\r\nvn -0.07 -0.05 -1\r\nvn -0.07 -0.04 -1\r\nvn -0.07 -0.04 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.04 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.03 -1\r\nvn -0.07 -0.04 -1\r\nvn -0.07 -0.04 -1\r\nvn -0.07 -0.05 -1\r\nvn -0.07 -0.05 -1\r\nvn -0.08 -0.07 -0.99\r\nvn -0.07 -0.06 -1\r\nvn -0.08 -0.09 -0.99\r\nvn -0.08 -0.08 -0.99\r\nvn -0.08 -0.12 -0.99\r\nvn -0.08 -0.11 -0.99\r\nvn -0.09 -0.15 -0.98\r\nvn -0.08 -0.14 -0.99\r\nvn -0.09 -0.19 -0.98\r\nvn -0.09 -0.17 -0.98\r\nvn -0.1 -0.23 -0.97\r\nvn -0.1 -0.21 -0.97\r\nvn -0.11 -0.27 -0.96\r\nvn -0.1 -0.25 -0.96\r\nvn -0.12 -0.32 -0.94\r\nvn -0.11 -0.29 -0.95\r\nvn -0.13 -0.37 -0.92\r\nvn -0.12 -0.34 -0.93\r\nvn -0.14 -0.42 -0.89\r\nvn -0.14 -0.39 -0.91\r\nvn -0.16 -0.49 -0.86\r\nvn -0.15 -0.46 -0.88\r\nvn -0.17 -0.6 -0.78\r\nvn -0.17 -0.54 -0.82\r\nvn -0.17 -0.78 -0.61\r\nvn -0.18 -0.69 -0.71\r\nvn -0.14 -0.9 -0.4\r\nvn -0.16 -0.85 -0.5\r\nvn -0.11 -0.95 -0.29\r\nvn -0.12 -0.93 -0.34\r\nvn -0.09 -0.97 -0.23\r\nvn -0.1 -0.96 -0.26\r\nvn -0.08 -0.98 -0.19\r\nvn -0.08 -0.97 -0.21\r\nvn -0.07 -0.98 -0.16\r\nvn -0.07 -0.98 -0.18\r\nvn -0.06 -0.99 -0.14\r\nvn -0.07 -0.99 -0.15\r\nvn -0.06 -0.99 -0.12\r\nvn -0.06 -0.99 -0.13\r\nvn -0.05 -0.99 -0.1\r\nvn -0.06 -0.99 -0.11\r\nvn -0.05 -0.99 -0.09\r\nvn -0.05 -0.99 -0.09\r\nvn -0.05 -1 -0.07\r\nvn -0.05 -1 -0.08\r\nvn -0.05 -1 -0.06\r\nvn -0.05 -1 -0.06\r\nvn -0.04 -1 -0.05\r\nvn -0.05 -1 -0.05\r\nvn -0.04 -1 -0.04\r\nvn -0.04 -1 -0.04\r\nvn -0.04 -1 -0.02\r\nvn -0.04 -1 -0.03\r\nvn -0.04 -1 -0.01\r\nvn -0.04 -1 -0.02\r\nvn -0.04 -1 0\r\nvn -0.04 -1 -0\r\nvn -0.04 -1 0.02\r\nvn -0.04 -1 0.01\r\nvn -0.04 -1 0.03\r\nvn -0.04 -1 0.02\r\nvn -0.04 -1 0.04\r\nvn -0.04 -1 0.03\r\nvn -0.04 -1 0.05\r\nvn -0.04 -1 0.04\r\nvn -0.04 -1 0.06\r\nvn -0.04 -1 0.06\r\nvn -0.05 -1 0.08\r\nvn -0.05 -1 0.07\r\nvn -0.05 -0.99 0.09\r\nvn -0.05 -1 0.09\r\nvn -0.05 -0.99 0.11\r\nvn -0.05 -0.99 0.1\r\nvn -0.06 -0.99 0.13\r\nvn -0.05 -0.99 0.12\r\nvn -0.06 -0.99 0.16\r\nvn -0.06 -0.99 0.14\r\nvn -0.07 -0.98 0.18\r\nvn -0.07 -0.98 0.17\r\nvn -0.08 -0.98 0.21\r\nvn -0.07 -0.98 0.19\r\nvn -0.09 -0.97 0.24\r\nvn -0.08 -0.97 0.22\r\nvn -0.12 -0.95 0.27\r\nvn -0.1 -0.96 0.26\r\nvn -0.18 -0.85 0.49\r\nvn -0.19 -0.93 0.31\r\nvn 0.04 -0.19 0.98\r\nvn 0.02 -0.19 0.98\r\nvn 0.03 -0.82 0.57\r\nvn -0 -0.82 0.57\r\nvn 0 -0.19 0.98\r\nvn 0.07 -0.18 0.98\r\nvn 0.05 -0.18 0.98\r\nvn 0.1 -0.18 0.98\r\nvn 0.08 -0.18 0.98\r\nvn 0.13 -0.17 0.98\r\nvn 0.11 -0.18 0.98\r\nvn 0.15 -0.17 0.97\r\nvn 0.14 -0.17 0.97\r\nvn 0.18 -0.16 0.97\r\nvn 0.17 -0.17 0.97\r\nvn 0.2 -0.16 0.97\r\nvn 0.19 -0.16 0.97\r\nvn 0.23 -0.15 0.96\r\nvn 0.21 -0.15 0.96\r\nvn 0.27 -0.13 0.95\r\nvn 0.24 -0.14 0.96\r\nvn 0.38 -0.15 0.92\r\nvn -0.15 -0.63 0.76\r\nvn -0.16 -0.72 0.67\r\nvn -0.13 -0.5 0.86\r\nvn -0.14 -0.56 0.82\r\nvn -0.11 -0.43 0.9\r\nvn -0.12 -0.46 0.88\r\nvn -0.1 -0.37 0.92\r\nvn -0.1 -0.4 0.91\r\nvn -0.08 -0.32 0.94\r\nvn -0.09 -0.34 0.93\r\nvn -0.07 -0.28 0.96\r\nvn -0.08 -0.3 0.95\r\nvn -0.07 -0.24 0.97\r\nvn -0.07 -0.26 0.96\r\nvn -0.06 -0.21 0.98\r\nvn -0.06 -0.22 0.97\r\nvn -0.05 -0.18 0.98\r\nvn -0.06 -0.19 0.98\r\nvn -0.05 -0.15 0.99\r\nvn -0.05 -0.16 0.99\r\nvn -0.05 -0.13 0.99\r\nvn -0.05 -0.14 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.13 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.13 0.99\r\nvn -0.05 -0.12 0.99\r\nvn -0.05 -0.15 0.99\r\nvn -0.05 -0.14 0.99\r\nvn -0.05 -0.17 0.98\r\nvn -0.05 -0.16 0.99\r\nvn -0.05 -0.19 0.98\r\nvn -0.05 -0.18 0.98\r\nvn -0.05 -0.21 0.98\r\nvn -0.05 -0.2 0.98\r\nvn -0.05 -0.22 0.97\r\nvn -0.05 -0.21 0.98\r\nvn -0.05 -0.24 0.97\r\nvn -0.05 -0.23 0.97\r\nvn -0.05 -0.29 0.95\r\nvn -0.05 -0.26 0.96\r\nvn -0.05 -0.37 0.93\r\nvn -0.05 -0.33 0.94\r\nvn -0.06 -0.45 0.89\r\nvn -0.06 -0.42 0.91\r\nvn -0.07 -0.52 0.85\r\nvn -0.06 -0.49 0.87\r\nvn -0.08 -0.59 0.81\r\nvn -0.07 -0.56 0.83\r\nvn -0.09 -0.63 0.77\r\nvn -0.08 -0.61 0.79\r\nvn -0.09 -0.66 0.74\r\nvn -0.09 -0.65 0.75\r\nvn -0.1 -0.69 0.72\r\nvn -0.1 -0.68 0.73\r\nvn -0.1 -0.71 0.69\r\nvn -0.1 -0.7 0.71\r\nvn -0.11 -0.74 0.67\r\nvn -0.11 -0.72 0.68\r\nvn -0.11 -0.76 0.64\r\nvn -0.11 -0.75 0.65\r\nvn -0.17 -0.86 0.48\r\nvn 0.27 0.84 0.47\r\nvn 0.29 0.76 0.58\r\nvn 0.77 0.53 0.36\r\nvn 0.74 0.63 0.23\r\nvn 0.29 0.67 0.69\r\nvn 0.77 0.42 0.48\r\nvn 0.76 0.52 0.38\r\nvn 0.74 0.62 0.24\r\nvn 0.75 0.54 0.37\r\nvn 0.74 0.63 0.23\r\nvn 0.76 0.41 0.5\r\nvn 0.76 0.44 0.48\r\nvn 1 -0 0.05\r\nvn 1 0.09 -0.02\r\nvn 0.98 0.15 -0.12\r\nvn -0.03 0.88 -0.47\r\nvn -0 0.88 -0.47\r\nvn -0.03 0.97 -0.25\r\nvn 0 0.97 -0.25\r\nvn -0.02 0.88 0.48\r\nvn 0 0.88 0.48\r\nvn 0.03 -0.88 0.47\r\nvn -0 -0.88 0.47\r\nvn 0.03 -0.93 0.36\r\nvn -0 -0.93 0.36\r\nvn 0.02 -0.92 -0.4\r\nvn -0 -0.92 -0.4\r\nvn -0.6 -0.43 -0.67\r\nvn -0.64 -0.48 -0.6\r\nvn -0.68 -0.37 -0.64\r\nvn -0.58 -0.39 -0.72\r\nvn -0.71 -0.42 -0.56\r\nvn -0.75 -0.38 -0.53\r\nvn -0.76 -0.35 -0.55\r\nvn -0.68 -0.36 -0.64\r\nvn -0.59 -0.36 -0.72\r\nvn -0.67 -0.71 0.21\r\nvn -0.93 -0.3 0.22\r\nvn -0.34 -0.62 0.71\r\nvn -0.65 -0.11 0.75\r\nvn -0.69 0.03 0.72\r\nvn -0.97 -0.2 0.16\r\nvn 0.59 0.22 0.78\r\nvn 0.72 0.12 0.69\r\nvn 0.68 0.34 0.65\r\nvn 0.5 0.3 0.81\r\nvn 0.81 0.21 0.55\r\nvn 0.84 0.28 0.46\r\nvn 0.82 0.34 0.46\r\nvn 0.68 0.36 0.64\r\nvn 0.5 0.36 0.79\r\nvn 0.93 -0.37 0.06\r\nvn 0.97 0.05 -0.24\r\nvn 0.67 -0.66 -0.34\r\nvn 0.72 -0.17 -0.67\r\nvn 0.69 -0.03 -0.72\r\nvn 0.95 0.17 -0.25\r\nvn -0.59 -0.36 -0.72\r\nvn -0.76 -0.36 -0.55\r\nvn -0.76 -0.35 -0.55\r\nvn -0.68 -0.36 -0.64\r\nvn -0.59 -0.36 -0.72\r\nvn -0.97 -0.21 0.16\r\nvn -0.69 0.02 0.72\r\nvn -0.69 0.02 0.72\r\nvn -0.96 -0.21 0.16\r\nvn 0.5 0.36 0.79\r\nvn 0.82 0.35 0.46\r\nvn 0.82 0.34 0.46\r\nvn 0.68 0.36 0.64\r\nvn 0.5 0.35 0.79\r\nvn 0.95 0.18 -0.25\r\nvn 0.69 -0.02 -0.72\r\nvn 0.69 -0.02 -0.72\r\nvn 0.95 0.18 -0.25\r\nvn -0.68 -0.36 -0.64\r\nvn -0.59 -0.36 -0.72\r\nvn -0.76 -0.35 -0.55\r\nvn -0.76 -0.35 -0.55\r\nvn -0.68 -0.35 -0.64\r\nvn -0.59 -0.35 -0.72\r\nvn -0.96 -0.21 0.16\r\nvn -0.69 0.02 0.72\r\nvn -0.69 0.01 0.72\r\nvn -0.96 -0.21 0.16\r\nvn 0.68 0.36 0.64\r\nvn 0.51 0.35 0.79\r\nvn 0.82 0.34 0.46\r\nvn 0.82 0.34 0.46\r\nvn 0.68 0.35 0.64\r\nvn 0.51 0.35 0.79\r\nvn 0.95 0.18 -0.25\r\nvn 0.69 -0.02 -0.72\r\nvn 0.69 -0.01 -0.72\r\nvn 0.95 0.18 -0.25\r\nvn -0.68 -0.35 -0.64\r\nvn -0.59 -0.35 -0.72\r\nvn -0.76 -0.35 -0.55\r\nvn -0.76 -0.35 -0.55\r\nvn -0.68 -0.35 -0.64\r\nvn -0.6 -0.35 -0.72\r\nvn -0.96 -0.21 0.16\r\nvn -0.69 0.01 0.72\r\nvn -0.69 0.01 0.73\r\nvn -0.96 -0.21 0.16\r\nvn 0.68 0.35 0.64\r\nvn 0.51 0.34 0.79\r\nvn 0.82 0.34 0.46\r\nvn 0.82 0.34 0.46\r\nvn 0.68 0.35 0.64\r\nvn 0.51 0.34 0.79\r\nvn 0.95 0.18 -0.25\r\nvn 0.69 -0.01 -0.72\r\nvn 0.69 -0.01 -0.73\r\nvn 0.95 0.18 -0.25\r\nvn -0.68 -0.35 -0.64\r\nvn -0.6 -0.35 -0.72\r\nvn -0.76 -0.34 -0.55\r\nvn -0.76 -0.34 -0.55\r\nvn -0.68 -0.35 -0.64\r\nvn -0.6 -0.34 -0.73\r\nvn -0.96 -0.21 0.16\r\nvn -0.69 0.01 0.73\r\nvn -0.69 0.01 0.73\r\nvn -0.96 -0.21 0.16\r\nvn 0.68 0.35 0.64\r\nvn 0.51 0.34 0.79\r\nvn 0.82 0.34 0.46\r\nvn 0.82 0.33 0.46\r\nvn 0.68 0.35 0.64\r\nvn 0.51 0.34 0.79\r\nvn 0.95 0.18 -0.25\r\nvn 0.69 -0.01 -0.73\r\nvn 0.69 -0.01 -0.73\r\nvn 0.95 0.18 -0.25\r\nvn -0.68 -0.34 -0.64\r\nvn -0.6 -0.34 -0.73\r\nvn -0.76 -0.34 -0.55\r\nvn -0.76 -0.34 -0.55\r\nvn -0.68 -0.34 -0.65\r\nvn -0.6 -0.34 -0.73\r\nvn -0.96 -0.21 0.16\r\nvn -0.69 0 0.73\r\nvn -0.69 0 0.73\r\nvn -0.96 -0.21 0.16\r\nvn 0.68 0.34 0.64\r\nvn 0.51 0.33 0.79\r\nvn 0.82 0.33 0.46\r\nvn 0.82 0.33 0.46\r\nvn 0.68 0.34 0.65\r\nvn 0.51 0.33 0.79\r\nvn 0.95 0.18 -0.25\r\nvn 0.69 -0 -0.73\r\nvn 0.69 -0 -0.73\r\nvn 0.95 0.18 -0.26\r\nvn -0.69 -0.33 -0.65\r\nvn -0.6 -0.33 -0.73\r\nvn -0.76 -0.33 -0.56\r\nvn -0.76 -0.33 -0.56\r\nvn -0.69 -0.33 -0.65\r\nvn -0.6 -0.33 -0.73\r\nvn -0.96 -0.21 0.16\r\nvn -0.69 0 0.73\r\nvn -0.69 -0 0.73\r\nvn -0.97 -0.21 0.16\r\nvn 0.69 0.34 0.65\r\nvn 0.51 0.33 0.79\r\nvn 0.82 0.33 0.46\r\nvn 0.83 0.32 0.47\r\nvn 0.69 0.33 0.65\r\nvn 0.51 0.32 0.8\r\nvn 0.95 0.18 -0.26\r\nvn 0.69 -0 -0.73\r\nvn 0.69 0 -0.73\r\nvn 0.95 0.18 -0.26\r\nvn -0.69 -0.32 -0.65\r\nvn -0.6 -0.32 -0.73\r\nvn -0.77 -0.32 -0.56\r\nvn -0.77 -0.3 -0.56\r\nvn -0.69 -0.31 -0.65\r\nvn -0.61 -0.3 -0.74\r\nvn -0.97 -0.2 0.16\r\nvn -0.68 -0.01 0.73\r\nvn -0.68 -0.01 0.73\r\nvn -0.97 -0.2 0.15\r\nvn 0.69 0.32 0.65\r\nvn 0.52 0.31 0.8\r\nvn 0.83 0.31 0.47\r\nvn 0.83 0.3 0.47\r\nvn 0.69 0.31 0.65\r\nvn 0.52 0.3 0.8\r\nvn 0.95 0.18 -0.26\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0.01 -0.73\r\nvn 0.95 0.17 -0.26\r\nvn -0.7 -0.29 -0.66\r\nvn -0.61 -0.28 -0.74\r\nvn -0.77 -0.29 -0.57\r\nvn -0.78 -0.27 -0.57\r\nvn -0.7 -0.27 -0.66\r\nvn -0.62 -0.26 -0.74\r\nvn -0.97 -0.19 0.15\r\nvn -0.68 -0.01 0.73\r\nvn -0.68 -0.02 0.73\r\nvn -0.97 -0.18 0.15\r\nvn 0.7 0.29 0.66\r\nvn 0.52 0.28 0.81\r\nvn 0.83 0.28 0.47\r\nvn 0.84 0.26 0.48\r\nvn 0.7 0.27 0.66\r\nvn 0.53 0.25 0.81\r\nvn 0.95 0.17 -0.25\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0.02 -0.73\r\nvn 0.95 0.16 -0.25\r\nvn -0.71 -0.24 -0.66\r\nvn -0.62 -0.24 -0.75\r\nvn -0.78 -0.24 -0.57\r\nvn -0.79 -0.22 -0.58\r\nvn -0.71 -0.22 -0.67\r\nvn -0.63 -0.22 -0.75\r\nvn -0.97 -0.17 0.15\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.98 -0.16 0.15\r\nvn 0.71 0.24 0.66\r\nvn 0.53 0.23 0.81\r\nvn 0.84 0.24 0.48\r\nvn 0.85 0.22 0.48\r\nvn 0.71 0.22 0.67\r\nvn 0.54 0.21 0.82\r\nvn 0.96 0.15 -0.25\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.96 0.15 -0.25\r\nvn -0.71 -0.2 -0.67\r\nvn -0.63 -0.2 -0.75\r\nvn -0.79 -0.2 -0.58\r\nvn -0.79 -0.18 -0.58\r\nvn -0.72 -0.18 -0.67\r\nvn -0.63 -0.17 -0.75\r\nvn -0.98 -0.15 0.15\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.98 -0.14 0.14\r\nvn 0.71 0.2 0.67\r\nvn 0.54 0.19 0.82\r\nvn 0.85 0.2 0.49\r\nvn 0.85 0.18 0.49\r\nvn 0.72 0.18 0.67\r\nvn 0.54 0.17 0.82\r\nvn 0.96 0.14 -0.25\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.96 0.12 -0.25\r\nvn -0.72 -0.16 -0.67\r\nvn -0.64 -0.15 -0.76\r\nvn -0.8 -0.16 -0.58\r\nvn -0.8 -0.14 -0.58\r\nvn -0.72 -0.14 -0.68\r\nvn -0.64 -0.13 -0.76\r\nvn -0.98 -0.12 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.98 -0.11 0.14\r\nvn 0.72 0.16 0.67\r\nvn 0.55 0.15 0.82\r\nvn 0.86 0.16 0.49\r\nvn 0.86 0.14 0.49\r\nvn 0.72 0.14 0.68\r\nvn 0.55 0.13 0.83\r\nvn 0.96 0.11 -0.25\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.96 0.1 -0.25\r\nvn -0.73 -0.12 -0.68\r\nvn -0.64 -0.12 -0.76\r\nvn -0.8 -0.13 -0.59\r\nvn -0.8 -0.12 -0.59\r\nvn -0.73 -0.11 -0.68\r\nvn -0.64 -0.11 -0.76\r\nvn -0.98 -0.1 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.98 -0.1 0.14\r\nvn 0.73 0.12 0.68\r\nvn 0.55 0.11 0.83\r\nvn 0.86 0.13 0.49\r\nvn 0.86 0.12 0.49\r\nvn 0.73 0.11 0.68\r\nvn 0.55 0.1 0.83\r\nvn 0.97 0.09 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.09 -0.24\r\nvn -0.73 -0.1 -0.68\r\nvn -0.64 -0.1 -0.76\r\nvn -0.8 -0.11 -0.59\r\nvn -0.8 -0.1 -0.59\r\nvn -0.73 -0.1 -0.68\r\nvn -0.64 -0.1 -0.76\r\nvn -0.99 -0.09 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.99 -0.09 0.14\r\nvn 0.73 0.1 0.68\r\nvn 0.55 0.09 0.83\r\nvn 0.86 0.11 0.49\r\nvn 0.86 0.1 0.49\r\nvn 0.73 0.1 0.68\r\nvn 0.55 0.09 0.83\r\nvn 0.97 0.08 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.08 -0.24\r\nvn -0.73 -0.1 -0.68\r\nvn -0.64 -0.09 -0.76\r\nvn -0.8 -0.1 -0.59\r\nvn -0.8 -0.1 -0.59\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.09 -0.76\r\nvn -0.99 -0.08 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.99 -0.08 0.14\r\nvn 0.73 0.1 0.68\r\nvn 0.55 0.09 0.83\r\nvn 0.86 0.1 0.49\r\nvn 0.86 0.1 0.49\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.97 0.08 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.08 -0.24\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.09 -0.76\r\nvn -0.8 -0.09 -0.59\r\nvn -0.8 -0.09 -0.59\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.99 -0.08 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.99 -0.08 0.14\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.86 0.09 0.49\r\nvn 0.86 0.09 0.49\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.97 0.07 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.07 -0.24\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.8 -0.09 -0.59\r\nvn -0.8 -0.09 -0.59\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.99 -0.08 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.99 -0.07 0.14\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.86 0.09 0.49\r\nvn 0.86 0.09 0.49\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.97 0.07 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.07 -0.24\r\nvn -0.73 -0.08 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.8 -0.09 -0.59\r\nvn -0.81 -0.08 -0.59\r\nvn -0.73 -0.08 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.99 -0.07 0.14\r\nvn -0.68 -0.03 0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.99 -0.07 0.14\r\nvn 0.73 0.08 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.86 0.09 0.49\r\nvn 0.87 0.09 0.49\r\nvn 0.73 0.08 0.68\r\nvn 0.56 0.07 0.83\r\nvn 0.97 0.07 -0.24\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.97 0.07 -0.24\r\nvn -0.73 -0.08 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.81 -0.08 -0.59\r\nvn -0.81 -0.08 -0.59\r\nvn -0.64 -0.08 -0.76\r\nvn -0.99 -0.07 0.14\r\nvn -0.68 -0.02 0.73\r\nvn -0.68 -0.02 0.73\r\nvn -0.99 -0.07 0.14\r\nvn 0.73 0.08 0.68\r\nvn 0.56 0.07 0.83\r\nvn 0.87 0.08 0.49\r\nvn 0.87 0.08 0.49\r\nvn 0.56 0.07 0.83\r\nvn 0.97 0.06 -0.24\r\nvn 0.68 0.02 -0.73\r\nvn 0.68 0.02 -0.73\r\nvn 0.97 0.06 -0.24\r\nvn -0.73 -0.08 -0.68\r\nvn -0.64 -0.08 -0.76\r\nvn -0.8 -0.09 -0.59\r\nvn -0.8 -0.09 -0.59\r\nvn -0.73 -0.09 -0.68\r\nvn -0.64 -0.09 -0.76\r\nvn -0.99 -0.07 0.15\r\nvn -0.68 -0.02 0.73\r\nvn -0.68 -0.02 0.73\r\nvn -0.99 -0.07 0.15\r\nvn 0.73 0.08 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.86 0.09 0.49\r\nvn 0.86 0.09 0.49\r\nvn 0.73 0.09 0.68\r\nvn 0.56 0.08 0.83\r\nvn 0.97 0.06 -0.24\r\nvn 0.68 0.02 -0.73\r\nvn 0.68 0.02 -0.73\r\nvn 0.97 0.07 -0.24\r\nvn -0.73 -0.1 -0.68\r\nvn -0.64 -0.09 -0.76\r\nvn -0.8 -0.1 -0.59\r\nvn -0.8 -0.11 -0.59\r\nvn -0.73 -0.1 -0.68\r\nvn -0.64 -0.1 -0.76\r\nvn -0.99 -0.07 0.15\r\nvn -0.68 -0.02 0.73\r\nvn -0.68 -0.02 0.73\r\nvn -0.99 -0.08 0.15\r\nvn 0.73 0.1 0.68\r\nvn 0.55 0.09 0.83\r\nvn 0.86 0.1 0.49\r\nvn 0.86 0.1 0.49\r\nvn 0.73 0.1 0.68\r\nvn 0.55 0.1 0.83\r\nvn 0.97 0.07 -0.24\r\nvn 0.68 0.02 -0.73\r\nvn 0.68 0.02 -0.73\r\nvn 0.97 0.07 -0.24\r\nvn -0.73 -0.11 -0.68\r\nvn -0.64 -0.11 -0.76\r\nvn -0.8 -0.12 -0.59\r\nvn -0.8 -0.13 -0.58\r\nvn -0.73 -0.13 -0.68\r\nvn -0.64 -0.12 -0.76\r\nvn -0.99 -0.08 0.15\r\nvn -0.68 -0.01 0.73\r\nvn -0.68 -0.01 0.73\r\nvn -0.98 -0.09 0.15\r\nvn 0.73 0.11 0.68\r\nvn 0.55 0.11 0.83\r\nvn 0.86 0.11 0.49\r\nvn 0.86 0.13 0.49\r\nvn 0.73 0.13 0.68\r\nvn 0.55 0.12 0.83\r\nvn 0.97 0.07 -0.24\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0.01 -0.73\r\nvn 0.97 0.08 -0.24\r\nvn -0.72 -0.14 -0.68\r\nvn -0.64 -0.14 -0.76\r\nvn -0.8 -0.14 -0.58\r\nvn -0.8 -0.15 -0.58\r\nvn -0.72 -0.16 -0.67\r\nvn -0.64 -0.15 -0.76\r\nvn -0.98 -0.09 0.15\r\nvn -0.68 -0.01 0.73\r\nvn -0.68 -0 0.73\r\nvn -0.98 -0.1 0.15\r\nvn 0.72 0.14 0.68\r\nvn 0.55 0.14 0.82\r\nvn 0.86 0.14 0.49\r\nvn 0.86 0.15 0.49\r\nvn 0.72 0.16 0.67\r\nvn 0.55 0.15 0.82\r\nvn 0.97 0.08 -0.24\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0 -0.73\r\nvn 0.97 0.09 -0.24\r\nvn -0.72 -0.17 -0.67\r\nvn -0.63 -0.17 -0.75\r\nvn -0.8 -0.17 -0.58\r\nvn -0.79 -0.18 -0.58\r\nvn -0.72 -0.19 -0.67\r\nvn -0.63 -0.19 -0.75\r\nvn -0.98 -0.11 0.15\r\nvn -0.68 -0 0.73\r\nvn -0.68 0 0.73\r\nvn -0.98 -0.11 0.15\r\nvn 0.72 0.17 0.67\r\nvn 0.55 0.17 0.82\r\nvn 0.86 0.17 0.49\r\nvn 0.85 0.18 0.49\r\nvn 0.72 0.19 0.67\r\nvn 0.54 0.18 0.82\r\nvn 0.97 0.09 -0.24\r\nvn 0.68 0 -0.73\r\nvn 0.68 -0 -0.73\r\nvn 0.97 0.1 -0.24\r\nvn -0.72 -0.2 -0.67\r\nvn -0.63 -0.2 -0.75\r\nvn -0.79 -0.2 -0.58\r\nvn -0.79 -0.22 -0.57\r\nvn -0.71 -0.22 -0.66\r\nvn -0.63 -0.22 -0.75\r\nvn -0.98 -0.12 0.15\r\nvn -0.68 0.01 0.73\r\nvn -0.68 0.02 0.73\r\nvn -0.98 -0.12 0.15\r\nvn 0.72 0.2 0.67\r\nvn 0.54 0.2 0.82\r\nvn 0.85 0.2 0.48\r\nvn 0.85 0.21 0.48\r\nvn 0.71 0.22 0.66\r\nvn 0.54 0.22 0.81\r\nvn 0.96 0.1 -0.24\r\nvn 0.68 -0.01 -0.73\r\nvn 0.68 -0.02 -0.73\r\nvn 0.96 0.11 -0.24\r\nvn -0.71 -0.24 -0.66\r\nvn -0.62 -0.24 -0.74\r\nvn -0.79 -0.24 -0.57\r\nvn -0.78 -0.25 -0.57\r\nvn -0.71 -0.26 -0.66\r\nvn -0.62 -0.26 -0.74\r\nvn -0.98 -0.13 0.16\r\nvn -0.69 0.03 0.73\r\nvn -0.69 0.04 0.73\r\nvn -0.98 -0.13 0.16\r\nvn 0.71 0.24 0.66\r\nvn 0.54 0.24 0.81\r\nvn 0.85 0.23 0.48\r\nvn 0.84 0.25 0.48\r\nvn 0.71 0.26 0.66\r\nvn 0.53 0.26 0.81\r\nvn 0.96 0.11 -0.24\r\nvn 0.69 -0.03 -0.73\r\nvn 0.69 -0.04 -0.73\r\nvn 0.96 0.11 -0.24\r\nvn -0.7 -0.28 -0.65\r\nvn -0.62 -0.28 -0.73\r\nvn -0.78 -0.27 -0.56\r\nvn -0.78 -0.29 -0.56\r\nvn -0.7 -0.3 -0.65\r\nvn -0.61 -0.3 -0.73\r\nvn -0.98 -0.14 0.16\r\nvn -0.69 0.04 0.72\r\nvn -0.69 0.06 0.72\r\nvn -0.98 -0.14 0.16\r\nvn 0.7 0.28 0.65\r\nvn 0.53 0.28 0.8\r\nvn 0.84 0.26 0.47\r\nvn 0.84 0.28 0.47\r\nvn 0.7 0.3 0.65\r\nvn 0.52 0.3 0.8\r\nvn 0.96 0.12 -0.24\r\nvn 0.69 -0.04 -0.72\r\nvn 0.69 -0.06 -0.72\r\nvn 0.96 0.12 -0.24\r\nvn -0.7 -0.32 -0.64\r\nvn -0.61 -0.32 -0.72\r\nvn -0.77 -0.31 -0.55\r\nvn -0.77 -0.33 -0.55\r\nvn -0.69 -0.34 -0.64\r\nvn -0.61 -0.35 -0.72\r\nvn -0.98 -0.15 0.16\r\nvn -0.69 0.07 0.72\r\nvn -0.7 0.08 0.71\r\nvn -0.98 -0.15 0.16\r\nvn 0.7 0.32 0.64\r\nvn 0.52 0.33 0.79\r\nvn 0.84 0.3 0.46\r\nvn 0.83 0.31 0.46\r\nvn 0.69 0.34 0.64\r\nvn 0.52 0.35 0.78\r\nvn 0.96 0.12 -0.24\r\nvn 0.69 -0.07 -0.72\r\nvn 0.7 -0.08 -0.71\r\nvn 0.96 0.12 -0.24\r\nvn -0.69 -0.36 -0.63\r\nvn -0.6 -0.37 -0.71\r\nvn -0.77 -0.35 -0.54\r\nvn -0.76 -0.36 -0.54\r\nvn -0.68 -0.38 -0.62\r\nvn -0.6 -0.39 -0.7\r\nvn -0.97 -0.15 0.16\r\nvn -0.7 0.09 0.71\r\nvn -0.7 0.1 0.71\r\nvn -0.97 -0.16 0.16\r\nvn 0.69 0.36 0.63\r\nvn 0.51 0.37 0.78\r\nvn 0.83 0.33 0.45\r\nvn 0.82 0.35 0.45\r\nvn 0.68 0.38 0.62\r\nvn 0.51 0.39 0.77\r\nvn 0.96 0.12 -0.24\r\nvn 0.7 -0.09 -0.71\r\nvn 0.7 -0.1 -0.71\r\nvn 0.96 0.12 -0.24\r\nvn -0.68 -0.4 -0.62\r\nvn -0.59 -0.41 -0.69\r\nvn -0.76 -0.38 -0.53\r\nvn -0.75 -0.4 -0.52\r\nvn -0.67 -0.42 -0.61\r\nvn -0.59 -0.43 -0.68\r\nvn -0.97 -0.16 0.16\r\nvn -0.7 0.12 0.7\r\nvn -0.71 0.13 0.69\r\nvn -0.97 -0.16 0.17\r\nvn 0.68 0.4 0.62\r\nvn 0.5 0.42 0.76\r\nvn 0.82 0.36 0.44\r\nvn 0.82 0.38 0.43\r\nvn 0.67 0.42 0.61\r\nvn 0.49 0.44 0.75\r\nvn 0.96 0.12 -0.24\r\nvn 0.7 -0.12 -0.7\r\nvn 0.71 -0.13 -0.69\r\nvn 0.96 0.12 -0.24\r\nvn -0.67 -0.44 -0.6\r\nvn -0.58 -0.46 -0.67\r\nvn -0.75 -0.42 -0.51\r\nvn -0.74 -0.44 -0.5\r\nvn -0.66 -0.46 -0.59\r\nvn -0.57 -0.48 -0.66\r\nvn -0.97 -0.16 0.17\r\nvn -0.71 0.15 0.69\r\nvn -0.72 0.17 0.68\r\nvn -0.97 -0.16 0.17\r\nvn 0.67 0.44 0.6\r\nvn 0.49 0.47 0.74\r\nvn 0.81 0.4 0.43\r\nvn 0.81 0.41 0.42\r\nvn 0.66 0.46 0.59\r\nvn 0.48 0.49 0.73\r\nvn 0.96 0.12 -0.24\r\nvn 0.71 -0.15 -0.69\r\nvn 0.72 -0.17 -0.68\r\nvn 0.96 0.12 -0.24\r\nvn -0.66 -0.49 -0.57\r\nvn -0.57 -0.51 -0.65\r\nvn -0.74 -0.46 -0.49\r\nvn -0.74 -0.48 -0.48\r\nvn -0.65 -0.51 -0.56\r\nvn -0.56 -0.53 -0.63\r\nvn -0.98 -0.15 0.16\r\nvn -0.72 0.19 0.66\r\nvn -0.73 0.22 0.64\r\nvn -0.98 -0.14 0.16\r\nvn 0.66 0.49 0.57\r\nvn 0.48 0.52 0.71\r\nvn 0.8 0.43 0.41\r\nvn 0.8 0.45 0.4\r\nvn 0.65 0.51 0.56\r\nvn 0.47 0.55 0.69\r\nvn 0.97 0.11 -0.23\r\nvn 0.72 -0.19 -0.66\r\nvn 0.73 -0.22 -0.65\r\nvn 0.97 0.1 -0.23\r\nvn -0.65 -0.53 -0.54\r\nvn -0.56 -0.56 -0.61\r\nvn -0.74 -0.5 -0.46\r\nvn -0.72 -0.51 -0.47\r\nvn -0.64 -0.55 -0.53\r\nvn -0.55 -0.59 -0.59\r\nvn -0.98 -0.11 0.17\r\nvn -0.74 0.25 0.63\r\nvn -0.75 0.28 0.6\r\nvn -0.78 0.31 0.54\r\nvn -0.81 0.35 0.47\r\nvn -0.98 -0.05 0.19\r\nvn 0.65 0.53 0.55\r\nvn 0.46 0.58 0.67\r\nvn 0.8 0.46 0.39\r\nvn 0.79 0.46 0.39\r\nvn 0.64 0.55 0.53\r\nvn 0.45 0.61 0.65\r\nvn 0.97 0.08 -0.21\r\nvn 0.74 -0.26 -0.62\r\nvn 0.75 -0.33 -0.57\r\nvn 0.98 0.03 -0.18\r\nvn -0.64 -0.56 -0.52\r\nvn -0.55 -0.61 -0.57\r\nvn -0.72 -0.51 -0.46\r\nvn -0.74 -0.5 -0.46\r\nvn -0.64 -0.57 -0.51\r\nvn -0.55 -0.63 -0.55\r\nvn 0.64 0.56 0.52\r\nvn 0.45 0.64 0.62\r\nvn 0.79 0.46 0.4\r\nvn 0.79 0.44 0.42\r\nvn 0.64 0.57 0.51\r\nvn 0.46 0.67 0.58\r\nvn 0.99 -0.03 -0.11\r\nvn 0.76 -0.42 -0.49\r\nvn 0.76 -0.53 -0.36\r\nvn 0.99 -0.12 -0.01\r\nvn -0.64 -0.57 -0.5\r\nvn -0.55 -0.64 -0.53\r\nvn -0.73 -0.49 -0.47\r\nvn -0.73 -0.49 -0.47\r\nvn -0.65 -0.58 -0.49\r\nvn -0.56 -0.66 -0.5\r\nvn 0.64 0.58 0.5\r\nvn 0.46 0.7 0.54\r\nvn 0.79 0.42 0.44\r\nvn 0.79 0.42 0.45\r\nvn 0.65 0.58 0.49\r\nvn 0.46 0.73 0.51\r\nvn 0.98 -0.2 0.09\r\nvn 0.74 -0.63 -0.23\r\nvn 0.71 -0.69 -0.12\r\nvn 0.95 -0.25 0.18\r\nvn -0.65 -0.59 -0.47\r\nvn -0.57 -0.67 -0.48\r\nvn -0.73 -0.5 -0.46\r\nvn -0.74 -0.51 -0.45\r\nvn -0.66 -0.6 -0.45\r\nvn -0.58 -0.68 -0.45\r\nvn -0.96 0.18 -0.2\r\nvn -0.95 0.2 -0.25\r\nvn -0.72 0.69 0.13\r\nvn -0.69 0.72 0.05\r\nvn -0.68 0.74 0.01\r\nvn -0.94 0.21 -0.28\r\nvn 0.65 0.59 0.47\r\nvn 0.48 0.74 0.47\r\nvn 0.79 0.41 0.45\r\nvn 0.8 0.42 0.44\r\nvn 0.66 0.6 0.45\r\nvn 0.49 0.75 0.44\r\nvn 0.94 -0.27 0.22\r\nvn 0.69 -0.72 -0.05\r\nvn 0.67 -0.74 -0.01\r\nvn 0.93 -0.28 0.25\r\nvn -0.67 -0.61 -0.43\r\nvn -0.59 -0.69 -0.42\r\nvn -0.74 -0.51 -0.43\r\nvn -0.75 -0.52 -0.41\r\nvn -0.68 -0.61 -0.41\r\nvn -0.59 -0.7 -0.4\r\nvn -0.94 0.21 -0.28\r\nvn -0.67 0.75 -0.02\r\nvn -0.66 0.75 -0.03\r\nvn -0.94 0.2 -0.28\r\nvn 0.67 0.61 0.43\r\nvn 0.5 0.76 0.41\r\nvn 0.8 0.42 0.42\r\nvn 0.81 0.43 0.41\r\nvn 0.68 0.61 0.41\r\nvn 0.51 0.77 0.39\r\nvn 0.92 -0.29 0.25\r\nvn 0.67 -0.75 0.02\r\nvn 0.66 -0.75 0.03\r\nvn 0.92 -0.29 0.25\r\nvn -0.68 -0.62 -0.39\r\nvn -0.6 -0.7 -0.38\r\nvn -0.76 -0.52 -0.39\r\nvn -0.76 -0.53 -0.37\r\nvn -0.69 -0.62 -0.37\r\nvn -0.61 -0.71 -0.36\r\nvn -0.94 0.2 -0.28\r\nvn -0.66 0.75 -0.05\r\nvn -0.65 0.75 -0.05\r\nvn -0.94 0.2 -0.27\r\nvn 0.68 0.62 0.39\r\nvn 0.52 0.77 0.37\r\nvn 0.81 0.43 0.39\r\nvn 0.82 0.44 0.37\r\nvn 0.69 0.62 0.37\r\nvn 0.52 0.78 0.35\r\nvn 0.92 -0.29 0.25\r\nvn 0.66 -0.75 0.05\r\nvn 0.65 -0.75 0.05\r\nvn 0.93 -0.29 0.25\r\nvn -0.7 -0.63 -0.35\r\nvn -0.61 -0.71 -0.34\r\nvn -0.77 -0.53 -0.35\r\nvn -0.77 -0.54 -0.34\r\nvn -0.7 -0.63 -0.33\r\nvn -0.62 -0.72 -0.32\r\nvn -0.94 0.2 -0.26\r\nvn -0.65 0.76 -0.05\r\nvn -0.65 0.76 -0.06\r\nvn -0.95 0.2 -0.25\r\nvn 0.7 0.63 0.35\r\nvn 0.53 0.78 0.33\r\nvn 0.83 0.44 0.35\r\nvn 0.83 0.44 0.34\r\nvn 0.7 0.63 0.33\r\nvn 0.53 0.79 0.31\r\nvn 0.93 -0.29 0.24\r\nvn 0.65 -0.76 0.05\r\nvn 0.65 -0.76 0.06\r\nvn 0.93 -0.28 0.23\r\nvn -0.71 -0.63 -0.31\r\nvn -0.62 -0.72 -0.31\r\nvn -0.78 -0.54 -0.32\r\nvn -0.78 -0.54 -0.3\r\nvn -0.71 -0.64 -0.3\r\nvn -0.63 -0.72 -0.29\r\nvn -0.95 0.19 -0.24\r\nvn -0.65 0.76 -0.06\r\nvn -0.65 0.76 -0.06\r\nvn -0.95 0.19 -0.23\r\nvn 0.71 0.63 0.31\r\nvn 0.54 0.79 0.29\r\nvn 0.84 0.45 0.32\r\nvn 0.84 0.45 0.3\r\nvn 0.71 0.64 0.3\r\nvn 0.54 0.79 0.28\r\nvn 0.93 -0.28 0.22\r\nvn 0.65 -0.76 0.06\r\nvn 0.65 -0.76 0.06\r\nvn 0.94 -0.28 0.21\r\nvn -0.72 -0.64 -0.28\r\nvn -0.63 -0.72 -0.27\r\nvn -0.79 -0.55 -0.28\r\nvn -0.79 -0.55 -0.27\r\nvn -0.72 -0.64 -0.26\r\nvn -0.64 -0.73 -0.25\r\nvn -0.96 0.19 -0.22\r\nvn -0.65 0.76 -0.06\r\nvn -0.65 0.76 -0.06\r\nvn -0.96 0.19 -0.21\r\nvn 0.72 0.64 0.28\r\nvn 0.55 0.79 0.26\r\nvn 0.85 0.45 0.29\r\nvn 0.85 0.45 0.27\r\nvn 0.72 0.64 0.26\r\nvn 0.55 0.8 0.24\r\nvn 0.94 -0.28 0.2\r\nvn 0.65 -0.76 0.06\r\nvn 0.65 -0.76 0.06\r\nvn 0.94 -0.28 0.19\r\nvn -0.72 -0.64 -0.25\r\nvn -0.64 -0.73 -0.24\r\nvn -0.8 -0.55 -0.25\r\nvn -0.8 -0.55 -0.23\r\nvn -0.73 -0.65 -0.23\r\nvn -0.65 -0.73 -0.22\r\nvn -0.96 0.19 -0.2\r\nvn -0.65 0.76 -0.05\r\nvn -0.65 0.76 -0.05\r\nvn -0.97 0.18 -0.18\r\nvn 0.72 0.64 0.25\r\nvn 0.56 0.8 0.23\r\nvn 0.85 0.46 0.25\r\nvn 0.86 0.46 0.23\r\nvn 0.73 0.65 0.23\r\nvn 0.56 0.8 0.21\r\nvn 0.94 -0.28 0.18\r\nvn 0.65 -0.76 0.05\r\nvn 0.65 -0.76 0.05\r\nvn 0.95 -0.28 0.17\r\nvn -0.73 -0.65 -0.21\r\nvn -0.65 -0.73 -0.2\r\nvn -0.8 -0.55 -0.22\r\nvn -0.81 -0.56 -0.2\r\nvn -0.73 -0.65 -0.2\r\nvn -0.65 -0.73 -0.19\r\nvn -0.97 0.18 -0.17\r\nvn -0.65 0.75 -0.05\r\nvn -0.65 0.75 -0.05\r\nvn -0.97 0.18 -0.16\r\nvn 0.73 0.65 0.21\r\nvn 0.56 0.8 0.19\r\nvn 0.86 0.46 0.22\r\nvn 0.86 0.46 0.2\r\nvn 0.73 0.65 0.2\r\nvn 0.57 0.8 0.18\r\nvn 0.95 -0.27 0.16\r\nvn 0.65 -0.75 0.05\r\nvn 0.65 -0.75 0.05\r\nvn 0.95 -0.27 0.15\r\nvn -0.74 -0.65 -0.18\r\nvn -0.65 -0.74 -0.17\r\nvn -0.81 -0.56 -0.18\r\nvn -0.81 -0.56 -0.17\r\nvn -0.74 -0.65 -0.17\r\nvn -0.66 -0.74 -0.16\r\nvn -0.97 0.18 -0.15\r\nvn -0.66 0.75 -0.04\r\nvn -0.66 0.75 -0.04\r\nvn -0.97 0.18 -0.14\r\nvn 0.74 0.65 0.18\r\nvn 0.57 0.81 0.17\r\nvn 0.87 0.46 0.19\r\nvn 0.87 0.46 0.17\r\nvn 0.74 0.65 0.17\r\nvn 0.57 0.81 0.15\r\nvn 0.95 -0.27 0.14\r\nvn 0.66 -0.75 0.04\r\nvn 0.66 -0.75 0.04\r\nvn 0.95 -0.27 0.13\r\nvn -0.74 -0.65 -0.15\r\nvn -0.66 -0.74 -0.15\r\nvn -0.81 -0.56 -0.16\r\nvn -0.82 -0.56 -0.14\r\nvn -0.74 -0.65 -0.14\r\nvn -0.66 -0.74 -0.13\r\nvn -0.98 0.18 -0.13\r\nvn -0.66 0.75 -0.04\r\nvn -0.66 0.75 -0.04\r\nvn -0.98 0.18 -0.12\r\nvn 0.74 0.65 0.15\r\nvn 0.57 0.81 0.14\r\nvn 0.87 0.46 0.16\r\nvn 0.87 0.47 0.14\r\nvn 0.74 0.65 0.14\r\nvn 0.57 0.81 0.13\r\nvn 0.96 -0.27 0.12\r\nvn 0.66 -0.75 0.04\r\nvn 0.66 -0.75 0.04\r\nvn 0.96 -0.27 0.11\r\nvn -0.75 -0.65 -0.13\r\nvn -0.66 -0.74 -0.12\r\nvn -0.82 -0.56 -0.13\r\nvn -0.82 -0.56 -0.12\r\nvn -0.75 -0.66 -0.12\r\nvn -0.66 -0.74 -0.11\r\nvn -0.98 0.18 -0.11\r\nvn -0.66 0.75 -0.03\r\nvn -0.66 0.75 -0.03\r\nvn -0.98 0.18 -0.1\r\nvn 0.75 0.65 0.13\r\nvn 0.58 0.81 0.12\r\nvn 0.87 0.47 0.13\r\nvn 0.88 0.47 0.12\r\nvn 0.75 0.66 0.12\r\nvn 0.58 0.81 0.1\r\nvn 0.96 -0.27 0.1\r\nvn 0.66 -0.75 0.03\r\nvn 0.66 -0.75 0.03\r\nvn 0.96 -0.27 0.09\r\nvn -0.75 -0.66 -0.1\r\nvn -0.66 -0.74 -0.1\r\nvn -0.82 -0.56 -0.11\r\nvn -0.82 -0.56 -0.09\r\nvn -0.75 -0.66 -0.09\r\nvn -0.67 -0.74 -0.09\r\nvn -0.98 0.18 -0.09\r\nvn -0.66 0.75 -0.03\r\nvn -0.66 0.75 -0.03\r\nvn -0.98 0.18 -0.08\r\nvn 0.75 0.66 0.1\r\nvn 0.58 0.81 0.09\r\nvn 0.88 0.47 0.11\r\nvn 0.88 0.47 0.09\r\nvn 0.75 0.66 0.09\r\nvn 0.58 0.81 0.08\r\nvn 0.96 -0.27 0.08\r\nvn 0.66 -0.75 0.03\r\nvn 0.66 -0.75 0.03\r\nvn 0.96 -0.27 0.07\r\nvn -0.75 -0.66 -0.08\r\nvn -0.67 -0.74 -0.07\r\nvn -0.82 -0.56 -0.08\r\nvn -0.82 -0.56 -0.07\r\nvn -0.75 -0.66 -0.06\r\nvn -0.67 -0.74 -0.06\r\nvn -0.98 0.18 -0.07\r\nvn -0.66 0.75 -0.02\r\nvn -0.66 0.75 -0.02\r\nvn -0.98 0.18 -0.06\r\nvn 0.75 0.66 0.08\r\nvn 0.58 0.81 0.07\r\nvn 0.88 0.47 0.08\r\nvn 0.88 0.47 0.07\r\nvn 0.75 0.66 0.06\r\nvn 0.58 0.81 0.06\r\nvn 0.96 -0.27 0.06\r\nvn 0.66 -0.75 0.02\r\nvn 0.66 -0.75 0.02\r\nvn 0.96 -0.27 0.05\r\nvn -0.75 -0.66 -0.05\r\nvn -0.67 -0.74 -0.05\r\nvn -0.82 -0.56 -0.05\r\nvn -0.82 -0.56 -0.04\r\nvn -0.75 -0.66 -0.03\r\nvn -0.67 -0.74 -0.03\r\nvn -0.98 0.18 -0.04\r\nvn -0.66 0.75 -0.02\r\nvn -0.66 0.75 -0.02\r\nvn -0.98 0.17 -0.03\r\nvn 0.75 0.66 0.05\r\nvn 0.58 0.81 0.04\r\nvn 0.88 0.47 0.05\r\nvn 0.88 0.47 0.04\r\nvn 0.75 0.66 0.03\r\nvn 0.58 0.81 0.03\r\nvn 0.96 -0.27 0.04\r\nvn 0.66 -0.75 0.02\r\nvn 0.66 -0.75 0.02\r\nvn 0.96 -0.27 0.03\r\nvn -0.75 -0.66 -0.02\r\nvn -0.67 -0.74 -0.02\r\nvn -0.83 -0.56 -0.02\r\nvn -0.83 -0.56 -0.01\r\nvn -0.75 -0.66 -0.01\r\nvn -0.67 -0.74 -0.01\r\nvn -0.98 0.17 -0.02\r\nvn -0.66 0.75 -0.01\r\nvn -0.66 0.75 -0.01\r\nvn -0.98 0.17 -0.01\r\nvn 0.75 0.66 0.02\r\nvn 0.58 0.81 0.02\r\nvn 0.88 0.47 0.02\r\nvn 0.88 0.47 0.01\r\nvn 0.75 0.66 0.01\r\nvn 0.58 0.81 0\r\nvn 0.96 -0.27 0.02\r\nvn 0.66 -0.75 0.01\r\nvn 0.66 -0.75 0.01\r\nvn 0.96 -0.27 0.01\r\nvn -0.75 -0.66 0.01\r\nvn -0.67 -0.74 0.01\r\nvn -0.83 -0.56 0\r\nvn -0.83 -0.56 0.02\r\nvn -0.75 -0.66 0.02\r\nvn -0.67 -0.74 0.02\r\nvn -0.98 0.17 -0\r\nvn -0.66 0.75 -0.01\r\nvn -0.66 0.75 -0.01\r\nvn -0.98 0.17 0.01\r\nvn 0.75 0.66 -0.01\r\nvn 0.58 0.81 -0.01\r\nvn 0.88 0.47 -0\r\nvn 0.88 0.47 -0.02\r\nvn 0.75 0.66 -0.02\r\nvn 0.58 0.81 -0.02\r\nvn 0.96 -0.27 0\r\nvn 0.66 -0.75 0.01\r\nvn 0.66 -0.75 0.01\r\nvn 0.96 -0.27 -0.01\r\nvn -0.75 -0.66 0.03\r\nvn -0.67 -0.74 0.03\r\nvn -0.83 -0.56 0.03\r\nvn -0.83 -0.56 0.04\r\nvn -0.75 -0.66 0.04\r\nvn -0.67 -0.74 0.04\r\nvn -0.98 0.17 0.02\r\nvn -0.66 0.75 -0\r\nvn -0.66 0.75 -0\r\nvn -0.98 0.18 0.03\r\nvn 0.75 0.66 -0.03\r\nvn 0.58 0.81 -0.03\r\nvn 0.88 0.47 -0.03\r\nvn 0.88 0.47 -0.04\r\nvn 0.75 0.66 -0.04\r\nvn 0.58 0.81 -0.04\r\nvn 0.96 -0.27 -0.01\r\nvn 0.66 -0.75 0\r\nvn 0.66 -0.75 0\r\nvn 0.96 -0.27 -0.02\r\nvn -0.75 -0.66 0.06\r\nvn -0.67 -0.74 0.06\r\nvn -0.82 -0.56 0.06\r\nvn -0.82 -0.56 0.07\r\nvn -0.75 -0.66 0.07\r\nvn -0.67 -0.74 0.07\r\nvn -0.98 0.18 0.04\r\nvn -0.66 0.75 0\r\nvn -0.66 0.75 0.01\r\nvn -0.98 0.18 0.05\r\nvn 0.75 0.66 -0.06\r\nvn 0.58 0.81 -0.05\r\nvn 0.88 0.47 -0.06\r\nvn 0.88 0.47 -0.07\r\nvn 0.75 0.66 -0.07\r\nvn 0.58 0.81 -0.07\r\nvn 0.96 -0.27 -0.03\r\nvn 0.66 -0.75 -0\r\nvn 0.66 -0.75 -0.01\r\nvn 0.96 -0.27 -0.04\r\nvn -0.75 -0.66 0.08\r\nvn -0.67 -0.74 0.08\r\nvn -0.82 -0.56 0.08\r\nvn -0.82 -0.56 0.09\r\nvn -0.75 -0.66 0.09\r\nvn -0.67 -0.74 0.09\r\nvn -0.98 0.18 0.06\r\nvn -0.66 0.75 0.01\r\nvn -0.66 0.75 0.01\r\nvn -0.98 0.18 0.07\r\nvn 0.75 0.66 -0.08\r\nvn 0.58 0.81 -0.08\r\nvn 0.88 0.47 -0.08\r\nvn 0.88 0.47 -0.09\r\nvn 0.75 0.66 -0.09\r\nvn 0.58 0.81 -0.09\r\nvn 0.96 -0.27 -0.05\r\nvn 0.66 -0.75 -0.01\r\nvn 0.66 -0.75 -0.01\r\nvn 0.96 -0.27 -0.06\r\nvn -0.75 -0.65 0.11\r\nvn -0.67 -0.74 0.1\r\nvn -0.82 -0.56 0.11\r\nvn -0.82 -0.56 0.12\r\nvn -0.75 -0.65 0.12\r\nvn -0.66 -0.74 0.12\r\nvn -0.98 0.18 0.08\r\nvn -0.66 0.75 0.01\r\nvn -0.66 0.75 0.02\r\nvn -0.98 0.18 0.09\r\nvn 0.75 0.65 -0.11\r\nvn 0.58 0.81 -0.1\r\nvn 0.88 0.47 -0.11\r\nvn 0.88 0.46 -0.12\r\nvn 0.75 0.65 -0.12\r\nvn 0.58 0.81 -0.11\r\nvn 0.96 -0.27 -0.07\r\nvn 0.66 -0.75 -0.01\r\nvn 0.66 -0.75 -0.02\r\nvn 0.96 -0.27 -0.08\r\nvn -0.75 -0.65 0.14\r\nvn -0.66 -0.74 0.13\r\nvn -0.82 -0.56 0.14\r\nvn -0.82 -0.56 0.15\r\nvn -0.74 -0.65 0.15\r\nvn -0.66 -0.74 0.15\r\nvn -0.98 0.18 0.1\r\nvn -0.66 0.75 0.02\r\nvn -0.66 0.75 0.02\r\nvn -0.98 0.18 0.11\r\nvn 0.75 0.65 -0.14\r\nvn 0.58 0.81 -0.13\r\nvn 0.88 0.46 -0.14\r\nvn 0.87 0.46 -0.15\r\nvn 0.74 0.65 -0.15\r\nvn 0.58 0.81 -0.14\r\nvn 0.96 -0.27 -0.09\r\nvn 0.66 -0.75 -0.02\r\nvn 0.66 -0.75 -0.02\r\nvn 0.96 -0.27 -0.1\r\nvn -0.74 -0.65 0.17\r\nvn -0.66 -0.73 0.16\r\nvn -0.81 -0.56 0.17\r\nvn -0.81 -0.55 0.19\r\nvn -0.74 -0.65 0.19\r\nvn -0.66 -0.73 0.18\r\nvn -0.98 0.18 0.12\r\nvn -0.66 0.75 0.02\r\nvn -0.66 0.75 0.03\r\nvn -0.97 0.18 0.14\r\nvn 0.74 0.65 -0.17\r\nvn 0.57 0.8 -0.16\r\nvn 0.87 0.46 -0.17\r\nvn 0.87 0.46 -0.19\r\nvn 0.74 0.65 -0.19\r\nvn 0.57 0.8 -0.17\r\nvn 0.96 -0.27 -0.11\r\nvn 0.66 -0.75 -0.02\r\nvn 0.66 -0.75 -0.03\r\nvn 0.95 -0.27 -0.12\r\nvn -0.74 -0.65 0.2\r\nvn -0.65 -0.73 0.2\r\nvn -0.81 -0.55 0.21\r\nvn -0.8 -0.55 0.22\r\nvn -0.73 -0.64 0.22\r\nvn -0.65 -0.73 0.22\r\nvn -0.97 0.18 0.15\r\nvn -0.65 0.76 0.03\r\nvn -0.65 0.76 0.03\r\nvn -0.97 0.19 0.16\r\nvn 0.74 0.65 -0.2\r\nvn 0.57 0.8 -0.19\r\nvn 0.87 0.46 -0.2\r\nvn 0.86 0.46 -0.22\r\nvn 0.73 0.64 -0.22\r\nvn 0.56 0.8 -0.21\r\nvn 0.95 -0.28 -0.14\r\nvn 0.65 -0.76 -0.03\r\nvn 0.65 -0.76 -0.03\r\nvn 0.95 -0.28 -0.15\r\nvn -0.73 -0.64 0.24\r\nvn -0.65 -0.73 0.23\r\nvn -0.8 -0.55 0.24\r\nvn -0.8 -0.54 0.26\r\nvn -0.72 -0.64 0.26\r\nvn -0.64 -0.72 0.25\r\nvn -0.97 0.19 0.18\r\nvn -0.65 0.76 0.03\r\nvn -0.65 0.76 0.04\r\nvn -0.96 0.19 0.19\r\nvn 0.73 0.64 -0.24\r\nvn 0.56 0.8 -0.23\r\nvn 0.86 0.45 -0.24\r\nvn 0.85 0.45 -0.26\r\nvn 0.72 0.64 -0.26\r\nvn 0.56 0.79 -0.24\r\nvn 0.95 -0.28 -0.16\r\nvn 0.65 -0.76 -0.03\r\nvn 0.65 -0.76 -0.04\r\nvn 0.94 -0.28 -0.17\r\nvn -0.72 -0.64 0.28\r\nvn -0.64 -0.72 0.27\r\nvn -0.79 -0.54 0.28\r\nvn -0.79 -0.54 0.3\r\nvn -0.72 -0.63 0.3\r\nvn -0.63 -0.72 0.29\r\nvn -0.96 0.19 0.2\r\nvn -0.65 0.76 0.04\r\nvn -0.65 0.76 0.04\r\nvn -0.96 0.19 0.21\r\nvn 0.72 0.64 -0.28\r\nvn 0.55 0.79 -0.26\r\nvn 0.85 0.45 -0.28\r\nvn 0.85 0.44 -0.3\r\nvn 0.72 0.63 -0.3\r\nvn 0.55 0.79 -0.28\r\nvn 0.94 -0.28 -0.18\r\nvn 0.65 -0.76 -0.04\r\nvn 0.65 -0.76 -0.04\r\nvn 0.94 -0.28 -0.19\r\nvn -0.71 -0.63 0.32\r\nvn -0.63 -0.71 0.31\r\nvn -0.78 -0.54 0.32\r\nvn -0.78 -0.53 0.34\r\nvn -0.7 -0.63 0.34\r\nvn -0.62 -0.71 0.33\r\nvn -0.95 0.2 0.23\r\nvn -0.65 0.76 0.04\r\nvn -0.95 0.2 0.24\r\nvn 0.71 0.63 -0.32\r\nvn 0.54 0.78 -0.3\r\nvn 0.84 0.44 -0.32\r\nvn 0.83 0.44 -0.34\r\nvn 0.7 0.63 -0.34\r\nvn 0.54 0.78 -0.32\r\nvn 0.94 -0.28 -0.21\r\nvn 0.65 -0.76 -0.04\r\nvn 0.93 -0.29 -0.22\r\nvn -0.7 -0.62 0.36\r\nvn -0.62 -0.71 0.35\r\nvn -0.77 -0.53 0.36\r\nvn -0.76 -0.52 0.38\r\nvn -0.69 -0.62 0.38\r\nvn -0.61 -0.7 0.37\r\nvn -0.95 0.2 0.25\r\nvn -0.65 0.76 0.04\r\nvn -0.65 0.76 0.04\r\nvn -0.94 0.2 0.26\r\nvn 0.7 0.62 -0.36\r\nvn 0.53 0.78 -0.34\r\nvn 0.83 0.43 -0.35\r\nvn 0.82 0.43 -0.37\r\nvn 0.69 0.62 -0.38\r\nvn 0.53 0.77 -0.36\r\nvn 0.93 -0.29 -0.23\r\nvn 0.65 -0.76 -0.04\r\nvn 0.65 -0.76 -0.04\r\nvn 0.93 -0.29 -0.24\r\nvn -0.69 -0.61 0.4\r\nvn -0.6 -0.7 0.39\r\nvn -0.76 -0.52 0.4\r\nvn -0.75 -0.51 0.42\r\nvn -0.68 -0.61 0.42\r\nvn -0.6 -0.69 0.41\r\nvn -0.94 0.21 0.27\r\nvn -0.66 0.75 0.03\r\nvn -0.66 0.75 0.02\r\nvn -0.94 0.21 0.28\r\nvn 0.69 0.61 -0.4\r\nvn 0.52 0.77 -0.38\r\nvn 0.82 0.42 -0.39\r\nvn 0.81 0.42 -0.41\r\nvn 0.68 0.61 -0.42\r\nvn 0.51 0.76 -0.4\r\nvn 0.92 -0.29 -0.24\r\nvn 0.66 -0.75 -0.03\r\nvn 0.66 -0.75 -0.02\r\nvn 0.92 -0.29 -0.25\r\nvn -0.67 -0.6 0.45\r\nvn -0.59 -0.68 0.44\r\nvn -0.74 -0.5 0.45\r\nvn -0.71 -0.49 0.5\r\nvn -0.63 -0.6 0.49\r\nvn -0.55 -0.69 0.47\r\nvn -0.94 0.22 0.27\r\nvn -0.67 0.74 -0.01\r\nvn -0.71 0.7 -0.05\r\nvn -0.94 0.2 0.27\r\nvn 0.67 0.6 -0.45\r\nvn 0.5 0.75 -0.44\r\nvn 0.8 0.41 -0.43\r\nvn 0.78 0.42 -0.47\r\nvn 0.63 0.6 -0.5\r\nvn 0.45 0.74 -0.51\r\nvn 0.92 -0.3 -0.26\r\nvn 0.66 -0.75 -0.02\r\nvn 0.66 -0.75 -0.04\r\nvn 0.91 -0.29 -0.29\r\nvn -0.57 -0.63 0.52\r\nvn -0.48 -0.73 0.49\r\nvn -0.64 -0.51 0.57\r\nvn -0.48 -0.69 0.54\r\nvn -0.5 -0.71 0.49\r\nvn -0.5 -0.7 0.52\r\nvn -0.89 0.14 0.44\r\nvn -0.74 0.66 0.11\r\nvn -0.52 0.64 0.57\r\nvn -0.62 -0.1 0.78\r\nvn 0.56 0.64 -0.52\r\nvn 0.37 0.76 -0.53\r\nvn 0.73 0.49 -0.48\r\nvn 0.69 0.57 -0.45\r\nvn 0.5 0.72 -0.49\r\nvn 0.37 0.84 -0.4\r\nvn 0.93 -0.22 -0.29\r\nvn 0.71 -0.71 0\r\nvn 0.77 -0.6 0.22\r\nvn 0.98 -0.1 -0.16\r\nvn -0.19 -0.97 -0.15\r\nvn 0.15 -0.75 -0.64\r\nvn 0.67 -0.66 -0.34\r\nvn 0.24 -0.93 0.27\r\nvn 0.06 0.75 -0.66\r\nvn 0.06 0.88 -0.47\r\nvn 0.09 0.88 -0.47\r\nvn 0.08 0.75 -0.65\r\nvn 0.06 0.97 -0.25\r\nvn 0.09 0.96 -0.25\r\nvn 0.11 0.96 -0.25\r\nvn 0.12 0.88 -0.46\r\nvn 0.11 0.75 -0.65\r\nvn 0.03 0.88 0.48\r\nvn 0.05 0.88 0.48\r\nvn 0 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0.07 0.87 0.48\r\nvn -0.06 -0.82 0.57\r\nvn -0.06 -0.88 0.47\r\nvn -0.09 -0.88 0.47\r\nvn -0.09 -0.82 0.57\r\nvn -0.06 -0.93 0.36\r\nvn -0.09 -0.93 0.36\r\nvn -0.11 -0.93 0.36\r\nvn -0.12 -0.88 0.46\r\nvn -0.11 -0.82 0.57\r\nvn -0.04 -0.92 -0.4\r\nvn -0.06 -0.91 -0.4\r\nvn -0 -0.47 -0.88\r\nvn -0 -0.47 -0.88\r\nvn -0 -0.47 -0.88\r\nvn -0.08 -0.91 -0.4\r\nvn 0.14 0.87 -0.46\r\nvn 0.14 0.75 -0.65\r\nvn 0.14 0.96 -0.25\r\nvn 0.16 0.95 -0.25\r\nvn 0.17 0.87 -0.46\r\nvn 0.16 0.74 -0.65\r\nvn 0.08 0.87 0.48\r\nvn 0.01 0.47 0.88\r\nvn 0.01 0.47 0.88\r\nvn 0.1 0.87 0.48\r\nvn -0.14 -0.87 0.46\r\nvn -0.14 -0.81 0.56\r\nvn -0.14 -0.92 0.36\r\nvn -0.17 -0.92 0.35\r\nvn -0.17 -0.87 0.46\r\nvn -0.16 -0.81 0.56\r\nvn -0.09 -0.91 -0.4\r\nvn -0.01 -0.47 -0.88\r\nvn -0.01 -0.47 -0.88\r\nvn -0.11 -0.91 -0.4\r\nvn 0.19 0.87 -0.46\r\nvn 0.18 0.74 -0.65\r\nvn 0.19 0.95 -0.25\r\nvn 0.21 0.95 -0.24\r\nvn 0.21 0.86 -0.46\r\nvn 0.21 0.73 -0.65\r\nvn 0.11 0.87 0.48\r\nvn 0.01 0.47 0.88\r\nvn 0.01 0.47 0.88\r\nvn 0.12 0.87 0.49\r\nvn -0.19 -0.87 0.46\r\nvn -0.19 -0.81 0.56\r\nvn -0.19 -0.92 0.35\r\nvn -0.21 -0.91 0.35\r\nvn -0.21 -0.86 0.46\r\nvn -0.21 -0.8 0.56\r\nvn -0.12 -0.91 -0.4\r\nvn -0.01 -0.47 -0.88\r\nvn -0.01 -0.47 -0.88\r\nvn -0.14 -0.9 -0.4\r\nvn 0.23 0.86 -0.46\r\nvn 0.23 0.73 -0.64\r\nvn 0.23 0.94 -0.24\r\nvn 0.25 0.94 -0.24\r\nvn 0.25 0.85 -0.45\r\nvn 0.25 0.73 -0.64\r\nvn 0.14 0.86 0.49\r\nvn 0.01 0.47 0.88\r\nvn 0.15 0.86 0.49\r\nvn -0.23 -0.86 0.46\r\nvn -0.23 -0.8 0.56\r\nvn -0.23 -0.91 0.35\r\nvn -0.25 -0.9 0.35\r\nvn -0.25 -0.85 0.45\r\nvn -0.25 -0.79 0.55\r\nvn -0.15 -0.9 -0.41\r\nvn -0.01 -0.47 -0.88\r\nvn -0.17 -0.9 -0.41\r\nvn 0.27 0.85 -0.45\r\nvn 0.27 0.72 -0.64\r\nvn 0.27 0.93 -0.24\r\nvn 0.29 0.93 -0.24\r\nvn 0.29 0.84 -0.45\r\nvn 0.28 0.72 -0.64\r\nvn 0.16 0.86 0.49\r\nvn 0.01 0.47 0.88\r\nvn 0.17 0.85 0.49\r\nvn -0.27 -0.85 0.45\r\nvn -0.27 -0.79 0.55\r\nvn -0.27 -0.9 0.34\r\nvn -0.29 -0.89 0.34\r\nvn -0.29 -0.84 0.45\r\nvn -0.29 -0.78 0.55\r\nvn -0.18 -0.9 -0.41\r\nvn -0.01 -0.47 -0.88\r\nvn -0.19 -0.89 -0.41\r\nvn 0.31 0.84 -0.45\r\nvn 0.3 0.71 -0.64\r\nvn 0.3 0.92 -0.23\r\nvn 0.32 0.92 -0.23\r\nvn 0.33 0.83 -0.44\r\nvn 0.32 0.71 -0.63\r\nvn 0.18 0.85 0.49\r\nvn 0.01 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0.18 0.85 0.49\r\nvn -0.31 -0.84 0.45\r\nvn -0.31 -0.78 0.55\r\nvn -0.31 -0.89 0.34\r\nvn -0.33 -0.88 0.34\r\nvn -0.33 -0.83 0.44\r\nvn -0.33 -0.77 0.54\r\nvn -0.2 -0.89 -0.41\r\nvn -0.01 -0.47 -0.88\r\nvn -0 -0.47 -0.88\r\nvn -0.21 -0.89 -0.41\r\nvn 0.35 0.83 -0.44\r\nvn 0.34 0.7 -0.63\r\nvn 0.34 0.91 -0.23\r\nvn 0.35 0.91 -0.23\r\nvn 0.36 0.82 -0.44\r\nvn 0.35 0.69 -0.63\r\nvn 0.19 0.85 0.5\r\nvn 0 0.47 0.88\r\nvn 0 0.47 0.88\r\nvn 0.2 0.84 0.5\r\nvn -0.35 -0.83 0.44\r\nvn -0.34 -0.77 0.54\r\nvn -0.34 -0.88 0.33\r\nvn -0.36 -0.87 0.33\r\nvn -0.36 -0.82 0.44\r\nvn -0.36 -0.76 0.54\r\nvn -0.22 -0.88 -0.41\r\nvn -0 -0.47 -0.88\r\nvn -0 -0.47 -0.88\r\nvn -0.23 -0.88 -0.42\r\nvn 0.38 0.82 -0.43\r\nvn 0.37 0.69 -0.62\r\nvn 0.37 0.9 -0.22\r\nvn 0.4 0.89 -0.21\r\nvn 0.41 0.81 -0.42\r\nvn 0.4 0.68 -0.61\r\nvn 0.21 0.84 0.5\r\nvn -0 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn 0.22 0.84 0.5\r\nvn -0.38 -0.82 0.43\r\nvn -0.38 -0.75 0.53\r\nvn -0.38 -0.87 0.33\r\nvn -0.4 -0.86 0.32\r\nvn -0.41 -0.81 0.42\r\nvn -0.41 -0.75 0.53\r\nvn -0.24 -0.88 -0.42\r\nvn 0 -0.47 -0.88\r\nvn 0.01 -0.47 -0.88\r\nvn -0.24 -0.87 -0.42\r\nvn 0.43 0.8 -0.41\r\nvn 0.42 0.68 -0.6\r\nvn 0.39 0.9 -0.21\r\nvn 0.45 0.78 -0.44\r\nvn 0.44 0.67 -0.6\r\nvn 0.11 0.88 0.47\r\nvn -0.13 0.52 0.84\r\nvn -0.49 0.63 0.6\r\nvn -0.13 0.98 0.12\r\nvn -0.43 -0.8 0.41\r\nvn -0.43 -0.75 0.51\r\nvn -0.42 -0.86 0.31\r\nvn -0.44 -0.85 0.3\r\nvn -0.45 -0.79 0.42\r\nvn -0.25 -0.87 -0.43\r\nvn 0.01 -0.47 -0.88\r\nvn 0.02 -0.48 -0.88\r\nvn -0.26 -0.87 -0.43\r\nvn 0.49 0.74 -0.46\r\nvn 0.46 0.66 -0.59\r\nvn 0.57 0.67 -0.47\r\nvn 0.49 0.66 -0.58\r\nvn -0.5 -0.75 0.44\r\nvn -0.5 -0.8 0.33\r\nvn -0.61 -0.69 0.38\r\nvn -0.57 -0.67 0.47\r\nvn -0.37 -0.83 -0.42\r\nvn -0.08 -0.42 -0.9\r\nvn -0.47 -0.09 -0.88\r\nvn -0.76 -0.58 -0.3\r\nvn 0.65 0.59 -0.49\r\nvn 0.48 0.64 -0.6\r\nvn 0.8 0.47 -0.37\r\nvn 0.81 0.47 -0.37\r\nvn 0.66 0.56 -0.51\r\nvn 0.47 0.62 -0.63\r\nvn 0.99 -0.07 0.09\r\nvn 0.76 -0.46 0.46\r\nvn 0.74 -0.36 0.57\r\nvn 0.98 0.02 0.19\r\nvn -0.64 -0.59 0.49\r\nvn -0.56 -0.62 0.55\r\nvn -0.71 -0.57 0.41\r\nvn -0.74 -0.51 0.43\r\nvn -0.66 -0.56 0.51\r\nvn -0.57 -0.59 0.57\r\nvn -0.97 -0.17 -0.18\r\nvn -0.71 0.28 -0.64\r\nvn -0.73 0.33 -0.6\r\nvn -0.98 -0.09 -0.16\r\nvn 0.66 0.54 -0.52\r\nvn 0.48 0.59 -0.65\r\nvn 0.81 0.47 -0.36\r\nvn 0.81 0.46 -0.37\r\nvn 0.67 0.52 -0.53\r\nvn 0.49 0.56 -0.67\r\nvn 0.97 0.07 0.24\r\nvn 0.72 -0.28 0.63\r\nvn 0.71 -0.22 0.67\r\nvn 0.96 0.1 0.26\r\nvn -0.66 -0.54 0.52\r\nvn -0.57 -0.57 0.59\r\nvn -0.74 -0.51 0.44\r\nvn -0.75 -0.49 0.45\r\nvn -0.67 -0.52 0.53\r\nvn -0.58 -0.54 0.61\r\nvn -0.97 -0.13 -0.18\r\nvn -0.72 0.26 -0.64\r\nvn -0.71 0.22 -0.67\r\nvn -0.97 -0.15 -0.2\r\nvn 0.67 0.5 -0.55\r\nvn 0.5 0.53 -0.69\r\nvn 0.81 0.44 -0.38\r\nvn 0.82 0.43 -0.38\r\nvn 0.68 0.48 -0.56\r\nvn 0.5 0.5 -0.7\r\nvn 0.96 0.12 0.27\r\nvn 0.7 -0.19 0.69\r\nvn 0.69 -0.17 0.7\r\nvn 0.95 0.13 0.28\r\nvn -0.67 -0.5 0.55\r\nvn -0.59 -0.52 0.62\r\nvn -0.75 -0.47 0.46\r\nvn -0.76 -0.45 0.47\r\nvn -0.68 -0.48 0.56\r\nvn -0.59 -0.49 0.64\r\nvn -0.97 -0.16 -0.2\r\nvn -0.7 0.19 -0.69\r\nvn -0.69 0.17 -0.7\r\nvn -0.96 -0.17 -0.2\r\nvn 0.69 0.45 -0.57\r\nvn 0.51 0.48 -0.72\r\nvn 0.82 0.41 -0.39\r\nvn 0.83 0.39 -0.4\r\nvn 0.69 0.43 -0.58\r\nvn 0.52 0.45 -0.73\r\nvn 0.95 0.13 0.28\r\nvn 0.69 -0.14 0.71\r\nvn 0.68 -0.13 0.72\r\nvn 0.95 0.13 0.28\r\nvn -0.69 -0.45 0.57\r\nvn -0.6 -0.47 0.65\r\nvn -0.76 -0.43 0.48\r\nvn -0.77 -0.42 0.49\r\nvn -0.69 -0.43 0.58\r\nvn -0.61 -0.44 0.66\r\nvn -0.96 -0.17 -0.21\r\nvn -0.69 0.14 -0.71\r\nvn -0.68 0.13 -0.72\r\nvn -0.96 -0.17 -0.21\r\nvn 0.7 0.41 -0.59\r\nvn 0.53 0.43 -0.74\r\nvn 0.83 0.38 -0.41\r\nvn 0.84 0.36 -0.41\r\nvn 0.7 0.39 -0.59\r\nvn 0.53 0.4 -0.75\r\nvn 0.95 0.14 0.28\r\nvn 0.68 -0.11 0.73\r\nvn 0.67 -0.1 0.73\r\nvn 0.95 0.14 0.28\r\nvn -0.7 -0.41 0.59\r\nvn -0.61 -0.42 0.67\r\nvn -0.77 -0.4 0.49\r\nvn -0.78 -0.38 0.5\r\nvn -0.7 -0.39 0.59\r\nvn -0.62 -0.4 0.68\r\nvn -0.96 -0.17 -0.2\r\nvn -0.68 0.11 -0.73\r\nvn -0.67 0.1 -0.73\r\nvn -0.96 -0.17 -0.2\r\nvn 0.71 0.37 -0.6\r\nvn 0.54 0.38 -0.75\r\nvn 0.84 0.34 -0.42\r\nvn 0.85 0.33 -0.42\r\nvn 0.71 0.35 -0.61\r\nvn 0.54 0.36 -0.76\r\nvn 0.95 0.14 0.28\r\nvn 0.67 -0.08 0.74\r\nvn 0.67 -0.07 0.74\r\nvn 0.95 0.13 0.28\r\nvn -0.71 -0.37 0.6\r\nvn -0.62 -0.38 0.68\r\nvn -0.78 -0.36 0.51\r\nvn -0.79 -0.34 0.51\r\nvn -0.71 -0.35 0.61\r\nvn -0.63 -0.36 0.69\r\nvn -0.96 -0.17 -0.2\r\nvn -0.67 0.08 -0.74\r\nvn -0.67 0.07 -0.74\r\nvn -0.97 -0.16 -0.2\r\nvn 0.72 0.33 -0.61\r\nvn 0.55 0.34 -0.77\r\nvn 0.85 0.31 -0.43\r\nvn 0.85 0.29 -0.43\r\nvn 0.72 0.31 -0.62\r\nvn 0.55 0.32 -0.77\r\nvn 0.95 0.13 0.28\r\nvn 0.66 -0.06 0.74\r\nvn 0.66 -0.05 0.75\r\nvn 0.95 0.13 0.28\r\nvn -0.72 -0.33 0.61\r\nvn -0.63 -0.34 0.7\r\nvn -0.79 -0.32 0.52\r\nvn -0.79 -0.31 0.52\r\nvn -0.72 -0.31 0.62\r\nvn -0.64 -0.32 0.7\r\nvn -0.97 -0.16 -0.2\r\nvn -0.66 0.06 -0.74\r\nvn -0.66 0.05 -0.75\r\nvn -0.97 -0.15 -0.2\r\nvn 0.72 0.3 -0.62\r\nvn 0.56 0.3 -0.78\r\nvn 0.86 0.28 -0.44\r\nvn 0.86 0.26 -0.44\r\nvn 0.73 0.28 -0.63\r\nvn 0.56 0.28 -0.78\r\nvn 0.95 0.12 0.28\r\nvn 0.66 -0.05 0.75\r\nvn 0.66 -0.04 0.75\r\nvn 0.95 0.12 0.28\r\nvn -0.73 -0.3 0.62\r\nvn -0.64 -0.3 0.71\r\nvn -0.8 -0.29 0.53\r\nvn -0.8 -0.27 0.53\r\nvn -0.73 -0.28 0.63\r\nvn -0.65 -0.28 0.71\r\nvn -0.97 -0.15 -0.2\r\nvn -0.66 0.05 -0.75\r\nvn -0.66 0.04 -0.75\r\nvn -0.97 -0.14 -0.19\r\nvn 0.73 0.26 -0.63\r\nvn 0.56 0.26 -0.78\r\nvn 0.86 0.24 -0.44\r\nvn 0.87 0.23 -0.45\r\nvn 0.74 0.24 -0.63\r\nvn 0.57 0.24 -0.79\r\nvn 0.95 0.11 0.28\r\nvn 0.66 -0.04 0.75\r\nvn 0.66 -0.03 0.75\r\nvn 0.95 0.1 0.28\r\nvn -0.73 -0.26 0.63\r\nvn -0.65 -0.26 0.72\r\nvn -0.81 -0.25 0.54\r\nvn -0.81 -0.23 0.54\r\nvn -0.74 -0.24 0.63\r\nvn -0.65 -0.24 0.72\r\nvn -0.97 -0.13 -0.19\r\nvn -0.66 0.04 -0.75\r\nvn -0.66 0.03 -0.75\r\nvn -0.97 -0.12 -0.19\r\nvn 0.74 0.22 -0.64\r\nvn 0.57 0.22 -0.79\r\nvn 0.87 0.21 -0.45\r\nvn 0.87 0.19 -0.45\r\nvn 0.74 0.21 -0.64\r\nvn 0.57 0.21 -0.79\r\nvn 0.96 0.1 0.28\r\nvn 0.66 -0.03 0.75\r\nvn 0.66 -0.03 0.75\r\nvn 0.96 0.09 0.28\r\nvn -0.74 -0.22 0.64\r\nvn -0.65 -0.22 0.72\r\nvn -0.81 -0.22 0.54\r\nvn -0.81 -0.2 0.55\r\nvn -0.74 -0.21 0.64\r\nvn -0.66 -0.21 0.72\r\nvn -0.98 -0.12 -0.19\r\nvn -0.66 0.03 -0.75\r\nvn -0.66 0.03 -0.75\r\nvn -0.98 -0.11 -0.19\r\nvn 0.74 0.19 -0.64\r\nvn 0.57 0.19 -0.8\r\nvn 0.87 0.18 -0.45\r\nvn 0.87 0.17 -0.46\r\nvn 0.74 0.18 -0.64\r\nvn 0.57 0.18 -0.8\r\nvn 0.96 0.08 0.28\r\nvn 0.66 -0.03 0.75\r\nvn 0.96 0.07 0.27\r\nvn -0.74 -0.19 0.64\r\nvn -0.66 -0.19 0.73\r\nvn -0.82 -0.19 0.55\r\nvn -0.82 -0.17 0.55\r\nvn -0.74 -0.18 0.64\r\nvn -0.66 -0.18 0.73\r\nvn -0.98 -0.1 -0.18\r\nvn -0.66 0.03 -0.75\r\nvn -0.98 -0.09 -0.18\r\nvn 0.75 0.16 -0.65\r\nvn 0.58 0.17 -0.8\r\nvn 0.88 0.15 -0.46\r\nvn 0.88 0.14 -0.46\r\nvn 0.75 0.15 -0.65\r\nvn 0.58 0.16 -0.8\r\nvn 0.96 0.06 0.27\r\nvn 0.66 -0.03 0.75\r\nvn 0.66 -0.03 0.75\r\nvn 0.96 0.06 0.27\r\nvn -0.75 -0.16 0.65\r\nvn -0.66 -0.17 0.73\r\nvn -0.82 -0.16 0.55\r\nvn -0.82 -0.15 0.55\r\nvn -0.75 -0.15 0.65\r\nvn -0.66 -0.15 0.73\r\nvn -0.98 -0.08 -0.18\r\nvn -0.66 0.03 -0.75\r\nvn -0.66 0.03 -0.75\r\nvn -0.98 -0.07 -0.18\r\nvn 0.75 0.14 -0.65\r\nvn 0.58 0.15 -0.8\r\nvn 0.88 0.13 -0.46\r\nvn 0.88 0.12 -0.46\r\nvn 0.75 0.13 -0.65\r\nvn 0.58 0.14 -0.8\r\nvn 0.96 0.05 0.27\r\nvn 0.66 -0.04 0.75\r\nvn 0.66 -0.04 0.75\r\nvn 0.96 0.04 0.27\r\nvn -0.75 -0.14 0.65\r\nvn -0.66 -0.14 0.73\r\nvn -0.82 -0.14 0.56\r\nvn -0.82 -0.13 0.56\r\nvn -0.75 -0.13 0.65\r\nvn -0.66 -0.14 0.73\r\nvn -0.98 -0.06 -0.18\r\nvn -0.66 0.04 -0.75\r\nvn -0.66 0.04 -0.75\r\nvn -0.98 -0.05 -0.18\r\nvn 0.75 0.13 -0.65\r\nvn 0.58 0.13 -0.8\r\nvn 0.88 0.11 -0.46\r\nvn 0.88 0.11 -0.46\r\nvn 0.75 0.12 -0.65\r\nvn 0.58 0.13 -0.8\r\nvn 0.96 0.03 0.27\r\nvn 0.66 -0.05 0.75\r\nvn 0.66 -0.05 0.75\r\nvn 0.96 0.02 0.27\r\nvn -0.75 -0.13 0.65\r\nvn -0.67 -0.13 0.74\r\nvn -0.82 -0.12 0.56\r\nvn -0.82 -0.12 0.56\r\nvn -0.75 -0.12 0.65\r\nvn -0.67 -0.13 0.74\r\nvn -0.98 -0.04 -0.18\r\nvn -0.66 0.05 -0.75\r\nvn -0.66 0.05 -0.75\r\nvn -0.98 -0.04 -0.17\r\nvn 0.75 0.12 -0.65\r\nvn 0.58 0.13 -0.8\r\nvn 0.88 0.1 -0.46\r\nvn 0.88 0.1 -0.46\r\nvn 0.75 0.12 -0.65\r\nvn 0.58 0.13 -0.8\r\nvn 0.96 0.01 0.27\r\nvn 0.66 -0.06 0.75\r\nvn 0.66 -0.07 0.75\r\nvn 0.96 0.01 0.27\r\nvn -0.75 -0.12 0.65\r\nvn -0.67 -0.13 0.74\r\nvn -0.82 -0.11 0.56\r\nvn -0.82 -0.11 0.56\r\nvn -0.75 -0.12 0.65\r\nvn -0.67 -0.13 0.74\r\nvn -0.98 -0.03 -0.17\r\nvn -0.66 0.06 -0.75\r\nvn -0.66 0.07 -0.75\r\nvn -0.98 -0.02 -0.17\r\nvn 0.75 0.12 -0.65\r\nvn 0.58 0.14 -0.8\r\nvn 0.88 0.1 -0.46\r\nvn 0.88 0.11 -0.46\r\nvn 0.75 0.13 -0.65\r\nvn 0.58 0.15 -0.8\r\nvn 0.96 0 0.27\r\nvn 0.66 -0.08 0.75\r\nvn 0.66 -0.09 0.74\r\nvn 0.96 -0.01 0.27\r\nvn -0.75 -0.12 0.65\r\nvn -0.67 -0.13 0.73\r\nvn -0.82 -0.11 0.56\r\nvn -0.82 -0.12 0.56\r\nvn -0.75 -0.13 0.65\r\nvn -0.66 -0.14 0.73\r\nvn -0.99 -0.02 -0.17\r\nvn -0.66 0.08 -0.75\r\nvn -0.66 0.09 -0.74\r\nvn -0.99 -0.01 -0.17\r\nvn 0.75 0.14 -0.65\r\nvn 0.58 0.15 -0.8\r\nvn 0.88 0.11 -0.46\r\nvn 0.88 0.11 -0.46\r\nvn 0.75 0.14 -0.65\r\nvn 0.58 0.16 -0.8\r\nvn 0.96 -0.01 0.27\r\nvn 0.66 -0.1 0.74\r\nvn 0.66 -0.11 0.74\r\nvn 0.96 -0.02 0.27\r\nvn -0.75 -0.14 0.65\r\nvn -0.66 -0.15 0.73\r\nvn -0.82 -0.12 0.56\r\nvn -0.82 -0.13 0.56\r\nvn -0.75 -0.14 0.65\r\nvn -0.66 -0.15 0.73\r\nvn -0.99 -0.01 -0.17\r\nvn -0.66 0.1 -0.74\r\nvn -0.66 0.11 -0.74\r\nvn -0.99 -0 -0.17\r\nvn 0.75 0.14 -0.65\r\nvn 0.58 0.17 -0.8\r\nvn 0.88 0.11 -0.46\r\nvn 0.88 0.12 -0.46\r\nvn 0.75 0.15 -0.65\r\nvn 0.58 0.17 -0.8\r\nvn 0.96 -0.02 0.26\r\nvn 0.66 -0.12 0.74\r\nvn 0.66 -0.12 0.74\r\nvn 0.96 -0.02 0.26\r\nvn -0.75 -0.14 0.65\r\nvn -0.66 -0.16 0.73\r\nvn -0.82 -0.13 0.56\r\nvn -0.82 -0.13 0.55\r\nvn -0.75 -0.15 0.65\r\nvn -0.66 -0.16 0.73\r\nvn -0.99 0 -0.17\r\nvn -0.66 0.12 -0.74\r\nvn -0.66 0.12 -0.74\r\nvn -0.99 0 -0.17\r\nvn 0.75 0.15 -0.65\r\nvn 0.58 0.18 -0.8\r\nvn 0.88 0.12 -0.46\r\nvn 0.88 0.12 -0.46\r\nvn 0.75 0.16 -0.64\r\nvn 0.58 0.19 -0.8\r\nvn 0.96 -0.03 0.26\r\nvn 0.66 -0.13 0.74\r\nvn 0.66 -0.14 0.74\r\nvn 0.96 -0.03 0.26\r\nvn -0.75 -0.15 0.65\r\nvn -0.66 -0.17 0.73\r\nvn -0.82 -0.14 0.55\r\nvn -0.82 -0.14 0.55\r\nvn -0.75 -0.16 0.64\r\nvn -0.66 -0.17 0.73\r\nvn -0.99 0.01 -0.17\r\nvn -0.66 0.13 -0.74\r\nvn -0.66 0.14 -0.74\r\nvn -0.99 0.01 -0.17\r\nvn 0.75 0.16 -0.64\r\nvn 0.58 0.19 -0.79\r\nvn 0.88 0.13 -0.46\r\nvn 0.88 0.13 -0.46\r\nvn 0.75 0.17 -0.64\r\nvn 0.58 0.2 -0.79\r\nvn 0.96 -0.03 0.26\r\nvn 0.66 -0.14 0.73\r\nvn 0.66 -0.15 0.73\r\nvn 0.96 -0.03 0.26\r\nvn -0.75 -0.16 0.64\r\nvn -0.66 -0.18 0.73\r\nvn -0.82 -0.15 0.55\r\nvn -0.82 -0.15 0.55\r\nvn -0.75 -0.17 0.64\r\nvn -0.66 -0.19 0.72\r\nvn -0.99 0.01 -0.17\r\nvn -0.66 0.14 -0.73\r\nvn -0.66 0.15 -0.73\r\nvn -0.99 0.01 -0.17\r\nvn 0.75 0.18 -0.64\r\nvn 0.58 0.21 -0.79\r\nvn 0.88 0.14 -0.46\r\nvn 0.88 0.15 -0.45\r\nvn 0.75 0.19 -0.64\r\nvn 0.58 0.22 -0.79\r\nvn 0.96 -0.03 0.26\r\nvn 0.66 -0.15 0.73\r\nvn 0.66 -0.16 0.73\r\nvn 0.96 -0.03 0.26\r\nvn -0.75 -0.18 0.64\r\nvn -0.66 -0.19 0.72\r\nvn -0.82 -0.16 0.55\r\nvn -0.82 -0.17 0.55\r\nvn -0.75 -0.19 0.64\r\nvn -0.66 -0.21 0.72\r\nvn -0.99 0.01 -0.17\r\nvn -0.66 0.15 -0.73\r\nvn -0.66 0.16 -0.73\r\nvn -0.99 0.01 -0.16\r\nvn 0.75 0.21 -0.63\r\nvn 0.58 0.24 -0.78\r\nvn 0.88 0.16 -0.45\r\nvn 0.88 0.18 -0.44\r\nvn 0.75 0.23 -0.63\r\nvn 0.57 0.27 -0.77\r\nvn 0.96 -0.03 0.27\r\nvn 0.66 -0.17 0.73\r\nvn 0.67 -0.19 0.72\r\nvn 0.96 -0.03 0.27\r\nvn -0.75 -0.21 0.63\r\nvn -0.66 -0.23 0.71\r\nvn -0.82 -0.19 0.54\r\nvn -0.82 -0.21 0.54\r\nvn -0.74 -0.23 0.63\r\nvn -0.66 -0.25 0.71\r\nvn -0.99 0 -0.16\r\nvn -0.66 0.17 -0.73\r\nvn -0.67 0.19 -0.72\r\nvn -0.99 -0 -0.16\r\nvn 0.74 0.26 -0.62\r\nvn 0.57 0.3 -0.76\r\nvn 0.88 0.21 -0.44\r\nvn 0.87 0.24 -0.42\r\nvn 0.74 0.3 -0.6\r\nvn 0.57 0.34 -0.75\r\nvn 0.96 -0.03 0.27\r\nvn 0.67 -0.21 0.72\r\nvn 0.67 -0.23 0.71\r\nvn 0.96 -0.03 0.27\r\nvn -0.74 -0.26 0.62\r\nvn -0.66 -0.29 0.7\r\nvn -0.82 -0.24 0.53\r\nvn -0.81 -0.27 0.51\r\nvn -0.74 -0.3 0.6\r\nvn -0.66 -0.33 0.68\r\nvn -0.99 -0 -0.16\r\nvn -0.67 0.21 -0.72\r\nvn -0.67 0.23 -0.71\r\nvn -0.99 -0.01 -0.16\r\nvn 0.74 0.34 -0.58\r\nvn 0.57 0.39 -0.73\r\nvn 0.87 0.27 -0.41\r\nvn 0.87 0.3 -0.4\r\nvn 0.73 0.38 -0.57\r\nvn 0.56 0.43 -0.71\r\nvn 0.96 -0.03 0.27\r\nvn 0.67 -0.26 0.69\r\nvn 0.67 -0.28 0.68\r\nvn 0.96 -0.03 0.27\r\nvn -0.74 -0.34 0.58\r\nvn -0.65 -0.37 0.66\r\nvn -0.81 -0.31 0.5\r\nvn -0.81 -0.34 0.48\r\nvn -0.73 -0.38 0.57\r\nvn -0.65 -0.41 0.64\r\nvn -0.99 -0.02 -0.17\r\nvn -0.67 0.26 -0.7\r\nvn -0.67 0.28 -0.68\r\nvn -0.99 -0.02 -0.17\r\nvn 0.73 0.41 -0.55\r\nvn 0.56 0.46 -0.69\r\nvn 0.87 0.33 -0.38\r\nvn 0.86 0.35 -0.37\r\nvn 0.73 0.43 -0.53\r\nvn 0.55 0.5 -0.67\r\nvn 0.96 -0.03 0.27\r\nvn 0.67 -0.3 0.67\r\nvn 0.68 -0.32 0.66\r\nvn 0.96 -0.03 0.27\r\nvn -0.73 -0.41 0.55\r\nvn -0.64 -0.44 0.63\r\nvn -0.8 -0.37 0.47\r\nvn -0.8 -0.39 0.45\r\nvn -0.73 -0.43 0.53\r\nvn -0.64 -0.47 0.61\r\nvn -0.98 -0.03 -0.17\r\nvn -0.67 0.3 -0.67\r\nvn -0.68 0.32 -0.66\r\nvn -0.98 -0.03 -0.17\r\nvn 0.72 0.46 -0.51\r\nvn 0.55 0.53 -0.65\r\nvn 0.86 0.37 -0.35\r\nvn 0.86 0.39 -0.34\r\nvn 0.72 0.49 -0.5\r\nvn 0.54 0.56 -0.63\r\nvn 0.96 -0.04 0.27\r\nvn 0.68 -0.34 0.65\r\nvn 0.68 -0.36 0.63\r\nvn 0.96 -0.04 0.26\r\nvn -0.72 -0.46 0.52\r\nvn -0.64 -0.5 0.59\r\nvn -0.8 -0.42 0.43\r\nvn -0.79 -0.44 0.42\r\nvn -0.72 -0.49 0.5\r\nvn -0.63 -0.53 0.57\r\nvn -0.98 -0.03 -0.18\r\nvn -0.68 0.34 -0.65\r\nvn -0.68 0.36 -0.63\r\nvn -0.98 -0.03 -0.18\r\nvn 0.72 0.51 -0.48\r\nvn 0.54 0.58 -0.61\r\nvn 0.85 0.41 -0.33\r\nvn 0.85 0.42 -0.32\r\nvn 0.71 0.52 -0.47\r\nvn 0.54 0.6 -0.6\r\nvn 0.96 -0.04 0.26\r\nvn 0.69 -0.38 0.62\r\nvn 0.69 -0.39 0.61\r\nvn 0.97 -0.05 0.26\r\nvn -0.71 -0.51 0.48\r\nvn -0.63 -0.55 0.55\r\nvn -0.79 -0.46 0.41\r\nvn -0.79 -0.47 0.39\r\nvn -0.71 -0.52 0.47\r\nvn -0.63 -0.56 0.54\r\nvn -0.98 -0.03 -0.18\r\nvn -0.69 0.38 -0.62\r\nvn -0.69 0.39 -0.61\r\nvn -0.98 -0.03 -0.18\r\nvn 0.71 0.53 -0.46\r\nvn 0.53 0.61 -0.58\r\nvn 0.85 0.43 -0.31\r\nvn 0.85 0.43 -0.3\r\nvn 0.71 0.54 -0.45\r\nvn 0.53 0.62 -0.57\r\nvn 0.97 -0.05 0.25\r\nvn 0.69 -0.41 0.6\r\nvn 0.69 -0.42 0.59\r\nvn 0.97 -0.05 0.25\r\nvn -0.71 -0.53 0.46\r\nvn -0.62 -0.58 0.53\r\nvn -0.79 -0.48 0.39\r\nvn -0.79 -0.49 0.38\r\nvn -0.71 -0.54 0.45\r\nvn -0.62 -0.59 0.52\r\nvn -0.98 -0.02 -0.17\r\nvn -0.69 0.41 -0.6\r\nvn -0.69 0.42 -0.59\r\nvn -0.98 -0.02 -0.17\r\nvn 0.71 0.55 -0.44\r\nvn 0.53 0.63 -0.56\r\nvn 0.85 0.44 -0.3\r\nvn 0.85 0.45 -0.29\r\nvn 0.71 0.56 -0.44\r\nvn 0.53 0.64 -0.55\r\nvn 0.97 -0.06 0.24\r\nvn 0.69 -0.42 0.58\r\nvn 0.7 -0.43 0.57\r\nvn 0.97 -0.06 0.24\r\nvn -0.71 -0.55 0.44\r\nvn -0.62 -0.6 0.51\r\nvn -0.78 -0.5 0.37\r\nvn -0.78 -0.5 0.37\r\nvn -0.71 -0.56 0.44\r\nvn -0.62 -0.61 0.5\r\nvn -0.99 -0.02 -0.17\r\nvn -0.69 0.42 -0.58\r\nvn -0.7 0.43 -0.57\r\nvn -0.99 -0.02 -0.17\r\nvn 0.7 0.57 -0.43\r\nvn 0.53 0.65 -0.54\r\nvn 0.84 0.45 -0.29\r\nvn 0.84 0.46 -0.28\r\nvn 0.7 0.57 -0.42\r\nvn 0.53 0.66 -0.53\r\nvn 0.97 -0.06 0.24\r\nvn 0.7 -0.44 0.56\r\nvn 0.7 -0.45 0.55\r\nvn 0.97 -0.06 0.24\r\nvn -0.7 -0.57 0.43\r\nvn -0.62 -0.61 0.49\r\nvn -0.78 -0.51 0.36\r\nvn -0.78 -0.52 0.35\r\nvn -0.7 -0.57 0.42\r\nvn -0.61 -0.62 0.48\r\nvn -0.99 -0.01 -0.17\r\nvn -0.7 0.44 -0.56\r\nvn -0.7 0.45 -0.55\r\nvn -0.99 -0.01 -0.17\r\nvn 0.7 0.58 -0.41\r\nvn 0.52 0.67 -0.52\r\nvn 0.84 0.46 -0.28\r\nvn 0.84 0.47 -0.27\r\nvn 0.7 0.59 -0.4\r\nvn 0.52 0.68 -0.51\r\nvn 0.97 -0.07 0.23\r\nvn 0.7 -0.46 0.54\r\nvn 0.7 -0.47 0.53\r\nvn 0.97 -0.07 0.23\r\nvn -0.7 -0.58 0.41\r\nvn -0.61 -0.63 0.47\r\nvn -0.78 -0.52 0.34\r\nvn -0.78 -0.53 0.34\r\nvn -0.7 -0.59 0.4\r\nvn -0.61 -0.64 0.46\r\nvn -0.99 -0.01 -0.16\r\nvn -0.7 0.46 -0.54\r\nvn -0.7 0.47 -0.53\r\nvn -0.99 -0.01 -0.16\r\nvn 0.7 0.6 -0.39\r\nvn 0.52 0.69 -0.5\r\nvn 0.84 0.47 -0.26\r\nvn 0.84 0.48 -0.25\r\nvn 0.7 0.61 -0.38\r\nvn 0.52 0.7 -0.49\r\nvn 0.97 -0.07 0.22\r\nvn 0.71 -0.48 0.52\r\nvn 0.71 -0.49 0.51\r\nvn 0.97 -0.08 0.22\r\nvn -0.7 -0.6 0.39\r\nvn -0.61 -0.65 0.45\r\nvn -0.78 -0.54 0.33\r\nvn -0.77 -0.55 0.32\r\nvn -0.7 -0.61 0.38\r\nvn -0.61 -0.66 0.44\r\nvn -0.99 -0.01 -0.16\r\nvn -0.71 0.48 -0.52\r\nvn -0.71 0.49 -0.51\r\nvn -0.99 -0 -0.16\r\nvn 0.7 0.6 -0.4\r\nvn 0.51 0.66 -0.56\r\nvn 0.86 0.41 -0.31\r\nvn 0.8 0.39 -0.45\r\nvn 0.7 0.38 -0.61\r\nvn 0.59 0.53 -0.6\r\nvn 0.97 -0.21 0.09\r\nvn 0.72 -0.62 0.32\r\nvn 0.54 -0.79 -0.29\r\nvn 0.83 -0.36 -0.43\r\nvn -0.69 -0.63 0.36\r\nvn -0.59 -0.71 0.39\r\nvn -0.77 -0.58 0.26\r\nvn -0.73 -0.64 0.24\r\nvn -0.66 -0.72 0.22\r\nvn -0.62 -0.72 0.32\r\nvn -0.95 -0.07 -0.31\r\nvn -0.67 0.35 -0.66\r\nvn -0.35 -0.16 -0.92\r\nvn -0.69 -0.47 -0.55\r\nvn -0.28 -0.85 -0.45\r\nvn 0.06 -1 -0.02\r\nvn 0.13 -0.61 -0.78\r\nvn 0.55 -0.79 -0.29\r\nvn -0.65 -0.11 0.75\r\nvn -0.2 0.1 0.97\r\nvn -0.21 0.22 0.95\r\nvn -0.34 -0.62 0.71\r\nvn 0.08 -0.33 0.94\r\nvn 0.69 -0.03 -0.72\r\nvn 0.72 -0.17 -0.67\r\nvn -0.69 0.02 0.72\r\nvn -0.21 0.22 0.95\r\nvn -0.21 0.21 0.95\r\nvn 0.69 -0.02 -0.72\r\nvn -0.2 0.21 0.96\r\nvn -0.2 0.21 0.96\r\nvn -0.2 0.2 0.96\r\nvn -0.2 0.2 0.96\r\nvn 0.69 -0.01 -0.72\r\nvn -0.2 0.2 0.96\r\nvn -0.2 0.19 0.96\r\nvn -0.2 0.19 0.96\r\nvn -0.2 0.19 0.96\r\nvn -0.69 0 0.73\r\nvn -0.2 0.18 0.96\r\nvn -0.2 0.18 0.96\r\nvn 0.69 -0 -0.73\r\nvn -0.68 -0.01 0.73\r\nvn -0.2 0.17 0.97\r\nvn -0.19 0.16 0.97\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0.01 -0.73\r\nvn -0.68 -0.01 0.73\r\nvn -0.19 0.14 0.97\r\nvn -0.19 0.13 0.97\r\nvn -0.68 -0.03 0.73\r\nvn -0.18 0.11 0.98\r\nvn -0.18 0.1 0.98\r\nvn 0.68 0.03 -0.73\r\nvn -0.68 -0.03 0.73\r\nvn -0.18 0.08 0.98\r\nvn -0.18 0.07 0.98\r\nvn 0.68 0.03 -0.73\r\nvn -0.18 0.06 0.98\r\nvn -0.17 0.05 0.98\r\nvn 0.68 0.03 -0.73\r\nvn -0.17 0.04 0.98\r\nvn -0.17 0.03 0.98\r\nvn -0.17 0.03 0.98\r\nvn -0.17 0.03 0.99\r\nvn 0.68 0.03 -0.73\r\nvn -0.17 0.03 0.99\r\nvn -0.17 0.03 0.99\r\nvn -0.68 -0.03 0.73\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.02 0.99\r\nvn -0.17 0.03 0.99\r\nvn -0.17 0.03 0.99\r\nvn -0.17 0.03 0.99\r\nvn -0.17 0.04 0.98\r\nvn -0.17 0.04 0.98\r\nvn -0.17 0.05 0.98\r\nvn -0.17 0.06 0.98\r\nvn -0.17 0.07 0.98\r\nvn -0.17 0.08 0.98\r\nvn -0.17 0.09 0.98\r\nvn -0.18 0.11 0.98\r\nvn -0.18 0.12 0.98\r\nvn -0.18 0.14 0.97\r\nvn -0.18 0.15 0.97\r\nvn -0.19 0.17 0.97\r\nvn -0.19 0.19 0.96\r\nvn -0.19 0.21 0.96\r\nvn -0.2 0.23 0.95\r\nvn -0.2 0.25 0.95\r\nvn -0.2 0.27 0.94\r\nvn -0.21 0.29 0.93\r\nvn 0.7 -0.09 -0.71\r\nvn 0.7 -0.1 -0.71\r\nvn -0.7 0.12 0.7\r\nvn -0.21 0.32 0.92\r\nvn -0.22 0.34 0.91\r\nvn 0.7 -0.12 -0.7\r\nvn -0.72 0.17 0.68\r\nvn -0.23 0.37 0.9\r\nvn -0.23 0.4 0.89\r\nvn 0.72 -0.17 -0.68\r\nvn 0.72 -0.17 -0.68\r\nvn -0.73 0.22 0.65\r\nvn -0.72 0.19 0.66\r\nvn -0.24 0.43 0.87\r\nvn -0.25 0.47 0.85\r\nvn 0.73 -0.22 -0.65\r\nvn 0.72 -0.19 -0.66\r\nvn 0.73 -0.22 -0.65\r\nvn -0.75 0.34 0.57\r\nvn -0.74 0.27 0.62\r\nvn -0.27 0.51 0.81\r\nvn -0.28 0.58 0.76\r\nvn 0.74 -0.26 -0.62\r\nvn 0.75 -0.33 -0.57\r\nvn 0.74 -0.26 -0.62\r\nvn 0.75 -0.33 -0.57\r\nvn 0.76 -0.42 -0.49\r\nvn 0.76 -0.42 -0.49\r\nvn 0.74 -0.63 -0.23\r\nvn 0.71 -0.69 -0.12\r\nvn 0.74 -0.63 -0.23\r\nvn 0.71 -0.69 -0.12\r\nvn -0.68 0.74 0.01\r\nvn -0.69 0.72 0.05\r\nvn -0.22 0.93 0.3\r\nvn -0.2 0.95 0.25\r\nvn -0.72 0.69 0.13\r\nvn -0.25 0.89 0.37\r\nvn 0.69 -0.72 -0.05\r\nvn 0.67 -0.74 -0.01\r\nvn 0.69 -0.72 -0.05\r\nvn 0.67 -0.74 -0.01\r\nvn -0.66 0.75 -0.03\r\nvn -0.67 0.75 -0.02\r\nvn -0.19 0.96 0.22\r\nvn -0.18 0.96 0.2\r\nvn 0.67 -0.75 0.02\r\nvn 0.66 -0.75 0.03\r\nvn 0.67 -0.75 0.02\r\nvn -0.65 0.75 -0.05\r\nvn -0.66 0.75 -0.05\r\nvn -0.17 0.97 0.18\r\nvn -0.17 0.97 0.16\r\nvn 0.66 -0.75 0.05\r\nvn -0.16 0.98 0.15\r\nvn -0.16 0.98 0.14\r\nvn 0.65 -0.76 0.06\r\nvn -0.16 0.98 0.13\r\nvn -0.15 0.98 0.12\r\nvn -0.15 0.98 0.11\r\nvn -0.15 0.98 0.1\r\nvn 0.65 -0.76 0.06\r\nvn -0.65 0.76 -0.05\r\nvn -0.15 0.99 0.09\r\nvn -0.14 0.99 0.08\r\nvn 0.65 -0.76 0.05\r\nvn -0.14 0.99 0.08\r\nvn -0.14 0.99 0.07\r\nvn 0.65 -0.75 0.05\r\nvn -0.14 0.99 0.06\r\nvn -0.14 0.99 0.06\r\nvn 0.66 -0.75 0.04\r\nvn -0.66 0.75 -0.04\r\nvn -0.14 0.99 0.05\r\nvn -0.14 0.99 0.05\r\nvn 0.66 -0.75 0.04\r\nvn -0.14 0.99 0.04\r\nvn -0.14 0.99 0.04\r\nvn -0.14 0.99 0.03\r\nvn -0.14 0.99 0.03\r\nvn -0.14 0.99 0.02\r\nvn -0.14 0.99 0.02\r\nvn -0.14 0.99 0.01\r\nvn -0.14 0.99 0.01\r\nvn -0.66 0.75 -0.01\r\nvn -0.66 0.75 -0.01\r\nvn -0.14 0.99 0\r\nvn -0.14 0.99 -0\r\nvn 0.66 -0.75 0.01\r\nvn -0.14 0.99 -0.01\r\nvn -0.14 0.99 -0.01\r\nvn -0.14 0.99 -0.02\r\nvn -0.14 0.99 -0.02\r\nvn -0.14 0.99 -0.03\r\nvn -0.14 0.99 -0.03\r\nvn -0.66 0.75 0.01\r\nvn -0.14 0.99 -0.04\r\nvn -0.14 0.99 -0.04\r\nvn 0.66 -0.75 -0.01\r\nvn -0.14 0.99 -0.05\r\nvn -0.14 0.99 -0.05\r\nvn -0.14 0.99 -0.06\r\nvn -0.14 0.99 -0.07\r\nvn -0.14 0.99 -0.07\r\nvn -0.14 0.99 -0.08\r\nvn 0.66 -0.75 -0.03\r\nvn -0.14 0.99 -0.09\r\nvn -0.14 0.99 -0.09\r\nvn 0.65 -0.76 -0.03\r\nvn -0.14 0.98 -0.1\r\nvn -0.15 0.98 -0.11\r\nvn 0.65 -0.76 -0.04\r\nvn -0.15 0.98 -0.12\r\nvn -0.15 0.98 -0.13\r\nvn -0.15 0.98 -0.14\r\nvn -0.16 0.98 -0.15\r\nvn -0.65 0.76 0.04\r\nvn -0.16 0.97 -0.17\r\nvn -0.17 0.97 -0.18\r\nvn 0.65 -0.76 -0.04\r\nvn -0.66 0.75 0.02\r\nvn -0.17 0.97 -0.19\r\nvn -0.18 0.96 -0.21\r\nvn 0.66 -0.75 -0.03\r\nvn 0.66 -0.75 -0.02\r\nvn 0.66 -0.75 -0.03\r\nvn -0.7 0.72 -0\r\nvn -0.67 0.75 0\r\nvn -0.18 0.95 -0.25\r\nvn -0.23 0.93 -0.29\r\nvn 0.66 -0.75 -0.02\r\nvn 0.67 -0.75 -0.03\r\nvn 0.66 -0.75 -0.01\r\nvn 0.67 -0.74 -0.02\r\nvn -0.71 0.68 0.17\r\nvn -0.28 0.94 -0.2\r\nvn 0.71 -0.7 0.01\r\nvn 0.77 -0.6 0.22\r\nvn 0.71 -0.7 0.02\r\nvn 0.77 -0.6 0.23\r\nvn 0.24 -0.93 0.27\r\nvn 0.55 -0.62 0.56\r\nvn 0 0.47 0.88\r\nvn -0 -0.47 -0.88\r\nvn 0.02 0.1 -1\r\nvn 0.03 0.1 -0.99\r\nvn 0.03 0.75 -0.66\r\nvn 0.05 0.1 -0.99\r\nvn 0.06 0.09 -0.99\r\nvn 0.07 0.09 -0.99\r\nvn 0.09 0.09 -0.99\r\nvn 0.1 0.09 -0.99\r\nvn 0.11 0.09 -0.99\r\nvn 0.12 0.08 -0.99\r\nvn 0.13 0.08 -0.99\r\nvn 0.14 0.08 -0.99\r\nvn 0.16 0.07 -0.99\r\nvn 0.17 0.07 -0.98\r\nvn 0.18 0.07 -0.98\r\nvn 0 0.47 0.88\r\nvn -0 -0.47 -0.88\r\nvn 0.19 0.06 -0.98\r\nvn 0.2 0.06 -0.98\r\nvn -0 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn -0 0.47 0.88\r\nvn -0.01 0.47 0.88\r\nvn 0.01 -0.47 -0.88\r\nvn 0.21 0.06 -0.98\r\nvn 0.23 0.05 -0.97\r\nvn -0.13 0.52 0.84\r\nvn -0.5 0.64 0.58\r\nvn -0.14 0.52 0.84\r\nvn 0.02 -0.48 -0.88\r\nvn 0.01 -0.47 -0.88\r\nvn 0.24 0.05 -0.97\r\nvn 0.26 0.05 -0.96\r\nvn -0.47 -0.09 -0.88\r\nvn -0.09 -0.42 -0.9\r\nvn 0.22 0.11 -0.97\r\nvn 0.03 0.38 -0.93\r\nvn 0.76 -0.46 0.46\r\nvn 0.74 -0.36 0.57\r\nvn 0.76 -0.46 0.45\r\nvn 0.74 -0.36 0.57\r\nvn -0.73 0.33 -0.6\r\nvn -0.71 0.28 -0.64\r\nvn -0.2 0.57 -0.8\r\nvn -0.25 0.58 -0.78\r\nvn 0.72 -0.28 0.63\r\nvn 0.71 -0.22 0.67\r\nvn 0.72 -0.28 0.63\r\nvn 0.71 -0.22 0.67\r\nvn -0.71 0.23 -0.67\r\nvn -0.72 0.26 -0.64\r\nvn -0.24 0.52 -0.82\r\nvn -0.23 0.47 -0.85\r\nvn 0.69 -0.17 0.7\r\nvn 0.7 -0.19 0.69\r\nvn -0.69 0.17 -0.7\r\nvn -0.7 0.19 -0.69\r\nvn -0.21 0.43 -0.87\r\nvn -0.2 0.4 -0.89\r\nvn 0.68 -0.13 0.72\r\nvn 0.69 -0.14 0.71\r\nvn -0.69 0.14 -0.71\r\nvn -0.2 0.37 -0.91\r\nvn -0.19 0.34 -0.92\r\nvn 0.68 -0.11 0.73\r\nvn -0.67 0.1 -0.73\r\nvn -0.18 0.32 -0.93\r\nvn -0.18 0.3 -0.94\r\nvn 0.67 -0.08 0.74\r\nvn -0.17 0.27 -0.95\r\nvn -0.17 0.25 -0.95\r\nvn -0.16 0.24 -0.96\r\nvn -0.16 0.22 -0.96\r\nvn 0.66 -0.05 0.75\r\nvn 0.66 -0.04 0.75\r\nvn -0.66 0.05 -0.75\r\nvn -0.15 0.2 -0.97\r\nvn -0.15 0.19 -0.97\r\nvn -0.66 0.04 -0.75\r\nvn -0.15 0.17 -0.97\r\nvn -0.15 0.16 -0.98\r\nvn -0.14 0.15 -0.98\r\nvn -0.14 0.14 -0.98\r\nvn -0.14 0.13 -0.98\r\nvn -0.14 0.12 -0.98\r\nvn -0.14 0.12 -0.98\r\nvn -0.14 0.11 -0.98\r\nvn -0.14 0.11 -0.98\r\nvn -0.14 0.11 -0.98\r\nvn 0.66 -0.05 0.75\r\nvn -0.66 0.05 -0.75\r\nvn -0.14 0.11 -0.98\r\nvn -0.14 0.11 -0.98\r\nvn -0.14 0.12 -0.98\r\nvn -0.14 0.13 -0.98\r\nvn -0.14 0.14 -0.98\r\nvn -0.14 0.15 -0.98\r\nvn -0.14 0.16 -0.98\r\nvn -0.14 0.17 -0.98\r\nvn -0.66 0.12 -0.74\r\nvn -0.14 0.18 -0.97\r\nvn -0.14 0.19 -0.97\r\nvn 0.66 -0.13 0.74\r\nvn -0.14 0.19 -0.97\r\nvn -0.15 0.2 -0.97\r\nvn -0.15 0.21 -0.97\r\nvn -0.15 0.22 -0.97\r\nvn 0.66 -0.16 0.73\r\nvn 0.66 -0.15 0.73\r\nvn 0.66 -0.16 0.73\r\nvn -0.66 0.16 -0.73\r\nvn -0.66 0.15 -0.73\r\nvn -0.15 0.22 -0.96\r\nvn -0.15 0.24 -0.96\r\nvn 0.66 -0.17 0.73\r\nvn 0.67 -0.19 0.72\r\nvn 0.67 -0.19 0.72\r\nvn -0.67 0.19 -0.72\r\nvn -0.66 0.17 -0.73\r\nvn -0.15 0.26 -0.95\r\nvn -0.15 0.28 -0.95\r\nvn 0.67 -0.21 0.72\r\nvn 0.67 -0.23 0.71\r\nvn 0.67 -0.21 0.72\r\nvn 0.67 -0.23 0.71\r\nvn -0.67 0.23 -0.71\r\nvn -0.67 0.21 -0.72\r\nvn -0.15 0.32 -0.94\r\nvn -0.16 0.36 -0.92\r\nvn 0.67 -0.26 0.69\r\nvn 0.67 -0.28 0.68\r\nvn -0.67 0.28 -0.68\r\nvn -0.67 0.26 -0.7\r\nvn -0.16 0.4 -0.9\r\nvn -0.16 0.44 -0.88\r\nvn 0.67 -0.3 0.67\r\nvn 0.68 -0.32 0.66\r\nvn -0.68 0.32 -0.66\r\nvn -0.67 0.3 -0.67\r\nvn -0.17 0.47 -0.87\r\nvn -0.17 0.51 -0.85\r\nvn 0.68 -0.36 0.63\r\nvn 0.68 -0.34 0.65\r\nvn 0.68 -0.36 0.63\r\nvn -0.68 0.36 -0.63\r\nvn -0.68 0.34 -0.65\r\nvn -0.18 0.54 -0.82\r\nvn -0.18 0.57 -0.8\r\nvn 0.69 -0.38 0.62\r\nvn 0.69 -0.39 0.61\r\nvn 0.69 -0.38 0.62\r\nvn 0.69 -0.39 0.61\r\nvn -0.69 0.39 -0.61\r\nvn -0.69 0.38 -0.62\r\nvn -0.18 0.59 -0.78\r\nvn -0.19 0.61 -0.77\r\nvn 0.69 -0.41 0.6\r\nvn 0.69 -0.42 0.59\r\nvn 0.69 -0.41 0.6\r\nvn -0.69 0.42 -0.59\r\nvn -0.69 0.41 -0.6\r\nvn -0.19 0.63 -0.75\r\nvn -0.19 0.65 -0.74\r\nvn -0.19 0.66 -0.73\r\nvn -0.2 0.67 -0.72\r\nvn -0.2 0.68 -0.71\r\nvn -0.2 0.69 -0.69\r\nvn -0.2 0.7 -0.68\r\nvn -0.21 0.72 -0.67\r\nvn 0.71 -0.49 0.51\r\nvn -0.71 0.49 -0.51\r\nvn -0.21 0.73 -0.65\r\nvn -0.21 0.74 -0.63\r\nvn 0.72 -0.62 0.32\r\nvn 0.55 -0.79 -0.29\r\nvn 0.72 -0.62 0.32\r\nvn -0.35 -0.16 -0.92\r\nvn -0.67 0.35 -0.66\r\nvn -0.22 0.62 -0.75\r\nvn 0.05 0.19 -0.98\r\nvn 0.43 -0.25 -0.87\r\nvn -0.69 -0 0.73\r\nvn -0.73 0.22 0.65\r\nvn -0.74 0.26 0.62\r\nvn -0.76 0.33 0.56\r\nvn -0.69 0.72 0.05\r\nvn -0.68 0.74 0.01\r\nvn -0.72 0.69 0.13\r\nvn -0.67 0.75 -0.02\r\nvn -0.66 0.75 -0.02\r\nvn -0.65 0.76 0.03\r\nvn -0.67 0.74 -0\r\nvn -0.7 0.71 -0.02\r\nvn -0.73 0.67 0.14\r\nvn 0.01 -0.47 -0.88\r\nvn 0.02 -0.48 -0.88\r\nvn -0.09 -0.42 -0.9\r\nvn -0.47 -0.09 -0.88\r\nvn -0.71 0.28 -0.64\r\nvn -0.73 0.33 -0.6\r\nvn -0.72 0.26 -0.64\r\nvn -0.71 0.23 -0.67\r\nvn -0.66 0.03 -0.75\r\nvn -0.66 0.04 -0.75\r\nvn -0.66 0.05 -0.75\r\nvn -0.66 0.15 -0.73\r\nvn -0.66 0.17 -0.73\r\nvn -0.67 0.19 -0.72\r\nvn -0.67 0.21 -0.72\r\nvn -0.67 0.23 -0.71\r\nvn -0.67 0.26 -0.7\r\nvn -0.68 0.34 -0.65\r\nvn -0.68 0.36 -0.63\r\nvn -0.69 0.38 -0.62\r\nvn -0.67 0.35 -0.66\r\nvn -0.35 -0.16 -0.92\r\nvn 0.17 -0.35 -0.92\r\nvn 0.11 -0.25 -0.96\r\nvn 0.11 -0.25 -0.96\r\nvn 0.11 -0.24 -0.96\r\nvn 0.11 -0.24 -0.97\r\nvn 0.11 -0.23 -0.97\r\nvn 0.11 -0.23 -0.97\r\nvn 0.11 -0.23 -0.97\r\nvn 0.11 -0.22 -0.97\r\nvn 0.11 -0.22 -0.97\r\nvn 0.11 -0.22 -0.97\r\nvn 0.1 -0.22 -0.97\r\nvn 0.1 -0.21 -0.97\r\nvn 0.1 -0.2 -0.97\r\nvn 0.1 -0.2 -0.98\r\nvn 0.1 -0.18 -0.98\r\nvn 0.09 -0.17 -0.98\r\nvn 0.09 -0.15 -0.98\r\nvn 0.08 -0.13 -0.99\r\nvn 0.08 -0.12 -0.99\r\nvn 0.08 -0.1 -0.99\r\nvn 0.07 -0.09 -0.99\r\nvn 0.07 -0.07 -0.99\r\nvn 0.07 -0.06 -1\r\nvn 0.07 -0.05 -1\r\nvn 0.07 -0.05 -1\r\nvn 0.07 -0.04 -1\r\nvn 0.07 -0.04 -1\r\nvn 0.07 -0.04 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.03 -1\r\nvn 0.07 -0.04 -1\r\nvn 0.07 -0.04 -1\r\nvn 0.07 -0.05 -1\r\nvn 0.07 -0.05 -1\r\nvn 0.07 -0.06 -1\r\nvn 0.08 -0.07 -0.99\r\nvn 0.08 -0.08 -0.99\r\nvn 0.08 -0.09 -0.99\r\nvn 0.08 -0.11 -0.99\r\nvn 0.08 -0.12 -0.99\r\nvn 0.08 -0.14 -0.99\r\nvn 0.09 -0.15 -0.98\r\nvn 0.09 -0.17 -0.98\r\nvn 0.09 -0.19 -0.98\r\nvn 0.1 -0.21 -0.97\r\nvn 0.1 -0.23 -0.97\r\nvn 0.1 -0.25 -0.96\r\nvn 0.11 -0.27 -0.96\r\nvn 0.11 -0.29 -0.95\r\nvn 0.12 -0.32 -0.94\r\nvn 0.12 -0.34 -0.93\r\nvn 0.13 -0.37 -0.92\r\nvn 0.14 -0.39 -0.91\r\nvn 0.14 -0.42 -0.89\r\nvn 0.15 -0.46 -0.88\r\nvn 0.16 -0.49 -0.86\r\nvn 0.17 -0.54 -0.82\r\nvn 0.17 -0.6 -0.78\r\nvn 0.18 -0.69 -0.71\r\nvn 0.17 -0.78 -0.61\r\nvn 0.16 -0.85 -0.5\r\nvn 0.14 -0.9 -0.4\r\nvn 0.12 -0.93 -0.34\r\nvn 0.11 -0.95 -0.29\r\nvn 0.1 -0.96 -0.26\r\nvn 0.09 -0.97 -0.23\r\nvn 0.08 -0.97 -0.21\r\nvn 0.08 -0.98 -0.19\r\nvn 0.07 -0.98 -0.18\r\nvn 0.07 -0.98 -0.16\r\nvn 0.07 -0.99 -0.15\r\nvn 0.06 -0.99 -0.14\r\nvn 0.06 -0.99 -0.13\r\nvn 0.06 -0.99 -0.12\r\nvn 0.06 -0.99 -0.11\r\nvn 0.05 -0.99 -0.1\r\nvn 0.05 -0.99 -0.09\r\nvn 0.05 -0.99 -0.09\r\nvn 0.05 -1 -0.08\r\nvn 0.05 -1 -0.07\r\nvn 0.05 -1 -0.06\r\nvn 0.05 -1 -0.06\r\nvn 0.05 -1 -0.05\r\nvn 0.04 -1 -0.05\r\nvn 0.04 -1 -0.04\r\nvn 0.04 -1 -0.04\r\nvn 0.04 -1 -0.03\r\nvn 0.04 -1 -0.02\r\nvn 0.04 -1 -0.02\r\nvn 0.04 -1 -0.01\r\nvn 0.04 -1 -0\r\nvn 0.04 -1 0\r\nvn 0.04 -1 0.01\r\nvn 0.04 -1 0.02\r\nvn 0.04 -1 0.02\r\nvn 0.04 -1 0.03\r\nvn 0.04 -1 0.03\r\nvn 0.04 -1 0.04\r\nvn 0.04 -1 0.04\r\nvn 0.04 -1 0.05\r\nvn 0.04 -1 0.06\r\nvn 0.04 -1 0.06\r\nvn 0.05 -1 0.07\r\nvn 0.05 -1 0.08\r\nvn 0.05 -1 0.09\r\nvn 0.05 -0.99 0.09\r\nvn 0.05 -0.99 0.1\r\nvn 0.05 -0.99 0.11\r\nvn 0.05 -0.99 0.12\r\nvn 0.06 -0.99 0.13\r\nvn 0.06 -0.99 0.14\r\nvn 0.06 -0.99 0.16\r\nvn 0.07 -0.98 0.17\r\nvn 0.07 -0.98 0.18\r\nvn 0.07 -0.98 0.19\r\nvn 0.08 -0.98 0.21\r\nvn 0.08 -0.97 0.22\r\nvn 0.09 -0.97 0.24\r\nvn 0.1 -0.96 0.26\r\nvn 0.12 -0.95 0.27\r\nvn 0.19 -0.93 0.31\r\nvn 0.18 -0.85 0.49\r\nvn -0.02 -0.19 0.98\r\nvn -0.04 -0.19 0.98\r\nvn -0.03 -0.82 0.57\r\nvn -0.05 -0.18 0.98\r\nvn -0.07 -0.18 0.98\r\nvn -0.08 -0.18 0.98\r\nvn -0.1 -0.18 0.98\r\nvn -0.11 -0.18 0.98\r\nvn -0.13 -0.17 0.98\r\nvn -0.14 -0.17 0.97\r\nvn -0.15 -0.17 0.97\r\nvn -0.17 -0.17 0.97\r\nvn -0.18 -0.16 0.97\r\nvn -0.19 -0.16 0.97\r\nvn -0.2 -0.16 0.97\r\nvn -0.21 -0.15 0.96\r\nvn -0.23 -0.15 0.96\r\nvn -0.24 -0.14 0.96\r\nvn -0.27 -0.13 0.95\r\nvn -0.38 -0.15 0.92\r\nvn 0.16 -0.72 0.67\r\nvn 0.15 -0.63 0.76\r\nvn 0.14 -0.56 0.82\r\nvn 0.13 -0.5 0.86\r\nvn 0.12 -0.46 0.88\r\nvn 0.11 -0.43 0.9\r\nvn 0.1 -0.4 0.91\r\nvn 0.1 -0.37 0.92\r\nvn 0.09 -0.34 0.93\r\nvn 0.08 -0.32 0.94\r\nvn 0.08 -0.3 0.95\r\nvn 0.07 -0.28 0.96\r\nvn 0.07 -0.26 0.96\r\nvn 0.07 -0.24 0.97\r\nvn 0.06 -0.22 0.97\r\nvn 0.06 -0.21 0.98\r\nvn 0.06 -0.19 0.98\r\nvn 0.05 -0.18 0.98\r\nvn 0.05 -0.16 0.99\r\nvn 0.05 -0.15 0.99\r\nvn 0.05 -0.14 0.99\r\nvn 0.05 -0.13 0.99\r\nvn 0.05 -0.13 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.12 0.99\r\nvn 0.05 -0.13 0.99\r\nvn 0.05 -0.14 0.99\r\nvn 0.05 -0.15 0.99\r\nvn 0.05 -0.16 0.99\r\nvn 0.05 -0.17 0.98\r\nvn 0.05 -0.18 0.98\r\nvn 0.05 -0.19 0.98\r\nvn 0.05 -0.2 0.98\r\nvn 0.05 -0.21 0.98\r\nvn 0.05 -0.21 0.98\r\nvn 0.05 -0.22 0.97\r\nvn 0.05 -0.23 0.97\r\nvn 0.05 -0.24 0.97\r\nvn 0.05 -0.26 0.96\r\nvn 0.05 -0.29 0.95\r\nvn 0.05 -0.33 0.94\r\nvn 0.05 -0.37 0.93\r\nvn 0.06 -0.42 0.91\r\nvn 0.06 -0.45 0.89\r\nvn 0.06 -0.49 0.87\r\nvn 0.07 -0.52 0.85\r\nvn 0.07 -0.56 0.83\r\nvn 0.08 -0.59 0.81\r\nvn 0.08 -0.61 0.79\r\nvn 0.09 -0.63 0.77\r\nvn 0.09 -0.65 0.75\r\nvn 0.09 -0.66 0.74\r\nvn 0.1 -0.68 0.73\r\nvn 0.1 -0.69 0.72\r\nvn 0.1 -0.7 0.71\r\nvn 0.1 -0.71 0.69\r\nvn 0.11 -0.72 0.68\r\nvn 0.11 -0.74 0.67\r\nvn 0.11 -0.75 0.65\r\nvn 0.11 -0.76 0.64\r\nvn 0.17 -0.86 0.48\r\nvn -0.27 0.84 0.47\r\nvn -0.74 0.63 0.23\r\nvn -0.77 0.53 0.36\r\nvn -0.29 0.76 0.58\r\nvn -0.77 0.42 0.48\r\nvn -0.29 0.67 0.69\r\nvn -0.74 0.62 0.24\r\nvn -0.76 0.52 0.38\r\nvn -0.74 0.63 0.23\r\nvn -0.75 0.54 0.37\r\nvn -0.76 0.41 0.5\r\nvn -0.76 0.44 0.48\r\nvn -1 -0 0.05\r\nvn -1 0.09 -0.02\r\nvn -0.98 0.15 -0.12\r\nvn 0.03 0.88 -0.47\r\nvn 0.03 0.97 -0.25\r\nvn 0.02 0.88 0.48\r\nvn -0.03 -0.88 0.47\r\nvn -0.03 -0.93 0.36\r\nvn -0.02 -0.92 -0.4\r\nvt 0.5759 0.6237\r\nvt 0.5755 0.6222\r\nvt 0.5774 0.6223\r\nvt 0.5772 0.6244\r\nvt 0.5758 0.6193\r\nvt 0.5778 0.6195\r\nvt 0.5798 0.6198\r\nvt 0.5793 0.6227\r\nvt 0.5786 0.624\r\nvt 0.58 0.6229\r\nvt 0.5789 0.6246\r\nvt 0.5805 0.62\r\nvt 0.581 0.6201\r\nvt 0.5804 0.6231\r\nvt 0.5792 0.6251\r\nvt 0.145 0.0029\r\nvt 0.1458 0.0042\r\nvt 0.144 0.0046\r\nvt 0.1437 0.0026\r\nvt 0.1463 0.007\r\nvt 0.1444 0.0073\r\nvt 0.1424 0.0076\r\nvt 0.1421 0.0047\r\nvt 0.1425 0.0032\r\nvt 0.1414 0.0047\r\nvt 0.142 0.0027\r\nvt 0.1417 0.0076\r\nvt 0.141 0.0077\r\nvt 0.1408 0.0046\r\nvt 0.1415 0.0023\r\nvt 0.5763 0.6162\r\nvt 0.5782 0.6164\r\nvt 0.5768 0.6128\r\nvt 0.5787 0.613\r\nvt 0.5806 0.6133\r\nvt 0.5802 0.6167\r\nvt 0.5809 0.6168\r\nvt 0.5813 0.6134\r\nvt 0.5818 0.6135\r\nvt 0.5814 0.6169\r\nvt 0.1468 0.0101\r\nvt 0.1448 0.0104\r\nvt 0.1472 0.0135\r\nvt 0.1453 0.0138\r\nvt 0.1434 0.0141\r\nvt 0.1429 0.0107\r\nvt 0.1421 0.0108\r\nvt 0.1427 0.0142\r\nvt 0.142 0.0143\r\nvt 0.1415 0.0109\r\nvt 0.5773 0.6092\r\nvt 0.5792 0.6095\r\nvt 0.5778 0.6055\r\nvt 0.5797 0.6058\r\nvt 0.5816 0.6061\r\nvt 0.5811 0.6097\r\nvt 0.5818 0.6098\r\nvt 0.5823 0.6062\r\nvt 0.5828 0.6062\r\nvt 0.5823 0.6099\r\nvt 0.1478 0.0171\r\nvt 0.1458 0.0173\r\nvt 0.1483 0.0207\r\nvt 0.1464 0.021\r\nvt 0.1444 0.0213\r\nvt 0.1439 0.0176\r\nvt 0.1432 0.0177\r\nvt 0.1437 0.0214\r\nvt 0.1431 0.0215\r\nvt 0.1425 0.0178\r\nvt 0.5783 0.6018\r\nvt 0.5802 0.6021\r\nvt 0.5788 0.5981\r\nvt 0.5808 0.5984\r\nvt 0.5827 0.5987\r\nvt 0.5822 0.6023\r\nvt 0.5828 0.6024\r\nvt 0.5833 0.5988\r\nvt 0.5839 0.5988\r\nvt 0.5833 0.6025\r\nvt 0.1488 0.0244\r\nvt 0.1469 0.0247\r\nvt 0.1494 0.0281\r\nvt 0.1474 0.0284\r\nvt 0.1455 0.0287\r\nvt 0.145 0.025\r\nvt 0.1443 0.0251\r\nvt 0.1448 0.0288\r\nvt 0.1442 0.0289\r\nvt 0.1436 0.0252\r\nvt 0.5793 0.5946\r\nvt 0.5813 0.5949\r\nvt 0.5798 0.5912\r\nvt 0.5817 0.5915\r\nvt 0.5837 0.5918\r\nvt 0.5832 0.5951\r\nvt 0.5838 0.5952\r\nvt 0.5843 0.5919\r\nvt 0.5848 0.5919\r\nvt 0.5844 0.5953\r\nvt 0.1499 0.0316\r\nvt 0.148 0.0319\r\nvt 0.1504 0.035\r\nvt 0.1484 0.0353\r\nvt 0.1465 0.0355\r\nvt 0.146 0.0322\r\nvt 0.1453 0.0323\r\nvt 0.1458 0.0356\r\nvt 0.1451 0.0357\r\nvt 0.1447 0.0324\r\nvt 0.5802 0.5882\r\nvt 0.5822 0.5884\r\nvt 0.5806 0.5854\r\nvt 0.5825 0.5857\r\nvt 0.5845 0.586\r\nvt 0.5841 0.5887\r\nvt 0.5847 0.5888\r\nvt 0.5851 0.5861\r\nvt 0.5856 0.5861\r\nvt 0.5853 0.5889\r\nvt 0.1508 0.0381\r\nvt 0.1489 0.0383\r\nvt 0.1512 0.0408\r\nvt 0.1493 0.041\r\nvt 0.1473 0.0413\r\nvt 0.1469 0.0386\r\nvt 0.1462 0.0387\r\nvt 0.1466 0.0414\r\nvt 0.146 0.0415\r\nvt 0.1456 0.0388\r\nvt 0.5809 0.5831\r\nvt 0.5828 0.5834\r\nvt 0.5812 0.5812\r\nvt 0.5831 0.5815\r\nvt 0.585 0.5818\r\nvt 0.5848 0.5837\r\nvt 0.5854 0.5838\r\nvt 0.5857 0.5819\r\nvt 0.5862 0.5819\r\nvt 0.586 0.5838\r\nvt 0.1515 0.043\r\nvt 0.1496 0.0433\r\nvt 0.1518 0.0449\r\nvt 0.1498 0.0452\r\nvt 0.1479 0.0455\r\nvt 0.1476 0.0436\r\nvt 0.1469 0.0437\r\nvt 0.1472 0.0456\r\nvt 0.1466 0.0457\r\nvt 0.1463 0.0438\r\nvt 0.5814 0.5796\r\nvt 0.5833 0.5799\r\nvt 0.5816 0.5783\r\nvt 0.5835 0.5786\r\nvt 0.5855 0.5788\r\nvt 0.5853 0.5802\r\nvt 0.5859 0.5803\r\nvt 0.5861 0.5789\r\nvt 0.5866 0.579\r\nvt 0.5864 0.5803\r\nvt 0.152 0.0465\r\nvt 0.1501 0.0468\r\nvt 0.1522 0.0478\r\nvt 0.1503 0.0481\r\nvt 0.1483 0.0483\r\nvt 0.1481 0.0471\r\nvt 0.1474 0.0471\r\nvt 0.1476 0.0484\r\nvt 0.147 0.0485\r\nvt 0.1468 0.0472\r\nvt 0.5818 0.5772\r\nvt 0.5837 0.5774\r\nvt 0.5819 0.5762\r\nvt 0.5838 0.5765\r\nvt 0.5858 0.5768\r\nvt 0.5856 0.5777\r\nvt 0.5863 0.5778\r\nvt 0.5864 0.5769\r\nvt 0.5869 0.5769\r\nvt 0.5868 0.5779\r\nvt 0.1523 0.0488\r\nvt 0.1504 0.0491\r\nvt 0.1525 0.0497\r\nvt 0.1505 0.05\r\nvt 0.1486 0.0503\r\nvt 0.1485 0.0494\r\nvt 0.1478 0.0495\r\nvt 0.1479 0.0504\r\nvt 0.1472 0.0505\r\nvt 0.1471 0.0496\r\nvt 0.582 0.5753\r\nvt 0.584 0.5756\r\nvt 0.5822 0.5745\r\nvt 0.5841 0.5748\r\nvt 0.586 0.5751\r\nvt 0.5859 0.5759\r\nvt 0.5865 0.576\r\nvt 0.5867 0.5752\r\nvt 0.5872 0.5753\r\nvt 0.5871 0.5761\r\nvt 0.1526 0.0505\r\nvt 0.1507 0.0508\r\nvt 0.1527 0.0513\r\nvt 0.1508 0.0516\r\nvt 0.1488 0.0519\r\nvt 0.1487 0.0511\r\nvt 0.148 0.0512\r\nvt 0.1481 0.052\r\nvt 0.1475 0.0521\r\nvt 0.1474 0.0513\r\nvt 0.5823 0.5737\r\nvt 0.5842 0.574\r\nvt 0.5824 0.5728\r\nvt 0.5843 0.5731\r\nvt 0.5863 0.5734\r\nvt 0.5861 0.5743\r\nvt 0.5868 0.5744\r\nvt 0.5869 0.5736\r\nvt 0.5874 0.5736\r\nvt 0.5873 0.5745\r\nvt 0.1528 0.052\r\nvt 0.1509 0.0523\r\nvt 0.153 0.0528\r\nvt 0.151 0.0531\r\nvt 0.1491 0.0534\r\nvt 0.149 0.0526\r\nvt 0.1483 0.0527\r\nvt 0.1484 0.0535\r\nvt 0.1477 0.0536\r\nvt 0.1476 0.0528\r\nvt 0.5826 0.5719\r\nvt 0.5845 0.5722\r\nvt 0.5827 0.5708\r\nvt 0.5847 0.5711\r\nvt 0.5866 0.5714\r\nvt 0.5864 0.5725\r\nvt 0.5871 0.5726\r\nvt 0.5872 0.5715\r\nvt 0.5878 0.5716\r\nvt 0.5876 0.5727\r\nvt 0.1531 0.0537\r\nvt 0.1512 0.054\r\nvt 0.1532 0.0547\r\nvt 0.1513 0.055\r\nvt 0.1494 0.0553\r\nvt 0.1492 0.0543\r\nvt 0.1485 0.0544\r\nvt 0.1487 0.0554\r\nvt 0.148 0.0555\r\nvt 0.1479 0.0545\r\nvt 0.5829 0.5695\r\nvt 0.5849 0.5698\r\nvt 0.5832 0.568\r\nvt 0.5851 0.5683\r\nvt 0.587 0.5686\r\nvt 0.5868 0.5701\r\nvt 0.5874 0.5702\r\nvt 0.5877 0.5687\r\nvt 0.5882 0.5688\r\nvt 0.588 0.5703\r\nvt 0.1534 0.056\r\nvt 0.1515 0.0562\r\nvt 0.1537 0.0574\r\nvt 0.1517 0.0577\r\nvt 0.1498 0.058\r\nvt 0.1496 0.0566\r\nvt 0.1489 0.0567\r\nvt 0.1491 0.0581\r\nvt 0.1485 0.0583\r\nvt 0.1482 0.0568\r\nvt 0.5834 0.5662\r\nvt 0.5854 0.5665\r\nvt 0.5838 0.564\r\nvt 0.5857 0.5643\r\nvt 0.5876 0.5646\r\nvt 0.5873 0.5668\r\nvt 0.588 0.5669\r\nvt 0.5883 0.5647\r\nvt 0.5888 0.5648\r\nvt 0.5885 0.5669\r\nvt 0.1539 0.0592\r\nvt 0.152 0.0595\r\nvt 0.1543 0.0614\r\nvt 0.1523 0.0617\r\nvt 0.1504 0.0619\r\nvt 0.1501 0.0598\r\nvt 0.1494 0.0599\r\nvt 0.1497 0.0621\r\nvt 0.149 0.0622\r\nvt 0.1487 0.06\r\nvt 0.5841 0.5615\r\nvt 0.5861 0.5618\r\nvt 0.5845 0.5586\r\nvt 0.5865 0.5589\r\nvt 0.5884 0.5592\r\nvt 0.588 0.5621\r\nvt 0.5887 0.5622\r\nvt 0.5891 0.5593\r\nvt 0.5896 0.5594\r\nvt 0.5892 0.5622\r\nvt 0.1546 0.0639\r\nvt 0.1527 0.0642\r\nvt 0.1551 0.0668\r\nvt 0.1531 0.067\r\nvt 0.1512 0.0673\r\nvt 0.1508 0.0645\r\nvt 0.1501 0.0646\r\nvt 0.1505 0.0674\r\nvt 0.1498 0.0675\r\nvt 0.1494 0.0647\r\nvt 0.585 0.5555\r\nvt 0.5869 0.5558\r\nvt 0.5855 0.5521\r\nvt 0.5874 0.5523\r\nvt 0.5893 0.5526\r\nvt 0.5889 0.556\r\nvt 0.5895 0.5561\r\nvt 0.59 0.5527\r\nvt 0.5905 0.5528\r\nvt 0.59 0.5562\r\nvt 0.1555 0.0699\r\nvt 0.1536 0.0702\r\nvt 0.156 0.0733\r\nvt 0.1541 0.0736\r\nvt 0.1521 0.0739\r\nvt 0.1517 0.0705\r\nvt 0.1509 0.0706\r\nvt 0.1514 0.074\r\nvt 0.1508 0.0741\r\nvt 0.1503 0.0707\r\nvt 0.586 0.5485\r\nvt 0.5879 0.5487\r\nvt 0.5865 0.5447\r\nvt 0.5884 0.545\r\nvt 0.5904 0.5453\r\nvt 0.5898 0.549\r\nvt 0.5905 0.5491\r\nvt 0.591 0.5454\r\nvt 0.5915 0.5454\r\nvt 0.591 0.5492\r\nvt 0.1565 0.0769\r\nvt 0.1546 0.0772\r\nvt 0.157 0.0807\r\nvt 0.1551 0.0809\r\nvt 0.1532 0.0812\r\nvt 0.1526 0.0775\r\nvt 0.1519 0.0776\r\nvt 0.1525 0.0813\r\nvt 0.1518 0.0814\r\nvt 0.1513 0.0777\r\nvt 0.587 0.5409\r\nvt 0.5889 0.5412\r\nvt 0.5875 0.5371\r\nvt 0.5895 0.5373\r\nvt 0.5914 0.5376\r\nvt 0.5909 0.5414\r\nvt 0.5915 0.5415\r\nvt 0.5921 0.5377\r\nvt 0.5926 0.5377\r\nvt 0.5921 0.5416\r\nvt 0.1575 0.0845\r\nvt 0.1556 0.0848\r\nvt 0.1581 0.0884\r\nvt 0.1561 0.0886\r\nvt 0.1542 0.0889\r\nvt 0.1537 0.085\r\nvt 0.153 0.0851\r\nvt 0.1535 0.089\r\nvt 0.1528 0.0891\r\nvt 0.1523 0.0852\r\nvt 0.588 0.5332\r\nvt 0.59 0.5335\r\nvt 0.5886 0.5294\r\nvt 0.5905 0.5296\r\nvt 0.5924 0.5299\r\nvt 0.5919 0.5337\r\nvt 0.5926 0.5338\r\nvt 0.5931 0.53\r\nvt 0.5936 0.53\r\nvt 0.5931 0.5339\r\nvt 0.1586 0.0922\r\nvt 0.1567 0.0925\r\nvt 0.1591 0.0961\r\nvt 0.1572 0.0963\r\nvt 0.1552 0.0966\r\nvt 0.1547 0.0927\r\nvt 0.154 0.0928\r\nvt 0.1545 0.0967\r\nvt 0.1539 0.0968\r\nvt 0.1534 0.0929\r\nvt 0.5891 0.5255\r\nvt 0.591 0.5258\r\nvt 0.5896 0.5217\r\nvt 0.5915 0.522\r\nvt 0.5934 0.5222\r\nvt 0.5929 0.526\r\nvt 0.5936 0.5261\r\nvt 0.5941 0.5223\r\nvt 0.5946 0.5224\r\nvt 0.5941 0.5262\r\nvt 0.1596 0.0999\r\nvt 0.1577 0.1002\r\nvt 0.1601 0.1037\r\nvt 0.1582 0.104\r\nvt 0.1562 0.1042\r\nvt 0.1557 0.1004\r\nvt 0.155 0.1005\r\nvt 0.1555 0.1043\r\nvt 0.1549 0.1044\r\nvt 0.1544 0.1006\r\nvt 0.59 0.5179\r\nvt 0.592 0.5182\r\nvt 0.5905 0.5142\r\nvt 0.5925 0.5144\r\nvt 0.5944 0.5147\r\nvt 0.5939 0.5184\r\nvt 0.5946 0.5185\r\nvt 0.5951 0.5148\r\nvt 0.5956 0.5148\r\nvt 0.5951 0.5186\r\nvt 0.1606 0.1075\r\nvt 0.1587 0.1078\r\nvt 0.1611 0.1113\r\nvt 0.1592 0.1116\r\nvt 0.1572 0.1118\r\nvt 0.1567 0.108\r\nvt 0.156 0.1081\r\nvt 0.1565 0.1119\r\nvt 0.1559 0.112\r\nvt 0.1554 0.1082\r\nvt 0.591 0.5104\r\nvt 0.593 0.5107\r\nvt 0.5915 0.5067\r\nvt 0.5935 0.5069\r\nvt 0.5954 0.5072\r\nvt 0.5949 0.5109\r\nvt 0.5956 0.511\r\nvt 0.596 0.5073\r\nvt 0.5966 0.5074\r\nvt 0.5961 0.5111\r\nvt 0.1616 0.1151\r\nvt 0.1597 0.1154\r\nvt 0.1621 0.1189\r\nvt 0.1602 0.1191\r\nvt 0.1582 0.1194\r\nvt 0.1577 0.1156\r\nvt 0.157 0.1157\r\nvt 0.1575 0.1195\r\nvt 0.1569 0.1196\r\nvt 0.1564 0.1158\r\nvt 0.592 0.503\r\nvt 0.5939 0.5033\r\nvt 0.5925 0.4993\r\nvt 0.5944 0.4996\r\nvt 0.5964 0.4998\r\nvt 0.5959 0.5035\r\nvt 0.5965 0.5036\r\nvt 0.597 0.4999\r\nvt 0.5975 0.5\r\nvt 0.5971 0.5037\r\nvt 0.1626 0.1226\r\nvt 0.1607 0.1229\r\nvt 0.1631 0.1263\r\nvt 0.1612 0.1266\r\nvt 0.1592 0.1268\r\nvt 0.1587 0.1231\r\nvt 0.158 0.1232\r\nvt 0.1585 0.1269\r\nvt 0.1579 0.127\r\nvt 0.1574 0.1233\r\nvt 0.593 0.4957\r\nvt 0.5949 0.4959\r\nvt 0.5935 0.4921\r\nvt 0.5954 0.4923\r\nvt 0.5973 0.4926\r\nvt 0.5968 0.4962\r\nvt 0.5975 0.4963\r\nvt 0.598 0.4927\r\nvt 0.5985 0.4927\r\nvt 0.598 0.4964\r\nvt 0.1636 0.13\r\nvt 0.1617 0.1303\r\nvt 0.1641 0.1337\r\nvt 0.1622 0.1339\r\nvt 0.1602 0.1342\r\nvt 0.1597 0.1305\r\nvt 0.159 0.1306\r\nvt 0.1595 0.1343\r\nvt 0.1589 0.1344\r\nvt 0.1584 0.1307\r\nvt 0.594 0.4885\r\nvt 0.5959 0.4887\r\nvt 0.5944 0.4849\r\nvt 0.5964 0.4852\r\nvt 0.5983 0.4854\r\nvt 0.5978 0.489\r\nvt 0.5985 0.4891\r\nvt 0.599 0.4855\r\nvt 0.5995 0.4856\r\nvt 0.599 0.4892\r\nvt 0.1646 0.1373\r\nvt 0.1627 0.1376\r\nvt 0.1651 0.1409\r\nvt 0.1632 0.1412\r\nvt 0.1612 0.1415\r\nvt 0.1607 0.1378\r\nvt 0.16 0.1379\r\nvt 0.1605 0.1416\r\nvt 0.1599 0.1416\r\nvt 0.1594 0.138\r\nvt 0.5949 0.4814\r\nvt 0.5969 0.4816\r\nvt 0.5954 0.4779\r\nvt 0.5974 0.4781\r\nvt 0.5993 0.4784\r\nvt 0.5988 0.4819\r\nvt 0.5995 0.482\r\nvt 0.6 0.4785\r\nvt 0.6005 0.4786\r\nvt 0.6 0.4821\r\nvt 0.1656 0.1445\r\nvt 0.1637 0.1448\r\nvt 0.1661 0.1481\r\nvt 0.1642 0.1483\r\nvt 0.1623 0.1486\r\nvt 0.1617 0.1451\r\nvt 0.161 0.1452\r\nvt 0.1615 0.1487\r\nvt 0.1609 0.1488\r\nvt 0.1604 0.1453\r\nvt 0.596 0.4744\r\nvt 0.5979 0.4747\r\nvt 0.5965 0.4709\r\nvt 0.5984 0.4712\r\nvt 0.6003 0.4715\r\nvt 0.5998 0.475\r\nvt 0.6005 0.4751\r\nvt 0.601 0.4716\r\nvt 0.6015 0.4717\r\nvt 0.601 0.4751\r\nvt 0.1666 0.1516\r\nvt 0.1647 0.1519\r\nvt 0.1672 0.1551\r\nvt 0.1653 0.1554\r\nvt 0.1633 0.1557\r\nvt 0.1628 0.1522\r\nvt 0.1621 0.1523\r\nvt 0.1626 0.1558\r\nvt 0.162 0.1559\r\nvt 0.1614 0.1524\r\nvt 0.597 0.4675\r\nvt 0.5989 0.4678\r\nvt 0.5976 0.4641\r\nvt 0.5995 0.4645\r\nvt 0.6014 0.4648\r\nvt 0.6009 0.4681\r\nvt 0.6015 0.4682\r\nvt 0.6021 0.4649\r\nvt 0.6026 0.465\r\nvt 0.602 0.4683\r\nvt 0.1677 0.1585\r\nvt 0.1658 0.1589\r\nvt 0.1683 0.1619\r\nvt 0.1664 0.1623\r\nvt 0.1645 0.1626\r\nvt 0.1639 0.1592\r\nvt 0.1632 0.1593\r\nvt 0.1638 0.1627\r\nvt 0.1631 0.1628\r\nvt 0.1625 0.1594\r\nvt 0.5982 0.4608\r\nvt 0.6001 0.4612\r\nvt 0.5988 0.4576\r\nvt 0.6007 0.458\r\nvt 0.6026 0.4583\r\nvt 0.602 0.4615\r\nvt 0.6027 0.4617\r\nvt 0.6033 0.4585\r\nvt 0.6038 0.4586\r\nvt 0.6032 0.4618\r\nvt 0.1689 0.1653\r\nvt 0.167 0.1656\r\nvt 0.1695 0.1685\r\nvt 0.1676 0.1689\r\nvt 0.1657 0.1693\r\nvt 0.1651 0.166\r\nvt 0.1644 0.1661\r\nvt 0.165 0.1694\r\nvt 0.1644 0.1696\r\nvt 0.1637 0.1662\r\nvt 0.5995 0.4544\r\nvt 0.6014 0.4548\r\nvt 0.6002 0.4513\r\nvt 0.6021 0.4518\r\nvt 0.6039 0.4522\r\nvt 0.6033 0.4552\r\nvt 0.6039 0.4554\r\nvt 0.6046 0.4523\r\nvt 0.6051 0.4525\r\nvt 0.6044 0.4555\r\nvt 0.1702 0.1717\r\nvt 0.1683 0.1721\r\nvt 0.1709 0.1748\r\nvt 0.169 0.1753\r\nvt 0.1671 0.1757\r\nvt 0.1664 0.1725\r\nvt 0.1657 0.1727\r\nvt 0.1664 0.1759\r\nvt 0.1658 0.176\r\nvt 0.1651 0.1728\r\nvt 0.6009 0.4483\r\nvt 0.6028 0.4488\r\nvt 0.6016 0.4454\r\nvt 0.6035 0.4459\r\nvt 0.6054 0.4464\r\nvt 0.6047 0.4493\r\nvt 0.6053 0.4494\r\nvt 0.606 0.4466\r\nvt 0.6065 0.4467\r\nvt 0.6058 0.4495\r\nvt 0.1716 0.1778\r\nvt 0.1697 0.1783\r\nvt 0.1724 0.1807\r\nvt 0.1705 0.1812\r\nvt 0.1686 0.1817\r\nvt 0.1678 0.1788\r\nvt 0.1672 0.1789\r\nvt 0.1679 0.1819\r\nvt 0.1673 0.1821\r\nvt 0.1665 0.1791\r\nvt 0.6024 0.4427\r\nvt 0.6042 0.4432\r\nvt 0.6031 0.4401\r\nvt 0.605 0.4406\r\nvt 0.6069 0.4411\r\nvt 0.6061 0.4437\r\nvt 0.6068 0.4439\r\nvt 0.6075 0.4412\r\nvt 0.608 0.4414\r\nvt 0.6073 0.444\r\nvt 0.1731 0.1835\r\nvt 0.1712 0.184\r\nvt 0.1739 0.1862\r\nvt 0.172 0.1867\r\nvt 0.1701 0.1872\r\nvt 0.1694 0.1846\r\nvt 0.1687 0.1848\r\nvt 0.1694 0.1874\r\nvt 0.1688 0.1876\r\nvt 0.168 0.1849\r\nvt 0.6038 0.4377\r\nvt 0.6057 0.4382\r\nvt 0.6044 0.4354\r\nvt 0.6063 0.4358\r\nvt 0.6082 0.4362\r\nvt 0.6076 0.4385\r\nvt 0.6084 0.4383\r\nvt 0.6089 0.4363\r\nvt 0.6093 0.4356\r\nvt 0.6093 0.4365\r\nvt 0.6094 0.4374\r\nvt 0.6088 0.4388\r\nvt 0.1746 0.1888\r\nvt 0.1727 0.1893\r\nvt 0.1752 0.1912\r\nvt 0.1733 0.1916\r\nvt 0.1714 0.1921\r\nvt 0.1708 0.1897\r\nvt 0.1701 0.1899\r\nvt 0.1707 0.1922\r\nvt 0.1701 0.1924\r\nvt 0.1695 0.1901\r\nvt 0.6048 0.4333\r\nvt 0.6068 0.4336\r\nvt 0.6051 0.4313\r\nvt 0.6071 0.4314\r\nvt 0.609 0.4316\r\nvt 0.6087 0.4339\r\nvt 0.1758 0.1936\r\nvt 0.1738 0.1939\r\nvt 0.1761 0.196\r\nvt 0.1741 0.1961\r\nvt 0.1721 0.1963\r\nvt 0.1719 0.1942\r\nvt 0.1712 0.1943\r\nvt 0.1714 0.1963\r\nvt 0.1708 0.1964\r\nvt 0.1705 0.1944\r\nvt 0.6052 0.4293\r\nvt 0.6071 0.4293\r\nvt 0.605 0.4272\r\nvt 0.607 0.4271\r\nvt 0.6089 0.4269\r\nvt 0.6091 0.4293\r\nvt 0.1761 0.1983\r\nvt 0.1742 0.1983\r\nvt 0.176 0.2007\r\nvt 0.1741 0.2006\r\nvt 0.1721 0.2005\r\nvt 0.1722 0.1983\r\nvt 0.1715 0.1983\r\nvt 0.1714 0.2004\r\nvt 0.1707 0.2004\r\nvt 0.1708 0.1983\r\nvt 0.6048 0.425\r\nvt 0.6067 0.4248\r\nvt 0.6045 0.4226\r\nvt 0.6065 0.4224\r\nvt 0.6084 0.4221\r\nvt 0.6087 0.4246\r\nvt 0.6094 0.4245\r\nvt 0.6096 0.4269\r\nvt 0.6091 0.4221\r\nvt 0.6096 0.422\r\nvt 0.6099 0.4244\r\nvt 0.6101 0.4268\r\nvt 0.1758 0.2032\r\nvt 0.1738 0.203\r\nvt 0.1755 0.2056\r\nvt 0.1735 0.2054\r\nvt 0.1716 0.2052\r\nvt 0.1719 0.2028\r\nvt 0.1712 0.2027\r\nvt 0.1709 0.2051\r\nvt 0.1702 0.205\r\nvt 0.1705 0.2026\r\nvt 0.6042 0.4201\r\nvt 0.6061 0.4199\r\nvt 0.6039 0.4175\r\nvt 0.6059 0.4173\r\nvt 0.6078 0.4171\r\nvt 0.6081 0.4196\r\nvt 0.6087 0.4196\r\nvt 0.6085 0.417\r\nvt 0.609 0.4169\r\nvt 0.6093 0.4195\r\nvt 0.1752 0.2082\r\nvt 0.1732 0.208\r\nvt 0.1749 0.2109\r\nvt 0.1729 0.2107\r\nvt 0.171 0.2105\r\nvt 0.1713 0.2078\r\nvt 0.1706 0.2077\r\nvt 0.1703 0.2104\r\nvt 0.1696 0.2103\r\nvt 0.1699 0.2076\r\nvt 0.6037 0.4148\r\nvt 0.6056 0.4146\r\nvt 0.6034 0.412\r\nvt 0.6054 0.4119\r\nvt 0.6073 0.4117\r\nvt 0.6075 0.4144\r\nvt 0.6082 0.4144\r\nvt 0.608 0.4117\r\nvt 0.6085 0.4117\r\nvt 0.6087 0.4143\r\nvt 0.1746 0.2136\r\nvt 0.1727 0.2134\r\nvt 0.1744 0.2163\r\nvt 0.1725 0.2162\r\nvt 0.1705 0.2161\r\nvt 0.1707 0.2132\r\nvt 0.17 0.2132\r\nvt 0.1698 0.216\r\nvt 0.1692 0.216\r\nvt 0.1694 0.2131\r\nvt 0.6033 0.4092\r\nvt 0.6052 0.4091\r\nvt 0.6032 0.4064\r\nvt 0.6051 0.4063\r\nvt 0.6071 0.4063\r\nvt 0.6072 0.409\r\nvt 0.6078 0.409\r\nvt 0.6077 0.4063\r\nvt 0.6083 0.4062\r\nvt 0.6084 0.409\r\nvt 0.1743 0.2191\r\nvt 0.1723 0.219\r\nvt 0.1742 0.2219\r\nvt 0.1722 0.2218\r\nvt 0.1703 0.2218\r\nvt 0.1704 0.2189\r\nvt 0.1697 0.2189\r\nvt 0.1696 0.2218\r\nvt 0.1689 0.2217\r\nvt 0.169 0.2189\r\nvt 0.6031 0.4036\r\nvt 0.6051 0.4036\r\nvt 0.6031 0.4008\r\nvt 0.6051 0.4008\r\nvt 0.607 0.4008\r\nvt 0.607 0.4035\r\nvt 0.6077 0.4035\r\nvt 0.6077 0.4008\r\nvt 0.6082 0.4008\r\nvt 0.6082 0.4035\r\nvt 0.1741 0.2247\r\nvt 0.1721 0.2247\r\nvt 0.1741 0.2275\r\nvt 0.1721 0.2275\r\nvt 0.1702 0.2275\r\nvt 0.1702 0.2247\r\nvt 0.1695 0.2246\r\nvt 0.1695 0.2275\r\nvt 0.1688 0.2275\r\nvt 0.1688 0.2246\r\nvt 0.6031 0.398\r\nvt 0.6051 0.398\r\nvt 0.6032 0.3952\r\nvt 0.6051 0.3953\r\nvt 0.6071 0.3953\r\nvt 0.607 0.398\r\nvt 0.6077 0.398\r\nvt 0.6077 0.3953\r\nvt 0.6083 0.3953\r\nvt 0.6082 0.398\r\nvt 0.1741 0.2303\r\nvt 0.1721 0.2304\r\nvt 0.1741 0.2331\r\nvt 0.1722 0.2332\r\nvt 0.1702 0.2332\r\nvt 0.1702 0.2304\r\nvt 0.1695 0.2304\r\nvt 0.1695 0.2332\r\nvt 0.1689 0.2332\r\nvt 0.1688 0.2304\r\nvt 0.6032 0.3925\r\nvt 0.6052 0.3925\r\nvt 0.6033 0.3898\r\nvt 0.6053 0.3898\r\nvt 0.6072 0.3899\r\nvt 0.6071 0.3926\r\nvt 0.6078 0.3926\r\nvt 0.6079 0.3899\r\nvt 0.6084 0.3899\r\nvt 0.6083 0.3926\r\nvt 0.1742 0.2359\r\nvt 0.1723 0.236\r\nvt 0.1743 0.2387\r\nvt 0.1724 0.2387\r\nvt 0.1704 0.2388\r\nvt 0.1703 0.236\r\nvt 0.1696 0.236\r\nvt 0.1697 0.2388\r\nvt 0.169 0.2388\r\nvt 0.1689 0.2361\r\nvt 0.6034 0.3871\r\nvt 0.6054 0.3872\r\nvt 0.6036 0.3845\r\nvt 0.6055 0.3845\r\nvt 0.6075 0.3846\r\nvt 0.6073 0.3872\r\nvt 0.608 0.3873\r\nvt 0.6081 0.3847\r\nvt 0.6086 0.3847\r\nvt 0.6085 0.3873\r\nvt 0.1744 0.2414\r\nvt 0.1725 0.2414\r\nvt 0.1745 0.244\r\nvt 0.1726 0.2441\r\nvt 0.1706 0.2442\r\nvt 0.1705 0.2415\r\nvt 0.1698 0.2415\r\nvt 0.1699 0.2442\r\nvt 0.1693 0.2443\r\nvt 0.1691 0.2416\r\nvt 0.6037 0.3819\r\nvt 0.6056 0.382\r\nvt 0.6038 0.3794\r\nvt 0.6058 0.3795\r\nvt 0.6077 0.3796\r\nvt 0.6076 0.3821\r\nvt 0.6082 0.3821\r\nvt 0.6084 0.3796\r\nvt 0.6089 0.3796\r\nvt 0.6088 0.3821\r\nvt 0.1747 0.2466\r\nvt 0.1727 0.2467\r\nvt 0.1748 0.2492\r\nvt 0.1729 0.2493\r\nvt 0.1709 0.2494\r\nvt 0.1708 0.2468\r\nvt 0.1701 0.2468\r\nvt 0.1702 0.2494\r\nvt 0.1695 0.2494\r\nvt 0.1694 0.2469\r\nvt 0.6039 0.3769\r\nvt 0.6059 0.377\r\nvt 0.6041 0.3746\r\nvt 0.606 0.3747\r\nvt 0.608 0.3748\r\nvt 0.6078 0.3772\r\nvt 0.6085 0.3772\r\nvt 0.6086 0.3748\r\nvt 0.6092 0.3749\r\nvt 0.609 0.3772\r\nvt 0.1749 0.2516\r\nvt 0.173 0.2517\r\nvt 0.1751 0.254\r\nvt 0.1731 0.2541\r\nvt 0.1712 0.2542\r\nvt 0.171 0.2518\r\nvt 0.1703 0.2519\r\nvt 0.1705 0.2543\r\nvt 0.1698 0.2543\r\nvt 0.1697 0.2519\r\nvt 0.6042 0.3723\r\nvt 0.6062 0.3724\r\nvt 0.6043 0.3701\r\nvt 0.6063 0.3702\r\nvt 0.6082 0.3703\r\nvt 0.6081 0.3725\r\nvt 0.6088 0.3726\r\nvt 0.6089 0.3704\r\nvt 0.6094 0.3704\r\nvt 0.6093 0.3726\r\nvt 0.1752 0.2563\r\nvt 0.1733 0.2565\r\nvt 0.1753 0.2586\r\nvt 0.1734 0.2587\r\nvt 0.1714 0.2588\r\nvt 0.1713 0.2566\r\nvt 0.1706 0.2566\r\nvt 0.1707 0.2588\r\nvt 0.1701 0.2589\r\nvt 0.1699 0.2566\r\nvt 0.6044 0.368\r\nvt 0.6064 0.3681\r\nvt 0.6046 0.366\r\nvt 0.6065 0.3661\r\nvt 0.6085 0.3662\r\nvt 0.6083 0.3682\r\nvt 0.609 0.3683\r\nvt 0.6091 0.3663\r\nvt 0.6096 0.3663\r\nvt 0.6095 0.3683\r\nvt 0.1755 0.2607\r\nvt 0.1735 0.2608\r\nvt 0.1756 0.2628\r\nvt 0.1736 0.2629\r\nvt 0.1717 0.263\r\nvt 0.1716 0.2609\r\nvt 0.1708 0.261\r\nvt 0.171 0.263\r\nvt 0.1703 0.2631\r\nvt 0.1702 0.261\r\nvt 0.6047 0.3641\r\nvt 0.6066 0.3642\r\nvt 0.6048 0.3622\r\nvt 0.6067 0.3623\r\nvt 0.6087 0.3624\r\nvt 0.6086 0.3643\r\nvt 0.6092 0.3643\r\nvt 0.6093 0.3624\r\nvt 0.6099 0.3625\r\nvt 0.6098 0.3643\r\nvt 0.1757 0.2647\r\nvt 0.1737 0.2649\r\nvt 0.1758 0.2667\r\nvt 0.1739 0.2668\r\nvt 0.1719 0.2669\r\nvt 0.1718 0.265\r\nvt 0.1711 0.265\r\nvt 0.1712 0.2669\r\nvt 0.1705 0.267\r\nvt 0.1704 0.265\r\nvt 0.6049 0.3603\r\nvt 0.6068 0.3604\r\nvt 0.605 0.3585\r\nvt 0.6069 0.3586\r\nvt 0.6089 0.3587\r\nvt 0.6088 0.3605\r\nvt 0.6095 0.3606\r\nvt 0.6096 0.3587\r\nvt 0.6101 0.3588\r\nvt 0.61 0.3606\r\nvt 0.1759 0.2686\r\nvt 0.174 0.2687\r\nvt 0.176 0.2704\r\nvt 0.1741 0.2706\r\nvt 0.1721 0.2707\r\nvt 0.172 0.2688\r\nvt 0.1713 0.2688\r\nvt 0.1714 0.2707\r\nvt 0.1708 0.2708\r\nvt 0.1706 0.2689\r\nvt 0.6051 0.3567\r\nvt 0.607 0.3568\r\nvt 0.6052 0.3548\r\nvt 0.6072 0.3549\r\nvt 0.6091 0.355\r\nvt 0.609 0.3569\r\nvt 0.6097 0.3569\r\nvt 0.6098 0.3551\r\nvt 0.6103 0.3551\r\nvt 0.6102 0.357\r\nvt 0.1761 0.2723\r\nvt 0.1742 0.2724\r\nvt 0.1762 0.2742\r\nvt 0.1743 0.2743\r\nvt 0.1723 0.2744\r\nvt 0.1722 0.2725\r\nvt 0.1715 0.2726\r\nvt 0.1716 0.2745\r\nvt 0.171 0.2745\r\nvt 0.1709 0.2726\r\nvt 0.6053 0.353\r\nvt 0.6073 0.3531\r\nvt 0.6054 0.3511\r\nvt 0.6074 0.3512\r\nvt 0.6093 0.3513\r\nvt 0.6092 0.3532\r\nvt 0.6099 0.3532\r\nvt 0.61 0.3513\r\nvt 0.6105 0.3514\r\nvt 0.6104 0.3533\r\nvt 0.1764 0.2761\r\nvt 0.1744 0.2762\r\nvt 0.1765 0.278\r\nvt 0.1745 0.2781\r\nvt 0.1726 0.2782\r\nvt 0.1725 0.2763\r\nvt 0.1717 0.2763\r\nvt 0.1719 0.2783\r\nvt 0.1712 0.2783\r\nvt 0.1711 0.2764\r\nvt 0.6055 0.3492\r\nvt 0.6075 0.3493\r\nvt 0.6056 0.3473\r\nvt 0.6076 0.3474\r\nvt 0.6095 0.3475\r\nvt 0.6094 0.3494\r\nvt 0.6101 0.3494\r\nvt 0.6102 0.3475\r\nvt 0.6107 0.3475\r\nvt 0.6106 0.3495\r\nvt 0.1766 0.28\r\nvt 0.1746 0.2801\r\nvt 0.1767 0.2819\r\nvt 0.1747 0.282\r\nvt 0.1728 0.2821\r\nvt 0.1727 0.2802\r\nvt 0.172 0.2802\r\nvt 0.1721 0.2822\r\nvt 0.1714 0.2822\r\nvt 0.1713 0.2802\r\nvt 0.6057 0.3453\r\nvt 0.6077 0.3454\r\nvt 0.6058 0.3433\r\nvt 0.6078 0.3434\r\nvt 0.6097 0.3435\r\nvt 0.6096 0.3455\r\nvt 0.6103 0.3455\r\nvt 0.6104 0.3436\r\nvt 0.6109 0.3436\r\nvt 0.6108 0.3456\r\nvt 0.1768 0.2839\r\nvt 0.1748 0.284\r\nvt 0.1769 0.2859\r\nvt 0.1749 0.286\r\nvt 0.173 0.2861\r\nvt 0.1729 0.2841\r\nvt 0.1722 0.2842\r\nvt 0.1723 0.2862\r\nvt 0.1716 0.2862\r\nvt 0.1715 0.2842\r\nvt 0.6059 0.3413\r\nvt 0.6079 0.3414\r\nvt 0.606 0.3393\r\nvt 0.608 0.3394\r\nvt 0.6099 0.3395\r\nvt 0.6098 0.3415\r\nvt 0.6105 0.3415\r\nvt 0.6106 0.3395\r\nvt 0.6111 0.3395\r\nvt 0.611 0.3416\r\nvt 0.177 0.288\r\nvt 0.1751 0.2881\r\nvt 0.1771 0.2901\r\nvt 0.1752 0.2902\r\nvt 0.1732 0.2903\r\nvt 0.1731 0.2882\r\nvt 0.1724 0.2882\r\nvt 0.1725 0.2903\r\nvt 0.1718 0.2903\r\nvt 0.1717 0.2883\r\nvt 0.6061 0.3372\r\nvt 0.6081 0.3373\r\nvt 0.6062 0.3351\r\nvt 0.6082 0.3352\r\nvt 0.6101 0.3353\r\nvt 0.61 0.3374\r\nvt 0.6107 0.3374\r\nvt 0.6108 0.3353\r\nvt 0.6113 0.3354\r\nvt 0.6112 0.3375\r\nvt 0.1772 0.2922\r\nvt 0.1753 0.2923\r\nvt 0.1773 0.2943\r\nvt 0.1754 0.2944\r\nvt 0.1734 0.2945\r\nvt 0.1733 0.2924\r\nvt 0.1726 0.2924\r\nvt 0.1727 0.2945\r\nvt 0.172 0.2946\r\nvt 0.1719 0.2924\r\nvt 0.6063 0.333\r\nvt 0.6083 0.3331\r\nvt 0.6064 0.3309\r\nvt 0.6084 0.3309\r\nvt 0.6103 0.331\r\nvt 0.6102 0.3332\r\nvt 0.6109 0.3332\r\nvt 0.611 0.331\r\nvt 0.6115 0.3311\r\nvt 0.6114 0.3332\r\nvt 0.1774 0.2965\r\nvt 0.1754 0.2966\r\nvt 0.1775 0.2987\r\nvt 0.1755 0.2988\r\nvt 0.1736 0.2989\r\nvt 0.1735 0.2967\r\nvt 0.1728 0.2967\r\nvt 0.1729 0.2989\r\nvt 0.1722 0.2989\r\nvt 0.1721 0.2967\r\nvt 0.6065 0.3287\r\nvt 0.6085 0.3287\r\nvt 0.6066 0.3264\r\nvt 0.6085 0.3265\r\nvt 0.6105 0.3266\r\nvt 0.6104 0.3288\r\nvt 0.6111 0.3288\r\nvt 0.6112 0.3266\r\nvt 0.6117 0.3266\r\nvt 0.6116 0.3289\r\nvt 0.1776 0.301\r\nvt 0.1756 0.3011\r\nvt 0.1776 0.3033\r\nvt 0.1757 0.3033\r\nvt 0.1737 0.3034\r\nvt 0.1737 0.3011\r\nvt 0.173 0.3011\r\nvt 0.173 0.3034\r\nvt 0.1724 0.3034\r\nvt 0.1723 0.3012\r\nvt 0.6067 0.3242\r\nvt 0.6086 0.3242\r\nvt 0.6067 0.3218\r\nvt 0.6087 0.3219\r\nvt 0.6106 0.3219\r\nvt 0.6106 0.3243\r\nvt 0.6112 0.3243\r\nvt 0.6113 0.322\r\nvt 0.6118 0.322\r\nvt 0.6118 0.3243\r\nvt 0.1777 0.3056\r\nvt 0.1758 0.3057\r\nvt 0.1778 0.308\r\nvt 0.1758 0.3081\r\nvt 0.1739 0.3081\r\nvt 0.1738 0.3057\r\nvt 0.1731 0.3057\r\nvt 0.1732 0.3081\r\nvt 0.1725 0.3081\r\nvt 0.1724 0.3058\r\nvt 0.6068 0.3195\r\nvt 0.6088 0.3195\r\nvt 0.6068 0.3171\r\nvt 0.6088 0.3171\r\nvt 0.6107 0.3171\r\nvt 0.6107 0.3195\r\nvt 0.6114 0.3196\r\nvt 0.6114 0.3171\r\nvt 0.6119 0.3171\r\nvt 0.6119 0.3196\r\nvt 0.1778 0.3105\r\nvt 0.1759 0.3105\r\nvt 0.1778 0.313\r\nvt 0.1759 0.313\r\nvt 0.1739 0.313\r\nvt 0.1739 0.3105\r\nvt 0.1732 0.3105\r\nvt 0.1732 0.313\r\nvt 0.1725 0.313\r\nvt 0.1725 0.3105\r\nvt 0.6069 0.3146\r\nvt 0.6088 0.3146\r\nvt 0.6069 0.3121\r\nvt 0.6088 0.3121\r\nvt 0.6108 0.312\r\nvt 0.6108 0.3146\r\nvt 0.6114 0.3146\r\nvt 0.6114 0.312\r\nvt 0.6119 0.312\r\nvt 0.612 0.3146\r\nvt 0.1778 0.3156\r\nvt 0.1759 0.3155\r\nvt 0.1778 0.3182\r\nvt 0.1758 0.3181\r\nvt 0.1739 0.3181\r\nvt 0.1739 0.3155\r\nvt 0.1732 0.3155\r\nvt 0.1732 0.3181\r\nvt 0.1725 0.318\r\nvt 0.1725 0.3155\r\nvt 0.6068 0.3096\r\nvt 0.6088 0.3095\r\nvt 0.6067 0.307\r\nvt 0.6087 0.3069\r\nvt 0.6106 0.3068\r\nvt 0.6107 0.3094\r\nvt 0.6114 0.3094\r\nvt 0.6113 0.3068\r\nvt 0.6118 0.3068\r\nvt 0.6119 0.3094\r\nvt 0.1777 0.3209\r\nvt 0.1757 0.3208\r\nvt 0.1775 0.3236\r\nvt 0.1756 0.3234\r\nvt 0.1736 0.3233\r\nvt 0.1738 0.3207\r\nvt 0.1731 0.3206\r\nvt 0.1729 0.3233\r\nvt 0.1723 0.3232\r\nvt 0.1724 0.3206\r\nvt 0.6066 0.3044\r\nvt 0.6086 0.3042\r\nvt 0.6064 0.3017\r\nvt 0.6084 0.3015\r\nvt 0.6103 0.3014\r\nvt 0.6105 0.3041\r\nvt 0.6112 0.3041\r\nvt 0.611 0.3013\r\nvt 0.6115 0.3013\r\nvt 0.6117 0.304\r\nvt 0.1773 0.3263\r\nvt 0.1754 0.3262\r\nvt 0.1771 0.3291\r\nvt 0.1751 0.3289\r\nvt 0.1732 0.3287\r\nvt 0.1734 0.326\r\nvt 0.1727 0.3259\r\nvt 0.1725 0.3286\r\nvt 0.1718 0.3285\r\nvt 0.1721 0.3259\r\nvt 0.6062 0.299\r\nvt 0.6081 0.2988\r\nvt 0.6058 0.2964\r\nvt 0.6077 0.2961\r\nvt 0.6097 0.2958\r\nvt 0.61 0.2986\r\nvt 0.6107 0.2985\r\nvt 0.6103 0.2957\r\nvt 0.6108 0.2956\r\nvt 0.6112 0.2984\r\nvt 0.1767 0.332\r\nvt 0.1748 0.3317\r\nvt 0.1762 0.3349\r\nvt 0.1743 0.3345\r\nvt 0.1724 0.3341\r\nvt 0.1728 0.3314\r\nvt 0.1721 0.3313\r\nvt 0.1717 0.334\r\nvt 0.1711 0.3339\r\nvt 0.1715 0.3312\r\nvt 0.6054 0.2938\r\nvt 0.6073 0.2934\r\nvt 0.6048 0.291\r\nvt 0.6066 0.2905\r\nvt 0.6084 0.29\r\nvt 0.6092 0.293\r\nvt 0.6098 0.2929\r\nvt 0.609 0.2898\r\nvt 0.6095 0.2897\r\nvt 0.6104 0.2928\r\nvt 0.1757 0.3377\r\nvt 0.1738 0.3372\r\nvt 0.1749 0.3407\r\nvt 0.1731 0.3401\r\nvt 0.1713 0.3395\r\nvt 0.1719 0.3368\r\nvt 0.1712 0.3366\r\nvt 0.1707 0.3393\r\nvt 0.17 0.3392\r\nvt 0.1706 0.3365\r\nvt 0.604 0.2879\r\nvt 0.6055 0.2871\r\nvt 0.6026 0.2844\r\nvt 0.6043 0.2835\r\nvt 0.6061 0.2818\r\nvt 0.6071 0.2863\r\nvt 0.6076 0.286\r\nvt 0.607 0.2821\r\nvt 0.6075 0.2823\r\nvt 0.6081 0.286\r\nvt 0.1739 0.3444\r\nvt 0.1723 0.3435\r\nvt 0.1735 0.349\r\nvt 0.1714 0.347\r\nvt 0.1693 0.3459\r\nvt 0.1706 0.3426\r\nvt 0.17 0.3422\r\nvt 0.1688 0.3451\r\nvt 0.1682 0.3447\r\nvt 0.1694 0.3421\r\nvt 0.5771 0.625\r\nvt 0.577 0.6255\r\nvt 0.5751 0.6245\r\nvt 0.5755 0.6242\r\nvt 0.2105 0.4064\r\nvt 0.2084 0.4033\r\nvt 0.21 0.4022\r\nvt 0.2121 0.4053\r\nvt 0.2063 0.4003\r\nvt 0.2079 0.3992\r\nvt 0.2095 0.3981\r\nvt 0.2116 0.4011\r\nvt 0.2137 0.4041\r\nvt 0.2122 0.4007\r\nvt 0.2143 0.4037\r\nvt 0.2101 0.3977\r\nvt 0.2106 0.3973\r\nvt 0.2127 0.4003\r\nvt 0.2148 0.4034\r\nvt 0.6462 0.2284\r\nvt 0.6442 0.2314\r\nvt 0.6426 0.2303\r\nvt 0.6446 0.2273\r\nvt 0.6422 0.2344\r\nvt 0.6406 0.2333\r\nvt 0.639 0.2322\r\nvt 0.641 0.2292\r\nvt 0.643 0.2262\r\nvt 0.6405 0.2288\r\nvt 0.6425 0.2259\r\nvt 0.6384 0.2318\r\nvt 0.638 0.2315\r\nvt 0.64 0.2285\r\nvt 0.6421 0.2256\r\nvt 0.2042 0.3972\r\nvt 0.2058 0.3961\r\nvt 0.202 0.3942\r\nvt 0.2036 0.3931\r\nvt 0.2052 0.3919\r\nvt 0.2074 0.395\r\nvt 0.208 0.3946\r\nvt 0.2058 0.3915\r\nvt 0.2064 0.3911\r\nvt 0.2085 0.3942\r\nvt 0.6401 0.2374\r\nvt 0.6385 0.2363\r\nvt 0.638 0.2404\r\nvt 0.6364 0.2393\r\nvt 0.6348 0.2381\r\nvt 0.6369 0.2351\r\nvt 0.6363 0.2348\r\nvt 0.6343 0.2378\r\nvt 0.6338 0.2375\r\nvt 0.6359 0.2345\r\nvt 0.1999 0.3911\r\nvt 0.2015 0.39\r\nvt 0.1977 0.388\r\nvt 0.1993 0.3869\r\nvt 0.2009 0.3858\r\nvt 0.2031 0.3889\r\nvt 0.2037 0.3884\r\nvt 0.2015 0.3853\r\nvt 0.202 0.385\r\nvt 0.2042 0.3881\r\nvt 0.6359 0.2434\r\nvt 0.6343 0.2423\r\nvt 0.6337 0.2464\r\nvt 0.6321 0.2453\r\nvt 0.6306 0.2442\r\nvt 0.6327 0.2411\r\nvt 0.6321 0.2408\r\nvt 0.63 0.2438\r\nvt 0.6296 0.2435\r\nvt 0.6317 0.2405\r\nvt 0.1955 0.3849\r\nvt 0.1971 0.3838\r\nvt 0.1933 0.3818\r\nvt 0.1949 0.3807\r\nvt 0.1965 0.3795\r\nvt 0.1987 0.3826\r\nvt 0.1993 0.3822\r\nvt 0.1971 0.3791\r\nvt 0.1976 0.3787\r\nvt 0.1998 0.3818\r\nvt 0.6316 0.2495\r\nvt 0.63 0.2483\r\nvt 0.6293 0.2525\r\nvt 0.6278 0.2514\r\nvt 0.6262 0.2502\r\nvt 0.6284 0.2472\r\nvt 0.6278 0.2468\r\nvt 0.6257 0.2498\r\nvt 0.6252 0.2495\r\nvt 0.6274 0.2465\r\nvt 0.1911 0.3787\r\nvt 0.1927 0.3776\r\nvt 0.1888 0.3757\r\nvt 0.1904 0.3745\r\nvt 0.192 0.3734\r\nvt 0.1942 0.3764\r\nvt 0.1948 0.376\r\nvt 0.1925 0.3729\r\nvt 0.1931 0.3725\r\nvt 0.1953 0.3756\r\nvt 0.6271 0.2556\r\nvt 0.6255 0.2544\r\nvt 0.6248 0.2586\r\nvt 0.6233 0.2574\r\nvt 0.6217 0.2562\r\nvt 0.624 0.2532\r\nvt 0.6234 0.2528\r\nvt 0.6212 0.2558\r\nvt 0.6208 0.2555\r\nvt 0.623 0.2525\r\nvt 0.1866 0.3727\r\nvt 0.1882 0.3715\r\nvt 0.1844 0.3697\r\nvt 0.1859 0.3685\r\nvt 0.1875 0.3674\r\nvt 0.1897 0.3703\r\nvt 0.1903 0.3699\r\nvt 0.188 0.3669\r\nvt 0.1886 0.3665\r\nvt 0.1908 0.3695\r\nvt 0.6226 0.2615\r\nvt 0.621 0.2603\r\nvt 0.6203 0.2644\r\nvt 0.6188 0.2632\r\nvt 0.6173 0.262\r\nvt 0.6195 0.2591\r\nvt 0.619 0.2587\r\nvt 0.6167 0.2616\r\nvt 0.6163 0.2612\r\nvt 0.6186 0.2584\r\nvt 0.1822 0.3669\r\nvt 0.1837 0.3657\r\nvt 0.18 0.3641\r\nvt 0.1815 0.3629\r\nvt 0.183 0.3617\r\nvt 0.1852 0.3645\r\nvt 0.1858 0.364\r\nvt 0.1836 0.3612\r\nvt 0.1841 0.3608\r\nvt 0.1863 0.3636\r\nvt 0.618 0.2672\r\nvt 0.6165 0.266\r\nvt 0.6158 0.2699\r\nvt 0.6143 0.2686\r\nvt 0.6128 0.2674\r\nvt 0.615 0.2647\r\nvt 0.6145 0.2643\r\nvt 0.6123 0.2669\r\nvt 0.6119 0.2666\r\nvt 0.6141 0.264\r\nvt 0.1778 0.3614\r\nvt 0.1794 0.3602\r\nvt 0.1757 0.3589\r\nvt 0.1771 0.3575\r\nvt 0.1785 0.3562\r\nvt 0.1809 0.359\r\nvt 0.1814 0.3585\r\nvt 0.1791 0.3557\r\nvt 0.1796 0.3553\r\nvt 0.1819 0.3581\r\nvt 0.6135 0.2725\r\nvt 0.6121 0.2712\r\nvt 0.6111 0.2751\r\nvt 0.6098 0.2737\r\nvt 0.6085 0.2723\r\nvt 0.6106 0.2699\r\nvt 0.6101 0.2695\r\nvt 0.608 0.2718\r\nvt 0.6076 0.2715\r\nvt 0.6097 0.2691\r\nvt 0.1737 0.3563\r\nvt 0.1748 0.3547\r\nvt 0.1717 0.3542\r\nvt 0.1725 0.3521\r\nvt 0.1759 0.353\r\nvt 0.1764 0.3524\r\nvt 0.1745 0.3488\r\nvt 0.1751 0.3487\r\nvt 0.1769 0.3521\r\nvt 0.6084 0.2781\r\nvt 0.6073 0.2764\r\nvt 0.6051 0.2787\r\nvt 0.6044 0.2766\r\nvt 0.6063 0.2747\r\nvt 0.606 0.2741\r\nvt 0.6042 0.2759\r\nvt 0.6039 0.2755\r\nvt 0.6056 0.2737\r\nvt 0.1701 0.3526\r\nvt 0.1706 0.3503\r\nvt 0.1686 0.3519\r\nvt 0.1689 0.3495\r\nvt 0.6034 0.2803\r\nvt 0.602 0.2809\r\nvt 0.6016 0.2784\r\nvt 0.6029 0.2779\r\nvt 0.6028 0.2771\r\nvt 0.6015 0.2776\r\nvt 0.6014 0.277\r\nvt 0.6025 0.2766\r\nvt 0.167 0.3522\r\nvt 0.1668 0.3501\r\nvt 0.165 0.3531\r\nvt 0.1645 0.3512\r\nvt 0.1639 0.3494\r\nvt 0.1666 0.348\r\nvt 0.1664 0.3472\r\nvt 0.1636 0.3487\r\nvt 0.1633 0.3481\r\nvt 0.166 0.3467\r\nvt 0.6 0.2826\r\nvt 0.6003 0.2804\r\nvt 0.5976 0.2814\r\nvt 0.5981 0.2796\r\nvt 0.5985 0.2777\r\nvt 0.6003 0.2782\r\nvt 0.6002 0.2775\r\nvt 0.5986 0.277\r\nvt 0.5987 0.2766\r\nvt 0.6003 0.277\r\nvt 0.1626 0.3539\r\nvt 0.162 0.3521\r\nvt 0.1599 0.3546\r\nvt 0.1594 0.3528\r\nvt 0.1589 0.3509\r\nvt 0.1614 0.3502\r\nvt 0.1612 0.3496\r\nvt 0.1588 0.3502\r\nvt 0.1586 0.3496\r\nvt 0.1609 0.3489\r\nvt 0.5952 0.2807\r\nvt 0.5957 0.2789\r\nvt 0.5928 0.2802\r\nvt 0.5932 0.2783\r\nvt 0.5935 0.2764\r\nvt 0.5962 0.277\r\nvt 0.5964 0.2764\r\nvt 0.5937 0.2758\r\nvt 0.5938 0.2753\r\nvt 0.5965 0.2759\r\nvt 0.1571 0.3553\r\nvt 0.1567 0.3534\r\nvt 0.1541 0.3558\r\nvt 0.1538 0.3539\r\nvt 0.1535 0.3519\r\nvt 0.1563 0.3515\r\nvt 0.1562 0.3508\r\nvt 0.1534 0.3512\r\nvt 0.1533 0.3506\r\nvt 0.156 0.3501\r\nvt 0.5902 0.2798\r\nvt 0.5905 0.2779\r\nvt 0.5874 0.2795\r\nvt 0.5877 0.2775\r\nvt 0.5879 0.2756\r\nvt 0.5908 0.276\r\nvt 0.5909 0.2753\r\nvt 0.588 0.2749\r\nvt 0.588 0.2744\r\nvt 0.591 0.2748\r\nvt 0.151 0.3562\r\nvt 0.1508 0.3543\r\nvt 0.1479 0.3565\r\nvt 0.1477 0.3546\r\nvt 0.1475 0.3527\r\nvt 0.1505 0.3523\r\nvt 0.1505 0.3516\r\nvt 0.1474 0.3519\r\nvt 0.1474 0.3513\r\nvt 0.1504 0.351\r\nvt 0.5845 0.2792\r\nvt 0.5847 0.2772\r\nvt 0.5815 0.2789\r\nvt 0.5817 0.277\r\nvt 0.5818 0.2751\r\nvt 0.5849 0.2753\r\nvt 0.5849 0.2746\r\nvt 0.5818 0.2744\r\nvt 0.5819 0.2739\r\nvt 0.585 0.2741\r\nvt 0.1446 0.3568\r\nvt 0.1445 0.3548\r\nvt 0.1413 0.357\r\nvt 0.1412 0.355\r\nvt 0.1411 0.3531\r\nvt 0.1443 0.3529\r\nvt 0.1443 0.3522\r\nvt 0.1411 0.3524\r\nvt 0.141 0.3517\r\nvt 0.1442 0.3515\r\nvt 0.5784 0.2788\r\nvt 0.5785 0.2768\r\nvt 0.5753 0.2787\r\nvt 0.5753 0.2767\r\nvt 0.5754 0.2748\r\nvt 0.5786 0.2749\r\nvt 0.5787 0.2742\r\nvt 0.5754 0.2741\r\nvt 0.5754 0.2736\r\nvt 0.5787 0.2737\r\nvt 0.1379 0.3571\r\nvt 0.1379 0.3551\r\nvt 0.1345 0.3572\r\nvt 0.1344 0.3552\r\nvt 0.1344 0.3533\r\nvt 0.1378 0.3532\r\nvt 0.1378 0.3525\r\nvt 0.1344 0.3526\r\nvt 0.1344 0.3519\r\nvt 0.1377 0.3518\r\nvt 0.572 0.2786\r\nvt 0.572 0.2767\r\nvt 0.5687 0.2786\r\nvt 0.5687 0.2766\r\nvt 0.5687 0.2747\r\nvt 0.5721 0.2747\r\nvt 0.5721 0.2741\r\nvt 0.5687 0.274\r\nvt 0.5687 0.2735\r\nvt 0.5721 0.2735\r\nvt 0.131 0.3572\r\nvt 0.131 0.3552\r\nvt 0.1275 0.3572\r\nvt 0.1275 0.3552\r\nvt 0.1275 0.3533\r\nvt 0.131 0.3533\r\nvt 0.131 0.3526\r\nvt 0.1275 0.3526\r\nvt 0.1275 0.3519\r\nvt 0.131 0.3519\r\nvt 0.5653 0.2786\r\nvt 0.5653 0.2766\r\nvt 0.5619 0.2786\r\nvt 0.5619 0.2767\r\nvt 0.5619 0.2747\r\nvt 0.5653 0.2747\r\nvt 0.5653 0.274\r\nvt 0.5619 0.2741\r\nvt 0.5618 0.2735\r\nvt 0.5653 0.2735\r\nvt 0.1239 0.3572\r\nvt 0.124 0.3552\r\nvt 0.1204 0.3571\r\nvt 0.1204 0.3552\r\nvt 0.1204 0.3532\r\nvt 0.124 0.3533\r\nvt 0.124 0.3525\r\nvt 0.1204 0.3525\r\nvt 0.1204 0.3518\r\nvt 0.124 0.3519\r\nvt 0.5584 0.2787\r\nvt 0.5584 0.2767\r\nvt 0.5549 0.2787\r\nvt 0.5549 0.2768\r\nvt 0.5549 0.2748\r\nvt 0.5584 0.2748\r\nvt 0.5584 0.2741\r\nvt 0.5548 0.2742\r\nvt 0.5548 0.2737\r\nvt 0.5584 0.2736\r\nvt 0.1168 0.357\r\nvt 0.1168 0.3551\r\nvt 0.1131 0.357\r\nvt 0.1132 0.355\r\nvt 0.1132 0.353\r\nvt 0.1168 0.3531\r\nvt 0.1168 0.3524\r\nvt 0.1132 0.3523\r\nvt 0.1133 0.3517\r\nvt 0.1169 0.3518\r\nvt 0.5514 0.2788\r\nvt 0.5514 0.2769\r\nvt 0.5479 0.2789\r\nvt 0.5478 0.277\r\nvt 0.5478 0.275\r\nvt 0.5513 0.2749\r\nvt 0.5513 0.2743\r\nvt 0.5477 0.2743\r\nvt 0.5477 0.2738\r\nvt 0.5513 0.2737\r\nvt 0.1095 0.3569\r\nvt 0.1095 0.3549\r\nvt 0.1058 0.3568\r\nvt 0.1059 0.3548\r\nvt 0.1059 0.3529\r\nvt 0.1096 0.353\r\nvt 0.1096 0.3522\r\nvt 0.106 0.3521\r\nvt 0.106 0.3515\r\nvt 0.1096 0.3516\r\nvt 0.5443 0.279\r\nvt 0.5442 0.2771\r\nvt 0.5407 0.2791\r\nvt 0.5406 0.2772\r\nvt 0.5406 0.2752\r\nvt 0.5442 0.2751\r\nvt 0.5442 0.2744\r\nvt 0.5406 0.2745\r\nvt 0.5405 0.274\r\nvt 0.5441 0.2739\r\nvt 0.1021 0.3567\r\nvt 0.1022 0.3547\r\nvt 0.0984 0.3565\r\nvt 0.0985 0.3546\r\nvt 0.0986 0.3526\r\nvt 0.1023 0.3528\r\nvt 0.1023 0.352\r\nvt 0.0986 0.3519\r\nvt 0.0986 0.3513\r\nvt 0.1023 0.3514\r\nvt 0.537 0.2792\r\nvt 0.537 0.2773\r\nvt 0.5334 0.2793\r\nvt 0.5333 0.2774\r\nvt 0.5333 0.2754\r\nvt 0.5369 0.2753\r\nvt 0.5369 0.2746\r\nvt 0.5333 0.2747\r\nvt 0.5332 0.2742\r\nvt 0.5369 0.2741\r\nvt 0.0947 0.3564\r\nvt 0.0948 0.3545\r\nvt 0.091 0.3563\r\nvt 0.091 0.3544\r\nvt 0.0911 0.3524\r\nvt 0.0948 0.3525\r\nvt 0.0948 0.3518\r\nvt 0.0911 0.3517\r\nvt 0.0911 0.351\r\nvt 0.0949 0.3512\r\nvt 0.5297 0.2794\r\nvt 0.5296 0.2775\r\nvt 0.526 0.2795\r\nvt 0.5259 0.2776\r\nvt 0.5259 0.2756\r\nvt 0.5296 0.2755\r\nvt 0.5296 0.2749\r\nvt 0.5259 0.275\r\nvt 0.5258 0.2744\r\nvt 0.5296 0.2743\r\nvt 0.0872 0.3562\r\nvt 0.0872 0.3542\r\nvt 0.0834 0.3561\r\nvt 0.0835 0.3541\r\nvt 0.0835 0.3522\r\nvt 0.0873 0.3523\r\nvt 0.0873 0.3516\r\nvt 0.0835 0.3515\r\nvt 0.0836 0.3508\r\nvt 0.0874 0.3509\r\nvt 0.5223 0.2796\r\nvt 0.5222 0.2777\r\nvt 0.5185 0.2798\r\nvt 0.5184 0.2778\r\nvt 0.5184 0.2759\r\nvt 0.5221 0.2757\r\nvt 0.5221 0.2751\r\nvt 0.5184 0.2752\r\nvt 0.5183 0.2747\r\nvt 0.5221 0.2745\r\nvt 0.0796 0.356\r\nvt 0.0797 0.354\r\nvt 0.0758 0.3558\r\nvt 0.0758 0.3539\r\nvt 0.0759 0.3519\r\nvt 0.0797 0.352\r\nvt 0.0797 0.3513\r\nvt 0.0759 0.3512\r\nvt 0.0759 0.3505\r\nvt 0.0798 0.3507\r\nvt 0.5147 0.2799\r\nvt 0.5146 0.2779\r\nvt 0.5109 0.28\r\nvt 0.5108 0.278\r\nvt 0.5108 0.2761\r\nvt 0.5146 0.276\r\nvt 0.5146 0.2753\r\nvt 0.5108 0.2754\r\nvt 0.5107 0.2749\r\nvt 0.5146 0.2748\r\nvt 0.0719 0.3557\r\nvt 0.072 0.3537\r\nvt 0.0681 0.3556\r\nvt 0.0682 0.3536\r\nvt 0.0682 0.3517\r\nvt 0.0721 0.3518\r\nvt 0.0721 0.3511\r\nvt 0.0683 0.351\r\nvt 0.0683 0.3503\r\nvt 0.0721 0.3504\r\nvt 0.5071 0.2801\r\nvt 0.507 0.2781\r\nvt 0.5032 0.2802\r\nvt 0.5032 0.2783\r\nvt 0.5031 0.2763\r\nvt 0.507 0.2762\r\nvt 0.5069 0.2755\r\nvt 0.5031 0.2756\r\nvt 0.5031 0.2751\r\nvt 0.5069 0.275\r\nvt 0.0642 0.3554\r\nvt 0.0643 0.3535\r\nvt 0.0604 0.3553\r\nvt 0.0605 0.3534\r\nvt 0.0605 0.3514\r\nvt 0.0644 0.3515\r\nvt 0.0644 0.3508\r\nvt 0.0605 0.3507\r\nvt 0.0606 0.35\r\nvt 0.0644 0.3502\r\nvt 0.4994 0.2803\r\nvt 0.4993 0.2784\r\nvt 0.4955 0.2804\r\nvt 0.4954 0.2785\r\nvt 0.4954 0.2765\r\nvt 0.4993 0.2764\r\nvt 0.4992 0.2758\r\nvt 0.4954 0.2759\r\nvt 0.4953 0.2754\r\nvt 0.4992 0.2752\r\nvt 0.0565 0.3552\r\nvt 0.0566 0.3532\r\nvt 0.0527 0.3551\r\nvt 0.0528 0.3531\r\nvt 0.0528 0.3512\r\nvt 0.0567 0.3513\r\nvt 0.0567 0.3506\r\nvt 0.0529 0.3504\r\nvt 0.0529 0.3498\r\nvt 0.0567 0.3499\r\nvt 0.4916 0.2806\r\nvt 0.4916 0.2786\r\nvt 0.4878 0.2807\r\nvt 0.4877 0.2787\r\nvt 0.4876 0.2768\r\nvt 0.4915 0.2767\r\nvt 0.4915 0.276\r\nvt 0.4876 0.2761\r\nvt 0.4876 0.2756\r\nvt 0.4915 0.2755\r\nvt 0.0489 0.3549\r\nvt 0.049 0.353\r\nvt 0.0453 0.3548\r\nvt 0.0454 0.3528\r\nvt 0.0455 0.3509\r\nvt 0.0491 0.351\r\nvt 0.0491 0.3503\r\nvt 0.0456 0.3502\r\nvt 0.0456 0.3495\r\nvt 0.0492 0.3496\r\nvt 0.484 0.2808\r\nvt 0.484 0.2788\r\nvt 0.4804 0.2809\r\nvt 0.4804 0.279\r\nvt 0.4803 0.277\r\nvt 0.4839 0.2769\r\nvt 0.4839 0.2762\r\nvt 0.4802 0.2764\r\nvt 0.4802 0.2758\r\nvt 0.4838 0.2757\r\nvt 0.0419 0.3546\r\nvt 0.042 0.3527\r\nvt 0.0388 0.3544\r\nvt 0.0389 0.3525\r\nvt 0.039 0.3505\r\nvt 0.0421 0.3507\r\nvt 0.0422 0.35\r\nvt 0.039 0.3498\r\nvt 0.0391 0.3492\r\nvt 0.0422 0.3493\r\nvt 0.477 0.2811\r\nvt 0.4769 0.2791\r\nvt 0.4739 0.2812\r\nvt 0.4738 0.2793\r\nvt 0.4737 0.2773\r\nvt 0.4769 0.2772\r\nvt 0.4768 0.2765\r\nvt 0.4736 0.2767\r\nvt 0.4736 0.2761\r\nvt 0.4768 0.276\r\nvt 0.0359 0.3543\r\nvt 0.036 0.3523\r\nvt 0.0334 0.3541\r\nvt 0.0335 0.3522\r\nvt 0.0337 0.3502\r\nvt 0.0362 0.3504\r\nvt 0.0362 0.3497\r\nvt 0.0337 0.3495\r\nvt 0.0338 0.3489\r\nvt 0.0362 0.349\r\nvt 0.471 0.2814\r\nvt 0.4709 0.2794\r\nvt 0.4685 0.2815\r\nvt 0.4684 0.2796\r\nvt 0.4683 0.2776\r\nvt 0.4708 0.2775\r\nvt 0.4708 0.2768\r\nvt 0.4682 0.277\r\nvt 0.4682 0.2764\r\nvt 0.4707 0.2763\r\nvt 0.0313 0.354\r\nvt 0.0314 0.352\r\nvt 0.0296 0.3539\r\nvt 0.0297 0.3519\r\nvt 0.0298 0.35\r\nvt 0.0316 0.3501\r\nvt 0.0316 0.3494\r\nvt 0.0299 0.3493\r\nvt 0.0299 0.3486\r\nvt 0.0317 0.3487\r\nvt 0.4664 0.2817\r\nvt 0.4663 0.2797\r\nvt 0.4646 0.2818\r\nvt 0.4645 0.2798\r\nvt 0.4644 0.2779\r\nvt 0.4661 0.2778\r\nvt 0.4661 0.2771\r\nvt 0.4643 0.2772\r\nvt 0.4643 0.2767\r\nvt 0.4661 0.2766\r\nvt 0.0282 0.3538\r\nvt 0.0283 0.3518\r\nvt 0.027 0.3537\r\nvt 0.0271 0.3517\r\nvt 0.0272 0.3498\r\nvt 0.0284 0.3499\r\nvt 0.0284 0.3492\r\nvt 0.0273 0.3491\r\nvt 0.0273 0.3484\r\nvt 0.0285 0.3485\r\nvt 0.4631 0.2819\r\nvt 0.463 0.2799\r\nvt 0.4619 0.282\r\nvt 0.4618 0.28\r\nvt 0.4616 0.2781\r\nvt 0.4629 0.278\r\nvt 0.4628 0.2773\r\nvt 0.4616 0.2774\r\nvt 0.4616 0.2769\r\nvt 0.4628 0.2768\r\nvt 0.0261 0.3537\r\nvt 0.0261 0.3517\r\nvt 0.0253 0.3536\r\nvt 0.0253 0.3517\r\nvt 0.0254 0.3497\r\nvt 0.0262 0.3497\r\nvt 0.0263 0.349\r\nvt 0.0254 0.349\r\nvt 0.0255 0.3483\r\nvt 0.0263 0.3484\r\nvt 0.4608 0.282\r\nvt 0.4607 0.2801\r\nvt 0.4598 0.282\r\nvt 0.4598 0.2801\r\nvt 0.4597 0.2782\r\nvt 0.4606 0.2781\r\nvt 0.4606 0.2775\r\nvt 0.4597 0.2775\r\nvt 0.4597 0.277\r\nvt 0.4606 0.2769\r\nvt 0.0246 0.3536\r\nvt 0.0247 0.3517\r\nvt 0.024 0.3536\r\nvt 0.024 0.3517\r\nvt 0.024 0.3497\r\nvt 0.0247 0.3497\r\nvt 0.0247 0.349\r\nvt 0.024 0.349\r\nvt 0.024 0.3483\r\nvt 0.0247 0.3483\r\nvt 0.459 0.2821\r\nvt 0.459 0.2801\r\nvt 0.4582 0.2821\r\nvt 0.4582 0.2801\r\nvt 0.4582 0.2782\r\nvt 0.4589 0.2782\r\nvt 0.4589 0.2775\r\nvt 0.4582 0.2775\r\nvt 0.4582 0.277\r\nvt 0.4589 0.277\r\nvt 0.0234 0.3536\r\nvt 0.0234 0.3517\r\nvt 0.0228 0.3537\r\nvt 0.0227 0.3517\r\nvt 0.0226 0.3497\r\nvt 0.0233 0.3497\r\nvt 0.0233 0.349\r\nvt 0.0226 0.349\r\nvt 0.0226 0.3484\r\nvt 0.0233 0.3483\r\nvt 0.4574 0.2821\r\nvt 0.4574 0.2801\r\nvt 0.4566 0.282\r\nvt 0.4567 0.2801\r\nvt 0.4567 0.2781\r\nvt 0.4575 0.2782\r\nvt 0.4575 0.2775\r\nvt 0.4568 0.2775\r\nvt 0.4568 0.277\r\nvt 0.4575 0.277\r\nvt 0.0221 0.3537\r\nvt 0.022 0.3517\r\nvt 0.0214 0.3537\r\nvt 0.0213 0.3518\r\nvt 0.0211 0.3498\r\nvt 0.0219 0.3498\r\nvt 0.0219 0.3491\r\nvt 0.0211 0.3491\r\nvt 0.021 0.3484\r\nvt 0.0218 0.3484\r\nvt 0.4558 0.282\r\nvt 0.4558 0.28\r\nvt 0.4549 0.2819\r\nvt 0.455 0.28\r\nvt 0.4552 0.2781\r\nvt 0.456 0.2781\r\nvt 0.456 0.2774\r\nvt 0.4552 0.2774\r\nvt 0.4552 0.2769\r\nvt 0.456 0.2769\r\nvt 0.0206 0.3538\r\nvt 0.0205 0.3518\r\nvt 0.0197 0.3539\r\nvt 0.0196 0.3519\r\nvt 0.0194 0.35\r\nvt 0.0203 0.3499\r\nvt 0.0202 0.3492\r\nvt 0.0193 0.3493\r\nvt 0.0192 0.3486\r\nvt 0.0202 0.3485\r\nvt 0.454 0.2819\r\nvt 0.4541 0.2799\r\nvt 0.453 0.2818\r\nvt 0.4532 0.2798\r\nvt 0.4533 0.2779\r\nvt 0.4543 0.278\r\nvt 0.4543 0.2773\r\nvt 0.4534 0.2772\r\nvt 0.4535 0.2767\r\nvt 0.4544 0.2768\r\nvt 0.0188 0.3539\r\nvt 0.0186 0.352\r\nvt 0.0177 0.3541\r\nvt 0.0175 0.3521\r\nvt 0.0172 0.3502\r\nvt 0.0183 0.3501\r\nvt 0.0183 0.3494\r\nvt 0.0171 0.3495\r\nvt 0.0171 0.3488\r\nvt 0.0182 0.3487\r\nvt 0.4519 0.2817\r\nvt 0.4521 0.2797\r\nvt 0.4508 0.2816\r\nvt 0.451 0.2796\r\nvt 0.4512 0.2777\r\nvt 0.4523 0.2778\r\nvt 0.4524 0.2771\r\nvt 0.4513 0.277\r\nvt 0.4513 0.2765\r\nvt 0.4524 0.2766\r\nvt 0.0164 0.3542\r\nvt 0.0162 0.3523\r\nvt 0.0151 0.3544\r\nvt 0.0149 0.3524\r\nvt 0.0146 0.3505\r\nvt 0.016 0.3503\r\nvt 0.0159 0.3496\r\nvt 0.0145 0.3498\r\nvt 0.0145 0.3491\r\nvt 0.0158 0.349\r\nvt 0.4495 0.2814\r\nvt 0.4497 0.2795\r\nvt 0.4481 0.2812\r\nvt 0.4483 0.2793\r\nvt 0.4486 0.2774\r\nvt 0.4499 0.2775\r\nvt 0.45 0.2769\r\nvt 0.4487 0.2767\r\nvt 0.4487 0.2762\r\nvt 0.4501 0.2764\r\nvt 0.0137 0.3546\r\nvt 0.0134 0.3526\r\nvt 0.0122 0.3548\r\nvt 0.0119 0.3528\r\nvt 0.0116 0.3509\r\nvt 0.0132 0.3507\r\nvt 0.0131 0.35\r\nvt 0.0115 0.3502\r\nvt 0.0115 0.3495\r\nvt 0.013 0.3493\r\nvt 0.4466 0.2811\r\nvt 0.4469 0.2791\r\nvt 0.445 0.2809\r\nvt 0.4453 0.2789\r\nvt 0.4456 0.277\r\nvt 0.4471 0.2772\r\nvt 0.4472 0.2765\r\nvt 0.4456 0.2763\r\nvt 0.4457 0.2758\r\nvt 0.4473 0.276\r\nvt 0.0106 0.355\r\nvt 0.0103 0.353\r\nvt 0.009 0.3553\r\nvt 0.0087 0.3533\r\nvt 0.0084 0.3513\r\nvt 0.01 0.3511\r\nvt 0.0099 0.3504\r\nvt 0.0082 0.3506\r\nvt 0.0081 0.3499\r\nvt 0.0098 0.3497\r\nvt 0.4434 0.2806\r\nvt 0.4437 0.2787\r\nvt 0.4417 0.2804\r\nvt 0.442 0.2785\r\nvt 0.4422 0.2765\r\nvt 0.4439 0.2768\r\nvt 0.444 0.2761\r\nvt 0.4423 0.2758\r\nvt 0.4424 0.2753\r\nvt 0.4441 0.2756\r\nvt 0.0073 0.3555\r\nvt 0.007 0.3535\r\nvt 0.0056 0.3558\r\nvt 0.0053 0.3538\r\nvt 0.0049 0.3519\r\nvt 0.0066 0.3516\r\nvt 0.0065 0.3509\r\nvt 0.0047 0.3512\r\nvt 0.0045 0.3505\r\nvt 0.0063 0.3502\r\nvt 0.4399 0.2802\r\nvt 0.4402 0.2782\r\nvt 0.4381 0.2799\r\nvt 0.4385 0.2779\r\nvt 0.4388 0.276\r\nvt 0.4405 0.2762\r\nvt 0.4406 0.2756\r\nvt 0.4388 0.2753\r\nvt 0.4388 0.2747\r\nvt 0.4407 0.275\r\nvt 0.0039 0.3559\r\nvt 0.0037 0.3541\r\nvt 0.0029 0.3555\r\nvt 0.0024 0.3544\r\nvt 0.0025 0.353\r\nvt 0.0032 0.3524\r\nvt 0.003 0.3517\r\nvt 0.002 0.3527\r\nvt 0.0014 0.3524\r\nvt 0.0026 0.3513\r\nvt 0.4364 0.2794\r\nvt 0.4368 0.2776\r\nvt 0.4356 0.2787\r\nvt 0.4354 0.2774\r\nvt 0.436 0.2762\r\nvt 0.437 0.2758\r\nvt 0.437 0.2752\r\nvt 0.4357 0.2757\r\nvt 0.4354 0.2753\r\nvt 0.4369 0.2747\r\nvt 0.4351 0.2791\r\nvt 0.435 0.2773\r\nvt 0.4347 0.2793\r\nvt 0.4345 0.2772\r\nvt 0.1477 0.0067\r\nvt 0.1471 0.0069\r\nvt 0.1465 0.004\r\nvt 0.1471 0.0037\r\nvt 0.1454 0.0022\r\nvt 0.1457 0.0017\r\nvt 0.1397 0.0078\r\nvt 0.1395 0.0044\r\nvt 0.5746 0.6192\r\nvt 0.5744 0.6223\r\nvt 0.5732 0.6224\r\nvt 0.5734 0.6191\r\nvt 0.5742 0.6253\r\nvt 0.1406 0.0015\r\nvt 0.1486 0.0133\r\nvt 0.1479 0.0134\r\nvt 0.1475 0.01\r\nvt 0.1481 0.0099\r\nvt 0.1408 0.0145\r\nvt 0.1402 0.0111\r\nvt 0.5756 0.6126\r\nvt 0.5751 0.616\r\nvt 0.5739 0.6158\r\nvt 0.5744 0.6124\r\nvt 0.1497 0.0205\r\nvt 0.149 0.0206\r\nvt 0.1485 0.017\r\nvt 0.1491 0.0169\r\nvt 0.1418 0.0217\r\nvt 0.1413 0.018\r\nvt 0.5766 0.6054\r\nvt 0.5761 0.609\r\nvt 0.5749 0.6089\r\nvt 0.5754 0.6052\r\nvt 0.1507 0.0279\r\nvt 0.1501 0.028\r\nvt 0.1495 0.0243\r\nvt 0.1502 0.0242\r\nvt 0.1429 0.029\r\nvt 0.1424 0.0254\r\nvt 0.5776 0.598\r\nvt 0.5771 0.6016\r\nvt 0.5759 0.6015\r\nvt 0.5765 0.5978\r\nvt 0.1517 0.0348\r\nvt 0.1511 0.0349\r\nvt 0.1506 0.0315\r\nvt 0.1513 0.0315\r\nvt 0.1439 0.0359\r\nvt 0.1434 0.0326\r\nvt 0.5786 0.5911\r\nvt 0.5781 0.5944\r\nvt 0.577 0.5943\r\nvt 0.5774 0.5909\r\nvt 0.1526 0.0406\r\nvt 0.1519 0.0407\r\nvt 0.1515 0.038\r\nvt 0.1522 0.0379\r\nvt 0.1447 0.0417\r\nvt 0.1444 0.039\r\nvt 0.5794 0.5853\r\nvt 0.579 0.588\r\nvt 0.5779 0.5878\r\nvt 0.5782 0.5851\r\nvt 0.1531 0.0447\r\nvt 0.1525 0.0448\r\nvt 0.1522 0.0429\r\nvt 0.1529 0.0429\r\nvt 0.1453 0.0458\r\nvt 0.1451 0.044\r\nvt 0.58 0.5811\r\nvt 0.5797 0.583\r\nvt 0.5786 0.5828\r\nvt 0.5788 0.5809\r\nvt 0.1535 0.0476\r\nvt 0.1529 0.0477\r\nvt 0.1527 0.0464\r\nvt 0.1534 0.0463\r\nvt 0.1457 0.0487\r\nvt 0.1455 0.0474\r\nvt 0.5804 0.5781\r\nvt 0.5802 0.5795\r\nvt 0.579 0.5793\r\nvt 0.5792 0.5779\r\nvt 0.1538 0.0495\r\nvt 0.1532 0.0496\r\nvt 0.153 0.0487\r\nvt 0.1537 0.0486\r\nvt 0.146 0.0507\r\nvt 0.1459 0.0498\r\nvt 0.5807 0.576\r\nvt 0.5806 0.577\r\nvt 0.5794 0.5768\r\nvt 0.5796 0.5758\r\nvt 0.1541 0.051\r\nvt 0.1534 0.0512\r\nvt 0.1533 0.0504\r\nvt 0.154 0.0503\r\nvt 0.1463 0.0523\r\nvt 0.1461 0.0515\r\nvt 0.581 0.5743\r\nvt 0.5809 0.5751\r\nvt 0.5797 0.575\r\nvt 0.5798 0.5742\r\nvt 0.1543 0.0526\r\nvt 0.1537 0.0527\r\nvt 0.1535 0.0519\r\nvt 0.1542 0.0518\r\nvt 0.1465 0.0538\r\nvt 0.1464 0.053\r\nvt 0.5812 0.5727\r\nvt 0.5811 0.5735\r\nvt 0.5799 0.5733\r\nvt 0.5801 0.5725\r\nvt 0.1546 0.0545\r\nvt 0.154 0.0546\r\nvt 0.1538 0.0536\r\nvt 0.1545 0.0535\r\nvt 0.1468 0.0557\r\nvt 0.1466 0.0547\r\nvt 0.5816 0.5706\r\nvt 0.5814 0.5717\r\nvt 0.5802 0.5715\r\nvt 0.5804 0.5704\r\nvt 0.155 0.0572\r\nvt 0.1544 0.0573\r\nvt 0.1541 0.0559\r\nvt 0.1548 0.0558\r\nvt 0.1472 0.0584\r\nvt 0.147 0.057\r\nvt 0.582 0.5678\r\nvt 0.5818 0.5693\r\nvt 0.5806 0.5692\r\nvt 0.5808 0.5676\r\nvt 0.1556 0.0612\r\nvt 0.155 0.0613\r\nvt 0.1546 0.0591\r\nvt 0.1553 0.059\r\nvt 0.1478 0.0623\r\nvt 0.1475 0.0602\r\nvt 0.5826 0.5639\r\nvt 0.5823 0.566\r\nvt 0.5811 0.5658\r\nvt 0.5814 0.5637\r\nvt 0.1564 0.0666\r\nvt 0.1558 0.0667\r\nvt 0.1553 0.0638\r\nvt 0.156 0.0637\r\nvt 0.1486 0.0677\r\nvt 0.1482 0.0648\r\nvt 0.5834 0.5585\r\nvt 0.583 0.5613\r\nvt 0.5818 0.5612\r\nvt 0.5822 0.5583\r\nvt 0.1574 0.0731\r\nvt 0.1567 0.0732\r\nvt 0.1562 0.0698\r\nvt 0.1569 0.0697\r\nvt 0.1495 0.0742\r\nvt 0.1491 0.0709\r\nvt 0.5843 0.5519\r\nvt 0.5838 0.5553\r\nvt 0.5826 0.5551\r\nvt 0.5831 0.5517\r\nvt 0.1584 0.0805\r\nvt 0.1577 0.0806\r\nvt 0.1572 0.0768\r\nvt 0.1579 0.0767\r\nvt 0.1506 0.0816\r\nvt 0.15 0.0778\r\nvt 0.5853 0.5446\r\nvt 0.5848 0.5483\r\nvt 0.5836 0.5482\r\nvt 0.5841 0.5444\r\nvt 0.1594 0.0882\r\nvt 0.1588 0.0883\r\nvt 0.1582 0.0844\r\nvt 0.1589 0.0843\r\nvt 0.1516 0.0892\r\nvt 0.1511 0.0854\r\nvt 0.5864 0.5369\r\nvt 0.5858 0.5408\r\nvt 0.5847 0.5406\r\nvt 0.5852 0.5367\r\nvt 0.1605 0.0959\r\nvt 0.1598 0.096\r\nvt 0.1593 0.0921\r\nvt 0.1599 0.092\r\nvt 0.1526 0.0969\r\nvt 0.1521 0.0931\r\nvt 0.5874 0.5292\r\nvt 0.5869 0.533\r\nvt 0.5857 0.5329\r\nvt 0.5862 0.529\r\nvt 0.1615 0.1035\r\nvt 0.1608 0.1036\r\nvt 0.1603 0.0998\r\nvt 0.161 0.0997\r\nvt 0.1536 0.1046\r\nvt 0.1531 0.1008\r\nvt 0.5884 0.5216\r\nvt 0.5879 0.5254\r\nvt 0.5867 0.5252\r\nvt 0.5872 0.5214\r\nvt 0.1625 0.1112\r\nvt 0.1618 0.1112\r\nvt 0.1613 0.1074\r\nvt 0.162 0.1074\r\nvt 0.1546 0.1122\r\nvt 0.1541 0.1084\r\nvt 0.5894 0.514\r\nvt 0.5889 0.5178\r\nvt 0.5877 0.5176\r\nvt 0.5882 0.5138\r\nvt 0.1635 0.1187\r\nvt 0.1628 0.1188\r\nvt 0.1623 0.115\r\nvt 0.163 0.1149\r\nvt 0.1556 0.1197\r\nvt 0.1551 0.116\r\nvt 0.5903 0.5065\r\nvt 0.5899 0.5103\r\nvt 0.5887 0.5101\r\nvt 0.5892 0.5064\r\nvt 0.1645 0.1261\r\nvt 0.1638 0.1262\r\nvt 0.1633 0.1225\r\nvt 0.164 0.1224\r\nvt 0.1566 0.1272\r\nvt 0.1561 0.1235\r\nvt 0.5913 0.4992\r\nvt 0.5908 0.5028\r\nvt 0.5896 0.5027\r\nvt 0.5901 0.499\r\nvt 0.1655 0.1335\r\nvt 0.1648 0.1336\r\nvt 0.1643 0.1299\r\nvt 0.165 0.1298\r\nvt 0.1576 0.1345\r\nvt 0.1571 0.1309\r\nvt 0.5923 0.4919\r\nvt 0.5918 0.4955\r\nvt 0.5906 0.4954\r\nvt 0.5911 0.4917\r\nvt 0.1665 0.1407\r\nvt 0.1658 0.1408\r\nvt 0.1653 0.1372\r\nvt 0.166 0.1371\r\nvt 0.1586 0.1418\r\nvt 0.1581 0.1382\r\nvt 0.5933 0.4847\r\nvt 0.5928 0.4883\r\nvt 0.5916 0.4881\r\nvt 0.5921 0.4846\r\nvt 0.1675 0.1479\r\nvt 0.1668 0.148\r\nvt 0.1663 0.1444\r\nvt 0.167 0.1443\r\nvt 0.1597 0.149\r\nvt 0.1591 0.1454\r\nvt 0.5943 0.4777\r\nvt 0.5938 0.4812\r\nvt 0.5926 0.481\r\nvt 0.5931 0.4775\r\nvt 0.1685 0.1549\r\nvt 0.1679 0.155\r\nvt 0.1673 0.1515\r\nvt 0.168 0.1514\r\nvt 0.1607 0.1561\r\nvt 0.1602 0.1526\r\nvt 0.5953 0.4707\r\nvt 0.5948 0.4742\r\nvt 0.5936 0.474\r\nvt 0.5941 0.4705\r\nvt 0.1697 0.1617\r\nvt 0.169 0.1618\r\nvt 0.1684 0.1584\r\nvt 0.1691 0.1583\r\nvt 0.1619 0.1631\r\nvt 0.1613 0.1596\r\nvt 0.5964 0.4639\r\nvt 0.5959 0.4673\r\nvt 0.5947 0.4671\r\nvt 0.5952 0.4637\r\nvt 0.1709 0.1682\r\nvt 0.1702 0.1684\r\nvt 0.1696 0.1651\r\nvt 0.1703 0.165\r\nvt 0.1632 0.1698\r\nvt 0.1625 0.1665\r\nvt 0.5976 0.4573\r\nvt 0.597 0.4606\r\nvt 0.5958 0.4604\r\nvt 0.5965 0.4571\r\nvt 0.1722 0.1745\r\nvt 0.1716 0.1746\r\nvt 0.1709 0.1716\r\nvt 0.1716 0.1714\r\nvt 0.1646 0.1763\r\nvt 0.1638 0.1731\r\nvt 0.599 0.451\r\nvt 0.5983 0.4542\r\nvt 0.5971 0.4539\r\nvt 0.5978 0.4508\r\nvt 0.1737 0.1804\r\nvt 0.1731 0.1806\r\nvt 0.1723 0.1777\r\nvt 0.173 0.1775\r\nvt 0.1661 0.1824\r\nvt 0.1653 0.1794\r\nvt 0.6005 0.4451\r\nvt 0.5997 0.448\r\nvt 0.5986 0.4477\r\nvt 0.5993 0.4448\r\nvt 0.1752 0.1858\r\nvt 0.1746 0.186\r\nvt 0.1738 0.1833\r\nvt 0.1745 0.1832\r\nvt 0.1676 0.1879\r\nvt 0.1668 0.1853\r\nvt 0.602 0.4398\r\nvt 0.6012 0.4424\r\nvt 0.6001 0.442\r\nvt 0.6008 0.4394\r\nvt 0.1766 0.1909\r\nvt 0.1759 0.1911\r\nvt 0.1753 0.1886\r\nvt 0.1759 0.1884\r\nvt 0.1689 0.1926\r\nvt 0.1683 0.1904\r\nvt 0.6032 0.4351\r\nvt 0.6026 0.4374\r\nvt 0.6015 0.437\r\nvt 0.602 0.4349\r\nvt 0.1695 0.1965\r\nvt 0.1693 0.1946\r\nvt 0.6039 0.4312\r\nvt 0.6037 0.4331\r\nvt 0.6025 0.4329\r\nvt 0.6027 0.4311\r\nvt 0.1695 0.2003\r\nvt 0.1696 0.1983\r\nvt 0.6039 0.4273\r\nvt 0.604 0.4293\r\nvt 0.6028 0.4293\r\nvt 0.6027 0.4274\r\nvt 0.1768 0.2058\r\nvt 0.1762 0.2057\r\nvt 0.1765 0.2032\r\nvt 0.1771 0.2033\r\nvt 0.1767 0.2008\r\nvt 0.1774 0.2008\r\nvt 0.169 0.2049\r\nvt 0.1693 0.2025\r\nvt 0.6033 0.4227\r\nvt 0.6036 0.4251\r\nvt 0.6024 0.4252\r\nvt 0.6021 0.4229\r\nvt 0.1762 0.211\r\nvt 0.1756 0.2109\r\nvt 0.1759 0.2083\r\nvt 0.1765 0.2084\r\nvt 0.1684 0.2102\r\nvt 0.1687 0.2075\r\nvt 0.6027 0.4176\r\nvt 0.603 0.4202\r\nvt 0.6018 0.4204\r\nvt 0.6015 0.4177\r\nvt 0.1758 0.2164\r\nvt 0.1751 0.2164\r\nvt 0.1753 0.2136\r\nvt 0.176 0.2137\r\nvt 0.1679 0.2159\r\nvt 0.1681 0.213\r\nvt 0.6023 0.4121\r\nvt 0.6025 0.4149\r\nvt 0.6013 0.415\r\nvt 0.6011 0.4122\r\nvt 0.1755 0.222\r\nvt 0.1749 0.2219\r\nvt 0.175 0.2191\r\nvt 0.1756 0.2192\r\nvt 0.1676 0.2217\r\nvt 0.1678 0.2188\r\nvt 0.602 0.4065\r\nvt 0.6021 0.4093\r\nvt 0.6009 0.4093\r\nvt 0.6008 0.4065\r\nvt 0.1755 0.2275\r\nvt 0.1748 0.2275\r\nvt 0.1748 0.2247\r\nvt 0.1755 0.2247\r\nvt 0.1676 0.2275\r\nvt 0.1676 0.2246\r\nvt 0.6019 0.4008\r\nvt 0.6019 0.4036\r\nvt 0.6007 0.4036\r\nvt 0.6007 0.4008\r\nvt 0.1755 0.2331\r\nvt 0.1749 0.2331\r\nvt 0.1748 0.2303\r\nvt 0.1755 0.2303\r\nvt 0.1676 0.2333\r\nvt 0.1676 0.2304\r\nvt 0.602 0.3952\r\nvt 0.6019 0.398\r\nvt 0.6007 0.398\r\nvt 0.6008 0.3952\r\nvt 0.1757 0.2386\r\nvt 0.175 0.2386\r\nvt 0.1749 0.2359\r\nvt 0.1756 0.2359\r\nvt 0.1678 0.2389\r\nvt 0.1677 0.2361\r\nvt 0.6021 0.3897\r\nvt 0.602 0.3924\r\nvt 0.6009 0.3924\r\nvt 0.6009 0.3897\r\nvt 0.1759 0.2439\r\nvt 0.1752 0.244\r\nvt 0.1751 0.2413\r\nvt 0.1758 0.2413\r\nvt 0.168 0.2443\r\nvt 0.1679 0.2416\r\nvt 0.6024 0.3844\r\nvt 0.6022 0.387\r\nvt 0.6011 0.387\r\nvt 0.6012 0.3843\r\nvt 0.1762 0.2491\r\nvt 0.1755 0.2491\r\nvt 0.1754 0.2466\r\nvt 0.176 0.2465\r\nvt 0.1683 0.2495\r\nvt 0.1682 0.2469\r\nvt 0.6026 0.3793\r\nvt 0.6025 0.3818\r\nvt 0.6013 0.3818\r\nvt 0.6014 0.3793\r\nvt 0.1764 0.254\r\nvt 0.1758 0.254\r\nvt 0.1756 0.2516\r\nvt 0.1763 0.2516\r\nvt 0.1686 0.2544\r\nvt 0.1684 0.252\r\nvt 0.6029 0.3745\r\nvt 0.6028 0.3769\r\nvt 0.6016 0.3768\r\nvt 0.6017 0.3744\r\nvt 0.1767 0.2585\r\nvt 0.176 0.2585\r\nvt 0.1759 0.2563\r\nvt 0.1766 0.2563\r\nvt 0.1688 0.2589\r\nvt 0.1687 0.2567\r\nvt 0.6031 0.37\r\nvt 0.603 0.3722\r\nvt 0.6018 0.3722\r\nvt 0.6019 0.37\r\nvt 0.1769 0.2627\r\nvt 0.1763 0.2627\r\nvt 0.1762 0.2607\r\nvt 0.1768 0.2606\r\nvt 0.1691 0.2631\r\nvt 0.1689 0.2611\r\nvt 0.6034 0.3659\r\nvt 0.6033 0.3679\r\nvt 0.6021 0.3679\r\nvt 0.6022 0.3659\r\nvt 0.1772 0.2666\r\nvt 0.1765 0.2666\r\nvt 0.1764 0.2647\r\nvt 0.1771 0.2647\r\nvt 0.1693 0.2671\r\nvt 0.1692 0.2651\r\nvt 0.6036 0.3621\r\nvt 0.6035 0.364\r\nvt 0.6023 0.3639\r\nvt 0.6024 0.362\r\nvt 0.1774 0.2704\r\nvt 0.1767 0.2704\r\nvt 0.1766 0.2685\r\nvt 0.1773 0.2685\r\nvt 0.1695 0.2708\r\nvt 0.1694 0.2689\r\nvt 0.6038 0.3584\r\nvt 0.6037 0.3602\r\nvt 0.6025 0.3602\r\nvt 0.6026 0.3583\r\nvt 0.1776 0.2741\r\nvt 0.177 0.2741\r\nvt 0.1768 0.2723\r\nvt 0.1775 0.2722\r\nvt 0.1697 0.2746\r\nvt 0.1696 0.2727\r\nvt 0.604 0.3548\r\nvt 0.6039 0.3566\r\nvt 0.6027 0.3565\r\nvt 0.6028 0.3547\r\nvt 0.1778 0.2779\r\nvt 0.1772 0.278\r\nvt 0.1771 0.276\r\nvt 0.1777 0.276\r\nvt 0.17 0.2784\r\nvt 0.1698 0.2765\r\nvt 0.6042 0.351\r\nvt 0.6041 0.3529\r\nvt 0.6029 0.3528\r\nvt 0.603 0.351\r\nvt 0.1781 0.2819\r\nvt 0.1774 0.2819\r\nvt 0.1773 0.2799\r\nvt 0.1779 0.2799\r\nvt 0.1702 0.2823\r\nvt 0.1701 0.2803\r\nvt 0.6044 0.3472\r\nvt 0.6043 0.3491\r\nvt 0.6031 0.3491\r\nvt 0.6032 0.3471\r\nvt 0.1783 0.2859\r\nvt 0.1776 0.2859\r\nvt 0.1775 0.2839\r\nvt 0.1782 0.2839\r\nvt 0.1704 0.2863\r\nvt 0.1703 0.2843\r\nvt 0.6046 0.3433\r\nvt 0.6045 0.3452\r\nvt 0.6034 0.3452\r\nvt 0.6035 0.3432\r\nvt 0.1785 0.29\r\nvt 0.1778 0.29\r\nvt 0.1777 0.288\r\nvt 0.1784 0.2879\r\nvt 0.1706 0.2904\r\nvt 0.1705 0.2883\r\nvt 0.6049 0.3392\r\nvt 0.6047 0.3413\r\nvt 0.6036 0.3412\r\nvt 0.6037 0.3392\r\nvt 0.1787 0.2943\r\nvt 0.178 0.2943\r\nvt 0.1779 0.2922\r\nvt 0.1786 0.2921\r\nvt 0.1708 0.2946\r\nvt 0.1707 0.2925\r\nvt 0.605 0.3351\r\nvt 0.6049 0.3372\r\nvt 0.6038 0.3371\r\nvt 0.6039 0.335\r\nvt 0.1789 0.2987\r\nvt 0.1782 0.2987\r\nvt 0.1781 0.2965\r\nvt 0.1788 0.2965\r\nvt 0.171 0.299\r\nvt 0.1709 0.2968\r\nvt 0.6052 0.3308\r\nvt 0.6051 0.333\r\nvt 0.604 0.3329\r\nvt 0.604 0.3308\r\nvt 0.179 0.3033\r\nvt 0.1784 0.3033\r\nvt 0.1783 0.301\r\nvt 0.1789 0.3009\r\nvt 0.1711 0.3035\r\nvt 0.1711 0.3012\r\nvt 0.6054 0.3264\r\nvt 0.6053 0.3286\r\nvt 0.6041 0.3286\r\nvt 0.6042 0.3263\r\nvt 0.1791 0.308\r\nvt 0.1785 0.308\r\nvt 0.1784 0.3056\r\nvt 0.1791 0.3056\r\nvt 0.1712 0.3082\r\nvt 0.1712 0.3058\r\nvt 0.6056 0.3218\r\nvt 0.6055 0.3241\r\nvt 0.6043 0.3241\r\nvt 0.6044 0.3218\r\nvt 0.1792 0.313\r\nvt 0.1785 0.313\r\nvt 0.1785 0.3105\r\nvt 0.1792 0.3105\r\nvt 0.1713 0.313\r\nvt 0.1713 0.3106\r\nvt 0.6057 0.317\r\nvt 0.6056 0.3194\r\nvt 0.6044 0.3194\r\nvt 0.6045 0.317\r\nvt 0.1791 0.3182\r\nvt 0.1785 0.3182\r\nvt 0.1785 0.3156\r\nvt 0.1792 0.3156\r\nvt 0.1712 0.318\r\nvt 0.1713 0.3155\r\nvt 0.6057 0.3121\r\nvt 0.6057 0.3146\r\nvt 0.6045 0.3146\r\nvt 0.6045 0.3121\r\nvt 0.1789 0.3237\r\nvt 0.1782 0.3236\r\nvt 0.1784 0.3209\r\nvt 0.179 0.3209\r\nvt 0.171 0.3232\r\nvt 0.1712 0.3206\r\nvt 0.6056 0.307\r\nvt 0.6056 0.3096\r\nvt 0.6044 0.3096\r\nvt 0.6044 0.3071\r\nvt 0.1784 0.3293\r\nvt 0.1778 0.3292\r\nvt 0.178 0.3264\r\nvt 0.1787 0.3265\r\nvt 0.1706 0.3284\r\nvt 0.1708 0.3258\r\nvt 0.6052 0.3018\r\nvt 0.6054 0.3044\r\nvt 0.6042 0.3045\r\nvt 0.604 0.3019\r\nvt 0.1776 0.3351\r\nvt 0.1769 0.335\r\nvt 0.1774 0.3321\r\nvt 0.1781 0.3322\r\nvt 0.1698 0.3337\r\nvt 0.1702 0.3311\r\nvt 0.6047 0.2965\r\nvt 0.605 0.2992\r\nvt 0.6038 0.2993\r\nvt 0.6035 0.2967\r\nvt 0.1761 0.3412\r\nvt 0.1755 0.3409\r\nvt 0.1763 0.3378\r\nvt 0.177 0.338\r\nvt 0.1688 0.3389\r\nvt 0.1693 0.3363\r\nvt 0.6037 0.2913\r\nvt 0.6042 0.294\r\nvt 0.6031 0.2942\r\nvt 0.6026 0.2916\r\nvt 0.1745 0.3448\r\nvt 0.1751 0.3449\r\nvt 0.1671 0.344\r\nvt 0.1682 0.3417\r\nvt 0.6018 0.2857\r\nvt 0.603 0.2884\r\nvt 0.602 0.2889\r\nvt 0.6008 0.2865\r\nvt 0.1436 0.0018\r\nvt 0.1435 0.0013\r\nvt 0.2158 0.4027\r\nvt 0.2179 0.4057\r\nvt 0.2169 0.4064\r\nvt 0.6472 0.2291\r\nvt 0.6493 0.2262\r\nvt 0.6502 0.2268\r\nvt 0.6482 0.2298\r\nvt 0.6513 0.2232\r\nvt 0.6523 0.2239\r\nvt 0.219 0.4094\r\nvt 0.22 0.4087\r\nvt 0.2093 0.4071\r\nvt 0.2099 0.4068\r\nvt 0.2119 0.4098\r\nvt 0.2114 0.4102\r\nvt 0.2125 0.4094\r\nvt 0.2146 0.4124\r\nvt 0.214 0.4128\r\nvt 0.2135 0.4132\r\nvt 0.2116 0.3966\r\nvt 0.2138 0.3996\r\nvt 0.6431 0.2351\r\nvt 0.6452 0.2321\r\nvt 0.6462 0.2328\r\nvt 0.6441 0.2357\r\nvt 0.2052 0.4011\r\nvt 0.2057 0.4007\r\nvt 0.2078 0.4037\r\nvt 0.2072 0.4041\r\nvt 0.2074 0.3904\r\nvt 0.2095 0.3935\r\nvt 0.639 0.2411\r\nvt 0.6411 0.2381\r\nvt 0.642 0.2387\r\nvt 0.64 0.2417\r\nvt 0.2009 0.395\r\nvt 0.2015 0.3946\r\nvt 0.2036 0.3976\r\nvt 0.203 0.398\r\nvt 0.203 0.3842\r\nvt 0.2052 0.3873\r\nvt 0.6347 0.2471\r\nvt 0.6369 0.2441\r\nvt 0.6378 0.2448\r\nvt 0.6357 0.2478\r\nvt 0.1966 0.3888\r\nvt 0.1971 0.3884\r\nvt 0.1993 0.3915\r\nvt 0.1988 0.3919\r\nvt 0.1986 0.378\r\nvt 0.2008 0.3811\r\nvt 0.6303 0.2532\r\nvt 0.6325 0.2502\r\nvt 0.6335 0.2509\r\nvt 0.6313 0.2539\r\nvt 0.1922 0.3826\r\nvt 0.1927 0.3822\r\nvt 0.195 0.3853\r\nvt 0.1944 0.3857\r\nvt 0.1941 0.3718\r\nvt 0.1963 0.3749\r\nvt 0.6258 0.2593\r\nvt 0.6281 0.2563\r\nvt 0.629 0.257\r\nvt 0.6267 0.26\r\nvt 0.1877 0.3765\r\nvt 0.1883 0.3761\r\nvt 0.1905 0.3791\r\nvt 0.19 0.3795\r\nvt 0.1896 0.3658\r\nvt 0.1918 0.3688\r\nvt 0.6212 0.2652\r\nvt 0.6235 0.2623\r\nvt 0.6245 0.263\r\nvt 0.6222 0.2659\r\nvt 0.1833 0.3706\r\nvt 0.1838 0.3702\r\nvt 0.186 0.3731\r\nvt 0.1855 0.3735\r\nvt 0.1851 0.36\r\nvt 0.1873 0.3629\r\nvt 0.6167 0.2707\r\nvt 0.619 0.268\r\nvt 0.6199 0.2687\r\nvt 0.6176 0.2714\r\nvt 0.1789 0.365\r\nvt 0.1794 0.3645\r\nvt 0.1816 0.3673\r\nvt 0.1811 0.3677\r\nvt 0.1805 0.3545\r\nvt 0.1829 0.3573\r\nvt 0.612 0.2759\r\nvt 0.6144 0.2733\r\nvt 0.6153 0.274\r\nvt 0.6129 0.2767\r\nvt 0.1747 0.3598\r\nvt 0.1752 0.3593\r\nvt 0.1773 0.3619\r\nvt 0.1768 0.3623\r\nvt 0.1764 0.3485\r\nvt 0.178 0.3514\r\nvt 0.6092 0.279\r\nvt 0.6102 0.2796\r\nvt 0.6087 0.2826\r\nvt 0.171 0.3554\r\nvt 0.1715 0.3549\r\nvt 0.1733 0.3569\r\nvt 0.1728 0.3574\r\nvt 0.1683 0.3535\r\nvt 0.1685 0.3528\r\nvt 0.1699 0.3535\r\nvt 0.1696 0.3541\r\nvt 0.1628 0.347\r\nvt 0.1652 0.3458\r\nvt 0.5972 0.2825\r\nvt 0.5997 0.2837\r\nvt 0.5991 0.2847\r\nvt 0.5968 0.2837\r\nvt 0.1653 0.3544\r\nvt 0.1652 0.3537\r\nvt 0.167 0.353\r\nvt 0.1671 0.3537\r\nvt 0.1583 0.3484\r\nvt 0.1606 0.3478\r\nvt 0.5925 0.2814\r\nvt 0.5949 0.2819\r\nvt 0.5946 0.283\r\nvt 0.5923 0.2826\r\nvt 0.1602 0.356\r\nvt 0.1601 0.3553\r\nvt 0.1628 0.3546\r\nvt 0.163 0.3552\r\nvt 0.1531 0.3494\r\nvt 0.1558 0.3489\r\nvt 0.5873 0.2806\r\nvt 0.59 0.281\r\nvt 0.5898 0.2822\r\nvt 0.5872 0.2818\r\nvt 0.1543 0.3571\r\nvt 0.1542 0.3565\r\nvt 0.1572 0.356\r\nvt 0.1573 0.3566\r\nvt 0.1472 0.35\r\nvt 0.1502 0.3497\r\nvt 0.5815 0.2801\r\nvt 0.5844 0.2804\r\nvt 0.5843 0.2815\r\nvt 0.5814 0.2813\r\nvt 0.148 0.3579\r\nvt 0.1479 0.3572\r\nvt 0.1511 0.3569\r\nvt 0.1512 0.3576\r\nvt 0.141 0.3505\r\nvt 0.1442 0.3503\r\nvt 0.5752 0.2799\r\nvt 0.5784 0.28\r\nvt 0.5783 0.2812\r\nvt 0.5752 0.2811\r\nvt 0.1414 0.3583\r\nvt 0.1413 0.3577\r\nvt 0.1447 0.3575\r\nvt 0.1447 0.3582\r\nvt 0.1344 0.3506\r\nvt 0.1377 0.3506\r\nvt 0.5687 0.2798\r\nvt 0.572 0.2798\r\nvt 0.572 0.281\r\nvt 0.5687 0.281\r\nvt 0.1345 0.3585\r\nvt 0.1345 0.3579\r\nvt 0.1379 0.3578\r\nvt 0.138 0.3585\r\nvt 0.1275 0.3507\r\nvt 0.131 0.3507\r\nvt 0.5619 0.2798\r\nvt 0.5653 0.2798\r\nvt 0.5653 0.281\r\nvt 0.5619 0.281\r\nvt 0.1275 0.3586\r\nvt 0.1275 0.3579\r\nvt 0.131 0.3579\r\nvt 0.131 0.3586\r\nvt 0.1205 0.3506\r\nvt 0.124 0.3506\r\nvt 0.555 0.2799\r\nvt 0.5585 0.2799\r\nvt 0.5585 0.2811\r\nvt 0.555 0.2811\r\nvt 0.1203 0.3585\r\nvt 0.1203 0.3578\r\nvt 0.1239 0.3579\r\nvt 0.1239 0.3585\r\nvt 0.1133 0.3504\r\nvt 0.1169 0.3505\r\nvt 0.5479 0.2801\r\nvt 0.5514 0.28\r\nvt 0.5515 0.2812\r\nvt 0.5479 0.2813\r\nvt 0.1131 0.3583\r\nvt 0.1131 0.3577\r\nvt 0.1167 0.3577\r\nvt 0.1167 0.3584\r\nvt 0.106 0.3502\r\nvt 0.1097 0.3503\r\nvt 0.5407 0.2803\r\nvt 0.5443 0.2802\r\nvt 0.5443 0.2814\r\nvt 0.5407 0.2815\r\nvt 0.1058 0.3581\r\nvt 0.1058 0.3575\r\nvt 0.1095 0.3576\r\nvt 0.1095 0.3582\r\nvt 0.0986 0.35\r\nvt 0.1023 0.3501\r\nvt 0.5334 0.2805\r\nvt 0.5371 0.2804\r\nvt 0.5371 0.2816\r\nvt 0.5335 0.2817\r\nvt 0.0984 0.3579\r\nvt 0.0984 0.3573\r\nvt 0.1021 0.3574\r\nvt 0.1021 0.358\r\nvt 0.0912 0.3498\r\nvt 0.0949 0.3499\r\nvt 0.526 0.2807\r\nvt 0.5297 0.2806\r\nvt 0.5298 0.2818\r\nvt 0.5261 0.2819\r\nvt 0.0909 0.3577\r\nvt 0.0909 0.357\r\nvt 0.0947 0.3571\r\nvt 0.0947 0.3578\r\nvt 0.0836 0.3496\r\nvt 0.0874 0.3497\r\nvt 0.5185 0.2809\r\nvt 0.5223 0.2808\r\nvt 0.5223 0.282\r\nvt 0.5186 0.2821\r\nvt 0.0834 0.3574\r\nvt 0.0834 0.3568\r\nvt 0.0872 0.3569\r\nvt 0.0871 0.3576\r\nvt 0.076 0.3493\r\nvt 0.0798 0.3494\r\nvt 0.5109 0.2812\r\nvt 0.5147 0.2811\r\nvt 0.5148 0.2822\r\nvt 0.511 0.2824\r\nvt 0.0757 0.3572\r\nvt 0.0757 0.3565\r\nvt 0.0796 0.3567\r\nvt 0.0795 0.3573\r\nvt 0.0683 0.3491\r\nvt 0.0722 0.3492\r\nvt 0.5033 0.2814\r\nvt 0.5071 0.2813\r\nvt 0.5071 0.2825\r\nvt 0.5033 0.2826\r\nvt 0.0681 0.3569\r\nvt 0.0681 0.3563\r\nvt 0.0719 0.3564\r\nvt 0.0719 0.3571\r\nvt 0.0606 0.3488\r\nvt 0.0645 0.3489\r\nvt 0.4955 0.2816\r\nvt 0.4994 0.2815\r\nvt 0.4994 0.2827\r\nvt 0.4956 0.2828\r\nvt 0.0603 0.3567\r\nvt 0.0604 0.356\r\nvt 0.0642 0.3562\r\nvt 0.0642 0.3568\r\nvt 0.0529 0.3485\r\nvt 0.0567 0.3487\r\nvt 0.4878 0.2819\r\nvt 0.4916 0.2817\r\nvt 0.4917 0.2829\r\nvt 0.4878 0.2831\r\nvt 0.0526 0.3564\r\nvt 0.0527 0.3558\r\nvt 0.0565 0.3559\r\nvt 0.0565 0.3566\r\nvt 0.0456 0.3482\r\nvt 0.0492 0.3484\r\nvt 0.4805 0.2821\r\nvt 0.4841 0.282\r\nvt 0.4841 0.2832\r\nvt 0.4805 0.2833\r\nvt 0.0453 0.3561\r\nvt 0.0453 0.3555\r\nvt 0.0489 0.3556\r\nvt 0.0489 0.3563\r\nvt 0.0391 0.3479\r\nvt 0.0423 0.3481\r\nvt 0.4739 0.2824\r\nvt 0.4771 0.2823\r\nvt 0.4771 0.2835\r\nvt 0.474 0.2836\r\nvt 0.0387 0.3558\r\nvt 0.0387 0.3551\r\nvt 0.0419 0.3553\r\nvt 0.0419 0.356\r\nvt 0.0338 0.3476\r\nvt 0.0363 0.3478\r\nvt 0.4686 0.2827\r\nvt 0.4711 0.2826\r\nvt 0.4712 0.2838\r\nvt 0.4687 0.2839\r\nvt 0.0333 0.3555\r\nvt 0.0334 0.3548\r\nvt 0.0359 0.355\r\nvt 0.0358 0.3556\r\nvt 0.03 0.3474\r\nvt 0.0318 0.3475\r\nvt 0.4647 0.283\r\nvt 0.4665 0.2829\r\nvt 0.4666 0.2841\r\nvt 0.4648 0.2842\r\nvt 0.0295 0.3552\r\nvt 0.0295 0.3546\r\nvt 0.0313 0.3547\r\nvt 0.0312 0.3553\r\nvt 0.0274 0.3472\r\nvt 0.0286 0.3473\r\nvt 0.4619 0.2832\r\nvt 0.4632 0.2831\r\nvt 0.4633 0.2843\r\nvt 0.462 0.2843\r\nvt 0.027 0.3551\r\nvt 0.027 0.3544\r\nvt 0.0281 0.3545\r\nvt 0.0281 0.3551\r\nvt 0.0255 0.3471\r\nvt 0.0264 0.3471\r\nvt 0.4598 0.2832\r\nvt 0.4608 0.2832\r\nvt 0.4608 0.2844\r\nvt 0.4599 0.2844\r\nvt 0.0253 0.355\r\nvt 0.0253 0.3544\r\nvt 0.0261 0.3544\r\nvt 0.0261 0.3551\r\nvt 0.024 0.3471\r\nvt 0.0247 0.3471\r\nvt 0.4582 0.2833\r\nvt 0.459 0.2832\r\nvt 0.459 0.2844\r\nvt 0.4582 0.2844\r\nvt 0.024 0.355\r\nvt 0.024 0.3544\r\nvt 0.0246 0.3544\r\nvt 0.0246 0.355\r\nvt 0.0225 0.3471\r\nvt 0.0233 0.3471\r\nvt 0.4565 0.2832\r\nvt 0.4574 0.2832\r\nvt 0.4573 0.2844\r\nvt 0.4565 0.2844\r\nvt 0.0228 0.355\r\nvt 0.0228 0.3544\r\nvt 0.0234 0.3544\r\nvt 0.0234 0.355\r\nvt 0.0209 0.3472\r\nvt 0.0217 0.3472\r\nvt 0.4548 0.2831\r\nvt 0.4557 0.2832\r\nvt 0.4556 0.2844\r\nvt 0.4547 0.2843\r\nvt 0.0215 0.3551\r\nvt 0.0214 0.3544\r\nvt 0.0221 0.3544\r\nvt 0.0222 0.3551\r\nvt 0.0191 0.3474\r\nvt 0.02 0.3473\r\nvt 0.4529 0.283\r\nvt 0.4539 0.2831\r\nvt 0.4538 0.2843\r\nvt 0.4528 0.2842\r\nvt 0.0198 0.3552\r\nvt 0.0198 0.3546\r\nvt 0.0207 0.3545\r\nvt 0.0207 0.3552\r\nvt 0.0169 0.3476\r\nvt 0.0181 0.3475\r\nvt 0.4506 0.2827\r\nvt 0.4518 0.2829\r\nvt 0.4517 0.2841\r\nvt 0.4505 0.2839\r\nvt 0.0178 0.3554\r\nvt 0.0177 0.3548\r\nvt 0.0188 0.3546\r\nvt 0.0189 0.3553\r\nvt 0.0143 0.3479\r\nvt 0.0157 0.3477\r\nvt 0.4479 0.2824\r\nvt 0.4493 0.2826\r\nvt 0.4492 0.2838\r\nvt 0.4478 0.2836\r\nvt 0.0153 0.3557\r\nvt 0.0152 0.3551\r\nvt 0.0165 0.3549\r\nvt 0.0166 0.3556\r\nvt 0.0113 0.3483\r\nvt 0.0129 0.3481\r\nvt 0.4449 0.282\r\nvt 0.4465 0.2822\r\nvt 0.4463 0.2834\r\nvt 0.4447 0.2832\r\nvt 0.0124 0.3561\r\nvt 0.0123 0.3555\r\nvt 0.0138 0.3553\r\nvt 0.0139 0.3559\r\nvt 0.0079 0.3487\r\nvt 0.0097 0.3485\r\nvt 0.4415 0.2816\r\nvt 0.4432 0.2818\r\nvt 0.4431 0.283\r\nvt 0.4413 0.2828\r\nvt 0.0091 0.3566\r\nvt 0.0091 0.356\r\nvt 0.0107 0.3557\r\nvt 0.0108 0.3564\r\nvt 0.0041 0.3494\r\nvt 0.0061 0.349\r\nvt 0.4378 0.281\r\nvt 0.4397 0.2813\r\nvt 0.4394 0.2825\r\nvt 0.4374 0.2821\r\nvt 0.0056 0.3572\r\nvt 0.0056 0.3565\r\nvt 0.0074 0.3562\r\nvt 0.0074 0.3569\r\nvt 0.0004 0.352\r\nvt 0.0019 0.3502\r\nvt 0.4359 0.2804\r\nvt 0.4352 0.2814\r\nvt 0.4337 0.2798\r\nvt 0.0022 0.3565\r\nvt 0.0026 0.3561\r\nvt 0.0039 0.3566\r\nvt 0.0037 0.3572\r\nvt 0.0018 0.3545\r\nvt 0.0012 0.3546\r\nvt 0.5822 0.6203\r\nvt 0.5815 0.6236\r\nvt 0.1482 0.0032\r\nvt 0.1489 0.0065\r\nvt 0.1464 0.0007\r\nvt 0.5798 0.626\r\nvt 0.583 0.6136\r\nvt 0.5826 0.617\r\nvt 0.1494 0.0097\r\nvt 0.1498 0.0131\r\nvt 0.584 0.6064\r\nvt 0.5835 0.6101\r\nvt 0.1503 0.0167\r\nvt 0.1509 0.0204\r\nvt 0.585 0.599\r\nvt 0.5845 0.6027\r\nvt 0.1514 0.0241\r\nvt 0.152 0.0277\r\nvt 0.586 0.5921\r\nvt 0.5855 0.5955\r\nvt 0.1525 0.0313\r\nvt 0.153 0.0346\r\nvt 0.5868 0.5863\r\nvt 0.5864 0.589\r\nvt 0.1534 0.0377\r\nvt 0.1538 0.0404\r\nvt 0.5874 0.5821\r\nvt 0.5871 0.584\r\nvt 0.1541 0.0427\r\nvt 0.1544 0.0446\r\nvt 0.5878 0.5792\r\nvt 0.5876 0.5805\r\nvt 0.1546 0.0461\r\nvt 0.1548 0.0474\r\nvt 0.5881 0.5771\r\nvt 0.588 0.5781\r\nvt 0.1549 0.0484\r\nvt 0.1551 0.0493\r\nvt 0.5884 0.5755\r\nvt 0.5882 0.5763\r\nvt 0.1552 0.0501\r\nvt 0.1553 0.0509\r\nvt 0.5886 0.5738\r\nvt 0.5885 0.5747\r\nvt 0.1554 0.0516\r\nvt 0.1555 0.0524\r\nvt 0.5889 0.5718\r\nvt 0.5888 0.5729\r\nvt 0.1557 0.0533\r\nvt 0.1558 0.0543\r\nvt 0.5894 0.5689\r\nvt 0.5891 0.5705\r\nvt 0.156 0.0556\r\nvt 0.1563 0.0571\r\nvt 0.59 0.565\r\nvt 0.5897 0.5671\r\nvt 0.1565 0.0588\r\nvt 0.1568 0.061\r\nvt 0.5908 0.5595\r\nvt 0.5904 0.5624\r\nvt 0.1572 0.0635\r\nvt 0.1576 0.0664\r\nvt 0.5917 0.5529\r\nvt 0.5912 0.5564\r\nvt 0.1581 0.0696\r\nvt 0.1586 0.073\r\nvt 0.5927 0.5456\r\nvt 0.5922 0.5493\r\nvt 0.1591 0.0766\r\nvt 0.1596 0.0803\r\nvt 0.5938 0.5379\r\nvt 0.5932 0.5418\r\nvt 0.1601 0.0841\r\nvt 0.1607 0.088\r\nvt 0.5948 0.5302\r\nvt 0.5943 0.534\r\nvt 0.1612 0.0919\r\nvt 0.1617 0.0957\r\nvt 0.5958 0.5225\r\nvt 0.5953 0.5263\r\nvt 0.1622 0.0996\r\nvt 0.1627 0.1034\r\nvt 0.5968 0.515\r\nvt 0.5963 0.5187\r\nvt 0.1632 0.1072\r\nvt 0.1637 0.111\r\nvt 0.5977 0.5075\r\nvt 0.5973 0.5112\r\nvt 0.1642 0.1148\r\nvt 0.1647 0.1185\r\nvt 0.5987 0.5002\r\nvt 0.5982 0.5038\r\nvt 0.1652 0.1223\r\nvt 0.1657 0.126\r\nvt 0.5997 0.4929\r\nvt 0.5992 0.4965\r\nvt 0.1662 0.1296\r\nvt 0.1667 0.1333\r\nvt 0.6007 0.4858\r\nvt 0.6002 0.4893\r\nvt 0.1672 0.1369\r\nvt 0.1677 0.1405\r\nvt 0.6017 0.4788\r\nvt 0.6012 0.4823\r\nvt 0.1682 0.1441\r\nvt 0.1687 0.1477\r\nvt 0.6027 0.4719\r\nvt 0.6022 0.4753\r\nvt 0.1692 0.1512\r\nvt 0.1698 0.1547\r\nvt 0.6038 0.4652\r\nvt 0.6032 0.4685\r\nvt 0.1703 0.1581\r\nvt 0.1709 0.1615\r\nvt 0.605 0.4588\r\nvt 0.6044 0.462\r\nvt 0.1715 0.1648\r\nvt 0.1721 0.168\r\nvt 0.6063 0.4527\r\nvt 0.6056 0.4557\r\nvt 0.1728 0.1712\r\nvt 0.1734 0.1742\r\nvt 0.6077 0.447\r\nvt 0.607 0.4498\r\nvt 0.1742 0.1772\r\nvt 0.1749 0.1801\r\nvt 0.6092 0.4418\r\nvt 0.6084 0.4443\r\nvt 0.1756 0.1828\r\nvt 0.1764 0.1855\r\nvt 0.6104 0.4369\r\nvt 0.6099 0.4393\r\nvt 0.1771 0.1881\r\nvt 0.1778 0.1905\r\nvt 0.6108 0.4218\r\nvt 0.6111 0.4243\r\nvt 0.1784 0.2034\r\nvt 0.1781 0.2059\r\nvt 0.1786 0.2009\r\nvt 0.6113 0.4268\r\nvt 0.6102 0.4168\r\nvt 0.6104 0.4194\r\nvt 0.1778 0.2085\r\nvt 0.1775 0.2111\r\nvt 0.6097 0.4116\r\nvt 0.6099 0.4142\r\nvt 0.1772 0.2138\r\nvt 0.177 0.2165\r\nvt 0.6095 0.4062\r\nvt 0.6096 0.4089\r\nvt 0.1769 0.2192\r\nvt 0.1768 0.222\r\nvt 0.6094 0.4008\r\nvt 0.6094 0.4035\r\nvt 0.1767 0.2248\r\nvt 0.1767 0.2275\r\nvt 0.6094 0.3953\r\nvt 0.6094 0.3981\r\nvt 0.1767 0.2303\r\nvt 0.1768 0.2331\r\nvt 0.6096 0.39\r\nvt 0.6095 0.3926\r\nvt 0.1768 0.2358\r\nvt 0.1769 0.2386\r\nvt 0.6098 0.3847\r\nvt 0.6097 0.3873\r\nvt 0.177 0.2412\r\nvt 0.1772 0.2439\r\nvt 0.6101 0.3797\r\nvt 0.61 0.3822\r\nvt 0.1773 0.2465\r\nvt 0.1774 0.249\r\nvt 0.6104 0.3749\r\nvt 0.6102 0.3773\r\nvt 0.1776 0.2515\r\nvt 0.1777 0.2539\r\nvt 0.6106 0.3705\r\nvt 0.6105 0.3727\r\nvt 0.1778 0.2562\r\nvt 0.1779 0.2584\r\nvt 0.6108 0.3664\r\nvt 0.6107 0.3684\r\nvt 0.1781 0.2606\r\nvt 0.1782 0.2626\r\nvt 0.6111 0.3625\r\nvt 0.6109 0.3644\r\nvt 0.1783 0.2646\r\nvt 0.1784 0.2665\r\nvt 0.6113 0.3588\r\nvt 0.6112 0.3607\r\nvt 0.1785 0.2684\r\nvt 0.1786 0.2703\r\nvt 0.6115 0.3552\r\nvt 0.6114 0.357\r\nvt 0.1787 0.2722\r\nvt 0.1789 0.274\r\nvt 0.6117 0.3514\r\nvt 0.6116 0.3533\r\nvt 0.179 0.2759\r\nvt 0.1791 0.2779\r\nvt 0.6119 0.3476\r\nvt 0.6118 0.3495\r\nvt 0.1792 0.2798\r\nvt 0.1793 0.2818\r\nvt 0.6121 0.3437\r\nvt 0.612 0.3456\r\nvt 0.1794 0.2838\r\nvt 0.1795 0.2858\r\nvt 0.6123 0.3396\r\nvt 0.6122 0.3416\r\nvt 0.1796 0.2879\r\nvt 0.1797 0.29\r\nvt 0.6125 0.3354\r\nvt 0.6124 0.3375\r\nvt 0.1798 0.2921\r\nvt 0.1799 0.2942\r\nvt 0.6127 0.3311\r\nvt 0.6126 0.3333\r\nvt 0.18 0.2964\r\nvt 0.1801 0.2986\r\nvt 0.6129 0.3267\r\nvt 0.6128 0.3289\r\nvt 0.1802 0.3009\r\nvt 0.1803 0.3032\r\nvt 0.613 0.322\r\nvt 0.613 0.3244\r\nvt 0.1803 0.3056\r\nvt 0.1804 0.308\r\nvt 0.6131 0.3171\r\nvt 0.6131 0.3196\r\nvt 0.1804 0.3105\r\nvt 0.1804 0.313\r\nvt 0.6131 0.312\r\nvt 0.6131 0.3146\r\nvt 0.1804 0.3156\r\nvt 0.1804 0.3183\r\nvt 0.613 0.3067\r\nvt 0.6131 0.3094\r\nvt 0.1803 0.321\r\nvt 0.1801 0.3237\r\nvt 0.6127 0.3012\r\nvt 0.6129 0.304\r\nvt 0.1799 0.3266\r\nvt 0.1797 0.3294\r\nvt 0.612 0.2954\r\nvt 0.6124 0.2983\r\nvt 0.1793 0.3324\r\nvt 0.1788 0.3353\r\nvt 0.6107 0.2895\r\nvt 0.6115 0.2926\r\nvt 0.1782 0.3383\r\nvt 0.1773 0.3416\r\nvt 0.6093 0.2859\r\nvt 0.1763 0.3453\r\nvt 0.5769 0.6267\r\nvt 0.1433 0\r\nvt 0.6411 0.2249\r\nvt 0.6431 0.2219\r\nvt 0.6441 0.2226\r\nvt 0.2104 0.4109\r\nvt 0.2083 0.4078\r\nvt 0.2124 0.4139\r\nvt 0.6461 0.2197\r\nvt 0.6451 0.219\r\nvt 0.637 0.2308\r\nvt 0.639 0.2278\r\nvt 0.2062 0.4048\r\nvt 0.2041 0.4018\r\nvt 0.6328 0.2368\r\nvt 0.6349 0.2338\r\nvt 0.202 0.3987\r\nvt 0.1999 0.3957\r\nvt 0.6286 0.2428\r\nvt 0.6307 0.2398\r\nvt 0.1978 0.3926\r\nvt 0.1956 0.3895\r\nvt 0.6243 0.2488\r\nvt 0.6265 0.2458\r\nvt 0.1934 0.3864\r\nvt 0.1912 0.3833\r\nvt 0.6198 0.2548\r\nvt 0.6221 0.2518\r\nvt 0.189 0.3803\r\nvt 0.1867 0.3772\r\nvt 0.6154 0.2605\r\nvt 0.6176 0.2577\r\nvt 0.1845 0.3742\r\nvt 0.1823 0.3713\r\nvt 0.611 0.2658\r\nvt 0.6132 0.2632\r\nvt 0.1801 0.3685\r\nvt 0.1779 0.3657\r\nvt 0.6067 0.2707\r\nvt 0.6088 0.2683\r\nvt 0.1758 0.3631\r\nvt 0.1738 0.3605\r\nvt 0.6031 0.2746\r\nvt 0.6048 0.2729\r\nvt 0.1718 0.3582\r\nvt 0.1702 0.3563\r\nvt 0.6011 0.2758\r\nvt 0.602 0.2757\r\nvt 0.169 0.3551\r\nvt 0.1681 0.3548\r\nvt 0.599 0.2754\r\nvt 0.6003 0.2759\r\nvt 0.1672 0.3549\r\nvt 0.1657 0.3555\r\nvt 0.594 0.2741\r\nvt 0.5967 0.2748\r\nvt 0.1634 0.3564\r\nvt 0.1605 0.3572\r\nvt 0.5882 0.2732\r\nvt 0.5911 0.2736\r\nvt 0.1576 0.3578\r\nvt 0.1545 0.3584\r\nvt 0.5819 0.2727\r\nvt 0.5851 0.2729\r\nvt 0.1513 0.3588\r\nvt 0.1481 0.3591\r\nvt 0.5754 0.2724\r\nvt 0.5787 0.2725\r\nvt 0.1448 0.3594\r\nvt 0.1414 0.3596\r\nvt 0.5687 0.2723\r\nvt 0.5721 0.2723\r\nvt 0.138 0.3597\r\nvt 0.1345 0.3598\r\nvt 0.5618 0.2723\r\nvt 0.5653 0.2723\r\nvt 0.131 0.3598\r\nvt 0.1275 0.3598\r\nvt 0.5548 0.2725\r\nvt 0.5583 0.2724\r\nvt 0.1239 0.3598\r\nvt 0.1203 0.3597\r\nvt 0.5477 0.2726\r\nvt 0.5513 0.2725\r\nvt 0.1167 0.3597\r\nvt 0.1131 0.3596\r\nvt 0.5405 0.2728\r\nvt 0.5441 0.2727\r\nvt 0.1094 0.3595\r\nvt 0.1058 0.3594\r\nvt 0.5332 0.273\r\nvt 0.5369 0.2729\r\nvt 0.1021 0.3593\r\nvt 0.0984 0.3592\r\nvt 0.5258 0.2732\r\nvt 0.5295 0.2731\r\nvt 0.0946 0.359\r\nvt 0.0909 0.3589\r\nvt 0.5183 0.2735\r\nvt 0.5221 0.2734\r\nvt 0.0871 0.3588\r\nvt 0.0833 0.3587\r\nvt 0.5107 0.2737\r\nvt 0.5145 0.2736\r\nvt 0.0795 0.3586\r\nvt 0.0757 0.3584\r\nvt 0.503 0.2739\r\nvt 0.5069 0.2738\r\nvt 0.0719 0.3583\r\nvt 0.068 0.3582\r\nvt 0.4953 0.2742\r\nvt 0.4992 0.274\r\nvt 0.0642 0.3581\r\nvt 0.0603 0.3579\r\nvt 0.4876 0.2744\r\nvt 0.4914 0.2743\r\nvt 0.0564 0.3578\r\nvt 0.0526 0.3577\r\nvt 0.4802 0.2746\r\nvt 0.4838 0.2745\r\nvt 0.0488 0.3575\r\nvt 0.0452 0.3574\r\nvt 0.4736 0.2749\r\nvt 0.4767 0.2748\r\nvt 0.0418 0.3572\r\nvt 0.0386 0.357\r\nvt 0.4681 0.2753\r\nvt 0.4707 0.2751\r\nvt 0.0358 0.3569\r\nvt 0.0333 0.3567\r\nvt 0.4642 0.2755\r\nvt 0.466 0.2754\r\nvt 0.0312 0.3566\r\nvt 0.0294 0.3565\r\nvt 0.4615 0.2757\r\nvt 0.4627 0.2756\r\nvt 0.028 0.3564\r\nvt 0.0269 0.3563\r\nvt 0.4597 0.2758\r\nvt 0.4605 0.2757\r\nvt 0.026 0.3563\r\nvt 0.0253 0.3563\r\nvt 0.4582 0.2758\r\nvt 0.4589 0.2758\r\nvt 0.0246 0.3563\r\nvt 0.0241 0.3563\r\nvt 0.4568 0.2758\r\nvt 0.4575 0.2758\r\nvt 0.0235 0.3563\r\nvt 0.0228 0.3563\r\nvt 0.4553 0.2757\r\nvt 0.4561 0.2757\r\nvt 0.0222 0.3563\r\nvt 0.0215 0.3563\r\nvt 0.4536 0.2755\r\nvt 0.4545 0.2756\r\nvt 0.0208 0.3564\r\nvt 0.0199 0.3565\r\nvt 0.4515 0.2753\r\nvt 0.4526 0.2754\r\nvt 0.019 0.3565\r\nvt 0.0179 0.3567\r\nvt 0.4489 0.275\r\nvt 0.4502 0.2752\r\nvt 0.0167 0.3568\r\nvt 0.0154 0.357\r\nvt 0.4459 0.2746\r\nvt 0.4474 0.2748\r\nvt 0.014 0.3571\r\nvt 0.0125 0.3574\r\nvt 0.4426 0.2741\r\nvt 0.4443 0.2744\r\nvt 0.011 0.3576\r\nvt 0.0093 0.3579\r\nvt 0.4388 0.2736\r\nvt 0.4408 0.2738\r\nvt 0.0076 0.3582\r\nvt 0.0056 0.3584\r\nvt 0.4346 0.2745\r\nvt 0.4366 0.2735\r\nvt 0.0033 0.3584\r\nvt 0.0013 0.3573\r\nvt 0.4333 0.277\r\nvt 0 0.3548\r\nvt 0.5752 0.6193\r\nvt 0.5749 0.6222\r\nvt 0.5761 0.6127\r\nvt 0.5756 0.6161\r\nvt 0.5771 0.6054\r\nvt 0.5766 0.6091\r\nvt 0.5782 0.598\r\nvt 0.5776 0.6017\r\nvt 0.5791 0.5911\r\nvt 0.5787 0.5945\r\nvt 0.5799 0.5853\r\nvt 0.5796 0.5881\r\nvt 0.5805 0.5811\r\nvt 0.5803 0.5831\r\nvt 0.5809 0.5782\r\nvt 0.5807 0.5795\r\nvt 0.5813 0.5761\r\nvt 0.5811 0.5771\r\nvt 0.5815 0.5744\r\nvt 0.5814 0.5752\r\nvt 0.5818 0.5727\r\nvt 0.5816 0.5736\r\nvt 0.5821 0.5707\r\nvt 0.5819 0.5718\r\nvt 0.5825 0.5679\r\nvt 0.5823 0.5694\r\nvt 0.5831 0.5639\r\nvt 0.5828 0.5661\r\nvt 0.5839 0.5585\r\nvt 0.5835 0.5614\r\nvt 0.5848 0.552\r\nvt 0.5843 0.5554\r\nvt 0.5858 0.5447\r\nvt 0.5853 0.5484\r\nvt 0.5869 0.537\r\nvt 0.5864 0.5408\r\nvt 0.5879 0.5293\r\nvt 0.5874 0.5331\r\nvt 0.5889 0.5216\r\nvt 0.5884 0.5254\r\nvt 0.5899 0.5141\r\nvt 0.5894 0.5178\r\nvt 0.5909 0.5066\r\nvt 0.5904 0.5103\r\nvt 0.5918 0.4992\r\nvt 0.5913 0.5029\r\nvt 0.5928 0.492\r\nvt 0.5923 0.4956\r\nvt 0.5938 0.4848\r\nvt 0.5933 0.4884\r\nvt 0.5948 0.4778\r\nvt 0.5943 0.4813\r\nvt 0.5958 0.4708\r\nvt 0.5953 0.4743\r\nvt 0.5969 0.464\r\nvt 0.5964 0.4674\r\nvt 0.5982 0.4574\r\nvt 0.5975 0.4607\r\nvt 0.5995 0.4512\r\nvt 0.5988 0.4543\r\nvt 0.601 0.4453\r\nvt 0.6002 0.4482\r\nvt 0.6025 0.4399\r\nvt 0.6017 0.4425\r\nvt 0.6037 0.4353\r\nvt 0.6031 0.4375\r\nvt 0.6044 0.4312\r\nvt 0.6042 0.4332\r\nvt 0.6044 0.4272\r\nvt 0.6045 0.4293\r\nvt 0.6039 0.4227\r\nvt 0.6042 0.425\r\nvt 0.6033 0.4175\r\nvt 0.6035 0.4202\r\nvt 0.6028 0.4121\r\nvt 0.603 0.4148\r\nvt 0.6025 0.4064\r\nvt 0.6026 0.4093\r\nvt 0.6024 0.4008\r\nvt 0.6025 0.4036\r\nvt 0.6025 0.3952\r\nvt 0.6025 0.398\r\nvt 0.6027 0.3897\r\nvt 0.6026 0.3924\r\nvt 0.6029 0.3844\r\nvt 0.6028 0.3871\r\nvt 0.6031 0.3793\r\nvt 0.603 0.3819\r\nvt 0.6034 0.3745\r\nvt 0.6033 0.3769\r\nvt 0.6037 0.3701\r\nvt 0.6035 0.3723\r\nvt 0.6039 0.366\r\nvt 0.6038 0.368\r\nvt 0.6041 0.3621\r\nvt 0.604 0.364\r\nvt 0.6043 0.3584\r\nvt 0.6042 0.3603\r\nvt 0.6045 0.3548\r\nvt 0.6044 0.3566\r\nvt 0.6048 0.3511\r\nvt 0.6046 0.3529\r\nvt 0.605 0.3472\r\nvt 0.6049 0.3492\r\nvt 0.6052 0.3433\r\nvt 0.6051 0.3453\r\nvt 0.6054 0.3393\r\nvt 0.6053 0.3413\r\nvt 0.6056 0.3351\r\nvt 0.6055 0.3372\r\nvt 0.6058 0.3308\r\nvt 0.6057 0.333\r\nvt 0.6059 0.3264\r\nvt 0.6058 0.3286\r\nvt 0.6061 0.3218\r\nvt 0.606 0.3241\r\nvt 0.6062 0.317\r\nvt 0.6061 0.3195\r\nvt 0.6062 0.3121\r\nvt 0.6062 0.3146\r\nvt 0.6061 0.307\r\nvt 0.6062 0.3096\r\nvt 0.6058 0.3018\r\nvt 0.6059 0.3044\r\nvt 0.6052 0.2965\r\nvt 0.6055 0.2991\r\nvt 0.6042 0.2912\r\nvt 0.6047 0.2939\r\nvt 0.6022 0.2853\r\nvt 0.6035 0.2882\r\nvt 0.6468 0.2288\r\nvt 0.6488 0.2259\r\nvt 0.6483 0.2255\r\nvt 0.6503 0.2225\r\nvt 0.6508 0.2229\r\nvt 0.6427 0.2348\r\nvt 0.6448 0.2318\r\nvt 0.6385 0.2408\r\nvt 0.6406 0.2378\r\nvt 0.6343 0.2468\r\nvt 0.6364 0.2438\r\nvt 0.6299 0.2529\r\nvt 0.6321 0.2499\r\nvt 0.6254 0.259\r\nvt 0.6276 0.256\r\nvt 0.6208 0.2648\r\nvt 0.6231 0.2619\r\nvt 0.6163 0.2703\r\nvt 0.6186 0.2676\r\nvt 0.6116 0.2756\r\nvt 0.614 0.2729\r\nvt 0.6088 0.2787\r\nvt 0.5974 0.2821\r\nvt 0.6 0.2833\r\nvt 0.5926 0.2809\r\nvt 0.595 0.2814\r\nvt 0.5873 0.2801\r\nvt 0.5901 0.2805\r\nvt 0.5815 0.2796\r\nvt 0.5845 0.2798\r\nvt 0.5752 0.2793\r\nvt 0.5784 0.2795\r\nvt 0.5687 0.2793\r\nvt 0.572 0.2793\r\nvt 0.5619 0.2793\r\nvt 0.5653 0.2793\r\nvt 0.555 0.2794\r\nvt 0.5584 0.2793\r\nvt 0.5479 0.2796\r\nvt 0.5514 0.2795\r\nvt 0.5407 0.2798\r\nvt 0.5443 0.2797\r\nvt 0.5334 0.28\r\nvt 0.5371 0.2799\r\nvt 0.526 0.2802\r\nvt 0.5297 0.2801\r\nvt 0.5185 0.2804\r\nvt 0.5223 0.2803\r\nvt 0.5109 0.2806\r\nvt 0.5147 0.2805\r\nvt 0.5032 0.2809\r\nvt 0.5071 0.2808\r\nvt 0.4955 0.2811\r\nvt 0.4994 0.281\r\nvt 0.4878 0.2813\r\nvt 0.4916 0.2812\r\nvt 0.4805 0.2816\r\nvt 0.4841 0.2815\r\nvt 0.4739 0.2819\r\nvt 0.4771 0.2817\r\nvt 0.4686 0.2822\r\nvt 0.4711 0.282\r\nvt 0.4647 0.2825\r\nvt 0.4664 0.2823\r\nvt 0.4619 0.2826\r\nvt 0.4632 0.2826\r\nvt 0.4598 0.2827\r\nvt 0.4608 0.2827\r\nvt 0.4582 0.2827\r\nvt 0.459 0.2827\r\nvt 0.4566 0.2827\r\nvt 0.4574 0.2827\r\nvt 0.4548 0.2826\r\nvt 0.4557 0.2827\r\nvt 0.4529 0.2825\r\nvt 0.4539 0.2825\r\nvt 0.4507 0.2822\r\nvt 0.4518 0.2823\r\nvt 0.448 0.2819\r\nvt 0.4494 0.2821\r\nvt 0.4449 0.2815\r\nvt 0.4465 0.2817\r\nvt 0.4416 0.2811\r\nvt 0.4433 0.2813\r\nvt 0.4379 0.2805\r\nvt 0.4398 0.2808\r\nvt 0.4362 0.28\r\nvt 0.1768 0.1983\r\nvt 0.1768 0.1959\r\nvt 0.1774 0.1958\r\nvt 0.1775 0.1983\r\nvt 0.1764 0.1935\r\nvt 0.1771 0.1933\r\nvt 0.1787 0.1956\r\nvt 0.1788 0.1983\r\nvt 0.6102 0.4317\r\nvt 0.6103 0.4293\r\nvt 0.6115 0.4293\r\nvt 0.6113 0.4319\r\nvt 0.1786 0.1926\r\nvt 0.6107 0.4347\r\nvt 0.6098 0.4342\r\nvt 0.6094 0.434\r\nvt 0.6097 0.4316\r\nvt 0.6098 0.4293\r\nvt 0.2141 0.4083\r\nvt 0.2162 0.4113\r\nvt 0.2158 0.4072\r\nvt 0.2178 0.4102\r\nvt 0.2163 0.4068\r\nvt 0.2184 0.4098\r\nvt 0.6467 0.2244\r\nvt 0.6487 0.2214\r\nvt 0.6451 0.2233\r\nvt 0.6471 0.2203\r\nvt 0.6445 0.2229\r\nvt 0.6465 0.22\r\nvt 0.9964 0.0066\r\nvt 0.9975 0.0075\r\nvt 0.9957 0.0085\r\nvt 0.9949 0.0068\r\nvt 0.9977 0.0089\r\nvt 0.9967 0.0101\r\nvt 0.9943 0.0116\r\nvt 0.9933 0.0099\r\nvt 0.9923 0.0081\r\nvt 0.9984 0.009\r\nvt 0.9971 0.0106\r\nvt 0.9989 0.0091\r\nvt 0.9975 0.011\r\nvt 0.9949 0.0126\r\nvt 0.9946 0.0122\r\nvt 0.5703 0.6269\r\nvt 0.5701 0.6283\r\nvt 0.5683 0.6273\r\nvt 0.5694 0.6257\r\nvt 0.569 0.6292\r\nvt 0.5675 0.629\r\nvt 0.565 0.6277\r\nvt 0.5659 0.6259\r\nvt 0.5669 0.6242\r\nvt 0.5693 0.6298\r\nvt 0.5673 0.6297\r\nvt 0.5695 0.6305\r\nvt 0.5671 0.6303\r\nvt 0.5643 0.629\r\nvt 0.5646 0.6284\r\nvt 0.9905 0.0114\r\nvt 0.9895 0.0097\r\nvt 0.9915 0.0131\r\nvt 0.9885 0.0148\r\nvt 0.9875 0.0131\r\nvt 0.9866 0.0114\r\nvt 0.9918 0.0137\r\nvt 0.9921 0.0142\r\nvt 0.9891 0.0158\r\nvt 0.9888 0.0154\r\nvt 0.5632 0.6244\r\nvt 0.5642 0.6227\r\nvt 0.5622 0.6261\r\nvt 0.5593 0.6244\r\nvt 0.5602 0.6227\r\nvt 0.5612 0.621\r\nvt 0.5619 0.6268\r\nvt 0.5615 0.6273\r\nvt 0.5586 0.6256\r\nvt 0.5589 0.625\r\nvt 0.9844 0.0148\r\nvt 0.9834 0.0131\r\nvt 0.9853 0.0165\r\nvt 0.9821 0.0184\r\nvt 0.9812 0.0167\r\nvt 0.9802 0.015\r\nvt 0.9857 0.0171\r\nvt 0.9859 0.0176\r\nvt 0.9827 0.0194\r\nvt 0.9824 0.0189\r\nvt 0.5571 0.6209\r\nvt 0.5581 0.6192\r\nvt 0.5561 0.6226\r\nvt 0.5529 0.6208\r\nvt 0.5539 0.6191\r\nvt 0.5549 0.6174\r\nvt 0.5558 0.6232\r\nvt 0.5555 0.6238\r\nvt 0.5522 0.622\r\nvt 0.5526 0.6214\r\nvt 0.9779 0.0185\r\nvt 0.9769 0.0168\r\nvt 0.9788 0.0202\r\nvt 0.9756 0.022\r\nvt 0.9747 0.0203\r\nvt 0.9737 0.0186\r\nvt 0.9792 0.0208\r\nvt 0.9794 0.0212\r\nvt 0.9762 0.0231\r\nvt 0.9759 0.0226\r\nvt 0.5506 0.6172\r\nvt 0.5516 0.6155\r\nvt 0.5497 0.6189\r\nvt 0.5465 0.6171\r\nvt 0.5474 0.6154\r\nvt 0.5484 0.6137\r\nvt 0.5493 0.6195\r\nvt 0.549 0.6201\r\nvt 0.5458 0.6183\r\nvt 0.5461 0.6177\r\nvt 0.9715 0.0221\r\nvt 0.9706 0.0204\r\nvt 0.9725 0.0238\r\nvt 0.9696 0.0255\r\nvt 0.9686 0.0238\r\nvt 0.9676 0.0221\r\nvt 0.9728 0.0244\r\nvt 0.9731 0.0248\r\nvt 0.9701 0.0265\r\nvt 0.9699 0.026\r\nvt 0.5443 0.6136\r\nvt 0.5453 0.6119\r\nvt 0.5434 0.6153\r\nvt 0.5404 0.6136\r\nvt 0.5414 0.6119\r\nvt 0.5424 0.6102\r\nvt 0.543 0.6159\r\nvt 0.5427 0.6165\r\nvt 0.5397 0.6148\r\nvt 0.5401 0.6143\r\nvt 0.9659 0.0253\r\nvt 0.9649 0.0236\r\nvt 0.9668 0.027\r\nvt 0.9645 0.0283\r\nvt 0.9635 0.0266\r\nvt 0.9625 0.0249\r\nvt 0.9672 0.0276\r\nvt 0.9674 0.028\r\nvt 0.965 0.0294\r\nvt 0.9648 0.0289\r\nvt 0.5387 0.6104\r\nvt 0.5397 0.6087\r\nvt 0.5377 0.6121\r\nvt 0.5354 0.6108\r\nvt 0.5363 0.6091\r\nvt 0.5373 0.6074\r\nvt 0.5374 0.6127\r\nvt 0.5371 0.6133\r\nvt 0.5347 0.612\r\nvt 0.535 0.6114\r\nvt 0.9615 0.0278\r\nvt 0.9605 0.0261\r\nvt 0.9624 0.0295\r\nvt 0.9607 0.0304\r\nvt 0.9598 0.0287\r\nvt 0.9588 0.027\r\nvt 0.9628 0.03\r\nvt 0.963 0.0305\r\nvt 0.9613 0.0315\r\nvt 0.9611 0.031\r\nvt 0.5343 0.6079\r\nvt 0.5353 0.6062\r\nvt 0.5334 0.6096\r\nvt 0.5317 0.6087\r\nvt 0.5326 0.607\r\nvt 0.5336 0.6053\r\nvt 0.533 0.6102\r\nvt 0.5327 0.6108\r\nvt 0.531 0.6099\r\nvt 0.5313 0.6093\r\nvt 0.9584 0.0295\r\nvt 0.9574 0.0278\r\nvt 0.9593 0.0312\r\nvt 0.9582 0.0319\r\nvt 0.9572 0.0302\r\nvt 0.9562 0.0285\r\nvt 0.9597 0.0318\r\nvt 0.9599 0.0322\r\nvt 0.9588 0.0329\r\nvt 0.9585 0.0324\r\nvt 0.5313 0.6062\r\nvt 0.5322 0.6045\r\nvt 0.5303 0.6079\r\nvt 0.5292 0.6073\r\nvt 0.5301 0.6056\r\nvt 0.5311 0.6039\r\nvt 0.53 0.6085\r\nvt 0.5296 0.6091\r\nvt 0.5285 0.6085\r\nvt 0.5288 0.6079\r\nvt 0.9562 0.0307\r\nvt 0.9553 0.029\r\nvt 0.9572 0.0324\r\nvt 0.9564 0.0329\r\nvt 0.9554 0.0312\r\nvt 0.9544 0.0295\r\nvt 0.9575 0.033\r\nvt 0.9578 0.0335\r\nvt 0.9569 0.0339\r\nvt 0.9567 0.0335\r\nvt 0.5292 0.605\r\nvt 0.5302 0.6033\r\nvt 0.5282 0.6067\r\nvt 0.5274 0.6063\r\nvt 0.5284 0.6046\r\nvt 0.5294 0.6029\r\nvt 0.5279 0.6073\r\nvt 0.5276 0.6079\r\nvt 0.5268 0.6075\r\nvt 0.5271 0.6069\r\nvt 0.9546 0.0317\r\nvt 0.9536 0.03\r\nvt 0.9556 0.0333\r\nvt 0.9549 0.0337\r\nvt 0.9539 0.0321\r\nvt 0.9529 0.0304\r\nvt 0.9559 0.0339\r\nvt 0.9562 0.0344\r\nvt 0.9555 0.0348\r\nvt 0.9552 0.0343\r\nvt 0.5277 0.6042\r\nvt 0.5287 0.6025\r\nvt 0.5267 0.6059\r\nvt 0.5261 0.6055\r\nvt 0.5271 0.6038\r\nvt 0.5281 0.6021\r\nvt 0.5264 0.6065\r\nvt 0.526 0.6071\r\nvt 0.5254 0.6067\r\nvt 0.5257 0.6061\r\nvt 0.9532 0.0325\r\nvt 0.9522 0.0308\r\nvt 0.9542 0.0342\r\nvt 0.9534 0.0346\r\nvt 0.9525 0.0329\r\nvt 0.9515 0.0312\r\nvt 0.9545 0.0347\r\nvt 0.9548 0.0352\r\nvt 0.9541 0.0356\r\nvt 0.9538 0.0352\r\nvt 0.5264 0.6034\r\nvt 0.5274 0.6017\r\nvt 0.5254 0.6051\r\nvt 0.5248 0.6047\r\nvt 0.5257 0.603\r\nvt 0.5267 0.6013\r\nvt 0.5251 0.6057\r\nvt 0.5247 0.6063\r\nvt 0.5241 0.6059\r\nvt 0.5244 0.6053\r\nvt 0.9516 0.0334\r\nvt 0.9507 0.0317\r\nvt 0.9526 0.0351\r\nvt 0.9517 0.0356\r\nvt 0.9507 0.034\r\nvt 0.9497 0.0323\r\nvt 0.953 0.0357\r\nvt 0.9532 0.0361\r\nvt 0.9523 0.0367\r\nvt 0.952 0.0362\r\nvt 0.525 0.6026\r\nvt 0.526 0.6009\r\nvt 0.524 0.6043\r\nvt 0.5231 0.6037\r\nvt 0.5241 0.602\r\nvt 0.5251 0.6003\r\nvt 0.5236 0.6049\r\nvt 0.5233 0.6054\r\nvt 0.5224 0.6049\r\nvt 0.5227 0.6043\r\nvt 0.9495 0.0346\r\nvt 0.9486 0.0329\r\nvt 0.9505 0.0363\r\nvt 0.9492 0.0371\r\nvt 0.9482 0.0354\r\nvt 0.9472 0.0337\r\nvt 0.9509 0.0369\r\nvt 0.9511 0.0373\r\nvt 0.9498 0.0381\r\nvt 0.9495 0.0377\r\nvt 0.523 0.6014\r\nvt 0.524 0.5997\r\nvt 0.522 0.6031\r\nvt 0.5207 0.6023\r\nvt 0.5217 0.6006\r\nvt 0.5227 0.599\r\nvt 0.5216 0.6037\r\nvt 0.5213 0.6043\r\nvt 0.52 0.6035\r\nvt 0.5203 0.6029\r\nvt 0.9466 0.0363\r\nvt 0.9457 0.0346\r\nvt 0.9476 0.038\r\nvt 0.9457 0.0391\r\nvt 0.9447 0.0374\r\nvt 0.9438 0.0357\r\nvt 0.9479 0.0386\r\nvt 0.9482 0.039\r\nvt 0.9463 0.0401\r\nvt 0.9461 0.0397\r\nvt 0.5201 0.5997\r\nvt 0.5211 0.598\r\nvt 0.5191 0.6014\r\nvt 0.5173 0.6003\r\nvt 0.5182 0.5987\r\nvt 0.5192 0.597\r\nvt 0.5188 0.602\r\nvt 0.5184 0.6026\r\nvt 0.5166 0.6015\r\nvt 0.5169 0.601\r\nvt 0.9425 0.0387\r\nvt 0.9416 0.037\r\nvt 0.9435 0.0404\r\nvt 0.941 0.0418\r\nvt 0.94 0.0401\r\nvt 0.939 0.0384\r\nvt 0.9438 0.0409\r\nvt 0.9441 0.0414\r\nvt 0.9416 0.0428\r\nvt 0.9413 0.0424\r\nvt 0.516 0.5974\r\nvt 0.517 0.5957\r\nvt 0.5151 0.5991\r\nvt 0.5126 0.5976\r\nvt 0.5135 0.5959\r\nvt 0.5145 0.5943\r\nvt 0.5147 0.5997\r\nvt 0.5144 0.6003\r\nvt 0.5119 0.5988\r\nvt 0.5122 0.5983\r\nvt 0.9372 0.0417\r\nvt 0.9363 0.04\r\nvt 0.9382 0.0434\r\nvt 0.9352 0.0451\r\nvt 0.9342 0.0434\r\nvt 0.9333 0.0417\r\nvt 0.9385 0.044\r\nvt 0.9388 0.0444\r\nvt 0.9358 0.0461\r\nvt 0.9355 0.0457\r\nvt 0.5108 0.5944\r\nvt 0.5117 0.5927\r\nvt 0.5098 0.5961\r\nvt 0.5068 0.5944\r\nvt 0.5078 0.5927\r\nvt 0.5087 0.591\r\nvt 0.5094 0.5967\r\nvt 0.5091 0.5973\r\nvt 0.5061 0.5956\r\nvt 0.5065 0.595\r\nvt 0.9311 0.0452\r\nvt 0.9301 0.0435\r\nvt 0.932 0.0469\r\nvt 0.9287 0.0487\r\nvt 0.9278 0.047\r\nvt 0.9268 0.0453\r\nvt 0.9323 0.0474\r\nvt 0.9326 0.0479\r\nvt 0.9293 0.0497\r\nvt 0.929 0.0493\r\nvt 0.5046 0.5909\r\nvt 0.5056 0.5892\r\nvt 0.5036 0.5926\r\nvt 0.5004 0.5907\r\nvt 0.5013 0.589\r\nvt 0.5023 0.5873\r\nvt 0.5033 0.5932\r\nvt 0.503 0.5938\r\nvt 0.4997 0.5919\r\nvt 0.5 0.5914\r\nvt 0.9244 0.0489\r\nvt 0.9234 0.0472\r\nvt 0.9253 0.0506\r\nvt 0.9219 0.0525\r\nvt 0.921 0.0508\r\nvt 0.92 0.0491\r\nvt 0.9257 0.0512\r\nvt 0.9259 0.0516\r\nvt 0.9225 0.0535\r\nvt 0.9223 0.0531\r\nvt 0.498 0.5872\r\nvt 0.4989 0.5855\r\nvt 0.497 0.5889\r\nvt 0.4936 0.587\r\nvt 0.4946 0.5853\r\nvt 0.4955 0.5836\r\nvt 0.4967 0.5895\r\nvt 0.4963 0.5901\r\nvt 0.4929 0.5882\r\nvt 0.4933 0.5876\r\nvt 0.9176 0.0527\r\nvt 0.9166 0.051\r\nvt 0.9185 0.0544\r\nvt 0.9151 0.0563\r\nvt 0.9142 0.0546\r\nvt 0.9132 0.0529\r\nvt 0.9189 0.055\r\nvt 0.9191 0.0554\r\nvt 0.9157 0.0573\r\nvt 0.9155 0.0568\r\nvt 0.4912 0.5834\r\nvt 0.4921 0.5817\r\nvt 0.4902 0.5851\r\nvt 0.4868 0.5832\r\nvt 0.4878 0.5815\r\nvt 0.4887 0.5798\r\nvt 0.4899 0.5857\r\nvt 0.4895 0.5863\r\nvt 0.4861 0.5844\r\nvt 0.4865 0.5838\r\nvt 0.9108 0.0564\r\nvt 0.9099 0.0547\r\nvt 0.9118 0.0581\r\nvt 0.9084 0.06\r\nvt 0.9074 0.0583\r\nvt 0.9065 0.0566\r\nvt 0.9121 0.0587\r\nvt 0.9123 0.0592\r\nvt 0.909 0.061\r\nvt 0.9087 0.0606\r\nvt 0.4844 0.5796\r\nvt 0.4853 0.5779\r\nvt 0.4834 0.5813\r\nvt 0.4801 0.5794\r\nvt 0.481 0.5777\r\nvt 0.4819 0.576\r\nvt 0.4831 0.5819\r\nvt 0.4828 0.5825\r\nvt 0.4794 0.5807\r\nvt 0.4797 0.5801\r\nvt 0.9041 0.0601\r\nvt 0.9032 0.0584\r\nvt 0.905 0.0618\r\nvt 0.9017 0.0637\r\nvt 0.9008 0.062\r\nvt 0.8998 0.0603\r\nvt 0.9054 0.0624\r\nvt 0.9056 0.0629\r\nvt 0.9023 0.0647\r\nvt 0.902 0.0643\r\nvt 0.4776 0.5759\r\nvt 0.4786 0.5742\r\nvt 0.4767 0.5776\r\nvt 0.4733 0.5757\r\nvt 0.4743 0.574\r\nvt 0.4752 0.5723\r\nvt 0.4763 0.5782\r\nvt 0.476 0.5788\r\nvt 0.4727 0.5769\r\nvt 0.473 0.5764\r\nvt 0.8975 0.0638\r\nvt 0.8965 0.0621\r\nvt 0.8984 0.0655\r\nvt 0.8951 0.0673\r\nvt 0.8942 0.0656\r\nvt 0.8932 0.0639\r\nvt 0.8987 0.0661\r\nvt 0.899 0.0665\r\nvt 0.8957 0.0684\r\nvt 0.8955 0.0679\r\nvt 0.471 0.5722\r\nvt 0.4719 0.5705\r\nvt 0.47 0.5739\r\nvt 0.4667 0.5721\r\nvt 0.4676 0.5703\r\nvt 0.4686 0.5686\r\nvt 0.4697 0.5745\r\nvt 0.4693 0.5751\r\nvt 0.466 0.5733\r\nvt 0.4663 0.5727\r\nvt 0.8909 0.0674\r\nvt 0.89 0.0657\r\nvt 0.8919 0.0691\r\nvt 0.8886 0.0709\r\nvt 0.8877 0.0692\r\nvt 0.8867 0.0675\r\nvt 0.8922 0.0697\r\nvt 0.8924 0.0702\r\nvt 0.8892 0.072\r\nvt 0.889 0.0715\r\nvt 0.4643 0.5685\r\nvt 0.4653 0.5668\r\nvt 0.4634 0.5702\r\nvt 0.4601 0.5684\r\nvt 0.4611 0.5667\r\nvt 0.462 0.565\r\nvt 0.463 0.5708\r\nvt 0.4627 0.5714\r\nvt 0.4594 0.5696\r\nvt 0.4598 0.569\r\nvt 0.8845 0.071\r\nvt 0.8835 0.0693\r\nvt 0.8854 0.0727\r\nvt 0.8822 0.0745\r\nvt 0.8813 0.0728\r\nvt 0.8803 0.0711\r\nvt 0.8857 0.0733\r\nvt 0.886 0.0737\r\nvt 0.8828 0.0755\r\nvt 0.8826 0.0751\r\nvt 0.4578 0.5649\r\nvt 0.4588 0.5632\r\nvt 0.4569 0.5666\r\nvt 0.4536 0.5648\r\nvt 0.4546 0.5631\r\nvt 0.4555 0.5614\r\nvt 0.4565 0.5672\r\nvt 0.4562 0.5678\r\nvt 0.453 0.566\r\nvt 0.4533 0.5654\r\nvt 0.8781 0.0745\r\nvt 0.8772 0.0728\r\nvt 0.8791 0.0762\r\nvt 0.876 0.078\r\nvt 0.875 0.0763\r\nvt 0.874 0.0746\r\nvt 0.8794 0.0768\r\nvt 0.8797 0.0773\r\nvt 0.8765 0.079\r\nvt 0.8763 0.0786\r\nvt 0.4514 0.5613\r\nvt 0.4523 0.5596\r\nvt 0.4504 0.563\r\nvt 0.4472 0.5612\r\nvt 0.4482 0.5595\r\nvt 0.4492 0.5578\r\nvt 0.4501 0.5636\r\nvt 0.4497 0.5642\r\nvt 0.4466 0.5624\r\nvt 0.4469 0.5618\r\nvt 0.8719 0.078\r\nvt 0.8709 0.0764\r\nvt 0.8729 0.0797\r\nvt 0.8698 0.0815\r\nvt 0.8688 0.0798\r\nvt 0.8678 0.0781\r\nvt 0.8732 0.0803\r\nvt 0.8734 0.0808\r\nvt 0.8704 0.0825\r\nvt 0.8701 0.0821\r\nvt 0.445 0.5577\r\nvt 0.446 0.556\r\nvt 0.4441 0.5594\r\nvt 0.4409 0.5576\r\nvt 0.4419 0.5559\r\nvt 0.4429 0.5542\r\nvt 0.4437 0.56\r\nvt 0.4434 0.5606\r\nvt 0.4403 0.5588\r\nvt 0.4406 0.5582\r\nvt 0.8658 0.0815\r\nvt 0.8648 0.0799\r\nvt 0.8667 0.0832\r\nvt 0.8637 0.085\r\nvt 0.8627 0.0833\r\nvt 0.8618 0.0816\r\nvt 0.8671 0.0838\r\nvt 0.8673 0.0843\r\nvt 0.8643 0.086\r\nvt 0.8641 0.0855\r\nvt 0.4388 0.5541\r\nvt 0.4398 0.5524\r\nvt 0.4378 0.5558\r\nvt 0.4347 0.554\r\nvt 0.4357 0.5523\r\nvt 0.4367 0.5507\r\nvt 0.4375 0.5564\r\nvt 0.4371 0.557\r\nvt 0.4341 0.5552\r\nvt 0.4344 0.5546\r\nvt 0.8598 0.085\r\nvt 0.8588 0.0834\r\nvt 0.8608 0.0867\r\nvt 0.8579 0.0884\r\nvt 0.8569 0.0868\r\nvt 0.8559 0.0851\r\nvt 0.8611 0.0873\r\nvt 0.8614 0.0877\r\nvt 0.8585 0.0895\r\nvt 0.8582 0.089\r\nvt 0.4327 0.5506\r\nvt 0.4337 0.5489\r\nvt 0.4317 0.5522\r\nvt 0.4287 0.5504\r\nvt 0.4298 0.5488\r\nvt 0.4308 0.5471\r\nvt 0.4314 0.5528\r\nvt 0.431 0.5534\r\nvt 0.428 0.5516\r\nvt 0.4284 0.551\r\nvt 0.854 0.0885\r\nvt 0.853 0.0869\r\nvt 0.8551 0.0902\r\nvt 0.8523 0.0919\r\nvt 0.8513 0.0903\r\nvt 0.8502 0.0887\r\nvt 0.8554 0.0908\r\nvt 0.8557 0.0912\r\nvt 0.853 0.0929\r\nvt 0.8527 0.0925\r\nvt 0.4269 0.547\r\nvt 0.4279 0.5453\r\nvt 0.4258 0.5486\r\nvt 0.423 0.5468\r\nvt 0.424 0.5452\r\nvt 0.4251 0.5435\r\nvt 0.4254 0.5492\r\nvt 0.4251 0.5498\r\nvt 0.4222 0.548\r\nvt 0.4226 0.5474\r\nvt 0.8486 0.092\r\nvt 0.8475 0.0904\r\nvt 0.8496 0.0937\r\nvt 0.8471 0.0954\r\nvt 0.846 0.0938\r\nvt 0.8449 0.0922\r\nvt 0.85 0.0942\r\nvt 0.8503 0.0947\r\nvt 0.8477 0.0964\r\nvt 0.8474 0.096\r\nvt 0.4213 0.5434\r\nvt 0.4224 0.5418\r\nvt 0.4202 0.545\r\nvt 0.4175 0.5432\r\nvt 0.4186 0.5416\r\nvt 0.4197 0.54\r\nvt 0.4198 0.5456\r\nvt 0.4194 0.5462\r\nvt 0.4167 0.5443\r\nvt 0.4171 0.5438\r\nvt 0.8435 0.0955\r\nvt 0.8423 0.094\r\nvt 0.8446 0.0971\r\nvt 0.8422 0.0988\r\nvt 0.8411 0.0973\r\nvt 0.8399 0.0957\r\nvt 0.845 0.0977\r\nvt 0.8453 0.0981\r\nvt 0.8429 0.0998\r\nvt 0.8426 0.0994\r\nvt 0.4161 0.5398\r\nvt 0.4172 0.5382\r\nvt 0.4149 0.5414\r\nvt 0.4124 0.5396\r\nvt 0.4136 0.538\r\nvt 0.4148 0.5365\r\nvt 0.4145 0.542\r\nvt 0.4141 0.5425\r\nvt 0.4116 0.5407\r\nvt 0.412 0.5402\r\nvt 0.8388 0.099\r\nvt 0.8376 0.0974\r\nvt 0.84 0.1005\r\nvt 0.8378 0.1022\r\nvt 0.8367 0.1006\r\nvt 0.8355 0.099\r\nvt 0.8404 0.1011\r\nvt 0.8407 0.1015\r\nvt 0.8385 0.1031\r\nvt 0.8381 0.1028\r\nvt 0.4113 0.5363\r\nvt 0.4124 0.5347\r\nvt 0.4101 0.5378\r\nvt 0.4079 0.5362\r\nvt 0.409 0.5346\r\nvt 0.4102 0.5331\r\nvt 0.4097 0.5384\r\nvt 0.4093 0.5389\r\nvt 0.4071 0.5373\r\nvt 0.4075 0.5367\r\nvt 0.8346 0.1021\r\nvt 0.8335 0.1006\r\nvt 0.8357 0.1038\r\nvt 0.8338 0.1052\r\nvt 0.8327 0.1036\r\nvt 0.8316 0.1019\r\nvt 0.8358 0.1046\r\nvt 0.8364 0.1048\r\nvt 0.8353 0.1059\r\nvt 0.8344 0.1061\r\nvt 0.8336 0.1064\r\nvt 0.834 0.1058\r\nvt 0.4069 0.533\r\nvt 0.4081 0.5314\r\nvt 0.4058 0.5346\r\nvt 0.4039 0.5332\r\nvt 0.405 0.5315\r\nvt 0.4061 0.5299\r\nvt 0.4054 0.5352\r\nvt 0.405 0.5357\r\nvt 0.4031 0.5343\r\nvt 0.4035 0.5338\r\nvt 0.8308 0.1048\r\nvt 0.8298 0.1031\r\nvt 0.8318 0.1065\r\nvt 0.8297 0.1077\r\nvt 0.8289 0.1059\r\nvt 0.828 0.1041\r\nvt 0.403 0.5303\r\nvt 0.404 0.5286\r\nvt 0.402 0.532\r\nvt 0.4002 0.531\r\nvt 0.4011 0.5292\r\nvt 0.4019 0.5274\r\nvt 0.4017 0.5326\r\nvt 0.4013 0.5332\r\nvt 0.3996 0.5322\r\nvt 0.3999 0.5316\r\nvt 0.8269 0.1067\r\nvt 0.8262 0.1049\r\nvt 0.8276 0.1086\r\nvt 0.8254 0.1093\r\nvt 0.8248 0.1075\r\nvt 0.8242 0.1056\r\nvt 0.399 0.5283\r\nvt 0.3998 0.5265\r\nvt 0.3983 0.5302\r\nvt 0.3963 0.5295\r\nvt 0.3969 0.5276\r\nvt 0.3975 0.5257\r\nvt 0.398 0.5308\r\nvt 0.3978 0.5314\r\nvt 0.3959 0.5308\r\nvt 0.3961 0.5301\r\nvt 0.8226 0.108\r\nvt 0.822 0.1062\r\nvt 0.8231 0.1099\r\nvt 0.8207 0.1105\r\nvt 0.8202 0.1087\r\nvt 0.8197 0.1068\r\nvt 0.8256 0.1099\r\nvt 0.8233 0.1106\r\nvt 0.8257 0.1104\r\nvt 0.8234 0.1111\r\nvt 0.821 0.1117\r\nvt 0.8209 0.1112\r\nvt 0.3946 0.527\r\nvt 0.3951 0.5251\r\nvt 0.3941 0.5288\r\nvt 0.3917 0.5282\r\nvt 0.3922 0.5263\r\nvt 0.3927 0.5245\r\nvt 0.3939 0.5295\r\nvt 0.3937 0.5302\r\nvt 0.3913 0.5296\r\nvt 0.3915 0.5289\r\nvt 0.8178 0.1093\r\nvt 0.8173 0.1074\r\nvt 0.8183 0.1112\r\nvt 0.8158 0.1118\r\nvt 0.8153 0.11\r\nvt 0.8147 0.1081\r\nvt 0.8184 0.1118\r\nvt 0.8186 0.1123\r\nvt 0.8161 0.113\r\nvt 0.816 0.1125\r\nvt 0.3897 0.5257\r\nvt 0.3902 0.5238\r\nvt 0.3892 0.5276\r\nvt 0.3866 0.5269\r\nvt 0.3871 0.525\r\nvt 0.3876 0.5231\r\nvt 0.389 0.5283\r\nvt 0.3888 0.5289\r\nvt 0.3862 0.5282\r\nvt 0.3864 0.5275\r\nvt 0.8127 0.1107\r\nvt 0.8121 0.1088\r\nvt 0.8132 0.1126\r\nvt 0.8107 0.1134\r\nvt 0.8101 0.1115\r\nvt 0.8095 0.1096\r\nvt 0.8134 0.1132\r\nvt 0.8136 0.1137\r\nvt 0.811 0.1145\r\nvt 0.8109 0.114\r\nvt 0.3845 0.5242\r\nvt 0.385 0.5223\r\nvt 0.3839 0.5261\r\nvt 0.3812 0.5252\r\nvt 0.3818 0.5234\r\nvt 0.3824 0.5215\r\nvt 0.3837 0.5268\r\nvt 0.3835 0.5274\r\nvt 0.3808 0.5266\r\nvt 0.381 0.5259\r\nvt 0.8074 0.1124\r\nvt 0.8068 0.1105\r\nvt 0.8081 0.1142\r\nvt 0.8055 0.1151\r\nvt 0.8048 0.1133\r\nvt 0.8042 0.1114\r\nvt 0.8083 0.1148\r\nvt 0.8084 0.1153\r\nvt 0.8059 0.1162\r\nvt 0.8057 0.1157\r\nvt 0.3791 0.5225\r\nvt 0.3797 0.5206\r\nvt 0.3785 0.5243\r\nvt 0.3758 0.5234\r\nvt 0.3764 0.5216\r\nvt 0.3771 0.5197\r\nvt 0.3783 0.525\r\nvt 0.378 0.5256\r\nvt 0.3753 0.5247\r\nvt 0.3755 0.5241\r\nvt 0.8022 0.1142\r\nvt 0.8015 0.1124\r\nvt 0.8029 0.1161\r\nvt 0.8003 0.1171\r\nvt 0.7996 0.1152\r\nvt 0.7989 0.1134\r\nvt 0.8031 0.1167\r\nvt 0.8033 0.1172\r\nvt 0.8008 0.1182\r\nvt 0.8006 0.1177\r\nvt 0.3738 0.5206\r\nvt 0.3745 0.5187\r\nvt 0.3731 0.5224\r\nvt 0.3704 0.5214\r\nvt 0.3711 0.5195\r\nvt 0.3718 0.5177\r\nvt 0.3728 0.5231\r\nvt 0.3726 0.5237\r\nvt 0.3699 0.5227\r\nvt 0.3702 0.522\r\nvt 0.797 0.1163\r\nvt 0.7963 0.1145\r\nvt 0.7978 0.1181\r\nvt 0.7952 0.1191\r\nvt 0.7945 0.1173\r\nvt 0.7938 0.1155\r\nvt 0.798 0.1187\r\nvt 0.7982 0.1192\r\nvt 0.7957 0.1202\r\nvt 0.7955 0.1197\r\nvt 0.3685 0.5185\r\nvt 0.3692 0.5167\r\nvt 0.3678 0.5203\r\nvt 0.3651 0.5192\r\nvt 0.3659 0.5174\r\nvt 0.3666 0.5156\r\nvt 0.3675 0.521\r\nvt 0.3672 0.5216\r\nvt 0.3646 0.5205\r\nvt 0.3649 0.5199\r\nvt 0.792 0.1184\r\nvt 0.7912 0.1166\r\nvt 0.7927 0.1202\r\nvt 0.7903 0.1212\r\nvt 0.7895 0.1195\r\nvt 0.7887 0.1177\r\nvt 0.793 0.1208\r\nvt 0.7932 0.1213\r\nvt 0.7908 0.1223\r\nvt 0.7905 0.1219\r\nvt 0.3633 0.5163\r\nvt 0.3641 0.5145\r\nvt 0.3626 0.5181\r\nvt 0.36 0.517\r\nvt 0.3608 0.5152\r\nvt 0.3616 0.5135\r\nvt 0.3623 0.5188\r\nvt 0.362 0.5194\r\nvt 0.3595 0.5183\r\nvt 0.3597 0.5177\r\nvt 0.7871 0.1205\r\nvt 0.7863 0.1187\r\nvt 0.7879 0.1223\r\nvt 0.7855 0.1234\r\nvt 0.7847 0.1216\r\nvt 0.7839 0.1198\r\nvt 0.7881 0.1229\r\nvt 0.7883 0.1234\r\nvt 0.786 0.1245\r\nvt 0.7857 0.124\r\nvt 0.3583 0.5142\r\nvt 0.3591 0.5124\r\nvt 0.3575 0.5159\r\nvt 0.3551 0.5148\r\nvt 0.3559 0.5131\r\nvt 0.3567 0.5113\r\nvt 0.3572 0.5166\r\nvt 0.357 0.5172\r\nvt 0.3545 0.5161\r\nvt 0.3548 0.5155\r\nvt 0.7823 0.1227\r\nvt 0.7815 0.1209\r\nvt 0.7831 0.1244\r\nvt 0.7809 0.1255\r\nvt 0.7801 0.1237\r\nvt 0.7793 0.1219\r\nvt 0.7834 0.125\r\nvt 0.7836 0.1255\r\nvt 0.7814 0.1265\r\nvt 0.7811 0.1261\r\nvt 0.3535 0.512\r\nvt 0.3543 0.5102\r\nvt 0.3527 0.5138\r\nvt 0.3504 0.5127\r\nvt 0.3512 0.5109\r\nvt 0.352 0.5092\r\nvt 0.3524 0.5144\r\nvt 0.3521 0.515\r\nvt 0.3498 0.514\r\nvt 0.3501 0.5134\r\nvt 0.7778 0.1247\r\nvt 0.777 0.1229\r\nvt 0.7787 0.1265\r\nvt 0.7765 0.1275\r\nvt 0.7757 0.1257\r\nvt 0.7749 0.1239\r\nvt 0.7789 0.1271\r\nvt 0.7791 0.1276\r\nvt 0.777 0.1285\r\nvt 0.7768 0.1281\r\nvt 0.3489 0.5099\r\nvt 0.3497 0.5081\r\nvt 0.3481 0.5117\r\nvt 0.3459 0.5107\r\nvt 0.3467 0.5089\r\nvt 0.3475 0.5071\r\nvt 0.3478 0.5123\r\nvt 0.3475 0.5129\r\nvt 0.3453 0.5119\r\nvt 0.3456 0.5113\r\nvt 0.7736 0.1266\r\nvt 0.7728 0.1249\r\nvt 0.7744 0.1284\r\nvt 0.7724 0.1293\r\nvt 0.7716 0.1276\r\nvt 0.7708 0.1258\r\nvt 0.7747 0.129\r\nvt 0.7749 0.1295\r\nvt 0.7729 0.1304\r\nvt 0.7727 0.1299\r\nvt 0.3446 0.5079\r\nvt 0.3454 0.5062\r\nvt 0.3438 0.5097\r\nvt 0.3418 0.5088\r\nvt 0.3426 0.507\r\nvt 0.3434 0.5052\r\nvt 0.3435 0.5103\r\nvt 0.3432 0.5109\r\nvt 0.3412 0.51\r\nvt 0.3415 0.5094\r\nvt 0.7697 0.1284\r\nvt 0.7689 0.1267\r\nvt 0.7705 0.1302\r\nvt 0.7687 0.1311\r\nvt 0.7679 0.1293\r\nvt 0.7671 0.1275\r\nvt 0.7708 0.1308\r\nvt 0.771 0.1313\r\nvt 0.7692 0.1321\r\nvt 0.769 0.1317\r\nvt 0.3406 0.5061\r\nvt 0.3415 0.5043\r\nvt 0.3398 0.5079\r\nvt 0.338 0.507\r\nvt 0.3388 0.5052\r\nvt 0.3396 0.5035\r\nvt 0.3395 0.5085\r\nvt 0.3393 0.5091\r\nvt 0.3374 0.5083\r\nvt 0.3377 0.5077\r\nvt 0.7661 0.1301\r\nvt 0.7653 0.1283\r\nvt 0.7669 0.1319\r\nvt 0.7652 0.1327\r\nvt 0.7644 0.1309\r\nvt 0.7636 0.1291\r\nvt 0.7672 0.1325\r\nvt 0.7674 0.1329\r\nvt 0.7657 0.1337\r\nvt 0.7655 0.1333\r\nvt 0.337 0.5044\r\nvt 0.3378 0.5026\r\nvt 0.3362 0.5062\r\nvt 0.3344 0.5054\r\nvt 0.3352 0.5036\r\nvt 0.336 0.5018\r\nvt 0.3359 0.5068\r\nvt 0.3356 0.5074\r\nvt 0.3338 0.5066\r\nvt 0.3341 0.506\r\nvt 0.7627 0.1317\r\nvt 0.7619 0.1299\r\nvt 0.7635 0.1334\r\nvt 0.7619 0.1342\r\nvt 0.7611 0.1324\r\nvt 0.7602 0.1307\r\nvt 0.7638 0.134\r\nvt 0.764 0.1345\r\nvt 0.7624 0.1353\r\nvt 0.7621 0.1348\r\nvt 0.3335 0.5028\r\nvt 0.3343 0.501\r\nvt 0.3327 0.5046\r\nvt 0.331 0.5038\r\nvt 0.3318 0.502\r\nvt 0.3326 0.5002\r\nvt 0.3324 0.5052\r\nvt 0.3321 0.5058\r\nvt 0.3304 0.505\r\nvt 0.3307 0.5044\r\nvt 0.7594 0.1332\r\nvt 0.7586 0.1314\r\nvt 0.7602 0.135\r\nvt 0.7585 0.1357\r\nvt 0.7577 0.134\r\nvt 0.7569 0.1322\r\nvt 0.7605 0.1356\r\nvt 0.7607 0.1361\r\nvt 0.759 0.1368\r\nvt 0.7588 0.1363\r\nvt 0.3301 0.5012\r\nvt 0.3309 0.4994\r\nvt 0.3293 0.503\r\nvt 0.3276 0.5022\r\nvt 0.3284 0.5004\r\nvt 0.3292 0.4987\r\nvt 0.329 0.5036\r\nvt 0.3287 0.5042\r\nvt 0.327 0.5035\r\nvt 0.3273 0.5028\r\nvt 0.756 0.1347\r\nvt 0.7552 0.133\r\nvt 0.7568 0.1365\r\nvt 0.7551 0.1373\r\nvt 0.7543 0.1355\r\nvt 0.7535 0.1338\r\nvt 0.7571 0.1371\r\nvt 0.7573 0.1376\r\nvt 0.7556 0.1384\r\nvt 0.7554 0.1379\r\nvt 0.3267 0.4996\r\nvt 0.3275 0.4979\r\nvt 0.3258 0.5014\r\nvt 0.3241 0.5006\r\nvt 0.3249 0.4988\r\nvt 0.3257 0.4971\r\nvt 0.3255 0.5021\r\nvt 0.3253 0.5027\r\nvt 0.3235 0.5019\r\nvt 0.3238 0.5012\r\nvt 0.7526 0.1363\r\nvt 0.7518 0.1346\r\nvt 0.7534 0.1381\r\nvt 0.7516 0.1389\r\nvt 0.7508 0.1371\r\nvt 0.75 0.1354\r\nvt 0.7537 0.1387\r\nvt 0.7539 0.1392\r\nvt 0.7521 0.14\r\nvt 0.7519 0.1395\r\nvt 0.3231 0.498\r\nvt 0.324 0.4962\r\nvt 0.3223 0.4998\r\nvt 0.3205 0.499\r\nvt 0.3213 0.4972\r\nvt 0.3222 0.4954\r\nvt 0.322 0.5004\r\nvt 0.3218 0.501\r\nvt 0.32 0.5002\r\nvt 0.3202 0.4996\r\nvt 0.749 0.1379\r\nvt 0.7482 0.1362\r\nvt 0.7499 0.1397\r\nvt 0.748 0.1405\r\nvt 0.7472 0.1388\r\nvt 0.7464 0.137\r\nvt 0.7501 0.1403\r\nvt 0.7503 0.1408\r\nvt 0.7485 0.1416\r\nvt 0.7483 0.1411\r\nvt 0.3195 0.4964\r\nvt 0.3203 0.4946\r\nvt 0.3187 0.4981\r\nvt 0.3169 0.4973\r\nvt 0.3177 0.4955\r\nvt 0.3185 0.4937\r\nvt 0.3184 0.4988\r\nvt 0.3182 0.4994\r\nvt 0.3163 0.4986\r\nvt 0.3166 0.498\r\nvt 0.7454 0.1396\r\nvt 0.7446 0.1378\r\nvt 0.7462 0.1414\r\nvt 0.7444 0.1422\r\nvt 0.7436 0.1404\r\nvt 0.7428 0.1387\r\nvt 0.7465 0.142\r\nvt 0.7467 0.1425\r\nvt 0.7448 0.1433\r\nvt 0.7446 0.1428\r\nvt 0.3158 0.4947\r\nvt 0.3166 0.4929\r\nvt 0.315 0.4965\r\nvt 0.3131 0.4956\r\nvt 0.3139 0.4938\r\nvt 0.3147 0.492\r\nvt 0.3147 0.4971\r\nvt 0.3144 0.4977\r\nvt 0.3126 0.4969\r\nvt 0.3128 0.4963\r\nvt 0.7417 0.1413\r\nvt 0.7409 0.1395\r\nvt 0.7425 0.1431\r\nvt 0.7405 0.1439\r\nvt 0.7398 0.1421\r\nvt 0.739 0.1404\r\nvt 0.7427 0.1437\r\nvt 0.743 0.1441\r\nvt 0.741 0.145\r\nvt 0.7408 0.1445\r\nvt 0.312 0.493\r\nvt 0.3128 0.4912\r\nvt 0.3112 0.4947\r\nvt 0.3093 0.4939\r\nvt 0.31 0.4921\r\nvt 0.3108 0.4903\r\nvt 0.3109 0.4954\r\nvt 0.3106 0.496\r\nvt 0.3087 0.4951\r\nvt 0.309 0.4945\r\nvt 0.7378 0.143\r\nvt 0.737 0.1412\r\nvt 0.7386 0.1448\r\nvt 0.7366 0.1457\r\nvt 0.7358 0.1439\r\nvt 0.735 0.1421\r\nvt 0.7389 0.1454\r\nvt 0.7391 0.1459\r\nvt 0.7371 0.1468\r\nvt 0.7369 0.1463\r\nvt 0.3081 0.4912\r\nvt 0.3088 0.4894\r\nvt 0.3073 0.493\r\nvt 0.3053 0.4921\r\nvt 0.306 0.4903\r\nvt 0.3068 0.4885\r\nvt 0.307 0.4936\r\nvt 0.3067 0.4942\r\nvt 0.3047 0.4934\r\nvt 0.305 0.4927\r\nvt 0.7338 0.1448\r\nvt 0.733 0.143\r\nvt 0.7346 0.1466\r\nvt 0.7325 0.1475\r\nvt 0.7318 0.1457\r\nvt 0.731 0.1439\r\nvt 0.7349 0.1472\r\nvt 0.7351 0.1476\r\nvt 0.733 0.1485\r\nvt 0.7328 0.1481\r\nvt 0.304 0.4894\r\nvt 0.3047 0.4876\r\nvt 0.3032 0.4912\r\nvt 0.3011 0.4903\r\nvt 0.3019 0.4885\r\nvt 0.3026 0.4867\r\nvt 0.3029 0.4919\r\nvt 0.3026 0.4925\r\nvt 0.3006 0.4916\r\nvt 0.3008 0.4909\r\nvt 0.7297 0.1466\r\nvt 0.7289 0.1448\r\nvt 0.7304 0.1484\r\nvt 0.7283 0.1493\r\nvt 0.7275 0.1475\r\nvt 0.7268 0.1457\r\nvt 0.7307 0.149\r\nvt 0.7309 0.1495\r\nvt 0.7287 0.1504\r\nvt 0.7285 0.1499\r\nvt 0.2997 0.4876\r\nvt 0.3005 0.4858\r\nvt 0.299 0.4894\r\nvt 0.2968 0.4885\r\nvt 0.2975 0.4867\r\nvt 0.2982 0.4849\r\nvt 0.2987 0.49\r\nvt 0.2984 0.4906\r\nvt 0.2962 0.4897\r\nvt 0.2965 0.4891\r\nvt 0.7253 0.1484\r\nvt 0.7246 0.1466\r\nvt 0.7261 0.1502\r\nvt 0.7238 0.1512\r\nvt 0.7231 0.1493\r\nvt 0.7224 0.1475\r\nvt 0.7263 0.1508\r\nvt 0.7265 0.1513\r\nvt 0.7243 0.1523\r\nvt 0.7241 0.1518\r\nvt 0.2952 0.4857\r\nvt 0.296 0.4839\r\nvt 0.2945 0.4875\r\nvt 0.2922 0.4866\r\nvt 0.2929 0.4848\r\nvt 0.2936 0.483\r\nvt 0.2943 0.4882\r\nvt 0.294 0.4888\r\nvt 0.2917 0.4879\r\nvt 0.292 0.4873\r\nvt 0.7208 0.1503\r\nvt 0.7201 0.1485\r\nvt 0.7215 0.1521\r\nvt 0.7191 0.153\r\nvt 0.7184 0.1512\r\nvt 0.7177 0.1494\r\nvt 0.7217 0.1527\r\nvt 0.7219 0.1532\r\nvt 0.7195 0.1541\r\nvt 0.7194 0.1536\r\nvt 0.2906 0.4839\r\nvt 0.2913 0.4821\r\nvt 0.2899 0.4857\r\nvt 0.2875 0.4848\r\nvt 0.2881 0.483\r\nvt 0.2888 0.4811\r\nvt 0.2896 0.4864\r\nvt 0.2894 0.487\r\nvt 0.287 0.4861\r\nvt 0.2872 0.4855\r\nvt 0.716 0.1521\r\nvt 0.7154 0.1503\r\nvt 0.7167 0.1539\r\nvt 0.7142 0.1548\r\nvt 0.7136 0.153\r\nvt 0.7129 0.1511\r\nvt 0.7169 0.1546\r\nvt 0.7171 0.155\r\nvt 0.7146 0.1559\r\nvt 0.7144 0.1554\r\nvt 0.2857 0.4821\r\nvt 0.2863 0.4803\r\nvt 0.285 0.4839\r\nvt 0.2825 0.4831\r\nvt 0.2831 0.4813\r\nvt 0.2837 0.4794\r\nvt 0.2848 0.4846\r\nvt 0.2846 0.4852\r\nvt 0.2821 0.4844\r\nvt 0.2823 0.4838\r\nvt 0.711 0.1538\r\nvt 0.7105 0.152\r\nvt 0.7116 0.1557\r\nvt 0.709 0.1565\r\nvt 0.7085 0.1546\r\nvt 0.7079 0.1528\r\nvt 0.7118 0.1563\r\nvt 0.712 0.1568\r\nvt 0.7094 0.1576\r\nvt 0.7092 0.1571\r\nvt 0.2805 0.4804\r\nvt 0.2811 0.4786\r\nvt 0.28 0.4823\r\nvt 0.2773 0.4816\r\nvt 0.2778 0.4797\r\nvt 0.2783 0.4778\r\nvt 0.2798 0.483\r\nvt 0.2796 0.4836\r\nvt 0.277 0.4829\r\nvt 0.2772 0.4823\r\nvt 0.7058 0.1554\r\nvt 0.7053 0.1535\r\nvt 0.7063 0.1573\r\nvt 0.7036 0.1579\r\nvt 0.7032 0.156\r\nvt 0.7027 0.1542\r\nvt 0.7065 0.1579\r\nvt 0.7066 0.1584\r\nvt 0.7039 0.1591\r\nvt 0.7037 0.1586\r\nvt 0.2751 0.479\r\nvt 0.2756 0.4771\r\nvt 0.2747 0.4809\r\nvt 0.272 0.4803\r\nvt 0.2724 0.4784\r\nvt 0.2727 0.4765\r\nvt 0.2745 0.4816\r\nvt 0.2744 0.4822\r\nvt 0.2717 0.4816\r\nvt 0.2719 0.481\r\nvt 0.7005 0.1566\r\nvt 0.7002 0.1547\r\nvt 0.7008 0.1585\r\nvt 0.6977 0.1589\r\nvt 0.6975 0.157\r\nvt 0.6974 0.1552\r\nvt 0.701 0.1592\r\nvt 0.7011 0.1597\r\nvt 0.6979 0.16\r\nvt 0.6978 0.1595\r\nvt 0.2696 0.4779\r\nvt 0.2699 0.476\r\nvt 0.2693 0.4798\r\nvt 0.2666 0.4793\r\nvt 0.2667 0.4775\r\nvt 0.2668 0.4756\r\nvt 0.2692 0.4805\r\nvt 0.2691 0.4811\r\nvt 0.2664 0.4807\r\nvt 0.2665 0.48\r\nvt 0.694 0.1573\r\nvt 0.6942 0.1556\r\nvt 0.6938 0.159\r\nvt 0.6893 0.1597\r\nvt 0.6902 0.1575\r\nvt 0.6904 0.1555\r\nvt 0.6937 0.1596\r\nvt 0.6939 0.1601\r\nvt 0.6903 0.1609\r\nvt 0.6899 0.1605\r\nvt 0.2632 0.477\r\nvt 0.263 0.4751\r\nvt 0.2635 0.4789\r\nvt 0.2599 0.4788\r\nvt 0.2596 0.4765\r\nvt 0.2586 0.4738\r\nvt 0.2636 0.4795\r\nvt 0.2635 0.4802\r\nvt 0.2606 0.4803\r\nvt 0.2605 0.4797\r\nvt 0.9981 0.0072\r\nvt 0.9967 0.006\r\nvt 0.9969 0.0055\r\nvt 0.9985 0.007\r\nvt 0.2187 0.4185\r\nvt 0.2203 0.4174\r\nvt 0.2224 0.4204\r\nvt 0.2208 0.4215\r\nvt 0.2219 0.4163\r\nvt 0.224 0.4193\r\nvt 0.2261 0.4224\r\nvt 0.2245 0.4235\r\nvt 0.2228 0.4246\r\nvt 0.2225 0.4159\r\nvt 0.2246 0.4189\r\nvt 0.2231 0.4155\r\nvt 0.2251 0.4186\r\nvt 0.2272 0.4216\r\nvt 0.2267 0.422\r\nvt 0.6543 0.2166\r\nvt 0.6527 0.2155\r\nvt 0.6547 0.2126\r\nvt 0.6563 0.2136\r\nvt 0.6511 0.2144\r\nvt 0.6531 0.2115\r\nvt 0.6551 0.2085\r\nvt 0.6567 0.2096\r\nvt 0.6583 0.2106\r\nvt 0.6505 0.2141\r\nvt 0.6525 0.2111\r\nvt 0.6501 0.2138\r\nvt 0.6521 0.2108\r\nvt 0.6541 0.2078\r\nvt 0.6545 0.2081\r\nvt 0.2265 0.4266\r\nvt 0.2249 0.4277\r\nvt 0.2281 0.4255\r\nvt 0.2302 0.4286\r\nvt 0.2286 0.4297\r\nvt 0.227 0.4308\r\nvt 0.2287 0.4251\r\nvt 0.2293 0.4247\r\nvt 0.2314 0.4278\r\nvt 0.2308 0.4282\r\nvt 0.6587 0.2065\r\nvt 0.6604 0.2076\r\nvt 0.6571 0.2055\r\nvt 0.6591 0.2024\r\nvt 0.6608 0.2035\r\nvt 0.6624 0.2046\r\nvt 0.6566 0.2051\r\nvt 0.6561 0.2048\r\nvt 0.6581 0.2018\r\nvt 0.6586 0.2021\r\nvt 0.2307 0.4328\r\nvt 0.229 0.4339\r\nvt 0.2323 0.4317\r\nvt 0.2344 0.4349\r\nvt 0.2328 0.436\r\nvt 0.2311 0.437\r\nvt 0.2329 0.4313\r\nvt 0.2335 0.431\r\nvt 0.2355 0.4341\r\nvt 0.235 0.4345\r\nvt 0.6628 0.2004\r\nvt 0.6644 0.2015\r\nvt 0.6612 0.1994\r\nvt 0.6632 0.1963\r\nvt 0.6648 0.1973\r\nvt 0.6665 0.1984\r\nvt 0.6606 0.199\r\nvt 0.6602 0.1987\r\nvt 0.6622 0.1956\r\nvt 0.6626 0.1959\r\nvt 0.2348 0.4391\r\nvt 0.2332 0.4402\r\nvt 0.2365 0.4381\r\nvt 0.2386 0.4413\r\nvt 0.2369 0.4423\r\nvt 0.2353 0.4434\r\nvt 0.2371 0.4377\r\nvt 0.2376 0.4373\r\nvt 0.2397 0.4405\r\nvt 0.2392 0.4409\r\nvt 0.6669 0.1942\r\nvt 0.6685 0.1953\r\nvt 0.6652 0.1931\r\nvt 0.6672 0.19\r\nvt 0.6689 0.191\r\nvt 0.6705 0.1921\r\nvt 0.6647 0.1928\r\nvt 0.6642 0.1925\r\nvt 0.6662 0.1894\r\nvt 0.6667 0.1896\r\nvt 0.239 0.4456\r\nvt 0.2373 0.4466\r\nvt 0.2406 0.4445\r\nvt 0.2427 0.4477\r\nvt 0.241 0.4488\r\nvt 0.2394 0.4498\r\nvt 0.2412 0.4441\r\nvt 0.2418 0.4438\r\nvt 0.2438 0.447\r\nvt 0.2433 0.4474\r\nvt 0.6709 0.1879\r\nvt 0.6725 0.1889\r\nvt 0.6692 0.1868\r\nvt 0.6712 0.1837\r\nvt 0.6728 0.1847\r\nvt 0.6745 0.1857\r\nvt 0.6687 0.1865\r\nvt 0.6682 0.1862\r\nvt 0.6702 0.183\r\nvt 0.6706 0.1833\r\nvt 0.243 0.452\r\nvt 0.2413 0.453\r\nvt 0.2447 0.4509\r\nvt 0.2466 0.4541\r\nvt 0.2449 0.4551\r\nvt 0.2432 0.4561\r\nvt 0.2453 0.4506\r\nvt 0.2458 0.4502\r\nvt 0.2478 0.4534\r\nvt 0.2472 0.4538\r\nvt 0.6748 0.1815\r\nvt 0.6764 0.1825\r\nvt 0.6731 0.1805\r\nvt 0.6749 0.1774\r\nvt 0.6766 0.1784\r\nvt 0.6783 0.1793\r\nvt 0.6725 0.1802\r\nvt 0.672 0.1799\r\nvt 0.6739 0.1768\r\nvt 0.6743 0.1771\r\nvt 0.2468 0.4582\r\nvt 0.2451 0.4592\r\nvt 0.2485 0.4573\r\nvt 0.2503 0.4603\r\nvt 0.2486 0.4613\r\nvt 0.2469 0.4623\r\nvt 0.2491 0.4569\r\nvt 0.2497 0.4566\r\nvt 0.2515 0.4596\r\nvt 0.2509 0.46\r\nvt 0.6784 0.1753\r\nvt 0.6801 0.1762\r\nvt 0.6766 0.1743\r\nvt 0.6783 0.1713\r\nvt 0.68 0.1722\r\nvt 0.6817 0.1731\r\nvt 0.6761 0.174\r\nvt 0.6756 0.1737\r\nvt 0.6772 0.1707\r\nvt 0.6777 0.171\r\nvt 0.2503 0.4643\r\nvt 0.2486 0.4653\r\nvt 0.252 0.4633\r\nvt 0.2537 0.4665\r\nvt 0.252 0.4673\r\nvt 0.2502 0.4681\r\nvt 0.2526 0.463\r\nvt 0.2532 0.4627\r\nvt 0.2549 0.4659\r\nvt 0.2544 0.4662\r\nvt 0.6816 0.1692\r\nvt 0.6833 0.1701\r\nvt 0.6798 0.1683\r\nvt 0.6813 0.1654\r\nvt 0.6831 0.1661\r\nvt 0.6849 0.1669\r\nvt 0.6793 0.168\r\nvt 0.6788 0.1678\r\nvt 0.6802 0.1649\r\nvt 0.6807 0.1652\r\nvt 0.2538 0.4706\r\nvt 0.2518 0.471\r\nvt 0.2557 0.4701\r\nvt 0.2553 0.4737\r\nvt 0.2531 0.4736\r\nvt 0.2564 0.4699\r\nvt 0.257 0.4695\r\nvt 0.2595 0.4724\r\nvt 0.2591 0.473\r\nvt 0.6847 0.1629\r\nvt 0.6867 0.1632\r\nvt 0.6827 0.1626\r\nvt 0.6838 0.16\r\nvt 0.686 0.1599\r\nvt 0.682 0.1624\r\nvt 0.6816 0.1622\r\nvt 0.6826 0.16\r\nvt 0.6831 0.1601\r\nvt 0.2563 0.4761\r\nvt 0.254 0.4757\r\nvt 0.2564 0.478\r\nvt 0.2541 0.4773\r\nvt 0.6868 0.1578\r\nvt 0.6845 0.1582\r\nvt 0.6845 0.1568\r\nvt 0.687 0.1562\r\nvt 0.6837 0.1584\r\nvt 0.6831 0.1583\r\nvt 0.6831 0.1571\r\nvt 0.6837 0.157\r\nvt 0.2551 0.4796\r\nvt 0.2532 0.4787\r\nvt 0.257 0.4807\r\nvt 0.2547 0.4827\r\nvt 0.2532 0.4814\r\nvt 0.2517 0.4803\r\nvt 0.2576 0.4811\r\nvt 0.2579 0.4817\r\nvt 0.2557 0.4836\r\nvt 0.2553 0.4831\r\nvt 0.6858 0.1548\r\nvt 0.6878 0.1538\r\nvt 0.6838 0.1556\r\nvt 0.6827 0.1542\r\nvt 0.6843 0.1531\r\nvt 0.6858 0.152\r\nvt 0.6831 0.1559\r\nvt 0.6826 0.1561\r\nvt 0.6817 0.1548\r\nvt 0.6821 0.1545\r\nvt 0.2515 0.4834\r\nvt 0.25 0.4822\r\nvt 0.253 0.4847\r\nvt 0.2515 0.4867\r\nvt 0.2499 0.4856\r\nvt 0.2483 0.4844\r\nvt 0.2535 0.4851\r\nvt 0.254 0.4855\r\nvt 0.2526 0.4875\r\nvt 0.2521 0.4871\r\nvt 0.6828 0.1511\r\nvt 0.6843 0.15\r\nvt 0.6812 0.1523\r\nvt 0.6797 0.15\r\nvt 0.6813 0.149\r\nvt 0.6829 0.1479\r\nvt 0.6807 0.1526\r\nvt 0.6803 0.1529\r\nvt 0.6787 0.1507\r\nvt 0.6791 0.1504\r\nvt 0.2484 0.4879\r\nvt 0.2467 0.4868\r\nvt 0.25 0.489\r\nvt 0.2485 0.4914\r\nvt 0.2468 0.4904\r\nvt 0.2452 0.4894\r\nvt 0.2506 0.4893\r\nvt 0.2511 0.4897\r\nvt 0.2497 0.4921\r\nvt 0.2491 0.4918\r\nvt 0.6799 0.1466\r\nvt 0.6816 0.1457\r\nvt 0.6782 0.1476\r\nvt 0.6768 0.1451\r\nvt 0.6785 0.1441\r\nvt 0.6803 0.1432\r\nvt 0.6777 0.148\r\nvt 0.6772 0.1482\r\nvt 0.6758 0.1456\r\nvt 0.6762 0.1454\r\nvt 0.2454 0.4931\r\nvt 0.2436 0.4921\r\nvt 0.2471 0.494\r\nvt 0.2457 0.4967\r\nvt 0.2439 0.4958\r\nvt 0.2422 0.495\r\nvt 0.2477 0.4943\r\nvt 0.2483 0.4946\r\nvt 0.2469 0.4973\r\nvt 0.2463 0.497\r\nvt 0.6772 0.1415\r\nvt 0.6789 0.1406\r\nvt 0.6754 0.1424\r\nvt 0.6741 0.1396\r\nvt 0.6759 0.1388\r\nvt 0.6776 0.1379\r\nvt 0.6749 0.1427\r\nvt 0.6744 0.1429\r\nvt 0.673 0.1401\r\nvt 0.6735 0.1399\r\nvt 0.2425 0.4987\r\nvt 0.2407 0.4979\r\nvt 0.2443 0.4996\r\nvt 0.2429 0.5025\r\nvt 0.2411 0.5017\r\nvt 0.2394 0.5009\r\nvt 0.2449 0.4999\r\nvt 0.2455 0.5002\r\nvt 0.2442 0.5031\r\nvt 0.2436 0.5028\r\nvt 0.6746 0.1359\r\nvt 0.6764 0.1351\r\nvt 0.6728 0.1367\r\nvt 0.6715 0.1337\r\nvt 0.6733 0.133\r\nvt 0.6751 0.1322\r\nvt 0.6722 0.137\r\nvt 0.6717 0.1372\r\nvt 0.6704 0.1342\r\nvt 0.6709 0.134\r\nvt 0.2398 0.5048\r\nvt 0.238 0.504\r\nvt 0.2416 0.5055\r\nvt 0.2403 0.5087\r\nvt 0.2385 0.5079\r\nvt 0.2367 0.5072\r\nvt 0.2423 0.5058\r\nvt 0.2429 0.5061\r\nvt 0.2416 0.5092\r\nvt 0.241 0.5089\r\nvt 0.672 0.1299\r\nvt 0.6738 0.1292\r\nvt 0.6702 0.1307\r\nvt 0.669 0.1276\r\nvt 0.6708 0.1268\r\nvt 0.6726 0.1261\r\nvt 0.6696 0.1309\r\nvt 0.6691 0.1311\r\nvt 0.6679 0.128\r\nvt 0.6684 0.1278\r\nvt 0.2372 0.5111\r\nvt 0.2354 0.5104\r\nvt 0.239 0.5118\r\nvt 0.2378 0.5151\r\nvt 0.2359 0.5144\r\nvt 0.2341 0.5137\r\nvt 0.2397 0.5121\r\nvt 0.2403 0.5124\r\nvt 0.239 0.5156\r\nvt 0.2384 0.5153\r\nvt 0.6696 0.1237\r\nvt 0.6714 0.123\r\nvt 0.6677 0.1244\r\nvt 0.6665 0.1212\r\nvt 0.6683 0.1205\r\nvt 0.6702 0.1198\r\nvt 0.6671 0.1246\r\nvt 0.6666 0.1248\r\nvt 0.6654 0.1216\r\nvt 0.6659 0.1214\r\nvt 0.2347 0.5177\r\nvt 0.2328 0.517\r\nvt 0.2365 0.5184\r\nvt 0.2352 0.5217\r\nvt 0.2334 0.521\r\nvt 0.2316 0.5203\r\nvt 0.2372 0.5186\r\nvt 0.2378 0.5189\r\nvt 0.2365 0.5222\r\nvt 0.2359 0.522\r\nvt 0.6671 0.1172\r\nvt 0.6689 0.1165\r\nvt 0.6653 0.1179\r\nvt 0.6641 0.1146\r\nvt 0.6659 0.1139\r\nvt 0.6677 0.1132\r\nvt 0.6647 0.1181\r\nvt 0.6642 0.1183\r\nvt 0.6629 0.115\r\nvt 0.6634 0.1148\r\nvt 0.2322 0.5244\r\nvt 0.2303 0.5237\r\nvt 0.234 0.5251\r\nvt 0.2328 0.5285\r\nvt 0.2309 0.5278\r\nvt 0.2291 0.5271\r\nvt 0.2347 0.5253\r\nvt 0.2353 0.5255\r\nvt 0.234 0.5289\r\nvt 0.2334 0.5287\r\nvt 0.6647 0.1106\r\nvt 0.6665 0.1099\r\nvt 0.6628 0.1113\r\nvt 0.6616 0.1079\r\nvt 0.6634 0.1073\r\nvt 0.6653 0.1066\r\nvt 0.6622 0.1115\r\nvt 0.6617 0.1117\r\nvt 0.6605 0.1083\r\nvt 0.661 0.1082\r\nvt 0.2297 0.5312\r\nvt 0.2278 0.5305\r\nvt 0.2315 0.5319\r\nvt 0.2303 0.5353\r\nvt 0.2284 0.5346\r\nvt 0.2266 0.534\r\nvt 0.2322 0.5321\r\nvt 0.2328 0.5323\r\nvt 0.2316 0.5358\r\nvt 0.2309 0.5356\r\nvt 0.6622 0.1039\r\nvt 0.6641 0.1032\r\nvt 0.6604 0.1046\r\nvt 0.6592 0.1012\r\nvt 0.661 0.1005\r\nvt 0.6628 0.0998\r\nvt 0.6598 0.1048\r\nvt 0.6593 0.105\r\nvt 0.658 0.1016\r\nvt 0.6585 0.1014\r\nvt 0.2272 0.5381\r\nvt 0.2253 0.5375\r\nvt 0.229 0.5388\r\nvt 0.2278 0.5423\r\nvt 0.2259 0.5416\r\nvt 0.2241 0.5409\r\nvt 0.2297 0.539\r\nvt 0.2303 0.5392\r\nvt 0.2291 0.5427\r\nvt 0.2284 0.5425\r\nvt 0.6598 0.0971\r\nvt 0.6616 0.0964\r\nvt 0.6579 0.0977\r\nvt 0.6567 0.0943\r\nvt 0.6585 0.0936\r\nvt 0.6604 0.093\r\nvt 0.6573 0.098\r\nvt 0.6568 0.0982\r\nvt 0.6556 0.0947\r\nvt 0.6561 0.0945\r\nvt 0.2247 0.5451\r\nvt 0.2228 0.5445\r\nvt 0.2265 0.5458\r\nvt 0.2252 0.5493\r\nvt 0.2234 0.5486\r\nvt 0.2216 0.548\r\nvt 0.2272 0.546\r\nvt 0.2278 0.5462\r\nvt 0.2265 0.5498\r\nvt 0.2259 0.5495\r\nvt 0.6573 0.0902\r\nvt 0.6591 0.0895\r\nvt 0.6554 0.0908\r\nvt 0.6542 0.0873\r\nvt 0.656 0.0867\r\nvt 0.6578 0.086\r\nvt 0.6548 0.0911\r\nvt 0.6543 0.0912\r\nvt 0.6531 0.0877\r\nvt 0.6535 0.0876\r\nvt 0.2221 0.5522\r\nvt 0.2203 0.5515\r\nvt 0.224 0.5529\r\nvt 0.2227 0.5564\r\nvt 0.2209 0.5558\r\nvt 0.219 0.5551\r\nvt 0.2246 0.5531\r\nvt 0.2253 0.5533\r\nvt 0.224 0.5569\r\nvt 0.2234 0.5567\r\nvt 0.6547 0.0832\r\nvt 0.6566 0.0825\r\nvt 0.6529 0.0838\r\nvt 0.6516 0.0803\r\nvt 0.6535 0.0796\r\nvt 0.6553 0.079\r\nvt 0.6523 0.084\r\nvt 0.6518 0.0842\r\nvt 0.6505 0.0807\r\nvt 0.651 0.0805\r\nvt 0.2196 0.5594\r\nvt 0.2177 0.5587\r\nvt 0.2214 0.56\r\nvt 0.2201 0.5636\r\nvt 0.2183 0.563\r\nvt 0.2165 0.5623\r\nvt 0.2221 0.5602\r\nvt 0.2227 0.5605\r\nvt 0.2214 0.5641\r\nvt 0.2208 0.5638\r\nvt 0.6522 0.0761\r\nvt 0.654 0.0754\r\nvt 0.6504 0.0767\r\nvt 0.6491 0.0731\r\nvt 0.6509 0.0725\r\nvt 0.6528 0.0718\r\nvt 0.6497 0.0769\r\nvt 0.6492 0.0771\r\nvt 0.648 0.0735\r\nvt 0.6485 0.0733\r\nvt 0.217 0.5666\r\nvt 0.2152 0.5659\r\nvt 0.2189 0.5672\r\nvt 0.2176 0.5708\r\nvt 0.2157 0.5702\r\nvt 0.2139 0.5695\r\nvt 0.2195 0.5675\r\nvt 0.2202 0.5677\r\nvt 0.2189 0.5713\r\nvt 0.2182 0.5711\r\nvt 0.6496 0.0689\r\nvt 0.6515 0.0682\r\nvt 0.6478 0.0695\r\nvt 0.6465 0.0659\r\nvt 0.6483 0.0652\r\nvt 0.6502 0.0646\r\nvt 0.6472 0.0697\r\nvt 0.6467 0.0699\r\nvt 0.6454 0.0663\r\nvt 0.6459 0.0661\r\nvt 0.2144 0.5738\r\nvt 0.2126 0.5732\r\nvt 0.2163 0.5745\r\nvt 0.215 0.5781\r\nvt 0.2132 0.5775\r\nvt 0.2113 0.5768\r\nvt 0.217 0.5747\r\nvt 0.2176 0.5749\r\nvt 0.2163 0.5786\r\nvt 0.2157 0.5783\r\nvt 0.647 0.0616\r\nvt 0.6489 0.0609\r\nvt 0.6452 0.0623\r\nvt 0.6439 0.0586\r\nvt 0.6457 0.058\r\nvt 0.6475 0.0573\r\nvt 0.6446 0.0625\r\nvt 0.6441 0.0627\r\nvt 0.6428 0.059\r\nvt 0.6432 0.0588\r\nvt 0.2119 0.5811\r\nvt 0.21 0.5805\r\nvt 0.2137 0.5818\r\nvt 0.2124 0.5854\r\nvt 0.2106 0.5847\r\nvt 0.2087 0.5841\r\nvt 0.2144 0.582\r\nvt 0.215 0.5822\r\nvt 0.2137 0.5858\r\nvt 0.2131 0.5856\r\nvt 0.6444 0.0543\r\nvt 0.6462 0.0536\r\nvt 0.6426 0.055\r\nvt 0.6413 0.0513\r\nvt 0.6431 0.0507\r\nvt 0.6449 0.05\r\nvt 0.6419 0.0552\r\nvt 0.6414 0.0554\r\nvt 0.6401 0.0517\r\nvt 0.6406 0.0516\r\nvt 0.2093 0.5882\r\nvt 0.2075 0.5876\r\nvt 0.2112 0.5889\r\nvt 0.21 0.5923\r\nvt 0.2082 0.5916\r\nvt 0.2063 0.591\r\nvt 0.2119 0.5891\r\nvt 0.2125 0.5893\r\nvt 0.2113 0.5927\r\nvt 0.2107 0.5925\r\nvt 0.6418 0.0472\r\nvt 0.6437 0.0465\r\nvt 0.64 0.0478\r\nvt 0.6388 0.0444\r\nvt 0.6406 0.0437\r\nvt 0.6425 0.0431\r\nvt 0.6394 0.048\r\nvt 0.6389 0.0482\r\nvt 0.6377 0.0448\r\nvt 0.6382 0.0446\r\nvt 0.2071 0.5949\r\nvt 0.2052 0.5942\r\nvt 0.2089 0.5955\r\nvt 0.2079 0.5985\r\nvt 0.2061 0.5978\r\nvt 0.2042 0.5972\r\nvt 0.2096 0.5957\r\nvt 0.2102 0.5959\r\nvt 0.2092 0.5989\r\nvt 0.2086 0.5987\r\nvt 0.6395 0.0405\r\nvt 0.6414 0.0399\r\nvt 0.6377 0.0411\r\nvt 0.6367 0.0381\r\nvt 0.6385 0.0375\r\nvt 0.6404 0.0369\r\nvt 0.6371 0.0414\r\nvt 0.6366 0.0415\r\nvt 0.6355 0.0385\r\nvt 0.636 0.0383\r\nvt 0.2052 0.6006\r\nvt 0.2033 0.6\r\nvt 0.207 0.6012\r\nvt 0.2063 0.6035\r\nvt 0.2044 0.6029\r\nvt 0.2026 0.6023\r\nvt 0.2077 0.6014\r\nvt 0.2084 0.6016\r\nvt 0.2076 0.604\r\nvt 0.207 0.6038\r\nvt 0.6376 0.0348\r\nvt 0.6395 0.0342\r\nvt 0.6358 0.0354\r\nvt 0.635 0.033\r\nvt 0.6368 0.0324\r\nvt 0.6387 0.0318\r\nvt 0.6351 0.0356\r\nvt 0.6346 0.0358\r\nvt 0.6338 0.0334\r\nvt 0.6343 0.0332\r\nvt 0.2038 0.6049\r\nvt 0.2019 0.6044\r\nvt 0.2056 0.6055\r\nvt 0.2051 0.6072\r\nvt 0.2033 0.6066\r\nvt 0.2014 0.606\r\nvt 0.2063 0.6057\r\nvt 0.207 0.6059\r\nvt 0.2064 0.6076\r\nvt 0.2058 0.6074\r\nvt 0.6362 0.0304\r\nvt 0.638 0.0298\r\nvt 0.6343 0.031\r\nvt 0.6338 0.0293\r\nvt 0.6356 0.0287\r\nvt 0.6375 0.0281\r\nvt 0.6337 0.0312\r\nvt 0.6332 0.0313\r\nvt 0.6326 0.0296\r\nvt 0.6331 0.0295\r\nvt 0.2028 0.608\r\nvt 0.201 0.6074\r\nvt 0.2047 0.6086\r\nvt 0.2043 0.6097\r\nvt 0.2025 0.6091\r\nvt 0.2006 0.6085\r\nvt 0.2054 0.6088\r\nvt 0.206 0.609\r\nvt 0.2056 0.6101\r\nvt 0.205 0.6099\r\nvt 0.6352 0.0273\r\nvt 0.6371 0.0266\r\nvt 0.6333 0.0278\r\nvt 0.633 0.0267\r\nvt 0.6348 0.0261\r\nvt 0.6367 0.0254\r\nvt 0.6327 0.028\r\nvt 0.6322 0.0282\r\nvt 0.6318 0.027\r\nvt 0.6323 0.0269\r\nvt 0.2021 0.61\r\nvt 0.2003 0.6093\r\nvt 0.204 0.6106\r\nvt 0.2037 0.6114\r\nvt 0.2019 0.6107\r\nvt 0.2 0.6101\r\nvt 0.2047 0.6109\r\nvt 0.2053 0.6111\r\nvt 0.205 0.6119\r\nvt 0.2044 0.6117\r\nvt 0.6345 0.0251\r\nvt 0.6363 0.0244\r\nvt 0.6326 0.0257\r\nvt 0.6323 0.0248\r\nvt 0.6342 0.0242\r\nvt 0.636 0.0235\r\nvt 0.632 0.0259\r\nvt 0.6315 0.0261\r\nvt 0.6312 0.0252\r\nvt 0.6317 0.0251\r\nvt 0.2016 0.6114\r\nvt 0.1998 0.6107\r\nvt 0.2035 0.6121\r\nvt 0.2032 0.6127\r\nvt 0.2014 0.612\r\nvt 0.1996 0.6112\r\nvt 0.2042 0.6123\r\nvt 0.2048 0.6126\r\nvt 0.2045 0.6132\r\nvt 0.2039 0.613\r\nvt 0.6339 0.0234\r\nvt 0.6357 0.0227\r\nvt 0.6321 0.0241\r\nvt 0.6318 0.0234\r\nvt 0.6336 0.0227\r\nvt 0.6354 0.022\r\nvt 0.6314 0.0243\r\nvt 0.631 0.0245\r\nvt 0.6307 0.0239\r\nvt 0.6312 0.0237\r\nvt 0.2012 0.6126\r\nvt 0.1993 0.6118\r\nvt 0.203 0.6133\r\nvt 0.2027 0.614\r\nvt 0.2009 0.6132\r\nvt 0.1991 0.6124\r\nvt 0.2036 0.6136\r\nvt 0.2042 0.6139\r\nvt 0.204 0.6146\r\nvt 0.2034 0.6143\r\nvt 0.6333 0.022\r\nvt 0.6351 0.0212\r\nvt 0.6315 0.0227\r\nvt 0.6312 0.0221\r\nvt 0.633 0.0213\r\nvt 0.6348 0.0205\r\nvt 0.6309 0.023\r\nvt 0.6304 0.0232\r\nvt 0.6301 0.0225\r\nvt 0.6306 0.0223\r\nvt 0.2006 0.6138\r\nvt 0.1988 0.613\r\nvt 0.2024 0.6147\r\nvt 0.2021 0.6154\r\nvt 0.2003 0.6145\r\nvt 0.1985 0.6137\r\nvt 0.203 0.615\r\nvt 0.2036 0.6152\r\nvt 0.2033 0.616\r\nvt 0.2027 0.6157\r\nvt 0.6327 0.0205\r\nvt 0.6345 0.0197\r\nvt 0.6309 0.0214\r\nvt 0.6306 0.0206\r\nvt 0.6323 0.0198\r\nvt 0.6341 0.019\r\nvt 0.6303 0.0216\r\nvt 0.6298 0.0219\r\nvt 0.6295 0.0211\r\nvt 0.63 0.0209\r\nvt 0.1999 0.6152\r\nvt 0.1982 0.6144\r\nvt 0.2017 0.6161\r\nvt 0.2013 0.6169\r\nvt 0.1995 0.616\r\nvt 0.1978 0.6152\r\nvt 0.2023 0.6164\r\nvt 0.2029 0.6167\r\nvt 0.2025 0.6176\r\nvt 0.2019 0.6173\r\nvt 0.6319 0.019\r\nvt 0.6337 0.0181\r\nvt 0.6302 0.0198\r\nvt 0.6298 0.019\r\nvt 0.6315 0.0181\r\nvt 0.6332 0.0172\r\nvt 0.6296 0.0201\r\nvt 0.6291 0.0204\r\nvt 0.6287 0.0196\r\nvt 0.6292 0.0193\r\nvt 0.1991 0.6169\r\nvt 0.1974 0.616\r\nvt 0.2008 0.6178\r\nvt 0.2003 0.6188\r\nvt 0.1986 0.6179\r\nvt 0.1969 0.617\r\nvt 0.2014 0.6182\r\nvt 0.202 0.6185\r\nvt 0.2015 0.6195\r\nvt 0.2009 0.6192\r\nvt 0.631 0.0172\r\nvt 0.6328 0.0163\r\nvt 0.6293 0.0181\r\nvt 0.6288 0.0171\r\nvt 0.6305 0.0162\r\nvt 0.6322 0.0153\r\nvt 0.6287 0.0184\r\nvt 0.6282 0.0186\r\nvt 0.6277 0.0176\r\nvt 0.6282 0.0174\r\nvt 0.198 0.619\r\nvt 0.1963 0.6181\r\nvt 0.1997 0.62\r\nvt 0.1991 0.6212\r\nvt 0.1974 0.6202\r\nvt 0.1956 0.6193\r\nvt 0.2003 0.6203\r\nvt 0.2009 0.6206\r\nvt 0.2003 0.6218\r\nvt 0.1997 0.6215\r\nvt 0.6299 0.015\r\nvt 0.6316 0.0141\r\nvt 0.6282 0.016\r\nvt 0.6275 0.0148\r\nvt 0.6292 0.0138\r\nvt 0.631 0.0129\r\nvt 0.6276 0.0163\r\nvt 0.6271 0.0165\r\nvt 0.6265 0.0153\r\nvt 0.6269 0.0151\r\nvt 0.1967 0.6215\r\nvt 0.1949 0.6205\r\nvt 0.1984 0.6224\r\nvt 0.1976 0.6238\r\nvt 0.1959 0.6228\r\nvt 0.1942 0.6219\r\nvt 0.199 0.6228\r\nvt 0.1996 0.6231\r\nvt 0.1988 0.6245\r\nvt 0.1982 0.6241\r\nvt 0.6285 0.0125\r\nvt 0.6302 0.0116\r\nvt 0.6268 0.0135\r\nvt 0.6261 0.0121\r\nvt 0.6278 0.0111\r\nvt 0.6295 0.0102\r\nvt 0.6262 0.0138\r\nvt 0.6258 0.014\r\nvt 0.625 0.0127\r\nvt 0.6255 0.0124\r\nvt 0.1951 0.6242\r\nvt 0.1934 0.6233\r\nvt 0.1968 0.6252\r\nvt 0.196 0.6267\r\nvt 0.1943 0.6257\r\nvt 0.1926 0.6247\r\nvt 0.1974 0.6256\r\nvt 0.198 0.6259\r\nvt 0.1972 0.6274\r\nvt 0.1966 0.6271\r\nvt 0.627 0.0097\r\nvt 0.6287 0.0087\r\nvt 0.6253 0.0107\r\nvt 0.6244 0.0092\r\nvt 0.6261 0.0082\r\nvt 0.6278 0.0072\r\nvt 0.6247 0.011\r\nvt 0.6242 0.0113\r\nvt 0.6233 0.0098\r\nvt 0.6238 0.0095\r\nvt 0.1934 0.6271\r\nvt 0.1917 0.6261\r\nvt 0.1951 0.6282\r\nvt 0.1942 0.6297\r\nvt 0.1926 0.6286\r\nvt 0.1908 0.6276\r\nvt 0.1957 0.6286\r\nvt 0.1963 0.629\r\nvt 0.1953 0.6306\r\nvt 0.1948 0.6301\r\nvt 0.6252 0.0067\r\nvt 0.6269 0.0057\r\nvt 0.6235 0.0077\r\nvt 0.6226 0.0062\r\nvt 0.6244 0.0052\r\nvt 0.626 0.0041\r\nvt 0.6229 0.008\r\nvt 0.6224 0.0082\r\nvt 0.6215 0.0066\r\nvt 0.622 0.0064\r\nvt 0.1917 0.63\r\nvt 0.1901 0.6292\r\nvt 0.1932 0.6311\r\nvt 0.1922 0.6316\r\nvt 0.191 0.6312\r\nvt 0.1901 0.6302\r\nvt 0.1937 0.6315\r\nvt 0.194 0.632\r\nvt 0.1924 0.6328\r\nvt 0.1924 0.6321\r\nvt 0.6234 0.0037\r\nvt 0.625 0.0027\r\nvt 0.6218 0.0046\r\nvt 0.6218 0.0035\r\nvt 0.6227 0.0025\r\nvt 0.624 0.0022\r\nvt 0.6213 0.0048\r\nvt 0.6208 0.0049\r\nvt 0.6208 0.0032\r\nvt 0.6212 0.0034\r\nvt 0.6225 0.0021\r\nvt 0.6242 0.0016\r\nvt 0.6222 0.0017\r\nvt 0.6242 0.0011\r\nvt 0.5677 0.623\r\nvt 0.5703 0.6248\r\nvt 0.5698 0.6252\r\nvt 0.5673 0.6236\r\nvt 0.5717 0.6267\r\nvt 0.571 0.6269\r\nvt 0.5669 0.6315\r\nvt 0.5638 0.6301\r\nvt 0.5699 0.6316\r\nvt 0.9945 0.0057\r\nvt 0.9943 0.0045\r\nvt 0.9973 0.0044\r\nvt 0.9918 0.007\r\nvt 0.9913 0.0059\r\nvt 0.5619 0.6198\r\nvt 0.5649 0.6215\r\nvt 0.5645 0.6221\r\nvt 0.5616 0.6204\r\nvt 0.5609 0.6284\r\nvt 0.5579 0.6267\r\nvt 0.989 0.0086\r\nvt 0.9884 0.0076\r\nvt 0.986 0.0103\r\nvt 0.9854 0.0093\r\nvt 0.5555 0.6162\r\nvt 0.5588 0.618\r\nvt 0.5584 0.6186\r\nvt 0.5552 0.6168\r\nvt 0.5548 0.6249\r\nvt 0.5516 0.623\r\nvt 0.9829 0.0121\r\nvt 0.9823 0.0111\r\nvt 0.9796 0.0139\r\nvt 0.979 0.0129\r\nvt 0.5491 0.6125\r\nvt 0.5523 0.6143\r\nvt 0.552 0.6149\r\nvt 0.5487 0.6131\r\nvt 0.5484 0.6212\r\nvt 0.5452 0.6193\r\nvt 0.9763 0.0158\r\nvt 0.9758 0.0147\r\nvt 0.9731 0.0176\r\nvt 0.9725 0.0166\r\nvt 0.543 0.609\r\nvt 0.546 0.6107\r\nvt 0.5456 0.6113\r\nvt 0.5427 0.6096\r\nvt 0.5421 0.6176\r\nvt 0.5391 0.6159\r\nvt 0.97 0.0194\r\nvt 0.9694 0.0183\r\nvt 0.9671 0.021\r\nvt 0.9665 0.02\r\nvt 0.538 0.6062\r\nvt 0.5403 0.6075\r\nvt 0.54 0.6081\r\nvt 0.5376 0.6067\r\nvt 0.5364 0.6144\r\nvt 0.5341 0.613\r\nvt 0.9643 0.0225\r\nvt 0.9638 0.0215\r\nvt 0.962 0.0239\r\nvt 0.9614 0.0229\r\nvt 0.5343 0.6041\r\nvt 0.536 0.605\r\nvt 0.5356 0.6056\r\nvt 0.534 0.6047\r\nvt 0.5321 0.6119\r\nvt 0.5304 0.611\r\nvt 0.9599 0.025\r\nvt 0.9594 0.024\r\nvt 0.9582 0.026\r\nvt 0.9577 0.0249\r\nvt 0.5318 0.6027\r\nvt 0.5329 0.6033\r\nvt 0.5326 0.6039\r\nvt 0.5315 0.6033\r\nvt 0.529 0.6102\r\nvt 0.5279 0.6095\r\nvt 0.9568 0.0268\r\nvt 0.9562 0.0257\r\nvt 0.9557 0.0274\r\nvt 0.9551 0.0264\r\nvt 0.5301 0.6017\r\nvt 0.5309 0.6021\r\nvt 0.5305 0.6027\r\nvt 0.5298 0.6023\r\nvt 0.5269 0.609\r\nvt 0.5261 0.6085\r\nvt 0.9547 0.028\r\nvt 0.9541 0.027\r\nvt 0.9538 0.0285\r\nvt 0.9532 0.0275\r\nvt 0.5288 0.6009\r\nvt 0.5294 0.6013\r\nvt 0.5291 0.6019\r\nvt 0.5284 0.6015\r\nvt 0.5254 0.6081\r\nvt 0.5248 0.6078\r\nvt 0.9531 0.0289\r\nvt 0.9524 0.0279\r\nvt 0.9523 0.0294\r\nvt 0.9517 0.0283\r\nvt 0.5274 0.6001\r\nvt 0.5281 0.6005\r\nvt 0.5278 0.6011\r\nvt 0.5271 0.6007\r\nvt 0.5241 0.6074\r\nvt 0.5234 0.607\r\nvt 0.9516 0.0298\r\nvt 0.951 0.0287\r\nvt 0.9509 0.0302\r\nvt 0.9503 0.0292\r\nvt 0.5257 0.5992\r\nvt 0.5266 0.5997\r\nvt 0.5263 0.6003\r\nvt 0.5254 0.5997\r\nvt 0.5226 0.6065\r\nvt 0.5217 0.606\r\nvt 0.9501 0.0307\r\nvt 0.9495 0.0297\r\nvt 0.9491 0.0312\r\nvt 0.9485 0.0302\r\nvt 0.5233 0.5978\r\nvt 0.5246 0.5985\r\nvt 0.5243 0.5991\r\nvt 0.523 0.5983\r\nvt 0.5207 0.6053\r\nvt 0.5194 0.6046\r\nvt 0.948 0.0319\r\nvt 0.9474 0.0309\r\nvt 0.9466 0.0327\r\nvt 0.946 0.0316\r\nvt 0.5199 0.5958\r\nvt 0.5218 0.5969\r\nvt 0.5214 0.5974\r\nvt 0.5196 0.5964\r\nvt 0.5178 0.6037\r\nvt 0.516 0.6026\r\nvt 0.9451 0.0336\r\nvt 0.9445 0.0326\r\nvt 0.9432 0.0347\r\nvt 0.9426 0.0336\r\nvt 0.5152 0.5931\r\nvt 0.5177 0.5945\r\nvt 0.5174 0.5951\r\nvt 0.5148 0.5936\r\nvt 0.5138 0.6013\r\nvt 0.5113 0.5999\r\nvt 0.941 0.0359\r\nvt 0.9404 0.0349\r\nvt 0.9384 0.0374\r\nvt 0.9379 0.0363\r\nvt 0.5094 0.5898\r\nvt 0.5124 0.5915\r\nvt 0.5121 0.5921\r\nvt 0.5091 0.5904\r\nvt 0.5085 0.5983\r\nvt 0.5055 0.5967\r\nvt 0.9357 0.039\r\nvt 0.9351 0.0379\r\nvt 0.9327 0.0406\r\nvt 0.9321 0.0396\r\nvt 0.503 0.5861\r\nvt 0.5062 0.588\r\nvt 0.5059 0.5886\r\nvt 0.5026 0.5867\r\nvt 0.5024 0.5949\r\nvt 0.4991 0.593\r\nvt 0.9295 0.0424\r\nvt 0.9289 0.0414\r\nvt 0.9262 0.0443\r\nvt 0.9257 0.0432\r\nvt 0.4962 0.5824\r\nvt 0.4996 0.5843\r\nvt 0.4993 0.5848\r\nvt 0.4958 0.5829\r\nvt 0.4957 0.5911\r\nvt 0.4923 0.5893\r\nvt 0.9229 0.0461\r\nvt 0.9223 0.0451\r\nvt 0.9195 0.048\r\nvt 0.9189 0.047\r\nvt 0.4894 0.5786\r\nvt 0.4928 0.5805\r\nvt 0.4924 0.581\r\nvt 0.489 0.5792\r\nvt 0.4889 0.5874\r\nvt 0.4855 0.5855\r\nvt 0.9161 0.0499\r\nvt 0.9155 0.0489\r\nvt 0.9127 0.0518\r\nvt 0.9121 0.0508\r\nvt 0.4826 0.5748\r\nvt 0.486 0.5767\r\nvt 0.4857 0.5773\r\nvt 0.4823 0.5754\r\nvt 0.4822 0.5836\r\nvt 0.4788 0.5817\r\nvt 0.9093 0.0537\r\nvt 0.9087 0.0526\r\nvt 0.9059 0.0555\r\nvt 0.9054 0.0545\r\nvt 0.4759 0.5711\r\nvt 0.4792 0.573\r\nvt 0.4789 0.5736\r\nvt 0.4756 0.5717\r\nvt 0.4754 0.5799\r\nvt 0.4721 0.578\r\nvt 0.9026 0.0574\r\nvt 0.902 0.0563\r\nvt 0.8993 0.0592\r\nvt 0.8987 0.0582\r\nvt 0.4693 0.5674\r\nvt 0.4726 0.5693\r\nvt 0.4722 0.5699\r\nvt 0.4689 0.568\r\nvt 0.4687 0.5762\r\nvt 0.4654 0.5743\r\nvt 0.8959 0.061\r\nvt 0.8954 0.06\r\nvt 0.8927 0.0629\r\nvt 0.8921 0.0618\r\nvt 0.4627 0.5638\r\nvt 0.466 0.5656\r\nvt 0.4656 0.5662\r\nvt 0.4624 0.5644\r\nvt 0.4621 0.5725\r\nvt 0.4588 0.5707\r\nvt 0.8894 0.0647\r\nvt 0.8888 0.0636\r\nvt 0.8862 0.0665\r\nvt 0.8856 0.0654\r\nvt 0.4562 0.5602\r\nvt 0.4594 0.562\r\nvt 0.4591 0.5626\r\nvt 0.4559 0.5608\r\nvt 0.4556 0.5689\r\nvt 0.4524 0.5671\r\nvt 0.8829 0.0683\r\nvt 0.8824 0.0672\r\nvt 0.8798 0.07\r\nvt 0.8792 0.069\r\nvt 0.4498 0.5566\r\nvt 0.453 0.5584\r\nvt 0.4527 0.559\r\nvt 0.4495 0.5572\r\nvt 0.4491 0.5653\r\nvt 0.4459 0.5635\r\nvt 0.8766 0.0718\r\nvt 0.876 0.0708\r\nvt 0.8735 0.0736\r\nvt 0.8729 0.0725\r\nvt 0.4436 0.553\r\nvt 0.4467 0.5548\r\nvt 0.4464 0.5554\r\nvt 0.4432 0.5536\r\nvt 0.4428 0.5617\r\nvt 0.4396 0.5599\r\nvt 0.8703 0.0753\r\nvt 0.8698 0.0743\r\nvt 0.8673 0.0771\r\nvt 0.8667 0.076\r\nvt 0.4374 0.5495\r\nvt 0.4405 0.5513\r\nvt 0.4401 0.5518\r\nvt 0.4371 0.5501\r\nvt 0.4365 0.5581\r\nvt 0.4334 0.5563\r\nvt 0.8642 0.0788\r\nvt 0.8636 0.0778\r\nvt 0.8612 0.0806\r\nvt 0.8606 0.0795\r\nvt 0.4315 0.5459\r\nvt 0.4344 0.5477\r\nvt 0.4341 0.5483\r\nvt 0.4311 0.5465\r\nvt 0.4304 0.5545\r\nvt 0.4274 0.5527\r\nvt 0.8582 0.0823\r\nvt 0.8576 0.0813\r\nvt 0.8552 0.0841\r\nvt 0.8546 0.0831\r\nvt 0.4258 0.5424\r\nvt 0.4286 0.5442\r\nvt 0.4283 0.5447\r\nvt 0.4255 0.543\r\nvt 0.4244 0.5508\r\nvt 0.4216 0.549\r\nvt 0.8524 0.0859\r\nvt 0.8517 0.0849\r\nvt 0.8495 0.0877\r\nvt 0.8489 0.0867\r\nvt 0.4205 0.5389\r\nvt 0.4231 0.5406\r\nvt 0.4228 0.5412\r\nvt 0.4201 0.5394\r\nvt 0.4188 0.5472\r\nvt 0.416 0.5454\r\nvt 0.8468 0.0894\r\nvt 0.8462 0.0884\r\nvt 0.8442 0.0912\r\nvt 0.8435 0.0902\r\nvt 0.4156 0.5353\r\nvt 0.418 0.5371\r\nvt 0.4176 0.5376\r\nvt 0.4152 0.5359\r\nvt 0.4134 0.5435\r\nvt 0.4109 0.5417\r\nvt 0.8416 0.093\r\nvt 0.841 0.092\r\nvt 0.8392 0.0948\r\nvt 0.8385 0.0938\r\nvt 0.411 0.5319\r\nvt 0.4133 0.5336\r\nvt 0.4129 0.5342\r\nvt 0.4106 0.5325\r\nvt 0.4085 0.5399\r\nvt 0.4063 0.5383\r\nvt 0.8369 0.0965\r\nvt 0.8362 0.0955\r\nvt 0.8348 0.0981\r\nvt 0.834 0.0971\r\nvt 0.4069 0.5288\r\nvt 0.4089 0.5303\r\nvt 0.4085 0.5309\r\nvt 0.4064 0.5293\r\nvt 0.4043 0.5367\r\nvt 0.4024 0.5354\r\nvt 0.8328 0.0996\r\nvt 0.8321 0.0987\r\nvt 0.8309 0.101\r\nvt 0.8302 0.1\r\nvt 0.4007 0.5342\r\nvt 0.399 0.5333\r\nvt 0.8292 0.1021\r\nvt 0.8286 0.1011\r\nvt 0.8275 0.1031\r\nvt 0.827 0.102\r\nvt 0.3974 0.5326\r\nvt 0.3955 0.532\r\nvt 0.8257 0.1038\r\nvt 0.8253 0.1027\r\nvt 0.8238 0.1044\r\nvt 0.8235 0.1033\r\nvt 0.3931 0.5231\r\nvt 0.3955 0.5238\r\nvt 0.3953 0.5244\r\nvt 0.3929 0.5238\r\nvt 0.3979 0.5244\r\nvt 0.3977 0.5251\r\nvt 0.3934 0.5314\r\nvt 0.391 0.5308\r\nvt 0.8217 0.105\r\nvt 0.8214 0.1039\r\nvt 0.8194 0.1056\r\nvt 0.8191 0.1045\r\nvt 0.388 0.5218\r\nvt 0.3906 0.5225\r\nvt 0.3904 0.5231\r\nvt 0.3878 0.5224\r\nvt 0.3885 0.5301\r\nvt 0.3859 0.5294\r\nvt 0.817 0.1062\r\nvt 0.8167 0.1051\r\nvt 0.8144 0.1069\r\nvt 0.8141 0.1058\r\nvt 0.3828 0.5202\r\nvt 0.3854 0.521\r\nvt 0.3852 0.5217\r\nvt 0.3826 0.5208\r\nvt 0.3831 0.5286\r\nvt 0.3804 0.5277\r\nvt 0.8118 0.1077\r\nvt 0.8114 0.1065\r\nvt 0.8091 0.1085\r\nvt 0.8088 0.1074\r\nvt 0.3776 0.5184\r\nvt 0.3802 0.5193\r\nvt 0.38 0.52\r\nvt 0.3773 0.5191\r\nvt 0.3776 0.5268\r\nvt 0.3749 0.5259\r\nvt 0.8064 0.1094\r\nvt 0.8061 0.1083\r\nvt 0.8038 0.1103\r\nvt 0.8034 0.1092\r\nvt 0.3723 0.5165\r\nvt 0.3749 0.5175\r\nvt 0.3747 0.5181\r\nvt 0.3721 0.5171\r\nvt 0.3722 0.5249\r\nvt 0.3695 0.5238\r\nvt 0.8011 0.1113\r\nvt 0.8007 0.1102\r\nvt 0.7985 0.1123\r\nvt 0.798 0.1112\r\nvt 0.3672 0.5144\r\nvt 0.3697 0.5154\r\nvt 0.3695 0.516\r\nvt 0.3669 0.515\r\nvt 0.3668 0.5227\r\nvt 0.3641 0.5216\r\nvt 0.7959 0.1134\r\nvt 0.7954 0.1123\r\nvt 0.7933 0.1144\r\nvt 0.7928 0.1133\r\nvt 0.3621 0.5122\r\nvt 0.3646 0.5133\r\nvt 0.3644 0.5139\r\nvt 0.3618 0.5128\r\nvt 0.3615 0.5205\r\nvt 0.359 0.5194\r\nvt 0.7908 0.1155\r\nvt 0.7903 0.1144\r\nvt 0.7882 0.1166\r\nvt 0.7878 0.1155\r\nvt 0.3572 0.51\r\nvt 0.3596 0.5111\r\nvt 0.3594 0.5117\r\nvt 0.357 0.5106\r\nvt 0.3565 0.5183\r\nvt 0.354 0.5172\r\nvt 0.7858 0.1177\r\nvt 0.7853 0.1166\r\nvt 0.7834 0.1187\r\nvt 0.7829 0.1176\r\nvt 0.3525 0.5079\r\nvt 0.3549 0.509\r\nvt 0.3546 0.5096\r\nvt 0.3523 0.5085\r\nvt 0.3516 0.5162\r\nvt 0.3493 0.5151\r\nvt 0.781 0.1198\r\nvt 0.7806 0.1187\r\nvt 0.7788 0.1208\r\nvt 0.7783 0.1197\r\nvt 0.3481 0.5059\r\nvt 0.3503 0.5069\r\nvt 0.35 0.5075\r\nvt 0.3478 0.5065\r\nvt 0.347 0.5141\r\nvt 0.3448 0.513\r\nvt 0.7765 0.1218\r\nvt 0.776 0.1208\r\nvt 0.7744 0.1228\r\nvt 0.7739 0.1218\r\nvt 0.344 0.504\r\nvt 0.346 0.5049\r\nvt 0.3457 0.5055\r\nvt 0.3437 0.5046\r\nvt 0.3427 0.5121\r\nvt 0.3407 0.5111\r\nvt 0.7723 0.1238\r\nvt 0.7718 0.1227\r\nvt 0.7703 0.1247\r\nvt 0.7698 0.1236\r\nvt 0.3402 0.5022\r\nvt 0.342 0.5031\r\nvt 0.3418 0.5037\r\nvt 0.3399 0.5028\r\nvt 0.3387 0.5102\r\nvt 0.3369 0.5094\r\nvt 0.7684 0.1256\r\nvt 0.7679 0.1245\r\nvt 0.7666 0.1264\r\nvt 0.7661 0.1253\r\nvt 0.3366 0.5006\r\nvt 0.3384 0.5014\r\nvt 0.3381 0.502\r\nvt 0.3363 0.5012\r\nvt 0.3351 0.5085\r\nvt 0.3333 0.5077\r\nvt 0.7648 0.1272\r\nvt 0.7643 0.1262\r\nvt 0.7631 0.128\r\nvt 0.7626 0.127\r\nvt 0.3332 0.499\r\nvt 0.3349 0.4998\r\nvt 0.3346 0.5004\r\nvt 0.3329 0.4996\r\nvt 0.3316 0.5069\r\nvt 0.3299 0.5061\r\nvt 0.7614 0.1288\r\nvt 0.7609 0.1277\r\nvt 0.7597 0.1296\r\nvt 0.7592 0.1285\r\nvt 0.3298 0.4974\r\nvt 0.3315 0.4982\r\nvt 0.3312 0.4988\r\nvt 0.3295 0.498\r\nvt 0.3282 0.5054\r\nvt 0.3265 0.5046\r\nvt 0.7581 0.1303\r\nvt 0.7576 0.1293\r\nvt 0.7564 0.1311\r\nvt 0.7559 0.13\r\nvt 0.3263 0.4958\r\nvt 0.328 0.4966\r\nvt 0.3278 0.4972\r\nvt 0.326 0.4964\r\nvt 0.3247 0.5038\r\nvt 0.323 0.503\r\nvt 0.7547 0.1319\r\nvt 0.7542 0.1308\r\nvt 0.753 0.1327\r\nvt 0.7525 0.1316\r\nvt 0.3227 0.4942\r\nvt 0.3245 0.495\r\nvt 0.3242 0.4956\r\nvt 0.3225 0.4948\r\nvt 0.3212 0.5022\r\nvt 0.3194 0.5013\r\nvt 0.7513 0.1335\r\nvt 0.7508 0.1324\r\nvt 0.7495 0.1343\r\nvt 0.749 0.1332\r\nvt 0.3191 0.4925\r\nvt 0.3209 0.4933\r\nvt 0.3206 0.4939\r\nvt 0.3188 0.4931\r\nvt 0.3176 0.5005\r\nvt 0.3158 0.4997\r\nvt 0.7477 0.1351\r\nvt 0.7473 0.134\r\nvt 0.7459 0.1359\r\nvt 0.7455 0.1348\r\nvt 0.3153 0.4908\r\nvt 0.3172 0.4916\r\nvt 0.3169 0.4923\r\nvt 0.315 0.4914\r\nvt 0.3139 0.4988\r\nvt 0.312 0.498\r\nvt 0.7441 0.1367\r\nvt 0.7436 0.1356\r\nvt 0.7423 0.1376\r\nvt 0.7418 0.1365\r\nvt 0.3114 0.489\r\nvt 0.3134 0.4899\r\nvt 0.3131 0.4905\r\nvt 0.3111 0.4897\r\nvt 0.3101 0.4971\r\nvt 0.3082 0.4963\r\nvt 0.7404 0.1384\r\nvt 0.7399 0.1373\r\nvt 0.7385 0.1393\r\nvt 0.738 0.1382\r\nvt 0.3074 0.4873\r\nvt 0.3094 0.4882\r\nvt 0.3091 0.4888\r\nvt 0.3071 0.4879\r\nvt 0.3062 0.4954\r\nvt 0.3042 0.4945\r\nvt 0.7365 0.1401\r\nvt 0.7361 0.139\r\nvt 0.7346 0.141\r\nvt 0.7341 0.1399\r\nvt 0.3032 0.4854\r\nvt 0.3053 0.4864\r\nvt 0.305 0.487\r\nvt 0.3029 0.4861\r\nvt 0.3022 0.4936\r\nvt 0.3001 0.4927\r\nvt 0.7326 0.1419\r\nvt 0.7321 0.1408\r\nvt 0.7305 0.1428\r\nvt 0.73 0.1417\r\nvt 0.2988 0.4836\r\nvt 0.301 0.4845\r\nvt 0.3007 0.4851\r\nvt 0.2985 0.4842\r\nvt 0.2979 0.4918\r\nvt 0.2958 0.4909\r\nvt 0.7284 0.1437\r\nvt 0.728 0.1426\r\nvt 0.7263 0.1446\r\nvt 0.7258 0.1435\r\nvt 0.2941 0.4817\r\nvt 0.2965 0.4826\r\nvt 0.2962 0.4833\r\nvt 0.2939 0.4823\r\nvt 0.2935 0.49\r\nvt 0.2913 0.4891\r\nvt 0.7241 0.1455\r\nvt 0.7237 0.1444\r\nvt 0.7219 0.1464\r\nvt 0.7215 0.1453\r\nvt 0.2893 0.4799\r\nvt 0.2917 0.4808\r\nvt 0.2915 0.4814\r\nvt 0.289 0.4805\r\nvt 0.2889 0.4882\r\nvt 0.2866 0.4873\r\nvt 0.7196 0.1474\r\nvt 0.7192 0.1462\r\nvt 0.7173 0.1483\r\nvt 0.7169 0.1471\r\nvt 0.2841 0.4781\r\nvt 0.2867 0.479\r\nvt 0.2865 0.4796\r\nvt 0.2839 0.4787\r\nvt 0.2842 0.4864\r\nvt 0.2817 0.4856\r\nvt 0.715 0.1491\r\nvt 0.7145 0.148\r\nvt 0.7125 0.15\r\nvt 0.7122 0.1489\r\nvt 0.2787 0.4765\r\nvt 0.2815 0.4773\r\nvt 0.2813 0.4779\r\nvt 0.2785 0.4771\r\nvt 0.2792 0.4848\r\nvt 0.2767 0.4841\r\nvt 0.7101 0.1508\r\nvt 0.7097 0.1497\r\nvt 0.7076 0.1516\r\nvt 0.7072 0.1505\r\nvt 0.273 0.4751\r\nvt 0.2759 0.4757\r\nvt 0.2757 0.4764\r\nvt 0.2729 0.4758\r\nvt 0.2741 0.4834\r\nvt 0.2715 0.4829\r\nvt 0.705 0.1523\r\nvt 0.7047 0.1512\r\nvt 0.7025 0.153\r\nvt 0.7022 0.1518\r\nvt 0.2668 0.4742\r\nvt 0.2701 0.4746\r\nvt 0.27 0.4753\r\nvt 0.2668 0.4749\r\nvt 0.2689 0.4824\r\nvt 0.2662 0.4819\r\nvt 0.6999 0.1535\r\nvt 0.6997 0.1524\r\nvt 0.6973 0.1541\r\nvt 0.6971 0.1529\r\nvt 0.2629 0.4738\r\nvt 0.2629 0.4745\r\nvt 0.2634 0.4814\r\nvt 0.2608 0.4816\r\nvt 0.6943 0.1545\r\nvt 0.6944 0.1533\r\nvt 0.6913 0.1543\r\nvt 0.6917 0.1531\r\nvt 0.5712 0.629\r\nvt 0.5707 0.6287\r\nvt 0.221 0.4125\r\nvt 0.222 0.4118\r\nvt 0.2241 0.4148\r\nvt 0.6533 0.2203\r\nvt 0.6543 0.2209\r\nvt 0.6553 0.2173\r\nvt 0.6563 0.218\r\nvt 0.2176 0.4192\r\nvt 0.2155 0.4162\r\nvt 0.2161 0.4158\r\nvt 0.2181 0.4189\r\nvt 0.2167 0.4154\r\nvt 0.2262 0.4179\r\nvt 0.2282 0.4209\r\nvt 0.6573 0.2143\r\nvt 0.6583 0.215\r\nvt 0.6593 0.2113\r\nvt 0.6603 0.212\r\nvt 0.2217 0.4253\r\nvt 0.2196 0.4223\r\nvt 0.2202 0.4219\r\nvt 0.2222 0.425\r\nvt 0.2303 0.424\r\nvt 0.2324 0.4271\r\nvt 0.6613 0.2083\r\nvt 0.6623 0.209\r\nvt 0.6634 0.2052\r\nvt 0.6644 0.2059\r\nvt 0.2258 0.4315\r\nvt 0.2238 0.4284\r\nvt 0.2243 0.428\r\nvt 0.2264 0.4311\r\nvt 0.2345 0.4303\r\nvt 0.2366 0.4334\r\nvt 0.6654 0.2022\r\nvt 0.6664 0.2028\r\nvt 0.6675 0.199\r\nvt 0.6685 0.1997\r\nvt 0.23 0.4378\r\nvt 0.2279 0.4346\r\nvt 0.2285 0.4343\r\nvt 0.2305 0.4374\r\nvt 0.2387 0.4366\r\nvt 0.2408 0.4399\r\nvt 0.6695 0.1959\r\nvt 0.6705 0.1965\r\nvt 0.6715 0.1927\r\nvt 0.6725 0.1934\r\nvt 0.2341 0.4441\r\nvt 0.2321 0.441\r\nvt 0.2326 0.4406\r\nvt 0.2347 0.4438\r\nvt 0.2428 0.4431\r\nvt 0.2449 0.4463\r\nvt 0.6736 0.1895\r\nvt 0.6746 0.1901\r\nvt 0.6755 0.1863\r\nvt 0.6765 0.1869\r\nvt 0.2382 0.4505\r\nvt 0.2362 0.4473\r\nvt 0.2367 0.447\r\nvt 0.2388 0.4502\r\nvt 0.2469 0.4496\r\nvt 0.2488 0.4528\r\nvt 0.6775 0.1831\r\nvt 0.6785 0.1837\r\nvt 0.6793 0.1799\r\nvt 0.6804 0.1805\r\nvt 0.2421 0.4568\r\nvt 0.2402 0.4537\r\nvt 0.2407 0.4534\r\nvt 0.2426 0.4565\r\nvt 0.2507 0.4559\r\nvt 0.2525 0.459\r\nvt 0.6811 0.1768\r\nvt 0.6821 0.1773\r\nvt 0.6828 0.1737\r\nvt 0.6838 0.1742\r\nvt 0.2457 0.463\r\nvt 0.2439 0.4599\r\nvt 0.2445 0.4596\r\nvt 0.2463 0.4626\r\nvt 0.2542 0.4621\r\nvt 0.256 0.4653\r\nvt 0.6844 0.1706\r\nvt 0.6854 0.1712\r\nvt 0.686 0.1674\r\nvt 0.687 0.1679\r\nvt 0.249 0.4688\r\nvt 0.2474 0.4659\r\nvt 0.248 0.4656\r\nvt 0.2496 0.4685\r\nvt 0.2579 0.4688\r\nvt 0.2601 0.4713\r\nvt 0.6878 0.1637\r\nvt 0.6887 0.1643\r\nvt 0.691 0.1619\r\nvt 0.2517 0.4738\r\nvt 0.2505 0.4715\r\nvt 0.2511 0.4712\r\nvt 0.2523 0.4736\r\nvt 0.2525 0.477\r\nvt 0.2524 0.4756\r\nvt 0.2531 0.4756\r\nvt 0.2532 0.4771\r\nvt 0.2585 0.4828\r\nvt 0.2565 0.4845\r\nvt 0.6887 0.1531\r\nvt 0.6894 0.1522\r\nvt 0.6867 0.1512\r\nvt 0.6876 0.1504\r\nvt 0.2506 0.4795\r\nvt 0.2518 0.4781\r\nvt 0.2524 0.4784\r\nvt 0.2511 0.4799\r\nvt 0.255 0.4863\r\nvt 0.2536 0.4882\r\nvt 0.6852 0.1493\r\nvt 0.6862 0.1486\r\nvt 0.6839 0.1473\r\nvt 0.6849 0.1466\r\nvt 0.2472 0.4836\r\nvt 0.249 0.4814\r\nvt 0.2495 0.4818\r\nvt 0.2478 0.484\r\nvt 0.2522 0.4904\r\nvt 0.2508 0.4927\r\nvt 0.6826 0.1451\r\nvt 0.6837 0.1445\r\nvt 0.6813 0.1427\r\nvt 0.6824 0.1421\r\nvt 0.244 0.4887\r\nvt 0.2456 0.4861\r\nvt 0.2461 0.4865\r\nvt 0.2445 0.489\r\nvt 0.2494 0.4952\r\nvt 0.248 0.4979\r\nvt 0.68 0.1401\r\nvt 0.6811 0.1396\r\nvt 0.6787 0.1374\r\nvt 0.6798 0.1369\r\nvt 0.2409 0.4943\r\nvt 0.2424 0.4915\r\nvt 0.243 0.4918\r\nvt 0.2415 0.4946\r\nvt 0.2466 0.5007\r\nvt 0.2453 0.5036\r\nvt 0.6774 0.1346\r\nvt 0.6785 0.1341\r\nvt 0.6762 0.1317\r\nvt 0.6773 0.1313\r\nvt 0.2381 0.5003\r\nvt 0.2395 0.4973\r\nvt 0.2401 0.4976\r\nvt 0.2387 0.5006\r\nvt 0.244 0.5066\r\nvt 0.2427 0.5097\r\nvt 0.6749 0.1287\r\nvt 0.676 0.1283\r\nvt 0.6737 0.1257\r\nvt 0.6748 0.1252\r\nvt 0.2354 0.5066\r\nvt 0.2368 0.5035\r\nvt 0.2374 0.5037\r\nvt 0.236 0.5069\r\nvt 0.2415 0.5128\r\nvt 0.2402 0.516\r\nvt 0.6725 0.1225\r\nvt 0.6736 0.1221\r\nvt 0.6713 0.1193\r\nvt 0.6724 0.1189\r\nvt 0.2328 0.5132\r\nvt 0.2341 0.5099\r\nvt 0.2347 0.5101\r\nvt 0.2335 0.5134\r\nvt 0.2389 0.5193\r\nvt 0.2377 0.5226\r\nvt 0.67 0.1161\r\nvt 0.6712 0.1157\r\nvt 0.6688 0.1128\r\nvt 0.6699 0.1124\r\nvt 0.2303 0.5199\r\nvt 0.2316 0.5165\r\nvt 0.2322 0.5167\r\nvt 0.2309 0.5201\r\nvt 0.2364 0.526\r\nvt 0.2352 0.5294\r\nvt 0.6676 0.1095\r\nvt 0.6687 0.1091\r\nvt 0.6664 0.1062\r\nvt 0.6675 0.1058\r\nvt 0.2278 0.5266\r\nvt 0.229 0.5232\r\nvt 0.2297 0.5235\r\nvt 0.2284 0.5269\r\nvt 0.234 0.5328\r\nvt 0.2327 0.5362\r\nvt 0.6652 0.1028\r\nvt 0.6663 0.1024\r\nvt 0.6639 0.0994\r\nvt 0.6651 0.099\r\nvt 0.2253 0.5335\r\nvt 0.2265 0.5301\r\nvt 0.2272 0.5303\r\nvt 0.2259 0.5337\r\nvt 0.2315 0.5397\r\nvt 0.2302 0.5431\r\nvt 0.6627 0.096\r\nvt 0.6638 0.0956\r\nvt 0.6615 0.0926\r\nvt 0.6626 0.0922\r\nvt 0.2228 0.5405\r\nvt 0.224 0.537\r\nvt 0.2247 0.5372\r\nvt 0.2234 0.5407\r\nvt 0.229 0.5467\r\nvt 0.2277 0.5502\r\nvt 0.6602 0.0891\r\nvt 0.6613 0.0887\r\nvt 0.659 0.0856\r\nvt 0.6601 0.0852\r\nvt 0.2203 0.5475\r\nvt 0.2215 0.544\r\nvt 0.2222 0.5442\r\nvt 0.2209 0.5478\r\nvt 0.2264 0.5537\r\nvt 0.2252 0.5573\r\nvt 0.6577 0.0821\r\nvt 0.6588 0.0817\r\nvt 0.6564 0.0786\r\nvt 0.6576 0.0782\r\nvt 0.2177 0.5547\r\nvt 0.219 0.5511\r\nvt 0.2196 0.5513\r\nvt 0.2184 0.5549\r\nvt 0.2239 0.5609\r\nvt 0.2226 0.5645\r\nvt 0.6552 0.075\r\nvt 0.6563 0.0746\r\nvt 0.6539 0.0714\r\nvt 0.655 0.071\r\nvt 0.2152 0.5618\r\nvt 0.2165 0.5582\r\nvt 0.2171 0.5585\r\nvt 0.2158 0.5621\r\nvt 0.2213 0.5681\r\nvt 0.22 0.5717\r\nvt 0.6526 0.0678\r\nvt 0.6537 0.0674\r\nvt 0.6513 0.0642\r\nvt 0.6524 0.0638\r\nvt 0.2126 0.5691\r\nvt 0.2139 0.5655\r\nvt 0.2145 0.5657\r\nvt 0.2132 0.5693\r\nvt 0.2187 0.5754\r\nvt 0.2175 0.579\r\nvt 0.65 0.0605\r\nvt 0.6511 0.0601\r\nvt 0.6487 0.0569\r\nvt 0.6498 0.0565\r\nvt 0.21 0.5763\r\nvt 0.2113 0.5727\r\nvt 0.2119 0.5729\r\nvt 0.2106 0.5766\r\nvt 0.2162 0.5826\r\nvt 0.2149 0.5862\r\nvt 0.6473 0.0532\r\nvt 0.6485 0.0528\r\nvt 0.6461 0.0496\r\nvt 0.6472 0.0492\r\nvt 0.2074 0.5836\r\nvt 0.2087 0.58\r\nvt 0.2093 0.5802\r\nvt 0.2081 0.5838\r\nvt 0.2137 0.5897\r\nvt 0.2125 0.5931\r\nvt 0.6448 0.0461\r\nvt 0.6459 0.0457\r\nvt 0.6436 0.0427\r\nvt 0.6447 0.0423\r\nvt 0.205 0.5906\r\nvt 0.2062 0.5872\r\nvt 0.2068 0.5874\r\nvt 0.2057 0.5908\r\nvt 0.2114 0.5963\r\nvt 0.2104 0.5993\r\nvt 0.6425 0.0395\r\nvt 0.6436 0.0391\r\nvt 0.6415 0.0365\r\nvt 0.6426 0.0361\r\nvt 0.2029 0.5968\r\nvt 0.2039 0.5938\r\nvt 0.2046 0.594\r\nvt 0.2036 0.597\r\nvt 0.2095 0.602\r\nvt 0.2088 0.6043\r\nvt 0.6406 0.0338\r\nvt 0.6417 0.0334\r\nvt 0.6398 0.0314\r\nvt 0.641 0.0311\r\nvt 0.2013 0.6019\r\nvt 0.202 0.5995\r\nvt 0.2027 0.5997\r\nvt 0.2019 0.6021\r\nvt 0.2081 0.6063\r\nvt 0.2076 0.608\r\nvt 0.6392 0.0294\r\nvt 0.6403 0.029\r\nvt 0.6386 0.0277\r\nvt 0.6398 0.0273\r\nvt 0.2001 0.6056\r\nvt 0.2006 0.6039\r\nvt 0.2013 0.6041\r\nvt 0.2007 0.6058\r\nvt 0.2072 0.6094\r\nvt 0.2068 0.6105\r\nvt 0.6382 0.0263\r\nvt 0.6393 0.0259\r\nvt 0.6378 0.0251\r\nvt 0.6389 0.0247\r\nvt 0.1993 0.608\r\nvt 0.1997 0.6069\r\nvt 0.2003 0.6071\r\nvt 0.1999 0.6082\r\nvt 0.2065 0.6115\r\nvt 0.2062 0.6123\r\nvt 0.6374 0.024\r\nvt 0.6386 0.0236\r\nvt 0.6371 0.0231\r\nvt 0.6382 0.0227\r\nvt 0.1987 0.6096\r\nvt 0.199 0.6089\r\nvt 0.1996 0.6091\r\nvt 0.1994 0.6098\r\nvt 0.2059 0.613\r\nvt 0.2057 0.6137\r\nvt 0.6368 0.0223\r\nvt 0.6379 0.0219\r\nvt 0.6365 0.0215\r\nvt 0.6376 0.0211\r\nvt 0.1983 0.6107\r\nvt 0.1985 0.6102\r\nvt 0.1991 0.6104\r\nvt 0.1989 0.611\r\nvt 0.2054 0.6144\r\nvt 0.2051 0.6151\r\nvt 0.6362 0.0208\r\nvt 0.6373 0.0203\r\nvt 0.6359 0.02\r\nvt 0.637 0.0196\r\nvt 0.1978 0.6119\r\nvt 0.1981 0.6113\r\nvt 0.1987 0.6115\r\nvt 0.1984 0.6121\r\nvt 0.2048 0.6158\r\nvt 0.2044 0.6165\r\nvt 0.6355 0.0193\r\nvt 0.6366 0.0188\r\nvt 0.6352 0.0185\r\nvt 0.6362 0.018\r\nvt 0.1973 0.6131\r\nvt 0.1976 0.6125\r\nvt 0.1982 0.6127\r\nvt 0.1979 0.6134\r\nvt 0.204 0.6173\r\nvt 0.2036 0.6181\r\nvt 0.6348 0.0176\r\nvt 0.6358 0.0171\r\nvt 0.6343 0.0167\r\nvt 0.6354 0.0162\r\nvt 0.1966 0.6146\r\nvt 0.1969 0.6138\r\nvt 0.1975 0.6141\r\nvt 0.1972 0.6149\r\nvt 0.2031 0.6191\r\nvt 0.2026 0.6201\r\nvt 0.6338 0.0157\r\nvt 0.6349 0.0152\r\nvt 0.6333 0.0147\r\nvt 0.6343 0.0141\r\nvt 0.1956 0.6164\r\nvt 0.1961 0.6154\r\nvt 0.1967 0.6157\r\nvt 0.1962 0.6167\r\nvt 0.202 0.6212\r\nvt 0.2014 0.6224\r\nvt 0.6327 0.0136\r\nvt 0.6337 0.013\r\nvt 0.632 0.0123\r\nvt 0.6331 0.0118\r\nvt 0.1944 0.6186\r\nvt 0.1951 0.6175\r\nvt 0.1957 0.6178\r\nvt 0.195 0.6189\r\nvt 0.2007 0.6237\r\nvt 0.1999 0.6251\r\nvt 0.6313 0.011\r\nvt 0.6323 0.0104\r\nvt 0.6305 0.0096\r\nvt 0.6316 0.009\r\nvt 0.193 0.6212\r\nvt 0.1937 0.6199\r\nvt 0.1943 0.6202\r\nvt 0.1936 0.6215\r\nvt 0.1991 0.6265\r\nvt 0.1983 0.6281\r\nvt 0.6297 0.0081\r\nvt 0.6308 0.0076\r\nvt 0.6289 0.0066\r\nvt 0.6299 0.006\r\nvt 0.1913 0.624\r\nvt 0.1922 0.6226\r\nvt 0.1928 0.6229\r\nvt 0.1919 0.6243\r\nvt 0.1973 0.6297\r\nvt 0.1962 0.6314\r\nvt 0.628 0.005\r\nvt 0.629 0.0044\r\nvt 0.627 0.0034\r\nvt 0.6279 0.0026\r\nvt 0.1895 0.6271\r\nvt 0.1904 0.6255\r\nvt 0.191 0.6258\r\nvt 0.1902 0.6273\r\nvt 0.1946 0.6331\r\nvt 0.1925 0.6339\r\nvt 0.6257 0.0019\r\nvt 0.6264 0.0009\r\nvt 0.6243 0\r\nvt 0.1889 0.6305\r\nvt 0.1889 0.6289\r\nvt 0.1895 0.6289\r\nvt 0.1895 0.6303\r\nvt 0.1904 0.6321\r\nvt 0.1907 0.6316\r\nvt 0.9984 0.0119\r\nvt 0.9955 0.0136\r\nvt 1 0.0094\r\nvt 0.5712 0.6239\r\nvt 0.5728 0.6265\r\nvt 0.5684 0.622\r\nvt 0.9926 0.0152\r\nvt 0.9897 0.0169\r\nvt 0.5655 0.6204\r\nvt 0.5625 0.6187\r\nvt 0.9865 0.0186\r\nvt 0.9833 0.0204\r\nvt 0.5594 0.617\r\nvt 0.5562 0.6151\r\nvt 0.98 0.0223\r\nvt 0.9768 0.0241\r\nvt 0.5529 0.6133\r\nvt 0.5497 0.6114\r\nvt 0.9737 0.0259\r\nvt 0.9707 0.0275\r\nvt 0.5466 0.6096\r\nvt 0.5436 0.608\r\nvt 0.968 0.0291\r\nvt 0.9656 0.0304\r\nvt 0.5409 0.6064\r\nvt 0.5386 0.6051\r\nvt 0.9636 0.0315\r\nvt 0.9619 0.0325\r\nvt 0.5366 0.604\r\nvt 0.5349 0.603\r\nvt 0.9605 0.0333\r\nvt 0.9593 0.0339\r\nvt 0.5335 0.6022\r\nvt 0.5324 0.6016\r\nvt 0.9584 0.0345\r\nvt 0.9575 0.035\r\nvt 0.5315 0.6011\r\nvt 0.5307 0.6006\r\nvt 0.9568 0.0354\r\nvt 0.9561 0.0358\r\nvt 0.53 0.6002\r\nvt 0.5294 0.5998\r\nvt 0.9554 0.0362\r\nvt 0.9547 0.0366\r\nvt 0.5287 0.5995\r\nvt 0.528 0.5991\r\nvt 0.9538 0.0371\r\nvt 0.9529 0.0377\r\nvt 0.5273 0.5986\r\nvt 0.5264 0.5981\r\nvt 0.9517 0.0384\r\nvt 0.9504 0.0391\r\nvt 0.5253 0.5975\r\nvt 0.524 0.5967\r\nvt 0.9488 0.0401\r\nvt 0.9469 0.0411\r\nvt 0.5224 0.5958\r\nvt 0.5205 0.5947\r\nvt 0.9447 0.0424\r\nvt 0.9421 0.0439\r\nvt 0.5183 0.5934\r\nvt 0.5158 0.592\r\nvt 0.9394 0.0455\r\nvt 0.9364 0.0472\r\nvt 0.513 0.5904\r\nvt 0.51 0.5887\r\nvt 0.9332 0.0489\r\nvt 0.9299 0.0508\r\nvt 0.5068 0.5869\r\nvt 0.5036 0.5851\r\nvt 0.9265 0.0527\r\nvt 0.9231 0.0546\r\nvt 0.5002 0.5832\r\nvt 0.4968 0.5813\r\nvt 0.9197 0.0565\r\nvt 0.9163 0.0584\r\nvt 0.4934 0.5794\r\nvt 0.49 0.5775\r\nvt 0.9129 0.0602\r\nvt 0.9095 0.0621\r\nvt 0.4866 0.5756\r\nvt 0.4832 0.5737\r\nvt 0.9062 0.0639\r\nvt 0.9029 0.0658\r\nvt 0.4798 0.5719\r\nvt 0.4765 0.57\r\nvt 0.8996 0.0676\r\nvt 0.8963 0.0694\r\nvt 0.4732 0.5682\r\nvt 0.4699 0.5664\r\nvt 0.893 0.0712\r\nvt 0.8898 0.073\r\nvt 0.4666 0.5645\r\nvt 0.4633 0.5627\r\nvt 0.8866 0.0748\r\nvt 0.8834 0.0766\r\nvt 0.46 0.5609\r\nvt 0.4568 0.5591\r\nvt 0.8802 0.0783\r\nvt 0.8771 0.0801\r\nvt 0.4536 0.5573\r\nvt 0.4504 0.5555\r\nvt 0.874 0.0818\r\nvt 0.871 0.0836\r\nvt 0.4473 0.5537\r\nvt 0.4442 0.552\r\nvt 0.8679 0.0853\r\nvt 0.8649 0.087\r\nvt 0.4411 0.5502\r\nvt 0.4381 0.5484\r\nvt 0.862 0.0888\r\nvt 0.8591 0.0905\r\nvt 0.4351 0.5466\r\nvt 0.4321 0.5449\r\nvt 0.8563 0.0922\r\nvt 0.8536 0.0939\r\nvt 0.4293 0.5431\r\nvt 0.4265 0.5413\r\nvt 0.851 0.0957\r\nvt 0.8484 0.0974\r\nvt 0.4238 0.5396\r\nvt 0.4212 0.5378\r\nvt 0.846 0.0991\r\nvt 0.8436 0.1008\r\nvt 0.4187 0.5361\r\nvt 0.4163 0.5343\r\nvt 0.8414 0.1024\r\nvt 0.8393 0.1041\r\nvt 0.414 0.5326\r\nvt 0.4118 0.531\r\nvt 0.8372 0.1057\r\nvt 0.8352 0.107\r\nvt 0.4097 0.5293\r\nvt 0.4077 0.5278\r\nvt 0.8237 0.1122\r\nvt 0.8213 0.1128\r\nvt 0.8261 0.1116\r\nvt 0.3958 0.5226\r\nvt 0.3983 0.5233\r\nvt 0.3934 0.5219\r\nvt 0.8189 0.1135\r\nvt 0.8164 0.1141\r\nvt 0.3909 0.5213\r\nvt 0.3883 0.5206\r\nvt 0.8139 0.1148\r\nvt 0.8114 0.1156\r\nvt 0.3858 0.5198\r\nvt 0.3832 0.519\r\nvt 0.8088 0.1165\r\nvt 0.8063 0.1174\r\nvt 0.3806 0.5182\r\nvt 0.378 0.5173\r\nvt 0.8037 0.1183\r\nvt 0.8012 0.1193\r\nvt 0.3754 0.5163\r\nvt 0.3728 0.5153\r\nvt 0.7987 0.1203\r\nvt 0.7962 0.1213\r\nvt 0.3702 0.5143\r\nvt 0.3676 0.5132\r\nvt 0.7937 0.1224\r\nvt 0.7912 0.1234\r\nvt 0.3651 0.5121\r\nvt 0.3626 0.5111\r\nvt 0.7888 0.1245\r\nvt 0.7864 0.1256\r\nvt 0.3601 0.51\r\nvt 0.3577 0.5089\r\nvt 0.7841 0.1266\r\nvt 0.7819 0.1276\r\nvt 0.3554 0.5078\r\nvt 0.3531 0.5068\r\nvt 0.7796 0.1286\r\nvt 0.7775 0.1296\r\nvt 0.3508 0.5057\r\nvt 0.3486 0.5047\r\nvt 0.7754 0.1306\r\nvt 0.7734 0.1315\r\nvt 0.3465 0.5038\r\nvt 0.3445 0.5028\r\nvt 0.7715 0.1324\r\nvt 0.7697 0.1332\r\nvt 0.3426 0.5019\r\nvt 0.3407 0.5011\r\nvt 0.7679 0.134\r\nvt 0.7662 0.1348\r\nvt 0.3389 0.5003\r\nvt 0.3371 0.4994\r\nvt 0.7645 0.1356\r\nvt 0.7629 0.1364\r\nvt 0.3354 0.4986\r\nvt 0.3337 0.4979\r\nvt 0.7612 0.1371\r\nvt 0.7595 0.1379\r\nvt 0.332 0.4971\r\nvt 0.3303 0.4963\r\nvt 0.7578 0.1387\r\nvt 0.7561 0.1395\r\nvt 0.3286 0.4955\r\nvt 0.3268 0.4947\r\nvt 0.7544 0.1403\r\nvt 0.7526 0.1411\r\nvt 0.325 0.4939\r\nvt 0.3232 0.493\r\nvt 0.7508 0.1419\r\nvt 0.749 0.1427\r\nvt 0.3214 0.4922\r\nvt 0.3196 0.4914\r\nvt 0.7472 0.1435\r\nvt 0.7453 0.1444\r\nvt 0.3177 0.4905\r\nvt 0.3158 0.4897\r\nvt 0.7434 0.1452\r\nvt 0.7415 0.1461\r\nvt 0.3139 0.4888\r\nvt 0.3119 0.4879\r\nvt 0.7396 0.147\r\nvt 0.7376 0.1478\r\nvt 0.3099 0.487\r\nvt 0.3079 0.4861\r\nvt 0.7356 0.1487\r\nvt 0.7335 0.1496\r\nvt 0.3058 0.4852\r\nvt 0.3037 0.4843\r\nvt 0.7314 0.1506\r\nvt 0.7292 0.1515\r\nvt 0.3015 0.4834\r\nvt 0.2992 0.4824\r\nvt 0.727 0.1524\r\nvt 0.7247 0.1534\r\nvt 0.297 0.4815\r\nvt 0.2946 0.4806\r\nvt 0.7224 0.1543\r\nvt 0.72 0.1552\r\nvt 0.2922 0.4796\r\nvt 0.2897 0.4787\r\nvt 0.7175 0.1562\r\nvt 0.715 0.1571\r\nvt 0.2871 0.4778\r\nvt 0.2845 0.4769\r\nvt 0.7124 0.1579\r\nvt 0.7097 0.1588\r\nvt 0.2818 0.4761\r\nvt 0.279 0.4753\r\nvt 0.7069 0.1596\r\nvt 0.7041 0.1603\r\nvt 0.2762 0.4745\r\nvt 0.2732 0.4739\r\nvt 0.7013 0.1609\r\nvt 0.6981 0.1612\r\nvt 0.2702 0.4734\r\nvt 0.2669 0.473\r\nvt 0.6943 0.1612\r\nvt 0.2631 0.4726\r\nvt 0.9996 0.0064\r\nvt 0.5723 0.6296\r\nvt 0.6481 0.2167\r\nvt 0.6471 0.2161\r\nvt 0.6491 0.2131\r\nvt 0.2145 0.4169\r\nvt 0.2165 0.4199\r\nvt 0.6511 0.2101\r\nvt 0.6531 0.2071\r\nvt 0.2186 0.423\r\nvt 0.2207 0.426\r\nvt 0.6551 0.2041\r\nvt 0.6571 0.2011\r\nvt 0.2227 0.4291\r\nvt 0.2248 0.4322\r\nvt 0.6592 0.1981\r\nvt 0.6612 0.195\r\nvt 0.2269 0.4353\r\nvt 0.2289 0.4385\r\nvt 0.6632 0.1919\r\nvt 0.6652 0.1887\r\nvt 0.231 0.4416\r\nvt 0.2331 0.4448\r\nvt 0.6672 0.1856\r\nvt 0.6691 0.1824\r\nvt 0.2351 0.448\r\nvt 0.2371 0.4512\r\nvt 0.671 0.1793\r\nvt 0.6728 0.1762\r\nvt 0.2391 0.4544\r\nvt 0.241 0.4575\r\nvt 0.6746 0.1731\r\nvt 0.6762 0.1701\r\nvt 0.2428 0.4606\r\nvt 0.2446 0.4636\r\nvt 0.6777 0.1672\r\nvt 0.6792 0.1644\r\nvt 0.2463 0.4665\r\nvt 0.2479 0.4694\r\nvt 0.6805 0.1618\r\nvt 0.6815 0.1596\r\nvt 0.2494 0.472\r\nvt 0.2506 0.4743\r\nvt 0.682 0.1582\r\nvt 0.6818 0.1573\r\nvt 0.2512 0.4758\r\nvt 0.2512 0.4767\r\nvt 0.6816 0.1565\r\nvt 0.6807 0.1554\r\nvt 0.2508 0.4776\r\nvt 0.2496 0.4787\r\nvt 0.6793 0.1536\r\nvt 0.6777 0.1513\r\nvt 0.248 0.4806\r\nvt 0.2462 0.4829\r\nvt 0.6762 0.1488\r\nvt 0.6747 0.1462\r\nvt 0.2445 0.4854\r\nvt 0.2429 0.4881\r\nvt 0.6733 0.1434\r\nvt 0.6719 0.1406\r\nvt 0.2413 0.4909\r\nvt 0.2398 0.4938\r\nvt 0.6706 0.1377\r\nvt 0.6693 0.1347\r\nvt 0.2384 0.4968\r\nvt 0.237 0.4998\r\nvt 0.668 0.1316\r\nvt 0.6668 0.1284\r\nvt 0.2356 0.503\r\nvt 0.2343 0.5062\r\nvt 0.6655 0.1252\r\nvt 0.6643 0.122\r\nvt 0.233 0.5094\r\nvt 0.2317 0.5127\r\nvt 0.663 0.1187\r\nvt 0.6618 0.1154\r\nvt 0.2304 0.5161\r\nvt 0.2291 0.5194\r\nvt 0.6606 0.1121\r\nvt 0.6594 0.1088\r\nvt 0.2279 0.5228\r\nvt 0.2266 0.5262\r\nvt 0.6581 0.1054\r\nvt 0.6569 0.102\r\nvt 0.2254 0.5296\r\nvt 0.2241 0.5331\r\nvt 0.6557 0.0986\r\nvt 0.6544 0.0951\r\nvt 0.2229 0.5366\r\nvt 0.2216 0.5401\r\nvt 0.6532 0.0916\r\nvt 0.6519 0.0881\r\nvt 0.2204 0.5436\r\nvt 0.2191 0.5471\r\nvt 0.6507 0.0846\r\nvt 0.6494 0.0811\r\nvt 0.2178 0.5507\r\nvt 0.2166 0.5542\r\nvt 0.6481 0.0775\r\nvt 0.6468 0.0739\r\nvt 0.2153 0.5578\r\nvt 0.214 0.5614\r\nvt 0.6455 0.0703\r\nvt 0.6442 0.0667\r\nvt 0.2127 0.565\r\nvt 0.2114 0.5687\r\nvt 0.6429 0.0631\r\nvt 0.6416 0.0594\r\nvt 0.2101 0.5723\r\nvt 0.2088 0.5759\r\nvt 0.6403 0.0558\r\nvt 0.639 0.0521\r\nvt 0.2075 0.5796\r\nvt 0.2063 0.5832\r\nvt 0.6378 0.0486\r\nvt 0.6366 0.0452\r\nvt 0.205 0.5867\r\nvt 0.2039 0.5902\r\nvt 0.6354 0.0419\r\nvt 0.6344 0.0389\r\nvt 0.2028 0.5934\r\nvt 0.2017 0.5964\r\nvt 0.6335 0.0361\r\nvt 0.6327 0.0337\r\nvt 0.2009 0.5991\r\nvt 0.2001 0.6015\r\nvt 0.632 0.0317\r\nvt 0.6315 0.03\r\nvt 0.1994 0.6036\r\nvt 0.1989 0.6052\r\nvt 0.6311 0.0286\r\nvt 0.6307 0.0274\r\nvt 0.1985 0.6065\r\nvt 0.1981 0.6076\r\nvt 0.6304 0.0265\r\nvt 0.6301 0.0257\r\nvt 0.1978 0.6084\r\nvt 0.1976 0.6091\r\nvt 0.6298 0.025\r\nvt 0.6296 0.0243\r\nvt 0.1974 0.6097\r\nvt 0.1971 0.6103\r\nvt 0.6293 0.0237\r\nvt 0.629 0.023\r\nvt 0.1969 0.6108\r\nvt 0.1967 0.6114\r\nvt 0.6287 0.0224\r\nvt 0.6284 0.0217\r\nvt 0.1964 0.612\r\nvt 0.1961 0.6126\r\nvt 0.628 0.0209\r\nvt 0.6276 0.0201\r\nvt 0.1958 0.6133\r\nvt 0.1954 0.614\r\nvt 0.6272 0.0192\r\nvt 0.6267 0.0182\r\nvt 0.195 0.6149\r\nvt 0.1945 0.6158\r\nvt 0.6261 0.0171\r\nvt 0.6254 0.0159\r\nvt 0.194 0.6169\r\nvt 0.1933 0.618\r\nvt 0.6247 0.0146\r\nvt 0.624 0.0133\r\nvt 0.1927 0.6193\r\nvt 0.1919 0.6206\r\nvt 0.6232 0.0118\r\nvt 0.6223 0.0104\r\nvt 0.1911 0.622\r\nvt 0.1903 0.6234\r\nvt 0.6214 0.0088\r\nvt 0.6204 0.0071\r\nvt 0.1893 0.6249\r\nvt 0.1884 0.6267\r\nvt 0.6196 0.005\r\nvt 0.6198 0.0028\r\nvt 0.1876 0.6288\r\nvt 0.1879 0.631\r\nvt 0.6216 0.0007\r\nvt 0.1897 0.6332\r\nvt 0.9947 0.0062\r\nvt 0.992 0.0075\r\nvt 0.9892 0.0091\r\nvt 0.9863 0.0108\r\nvt 0.9831 0.0126\r\nvt 0.9799 0.0144\r\nvt 0.9766 0.0162\r\nvt 0.9734 0.0181\r\nvt 0.9703 0.0198\r\nvt 0.9673 0.0215\r\nvt 0.9646 0.023\r\nvt 0.9622 0.0244\r\nvt 0.9602 0.0255\r\nvt 0.9585 0.0264\r\nvt 0.9571 0.0272\r\nvt 0.9559 0.0279\r\nvt 0.9549 0.0285\r\nvt 0.9541 0.029\r\nvt 0.9533 0.0294\r\nvt 0.9526 0.0298\r\nvt 0.9519 0.0302\r\nvt 0.9512 0.0307\r\nvt 0.9503 0.0311\r\nvt 0.9494 0.0317\r\nvt 0.9482 0.0324\r\nvt 0.9469 0.0331\r\nvt 0.9453 0.034\r\nvt 0.9434 0.0351\r\nvt 0.9412 0.0364\r\nvt 0.9387 0.0378\r\nvt 0.9359 0.0394\r\nvt 0.9329 0.0411\r\nvt 0.9298 0.0429\r\nvt 0.9265 0.0447\r\nvt 0.9231 0.0466\r\nvt 0.9197 0.0485\r\nvt 0.9163 0.0504\r\nvt 0.9129 0.0523\r\nvt 0.9095 0.0541\r\nvt 0.9062 0.056\r\nvt 0.9028 0.0578\r\nvt 0.8995 0.0597\r\nvt 0.8962 0.0615\r\nvt 0.8929 0.0633\r\nvt 0.8897 0.0651\r\nvt 0.8864 0.0669\r\nvt 0.8832 0.0687\r\nvt 0.88 0.0705\r\nvt 0.8768 0.0723\r\nvt 0.8737 0.074\r\nvt 0.8706 0.0758\r\nvt 0.8675 0.0775\r\nvt 0.8644 0.0793\r\nvt 0.8614 0.081\r\nvt 0.8584 0.0828\r\nvt 0.8555 0.0846\r\nvt 0.8526 0.0863\r\nvt 0.8498 0.0881\r\nvt 0.8471 0.0899\r\nvt 0.8445 0.0916\r\nvt 0.8419 0.0934\r\nvt 0.8395 0.0952\r\nvt 0.8372 0.0969\r\nvt 0.8351 0.0985\r\nvt 0.8331 0.1\r\nvt 0.8312 0.1014\r\nvt 0.8295 0.1026\r\nvt 0.8277 0.1035\r\nvt 0.8259 0.1043\r\nvt 0.824 0.1049\r\nvt 0.8219 0.1055\r\nvt 0.8196 0.1061\r\nvt 0.8171 0.1068\r\nvt 0.8146 0.1074\r\nvt 0.8119 0.1082\r\nvt 0.8093 0.109\r\nvt 0.8066 0.1099\r\nvt 0.804 0.1108\r\nvt 0.8013 0.1118\r\nvt 0.7987 0.1128\r\nvt 0.7961 0.1138\r\nvt 0.7935 0.1149\r\nvt 0.791 0.116\r\nvt 0.7885 0.1171\r\nvt 0.786 0.1181\r\nvt 0.7836 0.1192\r\nvt 0.7813 0.1203\r\nvt 0.779 0.1213\r\nvt 0.7768 0.1223\r\nvt 0.7746 0.1233\r\nvt 0.7725 0.1243\r\nvt 0.7705 0.1252\r\nvt 0.7686 0.1261\r\nvt 0.7668 0.1269\r\nvt 0.765 0.1277\r\nvt 0.7633 0.1285\r\nvt 0.7616 0.1293\r\nvt 0.76 0.1301\r\nvt 0.7583 0.1308\r\nvt 0.7566 0.1316\r\nvt 0.7549 0.1324\r\nvt 0.7532 0.1332\r\nvt 0.7515 0.1339\r\nvt 0.7497 0.1347\r\nvt 0.748 0.1356\r\nvt 0.7462 0.1364\r\nvt 0.7443 0.1372\r\nvt 0.7425 0.138\r\nvt 0.7406 0.1389\r\nvt 0.7387 0.1398\r\nvt 0.7367 0.1406\r\nvt 0.7348 0.1415\r\nvt 0.7328 0.1424\r\nvt 0.7307 0.1433\r\nvt 0.7286 0.1442\r\nvt 0.7265 0.1451\r\nvt 0.7243 0.146\r\nvt 0.7221 0.1469\r\nvt 0.7198 0.1478\r\nvt 0.7175 0.1487\r\nvt 0.7151 0.1496\r\nvt 0.7127 0.1505\r\nvt 0.7102 0.1513\r\nvt 0.7077 0.1521\r\nvt 0.7052 0.1528\r\nvt 0.7026 0.1535\r\nvt 0.7 0.1541\r\nvt 0.6973 0.1546\r\nvt 0.6943 0.155\r\nvt 0.6911 0.1548\r\nvt 0.6528 0.22\r\nvt 0.6549 0.217\r\nvt 0.6523 0.2196\r\nvt 0.6569 0.214\r\nvt 0.6589 0.211\r\nvt 0.6609 0.208\r\nvt 0.6629 0.205\r\nvt 0.665 0.2019\r\nvt 0.667 0.1988\r\nvt 0.6691 0.1956\r\nvt 0.6711 0.1924\r\nvt 0.6731 0.1892\r\nvt 0.6751 0.186\r\nvt 0.677 0.1828\r\nvt 0.6789 0.1797\r\nvt 0.6806 0.1765\r\nvt 0.6823 0.1734\r\nvt 0.6839 0.1704\r\nvt 0.6855 0.1671\r\nvt 0.6873 0.1634\r\nvt 0.6884 0.1535\r\nvt 0.6863 0.1516\r\nvt 0.6848 0.1496\r\nvt 0.6835 0.1476\r\nvt 0.6822 0.1453\r\nvt 0.6808 0.1429\r\nvt 0.6795 0.1403\r\nvt 0.6782 0.1376\r\nvt 0.677 0.1348\r\nvt 0.6757 0.1319\r\nvt 0.6745 0.1289\r\nvt 0.6732 0.1259\r\nvt 0.672 0.1227\r\nvt 0.6708 0.1195\r\nvt 0.6696 0.1163\r\nvt 0.6683 0.113\r\nvt 0.6671 0.1097\r\nvt 0.6659 0.1064\r\nvt 0.6647 0.103\r\nvt 0.6635 0.0996\r\nvt 0.6622 0.0962\r\nvt 0.661 0.0928\r\nvt 0.6597 0.0893\r\nvt 0.6585 0.0858\r\nvt 0.6572 0.0823\r\nvt 0.6559 0.0787\r\nvt 0.6547 0.0752\r\nvt 0.6534 0.0716\r\nvt 0.6521 0.068\r\nvt 0.6508 0.0644\r\nvt 0.6495 0.0607\r\nvt 0.6482 0.0571\r\nvt 0.6469 0.0534\r\nvt 0.6456 0.0498\r\nvt 0.6443 0.0463\r\nvt 0.6431 0.0429\r\nvt 0.642 0.0397\r\nvt 0.641 0.0367\r\nvt 0.6401 0.034\r\nvt 0.6393 0.0316\r\nvt 0.6387 0.0296\r\nvt 0.6381 0.0279\r\nvt 0.6377 0.0264\r\nvt 0.6373 0.0252\r\nvt 0.6369 0.0242\r\nvt 0.6366 0.0233\r\nvt 0.6363 0.0225\r\nvt 0.636 0.0217\r\nvt 0.6357 0.021\r\nvt 0.6354 0.0202\r\nvt 0.6351 0.0195\r\nvt 0.6347 0.0187\r\nvt 0.6343 0.0178\r\nvt 0.6338 0.0169\r\nvt 0.6334 0.016\r\nvt 0.6328 0.0149\r\nvt 0.6322 0.0138\r\nvt 0.6315 0.0126\r\nvt 0.6308 0.0113\r\nvt 0.6301 0.0099\r\nvt 0.6293 0.0084\r\nvt 0.6284 0.0069\r\nvt 0.6275 0.0053\r\nvt 0.6266 0.0037\r\nvt 0.6254 0.0023\r\nvt 0.4 0.5258\r\nvt 0.4003 0.5252\r\nvt 0.4026 0.5262\r\nvt 0.4022 0.5268\r\nvt 0.4048 0.5274\r\nvt 0.4044 0.528\r\nvt 0.4007 0.5241\r\nvt 0.4032 0.5251\r\nvt 0.828 0.1097\r\nvt 0.8285 0.1108\r\nvt 0.8303 0.1087\r\nvt 0.8309 0.1097\r\nvt 0.4059 0.5263\r\nvt 0.8325 0.1074\r\nvt 0.8333 0.1081\r\nvt 0.8321 0.1071\r\nvt 0.83 0.1083\r\nvt 0.8279 0.1092\r\nvt 0.2183 0.4143\r\nvt 0.2199 0.4132\r\nvt 0.2205 0.4128\r\nvt 0.6507 0.2185\r\nvt 0.6491 0.2174\r\nvt 0.6485 0.217\r\no Shape001\r\nusemtl finishing_trim\r\nf 6012/5842/5855 6013/5843/5856 6014/5844/5857 6015/5845/5858\r\nf 6016/5846/5859 6017/5847/5860 6014/5844/5857 6013/5843/5856\r\nf 6018/5848/5861 6019/5849/5862 6014/5844/5857 6017/5847/5860\r\nf 6020/5850/5863 6015/5845/5858 6014/5844/5857 6019/5849/5862\r\nf 6020/5850/5863 6019/5849/5862 6021/5851/5864 6022/5852/5865\r\nf 6018/5848/5861 6023/5853/5866 6021/5851/5864 6019/5849/5862\r\nf 6024/5854/5867 6025/5855/5868 6021/5851/5864 6023/5853/5866\r\nf 6026/5856/5869 6022/5852/5865 6021/5851/5864 6025/5855/5868\r\nf 6027/5857/5870 6028/5858/5871 6029/5859/5872 6030/5860/5873\r\nf 6031/5861/5874 6032/5862/5875 6029/5859/5872 6028/5858/5871\r\nf 6033/5863/5876 6034/5864/5877 6029/5859/5872 6032/5862/5875\r\nf 6035/5865/5878 6030/5860/5873 6029/5859/5872 6034/5864/5877\r\nf 6035/5865/5878 6034/5864/5877 6036/5866/5879 6037/5867/5880\r\nf 6033/5863/5876 6038/5868/5881 6036/5866/5879 6034/5864/5877\r\nf 6039/5869/5882 6040/5870/5883 6036/5866/5879 6038/5868/5881\r\nf 6041/5871/5884 6037/5867/5880 6036/5866/5879 6040/5870/5883\r\nf 6016/5846/5859 6042/5872/5885 6043/5873/5860 6017/5847/5860\r\nf 6044/5874/5886 6045/5875/5887 6043/5873/5860 6042/5872/5885\r\nf 6046/5876/5888 6047/5877/5889 6043/5873/5860 6045/5875/5887\r\nf 6018/5848/5861 6017/5847/5860 6043/5873/5860 6047/5877/5889\r\nf 6018/5848/5861 6047/5877/5889 6048/5878/5890 6023/5853/5866\r\nf 6046/5876/5888 6049/5879/5891 6048/5878/5890 6047/5877/5889\r\nf 6050/5880/5892 6051/5881/5893 6048/5878/5890 6049/5879/5891\r\nf 6024/5854/5867 6023/5853/5866 6048/5878/5890 6051/5881/5893\r\nf 6031/5861/5874 6052/5882/5894 6053/5883/5875 6032/5862/5875\r\nf 6054/5884/5895 6055/5885/5896 6053/5883/5875 6052/5882/5894\r\nf 6056/5886/5897 6057/5887/5898 6053/5883/5875 6055/5885/5896\r\nf 6033/5863/5876 6032/5862/5875 6053/5883/5875 6057/5887/5898\r\nf 6033/5863/5876 6057/5887/5898 6058/5888/5899 6038/5868/5881\r\nf 6056/5886/5897 6059/5889/5900 6058/5888/5899 6057/5887/5898\r\nf 6060/5890/5901 6061/5891/5902 6058/5888/5899 6059/5889/5900\r\nf 6039/5869/5882 6038/5868/5881 6058/5888/5899 6061/5891/5902\r\nf 6044/5874/5886 6062/5892/5903 6063/5893/5904 6045/5875/5887\r\nf 6064/5894/5905 6065/5895/5906 6063/5893/5904 6062/5892/5903\r\nf 6066/5896/5907 6067/5897/5908 6063/5893/5904 6065/5895/5906\r\nf 6046/5876/5888 6045/5875/5887 6063/5893/5904 6067/5897/5908\r\nf 6046/5876/5888 6067/5897/5908 6068/5898/5909 6049/5879/5891\r\nf 6066/5896/5907 6069/5899/5910 6068/5898/5909 6067/5897/5908\r\nf 6070/5900/5911 6071/5901/5912 6068/5898/5909 6069/5899/5910\r\nf 6050/5880/5892 6049/5879/5891 6068/5898/5909 6071/5901/5912\r\nf 6054/5884/5895 6072/5902/5913 6073/5903/5914 6055/5885/5896\r\nf 6074/5904/5915 6075/5905/5916 6073/5903/5914 6072/5902/5913\r\nf 6076/5906/5917 6077/5907/5918 6073/5903/5914 6075/5905/5916\r\nf 6056/5886/5897 6055/5885/5896 6073/5903/5914 6077/5907/5918\r\nf 6056/5886/5897 6077/5907/5918 6078/5908/5919 6059/5889/5900\r\nf 6076/5906/5917 6079/5909/5920 6078/5908/5919 6077/5907/5918\r\nf 6080/5910/5921 6081/5911/5922 6078/5908/5919 6079/5909/5920\r\nf 6060/5890/5901 6059/5889/5900 6078/5908/5919 6081/5911/5922\r\nf 6064/5894/5905 6082/5912/5923 6083/5913/5924 6065/5895/5906\r\nf 6084/5914/5925 6085/5915/5926 6083/5913/5924 6082/5912/5923\r\nf 6086/5916/5927 6087/5917/5928 6083/5913/5924 6085/5915/5926\r\nf 6066/5896/5907 6065/5895/5906 6083/5913/5924 6087/5917/5928\r\nf 6066/5896/5907 6087/5917/5928 6088/5918/5929 6069/5899/5910\r\nf 6086/5916/5927 6089/5919/5930 6088/5918/5929 6087/5917/5928\r\nf 6090/5920/5931 6091/5921/5932 6088/5918/5929 6089/5919/5930\r\nf 6070/5900/5911 6069/5899/5910 6088/5918/5929 6091/5921/5932\r\nf 6074/5904/5915 6092/5922/5933 6093/5923/5934 6075/5905/5916\r\nf 6094/5924/5935 6095/5925/5936 6093/5923/5934 6092/5922/5933\r\nf 6096/5926/5937 6097/5927/5938 6093/5923/5934 6095/5925/5936\r\nf 6076/5906/5917 6075/5905/5916 6093/5923/5934 6097/5927/5938\r\nf 6076/5906/5917 6097/5927/5938 6098/5928/5939 6079/5909/5920\r\nf 6096/5926/5937 6099/5929/5940 6098/5928/5939 6097/5927/5938\r\nf 6100/5930/5941 6101/5931/5942 6098/5928/5939 6099/5929/5940\r\nf 6080/5910/5921 6079/5909/5920 6098/5928/5939 6101/5931/5942\r\nf 6084/5914/5925 6102/5932/5943 6103/5933/5944 6085/5915/5926\r\nf 6104/5934/5945 6105/5935/5946 6103/5933/5944 6102/5932/5943\r\nf 6106/5936/5947 6107/5937/5948 6103/5933/5944 6105/5935/5946\r\nf 6086/5916/5927 6085/5915/5926 6103/5933/5944 6107/5937/5948\r\nf 6086/5916/5927 6107/5937/5948 6108/5938/5949 6089/5919/5930\r\nf 6106/5936/5947 6109/5939/5950 6108/5938/5949 6107/5937/5948\r\nf 6110/5940/5951 6111/5941/5952 6108/5938/5949 6109/5939/5950\r\nf 6090/5920/5931 6089/5919/5930 6108/5938/5949 6111/5941/5952\r\nf 6094/5924/5935 6112/5942/5953 6113/5943/5954 6095/5925/5936\r\nf 6114/5944/5955 6115/5945/5956 6113/5943/5954 6112/5942/5953\r\nf 6116/5946/5957 6117/5947/5958 6113/5943/5954 6115/5945/5956\r\nf 6096/5926/5937 6095/5925/5936 6113/5943/5954 6117/5947/5958\r\nf 6096/5926/5937 6117/5947/5958 6118/5948/5959 6099/5929/5940\r\nf 6116/5946/5957 6119/5949/5960 6118/5948/5959 6117/5947/5958\r\nf 6120/5950/5961 6121/5951/5962 6118/5948/5959 6119/5949/5960\r\nf 6100/5930/5941 6099/5929/5940 6118/5948/5959 6121/5951/5962\r\nf 6104/5934/5945 6122/5952/5963 6123/5953/5964 6105/5935/5946\r\nf 6124/5954/5965 6125/5955/5966 6123/5953/5964 6122/5952/5963\r\nf 6126/5956/5967 6127/5957/5968 6123/5953/5964 6125/5955/5966\r\nf 6106/5936/5947 6105/5935/5946 6123/5953/5964 6127/5957/5968\r\nf 6106/5936/5947 6127/5957/5968 6128/5958/5969 6109/5939/5950\r\nf 6126/5956/5967 6129/5959/5970 6128/5958/5969 6127/5957/5968\r\nf 6130/5960/5971 6131/5961/5972 6128/5958/5969 6129/5959/5970\r\nf 6110/5940/5951 6109/5939/5950 6128/5958/5969 6131/5961/5972\r\nf 6114/5944/5955 6132/5962/5973 6133/5963/5974 6115/5945/5956\r\nf 6134/5964/5975 6135/5965/5976 6133/5963/5974 6132/5962/5973\r\nf 6136/5966/5977 6137/5967/5978 6133/5963/5974 6135/5965/5976\r\nf 6116/5946/5957 6115/5945/5956 6133/5963/5974 6137/5967/5978\r\nf 6116/5946/5957 6137/5967/5978 6138/5968/5979 6119/5949/5960\r\nf 6136/5966/5977 6139/5969/5980 6138/5968/5979 6137/5967/5978\r\nf 6140/5970/5981 6141/5971/5982 6138/5968/5979 6139/5969/5980\r\nf 6120/5950/5961 6119/5949/5960 6138/5968/5979 6141/5971/5982\r\nf 6124/5954/5965 6142/5972/5983 6143/5973/5984 6125/5955/5966\r\nf 6144/5974/5985 6145/5975/5986 6143/5973/5984 6142/5972/5983\r\nf 6146/5976/5987 6147/5977/5988 6143/5973/5984 6145/5975/5986\r\nf 6126/5956/5967 6125/5955/5966 6143/5973/5984 6147/5977/5988\r\nf 6126/5956/5967 6147/5977/5988 6148/5978/5989 6129/5959/5970\r\nf 6146/5976/5987 6149/5979/5990 6148/5978/5989 6147/5977/5988\r\nf 6150/5980/5991 6151/5981/5992 6148/5978/5989 6149/5979/5990\r\nf 6130/5960/5971 6129/5959/5970 6148/5978/5989 6151/5981/5992\r\nf 6134/5964/5975 6152/5982/5993 6153/5983/5994 6135/5965/5976\r\nf 6154/5984/5995 6155/5985/5996 6153/5983/5994 6152/5982/5993\r\nf 6156/5986/5997 6157/5987/5998 6153/5983/5994 6155/5985/5996\r\nf 6136/5966/5977 6135/5965/5976 6153/5983/5994 6157/5987/5998\r\nf 6136/5966/5977 6157/5987/5998 6158/5988/5999 6139/5969/5980\r\nf 6156/5986/5997 6159/5989/6000 6158/5988/5999 6157/5987/5998\r\nf 6160/5990/6001 6161/5991/6002 6158/5988/5999 6159/5989/6000\r\nf 6140/5970/5981 6139/5969/5980 6158/5988/5999 6161/5991/6002\r\nf 6144/5974/5985 6162/5992/6003 6163/5993/6004 6145/5975/5986\r\nf 6164/5994/6005 6165/5995/6006 6163/5993/6004 6162/5992/6003\r\nf 6166/5996/6007 6167/5997/6008 6163/5993/6004 6165/5995/6006\r\nf 6146/5976/5987 6145/5975/5986 6163/5993/6004 6167/5997/6008\r\nf 6146/5976/5987 6167/5997/6008 6168/5998/6009 6149/5979/5990\r\nf 6166/5996/6007 6169/5999/6010 6168/5998/6009 6167/5997/6008\r\nf 6170/6000/6011 6171/6001/6012 6168/5998/6009 6169/5999/6010\r\nf 6150/5980/5991 6149/5979/5990 6168/5998/6009 6171/6001/6012\r\nf 6154/5984/5995 6172/6002/6013 6173/6003/6014 6155/5985/5996\r\nf 6174/6004/6015 6175/6005/6016 6173/6003/6014 6172/6002/6013\r\nf 6176/6006/6017 6177/6007/6018 6173/6003/6014 6175/6005/6016\r\nf 6156/5986/5997 6155/5985/5996 6173/6003/6014 6177/6007/6018\r\nf 6156/5986/5997 6177/6007/6018 6178/6008/6019 6159/5989/6000\r\nf 6176/6006/6017 6179/6009/6020 6178/6008/6019 6177/6007/6018\r\nf 6180/6010/6021 6181/6011/6022 6178/6008/6019 6179/6009/6020\r\nf 6160/5990/6001 6159/5989/6000 6178/6008/6019 6181/6011/6022\r\nf 6164/5994/6005 6182/6012/6023 6183/6013/6024 6165/5995/6006\r\nf 6184/6014/6025 6185/6015/6026 6183/6013/6024 6182/6012/6023\r\nf 6186/6016/6027 6187/6017/6028 6183/6013/6024 6185/6015/6026\r\nf 6166/5996/6007 6165/5995/6006 6183/6013/6024 6187/6017/6028\r\nf 6166/5996/6007 6187/6017/6028 6188/6018/6029 6169/5999/6010\r\nf 6186/6016/6027 6189/6019/6030 6188/6018/6029 6187/6017/6028\r\nf 6190/6020/6031 6191/6021/6032 6188/6018/6029 6189/6019/6030\r\nf 6170/6000/6011 6169/5999/6010 6188/6018/6029 6191/6021/6032\r\nf 6174/6004/6015 6192/6022/6033 6193/6023/6034 6175/6005/6016\r\nf 6194/6024/6035 6195/6025/6036 6193/6023/6034 6192/6022/6033\r\nf 6196/6026/6037 6197/6027/6038 6193/6023/6034 6195/6025/6036\r\nf 6176/6006/6017 6175/6005/6016 6193/6023/6034 6197/6027/6038\r\nf 6176/6006/6017 6197/6027/6038 6198/6028/6039 6179/6009/6020\r\nf 6196/6026/6037 6199/6029/6040 6198/6028/6039 6197/6027/6038\r\nf 6200/6030/6041 6201/6031/6042 6198/6028/6039 6199/6029/6040\r\nf 6180/6010/6021 6179/6009/6020 6198/6028/6039 6201/6031/6042\r\nf 6184/6014/6025 6202/6032/6043 6203/6033/6044 6185/6015/6026\r\nf 6204/6034/6045 6205/6035/6046 6203/6033/6044 6202/6032/6043\r\nf 6206/6036/6047 6207/6037/6048 6203/6033/6044 6205/6035/6046\r\nf 6186/6016/6027 6185/6015/6026 6203/6033/6044 6207/6037/6048\r\nf 6186/6016/6027 6207/6037/6048 6208/6038/6049 6189/6019/6030\r\nf 6206/6036/6047 6209/6039/6050 6208/6038/6049 6207/6037/6048\r\nf 6210/6040/6051 6211/6041/6052 6208/6038/6049 6209/6039/6050\r\nf 6190/6020/6031 6189/6019/6030 6208/6038/6049 6211/6041/6052\r\nf 6194/6024/6035 6212/6042/6053 6213/6043/6054 6195/6025/6036\r\nf 6214/6044/6055 6215/6045/6056 6213/6043/6054 6212/6042/6053\r\nf 6216/6046/6057 6217/6047/6058 6213/6043/6054 6215/6045/6056\r\nf 6196/6026/6037 6195/6025/6036 6213/6043/6054 6217/6047/6058\r\nf 6196/6026/6037 6217/6047/6058 6218/6048/6059 6199/6029/6040\r\nf 6216/6046/6057 6219/6049/6060 6218/6048/6059 6217/6047/6058\r\nf 6220/6050/6061 6221/6051/6062 6218/6048/6059 6219/6049/6060\r\nf 6200/6030/6041 6199/6029/6040 6218/6048/6059 6221/6051/6062\r\nf 6204/6034/6045 6222/6052/6063 6223/6053/6064 6205/6035/6046\r\nf 6224/6054/6065 6225/6055/6066 6223/6053/6064 6222/6052/6063\r\nf 6226/6056/6067 6227/6057/6068 6223/6053/6064 6225/6055/6066\r\nf 6206/6036/6047 6205/6035/6046 6223/6053/6064 6227/6057/6068\r\nf 6206/6036/6047 6227/6057/6068 6228/6058/6069 6209/6039/6050\r\nf 6226/6056/6067 6229/6059/6070 6228/6058/6069 6227/6057/6068\r\nf 6230/6060/6071 6231/6061/6072 6228/6058/6069 6229/6059/6070\r\nf 6210/6040/6051 6209/6039/6050 6228/6058/6069 6231/6061/6072\r\nf 6214/6044/6055 6232/6062/6073 6233/6063/6074 6215/6045/6056\r\nf 6234/6064/6075 6235/6065/6076 6233/6063/6074 6232/6062/6073\r\nf 6236/6066/6077 6237/6067/6078 6233/6063/6074 6235/6065/6076\r\nf 6216/6046/6057 6215/6045/6056 6233/6063/6074 6237/6067/6078\r\nf 6216/6046/6057 6237/6067/6078 6238/6068/6079 6219/6049/6060\r\nf 6236/6066/6077 6239/6069/6080 6238/6068/6079 6237/6067/6078\r\nf 6240/6070/6081 6241/6071/6082 6238/6068/6079 6239/6069/6080\r\nf 6220/6050/6061 6219/6049/6060 6238/6068/6079 6241/6071/6082\r\nf 6224/6054/6065 6242/6072/6083 6243/6073/6084 6225/6055/6066\r\nf 6244/6074/6085 6245/6075/6086 6243/6073/6084 6242/6072/6083\r\nf 6246/6076/6087 6247/6077/6088 6243/6073/6084 6245/6075/6086\r\nf 6226/6056/6067 6225/6055/6066 6243/6073/6084 6247/6077/6088\r\nf 6226/6056/6067 6247/6077/6088 6248/6078/6089 6229/6059/6070\r\nf 6246/6076/6087 6249/6079/6090 6248/6078/6089 6247/6077/6088\r\nf 6250/6080/6091 6251/6081/6092 6248/6078/6089 6249/6079/6090\r\nf 6230/6060/6071 6229/6059/6070 6248/6078/6089 6251/6081/6092\r\nf 6234/6064/6075 6252/6082/6093 6253/6083/6094 6235/6065/6076\r\nf 6254/6084/6095 6255/6085/6096 6253/6083/6094 6252/6082/6093\r\nf 6256/6086/6097 6257/6087/6098 6253/6083/6094 6255/6085/6096\r\nf 6236/6066/6077 6235/6065/6076 6253/6083/6094 6257/6087/6098\r\nf 6236/6066/6077 6257/6087/6098 6258/6088/6099 6239/6069/6080\r\nf 6256/6086/6097 6259/6089/6100 6258/6088/6099 6257/6087/6098\r\nf 6260/6090/6101 6261/6091/6102 6258/6088/6099 6259/6089/6100\r\nf 6240/6070/6081 6239/6069/6080 6258/6088/6099 6261/6091/6102\r\nf 6244/6074/6085 6262/6092/6103 6263/6093/6104 6245/6075/6086\r\nf 6264/6094/6105 6265/6095/6106 6263/6093/6104 6262/6092/6103\r\nf 6266/6096/6107 6267/6097/6108 6263/6093/6104 6265/6095/6106\r\nf 6246/6076/6087 6245/6075/6086 6263/6093/6104 6267/6097/6108\r\nf 6246/6076/6087 6267/6097/6108 6268/6098/6109 6249/6079/6090\r\nf 6266/6096/6107 6269/6099/6110 6268/6098/6109 6267/6097/6108\r\nf 6270/6100/6111 6271/6101/6112 6268/6098/6109 6269/6099/6110\r\nf 6250/6080/6091 6249/6079/6090 6268/6098/6109 6271/6101/6112\r\nf 6254/6084/6095 6272/6102/6113 6273/6103/6114 6255/6085/6096\r\nf 6274/6104/6115 6275/6105/6116 6273/6103/6114 6272/6102/6113\r\nf 6276/6106/6117 6277/6107/6118 6273/6103/6114 6275/6105/6116\r\nf 6256/6086/6097 6255/6085/6096 6273/6103/6114 6277/6107/6118\r\nf 6256/6086/6097 6277/6107/6118 6278/6108/6119 6259/6089/6100\r\nf 6276/6106/6117 6279/6109/6120 6278/6108/6119 6277/6107/6118\r\nf 6280/6110/6121 6281/6111/6122 6278/6108/6119 6279/6109/6120\r\nf 6260/6090/6101 6259/6089/6100 6278/6108/6119 6281/6111/6122\r\nf 6264/6094/6105 6282/6112/6123 6283/6113/6124 6265/6095/6106\r\nf 6284/6114/6125 6285/6115/6126 6283/6113/6124 6282/6112/6123\r\nf 6286/6116/6127 6287/6117/6128 6283/6113/6124 6285/6115/6126\r\nf 6266/6096/6107 6265/6095/6106 6283/6113/6124 6287/6117/6128\r\nf 6266/6096/6107 6287/6117/6128 6288/6118/6129 6269/6099/6110\r\nf 6286/6116/6127 6289/6119/6130 6288/6118/6129 6287/6117/6128\r\nf 6290/6120/6131 6291/6121/6132 6288/6118/6129 6289/6119/6130\r\nf 6270/6100/6111 6269/6099/6110 6288/6118/6129 6291/6121/6132\r\nf 6274/6104/6115 6292/6122/6133 6293/6123/6134 6275/6105/6116\r\nf 6294/6124/6135 6295/6125/6136 6293/6123/6134 6292/6122/6133\r\nf 6296/6126/6137 6297/6127/6138 6293/6123/6134 6295/6125/6136\r\nf 6276/6106/6117 6275/6105/6116 6293/6123/6134 6297/6127/6138\r\nf 6276/6106/6117 6297/6127/6138 6298/6128/6139 6279/6109/6120\r\nf 6296/6126/6137 6299/6129/6140 6298/6128/6139 6297/6127/6138\r\nf 6300/6130/6141 6301/6131/6142 6298/6128/6139 6299/6129/6140\r\nf 6280/6110/6121 6279/6109/6120 6298/6128/6139 6301/6131/6142\r\nf 6284/6114/6125 6302/6132/6143 6303/6133/6144 6285/6115/6126\r\nf 6304/6134/6145 6305/6135/6146 6303/6133/6144 6302/6132/6143\r\nf 6306/6136/6147 6307/6137/6148 6303/6133/6144 6305/6135/6146\r\nf 6286/6116/6127 6285/6115/6126 6303/6133/6144 6307/6137/6148\r\nf 6286/6116/6127 6307/6137/6148 6308/6138/6149 6289/6119/6130\r\nf 6306/6136/6147 6309/6139/6150 6308/6138/6149 6307/6137/6148\r\nf 6310/6140/6151 6311/6141/6152 6308/6138/6149 6309/6139/6150\r\nf 6290/6120/6131 6289/6119/6130 6308/6138/6149 6311/6141/6152\r\nf 6294/6124/6135 6312/6142/6153 6313/6143/6154 6295/6125/6136\r\nf 6314/6144/6155 6315/6145/6156 6313/6143/6154 6312/6142/6153\r\nf 6316/6146/6157 6317/6147/6158 6313/6143/6154 6315/6145/6156\r\nf 6296/6126/6137 6295/6125/6136 6313/6143/6154 6317/6147/6158\r\nf 6296/6126/6137 6317/6147/6158 6318/6148/6159 6299/6129/6140\r\nf 6316/6146/6157 6319/6149/6160 6318/6148/6159 6317/6147/6158\r\nf 6320/6150/6161 6321/6151/6162 6318/6148/6159 6319/6149/6160\r\nf 6300/6130/6141 6299/6129/6140 6318/6148/6159 6321/6151/6162\r\nf 6304/6134/6145 6322/6152/6163 6323/6153/6164 6305/6135/6146\r\nf 6324/6154/6165 6325/6155/6166 6323/6153/6164 6322/6152/6163\r\nf 6326/6156/6167 6327/6157/6168 6323/6153/6164 6325/6155/6166\r\nf 6306/6136/6147 6305/6135/6146 6323/6153/6164 6327/6157/6168\r\nf 6306/6136/6147 6327/6157/6168 6328/6158/6169 6309/6139/6150\r\nf 6326/6156/6167 6329/6159/6170 6328/6158/6169 6327/6157/6168\r\nf 6330/6160/6171 6331/6161/6172 6328/6158/6169 6329/6159/6170\r\nf 6310/6140/6151 6309/6139/6150 6328/6158/6169 6331/6161/6172\r\nf 6314/6144/6155 6332/6162/6173 6333/6163/6174 6315/6145/6156\r\nf 6334/6164/6175 6335/6165/6176 6333/6163/6174 6332/6162/6173\r\nf 6336/6166/6177 6337/6167/6178 6333/6163/6174 6335/6165/6176\r\nf 6316/6146/6157 6315/6145/6156 6333/6163/6174 6337/6167/6178\r\nf 6316/6146/6157 6337/6167/6178 6338/6168/6179 6319/6149/6160\r\nf 6336/6166/6177 6339/6169/6180 6338/6168/6179 6337/6167/6178\r\nf 6340/6170/6181 6341/6171/6182 6338/6168/6179 6339/6169/6180\r\nf 6320/6150/6161 6319/6149/6160 6338/6168/6179 6341/6171/6182\r\nf 6324/6154/6165 6342/6172/6183 6343/6173/6184 6325/6155/6166\r\nf 6344/6174/6185 6345/6175/6186 6343/6173/6184 6342/6172/6183\r\nf 6346/6176/6187 6347/6177/6188 6343/6173/6184 6345/6175/6186\r\nf 6326/6156/6167 6325/6155/6166 6343/6173/6184 6347/6177/6188\r\nf 6326/6156/6167 6347/6177/6188 6348/6178/6189 6329/6159/6170\r\nf 6346/6176/6187 6349/6179/6190 6348/6178/6189 6347/6177/6188\r\nf 6350/6180/6191 6351/6181/6192 6348/6178/6189 6349/6179/6190\r\nf 6330/6160/6171 6329/6159/6170 6348/6178/6189 6351/6181/6192\r\nf 6334/6164/6175 6352/6182/6193 6353/6183/6194 6335/6165/6176\r\nf 6354/6184/6195 6355/6185/6196 6353/6183/6194 6352/6182/6193\r\nf 6356/6186/6197 6357/6187/6198 6353/6183/6194 6355/6185/6196\r\nf 6336/6166/6177 6335/6165/6176 6353/6183/6194 6357/6187/6198\r\nf 6336/6166/6177 6357/6187/6198 6358/6188/6199 6339/6169/6180\r\nf 6356/6186/6197 6359/6189/6200 6358/6188/6199 6357/6187/6198\r\nf 6360/6190/6201 6361/6191/6202 6358/6188/6199 6359/6189/6200\r\nf 6340/6170/6181 6339/6169/6180 6358/6188/6199 6361/6191/6202\r\nf 6344/6174/6185 6362/6192/6203 6363/6193/6204 6345/6175/6186\r\nf 6364/6194/6205 6365/6195/6206 6363/6193/6204 6362/6192/6203\r\nf 6366/6196/6207 6367/6197/6208 6363/6193/6204 6365/6195/6206\r\nf 6346/6176/6187 6345/6175/6186 6363/6193/6204 6367/6197/6208\r\nf 6346/6176/6187 6367/6197/6208 6368/6198/6209 6349/6179/6190\r\nf 6366/6196/6207 6369/6199/6210 6368/6198/6209 6367/6197/6208\r\nf 6370/6200/6211 6371/6201/6212 6368/6198/6209 6369/6199/6210\r\nf 6350/6180/6191 6349/6179/6190 6368/6198/6209 6371/6201/6212\r\nf 6354/6184/6195 6372/6202/6213 6373/6203/6214 6355/6185/6196\r\nf 6374/6204/6215 6375/6205/6216 6373/6203/6214 6372/6202/6213\r\nf 6376/6206/6217 6377/6207/6218 6373/6203/6214 6375/6205/6216\r\nf 6356/6186/6197 6355/6185/6196 6373/6203/6214 6377/6207/6218\r\nf 6356/6186/6197 6377/6207/6218 6378/6208/6219 6359/6189/6200\r\nf 6376/6206/6217 6379/6209/6220 6378/6208/6219 6377/6207/6218\r\nf 6380/6210/6221 6381/6211/6222 6378/6208/6219 6379/6209/6220\r\nf 6360/6190/6201 6359/6189/6200 6378/6208/6219 6381/6211/6222\r\nf 6364/6194/6205 6382/6212/6223 6383/6213/6224 6365/6195/6206\r\nf 6384/6214/6225 6385/6215/6206 6383/6213/6224 6382/6212/6223\r\nf 6386/6216/6226 6387/6217/6227 6383/6213/6224 6385/6215/6206\r\nf 6366/6196/6207 6365/6195/6206 6383/6213/6224 6387/6217/6227\r\nf 6366/6196/6207 6387/6217/6227 6388/6218/6228 6369/6199/6210\r\nf 6386/6216/6226 6389/6219/6229 6388/6218/6228 6387/6217/6227\r\nf 6390/6220/6230 6391/6221/6231 6388/6218/6228 6389/6219/6229\r\nf 6370/6200/6211 6369/6199/6210 6388/6218/6228 6391/6221/6231\r\nf 6374/6204/6215 6392/6222/6232 6393/6223/6233 6375/6205/6216\r\nf 6394/6224/6234 6395/6225/6216 6393/6223/6233 6392/6222/6232\r\nf 6396/6226/6235 6397/6227/6236 6393/6223/6233 6395/6225/6216\r\nf 6376/6206/6217 6375/6205/6216 6393/6223/6233 6397/6227/6236\r\nf 6376/6206/6217 6397/6227/6236 6398/6228/6237 6379/6209/6220\r\nf 6396/6226/6235 6399/6229/6238 6398/6228/6237 6397/6227/6236\r\nf 6400/6230/6239 6401/6231/6240 6398/6228/6237 6399/6229/6238\r\nf 6380/6210/6221 6379/6209/6220 6398/6228/6237 6401/6231/6240\r\nf 6384/6214/6225 6402/6232/6241 6403/6233/6242 6385/6215/6206\r\nf 6404/6234/6243 6405/6235/6244 6403/6233/6242 6402/6232/6241\r\nf 6406/6236/6245 6407/6237/6246 6403/6233/6242 6405/6235/6244\r\nf 6386/6216/6226 6385/6215/6206 6403/6233/6242 6407/6237/6246\r\nf 6386/6216/6226 6407/6237/6246 6408/6238/6247 6389/6219/6229\r\nf 6406/6236/6245 6409/6239/6248 6408/6238/6247 6407/6237/6246\r\nf 6410/6240/6249 6411/6241/6250 6408/6238/6247 6409/6239/6248\r\nf 6390/6220/6230 6389/6219/6229 6408/6238/6247 6411/6241/6250\r\nf 6394/6224/6234 6412/6242/6251 6413/6243/6252 6395/6225/6216\r\nf 6414/6244/6253 6415/6245/6254 6413/6243/6252 6412/6242/6251\r\nf 6416/6246/6255 6417/6247/6256 6413/6243/6252 6415/6245/6254\r\nf 6396/6226/6235 6395/6225/6216 6413/6243/6252 6417/6247/6256\r\nf 6396/6226/6235 6417/6247/6256 6418/6248/6257 6399/6229/6238\r\nf 6416/6246/6255 6419/6249/6258 6418/6248/6257 6417/6247/6256\r\nf 6420/6250/6259 6421/6251/6260 6418/6248/6257 6419/6249/6258\r\nf 6400/6230/6239 6399/6229/6238 6418/6248/6257 6421/6251/6260\r\nf 6404/6234/6243 6422/6252/6261 6423/6253/6262 6405/6235/6244\r\nf 6424/6254/6263 6425/6255/6264 6423/6253/6262 6422/6252/6261\r\nf 6426/6256/6265 6427/6257/6266 6423/6253/6262 6425/6255/6264\r\nf 6406/6236/6245 6405/6235/6244 6423/6253/6262 6427/6257/6266\r\nf 6406/6236/6245 6427/6257/6266 6428/6258/6267 6409/6239/6248\r\nf 6426/6256/6265 6429/6259/6268 6428/6258/6267 6427/6257/6266\r\nf 6430/6260/6269 6431/6261/6270 6428/6258/6267 6429/6259/6268\r\nf 6410/6240/6249 6409/6239/6248 6428/6258/6267 6431/6261/6270\r\nf 6414/6244/6253 6432/6262/6271 6433/6263/6272 6415/6245/6254\r\nf 6434/6264/6273 6435/6265/6274 6433/6263/6272 6432/6262/6271\r\nf 6436/6266/6275 6437/6267/6276 6433/6263/6272 6435/6265/6274\r\nf 6416/6246/6255 6415/6245/6254 6433/6263/6272 6437/6267/6276\r\nf 6416/6246/6255 6437/6267/6276 6438/6268/6277 6419/6249/6258\r\nf 6436/6266/6275 6439/6269/6278 6438/6268/6277 6437/6267/6276\r\nf 6440/6270/6279 6441/6271/6280 6438/6268/6277 6439/6269/6278\r\nf 6420/6250/6259 6419/6249/6258 6438/6268/6277 6441/6271/6280\r\nf 6424/6254/6263 6442/6272/6281 6443/6273/6282 6425/6255/6264\r\nf 6444/6274/6283 6445/6275/6284 6443/6273/6282 6442/6272/6281\r\nf 6446/6276/6285 6447/6277/6286 6443/6273/6282 6445/6275/6284\r\nf 6426/6256/6265 6425/6255/6264 6443/6273/6282 6447/6277/6286\r\nf 6426/6256/6265 6447/6277/6286 6448/6278/6287 6429/6259/6268\r\nf 6446/6276/6285 6449/6279/6288 6448/6278/6287 6447/6277/6286\r\nf 6450/6280/6289 6451/6281/6290 6448/6278/6287 6449/6279/6288\r\nf 6430/6260/6269 6429/6259/6268 6448/6278/6287 6451/6281/6290\r\nf 6434/6264/6273 6452/6282/6291 6453/6283/6292 6435/6265/6274\r\nf 6454/6284/6293 6455/6285/6294 6453/6283/6292 6452/6282/6291\r\nf 6456/6286/6295 6457/6287/6296 6453/6283/6292 6455/6285/6294\r\nf 6436/6266/6275 6435/6265/6274 6453/6283/6292 6457/6287/6296\r\nf 6436/6266/6275 6457/6287/6296 6458/6288/6297 6439/6269/6278\r\nf 6456/6286/6295 6459/6289/6298 6458/6288/6297 6457/6287/6296\r\nf 6460/6290/6299 6461/6291/6300 6458/6288/6297 6459/6289/6298\r\nf 6440/6270/6279 6439/6269/6278 6458/6288/6297 6461/6291/6300\r\nf 6444/6274/6283 6462/6292/6301 6463/6293/6302 6445/6275/6284\r\nf 6464/6294/6303 6465/6295/6304 6463/6293/6302 6462/6292/6301\r\nf 6466/6296/6305 6467/6297/6306 6463/6293/6302 6465/6295/6304\r\nf 6446/6276/6285 6445/6275/6284 6463/6293/6302 6467/6297/6306\r\nf 6446/6276/6285 6467/6297/6306 6468/6298/6307 6449/6279/6288\r\nf 6466/6296/6305 6469/6299/6308 6468/6298/6307 6467/6297/6306\r\nf 6470/6300/6309 6471/6301/6310 6468/6298/6307 6469/6299/6308\r\nf 6450/6280/6289 6449/6279/6288 6468/6298/6307 6471/6301/6310\r\nf 6454/6284/6293 6472/6302/6311 6473/6303/6312 6455/6285/6294\r\nf 6474/6304/6313 6475/6305/6314 6473/6303/6312 6472/6302/6311\r\nf 6476/6306/6315 6477/6307/6316 6473/6303/6312 6475/6305/6314\r\nf 6456/6286/6295 6455/6285/6294 6473/6303/6312 6477/6307/6316\r\nf 6456/6286/6295 6477/6307/6316 6478/6308/6317 6459/6289/6298\r\nf 6476/6306/6315 6479/6309/6318 6478/6308/6317 6477/6307/6316\r\nf 6480/6310/6319 6481/6311/6320 6478/6308/6317 6479/6309/6318\r\nf 6460/6290/6299 6459/6289/6298 6478/6308/6317 6481/6311/6320\r\nf 6464/6294/6303 6482/6312/6321 6483/6313/6322 6465/6295/6304\r\nf 6484/6314/6323 6485/6315/6324 6483/6313/6322 6482/6312/6321\r\nf 6486/6316/6325 6487/6317/6326 6483/6313/6322 6485/6315/6324\r\nf 6466/6296/6305 6465/6295/6304 6483/6313/6322 6487/6317/6326\r\nf 6466/6296/6305 6487/6317/6326 6488/6318/6327 6469/6299/6308\r\nf 6486/6316/6325 6489/6319/6328 6488/6318/6327 6487/6317/6326\r\nf 6490/6320/6329 6491/6321/6330 6488/6318/6327 6489/6319/6328\r\nf 6470/6300/6309 6469/6299/6308 6488/6318/6327 6491/6321/6330\r\nf 6474/6304/6313 6492/6322/6331 6493/6323/6332 6475/6305/6314\r\nf 6494/6324/6333 6495/6325/6334 6493/6323/6332 6492/6322/6331\r\nf 6496/6326/6335 6497/6327/6336 6493/6323/6332 6495/6325/6334\r\nf 6476/6306/6315 6475/6305/6314 6493/6323/6332 6497/6327/6336\r\nf 6476/6306/6315 6497/6327/6336 6498/6328/6337 6479/6309/6318\r\nf 6496/6326/6335 6499/6329/6338 6498/6328/6337 6497/6327/6336\r\nf 6500/6330/6339 6501/6331/6340 6498/6328/6337 6499/6329/6338\r\nf 6480/6310/6319 6479/6309/6318 6498/6328/6337 6501/6331/6340\r\nf 6484/6314/6323 6502/6332/6341 6503/6333/6342 6485/6315/6324\r\nf 6504/6334/6343 6505/6335/6344 6503/6333/6342 6502/6332/6341\r\nf 6506/6336/6345 6507/6337/6346 6503/6333/6342 6505/6335/6344\r\nf 6486/6316/6325 6485/6315/6324 6503/6333/6342 6507/6337/6346\r\nf 6486/6316/6325 6507/6337/6346 6508/6338/6347 6489/6319/6328\r\nf 6506/6336/6345 6509/6339/6348 6508/6338/6347 6507/6337/6346\r\nf 6510/6340/6349 6511/6341/6350 6508/6338/6347 6509/6339/6348\r\nf 6490/6320/6329 6489/6319/6328 6508/6338/6347 6511/6341/6350\r\nf 6494/6324/6333 6512/6342/6351 6513/6343/6352 6495/6325/6334\r\nf 6514/6344/6353 6515/6345/6354 6513/6343/6352 6512/6342/6351\r\nf 6516/6346/6355 6517/6347/6356 6513/6343/6352 6515/6345/6354\r\nf 6496/6326/6335 6495/6325/6334 6513/6343/6352 6517/6347/6356\r\nf 6496/6326/6335 6517/6347/6356 6518/6348/6357 6499/6329/6338\r\nf 6516/6346/6355 6519/6349/6358 6518/6348/6357 6517/6347/6356\r\nf 6520/6350/6359 6521/6351/6360 6518/6348/6357 6519/6349/6358\r\nf 6500/6330/6339 6499/6329/6338 6518/6348/6357 6521/6351/6360\r\nf 6504/6334/6343 6522/6352/6361 6523/6353/6362 6505/6335/6344\r\nf 6524/6354/6363 6525/6355/6364 6523/6353/6362 6522/6352/6361\r\nf 6526/6356/6365 6527/6357/6366 6523/6353/6362 6525/6355/6364\r\nf 6506/6336/6345 6505/6335/6344 6523/6353/6362 6527/6357/6366\r\nf 6506/6336/6345 6527/6357/6366 6528/6358/6367 6509/6339/6348\r\nf 6526/6356/6365 6529/6359/6368 6528/6358/6367 6527/6357/6366\r\nf 6530/6360/6369 6531/6361/6370 6528/6358/6367 6529/6359/6368\r\nf 6510/6340/6349 6509/6339/6348 6528/6358/6367 6531/6361/6370\r\nf 6514/6344/6353 6532/6362/6371 6533/6363/6372 6515/6345/6354\r\nf 6534/6364/6373 6535/6365/6374 6533/6363/6372 6532/6362/6371\r\nf 6536/6366/6375 6537/6367/6376 6533/6363/6372 6535/6365/6374\r\nf 6516/6346/6355 6515/6345/6354 6533/6363/6372 6537/6367/6376\r\nf 6516/6346/6355 6537/6367/6376 6538/6368/6377 6519/6349/6358\r\nf 6536/6366/6375 6539/6369/6378 6538/6368/6377 6537/6367/6376\r\nf 6540/6370/6379 6541/6371/6380 6538/6368/6377 6539/6369/6378\r\nf 6520/6350/6359 6519/6349/6358 6538/6368/6377 6541/6371/6380\r\nf 6524/6354/6363 6542/6372/6381 6543/6373/6382 6525/6355/6364\r\nf 6544/6374/6383 6545/6375/6384 6543/6373/6382 6542/6372/6381\r\nf 6546/6376/6385 6547/6377/6386 6543/6373/6382 6545/6375/6384\r\nf 6526/6356/6365 6525/6355/6364 6543/6373/6382 6547/6377/6386\r\nf 6526/6356/6365 6547/6377/6386 6548/6378/6387 6529/6359/6368\r\nf 6546/6376/6385 6549/6379/6388 6548/6378/6387 6547/6377/6386\r\nf 6550/6380/6389 6551/6381/6390 6548/6378/6387 6549/6379/6388\r\nf 6530/6360/6369 6529/6359/6368 6548/6378/6387 6551/6381/6390\r\nf 6534/6364/6373 6552/6382/6391 6553/6383/6392 6535/6365/6374\r\nf 6554/6384/6393 6555/6385/6394 6553/6383/6392 6552/6382/6391\r\nf 6556/6386/6395 6557/6387/6396 6553/6383/6392 6555/6385/6394\r\nf 6536/6366/6375 6535/6365/6374 6553/6383/6392 6557/6387/6396\r\nf 6536/6366/6375 6557/6387/6396 6558/6388/6397 6539/6369/6378\r\nf 6556/6386/6395 6559/6389/6398 6558/6388/6397 6557/6387/6396\r\nf 6560/6390/6399 6561/6391/6400 6558/6388/6397 6559/6389/6398\r\nf 6540/6370/6379 6539/6369/6378 6558/6388/6397 6561/6391/6400\r\nf 6544/6374/6383 6562/6392/6401 6563/6393/6402 6545/6375/6384\r\nf 6564/6394/6403 6565/6395/6404 6563/6393/6402 6562/6392/6401\r\nf 6566/6396/6405 6567/6397/6406 6563/6393/6402 6565/6395/6404\r\nf 6546/6376/6385 6545/6375/6384 6563/6393/6402 6567/6397/6406\r\nf 6546/6376/6385 6567/6397/6406 6568/6398/6407 6549/6379/6388\r\nf 6566/6396/6405 6569/6399/6408 6568/6398/6407 6567/6397/6406\r\nf 6570/6400/6409 6571/6401/6410 6568/6398/6407 6569/6399/6408\r\nf 6550/6380/6389 6549/6379/6388 6568/6398/6407 6571/6401/6410\r\nf 6554/6384/6393 6572/6402/6411 6573/6403/6412 6555/6385/6394\r\nf 6574/6404/6413 6575/6405/6414 6573/6403/6412 6572/6402/6411\r\nf 6576/6406/6415 6577/6407/6416 6573/6403/6412 6575/6405/6414\r\nf 6556/6386/6395 6555/6385/6394 6573/6403/6412 6577/6407/6416\r\nf 6556/6386/6395 6577/6407/6416 6578/6408/6417 6559/6389/6398\r\nf 6576/6406/6415 6579/6409/6418 6578/6408/6417 6577/6407/6416\r\nf 6580/6410/6419 6581/6411/6420 6578/6408/6417 6579/6409/6418\r\nf 6560/6390/6399 6559/6389/6398 6578/6408/6417 6581/6411/6420\r\nf 6564/6394/6403 6582/6412/6421 6583/6413/6422 6565/6395/6404\r\nf 6584/6414/6423 6585/6415/6424 6583/6413/6422 6582/6412/6421\r\nf 6586/6416/6425 6587/6417/6426 6583/6413/6422 6585/6415/6424\r\nf 6566/6396/6405 6565/6395/6404 6583/6413/6422 6587/6417/6426\r\nf 6566/6396/6405 6587/6417/6426 6588/6418/6427 6569/6399/6408\r\nf 6586/6416/6425 6589/6419/6428 6588/6418/6427 6587/6417/6426\r\nf 6590/6420/6429 6591/6421/6430 6588/6418/6427 6589/6419/6428\r\nf 6570/6400/6409 6569/6399/6408 6588/6418/6427 6591/6421/6430\r\nf 6574/6404/6413 6592/6422/6431 6593/6423/6432 6575/6405/6414\r\nf 6594/6424/6433 6595/6425/6434 6593/6423/6432 6592/6422/6431\r\nf 6596/6426/6435 6597/6427/6436 6593/6423/6432 6595/6425/6434\r\nf 6576/6406/6415 6575/6405/6414 6593/6423/6432 6597/6427/6436\r\nf 6576/6406/6415 6597/6427/6436 6598/6428/6437 6579/6409/6418\r\nf 6596/6426/6435 6599/6429/6438 6598/6428/6437 6597/6427/6436\r\nf 6600/6430/6439 6601/6431/6440 6598/6428/6437 6599/6429/6438\r\nf 6580/6410/6419 6579/6409/6418 6598/6428/6437 6601/6431/6440\r\nf 6584/6414/6423 6602/6432/6441 6603/6433/6442 6585/6415/6424\r\nf 6604/6434/6443 6605/6435/6444 6603/6433/6442 6602/6432/6441\r\nf 6606/6436/6445 6607/6437/6446 6603/6433/6442 6605/6435/6444\r\nf 6586/6416/6425 6585/6415/6424 6603/6433/6442 6607/6437/6446\r\nf 6586/6416/6425 6607/6437/6446 6608/6438/6447 6589/6419/6428\r\nf 6606/6436/6445 6609/6439/6448 6608/6438/6447 6607/6437/6446\r\nf 6610/6440/6449 6611/6441/6450 6608/6438/6447 6609/6439/6448\r\nf 6590/6420/6429 6589/6419/6428 6608/6438/6447 6611/6441/6450\r\nf 6594/6424/6433 6612/6442/6451 6613/6443/6452 6595/6425/6434\r\nf 6614/6444/6453 6615/6445/6454 6613/6443/6452 6612/6442/6451\r\nf 6616/6446/6455 6617/6447/6456 6613/6443/6452 6615/6445/6454\r\nf 6596/6426/6435 6595/6425/6434 6613/6443/6452 6617/6447/6456\r\nf 6596/6426/6435 6617/6447/6456 6618/6448/6457 6599/6429/6438\r\nf 6616/6446/6455 6619/6449/6458 6618/6448/6457 6617/6447/6456\r\nf 6620/6450/6459 6621/6451/6460 6618/6448/6457 6619/6449/6458\r\nf 6600/6430/6439 6599/6429/6438 6618/6448/6457 6621/6451/6460\r\nf 6604/6434/6443 6622/6452/6461 6623/6453/6462 6605/6435/6444\r\nf 6624/6454/6463 6625/6455/6464 6623/6453/6462 6622/6452/6461\r\nf 6626/6456/6465 6627/6457/6466 6623/6453/6462 6625/6455/6464\r\nf 6606/6436/6445 6605/6435/6444 6623/6453/6462 6627/6457/6466\r\nf 6606/6436/6445 6627/6457/6466 6628/6458/6467 6609/6439/6448\r\nf 6626/6456/6465 6629/6459/6468 6628/6458/6467 6627/6457/6466\r\nf 6630/6460/6469 6631/6461/6470 6628/6458/6467 6629/6459/6468\r\nf 6610/6440/6449 6609/6439/6448 6628/6458/6467 6631/6461/6470\r\nf 6614/6444/6453 6632/6462/6471 6633/6463/6472 6615/6445/6454\r\nf 6634/6464/6473 6635/6465/6474 6633/6463/6472 6632/6462/6471\r\nf 6636/6466/6475 6637/6467/6476 6633/6463/6472 6635/6465/6474\r\nf 6616/6446/6455 6615/6445/6454 6633/6463/6472 6637/6467/6476\r\nf 6616/6446/6455 6637/6467/6476 6638/6468/6477 6619/6449/6458\r\nf 6636/6466/6475 6639/6469/6478 6638/6468/6477 6637/6467/6476\r\nf 6640/6470/6479 6641/6471/6480 6638/6468/6477 6639/6469/6478\r\nf 6620/6450/6459 6619/6449/6458 6638/6468/6477 6641/6471/6480\r\nf 6624/6454/6463 6642/6472/6481 6643/6473/6482 6625/6455/6464\r\nf 6644/6474/6483 6645/6475/6484 6643/6473/6482 6642/6472/6481\r\nf 6646/6476/6485 6647/6477/6486 6643/6473/6482 6645/6475/6484\r\nf 6626/6456/6465 6625/6455/6464 6643/6473/6482 6647/6477/6486\r\nf 6626/6456/6465 6647/6477/6486 6648/6478/6487 6629/6459/6468\r\nf 6646/6476/6485 6649/6479/6488 6648/6478/6487 6647/6477/6486\r\nf 6650/6480/6489 6651/6481/6490 6648/6478/6487 6649/6479/6488\r\nf 6630/6460/6469 6629/6459/6468 6648/6478/6487 6651/6481/6490\r\nf 6634/6464/6473 6652/6482/6491 6653/6483/6492 6635/6465/6474\r\nf 6654/6484/6493 6655/6485/6494 6653/6483/6492 6652/6482/6491\r\nf 6656/6486/6495 6657/6487/6496 6653/6483/6492 6655/6485/6494\r\nf 6636/6466/6475 6635/6465/6474 6653/6483/6492 6657/6487/6496\r\nf 6636/6466/6475 6657/6487/6496 6658/6488/6497 6639/6469/6478\r\nf 6656/6486/6495 6659/6489/6498 6658/6488/6497 6657/6487/6496\r\nf 6660/6490/6499 6661/6491/6500 6658/6488/6497 6659/6489/6498\r\nf 6640/6470/6479 6639/6469/6478 6658/6488/6497 6661/6491/6500\r\nf 6644/6474/6483 6662/6492/6501 6663/6493/6502 6645/6475/6484\r\nf 6664/6494/6503 6665/6495/6504 6663/6493/6502 6662/6492/6501\r\nf 6666/6496/6505 6667/6497/6506 6663/6493/6502 6665/6495/6504\r\nf 6646/6476/6485 6645/6475/6484 6663/6493/6502 6667/6497/6506\r\nf 6646/6476/6485 6667/6497/6506 6668/6498/6507 6649/6479/6488\r\nf 6666/6496/6505 6669/6499/6508 6668/6498/6507 6667/6497/6506\r\nf 6670/6500/6509 6671/6501/6510 6668/6498/6507 6669/6499/6508\r\nf 6672/6502/6511 6673/6503/6512 6668/6498/6507 6671/6501/6510\r\nf 6650/6480/6489 6649/6479/6488 6668/6498/6507 6673/6503/6512\r\nf 6654/6484/6493 6674/6504/6513 6675/6505/6514 6655/6485/6494\r\nf 6676/6506/6515 6677/6507/6516 6675/6505/6514 6674/6504/6513\r\nf 6678/6508/6517 6679/6509/6518 6675/6505/6514 6677/6507/6516\r\nf 6656/6486/6495 6655/6485/6494 6675/6505/6514 6679/6509/6518\r\nf 6656/6486/6495 6679/6509/6518 6680/6510/6519 6659/6489/6498\r\nf 6678/6508/6517 6681/6511/6520 6680/6510/6519 6679/6509/6518\r\nf 6682/6512/6521 6683/6513/6522 6680/6510/6519 6681/6511/6520\r\nf 6660/6490/6499 6659/6489/6498 6680/6510/6519 6683/6513/6522\r\nf 6664/6494/6503 6684/6514/6523 6685/6515/6524 6665/6495/6504\r\nf 6686/6516/6525 6687/6517/6526 6685/6515/6524 6684/6514/6523\r\nf 6688/6518/6527 6689/6519/6528 6685/6515/6524 6687/6517/6526\r\nf 6666/6496/6505 6665/6495/6504 6685/6515/6524 6689/6519/6528\r\nf 6676/6506/6515 6690/6520/6529 6691/6521/6530 6677/6507/6516\r\nf 6692/6522/6531 6693/6523/6532 6691/6521/6530 6690/6520/6529\r\nf 6694/6524/6533 6695/6525/6534 6691/6521/6530 6693/6523/6532\r\nf 6678/6508/6517 6677/6507/6516 6691/6521/6530 6695/6525/6534\r\nf 6678/6508/6517 6695/6525/6534 6696/6526/6535 6681/6511/6520\r\nf 6694/6524/6533 6697/6527/6536 6696/6526/6535 6695/6525/6534\r\nf 6698/6528/6537 6699/6529/6538 6696/6526/6535 6697/6527/6536\r\nf 6682/6512/6521 6681/6511/6520 6696/6526/6535 6699/6529/6538\r\nf 6686/6516/6525 6700/6530/6539 6701/6531/6540 6687/6517/6526\r\nf 6702/6532/6541 6703/6533/6542 6701/6531/6540 6700/6530/6539\r\nf 6704/6534/6543 6705/6535/6544 6701/6531/6540 6703/6533/6542\r\nf 6688/6518/6527 6687/6517/6526 6701/6531/6540 6705/6535/6544\r\nf 6692/6522/6531 6706/6536/6545 6707/6537/6546 6693/6523/6532\r\nf 6708/6538/6547 6709/6539/6548 6707/6537/6546 6706/6536/6545\r\nf 6710/6540/6549 6711/6541/6550 6707/6537/6546 6709/6539/6548\r\nf 6694/6524/6533 6693/6523/6532 6707/6537/6546 6711/6541/6550\r\nf 6694/6524/6533 6711/6541/6550 6712/6542/6551 6697/6527/6536\r\nf 6710/6540/6549 6713/6543/6552 6712/6542/6551 6711/6541/6550\r\nf 6714/6544/6553 6715/6545/6554 6712/6542/6551 6713/6543/6552\r\nf 6698/6528/6537 6697/6527/6536 6712/6542/6551 6715/6545/6554\r\nf 6702/6532/6541 6716/6546/6555 6717/6547/6556 6703/6533/6542\r\nf 6718/6548/6557 6719/6549/6558 6717/6547/6556 6716/6546/6555\r\nf 6720/6550/6559 6721/6551/6560 6717/6547/6556 6719/6549/6558\r\nf 6704/6534/6543 6703/6533/6542 6717/6547/6556 6721/6551/6560\r\nf 6704/6534/6543 6721/6551/6560 6722/6552/6561 6723/6553/6562\r\nf 6720/6550/6559 6724/6554/6563 6722/6552/6561 6721/6551/6560\r\nf 6725/6555/6564 6726/6556/6565 6722/6552/6561 6724/6554/6563\r\nf 6727/6557/6566 6723/6553/6562 6722/6552/6561 6726/6556/6565\r\nf 6708/6538/6547 6728/6558/6567 6729/6559/6568 6709/6539/6548\r\nf 6730/6560/6569 6731/6561/6570 6729/6559/6568 6728/6558/6567\r\nf 6732/6562/6571 6733/6563/6572 6729/6559/6568 6731/6561/6570\r\nf 6710/6540/6549 6709/6539/6548 6729/6559/6568 6733/6563/6572\r\nf 6710/6540/6549 6733/6563/6572 6734/6564/6573 6713/6543/6552\r\nf 6732/6562/6571 6735/6565/6574 6734/6564/6573 6733/6563/6572\r\nf 6736/6566/6575 6737/6567/6576 6734/6564/6573 6735/6565/6574\r\nf 6714/6544/6553 6713/6543/6552 6734/6564/6573 6737/6567/6576\r\nf 6718/6548/6557 6738/6568/6577 6739/6569/6578 6719/6549/6558\r\nf 6740/6570/6579 6741/6571/6580 6739/6569/6578 6738/6568/6577\r\nf 6742/6572/6581 6743/6573/6582 6739/6569/6578 6741/6571/6580\r\nf 6720/6550/6559 6719/6549/6558 6739/6569/6578 6743/6573/6582\r\nf 6720/6550/6559 6743/6573/6582 6744/6574/6583 6724/6554/6563\r\nf 6742/6572/6581 6745/6575/6584 6744/6574/6583 6743/6573/6582\r\nf 6746/6576/6585 6747/6577/6586 6744/6574/6583 6745/6575/6584\r\nf 6725/6555/6564 6724/6554/6563 6744/6574/6583 6747/6577/6586\r\nf 6730/6560/6569 6748/6578/6587 6749/6579/6588 6731/6561/6570\r\nf 6750/6580/6589 6751/6581/6590 6749/6579/6588 6748/6578/6587\r\nf 6752/6582/6591 6753/6583/6592 6749/6579/6588 6751/6581/6590\r\nf 6732/6562/6571 6731/6561/6570 6749/6579/6588 6753/6583/6592\r\nf 6732/6562/6571 6753/6583/6592 6754/6584/6593 6735/6565/6574\r\nf 6752/6582/6591 6755/6585/6594 6754/6584/6593 6753/6583/6592\r\nf 6756/6586/6595 6757/6587/6596 6754/6584/6593 6755/6585/6594\r\nf 6736/6566/6575 6735/6565/6574 6754/6584/6593 6757/6587/6596\r\nf 6740/6570/6579 6758/6588/6597 6759/6589/6598 6741/6571/6580\r\nf 6760/6590/6599 6761/6591/6600 6759/6589/6598 6758/6588/6597\r\nf 6762/6592/6601 6763/6593/6602 6759/6589/6598 6761/6591/6600\r\nf 6742/6572/6581 6741/6571/6580 6759/6589/6598 6763/6593/6602\r\nf 6742/6572/6581 6763/6593/6602 6764/6594/6603 6745/6575/6584\r\nf 6762/6592/6601 6765/6595/6604 6764/6594/6603 6763/6593/6602\r\nf 6766/6596/6605 6767/6597/6606 6764/6594/6603 6765/6595/6604\r\nf 6746/6576/6585 6745/6575/6584 6764/6594/6603 6767/6597/6606\r\nf 6750/6580/6589 6768/6598/6607 6769/6599/6608 6751/6581/6590\r\nf 6770/6600/6609 6771/6601/6610 6769/6599/6608 6768/6598/6607\r\nf 6772/6602/6611 6773/6603/6612 6769/6599/6608 6771/6601/6610\r\nf 6752/6582/6591 6751/6581/6590 6769/6599/6608 6773/6603/6612\r\nf 6752/6582/6591 6773/6603/6612 6774/6604/6613 6755/6585/6594\r\nf 6772/6602/6611 6775/6605/6614 6774/6604/6613 6773/6603/6612\r\nf 6776/6606/6615 6777/6607/6616 6774/6604/6613 6775/6605/6614\r\nf 6756/6586/6595 6755/6585/6594 6774/6604/6613 6777/6607/6616\r\nf 6760/6590/6599 6778/6608/6617 6779/6609/6618 6761/6591/6600\r\nf 6780/6610/6619 6781/6611/6620 6779/6609/6618 6778/6608/6617\r\nf 6782/6612/6621 6783/6613/6622 6779/6609/6618 6781/6611/6620\r\nf 6762/6592/6601 6761/6591/6600 6779/6609/6618 6783/6613/6622\r\nf 6762/6592/6601 6783/6613/6622 6784/6614/6623 6765/6595/6604\r\nf 6782/6612/6621 6785/6615/6624 6784/6614/6623 6783/6613/6622\r\nf 6786/6616/6625 6787/6617/6626 6784/6614/6623 6785/6615/6624\r\nf 6766/6596/6605 6765/6595/6604 6784/6614/6623 6787/6617/6626\r\nf 6770/6600/6609 6788/6618/6627 6789/6619/6628 6771/6601/6610\r\nf 6790/6620/6629 6791/6621/6630 6789/6619/6628 6788/6618/6627\r\nf 6792/6622/6631 6793/6623/6632 6789/6619/6628 6791/6621/6630\r\nf 6772/6602/6611 6771/6601/6610 6789/6619/6628 6793/6623/6632\r\nf 6772/6602/6611 6793/6623/6632 6794/6624/6633 6775/6605/6614\r\nf 6792/6622/6631 6795/6625/6634 6794/6624/6633 6793/6623/6632\r\nf 6796/6626/6635 6797/6627/6636 6794/6624/6633 6795/6625/6634\r\nf 6776/6606/6615 6775/6605/6614 6794/6624/6633 6797/6627/6636\r\nf 6780/6610/6619 6798/6628/6637 6799/6629/6638 6781/6611/6620\r\nf 6800/6630/6639 6801/6631/6640 6799/6629/6638 6798/6628/6637\r\nf 6802/6632/6641 6803/6633/6642 6799/6629/6638 6801/6631/6640\r\nf 6782/6612/6621 6781/6611/6620 6799/6629/6638 6803/6633/6642\r\nf 6782/6612/6621 6803/6633/6642 6804/6634/6643 6785/6615/6624\r\nf 6802/6632/6641 6805/6635/6644 6804/6634/6643 6803/6633/6642\r\nf 6806/6636/6645 6807/6637/6646 6804/6634/6643 6805/6635/6644\r\nf 6786/6616/6625 6785/6615/6624 6804/6634/6643 6807/6637/6646\r\nf 6790/6620/6629 6808/6638/6647 6809/6639/6648 6791/6621/6630\r\nf 6810/6640/6649 6811/6641/6650 6809/6639/6648 6808/6638/6647\r\nf 6812/6642/6651 6813/6643/6652 6809/6639/6648 6811/6641/6650\r\nf 6792/6622/6631 6791/6621/6630 6809/6639/6648 6813/6643/6652\r\nf 6792/6622/6631 6813/6643/6652 6814/6644/6653 6795/6625/6634\r\nf 6812/6642/6651 6815/6645/6654 6814/6644/6653 6813/6643/6652\r\nf 6816/6646/6655 6817/6647/6656 6814/6644/6653 6815/6645/6654\r\nf 6796/6626/6635 6795/6625/6634 6814/6644/6653 6817/6647/6656\r\nf 6800/6630/6639 6818/6648/6657 6819/6649/6658 6801/6631/6640\r\nf 6820/6650/6659 6821/6651/6660 6819/6649/6658 6818/6648/6657\r\nf 6822/6652/6661 6823/6653/6662 6819/6649/6658 6821/6651/6660\r\nf 6802/6632/6641 6801/6631/6640 6819/6649/6658 6823/6653/6662\r\nf 6802/6632/6641 6823/6653/6662 6824/6654/6663 6805/6635/6644\r\nf 6822/6652/6661 6825/6655/6664 6824/6654/6663 6823/6653/6662\r\nf 6826/6656/6665 6827/6657/6666 6824/6654/6663 6825/6655/6664\r\nf 6806/6636/6645 6805/6635/6644 6824/6654/6663 6827/6657/6666\r\nf 6810/6640/6649 6828/6658/6667 6829/6659/6668 6811/6641/6650\r\nf 6830/6660/6669 6831/6661/6670 6829/6659/6668 6828/6658/6667\r\nf 6832/6662/6671 6833/6663/6672 6829/6659/6668 6831/6661/6670\r\nf 6812/6642/6651 6811/6641/6650 6829/6659/6668 6833/6663/6672\r\nf 6812/6642/6651 6833/6663/6672 6834/6664/6673 6815/6645/6654\r\nf 6832/6662/6671 6835/6665/6674 6834/6664/6673 6833/6663/6672\r\nf 6836/6666/6675 6837/6667/6676 6834/6664/6673 6835/6665/6674\r\nf 6816/6646/6655 6815/6645/6654 6834/6664/6673 6837/6667/6676\r\nf 6820/6650/6659 6838/6668/6677 6839/6669/6678 6821/6651/6660\r\nf 6840/6670/6679 6841/6671/6680 6839/6669/6678 6838/6668/6677\r\nf 6842/6672/6681 6843/6673/6682 6839/6669/6678 6841/6671/6680\r\nf 6822/6652/6661 6821/6651/6660 6839/6669/6678 6843/6673/6682\r\nf 6822/6652/6661 6843/6673/6682 6844/6674/6683 6825/6655/6664\r\nf 6842/6672/6681 6845/6675/6684 6844/6674/6683 6843/6673/6682\r\nf 6846/6676/6685 6847/6677/6686 6844/6674/6683 6845/6675/6684\r\nf 6826/6656/6665 6825/6655/6664 6844/6674/6683 6847/6677/6686\r\nf 6830/6660/6669 6848/6678/6687 6849/6679/6688 6831/6661/6670\r\nf 6850/6680/6689 6851/6681/6690 6849/6679/6688 6848/6678/6687\r\nf 6852/6682/6691 6853/6683/6692 6849/6679/6688 6851/6681/6690\r\nf 6832/6662/6671 6831/6661/6670 6849/6679/6688 6853/6683/6692\r\nf 6832/6662/6671 6853/6683/6692 6854/6684/6693 6835/6665/6674\r\nf 6852/6682/6691 6855/6685/6694 6854/6684/6693 6853/6683/6692\r\nf 6856/6686/6695 6857/6687/6696 6854/6684/6693 6855/6685/6694\r\nf 6836/6666/6675 6835/6665/6674 6854/6684/6693 6857/6687/6696\r\nf 6840/6670/6679 6858/6688/6697 6859/6689/6698 6841/6671/6680\r\nf 6860/6690/6699 6861/6691/6700 6859/6689/6698 6858/6688/6697\r\nf 6862/6692/6701 6863/6693/6702 6859/6689/6698 6861/6691/6700\r\nf 6842/6672/6681 6841/6671/6680 6859/6689/6698 6863/6693/6702\r\nf 6842/6672/6681 6863/6693/6702 6864/6694/6703 6845/6675/6684\r\nf 6862/6692/6701 6865/6695/6704 6864/6694/6703 6863/6693/6702\r\nf 6866/6696/6705 6867/6697/6706 6864/6694/6703 6865/6695/6704\r\nf 6846/6676/6685 6845/6675/6684 6864/6694/6703 6867/6697/6706\r\nf 6850/6680/6689 6868/6698/6707 6869/6699/6708 6851/6681/6690\r\nf 6870/6700/6709 6871/6701/6710 6869/6699/6708 6868/6698/6707\r\nf 6872/6702/6711 6873/6703/6712 6869/6699/6708 6871/6701/6710\r\nf 6852/6682/6691 6851/6681/6690 6869/6699/6708 6873/6703/6712\r\nf 6852/6682/6691 6873/6703/6712 6874/6704/6713 6855/6685/6694\r\nf 6872/6702/6711 6875/6705/6714 6874/6704/6713 6873/6703/6712\r\nf 6876/6706/6715 6877/6707/6716 6874/6704/6713 6875/6705/6714\r\nf 6856/6686/6695 6855/6685/6694 6874/6704/6713 6877/6707/6716\r\nf 6860/6690/6699 6878/6708/6717 6879/6709/6718 6861/6691/6700\r\nf 6880/6710/6719 6881/6711/6720 6879/6709/6718 6878/6708/6717\r\nf 6882/6712/6721 6883/6713/6722 6879/6709/6718 6881/6711/6720\r\nf 6862/6692/6701 6861/6691/6700 6879/6709/6718 6883/6713/6722\r\nf 6862/6692/6701 6883/6713/6722 6884/6714/6723 6865/6695/6704\r\nf 6882/6712/6721 6885/6715/6724 6884/6714/6723 6883/6713/6722\r\nf 6886/6716/6725 6887/6717/6726 6884/6714/6723 6885/6715/6724\r\nf 6866/6696/6705 6865/6695/6704 6884/6714/6723 6887/6717/6726\r\nf 6870/6700/6709 6888/6718/6727 6889/6719/6728 6871/6701/6710\r\nf 6890/6720/6729 6891/6721/6730 6889/6719/6728 6888/6718/6727\r\nf 6892/6722/6731 6893/6723/6732 6889/6719/6728 6891/6721/6730\r\nf 6872/6702/6711 6871/6701/6710 6889/6719/6728 6893/6723/6732\r\nf 6872/6702/6711 6893/6723/6732 6894/6724/6733 6875/6705/6714\r\nf 6892/6722/6731 6895/6725/6734 6894/6724/6733 6893/6723/6732\r\nf 6896/6726/6735 6897/6727/6736 6894/6724/6733 6895/6725/6734\r\nf 6876/6706/6715 6875/6705/6714 6894/6724/6733 6897/6727/6736\r\nf 6880/6710/6719 6898/6728/6737 6899/6729/6738 6881/6711/6720\r\nf 6900/6730/6739 6901/6731/6740 6899/6729/6738 6898/6728/6737\r\nf 6902/6732/6741 6903/6733/6742 6899/6729/6738 6901/6731/6740\r\nf 6882/6712/6721 6881/6711/6720 6899/6729/6738 6903/6733/6742\r\nf 6882/6712/6721 6903/6733/6742 6904/6734/6743 6885/6715/6724\r\nf 6902/6732/6741 6905/6735/6744 6904/6734/6743 6903/6733/6742\r\nf 6906/6736/6745 6907/6737/6746 6904/6734/6743 6905/6735/6744\r\nf 6886/6716/6725 6885/6715/6724 6904/6734/6743 6907/6737/6746\r\nf 6890/6720/6729 6908/6738/6747 6909/6739/6748 6891/6721/6730\r\nf 6910/6740/6749 6911/6741/6750 6909/6739/6748 6908/6738/6747\r\nf 6912/6742/6751 6913/6743/6752 6909/6739/6748 6911/6741/6750\r\nf 6892/6722/6731 6891/6721/6730 6909/6739/6748 6913/6743/6752\r\nf 6892/6722/6731 6913/6743/6752 6914/6744/6753 6895/6725/6734\r\nf 6912/6742/6751 6915/6745/6754 6914/6744/6753 6913/6743/6752\r\nf 6916/6746/6755 6917/6747/6756 6914/6744/6753 6915/6745/6754\r\nf 6896/6726/6735 6895/6725/6734 6914/6744/6753 6917/6747/6756\r\nf 6900/6730/6739 6918/6748/6757 6919/6749/6758 6901/6731/6740\r\nf 6920/6750/6759 6921/6751/6760 6919/6749/6758 6918/6748/6757\r\nf 6922/6752/6761 6923/6753/6762 6919/6749/6758 6921/6751/6760\r\nf 6902/6732/6741 6901/6731/6740 6919/6749/6758 6923/6753/6762\r\nf 6902/6732/6741 6923/6753/6762 6924/6754/6763 6905/6735/6744\r\nf 6922/6752/6761 6925/6755/6764 6924/6754/6763 6923/6753/6762\r\nf 6926/6756/6765 6927/6757/6766 6924/6754/6763 6925/6755/6764\r\nf 6906/6736/6745 6905/6735/6744 6924/6754/6763 6927/6757/6766\r\nf 6910/6740/6749 6928/6758/6767 6929/6759/6768 6911/6741/6750\r\nf 6930/6760/6769 6931/6761/6770 6929/6759/6768 6928/6758/6767\r\nf 6932/6762/6771 6933/6763/6772 6929/6759/6768 6931/6761/6770\r\nf 6912/6742/6751 6911/6741/6750 6929/6759/6768 6933/6763/6772\r\nf 6912/6742/6751 6933/6763/6772 6934/6764/6773 6915/6745/6754\r\nf 6932/6762/6771 6935/6765/6774 6934/6764/6773 6933/6763/6772\r\nf 6936/6766/6775 6937/6767/6776 6934/6764/6773 6935/6765/6774\r\nf 6916/6746/6755 6915/6745/6754 6934/6764/6773 6937/6767/6776\r\nf 6920/6750/6759 6938/6768/6777 6939/6769/6778 6921/6751/6760\r\nf 6940/6770/6779 6941/6771/6780 6939/6769/6778 6938/6768/6777\r\nf 6942/6772/6781 6943/6773/6782 6939/6769/6778 6941/6771/6780\r\nf 6922/6752/6761 6921/6751/6760 6939/6769/6778 6943/6773/6782\r\nf 6922/6752/6761 6943/6773/6782 6944/6774/6783 6925/6755/6764\r\nf 6942/6772/6781 6945/6775/6784 6944/6774/6783 6943/6773/6782\r\nf 6946/6776/6785 6947/6777/6786 6944/6774/6783 6945/6775/6784\r\nf 6926/6756/6765 6925/6755/6764 6944/6774/6783 6947/6777/6786\r\nf 6930/6760/6769 6948/6778/6787 6949/6779/6788 6931/6761/6770\r\nf 6950/6780/6789 6951/6781/6790 6949/6779/6788 6948/6778/6787\r\nf 6952/6782/6791 6953/6783/6792 6949/6779/6788 6951/6781/6790\r\nf 6932/6762/6771 6931/6761/6770 6949/6779/6788 6953/6783/6792\r\nf 6932/6762/6771 6953/6783/6792 6954/6784/6793 6935/6765/6774\r\nf 6952/6782/6791 6955/6785/6794 6954/6784/6793 6953/6783/6792\r\nf 6956/6786/6795 6957/6787/6796 6954/6784/6793 6955/6785/6794\r\nf 6936/6766/6775 6935/6765/6774 6954/6784/6793 6957/6787/6796\r\nf 6940/6770/6779 6958/6788/6797 6959/6789/6798 6941/6771/6780\r\nf 6960/6790/6799 6961/6791/6800 6959/6789/6798 6958/6788/6797\r\nf 6962/6792/6801 6963/6793/6802 6959/6789/6798 6961/6791/6800\r\nf 6942/6772/6781 6941/6771/6780 6959/6789/6798 6963/6793/6802\r\nf 6942/6772/6781 6963/6793/6802 6964/6794/6803 6945/6775/6784\r\nf 6962/6792/6801 6965/6795/6804 6964/6794/6803 6963/6793/6802\r\nf 6966/6796/6805 6967/6797/6806 6964/6794/6803 6965/6795/6804\r\nf 6946/6776/6785 6945/6775/6784 6964/6794/6803 6967/6797/6806\r\nf 6950/6780/6789 6968/6798/6807 6969/6799/6808 6951/6781/6790\r\nf 6970/6800/6809 6971/6801/6810 6969/6799/6808 6968/6798/6807\r\nf 6972/6802/6811 6973/6803/6812 6969/6799/6808 6971/6801/6810\r\nf 6952/6782/6791 6951/6781/6790 6969/6799/6808 6973/6803/6812\r\nf 6952/6782/6791 6973/6803/6812 6974/6804/6813 6955/6785/6794\r\nf 6972/6802/6811 6975/6805/6814 6974/6804/6813 6973/6803/6812\r\nf 6976/6806/6815 6977/6807/6816 6974/6804/6813 6975/6805/6814\r\nf 6956/6786/6795 6955/6785/6794 6974/6804/6813 6977/6807/6816\r\nf 6960/6790/6799 6978/6808/6817 6979/6809/6818 6961/6791/6800\r\nf 6980/6810/6819 6981/6811/6820 6979/6809/6818 6978/6808/6817\r\nf 6982/6812/6821 6983/6813/6822 6979/6809/6818 6981/6811/6820\r\nf 6962/6792/6801 6961/6791/6800 6979/6809/6818 6983/6813/6822\r\nf 6962/6792/6801 6983/6813/6822 6984/6814/6823 6965/6795/6804\r\nf 6982/6812/6821 6985/6815/6824 6984/6814/6823 6983/6813/6822\r\nf 6986/6816/6825 6987/6817/6826 6984/6814/6823 6985/6815/6824\r\nf 6966/6796/6805 6965/6795/6804 6984/6814/6823 6987/6817/6826\r\nf 6970/6800/6809 6988/6818/6827 6989/6819/6828 6971/6801/6810\r\nf 6990/6820/6829 6991/6821/6830 6989/6819/6828 6988/6818/6827\r\nf 6992/6822/6831 6993/6823/6832 6989/6819/6828 6991/6821/6830\r\nf 6972/6802/6811 6971/6801/6810 6989/6819/6828 6993/6823/6832\r\nf 6972/6802/6811 6993/6823/6832 6994/6824/6833 6975/6805/6814\r\nf 6992/6822/6831 6995/6825/6834 6994/6824/6833 6993/6823/6832\r\nf 6996/6826/6835 6997/6827/6836 6994/6824/6833 6995/6825/6834\r\nf 6976/6806/6815 6975/6805/6814 6994/6824/6833 6997/6827/6836\r\nf 6980/6810/6819 6998/6828/6837 6999/6829/6838 6981/6811/6820\r\nf 7000/6830/6839 7001/6831/6840 6999/6829/6838 6998/6828/6837\r\nf 7002/6832/6841 7003/6833/6842 6999/6829/6838 7001/6831/6840\r\nf 6982/6812/6821 6981/6811/6820 6999/6829/6838 7003/6833/6842\r\nf 6982/6812/6821 7003/6833/6842 7004/6834/6843 6985/6815/6824\r\nf 7002/6832/6841 7005/6835/6844 7004/6834/6843 7003/6833/6842\r\nf 7006/6836/6845 7007/6837/6846 7004/6834/6843 7005/6835/6844\r\nf 6986/6816/6825 6985/6815/6824 7004/6834/6843 7007/6837/6846\r\nf 6990/6820/6829 7008/6838/6847 7009/6839/6848 6991/6821/6830\r\nf 7010/6840/6849 7011/6841/6850 7009/6839/6848 7008/6838/6847\r\nf 7012/6842/6851 7013/6843/6852 7009/6839/6848 7011/6841/6850\r\nf 6992/6822/6831 6991/6821/6830 7009/6839/6848 7013/6843/6852\r\nf 6992/6822/6831 7013/6843/6852 7014/6844/6853 6995/6825/6834\r\nf 7012/6842/6851 7015/6845/6854 7014/6844/6853 7013/6843/6852\r\nf 7016/6846/6855 7017/6847/6856 7014/6844/6853 7015/6845/6854\r\nf 6996/6826/6835 6995/6825/6834 7014/6844/6853 7017/6847/6856\r\nf 7000/6830/6839 7018/6848/6857 7019/6849/6858 7001/6831/6840\r\nf 7020/6850/6859 7021/6851/6860 7019/6849/6858 7018/6848/6857\r\nf 7022/6852/6861 7023/6853/6862 7019/6849/6858 7021/6851/6860\r\nf 7002/6832/6841 7001/6831/6840 7019/6849/6858 7023/6853/6862\r\nf 7002/6832/6841 7023/6853/6862 7024/6854/6863 7005/6835/6844\r\nf 7022/6852/6861 7025/6855/6864 7024/6854/6863 7023/6853/6862\r\nf 7026/6856/6865 7027/6857/6866 7024/6854/6863 7025/6855/6864\r\nf 7006/6836/6845 7005/6835/6844 7024/6854/6863 7027/6857/6866\r\nf 7010/6840/6849 7028/6858/6867 7029/6859/6868 7011/6841/6850\r\nf 7030/6860/6869 7031/6861/6870 7029/6859/6868 7028/6858/6867\r\nf 7032/6862/6871 7033/6863/6872 7029/6859/6868 7031/6861/6870\r\nf 7012/6842/6851 7011/6841/6850 7029/6859/6868 7033/6863/6872\r\nf 7012/6842/6851 7033/6863/6872 7034/6864/6873 7015/6845/6854\r\nf 7032/6862/6871 7035/6865/6874 7034/6864/6873 7033/6863/6872\r\nf 7036/6866/6875 7037/6867/6876 7034/6864/6873 7035/6865/6874\r\nf 7016/6846/6855 7015/6845/6854 7034/6864/6873 7037/6867/6876\r\nf 7020/6850/6859 7038/6868/6877 7039/6869/6878 7021/6851/6860\r\nf 7040/6870/6879 7041/6871/6880 7039/6869/6878 7038/6868/6877\r\nf 7042/6872/6881 7043/6873/6882 7039/6869/6878 7041/6871/6880\r\nf 7022/6852/6861 7021/6851/6860 7039/6869/6878 7043/6873/6882\r\nf 7022/6852/6861 7043/6873/6882 7044/6874/6883 7025/6855/6864\r\nf 7042/6872/6881 7045/6875/6884 7044/6874/6883 7043/6873/6882\r\nf 7046/6876/6885 7047/6877/6886 7044/6874/6883 7045/6875/6884\r\nf 7026/6856/6865 7025/6855/6864 7044/6874/6883 7047/6877/6886\r\nf 7030/6860/6869 7048/6878/6887 7049/6879/6888 7031/6861/6870\r\nf 7050/6880/6889 7051/6881/6890 7049/6879/6888 7048/6878/6887\r\nf 7052/6882/6891 7053/6883/6892 7049/6879/6888 7051/6881/6890\r\nf 7032/6862/6871 7031/6861/6870 7049/6879/6888 7053/6883/6892\r\nf 7032/6862/6871 7053/6883/6892 7054/6884/6893 7035/6865/6874\r\nf 7052/6882/6891 7055/6885/6894 7054/6884/6893 7053/6883/6892\r\nf 7056/6886/6895 7057/6887/6896 7054/6884/6893 7055/6885/6894\r\nf 7036/6866/6875 7035/6865/6874 7054/6884/6893 7057/6887/6896\r\nf 7040/6870/6879 7058/6888/6897 7059/6889/6898 7041/6871/6880\r\nf 7060/6890/6899 7061/6891/6900 7059/6889/6898 7058/6888/6897\r\nf 7062/6892/6901 7063/6893/6902 7059/6889/6898 7061/6891/6900\r\nf 7042/6872/6881 7041/6871/6880 7059/6889/6898 7063/6893/6902\r\nf 7042/6872/6881 7063/6893/6902 7064/6894/6903 7045/6875/6884\r\nf 7062/6892/6901 7065/6895/6904 7064/6894/6903 7063/6893/6902\r\nf 7066/6896/6905 7067/6897/6906 7064/6894/6903 7065/6895/6904\r\nf 7046/6876/6885 7045/6875/6884 7064/6894/6903 7067/6897/6906\r\nf 7050/6880/6889 7068/6898/6907 7069/6899/6908 7051/6881/6890\r\nf 7070/6900/6909 7071/6901/6910 7069/6899/6908 7068/6898/6907\r\nf 7072/6902/6911 7073/6903/6912 7069/6899/6908 7071/6901/6910\r\nf 7052/6882/6891 7051/6881/6890 7069/6899/6908 7073/6903/6912\r\nf 7052/6882/6891 7073/6903/6912 7074/6904/6913 7055/6885/6894\r\nf 7072/6902/6911 7075/6905/6914 7074/6904/6913 7073/6903/6912\r\nf 7076/6906/6915 7077/6907/6916 7074/6904/6913 7075/6905/6914\r\nf 7056/6886/6895 7055/6885/6894 7074/6904/6913 7077/6907/6916\r\nf 7060/6890/6899 7078/6908/6917 7079/6909/6918 7061/6891/6900\r\nf 7080/6910/6919 7081/6911/6920 7079/6909/6918 7078/6908/6917\r\nf 7082/6912/6921 7083/6913/6922 7079/6909/6918 7081/6911/6920\r\nf 7062/6892/6901 7061/6891/6900 7079/6909/6918 7083/6913/6922\r\nf 7062/6892/6901 7083/6913/6922 7084/6914/6923 7065/6895/6904\r\nf 7082/6912/6921 7085/6915/6924 7084/6914/6923 7083/6913/6922\r\nf 7086/6916/6925 7087/6917/6926 7084/6914/6923 7085/6915/6924\r\nf 7066/6896/6905 7065/6895/6904 7084/6914/6923 7087/6917/6926\r\nf 7070/6900/6909 7088/6918/6927 7089/6919/6928 7071/6901/6910\r\nf 7090/6920/6929 7091/6921/6930 7089/6919/6928 7088/6918/6927\r\nf 7092/6922/6931 7093/6923/6932 7089/6919/6928 7091/6921/6930\r\nf 7072/6902/6911 7071/6901/6910 7089/6919/6928 7093/6923/6932\r\nf 7072/6902/6911 7093/6923/6932 7094/6924/6933 7075/6905/6914\r\nf 7092/6922/6931 7095/6925/6934 7094/6924/6933 7093/6923/6932\r\nf 7096/6926/6935 7097/6927/6936 7094/6924/6933 7095/6925/6934\r\nf 7076/6906/6915 7075/6905/6914 7094/6924/6933 7097/6927/6936\r\nf 7080/6910/6919 7098/6928/6937 7099/6929/6938 7081/6911/6920\r\nf 7100/6930/6939 7101/6931/6940 7099/6929/6938 7098/6928/6937\r\nf 7102/6932/6941 7103/6933/6942 7099/6929/6938 7101/6931/6940\r\nf 7082/6912/6921 7081/6911/6920 7099/6929/6938 7103/6933/6942\r\nf 7082/6912/6921 7103/6933/6942 7104/6934/6943 7085/6915/6924\r\nf 7102/6932/6941 7105/6935/6944 7104/6934/6943 7103/6933/6942\r\nf 7106/6936/6945 7107/6937/6946 7104/6934/6943 7105/6935/6944\r\nf 7086/6916/6925 7085/6915/6924 7104/6934/6943 7107/6937/6946\r\nf 7090/6920/6929 7108/6938/6947 7109/6939/6948 7091/6921/6930\r\nf 7110/6940/6949 7111/6941/6950 7109/6939/6948 7108/6938/6947\r\nf 7112/6942/6951 7113/6943/6952 7109/6939/6948 7111/6941/6950\r\nf 7092/6922/6931 7091/6921/6930 7109/6939/6948 7113/6943/6952\r\nf 7092/6922/6931 7113/6943/6952 7114/6944/6953 7095/6925/6934\r\nf 7112/6942/6951 7115/6945/6954 7114/6944/6953 7113/6943/6952\r\nf 7116/6946/6955 7117/6947/6956 7114/6944/6953 7115/6945/6954\r\nf 7096/6926/6935 7095/6925/6934 7114/6944/6953 7117/6947/6956\r\nf 7100/6930/6939 7118/6948/6957 7119/6949/6958 7101/6931/6940\r\nf 7120/6950/6959 7121/6951/6960 7119/6949/6958 7118/6948/6957\r\nf 7122/6952/6961 7123/6953/6962 7119/6949/6958 7121/6951/6960\r\nf 7102/6932/6941 7101/6931/6940 7119/6949/6958 7123/6953/6962\r\nf 7102/6932/6941 7123/6953/6962 7124/6954/6963 7105/6935/6944\r\nf 7122/6952/6961 7125/6955/6964 7124/6954/6963 7123/6953/6962\r\nf 7126/6956/6965 7127/6957/6966 7124/6954/6963 7125/6955/6964\r\nf 7106/6936/6945 7105/6935/6944 7124/6954/6963 7127/6957/6966\r\nf 7110/6940/6949 7128/6958/6967 7129/6959/6968 7111/6941/6950\r\nf 7130/6960/6969 7131/6961/6970 7129/6959/6968 7128/6958/6967\r\nf 7132/6962/6971 7133/6963/6972 7129/6959/6968 7131/6961/6970\r\nf 7112/6942/6951 7111/6941/6950 7129/6959/6968 7133/6963/6972\r\nf 7112/6942/6951 7133/6963/6972 7134/6964/6973 7115/6945/6954\r\nf 7132/6962/6971 7135/6965/6974 7134/6964/6973 7133/6963/6972\r\nf 7136/6966/6975 7137/6967/6976 7134/6964/6973 7135/6965/6974\r\nf 7116/6946/6955 7115/6945/6954 7134/6964/6973 7137/6967/6976\r\nf 7120/6950/6959 7138/6968/6977 7139/6969/6978 7121/6951/6960\r\nf 7140/6970/6979 7141/6971/6980 7139/6969/6978 7138/6968/6977\r\nf 7142/6972/6981 7143/6973/6982 7139/6969/6978 7141/6971/6980\r\nf 7122/6952/6961 7121/6951/6960 7139/6969/6978 7143/6973/6982\r\nf 7122/6952/6961 7143/6973/6982 7144/6974/6983 7125/6955/6964\r\nf 7142/6972/6981 7145/6975/6984 7144/6974/6983 7143/6973/6982\r\nf 7146/6976/6985 7147/6977/6986 7144/6974/6983 7145/6975/6984\r\nf 7126/6956/6965 7125/6955/6964 7144/6974/6983 7147/6977/6986\r\nf 7130/6960/6969 7148/6978/6987 7149/6979/6988 7131/6961/6970\r\nf 7150/6980/6989 7151/6981/6990 7149/6979/6988 7148/6978/6987\r\nf 7152/6982/6991 7153/6983/6992 7149/6979/6988 7151/6981/6990\r\nf 7132/6962/6971 7131/6961/6970 7149/6979/6988 7153/6983/6992\r\nf 7132/6962/6971 7153/6983/6992 7154/6984/6993 7135/6965/6974\r\nf 7152/6982/6991 7155/6985/6994 7154/6984/6993 7153/6983/6992\r\nf 7156/6986/6995 7157/6987/6996 7154/6984/6993 7155/6985/6994\r\nf 7136/6966/6975 7135/6965/6974 7154/6984/6993 7157/6987/6996\r\nf 7140/6970/6979 7158/6988/6997 7159/6989/6998 7141/6971/6980\r\nf 7160/6990/6999 7161/6991/7000 7159/6989/6998 7158/6988/6997\r\nf 7162/6992/7001 7163/6993/7002 7159/6989/6998 7161/6991/7000\r\nf 7142/6972/6981 7141/6971/6980 7159/6989/6998 7163/6993/7002\r\nf 7142/6972/6981 7163/6993/7002 7164/6994/7003 7145/6975/6984\r\nf 7162/6992/7001 7165/6995/7004 7164/6994/7003 7163/6993/7002\r\nf 7166/6996/7005 7167/6997/7006 7164/6994/7003 7165/6995/7004\r\nf 7146/6976/6985 7145/6975/6984 7164/6994/7003 7167/6997/7006\r\nf 7150/6980/6989 7168/6998/7007 7169/6999/7008 7151/6981/6990\r\nf 7170/7000/7009 7171/7001/7010 7169/6999/7008 7168/6998/7007\r\nf 7172/7002/7011 7173/7003/7012 7169/6999/7008 7171/7001/7010\r\nf 7152/6982/6991 7151/6981/6990 7169/6999/7008 7173/7003/7012\r\nf 7152/6982/6991 7173/7003/7012 7174/7004/7013 7155/6985/6994\r\nf 7172/7002/7011 7175/7005/7014 7174/7004/7013 7173/7003/7012\r\nf 7176/7006/7015 7177/7007/7016 7174/7004/7013 7175/7005/7014\r\nf 7156/6986/6995 7155/6985/6994 7174/7004/7013 7177/7007/7016\r\nf 7160/6990/6999 7178/7008/7017 7179/7009/7018 7161/6991/7000\r\nf 7180/7010/7019 7181/7011/7020 7179/7009/7018 7178/7008/7017\r\nf 7182/7012/7021 7183/7013/7022 7179/7009/7018 7181/7011/7020\r\nf 7162/6992/7001 7161/6991/7000 7179/7009/7018 7183/7013/7022\r\nf 7162/6992/7001 7183/7013/7022 7184/7014/7023 7165/6995/7004\r\nf 7182/7012/7021 7185/7015/7024 7184/7014/7023 7183/7013/7022\r\nf 7186/7016/7025 7187/7017/7026 7184/7014/7023 7185/7015/7024\r\nf 7166/6996/7005 7165/6995/7004 7184/7014/7023 7187/7017/7026\r\nf 7170/7000/7009 7188/7018/7027 7189/7019/7028 7171/7001/7010\r\nf 7190/7020/7029 7191/7021/7030 7189/7019/7028 7188/7018/7027\r\nf 7192/7022/7031 7193/7023/7032 7189/7019/7028 7191/7021/7030\r\nf 7172/7002/7011 7171/7001/7010 7189/7019/7028 7193/7023/7032\r\nf 7172/7002/7011 7193/7023/7032 7194/7024/7033 7175/7005/7014\r\nf 7192/7022/7031 7195/7025/7034 7194/7024/7033 7193/7023/7032\r\nf 7196/7026/7035 7197/7027/7036 7194/7024/7033 7195/7025/7034\r\nf 7176/7006/7015 7175/7005/7014 7194/7024/7033 7197/7027/7036\r\nf 7180/7010/7019 7198/7028/7037 7199/7029/7038 7181/7011/7020\r\nf 7200/7030/7039 7201/7031/7040 7199/7029/7038 7198/7028/7037\r\nf 7202/7032/7041 7203/7033/7042 7199/7029/7038 7201/7031/7040\r\nf 7182/7012/7021 7181/7011/7020 7199/7029/7038 7203/7033/7042\r\nf 7182/7012/7021 7203/7033/7042 7204/7034/7043 7185/7015/7024\r\nf 7202/7032/7041 7205/7035/7044 7204/7034/7043 7203/7033/7042\r\nf 7206/7036/7045 7207/7037/7046 7204/7034/7043 7205/7035/7044\r\nf 7186/7016/7025 7185/7015/7024 7204/7034/7043 7207/7037/7046\r\nf 7190/7020/7029 7208/7038/7047 7209/7039/7048 7191/7021/7030\r\nf 7210/7040/7049 7211/7041/7050 7209/7039/7048 7208/7038/7047\r\nf 7212/7042/7051 7213/7043/7052 7209/7039/7048 7211/7041/7050\r\nf 7192/7022/7031 7191/7021/7030 7209/7039/7048 7213/7043/7052\r\nf 7192/7022/7031 7213/7043/7052 7214/7044/7053 7195/7025/7034\r\nf 7212/7042/7051 7215/7045/7054 7214/7044/7053 7213/7043/7052\r\nf 7216/7046/7055 7217/7047/7056 7214/7044/7053 7215/7045/7054\r\nf 7196/7026/7035 7195/7025/7034 7214/7044/7053 7217/7047/7056\r\nf 7200/7030/7039 7218/7048/7057 7219/7049/7058 7201/7031/7040\r\nf 7220/7050/7059 7221/7051/7060 7219/7049/7058 7218/7048/7057\r\nf 7222/7052/7061 7223/7053/7062 7219/7049/7058 7221/7051/7060\r\nf 7202/7032/7041 7201/7031/7040 7219/7049/7058 7223/7053/7062\r\nf 7202/7032/7041 7223/7053/7062 7224/7054/7063 7205/7035/7044\r\nf 7222/7052/7061 7225/7055/7064 7224/7054/7063 7223/7053/7062\r\nf 7226/7056/7065 7227/7057/7045 7224/7054/7063 7225/7055/7064\r\nf 7206/7036/7045 7205/7035/7044 7224/7054/7063 7227/7057/7045\r\nf 7210/7040/7049 7228/7058/7066 7229/7059/7067 7211/7041/7050\r\nf 7230/7060/7068 7231/7061/7069 7229/7059/7067 7228/7058/7066\r\nf 7232/7062/7070 7233/7063/7071 7229/7059/7067 7231/7061/7069\r\nf 7212/7042/7051 7211/7041/7050 7229/7059/7067 7233/7063/7071\r\nf 7212/7042/7051 7233/7063/7071 7234/7064/7072 7215/7045/7054\r\nf 7232/7062/7070 7235/7065/7073 7234/7064/7072 7233/7063/7071\r\nf 7236/7066/7074 7237/7067/7055 7234/7064/7072 7235/7065/7073\r\nf 7216/7046/7055 7215/7045/7054 7234/7064/7072 7237/7067/7055\r\nf 7220/7050/7059 7238/7068/7075 7239/7069/7076 7221/7051/7060\r\nf 7240/7070/7077 7241/7071/7078 7239/7069/7076 7238/7068/7075\r\nf 7242/7072/7079 7243/7073/7080 7239/7069/7076 7241/7071/7078\r\nf 7222/7052/7061 7221/7051/7060 7239/7069/7076 7243/7073/7080\r\nf 7222/7052/7061 7243/7073/7080 7244/7074/7081 7225/7055/7064\r\nf 7242/7072/7079 7245/7075/7082 7244/7074/7081 7243/7073/7080\r\nf 7246/7076/7083 7247/7077/7084 7244/7074/7081 7245/7075/7082\r\nf 7226/7056/7065 7225/7055/7064 7244/7074/7081 7247/7077/7084\r\nf 7230/7060/7068 7248/7078/7085 7249/7079/7086 7231/7061/7069\r\nf 7250/7080/7087 7251/7081/7088 7249/7079/7086 7248/7078/7085\r\nf 7252/7082/7089 7253/7083/7090 7249/7079/7086 7251/7081/7088\r\nf 7232/7062/7070 7231/7061/7069 7249/7079/7086 7253/7083/7090\r\nf 7232/7062/7070 7253/7083/7090 7254/7084/7091 7235/7065/7073\r\nf 7252/7082/7089 7255/7085/7092 7254/7084/7091 7253/7083/7090\r\nf 7256/7086/7093 7257/7087/7094 7254/7084/7091 7255/7085/7092\r\nf 7236/7066/7074 7235/7065/7073 7254/7084/7091 7257/7087/7094\r\nf 7240/7070/7077 7258/7088/7095 7259/7089/7096 7241/7071/7078\r\nf 7260/7090/7097 7261/7091/7098 7259/7089/7096 7258/7088/7095\r\nf 7262/7092/7099 7263/7093/7100 7259/7089/7096 7261/7091/7098\r\nf 7242/7072/7079 7241/7071/7078 7259/7089/7096 7263/7093/7100\r\nf 7242/7072/7079 7263/7093/7100 7264/7094/7101 7245/7075/7082\r\nf 7262/7092/7099 7265/7095/7102 7264/7094/7101 7263/7093/7100\r\nf 7266/7096/7103 7267/7097/7104 7264/7094/7101 7265/7095/7102\r\nf 7246/7076/7083 7245/7075/7082 7264/7094/7101 7267/7097/7104\r\nf 7250/7080/7087 7268/7098/7105 7269/7099/7106 7251/7081/7088\r\nf 7270/7100/7107 7271/7101/7108 7269/7099/7106 7268/7098/7105\r\nf 7272/7102/7109 7273/7103/7110 7269/7099/7106 7271/7101/7108\r\nf 7252/7082/7089 7251/7081/7088 7269/7099/7106 7273/7103/7110\r\nf 7252/7082/7089 7273/7103/7110 7274/7104/7111 7255/7085/7092\r\nf 7272/7102/7109 7275/7105/7112 7274/7104/7111 7273/7103/7110\r\nf 7276/7106/7113 7277/7107/7114 7274/7104/7111 7275/7105/7112\r\nf 7256/7086/7093 7255/7085/7092 7274/7104/7111 7277/7107/7114\r\nf 7260/7090/7097 7278/7108/7115 7279/7109/7116 7261/7091/7098\r\nf 7280/7110/7117 7281/7111/7118 7279/7109/7116 7278/7108/7115\r\nf 7282/7112/7119 7283/7113/7120 7279/7109/7116 7281/7111/7118\r\nf 7262/7092/7099 7261/7091/7098 7279/7109/7116 7283/7113/7120\r\nf 7262/7092/7099 7283/7113/7120 7284/7114/7121 7265/7095/7102\r\nf 7282/7112/7119 7285/7115/7122 7284/7114/7121 7283/7113/7120\r\nf 7286/7116/7123 7287/7117/7124 7284/7114/7121 7285/7115/7122\r\nf 7266/7096/7103 7265/7095/7102 7284/7114/7121 7287/7117/7124\r\nf 7270/7100/7107 7288/7118/7125 7289/7119/7126 7271/7101/7108\r\nf 7290/7120/7127 7291/7121/7128 7289/7119/7126 7288/7118/7125\r\nf 7292/7122/7129 7293/7123/7130 7289/7119/7126 7291/7121/7128\r\nf 7272/7102/7109 7271/7101/7108 7289/7119/7126 7293/7123/7130\r\nf 7272/7102/7109 7293/7123/7130 7294/7124/7131 7275/7105/7112\r\nf 7292/7122/7129 7295/7125/7132 7294/7124/7131 7293/7123/7130\r\nf 7296/7126/7133 7297/7127/7134 7294/7124/7131 7295/7125/7132\r\nf 7276/7106/7113 7275/7105/7112 7294/7124/7131 7297/7127/7134\r\nf 7280/7110/7117 7298/7128/7135 7299/7129/7136 7281/7111/7118\r\nf 7300/7130/7137 7301/7131/7138 7299/7129/7136 7298/7128/7135\r\nf 7302/7132/7139 7303/7133/7140 7299/7129/7136 7301/7131/7138\r\nf 7282/7112/7119 7281/7111/7118 7299/7129/7136 7303/7133/7140\r\nf 7282/7112/7119 7303/7133/7140 7304/7134/7141 7285/7115/7122\r\nf 7302/7132/7139 7305/7135/7142 7304/7134/7141 7303/7133/7140\r\nf 7306/7136/7143 7307/7137/7144 7304/7134/7141 7305/7135/7142\r\nf 7286/7116/7123 7285/7115/7122 7304/7134/7141 7307/7137/7144\r\nf 7290/7120/7127 7308/7138/7145 7309/7139/7146 7291/7121/7128\r\nf 7310/7140/7147 7311/7141/7148 7309/7139/7146 7308/7138/7145\r\nf 7312/7142/7149 7313/7143/7150 7309/7139/7146 7311/7141/7148\r\nf 7292/7122/7129 7291/7121/7128 7309/7139/7146 7313/7143/7150\r\nf 7292/7122/7129 7313/7143/7150 7314/7144/7151 7295/7125/7132\r\nf 7312/7142/7149 7315/7145/7152 7314/7144/7151 7313/7143/7150\r\nf 7316/7146/7153 7317/7147/7154 7314/7144/7151 7315/7145/7152\r\nf 7296/7126/7133 7295/7125/7132 7314/7144/7151 7317/7147/7154\r\nf 6020/5850/5863 6022/5852/5865 7318/7148/7155 6015/5845/5858\r\nf 6026/5856/5869 7319/7149/7156 7318/7148/7155 6022/5852/5865\r\nf 7320/7150/7157 7321/7151/7158 7318/7148/7155 7319/7149/7156\r\nf 6012/5842/5855 6015/5845/5858 7318/7148/7155 7321/7151/7158\r\nf 7322/7152/7159 7323/7153/7160 7324/7154/7161 7325/7155/7162\r\nf 7326/7156/7163 7327/7157/7164 7324/7154/7161 7323/7153/7160\r\nf 7328/7158/7165 7329/7159/7166 7324/7154/7161 7327/7157/7164\r\nf 7330/7160/7167 7325/7155/7162 7324/7154/7161 7329/7159/7166\r\nf 7330/7160/7167 7329/7159/7166 7331/7161/7168 7332/7162/7169\r\nf 7328/7158/7165 7333/7163/7170 7331/7161/7168 7329/7159/7166\r\nf 7334/7164/7171 7335/7165/7172 7331/7161/7168 7333/7163/7170\r\nf 7336/7166/7173 7332/7162/7169 7331/7161/7168 7335/7165/7172\r\nf 7337/7167/7174 7338/7168/7175 7339/7169/7176 7340/7170/7177\r\nf 7341/7171/7178 7342/7172/7179 7339/7169/7176 7338/7168/7175\r\nf 7343/7173/7180 7344/7174/7181 7339/7169/7176 7342/7172/7179\r\nf 7345/7175/7182 7340/7170/7177 7339/7169/7176 7344/7174/7181\r\nf 7345/7175/7182 7344/7174/7181 7346/7176/7183 7347/7177/7184\r\nf 7343/7173/7180 7348/7178/7185 7346/7176/7183 7344/7174/7181\r\nf 7349/7179/7186 7350/7180/7187 7346/7176/7183 7348/7178/7185\r\nf 7351/7181/7188 7347/7177/7184 7346/7176/7183 7350/7180/7187\r\nf 7326/7156/7163 7352/7182/7189 7353/7183/7190 7327/7157/7164\r\nf 7354/7184/7191 7355/7185/7192 7353/7183/7190 7352/7182/7189\r\nf 7356/7186/7193 7357/7187/7194 7353/7183/7190 7355/7185/7192\r\nf 7328/7158/7165 7327/7157/7164 7353/7183/7190 7357/7187/7194\r\nf 7328/7158/7165 7357/7187/7194 7358/7188/7195 7333/7163/7170\r\nf 7356/7186/7193 7359/7189/7196 7358/7188/7195 7357/7187/7194\r\nf 7360/7190/7197 7361/7191/7198 7358/7188/7195 7359/7189/7196\r\nf 7334/7164/7171 7333/7163/7170 7358/7188/7195 7361/7191/7198\r\nf 7341/7171/7178 7362/7192/7199 7363/7193/7200 7342/7172/7179\r\nf 7364/7194/7201 7365/7195/7202 7363/7193/7200 7362/7192/7199\r\nf 7366/7196/7203 7367/7197/7204 7363/7193/7200 7365/7195/7202\r\nf 7343/7173/7180 7342/7172/7179 7363/7193/7200 7367/7197/7204\r\nf 7343/7173/7180 7367/7197/7204 7368/7198/7205 7348/7178/7185\r\nf 7366/7196/7203 7369/7199/7206 7368/7198/7205 7367/7197/7204\r\nf 7370/7200/7207 7371/7201/7208 7368/7198/7205 7369/7199/7206\r\nf 7349/7179/7186 7348/7178/7185 7368/7198/7205 7371/7201/7208\r\nf 7354/7184/7191 7372/7202/7209 7373/7203/7210 7355/7185/7192\r\nf 7374/7204/7211 7375/7205/7212 7373/7203/7210 7372/7202/7209\r\nf 7376/7206/7213 7377/7207/7214 7373/7203/7210 7375/7205/7212\r\nf 7356/7186/7193 7355/7185/7192 7373/7203/7210 7377/7207/7214\r\nf 7356/7186/7193 7377/7207/7214 7378/7208/7215 7359/7189/7196\r\nf 7376/7206/7213 7379/7209/7216 7378/7208/7215 7377/7207/7214\r\nf 7380/7210/7217 7381/7211/7218 7378/7208/7215 7379/7209/7216\r\nf 7360/7190/7197 7359/7189/7196 7378/7208/7215 7381/7211/7218\r\nf 7364/7194/7201 7382/7212/7219 7383/7213/7220 7365/7195/7202\r\nf 7384/7214/7221 7385/7215/7222 7383/7213/7220 7382/7212/7219\r\nf 7386/7216/7223 7387/7217/7224 7383/7213/7220 7385/7215/7222\r\nf 7366/7196/7203 7365/7195/7202 7383/7213/7220 7387/7217/7224\r\nf 7366/7196/7203 7387/7217/7224 7388/7218/7225 7369/7199/7206\r\nf 7386/7216/7223 7389/7219/7226 7388/7218/7225 7387/7217/7224\r\nf 7390/7220/7227 7391/7221/7228 7388/7218/7225 7389/7219/7226\r\nf 7370/7200/7207 7369/7199/7206 7388/7218/7225 7391/7221/7228\r\nf 7374/7204/7211 7392/7222/7229 7393/7223/7230 7375/7205/7212\r\nf 7394/7224/7231 7395/7225/7232 7393/7223/7230 7392/7222/7229\r\nf 7396/7226/7233 7397/7227/7234 7393/7223/7230 7395/7225/7232\r\nf 7376/7206/7213 7375/7205/7212 7393/7223/7230 7397/7227/7234\r\nf 7376/7206/7213 7397/7227/7234 7398/7228/7235 7379/7209/7216\r\nf 7396/7226/7233 7399/7229/7236 7398/7228/7235 7397/7227/7234\r\nf 7400/7230/7237 7401/7231/7237 7398/7228/7235 7399/7229/7236\r\nf 7380/7210/7217 7379/7209/7216 7398/7228/7235 7401/7231/7237\r\nf 7384/7214/7221 7402/7232/7238 7403/7233/7239 7385/7215/7222\r\nf 7404/7234/7240 7405/7235/7241 7403/7233/7239 7402/7232/7238\r\nf 7406/7236/7242 7407/7237/7243 7403/7233/7239 7405/7235/7241\r\nf 7386/7216/7223 7385/7215/7222 7403/7233/7239 7407/7237/7243\r\nf 7386/7216/7223 7407/7237/7243 7408/7238/7244 7389/7219/7226\r\nf 7406/7236/7242 7409/7239/7245 7408/7238/7244 7407/7237/7243\r\nf 7410/7240/7246 7411/7241/7246 7408/7238/7244 7409/7239/7245\r\nf 7390/7220/7227 7389/7219/7226 7408/7238/7244 7411/7241/7246\r\nf 7394/7224/7231 7412/7242/7247 7413/7243/7248 7395/7225/7232\r\nf 7414/7244/7249 7415/7245/7250 7413/7243/7248 7412/7242/7247\r\nf 7416/7246/7251 7417/7247/7252 7413/7243/7248 7415/7245/7250\r\nf 7396/7226/7233 7395/7225/7232 7413/7243/7248 7417/7247/7252\r\nf 7396/7226/7233 7417/7247/7252 7418/7248/7253 7399/7229/7236\r\nf 7416/7246/7251 7419/7249/7254 7418/7248/7253 7417/7247/7252\r\nf 7420/7250/7255 7421/7251/7217 7418/7248/7253 7419/7249/7254\r\nf 7400/7230/7237 7399/7229/7236 7418/7248/7253 7421/7251/7217\r\nf 7404/7234/7240 7422/7252/7256 7423/7253/7257 7405/7235/7241\r\nf 7424/7254/7258 7425/7255/7259 7423/7253/7257 7422/7252/7256\r\nf 7426/7256/7260 7427/7257/7261 7423/7253/7257 7425/7255/7259\r\nf 7406/7236/7242 7405/7235/7241 7423/7253/7257 7427/7257/7261\r\nf 7406/7236/7242 7427/7257/7261 7428/7258/7262 7409/7239/7245\r\nf 7426/7256/7260 7429/7259/7263 7428/7258/7262 7427/7257/7261\r\nf 7430/7260/7264 7431/7261/7227 7428/7258/7262 7429/7259/7263\r\nf 7410/7240/7246 7409/7239/7245 7428/7258/7262 7431/7261/7227\r\nf 7414/7244/7249 7432/7262/7265 7433/7263/7266 7415/7245/7250\r\nf 7434/7264/7267 7435/7265/7268 7433/7263/7266 7432/7262/7265\r\nf 7436/7266/7269 7437/7267/7270 7433/7263/7266 7435/7265/7268\r\nf 7416/7246/7251 7415/7245/7250 7433/7263/7266 7437/7267/7270\r\nf 7416/7246/7251 7437/7267/7270 7438/7268/7271 7419/7249/7254\r\nf 7436/7266/7269 7439/7269/7272 7438/7268/7271 7437/7267/7270\r\nf 7440/7270/7273 7441/7271/7274 7438/7268/7271 7439/7269/7272\r\nf 7420/7250/7255 7419/7249/7254 7438/7268/7271 7441/7271/7274\r\nf 7424/7254/7258 7442/7272/7275 7443/7273/7276 7425/7255/7259\r\nf 7444/7274/7277 7445/7275/7278 7443/7273/7276 7442/7272/7275\r\nf 7446/7276/7279 7447/7277/7280 7443/7273/7276 7445/7275/7278\r\nf 7426/7256/7260 7425/7255/7259 7443/7273/7276 7447/7277/7280\r\nf 7426/7256/7260 7447/7277/7280 7448/7278/7281 7429/7259/7263\r\nf 7446/7276/7279 7449/7279/7282 7448/7278/7281 7447/7277/7280\r\nf 7450/7280/7283 7451/7281/7284 7448/7278/7281 7449/7279/7282\r\nf 7430/7260/7264 7429/7259/7263 7448/7278/7281 7451/7281/7284\r\nf 7434/7264/7267 7452/7282/7285 7453/7283/7286 7435/7265/7268\r\nf 7454/7284/7287 7455/7285/7288 7453/7283/7286 7452/7282/7285\r\nf 7456/7286/7289 7457/7287/7290 7453/7283/7286 7455/7285/7288\r\nf 7436/7266/7269 7435/7265/7268 7453/7283/7286 7457/7287/7290\r\nf 7436/7266/7269 7457/7287/7290 7458/7288/7291 7439/7269/7272\r\nf 7456/7286/7289 7459/7289/7292 7458/7288/7291 7457/7287/7290\r\nf 7460/7290/7293 7461/7291/7294 7458/7288/7291 7459/7289/7292\r\nf 7440/7270/7273 7439/7269/7272 7458/7288/7291 7461/7291/7294\r\nf 7444/7274/7277 7462/7292/7295 7463/7293/7296 7445/7275/7278\r\nf 7464/7294/7297 7465/7295/7298 7463/7293/7296 7462/7292/7295\r\nf 7466/7296/7299 7467/7297/7300 7463/7293/7296 7465/7295/7298\r\nf 7446/7276/7279 7445/7275/7278 7463/7293/7296 7467/7297/7300\r\nf 7446/7276/7279 7467/7297/7300 7468/7298/7301 7449/7279/7282\r\nf 7466/7296/7299 7469/7299/7302 7468/7298/7301 7467/7297/7300\r\nf 7470/7300/7303 7471/7301/7304 7468/7298/7301 7469/7299/7302\r\nf 7450/7280/7283 7449/7279/7282 7468/7298/7301 7471/7301/7304\r\nf 7454/7284/7287 7472/7302/7305 7473/7303/7306 7455/7285/7288\r\nf 7474/7304/7307 7475/7305/7308 7473/7303/7306 7472/7302/7305\r\nf 7476/7306/7309 7477/7307/7310 7473/7303/7306 7475/7305/7308\r\nf 7456/7286/7289 7455/7285/7288 7473/7303/7306 7477/7307/7310\r\nf 7456/7286/7289 7477/7307/7310 7478/7308/7311 7459/7289/7292\r\nf 7476/7306/7309 7479/7309/7312 7478/7308/7311 7477/7307/7310\r\nf 7480/7310/7313 7481/7311/7314 7478/7308/7311 7479/7309/7312\r\nf 7460/7290/7293 7459/7289/7292 7478/7308/7311 7481/7311/7314\r\nf 7464/7294/7297 7482/7312/7315 7483/7313/7316 7465/7295/7298\r\nf 7484/7314/7317 7485/7315/7318 7483/7313/7316 7482/7312/7315\r\nf 7486/7316/7319 7487/7317/7320 7483/7313/7316 7485/7315/7318\r\nf 7466/7296/7299 7465/7295/7298 7483/7313/7316 7487/7317/7320\r\nf 7466/7296/7299 7487/7317/7320 7488/7318/7321 7469/7299/7302\r\nf 7486/7316/7319 7489/7319/7322 7488/7318/7321 7487/7317/7320\r\nf 7490/7320/7323 7491/7321/7324 7488/7318/7321 7489/7319/7322\r\nf 7470/7300/7303 7469/7299/7302 7488/7318/7321 7491/7321/7324\r\nf 7474/7304/7307 7492/7322/7325 7493/7323/7326 7475/7305/7308\r\nf 7494/7324/7327 7495/7325/7328 7493/7323/7326 7492/7322/7325\r\nf 7310/7140/7147 7496/7326/7329 7493/7323/7326 7495/7325/7328\r\nf 7476/7306/7309 7475/7305/7308 7493/7323/7326 7496/7326/7329\r\nf 7476/7306/7309 7496/7326/7329 7497/7327/7330 7479/7309/7312\r\nf 7310/7140/7147 7498/7328/7331 7497/7327/7330 7496/7326/7329\r\nf 7499/7329/7332 7500/7330/7333 7497/7327/7330 7498/7328/7331\r\nf 7480/7310/7313 7479/7309/7312 7497/7327/7330 7500/7330/7333\r\nf 7484/7314/7317 7501/7331/7334 7502/7332/7335 7485/7315/7318\r\nf 7302/7132/7139 7503/7333/7336 7502/7332/7335 7501/7331/7334\r\nf 7504/7334/7337 7505/7335/7338 7502/7332/7335 7503/7333/7336\r\nf 7486/7316/7319 7485/7315/7318 7502/7332/7335 7505/7335/7338\r\nf 7486/7316/7319 7505/7335/7338 7506/7336/7339 7489/7319/7322\r\nf 7504/7334/7337 7507/7337/7340 7506/7336/7339 7505/7335/7338\r\nf 7508/7338/7341 7509/7339/7342 7506/7336/7339 7507/7337/7340\r\nf 7490/7320/7323 7489/7319/7322 7506/7336/7339 7509/7339/7342\r\nf 7494/7324/7327 7510/7340/7343 7511/7341/7344 7495/7325/7328\r\nf 7512/7342/7345 7513/7343/7346 7511/7341/7344 7510/7340/7343\r\nf 7312/7142/7149 7311/7141/7148 7511/7341/7344 7513/7343/7346\r\nf 7310/7140/7147 7495/7325/7328 7511/7341/7344 7311/7141/7148\r\nf 7302/7132/7139 7301/7131/7138 7514/7344/7347 7503/7333/7336\r\nf 7300/7130/7137 7515/7345/7348 7514/7344/7347 7301/7131/7138\r\nf 7516/7346/7349 7517/7347/7350 7514/7344/7347 7515/7345/7348\r\nf 7504/7334/7337 7503/7333/7336 7514/7344/7347 7517/7347/7350\r\nf 7504/7334/7337 7517/7347/7350 7518/7348/7351 7507/7337/7340\r\nf 7516/7346/7349 7519/7349/7352 7518/7348/7351 7517/7347/7350\r\nf 7520/7350/7353 7521/7351/7354 7518/7348/7351 7519/7349/7352\r\nf 7508/7338/7341 7507/7337/7340 7518/7348/7351 7521/7351/7354\r\nf 7512/7342/7345 7522/7352/7355 7523/7353/7356 7513/7343/7346\r\nf 7524/7354/7357 7525/7355/7358 7523/7353/7356 7522/7352/7355\r\nf 7526/7356/7359 7527/7357/7360 7523/7353/7356 7525/7355/7358\r\nf 7312/7142/7149 7513/7343/7346 7523/7353/7356 7527/7357/7360\r\nf 7312/7142/7149 7527/7357/7360 7528/7358/7361 7315/7145/7152\r\nf 7526/7356/7359 7529/7359/7362 7528/7358/7361 7527/7357/7360\r\nf 7530/7360/7363 7531/7361/7364 7528/7358/7361 7529/7359/7362\r\nf 7316/7146/7153 7315/7145/7152 7528/7358/7361 7531/7361/7364\r\nf 7300/7130/7137 7532/7362/7365 7533/7363/7366 7515/7345/7348\r\nf 7534/7364/7367 7535/7365/7368 7533/7363/7366 7532/7362/7365\r\nf 7536/7366/7369 7537/7367/7370 7533/7363/7366 7535/7365/7368\r\nf 7516/7346/7349 7515/7345/7348 7533/7363/7366 7537/7367/7370\r\nf 7516/7346/7349 7537/7367/7370 7538/7368/7371 7519/7349/7352\r\nf 7536/7366/7369 7539/7369/7372 7538/7368/7371 7537/7367/7370\r\nf 7540/7370/7373 7541/7371/7374 7538/7368/7371 7539/7369/7372\r\nf 7520/7350/7353 7519/7349/7352 7538/7368/7371 7541/7371/7374\r\nf 7524/7354/7357 7542/7372/7375 7543/7373/7376 7525/7355/7358\r\nf 7544/7374/7377 7545/7375/7378 7543/7373/7376 7542/7372/7375\r\nf 7546/7376/7379 7547/7377/7380 7543/7373/7376 7545/7375/7378\r\nf 7526/7356/7359 7525/7355/7358 7543/7373/7376 7547/7377/7380\r\nf 7526/7356/7359 7547/7377/7380 7548/7378/7381 7529/7359/7362\r\nf 7546/7376/7379 7549/7379/7382 7548/7378/7381 7547/7377/7380\r\nf 7550/7380/7383 7551/7381/7384 7548/7378/7381 7549/7379/7382\r\nf 7530/7360/7363 7529/7359/7362 7548/7378/7381 7551/7381/7384\r\nf 7534/7364/7367 7552/7382/7385 7553/7383/7386 7535/7365/7368\r\nf 7554/7384/7387 7555/7385/7388 7553/7383/7386 7552/7382/7385\r\nf 7556/7386/7389 7557/7387/7390 7553/7383/7386 7555/7385/7388\r\nf 7536/7366/7369 7535/7365/7368 7553/7383/7386 7557/7387/7390\r\nf 7536/7366/7369 7557/7387/7390 7558/7388/7391 7539/7369/7372\r\nf 7556/7386/7389 7559/7389/7392 7558/7388/7391 7557/7387/7390\r\nf 7560/7390/7393 7561/7391/7394 7558/7388/7391 7559/7389/7392\r\nf 7540/7370/7373 7539/7369/7372 7558/7388/7391 7561/7391/7394\r\nf 7544/7374/7377 7562/7392/7395 7563/7393/7396 7545/7375/7378\r\nf 7564/7394/7397 7565/7395/7398 7563/7393/7396 7562/7392/7395\r\nf 7566/7396/7399 7567/7397/7400 7563/7393/7396 7565/7395/7398\r\nf 7546/7376/7379 7545/7375/7378 7563/7393/7396 7567/7397/7400\r\nf 7546/7376/7379 7567/7397/7400 7568/7398/7401 7549/7379/7382\r\nf 7566/7396/7399 7569/7399/7402 7568/7398/7401 7567/7397/7400\r\nf 7570/7400/7403 7571/7401/7404 7568/7398/7401 7569/7399/7402\r\nf 7550/7380/7383 7549/7379/7382 7568/7398/7401 7571/7401/7404\r\nf 7554/7384/7387 7572/7402/7405 7573/7403/7406 7555/7385/7388\r\nf 7574/7404/7407 7575/7405/7408 7573/7403/7406 7572/7402/7405\r\nf 7576/7406/7409 7577/7407/7410 7573/7403/7406 7575/7405/7408\r\nf 7556/7386/7389 7555/7385/7388 7573/7403/7406 7577/7407/7410\r\nf 7556/7386/7389 7577/7407/7410 7578/7408/7411 7559/7389/7392\r\nf 7576/7406/7409 7579/7409/7412 7578/7408/7411 7577/7407/7410\r\nf 7580/7410/7413 7581/7411/7414 7578/7408/7411 7579/7409/7412\r\nf 7560/7390/7393 7559/7389/7392 7578/7408/7411 7581/7411/7414\r\nf 7564/7394/7397 7582/7412/7415 7583/7413/7416 7565/7395/7398\r\nf 7584/7414/7417 7585/7415/7418 7583/7413/7416 7582/7412/7415\r\nf 7586/7416/7419 7587/7417/7420 7583/7413/7416 7585/7415/7418\r\nf 7566/7396/7399 7565/7395/7398 7583/7413/7416 7587/7417/7420\r\nf 7566/7396/7399 7587/7417/7420 7588/7418/7421 7569/7399/7402\r\nf 7586/7416/7419 7589/7419/7422 7588/7418/7421 7587/7417/7420\r\nf 7590/7420/7423 7591/7421/7424 7588/7418/7421 7589/7419/7422\r\nf 7570/7400/7403 7569/7399/7402 7588/7418/7421 7591/7421/7424\r\nf 7574/7404/7407 7592/7422/7425 7593/7423/7426 7575/7405/7408\r\nf 7594/7424/7427 7595/7425/7428 7593/7423/7426 7592/7422/7425\r\nf 7596/7426/7429 7597/7427/7430 7593/7423/7426 7595/7425/7428\r\nf 7576/7406/7409 7575/7405/7408 7593/7423/7426 7597/7427/7430\r\nf 7576/7406/7409 7597/7427/7430 7598/7428/7431 7579/7409/7412\r\nf 7596/7426/7429 7599/7429/7432 7598/7428/7431 7597/7427/7430\r\nf 7600/7430/7433 7601/7431/7434 7598/7428/7431 7599/7429/7432\r\nf 7580/7410/7413 7579/7409/7412 7598/7428/7431 7601/7431/7434\r\nf 7584/7414/7417 7602/7432/7435 7603/7433/7436 7585/7415/7418\r\nf 7604/7434/7437 7605/7435/7438 7603/7433/7436 7602/7432/7435\r\nf 7606/7436/7439 7607/7437/7440 7603/7433/7436 7605/7435/7438\r\nf 7586/7416/7419 7585/7415/7418 7603/7433/7436 7607/7437/7440\r\nf 7586/7416/7419 7607/7437/7440 7608/7438/7441 7589/7419/7422\r\nf 7606/7436/7439 7609/7439/7442 7608/7438/7441 7607/7437/7440\r\nf 7610/7440/7443 7611/7441/7444 7608/7438/7441 7609/7439/7442\r\nf 7590/7420/7423 7589/7419/7422 7608/7438/7441 7611/7441/7444\r\nf 7594/7424/7427 7612/7442/7445 7613/7443/7446 7595/7425/7428\r\nf 7614/7444/7447 7615/7445/7448 7613/7443/7446 7612/7442/7445\r\nf 7616/7446/7449 7617/7447/7450 7613/7443/7446 7615/7445/7448\r\nf 7596/7426/7429 7595/7425/7428 7613/7443/7446 7617/7447/7450\r\nf 7596/7426/7429 7617/7447/7450 7618/7448/7451 7599/7429/7432\r\nf 7616/7446/7449 7619/7449/7452 7618/7448/7451 7617/7447/7450\r\nf 7620/7450/7453 7621/7451/7454 7618/7448/7451 7619/7449/7452\r\nf 7600/7430/7433 7599/7429/7432 7618/7448/7451 7621/7451/7454\r\nf 7604/7434/7437 7622/7452/7455 7623/7453/7456 7605/7435/7438\r\nf 7624/7454/7457 7625/7455/7458 7623/7453/7456 7622/7452/7455\r\nf 7626/7456/7459 7627/7457/7460 7623/7453/7456 7625/7455/7458\r\nf 7606/7436/7439 7605/7435/7438 7623/7453/7456 7627/7457/7460\r\nf 7606/7436/7439 7627/7457/7460 7628/7458/7461 7609/7439/7442\r\nf 7626/7456/7459 7629/7459/7462 7628/7458/7461 7627/7457/7460\r\nf 7630/7460/7463 7631/7461/7464 7628/7458/7461 7629/7459/7462\r\nf 7610/7440/7443 7609/7439/7442 7628/7458/7461 7631/7461/7464\r\nf 7614/7444/7447 7632/7462/7465 7633/7463/7466 7615/7445/7448\r\nf 7634/7464/7467 7635/7465/7468 7633/7463/7466 7632/7462/7465\r\nf 7636/7466/7469 7637/7467/7470 7633/7463/7466 7635/7465/7468\r\nf 7616/7446/7449 7615/7445/7448 7633/7463/7466 7637/7467/7470\r\nf 7616/7446/7449 7637/7467/7470 7638/7468/7471 7619/7449/7452\r\nf 7636/7466/7469 7639/7469/7472 7638/7468/7471 7637/7467/7470\r\nf 7640/7470/7473 7641/7471/7474 7638/7468/7471 7639/7469/7472\r\nf 7620/7450/7453 7619/7449/7452 7638/7468/7471 7641/7471/7474\r\nf 7624/7454/7457 7642/7472/7475 7643/7473/7476 7625/7455/7458\r\nf 7644/7474/7477 7645/7475/7478 7643/7473/7476 7642/7472/7475\r\nf 7646/7476/7479 7647/7477/7480 7643/7473/7476 7645/7475/7478\r\nf 7626/7456/7459 7625/7455/7458 7643/7473/7476 7647/7477/7480\r\nf 7626/7456/7459 7647/7477/7480 7648/7478/7481 7629/7459/7462\r\nf 7646/7476/7479 7649/7479/7482 7648/7478/7481 7647/7477/7480\r\nf 7650/7480/7483 7651/7481/7484 7648/7478/7481 7649/7479/7482\r\nf 7630/7460/7463 7629/7459/7462 7648/7478/7481 7651/7481/7484\r\nf 7634/7464/7467 7652/7482/7485 7653/7483/7486 7635/7465/7468\r\nf 7654/7484/7487 7655/7485/7488 7653/7483/7486 7652/7482/7485\r\nf 7656/7486/7489 7657/7487/7490 7653/7483/7486 7655/7485/7488\r\nf 7636/7466/7469 7635/7465/7468 7653/7483/7486 7657/7487/7490\r\nf 7636/7466/7469 7657/7487/7490 7658/7488/7491 7639/7469/7472\r\nf 7656/7486/7489 7659/7489/7492 7658/7488/7491 7657/7487/7490\r\nf 7660/7490/7493 7661/7491/7494 7658/7488/7491 7659/7489/7492\r\nf 7640/7470/7473 7639/7469/7472 7658/7488/7491 7661/7491/7494\r\nf 7644/7474/7477 7662/7492/7495 7663/7493/7496 7645/7475/7478\r\nf 7664/7494/7497 7665/7495/7498 7663/7493/7496 7662/7492/7495\r\nf 7666/7496/7499 7667/7497/7500 7663/7493/7496 7665/7495/7498\r\nf 7646/7476/7479 7645/7475/7478 7663/7493/7496 7667/7497/7500\r\nf 7646/7476/7479 7667/7497/7500 7668/7498/7501 7649/7479/7482\r\nf 7666/7496/7499 7669/7499/7502 7668/7498/7501 7667/7497/7500\r\nf 7670/7500/7503 7671/7501/7504 7668/7498/7501 7669/7499/7502\r\nf 7650/7480/7483 7649/7479/7482 7668/7498/7501 7671/7501/7504\r\nf 7654/7484/7487 7672/7502/7505 7673/7503/7506 7655/7485/7488\r\nf 7674/7504/7507 7675/7505/7508 7673/7503/7506 7672/7502/7505\r\nf 7676/7506/7509 7677/7507/7510 7673/7503/7506 7675/7505/7508\r\nf 7656/7486/7489 7655/7485/7488 7673/7503/7506 7677/7507/7510\r\nf 7656/7486/7489 7677/7507/7510 7678/7508/7511 7659/7489/7492\r\nf 7676/7506/7509 7679/7509/7512 7678/7508/7511 7677/7507/7510\r\nf 7680/7510/7513 7681/7511/7514 7678/7508/7511 7679/7509/7512\r\nf 7660/7490/7493 7659/7489/7492 7678/7508/7511 7681/7511/7514\r\nf 7664/7494/7497 7682/7512/7515 7683/7513/7516 7665/7495/7498\r\nf 7684/7514/7517 7685/7515/7518 7683/7513/7516 7682/7512/7515\r\nf 7686/7516/7519 7687/7517/7520 7683/7513/7516 7685/7515/7518\r\nf 7666/7496/7499 7665/7495/7498 7683/7513/7516 7687/7517/7520\r\nf 7666/7496/7499 7687/7517/7520 7688/7518/7521 7669/7499/7502\r\nf 7686/7516/7519 7689/7519/7522 7688/7518/7521 7687/7517/7520\r\nf 7690/7520/7523 7691/7521/7524 7688/7518/7521 7689/7519/7522\r\nf 7670/7500/7503 7669/7499/7502 7688/7518/7521 7691/7521/7524\r\nf 7674/7504/7507 7692/7522/7525 7693/7523/7526 7675/7505/7508\r\nf 7694/7524/7527 7695/7525/7528 7693/7523/7526 7692/7522/7525\r\nf 7696/7526/7529 7697/7527/7530 7693/7523/7526 7695/7525/7528\r\nf 7676/7506/7509 7675/7505/7508 7693/7523/7526 7697/7527/7530\r\nf 7676/7506/7509 7697/7527/7530 7698/7528/7531 7679/7509/7512\r\nf 7696/7526/7529 7699/7529/7532 7698/7528/7531 7697/7527/7530\r\nf 7700/7530/7533 7701/7531/7534 7698/7528/7531 7699/7529/7532\r\nf 7680/7510/7513 7679/7509/7512 7698/7528/7531 7701/7531/7534\r\nf 7684/7514/7517 7702/7532/7535 7703/7533/7536 7685/7515/7518\r\nf 7704/7534/7537 7705/7535/7538 7703/7533/7536 7702/7532/7535\r\nf 7706/7536/7539 7707/7537/7540 7703/7533/7536 7705/7535/7538\r\nf 7686/7516/7519 7685/7515/7518 7703/7533/7536 7707/7537/7540\r\nf 7686/7516/7519 7707/7537/7540 7708/7538/7541 7689/7519/7522\r\nf 7706/7536/7539 7709/7539/7542 7708/7538/7541 7707/7537/7540\r\nf 7710/7540/7543 7711/7541/7544 7708/7538/7541 7709/7539/7542\r\nf 7690/7520/7523 7689/7519/7522 7708/7538/7541 7711/7541/7544\r\nf 7694/7524/7527 7712/7542/7545 7713/7543/7546 7695/7525/7528\r\nf 7714/7544/7547 7715/7545/7548 7713/7543/7546 7712/7542/7545\r\nf 7716/7546/7549 7717/7547/7550 7713/7543/7546 7715/7545/7548\r\nf 7696/7526/7529 7695/7525/7528 7713/7543/7546 7717/7547/7550\r\nf 7696/7526/7529 7717/7547/7550 7718/7548/7551 7699/7529/7532\r\nf 7716/7546/7549 7719/7549/7552 7718/7548/7551 7717/7547/7550\r\nf 7720/7550/7553 7721/7551/7554 7718/7548/7551 7719/7549/7552\r\nf 7700/7530/7533 7699/7529/7532 7718/7548/7551 7721/7551/7554\r\nf 7704/7534/7537 7722/7552/7555 7723/7553/7556 7705/7535/7538\r\nf 7724/7554/7557 7725/7555/7558 7723/7553/7556 7722/7552/7555\r\nf 7726/7556/7559 7727/7557/7560 7723/7553/7556 7725/7555/7558\r\nf 7706/7536/7539 7705/7535/7538 7723/7553/7556 7727/7557/7560\r\nf 7706/7536/7539 7727/7557/7560 7728/7558/7561 7709/7539/7542\r\nf 7726/7556/7559 7729/7559/7562 7728/7558/7561 7727/7557/7560\r\nf 7730/7560/7543 7731/7561/7563 7728/7558/7561 7729/7559/7562\r\nf 7710/7540/7543 7709/7539/7542 7728/7558/7561 7731/7561/7563\r\nf 7714/7544/7547 7732/7562/7564 7733/7563/7565 7715/7545/7548\r\nf 7734/7564/7566 7735/7565/7567 7733/7563/7565 7732/7562/7564\r\nf 7736/7566/7568 7737/7567/7569 7733/7563/7565 7735/7565/7567\r\nf 7716/7546/7549 7715/7545/7548 7733/7563/7565 7737/7567/7569\r\nf 7716/7546/7549 7737/7567/7569 7738/7568/7570 7719/7549/7552\r\nf 7736/7566/7568 7739/7569/7571 7738/7568/7570 7737/7567/7569\r\nf 7740/7570/7553 7741/7571/7572 7738/7568/7570 7739/7569/7571\r\nf 7720/7550/7553 7719/7549/7552 7738/7568/7570 7741/7571/7572\r\nf 7724/7554/7557 7742/7572/7573 7743/7573/7574 7725/7555/7558\r\nf 7744/7574/7575 7745/7575/7576 7743/7573/7574 7742/7572/7573\r\nf 7746/7576/7577 7747/7577/7578 7743/7573/7574 7745/7575/7576\r\nf 7726/7556/7559 7725/7555/7558 7743/7573/7574 7747/7577/7578\r\nf 7726/7556/7559 7747/7577/7578 7748/7578/7579 7729/7559/7562\r\nf 7746/7576/7577 7749/7579/7580 7748/7578/7579 7747/7577/7578\r\nf 7750/7580/7581 7751/7581/7582 7748/7578/7579 7749/7579/7580\r\nf 7730/7560/7543 7729/7559/7562 7748/7578/7579 7751/7581/7582\r\nf 7734/7564/7566 7752/7582/7583 7753/7583/7584 7735/7565/7567\r\nf 7754/7584/7585 7755/7585/7586 7753/7583/7584 7752/7582/7583\r\nf 7756/7586/7587 7757/7587/7588 7753/7583/7584 7755/7585/7586\r\nf 7736/7566/7568 7735/7565/7567 7753/7583/7584 7757/7587/7588\r\nf 7736/7566/7568 7757/7587/7588 7758/7588/7589 7739/7569/7571\r\nf 7756/7586/7587 7759/7589/7590 7758/7588/7589 7757/7587/7588\r\nf 7760/7590/7591 7761/7591/7592 7758/7588/7589 7759/7589/7590\r\nf 7740/7570/7553 7739/7569/7571 7758/7588/7589 7761/7591/7592\r\nf 7744/7574/7575 7762/7592/7593 7763/7593/7594 7745/7575/7576\r\nf 7764/7594/7595 7765/7595/7596 7763/7593/7594 7762/7592/7593\r\nf 7766/7596/7597 7767/7597/7598 7763/7593/7594 7765/7595/7596\r\nf 7746/7576/7577 7745/7575/7576 7763/7593/7594 7767/7597/7598\r\nf 7746/7576/7577 7767/7597/7598 7768/7598/7599 7749/7579/7580\r\nf 7766/7596/7597 7769/7599/7600 7768/7598/7599 7767/7597/7598\r\nf 7770/7600/7601 7771/7601/7602 7768/7598/7599 7769/7599/7600\r\nf 7750/7580/7581 7749/7579/7580 7768/7598/7599 7771/7601/7602\r\nf 7754/7584/7585 7772/7602/7603 7773/7603/7604 7755/7585/7586\r\nf 7774/7604/7605 7775/7605/7606 7773/7603/7604 7772/7602/7603\r\nf 7776/7606/7607 7777/7607/7608 7773/7603/7604 7775/7605/7606\r\nf 7756/7586/7587 7755/7585/7586 7773/7603/7604 7777/7607/7608\r\nf 7756/7586/7587 7777/7607/7608 7778/7608/7609 7759/7589/7590\r\nf 7776/7606/7607 7779/7609/7610 7778/7608/7609 7777/7607/7608\r\nf 7780/7610/7611 7781/7611/7612 7778/7608/7609 7779/7609/7610\r\nf 7760/7590/7591 7759/7589/7590 7778/7608/7609 7781/7611/7612\r\nf 7764/7594/7595 7782/7612/7613 7783/7613/7614 7765/7595/7596\r\nf 7784/7614/7615 7785/7615/7616 7783/7613/7614 7782/7612/7613\r\nf 7786/7616/7617 7787/7617/7618 7783/7613/7614 7785/7615/7616\r\nf 7766/7596/7597 7765/7595/7596 7783/7613/7614 7787/7617/7618\r\nf 7766/7596/7597 7787/7617/7618 7788/7618/7619 7769/7599/7600\r\nf 7786/7616/7617 7789/7619/7620 7788/7618/7619 7787/7617/7618\r\nf 7790/7620/7621 7791/7621/7622 7788/7618/7619 7789/7619/7620\r\nf 7770/7600/7601 7769/7599/7600 7788/7618/7619 7791/7621/7622\r\nf 7774/7604/7605 7792/7622/7623 7793/7623/7624 7775/7605/7606\r\nf 7794/7624/7625 7795/7625/7626 7793/7623/7624 7792/7622/7623\r\nf 7796/7626/7627 7797/7627/7628 7793/7623/7624 7795/7625/7626\r\nf 7776/7606/7607 7775/7605/7606 7793/7623/7624 7797/7627/7628\r\nf 7776/7606/7607 7797/7627/7628 7798/7628/7629 7779/7609/7610\r\nf 7796/7626/7627 7799/7629/7630 7798/7628/7629 7797/7627/7628\r\nf 7800/7630/7631 7801/7631/7632 7798/7628/7629 7799/7629/7630\r\nf 7780/7610/7611 7779/7609/7610 7798/7628/7629 7801/7631/7632\r\nf 7784/7614/7615 7802/7632/7633 7803/7633/7634 7785/7615/7616\r\nf 7804/7634/7635 7805/7635/7636 7803/7633/7634 7802/7632/7633\r\nf 7806/7636/7637 7807/7637/7638 7803/7633/7634 7805/7635/7636\r\nf 7786/7616/7617 7785/7615/7616 7803/7633/7634 7807/7637/7638\r\nf 7786/7616/7617 7807/7637/7638 7808/7638/7639 7789/7619/7620\r\nf 7806/7636/7637 7809/7639/7640 7808/7638/7639 7807/7637/7638\r\nf 7810/7640/7641 7811/7641/7642 7808/7638/7639 7809/7639/7640\r\nf 7790/7620/7621 7789/7619/7620 7808/7638/7639 7811/7641/7642\r\nf 7794/7624/7625 7812/7642/7643 7813/7643/7644 7795/7625/7626\r\nf 7814/7644/7645 7815/7645/7646 7813/7643/7644 7812/7642/7643\r\nf 7816/7646/7647 7817/7647/7648 7813/7643/7644 7815/7645/7646\r\nf 7796/7626/7627 7795/7625/7626 7813/7643/7644 7817/7647/7648\r\nf 7796/7626/7627 7817/7647/7648 7818/7648/7649 7799/7629/7630\r\nf 7816/7646/7647 7819/7649/7650 7818/7648/7649 7817/7647/7648\r\nf 7820/7650/7651 7821/7651/7652 7818/7648/7649 7819/7649/7650\r\nf 7800/7630/7631 7799/7629/7630 7818/7648/7649 7821/7651/7652\r\nf 7804/7634/7635 7822/7652/7653 7823/7653/7654 7805/7635/7636\r\nf 7824/7654/7655 7825/7655/7656 7823/7653/7654 7822/7652/7653\r\nf 7826/7656/7657 7827/7657/7658 7823/7653/7654 7825/7655/7656\r\nf 7806/7636/7637 7805/7635/7636 7823/7653/7654 7827/7657/7658\r\nf 7806/7636/7637 7827/7657/7658 7828/7658/7659 7809/7639/7640\r\nf 7826/7656/7657 7829/7659/7660 7828/7658/7659 7827/7657/7658\r\nf 7830/7660/7661 7831/7661/7662 7828/7658/7659 7829/7659/7660\r\nf 7810/7640/7641 7809/7639/7640 7828/7658/7659 7831/7661/7662\r\nf 7814/7644/7645 7832/7662/7663 7833/7663/7664 7815/7645/7646\r\nf 7834/7664/7665 7835/7665/7666 7833/7663/7664 7832/7662/7663\r\nf 7836/7666/7667 7837/7667/7668 7833/7663/7664 7835/7665/7666\r\nf 7816/7646/7647 7815/7645/7646 7833/7663/7664 7837/7667/7668\r\nf 7816/7646/7647 7837/7667/7668 7838/7668/7669 7819/7649/7650\r\nf 7836/7666/7667 7839/7669/7670 7838/7668/7669 7837/7667/7668\r\nf 7840/7670/7671 7841/7671/7672 7838/7668/7669 7839/7669/7670\r\nf 7820/7650/7651 7819/7649/7650 7838/7668/7669 7841/7671/7672\r\nf 7824/7654/7655 7842/7672/7673 7843/7673/7674 7825/7655/7656\r\nf 7844/7674/7675 7845/7675/7676 7843/7673/7674 7842/7672/7673\r\nf 7846/7676/7677 7847/7677/7678 7843/7673/7674 7845/7675/7676\r\nf 7826/7656/7657 7825/7655/7656 7843/7673/7674 7847/7677/7678\r\nf 7826/7656/7657 7847/7677/7678 7848/7678/7679 7829/7659/7660\r\nf 7846/7676/7677 7849/7679/7680 7848/7678/7679 7847/7677/7678\r\nf 7850/7680/7681 7851/7681/7682 7848/7678/7679 7849/7679/7680\r\nf 7830/7660/7661 7829/7659/7660 7848/7678/7679 7851/7681/7682\r\nf 7834/7664/7665 7852/7682/7683 7853/7683/7684 7835/7665/7666\r\nf 7854/7684/7685 7855/7685/7686 7853/7683/7684 7852/7682/7683\r\nf 7856/7686/7687 7857/7687/7688 7853/7683/7684 7855/7685/7686\r\nf 7836/7666/7667 7835/7665/7666 7853/7683/7684 7857/7687/7688\r\nf 7836/7666/7667 7857/7687/7688 7858/7688/7689 7839/7669/7670\r\nf 7856/7686/7687 7859/7689/7690 7858/7688/7689 7857/7687/7688\r\nf 7860/7690/7691 7861/7691/7692 7858/7688/7689 7859/7689/7690\r\nf 7840/7670/7671 7839/7669/7670 7858/7688/7689 7861/7691/7692\r\nf 7844/7674/7675 7862/7692/7693 7863/7693/7694 7845/7675/7676\r\nf 7864/7694/7695 7865/7695/7696 7863/7693/7694 7862/7692/7693\r\nf 7866/7696/7697 7867/7697/7698 7863/7693/7694 7865/7695/7696\r\nf 7846/7676/7677 7845/7675/7676 7863/7693/7694 7867/7697/7698\r\nf 7846/7676/7677 7867/7697/7698 7868/7698/7699 7849/7679/7680\r\nf 7866/7696/7697 7869/7699/7700 7868/7698/7699 7867/7697/7698\r\nf 7870/7700/7701 7871/7701/7702 7868/7698/7699 7869/7699/7700\r\nf 7850/7680/7681 7849/7679/7680 7868/7698/7699 7871/7701/7702\r\nf 7854/7684/7685 7872/7702/7703 7873/7703/7704 7855/7685/7686\r\nf 7874/7704/7705 7875/7705/7706 7873/7703/7704 7872/7702/7703\r\nf 7876/7706/7707 7877/7707/7708 7873/7703/7704 7875/7705/7706\r\nf 7856/7686/7687 7855/7685/7686 7873/7703/7704 7877/7707/7708\r\nf 7856/7686/7687 7877/7707/7708 7878/7708/7709 7859/7689/7690\r\nf 7876/7706/7707 7879/7709/7710 7878/7708/7709 7877/7707/7708\r\nf 7880/7710/7711 7881/7711/7712 7878/7708/7709 7879/7709/7710\r\nf 7860/7690/7691 7859/7689/7690 7878/7708/7709 7881/7711/7712\r\nf 7864/7694/7695 7882/7712/7713 7883/7713/7714 7865/7695/7696\r\nf 7884/7714/7715 7885/7715/7716 7883/7713/7714 7882/7712/7713\r\nf 7886/7716/7717 7887/7717/7718 7883/7713/7714 7885/7715/7716\r\nf 7866/7696/7697 7865/7695/7696 7883/7713/7714 7887/7717/7718\r\nf 7866/7696/7697 7887/7717/7718 7888/7718/7719 7869/7699/7700\r\nf 7886/7716/7717 7889/7719/7720 7888/7718/7719 7887/7717/7718\r\nf 7890/7720/7721 7891/7721/7722 7888/7718/7719 7889/7719/7720\r\nf 7870/7700/7701 7869/7699/7700 7888/7718/7719 7891/7721/7722\r\nf 7874/7704/7705 7892/7722/7723 7893/7723/7724 7875/7705/7706\r\nf 7894/7724/7725 7895/7725/7726 7893/7723/7724 7892/7722/7723\r\nf 7896/7726/7727 7897/7727/7728 7893/7723/7724 7895/7725/7726\r\nf 7876/7706/7707 7875/7705/7706 7893/7723/7724 7897/7727/7728\r\nf 7876/7706/7707 7897/7727/7728 7898/7728/7729 7879/7709/7710\r\nf 7896/7726/7727 7899/7729/7730 7898/7728/7729 7897/7727/7728\r\nf 7900/7730/7731 7901/7731/7732 7898/7728/7729 7899/7729/7730\r\nf 7880/7710/7711 7879/7709/7710 7898/7728/7729 7901/7731/7732\r\nf 7884/7714/7715 7902/7732/7733 7903/7733/7734 7885/7715/7716\r\nf 7904/7734/7735 7905/7735/7736 7903/7733/7734 7902/7732/7733\r\nf 7906/7736/7737 7907/7737/7738 7903/7733/7734 7905/7735/7736\r\nf 7886/7716/7717 7885/7715/7716 7903/7733/7734 7907/7737/7738\r\nf 7886/7716/7717 7907/7737/7738 7908/7738/7739 7889/7719/7720\r\nf 7906/7736/7737 7909/7739/7740 7908/7738/7739 7907/7737/7738\r\nf 7910/7740/7741 7911/7741/7742 7908/7738/7739 7909/7739/7740\r\nf 7890/7720/7721 7889/7719/7720 7908/7738/7739 7911/7741/7742\r\nf 7894/7724/7725 7912/7742/7743 7913/7743/7744 7895/7725/7726\r\nf 7914/7744/7745 7915/7745/7746 7913/7743/7744 7912/7742/7743\r\nf 7916/7746/7747 7917/7747/7748 7913/7743/7744 7915/7745/7746\r\nf 7896/7726/7727 7895/7725/7726 7913/7743/7744 7917/7747/7748\r\nf 7896/7726/7727 7917/7747/7748 7918/7748/7749 7899/7729/7730\r\nf 7916/7746/7747 7919/7749/7750 7918/7748/7749 7917/7747/7748\r\nf 7920/7750/7751 7921/7751/7752 7918/7748/7749 7919/7749/7750\r\nf 7900/7730/7731 7899/7729/7730 7918/7748/7749 7921/7751/7752\r\nf 7904/7734/7735 7922/7752/7753 7923/7753/7754 7905/7735/7736\r\nf 7924/7754/7755 7925/7755/7756 7923/7753/7754 7922/7752/7753\r\nf 7926/7756/7757 7927/7757/7758 7923/7753/7754 7925/7755/7756\r\nf 7906/7736/7737 7905/7735/7736 7923/7753/7754 7927/7757/7758\r\nf 7906/7736/7737 7927/7757/7758 7928/7758/7759 7909/7739/7740\r\nf 7926/7756/7757 7929/7759/7760 7928/7758/7759 7927/7757/7758\r\nf 7930/7760/7761 7931/7761/7762 7928/7758/7759 7929/7759/7760\r\nf 7910/7740/7741 7909/7739/7740 7928/7758/7759 7931/7761/7762\r\nf 7914/7744/7745 7932/7762/7763 7933/7763/7764 7915/7745/7746\r\nf 7934/7764/7765 7935/7765/7766 7933/7763/7764 7932/7762/7763\r\nf 7936/7766/7767 7937/7767/7768 7933/7763/7764 7935/7765/7766\r\nf 7916/7746/7747 7915/7745/7746 7933/7763/7764 7937/7767/7768\r\nf 7916/7746/7747 7937/7767/7768 7938/7768/7769 7919/7749/7750\r\nf 7936/7766/7767 7939/7769/7770 7938/7768/7769 7937/7767/7768\r\nf 7940/7770/7771 7941/7771/7772 7938/7768/7769 7939/7769/7770\r\nf 7920/7750/7751 7919/7749/7750 7938/7768/7769 7941/7771/7772\r\nf 7924/7754/7755 7942/7772/7773 7943/7773/7774 7925/7755/7756\r\nf 7944/7774/7775 7945/7775/7776 7943/7773/7774 7942/7772/7773\r\nf 7946/7776/7777 7947/7777/7778 7943/7773/7774 7945/7775/7776\r\nf 7926/7756/7757 7925/7755/7756 7943/7773/7774 7947/7777/7778\r\nf 7926/7756/7757 7947/7777/7778 7948/7778/7779 7929/7759/7760\r\nf 7946/7776/7777 7949/7779/7780 7948/7778/7779 7947/7777/7778\r\nf 7950/7780/7781 7951/7781/7782 7948/7778/7779 7949/7779/7780\r\nf 7930/7760/7761 7929/7759/7760 7948/7778/7779 7951/7781/7782\r\nf 7934/7764/7765 7952/7782/7783 7953/7783/7784 7935/7765/7766\r\nf 7954/7784/7785 7955/7785/7786 7953/7783/7784 7952/7782/7783\r\nf 7956/7786/7787 7957/7787/7788 7953/7783/7784 7955/7785/7786\r\nf 7936/7766/7767 7935/7765/7766 7953/7783/7784 7957/7787/7788\r\nf 7936/7766/7767 7957/7787/7788 7958/7788/7789 7939/7769/7770\r\nf 7956/7786/7787 7959/7789/7790 7958/7788/7789 7957/7787/7788\r\nf 7960/7790/7791 7961/7791/7792 7958/7788/7789 7959/7789/7790\r\nf 7940/7770/7771 7939/7769/7770 7958/7788/7789 7961/7791/7792\r\nf 7944/7774/7775 7962/7792/7793 7963/7793/7794 7945/7775/7776\r\nf 7964/7794/7795 7965/7795/7796 7963/7793/7794 7962/7792/7793\r\nf 7966/7796/7797 7967/7797/7798 7963/7793/7794 7965/7795/7796\r\nf 7946/7776/7777 7945/7775/7776 7963/7793/7794 7967/7797/7798\r\nf 7946/7776/7777 7967/7797/7798 7968/7798/7799 7949/7779/7780\r\nf 7966/7796/7797 7969/7799/7800 7968/7798/7799 7967/7797/7798\r\nf 7970/7800/7801 7971/7801/7802 7968/7798/7799 7969/7799/7800\r\nf 7950/7780/7781 7949/7779/7780 7968/7798/7799 7971/7801/7802\r\nf 7954/7784/7785 7972/7802/7803 7973/7803/7804 7955/7785/7786\r\nf 7974/7804/7805 7975/7805/7806 7973/7803/7804 7972/7802/7803\r\nf 7976/7806/7807 7977/7807/7808 7973/7803/7804 7975/7805/7806\r\nf 7956/7786/7787 7955/7785/7786 7973/7803/7804 7977/7807/7808\r\nf 7956/7786/7787 7977/7807/7808 7978/7808/7809 7959/7789/7790\r\nf 7976/7806/7807 7979/7809/7810 7978/7808/7809 7977/7807/7808\r\nf 7980/7810/7811 7981/7811/7812 7978/7808/7809 7979/7809/7810\r\nf 7960/7790/7791 7959/7789/7790 7978/7808/7809 7981/7811/7812\r\nf 7964/7794/7795 7982/7812/7813 7983/7813/7814 7965/7795/7796\r\nf 7984/7814/7815 7985/7815/7816 7983/7813/7814 7982/7812/7813\r\nf 7986/7816/7817 7987/7817/7818 7983/7813/7814 7985/7815/7816\r\nf 7966/7796/7797 7965/7795/7796 7983/7813/7814 7987/7817/7818\r\nf 7966/7796/7797 7987/7817/7818 7988/7818/7819 7969/7799/7800\r\nf 7986/7816/7817 7989/7819/7820 7988/7818/7819 7987/7817/7818\r\nf 7990/7820/7821 7991/7821/7822 7988/7818/7819 7989/7819/7820\r\nf 7970/7800/7801 7969/7799/7800 7988/7818/7819 7991/7821/7822\r\nf 7974/7804/7805 7992/7822/7823 7993/7823/7824 7975/7805/7806\r\nf 7994/7824/7825 7995/7825/7826 7993/7823/7824 7992/7822/7823\r\nf 7996/7826/7827 7997/7827/7828 7993/7823/7824 7995/7825/7826\r\nf 7976/7806/7807 7975/7805/7806 7993/7823/7824 7997/7827/7828\r\nf 7976/7806/7807 7997/7827/7828 7998/7828/7829 7979/7809/7810\r\nf 7996/7826/7827 7999/7829/7830 7998/7828/7829 7997/7827/7828\r\nf 8000/7830/7831 8001/7831/7832 7998/7828/7829 7999/7829/7830\r\nf 7980/7810/7811 7979/7809/7810 7998/7828/7829 8001/7831/7832\r\nf 7984/7814/7815 8002/7832/7833 8003/7833/7834 7985/7815/7816\r\nf 8004/7834/7835 8005/7835/7836 8003/7833/7834 8002/7832/7833\r\nf 8006/7836/7837 8007/7837/7838 8003/7833/7834 8005/7835/7836\r\nf 7986/7816/7817 7985/7815/7816 8003/7833/7834 8007/7837/7838\r\nf 7986/7816/7817 8007/7837/7838 8008/7838/7839 7989/7819/7820\r\nf 8006/7836/7837 8009/7839/7840 8008/7838/7839 8007/7837/7838\r\nf 8010/7840/7841 8011/7841/7842 8008/7838/7839 8009/7839/7840\r\nf 7990/7820/7821 7989/7819/7820 8008/7838/7839 8011/7841/7842\r\nf 7994/7824/7825 8012/7842/7843 8013/7843/7844 7995/7825/7826\r\nf 8014/7844/7845 8015/7845/7846 8013/7843/7844 8012/7842/7843\r\nf 8016/7846/7847 8017/7847/7848 8013/7843/7844 8015/7845/7846\r\nf 7996/7826/7827 7995/7825/7826 8013/7843/7844 8017/7847/7848\r\nf 7996/7826/7827 8017/7847/7848 8018/7848/7849 7999/7829/7830\r\nf 8016/7846/7847 8019/7849/7850 8018/7848/7849 8017/7847/7848\r\nf 8020/7850/7851 8021/7851/7852 8018/7848/7849 8019/7849/7850\r\nf 8000/7830/7831 7999/7829/7830 8018/7848/7849 8021/7851/7852\r\nf 8004/7834/7835 8022/7852/7853 8023/7853/7854 8005/7835/7836\r\nf 8024/7854/7855 8025/7855/7856 8023/7853/7854 8022/7852/7853\r\nf 8026/7856/7857 8027/7857/7858 8023/7853/7854 8025/7855/7856\r\nf 8006/7836/7837 8005/7835/7836 8023/7853/7854 8027/7857/7858\r\nf 8006/7836/7837 8027/7857/7858 8028/7858/7859 8009/7839/7840\r\nf 8026/7856/7857 8029/7859/7860 8028/7858/7859 8027/7857/7858\r\nf 8030/7860/7861 8031/7861/7862 8028/7858/7859 8029/7859/7860\r\nf 8010/7840/7841 8009/7839/7840 8028/7858/7859 8031/7861/7862\r\nf 8014/7844/7845 8032/7862/7863 8033/7863/7864 8015/7845/7846\r\nf 8034/7864/7865 8035/7865/7866 8033/7863/7864 8032/7862/7863\r\nf 8036/7866/7867 8037/7867/7868 8033/7863/7864 8035/7865/7866\r\nf 8016/7846/7847 8015/7845/7846 8033/7863/7864 8037/7867/7868\r\nf 8016/7846/7847 8037/7867/7868 8038/7868/7869 8019/7849/7850\r\nf 8036/7866/7867 8039/7869/7870 8038/7868/7869 8037/7867/7868\r\nf 8040/7870/7871 8041/7871/7872 8038/7868/7869 8039/7869/7870\r\nf 8020/7850/7851 8019/7849/7850 8038/7868/7869 8041/7871/7872\r\nf 8024/7854/7855 8042/7872/7873 8043/7873/7874 8025/7855/7856\r\nf 8044/7874/7875 8045/7875/7876 8043/7873/7874 8042/7872/7873\r\nf 8046/7876/7877 8047/7877/7878 8043/7873/7874 8045/7875/7876\r\nf 8026/7856/7857 8025/7855/7856 8043/7873/7874 8047/7877/7878\r\nf 8026/7856/7857 8047/7877/7878 8048/7878/7879 8029/7859/7860\r\nf 8046/7876/7877 8049/7879/7880 8048/7878/7879 8047/7877/7878\r\nf 8050/7880/7881 8051/7881/7882 8048/7878/7879 8049/7879/7880\r\nf 8030/7860/7861 8029/7859/7860 8048/7878/7879 8051/7881/7882\r\nf 8034/7864/7865 8052/7882/7883 8053/7883/7884 8035/7865/7866\r\nf 8054/7884/7885 8055/7885/7886 8053/7883/7884 8052/7882/7883\r\nf 8056/7886/7887 8057/7887/7888 8053/7883/7884 8055/7885/7886\r\nf 8036/7866/7867 8035/7865/7866 8053/7883/7884 8057/7887/7888\r\nf 8036/7866/7867 8057/7887/7888 8058/7888/7889 8039/7869/7870\r\nf 8056/7886/7887 8059/7889/7890 8058/7888/7889 8057/7887/7888\r\nf 8060/7890/7891 8061/7891/7892 8058/7888/7889 8059/7889/7890\r\nf 8040/7870/7871 8039/7869/7870 8058/7888/7889 8061/7891/7892\r\nf 8044/7874/7875 8062/7892/7893 8063/7893/7894 8045/7875/7876\r\nf 8064/7894/7895 8065/7895/7896 8063/7893/7894 8062/7892/7893\r\nf 8066/7896/7897 8067/7897/7898 8063/7893/7894 8065/7895/7896\r\nf 8046/7876/7877 8045/7875/7876 8063/7893/7894 8067/7897/7898\r\nf 8046/7876/7877 8067/7897/7898 8068/7898/7899 8049/7879/7880\r\nf 8066/7896/7897 8069/7899/7900 8068/7898/7899 8067/7897/7898\r\nf 8070/7900/7901 8071/7901/7902 8068/7898/7899 8069/7899/7900\r\nf 8050/7880/7881 8049/7879/7880 8068/7898/7899 8071/7901/7902\r\nf 8054/7884/7885 8072/7902/7903 8073/7903/7904 8055/7885/7886\r\nf 8074/7904/7905 8075/7905/7906 8073/7903/7904 8072/7902/7903\r\nf 8076/7906/7907 8077/7907/7908 8073/7903/7904 8075/7905/7906\r\nf 8056/7886/7887 8055/7885/7886 8073/7903/7904 8077/7907/7908\r\nf 8056/7886/7887 8077/7907/7908 8078/7908/7909 8059/7889/7890\r\nf 8076/7906/7907 8079/7909/7910 8078/7908/7909 8077/7907/7908\r\nf 8080/7910/7911 8081/7911/7912 8078/7908/7909 8079/7909/7910\r\nf 8060/7890/7891 8059/7889/7890 8078/7908/7909 8081/7911/7912\r\nf 8064/7894/7895 8082/7912/7913 8083/7913/7914 8065/7895/7896\r\nf 8084/7914/7915 8085/7915/7916 8083/7913/7914 8082/7912/7913\r\nf 8086/7916/7917 8087/7917/7918 8083/7913/7914 8085/7915/7916\r\nf 8066/7896/7897 8065/7895/7896 8083/7913/7914 8087/7917/7918\r\nf 8066/7896/7897 8087/7917/7918 8088/7918/7919 8069/7899/7900\r\nf 8086/7916/7917 8089/7919/7920 8088/7918/7919 8087/7917/7918\r\nf 8090/7920/7921 8091/7921/7922 8088/7918/7919 8089/7919/7920\r\nf 8070/7900/7901 8069/7899/7900 8088/7918/7919 8091/7921/7922\r\nf 8074/7904/7905 8092/7922/7923 8093/7923/7924 8075/7905/7906\r\nf 8094/7924/7925 8095/7925/7926 8093/7923/7924 8092/7922/7923\r\nf 8096/7926/7927 8097/7927/7928 8093/7923/7924 8095/7925/7926\r\nf 8076/7906/7907 8075/7905/7906 8093/7923/7924 8097/7927/7928\r\nf 8076/7906/7907 8097/7927/7928 8098/7928/7929 8079/7909/7910\r\nf 8096/7926/7927 8099/7929/7930 8098/7928/7929 8097/7927/7928\r\nf 8100/7930/7931 8101/7931/7932 8098/7928/7929 8099/7929/7930\r\nf 8080/7910/7911 8079/7909/7910 8098/7928/7929 8101/7931/7932\r\nf 8084/7914/7915 8102/7932/7933 8103/7933/7934 8085/7915/7916\r\nf 8104/7934/7935 8105/7935/7936 8103/7933/7934 8102/7932/7933\r\nf 8106/7936/7937 8107/7937/7938 8103/7933/7934 8105/7935/7936\r\nf 8086/7916/7917 8085/7915/7916 8103/7933/7934 8107/7937/7938\r\nf 8086/7916/7917 8107/7937/7938 8108/7938/7939 8089/7919/7920\r\nf 8106/7936/7937 8109/7939/7940 8108/7938/7939 8107/7937/7938\r\nf 8110/7940/7941 8111/7941/7942 8108/7938/7939 8109/7939/7940\r\nf 8090/7920/7921 8089/7919/7920 8108/7938/7939 8111/7941/7942\r\nf 8094/7924/7925 8112/7942/7943 8113/7943/7944 8095/7925/7926\r\nf 8114/7944/7945 8115/7945/7946 8113/7943/7944 8112/7942/7943\r\nf 8116/7946/7947 8117/7947/7948 8113/7943/7944 8115/7945/7946\r\nf 8096/7926/7927 8095/7925/7926 8113/7943/7944 8117/7947/7948\r\nf 8096/7926/7927 8117/7947/7948 8118/7948/7949 8099/7929/7930\r\nf 8116/7946/7947 8119/7949/7950 8118/7948/7949 8117/7947/7948\r\nf 8120/7950/7951 8121/7951/7952 8118/7948/7949 8119/7949/7950\r\nf 8100/7930/7931 8099/7929/7930 8118/7948/7949 8121/7951/7952\r\nf 8104/7934/7935 8122/7952/7953 8123/7953/7954 8105/7935/7936\r\nf 8124/7954/7955 8125/7955/7956 8123/7953/7954 8122/7952/7953\r\nf 8126/7956/7957 8127/7957/7958 8123/7953/7954 8125/7955/7956\r\nf 8106/7936/7937 8105/7935/7936 8123/7953/7954 8127/7957/7958\r\nf 8106/7936/7937 8127/7957/7958 8128/7958/7959 8109/7939/7940\r\nf 8126/7956/7957 8129/7959/7960 8128/7958/7959 8127/7957/7958\r\nf 8130/7960/7961 8131/7961/7962 8128/7958/7959 8129/7959/7960\r\nf 8110/7940/7941 8109/7939/7940 8128/7958/7959 8131/7961/7962\r\nf 8114/7944/7945 8132/7962/7963 8133/7963/7964 8115/7945/7946\r\nf 8134/7964/7965 8135/7965/7966 8133/7963/7964 8132/7962/7963\r\nf 8136/7966/7967 8137/7967/7968 8133/7963/7964 8135/7965/7966\r\nf 8116/7946/7947 8115/7945/7946 8133/7963/7964 8137/7967/7968\r\nf 8116/7946/7947 8137/7967/7968 8138/7968/7969 8119/7949/7950\r\nf 8136/7966/7967 8139/7969/7970 8138/7968/7969 8137/7967/7968\r\nf 8140/7970/7971 8141/7971/7972 8138/7968/7969 8139/7969/7970\r\nf 8120/7950/7951 8119/7949/7950 8138/7968/7969 8141/7971/7972\r\nf 8124/7954/7955 8142/7972/7973 8143/7973/7974 8125/7955/7956\r\nf 8144/7974/7975 8145/7975/7976 8143/7973/7974 8142/7972/7973\r\nf 8146/7976/7977 8147/7977/7978 8143/7973/7974 8145/7975/7976\r\nf 8126/7956/7957 8125/7955/7956 8143/7973/7974 8147/7977/7978\r\nf 8126/7956/7957 8147/7977/7978 8148/7978/7979 8129/7959/7960\r\nf 8146/7976/7977 8149/7979/7980 8148/7978/7979 8147/7977/7978\r\nf 8150/7980/7981 8151/7981/7982 8148/7978/7979 8149/7979/7980\r\nf 8130/7960/7961 8129/7959/7960 8148/7978/7979 8151/7981/7982\r\nf 8134/7964/7965 8152/7982/7983 8153/7983/7984 8135/7965/7966\r\nf 8154/7984/7985 8155/7985/7986 8153/7983/7984 8152/7982/7983\r\nf 8156/7986/7987 8157/7987/7988 8153/7983/7984 8155/7985/7986\r\nf 8136/7966/7967 8135/7965/7966 8153/7983/7984 8157/7987/7988\r\nf 8136/7966/7967 8157/7987/7988 8158/7988/7989 8139/7969/7970\r\nf 8156/7986/7987 8159/7989/7990 8158/7988/7989 8157/7987/7988\r\nf 8160/7990/7991 8161/7991/7992 8158/7988/7989 8159/7989/7990\r\nf 8140/7970/7971 8139/7969/7970 8158/7988/7989 8161/7991/7992\r\nf 8144/7974/7975 8162/7992/7993 8163/7993/7994 8145/7975/7976\r\nf 8164/7994/7995 8165/7995/7996 8163/7993/7994 8162/7992/7993\r\nf 8166/7996/7997 8167/7997/7998 8163/7993/7994 8165/7995/7996\r\nf 8146/7976/7977 8145/7975/7976 8163/7993/7994 8167/7997/7998\r\nf 8146/7976/7977 8167/7997/7998 8168/7998/7999 8149/7979/7980\r\nf 8166/7996/7997 8169/7999/8000 8168/7998/7999 8167/7997/7998\r\nf 8170/8000/8001 8171/8001/8002 8168/7998/7999 8169/7999/8000\r\nf 8150/7980/7981 8149/7979/7980 8168/7998/7999 8171/8001/8002\r\nf 8154/7984/7985 8172/8002/8003 8173/8003/8004 8155/7985/7986\r\nf 8174/8004/8005 8175/8005/8006 8173/8003/8004 8172/8002/8003\r\nf 8176/8006/8007 8177/8007/8008 8173/8003/8004 8175/8005/8006\r\nf 8156/7986/7987 8155/7985/7986 8173/8003/8004 8177/8007/8008\r\nf 8156/7986/7987 8177/8007/8008 8178/8008/8009 8159/7989/7990\r\nf 8176/8006/8007 8179/8009/8010 8178/8008/8009 8177/8007/8008\r\nf 8180/8010/8011 8181/8011/8012 8178/8008/8009 8179/8009/8010\r\nf 8160/7990/7991 8159/7989/7990 8178/8008/8009 8181/8011/8012\r\nf 8174/8004/8005 8182/8012/8013 8183/8013/8014 8175/8005/8006\r\nf 8184/8014/8015 8185/8015/8016 8183/8013/8014 8182/8012/8013\r\nf 8180/8010/8011 8179/8009/8010 8183/8013/8014 8185/8015/8016\r\nf 8176/8006/8007 8175/8005/8006 8183/8013/8014 8179/8009/8010\r\nf 8186/8016/5867 8187/8017/8017 8188/8018/8018 8189/8019/8019\r\nf 6031/5861/5874 6028/5858/5871 8188/8018/8018 8187/8017/8017\r\nf 6027/5857/5870 8190/8020/8020 8188/8018/8018 6028/5858/5871\r\nf 8191/8021/8021 8189/8019/8019 8188/8018/8018 8190/8020/8020\r\nf 6039/5869/5882 8192/8022/5882 8193/8023/5883 6040/5870/5883\r\nf 8194/8024/5882 8195/8025/8022 8193/8026/5883 8192/8027/5882\r\nf 7320/7150/7157 8196/8028/8023 8193/8026/5883 8195/8025/8022\r\nf 6041/5871/5884 6040/5870/5883 8193/8023/5883 8196/8029/8023\r\nf 8197/8030/8024 8198/8031/8025 8199/8032/8026 8200/8033/5893\r\nf 6054/5884/5895 6052/5882/5894 8199/8032/8026 8198/8031/8025\r\nf 6031/5861/5874 8187/8017/8017 8199/8032/8026 6052/5882/5894\r\nf 8186/8016/5867 8200/8033/5893 8199/8032/8026 8187/8017/8017\r\nf 6060/5890/5901 8201/8034/5901 8202/8035/5902 6061/5891/5902\r\nf 8203/8036/8027 8204/8037/5902 8202/8038/5902 8201/8039/5901\r\nf 8194/8024/5882 8192/8027/5882 8202/8038/5902 8204/8037/5902\r\nf 6039/5869/5882 6061/5891/5902 8202/8035/5902 8192/8022/5882\r\nf 8205/8040/5911 8206/8041/8028 8207/8042/8029 8208/8043/5912\r\nf 6074/5904/5915 6072/5902/5913 8207/8042/8029 8206/8041/8028\r\nf 6054/5884/5895 8198/8031/8025 8207/8042/8029 6072/5902/5913\r\nf 8197/8030/8024 8208/8043/5912 8207/8042/8029 8198/8031/8025\r\nf 6080/5910/5921 8209/8044/5921 8210/8045/5922 6081/5911/5922\r\nf 8211/8046/5921 8212/8047/5922 8210/8048/5922 8209/8049/5921\r\nf 8203/8036/8027 8201/8039/5901 8210/8048/5922 8212/8047/5922\r\nf 6060/5890/5901 6081/5911/5922 8210/8045/5922 8201/8034/5901\r\nf 8213/8050/5931 8214/8051/8030 8215/8052/8031 8216/8053/8032\r\nf 6094/5924/5935 6092/5922/5933 8215/8052/8031 8214/8051/8030\r\nf 6074/5904/5915 8206/8041/8028 8215/8052/8031 6092/5922/5933\r\nf 8205/8040/5911 8216/8053/8032 8215/8052/8031 8206/8041/8028\r\nf 6100/5930/5941 8217/8054/5941 8218/8055/8033 6101/5931/5942\r\nf 8219/8056/5941 8220/8057/8033 8218/8058/8033 8217/8059/5941\r\nf 8211/8046/5921 8209/8049/5921 8218/8058/8033 8220/8057/8033\r\nf 6080/5910/5921 6101/5931/5942 8218/8055/8033 8209/8044/5921\r\nf 8221/8060/5951 8222/8061/8034 8223/8062/8035 8224/8063/5952\r\nf 6114/5944/5955 6112/5942/5953 8223/8062/8035 8222/8061/8034\r\nf 6094/5924/5935 8214/8051/8030 8223/8062/8035 6112/5942/5953\r\nf 8213/8050/5931 8224/8063/5952 8223/8062/8035 8214/8051/8030\r\nf 6120/5950/5961 8225/8064/5961 8226/8065/5962 6121/5951/5962\r\nf 8227/8066/5961 8228/8067/5962 8226/8068/5962 8225/8069/5961\r\nf 8219/8056/5941 8217/8059/5941 8226/8068/5962 8228/8067/5962\r\nf 6100/5930/5941 6121/5951/5962 8226/8065/5962 8217/8054/5941\r\nf 8229/8070/5971 8230/8071/8036 8231/8072/8037 8232/8073/5972\r\nf 6134/5964/5975 6132/5962/5973 8231/8072/8037 8230/8071/8036\r\nf 6114/5944/5955 8222/8061/8034 8231/8072/8037 6132/5962/5973\r\nf 8221/8060/5951 8232/8073/5972 8231/8072/8037 8222/8061/8034\r\nf 6140/5970/5981 8233/8074/5981 8234/8075/5982 6141/5971/5982\r\nf 8235/8076/5981 8236/8077/5982 8234/8078/5982 8233/8079/5981\r\nf 8227/8066/5961 8225/8069/5961 8234/8078/5982 8236/8077/5982\r\nf 6120/5950/5961 6141/5971/5982 8234/8075/5982 8225/8064/5961\r\nf 8237/8080/8038 8238/8081/8039 8239/8082/8040 8240/8083/5992\r\nf 6154/5984/5995 6152/5982/5993 8239/8082/8040 8238/8081/8039\r\nf 6134/5964/5975 8230/8071/8036 8239/8082/8040 6152/5982/5993\r\nf 8229/8070/5971 8240/8083/5992 8239/8082/8040 8230/8071/8036\r\nf 6160/5990/6001 8241/8084/6001 8242/8085/6002 6161/5991/6002\r\nf 8243/8086/6001 8244/8087/6002 8242/8088/6002 8241/8089/6001\r\nf 8235/8076/5981 8233/8079/5981 8242/8088/6002 8244/8087/6002\r\nf 6140/5970/5981 6161/5991/6002 8242/8085/6002 8233/8074/5981\r\nf 8245/8090/8041 8246/8091/8042 8247/8092/8043 8248/8093/6012\r\nf 6174/6004/6015 6172/6002/6013 8247/8092/8043 8246/8091/8042\r\nf 6154/5984/5995 8238/8081/8039 8247/8092/8043 6172/6002/6013\r\nf 8237/8080/8038 8248/8093/6012 8247/8092/8043 8238/8081/8039\r\nf 6180/6010/6021 8249/8094/8044 8250/8095/6022 6181/6011/6022\r\nf 8251/8096/8044 8252/8097/8045 8250/8098/6022 8249/8099/8044\r\nf 8243/8086/6001 8241/8089/6001 8250/8098/6022 8252/8097/8045\r\nf 6160/5990/6001 6181/6011/6022 8250/8095/6022 8241/8084/6001\r\nf 8253/8100/6031 8254/8101/8046 8255/8102/8047 8256/8103/8048\r\nf 6194/6024/6035 6192/6022/6033 8255/8102/8047 8254/8101/8046\r\nf 6174/6004/6015 8246/8091/8042 8255/8102/8047 6192/6022/6033\r\nf 8245/8090/8041 8256/8103/8048 8255/8102/8047 8246/8091/8042\r\nf 6200/6030/6041 8257/8104/6041 8258/8105/6042 6201/6031/6042\r\nf 8259/8106/6041 8260/8107/8049 8258/8108/6042 8257/8109/6041\r\nf 8251/8096/8044 8249/8099/8044 8258/8108/6042 8260/8107/8049\r\nf 6180/6010/6021 6201/6031/6042 8258/8105/6042 8249/8094/8044\r\nf 8261/8110/6051 8262/8111/8050 8263/8112/8051 8264/8113/6052\r\nf 6214/6044/6055 6212/6042/6053 8263/8112/8051 8262/8111/8050\r\nf 6194/6024/6035 8254/8101/8046 8263/8112/8051 6212/6042/6053\r\nf 8253/8100/6031 8264/8113/6052 8263/8112/8051 8254/8101/8046\r\nf 6220/6050/6061 8265/8114/6061 8266/8115/6062 6221/6051/6062\r\nf 8267/8116/6061 8268/8117/8052 8266/8118/6062 8265/8119/6061\r\nf 8259/8106/6041 8257/8109/6041 8266/8118/6062 8268/8117/8052\r\nf 6200/6030/6041 6221/6051/6062 8266/8115/6062 8257/8104/6041\r\nf 8269/8120/6071 8270/8121/8053 8271/8122/8054 8272/8123/8055\r\nf 6234/6064/6075 6232/6062/6073 8271/8122/8054 8270/8121/8053\r\nf 6214/6044/6055 8262/8111/8050 8271/8122/8054 6232/6062/6073\r\nf 8261/8110/6051 8272/8123/8055 8271/8122/8054 8262/8111/8050\r\nf 6240/6070/6081 8273/8124/6081 8274/8125/8056 6241/6071/6082\r\nf 8275/8126/6081 8276/8127/8056 8274/8128/8056 8273/8129/6081\r\nf 8267/8116/6061 8265/8119/6061 8274/8128/8056 8276/8127/8056\r\nf 6220/6050/6061 6241/6071/6082 8274/8125/8056 8265/8114/6061\r\nf 8277/8130/6091 8278/8131/8057 8279/8132/8058 8280/8133/6092\r\nf 6254/6084/6095 6252/6082/6093 8279/8132/8058 8278/8131/8057\r\nf 6234/6064/6075 8270/8121/8053 8279/8132/8058 6252/6082/6093\r\nf 8269/8120/6071 8280/8133/6092 8279/8132/8058 8270/8121/8053\r\nf 6260/6090/6101 8281/8134/8059 8282/8135/6102 6261/6091/6102\r\nf 8283/8136/6101 8284/8137/6102 8282/8138/6102 8281/8139/8059\r\nf 8275/8126/6081 8273/8129/6081 8282/8138/6102 8284/8137/6102\r\nf 6240/6070/6081 6261/6091/6102 8282/8135/6102 8273/8124/6081\r\nf 8285/8140/6111 8286/8141/8060 8287/8142/8061 8288/8143/6112\r\nf 6274/6104/6115 6272/6102/6113 8287/8142/8061 8286/8141/8060\r\nf 6254/6084/6095 8278/8131/8057 8287/8142/8061 6272/6102/6113\r\nf 8277/8130/6091 8288/8143/6112 8287/8142/8061 8278/8131/8057\r\nf 6280/6110/6121 8289/8144/6121 8290/8145/6122 6281/6111/6122\r\nf 8291/8146/6121 8292/8147/6122 8290/8148/6122 8289/8149/6121\r\nf 8283/8136/6101 8281/8139/8059 8290/8148/6122 8292/8147/6122\r\nf 6260/6090/6101 6281/6111/6122 8290/8145/6122 8281/8134/8059\r\nf 8293/8150/6131 8294/8151/8062 8295/8152/8063 8296/8153/6132\r\nf 6294/6124/6135 6292/6122/6133 8295/8152/8063 8294/8151/8062\r\nf 6274/6104/6115 8286/8141/8060 8295/8152/8063 6292/6122/6133\r\nf 8285/8140/6111 8296/8153/6132 8295/8152/8063 8286/8141/8060\r\nf 6300/6130/6141 8297/8154/6141 8298/8155/6142 6301/6131/6142\r\nf 8299/8156/6141 8300/8157/8064 8298/8158/6142 8297/8159/6141\r\nf 8291/8146/6121 8289/8149/6121 8298/8158/6142 8300/8157/8064\r\nf 6280/6110/6121 6301/6131/6142 8298/8155/6142 8289/8144/6121\r\nf 8301/8160/6151 8302/8161/8065 8303/8162/8066 8304/8163/6152\r\nf 6314/6144/6155 6312/6142/6153 8303/8162/8066 8302/8161/8065\r\nf 6294/6124/6135 8294/8151/8062 8303/8162/8066 6312/6142/6153\r\nf 8293/8150/6131 8304/8163/6152 8303/8162/8066 8294/8151/8062\r\nf 6320/6150/6161 8305/8164/6161 8306/8165/6162 6321/6151/6162\r\nf 8307/8166/6161 8308/8167/6162 8306/8168/6162 8305/8169/6161\r\nf 8299/8156/6141 8297/8159/6141 8306/8168/6162 8308/8167/6162\r\nf 6300/6130/6141 6321/6151/6162 8306/8165/6162 8297/8154/6141\r\nf 8309/8170/6171 8310/8171/8067 8311/8172/8068 8312/8173/6172\r\nf 6334/6164/6175 6332/6162/6173 8311/8172/8068 8310/8171/8067\r\nf 6314/6144/6155 8302/8161/8065 8311/8172/8068 6332/6162/6173\r\nf 8301/8160/6151 8312/8173/6172 8311/8172/8068 8302/8161/8065\r\nf 6340/6170/6181 8313/8174/6181 8314/8175/6182 6341/6171/6182\r\nf 8315/8176/6181 8316/8177/6182 8314/8178/6182 8313/8179/6181\r\nf 8307/8166/6161 8305/8169/6161 8314/8178/6182 8316/8177/6182\r\nf 6320/6150/6161 6341/6171/6182 8314/8175/6182 8305/8164/6161\r\nf 8317/8180/6191 8318/8181/8069 8319/8182/8070 8320/8183/6192\r\nf 6354/6184/6195 6352/6182/6193 8319/8182/8070 8318/8181/8069\r\nf 6334/6164/6175 8310/8171/8067 8319/8182/8070 6352/6182/6193\r\nf 8309/8170/6171 8320/8183/6192 8319/8182/8070 8310/8171/8067\r\nf 6360/6190/6201 8321/8184/6201 8322/8185/6202 6361/6191/6202\r\nf 8323/8186/6201 8324/8187/6202 8322/8188/6202 8321/8189/6201\r\nf 8315/8176/6181 8313/8179/6181 8322/8188/6202 8324/8187/6202\r\nf 6340/6170/6181 6361/6191/6202 8322/8185/6202 8313/8174/6181\r\nf 8325/8190/6211 8326/8191/8071 8327/8192/8072 8328/8193/6212\r\nf 6374/6204/6215 6372/6202/6213 8327/8192/8072 8326/8191/8071\r\nf 6354/6184/6195 8318/8181/8069 8327/8192/8072 6372/6202/6213\r\nf 8317/8180/6191 8328/8193/6212 8327/8192/8072 8318/8181/8069\r\nf 6380/6210/6221 8329/8194/6221 8330/8195/6222 6381/6211/6222\r\nf 8331/8196/6221 8332/8197/6222 8330/8198/6222 8329/8199/6221\r\nf 8323/8186/6201 8321/8189/6201 8330/8198/6222 8332/8197/6222\r\nf 6360/6190/6201 6381/6211/6222 8330/8195/6222 8321/8184/6201\r\nf 8333/8200/6230 8334/8201/8073 8335/8202/8074 8336/8203/6231\r\nf 6394/6224/6234 6392/6222/6232 8335/8202/8074 8334/8201/8073\r\nf 6374/6204/6215 8326/8191/8071 8335/8202/8074 6392/6222/6232\r\nf 8325/8190/6211 8336/8203/6231 8335/8202/8074 8326/8191/8071\r\nf 6400/6230/6239 8337/8204/6239 8338/8205/6240 6401/6231/6240\r\nf 8339/8206/6239 8340/8207/6240 8338/8208/6240 8337/8209/6239\r\nf 8331/8196/6221 8329/8199/6221 8338/8208/6240 8340/8207/6240\r\nf 6380/6210/6221 6401/6231/6240 8338/8205/6240 8329/8194/6221\r\nf 8341/8210/6249 8342/8211/8075 8343/8212/8076 8344/8213/6250\r\nf 6414/6244/6253 6412/6242/6251 8343/8212/8076 8342/8211/8075\r\nf 6394/6224/6234 8334/8201/8073 8343/8212/8076 6412/6242/6251\r\nf 8333/8200/6230 8344/8213/6250 8343/8212/8076 8334/8201/8073\r\nf 6420/6250/6259 8345/8214/6259 8346/8215/6260 6421/6251/6260\r\nf 8347/8216/6259 8348/8217/6260 8346/8218/6260 8345/8219/6259\r\nf 8339/8206/6239 8337/8209/6239 8346/8218/6260 8348/8217/6260\r\nf 6400/6230/6239 6421/6251/6260 8346/8215/6260 8337/8204/6239\r\nf 8349/8220/6269 8350/8221/8077 8351/8222/8078 8352/8223/6270\r\nf 6434/6264/6273 6432/6262/6271 8351/8222/8078 8350/8221/8077\r\nf 6414/6244/6253 8342/8211/8075 8351/8222/8078 6432/6262/6271\r\nf 8341/8210/6249 8352/8223/6270 8351/8222/8078 8342/8211/8075\r\nf 6440/6270/6279 8353/8224/6279 8354/8225/6280 6441/6271/6280\r\nf 8355/8226/6279 8356/8227/6280 8354/8228/6280 8353/8229/6279\r\nf 8347/8216/6259 8345/8219/6259 8354/8228/6280 8356/8227/6280\r\nf 6420/6250/6259 6441/6271/6280 8354/8225/6280 8345/8214/6259\r\nf 8357/8230/6289 8358/8231/8079 8359/8232/8080 8360/8233/6290\r\nf 6454/6284/6293 6452/6282/6291 8359/8232/8080 8358/8231/8079\r\nf 6434/6264/6273 8350/8221/8077 8359/8232/8080 6452/6282/6291\r\nf 8349/8220/6269 8360/8233/6290 8359/8232/8080 8350/8221/8077\r\nf 6460/6290/6299 8361/8234/6299 8362/8235/6300 6461/6291/6300\r\nf 8363/8236/6299 8364/8237/6300 8362/8238/6300 8361/8239/6299\r\nf 8355/8226/6279 8353/8229/6279 8362/8238/6300 8364/8237/6300\r\nf 6440/6270/6279 6461/6291/6300 8362/8235/6300 8353/8224/6279\r\nf 8365/8240/6309 8366/8241/8081 8367/8242/8082 8368/8243/6310\r\nf 6474/6304/6313 6472/6302/6311 8367/8242/8082 8366/8241/8081\r\nf 6454/6284/6293 8358/8231/8079 8367/8242/8082 6472/6302/6311\r\nf 8357/8230/6289 8368/8243/6310 8367/8242/8082 8358/8231/8079\r\nf 6480/6310/6319 8369/8244/6319 8370/8245/6320 6481/6311/6320\r\nf 8371/8246/6319 8372/8247/6320 8370/8248/6320 8369/8249/6319\r\nf 8363/8236/6299 8361/8239/6299 8370/8248/6320 8372/8247/6320\r\nf 6460/6290/6299 6481/6311/6320 8370/8245/6320 8361/8234/6299\r\nf 8373/8250/6329 8374/8251/8083 8375/8252/8084 8376/8253/6330\r\nf 6494/6324/6333 6492/6322/6331 8375/8252/8084 8374/8251/8083\r\nf 6474/6304/6313 8366/8241/8081 8375/8252/8084 6492/6322/6331\r\nf 8365/8240/6309 8376/8253/6330 8375/8252/8084 8366/8241/8081\r\nf 6500/6330/6339 8377/8254/6339 8378/8255/6340 6501/6331/6340\r\nf 8379/8256/6339 8380/8257/6340 8378/8258/6340 8377/8259/6339\r\nf 8371/8246/6319 8369/8249/6319 8378/8258/6340 8380/8257/6340\r\nf 6480/6310/6319 6501/6331/6340 8378/8255/6340 8369/8244/6319\r\nf 8381/8260/6349 8382/8261/8085 8383/8262/8086 8384/8263/6350\r\nf 6514/6344/6353 6512/6342/6351 8383/8262/8086 8382/8261/8085\r\nf 6494/6324/6333 8374/8251/8083 8383/8262/8086 6512/6342/6351\r\nf 8373/8250/6329 8384/8263/6350 8383/8262/8086 8374/8251/8083\r\nf 6520/6350/6359 8385/8264/6359 8386/8265/6360 6521/6351/6360\r\nf 8387/8266/6359 8388/8267/6360 8386/8268/6360 8385/8269/6359\r\nf 8379/8256/6339 8377/8259/6339 8386/8268/6360 8388/8267/6360\r\nf 6500/6330/6339 6521/6351/6360 8386/8265/6360 8377/8254/6339\r\nf 8389/8270/6369 8390/8271/8087 8391/8272/8088 8392/8273/6370\r\nf 6534/6364/6373 6532/6362/6371 8391/8272/8088 8390/8271/8087\r\nf 6514/6344/6353 8382/8261/8085 8391/8272/8088 6532/6362/6371\r\nf 8381/8260/6349 8392/8273/6370 8391/8272/8088 8382/8261/8085\r\nf 6540/6370/6379 8393/8274/6379 8394/8275/6380 6541/6371/6380\r\nf 8395/8276/6379 8396/8277/6380 8394/8278/6380 8393/8279/6379\r\nf 8387/8266/6359 8385/8269/6359 8394/8278/6380 8396/8277/6380\r\nf 6520/6350/6359 6541/6371/6380 8394/8275/6380 8385/8264/6359\r\nf 8397/8280/6389 8398/8281/8089 8399/8282/8090 8400/8283/6390\r\nf 6554/6384/6393 6552/6382/6391 8399/8282/8090 8398/8281/8089\r\nf 6534/6364/6373 8390/8271/8087 8399/8282/8090 6552/6382/6391\r\nf 8389/8270/6369 8400/8283/6390 8399/8282/8090 8390/8271/8087\r\nf 6560/6390/6399 8401/8284/6399 8402/8285/6400 6561/6391/6400\r\nf 8403/8286/6399 8404/8287/6400 8402/8288/6400 8401/8289/6399\r\nf 8395/8276/6379 8393/8279/6379 8402/8288/6400 8404/8287/6400\r\nf 6540/6370/6379 6561/6391/6400 8402/8285/6400 8393/8274/6379\r\nf 8405/8290/6409 8406/8291/8091 8407/8292/8092 8408/8293/6410\r\nf 6574/6404/6413 6572/6402/6411 8407/8292/8092 8406/8291/8091\r\nf 6554/6384/6393 8398/8281/8089 8407/8292/8092 6572/6402/6411\r\nf 8397/8280/6389 8408/8293/6410 8407/8292/8092 8398/8281/8089\r\nf 6580/6410/6419 8409/8294/6419 8410/8295/6420 6581/6411/6420\r\nf 8411/8296/6419 8412/8297/6420 8410/8298/6420 8409/8299/6419\r\nf 8403/8286/6399 8401/8289/6399 8410/8298/6420 8412/8297/6420\r\nf 6560/6390/6399 6581/6411/6420 8410/8295/6420 8401/8284/6399\r\nf 8413/8300/6429 8414/8301/8093 8415/8302/8094 8416/8303/6430\r\nf 6594/6424/6433 6592/6422/6431 8415/8302/8094 8414/8301/8093\r\nf 6574/6404/6413 8406/8291/8091 8415/8302/8094 6592/6422/6431\r\nf 8405/8290/6409 8416/8303/6430 8415/8302/8094 8406/8291/8091\r\nf 6600/6430/6439 8417/8304/8095 8418/8305/8096 6601/6431/6440\r\nf 8419/8306/8095 8420/8307/8096 8418/8308/8096 8417/8309/8095\r\nf 8411/8296/6419 8409/8299/6419 8418/8308/8096 8420/8307/8096\r\nf 6580/6410/6419 6601/6431/6440 8418/8305/8096 8409/8294/6419\r\nf 8421/8310/6449 8422/8311/8097 8423/8312/8098 8424/8313/8099\r\nf 6614/6444/6453 6612/6442/6451 8423/8312/8098 8422/8311/8097\r\nf 6594/6424/6433 8414/8301/8093 8423/8312/8098 6612/6442/6451\r\nf 8413/8300/6429 8424/8313/8099 8423/8312/8098 8414/8301/8093\r\nf 6620/6450/6459 8425/8314/8100 8426/8315/8101 6621/6451/6460\r\nf 8427/8316/8102 8428/8317/8101 8426/8318/8101 8425/8319/8100\r\nf 8419/8306/8095 8417/8309/8095 8426/8318/8101 8428/8317/8101\r\nf 6600/6430/6439 6621/6451/6460 8426/8315/8101 8417/8304/8095\r\nf 8429/8320/8103 8430/8321/8104 8431/8322/8105 8432/8323/6470\r\nf 6634/6464/6473 6632/6462/6471 8431/8322/8105 8430/8321/8104\r\nf 6614/6444/6453 8422/8311/8097 8431/8322/8105 6632/6462/6471\r\nf 8421/8310/6449 8432/8323/6470 8431/8322/8105 8422/8311/8097\r\nf 6640/6470/6479 8433/8324/8106 8434/8325/6480 6641/6471/6480\r\nf 8435/8326/8107 8436/8327/6480 8434/8328/6480 8433/8329/8106\r\nf 8427/8316/8102 8425/8319/8100 8434/8328/6480 8436/8327/6480\r\nf 6620/6450/6459 6641/6471/6480 8434/8325/6480 8425/8314/8100\r\nf 8437/8330/8108 8438/8331/8109 8439/8332/8110 8440/8333/8111\r\nf 6654/6484/6493 6652/6482/6491 8439/8332/8110 8438/8331/8109\r\nf 6634/6464/6473 8430/8321/8104 8439/8332/8110 6652/6482/6491\r\nf 8429/8320/8103 8440/8333/8111 8439/8332/8110 8430/8321/8104\r\nf 6660/6490/6499 8441/8334/8112 8442/8335/8113 6661/6491/6500\r\nf 8443/8336/8114 8444/8337/8115 8442/8338/8113 8441/8339/8112\r\nf 8435/8326/8107 8433/8329/8106 8442/8338/8113 8444/8337/8115\r\nf 6640/6470/6479 6661/6491/6500 8442/8335/8113 8433/8324/8106\r\nf 8445/8340/8116 8446/8341/8117 8447/8342/8118 8448/8343/8119\r\nf 6676/6506/6515 6674/6504/6513 8447/8342/8118 8446/8341/8117\r\nf 6654/6484/6493 8438/8331/8109 8447/8342/8118 6674/6504/6513\r\nf 8437/8330/8108 8448/8343/8119 8447/8342/8118 8438/8331/8109\r\nf 6682/6512/6521 8449/8344/8120 8450/8345/8121 6683/6513/6522\r\nf 8451/8346/8122 8452/8347/8123 8450/8348/8121 8449/8349/8120\r\nf 8443/8336/8114 8441/8339/8112 8450/8348/8121 8452/8347/8123\r\nf 6660/6490/6499 6683/6513/6522 8450/8345/8121 8441/8334/8112\r\nf 6698/6528/6537 8453/8350/6537 8454/8351/8124 6699/6529/6538\r\nf 8455/8352/6537 8456/8353/8125 8454/8354/8124 8453/8355/6537\r\nf 8451/8346/8122 8449/8349/8120 8454/8354/8124 8456/8353/8125\r\nf 6682/6512/6521 6699/6529/6538 8454/8351/8124 8449/8344/8120\r\nf 6714/6544/6553 8457/8356/8126 8458/8357/8127 6715/6545/6554\r\nf 8459/8358/8128 8460/8359/8129 8458/8360/8127 8457/8361/8126\r\nf 8455/8352/6537 8453/8355/6537 8458/8360/8127 8460/8359/8129\r\nf 6698/6528/6537 6715/6545/6554 8458/8357/8127 8453/8350/6537\r\nf 8461/8362/8130 8462/8363/8131 8463/8364/8132 8464/8365/8133\r\nf 6730/6560/6569 6728/6558/6567 8463/8364/8132 8462/8363/8131\r\nf 6708/6538/6547 8465/8366/8134 8463/8364/8132 6728/6558/6567\r\nf 8466/8367/8135 8464/8365/8133 8463/8364/8132 8465/8366/8134\r\nf 6736/6566/6575 8467/8368/8136 8468/8369/8137 6737/6567/6576\r\nf 8469/8370/8138 8470/8371/8139 8468/8372/8137 8467/8373/8136\r\nf 8459/8358/8128 8457/8361/8126 8468/8372/8137 8470/8371/8139\r\nf 6714/6544/6553 6737/6567/6576 8468/8369/8137 8457/8356/8126\r\nf 8471/8374/8140 8472/8375/8141 8473/8376/8142 8474/8377/8143\r\nf 6750/6580/6589 6748/6578/6587 8473/8376/8142 8472/8375/8141\r\nf 6730/6560/6569 8462/8363/8131 8473/8376/8142 6748/6578/6587\r\nf 8461/8362/8130 8474/8377/8143 8473/8376/8142 8462/8363/8131\r\nf 6756/6586/6595 8475/8378/8144 8476/8379/8145 6757/6587/6596\r\nf 8477/8380/8144 8478/8381/8146 8476/8382/8145 8475/8383/8144\r\nf 8469/8370/8138 8467/8373/8136 8476/8382/8145 8478/8381/8146\r\nf 6736/6566/6575 6757/6587/6596 8476/8379/8145 8467/8368/8136\r\nf 8479/8384/8147 8480/8385/8148 8481/8386/8149 8482/8387/8150\r\nf 6770/6600/6609 6768/6598/6607 8481/8386/8149 8480/8385/8148\r\nf 6750/6580/6589 8472/8375/8141 8481/8386/8149 6768/6598/6607\r\nf 8471/8374/8140 8482/8387/8150 8481/8386/8149 8472/8375/8141\r\nf 6776/6606/6615 8483/8388/8151 8484/8389/6616 6777/6607/6616\r\nf 8485/8390/8151 8486/8391/8152 8484/8392/6616 8483/8393/8151\r\nf 8477/8380/8144 8475/8383/8144 8484/8392/6616 8486/8391/8152\r\nf 6756/6586/6595 6777/6607/6616 8484/8389/6616 8475/8378/8144\r\nf 8487/8394/6625 8488/8395/8153 8489/8396/8154 8490/8397/6626\r\nf 6790/6620/6629 6788/6618/6627 8489/8396/8154 8488/8395/8153\r\nf 6770/6600/6609 8480/8385/8148 8489/8396/8154 6788/6618/6627\r\nf 8479/8384/8147 8490/8397/6626 8489/8396/8154 8480/8385/8148\r\nf 6796/6626/6635 8491/8398/6635 8492/8399/6636 6797/6627/6636\r\nf 8493/8400/6635 8494/8401/6636 8492/8402/6636 8491/8403/6635\r\nf 8485/8390/8151 8483/8393/8151 8492/8402/6636 8494/8401/6636\r\nf 6776/6606/6615 6797/6627/6636 8492/8399/6636 8483/8388/8151\r\nf 8495/8404/6645 8496/8405/8155 8497/8406/8156 8498/8407/6646\r\nf 6810/6640/6649 6808/6638/6647 8497/8406/8156 8496/8405/8155\r\nf 6790/6620/6629 8488/8395/8153 8497/8406/8156 6808/6638/6647\r\nf 8487/8394/6625 8498/8407/6646 8497/8406/8156 8488/8395/8153\r\nf 6816/6646/6655 8499/8408/6655 8500/8409/6656 6817/6647/6656\r\nf 8501/8410/6655 8502/8411/6656 8500/8412/6656 8499/8413/6655\r\nf 8493/8400/6635 8491/8403/6635 8500/8412/6656 8502/8411/6656\r\nf 6796/6626/6635 6817/6647/6656 8500/8409/6656 8491/8398/6635\r\nf 8503/8414/6665 8504/8415/8157 8505/8416/8158 8506/8417/6666\r\nf 6830/6660/6669 6828/6658/6667 8505/8416/8158 8504/8415/8157\r\nf 6810/6640/6649 8496/8405/8155 8505/8416/8158 6828/6658/6667\r\nf 8495/8404/6645 8506/8417/6666 8505/8416/8158 8496/8405/8155\r\nf 6836/6666/6675 8507/8418/6675 8508/8419/6676 6837/6667/6676\r\nf 8509/8420/6675 8510/8421/6676 8508/8422/6676 8507/8423/6675\r\nf 8501/8410/6655 8499/8413/6655 8508/8422/6676 8510/8421/6676\r\nf 6816/6646/6655 6837/6667/6676 8508/8419/6676 8499/8408/6655\r\nf 8511/8424/8159 8512/8425/8160 8513/8426/8161 8514/8427/6686\r\nf 6850/6680/6689 6848/6678/6687 8513/8426/8161 8512/8425/8160\r\nf 6830/6660/6669 8504/8415/8157 8513/8426/8161 6848/6678/6687\r\nf 8503/8414/6665 8514/8427/6686 8513/8426/8161 8504/8415/8157\r\nf 6856/6686/6695 8515/8428/6695 8516/8429/6696 6857/6687/6696\r\nf 8517/8430/6695 8518/8431/6696 8516/8432/6696 8515/8433/6695\r\nf 8509/8420/6675 8507/8423/6675 8516/8432/6696 8518/8431/6696\r\nf 6836/6666/6675 6857/6687/6696 8516/8429/6696 8507/8418/6675\r\nf 8519/8434/6705 8520/8435/8162 8521/8436/8163 8522/8437/6706\r\nf 6870/6700/6709 6868/6698/6707 8521/8436/8163 8520/8435/8162\r\nf 6850/6680/6689 8512/8425/8160 8521/8436/8163 6868/6698/6707\r\nf 8511/8424/8159 8522/8437/6706 8521/8436/8163 8512/8425/8160\r\nf 6876/6706/6715 8523/8438/6715 8524/8439/6716 6877/6707/6716\r\nf 8525/8440/6715 8526/8441/6716 8524/8442/6716 8523/8443/6715\r\nf 8517/8430/6695 8515/8433/6695 8524/8442/6716 8526/8441/6716\r\nf 6856/6686/6695 6877/6707/6716 8524/8439/6716 8515/8428/6695\r\nf 8527/8444/6725 8528/8445/8164 8529/8446/8165 8530/8447/6726\r\nf 6890/6720/6729 6888/6718/6727 8529/8446/8165 8528/8445/8164\r\nf 6870/6700/6709 8520/8435/8162 8529/8446/8165 6888/6718/6727\r\nf 8519/8434/6705 8530/8447/6726 8529/8446/8165 8520/8435/8162\r\nf 6896/6726/6735 8531/8448/6735 8532/8449/6736 6897/6727/6736\r\nf 8533/8450/6735 8534/8451/6736 8532/8452/6736 8531/8453/6735\r\nf 8525/8440/6715 8523/8443/6715 8532/8452/6736 8534/8451/6736\r\nf 6876/6706/6715 6897/6727/6736 8532/8449/6736 8523/8438/6715\r\nf 8535/8454/6745 8536/8455/8166 8537/8456/8167 8538/8457/6746\r\nf 6910/6740/6749 6908/6738/6747 8537/8456/8167 8536/8455/8166\r\nf 6890/6720/6729 8528/8445/8164 8537/8456/8167 6908/6738/6747\r\nf 8527/8444/6725 8538/8457/6746 8537/8456/8167 8528/8445/8164\r\nf 6916/6746/6755 8539/8458/6755 8540/8459/6756 6917/6747/6756\r\nf 8541/8460/6755 8542/8461/8168 8540/8462/6756 8539/8463/6755\r\nf 8533/8450/6735 8531/8453/6735 8540/8462/6756 8542/8461/8168\r\nf 6896/6726/6735 6917/6747/6756 8540/8459/6756 8531/8448/6735\r\nf 8543/8464/6765 8544/8465/8169 8545/8466/8170 8546/8467/6766\r\nf 6930/6760/6769 6928/6758/6767 8545/8466/8170 8544/8465/8169\r\nf 6910/6740/6749 8536/8455/8166 8545/8466/8170 6928/6758/6767\r\nf 8535/8454/6745 8546/8467/6766 8545/8466/8170 8536/8455/8166\r\nf 6936/6766/6775 8547/8468/6775 8548/8469/6776 6937/6767/6776\r\nf 8549/8470/6775 8550/8471/6776 8548/8472/6776 8547/8473/6775\r\nf 8541/8460/6755 8539/8463/6755 8548/8472/6776 8550/8471/6776\r\nf 6916/6746/6755 6937/6767/6776 8548/8469/6776 8539/8458/6755\r\nf 8551/8474/6785 8552/8475/8171 8553/8476/8172 8554/8477/6786\r\nf 6950/6780/6789 6948/6778/6787 8553/8476/8172 8552/8475/8171\r\nf 6930/6760/6769 8544/8465/8169 8553/8476/8172 6948/6778/6787\r\nf 8543/8464/6765 8554/8477/6786 8553/8476/8172 8544/8465/8169\r\nf 6956/6786/6795 8555/8478/6795 8556/8479/6796 6957/6787/6796\r\nf 8557/8480/6795 8558/8481/6796 8556/8482/6796 8555/8483/6795\r\nf 8549/8470/6775 8547/8473/6775 8556/8482/6796 8558/8481/6796\r\nf 6936/6766/6775 6957/6787/6796 8556/8479/6796 8547/8468/6775\r\nf 8559/8484/6805 8560/8485/8173 8561/8486/8174 8562/8487/6806\r\nf 6970/6800/6809 6968/6798/6807 8561/8486/8174 8560/8485/8173\r\nf 6950/6780/6789 8552/8475/8171 8561/8486/8174 6968/6798/6807\r\nf 8551/8474/6785 8562/8487/6806 8561/8486/8174 8552/8475/8171\r\nf 6976/6806/6815 8563/8488/6815 8564/8489/6816 6977/6807/6816\r\nf 8565/8490/6815 8566/8491/6816 8564/8492/6816 8563/8493/6815\r\nf 8557/8480/6795 8555/8483/6795 8564/8492/6816 8566/8491/6816\r\nf 6956/6786/6795 6977/6807/6816 8564/8489/6816 8555/8478/6795\r\nf 8567/8494/6825 8568/8495/8175 8569/8496/8176 8570/8497/6826\r\nf 6990/6820/6829 6988/6818/6827 8569/8496/8176 8568/8495/8175\r\nf 6970/6800/6809 8560/8485/8173 8569/8496/8176 6988/6818/6827\r\nf 8559/8484/6805 8570/8497/6826 8569/8496/8176 8560/8485/8173\r\nf 6996/6826/6835 8571/8498/6835 8572/8499/6836 6997/6827/6836\r\nf 8573/8500/6835 8574/8501/6836 8572/8502/6836 8571/8503/6835\r\nf 8565/8490/6815 8563/8493/6815 8572/8502/6836 8574/8501/6836\r\nf 6976/6806/6815 6997/6827/6836 8572/8499/6836 8563/8488/6815\r\nf 8575/8504/6845 8576/8505/8177 8577/8506/8178 8578/8507/8179\r\nf 7010/6840/6849 7008/6838/6847 8577/8506/8178 8576/8505/8177\r\nf 6990/6820/6829 8568/8495/8175 8577/8506/8178 7008/6838/6847\r\nf 8567/8494/6825 8578/8507/8179 8577/8506/8178 8568/8495/8175\r\nf 7016/6846/6855 8579/8508/6855 8580/8509/8180 7017/6847/6856\r\nf 8581/8510/6855 8582/8511/8180 8580/8512/8180 8579/8513/6855\r\nf 8573/8500/6835 8571/8503/6835 8580/8512/8180 8582/8511/8180\r\nf 6996/6826/6835 7017/6847/6856 8580/8509/8180 8571/8498/6835\r\nf 8583/8514/6865 8584/8515/8181 8585/8516/8182 8586/8517/6866\r\nf 7030/6860/6869 7028/6858/6867 8585/8516/8182 8584/8515/8181\r\nf 7010/6840/6849 8576/8505/8177 8585/8516/8182 7028/6858/6867\r\nf 8575/8504/6845 8586/8517/6866 8585/8516/8182 8576/8505/8177\r\nf 7036/6866/6875 8587/8518/6875 8588/8519/6876 7037/6867/6876\r\nf 8589/8520/6875 8590/8521/6876 8588/8522/6876 8587/8523/6875\r\nf 8581/8510/6855 8579/8513/6855 8588/8522/6876 8590/8521/6876\r\nf 7016/6846/6855 7037/6867/6876 8588/8519/6876 8579/8508/6855\r\nf 8591/8524/6885 8592/8525/8183 8593/8526/8184 8594/8527/6886\r\nf 7050/6880/6889 7048/6878/6887 8593/8526/8184 8592/8525/8183\r\nf 7030/6860/6869 8584/8515/8181 8593/8526/8184 7048/6878/6887\r\nf 8583/8514/6865 8594/8527/6886 8593/8526/8184 8584/8515/8181\r\nf 7056/6886/6895 8595/8528/6895 8596/8529/6896 7057/6887/6896\r\nf 8597/8530/6895 8598/8531/6896 8596/8532/6896 8595/8533/6895\r\nf 8589/8520/6875 8587/8523/6875 8596/8532/6896 8598/8531/6896\r\nf 7036/6866/6875 7057/6887/6896 8596/8529/6896 8587/8518/6875\r\nf 8599/8534/6905 8600/8535/8185 8601/8536/8186 8602/8537/6906\r\nf 7070/6900/6909 7068/6898/6907 8601/8536/8186 8600/8535/8185\r\nf 7050/6880/6889 8592/8525/8183 8601/8536/8186 7068/6898/6907\r\nf 8591/8524/6885 8602/8537/6906 8601/8536/8186 8592/8525/8183\r\nf 7076/6906/6915 8603/8538/6915 8604/8539/6916 7077/6907/6916\r\nf 8605/8540/6915 8606/8541/6916 8604/8542/6916 8603/8543/6915\r\nf 8597/8530/6895 8595/8533/6895 8604/8542/6916 8606/8541/6916\r\nf 7056/6886/6895 7077/6907/6916 8604/8539/6916 8595/8528/6895\r\nf 8607/8544/8187 8608/8545/8188 8609/8546/8189 8610/8547/6926\r\nf 7090/6920/6929 7088/6918/6927 8609/8546/8189 8608/8545/8188\r\nf 7070/6900/6909 8600/8535/8185 8609/8546/8189 7088/6918/6927\r\nf 8599/8534/6905 8610/8547/6926 8609/8546/8189 8600/8535/8185\r\nf 7096/6926/6935 8611/8548/8190 8612/8549/6936 7097/6927/6936\r\nf 8613/8550/6935 8614/8551/6936 8612/8552/6936 8611/8553/8190\r\nf 8605/8540/6915 8603/8543/6915 8612/8552/6936 8614/8551/6936\r\nf 7076/6906/6915 7097/6927/6936 8612/8549/6936 8603/8538/6915\r\nf 8615/8554/6945 8616/8555/8191 8617/8556/8192 8618/8557/6946\r\nf 7110/6940/6949 7108/6938/6947 8617/8556/8192 8616/8555/8191\r\nf 7090/6920/6929 8608/8545/8188 8617/8556/8192 7108/6938/6947\r\nf 8607/8544/8187 8618/8557/6946 8617/8556/8192 8608/8545/8188\r\nf 7116/6946/6955 8619/8558/6955 8620/8559/6956 7117/6947/6956\r\nf 8621/8560/6955 8622/8561/6956 8620/8562/6956 8619/8563/6955\r\nf 8613/8550/6935 8611/8553/8190 8620/8562/6956 8622/8561/6956\r\nf 7096/6926/6935 7117/6947/6956 8620/8559/6956 8611/8548/8190\r\nf 8623/8564/6965 8624/8565/8193 8625/8566/8194 8626/8567/6966\r\nf 7130/6960/6969 7128/6958/6967 8625/8566/8194 8624/8565/8193\r\nf 7110/6940/6949 8616/8555/8191 8625/8566/8194 7128/6958/6967\r\nf 8615/8554/6945 8626/8567/6966 8625/8566/8194 8616/8555/8191\r\nf 7136/6966/6975 8627/8568/6975 8628/8569/6976 7137/6967/6976\r\nf 8629/8570/6975 8630/8571/6976 8628/8572/6976 8627/8573/6975\r\nf 8621/8560/6955 8619/8563/6955 8628/8572/6976 8630/8571/6976\r\nf 7116/6946/6955 7137/6967/6976 8628/8569/6976 8619/8558/6955\r\nf 8631/8574/6985 8632/8575/8195 8633/8576/8196 8634/8577/6986\r\nf 7150/6980/6989 7148/6978/6987 8633/8576/8196 8632/8575/8195\r\nf 7130/6960/6969 8624/8565/8193 8633/8576/8196 7148/6978/6987\r\nf 8623/8564/6965 8634/8577/6986 8633/8576/8196 8624/8565/8193\r\nf 7156/6986/6995 8635/8578/6995 8636/8579/6996 7157/6987/6996\r\nf 8637/8580/8197 8638/8581/6996 8636/8582/6996 8635/8583/6995\r\nf 8629/8570/6975 8627/8573/6975 8636/8582/6996 8638/8581/6996\r\nf 7136/6966/6975 7157/6987/6996 8636/8579/6996 8627/8568/6975\r\nf 8639/8584/7005 8640/8585/8198 8641/8586/8199 8642/8587/7006\r\nf 7170/7000/7009 7168/6998/7007 8641/8586/8199 8640/8585/8198\r\nf 7150/6980/6989 8632/8575/8195 8641/8586/8199 7168/6998/7007\r\nf 8631/8574/6985 8642/8587/7006 8641/8586/8199 8632/8575/8195\r\nf 7176/7006/7015 8643/8588/8200 8644/8589/7016 7177/7007/7016\r\nf 8645/8590/8200 8646/8591/7016 8644/8592/7016 8643/8593/8200\r\nf 8637/8580/8197 8635/8583/6995 8644/8592/7016 8646/8591/7016\r\nf 7156/6986/6995 7177/7007/7016 8644/8589/7016 8635/8578/6995\r\nf 8647/8594/7025 8648/8595/8201 8649/8596/8202 8650/8597/7026\r\nf 7190/7020/7029 7188/7018/7027 8649/8596/8202 8648/8595/8201\r\nf 7170/7000/7009 8640/8585/8198 8649/8596/8202 7188/7018/7027\r\nf 8639/8584/7005 8650/8597/7026 8649/8596/8202 8640/8585/8198\r\nf 7196/7026/7035 8651/8598/7035 8652/8599/7036 7197/7027/7036\r\nf 8653/8600/7035 8654/8601/7036 8652/8602/7036 8651/8603/7035\r\nf 8645/8590/8200 8643/8593/8200 8652/8602/7036 8654/8601/7036\r\nf 7176/7006/7015 7197/7027/7036 8652/8599/7036 8643/8588/8200\r\nf 8655/8604/7045 8656/8605/8203 8657/8606/8204 8658/8607/7046\r\nf 7210/7040/7049 7208/7038/7047 8657/8606/8204 8656/8605/8203\r\nf 7190/7020/7029 8648/8595/8201 8657/8606/8204 7208/7038/7047\r\nf 8647/8594/7025 8658/8607/7046 8657/8606/8204 8648/8595/8201\r\nf 7216/7046/7055 8659/8608/7055 8660/8609/7056 7217/7047/7056\r\nf 8661/8610/7055 8662/8611/7056 8660/8612/7056 8659/8613/7055\r\nf 8653/8600/7035 8651/8603/7035 8660/8612/7056 8662/8611/7056\r\nf 7196/7026/7035 7217/7047/7056 8660/8609/7056 8651/8598/7035\r\nf 8663/8614/7065 8664/8615/8205 8665/8616/8206 8666/8617/7045\r\nf 7230/7060/7068 7228/7058/7066 8665/8616/8206 8664/8615/8205\r\nf 7210/7040/7049 8656/8605/8203 8665/8616/8206 7228/7058/7066\r\nf 8655/8604/7045 8666/8617/7045 8665/8616/8206 8656/8605/8203\r\nf 7236/7066/7074 8667/8618/7074 8668/8619/7055 7237/7067/7055\r\nf 8669/8620/7074 8670/8621/7055 8668/8622/7055 8667/8623/7074\r\nf 8661/8610/7055 8659/8613/7055 8668/8622/7055 8670/8621/7055\r\nf 7216/7046/7055 7237/7067/7055 8668/8619/7055 8659/8608/7055\r\nf 8671/8624/8207 8672/8625/8208 8673/8626/8209 8674/8627/8210\r\nf 7250/7080/7087 7248/7078/7085 8673/8626/8209 8672/8625/8208\r\nf 7230/7060/7068 8664/8615/8205 8673/8626/8209 7248/7078/7085\r\nf 8663/8614/7065 8674/8627/8210 8673/8626/8209 8664/8615/8205\r\nf 7256/7086/7093 8675/8628/8211 8676/8629/8212 7257/7087/7094\r\nf 8677/8630/8211 8678/8631/8212 8676/8632/8212 8675/8633/8211\r\nf 8669/8620/7074 8667/8623/7074 8676/8632/8212 8678/8631/8212\r\nf 7236/7066/7074 7257/7087/7094 8676/8629/8212 8667/8618/7074\r\nf 8679/8634/8213 8680/8635/8214 8681/8636/8215 8682/8637/7104\r\nf 7270/7100/7107 7268/7098/7105 8681/8636/8215 8680/8635/8214\r\nf 7250/7080/7087 8672/8625/8208 8681/8636/8215 7268/7098/7105\r\nf 8671/8624/8207 8682/8637/7104 8681/8636/8215 8672/8625/8208\r\nf 7276/7106/7113 8683/8638/8216 8684/8639/8217 7277/7107/7114\r\nf 8685/8640/8216 8686/8641/8218 8684/8642/8217 8683/8643/8216\r\nf 8677/8630/8211 8675/8633/8211 8684/8642/8217 8686/8641/8218\r\nf 7256/7086/7093 7277/7107/7114 8684/8639/8217 8675/8628/8211\r\nf 8687/8644/8219 8688/8645/8220 8689/8646/8221 8690/8647/8222\r\nf 7290/7120/7127 7288/7118/7125 8689/8646/8221 8688/8645/8220\r\nf 7270/7100/7107 8680/8635/8214 8689/8646/8221 7288/7118/7125\r\nf 8679/8634/8213 8690/8647/8222 8689/8646/8221 8680/8635/8214\r\nf 7296/7126/7133 8691/8648/8223 8692/8649/8224 7297/7127/7134\r\nf 8693/8650/8225 8694/8651/8226 8692/8652/8224 8691/8653/8223\r\nf 8685/8640/8216 8683/8643/8216 8692/8652/8224 8694/8651/8226\r\nf 7276/7106/7113 7297/7127/7134 8692/8649/8224 8683/8638/8216\r\nf 7499/7329/7332 7498/7328/7331 8695/8654/8227 8696/8655/8228\r\nf 7310/7140/7147 7308/7138/7145 8695/8654/8227 7498/7328/7331\r\nf 7290/7120/7127 8688/8645/8220 8695/8654/8227 7308/7138/7145\r\nf 8687/8644/8219 8696/8655/8228 8695/8654/8227 8688/8645/8220\r\nf 7316/7146/7153 8697/8656/8229 8698/8657/8230 7317/7147/7154\r\nf 8699/8658/8231 8700/8659/8232 8698/8660/8230 8697/8661/8229\r\nf 8693/8650/8225 8691/8653/8223 8698/8660/8230 8700/8659/8232\r\nf 7296/7126/7133 7317/7147/7154 8698/8657/8230 8691/8648/8223\r\nf 8191/8021/8021 8190/8020/8020 8701/8662/8233 8702/8663/7156\r\nf 6027/5857/5870 6030/5860/5873 8701/8662/8233 8190/8020/8020\r\nf 6035/5865/5878 6037/5867/5880 8701/8662/8233 6030/5860/5873\r\nf 6041/5871/5884 8702/8663/7156 8701/8662/8233 6037/5867/5880\r\nf 7336/7166/7173 8703/8664/7173 8704/8665/8234 8705/8666/8234\r\nf 8706/8667/7173 8707/8668/8234 8704/8669/8234 8703/8670/7173\r\nf 8708/8671/8235 8709/8672/8235 8704/8669/8234 8707/8668/8234\r\nf 8710/8673/8235 8705/8666/8234 8704/8665/8234 8709/8674/8235\r\nf 8711/8675/7188 8712/8676/8236 8713/8677/8237 8714/8678/8238\r\nf 7322/7152/7159 8715/8679/8239 8713/8677/8237 8712/8676/8236\r\nf 8716/8680/8240 8717/8681/8241 8713/8677/8237 8715/8679/8239\r\nf 8718/8682/8242 8714/8678/8238 8713/8677/8237 8717/8681/8241\r\nf 7334/7164/7171 8719/8683/7171 8720/8684/7172 7335/7165/7172\r\nf 8721/8685/7171 8722/8686/7172 8720/8687/7172 8719/8688/7171\r\nf 8706/8667/7173 8703/8670/7173 8720/8687/7172 8722/8686/7172\r\nf 7336/7166/7173 7335/7165/7172 8720/8684/7172 8703/8664/7173\r\nf 8723/8689/7186 8724/8690/8243 8725/8691/8244 8726/8692/7187\r\nf 7326/7156/7163 7323/7153/7160 8725/8691/8244 8724/8690/8243\r\nf 7322/7152/7159 8712/8676/8236 8725/8691/8244 7323/7153/7160\r\nf 8711/8675/7188 8726/8692/7187 8725/8691/8244 8712/8676/8236\r\nf 7360/7190/7197 8727/8693/7197 8728/8694/7198 7361/7191/7198\r\nf 8729/8695/7197 8730/8696/7198 8728/8697/7198 8727/8698/7197\r\nf 8721/8685/7171 8719/8688/7171 8728/8697/7198 8730/8696/7198\r\nf 7334/7164/7171 7361/7191/7198 8728/8694/7198 8719/8683/7171\r\nf 8731/8699/7207 8732/8700/8245 8733/8701/8246 8734/8702/7208\r\nf 7354/7184/7191 7352/7182/7189 8733/8701/8246 8732/8700/8245\r\nf 7326/7156/7163 8724/8690/8243 8733/8701/8246 7352/7182/7189\r\nf 8723/8689/7186 8734/8702/7208 8733/8701/8246 8724/8690/8243\r\nf 7380/7210/7217 8735/8703/7217 8736/8704/7218 7381/7211/7218\r\nf 8737/8705/7217 8738/8706/7218 8736/8707/7218 8735/8708/7217\r\nf 8729/8695/7197 8727/8698/7197 8736/8707/7218 8738/8706/7218\r\nf 7360/7190/7197 7381/7211/7218 8736/8704/7218 8727/8693/7197\r\nf 8739/8709/7227 8740/8710/8247 8741/8711/8248 8742/8712/7228\r\nf 7374/7204/7211 7372/7202/7209 8741/8711/8248 8740/8710/8247\r\nf 7354/7184/7191 8732/8700/8245 8741/8711/8248 7372/7202/7209\r\nf 8731/8699/7207 8742/8712/7228 8741/8711/8248 8732/8700/8245\r\nf 7400/7230/7237 8743/8713/7237 8744/8714/7237 7401/7231/7237\r\nf 8745/8715/7237 8746/8716/7237 8744/8717/7237 8743/8718/7237\r\nf 8737/8705/7217 8735/8708/7217 8744/8717/7237 8746/8716/7237\r\nf 7380/7210/7217 7401/7231/7237 8744/8714/7237 8735/8703/7217\r\nf 8747/8719/7246 8748/8720/8249 8749/8721/8250 8750/8722/7246\r\nf 7394/7224/7231 7392/7222/7229 8749/8721/8250 8748/8720/8249\r\nf 7374/7204/7211 8740/8710/8247 8749/8721/8250 7392/7222/7229\r\nf 8739/8709/7227 8750/8722/7246 8749/8721/8250 8740/8710/8247\r\nf 7420/7250/7255 8751/8723/7255 8752/8724/7217 7421/7251/7217\r\nf 8753/8725/7255 8754/8726/7217 8752/8727/7217 8751/8728/7255\r\nf 8745/8715/7237 8743/8718/7237 8752/8727/7217 8754/8726/7217\r\nf 7400/7230/7237 7421/7251/7217 8752/8724/7217 8743/8713/7237\r\nf 8755/8729/7264 8756/8730/8251 8757/8731/8252 8758/8732/7227\r\nf 7414/7244/7249 7412/7242/7247 8757/8731/8252 8756/8730/8251\r\nf 7394/7224/7231 8748/8720/8249 8757/8731/8252 7412/7242/7247\r\nf 8747/8719/7246 8758/8732/7227 8757/8731/8252 8748/8720/8249\r\nf 7440/7270/7273 8759/8733/7273 8760/8734/7274 7441/7271/7274\r\nf 8761/8735/7273 8762/8736/7274 8760/8737/7274 8759/8738/7273\r\nf 8753/8725/7255 8751/8728/7255 8760/8737/7274 8762/8736/7274\r\nf 7420/7250/7255 7441/7271/7274 8760/8734/7274 8751/8723/7255\r\nf 8763/8739/7283 8764/8740/8253 8765/8741/8254 8766/8742/7284\r\nf 7434/7264/7267 7432/7262/7265 8765/8741/8254 8764/8740/8253\r\nf 7414/7244/7249 8756/8730/8251 8765/8741/8254 7432/7262/7265\r\nf 8755/8729/7264 8766/8742/7284 8765/8741/8254 8756/8730/8251\r\nf 7460/7290/7293 8767/8743/7293 8768/8744/7294 7461/7291/7294\r\nf 8769/8745/8255 8770/8746/7294 8768/8747/7294 8767/8748/7293\r\nf 8761/8735/7273 8759/8738/7273 8768/8747/7294 8770/8746/7294\r\nf 7440/7270/7273 7461/7291/7294 8768/8744/7294 8759/8733/7273\r\nf 8771/8749/8256 8772/8750/8257 8773/8751/8258 8774/8752/7304\r\nf 7454/7284/7287 7452/7282/7285 8773/8751/8258 8772/8750/8257\r\nf 7434/7264/7267 8764/8740/8253 8773/8751/8258 7452/7282/7285\r\nf 8763/8739/7283 8774/8752/7304 8773/8751/8258 8764/8740/8253\r\nf 7480/7310/7313 8775/8753/8259 8776/8754/8260 7481/7311/7314\r\nf 8777/8755/8261 8778/8756/8262 8776/8757/8260 8775/8758/8259\r\nf 8769/8745/8255 8767/8748/7293 8776/8757/8260 8778/8756/8262\r\nf 7460/7290/7293 7481/7311/7314 8776/8754/8260 8767/8743/7293\r\nf 8779/8759/8263 8780/8760/8264 8781/8761/8265 8782/8762/7324\r\nf 7474/7304/7307 7472/7302/7305 8781/8761/8265 8780/8760/8264\r\nf 7454/7284/7287 8772/8750/8257 8781/8761/8265 7472/7302/7305\r\nf 8771/8749/8256 8782/8762/7324 8781/8761/8265 8772/8750/8257\r\nf 7499/7329/7332 8783/8763/8266 8784/8764/8267 7500/7330/7333\r\nf 7306/7136/7143 8785/8765/8268 8784/8766/8267 8783/8767/8266\r\nf 8777/8755/8261 8775/8758/8259 8784/8766/8267 8785/8765/8268\r\nf 7480/7310/7313 7500/7330/7333 8784/8764/8267 8775/8753/8259\r\nf 8786/8768/8269 8787/8769/8270 8788/8770/8271 8789/8771/8272\r\nf 7494/7324/7327 7492/7322/7325 8788/8770/8271 8787/8769/8270\r\nf 7474/7304/7307 8780/8760/8264 8788/8770/8271 7492/7322/7325\r\nf 8779/8759/8263 8789/8771/8272 8788/8770/8271 8780/8760/8264\r\nf 8790/8772/8273 8791/8773/8274 8792/8774/8275 8793/8775/8276\r\nf 7512/7342/7345 7510/7340/7343 8792/8774/8275 8791/8773/8274\r\nf 7494/7324/7327 8787/8769/8270 8792/8774/8275 7510/7340/7343\r\nf 8786/8768/8269 8793/8775/8276 8792/8774/8275 8787/8769/8270\r\nf 7530/7360/7363 8794/8776/8277 8795/8777/8278 7531/7361/7364\r\nf 8796/8778/8279 8797/8779/8280 8795/8780/8278 8794/8781/8277\r\nf 8699/8658/8231 8697/8661/8229 8795/8780/8278 8797/8779/8280\r\nf 7316/7146/7153 7531/7361/7364 8795/8777/8278 8697/8656/8229\r\nf 8798/8782/8281 8799/8783/8282 8800/8784/8283 8801/8785/8284\r\nf 7524/7354/7357 7522/7352/7355 8800/8784/8283 8799/8783/8282\r\nf 7512/7342/7345 8791/8773/8274 8800/8784/8283 7522/7352/7355\r\nf 8790/8772/8273 8801/8785/8284 8800/8784/8283 8791/8773/8274\r\nf 7550/7380/7383 8802/8786/8285 8803/8787/8286 7551/7381/7384\r\nf 8804/8788/8287 8805/8789/8288 8803/8790/8286 8802/8791/8285\r\nf 8796/8778/8279 8794/8781/8277 8803/8790/8286 8805/8789/8288\r\nf 7530/7360/7363 7551/7381/7384 8803/8787/8286 8794/8776/8277\r\nf 8806/8792/8289 8807/8793/8290 8808/8794/8291 8809/8795/8292\r\nf 7544/7374/7377 7542/7372/7375 8808/8794/8291 8807/8793/8290\r\nf 7524/7354/7357 8799/8783/8282 8808/8794/8291 7542/7372/7375\r\nf 8798/8782/8281 8809/8795/8292 8808/8794/8291 8799/8783/8282\r\nf 7570/7400/7403 8810/8796/8293 8811/8797/7404 7571/7401/7404\r\nf 8812/8798/8293 8813/8799/8294 8811/8800/7404 8810/8801/8293\r\nf 8804/8788/8287 8802/8791/8285 8811/8800/7404 8813/8799/8294\r\nf 7550/7380/7383 7571/7401/7404 8811/8797/7404 8802/8786/8285\r\nf 8814/8802/8295 8815/8803/8296 8816/8804/8297 8817/8805/8298\r\nf 7564/7394/7397 7562/7392/7395 8816/8804/8297 8815/8803/8296\r\nf 7544/7374/7377 8807/8793/8290 8816/8804/8297 7562/7392/7395\r\nf 8806/8792/8289 8817/8805/8298 8816/8804/8297 8807/8793/8290\r\nf 7590/7420/7423 8818/8806/8299 8819/8807/7424 7591/7421/7424\r\nf 8820/8808/8299 8821/8809/8300 8819/8810/7424 8818/8811/8299\r\nf 8812/8798/8293 8810/8801/8293 8819/8810/7424 8821/8809/8300\r\nf 7570/7400/7403 7591/7421/7424 8819/8807/7424 8810/8796/8293\r\nf 8822/8812/7433 8823/8813/8301 8824/8814/8302 8825/8815/8303\r\nf 7584/7414/7417 7582/7412/7415 8824/8814/8302 8823/8813/8301\r\nf 7564/7394/7397 8815/8803/8296 8824/8814/8302 7582/7412/7415\r\nf 8814/8802/8295 8825/8815/8303 8824/8814/8302 8815/8803/8296\r\nf 7610/7440/7443 8826/8816/7443 8827/8817/7444 7611/7441/7444\r\nf 8828/8818/7443 8829/8819/8304 8827/8820/7444 8826/8821/7443\r\nf 8820/8808/8299 8818/8811/8299 8827/8820/7444 8829/8819/8304\r\nf 7590/7420/7423 7611/7441/7444 8827/8817/7444 8818/8806/8299\r\nf 8830/8822/8305 8831/8823/8306 8832/8824/8307 8833/8825/7454\r\nf 7604/7434/7437 7602/7432/7435 8832/8824/8307 8831/8823/8306\r\nf 7584/7414/7417 8823/8813/8301 8832/8824/8307 7602/7432/7435\r\nf 8822/8812/7433 8833/8825/7454 8832/8824/8307 8823/8813/8301\r\nf 7630/7460/7463 8834/8826/7463 8835/8827/8308 7631/7461/7464\r\nf 8836/8828/8309 8837/8829/8308 8835/8830/8308 8834/8831/7463\r\nf 8828/8818/7443 8826/8821/7443 8835/8830/8308 8837/8829/8308\r\nf 7610/7440/7443 7631/7461/7464 8835/8827/8308 8826/8816/7443\r\nf 8838/8832/7473 8839/8833/8310 8840/8834/8311 8841/8835/7474\r\nf 7624/7454/7457 7622/7452/7455 8840/8834/8311 8839/8833/8310\r\nf 7604/7434/7437 8831/8823/8306 8840/8834/8311 7622/7452/7455\r\nf 8830/8822/8305 8841/8835/7474 8840/8834/8311 8831/8823/8306\r\nf 7650/7480/7483 8842/8836/7483 8843/8837/7484 7651/7481/7484\r\nf 8844/8838/7483 8845/8839/8312 8843/8840/7484 8842/8841/7483\r\nf 8836/8828/8309 8834/8831/7463 8843/8840/7484 8845/8839/8312\r\nf 7630/7460/7463 7651/7481/7484 8843/8837/7484 8834/8826/7463\r\nf 8846/8842/7493 8847/8843/8313 8848/8844/8314 8849/8845/7494\r\nf 7644/7474/7477 7642/7472/7475 8848/8844/8314 8847/8843/8313\r\nf 7624/7454/7457 8839/8833/8310 8848/8844/8314 7642/7472/7475\r\nf 8838/8832/7473 8849/8845/7494 8848/8844/8314 8839/8833/8310\r\nf 7670/7500/7503 8850/8846/7503 8851/8847/8315 7671/7501/7504\r\nf 8852/8848/8316 8853/8849/7504 8851/8850/8315 8850/8851/7503\r\nf 8844/8838/7483 8842/8841/7483 8851/8850/8315 8853/8849/7504\r\nf 7650/7480/7483 7671/7501/7504 8851/8847/8315 8842/8836/7483\r\nf 8854/8852/7513 8855/8853/8317 8856/8854/8318 8857/8855/7514\r\nf 7664/7494/7497 7662/7492/7495 8856/8854/8318 8855/8853/8317\r\nf 7644/7474/7477 8847/8843/8313 8856/8854/8318 7662/7492/7495\r\nf 8846/8842/7493 8857/8855/7514 8856/8854/8318 8847/8843/8313\r\nf 7690/7520/7523 8858/8856/7523 8859/8857/7524 7691/7521/7524\r\nf 8860/8858/7523 8861/8859/7524 8859/8860/7524 8858/8861/7523\r\nf 8852/8848/8316 8850/8851/7503 8859/8860/7524 8861/8859/7524\r\nf 7670/7500/7503 7691/7521/7524 8859/8857/7524 8850/8846/7503\r\nf 8862/8862/7533 8863/8863/8319 8864/8864/8320 8865/8865/8321\r\nf 7684/7514/7517 7682/7512/7515 8864/8864/8320 8863/8863/8319\r\nf 7664/7494/7497 8855/8853/8317 8864/8864/8320 7682/7512/7515\r\nf 8854/8852/7513 8865/8865/8321 8864/8864/8320 8855/8853/8317\r\nf 7710/7540/7543 8866/8866/7543 8867/8867/7544 7711/7541/7544\r\nf 8868/8868/7543 8869/8869/7544 8867/8870/7544 8866/8871/7543\r\nf 8860/8858/7523 8858/8861/7523 8867/8870/7544 8869/8869/7544\r\nf 7690/7520/7523 7711/7541/7544 8867/8867/7544 8858/8856/7523\r\nf 8870/8872/7553 8871/8873/8322 8872/8874/8323 8873/8875/7554\r\nf 7704/7534/7537 7702/7532/7535 8872/8874/8323 8871/8873/8322\r\nf 7684/7514/7517 8863/8863/8319 8872/8874/8323 7702/7532/7535\r\nf 8862/8862/7533 8873/8875/7554 8872/8874/8323 8863/8863/8319\r\nf 7730/7560/7543 8874/8876/7543 8875/8877/7563 7731/7561/7563\r\nf 8876/8878/7543 8877/8879/7563 8875/8880/7563 8874/8881/7543\r\nf 8868/8868/7543 8866/8871/7543 8875/8880/7563 8877/8879/7563\r\nf 7710/7540/7543 7731/7561/7563 8875/8877/7563 8866/8866/7543\r\nf 8878/8882/7553 8879/8883/8324 8880/8884/8325 8881/8885/7572\r\nf 7724/7554/7557 7722/7552/7555 8880/8884/8325 8879/8883/8324\r\nf 7704/7534/7537 8871/8873/8322 8880/8884/8325 7722/7552/7555\r\nf 8870/8872/7553 8881/8885/7572 8880/8884/8325 8871/8873/8322\r\nf 7750/7580/7581 8882/8886/7581 8883/8887/7582 7751/7581/7582\r\nf 8884/8888/7581 8885/8889/7582 8883/8890/7582 8882/8891/7581\r\nf 8876/8878/7543 8874/8881/7543 8883/8890/7582 8885/8889/7582\r\nf 7730/7560/7543 7751/7581/7582 8883/8887/7582 8874/8876/7543\r\nf 8886/8892/7591 8887/8893/8326 8888/8894/8327 8889/8895/7592\r\nf 7744/7574/7575 7742/7572/7573 8888/8894/8327 8887/8893/8326\r\nf 7724/7554/7557 8879/8883/8324 8888/8894/8327 7742/7572/7573\r\nf 8878/8882/7553 8889/8895/7592 8888/8894/8327 8879/8883/8324\r\nf 7770/7600/7601 8890/8896/7601 8891/8897/7602 7771/7601/7602\r\nf 8892/8898/7601 8893/8899/7602 8891/8900/7602 8890/8901/7601\r\nf 8884/8888/7581 8882/8891/7581 8891/8900/7602 8893/8899/7602\r\nf 7750/7580/7581 7771/7601/7602 8891/8897/7602 8882/8886/7581\r\nf 8894/8902/7611 8895/8903/8328 8896/8904/8329 8897/8905/7612\r\nf 7764/7594/7595 7762/7592/7593 8896/8904/8329 8895/8903/8328\r\nf 7744/7574/7575 8887/8893/8326 8896/8904/8329 7762/7592/7593\r\nf 8886/8892/7591 8897/8905/7612 8896/8904/8329 8887/8893/8326\r\nf 7790/7620/7621 8898/8906/7621 8899/8907/7622 7791/7621/7622\r\nf 8900/8908/7621 8901/8909/7622 8899/8910/7622 8898/8911/7621\r\nf 8892/8898/7601 8890/8901/7601 8899/8910/7622 8901/8909/7622\r\nf 7770/7600/7601 7791/7621/7622 8899/8907/7622 8890/8896/7601\r\nf 8902/8912/7631 8903/8913/8330 8904/8914/8331 8905/8915/7632\r\nf 7784/7614/7615 7782/7612/7613 8904/8914/8331 8903/8913/8330\r\nf 7764/7594/7595 8895/8903/8328 8904/8914/8331 7782/7612/7613\r\nf 8894/8902/7611 8905/8915/7632 8904/8914/8331 8895/8903/8328\r\nf 7810/7640/7641 8906/8916/7641 8907/8917/7642 7811/7641/7642\r\nf 8908/8918/7641 8909/8919/7642 8907/8920/7642 8906/8921/7641\r\nf 8900/8908/7621 8898/8911/7621 8907/8920/7642 8909/8919/7642\r\nf 7790/7620/7621 7811/7641/7642 8907/8917/7642 8898/8906/7621\r\nf 8910/8922/7651 8911/8923/8332 8912/8924/8333 8913/8925/7652\r\nf 7804/7634/7635 7802/7632/7633 8912/8924/8333 8911/8923/8332\r\nf 7784/7614/7615 8903/8913/8330 8912/8924/8333 7802/7632/7633\r\nf 8902/8912/7631 8913/8925/7652 8912/8924/8333 8903/8913/8330\r\nf 7830/7660/7661 8914/8926/7661 8915/8927/7662 7831/7661/7662\r\nf 8916/8928/7661 8917/8929/7662 8915/8930/7662 8914/8931/7661\r\nf 8908/8918/7641 8906/8921/7641 8915/8930/7662 8917/8929/7662\r\nf 7810/7640/7641 7831/7661/7662 8915/8927/7662 8906/8916/7641\r\nf 8918/8932/7671 8919/8933/8334 8920/8934/8335 8921/8935/7672\r\nf 7824/7654/7655 7822/7652/7653 8920/8934/8335 8919/8933/8334\r\nf 7804/7634/7635 8911/8923/8332 8920/8934/8335 7822/7652/7653\r\nf 8910/8922/7651 8921/8935/7672 8920/8934/8335 8911/8923/8332\r\nf 7850/7680/7681 8922/8936/7681 8923/8937/7682 7851/7681/7682\r\nf 8924/8938/7681 8925/8939/7682 8923/8940/7682 8922/8941/7681\r\nf 8916/8928/7661 8914/8931/7661 8923/8940/7682 8925/8939/7682\r\nf 7830/7660/7661 7851/7681/7682 8923/8937/7682 8914/8926/7661\r\nf 8926/8942/7691 8927/8943/8336 8928/8944/8337 8929/8945/7692\r\nf 7844/7674/7675 7842/7672/7673 8928/8944/8337 8927/8943/8336\r\nf 7824/7654/7655 8919/8933/8334 8928/8944/8337 7842/7672/7673\r\nf 8918/8932/7671 8929/8945/7692 8928/8944/8337 8919/8933/8334\r\nf 7870/7700/7701 8930/8946/7701 8931/8947/7702 7871/7701/7702\r\nf 8932/8948/7701 8933/8949/7702 8931/8950/7702 8930/8951/7701\r\nf 8924/8938/7681 8922/8941/7681 8931/8950/7702 8933/8949/7702\r\nf 7850/7680/7681 7871/7701/7702 8931/8947/7702 8922/8936/7681\r\nf 8934/8952/7711 8935/8953/8338 8936/8954/8339 8937/8955/7712\r\nf 7864/7694/7695 7862/7692/7693 8936/8954/8339 8935/8953/8338\r\nf 7844/7674/7675 8927/8943/8336 8936/8954/8339 7862/7692/7693\r\nf 8926/8942/7691 8937/8955/7712 8936/8954/8339 8927/8943/8336\r\nf 7890/7720/7721 8938/8956/7721 8939/8957/8340 7891/7721/7722\r\nf 8940/8958/7721 8941/8959/8340 8939/8960/8340 8938/8961/7721\r\nf 8932/8948/7701 8930/8951/7701 8939/8960/8340 8941/8959/8340\r\nf 7870/7700/7701 7891/7721/7722 8939/8957/8340 8930/8946/7701\r\nf 8942/8962/7731 8943/8963/8341 8944/8964/8342 8945/8965/8343\r\nf 7884/7714/7715 7882/7712/7713 8944/8964/8342 8943/8963/8341\r\nf 7864/7694/7695 8935/8953/8338 8944/8964/8342 7882/7712/7713\r\nf 8934/8952/7711 8945/8965/8343 8944/8964/8342 8935/8953/8338\r\nf 7910/7740/7741 8946/8966/7741 8947/8967/7742 7911/7741/7742\r\nf 8948/8968/7741 8949/8969/7742 8947/8970/7742 8946/8971/7741\r\nf 8940/8958/7721 8938/8961/7721 8947/8970/7742 8949/8969/7742\r\nf 7890/7720/7721 7911/7741/7742 8947/8967/7742 8938/8956/7721\r\nf 8950/8972/7751 8951/8973/8344 8952/8974/8345 8953/8975/7752\r\nf 7904/7734/7735 7902/7732/7733 8952/8974/8345 8951/8973/8344\r\nf 7884/7714/7715 8943/8963/8341 8952/8974/8345 7902/7732/7733\r\nf 8942/8962/7731 8953/8975/7752 8952/8974/8345 8943/8963/8341\r\nf 7930/7760/7761 8954/8976/8346 8955/8977/7762 7931/7761/7762\r\nf 8956/8978/8346 8957/8979/8347 8955/8980/7762 8954/8981/8346\r\nf 8948/8968/7741 8946/8971/7741 8955/8980/7762 8957/8979/8347\r\nf 7910/7740/7741 7931/7761/7762 8955/8977/7762 8946/8966/7741\r\nf 8958/8982/8348 8959/8983/8349 8960/8984/8350 8961/8985/8351\r\nf 7924/7754/7755 7922/7752/7753 8960/8984/8350 8959/8983/8349\r\nf 7904/7734/7735 8951/8973/8344 8960/8984/8350 7922/7752/7753\r\nf 8950/8972/7751 8961/8985/8351 8960/8984/8350 8951/8973/8344\r\nf 7950/7780/7781 8962/8986/8352 8963/8987/8353 7951/7781/7782\r\nf 8964/8988/8354 8965/8989/8355 8963/8990/8353 8962/8991/8352\r\nf 8956/8978/8346 8954/8981/8346 8963/8990/8353 8965/8989/8355\r\nf 7930/7760/7761 7951/7781/7782 8963/8987/8353 8954/8976/8346\r\nf 8966/8992/8356 8967/8993/8357 8968/8994/8358 8969/8995/8359\r\nf 7944/7774/7775 7942/7772/7773 8968/8994/8358 8967/8993/8357\r\nf 7924/7754/7755 8959/8983/8349 8968/8994/8358 7942/7772/7773\r\nf 8958/8982/8348 8969/8995/8359 8968/8994/8358 8959/8983/8349\r\nf 7970/7800/7801 8970/8996/8360 8971/8997/8361 7971/7801/7802\r\nf 8972/8998/8362 8973/8999/8363 8971/9000/8361 8970/9001/8360\r\nf 8964/8988/8354 8962/8991/8352 8971/9000/8361 8973/8999/8363\r\nf 7950/7780/7781 7971/7801/7802 8971/8997/8361 8962/8986/8352\r\nf 8974/9002/8364 8975/9003/8365 8976/9004/8366 8977/9005/8367\r\nf 7964/7794/7795 7962/7792/7793 8976/9004/8366 8975/9003/8365\r\nf 7944/7774/7775 8967/8993/8357 8976/9004/8366 7962/7792/7793\r\nf 8966/8992/8356 8977/9005/8367 8976/9004/8366 8967/8993/8357\r\nf 7990/7820/7821 8978/9006/8368 8979/9007/8369 7991/7821/7822\r\nf 8980/9008/8368 8981/9009/8369 8979/9010/8369 8978/9011/8368\r\nf 8972/8998/8362 8970/9001/8360 8979/9010/8369 8981/9009/8369\r\nf 7970/7800/7801 7991/7821/7822 8979/9007/8369 8970/8996/8360\r\nf 8982/9012/8370 8983/9013/8371 8984/9014/8372 8985/9015/8373\r\nf 7984/7814/7815 7982/7812/7813 8984/9014/8372 8983/9013/8371\r\nf 7964/7794/7795 8975/9003/8365 8984/9014/8372 7982/7812/7813\r\nf 8974/9002/8364 8985/9015/8373 8984/9014/8372 8975/9003/8365\r\nf 8010/7840/7841 8986/9016/7841 8987/9017/7842 8011/7841/7842\r\nf 8988/9018/8374 8989/9019/8375 8987/9020/7842 8986/9021/7841\r\nf 8980/9008/8368 8978/9011/8368 8987/9020/7842 8989/9019/8375\r\nf 7990/7820/7821 8011/7841/7842 8987/9017/7842 8978/9006/8368\r\nf 8990/9022/7851 8991/9023/8376 8992/9024/8377 8993/9025/8378\r\nf 8004/7834/7835 8002/7832/7833 8992/9024/8377 8991/9023/8376\r\nf 7984/7814/7815 8983/9013/8371 8992/9024/8377 8002/7832/7833\r\nf 8982/9012/8370 8993/9025/8378 8992/9024/8377 8983/9013/8371\r\nf 8030/7860/7861 8994/9026/8379 8995/9027/7862 8031/7861/7862\r\nf 8996/9028/8380 8997/9029/8381 8995/9030/7862 8994/9031/8379\r\nf 8988/9018/8374 8986/9021/7841 8995/9030/7862 8997/9029/8381\r\nf 8010/7840/7841 8031/7861/7862 8995/9027/7862 8986/9016/7841\r\nf 8998/9032/8382 8999/9033/8383 9000/9034/8384 9001/9035/8385\r\nf 8024/7854/7855 8022/7852/7853 9000/9034/8384 8999/9033/8383\r\nf 8004/7834/7835 8991/9023/8376 9000/9034/8384 8022/7852/7853\r\nf 8990/9022/7851 9001/9035/8385 9000/9034/8384 8991/9023/8376\r\nf 8050/7880/7881 9002/9036/8386 9003/9037/8387 8051/7881/7882\r\nf 9004/9038/8388 9005/9039/8389 9003/9040/8387 9002/9041/8386\r\nf 8996/9028/8380 8994/9031/8379 9003/9040/8387 9005/9039/8389\r\nf 8030/7860/7861 8051/7881/7882 9003/9037/8387 8994/9026/8379\r\nf 9006/9042/8390 9007/9043/8391 9008/9044/8392 9009/9045/8393\r\nf 8044/7874/7875 8042/7872/7873 9008/9044/8392 9007/9043/8391\r\nf 8024/7854/7855 8999/9033/8383 9008/9044/8392 8042/7872/7873\r\nf 8998/9032/8382 9009/9045/8393 9008/9044/8392 8999/9033/8383\r\nf 8070/7900/7901 9010/9046/8394 9011/9047/8395 8071/7901/7902\r\nf 9012/9048/8394 9013/9049/8396 9011/9050/8395 9010/9051/8394\r\nf 9004/9038/8388 9002/9041/8386 9011/9050/8395 9013/9049/8396\r\nf 8050/7880/7881 8071/7901/7902 9011/9047/8395 9002/9036/8386\r\nf 9014/9052/8397 9015/9053/8398 9016/9054/8399 9017/9055/8400\r\nf 8064/7894/7895 8062/7892/7893 9016/9054/8399 9015/9053/8398\r\nf 8044/7874/7875 9007/9043/8391 9016/9054/8399 8062/7892/7893\r\nf 9006/9042/8390 9017/9055/8400 9016/9054/8399 9007/9043/8391\r\nf 8090/7920/7921 9018/9056/7921 9019/9057/7922 8091/7921/7922\r\nf 9020/9058/7921 9021/9059/7922 9019/9060/7922 9018/9061/7921\r\nf 9012/9048/8394 9010/9051/8394 9019/9060/7922 9021/9059/7922\r\nf 8070/7900/7901 8091/7921/7922 9019/9057/7922 9010/9046/8394\r\nf 9022/9062/7931 9023/9063/8401 9024/9064/8402 9025/9065/7932\r\nf 8084/7914/7915 8082/7912/7913 9024/9064/8402 9023/9063/8401\r\nf 8064/7894/7895 9015/9053/8398 9024/9064/8402 8082/7912/7913\r\nf 9014/9052/8397 9025/9065/7932 9024/9064/8402 9015/9053/8398\r\nf 8110/7940/7941 9026/9066/7941 9027/9067/7942 8111/7941/7942\r\nf 9028/9068/7941 9029/9069/7942 9027/9070/7942 9026/9071/7941\r\nf 9020/9058/7921 9018/9061/7921 9027/9070/7942 9029/9069/7942\r\nf 8090/7920/7921 8111/7941/7942 9027/9067/7942 9018/9056/7921\r\nf 9030/9072/7951 9031/9073/8403 9032/9074/8404 9033/9075/7952\r\nf 8104/7934/7935 8102/7932/7933 9032/9074/8404 9031/9073/8403\r\nf 8084/7914/7915 9023/9063/8401 9032/9074/8404 8102/7932/7933\r\nf 9022/9062/7931 9033/9075/7952 9032/9074/8404 9023/9063/8401\r\nf 8130/7960/7961 9034/9076/7961 9035/9077/7962 8131/7961/7962\r\nf 9036/9078/7961 9037/9079/7962 9035/9080/7962 9034/9081/7961\r\nf 9028/9068/7941 9026/9071/7941 9035/9080/7962 9037/9079/7962\r\nf 8110/7940/7941 8131/7961/7962 9035/9077/7962 9026/9066/7941\r\nf 9038/9082/8405 9039/9083/8406 9040/9084/8407 9041/9085/7972\r\nf 8124/7954/7955 8122/7952/7953 9040/9084/8407 9039/9083/8406\r\nf 8104/7934/7935 9031/9073/8403 9040/9084/8407 8122/7952/7953\r\nf 9030/9072/7951 9041/9085/7972 9040/9084/8407 9031/9073/8403\r\nf 8150/7980/7981 9042/9086/7981 9043/9087/7982 8151/7981/7982\r\nf 9044/9088/8408 9045/9089/7982 9043/9090/7982 9042/9091/7981\r\nf 9036/9078/7961 9034/9081/7961 9043/9090/7982 9045/9089/7982\r\nf 8130/7960/7961 8151/7981/7982 9043/9087/7982 9034/9076/7961\r\nf 9046/9092/8409 9047/9093/8410 9048/9094/8411 9049/9095/7992\r\nf 8144/7974/7975 8142/7972/7973 9048/9094/8411 9047/9093/8410\r\nf 8124/7954/7955 9039/9083/8406 9048/9094/8411 8142/7972/7973\r\nf 9038/9082/8405 9049/9095/7992 9048/9094/8411 9039/9083/8406\r\nf 8170/8000/8001 9050/9096/8412 9051/9097/8413 8171/8001/8002\r\nf 8184/8014/8015 9052/9098/8414 9051/9099/8413 9050/9100/8412\r\nf 9044/9088/8408 9042/9091/7981 9051/9099/8413 9052/9098/8414\r\nf 8150/7980/7981 8171/8001/8002 9051/9097/8413 9042/9086/7981\r\nf 9053/9101/8415 9054/9102/8416 9055/9103/8417 9056/9104/8418\r\nf 8164/7994/7995 8162/7992/7993 9055/9103/8417 9054/9102/8416\r\nf 8144/7974/7975 9047/9093/8410 9055/9103/8417 8162/7992/7993\r\nf 9046/9092/8409 9056/9104/8418 9055/9103/8417 9047/9093/8410\r\nf 8170/8000/8001 8169/7999/8000 9057/9105/8419 9058/9106/8016\r\nf 8166/7996/7997 8165/7995/7996 9057/9105/8419 8169/7999/8000\r\nf 8164/7994/7995 9054/9102/8416 9057/9105/8419 8165/7995/7996\r\nf 9053/9101/8415 9058/9106/8016 9057/9105/8419 9054/9102/8416\r\nf 6024/5854/5867 9059/9107/5867 9060/9108/5868 6025/5855/5868\r\nf 8186/8016/5867 8189/8019/8019 9060/9109/5868 9059/9110/5867\r\nf 8191/8021/8021 9061/9111/5869 9060/9109/5868 8189/8019/8019\r\nf 6026/5856/5869 6025/5855/5868 9060/9108/5868 9061/9112/5869\r\nf 6050/5880/5892 9062/9113/5892 9063/9114/5893 6051/5881/5893\r\nf 8197/8030/8024 8200/8033/5893 9063/9115/5893 9062/9116/5892\r\nf 8186/8016/5867 9059/9110/5867 9063/9115/5893 8200/8033/5893\r\nf 6024/5854/5867 6051/5881/5893 9063/9114/5893 9059/9107/5867\r\nf 6070/5900/5911 9064/9117/5911 9065/9118/5912 6071/5901/5912\r\nf 8205/8040/5911 8208/8043/5912 9065/9119/5912 9064/9120/5911\r\nf 8197/8030/8024 9062/9116/5892 9065/9119/5912 8208/8043/5912\r\nf 6050/5880/5892 6071/5901/5912 9065/9118/5912 9062/9113/5892\r\nf 6090/5920/5931 9066/9121/5931 9067/9122/5932 6091/5921/5932\r\nf 8213/8050/5931 8216/8053/8032 9067/9123/5932 9066/9124/5931\r\nf 8205/8040/5911 9064/9120/5911 9067/9123/5932 8216/8053/8032\r\nf 6070/5900/5911 6091/5921/5932 9067/9122/5932 9064/9117/5911\r\nf 6110/5940/5951 9068/9125/5951 9069/9126/5952 6111/5941/5952\r\nf 8221/8060/5951 8224/8063/5952 9069/9127/5952 9068/9128/5951\r\nf 8213/8050/5931 9066/9124/5931 9069/9127/5952 8224/8063/5952\r\nf 6090/5920/5931 6111/5941/5952 9069/9126/5952 9066/9121/5931\r\nf 6130/5960/5971 9070/9129/5971 9071/9130/5972 6131/5961/5972\r\nf 8229/8070/5971 8232/8073/5972 9071/9131/5972 9070/9132/5971\r\nf 8221/8060/5951 9068/9128/5951 9071/9131/5972 8232/8073/5972\r\nf 6110/5940/5951 6131/5961/5972 9071/9130/5972 9068/9125/5951\r\nf 6150/5980/5991 9072/9133/5991 9073/9134/5992 6151/5981/5992\r\nf 8237/8080/8038 8240/8083/5992 9073/9135/5992 9072/9136/5991\r\nf 8229/8070/5971 9070/9132/5971 9073/9135/5992 8240/8083/5992\r\nf 6130/5960/5971 6151/5981/5992 9073/9134/5992 9070/9129/5971\r\nf 6170/6000/6011 9074/9137/8041 9075/9138/6012 6171/6001/6012\r\nf 8245/8090/8041 8248/8093/6012 9075/9139/6012 9074/9140/8041\r\nf 8237/8080/8038 9072/9136/5991 9075/9139/6012 8248/8093/6012\r\nf 6150/5980/5991 6171/6001/6012 9075/9138/6012 9072/9133/5991\r\nf 6190/6020/6031 9076/9141/6031 9077/9142/6032 6191/6021/6032\r\nf 8253/8100/6031 8256/8103/8048 9077/9143/6032 9076/9144/6031\r\nf 8245/8090/8041 9074/9140/8041 9077/9143/6032 8256/8103/8048\r\nf 6170/6000/6011 6191/6021/6032 9077/9142/6032 9074/9137/8041\r\nf 6210/6040/6051 9078/9145/6051 9079/9146/6052 6211/6041/6052\r\nf 8261/8110/6051 8264/8113/6052 9079/9147/6052 9078/9148/6051\r\nf 8253/8100/6031 9076/9144/6031 9079/9147/6052 8264/8113/6052\r\nf 6190/6020/6031 6211/6041/6052 9079/9146/6052 9076/9141/6031\r\nf 6230/6060/6071 9080/9149/6071 9081/9150/8055 6231/6061/6072\r\nf 8269/8120/6071 8272/8123/8055 9081/9151/8055 9080/9152/6071\r\nf 8261/8110/6051 9078/9148/6051 9081/9151/8055 8272/8123/8055\r\nf 6210/6040/6051 6231/6061/6072 9081/9150/8055 9078/9145/6051\r\nf 6250/6080/6091 9082/9153/6091 9083/9154/6092 6251/6081/6092\r\nf 8277/8130/6091 8280/8133/6092 9083/9155/6092 9082/9156/6091\r\nf 8269/8120/6071 9080/9152/6071 9083/9155/6092 8280/8133/6092\r\nf 6230/6060/6071 6251/6081/6092 9083/9154/6092 9080/9149/6071\r\nf 6270/6100/6111 9084/9157/6111 9085/9158/6112 6271/6101/6112\r\nf 8285/8140/6111 8288/8143/6112 9085/9159/6112 9084/9160/6111\r\nf 8277/8130/6091 9082/9156/6091 9085/9159/6112 8288/8143/6112\r\nf 6250/6080/6091 6271/6101/6112 9085/9158/6112 9082/9153/6091\r\nf 6290/6120/6131 9086/9161/6131 9087/9162/6132 6291/6121/6132\r\nf 8293/8150/6131 8296/8153/6132 9087/9163/6132 9086/9164/6131\r\nf 8285/8140/6111 9084/9160/6111 9087/9163/6132 8296/8153/6132\r\nf 6270/6100/6111 6291/6121/6132 9087/9162/6132 9084/9157/6111\r\nf 6310/6140/6151 9088/9165/6151 9089/9166/6152 6311/6141/6152\r\nf 8301/8160/6151 8304/8163/6152 9089/9167/6152 9088/9168/6151\r\nf 8293/8150/6131 9086/9164/6131 9089/9167/6152 8304/8163/6152\r\nf 6290/6120/6131 6311/6141/6152 9089/9166/6152 9086/9161/6131\r\nf 6330/6160/6171 9090/9169/6171 9091/9170/6172 6331/6161/6172\r\nf 8309/8170/6171 8312/8173/6172 9091/9171/6172 9090/9172/6171\r\nf 8301/8160/6151 9088/9168/6151 9091/9171/6172 8312/8173/6172\r\nf 6310/6140/6151 6331/6161/6172 9091/9170/6172 9088/9165/6151\r\nf 6350/6180/6191 9092/9173/6191 9093/9174/6192 6351/6181/6192\r\nf 8317/8180/6191 8320/8183/6192 9093/9175/6192 9092/9176/6191\r\nf 8309/8170/6171 9090/9172/6171 9093/9175/6192 8320/8183/6192\r\nf 6330/6160/6171 6351/6181/6192 9093/9174/6192 9090/9169/6171\r\nf 6370/6200/6211 9094/9177/6211 9095/9178/6212 6371/6201/6212\r\nf 8325/8190/6211 8328/8193/6212 9095/9179/6212 9094/9180/6211\r\nf 8317/8180/6191 9092/9176/6191 9095/9179/6212 8328/8193/6212\r\nf 6350/6180/6191 6371/6201/6212 9095/9178/6212 9092/9173/6191\r\nf 6390/6220/6230 9096/9181/6230 9097/9182/6231 6391/6221/6231\r\nf 8333/8200/6230 8336/8203/6231 9097/9183/6231 9096/9184/6230\r\nf 8325/8190/6211 9094/9180/6211 9097/9183/6231 8336/8203/6231\r\nf 6370/6200/6211 6391/6221/6231 9097/9182/6231 9094/9177/6211\r\nf 6410/6240/6249 9098/9185/6249 9099/9186/6250 6411/6241/6250\r\nf 8341/8210/6249 8344/8213/6250 9099/9187/6250 9098/9188/6249\r\nf 8333/8200/6230 9096/9184/6230 9099/9187/6250 8344/8213/6250\r\nf 6390/6220/6230 6411/6241/6250 9099/9186/6250 9096/9181/6230\r\nf 6430/6260/6269 9100/9189/6269 9101/9190/6270 6431/6261/6270\r\nf 8349/8220/6269 8352/8223/6270 9101/9191/6270 9100/9192/6269\r\nf 8341/8210/6249 9098/9188/6249 9101/9191/6270 8352/8223/6270\r\nf 6410/6240/6249 6431/6261/6270 9101/9190/6270 9098/9185/6249\r\nf 6450/6280/6289 9102/9193/6289 9103/9194/6290 6451/6281/6290\r\nf 8357/8230/6289 8360/8233/6290 9103/9195/6290 9102/9196/6289\r\nf 8349/8220/6269 9100/9192/6269 9103/9195/6290 8360/8233/6290\r\nf 6430/6260/6269 6451/6281/6290 9103/9194/6290 9100/9189/6269\r\nf 6470/6300/6309 9104/9197/6309 9105/9198/6310 6471/6301/6310\r\nf 8365/8240/6309 8368/8243/6310 9105/9199/6310 9104/9200/6309\r\nf 8357/8230/6289 9102/9196/6289 9105/9199/6310 8368/8243/6310\r\nf 6450/6280/6289 6471/6301/6310 9105/9198/6310 9102/9193/6289\r\nf 6490/6320/6329 9106/9201/6329 9107/9202/6330 6491/6321/6330\r\nf 8373/8250/6329 8376/8253/6330 9107/9203/6330 9106/9204/6329\r\nf 8365/8240/6309 9104/9200/6309 9107/9203/6330 8376/8253/6330\r\nf 6470/6300/6309 6491/6321/6330 9107/9202/6330 9104/9197/6309\r\nf 6510/6340/6349 9108/9205/6349 9109/9206/6350 6511/6341/6350\r\nf 8381/8260/6349 8384/8263/6350 9109/9207/6350 9108/9208/6349\r\nf 8373/8250/6329 9106/9204/6329 9109/9207/6350 8384/8263/6350\r\nf 6490/6320/6329 6511/6341/6350 9109/9206/6350 9106/9201/6329\r\nf 6530/6360/6369 9110/9209/6369 9111/9210/6370 6531/6361/6370\r\nf 8389/8270/6369 8392/8273/6370 9111/9211/6370 9110/9212/6369\r\nf 8381/8260/6349 9108/9208/6349 9111/9211/6370 8392/8273/6370\r\nf 6510/6340/6349 6531/6361/6370 9111/9210/6370 9108/9205/6349\r\nf 6550/6380/6389 9112/9213/6389 9113/9214/6390 6551/6381/6390\r\nf 8397/8280/6389 8400/8283/6390 9113/9215/6390 9112/9216/6389\r\nf 8389/8270/6369 9110/9212/6369 9113/9215/6390 8400/8283/6390\r\nf 6530/6360/6369 6551/6381/6390 9113/9214/6390 9110/9209/6369\r\nf 6570/6400/6409 9114/9217/6409 9115/9218/6410 6571/6401/6410\r\nf 8405/8290/6409 8408/8293/6410 9115/9219/6410 9114/9220/6409\r\nf 8397/8280/6389 9112/9216/6389 9115/9219/6410 8408/8293/6410\r\nf 6550/6380/6389 6571/6401/6410 9115/9218/6410 9112/9213/6389\r\nf 6590/6420/6429 9116/9221/6429 9117/9222/6430 6591/6421/6430\r\nf 8413/8300/6429 8416/8303/6430 9117/9223/6430 9116/9224/6429\r\nf 8405/8290/6409 9114/9220/6409 9117/9223/6430 8416/8303/6430\r\nf 6570/6400/6409 6591/6421/6430 9117/9222/6430 9114/9217/6409\r\nf 6610/6440/6449 9118/9225/6449 9119/9226/8099 6611/6441/6450\r\nf 8421/8310/6449 8424/8313/8099 9119/9227/8099 9118/9228/6449\r\nf 8413/8300/6429 9116/9224/6429 9119/9227/8099 8424/8313/8099\r\nf 6590/6420/6429 6611/6441/6450 9119/9226/8099 9116/9221/6429\r\nf 6630/6460/6469 9120/9229/6469 9121/9230/6470 6631/6461/6470\r\nf 8429/8320/8103 8432/8323/6470 9121/9231/6470 9120/9232/6469\r\nf 8421/8310/6449 9118/9228/6449 9121/9231/6470 8432/8323/6470\r\nf 6610/6440/6449 6631/6461/6470 9121/9230/6470 9118/9225/6449\r\nf 6650/6480/6489 9122/9233/8420 9123/9234/8111 6651/6481/6490\r\nf 8437/8330/8108 8440/8333/8111 9123/9235/8111 9122/9236/8420\r\nf 8429/8320/8103 9120/9232/6469 9123/9235/8111 8440/8333/8111\r\nf 6630/6460/6469 6651/6481/6490 9123/9234/8111 9120/9229/6469\r\nf 6672/6502/6511 9124/9237/8421 9125/9238/8422 6673/6503/6512\r\nf 8445/8340/8116 8448/8343/8119 9125/9239/8422 9124/9240/8421\r\nf 8437/8330/8108 9122/9236/8420 9125/9239/8422 8448/8343/8119\r\nf 6650/6480/6489 6673/6503/6512 9125/9238/8422 9122/9233/8420\r\nf 6725/6555/6564 9126/9241/8423 9127/9242/8424 6726/6556/6565\r\nf 8461/8362/8130 8464/8365/8133 9127/9243/8424 9126/9244/8423\r\nf 8466/8367/8135 9128/9245/8425 9127/9243/8424 8464/8365/8133\r\nf 6727/6557/6566 6726/6556/6565 9127/9242/8424 9128/9246/8425\r\nf 6746/6576/6585 9129/9247/8140 9130/9248/8426 6747/6577/6586\r\nf 8471/8374/8140 8474/8377/8143 9130/9249/8426 9129/9250/8140\r\nf 8461/8362/8130 9126/9244/8423 9130/9249/8426 8474/8377/8143\r\nf 6725/6555/6564 6747/6577/6586 9130/9248/8426 9126/9241/8423\r\nf 6766/6596/6605 9131/9251/6605 9132/9252/6606 6767/6597/6606\r\nf 8479/8384/8147 8482/8387/8150 9132/9253/6606 9131/9254/6605\r\nf 8471/8374/8140 9129/9250/8140 9132/9253/6606 8482/8387/8150\r\nf 6746/6576/6585 6767/6597/6606 9132/9252/6606 9129/9247/8140\r\nf 6786/6616/6625 9133/9255/6625 9134/9256/6626 6787/6617/6626\r\nf 8487/8394/6625 8490/8397/6626 9134/9257/6626 9133/9258/6625\r\nf 8479/8384/8147 9131/9254/6605 9134/9257/6626 8490/8397/6626\r\nf 6766/6596/6605 6787/6617/6626 9134/9256/6626 9131/9251/6605\r\nf 6806/6636/6645 9135/9259/6645 9136/9260/6646 6807/6637/6646\r\nf 8495/8404/6645 8498/8407/6646 9136/9261/6646 9135/9262/6645\r\nf 8487/8394/6625 9133/9258/6625 9136/9261/6646 8498/8407/6646\r\nf 6786/6616/6625 6807/6637/6646 9136/9260/6646 9133/9255/6625\r\nf 6826/6656/6665 9137/9263/6665 9138/9264/6666 6827/6657/6666\r\nf 8503/8414/6665 8506/8417/6666 9138/9265/6666 9137/9266/6665\r\nf 8495/8404/6645 9135/9262/6645 9138/9265/6666 8506/8417/6666\r\nf 6806/6636/6645 6827/6657/6666 9138/9264/6666 9135/9259/6645\r\nf 6846/6676/6685 9139/9267/6685 9140/9268/6686 6847/6677/6686\r\nf 8511/8424/8159 8514/8427/6686 9140/9269/6686 9139/9270/6685\r\nf 8503/8414/6665 9137/9266/6665 9140/9269/6686 8514/8427/6686\r\nf 6826/6656/6665 6847/6677/6686 9140/9268/6686 9137/9263/6665\r\nf 6866/6696/6705 9141/9271/6705 9142/9272/6706 6867/6697/6706\r\nf 8519/8434/6705 8522/8437/6706 9142/9273/6706 9141/9274/6705\r\nf 8511/8424/8159 9139/9270/6685 9142/9273/6706 8522/8437/6706\r\nf 6846/6676/6685 6867/6697/6706 9142/9272/6706 9139/9267/6685\r\nf 6886/6716/6725 9143/9275/6725 9144/9276/6726 6887/6717/6726\r\nf 8527/8444/6725 8530/8447/6726 9144/9277/6726 9143/9278/6725\r\nf 8519/8434/6705 9141/9274/6705 9144/9277/6726 8530/8447/6726\r\nf 6866/6696/6705 6887/6717/6726 9144/9276/6726 9141/9271/6705\r\nf 6906/6736/6745 9145/9279/6745 9146/9280/8427 6907/6737/6746\r\nf 8535/8454/6745 8538/8457/6746 9146/9281/8427 9145/9282/6745\r\nf 8527/8444/6725 9143/9278/6725 9146/9281/8427 8538/8457/6746\r\nf 6886/6716/6725 6907/6737/6746 9146/9280/8427 9143/9275/6725\r\nf 6926/6756/6765 9147/9283/6765 9148/9284/6766 6927/6757/6766\r\nf 8543/8464/6765 8546/8467/6766 9148/9285/6766 9147/9286/6765\r\nf 8535/8454/6745 9145/9282/6745 9148/9285/6766 8546/8467/6766\r\nf 6906/6736/6745 6927/6757/6766 9148/9284/6766 9145/9279/6745\r\nf 6946/6776/6785 9149/9287/6785 9150/9288/6786 6947/6777/6786\r\nf 8551/8474/6785 8554/8477/6786 9150/9289/6786 9149/9290/6785\r\nf 8543/8464/6765 9147/9286/6765 9150/9289/6786 8554/8477/6786\r\nf 6926/6756/6765 6947/6777/6786 9150/9288/6786 9147/9283/6765\r\nf 6966/6796/6805 9151/9291/6805 9152/9292/6806 6967/6797/6806\r\nf 8559/8484/6805 8562/8487/6806 9152/9293/6806 9151/9294/6805\r\nf 8551/8474/6785 9149/9290/6785 9152/9293/6806 8562/8487/6806\r\nf 6946/6776/6785 6967/6797/6806 9152/9292/6806 9149/9287/6785\r\nf 6986/6816/6825 9153/9295/6825 9154/9296/6826 6987/6817/6826\r\nf 8567/8494/6825 8570/8497/6826 9154/9297/6826 9153/9298/6825\r\nf 8559/8484/6805 9151/9294/6805 9154/9297/6826 8570/8497/6826\r\nf 6966/6796/6805 6987/6817/6826 9154/9296/6826 9151/9291/6805\r\nf 7006/6836/6845 9155/9299/6845 9156/9300/6846 7007/6837/6846\r\nf 8575/8504/6845 8578/8507/8179 9156/9301/6846 9155/9302/6845\r\nf 8567/8494/6825 9153/9298/6825 9156/9301/6846 8578/8507/8179\r\nf 6986/6816/6825 7007/6837/6846 9156/9300/6846 9153/9295/6825\r\nf 7026/6856/6865 9157/9303/6865 9158/9304/6866 7027/6857/6866\r\nf 8583/8514/6865 8586/8517/6866 9158/9305/6866 9157/9306/6865\r\nf 8575/8504/6845 9155/9302/6845 9158/9305/6866 8586/8517/6866\r\nf 7006/6836/6845 7027/6857/6866 9158/9304/6866 9155/9299/6845\r\nf 7046/6876/6885 9159/9307/6885 9160/9308/6886 7047/6877/6886\r\nf 8591/8524/6885 8594/8527/6886 9160/9309/6886 9159/9310/6885\r\nf 8583/8514/6865 9157/9306/6865 9160/9309/6886 8594/8527/6886\r\nf 7026/6856/6865 7047/6877/6886 9160/9308/6886 9157/9303/6865\r\nf 7066/6896/6905 9161/9311/6905 9162/9312/6906 7067/6897/6906\r\nf 8599/8534/6905 8602/8537/6906 9162/9313/6906 9161/9314/6905\r\nf 8591/8524/6885 9159/9310/6885 9162/9313/6906 8602/8537/6906\r\nf 7046/6876/6885 7067/6897/6906 9162/9312/6906 9159/9307/6885\r\nf 7086/6916/6925 9163/9315/6925 9164/9316/6926 7087/6917/6926\r\nf 8607/8544/8187 8610/8547/6926 9164/9317/6926 9163/9318/6925\r\nf 8599/8534/6905 9161/9314/6905 9164/9317/6926 8610/8547/6926\r\nf 7066/6896/6905 7087/6917/6926 9164/9316/6926 9161/9311/6905\r\nf 7106/6936/6945 9165/9319/6945 9166/9320/6946 7107/6937/6946\r\nf 8615/8554/6945 8618/8557/6946 9166/9321/6946 9165/9322/6945\r\nf 8607/8544/8187 9163/9318/6925 9166/9321/6946 8618/8557/6946\r\nf 7086/6916/6925 7107/6937/6946 9166/9320/6946 9163/9315/6925\r\nf 7126/6956/6965 9167/9323/6965 9168/9324/6966 7127/6957/6966\r\nf 8623/8564/6965 8626/8567/6966 9168/9325/6966 9167/9326/6965\r\nf 8615/8554/6945 9165/9322/6945 9168/9325/6966 8626/8567/6966\r\nf 7106/6936/6945 7127/6957/6966 9168/9324/6966 9165/9319/6945\r\nf 7146/6976/6985 9169/9327/6985 9170/9328/6986 7147/6977/6986\r\nf 8631/8574/6985 8634/8577/6986 9170/9329/6986 9169/9330/6985\r\nf 8623/8564/6965 9167/9326/6965 9170/9329/6986 8634/8577/6986\r\nf 7126/6956/6965 7147/6977/6986 9170/9328/6986 9167/9323/6965\r\nf 7166/6996/7005 9171/9331/7005 9172/9332/7006 7167/6997/7006\r\nf 8639/8584/7005 8642/8587/7006 9172/9333/7006 9171/9334/7005\r\nf 8631/8574/6985 9169/9330/6985 9172/9333/7006 8642/8587/7006\r\nf 7146/6976/6985 7167/6997/7006 9172/9332/7006 9169/9327/6985\r\nf 7186/7016/7025 9173/9335/7025 9174/9336/7026 7187/7017/7026\r\nf 8647/8594/7025 8650/8597/7026 9174/9337/7026 9173/9338/7025\r\nf 8639/8584/7005 9171/9334/7005 9174/9337/7026 8650/8597/7026\r\nf 7166/6996/7005 7187/7017/7026 9174/9336/7026 9171/9331/7005\r\nf 7206/7036/7045 9175/9339/7045 9176/9340/7046 7207/7037/7046\r\nf 8655/8604/7045 8658/8607/7046 9176/9341/7046 9175/9342/7045\r\nf 8647/8594/7025 9173/9338/7025 9176/9341/7046 8658/8607/7046\r\nf 7186/7016/7025 7207/7037/7046 9176/9340/7046 9173/9335/7025\r\nf 7226/7056/7065 9177/9343/7065 9178/9344/7045 7227/7057/7045\r\nf 8663/8614/7065 8666/8617/7045 9178/9345/7045 9177/9346/7065\r\nf 8655/8604/7045 9175/9342/7045 9178/9345/7045 8666/8617/7045\r\nf 7206/7036/7045 7227/7057/7045 9178/9344/7045 9175/9339/7045\r\nf 7246/7076/7083 9179/9347/8207 9180/9348/7084 7247/7077/7084\r\nf 8671/8624/8207 8674/8627/8210 9180/9349/7084 9179/9350/8207\r\nf 8663/8614/7065 9177/9346/7065 9180/9349/7084 8674/8627/8210\r\nf 7226/7056/7065 7247/7077/7084 9180/9348/7084 9177/9343/7065\r\nf 7266/7096/7103 9181/9351/8213 9182/9352/7104 7267/7097/7104\r\nf 8679/8634/8213 8682/8637/7104 9182/9353/7104 9181/9354/8213\r\nf 8671/8624/8207 9179/9350/8207 9182/9353/7104 8682/8637/7104\r\nf 7246/7076/7083 7267/7097/7104 9182/9352/7104 9179/9347/8207\r\nf 7286/7116/7123 9183/9355/8428 9184/9356/8429 7287/7117/7124\r\nf 8687/8644/8219 8690/8647/8222 9184/9357/8429 9183/9358/8428\r\nf 8679/8634/8213 9181/9354/8213 9184/9357/8429 8690/8647/8222\r\nf 7266/7096/7103 7287/7117/7124 9184/9356/8429 9181/9351/8213\r\nf 7306/7136/7143 8783/8767/8266 9185/9359/8430 7307/7137/7144\r\nf 7499/7329/7332 8696/8655/8228 9185/9360/8430 8783/8763/8266\r\nf 8687/8644/8219 9183/9358/8428 9185/9360/8430 8696/8655/8228\r\nf 7286/7116/7123 7307/7137/7144 9185/9359/8430 9183/9355/8428\r\nf 6026/5856/5869 9061/9112/5869 9186/9361/7156 7319/7149/7156\r\nf 8191/8021/8021 8702/8663/7156 9186/9362/7156 9061/9111/5869\r\nf 6041/5871/5884 8196/8029/8023 9186/9362/7156 8702/8663/7156\r\nf 7320/7150/7157 7319/7149/7156 9186/9361/7156 8196/8028/8023\r\nf 7351/7181/7188 9187/9363/7188 9188/9364/8238 9189/9365/8238\r\nf 8711/8675/7188 8714/8678/8238 9188/9366/8238 9187/9367/7188\r\nf 8718/8682/8242 9190/9368/8242 9188/9366/8238 8714/8678/8238\r\nf 9191/9369/8242 9189/9365/8238 9188/9364/8238 9190/9370/8242\r\nf 7349/7179/7186 9192/9371/7186 9193/9372/7187 7350/7180/7187\r\nf 8723/8689/7186 8726/8692/7187 9193/9373/7187 9192/9374/7186\r\nf 8711/8675/7188 9187/9367/7188 9193/9373/7187 8726/8692/7187\r\nf 7351/7181/7188 7350/7180/7187 9193/9372/7187 9187/9363/7188\r\nf 7370/7200/7207 9194/9375/7207 9195/9376/7208 7371/7201/7208\r\nf 8731/8699/7207 8734/8702/7208 9195/9377/7208 9194/9378/7207\r\nf 8723/8689/7186 9192/9374/7186 9195/9377/7208 8734/8702/7208\r\nf 7349/7179/7186 7371/7201/7208 9195/9376/7208 9192/9371/7186\r\nf 7390/7220/7227 9196/9379/7227 9197/9380/7228 7391/7221/7228\r\nf 8739/8709/7227 8742/8712/7228 9197/9381/7228 9196/9382/7227\r\nf 8731/8699/7207 9194/9378/7207 9197/9381/7228 8742/8712/7228\r\nf 7370/7200/7207 7391/7221/7228 9197/9380/7228 9194/9375/7207\r\nf 7410/7240/7246 9198/9383/7246 9199/9384/7246 7411/7241/7246\r\nf 8747/8719/7246 8750/8722/7246 9199/9385/7246 9198/9386/7246\r\nf 8739/8709/7227 9196/9382/7227 9199/9385/7246 8750/8722/7246\r\nf 7390/7220/7227 7411/7241/7246 9199/9384/7246 9196/9379/7227\r\nf 7430/7260/7264 9200/9387/7264 9201/9388/7227 7431/7261/7227\r\nf 8755/8729/7264 8758/8732/7227 9201/9389/7227 9200/9390/7264\r\nf 8747/8719/7246 9198/9386/7246 9201/9389/7227 8758/8732/7227\r\nf 7410/7240/7246 7431/7261/7227 9201/9388/7227 9198/9383/7246\r\nf 7450/7280/7283 9202/9391/7283 9203/9392/7284 7451/7281/7284\r\nf 8763/8739/7283 8766/8742/7284 9203/9393/7284 9202/9394/7283\r\nf 8755/8729/7264 9200/9390/7264 9203/9393/7284 8766/8742/7284\r\nf 7430/7260/7264 7451/7281/7284 9203/9392/7284 9200/9387/7264\r\nf 7470/7300/7303 9204/9395/8256 9205/9396/7304 7471/7301/7304\r\nf 8771/8749/8256 8774/8752/7304 9205/9397/7304 9204/9398/8256\r\nf 8763/8739/7283 9202/9394/7283 9205/9397/7304 8774/8752/7304\r\nf 7450/7280/7283 7471/7301/7304 9205/9396/7304 9202/9391/7283\r\nf 7490/7320/7323 9206/9399/7323 9207/9400/7324 7491/7321/7324\r\nf 8779/8759/8263 8782/8762/7324 9207/9401/7324 9206/9402/7323\r\nf 8771/8749/8256 9204/9398/8256 9207/9401/7324 8782/8762/7324\r\nf 7470/7300/7303 7491/7321/7324 9207/9400/7324 9204/9395/8256\r\nf 7508/7338/7341 9208/9403/8431 9209/9404/8432 7509/7339/7342\r\nf 8786/8768/8269 8789/8771/8272 9209/9405/8432 9208/9406/8431\r\nf 8779/8759/8263 9206/9402/7323 9209/9405/8432 8789/8771/8272\r\nf 7490/7320/7323 7509/7339/7342 9209/9404/8432 9206/9399/7323\r\nf 7520/7350/7353 9210/9407/8433 9211/9408/8434 7521/7351/7354\r\nf 8790/8772/8273 8793/8775/8276 9211/9409/8434 9210/9410/8433\r\nf 8786/8768/8269 9208/9406/8431 9211/9409/8434 8793/8775/8276\r\nf 7508/7338/7341 7521/7351/7354 9211/9408/8434 9208/9403/8431\r\nf 7540/7370/7373 9212/9411/8435 9213/9412/8436 7541/7371/7374\r\nf 8798/8782/8281 8801/8785/8284 9213/9413/8436 9212/9414/8435\r\nf 8790/8772/8273 9210/9410/8433 9213/9413/8436 8801/8785/8284\r\nf 7520/7350/7353 7541/7371/7374 9213/9412/8436 9210/9407/8433\r\nf 7560/7390/7393 9214/9415/8437 9215/9416/8438 7561/7391/7394\r\nf 8806/8792/8289 8809/8795/8292 9215/9417/8438 9214/9418/8437\r\nf 8798/8782/8281 9212/9414/8435 9215/9417/8438 8809/8795/8292\r\nf 7540/7370/7373 7561/7391/7394 9215/9416/8438 9212/9411/8435\r\nf 7580/7410/7413 9216/9419/8295 9217/9420/8298 7581/7411/7414\r\nf 8814/8802/8295 8817/8805/8298 9217/9421/8298 9216/9422/8295\r\nf 8806/8792/8289 9214/9418/8437 9217/9421/8298 8817/8805/8298\r\nf 7560/7390/7393 7581/7411/7414 9217/9420/8298 9214/9415/8437\r\nf 7600/7430/7433 9218/9423/7433 9219/9424/7434 7601/7431/7434\r\nf 8822/8812/7433 8825/8815/8303 9219/9425/7434 9218/9426/7433\r\nf 8814/8802/8295 9216/9422/8295 9219/9425/7434 8825/8815/8303\r\nf 7580/7410/7413 7601/7431/7434 9219/9424/7434 9216/9419/8295\r\nf 7620/7450/7453 9220/9427/8305 9221/9428/7454 7621/7451/7454\r\nf 8830/8822/8305 8833/8825/7454 9221/9429/7454 9220/9430/8305\r\nf 8822/8812/7433 9218/9426/7433 9221/9429/7454 8833/8825/7454\r\nf 7600/7430/7433 7621/7451/7454 9221/9428/7454 9218/9423/7433\r\nf 7640/7470/7473 9222/9431/7473 9223/9432/7474 7641/7471/7474\r\nf 8838/8832/7473 8841/8835/7474 9223/9433/7474 9222/9434/7473\r\nf 8830/8822/8305 9220/9430/8305 9223/9433/7474 8841/8835/7474\r\nf 7620/7450/7453 7641/7471/7474 9223/9432/7474 9220/9427/8305\r\nf 7660/7490/7493 9224/9435/7493 9225/9436/7494 7661/7491/7494\r\nf 8846/8842/7493 8849/8845/7494 9225/9437/7494 9224/9438/7493\r\nf 8838/8832/7473 9222/9434/7473 9225/9437/7494 8849/8845/7494\r\nf 7640/7470/7473 7661/7491/7494 9225/9436/7494 9222/9431/7473\r\nf 7680/7510/7513 9226/9439/7513 9227/9440/7514 7681/7511/7514\r\nf 8854/8852/7513 8857/8855/7514 9227/9441/7514 9226/9442/7513\r\nf 8846/8842/7493 9224/9438/7493 9227/9441/7514 8857/8855/7514\r\nf 7660/7490/7493 7681/7511/7514 9227/9440/7514 9224/9435/7493\r\nf 7700/7530/7533 9228/9443/7533 9229/9444/7534 7701/7531/7534\r\nf 8862/8862/7533 8865/8865/8321 9229/9445/7534 9228/9446/7533\r\nf 8854/8852/7513 9226/9442/7513 9229/9445/7534 8865/8865/8321\r\nf 7680/7510/7513 7701/7531/7534 9229/9444/7534 9226/9439/7513\r\nf 7720/7550/7553 9230/9447/7553 9231/9448/7554 7721/7551/7554\r\nf 8870/8872/7553 8873/8875/7554 9231/9449/7554 9230/9450/7553\r\nf 8862/8862/7533 9228/9446/7533 9231/9449/7554 8873/8875/7554\r\nf 7700/7530/7533 7721/7551/7554 9231/9448/7554 9228/9443/7533\r\nf 7740/7570/7553 9232/9451/7553 9233/9452/7572 7741/7571/7572\r\nf 8878/8882/7553 8881/8885/7572 9233/9453/7572 9232/9454/7553\r\nf 8870/8872/7553 9230/9450/7553 9233/9453/7572 8881/8885/7572\r\nf 7720/7550/7553 7741/7571/7572 9233/9452/7572 9230/9447/7553\r\nf 7760/7590/7591 9234/9455/7591 9235/9456/7592 7761/7591/7592\r\nf 8886/8892/7591 8889/8895/7592 9235/9457/7592 9234/9458/7591\r\nf 8878/8882/7553 9232/9454/7553 9235/9457/7592 8889/8895/7592\r\nf 7740/7570/7553 7761/7591/7592 9235/9456/7592 9232/9451/7553\r\nf 7780/7610/7611 9236/9459/7611 9237/9460/7612 7781/7611/7612\r\nf 8894/8902/7611 8897/8905/7612 9237/9461/7612 9236/9462/7611\r\nf 8886/8892/7591 9234/9458/7591 9237/9461/7612 8897/8905/7612\r\nf 7760/7590/7591 7781/7611/7612 9237/9460/7612 9234/9455/7591\r\nf 7800/7630/7631 9238/9463/7631 9239/9464/7632 7801/7631/7632\r\nf 8902/8912/7631 8905/8915/7632 9239/9465/7632 9238/9466/7631\r\nf 8894/8902/7611 9236/9462/7611 9239/9465/7632 8905/8915/7632\r\nf 7780/7610/7611 7801/7631/7632 9239/9464/7632 9236/9459/7611\r\nf 7820/7650/7651 9240/9467/7651 9241/9468/7652 7821/7651/7652\r\nf 8910/8922/7651 8913/8925/7652 9241/9469/7652 9240/9470/7651\r\nf 8902/8912/7631 9238/9466/7631 9241/9469/7652 8913/8925/7652\r\nf 7800/7630/7631 7821/7651/7652 9241/9468/7652 9238/9463/7631\r\nf 7840/7670/7671 9242/9471/7671 9243/9472/7672 7841/7671/7672\r\nf 8918/8932/7671 8921/8935/7672 9243/9473/7672 9242/9474/7671\r\nf 8910/8922/7651 9240/9470/7651 9243/9473/7672 8921/8935/7672\r\nf 7820/7650/7651 7841/7671/7672 9243/9472/7672 9240/9467/7651\r\nf 7860/7690/7691 9244/9475/7691 9245/9476/7692 7861/7691/7692\r\nf 8926/8942/7691 8929/8945/7692 9245/9477/7692 9244/9478/7691\r\nf 8918/8932/7671 9242/9474/7671 9245/9477/7692 8929/8945/7692\r\nf 7840/7670/7671 7861/7691/7692 9245/9476/7692 9242/9471/7671\r\nf 7880/7710/7711 9246/9479/7711 9247/9480/7712 7881/7711/7712\r\nf 8934/8952/7711 8937/8955/7712 9247/9481/7712 9246/9482/7711\r\nf 8926/8942/7691 9244/9478/7691 9247/9481/7712 8937/8955/7712\r\nf 7860/7690/7691 7881/7711/7712 9247/9480/7712 9244/9475/7691\r\nf 7900/7730/7731 9248/9483/7731 9249/9484/8439 7901/7731/7732\r\nf 8942/8962/7731 8945/8965/8343 9249/9485/8439 9248/9486/7731\r\nf 8934/8952/7711 9246/9482/7711 9249/9485/8439 8945/8965/8343\r\nf 7880/7710/7711 7901/7731/7732 9249/9484/8439 9246/9479/7711\r\nf 7920/7750/7751 9250/9487/7751 9251/9488/7752 7921/7751/7752\r\nf 8950/8972/7751 8953/8975/7752 9251/9489/7752 9250/9490/7751\r\nf 8942/8962/7731 9248/9486/7731 9251/9489/7752 8953/8975/7752\r\nf 7900/7730/7731 7921/7751/7752 9251/9488/7752 9248/9483/7731\r\nf 7940/7770/7771 9252/9491/8348 9253/9492/8440 7941/7771/7772\r\nf 8958/8982/8348 8961/8985/8351 9253/9493/8440 9252/9494/8348\r\nf 8950/8972/7751 9250/9490/7751 9253/9493/8440 8961/8985/8351\r\nf 7920/7750/7751 7941/7771/7772 9253/9492/8440 9250/9487/7751\r\nf 7960/7790/7791 9254/9495/8441 9255/9496/8442 7961/7791/7792\r\nf 8966/8992/8356 8969/8995/8359 9255/9497/8442 9254/9498/8441\r\nf 8958/8982/8348 9252/9494/8348 9255/9497/8442 8969/8995/8359\r\nf 7940/7770/7771 7961/7791/7792 9255/9496/8442 9252/9491/8348\r\nf 7980/7810/7811 9256/9499/8443 9257/9500/8444 7981/7811/7812\r\nf 8974/9002/8364 8977/9005/8367 9257/9501/8444 9256/9502/8443\r\nf 8966/8992/8356 9254/9498/8441 9257/9501/8444 8977/9005/8367\r\nf 7960/7790/7791 7981/7811/7812 9257/9500/8444 9254/9495/8441\r\nf 8000/7830/7831 9258/9503/7831 9259/9504/8445 8001/7831/7832\r\nf 8982/9012/8370 8985/9015/8373 9259/9505/8445 9258/9506/7831\r\nf 8974/9002/8364 9256/9502/8443 9259/9505/8445 8985/9015/8373\r\nf 7980/7810/7811 8001/7831/7832 9259/9504/8445 9256/9499/8443\r\nf 8020/7850/7851 9260/9507/7851 9261/9508/7852 8021/7851/7852\r\nf 8990/9022/7851 8993/9025/8378 9261/9509/7852 9260/9510/7851\r\nf 8982/9012/8370 9258/9506/7831 9261/9509/7852 8993/9025/8378\r\nf 8000/7830/7831 8021/7851/7852 9261/9508/7852 9258/9503/7831\r\nf 8040/7870/7871 9262/9511/8446 9263/9512/8447 8041/7871/7872\r\nf 8998/9032/8382 9001/9035/8385 9263/9513/8447 9262/9514/8446\r\nf 8990/9022/7851 9260/9510/7851 9263/9513/8447 9001/9035/8385\r\nf 8020/7850/7851 8041/7871/7872 9263/9512/8447 9260/9507/7851\r\nf 8060/7890/7891 9264/9515/8390 9265/9516/8448 8061/7891/7892\r\nf 9006/9042/8390 9009/9045/8393 9265/9517/8448 9264/9518/8390\r\nf 8998/9032/8382 9262/9514/8446 9265/9517/8448 9009/9045/8393\r\nf 8040/7870/7871 8061/7891/7892 9265/9516/8448 9262/9511/8446\r\nf 8080/7910/7911 9266/9519/7911 9267/9520/8400 8081/7911/7912\r\nf 9014/9052/8397 9017/9055/8400 9267/9521/8400 9266/9522/7911\r\nf 9006/9042/8390 9264/9518/8390 9267/9521/8400 9017/9055/8400\r\nf 8060/7890/7891 8081/7911/7912 9267/9520/8400 9264/9515/8390\r\nf 8100/7930/7931 9268/9523/7931 9269/9524/7932 8101/7931/7932\r\nf 9022/9062/7931 9025/9065/7932 9269/9525/7932 9268/9526/7931\r\nf 9014/9052/8397 9266/9522/7911 9269/9525/7932 9025/9065/7932\r\nf 8080/7910/7911 8101/7931/7932 9269/9524/7932 9266/9519/7911\r\nf 8120/7950/7951 9270/9527/7951 9271/9528/7952 8121/7951/7952\r\nf 9030/9072/7951 9033/9075/7952 9271/9529/7952 9270/9530/7951\r\nf 9022/9062/7931 9268/9526/7931 9271/9529/7952 9033/9075/7952\r\nf 8100/7930/7931 8121/7951/7952 9271/9528/7952 9268/9523/7931\r\nf 8140/7970/7971 9272/9531/7971 9273/9532/7972 8141/7971/7972\r\nf 9038/9082/8405 9041/9085/7972 9273/9533/7972 9272/9534/7971\r\nf 9030/9072/7951 9270/9530/7951 9273/9533/7972 9041/9085/7972\r\nf 8120/7950/7951 8141/7971/7972 9273/9532/7972 9270/9527/7951\r\nf 8160/7990/7991 9274/9535/7991 9275/9536/8449 8161/7991/7992\r\nf 9046/9092/8409 9049/9095/7992 9275/9537/8449 9274/9538/7991\r\nf 9038/9082/8405 9272/9534/7971 9275/9537/8449 9049/9095/7992\r\nf 8140/7970/7971 8161/7991/7992 9275/9536/8449 9272/9531/7971\r\nf 8180/8010/8011 9276/9539/8450 9277/9540/8451 8181/8011/8012\r\nf 9053/9101/8415 9056/9104/8418 9277/9541/8451 9276/9542/8450\r\nf 9046/9092/8409 9274/9538/7991 9277/9541/8451 9056/9104/8418\r\nf 8160/7990/7991 8181/8011/8012 9277/9540/8451 9274/9535/7991\r\nf 8184/8014/8015 9050/9100/8412 9278/9543/8016 8185/8015/8016\r\nf 8170/8000/8001 9058/9106/8016 9278/9544/8016 9050/9096/8412\r\nf 9053/9101/8415 9276/9542/8450 9278/9544/8016 9058/9106/8016\r\nf 8180/8010/8011 8185/8015/8016 9278/9543/8016 9276/9539/8450\r\nf 8194/8024/5882 9279/9545/8452 9280/9546/8453 8195/8025/8022\r\nf 6016/5846/5859 6013/5843/5856 9280/9546/8453 9279/9545/8452\r\nf 6012/5842/5855 7321/7151/7158 9280/9546/8453 6013/5843/5856\r\nf 7320/7150/7157 8195/8025/8022 9280/9546/8453 7321/7151/7158\r\nf 8203/8036/8027 9281/9547/8454 9282/9548/8455 8204/8037/5902\r\nf 6044/5874/5886 6042/5872/5885 9282/9548/8455 9281/9547/8454\r\nf 6016/5846/5859 9279/9545/8452 9282/9548/8455 6042/5872/5885\r\nf 8194/8024/5882 8204/8037/5902 9282/9548/8455 9279/9545/8452\r\nf 8211/8046/5921 9283/9549/8456 9284/9550/8457 8212/8047/5922\r\nf 6064/5894/5905 6062/5892/5903 9284/9550/8457 9283/9549/8456\r\nf 6044/5874/5886 9281/9547/8454 9284/9550/8457 6062/5892/5903\r\nf 8203/8036/8027 8212/8047/5922 9284/9550/8457 9281/9547/8454\r\nf 8219/8056/5941 9285/9551/8458 9286/9552/8459 8220/8057/8033\r\nf 6084/5914/5925 6082/5912/5923 9286/9552/8459 9285/9551/8458\r\nf 6064/5894/5905 9283/9549/8456 9286/9552/8459 6082/5912/5923\r\nf 8211/8046/5921 8220/8057/8033 9286/9552/8459 9283/9549/8456\r\nf 8227/8066/5961 9287/9553/8460 9288/9554/8461 8228/8067/5962\r\nf 6104/5934/5945 6102/5932/5943 9288/9554/8461 9287/9553/8460\r\nf 6084/5914/5925 9285/9551/8458 9288/9554/8461 6102/5932/5943\r\nf 8219/8056/5941 8228/8067/5962 9288/9554/8461 9285/9551/8458\r\nf 8235/8076/5981 9289/9555/8462 9290/9556/8463 8236/8077/5982\r\nf 6124/5954/5965 6122/5952/5963 9290/9556/8463 9289/9555/8462\r\nf 6104/5934/5945 9287/9553/8460 9290/9556/8463 6122/5952/5963\r\nf 8227/8066/5961 8236/8077/5982 9290/9556/8463 9287/9553/8460\r\nf 8243/8086/6001 9291/9557/8464 9292/9558/8465 8244/8087/6002\r\nf 6144/5974/5985 6142/5972/5983 9292/9558/8465 9291/9557/8464\r\nf 6124/5954/5965 9289/9555/8462 9292/9558/8465 6142/5972/5983\r\nf 8235/8076/5981 8244/8087/6002 9292/9558/8465 9289/9555/8462\r\nf 8251/8096/8044 9293/9559/8466 9294/9560/8467 8252/8097/8045\r\nf 6164/5994/6005 6162/5992/6003 9294/9560/8467 9293/9559/8466\r\nf 6144/5974/5985 9291/9557/8464 9294/9560/8467 6162/5992/6003\r\nf 8243/8086/6001 8252/8097/8045 9294/9560/8467 9291/9557/8464\r\nf 8259/8106/6041 9295/9561/8468 9296/9562/8469 8260/8107/8049\r\nf 6184/6014/6025 6182/6012/6023 9296/9562/8469 9295/9561/8468\r\nf 6164/5994/6005 9293/9559/8466 9296/9562/8469 6182/6012/6023\r\nf 8251/8096/8044 8260/8107/8049 9296/9562/8469 9293/9559/8466\r\nf 8267/8116/6061 9297/9563/8470 9298/9564/8471 8268/8117/8052\r\nf 6204/6034/6045 6202/6032/6043 9298/9564/8471 9297/9563/8470\r\nf 6184/6014/6025 9295/9561/8468 9298/9564/8471 6202/6032/6043\r\nf 8259/8106/6041 8268/8117/8052 9298/9564/8471 9295/9561/8468\r\nf 8275/8126/6081 9299/9565/8472 9300/9566/8473 8276/8127/8056\r\nf 6224/6054/6065 6222/6052/6063 9300/9566/8473 9299/9565/8472\r\nf 6204/6034/6045 9297/9563/8470 9300/9566/8473 6222/6052/6063\r\nf 8267/8116/6061 8276/8127/8056 9300/9566/8473 9297/9563/8470\r\nf 8283/8136/6101 9301/9567/8474 9302/9568/8475 8284/8137/6102\r\nf 6244/6074/6085 6242/6072/6083 9302/9568/8475 9301/9567/8474\r\nf 6224/6054/6065 9299/9565/8472 9302/9568/8475 6242/6072/6083\r\nf 8275/8126/6081 8284/8137/6102 9302/9568/8475 9299/9565/8472\r\nf 8291/8146/6121 9303/9569/8476 9304/9570/8477 8292/8147/6122\r\nf 6264/6094/6105 6262/6092/6103 9304/9570/8477 9303/9569/8476\r\nf 6244/6074/6085 9301/9567/8474 9304/9570/8477 6262/6092/6103\r\nf 8283/8136/6101 8292/8147/6122 9304/9570/8477 9301/9567/8474\r\nf 8299/8156/6141 9305/9571/8478 9306/9572/8479 8300/8157/8064\r\nf 6284/6114/6125 6282/6112/6123 9306/9572/8479 9305/9571/8478\r\nf 6264/6094/6105 9303/9569/8476 9306/9572/8479 6282/6112/6123\r\nf 8291/8146/6121 8300/8157/8064 9306/9572/8479 9303/9569/8476\r\nf 8307/8166/6161 9307/9573/8480 9308/9574/8481 8308/8167/6162\r\nf 6304/6134/6145 6302/6132/6143 9308/9574/8481 9307/9573/8480\r\nf 6284/6114/6125 9305/9571/8478 9308/9574/8481 6302/6132/6143\r\nf 8299/8156/6141 8308/8167/6162 9308/9574/8481 9305/9571/8478\r\nf 8315/8176/6181 9309/9575/8482 9310/9576/8483 8316/8177/6182\r\nf 6324/6154/6165 6322/6152/6163 9310/9576/8483 9309/9575/8482\r\nf 6304/6134/6145 9307/9573/8480 9310/9576/8483 6322/6152/6163\r\nf 8307/8166/6161 8316/8177/6182 9310/9576/8483 9307/9573/8480\r\nf 8323/8186/6201 9311/9577/8484 9312/9578/8485 8324/8187/6202\r\nf 6344/6174/6185 6342/6172/6183 9312/9578/8485 9311/9577/8484\r\nf 6324/6154/6165 9309/9575/8482 9312/9578/8485 6342/6172/6183\r\nf 8315/8176/6181 8324/8187/6202 9312/9578/8485 9309/9575/8482\r\nf 8331/8196/6221 9313/9579/8486 9314/9580/8487 8332/8197/6222\r\nf 6364/6194/6205 6362/6192/6203 9314/9580/8487 9313/9579/8486\r\nf 6344/6174/6185 9311/9577/8484 9314/9580/8487 6362/6192/6203\r\nf 8323/8186/6201 8332/8197/6222 9314/9580/8487 9311/9577/8484\r\nf 8339/8206/6239 9315/9581/8488 9316/9582/8489 8340/8207/6240\r\nf 6384/6214/6225 6382/6212/6223 9316/9582/8489 9315/9581/8488\r\nf 6364/6194/6205 9313/9579/8486 9316/9582/8489 6382/6212/6223\r\nf 8331/8196/6221 8340/8207/6240 9316/9582/8489 9313/9579/8486\r\nf 8347/8216/6259 9317/9583/8490 9318/9584/8491 8348/8217/6260\r\nf 6404/6234/6243 6402/6232/6241 9318/9584/8491 9317/9583/8490\r\nf 6384/6214/6225 9315/9581/8488 9318/9584/8491 6402/6232/6241\r\nf 8339/8206/6239 8348/8217/6260 9318/9584/8491 9315/9581/8488\r\nf 8355/8226/6279 9319/9585/8492 9320/9586/8493 8356/8227/6280\r\nf 6424/6254/6263 6422/6252/6261 9320/9586/8493 9319/9585/8492\r\nf 6404/6234/6243 9317/9583/8490 9320/9586/8493 6422/6252/6261\r\nf 8347/8216/6259 8356/8227/6280 9320/9586/8493 9317/9583/8490\r\nf 8363/8236/6299 9321/9587/8494 9322/9588/8495 8364/8237/6300\r\nf 6444/6274/6283 6442/6272/6281 9322/9588/8495 9321/9587/8494\r\nf 6424/6254/6263 9319/9585/8492 9322/9588/8495 6442/6272/6281\r\nf 8355/8226/6279 8364/8237/6300 9322/9588/8495 9319/9585/8492\r\nf 8371/8246/6319 9323/9589/8496 9324/9590/8497 8372/8247/6320\r\nf 6464/6294/6303 6462/6292/6301 9324/9590/8497 9323/9589/8496\r\nf 6444/6274/6283 9321/9587/8494 9324/9590/8497 6462/6292/6301\r\nf 8363/8236/6299 8372/8247/6320 9324/9590/8497 9321/9587/8494\r\nf 8379/8256/6339 9325/9591/8498 9326/9592/8499 8380/8257/6340\r\nf 6484/6314/6323 6482/6312/6321 9326/9592/8499 9325/9591/8498\r\nf 6464/6294/6303 9323/9589/8496 9326/9592/8499 6482/6312/6321\r\nf 8371/8246/6319 8380/8257/6340 9326/9592/8499 9323/9589/8496\r\nf 8387/8266/6359 9327/9593/8500 9328/9594/8501 8388/8267/6360\r\nf 6504/6334/6343 6502/6332/6341 9328/9594/8501 9327/9593/8500\r\nf 6484/6314/6323 9325/9591/8498 9328/9594/8501 6502/6332/6341\r\nf 8379/8256/6339 8388/8267/6360 9328/9594/8501 9325/9591/8498\r\nf 8395/8276/6379 9329/9595/8502 9330/9596/8503 8396/8277/6380\r\nf 6524/6354/6363 6522/6352/6361 9330/9596/8503 9329/9595/8502\r\nf 6504/6334/6343 9327/9593/8500 9330/9596/8503 6522/6352/6361\r\nf 8387/8266/6359 8396/8277/6380 9330/9596/8503 9327/9593/8500\r\nf 8403/8286/6399 9331/9597/8504 9332/9598/8505 8404/8287/6400\r\nf 6544/6374/6383 6542/6372/6381 9332/9598/8505 9331/9597/8504\r\nf 6524/6354/6363 9329/9595/8502 9332/9598/8505 6542/6372/6381\r\nf 8395/8276/6379 8404/8287/6400 9332/9598/8505 9329/9595/8502\r\nf 8411/8296/6419 9333/9599/8506 9334/9600/8507 8412/8297/6420\r\nf 6564/6394/6403 6562/6392/6401 9334/9600/8507 9333/9599/8506\r\nf 6544/6374/6383 9331/9597/8504 9334/9600/8507 6562/6392/6401\r\nf 8403/8286/6399 8412/8297/6420 9334/9600/8507 9331/9597/8504\r\nf 8419/8306/8095 9335/9601/8508 9336/9602/8509 8420/8307/8096\r\nf 6584/6414/6423 6582/6412/6421 9336/9602/8509 9335/9601/8508\r\nf 6564/6394/6403 9333/9599/8506 9336/9602/8509 6582/6412/6421\r\nf 8411/8296/6419 8420/8307/8096 9336/9602/8509 9333/9599/8506\r\nf 8427/8316/8102 9337/9603/8510 9338/9604/8511 8428/8317/8101\r\nf 6604/6434/6443 6602/6432/6441 9338/9604/8511 9337/9603/8510\r\nf 6584/6414/6423 9335/9601/8508 9338/9604/8511 6602/6432/6441\r\nf 8419/8306/8095 8428/8317/8101 9338/9604/8511 9335/9601/8508\r\nf 8435/8326/8107 9339/9605/8512 9340/9606/8513 8436/8327/6480\r\nf 6624/6454/6463 6622/6452/6461 9340/9606/8513 9339/9605/8512\r\nf 6604/6434/6443 9337/9603/8510 9340/9606/8513 6622/6452/6461\r\nf 8427/8316/8102 8436/8327/6480 9340/9606/8513 9337/9603/8510\r\nf 8443/8336/8114 9341/9607/8514 9342/9608/8515 8444/8337/8115\r\nf 6644/6474/6483 6642/6472/6481 9342/9608/8515 9341/9607/8514\r\nf 6624/6454/6463 9339/9605/8512 9342/9608/8515 6642/6472/6481\r\nf 8435/8326/8107 8444/8337/8115 9342/9608/8515 9339/9605/8512\r\nf 8451/8346/8122 9343/9609/8516 9344/9610/8517 8452/8347/8123\r\nf 6664/6494/6503 6662/6492/6501 9344/9610/8517 9343/9609/8516\r\nf 6644/6474/6483 9341/9607/8514 9344/9610/8517 6662/6492/6501\r\nf 8443/8336/8114 8452/8347/8123 9344/9610/8517 9341/9607/8514\r\nf 8455/8352/6537 9345/9611/8518 9346/9612/8519 8456/8353/8125\r\nf 6686/6516/6525 6684/6514/6523 9346/9612/8519 9345/9611/8518\r\nf 6664/6494/6503 9343/9609/8516 9346/9612/8519 6684/6514/6523\r\nf 8451/8346/8122 8456/8353/8125 9346/9612/8519 9343/9609/8516\r\nf 8459/8358/8128 9347/9613/8520 9348/9614/8521 8460/8359/8129\r\nf 6702/6532/6541 6700/6530/6539 9348/9614/8521 9347/9613/8520\r\nf 6686/6516/6525 9345/9611/8518 9348/9614/8521 6700/6530/6539\r\nf 8455/8352/6537 8460/8359/8129 9348/9614/8521 9345/9611/8518\r\nf 8469/8370/8138 9349/9615/8522 9350/9616/8523 8470/8371/8139\r\nf 6718/6548/6557 6716/6546/6555 9350/9616/8523 9349/9615/8522\r\nf 6702/6532/6541 9347/9613/8520 9350/9616/8523 6716/6546/6555\r\nf 8459/8358/8128 8470/8371/8139 9350/9616/8523 9347/9613/8520\r\nf 8477/8380/8144 9351/9617/8524 9352/9618/8525 8478/8381/8146\r\nf 6740/6570/6579 6738/6568/6577 9352/9618/8525 9351/9617/8524\r\nf 6718/6548/6557 9349/9615/8522 9352/9618/8525 6738/6568/6577\r\nf 8469/8370/8138 8478/8381/8146 9352/9618/8525 9349/9615/8522\r\nf 8485/8390/8151 9353/9619/8526 9354/9620/8527 8486/8391/8152\r\nf 6760/6590/6599 6758/6588/6597 9354/9620/8527 9353/9619/8526\r\nf 6740/6570/6579 9351/9617/8524 9354/9620/8527 6758/6588/6597\r\nf 8477/8380/8144 8486/8391/8152 9354/9620/8527 9351/9617/8524\r\nf 8493/8400/6635 9355/9621/8528 9356/9622/8529 8494/8401/6636\r\nf 6780/6610/6619 6778/6608/6617 9356/9622/8529 9355/9621/8528\r\nf 6760/6590/6599 9353/9619/8526 9356/9622/8529 6778/6608/6617\r\nf 8485/8390/8151 8494/8401/6636 9356/9622/8529 9353/9619/8526\r\nf 8501/8410/6655 9357/9623/8530 9358/9624/8531 8502/8411/6656\r\nf 6800/6630/6639 6798/6628/6637 9358/9624/8531 9357/9623/8530\r\nf 6780/6610/6619 9355/9621/8528 9358/9624/8531 6798/6628/6637\r\nf 8493/8400/6635 8502/8411/6656 9358/9624/8531 9355/9621/8528\r\nf 8509/8420/6675 9359/9625/8532 9360/9626/8533 8510/8421/6676\r\nf 6820/6650/6659 6818/6648/6657 9360/9626/8533 9359/9625/8532\r\nf 6800/6630/6639 9357/9623/8530 9360/9626/8533 6818/6648/6657\r\nf 8501/8410/6655 8510/8421/6676 9360/9626/8533 9357/9623/8530\r\nf 8517/8430/6695 9361/9627/8534 9362/9628/8535 8518/8431/6696\r\nf 6840/6670/6679 6838/6668/6677 9362/9628/8535 9361/9627/8534\r\nf 6820/6650/6659 9359/9625/8532 9362/9628/8535 6838/6668/6677\r\nf 8509/8420/6675 8518/8431/6696 9362/9628/8535 9359/9625/8532\r\nf 8525/8440/6715 9363/9629/8536 9364/9630/8537 8526/8441/6716\r\nf 6860/6690/6699 6858/6688/6697 9364/9630/8537 9363/9629/8536\r\nf 6840/6670/6679 9361/9627/8534 9364/9630/8537 6858/6688/6697\r\nf 8517/8430/6695 8526/8441/6716 9364/9630/8537 9361/9627/8534\r\nf 8533/8450/6735 9365/9631/8538 9366/9632/8539 8534/8451/6736\r\nf 6880/6710/6719 6878/6708/6717 9366/9632/8539 9365/9631/8538\r\nf 6860/6690/6699 9363/9629/8536 9366/9632/8539 6878/6708/6717\r\nf 8525/8440/6715 8534/8451/6736 9366/9632/8539 9363/9629/8536\r\nf 8541/8460/6755 9367/9633/8540 9368/9634/8541 8542/8461/8168\r\nf 6900/6730/6739 6898/6728/6737 9368/9634/8541 9367/9633/8540\r\nf 6880/6710/6719 9365/9631/8538 9368/9634/8541 6898/6728/6737\r\nf 8533/8450/6735 8542/8461/8168 9368/9634/8541 9365/9631/8538\r\nf 8549/8470/6775 9369/9635/8542 9370/9636/8543 8550/8471/6776\r\nf 6920/6750/6759 6918/6748/6757 9370/9636/8543 9369/9635/8542\r\nf 6900/6730/6739 9367/9633/8540 9370/9636/8543 6918/6748/6757\r\nf 8541/8460/6755 8550/8471/6776 9370/9636/8543 9367/9633/8540\r\nf 8557/8480/6795 9371/9637/8544 9372/9638/8545 8558/8481/6796\r\nf 6940/6770/6779 6938/6768/6777 9372/9638/8545 9371/9637/8544\r\nf 6920/6750/6759 9369/9635/8542 9372/9638/8545 6938/6768/6777\r\nf 8549/8470/6775 8558/8481/6796 9372/9638/8545 9369/9635/8542\r\nf 8565/8490/6815 9373/9639/8546 9374/9640/8547 8566/8491/6816\r\nf 6960/6790/6799 6958/6788/6797 9374/9640/8547 9373/9639/8546\r\nf 6940/6770/6779 9371/9637/8544 9374/9640/8547 6958/6788/6797\r\nf 8557/8480/6795 8566/8491/6816 9374/9640/8547 9371/9637/8544\r\nf 8573/8500/6835 9375/9641/8548 9376/9642/8549 8574/8501/6836\r\nf 6980/6810/6819 6978/6808/6817 9376/9642/8549 9375/9641/8548\r\nf 6960/6790/6799 9373/9639/8546 9376/9642/8549 6978/6808/6817\r\nf 8565/8490/6815 8574/8501/6836 9376/9642/8549 9373/9639/8546\r\nf 8581/8510/6855 9377/9643/8550 9378/9644/8551 8582/8511/8180\r\nf 7000/6830/6839 6998/6828/6837 9378/9644/8551 9377/9643/8550\r\nf 6980/6810/6819 9375/9641/8548 9378/9644/8551 6998/6828/6837\r\nf 8573/8500/6835 8582/8511/8180 9378/9644/8551 9375/9641/8548\r\nf 8589/8520/6875 9379/9645/8552 9380/9646/8553 8590/8521/6876\r\nf 7020/6850/6859 7018/6848/6857 9380/9646/8553 9379/9645/8552\r\nf 7000/6830/6839 9377/9643/8550 9380/9646/8553 7018/6848/6857\r\nf 8581/8510/6855 8590/8521/6876 9380/9646/8553 9377/9643/8550\r\nf 8597/8530/6895 9381/9647/8554 9382/9648/8555 8598/8531/6896\r\nf 7040/6870/6879 7038/6868/6877 9382/9648/8555 9381/9647/8554\r\nf 7020/6850/6859 9379/9645/8552 9382/9648/8555 7038/6868/6877\r\nf 8589/8520/6875 8598/8531/6896 9382/9648/8555 9379/9645/8552\r\nf 8605/8540/6915 9383/9649/8556 9384/9650/8557 8606/8541/6916\r\nf 7060/6890/6899 7058/6888/6897 9384/9650/8557 9383/9649/8556\r\nf 7040/6870/6879 9381/9647/8554 9384/9650/8557 7058/6888/6897\r\nf 8597/8530/6895 8606/8541/6916 9384/9650/8557 9381/9647/8554\r\nf 8613/8550/6935 9385/9651/8558 9386/9652/8559 8614/8551/6936\r\nf 7080/6910/6919 7078/6908/6917 9386/9652/8559 9385/9651/8558\r\nf 7060/6890/6899 9383/9649/8556 9386/9652/8559 7078/6908/6917\r\nf 8605/8540/6915 8614/8551/6936 9386/9652/8559 9383/9649/8556\r\nf 8621/8560/6955 9387/9653/8560 9388/9654/8561 8622/8561/6956\r\nf 7100/6930/6939 7098/6928/6937 9388/9654/8561 9387/9653/8560\r\nf 7080/6910/6919 9385/9651/8558 9388/9654/8561 7098/6928/6937\r\nf 8613/8550/6935 8622/8561/6956 9388/9654/8561 9385/9651/8558\r\nf 8629/8570/6975 9389/9655/8562 9390/9656/8563 8630/8571/6976\r\nf 7120/6950/6959 7118/6948/6957 9390/9656/8563 9389/9655/8562\r\nf 7100/6930/6939 9387/9653/8560 9390/9656/8563 7118/6948/6957\r\nf 8621/8560/6955 8630/8571/6976 9390/9656/8563 9387/9653/8560\r\nf 8637/8580/8197 9391/9657/8564 9392/9658/8565 8638/8581/6996\r\nf 7140/6970/6979 7138/6968/6977 9392/9658/8565 9391/9657/8564\r\nf 7120/6950/6959 9389/9655/8562 9392/9658/8565 7138/6968/6977\r\nf 8629/8570/6975 8638/8581/6996 9392/9658/8565 9389/9655/8562\r\nf 8645/8590/8200 9393/9659/8566 9394/9660/8567 8646/8591/7016\r\nf 7160/6990/6999 7158/6988/6997 9394/9660/8567 9393/9659/8566\r\nf 7140/6970/6979 9391/9657/8564 9394/9660/8567 7158/6988/6997\r\nf 8637/8580/8197 8646/8591/7016 9394/9660/8567 9391/9657/8564\r\nf 8653/8600/7035 9395/9661/8568 9396/9662/8569 8654/8601/7036\r\nf 7180/7010/7019 7178/7008/7017 9396/9662/8569 9395/9661/8568\r\nf 7160/6990/6999 9393/9659/8566 9396/9662/8569 7178/7008/7017\r\nf 8645/8590/8200 8654/8601/7036 9396/9662/8569 9393/9659/8566\r\nf 8661/8610/7055 9397/9663/8570 9398/9664/8571 8662/8611/7056\r\nf 7200/7030/7039 7198/7028/7037 9398/9664/8571 9397/9663/8570\r\nf 7180/7010/7019 9395/9661/8568 9398/9664/8571 7198/7028/7037\r\nf 8653/8600/7035 8662/8611/7056 9398/9664/8571 9395/9661/8568\r\nf 8669/8620/7074 9399/9665/8572 9400/9666/8573 8670/8621/7055\r\nf 7220/7050/7059 7218/7048/7057 9400/9666/8573 9399/9665/8572\r\nf 7200/7030/7039 9397/9663/8570 9400/9666/8573 7218/7048/7057\r\nf 8661/8610/7055 8670/8621/7055 9400/9666/8573 9397/9663/8570\r\nf 8677/8630/8211 9401/9667/8574 9402/9668/8575 8678/8631/8212\r\nf 7240/7070/7077 7238/7068/7075 9402/9668/8575 9401/9667/8574\r\nf 7220/7050/7059 9399/9665/8572 9402/9668/8575 7238/7068/7075\r\nf 8669/8620/7074 8678/8631/8212 9402/9668/8575 9399/9665/8572\r\nf 8685/8640/8216 9403/9669/8576 9404/9670/8577 8686/8641/8218\r\nf 7260/7090/7097 7258/7088/7095 9404/9670/8577 9403/9669/8576\r\nf 7240/7070/7077 9401/9667/8574 9404/9670/8577 7258/7088/7095\r\nf 8677/8630/8211 8686/8641/8218 9404/9670/8577 9401/9667/8574\r\nf 8693/8650/8225 9405/9671/8578 9406/9672/8579 8694/8651/8226\r\nf 7280/7110/7117 7278/7108/7115 9406/9672/8579 9405/9671/8578\r\nf 7260/7090/7097 9403/9669/8576 9406/9672/8579 7278/7108/7115\r\nf 8685/8640/8216 8694/8651/8226 9406/9672/8579 9403/9669/8576\r\nf 8699/8658/8231 9407/9673/8580 9408/9674/8581 8700/8659/8232\r\nf 7300/7130/7137 7298/7128/7135 9408/9674/8581 9407/9673/8580\r\nf 7280/7110/7117 9405/9671/8578 9408/9674/8581 7298/7128/7135\r\nf 8693/8650/8225 8700/8659/8232 9408/9674/8581 9405/9671/8578\r\nf 8706/8667/7173 9409/9675/8582 9410/9676/8583 8707/8668/8234\r\nf 7337/7167/7174 9411/9677/8584 9410/9676/8583 9409/9675/8582\r\nf 9412/9678/8585 9413/9679/8586 9410/9676/8583 9411/9677/8584\r\nf 8708/8671/8235 8707/8668/8234 9410/9676/8583 9413/9679/8586\r\nf 8721/8685/7171 9414/9680/8587 9415/9681/8588 8722/8686/7172\r\nf 7341/7171/7178 7338/7168/7175 9415/9681/8588 9414/9680/8587\r\nf 7337/7167/7174 9409/9675/8582 9415/9681/8588 7338/7168/7175\r\nf 8706/8667/7173 8722/8686/7172 9415/9681/8588 9409/9675/8582\r\nf 8729/8695/7197 9416/9682/8589 9417/9683/8590 8730/8696/7198\r\nf 7364/7194/7201 7362/7192/7199 9417/9683/8590 9416/9682/8589\r\nf 7341/7171/7178 9414/9680/8587 9417/9683/8590 7362/7192/7199\r\nf 8721/8685/7171 8730/8696/7198 9417/9683/8590 9414/9680/8587\r\nf 8737/8705/7217 9418/9684/8591 9419/9685/8592 8738/8706/7218\r\nf 7384/7214/7221 7382/7212/7219 9419/9685/8592 9418/9684/8591\r\nf 7364/7194/7201 9416/9682/8589 9419/9685/8592 7382/7212/7219\r\nf 8729/8695/7197 8738/8706/7218 9419/9685/8592 9416/9682/8589\r\nf 8745/8715/7237 9420/9686/8593 9421/9687/8594 8746/8716/7237\r\nf 7404/7234/7240 7402/7232/7238 9421/9687/8594 9420/9686/8593\r\nf 7384/7214/7221 9418/9684/8591 9421/9687/8594 7402/7232/7238\r\nf 8737/8705/7217 8746/8716/7237 9421/9687/8594 9418/9684/8591\r\nf 8753/8725/7255 9422/9688/8595 9423/9689/8596 8754/8726/7217\r\nf 7424/7254/7258 7422/7252/7256 9423/9689/8596 9422/9688/8595\r\nf 7404/7234/7240 9420/9686/8593 9423/9689/8596 7422/7252/7256\r\nf 8745/8715/7237 8754/8726/7217 9423/9689/8596 9420/9686/8593\r\nf 8761/8735/7273 9424/9690/8597 9425/9691/8598 8762/8736/7274\r\nf 7444/7274/7277 7442/7272/7275 9425/9691/8598 9424/9690/8597\r\nf 7424/7254/7258 9422/9688/8595 9425/9691/8598 7442/7272/7275\r\nf 8753/8725/7255 8762/8736/7274 9425/9691/8598 9422/9688/8595\r\nf 8769/8745/8255 9426/9692/8599 9427/9693/8600 8770/8746/7294\r\nf 7464/7294/7297 7462/7292/7295 9427/9693/8600 9426/9692/8599\r\nf 7444/7274/7277 9424/9690/8597 9427/9693/8600 7462/7292/7295\r\nf 8761/8735/7273 8770/8746/7294 9427/9693/8600 9424/9690/8597\r\nf 8777/8755/8261 9428/9694/8601 9429/9695/8602 8778/8756/8262\r\nf 7484/7314/7317 7482/7312/7315 9429/9695/8602 9428/9694/8601\r\nf 7464/7294/7297 9426/9692/8599 9429/9695/8602 7482/7312/7315\r\nf 8769/8745/8255 8778/8756/8262 9429/9695/8602 9426/9692/8599\r\nf 7306/7136/7143 7305/7135/7142 9430/9696/8603 8785/8765/8268\r\nf 7302/7132/7139 7501/7331/7334 9430/9696/8603 7305/7135/7142\r\nf 7484/7314/7317 9428/9694/8601 9430/9696/8603 7501/7331/7334\r\nf 8777/8755/8261 8785/8765/8268 9430/9696/8603 9428/9694/8601\r\nf 8796/8778/8279 9431/9697/8604 9432/9698/8605 8797/8779/8280\r\nf 7534/7364/7367 7532/7362/7365 9432/9698/8605 9431/9697/8604\r\nf 7300/7130/7137 9407/9673/8580 9432/9698/8605 7532/7362/7365\r\nf 8699/8658/8231 8797/8779/8280 9432/9698/8605 9407/9673/8580\r\nf 8804/8788/8287 9433/9699/8606 9434/9700/8607 8805/8789/8288\r\nf 7554/7384/7387 7552/7382/7385 9434/9700/8607 9433/9699/8606\r\nf 7534/7364/7367 9431/9697/8604 9434/9700/8607 7552/7382/7385\r\nf 8796/8778/8279 8805/8789/8288 9434/9700/8607 9431/9697/8604\r\nf 8812/8798/8293 9435/9701/8608 9436/9702/8609 8813/8799/8294\r\nf 7574/7404/7407 7572/7402/7405 9436/9702/8609 9435/9701/8608\r\nf 7554/7384/7387 9433/9699/8606 9436/9702/8609 7572/7402/7405\r\nf 8804/8788/8287 8813/8799/8294 9436/9702/8609 9433/9699/8606\r\nf 8820/8808/8299 9437/9703/8610 9438/9704/8611 8821/8809/8300\r\nf 7594/7424/7427 7592/7422/7425 9438/9704/8611 9437/9703/8610\r\nf 7574/7404/7407 9435/9701/8608 9438/9704/8611 7592/7422/7425\r\nf 8812/8798/8293 8821/8809/8300 9438/9704/8611 9435/9701/8608\r\nf 8828/8818/7443 9439/9705/8612 9440/9706/8613 8829/8819/8304\r\nf 7614/7444/7447 7612/7442/7445 9440/9706/8613 9439/9705/8612\r\nf 7594/7424/7427 9437/9703/8610 9440/9706/8613 7612/7442/7445\r\nf 8820/8808/8299 8829/8819/8304 9440/9706/8613 9437/9703/8610\r\nf 8836/8828/8309 9441/9707/8614 9442/9708/8615 8837/8829/8308\r\nf 7634/7464/7467 7632/7462/7465 9442/9708/8615 9441/9707/8614\r\nf 7614/7444/7447 9439/9705/8612 9442/9708/8615 7632/7462/7465\r\nf 8828/8818/7443 8837/8829/8308 9442/9708/8615 9439/9705/8612\r\nf 8844/8838/7483 9443/9709/8616 9444/9710/8617 8845/8839/8312\r\nf 7654/7484/7487 7652/7482/7485 9444/9710/8617 9443/9709/8616\r\nf 7634/7464/7467 9441/9707/8614 9444/9710/8617 7652/7482/7485\r\nf 8836/8828/8309 8845/8839/8312 9444/9710/8617 9441/9707/8614\r\nf 8852/8848/8316 9445/9711/8618 9446/9712/8619 8853/8849/7504\r\nf 7674/7504/7507 7672/7502/7505 9446/9712/8619 9445/9711/8618\r\nf 7654/7484/7487 9443/9709/8616 9446/9712/8619 7672/7502/7505\r\nf 8844/8838/7483 8853/8849/7504 9446/9712/8619 9443/9709/8616\r\nf 8860/8858/7523 9447/9713/8620 9448/9714/8621 8861/8859/7524\r\nf 7694/7524/7527 7692/7522/7525 9448/9714/8621 9447/9713/8620\r\nf 7674/7504/7507 9445/9711/8618 9448/9714/8621 7692/7522/7525\r\nf 8852/8848/8316 8861/8859/7524 9448/9714/8621 9445/9711/8618\r\nf 8868/8868/7543 9449/9715/8622 9450/9716/8623 8869/8869/7544\r\nf 7714/7544/7547 7712/7542/7545 9450/9716/8623 9449/9715/8622\r\nf 7694/7524/7527 9447/9713/8620 9450/9716/8623 7712/7542/7545\r\nf 8860/8858/7523 8869/8869/7544 9450/9716/8623 9447/9713/8620\r\nf 8876/8878/7543 9451/9717/8624 9452/9718/8625 8877/8879/7563\r\nf 7734/7564/7566 7732/7562/7564 9452/9718/8625 9451/9717/8624\r\nf 7714/7544/7547 9449/9715/8622 9452/9718/8625 7732/7562/7564\r\nf 8868/8868/7543 8877/8879/7563 9452/9718/8625 9449/9715/8622\r\nf 8884/8888/7581 9453/9719/8626 9454/9720/8627 8885/8889/7582\r\nf 7754/7584/7585 7752/7582/7583 9454/9720/8627 9453/9719/8626\r\nf 7734/7564/7566 9451/9717/8624 9454/9720/8627 7752/7582/7583\r\nf 8876/8878/7543 8885/8889/7582 9454/9720/8627 9451/9717/8624\r\nf 8892/8898/7601 9455/9721/8628 9456/9722/8629 8893/8899/7602\r\nf 7774/7604/7605 7772/7602/7603 9456/9722/8629 9455/9721/8628\r\nf 7754/7584/7585 9453/9719/8626 9456/9722/8629 7772/7602/7603\r\nf 8884/8888/7581 8893/8899/7602 9456/9722/8629 9453/9719/8626\r\nf 8900/8908/7621 9457/9723/8630 9458/9724/8631 8901/8909/7622\r\nf 7794/7624/7625 7792/7622/7623 9458/9724/8631 9457/9723/8630\r\nf 7774/7604/7605 9455/9721/8628 9458/9724/8631 7792/7622/7623\r\nf 8892/8898/7601 8901/8909/7622 9458/9724/8631 9455/9721/8628\r\nf 8908/8918/7641 9459/9725/8632 9460/9726/8633 8909/8919/7642\r\nf 7814/7644/7645 7812/7642/7643 9460/9726/8633 9459/9725/8632\r\nf 7794/7624/7625 9457/9723/8630 9460/9726/8633 7812/7642/7643\r\nf 8900/8908/7621 8909/8919/7642 9460/9726/8633 9457/9723/8630\r\nf 8916/8928/7661 9461/9727/8634 9462/9728/8635 8917/8929/7662\r\nf 7834/7664/7665 7832/7662/7663 9462/9728/8635 9461/9727/8634\r\nf 7814/7644/7645 9459/9725/8632 9462/9728/8635 7832/7662/7663\r\nf 8908/8918/7641 8917/8929/7662 9462/9728/8635 9459/9725/8632\r\nf 8924/8938/7681 9463/9729/8636 9464/9730/8637 8925/8939/7682\r\nf 7854/7684/7685 7852/7682/7683 9464/9730/8637 9463/9729/8636\r\nf 7834/7664/7665 9461/9727/8634 9464/9730/8637 7852/7682/7683\r\nf 8916/8928/7661 8925/8939/7682 9464/9730/8637 9461/9727/8634\r\nf 8932/8948/7701 9465/9731/8638 9466/9732/8639 8933/8949/7702\r\nf 7874/7704/7705 7872/7702/7703 9466/9732/8639 9465/9731/8638\r\nf 7854/7684/7685 9463/9729/8636 9466/9732/8639 7872/7702/7703\r\nf 8924/8938/7681 8933/8949/7702 9466/9732/8639 9463/9729/8636\r\nf 8940/8958/7721 9467/9733/8640 9468/9734/8641 8941/8959/8340\r\nf 7894/7724/7725 7892/7722/7723 9468/9734/8641 9467/9733/8640\r\nf 7874/7704/7705 9465/9731/8638 9468/9734/8641 7892/7722/7723\r\nf 8932/8948/7701 8941/8959/8340 9468/9734/8641 9465/9731/8638\r\nf 8948/8968/7741 9469/9735/8642 9470/9736/8643 8949/8969/7742\r\nf 7914/7744/7745 7912/7742/7743 9470/9736/8643 9469/9735/8642\r\nf 7894/7724/7725 9467/9733/8640 9470/9736/8643 7912/7742/7743\r\nf 8940/8958/7721 8949/8969/7742 9470/9736/8643 9467/9733/8640\r\nf 8956/8978/8346 9471/9737/8644 9472/9738/8645 8957/8979/8347\r\nf 7934/7764/7765 7932/7762/7763 9472/9738/8645 9471/9737/8644\r\nf 7914/7744/7745 9469/9735/8642 9472/9738/8645 7932/7762/7763\r\nf 8948/8968/7741 8957/8979/8347 9472/9738/8645 9469/9735/8642\r\nf 8964/8988/8354 9473/9739/8646 9474/9740/8647 8965/8989/8355\r\nf 7954/7784/7785 7952/7782/7783 9474/9740/8647 9473/9739/8646\r\nf 7934/7764/7765 9471/9737/8644 9474/9740/8647 7952/7782/7783\r\nf 8956/8978/8346 8965/8989/8355 9474/9740/8647 9471/9737/8644\r\nf 8972/8998/8362 9475/9741/8648 9476/9742/8649 8973/8999/8363\r\nf 7974/7804/7805 7972/7802/7803 9476/9742/8649 9475/9741/8648\r\nf 7954/7784/7785 9473/9739/8646 9476/9742/8649 7972/7802/7803\r\nf 8964/8988/8354 8973/8999/8363 9476/9742/8649 9473/9739/8646\r\nf 8980/9008/8368 9477/9743/8650 9478/9744/8651 8981/9009/8369\r\nf 7994/7824/7825 7992/7822/7823 9478/9744/8651 9477/9743/8650\r\nf 7974/7804/7805 9475/9741/8648 9478/9744/8651 7992/7822/7823\r\nf 8972/8998/8362 8981/9009/8369 9478/9744/8651 9475/9741/8648\r\nf 8988/9018/8374 9479/9745/8652 9480/9746/8653 8989/9019/8375\r\nf 8014/7844/7845 8012/7842/7843 9480/9746/8653 9479/9745/8652\r\nf 7994/7824/7825 9477/9743/8650 9480/9746/8653 8012/7842/7843\r\nf 8980/9008/8368 8989/9019/8375 9480/9746/8653 9477/9743/8650\r\nf 8996/9028/8380 9481/9747/8654 9482/9748/8655 8997/9029/8381\r\nf 8034/7864/7865 8032/7862/7863 9482/9748/8655 9481/9747/8654\r\nf 8014/7844/7845 9479/9745/8652 9482/9748/8655 8032/7862/7863\r\nf 8988/9018/8374 8997/9029/8381 9482/9748/8655 9479/9745/8652\r\nf 9004/9038/8388 9483/9749/8656 9484/9750/8657 9005/9039/8389\r\nf 8054/7884/7885 8052/7882/7883 9484/9750/8657 9483/9749/8656\r\nf 8034/7864/7865 9481/9747/8654 9484/9750/8657 8052/7882/7883\r\nf 8996/9028/8380 9005/9039/8389 9484/9750/8657 9481/9747/8654\r\nf 9012/9048/8394 9485/9751/8658 9486/9752/8659 9013/9049/8396\r\nf 8074/7904/7905 8072/7902/7903 9486/9752/8659 9485/9751/8658\r\nf 8054/7884/7885 9483/9749/8656 9486/9752/8659 8072/7902/7903\r\nf 9004/9038/8388 9013/9049/8396 9486/9752/8659 9483/9749/8656\r\nf 9020/9058/7921 9487/9753/8660 9488/9754/8661 9021/9059/7922\r\nf 8094/7924/7925 8092/7922/7923 9488/9754/8661 9487/9753/8660\r\nf 8074/7904/7905 9485/9751/8658 9488/9754/8661 8092/7922/7923\r\nf 9012/9048/8394 9021/9059/7922 9488/9754/8661 9485/9751/8658\r\nf 9028/9068/7941 9489/9755/8662 9490/9756/8663 9029/9069/7942\r\nf 8114/7944/7945 8112/7942/7943 9490/9756/8663 9489/9755/8662\r\nf 8094/7924/7925 9487/9753/8660 9490/9756/8663 8112/7942/7943\r\nf 9020/9058/7921 9029/9069/7942 9490/9756/8663 9487/9753/8660\r\nf 9036/9078/7961 9491/9757/8664 9492/9758/8665 9037/9079/7962\r\nf 8134/7964/7965 8132/7962/7963 9492/9758/8665 9491/9757/8664\r\nf 8114/7944/7945 9489/9755/8662 9492/9758/8665 8132/7962/7963\r\nf 9028/9068/7941 9037/9079/7962 9492/9758/8665 9489/9755/8662\r\nf 9044/9088/8408 9493/9759/8666 9494/9760/8667 9045/9089/7982\r\nf 8154/7984/7985 8152/7982/7983 9494/9760/8667 9493/9759/8666\r\nf 8134/7964/7965 9491/9757/8664 9494/9760/8667 8152/7982/7983\r\nf 9036/9078/7961 9045/9089/7982 9494/9760/8667 9491/9757/8664\r\nf 8184/8014/8015 8182/8012/8013 9495/9761/8668 9052/9098/8414\r\nf 8174/8004/8005 8172/8002/8003 9495/9761/8668 8182/8012/8013\r\nf 8154/7984/7985 9493/9759/8666 9495/9761/8668 8172/8002/8003\r\nf 9044/9088/8408 9052/9098/8414 9495/9761/8668 9493/9759/8666\r\nf 6708/6538/6547 6706/6536/6545 9496/9762/8669 8465/8366/8134\r\nf 6692/6522/6531 9497/9763/8670 9496/9762/8669 6706/6536/6545\r\nf 9498/9764/8671 9499/9765/8672 9496/9762/8669 9497/9763/8670\r\nf 8466/8367/8135 8465/8366/8134 9496/9762/8669 9499/9765/8672\r\nf 9498/9764/8671 9497/9763/8670 9500/9766/8673 9501/9767/8674\r\nf 6692/6522/6531 6690/6520/6529 9500/9766/8673 9497/9763/8670\r\nf 6676/6506/6515 8446/8341/8117 9500/9766/8673 6690/6520/6529\r\nf 8445/8340/8116 9501/9767/8674 9500/9766/8673 8446/8341/8117\r\nf 9498/9764/8671 9502/9768/8675 9503/9769/8676 9499/9765/8672\r\nf 9504/9770/8677 9505/9771/8678 9503/9772/8676 9502/9773/8675\r\nf 6727/6557/6566 9128/9246/8425 9503/9772/8676 9505/9771/8678\r\nf 8466/8367/8135 9499/9765/8672 9503/9769/8676 9128/9245/8425\r\nf 8445/8340/8116 9124/9240/8421 9506/9774/8679 9501/9767/8674\r\nf 6672/6502/6511 6671/6501/6510 9506/9775/8679 9124/9237/8421\r\nf 6670/6500/6509 9507/9776/8680 9506/9775/8679 6671/6501/6510\r\nf 9504/9770/8677 9502/9773/8675 9506/9775/8679 9507/9776/8680\r\nf 9498/9764/8671 9501/9767/8674 9506/9774/8679 9502/9768/8675\r\nf 6670/6500/6509 6669/6499/6508 9508/9777/8681 9507/9776/8680\r\nf 6666/6496/6505 6689/6519/6528 9508/9777/8681 6669/6499/6508\r\nf 6688/6518/6527 9509/9778/8682 9508/9777/8681 6689/6519/6528\r\nf 9504/9770/8677 9507/9776/8680 9508/9777/8681 9509/9778/8682\r\nf 9504/9770/8677 9509/9778/8682 9510/9779/8683 9505/9771/8678\r\nf 6688/6518/6527 6705/6535/6544 9510/9779/8683 9509/9778/8682\r\nf 6704/6534/6543 6723/6553/6562 9510/9779/8683 6705/6535/6544\r\nf 6727/6557/6566 9505/9771/8678 9510/9779/8683 6723/6553/6562\r\nf 8716/8680/8240 8715/8679/8239 9511/9780/8684 9512/9781/8685\r\nf 7322/7152/7159 7325/7155/7162 9511/9780/8684 8715/8679/8239\r\nf 7330/7160/7167 9513/9782/8686 9511/9780/8684 7325/7155/7162\r\nf 9514/9783/8687 9512/9781/8685 9511/9780/8684 9513/9782/8686\r\nf 9514/9783/8687 9513/9782/8686 9515/9784/8688 9516/9785/8689\r\nf 7330/7160/7167 7332/7162/7169 9515/9784/8688 9513/9782/8686\r\nf 7336/7166/7173 8705/8666/8234 9515/9784/8688 7332/7162/7169\r\nf 8710/8673/8235 9516/9785/8689 9515/9784/8688 8705/8666/8234\r\nf 9412/9678/8585 9411/9677/8584 9517/9786/8690 9518/9787/8691\r\nf 7337/7167/7174 7340/7170/7177 9517/9786/8690 9411/9677/8584\r\nf 7345/7175/7182 9519/9788/8692 9517/9786/8690 7340/7170/7177\r\nf 9520/9789/8693 9518/9787/8691 9517/9786/8690 9519/9788/8692\r\nf 9520/9789/8693 9519/9788/8692 9521/9790/8694 9522/9791/8695\r\nf 7345/7175/7182 7347/7177/7184 9521/9790/8694 9519/9788/8692\r\nf 7351/7181/7188 9189/9365/8238 9521/9790/8694 7347/7177/7184\r\nf 9191/9369/8242 9522/9791/8695 9521/9790/8694 9189/9365/8238\r\nf 9523/9792/8696 9524/9793/8697 9525/9794/8698 9526/9795/8699\r\nf 9527/9796/8700 9528/9797/8701 9525/9794/8698 9524/9793/8697\r\nf 9529/9798/8702 9530/9799/8703 9525/9794/8698 9528/9797/8701\r\nf 9531/9800/8704 9526/9795/8699 9525/9794/8698 9530/9799/8703\r\nf 9527/9796/8700 9532/9801/8705 9533/9802/8706 9528/9797/8701\r\nf 9534/9803/8707 9535/9804/8708 9533/9802/8706 9532/9801/8705\r\nf 9536/9805/8709 9537/9806/8710 9533/9802/8706 9535/9804/8708\r\nf 9529/9798/8702 9528/9797/8701 9533/9802/8706 9537/9806/8710\r\nf 9538/9807/8711 9539/9808/8712 9540/9809/8713 9541/9810/8714\r\nf 9542/9811/8715 9543/9812/8716 9540/9809/8713 9539/9808/8712\r\nf 9544/9813/8717 9545/9814/8718 9540/9809/8713 9543/9812/8716\r\nf 9546/9815/8719 9541/9810/8714 9540/9809/8713 9545/9814/8718\r\nf 9542/9811/8715 9547/9816/8720 9548/9817/8721 9543/9812/8716\r\nf 9549/9818/8722 9550/9819/8723 9548/9817/8721 9547/9816/8720\r\nf 9551/9820/8724 9552/9821/8725 9548/9817/8721 9550/9819/8723\r\nf 9544/9813/8717 9543/9812/8716 9548/9817/8721 9552/9821/8725\r\nf 9531/9800/8704 9530/9799/8703 9553/9822/8703 9554/9823/8726\r\nf 9529/9798/8702 9555/9824/8727 9553/9822/8703 9530/9799/8703\r\nf 9556/9825/8728 9557/9826/8729 9553/9822/8703 9555/9824/8727\r\nf 9558/9827/8730 9554/9823/8726 9553/9822/8703 9557/9826/8729\r\nf 9529/9798/8702 9537/9806/8710 9559/9828/8731 9555/9824/8727\r\nf 9536/9805/8709 9560/9829/8732 9559/9828/8731 9537/9806/8710\r\nf 9561/9830/8733 9562/9831/8734 9559/9828/8731 9560/9829/8732\r\nf 9556/9825/8728 9555/9824/8727 9559/9828/8731 9562/9831/8734\r\nf 9546/9815/8719 9545/9814/8718 9563/9832/8718 9564/9833/8735\r\nf 9544/9813/8717 9565/9834/8736 9563/9832/8718 9545/9814/8718\r\nf 9566/9835/8737 9567/9836/8738 9563/9832/8718 9565/9834/8736\r\nf 9568/9837/8739 9564/9833/8735 9563/9832/8718 9567/9836/8738\r\nf 9544/9813/8717 9552/9821/8725 9569/9838/8740 9565/9834/8736\r\nf 9551/9820/8724 9570/9839/8741 9569/9838/8740 9552/9821/8725\r\nf 9571/9840/8742 9572/9841/8743 9569/9838/8740 9570/9839/8741\r\nf 9566/9835/8737 9565/9834/8736 9569/9838/8740 9572/9841/8743\r\nf 9558/9827/8730 9557/9826/8729 9573/9842/8744 9574/9843/8745\r\nf 9556/9825/8728 9575/9844/8746 9573/9842/8744 9557/9826/8729\r\nf 9576/9845/8747 9577/9846/8748 9573/9842/8744 9575/9844/8746\r\nf 9578/9847/8749 9574/9843/8745 9573/9842/8744 9577/9846/8748\r\nf 9556/9825/8728 9562/9831/8734 9579/9848/8750 9575/9844/8746\r\nf 9561/9830/8733 9580/9849/8751 9579/9848/8750 9562/9831/8734\r\nf 9581/9850/8752 9582/9851/8753 9579/9848/8750 9580/9849/8751\r\nf 9576/9845/8747 9575/9844/8746 9579/9848/8750 9582/9851/8753\r\nf 9568/9837/8739 9567/9836/8738 9583/9852/8754 9584/9853/8755\r\nf 9566/9835/8737 9585/9854/8756 9583/9852/8754 9567/9836/8738\r\nf 9586/9855/8757 9587/9856/8758 9583/9852/8754 9585/9854/8756\r\nf 9588/9857/8759 9584/9853/8755 9583/9852/8754 9587/9856/8758\r\nf 9566/9835/8737 9572/9841/8743 9589/9858/8760 9585/9854/8756\r\nf 9571/9840/8742 9590/9859/8761 9589/9858/8760 9572/9841/8743\r\nf 9591/9860/8762 9592/9861/8763 9589/9858/8760 9590/9859/8761\r\nf 9586/9855/8757 9585/9854/8756 9589/9858/8760 9592/9861/8763\r\nf 9578/9847/8749 9577/9846/8748 9593/9862/8764 9594/9863/8765\r\nf 9576/9845/8747 9595/9864/8766 9593/9862/8764 9577/9846/8748\r\nf 9596/9865/8767 9597/9866/8768 9593/9862/8764 9595/9864/8766\r\nf 9598/9867/8769 9594/9863/8765 9593/9862/8764 9597/9866/8768\r\nf 9576/9845/8747 9582/9851/8753 9599/9868/8770 9595/9864/8766\r\nf 9581/9850/8752 9600/9869/8771 9599/9868/8770 9582/9851/8753\r\nf 9601/9870/8772 9602/9871/8773 9599/9868/8770 9600/9869/8771\r\nf 9596/9865/8767 9595/9864/8766 9599/9868/8770 9602/9871/8773\r\nf 9588/9857/8759 9587/9856/8758 9603/9872/8774 9604/9873/8775\r\nf 9586/9855/8757 9605/9874/8776 9603/9872/8774 9587/9856/8758\r\nf 9606/9875/8777 9607/9876/8778 9603/9872/8774 9605/9874/8776\r\nf 9608/9877/8779 9604/9873/8775 9603/9872/8774 9607/9876/8778\r\nf 9586/9855/8757 9592/9861/8763 9609/9878/8780 9605/9874/8776\r\nf 9591/9860/8762 9610/9879/8781 9609/9878/8780 9592/9861/8763\r\nf 9611/9880/8782 9612/9881/8783 9609/9878/8780 9610/9879/8781\r\nf 9606/9875/8777 9605/9874/8776 9609/9878/8780 9612/9881/8783\r\nf 9598/9867/8769 9597/9866/8768 9613/9882/8784 9614/9883/8785\r\nf 9596/9865/8767 9615/9884/8786 9613/9882/8784 9597/9866/8768\r\nf 9616/9885/8787 9617/9886/8788 9613/9882/8784 9615/9884/8786\r\nf 9618/9887/8789 9614/9883/8785 9613/9882/8784 9617/9886/8788\r\nf 9596/9865/8767 9602/9871/8773 9619/9888/8790 9615/9884/8786\r\nf 9601/9870/8772 9620/9889/8791 9619/9888/8790 9602/9871/8773\r\nf 9621/9890/8792 9622/9891/8793 9619/9888/8790 9620/9889/8791\r\nf 9616/9885/8787 9615/9884/8786 9619/9888/8790 9622/9891/8793\r\nf 9608/9877/8779 9607/9876/8778 9623/9892/8794 9624/9893/8795\r\nf 9606/9875/8777 9625/9894/8796 9623/9892/8794 9607/9876/8778\r\nf 9626/9895/8797 9627/9896/8798 9623/9892/8794 9625/9894/8796\r\nf 9628/9897/8799 9624/9893/8795 9623/9892/8794 9627/9896/8798\r\nf 9606/9875/8777 9612/9881/8783 9629/9898/8800 9625/9894/8796\r\nf 9611/9880/8782 9630/9899/8801 9629/9898/8800 9612/9881/8783\r\nf 9631/9900/8802 9632/9901/8803 9629/9898/8800 9630/9899/8801\r\nf 9626/9895/8797 9625/9894/8796 9629/9898/8800 9632/9901/8803\r\nf 9618/9887/8789 9617/9886/8788 9633/9902/8804 9634/9903/8805\r\nf 9616/9885/8787 9635/9904/8806 9633/9902/8804 9617/9886/8788\r\nf 9636/9905/8807 9637/9906/8808 9633/9902/8804 9635/9904/8806\r\nf 9638/9907/8809 9634/9903/8805 9633/9902/8804 9637/9906/8808\r\nf 9616/9885/8787 9622/9891/8793 9639/9908/8810 9635/9904/8806\r\nf 9621/9890/8792 9640/9909/8811 9639/9908/8810 9622/9891/8793\r\nf 9641/9910/8812 9642/9911/8813 9639/9908/8810 9640/9909/8811\r\nf 9636/9905/8807 9635/9904/8806 9639/9908/8810 9642/9911/8813\r\nf 9628/9897/8799 9627/9896/8798 9643/9912/8814 9644/9913/8815\r\nf 9626/9895/8797 9645/9914/8816 9643/9912/8814 9627/9896/8798\r\nf 9646/9915/8817 9647/9916/8818 9643/9912/8814 9645/9914/8816\r\nf 9648/9917/8819 9644/9913/8815 9643/9912/8814 9647/9916/8818\r\nf 9626/9895/8797 9632/9901/8803 9649/9918/8820 9645/9914/8816\r\nf 9631/9900/8802 9650/9919/8821 9649/9918/8820 9632/9901/8803\r\nf 9651/9920/8822 9652/9921/8823 9649/9918/8820 9650/9919/8821\r\nf 9646/9915/8817 9645/9914/8816 9649/9918/8820 9652/9921/8823\r\nf 9638/9907/8809 9637/9906/8808 9653/9922/8824 9654/9923/8825\r\nf 9636/9905/8807 9655/9924/8826 9653/9922/8824 9637/9906/8808\r\nf 9656/9925/8827 9657/9926/8828 9653/9922/8824 9655/9924/8826\r\nf 9658/9927/8829 9654/9923/8825 9653/9922/8824 9657/9926/8828\r\nf 9636/9905/8807 9642/9911/8813 9659/9928/8830 9655/9924/8826\r\nf 9641/9910/8812 9660/9929/8831 9659/9928/8830 9642/9911/8813\r\nf 9661/9930/8832 9662/9931/8833 9659/9928/8830 9660/9929/8831\r\nf 9656/9925/8827 9655/9924/8826 9659/9928/8830 9662/9931/8833\r\nf 9648/9917/8819 9647/9916/8818 9663/9932/8834 9664/9933/8835\r\nf 9646/9915/8817 9665/9934/8836 9663/9932/8834 9647/9916/8818\r\nf 9666/9935/8837 9667/9936/8838 9663/9932/8834 9665/9934/8836\r\nf 9668/9937/8839 9664/9933/8835 9663/9932/8834 9667/9936/8838\r\nf 9646/9915/8817 9652/9921/8823 9669/9938/8840 9665/9934/8836\r\nf 9651/9920/8822 9670/9939/8841 9669/9938/8840 9652/9921/8823\r\nf 9671/9940/8842 9672/9941/8843 9669/9938/8840 9670/9939/8841\r\nf 9666/9935/8837 9665/9934/8836 9669/9938/8840 9672/9941/8843\r\nf 9658/9927/8829 9657/9926/8828 9673/9942/8844 9674/9943/8845\r\nf 9656/9925/8827 9675/9944/8846 9673/9942/8844 9657/9926/8828\r\nf 9676/9945/8847 9677/9946/8848 9673/9942/8844 9675/9944/8846\r\nf 9678/9947/8849 9674/9943/8845 9673/9942/8844 9677/9946/8848\r\nf 9656/9925/8827 9662/9931/8833 9679/9948/8850 9675/9944/8846\r\nf 9661/9930/8832 9680/9949/8851 9679/9948/8850 9662/9931/8833\r\nf 9681/9950/8852 9682/9951/8853 9679/9948/8850 9680/9949/8851\r\nf 9676/9945/8847 9675/9944/8846 9679/9948/8850 9682/9951/8853\r\nf 9668/9937/8839 9667/9936/8838 9683/9952/8854 9684/9953/8855\r\nf 9666/9935/8837 9685/9954/8856 9683/9952/8854 9667/9936/8838\r\nf 9686/9955/8857 9687/9956/8858 9683/9952/8854 9685/9954/8856\r\nf 9688/9957/8859 9684/9953/8855 9683/9952/8854 9687/9956/8858\r\nf 9666/9935/8837 9672/9941/8843 9689/9958/8860 9685/9954/8856\r\nf 9671/9940/8842 9690/9959/8861 9689/9958/8860 9672/9941/8843\r\nf 9691/9960/8862 9692/9961/8863 9689/9958/8860 9690/9959/8861\r\nf 9686/9955/8857 9685/9954/8856 9689/9958/8860 9692/9961/8863\r\nf 9678/9947/8849 9677/9946/8848 9693/9962/8864 9694/9963/8865\r\nf 9676/9945/8847 9695/9964/8866 9693/9962/8864 9677/9946/8848\r\nf 9696/9965/8867 9697/9966/8868 9693/9962/8864 9695/9964/8866\r\nf 9698/9967/8869 9694/9963/8865 9693/9962/8864 9697/9966/8868\r\nf 9676/9945/8847 9682/9951/8853 9699/9968/8870 9695/9964/8866\r\nf 9681/9950/8852 9700/9969/8871 9699/9968/8870 9682/9951/8853\r\nf 9701/9970/8872 9702/9971/8873 9699/9968/8870 9700/9969/8871\r\nf 9696/9965/8867 9695/9964/8866 9699/9968/8870 9702/9971/8873\r\nf 9688/9957/8859 9687/9956/8858 9703/9972/8874 9704/9973/8875\r\nf 9686/9955/8857 9705/9974/8876 9703/9972/8874 9687/9956/8858\r\nf 9706/9975/8877 9707/9976/8878 9703/9972/8874 9705/9974/8876\r\nf 9708/9977/8879 9704/9973/8875 9703/9972/8874 9707/9976/8878\r\nf 9686/9955/8857 9692/9961/8863 9709/9978/8880 9705/9974/8876\r\nf 9691/9960/8862 9710/9979/8881 9709/9978/8880 9692/9961/8863\r\nf 9711/9980/8882 9712/9981/8883 9709/9978/8880 9710/9979/8881\r\nf 9706/9975/8877 9705/9974/8876 9709/9978/8880 9712/9981/8883\r\nf 9698/9967/8869 9697/9966/8868 9713/9982/8884 9714/9983/8885\r\nf 9696/9965/8867 9715/9984/8886 9713/9982/8884 9697/9966/8868\r\nf 9716/9985/8887 9717/9986/8888 9713/9982/8884 9715/9984/8886\r\nf 9718/9987/8889 9714/9983/8885 9713/9982/8884 9717/9986/8888\r\nf 9696/9965/8867 9702/9971/8873 9719/9988/8890 9715/9984/8886\r\nf 9701/9970/8872 9720/9989/8891 9719/9988/8890 9702/9971/8873\r\nf 9721/9990/8892 9722/9991/8893 9719/9988/8890 9720/9989/8891\r\nf 9716/9985/8887 9715/9984/8886 9719/9988/8890 9722/9991/8893\r\nf 9708/9977/8879 9707/9976/8878 9723/9992/8894 9724/9993/8895\r\nf 9706/9975/8877 9725/9994/8896 9723/9992/8894 9707/9976/8878\r\nf 9726/9995/8897 9727/9996/8898 9723/9992/8894 9725/9994/8896\r\nf 9728/9997/8899 9724/9993/8895 9723/9992/8894 9727/9996/8898\r\nf 9706/9975/8877 9712/9981/8883 9729/9998/8900 9725/9994/8896\r\nf 9711/9980/8882 9730/9999/8901 9729/9998/8900 9712/9981/8883\r\nf 9731/10000/8902 9732/10001/8903 9729/9998/8900 9730/9999/8901\r\nf 9726/9995/8897 9725/9994/8896 9729/9998/8900 9732/10001/8903\r\nf 9718/9987/8889 9717/9986/8888 9733/10002/8904 9734/10003/8905\r\nf 9716/9985/8887 9735/10004/8906 9733/10002/8904 9717/9986/8888\r\nf 9736/10005/8907 9737/10006/8908 9733/10002/8904 9735/10004/8906\r\nf 9738/10007/8909 9734/10003/8905 9733/10002/8904 9737/10006/8908\r\nf 9716/9985/8887 9722/9991/8893 9739/10008/8910 9735/10004/8906\r\nf 9721/9990/8892 9740/10009/8911 9739/10008/8910 9722/9991/8893\r\nf 9741/10010/8912 9742/10011/8913 9739/10008/8910 9740/10009/8911\r\nf 9736/10005/8907 9735/10004/8906 9739/10008/8910 9742/10011/8913\r\nf 9728/9997/8899 9727/9996/8898 9743/10012/8914 9744/10013/8915\r\nf 9726/9995/8897 9745/10014/8916 9743/10012/8914 9727/9996/8898\r\nf 9746/10015/8917 9747/10016/8918 9743/10012/8914 9745/10014/8916\r\nf 9748/10017/8919 9744/10013/8915 9743/10012/8914 9747/10016/8918\r\nf 9726/9995/8897 9732/10001/8903 9749/10018/8920 9745/10014/8916\r\nf 9731/10000/8902 9750/10019/8921 9749/10018/8920 9732/10001/8903\r\nf 9751/10020/8922 9752/10021/8923 9749/10018/8920 9750/10019/8921\r\nf 9746/10015/8917 9745/10014/8916 9749/10018/8920 9752/10021/8923\r\nf 9738/10007/8909 9737/10006/8908 9753/10022/8924 9754/10023/8925\r\nf 9736/10005/8907 9755/10024/8926 9753/10022/8924 9737/10006/8908\r\nf 9756/10025/8927 9757/10026/8928 9753/10022/8924 9755/10024/8926\r\nf 9758/10027/8929 9754/10023/8925 9753/10022/8924 9757/10026/8928\r\nf 9736/10005/8907 9742/10011/8913 9759/10028/8930 9755/10024/8926\r\nf 9741/10010/8912 9760/10029/8931 9759/10028/8930 9742/10011/8913\r\nf 9761/10030/8932 9762/10031/8933 9759/10028/8930 9760/10029/8931\r\nf 9756/10025/8927 9755/10024/8926 9759/10028/8930 9762/10031/8933\r\nf 9748/10017/8919 9747/10016/8918 9763/10032/8934 9764/10033/8935\r\nf 9746/10015/8917 9765/10034/8936 9763/10032/8934 9747/10016/8918\r\nf 9766/10035/8937 9767/10036/8938 9763/10032/8934 9765/10034/8936\r\nf 9768/10037/8939 9764/10033/8935 9763/10032/8934 9767/10036/8938\r\nf 9746/10015/8917 9752/10021/8923 9769/10038/8940 9765/10034/8936\r\nf 9751/10020/8922 9770/10039/8941 9769/10038/8940 9752/10021/8923\r\nf 9771/10040/8942 9772/10041/8943 9769/10038/8940 9770/10039/8941\r\nf 9766/10035/8937 9765/10034/8936 9769/10038/8940 9772/10041/8943\r\nf 9758/10027/8929 9757/10026/8928 9773/10042/8944 9774/10043/8945\r\nf 9756/10025/8927 9775/10044/8946 9773/10042/8944 9757/10026/8928\r\nf 9776/10045/8947 9777/10046/8948 9773/10042/8944 9775/10044/8946\r\nf 9778/10047/8949 9774/10043/8945 9773/10042/8944 9777/10046/8948\r\nf 9756/10025/8927 9762/10031/8933 9779/10048/8950 9775/10044/8946\r\nf 9761/10030/8932 9780/10049/8951 9779/10048/8950 9762/10031/8933\r\nf 9781/10050/8952 9782/10051/8953 9779/10048/8950 9780/10049/8951\r\nf 9776/10045/8947 9775/10044/8946 9779/10048/8950 9782/10051/8953\r\nf 9768/10037/8939 9767/10036/8938 9783/10052/8954 9784/10053/8955\r\nf 9766/10035/8937 9785/10054/8956 9783/10052/8954 9767/10036/8938\r\nf 9786/10055/8957 9787/10056/8958 9783/10052/8954 9785/10054/8956\r\nf 9788/10057/8959 9784/10053/8955 9783/10052/8954 9787/10056/8958\r\nf 9766/10035/8937 9772/10041/8943 9789/10058/8960 9785/10054/8956\r\nf 9771/10040/8942 9790/10059/8961 9789/10058/8960 9772/10041/8943\r\nf 9791/10060/8962 9792/10061/8963 9789/10058/8960 9790/10059/8961\r\nf 9786/10055/8957 9785/10054/8956 9789/10058/8960 9792/10061/8963\r\nf 9778/10047/8949 9777/10046/8948 9793/10062/8964 9794/10063/8965\r\nf 9776/10045/8947 9795/10064/8966 9793/10062/8964 9777/10046/8948\r\nf 9796/10065/8967 9797/10066/8968 9793/10062/8964 9795/10064/8966\r\nf 9798/10067/8969 9794/10063/8965 9793/10062/8964 9797/10066/8968\r\nf 9776/10045/8947 9782/10051/8953 9799/10068/8970 9795/10064/8966\r\nf 9781/10050/8952 9800/10069/8971 9799/10068/8970 9782/10051/8953\r\nf 9801/10070/8972 9802/10071/8973 9799/10068/8970 9800/10069/8971\r\nf 9796/10065/8967 9795/10064/8966 9799/10068/8970 9802/10071/8973\r\nf 9788/10057/8959 9787/10056/8958 9803/10072/8974 9804/10073/8975\r\nf 9786/10055/8957 9805/10074/8976 9803/10072/8974 9787/10056/8958\r\nf 9806/10075/8977 9807/10076/8978 9803/10072/8974 9805/10074/8976\r\nf 9808/10077/8979 9804/10073/8975 9803/10072/8974 9807/10076/8978\r\nf 9786/10055/8957 9792/10061/8963 9809/10078/8980 9805/10074/8976\r\nf 9791/10060/8962 9810/10079/8981 9809/10078/8980 9792/10061/8963\r\nf 9811/10080/8982 9812/10081/8983 9809/10078/8980 9810/10079/8981\r\nf 9806/10075/8977 9805/10074/8976 9809/10078/8980 9812/10081/8983\r\nf 9798/10067/8969 9797/10066/8968 9813/10082/8984 9814/10083/8985\r\nf 9796/10065/8967 9815/10084/8986 9813/10082/8984 9797/10066/8968\r\nf 9816/10085/8987 9817/10086/8988 9813/10082/8984 9815/10084/8986\r\nf 9818/10087/8989 9814/10083/8985 9813/10082/8984 9817/10086/8988\r\nf 9796/10065/8967 9802/10071/8973 9819/10088/8990 9815/10084/8986\r\nf 9801/10070/8972 9820/10089/8991 9819/10088/8990 9802/10071/8973\r\nf 9821/10090/8992 9822/10091/8993 9819/10088/8990 9820/10089/8991\r\nf 9816/10085/8987 9815/10084/8986 9819/10088/8990 9822/10091/8993\r\nf 9808/10077/8979 9807/10076/8978 9823/10092/8994 9824/10093/8995\r\nf 9806/10075/8977 9825/10094/8996 9823/10092/8994 9807/10076/8978\r\nf 9826/10095/8997 9827/10096/8998 9823/10092/8994 9825/10094/8996\r\nf 9828/10097/8999 9824/10093/8995 9823/10092/8994 9827/10096/8998\r\nf 9806/10075/8977 9812/10081/8983 9829/10098/9000 9825/10094/8996\r\nf 9811/10080/8982 9830/10099/9001 9829/10098/9000 9812/10081/8983\r\nf 9831/10100/9002 9832/10101/9003 9829/10098/9000 9830/10099/9001\r\nf 9826/10095/8997 9825/10094/8996 9829/10098/9000 9832/10101/9003\r\nf 9818/10087/8989 9817/10086/8988 9833/10102/9004 9834/10103/9005\r\nf 9816/10085/8987 9835/10104/9006 9833/10102/9004 9817/10086/8988\r\nf 9836/10105/9007 9837/10106/9008 9833/10102/9004 9835/10104/9006\r\nf 9838/10107/9009 9834/10103/9005 9833/10102/9004 9837/10106/9008\r\nf 9816/10085/8987 9822/10091/8993 9839/10108/9010 9835/10104/9006\r\nf 9821/10090/8992 9840/10109/9011 9839/10108/9010 9822/10091/8993\r\nf 9841/10110/9012 9842/10111/9013 9839/10108/9010 9840/10109/9011\r\nf 9836/10105/9007 9835/10104/9006 9839/10108/9010 9842/10111/9013\r\nf 9828/10097/8999 9827/10096/8998 9843/10112/9014 9844/10113/9015\r\nf 9826/10095/8997 9845/10114/9016 9843/10112/9014 9827/10096/8998\r\nf 9846/10115/9017 9847/10116/9018 9843/10112/9014 9845/10114/9016\r\nf 9848/10117/9019 9844/10113/9015 9843/10112/9014 9847/10116/9018\r\nf 9826/10095/8997 9832/10101/9003 9849/10118/9020 9845/10114/9016\r\nf 9831/10100/9002 9850/10119/9021 9849/10118/9020 9832/10101/9003\r\nf 9851/10120/9022 9852/10121/9023 9849/10118/9020 9850/10119/9021\r\nf 9846/10115/9017 9845/10114/9016 9849/10118/9020 9852/10121/9023\r\nf 9838/10107/9009 9837/10106/9008 9853/10122/9024 9854/10123/9025\r\nf 9836/10105/9007 9855/10124/9026 9853/10122/9024 9837/10106/9008\r\nf 9856/10125/9027 9857/10126/9028 9853/10122/9024 9855/10124/9026\r\nf 9858/10127/9029 9854/10123/9025 9853/10122/9024 9857/10126/9028\r\nf 9836/10105/9007 9842/10111/9013 9859/10128/9030 9855/10124/9026\r\nf 9841/10110/9012 9860/10129/9031 9859/10128/9030 9842/10111/9013\r\nf 9861/10130/9032 9862/10131/9033 9859/10128/9030 9860/10129/9031\r\nf 9856/10125/9027 9855/10124/9026 9859/10128/9030 9862/10131/9033\r\nf 9848/10117/9019 9847/10116/9018 9863/10132/9034 9864/10133/9035\r\nf 9846/10115/9017 9865/10134/9036 9863/10132/9034 9847/10116/9018\r\nf 9866/10135/9037 9867/10136/9038 9863/10132/9034 9865/10134/9036\r\nf 9868/10137/9039 9864/10133/9035 9863/10132/9034 9867/10136/9038\r\nf 9846/10115/9017 9852/10121/9023 9869/10138/9040 9865/10134/9036\r\nf 9851/10120/9022 9870/10139/9041 9869/10138/9040 9852/10121/9023\r\nf 9871/10140/9042 9872/10141/9043 9869/10138/9040 9870/10139/9041\r\nf 9866/10135/9037 9865/10134/9036 9869/10138/9040 9872/10141/9043\r\nf 9858/10127/9029 9857/10126/9028 9873/10142/9044 9874/10143/9045\r\nf 9856/10125/9027 9875/10144/9046 9873/10142/9044 9857/10126/9028\r\nf 9876/10145/9047 9877/10146/9048 9873/10142/9044 9875/10144/9046\r\nf 9878/10147/9049 9874/10143/9045 9873/10142/9044 9877/10146/9048\r\nf 9856/10125/9027 9862/10131/9033 9879/10148/9050 9875/10144/9046\r\nf 9861/10130/9032 9880/10149/9051 9879/10148/9050 9862/10131/9033\r\nf 9881/10150/9052 9882/10151/9053 9879/10148/9050 9880/10149/9051\r\nf 9876/10145/9047 9875/10144/9046 9879/10148/9050 9882/10151/9053\r\nf 9868/10137/9039 9867/10136/9038 9883/10152/9054 9884/10153/9055\r\nf 9866/10135/9037 9885/10154/9056 9883/10152/9054 9867/10136/9038\r\nf 9886/10155/9057 9887/10156/9058 9883/10152/9054 9885/10154/9056\r\nf 9888/10157/9059 9884/10153/9055 9883/10152/9054 9887/10156/9058\r\nf 9866/10135/9037 9872/10141/9043 9889/10158/9060 9885/10154/9056\r\nf 9871/10140/9042 9890/10159/9061 9889/10158/9060 9872/10141/9043\r\nf 9891/10160/9062 9892/10161/9063 9889/10158/9060 9890/10159/9061\r\nf 9886/10155/9057 9885/10154/9056 9889/10158/9060 9892/10161/9063\r\nf 9878/10147/9049 9877/10146/9048 9893/10162/9064 9894/10163/9065\r\nf 9876/10145/9047 9895/10164/9066 9893/10162/9064 9877/10146/9048\r\nf 9896/10165/9067 9897/10166/9048 9893/10162/9064 9895/10164/9066\r\nf 9898/10167/9068 9894/10163/9065 9893/10162/9064 9897/10166/9048\r\nf 9876/10145/9047 9882/10151/9053 9899/10168/9069 9895/10164/9066\r\nf 9881/10150/9052 9900/10169/9070 9899/10168/9069 9882/10151/9053\r\nf 9901/10170/9071 9902/10171/9072 9899/10168/9069 9900/10169/9070\r\nf 9896/10165/9067 9895/10164/9066 9899/10168/9069 9902/10171/9072\r\nf 9888/10157/9059 9887/10156/9058 9903/10172/9073 9904/10173/9074\r\nf 9886/10155/9057 9905/10174/9075 9903/10172/9073 9887/10156/9058\r\nf 9906/10175/9076 9907/10176/9058 9903/10172/9073 9905/10174/9075\r\nf 9908/10177/9077 9904/10173/9074 9903/10172/9073 9907/10176/9058\r\nf 9886/10155/9057 9892/10161/9063 9909/10178/9078 9905/10174/9075\r\nf 9891/10160/9062 9910/10179/9079 9909/10178/9078 9892/10161/9063\r\nf 9911/10180/9080 9912/10181/9081 9909/10178/9078 9910/10179/9079\r\nf 9906/10175/9076 9905/10174/9075 9909/10178/9078 9912/10181/9081\r\nf 9898/10167/9068 9897/10166/9048 9913/10182/9082 9914/10183/9083\r\nf 9896/10165/9067 9915/10184/9084 9913/10182/9082 9897/10166/9048\r\nf 9916/10185/9085 9917/10186/9086 9913/10182/9082 9915/10184/9084\r\nf 9918/10187/9087 9914/10183/9083 9913/10182/9082 9917/10186/9086\r\nf 9896/10165/9067 9902/10171/9072 9919/10188/9088 9915/10184/9084\r\nf 9901/10170/9071 9920/10189/9089 9919/10188/9088 9902/10171/9072\r\nf 9921/10190/9090 9922/10191/9091 9919/10188/9088 9920/10189/9089\r\nf 9916/10185/9085 9915/10184/9084 9919/10188/9088 9922/10191/9091\r\nf 9908/10177/9077 9907/10176/9058 9923/10192/9092 9924/10193/9093\r\nf 9906/10175/9076 9925/10194/9094 9923/10192/9092 9907/10176/9058\r\nf 9926/10195/9095 9927/10196/9096 9923/10192/9092 9925/10194/9094\r\nf 9928/10197/9097 9924/10193/9093 9923/10192/9092 9927/10196/9096\r\nf 9906/10175/9076 9912/10181/9081 9929/10198/9098 9925/10194/9094\r\nf 9911/10180/9080 9930/10199/9099 9929/10198/9098 9912/10181/9081\r\nf 9931/10200/9100 9932/10201/9101 9929/10198/9098 9930/10199/9099\r\nf 9926/10195/9095 9925/10194/9094 9929/10198/9098 9932/10201/9101\r\nf 9918/10187/9087 9917/10186/9086 9933/10202/9102 9934/10203/9103\r\nf 9916/10185/9085 9935/10204/9104 9933/10202/9102 9917/10186/9086\r\nf 9936/10205/9105 9937/10206/9106 9933/10202/9102 9935/10204/9104\r\nf 9938/10207/9107 9934/10203/9103 9933/10202/9102 9937/10206/9106\r\nf 9916/10185/9085 9922/10191/9091 9939/10208/9108 9935/10204/9104\r\nf 9921/10190/9090 9940/10209/9109 9939/10208/9108 9922/10191/9091\r\nf 9941/10210/9110 9942/10211/9111 9939/10208/9108 9940/10209/9109\r\nf 9936/10205/9105 9935/10204/9104 9939/10208/9108 9942/10211/9111\r\nf 9928/10197/9097 9927/10196/9096 9943/10212/9112 9944/10213/9113\r\nf 9926/10195/9095 9945/10214/9114 9943/10212/9112 9927/10196/9096\r\nf 9946/10215/9115 9947/10216/9116 9943/10212/9112 9945/10214/9114\r\nf 9948/10217/9117 9944/10213/9113 9943/10212/9112 9947/10216/9116\r\nf 9926/10195/9095 9932/10201/9101 9949/10218/9118 9945/10214/9114\r\nf 9931/10200/9100 9950/10219/9119 9949/10218/9118 9932/10201/9101\r\nf 9951/10220/9120 9952/10221/9121 9949/10218/9118 9950/10219/9119\r\nf 9946/10215/9115 9945/10214/9114 9949/10218/9118 9952/10221/9121\r\nf 9938/10207/9107 9937/10206/9106 9953/10222/9122 9954/10223/9123\r\nf 9936/10205/9105 9955/10224/9124 9953/10222/9122 9937/10206/9106\r\nf 9956/10225/9125 9957/10226/9126 9953/10222/9122 9955/10224/9124\r\nf 9958/10227/9127 9954/10223/9123 9953/10222/9122 9957/10226/9126\r\nf 9936/10205/9105 9942/10211/9111 9959/10228/9128 9955/10224/9124\r\nf 9941/10210/9110 9960/10229/9129 9959/10228/9128 9942/10211/9111\r\nf 9961/10230/9130 9962/10231/9131 9959/10228/9128 9960/10229/9129\r\nf 9956/10225/9125 9955/10224/9124 9959/10228/9128 9962/10231/9131\r\nf 9948/10217/9117 9947/10216/9116 9963/10232/9132 9964/10233/9133\r\nf 9946/10215/9115 9965/10234/9134 9963/10232/9132 9947/10216/9116\r\nf 9966/10235/9135 9967/10236/9136 9963/10232/9132 9965/10234/9134\r\nf 9968/10237/9137 9964/10233/9133 9963/10232/9132 9967/10236/9136\r\nf 9946/10215/9115 9952/10221/9121 9969/10238/9138 9965/10234/9134\r\nf 9951/10220/9120 9970/10239/9139 9969/10238/9138 9952/10221/9121\r\nf 9971/10240/9140 9972/10241/9141 9969/10238/9138 9970/10239/9139\r\nf 9966/10235/9135 9965/10234/9134 9969/10238/9138 9972/10241/9141\r\nf 9958/10227/9127 9957/10226/9126 9973/10242/9142 9974/10243/9143\r\nf 9956/10225/9125 9975/10244/9144 9973/10242/9142 9957/10226/9126\r\nf 9976/10245/9145 9977/10246/9146 9973/10242/9142 9975/10244/9144\r\nf 9978/10247/9147 9974/10243/9143 9973/10242/9142 9977/10246/9146\r\nf 9956/10225/9125 9962/10231/9131 9979/10248/9148 9975/10244/9144\r\nf 9961/10230/9130 9980/10249/9149 9979/10248/9148 9962/10231/9131\r\nf 9981/10250/9150 9982/10251/9151 9979/10248/9148 9980/10249/9149\r\nf 9976/10245/9145 9975/10244/9144 9979/10248/9148 9982/10251/9151\r\nf 9968/10237/9137 9967/10236/9136 9983/10252/9152 9984/10253/9153\r\nf 9966/10235/9135 9985/10254/9154 9983/10252/9152 9967/10236/9136\r\nf 9986/10255/9155 9987/10256/9156 9983/10252/9152 9985/10254/9154\r\nf 9988/10257/9157 9984/10253/9153 9983/10252/9152 9987/10256/9156\r\nf 9966/10235/9135 9972/10241/9141 9989/10258/9158 9985/10254/9154\r\nf 9971/10240/9140 9990/10259/9159 9989/10258/9158 9972/10241/9141\r\nf 9991/10260/9160 9992/10261/9161 9989/10258/9158 9990/10259/9159\r\nf 9986/10255/9155 9985/10254/9154 9989/10258/9158 9992/10261/9161\r\nf 9978/10247/9147 9977/10246/9146 9993/10262/9162 9994/10263/9163\r\nf 9976/10245/9145 9995/10264/9164 9993/10262/9162 9977/10246/9146\r\nf 9996/10265/9165 9997/10266/9166 9993/10262/9162 9995/10264/9164\r\nf 9998/10267/9167 9994/10263/9163 9993/10262/9162 9997/10266/9166\r\nf 9976/10245/9145 9982/10251/9151 9999/10268/9168 9995/10264/9164\r\nf 9981/10250/9150 10000/10269/9169 9999/10268/9168 9982/10251/9151\r\nf 10001/10270/9170 10002/10271/9171 9999/10268/9168 10000/10269/9169\r\nf 9996/10265/9165 9995/10264/9164 9999/10268/9168 10002/10271/9171\r\nf 9988/10257/9157 9987/10256/9156 10003/10272/9172 10004/10273/9173\r\nf 9986/10255/9155 10005/10274/9174 10003/10272/9172 9987/10256/9156\r\nf 10006/10275/9175 10007/10276/9176 10003/10272/9172 10005/10274/9174\r\nf 10008/10277/9177 10004/10273/9173 10003/10272/9172 10007/10276/9176\r\nf 9986/10255/9155 9992/10261/9161 10009/10278/9178 10005/10274/9174\r\nf 9991/10260/9160 10010/10279/9179 10009/10278/9178 9992/10261/9161\r\nf 10011/10280/9180 10012/10281/9181 10009/10278/9178 10010/10279/9179\r\nf 10006/10275/9175 10005/10274/9174 10009/10278/9178 10012/10281/9181\r\nf 9998/10267/9167 9997/10266/9166 10013/10282/9182 10014/10283/9183\r\nf 9996/10265/9165 10015/10284/9184 10013/10282/9182 9997/10266/9166\r\nf 10016/10285/9185 10017/10286/9186 10013/10282/9182 10015/10284/9184\r\nf 10018/10287/9187 10014/10283/9183 10013/10282/9182 10017/10286/9186\r\nf 9996/10265/9165 10002/10271/9171 10019/10288/9188 10015/10284/9184\r\nf 10001/10270/9170 10020/10289/9189 10019/10288/9188 10002/10271/9171\r\nf 10021/10290/9190 10022/10291/9191 10019/10288/9188 10020/10289/9189\r\nf 10016/10285/9185 10015/10284/9184 10019/10288/9188 10022/10291/9191\r\nf 10008/10277/9177 10007/10276/9176 10023/10292/9192 10024/10293/9193\r\nf 10006/10275/9175 10025/10294/9194 10023/10292/9192 10007/10276/9176\r\nf 10026/10295/9195 10027/10296/9196 10023/10292/9192 10025/10294/9194\r\nf 10028/10297/9197 10024/10293/9193 10023/10292/9192 10027/10296/9196\r\nf 10006/10275/9175 10012/10281/9181 10029/10298/9198 10025/10294/9194\r\nf 10011/10280/9180 10030/10299/9199 10029/10298/9198 10012/10281/9181\r\nf 10031/10300/9200 10032/10301/9201 10029/10298/9198 10030/10299/9199\r\nf 10026/10295/9195 10025/10294/9194 10029/10298/9198 10032/10301/9201\r\nf 10018/10287/9187 10017/10286/9186 10033/10302/9202 10034/10303/9203\r\nf 10016/10285/9185 10035/10304/9204 10033/10302/9202 10017/10286/9186\r\nf 10036/10305/9205 10037/10306/9206 10033/10302/9202 10035/10304/9204\r\nf 10038/10307/9207 10034/10303/9203 10033/10302/9202 10037/10306/9206\r\nf 10016/10285/9185 10022/10291/9191 10039/10308/9208 10035/10304/9204\r\nf 10021/10290/9190 10040/10309/9209 10039/10308/9208 10022/10291/9191\r\nf 10041/10310/9210 10042/10311/9211 10039/10308/9208 10040/10309/9209\r\nf 10036/10305/9205 10035/10304/9204 10039/10308/9208 10042/10311/9211\r\nf 10028/10297/9197 10027/10296/9196 10043/10312/9212 10044/10313/9213\r\nf 10026/10295/9195 10045/10314/9214 10043/10312/9212 10027/10296/9196\r\nf 10046/10315/9215 10047/10316/9216 10043/10312/9212 10045/10314/9214\r\nf 10048/10317/9217 10044/10313/9213 10043/10312/9212 10047/10316/9216\r\nf 10026/10295/9195 10032/10301/9201 10049/10318/9218 10045/10314/9214\r\nf 10031/10300/9200 10050/10319/9219 10049/10318/9218 10032/10301/9201\r\nf 10051/10320/9220 10052/10321/9221 10049/10318/9218 10050/10319/9219\r\nf 10046/10315/9215 10045/10314/9214 10049/10318/9218 10052/10321/9221\r\nf 10038/10307/9207 10037/10306/9206 10053/10322/9222 10054/10323/9223\r\nf 10036/10305/9205 10055/10324/9224 10053/10322/9222 10037/10306/9206\r\nf 10056/10325/9225 10057/10326/9226 10053/10322/9222 10055/10324/9224\r\nf 10058/10327/9227 10054/10323/9223 10053/10322/9222 10057/10326/9226\r\nf 10036/10305/9205 10042/10311/9211 10059/10328/9228 10055/10324/9224\r\nf 10041/10310/9210 10060/10329/9229 10059/10328/9228 10042/10311/9211\r\nf 10061/10330/9230 10062/10331/9231 10059/10328/9228 10060/10329/9229\r\nf 10056/10325/9225 10055/10324/9224 10059/10328/9228 10062/10331/9231\r\nf 10048/10317/9217 10047/10316/9216 10063/10332/9232 10064/10333/9233\r\nf 10046/10315/9215 10065/10334/9234 10063/10332/9232 10047/10316/9216\r\nf 10066/10335/9235 10067/10336/9236 10063/10332/9232 10065/10334/9234\r\nf 10068/10337/9237 10064/10333/9233 10063/10332/9232 10067/10336/9236\r\nf 10046/10315/9215 10052/10321/9221 10069/10338/9238 10065/10334/9234\r\nf 10051/10320/9220 10070/10339/9239 10069/10338/9238 10052/10321/9221\r\nf 10071/10340/9240 10072/10341/9241 10069/10338/9238 10070/10339/9239\r\nf 10066/10335/9235 10065/10334/9234 10069/10338/9238 10072/10341/9241\r\nf 10058/10327/9227 10057/10326/9226 10073/10342/9242 10074/10343/9243\r\nf 10056/10325/9225 10075/10344/9244 10073/10342/9242 10057/10326/9226\r\nf 10076/10345/9245 10077/10346/9246 10073/10342/9242 10075/10344/9244\r\nf 10078/10347/9247 10074/10343/9243 10073/10342/9242 10077/10346/9246\r\nf 10056/10325/9225 10062/10331/9231 10079/10348/9248 10075/10344/9244\r\nf 10061/10330/9230 10080/10349/9249 10079/10348/9248 10062/10331/9231\r\nf 10081/10350/9250 10082/10351/9251 10079/10348/9248 10080/10349/9249\r\nf 10076/10345/9245 10075/10344/9244 10079/10348/9248 10082/10351/9251\r\nf 10068/10337/9237 10067/10336/9236 10083/10352/9252 10084/10353/9253\r\nf 10066/10335/9235 10085/10354/9254 10083/10352/9252 10067/10336/9236\r\nf 10086/10355/9255 10087/10356/9256 10083/10352/9252 10085/10354/9254\r\nf 10088/10357/9257 10084/10353/9253 10083/10352/9252 10087/10356/9256\r\nf 10066/10335/9235 10072/10341/9241 10089/10358/9258 10085/10354/9254\r\nf 10071/10340/9240 10090/10359/9259 10089/10358/9258 10072/10341/9241\r\nf 10091/10360/9260 10092/10361/9261 10089/10358/9258 10090/10359/9259\r\nf 10086/10355/9255 10085/10354/9254 10089/10358/9258 10092/10361/9261\r\nf 10078/10347/9247 10077/10346/9246 10093/10362/9262 10094/10363/9263\r\nf 10076/10345/9245 10095/10364/9264 10093/10362/9262 10077/10346/9246\r\nf 10096/10365/9265 10097/10366/9266 10093/10362/9262 10095/10364/9264\r\nf 10098/10367/9267 10094/10363/9263 10093/10362/9262 10097/10366/9266\r\nf 10076/10345/9245 10082/10351/9251 10099/10368/9268 10095/10364/9264\r\nf 10081/10350/9250 10100/10369/9269 10099/10368/9268 10082/10351/9251\r\nf 10101/10370/9270 10102/10371/9271 10099/10368/9268 10100/10369/9269\r\nf 10096/10365/9265 10095/10364/9264 10099/10368/9268 10102/10371/9271\r\nf 10088/10357/9257 10087/10356/9256 10103/10372/9272 10104/10373/9273\r\nf 10086/10355/9255 10105/10374/9274 10103/10372/9272 10087/10356/9256\r\nf 10106/10375/9275 10107/10376/9276 10103/10372/9272 10105/10374/9274\r\nf 10108/10377/9277 10104/10373/9273 10103/10372/9272 10107/10376/9276\r\nf 10086/10355/9255 10092/10361/9261 10109/10378/9278 10105/10374/9274\r\nf 10091/10360/9260 10110/10379/9279 10109/10378/9278 10092/10361/9261\r\nf 10111/10380/9280 10112/10381/9281 10109/10378/9278 10110/10379/9279\r\nf 10106/10375/9275 10105/10374/9274 10109/10378/9278 10112/10381/9281\r\nf 10098/10367/9267 10097/10366/9266 10113/10382/9282 10114/10383/9283\r\nf 10096/10365/9265 10115/10384/9284 10113/10382/9282 10097/10366/9266\r\nf 10116/10385/9285 10117/10386/9286 10113/10382/9282 10115/10384/9284\r\nf 10118/10387/9287 10114/10383/9283 10113/10382/9282 10117/10386/9286\r\nf 10096/10365/9265 10102/10371/9271 10119/10388/9288 10115/10384/9284\r\nf 10101/10370/9270 10120/10389/9289 10119/10388/9288 10102/10371/9271\r\nf 10121/10390/9290 10122/10391/9291 10119/10388/9288 10120/10389/9289\r\nf 10116/10385/9285 10115/10384/9284 10119/10388/9288 10122/10391/9291\r\nf 10108/10377/9277 10107/10376/9276 10123/10392/9292 10124/10393/9293\r\nf 10106/10375/9275 10125/10394/9294 10123/10392/9292 10107/10376/9276\r\nf 10126/10395/9295 10127/10396/9296 10123/10392/9292 10125/10394/9294\r\nf 10128/10397/9297 10124/10393/9293 10123/10392/9292 10127/10396/9296\r\nf 10106/10375/9275 10112/10381/9281 10129/10398/9298 10125/10394/9294\r\nf 10111/10380/9280 10130/10399/9299 10129/10398/9298 10112/10381/9281\r\nf 10131/10400/9300 10132/10401/9301 10129/10398/9298 10130/10399/9299\r\nf 10126/10395/9295 10125/10394/9294 10129/10398/9298 10132/10401/9301\r\nf 10118/10387/9287 10117/10386/9286 10133/10402/9302 10134/10403/9303\r\nf 10116/10385/9285 10135/10404/9304 10133/10402/9302 10117/10386/9286\r\nf 10136/10405/9305 10137/10406/9306 10133/10402/9302 10135/10404/9304\r\nf 10138/10407/9307 10134/10403/9303 10133/10402/9302 10137/10406/9306\r\nf 10116/10385/9285 10122/10391/9291 10139/10408/9308 10135/10404/9304\r\nf 10121/10390/9290 10140/10409/9309 10139/10408/9308 10122/10391/9291\r\nf 10141/10410/9310 10142/10411/9311 10139/10408/9308 10140/10409/9309\r\nf 10136/10405/9305 10135/10404/9304 10139/10408/9308 10142/10411/9311\r\nf 10128/10397/9297 10127/10396/9296 10143/10412/9312 10144/10413/9313\r\nf 10126/10395/9295 10145/10414/9314 10143/10412/9312 10127/10396/9296\r\nf 10146/10415/9315 10147/10416/9316 10143/10412/9312 10145/10414/9314\r\nf 10148/10417/9317 10144/10413/9313 10143/10412/9312 10147/10416/9316\r\nf 10126/10395/9295 10132/10401/9301 10149/10418/9318 10145/10414/9314\r\nf 10131/10400/9300 10150/10419/9319 10149/10418/9318 10132/10401/9301\r\nf 10151/10420/9320 10152/10421/9321 10149/10418/9318 10150/10419/9319\r\nf 10146/10415/9315 10145/10414/9314 10149/10418/9318 10152/10421/9321\r\nf 10138/10407/9307 10137/10406/9306 10153/10422/9322 10154/10423/9323\r\nf 10136/10405/9305 10155/10424/9324 10153/10422/9322 10137/10406/9306\r\nf 10156/10425/9325 10157/10426/9326 10153/10422/9322 10155/10424/9324\r\nf 10158/10427/9327 10154/10423/9323 10153/10422/9322 10157/10426/9326\r\nf 10136/10405/9305 10142/10411/9311 10159/10428/9328 10155/10424/9324\r\nf 10141/10410/9310 10160/10429/9329 10159/10428/9328 10142/10411/9311\r\nf 10161/10430/9330 10162/10431/9331 10159/10428/9328 10160/10429/9329\r\nf 10156/10425/9325 10155/10424/9324 10159/10428/9328 10162/10431/9331\r\nf 10148/10417/9317 10147/10416/9316 10163/10432/9332 10164/10433/9333\r\nf 10146/10415/9315 10165/10434/9334 10163/10432/9332 10147/10416/9316\r\nf 10166/10435/9335 10167/10436/9336 10163/10432/9332 10165/10434/9334\r\nf 10168/10437/9337 10164/10433/9333 10163/10432/9332 10167/10436/9336\r\nf 10146/10415/9315 10152/10421/9321 10169/10438/9338 10165/10434/9334\r\nf 10151/10420/9320 10170/10439/9339 10169/10438/9338 10152/10421/9321\r\nf 10171/10440/9340 10172/10441/9341 10169/10438/9338 10170/10439/9339\r\nf 10166/10435/9335 10165/10434/9334 10169/10438/9338 10172/10441/9341\r\nf 10158/10427/9327 10157/10426/9326 10173/10442/9342 10174/10443/9343\r\nf 10156/10425/9325 10175/10444/9344 10173/10442/9342 10157/10426/9326\r\nf 10176/10445/9345 10177/10446/9346 10173/10442/9342 10175/10444/9344\r\nf 10178/10447/9347 10174/10443/9343 10173/10442/9342 10177/10446/9346\r\nf 10156/10425/9325 10162/10431/9331 10179/10448/9348 10175/10444/9344\r\nf 10161/10430/9330 10180/10449/9349 10179/10448/9348 10162/10431/9331\r\nf 10181/10450/9350 10182/10451/9351 10179/10448/9348 10180/10449/9349\r\nf 10183/10452/9352 10184/10453/9353 10179/10448/9348 10182/10451/9351\r\nf 10176/10445/9345 10175/10444/9344 10179/10448/9348 10184/10453/9353\r\nf 10168/10437/9337 10167/10436/9336 10185/10454/9354 10186/10455/9355\r\nf 10166/10435/9335 10187/10456/9356 10185/10454/9354 10167/10436/9336\r\nf 10188/10457/9357 10189/10458/9358 10185/10454/9354 10187/10456/9356\r\nf 10190/10459/9359 10186/10455/9355 10185/10454/9354 10189/10458/9358\r\nf 10166/10435/9335 10172/10441/9341 10191/10460/9360 10187/10456/9356\r\nf 10171/10440/9340 10192/10461/9361 10191/10460/9360 10172/10441/9341\r\nf 10193/10462/9362 10194/10463/9363 10191/10460/9360 10192/10461/9361\r\nf 10188/10457/9357 10187/10456/9356 10191/10460/9360 10194/10463/9363\r\nf 10178/10447/9347 10177/10446/9346 10195/10464/9364 10196/10465/9365\r\nf 10176/10445/9345 10197/10466/9366 10195/10464/9364 10177/10446/9346\r\nf 10198/10467/9367 10199/10468/9368 10195/10464/9364 10197/10466/9366\r\nf 10200/10469/9369 10196/10465/9365 10195/10464/9364 10199/10468/9368\r\nf 10190/10459/9359 10189/10458/9358 10201/10470/9370 10202/10471/9371\r\nf 10188/10457/9357 10203/10472/9372 10201/10470/9370 10189/10458/9358\r\nf 10204/10473/9373 10205/10474/9374 10201/10470/9370 10203/10472/9372\r\nf 10206/10475/9375 10202/10471/9371 10201/10470/9370 10205/10474/9374\r\nf 10188/10457/9357 10194/10463/9363 10207/10476/9376 10203/10472/9372\r\nf 10193/10462/9362 10208/10477/9377 10207/10476/9376 10194/10463/9363\r\nf 10209/10478/9378 10210/10479/9379 10207/10476/9376 10208/10477/9377\r\nf 10204/10473/9373 10203/10472/9372 10207/10476/9376 10210/10479/9379\r\nf 10200/10469/9369 10199/10468/9368 10211/10480/9380 10212/10481/9381\r\nf 10198/10467/9367 10213/10482/9382 10211/10480/9380 10199/10468/9368\r\nf 10214/10483/9383 10215/10484/9384 10211/10480/9380 10213/10482/9382\r\nf 10216/10485/9385 10212/10481/9381 10211/10480/9380 10215/10484/9384\r\nf 10206/10475/9375 10205/10474/9374 10217/10486/9386 10218/10487/9387\r\nf 10204/10473/9373 10219/10488/9388 10217/10486/9386 10205/10474/9374\r\nf 10220/10489/9389 10221/10490/9390 10217/10486/9386 10219/10488/9388\r\nf 10222/10491/9391 10218/10487/9387 10217/10486/9386 10221/10490/9390\r\nf 10204/10473/9373 10210/10479/9379 10223/10492/9392 10219/10488/9388\r\nf 10209/10478/9378 10224/10493/9393 10223/10492/9392 10210/10479/9379\r\nf 10225/10494/9394 10226/10495/9395 10223/10492/9392 10224/10493/9393\r\nf 10220/10489/9389 10219/10488/9388 10223/10492/9392 10226/10495/9395\r\nf 10216/10485/9385 10215/10484/9384 10227/10496/9396 10228/10497/9397\r\nf 10214/10483/9383 10229/10498/9398 10227/10496/9396 10215/10484/9384\r\nf 10230/10499/9399 10231/10500/9400 10227/10496/9396 10229/10498/9398\r\nf 10232/10501/9401 10228/10497/9397 10227/10496/9396 10231/10500/9400\r\nf 10214/10483/9383 10233/10502/9402 10234/10503/9403 10229/10498/9398\r\nf 10235/10504/9404 10236/10505/9405 10234/10503/9403 10233/10502/9402\r\nf 10237/10506/9406 10238/10507/9407 10234/10503/9403 10236/10505/9405\r\nf 10230/10499/9399 10229/10498/9398 10234/10503/9403 10238/10507/9407\r\nf 10222/10491/9391 10221/10490/9390 10239/10508/9408 10240/10509/9409\r\nf 10220/10489/9389 10241/10510/9410 10239/10508/9408 10221/10490/9390\r\nf 10242/10511/9411 10243/10512/9412 10239/10508/9408 10241/10510/9410\r\nf 10244/10513/9413 10240/10509/9409 10239/10508/9408 10243/10512/9412\r\nf 10220/10489/9389 10226/10495/9395 10245/10514/9414 10241/10510/9410\r\nf 10225/10494/9394 10246/10515/9415 10245/10514/9414 10226/10495/9395\r\nf 10247/10516/9416 10248/10517/9417 10245/10514/9414 10246/10515/9415\r\nf 10242/10511/9411 10241/10510/9410 10245/10514/9414 10248/10517/9417\r\nf 10232/10501/9401 10231/10500/9400 10249/10518/9418 10250/10519/9419\r\nf 10230/10499/9399 10251/10520/9420 10249/10518/9418 10231/10500/9400\r\nf 10252/10521/9421 10253/10522/9422 10249/10518/9418 10251/10520/9420\r\nf 10254/10523/9423 10250/10519/9419 10249/10518/9418 10253/10522/9422\r\nf 10230/10499/9399 10238/10507/9407 10255/10524/9424 10251/10520/9420\r\nf 10237/10506/9406 10256/10525/9425 10255/10524/9424 10238/10507/9407\r\nf 10257/10526/9426 10258/10527/9427 10255/10524/9424 10256/10525/9425\r\nf 10252/10521/9421 10251/10520/9420 10255/10524/9424 10258/10527/9427\r\nf 10244/10513/9413 10243/10512/9412 10259/10528/9428 10260/10529/9429\r\nf 10242/10511/9411 10261/10530/9430 10259/10528/9428 10243/10512/9412\r\nf 10262/10531/9431 10263/10532/9432 10259/10528/9428 10261/10530/9430\r\nf 10264/10533/9433 10260/10529/9429 10259/10528/9428 10263/10532/9432\r\nf 10242/10511/9411 10248/10517/9417 10265/10534/9434 10261/10530/9430\r\nf 10247/10516/9416 10266/10535/9435 10265/10534/9434 10248/10517/9417\r\nf 10267/10536/9436 10268/10537/9437 10265/10534/9434 10266/10535/9435\r\nf 10262/10531/9431 10261/10530/9430 10265/10534/9434 10268/10537/9437\r\nf 10254/10523/9423 10253/10522/9422 10269/10538/9438 10270/10539/9439\r\nf 10252/10521/9421 10271/10540/9440 10269/10538/9438 10253/10522/9422\r\nf 10272/10541/9441 10273/10542/9442 10269/10538/9438 10271/10540/9440\r\nf 10274/10543/9443 10270/10539/9439 10269/10538/9438 10273/10542/9442\r\nf 10252/10521/9421 10258/10527/9427 10275/10544/9444 10271/10540/9440\r\nf 10257/10526/9426 10276/10545/9445 10275/10544/9444 10258/10527/9427\r\nf 10277/10546/9446 10278/10547/9447 10275/10544/9444 10276/10545/9445\r\nf 10272/10541/9441 10271/10540/9440 10275/10544/9444 10278/10547/9447\r\nf 10264/10533/9433 10263/10532/9432 10279/10548/9448 10280/10549/9449\r\nf 10262/10531/9431 10281/10550/9450 10279/10548/9448 10263/10532/9432\r\nf 10282/10551/9451 10283/10552/9452 10279/10548/9448 10281/10550/9450\r\nf 10284/10553/9453 10280/10549/9449 10279/10548/9448 10283/10552/9452\r\nf 10262/10531/9431 10268/10537/9437 10285/10554/9454 10281/10550/9450\r\nf 10267/10536/9436 10286/10555/9455 10285/10554/9454 10268/10537/9437\r\nf 10287/10556/9456 10288/10557/9457 10285/10554/9454 10286/10555/9455\r\nf 10282/10551/9451 10281/10550/9450 10285/10554/9454 10288/10557/9457\r\nf 10274/10543/9443 10273/10542/9442 10289/10558/9458 10290/10559/9459\r\nf 10272/10541/9441 10291/10560/9460 10289/10558/9458 10273/10542/9442\r\nf 10292/10561/9461 10293/10562/9462 10289/10558/9458 10291/10560/9460\r\nf 10294/10563/9463 10290/10559/9459 10289/10558/9458 10293/10562/9462\r\nf 10272/10541/9441 10278/10547/9447 10295/10564/9464 10291/10560/9460\r\nf 10277/10546/9446 10296/10565/9465 10295/10564/9464 10278/10547/9447\r\nf 10297/10566/9466 10298/10567/9467 10295/10564/9464 10296/10565/9465\r\nf 10292/10561/9461 10291/10560/9460 10295/10564/9464 10298/10567/9467\r\nf 10284/10553/9453 10283/10552/9452 10299/10568/9468 10300/10569/9469\r\nf 10282/10551/9451 10301/10570/9470 10299/10568/9468 10283/10552/9452\r\nf 10302/10571/9471 10303/10572/9472 10299/10568/9468 10301/10570/9470\r\nf 10304/10573/9473 10300/10569/9469 10299/10568/9468 10303/10572/9472\r\nf 10282/10551/9451 10288/10557/9457 10305/10574/9474 10301/10570/9470\r\nf 10287/10556/9456 10306/10575/9475 10305/10574/9474 10288/10557/9457\r\nf 10307/10576/9476 10308/10577/9477 10305/10574/9474 10306/10575/9475\r\nf 10302/10571/9471 10301/10570/9470 10305/10574/9474 10308/10577/9477\r\nf 10294/10563/9463 10293/10562/9462 10309/10578/9478 10310/10579/9479\r\nf 10292/10561/9461 10311/10580/9480 10309/10578/9478 10293/10562/9462\r\nf 10312/10581/9481 10313/10582/9482 10309/10578/9478 10311/10580/9480\r\nf 10314/10583/9483 10310/10579/9479 10309/10578/9478 10313/10582/9482\r\nf 10292/10561/9461 10298/10567/9467 10315/10584/9484 10311/10580/9480\r\nf 10297/10566/9466 10316/10585/9485 10315/10584/9484 10298/10567/9467\r\nf 10317/10586/9486 10318/10587/9487 10315/10584/9484 10316/10585/9485\r\nf 10312/10581/9481 10311/10580/9480 10315/10584/9484 10318/10587/9487\r\nf 10304/10573/9473 10303/10572/9472 10319/10588/9488 10320/10589/9489\r\nf 10302/10571/9471 10321/10590/9490 10319/10588/9488 10303/10572/9472\r\nf 10322/10591/9491 10323/10592/9492 10319/10588/9488 10321/10590/9490\r\nf 10324/10593/9493 10320/10589/9489 10319/10588/9488 10323/10592/9492\r\nf 10302/10571/9471 10308/10577/9477 10325/10594/9494 10321/10590/9490\r\nf 10307/10576/9476 10326/10595/9495 10325/10594/9494 10308/10577/9477\r\nf 10327/10596/9496 10328/10597/9497 10325/10594/9494 10326/10595/9495\r\nf 10322/10591/9491 10321/10590/9490 10325/10594/9494 10328/10597/9497\r\nf 10314/10583/9483 10313/10582/9482 10329/10598/9498 10330/10599/9499\r\nf 10312/10581/9481 10331/10600/9500 10329/10598/9498 10313/10582/9482\r\nf 10332/10601/9501 10333/10602/9502 10329/10598/9498 10331/10600/9500\r\nf 10334/10603/9503 10330/10599/9499 10329/10598/9498 10333/10602/9502\r\nf 10312/10581/9481 10318/10587/9487 10335/10604/9504 10331/10600/9500\r\nf 10317/10586/9486 10336/10605/9505 10335/10604/9504 10318/10587/9487\r\nf 10337/10606/9506 10338/10607/9507 10335/10604/9504 10336/10605/9505\r\nf 10332/10601/9501 10331/10600/9500 10335/10604/9504 10338/10607/9507\r\nf 10324/10593/9493 10323/10592/9492 10339/10608/9508 10340/10609/9509\r\nf 10322/10591/9491 10341/10610/9510 10339/10608/9508 10323/10592/9492\r\nf 10342/10611/9511 10343/10612/9512 10339/10608/9508 10341/10610/9510\r\nf 10344/10613/9513 10340/10609/9509 10339/10608/9508 10343/10612/9512\r\nf 10322/10591/9491 10328/10597/9497 10345/10614/9514 10341/10610/9510\r\nf 10327/10596/9496 10346/10615/9515 10345/10614/9514 10328/10597/9497\r\nf 10347/10616/9516 10348/10617/9517 10345/10614/9514 10346/10615/9515\r\nf 10342/10611/9511 10341/10610/9510 10345/10614/9514 10348/10617/9517\r\nf 10334/10603/9503 10333/10602/9502 10349/10618/9518 10350/10619/9519\r\nf 10332/10601/9501 10351/10620/9520 10349/10618/9518 10333/10602/9502\r\nf 10352/10621/9521 10353/10622/9522 10349/10618/9518 10351/10620/9520\r\nf 10354/10623/9523 10350/10619/9519 10349/10618/9518 10353/10622/9522\r\nf 10332/10601/9501 10338/10607/9507 10355/10624/9524 10351/10620/9520\r\nf 10337/10606/9506 10356/10625/9525 10355/10624/9524 10338/10607/9507\r\nf 10357/10626/9526 10358/10627/9527 10355/10624/9524 10356/10625/9525\r\nf 10352/10621/9521 10351/10620/9520 10355/10624/9524 10358/10627/9527\r\nf 10344/10613/9513 10343/10612/9512 10359/10628/9528 10360/10629/9529\r\nf 10342/10611/9511 10361/10630/9530 10359/10628/9528 10343/10612/9512\r\nf 10362/10631/9531 10363/10632/9532 10359/10628/9528 10361/10630/9530\r\nf 10364/10633/9533 10360/10629/9529 10359/10628/9528 10363/10632/9532\r\nf 10342/10611/9511 10348/10617/9517 10365/10634/9534 10361/10630/9530\r\nf 10347/10616/9516 10366/10635/9535 10365/10634/9534 10348/10617/9517\r\nf 10367/10636/9536 10368/10637/9537 10365/10634/9534 10366/10635/9535\r\nf 10362/10631/9531 10361/10630/9530 10365/10634/9534 10368/10637/9537\r\nf 10354/10623/9523 10353/10622/9522 10369/10638/9538 10370/10639/9539\r\nf 10352/10621/9521 10371/10640/9540 10369/10638/9538 10353/10622/9522\r\nf 10372/10641/9541 10373/10642/9542 10369/10638/9538 10371/10640/9540\r\nf 10374/10643/9543 10370/10639/9539 10369/10638/9538 10373/10642/9542\r\nf 10352/10621/9521 10358/10627/9527 10375/10644/9544 10371/10640/9540\r\nf 10357/10626/9526 10376/10645/9545 10375/10644/9544 10358/10627/9527\r\nf 10377/10646/9546 10378/10647/9547 10375/10644/9544 10376/10645/9545\r\nf 10372/10641/9541 10371/10640/9540 10375/10644/9544 10378/10647/9547\r\nf 10364/10633/9533 10363/10632/9532 10379/10648/9548 10380/10649/9549\r\nf 10362/10631/9531 10381/10650/9550 10379/10648/9548 10363/10632/9532\r\nf 10382/10651/9551 10383/10652/9552 10379/10648/9548 10381/10650/9550\r\nf 10384/10653/9553 10380/10649/9549 10379/10648/9548 10383/10652/9552\r\nf 10362/10631/9531 10368/10637/9537 10385/10654/9554 10381/10650/9550\r\nf 10367/10636/9536 10386/10655/9555 10385/10654/9554 10368/10637/9537\r\nf 10387/10656/9556 10388/10657/9557 10385/10654/9554 10386/10655/9555\r\nf 10382/10651/9551 10381/10650/9550 10385/10654/9554 10388/10657/9557\r\nf 10374/10643/9543 10373/10642/9542 10389/10658/9558 10390/10659/9559\r\nf 10372/10641/9541 10391/10660/9560 10389/10658/9558 10373/10642/9542\r\nf 10392/10661/9561 10393/10662/9562 10389/10658/9558 10391/10660/9560\r\nf 10394/10663/9563 10390/10659/9559 10389/10658/9558 10393/10662/9562\r\nf 10372/10641/9541 10378/10647/9547 10395/10664/9564 10391/10660/9560\r\nf 10377/10646/9546 10396/10665/9565 10395/10664/9564 10378/10647/9547\r\nf 10397/10666/9566 10398/10667/9567 10395/10664/9564 10396/10665/9565\r\nf 10392/10661/9561 10391/10660/9560 10395/10664/9564 10398/10667/9567\r\nf 10384/10653/9553 10383/10652/9552 10399/10668/9568 10400/10669/9569\r\nf 10382/10651/9551 10401/10670/9570 10399/10668/9568 10383/10652/9552\r\nf 10402/10671/9571 10403/10672/9572 10399/10668/9568 10401/10670/9570\r\nf 10404/10673/9573 10400/10669/9569 10399/10668/9568 10403/10672/9572\r\nf 10382/10651/9551 10388/10657/9557 10405/10674/9574 10401/10670/9570\r\nf 10387/10656/9556 10406/10675/9575 10405/10674/9574 10388/10657/9557\r\nf 10407/10676/9576 10408/10677/9577 10405/10674/9574 10406/10675/9575\r\nf 10402/10671/9571 10401/10670/9570 10405/10674/9574 10408/10677/9577\r\nf 10394/10663/9563 10393/10662/9562 10409/10678/9578 10410/10679/9579\r\nf 10392/10661/9561 10411/10680/9580 10409/10678/9578 10393/10662/9562\r\nf 10412/10681/9581 10413/10682/9582 10409/10678/9578 10411/10680/9580\r\nf 10414/10683/9583 10410/10679/9579 10409/10678/9578 10413/10682/9582\r\nf 10392/10661/9561 10398/10667/9567 10415/10684/9584 10411/10680/9580\r\nf 10397/10666/9566 10416/10685/9585 10415/10684/9584 10398/10667/9567\r\nf 10417/10686/9586 10418/10687/9587 10415/10684/9584 10416/10685/9585\r\nf 10412/10681/9581 10411/10680/9580 10415/10684/9584 10418/10687/9587\r\nf 10404/10673/9573 10403/10672/9572 10419/10688/9588 10420/10689/9589\r\nf 10402/10671/9571 10421/10690/9590 10419/10688/9588 10403/10672/9572\r\nf 10422/10691/9591 10423/10692/9592 10419/10688/9588 10421/10690/9590\r\nf 10424/10693/9593 10420/10689/9589 10419/10688/9588 10423/10692/9592\r\nf 10402/10671/9571 10408/10677/9577 10425/10694/9594 10421/10690/9590\r\nf 10407/10676/9576 10426/10695/9595 10425/10694/9594 10408/10677/9577\r\nf 10427/10696/9596 10428/10697/9597 10425/10694/9594 10426/10695/9595\r\nf 10422/10691/9591 10421/10690/9590 10425/10694/9594 10428/10697/9597\r\nf 10414/10683/9583 10413/10682/9582 10429/10698/9598 10430/10699/9599\r\nf 10412/10681/9581 10431/10700/9600 10429/10698/9598 10413/10682/9582\r\nf 10432/10701/9601 10433/10702/9602 10429/10698/9598 10431/10700/9600\r\nf 10434/10703/9603 10430/10699/9599 10429/10698/9598 10433/10702/9602\r\nf 10412/10681/9581 10418/10687/9587 10435/10704/9604 10431/10700/9600\r\nf 10417/10686/9586 10436/10705/9605 10435/10704/9604 10418/10687/9587\r\nf 10437/10706/9606 10438/10707/9607 10435/10704/9604 10436/10705/9605\r\nf 10432/10701/9601 10431/10700/9600 10435/10704/9604 10438/10707/9607\r\nf 10424/10693/9593 10423/10692/9592 10439/10708/9608 10440/10709/9609\r\nf 10422/10691/9591 10441/10710/9610 10439/10708/9608 10423/10692/9592\r\nf 10442/10711/9611 10443/10712/9612 10439/10708/9608 10441/10710/9610\r\nf 10444/10713/9613 10440/10709/9609 10439/10708/9608 10443/10712/9612\r\nf 10422/10691/9591 10428/10697/9597 10445/10714/9614 10441/10710/9610\r\nf 10427/10696/9596 10446/10715/9615 10445/10714/9614 10428/10697/9597\r\nf 10447/10716/9616 10448/10717/9617 10445/10714/9614 10446/10715/9615\r\nf 10442/10711/9611 10441/10710/9610 10445/10714/9614 10448/10717/9617\r\nf 10434/10703/9603 10433/10702/9602 10449/10718/9618 10450/10719/9619\r\nf 10432/10701/9601 10451/10720/9620 10449/10718/9618 10433/10702/9602\r\nf 10452/10721/9621 10453/10722/9622 10449/10718/9618 10451/10720/9620\r\nf 10454/10723/9623 10450/10719/9619 10449/10718/9618 10453/10722/9622\r\nf 10432/10701/9601 10438/10707/9607 10455/10724/9624 10451/10720/9620\r\nf 10437/10706/9606 10456/10725/9625 10455/10724/9624 10438/10707/9607\r\nf 10457/10726/9626 10458/10727/9627 10455/10724/9624 10456/10725/9625\r\nf 10452/10721/9621 10451/10720/9620 10455/10724/9624 10458/10727/9627\r\nf 10444/10713/9613 10443/10712/9612 10459/10728/9628 10460/10729/9629\r\nf 10442/10711/9611 10461/10730/9630 10459/10728/9628 10443/10712/9612\r\nf 10462/10731/9631 10463/10732/9632 10459/10728/9628 10461/10730/9630\r\nf 10464/10733/9633 10460/10729/9629 10459/10728/9628 10463/10732/9632\r\nf 10442/10711/9611 10448/10717/9617 10465/10734/9634 10461/10730/9630\r\nf 10447/10716/9616 10466/10735/9635 10465/10734/9634 10448/10717/9617\r\nf 10467/10736/9636 10468/10737/9637 10465/10734/9634 10466/10735/9635\r\nf 10462/10731/9631 10461/10730/9630 10465/10734/9634 10468/10737/9637\r\nf 10454/10723/9623 10453/10722/9622 10469/10738/9638 10470/10739/9639\r\nf 10452/10721/9621 10471/10740/9640 10469/10738/9638 10453/10722/9622\r\nf 10472/10741/9641 10473/10742/9642 10469/10738/9638 10471/10740/9640\r\nf 10474/10743/9643 10470/10739/9639 10469/10738/9638 10473/10742/9642\r\nf 10452/10721/9621 10458/10727/9627 10475/10744/9644 10471/10740/9640\r\nf 10457/10726/9626 10476/10745/9645 10475/10744/9644 10458/10727/9627\r\nf 10477/10746/9646 10478/10747/9647 10475/10744/9644 10476/10745/9645\r\nf 10472/10741/9641 10471/10740/9640 10475/10744/9644 10478/10747/9647\r\nf 10464/10733/9633 10463/10732/9632 10479/10748/9648 10480/10749/9649\r\nf 10462/10731/9631 10481/10750/9650 10479/10748/9648 10463/10732/9632\r\nf 10482/10751/9651 10483/10752/9652 10479/10748/9648 10481/10750/9650\r\nf 10484/10753/9653 10480/10749/9649 10479/10748/9648 10483/10752/9652\r\nf 10462/10731/9631 10468/10737/9637 10485/10754/9654 10481/10750/9650\r\nf 10467/10736/9636 10486/10755/9655 10485/10754/9654 10468/10737/9637\r\nf 10487/10756/9656 10488/10757/9657 10485/10754/9654 10486/10755/9655\r\nf 10482/10751/9651 10481/10750/9650 10485/10754/9654 10488/10757/9657\r\nf 10474/10743/9643 10473/10742/9642 10489/10758/9658 10490/10759/9659\r\nf 10472/10741/9641 10491/10760/9660 10489/10758/9658 10473/10742/9642\r\nf 10492/10761/9661 10493/10762/9662 10489/10758/9658 10491/10760/9660\r\nf 10494/10763/9663 10490/10759/9659 10489/10758/9658 10493/10762/9662\r\nf 10472/10741/9641 10478/10747/9647 10495/10764/9664 10491/10760/9660\r\nf 10477/10746/9646 10496/10765/9665 10495/10764/9664 10478/10747/9647\r\nf 10497/10766/9666 10498/10767/9667 10495/10764/9664 10496/10765/9665\r\nf 10492/10761/9661 10491/10760/9660 10495/10764/9664 10498/10767/9667\r\nf 10484/10753/9653 10483/10752/9652 10499/10768/9668 10500/10769/9669\r\nf 10482/10751/9651 10501/10770/9670 10499/10768/9668 10483/10752/9652\r\nf 10502/10771/9671 10503/10772/9672 10499/10768/9668 10501/10770/9670\r\nf 10504/10773/9673 10500/10769/9669 10499/10768/9668 10503/10772/9672\r\nf 10482/10751/9651 10488/10757/9657 10505/10774/9674 10501/10770/9670\r\nf 10487/10756/9656 10506/10775/9675 10505/10774/9674 10488/10757/9657\r\nf 10507/10776/9676 10508/10777/9677 10505/10774/9674 10506/10775/9675\r\nf 10502/10771/9671 10501/10770/9670 10505/10774/9674 10508/10777/9677\r\nf 10494/10763/9663 10493/10762/9662 10509/10778/9678 10510/10779/9679\r\nf 10492/10761/9661 10511/10780/9680 10509/10778/9678 10493/10762/9662\r\nf 10512/10781/9681 10513/10782/9682 10509/10778/9678 10511/10780/9680\r\nf 10514/10783/9683 10510/10779/9679 10509/10778/9678 10513/10782/9682\r\nf 10492/10761/9661 10498/10767/9667 10515/10784/9684 10511/10780/9680\r\nf 10497/10766/9666 10516/10785/9685 10515/10784/9684 10498/10767/9667\r\nf 10517/10786/9686 10518/10787/9687 10515/10784/9684 10516/10785/9685\r\nf 10512/10781/9681 10511/10780/9680 10515/10784/9684 10518/10787/9687\r\nf 10504/10773/9673 10503/10772/9672 10519/10788/9688 10520/10789/9689\r\nf 10502/10771/9671 10521/10790/9690 10519/10788/9688 10503/10772/9672\r\nf 10522/10791/9691 10523/10792/9692 10519/10788/9688 10521/10790/9690\r\nf 10524/10793/9693 10520/10789/9689 10519/10788/9688 10523/10792/9692\r\nf 10502/10771/9671 10508/10777/9677 10525/10794/9694 10521/10790/9690\r\nf 10507/10776/9676 10526/10795/9695 10525/10794/9694 10508/10777/9677\r\nf 10527/10796/9696 10528/10797/9697 10525/10794/9694 10526/10795/9695\r\nf 10522/10791/9691 10521/10790/9690 10525/10794/9694 10528/10797/9697\r\nf 10514/10783/9683 10513/10782/9682 10529/10798/9698 10530/10799/9699\r\nf 10512/10781/9681 10531/10800/9700 10529/10798/9698 10513/10782/9682\r\nf 10532/10801/9701 10533/10802/9702 10529/10798/9698 10531/10800/9700\r\nf 10534/10803/9703 10530/10799/9699 10529/10798/9698 10533/10802/9702\r\nf 10512/10781/9681 10518/10787/9687 10535/10804/9704 10531/10800/9700\r\nf 10517/10786/9686 10536/10805/9705 10535/10804/9704 10518/10787/9687\r\nf 10537/10806/9706 10538/10807/9707 10535/10804/9704 10536/10805/9705\r\nf 10532/10801/9701 10531/10800/9700 10535/10804/9704 10538/10807/9707\r\nf 10524/10793/9693 10523/10792/9692 10539/10808/9708 10540/10809/9709\r\nf 10522/10791/9691 10541/10810/9710 10539/10808/9708 10523/10792/9692\r\nf 10542/10811/9711 10543/10812/9712 10539/10808/9708 10541/10810/9710\r\nf 10544/10813/9713 10540/10809/9709 10539/10808/9708 10543/10812/9712\r\nf 10522/10791/9691 10528/10797/9697 10545/10814/9714 10541/10810/9710\r\nf 10527/10796/9696 10546/10815/9715 10545/10814/9714 10528/10797/9697\r\nf 10547/10816/9716 10548/10817/9717 10545/10814/9714 10546/10815/9715\r\nf 10542/10811/9711 10541/10810/9710 10545/10814/9714 10548/10817/9717\r\nf 10534/10803/9703 10533/10802/9702 10549/10818/9718 10550/10819/9719\r\nf 10532/10801/9701 10551/10820/9720 10549/10818/9718 10533/10802/9702\r\nf 10552/10821/9721 10553/10822/9722 10549/10818/9718 10551/10820/9720\r\nf 10554/10823/9723 10550/10819/9719 10549/10818/9718 10553/10822/9722\r\nf 10532/10801/9701 10538/10807/9707 10555/10824/9724 10551/10820/9720\r\nf 10537/10806/9706 10556/10825/9725 10555/10824/9724 10538/10807/9707\r\nf 10557/10826/9726 10558/10827/9727 10555/10824/9724 10556/10825/9725\r\nf 10552/10821/9721 10551/10820/9720 10555/10824/9724 10558/10827/9727\r\nf 10544/10813/9713 10543/10812/9712 10559/10828/9728 10560/10829/9729\r\nf 10542/10811/9711 10561/10830/9730 10559/10828/9728 10543/10812/9712\r\nf 10562/10831/9731 10563/10832/9732 10559/10828/9728 10561/10830/9730\r\nf 10564/10833/9733 10560/10829/9729 10559/10828/9728 10563/10832/9732\r\nf 10542/10811/9711 10548/10817/9717 10565/10834/9734 10561/10830/9730\r\nf 10547/10816/9716 10566/10835/9735 10565/10834/9734 10548/10817/9717\r\nf 10567/10836/9736 10568/10837/9737 10565/10834/9734 10566/10835/9735\r\nf 10562/10831/9731 10561/10830/9730 10565/10834/9734 10568/10837/9737\r\nf 10554/10823/9723 10553/10822/9722 10569/10838/9738 10570/10839/9739\r\nf 10552/10821/9721 10571/10840/9740 10569/10838/9738 10553/10822/9722\r\nf 10572/10841/9741 10573/10842/9742 10569/10838/9738 10571/10840/9740\r\nf 10574/10843/9743 10570/10839/9739 10569/10838/9738 10573/10842/9742\r\nf 10552/10821/9721 10558/10827/9727 10575/10844/9744 10571/10840/9740\r\nf 10557/10826/9726 10576/10845/9745 10575/10844/9744 10558/10827/9727\r\nf 10577/10846/9746 10578/10847/9747 10575/10844/9744 10576/10845/9745\r\nf 10572/10841/9741 10571/10840/9740 10575/10844/9744 10578/10847/9747\r\nf 10564/10833/9733 10563/10832/9732 10579/10848/9748 10580/10849/9749\r\nf 10562/10831/9731 10581/10850/9750 10579/10848/9748 10563/10832/9732\r\nf 10582/10851/9751 10583/10852/9752 10579/10848/9748 10581/10850/9750\r\nf 10584/10853/9753 10580/10849/9749 10579/10848/9748 10583/10852/9752\r\nf 10562/10831/9731 10568/10837/9737 10585/10854/9754 10581/10850/9750\r\nf 10567/10836/9736 10586/10855/9755 10585/10854/9754 10568/10837/9737\r\nf 10587/10856/9756 10588/10857/9757 10585/10854/9754 10586/10855/9755\r\nf 10582/10851/9751 10581/10850/9750 10585/10854/9754 10588/10857/9757\r\nf 10574/10843/9743 10573/10842/9742 10589/10858/9758 10590/10859/9759\r\nf 10572/10841/9741 10591/10860/9760 10589/10858/9758 10573/10842/9742\r\nf 10592/10861/9761 10593/10862/9762 10589/10858/9758 10591/10860/9760\r\nf 10594/10863/9763 10590/10859/9759 10589/10858/9758 10593/10862/9762\r\nf 10572/10841/9741 10578/10847/9747 10595/10864/9764 10591/10860/9760\r\nf 10577/10846/9746 10596/10865/9765 10595/10864/9764 10578/10847/9747\r\nf 10597/10866/9766 10598/10867/9767 10595/10864/9764 10596/10865/9765\r\nf 10592/10861/9761 10591/10860/9760 10595/10864/9764 10598/10867/9767\r\nf 10584/10853/9753 10583/10852/9752 10599/10868/9768 10600/10869/9769\r\nf 10582/10851/9751 10601/10870/9770 10599/10868/9768 10583/10852/9752\r\nf 10602/10871/9771 10603/10872/9772 10599/10868/9768 10601/10870/9770\r\nf 10604/10873/9773 10600/10869/9769 10599/10868/9768 10603/10872/9772\r\nf 10582/10851/9751 10588/10857/9757 10605/10874/9774 10601/10870/9770\r\nf 10587/10856/9756 10606/10875/9775 10605/10874/9774 10588/10857/9757\r\nf 10607/10876/9776 10608/10877/9777 10605/10874/9774 10606/10875/9775\r\nf 10602/10871/9771 10601/10870/9770 10605/10874/9774 10608/10877/9777\r\nf 10594/10863/9763 10593/10862/9762 10609/10878/9778 10610/10879/9779\r\nf 10592/10861/9761 10611/10880/9780 10609/10878/9778 10593/10862/9762\r\nf 10612/10881/9781 10613/10882/9782 10609/10878/9778 10611/10880/9780\r\nf 10614/10883/9783 10610/10879/9779 10609/10878/9778 10613/10882/9782\r\nf 10592/10861/9761 10598/10867/9767 10615/10884/9784 10611/10880/9780\r\nf 10597/10866/9766 10616/10885/9785 10615/10884/9784 10598/10867/9767\r\nf 10617/10886/9786 10618/10887/9787 10615/10884/9784 10616/10885/9785\r\nf 10612/10881/9781 10611/10880/9780 10615/10884/9784 10618/10887/9787\r\nf 10604/10873/9773 10603/10872/9772 10619/10888/9788 10620/10889/9789\r\nf 10602/10871/9771 10621/10890/9790 10619/10888/9788 10603/10872/9772\r\nf 10622/10891/9791 10623/10892/9792 10619/10888/9788 10621/10890/9790\r\nf 10624/10893/9793 10620/10889/9789 10619/10888/9788 10623/10892/9792\r\nf 10602/10871/9771 10608/10877/9777 10625/10894/9794 10621/10890/9790\r\nf 10607/10876/9776 10626/10895/9795 10625/10894/9794 10608/10877/9777\r\nf 10627/10896/9796 10628/10897/9797 10625/10894/9794 10626/10895/9795\r\nf 10622/10891/9791 10621/10890/9790 10625/10894/9794 10628/10897/9797\r\nf 10614/10883/9783 10613/10882/9782 10629/10898/9798 10630/10899/9799\r\nf 10612/10881/9781 10631/10900/9800 10629/10898/9798 10613/10882/9782\r\nf 10632/10901/9801 10633/10902/9802 10629/10898/9798 10631/10900/9800\r\nf 10634/10903/9803 10630/10899/9799 10629/10898/9798 10633/10902/9802\r\nf 10612/10881/9781 10618/10887/9787 10635/10904/9804 10631/10900/9800\r\nf 10617/10886/9786 10636/10905/9805 10635/10904/9804 10618/10887/9787\r\nf 10637/10906/9806 10638/10907/9807 10635/10904/9804 10636/10905/9805\r\nf 10632/10901/9801 10631/10900/9800 10635/10904/9804 10638/10907/9807\r\nf 10624/10893/9793 10623/10892/9792 10639/10908/9808 10640/10909/9809\r\nf 10622/10891/9791 10641/10910/9810 10639/10908/9808 10623/10892/9792\r\nf 10642/10911/9811 10643/10912/9812 10639/10908/9808 10641/10910/9810\r\nf 10644/10913/9813 10640/10909/9809 10639/10908/9808 10643/10912/9812\r\nf 10622/10891/9791 10628/10897/9797 10645/10914/9814 10641/10910/9810\r\nf 10627/10896/9796 10646/10915/9815 10645/10914/9814 10628/10897/9797\r\nf 10647/10916/9816 10648/10917/9817 10645/10914/9814 10646/10915/9815\r\nf 10642/10911/9811 10641/10910/9810 10645/10914/9814 10648/10917/9817\r\nf 10634/10903/9803 10633/10902/9802 10649/10918/9818 10650/10919/9819\r\nf 10632/10901/9801 10651/10920/9820 10649/10918/9818 10633/10902/9802\r\nf 10652/10921/9821 10653/10922/9822 10649/10918/9818 10651/10920/9820\r\nf 10654/10923/9823 10650/10919/9819 10649/10918/9818 10653/10922/9822\r\nf 10632/10901/9801 10638/10907/9807 10655/10924/9824 10651/10920/9820\r\nf 10637/10906/9806 10656/10925/9825 10655/10924/9824 10638/10907/9807\r\nf 10657/10926/9826 10658/10927/9827 10655/10924/9824 10656/10925/9825\r\nf 10652/10921/9821 10651/10920/9820 10655/10924/9824 10658/10927/9827\r\nf 10644/10913/9813 10643/10912/9812 10659/10928/9828 10660/10929/9829\r\nf 10642/10911/9811 10661/10930/9830 10659/10928/9828 10643/10912/9812\r\nf 10662/10931/9831 10663/10932/9832 10659/10928/9828 10661/10930/9830\r\nf 10664/10933/9833 10660/10929/9829 10659/10928/9828 10663/10932/9832\r\nf 10642/10911/9811 10648/10917/9817 10665/10934/9834 10661/10930/9830\r\nf 10647/10916/9816 10666/10935/9835 10665/10934/9834 10648/10917/9817\r\nf 10667/10936/9836 10668/10937/9837 10665/10934/9834 10666/10935/9835\r\nf 10662/10931/9831 10661/10930/9830 10665/10934/9834 10668/10937/9837\r\nf 10654/10923/9823 10653/10922/9822 10669/10938/9838 10670/10939/9839\r\nf 10652/10921/9821 10671/10940/9840 10669/10938/9838 10653/10922/9822\r\nf 10672/10941/9841 10673/10942/9842 10669/10938/9838 10671/10940/9840\r\nf 10674/10943/9843 10670/10939/9839 10669/10938/9838 10673/10942/9842\r\nf 10652/10921/9821 10658/10927/9827 10675/10944/9844 10671/10940/9840\r\nf 10657/10926/9826 10676/10945/9845 10675/10944/9844 10658/10927/9827\r\nf 10677/10946/9846 10678/10947/9847 10675/10944/9844 10676/10945/9845\r\nf 10672/10941/9841 10671/10940/9840 10675/10944/9844 10678/10947/9847\r\nf 10664/10933/9833 10663/10932/9832 10679/10948/9848 10680/10949/9849\r\nf 10662/10931/9831 10681/10950/9850 10679/10948/9848 10663/10932/9832\r\nf 10682/10951/9851 10683/10952/9852 10679/10948/9848 10681/10950/9850\r\nf 10684/10953/9853 10680/10949/9849 10679/10948/9848 10683/10952/9852\r\nf 10662/10931/9831 10668/10937/9837 10685/10954/9854 10681/10950/9850\r\nf 10667/10936/9836 10686/10955/9855 10685/10954/9854 10668/10937/9837\r\nf 10687/10956/9856 10688/10957/9857 10685/10954/9854 10686/10955/9855\r\nf 10682/10951/9851 10681/10950/9850 10685/10954/9854 10688/10957/9857\r\nf 10674/10943/9843 10673/10942/9842 10689/10958/9858 10690/10959/9859\r\nf 10672/10941/9841 10691/10960/9860 10689/10958/9858 10673/10942/9842\r\nf 10692/10961/9861 10693/10962/9862 10689/10958/9858 10691/10960/9860\r\nf 10694/10963/9863 10690/10959/9859 10689/10958/9858 10693/10962/9862\r\nf 10672/10941/9841 10678/10947/9847 10695/10964/9864 10691/10960/9860\r\nf 10677/10946/9846 10696/10965/9865 10695/10964/9864 10678/10947/9847\r\nf 10697/10966/9866 10698/10967/9867 10695/10964/9864 10696/10965/9865\r\nf 10692/10961/9861 10691/10960/9860 10695/10964/9864 10698/10967/9867\r\nf 10684/10953/9853 10683/10952/9852 10699/10968/9868 10700/10969/9869\r\nf 10682/10951/9851 10701/10970/9870 10699/10968/9868 10683/10952/9852\r\nf 10702/10971/9871 10703/10972/9872 10699/10968/9868 10701/10970/9870\r\nf 10704/10973/9873 10700/10969/9869 10699/10968/9868 10703/10972/9872\r\nf 10682/10951/9851 10688/10957/9857 10705/10974/9874 10701/10970/9870\r\nf 10687/10956/9856 10706/10975/9875 10705/10974/9874 10688/10957/9857\r\nf 10707/10976/9876 10708/10977/9877 10705/10974/9874 10706/10975/9875\r\nf 10702/10971/9871 10701/10970/9870 10705/10974/9874 10708/10977/9877\r\nf 10694/10963/9863 10693/10962/9862 10709/10978/9878 10710/10979/9879\r\nf 10692/10961/9861 10711/10980/9880 10709/10978/9878 10693/10962/9862\r\nf 10712/10981/9881 10713/10982/9882 10709/10978/9878 10711/10980/9880\r\nf 10714/10983/9883 10710/10979/9879 10709/10978/9878 10713/10982/9882\r\nf 10692/10961/9861 10698/10967/9867 10715/10984/9884 10711/10980/9880\r\nf 10697/10966/9866 10716/10985/9885 10715/10984/9884 10698/10967/9867\r\nf 10717/10986/9886 10718/10987/9887 10715/10984/9884 10716/10985/9885\r\nf 10712/10981/9881 10711/10980/9880 10715/10984/9884 10718/10987/9887\r\nf 10704/10973/9873 10703/10972/9872 10719/10988/9888 10720/10989/9889\r\nf 10702/10971/9871 10721/10990/9890 10719/10988/9888 10703/10972/9872\r\nf 10722/10991/9891 10723/10992/9892 10719/10988/9888 10721/10990/9890\r\nf 10724/10993/9893 10720/10989/9889 10719/10988/9888 10723/10992/9892\r\nf 10702/10971/9871 10708/10977/9877 10725/10994/9894 10721/10990/9890\r\nf 10707/10976/9876 10726/10995/9895 10725/10994/9894 10708/10977/9877\r\nf 10727/10996/9896 10728/10997/9897 10725/10994/9894 10726/10995/9895\r\nf 10722/10991/9891 10721/10990/9890 10725/10994/9894 10728/10997/9897\r\nf 10714/10983/9883 10713/10982/9882 10729/10998/9898 10730/10999/9899\r\nf 10712/10981/9881 10731/11000/9900 10729/10998/9898 10713/10982/9882\r\nf 10732/11001/9901 10733/11002/9902 10729/10998/9898 10731/11000/9900\r\nf 10734/11003/9903 10730/10999/9899 10729/10998/9898 10733/11002/9902\r\nf 10712/10981/9881 10718/10987/9887 10735/11004/9904 10731/11000/9900\r\nf 10717/10986/9886 10736/11005/9886 10735/11004/9904 10718/10987/9887\r\nf 10737/11006/9905 10738/11007/9906 10735/11004/9904 10736/11005/9886\r\nf 10732/11001/9901 10731/11000/9900 10735/11004/9904 10738/11007/9906\r\nf 10724/10993/9893 10723/10992/9892 10739/11008/9907 10740/11009/9908\r\nf 10722/10991/9891 10741/11010/9909 10739/11008/9907 10723/10992/9892\r\nf 10742/11011/9910 10743/11012/9911 10739/11008/9907 10741/11010/9909\r\nf 10744/11013/9912 10740/11009/9908 10739/11008/9907 10743/11012/9911\r\nf 10722/10991/9891 10728/10997/9897 10745/11014/9913 10741/11010/9909\r\nf 10727/10996/9896 10746/11015/9896 10745/11014/9913 10728/10997/9897\r\nf 10747/11016/9914 10748/11017/9915 10745/11014/9913 10746/11015/9896\r\nf 10742/11011/9910 10741/11010/9909 10745/11014/9913 10748/11017/9915\r\nf 10734/11003/9903 10733/11002/9902 10749/11018/9916 10750/11019/9917\r\nf 10732/11001/9901 10751/11020/9918 10749/11018/9916 10733/11002/9902\r\nf 10752/11021/9919 10753/11022/9920 10749/11018/9916 10751/11020/9918\r\nf 10754/11023/9921 10750/11019/9917 10749/11018/9916 10753/11022/9920\r\nf 10732/11001/9901 10738/11007/9906 10755/11024/9922 10751/11020/9918\r\nf 10737/11006/9905 10756/11025/9923 10755/11024/9922 10738/11007/9906\r\nf 10757/11026/9924 10758/11027/9925 10755/11024/9922 10756/11025/9923\r\nf 10752/11021/9919 10751/11020/9918 10755/11024/9922 10758/11027/9925\r\nf 10744/11013/9912 10743/11012/9911 10759/11028/9926 10760/11029/9927\r\nf 10742/11011/9910 10761/11030/9928 10759/11028/9926 10743/11012/9911\r\nf 10762/11031/9929 10763/11032/9930 10759/11028/9926 10761/11030/9928\r\nf 10764/11033/9931 10760/11029/9927 10759/11028/9926 10763/11032/9930\r\nf 10742/11011/9910 10748/11017/9915 10765/11034/9932 10761/11030/9928\r\nf 10747/11016/9914 10766/11035/9933 10765/11034/9932 10748/11017/9915\r\nf 10767/11036/9934 10768/11037/9935 10765/11034/9932 10766/11035/9933\r\nf 10762/11031/9929 10761/11030/9928 10765/11034/9932 10768/11037/9935\r\nf 10754/11023/9921 10753/11022/9920 10769/11038/9936 10770/11039/9937\r\nf 10752/11021/9919 10771/11040/9938 10769/11038/9936 10753/11022/9920\r\nf 10772/11041/9939 10773/11042/9940 10769/11038/9936 10771/11040/9938\r\nf 10774/11043/9941 10770/11039/9937 10769/11038/9936 10773/11042/9940\r\nf 10752/11021/9919 10758/11027/9925 10775/11044/9942 10771/11040/9938\r\nf 10757/11026/9924 10776/11045/9943 10775/11044/9942 10758/11027/9925\r\nf 10777/11046/9944 10778/11047/9945 10775/11044/9942 10776/11045/9943\r\nf 10772/11041/9939 10771/11040/9938 10775/11044/9942 10778/11047/9945\r\nf 10764/11033/9931 10763/11032/9930 10779/11048/9946 10780/11049/9947\r\nf 10762/11031/9929 10781/11050/9948 10779/11048/9946 10763/11032/9930\r\nf 10782/11051/9949 10783/11052/9950 10779/11048/9946 10781/11050/9948\r\nf 10784/11053/9951 10780/11049/9947 10779/11048/9946 10783/11052/9950\r\nf 10762/11031/9929 10768/11037/9935 10785/11054/9952 10781/11050/9948\r\nf 10767/11036/9934 10786/11055/9953 10785/11054/9952 10768/11037/9935\r\nf 10787/11056/9954 10788/11057/9955 10785/11054/9952 10786/11055/9953\r\nf 10782/11051/9949 10781/11050/9948 10785/11054/9952 10788/11057/9955\r\nf 10774/11043/9941 10773/11042/9940 10789/11058/9956 10790/11059/9957\r\nf 10772/11041/9939 10791/11060/9958 10789/11058/9956 10773/11042/9940\r\nf 10792/11061/9959 10793/11062/9960 10789/11058/9956 10791/11060/9958\r\nf 10794/11063/9961 10790/11059/9957 10789/11058/9956 10793/11062/9960\r\nf 10772/11041/9939 10778/11047/9945 10795/11064/9962 10791/11060/9958\r\nf 10777/11046/9944 10796/11065/9963 10795/11064/9962 10778/11047/9945\r\nf 10797/11066/9964 10798/11067/9965 10795/11064/9962 10796/11065/9963\r\nf 10792/11061/9959 10791/11060/9958 10795/11064/9962 10798/11067/9965\r\nf 10784/11053/9951 10783/11052/9950 10799/11068/9966 10800/11069/9967\r\nf 10782/11051/9949 10801/11070/9968 10799/11068/9966 10783/11052/9950\r\nf 10802/11071/9969 10803/11072/9970 10799/11068/9966 10801/11070/9968\r\nf 10804/11073/9971 10800/11069/9967 10799/11068/9966 10803/11072/9970\r\nf 10782/11051/9949 10788/11057/9955 10805/11074/9972 10801/11070/9968\r\nf 10787/11056/9954 10806/11075/9973 10805/11074/9972 10788/11057/9955\r\nf 10807/11076/9974 10808/11077/9975 10805/11074/9972 10806/11075/9973\r\nf 10802/11071/9969 10801/11070/9968 10805/11074/9972 10808/11077/9975\r\nf 10794/11063/9961 10793/11062/9960 10809/11078/9976 10810/11079/9977\r\nf 10792/11061/9959 10811/11080/9978 10809/11078/9976 10793/11062/9960\r\nf 10812/11081/9979 10813/11082/9980 10809/11078/9976 10811/11080/9978\r\nf 10814/11083/9981 10810/11079/9977 10809/11078/9976 10813/11082/9980\r\nf 10792/11061/9959 10798/11067/9965 10815/11084/9982 10811/11080/9978\r\nf 10797/11066/9964 10816/11085/9983 10815/11084/9982 10798/11067/9965\r\nf 10817/11086/9984 10818/11087/9985 10815/11084/9982 10816/11085/9983\r\nf 10812/11081/9979 10811/11080/9978 10815/11084/9982 10818/11087/9985\r\nf 10804/11073/9971 10803/11072/9970 10819/11088/9986 10820/11089/9987\r\nf 10802/11071/9969 10821/11090/9988 10819/11088/9986 10803/11072/9970\r\nf 10822/11091/9989 10823/11092/9990 10819/11088/9986 10821/11090/9988\r\nf 10824/11093/9991 10820/11089/9987 10819/11088/9986 10823/11092/9990\r\nf 10802/11071/9969 10808/11077/9975 10825/11094/9992 10821/11090/9988\r\nf 10807/11076/9974 10826/11095/9993 10825/11094/9992 10808/11077/9975\r\nf 10827/11096/9994 10828/11097/9995 10825/11094/9992 10826/11095/9993\r\nf 10822/11091/9989 10821/11090/9988 10825/11094/9992 10828/11097/9995\r\nf 9527/9796/8700 9524/9793/8697 10829/11098/9996 9532/9801/8705\r\nf 9523/9792/8696 10830/11099/9997 10829/11098/9996 9524/9793/8697\r\nf 10831/11100/9998 10832/11101/9999 10829/11098/9996 10830/11099/9997\r\nf 9534/9803/8707 9532/9801/8705 10829/11098/9996 10832/11101/9999\r\nf 10833/11102/10000 10834/11103/10001 10835/11104/10002 10836/11105/10003\r\nf 10837/11106/10004 10838/11107/10005 10835/11104/10002 10834/11103/10001\r\nf 10839/11108/10006 10840/11109/10007 10835/11104/10002 10838/11107/10005\r\nf 10841/11110/10008 10836/11105/10003 10835/11104/10002 10840/11109/10007\r\nf 10837/11106/10004 10842/11111/10009 10843/11112/10010 10838/11107/10005\r\nf 10844/11113/10011 10845/11114/10012 10843/11112/10010 10842/11111/10009\r\nf 10846/11115/10013 10847/11116/10014 10843/11112/10010 10845/11114/10012\r\nf 10839/11108/10006 10838/11107/10005 10843/11112/10010 10847/11116/10014\r\nf 10848/11117/10015 10849/11118/10016 10850/11119/10017 10851/11120/10018\r\nf 10852/11121/10019 10853/11122/10020 10850/11119/10017 10849/11118/10016\r\nf 10854/11123/10021 10855/11124/10022 10850/11119/10017 10853/11122/10020\r\nf 10856/11125/10023 10851/11120/10018 10850/11119/10017 10855/11124/10022\r\nf 10852/11121/10019 10857/11126/10024 10858/11127/10025 10853/11122/10020\r\nf 10859/11128/10026 10860/11129/10027 10858/11127/10025 10857/11126/10024\r\nf 10861/11130/10028 10862/11131/10029 10858/11127/10025 10860/11129/10027\r\nf 10854/11123/10021 10853/11122/10020 10858/11127/10025 10862/11131/10029\r\nf 10841/11110/10008 10840/11109/10007 10863/11132/10030 10864/11133/10031\r\nf 10839/11108/10006 10865/11134/10032 10863/11132/10030 10840/11109/10007\r\nf 10866/11135/10033 10867/11136/10034 10863/11132/10030 10865/11134/10032\r\nf 10868/11137/10035 10864/11133/10031 10863/11132/10030 10867/11136/10034\r\nf 10839/11108/10006 10847/11116/10014 10869/11138/10036 10865/11134/10032\r\nf 10846/11115/10013 10870/11139/10037 10869/11138/10036 10847/11116/10014\r\nf 10871/11140/10038 10872/11141/10039 10869/11138/10036 10870/11139/10037\r\nf 10866/11135/10033 10865/11134/10032 10869/11138/10036 10872/11141/10039\r\nf 10856/11125/10023 10855/11124/10022 10873/11142/10040 10874/11143/10041\r\nf 10854/11123/10021 10875/11144/10042 10873/11142/10040 10855/11124/10022\r\nf 10876/11145/10043 10877/11146/10044 10873/11142/10040 10875/11144/10042\r\nf 10878/11147/10045 10874/11143/10041 10873/11142/10040 10877/11146/10044\r\nf 10854/11123/10021 10862/11131/10029 10879/11148/10046 10875/11144/10042\r\nf 10861/11130/10028 10880/11149/10047 10879/11148/10046 10862/11131/10029\r\nf 10881/11150/10048 10882/11151/10049 10879/11148/10046 10880/11149/10047\r\nf 10876/11145/10043 10875/11144/10042 10879/11148/10046 10882/11151/10049\r\nf 10868/11137/10035 10867/11136/10034 10883/11152/10050 10884/11153/10051\r\nf 10866/11135/10033 10885/11154/10052 10883/11152/10050 10867/11136/10034\r\nf 10886/11155/10053 10887/11156/10054 10883/11152/10050 10885/11154/10052\r\nf 10888/11157/10055 10884/11153/10051 10883/11152/10050 10887/11156/10054\r\nf 10866/11135/10033 10872/11141/10039 10889/11158/10056 10885/11154/10052\r\nf 10871/11140/10038 10890/11159/10057 10889/11158/10056 10872/11141/10039\r\nf 10891/11160/10058 10892/11161/10059 10889/11158/10056 10890/11159/10057\r\nf 10886/11155/10053 10885/11154/10052 10889/11158/10056 10892/11161/10059\r\nf 10878/11147/10045 10877/11146/10044 10893/11162/10060 10894/11163/10061\r\nf 10876/11145/10043 10895/11164/10062 10893/11162/10060 10877/11146/10044\r\nf 10896/11165/10063 10897/11166/10064 10893/11162/10060 10895/11164/10062\r\nf 10898/11167/10065 10894/11163/10061 10893/11162/10060 10897/11166/10064\r\nf 10876/11145/10043 10882/11151/10049 10899/11168/10066 10895/11164/10062\r\nf 10881/11150/10048 10900/11169/10067 10899/11168/10066 10882/11151/10049\r\nf 10901/11170/10068 10902/11171/10069 10899/11168/10066 10900/11169/10067\r\nf 10896/11165/10063 10895/11164/10062 10899/11168/10066 10902/11171/10069\r\nf 10888/11157/10055 10887/11156/10054 10903/11172/10070 10904/11173/10071\r\nf 10886/11155/10053 10905/11174/10072 10903/11172/10070 10887/11156/10054\r\nf 10906/11175/10073 10907/11176/10074 10903/11172/10070 10905/11174/10072\r\nf 10908/11177/10075 10904/11173/10071 10903/11172/10070 10907/11176/10074\r\nf 10886/11155/10053 10892/11161/10059 10909/11178/10076 10905/11174/10072\r\nf 10891/11160/10058 10910/11179/10077 10909/11178/10076 10892/11161/10059\r\nf 10911/11180/10077 10912/11181/10078 10909/11178/10076 10910/11179/10077\r\nf 10906/11175/10073 10905/11174/10072 10909/11178/10076 10912/11181/10078\r\nf 10898/11167/10065 10897/11166/10064 10913/11182/10079 10914/11183/10080\r\nf 10896/11165/10063 10915/11184/10081 10913/11182/10079 10897/11166/10064\r\nf 10916/11185/10082 10917/11186/10083 10913/11182/10079 10915/11184/10081\r\nf 10918/11187/10084 10914/11183/10080 10913/11182/10079 10917/11186/10083\r\nf 10896/11165/10063 10902/11171/10069 10919/11188/10085 10915/11184/10081\r\nf 10901/11170/10068 10920/11189/10086 10919/11188/10085 10902/11171/10069\r\nf 10921/11190/10086 10922/11191/10087 10919/11188/10085 10920/11189/10086\r\nf 10916/11185/10082 10915/11184/10081 10919/11188/10085 10922/11191/10087\r\nf 10908/11177/10075 10907/11176/10074 10923/11192/10088 10924/11193/10089\r\nf 10906/11175/10073 10925/11194/10090 10923/11192/10088 10907/11176/10074\r\nf 10926/11195/10091 10927/11196/10092 10923/11192/10088 10925/11194/10090\r\nf 10928/11197/10093 10924/11193/10089 10923/11192/10088 10927/11196/10092\r\nf 10906/11175/10073 10912/11181/10078 10929/11198/10094 10925/11194/10090\r\nf 10911/11180/10077 10930/11199/10058 10929/11198/10094 10912/11181/10078\r\nf 10931/11200/10095 10932/11201/10096 10929/11198/10094 10930/11199/10058\r\nf 10926/11195/10091 10925/11194/10090 10929/11198/10094 10932/11201/10096\r\nf 10918/11187/10084 10917/11186/10083 10933/11202/10097 10934/11203/10098\r\nf 10916/11185/10082 10935/11204/10099 10933/11202/10097 10917/11186/10083\r\nf 10936/11205/10100 10937/11206/10101 10933/11202/10097 10935/11204/10099\r\nf 10938/11207/10102 10934/11203/10098 10933/11202/10097 10937/11206/10101\r\nf 10916/11185/10082 10922/11191/10087 10939/11208/10103 10935/11204/10099\r\nf 10921/11190/10086 10940/11209/10068 10939/11208/10103 10922/11191/10087\r\nf 10941/11210/10104 10942/11211/10105 10939/11208/10103 10940/11209/10068\r\nf 10936/11205/10100 10935/11204/10099 10939/11208/10103 10942/11211/10105\r\nf 10928/11197/10093 10927/11196/10092 10943/11212/10106 10944/11213/10107\r\nf 10926/11195/10091 10945/11214/10108 10943/11212/10106 10927/11196/10092\r\nf 10946/11215/10109 10947/11216/10110 10943/11212/10106 10945/11214/10108\r\nf 10948/11217/10111 10944/11213/10107 10943/11212/10106 10947/11216/10110\r\nf 10926/11195/10091 10932/11201/10096 10949/11218/10112 10945/11214/10108\r\nf 10931/11200/10095 10950/11219/10113 10949/11218/10112 10932/11201/10096\r\nf 10951/11220/10114 10952/11221/10115 10949/11218/10112 10950/11219/10113\r\nf 10946/11215/10109 10945/11214/10108 10949/11218/10112 10952/11221/10115\r\nf 10938/11207/10102 10937/11206/10101 10953/11222/10116 10954/11223/10117\r\nf 10936/11205/10100 10955/11224/10118 10953/11222/10116 10937/11206/10101\r\nf 10956/11225/10119 10957/11226/10120 10953/11222/10116 10955/11224/10118\r\nf 10958/11227/10121 10954/11223/10117 10953/11222/10116 10957/11226/10120\r\nf 10936/11205/10100 10942/11211/10105 10959/11228/10122 10955/11224/10118\r\nf 10941/11210/10104 10960/11229/10123 10959/11228/10122 10942/11211/10105\r\nf 10961/11230/10124 10962/11231/10125 10959/11228/10122 10960/11229/10123\r\nf 10956/11225/10119 10955/11224/10118 10959/11228/10122 10962/11231/10125\r\nf 10948/11217/10111 10947/11216/10110 10963/11232/10126 10964/11233/10127\r\nf 10946/11215/10109 10965/11234/10128 10963/11232/10126 10947/11216/10110\r\nf 10966/11235/10129 10967/11236/10130 10963/11232/10126 10965/11234/10128\r\nf 10968/11237/10131 10964/11233/10127 10963/11232/10126 10967/11236/10130\r\nf 10946/11215/10109 10952/11221/10115 10969/11238/10132 10965/11234/10128\r\nf 10951/11220/10114 10970/11239/10133 10969/11238/10132 10952/11221/10115\r\nf 10971/11240/10134 10972/11241/10135 10969/11238/10132 10970/11239/10133\r\nf 10966/11235/10129 10965/11234/10128 10969/11238/10132 10972/11241/10135\r\nf 10958/11227/10121 10957/11226/10120 10973/11242/10136 10974/11243/10137\r\nf 10956/11225/10119 10975/11244/10138 10973/11242/10136 10957/11226/10120\r\nf 10976/11245/10139 10977/11246/10140 10973/11242/10136 10975/11244/10138\r\nf 10978/11247/10141 10974/11243/10137 10973/11242/10136 10977/11246/10140\r\nf 10956/11225/10119 10962/11231/10125 10979/11248/10142 10975/11244/10138\r\nf 10961/11230/10124 10980/11249/10143 10979/11248/10142 10962/11231/10125\r\nf 10981/11250/10144 10982/11251/10145 10979/11248/10142 10980/11249/10143\r\nf 10976/11245/10139 10975/11244/10138 10979/11248/10142 10982/11251/10145\r\nf 10968/11237/10131 10967/11236/10130 10983/11252/10146 10984/11253/10147\r\nf 10966/11235/10129 10985/11254/10148 10983/11252/10146 10967/11236/10130\r\nf 10986/11255/10149 10987/11256/10150 10983/11252/10146 10985/11254/10148\r\nf 10988/11257/10151 10984/11253/10147 10983/11252/10146 10987/11256/10150\r\nf 10966/11235/10129 10972/11241/10135 10989/11258/10152 10985/11254/10148\r\nf 10971/11240/10134 10990/11259/10153 10989/11258/10152 10972/11241/10135\r\nf 10991/11260/10154 10992/11261/10155 10989/11258/10152 10990/11259/10153\r\nf 10986/11255/10149 10985/11254/10148 10989/11258/10152 10992/11261/10155\r\nf 10978/11247/10141 10977/11246/10140 10993/11262/10156 10994/11263/10157\r\nf 10976/11245/10139 10995/11264/10158 10993/11262/10156 10977/11246/10140\r\nf 10996/11265/10159 10997/11266/10160 10993/11262/10156 10995/11264/10158\r\nf 10998/11267/10161 10994/11263/10157 10993/11262/10156 10997/11266/10160\r\nf 10976/11245/10139 10982/11251/10145 10999/11268/10162 10995/11264/10158\r\nf 10981/11250/10144 11000/11269/10163 10999/11268/10162 10982/11251/10145\r\nf 11001/11270/10164 11002/11271/10165 10999/11268/10162 11000/11269/10163\r\nf 10996/11265/10159 10995/11264/10158 10999/11268/10162 11002/11271/10165\r\nf 10988/11257/10151 10987/11256/10150 11003/11272/10166 11004/11273/10167\r\nf 10986/11255/10149 11005/11274/10168 11003/11272/10166 10987/11256/10150\r\nf 10824/11093/9991 11006/11275/10169 11003/11272/10166 11005/11274/10168\r\nf 11007/11276/10170 11004/11273/10167 11003/11272/10166 11006/11275/10169\r\nf 10986/11255/10149 10992/11261/10155 11008/11277/10171 11005/11274/10168\r\nf 10991/11260/10154 11009/11278/10172 11008/11277/10171 10992/11261/10155\r\nf 11010/11279/10173 11011/11280/10174 11008/11277/10171 11009/11278/10172\r\nf 10824/11093/9991 11005/11274/10168 11008/11277/10171 11011/11280/10174\r\nf 10998/11267/10161 10997/11266/10160 11012/11281/10175 11013/11282/10176\r\nf 10996/11265/10159 11014/11283/10177 11012/11281/10175 10997/11266/10160\r\nf 11015/11284/10178 11016/11285/10179 11012/11281/10175 11014/11283/10177\r\nf 10812/11081/9979 11013/11282/10176 11012/11281/10175 11016/11285/10179\r\nf 10996/11265/10159 11002/11271/10165 11017/11286/10180 11014/11283/10177\r\nf 11001/11270/10164 11018/11287/10181 11017/11286/10180 11002/11271/10165\r\nf 11019/11288/10182 11020/11289/10183 11017/11286/10180 11018/11287/10181\r\nf 11015/11284/10178 11014/11283/10177 11017/11286/10180 11020/11289/10183\r\nf 11007/11276/10170 11006/11275/10169 11021/11290/10184 11022/11291/10185\r\nf 10824/11093/9991 10823/11092/9990 11021/11290/10184 11006/11275/10169\r\nf 10822/11091/9989 11023/11292/10186 11021/11290/10184 10823/11092/9990\r\nf 11024/11293/10187 11022/11291/10185 11021/11290/10184 11023/11292/10186\r\nf 10812/11081/9979 11016/11285/10179 11025/11294/10188 10813/11082/9980\r\nf 11015/11284/10178 11026/11295/10189 11025/11294/10188 11016/11285/10179\r\nf 11027/11296/10190 11028/11297/10191 11025/11294/10188 11026/11295/10189\r\nf 10814/11083/9981 10813/11082/9980 11025/11294/10188 11028/11297/10191\r\nf 11015/11284/10178 11020/11289/10183 11029/11298/10192 11026/11295/10189\r\nf 11019/11288/10182 11030/11299/10193 11029/11298/10192 11020/11289/10183\r\nf 11031/11300/10194 11032/11301/10195 11029/11298/10192 11030/11299/10193\r\nf 11027/11296/10190 11026/11295/10189 11029/11298/10192 11032/11301/10195\r\nf 11024/11293/10187 11023/11292/10186 11033/11302/10196 11034/11303/10197\r\nf 10822/11091/9989 11035/11304/10198 11033/11302/10196 11023/11292/10186\r\nf 11036/11305/10199 11037/11306/10200 11033/11302/10196 11035/11304/10198\r\nf 11038/11307/10201 11034/11303/10197 11033/11302/10196 11037/11306/10200\r\nf 10822/11091/9989 10828/11097/9995 11039/11308/10202 11035/11304/10198\r\nf 10827/11096/9994 11040/11309/10203 11039/11308/10202 10828/11097/9995\r\nf 11041/11310/10204 11042/11311/10205 11039/11308/10202 11040/11309/10203\r\nf 11036/11305/10199 11035/11304/10198 11039/11308/10202 11042/11311/10205\r\nf 10814/11083/9981 11028/11297/10191 11043/11312/10206 11044/11313/10207\r\nf 11027/11296/10190 11045/11314/10208 11043/11312/10206 11028/11297/10191\r\nf 11046/11315/10209 11047/11316/10210 11043/11312/10206 11045/11314/10208\r\nf 11048/11317/10211 11044/11313/10207 11043/11312/10206 11047/11316/10210\r\nf 11027/11296/10190 11032/11301/10195 11049/11318/10212 11045/11314/10208\r\nf 11031/11300/10194 11050/11319/10213 11049/11318/10212 11032/11301/10195\r\nf 11051/11320/10214 11052/11321/10215 11049/11318/10212 11050/11319/10213\r\nf 11046/11315/10209 11045/11314/10208 11049/11318/10212 11052/11321/10215\r\nf 11038/11307/10201 11037/11306/10200 11053/11322/10216 11054/11323/10217\r\nf 11036/11305/10199 11055/11324/10218 11053/11322/10216 11037/11306/10200\r\nf 11056/11325/10219 11057/11326/10220 11053/11322/10216 11055/11324/10218\r\nf 11058/11327/10221 11054/11323/10217 11053/11322/10216 11057/11326/10220\r\nf 11036/11305/10199 11042/11311/10205 11059/11328/10222 11055/11324/10218\r\nf 11041/11310/10204 11060/11329/10223 11059/11328/10222 11042/11311/10205\r\nf 11061/11330/10224 11062/11331/10225 11059/11328/10222 11060/11329/10223\r\nf 11056/11325/10219 11055/11324/10218 11059/11328/10222 11062/11331/10225\r\nf 11048/11317/10211 11047/11316/10210 11063/11332/10226 11064/11333/10227\r\nf 11046/11315/10209 11065/11334/10228 11063/11332/10226 11047/11316/10210\r\nf 11066/11335/10229 11067/11336/10230 11063/11332/10226 11065/11334/10228\r\nf 11068/11337/10231 11064/11333/10227 11063/11332/10226 11067/11336/10230\r\nf 11046/11315/10209 11052/11321/10215 11069/11338/10232 11065/11334/10228\r\nf 11051/11320/10214 11070/11339/10233 11069/11338/10232 11052/11321/10215\r\nf 11071/11340/10234 11072/11341/10235 11069/11338/10232 11070/11339/10233\r\nf 11066/11335/10229 11065/11334/10228 11069/11338/10232 11072/11341/10235\r\nf 11058/11327/10221 11057/11326/10220 11073/11342/10236 11074/11343/10237\r\nf 11056/11325/10219 11075/11344/10238 11073/11342/10236 11057/11326/10220\r\nf 11076/11345/10239 11077/11346/10240 11073/11342/10236 11075/11344/10238\r\nf 11078/11347/10241 11074/11343/10237 11073/11342/10236 11077/11346/10240\r\nf 11056/11325/10219 11062/11331/10225 11079/11348/10242 11075/11344/10238\r\nf 11061/11330/10224 11080/11349/10243 11079/11348/10242 11062/11331/10225\r\nf 11081/11350/10244 11082/11351/10245 11079/11348/10242 11080/11349/10243\r\nf 11076/11345/10239 11075/11344/10238 11079/11348/10242 11082/11351/10245\r\nf 11068/11337/10231 11067/11336/10230 11083/11352/10246 11084/11353/10247\r\nf 11066/11335/10229 11085/11354/10248 11083/11352/10246 11067/11336/10230\r\nf 11086/11355/10249 11087/11356/10250 11083/11352/10246 11085/11354/10248\r\nf 11088/11357/10251 11084/11353/10247 11083/11352/10246 11087/11356/10250\r\nf 11066/11335/10229 11072/11341/10235 11089/11358/10252 11085/11354/10248\r\nf 11071/11340/10234 11090/11359/10253 11089/11358/10252 11072/11341/10235\r\nf 11091/11360/10254 11092/11361/10255 11089/11358/10252 11090/11359/10253\r\nf 11086/11355/10249 11085/11354/10248 11089/11358/10252 11092/11361/10255\r\nf 11078/11347/10241 11077/11346/10240 11093/11362/10256 11094/11363/10257\r\nf 11076/11345/10239 11095/11364/10258 11093/11362/10256 11077/11346/10240\r\nf 11096/11365/10259 11097/11366/10260 11093/11362/10256 11095/11364/10258\r\nf 11098/11367/10261 11094/11363/10257 11093/11362/10256 11097/11366/10260\r\nf 11076/11345/10239 11082/11351/10245 11099/11368/10262 11095/11364/10258\r\nf 11081/11350/10244 11100/11369/10263 11099/11368/10262 11082/11351/10245\r\nf 11101/11370/10264 11102/11371/10265 11099/11368/10262 11100/11369/10263\r\nf 11096/11365/10259 11095/11364/10258 11099/11368/10262 11102/11371/10265\r\nf 11088/11357/10251 11087/11356/10250 11103/11372/10266 11104/11373/10267\r\nf 11086/11355/10249 11105/11374/10268 11103/11372/10266 11087/11356/10250\r\nf 11106/11375/10269 11107/11376/10270 11103/11372/10266 11105/11374/10268\r\nf 11108/11377/10271 11104/11373/10267 11103/11372/10266 11107/11376/10270\r\nf 11086/11355/10249 11092/11361/10255 11109/11378/10272 11105/11374/10268\r\nf 11091/11360/10254 11110/11379/10273 11109/11378/10272 11092/11361/10255\r\nf 11111/11380/10274 11112/11381/10275 11109/11378/10272 11110/11379/10273\r\nf 11106/11375/10269 11105/11374/10268 11109/11378/10272 11112/11381/10275\r\nf 11098/11367/10261 11097/11366/10260 11113/11382/10276 11114/11383/10277\r\nf 11096/11365/10259 11115/11384/10278 11113/11382/10276 11097/11366/10260\r\nf 11116/11385/10279 11117/11386/10280 11113/11382/10276 11115/11384/10278\r\nf 11118/11387/10281 11114/11383/10277 11113/11382/10276 11117/11386/10280\r\nf 11096/11365/10259 11102/11371/10265 11119/11388/10282 11115/11384/10278\r\nf 11101/11370/10264 11120/11389/10283 11119/11388/10282 11102/11371/10265\r\nf 11121/11390/10284 11122/11391/10285 11119/11388/10282 11120/11389/10283\r\nf 11116/11385/10279 11115/11384/10278 11119/11388/10282 11122/11391/10285\r\nf 11108/11377/10271 11107/11376/10270 11123/11392/10286 11124/11393/10287\r\nf 11106/11375/10269 11125/11394/10288 11123/11392/10286 11107/11376/10270\r\nf 11126/11395/10289 11127/11396/10290 11123/11392/10286 11125/11394/10288\r\nf 11128/11397/10291 11124/11393/10287 11123/11392/10286 11127/11396/10290\r\nf 11106/11375/10269 11112/11381/10275 11129/11398/10292 11125/11394/10288\r\nf 11111/11380/10274 11130/11399/10293 11129/11398/10292 11112/11381/10275\r\nf 11131/11400/10294 11132/11401/10295 11129/11398/10292 11130/11399/10293\r\nf 11126/11395/10289 11125/11394/10288 11129/11398/10292 11132/11401/10295\r\nf 11118/11387/10281 11117/11386/10280 11133/11402/10296 11134/11403/10297\r\nf 11116/11385/10279 11135/11404/10298 11133/11402/10296 11117/11386/10280\r\nf 11136/11405/10299 11137/11406/10300 11133/11402/10296 11135/11404/10298\r\nf 11138/11407/10301 11134/11403/10297 11133/11402/10296 11137/11406/10300\r\nf 11116/11385/10279 11122/11391/10285 11139/11408/10302 11135/11404/10298\r\nf 11121/11390/10284 11140/11409/10303 11139/11408/10302 11122/11391/10285\r\nf 11141/11410/10304 11142/11411/10305 11139/11408/10302 11140/11409/10303\r\nf 11136/11405/10299 11135/11404/10298 11139/11408/10302 11142/11411/10305\r\nf 11128/11397/10291 11127/11396/10290 11143/11412/10306 11144/11413/10307\r\nf 11126/11395/10289 11145/11414/10308 11143/11412/10306 11127/11396/10290\r\nf 11146/11415/10309 11147/11416/10310 11143/11412/10306 11145/11414/10308\r\nf 11148/11417/10311 11144/11413/10307 11143/11412/10306 11147/11416/10310\r\nf 11126/11395/10289 11132/11401/10295 11149/11418/10312 11145/11414/10308\r\nf 11131/11400/10294 11150/11419/10313 11149/11418/10312 11132/11401/10295\r\nf 11151/11420/10314 11152/11421/10315 11149/11418/10312 11150/11419/10313\r\nf 11146/11415/10309 11145/11414/10308 11149/11418/10312 11152/11421/10315\r\nf 11138/11407/10301 11137/11406/10300 11153/11422/10316 11154/11423/10317\r\nf 11136/11405/10299 11155/11424/10318 11153/11422/10316 11137/11406/10300\r\nf 11156/11425/10319 11157/11426/10320 11153/11422/10316 11155/11424/10318\r\nf 11158/11427/10321 11154/11423/10317 11153/11422/10316 11157/11426/10320\r\nf 11136/11405/10299 11142/11411/10305 11159/11428/10322 11155/11424/10318\r\nf 11141/11410/10304 11160/11429/10323 11159/11428/10322 11142/11411/10305\r\nf 11161/11430/10324 11162/11431/10325 11159/11428/10322 11160/11429/10323\r\nf 11156/11425/10319 11155/11424/10318 11159/11428/10322 11162/11431/10325\r\nf 11148/11417/10311 11147/11416/10310 11163/11432/10326 11164/11433/10327\r\nf 11146/11415/10309 11165/11434/10328 11163/11432/10326 11147/11416/10310\r\nf 11166/11435/10329 11167/11436/10330 11163/11432/10326 11165/11434/10328\r\nf 11168/11437/10331 11164/11433/10327 11163/11432/10326 11167/11436/10330\r\nf 11146/11415/10309 11152/11421/10315 11169/11438/10332 11165/11434/10328\r\nf 11151/11420/10314 11170/11439/10333 11169/11438/10332 11152/11421/10315\r\nf 11171/11440/10334 11172/11441/10335 11169/11438/10332 11170/11439/10333\r\nf 11166/11435/10329 11165/11434/10328 11169/11438/10332 11172/11441/10335\r\nf 11158/11427/10321 11157/11426/10320 11173/11442/10336 11174/11443/10337\r\nf 11156/11425/10319 11175/11444/10338 11173/11442/10336 11157/11426/10320\r\nf 11176/11445/10339 11177/11446/10340 11173/11442/10336 11175/11444/10338\r\nf 11178/11447/10341 11174/11443/10337 11173/11442/10336 11177/11446/10340\r\nf 11156/11425/10319 11162/11431/10325 11179/11448/10342 11175/11444/10338\r\nf 11161/11430/10324 11180/11449/10343 11179/11448/10342 11162/11431/10325\r\nf 11181/11450/10344 11182/11451/10345 11179/11448/10342 11180/11449/10343\r\nf 11176/11445/10339 11175/11444/10338 11179/11448/10342 11182/11451/10345\r\nf 11168/11437/10331 11167/11436/10330 11183/11452/10346 11184/11453/10347\r\nf 11166/11435/10329 11185/11454/10348 11183/11452/10346 11167/11436/10330\r\nf 11186/11455/10349 11187/11456/10350 11183/11452/10346 11185/11454/10348\r\nf 11188/11457/10351 11184/11453/10347 11183/11452/10346 11187/11456/10350\r\nf 11166/11435/10329 11172/11441/10335 11189/11458/10352 11185/11454/10348\r\nf 11171/11440/10334 11190/11459/10353 11189/11458/10352 11172/11441/10335\r\nf 11191/11460/10354 11192/11461/10355 11189/11458/10352 11190/11459/10353\r\nf 11186/11455/10349 11185/11454/10348 11189/11458/10352 11192/11461/10355\r\nf 11178/11447/10341 11177/11446/10340 11193/11462/10356 11194/11463/10357\r\nf 11176/11445/10339 11195/11464/10358 11193/11462/10356 11177/11446/10340\r\nf 11196/11465/10359 11197/11466/10360 11193/11462/10356 11195/11464/10358\r\nf 11198/11467/10361 11194/11463/10357 11193/11462/10356 11197/11466/10360\r\nf 11176/11445/10339 11182/11451/10345 11199/11468/10362 11195/11464/10358\r\nf 11181/11450/10344 11200/11469/10363 11199/11468/10362 11182/11451/10345\r\nf 11201/11470/10364 11202/11471/10365 11199/11468/10362 11200/11469/10363\r\nf 11196/11465/10359 11195/11464/10358 11199/11468/10362 11202/11471/10365\r\nf 11188/11457/10351 11187/11456/10350 11203/11472/10366 11204/11473/10367\r\nf 11186/11455/10349 11205/11474/10368 11203/11472/10366 11187/11456/10350\r\nf 11206/11475/10369 11207/11476/10370 11203/11472/10366 11205/11474/10368\r\nf 11208/11477/10371 11204/11473/10367 11203/11472/10366 11207/11476/10370\r\nf 11186/11455/10349 11192/11461/10355 11209/11478/10372 11205/11474/10368\r\nf 11191/11460/10354 11210/11479/10373 11209/11478/10372 11192/11461/10355\r\nf 11211/11480/10374 11212/11481/10375 11209/11478/10372 11210/11479/10373\r\nf 11206/11475/10369 11205/11474/10368 11209/11478/10372 11212/11481/10375\r\nf 11198/11467/10361 11197/11466/10360 11213/11482/10376 11214/11483/10377\r\nf 11196/11465/10359 11215/11484/10378 11213/11482/10376 11197/11466/10360\r\nf 11216/11485/10379 11217/11486/10380 11213/11482/10376 11215/11484/10378\r\nf 11218/11487/10381 11214/11483/10377 11213/11482/10376 11217/11486/10380\r\nf 11196/11465/10359 11202/11471/10365 11219/11488/10382 11215/11484/10378\r\nf 11201/11470/10364 11220/11489/10383 11219/11488/10382 11202/11471/10365\r\nf 11221/11490/10384 11222/11491/10385 11219/11488/10382 11220/11489/10383\r\nf 11216/11485/10379 11215/11484/10378 11219/11488/10382 11222/11491/10385\r\nf 11208/11477/10371 11207/11476/10370 11223/11492/10386 11224/11493/10387\r\nf 11206/11475/10369 11225/11494/10388 11223/11492/10386 11207/11476/10370\r\nf 11226/11495/10389 11227/11496/10390 11223/11492/10386 11225/11494/10388\r\nf 11228/11497/10391 11224/11493/10387 11223/11492/10386 11227/11496/10390\r\nf 11206/11475/10369 11212/11481/10375 11229/11498/10392 11225/11494/10388\r\nf 11211/11480/10374 11230/11499/10393 11229/11498/10392 11212/11481/10375\r\nf 11231/11500/10394 11232/11501/10395 11229/11498/10392 11230/11499/10393\r\nf 11226/11495/10389 11225/11494/10388 11229/11498/10392 11232/11501/10395\r\nf 11218/11487/10381 11217/11486/10380 11233/11502/10396 11234/11503/10397\r\nf 11216/11485/10379 11235/11504/10398 11233/11502/10396 11217/11486/10380\r\nf 11236/11505/10399 11237/11506/10400 11233/11502/10396 11235/11504/10398\r\nf 11238/11507/10401 11234/11503/10397 11233/11502/10396 11237/11506/10400\r\nf 11216/11485/10379 11222/11491/10385 11239/11508/10402 11235/11504/10398\r\nf 11221/11490/10384 11240/11509/10403 11239/11508/10402 11222/11491/10385\r\nf 11241/11510/10384 11242/11511/10404 11239/11508/10402 11240/11509/10403\r\nf 11236/11505/10399 11235/11504/10398 11239/11508/10402 11242/11511/10404\r\nf 11228/11497/10391 11227/11496/10390 11243/11512/10405 11244/11513/10406\r\nf 11226/11495/10389 11245/11514/10407 11243/11512/10405 11227/11496/10390\r\nf 11246/11515/10408 11247/11516/10409 11243/11512/10405 11245/11514/10407\r\nf 11248/11517/10410 11244/11513/10406 11243/11512/10405 11247/11516/10409\r\nf 11226/11495/10389 11232/11501/10395 11249/11518/10411 11245/11514/10407\r\nf 11231/11500/10394 11250/11519/10412 11249/11518/10411 11232/11501/10395\r\nf 11251/11520/10394 11252/11521/10413 11249/11518/10411 11250/11519/10412\r\nf 11246/11515/10408 11245/11514/10407 11249/11518/10411 11252/11521/10413\r\nf 11238/11507/10401 11237/11506/10400 11253/11522/10414 11254/11523/10415\r\nf 11236/11505/10399 11255/11524/10416 11253/11522/10414 11237/11506/10400\r\nf 11256/11525/10417 11257/11526/10418 11253/11522/10414 11255/11524/10416\r\nf 11258/11527/10419 11254/11523/10415 11253/11522/10414 11257/11526/10418\r\nf 11236/11505/10399 11242/11511/10404 11259/11528/10420 11255/11524/10416\r\nf 11241/11510/10384 11260/11529/10421 11259/11528/10420 11242/11511/10404\r\nf 11261/11530/10422 11262/11531/10423 11259/11528/10420 11260/11529/10421\r\nf 11256/11525/10417 11255/11524/10416 11259/11528/10420 11262/11531/10423\r\nf 11248/11517/10410 11247/11516/10409 11263/11532/10424 11264/11533/10425\r\nf 11246/11515/10408 11265/11534/10426 11263/11532/10424 11247/11516/10409\r\nf 11266/11535/10427 11267/11536/10428 11263/11532/10424 11265/11534/10426\r\nf 11268/11537/10429 11264/11533/10425 11263/11532/10424 11267/11536/10428\r\nf 11246/11515/10408 11252/11521/10413 11269/11538/10430 11265/11534/10426\r\nf 11251/11520/10394 11270/11539/10431 11269/11538/10430 11252/11521/10413\r\nf 11271/11540/10432 11272/11541/10433 11269/11538/10430 11270/11539/10431\r\nf 11266/11535/10427 11265/11534/10426 11269/11538/10430 11272/11541/10433\r\nf 11258/11527/10419 11257/11526/10418 11273/11542/10434 11274/11543/10435\r\nf 11256/11525/10417 11275/11544/10436 11273/11542/10434 11257/11526/10418\r\nf 11276/11545/10437 11277/11546/10438 11273/11542/10434 11275/11544/10436\r\nf 11278/11547/10439 11274/11543/10435 11273/11542/10434 11277/11546/10438\r\nf 11256/11525/10417 11262/11531/10423 11279/11548/10440 11275/11544/10436\r\nf 11261/11530/10422 11280/11549/10441 11279/11548/10440 11262/11531/10423\r\nf 11281/11550/10442 11282/11551/10443 11279/11548/10440 11280/11549/10441\r\nf 11276/11545/10437 11275/11544/10436 11279/11548/10440 11282/11551/10443\r\nf 11268/11537/10429 11267/11536/10428 11283/11552/10444 11284/11553/10445\r\nf 11266/11535/10427 11285/11554/10446 11283/11552/10444 11267/11536/10428\r\nf 11286/11555/10447 11287/11556/10448 11283/11552/10444 11285/11554/10446\r\nf 11288/11557/10449 11284/11553/10445 11283/11552/10444 11287/11556/10448\r\nf 11266/11535/10427 11272/11541/10433 11289/11558/10450 11285/11554/10446\r\nf 11271/11540/10432 11290/11559/10451 11289/11558/10450 11272/11541/10433\r\nf 11291/11560/10452 11292/11561/10453 11289/11558/10450 11290/11559/10451\r\nf 11286/11555/10447 11285/11554/10446 11289/11558/10450 11292/11561/10453\r\nf 11278/11547/10439 11277/11546/10438 11293/11562/10454 11294/11563/10455\r\nf 11276/11545/10437 11295/11564/10456 11293/11562/10454 11277/11546/10438\r\nf 11296/11565/10457 11297/11566/10458 11293/11562/10454 11295/11564/10456\r\nf 11298/11567/10459 11294/11563/10455 11293/11562/10454 11297/11566/10458\r\nf 11276/11545/10437 11282/11551/10443 11299/11568/10460 11295/11564/10456\r\nf 11281/11550/10442 11300/11569/10461 11299/11568/10460 11282/11551/10443\r\nf 11301/11570/10462 11302/11571/10463 11299/11568/10460 11300/11569/10461\r\nf 11296/11565/10457 11295/11564/10456 11299/11568/10460 11302/11571/10463\r\nf 11288/11557/10449 11287/11556/10448 11303/11572/10464 11304/11573/10465\r\nf 11286/11555/10447 11305/11574/10466 11303/11572/10464 11287/11556/10448\r\nf 11306/11575/10467 11307/11576/10468 11303/11572/10464 11305/11574/10466\r\nf 11308/11577/10469 11304/11573/10465 11303/11572/10464 11307/11576/10468\r\nf 11286/11555/10447 11292/11561/10453 11309/11578/10470 11305/11574/10466\r\nf 11291/11560/10452 11310/11579/10471 11309/11578/10470 11292/11561/10453\r\nf 11311/11580/10472 11312/11581/10473 11309/11578/10470 11310/11579/10471\r\nf 11306/11575/10467 11305/11574/10466 11309/11578/10470 11312/11581/10473\r\nf 11298/11567/10459 11297/11566/10458 11313/11582/10474 11314/11583/10475\r\nf 11296/11565/10457 11315/11584/10476 11313/11582/10474 11297/11566/10458\r\nf 11316/11585/10477 11317/11586/10478 11313/11582/10474 11315/11584/10476\r\nf 11318/11587/10479 11314/11583/10475 11313/11582/10474 11317/11586/10478\r\nf 11296/11565/10457 11302/11571/10463 11319/11588/10480 11315/11584/10476\r\nf 11301/11570/10462 11320/11589/10481 11319/11588/10480 11302/11571/10463\r\nf 11321/11590/10482 11322/11591/10483 11319/11588/10480 11320/11589/10481\r\nf 11316/11585/10477 11315/11584/10476 11319/11588/10480 11322/11591/10483\r\nf 11308/11577/10469 11307/11576/10468 11323/11592/10484 11324/11593/10485\r\nf 11306/11575/10467 11325/11594/10486 11323/11592/10484 11307/11576/10468\r\nf 11326/11595/10487 11327/11596/10488 11323/11592/10484 11325/11594/10486\r\nf 11328/11597/10489 11324/11593/10485 11323/11592/10484 11327/11596/10488\r\nf 11306/11575/10467 11312/11581/10473 11329/11598/10490 11325/11594/10486\r\nf 11311/11580/10472 11330/11599/10491 11329/11598/10490 11312/11581/10473\r\nf 11331/11600/10492 11332/11601/10493 11329/11598/10490 11330/11599/10491\r\nf 11326/11595/10487 11325/11594/10486 11329/11598/10490 11332/11601/10493\r\nf 11318/11587/10479 11317/11586/10478 11333/11602/10494 11334/11603/10495\r\nf 11316/11585/10477 11335/11604/10496 11333/11602/10494 11317/11586/10478\r\nf 11336/11605/10497 11337/11606/10498 11333/11602/10494 11335/11604/10496\r\nf 11338/11607/10499 11334/11603/10495 11333/11602/10494 11337/11606/10498\r\nf 11316/11585/10477 11322/11591/10483 11339/11608/10500 11335/11604/10496\r\nf 11321/11590/10482 11340/11609/10501 11339/11608/10500 11322/11591/10483\r\nf 11341/11610/10502 11342/11611/10503 11339/11608/10500 11340/11609/10501\r\nf 11336/11605/10497 11335/11604/10496 11339/11608/10500 11342/11611/10503\r\nf 11328/11597/10489 11327/11596/10488 11343/11612/10504 11344/11613/10505\r\nf 11326/11595/10487 11345/11614/10506 11343/11612/10504 11327/11596/10488\r\nf 11346/11615/10507 11347/11616/10508 11343/11612/10504 11345/11614/10506\r\nf 11348/11617/10509 11344/11613/10505 11343/11612/10504 11347/11616/10508\r\nf 11326/11595/10487 11332/11601/10493 11349/11618/10510 11345/11614/10506\r\nf 11331/11600/10492 11350/11619/10511 11349/11618/10510 11332/11601/10493\r\nf 11351/11620/10512 11352/11621/10513 11349/11618/10510 11350/11619/10511\r\nf 11346/11615/10507 11345/11614/10506 11349/11618/10510 11352/11621/10513\r\nf 11338/11607/10499 11337/11606/10498 11353/11622/10514 11354/11623/10515\r\nf 11336/11605/10497 11355/11624/10516 11353/11622/10514 11337/11606/10498\r\nf 11356/11625/10517 11357/11626/10518 11353/11622/10514 11355/11624/10516\r\nf 11358/11627/10519 11354/11623/10515 11353/11622/10514 11357/11626/10518\r\nf 11336/11605/10497 11342/11611/10503 11359/11628/10520 11355/11624/10516\r\nf 11341/11610/10502 11360/11629/10521 11359/11628/10520 11342/11611/10503\r\nf 11361/11630/10522 11362/11631/10523 11359/11628/10520 11360/11629/10521\r\nf 11356/11625/10517 11355/11624/10516 11359/11628/10520 11362/11631/10523\r\nf 11348/11617/10509 11347/11616/10508 11363/11632/10524 11364/11633/10525\r\nf 11346/11615/10507 11365/11634/10526 11363/11632/10524 11347/11616/10508\r\nf 11366/11635/10527 11367/11636/10528 11363/11632/10524 11365/11634/10526\r\nf 11368/11637/10529 11364/11633/10525 11363/11632/10524 11367/11636/10528\r\nf 11346/11615/10507 11352/11621/10513 11369/11638/10530 11365/11634/10526\r\nf 11351/11620/10512 11370/11639/10531 11369/11638/10530 11352/11621/10513\r\nf 11371/11640/10532 11372/11641/10533 11369/11638/10530 11370/11639/10531\r\nf 11366/11635/10527 11365/11634/10526 11369/11638/10530 11372/11641/10533\r\nf 11358/11627/10519 11357/11626/10518 11373/11642/10534 11374/11643/10535\r\nf 11356/11625/10517 11375/11644/10536 11373/11642/10534 11357/11626/10518\r\nf 11376/11645/10537 11377/11646/10538 11373/11642/10534 11375/11644/10536\r\nf 11378/11647/10539 11374/11643/10535 11373/11642/10534 11377/11646/10538\r\nf 11356/11625/10517 11362/11631/10523 11379/11648/10540 11375/11644/10536\r\nf 11361/11630/10522 11380/11649/10541 11379/11648/10540 11362/11631/10523\r\nf 11381/11650/10542 11382/11651/10543 11379/11648/10540 11380/11649/10541\r\nf 11376/11645/10537 11375/11644/10536 11379/11648/10540 11382/11651/10543\r\nf 11368/11637/10529 11367/11636/10528 11383/11652/10544 11384/11653/10545\r\nf 11366/11635/10527 11385/11654/10546 11383/11652/10544 11367/11636/10528\r\nf 11386/11655/10547 11387/11656/10548 11383/11652/10544 11385/11654/10546\r\nf 11388/11657/10549 11384/11653/10545 11383/11652/10544 11387/11656/10548\r\nf 11366/11635/10527 11372/11641/10533 11389/11658/10550 11385/11654/10546\r\nf 11371/11640/10532 11390/11659/10551 11389/11658/10550 11372/11641/10533\r\nf 11391/11660/10552 11392/11661/10553 11389/11658/10550 11390/11659/10551\r\nf 11386/11655/10547 11385/11654/10546 11389/11658/10550 11392/11661/10553\r\nf 11378/11647/10539 11377/11646/10538 11393/11662/10554 11394/11663/10555\r\nf 11376/11645/10537 11395/11664/10556 11393/11662/10554 11377/11646/10538\r\nf 11396/11665/10557 11397/11666/10558 11393/11662/10554 11395/11664/10556\r\nf 11398/11667/10559 11394/11663/10555 11393/11662/10554 11397/11666/10558\r\nf 11376/11645/10537 11382/11651/10543 11399/11668/10560 11395/11664/10556\r\nf 11381/11650/10542 11400/11669/10561 11399/11668/10560 11382/11651/10543\r\nf 11401/11670/10562 11402/11671/10563 11399/11668/10560 11400/11669/10561\r\nf 11396/11665/10557 11395/11664/10556 11399/11668/10560 11402/11671/10563\r\nf 11388/11657/10549 11387/11656/10548 11403/11672/10564 11404/11673/10565\r\nf 11386/11655/10547 11405/11674/10566 11403/11672/10564 11387/11656/10548\r\nf 11406/11675/10567 11407/11676/10568 11403/11672/10564 11405/11674/10566\r\nf 11408/11677/10569 11404/11673/10565 11403/11672/10564 11407/11676/10568\r\nf 11386/11655/10547 11392/11661/10553 11409/11678/10570 11405/11674/10566\r\nf 11391/11660/10552 11410/11679/10571 11409/11678/10570 11392/11661/10553\r\nf 11411/11680/10572 11412/11681/10573 11409/11678/10570 11410/11679/10571\r\nf 11406/11675/10567 11405/11674/10566 11409/11678/10570 11412/11681/10573\r\nf 11398/11667/10559 11397/11666/10558 11413/11682/10574 11414/11683/10575\r\nf 11396/11665/10557 11415/11684/10576 11413/11682/10574 11397/11666/10558\r\nf 11416/11685/10577 11417/11686/10578 11413/11682/10574 11415/11684/10576\r\nf 11418/11687/10579 11414/11683/10575 11413/11682/10574 11417/11686/10578\r\nf 11396/11665/10557 11402/11671/10563 11419/11688/10580 11415/11684/10576\r\nf 11401/11670/10562 11420/11689/10581 11419/11688/10580 11402/11671/10563\r\nf 11421/11690/10582 11422/11691/10583 11419/11688/10580 11420/11689/10581\r\nf 11416/11685/10577 11415/11684/10576 11419/11688/10580 11422/11691/10583\r\nf 11408/11677/10569 11407/11676/10568 11423/11692/10584 11424/11693/10585\r\nf 11406/11675/10567 11425/11694/10586 11423/11692/10584 11407/11676/10568\r\nf 11426/11695/10587 11427/11696/10588 11423/11692/10584 11425/11694/10586\r\nf 11428/11697/10589 11424/11693/10585 11423/11692/10584 11427/11696/10588\r\nf 11406/11675/10567 11412/11681/10573 11429/11698/10590 11425/11694/10586\r\nf 11411/11680/10572 11430/11699/10591 11429/11698/10590 11412/11681/10573\r\nf 11431/11700/10592 11432/11701/10593 11429/11698/10590 11430/11699/10591\r\nf 11426/11695/10587 11425/11694/10586 11429/11698/10590 11432/11701/10593\r\nf 11418/11687/10579 11417/11686/10578 11433/11702/10594 11434/11703/10595\r\nf 11416/11685/10577 11435/11704/10596 11433/11702/10594 11417/11686/10578\r\nf 11436/11705/10597 11437/11706/10598 11433/11702/10594 11435/11704/10596\r\nf 11438/11707/10599 11434/11703/10595 11433/11702/10594 11437/11706/10598\r\nf 11416/11685/10577 11422/11691/10583 11439/11708/10600 11435/11704/10596\r\nf 11421/11690/10582 11440/11709/10601 11439/11708/10600 11422/11691/10583\r\nf 11441/11710/10602 11442/11711/10603 11439/11708/10600 11440/11709/10601\r\nf 11436/11705/10597 11435/11704/10596 11439/11708/10600 11442/11711/10603\r\nf 11428/11697/10589 11427/11696/10588 11443/11712/10604 11444/11713/10605\r\nf 11426/11695/10587 11445/11714/10606 11443/11712/10604 11427/11696/10588\r\nf 11446/11715/10607 11447/11716/10608 11443/11712/10604 11445/11714/10606\r\nf 11448/11717/10609 11444/11713/10605 11443/11712/10604 11447/11716/10608\r\nf 11426/11695/10587 11432/11701/10593 11449/11718/10610 11445/11714/10606\r\nf 11431/11700/10592 11450/11719/10611 11449/11718/10610 11432/11701/10593\r\nf 11451/11720/10612 11452/11721/10613 11449/11718/10610 11450/11719/10611\r\nf 11446/11715/10607 11445/11714/10606 11449/11718/10610 11452/11721/10613\r\nf 11438/11707/10599 11437/11706/10598 11453/11722/10614 11454/11723/10615\r\nf 11436/11705/10597 11455/11724/10616 11453/11722/10614 11437/11706/10598\r\nf 11456/11725/10617 11457/11726/10618 11453/11722/10614 11455/11724/10616\r\nf 11458/11727/10619 11454/11723/10615 11453/11722/10614 11457/11726/10618\r\nf 11436/11705/10597 11442/11711/10603 11459/11728/10620 11455/11724/10616\r\nf 11441/11710/10602 11460/11729/10621 11459/11728/10620 11442/11711/10603\r\nf 11461/11730/10622 11462/11731/10623 11459/11728/10620 11460/11729/10621\r\nf 11456/11725/10617 11455/11724/10616 11459/11728/10620 11462/11731/10623\r\nf 11448/11717/10609 11447/11716/10608 11463/11732/10624 11464/11733/10625\r\nf 11446/11715/10607 11465/11734/10626 11463/11732/10624 11447/11716/10608\r\nf 11466/11735/10627 11467/11736/10628 11463/11732/10624 11465/11734/10626\r\nf 11468/11737/10629 11464/11733/10625 11463/11732/10624 11467/11736/10628\r\nf 11446/11715/10607 11452/11721/10613 11469/11738/10630 11465/11734/10626\r\nf 11451/11720/10612 11470/11739/10631 11469/11738/10630 11452/11721/10613\r\nf 11471/11740/10632 11472/11741/10633 11469/11738/10630 11470/11739/10631\r\nf 11466/11735/10627 11465/11734/10626 11469/11738/10630 11472/11741/10633\r\nf 11458/11727/10619 11457/11726/10618 11473/11742/10634 11474/11743/10635\r\nf 11456/11725/10617 11475/11744/10636 11473/11742/10634 11457/11726/10618\r\nf 11476/11745/10637 11477/11746/10638 11473/11742/10634 11475/11744/10636\r\nf 11478/11747/10639 11474/11743/10635 11473/11742/10634 11477/11746/10638\r\nf 11456/11725/10617 11462/11731/10623 11479/11748/10640 11475/11744/10636\r\nf 11461/11730/10622 11480/11749/10641 11479/11748/10640 11462/11731/10623\r\nf 11481/11750/10642 11482/11751/10643 11479/11748/10640 11480/11749/10641\r\nf 11476/11745/10637 11475/11744/10636 11479/11748/10640 11482/11751/10643\r\nf 11468/11737/10629 11467/11736/10628 11483/11752/10644 11484/11753/10645\r\nf 11466/11735/10627 11485/11754/10646 11483/11752/10644 11467/11736/10628\r\nf 11486/11755/10647 11487/11756/10648 11483/11752/10644 11485/11754/10646\r\nf 11488/11757/10649 11484/11753/10645 11483/11752/10644 11487/11756/10648\r\nf 11466/11735/10627 11472/11741/10633 11489/11758/10650 11485/11754/10646\r\nf 11471/11740/10632 11490/11759/10651 11489/11758/10650 11472/11741/10633\r\nf 11491/11760/10652 11492/11761/10653 11489/11758/10650 11490/11759/10651\r\nf 11486/11755/10647 11485/11754/10646 11489/11758/10650 11492/11761/10653\r\nf 11478/11747/10639 11477/11746/10638 11493/11762/10654 11494/11763/10655\r\nf 11476/11745/10637 11495/11764/10656 11493/11762/10654 11477/11746/10638\r\nf 11496/11765/10657 11497/11766/10658 11493/11762/10654 11495/11764/10656\r\nf 11498/11767/10659 11494/11763/10655 11493/11762/10654 11497/11766/10658\r\nf 11476/11745/10637 11482/11751/10643 11499/11768/10660 11495/11764/10656\r\nf 11481/11750/10642 11500/11769/10661 11499/11768/10660 11482/11751/10643\r\nf 11501/11770/10662 11502/11771/10663 11499/11768/10660 11500/11769/10661\r\nf 11496/11765/10657 11495/11764/10656 11499/11768/10660 11502/11771/10663\r\nf 11488/11757/10649 11487/11756/10648 11503/11772/10664 11504/11773/10665\r\nf 11486/11755/10647 11505/11774/10666 11503/11772/10664 11487/11756/10648\r\nf 11506/11775/10667 11507/11776/10668 11503/11772/10664 11505/11774/10666\r\nf 11508/11777/10669 11504/11773/10665 11503/11772/10664 11507/11776/10668\r\nf 11486/11755/10647 11492/11761/10653 11509/11778/10670 11505/11774/10666\r\nf 11491/11760/10652 11510/11779/10671 11509/11778/10670 11492/11761/10653\r\nf 11511/11780/10672 11512/11781/10673 11509/11778/10670 11510/11779/10671\r\nf 11506/11775/10667 11505/11774/10666 11509/11778/10670 11512/11781/10673\r\nf 11498/11767/10659 11497/11766/10658 11513/11782/10674 11514/11783/10675\r\nf 11496/11765/10657 11515/11784/10676 11513/11782/10674 11497/11766/10658\r\nf 11516/11785/10677 11517/11786/10678 11513/11782/10674 11515/11784/10676\r\nf 11518/11787/10679 11514/11783/10675 11513/11782/10674 11517/11786/10678\r\nf 11496/11765/10657 11502/11771/10663 11519/11788/10680 11515/11784/10676\r\nf 11501/11770/10662 11520/11789/10681 11519/11788/10680 11502/11771/10663\r\nf 11521/11790/10682 11522/11791/10683 11519/11788/10680 11520/11789/10681\r\nf 11516/11785/10677 11515/11784/10676 11519/11788/10680 11522/11791/10683\r\nf 11508/11777/10669 11507/11776/10668 11523/11792/10684 11524/11793/10685\r\nf 11506/11775/10667 11525/11794/10686 11523/11792/10684 11507/11776/10668\r\nf 11526/11795/10687 11527/11796/10688 11523/11792/10684 11525/11794/10686\r\nf 11528/11797/10689 11524/11793/10685 11523/11792/10684 11527/11796/10688\r\nf 11506/11775/10667 11512/11781/10673 11529/11798/10690 11525/11794/10686\r\nf 11511/11780/10672 11530/11799/10691 11529/11798/10690 11512/11781/10673\r\nf 11531/11800/10692 11532/11801/10693 11529/11798/10690 11530/11799/10691\r\nf 11526/11795/10687 11525/11794/10686 11529/11798/10690 11532/11801/10693\r\nf 11518/11787/10679 11517/11786/10678 11533/11802/10694 11534/11803/10695\r\nf 11516/11785/10677 11535/11804/10696 11533/11802/10694 11517/11786/10678\r\nf 11536/11805/10697 11537/11806/10698 11533/11802/10694 11535/11804/10696\r\nf 11538/11807/10699 11534/11803/10695 11533/11802/10694 11537/11806/10698\r\nf 11516/11785/10677 11522/11791/10683 11539/11808/10700 11535/11804/10696\r\nf 11521/11790/10682 11540/11809/10701 11539/11808/10700 11522/11791/10683\r\nf 11541/11810/10702 11542/11811/10703 11539/11808/10700 11540/11809/10701\r\nf 11536/11805/10697 11535/11804/10696 11539/11808/10700 11542/11811/10703\r\nf 11528/11797/10689 11527/11796/10688 11543/11812/10704 11544/11813/10705\r\nf 11526/11795/10687 11545/11814/10706 11543/11812/10704 11527/11796/10688\r\nf 11546/11815/10707 11547/11816/10708 11543/11812/10704 11545/11814/10706\r\nf 11548/11817/10709 11544/11813/10705 11543/11812/10704 11547/11816/10708\r\nf 11526/11795/10687 11532/11801/10693 11549/11818/10710 11545/11814/10706\r\nf 11531/11800/10692 11550/11819/10711 11549/11818/10710 11532/11801/10693\r\nf 11551/11820/10712 11552/11821/10713 11549/11818/10710 11550/11819/10711\r\nf 11546/11815/10707 11545/11814/10706 11549/11818/10710 11552/11821/10713\r\nf 11538/11807/10699 11537/11806/10698 11553/11822/10714 11554/11823/10715\r\nf 11536/11805/10697 11555/11824/10716 11553/11822/10714 11537/11806/10698\r\nf 11556/11825/10717 11557/11826/10718 11553/11822/10714 11555/11824/10716\r\nf 11558/11827/10719 11554/11823/10715 11553/11822/10714 11557/11826/10718\r\nf 11536/11805/10697 11542/11811/10703 11559/11828/10720 11555/11824/10716\r\nf 11541/11810/10702 11560/11829/10721 11559/11828/10720 11542/11811/10703\r\nf 11561/11830/10722 11562/11831/10723 11559/11828/10720 11560/11829/10721\r\nf 11556/11825/10717 11555/11824/10716 11559/11828/10720 11562/11831/10723\r\nf 11548/11817/10709 11547/11816/10708 11563/11832/10724 11564/11833/10725\r\nf 11546/11815/10707 11565/11834/10726 11563/11832/10724 11547/11816/10708\r\nf 11566/11835/10727 11567/11836/10728 11563/11832/10724 11565/11834/10726\r\nf 11568/11837/10729 11564/11833/10725 11563/11832/10724 11567/11836/10728\r\nf 11546/11815/10707 11552/11821/10713 11569/11838/10730 11565/11834/10726\r\nf 11551/11820/10712 11570/11839/10731 11569/11838/10730 11552/11821/10713\r\nf 11571/11840/10732 11572/11841/10733 11569/11838/10730 11570/11839/10731\r\nf 11566/11835/10727 11565/11834/10726 11569/11838/10730 11572/11841/10733\r\nf 11558/11827/10719 11557/11826/10718 11573/11842/10734 11574/11843/10735\r\nf 11556/11825/10717 11575/11844/10736 11573/11842/10734 11557/11826/10718\r\nf 11576/11845/10737 11577/11846/10738 11573/11842/10734 11575/11844/10736\r\nf 11578/11847/10739 11574/11843/10735 11573/11842/10734 11577/11846/10738\r\nf 11556/11825/10717 11562/11831/10723 11579/11848/10740 11575/11844/10736\r\nf 11561/11830/10722 11580/11849/10741 11579/11848/10740 11562/11831/10723\r\nf 11581/11850/10742 11582/11851/10743 11579/11848/10740 11580/11849/10741\r\nf 11576/11845/10737 11575/11844/10736 11579/11848/10740 11582/11851/10743\r\nf 11568/11837/10729 11567/11836/10728 11583/11852/10744 11584/11853/10745\r\nf 11566/11835/10727 11585/11854/10746 11583/11852/10744 11567/11836/10728\r\nf 11586/11855/10747 11587/11856/10748 11583/11852/10744 11585/11854/10746\r\nf 11588/11857/10749 11584/11853/10745 11583/11852/10744 11587/11856/10748\r\nf 11566/11835/10727 11572/11841/10733 11589/11858/10750 11585/11854/10746\r\nf 11571/11840/10732 11590/11859/10751 11589/11858/10750 11572/11841/10733\r\nf 11591/11860/10752 11592/11861/10753 11589/11858/10750 11590/11859/10751\r\nf 11586/11855/10747 11585/11854/10746 11589/11858/10750 11592/11861/10753\r\nf 11578/11847/10739 11577/11846/10738 11593/11862/10754 11594/11863/10755\r\nf 11576/11845/10737 11595/11864/10756 11593/11862/10754 11577/11846/10738\r\nf 11596/11865/10757 11597/11866/10758 11593/11862/10754 11595/11864/10756\r\nf 11598/11867/10759 11594/11863/10755 11593/11862/10754 11597/11866/10758\r\nf 11576/11845/10737 11582/11851/10743 11599/11868/10760 11595/11864/10756\r\nf 11581/11850/10742 11600/11869/10761 11599/11868/10760 11582/11851/10743\r\nf 11601/11870/10762 11602/11871/10763 11599/11868/10760 11600/11869/10761\r\nf 11596/11865/10757 11595/11864/10756 11599/11868/10760 11602/11871/10763\r\nf 11588/11857/10749 11587/11856/10748 11603/11872/10764 11604/11873/10765\r\nf 11586/11855/10747 11605/11874/10766 11603/11872/10764 11587/11856/10748\r\nf 11606/11875/10767 11607/11876/10768 11603/11872/10764 11605/11874/10766\r\nf 11608/11877/10769 11604/11873/10765 11603/11872/10764 11607/11876/10768\r\nf 11586/11855/10747 11592/11861/10753 11609/11878/10770 11605/11874/10766\r\nf 11591/11860/10752 11610/11879/10771 11609/11878/10770 11592/11861/10753\r\nf 11611/11880/10772 11612/11881/10773 11609/11878/10770 11610/11879/10771\r\nf 11606/11875/10767 11605/11874/10766 11609/11878/10770 11612/11881/10773\r\nf 11598/11867/10759 11597/11866/10758 11613/11882/10774 11614/11883/10775\r\nf 11596/11865/10757 11615/11884/10776 11613/11882/10774 11597/11866/10758\r\nf 11616/11885/10777 11617/11886/10778 11613/11882/10774 11615/11884/10776\r\nf 11618/11887/10779 11614/11883/10775 11613/11882/10774 11617/11886/10778\r\nf 11596/11865/10757 11602/11871/10763 11619/11888/10780 11615/11884/10776\r\nf 11601/11870/10762 11620/11889/10781 11619/11888/10780 11602/11871/10763\r\nf 11621/11890/10782 11622/11891/10783 11619/11888/10780 11620/11889/10781\r\nf 11616/11885/10777 11615/11884/10776 11619/11888/10780 11622/11891/10783\r\nf 11608/11877/10769 11607/11876/10768 11623/11892/10784 11624/11893/10785\r\nf 11606/11875/10767 11625/11894/10786 11623/11892/10784 11607/11876/10768\r\nf 11626/11895/10787 11627/11896/10788 11623/11892/10784 11625/11894/10786\r\nf 11628/11897/10789 11624/11893/10785 11623/11892/10784 11627/11896/10788\r\nf 11606/11875/10767 11612/11881/10773 11629/11898/10790 11625/11894/10786\r\nf 11611/11880/10772 11630/11899/10791 11629/11898/10790 11612/11881/10773\r\nf 11631/11900/10792 11632/11901/10793 11629/11898/10790 11630/11899/10791\r\nf 11626/11895/10787 11625/11894/10786 11629/11898/10790 11632/11901/10793\r\nf 11618/11887/10779 11617/11886/10778 11633/11902/10794 11634/11903/10795\r\nf 11616/11885/10777 11635/11904/10796 11633/11902/10794 11617/11886/10778\r\nf 11636/11905/10797 11637/11906/10798 11633/11902/10794 11635/11904/10796\r\nf 11638/11907/10799 11634/11903/10795 11633/11902/10794 11637/11906/10798\r\nf 11616/11885/10777 11622/11891/10783 11639/11908/10800 11635/11904/10796\r\nf 11621/11890/10782 11640/11909/10801 11639/11908/10800 11622/11891/10783\r\nf 11641/11910/10802 11642/11911/10803 11639/11908/10800 11640/11909/10801\r\nf 11636/11905/10797 11635/11904/10796 11639/11908/10800 11642/11911/10803\r\nf 11628/11897/10789 11627/11896/10788 11643/11912/10804 11644/11913/10805\r\nf 11626/11895/10787 11645/11914/10806 11643/11912/10804 11627/11896/10788\r\nf 11646/11915/10807 11647/11916/10808 11643/11912/10804 11645/11914/10806\r\nf 11648/11917/10809 11644/11913/10805 11643/11912/10804 11647/11916/10808\r\nf 11626/11895/10787 11632/11901/10793 11649/11918/10810 11645/11914/10806\r\nf 11631/11900/10792 11650/11919/10811 11649/11918/10810 11632/11901/10793\r\nf 11651/11920/10812 11652/11921/10813 11649/11918/10810 11650/11919/10811\r\nf 11646/11915/10807 11645/11914/10806 11649/11918/10810 11652/11921/10813\r\nf 11638/11907/10799 11637/11906/10798 11653/11922/10814 11654/11923/10815\r\nf 11636/11905/10797 11655/11924/10816 11653/11922/10814 11637/11906/10798\r\nf 11656/11925/10817 11657/11926/10818 11653/11922/10814 11655/11924/10816\r\nf 11658/11927/10819 11654/11923/10815 11653/11922/10814 11657/11926/10818\r\nf 11636/11905/10797 11642/11911/10803 11659/11928/10820 11655/11924/10816\r\nf 11641/11910/10802 11660/11929/10821 11659/11928/10820 11642/11911/10803\r\nf 11661/11930/10822 11662/11931/10823 11659/11928/10820 11660/11929/10821\r\nf 11656/11925/10817 11655/11924/10816 11659/11928/10820 11662/11931/10823\r\nf 11648/11917/10809 11647/11916/10808 11663/11932/10824 11664/11933/10825\r\nf 11646/11915/10807 11665/11934/10826 11663/11932/10824 11647/11916/10808\r\nf 11666/11935/10827 11667/11936/10828 11663/11932/10824 11665/11934/10826\r\nf 11668/11937/10829 11664/11933/10825 11663/11932/10824 11667/11936/10828\r\nf 11646/11915/10807 11652/11921/10813 11669/11938/10830 11665/11934/10826\r\nf 11651/11920/10812 11670/11939/10831 11669/11938/10830 11652/11921/10813\r\nf 11671/11940/10832 11672/11941/10833 11669/11938/10830 11670/11939/10831\r\nf 11666/11935/10827 11665/11934/10826 11669/11938/10830 11672/11941/10833\r\nf 11658/11927/10819 11657/11926/10818 11673/11942/10834 11674/11943/10835\r\nf 11656/11925/10817 11675/11944/10836 11673/11942/10834 11657/11926/10818\r\nf 11676/11945/10837 11677/11946/10838 11673/11942/10834 11675/11944/10836\r\nf 11678/11947/10839 11674/11943/10835 11673/11942/10834 11677/11946/10838\r\nf 11656/11925/10817 11662/11931/10823 11679/11948/10840 11675/11944/10836\r\nf 11661/11930/10822 11680/11949/10841 11679/11948/10840 11662/11931/10823\r\nf 11681/11950/10842 11682/11951/10843 11679/11948/10840 11680/11949/10841\r\nf 11676/11945/10837 11675/11944/10836 11679/11948/10840 11682/11951/10843\r\nf 11668/11937/10829 11667/11936/10828 11683/11952/10844 11684/11953/10845\r\nf 11666/11935/10827 11685/11954/10846 11683/11952/10844 11667/11936/10828\r\nf 11686/11955/10847 11687/11956/10848 11683/11952/10844 11685/11954/10846\r\nf 11688/11957/10849 11684/11953/10845 11683/11952/10844 11687/11956/10848\r\nf 11666/11935/10827 11672/11941/10833 11689/11958/10850 11685/11954/10846\r\nf 11671/11940/10832 11690/11959/10851 11689/11958/10850 11672/11941/10833\r\nf 11691/11960/10852 11692/11961/10853 11689/11958/10850 11690/11959/10851\r\nf 11686/11955/10847 11685/11954/10846 11689/11958/10850 11692/11961/10853\r\nf 11688/11957/10849 11687/11956/10848 11693/11962/10854 11694/11963/10855\r\nf 11686/11955/10847 11692/11961/10853 11693/11962/10854 11687/11956/10848\r\nf 11691/11960/10852 11695/11964/10856 11693/11962/10854 11692/11961/10853\r\nf 11696/11965/10857 11694/11963/10855 11693/11962/10854 11695/11964/10856\r\nf 11697/11966/8709 11698/11967/10858 11699/11968/10859 11700/11969/10860\r\nf 11701/11970/10861 11702/11971/10862 11699/11968/10859 11698/11967/10858\r\nf 9538/9807/8711 9541/9810/8714 11699/11968/10859 11702/11971/10862\r\nf 9546/9815/8719 11700/11969/10860 11699/11968/10859 9541/9810/8714\r\nf 9551/9820/8724 9550/9819/8723 11703/11972/8723 11704/11973/10863\r\nf 9549/9818/8722 11705/11974/8722 11703/11972/8723 9550/9819/8723\r\nf 10831/11100/9998 11706/11975/10864 11703/11976/8723 11705/11977/8722\r\nf 11707/11978/10863 11704/11979/10863 11703/11976/8723 11706/11975/10864\r\nf 11708/11980/10865 11709/11981/8732 11710/11982/10866 11711/11983/10867\r\nf 11697/11966/8709 11700/11969/10860 11710/11982/10866 11709/11981/8732\r\nf 9546/9815/8719 9564/9833/8735 11710/11982/10866 11700/11969/10860\r\nf 9568/9837/8739 11711/11983/10867 11710/11982/10866 9564/9833/8735\r\nf 9571/9840/8742 9570/9839/8741 11712/11984/8741 11713/11985/10868\r\nf 9551/9820/8724 11704/11973/10863 11712/11984/8741 9570/9839/8741\r\nf 11707/11978/10863 11714/11986/8741 11712/11987/8741 11704/11979/10863\r\nf 11715/11988/8742 11713/11989/10868 11712/11987/8741 11714/11986/8741\r\nf 11716/11990/8752 11717/11991/8751 11718/11992/10869 11719/11993/10870\r\nf 11708/11980/10865 11711/11983/10867 11718/11992/10869 11717/11991/8751\r\nf 9568/9837/8739 9584/9853/8755 11718/11992/10869 11711/11983/10867\r\nf 9588/9857/8759 11719/11993/10870 11718/11992/10869 9584/9853/8755\r\nf 9591/9860/8762 9590/9859/8761 11720/11994/8761 11721/11995/8762\r\nf 9571/9840/8742 11713/11985/10868 11720/11994/8761 9590/9859/8761\r\nf 11715/11988/8742 11722/11996/8761 11720/11997/8761 11713/11989/10868\r\nf 11723/11998/8762 11721/11999/8762 11720/11997/8761 11722/11996/8761\r\nf 11724/12000/8772 11725/12001/8771 11726/12002/10871 11727/12003/10872\r\nf 11716/11990/8752 11719/11993/10870 11726/12002/10871 11725/12001/8771\r\nf 9588/9857/8759 9604/9873/8775 11726/12002/10871 11719/11993/10870\r\nf 9608/9877/8779 11727/12003/10872 11726/12002/10871 9604/9873/8775\r\nf 9611/9880/8782 9610/9879/8781 11728/12004/8781 11729/12005/8782\r\nf 9591/9860/8762 11721/11995/8762 11728/12004/8781 9610/9879/8781\r\nf 11723/11998/8762 11730/12006/10873 11728/12007/8781 11721/11999/8762\r\nf 11731/12008/8782 11729/12009/8782 11728/12007/8781 11730/12006/10873\r\nf 11732/12010/8792 11733/12011/8791 11734/12012/10874 11735/12013/10875\r\nf 11724/12000/8772 11727/12003/10872 11734/12012/10874 11733/12011/8791\r\nf 9608/9877/8779 9624/9893/8795 11734/12012/10874 11727/12003/10872\r\nf 9628/9897/8799 11735/12013/10875 11734/12012/10874 9624/9893/8795\r\nf 9631/9900/8802 9630/9899/8801 11736/12014/8801 11737/12015/8802\r\nf 9611/9880/8782 11729/12005/8782 11736/12014/8801 9630/9899/8801\r\nf 11731/12008/8782 11738/12016/8801 11736/12017/8801 11729/12009/8782\r\nf 11739/12018/8802 11737/12019/8802 11736/12017/8801 11738/12016/8801\r\nf 11740/12020/8812 11741/12021/8811 11742/12022/10876 11743/12023/10877\r\nf 11732/12010/8792 11735/12013/10875 11742/12022/10876 11741/12021/8811\r\nf 9628/9897/8799 9644/9913/8815 11742/12022/10876 11735/12013/10875\r\nf 9648/9917/8819 11743/12023/10877 11742/12022/10876 9644/9913/8815\r\nf 9651/9920/8822 9650/9919/8821 11744/12024/8821 11745/12025/8822\r\nf 9631/9900/8802 11737/12015/8802 11744/12024/8821 9650/9919/8821\r\nf 11739/12018/8802 11746/12026/8821 11744/12027/8821 11737/12019/8802\r\nf 11747/12028/8822 11745/12029/8822 11744/12027/8821 11746/12026/8821\r\nf 11748/12030/8832 11749/12031/10878 11750/12032/10879 11751/12033/10880\r\nf 11740/12020/8812 11743/12023/10877 11750/12032/10879 11749/12031/10878\r\nf 9648/9917/8819 9664/9933/8835 11750/12032/10879 11743/12023/10877\r\nf 9668/9937/8839 11751/12033/10880 11750/12032/10879 9664/9933/8835\r\nf 9671/9940/8842 9670/9939/8841 11752/12034/10881 11753/12035/8842\r\nf 9651/9920/8822 11745/12025/8822 11752/12034/10881 9670/9939/8841\r\nf 11747/12028/8822 11754/12036/10881 11752/12037/10881 11745/12029/8822\r\nf 11755/12038/8842 11753/12039/8842 11752/12037/10881 11754/12036/10881\r\nf 11756/12040/10882 11757/12041/8851 11758/12042/10883 11759/12043/10884\r\nf 11748/12030/8832 11751/12033/10880 11758/12042/10883 11757/12041/8851\r\nf 9668/9937/8839 9684/9953/8855 11758/12042/10883 11751/12033/10880\r\nf 9688/9957/8859 11759/12043/10884 11758/12042/10883 9684/9953/8855\r\nf 9691/9960/8862 9690/9959/8861 11760/12044/8861 11761/12045/10885\r\nf 9671/9940/8842 11753/12035/8842 11760/12044/8861 9690/9959/8861\r\nf 11755/12038/8842 11762/12046/10886 11760/12047/8861 11753/12039/8842\r\nf 11763/12048/10885 11761/12049/10885 11760/12047/8861 11762/12046/10886\r\nf 11764/12050/8872 11765/12051/10887 11766/12052/10888 11767/12053/10889\r\nf 11756/12040/10882 11759/12043/10884 11766/12052/10888 11765/12051/10887\r\nf 9688/9957/8859 9704/9973/8875 11766/12052/10888 11759/12043/10884\r\nf 9708/9977/8879 11767/12053/10889 11766/12052/10888 9704/9973/8875\r\nf 9711/9980/8882 9710/9979/8881 11768/12054/8881 11769/12055/8882\r\nf 9691/9960/8862 11761/12045/10885 11768/12054/8881 9710/9979/8881\r\nf 11763/12048/10885 11770/12056/8881 11768/12057/8881 11761/12049/10885\r\nf 11771/12058/8882 11769/12059/8882 11768/12057/8881 11770/12056/8881\r\nf 11772/12060/8892 11773/12061/10890 11774/12062/10891 11775/12063/10892\r\nf 11764/12050/8872 11767/12053/10889 11774/12062/10891 11773/12061/10890\r\nf 9708/9977/8879 9724/9993/8895 11774/12062/10891 11767/12053/10889\r\nf 9728/9997/8899 11775/12063/10892 11774/12062/10891 9724/9993/8895\r\nf 9731/10000/8902 9730/9999/8901 11776/12064/8901 11777/12065/8902\r\nf 9711/9980/8882 11769/12055/8882 11776/12064/8901 9730/9999/8901\r\nf 11771/12058/8882 11778/12066/10893 11776/12067/8901 11769/12059/8882\r\nf 11779/12068/8902 11777/12069/8902 11776/12067/8901 11778/12066/10893\r\nf 11780/12070/8912 11781/12071/10894 11782/12072/10895 11783/12073/10896\r\nf 11772/12060/8892 11775/12063/10892 11782/12072/10895 11781/12071/10894\r\nf 9728/9997/8899 9744/10013/8915 11782/12072/10895 11775/12063/10892\r\nf 9748/10017/8919 11783/12073/10896 11782/12072/10895 9744/10013/8915\r\nf 9751/10020/8922 9750/10019/8921 11784/12074/10897 11785/12075/8922\r\nf 9731/10000/8902 11777/12065/8902 11784/12074/10897 9750/10019/8921\r\nf 11779/12068/8902 11786/12076/10897 11784/12077/10897 11777/12069/8902\r\nf 11787/12078/8922 11785/12079/8922 11784/12077/10897 11786/12076/10897\r\nf 11788/12080/8932 11789/12081/8931 11790/12082/10898 11791/12083/10899\r\nf 11780/12070/8912 11783/12073/10896 11790/12082/10898 11789/12081/8931\r\nf 9748/10017/8919 9764/10033/8935 11790/12082/10898 11783/12073/10896\r\nf 9768/10037/8939 11791/12083/10899 11790/12082/10898 9764/10033/8935\r\nf 9771/10040/8942 9770/10039/8941 11792/12084/8941 11793/12085/10900\r\nf 9751/10020/8922 11785/12075/8922 11792/12084/8941 9770/10039/8941\r\nf 11787/12078/8922 11794/12086/8941 11792/12087/8941 11785/12079/8922\r\nf 11795/12088/8942 11793/12089/10900 11792/12087/8941 11794/12086/8941\r\nf 11796/12090/8952 11797/12091/8951 11798/12092/10901 11799/12093/10902\r\nf 11788/12080/8932 11791/12083/10899 11798/12092/10901 11797/12091/8951\r\nf 9768/10037/8939 9784/10053/8955 11798/12092/10901 11791/12083/10899\r\nf 9788/10057/8959 11799/12093/10902 11798/12092/10901 9784/10053/8955\r\nf 9791/10060/8962 9790/10059/8961 11800/12094/8961 11801/12095/8962\r\nf 9771/10040/8942 11793/12085/10900 11800/12094/8961 9790/10059/8961\r\nf 11795/12088/8942 11802/12096/8961 11800/12097/8961 11793/12089/10900\r\nf 11803/12098/8962 11801/12099/8962 11800/12097/8961 11802/12096/8961\r\nf 11804/12100/8972 11805/12101/8971 11806/12102/10903 11807/12103/10904\r\nf 11796/12090/8952 11799/12093/10902 11806/12102/10903 11805/12101/8971\r\nf 9788/10057/8959 9804/10073/8975 11806/12102/10903 11799/12093/10902\r\nf 9808/10077/8979 11807/12103/10904 11806/12102/10903 9804/10073/8975\r\nf 9811/10080/8982 9810/10079/8981 11808/12104/10905 11809/12105/8982\r\nf 9791/10060/8962 11801/12095/8962 11808/12104/10905 9810/10079/8981\r\nf 11803/12098/8962 11810/12106/10905 11808/12107/10905 11801/12099/8962\r\nf 11811/12108/8982 11809/12109/8982 11808/12107/10905 11810/12106/10905\r\nf 11812/12110/8992 11813/12111/8991 11814/12112/10906 11815/12113/10907\r\nf 11804/12100/8972 11807/12103/10904 11814/12112/10906 11813/12111/8991\r\nf 9808/10077/8979 9824/10093/8995 11814/12112/10906 11807/12103/10904\r\nf 9828/10097/8999 11815/12113/10907 11814/12112/10906 9824/10093/8995\r\nf 9831/10100/9002 9830/10099/9001 11816/12114/9001 11817/12115/9002\r\nf 9811/10080/8982 11809/12105/8982 11816/12114/9001 9830/10099/9001\r\nf 11811/12108/8982 11818/12116/9001 11816/12117/9001 11809/12109/8982\r\nf 11819/12118/9002 11817/12119/9002 11816/12117/9001 11818/12116/9001\r\nf 11820/12120/9012 11821/12121/10908 11822/12122/10909 11823/12123/10910\r\nf 11812/12110/8992 11815/12113/10907 11822/12122/10909 11821/12121/10908\r\nf 9828/10097/8999 9844/10113/9015 11822/12122/10909 11815/12113/10907\r\nf 9848/10117/9019 11823/12123/10910 11822/12122/10909 9844/10113/9015\r\nf 9851/10120/9022 9850/10119/9021 11824/12124/9021 11825/12125/9022\r\nf 9831/10100/9002 11817/12115/9002 11824/12124/9021 9850/10119/9021\r\nf 11819/12118/9002 11826/12126/9021 11824/12127/9021 11817/12119/9002\r\nf 11827/12128/9022 11825/12129/9022 11824/12127/9021 11826/12126/9021\r\nf 11828/12130/9032 11829/12131/9031 11830/12132/10911 11831/12133/10912\r\nf 11820/12120/9012 11823/12123/10910 11830/12132/10911 11829/12131/9031\r\nf 9848/10117/9019 9864/10133/9035 11830/12132/10911 11823/12123/10910\r\nf 9868/10137/9039 11831/12133/10912 11830/12132/10911 9864/10133/9035\r\nf 9871/10140/9042 9870/10139/9041 11832/12134/9041 11833/12135/9042\r\nf 9851/10120/9022 11825/12125/9022 11832/12134/9041 9870/10139/9041\r\nf 11827/12128/9022 11834/12136/9041 11832/12137/9041 11825/12129/9022\r\nf 11835/12138/9042 11833/12139/9042 11832/12137/9041 11834/12136/9041\r\nf 11836/12140/9052 11837/12141/9051 11838/12142/10913 11839/12143/10914\r\nf 11828/12130/9032 11831/12133/10912 11838/12142/10913 11837/12141/9051\r\nf 9868/10137/9039 9884/10153/9055 11838/12142/10913 11831/12133/10912\r\nf 9888/10157/9059 11839/12143/10914 11838/12142/10913 9884/10153/9055\r\nf 9891/10160/9062 9890/10159/9061 11840/12144/9061 11841/12145/9062\r\nf 9871/10140/9042 11833/12135/9042 11840/12144/9061 9890/10159/9061\r\nf 11835/12138/9042 11842/12146/9061 11840/12147/9061 11833/12139/9042\r\nf 11843/12148/9062 11841/12149/9062 11840/12147/9061 11842/12146/9061\r\nf 11844/12150/9071 11845/12151/9070 11846/12152/10915 11847/12153/10916\r\nf 11836/12140/9052 11839/12143/10914 11846/12152/10915 11845/12151/9070\r\nf 9888/10157/9059 9904/10173/9074 11846/12152/10915 11839/12143/10914\r\nf 9908/10177/9077 11847/12153/10916 11846/12152/10915 9904/10173/9074\r\nf 9911/10180/9080 9910/10179/9079 11848/12154/9079 11849/12155/9080\r\nf 9891/10160/9062 11841/12145/9062 11848/12154/9079 9910/10179/9079\r\nf 11843/12148/9062 11850/12156/9079 11848/12157/9079 11841/12149/9062\r\nf 11851/12158/9080 11849/12159/9080 11848/12157/9079 11850/12156/9079\r\nf 11852/12160/9090 11853/12161/9089 11854/12162/10917 11855/12163/10918\r\nf 11844/12150/9071 11847/12153/10916 11854/12162/10917 11853/12161/9089\r\nf 9908/10177/9077 9924/10193/9093 11854/12162/10917 11847/12153/10916\r\nf 9928/10197/9097 11855/12163/10918 11854/12162/10917 9924/10193/9093\r\nf 9931/10200/9100 9930/10199/9099 11856/12164/9099 11857/12165/9100\r\nf 9911/10180/9080 11849/12155/9080 11856/12164/9099 9930/10199/9099\r\nf 11851/12158/9080 11858/12166/9099 11856/12167/9099 11849/12159/9080\r\nf 11859/12168/9100 11857/12169/9100 11856/12167/9099 11858/12166/9099\r\nf 11860/12170/9110 11861/12171/9109 11862/12172/10919 11863/12173/10920\r\nf 11852/12160/9090 11855/12163/10918 11862/12172/10919 11861/12171/9109\r\nf 9928/10197/9097 9944/10213/9113 11862/12172/10919 11855/12163/10918\r\nf 9948/10217/9117 11863/12173/10920 11862/12172/10919 9944/10213/9113\r\nf 9951/10220/9120 9950/10219/9119 11864/12174/9119 11865/12175/9120\r\nf 9931/10200/9100 11857/12165/9100 11864/12174/9119 9950/10219/9119\r\nf 11859/12168/9100 11866/12176/9119 11864/12177/9119 11857/12169/9100\r\nf 11867/12178/9120 11865/12179/9120 11864/12177/9119 11866/12176/9119\r\nf 11868/12180/9130 11869/12181/9129 11870/12182/10921 11871/12183/10922\r\nf 11860/12170/9110 11863/12173/10920 11870/12182/10921 11869/12181/9129\r\nf 9948/10217/9117 9964/10233/9133 11870/12182/10921 11863/12173/10920\r\nf 9968/10237/9137 11871/12183/10922 11870/12182/10921 9964/10233/9133\r\nf 9971/10240/9140 9970/10239/9139 11872/12184/9139 11873/12185/9140\r\nf 9951/10220/9120 11865/12175/9120 11872/12184/9139 9970/10239/9139\r\nf 11867/12178/9120 11874/12186/9139 11872/12187/9139 11865/12179/9120\r\nf 11875/12188/9140 11873/12189/9140 11872/12187/9139 11874/12186/9139\r\nf 11876/12190/9150 11877/12191/9149 11878/12192/10923 11879/12193/10924\r\nf 11868/12180/9130 11871/12183/10922 11878/12192/10923 11877/12191/9149\r\nf 9968/10237/9137 9984/10253/9153 11878/12192/10923 11871/12183/10922\r\nf 9988/10257/9157 11879/12193/10924 11878/12192/10923 9984/10253/9153\r\nf 9991/10260/9160 9990/10259/9159 11880/12194/9159 11881/12195/9160\r\nf 9971/10240/9140 11873/12185/9140 11880/12194/9159 9990/10259/9159\r\nf 11875/12188/9140 11882/12196/9159 11880/12197/9159 11873/12189/9140\r\nf 11883/12198/9160 11881/12199/9160 11880/12197/9159 11882/12196/9159\r\nf 11884/12200/9170 11885/12201/9169 11886/12202/10925 11887/12203/10926\r\nf 11876/12190/9150 11879/12193/10924 11886/12202/10925 11885/12201/9169\r\nf 9988/10257/9157 10004/10273/9173 11886/12202/10925 11879/12193/10924\r\nf 10008/10277/9177 11887/12203/10926 11886/12202/10925 10004/10273/9173\r\nf 10011/10280/9180 10010/10279/9179 11888/12204/9179 11889/12205/9180\r\nf 9991/10260/9160 11881/12195/9160 11888/12204/9179 10010/10279/9179\r\nf 11883/12198/9160 11890/12206/9179 11888/12207/9179 11881/12199/9160\r\nf 11891/12208/9180 11889/12209/9180 11888/12207/9179 11890/12206/9179\r\nf 11892/12210/9190 11893/12211/9189 11894/12212/10927 11895/12213/10928\r\nf 11884/12200/9170 11887/12203/10926 11894/12212/10927 11893/12211/9189\r\nf 10008/10277/9177 10024/10293/9193 11894/12212/10927 11887/12203/10926\r\nf 10028/10297/9197 11895/12213/10928 11894/12212/10927 10024/10293/9193\r\nf 10031/10300/9200 10030/10299/9199 11896/12214/9199 11897/12215/9200\r\nf 10011/10280/9180 11889/12205/9180 11896/12214/9199 10030/10299/9199\r\nf 11891/12208/9180 11898/12216/9199 11896/12217/9199 11889/12209/9180\r\nf 11899/12218/9200 11897/12219/9200 11896/12217/9199 11898/12216/9199\r\nf 11900/12220/9210 11901/12221/9209 11902/12222/10929 11903/12223/10930\r\nf 11892/12210/9190 11895/12213/10928 11902/12222/10929 11901/12221/9209\r\nf 10028/10297/9197 10044/10313/9213 11902/12222/10929 11895/12213/10928\r\nf 10048/10317/9217 11903/12223/10930 11902/12222/10929 10044/10313/9213\r\nf 10051/10320/9220 10050/10319/9219 11904/12224/9219 11905/12225/9220\r\nf 10031/10300/9200 11897/12215/9200 11904/12224/9219 10050/10319/9219\r\nf 11899/12218/9200 11906/12226/9219 11904/12227/9219 11897/12219/9200\r\nf 11907/12228/9220 11905/12229/9220 11904/12227/9219 11906/12226/9219\r\nf 11908/12230/9230 11909/12231/9229 11910/12232/10931 11911/12233/10932\r\nf 11900/12220/9210 11903/12223/10930 11910/12232/10931 11909/12231/9229\r\nf 10048/10317/9217 10064/10333/9233 11910/12232/10931 11903/12223/10930\r\nf 10068/10337/9237 11911/12233/10932 11910/12232/10931 10064/10333/9233\r\nf 10071/10340/9240 10070/10339/9239 11912/12234/9239 11913/12235/9240\r\nf 10051/10320/9220 11905/12225/9220 11912/12234/9239 10070/10339/9239\r\nf 11907/12228/9220 11914/12236/9239 11912/12237/9239 11905/12229/9220\r\nf 11915/12238/9240 11913/12239/9240 11912/12237/9239 11914/12236/9239\r\nf 11916/12240/9250 11917/12241/9249 11918/12242/10933 11919/12243/10934\r\nf 11908/12230/9230 11911/12233/10932 11918/12242/10933 11917/12241/9249\r\nf 10068/10337/9237 10084/10353/9253 11918/12242/10933 11911/12233/10932\r\nf 10088/10357/9257 11919/12243/10934 11918/12242/10933 10084/10353/9253\r\nf 10091/10360/9260 10090/10359/9259 11920/12244/9259 11921/12245/9260\r\nf 10071/10340/9240 11913/12235/9240 11920/12244/9259 10090/10359/9259\r\nf 11915/12238/9240 11922/12246/9259 11920/12247/9259 11913/12239/9240\r\nf 11923/12248/9260 11921/12249/9260 11920/12247/9259 11922/12246/9259\r\nf 11924/12250/9270 11925/12251/9269 11926/12252/10935 11927/12253/10936\r\nf 11916/12240/9250 11919/12243/10934 11926/12252/10935 11925/12251/9269\r\nf 10088/10357/9257 10104/10373/9273 11926/12252/10935 11919/12243/10934\r\nf 10108/10377/9277 11927/12253/10936 11926/12252/10935 10104/10373/9273\r\nf 10111/10380/9280 10110/10379/9279 11928/12254/10937 11929/12255/9280\r\nf 10091/10360/9260 11921/12245/9260 11928/12254/10937 10110/10379/9279\r\nf 11923/12248/9260 11930/12256/10937 11928/12257/10937 11921/12249/9260\r\nf 11931/12258/10938 11929/12259/9280 11928/12257/10937 11930/12256/10937\r\nf 11932/12260/9290 11933/12261/10939 11934/12262/10940 11935/12263/10941\r\nf 11924/12250/9270 11927/12253/10936 11934/12262/10940 11933/12261/10939\r\nf 10108/10377/9277 10124/10393/9293 11934/12262/10940 11927/12253/10936\r\nf 10128/10397/9297 11935/12263/10941 11934/12262/10940 10124/10393/9293\r\nf 10131/10400/9300 10130/10399/9299 11936/12264/10942 11937/12265/9300\r\nf 10111/10380/9280 11929/12255/9280 11936/12264/10942 10130/10399/9299\r\nf 11931/12258/10938 11938/12266/10942 11936/12267/10942 11929/12259/9280\r\nf 11939/12268/9300 11937/12269/9300 11936/12267/10942 11938/12266/10942\r\nf 11940/12270/10943 11941/12271/9309 11942/12272/10944 11943/12273/10945\r\nf 11932/12260/9290 11935/12263/10941 11942/12272/10944 11941/12271/9309\r\nf 10128/10397/9297 10144/10413/9313 11942/12272/10944 11935/12263/10941\r\nf 10148/10417/9317 11943/12273/10945 11942/12272/10944 10144/10413/9313\r\nf 10151/10420/9320 10150/10419/9319 11944/12274/9319 11945/12275/10946\r\nf 10131/10400/9300 11937/12265/9300 11944/12274/9319 10150/10419/9319\r\nf 11939/12268/9300 11946/12276/9319 11944/12277/9319 11937/12269/9300\r\nf 11947/12278/10947 11945/12279/10946 11944/12277/9319 11946/12276/9319\r\nf 11948/12280/10948 11949/12281/10949 11950/12282/10950 11951/12283/10951\r\nf 11940/12270/10943 11943/12273/10945 11950/12282/10950 11949/12281/10949\r\nf 10148/10417/9317 10164/10433/9333 11950/12282/10950 11943/12273/10945\r\nf 10168/10437/9337 11951/12283/10951 11950/12282/10950 10164/10433/9333\r\nf 10171/10440/9340 10170/10439/9339 11952/12284/9339 11953/12285/10952\r\nf 10151/10420/9320 11945/12275/10946 11952/12284/9339 10170/10439/9339\r\nf 11947/12278/10947 11954/12286/10953 11952/12287/9339 11945/12279/10946\r\nf 11955/12288/10954 11953/12289/10952 11952/12287/9339 11954/12286/10953\r\nf 11956/12290/10955 11957/12291/10956 11958/12292/10957 11959/12293/10958\r\nf 11948/12280/10948 11951/12283/10951 11958/12292/10957 11957/12291/10956\r\nf 10168/10437/9337 10186/10455/9355 11958/12292/10957 11951/12283/10951\r\nf 10190/10459/9359 11959/12293/10958 11958/12292/10957 10186/10455/9355\r\nf 10193/10462/9362 10192/10461/9361 11960/12294/10959 11961/12295/10960\r\nf 10171/10440/9340 11953/12285/10952 11960/12294/10959 10192/10461/9361\r\nf 11955/12288/10954 11962/12296/10961 11960/12297/10959 11953/12289/10952\r\nf 11963/12298/10962 11961/12299/10960 11960/12297/10959 11962/12296/10961\r\nf 10209/10478/9378 10208/10477/9377 11964/12300/10963 11965/12301/9378\r\nf 10193/10462/9362 11961/12295/10960 11964/12300/10963 10208/10477/9377\r\nf 11963/12298/10962 11966/12302/10964 11964/12303/10963 11961/12299/10960\r\nf 11967/12304/9378 11965/12305/9378 11964/12303/10963 11966/12302/10964\r\nf 10225/10494/9394 10224/10493/9393 11968/12306/10965 11969/12307/10966\r\nf 10209/10478/9378 11965/12301/9378 11968/12306/10965 10224/10493/9393\r\nf 11967/12304/9378 11970/12308/10967 11968/12309/10965 11965/12305/9378\r\nf 11971/12310/10968 11969/12311/10966 11968/12309/10965 11970/12308/10967\r\nf 11972/12312/10969 11973/12313/10970 11974/12314/10971 11975/12315/10972\r\nf 11976/12316/10973 11977/12317/10974 11974/12314/10971 11973/12313/10970\r\nf 10222/10491/9391 10240/10509/9409 11974/12314/10971 11977/12317/10974\r\nf 10244/10513/9413 11975/12315/10972 11974/12314/10971 10240/10509/9409\r\nf 10247/10516/9416 10246/10515/9415 11978/12318/10975 11979/12319/10976\r\nf 10225/10494/9394 11969/12307/10966 11978/12318/10975 10246/10515/9415\r\nf 11971/12310/10968 11980/12320/10977 11978/12321/10975 11969/12311/10966\r\nf 11981/12322/10978 11979/12323/10976 11978/12321/10975 11980/12320/10977\r\nf 11982/12324/10979 11983/12325/10980 11984/12326/10981 11985/12327/10982\r\nf 11972/12312/10969 11975/12315/10972 11984/12326/10981 11983/12325/10980\r\nf 10244/10513/9413 10260/10529/9429 11984/12326/10981 11975/12315/10972\r\nf 10264/10533/9433 11985/12327/10982 11984/12326/10981 10260/10529/9429\r\nf 10267/10536/9436 10266/10535/9435 11986/12328/10983 11987/12329/10984\r\nf 10247/10516/9416 11979/12319/10976 11986/12328/10983 10266/10535/9435\r\nf 11981/12322/10978 11988/12330/10985 11986/12331/10983 11979/12323/10976\r\nf 11989/12332/10984 11987/12333/10984 11986/12331/10983 11988/12330/10985\r\nf 11990/12334/10986 11991/12335/10987 11992/12336/10988 11993/12337/10989\r\nf 11982/12324/10979 11985/12327/10982 11992/12336/10988 11991/12335/10987\r\nf 10264/10533/9433 10280/10549/9449 11992/12336/10988 11985/12327/10982\r\nf 10284/10553/9453 11993/12337/10989 11992/12336/10988 10280/10549/9449\r\nf 10287/10556/9456 10286/10555/9455 11994/12338/9455 11995/12339/9456\r\nf 10267/10536/9436 11987/12329/10984 11994/12338/9455 10286/10555/9455\r\nf 11989/12332/10984 11996/12340/10990 11994/12341/9455 11987/12333/10984\r\nf 11997/12342/9456 11995/12343/9456 11994/12341/9455 11996/12340/10990\r\nf 11998/12344/9466 11999/12345/9465 12000/12346/10991 12001/12347/10992\r\nf 11990/12334/10986 11993/12337/10989 12000/12346/10991 11999/12345/9465\r\nf 10284/10553/9453 10300/10569/9469 12000/12346/10991 11993/12337/10989\r\nf 10304/10573/9473 12001/12347/10992 12000/12346/10991 10300/10569/9469\r\nf 10307/10576/9476 10306/10575/9475 12002/12348/9475 12003/12349/9476\r\nf 10287/10556/9456 11995/12339/9456 12002/12348/9475 10306/10575/9475\r\nf 11997/12342/9456 12004/12350/9475 12002/12351/9475 11995/12343/9456\r\nf 12005/12352/10993 12003/12353/9476 12002/12351/9475 12004/12350/9475\r\nf 12006/12354/9486 12007/12355/9485 12008/12356/10994 12009/12357/10995\r\nf 11998/12344/9466 12001/12347/10992 12008/12356/10994 12007/12355/9485\r\nf 10304/10573/9473 10320/10589/9489 12008/12356/10994 12001/12347/10992\r\nf 10324/10593/9493 12009/12357/10995 12008/12356/10994 10320/10589/9489\r\nf 10327/10596/9496 10326/10595/9495 12010/12358/9495 12011/12359/9496\r\nf 10307/10576/9476 12003/12349/9476 12010/12358/9495 10326/10595/9495\r\nf 12005/12352/10993 12012/12360/9495 12010/12361/9495 12003/12353/9476\r\nf 12013/12362/9496 12011/12363/9496 12010/12361/9495 12012/12360/9495\r\nf 12014/12364/9506 12015/12365/9505 12016/12366/10996 12017/12367/10997\r\nf 12006/12354/9486 12009/12357/10995 12016/12366/10996 12015/12365/9505\r\nf 10324/10593/9493 10340/10609/9509 12016/12366/10996 12009/12357/10995\r\nf 10344/10613/9513 12017/12367/10997 12016/12366/10996 10340/10609/9509\r\nf 10347/10616/9516 10346/10615/9515 12018/12368/9515 12019/12369/10998\r\nf 10327/10596/9496 12011/12359/9496 12018/12368/9515 10346/10615/9515\r\nf 12013/12362/9496 12020/12370/9515 12018/12371/9515 12011/12363/9496\r\nf 12021/12372/10998 12019/12373/10998 12018/12371/9515 12020/12370/9515\r\nf 12022/12374/10999 12023/12375/9525 12024/12376/11000 12025/12377/11001\r\nf 12014/12364/9506 12017/12367/10997 12024/12376/11000 12023/12375/9525\r\nf 10344/10613/9513 10360/10629/9529 12024/12376/11000 12017/12367/10997\r\nf 10364/10633/9533 12025/12377/11001 12024/12376/11000 10360/10629/9529\r\nf 10367/10636/9536 10366/10635/9535 12026/12378/9535 12027/12379/11002\r\nf 10347/10616/9516 12019/12369/10998 12026/12378/9535 10366/10635/9535\r\nf 12021/12372/10998 12028/12380/9535 12026/12381/9535 12019/12373/10998\r\nf 12029/12382/9536 12027/12383/11002 12026/12381/9535 12028/12380/9535\r\nf 12030/12384/9546 12031/12385/9545 12032/12386/11003 12033/12387/11004\r\nf 12022/12374/10999 12025/12377/11001 12032/12386/11003 12031/12385/9545\r\nf 10364/10633/9533 10380/10649/9549 12032/12386/11003 12025/12377/11001\r\nf 10384/10653/9553 12033/12387/11004 12032/12386/11003 10380/10649/9549\r\nf 10387/10656/9556 10386/10655/9555 12034/12388/9555 12035/12389/9556\r\nf 10367/10636/9536 12027/12379/11002 12034/12388/9555 10386/10655/9555\r\nf 12029/12382/9536 12036/12390/9555 12034/12391/9555 12027/12383/11002\r\nf 12037/12392/11005 12035/12393/9556 12034/12391/9555 12036/12390/9555\r\nf 12038/12394/9566 12039/12395/9565 12040/12396/11006 12041/12397/11007\r\nf 12030/12384/9546 12033/12387/11004 12040/12396/11006 12039/12395/9565\r\nf 10384/10653/9553 10400/10669/9569 12040/12396/11006 12033/12387/11004\r\nf 10404/10673/9573 12041/12397/11007 12040/12396/11006 10400/10669/9569\r\nf 10407/10676/9576 10406/10675/9575 12042/12398/9575 12043/12399/9576\r\nf 10387/10656/9556 12035/12389/9556 12042/12398/9575 10406/10675/9575\r\nf 12037/12392/11005 12044/12400/9575 12042/12401/9575 12035/12393/9556\r\nf 12045/12402/11008 12043/12403/9576 12042/12401/9575 12044/12400/9575\r\nf 12046/12404/9586 12047/12405/11009 12048/12406/11010 12049/12407/11011\r\nf 12038/12394/9566 12041/12397/11007 12048/12406/11010 12047/12405/11009\r\nf 10404/10673/9573 10420/10689/9589 12048/12406/11010 12041/12397/11007\r\nf 10424/10693/9593 12049/12407/11011 12048/12406/11010 10420/10689/9589\r\nf 10427/10696/9596 10426/10695/9595 12050/12408/9595 12051/12409/9596\r\nf 10407/10676/9576 12043/12399/9576 12050/12408/9595 10426/10695/9595\r\nf 12045/12402/11008 12052/12410/11012 12050/12411/9595 12043/12403/9576\r\nf 12053/12412/9596 12051/12413/9596 12050/12411/9595 12052/12410/11012\r\nf 12054/12414/9606 12055/12415/9605 12056/12416/11013 12057/12417/11014\r\nf 12046/12404/9586 12049/12407/11011 12056/12416/11013 12055/12415/9605\r\nf 10424/10693/9593 10440/10709/9609 12056/12416/11013 12049/12407/11011\r\nf 10444/10713/9613 12057/12417/11014 12056/12416/11013 10440/10709/9609\r\nf 10447/10716/9616 10446/10715/9615 12058/12418/9615 12059/12419/9616\r\nf 10427/10696/9596 12051/12409/9596 12058/12418/9615 10446/10715/9615\r\nf 12053/12412/9596 12060/12420/9615 12058/12421/9615 12051/12413/9596\r\nf 12061/12422/9616 12059/12423/9616 12058/12421/9615 12060/12420/9615\r\nf 12062/12424/9626 12063/12425/9625 12064/12426/11015 12065/12427/11016\r\nf 12054/12414/9606 12057/12417/11014 12064/12426/11015 12063/12425/9625\r\nf 10444/10713/9613 10460/10729/9629 12064/12426/11015 12057/12417/11014\r\nf 10464/10733/9633 12065/12427/11016 12064/12426/11015 10460/10729/9629\r\nf 10467/10736/9636 10466/10735/9635 12066/12428/9635 12067/12429/9636\r\nf 10447/10716/9616 12059/12419/9616 12066/12428/9635 10466/10735/9635\r\nf 12061/12422/9616 12068/12430/9635 12066/12431/9635 12059/12423/9616\r\nf 12069/12432/9636 12067/12433/9636 12066/12431/9635 12068/12430/9635\r\nf 12070/12434/9646 12071/12435/9645 12072/12436/11017 12073/12437/11018\r\nf 12062/12424/9626 12065/12427/11016 12072/12436/11017 12071/12435/9645\r\nf 10464/10733/9633 10480/10749/9649 12072/12436/11017 12065/12427/11016\r\nf 10484/10753/9653 12073/12437/11018 12072/12436/11017 10480/10749/9649\r\nf 10487/10756/9656 10486/10755/9655 12074/12438/9655 12075/12439/9656\r\nf 10467/10736/9636 12067/12429/9636 12074/12438/9655 10486/10755/9655\r\nf 12069/12432/9636 12076/12440/9655 12074/12441/9655 12067/12433/9636\r\nf 12077/12442/9656 12075/12443/9656 12074/12441/9655 12076/12440/9655\r\nf 12078/12444/9666 12079/12445/9665 12080/12446/11019 12081/12447/11020\r\nf 12070/12434/9646 12073/12437/11018 12080/12446/11019 12079/12445/9665\r\nf 10484/10753/9653 10500/10769/9669 12080/12446/11019 12073/12437/11018\r\nf 10504/10773/9673 12081/12447/11020 12080/12446/11019 10500/10769/9669\r\nf 10507/10776/9676 10506/10775/9675 12082/12448/9675 12083/12449/9676\r\nf 10487/10756/9656 12075/12439/9656 12082/12448/9675 10506/10775/9675\r\nf 12077/12442/9656 12084/12450/9675 12082/12451/9675 12075/12443/9656\r\nf 12085/12452/9676 12083/12453/9676 12082/12451/9675 12084/12450/9675\r\nf 12086/12454/11021 12087/12455/11022 12088/12456/11023 12089/12457/11024\r\nf 12078/12444/9666 12081/12447/11020 12088/12456/11023 12087/12455/11022\r\nf 10504/10773/9673 10520/10789/9689 12088/12456/11023 12081/12447/11020\r\nf 10524/10793/9693 12089/12457/11024 12088/12456/11023 10520/10789/9689\r\nf 10527/10796/9696 10526/10795/9695 12090/12458/9695 12091/12459/9696\r\nf 10507/10776/9676 12083/12449/9676 12090/12458/9695 10526/10795/9695\r\nf 12085/12452/9676 12092/12460/11025 12090/12461/9695 12083/12453/9676\r\nf 12093/12462/9696 12091/12463/9696 12090/12461/9695 12092/12460/11025\r\nf 12094/12464/9706 12095/12465/9705 12096/12466/11026 12097/12467/11027\r\nf 12086/12454/11021 12089/12457/11024 12096/12466/11026 12095/12465/9705\r\nf 10524/10793/9693 10540/10809/9709 12096/12466/11026 12089/12457/11024\r\nf 10544/10813/9713 12097/12467/11027 12096/12466/11026 10540/10809/9709\r\nf 10547/10816/9716 10546/10815/9715 12098/12468/9715 12099/12469/9716\r\nf 10527/10796/9696 12091/12459/9696 12098/12468/9715 10546/10815/9715\r\nf 12093/12462/9696 12100/12470/9715 12098/12471/9715 12091/12463/9696\r\nf 12101/12472/9716 12099/12473/9716 12098/12471/9715 12100/12470/9715\r\nf 12102/12474/9726 12103/12475/9725 12104/12476/11028 12105/12477/11029\r\nf 12094/12464/9706 12097/12467/11027 12104/12476/11028 12103/12475/9725\r\nf 10544/10813/9713 10560/10829/9729 12104/12476/11028 12097/12467/11027\r\nf 10564/10833/9733 12105/12477/11029 12104/12476/11028 10560/10829/9729\r\nf 10567/10836/9736 10566/10835/9735 12106/12478/9735 12107/12479/9736\r\nf 10547/10816/9716 12099/12469/9716 12106/12478/9735 10566/10835/9735\r\nf 12101/12472/9716 12108/12480/9735 12106/12481/9735 12099/12473/9716\r\nf 12109/12482/9736 12107/12483/9736 12106/12481/9735 12108/12480/9735\r\nf 12110/12484/9746 12111/12485/9745 12112/12486/11030 12113/12487/11031\r\nf 12102/12474/9726 12105/12477/11029 12112/12486/11030 12111/12485/9745\r\nf 10564/10833/9733 10580/10849/9749 12112/12486/11030 12105/12477/11029\r\nf 10584/10853/9753 12113/12487/11031 12112/12486/11030 10580/10849/9749\r\nf 10587/10856/9756 10586/10855/9755 12114/12488/9755 12115/12489/9756\r\nf 10567/10836/9736 12107/12479/9736 12114/12488/9755 10586/10855/9755\r\nf 12109/12482/9736 12116/12490/9755 12114/12491/9755 12107/12483/9736\r\nf 12117/12492/9756 12115/12493/9756 12114/12491/9755 12116/12490/9755\r\nf 12118/12494/11032 12119/12495/9765 12120/12496/11033 12121/12497/11034\r\nf 12110/12484/9746 12113/12487/11031 12120/12496/11033 12119/12495/9765\r\nf 10584/10853/9753 10600/10869/9769 12120/12496/11033 12113/12487/11031\r\nf 10604/10873/9773 12121/12497/11034 12120/12496/11033 10600/10869/9769\r\nf 10607/10876/9776 10606/10875/9775 12122/12498/9775 12123/12499/9776\r\nf 10587/10856/9756 12115/12489/9756 12122/12498/9775 10606/10875/9775\r\nf 12117/12492/9756 12124/12500/9775 12122/12501/9775 12115/12493/9756\r\nf 12125/12502/11035 12123/12503/9776 12122/12501/9775 12124/12500/9775\r\nf 12126/12504/9786 12127/12505/9785 12128/12506/11036 12129/12507/11037\r\nf 12118/12494/11032 12121/12497/11034 12128/12506/11036 12127/12505/9785\r\nf 10604/10873/9773 10620/10889/9789 12128/12506/11036 12121/12497/11034\r\nf 10624/10893/9793 12129/12507/11037 12128/12506/11036 10620/10889/9789\r\nf 10627/10896/9796 10626/10895/9795 12130/12508/9795 12131/12509/9796\r\nf 10607/10876/9776 12123/12499/9776 12130/12508/9795 10626/10895/9795\r\nf 12125/12502/11035 12132/12510/9795 12130/12511/9795 12123/12503/9776\r\nf 12133/12512/9796 12131/12513/9796 12130/12511/9795 12132/12510/9795\r\nf 12134/12514/9806 12135/12515/9805 12136/12516/11038 12137/12517/11039\r\nf 12126/12504/9786 12129/12507/11037 12136/12516/11038 12135/12515/9805\r\nf 10624/10893/9793 10640/10909/9809 12136/12516/11038 12129/12507/11037\r\nf 10644/10913/9813 12137/12517/11039 12136/12516/11038 10640/10909/9809\r\nf 10647/10916/9816 10646/10915/9815 12138/12518/9815 12139/12519/9816\r\nf 10627/10896/9796 12131/12509/9796 12138/12518/9815 10646/10915/9815\r\nf 12133/12512/9796 12140/12520/9815 12138/12521/9815 12131/12513/9796\r\nf 12141/12522/9816 12139/12523/9816 12138/12521/9815 12140/12520/9815\r\nf 12142/12524/9826 12143/12525/9825 12144/12526/11040 12145/12527/11041\r\nf 12134/12514/9806 12137/12517/11039 12144/12526/11040 12143/12525/9825\r\nf 10644/10913/9813 10660/10929/9829 12144/12526/11040 12137/12517/11039\r\nf 10664/10933/9833 12145/12527/11041 12144/12526/11040 10660/10929/9829\r\nf 10667/10936/9836 10666/10935/9835 12146/12528/9835 12147/12529/9836\r\nf 10647/10916/9816 12139/12519/9816 12146/12528/9835 10666/10935/9835\r\nf 12141/12522/9816 12148/12530/9835 12146/12531/9835 12139/12523/9816\r\nf 12149/12532/11042 12147/12533/9836 12146/12531/9835 12148/12530/9835\r\nf 12150/12534/9846 12151/12535/9845 12152/12536/11043 12153/12537/11044\r\nf 12142/12524/9826 12145/12527/11041 12152/12536/11043 12151/12535/9845\r\nf 10664/10933/9833 10680/10949/9849 12152/12536/11043 12145/12527/11041\r\nf 10684/10953/9853 12153/12537/11044 12152/12536/11043 10680/10949/9849\r\nf 10687/10956/9856 10686/10955/9855 12154/12538/9855 12155/12539/9856\r\nf 10667/10936/9836 12147/12529/9836 12154/12538/9855 10686/10955/9855\r\nf 12149/12532/11042 12156/12540/9855 12154/12541/9855 12147/12533/9836\r\nf 12157/12542/11045 12155/12543/9856 12154/12541/9855 12156/12540/9855\r\nf 12158/12544/9866 12159/12545/9865 12160/12546/11046 12161/12547/11047\r\nf 12150/12534/9846 12153/12537/11044 12160/12546/11046 12159/12545/9865\r\nf 10684/10953/9853 10700/10969/9869 12160/12546/11046 12153/12537/11044\r\nf 10704/10973/9873 12161/12547/11047 12160/12546/11046 10700/10969/9869\r\nf 10707/10976/9876 10706/10975/9875 12162/12548/9875 12163/12549/9876\r\nf 10687/10956/9856 12155/12539/9856 12162/12548/9875 10706/10975/9875\r\nf 12157/12542/11045 12164/12550/9875 12162/12551/9875 12155/12543/9856\r\nf 12165/12552/11048 12163/12553/9876 12162/12551/9875 12164/12550/9875\r\nf 12166/12554/9886 12167/12555/9885 12168/12556/11049 12169/12557/11050\r\nf 12158/12544/9866 12161/12547/11047 12168/12556/11049 12167/12555/9885\r\nf 10704/10973/9873 10720/10989/9889 12168/12556/11049 12161/12547/11047\r\nf 10724/10993/9893 12169/12557/11050 12168/12556/11049 10720/10989/9889\r\nf 10727/10996/9896 10726/10995/9895 12170/12558/9895 12171/12559/9896\r\nf 10707/10976/9876 12163/12549/9876 12170/12558/9895 10726/10995/9895\r\nf 12165/12552/11048 12172/12560/9895 12170/12561/9895 12163/12553/9876\r\nf 12173/12562/9896 12171/12563/9896 12170/12561/9895 12172/12560/9895\r\nf 12174/12564/9905 12175/12565/9886 12176/12566/11051 12177/12567/11052\r\nf 12166/12554/9886 12169/12557/11050 12176/12566/11051 12175/12565/9886\r\nf 10724/10993/9893 10740/11009/9908 12176/12566/11051 12169/12557/11050\r\nf 10744/11013/9912 12177/12567/11052 12176/12566/11051 10740/11009/9908\r\nf 10747/11016/9914 10746/11015/9896 12178/12568/9896 12179/12569/9914\r\nf 10727/10996/9896 12171/12559/9896 12178/12568/9896 10746/11015/9896\r\nf 12173/12562/9896 12180/12570/9896 12178/12571/9896 12171/12563/9896\r\nf 12181/12572/9914 12179/12573/9914 12178/12571/9896 12180/12570/9896\r\nf 12182/12574/11053 12183/12575/9923 12184/12576/11054 12185/12577/11055\r\nf 12174/12564/9905 12177/12567/11052 12184/12576/11054 12183/12575/9923\r\nf 10744/11013/9912 10760/11029/9927 12184/12576/11054 12177/12567/11052\r\nf 10764/11033/9931 12185/12577/11055 12184/12576/11054 10760/11029/9927\r\nf 10767/11036/9934 10766/11035/9933 12186/12578/9933 12187/12579/11056\r\nf 10747/11016/9914 12179/12569/9914 12186/12578/9933 10766/11035/9933\r\nf 12181/12572/9914 12188/12580/9933 12186/12581/9933 12179/12573/9914\r\nf 12189/12582/11056 12187/12583/11056 12186/12581/9933 12188/12580/9933\r\nf 12190/12584/11057 12191/12585/9943 12192/12586/11058 12193/12587/11059\r\nf 12182/12574/11053 12185/12577/11055 12192/12586/11058 12191/12585/9943\r\nf 10764/11033/9931 10780/11049/9947 12192/12586/11058 12185/12577/11055\r\nf 10784/11053/9951 12193/12587/11059 12192/12586/11058 10780/11049/9947\r\nf 10787/11056/9954 10786/11055/9953 12194/12588/11060 12195/12589/11061\r\nf 10767/11036/9934 12187/12579/11056 12194/12588/11060 10786/11055/9953\r\nf 12189/12582/11056 12196/12590/11062 12194/12591/11060 12187/12583/11056\r\nf 12197/12592/11061 12195/12593/11061 12194/12591/11060 12196/12590/11062\r\nf 12198/12594/11063 12199/12595/11064 12200/12596/11065 12201/12597/11066\r\nf 12190/12584/11057 12193/12587/11059 12200/12596/11065 12199/12595/11064\r\nf 10784/11053/9951 10800/11069/9967 12200/12596/11065 12193/12587/11059\r\nf 10804/11073/9971 12201/12597/11066 12200/12596/11065 10800/11069/9967\r\nf 10807/11076/9974 10806/11075/9973 12202/12598/11067 12203/12599/11068\r\nf 10787/11056/9954 12195/12589/11061 12202/12598/11067 10806/11075/9973\r\nf 12197/12592/11061 12204/12600/11069 12202/12601/11067 12195/12593/11061\r\nf 12205/12602/11070 12203/12603/11068 12202/12601/11067 12204/12600/11069\r\nf 11010/11279/10173 12206/12604/11071 12207/12605/11072 11011/11280/10174\r\nf 12198/12594/11063 12201/12597/11066 12207/12605/11072 12206/12604/11071\r\nf 10804/11073/9971 10820/11089/9987 12207/12605/11072 12201/12597/11066\r\nf 10824/11093/9991 11011/11280/10174 12207/12605/11072 10820/11089/9987\r\nf 10827/11096/9994 10826/11095/9993 12208/12606/11073 12209/12607/11074\r\nf 10807/11076/9974 12203/12599/11068 12208/12606/11073 10826/11095/9993\r\nf 12205/12602/11070 12210/12608/11075 12208/12609/11073 12203/12603/11068\r\nf 12211/12610/11076 12209/12611/11074 12208/12609/11073 12210/12608/11075\r\nf 11701/11970/10861 12212/12612/11077 12213/12613/11078 11702/11971/10862\r\nf 9549/9818/8722 9547/9816/8720 12213/12613/11078 12212/12612/11077\r\nf 9542/9811/8715 9539/9808/8712 12213/12613/11078 9547/9816/8720\r\nf 9538/9807/8711 11702/11971/10862 12213/12613/11078 9539/9808/8712\r\nf 10844/11113/10011 12214/12614/11079 12215/12615/11079 12216/12616/10011\r\nf 8710/8673/8235 8709/8674/8235 12215/12615/11079 12214/12614/11079\r\nf 8708/8671/8235 12217/12617/11079 12215/12618/11079 8709/8672/8235\r\nf 12218/12619/10011 12216/12620/10011 12215/12618/11079 12217/12617/11079\r\nf 12219/12621/10026 12220/12622/11080 12221/12623/11081 12222/12624/11082\r\nf 8718/8682/8242 8717/8681/8241 12221/12623/11081 12220/12622/11080\r\nf 8716/8680/8240 12223/12625/11083 12221/12623/11081 8717/8681/8241\r\nf 10833/11102/10000 12222/12624/11082 12221/12623/11081 12223/12625/11083\r\nf 10846/11115/10013 10845/11114/10012 12224/12626/10012 12225/12627/10013\r\nf 10844/11113/10011 12216/12616/10011 12224/12626/10012 10845/11114/10012\r\nf 12218/12619/10011 12226/12628/10012 12224/12629/10012 12216/12620/10011\r\nf 12227/12630/10013 12225/12631/10013 12224/12629/10012 12226/12628/10012\r\nf 12228/12632/10028 12229/12633/10027 12230/12634/11084 12231/12635/11085\r\nf 12219/12621/10026 12222/12624/11082 12230/12634/11084 12229/12633/10027\r\nf 10833/11102/10000 10836/11105/10003 12230/12634/11084 12222/12624/11082\r\nf 10841/11110/10008 12231/12635/11085 12230/12634/11084 10836/11105/10003\r\nf 10871/11140/10038 10870/11139/10037 12232/12636/10037 12233/12637/10038\r\nf 10846/11115/10013 12225/12627/10013 12232/12636/10037 10870/11139/10037\r\nf 12227/12630/10013 12234/12638/10037 12232/12639/10037 12225/12631/10013\r\nf 12235/12640/10038 12233/12641/10038 12232/12639/10037 12234/12638/10037\r\nf 12236/12642/10048 12237/12643/10047 12238/12644/11086 12239/12645/11087\r\nf 12228/12632/10028 12231/12635/11085 12238/12644/11086 12237/12643/10047\r\nf 10841/11110/10008 10864/11133/10031 12238/12644/11086 12231/12635/11085\r\nf 10868/11137/10035 12239/12645/11087 12238/12644/11086 10864/11133/10031\r\nf 10891/11160/10058 10890/11159/10057 12240/12646/10057 12241/12647/10058\r\nf 10871/11140/10038 12233/12637/10038 12240/12646/10057 10890/11159/10057\r\nf 12235/12640/10038 12242/12648/10057 12240/12649/10057 12233/12641/10038\r\nf 12243/12650/10058 12241/12651/10058 12240/12649/10057 12242/12648/10057\r\nf 12244/12652/10068 12245/12653/10067 12246/12654/11088 12247/12655/11089\r\nf 12236/12642/10048 12239/12645/11087 12246/12654/11088 12245/12653/10067\r\nf 10868/11137/10035 10884/11153/10051 12246/12654/11088 12239/12645/11087\r\nf 10888/11157/10055 12247/12655/11089 12246/12654/11088 10884/11153/10051\r\nf 10911/11180/10077 10910/11179/10077 12248/12656/10077 12249/12657/10077\r\nf 10891/11160/10058 12241/12647/10058 12248/12656/10077 10910/11179/10077\r\nf 12243/12650/10058 12250/12658/10077 12248/12659/10077 12241/12651/10058\r\nf 12251/12660/10077 12249/12661/10077 12248/12659/10077 12250/12658/10077\r\nf 12252/12662/10086 12253/12663/10086 12254/12664/11090 12255/12665/11091\r\nf 12244/12652/10068 12247/12655/11089 12254/12664/11090 12253/12663/10086\r\nf 10888/11157/10055 10904/11173/10071 12254/12664/11090 12247/12655/11089\r\nf 10908/11177/10075 12255/12665/11091 12254/12664/11090 10904/11173/10071\r\nf 10931/11200/10095 10930/11199/10058 12256/12666/10058 12257/12667/10095\r\nf 10911/11180/10077 12249/12657/10077 12256/12666/10058 10930/11199/10058\r\nf 12251/12660/10077 12258/12668/10058 12256/12669/10058 12249/12661/10077\r\nf 12259/12670/10095 12257/12671/10095 12256/12669/10058 12258/12668/10058\r\nf 12260/12672/10104 12261/12673/10068 12262/12674/11092 12263/12675/11093\r\nf 12252/12662/10086 12255/12665/11091 12262/12674/11092 12261/12673/10068\r\nf 10908/11177/10075 10924/11193/10089 12262/12674/11092 12255/12665/11091\r\nf 10928/11197/10093 12263/12675/11093 12262/12674/11092 10924/11193/10089\r\nf 10951/11220/10114 10950/11219/10113 12264/12676/10113 12265/12677/10114\r\nf 10931/11200/10095 12257/12667/10095 12264/12676/10113 10950/11219/10113\r\nf 12259/12670/10095 12266/12678/10113 12264/12679/10113 12257/12671/10095\r\nf 12267/12680/10114 12265/12681/10114 12264/12679/10113 12266/12678/10113\r\nf 12268/12682/10124 12269/12683/10123 12270/12684/11094 12271/12685/11095\r\nf 12260/12672/10104 12263/12675/11093 12270/12684/11094 12269/12683/10123\r\nf 10928/11197/10093 10944/11213/10107 12270/12684/11094 12263/12675/11093\r\nf 10948/11217/10111 12271/12685/11095 12270/12684/11094 10944/11213/10107\r\nf 10971/11240/10134 10970/11239/10133 12272/12686/10133 12273/12687/11096\r\nf 10951/11220/10114 12265/12677/10114 12272/12686/10133 10970/11239/10133\r\nf 12267/12680/10114 12274/12688/10133 12272/12689/10133 12265/12681/10114\r\nf 12275/12690/11096 12273/12691/11096 12272/12689/10133 12274/12688/10133\r\nf 12276/12692/11097 12277/12693/10143 12278/12694/11098 12279/12695/11099\r\nf 12268/12682/10124 12271/12685/11095 12278/12694/11098 12277/12693/10143\r\nf 10948/11217/10111 10964/11233/10127 12278/12694/11098 12271/12685/11095\r\nf 10968/11237/10131 12279/12695/11099 12278/12694/11098 10964/11233/10127\r\nf 10991/11260/10154 10990/11259/10153 12280/12696/11100 12281/12697/11101\r\nf 10971/11240/10134 12273/12687/11096 12280/12696/11100 10990/11259/10153\r\nf 12275/12690/11096 12282/12698/11102 12280/12699/11100 12273/12691/11096\r\nf 12283/12700/11103 12281/12701/11101 12280/12699/11100 12282/12698/11102\r\nf 12284/12702/11104 12285/12703/10163 12286/12704/11105 12287/12705/11106\r\nf 12276/12692/11097 12279/12695/11099 12286/12704/11105 12285/12703/10163\r\nf 10968/11237/10131 10984/11253/10147 12286/12704/11105 12279/12695/11099\r\nf 10988/11257/10151 12287/12705/11106 12286/12704/11105 10984/11253/10147\r\nf 11010/11279/10173 11009/11278/10172 12288/12706/11107 12289/12707/11108\r\nf 10991/11260/10154 12281/12697/11101 12288/12706/11107 11009/11278/10172\r\nf 12283/12700/11103 12290/12708/11109 12288/12709/11107 12281/12701/11101\r\nf 10817/11086/9984 12289/12710/11108 12288/12709/11107 12290/12708/11109\r\nf 12291/12711/11110 12292/12712/11111 12293/12713/11112 12294/12714/11113\r\nf 12284/12702/11104 12287/12705/11106 12293/12713/11112 12292/12712/11111\r\nf 10988/11257/10151 11004/11273/10167 12293/12713/11112 12287/12705/11106\r\nf 11007/11276/10170 12294/12714/11113 12293/12713/11112 11004/11273/10167\r\nf 12295/12715/11114 12296/12716/11115 12297/12717/11116 12298/12718/11117\r\nf 12291/12711/11110 12294/12714/11113 12297/12717/11116 12296/12716/11115\r\nf 11007/11276/10170 11022/11291/10185 12297/12717/11116 12294/12714/11113\r\nf 11024/11293/10187 12298/12718/11117 12297/12717/11116 11022/11291/10185\r\nf 11041/11310/10204 11040/11309/10203 12299/12719/11118 12300/12720/11119\r\nf 10827/11096/9994 12209/12607/11074 12299/12719/11118 11040/11309/10203\r\nf 12211/12610/11076 12301/12721/11120 12299/12722/11118 12209/12611/11074\r\nf 12302/12723/11121 12300/12724/11119 12299/12722/11118 12301/12721/11120\r\nf 12303/12725/11122 12304/12726/11123 12305/12727/11124 12306/12728/11125\r\nf 12295/12715/11114 12298/12718/11117 12305/12727/11124 12304/12726/11123\r\nf 11024/11293/10187 11034/11303/10197 12305/12727/11124 12298/12718/11117\r\nf 11038/11307/10201 12306/12728/11125 12305/12727/11124 11034/11303/10197\r\nf 11061/11330/10224 11060/11329/10223 12307/12729/11126 12308/12730/11127\r\nf 11041/11310/10204 12300/12720/11119 12307/12729/11126 11060/11329/10223\r\nf 12302/12723/11121 12309/12731/11128 12307/12732/11126 12300/12724/11119\r\nf 12310/12733/11129 12308/12734/11127 12307/12732/11126 12309/12731/11128\r\nf 12311/12735/11130 12312/12736/11131 12313/12737/11132 12314/12738/11133\r\nf 12303/12725/11122 12306/12728/11125 12313/12737/11132 12312/12736/11131\r\nf 11038/11307/10201 11054/11323/10217 12313/12737/11132 12306/12728/11125\r\nf 11058/11327/10221 12314/12738/11133 12313/12737/11132 11054/11323/10217\r\nf 11081/11350/10244 11080/11349/10243 12315/12739/10243 12316/12740/11134\r\nf 11061/11330/10224 12308/12730/11127 12315/12739/10243 11080/11349/10243\r\nf 12310/12733/11129 12317/12741/11135 12315/12742/10243 12308/12734/11127\r\nf 12318/12743/11134 12316/12744/11134 12315/12742/10243 12317/12741/11135\r\nf 12319/12745/11136 12320/12746/11137 12321/12747/11138 12322/12748/11139\r\nf 12311/12735/11130 12314/12738/11133 12321/12747/11138 12320/12746/11137\r\nf 11058/11327/10221 11074/11343/10237 12321/12747/11138 12314/12738/11133\r\nf 11078/11347/10241 12322/12748/11139 12321/12747/11138 11074/11343/10237\r\nf 11101/11370/10264 11100/11369/10263 12323/12749/10263 12324/12750/11140\r\nf 11081/11350/10244 12316/12740/11134 12323/12749/10263 11100/11369/10263\r\nf 12318/12743/11134 12325/12751/11141 12323/12752/10263 12316/12744/11134\r\nf 12326/12753/11140 12324/12754/11140 12323/12752/10263 12325/12751/11141\r\nf 12327/12755/10274 12328/12756/11142 12329/12757/11143 12330/12758/11144\r\nf 12319/12745/11136 12322/12748/11139 12329/12757/11143 12328/12756/11142\r\nf 11078/11347/10241 11094/11363/10257 12329/12757/11143 12322/12748/11139\r\nf 11098/11367/10261 12330/12758/11144 12329/12757/11143 11094/11363/10257\r\nf 11121/11390/10284 11120/11389/10283 12331/12759/10283 12332/12760/10284\r\nf 11101/11370/10264 12324/12750/11140 12331/12759/10283 11120/11389/10283\r\nf 12326/12753/11140 12333/12761/11145 12331/12762/10283 12324/12754/11140\r\nf 12334/12763/10284 12332/12764/10284 12331/12762/10283 12333/12761/11145\r\nf 12335/12765/11146 12336/12766/10293 12337/12767/11147 12338/12768/11148\r\nf 12327/12755/10274 12330/12758/11144 12337/12767/11147 12336/12766/10293\r\nf 11098/11367/10261 11114/11383/10277 12337/12767/11147 12330/12758/11144\r\nf 11118/11387/10281 12338/12768/11148 12337/12767/11147 11114/11383/10277\r\nf 11141/11410/10304 11140/11409/10303 12339/12769/11149 12340/12770/10304\r\nf 11121/11390/10284 12332/12760/10284 12339/12769/11149 11140/11409/10303\r\nf 12334/12763/10284 12341/12771/11149 12339/12772/11149 12332/12764/10284\r\nf 12342/12773/10304 12340/12774/10304 12339/12772/11149 12341/12771/11149\r\nf 12343/12775/10314 12344/12776/10313 12345/12777/11150 12346/12778/11151\r\nf 12335/12765/11146 12338/12768/11148 12345/12777/11150 12344/12776/10313\r\nf 11118/11387/10281 11134/11403/10297 12345/12777/11150 12338/12768/11148\r\nf 11138/11407/10301 12346/12778/11151 12345/12777/11150 11134/11403/10297\r\nf 11161/11430/10324 11160/11429/10323 12347/12779/10323 12348/12780/10324\r\nf 11141/11410/10304 12340/12770/10304 12347/12779/10323 11160/11429/10323\r\nf 12342/12773/10304 12349/12781/10323 12347/12782/10323 12340/12774/10304\r\nf 12350/12783/10324 12348/12784/10324 12347/12782/10323 12349/12781/10323\r\nf 12351/12785/10334 12352/12786/10333 12353/12787/11152 12354/12788/11153\r\nf 12343/12775/10314 12346/12778/11151 12353/12787/11152 12352/12786/10333\r\nf 11138/11407/10301 11154/11423/10317 12353/12787/11152 12346/12778/11151\r\nf 11158/11427/10321 12354/12788/11153 12353/12787/11152 11154/11423/10317\r\nf 11181/11450/10344 11180/11449/10343 12355/12789/11154 12356/12790/10344\r\nf 11161/11430/10324 12348/12780/10324 12355/12789/11154 11180/11449/10343\r\nf 12350/12783/10324 12357/12791/11154 12355/12792/11154 12348/12784/10324\r\nf 12358/12793/11155 12356/12794/10344 12355/12792/11154 12357/12791/11154\r\nf 12359/12795/10354 12360/12796/11156 12361/12797/11157 12362/12798/11158\r\nf 12351/12785/10334 12354/12788/11153 12361/12797/11157 12360/12796/11156\r\nf 11158/11427/10321 11174/11443/10337 12361/12797/11157 12354/12788/11153\r\nf 11178/11447/10341 12362/12798/11158 12361/12797/11157 11174/11443/10337\r\nf 11201/11470/10364 11200/11469/10363 12363/12799/10363 12364/12800/10364\r\nf 11181/11450/10344 12356/12790/10344 12363/12799/10363 11200/11469/10363\r\nf 12358/12793/11155 12365/12801/10363 12363/12802/10363 12356/12794/10344\r\nf 12366/12803/10364 12364/12804/10364 12363/12802/10363 12365/12801/10363\r\nf 12367/12805/10374 12368/12806/11159 12369/12807/11160 12370/12808/11161\r\nf 12359/12795/10354 12362/12798/11158 12369/12807/11160 12368/12806/11159\r\nf 11178/11447/10341 11194/11463/10357 12369/12807/11160 12362/12798/11158\r\nf 11198/11467/10361 12370/12808/11161 12369/12807/11160 11194/11463/10357\r\nf 11221/11490/10384 11220/11489/10383 12371/12809/10383 12372/12810/10384\r\nf 11201/11470/10364 12364/12800/10364 12371/12809/10383 11220/11489/10383\r\nf 12366/12803/10364 12373/12811/10383 12371/12812/10383 12364/12804/10364\r\nf 12374/12813/10384 12372/12814/10384 12371/12812/10383 12373/12811/10383\r\nf 12375/12815/10394 12376/12816/10393 12377/12817/11162 12378/12818/11163\r\nf 12367/12805/10374 12370/12808/11161 12377/12817/11162 12376/12816/10393\r\nf 11198/11467/10361 11214/11483/10377 12377/12817/11162 12370/12808/11161\r\nf 11218/11487/10381 12378/12818/11163 12377/12817/11162 11214/11483/10377\r\nf 11241/11510/10384 11240/11509/10403 12379/12819/10403 12380/12820/10384\r\nf 11221/11490/10384 12372/12810/10384 12379/12819/10403 11240/11509/10403\r\nf 12374/12813/10384 12381/12821/10403 12379/12822/10403 12372/12814/10384\r\nf 12382/12823/10384 12380/12824/10384 12379/12822/10403 12381/12821/10403\r\nf 12383/12825/10394 12384/12826/10412 12385/12827/11164 12386/12828/11165\r\nf 12375/12815/10394 12378/12818/11163 12385/12827/11164 12384/12826/10412\r\nf 11218/11487/10381 11234/11503/10397 12385/12827/11164 12378/12818/11163\r\nf 11238/11507/10401 12386/12828/11165 12385/12827/11164 11234/11503/10397\r\nf 11261/11530/10422 11260/11529/10421 12387/12829/10421 12388/12830/10422\r\nf 11241/11510/10384 12380/12820/10384 12387/12829/10421 11260/11529/10421\r\nf 12382/12823/10384 12389/12831/10421 12387/12832/10421 12380/12824/10384\r\nf 12390/12833/10422 12388/12834/10422 12387/12832/10421 12389/12831/10421\r\nf 12391/12835/10432 12392/12836/10431 12393/12837/11166 12394/12838/11167\r\nf 12383/12825/10394 12386/12828/11165 12393/12837/11166 12392/12836/10431\r\nf 11238/11507/10401 11254/11523/10415 12393/12837/11166 12386/12828/11165\r\nf 11258/11527/10419 12394/12838/11167 12393/12837/11166 11254/11523/10415\r\nf 11281/11550/10442 11280/11549/10441 12395/12839/10441 12396/12840/10442\r\nf 11261/11530/10422 12388/12830/10422 12395/12839/10441 11280/11549/10441\r\nf 12390/12833/10422 12397/12841/10441 12395/12842/10441 12388/12834/10422\r\nf 12398/12843/10442 12396/12844/10442 12395/12842/10441 12397/12841/10441\r\nf 12399/12845/10452 12400/12846/10451 12401/12847/11168 12402/12848/11169\r\nf 12391/12835/10432 12394/12838/11167 12401/12847/11168 12400/12846/10451\r\nf 11258/11527/10419 11274/11543/10435 12401/12847/11168 12394/12838/11167\r\nf 11278/11547/10439 12402/12848/11169 12401/12847/11168 11274/11543/10435\r\nf 11301/11570/10462 11300/11569/10461 12403/12849/10461 12404/12850/10462\r\nf 11281/11550/10442 12396/12840/10442 12403/12849/10461 11300/11569/10461\r\nf 12398/12843/10442 12405/12851/11170 12403/12852/10461 12396/12844/10442\r\nf 12406/12853/10462 12404/12854/10462 12403/12852/10461 12405/12851/11170\r\nf 12407/12855/11171 12408/12856/10471 12409/12857/11172 12410/12858/11173\r\nf 12399/12845/10452 12402/12848/11169 12409/12857/11172 12408/12856/10471\r\nf 11278/11547/10439 11294/11563/10455 12409/12857/11172 12402/12848/11169\r\nf 11298/11567/10459 12410/12858/11173 12409/12857/11172 11294/11563/10455\r\nf 11321/11590/10482 11320/11589/10481 12411/12859/10481 12412/12860/10482\r\nf 11301/11570/10462 12404/12850/10462 12411/12859/10481 11320/11589/10481\r\nf 12406/12853/10462 12413/12861/10481 12411/12862/10481 12404/12854/10462\r\nf 12414/12863/10482 12412/12864/10482 12411/12862/10481 12413/12861/10481\r\nf 12415/12865/10492 12416/12866/10491 12417/12867/11174 12418/12868/11175\r\nf 12407/12855/11171 12410/12858/11173 12417/12867/11174 12416/12866/10491\r\nf 11298/11567/10459 11314/11583/10475 12417/12867/11174 12410/12858/11173\r\nf 11318/11587/10479 12418/12868/11175 12417/12867/11174 11314/11583/10475\r\nf 11341/11610/10502 11340/11609/10501 12419/12869/10501 12420/12870/10502\r\nf 11321/11590/10482 12412/12860/10482 12419/12869/10501 11340/11609/10501\r\nf 12414/12863/10482 12421/12871/10501 12419/12872/10501 12412/12864/10482\r\nf 12422/12873/10502 12420/12874/10502 12419/12872/10501 12421/12871/10501\r\nf 12423/12875/10512 12424/12876/10511 12425/12877/11176 12426/12878/11177\r\nf 12415/12865/10492 12418/12868/11175 12425/12877/11176 12424/12876/10511\r\nf 11318/11587/10479 11334/11603/10495 12425/12877/11176 12418/12868/11175\r\nf 11338/11607/10499 12426/12878/11177 12425/12877/11176 11334/11603/10495\r\nf 11361/11630/10522 11360/11629/10521 12427/12879/10521 12428/12880/10522\r\nf 11341/11610/10502 12420/12870/10502 12427/12879/10521 11360/11629/10521\r\nf 12422/12873/10502 12429/12881/10521 12427/12882/10521 12420/12874/10502\r\nf 12430/12883/10522 12428/12884/10522 12427/12882/10521 12429/12881/10521\r\nf 12431/12885/10532 12432/12886/10531 12433/12887/11178 12434/12888/11179\r\nf 12423/12875/10512 12426/12878/11177 12433/12887/11178 12432/12886/10531\r\nf 11338/11607/10499 11354/11623/10515 12433/12887/11178 12426/12878/11177\r\nf 11358/11627/10519 12434/12888/11179 12433/12887/11178 11354/11623/10515\r\nf 11381/11650/10542 11380/11649/10541 12435/12889/10541 12436/12890/10542\r\nf 11361/11630/10522 12428/12880/10522 12435/12889/10541 11380/11649/10541\r\nf 12430/12883/10522 12437/12891/10541 12435/12892/10541 12428/12884/10522\r\nf 12438/12893/10542 12436/12894/10542 12435/12892/10541 12437/12891/10541\r\nf 12439/12895/10552 12440/12896/11180 12441/12897/11181 12442/12898/11182\r\nf 12431/12885/10532 12434/12888/11179 12441/12897/11181 12440/12896/11180\r\nf 11358/11627/10519 11374/11643/10535 12441/12897/11181 12434/12888/11179\r\nf 11378/11647/10539 12442/12898/11182 12441/12897/11181 11374/11643/10535\r\nf 11401/11670/10562 11400/11669/10561 12443/12899/10561 12444/12900/10562\r\nf 11381/11650/10542 12436/12890/10542 12443/12899/10561 11400/11669/10561\r\nf 12438/12893/10542 12445/12901/11183 12443/12902/10561 12436/12894/10542\r\nf 12446/12903/10562 12444/12904/10562 12443/12902/10561 12445/12901/11183\r\nf 12447/12905/10572 12448/12906/10571 12449/12907/11184 12450/12908/11185\r\nf 12439/12895/10552 12442/12898/11182 12449/12907/11184 12448/12906/10571\r\nf 11378/11647/10539 11394/11663/10555 12449/12907/11184 12442/12898/11182\r\nf 11398/11667/10559 12450/12908/11185 12449/12907/11184 11394/11663/10555\r\nf 11421/11690/10582 11420/11689/10581 12451/12909/10581 12452/12910/10582\r\nf 11401/11670/10562 12444/12900/10562 12451/12909/10581 11420/11689/10581\r\nf 12446/12903/10562 12453/12911/10581 12451/12912/10581 12444/12904/10562\r\nf 12454/12913/10582 12452/12914/10582 12451/12912/10581 12453/12911/10581\r\nf 12455/12915/10592 12456/12916/10591 12457/12917/11186 12458/12918/11187\r\nf 12447/12905/10572 12450/12908/11185 12457/12917/11186 12456/12916/10591\r\nf 11398/11667/10559 11414/11683/10575 12457/12917/11186 12450/12908/11185\r\nf 11418/11687/10579 12458/12918/11187 12457/12917/11186 11414/11683/10575\r\nf 11441/11710/10602 11440/11709/10601 12459/12919/10601 12460/12920/11188\r\nf 11421/11690/10582 12452/12910/10582 12459/12919/10601 11440/11709/10601\r\nf 12454/12913/10582 12461/12921/11189 12459/12922/10601 12452/12914/10582\r\nf 12462/12923/11190 12460/12924/11188 12459/12922/10601 12461/12921/11189\r\nf 12463/12925/11191 12464/12926/11192 12465/12927/11193 12466/12928/11194\r\nf 12455/12915/10592 12458/12918/11187 12465/12927/11193 12464/12926/11192\r\nf 11418/11687/10579 11434/11703/10595 12465/12927/11193 12458/12918/11187\r\nf 11438/11707/10599 12466/12928/11194 12465/12927/11193 11434/11703/10595\r\nf 11461/11730/10622 11460/11729/10621 12467/12929/11195 12468/12930/11196\r\nf 11441/11710/10602 12460/12920/11188 12467/12929/11195 11460/11729/10621\r\nf 12462/12923/11190 12469/12931/11195 12467/12932/11195 12460/12924/11188\r\nf 12470/12933/11197 12468/12934/11196 12467/12932/11195 12469/12931/11195\r\nf 12471/12935/11198 12472/12936/11199 12473/12937/11200 12474/12938/11201\r\nf 12463/12925/11191 12466/12928/11194 12473/12937/11200 12472/12936/11199\r\nf 11438/11707/10599 11454/11723/10615 12473/12937/11200 12466/12928/11194\r\nf 11458/11727/10619 12474/12938/11201 12473/12937/11200 11454/11723/10615\r\nf 11481/11750/10642 11480/11749/10641 12475/12939/11202 12476/12940/11203\r\nf 11461/11730/10622 12468/12930/11196 12475/12939/11202 11480/11749/10641\r\nf 12470/12933/11197 12477/12941/11204 12475/12942/11202 12468/12934/11196\r\nf 12478/12943/11205 12476/12944/11203 12475/12942/11202 12477/12941/11204\r\nf 12479/12945/11206 12480/12946/11207 12481/12947/11208 12482/12948/11209\r\nf 12471/12935/11198 12474/12938/11201 12481/12947/11208 12480/12946/11207\r\nf 11458/11727/10619 11474/11743/10635 12481/12947/11208 12474/12938/11201\r\nf 11478/11747/10639 12482/12948/11209 12481/12947/11208 11474/11743/10635\r\nf 11501/11770/10662 11500/11769/10661 12483/12949/11210 12484/12950/11211\r\nf 11481/11750/10642 12476/12940/11203 12483/12949/11210 11500/11769/10661\r\nf 12478/12943/11205 12485/12951/11210 12483/12952/11210 12476/12944/11203\r\nf 12486/12953/11211 12484/12954/11211 12483/12952/11210 12485/12951/11210\r\nf 12487/12955/11212 12488/12956/11213 12489/12957/11214 12490/12958/11215\r\nf 12479/12945/11206 12482/12948/11209 12489/12957/11214 12488/12956/11213\r\nf 11478/11747/10639 11494/11763/10655 12489/12957/11214 12482/12948/11209\r\nf 11498/11767/10659 12490/12958/11215 12489/12957/11214 11494/11763/10655\r\nf 11521/11790/10682 11520/11789/10681 12491/12959/10681 12492/12960/10682\r\nf 11501/11770/10662 12484/12950/11211 12491/12959/10681 11520/11789/10681\r\nf 12486/12953/11211 12493/12961/11216 12491/12962/10681 12484/12954/11211\r\nf 12494/12963/11217 12492/12964/10682 12491/12962/10681 12493/12961/11216\r\nf 12495/12965/11218 12496/12966/11219 12497/12967/11220 12498/12968/11221\r\nf 12487/12955/11212 12490/12958/11215 12497/12967/11220 12496/12966/11219\r\nf 11498/11767/10659 11514/11783/10675 12497/12967/11220 12490/12958/11215\r\nf 11518/11787/10679 12498/12968/11221 12497/12967/11220 11514/11783/10675\r\nf 11541/11810/10702 11540/11809/10701 12499/12969/10701 12500/12970/11222\r\nf 11521/11790/10682 12492/12960/10682 12499/12969/10701 11540/11809/10701\r\nf 12494/12963/11217 12501/12971/11223 12499/12972/10701 12492/12964/10682\r\nf 12502/12973/11224 12500/12974/11222 12499/12972/10701 12501/12971/11223\r\nf 12503/12975/11225 12504/12976/11226 12505/12977/11227 12506/12978/11228\r\nf 12495/12965/11218 12498/12968/11221 12505/12977/11227 12504/12976/11226\r\nf 11518/11787/10679 11534/11803/10695 12505/12977/11227 12498/12968/11221\r\nf 11538/11807/10699 12506/12978/11228 12505/12977/11227 11534/11803/10695\r\nf 11561/11830/10722 11560/11829/10721 12507/12979/11229 12508/12980/11230\r\nf 11541/11810/10702 12500/12970/11222 12507/12979/11229 11560/11829/10721\r\nf 12502/12973/11224 12509/12981/11231 12507/12982/11229 12500/12974/11222\r\nf 12510/12983/11232 12508/12984/11230 12507/12982/11229 12509/12981/11231\r\nf 12511/12985/11233 12512/12986/11234 12513/12987/11235 12514/12988/11236\r\nf 12503/12975/11225 12506/12978/11228 12513/12987/11235 12512/12986/11234\r\nf 11538/11807/10699 11554/11823/10715 12513/12987/11235 12506/12978/11228\r\nf 11558/11827/10719 12514/12988/11236 12513/12987/11235 11554/11823/10715\r\nf 11581/11850/10742 11580/11849/10741 12515/12989/11237 12516/12990/11238\r\nf 11561/11830/10722 12508/12980/11230 12515/12989/11237 11580/11849/10741\r\nf 12510/12983/11232 12517/12991/11239 12515/12992/11237 12508/12984/11230\r\nf 12518/12993/11238 12516/12994/11238 12515/12992/11237 12517/12991/11239\r\nf 12519/12995/11240 12520/12996/11241 12521/12997/11242 12522/12998/11243\r\nf 12511/12985/11233 12514/12988/11236 12521/12997/11242 12520/12996/11241\r\nf 11558/11827/10719 11574/11843/10735 12521/12997/11242 12514/12988/11236\r\nf 11578/11847/10739 12522/12998/11243 12521/12997/11242 11574/11843/10735\r\nf 11601/11870/10762 11600/11869/10761 12523/12999/10761 12524/13000/10762\r\nf 11581/11850/10742 12516/12990/11238 12523/12999/10761 11600/11869/10761\r\nf 12518/12993/11238 12525/13001/10761 12523/13002/10761 12516/12994/11238\r\nf 12526/13003/10762 12524/13004/10762 12523/13002/10761 12525/13001/10761\r\nf 12527/13005/10772 12528/13006/10771 12529/13007/11244 12530/13008/11245\r\nf 12519/12995/11240 12522/12998/11243 12529/13007/11244 12528/13006/10771\r\nf 11578/11847/10739 11594/11863/10755 12529/13007/11244 12522/12998/11243\r\nf 11598/11867/10759 12530/13008/11245 12529/13007/11244 11594/11863/10755\r\nf 11621/11890/10782 11620/11889/10781 12531/13009/10781 12532/13010/10782\r\nf 11601/11870/10762 12524/13000/10762 12531/13009/10781 11620/11889/10781\r\nf 12526/13003/10762 12533/13011/10781 12531/13012/10781 12524/13004/10762\r\nf 12534/13013/10782 12532/13014/10782 12531/13012/10781 12533/13011/10781\r\nf 12535/13015/10792 12536/13016/10791 12537/13017/11246 12538/13018/11247\r\nf 12527/13005/10772 12530/13008/11245 12537/13017/11246 12536/13016/10791\r\nf 11598/11867/10759 11614/11883/10775 12537/13017/11246 12530/13008/11245\r\nf 11618/11887/10779 12538/13018/11247 12537/13017/11246 11614/11883/10775\r\nf 11641/11910/10802 11640/11909/10801 12539/13019/10801 12540/13020/10802\r\nf 11621/11890/10782 12532/13010/10782 12539/13019/10801 11640/11909/10801\r\nf 12534/13013/10782 12541/13021/10801 12539/13022/10801 12532/13014/10782\r\nf 12542/13023/10802 12540/13024/10802 12539/13022/10801 12541/13021/10801\r\nf 12543/13025/10812 12544/13026/10811 12545/13027/11248 12546/13028/11249\r\nf 12535/13015/10792 12538/13018/11247 12545/13027/11248 12544/13026/10811\r\nf 11618/11887/10779 11634/11903/10795 12545/13027/11248 12538/13018/11247\r\nf 11638/11907/10799 12546/13028/11249 12545/13027/11248 11634/11903/10795\r\nf 11661/11930/10822 11660/11929/10821 12547/13029/10821 12548/13030/10822\r\nf 11641/11910/10802 12540/13020/10802 12547/13029/10821 11660/11929/10821\r\nf 12542/13023/10802 12549/13031/10821 12547/13032/10821 12540/13024/10802\r\nf 12550/13033/11250 12548/13034/10822 12547/13032/10821 12549/13031/10821\r\nf 12551/13035/11251 12552/13036/10831 12553/13037/11252 12554/13038/11253\r\nf 12543/13025/10812 12546/13028/11249 12553/13037/11252 12552/13036/10831\r\nf 11638/11907/10799 11654/11923/10815 12553/13037/11252 12546/13028/11249\r\nf 11658/11927/10819 12554/13038/11253 12553/13037/11252 11654/11923/10815\r\nf 11681/11950/10842 11680/11949/10841 12555/13039/11254 12556/13040/11255\r\nf 11661/11930/10822 12548/13030/10822 12555/13039/11254 11680/11949/10841\r\nf 12550/13033/11250 12557/13041/11256 12555/13042/11254 12548/13034/10822\r\nf 11696/11965/10857 12556/13043/11255 12555/13042/11254 12557/13041/11256\r\nf 12558/13044/11257 12559/13045/11258 12560/13046/11259 12561/13047/11260\r\nf 12551/13035/11251 12554/13038/11253 12560/13046/11259 12559/13045/11258\r\nf 11658/11927/10819 11674/11943/10835 12560/13046/11259 12554/13038/11253\r\nf 11678/11947/10839 12561/13047/11260 12560/13046/11259 11674/11943/10835\r\nf 11681/11950/10842 12562/13048/10856 12563/13049/11261 11682/11951/10843\r\nf 12558/13044/11257 12561/13047/11260 12563/13049/11261 12562/13048/10856\r\nf 11678/11947/10839 11677/11946/10838 12563/13049/11261 12561/13047/11260\r\nf 11676/11945/10837 11682/11951/10843 12563/13049/11261 11677/11946/10838\r\nf 9536/9805/8709 9535/9804/8708 12564/13050/10858 12565/13051/8709\r\nf 9534/9803/8707 12566/13052/8707 12564/13050/10858 9535/9804/8708\r\nf 11701/11970/10861 11698/11967/10858 12564/13053/10858 12566/13054/8707\r\nf 11697/11966/8709 12565/13055/8709 12564/13053/10858 11698/11967/10858\r\nf 9561/9830/8733 9560/9829/8732 12567/13056/8732 12568/13057/10865\r\nf 9536/9805/8709 12565/13051/8709 12567/13056/8732 9560/9829/8732\r\nf 11697/11966/8709 11709/11981/8732 12567/13058/8732 12565/13055/8709\r\nf 11708/11980/10865 12568/13059/10865 12567/13058/8732 11709/11981/8732\r\nf 9581/9850/8752 9580/9849/8751 12569/13060/8751 12570/13061/8752\r\nf 9561/9830/8733 12568/13057/10865 12569/13060/8751 9580/9849/8751\r\nf 11708/11980/10865 11717/11991/8751 12569/13062/8751 12568/13059/10865\r\nf 11716/11990/8752 12570/13063/8752 12569/13062/8751 11717/11991/8751\r\nf 9601/9870/8772 9600/9869/8771 12571/13064/8771 12572/13065/8772\r\nf 9581/9850/8752 12570/13061/8752 12571/13064/8771 9600/9869/8771\r\nf 11716/11990/8752 11725/12001/8771 12571/13066/8771 12570/13063/8752\r\nf 11724/12000/8772 12572/13067/8772 12571/13066/8771 11725/12001/8771\r\nf 9621/9890/8792 9620/9889/8791 12573/13068/8791 12574/13069/8792\r\nf 9601/9870/8772 12572/13065/8772 12573/13068/8791 9620/9889/8791\r\nf 11724/12000/8772 11733/12011/8791 12573/13070/8791 12572/13067/8772\r\nf 11732/12010/8792 12574/13071/8792 12573/13070/8791 11733/12011/8791\r\nf 9641/9910/8812 9640/9909/8811 12575/13072/8811 12576/13073/8812\r\nf 9621/9890/8792 12574/13069/8792 12575/13072/8811 9640/9909/8811\r\nf 11732/12010/8792 11741/12021/8811 12575/13074/8811 12574/13071/8792\r\nf 11740/12020/8812 12576/13075/8812 12575/13074/8811 11741/12021/8811\r\nf 9661/9930/8832 9660/9929/8831 12577/13076/8831 12578/13077/11262\r\nf 9641/9910/8812 12576/13073/8812 12577/13076/8831 9660/9929/8831\r\nf 11740/12020/8812 11749/12031/10878 12577/13078/8831 12576/13075/8812\r\nf 11748/12030/8832 12578/13079/11262 12577/13078/8831 11749/12031/10878\r\nf 9681/9950/8852 9680/9949/8851 12579/13080/8851 12580/13081/10882\r\nf 9661/9930/8832 12578/13077/11262 12579/13080/8851 9680/9949/8851\r\nf 11748/12030/8832 11757/12041/8851 12579/13082/8851 12578/13079/11262\r\nf 11756/12040/10882 12580/13083/10882 12579/13082/8851 11757/12041/8851\r\nf 9701/9970/8872 9700/9969/8871 12581/13084/8871 12582/13085/8872\r\nf 9681/9950/8852 12580/13081/10882 12581/13084/8871 9700/9969/8871\r\nf 11756/12040/10882 11765/12051/10887 12581/13086/8871 12580/13083/10882\r\nf 11764/12050/8872 12582/13087/8872 12581/13086/8871 11765/12051/10887\r\nf 9721/9990/8892 9720/9989/8891 12583/13088/10890 12584/13089/8892\r\nf 9701/9970/8872 12582/13085/8872 12583/13088/10890 9720/9989/8891\r\nf 11764/12050/8872 11773/12061/10890 12583/13090/10890 12582/13087/8872\r\nf 11772/12060/8892 12584/13091/8892 12583/13090/10890 11773/12061/10890\r\nf 9741/10010/8912 9740/10009/8911 12585/13092/8911 12586/13093/8912\r\nf 9721/9990/8892 12584/13089/8892 12585/13092/8911 9740/10009/8911\r\nf 11772/12060/8892 11781/12071/10894 12585/13094/8911 12584/13091/8892\r\nf 11780/12070/8912 12586/13095/8912 12585/13094/8911 11781/12071/10894\r\nf 9761/10030/8932 9760/10029/8931 12587/13096/8931 12588/13097/8932\r\nf 9741/10010/8912 12586/13093/8912 12587/13096/8931 9760/10029/8931\r\nf 11780/12070/8912 11789/12081/8931 12587/13098/8931 12586/13095/8912\r\nf 11788/12080/8932 12588/13099/8932 12587/13098/8931 11789/12081/8931\r\nf 9781/10050/8952 9780/10049/8951 12589/13100/8951 12590/13101/8952\r\nf 9761/10030/8932 12588/13097/8932 12589/13100/8951 9780/10049/8951\r\nf 11788/12080/8932 11797/12091/8951 12589/13102/8951 12588/13099/8932\r\nf 11796/12090/8952 12590/13103/8952 12589/13102/8951 11797/12091/8951\r\nf 9801/10070/8972 9800/10069/8971 12591/13104/8971 12592/13105/8972\r\nf 9781/10050/8952 12590/13101/8952 12591/13104/8971 9800/10069/8971\r\nf 11796/12090/8952 11805/12101/8971 12591/13106/8971 12590/13103/8952\r\nf 11804/12100/8972 12592/13107/8972 12591/13106/8971 11805/12101/8971\r\nf 9821/10090/8992 9820/10089/8991 12593/13108/8991 12594/13109/8992\r\nf 9801/10070/8972 12592/13105/8972 12593/13108/8991 9820/10089/8991\r\nf 11804/12100/8972 11813/12111/8991 12593/13110/8991 12592/13107/8972\r\nf 11812/12110/8992 12594/13111/8992 12593/13110/8991 11813/12111/8991\r\nf 9841/10110/9012 9840/10109/9011 12595/13112/9011 12596/13113/9012\r\nf 9821/10090/8992 12594/13109/8992 12595/13112/9011 9840/10109/9011\r\nf 11812/12110/8992 11821/12121/10908 12595/13114/9011 12594/13111/8992\r\nf 11820/12120/9012 12596/13115/9012 12595/13114/9011 11821/12121/10908\r\nf 9861/10130/9032 9860/10129/9031 12597/13116/9031 12598/13117/9032\r\nf 9841/10110/9012 12596/13113/9012 12597/13116/9031 9860/10129/9031\r\nf 11820/12120/9012 11829/12131/9031 12597/13118/9031 12596/13115/9012\r\nf 11828/12130/9032 12598/13119/9032 12597/13118/9031 11829/12131/9031\r\nf 9881/10150/9052 9880/10149/9051 12599/13120/9051 12600/13121/9052\r\nf 9861/10130/9032 12598/13117/9032 12599/13120/9051 9880/10149/9051\r\nf 11828/12130/9032 11837/12141/9051 12599/13122/9051 12598/13119/9032\r\nf 11836/12140/9052 12600/13123/9052 12599/13122/9051 11837/12141/9051\r\nf 9901/10170/9071 9900/10169/9070 12601/13124/9070 12602/13125/9071\r\nf 9881/10150/9052 12600/13121/9052 12601/13124/9070 9900/10169/9070\r\nf 11836/12140/9052 11845/12151/9070 12601/13126/9070 12600/13123/9052\r\nf 11844/12150/9071 12602/13127/9071 12601/13126/9070 11845/12151/9070\r\nf 9921/10190/9090 9920/10189/9089 12603/13128/9089 12604/13129/9090\r\nf 9901/10170/9071 12602/13125/9071 12603/13128/9089 9920/10189/9089\r\nf 11844/12150/9071 11853/12161/9089 12603/13130/9089 12602/13127/9071\r\nf 11852/12160/9090 12604/13131/9090 12603/13130/9089 11853/12161/9089\r\nf 9941/10210/9110 9940/10209/9109 12605/13132/9109 12606/13133/9110\r\nf 9921/10190/9090 12604/13129/9090 12605/13132/9109 9940/10209/9109\r\nf 11852/12160/9090 11861/12171/9109 12605/13134/9109 12604/13131/9090\r\nf 11860/12170/9110 12606/13135/9110 12605/13134/9109 11861/12171/9109\r\nf 9961/10230/9130 9960/10229/9129 12607/13136/9129 12608/13137/9130\r\nf 9941/10210/9110 12606/13133/9110 12607/13136/9129 9960/10229/9129\r\nf 11860/12170/9110 11869/12181/9129 12607/13138/9129 12606/13135/9110\r\nf 11868/12180/9130 12608/13139/9130 12607/13138/9129 11869/12181/9129\r\nf 9981/10250/9150 9980/10249/9149 12609/13140/9149 12610/13141/9150\r\nf 9961/10230/9130 12608/13137/9130 12609/13140/9149 9980/10249/9149\r\nf 11868/12180/9130 11877/12191/9149 12609/13142/9149 12608/13139/9130\r\nf 11876/12190/9150 12610/13143/9150 12609/13142/9149 11877/12191/9149\r\nf 10001/10270/9170 10000/10269/9169 12611/13144/9169 12612/13145/9170\r\nf 9981/10250/9150 12610/13141/9150 12611/13144/9169 10000/10269/9169\r\nf 11876/12190/9150 11885/12201/9169 12611/13146/9169 12610/13143/9150\r\nf 11884/12200/9170 12612/13147/9170 12611/13146/9169 11885/12201/9169\r\nf 10021/10290/9190 10020/10289/9189 12613/13148/9189 12614/13149/9190\r\nf 10001/10270/9170 12612/13145/9170 12613/13148/9189 10020/10289/9189\r\nf 11884/12200/9170 11893/12211/9189 12613/13150/9189 12612/13147/9170\r\nf 11892/12210/9190 12614/13151/9190 12613/13150/9189 11893/12211/9189\r\nf 10041/10310/9210 10040/10309/9209 12615/13152/9209 12616/13153/9210\r\nf 10021/10290/9190 12614/13149/9190 12615/13152/9209 10040/10309/9209\r\nf 11892/12210/9190 11901/12221/9209 12615/13154/9209 12614/13151/9190\r\nf 11900/12220/9210 12616/13155/9210 12615/13154/9209 11901/12221/9209\r\nf 10061/10330/9230 10060/10329/9229 12617/13156/9229 12618/13157/9230\r\nf 10041/10310/9210 12616/13153/9210 12617/13156/9229 10060/10329/9229\r\nf 11900/12220/9210 11909/12231/9229 12617/13158/9229 12616/13155/9210\r\nf 11908/12230/9230 12618/13159/9230 12617/13158/9229 11909/12231/9229\r\nf 10081/10350/9250 10080/10349/9249 12619/13160/9249 12620/13161/9250\r\nf 10061/10330/9230 12618/13157/9230 12619/13160/9249 10080/10349/9249\r\nf 11908/12230/9230 11917/12241/9249 12619/13162/9249 12618/13159/9230\r\nf 11916/12240/9250 12620/13163/9250 12619/13162/9249 11917/12241/9249\r\nf 10101/10370/9270 10100/10369/9269 12621/13164/9269 12622/13165/9270\r\nf 10081/10350/9250 12620/13161/9250 12621/13164/9269 10100/10369/9269\r\nf 11916/12240/9250 11925/12251/9269 12621/13166/9269 12620/13163/9250\r\nf 11924/12250/9270 12622/13167/9270 12621/13166/9269 11925/12251/9269\r\nf 10121/10390/9290 10120/10389/9289 12623/13168/10939 12624/13169/9290\r\nf 10101/10370/9270 12622/13165/9270 12623/13168/10939 10120/10389/9289\r\nf 11924/12250/9270 11933/12261/10939 12623/13170/10939 12622/13167/9270\r\nf 11932/12260/9290 12624/13171/9290 12623/13170/10939 11933/12261/10939\r\nf 10141/10410/9310 10140/10409/9309 12625/13172/9309 12626/13173/9310\r\nf 10121/10390/9290 12624/13169/9290 12625/13172/9309 10140/10409/9309\r\nf 11932/12260/9290 11941/12271/9309 12625/13174/9309 12624/13171/9290\r\nf 11940/12270/10943 12626/13175/9310 12625/13174/9309 11941/12271/9309\r\nf 10161/10430/9330 10160/10429/9329 12627/13176/10949 12628/13177/11263\r\nf 10141/10410/9310 12626/13173/9310 12627/13176/10949 10160/10429/9329\r\nf 11940/12270/10943 11949/12281/10949 12627/13178/10949 12626/13175/9310\r\nf 11948/12280/10948 12628/13179/11263 12627/13178/10949 11949/12281/10949\r\nf 10181/10450/9350 10180/10449/9349 12629/13180/11264 12630/13181/11265\r\nf 10161/10430/9330 12628/13177/11263 12629/13180/11264 10180/10449/9349\r\nf 11948/12280/10948 11957/12291/10956 12629/13182/11264 12628/13179/11263\r\nf 11956/12290/10955 12630/13183/11265 12629/13182/11264 11957/12291/10956\r\nf 10237/10506/9406 10236/10505/9405 12631/13184/11266 12632/13185/11267\r\nf 10235/10504/9404 12633/13186/11268 12631/13184/11266 10236/10505/9405\r\nf 11976/12316/10973 11973/12313/10970 12631/13187/11266 12633/13188/11268\r\nf 11972/12312/10969 12632/13189/11267 12631/13187/11266 11973/12313/10970\r\nf 10257/10526/9426 10256/10525/9425 12634/13190/11269 12635/13191/10979\r\nf 10237/10506/9406 12632/13185/11267 12634/13190/11269 10256/10525/9425\r\nf 11972/12312/10969 11983/12325/10980 12634/13192/11269 12632/13189/11267\r\nf 11982/12324/10979 12635/13193/10979 12634/13192/11269 11983/12325/10980\r\nf 10277/10546/9446 10276/10545/9445 12636/13194/9445 12637/13195/9446\r\nf 10257/10526/9426 12635/13191/10979 12636/13194/9445 10276/10545/9445\r\nf 11982/12324/10979 11991/12335/10987 12636/13196/9445 12635/13193/10979\r\nf 11990/12334/10986 12637/13197/9446 12636/13196/9445 11991/12335/10987\r\nf 10297/10566/9466 10296/10565/9465 12638/13198/9465 12639/13199/9466\r\nf 10277/10546/9446 12637/13195/9446 12638/13198/9465 10296/10565/9465\r\nf 11990/12334/10986 11999/12345/9465 12638/13200/9465 12637/13197/9446\r\nf 11998/12344/9466 12639/13201/9466 12638/13200/9465 11999/12345/9465\r\nf 10317/10586/9486 10316/10585/9485 12640/13202/9485 12641/13203/9486\r\nf 10297/10566/9466 12639/13199/9466 12640/13202/9485 10316/10585/9485\r\nf 11998/12344/9466 12007/12355/9485 12640/13204/9485 12639/13201/9466\r\nf 12006/12354/9486 12641/13205/9486 12640/13204/9485 12007/12355/9485\r\nf 10337/10606/9506 10336/10605/9505 12642/13206/9505 12643/13207/9506\r\nf 10317/10586/9486 12641/13203/9486 12642/13206/9505 10336/10605/9505\r\nf 12006/12354/9486 12015/12365/9505 12642/13208/9505 12641/13205/9486\r\nf 12014/12364/9506 12643/13209/9506 12642/13208/9505 12015/12365/9505\r\nf 10357/10626/9526 10356/10625/9525 12644/13210/9525 12645/13211/9526\r\nf 10337/10606/9506 12643/13207/9506 12644/13210/9525 10356/10625/9525\r\nf 12014/12364/9506 12023/12375/9525 12644/13212/9525 12643/13209/9506\r\nf 12022/12374/10999 12645/13213/9526 12644/13212/9525 12023/12375/9525\r\nf 10377/10646/9546 10376/10645/9545 12646/13214/9545 12647/13215/9546\r\nf 10357/10626/9526 12645/13211/9526 12646/13214/9545 10376/10645/9545\r\nf 12022/12374/10999 12031/12385/9545 12646/13216/9545 12645/13213/9526\r\nf 12030/12384/9546 12647/13217/9546 12646/13216/9545 12031/12385/9545\r\nf 10397/10666/9566 10396/10665/9565 12648/13218/9565 12649/13219/9566\r\nf 10377/10646/9546 12647/13215/9546 12648/13218/9565 10396/10665/9565\r\nf 12030/12384/9546 12039/12395/9565 12648/13220/9565 12647/13217/9546\r\nf 12038/12394/9566 12649/13221/9566 12648/13220/9565 12039/12395/9565\r\nf 10417/10686/9586 10416/10685/9585 12650/13222/9585 12651/13223/9586\r\nf 10397/10666/9566 12649/13219/9566 12650/13222/9585 10416/10685/9585\r\nf 12038/12394/9566 12047/12405/11009 12650/13224/9585 12649/13221/9566\r\nf 12046/12404/9586 12651/13225/9586 12650/13224/9585 12047/12405/11009\r\nf 10437/10706/9606 10436/10705/9605 12652/13226/9605 12653/13227/9606\r\nf 10417/10686/9586 12651/13223/9586 12652/13226/9605 10436/10705/9605\r\nf 12046/12404/9586 12055/12415/9605 12652/13228/9605 12651/13225/9586\r\nf 12054/12414/9606 12653/13229/9606 12652/13228/9605 12055/12415/9605\r\nf 10457/10726/9626 10456/10725/9625 12654/13230/9625 12655/13231/9626\r\nf 10437/10706/9606 12653/13227/9606 12654/13230/9625 10456/10725/9625\r\nf 12054/12414/9606 12063/12425/9625 12654/13232/9625 12653/13229/9606\r\nf 12062/12424/9626 12655/13233/9626 12654/13232/9625 12063/12425/9625\r\nf 10477/10746/9646 10476/10745/9645 12656/13234/9645 12657/13235/9646\r\nf 10457/10726/9626 12655/13231/9626 12656/13234/9645 10476/10745/9645\r\nf 12062/12424/9626 12071/12435/9645 12656/13236/9645 12655/13233/9626\r\nf 12070/12434/9646 12657/13237/9646 12656/13236/9645 12071/12435/9645\r\nf 10497/10766/9666 10496/10765/9665 12658/13238/11270 12659/13239/9666\r\nf 10477/10746/9646 12657/13235/9646 12658/13238/11270 10496/10765/9665\r\nf 12070/12434/9646 12079/12445/9665 12658/13240/11270 12657/13237/9646\r\nf 12078/12444/9666 12659/13241/9666 12658/13240/11270 12079/12445/9665\r\nf 10517/10786/9686 10516/10785/9685 12660/13242/9685 12661/13243/9686\r\nf 10497/10766/9666 12659/13239/9666 12660/13242/9685 10516/10785/9685\r\nf 12078/12444/9666 12087/12455/11022 12660/13244/9685 12659/13241/9666\r\nf 12086/12454/11021 12661/13245/9686 12660/13244/9685 12087/12455/11022\r\nf 10537/10806/9706 10536/10805/9705 12662/13246/9705 12663/13247/9706\r\nf 10517/10786/9686 12661/13243/9686 12662/13246/9705 10536/10805/9705\r\nf 12086/12454/11021 12095/12465/9705 12662/13248/9705 12661/13245/9686\r\nf 12094/12464/9706 12663/13249/9706 12662/13248/9705 12095/12465/9705\r\nf 10557/10826/9726 10556/10825/9725 12664/13250/9725 12665/13251/9726\r\nf 10537/10806/9706 12663/13247/9706 12664/13250/9725 10556/10825/9725\r\nf 12094/12464/9706 12103/12475/9725 12664/13252/9725 12663/13249/9706\r\nf 12102/12474/9726 12665/13253/9726 12664/13252/9725 12103/12475/9725\r\nf 10577/10846/9746 10576/10845/9745 12666/13254/9745 12667/13255/9746\r\nf 10557/10826/9726 12665/13251/9726 12666/13254/9745 10576/10845/9745\r\nf 12102/12474/9726 12111/12485/9745 12666/13256/9745 12665/13253/9726\r\nf 12110/12484/9746 12667/13257/9746 12666/13256/9745 12111/12485/9745\r\nf 10597/10866/9766 10596/10865/9765 12668/13258/9765 12669/13259/9766\r\nf 10577/10846/9746 12667/13255/9746 12668/13258/9765 10596/10865/9765\r\nf 12110/12484/9746 12119/12495/9765 12668/13260/9765 12667/13257/9746\r\nf 12118/12494/11032 12669/13261/9766 12668/13260/9765 12119/12495/9765\r\nf 10617/10886/9786 10616/10885/9785 12670/13262/9785 12671/13263/9786\r\nf 10597/10866/9766 12669/13259/9766 12670/13262/9785 10616/10885/9785\r\nf 12118/12494/11032 12127/12505/9785 12670/13264/9785 12669/13261/9766\r\nf 12126/12504/9786 12671/13265/9786 12670/13264/9785 12127/12505/9785\r\nf 10637/10906/9806 10636/10905/9805 12672/13266/9805 12673/13267/9806\r\nf 10617/10886/9786 12671/13263/9786 12672/13266/9805 10636/10905/9805\r\nf 12126/12504/9786 12135/12515/9805 12672/13268/9805 12671/13265/9786\r\nf 12134/12514/9806 12673/13269/9806 12672/13268/9805 12135/12515/9805\r\nf 10657/10926/9826 10656/10925/9825 12674/13270/9825 12675/13271/9826\r\nf 10637/10906/9806 12673/13267/9806 12674/13270/9825 10656/10925/9825\r\nf 12134/12514/9806 12143/12525/9825 12674/13272/9825 12673/13269/9806\r\nf 12142/12524/9826 12675/13273/9826 12674/13272/9825 12143/12525/9825\r\nf 10677/10946/9846 10676/10945/9845 12676/13274/9845 12677/13275/11271\r\nf 10657/10926/9826 12675/13271/9826 12676/13274/9845 10676/10945/9845\r\nf 12142/12524/9826 12151/12535/9845 12676/13276/9845 12675/13273/9826\r\nf 12150/12534/9846 12677/13277/11271 12676/13276/9845 12151/12535/9845\r\nf 10697/10966/9866 10696/10965/9865 12678/13278/9865 12679/13279/9866\r\nf 10677/10946/9846 12677/13275/11271 12678/13278/9865 10696/10965/9865\r\nf 12150/12534/9846 12159/12545/9865 12678/13280/9865 12677/13277/11271\r\nf 12158/12544/9866 12679/13281/9866 12678/13280/9865 12159/12545/9865\r\nf 10717/10986/9886 10716/10985/9885 12680/13282/9885 12681/13283/9886\r\nf 10697/10966/9866 12679/13279/9866 12680/13282/9885 10716/10985/9885\r\nf 12158/12544/9866 12167/12555/9885 12680/13284/9885 12679/13281/9866\r\nf 12166/12554/9886 12681/13285/9886 12680/13284/9885 12167/12555/9885\r\nf 10737/11006/9905 10736/11005/9886 12682/13286/9886 12683/13287/9905\r\nf 10717/10986/9886 12681/13283/9886 12682/13286/9886 10736/11005/9886\r\nf 12166/12554/9886 12175/12565/9886 12682/13288/9886 12681/13285/9886\r\nf 12174/12564/9905 12683/13289/9905 12682/13288/9886 12175/12565/9886\r\nf 10757/11026/9924 10756/11025/9923 12684/13290/9923 12685/13291/11053\r\nf 10737/11006/9905 12683/13287/9905 12684/13290/9923 10756/11025/9923\r\nf 12174/12564/9905 12183/12575/9923 12684/13292/9923 12683/13289/9905\r\nf 12182/12574/11053 12685/13293/11053 12684/13292/9923 12183/12575/9923\r\nf 10777/11046/9944 10776/11045/9943 12686/13294/9943 12687/13295/11057\r\nf 10757/11026/9924 12685/13291/11053 12686/13294/9943 10776/11045/9943\r\nf 12182/12574/11053 12191/12585/9943 12686/13296/9943 12685/13293/11053\r\nf 12190/12584/11057 12687/13297/11057 12686/13296/9943 12191/12585/9943\r\nf 10797/11066/9964 10796/11065/9963 12688/13298/11272 12689/13299/11273\r\nf 10777/11046/9944 12687/13295/11057 12688/13298/11272 10796/11065/9963\r\nf 12190/12584/11057 12199/12595/11064 12688/13300/11272 12687/13297/11057\r\nf 12198/12594/11063 12689/13301/11273 12688/13300/11272 12199/12595/11064\r\nf 10817/11086/9984 10816/11085/9983 12690/13302/11274 12289/12710/11108\r\nf 10797/11066/9964 12689/13299/11273 12690/13302/11274 10816/11085/9983\r\nf 12198/12594/11063 12206/12604/11071 12690/13303/11274 12689/13301/11273\r\nf 11010/11279/10173 12289/12707/11108 12690/13303/11274 12206/12604/11071\r\nf 9534/9803/8707 10832/11101/9999 12691/13304/9999 12566/13052/8707\r\nf 10831/11100/9998 11705/11977/8722 12691/13304/9999 10832/11101/9999\r\nf 9549/9818/8722 12212/12612/11077 12691/13305/9999 11705/11974/8722\r\nf 11701/11970/10861 12566/13054/8707 12691/13305/9999 12212/12612/11077\r\nf 10859/11128/10026 12692/13306/11080 12693/13307/11080 12694/13308/10026\r\nf 9191/9369/8242 9190/9370/8242 12693/13307/11080 12692/13306/11080\r\nf 8718/8682/8242 12220/12622/11080 12693/13309/11080 9190/9368/8242\r\nf 12219/12621/10026 12694/13310/10026 12693/13309/11080 12220/12622/11080\r\nf 10861/11130/10028 10860/11129/10027 12695/13311/10027 12696/13312/10028\r\nf 10859/11128/10026 12694/13308/10026 12695/13311/10027 10860/11129/10027\r\nf 12219/12621/10026 12229/12633/10027 12695/13313/10027 12694/13310/10026\r\nf 12228/12632/10028 12696/13314/10028 12695/13313/10027 12229/12633/10027\r\nf 10881/11150/10048 10880/11149/10047 12697/13315/10047 12698/13316/10048\r\nf 10861/11130/10028 12696/13312/10028 12697/13315/10047 10880/11149/10047\r\nf 12228/12632/10028 12237/12643/10047 12697/13317/10047 12696/13314/10028\r\nf 12236/12642/10048 12698/13318/10048 12697/13317/10047 12237/12643/10047\r\nf 10901/11170/10068 10900/11169/10067 12699/13319/10067 12700/13320/10068\r\nf 10881/11150/10048 12698/13316/10048 12699/13319/10067 10900/11169/10067\r\nf 12236/12642/10048 12245/12653/10067 12699/13321/10067 12698/13318/10048\r\nf 12244/12652/10068 12700/13322/10068 12699/13321/10067 12245/12653/10067\r\nf 10921/11190/10086 10920/11189/10086 12701/13323/10086 12702/13324/10086\r\nf 10901/11170/10068 12700/13320/10068 12701/13323/10086 10920/11189/10086\r\nf 12244/12652/10068 12253/12663/10086 12701/13325/10086 12700/13322/10068\r\nf 12252/12662/10086 12702/13326/10086 12701/13325/10086 12253/12663/10086\r\nf 10941/11210/10104 10940/11209/10068 12703/13327/10068 12704/13328/10104\r\nf 10921/11190/10086 12702/13324/10086 12703/13327/10068 10940/11209/10068\r\nf 12252/12662/10086 12261/12673/10068 12703/13329/10068 12702/13326/10086\r\nf 12260/12672/10104 12704/13330/10104 12703/13329/10068 12261/12673/10068\r\nf 10961/11230/10124 10960/11229/10123 12705/13331/10123 12706/13332/10124\r\nf 10941/11210/10104 12704/13328/10104 12705/13331/10123 10960/11229/10123\r\nf 12260/12672/10104 12269/12683/10123 12705/13333/10123 12704/13330/10104\r\nf 12268/12682/10124 12706/13334/10124 12705/13333/10123 12269/12683/10123\r\nf 10981/11250/10144 10980/11249/10143 12707/13335/10143 12708/13336/11097\r\nf 10961/11230/10124 12706/13332/10124 12707/13335/10143 10980/11249/10143\r\nf 12268/12682/10124 12277/12693/10143 12707/13337/10143 12706/13334/10124\r\nf 12276/12692/11097 12708/13338/11097 12707/13337/10143 12277/12693/10143\r\nf 11001/11270/10164 11000/11269/10163 12709/13339/10163 12710/13340/11104\r\nf 10981/11250/10144 12708/13336/11097 12709/13339/10163 11000/11269/10163\r\nf 12276/12692/11097 12285/12703/10163 12709/13341/10163 12708/13338/11097\r\nf 12284/12702/11104 12710/13342/11104 12709/13341/10163 12285/12703/10163\r\nf 11019/11288/10182 11018/11287/10181 12711/13343/11275 12712/13344/11276\r\nf 11001/11270/10164 12710/13340/11104 12711/13343/11275 11018/11287/10181\r\nf 12284/12702/11104 12292/12712/11111 12711/13345/11275 12710/13342/11104\r\nf 12291/12711/11110 12712/13346/11276 12711/13345/11275 12292/12712/11111\r\nf 11031/11300/10194 11030/11299/10193 12713/13347/11277 12714/13348/11278\r\nf 11019/11288/10182 12712/13344/11276 12713/13347/11277 11030/11299/10193\r\nf 12291/12711/11110 12296/12716/11115 12713/13349/11277 12712/13346/11276\r\nf 12295/12715/11114 12714/13350/11278 12713/13349/11277 12296/12716/11115\r\nf 11051/11320/10214 11050/11319/10213 12715/13351/11279 12716/13352/11280\r\nf 11031/11300/10194 12714/13348/11278 12715/13351/11279 11050/11319/10213\r\nf 12295/12715/11114 12304/12726/11123 12715/13353/11279 12714/13350/11278\r\nf 12303/12725/11122 12716/13354/11280 12715/13353/11279 12304/12726/11123\r\nf 11071/11340/10234 11070/11339/10233 12717/13355/11281 12718/13356/11282\r\nf 11051/11320/10214 12716/13352/11280 12717/13355/11281 11070/11339/10233\r\nf 12303/12725/11122 12312/12736/11131 12717/13357/11281 12716/13354/11280\r\nf 12311/12735/11130 12718/13358/11282 12717/13357/11281 12312/12736/11131\r\nf 11091/11360/10254 11090/11359/10253 12719/13359/11137 12720/13360/11136\r\nf 11071/11340/10234 12718/13356/11282 12719/13359/11137 11090/11359/10253\r\nf 12311/12735/11130 12320/12746/11137 12719/13361/11137 12718/13358/11282\r\nf 12319/12745/11136 12720/13362/11136 12719/13361/11137 12320/12746/11137\r\nf 11111/11380/10274 11110/11379/10273 12721/13363/11142 12722/13364/10274\r\nf 11091/11360/10254 12720/13360/11136 12721/13363/11142 11110/11379/10273\r\nf 12319/12745/11136 12328/12756/11142 12721/13365/11142 12720/13362/11136\r\nf 12327/12755/10274 12722/13366/10274 12721/13365/11142 12328/12756/11142\r\nf 11131/11400/10294 11130/11399/10293 12723/13367/10293 12724/13368/10294\r\nf 11111/11380/10274 12722/13364/10274 12723/13367/10293 11130/11399/10293\r\nf 12327/12755/10274 12336/12766/10293 12723/13369/10293 12722/13366/10274\r\nf 12335/12765/11146 12724/13370/10294 12723/13369/10293 12336/12766/10293\r\nf 11151/11420/10314 11150/11419/10313 12725/13371/10313 12726/13372/10314\r\nf 11131/11400/10294 12724/13368/10294 12725/13371/10313 11150/11419/10313\r\nf 12335/12765/11146 12344/12776/10313 12725/13373/10313 12724/13370/10294\r\nf 12343/12775/10314 12726/13374/10314 12725/13373/10313 12344/12776/10313\r\nf 11171/11440/10334 11170/11439/10333 12727/13375/10333 12728/13376/10334\r\nf 11151/11420/10314 12726/13372/10314 12727/13375/10333 11170/11439/10333\r\nf 12343/12775/10314 12352/12786/10333 12727/13377/10333 12726/13374/10314\r\nf 12351/12785/10334 12728/13378/10334 12727/13377/10333 12352/12786/10333\r\nf 11191/11460/10354 11190/11459/10353 12729/13379/11156 12730/13380/10354\r\nf 11171/11440/10334 12728/13376/10334 12729/13379/11156 11190/11459/10353\r\nf 12351/12785/10334 12360/12796/11156 12729/13381/11156 12728/13378/10334\r\nf 12359/12795/10354 12730/13382/10354 12729/13381/11156 12360/12796/11156\r\nf 11211/11480/10374 11210/11479/10373 12731/13383/11159 12732/13384/10374\r\nf 11191/11460/10354 12730/13380/10354 12731/13383/11159 11210/11479/10373\r\nf 12359/12795/10354 12368/12806/11159 12731/13385/11159 12730/13382/10354\r\nf 12367/12805/10374 12732/13386/10374 12731/13385/11159 12368/12806/11159\r\nf 11231/11500/10394 11230/11499/10393 12733/13387/10393 12734/13388/10394\r\nf 11211/11480/10374 12732/13384/10374 12733/13387/10393 11230/11499/10393\r\nf 12367/12805/10374 12376/12816/10393 12733/13389/10393 12732/13386/10374\r\nf 12375/12815/10394 12734/13390/10394 12733/13389/10393 12376/12816/10393\r\nf 11251/11520/10394 11250/11519/10412 12735/13391/10412 12736/13392/10394\r\nf 11231/11500/10394 12734/13388/10394 12735/13391/10412 11250/11519/10412\r\nf 12375/12815/10394 12384/12826/10412 12735/13393/10412 12734/13390/10394\r\nf 12383/12825/10394 12736/13394/10394 12735/13393/10412 12384/12826/10412\r\nf 11271/11540/10432 11270/11539/10431 12737/13395/10431 12738/13396/11283\r\nf 11251/11520/10394 12736/13392/10394 12737/13395/10431 11270/11539/10431\r\nf 12383/12825/10394 12392/12836/10431 12737/13397/10431 12736/13394/10394\r\nf 12391/12835/10432 12738/13398/11283 12737/13397/10431 12392/12836/10431\r\nf 11291/11560/10452 11290/11559/10451 12739/13399/11284 12740/13400/10452\r\nf 11271/11540/10432 12738/13396/11283 12739/13399/11284 11290/11559/10451\r\nf 12391/12835/10432 12400/12846/10451 12739/13401/11284 12738/13398/11283\r\nf 12399/12845/10452 12740/13402/10452 12739/13401/11284 12400/12846/10451\r\nf 11311/11580/10472 11310/11579/10471 12741/13403/10471 12742/13404/11285\r\nf 11291/11560/10452 12740/13400/10452 12741/13403/10471 11310/11579/10471\r\nf 12399/12845/10452 12408/12856/10471 12741/13405/10471 12740/13402/10452\r\nf 12407/12855/11171 12742/13406/11285 12741/13405/10471 12408/12856/10471\r\nf 11331/11600/10492 11330/11599/10491 12743/13407/10491 12744/13408/10492\r\nf 11311/11580/10472 12742/13404/11285 12743/13407/10491 11330/11599/10491\r\nf 12407/12855/11171 12416/12866/10491 12743/13409/10491 12742/13406/11285\r\nf 12415/12865/10492 12744/13410/10492 12743/13409/10491 12416/12866/10491\r\nf 11351/11620/10512 11350/11619/10511 12745/13411/10511 12746/13412/10512\r\nf 11331/11600/10492 12744/13408/10492 12745/13411/10511 11350/11619/10511\r\nf 12415/12865/10492 12424/12876/10511 12745/13413/10511 12744/13410/10492\r\nf 12423/12875/10512 12746/13414/10512 12745/13413/10511 12424/12876/10511\r\nf 11371/11640/10532 11370/11639/10531 12747/13415/10531 12748/13416/10532\r\nf 11351/11620/10512 12746/13412/10512 12747/13415/10531 11370/11639/10531\r\nf 12423/12875/10512 12432/12886/10531 12747/13417/10531 12746/13414/10512\r\nf 12431/12885/10532 12748/13418/10532 12747/13417/10531 12432/12886/10531\r\nf 11391/11660/10552 11390/11659/10551 12749/13419/11180 12750/13420/10552\r\nf 11371/11640/10532 12748/13416/10532 12749/13419/11180 11390/11659/10551\r\nf 12431/12885/10532 12440/12896/11180 12749/13421/11180 12748/13418/10532\r\nf 12439/12895/10552 12750/13422/10552 12749/13421/11180 12440/12896/11180\r\nf 11411/11680/10572 11410/11679/10571 12751/13423/10571 12752/13424/10572\r\nf 11391/11660/10552 12750/13420/10552 12751/13423/10571 11410/11679/10571\r\nf 12439/12895/10552 12448/12906/10571 12751/13425/10571 12750/13422/10552\r\nf 12447/12905/10572 12752/13426/10572 12751/13425/10571 12448/12906/10571\r\nf 11431/11700/10592 11430/11699/10591 12753/13427/10591 12754/13428/10592\r\nf 11411/11680/10572 12752/13424/10572 12753/13427/10591 11430/11699/10591\r\nf 12447/12905/10572 12456/12916/10591 12753/13429/10591 12752/13426/10572\r\nf 12455/12915/10592 12754/13430/10592 12753/13429/10591 12456/12916/10591\r\nf 11451/11720/10612 11450/11719/10611 12755/13431/11286 12756/13432/11191\r\nf 11431/11700/10592 12754/13428/10592 12755/13431/11286 11450/11719/10611\r\nf 12455/12915/10592 12464/12926/11192 12755/13433/11286 12754/13430/10592\r\nf 12463/12925/11191 12756/13434/11191 12755/13433/11286 12464/12926/11192\r\nf 11471/11740/10632 11470/11739/10631 12757/13435/11287 12758/13436/11288\r\nf 11451/11720/10612 12756/13432/11191 12757/13435/11287 11470/11739/10631\r\nf 12463/12925/11191 12472/12936/11199 12757/13437/11287 12756/13434/11191\r\nf 12471/12935/11198 12758/13438/11288 12757/13437/11287 12472/12936/11199\r\nf 11491/11760/10652 11490/11759/10651 12759/13439/11289 12760/13440/11290\r\nf 11471/11740/10632 12758/13436/11288 12759/13439/11289 11490/11759/10651\r\nf 12471/12935/11198 12480/12946/11207 12759/13441/11289 12758/13438/11288\r\nf 12479/12945/11206 12760/13442/11290 12759/13441/11289 12480/12946/11207\r\nf 11511/11780/10672 11510/11779/10671 12761/13443/11291 12762/13444/10672\r\nf 11491/11760/10652 12760/13440/11290 12761/13443/11291 11510/11779/10671\r\nf 12479/12945/11206 12488/12956/11213 12761/13445/11291 12760/13442/11290\r\nf 12487/12955/11212 12762/13446/10672 12761/13445/11291 12488/12956/11213\r\nf 11531/11800/10692 11530/11799/10691 12763/13447/10691 12764/13448/11218\r\nf 11511/11780/10672 12762/13444/10672 12763/13447/10691 11530/11799/10691\r\nf 12487/12955/11212 12496/12966/11219 12763/13449/10691 12762/13446/10672\r\nf 12495/12965/11218 12764/13450/11218 12763/13449/10691 12496/12966/11219\r\nf 11551/11820/10712 11550/11819/10711 12765/13451/11292 12766/13452/11293\r\nf 11531/11800/10692 12764/13448/11218 12765/13451/11292 11550/11819/10711\r\nf 12495/12965/11218 12504/12976/11226 12765/13453/11292 12764/13450/11218\r\nf 12503/12975/11225 12766/13454/11293 12765/13453/11292 12504/12976/11226\r\nf 11571/11840/10732 11570/11839/10731 12767/13455/11294 12768/13456/11233\r\nf 11551/11820/10712 12766/13452/11293 12767/13455/11294 11570/11839/10731\r\nf 12503/12975/11225 12512/12986/11234 12767/13457/11294 12766/13454/11293\r\nf 12511/12985/11233 12768/13458/11233 12767/13457/11294 12512/12986/11234\r\nf 11591/11860/10752 11590/11859/10751 12769/13459/11241 12770/13460/10752\r\nf 11571/11840/10732 12768/13456/11233 12769/13459/11241 11590/11859/10751\r\nf 12511/12985/11233 12520/12996/11241 12769/13461/11241 12768/13458/11233\r\nf 12519/12995/11240 12770/13462/10752 12769/13461/11241 12520/12996/11241\r\nf 11611/11880/10772 11610/11879/10771 12771/13463/10771 12772/13464/10772\r\nf 11591/11860/10752 12770/13460/10752 12771/13463/10771 11610/11879/10771\r\nf 12519/12995/11240 12528/13006/10771 12771/13465/10771 12770/13462/10752\r\nf 12527/13005/10772 12772/13466/10772 12771/13465/10771 12528/13006/10771\r\nf 11631/11900/10792 11630/11899/10791 12773/13467/10791 12774/13468/10792\r\nf 11611/11880/10772 12772/13464/10772 12773/13467/10791 11630/11899/10791\r\nf 12527/13005/10772 12536/13016/10791 12773/13469/10791 12772/13466/10772\r\nf 12535/13015/10792 12774/13470/10792 12773/13469/10791 12536/13016/10791\r\nf 11651/11920/10812 11650/11919/10811 12775/13471/10811 12776/13472/10812\r\nf 11631/11900/10792 12774/13468/10792 12775/13471/10811 11650/11919/10811\r\nf 12535/13015/10792 12544/13026/10811 12775/13473/10811 12774/13470/10792\r\nf 12543/13025/10812 12776/13474/10812 12775/13473/10811 12544/13026/10811\r\nf 11671/11940/10832 11670/11939/10831 12777/13475/10831 12778/13476/10832\r\nf 11651/11920/10812 12776/13472/10812 12777/13475/10831 11670/11939/10831\r\nf 12543/13025/10812 12552/13036/10831 12777/13477/10831 12776/13474/10812\r\nf 12551/13035/11251 12778/13478/10832 12777/13477/10831 12552/13036/10831\r\nf 11691/11960/10852 11690/11959/10851 12779/13479/11295 12780/13480/11296\r\nf 11671/11940/10832 12778/13476/10832 12779/13479/11295 11690/11959/10851\r\nf 12551/13035/11251 12559/13045/11258 12779/13481/11295 12778/13478/10832\r\nf 12558/13044/11257 12780/13482/11296 12779/13481/11295 12559/13045/11258\r\nf 11696/11965/10857 11695/11964/10856 12781/13483/10856 12556/13043/11255\r\nf 11691/11960/10852 12780/13480/11296 12781/13483/10856 11695/11964/10856\r\nf 12558/13044/11257 12562/13048/10856 12781/13484/10856 12780/13482/11296\r\nf 11681/11950/10842 12556/13040/11255 12781/13484/10856 12562/13048/10856\r\nf 11707/11978/10863 11706/11975/10864 12782/13485/11297 12783/13486/11298\r\nf 10831/11100/9998 10830/11099/9997 12782/13485/11297 11706/11975/10864\r\nf 9523/9792/8696 9526/9795/8699 12782/13485/11297 10830/11099/9997\r\nf 9531/9800/8704 12783/13486/11298 12782/13485/11297 9526/9795/8699\r\nf 11715/11988/8742 11714/11986/8741 12784/13487/11299 12785/13488/11300\r\nf 11707/11978/10863 12783/13486/11298 12784/13487/11299 11714/11986/8741\r\nf 9531/9800/8704 9554/9823/8726 12784/13487/11299 12783/13486/11298\r\nf 9558/9827/8730 12785/13488/11300 12784/13487/11299 9554/9823/8726\r\nf 11723/11998/8762 11722/11996/8761 12786/13489/11301 12787/13490/11302\r\nf 11715/11988/8742 12785/13488/11300 12786/13489/11301 11722/11996/8761\r\nf 9558/9827/8730 9574/9843/8745 12786/13489/11301 12785/13488/11300\r\nf 9578/9847/8749 12787/13490/11302 12786/13489/11301 9574/9843/8745\r\nf 11731/12008/8782 11730/12006/10873 12788/13491/11303 12789/13492/11304\r\nf 11723/11998/8762 12787/13490/11302 12788/13491/11303 11730/12006/10873\r\nf 9578/9847/8749 9594/9863/8765 12788/13491/11303 12787/13490/11302\r\nf 9598/9867/8769 12789/13492/11304 12788/13491/11303 9594/9863/8765\r\nf 11739/12018/8802 11738/12016/8801 12790/13493/11305 12791/13494/11306\r\nf 11731/12008/8782 12789/13492/11304 12790/13493/11305 11738/12016/8801\r\nf 9598/9867/8769 9614/9883/8785 12790/13493/11305 12789/13492/11304\r\nf 9618/9887/8789 12791/13494/11306 12790/13493/11305 9614/9883/8785\r\nf 11747/12028/8822 11746/12026/8821 12792/13495/11307 12793/13496/11308\r\nf 11739/12018/8802 12791/13494/11306 12792/13495/11307 11746/12026/8821\r\nf 9618/9887/8789 9634/9903/8805 12792/13495/11307 12791/13494/11306\r\nf 9638/9907/8809 12793/13496/11308 12792/13495/11307 9634/9903/8805\r\nf 11755/12038/8842 11754/12036/10881 12794/13497/11309 12795/13498/11310\r\nf 11747/12028/8822 12793/13496/11308 12794/13497/11309 11754/12036/10881\r\nf 9638/9907/8809 9654/9923/8825 12794/13497/11309 12793/13496/11308\r\nf 9658/9927/8829 12795/13498/11310 12794/13497/11309 9654/9923/8825\r\nf 11763/12048/10885 11762/12046/10886 12796/13499/11311 12797/13500/11312\r\nf 11755/12038/8842 12795/13498/11310 12796/13499/11311 11762/12046/10886\r\nf 9658/9927/8829 9674/9943/8845 12796/13499/11311 12795/13498/11310\r\nf 9678/9947/8849 12797/13500/11312 12796/13499/11311 9674/9943/8845\r\nf 11771/12058/8882 11770/12056/8881 12798/13501/11313 12799/13502/11314\r\nf 11763/12048/10885 12797/13500/11312 12798/13501/11313 11770/12056/8881\r\nf 9678/9947/8849 9694/9963/8865 12798/13501/11313 12797/13500/11312\r\nf 9698/9967/8869 12799/13502/11314 12798/13501/11313 9694/9963/8865\r\nf 11779/12068/8902 11778/12066/10893 12800/13503/11315 12801/13504/11316\r\nf 11771/12058/8882 12799/13502/11314 12800/13503/11315 11778/12066/10893\r\nf 9698/9967/8869 9714/9983/8885 12800/13503/11315 12799/13502/11314\r\nf 9718/9987/8889 12801/13504/11316 12800/13503/11315 9714/9983/8885\r\nf 11787/12078/8922 11786/12076/10897 12802/13505/11317 12803/13506/11318\r\nf 11779/12068/8902 12801/13504/11316 12802/13505/11317 11786/12076/10897\r\nf 9718/9987/8889 9734/10003/8905 12802/13505/11317 12801/13504/11316\r\nf 9738/10007/8909 12803/13506/11318 12802/13505/11317 9734/10003/8905\r\nf 11795/12088/8942 11794/12086/8941 12804/13507/11319 12805/13508/11320\r\nf 11787/12078/8922 12803/13506/11318 12804/13507/11319 11794/12086/8941\r\nf 9738/10007/8909 9754/10023/8925 12804/13507/11319 12803/13506/11318\r\nf 9758/10027/8929 12805/13508/11320 12804/13507/11319 9754/10023/8925\r\nf 11803/12098/8962 11802/12096/8961 12806/13509/11321 12807/13510/11322\r\nf 11795/12088/8942 12805/13508/11320 12806/13509/11321 11802/12096/8961\r\nf 9758/10027/8929 9774/10043/8945 12806/13509/11321 12805/13508/11320\r\nf 9778/10047/8949 12807/13510/11322 12806/13509/11321 9774/10043/8945\r\nf 11811/12108/8982 11810/12106/10905 12808/13511/11323 12809/13512/11324\r\nf 11803/12098/8962 12807/13510/11322 12808/13511/11323 11810/12106/10905\r\nf 9778/10047/8949 9794/10063/8965 12808/13511/11323 12807/13510/11322\r\nf 9798/10067/8969 12809/13512/11324 12808/13511/11323 9794/10063/8965\r\nf 11819/12118/9002 11818/12116/9001 12810/13513/11325 12811/13514/11326\r\nf 11811/12108/8982 12809/13512/11324 12810/13513/11325 11818/12116/9001\r\nf 9798/10067/8969 9814/10083/8985 12810/13513/11325 12809/13512/11324\r\nf 9818/10087/8989 12811/13514/11326 12810/13513/11325 9814/10083/8985\r\nf 11827/12128/9022 11826/12126/9021 12812/13515/11327 12813/13516/11328\r\nf 11819/12118/9002 12811/13514/11326 12812/13515/11327 11826/12126/9021\r\nf 9818/10087/8989 9834/10103/9005 12812/13515/11327 12811/13514/11326\r\nf 9838/10107/9009 12813/13516/11328 12812/13515/11327 9834/10103/9005\r\nf 11835/12138/9042 11834/12136/9041 12814/13517/11329 12815/13518/11330\r\nf 11827/12128/9022 12813/13516/11328 12814/13517/11329 11834/12136/9041\r\nf 9838/10107/9009 9854/10123/9025 12814/13517/11329 12813/13516/11328\r\nf 9858/10127/9029 12815/13518/11330 12814/13517/11329 9854/10123/9025\r\nf 11843/12148/9062 11842/12146/9061 12816/13519/11331 12817/13520/11332\r\nf 11835/12138/9042 12815/13518/11330 12816/13519/11331 11842/12146/9061\r\nf 9858/10127/9029 9874/10143/9045 12816/13519/11331 12815/13518/11330\r\nf 9878/10147/9049 12817/13520/11332 12816/13519/11331 9874/10143/9045\r\nf 11851/12158/9080 11850/12156/9079 12818/13521/11333 12819/13522/11334\r\nf 11843/12148/9062 12817/13520/11332 12818/13521/11333 11850/12156/9079\r\nf 9878/10147/9049 9894/10163/9065 12818/13521/11333 12817/13520/11332\r\nf 9898/10167/9068 12819/13522/11334 12818/13521/11333 9894/10163/9065\r\nf 11859/12168/9100 11858/12166/9099 12820/13523/11335 12821/13524/11336\r\nf 11851/12158/9080 12819/13522/11334 12820/13523/11335 11858/12166/9099\r\nf 9898/10167/9068 9914/10183/9083 12820/13523/11335 12819/13522/11334\r\nf 9918/10187/9087 12821/13524/11336 12820/13523/11335 9914/10183/9083\r\nf 11867/12178/9120 11866/12176/9119 12822/13525/11337 12823/13526/11338\r\nf 11859/12168/9100 12821/13524/11336 12822/13525/11337 11866/12176/9119\r\nf 9918/10187/9087 9934/10203/9103 12822/13525/11337 12821/13524/11336\r\nf 9938/10207/9107 12823/13526/11338 12822/13525/11337 9934/10203/9103\r\nf 11875/12188/9140 11874/12186/9139 12824/13527/11339 12825/13528/11340\r\nf 11867/12178/9120 12823/13526/11338 12824/13527/11339 11874/12186/9139\r\nf 9938/10207/9107 9954/10223/9123 12824/13527/11339 12823/13526/11338\r\nf 9958/10227/9127 12825/13528/11340 12824/13527/11339 9954/10223/9123\r\nf 11883/12198/9160 11882/12196/9159 12826/13529/11341 12827/13530/11342\r\nf 11875/12188/9140 12825/13528/11340 12826/13529/11341 11882/12196/9159\r\nf 9958/10227/9127 9974/10243/9143 12826/13529/11341 12825/13528/11340\r\nf 9978/10247/9147 12827/13530/11342 12826/13529/11341 9974/10243/9143\r\nf 11891/12208/9180 11890/12206/9179 12828/13531/11343 12829/13532/11344\r\nf 11883/12198/9160 12827/13530/11342 12828/13531/11343 11890/12206/9179\r\nf 9978/10247/9147 9994/10263/9163 12828/13531/11343 12827/13530/11342\r\nf 9998/10267/9167 12829/13532/11344 12828/13531/11343 9994/10263/9163\r\nf 11899/12218/9200 11898/12216/9199 12830/13533/11345 12831/13534/11346\r\nf 11891/12208/9180 12829/13532/11344 12830/13533/11345 11898/12216/9199\r\nf 9998/10267/9167 10014/10283/9183 12830/13533/11345 12829/13532/11344\r\nf 10018/10287/9187 12831/13534/11346 12830/13533/11345 10014/10283/9183\r\nf 11907/12228/9220 11906/12226/9219 12832/13535/11347 12833/13536/11348\r\nf 11899/12218/9200 12831/13534/11346 12832/13535/11347 11906/12226/9219\r\nf 10018/10287/9187 10034/10303/9203 12832/13535/11347 12831/13534/11346\r\nf 10038/10307/9207 12833/13536/11348 12832/13535/11347 10034/10303/9203\r\nf 11915/12238/9240 11914/12236/9239 12834/13537/11349 12835/13538/11350\r\nf 11907/12228/9220 12833/13536/11348 12834/13537/11349 11914/12236/9239\r\nf 10038/10307/9207 10054/10323/9223 12834/13537/11349 12833/13536/11348\r\nf 10058/10327/9227 12835/13538/11350 12834/13537/11349 10054/10323/9223\r\nf 11923/12248/9260 11922/12246/9259 12836/13539/11351 12837/13540/11352\r\nf 11915/12238/9240 12835/13538/11350 12836/13539/11351 11922/12246/9259\r\nf 10058/10327/9227 10074/10343/9243 12836/13539/11351 12835/13538/11350\r\nf 10078/10347/9247 12837/13540/11352 12836/13539/11351 10074/10343/9243\r\nf 11931/12258/10938 11930/12256/10937 12838/13541/11353 12839/13542/11354\r\nf 11923/12248/9260 12837/13540/11352 12838/13541/11353 11930/12256/10937\r\nf 10078/10347/9247 10094/10363/9263 12838/13541/11353 12837/13540/11352\r\nf 10098/10367/9267 12839/13542/11354 12838/13541/11353 10094/10363/9263\r\nf 11939/12268/9300 11938/12266/10942 12840/13543/11355 12841/13544/11356\r\nf 11931/12258/10938 12839/13542/11354 12840/13543/11355 11938/12266/10942\r\nf 10098/10367/9267 10114/10383/9283 12840/13543/11355 12839/13542/11354\r\nf 10118/10387/9287 12841/13544/11356 12840/13543/11355 10114/10383/9283\r\nf 11947/12278/10947 11946/12276/9319 12842/13545/11357 12843/13546/11358\r\nf 11939/12268/9300 12841/13544/11356 12842/13545/11357 11946/12276/9319\r\nf 10118/10387/9287 10134/10403/9303 12842/13545/11357 12841/13544/11356\r\nf 10138/10407/9307 12843/13546/11358 12842/13545/11357 10134/10403/9303\r\nf 11955/12288/10954 11954/12286/10953 12844/13547/11359 12845/13548/11360\r\nf 11947/12278/10947 12843/13546/11358 12844/13547/11359 11954/12286/10953\r\nf 10138/10407/9307 10154/10423/9323 12844/13547/11359 12843/13546/11358\r\nf 10158/10427/9327 12845/13548/11360 12844/13547/11359 10154/10423/9323\r\nf 11963/12298/10962 11962/12296/10961 12846/13549/11361 12847/13550/11362\r\nf 11955/12288/10954 12845/13548/11360 12846/13549/11361 11962/12296/10961\r\nf 10158/10427/9327 10174/10443/9343 12846/13549/11361 12845/13548/11360\r\nf 10178/10447/9347 12847/13550/11362 12846/13549/11361 10174/10443/9343\r\nf 11967/12304/9378 11966/12302/10964 12848/13551/11363 12849/13552/11364\r\nf 11963/12298/10962 12847/13550/11362 12848/13551/11363 11966/12302/10964\r\nf 10178/10447/9347 10196/10465/9365 12848/13551/11363 12847/13550/11362\r\nf 10200/10469/9369 12849/13552/11364 12848/13551/11363 10196/10465/9365\r\nf 11971/12310/10968 11970/12308/10967 12850/13553/11365 12851/13554/11366\r\nf 11967/12304/9378 12849/13552/11364 12850/13553/11365 11970/12308/10967\r\nf 10200/10469/9369 10212/10481/9381 12850/13553/11365 12849/13552/11364\r\nf 10216/10485/9385 12851/13554/11366 12850/13553/11365 10212/10481/9381\r\nf 11981/12322/10978 11980/12320/10977 12852/13555/11367 12853/13556/11368\r\nf 11971/12310/10968 12851/13554/11366 12852/13555/11367 11980/12320/10977\r\nf 10216/10485/9385 10228/10497/9397 12852/13555/11367 12851/13554/11366\r\nf 10232/10501/9401 12853/13556/11368 12852/13555/11367 10228/10497/9397\r\nf 11989/12332/10984 11988/12330/10985 12854/13557/11369 12855/13558/11370\r\nf 11981/12322/10978 12853/13556/11368 12854/13557/11369 11988/12330/10985\r\nf 10232/10501/9401 10250/10519/9419 12854/13557/11369 12853/13556/11368\r\nf 10254/10523/9423 12855/13558/11370 12854/13557/11369 10250/10519/9419\r\nf 11997/12342/9456 11996/12340/10990 12856/13559/11371 12857/13560/11372\r\nf 11989/12332/10984 12855/13558/11370 12856/13559/11371 11996/12340/10990\r\nf 10254/10523/9423 10270/10539/9439 12856/13559/11371 12855/13558/11370\r\nf 10274/10543/9443 12857/13560/11372 12856/13559/11371 10270/10539/9439\r\nf 12005/12352/10993 12004/12350/9475 12858/13561/11373 12859/13562/11374\r\nf 11997/12342/9456 12857/13560/11372 12858/13561/11373 12004/12350/9475\r\nf 10274/10543/9443 10290/10559/9459 12858/13561/11373 12857/13560/11372\r\nf 10294/10563/9463 12859/13562/11374 12858/13561/11373 10290/10559/9459\r\nf 12013/12362/9496 12012/12360/9495 12860/13563/11375 12861/13564/11376\r\nf 12005/12352/10993 12859/13562/11374 12860/13563/11375 12012/12360/9495\r\nf 10294/10563/9463 10310/10579/9479 12860/13563/11375 12859/13562/11374\r\nf 10314/10583/9483 12861/13564/11376 12860/13563/11375 10310/10579/9479\r\nf 12021/12372/10998 12020/12370/9515 12862/13565/11377 12863/13566/11378\r\nf 12013/12362/9496 12861/13564/11376 12862/13565/11377 12020/12370/9515\r\nf 10314/10583/9483 10330/10599/9499 12862/13565/11377 12861/13564/11376\r\nf 10334/10603/9503 12863/13566/11378 12862/13565/11377 10330/10599/9499\r\nf 12029/12382/9536 12028/12380/9535 12864/13567/11379 12865/13568/11380\r\nf 12021/12372/10998 12863/13566/11378 12864/13567/11379 12028/12380/9535\r\nf 10334/10603/9503 10350/10619/9519 12864/13567/11379 12863/13566/11378\r\nf 10354/10623/9523 12865/13568/11380 12864/13567/11379 10350/10619/9519\r\nf 12037/12392/11005 12036/12390/9555 12866/13569/11381 12867/13570/11382\r\nf 12029/12382/9536 12865/13568/11380 12866/13569/11381 12036/12390/9555\r\nf 10354/10623/9523 10370/10639/9539 12866/13569/11381 12865/13568/11380\r\nf 10374/10643/9543 12867/13570/11382 12866/13569/11381 10370/10639/9539\r\nf 12045/12402/11008 12044/12400/9575 12868/13571/11383 12869/13572/11384\r\nf 12037/12392/11005 12867/13570/11382 12868/13571/11383 12044/12400/9575\r\nf 10374/10643/9543 10390/10659/9559 12868/13571/11383 12867/13570/11382\r\nf 10394/10663/9563 12869/13572/11384 12868/13571/11383 10390/10659/9559\r\nf 12053/12412/9596 12052/12410/11012 12870/13573/11385 12871/13574/11386\r\nf 12045/12402/11008 12869/13572/11384 12870/13573/11385 12052/12410/11012\r\nf 10394/10663/9563 10410/10679/9579 12870/13573/11385 12869/13572/11384\r\nf 10414/10683/9583 12871/13574/11386 12870/13573/11385 10410/10679/9579\r\nf 12061/12422/9616 12060/12420/9615 12872/13575/11387 12873/13576/11388\r\nf 12053/12412/9596 12871/13574/11386 12872/13575/11387 12060/12420/9615\r\nf 10414/10683/9583 10430/10699/9599 12872/13575/11387 12871/13574/11386\r\nf 10434/10703/9603 12873/13576/11388 12872/13575/11387 10430/10699/9599\r\nf 12069/12432/9636 12068/12430/9635 12874/13577/11389 12875/13578/11390\r\nf 12061/12422/9616 12873/13576/11388 12874/13577/11389 12068/12430/9635\r\nf 10434/10703/9603 10450/10719/9619 12874/13577/11389 12873/13576/11388\r\nf 10454/10723/9623 12875/13578/11390 12874/13577/11389 10450/10719/9619\r\nf 12077/12442/9656 12076/12440/9655 12876/13579/11391 12877/13580/11392\r\nf 12069/12432/9636 12875/13578/11390 12876/13579/11391 12076/12440/9655\r\nf 10454/10723/9623 10470/10739/9639 12876/13579/11391 12875/13578/11390\r\nf 10474/10743/9643 12877/13580/11392 12876/13579/11391 10470/10739/9639\r\nf 12085/12452/9676 12084/12450/9675 12878/13581/11393 12879/13582/11394\r\nf 12077/12442/9656 12877/13580/11392 12878/13581/11393 12084/12450/9675\r\nf 10474/10743/9643 10490/10759/9659 12878/13581/11393 12877/13580/11392\r\nf 10494/10763/9663 12879/13582/11394 12878/13581/11393 10490/10759/9659\r\nf 12093/12462/9696 12092/12460/11025 12880/13583/11395 12881/13584/11396\r\nf 12085/12452/9676 12879/13582/11394 12880/13583/11395 12092/12460/11025\r\nf 10494/10763/9663 10510/10779/9679 12880/13583/11395 12879/13582/11394\r\nf 10514/10783/9683 12881/13584/11396 12880/13583/11395 10510/10779/9679\r\nf 12101/12472/9716 12100/12470/9715 12882/13585/11397 12883/13586/11398\r\nf 12093/12462/9696 12881/13584/11396 12882/13585/11397 12100/12470/9715\r\nf 10514/10783/9683 10530/10799/9699 12882/13585/11397 12881/13584/11396\r\nf 10534/10803/9703 12883/13586/11398 12882/13585/11397 10530/10799/9699\r\nf 12109/12482/9736 12108/12480/9735 12884/13587/11399 12885/13588/11400\r\nf 12101/12472/9716 12883/13586/11398 12884/13587/11399 12108/12480/9735\r\nf 10534/10803/9703 10550/10819/9719 12884/13587/11399 12883/13586/11398\r\nf 10554/10823/9723 12885/13588/11400 12884/13587/11399 10550/10819/9719\r\nf 12117/12492/9756 12116/12490/9755 12886/13589/11401 12887/13590/11402\r\nf 12109/12482/9736 12885/13588/11400 12886/13589/11401 12116/12490/9755\r\nf 10554/10823/9723 10570/10839/9739 12886/13589/11401 12885/13588/11400\r\nf 10574/10843/9743 12887/13590/11402 12886/13589/11401 10570/10839/9739\r\nf 12125/12502/11035 12124/12500/9775 12888/13591/11403 12889/13592/11404\r\nf 12117/12492/9756 12887/13590/11402 12888/13591/11403 12124/12500/9775\r\nf 10574/10843/9743 10590/10859/9759 12888/13591/11403 12887/13590/11402\r\nf 10594/10863/9763 12889/13592/11404 12888/13591/11403 10590/10859/9759\r\nf 12133/12512/9796 12132/12510/9795 12890/13593/11405 12891/13594/11406\r\nf 12125/12502/11035 12889/13592/11404 12890/13593/11405 12132/12510/9795\r\nf 10594/10863/9763 10610/10879/9779 12890/13593/11405 12889/13592/11404\r\nf 10614/10883/9783 12891/13594/11406 12890/13593/11405 10610/10879/9779\r\nf 12141/12522/9816 12140/12520/9815 12892/13595/11407 12893/13596/11408\r\nf 12133/12512/9796 12891/13594/11406 12892/13595/11407 12140/12520/9815\r\nf 10614/10883/9783 10630/10899/9799 12892/13595/11407 12891/13594/11406\r\nf 10634/10903/9803 12893/13596/11408 12892/13595/11407 10630/10899/9799\r\nf 12149/12532/11042 12148/12530/9835 12894/13597/11409 12895/13598/11410\r\nf 12141/12522/9816 12893/13596/11408 12894/13597/11409 12148/12530/9835\r\nf 10634/10903/9803 10650/10919/9819 12894/13597/11409 12893/13596/11408\r\nf 10654/10923/9823 12895/13598/11410 12894/13597/11409 10650/10919/9819\r\nf 12157/12542/11045 12156/12540/9855 12896/13599/11411 12897/13600/11412\r\nf 12149/12532/11042 12895/13598/11410 12896/13599/11411 12156/12540/9855\r\nf 10654/10923/9823 10670/10939/9839 12896/13599/11411 12895/13598/11410\r\nf 10674/10943/9843 12897/13600/11412 12896/13599/11411 10670/10939/9839\r\nf 12165/12552/11048 12164/12550/9875 12898/13601/11413 12899/13602/11414\r\nf 12157/12542/11045 12897/13600/11412 12898/13601/11413 12164/12550/9875\r\nf 10674/10943/9843 10690/10959/9859 12898/13601/11413 12897/13600/11412\r\nf 10694/10963/9863 12899/13602/11414 12898/13601/11413 10690/10959/9859\r\nf 12173/12562/9896 12172/12560/9895 12900/13603/11415 12901/13604/11416\r\nf 12165/12552/11048 12899/13602/11414 12900/13603/11415 12172/12560/9895\r\nf 10694/10963/9863 10710/10979/9879 12900/13603/11415 12899/13602/11414\r\nf 10714/10983/9883 12901/13604/11416 12900/13603/11415 10710/10979/9879\r\nf 12181/12572/9914 12180/12570/9896 12902/13605/11417 12903/13606/11418\r\nf 12173/12562/9896 12901/13604/11416 12902/13605/11417 12180/12570/9896\r\nf 10714/10983/9883 10730/10999/9899 12902/13605/11417 12901/13604/11416\r\nf 10734/11003/9903 12903/13606/11418 12902/13605/11417 10730/10999/9899\r\nf 12189/12582/11056 12188/12580/9933 12904/13607/11419 12905/13608/11420\r\nf 12181/12572/9914 12903/13606/11418 12904/13607/11419 12188/12580/9933\r\nf 10734/11003/9903 10750/11019/9917 12904/13607/11419 12903/13606/11418\r\nf 10754/11023/9921 12905/13608/11420 12904/13607/11419 10750/11019/9917\r\nf 12197/12592/11061 12196/12590/11062 12906/13609/11421 12907/13610/11422\r\nf 12189/12582/11056 12905/13608/11420 12906/13609/11421 12196/12590/11062\r\nf 10754/11023/9921 10770/11039/9937 12906/13609/11421 12905/13608/11420\r\nf 10774/11043/9941 12907/13610/11422 12906/13609/11421 10770/11039/9937\r\nf 12205/12602/11070 12204/12600/11069 12908/13611/11423 12909/13612/11424\r\nf 12197/12592/11061 12907/13610/11422 12908/13611/11423 12204/12600/11069\r\nf 10774/11043/9941 10790/11059/9957 12908/13611/11423 12907/13610/11422\r\nf 10794/11063/9961 12909/13612/11424 12908/13611/11423 10790/11059/9957\r\nf 12211/12610/11076 12210/12608/11075 12910/13613/11425 12911/13614/11426\r\nf 12205/12602/11070 12909/13612/11424 12910/13613/11425 12210/12608/11075\r\nf 10794/11063/9961 10810/11079/9977 12910/13613/11425 12909/13612/11424\r\nf 10814/11083/9981 12911/13614/11426 12910/13613/11425 10810/11079/9977\r\nf 12218/12619/10011 12217/12617/11079 12912/13615/11427 12913/13616/11428\r\nf 8708/8671/8235 9413/9679/8586 12912/13615/11427 12217/12617/11079\r\nf 9412/9678/8585 12914/13617/11429 12912/13615/11427 9413/9679/8586\r\nf 10848/11117/10015 12913/13616/11428 12912/13615/11427 12914/13617/11429\r\nf 12227/12630/10013 12226/12628/10012 12915/13618/11430 12916/13619/11431\r\nf 12218/12619/10011 12913/13616/11428 12915/13618/11430 12226/12628/10012\r\nf 10848/11117/10015 10851/11120/10018 12915/13618/11430 12913/13616/11428\r\nf 10856/11125/10023 12916/13619/11431 12915/13618/11430 10851/11120/10018\r\nf 12235/12640/10038 12234/12638/10037 12917/13620/11432 12918/13621/11433\r\nf 12227/12630/10013 12916/13619/11431 12917/13620/11432 12234/12638/10037\r\nf 10856/11125/10023 10874/11143/10041 12917/13620/11432 12916/13619/11431\r\nf 10878/11147/10045 12918/13621/11433 12917/13620/11432 10874/11143/10041\r\nf 12243/12650/10058 12242/12648/10057 12919/13622/11434 12920/13623/11435\r\nf 12235/12640/10038 12918/13621/11433 12919/13622/11434 12242/12648/10057\r\nf 10878/11147/10045 10894/11163/10061 12919/13622/11434 12918/13621/11433\r\nf 10898/11167/10065 12920/13623/11435 12919/13622/11434 10894/11163/10061\r\nf 12251/12660/10077 12250/12658/10077 12921/13624/11436 12922/13625/11437\r\nf 12243/12650/10058 12920/13623/11435 12921/13624/11436 12250/12658/10077\r\nf 10898/11167/10065 10914/11183/10080 12921/13624/11436 12920/13623/11435\r\nf 10918/11187/10084 12922/13625/11437 12921/13624/11436 10914/11183/10080\r\nf 12259/12670/10095 12258/12668/10058 12923/13626/11438 12924/13627/11439\r\nf 12251/12660/10077 12922/13625/11437 12923/13626/11438 12258/12668/10058\r\nf 10918/11187/10084 10934/11203/10098 12923/13626/11438 12922/13625/11437\r\nf 10938/11207/10102 12924/13627/11439 12923/13626/11438 10934/11203/10098\r\nf 12267/12680/10114 12266/12678/10113 12925/13628/11440 12926/13629/11441\r\nf 12259/12670/10095 12924/13627/11439 12925/13628/11440 12266/12678/10113\r\nf 10938/11207/10102 10954/11223/10117 12925/13628/11440 12924/13627/11439\r\nf 10958/11227/10121 12926/13629/11441 12925/13628/11440 10954/11223/10117\r\nf 12275/12690/11096 12274/12688/10133 12927/13630/11442 12928/13631/11443\r\nf 12267/12680/10114 12926/13629/11441 12927/13630/11442 12274/12688/10133\r\nf 10958/11227/10121 10974/11243/10137 12927/13630/11442 12926/13629/11441\r\nf 10978/11247/10141 12928/13631/11443 12927/13630/11442 10974/11243/10137\r\nf 12283/12700/11103 12282/12698/11102 12929/13632/11444 12930/13633/11445\r\nf 12275/12690/11096 12928/13631/11443 12929/13632/11444 12282/12698/11102\r\nf 10978/11247/10141 10994/11263/10157 12929/13632/11444 12928/13631/11443\r\nf 10998/11267/10161 12930/13633/11445 12929/13632/11444 10994/11263/10157\r\nf 10817/11086/9984 12290/12708/11109 12931/13634/11446 10818/11087/9985\r\nf 12283/12700/11103 12930/13633/11445 12931/13634/11446 12290/12708/11109\r\nf 10998/11267/10161 11013/11282/10176 12931/13634/11446 12930/13633/11445\r\nf 10812/11081/9979 10818/11087/9985 12931/13634/11446 11013/11282/10176\r\nf 12302/12723/11121 12301/12721/11120 12932/13635/11447 12933/13636/11448\r\nf 12211/12610/11076 12911/13614/11426 12932/13635/11447 12301/12721/11120\r\nf 10814/11083/9981 11044/11313/10207 12932/13635/11447 12911/13614/11426\r\nf 11048/11317/10211 12933/13636/11448 12932/13635/11447 11044/11313/10207\r\nf 12310/12733/11129 12309/12731/11128 12934/13637/11449 12935/13638/11450\r\nf 12302/12723/11121 12933/13636/11448 12934/13637/11449 12309/12731/11128\r\nf 11048/11317/10211 11064/11333/10227 12934/13637/11449 12933/13636/11448\r\nf 11068/11337/10231 12935/13638/11450 12934/13637/11449 11064/11333/10227\r\nf 12318/12743/11134 12317/12741/11135 12936/13639/11451 12937/13640/11452\r\nf 12310/12733/11129 12935/13638/11450 12936/13639/11451 12317/12741/11135\r\nf 11068/11337/10231 11084/11353/10247 12936/13639/11451 12935/13638/11450\r\nf 11088/11357/10251 12937/13640/11452 12936/13639/11451 11084/11353/10247\r\nf 12326/12753/11140 12325/12751/11141 12938/13641/11453 12939/13642/11454\r\nf 12318/12743/11134 12937/13640/11452 12938/13641/11453 12325/12751/11141\r\nf 11088/11357/10251 11104/11373/10267 12938/13641/11453 12937/13640/11452\r\nf 11108/11377/10271 12939/13642/11454 12938/13641/11453 11104/11373/10267\r\nf 12334/12763/10284 12333/12761/11145 12940/13643/11455 12941/13644/11456\r\nf 12326/12753/11140 12939/13642/11454 12940/13643/11455 12333/12761/11145\r\nf 11108/11377/10271 11124/11393/10287 12940/13643/11455 12939/13642/11454\r\nf 11128/11397/10291 12941/13644/11456 12940/13643/11455 11124/11393/10287\r\nf 12342/12773/10304 12341/12771/11149 12942/13645/11457 12943/13646/11458\r\nf 12334/12763/10284 12941/13644/11456 12942/13645/11457 12341/12771/11149\r\nf 11128/11397/10291 11144/11413/10307 12942/13645/11457 12941/13644/11456\r\nf 11148/11417/10311 12943/13646/11458 12942/13645/11457 11144/11413/10307\r\nf 12350/12783/10324 12349/12781/10323 12944/13647/11459 12945/13648/11460\r\nf 12342/12773/10304 12943/13646/11458 12944/13647/11459 12349/12781/10323\r\nf 11148/11417/10311 11164/11433/10327 12944/13647/11459 12943/13646/11458\r\nf 11168/11437/10331 12945/13648/11460 12944/13647/11459 11164/11433/10327\r\nf 12358/12793/11155 12357/12791/11154 12946/13649/11461 12947/13650/11462\r\nf 12350/12783/10324 12945/13648/11460 12946/13649/11461 12357/12791/11154\r\nf 11168/11437/10331 11184/11453/10347 12946/13649/11461 12945/13648/11460\r\nf 11188/11457/10351 12947/13650/11462 12946/13649/11461 11184/11453/10347\r\nf 12366/12803/10364 12365/12801/10363 12948/13651/11463 12949/13652/11464\r\nf 12358/12793/11155 12947/13650/11462 12948/13651/11463 12365/12801/10363\r\nf 11188/11457/10351 11204/11473/10367 12948/13651/11463 12947/13650/11462\r\nf 11208/11477/10371 12949/13652/11464 12948/13651/11463 11204/11473/10367\r\nf 12374/12813/10384 12373/12811/10383 12950/13653/11465 12951/13654/11466\r\nf 12366/12803/10364 12949/13652/11464 12950/13653/11465 12373/12811/10383\r\nf 11208/11477/10371 11224/11493/10387 12950/13653/11465 12949/13652/11464\r\nf 11228/11497/10391 12951/13654/11466 12950/13653/11465 11224/11493/10387\r\nf 12382/12823/10384 12381/12821/10403 12952/13655/11467 12953/13656/11468\r\nf 12374/12813/10384 12951/13654/11466 12952/13655/11467 12381/12821/10403\r\nf 11228/11497/10391 11244/11513/10406 12952/13655/11467 12951/13654/11466\r\nf 11248/11517/10410 12953/13656/11468 12952/13655/11467 11244/11513/10406\r\nf 12390/12833/10422 12389/12831/10421 12954/13657/11469 12955/13658/11470\r\nf 12382/12823/10384 12953/13656/11468 12954/13657/11469 12389/12831/10421\r\nf 11248/11517/10410 11264/11533/10425 12954/13657/11469 12953/13656/11468\r\nf 11268/11537/10429 12955/13658/11470 12954/13657/11469 11264/11533/10425\r\nf 12398/12843/10442 12397/12841/10441 12956/13659/11471 12957/13660/11472\r\nf 12390/12833/10422 12955/13658/11470 12956/13659/11471 12397/12841/10441\r\nf 11268/11537/10429 11284/11553/10445 12956/13659/11471 12955/13658/11470\r\nf 11288/11557/10449 12957/13660/11472 12956/13659/11471 11284/11553/10445\r\nf 12406/12853/10462 12405/12851/11170 12958/13661/11473 12959/13662/11474\r\nf 12398/12843/10442 12957/13660/11472 12958/13661/11473 12405/12851/11170\r\nf 11288/11557/10449 11304/11573/10465 12958/13661/11473 12957/13660/11472\r\nf 11308/11577/10469 12959/13662/11474 12958/13661/11473 11304/11573/10465\r\nf 12414/12863/10482 12413/12861/10481 12960/13663/11475 12961/13664/11476\r\nf 12406/12853/10462 12959/13662/11474 12960/13663/11475 12413/12861/10481\r\nf 11308/11577/10469 11324/11593/10485 12960/13663/11475 12959/13662/11474\r\nf 11328/11597/10489 12961/13664/11476 12960/13663/11475 11324/11593/10485\r\nf 12422/12873/10502 12421/12871/10501 12962/13665/11477 12963/13666/11478\r\nf 12414/12863/10482 12961/13664/11476 12962/13665/11477 12421/12871/10501\r\nf 11328/11597/10489 11344/11613/10505 12962/13665/11477 12961/13664/11476\r\nf 11348/11617/10509 12963/13666/11478 12962/13665/11477 11344/11613/10505\r\nf 12430/12883/10522 12429/12881/10521 12964/13667/11479 12965/13668/11480\r\nf 12422/12873/10502 12963/13666/11478 12964/13667/11479 12429/12881/10521\r\nf 11348/11617/10509 11364/11633/10525 12964/13667/11479 12963/13666/11478\r\nf 11368/11637/10529 12965/13668/11480 12964/13667/11479 11364/11633/10525\r\nf 12438/12893/10542 12437/12891/10541 12966/13669/11481 12967/13670/11482\r\nf 12430/12883/10522 12965/13668/11480 12966/13669/11481 12437/12891/10541\r\nf 11368/11637/10529 11384/11653/10545 12966/13669/11481 12965/13668/11480\r\nf 11388/11657/10549 12967/13670/11482 12966/13669/11481 11384/11653/10545\r\nf 12446/12903/10562 12445/12901/11183 12968/13671/11483 12969/13672/11484\r\nf 12438/12893/10542 12967/13670/11482 12968/13671/11483 12445/12901/11183\r\nf 11388/11657/10549 11404/11673/10565 12968/13671/11483 12967/13670/11482\r\nf 11408/11677/10569 12969/13672/11484 12968/13671/11483 11404/11673/10565\r\nf 12454/12913/10582 12453/12911/10581 12970/13673/11485 12971/13674/11486\r\nf 12446/12903/10562 12969/13672/11484 12970/13673/11485 12453/12911/10581\r\nf 11408/11677/10569 11424/11693/10585 12970/13673/11485 12969/13672/11484\r\nf 11428/11697/10589 12971/13674/11486 12970/13673/11485 11424/11693/10585\r\nf 12462/12923/11190 12461/12921/11189 12972/13675/11487 12973/13676/11488\r\nf 12454/12913/10582 12971/13674/11486 12972/13675/11487 12461/12921/11189\r\nf 11428/11697/10589 11444/11713/10605 12972/13675/11487 12971/13674/11486\r\nf 11448/11717/10609 12973/13676/11488 12972/13675/11487 11444/11713/10605\r\nf 12470/12933/11197 12469/12931/11195 12974/13677/11489 12975/13678/11490\r\nf 12462/12923/11190 12973/13676/11488 12974/13677/11489 12469/12931/11195\r\nf 11448/11717/10609 11464/11733/10625 12974/13677/11489 12973/13676/11488\r\nf 11468/11737/10629 12975/13678/11490 12974/13677/11489 11464/11733/10625\r\nf 12478/12943/11205 12477/12941/11204 12976/13679/11491 12977/13680/11492\r\nf 12470/12933/11197 12975/13678/11490 12976/13679/11491 12477/12941/11204\r\nf 11468/11737/10629 11484/11753/10645 12976/13679/11491 12975/13678/11490\r\nf 11488/11757/10649 12977/13680/11492 12976/13679/11491 11484/11753/10645\r\nf 12486/12953/11211 12485/12951/11210 12978/13681/11493 12979/13682/11494\r\nf 12478/12943/11205 12977/13680/11492 12978/13681/11493 12485/12951/11210\r\nf 11488/11757/10649 11504/11773/10665 12978/13681/11493 12977/13680/11492\r\nf 11508/11777/10669 12979/13682/11494 12978/13681/11493 11504/11773/10665\r\nf 12494/12963/11217 12493/12961/11216 12980/13683/11495 12981/13684/11496\r\nf 12486/12953/11211 12979/13682/11494 12980/13683/11495 12493/12961/11216\r\nf 11508/11777/10669 11524/11793/10685 12980/13683/11495 12979/13682/11494\r\nf 11528/11797/10689 12981/13684/11496 12980/13683/11495 11524/11793/10685\r\nf 12502/12973/11224 12501/12971/11223 12982/13685/11497 12983/13686/11498\r\nf 12494/12963/11217 12981/13684/11496 12982/13685/11497 12501/12971/11223\r\nf 11528/11797/10689 11544/11813/10705 12982/13685/11497 12981/13684/11496\r\nf 11548/11817/10709 12983/13686/11498 12982/13685/11497 11544/11813/10705\r\nf 12510/12983/11232 12509/12981/11231 12984/13687/11499 12985/13688/11500\r\nf 12502/12973/11224 12983/13686/11498 12984/13687/11499 12509/12981/11231\r\nf 11548/11817/10709 11564/11833/10725 12984/13687/11499 12983/13686/11498\r\nf 11568/11837/10729 12985/13688/11500 12984/13687/11499 11564/11833/10725\r\nf 12518/12993/11238 12517/12991/11239 12986/13689/11501 12987/13690/11502\r\nf 12510/12983/11232 12985/13688/11500 12986/13689/11501 12517/12991/11239\r\nf 11568/11837/10729 11584/11853/10745 12986/13689/11501 12985/13688/11500\r\nf 11588/11857/10749 12987/13690/11502 12986/13689/11501 11584/11853/10745\r\nf 12526/13003/10762 12525/13001/10761 12988/13691/11503 12989/13692/11504\r\nf 12518/12993/11238 12987/13690/11502 12988/13691/11503 12525/13001/10761\r\nf 11588/11857/10749 11604/11873/10765 12988/13691/11503 12987/13690/11502\r\nf 11608/11877/10769 12989/13692/11504 12988/13691/11503 11604/11873/10765\r\nf 12534/13013/10782 12533/13011/10781 12990/13693/11505 12991/13694/11506\r\nf 12526/13003/10762 12989/13692/11504 12990/13693/11505 12533/13011/10781\r\nf 11608/11877/10769 11624/11893/10785 12990/13693/11505 12989/13692/11504\r\nf 11628/11897/10789 12991/13694/11506 12990/13693/11505 11624/11893/10785\r\nf 12542/13023/10802 12541/13021/10801 12992/13695/11507 12993/13696/11508\r\nf 12534/13013/10782 12991/13694/11506 12992/13695/11507 12541/13021/10801\r\nf 11628/11897/10789 11644/11913/10805 12992/13695/11507 12991/13694/11506\r\nf 11648/11917/10809 12993/13696/11508 12992/13695/11507 11644/11913/10805\r\nf 12550/13033/11250 12549/13031/10821 12994/13697/11509 12995/13698/11510\r\nf 12542/13023/10802 12993/13696/11508 12994/13697/11509 12549/13031/10821\r\nf 11648/11917/10809 11664/11933/10825 12994/13697/11509 12993/13696/11508\r\nf 11668/11937/10829 12995/13698/11510 12994/13697/11509 11664/11933/10825\r\nf 11696/11965/10857 12557/13041/11256 12996/13699/11511 11694/11963/10855\r\nf 12550/13033/11250 12995/13698/11510 12996/13699/11511 12557/13041/11256\r\nf 11668/11937/10829 11684/11953/10845 12996/13699/11511 12995/13698/11510\r\nf 11688/11957/10849 11694/11963/10855 12996/13699/11511 11684/11953/10845\r\nf 10222/10491/9391 11977/12317/10974 12997/13700/11512 10218/10487/9387\r\nf 11976/12316/10973 12998/13701/11513 12997/13700/11512 11977/12317/10974\r\nf 12999/13702/11514 13000/13703/11515 12997/13700/11512 12998/13701/11513\r\nf 10206/10475/9375 10218/10487/9387 12997/13700/11512 13000/13703/11515\r\nf 12999/13702/11514 13001/13704/11516 13002/13705/11517 13000/13703/11515\r\nf 11956/12290/10955 11959/12293/10958 13002/13705/11517 13001/13704/11516\r\nf 10190/10459/9359 10202/10471/9371 13002/13705/11517 11959/12293/10958\r\nf 10206/10475/9375 13000/13703/11515 13002/13705/11517 10202/10471/9371\r\nf 12999/13702/11514 12998/13701/11513 13003/13706/11518 13004/13707/11519\r\nf 11976/12316/10973 12633/13188/11268 13003/13706/11518 12998/13701/11513\r\nf 10235/10504/9404 13005/13708/11520 13003/13709/11518 12633/13186/11268\r\nf 13006/13710/11521 13004/13711/11519 13003/13709/11518 13005/13708/11520\r\nf 11956/12290/10955 13001/13704/11516 13007/13712/11522 12630/13183/11265\r\nf 12999/13702/11514 13004/13707/11519 13007/13712/11522 13001/13704/11516\r\nf 13006/13710/11521 13008/13713/11523 13007/13714/11522 13004/13711/11519\r\nf 10183/10452/9352 10182/10451/9351 13007/13714/11522 13008/13713/11523\r\nf 10181/10450/9350 12630/13181/11265 13007/13714/11522 10182/10451/9351\r\nf 10183/10452/9352 13008/13713/11523 13009/13715/11524 10184/10453/9353\r\nf 13006/13710/11521 13010/13716/11525 13009/13715/11524 13008/13713/11523\r\nf 10198/10467/9367 10197/10466/9366 13009/13715/11524 13010/13716/11525\r\nf 10176/10445/9345 10184/10453/9353 13009/13715/11524 10197/10466/9366\r\nf 13006/13710/11521 13005/13708/11520 13011/13717/11526 13010/13716/11525\r\nf 10235/10504/9404 10233/10502/9402 13011/13717/11526 13005/13708/11520\r\nf 10214/10483/9383 10213/10482/9382 13011/13717/11526 10233/10502/9402\r\nf 10198/10467/9367 13010/13716/11525 13011/13717/11526 10213/10482/9382\r\nf 8716/8680/8240 9512/9781/8685 13012/13718/11527 12223/12625/11083\r\nf 9514/9783/8687 13013/13719/11528 13012/13718/11527 9512/9781/8685\r\nf 10837/11106/10004 10834/11103/10001 13012/13718/11527 13013/13719/11528\r\nf 10833/11102/10000 12223/12625/11083 13012/13718/11527 10834/11103/10001\r\nf 9514/9783/8687 9516/9785/8689 13014/13720/11529 13013/13719/11528\r\nf 8710/8673/8235 12214/12614/11079 13014/13720/11529 9516/9785/8689\r\nf 10844/11113/10011 10842/11111/10009 13014/13720/11529 12214/12614/11079\r\nf 10837/11106/10004 13013/13719/11528 13014/13720/11529 10842/11111/10009\r\nf 9412/9678/8585 9518/9787/8691 13015/13721/11530 12914/13617/11429\r\nf 9520/9789/8693 13016/13722/11531 13015/13721/11530 9518/9787/8691\r\nf 10852/11121/10019 10849/11118/10016 13015/13721/11530 13016/13722/11531\r\nf 10848/11117/10015 12914/13617/11429 13015/13721/11530 10849/11118/10016\r\nf 9520/9789/8693 9522/9791/8695 13017/13723/11532 13016/13722/11531\r\nf 9191/9369/8242 12692/13306/11080 13017/13723/11532 9522/9791/8695\r\nf 10859/11128/10026 10857/11126/10024 13017/13723/11532 12692/13306/11080\r\nf 10852/11121/10019 13016/13722/11531 13017/13723/11532 10857/11126/10024\r\nv 308.49 35.46 -48.15\r\nv 308.41 38.22 -47.36\r\nv 313.37 37.38 -44.14\r\nv 313.45 34.55 -44.99\r\nv 308.31 41.61 -46.47\r\nv 313.26 40.84 -43.18\r\nv 318 40.13 -39.52\r\nv 318.1 36.59 -40.54\r\nv 318.18 33.7 -41.44\r\nv 308.04 49.45 -44.69\r\nv 312.99 48.85 -41.25\r\nv 313.14 44.74 -42.19\r\nv 308.18 45.42 -45.56\r\nv 317.72 48.3 -37.48\r\nv 317.87 44.1 -38.47\r\nv 322.38 43.5 -34.34\r\nv 322.53 39.44 -35.4\r\nv 322.21 47.77 -33.33\r\nv 322.65 35.83 -36.44\r\nv 322.74 32.87 -37.34\r\nv 309.72 101.16 -18.72\r\nv 309.3 103.59 -15.93\r\nv 312.38 102.63 -14.33\r\nv 313.09 100.35 -16.57\r\nv 309.25 105.63 -12.8\r\nv 312.16 104.45 -11.77\r\nv 311.14 95.34 -23.51\r\nv 310.37 98.4 -21.24\r\nv 313.94 97.68 -18.67\r\nv 314.89 94.68 -20.62\r\nv 302.84 97.07 -28.33\r\nv 302.33 100.28 -25.41\r\nv 306.49 99.29 -23.46\r\nv 307.12 96.16 -26.06\r\nv 301.96 103.22 -22.18\r\nv 306 102.14 -20.57\r\nv 301.86 108.14 -14.75\r\nv 305.77 106.88 -13.8\r\nv 305.73 104.69 -17.35\r\nv 301.78 105.85 -18.62\r\nv 312.8 88.52 -27.39\r\nv 311.96 92.03 -25.56\r\nv 315.85 91.43 -22.4\r\nv 316.8 87.97 -24\r\nv 314.39 81.02 -30.42\r\nv 313.62 84.84 -29.01\r\nv 317.71 84.35 -25.43\r\nv 318.57 80.59 -26.7\r\nv 305.3 82.16 -36.96\r\nv 304.68 86.11 -35.23\r\nv 309.26 85.43 -32.27\r\nv 309.96 81.55 -33.84\r\nv 304.05 89.94 -33.22\r\nv 308.54 89.19 -30.45\r\nv 307.81 92.78 -28.39\r\nv 303.42 93.61 -30.93\r\nv 286.85 39.7 -57.9\r\nv 286.85 42.11 -57.26\r\nv 292.33 41.15 -55.31\r\nv 292.35 38.64 -55.98\r\nv 286.82 45.09 -56.57\r\nv 292.28 44.23 -54.58\r\nv 297.74 43.34 -52.21\r\nv 297.81 40.15 -52.98\r\nv 297.85 37.55 -53.68\r\nv 286.73 52.1 -55.16\r\nv 292.14 51.46 -53.1\r\nv 292.22 47.73 -53.83\r\nv 286.78 48.47 -55.85\r\nv 297.55 50.79 -50.65\r\nv 297.65 46.95 -51.41\r\nv 303 46.17 -48.65\r\nv 302.87 50.11 -47.83\r\nv 303.11 42.45 -49.49\r\nv 303.26 36.48 -51.06\r\nv 303.2 39.16 -50.32\r\nv 319.08 23.92 -49.92\r\nv 318.9 24.94 -48.32\r\nv 323.8 23.64 -44.06\r\nv 324.05 22.46 -45.73\r\nv 318.75 25.87 -46.96\r\nv 323.58 24.7 -42.66\r\nv 319.45 21.75 -53.88\r\nv 319.26 22.85 -51.78\r\nv 324.32 21.21 -47.63\r\nv 324.6 19.93 -49.74\r\nv 308.59 25.32 -60.81\r\nv 308.61 26.08 -58.64\r\nv 314.02 24.46 -55.43\r\nv 314.09 23.54 -57.56\r\nv 308.62 26.84 -56.79\r\nv 313.93 25.37 -53.58\r\nv 308.59 28.28 -53.9\r\nv 313.76 27.05 -50.66\r\nv 313.84 26.24 -51.99\r\nv 308.6 27.58 -55.22\r\nv 298.82 94.48 -33.21\r\nv 298.33 98.02 -30.36\r\nv 299.35 90.74 -35.73\r\nv 294.52 91.55 -37.94\r\nv 294.07 95.36 -35.23\r\nv 293.67 98.96 -32.15\r\nv 300.44 82.81 -39.81\r\nv 299.9 86.83 -37.92\r\nv 295.45 83.47 -42.35\r\nv 294.98 87.57 -40.31\r\nv 285.32 84.7 -46.41\r\nv 285.01 88.92 -44.12\r\nv 290 88.28 -42.39\r\nv 290.38 84.12 -44.56\r\nv 284.7 93.01 -41.45\r\nv 289.61 92.32 -39.86\r\nv 284.16 100.63 -34.97\r\nv 288.92 99.85 -33.69\r\nv 289.24 96.19 -36.96\r\nv 284.41 96.94 -38.4\r\nv 297.53 107 -19.79\r\nv 297.63 109.37 -15.64\r\nv 297.64 104.3 -23.63\r\nv 293.13 105.35 -24.92\r\nv 293.06 108.1 -20.83\r\nv 293.17 110.52 -16.45\r\nv 297.93 101.3 -27.16\r\nv 293.35 102.3 -28.71\r\nv 283.96 104.05 -31.14\r\nv 288.67 103.23 -30.04\r\nv 283.84 107.18 -26.97\r\nv 288.5 106.32 -26.04\r\nv 283.92 112.45 -17.78\r\nv 288.57 111.56 -17.17\r\nv 288.46 109.1 -21.74\r\nv 283.82 109.98 -22.5\r\nv 303.17 29.02 -58.12\r\nv 303.19 29.62 -56.83\r\nv 303.12 28.39 -59.66\r\nv 297.54 29.96 -62.17\r\nv 297.61 30.49 -60.68\r\nv 297.66 31.01 -59.43\r\nv 302.98 27.13 -63.66\r\nv 303.06 27.76 -61.48\r\nv 297.34 28.93 -66.13\r\nv 297.45 29.43 -63.96\r\nv 286.23 32.22 -69.89\r\nv 286.34 32.57 -67.77\r\nv 291.86 31.06 -66.05\r\nv 291.73 30.64 -68.2\r\nv 286.44 32.94 -66.07\r\nv 291.95 31.49 -64.31\r\nv 286.57 33.72 -63.53\r\nv 292.09 32.4 -61.67\r\nv 292.03 31.94 -62.88\r\nv 286.51 33.33 -64.69\r\nv 279.63 97.55 -39.54\r\nv 279.45 101.28 -35.97\r\nv 279.85 93.59 -42.71\r\nv 275.05 94.02 -43.62\r\nv 274.9 98.01 -40.36\r\nv 274.78 101.76 -36.7\r\nv 280.31 85.2 -47.88\r\nv 280.08 89.45 -45.49\r\nv 275.36 85.57 -48.94\r\nv 275.2 89.86 -46.48\r\nv 265.52 85.91 -49.81\r\nv 265.52 90.21 -47.29\r\nv 270.35 90.12 -47.08\r\nv 270.43 85.82 -49.59\r\nv 265.52 94.4 -44.36\r\nv 270.27 94.3 -44.17\r\nv 265.52 102.17 -37.29\r\nv 270.14 102.07 -37.14\r\nv 270.2 98.3 -40.85\r\nv 265.52 98.4 -41.02\r\nv 279.21 110.69 -23.1\r\nv 279.29 113.17 -18.26\r\nv 279.22 107.87 -27.7\r\nv 274.63 108.38 -28.23\r\nv 274.63 111.21 -23.54\r\nv 274.69 113.7 -18.61\r\nv 279.31 104.72 -32.01\r\nv 274.69 105.22 -32.64\r\nv 265.52 105.65 -33.15\r\nv 270.1 105.54 -33.02\r\nv 265.52 108.81 -28.66\r\nv 270.07 108.7 -28.55\r\nv 265.52 114.13 -18.9\r\nv 270.1 114.02 -18.83\r\nv 270.07 111.53 -23.8\r\nv 265.52 111.64 -23.89\r\nv 281.13 34.58 -66.1\r\nv 281.19 34.94 -64.99\r\nv 281.07 34.24 -67.43\r\nv 275.82 35.3 -68.4\r\nv 275.87 35.61 -67.11\r\nv 275.91 35.94 -66.03\r\nv 280.9 33.62 -71.2\r\nv 280.99 33.92 -69.09\r\nv 275.7 34.74 -72.12\r\nv 275.77 35.01 -70.03\r\nv 265.52 35.76 -72.85\r\nv 265.52 36.02 -70.77\r\nv 270.62 35.74 -70.59\r\nv 270.59 35.48 -72.67\r\nv 265.52 36.29 -69.18\r\nv 270.65 36.02 -68.98\r\nv 265.52 36.89 -66.87\r\nv 270.69 36.63 -66.66\r\nv 270.67 36.31 -67.72\r\nv 265.52 36.58 -67.92\r\nv 313.49 32.56 -45.67\r\nv 308.53 33.52 -48.8\r\nv 318.24 31.67 -42.13\r\nv 318.29 30.29 -42.69\r\nv 313.52 31.21 -46.23\r\nv 308.54 32.21 -49.35\r\nv 322.82 30.79 -38.03\r\nv 322.89 29.38 -38.57\r\nv 318.38 28.68 -43.7\r\nv 318.33 29.36 -43.19\r\nv 322.96 28.43 -39.04\r\nv 323.03 27.72 -39.51\r\nv 308.55 30.69 -50.43\r\nv 308.55 31.33 -49.87\r\nv 313.54 30.31 -46.74\r\nv 313.57 29.65 -47.28\r\nv 292.35 36.88 -56.54\r\nv 286.84 37.99 -58.45\r\nv 297.86 35.73 -54.27\r\nv 297.86 34.49 -54.8\r\nv 292.33 35.68 -57.06\r\nv 286.82 36.83 -58.96\r\nv 303.29 34.59 -51.68\r\nv 303.29 33.32 -52.22\r\nv 303.26 31.85 -53.32\r\nv 303.28 32.47 -52.75\r\nv 297.8 33.07 -55.91\r\nv 297.83 33.67 -55.33\r\nv 286.75 35.51 -60.05\r\nv 286.79 36.06 -59.47\r\nv 292.3 34.88 -57.58\r\nv 292.27 34.31 -58.17\r\nv 318.62 26.66 -45.87\r\nv 323.4 25.58 -41.56\r\nv 318.52 27.37 -44.99\r\nv 323.25 26.35 -40.71\r\nv 308.57 28.91 -52.78\r\nv 313.69 27.76 -49.56\r\nv 308.56 29.51 -51.86\r\nv 313.64 28.4 -48.65\r\nv 313.6 29.02 -47.9\r\nv 308.55 30.09 -51.08\r\nv 318.44 28.02 -44.28\r\nv 323.13 27.05 -40.05\r\nv 303.21 30.18 -55.72\r\nv 297.71 31.51 -58.34\r\nv 297.74 32.01 -57.4\r\nv 303.23 30.73 -54.78\r\nv 286.62 34.13 -62.49\r\nv 292.15 32.84 -60.6\r\nv 286.67 34.55 -61.56\r\nv 292.19 33.3 -59.67\r\nv 292.23 33.79 -58.86\r\nv 286.71 35.01 -60.75\r\nv 297.77 32.52 -56.59\r\nv 303.24 31.28 -53.99\r\nv 281.23 35.31 -63.97\r\nv 275.94 36.29 -65.03\r\nv 275.97 36.66 -64.12\r\nv 281.27 35.71 -63.05\r\nv 265.52 37.21 -65.89\r\nv 270.71 36.96 -65.67\r\nv 265.52 37.57 -64.98\r\nv 270.73 37.31 -64.76\r\nv 265.52 38.41 -63.48\r\nv 270.76 38.16 -63.26\r\nv 270.74 37.71 -63.95\r\nv 265.52 37.96 -64.17\r\nv 276.02 37.53 -62.61\r\nv 276 37.07 -63.31\r\nv 281.31 36.14 -62.24\r\nv 281.34 36.61 -61.54\r\nv 315.71 73.13 -32.69\r\nv 315.09 77.12 -31.64\r\nv 319.33 76.73 -27.79\r\nv 320.01 72.79 -28.74\r\nv 316.69 64.99 -34.41\r\nv 316.25 69.09 -33.6\r\nv 320.6 68.76 -29.58\r\nv 321.08 64.66 -30.34\r\nv 307.18 65.79 -41.66\r\nv 306.81 69.92 -40.75\r\nv 311.64 69.47 -37.32\r\nv 312.05 65.36 -38.19\r\nv 306.37 74.04 -39.69\r\nv 311.15 73.55 -36.33\r\nv 310.59 77.59 -35.18\r\nv 305.86 78.12 -38.44\r\nv 300.94 78.71 -41.41\r\nv 301.38 74.56 -42.76\r\nv 296.26 75.11 -45.52\r\nv 295.87 79.31 -44.08\r\nv 302.11 66.26 -44.85\r\nv 301.78 70.41 -43.89\r\nv 296.89 66.75 -47.71\r\nv 296.6 70.91 -46.72\r\nv 286.3 67.68 -52.33\r\nv 286.1 71.87 -51.26\r\nv 291.36 71.41 -49.19\r\nv 291.6 67.23 -50.22\r\nv 285.87 76.14 -49.95\r\nv 291.07 75.64 -47.92\r\nv 290.74 79.89 -46.4\r\nv 285.61 80.43 -48.34\r\nv 280.53 80.88 -49.88\r\nv 280.73 76.56 -51.55\r\nv 275.63 76.88 -52.71\r\nv 275.5 81.23 -51.01\r\nv 281.05 68.08 -54\r\nv 280.91 72.27 -52.91\r\nv 275.85 68.39 -55.21\r\nv 275.75 72.59 -54.1\r\nv 265.52 68.67 -56.21\r\nv 265.52 72.87 -55.09\r\nv 270.63 72.79 -54.84\r\nv 270.68 68.59 -55.96\r\nv 265.52 77.18 -53.67\r\nv 270.57 77.1 -53.43\r\nv 270.5 81.46 -51.69\r\nv 265.52 81.54 -51.92\r\nv 307.88 53.51 -43.92\r\nv 312.83 52.98 -40.44\r\nv 307.7 57.58 -43.19\r\nv 312.63 57.11 -39.69\r\nv 317.32 56.68 -35.88\r\nv 317.54 52.49 -36.63\r\nv 312.37 61.24 -38.96\r\nv 307.47 61.68 -42.46\r\nv 317.05 60.85 -35.16\r\nv 321.47 60.5 -31.04\r\nv 321.78 56.29 -31.74\r\nv 322.02 52.04 -32.48\r\nv 286.66 55.83 -54.53\r\nv 292.05 55.27 -52.44\r\nv 286.57 59.67 -53.89\r\nv 291.94 59.17 -51.79\r\nv 297.29 58.64 -49.27\r\nv 297.43 54.68 -49.95\r\nv 291.79 63.16 -51.06\r\nv 286.45 63.62 -53.18\r\nv 297.11 62.66 -48.55\r\nv 302.36 62.16 -45.66\r\nv 302.57 58.1 -46.4\r\nv 302.73 54.08 -47.1\r\nv 265.52 53.56 -58.94\r\nv 270.78 53.44 -58.7\r\nv 270.79 50.05 -59.33\r\nv 265.52 50.19 -59.57\r\nv 276.06 53.13 -57.98\r\nv 276.08 49.68 -58.62\r\nv 276.1 46.51 -59.28\r\nv 270.8 46.94 -59.98\r\nv 265.52 47.11 -60.21\r\nv 281.41 49.14 -57.46\r\nv 281.37 52.67 -56.79\r\nv 281.43 45.87 -58.14\r\nv 281.44 40.67 -59.43\r\nv 281.44 43 -58.81\r\nv 276.1 41.48 -60.53\r\nv 276.1 43.73 -59.93\r\nv 265.52 42.24 -61.43\r\nv 265.52 44.42 -60.84\r\nv 270.8 44.23 -60.61\r\nv 270.8 42.03 -61.2\r\nv 270.79 40.47 -61.71\r\nv 265.52 40.69 -61.93\r\nv 276.09 39.88 -61.04\r\nv 276.07 38.79 -61.53\r\nv 270.78 39.4 -62.18\r\nv 265.52 39.63 -62.41\r\nv 281.43 39.02 -59.95\r\nv 281.41 37.9 -60.45\r\nv 281.38 37.15 -60.96\r\nv 276.05 38.05 -62.03\r\nv 265.52 38.91 -62.9\r\nv 270.77 38.68 -62.68\r\nv 270.72 64.56 -56.83\r\nv 265.52 64.64 -57.08\r\nv 275.93 64.35 -56.08\r\nv 275.99 60.46 -56.78\r\nv 270.74 60.7 -57.52\r\nv 265.52 60.78 -57.78\r\nv 281.17 64.02 -54.86\r\nv 281.26 60.11 -55.57\r\nv 281.32 56.33 -56.19\r\nv 276.03 56.72 -57.39\r\nv 265.52 57.09 -58.37\r\nv 270.77 56.99 -58.12\r\nv 222.54 35.46 -48.15\r\nv 217.59 34.55 -44.99\r\nv 217.66 37.38 -44.14\r\nv 222.62 38.22 -47.36\r\nv 212.85 33.7 -41.44\r\nv 212.93 36.59 -40.54\r\nv 213.04 40.13 -39.52\r\nv 217.77 40.84 -43.18\r\nv 222.72 41.61 -46.47\r\nv 208.39 35.83 -36.44\r\nv 208.51 39.44 -35.4\r\nv 208.29 32.87 -37.34\r\nv 213.31 48.3 -37.48\r\nv 213.16 44.1 -38.47\r\nv 208.65 43.5 -34.34\r\nv 208.82 47.77 -33.33\r\nv 222.99 49.45 -44.69\r\nv 222.85 45.42 -45.56\r\nv 217.89 44.74 -42.19\r\nv 218.04 48.85 -41.25\r\nv 221.78 105.63 -12.8\r\nv 221.73 103.59 -15.93\r\nv 218.65 102.63 -14.33\r\nv 218.87 104.45 -11.77\r\nv 221.31 101.16 -18.72\r\nv 217.95 100.35 -16.57\r\nv 229.18 108.14 -14.75\r\nv 229.25 105.85 -18.62\r\nv 225.3 104.69 -17.35\r\nv 225.26 106.88 -13.8\r\nv 229.07 103.22 -22.18\r\nv 225.03 102.14 -20.57\r\nv 228.19 97.07 -28.33\r\nv 223.91 96.16 -26.06\r\nv 224.54 99.29 -23.46\r\nv 228.7 100.28 -25.41\r\nv 219.89 95.34 -23.51\r\nv 220.66 98.4 -21.24\r\nv 217.09 97.68 -18.67\r\nv 216.15 94.68 -20.62\r\nv 219.07 92.03 -25.56\r\nv 215.19 91.43 -22.4\r\nv 218.23 88.52 -27.39\r\nv 214.23 87.97 -24\r\nv 227.61 93.61 -30.93\r\nv 223.22 92.78 -28.39\r\nv 226.98 89.94 -33.22\r\nv 222.49 89.19 -30.45\r\nv 225.73 82.16 -36.96\r\nv 221.08 81.55 -33.84\r\nv 221.77 85.43 -32.27\r\nv 226.35 86.11 -35.23\r\nv 216.64 81.02 -30.42\r\nv 217.42 84.84 -29.01\r\nv 213.32 84.35 -25.43\r\nv 212.46 80.59 -26.7\r\nv 244.18 39.7 -57.9\r\nv 238.68 38.64 -55.98\r\nv 238.7 41.15 -55.31\r\nv 244.19 42.11 -57.26\r\nv 233.18 37.55 -53.68\r\nv 233.22 40.15 -52.98\r\nv 233.29 43.34 -52.21\r\nv 238.75 44.23 -54.58\r\nv 244.21 45.09 -56.57\r\nv 227.83 39.16 -50.32\r\nv 227.77 36.48 -51.06\r\nv 227.92 42.45 -49.49\r\nv 228.16 50.11 -47.83\r\nv 228.04 46.17 -48.65\r\nv 233.49 50.79 -50.65\r\nv 233.38 46.95 -51.41\r\nv 244.3 52.1 -55.16\r\nv 244.25 48.47 -55.85\r\nv 238.81 47.73 -53.83\r\nv 238.89 51.46 -53.1\r\nv 212.29 25.87 -46.96\r\nv 212.13 24.94 -48.32\r\nv 207.23 23.64 -44.06\r\nv 207.45 24.7 -42.66\r\nv 211.95 23.92 -49.92\r\nv 206.99 22.46 -45.73\r\nv 222.44 28.28 -53.9\r\nv 222.43 27.58 -55.22\r\nv 217.19 26.24 -51.99\r\nv 217.27 27.05 -50.66\r\nv 222.42 26.84 -56.79\r\nv 217.1 25.37 -53.58\r\nv 222.44 25.32 -60.81\r\nv 216.94 23.54 -57.56\r\nv 217.01 24.46 -55.42\r\nv 222.42 26.08 -58.64\r\nv 211.59 21.75 -53.88\r\nv 211.77 22.85 -51.78\r\nv 206.72 21.21 -47.63\r\nv 206.43 19.93 -49.74\r\nv 232.7 98.02 -30.36\r\nv 232.21 94.48 -33.21\r\nv 237.36 98.96 -32.15\r\nv 236.96 95.36 -35.23\r\nv 236.51 91.55 -37.94\r\nv 231.68 90.74 -35.73\r\nv 246.87 100.63 -34.97\r\nv 246.62 96.94 -38.4\r\nv 241.79 96.19 -36.96\r\nv 242.11 99.85 -33.69\r\nv 246.33 93.01 -41.45\r\nv 241.42 92.32 -39.86\r\nv 245.71 84.7 -46.41\r\nv 240.65 84.12 -44.56\r\nv 241.03 88.28 -42.39\r\nv 246.02 88.92 -44.12\r\nv 235.59 83.47 -42.35\r\nv 236.05 87.57 -40.31\r\nv 231.13 86.83 -37.92\r\nv 230.59 82.81 -39.81\r\nv 233.4 109.37 -15.64\r\nv 233.51 107 -19.79\r\nv 237.86 110.52 -16.45\r\nv 237.97 108.1 -20.83\r\nv 237.9 105.35 -24.92\r\nv 233.39 104.3 -23.63\r\nv 247.11 112.45 -17.78\r\nv 247.21 109.98 -22.5\r\nv 242.57 109.1 -21.74\r\nv 242.46 111.56 -17.17\r\nv 247.19 107.18 -26.97\r\nv 242.53 106.32 -26.04\r\nv 242.37 103.23 -30.04\r\nv 247.07 104.05 -31.14\r\nv 237.69 102.3 -28.71\r\nv 233.11 101.3 -27.16\r\nv 227.84 29.62 -56.83\r\nv 227.87 29.02 -58.12\r\nv 233.37 31.01 -59.43\r\nv 233.42 30.49 -60.68\r\nv 233.49 29.96 -62.17\r\nv 227.91 28.39 -59.66\r\nv 244.46 33.72 -63.53\r\nv 244.52 33.33 -64.69\r\nv 239 31.94 -62.88\r\nv 238.94 32.4 -61.67\r\nv 244.6 32.94 -66.07\r\nv 239.08 31.49 -64.31\r\nv 244.8 32.22 -69.89\r\nv 239.3 30.64 -68.2\r\nv 239.18 31.06 -66.05\r\nv 244.69 32.57 -67.77\r\nv 233.69 28.93 -66.13\r\nv 233.58 29.43 -63.96\r\nv 227.97 27.76 -61.48\r\nv 228.05 27.13 -63.66\r\nv 251.58 101.28 -35.97\r\nv 251.4 97.55 -39.54\r\nv 256.25 101.76 -36.7\r\nv 256.13 98.01 -40.36\r\nv 255.98 94.02 -43.62\r\nv 251.18 93.59 -42.71\r\nv 260.83 98.3 -40.85\r\nv 260.89 102.07 -37.14\r\nv 260.76 94.3 -44.17\r\nv 260.6 85.82 -49.59\r\nv 260.68 90.12 -47.08\r\nv 255.68 85.57 -48.94\r\nv 255.83 89.86 -46.48\r\nv 250.95 89.45 -45.49\r\nv 250.72 85.2 -47.88\r\nv 251.74 113.17 -18.26\r\nv 251.82 110.69 -23.1\r\nv 256.34 113.7 -18.61\r\nv 256.4 111.21 -23.54\r\nv 256.4 108.38 -28.23\r\nv 251.81 107.87 -27.7\r\nv 260.96 111.53 -23.8\r\nv 260.93 114.02 -18.83\r\nv 260.96 108.7 -28.55\r\nv 260.94 105.54 -33.02\r\nv 256.34 105.22 -32.64\r\nv 251.73 104.72 -32.01\r\nv 249.84 34.94 -64.99\r\nv 249.9 34.58 -66.1\r\nv 255.13 35.94 -66.03\r\nv 255.16 35.61 -67.11\r\nv 255.21 35.3 -68.4\r\nv 249.96 34.24 -67.43\r\nv 260.36 36.31 -67.72\r\nv 260.34 36.63 -66.66\r\nv 260.38 36.02 -68.98\r\nv 260.45 35.48 -72.67\r\nv 260.41 35.74 -70.59\r\nv 255.33 34.74 -72.12\r\nv 255.26 35.01 -70.03\r\nv 250.04 33.92 -69.09\r\nv 250.13 33.62 -71.2\r\nv 222.5 33.52 -48.8\r\nv 217.54 32.56 -45.67\r\nv 222.49 32.21 -49.35\r\nv 217.51 31.21 -46.23\r\nv 212.74 30.29 -42.69\r\nv 212.79 31.67 -42.13\r\nv 222.49 30.69 -50.43\r\nv 217.47 29.65 -47.28\r\nv 217.49 30.31 -46.74\r\nv 222.49 31.33 -49.87\r\nv 212.66 28.68 -43.7\r\nv 212.7 29.36 -43.19\r\nv 208.08 28.43 -39.04\r\nv 208.14 29.38 -38.57\r\nv 208 27.72 -39.51\r\nv 208.21 30.79 -38.03\r\nv 244.19 37.99 -58.45\r\nv 238.68 36.88 -56.54\r\nv 244.21 36.83 -58.96\r\nv 238.7 35.68 -57.06\r\nv 233.18 34.49 -54.8\r\nv 233.17 35.73 -54.27\r\nv 244.28 35.51 -60.05\r\nv 238.76 34.31 -58.17\r\nv 238.73 34.88 -57.58\r\nv 244.24 36.06 -59.47\r\nv 233.23 33.07 -55.91\r\nv 233.2 33.67 -55.33\r\nv 227.75 32.47 -52.75\r\nv 227.77 31.85 -53.32\r\nv 227.74 33.32 -52.22\r\nv 227.74 34.59 -51.68\r\nv 212.51 27.37 -44.99\r\nv 212.41 26.66 -45.87\r\nv 207.63 25.58 -41.56\r\nv 207.78 26.35 -40.71\r\nv 212.59 28.02 -44.28\r\nv 207.9 27.05 -40.05\r\nv 222.48 30.09 -51.08\r\nv 217.44 29.02 -47.9\r\nv 222.47 29.51 -51.86\r\nv 217.39 28.4 -48.65\r\nv 217.34 27.76 -49.56\r\nv 222.46 28.91 -52.78\r\nv 227.82 30.18 -55.72\r\nv 227.8 30.73 -54.78\r\nv 233.29 32.01 -57.4\r\nv 233.32 31.51 -58.34\r\nv 227.79 31.28 -53.99\r\nv 233.26 32.52 -56.59\r\nv 244.32 35.01 -60.75\r\nv 238.8 33.79 -58.86\r\nv 244.36 34.55 -61.56\r\nv 238.84 33.3 -59.67\r\nv 238.88 32.84 -60.6\r\nv 244.41 34.13 -62.49\r\nv 249.8 35.31 -63.97\r\nv 249.76 35.71 -63.05\r\nv 255.06 36.66 -64.12\r\nv 255.09 36.29 -65.03\r\nv 249.69 36.61 -61.54\r\nv 249.72 36.14 -62.24\r\nv 255.01 37.53 -62.61\r\nv 255.03 37.07 -63.31\r\nv 260.29 37.71 -63.95\r\nv 260.28 38.16 -63.26\r\nv 260.3 37.31 -64.76\r\nv 260.32 36.96 -65.67\r\nv 215.94 77.12 -31.64\r\nv 211.7 76.73 -27.79\r\nv 215.32 73.13 -32.69\r\nv 211.02 72.79 -28.74\r\nv 225.17 78.12 -38.44\r\nv 220.44 77.59 -35.18\r\nv 224.66 74.04 -39.69\r\nv 219.88 73.56 -36.33\r\nv 223.85 65.79 -41.66\r\nv 218.98 65.36 -38.19\r\nv 219.39 69.47 -37.32\r\nv 224.22 69.92 -40.75\r\nv 214.34 64.99 -34.41\r\nv 214.79 69.09 -33.6\r\nv 210.44 68.76 -29.58\r\nv 209.95 64.66 -30.34\r\nv 230.1 78.71 -41.41\r\nv 235.16 79.31 -44.08\r\nv 234.77 75.11 -45.52\r\nv 229.65 74.56 -42.76\r\nv 245.42 80.43 -48.34\r\nv 240.29 79.89 -46.4\r\nv 245.16 76.14 -49.95\r\nv 239.96 75.64 -47.92\r\nv 244.73 67.68 -52.33\r\nv 239.43 67.23 -50.22\r\nv 239.68 71.41 -49.19\r\nv 244.93 71.87 -51.26\r\nv 234.14 66.75 -47.71\r\nv 234.43 70.91 -46.72\r\nv 229.26 70.41 -43.89\r\nv 228.93 66.26 -44.85\r\nv 250.5 80.88 -49.88\r\nv 255.53 81.23 -51.01\r\nv 255.4 76.88 -52.71\r\nv 250.3 76.56 -51.55\r\nv 260.53 81.46 -51.69\r\nv 260.46 77.1 -53.43\r\nv 260.36 68.59 -55.96\r\nv 260.41 72.79 -54.84\r\nv 255.18 68.39 -55.21\r\nv 255.28 72.59 -54.1\r\nv 250.13 72.27 -52.91\r\nv 249.98 68.08 -54\r\nv 218.21 52.98 -40.44\r\nv 223.15 53.51 -43.92\r\nv 213.49 52.49 -36.63\r\nv 213.71 56.68 -35.88\r\nv 218.41 57.11 -39.69\r\nv 223.33 57.58 -43.19\r\nv 209.01 52.04 -32.48\r\nv 209.26 56.29 -31.74\r\nv 213.99 60.85 -35.16\r\nv 209.56 60.5 -31.04\r\nv 223.56 61.68 -42.46\r\nv 218.66 61.24 -38.96\r\nv 238.98 55.27 -52.44\r\nv 244.37 55.83 -54.53\r\nv 233.6 54.68 -49.95\r\nv 233.74 58.64 -49.27\r\nv 239.1 59.17 -51.79\r\nv 244.46 59.67 -53.89\r\nv 228.3 54.08 -47.1\r\nv 228.46 58.1 -46.4\r\nv 228.67 62.16 -45.66\r\nv 233.92 62.66 -48.55\r\nv 244.58 63.62 -53.18\r\nv 239.24 63.16 -51.06\r\nv 260.24 50.05 -59.33\r\nv 260.25 53.44 -58.7\r\nv 260.23 46.94 -59.98\r\nv 254.94 46.51 -59.28\r\nv 254.95 49.68 -58.62\r\nv 254.97 53.13 -57.98\r\nv 260.24 42.03 -61.2\r\nv 260.23 44.23 -60.61\r\nv 254.94 41.48 -60.53\r\nv 254.93 43.73 -59.93\r\nv 249.59 43 -58.81\r\nv 249.59 40.67 -59.43\r\nv 249.6 45.87 -58.14\r\nv 249.66 52.67 -56.79\r\nv 249.62 49.14 -57.46\r\nv 260.24 40.47 -61.71\r\nv 260.25 39.4 -62.18\r\nv 254.96 38.79 -61.53\r\nv 254.95 39.88 -61.04\r\nv 260.26 38.68 -62.68\r\nv 254.99 38.05 -62.03\r\nv 249.65 37.15 -60.96\r\nv 249.62 37.9 -60.45\r\nv 249.6 39.02 -59.95\r\nv 260.32 64.56 -56.83\r\nv 260.29 60.7 -57.52\r\nv 255.04 60.46 -56.78\r\nv 255.1 64.35 -56.08\r\nv 260.27 56.99 -58.12\r\nv 255 56.72 -57.39\r\nv 249.71 56.33 -56.19\r\nv 249.77 60.11 -55.57\r\nv 249.86 64.02 -54.86\r\nvn 0.46 0.2 -0.86\r\nvn 0.46 0.18 -0.87\r\nvn 0.51 0.19 -0.84\r\nvn 0.51 0.2 -0.84\r\nvn 0.47 0.17 -0.87\r\nvn 0.52 0.17 -0.84\r\nvn 0.57 0.17 -0.8\r\nvn 0.57 0.19 -0.8\r\nvn 0.57 0.2 -0.8\r\nvn 0.48 0.14 -0.87\r\nvn 0.53 0.15 -0.84\r\nvn 0.52 0.16 -0.84\r\nvn 0.47 0.16 -0.87\r\nvn 0.58 0.15 -0.8\r\nvn 0.58 0.16 -0.8\r\nvn 0.6 0.16 -0.78\r\nvn 0.6 0.17 -0.78\r\nvn 0.6 0.14 -0.78\r\nvn 0.6 0.18 -0.78\r\nvn 0.6 0.2 -0.77\r\nvn 0.47 0.58 -0.67\r\nvn 0.45 0.64 -0.63\r\nvn 0.48 0.62 -0.62\r\nvn 0.5 0.56 -0.67\r\nvn 0.44 0.67 -0.6\r\nvn 0.47 0.65 -0.6\r\nvn 0.51 0.46 -0.72\r\nvn 0.49 0.52 -0.7\r\nvn 0.52 0.5 -0.69\r\nvn 0.54 0.45 -0.71\r\nvn 0.42 0.51 -0.75\r\nvn 0.4 0.57 -0.72\r\nvn 0.44 0.54 -0.71\r\nvn 0.46 0.49 -0.74\r\nvn 0.38 0.63 -0.68\r\nvn 0.42 0.6 -0.68\r\nvn 0.36 0.72 -0.6\r\nvn 0.4 0.69 -0.6\r\nvn 0.41 0.66 -0.63\r\nvn 0.37 0.68 -0.63\r\nvn 0.54 0.37 -0.76\r\nvn 0.53 0.41 -0.74\r\nvn 0.55 0.4 -0.73\r\nvn 0.57 0.36 -0.74\r\nvn 0.56 0.28 -0.78\r\nvn 0.55 0.33 -0.77\r\nvn 0.58 0.32 -0.75\r\nvn 0.58 0.28 -0.76\r\nvn 0.47 0.3 -0.83\r\nvn 0.46 0.35 -0.82\r\nvn 0.5 0.34 -0.8\r\nvn 0.51 0.29 -0.81\r\nvn 0.45 0.4 -0.8\r\nvn 0.49 0.38 -0.78\r\nvn 0.48 0.43 -0.76\r\nvn 0.44 0.45 -0.78\r\nvn 0.28 0.2 -0.94\r\nvn 0.28 0.17 -0.95\r\nvn 0.33 0.17 -0.93\r\nvn 0.33 0.2 -0.92\r\nvn 0.28 0.15 -0.95\r\nvn 0.33 0.16 -0.93\r\nvn 0.38 0.16 -0.91\r\nvn 0.37 0.17 -0.91\r\nvn 0.38 0.2 -0.91\r\nvn 0.28 0.13 -0.95\r\nvn 0.33 0.13 -0.93\r\nvn 0.33 0.14 -0.93\r\nvn 0.28 0.14 -0.95\r\nvn 0.38 0.13 -0.91\r\nvn 0.38 0.15 -0.91\r\nvn 0.43 0.15 -0.89\r\nvn 0.43 0.14 -0.89\r\nvn 0.42 0.16 -0.89\r\nvn 0.42 0.2 -0.89\r\nvn 0.42 0.18 -0.89\r\nvn 0.54 0.68 -0.5\r\nvn 0.54 0.66 -0.53\r\nvn 0.57 0.63 -0.53\r\nvn 0.57 0.65 -0.51\r\nvn 0.54 0.64 -0.55\r\nvn 0.57 0.61 -0.55\r\nvn 0.54 0.7 -0.45\r\nvn 0.54 0.7 -0.47\r\nvn 0.57 0.66 -0.49\r\nvn 0.57 0.67 -0.47\r\nvn 0.46 0.79 -0.4\r\nvn 0.46 0.78 -0.42\r\nvn 0.49 0.74 -0.45\r\nvn 0.5 0.75 -0.43\r\nvn 0.45 0.76 -0.47\r\nvn 0.49 0.72 -0.49\r\nvn 0.45 0.71 -0.54\r\nvn 0.49 0.68 -0.55\r\nvn 0.49 0.7 -0.53\r\nvn 0.45 0.73 -0.51\r\nvn 0.39 0.47 -0.79\r\nvn 0.38 0.53 -0.76\r\nvn 0.41 0.41 -0.82\r\nvn 0.36 0.43 -0.83\r\nvn 0.35 0.48 -0.8\r\nvn 0.34 0.55 -0.77\r\nvn 0.42 0.31 -0.85\r\nvn 0.42 0.36 -0.84\r\nvn 0.38 0.32 -0.87\r\nvn 0.37 0.37 -0.85\r\nvn 0.28 0.34 -0.9\r\nvn 0.27 0.39 -0.88\r\nvn 0.33 0.38 -0.86\r\nvn 0.33 0.33 -0.88\r\nvn 0.27 0.45 -0.85\r\nvn 0.32 0.44 -0.84\r\nvn 0.25 0.58 -0.78\r\nvn 0.3 0.56 -0.77\r\nvn 0.31 0.5 -0.81\r\nvn 0.26 0.51 -0.82\r\nvn 0.33 0.71 -0.62\r\nvn 0.32 0.74 -0.6\r\nvn 0.35 0.65 -0.68\r\nvn 0.31 0.67 -0.67\r\nvn 0.3 0.73 -0.62\r\nvn 0.29 0.75 -0.59\r\nvn 0.36 0.59 -0.72\r\nvn 0.33 0.61 -0.72\r\nvn 0.24 0.65 -0.72\r\nvn 0.28 0.63 -0.72\r\nvn 0.23 0.71 -0.67\r\nvn 0.27 0.69 -0.67\r\nvn 0.21 0.79 -0.58\r\nvn 0.25 0.77 -0.59\r\nvn 0.26 0.74 -0.61\r\nvn 0.22 0.76 -0.61\r\nvn 0.42 0.77 -0.48\r\nvn 0.42 0.74 -0.52\r\nvn 0.42 0.8 -0.43\r\nvn 0.39 0.83 -0.4\r\nvn 0.39 0.8 -0.45\r\nvn 0.39 0.78 -0.49\r\nvn 0.43 0.83 -0.35\r\nvn 0.43 0.82 -0.38\r\nvn 0.4 0.86 -0.31\r\nvn 0.4 0.85 -0.34\r\nvn 0.33 0.91 -0.23\r\nvn 0.33 0.91 -0.26\r\nvn 0.36 0.88 -0.3\r\nvn 0.37 0.89 -0.27\r\nvn 0.33 0.89 -0.33\r\nvn 0.36 0.86 -0.36\r\nvn 0.33 0.83 -0.45\r\nvn 0.36 0.8 -0.47\r\nvn 0.36 0.83 -0.42\r\nvn 0.33 0.86 -0.4\r\nvn 0.2 0.53 -0.82\r\nvn 0.2 0.59 -0.78\r\nvn 0.21 0.46 -0.86\r\nvn 0.15 0.47 -0.87\r\nvn 0.14 0.54 -0.83\r\nvn 0.14 0.61 -0.78\r\nvn 0.22 0.35 -0.91\r\nvn 0.22 0.4 -0.89\r\nvn 0.15 0.35 -0.92\r\nvn 0.15 0.41 -0.9\r\nvn -0 0.36 -0.93\r\nvn 0 0.42 -0.91\r\nvn 0.08 0.42 -0.91\r\nvn 0.08 0.36 -0.93\r\nvn -0 0.48 -0.88\r\nvn 0.08 0.48 -0.88\r\nvn 0 0.61 -0.79\r\nvn 0.07 0.61 -0.79\r\nvn 0.07 0.54 -0.84\r\nvn -0 0.55 -0.84\r\nvn 0.17 0.77 -0.61\r\nvn 0.16 0.8 -0.58\r\nvn 0.18 0.72 -0.67\r\nvn 0.13 0.73 -0.67\r\nvn 0.12 0.79 -0.61\r\nvn 0.11 0.81 -0.57\r\nvn 0.19 0.66 -0.73\r\nvn 0.13 0.67 -0.73\r\nvn 0 0.68 -0.73\r\nvn 0.07 0.68 -0.73\r\nvn -0 0.74 -0.67\r\nvn 0.06 0.74 -0.67\r\nvn -0 0.82 -0.57\r\nvn 0.06 0.82 -0.57\r\nvn 0.06 0.79 -0.61\r\nvn -0 0.8 -0.61\r\nvn 0.28 0.88 -0.37\r\nvn 0.28 0.86 -0.43\r\nvn 0.28 0.91 -0.3\r\nvn 0.21 0.93 -0.28\r\nvn 0.21 0.91 -0.35\r\nvn 0.21 0.89 -0.41\r\nvn 0.28 0.94 -0.2\r\nvn 0.28 0.93 -0.23\r\nvn 0.21 0.96 -0.18\r\nvn 0.21 0.95 -0.21\r\nvn 0 0.99 -0.17\r\nvn 0 0.98 -0.2\r\nvn 0.12 0.97 -0.2\r\nvn 0.12 0.98 -0.17\r\nvn 0 0.96 -0.27\r\nvn 0.12 0.95 -0.27\r\nvn 0 0.92 -0.4\r\nvn 0.12 0.91 -0.4\r\nvn 0.12 0.93 -0.34\r\nvn 0 0.94 -0.34\r\nvn 0.51 0.23 -0.83\r\nvn 0.46 0.24 -0.85\r\nvn 0.57 0.23 -0.79\r\nvn 0.57 0.28 -0.77\r\nvn 0.51 0.3 -0.81\r\nvn 0.47 0.31 -0.83\r\nvn 0.6 0.22 -0.77\r\nvn 0.6 0.27 -0.75\r\nvn 0.56 0.45 -0.69\r\nvn 0.57 0.36 -0.74\r\nvn 0.6 0.34 -0.72\r\nvn 0.6 0.42 -0.68\r\nvn 0.47 0.51 -0.72\r\nvn 0.47 0.41 -0.78\r\nvn 0.51 0.39 -0.77\r\nvn 0.51 0.48 -0.72\r\nvn 0.34 0.24 -0.91\r\nvn 0.29 0.25 -0.92\r\nvn 0.38 0.24 -0.89\r\nvn 0.39 0.32 -0.86\r\nvn 0.35 0.33 -0.88\r\nvn 0.3 0.34 -0.89\r\nvn 0.42 0.24 -0.88\r\nvn 0.43 0.31 -0.85\r\nvn 0.43 0.53 -0.73\r\nvn 0.43 0.43 -0.79\r\nvn 0.4 0.56 -0.73\r\nvn 0.4 0.44 -0.8\r\nvn 0.33 0.61 -0.72\r\nvn 0.32 0.48 -0.82\r\nvn 0.36 0.46 -0.81\r\nvn 0.37 0.58 -0.73\r\nvn 0.54 0.61 -0.58\r\nvn 0.57 0.58 -0.58\r\nvn 0.55 0.57 -0.61\r\nvn 0.58 0.54 -0.61\r\nvn 0.45 0.69 -0.57\r\nvn 0.49 0.65 -0.58\r\nvn 0.46 0.64 -0.61\r\nvn 0.49 0.61 -0.62\r\nvn 0.5 0.55 -0.67\r\nvn 0.46 0.58 -0.67\r\nvn 0.56 0.51 -0.65\r\nvn 0.59 0.48 -0.65\r\nvn 0.42 0.72 -0.55\r\nvn 0.39 0.75 -0.53\r\nvn 0.4 0.7 -0.59\r\nvn 0.43 0.67 -0.6\r\nvn 0.33 0.8 -0.49\r\nvn 0.36 0.78 -0.51\r\nvn 0.33 0.76 -0.56\r\nvn 0.37 0.73 -0.57\r\nvn 0.37 0.67 -0.65\r\nvn 0.33 0.7 -0.64\r\nvn 0.4 0.64 -0.66\r\nvn 0.43 0.61 -0.66\r\nvn 0.28 0.83 -0.48\r\nvn 0.21 0.86 -0.46\r\nvn 0.21 0.82 -0.53\r\nvn 0.28 0.79 -0.54\r\nvn 0 0.89 -0.45\r\nvn 0.12 0.88 -0.45\r\nvn 0 0.85 -0.52\r\nvn 0.12 0.84 -0.52\r\nvn -0 0.69 -0.73\r\nvn 0.11 0.68 -0.72\r\nvn 0.12 0.78 -0.62\r\nvn 0 0.79 -0.62\r\nvn 0.2 0.66 -0.72\r\nvn 0.21 0.76 -0.62\r\nvn 0.28 0.73 -0.63\r\nvn 0.27 0.63 -0.72\r\nvn 0.57 0.21 -0.79\r\nvn 0.57 0.25 -0.79\r\nvn 0.59 0.24 -0.77\r\nvn 0.6 0.21 -0.78\r\nvn 0.58 0.16 -0.8\r\nvn 0.57 0.18 -0.8\r\nvn 0.6 0.18 -0.78\r\nvn 0.6 0.15 -0.78\r\nvn 0.48 0.16 -0.86\r\nvn 0.48 0.19 -0.86\r\nvn 0.53 0.19 -0.83\r\nvn 0.53 0.16 -0.83\r\nvn 0.48 0.22 -0.85\r\nvn 0.52 0.22 -0.82\r\nvn 0.52 0.25 -0.82\r\nvn 0.47 0.26 -0.84\r\nvn 0.43 0.26 -0.86\r\nvn 0.43 0.23 -0.87\r\nvn 0.39 0.23 -0.89\r\nvn 0.39 0.27 -0.88\r\nvn 0.44 0.16 -0.88\r\nvn 0.44 0.19 -0.88\r\nvn 0.39 0.17 -0.91\r\nvn 0.39 0.2 -0.9\r\nvn 0.29 0.17 -0.94\r\nvn 0.29 0.2 -0.94\r\nvn 0.34 0.2 -0.92\r\nvn 0.34 0.17 -0.92\r\nvn 0.29 0.24 -0.93\r\nvn 0.34 0.24 -0.91\r\nvn 0.34 0.28 -0.9\r\nvn 0.28 0.29 -0.92\r\nvn 0.22 0.29 -0.93\r\nvn 0.23 0.25 -0.94\r\nvn 0.16 0.25 -0.96\r\nvn 0.16 0.3 -0.94\r\nvn 0.23 0.17 -0.96\r\nvn 0.23 0.2 -0.95\r\nvn 0.16 0.17 -0.97\r\nvn 0.16 0.21 -0.97\r\nvn -0 0.17 -0.99\r\nvn -0 0.21 -0.98\r\nvn 0.08 0.21 -0.97\r\nvn 0.08 0.17 -0.98\r\nvn -0 0.25 -0.97\r\nvn 0.08 0.25 -0.96\r\nvn 0.08 0.3 -0.95\r\nvn -0 0.3 -0.95\r\nvn 0.48 0.13 -0.87\r\nvn 0.53 0.13 -0.84\r\nvn 0.48 0.13 -0.87\r\nvn 0.53 0.13 -0.84\r\nvn 0.58 0.13 -0.8\r\nvn 0.58 0.13 -0.8\r\nvn 0.53 0.14 -0.84\r\nvn 0.48 0.14 -0.86\r\nvn 0.58 0.14 -0.8\r\nvn 0.6 0.14 -0.79\r\nvn 0.6 0.13 -0.79\r\nvn 0.6 0.13 -0.79\r\nvn 0.28 0.12 -0.95\r\nvn 0.34 0.12 -0.93\r\nvn 0.28 0.12 -0.95\r\nvn 0.34 0.13 -0.93\r\nvn 0.39 0.13 -0.91\r\nvn 0.39 0.13 -0.91\r\nvn 0.34 0.14 -0.93\r\nvn 0.29 0.14 -0.95\r\nvn 0.39 0.14 -0.91\r\nvn 0.44 0.14 -0.89\r\nvn 0.44 0.13 -0.89\r\nvn 0.43 0.13 -0.89\r\nvn -0 0.12 -0.99\r\nvn 0.08 0.12 -0.99\r\nvn 0.08 0.14 -0.99\r\nvn -0 0.14 -0.99\r\nvn 0.15 0.12 -0.98\r\nvn 0.15 0.14 -0.98\r\nvn 0.15 0.15 -0.98\r\nvn 0.08 0.16 -0.98\r\nvn -0 0.16 -0.99\r\nvn 0.22 0.14 -0.97\r\nvn 0.22 0.12 -0.97\r\nvn 0.22 0.15 -0.96\r\nvn 0.23 0.2 -0.95\r\nvn 0.22 0.17 -0.96\r\nvn 0.16 0.2 -0.97\r\nvn 0.16 0.17 -0.97\r\nvn -0 0.21 -0.98\r\nvn -0 0.18 -0.98\r\nvn 0.08 0.18 -0.98\r\nvn 0.08 0.21 -0.97\r\nvn 0.09 0.26 -0.96\r\nvn -0 0.27 -0.96\r\nvn 0.17 0.26 -0.95\r\nvn 0.18 0.36 -0.91\r\nvn 0.1 0.37 -0.92\r\nvn -0 0.37 -0.93\r\nvn 0.23 0.25 -0.94\r\nvn 0.25 0.35 -0.9\r\nvn 0.26 0.5 -0.82\r\nvn 0.19 0.52 -0.83\r\nvn -0 0.54 -0.84\r\nvn 0.11 0.53 -0.84\r\nvn 0.08 0.14 -0.99\r\nvn -0 0.14 -0.99\r\nvn 0.16 0.14 -0.98\r\nvn 0.16 0.12 -0.98\r\nvn 0.08 0.12 -0.99\r\nvn -0 0.12 -0.99\r\nvn 0.23 0.14 -0.96\r\nvn 0.22 0.12 -0.97\r\nvn 0.22 0.12 -0.97\r\nvn 0.15 0.12 -0.98\r\nvn -0 0.12 -0.99\r\nvn 0.08 0.12 -0.99\r\nvn -0.46 0.2 -0.86\r\nvn -0.51 0.2 -0.84\r\nvn -0.51 0.19 -0.84\r\nvn -0.46 0.18 -0.87\r\nvn -0.57 0.2 -0.8\r\nvn -0.57 0.19 -0.8\r\nvn -0.57 0.17 -0.8\r\nvn -0.52 0.17 -0.84\r\nvn -0.47 0.17 -0.87\r\nvn -0.6 0.18 -0.78\r\nvn -0.6 0.17 -0.78\r\nvn -0.6 0.2 -0.77\r\nvn -0.58 0.15 -0.8\r\nvn -0.58 0.16 -0.8\r\nvn -0.6 0.16 -0.78\r\nvn -0.6 0.14 -0.78\r\nvn -0.48 0.14 -0.87\r\nvn -0.47 0.16 -0.87\r\nvn -0.52 0.16 -0.84\r\nvn -0.53 0.15 -0.84\r\nvn -0.44 0.67 -0.6\r\nvn -0.45 0.64 -0.63\r\nvn -0.48 0.62 -0.62\r\nvn -0.47 0.65 -0.6\r\nvn -0.47 0.58 -0.67\r\nvn -0.5 0.56 -0.67\r\nvn -0.36 0.72 -0.6\r\nvn -0.37 0.68 -0.63\r\nvn -0.41 0.66 -0.63\r\nvn -0.4 0.69 -0.6\r\nvn -0.38 0.63 -0.68\r\nvn -0.42 0.6 -0.68\r\nvn -0.42 0.51 -0.75\r\nvn -0.46 0.49 -0.74\r\nvn -0.44 0.54 -0.71\r\nvn -0.4 0.57 -0.72\r\nvn -0.51 0.46 -0.72\r\nvn -0.49 0.52 -0.7\r\nvn -0.52 0.5 -0.69\r\nvn -0.54 0.45 -0.71\r\nvn -0.53 0.41 -0.74\r\nvn -0.55 0.4 -0.73\r\nvn -0.54 0.37 -0.76\r\nvn -0.57 0.36 -0.74\r\nvn -0.44 0.45 -0.78\r\nvn -0.48 0.43 -0.76\r\nvn -0.45 0.4 -0.8\r\nvn -0.49 0.38 -0.78\r\nvn -0.47 0.3 -0.83\r\nvn -0.51 0.29 -0.81\r\nvn -0.5 0.34 -0.8\r\nvn -0.46 0.35 -0.82\r\nvn -0.56 0.28 -0.78\r\nvn -0.55 0.33 -0.77\r\nvn -0.58 0.32 -0.75\r\nvn -0.58 0.28 -0.76\r\nvn -0.28 0.2 -0.94\r\nvn -0.33 0.2 -0.92\r\nvn -0.33 0.17 -0.93\r\nvn -0.28 0.17 -0.95\r\nvn -0.38 0.2 -0.91\r\nvn -0.37 0.17 -0.91\r\nvn -0.38 0.16 -0.91\r\nvn -0.33 0.16 -0.93\r\nvn -0.28 0.15 -0.95\r\nvn -0.42 0.18 -0.89\r\nvn -0.42 0.2 -0.89\r\nvn -0.42 0.16 -0.89\r\nvn -0.43 0.14 -0.89\r\nvn -0.43 0.15 -0.89\r\nvn -0.38 0.13 -0.91\r\nvn -0.38 0.15 -0.91\r\nvn -0.28 0.13 -0.95\r\nvn -0.28 0.14 -0.95\r\nvn -0.33 0.14 -0.93\r\nvn -0.33 0.13 -0.93\r\nvn -0.54 0.64 -0.55\r\nvn -0.54 0.66 -0.53\r\nvn -0.57 0.63 -0.53\r\nvn -0.57 0.61 -0.55\r\nvn -0.54 0.68 -0.5\r\nvn -0.57 0.65 -0.51\r\nvn -0.45 0.71 -0.54\r\nvn -0.45 0.73 -0.51\r\nvn -0.49 0.7 -0.53\r\nvn -0.49 0.68 -0.55\r\nvn -0.45 0.76 -0.47\r\nvn -0.49 0.72 -0.49\r\nvn -0.46 0.79 -0.4\r\nvn -0.5 0.75 -0.43\r\nvn -0.49 0.74 -0.45\r\nvn -0.46 0.78 -0.42\r\nvn -0.54 0.7 -0.45\r\nvn -0.54 0.7 -0.47\r\nvn -0.57 0.66 -0.49\r\nvn -0.57 0.67 -0.47\r\nvn -0.38 0.53 -0.76\r\nvn -0.39 0.47 -0.79\r\nvn -0.34 0.55 -0.77\r\nvn -0.35 0.48 -0.8\r\nvn -0.36 0.43 -0.83\r\nvn -0.41 0.41 -0.82\r\nvn -0.25 0.58 -0.78\r\nvn -0.26 0.51 -0.82\r\nvn -0.31 0.5 -0.81\r\nvn -0.3 0.56 -0.77\r\nvn -0.27 0.45 -0.85\r\nvn -0.32 0.44 -0.84\r\nvn -0.28 0.34 -0.9\r\nvn -0.33 0.33 -0.88\r\nvn -0.33 0.38 -0.86\r\nvn -0.27 0.39 -0.88\r\nvn -0.38 0.32 -0.87\r\nvn -0.37 0.37 -0.85\r\nvn -0.42 0.36 -0.84\r\nvn -0.42 0.31 -0.85\r\nvn -0.32 0.74 -0.6\r\nvn -0.33 0.71 -0.62\r\nvn -0.29 0.75 -0.59\r\nvn -0.3 0.73 -0.62\r\nvn -0.31 0.67 -0.67\r\nvn -0.35 0.65 -0.68\r\nvn -0.21 0.79 -0.58\r\nvn -0.22 0.76 -0.61\r\nvn -0.26 0.74 -0.61\r\nvn -0.25 0.77 -0.59\r\nvn -0.23 0.71 -0.67\r\nvn -0.27 0.69 -0.67\r\nvn -0.28 0.63 -0.72\r\nvn -0.24 0.65 -0.72\r\nvn -0.33 0.61 -0.72\r\nvn -0.36 0.59 -0.72\r\nvn -0.42 0.74 -0.52\r\nvn -0.42 0.77 -0.48\r\nvn -0.39 0.78 -0.49\r\nvn -0.39 0.8 -0.45\r\nvn -0.39 0.83 -0.4\r\nvn -0.42 0.8 -0.43\r\nvn -0.33 0.83 -0.45\r\nvn -0.33 0.86 -0.4\r\nvn -0.36 0.83 -0.42\r\nvn -0.36 0.8 -0.47\r\nvn -0.33 0.89 -0.33\r\nvn -0.36 0.86 -0.36\r\nvn -0.33 0.91 -0.23\r\nvn -0.37 0.89 -0.27\r\nvn -0.36 0.88 -0.3\r\nvn -0.33 0.91 -0.26\r\nvn -0.4 0.86 -0.31\r\nvn -0.4 0.85 -0.34\r\nvn -0.43 0.82 -0.38\r\nvn -0.43 0.83 -0.35\r\nvn -0.2 0.59 -0.78\r\nvn -0.2 0.53 -0.82\r\nvn -0.14 0.61 -0.78\r\nvn -0.14 0.54 -0.83\r\nvn -0.15 0.47 -0.87\r\nvn -0.21 0.46 -0.86\r\nvn -0.07 0.54 -0.84\r\nvn -0.07 0.61 -0.79\r\nvn -0.08 0.48 -0.88\r\nvn -0.08 0.36 -0.93\r\nvn -0.08 0.42 -0.91\r\nvn -0.15 0.35 -0.92\r\nvn -0.15 0.41 -0.9\r\nvn -0.22 0.4 -0.89\r\nvn -0.22 0.35 -0.91\r\nvn -0.16 0.8 -0.58\r\nvn -0.17 0.77 -0.61\r\nvn -0.11 0.81 -0.57\r\nvn -0.12 0.79 -0.61\r\nvn -0.13 0.73 -0.67\r\nvn -0.18 0.72 -0.67\r\nvn -0.06 0.79 -0.61\r\nvn -0.06 0.82 -0.57\r\nvn -0.06 0.74 -0.67\r\nvn -0.07 0.68 -0.73\r\nvn -0.13 0.67 -0.73\r\nvn -0.19 0.66 -0.73\r\nvn -0.28 0.86 -0.43\r\nvn -0.28 0.88 -0.37\r\nvn -0.21 0.89 -0.41\r\nvn -0.21 0.91 -0.35\r\nvn -0.21 0.93 -0.28\r\nvn -0.28 0.91 -0.3\r\nvn -0.12 0.93 -0.34\r\nvn -0.12 0.91 -0.4\r\nvn -0.12 0.95 -0.27\r\nvn -0.12 0.98 -0.17\r\nvn -0.12 0.97 -0.2\r\nvn -0.21 0.96 -0.18\r\nvn -0.21 0.95 -0.21\r\nvn -0.28 0.93 -0.23\r\nvn -0.28 0.94 -0.2\r\nvn -0.46 0.24 -0.85\r\nvn -0.51 0.23 -0.83\r\nvn -0.47 0.31 -0.83\r\nvn -0.51 0.3 -0.81\r\nvn -0.57 0.28 -0.77\r\nvn -0.57 0.23 -0.79\r\nvn -0.47 0.51 -0.72\r\nvn -0.51 0.48 -0.72\r\nvn -0.51 0.39 -0.77\r\nvn -0.47 0.41 -0.78\r\nvn -0.56 0.45 -0.69\r\nvn -0.57 0.36 -0.74\r\nvn -0.6 0.34 -0.72\r\nvn -0.6 0.27 -0.75\r\nvn -0.6 0.42 -0.68\r\nvn -0.6 0.22 -0.77\r\nvn -0.29 0.25 -0.92\r\nvn -0.34 0.24 -0.91\r\nvn -0.3 0.34 -0.89\r\nvn -0.35 0.33 -0.88\r\nvn -0.39 0.32 -0.86\r\nvn -0.38 0.24 -0.89\r\nvn -0.33 0.61 -0.72\r\nvn -0.37 0.58 -0.73\r\nvn -0.36 0.46 -0.81\r\nvn -0.32 0.48 -0.82\r\nvn -0.4 0.56 -0.73\r\nvn -0.4 0.44 -0.8\r\nvn -0.43 0.43 -0.79\r\nvn -0.43 0.53 -0.73\r\nvn -0.43 0.31 -0.85\r\nvn -0.42 0.24 -0.88\r\nvn -0.55 0.57 -0.61\r\nvn -0.54 0.61 -0.58\r\nvn -0.57 0.58 -0.58\r\nvn -0.58 0.54 -0.61\r\nvn -0.56 0.51 -0.65\r\nvn -0.59 0.48 -0.65\r\nvn -0.46 0.58 -0.67\r\nvn -0.5 0.55 -0.67\r\nvn -0.46 0.64 -0.61\r\nvn -0.49 0.61 -0.62\r\nvn -0.49 0.65 -0.58\r\nvn -0.45 0.69 -0.57\r\nvn -0.42 0.72 -0.55\r\nvn -0.43 0.67 -0.6\r\nvn -0.4 0.7 -0.59\r\nvn -0.39 0.75 -0.53\r\nvn -0.43 0.61 -0.66\r\nvn -0.4 0.64 -0.66\r\nvn -0.33 0.7 -0.64\r\nvn -0.37 0.67 -0.65\r\nvn -0.33 0.76 -0.56\r\nvn -0.37 0.73 -0.57\r\nvn -0.36 0.78 -0.51\r\nvn -0.33 0.8 -0.49\r\nvn -0.28 0.83 -0.48\r\nvn -0.28 0.79 -0.54\r\nvn -0.21 0.82 -0.53\r\nvn -0.21 0.86 -0.46\r\nvn -0.27 0.63 -0.72\r\nvn -0.28 0.73 -0.63\r\nvn -0.2 0.66 -0.72\r\nvn -0.21 0.76 -0.62\r\nvn -0.12 0.78 -0.62\r\nvn -0.11 0.68 -0.72\r\nvn -0.12 0.84 -0.52\r\nvn -0.12 0.88 -0.45\r\nvn -0.57 0.25 -0.79\r\nvn -0.59 0.24 -0.77\r\nvn -0.57 0.21 -0.79\r\nvn -0.6 0.21 -0.78\r\nvn -0.47 0.26 -0.84\r\nvn -0.52 0.25 -0.82\r\nvn -0.48 0.22 -0.85\r\nvn -0.52 0.22 -0.82\r\nvn -0.48 0.16 -0.86\r\nvn -0.53 0.16 -0.83\r\nvn -0.53 0.19 -0.83\r\nvn -0.48 0.19 -0.86\r\nvn -0.58 0.16 -0.8\r\nvn -0.57 0.18 -0.8\r\nvn -0.6 0.18 -0.78\r\nvn -0.6 0.15 -0.78\r\nvn -0.43 0.26 -0.86\r\nvn -0.39 0.27 -0.88\r\nvn -0.39 0.23 -0.89\r\nvn -0.43 0.23 -0.87\r\nvn -0.28 0.29 -0.92\r\nvn -0.34 0.28 -0.9\r\nvn -0.29 0.24 -0.93\r\nvn -0.34 0.24 -0.91\r\nvn -0.29 0.17 -0.94\r\nvn -0.34 0.17 -0.92\r\nvn -0.34 0.2 -0.92\r\nvn -0.29 0.2 -0.94\r\nvn -0.39 0.17 -0.91\r\nvn -0.39 0.2 -0.9\r\nvn -0.44 0.19 -0.88\r\nvn -0.44 0.16 -0.88\r\nvn -0.22 0.29 -0.93\r\nvn -0.16 0.3 -0.94\r\nvn -0.16 0.25 -0.96\r\nvn -0.23 0.25 -0.94\r\nvn -0.08 0.3 -0.95\r\nvn -0.08 0.25 -0.96\r\nvn -0.08 0.17 -0.98\r\nvn -0.08 0.21 -0.97\r\nvn -0.16 0.17 -0.97\r\nvn -0.16 0.21 -0.97\r\nvn -0.23 0.2 -0.95\r\nvn -0.23 0.17 -0.96\r\nvn -0.53 0.13 -0.84\r\nvn -0.48 0.13 -0.87\r\nvn -0.58 0.13 -0.8\r\nvn -0.58 0.13 -0.8\r\nvn -0.53 0.13 -0.84\r\nvn -0.48 0.13 -0.87\r\nvn -0.6 0.13 -0.79\r\nvn -0.6 0.13 -0.79\r\nvn -0.58 0.14 -0.8\r\nvn -0.6 0.14 -0.79\r\nvn -0.48 0.14 -0.86\r\nvn -0.53 0.14 -0.84\r\nvn -0.34 0.12 -0.93\r\nvn -0.28 0.12 -0.95\r\nvn -0.39 0.13 -0.91\r\nvn -0.39 0.13 -0.91\r\nvn -0.34 0.13 -0.93\r\nvn -0.28 0.12 -0.95\r\nvn -0.43 0.13 -0.89\r\nvn -0.44 0.13 -0.89\r\nvn -0.44 0.14 -0.89\r\nvn -0.39 0.14 -0.91\r\nvn -0.29 0.14 -0.95\r\nvn -0.34 0.14 -0.93\r\nvn -0.08 0.14 -0.99\r\nvn -0.08 0.12 -0.99\r\nvn -0.08 0.16 -0.98\r\nvn -0.15 0.15 -0.98\r\nvn -0.15 0.14 -0.98\r\nvn -0.15 0.12 -0.98\r\nvn -0.08 0.21 -0.97\r\nvn -0.08 0.18 -0.98\r\nvn -0.16 0.2 -0.97\r\nvn -0.16 0.17 -0.97\r\nvn -0.22 0.17 -0.96\r\nvn -0.23 0.2 -0.95\r\nvn -0.22 0.15 -0.96\r\nvn -0.22 0.12 -0.97\r\nvn -0.22 0.14 -0.97\r\nvn -0.09 0.26 -0.96\r\nvn -0.1 0.37 -0.92\r\nvn -0.18 0.36 -0.91\r\nvn -0.17 0.26 -0.95\r\nvn -0.11 0.53 -0.84\r\nvn -0.19 0.52 -0.83\r\nvn -0.26 0.5 -0.82\r\nvn -0.25 0.35 -0.9\r\nvn -0.23 0.25 -0.94\r\nvn -0.08 0.14 -0.99\r\nvn -0.08 0.12 -0.99\r\nvn -0.16 0.12 -0.98\r\nvn -0.16 0.14 -0.98\r\nvn -0.08 0.12 -0.99\r\nvn -0.15 0.12 -0.98\r\nvn -0.22 0.12 -0.97\r\nvn -0.22 0.12 -0.97\r\nvn -0.23 0.14 -0.96\r\nvt 0.1363 0.2344\r\nvt 0.137 0.2586\r\nvt 0.095 0.2625\r\nvt 0.0944 0.2376\r\nvt 0.1379 0.2879\r\nvt 0.0959 0.2927\r\nvt 0.0559 0.2991\r\nvt 0.055 0.2681\r\nvt 0.0543 0.2426\r\nvt 0.1401 0.355\r\nvt 0.0982 0.3615\r\nvt 0.097 0.3263\r\nvt 0.1389 0.3207\r\nvt 0.0582 0.3695\r\nvt 0.057 0.3335\r\nvt 0.0188 0.3423\r\nvt 0.0176 0.3072\r\nvt 0.0202 0.379\r\nvt 0.0165 0.2756\r\nvt 0.0157 0.2495\r\nvt 0.1259 0.8435\r\nvt 0.1295 0.8715\r\nvt 0.1034 0.8693\r\nvt 0.0975 0.8442\r\nvt 0.1299 0.8977\r\nvt 0.1053 0.8919\r\nvt 0.1139 0.7824\r\nvt 0.1205 0.8137\r\nvt 0.0902 0.8165\r\nvt 0.0822 0.7867\r\nvt 0.1842 0.7802\r\nvt 0.1885 0.8148\r\nvt 0.1533 0.8134\r\nvt 0.1479 0.7805\r\nvt 0.1916 0.8483\r\nvt 0.1574 0.8451\r\nvt 0.1925 0.911\r\nvt 0.1594 0.9042\r\nvt 0.1597 0.8755\r\nvt 0.1932 0.8804\r\nvt 0.0999 0.7165\r\nvt 0.107 0.7499\r\nvt 0.0741 0.7555\r\nvt 0.0661 0.7233\r\nvt 0.0864 0.6481\r\nvt 0.093 0.6825\r\nvt 0.0583 0.6903\r\nvt 0.0511 0.6568\r\nvt 0.1634 0.6356\r\nvt 0.1686 0.6721\r\nvt 0.1298 0.6765\r\nvt 0.1239 0.6411\r\nvt 0.1739 0.7086\r\nvt 0.1359 0.7117\r\nvt 0.1421 0.7465\r\nvt 0.1792 0.7447\r\nvt 0.3194 0.2357\r\nvt 0.3195 0.2567\r\nvt 0.2731 0.2555\r\nvt 0.2729 0.2338\r\nvt 0.3197 0.2822\r\nvt 0.2735 0.282\r\nvt 0.2273 0.2828\r\nvt 0.2267 0.2553\r\nvt 0.2263 0.2327\r\nvt 0.3205 0.3415\r\nvt 0.2747 0.3432\r\nvt 0.274 0.3117\r\nvt 0.32 0.3109\r\nvt 0.2289 0.346\r\nvt 0.228 0.3135\r\nvt 0.1828 0.3164\r\nvt 0.1839 0.3499\r\nvt 0.1819 0.2847\r\nvt 0.1806 0.2329\r\nvt 0.1811 0.2563\r\nvt 0.0467 0.1386\r\nvt 0.0482 0.1518\r\nvt 0.0068 0.1555\r\nvt 0.0047 0.1409\r\nvt 0.0495 0.1634\r\nvt 0.0086 0.1683\r\nvt 0.0436 0.1087\r\nvt 0.0452 0.1241\r\nvt 0.0024 0.1249\r\nvt -0 0.108\r\nvt 0.1355 0.114\r\nvt 0.1353 0.127\r\nvt 0.0895 0.1248\r\nvt 0.0889 0.1107\r\nvt 0.1353 0.139\r\nvt 0.0902 0.138\r\nvt 0.1355 0.1597\r\nvt 0.0917 0.1606\r\nvt 0.091 0.1499\r\nvt 0.1353 0.1499\r\nvt 0.2182 0.7441\r\nvt 0.2223 0.781\r\nvt 0.2137 0.7066\r\nvt 0.2546 0.7057\r\nvt 0.2584 0.7443\r\nvt 0.2618 0.7825\r\nvt 0.2045 0.6314\r\nvt 0.209 0.669\r\nvt 0.2467 0.6283\r\nvt 0.2506 0.6669\r\nvt 0.3324 0.6246\r\nvt 0.335 0.665\r\nvt 0.2928 0.6657\r\nvt 0.2895 0.6261\r\nvt 0.3376 0.7057\r\nvt 0.2961 0.7055\r\nvt 0.3423 0.7864\r\nvt 0.3019 0.7844\r\nvt 0.2992 0.7452\r\nvt 0.3401 0.7463\r\nvt 0.2291 0.8856\r\nvt 0.2283 0.9176\r\nvt 0.2282 0.852\r\nvt 0.2663 0.8559\r\nvt 0.2669 0.8908\r\nvt 0.266 0.9239\r\nvt 0.2257 0.817\r\nvt 0.2645 0.8198\r\nvt 0.3439 0.8255\r\nvt 0.3041 0.8227\r\nvt 0.3449 0.8635\r\nvt 0.3055 0.8599\r\nvt 0.3443 0.9347\r\nvt 0.3049 0.9297\r\nvt 0.3058 0.8957\r\nvt 0.3451 0.9\r\nvt 0.1814 0.1516\r\nvt 0.1811 0.1605\r\nvt 0.1817 0.1417\r\nvt 0.2289 0.1458\r\nvt 0.2284 0.1548\r\nvt 0.2279 0.1629\r\nvt 0.1829 0.1188\r\nvt 0.1822 0.1308\r\nvt 0.2307 0.1248\r\nvt 0.2297 0.1358\r\nvt 0.3247 0.1382\r\nvt 0.3237 0.1479\r\nvt 0.2771 0.1416\r\nvt 0.2781 0.1314\r\nvt 0.3229 0.1563\r\nvt 0.2762 0.1508\r\nvt 0.3218 0.1707\r\nvt 0.2751 0.1664\r\nvt 0.2756 0.1589\r\nvt 0.3223 0.1638\r\nvt 0.3805 0.7473\r\nvt 0.3821 0.7881\r\nvt 0.3787 0.7061\r\nvt 0.4193 0.7065\r\nvt 0.4206 0.7483\r\nvt 0.4216 0.7895\r\nvt 0.3748 0.6237\r\nvt 0.3767 0.6647\r\nvt 0.4167 0.6232\r\nvt 0.418 0.6647\r\nvt 0.5 0.6229\r\nvt 0.5 0.6648\r\nvt 0.4591 0.6647\r\nvt 0.4584 0.623\r\nvt 0.5 0.707\r\nvt 0.4597 0.7069\r\nvt 0.5 0.7908\r\nvt 0.4609 0.7905\r\nvt 0.4603 0.7489\r\nvt 0.5 0.7492\r\nvt 0.3841 0.9036\r\nvt 0.3834 0.9388\r\nvt 0.384 0.8665\r\nvt 0.4228 0.8688\r\nvt 0.4229 0.9062\r\nvt 0.4224 0.9417\r\nvt 0.3833 0.8279\r\nvt 0.4224 0.8298\r\nvt 0.5 0.8314\r\nvt 0.4613 0.831\r\nvt 0.5 0.8708\r\nvt 0.4615 0.8702\r\nvt 0.5 0.9442\r\nvt 0.4612 0.9435\r\nvt 0.4615 0.9079\r\nvt 0.5 0.9084\r\nvt 0.3678 0.169\r\nvt 0.3674 0.1755\r\nvt 0.3683 0.162\r\nvt 0.4128 0.1671\r\nvt 0.4124 0.1738\r\nvt 0.412 0.1799\r\nvt 0.3698 0.1449\r\nvt 0.369 0.1541\r\nvt 0.4138 0.1506\r\nvt 0.4132 0.1595\r\nvt 0.5 0.1562\r\nvt 0.5 0.165\r\nvt 0.4568 0.1634\r\nvt 0.4571 0.1546\r\nvt 0.5 0.1723\r\nvt 0.4565 0.1708\r\nvt 0.5 0.1845\r\nvt 0.4562 0.1832\r\nvt 0.4563 0.1773\r\nvt 0.5 0.1787\r\nvt 0.094 0.2199\r\nvt 0.136 0.2172\r\nvt 0.0538 0.2245\r\nvt 0.0534 0.2119\r\nvt 0.0937 0.2076\r\nvt 0.1359 0.2052\r\nvt 0.0151 0.231\r\nvt 0.0145 0.2183\r\nvt 0.0527 0.196\r\nvt 0.0531 0.203\r\nvt 0.0139 0.2093\r\nvt 0.0133 0.2023\r\nvt 0.1359 0.1898\r\nvt 0.1358 0.1966\r\nvt 0.0935 0.1989\r\nvt 0.0934 0.1919\r\nvt 0.2729 0.2181\r\nvt 0.3195 0.2206\r\nvt 0.2262 0.2166\r\nvt 0.2263 0.2052\r\nvt 0.273 0.2071\r\nvt 0.3197 0.2099\r\nvt 0.1803 0.2162\r\nvt 0.1803 0.2044\r\nvt 0.1806 0.1894\r\nvt 0.1804 0.1961\r\nvt 0.2268 0.1905\r\nvt 0.2265 0.197\r\nvt 0.3203 0.196\r\nvt 0.32 0.2022\r\nvt 0.2733 0.1992\r\nvt 0.2736 0.1928\r\nvt 0.0506 0.1732\r\nvt 0.0102 0.1788\r\nvt 0.0515 0.1816\r\nvt 0.0114 0.1876\r\nvt 0.1356 0.1682\r\nvt 0.0923 0.1697\r\nvt 0.1357 0.1759\r\nvt 0.0928 0.1777\r\nvt 0.0931 0.185\r\nvt 0.1358 0.183\r\nvt 0.0521 0.189\r\nvt 0.0125 0.1952\r\nvt 0.181 0.1686\r\nvt 0.2276 0.1704\r\nvt 0.2273 0.1773\r\nvt 0.1808 0.1759\r\nvt 0.3213 0.1773\r\nvt 0.2746 0.1734\r\nvt 0.3209 0.1836\r\nvt 0.2742 0.18\r\nvt 0.2739 0.1864\r\nvt 0.3206 0.1898\r\nvt 0.227 0.184\r\nvt 0.1807 0.1827\r\nvt 0.367 0.1817\r\nvt 0.4118 0.1858\r\nvt 0.4115 0.1917\r\nvt 0.3666 0.1878\r\nvt 0.5 0.1903\r\nvt 0.456 0.189\r\nvt 0.5 0.196\r\nvt 0.4559 0.1947\r\nvt 0.5 0.2074\r\nvt 0.4556 0.2063\r\nvt 0.4557 0.2005\r\nvt 0.5 0.2017\r\nvt 0.4111 0.2035\r\nvt 0.4113 0.1976\r\nvt 0.3663 0.1938\r\nvt 0.366 0.1998\r\nvt 0.0753 0.5791\r\nvt 0.0805 0.6136\r\nvt 0.0446 0.6231\r\nvt 0.0388 0.5892\r\nvt 0.0669 0.5098\r\nvt 0.0707 0.5445\r\nvt 0.0339 0.5551\r\nvt 0.0298 0.5206\r\nvt 0.1474 0.4925\r\nvt 0.1505 0.5277\r\nvt 0.1097 0.5354\r\nvt 0.1062 0.5005\r\nvt 0.1543 0.5633\r\nvt 0.1138 0.5705\r\nvt 0.1186 0.6057\r\nvt 0.1586 0.5993\r\nvt 0.2003 0.5941\r\nvt 0.1965 0.5574\r\nvt 0.2398 0.5526\r\nvt 0.2431 0.5901\r\nvt 0.1904 0.4857\r\nvt 0.1931 0.5212\r\nvt 0.2345 0.4802\r\nvt 0.2369 0.5159\r\nvt 0.3241 0.4724\r\nvt 0.3258 0.5086\r\nvt 0.2813 0.5118\r\nvt 0.2793 0.4758\r\nvt 0.3277 0.5462\r\nvt 0.2837 0.549\r\nvt 0.2865 0.5871\r\nvt 0.33 0.5849\r\nvt 0.3729 0.5835\r\nvt 0.3712 0.5443\r\nvt 0.4144 0.543\r\nvt 0.4155 0.5825\r\nvt 0.3685 0.4701\r\nvt 0.3698 0.5064\r\nvt 0.4125 0.4685\r\nvt 0.4134 0.5049\r\nvt 0.5 0.4675\r\nvt 0.5 0.5039\r\nvt 0.4567 0.5041\r\nvt 0.4563 0.4677\r\nvt 0.5 0.5422\r\nvt 0.4572 0.5424\r\nvt 0.4578 0.5821\r\nvt 0.5 0.582\r\nvt 0.1414 0.3892\r\nvt 0.0996 0.3964\r\nvt 0.143 0.4234\r\nvt 0.1013 0.4311\r\nvt 0.0616 0.4402\r\nvt 0.0597 0.405\r\nvt 0.1035 0.4657\r\nvt 0.1449 0.4577\r\nvt 0.0639 0.4751\r\nvt 0.0265 0.4858\r\nvt 0.0239 0.4506\r\nvt 0.0219 0.415\r\nvt 0.3211 0.3727\r\nvt 0.2754 0.3752\r\nvt 0.3218 0.4047\r\nvt 0.2764 0.4077\r\nvt 0.2311 0.4118\r\nvt 0.2299 0.3787\r\nvt 0.2777 0.4412\r\nvt 0.3228 0.4379\r\nvt 0.2326 0.4456\r\nvt 0.1882 0.451\r\nvt 0.1864 0.417\r\nvt 0.1851 0.3833\r\nvt 0.5 0.3406\r\nvt 0.4554 0.3404\r\nvt 0.4553 0.3119\r\nvt 0.5 0.3122\r\nvt 0.4107 0.3404\r\nvt 0.4105 0.3114\r\nvt 0.4104 0.2844\r\nvt 0.4553 0.2855\r\nvt 0.5 0.286\r\nvt 0.3655 0.3109\r\nvt 0.3658 0.3406\r\nvt 0.3653 0.2831\r\nvt 0.3652 0.2383\r\nvt 0.3652 0.2585\r\nvt 0.4104 0.241\r\nvt 0.4104 0.2606\r\nvt 0.5 0.2441\r\nvt 0.5 0.263\r\nvt 0.4553 0.2622\r\nvt 0.4553 0.2431\r\nvt 0.4554 0.2293\r\nvt 0.5 0.2303\r\nvt 0.4105 0.2269\r\nvt 0.4107 0.2168\r\nvt 0.4554 0.2194\r\nvt 0.5 0.2205\r\nvt 0.3653 0.2238\r\nvt 0.3655 0.2134\r\nvt 0.3657 0.2059\r\nvt 0.4109 0.2094\r\nvt 0.5 0.2133\r\nvt 0.4555 0.2121\r\nvt 0.456 0.4334\r\nvt 0.5 0.4332\r\nvt 0.4119 0.4341\r\nvt 0.4114 0.4015\r\nvt 0.4557 0.4009\r\nvt 0.5 0.4008\r\nvt 0.3675 0.4356\r\nvt 0.3668 0.4027\r\nvt 0.3662 0.3712\r\nvt 0.411 0.3703\r\nvt 0.5 0.37\r\nvt 0.4555 0.37\r\nvt 0.8636 0.2344\r\nvt 0.9056 0.2376\r\nvt 0.905 0.2625\r\nvt 0.863 0.2586\r\nvt 0.9457 0.2425\r\nvt 0.945 0.2681\r\nvt 0.9441 0.299\r\nvt 0.9041 0.2927\r\nvt 0.8621 0.2879\r\nvt 0.9835 0.2755\r\nvt 0.9825 0.3071\r\nvt 0.9843 0.2494\r\nvt 0.9418 0.3694\r\nvt 0.943 0.3335\r\nvt 0.9813 0.3423\r\nvt 0.9798 0.3789\r\nvt 0.8599 0.355\r\nvt 0.8611 0.3206\r\nvt 0.903 0.3263\r\nvt 0.9018 0.3615\r\nvt 0.8701 0.8977\r\nvt 0.8706 0.8715\r\nvt 0.8966 0.8692\r\nvt 0.8948 0.8918\r\nvt 0.8741 0.8435\r\nvt 0.9026 0.8442\r\nvt 0.8076 0.9109\r\nvt 0.8069 0.8804\r\nvt 0.8404 0.8754\r\nvt 0.8407 0.9042\r\nvt 0.8084 0.8482\r\nvt 0.8426 0.8451\r\nvt 0.8159 0.7802\r\nvt 0.8521 0.7804\r\nvt 0.8468 0.8134\r\nvt 0.8116 0.8148\r\nvt 0.8862 0.7824\r\nvt 0.8796 0.8137\r\nvt 0.9099 0.8164\r\nvt 0.9178 0.7866\r\nvt 0.8931 0.7499\r\nvt 0.9259 0.7555\r\nvt 0.9001 0.7165\r\nvt 0.934 0.7233\r\nvt 0.8208 0.7447\r\nvt 0.858 0.7464\r\nvt 0.8261 0.7085\r\nvt 0.8641 0.7117\r\nvt 0.8367 0.6355\r\nvt 0.8761 0.641\r\nvt 0.8702 0.6765\r\nvt 0.8315 0.6721\r\nvt 0.9136 0.648\r\nvt 0.9071 0.6824\r\nvt 0.9417 0.6903\r\nvt 0.949 0.6568\r\nvt 0.6806 0.2357\r\nvt 0.7271 0.2337\r\nvt 0.7269 0.2555\r\nvt 0.6805 0.2566\r\nvt 0.7736 0.2327\r\nvt 0.7733 0.2553\r\nvt 0.7727 0.2827\r\nvt 0.7265 0.282\r\nvt 0.6803 0.2821\r\nvt 0.8189 0.2563\r\nvt 0.8194 0.2329\r\nvt 0.8181 0.2847\r\nvt 0.8161 0.3498\r\nvt 0.8172 0.3164\r\nvt 0.771 0.346\r\nvt 0.7719 0.3135\r\nvt 0.6795 0.3415\r\nvt 0.68 0.3109\r\nvt 0.726 0.3117\r\nvt 0.7253 0.3432\r\nvt 0.9505 0.1634\r\nvt 0.9518 0.1517\r\nvt 0.9932 0.1555\r\nvt 0.9914 0.1683\r\nvt 0.9533 0.1386\r\nvt 0.9953 0.1408\r\nvt 0.8645 0.1596\r\nvt 0.8646 0.1499\r\nvt 0.909 0.1499\r\nvt 0.9083 0.1605\r\nvt 0.8647 0.139\r\nvt 0.9097 0.1379\r\nvt 0.8645 0.114\r\nvt 0.9111 0.1106\r\nvt 0.9104 0.1248\r\nvt 0.8647 0.127\r\nvt 0.9564 0.1087\r\nvt 0.9548 0.1241\r\nvt 0.9976 0.1248\r\nvt 1 0.1079\r\nvt 0.7777 0.7809\r\nvt 0.7818 0.744\r\nvt 0.7383 0.7824\r\nvt 0.7417 0.7443\r\nvt 0.7454 0.7057\r\nvt 0.7863 0.7066\r\nvt 0.6578 0.7863\r\nvt 0.6599 0.7463\r\nvt 0.7008 0.7451\r\nvt 0.6981 0.7843\r\nvt 0.6624 0.7057\r\nvt 0.7039 0.7055\r\nvt 0.6676 0.6246\r\nvt 0.7104 0.6261\r\nvt 0.7072 0.6656\r\nvt 0.665 0.665\r\nvt 0.7533 0.6283\r\nvt 0.7494 0.6669\r\nvt 0.791 0.669\r\nvt 0.7955 0.6313\r\nvt 0.7718 0.9176\r\nvt 0.7709 0.8855\r\nvt 0.7341 0.9239\r\nvt 0.7331 0.8907\r\nvt 0.7337 0.8559\r\nvt 0.7719 0.8519\r\nvt 0.6558 0.9347\r\nvt 0.6549 0.9\r\nvt 0.6942 0.8956\r\nvt 0.6952 0.9297\r\nvt 0.6551 0.8635\r\nvt 0.6946 0.8599\r\nvt 0.6959 0.8227\r\nvt 0.6561 0.8255\r\nvt 0.7355 0.8197\r\nvt 0.7743 0.817\r\nvt 0.8188 0.1605\r\nvt 0.8186 0.1516\r\nvt 0.772 0.1629\r\nvt 0.7716 0.1547\r\nvt 0.771 0.1457\r\nvt 0.8182 0.1417\r\nvt 0.6782 0.1707\r\nvt 0.6776 0.1638\r\nvt 0.7244 0.1589\r\nvt 0.7249 0.1664\r\nvt 0.677 0.1563\r\nvt 0.7237 0.1507\r\nvt 0.6753 0.1382\r\nvt 0.7218 0.1314\r\nvt 0.7229 0.1416\r\nvt 0.6762 0.1479\r\nvt 0.7693 0.1247\r\nvt 0.7703 0.1358\r\nvt 0.8177 0.1308\r\nvt 0.817 0.1188\r\nvt 0.6179 0.7881\r\nvt 0.6195 0.7473\r\nvt 0.5784 0.7895\r\nvt 0.5794 0.7483\r\nvt 0.5807 0.7065\r\nvt 0.6213 0.7061\r\nvt 0.5397 0.7489\r\nvt 0.5392 0.7905\r\nvt 0.5403 0.7068\r\nvt 0.5416 0.623\r\nvt 0.5409 0.6647\r\nvt 0.5833 0.6232\r\nvt 0.582 0.6646\r\nvt 0.6233 0.6647\r\nvt 0.6252 0.6237\r\nvt 0.6166 0.9387\r\nvt 0.6159 0.9036\r\nvt 0.5777 0.9417\r\nvt 0.5772 0.9062\r\nvt 0.5772 0.8688\r\nvt 0.616 0.8665\r\nvt 0.5386 0.9079\r\nvt 0.5388 0.9435\r\nvt 0.5386 0.8702\r\nvt 0.5388 0.831\r\nvt 0.5776 0.8298\r\nvt 0.6167 0.8279\r\nvt 0.6326 0.1754\r\nvt 0.6321 0.169\r\nvt 0.5879 0.1799\r\nvt 0.5876 0.1738\r\nvt 0.5872 0.1671\r\nvt 0.6316 0.162\r\nvt 0.5436 0.1773\r\nvt 0.5438 0.1832\r\nvt 0.5434 0.1708\r\nvt 0.5429 0.1546\r\nvt 0.5432 0.1634\r\nvt 0.5861 0.1506\r\nvt 0.5867 0.1595\r\nvt 0.631 0.1541\r\nvt 0.6301 0.1449\r\nvt 0.864 0.2172\r\nvt 0.906 0.2199\r\nvt 0.8641 0.2051\r\nvt 0.9063 0.2075\r\nvt 0.9466 0.2119\r\nvt 0.9462 0.2244\r\nvt 0.8641 0.1897\r\nvt 0.9066 0.1919\r\nvt 0.9064 0.1988\r\nvt 0.8641 0.1966\r\nvt 0.9473 0.196\r\nvt 0.9469 0.203\r\nvt 0.9861 0.2093\r\nvt 0.9855 0.2182\r\nvt 0.9867 0.2022\r\nvt 0.9849 0.231\r\nvt 0.6805 0.2206\r\nvt 0.7271 0.2181\r\nvt 0.6803 0.2099\r\nvt 0.7269 0.2071\r\nvt 0.7737 0.2052\r\nvt 0.7737 0.2165\r\nvt 0.6797 0.1959\r\nvt 0.7264 0.1928\r\nvt 0.7267 0.1991\r\nvt 0.68 0.2022\r\nvt 0.7732 0.1905\r\nvt 0.7735 0.197\r\nvt 0.8195 0.196\r\nvt 0.8194 0.1894\r\nvt 0.8197 0.2044\r\nvt 0.8196 0.2161\r\nvt 0.9485 0.1815\r\nvt 0.9494 0.1731\r\nvt 0.9898 0.1788\r\nvt 0.9886 0.1875\r\nvt 0.9479 0.189\r\nvt 0.9876 0.1952\r\nvt 0.8642 0.1829\r\nvt 0.9069 0.185\r\nvt 0.8642 0.1759\r\nvt 0.9072 0.1777\r\nvt 0.9077 0.1697\r\nvt 0.8644 0.1682\r\nvt 0.819 0.1685\r\nvt 0.8191 0.1758\r\nvt 0.7727 0.1773\r\nvt 0.7724 0.1704\r\nvt 0.8193 0.1827\r\nvt 0.773 0.184\r\nvt 0.6794 0.1898\r\nvt 0.7261 0.1864\r\nvt 0.679 0.1836\r\nvt 0.7257 0.18\r\nvt 0.7253 0.1734\r\nvt 0.6786 0.1772\r\nvt 0.633 0.1817\r\nvt 0.6333 0.1877\r\nvt 0.5884 0.1917\r\nvt 0.5882 0.1858\r\nvt 0.6339 0.1998\r\nvt 0.6336 0.1938\r\nvt 0.5889 0.2034\r\nvt 0.5887 0.1976\r\nvt 0.5442 0.2005\r\nvt 0.5443 0.2063\r\nvt 0.5441 0.1947\r\nvt 0.5439 0.189\r\nvt 0.9195 0.6136\r\nvt 0.9555 0.6231\r\nvt 0.9248 0.579\r\nvt 0.9612 0.5892\r\nvt 0.8414 0.5992\r\nvt 0.8814 0.6057\r\nvt 0.8457 0.5633\r\nvt 0.8862 0.5704\r\nvt 0.8526 0.4924\r\nvt 0.8938 0.5004\r\nvt 0.8904 0.5353\r\nvt 0.8495 0.5276\r\nvt 0.9331 0.5098\r\nvt 0.9293 0.5444\r\nvt 0.9661 0.555\r\nvt 0.9703 0.5206\r\nvt 0.7997 0.5941\r\nvt 0.7569 0.5901\r\nvt 0.7602 0.5526\r\nvt 0.8035 0.5574\r\nvt 0.67 0.5849\r\nvt 0.7135 0.5871\r\nvt 0.6722 0.5462\r\nvt 0.7162 0.5489\r\nvt 0.6759 0.4724\r\nvt 0.7207 0.4758\r\nvt 0.7187 0.5118\r\nvt 0.6742 0.5086\r\nvt 0.7655 0.4802\r\nvt 0.7631 0.5159\r\nvt 0.8069 0.5212\r\nvt 0.8096 0.4857\r\nvt 0.6271 0.5834\r\nvt 0.5845 0.5825\r\nvt 0.5856 0.543\r\nvt 0.6287 0.5442\r\nvt 0.5422 0.5821\r\nvt 0.5427 0.5424\r\nvt 0.5437 0.4677\r\nvt 0.5432 0.5041\r\nvt 0.5874 0.4685\r\nvt 0.5866 0.5049\r\nvt 0.6302 0.5064\r\nvt 0.6315 0.47\r\nvt 0.9004 0.3964\r\nvt 0.8585 0.3891\r\nvt 0.9403 0.405\r\nvt 0.9384 0.4401\r\nvt 0.8987 0.4311\r\nvt 0.857 0.4233\r\nvt 0.9782 0.415\r\nvt 0.9761 0.4506\r\nvt 0.9361 0.475\r\nvt 0.9735 0.4858\r\nvt 0.8551 0.4577\r\nvt 0.8965 0.4657\r\nvt 0.7245 0.3751\r\nvt 0.6789 0.3727\r\nvt 0.7701 0.3786\r\nvt 0.7689 0.4118\r\nvt 0.7236 0.4077\r\nvt 0.6781 0.4047\r\nvt 0.8149 0.3833\r\nvt 0.8135 0.4169\r\nvt 0.8118 0.451\r\nvt 0.7674 0.4455\r\nvt 0.6772 0.4379\r\nvt 0.7223 0.4412\r\nvt 0.5446 0.3119\r\nvt 0.5445 0.3404\r\nvt 0.5447 0.2855\r\nvt 0.5895 0.2844\r\nvt 0.5894 0.3113\r\nvt 0.5892 0.3404\r\nvt 0.5447 0.2431\r\nvt 0.5447 0.2622\r\nvt 0.5895 0.241\r\nvt 0.5896 0.2606\r\nvt 0.6348 0.2585\r\nvt 0.6348 0.2383\r\nvt 0.6347 0.2831\r\nvt 0.6342 0.3406\r\nvt 0.6345 0.3109\r\nvt 0.5446 0.2293\r\nvt 0.5445 0.2194\r\nvt 0.5893 0.2168\r\nvt 0.5894 0.2269\r\nvt 0.5444 0.2121\r\nvt 0.5891 0.2094\r\nvt 0.6342 0.2058\r\nvt 0.6345 0.2134\r\nvt 0.6346 0.2237\r\nvt 0.544 0.4334\r\nvt 0.5442 0.4009\r\nvt 0.5886 0.4015\r\nvt 0.5881 0.4341\r\nvt 0.5444 0.37\r\nvt 0.589 0.3703\r\nvt 0.6338 0.3711\r\nvt 0.6332 0.4027\r\nvt 0.6325 0.4356\r\no panel_front\r\nusemtl Material__36\r\nf 13018/13724/11533 13019/13725/11534 13020/13726/11535 13021/13727/11536\r\nf 13022/13728/11537 13023/13729/11538 13020/13726/11535 13019/13725/11534\r\nf 13024/13730/11539 13025/13731/11540 13020/13726/11535 13023/13729/11538\r\nf 13026/13732/11541 13021/13727/11536 13020/13726/11535 13025/13731/11540\r\nf 13027/13733/11542 13028/13734/11543 13029/13735/11544 13030/13736/11545\r\nf 13031/13737/11546 13032/13738/11547 13029/13735/11544 13028/13734/11543\r\nf 13024/13730/11539 13023/13729/11538 13029/13735/11544 13032/13738/11547\r\nf 13022/13728/11537 13030/13736/11545 13029/13735/11544 13023/13729/11538\r\nf 13024/13730/11539 13032/13738/11547 13033/13739/11548 13034/13740/11549\r\nf 13031/13737/11546 13035/13741/11550 13033/13739/11548 13032/13738/11547\r\nf 13026/13732/11541 13025/13731/11540 13036/13742/11551 13037/13743/11552\r\nf 13024/13730/11539 13034/13740/11549 13036/13742/11551 13025/13731/11540\r\nf 13038/13744/11553 13039/13745/11554 13040/13746/11555 13041/13747/11556\r\nf 13042/13748/11557 13043/13749/11558 13040/13746/11555 13039/13745/11554\r\nf 13044/13750/11559 13045/13751/11560 13046/13752/11561 13047/13753/11562\r\nf 13038/13744/11553 13041/13747/11556 13046/13752/11561 13045/13751/11560\r\nf 13048/13754/11563 13049/13755/11564 13050/13756/11565 13051/13757/11566\r\nf 13052/13758/11567 13053/13759/11568 13050/13756/11565 13049/13755/11564\r\nf 13038/13744/11553 13045/13751/11560 13050/13756/11565 13053/13759/11568\r\nf 13044/13750/11559 13051/13757/11566 13050/13756/11565 13045/13751/11560\r\nf 13054/13760/11569 13055/13761/11570 13056/13762/11571 13057/13763/11572\r\nf 13042/13748/11557 13039/13745/11554 13056/13762/11571 13055/13761/11570\r\nf 13038/13744/11553 13053/13759/11568 13056/13762/11571 13039/13745/11554\r\nf 13052/13758/11567 13057/13763/11572 13056/13762/11571 13053/13759/11568\r\nf 13058/13764/11573 13059/13765/11574 13060/13766/11575 13061/13767/11576\r\nf 13044/13750/11559 13047/13753/11562 13060/13766/11575 13059/13765/11574\r\nf 13062/13768/11577 13063/13769/11578 13064/13770/11579 13065/13771/11580\r\nf 13058/13764/11573 13061/13767/11576 13064/13770/11579 13063/13769/11578\r\nf 13066/13772/11581 13067/13773/11582 13068/13774/11583 13069/13775/11584\r\nf 13070/13776/11585 13071/13777/11586 13068/13774/11583 13067/13773/11582\r\nf 13058/13764/11573 13063/13769/11578 13068/13774/11583 13071/13777/11586\r\nf 13062/13768/11577 13069/13775/11584 13068/13774/11583 13063/13769/11578\r\nf 13048/13754/11563 13051/13757/11566 13072/13778/11587 13073/13779/11588\r\nf 13044/13750/11559 13059/13765/11574 13072/13778/11587 13051/13757/11566\r\nf 13058/13764/11573 13071/13777/11586 13072/13778/11587 13059/13765/11574\r\nf 13070/13776/11585 13073/13779/11588 13072/13778/11587 13071/13777/11586\r\nf 13074/13780/11589 13075/13781/11590 13076/13782/11591 13077/13783/11592\r\nf 13078/13784/11593 13079/13785/11594 13076/13782/11591 13075/13781/11590\r\nf 13080/13786/11595 13081/13787/11596 13076/13782/11591 13079/13785/11594\r\nf 13082/13788/11597 13077/13783/11592 13076/13782/11591 13081/13787/11596\r\nf 13083/13789/11598 13084/13790/11599 13085/13791/11600 13086/13792/11601\r\nf 13087/13793/11602 13088/13794/11603 13085/13791/11600 13084/13790/11599\r\nf 13080/13786/11595 13079/13785/11594 13085/13791/11600 13088/13794/11603\r\nf 13078/13784/11593 13086/13792/11601 13085/13791/11600 13079/13785/11594\r\nf 13027/13733/11542 13030/13736/11545 13089/13795/11604 13090/13796/11605\r\nf 13022/13728/11537 13091/13797/11606 13089/13795/11604 13030/13736/11545\r\nf 13080/13786/11595 13088/13794/11603 13089/13795/11604 13091/13797/11606\r\nf 13087/13793/11602 13090/13796/11605 13089/13795/11604 13088/13794/11603\r\nf 13018/13724/11533 13092/13798/11607 13093/13799/11608 13019/13725/11534\r\nf 13082/13788/11597 13081/13787/11596 13093/13799/11608 13092/13798/11607\r\nf 13080/13786/11595 13091/13797/11606 13093/13799/11608 13081/13787/11596\r\nf 13022/13728/11537 13019/13725/11534 13093/13799/11608 13091/13797/11606\r\nf 13094/13800/11609 13095/13801/11610 13096/13802/11611 13097/13803/11612\r\nf 13098/13804/11613 13099/13805/11614 13096/13802/11611 13095/13801/11610\r\nf 13100/13806/11615 13101/13807/11616 13102/13808/11617 13103/13809/11618\r\nf 13094/13800/11609 13097/13803/11612 13102/13808/11617 13101/13807/11616\r\nf 13104/13810/11619 13105/13811/11620 13106/13812/11621 13107/13813/11622\r\nf 13108/13814/11623 13109/13815/11624 13106/13812/11621 13105/13811/11620\r\nf 13094/13800/11609 13101/13807/11616 13106/13812/11621 13109/13815/11624\r\nf 13100/13806/11615 13107/13813/11622 13106/13812/11621 13101/13807/11616\r\nf 13110/13816/11625 13111/13817/11626 13112/13818/11627 13113/13819/11628\r\nf 13098/13804/11613 13095/13801/11610 13112/13818/11627 13111/13817/11626\r\nf 13094/13800/11609 13109/13815/11624 13112/13818/11627 13095/13801/11610\r\nf 13108/13814/11623 13113/13819/11628 13112/13818/11627 13109/13815/11624\r\nf 13048/13754/11563 13073/13779/11588 13114/13820/11629 13115/13821/11630\r\nf 13070/13776/11585 13116/13822/11631 13114/13820/11629 13073/13779/11588\r\nf 13117/13823/11632 13118/13824/11633 13114/13820/11629 13116/13822/11631\r\nf 13119/13825/11634 13115/13821/11630 13114/13820/11629 13118/13824/11633\r\nf 13066/13772/11581 13120/13826/11635 13121/13827/11636 13067/13773/11582\r\nf 13122/13828/11637 13123/13829/11638 13121/13827/11636 13120/13826/11635\r\nf 13117/13823/11632 13116/13822/11631 13121/13827/11636 13123/13829/11638\r\nf 13070/13776/11585 13067/13773/11582 13121/13827/11636 13116/13822/11631\r\nf 13124/13830/11639 13125/13831/11640 13126/13832/11641 13127/13833/11642\r\nf 13128/13834/11643 13129/13835/11644 13126/13832/11641 13125/13831/11640\r\nf 13117/13823/11632 13123/13829/11638 13126/13832/11641 13129/13835/11644\r\nf 13122/13828/11637 13127/13833/11642 13126/13832/11641 13123/13829/11638\r\nf 13130/13836/11645 13131/13837/11646 13132/13838/11647 13133/13839/11648\r\nf 13119/13825/11634 13118/13824/11633 13132/13838/11647 13131/13837/11646\r\nf 13117/13823/11632 13129/13835/11644 13132/13838/11647 13118/13824/11633\r\nf 13128/13834/11643 13133/13839/11648 13132/13838/11647 13129/13835/11644\r\nf 13054/13760/11569 13057/13763/11572 13134/13840/11649 13135/13841/11650\r\nf 13052/13758/11567 13136/13842/11651 13134/13840/11649 13057/13763/11572\r\nf 13137/13843/11652 13138/13844/11653 13134/13840/11649 13136/13842/11651\r\nf 13139/13845/11654 13135/13841/11650 13134/13840/11649 13138/13844/11653\r\nf 13048/13754/11563 13115/13821/11630 13140/13846/11655 13049/13755/11564\r\nf 13119/13825/11634 13141/13847/11656 13140/13846/11655 13115/13821/11630\r\nf 13137/13843/11652 13136/13842/11651 13140/13846/11655 13141/13847/11656\r\nf 13052/13758/11567 13049/13755/11564 13140/13846/11655 13136/13842/11651\r\nf 13130/13836/11645 13142/13848/11657 13143/13849/11658 13131/13837/11646\r\nf 13144/13850/11659 13145/13851/11660 13143/13849/11658 13142/13848/11657\r\nf 13137/13843/11652 13141/13847/11656 13143/13849/11658 13145/13851/11660\r\nf 13119/13825/11634 13131/13837/11646 13143/13849/11658 13141/13847/11656\r\nf 13146/13852/11661 13147/13853/11662 13148/13854/11663 13149/13855/11664\r\nf 13139/13845/11654 13138/13844/11653 13148/13854/11663 13147/13853/11662\r\nf 13137/13843/11652 13145/13851/11660 13148/13854/11663 13138/13844/11653\r\nf 13144/13850/11659 13149/13855/11664 13148/13854/11663 13145/13851/11660\r\nf 13110/13816/11625 13113/13819/11628 13150/13856/11665 13151/13857/11666\r\nf 13108/13814/11623 13152/13858/11667 13150/13856/11665 13113/13819/11628\r\nf 13153/13859/11668 13154/13860/11669 13150/13856/11665 13152/13858/11667\r\nf 13155/13861/11670 13151/13857/11666 13150/13856/11665 13154/13860/11669\r\nf 13104/13810/11619 13156/13862/11671 13157/13863/11672 13105/13811/11620\r\nf 13158/13864/11673 13159/13865/11674 13157/13863/11672 13156/13862/11671\r\nf 13153/13859/11668 13152/13858/11667 13157/13863/11672 13159/13865/11674\r\nf 13108/13814/11623 13105/13811/11620 13157/13863/11672 13152/13858/11667\r\nf 13160/13866/11675 13161/13867/11676 13162/13868/11677 13163/13869/11678\r\nf 13164/13870/11679 13165/13871/11680 13162/13868/11677 13161/13867/11676\r\nf 13153/13859/11668 13159/13865/11674 13162/13868/11677 13165/13871/11680\r\nf 13158/13864/11673 13163/13869/11678 13162/13868/11677 13159/13865/11674\r\nf 13166/13872/11681 13167/13873/11682 13168/13874/11683 13169/13875/11684\r\nf 13155/13861/11670 13154/13860/11669 13168/13874/11683 13167/13873/11682\r\nf 13153/13859/11668 13165/13871/11680 13168/13874/11683 13154/13860/11669\r\nf 13164/13870/11679 13169/13875/11684 13168/13874/11683 13165/13871/11680\r\nf 13130/13836/11645 13133/13839/11648 13170/13876/11685 13171/13877/11686\r\nf 13128/13834/11643 13172/13878/11687 13170/13876/11685 13133/13839/11648\r\nf 13173/13879/11688 13174/13880/11689 13170/13876/11685 13172/13878/11687\r\nf 13175/13881/11690 13171/13877/11686 13170/13876/11685 13174/13880/11689\r\nf 13124/13830/11639 13176/13882/11691 13177/13883/11692 13125/13831/11640\r\nf 13178/13884/11693 13179/13885/11694 13177/13883/11692 13176/13882/11691\r\nf 13173/13879/11688 13172/13878/11687 13177/13883/11692 13179/13885/11694\r\nf 13128/13834/11643 13125/13831/11640 13177/13883/11692 13172/13878/11687\r\nf 13180/13886/11695 13181/13887/11696 13182/13888/11697 13183/13889/11698\r\nf 13184/13890/11699 13185/13891/11700 13182/13888/11697 13181/13887/11696\r\nf 13173/13879/11688 13179/13885/11694 13182/13888/11697 13185/13891/11700\r\nf 13178/13884/11693 13183/13889/11698 13182/13888/11697 13179/13885/11694\r\nf 13186/13892/11701 13187/13893/11702 13188/13894/11703 13189/13895/11704\r\nf 13175/13881/11690 13174/13880/11689 13188/13894/11703 13187/13893/11702\r\nf 13173/13879/11688 13185/13891/11700 13188/13894/11703 13174/13880/11689\r\nf 13184/13890/11699 13189/13895/11704 13188/13894/11703 13185/13891/11700\r\nf 13146/13852/11661 13149/13855/11664 13190/13896/11705 13191/13897/11706\r\nf 13144/13850/11659 13192/13898/11707 13190/13896/11705 13149/13855/11664\r\nf 13193/13899/11708 13194/13900/11709 13190/13896/11705 13192/13898/11707\r\nf 13195/13901/11710 13191/13897/11706 13190/13896/11705 13194/13900/11709\r\nf 13130/13836/11645 13171/13877/11686 13196/13902/11711 13142/13848/11657\r\nf 13175/13881/11690 13197/13903/11712 13196/13902/11711 13171/13877/11686\r\nf 13193/13899/11708 13192/13898/11707 13196/13902/11711 13197/13903/11712\r\nf 13144/13850/11659 13142/13848/11657 13196/13902/11711 13192/13898/11707\r\nf 13186/13892/11701 13198/13904/11713 13199/13905/11714 13187/13893/11702\r\nf 13200/13906/11715 13201/13907/11716 13199/13905/11714 13198/13904/11713\r\nf 13193/13899/11708 13197/13903/11712 13199/13905/11714 13201/13907/11716\r\nf 13175/13881/11690 13187/13893/11702 13199/13905/11714 13197/13903/11712\r\nf 13202/13908/11717 13203/13909/11718 13204/13910/11719 13205/13911/11720\r\nf 13195/13901/11710 13194/13900/11709 13204/13910/11719 13203/13909/11718\r\nf 13193/13899/11708 13201/13907/11716 13204/13910/11719 13194/13900/11709\r\nf 13200/13906/11715 13205/13911/11720 13204/13910/11719 13201/13907/11716\r\nf 13166/13872/11681 13169/13875/11684 13206/13912/11721 13207/13913/11722\r\nf 13164/13870/11679 13208/13914/11723 13206/13912/11721 13169/13875/11684\r\nf 13209/13915/11724 13210/13916/11725 13206/13912/11721 13208/13914/11723\r\nf 13211/13917/11726 13207/13913/11722 13206/13912/11721 13210/13916/11725\r\nf 13160/13866/11675 13212/13918/11727 13213/13919/11728 13161/13867/11676\r\nf 13214/13920/11729 13215/13921/11730 13213/13919/11728 13212/13918/11727\r\nf 13209/13915/11724 13208/13914/11723 13213/13919/11728 13215/13921/11730\r\nf 13164/13870/11679 13161/13867/11676 13213/13919/11728 13208/13914/11723\r\nf 13216/13922/11731 13217/13923/11732 13218/13924/11733 13219/13925/11734\r\nf 13220/13926/11735 13221/13927/11736 13218/13924/11733 13217/13923/11732\r\nf 13209/13915/11724 13215/13921/11730 13218/13924/11733 13221/13927/11736\r\nf 13214/13920/11729 13219/13925/11734 13218/13924/11733 13215/13921/11730\r\nf 13222/13928/11737 13223/13929/11738 13224/13930/11739 13225/13931/11740\r\nf 13211/13917/11726 13210/13916/11725 13224/13930/11739 13223/13929/11738\r\nf 13209/13915/11724 13221/13927/11736 13224/13930/11739 13210/13916/11725\r\nf 13220/13926/11735 13225/13931/11740 13224/13930/11739 13221/13927/11736\r\nf 13018/13724/11533 13021/13727/11536 13226/13932/11741 13227/13933/11742\r\nf 13026/13732/11541 13228/13934/11743 13226/13932/11741 13021/13727/11536\r\nf 13229/13935/11744 13230/13936/11745 13226/13932/11741 13228/13934/11743\r\nf 13231/13937/11746 13227/13933/11742 13226/13932/11741 13230/13936/11745\r\nf 13229/13935/11744 13228/13934/11743 13232/13938/11747 13233/13939/11748\r\nf 13026/13732/11541 13037/13743/11552 13232/13938/11747 13228/13934/11743\r\nf 13234/13940/11749 13235/13941/11750 13236/13942/11751 13237/13943/11752\r\nf 13229/13935/11744 13233/13939/11748 13236/13942/11751 13235/13941/11750\r\nf 13238/13944/11753 13239/13945/11754 13240/13946/11755 13241/13947/11756\r\nf 13231/13937/11746 13230/13936/11745 13240/13946/11755 13239/13945/11754\r\nf 13229/13935/11744 13235/13941/11750 13240/13946/11755 13230/13936/11745\r\nf 13234/13940/11749 13241/13947/11756 13240/13946/11755 13235/13941/11750\r\nf 13074/13780/11589 13077/13783/11592 13242/13948/11757 13243/13949/11758\r\nf 13082/13788/11597 13244/13950/11759 13242/13948/11757 13077/13783/11592\r\nf 13245/13951/11760 13246/13952/11761 13242/13948/11757 13244/13950/11759\r\nf 13247/13953/11762 13243/13949/11758 13242/13948/11757 13246/13952/11761\r\nf 13018/13724/11533 13227/13933/11742 13248/13954/11763 13092/13798/11607\r\nf 13231/13937/11746 13249/13955/11764 13248/13954/11763 13227/13933/11742\r\nf 13245/13951/11760 13244/13950/11759 13248/13954/11763 13249/13955/11764\r\nf 13082/13788/11597 13092/13798/11607 13248/13954/11763 13244/13950/11759\r\nf 13238/13944/11753 13250/13956/11765 13251/13957/11766 13239/13945/11754\r\nf 13252/13958/11767 13253/13959/11768 13251/13957/11766 13250/13956/11765\r\nf 13245/13951/11760 13249/13955/11764 13251/13957/11766 13253/13959/11768\r\nf 13231/13937/11746 13239/13945/11754 13251/13957/11766 13249/13955/11764\r\nf 13254/13960/11769 13255/13961/11770 13256/13962/11771 13257/13963/11772\r\nf 13247/13953/11762 13246/13952/11761 13256/13962/11771 13255/13961/11770\r\nf 13245/13951/11760 13253/13959/11768 13256/13962/11771 13246/13952/11761\r\nf 13252/13958/11767 13257/13963/11772 13256/13962/11771 13253/13959/11768\r\nf 13098/13804/11613 13258/13964/11773 13259/13965/11774 13099/13805/11614\r\nf 13260/13966/11775 13261/13967/11776 13259/13965/11774 13258/13964/11773\r\nf 13110/13816/11625 13262/13968/11777 13263/13969/11778 13111/13817/11626\r\nf 13264/13970/11779 13265/13971/11780 13263/13969/11778 13262/13968/11777\r\nf 13260/13966/11775 13258/13964/11773 13263/13969/11778 13265/13971/11780\r\nf 13098/13804/11613 13111/13817/11626 13263/13969/11778 13258/13964/11773\r\nf 13238/13944/11753 13241/13947/11756 13266/13972/11781 13267/13973/11782\r\nf 13234/13940/11749 13268/13974/11783 13266/13972/11781 13241/13947/11756\r\nf 13260/13966/11775 13265/13971/11780 13266/13972/11781 13268/13974/11783\r\nf 13264/13970/11779 13267/13973/11782 13266/13972/11781 13265/13971/11780\r\nf 13260/13966/11775 13268/13974/11783 13269/13975/11784 13261/13967/11776\r\nf 13234/13940/11749 13237/13943/11752 13269/13975/11784 13268/13974/11783\r\nf 13110/13816/11625 13151/13857/11666 13270/13976/11785 13262/13968/11777\r\nf 13155/13861/11670 13271/13977/11786 13270/13976/11785 13151/13857/11666\r\nf 13272/13978/11787 13273/13979/11788 13270/13976/11785 13271/13977/11786\r\nf 13264/13970/11779 13262/13968/11777 13270/13976/11785 13273/13979/11788\r\nf 13166/13872/11681 13274/13980/11789 13275/13981/11790 13167/13873/11682\r\nf 13276/13982/11791 13277/13983/11792 13275/13981/11790 13274/13980/11789\r\nf 13272/13978/11787 13271/13977/11786 13275/13981/11790 13277/13983/11792\r\nf 13155/13861/11670 13167/13873/11682 13275/13981/11790 13271/13977/11786\r\nf 13254/13960/11769 13257/13963/11772 13278/13984/11793 13279/13985/11794\r\nf 13252/13958/11767 13280/13986/11795 13278/13984/11793 13257/13963/11772\r\nf 13272/13978/11787 13277/13983/11792 13278/13984/11793 13280/13986/11795\r\nf 13276/13982/11791 13279/13985/11794 13278/13984/11793 13277/13983/11792\r\nf 13238/13944/11753 13267/13973/11782 13281/13987/11796 13250/13956/11765\r\nf 13264/13970/11779 13273/13979/11788 13281/13987/11796 13267/13973/11782\r\nf 13272/13978/11787 13280/13986/11795 13281/13987/11796 13273/13979/11788\r\nf 13252/13958/11767 13250/13956/11765 13281/13987/11796 13280/13986/11795\r\nf 13166/13872/11681 13207/13913/11722 13282/13988/11797 13274/13980/11789\r\nf 13211/13917/11726 13283/13989/11798 13282/13988/11797 13207/13913/11722\r\nf 13284/13990/11799 13285/13991/11800 13282/13988/11797 13283/13989/11798\r\nf 13276/13982/11791 13274/13980/11789 13282/13988/11797 13285/13991/11800\r\nf 13222/13928/11737 13286/13992/11801 13287/13993/11802 13223/13929/11738\r\nf 13288/13994/11803 13289/13995/11804 13287/13993/11802 13286/13992/11801\r\nf 13284/13990/11799 13283/13989/11798 13287/13993/11802 13289/13995/11804\r\nf 13211/13917/11726 13223/13929/11738 13287/13993/11802 13283/13989/11798\r\nf 13290/13996/11805 13291/13997/11806 13292/13998/11807 13293/13999/11808\r\nf 13294/14000/11809 13295/14001/11810 13292/13998/11807 13291/13997/11806\r\nf 13284/13990/11799 13289/13995/11804 13292/13998/11807 13295/14001/11810\r\nf 13288/13994/11803 13293/13999/11808 13292/13998/11807 13289/13995/11804\r\nf 13254/13960/11769 13279/13985/11794 13296/14002/11811 13297/14003/11812\r\nf 13276/13982/11791 13285/13991/11800 13296/14002/11811 13279/13985/11794\r\nf 13284/13990/11799 13295/14001/11810 13296/14002/11811 13285/13991/11800\r\nf 13294/14000/11809 13297/14003/11812 13296/14002/11811 13295/14001/11810\r\nf 13298/14004/11813 13299/14005/11814 13300/14006/11815 13301/14007/11816\r\nf 13062/13768/11577 13065/13771/11580 13300/14006/11815 13299/14005/11814\r\nf 13302/14008/11817 13303/14009/11818 13304/14010/11819 13305/14011/11820\r\nf 13298/14004/11813 13301/14007/11816 13304/14010/11819 13303/14009/11818\r\nf 13306/14012/11821 13307/14013/11822 13308/14014/11823 13309/14015/11824\r\nf 13310/14016/11825 13311/14017/11826 13308/14014/11823 13307/14013/11822\r\nf 13298/14004/11813 13303/14009/11818 13308/14014/11823 13311/14017/11826\r\nf 13302/14008/11817 13309/14015/11824 13308/14014/11823 13303/14009/11818\r\nf 13066/13772/11581 13069/13775/11584 13312/14018/11827 13313/14019/11828\r\nf 13062/13768/11577 13299/14005/11814 13312/14018/11827 13069/13775/11584\r\nf 13298/14004/11813 13311/14017/11826 13312/14018/11827 13299/14005/11814\r\nf 13310/14016/11825 13313/14019/11828 13312/14018/11827 13311/14017/11826\r\nf 13066/13772/11581 13313/14019/11828 13314/14020/11829 13120/13826/11635\r\nf 13310/14016/11825 13315/14021/11830 13314/14020/11829 13313/14019/11828\r\nf 13316/14022/11831 13317/14023/11832 13314/14020/11829 13315/14021/11830\r\nf 13122/13828/11637 13120/13826/11635 13314/14020/11829 13317/14023/11832\r\nf 13306/14012/11821 13318/14024/11833 13319/14025/11834 13307/14013/11822\r\nf 13320/14026/11835 13321/14027/11836 13319/14025/11834 13318/14024/11833\r\nf 13316/14022/11831 13315/14021/11830 13319/14025/11834 13321/14027/11836\r\nf 13310/14016/11825 13307/14013/11822 13319/14025/11834 13315/14021/11830\r\nf 13322/14028/11837 13323/14029/11838 13324/14030/11839 13325/14031/11840\r\nf 13326/14032/11841 13327/14033/11842 13324/14030/11839 13323/14029/11838\r\nf 13316/14022/11831 13321/14027/11836 13324/14030/11839 13327/14033/11842\r\nf 13320/14026/11835 13325/14031/11840 13324/14030/11839 13321/14027/11836\r\nf 13124/13830/11639 13127/13833/11642 13328/14034/11843 13329/14035/11844\r\nf 13122/13828/11637 13317/14023/11832 13328/14034/11843 13127/13833/11642\r\nf 13316/14022/11831 13327/14033/11842 13328/14034/11843 13317/14023/11832\r\nf 13326/14032/11841 13329/14035/11844 13328/14034/11843 13327/14033/11842\r\nf 13124/13830/11639 13329/14035/11844 13330/14036/11845 13176/13882/11691\r\nf 13326/14032/11841 13331/14037/11846 13330/14036/11845 13329/14035/11844\r\nf 13332/14038/11847 13333/14039/11848 13330/14036/11845 13331/14037/11846\r\nf 13178/13884/11693 13176/13882/11691 13330/14036/11845 13333/14039/11848\r\nf 13322/14028/11837 13334/14040/11849 13335/14041/11850 13323/14029/11838\r\nf 13336/14042/11851 13337/14043/11852 13335/14041/11850 13334/14040/11849\r\nf 13332/14038/11847 13331/14037/11846 13335/14041/11850 13337/14043/11852\r\nf 13326/14032/11841 13323/14029/11838 13335/14041/11850 13331/14037/11846\r\nf 13338/14044/11853 13339/14045/11854 13340/14046/11855 13341/14047/11856\r\nf 13342/14048/11857 13343/14049/11858 13340/14046/11855 13339/14045/11854\r\nf 13332/14038/11847 13337/14043/11852 13340/14046/11855 13343/14049/11858\r\nf 13336/14042/11851 13341/14047/11856 13340/14046/11855 13337/14043/11852\r\nf 13180/13886/11695 13183/13889/11698 13344/14050/11859 13345/14051/11860\r\nf 13178/13884/11693 13333/14039/11848 13344/14050/11859 13183/13889/11698\r\nf 13332/14038/11847 13343/14049/11858 13344/14050/11859 13333/14039/11848\r\nf 13342/14048/11857 13345/14051/11860 13344/14050/11859 13343/14049/11858\r\nf 13027/13733/11542 13346/14052/11861 13347/14053/11862 13028/13734/11543\r\nf 13348/14054/11863 13349/14055/11864 13347/14053/11862 13346/14052/11861\r\nf 13350/14056/11865 13351/14057/11866 13347/14053/11862 13349/14055/11864\r\nf 13031/13737/11546 13028/13734/11543 13347/14053/11862 13351/14057/11866\r\nf 13306/14012/11821 13309/14015/11824 13352/14058/11867 13353/14059/11868\r\nf 13302/14008/11817 13354/14060/11869 13352/14058/11867 13309/14015/11824\r\nf 13350/14056/11865 13349/14055/11864 13352/14058/11867 13354/14060/11869\r\nf 13348/14054/11863 13353/14059/11868 13352/14058/11867 13349/14055/11864\r\nf 13350/14056/11865 13354/14060/11869 13355/14061/11870 13356/14062/11871\r\nf 13302/14008/11817 13305/14011/11820 13355/14061/11870 13354/14060/11869\r\nf 13031/13737/11546 13351/14057/11866 13357/14063/11872 13035/13741/11550\r\nf 13350/14056/11865 13356/14062/11871 13357/14063/11872 13351/14057/11866\r\nf 13083/13789/11598 13358/14064/11873 13359/14065/11874 13084/13790/11599\r\nf 13360/14066/11875 13361/14067/11876 13359/14065/11874 13358/14064/11873\r\nf 13362/14068/11877 13363/14069/11878 13359/14065/11874 13361/14067/11876\r\nf 13087/13793/11602 13084/13790/11599 13359/14065/11874 13363/14069/11878\r\nf 13322/14028/11837 13325/14031/11840 13364/14070/11879 13365/14071/11880\r\nf 13320/14026/11835 13366/14072/11881 13364/14070/11879 13325/14031/11840\r\nf 13362/14068/11877 13361/14067/11876 13364/14070/11879 13366/14072/11881\r\nf 13360/14066/11875 13365/14071/11880 13364/14070/11879 13361/14067/11876\r\nf 13306/14012/11821 13353/14059/11868 13367/14073/11882 13318/14024/11833\r\nf 13348/14054/11863 13368/14074/11883 13367/14073/11882 13353/14059/11868\r\nf 13362/14068/11877 13366/14072/11881 13367/14073/11882 13368/14074/11883\r\nf 13320/14026/11835 13318/14024/11833 13367/14073/11882 13366/14072/11881\r\nf 13027/13733/11542 13090/13796/11605 13369/14075/11884 13346/14052/11861\r\nf 13087/13793/11602 13363/14069/11878 13369/14075/11884 13090/13796/11605\r\nf 13362/14068/11877 13368/14074/11883 13369/14075/11884 13363/14069/11878\r\nf 13348/14054/11863 13346/14052/11861 13369/14075/11884 13368/14074/11883\r\nf 13370/14076/11885 13371/14077/11886 13372/14078/11887 13373/14079/11888\r\nf 13374/14080/11889 13375/14081/11890 13372/14078/11887 13371/14077/11886\r\nf 13376/14082/11891 13377/14083/11892 13372/14078/11887 13375/14081/11890\r\nf 13378/14084/11893 13373/14079/11888 13372/14078/11887 13377/14083/11892\r\nf 13083/13789/11598 13086/13792/11601 13379/14085/11894 13380/14086/11895\r\nf 13078/13784/11593 13381/14087/11896 13379/14085/11894 13086/13792/11601\r\nf 13376/14082/11891 13375/14081/11890 13379/14085/11894 13381/14087/11896\r\nf 13374/14080/11889 13380/14086/11895 13379/14085/11894 13375/14081/11890\r\nf 13074/13780/11589 13382/14088/11897 13383/14089/11898 13075/13781/11590\r\nf 13384/14090/11899 13385/14091/11900 13383/14089/11898 13382/14088/11897\r\nf 13376/14082/11891 13381/14087/11896 13383/14089/11898 13385/14091/11900\r\nf 13078/13784/11593 13075/13781/11590 13383/14089/11898 13381/14087/11896\r\nf 13386/14092/11901 13387/14093/11902 13388/14094/11903 13389/14095/11904\r\nf 13378/14084/11893 13377/14083/11892 13388/14094/11903 13387/14093/11902\r\nf 13376/14082/11891 13385/14091/11900 13388/14094/11903 13377/14083/11892\r\nf 13384/14090/11899 13389/14095/11904 13388/14094/11903 13385/14091/11900\r\nf 13386/14092/11901 13389/14095/11904 13390/14096/11905 13391/14097/11906\r\nf 13384/14090/11899 13392/14098/11907 13390/14096/11905 13389/14095/11904\r\nf 13393/14099/11908 13394/14100/11909 13390/14096/11905 13392/14098/11907\r\nf 13395/14101/11910 13391/14097/11906 13390/14096/11905 13394/14100/11909\r\nf 13074/13780/11589 13243/13949/11758 13396/14102/11911 13382/14088/11897\r\nf 13247/13953/11762 13397/14103/11912 13396/14102/11911 13243/13949/11758\r\nf 13393/14099/11908 13392/14098/11907 13396/14102/11911 13397/14103/11912\r\nf 13384/14090/11899 13382/14088/11897 13396/14102/11911 13392/14098/11907\r\nf 13254/13960/11769 13297/14003/11812 13398/14104/11913 13255/13961/11770\r\nf 13294/14000/11809 13399/14105/11914 13398/14104/11913 13297/14003/11812\r\nf 13393/14099/11908 13397/14103/11912 13398/14104/11913 13399/14105/11914\r\nf 13247/13953/11762 13255/13961/11770 13398/14104/11913 13397/14103/11912\r\nf 13290/13996/11805 13400/14106/11915 13401/14107/11916 13291/13997/11806\r\nf 13395/14101/11910 13394/14100/11909 13401/14107/11916 13400/14106/11915\r\nf 13393/14099/11908 13399/14105/11914 13401/14107/11916 13394/14100/11909\r\nf 13294/14000/11809 13291/13997/11806 13401/14107/11916 13399/14105/11914\r\nf 13338/14044/11853 13341/14047/11856 13402/14108/11917 13403/14109/11918\r\nf 13336/14042/11851 13404/14110/11919 13402/14108/11917 13341/14047/11856\r\nf 13405/14111/11920 13406/14112/11921 13402/14108/11917 13404/14110/11919\r\nf 13407/14113/11922 13403/14109/11918 13402/14108/11917 13406/14112/11921\r\nf 13322/14028/11837 13365/14071/11880 13408/14114/11923 13334/14040/11849\r\nf 13360/14066/11875 13409/14115/11924 13408/14114/11923 13365/14071/11880\r\nf 13405/14111/11920 13404/14110/11919 13408/14114/11923 13409/14115/11924\r\nf 13336/14042/11851 13334/14040/11849 13408/14114/11923 13404/14110/11919\r\nf 13083/13789/11598 13380/14086/11895 13410/14116/11925 13358/14064/11873\r\nf 13374/14080/11889 13411/14117/11926 13410/14116/11925 13380/14086/11895\r\nf 13405/14111/11920 13409/14115/11924 13410/14116/11925 13411/14117/11926\r\nf 13360/14066/11875 13358/14064/11873 13410/14116/11925 13409/14115/11924\r\nf 13370/14076/11885 13412/14118/11927 13413/14119/11928 13371/14077/11886\r\nf 13407/14113/11922 13406/14112/11921 13413/14119/11928 13412/14118/11927\r\nf 13405/14111/11920 13411/14117/11926 13413/14119/11928 13406/14112/11921\r\nf 13374/14080/11889 13371/14077/11886 13413/14119/11928 13411/14117/11926\r\nf 13414/14120/11929 13415/14121/11930 13416/14122/11931 13417/14123/11932\r\nf 13418/14124/11933 13419/14125/11934 13416/14122/11931 13415/14121/11930\r\nf 13420/14126/11935 13421/14127/11936 13416/14122/11931 13419/14125/11934\r\nf 13422/14128/11937 13417/14123/11932 13416/14122/11931 13421/14127/11936\r\nf 13420/14126/11935 13419/14125/11934 13423/14129/11938 13424/14130/11939\r\nf 13418/14124/11933 13425/14131/11940 13423/14129/11938 13419/14125/11934\r\nf 13426/14132/11941 13427/14133/11942 13428/14134/11943 13429/14135/11944\r\nf 13420/14126/11935 13424/14130/11939 13428/14134/11943 13427/14133/11942\r\nf 13430/14136/11945 13431/14137/11946 13432/14138/11947 13433/14139/11948\r\nf 13422/14128/11937 13421/14127/11936 13432/14138/11947 13431/14137/11946\r\nf 13420/14126/11935 13427/14133/11942 13432/14138/11947 13421/14127/11936\r\nf 13426/14132/11941 13433/14139/11948 13432/14138/11947 13427/14133/11942\r\nf 13434/14140/11949 13435/14141/11950 13436/14142/11951 13437/14143/11952\r\nf 13438/14144/11953 13439/14145/11954 13436/14142/11951 13435/14141/11950\r\nf 13440/14146/11955 13441/14147/11956 13442/14148/11957 13443/14149/11958\r\nf 13444/14150/11959 13445/14151/11960 13442/14148/11957 13441/14147/11956\r\nf 13438/14144/11953 13435/14141/11950 13442/14148/11957 13445/14151/11960\r\nf 13434/14140/11949 13443/14149/11958 13442/14148/11957 13435/14141/11950\r\nf 13446/14152/11961 13447/14153/11962 13448/14154/11963 13449/14155/11964\r\nf 13450/14156/11965 13451/14157/11966 13448/14154/11963 13447/14153/11962\r\nf 13438/14144/11953 13445/14151/11960 13448/14154/11963 13451/14157/11966\r\nf 13444/14150/11959 13449/14155/11964 13448/14154/11963 13445/14151/11960\r\nf 13438/14144/11953 13451/14157/11966 13452/14158/11967 13439/14145/11954\r\nf 13450/14156/11965 13453/14159/11968 13452/14158/11967 13451/14157/11966\r\nf 13450/14156/11965 13454/14160/11969 13455/14161/11970 13453/14159/11968\r\nf 13456/14162/11971 13457/14163/11972 13455/14161/11970 13454/14160/11969\r\nf 13446/14152/11961 13458/14164/11973 13459/14165/11974 13447/14153/11962\r\nf 13460/14166/11975 13461/14167/11976 13459/14165/11974 13458/14164/11973\r\nf 13456/14162/11971 13454/14160/11969 13459/14165/11974 13461/14167/11976\r\nf 13450/14156/11965 13447/14153/11962 13459/14165/11974 13454/14160/11969\r\nf 13462/14168/11977 13463/14169/11978 13464/14170/11979 13465/14171/11980\r\nf 13466/14172/11981 13467/14173/11982 13464/14170/11979 13463/14169/11978\r\nf 13456/14162/11971 13461/14167/11976 13464/14170/11979 13467/14173/11982\r\nf 13460/14166/11975 13465/14171/11980 13464/14170/11979 13461/14167/11976\r\nf 13456/14162/11971 13467/14173/11982 13468/14174/11983 13457/14163/11972\r\nf 13466/14172/11981 13469/14175/11984 13468/14174/11983 13467/14173/11982\r\nf 13470/14176/11985 13471/14177/11986 13472/14178/11987 13473/14179/11988\r\nf 13474/14180/11989 13475/14181/11990 13472/14178/11987 13471/14177/11986\r\nf 13476/14182/11991 13477/14183/11992 13472/14178/11987 13475/14181/11990\r\nf 13478/14184/11993 13473/14179/11988 13472/14178/11987 13477/14183/11992\r\nf 13414/14120/11929 13417/14123/11932 13479/14185/11994 13480/14186/11995\r\nf 13422/14128/11937 13481/14187/11996 13479/14185/11994 13417/14123/11932\r\nf 13476/14182/11991 13475/14181/11990 13479/14185/11994 13481/14187/11996\r\nf 13474/14180/11989 13480/14186/11995 13479/14185/11994 13475/14181/11990\r\nf 13430/14136/11945 13482/14188/11997 13483/14189/11998 13431/14137/11946\r\nf 13484/14190/11999 13485/14191/12000 13483/14189/11998 13482/14188/11997\r\nf 13476/14182/11991 13481/14187/11996 13483/14189/11998 13485/14191/12000\r\nf 13422/14128/11937 13431/14137/11946 13483/14189/11998 13481/14187/11996\r\nf 13486/14192/12001 13487/14193/12002 13488/14194/12003 13489/14195/12004\r\nf 13478/14184/11993 13477/14183/11992 13488/14194/12003 13487/14193/12002\r\nf 13476/14182/11991 13485/14191/12000 13488/14194/12003 13477/14183/11992\r\nf 13484/14190/11999 13489/14195/12004 13488/14194/12003 13485/14191/12000\r\nf 13490/14196/12005 13491/14197/12006 13492/14198/12007 13493/14199/12008\r\nf 13494/14200/12009 13495/14201/12010 13492/14198/12007 13491/14197/12006\r\nf 13496/14202/12011 13497/14203/12012 13498/14204/12013 13499/14205/12014\r\nf 13500/14206/12015 13501/14207/12016 13498/14204/12013 13497/14203/12012\r\nf 13494/14200/12009 13491/14197/12006 13498/14204/12013 13501/14207/12016\r\nf 13490/14196/12005 13499/14205/12014 13498/14204/12013 13491/14197/12006\r\nf 13502/14208/12017 13503/14209/12018 13504/14210/12019 13505/14211/12020\r\nf 13506/14212/12021 13507/14213/12022 13504/14210/12019 13503/14209/12018\r\nf 13494/14200/12009 13501/14207/12016 13504/14210/12019 13507/14213/12022\r\nf 13500/14206/12015 13505/14211/12020 13504/14210/12019 13501/14207/12016\r\nf 13494/14200/12009 13507/14213/12022 13508/14214/12023 13495/14201/12010\r\nf 13506/14212/12021 13509/14215/12024 13508/14214/12023 13507/14213/12022\r\nf 13446/14152/11961 13510/14216/12025 13511/14217/12026 13458/14164/11973\r\nf 13512/14218/12027 13513/14219/12028 13511/14217/12026 13510/14216/12025\r\nf 13514/14220/12029 13515/14221/12030 13511/14217/12026 13513/14219/12028\r\nf 13460/14166/11975 13458/14164/11973 13511/14217/12026 13515/14221/12030\r\nf 13516/14222/12031 13517/14223/12032 13518/14224/12033 13519/14225/12034\r\nf 13520/14226/12035 13521/14227/12036 13518/14224/12033 13517/14223/12032\r\nf 13514/14220/12029 13513/14219/12028 13518/14224/12033 13521/14227/12036\r\nf 13512/14218/12027 13519/14225/12034 13518/14224/12033 13513/14219/12028\r\nf 13522/14228/12037 13523/14229/12038 13524/14230/12039 13525/14231/12040\r\nf 13526/14232/12041 13527/14233/12042 13524/14230/12039 13523/14229/12038\r\nf 13514/14220/12029 13521/14227/12036 13524/14230/12039 13527/14233/12042\r\nf 13520/14226/12035 13525/14231/12040 13524/14230/12039 13521/14227/12036\r\nf 13462/14168/11977 13465/14171/11980 13528/14234/12043 13529/14235/12044\r\nf 13460/14166/11975 13515/14221/12030 13528/14234/12043 13465/14171/11980\r\nf 13514/14220/12029 13527/14233/12042 13528/14234/12043 13515/14221/12030\r\nf 13526/14232/12041 13529/14235/12044 13528/14234/12043 13527/14233/12042\r\nf 13440/14146/11955 13530/14236/12045 13531/14237/12046 13441/14147/11956\r\nf 13532/14238/12047 13533/14239/12048 13531/14237/12046 13530/14236/12045\r\nf 13534/14240/12049 13535/14241/12050 13531/14237/12046 13533/14239/12048\r\nf 13444/14150/11959 13441/14147/11956 13531/14237/12046 13535/14241/12050\r\nf 13536/14242/12051 13537/14243/12052 13538/14244/12053 13539/14245/12054\r\nf 13540/14246/12055 13541/14247/12056 13538/14244/12053 13537/14243/12052\r\nf 13534/14240/12049 13533/14239/12048 13538/14244/12053 13541/14247/12056\r\nf 13532/14238/12047 13539/14245/12054 13538/14244/12053 13533/14239/12048\r\nf 13516/14222/12031 13519/14225/12034 13542/14248/12057 13543/14249/12058\r\nf 13512/14218/12027 13544/14250/12059 13542/14248/12057 13519/14225/12034\r\nf 13534/14240/12049 13541/14247/12056 13542/14248/12057 13544/14250/12059\r\nf 13540/14246/12055 13543/14249/12058 13542/14248/12057 13541/14247/12056\r\nf 13446/14152/11961 13449/14155/11964 13545/14251/12060 13510/14216/12025\r\nf 13444/14150/11959 13535/14241/12050 13545/14251/12060 13449/14155/11964\r\nf 13534/14240/12049 13544/14250/12059 13545/14251/12060 13535/14241/12050\r\nf 13512/14218/12027 13510/14216/12025 13545/14251/12060 13544/14250/12059\r\nf 13496/14202/12011 13546/14252/12061 13547/14253/12062 13497/14203/12012\r\nf 13548/14254/12063 13549/14255/12064 13547/14253/12062 13546/14252/12061\r\nf 13550/14256/12065 13551/14257/12066 13547/14253/12062 13549/14255/12064\r\nf 13500/14206/12015 13497/14203/12012 13547/14253/12062 13551/14257/12066\r\nf 13552/14258/12067 13553/14259/12068 13554/14260/12069 13555/14261/12070\r\nf 13556/14262/12071 13557/14263/12072 13554/14260/12069 13553/14259/12068\r\nf 13550/14256/12065 13549/14255/12064 13554/14260/12069 13557/14263/12072\r\nf 13548/14254/12063 13555/14261/12070 13554/14260/12069 13549/14255/12064\r\nf 13558/14264/12073 13559/14265/12074 13560/14266/12075 13561/14267/12076\r\nf 13562/14268/12077 13563/14269/12078 13560/14266/12075 13559/14265/12074\r\nf 13550/14256/12065 13557/14263/12072 13560/14266/12075 13563/14269/12078\r\nf 13556/14262/12071 13561/14267/12076 13560/14266/12075 13557/14263/12072\r\nf 13502/14208/12017 13505/14211/12020 13564/14270/12079 13565/14271/12080\r\nf 13500/14206/12015 13551/14257/12066 13564/14270/12079 13505/14211/12020\r\nf 13550/14256/12065 13563/14269/12078 13564/14270/12079 13551/14257/12066\r\nf 13562/14268/12077 13565/14271/12080 13564/14270/12079 13563/14269/12078\r\nf 13516/14222/12031 13566/14272/12081 13567/14273/12082 13517/14223/12032\r\nf 13568/14274/12083 13569/14275/12084 13567/14273/12082 13566/14272/12081\r\nf 13570/14276/12085 13571/14277/12086 13567/14273/12082 13569/14275/12084\r\nf 13520/14226/12035 13517/14223/12032 13567/14273/12082 13571/14277/12086\r\nf 13186/13892/11701 13189/13895/11704 13572/14278/12087 13573/14279/12088\r\nf 13184/13890/11699 13574/14280/12089 13572/14278/12087 13189/13895/11704\r\nf 13570/14276/12085 13569/14275/12084 13572/14278/12087 13574/14280/12089\r\nf 13568/14274/12083 13573/14279/12088 13572/14278/12087 13569/14275/12084\r\nf 13180/13886/11695 13575/14281/12090 13576/14282/12091 13181/13887/11696\r\nf 13577/14283/12092 13578/14284/12093 13576/14282/12091 13575/14281/12090\r\nf 13570/14276/12085 13574/14280/12089 13576/14282/12091 13578/14284/12093\r\nf 13184/13890/11699 13181/13887/11696 13576/14282/12091 13574/14280/12089\r\nf 13522/14228/12037 13525/14231/12040 13579/14285/12094 13580/14286/12095\r\nf 13520/14226/12035 13571/14277/12086 13579/14285/12094 13525/14231/12040\r\nf 13570/14276/12085 13578/14284/12093 13579/14285/12094 13571/14277/12086\r\nf 13577/14283/12092 13580/14286/12095 13579/14285/12094 13578/14284/12093\r\nf 13536/14242/12051 13581/14287/12096 13582/14288/12097 13537/14243/12052\r\nf 13583/14289/12098 13584/14290/12099 13582/14288/12097 13581/14287/12096\r\nf 13585/14291/12100 13586/14292/12101 13582/14288/12097 13584/14290/12099\r\nf 13540/14246/12055 13537/14243/12052 13582/14288/12097 13586/14292/12101\r\nf 13202/13908/11717 13205/13911/11720 13587/14293/12102 13588/14294/12103\r\nf 13200/13906/11715 13589/14295/12104 13587/14293/12102 13205/13911/11720\r\nf 13585/14291/12100 13584/14290/12099 13587/14293/12102 13589/14295/12104\r\nf 13583/14289/12098 13588/14294/12103 13587/14293/12102 13584/14290/12099\r\nf 13186/13892/11701 13573/14279/12088 13590/14296/12105 13198/13904/11713\r\nf 13568/14274/12083 13591/14297/12106 13590/14296/12105 13573/14279/12088\r\nf 13585/14291/12100 13589/14295/12104 13590/14296/12105 13591/14297/12106\r\nf 13200/13906/11715 13198/13904/11713 13590/14296/12105 13589/14295/12104\r\nf 13516/14222/12031 13543/14249/12058 13592/14298/12107 13566/14272/12081\r\nf 13540/14246/12055 13586/14292/12101 13592/14298/12107 13543/14249/12058\r\nf 13585/14291/12100 13591/14297/12106 13592/14298/12107 13586/14292/12101\r\nf 13568/14274/12083 13566/14272/12081 13592/14298/12107 13591/14297/12106\r\nf 13552/14258/12067 13593/14299/12108 13594/14300/12109 13553/14259/12068\r\nf 13595/14301/12110 13596/14302/12111 13594/14300/12109 13593/14299/12108\r\nf 13597/14303/12112 13598/14304/12113 13594/14300/12109 13596/14302/12111\r\nf 13556/14262/12071 13553/14259/12068 13594/14300/12109 13598/14304/12113\r\nf 13222/13928/11737 13225/13931/11740 13599/14305/12114 13600/14306/12115\r\nf 13220/13926/11735 13601/14307/12116 13599/14305/12114 13225/13931/11740\r\nf 13597/14303/12112 13596/14302/12111 13599/14305/12114 13601/14307/12116\r\nf 13595/14301/12110 13600/14306/12115 13599/14305/12114 13596/14302/12111\r\nf 13216/13922/11731 13602/14308/12117 13603/14309/12118 13217/13923/11732\r\nf 13604/14310/12119 13605/14311/12120 13603/14309/12118 13602/14308/12117\r\nf 13597/14303/12112 13601/14307/12116 13603/14309/12118 13605/14311/12120\r\nf 13220/13926/11735 13217/13923/11732 13603/14309/12118 13601/14307/12116\r\nf 13558/14264/12073 13561/14267/12076 13606/14312/12121 13607/14313/12122\r\nf 13556/14262/12071 13598/14304/12113 13606/14312/12121 13561/14267/12076\r\nf 13597/14303/12112 13605/14311/12120 13606/14312/12121 13598/14304/12113\r\nf 13604/14310/12119 13607/14313/12122 13606/14312/12121 13605/14311/12120\r\nf 13414/14120/11929 13608/14314/12123 13609/14315/12124 13415/14121/11930\r\nf 13610/14316/12125 13611/14317/12126 13609/14315/12124 13608/14314/12123\r\nf 13612/14318/12127 13613/14319/12128 13609/14315/12124 13611/14317/12126\r\nf 13418/14124/11933 13415/14121/11930 13609/14315/12124 13613/14319/12128\r\nf 13614/14320/12129 13615/14321/12130 13616/14322/12131 13617/14323/12132\r\nf 13618/14324/12133 13619/14325/12134 13616/14322/12131 13615/14321/12130\r\nf 13612/14318/12127 13611/14317/12126 13616/14322/12131 13619/14325/12134\r\nf 13610/14316/12125 13617/14323/12132 13616/14322/12131 13611/14317/12126\r\nf 13612/14318/12127 13619/14325/12134 13620/14326/12135 13621/14327/12136\r\nf 13618/14324/12133 13622/14328/12137 13620/14326/12135 13619/14325/12134\r\nf 13418/14124/11933 13613/14319/12128 13623/14329/12138 13425/14131/11940\r\nf 13612/14318/12127 13621/14327/12136 13623/14329/12138 13613/14319/12128\r\nf 13470/14176/11985 13624/14330/12139 13625/14331/12140 13471/14177/11986\r\nf 13626/14332/12141 13627/14333/12142 13625/14331/12140 13624/14330/12139\r\nf 13628/14334/12143 13629/14335/12144 13625/14331/12140 13627/14333/12142\r\nf 13474/14180/11989 13471/14177/11986 13625/14331/12140 13629/14335/12144\r\nf 13630/14336/12145 13631/14337/12146 13632/14338/12147 13633/14339/12148\r\nf 13634/14340/12149 13635/14341/12150 13632/14338/12147 13631/14337/12146\r\nf 13628/14334/12143 13627/14333/12142 13632/14338/12147 13635/14341/12150\r\nf 13626/14332/12141 13633/14339/12148 13632/14338/12147 13627/14333/12142\r\nf 13614/14320/12129 13617/14323/12132 13636/14342/12151 13637/14343/12152\r\nf 13610/14316/12125 13638/14344/12153 13636/14342/12151 13617/14323/12132\r\nf 13628/14334/12143 13635/14341/12150 13636/14342/12151 13638/14344/12153\r\nf 13634/14340/12149 13637/14343/12152 13636/14342/12151 13635/14341/12150\r\nf 13414/14120/11929 13480/14186/11995 13639/14345/12154 13608/14314/12123\r\nf 13474/14180/11989 13629/14335/12144 13639/14345/12154 13480/14186/11995\r\nf 13628/14334/12143 13638/14344/12153 13639/14345/12154 13629/14335/12144\r\nf 13610/14316/12125 13608/14314/12123 13639/14345/12154 13638/14344/12153\r\nf 13640/14346/12155 13641/14347/12156 13642/14348/12157 13643/14349/12158\r\nf 13490/14196/12005 13493/14199/12008 13642/14348/12157 13641/14347/12156\r\nf 13618/14324/12133 13644/14350/12159 13645/14351/12160 13622/14328/12137\r\nf 13640/14346/12155 13643/14349/12158 13645/14351/12160 13644/14350/12159\r\nf 13614/14320/12129 13646/14352/12161 13647/14353/12162 13615/14321/12130\r\nf 13648/14354/12163 13649/14355/12164 13647/14353/12162 13646/14352/12161\r\nf 13640/14346/12155 13644/14350/12159 13647/14353/12162 13649/14355/12164\r\nf 13618/14324/12133 13615/14321/12130 13647/14353/12162 13644/14350/12159\r\nf 13496/14202/12011 13499/14205/12014 13650/14356/12165 13651/14357/12166\r\nf 13490/14196/12005 13641/14347/12156 13650/14356/12165 13499/14205/12014\r\nf 13640/14346/12155 13649/14355/12164 13650/14356/12165 13641/14347/12156\r\nf 13648/14354/12163 13651/14357/12166 13650/14356/12165 13649/14355/12164\r\nf 13496/14202/12011 13651/14357/12166 13652/14358/12167 13546/14252/12061\r\nf 13648/14354/12163 13653/14359/12168 13652/14358/12167 13651/14357/12166\r\nf 13654/14360/12169 13655/14361/12170 13652/14358/12167 13653/14359/12168\r\nf 13548/14254/12063 13546/14252/12061 13652/14358/12167 13655/14361/12170\r\nf 13614/14320/12129 13637/14343/12152 13656/14362/12171 13646/14352/12161\r\nf 13634/14340/12149 13657/14363/12172 13656/14362/12171 13637/14343/12152\r\nf 13654/14360/12169 13653/14359/12168 13656/14362/12171 13657/14363/12172\r\nf 13648/14354/12163 13646/14352/12161 13656/14362/12171 13653/14359/12168\r\nf 13630/14336/12145 13658/14364/12173 13659/14365/12174 13631/14337/12146\r\nf 13660/14366/12175 13661/14367/12176 13659/14365/12174 13658/14364/12173\r\nf 13654/14360/12169 13657/14363/12172 13659/14365/12174 13661/14367/12176\r\nf 13634/14340/12149 13631/14337/12146 13659/14365/12174 13657/14363/12172\r\nf 13552/14258/12067 13555/14261/12070 13662/14368/12177 13663/14369/12178\r\nf 13548/14254/12063 13655/14361/12170 13662/14368/12177 13555/14261/12070\r\nf 13654/14360/12169 13661/14367/12176 13662/14368/12177 13655/14361/12170\r\nf 13660/14366/12175 13663/14369/12178 13662/14368/12177 13661/14367/12176\r\nf 13552/14258/12067 13663/14369/12178 13664/14370/12179 13593/14299/12108\r\nf 13660/14366/12175 13665/14371/12180 13664/14370/12179 13663/14369/12178\r\nf 13666/14372/12181 13667/14373/12182 13664/14370/12179 13665/14371/12180\r\nf 13595/14301/12110 13593/14299/12108 13664/14370/12179 13667/14373/12182\r\nf 13630/14336/12145 13668/14374/12183 13669/14375/12184 13658/14364/12173\r\nf 13670/14376/12185 13671/14377/12186 13669/14375/12184 13668/14374/12183\r\nf 13666/14372/12181 13665/14371/12180 13669/14375/12184 13671/14377/12186\r\nf 13660/14366/12175 13658/14364/12173 13669/14375/12184 13665/14371/12180\r\nf 13290/13996/11805 13293/13999/11808 13672/14378/12187 13673/14379/12188\r\nf 13288/13994/11803 13674/14380/12189 13672/14378/12187 13293/13999/11808\r\nf 13666/14372/12181 13671/14377/12186 13672/14378/12187 13674/14380/12189\r\nf 13670/14376/12185 13673/14379/12188 13672/14378/12187 13671/14377/12186\r\nf 13222/13928/11737 13600/14306/12115 13675/14381/12190 13286/13992/11801\r\nf 13595/14301/12110 13667/14373/12182 13675/14381/12190 13600/14306/12115\r\nf 13666/14372/12181 13674/14380/12189 13675/14381/12190 13667/14373/12182\r\nf 13288/13994/11803 13286/13992/11801 13675/14381/12190 13674/14380/12189\r\nf 13466/14172/11981 13676/14382/12191 13677/14383/12192 13469/14175/11984\r\nf 13678/14384/12193 13679/14385/12194 13677/14383/12192 13676/14382/12191\r\nf 13462/14168/11977 13680/14386/12195 13681/14387/12196 13463/14169/11978\r\nf 13682/14388/12197 13683/14389/12198 13681/14387/12196 13680/14386/12195\r\nf 13678/14384/12193 13676/14382/12191 13681/14387/12196 13683/14389/12198\r\nf 13466/14172/11981 13463/14169/11978 13681/14387/12196 13676/14382/12191\r\nf 13684/14390/12199 13685/14391/12200 13686/14392/12201 13687/14393/12202\r\nf 13688/14394/12203 13689/14395/12204 13686/14392/12201 13685/14391/12200\r\nf 13678/14384/12193 13683/14389/12198 13686/14392/12201 13689/14395/12204\r\nf 13682/14388/12197 13687/14393/12202 13686/14392/12201 13683/14389/12198\r\nf 13678/14384/12193 13689/14395/12204 13690/14396/12205 13679/14385/12194\r\nf 13688/14394/12203 13691/14397/12206 13690/14396/12205 13689/14395/12204\r\nf 13462/14168/11977 13529/14235/12044 13692/14398/12207 13680/14386/12195\r\nf 13526/14232/12041 13693/14399/12208 13692/14398/12207 13529/14235/12044\r\nf 13694/14400/12209 13695/14401/12210 13692/14398/12207 13693/14399/12208\r\nf 13682/14388/12197 13680/14386/12195 13692/14398/12207 13695/14401/12210\r\nf 13522/14228/12037 13696/14402/12211 13697/14403/12212 13523/14229/12038\r\nf 13698/14404/12213 13699/14405/12214 13697/14403/12212 13696/14402/12211\r\nf 13694/14400/12209 13693/14399/12208 13697/14403/12212 13699/14405/12214\r\nf 13526/14232/12041 13523/14229/12038 13697/14403/12212 13693/14399/12208\r\nf 13700/14406/12215 13701/14407/12216 13702/14408/12217 13703/14409/12218\r\nf 13704/14410/12219 13705/14411/12220 13702/14408/12217 13701/14407/12216\r\nf 13694/14400/12209 13699/14405/12214 13702/14408/12217 13705/14411/12220\r\nf 13698/14404/12213 13703/14409/12218 13702/14408/12217 13699/14405/12214\r\nf 13684/14390/12199 13687/14393/12202 13706/14412/12221 13707/14413/12222\r\nf 13682/14388/12197 13695/14401/12210 13706/14412/12221 13687/14393/12202\r\nf 13694/14400/12209 13705/14411/12220 13706/14412/12221 13695/14401/12210\r\nf 13704/14410/12219 13707/14413/12222 13706/14412/12221 13705/14411/12220\r\nf 13522/14228/12037 13580/14286/12095 13708/14414/12223 13696/14402/12211\r\nf 13577/14283/12092 13709/14415/12224 13708/14414/12223 13580/14286/12095\r\nf 13710/14416/12225 13711/14417/12226 13708/14414/12223 13709/14415/12224\r\nf 13698/14404/12213 13696/14402/12211 13708/14414/12223 13711/14417/12226\r\nf 13180/13886/11695 13345/14051/11860 13712/14418/12227 13575/14281/12090\r\nf 13342/14048/11857 13713/14419/12228 13712/14418/12227 13345/14051/11860\r\nf 13710/14416/12225 13709/14415/12224 13712/14418/12227 13713/14419/12228\r\nf 13577/14283/12092 13575/14281/12090 13712/14418/12227 13709/14415/12224\r\nf 13338/14044/11853 13714/14420/12229 13715/14421/12230 13339/14045/11854\r\nf 13716/14422/12231 13717/14423/12232 13715/14421/12230 13714/14420/12229\r\nf 13710/14416/12225 13713/14419/12228 13715/14421/12230 13717/14423/12232\r\nf 13342/14048/11857 13339/14045/11854 13715/14421/12230 13713/14419/12228\r\nf 13700/14406/12215 13703/14409/12218 13718/14424/12233 13719/14425/12234\r\nf 13698/14404/12213 13711/14417/12226 13718/14424/12233 13703/14409/12218\r\nf 13710/14416/12225 13717/14423/12232 13718/14424/12233 13711/14417/12226\r\nf 13716/14422/12231 13719/14425/12234 13718/14424/12233 13717/14423/12232\r\nf 13430/14136/11945 13433/14139/11948 13720/14426/12235 13721/14427/12236\r\nf 13426/14132/11941 13722/14428/12237 13720/14426/12235 13433/14139/11948\r\nf 13723/14429/12238 13724/14430/12239 13720/14426/12235 13722/14428/12237\r\nf 13725/14431/12240 13721/14427/12236 13720/14426/12235 13724/14430/12239\r\nf 13723/14429/12238 13722/14428/12237 13726/14432/12241 13727/14433/12242\r\nf 13426/14132/11941 13429/14135/11944 13726/14432/12241 13722/14428/12237\r\nf 13688/14394/12203 13728/14434/12243 13729/14435/12244 13691/14397/12206\r\nf 13723/14429/12238 13727/14433/12242 13729/14435/12244 13728/14434/12243\r\nf 13684/14390/12199 13730/14436/12245 13731/14437/12246 13685/14391/12200\r\nf 13725/14431/12240 13724/14430/12239 13731/14437/12246 13730/14436/12245\r\nf 13723/14429/12238 13728/14434/12243 13731/14437/12246 13724/14430/12239\r\nf 13688/14394/12203 13685/14391/12200 13731/14437/12246 13728/14434/12243\r\nf 13486/14192/12001 13489/14195/12004 13732/14438/12247 13733/14439/12248\r\nf 13484/14190/11999 13734/14440/12249 13732/14438/12247 13489/14195/12004\r\nf 13735/14441/12250 13736/14442/12251 13732/14438/12247 13734/14440/12249\r\nf 13737/14443/12252 13733/14439/12248 13732/14438/12247 13736/14442/12251\r\nf 13430/14136/11945 13721/14427/12236 13738/14444/12253 13482/14188/11997\r\nf 13725/14431/12240 13739/14445/12254 13738/14444/12253 13721/14427/12236\r\nf 13735/14441/12250 13734/14440/12249 13738/14444/12253 13739/14445/12254\r\nf 13484/14190/11999 13482/14188/11997 13738/14444/12253 13734/14440/12249\r\nf 13684/14390/12199 13707/14413/12222 13740/14446/12255 13730/14436/12245\r\nf 13704/14410/12219 13741/14447/12256 13740/14446/12255 13707/14413/12222\r\nf 13735/14441/12250 13739/14445/12254 13740/14446/12255 13741/14447/12256\r\nf 13725/14431/12240 13730/14436/12245 13740/14446/12255 13739/14445/12254\r\nf 13700/14406/12215 13742/14448/12257 13743/14449/12258 13701/14407/12216\r\nf 13737/14443/12252 13736/14442/12251 13743/14449/12258 13742/14448/12257\r\nf 13735/14441/12250 13741/14447/12256 13743/14449/12258 13736/14442/12251\r\nf 13704/14410/12219 13701/14407/12216 13743/14449/12258 13741/14447/12256\r\nf 13370/14076/11885 13373/14079/11888 13744/14450/12259 13745/14451/12260\r\nf 13378/14084/11893 13746/14452/12261 13744/14450/12259 13373/14079/11888\r\nf 13747/14453/12262 13748/14454/12263 13744/14450/12259 13746/14452/12261\r\nf 13749/14455/12264 13745/14451/12260 13744/14450/12259 13748/14454/12263\r\nf 13386/14092/11901 13750/14456/12265 13751/14457/12266 13387/14093/11902\r\nf 13752/14458/12267 13753/14459/12268 13751/14457/12266 13750/14456/12265\r\nf 13747/14453/12262 13746/14452/12261 13751/14457/12266 13753/14459/12268\r\nf 13378/14084/11893 13387/14093/11902 13751/14457/12266 13746/14452/12261\r\nf 13470/14176/11985 13473/14179/11988 13754/14460/12269 13755/14461/12270\r\nf 13478/14184/11993 13756/14462/12271 13754/14460/12269 13473/14179/11988\r\nf 13747/14453/12262 13753/14459/12268 13754/14460/12269 13756/14462/12271\r\nf 13752/14458/12267 13755/14461/12270 13754/14460/12269 13753/14459/12268\r\nf 13486/14192/12001 13757/14463/12272 13758/14464/12273 13487/14193/12002\r\nf 13749/14455/12264 13748/14454/12263 13758/14464/12273 13757/14463/12272\r\nf 13747/14453/12262 13756/14462/12271 13758/14464/12273 13748/14454/12263\r\nf 13478/14184/11993 13487/14193/12002 13758/14464/12273 13756/14462/12271\r\nf 13386/14092/11901 13391/14097/11906 13759/14465/12274 13750/14456/12265\r\nf 13395/14101/11910 13760/14466/12275 13759/14465/12274 13391/14097/11906\r\nf 13761/14467/12276 13762/14468/12277 13759/14465/12274 13760/14466/12275\r\nf 13752/14458/12267 13750/14456/12265 13759/14465/12274 13762/14468/12277\r\nf 13290/13996/11805 13673/14379/12188 13763/14469/12278 13400/14106/11915\r\nf 13670/14376/12185 13764/14470/12279 13763/14469/12278 13673/14379/12188\r\nf 13761/14467/12276 13760/14466/12275 13763/14469/12278 13764/14470/12279\r\nf 13395/14101/11910 13400/14106/11915 13763/14469/12278 13760/14466/12275\r\nf 13630/14336/12145 13633/14339/12148 13765/14471/12280 13668/14374/12183\r\nf 13626/14332/12141 13766/14472/12281 13765/14471/12280 13633/14339/12148\r\nf 13761/14467/12276 13764/14470/12279 13765/14471/12280 13766/14472/12281\r\nf 13670/14376/12185 13668/14374/12183 13765/14471/12280 13764/14470/12279\r\nf 13470/14176/11985 13755/14461/12270 13767/14473/12282 13624/14330/12139\r\nf 13752/14458/12267 13762/14468/12277 13767/14473/12282 13755/14461/12270\r\nf 13761/14467/12276 13766/14472/12281 13767/14473/12282 13762/14468/12277\r\nf 13626/14332/12141 13624/14330/12139 13767/14473/12282 13766/14472/12281\r\nf 13338/14044/11853 13403/14109/11918 13768/14474/12283 13714/14420/12229\r\nf 13407/14113/11922 13769/14475/12284 13768/14474/12283 13403/14109/11918\r\nf 13770/14476/12285 13771/14477/12286 13768/14474/12283 13769/14475/12284\r\nf 13716/14422/12231 13714/14420/12229 13768/14474/12283 13771/14477/12286\r\nf 13370/14076/11885 13745/14451/12260 13772/14478/12287 13412/14118/11927\r\nf 13749/14455/12264 13773/14479/12288 13772/14478/12287 13745/14451/12260\r\nf 13770/14476/12285 13769/14475/12284 13772/14478/12287 13773/14479/12288\r\nf 13407/14113/11922 13412/14118/11927 13772/14478/12287 13769/14475/12284\r\nf 13486/14192/12001 13733/14439/12248 13774/14480/12289 13757/14463/12272\r\nf 13737/14443/12252 13775/14481/12290 13774/14480/12289 13733/14439/12248\r\nf 13770/14476/12285 13773/14479/12288 13774/14480/12289 13775/14481/12290\r\nf 13749/14455/12264 13757/14463/12272 13774/14480/12289 13773/14479/12288\r\nf 13700/14406/12215 13719/14425/12234 13776/14482/12291 13742/14448/12257\r\nf 13716/14422/12231 13771/14477/12286 13776/14482/12291 13719/14425/12234\r\nf 13770/14476/12285 13775/14481/12290 13776/14482/12291 13771/14477/12286\r\nf 13737/14443/12252 13742/14448/12257 13776/14482/12291 13775/14481/12290\r\nv 315.08 65 123.12\r\nv 315.5 60.82 123.54\r\nv 318.71 60.57 120.59\r\nv 318.26 64.76 120.19\r\nv 315.87 56.61 123.87\r\nv 319.1 56.32 120.89\r\nv 306.04 65.74 130.01\r\nv 306.4 61.58 130.5\r\nv 311.06 61.19 127.16\r\nv 310.67 65.36 126.71\r\nv 306.73 57.43 130.86\r\nv 311.41 57.02 127.5\r\nv 307.36 49.2 131.38\r\nv 312.08 48.66 128\r\nv 311.74 52.84 127.77\r\nv 307.04 53.3 131.14\r\nv 316.58 48.13 124.35\r\nv 316.23 52.38 124.12\r\nv 319.47 52.04 121.14\r\nv 319.83 47.74 121.36\r\nv 302.09 97.42 117.47\r\nv 297.84 98.37 119.36\r\nv 298.22 94.8 122.19\r\nv 302.57 93.93 120.07\r\nv 293.42 99.29 120.99\r\nv 293.73 95.65 124.02\r\nv 294.08 91.77 126.69\r\nv 298.66 91.01 124.68\r\nv 303.09 90.22 122.35\r\nv 284.26 100.92 123.49\r\nv 284.45 97.14 126.85\r\nv 289.12 96.44 125.57\r\nv 288.88 100.16 122.36\r\nv 284.67 93.13 129.82\r\nv 289.41 92.49 128.41\r\nv 285.17 84.63 134.6\r\nv 290.04 84.12 132.96\r\nv 289.72 88.37 130.87\r\nv 284.91 88.94 132.41\r\nv 294.86 83.55 130.97\r\nv 294.47 87.72 129.01\r\nv 304.17 82.34 126.01\r\nv 303.63 86.35 124.34\r\nv 299.12 87.04 126.83\r\nv 299.58 82.95 128.66\r\nv 301.37 108.12 104.68\r\nv 297.36 109.38 105.5\r\nv 297.24 107.38 108.98\r\nv 301.26 106.21 107.91\r\nv 293 108.49 109.91\r\nv 293.12 110.55 106.22\r\nv 293.03 105.74 113.89\r\nv 297.31 104.69 112.72\r\nv 301.39 103.6 111.38\r\nv 284.07 110.38 111.36\r\nv 288.59 109.5 110.71\r\nv 288.7 111.61 106.84\r\nv 284.16 112.52 107.36\r\nv 284.06 107.56 115.68\r\nv 288.6 106.71 114.87\r\nv 288.7 103.59 118.78\r\nv 284.13 104.4 119.75\r\nv 293.18 102.67 117.59\r\nv 301.68 100.66 114.57\r\nv 297.52 101.68 116.19\r\nv 301.24 66.13 133\r\nv 301.56 61.96 133.52\r\nv 296.29 66.5 135.65\r\nv 296.58 62.34 136.22\r\nv 296.84 58.24 136.64\r\nv 301.86 57.84 133.91\r\nv 286.13 67.17 139.89\r\nv 286.33 62.99 140.54\r\nv 291.49 62.68 138.57\r\nv 291.24 66.86 137.96\r\nv 286.52 58.92 141.02\r\nv 291.71 58.6 139.02\r\nv 286.87 51.09 141.71\r\nv 292.15 50.7 139.65\r\nv 291.93 54.61 139.36\r\nv 286.69 54.95 141.39\r\nv 297.35 50.24 137.23\r\nv 297.09 54.21 136.96\r\nv 302.14 53.77 134.21\r\nv 302.43 49.73 134.47\r\nv 306.15 96.49 115.32\r\nv 305.62 99.64 112.72\r\nv 309.97 95.63 112.88\r\nv 309.3 98.7 110.6\r\nv 308.75 101.47 108.1\r\nv 305.22 102.49 109.86\r\nv 311.7 98.15 108.87\r\nv 310.94 100.87 106.69\r\nv 312.53 95.11 110.89\r\nv 308.39 103.88 105.37\r\nv 310.31 103.21 104.37\r\nv 310.37 104.63 102.14\r\nv 308.42 105.51 102.82\r\nv 305.01 105.02 106.72\r\nv 305.09 106.82 103.78\r\nv 310.22 69.52 126.1\r\nv 305.63 69.93 129.35\r\nv 314.6 69.15 122.56\r\nv 314.06 73.24 121.83\r\nv 309.72 73.65 125.31\r\nv 305.18 74.11 128.49\r\nv 317.75 68.89 119.66\r\nv 317.17 72.96 118.99\r\nv 312.82 81.2 119.78\r\nv 313.46 77.26 120.92\r\nv 316.52 76.95 118.15\r\nv 315.81 80.85 117.12\r\nv 304.69 78.25 127.39\r\nv 309.18 77.73 124.3\r\nv 308.6 81.75 123.05\r\nv 310.53 23.98 135.12\r\nv 305.29 24.93 138.44\r\nv 305.96 21.9 139.82\r\nv 311.28 20.92 136.43\r\nv 299.84 25.84 141.46\r\nv 300.42 22.83 142.89\r\nv 301.12 19.26 144.62\r\nv 306.75 18.3 141.49\r\nv 312.15 17.3 138.03\r\nv 288.53 27.34 146.46\r\nv 288.94 24.39 147.95\r\nv 294.73 23.67 145.61\r\nv 294.23 26.65 144.14\r\nv 289.43 20.89 149.75\r\nv 295.33 20.13 147.38\r\nv 290.39 13.44 153.77\r\nv 296.5 12.61 151.35\r\nv 295.95 16.34 149.33\r\nv 289.94 17.14 151.72\r\nv 301.85 15.44 146.53\r\nv 302.49 11.69 148.52\r\nv 313.07 13.43 139.81\r\nv 307.57 14.46 143.34\r\nv 308.32 10.69 145.27\r\nv 313.96 9.64 141.64\r\nv 320.2 22.04 127.64\r\nv 321.07 18.95 128.81\r\nv 324.53 18.21 125.56\r\nv 323.61 21.31 124.46\r\nv 322.1 15.29 130.24\r\nv 325.62 14.52 126.91\r\nv 316.31 19.94 132.75\r\nv 315.51 23.01 131.51\r\nv 317.27 16.29 134.28\r\nv 319.26 8.58 137.68\r\nv 318.28 12.4 135.95\r\nv 323.19 11.37 131.82\r\nv 324.26 7.54 133.44\r\nv 326.8 10.59 128.38\r\nv 327.9 6.75 129.95\r\nv 279.61 101.54 124.37\r\nv 279.74 97.72 127.85\r\nv 274.93 102.01 125.01\r\nv 275.01 98.16 128.57\r\nv 275.12 94.06 131.73\r\nv 279.9 93.66 130.93\r\nv 265.52 102.41 125.52\r\nv 265.52 98.53 129.15\r\nv 270.27 98.43 129.01\r\nv 270.23 102.3 125.39\r\nv 265.52 94.4 132.38\r\nv 270.32 94.31 132.22\r\nv 265.52 85.63 137.62\r\nv 270.44 85.56 137.42\r\nv 270.38 89.99 135.03\r\nv 265.52 90.07 135.21\r\nv 275.36 85.36 136.85\r\nv 275.24 89.77 134.49\r\nv 280.08 89.41 133.62\r\nv 280.27 85.04 135.9\r\nv 279.56 113.26 107.77\r\nv 279.48 111.1 111.88\r\nv 274.85 111.63 112.25\r\nv 274.9 113.81 108.07\r\nv 274.84 108.77 116.78\r\nv 279.47 108.25 116.32\r\nv 265.52 112.08 112.56\r\nv 270.19 111.97 112.48\r\nv 270.21 114.15 108.25\r\nv 265.52 114.27 108.31\r\nv 265.52 109.21 117.15\r\nv 270.18 109.09 117.06\r\nv 270.2 105.87 121.39\r\nv 265.52 105.98 121.5\r\nv 274.87 105.55 121.05\r\nv 279.52 105.06 120.51\r\nv 281 67.42 141.43\r\nv 281.15 63.23 142.12\r\nv 275.85 67.61 142.56\r\nv 275.95 63.41 143.28\r\nv 276.05 59.36 143.81\r\nv 281.29 59.17 142.63\r\nv 265.52 67.77 143.5\r\nv 265.52 63.57 144.24\r\nv 270.74 63.53 143.99\r\nv 270.69 67.73 143.26\r\nv 265.52 59.51 144.8\r\nv 270.78 59.47 144.54\r\nv 265.52 51.79 145.66\r\nv 270.88 51.75 145.39\r\nv 270.83 55.55 144.99\r\nv 265.52 55.59 145.25\r\nv 276.23 51.61 144.61\r\nv 276.14 55.43 144.24\r\nv 281.43 55.23 143.03\r\nv 281.56 51.39 143.38\r\nv 282.81 27.86 148.38\r\nv 283.12 24.95 149.88\r\nv 277.05 28.22 149.84\r\nv 277.27 25.35 151.33\r\nv 277.52 21.95 153.13\r\nv 283.49 21.5 151.68\r\nv 265.52 28.5 151.11\r\nv 265.52 25.67 152.58\r\nv 271.4 25.59 152.26\r\nv 271.29 28.43 150.78\r\nv 265.52 22.32 154.37\r\nv 271.52 22.23 154.05\r\nv 265.52 15.13 158.41\r\nv 271.77 15.01 158.09\r\nv 271.65 18.61 156.03\r\nv 265.52 18.72 156.34\r\nv 277.78 18.29 155.12\r\nv 278.01 14.67 157.18\r\nv 283.88 17.8 153.67\r\nv 284.22 14.13 155.72\r\nv 318 33.21 125.21\r\nv 318.23 31.07 125.37\r\nv 321.54 30.38 122.35\r\nv 321.3 32.55 122.2\r\nv 318.42 29.57 125.51\r\nv 321.74 28.86 122.48\r\nv 308.63 34.97 132.31\r\nv 308.84 32.91 132.49\r\nv 313.66 31.99 129.06\r\nv 313.44 34.09 128.89\r\nv 309 31.45 132.66\r\nv 313.84 30.51 129.22\r\nv 309.28 29.55 133.03\r\nv 314.15 28.59 129.55\r\nv 314 29.45 129.37\r\nv 309.14 30.4 132.84\r\nv 318.75 27.64 125.81\r\nv 318.59 28.5 125.65\r\nv 321.9 27.79 122.61\r\nv 322.07 26.93 122.75\r\nv 303.58 35.84 135.44\r\nv 303.77 33.82 135.64\r\nv 298.36 36.66 138.27\r\nv 298.52 34.69 138.49\r\nv 298.65 33.27 138.7\r\nv 303.92 32.39 135.83\r\nv 287.56 38.02 142.96\r\nv 287.66 36.11 143.22\r\nv 293.14 35.46 141.02\r\nv 293 37.4 140.79\r\nv 287.74 34.72 143.46\r\nv 293.24 34.06 141.25\r\nv 287.87 32.83 143.98\r\nv 293.41 32.17 141.73\r\nv 293.32 33.02 141.48\r\nv 287.81 33.69 143.71\r\nv 298.86 31.37 139.14\r\nv 298.75 32.23 138.91\r\nv 304.05 31.34 136.02\r\nv 304.17 30.49 136.24\r\nv 293.88 28.78 143.13\r\nv 288.25 29.44 145.42\r\nv 299.43 27.98 140.47\r\nv 299.15 29.47 139.83\r\nv 293.65 30.26 142.45\r\nv 288.06 30.92 144.73\r\nv 310.02 26.14 134.21\r\nv 304.82 27.09 137.5\r\nv 309.67 27.64 133.63\r\nv 304.51 28.58 136.88\r\nv 304.31 29.64 136.49\r\nv 309.44 28.7 133.26\r\nv 298.98 30.53 139.42\r\nv 287.95 31.98 144.28\r\nv 293.51 31.32 142.02\r\nv 314.94 25.18 130.65\r\nv 319.6 24.23 126.83\r\nv 319.19 25.74 126.32\r\nv 314.57 26.69 130.1\r\nv 322.97 23.51 123.7\r\nv 322.54 25.02 123.22\r\nv 318.93 26.8 126.01\r\nv 322.26 26.09 122.93\r\nv 314.32 27.75 129.76\r\nv 282.08 38.48 144.78\r\nv 282.16 36.59 145.06\r\nv 276.57 38.8 146.16\r\nv 276.63 36.92 146.47\r\nv 276.66 35.55 146.74\r\nv 282.22 35.22 145.32\r\nv 265.52 39.05 147.37\r\nv 265.52 37.17 147.7\r\nv 271.07 37.11 147.37\r\nv 271.05 38.99 147.05\r\nv 265.52 35.8 147.99\r\nv 271.09 35.74 147.66\r\nv 265.52 33.9 148.56\r\nv 271.12 33.84 148.23\r\nv 271.11 34.71 147.94\r\nv 265.52 34.77 148.27\r\nv 276.72 33.66 147.3\r\nv 276.69 34.52 147.02\r\nv 282.26 34.18 145.58\r\nv 282.31 33.32 145.86\r\nv 290.97 71.13 137.13\r\nv 285.91 71.47 139.01\r\nv 295.97 70.75 134.88\r\nv 295.62 75.03 133.87\r\nv 290.68 75.46 136.05\r\nv 285.67 75.84 137.87\r\nv 300.87 70.34 132.28\r\nv 300.47 74.57 131.34\r\nv 300.04 78.78 130.15\r\nv 295.25 79.31 132.58\r\nv 285.42 80.24 136.41\r\nv 290.36 79.8 134.67\r\nv 310.68 92.31 114.95\r\nv 313.37 91.83 112.74\r\nv 311.4 88.77 116.79\r\nv 314.21 88.33 114.4\r\nv 306.73 93.09 117.66\r\nv 307.35 89.46 119.72\r\nv 307.98 85.67 121.52\r\nv 312.12 85.05 118.41\r\nv 315.03 84.66 115.86\r\nv 313.14 37.01 128.69\r\nv 308.35 37.81 132.1\r\nv 317.69 36.2 125.02\r\nv 317.34 39.83 124.81\r\nv 312.8 40.54 128.47\r\nv 308.03 41.27 131.87\r\nv 320.98 35.6 122.02\r\nv 320.61 39.29 121.81\r\nv 316.96 43.88 124.58\r\nv 320.22 43.41 121.59\r\nv 307.7 45.13 131.63\r\nv 312.44 44.5 128.24\r\nv 292.82 40.05 140.53\r\nv 287.42 40.62 142.67\r\nv 298.14 39.37 138.03\r\nv 297.89 42.65 137.78\r\nv 292.61 43.26 140.24\r\nv 287.25 43.77 142.36\r\nv 303.34 38.61 135.22\r\nv 303.05 41.98 134.98\r\nv 302.74 45.74 134.72\r\nv 297.62 46.33 137.51\r\nv 287.06 47.31 142.04\r\nv 292.38 46.86 139.95\r\nv 271.22 30.48 149.73\r\nv 265.52 30.54 150.07\r\nv 276.91 30.28 148.8\r\nv 276.82 31.74 148.09\r\nv 271.17 31.93 149.03\r\nv 265.52 31.99 149.36\r\nv 282.59 29.94 147.34\r\nv 282.45 31.41 146.63\r\nv 282.37 32.47 146.18\r\nv 276.76 32.8 147.63\r\nv 265.52 33.04 148.9\r\nv 271.14 32.98 148.56\r\nv 270.63 72.08 142.28\r\nv 265.52 72.13 142.51\r\nv 275.74 71.95 141.61\r\nv 275.61 76.4 140.36\r\nv 270.57 76.55 141.01\r\nv 265.52 76.6 141.23\r\nv 280.83 71.74 140.51\r\nv 280.65 76.16 139.31\r\nv 280.46 80.61 137.79\r\nv 275.49 80.88 138.79\r\nv 265.52 81.12 139.61\r\nv 270.51 81.06 139.4\r\nv 271.01 41.53 146.67\r\nv 265.52 41.58 146.98\r\nv 276.5 41.35 145.81\r\nv 276.42 44.43 145.42\r\nv 270.97 44.59 146.25\r\nv 265.52 44.65 146.54\r\nv 281.97 41.05 144.45\r\nv 281.85 44.16 144.1\r\nv 281.71 47.66 143.74\r\nv 276.32 47.9 145.01\r\nv 265.52 48.1 146.1\r\nv 270.92 48.05 145.82\r\nv 215.16 56.61 123.87\r\nv 215.53 60.82 123.54\r\nv 212.33 60.57 120.59\r\nv 211.93 56.32 120.89\r\nv 215.95 65 123.12\r\nv 212.77 64.76 120.19\r\nv 214.45 48.13 124.35\r\nv 214.8 52.38 124.12\r\nv 211.57 52.04 121.14\r\nv 211.2 47.74 121.36\r\nv 223.68 49.2 131.38\r\nv 223.99 53.3 131.14\r\nv 219.29 52.84 127.77\r\nv 218.95 48.66 128\r\nv 224.3 57.43 130.86\r\nv 219.62 57.02 127.5\r\nv 224.99 65.74 130.01\r\nv 220.36 65.36 126.71\r\nv 219.97 61.19 127.16\r\nv 224.63 61.58 130.5\r\nv 228.94 97.42 117.47\r\nv 228.46 93.93 120.07\r\nv 232.81 94.8 122.19\r\nv 233.19 98.37 119.36\r\nv 227.94 90.22 122.35\r\nv 232.38 91.01 124.68\r\nv 236.95 91.77 126.69\r\nv 237.31 95.65 124.02\r\nv 237.61 99.29 120.99\r\nv 226.86 82.34 126.01\r\nv 231.45 82.95 128.66\r\nv 231.92 87.04 126.83\r\nv 227.4 86.35 124.34\r\nv 236.17 83.55 130.97\r\nv 236.57 87.72 129.01\r\nv 245.86 84.63 134.6\r\nv 246.12 88.94 132.41\r\nv 241.31 88.37 130.87\r\nv 240.99 84.12 132.96\r\nv 246.36 93.13 129.82\r\nv 241.63 92.49 128.41\r\nv 246.77 100.92 123.49\r\nv 242.15 100.16 122.36\r\nv 241.91 96.44 125.57\r\nv 246.58 97.14 126.85\r\nv 229.77 106.21 107.91\r\nv 233.79 107.38 108.98\r\nv 233.67 109.38 105.5\r\nv 229.66 108.12 104.68\r\nv 229.64 103.6 111.38\r\nv 233.72 104.69 112.72\r\nv 238 105.74 113.89\r\nv 238.03 108.49 109.91\r\nv 237.91 110.55 106.22\r\nv 233.51 101.68 116.19\r\nv 229.35 100.66 114.57\r\nv 237.85 102.67 117.59\r\nv 246.9 104.4 119.75\r\nv 242.33 103.59 118.78\r\nv 246.97 107.56 115.68\r\nv 242.44 106.71 114.87\r\nv 246.87 112.52 107.36\r\nv 242.33 111.61 106.84\r\nv 242.44 109.5 110.71\r\nv 246.97 110.38 111.36\r\nv 229.47 61.96 133.52\r\nv 229.79 66.13 133\r\nv 229.17 57.84 133.91\r\nv 234.19 58.24 136.64\r\nv 234.45 62.34 136.22\r\nv 234.74 66.5 135.65\r\nv 228.6 49.73 134.47\r\nv 228.89 53.77 134.21\r\nv 233.68 50.24 137.23\r\nv 233.94 54.21 136.96\r\nv 244.16 51.09 141.71\r\nv 244.34 54.95 141.39\r\nv 239.1 54.61 139.36\r\nv 238.88 50.7 139.65\r\nv 244.51 58.92 141.02\r\nv 239.32 58.6 139.02\r\nv 244.9 67.17 139.89\r\nv 239.79 66.86 137.96\r\nv 239.54 62.68 138.57\r\nv 244.7 62.99 140.54\r\nv 225.41 99.64 112.72\r\nv 224.88 96.49 115.32\r\nv 225.82 102.49 109.86\r\nv 222.29 101.47 108.1\r\nv 221.73 98.7 110.6\r\nv 221.06 95.63 112.88\r\nv 225.94 106.82 103.78\r\nv 226.02 105.02 106.72\r\nv 222.65 103.88 105.37\r\nv 222.61 105.51 102.82\r\nv 220.73 103.21 104.37\r\nv 220.1 100.87 106.69\r\nv 220.66 104.63 102.14\r\nv 219.34 98.15 108.87\r\nv 218.5 95.11 110.89\r\nv 225.4 69.93 129.35\r\nv 220.81 69.52 126.1\r\nv 225.85 74.11 128.49\r\nv 221.31 73.65 125.31\r\nv 216.97 73.24 121.83\r\nv 216.43 69.15 122.56\r\nv 222.44 81.75 123.05\r\nv 221.85 77.73 124.3\r\nv 226.34 78.25 127.39\r\nv 218.22 81.2 119.78\r\nv 217.57 77.26 120.92\r\nv 214.51 76.95 118.15\r\nv 213.86 72.96 118.99\r\nv 215.23 80.85 117.12\r\nv 213.28 68.89 119.66\r\nv 220.5 23.98 135.12\r\nv 219.76 20.92 136.43\r\nv 225.07 21.9 139.82\r\nv 225.74 24.93 138.44\r\nv 218.88 17.3 138.03\r\nv 224.28 18.3 141.49\r\nv 229.91 19.26 144.62\r\nv 230.61 22.83 142.89\r\nv 231.2 25.84 141.46\r\nv 217.07 9.64 141.64\r\nv 222.71 10.69 145.27\r\nv 223.46 14.46 143.34\r\nv 217.96 13.43 139.81\r\nv 229.18 15.44 146.53\r\nv 228.54 11.69 148.52\r\nv 241.09 17.14 151.72\r\nv 235.08 16.34 149.33\r\nv 234.53 12.61 151.35\r\nv 240.64 13.44 153.77\r\nv 241.6 20.89 149.75\r\nv 235.71 20.13 147.38\r\nv 242.5 27.34 146.46\r\nv 236.8 26.65 144.14\r\nv 236.3 23.67 145.61\r\nv 242.09 24.39 147.95\r\nv 208.94 15.29 130.24\r\nv 209.96 18.95 128.81\r\nv 206.5 18.21 125.56\r\nv 205.41 14.52 126.91\r\nv 210.83 22.04 127.64\r\nv 207.42 21.31 124.46\r\nv 207.84 11.37 131.82\r\nv 204.23 10.59 128.38\r\nv 203.15 6.75 129.94\r\nv 206.78 7.54 133.43\r\nv 212.75 12.4 135.95\r\nv 211.77 8.58 137.68\r\nv 213.76 16.29 134.28\r\nv 215.52 23.01 131.51\r\nv 214.72 19.94 132.75\r\nv 251.29 97.72 127.85\r\nv 251.42 101.54 124.37\r\nv 251.13 93.66 130.93\r\nv 255.91 94.06 131.73\r\nv 256.02 98.16 128.57\r\nv 256.1 102.01 125.01\r\nv 250.76 85.04 135.9\r\nv 250.95 89.41 133.62\r\nv 255.67 85.36 136.85\r\nv 255.8 89.77 134.49\r\nv 260.65 89.99 135.03\r\nv 260.59 85.56 137.42\r\nv 260.71 94.31 132.22\r\nv 260.81 102.3 125.39\r\nv 260.76 98.43 129.01\r\nv 251.55 111.1 111.88\r\nv 251.47 113.26 107.77\r\nv 251.56 108.25 116.32\r\nv 256.19 108.77 116.78\r\nv 256.18 111.63 112.25\r\nv 256.13 113.81 108.07\r\nv 251.52 105.06 120.51\r\nv 256.16 105.55 121.05\r\nv 260.83 105.87 121.39\r\nv 260.85 109.09 117.06\r\nv 260.82 114.15 108.25\r\nv 260.84 111.97 112.48\r\nv 249.88 63.23 142.12\r\nv 250.03 67.42 141.43\r\nv 249.74 59.17 142.63\r\nv 254.99 59.36 143.81\r\nv 255.08 63.41 143.28\r\nv 255.18 67.61 142.56\r\nv 249.47 51.39 143.38\r\nv 249.6 55.23 143.03\r\nv 254.8 51.61 144.61\r\nv 254.89 55.43 144.24\r\nv 260.2 55.55 144.99\r\nv 260.16 51.75 145.39\r\nv 260.25 59.47 144.54\r\nv 260.35 67.73 143.26\r\nv 260.3 63.53 143.99\r\nv 247.91 24.95 149.88\r\nv 248.23 27.86 148.38\r\nv 247.54 21.5 151.68\r\nv 253.52 21.95 153.13\r\nv 253.77 25.35 151.33\r\nv 253.98 28.22 149.84\r\nv 246.81 14.13 155.72\r\nv 247.15 17.8 153.67\r\nv 253.25 18.29 155.12\r\nv 253.02 14.67 157.18\r\nv 259.38 18.61 156.03\r\nv 259.26 15.01 158.09\r\nv 259.51 22.23 154.05\r\nv 259.74 28.43 150.78\r\nv 259.64 25.59 152.26\r\nv 212.61 29.57 125.51\r\nv 212.8 31.07 125.37\r\nv 209.49 30.38 122.35\r\nv 209.3 28.86 122.48\r\nv 213.03 33.21 125.21\r\nv 209.74 32.55 122.2\r\nv 212.28 27.64 125.81\r\nv 212.45 28.5 125.65\r\nv 209.13 27.79 122.61\r\nv 208.96 26.93 122.75\r\nv 221.75 29.55 133.03\r\nv 221.89 30.4 132.84\r\nv 217.04 29.45 129.37\r\nv 216.88 28.59 129.55\r\nv 222.03 31.45 132.66\r\nv 217.19 30.51 129.22\r\nv 222.41 34.97 132.31\r\nv 217.59 34.09 128.89\r\nv 217.37 31.99 129.06\r\nv 222.2 32.91 132.49\r\nv 227.26 33.82 135.64\r\nv 227.45 35.84 135.44\r\nv 227.11 32.39 135.83\r\nv 232.38 33.27 138.7\r\nv 232.51 34.69 138.49\r\nv 232.67 36.66 138.27\r\nv 226.86 30.49 136.24\r\nv 226.99 31.34 136.02\r\nv 232.17 31.37 139.14\r\nv 232.28 32.23 138.91\r\nv 243.16 32.83 143.98\r\nv 243.22 33.69 143.71\r\nv 237.71 33.02 141.48\r\nv 237.62 32.17 141.73\r\nv 243.29 34.72 143.46\r\nv 237.79 34.06 141.25\r\nv 243.47 38.02 142.96\r\nv 238.03 37.4 140.79\r\nv 237.9 35.46 141.02\r\nv 243.37 36.11 143.22\r\nv 242.78 29.44 145.42\r\nv 237.15 28.78 143.13\r\nv 242.97 30.92 144.73\r\nv 237.38 30.26 142.45\r\nv 231.88 29.47 139.83\r\nv 231.61 27.98 140.47\r\nv 237.53 31.32 142.02\r\nv 243.08 31.98 144.28\r\nv 232.05 30.53 139.42\r\nv 221.59 28.7 133.26\r\nv 226.72 29.64 136.49\r\nv 221.36 27.64 133.63\r\nv 226.52 28.58 136.88\r\nv 226.21 27.09 137.5\r\nv 221.02 26.14 134.21\r\nv 216.09 25.18 130.65\r\nv 216.46 26.69 130.1\r\nv 211.84 25.74 126.32\r\nv 211.43 24.23 126.83\r\nv 216.71 27.75 129.76\r\nv 212.1 26.8 126.01\r\nv 208.77 26.09 122.93\r\nv 208.49 25.02 123.22\r\nv 208.06 23.51 123.7\r\nv 248.87 36.59 145.06\r\nv 248.95 38.48 144.78\r\nv 248.82 35.22 145.32\r\nv 254.37 35.55 146.74\r\nv 254.41 36.92 146.47\r\nv 254.46 38.8 146.16\r\nv 248.72 33.32 145.86\r\nv 248.77 34.18 145.58\r\nv 254.31 33.66 147.3\r\nv 254.34 34.52 147.02\r\nv 259.92 34.71 147.94\r\nv 259.91 33.84 148.23\r\nv 259.94 35.74 147.66\r\nv 259.98 38.99 147.05\r\nv 259.96 37.11 147.37\r\nv 245.12 71.47 139.01\r\nv 240.06 71.13 137.13\r\nv 245.36 75.84 137.87\r\nv 240.36 75.46 136.05\r\nv 235.41 75.03 133.87\r\nv 235.06 70.75 134.88\r\nv 240.67 79.8 134.67\r\nv 245.61 80.24 136.41\r\nv 235.78 79.31 132.58\r\nv 231 78.78 130.15\r\nv 230.56 74.57 131.34\r\nv 230.16 70.34 132.28\r\nv 219.63 88.77 116.79\r\nv 220.36 92.31 114.95\r\nv 217.66 91.83 112.74\r\nv 216.82 88.33 114.4\r\nv 218.91 85.05 118.41\r\nv 216 84.66 115.86\r\nv 223.05 85.67 121.52\r\nv 223.68 89.46 119.72\r\nv 224.3 93.09 117.66\r\nv 222.68 37.81 132.1\r\nv 217.89 37.01 128.69\r\nv 223 41.27 131.87\r\nv 218.23 40.54 128.47\r\nv 213.69 39.83 124.81\r\nv 213.34 36.2 125.02\r\nv 218.59 44.5 128.24\r\nv 223.34 45.13 131.63\r\nv 214.07 43.88 124.58\r\nv 210.81 43.41 121.59\r\nv 210.42 39.29 121.81\r\nv 210.05 35.6 122.02\r\nv 243.62 40.62 142.67\r\nv 238.21 40.05 140.53\r\nv 243.79 43.77 142.36\r\nv 238.42 43.26 140.24\r\nv 233.14 42.65 137.78\r\nv 232.89 39.37 138.03\r\nv 238.65 46.86 139.95\r\nv 243.97 47.31 142.04\r\nv 233.41 46.33 137.51\r\nv 228.29 45.74 134.72\r\nv 227.98 41.98 134.98\r\nv 227.69 38.61 135.22\r\nv 259.82 30.48 149.73\r\nv 259.86 31.93 149.03\r\nv 254.21 31.74 148.09\r\nv 254.12 30.28 148.8\r\nv 259.89 32.98 148.56\r\nv 254.27 32.8 147.63\r\nv 248.67 32.47 146.18\r\nv 248.58 31.41 146.63\r\nv 248.44 29.94 147.34\r\nv 260.4 72.08 142.28\r\nv 260.46 76.55 141.01\r\nv 255.42 76.4 140.36\r\nv 255.3 71.95 141.61\r\nv 260.53 81.06 139.4\r\nv 255.54 80.88 138.79\r\nv 250.57 80.61 137.79\r\nv 250.38 76.16 139.31\r\nv 250.2 71.74 140.51\r\nv 260.02 41.53 146.67\r\nv 260.06 44.59 146.25\r\nv 254.61 44.43 145.42\r\nv 254.53 41.35 145.81\r\nv 260.11 48.05 145.82\r\nv 254.71 47.9 145.01\r\nv 249.33 47.66 143.74\r\nv 249.19 44.16 144.1\r\nv 249.06 41.05 144.45\r\nv 301.72 110.34 100.23\r\nv 297.69 111.67 100.81\r\nv 302.28 111.88 96.09\r\nv 298.17 113.29 96.48\r\nv 293.83 114.57 96.85\r\nv 293.41 112.89 101.34\r\nv 303.71 114.14 87.5\r\nv 299.4 115.7 87.61\r\nv 298.76 114.61 92.06\r\nv 302.97 113.13 91.83\r\nv 294.87 117.06 87.74\r\nv 294.33 115.94 92.3\r\nv 285.34 119.22 88.02\r\nv 284.98 118.06 92.72\r\nv 289.72 117.1 92.52\r\nv 290.16 118.24 87.88\r\nv 284.65 116.64 97.46\r\nv 289.3 115.69 97.18\r\nv 288.95 113.98 101.8\r\nv 284.37 114.92 102.19\r\nv 309.58 108.68 95.38\r\nv 308.81 107.46 99.02\r\nv 311.67 105.93 98.53\r\nv 312.66 106.86 95.24\r\nv 311.48 110.48 87.44\r\nv 310.5 109.67 91.5\r\nv 313.76 107.66 91.56\r\nv 314.88 108.34 87.62\r\nv 306.89 111.49 91.61\r\nv 307.74 112.41 87.43\r\nv 306.1 110.35 95.69\r\nv 305.46 108.93 99.61\r\nv 308.56 113.18 83.19\r\nv 304.42 114.98 83.17\r\nv 312.41 111.17 83.3\r\nv 313.28 111.75 79.1\r\nv 309.33 113.82 78.94\r\nv 305.1 115.66 78.86\r\nv 315.94 108.94 83.53\r\nv 316.91 109.46 79.38\r\nv 314.76 112.63 70.76\r\nv 314.07 112.24 74.91\r\nv 317.79 109.9 75.19\r\nv 318.56 110.27 71.04\r\nv 306.29 116.64 70.44\r\nv 305.73 116.21 74.61\r\nv 310.04 114.34 74.72\r\nv 310.67 114.76 70.57\r\nv 310.55 108.52 -5.39\r\nv 311.52 109.58 -1.34\r\nv 314.94 107.6 -1.37\r\nv 313.79 106.73 -5.22\r\nv 312.51 110.44 2.77\r\nv 316.07 108.34 2.63\r\nv 309.25 105.59 -12.8\r\nv 309.73 107.22 -9.25\r\nv 312.75 105.73 -8.71\r\nv 312.16 104.4 -11.77\r\nv 301.86 108.09 -14.75\r\nv 302.25 110.03 -10.56\r\nv 306.2 108.65 -9.89\r\nv 305.77 106.83 -13.8\r\nv 302.87 111.64 -6.19\r\nv 306.89 110.15 -5.74\r\nv 304.4 114.03 2.63\r\nv 308.62 112.33 2.75\r\nv 307.73 111.36 -1.49\r\nv 303.62 112.96 -1.74\r\nv 306.85 116.48 18.31\r\nv 311.31 114.65 18.26\r\nv 310.77 114.26 14.6\r\nv 306.36 116.08 14.64\r\nv 315.47 112.58 18.15\r\nv 314.89 112.2 14.48\r\nv 314.2 111.74 10.68\r\nv 310.14 113.76 10.79\r\nv 305.78 115.56 10.82\r\nv 318.68 109.92 14.26\r\nv 317.94 109.49 10.47\r\nv 319.31 110.28 17.95\r\nv 313.41 111.15 6.78\r\nv 317.07 108.96 6.59\r\nv 305.12 114.89 6.81\r\nv 309.42 113.12 6.84\r\nv 286.72 121.57 18.11\r\nv 291.96 120.63 18.19\r\nv 291.61 120.21 14.49\r\nv 286.43 121.16 14.39\r\nv 297.11 119.47 18.26\r\nv 296.71 119.05 14.57\r\nv 296.24 118.51 10.69\r\nv 291.21 119.67 10.58\r\nv 286.09 120.62 10.47\r\nv 301.64 117.68 14.63\r\nv 302.1 118.09 18.31\r\nv 301.12 117.14 10.78\r\nv 299.89 115.55 2.46\r\nv 300.53 116.45 6.73\r\nv 295.15 116.89 2.26\r\nv 295.72 117.81 6.6\r\nv 285.32 118.98 1.86\r\nv 285.72 119.91 6.3\r\nv 290.76 118.96 6.45\r\nv 290.27 118.04 2.06\r\nv 289.76 116.85 -2.63\r\nv 284.9 117.79 -2.9\r\nv 294.55 115.72 -2.34\r\nv 293.98 114.28 -7.08\r\nv 289.27 115.38 -7.49\r\nv 284.5 116.31 -7.85\r\nv 299.2 114.42 -2.03\r\nv 298.54 113.03 -6.64\r\nv 297.63 109.32 -15.64\r\nv 297.99 111.33 -11.22\r\nv 293.17 110.47 -16.45\r\nv 293.5 112.54 -11.83\r\nv 283.92 112.4 -17.78\r\nv 284.16 114.52 -12.85\r\nv 288.86 113.61 -12.38\r\nv 288.57 111.51 -17.17\r\nv 300.02 116.58 83.2\r\nv 300.61 117.29 78.83\r\nv 295.89 118.7 78.84\r\nv 295.39 117.97 83.25\r\nv 301.64 118.29 70.37\r\nv 301.15 117.85 74.55\r\nv 296.78 119.71 70.34\r\nv 296.36 119.26 74.53\r\nv 286.6 121.87 70.34\r\nv 286.33 121.44 74.57\r\nv 291.4 120.46 74.54\r\nv 291.75 120.9 70.33\r\nv 286.02 120.87 78.93\r\nv 291.01 119.89 78.88\r\nv 290.6 119.16 83.33\r\nv 285.69 120.14 83.42\r\nv 279.29 113.12 -18.26\r\nv 279.48 115.25 -13.23\r\nv 274.69 113.65 -18.61\r\nv 274.82 115.78 -13.51\r\nv 274.99 117.58 -8.36\r\nv 279.74 117.04 -8.14\r\nv 265.52 114.08 -18.9\r\nv 265.52 116.21 -13.74\r\nv 270.16 116.1 -13.68\r\nv 270.1 113.97 -18.83\r\nv 265.52 118.01 -8.54\r\nv 270.25 117.9 -8.5\r\nv 265.52 120.69 1.45\r\nv 270.47 120.58 1.48\r\nv 270.36 119.38 -3.4\r\nv 265.52 119.49 -3.44\r\nv 275.42 120.26 1.56\r\nv 275.2 119.06 -3.29\r\nv 280.05 118.53 -3.12\r\nv 280.37 119.72 1.69\r\nv 280.7 120.91 83.49\r\nv 280.44 119.99 88.13\r\nv 280.95 121.64 78.98\r\nv 275.83 122.2 79.02\r\nv 275.67 121.48 83.55\r\nv 275.5 120.55 88.22\r\nv 281.39 122.64 70.36\r\nv 281.18 122.2 74.6\r\nv 276.13 123.19 70.38\r\nv 275.99 122.76 74.63\r\nv 265.52 123.63 70.4\r\nv 265.52 123.21 74.66\r\nv 270.76 123.09 74.65\r\nv 270.83 123.52 70.39\r\nv 265.52 122.65 79.06\r\nv 270.68 122.54 79.05\r\nv 265.52 121.01 88.29\r\nv 270.51 120.9 88.27\r\nv 270.6 121.82 83.59\r\nv 265.52 121.93 83.61\r\nv 317.12 113.75 43.77\r\nv 317.07 113.75 46.62\r\nv 321.05 111.37 46.71\r\nv 321.1 111.37 43.83\r\nv 316.96 113.71 49.49\r\nv 320.93 111.33 49.61\r\nv 308.25 117.75 43.67\r\nv 308.2 117.76 46.48\r\nv 312.78 115.88 46.55\r\nv 312.84 115.87 43.72\r\nv 308.1 117.74 49.32\r\nv 312.67 115.85 49.4\r\nv 307.75 117.58 55.41\r\nv 312.29 115.69 55.51\r\nv 312.51 115.79 52.35\r\nv 307.95 117.68 52.27\r\nv 316.55 113.54 55.64\r\nv 316.79 113.65 52.47\r\nv 320.75 111.26 52.61\r\nv 320.5 111.15 55.82\r\nv 303.1 119.32 52.2\r\nv 302.92 119.23 55.34\r\nv 303.24 119.38 49.26\r\nv 298.15 120.78 49.21\r\nv 298.03 120.73 52.15\r\nv 297.88 120.64 55.29\r\nv 303.37 119.38 43.62\r\nv 303.32 119.39 46.43\r\nv 298.26 120.78 43.57\r\nv 298.22 120.79 46.38\r\nv 287.58 122.9 43.48\r\nv 287.55 122.92 46.3\r\nv 292.95 121.96 46.34\r\nv 292.98 121.95 43.52\r\nv 287.51 122.91 49.14\r\nv 292.89 121.95 49.17\r\nv 287.33 122.77 55.24\r\nv 292.66 121.81 55.26\r\nv 292.79 121.9 52.12\r\nv 287.43 122.86 52.09\r\nv 282.12 123.64 43.44\r\nv 282.1 123.66 46.27\r\nv 276.61 124.17 43.42\r\nv 276.6 124.19 46.25\r\nv 276.57 124.19 49.11\r\nv 282.06 123.65 49.12\r\nv 265.52 124.6 43.39\r\nv 265.52 124.62 46.23\r\nv 271.06 124.52 46.24\r\nv 271.07 124.49 43.4\r\nv 265.52 124.62 49.09\r\nv 271.05 124.51 49.1\r\nv 265.52 124.49 55.23\r\nv 271.01 124.38 55.23\r\nv 271.03 124.46 52.06\r\nv 265.52 124.57 52.06\r\nv 276.49 124.06 55.23\r\nv 276.54 124.14 52.07\r\nv 282.01 123.61 52.08\r\nv 281.93 123.52 55.23\r\nv 315.84 113.21 62.82\r\nv 315.35 112.95 66.72\r\nv 319.2 110.57 66.98\r\nv 319.73 110.82 63.05\r\nv 316.23 113.4 59.11\r\nv 320.16 111.01 59.31\r\nv 307.49 117.44 58.84\r\nv 312 115.54 58.95\r\nv 307.16 117.25 62.52\r\nv 311.64 115.35 62.64\r\nv 311.2 115.09 66.53\r\nv 306.76 116.98 66.4\r\nv 302.06 118.63 66.32\r\nv 302.41 118.9 62.44\r\nv 297.43 120.31 62.39\r\nv 297.14 120.05 66.28\r\nv 302.69 119.09 58.76\r\nv 297.68 120.5 58.72\r\nv 287.2 122.65 58.68\r\nv 292.5 121.68 58.69\r\nv 287.03 122.46 62.36\r\nv 292.29 121.5 62.37\r\nv 292.04 121.24 66.26\r\nv 286.84 122.21 66.26\r\nv 281.56 122.97 66.27\r\nv 281.71 123.22 62.36\r\nv 276.34 123.76 62.37\r\nv 276.24 123.51 66.28\r\nv 281.83 123.4 58.67\r\nv 276.42 123.94 58.67\r\nv 265.52 124.37 58.67\r\nv 270.98 124.26 58.67\r\nv 265.52 124.2 62.38\r\nv 270.94 124.09 62.37\r\nv 270.89 123.84 66.29\r\nv 265.52 123.95 66.29\r\nv 316.83 113.46 31.65\r\nv 316.99 113.58 34.79\r\nv 320.94 111.23 34.76\r\nv 320.77 111.12 31.58\r\nv 317.09 113.67 37.86\r\nv 321.05 111.31 37.86\r\nv 308.02 117.42 31.69\r\nv 308.15 117.55 34.79\r\nv 312.72 115.69 34.8\r\nv 312.58 115.56 31.68\r\nv 308.23 117.64 37.82\r\nv 312.81 115.78 37.85\r\nv 312.85 115.84 40.82\r\nv 308.26 117.71 40.78\r\nv 317.13 113.72 40.85\r\nv 321.1 111.35 40.88\r\nv 303.17 119.04 31.66\r\nv 303.29 119.17 34.76\r\nv 298.07 120.42 31.61\r\nv 298.18 120.55 34.71\r\nv 298.25 120.66 37.73\r\nv 303.36 119.27 37.78\r\nv 287.43 122.52 31.5\r\nv 287.51 122.66 34.6\r\nv 292.9 121.71 34.65\r\nv 292.81 121.58 31.56\r\nv 287.56 122.77 37.63\r\nv 292.96 121.82 37.68\r\nv 292.99 121.9 40.64\r\nv 287.58 122.85 40.59\r\nv 298.27 120.73 40.69\r\nv 303.38 119.34 40.73\r\nv 281.99 123.26 31.44\r\nv 282.06 123.4 34.55\r\nv 276.52 123.79 31.39\r\nv 276.57 123.92 34.5\r\nv 276.6 124.03 37.55\r\nv 282.1 123.51 37.58\r\nv 265.52 124.21 31.35\r\nv 265.52 124.35 34.47\r\nv 271.05 124.24 34.48\r\nv 271.03 124.1 31.36\r\nv 265.52 124.46 37.51\r\nv 271.06 124.35 37.52\r\nv 271.07 124.44 40.5\r\nv 265.52 124.54 40.49\r\nv 276.61 124.12 40.52\r\nv 282.12 123.59 40.55\r\nv 316.32 113.13 25.11\r\nv 316.61 113.32 28.42\r\nv 320.53 110.98 28.32\r\nv 320.22 110.8 24.97\r\nv 315.94 112.88 21.69\r\nv 319.82 110.56 21.52\r\nv 307.26 116.81 21.84\r\nv 311.75 114.97 21.79\r\nv 307.58 117.06 25.23\r\nv 312.1 115.22 25.19\r\nv 312.38 115.41 28.48\r\nv 307.83 117.26 28.5\r\nv 302.47 118.42 21.83\r\nv 297.44 119.8 21.79\r\nv 297.71 120.06 25.18\r\nv 302.76 118.68 25.22\r\nv 286.96 121.9 21.65\r\nv 292.25 120.96 21.73\r\nv 287.16 122.16 25.05\r\nv 292.49 121.22 25.12\r\nv 292.67 121.42 28.38\r\nv 287.31 122.36 28.32\r\nv 297.92 120.26 28.44\r\nv 302.99 118.88 28.49\r\nv 281.45 122.31 18.03\r\nv 281.63 122.63 21.58\r\nv 276.15 122.83 17.97\r\nv 276.28 123.16 21.52\r\nv 276.38 123.42 24.93\r\nv 281.79 122.89 24.98\r\nv 265.52 123.25 17.91\r\nv 265.52 123.58 21.47\r\nv 270.9 123.47 21.48\r\nv 270.84 123.15 17.92\r\nv 265.52 123.84 24.88\r\nv 270.95 123.73 24.89\r\nv 270.99 123.94 28.18\r\nv 265.52 124.04 28.16\r\nv 276.46 123.62 28.21\r\nv 281.9 123.1 28.26\r\nv 270.27 116.57 102.87\r\nv 265.52 116.69 102.92\r\nv 275.01 116.22 102.73\r\nv 275.15 117.97 97.86\r\nv 270.34 118.31 97.97\r\nv 265.52 118.43 98.01\r\nv 279.72 115.67 102.5\r\nv 279.93 117.41 97.69\r\nv 280.18 118.83 92.88\r\nv 275.32 119.4 93.01\r\nv 265.52 119.86 93.11\r\nv 270.43 119.74 93.08\r\nv 270.76 122.74 14.19\r\nv 265.52 122.85 14.17\r\nv 275.99 122.43 14.23\r\nv 275.82 121.89 10.28\r\nv 270.67 122.21 10.23\r\nv 265.52 122.32 10.21\r\nv 281.22 121.9 14.31\r\nv 280.96 121.36 10.37\r\nv 280.67 120.65 6.17\r\nv 275.62 121.19 6.07\r\nv 265.52 121.61 5.98\r\nv 270.57 121.51 6\r\nv 233.35 111.67 100.81\r\nv 229.31 110.34 100.23\r\nv 237.62 112.89 101.34\r\nv 237.2 114.57 96.85\r\nv 232.86 113.29 96.48\r\nv 228.75 111.88 96.09\r\nv 246.66 114.92 102.19\r\nv 242.08 113.98 101.8\r\nv 246.38 116.64 97.46\r\nv 241.73 115.69 97.18\r\nv 245.69 119.22 88.02\r\nv 240.87 118.24 87.88\r\nv 241.31 117.1 92.52\r\nv 246.05 118.06 92.72\r\nv 236.17 117.06 87.74\r\nv 236.7 115.94 92.3\r\nv 227.32 114.14 87.5\r\nv 228.06 113.13 91.83\r\nv 232.27 114.61 92.06\r\nv 231.63 115.7 87.61\r\nv 222.22 107.46 99.02\r\nv 219.36 105.93 98.53\r\nv 221.45 108.68 95.38\r\nv 218.37 106.86 95.24\r\nv 225.57 108.93 99.61\r\nv 224.93 110.35 95.69\r\nv 223.29 112.41 87.43\r\nv 224.14 111.49 91.61\r\nv 219.55 110.48 87.44\r\nv 220.53 109.67 91.5\r\nv 217.27 107.66 91.56\r\nv 216.15 108.34 87.62\r\nv 226.61 114.98 83.17\r\nv 222.47 113.18 83.19\r\nv 225.93 115.66 78.86\r\nv 221.7 113.82 78.94\r\nv 217.75 111.75 79.1\r\nv 218.62 111.17 83.3\r\nv 224.74 116.64 70.44\r\nv 220.36 114.76 70.57\r\nv 220.99 114.34 74.72\r\nv 225.3 116.21 74.61\r\nv 216.27 112.63 70.76\r\nv 216.96 112.24 74.91\r\nv 213.24 109.9 75.19\r\nv 214.12 109.46 79.38\r\nv 212.48 110.27 71.04\r\nv 215.09 108.94 83.53\r\nv 218.52 110.44 2.77\r\nv 219.52 109.58 -1.34\r\nv 216.1 107.6 -1.37\r\nv 214.97 108.34 2.63\r\nv 220.49 108.52 -5.39\r\nv 217.24 106.73 -5.22\r\nv 226.63 114.03 2.63\r\nv 227.41 112.96 -1.74\r\nv 223.3 111.36 -1.49\r\nv 222.41 112.33 2.75\r\nv 228.16 111.64 -6.19\r\nv 224.14 110.15 -5.74\r\nv 229.18 108.09 -14.75\r\nv 225.26 106.83 -13.8\r\nv 224.84 108.65 -9.89\r\nv 228.78 110.03 -10.56\r\nv 221.78 105.59 -12.8\r\nv 221.3 107.22 -9.25\r\nv 218.28 105.73 -8.71\r\nv 218.87 104.4 -11.77\r\nv 224.18 116.48 18.31\r\nv 224.68 116.08 14.64\r\nv 220.26 114.26 14.6\r\nv 219.72 114.65 18.26\r\nv 225.26 115.56 10.82\r\nv 220.89 113.76 10.79\r\nv 216.83 111.74 10.68\r\nv 216.14 112.2 14.48\r\nv 215.56 112.58 18.15\r\nv 221.61 113.12 6.84\r\nv 225.91 114.89 6.81\r\nv 217.62 111.15 6.78\r\nv 213.97 108.96 6.59\r\nv 213.1 109.49 10.47\r\nv 212.35 109.92 14.26\r\nv 211.72 110.28 17.95\r\nv 244.31 121.57 18.11\r\nv 244.6 121.16 14.39\r\nv 239.42 120.22 14.49\r\nv 239.07 120.63 18.19\r\nv 244.94 120.62 10.47\r\nv 239.82 119.67 10.58\r\nv 234.79 118.51 10.69\r\nv 234.33 119.05 14.57\r\nv 233.92 119.47 18.26\r\nv 245.71 118.98 1.86\r\nv 240.76 118.04 2.06\r\nv 240.27 118.96 6.45\r\nv 245.31 119.91 6.3\r\nv 235.88 116.89 2.26\r\nv 235.31 117.81 6.6\r\nv 230.5 116.45 6.73\r\nv 231.14 115.55 2.46\r\nv 229.91 117.14 10.78\r\nv 228.94 118.09 18.31\r\nv 229.39 117.68 14.63\r\nv 246.13 117.79 -2.9\r\nv 241.27 116.85 -2.63\r\nv 246.53 116.31 -7.85\r\nv 241.76 115.38 -7.49\r\nv 237.05 114.28 -7.08\r\nv 236.48 115.72 -2.34\r\nv 247.11 112.4 -17.78\r\nv 242.46 111.51 -17.17\r\nv 242.17 113.61 -12.38\r\nv 246.87 114.52 -12.85\r\nv 237.86 110.47 -16.45\r\nv 237.53 112.54 -11.83\r\nv 233.04 111.33 -11.22\r\nv 233.4 109.32 -15.64\r\nv 232.49 113.03 -6.64\r\nv 231.83 114.42 -2.03\r\nv 231.01 116.58 83.2\r\nv 235.64 117.97 83.25\r\nv 235.14 118.7 78.84\r\nv 230.42 117.29 78.83\r\nv 245.35 120.14 83.42\r\nv 240.43 119.16 83.33\r\nv 245.01 120.87 78.93\r\nv 240.02 119.89 78.88\r\nv 244.43 121.87 70.34\r\nv 239.28 120.9 70.33\r\nv 239.63 120.46 74.54\r\nv 244.71 121.44 74.57\r\nv 234.25 119.71 70.34\r\nv 234.67 119.26 74.53\r\nv 229.88 117.85 74.55\r\nv 229.39 118.29 70.37\r\nv 251.55 115.25 -13.23\r\nv 251.74 113.12 -18.26\r\nv 251.29 117.04 -8.14\r\nv 256.04 117.58 -8.36\r\nv 256.22 115.78 -13.51\r\nv 256.34 113.65 -18.61\r\nv 250.66 119.72 1.69\r\nv 250.99 118.53 -3.12\r\nv 255.62 120.26 1.56\r\nv 255.83 119.06 -3.29\r\nv 260.68 119.38 -3.4\r\nv 260.57 120.58 1.48\r\nv 260.78 117.9 -8.5\r\nv 260.93 113.97 -18.83\r\nv 260.87 116.1 -13.68\r\nv 250.59 119.99 88.13\r\nv 250.33 120.91 83.49\r\nv 255.53 120.55 88.22\r\nv 255.36 121.48 83.55\r\nv 255.2 122.2 79.02\r\nv 250.08 121.64 78.98\r\nv 260.43 121.82 83.59\r\nv 260.52 120.9 88.27\r\nv 260.35 122.54 79.05\r\nv 260.2 123.52 70.39\r\nv 260.27 123.09 74.65\r\nv 254.91 123.19 70.38\r\nv 255.04 122.76 74.63\r\nv 249.85 122.2 74.6\r\nv 249.64 122.64 70.36\r\nv 214.07 113.71 49.49\r\nv 213.96 113.75 46.62\r\nv 209.98 111.37 46.71\r\nv 210.1 111.33 49.61\r\nv 213.91 113.75 43.77\r\nv 209.93 111.37 43.83\r\nv 214.48 113.54 55.64\r\nv 214.25 113.65 52.47\r\nv 210.28 111.26 52.61\r\nv 210.53 111.15 55.82\r\nv 223.28 117.58 55.41\r\nv 223.08 117.68 52.27\r\nv 218.52 115.79 52.35\r\nv 218.74 115.69 55.51\r\nv 222.93 117.74 49.32\r\nv 218.36 115.85 49.4\r\nv 222.78 117.75 43.67\r\nv 218.2 115.87 43.72\r\nv 218.25 115.88 46.55\r\nv 222.84 117.76 46.48\r\nv 228.11 119.23 55.34\r\nv 227.93 119.32 52.2\r\nv 233.15 120.64 55.29\r\nv 233 120.73 52.15\r\nv 232.88 120.78 49.21\r\nv 227.8 119.38 49.26\r\nv 243.7 122.77 55.24\r\nv 243.6 122.86 52.09\r\nv 238.24 121.9 52.12\r\nv 238.37 121.81 55.26\r\nv 243.53 122.91 49.14\r\nv 238.15 121.95 49.17\r\nv 243.45 122.9 43.48\r\nv 238.05 121.95 43.52\r\nv 238.08 121.96 46.34\r\nv 243.48 122.92 46.3\r\nv 232.77 120.78 43.57\r\nv 232.81 120.79 46.38\r\nv 227.71 119.39 46.43\r\nv 227.66 119.38 43.62\r\nv 248.93 123.66 46.27\r\nv 248.91 123.64 43.44\r\nv 248.97 123.65 49.12\r\nv 254.46 124.19 49.11\r\nv 254.43 124.19 46.25\r\nv 254.42 124.17 43.42\r\nv 249.1 123.52 55.23\r\nv 249.02 123.61 52.08\r\nv 254.55 124.06 55.23\r\nv 254.5 124.14 52.07\r\nv 260 124.46 52.06\r\nv 260.02 124.38 55.23\r\nv 259.98 124.51 49.1\r\nv 259.96 124.49 43.4\r\nv 259.97 124.52 46.24\r\nv 215.68 112.95 66.72\r\nv 211.83 110.57 66.98\r\nv 215.19 113.21 62.82\r\nv 211.3 110.82 63.05\r\nv 224.27 116.98 66.4\r\nv 219.83 115.09 66.53\r\nv 223.87 117.25 62.52\r\nv 219.39 115.35 62.64\r\nv 219.03 115.54 58.95\r\nv 223.55 117.44 58.84\r\nv 214.8 113.4 59.11\r\nv 210.87 111.01 59.31\r\nv 228.97 118.63 66.32\r\nv 233.9 120.05 66.28\r\nv 233.6 120.31 62.39\r\nv 228.62 118.9 62.44\r\nv 244.19 122.21 66.26\r\nv 238.99 121.24 66.26\r\nv 244 122.46 62.36\r\nv 238.74 121.5 62.37\r\nv 238.53 121.68 58.69\r\nv 243.83 122.65 58.68\r\nv 233.35 120.5 58.72\r\nv 228.34 119.09 58.76\r\nv 249.47 122.97 66.27\r\nv 254.79 123.51 66.28\r\nv 254.69 123.76 62.37\r\nv 249.32 123.22 62.36\r\nv 260.14 123.84 66.29\r\nv 260.1 124.09 62.37\r\nv 260.06 124.26 58.67\r\nv 254.61 123.94 58.67\r\nv 249.2 123.4 58.67\r\nv 213.94 113.67 37.86\r\nv 214.04 113.58 34.79\r\nv 210.09 111.23 34.76\r\nv 209.98 111.31 37.86\r\nv 214.2 113.46 31.65\r\nv 210.26 111.12 31.58\r\nv 213.9 113.72 40.85\r\nv 209.93 111.35 40.88\r\nv 222.77 117.71 40.78\r\nv 218.18 115.84 40.82\r\nv 222.8 117.64 37.82\r\nv 218.22 115.78 37.85\r\nv 223.01 117.42 31.69\r\nv 218.45 115.56 31.68\r\nv 218.31 115.69 34.8\r\nv 222.88 117.55 34.79\r\nv 227.75 119.17 34.76\r\nv 227.87 119.04 31.66\r\nv 227.67 119.27 37.78\r\nv 232.78 120.66 37.73\r\nv 232.85 120.55 34.71\r\nv 232.96 120.42 31.61\r\nv 227.65 119.34 40.73\r\nv 232.76 120.73 40.69\r\nv 243.45 122.85 40.59\r\nv 238.04 121.9 40.64\r\nv 243.47 122.77 37.63\r\nv 238.07 121.82 37.68\r\nv 243.6 122.52 31.5\r\nv 238.22 121.58 31.56\r\nv 238.13 121.71 34.65\r\nv 243.52 122.66 34.6\r\nv 248.97 123.4 34.55\r\nv 249.04 123.26 31.44\r\nv 248.93 123.51 37.58\r\nv 254.44 124.03 37.55\r\nv 254.46 123.92 34.5\r\nv 254.51 123.79 31.39\r\nv 248.91 123.59 40.55\r\nv 254.42 124.12 40.52\r\nv 259.96 124.44 40.5\r\nv 259.97 124.35 37.52\r\nv 260.01 124.1 31.36\r\nv 259.98 124.24 34.48\r\nv 214.42 113.32 28.42\r\nv 210.5 110.98 28.32\r\nv 214.71 113.13 25.11\r\nv 210.81 110.8 24.97\r\nv 223.2 117.26 28.5\r\nv 218.66 115.41 28.48\r\nv 223.45 117.06 25.23\r\nv 218.93 115.22 25.19\r\nv 219.28 114.97 21.79\r\nv 223.77 116.81 21.84\r\nv 215.09 112.88 21.69\r\nv 211.22 110.56 21.52\r\nv 228.57 118.42 21.83\r\nv 228.27 118.68 25.22\r\nv 233.32 120.06 25.18\r\nv 233.59 119.8 21.79\r\nv 228.04 118.88 28.49\r\nv 233.11 120.26 28.44\r\nv 243.72 122.36 28.32\r\nv 238.36 121.42 28.38\r\nv 243.87 122.16 25.05\r\nv 238.54 121.22 25.12\r\nv 238.78 120.96 21.73\r\nv 244.07 121.9 21.65\r\nv 249.4 122.63 21.58\r\nv 249.59 122.31 18.03\r\nv 249.25 122.89 24.98\r\nv 254.65 123.42 24.93\r\nv 254.76 123.16 21.52\r\nv 254.88 122.83 17.97\r\nv 249.13 123.1 28.26\r\nv 254.57 123.62 28.21\r\nv 260.04 123.94 28.18\r\nv 260.08 123.73 24.89\r\nv 260.2 123.15 17.92\r\nv 260.13 123.47 21.48\r\nv 260.76 116.57 102.87\r\nv 260.69 118.31 97.97\r\nv 255.88 117.97 97.86\r\nv 256.02 116.22 102.73\r\nv 260.6 119.74 93.08\r\nv 255.71 119.4 93.01\r\nv 250.85 118.83 92.88\r\nv 251.1 117.41 97.69\r\nv 251.31 115.67 102.5\r\nv 260.27 122.74 14.19\r\nv 260.36 122.21 10.23\r\nv 255.21 121.89 10.28\r\nv 255.04 122.43 14.23\r\nv 260.46 121.51 6\r\nv 255.41 121.19 6.07\r\nv 250.36 120.65 6.17\r\nv 250.07 121.36 10.37\r\nv 249.81 121.9 14.31\r\nv 290.42 12.56 154.36\r\nv 296.54 11.74 151.95\r\nv 302.54 10.73 149.17\r\nv 308.36 9.73 145.93\r\nv 314 8.62 142.33\r\nv 319.25 7.36 138.57\r\nv 324.35 6.28 134.43\r\nv 328.05 5.47 130.88\r\nv 265.52 14.26 159.01\r\nv 271.78 14.14 158.7\r\nv 278.02 13.79 157.78\r\nv 284.24 13.26 156.32\r\nv 217.08 8.35 142.57\r\nv 222.6 9.53 146.02\r\nv 228.5 10.64 149.24\r\nv 234.5 11.56 152.08\r\nv 240.61 12.56 154.36\r\nv 203.08 5.32 130.9\r\nv 206.81 6.11 134.5\r\nv 211.78 7.29 138.62\r\nv 246.79 13.26 156.32\r\nv 253.01 13.79 157.78\r\nv 259.25 14.14 158.7\r\nvn 0.58 0.12 0.81\r\nvn 0.58 0.1 0.81\r\nvn 0.6 0.1 0.79\r\nvn 0.6 0.12 0.79\r\nvn 0.58 0.08 0.81\r\nvn 0.6 0.08 0.79\r\nvn 0.48 0.12 0.87\r\nvn 0.48 0.1 0.87\r\nvn 0.53 0.1 0.84\r\nvn 0.53 0.12 0.84\r\nvn 0.48 0.08 0.87\r\nvn 0.53 0.08 0.84\r\nvn 0.48 0.07 0.87\r\nvn 0.53 0.07 0.84\r\nvn 0.53 0.07 0.84\r\nvn 0.48 0.07 0.87\r\nvn 0.58 0.08 0.81\r\nvn 0.58 0.08 0.81\r\nvn 0.6 0.08 0.8\r\nvn 0.6 0.08 0.8\r\nvn 0.42 0.49 0.76\r\nvn 0.38 0.51 0.77\r\nvn 0.39 0.45 0.8\r\nvn 0.44 0.44 0.79\r\nvn 0.33 0.53 0.78\r\nvn 0.34 0.47 0.81\r\nvn 0.35 0.41 0.84\r\nvn 0.4 0.4 0.83\r\nvn 0.45 0.38 0.81\r\nvn 0.23 0.56 0.79\r\nvn 0.24 0.5 0.83\r\nvn 0.29 0.48 0.82\r\nvn 0.28 0.55 0.79\r\nvn 0.25 0.43 0.87\r\nvn 0.3 0.42 0.86\r\nvn 0.26 0.31 0.91\r\nvn 0.31 0.31 0.9\r\nvn 0.31 0.36 0.88\r\nvn 0.25 0.37 0.89\r\nvn 0.36 0.3 0.88\r\nvn 0.36 0.35 0.86\r\nvn 0.47 0.28 0.84\r\nvn 0.46 0.33 0.83\r\nvn 0.41 0.34 0.85\r\nvn 0.41 0.29 0.86\r\nvn 0.38 0.73 0.58\r\nvn 0.34 0.75 0.57\r\nvn 0.34 0.7 0.63\r\nvn 0.38 0.68 0.63\r\nvn 0.3 0.72 0.63\r\nvn 0.3 0.77 0.57\r\nvn 0.31 0.66 0.69\r\nvn 0.35 0.64 0.69\r\nvn 0.39 0.61 0.68\r\nvn 0.21 0.75 0.62\r\nvn 0.26 0.74 0.63\r\nvn 0.25 0.78 0.57\r\nvn 0.21 0.8 0.56\r\nvn 0.22 0.69 0.69\r\nvn 0.27 0.68 0.69\r\nvn 0.28 0.62 0.74\r\nvn 0.23 0.63 0.74\r\nvn 0.32 0.6 0.74\r\nvn 0.41 0.55 0.72\r\nvn 0.36 0.58 0.73\r\nvn 0.43 0.13 0.89\r\nvn 0.43 0.1 0.9\r\nvn 0.38 0.13 0.92\r\nvn 0.38 0.1 0.92\r\nvn 0.38 0.08 0.92\r\nvn 0.43 0.08 0.9\r\nvn 0.27 0.13 0.95\r\nvn 0.27 0.1 0.96\r\nvn 0.33 0.1 0.94\r\nvn 0.33 0.13 0.94\r\nvn 0.27 0.08 0.96\r\nvn 0.33 0.08 0.94\r\nvn 0.28 0.07 0.96\r\nvn 0.33 0.07 0.94\r\nvn 0.33 0.07 0.94\r\nvn 0.28 0.07 0.96\r\nvn 0.38 0.07 0.92\r\nvn 0.38 0.07 0.92\r\nvn 0.43 0.07 0.9\r\nvn 0.43 0.07 0.9\r\nvn 0.47 0.47 0.74\r\nvn 0.46 0.53 0.72\r\nvn 0.52 0.45 0.72\r\nvn 0.51 0.5 0.7\r\nvn 0.49 0.56 0.67\r\nvn 0.44 0.59 0.68\r\nvn 0.53 0.49 0.69\r\nvn 0.51 0.54 0.66\r\nvn 0.55 0.44 0.71\r\nvn 0.47 0.62 0.62\r\nvn 0.5 0.61 0.62\r\nvn 0.49 0.66 0.57\r\nvn 0.47 0.67 0.57\r\nvn 0.42 0.65 0.63\r\nvn 0.42 0.7 0.58\r\nvn 0.53 0.15 0.84\r\nvn 0.48 0.16 0.86\r\nvn 0.58 0.15 0.8\r\nvn 0.57 0.19 0.8\r\nvn 0.52 0.19 0.83\r\nvn 0.48 0.19 0.86\r\nvn 0.6 0.15 0.79\r\nvn 0.6 0.18 0.78\r\nvn 0.56 0.27 0.78\r\nvn 0.57 0.22 0.79\r\nvn 0.59 0.22 0.77\r\nvn 0.59 0.26 0.77\r\nvn 0.47 0.23 0.85\r\nvn 0.52 0.23 0.82\r\nvn 0.52 0.27 0.81\r\nvn 0.51 0.35 0.79\r\nvn 0.46 0.35 0.82\r\nvn 0.46 0.36 0.81\r\nvn 0.51 0.35 0.78\r\nvn 0.41 0.35 0.84\r\nvn 0.41 0.36 0.84\r\nvn 0.41 0.36 0.83\r\nvn 0.46 0.36 0.81\r\nvn 0.51 0.36 0.78\r\nvn 0.31 0.35 0.88\r\nvn 0.31 0.36 0.88\r\nvn 0.36 0.36 0.86\r\nvn 0.36 0.35 0.86\r\nvn 0.31 0.36 0.88\r\nvn 0.36 0.37 0.86\r\nvn 0.31 0.4 0.86\r\nvn 0.36 0.4 0.84\r\nvn 0.36 0.37 0.85\r\nvn 0.31 0.37 0.88\r\nvn 0.42 0.37 0.83\r\nvn 0.42 0.4 0.82\r\nvn 0.51 0.37 0.78\r\nvn 0.47 0.37 0.8\r\nvn 0.47 0.39 0.79\r\nvn 0.52 0.38 0.77\r\nvn 0.6 0.34 0.73\r\nvn 0.6 0.35 0.72\r\nvn 0.62 0.35 0.7\r\nvn 0.62 0.34 0.71\r\nvn 0.6 0.35 0.72\r\nvn 0.62 0.35 0.7\r\nvn 0.56 0.35 0.75\r\nvn 0.55 0.34 0.76\r\nvn 0.56 0.36 0.75\r\nvn 0.56 0.37 0.74\r\nvn 0.56 0.36 0.74\r\nvn 0.6 0.36 0.71\r\nvn 0.6 0.39 0.69\r\nvn 0.62 0.36 0.7\r\nvn 0.62 0.39 0.68\r\nvn 0.18 0.58 0.8\r\nvn 0.19 0.51 0.84\r\nvn 0.12 0.59 0.8\r\nvn 0.13 0.52 0.85\r\nvn 0.13 0.45 0.88\r\nvn 0.19 0.44 0.88\r\nvn 0 0.59 0.8\r\nvn 0 0.52 0.85\r\nvn 0.07 0.52 0.85\r\nvn 0.06 0.59 0.8\r\nvn 0 0.46 0.89\r\nvn 0.07 0.46 0.89\r\nvn 0 0.33 0.94\r\nvn 0.07 0.33 0.94\r\nvn 0.07 0.39 0.92\r\nvn 0 0.39 0.92\r\nvn 0.14 0.33 0.94\r\nvn 0.13 0.39 0.91\r\nvn 0.19 0.38 0.9\r\nvn 0.2 0.32 0.93\r\nvn 0.16 0.81 0.56\r\nvn 0.17 0.76 0.62\r\nvn 0.11 0.77 0.62\r\nvn 0.11 0.82 0.56\r\nvn 0.12 0.72 0.69\r\nvn 0.17 0.71 0.69\r\nvn 0 0.78 0.62\r\nvn 0.06 0.78 0.62\r\nvn 0.06 0.83 0.56\r\nvn 0 0.83 0.56\r\nvn 0 0.72 0.69\r\nvn 0.06 0.72 0.69\r\nvn 0.06 0.66 0.75\r\nvn 0 0.66 0.75\r\nvn 0.12 0.65 0.75\r\nvn 0.18 0.64 0.74\r\nvn 0.21 0.14 0.97\r\nvn 0.21 0.11 0.97\r\nvn 0.14 0.14 0.98\r\nvn 0.15 0.11 0.98\r\nvn 0.15 0.09 0.99\r\nvn 0.21 0.09 0.97\r\nvn 0 0.14 0.99\r\nvn 0 0.11 0.99\r\nvn 0.08 0.11 0.99\r\nvn 0.07 0.14 0.99\r\nvn 0 0.09 1\r\nvn 0.08 0.09 0.99\r\nvn 0 0.08 1\r\nvn 0.08 0.08 0.99\r\nvn 0.08 0.08 0.99\r\nvn 0 0.08 1\r\nvn 0.15 0.08 0.99\r\nvn 0.15 0.08 0.99\r\nvn 0.22 0.07 0.97\r\nvn 0.22 0.07 0.97\r\nvn 0.25 0.35 0.9\r\nvn 0.24 0.36 0.9\r\nvn 0.17 0.35 0.92\r\nvn 0.17 0.36 0.92\r\nvn 0.17 0.36 0.92\r\nvn 0.24 0.36 0.9\r\nvn 0 0.35 0.94\r\nvn 0 0.35 0.94\r\nvn 0.09 0.35 0.93\r\nvn 0.09 0.35 0.93\r\nvn 0 0.36 0.93\r\nvn 0.09 0.36 0.93\r\nvn 0 0.41 0.91\r\nvn 0.09 0.41 0.91\r\nvn 0.09 0.37 0.92\r\nvn -0 0.37 0.93\r\nvn 0.17 0.37 0.91\r\nvn 0.17 0.41 0.9\r\nvn 0.24 0.37 0.9\r\nvn 0.24 0.41 0.88\r\nvn 0.58 0.09 0.81\r\nvn 0.58 0.11 0.8\r\nvn 0.61 0.11 0.79\r\nvn 0.61 0.09 0.79\r\nvn 0.59 0.14 0.8\r\nvn 0.61 0.14 0.78\r\nvn 0.49 0.09 0.87\r\nvn 0.49 0.11 0.86\r\nvn 0.54 0.11 0.84\r\nvn 0.54 0.09 0.84\r\nvn 0.49 0.14 0.86\r\nvn 0.54 0.14 0.83\r\nvn 0.5 0.23 0.83\r\nvn 0.55 0.23 0.8\r\nvn 0.55 0.18 0.82\r\nvn 0.5 0.19 0.85\r\nvn 0.59 0.22 0.77\r\nvn 0.59 0.18 0.79\r\nvn 0.61 0.18 0.77\r\nvn 0.62 0.22 0.76\r\nvn 0.44 0.09 0.89\r\nvn 0.44 0.11 0.89\r\nvn 0.39 0.09 0.92\r\nvn 0.39 0.11 0.91\r\nvn 0.4 0.15 0.91\r\nvn 0.45 0.14 0.88\r\nvn 0.29 0.1 0.95\r\nvn 0.3 0.12 0.95\r\nvn 0.35 0.12 0.93\r\nvn 0.34 0.09 0.93\r\nvn 0.3 0.16 0.94\r\nvn 0.35 0.15 0.92\r\nvn 0.31 0.24 0.92\r\nvn 0.36 0.24 0.9\r\nvn 0.35 0.2 0.91\r\nvn 0.3 0.2 0.93\r\nvn 0.41 0.24 0.88\r\nvn 0.4 0.19 0.89\r\nvn 0.45 0.19 0.87\r\nvn 0.45 0.23 0.86\r\nvn 0.36 0.34 0.87\r\nvn 0.31 0.34 0.89\r\nvn 0.41 0.34 0.85\r\nvn 0.41 0.32 0.86\r\nvn 0.36 0.32 0.88\r\nvn 0.31 0.32 0.9\r\nvn 0.51 0.34 0.79\r\nvn 0.46 0.34 0.82\r\nvn 0.51 0.31 0.8\r\nvn 0.46 0.31 0.83\r\nvn 0.46 0.27 0.85\r\nvn 0.5 0.27 0.82\r\nvn 0.41 0.28 0.87\r\nvn 0.31 0.28 0.91\r\nvn 0.36 0.28 0.89\r\nvn 0.55 0.33 0.76\r\nvn 0.6 0.33 0.73\r\nvn 0.6 0.3 0.74\r\nvn 0.55 0.31 0.78\r\nvn 0.62 0.33 0.72\r\nvn 0.62 0.3 0.73\r\nvn 0.6 0.26 0.76\r\nvn 0.62 0.26 0.74\r\nvn 0.55 0.27 0.79\r\nvn 0.24 0.1 0.97\r\nvn 0.24 0.13 0.96\r\nvn 0.17 0.11 0.98\r\nvn 0.17 0.13 0.98\r\nvn 0.17 0.17 0.97\r\nvn 0.24 0.16 0.96\r\nvn 0 0.12 0.99\r\nvn -0 0.14 0.99\r\nvn 0.09 0.14 0.99\r\nvn 0.09 0.12 0.99\r\nvn 0 0.17 0.99\r\nvn 0.09 0.17 0.98\r\nvn 0 0.25 0.97\r\nvn 0.09 0.25 0.96\r\nvn 0.09 0.21 0.97\r\nvn 0 0.21 0.98\r\nvn 0.18 0.25 0.95\r\nvn 0.18 0.21 0.96\r\nvn 0.25 0.21 0.95\r\nvn 0.25 0.25 0.94\r\nvn 0.32 0.17 0.93\r\nvn 0.27 0.17 0.95\r\nvn 0.38 0.16 0.91\r\nvn 0.37 0.2 0.91\r\nvn 0.32 0.21 0.92\r\nvn 0.26 0.21 0.94\r\nvn 0.43 0.16 0.89\r\nvn 0.42 0.2 0.88\r\nvn 0.42 0.24 0.88\r\nvn 0.37 0.25 0.9\r\nvn 0.26 0.26 0.93\r\nvn 0.32 0.25 0.91\r\nvn 0.54 0.4 0.74\r\nvn 0.56 0.39 0.73\r\nvn 0.55 0.36 0.76\r\nvn 0.57 0.35 0.74\r\nvn 0.49 0.42 0.77\r\nvn 0.5 0.37 0.79\r\nvn 0.51 0.32 0.8\r\nvn 0.56 0.31 0.77\r\nvn 0.58 0.3 0.76\r\nvn 0.53 0.09 0.84\r\nvn 0.48 0.08 0.87\r\nvn 0.58 0.09 0.81\r\nvn 0.58 0.08 0.81\r\nvn 0.53 0.08 0.84\r\nvn 0.48 0.08 0.87\r\nvn 0.6 0.09 0.79\r\nvn 0.6 0.08 0.79\r\nvn 0.58 0.08 0.81\r\nvn 0.6 0.08 0.79\r\nvn 0.48 0.07 0.87\r\nvn 0.53 0.08 0.84\r\nvn 0.34 0.08 0.94\r\nvn 0.29 0.09 0.95\r\nvn 0.39 0.08 0.92\r\nvn 0.39 0.08 0.92\r\nvn 0.34 0.08 0.94\r\nvn 0.28 0.08 0.96\r\nvn 0.44 0.08 0.9\r\nvn 0.44 0.08 0.9\r\nvn 0.43 0.07 0.9\r\nvn 0.39 0.07 0.92\r\nvn 0.28 0.07 0.96\r\nvn 0.33 0.07 0.94\r\nvn 0.09 0.34 0.94\r\nvn 0 0.34 0.94\r\nvn 0.18 0.34 0.92\r\nvn 0.18 0.32 0.93\r\nvn 0.09 0.32 0.94\r\nvn 0 0.32 0.95\r\nvn 0.25 0.34 0.91\r\nvn 0.25 0.32 0.92\r\nvn 0.25 0.28 0.93\r\nvn 0.18 0.28 0.94\r\nvn 0 0.28 0.96\r\nvn 0.09 0.28 0.95\r\nvn 0.07 0.18 0.98\r\nvn -0 0.18 0.98\r\nvn 0.14 0.18 0.97\r\nvn 0.14 0.22 0.96\r\nvn 0.07 0.22 0.97\r\nvn 0 0.22 0.97\r\nvn 0.21 0.17 0.96\r\nvn 0.21 0.22 0.95\r\nvn 0.2 0.27 0.94\r\nvn 0.14 0.27 0.95\r\nvn 0 0.28 0.96\r\nvn 0.07 0.27 0.96\r\nvn 0.09 0.1 0.99\r\nvn 0 0.11 0.99\r\nvn 0.17 0.1 0.98\r\nvn 0.16 0.09 0.98\r\nvn 0.09 0.09 0.99\r\nvn 0 0.1 1\r\nvn 0.23 0.09 0.97\r\nvn 0.23 0.08 0.97\r\nvn 0.22 0.08 0.97\r\nvn 0.16 0.08 0.98\r\nvn 0 0.09 1\r\nvn 0.08 0.09 0.99\r\nvn -0.58 0.08 0.81\r\nvn -0.58 0.1 0.81\r\nvn -0.6 0.1 0.79\r\nvn -0.6 0.08 0.79\r\nvn -0.58 0.12 0.81\r\nvn -0.6 0.12 0.79\r\nvn -0.58 0.08 0.81\r\nvn -0.58 0.08 0.81\r\nvn -0.6 0.08 0.8\r\nvn -0.6 0.08 0.8\r\nvn -0.48 0.07 0.87\r\nvn -0.48 0.07 0.87\r\nvn -0.53 0.07 0.84\r\nvn -0.53 0.07 0.84\r\nvn -0.48 0.08 0.87\r\nvn -0.53 0.08 0.84\r\nvn -0.48 0.12 0.87\r\nvn -0.53 0.12 0.84\r\nvn -0.53 0.1 0.84\r\nvn -0.48 0.1 0.87\r\nvn -0.42 0.49 0.76\r\nvn -0.44 0.44 0.79\r\nvn -0.39 0.45 0.8\r\nvn -0.38 0.51 0.77\r\nvn -0.45 0.38 0.81\r\nvn -0.4 0.4 0.83\r\nvn -0.35 0.41 0.84\r\nvn -0.34 0.47 0.81\r\nvn -0.33 0.53 0.78\r\nvn -0.47 0.28 0.84\r\nvn -0.41 0.29 0.86\r\nvn -0.41 0.34 0.85\r\nvn -0.46 0.33 0.83\r\nvn -0.36 0.3 0.88\r\nvn -0.36 0.35 0.86\r\nvn -0.26 0.31 0.91\r\nvn -0.25 0.37 0.89\r\nvn -0.31 0.36 0.88\r\nvn -0.31 0.31 0.9\r\nvn -0.25 0.43 0.87\r\nvn -0.3 0.42 0.86\r\nvn -0.23 0.56 0.79\r\nvn -0.28 0.55 0.79\r\nvn -0.29 0.48 0.82\r\nvn -0.24 0.5 0.83\r\nvn -0.38 0.68 0.63\r\nvn -0.34 0.7 0.63\r\nvn -0.34 0.75 0.57\r\nvn -0.38 0.73 0.58\r\nvn -0.39 0.61 0.68\r\nvn -0.35 0.64 0.69\r\nvn -0.31 0.66 0.69\r\nvn -0.3 0.72 0.63\r\nvn -0.3 0.77 0.57\r\nvn -0.36 0.58 0.73\r\nvn -0.41 0.55 0.72\r\nvn -0.32 0.6 0.74\r\nvn -0.23 0.63 0.74\r\nvn -0.28 0.62 0.74\r\nvn -0.22 0.69 0.69\r\nvn -0.27 0.68 0.69\r\nvn -0.21 0.8 0.56\r\nvn -0.25 0.78 0.57\r\nvn -0.26 0.74 0.63\r\nvn -0.21 0.75 0.62\r\nvn -0.43 0.1 0.9\r\nvn -0.43 0.13 0.89\r\nvn -0.43 0.08 0.9\r\nvn -0.38 0.08 0.92\r\nvn -0.38 0.1 0.92\r\nvn -0.38 0.13 0.92\r\nvn -0.43 0.07 0.9\r\nvn -0.43 0.07 0.9\r\nvn -0.38 0.07 0.92\r\nvn -0.38 0.07 0.92\r\nvn -0.28 0.07 0.96\r\nvn -0.28 0.07 0.96\r\nvn -0.33 0.07 0.94\r\nvn -0.33 0.07 0.94\r\nvn -0.27 0.08 0.96\r\nvn -0.33 0.08 0.94\r\nvn -0.27 0.13 0.95\r\nvn -0.33 0.13 0.94\r\nvn -0.33 0.1 0.94\r\nvn -0.27 0.1 0.96\r\nvn -0.46 0.53 0.72\r\nvn -0.47 0.47 0.74\r\nvn -0.44 0.59 0.68\r\nvn -0.49 0.56 0.67\r\nvn -0.51 0.5 0.7\r\nvn -0.52 0.45 0.72\r\nvn -0.42 0.7 0.58\r\nvn -0.42 0.65 0.63\r\nvn -0.47 0.62 0.62\r\nvn -0.47 0.67 0.57\r\nvn -0.5 0.61 0.62\r\nvn -0.51 0.54 0.66\r\nvn -0.49 0.66 0.57\r\nvn -0.53 0.49 0.69\r\nvn -0.55 0.44 0.71\r\nvn -0.48 0.16 0.86\r\nvn -0.53 0.15 0.84\r\nvn -0.48 0.19 0.86\r\nvn -0.52 0.19 0.83\r\nvn -0.57 0.19 0.8\r\nvn -0.58 0.15 0.8\r\nvn -0.52 0.27 0.81\r\nvn -0.52 0.23 0.82\r\nvn -0.47 0.23 0.85\r\nvn -0.56 0.27 0.78\r\nvn -0.57 0.22 0.79\r\nvn -0.59 0.22 0.77\r\nvn -0.6 0.18 0.78\r\nvn -0.59 0.26 0.77\r\nvn -0.6 0.15 0.79\r\nvn -0.51 0.35 0.79\r\nvn -0.51 0.35 0.78\r\nvn -0.46 0.36 0.81\r\nvn -0.46 0.35 0.82\r\nvn -0.51 0.36 0.78\r\nvn -0.46 0.36 0.81\r\nvn -0.41 0.36 0.83\r\nvn -0.41 0.36 0.84\r\nvn -0.41 0.35 0.84\r\nvn -0.52 0.38 0.77\r\nvn -0.47 0.39 0.79\r\nvn -0.47 0.37 0.8\r\nvn -0.51 0.37 0.78\r\nvn -0.42 0.37 0.83\r\nvn -0.42 0.4 0.82\r\nvn -0.31 0.37 0.88\r\nvn -0.36 0.37 0.85\r\nvn -0.36 0.4 0.84\r\nvn -0.31 0.4 0.86\r\nvn -0.31 0.36 0.88\r\nvn -0.36 0.37 0.86\r\nvn -0.31 0.35 0.88\r\nvn -0.36 0.35 0.86\r\nvn -0.36 0.36 0.86\r\nvn -0.31 0.36 0.88\r\nvn -0.6 0.35 0.72\r\nvn -0.6 0.35 0.72\r\nvn -0.62 0.35 0.7\r\nvn -0.62 0.35 0.7\r\nvn -0.6 0.34 0.73\r\nvn -0.62 0.34 0.71\r\nvn -0.6 0.36 0.71\r\nvn -0.62 0.35 0.7\r\nvn -0.63 0.36 0.69\r\nvn -0.61 0.36 0.7\r\nvn -0.56 0.36 0.74\r\nvn -0.56 0.37 0.74\r\nvn -0.56 0.36 0.75\r\nvn -0.55 0.34 0.76\r\nvn -0.56 0.35 0.75\r\nvn -0.19 0.51 0.84\r\nvn -0.18 0.58 0.8\r\nvn -0.19 0.44 0.88\r\nvn -0.13 0.45 0.88\r\nvn -0.13 0.52 0.85\r\nvn -0.12 0.59 0.8\r\nvn -0.2 0.32 0.93\r\nvn -0.19 0.38 0.9\r\nvn -0.14 0.33 0.94\r\nvn -0.13 0.39 0.91\r\nvn -0.07 0.39 0.92\r\nvn -0.07 0.33 0.94\r\nvn -0.07 0.46 0.89\r\nvn -0.06 0.59 0.8\r\nvn -0.07 0.52 0.85\r\nvn -0.17 0.76 0.62\r\nvn -0.16 0.81 0.56\r\nvn -0.17 0.71 0.69\r\nvn -0.12 0.72 0.69\r\nvn -0.11 0.77 0.62\r\nvn -0.11 0.82 0.56\r\nvn -0.18 0.64 0.74\r\nvn -0.12 0.65 0.75\r\nvn -0.06 0.66 0.75\r\nvn -0.06 0.72 0.69\r\nvn -0.06 0.83 0.56\r\nvn -0.06 0.78 0.62\r\nvn -0.21 0.11 0.97\r\nvn -0.21 0.14 0.97\r\nvn -0.21 0.09 0.97\r\nvn -0.15 0.09 0.99\r\nvn -0.15 0.11 0.98\r\nvn -0.14 0.14 0.98\r\nvn -0.22 0.07 0.97\r\nvn -0.22 0.07 0.97\r\nvn -0.15 0.08 0.99\r\nvn -0.15 0.08 0.99\r\nvn -0.08 0.08 0.99\r\nvn -0.08 0.08 0.99\r\nvn -0.08 0.09 0.99\r\nvn -0.07 0.14 0.99\r\nvn -0.08 0.11 0.99\r\nvn -0.24 0.36 0.9\r\nvn -0.25 0.35 0.9\r\nvn -0.24 0.36 0.9\r\nvn -0.17 0.36 0.92\r\nvn -0.17 0.36 0.92\r\nvn -0.17 0.35 0.92\r\nvn -0.24 0.41 0.88\r\nvn -0.24 0.37 0.9\r\nvn -0.17 0.37 0.91\r\nvn -0.17 0.41 0.9\r\nvn -0.09 0.37 0.92\r\nvn -0.09 0.41 0.91\r\nvn -0.09 0.36 0.93\r\nvn -0.09 0.35 0.93\r\nvn -0.09 0.35 0.93\r\nvn -0.59 0.14 0.8\r\nvn -0.58 0.11 0.8\r\nvn -0.61 0.11 0.79\r\nvn -0.61 0.14 0.78\r\nvn -0.58 0.09 0.81\r\nvn -0.61 0.09 0.79\r\nvn -0.59 0.22 0.77\r\nvn -0.59 0.18 0.79\r\nvn -0.61 0.18 0.77\r\nvn -0.62 0.22 0.76\r\nvn -0.5 0.23 0.83\r\nvn -0.5 0.19 0.85\r\nvn -0.55 0.18 0.82\r\nvn -0.55 0.23 0.8\r\nvn -0.49 0.14 0.86\r\nvn -0.54 0.14 0.83\r\nvn -0.49 0.09 0.87\r\nvn -0.54 0.09 0.84\r\nvn -0.54 0.11 0.84\r\nvn -0.49 0.11 0.86\r\nvn -0.44 0.11 0.89\r\nvn -0.44 0.09 0.89\r\nvn -0.45 0.14 0.88\r\nvn -0.4 0.15 0.91\r\nvn -0.39 0.11 0.91\r\nvn -0.39 0.09 0.92\r\nvn -0.45 0.23 0.86\r\nvn -0.45 0.19 0.87\r\nvn -0.41 0.24 0.88\r\nvn -0.4 0.19 0.89\r\nvn -0.31 0.24 0.92\r\nvn -0.3 0.2 0.93\r\nvn -0.35 0.2 0.91\r\nvn -0.36 0.24 0.9\r\nvn -0.3 0.16 0.94\r\nvn -0.35 0.15 0.92\r\nvn -0.29 0.1 0.95\r\nvn -0.34 0.09 0.93\r\nvn -0.35 0.12 0.93\r\nvn -0.3 0.12 0.95\r\nvn -0.31 0.34 0.89\r\nvn -0.36 0.34 0.87\r\nvn -0.31 0.32 0.9\r\nvn -0.36 0.32 0.88\r\nvn -0.41 0.32 0.86\r\nvn -0.41 0.34 0.85\r\nvn -0.36 0.28 0.89\r\nvn -0.31 0.28 0.91\r\nvn -0.41 0.28 0.87\r\nvn -0.5 0.27 0.82\r\nvn -0.46 0.27 0.85\r\nvn -0.51 0.31 0.8\r\nvn -0.46 0.31 0.83\r\nvn -0.46 0.34 0.82\r\nvn -0.51 0.34 0.79\r\nvn -0.55 0.33 0.76\r\nvn -0.55 0.31 0.78\r\nvn -0.6 0.3 0.74\r\nvn -0.6 0.33 0.73\r\nvn -0.55 0.27 0.79\r\nvn -0.6 0.26 0.76\r\nvn -0.62 0.26 0.74\r\nvn -0.62 0.3 0.73\r\nvn -0.62 0.33 0.72\r\nvn -0.24 0.13 0.96\r\nvn -0.24 0.1 0.97\r\nvn -0.24 0.16 0.96\r\nvn -0.17 0.17 0.97\r\nvn -0.17 0.13 0.98\r\nvn -0.17 0.11 0.98\r\nvn -0.25 0.25 0.94\r\nvn -0.25 0.21 0.95\r\nvn -0.18 0.25 0.95\r\nvn -0.18 0.21 0.96\r\nvn -0.09 0.21 0.97\r\nvn -0.09 0.25 0.96\r\nvn -0.09 0.17 0.98\r\nvn -0.09 0.12 0.99\r\nvn -0.09 0.14 0.99\r\nvn -0.27 0.17 0.95\r\nvn -0.32 0.17 0.93\r\nvn -0.26 0.21 0.94\r\nvn -0.32 0.21 0.92\r\nvn -0.37 0.2 0.91\r\nvn -0.38 0.16 0.91\r\nvn -0.32 0.25 0.91\r\nvn -0.26 0.26 0.93\r\nvn -0.37 0.25 0.9\r\nvn -0.42 0.24 0.88\r\nvn -0.42 0.2 0.88\r\nvn -0.43 0.16 0.89\r\nvn -0.55 0.36 0.76\r\nvn -0.54 0.4 0.74\r\nvn -0.56 0.39 0.73\r\nvn -0.57 0.35 0.74\r\nvn -0.56 0.31 0.77\r\nvn -0.58 0.3 0.76\r\nvn -0.51 0.32 0.8\r\nvn -0.5 0.37 0.79\r\nvn -0.49 0.42 0.77\r\nvn -0.48 0.08 0.87\r\nvn -0.53 0.09 0.84\r\nvn -0.48 0.08 0.87\r\nvn -0.53 0.08 0.84\r\nvn -0.58 0.08 0.81\r\nvn -0.58 0.09 0.81\r\nvn -0.53 0.08 0.84\r\nvn -0.48 0.07 0.87\r\nvn -0.58 0.08 0.81\r\nvn -0.6 0.08 0.79\r\nvn -0.6 0.08 0.79\r\nvn -0.6 0.09 0.79\r\nvn -0.29 0.09 0.95\r\nvn -0.34 0.08 0.94\r\nvn -0.28 0.08 0.96\r\nvn -0.34 0.08 0.94\r\nvn -0.39 0.08 0.92\r\nvn -0.39 0.08 0.92\r\nvn -0.33 0.07 0.94\r\nvn -0.28 0.07 0.96\r\nvn -0.39 0.07 0.92\r\nvn -0.43 0.07 0.9\r\nvn -0.44 0.08 0.9\r\nvn -0.44 0.08 0.9\r\nvn -0.09 0.34 0.94\r\nvn -0.09 0.32 0.94\r\nvn -0.18 0.32 0.93\r\nvn -0.18 0.34 0.92\r\nvn -0.09 0.28 0.95\r\nvn -0.18 0.28 0.94\r\nvn -0.25 0.28 0.93\r\nvn -0.25 0.32 0.92\r\nvn -0.25 0.34 0.91\r\nvn -0.07 0.18 0.98\r\nvn -0.07 0.22 0.97\r\nvn -0.14 0.22 0.96\r\nvn -0.14 0.18 0.97\r\nvn -0.07 0.27 0.96\r\nvn -0.14 0.27 0.95\r\nvn -0.2 0.27 0.94\r\nvn -0.21 0.22 0.95\r\nvn -0.21 0.17 0.96\r\nvn -0.09 0.1 0.99\r\nvn -0.09 0.09 0.99\r\nvn -0.16 0.09 0.98\r\nvn -0.17 0.1 0.98\r\nvn -0.08 0.09 0.99\r\nvn -0.16 0.08 0.98\r\nvn -0.22 0.08 0.97\r\nvn -0.23 0.08 0.97\r\nvn -0.23 0.09 0.97\r\nvn 0.37 0.77 0.52\r\nvn 0.33 0.79 0.51\r\nvn 0.38 0.81 0.44\r\nvn 0.33 0.83 0.44\r\nvn 0.29 0.85 0.44\r\nvn 0.29 0.81 0.51\r\nvn 0.39 0.86 0.34\r\nvn 0.34 0.88 0.33\r\nvn 0.33 0.86 0.38\r\nvn 0.38 0.84 0.39\r\nvn 0.29 0.9 0.32\r\nvn 0.29 0.88 0.38\r\nvn 0.2 0.93 0.31\r\nvn 0.2 0.91 0.36\r\nvn 0.24 0.9 0.37\r\nvn 0.24 0.92 0.32\r\nvn 0.2 0.88 0.42\r\nvn 0.25 0.87 0.43\r\nvn 0.25 0.83 0.5\r\nvn 0.2 0.84 0.5\r\nvn 0.48 0.76 0.44\r\nvn 0.47 0.72 0.51\r\nvn 0.5 0.7 0.51\r\nvn 0.51 0.74 0.44\r\nvn 0.5 0.79 0.35\r\nvn 0.49 0.78 0.39\r\nvn 0.52 0.76 0.39\r\nvn 0.53 0.77 0.35\r\nvn 0.44 0.81 0.39\r\nvn 0.44 0.83 0.34\r\nvn 0.43 0.79 0.44\r\nvn 0.42 0.75 0.52\r\nvn 0.45 0.84 0.3\r\nvn 0.39 0.87 0.29\r\nvn 0.51 0.8 0.31\r\nvn 0.52 0.81 0.27\r\nvn 0.46 0.85 0.26\r\nvn 0.4 0.88 0.25\r\nvn 0.54 0.78 0.31\r\nvn 0.55 0.79 0.28\r\nvn 0.52 0.83 0.2\r\nvn 0.52 0.82 0.24\r\nvn 0.55 0.8 0.24\r\nvn 0.56 0.81 0.21\r\nvn 0.4 0.9 0.18\r\nvn 0.4 0.89 0.21\r\nvn 0.46 0.86 0.22\r\nvn 0.46 0.87 0.19\r\nvn 0.46 0.77 -0.45\r\nvn 0.47 0.79 -0.4\r\nvn 0.5 0.77 -0.4\r\nvn 0.49 0.75 -0.45\r\nvn 0.48 0.8 -0.35\r\nvn 0.51 0.78 -0.35\r\nvn 0.45 0.72 -0.53\r\nvn 0.45 0.74 -0.5\r\nvn 0.48 0.72 -0.5\r\nvn 0.48 0.7 -0.54\r\nvn 0.36 0.76 -0.53\r\nvn 0.36 0.79 -0.51\r\nvn 0.4 0.76 -0.51\r\nvn 0.4 0.74 -0.54\r\nvn 0.36 0.82 -0.45\r\nvn 0.4 0.8 -0.45\r\nvn 0.37 0.86 -0.35\r\nvn 0.42 0.83 -0.35\r\nvn 0.41 0.82 -0.4\r\nvn 0.36 0.84 -0.4\r\nvn 0.38 0.91 -0.19\r\nvn 0.45 0.87 -0.19\r\nvn 0.44 0.87 -0.23\r\nvn 0.38 0.9 -0.22\r\nvn 0.51 0.84 -0.2\r\nvn 0.51 0.83 -0.24\r\nvn 0.5 0.82 -0.27\r\nvn 0.44 0.86 -0.27\r\nvn 0.38 0.89 -0.26\r\nvn 0.54 0.81 -0.24\r\nvn 0.53 0.8 -0.28\r\nvn 0.54 0.81 -0.2\r\nvn 0.49 0.81 -0.31\r\nvn 0.52 0.79 -0.31\r\nvn 0.37 0.88 -0.3\r\nvn 0.43 0.85 -0.31\r\nvn 0.18 0.97 -0.15\r\nvn 0.22 0.96 -0.16\r\nvn 0.22 0.95 -0.19\r\nvn 0.18 0.97 -0.19\r\nvn 0.27 0.95 -0.17\r\nvn 0.27 0.94 -0.2\r\nvn 0.27 0.93 -0.24\r\nvn 0.23 0.95 -0.23\r\nvn 0.18 0.96 -0.23\r\nvn 0.32 0.92 -0.21\r\nvn 0.32 0.93 -0.18\r\nvn 0.32 0.91 -0.25\r\nvn 0.32 0.88 -0.34\r\nvn 0.32 0.9 -0.3\r\nvn 0.27 0.9 -0.34\r\nvn 0.27 0.92 -0.29\r\nvn 0.19 0.93 -0.32\r\nvn 0.18 0.94 -0.27\r\nvn 0.23 0.93 -0.28\r\nvn 0.23 0.92 -0.33\r\nvn 0.23 0.9 -0.38\r\nvn 0.19 0.91 -0.37\r\nvn 0.28 0.88 -0.39\r\nvn 0.28 0.85 -0.44\r\nvn 0.24 0.87 -0.43\r\nvn 0.2 0.88 -0.42\r\nvn 0.32 0.86 -0.39\r\nvn 0.32 0.84 -0.44\r\nvn 0.33 0.78 -0.53\r\nvn 0.32 0.8 -0.5\r\nvn 0.29 0.8 -0.52\r\nvn 0.28 0.82 -0.5\r\nvn 0.21 0.83 -0.51\r\nvn 0.2 0.85 -0.48\r\nvn 0.24 0.84 -0.49\r\nvn 0.25 0.82 -0.52\r\nvn 0.34 0.9 0.28\r\nvn 0.34 0.91 0.24\r\nvn 0.29 0.93 0.23\r\nvn 0.29 0.92 0.28\r\nvn 0.34 0.93 0.17\r\nvn 0.34 0.92 0.2\r\nvn 0.28 0.95 0.16\r\nvn 0.28 0.94 0.19\r\nvn 0.18 0.97 0.14\r\nvn 0.19 0.97 0.17\r\nvn 0.23 0.95 0.18\r\nvn 0.23 0.96 0.15\r\nvn 0.19 0.96 0.21\r\nvn 0.24 0.95 0.22\r\nvn 0.24 0.93 0.27\r\nvn 0.19 0.95 0.26\r\nvn 0.17 0.85 -0.51\r\nvn 0.16 0.86 -0.48\r\nvn 0.11 0.86 -0.5\r\nvn 0.11 0.88 -0.47\r\nvn 0.1 0.91 -0.41\r\nvn 0.15 0.9 -0.42\r\nvn -0 0.87 -0.5\r\nvn 0 0.88 -0.47\r\nvn 0.06 0.88 -0.47\r\nvn 0.06 0.86 -0.5\r\nvn 0 0.91 -0.41\r\nvn 0.05 0.91 -0.41\r\nvn 0 0.95 -0.3\r\nvn 0.05 0.95 -0.3\r\nvn 0.05 0.94 -0.35\r\nvn 0 0.94 -0.35\r\nvn 0.1 0.95 -0.3\r\nvn 0.1 0.93 -0.35\r\nvn 0.15 0.92 -0.36\r\nvn 0.14 0.94 -0.31\r\nvn 0.15 0.96 0.25\r\nvn 0.15 0.94 0.3\r\nvn 0.14 0.97 0.21\r\nvn 0.1 0.98 0.2\r\nvn 0.1 0.96 0.24\r\nvn 0.1 0.95 0.3\r\nvn 0.14 0.98 0.13\r\nvn 0.14 0.98 0.17\r\nvn 0.09 0.99 0.13\r\nvn 0.1 0.98 0.16\r\nvn 0 0.99 0.12\r\nvn 0 0.99 0.16\r\nvn 0.05 0.99 0.16\r\nvn 0.05 0.99 0.12\r\nvn 0 0.98 0.19\r\nvn 0.05 0.98 0.2\r\nvn 0 0.96 0.29\r\nvn 0.05 0.96 0.29\r\nvn 0.05 0.97 0.24\r\nvn 0 0.97 0.24\r\nvn 0.52 0.85 0\r\nvn 0.53 0.85 0.03\r\nvn 0.56 0.83 0.03\r\nvn 0.56 0.83 0\r\nvn 0.53 0.85 0.05\r\nvn 0.56 0.83 0.06\r\nvn 0.39 0.92 -0.01\r\nvn 0.39 0.92 0.02\r\nvn 0.46 0.89 0.02\r\nvn 0.45 0.89 -0\r\nvn 0.39 0.92 0.04\r\nvn 0.46 0.89 0.04\r\nvn 0.39 0.92 0.08\r\nvn 0.46 0.88 0.09\r\nvn 0.46 0.89 0.07\r\nvn 0.39 0.92 0.06\r\nvn 0.53 0.84 0.1\r\nvn 0.53 0.85 0.08\r\nvn 0.56 0.82 0.08\r\nvn 0.56 0.82 0.1\r\nvn 0.33 0.94 0.05\r\nvn 0.33 0.94 0.07\r\nvn 0.33 0.94 0.03\r\nvn 0.27 0.96 0.03\r\nvn 0.27 0.96 0.05\r\nvn 0.27 0.96 0.06\r\nvn 0.33 0.95 -0.01\r\nvn 0.33 0.95 0.01\r\nvn 0.27 0.96 -0.01\r\nvn 0.27 0.96 0.01\r\nvn 0.17 0.98 -0.02\r\nvn 0.17 0.98 0\r\nvn 0.22 0.98 0\r\nvn 0.22 0.98 -0.01\r\nvn 0.17 0.98 0.02\r\nvn 0.22 0.98 0.02\r\nvn 0.18 0.98 0.05\r\nvn 0.22 0.97 0.06\r\nvn 0.22 0.97 0.04\r\nvn 0.18 0.98 0.04\r\nvn 0.13 0.99 -0.02\r\nvn 0.13 0.99 -0\r\nvn 0.09 1 -0.02\r\nvn 0.09 1 -0\r\nvn 0.09 1 0.01\r\nvn 0.13 0.99 0.02\r\nvn 0 1 -0.02\r\nvn 0 1 -0\r\nvn 0.04 1 -0\r\nvn 0.04 1 -0.02\r\nvn 0 1 0.01\r\nvn 0.04 1 0.01\r\nvn 0 1 0.04\r\nvn 0.04 1 0.04\r\nvn 0.04 1 0.03\r\nvn 0 1 0.03\r\nvn 0.09 1 0.04\r\nvn 0.09 1 0.03\r\nvn 0.13 0.99 0.03\r\nvn 0.13 0.99 0.05\r\nvn 0.53 0.84 0.14\r\nvn 0.53 0.83 0.17\r\nvn 0.56 0.81 0.18\r\nvn 0.56 0.82 0.15\r\nvn 0.53 0.84 0.12\r\nvn 0.56 0.82 0.13\r\nvn 0.39 0.91 0.1\r\nvn 0.46 0.88 0.11\r\nvn 0.39 0.91 0.12\r\nvn 0.46 0.88 0.13\r\nvn 0.46 0.87 0.16\r\nvn 0.4 0.91 0.15\r\nvn 0.33 0.93 0.14\r\nvn 0.33 0.94 0.11\r\nvn 0.28 0.96 0.1\r\nvn 0.28 0.95 0.13\r\nvn 0.33 0.94 0.09\r\nvn 0.28 0.96 0.08\r\nvn 0.18 0.98 0.07\r\nvn 0.22 0.97 0.07\r\nvn 0.18 0.98 0.09\r\nvn 0.23 0.97 0.1\r\nvn 0.23 0.97 0.12\r\nvn 0.18 0.98 0.11\r\nvn 0.14 0.98 0.11\r\nvn 0.14 0.99 0.08\r\nvn 0.09 0.99 0.08\r\nvn 0.09 0.99 0.1\r\nvn 0.13 0.99 0.06\r\nvn 0.09 0.99 0.06\r\nvn 0 1 0.06\r\nvn 0.04 1 0.06\r\nvn 0 1 0.08\r\nvn 0.05 1 0.08\r\nvn 0.05 0.99 0.1\r\nvn 0 1 0.1\r\nvn 0.52 0.85 -0.09\r\nvn 0.52 0.85 -0.06\r\nvn 0.56 0.83 -0.07\r\nvn 0.56 0.83 -0.09\r\nvn 0.52 0.85 -0.04\r\nvn 0.56 0.83 -0.04\r\nvn 0.38 0.92 -0.08\r\nvn 0.39 0.92 -0.06\r\nvn 0.45 0.89 -0.06\r\nvn 0.45 0.89 -0.08\r\nvn 0.39 0.92 -0.04\r\nvn 0.45 0.89 -0.04\r\nvn 0.45 0.89 -0.02\r\nvn 0.39 0.92 -0.02\r\nvn 0.52 0.85 -0.02\r\nvn 0.56 0.83 -0.02\r\nvn 0.32 0.94 -0.08\r\nvn 0.32 0.94 -0.06\r\nvn 0.27 0.96 -0.08\r\nvn 0.27 0.96 -0.06\r\nvn 0.27 0.96 -0.05\r\nvn 0.32 0.95 -0.04\r\nvn 0.17 0.98 -0.07\r\nvn 0.17 0.98 -0.06\r\nvn 0.22 0.97 -0.06\r\nvn 0.22 0.97 -0.07\r\nvn 0.17 0.98 -0.05\r\nvn 0.22 0.97 -0.05\r\nvn 0.22 0.98 -0.03\r\nvn 0.17 0.98 -0.03\r\nvn 0.27 0.96 -0.03\r\nvn 0.32 0.95 -0.03\r\nvn 0.13 0.99 -0.07\r\nvn 0.13 0.99 -0.06\r\nvn 0.09 0.99 -0.07\r\nvn 0.09 0.99 -0.06\r\nvn 0.09 1 -0.05\r\nvn 0.13 0.99 -0.05\r\nvn 0 1 -0.07\r\nvn 0 1 -0.06\r\nvn 0.04 1 -0.06\r\nvn 0.04 1 -0.07\r\nvn 0 1 -0.05\r\nvn 0.04 1 -0.05\r\nvn 0.04 1 -0.03\r\nvn 0 1 -0.03\r\nvn 0.09 1 -0.03\r\nvn 0.13 0.99 -0.03\r\nvn 0.52 0.84 -0.14\r\nvn 0.52 0.85 -0.11\r\nvn 0.55 0.82 -0.11\r\nvn 0.55 0.82 -0.14\r\nvn 0.51 0.84 -0.17\r\nvn 0.55 0.82 -0.17\r\nvn 0.38 0.91 -0.16\r\nvn 0.45 0.88 -0.16\r\nvn 0.38 0.91 -0.13\r\nvn 0.45 0.88 -0.13\r\nvn 0.45 0.89 -0.11\r\nvn 0.38 0.92 -0.1\r\nvn 0.32 0.93 -0.15\r\nvn 0.27 0.95 -0.14\r\nvn 0.27 0.96 -0.12\r\nvn 0.32 0.94 -0.12\r\nvn 0.18 0.98 -0.13\r\nvn 0.22 0.97 -0.13\r\nvn 0.17 0.98 -0.11\r\nvn 0.22 0.97 -0.11\r\nvn 0.22 0.97 -0.09\r\nvn 0.17 0.98 -0.09\r\nvn 0.27 0.96 -0.09\r\nvn 0.32 0.94 -0.1\r\nvn 0.13 0.98 -0.15\r\nvn 0.13 0.98 -0.12\r\nvn 0.09 0.99 -0.14\r\nvn 0.09 0.99 -0.12\r\nvn 0.09 0.99 -0.1\r\nvn 0.13 0.99 -0.1\r\nvn 0 0.99 -0.14\r\nvn 0 0.99 -0.12\r\nvn 0.04 0.99 -0.12\r\nvn 0.04 0.99 -0.14\r\nvn 0 1 -0.1\r\nvn 0.04 0.99 -0.1\r\nvn 0.04 1 -0.08\r\nvn 0 1 -0.08\r\nvn 0.09 0.99 -0.08\r\nvn 0.13 0.99 -0.08\r\nvn 0.06 0.87 0.49\r\nvn -0 0.87 0.49\r\nvn 0.11 0.86 0.49\r\nvn 0.11 0.9 0.41\r\nvn 0.05 0.91 0.41\r\nvn 0 0.91 0.41\r\nvn 0.16 0.86 0.49\r\nvn 0.16 0.89 0.42\r\nvn 0.15 0.92 0.36\r\nvn 0.1 0.93 0.35\r\nvn 0 0.94 0.35\r\nvn 0.05 0.94 0.35\r\nvn 0.05 0.98 -0.17\r\nvn -0 0.99 -0.17\r\nvn 0.09 0.98 -0.17\r\nvn 0.09 0.97 -0.21\r\nvn 0.05 0.98 -0.21\r\nvn -0 0.98 -0.21\r\nvn 0.14 0.97 -0.18\r\nvn 0.14 0.97 -0.22\r\nvn 0.14 0.95 -0.26\r\nvn 0.1 0.96 -0.26\r\nvn 0 0.97 -0.25\r\nvn 0.05 0.97 -0.25\r\nvn -0.33 0.79 0.51\r\nvn -0.37 0.77 0.52\r\nvn -0.29 0.81 0.51\r\nvn -0.29 0.85 0.44\r\nvn -0.33 0.83 0.44\r\nvn -0.38 0.81 0.44\r\nvn -0.2 0.84 0.5\r\nvn -0.25 0.83 0.5\r\nvn -0.2 0.88 0.42\r\nvn -0.25 0.87 0.43\r\nvn -0.2 0.93 0.31\r\nvn -0.24 0.92 0.32\r\nvn -0.24 0.9 0.37\r\nvn -0.2 0.91 0.36\r\nvn -0.29 0.9 0.32\r\nvn -0.29 0.88 0.38\r\nvn -0.39 0.86 0.34\r\nvn -0.38 0.84 0.39\r\nvn -0.33 0.86 0.38\r\nvn -0.34 0.88 0.33\r\nvn -0.47 0.72 0.51\r\nvn -0.5 0.7 0.51\r\nvn -0.48 0.76 0.44\r\nvn -0.51 0.74 0.44\r\nvn -0.42 0.75 0.52\r\nvn -0.43 0.79 0.44\r\nvn -0.44 0.83 0.34\r\nvn -0.44 0.81 0.39\r\nvn -0.5 0.79 0.35\r\nvn -0.49 0.78 0.39\r\nvn -0.52 0.76 0.39\r\nvn -0.53 0.77 0.35\r\nvn -0.39 0.87 0.29\r\nvn -0.45 0.84 0.3\r\nvn -0.4 0.88 0.25\r\nvn -0.46 0.85 0.26\r\nvn -0.52 0.81 0.27\r\nvn -0.51 0.8 0.31\r\nvn -0.4 0.9 0.18\r\nvn -0.46 0.87 0.19\r\nvn -0.46 0.86 0.22\r\nvn -0.4 0.89 0.21\r\nvn -0.52 0.83 0.2\r\nvn -0.52 0.82 0.24\r\nvn -0.55 0.8 0.24\r\nvn -0.55 0.79 0.28\r\nvn -0.56 0.81 0.21\r\nvn -0.54 0.78 0.31\r\nvn -0.48 0.8 -0.35\r\nvn -0.47 0.79 -0.4\r\nvn -0.5 0.77 -0.4\r\nvn -0.51 0.78 -0.35\r\nvn -0.46 0.77 -0.45\r\nvn -0.49 0.75 -0.45\r\nvn -0.37 0.86 -0.35\r\nvn -0.36 0.84 -0.4\r\nvn -0.41 0.82 -0.4\r\nvn -0.42 0.83 -0.35\r\nvn -0.36 0.82 -0.45\r\nvn -0.4 0.8 -0.45\r\nvn -0.36 0.76 -0.53\r\nvn -0.4 0.74 -0.54\r\nvn -0.4 0.76 -0.51\r\nvn -0.36 0.79 -0.51\r\nvn -0.45 0.72 -0.53\r\nvn -0.45 0.74 -0.5\r\nvn -0.48 0.72 -0.5\r\nvn -0.48 0.7 -0.54\r\nvn -0.38 0.91 -0.19\r\nvn -0.38 0.9 -0.22\r\nvn -0.44 0.87 -0.23\r\nvn -0.45 0.87 -0.19\r\nvn -0.38 0.89 -0.26\r\nvn -0.44 0.86 -0.27\r\nvn -0.5 0.82 -0.27\r\nvn -0.51 0.83 -0.24\r\nvn -0.51 0.84 -0.2\r\nvn -0.43 0.85 -0.31\r\nvn -0.37 0.88 -0.3\r\nvn -0.49 0.81 -0.31\r\nvn -0.52 0.79 -0.31\r\nvn -0.53 0.8 -0.28\r\nvn -0.54 0.81 -0.24\r\nvn -0.54 0.81 -0.2\r\nvn -0.18 0.97 -0.15\r\nvn -0.18 0.97 -0.19\r\nvn -0.22 0.95 -0.19\r\nvn -0.22 0.96 -0.16\r\nvn -0.18 0.96 -0.23\r\nvn -0.23 0.95 -0.23\r\nvn -0.27 0.93 -0.24\r\nvn -0.27 0.94 -0.2\r\nvn -0.27 0.95 -0.17\r\nvn -0.19 0.93 -0.32\r\nvn -0.23 0.92 -0.33\r\nvn -0.23 0.93 -0.28\r\nvn -0.18 0.94 -0.27\r\nvn -0.27 0.9 -0.34\r\nvn -0.27 0.92 -0.29\r\nvn -0.32 0.9 -0.3\r\nvn -0.32 0.88 -0.34\r\nvn -0.32 0.91 -0.25\r\nvn -0.32 0.93 -0.18\r\nvn -0.32 0.92 -0.21\r\nvn -0.19 0.91 -0.37\r\nvn -0.23 0.9 -0.38\r\nvn -0.2 0.88 -0.42\r\nvn -0.24 0.87 -0.43\r\nvn -0.28 0.85 -0.44\r\nvn -0.28 0.88 -0.39\r\nvn -0.21 0.83 -0.51\r\nvn -0.25 0.82 -0.52\r\nvn -0.24 0.84 -0.49\r\nvn -0.2 0.85 -0.48\r\nvn -0.29 0.8 -0.52\r\nvn -0.28 0.82 -0.5\r\nvn -0.32 0.8 -0.5\r\nvn -0.33 0.78 -0.53\r\nvn -0.32 0.84 -0.44\r\nvn -0.32 0.86 -0.39\r\nvn -0.34 0.9 0.28\r\nvn -0.29 0.92 0.28\r\nvn -0.29 0.93 0.23\r\nvn -0.34 0.91 0.24\r\nvn -0.19 0.95 0.26\r\nvn -0.24 0.93 0.27\r\nvn -0.19 0.96 0.21\r\nvn -0.24 0.95 0.22\r\nvn -0.18 0.97 0.14\r\nvn -0.23 0.96 0.15\r\nvn -0.23 0.95 0.18\r\nvn -0.19 0.97 0.17\r\nvn -0.28 0.95 0.16\r\nvn -0.28 0.94 0.19\r\nvn -0.34 0.92 0.2\r\nvn -0.34 0.93 0.17\r\nvn -0.16 0.86 -0.48\r\nvn -0.17 0.85 -0.51\r\nvn -0.15 0.9 -0.42\r\nvn -0.1 0.91 -0.41\r\nvn -0.11 0.88 -0.47\r\nvn -0.11 0.86 -0.5\r\nvn -0.14 0.94 -0.31\r\nvn -0.15 0.92 -0.36\r\nvn -0.1 0.95 -0.3\r\nvn -0.1 0.93 -0.35\r\nvn -0.05 0.94 -0.35\r\nvn -0.05 0.95 -0.3\r\nvn -0.05 0.91 -0.41\r\nvn -0.06 0.86 -0.5\r\nvn -0.06 0.88 -0.47\r\nvn -0.15 0.94 0.3\r\nvn -0.15 0.96 0.25\r\nvn -0.1 0.95 0.3\r\nvn -0.1 0.96 0.24\r\nvn -0.1 0.98 0.2\r\nvn -0.14 0.97 0.21\r\nvn -0.05 0.97 0.24\r\nvn -0.05 0.96 0.29\r\nvn -0.05 0.98 0.2\r\nvn -0.05 0.99 0.12\r\nvn -0.05 0.99 0.16\r\nvn -0.09 0.99 0.13\r\nvn -0.1 0.98 0.16\r\nvn -0.14 0.98 0.17\r\nvn -0.14 0.98 0.13\r\nvn -0.53 0.85 0.05\r\nvn -0.53 0.85 0.03\r\nvn -0.56 0.83 0.03\r\nvn -0.56 0.83 0.06\r\nvn -0.52 0.85 0\r\nvn -0.56 0.83 0\r\nvn -0.53 0.84 0.1\r\nvn -0.53 0.85 0.08\r\nvn -0.56 0.82 0.08\r\nvn -0.56 0.82 0.1\r\nvn -0.39 0.92 0.08\r\nvn -0.39 0.92 0.06\r\nvn -0.46 0.89 0.07\r\nvn -0.46 0.88 0.09\r\nvn -0.39 0.92 0.04\r\nvn -0.46 0.89 0.04\r\nvn -0.39 0.92 -0.01\r\nvn -0.45 0.89 -0\r\nvn -0.46 0.89 0.02\r\nvn -0.39 0.92 0.02\r\nvn -0.33 0.94 0.07\r\nvn -0.33 0.94 0.05\r\nvn -0.27 0.96 0.06\r\nvn -0.27 0.96 0.05\r\nvn -0.27 0.96 0.03\r\nvn -0.33 0.94 0.03\r\nvn -0.18 0.98 0.05\r\nvn -0.18 0.98 0.04\r\nvn -0.22 0.97 0.04\r\nvn -0.22 0.97 0.06\r\nvn -0.17 0.98 0.02\r\nvn -0.22 0.98 0.02\r\nvn -0.17 0.98 -0.02\r\nvn -0.22 0.98 -0.01\r\nvn -0.22 0.98 0\r\nvn -0.17 0.98 0\r\nvn -0.27 0.96 -0.01\r\nvn -0.27 0.96 0.01\r\nvn -0.33 0.95 0.01\r\nvn -0.33 0.95 -0.01\r\nvn -0.13 0.99 -0\r\nvn -0.13 0.99 -0.02\r\nvn -0.13 0.99 0.02\r\nvn -0.09 1 0.01\r\nvn -0.09 1 -0\r\nvn -0.09 1 -0.02\r\nvn -0.13 0.99 0.05\r\nvn -0.13 0.99 0.03\r\nvn -0.09 1 0.04\r\nvn -0.09 1 0.03\r\nvn -0.04 1 0.03\r\nvn -0.04 1 0.04\r\nvn -0.04 1 0.01\r\nvn -0.04 1 -0.02\r\nvn -0.04 1 -0\r\nvn -0.53 0.83 0.17\r\nvn -0.56 0.81 0.18\r\nvn -0.53 0.84 0.14\r\nvn -0.56 0.82 0.15\r\nvn -0.4 0.91 0.15\r\nvn -0.46 0.87 0.16\r\nvn -0.39 0.91 0.12\r\nvn -0.46 0.88 0.13\r\nvn -0.46 0.88 0.11\r\nvn -0.39 0.91 0.1\r\nvn -0.53 0.84 0.12\r\nvn -0.56 0.82 0.13\r\nvn -0.33 0.93 0.14\r\nvn -0.28 0.95 0.13\r\nvn -0.28 0.96 0.1\r\nvn -0.33 0.94 0.11\r\nvn -0.18 0.98 0.11\r\nvn -0.23 0.97 0.12\r\nvn -0.18 0.98 0.09\r\nvn -0.23 0.97 0.1\r\nvn -0.22 0.97 0.07\r\nvn -0.18 0.98 0.07\r\nvn -0.28 0.96 0.08\r\nvn -0.33 0.94 0.09\r\nvn -0.14 0.98 0.11\r\nvn -0.09 0.99 0.1\r\nvn -0.09 0.99 0.08\r\nvn -0.14 0.99 0.08\r\nvn -0.05 0.99 0.1\r\nvn -0.05 1 0.08\r\nvn -0.04 1 0.06\r\nvn -0.09 0.99 0.06\r\nvn -0.13 0.99 0.06\r\nvn -0.52 0.85 -0.04\r\nvn -0.52 0.85 -0.06\r\nvn -0.56 0.83 -0.07\r\nvn -0.56 0.83 -0.04\r\nvn -0.52 0.85 -0.09\r\nvn -0.56 0.83 -0.09\r\nvn -0.52 0.85 -0.02\r\nvn -0.56 0.83 -0.02\r\nvn -0.39 0.92 -0.02\r\nvn -0.45 0.89 -0.02\r\nvn -0.39 0.92 -0.04\r\nvn -0.45 0.89 -0.04\r\nvn -0.38 0.92 -0.08\r\nvn -0.45 0.89 -0.08\r\nvn -0.45 0.89 -0.06\r\nvn -0.39 0.92 -0.06\r\nvn -0.32 0.94 -0.06\r\nvn -0.32 0.94 -0.08\r\nvn -0.32 0.95 -0.04\r\nvn -0.27 0.96 -0.05\r\nvn -0.27 0.96 -0.06\r\nvn -0.27 0.96 -0.08\r\nvn -0.32 0.95 -0.03\r\nvn -0.27 0.96 -0.03\r\nvn -0.17 0.98 -0.03\r\nvn -0.22 0.98 -0.03\r\nvn -0.17 0.98 -0.05\r\nvn -0.22 0.97 -0.05\r\nvn -0.17 0.98 -0.07\r\nvn -0.22 0.97 -0.07\r\nvn -0.22 0.97 -0.06\r\nvn -0.17 0.98 -0.06\r\nvn -0.13 0.99 -0.06\r\nvn -0.13 0.99 -0.07\r\nvn -0.13 0.99 -0.05\r\nvn -0.09 1 -0.05\r\nvn -0.09 0.99 -0.06\r\nvn -0.09 0.99 -0.07\r\nvn -0.13 0.99 -0.03\r\nvn -0.09 1 -0.03\r\nvn -0.04 1 -0.03\r\nvn -0.04 1 -0.05\r\nvn -0.04 1 -0.07\r\nvn -0.04 1 -0.06\r\nvn -0.52 0.85 -0.11\r\nvn -0.55 0.82 -0.11\r\nvn -0.52 0.84 -0.14\r\nvn -0.55 0.82 -0.14\r\nvn -0.38 0.92 -0.1\r\nvn -0.45 0.89 -0.11\r\nvn -0.38 0.91 -0.13\r\nvn -0.45 0.88 -0.13\r\nvn -0.45 0.88 -0.16\r\nvn -0.38 0.91 -0.16\r\nvn -0.51 0.84 -0.17\r\nvn -0.55 0.82 -0.17\r\nvn -0.32 0.93 -0.15\r\nvn -0.32 0.94 -0.12\r\nvn -0.27 0.96 -0.12\r\nvn -0.27 0.95 -0.14\r\nvn -0.32 0.94 -0.1\r\nvn -0.27 0.96 -0.09\r\nvn -0.17 0.98 -0.09\r\nvn -0.22 0.97 -0.09\r\nvn -0.17 0.98 -0.11\r\nvn -0.22 0.97 -0.11\r\nvn -0.22 0.97 -0.13\r\nvn -0.18 0.98 -0.13\r\nvn -0.13 0.98 -0.12\r\nvn -0.13 0.98 -0.15\r\nvn -0.13 0.99 -0.1\r\nvn -0.09 0.99 -0.1\r\nvn -0.09 0.99 -0.12\r\nvn -0.09 0.99 -0.14\r\nvn -0.13 0.99 -0.08\r\nvn -0.09 0.99 -0.08\r\nvn -0.04 1 -0.08\r\nvn -0.04 0.99 -0.1\r\nvn -0.04 0.99 -0.14\r\nvn -0.04 0.99 -0.12\r\nvn -0.06 0.87 0.49\r\nvn -0.05 0.91 0.41\r\nvn -0.11 0.9 0.41\r\nvn -0.11 0.86 0.49\r\nvn -0.05 0.94 0.35\r\nvn -0.1 0.93 0.35\r\nvn -0.15 0.92 0.36\r\nvn -0.16 0.89 0.42\r\nvn -0.16 0.86 0.49\r\nvn -0.05 0.98 -0.17\r\nvn -0.05 0.98 -0.21\r\nvn -0.09 0.97 -0.21\r\nvn -0.09 0.98 -0.17\r\nvn -0.05 0.97 -0.25\r\nvn -0.1 0.96 -0.26\r\nvn -0.14 0.95 -0.26\r\nvn -0.14 0.97 -0.22\r\nvn -0.14 0.97 -0.18\r\nvn 0.31 0.43 0.85\r\nvn 0.36 0.42 0.83\r\nvn 0.41 0.41 0.81\r\nvn 0.46 0.4 0.79\r\nvn 0.51 0.39 0.76\r\nvn 0.55 0.38 0.74\r\nvn 0.59 0.42 0.69\r\nvn 0.62 0.41 0.67\r\nvn 0 0.44 0.9\r\nvn 0.09 0.44 0.89\r\nvn 0.17 0.44 0.88\r\nvn 0.24 0.43 0.87\r\nvn -0.51 0.39 0.76\r\nvn -0.47 0.4 0.79\r\nvn -0.41 0.41 0.81\r\nvn -0.36 0.42 0.83\r\nvn -0.31 0.43 0.85\r\nvn -0.63 0.36 0.69\r\nvn -0.6 0.37 0.71\r\nvn -0.56 0.38 0.73\r\nvn -0.24 0.43 0.87\r\nvn -0.17 0.44 0.88\r\nvn -0.09 0.44 0.89\r\nvt 0.7525 0.3098\r\nvt 0.7546 0.2931\r\nvt 0.771 0.3028\r\nvt 0.7687 0.3194\r\nvt 0.7565 0.2767\r\nvt 0.773 0.2863\r\nvt 0.7064 0.2877\r\nvt 0.7082 0.2709\r\nvt 0.732 0.2815\r\nvt 0.73 0.2982\r\nvt 0.7099 0.2546\r\nvt 0.7338 0.2651\r\nvt 0.7131 0.2229\r\nvt 0.7372 0.233\r\nvt 0.7355 0.249\r\nvt 0.7115 0.2386\r\nvt 0.7601 0.2442\r\nvt 0.7583 0.2604\r\nvt 0.7749 0.2699\r\nvt 0.7767 0.2535\r\nvt 0.6863 0.4476\r\nvt 0.6646 0.4442\r\nvt 0.6665 0.4211\r\nvt 0.6887 0.4256\r\nvt 0.642 0.4417\r\nvt 0.6436 0.4176\r\nvt 0.6454 0.394\r\nvt 0.6687 0.3984\r\nvt 0.6913 0.4039\r\nvt 0.5953 0.4386\r\nvt 0.5963 0.4129\r\nvt 0.6201 0.4149\r\nvt 0.6189 0.4399\r\nvt 0.5974 0.3877\r\nvt 0.6216 0.3905\r\nvt 0.5999 0.3397\r\nvt 0.6248 0.3438\r\nvt 0.6231 0.3667\r\nvt 0.5986 0.3632\r\nvt 0.6494 0.3489\r\nvt 0.6474 0.371\r\nvt 0.6969 0.3622\r\nvt 0.6941 0.3828\r\nvt 0.6711 0.3763\r\nvt 0.6735 0.355\r\nvt 0.6826 0.5322\r\nvt 0.6621 0.5338\r\nvt 0.6615 0.5141\r\nvt 0.682 0.5137\r\nvt 0.6399 0.5148\r\nvt 0.6405 0.5355\r\nvt 0.64 0.4906\r\nvt 0.6619 0.4909\r\nvt 0.6827 0.4918\r\nvt 0.5943 0.5164\r\nvt 0.6174 0.5156\r\nvt 0.618 0.5371\r\nvt 0.5948 0.5386\r\nvt 0.5943 0.4907\r\nvt 0.6174 0.4905\r\nvt 0.618 0.4652\r\nvt 0.5947 0.4647\r\nvt 0.6408 0.4661\r\nvt 0.6842 0.4697\r\nvt 0.6629 0.4676\r\nvt 0.6819 0.2784\r\nvt 0.6835 0.2614\r\nvt 0.6567 0.2703\r\nvt 0.6581 0.2531\r\nvt 0.6595 0.2368\r\nvt 0.6851 0.2451\r\nvt 0.6049 0.2575\r\nvt 0.6059 0.24\r\nvt 0.6322 0.246\r\nvt 0.6309 0.2633\r\nvt 0.6068 0.2235\r\nvt 0.6333 0.2295\r\nvt 0.6086 0.1927\r\nvt 0.6355 0.1986\r\nvt 0.6344 0.2138\r\nvt 0.6077 0.2078\r\nvt 0.6621 0.2056\r\nvt 0.6608 0.221\r\nvt 0.6865 0.2293\r\nvt 0.688 0.2137\r\nvt 0.7069 0.4519\r\nvt 0.7042 0.4726\r\nvt 0.7264 0.4575\r\nvt 0.723 0.4768\r\nvt 0.7202 0.4958\r\nvt 0.7022 0.4933\r\nvt 0.7352 0.481\r\nvt 0.7314 0.4987\r\nvt 0.7395 0.4628\r\nvt 0.7184 0.5144\r\nvt 0.7281 0.5155\r\nvt 0.7285 0.5287\r\nvt 0.7185 0.5294\r\nvt 0.7011 0.5137\r\nvt 0.7016 0.5307\r\nvt 0.7277 0.3155\r\nvt 0.7043 0.3053\r\nvt 0.7501 0.3268\r\nvt 0.7473 0.3442\r\nvt 0.7252 0.3333\r\nvt 0.702 0.3235\r\nvt 0.7661 0.3363\r\nvt 0.7631 0.3534\r\nvt 0.7409 0.3805\r\nvt 0.7442 0.3621\r\nvt 0.7598 0.3709\r\nvt 0.7562 0.3887\r\nvt 0.6995 0.3425\r\nvt 0.7224 0.3517\r\nvt 0.7194 0.3707\r\nvt 0.7293 0.118\r\nvt 0.7026 0.1096\r\nvt 0.706 0.0936\r\nvt 0.7331 0.1022\r\nvt 0.6747 0.102\r\nvt 0.6777 0.086\r\nvt 0.6813 0.0668\r\nvt 0.71 0.0746\r\nvt 0.7375 0.0834\r\nvt 0.6171 0.0895\r\nvt 0.6192 0.0735\r\nvt 0.6487 0.0793\r\nvt 0.6461 0.0953\r\nvt 0.6217 0.0544\r\nvt 0.6517 0.0601\r\nvt 0.6266 0.0129\r\nvt 0.6577 0.0186\r\nvt 0.6549 0.0394\r\nvt 0.6243 0.0337\r\nvt 0.685 0.0461\r\nvt 0.6883 0.0254\r\nvt 0.7422 0.063\r\nvt 0.7142 0.054\r\nvt 0.718 0.0334\r\nvt 0.7468 0.0427\r\nvt 0.7786 0.1378\r\nvt 0.783 0.1224\r\nvt 0.8007 0.1314\r\nvt 0.796 0.1466\r\nvt 0.7883 0.104\r\nvt 0.8063 0.1132\r\nvt 0.7588 0.1119\r\nvt 0.7547 0.1275\r\nvt 0.7636 0.0932\r\nvt 0.7738 0.053\r\nvt 0.7688 0.073\r\nvt 0.7938 0.0842\r\nvt 0.7993 0.0645\r\nvt 0.8122 0.0936\r\nvt 0.8178 0.0741\r\nvt 0.5716 0.4377\r\nvt 0.5723 0.4114\r\nvt 0.5477 0.4372\r\nvt 0.5482 0.4104\r\nvt 0.5487 0.3842\r\nvt 0.5731 0.3856\r\nvt 0.4997 0.4368\r\nvt 0.4997 0.4097\r\nvt 0.524 0.4098\r\nvt 0.5237 0.4368\r\nvt 0.4997 0.3831\r\nvt 0.5242 0.3834\r\nvt 0.4997 0.3325\r\nvt 0.5248 0.333\r\nvt 0.5245 0.3577\r\nvt 0.4997 0.3573\r\nvt 0.5499 0.3343\r\nvt 0.5493 0.3588\r\nvt 0.574 0.3606\r\nvt 0.575 0.3366\r\nvt 0.5713 0.5398\r\nvt 0.571 0.5172\r\nvt 0.5473 0.5178\r\nvt 0.5476 0.5407\r\nvt 0.5473 0.4912\r\nvt 0.5709 0.4909\r\nvt 0.4997 0.5183\r\nvt 0.5236 0.5182\r\nvt 0.5237 0.5413\r\nvt 0.4997 0.5415\r\nvt 0.4997 0.4914\r\nvt 0.5235 0.4914\r\nvt 0.5236 0.4641\r\nvt 0.4997 0.4641\r\nvt 0.5474 0.4642\r\nvt 0.5711 0.4643\r\nvt 0.5787 0.2528\r\nvt 0.5795 0.2352\r\nvt 0.5524 0.2495\r\nvt 0.5529 0.2317\r\nvt 0.5534 0.2151\r\nvt 0.5802 0.2187\r\nvt 0.4997 0.2467\r\nvt 0.4997 0.2288\r\nvt 0.5263 0.2296\r\nvt 0.5261 0.2474\r\nvt 0.4997 0.2121\r\nvt 0.5266 0.2129\r\nvt 0.4997 0.1811\r\nvt 0.5271 0.1819\r\nvt 0.5268 0.1971\r\nvt 0.4997 0.1963\r\nvt 0.5544 0.1841\r\nvt 0.5539 0.1993\r\nvt 0.5809 0.2029\r\nvt 0.5816 0.1878\r\nvt 0.5879 0.0845\r\nvt 0.5895 0.0686\r\nvt 0.5586 0.0806\r\nvt 0.5596 0.0648\r\nvt 0.5609 0.0461\r\nvt 0.5914 0.0496\r\nvt 0.4997 0.0771\r\nvt 0.4997 0.0615\r\nvt 0.5297 0.0624\r\nvt 0.5292 0.078\r\nvt 0.4997 0.043\r\nvt 0.5303 0.0438\r\nvt 0.4997 0.0024\r\nvt 0.5316 0.0031\r\nvt 0.531 0.0236\r\nvt 0.4997 0.0228\r\nvt 0.5623 0.0257\r\nvt 0.5634 0.0051\r\nvt 0.5934 0.0291\r\nvt 0.5951 0.0084\r\nvt 0.7674 0.187\r\nvt 0.7686 0.1787\r\nvt 0.7854 0.187\r\nvt 0.7842 0.1954\r\nvt 0.7695 0.1727\r\nvt 0.7864 0.1811\r\nvt 0.7196 0.1679\r\nvt 0.7206 0.1598\r\nvt 0.7452 0.1688\r\nvt 0.7441 0.177\r\nvt 0.7215 0.1539\r\nvt 0.7462 0.1629\r\nvt 0.7229 0.1457\r\nvt 0.7477 0.1547\r\nvt 0.7469 0.1584\r\nvt 0.7222 0.1495\r\nvt 0.7712 0.1647\r\nvt 0.7704 0.1683\r\nvt 0.7873 0.1767\r\nvt 0.7881 0.1731\r\nvt 0.6939 0.1599\r\nvt 0.6948 0.1518\r\nvt 0.6672 0.1527\r\nvt 0.668 0.1448\r\nvt 0.6687 0.1389\r\nvt 0.6956 0.146\r\nvt 0.6121 0.1408\r\nvt 0.6127 0.1329\r\nvt 0.6406 0.1385\r\nvt 0.6399 0.1463\r\nvt 0.6131 0.1271\r\nvt 0.6411 0.1326\r\nvt 0.6137 0.1184\r\nvt 0.642 0.124\r\nvt 0.6415 0.128\r\nvt 0.6134 0.1224\r\nvt 0.6698 0.1304\r\nvt 0.6692 0.1343\r\nvt 0.6962 0.1415\r\nvt 0.6968 0.1376\r\nvt 0.6444 0.1067\r\nvt 0.6157 0.1009\r\nvt 0.6726 0.1133\r\nvt 0.6713 0.121\r\nvt 0.6432 0.1145\r\nvt 0.6147 0.1087\r\nvt 0.7267 0.1291\r\nvt 0.7002 0.1208\r\nvt 0.7249 0.1367\r\nvt 0.6986 0.1284\r\nvt 0.6975 0.1336\r\nvt 0.7237 0.1418\r\nvt 0.6704 0.1263\r\nvt 0.6141 0.1142\r\nvt 0.6425 0.1199\r\nvt 0.7518 0.1384\r\nvt 0.7755 0.1486\r\nvt 0.7735 0.156\r\nvt 0.7499 0.1458\r\nvt 0.7927 0.1573\r\nvt 0.7905 0.1645\r\nvt 0.7721 0.1609\r\nvt 0.7891 0.1694\r\nvt 0.7486 0.1509\r\nvt 0.5842 0.136\r\nvt 0.5846 0.1281\r\nvt 0.5561 0.1322\r\nvt 0.5564 0.1243\r\nvt 0.5566 0.1183\r\nvt 0.5849 0.1222\r\nvt 0.4997 0.1287\r\nvt 0.4997 0.1207\r\nvt 0.5281 0.1217\r\nvt 0.5279 0.1296\r\nvt 0.4997 0.1147\r\nvt 0.5282 0.1157\r\nvt 0.4997 0.1058\r\nvt 0.5283 0.1068\r\nvt 0.5282 0.111\r\nvt 0.4997 0.11\r\nvt 0.5569 0.1094\r\nvt 0.5567 0.1136\r\nvt 0.5851 0.1175\r\nvt 0.5854 0.1134\r\nvt 0.6295 0.2817\r\nvt 0.6037 0.2762\r\nvt 0.655 0.2884\r\nvt 0.6532 0.3076\r\nvt 0.628 0.3013\r\nvt 0.6025 0.2962\r\nvt 0.68 0.2963\r\nvt 0.678 0.315\r\nvt 0.6758 0.3345\r\nvt 0.6513 0.3277\r\nvt 0.6012 0.3174\r\nvt 0.6264 0.322\r\nvt 0.73 0.4381\r\nvt 0.7438 0.4442\r\nvt 0.7337 0.4186\r\nvt 0.7481 0.4256\r\nvt 0.7099 0.4312\r\nvt 0.7131 0.4106\r\nvt 0.7163 0.3904\r\nvt 0.7374 0.3994\r\nvt 0.7522 0.407\r\nvt 0.7426 0.1883\r\nvt 0.7182 0.179\r\nvt 0.7658 0.1985\r\nvt 0.764 0.2124\r\nvt 0.7409 0.2019\r\nvt 0.7166 0.1923\r\nvt 0.7826 0.2071\r\nvt 0.7807 0.2213\r\nvt 0.7621 0.2279\r\nvt 0.7787 0.237\r\nvt 0.7148 0.2072\r\nvt 0.739 0.2171\r\nvt 0.639 0.1569\r\nvt 0.6114 0.1513\r\nvt 0.6661 0.1634\r\nvt 0.6648 0.1762\r\nvt 0.6379 0.1695\r\nvt 0.6105 0.1638\r\nvt 0.6926 0.1707\r\nvt 0.6911 0.1838\r\nvt 0.6896 0.1983\r\nvt 0.6634 0.1905\r\nvt 0.6096 0.1778\r\nvt 0.6367 0.1836\r\nvt 0.5288 0.0892\r\nvt 0.4997 0.0882\r\nvt 0.5578 0.0918\r\nvt 0.5574 0.0996\r\nvt 0.5286 0.097\r\nvt 0.4997 0.096\r\nvt 0.5868 0.0958\r\nvt 0.5861 0.1037\r\nvt 0.5856 0.1091\r\nvt 0.5571 0.1051\r\nvt 0.4997 0.1015\r\nvt 0.5284 0.1025\r\nvt 0.5258 0.2667\r\nvt 0.4997 0.2661\r\nvt 0.5518 0.2687\r\nvt 0.5512 0.2892\r\nvt 0.5255 0.2875\r\nvt 0.4997 0.2869\r\nvt 0.5778 0.2718\r\nvt 0.5769 0.2921\r\nvt 0.576 0.3137\r\nvt 0.5506 0.3111\r\nvt 0.4997 0.309\r\nvt 0.5252 0.3096\r\nvt 0.5278 0.1402\r\nvt 0.4997 0.1393\r\nvt 0.5557 0.1427\r\nvt 0.5553 0.1552\r\nvt 0.5275 0.1528\r\nvt 0.4997 0.152\r\nvt 0.5836 0.1464\r\nvt 0.583 0.159\r\nvt 0.5823 0.1729\r\nvt 0.5548 0.1693\r\nvt 0.4997 0.1661\r\nvt 0.5273 0.1669\r\nvt 0.2429 0.2767\r\nvt 0.2448 0.2931\r\nvt 0.2285 0.3028\r\nvt 0.2265 0.2863\r\nvt 0.2469 0.3098\r\nvt 0.2307 0.3194\r\nvt 0.2393 0.2442\r\nvt 0.2411 0.2604\r\nvt 0.2246 0.2699\r\nvt 0.2227 0.2535\r\nvt 0.2864 0.2229\r\nvt 0.288 0.2386\r\nvt 0.264 0.249\r\nvt 0.2623 0.2331\r\nvt 0.2896 0.2546\r\nvt 0.2657 0.2651\r\nvt 0.2931 0.2878\r\nvt 0.2695 0.2982\r\nvt 0.2675 0.2815\r\nvt 0.2912 0.2709\r\nvt 0.3132 0.4476\r\nvt 0.3108 0.4256\r\nvt 0.3329 0.4211\r\nvt 0.3349 0.4442\r\nvt 0.3081 0.404\r\nvt 0.3307 0.3985\r\nvt 0.3541 0.394\r\nvt 0.3559 0.4176\r\nvt 0.3574 0.4417\r\nvt 0.3026 0.3623\r\nvt 0.326 0.355\r\nvt 0.3284 0.3764\r\nvt 0.3054 0.3828\r\nvt 0.3501 0.3489\r\nvt 0.3521 0.371\r\nvt 0.3995 0.3397\r\nvt 0.4008 0.3633\r\nvt 0.3763 0.3667\r\nvt 0.3746 0.3438\r\nvt 0.4021 0.3877\r\nvt 0.3779 0.3905\r\nvt 0.4041 0.4387\r\nvt 0.3806 0.4399\r\nvt 0.3793 0.4149\r\nvt 0.4032 0.4129\r\nvt 0.3174 0.5137\r\nvt 0.3379 0.5141\r\nvt 0.3373 0.5338\r\nvt 0.3169 0.5322\r\nvt 0.3168 0.4918\r\nvt 0.3376 0.491\r\nvt 0.3594 0.4906\r\nvt 0.3596 0.5148\r\nvt 0.359 0.5355\r\nvt 0.3365 0.4676\r\nvt 0.3153 0.4697\r\nvt 0.3587 0.4661\r\nvt 0.4048 0.4647\r\nvt 0.3815 0.4652\r\nvt 0.4052 0.4907\r\nvt 0.382 0.4906\r\nvt 0.4046 0.5386\r\nvt 0.3815 0.5371\r\nvt 0.3821 0.5156\r\nvt 0.4051 0.5165\r\nvt 0.3159 0.2614\r\nvt 0.3176 0.2784\r\nvt 0.3144 0.2451\r\nvt 0.34 0.2368\r\nvt 0.3413 0.2531\r\nvt 0.3428 0.2703\r\nvt 0.3115 0.2137\r\nvt 0.3129 0.2293\r\nvt 0.3374 0.2056\r\nvt 0.3387 0.221\r\nvt 0.3908 0.1927\r\nvt 0.3917 0.2078\r\nvt 0.365 0.2138\r\nvt 0.3639 0.1986\r\nvt 0.3926 0.2235\r\nvt 0.3661 0.2296\r\nvt 0.3946 0.2575\r\nvt 0.3685 0.2633\r\nvt 0.3673 0.246\r\nvt 0.3936 0.24\r\nvt 0.2952 0.4726\r\nvt 0.2925 0.4519\r\nvt 0.2973 0.4933\r\nvt 0.2793 0.4959\r\nvt 0.2765 0.4769\r\nvt 0.273 0.4576\r\nvt 0.2979 0.5307\r\nvt 0.2983 0.5137\r\nvt 0.2811 0.5144\r\nvt 0.281 0.5294\r\nvt 0.2713 0.5155\r\nvt 0.2681 0.4987\r\nvt 0.271 0.5287\r\nvt 0.2642 0.4811\r\nvt 0.26 0.4628\r\nvt 0.2951 0.3053\r\nvt 0.2717 0.3155\r\nvt 0.2975 0.3235\r\nvt 0.2743 0.3333\r\nvt 0.2522 0.3443\r\nvt 0.2494 0.3268\r\nvt 0.28 0.3707\r\nvt 0.2771 0.3517\r\nvt 0.2999 0.3425\r\nvt 0.2585 0.3805\r\nvt 0.2552 0.3621\r\nvt 0.2396 0.3709\r\nvt 0.2363 0.3534\r\nvt 0.2433 0.3888\r\nvt 0.2334 0.3363\r\nvt 0.2701 0.118\r\nvt 0.2664 0.1023\r\nvt 0.2935 0.0936\r\nvt 0.2969 0.1096\r\nvt 0.2619 0.0834\r\nvt 0.2895 0.0746\r\nvt 0.3182 0.0668\r\nvt 0.3217 0.086\r\nvt 0.3247 0.102\r\nvt 0.2527 0.0427\r\nvt 0.2814 0.0334\r\nvt 0.2852 0.054\r\nvt 0.2572 0.063\r\nvt 0.3144 0.0462\r\nvt 0.3111 0.0254\r\nvt 0.3752 0.0337\r\nvt 0.3445 0.0394\r\nvt 0.3417 0.0186\r\nvt 0.3729 0.0129\r\nvt 0.3778 0.0544\r\nvt 0.3477 0.0601\r\nvt 0.3823 0.0895\r\nvt 0.3533 0.0954\r\nvt 0.3507 0.0793\r\nvt 0.3802 0.0735\r\nvt 0.2112 0.104\r\nvt 0.2164 0.1225\r\nvt 0.1988 0.1314\r\nvt 0.1932 0.1132\r\nvt 0.2208 0.1379\r\nvt 0.2035 0.1466\r\nvt 0.2056 0.0842\r\nvt 0.1872 0.0937\r\nvt 0.1817 0.0741\r\nvt 0.2002 0.0645\r\nvt 0.2307 0.0731\r\nvt 0.2257 0.0531\r\nvt 0.2358 0.0932\r\nvt 0.2448 0.1275\r\nvt 0.2407 0.1119\r\nvt 0.4272 0.4114\r\nvt 0.4279 0.4377\r\nvt 0.4264 0.3856\r\nvt 0.4508 0.3842\r\nvt 0.4513 0.4104\r\nvt 0.4517 0.4372\r\nvt 0.4245 0.3366\r\nvt 0.4254 0.3606\r\nvt 0.4495 0.3343\r\nvt 0.4502 0.3588\r\nvt 0.4749 0.3577\r\nvt 0.4746 0.333\r\nvt 0.4752 0.3834\r\nvt 0.4757 0.4368\r\nvt 0.4755 0.4098\r\nvt 0.4285 0.5172\r\nvt 0.4281 0.5398\r\nvt 0.4286 0.4909\r\nvt 0.4522 0.4912\r\nvt 0.4521 0.5178\r\nvt 0.4519 0.5407\r\nvt 0.4283 0.4644\r\nvt 0.452 0.4642\r\nvt 0.4759 0.4641\r\nvt 0.4759 0.4914\r\nvt 0.4758 0.5413\r\nvt 0.4759 0.5182\r\nvt 0.42 0.2352\r\nvt 0.4208 0.2529\r\nvt 0.4193 0.2187\r\nvt 0.446 0.2151\r\nvt 0.4465 0.2317\r\nvt 0.447 0.2495\r\nvt 0.4179 0.1878\r\nvt 0.4186 0.203\r\nvt 0.4451 0.1842\r\nvt 0.4456 0.1993\r\nvt 0.4726 0.1971\r\nvt 0.4724 0.1819\r\nvt 0.4729 0.2129\r\nvt 0.4734 0.2474\r\nvt 0.4731 0.2296\r\nvt 0.41 0.0686\r\nvt 0.4116 0.0845\r\nvt 0.4081 0.0496\r\nvt 0.4385 0.0461\r\nvt 0.4398 0.0649\r\nvt 0.4409 0.0806\r\nvt 0.4043 0.0084\r\nvt 0.4061 0.0291\r\nvt 0.4372 0.0257\r\nvt 0.436 0.0051\r\nvt 0.4684 0.0236\r\nvt 0.4678 0.0031\r\nvt 0.4691 0.0438\r\nvt 0.4703 0.078\r\nvt 0.4697 0.0624\r\nvt 0.2299 0.1728\r\nvt 0.2309 0.1787\r\nvt 0.214 0.1871\r\nvt 0.213 0.1811\r\nvt 0.2321 0.187\r\nvt 0.2153 0.1955\r\nvt 0.2283 0.1647\r\nvt 0.2291 0.1684\r\nvt 0.2122 0.1767\r\nvt 0.2113 0.1731\r\nvt 0.2765 0.1457\r\nvt 0.2772 0.1495\r\nvt 0.2525 0.1585\r\nvt 0.2517 0.1547\r\nvt 0.278 0.154\r\nvt 0.2533 0.1629\r\nvt 0.2799 0.168\r\nvt 0.2554 0.177\r\nvt 0.2542 0.1688\r\nvt 0.2788 0.1599\r\nvt 0.3046 0.1519\r\nvt 0.3056 0.1599\r\nvt 0.3039 0.146\r\nvt 0.3308 0.1389\r\nvt 0.3314 0.1448\r\nvt 0.3322 0.1527\r\nvt 0.3026 0.1376\r\nvt 0.3032 0.1415\r\nvt 0.3297 0.1304\r\nvt 0.3302 0.1343\r\nvt 0.3857 0.1184\r\nvt 0.386 0.1224\r\nvt 0.3579 0.128\r\nvt 0.3575 0.124\r\nvt 0.3864 0.1271\r\nvt 0.3583 0.1326\r\nvt 0.3873 0.1408\r\nvt 0.3596 0.1464\r\nvt 0.3589 0.1385\r\nvt 0.3868 0.133\r\nvt 0.3838 0.1009\r\nvt 0.3551 0.1067\r\nvt 0.3847 0.1087\r\nvt 0.3562 0.1145\r\nvt 0.3282 0.1211\r\nvt 0.3268 0.1133\r\nvt 0.357 0.1199\r\nvt 0.3853 0.1142\r\nvt 0.3291 0.1264\r\nvt 0.2757 0.1418\r\nvt 0.3019 0.1337\r\nvt 0.2746 0.1367\r\nvt 0.3009 0.1284\r\nvt 0.2993 0.1208\r\nvt 0.2728 0.1291\r\nvt 0.2477 0.1384\r\nvt 0.2496 0.1459\r\nvt 0.226 0.156\r\nvt 0.2239 0.1487\r\nvt 0.2508 0.1509\r\nvt 0.2273 0.161\r\nvt 0.2103 0.1694\r\nvt 0.2089 0.1645\r\nvt 0.2067 0.1573\r\nvt 0.4149 0.1281\r\nvt 0.4153 0.136\r\nvt 0.4146 0.1222\r\nvt 0.4429 0.1183\r\nvt 0.4431 0.1243\r\nvt 0.4433 0.1322\r\nvt 0.4141 0.1134\r\nvt 0.4143 0.1175\r\nvt 0.4426 0.1094\r\nvt 0.4427 0.1136\r\nvt 0.4712 0.111\r\nvt 0.4711 0.1068\r\nvt 0.4713 0.1157\r\nvt 0.4715 0.1296\r\nvt 0.4714 0.1217\r\nvt 0.3957 0.2762\r\nvt 0.3699 0.2817\r\nvt 0.3969 0.2962\r\nvt 0.3714 0.3013\r\nvt 0.3462 0.3076\r\nvt 0.3444 0.2884\r\nvt 0.373 0.322\r\nvt 0.3982 0.3174\r\nvt 0.3481 0.3277\r\nvt 0.3237 0.3345\r\nvt 0.3215 0.315\r\nvt 0.3194 0.2963\r\nvt 0.2657 0.4186\r\nvt 0.2694 0.4381\r\nvt 0.2557 0.4442\r\nvt 0.2514 0.4256\r\nvt 0.2621 0.3994\r\nvt 0.2472 0.4071\r\nvt 0.2832 0.3904\r\nvt 0.2864 0.4106\r\nvt 0.2895 0.4312\r\nvt 0.2813 0.179\r\nvt 0.2568 0.1883\r\nvt 0.2829 0.1924\r\nvt 0.2586 0.2019\r\nvt 0.2355 0.2125\r\nvt 0.2337 0.1986\r\nvt 0.2604 0.2171\r\nvt 0.2846 0.2072\r\nvt 0.2374 0.228\r\nvt 0.2207 0.237\r\nvt 0.2187 0.2213\r\nvt 0.2169 0.2072\r\nvt 0.388 0.1513\r\nvt 0.3605 0.1569\r\nvt 0.3889 0.1638\r\nvt 0.3616 0.1695\r\nvt 0.3346 0.1762\r\nvt 0.3333 0.1634\r\nvt 0.3627 0.1836\r\nvt 0.3898 0.1778\r\nvt 0.336 0.1905\r\nvt 0.3099 0.1984\r\nvt 0.3083 0.1838\r\nvt 0.3069 0.1707\r\nvt 0.4706 0.0892\r\nvt 0.4709 0.097\r\nvt 0.4421 0.0996\r\nvt 0.4416 0.0918\r\nvt 0.471 0.1025\r\nvt 0.4424 0.1051\r\nvt 0.4138 0.1091\r\nvt 0.4134 0.1037\r\nvt 0.4126 0.0958\r\nvt 0.4737 0.2667\r\nvt 0.474 0.2875\r\nvt 0.4482 0.2892\r\nvt 0.4476 0.2687\r\nvt 0.4743 0.3096\r\nvt 0.4489 0.3111\r\nvt 0.4235 0.3137\r\nvt 0.4225 0.2922\r\nvt 0.4216 0.2718\r\nvt 0.4717 0.1402\r\nvt 0.4719 0.1528\r\nvt 0.4441 0.1552\r\nvt 0.4437 0.1427\r\nvt 0.4721 0.1669\r\nvt 0.4446 0.1693\r\nvt 0.4172 0.1729\r\nvt 0.4164 0.159\r\nvt 0.4158 0.1465\r\nvt 0.6844 0.5562\r\nvt 0.6638 0.5589\r\nvt 0.6872 0.5766\r\nvt 0.6663 0.5803\r\nvt 0.6441 0.5837\r\nvt 0.642 0.5615\r\nvt 0.6945 0.6157\r\nvt 0.6725 0.6209\r\nvt 0.6693 0.601\r\nvt 0.6907 0.5964\r\nvt 0.6494 0.6254\r\nvt 0.6467 0.605\r\nvt 0.6008 0.6322\r\nvt 0.599 0.6112\r\nvt 0.6232 0.6084\r\nvt 0.6254 0.6291\r\nvt 0.5973 0.589\r\nvt 0.621 0.5866\r\nvt 0.6193 0.5638\r\nvt 0.5959 0.5658\r\nvt 0.7244 0.5676\r\nvt 0.7205 0.5501\r\nvt 0.7351 0.5463\r\nvt 0.7401 0.5615\r\nvt 0.7341 0.6026\r\nvt 0.7291 0.5851\r\nvt 0.7458 0.5776\r\nvt 0.7515 0.5942\r\nvt 0.7107 0.5913\r\nvt 0.7151 0.6096\r\nvt 0.7067 0.5725\r\nvt 0.7034 0.5533\r\nvt 0.7193 0.6276\r\nvt 0.6982 0.6342\r\nvt 0.7389 0.62\r\nvt 0.7433 0.6371\r\nvt 0.7232 0.6452\r\nvt 0.7016 0.6522\r\nvt 0.7569 0.611\r\nvt 0.7618 0.6278\r\nvt 0.7509 0.6702\r\nvt 0.7473 0.6539\r\nvt 0.7663 0.6444\r\nvt 0.7702 0.6607\r\nvt 0.7077 0.6859\r\nvt 0.7048 0.6694\r\nvt 0.7268 0.6622\r\nvt 0.73 0.6786\r\nvt 0.7294 0.9285\r\nvt 0.7344 0.9178\r\nvt 0.7518 0.9107\r\nvt 0.746 0.9214\r\nvt 0.7394 0.9062\r\nvt 0.7576 0.899\r\nvt 0.7228 0.9444\r\nvt 0.7252 0.9376\r\nvt 0.7406 0.9302\r\nvt 0.7376 0.9364\r\nvt 0.6851 0.9605\r\nvt 0.6871 0.9525\r\nvt 0.7072 0.9451\r\nvt 0.7051 0.9525\r\nvt 0.6903 0.9426\r\nvt 0.7108 0.9356\r\nvt 0.6981 0.9197\r\nvt 0.7196 0.9131\r\nvt 0.7151 0.9247\r\nvt 0.6941 0.9315\r\nvt 0.7106 0.8723\r\nvt 0.7333 0.8658\r\nvt 0.7306 0.8776\r\nvt 0.708 0.884\r\nvt 0.7545 0.8588\r\nvt 0.7515 0.8706\r\nvt 0.748 0.8825\r\nvt 0.7273 0.8894\r\nvt 0.7051 0.8958\r\nvt 0.7709 0.863\r\nvt 0.7671 0.8751\r\nvt 0.7741 0.8511\r\nvt 0.744 0.8944\r\nvt 0.7626 0.8871\r\nvt 0.7017 0.9078\r\nvt 0.7237 0.9013\r\nvt 0.6079 0.8915\r\nvt 0.6346 0.8878\r\nvt 0.6328 0.8996\r\nvt 0.6064 0.9033\r\nvt 0.6609 0.8833\r\nvt 0.6588 0.895\r\nvt 0.6564 0.907\r\nvt 0.6308 0.9116\r\nvt 0.6047 0.9155\r\nvt 0.684 0.8898\r\nvt 0.6863 0.8781\r\nvt 0.6813 0.9017\r\nvt 0.6751 0.9258\r\nvt 0.6783 0.9137\r\nvt 0.6509 0.9314\r\nvt 0.6538 0.9191\r\nvt 0.6008 0.9404\r\nvt 0.6028 0.9278\r\nvt 0.6285 0.9238\r\nvt 0.626 0.9363\r\nvt 0.6234 0.9488\r\nvt 0.5986 0.9531\r\nvt 0.6479 0.9436\r\nvt 0.6449 0.9554\r\nvt 0.6209 0.9609\r\nvt 0.5966 0.9655\r\nvt 0.6715 0.9378\r\nvt 0.6682 0.9493\r\nvt 0.6635 0.9681\r\nvt 0.6654 0.9596\r\nvt 0.6408 0.9752\r\nvt 0.6425 0.9661\r\nvt 0.5936 0.987\r\nvt 0.5948 0.977\r\nvt 0.6188 0.972\r\nvt 0.6174 0.9815\r\nvt 0.6757 0.6399\r\nvt 0.6787 0.6582\r\nvt 0.6547 0.6632\r\nvt 0.6521 0.6448\r\nvt 0.684 0.6921\r\nvt 0.6815 0.6756\r\nvt 0.6592 0.6974\r\nvt 0.657 0.6807\r\nvt 0.6073 0.7052\r\nvt 0.6059 0.6885\r\nvt 0.6318 0.685\r\nvt 0.6335 0.7017\r\nvt 0.6043 0.6708\r\nvt 0.6298 0.6674\r\nvt 0.6276 0.6488\r\nvt 0.6026 0.6521\r\nvt 0.57 0.9913\r\nvt 0.571 0.981\r\nvt 0.5466 0.9945\r\nvt 0.5472 0.9839\r\nvt 0.5481 0.9719\r\nvt 0.5723 0.9692\r\nvt 0.4998 0.9971\r\nvt 0.4998 0.9863\r\nvt 0.5235 0.9857\r\nvt 0.5231 0.9964\r\nvt 0.4998 0.9742\r\nvt 0.5239 0.9736\r\nvt 0.4998 0.9481\r\nvt 0.525 0.9476\r\nvt 0.5245 0.9607\r\nvt 0.4998 0.9612\r\nvt 0.5503 0.9461\r\nvt 0.5491 0.9592\r\nvt 0.5739 0.9566\r\nvt 0.5755 0.9437\r\nvt 0.5772 0.6546\r\nvt 0.5759 0.6346\r\nvt 0.5785 0.6734\r\nvt 0.5524 0.6753\r\nvt 0.5515 0.6564\r\nvt 0.5506 0.6363\r\nvt 0.5807 0.7079\r\nvt 0.5796 0.6912\r\nvt 0.5538 0.7099\r\nvt 0.5531 0.6931\r\nvt 0.4997 0.7114\r\nvt 0.4997 0.6946\r\nvt 0.5265 0.6942\r\nvt 0.5268 0.711\r\nvt 0.4997 0.6768\r\nvt 0.5261 0.6764\r\nvt 0.4997 0.6377\r\nvt 0.5252 0.6374\r\nvt 0.5257 0.6575\r\nvt 0.4997 0.6579\r\nvt 0.7629 0.7711\r\nvt 0.7626 0.7608\r\nvt 0.7829 0.7519\r\nvt 0.7832 0.7623\r\nvt 0.7621 0.7504\r\nvt 0.7824 0.7414\r\nvt 0.7177 0.786\r\nvt 0.7174 0.7759\r\nvt 0.7408 0.7688\r\nvt 0.7411 0.779\r\nvt 0.7169 0.7656\r\nvt 0.7402 0.7585\r\nvt 0.7151 0.7432\r\nvt 0.7383 0.736\r\nvt 0.7394 0.7477\r\nvt 0.7161 0.7549\r\nvt 0.76 0.7277\r\nvt 0.7612 0.7395\r\nvt 0.7814 0.7303\r\nvt 0.7801 0.7185\r\nvt 0.6914 0.761\r\nvt 0.6905 0.7494\r\nvt 0.6921 0.7718\r\nvt 0.6662 0.7771\r\nvt 0.6656 0.7663\r\nvt 0.6648 0.7547\r\nvt 0.6928 0.7921\r\nvt 0.6926 0.782\r\nvt 0.6668 0.7973\r\nvt 0.6665 0.7873\r\nvt 0.6123 0.8053\r\nvt 0.6121 0.7952\r\nvt 0.6396 0.7917\r\nvt 0.6398 0.8017\r\nvt 0.6119 0.785\r\nvt 0.6393 0.7814\r\nvt 0.611 0.7627\r\nvt 0.6382 0.7591\r\nvt 0.6388 0.7707\r\nvt 0.6115 0.7743\r\nvt 0.5844 0.8081\r\nvt 0.5843 0.798\r\nvt 0.5563 0.8101\r\nvt 0.5563 0.8001\r\nvt 0.5561 0.7898\r\nvt 0.5841 0.7878\r\nvt 0.4998 0.8118\r\nvt 0.4998 0.8017\r\nvt 0.528 0.8013\r\nvt 0.5281 0.8114\r\nvt 0.4998 0.7914\r\nvt 0.528 0.791\r\nvt 0.4997 0.7689\r\nvt 0.5278 0.7686\r\nvt 0.5279 0.7802\r\nvt 0.4998 0.7806\r\nvt 0.5557 0.7674\r\nvt 0.556 0.779\r\nvt 0.5838 0.777\r\nvt 0.5835 0.7654\r\nvt 0.7564 0.7008\r\nvt 0.7539 0.6859\r\nvt 0.7735 0.6763\r\nvt 0.7762 0.6913\r\nvt 0.7584 0.7148\r\nvt 0.7784 0.7054\r\nvt 0.7138 0.7304\r\nvt 0.7368 0.7231\r\nvt 0.7121 0.7165\r\nvt 0.735 0.7092\r\nvt 0.7327 0.6943\r\nvt 0.7101 0.7016\r\nvt 0.6861 0.7079\r\nvt 0.6879 0.7228\r\nvt 0.6625 0.7281\r\nvt 0.661 0.7132\r\nvt 0.6893 0.7367\r\nvt 0.6638 0.742\r\nvt 0.6103 0.7499\r\nvt 0.6373 0.7463\r\nvt 0.6095 0.736\r\nvt 0.6363 0.7325\r\nvt 0.635 0.7176\r\nvt 0.6085 0.7211\r\nvt 0.5816 0.7238\r\nvt 0.5823 0.7387\r\nvt 0.5549 0.7407\r\nvt 0.5544 0.7258\r\nvt 0.583 0.7526\r\nvt 0.5554 0.7546\r\nvt 0.4997 0.7561\r\nvt 0.5276 0.7558\r\nvt 0.4997 0.7422\r\nvt 0.5274 0.7418\r\nvt 0.5271 0.7269\r\nvt 0.4997 0.7273\r\nvt 0.7614 0.8135\r\nvt 0.7622 0.8027\r\nvt 0.7824 0.7943\r\nvt 0.7815 0.8053\r\nvt 0.7627 0.792\r\nvt 0.783 0.7834\r\nvt 0.7165 0.8277\r\nvt 0.7172 0.8171\r\nvt 0.7405 0.8103\r\nvt 0.7398 0.821\r\nvt 0.7176 0.8065\r\nvt 0.7409 0.7997\r\nvt 0.7411 0.7892\r\nvt 0.7177 0.7962\r\nvt 0.763 0.7814\r\nvt 0.7832 0.7728\r\nvt 0.6918 0.8337\r\nvt 0.6924 0.823\r\nvt 0.6658 0.8389\r\nvt 0.6663 0.8282\r\nvt 0.6667 0.8178\r\nvt 0.6927 0.8126\r\nvt 0.6115 0.8469\r\nvt 0.6119 0.8363\r\nvt 0.6394 0.8327\r\nvt 0.6389 0.8433\r\nvt 0.6122 0.8258\r\nvt 0.6397 0.8222\r\nvt 0.6398 0.8119\r\nvt 0.6123 0.8155\r\nvt 0.6668 0.8075\r\nvt 0.6929 0.8022\r\nvt 0.5838 0.8498\r\nvt 0.5841 0.8391\r\nvt 0.5559 0.8519\r\nvt 0.5561 0.8412\r\nvt 0.5563 0.8307\r\nvt 0.5843 0.8287\r\nvt 0.4998 0.8536\r\nvt 0.4998 0.8429\r\nvt 0.528 0.8425\r\nvt 0.5279 0.8531\r\nvt 0.4998 0.8324\r\nvt 0.528 0.832\r\nvt 0.5281 0.8216\r\nvt 0.4998 0.822\r\nvt 0.5563 0.8203\r\nvt 0.5844 0.8183\r\nvt 0.7588 0.8358\r\nvt 0.7603 0.8246\r\nvt 0.7803 0.8165\r\nvt 0.7787 0.8278\r\nvt 0.7569 0.8472\r\nvt 0.7767 0.8394\r\nvt 0.7126 0.8609\r\nvt 0.7356 0.8543\r\nvt 0.7143 0.8496\r\nvt 0.7373 0.843\r\nvt 0.7387 0.8319\r\nvt 0.7156 0.8386\r\nvt 0.6882 0.8667\r\nvt 0.6626 0.8719\r\nvt 0.6639 0.8606\r\nvt 0.6897 0.8555\r\nvt 0.6091 0.88\r\nvt 0.6361 0.8763\r\nvt 0.6101 0.8687\r\nvt 0.6373 0.8651\r\nvt 0.6382 0.8541\r\nvt 0.6109 0.8577\r\nvt 0.665 0.8497\r\nvt 0.6909 0.8445\r\nvt 0.581 0.8944\r\nvt 0.582 0.8829\r\nvt 0.554 0.8966\r\nvt 0.5546 0.885\r\nvt 0.5552 0.8737\r\nvt 0.5827 0.8716\r\nvt 0.4998 0.8983\r\nvt 0.4998 0.8867\r\nvt 0.5272 0.8863\r\nvt 0.5269 0.8979\r\nvt 0.4998 0.8754\r\nvt 0.5275 0.875\r\nvt 0.5277 0.864\r\nvt 0.4998 0.8644\r\nvt 0.5556 0.8627\r\nvt 0.5833 0.8606\r\nvt 0.524 0.5693\r\nvt 0.4997 0.5696\r\nvt 0.5482 0.5686\r\nvt 0.5489 0.5924\r\nvt 0.5244 0.5932\r\nvt 0.4997 0.5935\r\nvt 0.5722 0.5674\r\nvt 0.5732 0.5909\r\nvt 0.5745 0.6134\r\nvt 0.5497 0.615\r\nvt 0.4997 0.6163\r\nvt 0.5248 0.6159\r\nvt 0.5265 0.9098\r\nvt 0.4998 0.9103\r\nvt 0.5532 0.9085\r\nvt 0.5523 0.9207\r\nvt 0.526 0.9221\r\nvt 0.4998 0.9225\r\nvt 0.5798 0.9063\r\nvt 0.5785 0.9185\r\nvt 0.5771 0.931\r\nvt 0.5513 0.9333\r\nvt 0.4998 0.9351\r\nvt 0.5255 0.9347\r\nvt 0.3357 0.559\r\nvt 0.3151 0.5562\r\nvt 0.3575 0.5615\r\nvt 0.3553 0.5837\r\nvt 0.3332 0.5804\r\nvt 0.3122 0.5766\r\nvt 0.4036 0.5658\r\nvt 0.3802 0.5638\r\nvt 0.4021 0.589\r\nvt 0.3784 0.5866\r\nvt 0.3987 0.6322\r\nvt 0.3741 0.6291\r\nvt 0.3763 0.6084\r\nvt 0.4005 0.6112\r\nvt 0.3501 0.6254\r\nvt 0.3528 0.605\r\nvt 0.305 0.6157\r\nvt 0.3087 0.5965\r\nvt 0.3302 0.601\r\nvt 0.327 0.6209\r\nvt 0.2789 0.5501\r\nvt 0.2644 0.5463\r\nvt 0.275 0.5676\r\nvt 0.2593 0.5615\r\nvt 0.296 0.5534\r\nvt 0.2928 0.5725\r\nvt 0.2844 0.6097\r\nvt 0.2887 0.5913\r\nvt 0.2654 0.6026\r\nvt 0.2703 0.5851\r\nvt 0.2537 0.5776\r\nvt 0.248 0.5942\r\nvt 0.3013 0.6343\r\nvt 0.2802 0.6277\r\nvt 0.2979 0.6522\r\nvt 0.2763 0.6452\r\nvt 0.2562 0.6371\r\nvt 0.2606 0.62\r\nvt 0.2918 0.6859\r\nvt 0.2695 0.6786\r\nvt 0.2727 0.6622\r\nvt 0.2947 0.6694\r\nvt 0.2486 0.6702\r\nvt 0.2522 0.6539\r\nvt 0.2332 0.6444\r\nvt 0.2377 0.6278\r\nvt 0.2293 0.6607\r\nvt 0.2426 0.611\r\nvt 0.2601 0.9062\r\nvt 0.2652 0.9178\r\nvt 0.2477 0.9107\r\nvt 0.242 0.899\r\nvt 0.2701 0.9285\r\nvt 0.2536 0.9214\r\nvt 0.3015 0.9197\r\nvt 0.3055 0.9315\r\nvt 0.2845 0.9248\r\nvt 0.28 0.9131\r\nvt 0.3093 0.9426\r\nvt 0.2888 0.9356\r\nvt 0.3145 0.9605\r\nvt 0.2945 0.9525\r\nvt 0.2923 0.9451\r\nvt 0.3124 0.9525\r\nvt 0.2767 0.9444\r\nvt 0.2743 0.9376\r\nvt 0.2589 0.9303\r\nvt 0.2619 0.9364\r\nvt 0.289 0.8723\r\nvt 0.2915 0.884\r\nvt 0.269 0.8776\r\nvt 0.2662 0.8659\r\nvt 0.2945 0.8959\r\nvt 0.2722 0.8894\r\nvt 0.2515 0.8825\r\nvt 0.248 0.8706\r\nvt 0.245 0.8588\r\nvt 0.2759 0.9013\r\nvt 0.2978 0.9078\r\nvt 0.2555 0.8944\r\nvt 0.2369 0.8871\r\nvt 0.2324 0.8751\r\nvt 0.2286 0.8631\r\nvt 0.2255 0.8511\r\nvt 0.3916 0.8915\r\nvt 0.3931 0.9033\r\nvt 0.3667 0.8996\r\nvt 0.3649 0.8878\r\nvt 0.3948 0.9155\r\nvt 0.3687 0.9116\r\nvt 0.3431 0.907\r\nvt 0.3407 0.8951\r\nvt 0.3386 0.8833\r\nvt 0.3988 0.9404\r\nvt 0.3735 0.9363\r\nvt 0.371 0.9239\r\nvt 0.3967 0.9278\r\nvt 0.3486 0.9314\r\nvt 0.3457 0.9191\r\nvt 0.3212 0.9138\r\nvt 0.3245 0.9258\r\nvt 0.3182 0.9017\r\nvt 0.3132 0.8782\r\nvt 0.3155 0.8899\r\nvt 0.4009 0.9532\r\nvt 0.3761 0.9488\r\nvt 0.403 0.9655\r\nvt 0.3786 0.9609\r\nvt 0.3546 0.9554\r\nvt 0.3517 0.9436\r\nvt 0.4059 0.987\r\nvt 0.3822 0.9816\r\nvt 0.3807 0.972\r\nvt 0.4047 0.977\r\nvt 0.3587 0.9752\r\nvt 0.3571 0.9661\r\nvt 0.3342 0.9596\r\nvt 0.336 0.9681\r\nvt 0.3314 0.9493\r\nvt 0.328 0.9378\r\nvt 0.3238 0.64\r\nvt 0.3474 0.6448\r\nvt 0.3448 0.6632\r\nvt 0.3208 0.6582\r\nvt 0.3969 0.6521\r\nvt 0.3718 0.6488\r\nvt 0.3952 0.6708\r\nvt 0.3697 0.6674\r\nvt 0.3922 0.7052\r\nvt 0.366 0.7018\r\nvt 0.3677 0.6851\r\nvt 0.3936 0.6885\r\nvt 0.3403 0.6974\r\nvt 0.3424 0.6808\r\nvt 0.318 0.6756\r\nvt 0.3155 0.6922\r\nvt 0.4286 0.981\r\nvt 0.4295 0.9913\r\nvt 0.4272 0.9692\r\nvt 0.4514 0.9719\r\nvt 0.4523 0.9839\r\nvt 0.453 0.9945\r\nvt 0.424 0.9437\r\nvt 0.4257 0.9566\r\nvt 0.4493 0.9461\r\nvt 0.4504 0.9592\r\nvt 0.4751 0.9607\r\nvt 0.4745 0.9476\r\nvt 0.4756 0.9736\r\nvt 0.4764 0.9964\r\nvt 0.4761 0.9857\r\nvt 0.4236 0.6346\r\nvt 0.4223 0.6546\r\nvt 0.4488 0.6363\r\nvt 0.448 0.6564\r\nvt 0.4471 0.6753\r\nvt 0.421 0.6734\r\nvt 0.4738 0.6575\r\nvt 0.4742 0.6374\r\nvt 0.4734 0.6764\r\nvt 0.4727 0.711\r\nvt 0.473 0.6942\r\nvt 0.4456 0.7099\r\nvt 0.4463 0.6931\r\nvt 0.4199 0.6912\r\nvt 0.4188 0.7079\r\nvt 0.2374 0.7505\r\nvt 0.2369 0.7609\r\nvt 0.2166 0.7519\r\nvt 0.2171 0.7414\r\nvt 0.2366 0.7711\r\nvt 0.2163 0.7623\r\nvt 0.2395 0.7278\r\nvt 0.2383 0.7395\r\nvt 0.2181 0.7304\r\nvt 0.2194 0.7185\r\nvt 0.2844 0.7432\r\nvt 0.2834 0.7549\r\nvt 0.2601 0.7477\r\nvt 0.2612 0.736\r\nvt 0.2826 0.7656\r\nvt 0.2593 0.7585\r\nvt 0.2818 0.786\r\nvt 0.2584 0.779\r\nvt 0.2587 0.7689\r\nvt 0.2821 0.7759\r\nvt 0.309 0.7495\r\nvt 0.3081 0.7611\r\nvt 0.3347 0.7547\r\nvt 0.3339 0.7663\r\nvt 0.3333 0.7771\r\nvt 0.3074 0.7718\r\nvt 0.3885 0.7627\r\nvt 0.388 0.7743\r\nvt 0.3607 0.7707\r\nvt 0.3613 0.7591\r\nvt 0.3876 0.785\r\nvt 0.3602 0.7815\r\nvt 0.3872 0.8053\r\nvt 0.3597 0.8017\r\nvt 0.3599 0.7917\r\nvt 0.3874 0.7953\r\nvt 0.3328 0.7973\r\nvt 0.333 0.7873\r\nvt 0.3069 0.782\r\nvt 0.3067 0.7921\r\nvt 0.4152 0.7981\r\nvt 0.4151 0.8081\r\nvt 0.4154 0.7878\r\nvt 0.4434 0.7898\r\nvt 0.4432 0.8001\r\nvt 0.4432 0.8101\r\nvt 0.416 0.7654\r\nvt 0.4157 0.777\r\nvt 0.4438 0.7674\r\nvt 0.4436 0.779\r\nvt 0.4716 0.7802\r\nvt 0.4717 0.7686\r\nvt 0.4715 0.791\r\nvt 0.4714 0.8114\r\nvt 0.4715 0.8013\r\nvt 0.2456 0.6859\r\nvt 0.226 0.6764\r\nvt 0.2431 0.7008\r\nvt 0.2233 0.6913\r\nvt 0.2894 0.7017\r\nvt 0.2668 0.6943\r\nvt 0.2874 0.7165\r\nvt 0.2645 0.7092\r\nvt 0.2627 0.7231\r\nvt 0.2857 0.7304\r\nvt 0.2411 0.7148\r\nvt 0.2211 0.7054\r\nvt 0.3134 0.7079\r\nvt 0.3385 0.7132\r\nvt 0.337 0.7281\r\nvt 0.3116 0.7228\r\nvt 0.391 0.7211\r\nvt 0.3645 0.7176\r\nvt 0.39 0.736\r\nvt 0.3632 0.7325\r\nvt 0.3622 0.7464\r\nvt 0.3892 0.7499\r\nvt 0.3357 0.742\r\nvt 0.3102 0.7367\r\nvt 0.4179 0.7238\r\nvt 0.445 0.7258\r\nvt 0.4445 0.7407\r\nvt 0.4172 0.7387\r\nvt 0.4724 0.7269\r\nvt 0.4721 0.7418\r\nvt 0.4719 0.7558\r\nvt 0.4441 0.7546\r\nvt 0.4165 0.7526\r\nvt 0.2368 0.792\r\nvt 0.2373 0.8027\r\nvt 0.2171 0.7943\r\nvt 0.2165 0.7835\r\nvt 0.2381 0.8136\r\nvt 0.218 0.8053\r\nvt 0.2365 0.7815\r\nvt 0.2163 0.7728\r\nvt 0.2818 0.7962\r\nvt 0.2584 0.7893\r\nvt 0.2819 0.8066\r\nvt 0.2586 0.7997\r\nvt 0.283 0.8278\r\nvt 0.2598 0.821\r\nvt 0.259 0.8103\r\nvt 0.2823 0.8171\r\nvt 0.3072 0.8231\r\nvt 0.3078 0.8337\r\nvt 0.3068 0.8126\r\nvt 0.3328 0.8178\r\nvt 0.3332 0.8283\r\nvt 0.3337 0.8389\r\nvt 0.3066 0.8023\r\nvt 0.3327 0.8075\r\nvt 0.3872 0.8155\r\nvt 0.3597 0.8119\r\nvt 0.3873 0.8258\r\nvt 0.3598 0.8222\r\nvt 0.388 0.8469\r\nvt 0.3606 0.8433\r\nvt 0.3601 0.8327\r\nvt 0.3876 0.8363\r\nvt 0.4154 0.8392\r\nvt 0.4157 0.8498\r\nvt 0.4152 0.8287\r\nvt 0.4433 0.8307\r\nvt 0.4434 0.8412\r\nvt 0.4436 0.8519\r\nvt 0.4151 0.8183\r\nvt 0.4432 0.8204\r\nvt 0.4714 0.8216\r\nvt 0.4715 0.832\r\nvt 0.4717 0.8531\r\nvt 0.4715 0.8425\r\nvt 0.2392 0.8246\r\nvt 0.2192 0.8165\r\nvt 0.2407 0.8358\r\nvt 0.2208 0.8278\r\nvt 0.284 0.8386\r\nvt 0.2608 0.832\r\nvt 0.2852 0.8496\r\nvt 0.2622 0.8431\r\nvt 0.264 0.8544\r\nvt 0.2869 0.8609\r\nvt 0.2426 0.8472\r\nvt 0.2229 0.8394\r\nvt 0.3113 0.8667\r\nvt 0.3098 0.8555\r\nvt 0.3356 0.8607\r\nvt 0.3369 0.8719\r\nvt 0.3086 0.8445\r\nvt 0.3345 0.8497\r\nvt 0.3886 0.8577\r\nvt 0.3613 0.8541\r\nvt 0.3894 0.8687\r\nvt 0.3622 0.8651\r\nvt 0.3634 0.8763\r\nvt 0.3904 0.88\r\nvt 0.4176 0.8829\r\nvt 0.4185 0.8944\r\nvt 0.4168 0.8716\r\nvt 0.4444 0.8737\r\nvt 0.4449 0.885\r\nvt 0.4455 0.8966\r\nvt 0.4162 0.8606\r\nvt 0.4439 0.8627\r\nvt 0.4718 0.864\r\nvt 0.472 0.875\r\nvt 0.4726 0.8979\r\nvt 0.4723 0.8863\r\nvt 0.4755 0.5693\r\nvt 0.4751 0.5932\r\nvt 0.4506 0.5924\r\nvt 0.4513 0.5686\r\nvt 0.4747 0.6159\r\nvt 0.4497 0.615\r\nvt 0.425 0.6134\r\nvt 0.4262 0.5909\r\nvt 0.4273 0.5674\r\nvt 0.473 0.9098\r\nvt 0.4735 0.9221\r\nvt 0.4472 0.9207\r\nvt 0.4463 0.9085\r\nvt 0.474 0.9347\r\nvt 0.4482 0.9333\r\nvt 0.4225 0.931\r\nvt 0.421 0.9185\r\nvt 0.4197 0.9063\r\nvt 0.6267 0.0076\r\nvt 0.6579 0.0133\r\nvt 0.6885 0.0196\r\nvt 0.7182 0.0276\r\nvt 0.747 0.0365\r\nvt 0.7738 0.0454\r\nvt 0.7998 0.0563\r\nvt 0.8186 0.0661\r\nvt 0.4997 -0.0029\r\nvt 0.5316 -0.0022\r\nvt 0.5635 -0.0002\r\nvt 0.5952 0.0031\r\nvt 0.2527 0.0346\r\nvt 0.2809 0.0265\r\nvt 0.3109 0.019\r\nvt 0.3415 0.0122\r\nvt 0.3727 0.0076\r\nvt 0.1813 0.0655\r\nvt 0.2003 0.0555\r\nvt 0.2257 0.045\r\nvt 0.4042 0.0031\r\nvt 0.4359 -0.0002\r\nvt 0.4678 -0.0022\r\no panel_rear\r\nusemtl Panel_Rear\r\nf 13777/14483/12292 13778/14484/12293 13779/14485/12294 13780/14486/12295\r\nf 13781/14487/12296 13782/14488/12297 13779/14485/12294 13778/14484/12293\r\nf 13783/14489/12298 13784/14490/12299 13785/14491/12300 13786/14492/12301\r\nf 13787/14493/12302 13788/14494/12303 13785/14491/12300 13784/14490/12299\r\nf 13781/14487/12296 13778/14484/12293 13785/14491/12300 13788/14494/12303\r\nf 13777/14483/12292 13786/14492/12301 13785/14491/12300 13778/14484/12293\r\nf 13789/14495/12304 13790/14496/12305 13791/14497/12306 13792/14498/12307\r\nf 13793/14499/12308 13794/14500/12309 13791/14497/12306 13790/14496/12305\r\nf 13781/14487/12296 13788/14494/12303 13791/14497/12306 13794/14500/12309\r\nf 13787/14493/12302 13792/14498/12307 13791/14497/12306 13788/14494/12303\r\nf 13781/14487/12296 13794/14500/12309 13795/14501/12310 13782/14488/12297\r\nf 13793/14499/12308 13796/14502/12311 13795/14501/12310 13794/14500/12309\r\nf 13797/14503/12312 13798/14504/12313 13799/14505/12314 13800/14506/12315\r\nf 13801/14507/12316 13802/14508/12317 13799/14505/12314 13798/14504/12313\r\nf 13803/14509/12318 13804/14510/12319 13799/14505/12314 13802/14508/12317\r\nf 13805/14511/12320 13800/14506/12315 13799/14505/12314 13804/14510/12319\r\nf 13806/14512/12321 13807/14513/12322 13808/14514/12323 13809/14515/12324\r\nf 13810/14516/12325 13811/14517/12326 13808/14514/12323 13807/14513/12322\r\nf 13803/14509/12318 13802/14508/12317 13808/14514/12323 13811/14517/12326\r\nf 13801/14507/12316 13809/14515/12324 13808/14514/12323 13802/14508/12317\r\nf 13812/14518/12327 13813/14519/12328 13814/14520/12329 13815/14521/12330\r\nf 13816/14522/12331 13817/14523/12332 13814/14520/12329 13813/14519/12328\r\nf 13803/14509/12318 13811/14517/12326 13814/14520/12329 13817/14523/12332\r\nf 13810/14516/12325 13815/14521/12330 13814/14520/12329 13811/14517/12326\r\nf 13818/14524/12333 13819/14525/12334 13820/14526/12335 13821/14527/12336\r\nf 13805/14511/12320 13804/14510/12319 13820/14526/12335 13819/14525/12334\r\nf 13803/14509/12318 13817/14523/12332 13820/14526/12335 13804/14510/12319\r\nf 13816/14522/12331 13821/14527/12336 13820/14526/12335 13817/14523/12332\r\nf 13822/14528/12337 13823/14529/12338 13824/14530/12339 13825/14531/12340\r\nf 13826/14532/12341 13824/14530/12339 13823/14529/12338 13827/14533/12342\r\nf 13828/14534/12343 13829/14535/12344 13824/14530/12339 13826/14532/12341\r\nf 13830/14536/12345 13825/14531/12340 13824/14530/12339 13829/14535/12344\r\nf 13831/14537/12346 13832/14538/12347 13833/14539/12348 13834/14540/12349\r\nf 13835/14541/12350 13836/14542/12351 13832/14538/12347 13831/14537/12346\r\nf 13828/14534/12343 13826/14532/12341 13832/14538/12347 13836/14542/12351\r\nf 13827/14533/12342 13833/14539/12348 13832/14538/12347 13826/14532/12341\r\nf 13806/14512/12321 13809/14515/12324 13837/14543/12352 13838/14544/12353\r\nf 13801/14507/12316 13839/14545/12354 13837/14543/12352 13809/14515/12324\r\nf 13828/14534/12343 13836/14542/12351 13837/14543/12352 13839/14545/12354\r\nf 13835/14541/12350 13838/14544/12353 13837/14543/12352 13836/14542/12351\r\nf 13797/14503/12312 13840/14546/12355 13841/14547/12356 13798/14504/12313\r\nf 13830/14536/12345 13829/14535/12344 13841/14547/12356 13840/14546/12355\r\nf 13828/14534/12343 13839/14545/12354 13841/14547/12356 13829/14535/12344\r\nf 13801/14507/12316 13798/14504/12313 13841/14547/12356 13839/14545/12354\r\nf 13783/14489/12298 13842/14548/12357 13843/14549/12358 13784/14490/12299\r\nf 13844/14550/12359 13845/14551/12360 13843/14549/12358 13842/14548/12357\r\nf 13846/14552/12361 13847/14553/12362 13843/14549/12358 13845/14551/12360\r\nf 13787/14493/12302 13784/14490/12299 13843/14549/12358 13847/14553/12362\r\nf 13848/14554/12363 13849/14555/12364 13850/14556/12365 13851/14557/12366\r\nf 13852/14558/12367 13853/14559/12368 13850/14556/12365 13849/14555/12364\r\nf 13846/14552/12361 13845/14551/12360 13850/14556/12365 13853/14559/12368\r\nf 13844/14550/12359 13851/14557/12366 13850/14556/12365 13845/14551/12360\r\nf 13854/14560/12369 13855/14561/12370 13856/14562/12371 13857/14563/12372\r\nf 13858/14564/12373 13859/14565/12374 13856/14562/12371 13855/14561/12370\r\nf 13846/14552/12361 13853/14559/12368 13856/14562/12371 13859/14565/12374\r\nf 13852/14558/12367 13857/14563/12372 13856/14562/12371 13853/14559/12368\r\nf 13789/14495/12304 13792/14498/12307 13860/14566/12375 13861/14567/12376\r\nf 13787/14493/12302 13847/14553/12362 13860/14566/12375 13792/14498/12307\r\nf 13846/14552/12361 13859/14565/12374 13860/14566/12375 13847/14553/12362\r\nf 13858/14564/12373 13861/14567/12376 13860/14566/12375 13859/14565/12374\r\nf 13797/14503/12312 13862/14568/12377 13863/14569/12378 13840/14546/12355\r\nf 13864/14570/12379 13865/14571/12380 13863/14569/12378 13862/14568/12377\r\nf 13866/14572/12381 13867/14573/12382 13863/14569/12378 13865/14571/12380\r\nf 13830/14536/12345 13840/14546/12355 13863/14569/12378 13867/14573/12382\r\nf 13866/14572/12381 13865/14571/12380 13868/14574/12383 13869/14575/12384\r\nf 13864/14570/12379 13870/14576/12385 13868/14574/12383 13865/14571/12380\r\nf 13871/14577/12386 13872/14578/12387 13873/14579/12388 13874/14580/12389\r\nf 13866/14572/12381 13869/14575/12384 13872/14578/12387 13871/14577/12386\r\nf 13825/14531/12340 13875/14581/12390 13876/14582/12391 13822/14528/12337\r\nf 13830/14536/12345 13867/14573/12382 13875/14581/12390 13825/14531/12340\r\nf 13866/14572/12381 13871/14577/12386 13875/14581/12390 13867/14573/12382\r\nf 13874/14580/12389 13876/14582/12391 13875/14581/12390 13871/14577/12386\r\nf 13783/14489/12298 13786/14492/12301 13877/14583/12392 13878/14584/12393\r\nf 13777/14483/12292 13879/14585/12394 13877/14583/12392 13786/14492/12301\r\nf 13880/14586/12395 13881/14587/12396 13877/14583/12392 13879/14585/12394\r\nf 13882/14588/12397 13878/14584/12393 13877/14583/12392 13881/14587/12396\r\nf 13880/14586/12395 13879/14585/12394 13883/14589/12398 13884/14590/12399\r\nf 13777/14483/12292 13780/14486/12295 13883/14589/12398 13879/14585/12394\r\nf 13885/14591/12400 13886/14592/12401 13887/14593/12402 13888/14594/12403\r\nf 13880/14586/12395 13884/14590/12399 13887/14593/12402 13886/14592/12401\r\nf 13818/14524/12333 13889/14595/12404 13890/14596/12405 13891/14597/12406\r\nf 13882/14588/12397 13881/14587/12396 13890/14596/12405 13889/14595/12404\r\nf 13880/14586/12395 13886/14592/12401 13890/14596/12405 13881/14587/12396\r\nf 13885/14591/12400 13891/14597/12406 13890/14596/12405 13886/14592/12401\r\nf 13892/14598/12407 13893/14599/12408 13894/14600/12409 13895/14601/12410\r\nf 13896/14602/12411 13897/14603/12412 13894/14600/12409 13893/14599/12408\r\nf 13898/14604/12413 13899/14605/12414 13894/14600/12409 13897/14603/12412\r\nf 13900/14606/12415 13895/14601/12410 13894/14600/12409 13899/14605/12414\r\nf 13901/14607/12416 13902/14608/12417 13903/14609/12418 13904/14610/12419\r\nf 13905/14611/12420 13906/14612/12421 13903/14609/12418 13902/14608/12417\r\nf 13898/14604/12413 13897/14603/12412 13903/14609/12418 13906/14612/12421\r\nf 13896/14602/12411 13904/14610/12419 13903/14609/12418 13897/14603/12412\r\nf 13907/14613/12422 13908/14614/12423 13909/14615/12424 13910/14616/12425\r\nf 13911/14617/12426 13909/14615/12424 13908/14614/12423 13912/14618/12427\r\nf 13898/14604/12413 13906/14612/12421 13909/14615/12424 13911/14617/12426\r\nf 13905/14611/12420 13910/14616/12425 13909/14615/12424 13906/14612/12421\r\nf 13913/14619/12428 13914/14620/12429 13915/14621/12430 13916/14622/12431\r\nf 13900/14606/12415 13899/14605/12414 13914/14620/12429 13913/14619/12428\r\nf 13898/14604/12413 13911/14617/12426 13914/14620/12429 13899/14605/12414\r\nf 13912/14618/12427 13915/14621/12430 13914/14620/12429 13911/14617/12426\r\nf 13917/14623/12432 13918/14624/12433 13919/14625/12434 13920/14626/12435\r\nf 13921/14627/12436 13922/14628/12437 13919/14625/12434 13918/14624/12433\r\nf 13892/14598/12407 13895/14601/12410 13923/14629/12438 13924/14630/12439\r\nf 13900/14606/12415 13925/14631/12440 13923/14629/12438 13895/14601/12410\r\nf 13921/14627/12436 13918/14624/12433 13923/14629/12438 13925/14631/12440\r\nf 13917/14623/12432 13924/14630/12439 13923/14629/12438 13918/14624/12433\r\nf 13916/14622/12431 13926/14632/12441 13927/14633/12442 13913/14619/12428\r\nf 13928/14634/12443 13927/14633/12442 13926/14632/12441 13929/14635/12444\r\nf 13921/14627/12436 13925/14631/12440 13927/14633/12442 13928/14634/12443\r\nf 13900/14606/12415 13913/14619/12428 13927/14633/12442 13925/14631/12440\r\nf 13921/14627/12436 13928/14634/12443 13930/14636/12445 13922/14628/12437\r\nf 13929/14635/12444 13931/14637/12446 13930/14636/12445 13928/14634/12443\r\nf 13806/14512/12321 13932/14638/12447 13933/14639/12448 13807/14513/12322\r\nf 13934/14640/12449 13935/14641/12450 13933/14639/12448 13932/14638/12447\r\nf 13936/14642/12451 13937/14643/12452 13933/14639/12448 13935/14641/12450\r\nf 13810/14516/12325 13807/14513/12322 13933/14639/12448 13937/14643/12452\r\nf 13938/14644/12453 13939/14645/12454 13940/14646/12455 13941/14647/12456\r\nf 13942/14648/12457 13943/14649/12458 13940/14646/12455 13939/14645/12454\r\nf 13936/14642/12451 13935/14641/12450 13940/14646/12455 13943/14649/12458\r\nf 13934/14640/12449 13941/14647/12456 13940/14646/12455 13935/14641/12450\r\nf 13944/14650/12459 13945/14651/12460 13946/14652/12461 13947/14653/12462\r\nf 13948/14654/12463 13949/14655/12464 13946/14652/12461 13945/14651/12460\r\nf 13936/14642/12451 13943/14649/12458 13946/14652/12461 13949/14655/12464\r\nf 13942/14648/12457 13947/14653/12462 13946/14652/12461 13943/14649/12458\r\nf 13812/14518/12327 13815/14521/12330 13950/14656/12465 13951/14657/12466\r\nf 13810/14516/12325 13937/14643/12452 13950/14656/12465 13815/14521/12330\r\nf 13936/14642/12451 13949/14655/12464 13950/14656/12465 13937/14643/12452\r\nf 13948/14654/12463 13951/14657/12466 13950/14656/12465 13949/14655/12464\r\nf 13834/14540/12349 13952/14658/12467 13953/14659/12468 13831/14537/12346\r\nf 13954/14660/12469 13953/14659/12468 13952/14658/12467 13955/14661/12470\r\nf 13956/14662/12471 13957/14663/12472 13953/14659/12468 13954/14660/12469\r\nf 13835/14541/12350 13831/14537/12346 13953/14659/12468 13957/14663/12472\r\nf 13958/14664/12473 13959/14665/12474 13960/14666/12475 13961/14667/12476\r\nf 13962/14668/12477 13963/14669/12478 13959/14665/12474 13958/14664/12473\r\nf 13956/14662/12471 13954/14660/12469 13959/14665/12474 13963/14669/12478\r\nf 13955/14661/12470 13960/14666/12475 13959/14665/12474 13954/14660/12469\r\nf 13938/14644/12453 13941/14647/12456 13964/14670/12479 13965/14671/12480\r\nf 13934/14640/12449 13966/14672/12481 13964/14670/12479 13941/14647/12456\r\nf 13956/14662/12471 13963/14669/12478 13964/14670/12479 13966/14672/12481\r\nf 13962/14668/12477 13965/14671/12480 13964/14670/12479 13963/14669/12478\r\nf 13806/14512/12321 13838/14544/12353 13967/14673/12482 13932/14638/12447\r\nf 13835/14541/12350 13957/14663/12472 13967/14673/12482 13838/14544/12353\r\nf 13956/14662/12471 13966/14672/12481 13967/14673/12482 13957/14663/12472\r\nf 13934/14640/12449 13932/14638/12447 13967/14673/12482 13966/14672/12481\r\nf 13848/14554/12363 13968/14674/12483 13969/14675/12484 13849/14555/12364\r\nf 13970/14676/12485 13971/14677/12486 13969/14675/12484 13968/14674/12483\r\nf 13972/14678/12487 13973/14679/12488 13969/14675/12484 13971/14677/12486\r\nf 13852/14558/12367 13849/14555/12364 13969/14675/12484 13973/14679/12488\r\nf 13974/14680/12489 13975/14681/12490 13976/14682/12491 13977/14683/12492\r\nf 13978/14684/12493 13979/14685/12494 13976/14682/12491 13975/14681/12490\r\nf 13972/14678/12487 13971/14677/12486 13976/14682/12491 13979/14685/12494\r\nf 13970/14676/12485 13977/14683/12492 13976/14682/12491 13971/14677/12486\r\nf 13980/14686/12495 13981/14687/12496 13982/14688/12497 13983/14689/12498\r\nf 13984/14690/12499 13985/14691/12500 13982/14688/12497 13981/14687/12496\r\nf 13972/14678/12487 13979/14685/12494 13982/14688/12497 13985/14691/12500\r\nf 13978/14684/12493 13983/14689/12498 13982/14688/12497 13979/14685/12494\r\nf 13854/14560/12369 13857/14563/12372 13986/14692/12501 13987/14693/12502\r\nf 13852/14558/12367 13973/14679/12488 13986/14692/12501 13857/14563/12372\r\nf 13972/14678/12487 13985/14691/12500 13986/14692/12501 13973/14679/12488\r\nf 13984/14690/12499 13987/14693/12502 13986/14692/12501 13985/14691/12500\r\nf 13901/14607/12416 13988/14694/12503 13989/14695/12504 13902/14608/12417\r\nf 13990/14696/12505 13991/14697/12506 13989/14695/12504 13988/14694/12503\r\nf 13992/14698/12507 13993/14699/12508 13989/14695/12504 13991/14697/12506\r\nf 13905/14611/12420 13902/14608/12417 13989/14695/12504 13993/14699/12508\r\nf 13994/14700/12509 13995/14701/12510 13996/14702/12511 13997/14703/12512\r\nf 13998/14704/12513 13999/14705/12514 13996/14702/12511 13995/14701/12510\r\nf 13992/14698/12507 13991/14697/12506 13996/14702/12511 13999/14705/12514\r\nf 13990/14696/12505 13997/14703/12512 13996/14702/12511 13991/14697/12506\r\nf 14000/14706/12515 14001/14707/12516 14002/14708/12517 14003/14709/12518\r\nf 14004/14710/12519 14002/14708/12517 14001/14707/12516 14005/14711/12520\r\nf 13992/14698/12507 13999/14705/12514 14002/14708/12517 14004/14710/12519\r\nf 13998/14704/12513 14003/14709/12518 14002/14708/12517 13999/14705/12514\r\nf 13910/14616/12425 14006/14712/12521 14007/14713/12522 13907/14613/12422\r\nf 13905/14611/12420 13993/14699/12508 14006/14712/12521 13910/14616/12425\r\nf 13992/14698/12507 14004/14710/12519 14006/14712/12521 13993/14699/12508\r\nf 14005/14711/12520 14007/14713/12522 14006/14712/12521 14004/14710/12519\r\nf 14008/14714/12523 14009/14715/12524 14010/14716/12525 14011/14717/12526\r\nf 14012/14718/12527 14013/14719/12528 14010/14716/12525 14009/14715/12524\r\nf 14014/14720/12529 14015/14721/12530 14016/14722/12531 14017/14723/12532\r\nf 14018/14724/12533 14019/14725/12534 14016/14722/12531 14015/14721/12530\r\nf 14012/14718/12527 14009/14715/12524 14016/14722/12531 14019/14725/12534\r\nf 14008/14714/12523 14017/14723/12532 14016/14722/12531 14009/14715/12524\r\nf 14020/14726/12535 14021/14727/12536 14022/14728/12537 14023/14729/12538\r\nf 14024/14730/12539 14025/14731/12540 14022/14728/12537 14021/14727/12536\r\nf 14012/14718/12527 14019/14725/12534 14022/14728/12537 14025/14731/12540\r\nf 14018/14724/12533 14023/14729/12538 14022/14728/12537 14019/14725/12534\r\nf 14012/14718/12527 14025/14731/12540 14026/14732/12541 14013/14719/12528\r\nf 14024/14730/12539 14027/14733/12542 14026/14732/12541 14025/14731/12540\r\nf 14014/14720/12529 14028/14734/12543 14029/14735/12544 14015/14721/12530\r\nf 14030/14736/12545 14031/14737/12546 14029/14735/12544 14028/14734/12543\r\nf 14032/14738/12547 14033/14739/12548 14029/14735/12544 14031/14737/12546\r\nf 14018/14724/12533 14015/14721/12530 14029/14735/12544 14033/14739/12548\r\nf 14034/14740/12549 14035/14741/12550 14036/14742/12551 14037/14743/12552\r\nf 14038/14744/12553 14039/14745/12554 14036/14742/12551 14035/14741/12550\r\nf 14032/14738/12547 14031/14737/12546 14036/14742/12551 14039/14745/12554\r\nf 14030/14736/12545 14037/14743/12552 14036/14742/12551 14031/14737/12546\r\nf 14040/14746/12555 14041/14747/12556 14042/14748/12557 14043/14749/12558\r\nf 14044/14750/12559 14045/14751/12560 14042/14748/12557 14041/14747/12556\r\nf 14032/14738/12547 14039/14745/12554 14042/14748/12557 14045/14751/12560\r\nf 14038/14744/12553 14043/14749/12558 14042/14748/12557 14039/14745/12554\r\nf 14020/14726/12535 14023/14729/12538 14046/14752/12561 14047/14753/12562\r\nf 14018/14724/12533 14033/14739/12548 14046/14752/12561 14023/14729/12538\r\nf 14032/14738/12547 14045/14751/12560 14046/14752/12561 14033/14739/12548\r\nf 14044/14750/12559 14047/14753/12562 14046/14752/12561 14045/14751/12560\r\nf 13901/14607/12416 13904/14610/12419 14048/14754/12563 14049/14755/12564\r\nf 13896/14602/12411 14050/14756/12565 14048/14754/12563 13904/14610/12419\r\nf 14051/14757/12566 14052/14758/12567 14048/14754/12563 14050/14756/12565\r\nf 14053/14759/12568 14049/14755/12564 14048/14754/12563 14052/14758/12567\r\nf 13892/14598/12407 14054/14760/12569 14055/14761/12570 13893/14599/12408\r\nf 14056/14762/12571 14057/14763/12572 14055/14761/12570 14054/14760/12569\r\nf 14051/14757/12566 14050/14756/12565 14055/14761/12570 14057/14763/12572\r\nf 13896/14602/12411 13893/14599/12408 14055/14761/12570 14050/14756/12565\r\nf 14020/14726/12535 14047/14753/12562 14058/14764/12573 14059/14765/12574\r\nf 14044/14750/12559 14060/14766/12575 14058/14764/12573 14047/14753/12562\r\nf 14051/14757/12566 14057/14763/12572 14058/14764/12573 14060/14766/12575\r\nf 14056/14762/12571 14059/14765/12574 14058/14764/12573 14057/14763/12572\r\nf 14040/14746/12555 14061/14767/12576 14062/14768/12577 14041/14747/12556\r\nf 14053/14759/12568 14052/14758/12567 14062/14768/12577 14061/14767/12576\r\nf 14051/14757/12566 14060/14766/12575 14062/14768/12577 14052/14758/12567\r\nf 14044/14750/12559 14041/14747/12556 14062/14768/12577 14060/14766/12575\r\nf 13892/14598/12407 13924/14630/12439 14063/14769/12578 14054/14760/12569\r\nf 13917/14623/12432 14064/14770/12579 14063/14769/12578 13924/14630/12439\r\nf 14065/14771/12580 14066/14772/12581 14063/14769/12578 14064/14770/12579\r\nf 14056/14762/12571 14054/14760/12569 14063/14769/12578 14066/14772/12581\r\nf 14065/14771/12580 14064/14770/12579 14067/14773/12582 14068/14774/12583\r\nf 13917/14623/12432 13920/14626/12435 14067/14773/12582 14064/14770/12579\r\nf 14024/14730/12539 14069/14775/12584 14070/14776/12585 14027/14733/12542\r\nf 14065/14771/12580 14068/14774/12583 14070/14776/12585 14069/14775/12584\r\nf 14020/14726/12535 14059/14765/12574 14071/14777/12586 14021/14727/12536\r\nf 14056/14762/12571 14066/14772/12581 14071/14777/12586 14059/14765/12574\r\nf 14065/14771/12580 14069/14775/12584 14071/14777/12586 14066/14772/12581\r\nf 14024/14730/12539 14021/14727/12536 14071/14777/12586 14069/14775/12584\r\nf 14034/14740/12549 14072/14778/12587 14073/14779/12588 14035/14741/12550\r\nf 14074/14780/12589 14075/14781/12590 14073/14779/12588 14072/14778/12587\r\nf 14076/14782/12591 14077/14783/12592 14073/14779/12588 14075/14781/12590\r\nf 14038/14744/12553 14035/14741/12550 14073/14779/12588 14077/14783/12592\r\nf 14078/14784/12593 14079/14785/12594 14080/14786/12595 14081/14787/12596\r\nf 14082/14788/12597 14083/14789/12598 14080/14786/12595 14079/14785/12594\r\nf 14076/14782/12591 14075/14781/12590 14080/14786/12595 14083/14789/12598\r\nf 14074/14780/12589 14081/14787/12596 14080/14786/12595 14075/14781/12590\r\nf 14084/14790/12599 14085/14791/12600 14086/14792/12601 14087/14793/12602\r\nf 14088/14794/12603 14089/14795/12604 14086/14792/12601 14085/14791/12600\r\nf 14076/14782/12591 14083/14789/12598 14086/14792/12601 14089/14795/12604\r\nf 14082/14788/12597 14087/14793/12602 14086/14792/12601 14083/14789/12598\r\nf 14040/14746/12555 14043/14749/12558 14090/14796/12605 14091/14797/12606\r\nf 14038/14744/12553 14077/14783/12592 14090/14796/12605 14043/14749/12558\r\nf 14076/14782/12591 14089/14795/12604 14090/14796/12605 14077/14783/12592\r\nf 14088/14794/12603 14091/14797/12606 14090/14796/12605 14089/14795/12604\r\nf 13848/14554/12363 13851/14557/12366 14092/14798/12607 14093/14799/12608\r\nf 13844/14550/12359 14094/14800/12609 14092/14798/12607 13851/14557/12366\r\nf 14095/14801/12610 14096/14802/12611 14092/14798/12607 14094/14800/12609\r\nf 14097/14803/12612 14093/14799/12608 14092/14798/12607 14096/14802/12611\r\nf 13783/14489/12298 13878/14584/12393 14098/14804/12613 13842/14548/12357\r\nf 13882/14588/12397 14099/14805/12614 14098/14804/12613 13878/14584/12393\r\nf 14095/14801/12610 14094/14800/12609 14098/14804/12613 14099/14805/12614\r\nf 13844/14550/12359 13842/14548/12357 14098/14804/12613 14094/14800/12609\r\nf 13818/14524/12333 13821/14527/12336 14100/14806/12615 13889/14595/12404\r\nf 13816/14522/12331 14101/14807/12616 14100/14806/12615 13821/14527/12336\r\nf 14095/14801/12610 14099/14805/12614 14100/14806/12615 14101/14807/12616\r\nf 13882/14588/12397 13889/14595/12404 14100/14806/12615 14099/14805/12614\r\nf 13812/14518/12327 14102/14808/12617 14103/14809/12618 13813/14519/12328\r\nf 14097/14803/12612 14096/14802/12611 14103/14809/12618 14102/14808/12617\r\nf 14095/14801/12610 14101/14807/12616 14103/14809/12618 14096/14802/12611\r\nf 13816/14522/12331 13813/14519/12328 14103/14809/12618 14101/14807/12616\r\nf 13864/14570/12379 14104/14810/12619 14105/14811/12620 13870/14576/12385\r\nf 14106/14812/12621 14107/14813/12622 14105/14811/12620 14104/14810/12619\r\nf 13797/14503/12312 13800/14506/12315 14108/14814/12623 13862/14568/12377\r\nf 13805/14511/12320 14109/14815/12624 14108/14814/12623 13800/14506/12315\r\nf 14106/14812/12621 14104/14810/12619 14108/14814/12623 14109/14815/12624\r\nf 13864/14570/12379 13862/14568/12377 14108/14814/12623 14104/14810/12619\r\nf 13818/14524/12333 13891/14597/12406 14110/14816/12625 13819/14525/12334\r\nf 13885/14591/12400 14111/14817/12626 14110/14816/12625 13891/14597/12406\r\nf 14106/14812/12621 14109/14815/12624 14110/14816/12625 14111/14817/12626\r\nf 13805/14511/12320 13819/14525/12334 14110/14816/12625 14109/14815/12624\r\nf 14106/14812/12621 14111/14817/12626 14112/14818/12627 14107/14813/12622\r\nf 13885/14591/12400 13888/14594/12403 14112/14818/12627 14111/14817/12626\r\nf 14014/14720/12529 14017/14723/12532 14113/14819/12628 14114/14820/12629\r\nf 14008/14714/12523 14115/14821/12630 14113/14819/12628 14017/14723/12532\r\nf 14116/14822/12631 14117/14823/12632 14113/14819/12628 14115/14821/12630\r\nf 14118/14824/12633 14114/14820/12629 14113/14819/12628 14117/14823/12632\r\nf 14116/14822/12631 14115/14821/12630 14119/14825/12634 14120/14826/12635\r\nf 14008/14714/12523 14011/14717/12526 14119/14825/12634 14115/14821/12630\r\nf 13793/14499/12308 14121/14827/12636 14122/14828/12637 13796/14502/12311\r\nf 14116/14822/12631 14120/14826/12635 14122/14828/12637 14121/14827/12636\r\nf 13789/14495/12304 14123/14829/12638 14124/14830/12639 13790/14496/12305\r\nf 14118/14824/12633 14117/14823/12632 14124/14830/12639 14123/14829/12638\r\nf 14116/14822/12631 14121/14827/12636 14124/14830/12639 14117/14823/12632\r\nf 13793/14499/12308 13790/14496/12305 14124/14830/12639 14121/14827/12636\r\nf 14034/14740/12549 14037/14743/12552 14125/14831/12640 14126/14832/12641\r\nf 14030/14736/12545 14127/14833/12642 14125/14831/12640 14037/14743/12552\r\nf 14128/14834/12643 14129/14835/12644 14125/14831/12640 14127/14833/12642\r\nf 14130/14836/12645 14126/14832/12641 14125/14831/12640 14129/14835/12644\r\nf 14014/14720/12529 14114/14820/12629 14131/14837/12646 14028/14734/12543\r\nf 14118/14824/12633 14132/14838/12647 14131/14837/12646 14114/14820/12629\r\nf 14128/14834/12643 14127/14833/12642 14131/14837/12646 14132/14838/12647\r\nf 14030/14736/12545 14028/14734/12543 14131/14837/12646 14127/14833/12642\r\nf 13789/14495/12304 13861/14567/12376 14133/14839/12648 14123/14829/12638\r\nf 13858/14564/12373 14134/14840/12649 14133/14839/12648 13861/14567/12376\r\nf 14128/14834/12643 14132/14838/12647 14133/14839/12648 14134/14840/12649\r\nf 14118/14824/12633 14123/14829/12638 14133/14839/12648 14132/14838/12647\r\nf 13854/14560/12369 14135/14841/12650 14136/14842/12651 13855/14561/12370\r\nf 14130/14836/12645 14129/14835/12644 14136/14842/12651 14135/14841/12650\r\nf 14128/14834/12643 14134/14840/12649 14136/14842/12651 14129/14835/12644\r\nf 13858/14564/12373 13855/14561/12370 14136/14842/12651 14134/14840/12649\r\nf 13994/14700/12509 13997/14703/12512 14137/14843/12652 14138/14844/12653\r\nf 13990/14696/12505 14139/14845/12654 14137/14843/12652 13997/14703/12512\r\nf 14140/14846/12655 14141/14847/12656 14137/14843/12652 14139/14845/12654\r\nf 14142/14848/12657 14138/14844/12653 14137/14843/12652 14141/14847/12656\r\nf 13901/14607/12416 14049/14755/12564 14143/14849/12658 13988/14694/12503\r\nf 14053/14759/12568 14144/14850/12659 14143/14849/12658 14049/14755/12564\r\nf 14140/14846/12655 14139/14845/12654 14143/14849/12658 14144/14850/12659\r\nf 13990/14696/12505 13988/14694/12503 14143/14849/12658 14139/14845/12654\r\nf 14040/14746/12555 14091/14797/12606 14145/14851/12660 14061/14767/12576\r\nf 14088/14794/12603 14146/14852/12661 14145/14851/12660 14091/14797/12606\r\nf 14140/14846/12655 14144/14850/12659 14145/14851/12660 14146/14852/12661\r\nf 14053/14759/12568 14061/14767/12576 14145/14851/12660 14144/14850/12659\r\nf 14084/14790/12599 14147/14853/12662 14148/14854/12663 14085/14791/12600\r\nf 14142/14848/12657 14141/14847/12656 14148/14854/12663 14147/14853/12662\r\nf 14140/14846/12655 14146/14852/12661 14148/14854/12663 14141/14847/12656\r\nf 14088/14794/12603 14085/14791/12600 14148/14854/12663 14146/14852/12661\r\nf 13974/14680/12489 13977/14683/12492 14149/14855/12664 14150/14856/12665\r\nf 13970/14676/12485 14151/14857/12666 14149/14855/12664 13977/14683/12492\r\nf 14152/14858/12667 14153/14859/12668 14149/14855/12664 14151/14857/12666\r\nf 14154/14860/12669 14150/14856/12665 14149/14855/12664 14153/14859/12668\r\nf 13848/14554/12363 14093/14799/12608 14155/14861/12670 13968/14674/12483\r\nf 14097/14803/12612 14156/14862/12671 14155/14861/12670 14093/14799/12608\r\nf 14152/14858/12667 14151/14857/12666 14155/14861/12670 14156/14862/12671\r\nf 13970/14676/12485 13968/14674/12483 14155/14861/12670 14151/14857/12666\r\nf 13812/14518/12327 13951/14657/12466 14157/14863/12672 14102/14808/12617\r\nf 13948/14654/12463 14158/14864/12673 14157/14863/12672 13951/14657/12466\r\nf 14152/14858/12667 14156/14862/12671 14157/14863/12672 14158/14864/12673\r\nf 14097/14803/12612 14102/14808/12617 14157/14863/12672 14156/14862/12671\r\nf 13944/14650/12459 14159/14865/12674 14160/14866/12675 13945/14651/12460\r\nf 14154/14860/12669 14153/14859/12668 14160/14866/12675 14159/14865/12674\r\nf 14152/14858/12667 14158/14864/12673 14160/14866/12675 14153/14859/12668\r\nf 13948/14654/12463 13945/14651/12460 14160/14866/12675 14158/14864/12673\r\nf 14078/14784/12593 14081/14787/12596 14161/14867/12676 14162/14868/12677\r\nf 14074/14780/12589 14163/14869/12678 14161/14867/12676 14081/14787/12596\r\nf 14164/14870/12679 14165/14871/12680 14161/14867/12676 14163/14869/12678\r\nf 14166/14872/12681 14162/14868/12677 14161/14867/12676 14165/14871/12680\r\nf 14034/14740/12549 14126/14832/12641 14167/14873/12682 14072/14778/12587\r\nf 14130/14836/12645 14168/14874/12683 14167/14873/12682 14126/14832/12641\r\nf 14164/14870/12679 14163/14869/12678 14167/14873/12682 14168/14874/12683\r\nf 14074/14780/12589 14072/14778/12587 14167/14873/12682 14163/14869/12678\r\nf 13854/14560/12369 13987/14693/12502 14169/14875/12684 14135/14841/12650\r\nf 13984/14690/12499 14170/14876/12685 14169/14875/12684 13987/14693/12502\r\nf 14164/14870/12679 14168/14874/12683 14169/14875/12684 14170/14876/12685\r\nf 14130/14836/12645 14135/14841/12650 14169/14875/12684 14168/14874/12683\r\nf 13980/14686/12495 14171/14877/12686 14172/14878/12687 13981/14687/12496\r\nf 14166/14872/12681 14165/14871/12680 14172/14878/12687 14171/14877/12686\r\nf 14164/14870/12679 14170/14876/12685 14172/14878/12687 14165/14871/12680\r\nf 13984/14690/12499 13981/14687/12496 14172/14878/12687 14170/14876/12685\r\nf 14173/14879/12688 14174/14880/12689 14175/14881/12690 14176/14882/12691\r\nf 14177/14883/12692 14178/14884/12693 14175/14881/12690 14174/14880/12689\r\nf 14179/14885/12694 14180/14886/12695 14181/14887/12696 14182/14888/12697\r\nf 14173/14879/12688 14176/14882/12691 14181/14887/12696 14180/14886/12695\r\nf 14183/14889/12698 14184/14890/12699 14185/14891/12700 14186/14892/12701\r\nf 14187/14893/12702 14188/14894/12703 14185/14891/12700 14184/14890/12699\r\nf 14173/14879/12688 14180/14886/12695 14185/14891/12700 14188/14894/12703\r\nf 14179/14885/12694 14186/14892/12701 14185/14891/12700 14180/14886/12695\r\nf 14189/14895/12704 14190/14896/12705 14191/14897/12706 14192/14898/12707\r\nf 14177/14883/12692 14174/14880/12689 14191/14897/12706 14190/14896/12705\r\nf 14173/14879/12688 14188/14894/12703 14191/14897/12706 14174/14880/12689\r\nf 14187/14893/12702 14192/14898/12707 14191/14897/12706 14188/14894/12703\r\nf 14193/14899/12708 14194/14900/12709 14195/14901/12710 14196/14902/12711\r\nf 14197/14903/12712 14198/14904/12713 14195/14901/12710 14194/14900/12709\r\nf 14199/14905/12714 14200/14906/12715 14195/14901/12710 14198/14904/12713\r\nf 14201/14907/12716 14196/14902/12711 14195/14901/12710 14200/14906/12715\r\nf 14202/14908/12717 14203/14909/12718 14204/14910/12719 14205/14911/12720\r\nf 14206/14912/12721 14207/14913/12722 14204/14910/12719 14203/14909/12718\r\nf 14199/14905/12714 14198/14904/12713 14204/14910/12719 14207/14913/12722\r\nf 14197/14903/12712 14205/14911/12720 14204/14910/12719 14198/14904/12713\r\nf 14208/14914/12723 14209/14915/12724 14210/14916/12725 14211/14917/12726\r\nf 14212/14918/12727 14213/14919/12728 14210/14916/12725 14209/14915/12724\r\nf 14199/14905/12714 14207/14913/12722 14210/14916/12725 14213/14919/12728\r\nf 14206/14912/12721 14211/14917/12726 14210/14916/12725 14207/14913/12722\r\nf 14214/14920/12729 14215/14921/12730 14216/14922/12731 14217/14923/12732\r\nf 14201/14907/12716 14200/14906/12715 14216/14922/12731 14215/14921/12730\r\nf 14199/14905/12714 14213/14919/12728 14216/14922/12731 14200/14906/12715\r\nf 14212/14918/12727 14217/14923/12732 14216/14922/12731 14213/14919/12728\r\nf 14218/14924/12733 14219/14925/12734 14220/14926/12735 14221/14927/12736\r\nf 14222/14928/12737 14223/14929/12738 14219/14925/12734 14218/14924/12733\r\nf 14224/14930/12739 14225/14931/12740 14219/14925/12734 14223/14929/12738\r\nf 14226/14932/12741 14220/14926/12735 14219/14925/12734 14225/14931/12740\r\nf 14193/14899/12708 14196/14902/12711 14227/14933/12742 14228/14934/12743\r\nf 14201/14907/12716 14229/14935/12744 14227/14933/12742 14196/14902/12711\r\nf 14224/14930/12739 14223/14929/12738 14227/14933/12742 14229/14935/12744\r\nf 14222/14928/12737 14228/14934/12743 14227/14933/12742 14223/14929/12738\r\nf 14214/14920/12729 14230/14936/12745 14231/14937/12746 14215/14921/12730\r\nf 14232/14938/12747 14233/14939/12748 14231/14937/12746 14230/14936/12745\r\nf 14224/14930/12739 14229/14935/12744 14231/14937/12746 14233/14939/12748\r\nf 14201/14907/12716 14215/14921/12730 14231/14937/12746 14229/14935/12744\r\nf 14234/14940/12749 14235/14941/12750 14236/14942/12751 14237/14943/12752\r\nf 14225/14931/12740 14236/14942/12751 14235/14941/12750 14226/14932/12741\r\nf 14224/14930/12739 14233/14939/12748 14236/14942/12751 14225/14931/12740\r\nf 14232/14938/12747 14237/14943/12752 14236/14942/12751 14233/14939/12748\r\nf 14189/14895/12704 14192/14898/12707 14238/14944/12753 14239/14945/12754\r\nf 14187/14893/12702 14240/14946/12755 14238/14944/12753 14192/14898/12707\r\nf 14241/14947/12756 14242/14948/12757 14238/14944/12753 14240/14946/12755\r\nf 14243/14949/12758 14239/14945/12754 14238/14944/12753 14242/14948/12757\r\nf 14183/14889/12698 14244/14950/12759 14245/14951/12760 14184/14890/12699\r\nf 14246/14952/12761 14247/14953/12762 14245/14951/12760 14244/14950/12759\r\nf 14241/14947/12756 14240/14946/12755 14245/14951/12760 14247/14953/12762\r\nf 14187/14893/12702 14184/14890/12699 14245/14951/12760 14240/14946/12755\r\nf 14248/14954/12763 14249/14955/12764 14250/14956/12765 14251/14957/12766\r\nf 14252/14958/12767 14253/14959/12768 14250/14956/12765 14249/14955/12764\r\nf 14241/14947/12756 14247/14953/12762 14250/14956/12765 14253/14959/12768\r\nf 14246/14952/12761 14251/14957/12766 14250/14956/12765 14247/14953/12762\r\nf 14254/14960/12769 14255/14961/12770 14256/14962/12771 14257/14963/12772\r\nf 14243/14949/12758 14242/14948/12757 14256/14962/12771 14255/14961/12770\r\nf 14241/14947/12756 14253/14959/12768 14256/14962/12771 14242/14948/12757\r\nf 14252/14958/12767 14257/14963/12772 14256/14962/12771 14253/14959/12768\r\nf 14193/14899/12708 14228/14934/12743 14258/14964/12773 14259/14965/12774\r\nf 14222/14928/12737 14260/14966/12775 14258/14964/12773 14228/14934/12743\r\nf 14261/14967/12776 14262/14968/12777 14258/14964/12773 14260/14966/12775\r\nf 14263/14969/12778 14259/14965/12774 14258/14964/12773 14262/14968/12777\r\nf 14221/14927/12736 14264/14970/12779 14265/14971/12780 14218/14924/12733\r\nf 14266/14972/12781 14265/14971/12780 14264/14970/12779 14267/14973/12782\r\nf 14261/14967/12776 14260/14966/12775 14265/14971/12780 14266/14972/12781\r\nf 14222/14928/12737 14218/14924/12733 14265/14971/12780 14260/14966/12775\r\nf 14261/14967/12776 14266/14972/12781 14268/14974/12783 14269/14975/12784\r\nf 14267/14973/12782 14270/14976/12785 14268/14974/12783 14266/14972/12781\r\nf 14263/14969/12778 14262/14968/12777 14271/14977/12786 14272/14978/12787\r\nf 14261/14967/12776 14269/14975/12784 14271/14977/12786 14262/14968/12777\r\nf 14189/14895/12704 14273/14979/12788 14274/14980/12789 14190/14896/12705\r\nf 14275/14981/12790 14276/14982/12791 14274/14980/12789 14273/14979/12788\r\nf 14277/14983/12792 14278/14984/12793 14274/14980/12789 14276/14982/12791\r\nf 14177/14883/12692 14190/14896/12705 14274/14980/12789 14278/14984/12793\r\nf 14202/14908/12717 14279/14985/12794 14280/14986/12795 14281/14987/12796\r\nf 14282/14988/12797 14283/14989/12798 14280/14986/12795 14279/14985/12794\r\nf 14277/14983/12792 14276/14982/12791 14280/14986/12795 14283/14989/12798\r\nf 14275/14981/12790 14281/14987/12796 14280/14986/12795 14276/14982/12791\r\nf 14277/14983/12792 14283/14989/12798 14284/14990/12799 14285/14991/12800\r\nf 14282/14988/12797 14286/14992/12801 14284/14990/12799 14283/14989/12798\r\nf 14177/14883/12692 14278/14984/12793 14287/14993/12802 14178/14884/12693\r\nf 14277/14983/12792 14285/14991/12800 14287/14993/12802 14278/14984/12793\r\nf 14288/14994/12803 14289/14995/12804 14290/14996/12805 14291/14997/12806\r\nf 14292/14998/12807 14293/14999/12808 14290/14996/12805 14289/14995/12804\r\nf 14294/15000/12809 14295/15001/12810 14290/14996/12805 14293/14999/12808\r\nf 14296/15002/12811 14291/14997/12806 14290/14996/12805 14295/15001/12810\r\nf 14297/15003/12812 14298/15004/12813 14299/15005/12814 14300/15006/12815\r\nf 14301/15007/12816 14299/15005/12814 14298/15004/12813 14302/15008/12817\r\nf 14294/15000/12809 14293/14999/12808 14299/15005/12814 14301/15007/12816\r\nf 14292/14998/12807 14300/15006/12815 14299/15005/12814 14293/14999/12808\r\nf 14303/15009/12818 14304/15010/12819 14305/15011/12820 14306/15012/12821\r\nf 14307/15013/12822 14308/15014/12823 14304/15010/12819 14303/15009/12818\r\nf 14294/15000/12809 14301/15007/12816 14304/15010/12819 14308/15014/12823\r\nf 14302/15008/12817 14305/15011/12820 14304/15010/12819 14301/15007/12816\r\nf 14309/15015/12824 14310/15016/12825 14311/15017/12826 14312/15018/12827\r\nf 14296/15002/12811 14295/15001/12810 14311/15017/12826 14310/15016/12825\r\nf 14294/15000/12809 14308/15014/12823 14311/15017/12826 14295/15001/12810\r\nf 14307/15013/12822 14312/15018/12827 14311/15017/12826 14308/15014/12823\r\nf 14313/15019/12828 14314/15020/12829 14315/15021/12830 14316/15022/12831\r\nf 14317/15023/12832 14318/15024/12833 14315/15021/12830 14314/15020/12829\r\nf 14319/15025/12834 14320/15026/12835 14321/15027/12836 14322/15028/12837\r\nf 14313/15019/12828 14316/15022/12831 14320/15026/12835 14319/15025/12834\r\nf 14300/15006/12815 14323/15029/12838 14324/15030/12839 14297/15003/12812\r\nf 14292/14998/12807 14325/15031/12840 14323/15029/12838 14300/15006/12815\r\nf 14313/15019/12828 14319/15025/12834 14323/15029/12838 14325/15031/12840\r\nf 14322/15028/12837 14324/15030/12839 14323/15029/12838 14319/15025/12834\r\nf 14288/14994/12803 14326/15032/12841 14327/15033/12842 14289/14995/12804\r\nf 14317/15023/12832 14314/15020/12829 14327/15033/12842 14326/15032/12841\r\nf 14313/15019/12828 14325/15031/12840 14327/15033/12842 14314/15020/12829\r\nf 14292/14998/12807 14289/14995/12804 14327/15033/12842 14325/15031/12840\r\nf 14214/14920/12729 14217/14923/12732 14328/15034/12843 14329/15035/12844\r\nf 14212/14918/12727 14330/15036/12845 14328/15034/12843 14217/14923/12732\r\nf 14331/15037/12846 14332/15038/12847 14328/15034/12843 14330/15036/12845\r\nf 14333/15039/12848 14329/15035/12844 14328/15034/12843 14332/15038/12847\r\nf 14208/14914/12723 14334/15040/12849 14335/15041/12850 14209/14915/12724\r\nf 14336/15042/12851 14337/15043/12852 14335/15041/12850 14334/15040/12849\r\nf 14331/15037/12846 14330/15036/12845 14335/15041/12850 14337/15043/12852\r\nf 14212/14918/12727 14209/14915/12724 14335/15041/12850 14330/15036/12845\r\nf 13944/14650/12459 13947/14653/12462 14338/15044/12853 14339/15045/12854\r\nf 13942/14648/12457 14340/15046/12855 14338/15044/12853 13947/14653/12462\r\nf 14331/15037/12846 14337/15043/12852 14338/15044/12853 14340/15046/12855\r\nf 14336/15042/12851 14339/15045/12854 14338/15044/12853 14337/15043/12852\r\nf 13938/14644/12453 14341/15047/12856 14342/15048/12857 13939/14645/12454\r\nf 14333/15039/12848 14332/15038/12847 14342/15048/12857 14341/15047/12856\r\nf 14331/15037/12846 14340/15046/12855 14342/15048/12857 14332/15038/12847\r\nf 13942/14648/12457 13939/14645/12454 14342/15048/12857 14340/15046/12855\r\nf 14237/14943/12752 14343/15049/12858 14344/15050/12859 14234/14940/12749\r\nf 14232/14938/12747 14345/15051/12860 14343/15049/12858 14237/14943/12752\r\nf 14346/15052/12861 14347/15053/12862 14343/15049/12858 14345/15051/12860\r\nf 14348/15054/12863 14344/15050/12859 14343/15049/12858 14347/15053/12862\r\nf 14214/14920/12729 14329/15035/12844 14349/15055/12864 14230/14936/12745\r\nf 14333/15039/12848 14350/15056/12865 14349/15055/12864 14329/15035/12844\r\nf 14346/15052/12861 14345/15051/12860 14349/15055/12864 14350/15056/12865\r\nf 14232/14938/12747 14230/14936/12745 14349/15055/12864 14345/15051/12860\r\nf 13938/14644/12453 13965/14671/12480 14351/15057/12866 14341/15047/12856\r\nf 13962/14668/12477 14352/15058/12867 14351/15057/12866 13965/14671/12480\r\nf 14346/15052/12861 14350/15056/12865 14351/15057/12866 14352/15058/12867\r\nf 14333/15039/12848 14341/15047/12856 14351/15057/12866 14350/15056/12865\r\nf 13961/14667/12476 14353/15059/12868 14354/15060/12869 13958/14664/12473\r\nf 14347/15053/12862 14354/15060/12869 14353/15059/12868 14348/15054/12863\r\nf 14346/15052/12861 14352/15058/12867 14354/15060/12869 14347/15053/12862\r\nf 13962/14668/12477 13958/14664/12473 14354/15060/12869 14352/15058/12867\r\nf 14254/14960/12769 14257/14963/12772 14355/15061/12870 14356/15062/12871\r\nf 14252/14958/12767 14357/15063/12872 14355/15061/12870 14257/14963/12772\r\nf 14358/15064/12873 14359/15065/12874 14355/15061/12870 14357/15063/12872\r\nf 14360/15066/12875 14356/15062/12871 14355/15061/12870 14359/15065/12874\r\nf 14248/14954/12763 14361/15067/12876 14362/15068/12877 14249/14955/12764\r\nf 14363/15069/12878 14364/15070/12879 14362/15068/12877 14361/15067/12876\r\nf 14358/15064/12873 14357/15063/12872 14362/15068/12877 14364/15070/12879\r\nf 14252/14958/12767 14249/14955/12764 14362/15068/12877 14357/15063/12872\r\nf 13980/14686/12495 13983/14689/12498 14365/15071/12880 14366/15072/12881\r\nf 13978/14684/12493 14367/15073/12882 14365/15071/12880 13983/14689/12498\r\nf 14358/15064/12873 14364/15070/12879 14365/15071/12880 14367/15073/12882\r\nf 14363/15069/12878 14366/15072/12881 14365/15071/12880 14364/15070/12879\r\nf 13974/14680/12489 14368/15074/12883 14369/15075/12884 13975/14681/12490\r\nf 14360/15066/12875 14359/15065/12874 14369/15075/12884 14368/15074/12883\r\nf 14358/15064/12873 14367/15073/12882 14369/15075/12884 14359/15065/12874\r\nf 13978/14684/12493 13975/14681/12490 14369/15075/12884 14367/15073/12882\r\nf 14309/15015/12824 14312/15018/12827 14370/15076/12885 14371/15077/12886\r\nf 14307/15013/12822 14372/15078/12887 14370/15076/12885 14312/15018/12827\r\nf 14373/15079/12888 14374/15080/12889 14370/15076/12885 14372/15078/12887\r\nf 14375/15081/12890 14371/15077/12886 14370/15076/12885 14374/15080/12889\r\nf 14306/15012/12821 14376/15082/12891 14377/15083/12892 14303/15009/12818\r\nf 14378/15084/12893 14377/15083/12892 14376/15082/12891 14379/15085/12894\r\nf 14373/15079/12888 14372/15078/12887 14377/15083/12892 14378/15084/12893\r\nf 14307/15013/12822 14303/15009/12818 14377/15083/12892 14372/15078/12887\r\nf 14003/14709/12518 14380/15086/12895 14381/15087/12896 14000/14706/12515\r\nf 13998/14704/12513 14382/15088/12897 14380/15086/12895 14003/14709/12518\r\nf 14373/15079/12888 14378/15084/12893 14380/15086/12895 14382/15088/12897\r\nf 14379/15085/12894 14381/15087/12896 14380/15086/12895 14378/15084/12893\r\nf 13994/14700/12509 14383/15089/12898 14384/15090/12899 13995/14701/12510\r\nf 14375/15081/12890 14374/15080/12889 14384/15090/12899 14383/15089/12898\r\nf 14373/15079/12888 14382/15088/12897 14384/15090/12899 14374/15080/12889\r\nf 13998/14704/12513 13995/14701/12510 14384/15090/12899 14382/15088/12897\r\nf 14385/15091/12900 14386/15092/12901 14387/15093/12902 14388/15094/12903\r\nf 14389/15095/12904 14390/15096/12905 14387/15093/12902 14386/15092/12901\r\nf 14391/15097/12906 14392/15098/12907 14393/15099/12908 14394/15100/12909\r\nf 14385/15091/12900 14388/15094/12903 14393/15099/12908 14392/15098/12907\r\nf 14395/15101/12910 14396/15102/12911 14397/15103/12912 14398/15104/12913\r\nf 14399/15105/12914 14400/15106/12915 14397/15103/12912 14396/15102/12911\r\nf 14385/15091/12900 14392/15098/12907 14397/15103/12912 14400/15106/12915\r\nf 14391/15097/12906 14398/15104/12913 14397/15103/12912 14392/15098/12907\r\nf 14401/15107/12916 14402/15108/12917 14403/15109/12918 14404/15110/12919\r\nf 14389/15095/12904 14386/15092/12901 14403/15109/12918 14402/15108/12917\r\nf 14385/15091/12900 14400/15106/12915 14403/15109/12918 14386/15092/12901\r\nf 14399/15105/12914 14404/15110/12919 14403/15109/12918 14400/15106/12915\r\nf 14401/15107/12916 14404/15110/12919 14405/15111/12920 14406/15112/12921\r\nf 14399/15105/12914 14407/15113/12922 14405/15111/12920 14404/15110/12919\r\nf 14408/15114/12923 14409/15115/12924 14405/15111/12920 14407/15113/12922\r\nf 14410/15116/12925 14406/15112/12921 14405/15111/12920 14409/15115/12924\r\nf 14395/15101/12910 14411/15117/12926 14412/15118/12927 14396/15102/12911\r\nf 14413/15119/12928 14414/15120/12929 14412/15118/12927 14411/15117/12926\r\nf 14408/15114/12923 14407/15113/12922 14412/15118/12927 14414/15120/12929\r\nf 14399/15105/12914 14396/15102/12911 14412/15118/12927 14407/15113/12922\r\nf 14415/15121/12930 14416/15122/12931 14417/15123/12932 14418/15124/12933\r\nf 14419/15125/12934 14420/15126/12935 14417/15123/12932 14416/15122/12931\r\nf 14408/15114/12923 14414/15120/12929 14417/15123/12932 14420/15126/12935\r\nf 14413/15119/12928 14418/15124/12933 14417/15123/12932 14414/15120/12929\r\nf 14421/15127/12936 14422/15128/12937 14423/15129/12938 14424/15130/12939\r\nf 14410/15116/12925 14409/15115/12924 14423/15129/12938 14422/15128/12937\r\nf 14408/15114/12923 14420/15126/12935 14423/15129/12938 14409/15115/12924\r\nf 14419/15125/12934 14424/15130/12939 14423/15129/12938 14420/15126/12935\r\nf 14309/15015/12824 14425/15131/12940 14426/15132/12941 14310/15016/12825\r\nf 14427/15133/12942 14428/15134/12943 14426/15132/12941 14425/15131/12940\r\nf 14429/15135/12944 14430/15136/12945 14426/15132/12941 14428/15134/12943\r\nf 14296/15002/12811 14310/15016/12825 14426/15132/12941 14430/15136/12945\r\nf 14415/15121/12930 14418/15124/12933 14431/15137/12946 14432/15138/12947\r\nf 14413/15119/12928 14433/15139/12948 14431/15137/12946 14418/15124/12933\r\nf 14429/15135/12944 14428/15134/12943 14431/15137/12946 14433/15139/12948\r\nf 14427/15133/12942 14432/15138/12947 14431/15137/12946 14428/15134/12943\r\nf 14395/15101/12910 14434/15140/12949 14435/15141/12950 14411/15117/12926\r\nf 14436/15142/12951 14437/15143/12952 14435/15141/12950 14434/15140/12949\r\nf 14429/15135/12944 14433/15139/12948 14435/15141/12950 14437/15143/12952\r\nf 14413/15119/12928 14411/15117/12926 14435/15141/12950 14433/15139/12948\r\nf 14288/14994/12803 14291/14997/12806 14438/15144/12953 14439/15145/12954\r\nf 14296/15002/12811 14430/15136/12945 14438/15144/12953 14291/14997/12806\r\nf 14429/15135/12944 14437/15143/12952 14438/15144/12953 14430/15136/12945\r\nf 14436/15142/12951 14439/15145/12954 14438/15144/12953 14437/15143/12952\r\nf 14288/14994/12803 14439/15145/12954 14440/15146/12955 14326/15032/12841\r\nf 14436/15142/12951 14441/15147/12956 14440/15146/12955 14439/15145/12954\r\nf 14442/15148/12957 14443/15149/12958 14440/15146/12955 14441/15147/12956\r\nf 14317/15023/12832 14326/15032/12841 14440/15146/12955 14443/15149/12958\r\nf 14395/15101/12910 14398/15104/12913 14444/15150/12959 14434/15140/12949\r\nf 14391/15097/12906 14445/15151/12960 14444/15150/12959 14398/15104/12913\r\nf 14442/15148/12957 14441/15147/12956 14444/15150/12959 14445/15151/12960\r\nf 14436/15142/12951 14434/15140/12949 14444/15150/12959 14441/15147/12956\r\nf 14442/15148/12957 14445/15151/12960 14446/15152/12961 14447/15153/12962\r\nf 14391/15097/12906 14394/15100/12909 14446/15152/12961 14445/15151/12960\r\nf 14317/15023/12832 14443/15149/12958 14448/15154/12963 14318/15024/12833\r\nf 14442/15148/12957 14447/15153/12962 14448/15154/12963 14443/15149/12958\r\nf 14421/15127/12936 14424/15130/12939 14449/15155/12964 14450/15156/12965\r\nf 14419/15125/12934 14451/15157/12966 14449/15155/12964 14424/15130/12939\r\nf 14452/15158/12967 14453/15159/12968 14449/15155/12964 14451/15157/12966\r\nf 14454/15160/12969 14450/15156/12965 14449/15155/12964 14453/15159/12968\r\nf 14415/15121/12930 14455/15161/12970 14456/15162/12971 14416/15122/12931\r\nf 14457/15163/12972 14458/15164/12973 14456/15162/12971 14455/15161/12970\r\nf 14452/15158/12967 14451/15157/12966 14456/15162/12971 14458/15164/12973\r\nf 14419/15125/12934 14416/15122/12931 14456/15162/12971 14451/15157/12966\r\nf 14084/14790/12599 14087/14793/12602 14459/15165/12974 14460/15166/12975\r\nf 14082/14788/12597 14461/15167/12976 14459/15165/12974 14087/14793/12602\r\nf 14452/15158/12967 14458/15164/12973 14459/15165/12974 14461/15167/12976\r\nf 14457/15163/12972 14460/15166/12975 14459/15165/12974 14458/15164/12973\r\nf 14078/14784/12593 14462/15168/12977 14463/15169/12978 14079/14785/12594\r\nf 14454/15160/12969 14453/15159/12968 14463/15169/12978 14462/15168/12977\r\nf 14452/15158/12967 14461/15167/12976 14463/15169/12978 14453/15159/12968\r\nf 14082/14788/12597 14079/14785/12594 14463/15169/12978 14461/15167/12976\r\nf 14254/14960/12769 14464/15170/12979 14465/15171/12980 14255/14961/12770\r\nf 14466/15172/12981 14467/15173/12982 14465/15171/12980 14464/15170/12979\r\nf 14468/15174/12983 14469/15175/12984 14465/15171/12980 14467/15173/12982\r\nf 14243/14949/12758 14255/14961/12770 14465/15171/12980 14469/15175/12984\r\nf 14208/14914/12723 14211/14917/12726 14470/15176/12985 14471/15177/12986\r\nf 14206/14912/12721 14472/15178/12987 14470/15176/12985 14211/14917/12726\r\nf 14468/15174/12983 14467/15173/12982 14470/15176/12985 14472/15178/12987\r\nf 14466/15172/12981 14471/15177/12986 14470/15176/12985 14467/15173/12982\r\nf 14202/14908/12717 14281/14987/12796 14473/15179/12988 14203/14909/12718\r\nf 14275/14981/12790 14474/15180/12989 14473/15179/12988 14281/14987/12796\r\nf 14468/15174/12983 14472/15178/12987 14473/15179/12988 14474/15180/12989\r\nf 14206/14912/12721 14203/14909/12718 14473/15179/12988 14472/15178/12987\r\nf 14189/14895/12704 14239/14945/12754 14475/15181/12990 14273/14979/12788\r\nf 14243/14949/12758 14469/15175/12984 14475/15181/12990 14239/14945/12754\r\nf 14468/15174/12983 14474/15180/12989 14475/15181/12990 14469/15175/12984\r\nf 14275/14981/12790 14273/14979/12788 14475/15181/12990 14474/15180/12989\r\nf 14476/15182/12991 14477/15183/12992 14478/15184/12993 14479/15185/12994\r\nf 14263/14969/12778 14272/14978/12787 14478/15184/12993 14477/15183/12992\r\nf 14282/14988/12797 14480/15186/12995 14481/15187/12996 14286/14992/12801\r\nf 14476/15182/12991 14479/15185/12994 14481/15187/12996 14480/15186/12995\r\nf 14202/14908/12717 14205/14911/12720 14482/15188/12997 14279/14985/12794\r\nf 14197/14903/12712 14483/15189/12998 14482/15188/12997 14205/14911/12720\r\nf 14476/15182/12991 14480/15186/12995 14482/15188/12997 14483/15189/12998\r\nf 14282/14988/12797 14279/14985/12794 14482/15188/12997 14480/15186/12995\r\nf 14193/14899/12708 14259/14965/12774 14484/15190/12999 14194/14900/12709\r\nf 14263/14969/12778 14477/15183/12992 14484/15190/12999 14259/14965/12774\r\nf 14476/15182/12991 14483/15189/12998 14484/15190/12999 14477/15183/12992\r\nf 14197/14903/12712 14194/14900/12709 14484/15190/12999 14483/15189/12998\r\nf 14401/15107/12916 14485/15191/13000 14486/15192/13001 14402/15108/12917\r\nf 14487/15193/13002 14488/15194/13003 14486/15192/13001 14485/15191/13000\r\nf 14489/15195/13004 14490/15196/13005 14486/15192/13001 14488/15194/13003\r\nf 14389/15095/12904 14402/15108/12917 14486/15192/13001 14490/15196/13005\r\nf 14183/14889/12698 14186/14892/12701 14491/15197/13006 14492/15198/13007\r\nf 14179/14885/12694 14493/15199/13008 14491/15197/13006 14186/14892/12701\r\nf 14489/15195/13004 14488/15194/13003 14491/15197/13006 14493/15199/13008\r\nf 14487/15193/13002 14492/15198/13007 14491/15197/13006 14488/15194/13003\r\nf 14489/15195/13004 14493/15199/13008 14494/15200/13009 14495/15201/13010\r\nf 14179/14885/12694 14182/14888/12697 14494/15200/13009 14493/15199/13008\r\nf 14389/15095/12904 14490/15196/13005 14496/15202/13011 14390/15096/12905\r\nf 14489/15195/13004 14495/15201/13010 14496/15202/13011 14490/15196/13005\r\nf 14421/15127/12936 14497/15203/13012 14498/15204/13013 14422/15128/12937\r\nf 14499/15205/13014 14500/15206/13015 14498/15204/13013 14497/15203/13012\r\nf 14501/15207/13016 14502/15208/13017 14498/15204/13013 14500/15206/13015\r\nf 14410/15116/12925 14422/15128/12937 14498/15204/13013 14502/15208/13017\r\nf 14248/14954/12763 14251/14957/12766 14503/15209/13018 14504/15210/13019\r\nf 14246/14952/12761 14505/15211/13020 14503/15209/13018 14251/14957/12766\r\nf 14501/15207/13016 14500/15206/13015 14503/15209/13018 14505/15211/13020\r\nf 14499/15205/13014 14504/15210/13019 14503/15209/13018 14500/15206/13015\r\nf 14183/14889/12698 14492/15198/13007 14506/15212/13021 14244/14950/12759\r\nf 14487/15193/13002 14507/15213/13022 14506/15212/13021 14492/15198/13007\r\nf 14501/15207/13016 14505/15211/13020 14506/15212/13021 14507/15213/13022\r\nf 14246/14952/12761 14244/14950/12759 14506/15212/13021 14505/15211/13020\r\nf 14401/15107/12916 14406/15112/12921 14508/15214/13023 14485/15191/13000\r\nf 14410/15116/12925 14502/15208/13017 14508/15214/13023 14406/15112/12921\r\nf 14501/15207/13016 14507/15213/13022 14508/15214/13023 14502/15208/13017\r\nf 14487/15193/13002 14485/15191/13000 14508/15214/13023 14507/15213/13022\r\nf 13994/14700/12509 14138/14844/12653 14509/15215/13024 14383/15089/12898\r\nf 14142/14848/12657 14510/15216/13025 14509/15215/13024 14138/14844/12653\r\nf 14511/15217/13026 14512/15218/13027 14509/15215/13024 14510/15216/13025\r\nf 14375/15081/12890 14383/15089/12898 14509/15215/13024 14512/15218/13027\r\nf 14084/14790/12599 14460/15166/12975 14513/15219/13028 14147/14853/12662\r\nf 14457/15163/12972 14514/15220/13029 14513/15219/13028 14460/15166/12975\r\nf 14511/15217/13026 14510/15216/13025 14513/15219/13028 14514/15220/13029\r\nf 14142/14848/12657 14147/14853/12662 14513/15219/13028 14510/15216/13025\r\nf 14415/15121/12930 14432/15138/12947 14515/15221/13030 14455/15161/12970\r\nf 14427/15133/12942 14516/15222/13031 14515/15221/13030 14432/15138/12947\r\nf 14511/15217/13026 14514/15220/13029 14515/15221/13030 14516/15222/13031\r\nf 14457/15163/12972 14455/15161/12970 14515/15221/13030 14514/15220/13029\r\nf 14309/15015/12824 14371/15077/12886 14517/15223/13032 14425/15131/12940\r\nf 14375/15081/12890 14512/15218/13027 14517/15223/13032 14371/15077/12886\r\nf 14511/15217/13026 14516/15222/13031 14517/15223/13032 14512/15218/13027\r\nf 14427/15133/12942 14425/15131/12940 14517/15223/13032 14516/15222/13031\r\nf 13974/14680/12489 14150/14856/12665 14518/15224/13033 14368/15074/12883\r\nf 14154/14860/12669 14519/15225/13034 14518/15224/13033 14150/14856/12665\r\nf 14520/15226/13035 14521/15227/13036 14518/15224/13033 14519/15225/13034\r\nf 14360/15066/12875 14368/15074/12883 14518/15224/13033 14521/15227/13036\r\nf 13944/14650/12459 14339/15045/12854 14522/15228/13037 14159/14865/12674\r\nf 14336/15042/12851 14523/15229/13038 14522/15228/13037 14339/15045/12854\r\nf 14520/15226/13035 14519/15225/13034 14522/15228/13037 14523/15229/13038\r\nf 14154/14860/12669 14159/14865/12674 14522/15228/13037 14519/15225/13034\r\nf 14208/14914/12723 14471/15177/12986 14524/15230/13039 14334/15040/12849\r\nf 14466/15172/12981 14525/15231/13040 14524/15230/13039 14471/15177/12986\r\nf 14520/15226/13035 14523/15229/13038 14524/15230/13039 14525/15231/13040\r\nf 14336/15042/12851 14334/15040/12849 14524/15230/13039 14523/15229/13038\r\nf 14254/14960/12769 14356/15062/12871 14526/15232/13041 14464/15170/12979\r\nf 14360/15066/12875 14521/15227/13036 14526/15232/13041 14356/15062/12871\r\nf 14520/15226/13035 14525/15231/13040 14526/15232/13041 14521/15227/13036\r\nf 14466/15172/12981 14464/15170/12979 14526/15232/13041 14525/15231/13040\r\nf 14078/14784/12593 14162/14868/12677 14527/15233/13042 14462/15168/12977\r\nf 14166/14872/12681 14528/15234/13043 14527/15233/13042 14162/14868/12677\r\nf 14529/15235/13044 14530/15236/13045 14527/15233/13042 14528/15234/13043\r\nf 14454/15160/12969 14462/15168/12977 14527/15233/13042 14530/15236/13045\r\nf 13980/14686/12495 14366/15072/12881 14531/15237/13046 14171/14877/12686\r\nf 14363/15069/12878 14532/15238/13047 14531/15237/13046 14366/15072/12881\r\nf 14529/15235/13044 14528/15234/13043 14531/15237/13046 14532/15238/13047\r\nf 14166/14872/12681 14171/14877/12686 14531/15237/13046 14528/15234/13043\r\nf 14248/14954/12763 14504/15210/13019 14533/15239/13048 14361/15067/12876\r\nf 14499/15205/13014 14534/15240/13049 14533/15239/13048 14504/15210/13019\r\nf 14529/15235/13044 14532/15238/13047 14533/15239/13048 14534/15240/13049\r\nf 14363/15069/12878 14361/15067/12876 14533/15239/13048 14532/15238/13047\r\nf 14421/15127/12936 14450/15156/12965 14535/15241/13050 14497/15203/13012\r\nf 14454/15160/12969 14530/15236/13045 14535/15241/13050 14450/15156/12965\r\nf 14529/15235/13044 14534/15240/13049 14535/15241/13050 14530/15236/13045\r\nf 14499/15205/13014 14497/15203/13012 14535/15241/13050 14534/15240/13049\r\nf 14536/15242/13051 14537/15243/13052 13823/14529/12338 13822/14528/12337\r\nf 14538/15244/13053 14539/15245/13054 14537/15243/13052 14536/15242/13051\r\nf 14540/15246/13055 14541/15247/13056 14537/15243/13052 14539/15245/13054\r\nf 13827/14533/12342 13823/14529/12338 14537/15243/13052 14541/15247/13056\r\nf 14542/15248/13057 14543/15249/13058 14544/15250/13059 14545/15251/13060\r\nf 14546/15252/13061 14547/15253/13062 14544/15250/13059 14543/15249/13058\r\nf 14540/15246/13055 14539/15245/13054 14544/15250/13059 14547/15253/13062\r\nf 14538/15244/13053 14545/15251/13060 14544/15250/13059 14539/15245/13054\r\nf 14548/15254/13063 14549/15255/13064 14550/15256/13065 14551/15257/13066\r\nf 14552/15258/13067 14553/15259/13068 14550/15256/13065 14549/15255/13064\r\nf 14540/15246/13055 14547/15253/13062 14550/15256/13065 14553/15259/13068\r\nf 14546/15252/13061 14551/15257/13066 14550/15256/13065 14547/15253/13062\r\nf 13834/14540/12349 13833/14539/12348 14554/15260/13069 14555/15261/13070\r\nf 14541/15247/13056 14554/15260/13069 13833/14539/12348 13827/14533/12342\r\nf 14540/15246/13055 14553/15259/13068 14554/15260/13069 14541/15247/13056\r\nf 14552/15258/13067 14555/15261/13070 14554/15260/13069 14553/15259/13068\r\nf 14556/15262/13071 14557/15263/13072 14558/15264/13073 14559/15265/13074\r\nf 13874/14580/12389 13873/14579/12388 14558/15264/13073 14557/15263/13072\r\nf 14560/15266/13075 14561/15267/13076 14562/15268/13077 14563/15269/13078\r\nf 14556/15262/13071 14559/15265/13074 14562/15268/13077 14561/15267/13076\r\nf 14542/15248/13057 14545/15251/13060 14564/15270/13079 14565/15271/13080\r\nf 14538/15244/13053 14566/15272/13081 14564/15270/13079 14545/15251/13060\r\nf 14556/15262/13071 14561/15267/13076 14564/15270/13079 14566/15272/13081\r\nf 14560/15266/13075 14565/15271/13080 14564/15270/13079 14561/15267/13076\r\nf 13822/14528/12337 13876/14582/12391 14567/15273/13082 14536/15242/13051\r\nf 14557/15263/13072 14567/15273/13082 13876/14582/12391 13874/14580/12389\r\nf 14556/15262/13071 14566/15272/13081 14567/15273/13082 14557/15263/13072\r\nf 14538/15244/13053 14536/15242/13051 14567/15273/13082 14566/15272/13081\r\nf 14542/15248/13057 14565/15271/13080 14568/15274/13083 14569/15275/13084\r\nf 14560/15266/13075 14570/15276/13085 14568/15274/13083 14565/15271/13080\r\nf 14571/15277/13086 14572/15278/13087 14568/15274/13083 14570/15276/13085\r\nf 14573/15279/13088 14569/15275/13084 14568/15274/13083 14572/15278/13087\r\nf 14571/15277/13086 14570/15276/13085 14574/15280/13089 14575/15281/13090\r\nf 14560/15266/13075 14563/15269/13078 14574/15280/13089 14570/15276/13085\r\nf 14576/15282/13091 14577/15283/13092 14578/15284/13093 14579/15285/13094\r\nf 14571/15277/13086 14575/15281/13090 14578/15284/13093 14577/15283/13092\r\nf 14580/15286/13095 14581/15287/13096 14582/15288/13097 14583/15289/13098\r\nf 14573/15279/13088 14572/15278/13087 14582/15288/13097 14581/15287/13096\r\nf 14571/15277/13086 14577/15283/13092 14582/15288/13097 14572/15278/13087\r\nf 14576/15282/13091 14583/15289/13098 14582/15288/13097 14577/15283/13092\r\nf 14584/15290/13099 14585/15291/13100 14586/15292/13101 14587/15293/13102\r\nf 14588/15294/13103 14589/15295/13104 14586/15292/13101 14585/15291/13100\r\nf 14590/15296/13105 14591/15297/13106 14592/15298/13107 14593/15299/13108\r\nf 14584/15290/13099 14587/15293/13102 14592/15298/13107 14591/15297/13106\r\nf 14594/15300/13109 14595/15301/13110 14596/15302/13111 14597/15303/13112\r\nf 14598/15304/13113 14599/15305/13114 14596/15302/13111 14595/15301/13110\r\nf 14584/15290/13099 14591/15297/13106 14596/15302/13111 14599/15305/13114\r\nf 14590/15296/13105 14597/15303/13112 14596/15302/13111 14591/15297/13106\r\nf 14600/15306/13115 14601/15307/13116 14602/15308/13117 14603/15309/13118\r\nf 14588/15294/13103 14585/15291/13100 14602/15308/13117 14601/15307/13116\r\nf 14584/15290/13099 14599/15305/13114 14602/15308/13117 14585/15291/13100\r\nf 14598/15304/13113 14603/15309/13118 14602/15308/13117 14599/15305/13114\r\nf 14604/15310/13119 14605/15311/13120 14606/15312/13121 14607/15313/13122\r\nf 14608/15314/13123 14609/15315/13124 14606/15312/13121 14605/15311/13120\r\nf 14610/15316/13125 14611/15317/13126 14606/15312/13121 14609/15315/13124\r\nf 14612/15318/13127 14607/15313/13122 14606/15312/13121 14611/15317/13126\r\nf 14610/15316/13125 14609/15315/13124 14613/15319/13128 14614/15320/13129\r\nf 14608/15314/13123 14615/15321/13130 14613/15319/13128 14609/15315/13124\r\nf 14588/15294/13103 14616/15322/13131 14617/15323/13132 14589/15295/13104\r\nf 14610/15316/13125 14614/15320/13129 14617/15323/13132 14616/15322/13131\r\nf 14600/15306/13115 14618/15324/13133 14619/15325/13134 14601/15307/13116\r\nf 14612/15318/13127 14611/15317/13126 14619/15325/13134 14618/15324/13133\r\nf 14610/15316/13125 14616/15322/13131 14619/15325/13134 14611/15317/13126\r\nf 14588/15294/13103 14601/15307/13116 14619/15325/13134 14616/15322/13131\r\nf 14620/15326/13135 14621/15327/13136 14622/15328/13137 14623/15329/13138\r\nf 14624/15330/13139 14625/15331/13140 14622/15328/13137 14621/15327/13136\r\nf 14626/15332/13141 14627/15333/13142 14622/15328/13137 14625/15331/13140\r\nf 14628/15334/13143 14623/15329/13138 14622/15328/13137 14627/15333/13142\r\nf 14604/15310/13119 14607/15313/13122 14629/15335/13144 14630/15336/13145\r\nf 14612/15318/13127 14631/15337/13146 14629/15335/13144 14607/15313/13122\r\nf 14626/15332/13141 14625/15331/13140 14629/15335/13144 14631/15337/13146\r\nf 14624/15330/13139 14630/15336/13145 14629/15335/13144 14625/15331/13140\r\nf 14600/15306/13115 14632/15338/13147 14633/15339/13148 14618/15324/13133\r\nf 14634/15340/13149 14635/15341/13150 14633/15339/13148 14632/15338/13147\r\nf 14626/15332/13141 14631/15337/13146 14633/15339/13148 14635/15341/13150\r\nf 14612/15318/13127 14618/15324/13133 14633/15339/13148 14631/15337/13146\r\nf 14636/15342/13151 14637/15343/13152 14638/15344/13153 14639/15345/13154\r\nf 14628/15334/13143 14627/15333/13142 14638/15344/13153 14637/15343/13152\r\nf 14626/15332/13141 14635/15341/13150 14638/15344/13153 14627/15333/13142\r\nf 14634/15340/13149 14639/15345/13154 14638/15344/13153 14635/15341/13150\r\nf 14636/15342/13151 14639/15345/13154 14640/15346/13155 14641/15347/13156\r\nf 14634/15340/13149 14642/15348/13157 14640/15346/13155 14639/15345/13154\r\nf 14643/15349/13158 14644/15350/13159 14640/15346/13155 14642/15348/13157\r\nf 14645/15351/13160 14641/15347/13156 14640/15346/13155 14644/15350/13159\r\nf 14600/15306/13115 14603/15309/13118 14646/15352/13161 14632/15338/13147\r\nf 14598/15304/13113 14647/15353/13162 14646/15352/13161 14603/15309/13118\r\nf 14643/15349/13158 14642/15348/13157 14646/15352/13161 14647/15353/13162\r\nf 14634/15340/13149 14632/15338/13147 14646/15352/13161 14642/15348/13157\r\nf 14594/15300/13109 14648/15354/13163 14649/15355/13164 14595/15301/13110\r\nf 14650/15356/13165 14651/15357/13166 14649/15355/13164 14648/15354/13163\r\nf 14643/15349/13158 14647/15353/13162 14649/15355/13164 14651/15357/13166\r\nf 14598/15304/13113 14595/15301/13110 14649/15355/13164 14647/15353/13162\r\nf 14652/15358/13167 14653/15359/13168 14654/15360/13169 14655/15361/13170\r\nf 14645/15351/13160 14644/15350/13159 14654/15360/13169 14653/15359/13168\r\nf 14643/15349/13158 14651/15357/13166 14654/15360/13169 14644/15350/13159\r\nf 14650/15356/13165 14655/15361/13170 14654/15360/13169 14651/15357/13166\r\nf 14542/15248/13057 14569/15275/13084 14656/15362/13171 14543/15249/13058\r\nf 14573/15279/13088 14657/15363/13172 14656/15362/13171 14569/15275/13084\r\nf 14658/15364/13173 14659/15365/13174 14656/15362/13171 14657/15363/13172\r\nf 14546/15252/13061 14543/15249/13058 14656/15362/13171 14659/15365/13174\r\nf 14580/15286/13095 14660/15366/13175 14661/15367/13176 14581/15287/13096\r\nf 14662/15368/13177 14663/15369/13178 14661/15367/13176 14660/15366/13175\r\nf 14658/15364/13173 14657/15363/13172 14661/15367/13176 14663/15369/13178\r\nf 14573/15279/13088 14581/15287/13096 14661/15367/13176 14657/15363/13172\r\nf 14664/15370/13179 14665/15371/13180 14666/15372/13181 14667/15373/13182\r\nf 14668/15374/13183 14669/15375/13184 14666/15372/13181 14665/15371/13180\r\nf 14658/15364/13173 14663/15369/13178 14666/15372/13181 14669/15375/13184\r\nf 14662/15368/13177 14667/15373/13182 14666/15372/13181 14663/15369/13178\r\nf 14548/15254/13063 14551/15257/13066 14670/15376/13185 14671/15377/13186\r\nf 14546/15252/13061 14659/15365/13174 14670/15376/13185 14551/15257/13066\r\nf 14658/15364/13173 14669/15375/13184 14670/15376/13185 14659/15365/13174\r\nf 14668/15374/13183 14671/15377/13186 14670/15376/13185 14669/15375/13184\r\nf 14652/15358/13167 14672/15378/13187 14673/15379/13188 14653/15359/13168\r\nf 14674/15380/13189 14675/15381/13190 14673/15379/13188 14672/15378/13187\r\nf 14676/15382/13191 14677/15383/13192 14673/15379/13188 14675/15381/13190\r\nf 14645/15351/13160 14653/15359/13168 14673/15379/13188 14677/15383/13192\r\nf 14678/15384/13193 14679/15385/13194 14680/15386/13195 14681/15387/13196\r\nf 14682/15388/13197 14683/15389/13198 14680/15386/13195 14679/15385/13194\r\nf 14676/15382/13191 14675/15381/13190 14680/15386/13195 14683/15389/13198\r\nf 14674/15380/13189 14681/15387/13196 14680/15386/13195 14675/15381/13190\r\nf 14684/15390/13199 14685/15391/13200 14686/15392/13201 14687/15393/13202\r\nf 14688/15394/13203 14689/15395/13204 14686/15392/13201 14685/15391/13200\r\nf 14676/15382/13191 14683/15389/13198 14686/15392/13201 14689/15395/13204\r\nf 14682/15388/13197 14687/15393/13202 14686/15392/13201 14683/15389/13198\r\nf 14636/15342/13151 14641/15347/13156 14690/15396/13205 14691/15397/13206\r\nf 14645/15351/13160 14677/15383/13192 14690/15396/13205 14641/15347/13156\r\nf 14676/15382/13191 14689/15395/13204 14690/15396/13205 14677/15383/13192\r\nf 14688/15394/13203 14691/15397/13206 14690/15396/13205 14689/15395/13204\r\nf 14548/15254/13063 14671/15377/13186 14692/15398/13207 14693/15399/13208\r\nf 14668/15374/13183 14694/15400/13209 14692/15398/13207 14671/15377/13186\r\nf 14695/15401/13210 14696/15402/13211 14692/15398/13207 14694/15400/13209\r\nf 14697/15403/13212 14693/15399/13208 14692/15398/13207 14696/15402/13211\r\nf 14664/15370/13179 14698/15404/13213 14699/15405/13214 14665/15371/13180\r\nf 14700/15406/13215 14701/15407/13216 14699/15405/13214 14698/15404/13213\r\nf 14695/15401/13210 14694/15400/13209 14699/15405/13214 14701/15407/13216\r\nf 14668/15374/13183 14665/15371/13180 14699/15405/13214 14694/15400/13209\r\nf 14702/15408/13217 14703/15409/13218 14704/15410/13219 14705/15411/13220\r\nf 14706/15412/13221 14707/15413/13222 14704/15410/13219 14703/15409/13218\r\nf 14695/15401/13210 14701/15407/13216 14704/15410/13219 14707/15413/13222\r\nf 14700/15406/13215 14705/15411/13220 14704/15410/13219 14701/15407/13216\r\nf 14708/15414/13223 14709/15415/13224 14710/15416/13225 14711/15417/13226\r\nf 14697/15403/13212 14696/15402/13211 14710/15416/13225 14709/15415/13224\r\nf 14695/15401/13210 14707/15413/13222 14710/15416/13225 14696/15402/13211\r\nf 14706/15412/13221 14711/15417/13226 14710/15416/13225 14707/15413/13222\r\nf 14712/15418/13227 14713/15419/13228 14714/15420/13229 14715/15421/13230\r\nf 14716/15422/13231 14717/15423/13232 14714/15420/13229 14713/15419/13228\r\nf 14718/15424/13233 14719/15425/13234 14720/15426/13235 14721/15427/13236\r\nf 14722/15428/13237 14723/15429/13238 14720/15426/13235 14719/15425/13234\r\nf 14716/15422/13231 14713/15419/13228 14720/15426/13235 14723/15429/13238\r\nf 14712/15418/13227 14721/15427/13236 14720/15426/13235 14713/15419/13228\r\nf 14724/15430/13239 14725/15431/13240 14726/15432/13241 14727/15433/13242\r\nf 14728/15434/13243 14729/15435/13244 14726/15432/13241 14725/15431/13240\r\nf 14716/15422/13231 14723/15429/13238 14726/15432/13241 14729/15435/13244\r\nf 14722/15428/13237 14727/15433/13242 14726/15432/13241 14723/15429/13238\r\nf 14716/15422/13231 14729/15435/13244 14730/15436/13245 14717/15423/13232\r\nf 14728/15434/13243 14731/15437/13246 14730/15436/13245 14729/15435/13244\r\nf 14724/15430/13239 14727/15433/13242 14732/15438/13247 14733/15439/13248\r\nf 14722/15428/13237 14734/15440/13249 14732/15438/13247 14727/15433/13242\r\nf 14735/15441/13250 14736/15442/13251 14732/15438/13247 14734/15440/13249\r\nf 14737/15443/13252 14733/15439/13248 14732/15438/13247 14736/15442/13251\r\nf 14718/15424/13233 14738/15444/13253 14739/15445/13254 14719/15425/13234\r\nf 14740/15446/13255 14741/15447/13256 14739/15445/13254 14738/15444/13253\r\nf 14735/15441/13250 14734/15440/13249 14739/15445/13254 14741/15447/13256\r\nf 14722/15428/13237 14719/15425/13234 14739/15445/13254 14734/15440/13249\r\nf 14742/15448/13257 14743/15449/13258 14744/15450/13259 14745/15451/13260\r\nf 14746/15452/13261 14747/15453/13262 14744/15450/13259 14743/15449/13258\r\nf 14735/15441/13250 14741/15447/13256 14744/15450/13259 14747/15453/13262\r\nf 14740/15446/13255 14745/15451/13260 14744/15450/13259 14741/15447/13256\r\nf 14748/15454/13263 14749/15455/13264 14750/15456/13265 14751/15457/13266\r\nf 14737/15443/13252 14736/15442/13251 14750/15456/13265 14749/15455/13264\r\nf 14735/15441/13250 14747/15453/13262 14750/15456/13265 14736/15442/13251\r\nf 14746/15452/13261 14751/15457/13266 14750/15456/13265 14747/15453/13262\r\nf 14742/15448/13257 14752/15458/13267 14753/15459/13268 14743/15449/13258\r\nf 14754/15460/13269 14755/15461/13270 14753/15459/13268 14752/15458/13267\r\nf 14756/15462/13271 14757/15463/13272 14753/15459/13268 14755/15461/13270\r\nf 14746/15452/13261 14743/15449/13258 14753/15459/13268 14757/15463/13272\r\nf 14758/15464/13273 14759/15465/13274 14760/15466/13275 14761/15467/13276\r\nf 14762/15468/13277 14763/15469/13278 14760/15466/13275 14759/15465/13274\r\nf 14756/15462/13271 14755/15461/13270 14760/15466/13275 14763/15469/13278\r\nf 14754/15460/13269 14761/15467/13276 14760/15466/13275 14755/15461/13270\r\nf 14764/15470/13279 14765/15471/13280 14766/15472/13281 14767/15473/13282\r\nf 14768/15474/13283 14769/15475/13284 14766/15472/13281 14765/15471/13280\r\nf 14756/15462/13271 14763/15469/13278 14766/15472/13281 14769/15475/13284\r\nf 14762/15468/13277 14767/15473/13282 14766/15472/13281 14763/15469/13278\r\nf 14748/15454/13263 14751/15457/13266 14770/15476/13285 14771/15477/13286\r\nf 14746/15452/13261 14757/15463/13272 14770/15476/13285 14751/15457/13266\r\nf 14756/15462/13271 14769/15475/13284 14770/15476/13285 14757/15463/13272\r\nf 14768/15474/13283 14771/15477/13286 14770/15476/13285 14769/15475/13284\r\nf 14772/15478/13287 14773/15479/13288 14774/15480/13289 14775/15481/13290\r\nf 14576/15282/13091 14579/15285/13094 14774/15480/13289 14773/15479/13288\r\nf 14728/15434/13243 14776/15482/13291 14777/15483/13292 14731/15437/13246\r\nf 14772/15478/13287 14775/15481/13290 14777/15483/13292 14776/15482/13291\r\nf 14724/15430/13239 14778/15484/13293 14779/15485/13294 14725/15431/13240\r\nf 14780/15486/13295 14781/15487/13296 14779/15485/13294 14778/15484/13293\r\nf 14772/15478/13287 14776/15482/13291 14779/15485/13294 14781/15487/13296\r\nf 14728/15434/13243 14725/15431/13240 14779/15485/13294 14776/15482/13291\r\nf 14580/15286/13095 14583/15289/13098 14782/15488/13297 14783/15489/13298\r\nf 14576/15282/13091 14773/15479/13288 14782/15488/13297 14583/15289/13098\r\nf 14772/15478/13287 14781/15487/13296 14782/15488/13297 14773/15479/13288\r\nf 14780/15486/13295 14783/15489/13298 14782/15488/13297 14781/15487/13296\r\nf 14580/15286/13095 14783/15489/13298 14784/15490/13299 14660/15366/13175\r\nf 14780/15486/13295 14785/15491/13300 14784/15490/13299 14783/15489/13298\r\nf 14786/15492/13301 14787/15493/13302 14784/15490/13299 14785/15491/13300\r\nf 14662/15368/13177 14660/15366/13175 14784/15490/13299 14787/15493/13302\r\nf 14724/15430/13239 14733/15439/13248 14788/15494/13303 14778/15484/13293\r\nf 14737/15443/13252 14789/15495/13304 14788/15494/13303 14733/15439/13248\r\nf 14786/15492/13301 14785/15491/13300 14788/15494/13303 14789/15495/13304\r\nf 14780/15486/13295 14778/15484/13293 14788/15494/13303 14785/15491/13300\r\nf 14748/15454/13263 14790/15496/13305 14791/15497/13306 14749/15455/13264\r\nf 14792/15498/13307 14793/15499/13308 14791/15497/13306 14790/15496/13305\r\nf 14786/15492/13301 14789/15495/13304 14791/15497/13306 14793/15499/13308\r\nf 14737/15443/13252 14749/15455/13264 14791/15497/13306 14789/15495/13304\r\nf 14664/15370/13179 14667/15373/13182 14794/15500/13309 14795/15501/13310\r\nf 14662/15368/13177 14787/15493/13302 14794/15500/13309 14667/15373/13182\r\nf 14786/15492/13301 14793/15499/13308 14794/15500/13309 14787/15493/13302\r\nf 14792/15498/13307 14795/15501/13310 14794/15500/13309 14793/15499/13308\r\nf 14664/15370/13179 14795/15501/13310 14796/15502/13311 14698/15404/13213\r\nf 14792/15498/13307 14797/15503/13312 14796/15502/13311 14795/15501/13310\r\nf 14798/15504/13313 14799/15505/13314 14796/15502/13311 14797/15503/13312\r\nf 14700/15406/13215 14698/15404/13213 14796/15502/13311 14799/15505/13314\r\nf 14748/15454/13263 14771/15477/13286 14800/15506/13315 14790/15496/13305\r\nf 14768/15474/13283 14801/15507/13316 14800/15506/13315 14771/15477/13286\r\nf 14798/15504/13313 14797/15503/13312 14800/15506/13315 14801/15507/13316\r\nf 14792/15498/13307 14790/15496/13305 14800/15506/13315 14797/15503/13312\r\nf 14764/15470/13279 14802/15508/13317 14803/15509/13318 14765/15471/13280\r\nf 14804/15510/13319 14805/15511/13320 14803/15509/13318 14802/15508/13317\r\nf 14798/15504/13313 14801/15507/13316 14803/15509/13318 14805/15511/13320\r\nf 14768/15474/13283 14765/15471/13280 14803/15509/13318 14801/15507/13316\r\nf 14702/15408/13217 14705/15411/13220 14806/15512/13321 14807/15513/13322\r\nf 14700/15406/13215 14799/15505/13314 14806/15512/13321 14705/15411/13220\r\nf 14798/15504/13313 14805/15511/13320 14806/15512/13321 14799/15505/13314\r\nf 14804/15510/13319 14807/15513/13322 14806/15512/13321 14805/15511/13320\r\nf 14808/15514/13323 14809/15515/13324 14810/15516/13325 14811/15517/13326\r\nf 14812/15518/13327 14813/15519/13328 14810/15516/13325 14809/15515/13324\r\nf 14814/15520/13329 14815/15521/13330 14816/15522/13331 14817/15523/13332\r\nf 14818/15524/13333 14819/15525/13334 14816/15522/13331 14815/15521/13330\r\nf 14812/15518/13327 14809/15515/13324 14816/15522/13331 14819/15525/13334\r\nf 14808/15514/13323 14817/15523/13332 14816/15522/13331 14809/15515/13324\r\nf 14718/15424/13233 14721/15427/13236 14820/15526/13335 14821/15527/13336\r\nf 14712/15418/13227 14822/15528/13337 14820/15526/13335 14721/15427/13236\r\nf 14812/15518/13327 14819/15525/13334 14820/15526/13335 14822/15528/13337\r\nf 14818/15524/13333 14821/15527/13336 14820/15526/13335 14819/15525/13334\r\nf 14812/15518/13327 14822/15528/13337 14823/15529/13338 14813/15519/13328\r\nf 14712/15418/13227 14715/15421/13230 14823/15529/13338 14822/15528/13337\r\nf 14814/15520/13329 14824/15530/13339 14825/15531/13340 14815/15521/13330\r\nf 14826/15532/13341 14827/15533/13342 14825/15531/13340 14824/15530/13339\r\nf 14828/15534/13343 14829/15535/13344 14825/15531/13340 14827/15533/13342\r\nf 14818/15524/13333 14815/15521/13330 14825/15531/13340 14829/15535/13344\r\nf 14830/15536/13345 14831/15537/13346 14832/15538/13347 14833/15539/13348\r\nf 14834/15540/13349 14835/15541/13350 14832/15538/13347 14831/15537/13346\r\nf 14828/15534/13343 14827/15533/13342 14832/15538/13347 14835/15541/13350\r\nf 14826/15532/13341 14833/15539/13348 14832/15538/13347 14827/15533/13342\r\nf 14742/15448/13257 14745/15451/13260 14836/15542/13351 14837/15543/13352\r\nf 14740/15446/13255 14838/15544/13353 14836/15542/13351 14745/15451/13260\r\nf 14828/15534/13343 14835/15541/13350 14836/15542/13351 14838/15544/13353\r\nf 14834/15540/13349 14837/15543/13352 14836/15542/13351 14835/15541/13350\r\nf 14718/15424/13233 14821/15527/13336 14839/15545/13354 14738/15444/13253\r\nf 14818/15524/13333 14829/15535/13344 14839/15545/13354 14821/15527/13336\r\nf 14828/15534/13343 14838/15544/13353 14839/15545/13354 14829/15535/13344\r\nf 14740/15446/13255 14738/15444/13253 14839/15545/13354 14838/15544/13353\r\nf 14830/15536/13345 14840/15546/13355 14841/15547/13356 14831/15537/13346\r\nf 14842/15548/13357 14843/15549/13358 14841/15547/13356 14840/15546/13355\r\nf 14844/15550/13359 14845/15551/13360 14841/15547/13356 14843/15549/13358\r\nf 14834/15540/13349 14831/15537/13346 14841/15547/13356 14845/15551/13360\r\nf 14846/15552/13361 14847/15553/13362 14848/15554/13363 14849/15555/13364\r\nf 14850/15556/13365 14851/15557/13366 14848/15554/13363 14847/15553/13362\r\nf 14844/15550/13359 14843/15549/13358 14848/15554/13363 14851/15557/13366\r\nf 14842/15548/13357 14849/15555/13364 14848/15554/13363 14843/15549/13358\r\nf 14758/15464/13273 14761/15467/13276 14852/15558/13367 14853/15559/13368\r\nf 14754/15460/13269 14854/15560/13369 14852/15558/13367 14761/15467/13276\r\nf 14844/15550/13359 14851/15557/13366 14852/15558/13367 14854/15560/13369\r\nf 14850/15556/13365 14853/15559/13368 14852/15558/13367 14851/15557/13366\r\nf 14742/15448/13257 14837/15543/13352 14855/15561/13370 14752/15458/13267\r\nf 14834/15540/13349 14845/15551/13360 14855/15561/13370 14837/15543/13352\r\nf 14844/15550/13359 14854/15560/13369 14855/15561/13370 14845/15551/13360\r\nf 14754/15460/13269 14752/15458/13267 14855/15561/13370 14854/15560/13369\r\nf 14856/15562/13371 14857/15563/13372 14858/15564/13373 14859/15565/13374\r\nf 14808/15514/13323 14811/15517/13326 14858/15564/13373 14857/15563/13372\r\nf 14608/15314/13123 14860/15566/13375 14861/15567/13376 14615/15321/13130\r\nf 14856/15562/13371 14859/15565/13374 14861/15567/13376 14860/15566/13375\r\nf 14604/15310/13119 14862/15568/13377 14863/15569/13378 14605/15311/13120\r\nf 14864/15570/13379 14865/15571/13380 14863/15569/13378 14862/15568/13377\r\nf 14856/15562/13371 14860/15566/13375 14863/15569/13378 14865/15571/13380\r\nf 14608/15314/13123 14605/15311/13120 14863/15569/13378 14860/15566/13375\r\nf 14814/15520/13329 14817/15523/13332 14866/15572/13381 14867/15573/13382\r\nf 14808/15514/13323 14857/15563/13372 14866/15572/13381 14817/15523/13332\r\nf 14856/15562/13371 14865/15571/13380 14866/15572/13381 14857/15563/13372\r\nf 14864/15570/13379 14867/15573/13382 14866/15572/13381 14865/15571/13380\r\nf 14604/15310/13119 14630/15336/13145 14868/15574/13383 14862/15568/13377\r\nf 14624/15330/13139 14869/15575/13384 14868/15574/13383 14630/15336/13145\r\nf 14870/15576/13385 14871/15577/13386 14868/15574/13383 14869/15575/13384\r\nf 14864/15570/13379 14862/15568/13377 14868/15574/13383 14871/15577/13386\r\nf 14620/15326/13135 14872/15578/13387 14873/15579/13388 14621/15327/13136\r\nf 14874/15580/13389 14875/15581/13390 14873/15579/13388 14872/15578/13387\r\nf 14870/15576/13385 14869/15575/13384 14873/15579/13388 14875/15581/13390\r\nf 14624/15330/13139 14621/15327/13136 14873/15579/13388 14869/15575/13384\r\nf 14830/15536/13345 14833/15539/13348 14876/15582/13391 14877/15583/13392\r\nf 14826/15532/13341 14878/15584/13393 14876/15582/13391 14833/15539/13348\r\nf 14870/15576/13385 14875/15581/13390 14876/15582/13391 14878/15584/13393\r\nf 14874/15580/13389 14877/15583/13392 14876/15582/13391 14875/15581/13390\r\nf 14814/15520/13329 14867/15573/13382 14879/15585/13394 14824/15530/13339\r\nf 14864/15570/13379 14871/15577/13386 14879/15585/13394 14867/15573/13382\r\nf 14870/15576/13385 14878/15584/13393 14879/15585/13394 14871/15577/13386\r\nf 14826/15532/13341 14824/15530/13339 14879/15585/13394 14878/15584/13393\r\nf 14620/15326/13135 14880/15586/13395 14881/15587/13396 14872/15578/13387\r\nf 14882/15588/13397 14883/15589/13398 14881/15587/13396 14880/15586/13395\r\nf 14884/15590/13399 14885/15591/13400 14881/15587/13396 14883/15589/13398\r\nf 14874/15580/13389 14872/15578/13387 14881/15587/13396 14885/15591/13400\r\nf 14886/15592/13401 14887/15593/13402 14888/15594/13403 14889/15595/13404\r\nf 14890/15596/13405 14891/15597/13406 14888/15594/13403 14887/15593/13402\r\nf 14884/15590/13399 14883/15589/13398 14888/15594/13403 14891/15597/13406\r\nf 14882/15588/13397 14889/15595/13404 14888/15594/13403 14883/15589/13398\r\nf 14846/15552/13361 14849/15555/13364 14892/15598/13407 14893/15599/13408\r\nf 14842/15548/13357 14894/15600/13409 14892/15598/13407 14849/15555/13364\r\nf 14884/15590/13399 14891/15597/13406 14892/15598/13407 14894/15600/13409\r\nf 14890/15596/13405 14893/15599/13408 14892/15598/13407 14891/15597/13406\r\nf 14830/15536/13345 14877/15583/13392 14895/15601/13410 14840/15546/13355\r\nf 14874/15580/13389 14885/15591/13400 14895/15601/13410 14877/15583/13392\r\nf 14884/15590/13399 14894/15600/13409 14895/15601/13410 14885/15591/13400\r\nf 14842/15548/13357 14840/15546/13355 14895/15601/13410 14894/15600/13409\r\nf 13961/14667/12476 13960/14666/12475 14896/15602/13411 14897/15603/13412\r\nf 14898/15604/13413 14896/15602/13411 13960/14666/12475 13955/14661/12470\r\nf 14899/15605/13414 14900/15606/13415 14896/15602/13411 14898/15604/13413\r\nf 14901/15607/13416 14897/15603/13412 14896/15602/13411 14900/15606/13415\r\nf 14555/15261/13070 14902/15608/13417 13952/14658/12467 13834/14540/12349\r\nf 14552/15258/13067 14903/15609/13418 14902/15608/13417 14555/15261/13070\r\nf 14899/15605/13414 14898/15604/13413 14902/15608/13417 14903/15609/13418\r\nf 13955/14661/12470 13952/14658/12467 14902/15608/13417 14898/15604/13413\r\nf 14548/15254/13063 14693/15399/13208 14904/15610/13419 14549/15255/13064\r\nf 14697/15403/13212 14905/15611/13420 14904/15610/13419 14693/15399/13208\r\nf 14899/15605/13414 14903/15609/13418 14904/15610/13419 14905/15611/13420\r\nf 14552/15258/13067 14549/15255/13064 14904/15610/13419 14903/15609/13418\r\nf 14708/15414/13223 14906/15612/13421 14907/15613/13422 14709/15415/13224\r\nf 14901/15607/13416 14900/15606/13415 14907/15613/13422 14906/15612/13421\r\nf 14899/15605/13414 14905/15611/13420 14907/15613/13422 14900/15606/13415\r\nf 14697/15403/13212 14709/15415/13224 14907/15613/13422 14905/15611/13420\r\nf 14886/15592/13401 14889/15595/13404 14908/15614/13423 14909/15615/13424\r\nf 14882/15588/13397 14910/15616/13425 14908/15614/13423 14889/15595/13404\r\nf 14911/15617/13426 14912/15618/13427 14908/15614/13423 14910/15616/13425\r\nf 14913/15619/13428 14909/15615/13424 14908/15614/13423 14912/15618/13427\r\nf 14620/15326/13135 14623/15329/13138 14914/15620/13429 14880/15586/13395\r\nf 14628/15334/13143 14915/15621/13430 14914/15620/13429 14623/15329/13138\r\nf 14911/15617/13426 14910/15616/13425 14914/15620/13429 14915/15621/13430\r\nf 14882/15588/13397 14880/15586/13395 14914/15620/13429 14910/15616/13425\r\nf 14636/15342/13151 14691/15397/13206 14916/15622/13431 14637/15343/13152\r\nf 14688/15394/13203 14917/15623/13432 14916/15622/13431 14691/15397/13206\r\nf 14911/15617/13426 14915/15621/13430 14916/15622/13431 14917/15623/13432\r\nf 14628/15334/13143 14637/15343/13152 14916/15622/13431 14915/15621/13430\r\nf 14684/15390/13199 14918/15624/13433 14919/15625/13434 14685/15391/13200\r\nf 14913/15619/13428 14912/15618/13427 14919/15625/13434 14918/15624/13433\r\nf 14911/15617/13426 14917/15623/13432 14919/15625/13434 14912/15618/13427\r\nf 14688/15394/13203 14685/15391/13200 14919/15625/13434 14917/15623/13432\r\nf 14221/14927/12736 14220/14926/12735 14920/15626/13435 14921/15627/13436\r\nf 14922/15628/13437 14920/15626/13435 14220/14926/12735 14226/14932/12741\r\nf 14923/15629/13438 14924/15630/13439 14920/15626/13435 14922/15628/13437\r\nf 14925/15631/13440 14921/15627/13436 14920/15626/13435 14924/15630/13439\r\nf 14926/15632/13441 14927/15633/13442 14235/14941/12750 14234/14940/12749\r\nf 14928/15634/13443 14929/15635/13444 14927/15633/13442 14926/15632/13441\r\nf 14923/15629/13438 14922/15628/13437 14927/15633/13442 14929/15635/13444\r\nf 14226/14932/12741 14235/14941/12750 14927/15633/13442 14922/15628/13437\r\nf 14930/15636/13445 14931/15637/13446 14932/15638/13447 14933/15639/13448\r\nf 14934/15640/13449 14935/15641/13450 14932/15638/13447 14931/15637/13446\r\nf 14923/15629/13438 14929/15635/13444 14932/15638/13447 14935/15641/13450\r\nf 14928/15634/13443 14933/15639/13448 14932/15638/13447 14929/15635/13444\r\nf 14936/15642/13451 14937/15643/13452 14938/15644/13453 14939/15645/13454\r\nf 14925/15631/13440 14924/15630/13439 14938/15644/13453 14937/15643/13452\r\nf 14923/15629/13438 14935/15641/13450 14938/15644/13453 14924/15630/13439\r\nf 14934/15640/13449 14939/15645/13454 14938/15644/13453 14935/15641/13450\r\nf 14940/15646/13455 14941/15647/13456 14270/14976/12785 14267/14973/12782\r\nf 14942/15648/13457 14943/15649/13458 14941/15647/13456 14940/15646/13455\r\nf 14921/15627/13436 14944/15650/13459 14264/14970/12779 14221/14927/12736\r\nf 14925/15631/13440 14945/15651/13460 14944/15650/13459 14921/15627/13436\r\nf 14942/15648/13457 14940/15646/13455 14944/15650/13459 14945/15651/13460\r\nf 14267/14973/12782 14264/14970/12779 14944/15650/13459 14940/15646/13455\r\nf 14936/15642/13451 14946/15652/13461 14947/15653/13462 14937/15643/13452\r\nf 14948/15654/13463 14949/15655/13464 14947/15653/13462 14946/15652/13461\r\nf 14942/15648/13457 14945/15651/13460 14947/15653/13462 14949/15655/13464\r\nf 14925/15631/13440 14937/15643/13452 14947/15653/13462 14945/15651/13460\r\nf 14942/15648/13457 14949/15655/13464 14950/15656/13465 14943/15649/13458\r\nf 14948/15654/13463 14951/15657/13466 14950/15656/13465 14949/15655/13464\r\nf 14936/15642/13451 14952/15658/13467 14953/15659/13468 14946/15652/13461\r\nf 14954/15660/13469 14955/15661/13470 14953/15659/13468 14952/15658/13467\r\nf 14956/15662/13471 14957/15663/13472 14953/15659/13468 14955/15661/13470\r\nf 14948/15654/13463 14946/15652/13461 14953/15659/13468 14957/15663/13472\r\nf 14958/15664/13473 14959/15665/13474 14960/15666/13475 14961/15667/13476\r\nf 14962/15668/13477 14963/15669/13478 14960/15666/13475 14959/15665/13474\r\nf 14956/15662/13471 14955/15661/13470 14960/15666/13475 14963/15669/13478\r\nf 14954/15660/13469 14961/15667/13476 14960/15666/13475 14955/15661/13470\r\nf 14956/15662/13471 14963/15669/13478 14964/15670/13479 14965/15671/13480\r\nf 14962/15668/13477 14966/15672/13481 14964/15670/13479 14963/15669/13478\r\nf 14948/15654/13463 14957/15663/13472 14967/15673/13482 14951/15657/13466\r\nf 14956/15662/13471 14965/15671/13480 14967/15673/13482 14957/15663/13472\r\nf 14968/15674/13483 14969/15675/13484 14970/15676/13485 14971/15677/13486\r\nf 14972/15678/13487 14973/15679/13488 14970/15676/13485 14969/15675/13484\r\nf 14974/15680/13489 14975/15681/13490 14976/15682/13491 14977/15683/13492\r\nf 14978/15684/13493 14979/15685/13494 14976/15682/13491 14975/15681/13490\r\nf 14972/15678/13487 14969/15675/13484 14976/15682/13491 14979/15685/13494\r\nf 14968/15674/13483 14977/15683/13492 14976/15682/13491 14969/15675/13484\r\nf 14980/15686/13495 14981/15687/13496 14982/15688/13497 14983/15689/13498\r\nf 14984/15690/13499 14985/15691/13500 14982/15688/13497 14981/15687/13496\r\nf 14972/15678/13487 14979/15685/13494 14982/15688/13497 14985/15691/13500\r\nf 14978/15684/13493 14983/15689/13498 14982/15688/13497 14979/15685/13494\r\nf 14972/15678/13487 14985/15691/13500 14986/15692/13501 14973/15679/13488\r\nf 14984/15690/13499 14987/15693/13502 14986/15692/13501 14985/15691/13500\r\nf 14988/15694/13503 14989/15695/13504 14990/15696/13505 14991/15697/13506\r\nf 14992/15698/13507 14993/15699/13508 14990/15696/13505 14989/15695/13504\r\nf 14994/15700/13509 14995/15701/13510 14990/15696/13505 14993/15699/13508\r\nf 14996/15702/13511 14991/15697/13506 14990/15696/13505 14995/15701/13510\r\nf 14974/15680/13489 14977/15683/13492 14997/15703/13512 14998/15704/13513\r\nf 14968/15674/13483 14999/15705/13514 14997/15703/13512 14977/15683/13492\r\nf 14994/15700/13509 14993/15699/13508 14997/15703/13512 14999/15705/13514\r\nf 14992/15698/13507 14998/15704/13513 14997/15703/13512 14993/15699/13508\r\nf 14994/15700/13509 14999/15705/13514 15000/15706/13515 15001/15707/13516\r\nf 14968/15674/13483 14971/15677/13486 15000/15706/13515 14999/15705/13514\r\nf 14996/15702/13511 14995/15701/13510 15002/15708/13517 15003/15709/13518\r\nf 14994/15700/13509 15001/15707/13516 15002/15708/13517 14995/15701/13510\r\nf 15004/15710/13519 15005/15711/13520 15006/15712/13521 15007/15713/13522\r\nf 15008/15714/13523 15009/15715/13524 15006/15712/13521 15005/15711/13520\r\nf 15010/15716/13525 15011/15717/13526 15006/15712/13521 15009/15715/13524\r\nf 15012/15718/13527 15007/15713/13522 15006/15712/13521 15011/15717/13526\r\nf 15013/15719/13528 15014/15720/13529 15015/15721/13530 15016/15722/13531\r\nf 15017/15723/13532 15018/15724/13533 15015/15721/13530 15014/15720/13529\r\nf 15010/15716/13525 15009/15715/13524 15015/15721/13530 15018/15724/13533\r\nf 15008/15714/13523 15016/15722/13531 15015/15721/13530 15009/15715/13524\r\nf 14974/15680/13489 14998/15704/13513 15019/15725/13534 15020/15726/13535\r\nf 14992/15698/13507 15021/15727/13536 15019/15725/13534 14998/15704/13513\r\nf 15010/15716/13525 15018/15724/13533 15019/15725/13534 15021/15727/13536\r\nf 15017/15723/13532 15020/15726/13535 15019/15725/13534 15018/15724/13533\r\nf 14988/15694/13503 15022/15728/13537 15023/15729/13538 14989/15695/13504\r\nf 15012/15718/13527 15011/15717/13526 15023/15729/13538 15022/15728/13537\r\nf 15010/15716/13525 15021/15727/13536 15023/15729/13538 15011/15717/13526\r\nf 14992/15698/13507 14989/15695/13504 15023/15729/13538 15021/15727/13536\r\nf 15013/15719/13528 15024/15730/13539 15025/15731/13540 15014/15720/13529\r\nf 15026/15732/13541 15027/15733/13542 15025/15731/13540 15024/15730/13539\r\nf 15028/15734/13543 15029/15735/13544 15025/15731/13540 15027/15733/13542\r\nf 15017/15723/13532 15014/15720/13529 15025/15731/13540 15029/15735/13544\r\nf 15030/15736/13545 15031/15737/13546 15032/15738/13547 15033/15739/13548\r\nf 15034/15740/13549 15035/15741/13550 15032/15738/13547 15031/15737/13546\r\nf 15028/15734/13543 15027/15733/13542 15032/15738/13547 15035/15741/13550\r\nf 15026/15732/13541 15033/15739/13548 15032/15738/13547 15027/15733/13542\r\nf 14980/15686/13495 14983/15689/13498 15036/15742/13551 15037/15743/13552\r\nf 14978/15684/13493 15038/15744/13553 15036/15742/13551 14983/15689/13498\r\nf 15028/15734/13543 15035/15741/13550 15036/15742/13551 15038/15744/13553\r\nf 15034/15740/13549 15037/15743/13552 15036/15742/13551 15035/15741/13550\r\nf 14974/15680/13489 15020/15726/13535 15039/15745/13554 14975/15681/13490\r\nf 15017/15723/13532 15029/15735/13544 15039/15745/13554 15020/15726/13535\r\nf 15028/15734/13543 15038/15744/13553 15039/15745/13554 15029/15735/13544\r\nf 14978/15684/13493 14975/15681/13490 15039/15745/13554 15038/15744/13553\r\nf 14936/15642/13451 14939/15645/13454 15040/15746/13555 14952/15658/13467\r\nf 14934/15640/13449 15041/15747/13556 15040/15746/13555 14939/15645/13454\r\nf 15042/15748/13557 15043/15749/13558 15040/15746/13555 15041/15747/13556\r\nf 14954/15660/13469 14952/15658/13467 15040/15746/13555 15043/15749/13558\r\nf 14930/15636/13445 15044/15750/13559 15045/15751/13560 14931/15637/13446\r\nf 15046/15752/13561 15047/15753/13562 15045/15751/13560 15044/15750/13559\r\nf 15042/15748/13557 15041/15747/13556 15045/15751/13560 15047/15753/13562\r\nf 14934/15640/13449 14931/15637/13446 15045/15751/13560 15041/15747/13556\r\nf 15048/15754/13563 15049/15755/13564 15050/15756/13565 15051/15757/13566\r\nf 15052/15758/13567 15053/15759/13568 15050/15756/13565 15049/15755/13564\r\nf 15042/15748/13557 15047/15753/13562 15050/15756/13565 15053/15759/13568\r\nf 15046/15752/13561 15051/15757/13566 15050/15756/13565 15047/15753/13562\r\nf 14958/15664/13473 14961/15667/13476 15054/15760/13569 15055/15761/13570\r\nf 14954/15660/13469 15043/15749/13558 15054/15760/13569 14961/15667/13476\r\nf 15042/15748/13557 15053/15759/13568 15054/15760/13569 15043/15749/13558\r\nf 15052/15758/13567 15055/15761/13570 15054/15760/13569 15053/15759/13568\r\nf 15030/15736/13545 15033/15739/13548 15056/15762/13571 15057/15763/13572\r\nf 15026/15732/13541 15058/15764/13573 15056/15762/13571 15033/15739/13548\r\nf 15059/15765/13574 15060/15766/13575 15056/15762/13571 15058/15764/13573\r\nf 15061/15767/13576 15057/15763/13572 15056/15762/13571 15060/15766/13575\r\nf 15013/15719/13528 15062/15768/13577 15063/15769/13578 15024/15730/13539\r\nf 15064/15770/13579 15065/15771/13580 15063/15769/13578 15062/15768/13577\r\nf 15059/15765/13574 15058/15764/13573 15063/15769/13578 15065/15771/13580\r\nf 15026/15732/13541 15024/15730/13539 15063/15769/13578 15058/15764/13573\r\nf 14684/15390/13199 14687/15393/13202 15066/15772/13581 15067/15773/13582\r\nf 14682/15388/13197 15068/15774/13583 15066/15772/13581 14687/15393/13202\r\nf 15059/15765/13574 15065/15771/13580 15066/15772/13581 15068/15774/13583\r\nf 15064/15770/13579 15067/15773/13582 15066/15772/13581 15065/15771/13580\r\nf 14678/15384/13193 15069/15775/13584 15070/15776/13585 14679/15385/13194\r\nf 15061/15767/13576 15060/15766/13575 15070/15776/13585 15069/15775/13584\r\nf 15059/15765/13574 15068/15774/13583 15070/15776/13585 15060/15766/13575\r\nf 14682/15388/13197 14679/15385/13194 15070/15776/13585 15068/15774/13583\r\nf 14930/15636/13445 15071/15777/13586 15072/15778/13587 15044/15750/13559\r\nf 15073/15779/13588 15074/15780/13589 15072/15778/13587 15071/15777/13586\r\nf 15075/15781/13590 15076/15782/13591 15072/15778/13587 15074/15780/13589\r\nf 15046/15752/13561 15044/15750/13559 15072/15778/13587 15076/15782/13591\r\nf 14708/15414/13223 14711/15417/13226 15077/15783/13592 15078/15784/13593\r\nf 14706/15412/13221 15079/15785/13594 15077/15783/13592 14711/15417/13226\r\nf 15075/15781/13590 15074/15780/13589 15077/15783/13592 15079/15785/13594\r\nf 15073/15779/13588 15078/15784/13593 15077/15783/13592 15074/15780/13589\r\nf 14702/15408/13217 15080/15786/13595 15081/15787/13596 14703/15409/13218\r\nf 15082/15788/13597 15083/15789/13598 15081/15787/13596 15080/15786/13595\r\nf 15075/15781/13590 15079/15785/13594 15081/15787/13596 15083/15789/13598\r\nf 14706/15412/13221 14703/15409/13218 15081/15787/13596 15079/15785/13594\r\nf 15048/15754/13563 15051/15757/13566 15084/15790/13599 15085/15791/13600\r\nf 15046/15752/13561 15076/15782/13591 15084/15790/13599 15051/15757/13566\r\nf 15075/15781/13590 15083/15789/13598 15084/15790/13599 15076/15782/13591\r\nf 15082/15788/13597 15085/15791/13600 15084/15790/13599 15083/15789/13598\r\nf 15086/15792/13601 15087/15793/13602 15088/15794/13603 15089/15795/13604\r\nf 15090/15796/13605 15091/15797/13606 15088/15794/13603 15087/15793/13602\r\nf 15092/15798/13607 15093/15799/13608 15094/15800/13609 15095/15801/13610\r\nf 15086/15792/13601 15089/15795/13604 15094/15800/13609 15093/15799/13608\r\nf 15096/15802/13611 15097/15803/13612 15098/15804/13613 15099/15805/13614\r\nf 15100/15806/13615 15101/15807/13616 15098/15804/13613 15097/15803/13612\r\nf 15086/15792/13601 15093/15799/13608 15098/15804/13613 15101/15807/13616\r\nf 15092/15798/13607 15099/15805/13614 15098/15804/13613 15093/15799/13608\r\nf 15102/15808/13617 15103/15809/13618 15104/15810/13619 15105/15811/13620\r\nf 15090/15796/13605 15087/15793/13602 15104/15810/13619 15103/15809/13618\r\nf 15086/15792/13601 15101/15807/13616 15104/15810/13619 15087/15793/13602\r\nf 15100/15806/13615 15105/15811/13620 15104/15810/13619 15101/15807/13616\r\nf 15096/15802/13611 15106/15812/13621 15107/15813/13622 15097/15803/13612\r\nf 15108/15814/13623 15109/15815/13624 15107/15813/13622 15106/15812/13621\r\nf 15110/15816/13625 15111/15817/13626 15107/15813/13622 15109/15815/13624\r\nf 15100/15806/13615 15097/15803/13612 15107/15813/13622 15111/15817/13626\r\nf 15112/15818/13627 15113/15819/13628 15114/15820/13629 15115/15821/13630\r\nf 15116/15822/13631 15117/15823/13632 15114/15820/13629 15113/15819/13628\r\nf 15110/15816/13625 15109/15815/13624 15114/15820/13629 15117/15823/13632\r\nf 15108/15814/13623 15115/15821/13630 15114/15820/13629 15109/15815/13624\r\nf 15118/15824/13633 15119/15825/13634 15120/15826/13635 15121/15827/13636\r\nf 15122/15828/13637 15123/15829/13638 15120/15826/13635 15119/15825/13634\r\nf 15110/15816/13625 15117/15823/13632 15120/15826/13635 15123/15829/13638\r\nf 15116/15822/13631 15121/15827/13636 15120/15826/13635 15117/15823/13632\r\nf 15102/15808/13617 15105/15811/13620 15124/15830/13639 15125/15831/13640\r\nf 15100/15806/13615 15111/15817/13626 15124/15830/13639 15105/15811/13620\r\nf 15110/15816/13625 15123/15829/13638 15124/15830/13639 15111/15817/13626\r\nf 15122/15828/13637 15125/15831/13640 15124/15830/13639 15123/15829/13638\r\nf 15118/15824/13633 15121/15827/13636 15126/15832/13641 15127/15833/13642\r\nf 15116/15822/13631 15128/15834/13643 15126/15832/13641 15121/15827/13636\r\nf 15129/15835/13644 15130/15836/13645 15126/15832/13641 15128/15834/13643\r\nf 15131/15837/13646 15127/15833/13642 15126/15832/13641 15130/15836/13645\r\nf 15112/15818/13627 15132/15838/13647 15133/15839/13648 15113/15819/13628\r\nf 15134/15840/13649 15135/15841/13650 15133/15839/13648 15132/15838/13647\r\nf 15129/15835/13644 15128/15834/13643 15133/15839/13648 15135/15841/13650\r\nf 15116/15822/13631 15113/15819/13628 15133/15839/13648 15128/15834/13643\r\nf 14764/15470/13279 14767/15473/13282 15136/15842/13651 15137/15843/13652\r\nf 14762/15468/13277 15138/15844/13653 15136/15842/13651 14767/15473/13282\r\nf 15129/15835/13644 15135/15841/13650 15136/15842/13651 15138/15844/13653\r\nf 15134/15840/13649 15137/15843/13652 15136/15842/13651 15135/15841/13650\r\nf 14758/15464/13273 15139/15845/13654 15140/15846/13655 14759/15465/13274\r\nf 15131/15837/13646 15130/15836/13645 15140/15846/13655 15139/15845/13654\r\nf 15129/15835/13644 15138/15844/13653 15140/15846/13655 15130/15836/13645\r\nf 14762/15468/13277 14759/15465/13274 15140/15846/13655 15138/15844/13653\r\nf 14962/15668/13477 15141/15847/13656 15142/15848/13657 14966/15672/13481\r\nf 15143/15849/13658 15144/15850/13659 15142/15848/13657 15141/15847/13656\r\nf 14958/15664/13473 15145/15851/13660 15146/15852/13661 14959/15665/13474\r\nf 15147/15853/13662 15148/15854/13663 15146/15852/13661 15145/15851/13660\r\nf 15143/15849/13658 15141/15847/13656 15146/15852/13661 15148/15854/13663\r\nf 14962/15668/13477 14959/15665/13474 15146/15852/13661 15141/15847/13656\r\nf 15096/15802/13611 15099/15805/13614 15149/15855/13664 15150/15856/13665\r\nf 15092/15798/13607 15151/15857/13666 15149/15855/13664 15099/15805/13614\r\nf 15143/15849/13658 15148/15854/13663 15149/15855/13664 15151/15857/13666\r\nf 15147/15853/13662 15150/15856/13665 15149/15855/13664 15148/15854/13663\r\nf 15143/15849/13658 15151/15857/13666 15152/15858/13667 15144/15850/13659\r\nf 15092/15798/13607 15095/15801/13610 15152/15858/13667 15151/15857/13666\r\nf 14958/15664/13473 15055/15761/13570 15153/15859/13668 15145/15851/13660\r\nf 15052/15758/13567 15154/15860/13669 15153/15859/13668 15055/15761/13570\r\nf 15155/15861/13670 15156/15862/13671 15153/15859/13668 15154/15860/13669\r\nf 15147/15853/13662 15145/15851/13660 15153/15859/13668 15156/15862/13671\r\nf 15048/15754/13563 15157/15863/13672 15158/15864/13673 15049/15755/13564\r\nf 15159/15865/13674 15160/15866/13675 15158/15864/13673 15157/15863/13672\r\nf 15155/15861/13670 15154/15860/13669 15158/15864/13673 15160/15866/13675\r\nf 15052/15758/13567 15049/15755/13564 15158/15864/13673 15154/15860/13669\r\nf 15112/15818/13627 15115/15821/13630 15161/15867/13676 15162/15868/13677\r\nf 15108/15814/13623 15163/15869/13678 15161/15867/13676 15115/15821/13630\r\nf 15155/15861/13670 15160/15866/13675 15161/15867/13676 15163/15869/13678\r\nf 15159/15865/13674 15162/15868/13677 15161/15867/13676 15160/15866/13675\r\nf 15096/15802/13611 15150/15856/13665 15164/15870/13679 15106/15812/13621\r\nf 15147/15853/13662 15156/15862/13671 15164/15870/13679 15150/15856/13665\r\nf 15155/15861/13670 15163/15869/13678 15164/15870/13679 15156/15862/13671\r\nf 15108/15814/13623 15106/15812/13621 15164/15870/13679 15163/15869/13678\r\nf 15048/15754/13563 15085/15791/13600 15165/15871/13680 15157/15863/13672\r\nf 15082/15788/13597 15166/15872/13681 15165/15871/13680 15085/15791/13600\r\nf 15167/15873/13682 15168/15874/13683 15165/15871/13680 15166/15872/13681\r\nf 15159/15865/13674 15157/15863/13672 15165/15871/13680 15168/15874/13683\r\nf 14702/15408/13217 14807/15513/13322 15169/15875/13684 15080/15786/13595\r\nf 14804/15510/13319 15170/15876/13685 15169/15875/13684 14807/15513/13322\r\nf 15167/15873/13682 15166/15872/13681 15169/15875/13684 15170/15876/13685\r\nf 15082/15788/13597 15080/15786/13595 15169/15875/13684 15166/15872/13681\r\nf 14764/15470/13279 15137/15843/13652 15171/15877/13686 14802/15508/13317\r\nf 15134/15840/13649 15172/15878/13687 15171/15877/13686 15137/15843/13652\r\nf 15167/15873/13682 15170/15876/13685 15171/15877/13686 15172/15878/13687\r\nf 14804/15510/13319 14802/15508/13317 15171/15877/13686 15170/15876/13685\r\nf 15112/15818/13627 15162/15868/13677 15173/15879/13688 15132/15838/13647\r\nf 15159/15865/13674 15168/15874/13683 15173/15879/13688 15162/15868/13677\r\nf 15167/15873/13682 15172/15878/13687 15173/15879/13688 15168/15874/13683\r\nf 15134/15840/13649 15132/15838/13647 15173/15879/13688 15172/15878/13687\r\nf 15174/15880/13689 15175/15881/13690 15176/15882/13691 15177/15883/13692\r\nf 15178/15884/13693 15179/15885/13694 15176/15882/13691 15175/15881/13690\r\nf 15090/15796/13605 15180/15886/13695 15181/15887/13696 15091/15797/13606\r\nf 15174/15880/13689 15177/15883/13692 15181/15887/13696 15180/15886/13695\r\nf 15102/15808/13617 15182/15888/13697 15183/15889/13698 15103/15809/13618\r\nf 15184/15890/13699 15185/15891/13700 15183/15889/13698 15182/15888/13697\r\nf 15174/15880/13689 15180/15886/13695 15183/15889/13698 15185/15891/13700\r\nf 15090/15796/13605 15103/15809/13618 15183/15889/13698 15180/15886/13695\r\nf 15186/15892/13701 15187/15893/13702 15188/15894/13703 15189/15895/13704\r\nf 15178/15884/13693 15175/15881/13690 15188/15894/13703 15187/15893/13702\r\nf 15174/15880/13689 15185/15891/13700 15188/15894/13703 15175/15881/13690\r\nf 15184/15890/13699 15189/15895/13704 15188/15894/13703 15185/15891/13700\r\nf 15186/15892/13701 15189/15895/13704 15190/15896/13705 15191/15897/13706\r\nf 15184/15890/13699 15192/15898/13707 15190/15896/13705 15189/15895/13704\r\nf 15193/15899/13708 15194/15900/13709 15190/15896/13705 15192/15898/13707\r\nf 15195/15901/13710 15191/15897/13706 15190/15896/13705 15194/15900/13709\r\nf 15102/15808/13617 15125/15831/13640 15196/15902/13711 15182/15888/13697\r\nf 15122/15828/13637 15197/15903/13712 15196/15902/13711 15125/15831/13640\r\nf 15193/15899/13708 15192/15898/13707 15196/15902/13711 15197/15903/13712\r\nf 15184/15890/13699 15182/15888/13697 15196/15902/13711 15192/15898/13707\r\nf 15118/15824/13633 15198/15904/13713 15199/15905/13714 15119/15825/13634\r\nf 15200/15906/13715 15201/15907/13716 15199/15905/13714 15198/15904/13713\r\nf 15193/15899/13708 15197/15903/13712 15199/15905/13714 15201/15907/13716\r\nf 15122/15828/13637 15119/15825/13634 15199/15905/13714 15197/15903/13712\r\nf 15202/15908/13717 15203/15909/13718 15204/15910/13719 15205/15911/13720\r\nf 15195/15901/13710 15194/15900/13709 15204/15910/13719 15203/15909/13718\r\nf 15193/15899/13708 15201/15907/13716 15204/15910/13719 15194/15900/13709\r\nf 15200/15906/13715 15205/15911/13720 15204/15910/13719 15201/15907/13716\r\nf 15202/15908/13717 15205/15911/13720 15206/15912/13721 15207/15913/13722\r\nf 15200/15906/13715 15208/15914/13723 15206/15912/13721 15205/15911/13720\r\nf 15209/15915/13724 15210/15916/13725 15206/15912/13721 15208/15914/13723\r\nf 15211/15917/13726 15207/15913/13722 15206/15912/13721 15210/15916/13725\r\nf 15118/15824/13633 15127/15833/13642 15212/15918/13727 15198/15904/13713\r\nf 15131/15837/13646 15213/15919/13728 15212/15918/13727 15127/15833/13642\r\nf 15209/15915/13724 15208/15914/13723 15212/15918/13727 15213/15919/13728\r\nf 15200/15906/13715 15198/15904/13713 15212/15918/13727 15208/15914/13723\r\nf 14758/15464/13273 14853/15559/13368 15214/15920/13729 15139/15845/13654\r\nf 14850/15556/13365 15215/15921/13730 15214/15920/13729 14853/15559/13368\r\nf 15209/15915/13724 15213/15919/13728 15214/15920/13729 15215/15921/13730\r\nf 15131/15837/13646 15139/15845/13654 15214/15920/13729 15213/15919/13728\r\nf 14846/15552/13361 15216/15922/13731 15217/15923/13732 14847/15553/13362\r\nf 15211/15917/13726 15210/15916/13725 15217/15923/13732 15216/15922/13731\r\nf 15209/15915/13724 15215/15921/13730 15217/15923/13732 15210/15916/13725\r\nf 14850/15556/13365 14847/15553/13362 15217/15923/13732 15215/15921/13730\r\nf 15178/15884/13693 15218/15924/13733 15219/15925/13734 15179/15885/13694\r\nf 15220/15926/13735 15221/15927/13736 15219/15925/13734 15218/15924/13733\r\nf 15186/15892/13701 15222/15928/13737 15223/15929/13738 15187/15893/13702\r\nf 15224/15930/13739 15225/15931/13740 15223/15929/13738 15222/15928/13737\r\nf 15220/15926/13735 15218/15924/13733 15223/15929/13738 15225/15931/13740\r\nf 15178/15884/13693 15187/15893/13702 15223/15929/13738 15218/15924/13733\r\nf 14988/15694/13503 14991/15697/13506 15226/15932/13741 15227/15933/13742\r\nf 14996/15702/13511 15228/15934/13743 15226/15932/13741 14991/15697/13506\r\nf 15220/15926/13735 15225/15931/13740 15226/15932/13741 15228/15934/13743\r\nf 15224/15930/13739 15227/15933/13742 15226/15932/13741 15225/15931/13740\r\nf 15220/15926/13735 15228/15934/13743 15229/15935/13744 15221/15927/13736\r\nf 14996/15702/13511 15003/15709/13518 15229/15935/13744 15228/15934/13743\r\nf 14988/15694/13503 15227/15933/13742 15230/15936/13745 15022/15728/13537\r\nf 15224/15930/13739 15231/15937/13746 15230/15936/13745 15227/15933/13742\r\nf 15232/15938/13747 15233/15939/13748 15230/15936/13745 15231/15937/13746\r\nf 15012/15718/13527 15022/15728/13537 15230/15936/13745 15233/15939/13748\r\nf 15186/15892/13701 15191/15897/13706 15234/15940/13749 15222/15928/13737\r\nf 15195/15901/13710 15235/15941/13750 15234/15940/13749 15191/15897/13706\r\nf 15232/15938/13747 15231/15937/13746 15234/15940/13749 15235/15941/13750\r\nf 15224/15930/13739 15222/15928/13737 15234/15940/13749 15231/15937/13746\r\nf 15202/15908/13717 15236/15942/13751 15237/15943/13752 15203/15909/13718\r\nf 15238/15944/13753 15239/15945/13754 15237/15943/13752 15236/15942/13751\r\nf 15232/15938/13747 15235/15941/13750 15237/15943/13752 15239/15945/13754\r\nf 15195/15901/13710 15203/15909/13718 15237/15943/13752 15235/15941/13750\r\nf 15004/15710/13519 15007/15713/13522 15240/15946/13755 15241/15947/13756\r\nf 15012/15718/13527 15233/15939/13748 15240/15946/13755 15007/15713/13522\r\nf 15232/15938/13747 15239/15945/13754 15240/15946/13755 15233/15939/13748\r\nf 15238/15944/13753 15241/15947/13756 15240/15946/13755 15239/15945/13754\r\nf 15004/15710/13519 15241/15947/13756 15242/15948/13757 15243/15949/13758\r\nf 15238/15944/13753 15244/15950/13759 15242/15948/13757 15241/15947/13756\r\nf 15245/15951/13760 15246/15952/13761 15242/15948/13757 15244/15950/13759\r\nf 15247/15953/13762 15243/15949/13758 15242/15948/13757 15246/15952/13761\r\nf 15202/15908/13717 15207/15913/13722 15248/15954/13763 15236/15942/13751\r\nf 15211/15917/13726 15249/15955/13764 15248/15954/13763 15207/15913/13722\r\nf 15245/15951/13760 15244/15950/13759 15248/15954/13763 15249/15955/13764\r\nf 15238/15944/13753 15236/15942/13751 15248/15954/13763 15244/15950/13759\r\nf 14846/15552/13361 14893/15599/13408 15250/15956/13765 15216/15922/13731\r\nf 14890/15596/13405 15251/15957/13766 15250/15956/13765 14893/15599/13408\r\nf 15245/15951/13760 15249/15955/13764 15250/15956/13765 15251/15957/13766\r\nf 15211/15917/13726 15216/15922/13731 15250/15956/13765 15249/15955/13764\r\nf 14886/15592/13401 15252/15958/13767 15253/15959/13768 14887/15593/13402\r\nf 15247/15953/13762 15246/15952/13761 15253/15959/13768 15252/15958/13767\r\nf 15245/15951/13760 15251/15957/13766 15253/15959/13768 15246/15952/13761\r\nf 14890/15596/13405 14887/15593/13402 15253/15959/13768 15251/15957/13766\r\nf 14897/15603/13412 15254/15960/13769 14353/15059/12868 13961/14667/12476\r\nf 14901/15607/13416 15255/15961/13770 15254/15960/13769 14897/15603/13412\r\nf 15256/15962/13771 15257/15963/13772 15254/15960/13769 15255/15961/13770\r\nf 14348/15054/12863 14353/15059/12868 15254/15960/13769 15257/15963/13772\r\nf 14708/15414/13223 15078/15784/13593 15258/15964/13773 14906/15612/13421\r\nf 15073/15779/13588 15259/15965/13774 15258/15964/13773 15078/15784/13593\r\nf 15256/15962/13771 15255/15961/13770 15258/15964/13773 15259/15965/13774\r\nf 14901/15607/13416 14906/15612/13421 15258/15964/13773 15255/15961/13770\r\nf 14930/15636/13445 14933/15639/13448 15260/15966/13775 15071/15777/13586\r\nf 14928/15634/13443 15261/15967/13776 15260/15966/13775 14933/15639/13448\r\nf 15256/15962/13771 15259/15965/13774 15260/15966/13775 15261/15967/13776\r\nf 15073/15779/13588 15071/15777/13586 15260/15966/13775 15259/15965/13774\r\nf 14234/14940/12749 14344/15050/12859 15262/15968/13777 14926/15632/13441\r\nf 15257/15963/13772 15262/15968/13777 14344/15050/12859 14348/15054/12863\r\nf 15256/15962/13771 15261/15967/13776 15262/15968/13777 15257/15963/13772\r\nf 14928/15634/13443 14926/15632/13441 15262/15968/13777 15261/15967/13776\r\nf 14886/15592/13401 14909/15615/13424 15263/15969/13778 15252/15958/13767\r\nf 14913/15619/13428 15264/15970/13779 15263/15969/13778 14909/15615/13424\r\nf 15265/15971/13780 15266/15972/13781 15263/15969/13778 15264/15970/13779\r\nf 15247/15953/13762 15252/15958/13767 15263/15969/13778 15266/15972/13781\r\nf 14684/15390/13199 15067/15773/13582 15267/15973/13782 14918/15624/13433\r\nf 15064/15770/13579 15268/15974/13783 15267/15973/13782 15067/15773/13582\r\nf 15265/15971/13780 15264/15970/13779 15267/15973/13782 15268/15974/13783\r\nf 14913/15619/13428 14918/15624/13433 15267/15973/13782 15264/15970/13779\r\nf 15013/15719/13528 15016/15722/13531 15269/15975/13784 15062/15768/13577\r\nf 15008/15714/13523 15270/15976/13785 15269/15975/13784 15016/15722/13531\r\nf 15265/15971/13780 15268/15974/13783 15269/15975/13784 15270/15976/13785\r\nf 15064/15770/13579 15062/15768/13577 15269/15975/13784 15268/15974/13783\r\nf 15004/15710/13519 15243/15949/13758 15271/15977/13786 15005/15711/13520\r\nf 15247/15953/13762 15266/15972/13781 15271/15977/13786 15243/15949/13758\r\nf 15265/15971/13780 15270/15976/13785 15271/15977/13786 15266/15972/13781\r\nf 15008/15714/13523 15005/15711/13520 15271/15977/13786 15270/15976/13785\r\nf 13908/14614/12423 13907/14613/12422 15272/15978/13787 15273/15979/13788\r\nf 13912/14618/12427 13908/14614/12423 15273/15979/13788 15274/15980/13789\r\nf 13916/14622/12431 13915/14621/12430 15275/15981/13790 15276/15982/13791\r\nf 13915/14621/12430 13912/14618/12427 15274/15980/13789 15275/15981/13790\r\nf 13926/14632/12441 13916/14622/12431 15276/15982/13791 15277/15983/13792\r\nf 13929/14635/12444 13926/14632/12441 15277/15983/13792 15278/15984/13793\r\nf 13931/14637/12446 13929/14635/12444 15278/15984/13793 15279/15985/13794\r\nf 14001/14707/12516 14000/14706/12515 15280/15986/13795 15281/15987/13796\r\nf 14005/14711/12520 14001/14707/12516 15281/15987/13796 15282/15988/13797\r\nf 13907/14613/12422 14007/14713/12522 15283/15989/13798 15272/15978/13787\r\nf 14007/14713/12522 14005/14711/12520 15282/15988/13797 15283/15989/13798\r\nf 14298/15004/12813 14297/15003/12812 15284/15990/13799 15285/15991/13800\r\nf 14302/15008/12817 14298/15004/12813 15285/15991/13800 15286/15992/13801\r\nf 14306/15012/12821 14305/15011/12820 15287/15993/13802 15288/15994/13803\r\nf 14305/15011/12820 14302/15008/12817 15286/15992/13801 15287/15993/13802\r\nf 14322/15028/12837 14321/15027/12836 15289/15995/13804 15290/15996/13805\r\nf 14297/15003/12812 14324/15030/12839 15291/15997/13806 15284/15990/13799\r\nf 14324/15030/12839 14322/15028/12837 15290/15996/13805 15291/15997/13806\r\nf 14376/15082/12891 14306/15012/12821 15288/15994/13803 15292/15998/13807\r\nf 14379/15085/12894 14376/15082/12891 15292/15998/13807 15293/15999/13808\r\nf 14000/14706/12515 14381/15087/12896 15294/16000/13809 15280/15986/13795\r\nf 14381/15087/12896 14379/15085/12894 15293/15999/13808 15294/16000/13809\r\nv 333.54 63.98 97.29\r\nv 327.38 64.18 107.31\r\nv 328.27 55.47 107.92\r\nv 334.5 55.04 97.87\r\nv 320.38 64.44 117.34\r\nv 321.2 55.97 117.97\r\nv 321.85 47.27 118.45\r\nv 329 46.42 108.37\r\nv 335.29 45.67 98.3\r\nv 314.4 94.25 109.23\r\nv 316.05 87.7 112.29\r\nv 322.41 87.54 102.99\r\nv 319.81 94.41 100.53\r\nv 317.8 80.34 114.66\r\nv 324.49 80.17 104.97\r\nv 330.36 80.12 95.27\r\nv 327.98 87.52 93.65\r\nv 324.59 94.66 91.72\r\nv 312.37 104.3 101.41\r\nv 313.26 99.58 105.49\r\nv 316.6 100.68 97.65\r\nv 313.09 106.65 94.57\r\nv 319.72 101.69 89.57\r\nv 314.12 108.65 87.32\r\nv 324.13 32.58 -37.28\r\nv 323.64 40.43 -35.15\r\nv 326.53 39.64 -30.67\r\nv 327.04 31.8 -32.58\r\nv 323.05 49.69 -32.97\r\nv 325.97 48.97 -28.61\r\nv 329.11 48.19 -23.89\r\nv 329.7 38.77 -25.74\r\nv 330.27 30.94 -27.38\r\nv 322.41 102.49 80.99\r\nv 315.78 109.94 79.23\r\nv 328.12 95.09 82.68\r\nv 330.63 95.54 73.69\r\nv 324.57 103.12 72.32\r\nv 317.53 110.75 70.89\r\nv 337.82 28.91 -14.48\r\nv 337.08 36.76 -13.52\r\nv 339.91 36.01 -6.88\r\nv 340.58 28.12 -7.8\r\nv 336.26 46.41 -12.44\r\nv 339.17 45.78 -5.88\r\nv 341.62 45.29 0.77\r\nv 342.28 35.4 -0.12\r\nv 342.87 27.44 -0.99\r\nv 343.46 63.96 14.13\r\nv 342.02 63.96 7.75\r\nv 340.5 72.55 8.27\r\nv 341.85 72.66 14.5\r\nv 340.12 63.98 1.8\r\nv 338.68 72.45 2.45\r\nv 336.65 80.4 3.35\r\nv 338.4 80.56 9.01\r\nv 339.68 80.75 15.07\r\nv 311.08 105.92 -13.37\r\nv 312.06 106.63 -10.15\r\nv 315.33 100.83 -13.64\r\nv 313.49 100.51 -17.24\r\nv 313.18 107.43 -6.48\r\nv 317.23 101.19 -9.82\r\nv 320.83 94.71 -12.96\r\nv 318.3 94.67 -16.96\r\nv 315.79 94.64 -20.91\r\nv 345.55 44.53 13.81\r\nv 344.63 54.5 13.92\r\nv 345.82 54.39 21.71\r\nv 346.81 44.31 21.69\r\nv 344.58 63.97 21.81\r\nv 345.35 63.97 29.92\r\nv 346.6 54.33 29.92\r\nv 347.6 44.17 29.96\r\nv 331.46 80.22 -8.63\r\nv 333.32 72.39 -9.87\r\nv 330.28 72.43 -14.98\r\nv 328.56 80.24 -13.52\r\nv 334.69 64.15 -10.78\r\nv 331.49 64.33 -16.14\r\nv 328.2 64.54 -21.03\r\nv 327.14 72.51 -19.64\r\nv 325.59 80.31 -17.95\r\nv 315.86 109.34 2.61\r\nv 316.64 109.91 6.23\r\nv 322.8 102.45 3.34\r\nv 321.19 102.11 -1.1\r\nv 317.23 110.33 9.73\r\nv 324.12 102.78 7.86\r\nv 330.04 95.28 6.1\r\nv 328.11 95.05 0.74\r\nv 325.84 94.88 -4.4\r\nv 339.51 17.63 100.45\r\nv 332.91 19.03 111.12\r\nv 336.08 11.17 113.82\r\nv 342.95 10.17 102.45\r\nv 325.41 20.58 121.9\r\nv 328.39 12.19 125.41\r\nv 332.21 1.55 129.96\r\nv 340.14 1.25 117.29\r\nv 347.38 0.78 105.03\r\nv 347.65 15.81 79.7\r\nv 344.28 16.58 89.97\r\nv 348.18 9.22 91.54\r\nv 352 8.42 80.98\r\nv 353.23 -0.04 93.57\r\nv 357.63 -0.89 82.6\r\nv 351.66 14.95 60.47\r\nv 350.07 15.28 69.65\r\nv 354.63 7.88 70.67\r\nv 356.3 7.55 61.2\r\nv 360.52 -1.44 71.8\r\nv 362.27 -1.77 61.87\r\nv 352.97 14.65 45.76\r\nv 352.57 14.75 52.81\r\nv 357.24 7.37 53.2\r\nv 357.6 7.24 45.71\r\nv 363.25 -1.93 53.54\r\nv 363.55 -2.1 45.6\r\nv 325.17 24.46 -42.21\r\nv 328.43 23.43 -37.21\r\nv 329.51 20.66 -40.39\r\nv 325.57 22.18 -45.22\r\nv 332.02 22.26 -31.55\r\nv 333.71 18.93 -34.72\r\nv 335.84 14.97 -38.61\r\nv 330.84 17.42 -44.25\r\nv 325.99 19.57 -48.83\r\nv 358.58 1.49 7.44\r\nv 353.86 9.45 10.1\r\nv 355.69 8.16 19.65\r\nv 360.78 -0.66 17.34\r\nv 350.16 15.82 12.15\r\nv 351.69 15.17 21.3\r\nv 352.63 14.76 30.41\r\nv 356.91 7.25 29.22\r\nv 362.36 -2.23 27.41\r\nv 346.01 9.01 -22.78\r\nv 342.84 14.76 -19.66\r\nv 346.06 13.3 -12.83\r\nv 349.66 6.98 -16.08\r\nv 340.33 19.45 -17.15\r\nv 343.23 18.43 -10.25\r\nv 345.59 17.56 -3.36\r\nv 348.72 12.09 -6.04\r\nv 352.71 5.31 -9.45\r\nv 357.52 7.08 37.81\r\nv 363.27 -2.45 36.86\r\nv 353.02 14.62 38.43\r\nv 323.01 31.41 119.61\r\nv 323.37 27.9 119.89\r\nv 330.79 26.16 109.51\r\nv 330.39 29.79 109.28\r\nv 323.69 26.18 120.11\r\nv 331.11 24.44 109.69\r\nv 337.6 22.92 99.34\r\nv 337.27 24.64 99.19\r\nv 336.85 28.37 99.02\r\nv 338.28 25.2 -14.97\r\nv 334.53 26.23 -21.84\r\nv 334.13 29.91 -21.18\r\nv 338.65 23.59 -15.33\r\nv 334.85 24.66 -22.32\r\nv 330.86 25.75 -28.89\r\nv 330.6 27.28 -28.26\r\nv 347.13 26.14 13.66\r\nv 348.45 25.73 21.68\r\nv 348.89 21.85 21.51\r\nv 347.56 22.29 13.44\r\nv 349.23 25.48 29.95\r\nv 349.66 21.59 29.79\r\nv 350.07 19.93 29.81\r\nv 349.29 20.19 21.41\r\nv 347.95 20.63 13.19\r\nv 345.57 22.94 5.8\r\nv 345.16 26.74 6.13\r\nv 345.95 21.29 5.5\r\nv 343.65 22.04 -1.73\r\nv 343.27 23.68 -1.43\r\nv 338.03 21.23 99.58\r\nv 331.54 22.76 109.99\r\nv 324.11 24.49 120.46\r\nv 324.82 26.1 -40.39\r\nv 327.84 25.25 -35.38\r\nv 324.55 27.47 -39.29\r\nv 327.54 26.65 -34.37\r\nv 331.2 24.3 -29.8\r\nv 348.5 19.01 12.97\r\nv 349.89 18.58 21.61\r\nv 350.71 18.32 30.31\r\nv 339.13 22.03 -15.88\r\nv 341.91 21.18 -9.03\r\nv 341.4 22.76 -8.57\r\nv 344.16 20.44 -2.15\r\nv 326.13 72.4 106.37\r\nv 332.18 72.3 96.45\r\nv 319.24 72.58 116.28\r\nv 340.69 80.91 22.42\r\nv 342.92 72.75 22.04\r\nv 341.39 81.02 30.2\r\nv 343.66 72.81 30.01\r\nv 335.62 88.14 10.02\r\nv 336.82 88.39 15.87\r\nv 333.99 87.91 4.58\r\nv 331.47 95.58 11.22\r\nv 332.55 95.9 16.82\r\nv 326.37 87.66 -11.51\r\nv 329.12 87.65 -6.86\r\nv 323.55 87.73 -15.7\r\nv 323.38 94.77 -8.87\r\nv 331.97 87.76 84.22\r\nv 334.58 80.26 85.54\r\nv 337.52 80.5 75.97\r\nv 334.74 88.09 74.92\r\nv 329.75 37.2 108.86\r\nv 336.13 36.08 98.7\r\nv 322.48 38.48 119.06\r\nv 346.42 34.32 13.75\r\nv 347.72 33.98 21.72\r\nv 348.51 33.78 29.99\r\nv 335.57 55.51 -11.58\r\nv 332.19 56.04 -17.26\r\nv 332.73 47.27 -18.47\r\nv 328.74 56.61 -22.39\r\nv 343.03 54.66 7.41\r\nv 343.78 44.87 7.09\r\nv 341 54.87 1.33\r\nv 319.61 111.65 55.64\r\nv 327.05 103.87 56.41\r\nv 327.54 104.1 50.02\r\nv 319.95 111.94 49.55\r\nv 333.46 96.16 57.13\r\nv 334.07 96.34 50.46\r\nv 334.31 96.44 44.26\r\nv 327.71 104.22 44.08\r\nv 320.05 112.08 43.89\r\nv 343.08 44.75 78.3\r\nv 345.32 44.47 68.53\r\nv 344.42 54.45 68.31\r\nv 342.21 54.58 77.92\r\nv 346.77 44.28 59.62\r\nv 345.83 54.36 59.5\r\nv 344.64 63.92 59.17\r\nv 343.27 63.91 67.89\r\nv 341.12 63.91 77.42\r\nv 345.41 21.04 78.85\r\nv 347.68 20.51 68.35\r\nv 347.31 22.21 68.21\r\nv 345.07 22.76 78.77\r\nv 349.16 20.16 58.95\r\nv 348.78 21.84 58.8\r\nv 348.34 25.72 58.98\r\nv 346.88 26.08 68.3\r\nv 344.63 26.61 78.71\r\nv 343.01 72.69 58.78\r\nv 341.69 72.6 67.38\r\nv 340.78 80.85 58.32\r\nv 339.52 80.69 66.74\r\nv 339.6 72.49 76.79\r\nv 337.82 88.58 57.77\r\nv 338.49 88.72 50.85\r\nv 341.5 80.96 51.18\r\nv 341.78 81.02 44.53\r\nv 338.76 88.8 44.4\r\nv 346.18 34.26 68.47\r\nv 343.93 34.69 78.56\r\nv 347.64 33.97 59.35\r\nv 326.09 103.56 63.98\r\nv 318.8 111.29 62.89\r\nv 332.37 95.9 65.02\r\nv 336.63 88.37 65.94\r\nv 339.86 45.14 88.25\r\nv 339.03 54.76 87.82\r\nv 338 63.91 87.28\r\nv 342.21 21.81 89.12\r\nv 341.87 23.54 89.02\r\nv 341.44 27.34 88.89\r\nv 336.55 72.36 86.53\r\nv 340.72 35.27 88.62\r\nv 327.74 104.2 37.85\r\nv 320.13 112.02 37.95\r\nv 334.3 96.44 37.75\r\nv 334.02 96.36 30.89\r\nv 327.53 104.07 31.29\r\nv 319.99 111.84 31.71\r\nv 338.74 88.83 37.63\r\nv 341.75 81.05 37.55\r\nv 338.41 88.77 30.51\r\nv 347.97 44.11 45.29\r\nv 346.99 54.29 45.04\r\nv 346.65 54.31 52.03\r\nv 347.61 44.16 52.21\r\nv 345.75 63.95 44.82\r\nv 345.43 63.94 51.75\r\nv 350 19.93 52.01\r\nv 349.61 21.61 51.97\r\nv 350.36 19.82 45.66\r\nv 349.96 21.5 45.75\r\nv 349.53 25.4 45.78\r\nv 349.17 25.5 52.09\r\nv 344.06 72.8 44.66\r\nv 343.76 72.76 51.48\r\nv 348.48 33.79 52.2\r\nv 348.84 33.7 45.58\r\nv 346.99 54.29 37.68\r\nv 347.98 44.1 37.85\r\nv 345.73 63.96 37.57\r\nv 350.41 19.82 38.08\r\nv 350 21.48 38.11\r\nv 349.57 25.38 38.19\r\nv 344.03 72.82 37.52\r\nv 348.87 33.69 38.06\r\nv 318.69 111.32 18.99\r\nv 319.44 111.63 25.15\r\nv 326.96 103.86 24.38\r\nv 326.15 103.57 17.88\r\nv 333.42 96.16 23.65\r\nv 337.76 88.61 22.97\r\nv 351.04 18.2 38.35\r\nv 350.96 18.19 45.71\r\nv 350.57 18.29 52.35\r\nv 349.7 18.51 59.62\r\nv 348.17 18.85 68.85\r\nv 345.86 19.37 79.07\r\nv 342.65 20.13 89.3\r\nv 333.45 37.75 -19.92\r\nv 319.26 101.65 -5.57\r\nv 314.58 108.43 -1.93\r\nv 324 72.59 -24.32\r\nv 322.58 80.37 -22.43\r\nv 324.98 64.73 -25.82\r\nv 321.81 64.91 -30.54\r\nv 320.85 72.67 -29\r\nv 319.56 80.44 -26.92\r\nv 338.44 16.8 -27.38\r\nv 341.15 11.92 -30.87\r\nv 336.29 20.83 -24.56\r\nv 327.33 28.16 -33.62\r\nv 324.37 28.95 -38.46\r\nv 335.26 23.15 -23.05\r\nv 320.7 87.79 -19.93\r\nv 317.84 87.86 -24.18\r\nv 325.55 57.1 -27.12\r\nv 322.49 57.55 -31.65\r\nv 344.53 34.81 6.61\r\nv 336.2 72.39 -3.82\r\nv 334.25 80.28 -2.77\r\nv 337.62 64.04 -4.58\r\nv 325.22 103.18 12.54\r\nv 317.93 110.85 13.92\r\nv 351.43 10.78 1.57\r\nv 355.79 3.48 -1.55\r\nv 348.01 16.66 4.02\r\nv 341.01 24.39 -8.26\r\nv 346.47 19.67 5.12\r\nv 331.74 87.74 -1.27\r\nv 338.51 55.14 -5.19\r\nvn 0.82 0.14 0.55\r\nvn 0.76 0.13 0.64\r\nvn 0.76 0.09 0.64\r\nvn 0.82 0.1 0.56\r\nvn 0.74 0.13 0.66\r\nvn 0.74 0.09 0.67\r\nvn 0.74 0.08 0.67\r\nvn 0.76 0.08 0.64\r\nvn 0.82 0.08 0.56\r\nvn 0.71 0.39 0.58\r\nvn 0.71 0.33 0.62\r\nvn 0.72 0.35 0.59\r\nvn 0.71 0.45 0.55\r\nvn 0.72 0.26 0.64\r\nvn 0.74 0.27 0.62\r\nvn 0.8 0.27 0.53\r\nvn 0.78 0.37 0.51\r\nvn 0.74 0.49 0.47\r\nvn 0.9 0.33 0.28\r\nvn 0.76 0.41 0.5\r\nvn 0.71 0.52 0.47\r\nvn 0.76 0.54 0.35\r\nvn 0.71 0.58 0.41\r\nvn 0.71 0.61 0.34\r\nvn 0.79 0.17 -0.59\r\nvn 0.78 0.16 -0.61\r\nvn 0.78 0.15 -0.61\r\nvn 0.79 0.16 -0.59\r\nvn 0.77 0.14 -0.63\r\nvn 0.77 0.13 -0.63\r\nvn 0.77 0.12 -0.63\r\nvn 0.78 0.14 -0.61\r\nvn 0.79 0.16 -0.6\r\nvn 0.72 0.61 0.33\r\nvn 0.71 0.64 0.3\r\nvn 0.77 0.52 0.37\r\nvn 0.8 0.53 0.28\r\nvn 0.74 0.62 0.26\r\nvn 0.72 0.65 0.24\r\nvn 0.85 0.13 -0.5\r\nvn 0.85 0.11 -0.52\r\nvn 0.9 0.1 -0.43\r\nvn 0.9 0.12 -0.42\r\nvn 0.84 0.1 -0.54\r\nvn 0.89 0.09 -0.45\r\nvn 0.91 0.08 -0.41\r\nvn 0.92 0.09 -0.39\r\nvn 0.92 0.11 -0.38\r\nvn 0.96 0.14 -0.23\r\nvn 0.94 0.13 -0.33\r\nvn 0.92 0.2 -0.32\r\nvn 0.95 0.21 -0.22\r\nvn 0.9 0.13 -0.41\r\nvn 0.89 0.2 -0.41\r\nvn 0.87 0.28 -0.4\r\nvn 0.9 0.29 -0.32\r\nvn 0.93 0.29 -0.22\r\nvn 0.72 0.53 -0.45\r\nvn 0.7 0.56 -0.45\r\nvn 0.68 0.53 -0.51\r\nvn 0.69 0.51 -0.52\r\nvn 0.68 0.58 -0.45\r\nvn 0.67 0.55 -0.5\r\nvn 0.68 0.47 -0.56\r\nvn 0.68 0.46 -0.57\r\nvn 0.69 0.44 -0.57\r\nvn 0.96 0.08 -0.26\r\nvn 0.96 0.1 -0.24\r\nvn 0.98 0.1 -0.16\r\nvn 0.98 0.08 -0.16\r\nvn 0.98 0.15 -0.15\r\nvn 0.98 0.15 -0.09\r\nvn 0.99 0.1 -0.09\r\nvn 0.99 0.08 -0.09\r\nvn 0.79 0.28 -0.54\r\nvn 0.81 0.2 -0.56\r\nvn 0.76 0.2 -0.62\r\nvn 0.75 0.28 -0.61\r\nvn 0.82 0.14 -0.56\r\nvn 0.77 0.15 -0.62\r\nvn 0.75 0.15 -0.64\r\nvn 0.74 0.2 -0.64\r\nvn 0.73 0.28 -0.63\r\nvn 0.7 0.61 -0.37\r\nvn 0.72 0.63 -0.3\r\nvn 0.71 0.6 -0.36\r\nvn 0.69 0.59 -0.43\r\nvn 0.71 0.65 -0.27\r\nvn 0.72 0.62 -0.31\r\nvn 0.77 0.52 -0.36\r\nvn 0.75 0.51 -0.42\r\nvn 0.72 0.5 -0.48\r\nvn 0.78 0.4 0.47\r\nvn 0.74 0.39 0.55\r\nvn 0.73 0.41 0.55\r\nvn 0.77 0.42 0.48\r\nvn 0.72 0.39 0.57\r\nvn 0.72 0.41 0.56\r\nvn 0.72 0.41 0.56\r\nvn 0.73 0.41 0.55\r\nvn 0.75 0.43 0.5\r\nvn 0.85 0.46 0.27\r\nvn 0.83 0.43 0.36\r\nvn 0.8 0.46 0.38\r\nvn 0.82 0.49 0.28\r\nvn 0.79 0.46 0.41\r\nvn 0.82 0.49 0.3\r\nvn 0.86 0.49 0.14\r\nvn 0.85 0.48 0.2\r\nvn 0.84 0.5 0.2\r\nvn 0.85 0.51 0.14\r\nvn 0.84 0.51 0.21\r\nvn 0.85 0.51 0.14\r\nvn 0.88 0.48 0.03\r\nvn 0.87 0.49 0.09\r\nvn 0.86 0.51 0.08\r\nvn 0.86 0.5 0.01\r\nvn 0.86 0.51 0.07\r\nvn 0.86 0.51 -0.02\r\nvn 0.72 0.53 -0.45\r\nvn 0.71 0.56 -0.43\r\nvn 0.68 0.6 -0.42\r\nvn 0.68 0.56 -0.47\r\nvn 0.71 0.57 -0.41\r\nvn 0.7 0.6 -0.38\r\nvn 0.71 0.6 -0.36\r\nvn 0.66 0.62 -0.43\r\nvn 0.64 0.59 -0.49\r\nvn 0.85 0.5 -0.13\r\nvn 0.85 0.5 -0.16\r\nvn 0.87 0.48 -0.1\r\nvn 0.87 0.48 -0.09\r\nvn 0.86 0.48 -0.18\r\nvn 0.88 0.46 -0.12\r\nvn 0.89 0.46 -0.07\r\nvn 0.88 0.47 -0.07\r\nvn 0.88 0.48 -0.08\r\nvn 0.78 0.53 -0.33\r\nvn 0.78 0.52 -0.35\r\nvn 0.8 0.52 -0.3\r\nvn 0.79 0.53 -0.3\r\nvn 0.78 0.5 -0.37\r\nvn 0.81 0.49 -0.31\r\nvn 0.83 0.5 -0.27\r\nvn 0.81 0.53 -0.26\r\nvn 0.81 0.53 -0.26\r\nvn 0.87 0.49 -0.05\r\nvn 0.87 0.49 -0.08\r\nvn 0.88 0.47 -0.02\r\nvn 0.74 0.1 0.66\r\nvn 0.74 0.14 0.65\r\nvn 0.76 0.14 0.63\r\nvn 0.76 0.1 0.64\r\nvn 0.74 0.22 0.63\r\nvn 0.76 0.22 0.61\r\nvn 0.82 0.21 0.53\r\nvn 0.83 0.14 0.55\r\nvn 0.83 0.1 0.56\r\nvn 0.85 0.2 -0.48\r\nvn 0.81 0.21 -0.55\r\nvn 0.8 0.14 -0.58\r\nvn 0.84 0.3 -0.45\r\nvn 0.79 0.32 -0.52\r\nvn 0.78 0.34 -0.53\r\nvn 0.79 0.23 -0.57\r\nvn 0.96 0.1 -0.25\r\nvn 0.98 0.09 -0.16\r\nvn 0.97 0.17 -0.15\r\nvn 0.95 0.17 -0.24\r\nvn 0.99 0.09 -0.08\r\nvn 0.98 0.16 -0.08\r\nvn 0.96 0.28 -0.08\r\nvn 0.95 0.27 -0.14\r\nvn 0.94 0.27 -0.23\r\nvn 0.93 0.18 -0.32\r\nvn 0.94 0.1 -0.34\r\nvn 0.91 0.28 -0.3\r\nvn 0.9 0.28 -0.33\r\nvn 0.92 0.18 -0.36\r\nvn 0.81 0.32 0.5\r\nvn 0.75 0.32 0.58\r\nvn 0.74 0.31 0.6\r\nvn 0.75 0.47 -0.47\r\nvn 0.75 0.47 -0.47\r\nvn 0.77 0.37 -0.52\r\nvn 0.77 0.35 -0.53\r\nvn 0.75 0.47 -0.47\r\nvn 0.9 0.39 -0.21\r\nvn 0.92 0.38 -0.13\r\nvn 0.92 0.39 -0.07\r\nvn 0.81 0.41 -0.41\r\nvn 0.85 0.4 -0.34\r\nvn 0.88 0.29 -0.37\r\nvn 0.87 0.4 -0.3\r\nvn 0.75 0.19 0.63\r\nvn 0.81 0.2 0.55\r\nvn 0.73 0.19 0.65\r\nvn 0.95 0.29 -0.15\r\nvn 0.97 0.21 -0.15\r\nvn 0.95 0.29 -0.09\r\nvn 0.97 0.21 -0.09\r\nvn 0.87 0.4 -0.3\r\nvn 0.89 0.4 -0.21\r\nvn 0.83 0.39 -0.39\r\nvn 0.8 0.54 -0.28\r\nvn 0.82 0.54 -0.2\r\nvn 0.72 0.37 -0.58\r\nvn 0.76 0.38 -0.52\r\nvn 0.71 0.37 -0.61\r\nvn 0.69 0.49 -0.54\r\nvn 0.83 0.39 0.4\r\nvn 0.86 0.28 0.42\r\nvn 0.9 0.29 0.31\r\nvn 0.87 0.4 0.3\r\nvn 0.76 0.08 0.64\r\nvn 0.82 0.09 0.56\r\nvn 0.74 0.08 0.67\r\nvn 0.96 0.08 -0.26\r\nvn 0.98 0.08 -0.16\r\nvn 0.99 0.08 -0.09\r\nvn 0.83 0.11 -0.55\r\nvn 0.78 0.11 -0.62\r\nvn 0.78 0.11 -0.61\r\nvn 0.76 0.12 -0.64\r\nvn 0.93 0.09 -0.34\r\nvn 0.93 0.08 -0.36\r\nvn 0.91 0.08 -0.41\r\nvn 0.74 0.66 0.12\r\nvn 0.76 0.64 0.13\r\nvn 0.76 0.64 0.08\r\nvn 0.74 0.67 0.07\r\nvn 0.83 0.54 0.15\r\nvn 0.83 0.55 0.09\r\nvn 0.84 0.55 0.03\r\nvn 0.77 0.64 0.02\r\nvn 0.74 0.67 0.02\r\nvn 0.94 0.08 0.33\r\nvn 0.97 0.08 0.24\r\nvn 0.97 0.1 0.24\r\nvn 0.94 0.1 0.33\r\nvn 0.98 0.08 0.17\r\nvn 0.98 0.1 0.17\r\nvn 0.98 0.14 0.17\r\nvn 0.96 0.14 0.24\r\nvn 0.93 0.14 0.33\r\nvn 0.93 0.22 0.3\r\nvn 0.94 0.25 0.21\r\nvn 0.96 0.15 0.22\r\nvn 0.94 0.14 0.3\r\nvn 0.95 0.27 0.16\r\nvn 0.97 0.15 0.17\r\nvn 0.98 0.08 0.17\r\nvn 0.97 0.09 0.23\r\nvn 0.94 0.09 0.31\r\nvn 0.96 0.21 0.16\r\nvn 0.95 0.21 0.23\r\nvn 0.94 0.29 0.16\r\nvn 0.93 0.29 0.23\r\nvn 0.92 0.21 0.32\r\nvn 0.9 0.41 0.15\r\nvn 0.91 0.41 0.09\r\nvn 0.95 0.29 0.09\r\nvn 0.96 0.29 0.03\r\nvn 0.91 0.41 0.03\r\nvn 0.97 0.07 0.24\r\nvn 0.94 0.08 0.32\r\nvn 0.98 0.07 0.17\r\nvn 0.75 0.63 0.19\r\nvn 0.73 0.66 0.18\r\nvn 0.82 0.54 0.21\r\nvn 0.89 0.4 0.22\r\nvn 0.89 0.08 0.44\r\nvn 0.89 0.1 0.44\r\nvn 0.89 0.14 0.43\r\nvn 0.89 0.21 0.41\r\nvn 0.9 0.14 0.42\r\nvn 0.9 0.1 0.43\r\nvn 0.88 0.2 0.43\r\nvn 0.9 0.09 0.44\r\nvn 0.77 0.64 -0.02\r\nvn 0.74 0.67 -0.02\r\nvn 0.84 0.55 -0.03\r\nvn 0.83 0.55 -0.08\r\nvn 0.76 0.64 -0.08\r\nvn 0.74 0.67 -0.08\r\nvn 0.91 0.41 -0.03\r\nvn 0.96 0.29 -0.03\r\nvn 0.91 0.41 -0.09\r\nvn 1 0.08 0.03\r\nvn 0.99 0.1 0.03\r\nvn 0.99 0.1 0.1\r\nvn 0.99 0.08 0.1\r\nvn 0.99 0.15 0.03\r\nvn 0.98 0.15 0.1\r\nvn 0.96 0.27 0.1\r\nvn 0.98 0.16 0.11\r\nvn 0.96 0.27 0.04\r\nvn 0.99 0.16 0.04\r\nvn 1 0.09 0.04\r\nvn 0.99 0.09 0.11\r\nvn 0.98 0.21 0.03\r\nvn 0.97 0.21 0.09\r\nvn 0.99 0.08 0.11\r\nvn 1 0.08 0.04\r\nvn 0.99 0.1 -0.03\r\nvn 1 0.08 -0.03\r\nvn 0.99 0.15 -0.03\r\nvn 0.96 0.28 -0.02\r\nvn 0.99 0.16 -0.02\r\nvn 1 0.09 -0.02\r\nvn 0.98 0.21 -0.03\r\nvn 1 0.08 -0.03\r\nvn 0.72 0.66 -0.19\r\nvn 0.73 0.67 -0.14\r\nvn 0.76 0.64 -0.13\r\nvn 0.75 0.64 -0.2\r\nvn 0.83 0.54 -0.14\r\nvn 0.9 0.4 -0.14\r\nvn 0.92 0.4 -0.01\r\nvn 0.92 0.39 0.04\r\nvn 0.91 0.4 0.09\r\nvn 0.9 0.4 0.15\r\nvn 0.9 0.38 0.2\r\nvn 0.89 0.35 0.28\r\nvn 0.86 0.33 0.38\r\nvn 0.8 0.13 -0.59\r\nvn 0.67 0.57 -0.48\r\nvn 0.68 0.6 -0.43\r\nvn 0.74 0.2 -0.64\r\nvn 0.73 0.28 -0.63\r\nvn 0.75 0.15 -0.64\r\nvn 0.75 0.16 -0.64\r\nvn 0.74 0.21 -0.64\r\nvn 0.73 0.28 -0.63\r\nvn 0.74 0.56 -0.37\r\nvn 0.76 0.56 -0.33\r\nvn 0.74 0.53 -0.41\r\nvn 0.79 0.24 -0.57\r\nvn 0.79 0.25 -0.57\r\nvn 0.77 0.44 -0.46\r\nvn 0.71 0.36 -0.61\r\nvn 0.71 0.36 -0.61\r\nvn 0.76 0.13 -0.64\r\nvn 0.76 0.13 -0.64\r\nvn 0.93 0.08 -0.35\r\nvn 0.85 0.2 -0.48\r\nvn 0.84 0.28 -0.47\r\nvn 0.87 0.14 -0.48\r\nvn 0.73 0.63 -0.26\r\nvn 0.71 0.66 -0.24\r\nvn 0.83 0.52 -0.22\r\nvn 0.83 0.52 -0.2\r\nvn 0.84 0.49 -0.24\r\nvn 0.9 0.19 -0.4\r\nvn 0.88 0.4 -0.27\r\nvn 0.8 0.38 -0.45\r\nvn 0.88 0.09 -0.47\r\nvt 0.1859 0.5654\r\nvt 0.1335 0.5791\r\nvt 0.1297 0.5314\r\nvt 0.1822 0.5163\r\nvt 0.0816 0.5947\r\nvt 0.0776 0.5483\r\nvt 0.0745 0.501\r\nvt 0.1267 0.4821\r\nvt 0.1793 0.4652\r\nvt 0.1301 0.7621\r\nvt 0.1121 0.725\r\nvt 0.1604 0.7113\r\nvt 0.1756 0.7519\r\nvt 0.098 0.6832\r\nvt 0.1482 0.6688\r\nvt 0.199 0.6564\r\nvt 0.2093 0.6997\r\nvt 0.222 0.7433\r\nvt 0.1749 0.8178\r\nvt 0.1516 0.7918\r\nvt 0.1934 0.7904\r\nvt 0.2126 0.8278\r\nvt 0.2367 0.7888\r\nvt 0.2524 0.8353\r\nvt 0.9413 0.3913\r\nvt 0.9297 0.4343\r\nvt 0.9031 0.4258\r\nvt 0.9135 0.3829\r\nvt 0.9179 0.4849\r\nvt 0.8919 0.4767\r\nvt 0.8638 0.4678\r\nvt 0.8738 0.4164\r\nvt 0.8827 0.3735\r\nvt 0.283 0.7868\r\nvt 0.2966 0.8378\r\nvt 0.2704 0.7377\r\nvt 0.3191 0.7341\r\nvt 0.3301 0.7848\r\nvt 0.342 0.8375\r\nvt 0.8065 0.3517\r\nvt 0.8016 0.3949\r\nvt 0.763 0.3871\r\nvt 0.7678 0.3438\r\nvt 0.796 0.4478\r\nvt 0.7578 0.4405\r\nvt 0.7194 0.4347\r\nvt 0.724 0.3808\r\nvt 0.7285 0.3374\r\nvt 0.6439 0.5331\r\nvt 0.6803 0.5345\r\nvt 0.6782 0.5829\r\nvt 0.6427 0.5822\r\nvt 0.7145 0.5369\r\nvt 0.7116 0.5845\r\nvt 0.7078 0.6305\r\nvt 0.6753 0.6293\r\nvt 0.6408 0.6291\r\nvt 0.8153 0.8083\r\nvt 0.7968 0.8109\r\nvt 0.8144 0.7737\r\nvt 0.8355 0.7746\r\nvt 0.7757 0.8138\r\nvt 0.7921 0.7729\r\nvt 0.8076 0.7316\r\nvt 0.8313 0.7351\r\nvt 0.8547 0.7387\r\nvt 0.6445 0.4261\r\nvt 0.6444 0.4807\r\nvt 0.6003 0.4794\r\nvt 0.5998 0.4241\r\nvt 0.6005 0.5325\r\nvt 0.5548 0.5326\r\nvt 0.5541 0.4791\r\nvt 0.5533 0.4234\r\nvt 0.7775 0.6366\r\nvt 0.7833 0.5915\r\nvt 0.8135 0.5962\r\nvt 0.8064 0.641\r\nvt 0.7876 0.5451\r\nvt 0.8193 0.5508\r\nvt 0.8484 0.5568\r\nvt 0.8413 0.6013\r\nvt 0.8327 0.6458\r\nvt 0.7235 0.8209\r\nvt 0.7029 0.8231\r\nvt 0.7156 0.7721\r\nvt 0.7413 0.7724\r\nvt 0.6831 0.825\r\nvt 0.6897 0.7723\r\nvt 0.6962 0.7217\r\nvt 0.7271 0.723\r\nvt 0.757 0.7251\r\nvt 0.165 0.3092\r\nvt 0.1092 0.3301\r\nvt 0.0924 0.2833\r\nvt 0.1519 0.2638\r\nvt 0.0533 0.3536\r\nvt 0.0321 0.3043\r\nvt 0.0046 0.2418\r\nvt 0.0708 0.224\r\nvt 0.135 0.2066\r\nvt 0.2761 0.2814\r\nvt 0.2207 0.2933\r\nvt 0.2098 0.2476\r\nvt 0.2665 0.2347\r\nvt 0.1956 0.1898\r\nvt 0.2543 0.1756\r\nvt 0.381 0.2664\r\nvt 0.3307 0.2726\r\nvt 0.3225 0.2254\r\nvt 0.3743 0.219\r\nvt 0.3129 0.1657\r\nvt 0.3672 0.1591\r\nvt 0.4623 0.2599\r\nvt 0.4232 0.2624\r\nvt 0.4184 0.215\r\nvt 0.4599 0.2123\r\nvt 0.4131 0.155\r\nvt 0.4572 0.1522\r\nvt 0.9682 0.3455\r\nvt 0.9385 0.3351\r\nvt 0.9556 0.3179\r\nvt 0.9847 0.3321\r\nvt 0.9049 0.3235\r\nvt 0.9216 0.3023\r\nvt 0.9421 0.2768\r\nvt 0.9763 0.2976\r\nvt 1.0046 0.3168\r\nvt 0.6727 0.1733\r\nvt 0.6605 0.2244\r\nvt 0.6062 0.216\r\nvt 0.6163 0.1597\r\nvt 0.6512 0.2651\r\nvt 0.5993 0.2606\r\nvt 0.548 0.2583\r\nvt 0.5522 0.2107\r\nvt 0.5593 0.1504\r\nvt 0.8482 0.23\r\nvt 0.8325 0.2667\r\nvt 0.7927 0.2545\r\nvt 0.8088 0.214\r\nvt 0.82 0.2965\r\nvt 0.7799 0.2872\r\nvt 0.7401 0.2797\r\nvt 0.7533 0.2446\r\nvt 0.7701 0.2009\r\nvt 0.504 0.2102\r\nvt 0.5061 0.1493\r\nvt 0.5031 0.2583\r\nvt 0.0675 0.4149\r\nvt 0.0657 0.3956\r\nvt 0.1194 0.3715\r\nvt 0.1209 0.3914\r\nvt 0.0643 0.386\r\nvt 0.1182 0.3618\r\nvt 0.1722 0.3404\r\nvt 0.1732 0.3501\r\nvt 0.1744 0.3707\r\nvt 0.809 0.3311\r\nvt 0.8494 0.3419\r\nvt 0.8459 0.3624\r\nvt 0.8108 0.3218\r\nvt 0.8519 0.3329\r\nvt 0.8907 0.3446\r\nvt 0.8874 0.3533\r\nvt 0.6445 0.3255\r\nvt 0.599 0.3224\r\nvt 0.5997 0.301\r\nvt 0.6454 0.3043\r\nvt 0.5525 0.3212\r\nvt 0.5531 0.2998\r\nvt 0.5528 0.2902\r\nvt 0.6001 0.2915\r\nvt 0.6466 0.2948\r\nvt 0.6891 0.3099\r\nvt 0.6875 0.3309\r\nvt 0.6906 0.3004\r\nvt 0.7322 0.3071\r\nvt 0.7307 0.3166\r\nvt 0.1706 0.3308\r\nvt 0.1163 0.3522\r\nvt 0.0621 0.3764\r\nvt 0.9582 0.3551\r\nvt 0.9286 0.3462\r\nvt 0.9522 0.3631\r\nvt 0.9231 0.3543\r\nvt 0.8956 0.3362\r\nvt 0.6475 0.2851\r\nvt 0.5986 0.2819\r\nvt 0.5497 0.2806\r\nvt 0.8136 0.3126\r\nvt 0.7738 0.3044\r\nvt 0.7716 0.3137\r\nvt 0.7342 0.2977\r\nvt 0.1395 0.6249\r\nvt 0.1914 0.6119\r\nvt 0.0881 0.6397\r\nvt 0.5992 0.6295\r\nvt 0.6001 0.5821\r\nvt 0.5555 0.6302\r\nvt 0.5553 0.5825\r\nvt 0.6712 0.6747\r\nvt 0.6379 0.6749\r\nvt 0.7024 0.6754\r\nvt 0.6668 0.7217\r\nvt 0.635 0.7225\r\nvt 0.7964 0.6846\r\nvt 0.7689 0.6805\r\nvt 0.8213 0.6892\r\nvt 0.7833 0.7281\r\nvt 0.2596 0.6922\r\nvt 0.2509 0.648\r\nvt 0.3026 0.6424\r\nvt 0.31 0.6875\r\nvt 0.1236 0.4319\r\nvt 0.1766 0.4129\r\nvt 0.0708 0.4533\r\nvt 0.6443 0.3703\r\nvt 0.5992 0.3676\r\nvt 0.5527 0.3666\r\nvt 0.7916 0.4975\r\nvt 0.8251 0.5053\r\nvt 0.8316 0.4575\r\nvt 0.8557 0.5135\r\nvt 0.6816 0.4832\r\nvt 0.6829 0.4298\r\nvt 0.7166 0.4868\r\nvt 0.4259 0.8359\r\nvt 0.4175 0.7816\r\nvt 0.4528 0.7808\r\nvt 0.4597 0.8358\r\nvt 0.4098 0.7294\r\nvt 0.4466 0.7281\r\nvt 0.4811 0.7271\r\nvt 0.4858 0.7801\r\nvt 0.4911 0.8353\r\nvt 0.2864 0.4431\r\nvt 0.3396 0.4359\r\nvt 0.3414 0.4904\r\nvt 0.289 0.4967\r\nvt 0.3885 0.4307\r\nvt 0.3897 0.4859\r\nvt 0.3922 0.5388\r\nvt 0.3444 0.5427\r\nvt 0.2924 0.5481\r\nvt 0.2821 0.3131\r\nvt 0.3393 0.3044\r\nvt 0.3403 0.314\r\nvt 0.2827 0.3228\r\nvt 0.3909 0.2982\r\nvt 0.392 0.3078\r\nvt 0.3912 0.3292\r\nvt 0.34 0.3353\r\nvt 0.2833 0.344\r\nvt 0.3953 0.5882\r\nvt 0.3481 0.5916\r\nvt 0.3991 0.6354\r\nvt 0.3529 0.6383\r\nvt 0.2968 0.5963\r\nvt 0.4038 0.6816\r\nvt 0.442 0.6799\r\nvt 0.4385 0.6334\r\nvt 0.4754 0.6321\r\nvt 0.4778 0.6787\r\nvt 0.3395 0.3801\r\nvt 0.2845 0.3881\r\nvt 0.3895 0.3743\r\nvt 0.3758 0.783\r\nvt 0.3859 0.8367\r\nvt 0.3665 0.7314\r\nvt 0.3589 0.684\r\nvt 0.2328 0.4525\r\nvt 0.2356 0.5051\r\nvt 0.2393 0.5554\r\nvt 0.2266 0.3246\r\nvt 0.2273 0.3344\r\nvt 0.2283 0.3553\r\nvt 0.2442 0.6028\r\nvt 0.2302 0.3987\r\nvt 0.5205 0.7788\r\nvt 0.5242 0.8338\r\nvt 0.5174 0.726\r\nvt 0.5558 0.7248\r\nvt 0.5572 0.7773\r\nvt 0.5591 0.832\r\nvt 0.5155 0.6777\r\nvt 0.5143 0.631\r\nvt 0.5554 0.6767\r\nvt 0.4677 0.4251\r\nvt 0.4696 0.4809\r\nvt 0.4309 0.4829\r\nvt 0.4293 0.4273\r\nvt 0.4715 0.5344\r\nvt 0.4331 0.5361\r\nvt 0.4291 0.2943\r\nvt 0.4295 0.3039\r\nvt 0.4643 0.292\r\nvt 0.464 0.3016\r\nvt 0.4641 0.323\r\nvt 0.4291 0.3253\r\nvt 0.4734 0.5844\r\nvt 0.4356 0.5859\r\nvt 0.4289 0.3706\r\nvt 0.4656 0.3684\r\nvt 0.5106 0.4796\r\nvt 0.5091 0.4238\r\nvt 0.512 0.5332\r\nvt 0.5065 0.2905\r\nvt 0.5065 0.3001\r\nvt 0.5064 0.3216\r\nvt 0.5132 0.5833\r\nvt 0.5075 0.3669\r\nvt 0.6307 0.8293\r\nvt 0.5959 0.8307\r\nvt 0.596 0.776\r\nvt 0.6327 0.7747\r\nvt 0.5965 0.7236\r\nvt 0.5978 0.6757\r\nvt 0.5046 0.2808\r\nvt 0.4637 0.2823\r\nvt 0.4269 0.2846\r\nvt 0.3869 0.2886\r\nvt 0.3362 0.2947\r\nvt 0.2806 0.3034\r\nvt 0.2254 0.315\r\nvt 0.8393 0.4054\r\nvt 0.7672 0.7726\r\nvt 0.7496 0.8174\r\nvt 0.8691 0.6064\r\nvt 0.8593 0.6506\r\nvt 0.8769 0.5627\r\nvt 0.905 0.5684\r\nvt 0.897 0.6116\r\nvt 0.8861 0.6555\r\nvt 0.8781 0.284\r\nvt 0.896 0.2526\r\nvt 0.8635 0.3097\r\nvt 0.9191 0.3628\r\nvt 0.9477 0.3714\r\nvt 0.8557 0.3241\r\nvt 0.8465 0.6938\r\nvt 0.8718 0.6985\r\nvt 0.8838 0.5209\r\nvt 0.9108 0.5279\r\nvt 0.6852 0.375\r\nvt 0.748 0.5875\r\nvt 0.7432 0.633\r\nvt 0.7515 0.5405\r\nvt 0.663 0.7733\r\nvt 0.6594 0.8272\r\nvt 0.7094 0.2342\r\nvt 0.7244 0.1872\r\nvt 0.6977 0.2719\r\nvt 0.7701 0.3231\r\nvt 0.6924 0.2908\r\nvt 0.7363 0.6774\r\nvt 0.7543 0.4915\r\no side_strip_bot_R\r\nusemtl side_strip_bot_R\r\nf 15295/16001/13810 15296/16002/13811 15297/16003/13812 15298/16004/13813\r\nf 15296/16002/13811 15299/16005/13814 15300/16006/13815 15297/16003/13812\r\nf 15301/16007/13816 15302/16008/13817 15297/16003/13812 15300/16006/13815\r\nf 15302/16008/13817 15303/16009/13818 15298/16004/13813 15297/16003/13812\r\nf 15304/16010/13819 15305/16011/13820 15306/16012/13821 15307/16013/13822\r\nf 15305/16011/13820 15308/16014/13823 15309/16015/13824 15306/16012/13821\r\nf 15310/16016/13825 15311/16017/13826 15306/16012/13821 15309/16015/13824\r\nf 15311/16017/13826 15312/16018/13827 15307/16013/13822 15306/16012/13821\r\nf 15313/16019/13828 15314/16020/13829 15315/16021/13830 15316/16022/13831\r\nf 15314/16020/13829 15304/16010/13819 15307/16013/13822 15315/16021/13830\r\nf 15312/16018/13827 15317/16023/13832 15315/16021/13830 15307/16013/13822\r\nf 15317/16023/13832 15318/16024/13833 15316/16022/13831 15315/16021/13830\r\nf 15319/16025/13834 15320/16026/13835 15321/16027/13836 15322/16028/13837\r\nf 15320/16026/13835 15323/16029/13838 15324/16030/13839 15321/16027/13836\r\nf 15325/16031/13840 15326/16032/13841 15321/16027/13836 15324/16030/13839\r\nf 15326/16032/13841 15327/16033/13842 15322/16028/13837 15321/16027/13836\r\nf 15318/16024/13833 15317/16023/13832 15328/16034/13843 15329/16035/13844\r\nf 15317/16023/13832 15312/16018/13827 15330/16036/13845 15328/16034/13843\r\nf 15331/16037/13846 15332/16038/13847 15328/16034/13843 15330/16036/13845\r\nf 15332/16038/13847 15333/16039/13848 15329/16035/13844 15328/16034/13843\r\nf 15334/16040/13849 15335/16041/13850 15336/16042/13851 15337/16043/13852\r\nf 15335/16041/13850 15338/16044/13853 15339/16045/13854 15336/16042/13851\r\nf 15340/16046/13855 15341/16047/13856 15336/16042/13851 15339/16045/13854\r\nf 15341/16047/13856 15342/16048/13857 15337/16043/13852 15336/16042/13851\r\nf 15343/16049/13858 15344/16050/13859 15345/16051/13860 15346/16052/13861\r\nf 15344/16050/13859 15347/16053/13862 15348/16054/13863 15345/16051/13860\r\nf 15349/16055/13864 15350/16056/13865 15345/16051/13860 15348/16054/13863\r\nf 15350/16056/13865 15351/16057/13866 15346/16052/13861 15345/16051/13860\r\nf 15352/16058/13867 15353/16059/13868 15354/16060/13869 15355/16061/13870\r\nf 15353/16059/13868 15356/16062/13871 15357/16063/13872 15354/16060/13869\r\nf 15358/16064/13873 15359/16065/13874 15354/16060/13869 15357/16063/13872\r\nf 15359/16065/13874 15360/16066/13875 15355/16061/13870 15354/16060/13869\r\nf 15361/16067/13876 15362/16068/13877 15363/16069/13878 15364/16070/13879\r\nf 15362/16068/13877 15343/16049/13858 15365/16071/13880 15363/16069/13878\r\nf 15366/16072/13881 15367/16073/13882 15363/16069/13878 15365/16071/13880\r\nf 15367/16073/13882 15368/16074/13883 15364/16070/13879 15363/16069/13878\r\nf 15369/16075/13884 15370/16076/13885 15371/16077/13886 15372/16078/13887\r\nf 15370/16076/13885 15373/16079/13888 15374/16080/13889 15371/16077/13886\r\nf 15375/16081/13890 15376/16082/13891 15371/16077/13886 15374/16080/13889\r\nf 15376/16082/13891 15377/16083/13892 15372/16078/13887 15371/16077/13886\r\nf 15378/16084/13893 15379/16085/13894 15380/16086/13895 15381/16087/13896\r\nf 15379/16085/13894 15382/16088/13897 15383/16089/13898 15380/16086/13895\r\nf 15384/16090/13899 15385/16091/13900 15380/16086/13895 15383/16089/13898\r\nf 15385/16091/13900 15386/16092/13901 15381/16087/13896 15380/16086/13895\r\nf 15387/16093/13902 15388/16094/13903 15389/16095/13904 15390/16096/13905\r\nf 15388/16094/13903 15391/16097/13906 15392/16098/13907 15389/16095/13904\r\nf 15393/16099/13908 15394/16100/13909 15389/16095/13904 15392/16098/13907\r\nf 15394/16100/13909 15395/16101/13910 15390/16096/13905 15389/16095/13904\r\nf 15396/16102/13911 15397/16103/13912 15398/16104/13913 15399/16105/13914\r\nf 15397/16103/13912 15387/16093/13902 15390/16096/13905 15398/16104/13913\r\nf 15395/16101/13910 15400/16106/13915 15398/16104/13913 15390/16096/13905\r\nf 15400/16106/13915 15401/16107/13916 15399/16105/13914 15398/16104/13913\r\nf 15402/16108/13917 15403/16109/13918 15404/16110/13919 15405/16111/13920\r\nf 15403/16109/13918 15396/16102/13911 15399/16105/13914 15404/16110/13919\r\nf 15401/16107/13916 15406/16112/13921 15404/16110/13919 15399/16105/13914\r\nf 15406/16112/13921 15407/16113/13922 15405/16111/13920 15404/16110/13919\r\nf 15408/16114/13923 15409/16115/13924 15410/16116/13925 15411/16117/13926\r\nf 15409/16115/13924 15402/16108/13917 15405/16111/13920 15410/16116/13925\r\nf 15407/16113/13922 15412/16118/13927 15410/16116/13925 15405/16111/13920\r\nf 15412/16118/13927 15413/16119/13928 15411/16117/13926 15410/16116/13925\r\nf 15414/16120/13929 15415/16121/13930 15416/16122/13931 15417/16123/13932\r\nf 15415/16121/13930 15418/16124/13933 15419/16125/13934 15416/16122/13931\r\nf 15420/16126/13935 15421/16127/13936 15416/16122/13931 15419/16125/13934\r\nf 15421/16127/13936 15422/16128/13937 15417/16123/13932 15416/16122/13931\r\nf 15423/16129/13938 15424/16130/13939 15425/16131/13940 15426/16132/13941\r\nf 15424/16130/13939 15427/16133/13942 15428/16134/13943 15425/16131/13940\r\nf 15429/16135/13944 15430/16136/13945 15425/16131/13940 15428/16134/13943\r\nf 15430/16136/13945 15431/16137/13946 15426/16132/13941 15425/16131/13940\r\nf 15432/16138/13947 15433/16139/13948 15434/16140/13949 15435/16141/13950\r\nf 15433/16139/13948 15436/16142/13951 15437/16143/13952 15434/16140/13949\r\nf 15438/16144/13953 15439/16145/13954 15434/16140/13949 15437/16143/13952\r\nf 15439/16145/13954 15440/16146/13955 15435/16141/13950 15434/16140/13949\r\nf 15431/16137/13946 15430/16136/13945 15441/16147/13956 15442/16148/13957\r\nf 15430/16136/13945 15429/16135/13944 15443/16149/13958 15441/16147/13956\r\nf 15408/16114/13923 15411/16117/13926 15441/16147/13956 15443/16149/13958\r\nf 15411/16117/13926 15413/16119/13928 15442/16148/13957 15441/16147/13956\r\nf 15444/16150/13959 15445/16151/13960 15446/16152/13961 15447/16153/13962\r\nf 15445/16151/13960 15448/16154/13963 15449/16155/13964 15446/16152/13961\r\nf 15450/16156/13965 15451/16157/13966 15446/16152/13961 15449/16155/13964\r\nf 15451/16157/13966 15452/16158/13967 15447/16153/13962 15446/16152/13961\r\nf 15334/16040/13849 15453/16159/13968 15454/16160/13969 15455/16161/13970\r\nf 15453/16159/13968 15456/16162/13971 15457/16163/13972 15454/16160/13969\r\nf 15458/16164/13973 15459/16165/13974 15454/16160/13969 15457/16163/13972\r\nf 15459/16165/13974 15327/16033/13842 15455/16161/13970 15454/16160/13969\r\nf 15460/16166/13975 15461/16167/13976 15462/16168/13977 15463/16169/13978\r\nf 15461/16167/13976 15464/16170/13979 15465/16171/13980 15462/16168/13977\r\nf 15466/16172/13981 15467/16173/13982 15462/16168/13977 15465/16171/13980\r\nf 15467/16173/13982 15468/16174/13983 15463/16169/13978 15462/16168/13977\r\nf 15460/16166/13975 15463/16169/13978 15469/16175/13984 15470/16176/13985\r\nf 15463/16169/13978 15468/16174/13983 15471/16177/13986 15469/16175/13984\r\nf 15472/16178/13987 15473/16179/13988 15469/16175/13984 15471/16177/13986\r\nf 15473/16179/13988 15342/16048/13857 15470/16176/13985 15469/16175/13984\r\nf 15387/16093/13902 15474/16180/13989 15475/16181/13990 15388/16094/13903\r\nf 15474/16180/13989 15450/16156/13965 15449/16155/13964 15475/16181/13990\r\nf 15448/16154/13963 15476/16182/13991 15475/16181/13990 15449/16155/13964\r\nf 15476/16182/13991 15391/16097/13906 15388/16094/13903 15475/16181/13990\r\nf 15414/16120/13929 15477/16183/13992 15478/16184/13993 15415/16121/13930\r\nf 15477/16183/13992 15479/16185/13994 15480/16186/13995 15478/16184/13993\r\nf 15458/16164/13973 15481/16187/13996 15478/16184/13993 15480/16186/13995\r\nf 15481/16187/13996 15418/16124/13933 15415/16121/13930 15478/16184/13993\r\nf 15427/16133/13942 15482/16188/13997 15483/16189/13998 15428/16134/13943\r\nf 15482/16188/13997 15468/16174/13983 15467/16173/13982 15483/16189/13998\r\nf 15466/16172/13981 15484/16190/13999 15483/16189/13998 15467/16173/13982\r\nf 15484/16190/13999 15429/16135/13944 15428/16134/13943 15483/16189/13998\r\nf 15436/16142/13951 15485/16191/14000 15486/16192/14001 15437/16143/13952\r\nf 15485/16191/14000 15456/16162/13971 15487/16193/14002 15486/16192/14001\r\nf 15472/16178/13987 15488/16194/14003 15486/16192/14001 15487/16193/14002\r\nf 15488/16194/14003 15438/16144/13953 15437/16143/13952 15486/16192/14001\r\nf 15310/16016/13825 15309/16015/13824 15489/16195/14004 15490/16196/14005\r\nf 15309/16015/13824 15308/16014/13823 15491/16197/14006 15489/16195/14004\r\nf 15299/16005/13814 15296/16002/13811 15489/16195/14004 15491/16197/14006\r\nf 15296/16002/13811 15295/16001/13810 15490/16196/14005 15489/16195/14004\r\nf 15351/16057/13866 15492/16198/14007 15493/16199/14008 15346/16052/13861\r\nf 15492/16198/14007 15494/16200/14009 15495/16201/14010 15493/16199/14008\r\nf 15366/16072/13881 15365/16071/13880 15493/16199/14008 15495/16201/14010\r\nf 15365/16071/13880 15343/16049/13858 15346/16052/13861 15493/16199/14008\r\nf 15351/16057/13866 15350/16056/13865 15496/16202/14011 15497/16203/14012\r\nf 15350/16056/13865 15349/16055/13864 15498/16204/14013 15496/16202/14011\r\nf 15384/16090/13899 15499/16205/14014 15496/16202/14011 15498/16204/14013\r\nf 15499/16205/14014 15500/16206/14015 15497/16203/14012 15496/16202/14011\r\nf 15369/16075/13884 15372/16078/13887 15501/16207/14016 15502/16208/14017\r\nf 15372/16078/13887 15377/16083/13892 15503/16209/14018 15501/16207/14016\r\nf 15358/16064/13873 15504/16210/14019 15501/16207/14016 15503/16209/14018\r\nf 15504/16210/14019 15386/16092/13901 15502/16208/14017 15501/16207/14016\r\nf 15312/16018/13827 15311/16017/13826 15505/16211/14020 15330/16036/13845\r\nf 15311/16017/13826 15310/16016/13825 15506/16212/14021 15505/16211/14020\r\nf 15507/16213/14022 15508/16214/14023 15505/16211/14020 15506/16212/14021\r\nf 15508/16214/14023 15331/16037/13846 15330/16036/13845 15505/16211/14020\r\nf 15303/16009/13818 15302/16008/13817 15509/16215/14024 15510/16216/14025\r\nf 15302/16008/13817 15301/16007/13816 15511/16217/14026 15509/16215/14024\r\nf 15444/16150/13959 15447/16153/13962 15509/16215/14024 15511/16217/14026\r\nf 15447/16153/13962 15452/16158/13967 15510/16216/14025 15509/16215/14024\r\nf 15460/16166/13975 15512/16218/14027 15513/16219/14028 15461/16167/13976\r\nf 15512/16218/14027 15361/16067/13876 15364/16070/13879 15513/16219/14028\r\nf 15368/16074/13883 15514/16220/14029 15513/16219/14028 15364/16070/13879\r\nf 15514/16220/14029 15464/16170/13979 15461/16167/13976 15513/16219/14028\r\nf 15373/16079/13888 15515/16221/14030 15516/16222/14031 15374/16080/13889\r\nf 15515/16221/14030 15338/16044/13853 15517/16223/14032 15516/16222/14031\r\nf 15325/16031/13840 15518/16224/14033 15516/16222/14031 15517/16223/14032\r\nf 15518/16224/14033 15375/16081/13890 15374/16080/13889 15516/16222/14031\r\nf 15343/16049/13858 15362/16068/13877 15519/16225/14034 15344/16050/13859\r\nf 15362/16068/13877 15361/16067/13876 15520/16226/14035 15519/16225/14034\r\nf 15340/16046/13855 15521/16227/14036 15519/16225/14034 15520/16226/14035\r\nf 15521/16227/14036 15347/16053/13862 15344/16050/13859 15519/16225/14034\r\nf 15522/16228/14037 15523/16229/14038 15524/16230/14039 15525/16231/14040\r\nf 15523/16229/14038 15526/16232/14041 15527/16233/14042 15524/16230/14039\r\nf 15528/16234/14043 15529/16235/14044 15524/16230/14039 15527/16233/14042\r\nf 15529/16235/14044 15530/16236/14045 15525/16231/14040 15524/16230/14039\r\nf 15531/16237/14046 15532/16238/14047 15533/16239/14048 15534/16240/14049\r\nf 15532/16238/14047 15535/16241/14050 15536/16242/14051 15533/16239/14048\r\nf 15537/16243/14052 15538/16244/14053 15533/16239/14048 15536/16242/14051\r\nf 15538/16244/14053 15539/16245/14054 15534/16240/14049 15533/16239/14048\r\nf 15540/16246/14055 15541/16247/14056 15542/16248/14057 15543/16249/14058\r\nf 15541/16247/14056 15544/16250/14059 15545/16251/14060 15542/16248/14057\r\nf 15546/16252/14061 15547/16253/14062 15542/16248/14057 15545/16251/14060\r\nf 15547/16253/14062 15548/16254/14063 15543/16249/14058 15542/16248/14057\r\nf 15537/16243/14052 15549/16255/14064 15550/16256/14065 15538/16244/14053\r\nf 15549/16255/14064 15551/16257/14066 15552/16258/14067 15550/16256/14065\r\nf 15507/16213/14022 15553/16259/14068 15550/16256/14065 15552/16258/14067\r\nf 15553/16259/14068 15539/16245/14054 15538/16244/14053 15550/16256/14065\r\nf 15526/16232/14041 15554/16260/14069 15555/16261/14070 15527/16233/14042\r\nf 15554/16260/14069 15551/16257/14066 15556/16262/14071 15555/16261/14070\r\nf 15557/16263/14072 15558/16264/14073 15555/16261/14070 15556/16262/14071\r\nf 15558/16264/14073 15528/16234/14043 15527/16233/14042 15555/16261/14070\r\nf 15548/16254/14063 15547/16253/14062 15559/16265/14074 15560/16266/14075\r\nf 15547/16253/14062 15546/16252/14061 15561/16267/14076 15559/16265/14074\r\nf 15535/16241/14050 15532/16238/14047 15559/16265/14074 15561/16267/14076\r\nf 15532/16238/14047 15531/16237/14046 15560/16266/14075 15559/16265/14074\r\nf 15333/16039/13848 15332/16038/13847 15562/16268/14077 15563/16269/14078\r\nf 15332/16038/13847 15331/16037/13846 15564/16270/14079 15562/16268/14077\r\nf 15526/16232/14041 15523/16229/14038 15562/16268/14077 15564/16270/14079\r\nf 15523/16229/14038 15522/16228/14037 15563/16269/14078 15562/16268/14077\r\nf 15331/16037/13846 15508/16214/14023 15565/16271/14080 15564/16270/14079\r\nf 15508/16214/14023 15507/16213/14022 15552/16258/14067 15565/16271/14080\r\nf 15551/16257/14066 15554/16260/14069 15565/16271/14080 15552/16258/14067\r\nf 15554/16260/14069 15526/16232/14041 15564/16270/14079 15565/16271/14080\r\nf 15303/16009/13818 15566/16272/14081 15567/16273/14082 15298/16004/13813\r\nf 15566/16272/14081 15531/16237/14046 15534/16240/14049 15567/16273/14082\r\nf 15539/16245/14054 15568/16274/14083 15567/16273/14082 15534/16240/14049\r\nf 15568/16274/14083 15295/16001/13810 15298/16004/13813 15567/16273/14082\r\nf 15450/16156/13965 15569/16275/14084 15570/16276/14085 15451/16157/13966\r\nf 15569/16275/14084 15540/16246/14055 15543/16249/14058 15570/16276/14085\r\nf 15548/16254/14063 15571/16277/14086 15570/16276/14085 15543/16249/14058\r\nf 15571/16277/14086 15452/16158/13967 15451/16157/13966 15570/16276/14085\r\nf 15539/16245/14054 15553/16259/14068 15572/16278/14087 15568/16274/14083\r\nf 15553/16259/14068 15507/16213/14022 15506/16212/14021 15572/16278/14087\r\nf 15310/16016/13825 15490/16196/14005 15572/16278/14087 15506/16212/14021\r\nf 15490/16196/14005 15295/16001/13810 15568/16274/14083 15572/16278/14087\r\nf 15452/16158/13967 15571/16277/14086 15573/16279/14088 15510/16216/14025\r\nf 15571/16277/14086 15548/16254/14063 15560/16266/14075 15573/16279/14088\r\nf 15531/16237/14046 15566/16272/14081 15573/16279/14088 15560/16266/14075\r\nf 15566/16272/14081 15303/16009/13818 15510/16216/14025 15573/16279/14088\r\nf 15530/16236/14045 15529/16235/14044 15574/16280/14089 15575/16281/14090\r\nf 15529/16235/14044 15528/16234/14043 15576/16282/14091 15574/16280/14089\r\nf 15577/16283/14092 15578/16284/14093 15574/16280/14089 15576/16282/14091\r\nf 15578/16284/14093 15579/16285/14094 15575/16281/14090 15574/16280/14089\r\nf 15528/16234/14043 15558/16264/14073 15580/16286/14095 15576/16282/14091\r\nf 15558/16264/14073 15557/16263/14072 15581/16287/14096 15580/16286/14095\r\nf 15494/16200/14009 15582/16288/14097 15580/16286/14095 15581/16287/14096\r\nf 15582/16288/14097 15577/16283/14092 15576/16282/14091 15580/16286/14095\r\nf 15583/16289/14098 15584/16290/14099 15585/16291/14100 15586/16292/14101\r\nf 15584/16290/14099 15587/16293/14102 15588/16294/14103 15585/16291/14100\r\nf 15537/16243/14052 15536/16242/14051 15585/16291/14100 15588/16294/14103\r\nf 15536/16242/14051 15535/16241/14050 15586/16292/14101 15585/16291/14100\r\nf 15544/16250/14059 15589/16295/14104 15590/16296/14105 15545/16251/14060\r\nf 15589/16295/14104 15591/16297/14106 15592/16298/14107 15590/16296/14105\r\nf 15593/16299/14108 15594/16300/14109 15590/16296/14105 15592/16298/14107\r\nf 15594/16300/14109 15546/16252/14061 15545/16251/14060 15590/16296/14105\r\nf 15587/16293/14102 15595/16301/14110 15596/16302/14111 15588/16294/14103\r\nf 15595/16301/14110 15557/16263/14072 15556/16262/14071 15596/16302/14111\r\nf 15551/16257/14066 15549/16255/14064 15596/16302/14111 15556/16262/14071\r\nf 15549/16255/14064 15537/16243/14052 15588/16294/14103 15596/16302/14111\r\nf 15546/16252/14061 15594/16300/14109 15597/16303/14112 15561/16267/14076\r\nf 15594/16300/14109 15593/16299/14108 15598/16304/14113 15597/16303/14112\r\nf 15583/16289/14098 15586/16292/14101 15597/16303/14112 15598/16304/14113\r\nf 15586/16292/14101 15535/16241/14050 15561/16267/14076 15597/16303/14112\r\nf 15368/16074/13883 15367/16073/13882 15599/16305/14114 15600/16306/14115\r\nf 15367/16073/13882 15366/16072/13881 15601/16307/14116 15599/16305/14114\r\nf 15587/16293/14102 15584/16290/14099 15599/16305/14114 15601/16307/14116\r\nf 15584/16290/14099 15583/16289/14098 15600/16306/14115 15599/16305/14114\r\nf 15591/16297/14106 15602/16308/14117 15603/16309/14118 15592/16298/14107\r\nf 15602/16308/14117 15466/16172/13981 15465/16171/13980 15603/16309/14118\r\nf 15464/16170/13979 15604/16310/14119 15603/16309/14118 15465/16171/13980\r\nf 15604/16310/14119 15593/16299/14108 15592/16298/14107 15603/16309/14118\r\nf 15366/16072/13881 15495/16201/14010 15605/16311/14120 15601/16307/14116\r\nf 15495/16201/14010 15494/16200/14009 15581/16287/14096 15605/16311/14120\r\nf 15557/16263/14072 15595/16301/14110 15605/16311/14120 15581/16287/14096\r\nf 15595/16301/14110 15587/16293/14102 15601/16307/14116 15605/16311/14120\r\nf 15464/16170/13979 15514/16220/14029 15606/16312/14121 15604/16310/14119\r\nf 15514/16220/14029 15368/16074/13883 15600/16306/14115 15606/16312/14121\r\nf 15583/16289/14098 15598/16304/14113 15606/16312/14121 15600/16306/14115\r\nf 15598/16304/14113 15593/16299/14108 15604/16310/14119 15606/16312/14121\r\nf 15607/16313/14122 15608/16314/14123 15609/16315/14124 15610/16316/14125\r\nf 15608/16314/14123 15579/16285/14094 15578/16284/14093 15609/16315/14124\r\nf 15577/16283/14092 15611/16317/14126 15609/16315/14124 15578/16284/14093\r\nf 15611/16317/14126 15500/16206/14015 15610/16316/14125 15609/16315/14124\r\nf 15500/16206/14015 15611/16317/14126 15612/16318/14127 15497/16203/14012\r\nf 15611/16317/14126 15577/16283/14092 15582/16288/14097 15612/16318/14127\r\nf 15494/16200/14009 15492/16198/14007 15612/16318/14127 15582/16288/14097\r\nf 15492/16198/14007 15351/16057/13866 15497/16203/14012 15612/16318/14127\r\nf 15429/16135/13944 15484/16190/13999 15613/16319/14128 15443/16149/13958\r\nf 15484/16190/13999 15466/16172/13981 15602/16308/14117 15613/16319/14128\r\nf 15591/16297/14106 15614/16320/14129 15613/16319/14128 15602/16308/14117\r\nf 15614/16320/14129 15408/16114/13923 15443/16149/13958 15613/16319/14128\r\nf 15408/16114/13923 15614/16320/14129 15615/16321/14130 15409/16115/13924\r\nf 15614/16320/14129 15591/16297/14106 15589/16295/14104 15615/16321/14130\r\nf 15544/16250/14059 15616/16322/14131 15615/16321/14130 15589/16295/14104\r\nf 15616/16322/14131 15402/16108/13917 15409/16115/13924 15615/16321/14130\r\nf 15402/16108/13917 15616/16322/14131 15617/16323/14132 15403/16109/13918\r\nf 15616/16322/14131 15544/16250/14059 15541/16247/14056 15617/16323/14132\r\nf 15540/16246/14055 15618/16324/14133 15617/16323/14132 15541/16247/14056\r\nf 15618/16324/14133 15396/16102/13911 15403/16109/13918 15617/16323/14132\r\nf 15387/16093/13902 15397/16103/13912 15619/16325/14134 15474/16180/13989\r\nf 15397/16103/13912 15396/16102/13911 15618/16324/14133 15619/16325/14134\r\nf 15540/16246/14055 15569/16275/14084 15619/16325/14134 15618/16324/14133\r\nf 15569/16275/14084 15450/16156/13965 15474/16180/13989 15619/16325/14134\r\nf 15327/16033/13842 15326/16032/13841 15620/16326/14135 15455/16161/13970\r\nf 15326/16032/13841 15325/16031/13840 15517/16223/14032 15620/16326/14135\r\nf 15338/16044/13853 15335/16041/13850 15620/16326/14135 15517/16223/14032\r\nf 15335/16041/13850 15334/16040/13849 15455/16161/13970 15620/16326/14135\r\nf 15358/16064/13873 15357/16063/13872 15621/16327/14136 15504/16210/14019\r\nf 15357/16063/13872 15356/16062/13871 15622/16328/14137 15621/16327/14136\r\nf 15378/16084/13893 15381/16087/13896 15621/16327/14136 15622/16328/14137\r\nf 15381/16087/13896 15386/16092/13901 15504/16210/14019 15621/16327/14136\r\nf 15377/16083/13892 15376/16082/13891 15623/16329/14138 15624/16330/14139\r\nf 15376/16082/13891 15375/16081/13890 15625/16331/14140 15623/16329/14138\r\nf 15626/16332/14141 15627/16333/14142 15623/16329/14138 15625/16331/14140\r\nf 15627/16333/14142 15628/16334/14143 15624/16330/14139 15623/16329/14138\r\nf 15420/16126/13935 15419/16125/13934 15629/16335/14144 15630/16336/14145\r\nf 15419/16125/13934 15418/16124/13933 15631/16337/14146 15629/16335/14144\r\nf 15436/16142/13951 15433/16139/13948 15629/16335/14144 15631/16337/14146\r\nf 15433/16139/13948 15432/16138/13947 15630/16336/14145 15629/16335/14144\r\nf 15327/16033/13842 15459/16165/13974 15632/16338/14147 15322/16028/13837\r\nf 15459/16165/13974 15458/16164/13973 15480/16186/13995 15632/16338/14147\r\nf 15479/16185/13994 15633/16339/14148 15632/16338/14147 15480/16186/13995\r\nf 15633/16339/14148 15319/16025/13834 15322/16028/13837 15632/16338/14147\r\nf 15418/16124/13933 15481/16187/13996 15634/16340/14149 15631/16337/14146\r\nf 15481/16187/13996 15458/16164/13973 15457/16163/13972 15634/16340/14149\r\nf 15456/16162/13971 15485/16191/14000 15634/16340/14149 15457/16163/13972\r\nf 15485/16191/14000 15436/16142/13951 15631/16337/14146 15634/16340/14149\r\nf 15358/16064/13873 15503/16209/14018 15635/16341/14150 15359/16065/13874\r\nf 15503/16209/14018 15377/16083/13892 15624/16330/14139 15635/16341/14150\r\nf 15628/16334/14143 15636/16342/14151 15635/16341/14150 15624/16330/14139\r\nf 15636/16342/14151 15360/16066/13875 15359/16065/13874 15635/16341/14150\r\nf 15375/16081/13890 15518/16224/14033 15637/16343/14152 15625/16331/14140\r\nf 15518/16224/14033 15325/16031/13840 15324/16030/13839 15637/16343/14152\r\nf 15323/16029/13838 15638/16344/14153 15637/16343/14152 15324/16030/13839\r\nf 15638/16344/14153 15626/16332/14141 15625/16331/14140 15637/16343/14152\r\nf 15342/16048/13857 15341/16047/13856 15639/16345/14154 15470/16176/13985\r\nf 15341/16047/13856 15340/16046/13855 15520/16226/14035 15639/16345/14154\r\nf 15361/16067/13876 15512/16218/14027 15639/16345/14154 15520/16226/14035\r\nf 15512/16218/14027 15460/16166/13975 15470/16176/13985 15639/16345/14154\r\nf 15349/16055/13864 15348/16054/13863 15640/16346/14155 15641/16347/14156\r\nf 15348/16054/13863 15347/16053/13862 15642/16348/14157 15640/16346/14155\r\nf 15373/16079/13888 15370/16076/13885 15640/16346/14155 15642/16348/14157\r\nf 15370/16076/13885 15369/16075/13884 15641/16347/14156 15640/16346/14155\r\nf 15384/16090/13899 15383/16089/13898 15643/16349/14158 15499/16205/14014\r\nf 15383/16089/13898 15382/16088/13897 15644/16350/14159 15643/16349/14158\r\nf 15607/16313/14122 15610/16316/14125 15643/16349/14158 15644/16350/14159\r\nf 15610/16316/14125 15500/16206/14015 15499/16205/14014 15643/16349/14158\r\nf 15440/16146/13955 15439/16145/13954 15645/16351/14160 15646/16352/14161\r\nf 15439/16145/13954 15438/16144/13953 15647/16353/14162 15645/16351/14160\r\nf 15427/16133/13942 15424/16130/13939 15645/16351/14160 15647/16353/14162\r\nf 15424/16130/13939 15423/16129/13938 15646/16352/14161 15645/16351/14160\r\nf 15342/16048/13857 15473/16179/13988 15648/16354/14163 15337/16043/13852\r\nf 15473/16179/13988 15472/16178/13987 15487/16193/14002 15648/16354/14163\r\nf 15456/16162/13971 15453/16159/13968 15648/16354/14163 15487/16193/14002\r\nf 15453/16159/13968 15334/16040/13849 15337/16043/13852 15648/16354/14163\r\nf 15438/16144/13953 15488/16194/14003 15649/16355/14164 15647/16353/14162\r\nf 15488/16194/14003 15472/16178/13987 15471/16177/13986 15649/16355/14164\r\nf 15468/16174/13983 15482/16188/13997 15649/16355/14164 15471/16177/13986\r\nf 15482/16188/13997 15427/16133/13942 15647/16353/14162 15649/16355/14164\r\nf 15384/16090/13899 15498/16204/14013 15650/16356/14165 15385/16091/13900\r\nf 15498/16204/14013 15349/16055/13864 15641/16347/14156 15650/16356/14165\r\nf 15369/16075/13884 15502/16208/14017 15650/16356/14165 15641/16347/14156\r\nf 15502/16208/14017 15386/16092/13901 15385/16091/13900 15650/16356/14165\r\nf 15347/16053/13862 15521/16227/14036 15651/16357/14166 15642/16348/14157\r\nf 15521/16227/14036 15340/16046/13855 15339/16045/13854 15651/16357/14166\r\nf 15338/16044/13853 15515/16221/14030 15651/16357/14166 15339/16045/13854\r\nf 15515/16221/14030 15373/16079/13888 15642/16348/14157 15651/16357/14166\r\nv 197.49 63.98 97.29\r\nv 196.53 55.04 97.87\r\nv 202.76 55.47 107.92\r\nv 203.65 64.18 107.31\r\nv 195.74 45.67 98.3\r\nv 202.03 46.42 108.37\r\nv 209.18 47.27 118.45\r\nv 209.83 55.97 117.97\r\nv 210.65 64.44 117.34\r\nv 216.63 94.25 109.23\r\nv 211.22 94.41 100.53\r\nv 208.62 87.54 102.99\r\nv 214.98 87.7 112.29\r\nv 206.44 94.66 91.72\r\nv 203.05 87.52 93.65\r\nv 200.67 80.12 95.27\r\nv 206.54 80.17 104.97\r\nv 213.24 80.34 114.66\r\nv 218.66 104.3 101.41\r\nv 217.94 106.65 94.57\r\nv 214.43 100.68 97.65\r\nv 217.77 99.58 105.49\r\nv 216.91 108.65 87.32\r\nv 211.31 101.69 89.57\r\nv 206.91 32.58 -37.28\r\nv 203.99 31.8 -32.58\r\nv 204.5 39.64 -30.67\r\nv 207.39 40.43 -35.15\r\nv 200.76 30.94 -27.38\r\nv 201.33 38.77 -25.74\r\nv 201.92 48.19 -23.89\r\nv 205.06 48.97 -28.61\r\nv 207.98 49.69 -32.97\r\nv 215.25 109.94 79.23\r\nv 208.62 102.49 80.99\r\nv 213.51 110.75 70.89\r\nv 206.46 103.12 72.32\r\nv 200.4 95.54 73.69\r\nv 202.91 95.09 82.68\r\nv 193.21 28.91 -14.48\r\nv 190.45 28.12 -7.8\r\nv 191.12 36.01 -6.88\r\nv 193.95 36.76 -13.52\r\nv 188.17 27.44 -0.99\r\nv 188.75 35.4 -0.12\r\nv 189.41 45.29 0.77\r\nv 191.86 45.78 -5.88\r\nv 194.77 46.41 -12.44\r\nv 187.57 63.96 14.13\r\nv 189.18 72.66 14.5\r\nv 190.53 72.55 8.27\r\nv 189.01 63.96 7.75\r\nv 191.35 80.75 15.07\r\nv 192.64 80.56 9.01\r\nv 194.38 80.4 3.35\r\nv 192.35 72.45 2.45\r\nv 190.91 63.98 1.8\r\nv 219.95 105.92 -13.37\r\nv 217.54 100.51 -17.24\r\nv 215.71 100.83 -13.64\r\nv 218.97 106.63 -10.15\r\nv 215.25 94.64 -20.91\r\nv 212.73 94.67 -16.96\r\nv 210.2 94.71 -12.96\r\nv 213.81 101.19 -9.82\r\nv 217.85 107.43 -6.48\r\nv 185.48 44.53 13.81\r\nv 184.23 44.31 21.69\r\nv 185.21 54.39 21.71\r\nv 186.4 54.5 13.92\r\nv 183.43 44.17 29.96\r\nv 184.43 54.33 29.92\r\nv 185.69 63.97 29.92\r\nv 186.45 63.97 21.81\r\nv 199.57 80.22 -8.63\r\nv 202.47 80.24 -13.52\r\nv 200.76 72.43 -14.98\r\nv 197.71 72.39 -9.87\r\nv 205.44 80.31 -17.95\r\nv 203.89 72.51 -19.64\r\nv 202.83 64.54 -21.03\r\nv 199.54 64.33 -16.14\r\nv 196.34 64.15 -10.78\r\nv 215.17 109.34 2.61\r\nv 209.84 102.11 -1.1\r\nv 208.24 102.45 3.34\r\nv 214.39 109.91 6.23\r\nv 205.19 94.88 -4.4\r\nv 202.92 95.05 0.74\r\nv 200.99 95.28 6.1\r\nv 206.91 102.78 7.86\r\nv 213.81 110.33 9.73\r\nv 191.52 17.63 100.45\r\nv 188.08 10.17 102.45\r\nv 194.95 11.17 113.82\r\nv 198.12 19.03 111.12\r\nv 183.66 0.78 105.03\r\nv 190.89 1.25 117.29\r\nv 198.82 1.55 129.96\r\nv 202.64 12.19 125.41\r\nv 205.63 20.58 121.9\r\nv 183.38 15.81 79.7\r\nv 179.03 8.42 80.98\r\nv 182.85 9.22 91.54\r\nv 186.76 16.58 89.97\r\nv 173.4 -0.89 82.6\r\nv 177.8 -0.04 93.57\r\nv 179.37 14.95 60.47\r\nv 174.73 7.55 61.2\r\nv 176.4 7.88 70.67\r\nv 180.96 15.28 69.65\r\nv 168.76 -1.77 61.87\r\nv 170.51 -1.44 71.8\r\nv 178.06 14.65 45.76\r\nv 173.43 7.24 45.71\r\nv 173.79 7.37 53.2\r\nv 178.46 14.75 52.81\r\nv 167.48 -2.1 45.6\r\nv 167.78 -1.93 53.54\r\nv 205.86 24.46 -42.21\r\nv 205.46 22.18 -45.22\r\nv 201.52 20.66 -40.39\r\nv 202.6 23.43 -37.21\r\nv 205.04 19.57 -48.83\r\nv 200.19 17.42 -44.25\r\nv 195.19 14.97 -38.61\r\nv 197.32 18.93 -34.72\r\nv 199.01 22.26 -31.55\r\nv 172.45 1.49 7.44\r\nv 170.25 -0.66 17.34\r\nv 175.35 8.16 19.65\r\nv 177.17 9.45 10.1\r\nv 168.67 -2.23 27.41\r\nv 174.12 7.25 29.22\r\nv 178.4 14.76 30.41\r\nv 179.34 15.17 21.3\r\nv 180.87 15.82 12.15\r\nv 185.02 9.01 -22.78\r\nv 181.38 6.98 -16.08\r\nv 184.97 13.3 -12.83\r\nv 188.19 14.76 -19.66\r\nv 178.32 5.31 -9.45\r\nv 182.31 12.09 -6.04\r\nv 185.44 17.56 -3.36\r\nv 187.8 18.43 -10.25\r\nv 190.7 19.45 -17.15\r\nv 167.76 -2.45 36.86\r\nv 173.51 7.08 37.81\r\nv 178.01 14.62 38.43\r\nv 208.03 31.41 119.61\r\nv 200.64 29.79 109.28\r\nv 200.24 26.16 109.51\r\nv 207.66 27.9 119.89\r\nv 194.19 28.37 99.02\r\nv 193.76 24.64 99.19\r\nv 193.43 22.92 99.34\r\nv 199.92 24.44 109.69\r\nv 207.34 26.18 120.11\r\nv 196.91 29.91 -21.18\r\nv 196.5 26.23 -21.84\r\nv 192.76 25.2 -14.97\r\nv 200.43 27.28 -28.26\r\nv 200.17 25.75 -28.89\r\nv 196.18 24.66 -22.32\r\nv 192.38 23.59 -15.33\r\nv 183.9 26.14 13.66\r\nv 183.47 22.29 13.44\r\nv 182.14 21.85 21.51\r\nv 182.58 25.73 21.68\r\nv 183.09 20.63 13.19\r\nv 181.75 20.19 21.41\r\nv 180.96 19.93 29.81\r\nv 181.37 21.59 29.79\r\nv 181.81 25.48 29.95\r\nv 185.87 26.74 6.13\r\nv 185.46 22.94 5.8\r\nv 187.76 23.68 -1.43\r\nv 187.38 22.04 -1.73\r\nv 185.08 21.29 5.5\r\nv 199.49 22.76 109.99\r\nv 193 21.23 99.58\r\nv 206.93 24.49 120.46\r\nv 203.2 25.25 -35.38\r\nv 206.21 26.1 -40.39\r\nv 199.83 24.3 -29.8\r\nv 203.5 26.65 -34.37\r\nv 206.49 27.47 -39.29\r\nv 181.15 18.58 21.61\r\nv 182.53 19.01 12.97\r\nv 180.32 18.32 30.31\r\nv 189.13 21.18 -9.03\r\nv 191.9 22.03 -15.88\r\nv 186.87 20.44 -2.15\r\nv 189.63 22.76 -8.57\r\nv 198.85 72.3 96.45\r\nv 204.9 72.4 106.37\r\nv 211.79 72.58 116.28\r\nv 188.11 72.75 22.04\r\nv 190.34 80.91 22.42\r\nv 187.38 72.81 30.01\r\nv 189.64 81.02 30.2\r\nv 194.21 88.39 15.87\r\nv 195.41 88.14 10.02\r\nv 198.48 95.9 16.82\r\nv 199.56 95.58 11.22\r\nv 197.04 87.91 4.58\r\nv 201.91 87.65 -6.86\r\nv 204.66 87.66 -11.51\r\nv 207.65 94.77 -8.87\r\nv 207.48 87.73 -15.7\r\nv 199.06 87.76 84.22\r\nv 196.29 88.09 74.92\r\nv 193.52 80.5 75.97\r\nv 196.45 80.26 85.54\r\nv 194.9 36.08 98.7\r\nv 201.28 37.2 108.86\r\nv 208.56 38.48 119.06\r\nv 183.31 33.98 21.72\r\nv 184.61 34.32 13.75\r\nv 182.52 33.78 29.99\r\nv 198.84 56.04 -17.26\r\nv 195.46 55.51 -11.58\r\nv 202.3 56.61 -22.39\r\nv 198.3 47.27 -18.47\r\nv 188 54.66 7.41\r\nv 190.03 54.87 1.33\r\nv 187.25 44.87 7.09\r\nv 211.42 111.65 55.64\r\nv 211.08 111.94 49.55\r\nv 203.5 104.1 50.02\r\nv 203.98 103.87 56.41\r\nv 210.98 112.08 43.89\r\nv 203.32 104.22 44.08\r\nv 196.73 96.44 44.26\r\nv 196.97 96.34 50.46\r\nv 197.57 96.16 57.13\r\nv 187.96 44.75 78.3\r\nv 188.82 54.58 77.92\r\nv 186.62 54.45 68.31\r\nv 185.72 44.47 68.53\r\nv 189.91 63.91 77.42\r\nv 187.76 63.91 67.89\r\nv 186.39 63.92 59.17\r\nv 185.2 54.36 59.5\r\nv 184.27 44.28 59.62\r\nv 185.62 21.04 78.85\r\nv 185.97 22.76 78.77\r\nv 183.72 22.21 68.21\r\nv 183.35 20.51 68.35\r\nv 186.4 26.61 78.71\r\nv 184.15 26.08 68.3\r\nv 182.69 25.72 58.98\r\nv 182.26 21.84 58.8\r\nv 181.88 20.16 58.95\r\nv 189.34 72.6 67.38\r\nv 188.02 72.69 58.78\r\nv 191.43 72.49 76.79\r\nv 191.51 80.69 66.74\r\nv 190.25 80.85 58.32\r\nv 192.54 88.72 50.85\r\nv 193.21 88.58 57.77\r\nv 192.27 88.8 44.4\r\nv 189.25 81.02 44.53\r\nv 189.53 80.96 51.18\r\nv 187.1 34.69 78.56\r\nv 184.85 34.26 68.47\r\nv 183.39 33.97 59.35\r\nv 212.23 111.29 62.89\r\nv 204.95 103.56 63.98\r\nv 198.67 95.9 65.02\r\nv 194.4 88.37 65.94\r\nv 192.01 54.76 87.82\r\nv 191.17 45.14 88.25\r\nv 193.03 63.91 87.28\r\nv 189.16 23.54 89.02\r\nv 188.82 21.81 89.12\r\nv 189.59 27.34 88.89\r\nv 194.48 72.36 86.53\r\nv 190.31 35.27 88.62\r\nv 210.9 112.02 37.95\r\nv 203.29 104.2 37.85\r\nv 211.04 111.84 31.71\r\nv 203.5 104.07 31.29\r\nv 197.02 96.36 30.89\r\nv 196.73 96.44 37.75\r\nv 192.29 88.83 37.63\r\nv 192.62 88.77 30.51\r\nv 189.28 81.05 37.55\r\nv 183.07 44.11 45.29\r\nv 183.43 44.16 52.21\r\nv 184.38 54.31 52.03\r\nv 184.04 54.29 45.04\r\nv 185.61 63.94 51.75\r\nv 185.29 63.95 44.82\r\nv 181.43 21.61 51.97\r\nv 181.03 19.93 52.01\r\nv 181.86 25.5 52.09\r\nv 181.5 25.4 45.78\r\nv 181.07 21.5 45.75\r\nv 180.67 19.82 45.66\r\nv 187.27 72.76 51.48\r\nv 186.97 72.8 44.66\r\nv 182.55 33.79 52.2\r\nv 182.19 33.7 45.58\r\nv 183.05 44.1 37.85\r\nv 184.05 54.29 37.68\r\nv 185.31 63.96 37.57\r\nv 181.03 21.48 38.11\r\nv 180.62 19.82 38.08\r\nv 181.46 25.38 38.19\r\nv 187 72.82 37.52\r\nv 182.17 33.69 38.06\r\nv 212.34 111.32 18.99\r\nv 204.88 103.57 17.88\r\nv 204.07 103.86 24.38\r\nv 211.59 111.63 25.15\r\nv 197.61 96.16 23.65\r\nv 193.27 88.61 22.97\r\nv 180 18.2 38.35\r\nv 180.07 18.19 45.71\r\nv 180.46 18.29 52.35\r\nv 181.33 18.51 59.62\r\nv 182.86 18.85 68.85\r\nv 185.17 19.37 79.07\r\nv 188.39 20.13 89.3\r\nv 197.58 37.75 -19.92\r\nv 211.77 101.65 -5.57\r\nv 216.45 108.43 -1.93\r\nv 208.45 80.37 -22.43\r\nv 207.03 72.59 -24.32\r\nv 211.48 80.44 -26.92\r\nv 210.18 72.67 -29\r\nv 209.22 64.91 -30.54\r\nv 206.05 64.73 -25.81\r\nv 189.88 11.92 -30.87\r\nv 192.59 16.8 -27.38\r\nv 194.74 20.83 -24.56\r\nv 203.71 28.16 -33.62\r\nv 206.66 28.95 -38.46\r\nv 195.77 23.15 -23.05\r\nv 210.33 87.79 -19.93\r\nv 213.19 87.86 -24.18\r\nv 205.49 57.1 -27.12\r\nv 208.54 57.55 -31.65\r\nv 186.5 34.81 6.61\r\nv 196.78 80.28 -2.77\r\nv 194.83 72.39 -3.82\r\nv 193.41 64.04 -4.58\r\nv 205.81 103.18 12.54\r\nv 213.1 110.85 13.92\r\nv 175.25 3.48 -1.55\r\nv 179.6 10.78 1.57\r\nv 183.02 16.66 4.02\r\nv 190.02 24.39 -8.26\r\nv 184.56 19.67 5.12\r\nv 199.29 87.74 -1.27\r\nv 192.52 55.14 -5.19\r\nvn -0.82 0.14 0.55\r\nvn -0.82 0.1 0.56\r\nvn -0.76 0.09 0.64\r\nvn -0.76 0.13 0.64\r\nvn -0.82 0.08 0.56\r\nvn -0.76 0.08 0.64\r\nvn -0.74 0.08 0.67\r\nvn -0.74 0.09 0.67\r\nvn -0.74 0.13 0.66\r\nvn -0.71 0.39 0.58\r\nvn -0.71 0.45 0.55\r\nvn -0.72 0.35 0.59\r\nvn -0.71 0.33 0.62\r\nvn -0.74 0.49 0.47\r\nvn -0.78 0.37 0.51\r\nvn -0.8 0.27 0.53\r\nvn -0.74 0.27 0.62\r\nvn -0.72 0.26 0.64\r\nvn -0.9 0.33 0.28\r\nvn -0.76 0.54 0.35\r\nvn -0.71 0.52 0.47\r\nvn -0.76 0.41 0.5\r\nvn -0.71 0.61 0.34\r\nvn -0.71 0.58 0.41\r\nvn -0.79 0.17 -0.59\r\nvn -0.79 0.16 -0.59\r\nvn -0.78 0.15 -0.61\r\nvn -0.78 0.16 -0.61\r\nvn -0.79 0.16 -0.6\r\nvn -0.78 0.14 -0.61\r\nvn -0.77 0.12 -0.63\r\nvn -0.77 0.13 -0.63\r\nvn -0.77 0.14 -0.63\r\nvn -0.71 0.64 0.3\r\nvn -0.72 0.61 0.33\r\nvn -0.72 0.65 0.24\r\nvn -0.74 0.62 0.26\r\nvn -0.8 0.53 0.28\r\nvn -0.77 0.52 0.37\r\nvn -0.85 0.13 -0.5\r\nvn -0.9 0.12 -0.42\r\nvn -0.9 0.1 -0.43\r\nvn -0.85 0.11 -0.52\r\nvn -0.92 0.11 -0.38\r\nvn -0.92 0.09 -0.39\r\nvn -0.91 0.08 -0.41\r\nvn -0.89 0.09 -0.45\r\nvn -0.84 0.1 -0.54\r\nvn -0.96 0.14 -0.23\r\nvn -0.95 0.21 -0.22\r\nvn -0.92 0.2 -0.32\r\nvn -0.94 0.13 -0.33\r\nvn -0.93 0.29 -0.22\r\nvn -0.9 0.29 -0.32\r\nvn -0.87 0.28 -0.4\r\nvn -0.89 0.2 -0.41\r\nvn -0.9 0.13 -0.41\r\nvn -0.72 0.53 -0.45\r\nvn -0.69 0.51 -0.52\r\nvn -0.68 0.53 -0.51\r\nvn -0.7 0.56 -0.45\r\nvn -0.69 0.44 -0.57\r\nvn -0.68 0.46 -0.57\r\nvn -0.68 0.47 -0.56\r\nvn -0.67 0.55 -0.5\r\nvn -0.68 0.58 -0.45\r\nvn -0.96 0.08 -0.26\r\nvn -0.98 0.08 -0.16\r\nvn -0.98 0.1 -0.16\r\nvn -0.96 0.1 -0.24\r\nvn -0.99 0.08 -0.09\r\nvn -0.99 0.1 -0.09\r\nvn -0.98 0.15 -0.09\r\nvn -0.98 0.15 -0.15\r\nvn -0.79 0.28 -0.54\r\nvn -0.75 0.28 -0.61\r\nvn -0.76 0.2 -0.62\r\nvn -0.81 0.2 -0.56\r\nvn -0.73 0.28 -0.63\r\nvn -0.74 0.2 -0.64\r\nvn -0.75 0.15 -0.64\r\nvn -0.77 0.15 -0.62\r\nvn -0.82 0.14 -0.56\r\nvn -0.7 0.61 -0.37\r\nvn -0.69 0.59 -0.43\r\nvn -0.71 0.6 -0.36\r\nvn -0.72 0.63 -0.3\r\nvn -0.72 0.5 -0.48\r\nvn -0.75 0.51 -0.42\r\nvn -0.77 0.52 -0.36\r\nvn -0.72 0.62 -0.31\r\nvn -0.71 0.65 -0.27\r\nvn -0.78 0.4 0.47\r\nvn -0.77 0.42 0.48\r\nvn -0.73 0.41 0.55\r\nvn -0.74 0.39 0.55\r\nvn -0.75 0.43 0.5\r\nvn -0.73 0.41 0.55\r\nvn -0.72 0.41 0.56\r\nvn -0.72 0.41 0.56\r\nvn -0.72 0.39 0.57\r\nvn -0.85 0.46 0.27\r\nvn -0.82 0.49 0.28\r\nvn -0.8 0.46 0.38\r\nvn -0.83 0.43 0.36\r\nvn -0.82 0.49 0.3\r\nvn -0.79 0.46 0.41\r\nvn -0.86 0.49 0.14\r\nvn -0.85 0.51 0.14\r\nvn -0.84 0.5 0.2\r\nvn -0.85 0.48 0.2\r\nvn -0.85 0.51 0.14\r\nvn -0.84 0.51 0.21\r\nvn -0.88 0.48 0.03\r\nvn -0.86 0.5 0.01\r\nvn -0.86 0.51 0.08\r\nvn -0.87 0.49 0.09\r\nvn -0.86 0.51 -0.02\r\nvn -0.86 0.51 0.07\r\nvn -0.72 0.53 -0.45\r\nvn -0.68 0.56 -0.47\r\nvn -0.68 0.6 -0.42\r\nvn -0.71 0.56 -0.43\r\nvn -0.64 0.59 -0.49\r\nvn -0.66 0.62 -0.43\r\nvn -0.71 0.6 -0.36\r\nvn -0.7 0.6 -0.38\r\nvn -0.71 0.57 -0.41\r\nvn -0.85 0.5 -0.13\r\nvn -0.87 0.48 -0.09\r\nvn -0.87 0.48 -0.1\r\nvn -0.85 0.5 -0.16\r\nvn -0.88 0.48 -0.08\r\nvn -0.88 0.47 -0.07\r\nvn -0.89 0.46 -0.07\r\nvn -0.88 0.46 -0.12\r\nvn -0.86 0.48 -0.18\r\nvn -0.78 0.53 -0.33\r\nvn -0.79 0.53 -0.3\r\nvn -0.8 0.52 -0.3\r\nvn -0.78 0.52 -0.35\r\nvn -0.81 0.53 -0.26\r\nvn -0.81 0.53 -0.26\r\nvn -0.83 0.5 -0.27\r\nvn -0.81 0.49 -0.31\r\nvn -0.78 0.5 -0.37\r\nvn -0.87 0.49 -0.08\r\nvn -0.87 0.49 -0.05\r\nvn -0.88 0.47 -0.02\r\nvn -0.74 0.1 0.66\r\nvn -0.76 0.1 0.64\r\nvn -0.76 0.14 0.63\r\nvn -0.74 0.14 0.65\r\nvn -0.83 0.1 0.56\r\nvn -0.83 0.14 0.55\r\nvn -0.82 0.21 0.53\r\nvn -0.76 0.22 0.61\r\nvn -0.74 0.22 0.63\r\nvn -0.8 0.14 -0.58\r\nvn -0.81 0.21 -0.55\r\nvn -0.85 0.2 -0.48\r\nvn -0.79 0.23 -0.57\r\nvn -0.78 0.34 -0.53\r\nvn -0.79 0.32 -0.52\r\nvn -0.84 0.3 -0.45\r\nvn -0.96 0.1 -0.25\r\nvn -0.95 0.17 -0.24\r\nvn -0.97 0.17 -0.15\r\nvn -0.98 0.09 -0.16\r\nvn -0.94 0.27 -0.23\r\nvn -0.95 0.27 -0.14\r\nvn -0.96 0.28 -0.08\r\nvn -0.98 0.16 -0.08\r\nvn -0.99 0.09 -0.08\r\nvn -0.94 0.1 -0.34\r\nvn -0.93 0.18 -0.32\r\nvn -0.92 0.18 -0.36\r\nvn -0.9 0.28 -0.33\r\nvn -0.91 0.28 -0.3\r\nvn -0.75 0.32 0.58\r\nvn -0.81 0.32 0.5\r\nvn -0.74 0.31 0.6\r\nvn -0.75 0.47 -0.47\r\nvn -0.75 0.47 -0.47\r\nvn -0.75 0.47 -0.47\r\nvn -0.77 0.35 -0.53\r\nvn -0.77 0.37 -0.52\r\nvn -0.92 0.38 -0.13\r\nvn -0.9 0.39 -0.21\r\nvn -0.92 0.39 -0.07\r\nvn -0.85 0.4 -0.34\r\nvn -0.81 0.41 -0.41\r\nvn -0.87 0.4 -0.3\r\nvn -0.88 0.29 -0.37\r\nvn -0.81 0.2 0.55\r\nvn -0.75 0.19 0.63\r\nvn -0.73 0.19 0.65\r\nvn -0.97 0.21 -0.15\r\nvn -0.95 0.29 -0.15\r\nvn -0.97 0.21 -0.09\r\nvn -0.95 0.29 -0.09\r\nvn -0.89 0.4 -0.21\r\nvn -0.87 0.4 -0.3\r\nvn -0.82 0.54 -0.2\r\nvn -0.8 0.54 -0.28\r\nvn -0.83 0.39 -0.39\r\nvn -0.76 0.38 -0.52\r\nvn -0.72 0.37 -0.58\r\nvn -0.69 0.49 -0.54\r\nvn -0.71 0.37 -0.61\r\nvn -0.83 0.39 0.4\r\nvn -0.87 0.4 0.3\r\nvn -0.9 0.29 0.31\r\nvn -0.86 0.28 0.42\r\nvn -0.82 0.09 0.56\r\nvn -0.76 0.08 0.64\r\nvn -0.74 0.08 0.67\r\nvn -0.98 0.08 -0.16\r\nvn -0.96 0.08 -0.26\r\nvn -0.99 0.08 -0.09\r\nvn -0.78 0.11 -0.62\r\nvn -0.83 0.11 -0.55\r\nvn -0.76 0.12 -0.64\r\nvn -0.78 0.11 -0.61\r\nvn -0.93 0.09 -0.34\r\nvn -0.91 0.08 -0.41\r\nvn -0.93 0.08 -0.36\r\nvn -0.74 0.66 0.12\r\nvn -0.74 0.67 0.07\r\nvn -0.76 0.64 0.08\r\nvn -0.76 0.64 0.13\r\nvn -0.74 0.67 0.02\r\nvn -0.77 0.64 0.02\r\nvn -0.84 0.55 0.03\r\nvn -0.83 0.55 0.09\r\nvn -0.83 0.54 0.15\r\nvn -0.94 0.08 0.33\r\nvn -0.94 0.1 0.33\r\nvn -0.97 0.1 0.24\r\nvn -0.97 0.08 0.24\r\nvn -0.93 0.14 0.33\r\nvn -0.96 0.14 0.24\r\nvn -0.98 0.14 0.17\r\nvn -0.98 0.1 0.17\r\nvn -0.98 0.08 0.17\r\nvn -0.93 0.22 0.3\r\nvn -0.94 0.14 0.3\r\nvn -0.96 0.15 0.22\r\nvn -0.94 0.25 0.21\r\nvn -0.94 0.09 0.31\r\nvn -0.97 0.09 0.23\r\nvn -0.98 0.08 0.17\r\nvn -0.97 0.15 0.17\r\nvn -0.95 0.27 0.16\r\nvn -0.95 0.21 0.23\r\nvn -0.96 0.21 0.16\r\nvn -0.92 0.21 0.32\r\nvn -0.93 0.29 0.23\r\nvn -0.94 0.29 0.16\r\nvn -0.91 0.41 0.09\r\nvn -0.9 0.41 0.15\r\nvn -0.91 0.41 0.03\r\nvn -0.96 0.29 0.03\r\nvn -0.95 0.29 0.09\r\nvn -0.94 0.08 0.32\r\nvn -0.97 0.07 0.24\r\nvn -0.98 0.07 0.17\r\nvn -0.73 0.66 0.18\r\nvn -0.75 0.63 0.19\r\nvn -0.82 0.54 0.21\r\nvn -0.89 0.4 0.22\r\nvn -0.89 0.1 0.44\r\nvn -0.89 0.08 0.44\r\nvn -0.89 0.14 0.43\r\nvn -0.9 0.14 0.42\r\nvn -0.89 0.21 0.41\r\nvn -0.9 0.1 0.43\r\nvn -0.88 0.2 0.43\r\nvn -0.9 0.09 0.44\r\nvn -0.74 0.67 -0.02\r\nvn -0.77 0.64 -0.02\r\nvn -0.74 0.67 -0.08\r\nvn -0.76 0.64 -0.08\r\nvn -0.83 0.55 -0.08\r\nvn -0.84 0.55 -0.03\r\nvn -0.91 0.41 -0.03\r\nvn -0.91 0.41 -0.09\r\nvn -0.96 0.29 -0.03\r\nvn -1 0.08 0.03\r\nvn -0.99 0.08 0.1\r\nvn -0.99 0.1 0.1\r\nvn -0.99 0.1 0.03\r\nvn -0.98 0.15 0.1\r\nvn -0.99 0.15 0.03\r\nvn -0.98 0.16 0.11\r\nvn -0.96 0.27 0.1\r\nvn -0.99 0.09 0.11\r\nvn -1 0.09 0.04\r\nvn -0.99 0.16 0.04\r\nvn -0.96 0.27 0.04\r\nvn -0.97 0.21 0.09\r\nvn -0.98 0.21 0.03\r\nvn -0.99 0.08 0.11\r\nvn -1 0.08 0.04\r\nvn -1 0.08 -0.03\r\nvn -0.99 0.1 -0.03\r\nvn -0.99 0.15 -0.03\r\nvn -0.99 0.16 -0.02\r\nvn -0.96 0.28 -0.02\r\nvn -1 0.09 -0.02\r\nvn -0.98 0.21 -0.03\r\nvn -1 0.08 -0.03\r\nvn -0.72 0.66 -0.19\r\nvn -0.75 0.64 -0.2\r\nvn -0.76 0.64 -0.13\r\nvn -0.73 0.67 -0.14\r\nvn -0.83 0.54 -0.14\r\nvn -0.9 0.4 -0.14\r\nvn -0.92 0.4 -0.01\r\nvn -0.92 0.39 0.04\r\nvn -0.91 0.4 0.09\r\nvn -0.9 0.4 0.15\r\nvn -0.9 0.38 0.2\r\nvn -0.89 0.35 0.28\r\nvn -0.86 0.33 0.38\r\nvn -0.8 0.13 -0.59\r\nvn -0.67 0.57 -0.48\r\nvn -0.68 0.6 -0.43\r\nvn -0.73 0.28 -0.63\r\nvn -0.74 0.2 -0.64\r\nvn -0.73 0.28 -0.63\r\nvn -0.74 0.21 -0.64\r\nvn -0.75 0.16 -0.64\r\nvn -0.75 0.15 -0.64\r\nvn -0.76 0.56 -0.33\r\nvn -0.74 0.56 -0.37\r\nvn -0.74 0.53 -0.41\r\nvn -0.79 0.24 -0.57\r\nvn -0.79 0.25 -0.57\r\nvn -0.77 0.44 -0.46\r\nvn -0.71 0.36 -0.61\r\nvn -0.71 0.36 -0.61\r\nvn -0.76 0.13 -0.64\r\nvn -0.76 0.13 -0.64\r\nvn -0.93 0.08 -0.35\r\nvn -0.84 0.28 -0.47\r\nvn -0.85 0.2 -0.48\r\nvn -0.87 0.14 -0.48\r\nvn -0.73 0.63 -0.26\r\nvn -0.71 0.66 -0.24\r\nvn -0.83 0.52 -0.2\r\nvn -0.83 0.52 -0.22\r\nvn -0.84 0.49 -0.24\r\nvn -0.9 0.19 -0.4\r\nvn -0.88 0.4 -0.27\r\nvn -0.8 0.38 -0.45\r\nvn -0.88 0.09 -0.47\r\nvt 0.8141 0.5654\r\nvt 0.8178 0.5163\r\nvt 0.8703 0.5314\r\nvt 0.8665 0.5791\r\nvt 0.8207 0.4652\r\nvt 0.8733 0.4821\r\nvt 0.9255 0.501\r\nvt 0.9224 0.5483\r\nvt 0.9184 0.5947\r\nvt 0.8699 0.7621\r\nvt 0.8244 0.7519\r\nvt 0.8396 0.7113\r\nvt 0.8879 0.725\r\nvt 0.778 0.7433\r\nvt 0.7907 0.6997\r\nvt 0.801 0.6564\r\nvt 0.8518 0.6688\r\nvt 0.902 0.6832\r\nvt 0.8251 0.8178\r\nvt 0.7874 0.8278\r\nvt 0.8066 0.7904\r\nvt 0.8484 0.7918\r\nvt 0.7476 0.8353\r\nvt 0.7633 0.7888\r\nvt 0.0587 0.3913\r\nvt 0.0865 0.3829\r\nvt 0.0969 0.4258\r\nvt 0.0703 0.4343\r\nvt 0.1173 0.3735\r\nvt 0.1262 0.4164\r\nvt 0.1362 0.4678\r\nvt 0.1081 0.4767\r\nvt 0.0821 0.4849\r\nvt 0.7034 0.8378\r\nvt 0.717 0.7868\r\nvt 0.658 0.8375\r\nvt 0.6699 0.7848\r\nvt 0.6809 0.7341\r\nvt 0.7296 0.7377\r\nvt 0.1935 0.3517\r\nvt 0.2322 0.3438\r\nvt 0.237 0.3871\r\nvt 0.1984 0.3949\r\nvt 0.2715 0.3374\r\nvt 0.276 0.3808\r\nvt 0.2806 0.4347\r\nvt 0.2422 0.4405\r\nvt 0.204 0.4478\r\nvt 0.3561 0.5331\r\nvt 0.3573 0.5822\r\nvt 0.3218 0.5829\r\nvt 0.3197 0.5345\r\nvt 0.3592 0.6291\r\nvt 0.3247 0.6293\r\nvt 0.2922 0.6305\r\nvt 0.2884 0.5845\r\nvt 0.2855 0.5369\r\nvt 0.1847 0.8083\r\nvt 0.1645 0.7746\r\nvt 0.1856 0.7737\r\nvt 0.2032 0.8109\r\nvt 0.1453 0.7387\r\nvt 0.1687 0.7351\r\nvt 0.1924 0.7316\r\nvt 0.2079 0.7729\r\nvt 0.2243 0.8138\r\nvt 0.3555 0.4261\r\nvt 0.4002 0.4241\r\nvt 0.3997 0.4794\r\nvt 0.3556 0.4807\r\nvt 0.4467 0.4234\r\nvt 0.4459 0.4791\r\nvt 0.4452 0.5326\r\nvt 0.3995 0.5325\r\nvt 0.2225 0.6366\r\nvt 0.1936 0.641\r\nvt 0.1865 0.5962\r\nvt 0.2167 0.5915\r\nvt 0.1673 0.6458\r\nvt 0.1587 0.6013\r\nvt 0.1516 0.5568\r\nvt 0.1807 0.5508\r\nvt 0.2124 0.5451\r\nvt 0.2765 0.8209\r\nvt 0.2587 0.7724\r\nvt 0.2844 0.7721\r\nvt 0.2971 0.8231\r\nvt 0.243 0.7251\r\nvt 0.2729 0.723\r\nvt 0.3038 0.7217\r\nvt 0.3103 0.7723\r\nvt 0.3169 0.825\r\nvt 0.835 0.3092\r\nvt 0.8481 0.2638\r\nvt 0.9076 0.2833\r\nvt 0.8908 0.3301\r\nvt 0.865 0.2066\r\nvt 0.9292 0.224\r\nvt 0.9954 0.2418\r\nvt 0.9679 0.3043\r\nvt 0.9467 0.3536\r\nvt 0.7239 0.2814\r\nvt 0.7335 0.2347\r\nvt 0.7902 0.2476\r\nvt 0.7793 0.2933\r\nvt 0.7457 0.1756\r\nvt 0.8044 0.1898\r\nvt 0.619 0.2664\r\nvt 0.6257 0.219\r\nvt 0.6775 0.2254\r\nvt 0.6693 0.2726\r\nvt 0.6328 0.1591\r\nvt 0.6871 0.1657\r\nvt 0.5377 0.2599\r\nvt 0.5401 0.2123\r\nvt 0.5816 0.215\r\nvt 0.5768 0.2624\r\nvt 0.5428 0.1522\r\nvt 0.5869 0.155\r\nvt 0.0318 0.3455\r\nvt 0.0153 0.3321\r\nvt 0.0444 0.3179\r\nvt 0.0615 0.3351\r\nvt -0.0046 0.3168\r\nvt 0.0237 0.2976\r\nvt 0.0579 0.2768\r\nvt 0.0784 0.3023\r\nvt 0.0951 0.3235\r\nvt 0.3273 0.1733\r\nvt 0.3837 0.1597\r\nvt 0.3938 0.216\r\nvt 0.3395 0.2244\r\nvt 0.4407 0.1504\r\nvt 0.4478 0.2107\r\nvt 0.452 0.2583\r\nvt 0.4007 0.2606\r\nvt 0.3488 0.2651\r\nvt 0.1518 0.23\r\nvt 0.1912 0.214\r\nvt 0.2073 0.2545\r\nvt 0.1675 0.2667\r\nvt 0.2299 0.2009\r\nvt 0.2467 0.2446\r\nvt 0.2599 0.2797\r\nvt 0.2201 0.2872\r\nvt 0.18 0.2965\r\nvt 0.4939 0.1493\r\nvt 0.496 0.2102\r\nvt 0.4969 0.2583\r\nvt 0.9325 0.4149\r\nvt 0.8791 0.3914\r\nvt 0.8806 0.3715\r\nvt 0.9343 0.3956\r\nvt 0.8256 0.3707\r\nvt 0.8268 0.3501\r\nvt 0.8278 0.3404\r\nvt 0.8818 0.3618\r\nvt 0.9357 0.386\r\nvt 0.1541 0.3624\r\nvt 0.1506 0.3419\r\nvt 0.191 0.3311\r\nvt 0.1126 0.3533\r\nvt 0.1093 0.3446\r\nvt 0.1481 0.3329\r\nvt 0.1892 0.3218\r\nvt 0.3555 0.3255\r\nvt 0.3546 0.3043\r\nvt 0.4003 0.301\r\nvt 0.401 0.3224\r\nvt 0.3534 0.2948\r\nvt 0.3999 0.2915\r\nvt 0.4472 0.2902\r\nvt 0.4469 0.2998\r\nvt 0.4475 0.3212\r\nvt 0.3125 0.3309\r\nvt 0.3109 0.3099\r\nvt 0.2693 0.3166\r\nvt 0.2678 0.3071\r\nvt 0.3094 0.3004\r\nvt 0.8837 0.3522\r\nvt 0.8294 0.3308\r\nvt 0.9379 0.3764\r\nvt 0.0714 0.3462\r\nvt 0.0418 0.3551\r\nvt 0.1044 0.3362\r\nvt 0.0769 0.3543\r\nvt 0.0478 0.3631\r\nvt 0.4014 0.2819\r\nvt 0.3525 0.2851\r\nvt 0.4503 0.2806\r\nvt 0.2262 0.3044\r\nvt 0.1864 0.3126\r\nvt 0.2658 0.2977\r\nvt 0.2284 0.3137\r\nvt 0.8086 0.6119\r\nvt 0.8605 0.6249\r\nvt 0.9119 0.6397\r\nvt 0.3999 0.5821\r\nvt 0.4008 0.6295\r\nvt 0.4447 0.5825\r\nvt 0.4445 0.6302\r\nvt 0.3621 0.6749\r\nvt 0.3288 0.6747\r\nvt 0.365 0.7225\r\nvt 0.3332 0.7217\r\nvt 0.2976 0.6754\r\nvt 0.2311 0.6805\r\nvt 0.2036 0.6846\r\nvt 0.2167 0.7281\r\nvt 0.1787 0.6892\r\nvt 0.7404 0.6922\r\nvt 0.69 0.6875\r\nvt 0.6974 0.6424\r\nvt 0.7491 0.648\r\nvt 0.8234 0.4129\r\nvt 0.8764 0.4319\r\nvt 0.9292 0.4533\r\nvt 0.4008 0.3676\r\nvt 0.3557 0.3703\r\nvt 0.4473 0.3666\r\nvt 0.1749 0.5053\r\nvt 0.2084 0.4975\r\nvt 0.1443 0.5135\r\nvt 0.1684 0.4575\r\nvt 0.3184 0.4832\r\nvt 0.2834 0.4868\r\nvt 0.3171 0.4298\r\nvt 0.5741 0.8359\r\nvt 0.5403 0.8358\r\nvt 0.5472 0.7808\r\nvt 0.5825 0.7816\r\nvt 0.5089 0.8353\r\nvt 0.5142 0.7801\r\nvt 0.5189 0.7271\r\nvt 0.5534 0.7281\r\nvt 0.5902 0.7294\r\nvt 0.7136 0.4431\r\nvt 0.711 0.4967\r\nvt 0.6586 0.4904\r\nvt 0.6604 0.4359\r\nvt 0.7076 0.5481\r\nvt 0.6556 0.5427\r\nvt 0.6078 0.5388\r\nvt 0.6103 0.4859\r\nvt 0.6115 0.4307\r\nvt 0.7179 0.3131\r\nvt 0.7173 0.3228\r\nvt 0.6597 0.314\r\nvt 0.6607 0.3044\r\nvt 0.7167 0.344\r\nvt 0.66 0.3353\r\nvt 0.6088 0.3292\r\nvt 0.608 0.3078\r\nvt 0.6091 0.2982\r\nvt 0.6519 0.5916\r\nvt 0.6047 0.5882\r\nvt 0.7032 0.5963\r\nvt 0.6471 0.6383\r\nvt 0.6009 0.6354\r\nvt 0.558 0.6799\r\nvt 0.5962 0.6816\r\nvt 0.5222 0.6787\r\nvt 0.5246 0.6321\r\nvt 0.5615 0.6334\r\nvt 0.7155 0.3881\r\nvt 0.6605 0.3801\r\nvt 0.6105 0.3743\r\nvt 0.6141 0.8367\r\nvt 0.6242 0.783\r\nvt 0.6335 0.7314\r\nvt 0.6411 0.684\r\nvt 0.7644 0.5051\r\nvt 0.7672 0.4525\r\nvt 0.7607 0.5554\r\nvt 0.7727 0.3344\r\nvt 0.7734 0.3246\r\nvt 0.7717 0.3553\r\nvt 0.7558 0.6028\r\nvt 0.7698 0.3987\r\nvt 0.4758 0.8338\r\nvt 0.4795 0.7788\r\nvt 0.4409 0.832\r\nvt 0.4428 0.7773\r\nvt 0.4442 0.7248\r\nvt 0.4826 0.726\r\nvt 0.4845 0.6777\r\nvt 0.4446 0.6767\r\nvt 0.4857 0.631\r\nvt 0.5323 0.4251\r\nvt 0.5707 0.4273\r\nvt 0.5691 0.4829\r\nvt 0.5304 0.4809\r\nvt 0.5669 0.5361\r\nvt 0.5285 0.5344\r\nvt 0.5705 0.3039\r\nvt 0.5709 0.2943\r\nvt 0.5709 0.3253\r\nvt 0.5359 0.323\r\nvt 0.536 0.3016\r\nvt 0.5357 0.292\r\nvt 0.5644 0.5859\r\nvt 0.5266 0.5844\r\nvt 0.5711 0.3706\r\nvt 0.5344 0.3684\r\nvt 0.4909 0.4238\r\nvt 0.4894 0.4796\r\nvt 0.488 0.5332\r\nvt 0.4935 0.3001\r\nvt 0.4935 0.2905\r\nvt 0.4936 0.3216\r\nvt 0.4868 0.5833\r\nvt 0.4925 0.3669\r\nvt 0.3693 0.8293\r\nvt 0.3673 0.7747\r\nvt 0.404 0.776\r\nvt 0.4041 0.8307\r\nvt 0.4035 0.7236\r\nvt 0.4022 0.6757\r\nvt 0.4954 0.2808\r\nvt 0.5363 0.2823\r\nvt 0.5731 0.2846\r\nvt 0.6131 0.2886\r\nvt 0.6638 0.2947\r\nvt 0.7194 0.3034\r\nvt 0.7746 0.315\r\nvt 0.1607 0.4054\r\nvt 0.2328 0.7726\r\nvt 0.2504 0.8174\r\nvt 0.1407 0.6506\r\nvt 0.1309 0.6064\r\nvt 0.1139 0.6555\r\nvt 0.103 0.6116\r\nvt 0.095 0.5684\r\nvt 0.1231 0.5627\r\nvt 0.104 0.2526\r\nvt 0.1219 0.284\r\nvt 0.1365 0.3097\r\nvt 0.0809 0.3628\r\nvt 0.0523 0.3714\r\nvt 0.1443 0.3241\r\nvt 0.1535 0.6938\r\nvt 0.1282 0.6985\r\nvt 0.1162 0.5209\r\nvt 0.0892 0.5279\r\nvt 0.3148 0.375\r\nvt 0.2568 0.633\r\nvt 0.252 0.5875\r\nvt 0.2485 0.5405\r\nvt 0.337 0.7733\r\nvt 0.3406 0.8272\r\nvt 0.2756 0.1872\r\nvt 0.2906 0.2342\r\nvt 0.3023 0.2719\r\nvt 0.2299 0.3231\r\nvt 0.3076 0.2908\r\nvt 0.2637 0.6774\r\nvt 0.2457 0.4915\r\no side_strip_bot_L\r\nusemtl side_strip_bot_L\r\nf 15652/16358/14167 15653/16359/14168 15654/16360/14169 15655/16361/14170\r\nf 15653/16359/14168 15656/16362/14171 15657/16363/14172 15654/16360/14169\r\nf 15658/16364/14173 15659/16365/14174 15654/16360/14169 15657/16363/14172\r\nf 15659/16365/14174 15660/16366/14175 15655/16361/14170 15654/16360/14169\r\nf 15661/16367/14176 15662/16368/14177 15663/16369/14178 15664/16370/14179\r\nf 15662/16368/14177 15665/16371/14180 15666/16372/14181 15663/16369/14178\r\nf 15667/16373/14182 15668/16374/14183 15663/16369/14178 15666/16372/14181\r\nf 15668/16374/14183 15669/16375/14184 15664/16370/14179 15663/16369/14178\r\nf 15670/16376/14185 15671/16377/14186 15672/16378/14187 15673/16379/14188\r\nf 15671/16377/14186 15674/16380/14189 15675/16381/14190 15672/16378/14187\r\nf 15665/16371/14180 15662/16368/14177 15672/16378/14187 15675/16381/14190\r\nf 15662/16368/14177 15661/16367/14176 15673/16379/14188 15672/16378/14187\r\nf 15676/16382/14191 15677/16383/14192 15678/16384/14193 15679/16385/14194\r\nf 15677/16383/14192 15680/16386/14195 15681/16387/14196 15678/16384/14193\r\nf 15682/16388/14197 15683/16389/14198 15678/16384/14193 15681/16387/14196\r\nf 15683/16389/14198 15684/16390/14199 15679/16385/14194 15678/16384/14193\r\nf 15674/16380/14189 15685/16391/14200 15686/16392/14201 15675/16381/14190\r\nf 15685/16391/14200 15687/16393/14202 15688/16394/14203 15686/16392/14201\r\nf 15689/16395/14204 15690/16396/14205 15686/16392/14201 15688/16394/14203\r\nf 15690/16396/14205 15665/16371/14180 15675/16381/14190 15686/16392/14201\r\nf 15691/16397/14206 15692/16398/14207 15693/16399/14208 15694/16400/14209\r\nf 15692/16398/14207 15695/16401/14210 15696/16402/14211 15693/16399/14208\r\nf 15697/16403/14212 15698/16404/14213 15693/16399/14208 15696/16402/14211\r\nf 15698/16404/14213 15699/16405/14214 15694/16400/14209 15693/16399/14208\r\nf 15700/16406/14215 15701/16407/14216 15702/16408/14217 15703/16409/14218\r\nf 15701/16407/14216 15704/16410/14219 15705/16411/14220 15702/16408/14217\r\nf 15706/16412/14221 15707/16413/14222 15702/16408/14217 15705/16411/14220\r\nf 15707/16413/14222 15708/16414/14223 15703/16409/14218 15702/16408/14217\r\nf 15709/16415/14224 15710/16416/14225 15711/16417/14226 15712/16418/14227\r\nf 15710/16416/14225 15713/16419/14228 15714/16420/14229 15711/16417/14226\r\nf 15715/16421/14230 15716/16422/14231 15711/16417/14226 15714/16420/14229\r\nf 15716/16422/14231 15717/16423/14232 15712/16418/14227 15711/16417/14226\r\nf 15718/16424/14233 15719/16425/14234 15720/16426/14235 15721/16427/14236\r\nf 15719/16425/14234 15722/16428/14237 15723/16429/14238 15720/16426/14235\r\nf 15724/16430/14239 15725/16431/14240 15720/16426/14235 15723/16429/14238\r\nf 15725/16431/14240 15700/16406/14215 15721/16427/14236 15720/16426/14235\r\nf 15726/16432/14241 15727/16433/14242 15728/16434/14243 15729/16435/14244\r\nf 15727/16433/14242 15730/16436/14245 15731/16437/14246 15728/16434/14243\r\nf 15732/16438/14247 15733/16439/14248 15728/16434/14243 15731/16437/14246\r\nf 15733/16439/14248 15734/16440/14249 15729/16435/14244 15728/16434/14243\r\nf 15735/16441/14250 15736/16442/14251 15737/16443/14252 15738/16444/14253\r\nf 15736/16442/14251 15739/16445/14254 15740/16446/14255 15737/16443/14252\r\nf 15741/16447/14256 15742/16448/14257 15737/16443/14252 15740/16446/14255\r\nf 15742/16448/14257 15743/16449/14258 15738/16444/14253 15737/16443/14252\r\nf 15744/16450/14259 15745/16451/14260 15746/16452/14261 15747/16453/14262\r\nf 15745/16451/14260 15748/16454/14263 15749/16455/14264 15746/16452/14261\r\nf 15750/16456/14265 15751/16457/14266 15746/16452/14261 15749/16455/14264\r\nf 15751/16457/14266 15752/16458/14267 15747/16453/14262 15746/16452/14261\r\nf 15753/16459/14268 15754/16460/14269 15755/16461/14270 15756/16462/14271\r\nf 15754/16460/14269 15757/16463/14272 15758/16464/14273 15755/16461/14270\r\nf 15748/16454/14263 15745/16451/14260 15755/16461/14270 15758/16464/14273\r\nf 15745/16451/14260 15744/16450/14259 15756/16462/14271 15755/16461/14270\r\nf 15759/16465/14274 15760/16466/14275 15761/16467/14276 15762/16468/14277\r\nf 15760/16466/14275 15763/16469/14278 15764/16470/14279 15761/16467/14276\r\nf 15757/16463/14272 15754/16460/14269 15761/16467/14276 15764/16470/14279\r\nf 15754/16460/14269 15753/16459/14268 15762/16468/14277 15761/16467/14276\r\nf 15765/16471/14280 15766/16472/14281 15767/16473/14282 15768/16474/14283\r\nf 15766/16472/14281 15769/16475/14284 15770/16476/14285 15767/16473/14282\r\nf 15763/16469/14278 15760/16466/14275 15767/16473/14282 15770/16476/14285\r\nf 15760/16466/14275 15759/16465/14274 15768/16474/14283 15767/16473/14282\r\nf 15771/16477/14286 15772/16478/14287 15773/16479/14288 15774/16480/14289\r\nf 15772/16478/14287 15775/16481/14290 15776/16482/14291 15773/16479/14288\r\nf 15777/16483/14292 15778/16484/14293 15773/16479/14288 15776/16482/14291\r\nf 15778/16484/14293 15779/16485/14294 15774/16480/14289 15773/16479/14288\r\nf 15780/16486/14295 15781/16487/14296 15782/16488/14297 15783/16489/14298\r\nf 15781/16487/14296 15784/16490/14299 15785/16491/14300 15782/16488/14297\r\nf 15786/16492/14301 15787/16493/14302 15782/16488/14297 15785/16491/14300\r\nf 15787/16493/14302 15788/16494/14303 15783/16489/14298 15782/16488/14297\r\nf 15789/16495/14304 15790/16496/14305 15791/16497/14306 15792/16498/14307\r\nf 15790/16496/14305 15793/16499/14308 15794/16500/14309 15791/16497/14306\r\nf 15795/16501/14310 15796/16502/14311 15791/16497/14306 15794/16500/14309\r\nf 15796/16502/14311 15797/16503/14312 15792/16498/14307 15791/16497/14306\r\nf 15784/16490/14299 15798/16504/14313 15799/16505/14314 15785/16491/14300\r\nf 15798/16504/14313 15769/16475/14284 15766/16472/14281 15799/16505/14314\r\nf 15765/16471/14280 15800/16506/14315 15799/16505/14314 15766/16472/14281\r\nf 15800/16506/14315 15786/16492/14301 15785/16491/14300 15799/16505/14314\r\nf 15801/16507/14316 15802/16508/14317 15803/16509/14318 15804/16510/14319\r\nf 15802/16508/14317 15805/16511/14320 15806/16512/14321 15803/16509/14318\r\nf 15807/16513/14322 15808/16514/14323 15803/16509/14318 15806/16512/14321\r\nf 15808/16514/14323 15809/16515/14324 15804/16510/14319 15803/16509/14318\r\nf 15691/16397/14206 15810/16516/14325 15811/16517/14326 15812/16518/14327\r\nf 15810/16516/14325 15680/16386/14195 15813/16519/14328 15811/16517/14326\r\nf 15814/16520/14329 15815/16521/14330 15811/16517/14326 15813/16519/14328\r\nf 15815/16521/14330 15816/16522/14331 15812/16518/14327 15811/16517/14326\r\nf 15817/16523/14332 15818/16524/14333 15819/16525/14334 15820/16526/14335\r\nf 15818/16524/14333 15821/16527/14336 15822/16528/14337 15819/16525/14334\r\nf 15823/16529/14338 15824/16530/14339 15819/16525/14334 15822/16528/14337\r\nf 15824/16530/14339 15825/16531/14340 15820/16526/14335 15819/16525/14334\r\nf 15817/16523/14332 15826/16532/14341 15827/16533/14342 15818/16524/14333\r\nf 15826/16532/14341 15695/16401/14210 15828/16534/14343 15827/16533/14342\r\nf 15829/16535/14344 15830/16536/14345 15827/16533/14342 15828/16534/14343\r\nf 15830/16536/14345 15821/16527/14336 15818/16524/14333 15827/16533/14342\r\nf 15744/16450/14259 15747/16453/14262 15831/16537/14346 15832/16538/14347\r\nf 15747/16453/14262 15752/16458/14267 15833/16539/14348 15831/16537/14346\r\nf 15809/16515/14324 15808/16514/14323 15831/16537/14346 15833/16539/14348\r\nf 15808/16514/14323 15807/16513/14322 15832/16538/14347 15831/16537/14346\r\nf 15771/16477/14286 15774/16480/14289 15834/16540/14349 15835/16541/14350\r\nf 15774/16480/14289 15779/16485/14294 15836/16542/14351 15834/16540/14349\r\nf 15814/16520/14329 15837/16543/14352 15834/16540/14349 15836/16542/14351\r\nf 15837/16543/14352 15838/16544/14353 15835/16541/14350 15834/16540/14349\r\nf 15788/16494/14303 15787/16493/14302 15839/16545/14354 15840/16546/14355\r\nf 15787/16493/14302 15786/16492/14301 15841/16547/14356 15839/16545/14354\r\nf 15823/16529/14338 15822/16528/14337 15839/16545/14354 15841/16547/14356\r\nf 15822/16528/14337 15821/16527/14336 15840/16546/14355 15839/16545/14354\r\nf 15797/16503/14312 15796/16502/14311 15842/16548/14357 15843/16549/14358\r\nf 15796/16502/14311 15795/16501/14310 15844/16550/14359 15842/16548/14357\r\nf 15829/16535/14344 15845/16551/14360 15842/16548/14357 15844/16550/14359\r\nf 15845/16551/14360 15816/16522/14331 15843/16549/14358 15842/16548/14357\r\nf 15667/16373/14182 15846/16552/14361 15847/16553/14362 15668/16374/14183\r\nf 15846/16552/14361 15652/16358/14167 15655/16361/14170 15847/16553/14362\r\nf 15660/16366/14175 15848/16554/14363 15847/16553/14362 15655/16361/14170\r\nf 15848/16554/14363 15669/16375/14184 15668/16374/14183 15847/16553/14362\r\nf 15704/16410/14219 15701/16407/14216 15849/16555/14364 15850/16556/14365\r\nf 15701/16407/14216 15700/16406/14215 15725/16431/14240 15849/16555/14364\r\nf 15724/16430/14239 15851/16557/14366 15849/16555/14364 15725/16431/14240\r\nf 15851/16557/14366 15852/16558/14367 15850/16556/14365 15849/16555/14364\r\nf 15704/16410/14219 15853/16559/14368 15854/16560/14369 15705/16411/14220\r\nf 15853/16559/14368 15855/16561/14370 15856/16562/14371 15854/16560/14369\r\nf 15741/16447/14256 15857/16563/14372 15854/16560/14369 15856/16562/14371\r\nf 15857/16563/14372 15706/16412/14221 15705/16411/14220 15854/16560/14369\r\nf 15726/16432/14241 15858/16564/14373 15859/16565/14374 15727/16433/14242\r\nf 15858/16564/14373 15739/16445/14254 15860/16566/14375 15859/16565/14374\r\nf 15715/16421/14230 15861/16567/14376 15859/16565/14374 15860/16566/14375\r\nf 15861/16567/14376 15730/16436/14245 15727/16433/14242 15859/16565/14374\r\nf 15665/16371/14180 15690/16396/14205 15862/16568/14377 15666/16372/14181\r\nf 15690/16396/14205 15689/16395/14204 15863/16569/14378 15862/16568/14377\r\nf 15864/16570/14379 15865/16571/14380 15862/16568/14377 15863/16569/14378\r\nf 15865/16571/14380 15667/16373/14182 15666/16372/14181 15862/16568/14377\r\nf 15656/16362/14171 15866/16572/14381 15867/16573/14382 15657/16363/14172\r\nf 15866/16572/14381 15805/16511/14320 15802/16508/14317 15867/16573/14382\r\nf 15801/16507/14316 15868/16574/14383 15867/16573/14382 15802/16508/14317\r\nf 15868/16574/14383 15658/16364/14173 15657/16363/14172 15867/16573/14382\r\nf 15817/16523/14332 15820/16526/14335 15869/16575/14384 15870/16576/14385\r\nf 15820/16526/14335 15825/16531/14340 15871/16577/14386 15869/16575/14384\r\nf 15722/16428/14237 15719/16425/14234 15869/16575/14384 15871/16577/14386\r\nf 15719/16425/14234 15718/16424/14233 15870/16576/14385 15869/16575/14384\r\nf 15734/16440/14249 15733/16439/14248 15872/16578/14387 15873/16579/14388\r\nf 15733/16439/14248 15732/16438/14247 15874/16580/14389 15872/16578/14387\r\nf 15682/16388/14197 15875/16581/14390 15872/16578/14387 15874/16580/14389\r\nf 15875/16581/14390 15699/16405/14214 15873/16579/14388 15872/16578/14387\r\nf 15700/16406/14215 15703/16409/14218 15876/16582/14391 15721/16427/14236\r\nf 15703/16409/14218 15708/16414/14223 15877/16583/14392 15876/16582/14391\r\nf 15697/16403/14212 15878/16584/14393 15876/16582/14391 15877/16583/14392\r\nf 15878/16584/14393 15718/16424/14233 15721/16427/14236 15876/16582/14391\r\nf 15879/16585/14394 15880/16586/14395 15881/16587/14396 15882/16588/14397\r\nf 15880/16586/14395 15883/16589/14398 15884/16590/14399 15881/16587/14396\r\nf 15885/16591/14400 15886/16592/14401 15881/16587/14396 15884/16590/14399\r\nf 15886/16592/14401 15887/16593/14402 15882/16588/14397 15881/16587/14396\r\nf 15888/16594/14403 15889/16595/14404 15890/16596/14405 15891/16597/14406\r\nf 15889/16595/14404 15892/16598/14407 15893/16599/14408 15890/16596/14405\r\nf 15894/16600/14409 15895/16601/14410 15890/16596/14405 15893/16599/14408\r\nf 15895/16601/14410 15896/16602/14411 15891/16597/14406 15890/16596/14405\r\nf 15897/16603/14412 15898/16604/14413 15899/16605/14414 15900/16606/14415\r\nf 15898/16604/14413 15901/16607/14416 15902/16608/14417 15899/16605/14414\r\nf 15903/16609/14418 15904/16610/14419 15899/16605/14414 15902/16608/14417\r\nf 15904/16610/14419 15905/16611/14420 15900/16606/14415 15899/16605/14414\r\nf 15894/16600/14409 15893/16599/14408 15906/16612/14421 15907/16613/14422\r\nf 15893/16599/14408 15892/16598/14407 15908/16614/14423 15906/16612/14421\r\nf 15864/16570/14379 15909/16615/14424 15906/16612/14421 15908/16614/14423\r\nf 15909/16615/14424 15910/16616/14425 15907/16613/14422 15906/16612/14421\r\nf 15887/16593/14402 15886/16592/14401 15911/16617/14426 15912/16618/14427\r\nf 15886/16592/14401 15885/16591/14400 15913/16619/14428 15911/16617/14426\r\nf 15914/16620/14429 15915/16621/14430 15911/16617/14426 15913/16619/14428\r\nf 15915/16621/14430 15910/16616/14425 15912/16618/14427 15911/16617/14426\r\nf 15901/16607/14416 15916/16622/14431 15917/16623/14432 15902/16608/14417\r\nf 15916/16622/14431 15888/16594/14403 15891/16597/14406 15917/16623/14432\r\nf 15896/16602/14411 15918/16624/14433 15917/16623/14432 15891/16597/14406\r\nf 15918/16624/14433 15903/16609/14418 15902/16608/14417 15917/16623/14432\r\nf 15687/16393/14202 15919/16625/14434 15920/16626/14435 15688/16394/14203\r\nf 15919/16625/14434 15879/16585/14394 15882/16588/14397 15920/16626/14435\r\nf 15887/16593/14402 15921/16627/14436 15920/16626/14435 15882/16588/14397\r\nf 15921/16627/14436 15689/16395/14204 15688/16394/14203 15920/16626/14435\r\nf 15689/16395/14204 15921/16627/14436 15922/16628/14437 15863/16569/14378\r\nf 15921/16627/14436 15887/16593/14402 15912/16618/14427 15922/16628/14437\r\nf 15910/16616/14425 15909/16615/14424 15922/16628/14437 15912/16618/14427\r\nf 15909/16615/14424 15864/16570/14379 15863/16569/14378 15922/16628/14437\r\nf 15656/16362/14171 15653/16359/14168 15923/16629/14438 15924/16630/14439\r\nf 15653/16359/14168 15652/16358/14167 15925/16631/14440 15923/16629/14438\r\nf 15892/16598/14407 15889/16595/14404 15923/16629/14438 15925/16631/14440\r\nf 15889/16595/14404 15888/16594/14403 15924/16630/14439 15923/16629/14438\r\nf 15807/16513/14322 15806/16512/14321 15926/16632/14441 15927/16633/14442\r\nf 15806/16512/14321 15805/16511/14320 15928/16634/14443 15926/16632/14441\r\nf 15901/16607/14416 15898/16604/14413 15926/16632/14441 15928/16634/14443\r\nf 15898/16604/14413 15897/16603/14412 15927/16633/14442 15926/16632/14441\r\nf 15892/16598/14407 15925/16631/14440 15929/16635/14444 15908/16614/14423\r\nf 15925/16631/14440 15652/16358/14167 15846/16552/14361 15929/16635/14444\r\nf 15667/16373/14182 15865/16571/14380 15929/16635/14444 15846/16552/14361\r\nf 15865/16571/14380 15864/16570/14379 15908/16614/14423 15929/16635/14444\r\nf 15805/16511/14320 15866/16572/14381 15930/16636/14445 15928/16634/14443\r\nf 15866/16572/14381 15656/16362/14171 15924/16630/14439 15930/16636/14445\r\nf 15888/16594/14403 15916/16622/14431 15930/16636/14445 15924/16630/14439\r\nf 15916/16622/14431 15901/16607/14416 15928/16634/14443 15930/16636/14445\r\nf 15883/16589/14398 15931/16637/14446 15932/16638/14447 15884/16590/14399\r\nf 15931/16637/14446 15933/16639/14448 15934/16640/14449 15932/16638/14447\r\nf 15935/16641/14450 15936/16642/14451 15932/16638/14447 15934/16640/14449\r\nf 15936/16642/14451 15885/16591/14400 15884/16590/14399 15932/16638/14447\r\nf 15885/16591/14400 15936/16642/14451 15937/16643/14452 15913/16619/14428\r\nf 15936/16642/14451 15935/16641/14450 15938/16644/14453 15937/16643/14452\r\nf 15852/16558/14367 15939/16645/14454 15937/16643/14452 15938/16644/14453\r\nf 15939/16645/14454 15914/16620/14429 15913/16619/14428 15937/16643/14452\r\nf 15940/16646/14455 15941/16647/14456 15942/16648/14457 15943/16649/14458\r\nf 15941/16647/14456 15896/16602/14411 15895/16601/14410 15942/16648/14457\r\nf 15894/16600/14409 15944/16650/14459 15942/16648/14457 15895/16601/14410\r\nf 15944/16650/14459 15945/16651/14460 15943/16649/14458 15942/16648/14457\r\nf 15905/16611/14420 15904/16610/14419 15946/16652/14461 15947/16653/14462\r\nf 15904/16610/14419 15903/16609/14418 15948/16654/14463 15946/16652/14461\r\nf 15949/16655/14464 15950/16656/14465 15946/16652/14461 15948/16654/14463\r\nf 15950/16656/14465 15951/16657/14466 15947/16653/14462 15946/16652/14461\r\nf 15945/16651/14460 15944/16650/14459 15952/16658/14467 15953/16659/14468\r\nf 15944/16650/14459 15894/16600/14409 15907/16613/14422 15952/16658/14467\r\nf 15910/16616/14425 15915/16621/14430 15952/16658/14467 15907/16613/14422\r\nf 15915/16621/14430 15914/16620/14429 15953/16659/14468 15952/16658/14467\r\nf 15903/16609/14418 15918/16624/14433 15954/16660/14469 15948/16654/14463\r\nf 15918/16624/14433 15896/16602/14411 15941/16647/14456 15954/16660/14469\r\nf 15940/16646/14455 15955/16661/14470 15954/16660/14469 15941/16647/14456\r\nf 15955/16661/14470 15949/16655/14464 15948/16654/14463 15954/16660/14469\r\nf 15722/16428/14237 15956/16662/14471 15957/16663/14472 15723/16429/14238\r\nf 15956/16662/14471 15940/16646/14455 15943/16649/14458 15957/16663/14472\r\nf 15945/16651/14460 15958/16664/14473 15957/16663/14472 15943/16649/14458\r\nf 15958/16664/14473 15724/16430/14239 15723/16429/14238 15957/16663/14472\r\nf 15951/16657/14466 15950/16656/14465 15959/16665/14474 15960/16666/14475\r\nf 15950/16656/14465 15949/16655/14464 15961/16667/14476 15959/16665/14474\r\nf 15825/16531/14340 15824/16530/14339 15959/16665/14474 15961/16667/14476\r\nf 15824/16530/14339 15823/16529/14338 15960/16666/14475 15959/16665/14474\r\nf 15724/16430/14239 15958/16664/14473 15962/16668/14477 15851/16557/14366\r\nf 15958/16664/14473 15945/16651/14460 15953/16659/14468 15962/16668/14477\r\nf 15914/16620/14429 15939/16645/14454 15962/16668/14477 15953/16659/14468\r\nf 15939/16645/14454 15852/16558/14367 15851/16557/14366 15962/16668/14477\r\nf 15825/16531/14340 15961/16667/14476 15963/16669/14478 15871/16577/14386\r\nf 15961/16667/14476 15949/16655/14464 15955/16661/14470 15963/16669/14478\r\nf 15940/16646/14455 15956/16662/14471 15963/16669/14478 15955/16661/14470\r\nf 15956/16662/14471 15722/16428/14237 15871/16577/14386 15963/16669/14478\r\nf 15964/16670/14479 15965/16671/14480 15966/16672/14481 15967/16673/14482\r\nf 15965/16671/14480 15855/16561/14370 15968/16674/14483 15966/16672/14481\r\nf 15935/16641/14450 15934/16640/14449 15966/16672/14481 15968/16674/14483\r\nf 15934/16640/14449 15933/16639/14448 15967/16673/14482 15966/16672/14481\r\nf 15855/16561/14370 15853/16559/14368 15969/16675/14484 15968/16674/14483\r\nf 15853/16559/14368 15704/16410/14219 15850/16556/14365 15969/16675/14484\r\nf 15852/16558/14367 15938/16644/14453 15969/16675/14484 15850/16556/14365\r\nf 15938/16644/14453 15935/16641/14450 15968/16674/14483 15969/16675/14484\r\nf 15786/16492/14301 15800/16506/14315 15970/16676/14485 15841/16547/14356\r\nf 15800/16506/14315 15765/16471/14280 15971/16677/14486 15970/16676/14485\r\nf 15951/16657/14466 15960/16666/14475 15970/16676/14485 15971/16677/14486\r\nf 15960/16666/14475 15823/16529/14338 15841/16547/14356 15970/16676/14485\r\nf 15765/16471/14280 15768/16474/14283 15972/16678/14487 15971/16677/14486\r\nf 15768/16474/14283 15759/16465/14274 15973/16679/14488 15972/16678/14487\r\nf 15905/16611/14420 15947/16653/14462 15972/16678/14487 15973/16679/14488\r\nf 15947/16653/14462 15951/16657/14466 15971/16677/14486 15972/16678/14487\r\nf 15759/16465/14274 15762/16468/14277 15974/16680/14489 15973/16679/14488\r\nf 15762/16468/14277 15753/16459/14268 15975/16681/14490 15974/16680/14489\r\nf 15897/16603/14412 15900/16606/14415 15974/16680/14489 15975/16681/14490\r\nf 15900/16606/14415 15905/16611/14420 15973/16679/14488 15974/16680/14489\r\nf 15744/16450/14259 15832/16538/14347 15976/16682/14491 15756/16462/14271\r\nf 15832/16538/14347 15807/16513/14322 15927/16633/14442 15976/16682/14491\r\nf 15897/16603/14412 15975/16681/14490 15976/16682/14491 15927/16633/14442\r\nf 15975/16681/14490 15753/16459/14268 15756/16462/14271 15976/16682/14491\r\nf 15680/16386/14195 15810/16516/14325 15977/16683/14492 15681/16387/14196\r\nf 15810/16516/14325 15691/16397/14206 15694/16400/14209 15977/16683/14492\r\nf 15699/16405/14214 15875/16581/14390 15977/16683/14492 15694/16400/14209\r\nf 15875/16581/14390 15682/16388/14197 15681/16387/14196 15977/16683/14492\r\nf 15715/16421/14230 15860/16566/14375 15978/16684/14493 15716/16422/14231\r\nf 15860/16566/14375 15739/16445/14254 15736/16442/14251 15978/16684/14493\r\nf 15735/16441/14250 15979/16685/14494 15978/16684/14493 15736/16442/14251\r\nf 15979/16685/14494 15717/16423/14232 15716/16422/14231 15978/16684/14493\r\nf 15730/16436/14245 15980/16686/14495 15981/16687/14496 15731/16437/14246\r\nf 15980/16686/14495 15982/16688/14497 15983/16689/14498 15981/16687/14496\r\nf 15984/16690/14499 15985/16691/14500 15981/16687/14496 15983/16689/14498\r\nf 15985/16691/14500 15732/16438/14247 15731/16437/14246 15981/16687/14496\r\nf 15777/16483/14292 15986/16692/14501 15987/16693/14502 15778/16484/14293\r\nf 15986/16692/14501 15789/16495/14304 15792/16498/14307 15987/16693/14502\r\nf 15797/16503/14312 15988/16694/14503 15987/16693/14502 15792/16498/14307\r\nf 15988/16694/14503 15779/16485/14294 15778/16484/14293 15987/16693/14502\r\nf 15680/16386/14195 15677/16383/14192 15989/16695/14504 15813/16519/14328\r\nf 15677/16383/14192 15676/16382/14191 15990/16696/14505 15989/16695/14504\r\nf 15838/16544/14353 15837/16543/14352 15989/16695/14504 15990/16696/14505\r\nf 15837/16543/14352 15814/16520/14329 15813/16519/14328 15989/16695/14504\r\nf 15779/16485/14294 15988/16694/14503 15991/16697/14506 15836/16542/14351\r\nf 15988/16694/14503 15797/16503/14312 15843/16549/14358 15991/16697/14506\r\nf 15816/16522/14331 15815/16521/14330 15991/16697/14506 15843/16549/14358\r\nf 15815/16521/14330 15814/16520/14329 15836/16542/14351 15991/16697/14506\r\nf 15715/16421/14230 15714/16420/14229 15992/16698/14507 15861/16567/14376\r\nf 15714/16420/14229 15713/16419/14228 15993/16699/14508 15992/16698/14507\r\nf 15982/16688/14497 15980/16686/14495 15992/16698/14507 15993/16699/14508\r\nf 15980/16686/14495 15730/16436/14245 15861/16567/14376 15992/16698/14507\r\nf 15732/16438/14247 15985/16691/14500 15994/16700/14509 15874/16580/14389\r\nf 15985/16691/14500 15984/16690/14499 15995/16701/14510 15994/16700/14509\r\nf 15684/16390/14199 15683/16389/14198 15994/16700/14509 15995/16701/14510\r\nf 15683/16389/14198 15682/16388/14197 15874/16580/14389 15994/16700/14509\r\nf 15695/16401/14210 15826/16532/14341 15996/16702/14511 15696/16402/14211\r\nf 15826/16532/14341 15817/16523/14332 15870/16576/14385 15996/16702/14511\r\nf 15718/16424/14233 15878/16584/14393 15996/16702/14511 15870/16576/14385\r\nf 15878/16584/14393 15697/16403/14212 15696/16402/14211 15996/16702/14511\r\nf 15706/16412/14221 15997/16703/14512 15998/16704/14513 15707/16413/14222\r\nf 15997/16703/14512 15726/16432/14241 15729/16435/14244 15998/16704/14513\r\nf 15734/16440/14249 15999/16705/14514 15998/16704/14513 15729/16435/14244\r\nf 15999/16705/14514 15708/16414/14223 15707/16413/14222 15998/16704/14513\r\nf 15741/16447/14256 15856/16562/14371 16000/16706/14515 15742/16448/14257\r\nf 15856/16562/14371 15855/16561/14370 15965/16671/14480 16000/16706/14515\r\nf 15964/16670/14479 16001/16707/14516 16000/16706/14515 15965/16671/14480\r\nf 16001/16707/14516 15743/16449/14258 15742/16448/14257 16000/16706/14515\r\nf 15793/16499/14308 16002/16708/14517 16003/16709/14518 15794/16500/14309\r\nf 16002/16708/14517 15780/16486/14295 15783/16489/14298 16003/16709/14518\r\nf 15788/16494/14303 16004/16710/14519 16003/16709/14518 15783/16489/14298\r\nf 16004/16710/14519 15795/16501/14310 15794/16500/14309 16003/16709/14518\r\nf 15695/16401/14210 15692/16398/14207 16005/16711/14520 15828/16534/14343\r\nf 15692/16398/14207 15691/16397/14206 15812/16518/14327 16005/16711/14520\r\nf 15816/16522/14331 15845/16551/14360 16005/16711/14520 15812/16518/14327\r\nf 15845/16551/14360 15829/16535/14344 15828/16534/14343 16005/16711/14520\r\nf 15795/16501/14310 16004/16710/14519 16006/16712/14521 15844/16550/14359\r\nf 16004/16710/14519 15788/16494/14303 15840/16546/14355 16006/16712/14521\r\nf 15821/16527/14336 15830/16536/14345 16006/16712/14521 15840/16546/14355\r\nf 15830/16536/14345 15829/16535/14344 15844/16550/14359 16006/16712/14521\r\nf 15741/16447/14256 15740/16446/14255 16007/16713/14522 15857/16563/14372\r\nf 15740/16446/14255 15739/16445/14254 15858/16564/14373 16007/16713/14522\r\nf 15726/16432/14241 15997/16703/14512 16007/16713/14522 15858/16564/14373\r\nf 15997/16703/14512 15706/16412/14221 15857/16563/14372 16007/16713/14522\r\nf 15708/16414/14223 15999/16705/14514 16008/16714/14523 15877/16583/14392\r\nf 15999/16705/14514 15734/16440/14249 15873/16579/14388 16008/16714/14523\r\nf 15699/16405/14214 15698/16404/14213 16008/16714/14523 15873/16579/14388\r\nf 15698/16404/14213 15697/16403/14212 15877/16583/14392 16008/16714/14523\r\nv 208.12 37.62 118.16\r\nv 208.93 37.68 119.05\r\nv 208.77 37.53 119.23\r\nv 207.96 37.46 118.34\r\nv 209.09 37.83 119.4\r\nv 209.07 37.72 119.64\r\nv 208.83 37.73 119.87\r\nv 208.53 37.54 119.46\r\nv 207.72 37.47 118.56\r\nv 214.68 90 111.98\r\nv 213.88 89.92 111.14\r\nv 214.09 89.77 110.96\r\nv 214.88 89.85 111.8\r\nv 212.89 89.81 110.04\r\nv 213.1 89.67 109.87\r\nv 213.36 89.5 109.65\r\nv 214.35 89.6 110.74\r\nv 215.14 89.68 111.57\r\nv 206.09 63.82 -27.35\r\nv 207.22 63.34 -28.86\r\nv 207.52 63.26 -28.67\r\nv 206.39 63.75 -27.15\r\nv 208.05 62.99 -29.95\r\nv 208.32 62.91 -29.76\r\nv 208.66 62.84 -29.54\r\nv 207.91 63.18 -28.42\r\nv 206.77 63.66 -26.91\r\nv 207.79 51.53 -32.56\r\nv 207.36 51.41 -32.11\r\nv 207.58 51.37 -31.79\r\nv 208.02 51.49 -32.24\r\nv 206.79 51.25 -31.48\r\nv 207.02 51.22 -31.17\r\nv 207.3 51.16 -30.78\r\nv 207.86 51.31 -31.38\r\nv 208.29 51.43 -31.83\r\nv 208.8 49.12 -32.38\r\nv 208.67 48.92 -32.06\r\nv 208.53 48.8 -32.39\r\nv 208.79 49.05 -32.71\r\nv 208.1 48.77 -31.45\r\nv 207.96 48.65 -31.77\r\nv 207.73 48.7 -32.07\r\nv 208.3 48.85 -32.71\r\nv 208.56 49.1 -33.03\r\nv 191.42 46.53 -9.95\r\nv 190.06 45.98 -7.27\r\nv 190.34 45.96 -7.14\r\nv 191.69 46.52 -9.81\r\nv 188.77 45.42 -4.35\r\nv 189.06 45.4 -4.24\r\nv 189.41 45.38 -4.09\r\nv 190.69 45.94 -6.99\r\nv 192.03 46.49 -9.64\r\nv 192.81 44.08 -10.23\r\nv 191.46 43.53 -7.56\r\nv 191.26 43.39 -7.7\r\nv 192.62 43.94 -10.38\r\nv 190.17 42.97 -4.65\r\nv 189.97 42.82 -4.78\r\nv 189.69 42.85 -4.9\r\nv 190.98 43.41 -7.83\r\nv 192.35 43.97 -10.52\r\nv 186 43.92 2.8\r\nv 187.28 44.54 -1.17\r\nv 187.4 44.2 -1.28\r\nv 186.12 43.58 2.7\r\nv 188.54 45.13 -4.54\r\nv 188.67 44.79 -4.64\r\nv 188.81 44.45 -4.75\r\nv 187.54 43.86 -1.38\r\nv 186.26 43.24 2.6\r\nv 207.96 63.41 -30.15\r\nv 207.07 63.7 -29.07\r\nv 207.18 64.02 -29.1\r\nv 208.06 63.71 -30.17\r\nv 205.94 64.18 -27.55\r\nv 206.05 64.51 -27.57\r\nv 206.25 64.96 -27.58\r\nv 207.39 64.47 -29.11\r\nv 208.25 64.11 -30.19\r\nv 206.08 89.41 101.57\r\nv 207.93 89.66 104.18\r\nv 208.03 89.97 104.08\r\nv 206.19 89.72 101.47\r\nv 209.7 89.88 106.47\r\nv 209.8 90.19 106.36\r\nv 209.99 90.62 106.25\r\nv 208.23 90.4 103.96\r\nv 206.39 90.15 101.36\r\nv 211.36 90.07 108.49\r\nv 211.46 90.38 108.38\r\nv 212.77 90.22 110.11\r\nv 212.86 90.53 110\r\nv 213.05 90.96 109.88\r\nv 211.65 90.81 108.26\r\nv 213.75 90.33 111.24\r\nv 213.84 90.64 111.13\r\nv 214.52 90.41 112.11\r\nv 214.61 90.72 112\r\nv 214.79 91.14 111.88\r\nv 214.02 91.06 111\r\nv 207.91 39.85 120.44\r\nv 207.41 39.81 119.92\r\nv 207.55 39.47 119.9\r\nv 208.05 39.51 120.42\r\nv 206.6 39.75 119.02\r\nv 206.74 39.4 118.99\r\nv 206.89 39.06 118.96\r\nv 207.7 39.13 119.86\r\nv 208.2 39.16 120.38\r\nv 190.5 37.95 94.24\r\nv 188.4 37.7 89.31\r\nv 188.53 37.35 89.28\r\nv 190.64 37.6 94.21\r\nv 186.47 37.54 84.11\r\nv 186.6 37.2 84.07\r\nv 186.75 36.85 84.03\r\nv 188.68 37.01 89.25\r\nv 190.79 37.26 94.18\r\nv 204.45 37.19 114.71\r\nv 206.28 37.35 116.9\r\nv 206.15 37.37 117.01\r\nv 204.33 37.21 114.81\r\nv 207.59 37.49 118.67\r\nv 207.48 37.68 118.74\r\nv 206.03 37.55 117.07\r\nv 204.21 37.39 114.88\r\nv 208.4 37.56 119.57\r\nv 208.71 37.75 119.99\r\nv 208.52 37.88 119.94\r\nv 208.29 37.74 119.64\r\nv 213.78 90.01 111.23\r\nv 212.78 89.9 110.13\r\nv 214.57 90.09 112.07\r\nv 214.53 90.16 112.11\r\nv 213.74 90.08 111.26\r\nv 212.74 89.97 110.15\r\nv 215.67 92.14 111.58\r\nv 214.9 92.06 110.72\r\nv 214.78 92.11 110.82\r\nv 215.55 92.19 111.69\r\nv 213.92 91.95 109.6\r\nv 213.8 92 109.7\r\nv 213.66 91.88 109.76\r\nv 214.63 91.98 110.88\r\nv 215.4 92.06 111.75\r\nv 212.53 91.8 107.99\r\nv 212.41 91.85 108.08\r\nv 210.88 91.61 105.99\r\nv 210.75 91.66 106.08\r\nv 210.6 91.53 106.13\r\nv 212.26 91.72 108.14\r\nv 207.24 51.41 -32.26\r\nv 206.67 51.25 -31.62\r\nv 207.68 51.53 -32.72\r\nv 207.63 51.5 -32.78\r\nv 207.19 51.38 -32.32\r\nv 206.62 51.22 -31.68\r\nv 208.18 48.9 -32.85\r\nv 208.45 49.15 -33.17\r\nv 207.61 48.74 -32.2\r\nv 207.49 48.93 -32.22\r\nv 208.06 49.09 -32.87\r\nv 208.27 49.27 -33.09\r\nv 204.86 50.73 -29.06\r\nv 205.98 51.03 -30.46\r\nv 205.86 51.03 -30.59\r\nv 204.74 50.73 -29.17\r\nv 205.8 51 -30.64\r\nv 204.68 50.69 -29.22\r\nv 204.68 50.46 -29.33\r\nv 203.2 50.07 -27.51\r\nv 203.17 50.23 -27.44\r\nv 204.65 50.63 -29.26\r\nv 201.47 49.6 -25.36\r\nv 201.44 49.77 -25.3\r\nv 201.47 49.84 -25.26\r\nv 203.2 50.3 -27.4\r\nv 201.74 48.93 -25.59\r\nv 203.47 49.39 -27.73\r\nv 203.54 49.22 -27.77\r\nv 201.81 48.75 -25.63\r\nv 204.96 49.78 -29.56\r\nv 205.02 49.61 -29.59\r\nv 205.11 49.39 -29.62\r\nv 203.62 48.99 -27.79\r\nv 201.9 48.53 -25.65\r\nv 206.08 50.09 -30.99\r\nv 206.15 49.92 -31.02\r\nv 206.91 50.32 -32.02\r\nv 206.97 50.14 -32.06\r\nv 207.06 49.92 -32.09\r\nv 206.24 49.69 -31.05\r\nv 207.63 51.26 -32.89\r\nv 207.19 51.14 -32.43\r\nv 207.16 51.31 -32.37\r\nv 207.6 51.43 -32.82\r\nv 206.62 50.99 -31.79\r\nv 206.59 51.15 -31.72\r\nv 207.48 50.47 -32.68\r\nv 207.54 50.3 -32.71\r\nv 207.92 50.59 -33.13\r\nv 207.99 50.42 -33.17\r\nv 208.02 50.34 -33.16\r\nv 207.63 50.07 -32.74\r\nv 207.03 63.53 -29.05\r\nv 205.9 64.02 -27.53\r\nv 207.91 63.22 -30.12\r\nv 207.9 63.12 -30.09\r\nv 207.04 63.45 -29.02\r\nv 205.91 63.94 -27.5\r\nv 207.71 64.99 -29.11\r\nv 208.56 64.61 -30.19\r\nv 206.57 65.48 -27.58\r\nv 206.88 65.9 -27.56\r\nv 208.01 65.41 -29.09\r\nv 208.86 65.02 -30.17\r\nv 204.91 65.53 -25.65\r\nv 205.23 66.05 -25.65\r\nv 203.37 66.17 -23.4\r\nv 203.7 66.69 -23.39\r\nv 204 67.11 -23.37\r\nv 205.53 66.47 -25.62\r\nv 201.68 66.88 -20.88\r\nv 202 67.4 -20.88\r\nv 199.95 67.63 -18.24\r\nv 200.27 68.15 -18.24\r\nv 200.58 68.57 -18.22\r\nv 202.3 67.82 -20.86\r\nv 198.34 68.39 -15.57\r\nv 198.66 68.91 -15.58\r\nv 196.84 69.17 -12.89\r\nv 197.16 69.68 -12.89\r\nv 197.47 70.1 -12.87\r\nv 198.97 69.33 -15.56\r\nv 195.45 69.94 -10.16\r\nv 195.78 70.46 -10.17\r\nv 194.18 70.72 -7.39\r\nv 194.51 71.24 -7.4\r\nv 194.82 71.66 -7.39\r\nv 196.09 70.88 -10.15\r\nv 193.03 71.5 -4.57\r\nv 193.36 72.02 -4.58\r\nv 191.97 72.29 -1.58\r\nv 192.3 72.81 -1.6\r\nv 192.62 73.23 -1.59\r\nv 193.67 72.43 -4.57\r\nv 191 73.12 1.69\r\nv 191.33 73.64 1.67\r\nv 190.09 74 5.35\r\nv 190.42 74.52 5.34\r\nv 190.74 74.94 5.34\r\nv 191.65 74.05 1.68\r\nv 189.22 74.96 9.53\r\nv 189.56 75.47 9.51\r\nv 188.44 75.96 14.04\r\nv 188.77 76.47 14.02\r\nv 189.09 76.88 14.01\r\nv 189.87 75.89 9.51\r\nv 187.77 76.97 18.7\r\nv 188.1 77.48 18.67\r\nv 187.26 77.97 23.34\r\nv 187.59 78.48 23.31\r\nv 187.91 78.89 23.31\r\nv 188.42 77.9 18.67\r\nv 186.95 78.92 27.78\r\nv 187.28 79.43 27.75\r\nv 186.8 79.83 32.07\r\nv 187.13 80.34 32.04\r\nv 187.45 80.76 32.03\r\nv 187.59 79.85 27.74\r\nv 186.82 80.69 36.24\r\nv 187.15 81.21 36.21\r\nv 186.96 81.51 40.35\r\nv 187.3 82.02 40.32\r\nv 187.61 82.43 40.31\r\nv 187.46 81.62 36.2\r\nv 187.23 82.26 44.43\r\nv 187.56 82.77 44.4\r\nv 187.65 82.99 48.58\r\nv 187.98 83.51 48.54\r\nv 188.29 83.92 48.52\r\nv 187.88 83.19 44.39\r\nv 188.25 83.72 52.87\r\nv 188.58 84.24 52.83\r\nv 189.07 84.48 57.41\r\nv 189.4 85 57.37\r\nv 189.72 85.41 57.35\r\nv 188.89 84.65 52.81\r\nv 190.15 85.3 62.28\r\nv 190.48 85.81 62.24\r\nv 191.47 86.13 67.36\r\nv 191.8 86.64 67.31\r\nv 192.11 87.05 67.29\r\nv 190.8 86.22 62.22\r\nv 192.98 86.93 72.49\r\nv 193.31 87.44 72.45\r\nv 194.68 87.65 77.56\r\nv 195.01 88.16 77.51\r\nv 195.32 88.57 77.48\r\nv 193.63 87.85 72.42\r\nv 196.53 88.26 82.4\r\nv 196.86 88.77 82.35\r\nv 198.48 88.76 86.97\r\nv 198.81 89.27 86.92\r\nv 199.12 89.68 86.88\r\nv 197.17 89.18 82.32\r\nv 200.49 89.19 91.19\r\nv 200.81 89.7 91.14\r\nv 202.5 89.55 95.02\r\nv 202.82 90.06 94.97\r\nv 203.13 90.47 94.93\r\nv 201.12 90.11 91.11\r\nv 204.47 89.87 98.4\r\nv 204.79 90.38 98.34\r\nv 206.7 90.66 101.3\r\nv 207.01 91.07 101.26\r\nv 205.1 90.79 98.3\r\nv 207.9 89.49 104.22\r\nv 209.66 89.71 106.52\r\nv 206.04 89.25 101.61\r\nv 206.06 89.16 101.61\r\nv 207.91 89.41 104.22\r\nv 209.67 89.63 106.52\r\nv 208.55 90.91 103.9\r\nv 210.3 91.12 106.18\r\nv 208.85 91.32 103.85\r\nv 211.33 89.9 108.53\r\nv 212.73 90.06 110.16\r\nv 211.34 89.82 108.53\r\nv 211.96 91.31 108.19\r\nv 213.36 91.47 109.81\r\nv 214.34 91.57 110.93\r\nv 215.11 91.65 111.8\r\nv 207.38 39.99 119.91\r\nv 206.57 39.92 119.01\r\nv 207.88 40.03 120.43\r\nv 207.91 40.1 120.39\r\nv 207.41 40.06 119.88\r\nv 206.6 39.99 118.98\r\nv 207.77 38.95 119.84\r\nv 208.26 38.99 120.36\r\nv 206.95 38.89 118.94\r\nv 207.04 38.66 118.9\r\nv 207.85 38.73 119.8\r\nv 208.3 38.92 120.33\r\nv 205.44 38.94 117.29\r\nv 205.51 38.76 117.27\r\nv 203.62 38.77 115.09\r\nv 203.68 38.6 115.07\r\nv 203.77 38.38 115.04\r\nv 205.6 38.54 117.23\r\nv 183.62 42.84 12.98\r\nv 183.92 42.84 13.05\r\nv 185.22 43.54 7.77\r\nv 184.92 43.55 7.69\r\nv 184.29 42.83 13.13\r\nv 185.59 43.53 7.87\r\nv 186.9 44.19 3.15\r\nv 186.54 44.21 3.04\r\nv 186.24 44.22 2.95\r\nv 185.03 40.4 12.65\r\nv 184.82 40.25 12.56\r\nv 186.12 40.95 7.28\r\nv 186.32 41.1 7.37\r\nv 184.52 40.27 12.5\r\nv 185.82 40.97 7.2\r\nv 187.15 41.64 2.44\r\nv 187.44 41.62 2.53\r\nv 187.65 41.77 2.63\r\nv 181.43 41.43 24.13\r\nv 181.74 41.43 24.18\r\nv 182.73 42.13 18.6\r\nv 182.43 42.13 18.54\r\nv 182.11 41.42 24.23\r\nv 183.11 42.12 18.67\r\nv 182.85 38.99 23.77\r\nv 182.63 38.84 23.7\r\nv 183.63 39.54 18.12\r\nv 183.85 39.69 18.2\r\nv 182.33 38.85 23.65\r\nv 183.33 39.56 18.06\r\nv 180.21 40.14 34.6\r\nv 180.52 40.14 34.62\r\nv 181.01 40.76 29.51\r\nv 180.7 40.76 29.47\r\nv 180.9 40.14 34.65\r\nv 181.39 40.75 29.55\r\nv 181.64 37.71 34.21\r\nv 181.42 37.55 34.16\r\nv 181.9 38.17 29.04\r\nv 182.12 38.32 29.1\r\nv 181.11 37.56 34.14\r\nv 181.6 38.18 29.01\r\nv 179.88 39.15 44.23\r\nv 180.19 39.16 44.23\r\nv 180.26 39.6 39.52\r\nv 179.95 39.6 39.51\r\nv 180.57 39.16 44.23\r\nv 180.64 39.6 39.54\r\nv 181.3 36.72 43.83\r\nv 181.08 36.56 43.81\r\nv 181.15 37.01 39.08\r\nv 181.37 37.16 39.12\r\nv 180.78 36.57 43.81\r\nv 180.85 37.01 39.07\r\nv 180.27 38.59 53.27\r\nv 180.58 38.6 53.24\r\nv 180.29 38.83 48.76\r\nv 179.98 38.83 48.77\r\nv 180.96 38.6 53.21\r\nv 180.67 38.83 48.74\r\nv 181.7 36.16 52.86\r\nv 181.48 36 52.86\r\nv 181.19 36.23 48.36\r\nv 181.41 36.39 48.37\r\nv 181.17 36 52.89\r\nv 180.88 36.24 48.38\r\nv 181.47 38.24 62.65\r\nv 181.78 38.25 62.6\r\nv 181.07 38.41 57.81\r\nv 180.76 38.41 57.85\r\nv 182.16 38.26 62.54\r\nv 181.45 38.42 57.76\r\nv 182.9 35.81 62.22\r\nv 182.68 35.65 62.25\r\nv 181.97 35.81 57.45\r\nv 182.19 35.97 57.43\r\nv 182.38 35.66 62.3\r\nv 181.66 35.82 57.48\r\nv 183.61 37.92 73.19\r\nv 183.91 37.93 73.11\r\nv 182.73 38.08 67.75\r\nv 182.42 38.07 67.81\r\nv 184.28 37.94 73.02\r\nv 183.1 38.08 67.68\r\nv 185.03 35.49 72.73\r\nv 184.82 35.33 72.78\r\nv 183.63 35.48 67.4\r\nv 183.84 35.64 67.37\r\nv 184.52 35.34 72.85\r\nv 183.33 35.48 67.46\r\nv 186.71 37.86 84.03\r\nv 187 37.87 83.93\r\nv 185.33 37.85 78.55\r\nv 185.04 37.84 78.64\r\nv 187.36 37.87 83.8\r\nv 185.7 37.85 78.45\r\nv 188.13 35.43 83.56\r\nv 187.92 35.27 83.63\r\nv 186.25 35.25 78.23\r\nv 186.46 35.41 78.18\r\nv 187.63 35.27 83.73\r\nv 185.95 35.25 78.32\r\nv 190.72 38.26 94.12\r\nv 191 38.27 93.99\r\nv 188.91 38.02 89.1\r\nv 188.63 38.01 89.21\r\nv 191.35 38.27 93.83\r\nv 189.27 38.03 88.95\r\nv 192.14 35.83 93.64\r\nv 191.94 35.67 93.73\r\nv 189.85 35.42 88.82\r\nv 190.04 35.58 88.74\r\nv 191.66 35.68 93.86\r\nv 189.56 35.42 88.94\r\nv 195.16 38.87 102.82\r\nv 195.42 38.88 102.66\r\nv 193.2 38.57 98.53\r\nv 192.92 38.56 98.68\r\nv 195.76 38.88 102.47\r\nv 193.54 38.57 98.36\r\nv 196.55 36.44 102.3\r\nv 196.37 36.28 102.42\r\nv 194.14 35.97 98.29\r\nv 194.33 36.13 98.18\r\nv 196.1 36.28 102.57\r\nv 193.87 35.98 98.43\r\nv 199.49 39.38 109.67\r\nv 199.75 39.39 109.49\r\nv 197.61 39.15 106.3\r\nv 197.35 39.14 106.46\r\nv 200.06 39.39 109.27\r\nv 197.94 39.15 106.09\r\nv 200.86 36.95 109.11\r\nv 200.69 36.79 109.25\r\nv 198.56 36.55 106.06\r\nv 198.74 36.71 105.93\r\nv 200.44 36.79 109.43\r\nv 198.3 36.56 106.22\r\nv 203.51 39.77 114.96\r\nv 203.75 39.78 114.76\r\nv 201.8 39.59 112.3\r\nv 201.55 39.59 112.49\r\nv 204.05 39.78 114.52\r\nv 202.1 39.59 112.07\r\nv 204.85 37.34 114.36\r\nv 204.69 37.18 114.51\r\nv 202.74 36.99 112.05\r\nv 202.9 37.15 111.9\r\nv 202.5 37 112.24\r\nv 206.78 40.06 118.81\r\nv 207.02 40.06 118.59\r\nv 205.57 39.94 116.94\r\nv 205.33 39.93 117.15\r\nv 207.32 40.06 118.32\r\nv 205.87 39.94 116.68\r\nv 206.52 37.34 116.69\r\nv 206.67 37.5 116.52\r\nv 208.11 40.16 120.2\r\nv 208.34 40.17 119.98\r\nv 207.84 40.13 119.47\r\nv 207.6 40.12 119.7\r\nv 208.61 40.16 119.71\r\nv 208.13 40.12 119.2\r\nv 214.41 91.66 109.19\r\nv 214.14 91.82 109.42\r\nv 215.12 91.93 110.52\r\nv 215.39 91.76 110.28\r\nv 215.89 92.01 111.37\r\nv 216.17 91.84 111.11\r\nv 209.82 89.47 106.41\r\nv 210.04 89.33 106.25\r\nv 211.7 89.52 108.25\r\nv 211.48 89.66 108.42\r\nv 210.32 89.16 106.04\r\nv 211.97 89.35 108.04\r\nv 211.38 91.31 105.61\r\nv 211.1 91.48 105.82\r\nv 212.75 91.67 107.81\r\nv 213.02 91.5 107.6\r\nv 206.21 89 101.52\r\nv 206.45 88.86 101.37\r\nv 208.29 89.11 103.97\r\nv 208.06 89.25 104.13\r\nv 206.74 88.7 101.19\r\nv 208.57 88.94 103.78\r\nv 207.82 90.86 100.79\r\nv 207.53 91.02 100.97\r\nv 209.36 91.27 103.55\r\nv 209.64 91.1 103.36\r\nv 207.29 91.15 101.12\r\nv 209.13 91.39 103.71\r\nv 202.32 88.4 95.18\r\nv 202.57 88.26 95.05\r\nv 204.54 88.58 98.42\r\nv 204.3 88.72 98.55\r\nv 202.88 88.1 94.89\r\nv 204.84 88.42 98.25\r\nv 203.99 90.26 94.53\r\nv 203.68 90.43 94.69\r\nv 205.63 90.74 98.03\r\nv 205.93 90.58 97.86\r\nv 203.43 90.55 94.82\r\nv 205.39 90.87 98.17\r\nv 198.31 87.61 87.12\r\nv 198.56 87.48 87.02\r\nv 200.56 87.9 91.23\r\nv 200.31 88.04 91.35\r\nv 198.89 87.32 86.89\r\nv 200.88 87.74 91.09\r\nv 200.01 89.48 86.55\r\nv 199.69 89.64 86.68\r\nv 201.68 90.06 90.88\r\nv 202 89.9 90.74\r\nv 199.42 89.76 86.79\r\nv 201.42 90.19 91\r\nv 194.51 86.5 77.73\r\nv 194.78 86.37 77.65\r\nv 196.62 86.97 82.47\r\nv 196.36 87.11 82.56\r\nv 195.11 86.21 77.55\r\nv 196.95 86.81 82.36\r\nv 196.24 88.38 77.22\r\nv 195.9 88.53 77.32\r\nv 197.74 89.14 82.14\r\nv 198.07 88.98 82.02\r\nv 195.63 88.65 77.4\r\nv 197.48 89.26 82.23\r\nv 191.3 84.98 67.54\r\nv 191.57 84.85 67.49\r\nv 193.09 85.65 72.61\r\nv 192.81 85.78 72.67\r\nv 191.92 84.7 67.42\r\nv 193.43 85.49 72.52\r\nv 193.05 86.86 67.1\r\nv 192.7 87.02 67.17\r\nv 194.22 87.81 72.28\r\nv 194.55 87.66 72.2\r\nv 192.42 87.14 67.23\r\nv 193.94 87.93 72.35\r\nv 188.9 83.34 57.6\r\nv 189.18 83.21 57.56\r\nv 190.26 84.03 62.43\r\nv 189.99 84.15 62.47\r\nv 189.53 83.06 57.52\r\nv 190.61 83.87 62.37\r\nv 190.67 85.23 57.22\r\nv 190.32 85.38 57.27\r\nv 191.4 86.19 62.12\r\nv 191.74 86.04 62.06\r\nv 190.04 85.5 57.3\r\nv 191.12 86.31 62.16\r\nv 187.48 81.85 48.77\r\nv 187.76 81.73 48.75\r\nv 188.36 82.45 53.04\r\nv 188.08 82.58 53.06\r\nv 188.11 81.58 48.74\r\nv 188.71 82.31 53\r\nv 189.26 83.75 48.47\r\nv 188.9 83.9 48.48\r\nv 189.5 84.63 52.75\r\nv 189.85 84.48 52.72\r\nv 188.61 84.01 48.5\r\nv 189.21 84.74 52.78\r\nv 186.79 80.36 40.55\r\nv 187.08 80.24 40.56\r\nv 187.34 81 44.63\r\nv 187.06 81.12 44.63\r\nv 187.43 80.1 40.57\r\nv 187.7 80.85 44.62\r\nv 188.58 82.28 40.31\r\nv 188.22 82.42 40.31\r\nv 188.49 83.17 44.37\r\nv 188.84 83.03 44.36\r\nv 187.93 82.52 40.3\r\nv 188.2 83.28 44.37\r\nv 186.63 78.69 32.28\r\nv 186.92 78.57 32.31\r\nv 186.93 79.43 36.47\r\nv 186.65 79.55 36.45\r\nv 187.27 78.44 32.34\r\nv 187.29 79.3 36.49\r\nv 188.42 80.61 32.1\r\nv 188.06 80.75 32.06\r\nv 188.08 81.61 36.22\r\nv 188.43 81.47 36.24\r\nv 187.77 80.85 32.04\r\nv 187.79 81.71 36.2\r\nv 187.09 76.82 23.55\r\nv 187.38 76.71 23.6\r\nv 187.06 77.67 28.03\r\nv 186.78 77.78 27.99\r\nv 187.73 76.58 23.66\r\nv 187.42 77.53 28.08\r\nv 188.88 78.76 23.44\r\nv 188.52 78.89 23.38\r\nv 188.21 79.84 27.8\r\nv 188.56 79.71 27.84\r\nv 188.23 78.99 23.33\r\nv 187.92 79.95 27.76\r\nv 188.27 74.82 14.25\r\nv 188.55 74.71 14.32\r\nv 187.88 75.72 18.97\r\nv 187.6 75.83 18.91\r\nv 188.9 74.58 14.4\r\nv 188.24 75.59 19.04\r\nv 190.04 76.77 14.21\r\nv 189.69 76.89 14.12\r\nv 189.03 77.9 18.76\r\nv 189.38 77.77 18.83\r\nv 189.41 76.99 14.06\r\nv 188.74 78 18.71\r\nv 189.92 72.87 5.57\r\nv 190.19 72.76 5.66\r\nv 189.33 73.71 9.82\r\nv 189.05 73.82 9.74\r\nv 190.54 72.64 5.77\r\nv 189.68 73.59 9.92\r\nv 191.68 74.83 5.59\r\nv 191.34 74.95 5.49\r\nv 190.47 75.9 9.64\r\nv 190.82 75.78 9.73\r\nv 191.05 75.04 5.4\r\nv 190.19 75.99 9.56\r\nv 191.8 71.16 -1.35\r\nv 192.07 71.06 -1.24\r\nv 191.1 71.88 2.01\r\nv 190.82 71.99 1.91\r\nv 192.4 70.95 -1.1\r\nv 191.44 71.77 2.14\r\nv 193.54 73.14 -1.26\r\nv 193.21 73.25 -1.4\r\nv 192.24 74.08 1.84\r\nv 192.58 73.96 1.97\r\nv 192.93 73.34 -1.51\r\nv 191.96 74.16 1.74\r\nv 194 69.59 -7.16\r\nv 194.26 69.5 -7.02\r\nv 193.11 70.27 -4.21\r\nv 192.85 70.37 -4.34\r\nv 194.59 69.39 -6.85\r\nv 193.45 70.16 -4.05\r\nv 195.72 71.59 -6.99\r\nv 195.39 71.7 -7.16\r\nv 194.25 72.47 -4.36\r\nv 194.58 72.36 -4.21\r\nv 195.12 71.78 -7.29\r\nv 193.98 72.55 -4.49\r\nv 196.65 68.03 -12.65\r\nv 196.91 67.95 -12.49\r\nv 195.52 68.72 -9.78\r\nv 195.27 68.81 -9.92\r\nv 197.22 67.85 -12.3\r\nv 195.84 68.62 -9.59\r\nv 198.34 70.05 -12.41\r\nv 198.02 70.15 -12.6\r\nv 196.65 70.92 -9.9\r\nv 196.97 70.82 -9.72\r\nv 197.77 70.23 -12.76\r\nv 196.39 71 -10.05\r\nv 199.77 66.5 -18.01\r\nv 200.01 66.41 -17.83\r\nv 198.4 67.18 -15.17\r\nv 198.16 67.26 -15.34\r\nv 200.31 66.32 -17.62\r\nv 198.71 67.08 -14.97\r\nv 201.42 68.54 -17.7\r\nv 201.11 68.63 -17.91\r\nv 199.51 69.39 -15.27\r\nv 199.82 69.29 -15.06\r\nv 200.87 68.7 -18.09\r\nv 199.26 69.46 -15.44\r\nv 203.2 65.04 -23.17\r\nv 203.44 64.96 -22.98\r\nv 201.74 65.67 -20.48\r\nv 201.5 65.75 -20.66\r\nv 203.75 64.87 -22.75\r\nv 202.03 65.57 -20.25\r\nv 204.84 67.09 -22.81\r\nv 204.53 67.18 -23.04\r\nv 202.83 67.88 -20.55\r\nv 203.13 67.79 -20.32\r\nv 204.29 67.24 -23.23\r\nv 202.59 67.95 -20.73\r\nv 205.01 64.31 -25.23\r\nv 204.74 64.39 -25.42\r\nv 205.35 64.23 -25\r\nv 207.86 65.88 -26.98\r\nv 207.48 65.96 -27.22\r\nv 206.1 66.53 -25.29\r\nv 206.44 66.45 -25.06\r\nv 207.18 66.03 -27.41\r\nv 205.83 66.6 -25.48\r\nv 209.74 65.05 -29.63\r\nv 209.41 65.12 -29.85\r\nv 208.62 65.48 -28.74\r\nv 208.99 65.39 -28.5\r\nv 209.15 65.17 -30.03\r\nv 208.31 65.54 -28.94\r\nv 208.78 62.76 -30.95\r\nv 208.81 62.7 -30.89\r\nv 208.67 62.76 -30.43\r\nv 208.5 62.83 -30.56\r\nv 208.86 62.66 -30.83\r\nv 208.89 62.7 -30.28\r\nv 209.99 64.9 -30.38\r\nv 209.78 64.94 -30.53\r\nv 209.99 64.86 -30.94\r\nv 209.92 64.85 -31\r\nv 209.85 64.83 -31.04\r\nv 209.6 64.96 -30.65\r\nv 205.1 50.7 -28.85\r\nv 206.21 51 -30.2\r\nv 205.39 50.65 -28.59\r\nv 206.5 50.95 -29.88\r\nv 206.18 48.25 -29.21\r\nv 206.02 48.12 -29.43\r\nv 207.14 48.43 -30.79\r\nv 207.29 48.55 -30.53\r\nv 205.79 48.17 -29.64\r\nv 206.91 48.47 -31.05\r\nv 201.66 49.87 -25.11\r\nv 201.9 49.85 -24.92\r\nv 203.62 50.31 -27.05\r\nv 203.38 50.34 -27.24\r\nv 202.2 49.8 -24.69\r\nv 203.92 50.27 -26.82\r\nv 202.98 47.4 -25.3\r\nv 202.82 47.27 -25.5\r\nv 204.55 47.73 -27.63\r\nv 204.7 47.86 -27.43\r\nv 202.58 47.31 -25.68\r\nv 204.31 47.78 -27.82\r\nv 197.92 48.81 -20.22\r\nv 198.18 48.78 -20.04\r\nv 200.05 49.33 -22.54\r\nv 199.8 49.36 -22.73\r\nv 198.49 48.74 -19.82\r\nv 200.35 49.29 -22.32\r\nv 199.27 46.34 -20.43\r\nv 199.1 46.21 -20.62\r\nv 200.97 46.76 -23.12\r\nv 201.13 46.89 -22.93\r\nv 198.85 46.25 -20.8\r\nv 200.72 46.8 -23.3\r\nv 194.47 47.67 -15.15\r\nv 194.73 47.65 -14.99\r\nv 196.4 48.22 -17.53\r\nv 196.14 48.24 -17.7\r\nv 195.05 47.62 -14.79\r\nv 196.72 48.19 -17.32\r\nv 195.83 45.21 -15.4\r\nv 195.66 45.08 -15.57\r\nv 197.33 45.65 -18.11\r\nv 197.5 45.78 -17.93\r\nv 195.39 45.11 -15.73\r\nv 197.07 45.68 -18.28\r\nv 193.16 47.08 -12.42\r\nv 192.89 47.1 -12.58\r\nv 193.49 47.05 -12.24\r\nv 194.09 44.51 -13\r\nv 194.27 44.64 -12.84\r\nv 193.82 44.54 -13.15\r\nv 187.8 44.83 -0.91\r\nv 187.51 44.83 -1.01\r\nv 188.17 44.81 -0.78\r\nv 188.71 42.24 -1.43\r\nv 188.92 42.39 -1.31\r\nv 188.42 42.26 -1.53\r\nv 184.8 42.91 7.47\r\nv 184.67 43.25 7.56\r\nv 184.93 42.57 7.38\r\nv 183.63 41.86 12.68\r\nv 183.49 42.2 12.77\r\nv 183.37 42.55 12.86\r\nv 191.2 46.25 -10.17\r\nv 191.34 45.91 -10.28\r\nv 189.97 45.35 -7.58\r\nv 189.84 45.69 -7.47\r\nv 191.48 45.58 -10.39\r\nv 190.11 45.01 -7.69\r\nv 194.26 47.4 -15.39\r\nv 194.39 47.06 -15.51\r\nv 192.81 46.48 -12.92\r\nv 192.68 46.82 -12.8\r\nv 194.54 46.72 -15.62\r\nv 192.96 46.15 -13.03\r\nv 197.73 48.53 -20.47\r\nv 197.86 48.2 -20.58\r\nv 196.08 47.63 -18.06\r\nv 195.95 47.97 -17.94\r\nv 198.01 47.86 -20.7\r\nv 196.22 47.29 -18.17\r\nv 201.6 49.26 -25.48\r\nv 199.74 48.75 -23.1\r\nv 199.61 49.08 -22.98\r\nv 199.88 48.41 -23.21\r\nv 204.81 50.12 -29.45\r\nv 203.33 49.73 -27.62\r\nv 206.76 50.65 -31.91\r\nv 205.94 50.43 -30.87\r\nv 205.8 50.76 -30.75\r\nv 207.77 50.93 -33.02\r\nv 207.33 50.81 -32.56\r\nv 208.65 63.65 -30.75\r\nv 208.57 63.44 -30.73\r\nv 208.79 63.93 -30.76\r\nv 209.17 63.85 -31.08\r\nv 209.08 63.73 -31.08\r\nv 209.05 63.63 -31.07\r\nv 203.07 65.4 -23.36\r\nv 203.17 65.73 -23.38\r\nv 204.71 65.08 -25.64\r\nv 204.6 64.75 -25.62\r\nv 199.64 66.86 -18.2\r\nv 199.75 67.19 -18.22\r\nv 201.47 66.44 -20.87\r\nv 201.37 66.11 -20.85\r\nv 196.52 68.4 -12.83\r\nv 196.63 68.72 -12.86\r\nv 198.13 67.95 -15.55\r\nv 198.02 67.63 -15.53\r\nv 193.85 69.96 -7.32\r\nv 193.97 70.28 -7.36\r\nv 195.24 69.5 -10.13\r\nv 195.13 69.18 -10.1\r\nv 191.64 71.53 -1.5\r\nv 191.76 71.86 -1.54\r\nv 192.82 71.06 -4.54\r\nv 192.7 70.74 -4.49\r\nv 189.76 73.25 5.45\r\nv 189.88 73.57 5.4\r\nv 190.79 72.69 1.73\r\nv 190.67 72.36 1.78\r\nv 188.11 75.2 14.14\r\nv 188.22 75.52 14.08\r\nv 189.01 74.52 9.58\r\nv 188.89 74.2 9.62\r\nv 186.93 77.21 23.45\r\nv 187.05 77.53 23.39\r\nv 187.56 76.53 18.75\r\nv 187.44 76.21 18.8\r\nv 186.47 79.08 32.19\r\nv 186.59 79.4 32.13\r\nv 186.73 78.49 27.84\r\nv 186.61 78.17 27.9\r\nv 186.63 80.76 40.49\r\nv 186.75 81.07 40.42\r\nv 186.6 80.26 36.31\r\nv 186.48 79.94 36.38\r\nv 187.31 82.25 48.72\r\nv 187.43 82.56 48.65\r\nv 187.01 81.83 44.5\r\nv 186.89 81.51 44.57\r\nv 188.74 83.74 57.56\r\nv 188.86 84.05 57.49\r\nv 188.03 83.29 52.94\r\nv 187.92 82.98 53.02\r\nv 191.14 85.38 67.52\r\nv 191.26 85.7 67.44\r\nv 189.94 84.87 62.36\r\nv 189.83 84.56 62.44\r\nv 194.36 86.91 77.73\r\nv 194.47 87.22 77.64\r\nv 192.77 86.5 72.58\r\nv 192.66 86.18 72.66\r\nv 198.16 88.02 87.14\r\nv 198.27 88.33 87.06\r\nv 196.32 87.83 82.49\r\nv 196.21 87.51 82.57\r\nv 202.19 88.81 95.21\r\nv 202.3 89.12 95.12\r\nv 200.28 88.76 91.29\r\nv 200.17 88.45 91.38\r\nv 204.27 89.44 98.5\r\nv 204.16 89.13 98.6\r\nv 203.33 39.46 115.15\r\nv 203.47 39.12 115.13\r\nv 205.29 39.28 117.32\r\nv 205.16 39.62 117.35\r\nv 199.3 39.07 109.84\r\nv 199.44 38.72 109.82\r\nv 201.51 38.93 112.64\r\nv 201.37 39.28 112.67\r\nv 199.59 38.38 109.79\r\nv 201.66 38.59 112.61\r\nv 194.95 38.56 102.97\r\nv 195.09 38.21 102.94\r\nv 197.29 38.49 106.6\r\nv 197.16 38.83 106.63\r\nv 195.24 37.87 102.91\r\nv 197.44 38.14 106.57\r\nv 192.85 37.9 98.79\r\nv 192.71 38.25 98.81\r\nv 193 37.56 98.76\r\nv 183.36 37.61 73.24\r\nv 183.49 37.26 73.19\r\nv 184.92 37.18 78.66\r\nv 184.79 37.53 78.71\r\nv 183.63 36.92 73.13\r\nv 185.07 36.84 78.61\r\nv 181.22 37.93 62.67\r\nv 181.35 37.59 62.62\r\nv 182.3 37.41 67.79\r\nv 182.17 37.76 67.85\r\nv 181.49 37.24 62.56\r\nv 182.44 37.07 67.74\r\nv 180.01 38.28 53.26\r\nv 180.14 37.93 53.2\r\nv 180.63 37.75 57.8\r\nv 180.51 38.09 57.86\r\nv 180.28 37.59 53.14\r\nv 180.77 37.4 57.74\r\nv 179.62 38.84 44.19\r\nv 179.75 38.5 44.12\r\nv 179.85 38.17 48.68\r\nv 179.72 38.51 48.75\r\nv 179.88 38.15 44.05\r\nv 179.99 37.83 48.62\r\nv 179.96 39.83 34.53\r\nv 180.08 39.49 34.44\r\nv 179.81 38.94 39.38\r\nv 179.69 39.29 39.46\r\nv 180.22 39.15 34.36\r\nv 179.95 38.6 39.3\r\nv 181.18 41.13 24.03\r\nv 181.3 40.79 23.94\r\nv 180.57 40.11 29.3\r\nv 180.44 40.45 29.39\r\nv 181.44 40.44 23.85\r\nv 180.71 39.77 29.22\r\nv 182.3 41.49 18.34\r\nv 182.18 41.83 18.43\r\nv 182.44 41.15 18.26\r\nv 184.76 43.53 7.65\r\nv 183.46 42.83 12.94\r\nv 186.09 44.2 2.9\r\nv 186.02 44.16 2.87\r\nv 184.69 43.49 7.63\r\nv 183.39 42.79 12.92\r\nv 185.66 41 7.16\r\nv 186.99 41.67 2.39\r\nv 184.36 40.3 12.46\r\nv 184.23 40.48 12.47\r\nv 185.53 41.18 7.17\r\nv 186.86 41.85 2.4\r\nv 182.27 42.12 18.51\r\nv 181.27 41.41 24.1\r\nv 182.2 42.07 18.49\r\nv 181.2 41.37 24.08\r\nv 183.17 39.58 18.04\r\nv 182.17 38.88 23.63\r\nv 182.04 39.06 23.65\r\nv 183.04 39.76 18.05\r\nv 180.54 40.74 29.45\r\nv 180.05 40.12 34.58\r\nv 180.47 40.7 29.44\r\nv 179.98 40.08 34.57\r\nv 181.44 38.2 28.99\r\nv 180.95 37.58 34.13\r\nv 180.82 37.77 34.15\r\nv 181.3 38.39 29.01\r\nv 179.79 39.58 39.51\r\nv 179.72 39.13 44.23\r\nv 179.71 39.53 39.5\r\nv 179.64 39.09 44.23\r\nv 180.68 37.04 39.07\r\nv 180.61 36.59 43.82\r\nv 180.48 36.77 43.84\r\nv 180.55 37.22 39.09\r\nv 179.82 38.8 48.78\r\nv 180.11 38.57 53.28\r\nv 179.75 38.76 48.78\r\nv 180.04 38.52 53.28\r\nv 180.72 36.26 48.39\r\nv 181.01 36.02 52.91\r\nv 180.88 36.2 52.94\r\nv 180.58 36.44 48.41\r\nv 180.6 38.38 57.87\r\nv 181.32 38.22 62.68\r\nv 180.53 38.34 57.87\r\nv 181.24 38.18 62.69\r\nv 181.5 35.84 57.51\r\nv 182.22 35.68 62.32\r\nv 182.09 35.86 62.36\r\nv 181.37 36.02 57.54\r\nv 182.26 38.05 67.84\r\nv 183.45 37.9 73.23\r\nv 182.19 38 67.86\r\nv 183.38 37.86 73.24\r\nv 183.17 35.5 67.5\r\nv 184.36 35.36 72.89\r\nv 184.23 35.54 72.93\r\nv 183.04 35.68 67.53\r\nv 184.88 37.82 78.68\r\nv 186.55 37.83 84.08\r\nv 184.81 37.77 78.7\r\nv 186.49 37.79 84.1\r\nv 185.8 35.27 78.37\r\nv 187.48 35.29 83.78\r\nv 187.35 35.47 83.83\r\nv 185.66 35.45 78.41\r\nv 188.48 37.99 89.27\r\nv 190.58 38.24 94.18\r\nv 188.41 37.94 89.3\r\nv 190.51 38.19 94.22\r\nv 189.41 35.45 88.99\r\nv 191.51 35.7 93.93\r\nv 191.39 35.88 93.98\r\nv 189.28 35.63 89.04\r\nv 192.78 38.54 98.75\r\nv 195.02 38.85 102.9\r\nv 192.72 38.49 98.79\r\nv 194.96 38.8 102.94\r\nv 193.72 36 98.5\r\nv 195.96 36.31 102.65\r\nv 195.84 36.49 102.71\r\nv 193.59 36.18 98.55\r\nv 197.22 39.12 106.55\r\nv 199.36 39.36 109.77\r\nv 197.16 39.07 106.59\r\nv 199.3 39.31 109.81\r\nv 198.16 36.58 106.31\r\nv 200.31 36.82 109.52\r\nv 200.18 37 109.58\r\nv 198.04 36.76 106.37\r\nv 201.42 39.57 112.58\r\nv 203.38 39.75 115.06\r\nv 201.37 39.52 112.63\r\nv 203.33 39.71 115.11\r\nv 202.37 37.03 112.34\r\nv 202.25 37.21 112.4\r\nv 205.21 39.91 117.26\r\nv 206.65 40.04 118.92\r\nv 205.15 39.87 117.31\r\nv 207.47 40.1 119.82\r\nv 207.98 40.14 120.33\r\nv 211.37 89.75 108.51\r\nv 209.71 89.56 106.49\r\nv 207.95 89.34 104.2\r\nv 206.1 89.09 101.59\r\nv 209 91.44 103.8\r\nv 207.16 91.2 101.21\r\nv 204.18 88.81 98.62\r\nv 202.2 88.49 95.24\r\nv 204.13 88.88 98.64\r\nv 202.16 88.56 95.25\r\nv 205.25 90.92 98.25\r\nv 203.29 90.6 94.89\r\nv 200.19 88.13 91.4\r\nv 198.18 87.7 87.17\r\nv 200.14 88.2 91.42\r\nv 198.13 87.77 87.18\r\nv 201.28 90.24 91.07\r\nv 199.28 89.81 86.85\r\nv 196.22 87.19 82.6\r\nv 194.38 86.59 77.76\r\nv 196.18 87.26 82.61\r\nv 194.33 86.66 77.77\r\nv 197.33 89.31 82.29\r\nv 195.48 88.7 77.45\r\nv 192.68 85.87 72.7\r\nv 191.16 85.07 67.57\r\nv 192.63 85.93 72.71\r\nv 191.11 85.14 67.57\r\nv 193.79 87.98 72.39\r\nv 192.27 87.18 67.26\r\nv 189.85 84.24 62.49\r\nv 188.76 83.42 57.61\r\nv 189.8 84.31 62.49\r\nv 188.71 83.49 57.61\r\nv 190.96 86.35 62.19\r\nv 189.88 85.54 57.33\r\nv 187.94 82.66 53.07\r\nv 187.33 81.93 48.77\r\nv 187.89 82.73 53.06\r\nv 187.28 82 48.76\r\nv 189.06 84.78 52.8\r\nv 188.46 84.05 48.51\r\nv 186.92 81.2 44.63\r\nv 186.65 80.44 40.55\r\nv 186.87 81.27 44.62\r\nv 186.6 80.51 40.54\r\nv 188.04 83.32 44.38\r\nv 187.78 82.56 40.3\r\nv 186.5 79.63 36.44\r\nv 186.49 78.77 32.26\r\nv 186.45 79.7 36.43\r\nv 186.44 78.83 32.24\r\nv 187.63 81.75 36.2\r\nv 187.62 80.89 32.03\r\nv 186.63 77.86 27.97\r\nv 186.95 76.9 23.52\r\nv 186.58 77.92 27.95\r\nv 186.9 76.96 23.5\r\nv 187.76 79.98 27.74\r\nv 188.08 79.03 23.31\r\nv 187.46 75.9 18.87\r\nv 188.13 74.89 14.21\r\nv 187.41 75.97 18.85\r\nv 188.08 74.95 14.19\r\nv 188.59 78.03 18.68\r\nv 189.25 77.02 14.02\r\nv 188.91 73.89 9.7\r\nv 189.78 72.94 5.52\r\nv 188.86 73.95 9.68\r\nv 189.73 73 5.5\r\nv 190.04 76.03 9.52\r\nv 190.9 75.07 5.35\r\nv 190.69 72.05 1.86\r\nv 191.66 71.23 -1.41\r\nv 190.64 72.12 1.83\r\nv 191.61 71.29 -1.45\r\nv 191.81 74.19 1.7\r\nv 192.78 73.37 -1.57\r\nv 192.72 70.43 -4.4\r\nv 193.87 69.65 -7.23\r\nv 192.67 70.49 -4.44\r\nv 193.82 69.71 -7.27\r\nv 193.83 72.57 -4.55\r\nv 194.98 71.8 -7.36\r\nv 195.14 68.87 -10\r\nv 196.53 68.09 -12.73\r\nv 195.09 68.93 -10.04\r\nv 196.48 68.15 -12.77\r\nv 196.25 71.02 -10.12\r\nv 197.63 70.25 -12.84\r\nv 198.03 67.32 -15.43\r\nv 199.65 66.56 -18.1\r\nv 197.99 67.38 -15.47\r\nv 199.61 66.61 -18.14\r\nv 199.12 69.48 -15.52\r\nv 200.73 68.72 -18.18\r\nv 201.38 65.8 -20.75\r\nv 203.08 65.09 -23.27\r\nv 201.34 65.86 -20.8\r\nv 203.04 65.15 -23.31\r\nv 202.46 67.97 -20.82\r\nv 204.15 67.26 -23.33\r\nv 204.61 64.45 -25.52\r\nv 205.95 63.88 -27.45\r\nv 204.57 64.51 -25.57\r\nv 205.68 66.61 -25.58\r\nv 207.03 66.04 -27.51\r\nv 207.08 63.39 -28.97\r\nv 207.93 63.05 -30.04\r\nv 208.16 65.55 -29.04\r\nv 209.01 65.17 -30.13\r\nv 208.43 62.89 -30.63\r\nv 208.77 62.84 -30.98\r\nv 208.44 63 -30.67\r\nv 208.82 63 -31.01\r\nv 209.49 64.94 -30.72\r\nv 209.8 64.77 -31.06\r\nv 209.68 64.59 -31.07\r\nv 209.36 64.78 -30.74\r\nv 206.79 48.51 -31.17\r\nv 205.66 48.21 -29.74\r\nv 205.54 48.4 -29.75\r\nv 206.67 48.7 -31.18\r\nv 203.25 50.33 -27.35\r\nv 201.53 49.87 -25.21\r\nv 204.18 47.82 -27.91\r\nv 202.45 47.35 -25.77\r\nv 202.33 47.54 -25.78\r\nv 204.06 48 -27.93\r\nv 199.67 49.35 -22.83\r\nv 197.79 48.8 -20.31\r\nv 199.61 49.32 -22.88\r\nv 197.73 48.77 -20.36\r\nv 200.59 46.84 -23.39\r\nv 198.72 46.29 -20.88\r\nv 198.6 46.47 -20.89\r\nv 200.47 47.02 -23.4\r\nv 196.01 48.24 -17.79\r\nv 194.33 47.67 -15.24\r\nv 195.95 48.2 -17.84\r\nv 194.27 47.63 -15.29\r\nv 196.94 45.72 -18.36\r\nv 195.26 45.15 -15.81\r\nv 195.13 45.34 -15.82\r\nv 196.81 45.91 -18.37\r\nv 192.75 47.09 -12.66\r\nv 191.28 46.53 -10.03\r\nv 192.69 47.06 -12.7\r\nv 191.21 46.49 -10.07\r\nv 193.68 44.58 -13.23\r\nv 192.2 44.01 -10.59\r\nv 192.08 44.19 -10.59\r\nv 193.55 44.76 -13.23\r\nv 189.92 45.97 -7.35\r\nv 188.62 45.4 -4.42\r\nv 189.85 45.93 -7.38\r\nv 188.56 45.36 -4.45\r\nv 190.84 43.45 -7.89\r\nv 189.54 42.88 -4.95\r\nv 189.41 43.06 -4.95\r\nv 190.71 43.63 -7.89\r\nv 187.36 44.82 -1.07\r\nv 187.29 44.78 -1.1\r\nv 188.27 42.29 -1.58\r\nv 188.14 42.48 -1.58\r\nv 184.65 43.42 7.6\r\nv 185.98 44.09 2.84\r\nv 183.35 42.72 12.9\r\nv 185 42.39 7.34\r\nv 183.7 41.69 12.64\r\nv 186.33 43.07 2.57\r\nv 186.42 42.84 2.54\r\nv 185.09 42.17 7.31\r\nv 183.79 41.46 12.62\r\nv 187.25 44.71 -1.12\r\nv 188.52 45.3 -4.48\r\nv 187.61 43.69 -1.41\r\nv 188.88 44.28 -4.78\r\nv 188.97 44.05 -4.81\r\nv 187.7 43.46 -1.44\r\nv 189.81 45.86 -7.42\r\nv 191.18 46.42 -10.11\r\nv 190.18 44.84 -7.73\r\nv 191.55 45.4 -10.43\r\nv 191.64 45.18 -10.46\r\nv 190.27 44.62 -7.76\r\nv 192.65 46.99 -12.74\r\nv 194.23 47.56 -15.33\r\nv 193.02 45.98 -13.07\r\nv 194.61 46.55 -15.66\r\nv 194.69 46.32 -15.68\r\nv 193.11 45.75 -13.09\r\nv 195.92 48.14 -17.88\r\nv 197.7 48.7 -20.4\r\nv 196.29 47.12 -18.21\r\nv 198.07 47.69 -20.73\r\nv 198.16 47.46 -20.76\r\nv 196.38 46.9 -18.23\r\nv 199.58 49.25 -22.92\r\nv 199.95 48.24 -23.24\r\nv 200.04 48.01 -23.27\r\nv 205.77 50.93 -30.69\r\nv 208.49 63.2 -30.71\r\nv 208.93 63.33 -31.04\r\nv 209.08 64.37 -30.76\r\nv 209.41 64.21 -31.08\r\nv 204.56 64.59 -25.6\r\nv 203.03 65.23 -23.34\r\nv 201.33 65.94 -20.83\r\nv 199.6 66.69 -18.17\r\nv 197.98 67.46 -15.5\r\nv 196.47 68.23 -12.8\r\nv 195.08 69.01 -10.07\r\nv 193.81 69.79 -7.29\r\nv 192.66 70.57 -4.47\r\nv 191.6 71.37 -1.47\r\nv 190.63 72.2 1.8\r\nv 189.72 73.08 5.47\r\nv 188.85 74.04 9.65\r\nv 188.06 75.03 14.17\r\nv 187.4 76.05 18.83\r\nv 186.88 77.05 23.48\r\nv 186.57 78 27.93\r\nv 186.42 78.92 32.23\r\nv 186.44 79.78 36.41\r\nv 186.59 80.59 40.52\r\nv 186.85 81.35 44.6\r\nv 187.27 82.08 48.75\r\nv 187.87 82.81 53.05\r\nv 188.7 83.57 57.6\r\nv 189.78 84.39 62.48\r\nv 191.1 85.22 67.56\r\nv 192.62 86.02 72.7\r\nv 194.31 86.74 77.76\r\nv 196.17 87.35 82.61\r\nv 198.12 87.85 87.18\r\nv 200.13 88.28 91.41\r\nv 202.15 88.64 95.25\r\nv 204.12 88.96 98.64\r\nv 213.72 90.16 111.27\r\nv 214.51 90.24 112.13\r\nv 205.12 39.8 117.34\r\nv 203.3 39.64 115.14\r\nv 201.34 39.45 112.66\r\nv 199.27 39.24 109.84\r\nv 201.72 38.42 112.59\r\nv 199.66 38.21 109.77\r\nv 199.75 37.98 109.74\r\nv 201.81 38.19 112.56\r\nv 197.12 39 106.62\r\nv 194.92 38.73 102.96\r\nv 197.51 37.97 106.56\r\nv 195.31 37.7 102.9\r\nv 195.4 37.47 102.86\r\nv 197.6 37.74 106.52\r\nv 192.68 38.42 98.81\r\nv 190.47 38.12 94.24\r\nv 193.07 37.39 98.74\r\nv 190.85 37.09 94.16\r\nv 190.95 36.86 94.13\r\nv 193.16 37.16 98.71\r\nv 188.37 37.87 89.32\r\nv 186.45 37.72 84.12\r\nv 188.75 36.84 89.23\r\nv 186.82 36.68 84.01\r\nv 186.91 36.45 83.98\r\nv 188.84 36.61 89.2\r\nv 184.77 37.7 78.72\r\nv 183.34 37.78 73.25\r\nv 185.13 36.66 78.59\r\nv 183.7 36.75 73.11\r\nv 183.79 36.52 73.08\r\nv 185.22 36.44 78.56\r\nv 182.15 37.93 67.86\r\nv 181.2 38.1 62.69\r\nv 182.51 36.89 67.72\r\nv 181.55 37.07 62.54\r\nv 181.64 36.84 62.51\r\nv 182.6 36.67 67.69\r\nv 180.49 38.27 57.88\r\nv 179.99 38.45 53.28\r\nv 180.84 37.23 57.72\r\nv 180.34 37.41 53.11\r\nv 180.43 37.19 53.08\r\nv 180.93 37.01 57.69\r\nv 179.7 38.69 48.78\r\nv 179.6 39.01 44.22\r\nv 180.05 37.65 48.59\r\nv 179.95 37.98 44.02\r\nv 180.04 37.76 43.99\r\nv 180.14 37.43 48.56\r\nv 179.67 39.46 39.49\r\nv 179.94 40.01 34.56\r\nv 180.02 38.43 39.27\r\nv 180.28 38.98 34.33\r\nv 180.37 38.75 34.3\r\nv 180.11 38.2 39.24\r\nv 180.42 40.63 29.43\r\nv 181.16 41.3 24.07\r\nv 180.77 39.6 29.18\r\nv 181.5 40.27 23.82\r\nv 181.59 40.05 23.79\r\nv 180.86 39.37 29.15\r\nv 182.16 42 18.47\r\nv 182.51 40.98 18.22\r\nv 182.6 40.75 18.19\r\nv 187.26 42.88 2.83\r\nv 185.94 42.21 7.57\r\nv 186.21 41.57 7.47\r\nv 187.53 42.24 2.73\r\nv 184.64 41.51 12.84\r\nv 184.92 40.88 12.74\r\nv 183.46 40.8 18.38\r\nv 183.73 40.17 18.29\r\nv 182.46 40.11 23.95\r\nv 182.74 39.47 23.85\r\nv 181.73 39.44 29.28\r\nv 182.01 38.8 29.18\r\nv 181.25 38.82 34.38\r\nv 181.53 38.18 34.29\r\nv 180.99 38.28 39.29\r\nv 181.26 37.64 39.19\r\nv 180.92 37.83 44\r\nv 181.19 37.19 43.91\r\nv 181.02 37.51 48.54\r\nv 181.3 36.87 48.44\r\nv 181.31 37.27 53.02\r\nv 181.58 36.63 52.93\r\nv 181.8 37.09 57.59\r\nv 182.08 36.45 57.5\r\nv 182.51 36.93 62.38\r\nv 182.79 36.29 62.28\r\nv 183.46 36.76 67.52\r\nv 183.73 36.12 67.42\r\nv 184.64 36.61 72.88\r\nv 184.92 35.97 72.78\r\nv 186.07 36.53 78.32\r\nv 186.34 35.89 78.22\r\nv 187.74 36.54 83.7\r\nv 188.01 35.91 83.6\r\nv 189.65 36.7 88.87\r\nv 189.93 36.06 88.78\r\nv 191.74 36.95 93.77\r\nv 192.02 36.31 93.67\r\nv 193.93 37.25 98.31\r\nv 194.21 36.61 98.21\r\nv 196.15 37.55 102.42\r\nv 196.43 36.92 102.32\r\nv 198.33 37.82 106.04\r\nv 198.61 37.19 105.95\r\nv 200.46 38.06 109.22\r\nv 200.73 37.42 109.13\r\nv 202.5 38.27 112.01\r\nv 202.77 37.63 111.92\r\nv 204.44 38.45 114.46\r\nv 204.72 37.81 114.37\r\nv 206.27 38.61 116.62\r\nv 206.54 37.97 116.52\r\nv 207.71 38.73 118.26\r\nv 207.99 38.1 118.16\r\nv 208.52 38.8 119.14\r\nv 208.79 38.16 119.05\r\nv 208.81 38.83 119.6\r\nv 209.09 38.19 119.5\r\nv 208.71 38.84 119.94\r\nv 208.99 38.2 119.84\r\nv 208.47 38.85 120.17\r\nv 208.75 38.21 120.07\r\nv 208.35 38.87 120.28\r\nv 208.62 38.24 120.19\r\nv 208.45 38.25 120.1\r\nv 208.06 38.23 119.73\r\nv 207.25 38.16 118.83\r\nv 205.81 38.04 117.16\r\nv 203.98 37.88 114.96\r\nv 202.02 37.69 112.48\r\nv 199.96 37.48 109.66\r\nv 197.81 37.24 106.45\r\nv 195.61 36.97 102.79\r\nv 193.37 36.66 98.64\r\nv 191.16 36.36 94.06\r\nv 189.05 36.11 89.12\r\nv 187.12 35.95 83.91\r\nv 185.44 35.94 78.49\r\nv 184 36.02 73.01\r\nv 182.81 36.17 67.61\r\nv 181.86 36.34 62.43\r\nv 181.14 36.51 57.61\r\nv 180.65 36.69 53.01\r\nv 180.36 36.93 48.49\r\nv 180.25 37.26 43.92\r\nv 180.32 37.7 39.17\r\nv 180.59 38.25 34.22\r\nv 181.08 38.87 29.08\r\nv 181.81 39.55 23.72\r\nv 182.81 40.25 18.12\r\nv 184 40.96 12.54\r\nv 185.3 41.67 7.24\r\nv 186.63 42.34 2.47\r\nv 187.91 42.96 -1.51\r\nv 189.18 43.55 -4.88\r\nv 190.48 44.12 -7.83\r\nv 191.85 44.68 -10.53\r\nv 193.33 45.25 -13.17\r\nv 194.91 45.82 -15.75\r\nv 196.59 46.4 -18.3\r\nv 198.37 46.96 -20.83\r\nv 200.25 47.51 -23.34\r\nv 202.11 48.03 -25.72\r\nv 203.84 48.49 -27.87\r\nv 205.32 48.88 -29.69\r\nv 206.45 49.19 -31.12\r\nv 207.27 49.42 -32.16\r\nv 207.84 49.57 -32.81\r\nv 208.19 49.65 -33.14\r\nv 208.08 50.29 -33.12\r\nv 208.35 49.66 -33.21\r\nv 208.19 50.25 -32.98\r\nv 208.47 49.61 -33.07\r\nv 208.42 50.2 -32.65\r\nv 208.69 49.56 -32.75\r\nv 208.51 50.12 -32.23\r\nv 208.79 49.49 -32.33\r\nv 208.25 50.02 -31.79\r\nv 208.53 49.38 -31.89\r\nv 207.69 49.87 -31.19\r\nv 207.97 49.23 -31.29\r\nv 206.88 49.65 -30.28\r\nv 207.16 49.01 -30.37\r\nv 205.77 49.35 -28.98\r\nv 206.04 48.72 -29.07\r\nv 204.29 48.97 -27.2\r\nv 204.57 48.33 -27.3\r\nv 202.58 48.5 -25.07\r\nv 202.85 47.87 -25.17\r\nv 200.73 47.99 -22.7\r\nv 201 47.35 -22.8\r\nv 198.87 47.45 -20.21\r\nv 199.14 46.81 -20.31\r\nv 197.1 46.89 -17.71\r\nv 197.37 46.25 -17.8\r\nv 195.43 46.32 -15.18\r\nv 195.71 45.68 -15.28\r\nv 193.87 45.75 -12.62\r\nv 194.14 45.11 -12.72\r\nv 192.41 45.19 -10.02\r\nv 192.69 44.55 -10.11\r\nv 191.06 44.64 -7.35\r\nv 191.34 44 -7.45\r\nv 189.78 44.08 -4.45\r\nv 190.05 43.44 -4.54\r\nv 188.52 43.5 -1.11\r\nv 188.8 42.86 -1.21\r\nv 322.52 37.62 118.16\r\nv 322.68 37.46 118.34\r\nv 321.87 37.53 119.23\r\nv 321.71 37.68 119.05\r\nv 322.92 37.47 118.56\r\nv 322.11 37.54 119.46\r\nv 321.81 37.73 119.87\r\nv 321.57 37.72 119.64\r\nv 321.55 37.83 119.4\r\nv 315.96 90 111.98\r\nv 315.75 89.85 111.8\r\nv 316.54 89.77 110.96\r\nv 316.75 89.92 111.14\r\nv 315.49 89.68 111.57\r\nv 316.28 89.6 110.74\r\nv 317.27 89.5 109.65\r\nv 317.54 89.67 109.87\r\nv 317.75 89.81 110.04\r\nv 324.55 63.82 -27.35\r\nv 324.25 63.75 -27.15\r\nv 323.12 63.26 -28.67\r\nv 323.42 63.34 -28.86\r\nv 323.87 63.66 -26.91\r\nv 322.73 63.18 -28.42\r\nv 321.98 62.84 -29.54\r\nv 322.32 62.91 -29.76\r\nv 322.59 62.99 -29.95\r\nv 322.84 51.53 -32.56\r\nv 322.62 51.49 -32.24\r\nv 323.06 51.37 -31.79\r\nv 323.28 51.41 -32.11\r\nv 322.34 51.43 -31.83\r\nv 322.78 51.31 -31.38\r\nv 323.33 51.16 -30.78\r\nv 323.62 51.22 -31.17\r\nv 323.84 51.25 -31.48\r\nv 321.83 49.12 -32.38\r\nv 321.85 49.05 -32.71\r\nv 322.11 48.8 -32.39\r\nv 321.97 48.92 -32.06\r\nv 322.07 49.1 -33.03\r\nv 322.33 48.85 -32.71\r\nv 322.9 48.7 -32.07\r\nv 322.67 48.65 -31.77\r\nv 322.53 48.77 -31.45\r\nv 339.21 46.53 -9.95\r\nv 338.94 46.52 -9.81\r\nv 340.29 45.96 -7.14\r\nv 340.57 45.98 -7.27\r\nv 338.6 46.49 -9.64\r\nv 339.95 45.94 -6.99\r\nv 341.22 45.38 -4.09\r\nv 341.58 45.4 -4.24\r\nv 341.86 45.42 -4.35\r\nv 337.83 44.08 -10.23\r\nv 338.01 43.94 -10.38\r\nv 339.37 43.39 -7.7\r\nv 339.18 43.53 -7.56\r\nv 338.29 43.97 -10.52\r\nv 339.65 43.41 -7.83\r\nv 340.95 42.85 -4.9\r\nv 340.66 42.82 -4.78\r\nv 340.46 42.97 -4.65\r\nv 344.64 43.92 2.8\r\nv 344.51 43.58 2.7\r\nv 343.23 44.2 -1.28\r\nv 343.36 44.54 -1.17\r\nv 344.37 43.24 2.6\r\nv 343.09 43.86 -1.38\r\nv 341.82 44.45 -4.75\r\nv 341.96 44.79 -4.64\r\nv 342.09 45.13 -4.54\r\nv 322.68 63.41 -30.15\r\nv 322.57 63.71 -30.17\r\nv 323.45 64.02 -29.1\r\nv 323.56 63.7 -29.07\r\nv 322.38 64.11 -30.19\r\nv 323.25 64.47 -29.11\r\nv 324.38 64.96 -27.58\r\nv 324.58 64.51 -27.57\r\nv 324.69 64.18 -27.55\r\nv 324.55 89.41 101.57\r\nv 324.45 89.72 101.47\r\nv 322.6 89.97 104.08\r\nv 322.7 89.66 104.18\r\nv 324.25 90.15 101.36\r\nv 322.41 90.4 103.96\r\nv 320.65 90.62 106.25\r\nv 320.84 90.19 106.36\r\nv 320.94 89.88 106.47\r\nv 319.18 90.38 108.38\r\nv 319.27 90.07 108.49\r\nv 318.99 90.81 108.26\r\nv 317.59 90.96 109.88\r\nv 317.78 90.53 110\r\nv 317.87 90.22 110.11\r\nv 316.8 90.64 111.13\r\nv 316.89 90.33 111.24\r\nv 316.61 91.06 111\r\nv 315.84 91.14 111.88\r\nv 316.02 90.72 112\r\nv 316.11 90.41 112.11\r\nv 322.72 39.85 120.44\r\nv 322.59 39.51 120.42\r\nv 323.08 39.47 119.9\r\nv 323.22 39.81 119.92\r\nv 322.44 39.16 120.38\r\nv 322.94 39.13 119.86\r\nv 323.75 39.06 118.96\r\nv 323.9 39.4 118.99\r\nv 324.03 39.75 119.02\r\nv 340.13 37.95 94.24\r\nv 340 37.6 94.21\r\nv 342.1 37.35 89.28\r\nv 342.24 37.7 89.31\r\nv 339.85 37.26 94.18\r\nv 341.96 37.01 89.25\r\nv 343.89 36.85 84.03\r\nv 344.03 37.2 84.07\r\nv 344.16 37.54 84.11\r\nv 326.18 37.19 114.71\r\nv 326.31 37.21 114.81\r\nv 324.48 37.37 117.01\r\nv 324.36 37.35 116.9\r\nv 326.43 37.39 114.88\r\nv 324.6 37.55 117.07\r\nv 323.16 37.68 118.74\r\nv 323.04 37.49 118.67\r\nv 322.23 37.56 119.57\r\nv 322.35 37.74 119.64\r\nv 322.11 37.88 119.94\r\nv 321.93 37.75 119.99\r\nv 317.85 89.9 110.13\r\nv 316.85 90.01 111.23\r\nv 317.89 89.97 110.15\r\nv 316.89 90.08 111.26\r\nv 316.1 90.16 112.11\r\nv 316.06 90.09 112.07\r\nv 314.97 92.14 111.58\r\nv 315.09 92.19 111.69\r\nv 315.86 92.11 110.82\r\nv 315.74 92.06 110.72\r\nv 315.23 92.06 111.75\r\nv 316 91.98 110.88\r\nv 316.98 91.88 109.76\r\nv 316.83 92 109.7\r\nv 316.71 91.95 109.6\r\nv 318.23 91.85 108.08\r\nv 318.11 91.8 107.99\r\nv 318.38 91.72 108.14\r\nv 320.03 91.53 106.13\r\nv 319.88 91.66 106.08\r\nv 319.76 91.61 105.99\r\nv 323.96 51.25 -31.62\r\nv 323.4 51.41 -32.26\r\nv 324.02 51.22 -31.68\r\nv 323.45 51.38 -32.32\r\nv 323.01 51.5 -32.78\r\nv 322.96 51.53 -32.72\r\nv 322.19 49.15 -33.17\r\nv 322.45 48.9 -32.85\r\nv 322.37 49.27 -33.09\r\nv 322.57 49.09 -32.87\r\nv 323.14 48.93 -32.22\r\nv 323.02 48.74 -32.2\r\nv 325.78 50.73 -29.06\r\nv 325.9 50.73 -29.17\r\nv 324.78 51.03 -30.59\r\nv 324.66 51.03 -30.46\r\nv 325.96 50.69 -29.22\r\nv 324.83 51 -30.64\r\nv 325.96 50.46 -29.33\r\nv 325.99 50.63 -29.26\r\nv 327.47 50.23 -27.44\r\nv 327.44 50.07 -27.51\r\nv 327.44 50.3 -27.4\r\nv 329.16 49.84 -25.26\r\nv 329.2 49.77 -25.3\r\nv 329.17 49.6 -25.36\r\nv 328.89 48.93 -25.59\r\nv 328.83 48.75 -25.63\r\nv 327.1 49.22 -27.77\r\nv 327.16 49.39 -27.73\r\nv 328.74 48.53 -25.65\r\nv 327.01 48.99 -27.79\r\nv 325.53 49.39 -29.62\r\nv 325.61 49.61 -29.59\r\nv 325.68 49.78 -29.56\r\nv 324.49 49.92 -31.02\r\nv 324.55 50.09 -30.99\r\nv 324.4 49.69 -31.05\r\nv 323.58 49.92 -32.09\r\nv 323.66 50.14 -32.06\r\nv 323.73 50.32 -32.02\r\nv 323 51.26 -32.89\r\nv 323.04 51.43 -32.82\r\nv 323.48 51.31 -32.37\r\nv 323.45 51.14 -32.43\r\nv 324.05 51.15 -31.72\r\nv 324.01 50.99 -31.79\r\nv 323.09 50.3 -32.71\r\nv 323.16 50.47 -32.68\r\nv 323.01 50.07 -32.74\r\nv 322.61 50.34 -33.16\r\nv 322.65 50.42 -33.17\r\nv 322.72 50.59 -33.13\r\nv 324.73 64.02 -27.53\r\nv 323.6 63.53 -29.05\r\nv 324.72 63.94 -27.5\r\nv 323.6 63.45 -29.02\r\nv 322.74 63.12 -30.09\r\nv 322.73 63.22 -30.12\r\nv 322.07 64.61 -30.19\r\nv 322.93 64.99 -29.11\r\nv 321.77 65.02 -30.17\r\nv 322.62 65.41 -29.09\r\nv 323.76 65.9 -27.56\r\nv 324.06 65.48 -27.58\r\nv 325.41 66.05 -25.65\r\nv 325.73 65.53 -25.65\r\nv 325.1 66.47 -25.62\r\nv 326.64 67.11 -23.37\r\nv 326.94 66.69 -23.39\r\nv 327.26 66.17 -23.4\r\nv 328.64 67.4 -20.88\r\nv 328.96 66.88 -20.88\r\nv 328.33 67.82 -20.86\r\nv 330.06 68.57 -18.22\r\nv 330.36 68.15 -18.24\r\nv 330.69 67.63 -18.24\r\nv 331.98 68.91 -15.58\r\nv 332.3 68.39 -15.57\r\nv 331.67 69.33 -15.56\r\nv 333.17 70.1 -12.87\r\nv 333.47 69.68 -12.89\r\nv 333.8 69.17 -12.89\r\nv 334.86 70.46 -10.17\r\nv 335.19 69.94 -10.16\r\nv 334.55 70.88 -10.15\r\nv 335.82 71.66 -7.39\r\nv 336.13 71.24 -7.4\r\nv 336.45 70.72 -7.39\r\nv 337.28 72.02 -4.58\r\nv 337.61 71.5 -4.57\r\nv 336.97 72.43 -4.57\r\nv 338.02 73.23 -1.59\r\nv 338.33 72.81 -1.6\r\nv 338.66 72.29 -1.58\r\nv 339.3 73.64 1.67\r\nv 339.63 73.12 1.69\r\nv 338.99 74.05 1.68\r\nv 339.9 74.94 5.34\r\nv 340.21 74.52 5.34\r\nv 340.54 74 5.35\r\nv 341.08 75.47 9.51\r\nv 341.41 74.96 9.53\r\nv 340.77 75.89 9.51\r\nv 341.55 76.88 14.01\r\nv 341.86 76.47 14.02\r\nv 342.2 75.96 14.04\r\nv 342.53 77.48 18.67\r\nv 342.86 76.97 18.7\r\nv 342.22 77.9 18.67\r\nv 342.73 78.89 23.31\r\nv 343.04 78.48 23.31\r\nv 343.37 77.97 23.34\r\nv 343.36 79.43 27.75\r\nv 343.69 78.92 27.78\r\nv 343.04 79.85 27.74\r\nv 343.19 80.76 32.03\r\nv 343.5 80.34 32.04\r\nv 343.83 79.83 32.07\r\nv 343.49 81.21 36.21\r\nv 343.82 80.69 36.24\r\nv 343.17 81.62 36.2\r\nv 343.03 82.43 40.31\r\nv 343.34 82.02 40.32\r\nv 343.67 81.51 40.35\r\nv 343.08 82.78 44.4\r\nv 343.41 82.26 44.43\r\nv 342.76 83.19 44.39\r\nv 342.34 83.92 48.52\r\nv 342.66 83.51 48.54\r\nv 342.99 82.99 48.58\r\nv 342.06 84.24 52.83\r\nv 342.39 83.72 52.87\r\nv 341.74 84.65 52.81\r\nv 340.92 85.41 57.35\r\nv 341.23 85 57.37\r\nv 341.56 84.48 57.41\r\nv 340.15 85.81 62.24\r\nv 340.48 85.3 62.28\r\nv 339.84 86.22 62.22\r\nv 338.53 87.05 67.29\r\nv 338.84 86.64 67.31\r\nv 339.17 86.13 67.36\r\nv 337.32 87.44 72.45\r\nv 337.65 86.93 72.49\r\nv 337.01 87.85 72.42\r\nv 335.31 88.57 77.48\r\nv 335.63 88.16 77.51\r\nv 335.95 87.65 77.56\r\nv 333.78 88.77 82.35\r\nv 334.1 88.26 82.4\r\nv 333.47 89.18 82.32\r\nv 331.52 89.68 86.88\r\nv 331.83 89.27 86.92\r\nv 332.15 88.76 86.97\r\nv 329.82 89.7 91.14\r\nv 330.15 89.19 91.19\r\nv 329.51 90.11 91.11\r\nv 327.5 90.47 94.93\r\nv 327.81 90.06 94.97\r\nv 328.13 89.55 95.02\r\nv 325.84 90.38 98.34\r\nv 326.16 89.87 98.4\r\nv 325.54 90.79 98.3\r\nv 323.63 91.07 101.26\r\nv 323.93 90.66 101.3\r\nv 320.97 89.71 106.52\r\nv 322.74 89.49 104.22\r\nv 320.96 89.63 106.52\r\nv 322.73 89.41 104.22\r\nv 324.58 89.16 101.61\r\nv 324.59 89.25 101.61\r\nv 322.09 90.91 103.9\r\nv 321.79 91.32 103.85\r\nv 320.33 91.12 106.18\r\nv 317.9 90.06 110.16\r\nv 319.31 89.9 108.53\r\nv 319.3 89.82 108.53\r\nv 318.68 91.31 108.19\r\nv 317.28 91.47 109.81\r\nv 316.3 91.57 110.93\r\nv 315.53 91.65 111.8\r\nv 324.07 39.92 119.01\r\nv 323.25 39.99 119.91\r\nv 324.04 39.99 118.98\r\nv 323.22 40.06 119.88\r\nv 322.72 40.1 120.39\r\nv 322.76 40.03 120.43\r\nv 322.37 38.99 120.36\r\nv 322.87 38.95 119.84\r\nv 322.34 38.92 120.33\r\nv 322.78 38.73 119.8\r\nv 323.6 38.66 118.9\r\nv 323.68 38.89 118.94\r\nv 325.13 38.76 117.27\r\nv 325.19 38.94 117.29\r\nv 325.04 38.54 117.23\r\nv 326.87 38.38 115.04\r\nv 326.95 38.6 115.07\r\nv 327.02 38.77 115.09\r\nv 347.02 42.84 12.98\r\nv 345.72 43.55 7.69\r\nv 345.42 43.54 7.77\r\nv 346.71 42.84 13.05\r\nv 344.39 44.22 2.95\r\nv 344.1 44.21 3.04\r\nv 343.73 44.19 3.15\r\nv 345.05 43.53 7.87\r\nv 346.34 42.83 13.13\r\nv 345.61 40.4 12.65\r\nv 344.31 41.1 7.37\r\nv 344.52 40.95 7.28\r\nv 345.82 40.25 12.56\r\nv 342.99 41.77 2.63\r\nv 343.19 41.62 2.53\r\nv 343.49 41.64 2.44\r\nv 344.82 40.97 7.2\r\nv 346.12 40.27 12.5\r\nv 349.2 41.43 24.13\r\nv 348.2 42.13 18.54\r\nv 347.9 42.13 18.6\r\nv 348.9 41.43 24.18\r\nv 347.52 42.12 18.67\r\nv 348.52 41.42 24.23\r\nv 347.79 38.99 23.77\r\nv 346.79 39.69 18.2\r\nv 347 39.54 18.12\r\nv 348 38.84 23.7\r\nv 347.31 39.56 18.06\r\nv 348.31 38.85 23.65\r\nv 350.42 40.14 34.6\r\nv 349.94 40.76 29.47\r\nv 349.63 40.76 29.51\r\nv 350.11 40.14 34.62\r\nv 349.25 40.75 29.55\r\nv 349.73 40.14 34.65\r\nv 349 37.71 34.21\r\nv 348.51 38.32 29.1\r\nv 348.73 38.17 29.04\r\nv 349.22 37.55 34.16\r\nv 349.04 38.18 29.01\r\nv 349.52 37.56 34.14\r\nv 350.76 39.15 44.23\r\nv 350.69 39.6 39.51\r\nv 350.38 39.6 39.52\r\nv 350.45 39.16 44.23\r\nv 350 39.6 39.54\r\nv 350.07 39.16 44.23\r\nv 349.33 36.72 43.83\r\nv 349.26 37.16 39.12\r\nv 349.48 37.01 39.08\r\nv 349.55 36.56 43.81\r\nv 349.79 37.01 39.07\r\nv 349.86 36.57 43.81\r\nv 350.37 38.59 53.27\r\nv 350.66 38.83 48.77\r\nv 350.35 38.83 48.76\r\nv 350.06 38.6 53.24\r\nv 349.96 38.83 48.74\r\nv 349.68 38.6 53.21\r\nv 348.94 36.16 52.86\r\nv 349.23 36.39 48.37\r\nv 349.45 36.23 48.36\r\nv 349.16 36 52.86\r\nv 349.76 36.24 48.38\r\nv 349.47 36 52.89\r\nv 349.16 38.24 62.65\r\nv 349.87 38.41 57.85\r\nv 349.57 38.41 57.81\r\nv 348.86 38.25 62.6\r\nv 349.19 38.42 57.76\r\nv 348.48 38.26 62.54\r\nv 347.74 35.81 62.22\r\nv 348.45 35.97 57.43\r\nv 348.66 35.81 57.45\r\nv 347.95 35.65 62.25\r\nv 348.97 35.82 57.48\r\nv 348.26 35.66 62.3\r\nv 347.03 37.92 73.19\r\nv 348.21 38.07 67.81\r\nv 347.91 38.08 67.75\r\nv 346.73 37.93 73.11\r\nv 347.53 38.08 67.68\r\nv 346.35 37.94 73.02\r\nv 345.61 35.49 72.73\r\nv 346.79 35.64 67.37\r\nv 347 35.48 67.4\r\nv 345.82 35.33 72.78\r\nv 347.31 35.48 67.46\r\nv 346.12 35.34 72.85\r\nv 343.93 37.86 84.03\r\nv 345.6 37.84 78.64\r\nv 345.3 37.85 78.55\r\nv 343.64 37.87 83.93\r\nv 344.94 37.85 78.45\r\nv 343.27 37.87 83.8\r\nv 342.51 35.43 83.56\r\nv 344.18 35.41 78.18\r\nv 344.39 35.25 78.23\r\nv 342.71 35.27 83.63\r\nv 344.68 35.25 78.32\r\nv 343 35.27 83.73\r\nv 339.91 38.26 94.12\r\nv 342.01 38.01 89.21\r\nv 341.72 38.02 89.1\r\nv 339.63 38.27 93.99\r\nv 341.37 38.03 88.95\r\nv 339.29 38.27 93.83\r\nv 338.5 35.83 93.64\r\nv 340.59 35.58 88.74\r\nv 340.79 35.42 88.82\r\nv 338.69 35.67 93.73\r\nv 341.08 35.42 88.94\r\nv 338.97 35.68 93.86\r\nv 335.48 38.87 102.82\r\nv 337.71 38.56 98.68\r\nv 337.44 38.57 98.53\r\nv 335.21 38.88 102.66\r\nv 337.1 38.57 98.36\r\nv 334.88 38.88 102.47\r\nv 334.08 36.44 102.3\r\nv 336.31 36.13 98.18\r\nv 336.49 35.97 98.29\r\nv 334.26 36.28 102.42\r\nv 336.77 35.98 98.43\r\nv 334.53 36.28 102.57\r\nv 331.14 39.38 109.67\r\nv 333.28 39.14 106.46\r\nv 333.02 39.15 106.3\r\nv 330.89 39.39 109.49\r\nv 332.7 39.15 106.09\r\nv 330.58 39.39 109.27\r\nv 329.78 36.95 109.11\r\nv 331.9 36.71 105.93\r\nv 332.07 36.55 106.06\r\nv 329.94 36.79 109.25\r\nv 332.33 36.56 106.22\r\nv 330.2 36.79 109.43\r\nv 327.13 39.77 114.96\r\nv 329.08 39.59 112.49\r\nv 328.84 39.59 112.3\r\nv 326.89 39.78 114.76\r\nv 328.53 39.59 112.07\r\nv 326.59 39.78 114.52\r\nv 325.79 37.34 114.36\r\nv 327.73 37.15 111.9\r\nv 327.89 36.99 112.05\r\nv 325.94 37.18 114.51\r\nv 328.14 37 112.24\r\nv 323.86 40.06 118.81\r\nv 325.3 39.93 117.15\r\nv 325.06 39.94 116.94\r\nv 323.62 40.06 118.59\r\nv 324.76 39.94 116.68\r\nv 323.32 40.06 118.32\r\nv 323.96 37.5 116.52\r\nv 324.12 37.34 116.69\r\nv 322.53 40.16 120.2\r\nv 323.04 40.12 119.7\r\nv 322.8 40.13 119.47\r\nv 322.3 40.17 119.98\r\nv 322.51 40.12 119.2\r\nv 322.02 40.16 119.71\r\nv 316.23 91.66 109.19\r\nv 315.25 91.76 110.28\r\nv 315.52 91.93 110.52\r\nv 316.5 91.82 109.42\r\nv 314.46 91.84 111.11\r\nv 314.74 92.01 111.37\r\nv 320.81 89.47 106.41\r\nv 319.15 89.66 108.42\r\nv 318.94 89.52 108.25\r\nv 320.59 89.33 106.25\r\nv 318.67 89.35 108.04\r\nv 320.32 89.16 106.04\r\nv 319.26 91.31 105.61\r\nv 317.62 91.5 107.6\r\nv 317.89 91.67 107.81\r\nv 319.53 91.48 105.82\r\nv 324.42 89 101.52\r\nv 322.58 89.25 104.13\r\nv 322.35 89.11 103.97\r\nv 324.19 88.86 101.37\r\nv 322.06 88.94 103.78\r\nv 323.9 88.7 101.19\r\nv 322.81 90.86 100.79\r\nv 320.99 91.1 103.36\r\nv 321.28 91.27 103.55\r\nv 323.1 91.02 100.97\r\nv 321.51 91.39 103.71\r\nv 323.34 91.15 101.12\r\nv 328.31 88.4 95.18\r\nv 326.34 88.72 98.55\r\nv 326.1 88.58 98.42\r\nv 328.06 88.26 95.05\r\nv 325.8 88.42 98.25\r\nv 327.76 88.1 94.89\r\nv 326.65 90.26 94.53\r\nv 324.7 90.58 97.86\r\nv 325 90.74 98.03\r\nv 326.96 90.43 94.69\r\nv 325.25 90.87 98.17\r\nv 327.21 90.55 94.82\r\nv 332.33 87.61 87.12\r\nv 330.32 88.04 91.35\r\nv 330.07 87.9 91.23\r\nv 332.07 87.48 87.02\r\nv 329.76 87.74 91.09\r\nv 331.75 87.32 86.89\r\nv 330.63 89.48 86.55\r\nv 328.64 89.9 90.74\r\nv 328.96 90.06 90.88\r\nv 330.95 89.64 86.68\r\nv 329.21 90.19 91\r\nv 331.21 89.76 86.79\r\nv 336.13 86.5 77.73\r\nv 334.28 87.11 82.56\r\nv 334.02 86.97 82.47\r\nv 335.86 86.37 77.65\r\nv 333.69 86.81 82.36\r\nv 335.52 86.21 77.55\r\nv 334.4 88.38 77.22\r\nv 332.56 88.98 82.02\r\nv 332.89 89.14 82.14\r\nv 334.73 88.53 77.32\r\nv 333.16 89.26 82.23\r\nv 335 88.65 77.4\r\nv 339.34 84.98 67.54\r\nv 337.82 85.78 72.67\r\nv 337.55 85.65 72.61\r\nv 339.06 84.85 67.49\r\nv 337.21 85.49 72.52\r\nv 338.72 84.7 67.42\r\nv 337.59 86.86 67.1\r\nv 336.08 87.66 72.2\r\nv 336.42 87.81 72.28\r\nv 337.93 87.02 67.17\r\nv 336.7 87.93 72.35\r\nv 338.21 87.14 67.23\r\nv 341.73 83.34 57.6\r\nv 340.65 84.15 62.47\r\nv 340.37 84.03 62.43\r\nv 341.45 83.21 57.56\r\nv 340.03 83.87 62.37\r\nv 341.1 83.06 57.52\r\nv 339.96 85.23 57.22\r\nv 338.89 86.04 62.06\r\nv 339.24 86.19 62.12\r\nv 340.31 85.38 57.27\r\nv 339.52 86.31 62.16\r\nv 340.6 85.5 57.3\r\nv 343.16 81.85 48.77\r\nv 342.56 82.58 53.06\r\nv 342.27 82.45 53.04\r\nv 342.88 81.73 48.75\r\nv 341.92 82.31 53\r\nv 342.52 81.58 48.74\r\nv 341.38 83.75 48.47\r\nv 340.78 84.48 52.72\r\nv 341.13 84.63 52.75\r\nv 341.73 83.9 48.48\r\nv 341.42 84.74 52.78\r\nv 342.02 84.01 48.5\r\nv 343.84 80.36 40.55\r\nv 343.58 81.12 44.63\r\nv 343.29 81 44.63\r\nv 343.56 80.24 40.56\r\nv 342.94 80.85 44.62\r\nv 343.2 80.1 40.57\r\nv 342.06 82.28 40.31\r\nv 341.79 83.03 44.36\r\nv 342.15 83.17 44.37\r\nv 342.41 82.42 40.31\r\nv 342.44 83.28 44.37\r\nv 342.7 82.52 40.3\r\nv 344 78.69 32.28\r\nv 343.99 79.55 36.45\r\nv 343.71 79.43 36.47\r\nv 343.72 78.57 32.31\r\nv 343.35 79.3 36.49\r\nv 343.36 78.44 32.34\r\nv 342.21 80.61 32.1\r\nv 342.2 81.47 36.24\r\nv 342.56 81.61 36.22\r\nv 342.57 80.75 32.06\r\nv 342.85 81.71 36.2\r\nv 342.86 80.85 32.04\r\nv 343.54 76.82 23.55\r\nv 343.86 77.78 27.99\r\nv 343.58 77.67 28.03\r\nv 343.26 76.71 23.6\r\nv 343.22 77.53 28.08\r\nv 342.91 76.58 23.66\r\nv 341.76 78.76 23.44\r\nv 342.07 79.71 27.84\r\nv 342.43 79.84 27.8\r\nv 342.11 78.89 23.38\r\nv 342.72 79.95 27.76\r\nv 342.4 78.99 23.33\r\nv 342.37 74.82 14.25\r\nv 343.03 75.83 18.91\r\nv 342.75 75.72 18.97\r\nv 342.09 74.71 14.32\r\nv 342.4 75.59 19.04\r\nv 341.74 74.58 14.4\r\nv 340.59 76.77 14.21\r\nv 341.25 77.77 18.83\r\nv 341.61 77.9 18.76\r\nv 340.94 76.89 14.12\r\nv 341.89 78 18.71\r\nv 341.23 76.99 14.06\r\nv 340.72 72.87 5.57\r\nv 341.58 73.82 9.74\r\nv 341.3 73.71 9.82\r\nv 340.44 72.76 5.66\r\nv 340.96 73.59 9.92\r\nv 340.1 72.64 5.77\r\nv 338.95 74.83 5.59\r\nv 339.81 75.78 9.73\r\nv 340.16 75.9 9.64\r\nv 339.3 74.95 5.49\r\nv 340.45 75.99 9.56\r\nv 339.58 75.04 5.4\r\nv 338.84 71.16 -1.35\r\nv 339.81 71.99 1.91\r\nv 339.54 71.88 2.01\r\nv 338.57 71.06 -1.24\r\nv 339.19 71.77 2.14\r\nv 338.23 70.95 -1.1\r\nv 337.09 73.14 -1.26\r\nv 338.05 73.96 1.97\r\nv 338.39 74.08 1.84\r\nv 337.43 73.25 -1.4\r\nv 338.67 74.16 1.74\r\nv 337.71 73.34 -1.51\r\nv 336.64 69.59 -7.16\r\nv 337.79 70.37 -4.34\r\nv 337.52 70.27 -4.21\r\nv 336.38 69.5 -7.02\r\nv 337.19 70.16 -4.05\r\nv 336.05 69.39 -6.85\r\nv 334.92 71.59 -6.99\r\nv 336.05 72.36 -4.21\r\nv 336.39 72.47 -4.36\r\nv 335.24 71.7 -7.16\r\nv 336.66 72.55 -4.49\r\nv 335.51 71.78 -7.29\r\nv 333.98 68.03 -12.65\r\nv 335.37 68.81 -9.92\r\nv 335.11 68.72 -9.78\r\nv 333.73 67.95 -12.49\r\nv 334.79 68.62 -9.59\r\nv 333.42 67.85 -12.3\r\nv 332.3 70.05 -12.41\r\nv 333.67 70.82 -9.72\r\nv 333.99 70.92 -9.9\r\nv 332.61 70.15 -12.6\r\nv 334.25 71 -10.05\r\nv 332.87 70.23 -12.76\r\nv 330.86 66.5 -18.01\r\nv 332.48 67.26 -15.34\r\nv 332.23 67.18 -15.17\r\nv 330.62 66.41 -17.83\r\nv 331.93 67.08 -14.97\r\nv 330.32 66.32 -17.62\r\nv 329.22 68.54 -17.7\r\nv 330.81 69.29 -15.06\r\nv 331.12 69.39 -15.27\r\nv 329.52 68.63 -17.91\r\nv 331.37 69.46 -15.44\r\nv 329.77 68.7 -18.09\r\nv 327.44 65.04 -23.17\r\nv 329.14 65.75 -20.66\r\nv 328.9 65.67 -20.48\r\nv 327.19 64.96 -22.98\r\nv 328.6 65.57 -20.25\r\nv 326.88 64.87 -22.75\r\nv 325.79 67.09 -22.81\r\nv 327.51 67.79 -20.32\r\nv 327.8 67.88 -20.55\r\nv 326.1 67.18 -23.04\r\nv 328.05 67.95 -20.73\r\nv 326.35 67.24 -23.23\r\nv 325.9 64.39 -25.42\r\nv 325.62 64.31 -25.23\r\nv 325.28 64.23 -25\r\nv 322.78 65.88 -26.98\r\nv 324.19 66.45 -25.06\r\nv 324.53 66.53 -25.29\r\nv 323.16 65.96 -27.22\r\nv 324.81 66.6 -25.48\r\nv 323.46 66.03 -27.41\r\nv 320.89 65.05 -29.63\r\nv 321.64 65.39 -28.5\r\nv 322.02 65.48 -28.74\r\nv 321.22 65.12 -29.85\r\nv 322.32 65.54 -28.94\r\nv 321.49 65.17 -30.03\r\nv 321.86 62.76 -30.95\r\nv 322.13 62.83 -30.56\r\nv 321.96 62.76 -30.43\r\nv 321.82 62.7 -30.89\r\nv 321.74 62.7 -30.28\r\nv 321.77 62.66 -30.83\r\nv 320.86 64.94 -30.53\r\nv 320.64 64.9 -30.38\r\nv 321.04 64.96 -30.65\r\nv 320.78 64.83 -31.04\r\nv 320.71 64.85 -31\r\nv 320.64 64.86 -30.94\r\nv 324.43 51 -30.2\r\nv 325.54 50.7 -28.85\r\nv 324.14 50.95 -29.88\r\nv 325.25 50.65 -28.59\r\nv 324.46 48.25 -29.21\r\nv 323.34 48.55 -30.53\r\nv 323.49 48.43 -30.79\r\nv 324.61 48.12 -29.43\r\nv 323.72 48.47 -31.05\r\nv 324.85 48.17 -29.64\r\nv 328.98 49.87 -25.11\r\nv 327.26 50.34 -27.24\r\nv 327.01 50.31 -27.05\r\nv 328.74 49.85 -24.92\r\nv 326.72 50.27 -26.82\r\nv 328.43 49.8 -24.69\r\nv 327.65 47.4 -25.3\r\nv 325.93 47.86 -27.43\r\nv 326.09 47.73 -27.63\r\nv 327.81 47.27 -25.5\r\nv 326.33 47.78 -27.82\r\nv 328.06 47.31 -25.68\r\nv 332.71 48.81 -20.22\r\nv 330.84 49.36 -22.73\r\nv 330.59 49.33 -22.54\r\nv 332.46 48.78 -20.04\r\nv 330.28 49.29 -22.32\r\nv 332.15 48.74 -19.82\r\nv 331.37 46.34 -20.43\r\nv 329.5 46.89 -22.93\r\nv 329.66 46.76 -23.12\r\nv 331.53 46.21 -20.62\r\nv 329.91 46.8 -23.3\r\nv 331.78 46.25 -20.8\r\nv 336.17 47.67 -15.15\r\nv 334.49 48.24 -17.7\r\nv 334.23 48.22 -17.53\r\nv 335.91 47.65 -14.99\r\nv 333.91 48.19 -17.32\r\nv 335.58 47.62 -14.79\r\nv 334.8 45.21 -15.4\r\nv 333.13 45.78 -17.93\r\nv 333.3 45.65 -18.11\r\nv 334.98 45.08 -15.57\r\nv 333.56 45.68 -18.28\r\nv 335.24 45.11 -15.73\r\nv 337.75 47.1 -12.58\r\nv 337.48 47.08 -12.42\r\nv 337.14 47.05 -12.24\r\nv 336.37 44.64 -12.84\r\nv 336.55 44.51 -13\r\nv 336.82 44.54 -13.15\r\nv 343.12 44.83 -1.01\r\nv 342.83 44.83 -0.91\r\nv 342.47 44.81 -0.78\r\nv 341.72 42.39 -1.31\r\nv 341.92 42.24 -1.43\r\nv 342.21 42.26 -1.53\r\nv 345.96 43.25 7.56\r\nv 345.84 42.91 7.47\r\nv 347.26 42.55 12.86\r\nv 347.14 42.2 12.77\r\nv 347 41.86 12.68\r\nv 345.7 42.57 7.38\r\nv 339.43 46.25 -10.17\r\nv 340.8 45.69 -7.47\r\nv 340.67 45.35 -7.58\r\nv 339.3 45.91 -10.28\r\nv 340.52 45.01 -7.69\r\nv 339.15 45.58 -10.39\r\nv 336.37 47.4 -15.39\r\nv 337.96 46.82 -12.8\r\nv 337.82 46.48 -12.92\r\nv 336.24 47.06 -15.51\r\nv 337.68 46.15 -13.03\r\nv 336.1 46.72 -15.62\r\nv 332.91 48.53 -20.47\r\nv 334.69 47.97 -17.94\r\nv 334.56 47.63 -18.06\r\nv 332.77 48.2 -20.58\r\nv 334.41 47.29 -18.17\r\nv 332.63 47.86 -20.7\r\nv 331.03 49.08 -22.98\r\nv 330.9 48.75 -23.1\r\nv 329.04 49.26 -25.48\r\nv 330.75 48.41 -23.21\r\nv 327.31 49.73 -27.62\r\nv 325.82 50.12 -29.45\r\nv 324.83 50.76 -30.75\r\nv 324.7 50.43 -30.87\r\nv 323.88 50.65 -31.91\r\nv 323.31 50.81 -32.56\r\nv 322.87 50.93 -33.02\r\nv 322.06 63.44 -30.73\r\nv 321.99 63.65 -30.75\r\nv 321.59 63.63 -31.07\r\nv 321.55 63.73 -31.08\r\nv 321.47 63.85 -31.08\r\nv 321.85 63.93 -30.76\r\nv 327.57 65.4 -23.36\r\nv 326.03 64.75 -25.62\r\nv 325.93 65.08 -25.64\r\nv 327.46 65.73 -23.38\r\nv 331 66.86 -18.2\r\nv 329.27 66.11 -20.85\r\nv 329.16 66.44 -20.87\r\nv 330.89 67.19 -18.22\r\nv 334.12 68.4 -12.83\r\nv 332.62 67.63 -15.53\r\nv 332.5 67.95 -15.55\r\nv 334.01 68.72 -12.86\r\nv 336.78 69.96 -7.32\r\nv 335.51 69.18 -10.1\r\nv 335.39 69.5 -10.13\r\nv 336.67 70.28 -7.36\r\nv 338.99 71.53 -1.5\r\nv 337.94 70.74 -4.49\r\nv 337.82 71.06 -4.54\r\nv 338.87 71.86 -1.54\r\nv 340.88 73.25 5.45\r\nv 339.97 72.36 1.78\r\nv 339.85 72.69 1.73\r\nv 340.76 73.57 5.4\r\nv 342.53 75.2 14.14\r\nv 341.74 74.2 9.62\r\nv 341.63 74.52 9.58\r\nv 342.41 75.52 14.08\r\nv 343.71 77.21 23.45\r\nv 343.2 76.21 18.8\r\nv 343.08 76.53 18.75\r\nv 343.59 77.53 23.39\r\nv 344.17 79.08 32.19\r\nv 344.02 78.17 27.9\r\nv 343.91 78.49 27.84\r\nv 344.05 79.4 32.13\r\nv 344.01 80.76 40.49\r\nv 344.15 79.94 36.38\r\nv 344.04 80.26 36.31\r\nv 343.89 81.07 40.42\r\nv 343.32 82.25 48.72\r\nv 343.74 81.51 44.57\r\nv 343.62 81.83 44.5\r\nv 343.2 82.56 48.65\r\nv 341.89 83.74 57.56\r\nv 342.72 82.98 53.02\r\nv 342.6 83.29 52.94\r\nv 341.78 84.05 57.49\r\nv 339.5 85.38 67.52\r\nv 340.81 84.56 62.44\r\nv 340.69 84.87 62.36\r\nv 339.38 85.7 67.44\r\nv 336.28 86.91 77.73\r\nv 337.98 86.18 72.66\r\nv 337.86 86.5 72.58\r\nv 336.16 87.22 77.64\r\nv 332.47 88.02 87.14\r\nv 334.43 87.51 82.57\r\nv 334.31 87.83 82.49\r\nv 332.36 88.33 87.06\r\nv 328.45 88.81 95.21\r\nv 330.47 88.45 91.38\r\nv 330.35 88.76 91.29\r\nv 328.34 89.12 95.12\r\nv 326.47 89.13 98.6\r\nv 326.37 89.44 98.5\r\nv 327.31 39.46 115.15\r\nv 325.48 39.62 117.35\r\nv 325.34 39.28 117.32\r\nv 327.17 39.12 115.13\r\nv 331.33 39.07 109.84\r\nv 329.27 39.28 112.67\r\nv 329.13 38.93 112.64\r\nv 331.2 38.72 109.82\r\nv 328.98 38.59 112.61\r\nv 331.05 38.38 109.79\r\nv 335.68 38.56 102.97\r\nv 333.48 38.83 106.63\r\nv 333.34 38.49 106.6\r\nv 335.55 38.21 102.94\r\nv 333.19 38.14 106.57\r\nv 335.4 37.87 102.91\r\nv 337.93 38.25 98.81\r\nv 337.79 37.9 98.79\r\nv 337.64 37.56 98.76\r\nv 347.27 37.61 73.24\r\nv 345.84 37.53 78.71\r\nv 345.71 37.18 78.66\r\nv 347.15 37.26 73.19\r\nv 345.57 36.84 78.61\r\nv 347 36.92 73.13\r\nv 349.41 37.93 62.67\r\nv 348.46 37.76 67.85\r\nv 348.34 37.41 67.79\r\nv 349.29 37.59 62.62\r\nv 348.2 37.07 67.74\r\nv 349.15 37.24 62.56\r\nv 350.62 38.28 53.26\r\nv 350.13 38.09 57.86\r\nv 350 37.75 57.8\r\nv 350.5 37.93 53.2\r\nv 349.86 37.4 57.74\r\nv 350.36 37.59 53.14\r\nv 351.01 38.84 44.19\r\nv 350.91 38.51 48.75\r\nv 350.79 38.17 48.68\r\nv 350.89 38.5 44.12\r\nv 350.65 37.83 48.62\r\nv 350.75 38.15 44.05\r\nv 350.68 39.83 34.53\r\nv 350.94 39.29 39.46\r\nv 350.82 38.95 39.38\r\nv 350.55 39.49 34.44\r\nv 350.68 38.6 39.3\r\nv 350.42 39.15 34.36\r\nv 349.46 41.13 24.03\r\nv 350.19 40.45 29.39\r\nv 350.07 40.11 29.3\r\nv 349.33 40.79 23.94\r\nv 349.93 39.77 29.22\r\nv 349.2 40.44 23.85\r\nv 348.46 41.83 18.43\r\nv 348.33 41.49 18.34\r\nv 348.19 41.15 18.26\r\nv 347.17 42.83 12.94\r\nv 345.87 43.53 7.65\r\nv 347.24 42.79 12.92\r\nv 345.94 43.49 7.63\r\nv 344.62 44.16 2.87\r\nv 344.55 44.2 2.9\r\nv 343.64 41.67 2.39\r\nv 344.97 41 7.16\r\nv 343.78 41.85 2.4\r\nv 345.11 41.18 7.17\r\nv 346.41 40.48 12.47\r\nv 346.27 40.3 12.46\r\nv 349.36 41.41 24.1\r\nv 348.36 42.12 18.51\r\nv 349.44 41.37 24.08\r\nv 348.43 42.07 18.49\r\nv 347.47 39.58 18.04\r\nv 347.6 39.76 18.05\r\nv 348.6 39.06 23.65\r\nv 348.47 38.88 23.63\r\nv 350.58 40.12 34.58\r\nv 350.1 40.74 29.45\r\nv 350.66 40.08 34.57\r\nv 350.17 40.7 29.44\r\nv 349.2 38.21 28.99\r\nv 349.33 38.39 29.01\r\nv 349.82 37.77 34.15\r\nv 349.69 37.58 34.13\r\nv 350.92 39.13 44.23\r\nv 350.85 39.58 39.51\r\nv 350.99 39.09 44.23\r\nv 350.92 39.53 39.5\r\nv 349.95 37.04 39.07\r\nv 350.09 37.22 39.09\r\nv 350.15 36.77 43.84\r\nv 350.02 36.59 43.82\r\nv 350.53 38.57 53.28\r\nv 350.82 38.8 48.78\r\nv 350.6 38.52 53.28\r\nv 350.89 38.76 48.78\r\nv 349.92 36.26 48.39\r\nv 350.05 36.44 48.41\r\nv 349.76 36.2 52.94\r\nv 349.63 36.02 52.91\r\nv 349.32 38.22 62.68\r\nv 350.03 38.38 57.87\r\nv 349.39 38.18 62.69\r\nv 350.11 38.34 57.87\r\nv 349.13 35.84 57.51\r\nv 349.26 36.02 57.54\r\nv 348.55 35.86 62.36\r\nv 348.42 35.68 62.32\r\nv 347.18 37.9 73.23\r\nv 348.37 38.05 67.84\r\nv 347.25 37.86 73.24\r\nv 348.44 38 67.86\r\nv 347.47 35.5 67.5\r\nv 347.6 35.69 67.53\r\nv 346.41 35.54 72.93\r\nv 346.27 35.36 72.89\r\nv 344.08 37.83 84.08\r\nv 345.75 37.82 78.68\r\nv 344.15 37.79 84.1\r\nv 345.82 37.77 78.7\r\nv 344.84 35.27 78.37\r\nv 344.97 35.45 78.41\r\nv 343.29 35.47 83.83\r\nv 343.16 35.29 83.78\r\nv 340.06 38.24 94.18\r\nv 342.16 37.99 89.27\r\nv 340.13 38.19 94.22\r\nv 342.23 37.94 89.3\r\nv 341.23 35.45 88.99\r\nv 341.36 35.63 89.04\r\nv 339.25 35.88 93.98\r\nv 339.12 35.7 93.93\r\nv 335.62 38.85 102.9\r\nv 337.86 38.54 98.75\r\nv 335.68 38.8 102.94\r\nv 337.92 38.49 98.79\r\nv 336.91 36 98.5\r\nv 337.04 36.18 98.55\r\nv 334.8 36.49 102.71\r\nv 334.67 36.31 102.65\r\nv 331.27 39.36 109.77\r\nv 333.42 39.12 106.55\r\nv 331.33 39.31 109.81\r\nv 333.48 39.07 106.59\r\nv 332.47 36.58 106.31\r\nv 332.6 36.76 106.37\r\nv 330.45 37 109.58\r\nv 330.33 36.82 109.52\r\nv 327.25 39.75 115.06\r\nv 329.21 39.57 112.58\r\nv 327.31 39.71 115.11\r\nv 329.27 39.52 112.63\r\nv 328.27 37.03 112.34\r\nv 328.39 37.21 112.4\r\nv 323.98 40.04 118.92\r\nv 325.43 39.91 117.26\r\nv 325.48 39.87 117.31\r\nv 322.66 40.14 120.33\r\nv 323.16 40.1 119.82\r\nv 320.92 89.56 106.49\r\nv 319.26 89.75 108.51\r\nv 324.54 89.09 101.59\r\nv 322.69 89.34 104.2\r\nv 321.64 91.44 103.8\r\nv 323.47 91.2 101.21\r\nv 328.43 88.49 95.24\r\nv 326.46 88.81 98.62\r\nv 328.48 88.56 95.25\r\nv 326.5 88.88 98.64\r\nv 325.38 90.92 98.25\r\nv 327.35 90.6 94.89\r\nv 332.46 87.7 87.17\r\nv 330.45 88.13 91.4\r\nv 332.5 87.77 87.18\r\nv 330.5 88.2 91.42\r\nv 329.35 90.24 91.07\r\nv 331.36 89.81 86.85\r\nv 336.26 86.59 77.76\r\nv 334.41 87.19 82.6\r\nv 336.31 86.66 77.77\r\nv 334.46 87.26 82.61\r\nv 333.31 89.31 82.29\r\nv 335.15 88.7 77.45\r\nv 339.47 85.07 67.57\r\nv 337.96 85.87 72.7\r\nv 339.52 85.14 67.57\r\nv 338.01 85.93 72.71\r\nv 336.85 87.98 72.39\r\nv 338.36 87.18 67.26\r\nv 341.87 83.42 57.61\r\nv 340.79 84.24 62.49\r\nv 341.92 83.49 57.61\r\nv 340.84 84.31 62.49\r\nv 339.67 86.35 62.19\r\nv 340.75 85.54 57.33\r\nv 343.3 81.93 48.77\r\nv 342.7 82.66 53.07\r\nv 343.35 82 48.76\r\nv 342.75 82.73 53.06\r\nv 341.58 84.78 52.8\r\nv 342.18 84.05 48.51\r\nv 343.98 80.44 40.55\r\nv 343.72 81.2 44.63\r\nv 344.04 80.51 40.54\r\nv 343.77 81.27 44.62\r\nv 342.59 83.32 44.38\r\nv 342.86 82.56 40.3\r\nv 344.14 78.77 32.26\r\nv 344.13 79.63 36.44\r\nv 344.2 78.83 32.24\r\nv 344.18 79.7 36.43\r\nv 343.01 81.75 36.2\r\nv 343.02 80.89 32.03\r\nv 343.69 76.9 23.52\r\nv 344 77.86 27.97\r\nv 343.74 76.96 23.5\r\nv 344.05 77.92 27.95\r\nv 342.88 79.98 27.74\r\nv 342.56 79.03 23.31\r\nv 342.51 74.89 14.21\r\nv 343.17 75.9 18.87\r\nv 342.56 74.95 14.19\r\nv 343.23 75.97 18.85\r\nv 342.05 78.03 18.68\r\nv 341.38 77.02 14.02\r\nv 340.86 72.94 5.52\r\nv 341.72 73.89 9.7\r\nv 340.91 73 5.5\r\nv 341.77 73.95 9.68\r\nv 340.6 76.03 9.52\r\nv 339.74 75.07 5.35\r\nv 338.97 71.23 -1.41\r\nv 339.95 72.05 1.86\r\nv 339.02 71.29 -1.45\r\nv 340 72.12 1.83\r\nv 338.83 74.19 1.7\r\nv 337.86 73.37 -1.57\r\nv 336.77 69.65 -7.23\r\nv 337.92 70.43 -4.4\r\nv 336.81 69.71 -7.27\r\nv 337.97 70.49 -4.44\r\nv 336.81 72.57 -4.55\r\nv 335.66 71.8 -7.36\r\nv 334.11 68.09 -12.73\r\nv 335.49 68.87 -10\r\nv 334.15 68.15 -12.77\r\nv 335.54 68.93 -10.04\r\nv 334.39 71.02 -10.12\r\nv 333.01 70.25 -12.84\r\nv 330.98 66.56 -18.1\r\nv 332.6 67.32 -15.43\r\nv 331.03 66.61 -18.14\r\nv 332.65 67.38 -15.47\r\nv 331.51 69.48 -15.52\r\nv 329.9 68.72 -18.18\r\nv 327.55 65.09 -23.27\r\nv 329.26 65.8 -20.75\r\nv 327.6 65.15 -23.31\r\nv 329.3 65.86 -20.8\r\nv 328.18 67.97 -20.82\r\nv 326.48 67.26 -23.33\r\nv 324.68 63.88 -27.45\r\nv 326.02 64.45 -25.52\r\nv 326.06 64.51 -25.57\r\nv 324.95 66.61 -25.58\r\nv 323.61 66.04 -27.51\r\nv 322.71 63.05 -30.04\r\nv 323.56 63.39 -28.97\r\nv 322.47 65.55 -29.04\r\nv 321.63 65.17 -30.13\r\nv 321.86 62.84 -30.98\r\nv 322.2 62.89 -30.63\r\nv 321.82 63 -31.01\r\nv 322.2 63 -30.67\r\nv 321.14 64.94 -30.72\r\nv 321.28 64.78 -30.74\r\nv 320.96 64.59 -31.07\r\nv 320.84 64.77 -31.06\r\nv 323.85 48.51 -31.17\r\nv 323.96 48.7 -31.18\r\nv 325.09 48.4 -29.75\r\nv 324.97 48.21 -29.74\r\nv 329.11 49.87 -25.21\r\nv 327.38 50.33 -27.35\r\nv 326.46 47.82 -27.91\r\nv 326.58 48 -27.93\r\nv 328.3 47.54 -25.78\r\nv 328.18 47.35 -25.77\r\nv 332.84 48.8 -20.31\r\nv 330.97 49.35 -22.83\r\nv 332.9 48.77 -20.36\r\nv 331.02 49.32 -22.88\r\nv 330.04 46.84 -23.39\r\nv 330.16 47.02 -23.4\r\nv 332.04 46.47 -20.89\r\nv 331.92 46.29 -20.88\r\nv 336.31 47.67 -15.24\r\nv 334.62 48.24 -17.79\r\nv 336.37 47.63 -15.29\r\nv 334.68 48.2 -17.84\r\nv 333.7 45.72 -18.36\r\nv 333.82 45.91 -18.37\r\nv 335.5 45.34 -15.82\r\nv 335.38 45.15 -15.81\r\nv 339.36 46.53 -10.03\r\nv 337.89 47.09 -12.66\r\nv 339.42 46.49 -10.07\r\nv 337.95 47.06 -12.7\r\nv 336.96 44.58 -13.23\r\nv 337.08 44.76 -13.23\r\nv 338.56 44.19 -10.59\r\nv 338.43 44.01 -10.59\r\nv 342.01 45.4 -4.42\r\nv 340.72 45.97 -7.35\r\nv 342.08 45.36 -4.45\r\nv 340.78 45.93 -7.38\r\nv 339.8 43.45 -7.89\r\nv 339.93 43.63 -7.89\r\nv 341.23 43.06 -4.95\r\nv 341.1 42.88 -4.95\r\nv 343.27 44.82 -1.07\r\nv 343.34 44.78 -1.1\r\nv 342.37 42.29 -1.58\r\nv 342.5 42.48 -1.58\r\nv 344.66 44.09 2.84\r\nv 345.99 43.42 7.6\r\nv 347.29 42.72 12.9\r\nv 346.94 41.69 12.64\r\nv 345.64 42.39 7.34\r\nv 346.85 41.46 12.62\r\nv 345.55 42.17 7.31\r\nv 344.22 42.84 2.54\r\nv 344.31 43.07 2.57\r\nv 342.12 45.3 -4.48\r\nv 343.38 44.71 -1.12\r\nv 343.03 43.69 -1.41\r\nv 342.94 43.46 -1.44\r\nv 341.67 44.05 -4.81\r\nv 341.76 44.28 -4.78\r\nv 339.46 46.42 -10.11\r\nv 340.82 45.86 -7.42\r\nv 340.46 44.84 -7.73\r\nv 340.37 44.62 -7.76\r\nv 339 45.18 -10.46\r\nv 339.09 45.4 -10.43\r\nv 336.4 47.56 -15.33\r\nv 337.98 46.99 -12.74\r\nv 337.61 45.98 -13.07\r\nv 337.52 45.75 -13.09\r\nv 335.94 46.32 -15.68\r\nv 336.03 46.55 -15.66\r\nv 332.94 48.7 -20.4\r\nv 334.72 48.14 -17.88\r\nv 334.35 47.12 -18.21\r\nv 334.26 46.9 -18.23\r\nv 332.48 47.46 -20.76\r\nv 332.56 47.69 -20.73\r\nv 331.06 49.25 -22.92\r\nv 330.69 48.24 -23.24\r\nv 330.6 48.01 -23.27\r\nv 324.86 50.93 -30.69\r\nv 322.14 63.2 -30.71\r\nv 321.7 63.33 -31.04\r\nv 321.22 64.21 -31.08\r\nv 321.56 64.37 -30.76\r\nv 327.61 65.23 -23.34\r\nv 326.07 64.59 -25.6\r\nv 331.04 66.69 -18.17\r\nv 329.31 65.94 -20.83\r\nv 334.16 68.23 -12.8\r\nv 332.66 67.46 -15.5\r\nv 336.82 69.79 -7.29\r\nv 335.55 69.01 -10.07\r\nv 339.04 71.37 -1.47\r\nv 337.98 70.57 -4.47\r\nv 340.92 73.08 5.47\r\nv 340.01 72.2 1.8\r\nv 342.57 75.03 14.17\r\nv 341.79 74.04 9.65\r\nv 343.75 77.05 23.48\r\nv 343.24 76.05 18.83\r\nv 344.21 78.92 32.23\r\nv 344.07 78 27.93\r\nv 344.05 80.59 40.52\r\nv 344.2 79.78 36.41\r\nv 343.37 82.08 48.75\r\nv 343.78 81.35 44.6\r\nv 341.94 83.57 57.6\r\nv 342.76 82.81 53.05\r\nv 339.54 85.22 67.56\r\nv 340.85 84.39 62.48\r\nv 336.32 86.74 77.76\r\nv 338.02 86.02 72.7\r\nv 332.52 87.85 87.18\r\nv 334.47 87.35 82.61\r\nv 328.49 88.64 95.25\r\nv 330.51 88.28 91.41\r\nv 326.51 88.96 98.64\r\nv 316.13 90.24 112.13\r\nv 316.91 90.16 111.27\r\nv 327.34 39.64 115.14\r\nv 325.51 39.8 117.34\r\nv 331.37 39.24 109.84\r\nv 329.3 39.45 112.66\r\nv 328.91 38.42 112.59\r\nv 328.82 38.19 112.56\r\nv 330.89 37.98 109.74\r\nv 330.98 38.21 109.77\r\nv 335.72 38.73 102.96\r\nv 333.51 39 106.62\r\nv 333.12 37.97 106.56\r\nv 333.03 37.74 106.52\r\nv 335.24 37.47 102.86\r\nv 335.33 37.7 102.9\r\nv 340.16 38.12 94.24\r\nv 337.96 38.42 98.81\r\nv 337.57 37.39 98.74\r\nv 337.48 37.16 98.71\r\nv 339.69 36.86 94.13\r\nv 339.78 37.09 94.16\r\nv 344.19 37.72 84.12\r\nv 342.26 37.87 89.32\r\nv 341.89 36.84 89.23\r\nv 341.8 36.61 89.2\r\nv 343.73 36.45 83.98\r\nv 343.82 36.68 84.01\r\nv 347.3 37.78 73.25\r\nv 345.87 37.7 78.72\r\nv 345.5 36.66 78.59\r\nv 345.41 36.44 78.56\r\nv 346.85 36.52 73.08\r\nv 346.94 36.75 73.11\r\nv 349.43 38.1 62.69\r\nv 348.49 37.93 67.86\r\nv 348.13 36.89 67.72\r\nv 348.04 36.67 67.69\r\nv 348.99 36.84 62.51\r\nv 349.08 37.07 62.54\r\nv 350.64 38.45 53.28\r\nv 350.15 38.27 57.88\r\nv 349.8 37.23 57.72\r\nv 349.71 37.01 57.69\r\nv 350.2 37.19 53.08\r\nv 350.29 37.41 53.11\r\nv 351.04 39.01 44.22\r\nv 350.93 38.69 48.78\r\nv 350.58 37.65 48.59\r\nv 350.49 37.43 48.56\r\nv 350.6 37.76 43.99\r\nv 350.69 37.98 44.02\r\nv 350.7 40.01 34.56\r\nv 350.97 39.46 39.49\r\nv 350.62 38.43 39.27\r\nv 350.53 38.2 39.24\r\nv 350.26 38.75 34.3\r\nv 350.35 38.98 34.33\r\nv 349.48 41.3 24.07\r\nv 350.21 40.63 29.43\r\nv 349.86 39.6 29.18\r\nv 349.77 39.37 29.15\r\nv 349.04 40.05 23.79\r\nv 349.13 40.27 23.82\r\nv 348.48 42 18.47\r\nv 348.13 40.98 18.22\r\nv 348.04 40.75 18.19\r\nv 343.38 42.88 2.83\r\nv 343.1 42.24 2.73\r\nv 344.42 41.57 7.47\r\nv 344.7 42.21 7.57\r\nv 345.72 40.88 12.74\r\nv 345.99 41.51 12.84\r\nv 346.9 40.17 18.29\r\nv 347.18 40.8 18.38\r\nv 347.9 39.47 23.85\r\nv 348.17 40.11 23.95\r\nv 348.63 38.8 29.18\r\nv 348.9 39.44 29.28\r\nv 349.11 38.18 34.29\r\nv 349.38 38.82 34.38\r\nv 349.37 37.64 39.19\r\nv 349.65 38.28 39.29\r\nv 349.44 37.19 43.91\r\nv 349.72 37.83 44\r\nv 349.34 36.87 48.44\r\nv 349.62 37.51 48.54\r\nv 349.05 36.63 52.93\r\nv 349.33 37.27 53.02\r\nv 348.56 36.45 57.5\r\nv 348.83 37.09 57.59\r\nv 347.85 36.29 62.28\r\nv 348.12 36.93 62.38\r\nv 346.9 36.12 67.42\r\nv 347.18 36.76 67.52\r\nv 345.72 35.97 72.78\r\nv 346 36.61 72.88\r\nv 344.29 35.89 78.22\r\nv 344.57 36.53 78.32\r\nv 342.62 35.91 83.6\r\nv 342.9 36.54 83.7\r\nv 340.71 36.06 88.78\r\nv 340.98 36.7 88.87\r\nv 338.62 36.31 93.67\r\nv 338.89 36.95 93.77\r\nv 336.43 36.61 98.21\r\nv 336.7 37.25 98.31\r\nv 334.21 36.92 102.32\r\nv 334.48 37.55 102.42\r\nv 332.03 37.19 105.95\r\nv 332.3 37.82 106.04\r\nv 329.9 37.42 109.13\r\nv 330.18 38.06 109.22\r\nv 327.86 37.63 111.92\r\nv 328.14 38.27 112.01\r\nv 325.92 37.81 114.37\r\nv 326.19 38.45 114.46\r\nv 324.09 37.97 116.52\r\nv 324.37 38.61 116.62\r\nv 322.65 38.1 118.16\r\nv 322.92 38.73 118.26\r\nv 321.84 38.16 119.05\r\nv 322.12 38.8 119.14\r\nv 321.55 38.19 119.5\r\nv 321.82 38.83 119.6\r\nv 321.65 38.2 119.84\r\nv 321.92 38.84 119.94\r\nv 321.89 38.21 120.07\r\nv 322.16 38.85 120.17\r\nv 322.01 38.24 120.19\r\nv 322.29 38.87 120.28\r\nv 322.18 38.25 120.1\r\nv 322.57 38.23 119.73\r\nv 323.38 38.16 118.83\r\nv 324.83 38.04 117.16\r\nv 326.65 37.88 114.96\r\nv 328.61 37.69 112.48\r\nv 330.68 37.48 109.66\r\nv 332.82 37.24 106.45\r\nv 335.02 36.97 102.79\r\nv 337.27 36.66 98.64\r\nv 339.48 36.36 94.06\r\nv 341.58 36.11 89.12\r\nv 343.52 35.95 83.91\r\nv 345.2 35.94 78.49\r\nv 346.63 36.02 73.01\r\nv 347.83 36.17 67.61\r\nv 348.78 36.34 62.43\r\nv 349.49 36.51 57.61\r\nv 349.99 36.69 53.01\r\nv 350.28 36.93 48.49\r\nv 350.38 37.26 43.92\r\nv 350.31 37.7 39.17\r\nv 350.05 38.25 34.22\r\nv 349.56 38.87 29.08\r\nv 348.83 39.55 23.72\r\nv 347.83 40.25 18.12\r\nv 346.63 40.96 12.54\r\nv 345.33 41.67 7.24\r\nv 344 42.34 2.47\r\nv 342.72 42.96 -1.51\r\nv 341.45 43.55 -4.88\r\nv 340.15 44.12 -7.83\r\nv 338.79 44.68 -10.53\r\nv 337.31 45.25 -13.17\r\nv 335.73 45.82 -15.75\r\nv 334.05 46.4 -18.3\r\nv 332.26 46.96 -20.83\r\nv 330.39 47.51 -23.34\r\nv 328.53 48.03 -25.72\r\nv 326.8 48.49 -27.87\r\nv 325.32 48.88 -29.69\r\nv 324.19 49.19 -31.12\r\nv 323.36 49.42 -32.16\r\nv 322.79 49.57 -32.81\r\nv 322.44 49.65 -33.14\r\nv 322.29 49.66 -33.21\r\nv 322.56 50.29 -33.12\r\nv 322.17 49.61 -33.07\r\nv 322.44 50.25 -32.98\r\nv 321.94 49.56 -32.75\r\nv 322.22 50.2 -32.65\r\nv 321.85 49.49 -32.33\r\nv 322.12 50.12 -32.23\r\nv 322.11 49.38 -31.89\r\nv 322.38 50.02 -31.79\r\nv 322.67 49.23 -31.29\r\nv 322.94 49.87 -31.19\r\nv 323.48 49.01 -30.37\r\nv 323.75 49.65 -30.28\r\nv 324.59 48.72 -29.07\r\nv 324.87 49.35 -28.98\r\nv 326.07 48.33 -27.3\r\nv 326.34 48.97 -27.2\r\nv 327.78 47.87 -25.17\r\nv 328.06 48.5 -25.07\r\nv 329.63 47.35 -22.8\r\nv 329.91 47.99 -22.7\r\nv 331.49 46.81 -20.31\r\nv 331.77 47.45 -20.21\r\nv 333.26 46.25 -17.8\r\nv 333.54 46.89 -17.71\r\nv 334.93 45.68 -15.28\r\nv 335.2 46.32 -15.18\r\nv 336.49 45.11 -12.72\r\nv 336.77 45.75 -12.62\r\nv 337.95 44.55 -10.11\r\nv 338.22 45.19 -10.02\r\nv 339.3 44 -7.45\r\nv 339.57 44.64 -7.35\r\nv 340.58 43.44 -4.54\r\nv 340.86 44.08 -4.45\r\nv 341.84 42.86 -1.21\r\nv 342.11 43.5 -1.11\r\nvn 0.7 -0.38 -0.6\r\nvn 0.81 -0.37 -0.46\r\nvn 0.35 -0.94 -0.03\r\nvn 0.24 -0.96 -0.14\r\nvn 0.96 -0.15 -0.22\r\nvn 0.8 -0.53 0.28\r\nvn 0.48 -0.66 0.59\r\nvn 0.08 -0.98 0.2\r\nvn -0.02 -1 0.1\r\nvn -0.33 -0.86 0.39\r\nvn -0.33 -0.86 0.39\r\nvn -0.29 -0.89 0.35\r\nvn -0.29 -0.89 0.36\r\nvn -0.34 -0.86 0.38\r\nvn -0.3 -0.89 0.35\r\nvn -0.29 -0.9 0.34\r\nvn -0.28 -0.9 0.35\r\nvn -0.28 -0.89 0.35\r\nvn -0.34 -0.94 0.04\r\nvn -0.34 -0.94 0.05\r\nvn -0.28 -0.95 0.1\r\nvn -0.29 -0.95 0.08\r\nvn -0.35 -0.94 0.01\r\nvn -0.29 -0.95 0.08\r\nvn -0.28 -0.95 0.1\r\nvn -0.27 -0.96 0.1\r\nvn -0.28 -0.96 0.09\r\nvn -0.12 0.98 0.14\r\nvn -0.11 0.98 0.14\r\nvn -0.08 0.98 0.18\r\nvn -0.08 0.98 0.18\r\nvn -0.1 0.98 0.14\r\nvn -0.06 0.98 0.18\r\nvn -0.06 0.98 0.18\r\nvn -0.07 0.98 0.18\r\nvn -0.07 0.98 0.19\r\nvn 0.97 -0.18 0.14\r\nvn 0.8 -0.49 0.35\r\nvn 0.39 -0.91 -0.12\r\nvn 0.86 -0.41 -0.32\r\nvn 0.68 -0.55 0.48\r\nvn 0.23 -0.97 -0.03\r\nvn -0.01 -0.97 -0.23\r\nvn 0.16 -0.93 -0.34\r\nvn 0.65 -0.46 -0.61\r\nvn -0.09 0.98 0.16\r\nvn -0.09 0.98 0.16\r\nvn -0.03 0.98 0.18\r\nvn -0.03 0.98 0.19\r\nvn -0.08 0.99 0.15\r\nvn -0.02 0.98 0.17\r\nvn -0.01 0.98 0.18\r\nvn -0.01 0.98 0.19\r\nvn -0.02 0.98 0.2\r\nvn 0.83 -0.44 0.35\r\nvn 0.85 -0.43 0.32\r\nvn 0.28 -0.96 -0.06\r\nvn 0.28 -0.96 -0.06\r\nvn 0.86 -0.42 0.28\r\nvn 0.28 -0.96 -0.06\r\nvn -0.07 -0.98 -0.21\r\nvn -0.06 -0.97 -0.22\r\nvn -0.05 -0.97 -0.23\r\nvn -0.94 -0.15 -0.3\r\nvn -0.93 -0.13 -0.35\r\nvn -0.9 -0.25 -0.36\r\nvn -0.91 -0.27 -0.31\r\nvn -0.91 -0.12 -0.39\r\nvn -0.89 -0.23 -0.4\r\nvn -0.88 -0.25 -0.41\r\nvn -0.9 -0.27 -0.36\r\nvn -0.91 -0.28 -0.31\r\nvn -0.71 0.14 -0.68\r\nvn -0.76 0.15 -0.64\r\nvn -0.72 0.25 -0.64\r\nvn -0.69 0.24 -0.68\r\nvn -0.78 0.16 -0.61\r\nvn -0.74 0.26 -0.61\r\nvn -0.7 0.37 -0.61\r\nvn -0.68 0.36 -0.64\r\nvn -0.65 0.34 -0.68\r\nvn -0.79 0.36 0.5\r\nvn -0.76 0.37 0.53\r\nvn -0.74 0.44 0.51\r\nvn -0.76 0.44 0.47\r\nvn -0.74 0.37 0.56\r\nvn -0.72 0.44 0.53\r\nvn -0.71 0.48 0.52\r\nvn -0.72 0.49 0.49\r\nvn -0.74 0.49 0.46\r\nvn -0.73 0.37 0.58\r\nvn -0.71 0.44 0.55\r\nvn -0.72 0.36 0.59\r\nvn -0.7 0.44 0.56\r\nvn -0.69 0.48 0.55\r\nvn -0.69 0.48 0.54\r\nvn -0.72 0.33 0.61\r\nvn -0.7 0.43 0.57\r\nvn -0.73 0.28 0.62\r\nvn -0.7 0.43 0.57\r\nvn -0.68 0.48 0.56\r\nvn -0.68 0.48 0.55\r\nvn -0.7 -0.22 0.68\r\nvn -0.71 -0.22 0.67\r\nvn -0.68 -0.34 0.65\r\nvn -0.67 -0.34 0.66\r\nvn -0.72 -0.22 0.65\r\nvn -0.69 -0.34 0.64\r\nvn -0.69 -0.35 0.63\r\nvn -0.67 -0.35 0.65\r\nvn -0.67 -0.34 0.66\r\nvn -0.87 -0.26 0.41\r\nvn -0.89 -0.26 0.37\r\nvn -0.85 -0.38 0.36\r\nvn -0.83 -0.38 0.4\r\nvn -0.91 -0.26 0.31\r\nvn -0.87 -0.38 0.3\r\nvn -0.87 -0.39 0.3\r\nvn -0.85 -0.39 0.35\r\nvn -0.83 -0.38 0.4\r\nvn -0.03 -0.99 0.1\r\nvn -0.03 -0.99 0.1\r\nvn -0.36 -0.86 0.37\r\nvn -0.37 -0.86 0.36\r\nvn -0.35 -0.86 0.38\r\nvn -0.63 -0.5 0.59\r\nvn -0.64 -0.51 0.58\r\nvn -0.65 -0.51 0.56\r\nvn -0.26 -0.86 0.44\r\nvn 0.1 -0.61 0.78\r\nvn -0.55 -0.45 0.7\r\nvn -0.59 -0.5 0.64\r\nvn -0.48 -0.71 0.51\r\nvn -0.5 -0.7 0.51\r\nvn -0.46 -0.73 0.51\r\nvn -0.61 -0.5 0.61\r\nvn -0.65 -0.42 0.64\r\nvn -0.69 -0.35 0.64\r\nvn 0.21 0.94 -0.27\r\nvn 0.22 0.93 -0.28\r\nvn -0.19 0.98 0.08\r\nvn -0.16 0.99 0.05\r\nvn 0.23 0.93 -0.29\r\nvn -0.19 0.98 0.07\r\nvn -0.6 0.66 0.46\r\nvn -0.59 0.66 0.46\r\nvn -0.58 0.68 0.45\r\nvn 0.24 0.93 -0.29\r\nvn -0.19 0.98 0.07\r\nvn 0.24 0.93 -0.28\r\nvn -0.19 0.98 0.06\r\nvn -0.61 0.67 0.43\r\nvn -0.6 0.66 0.45\r\nvn -0.28 0.96 -0.02\r\nvn -0.28 0.96 -0.02\r\nvn -0.29 0.96 -0.02\r\nvn -0.54 0.78 -0.32\r\nvn -0.55 0.77 -0.31\r\nvn -0.57 0.77 -0.29\r\nvn -0.23 -0.77 -0.6\r\nvn 0.19 -0.38 -0.9\r\nvn -0.38 -0.78 -0.5\r\nvn -0.68 -0.35 -0.65\r\nvn -0.62 -0.33 -0.72\r\nvn -0.57 -0.24 -0.79\r\nvn -0.08 0.99 0.15\r\nvn -0.09 0.99 0.14\r\nvn -0.28 0.96 -0.01\r\nvn -0.27 0.96 -0.01\r\nvn -0.57 0.77 -0.28\r\nvn -0.57 0.77 -0.29\r\nvn -0.78 0.01 -0.62\r\nvn -0.78 0.01 -0.63\r\nvn -0.76 0.36 -0.54\r\nvn -0.76 0.36 -0.54\r\nvn -0.78 0 -0.62\r\nvn -0.77 0.36 -0.53\r\nvn -0.57 0.77 -0.29\r\nvn -0.57 0.77 -0.29\r\nvn -0.76 -0.13 -0.63\r\nvn -0.76 -0.13 -0.64\r\nvn -0.74 -0.18 -0.64\r\nvn -0.75 -0.18 -0.64\r\nvn -0.76 -0.13 -0.64\r\nvn -0.75 -0.18 -0.64\r\nvn -0.74 -0.22 -0.64\r\nvn -0.73 -0.22 -0.64\r\nvn -0.74 -0.22 -0.64\r\nvn -0.76 -0.14 -0.63\r\nvn -0.75 -0.18 -0.63\r\nvn -0.75 -0.12 -0.65\r\nvn -0.74 -0.17 -0.65\r\nvn -0.73 -0.21 -0.65\r\nvn -0.74 -0.22 -0.63\r\nvn -0.72 0.03 -0.69\r\nvn -0.74 0.03 -0.67\r\nvn -0.73 0.37 -0.58\r\nvn -0.71 0.37 -0.6\r\nvn -0.77 0.01 -0.64\r\nvn -0.76 0.36 -0.55\r\nvn -0.72 -0.1 -0.69\r\nvn -0.71 -0.16 -0.69\r\nvn -0.7 -0.09 -0.71\r\nvn -0.66 -0.26 -0.71\r\nvn -0.42 -0.25 -0.87\r\nvn -0.69 -0.19 -0.7\r\nvn -0.8 -0.1 -0.59\r\nvn -0.82 -0.09 -0.56\r\nvn -0.74 -0.02 -0.67\r\nvn -0.75 -0.37 -0.55\r\nvn -0.75 -0.51 -0.42\r\nvn -0.77 -0.5 -0.4\r\nvn -0.63 0.44 -0.63\r\nvn -0.61 0.43 -0.67\r\nvn -0.65 0.45 -0.61\r\nvn -0.56 0.58 -0.58\r\nvn -0.55 0.57 -0.61\r\nvn -0.53 0.54 -0.65\r\nvn -0.71 0.37 -0.6\r\nvn -0.66 0.46 -0.59\r\nvn -0.71 0.37 -0.59\r\nvn -0.67 0.46 -0.58\r\nvn -0.58 0.59 -0.56\r\nvn -0.57 0.59 -0.57\r\nvn -0.72 0.38 -0.58\r\nvn -0.67 0.47 -0.58\r\nvn -0.73 0.38 -0.57\r\nvn -0.68 0.47 -0.56\r\nvn -0.59 0.6 -0.54\r\nvn -0.58 0.6 -0.55\r\nvn -0.74 0.39 -0.54\r\nvn -0.69 0.48 -0.54\r\nvn -0.76 0.4 -0.52\r\nvn -0.7 0.49 -0.52\r\nvn -0.61 0.61 -0.5\r\nvn -0.6 0.61 -0.52\r\nvn -0.77 0.41 -0.49\r\nvn -0.72 0.5 -0.49\r\nvn -0.79 0.42 -0.46\r\nvn -0.73 0.51 -0.46\r\nvn -0.63 0.63 -0.45\r\nvn -0.62 0.62 -0.48\r\nvn -0.8 0.43 -0.42\r\nvn -0.75 0.51 -0.42\r\nvn -0.82 0.44 -0.38\r\nvn -0.76 0.52 -0.38\r\nvn -0.66 0.65 -0.38\r\nvn -0.65 0.64 -0.42\r\nvn -0.83 0.45 -0.34\r\nvn -0.77 0.53 -0.34\r\nvn -0.84 0.45 -0.3\r\nvn -0.78 0.54 -0.31\r\nvn -0.68 0.66 -0.31\r\nvn -0.67 0.66 -0.34\r\nvn -0.85 0.46 -0.27\r\nvn -0.79 0.55 -0.27\r\nvn -0.85 0.46 -0.24\r\nvn -0.8 0.55 -0.25\r\nvn -0.69 0.67 -0.26\r\nvn -0.69 0.67 -0.28\r\nvn -0.86 0.47 -0.21\r\nvn -0.8 0.56 -0.22\r\nvn -0.86 0.47 -0.18\r\nvn -0.81 0.56 -0.19\r\nvn -0.7 0.68 -0.21\r\nvn -0.7 0.68 -0.23\r\nvn -0.87 0.48 -0.15\r\nvn -0.81 0.56 -0.16\r\nvn -0.87 0.48 -0.11\r\nvn -0.81 0.57 -0.13\r\nvn -0.71 0.69 -0.15\r\nvn -0.71 0.68 -0.18\r\nvn -0.87 0.49 -0.08\r\nvn -0.82 0.57 -0.1\r\nvn -0.87 0.49 -0.05\r\nvn -0.82 0.57 -0.07\r\nvn -0.71 0.69 -0.1\r\nvn -0.71 0.69 -0.13\r\nvn -0.87 0.49 -0.02\r\nvn -0.82 0.57 -0.04\r\nvn -0.87 0.5 0.02\r\nvn -0.82 0.58 -0\r\nvn -0.72 0.7 -0.03\r\nvn -0.72 0.69 -0.07\r\nvn -0.86 0.5 0.05\r\nvn -0.82 0.58 0.03\r\nvn -0.86 0.5 0.09\r\nvn -0.81 0.58 0.07\r\nvn -0.71 0.7 0.03\r\nvn -0.72 0.7 -0\r\nvn -0.86 0.5 0.12\r\nvn -0.81 0.58 0.1\r\nvn -0.85 0.5 0.15\r\nvn -0.81 0.58 0.13\r\nvn -0.71 0.7 0.09\r\nvn -0.71 0.7 0.06\r\nvn -0.84 0.5 0.19\r\nvn -0.8 0.58 0.17\r\nvn -0.83 0.5 0.23\r\nvn -0.79 0.57 0.21\r\nvn -0.7 0.7 0.16\r\nvn -0.7 0.7 0.12\r\nvn -0.82 0.5 0.27\r\nvn -0.78 0.57 0.25\r\nvn -0.81 0.5 0.31\r\nvn -0.77 0.57 0.29\r\nvn -0.68 0.69 0.23\r\nvn -0.69 0.7 0.2\r\nvn -0.79 0.5 0.35\r\nvn -0.76 0.56 0.33\r\nvn -0.78 0.5 0.38\r\nvn -0.75 0.56 0.36\r\nvn -0.66 0.69 0.3\r\nvn -0.67 0.69 0.27\r\nvn -0.76 0.49 0.42\r\nvn -0.73 0.55 0.4\r\nvn -0.72 0.54 0.43\r\nvn -0.64 0.68 0.37\r\nvn -0.65 0.68 0.34\r\nvn -0.8 0.12 0.58\r\nvn -0.78 0.12 0.61\r\nvn -0.83 0.11 0.55\r\nvn -0.77 -0.33 0.55\r\nvn -0.74 -0.33 0.58\r\nvn -0.72 -0.32 0.61\r\nvn -0.7 0.54 0.47\r\nvn -0.69 0.53 0.5\r\nvn -0.62 0.67 0.4\r\nvn -0.76 0.12 0.63\r\nvn -0.75 0.09 0.65\r\nvn -0.71 -0.32 0.63\r\nvn -0.68 0.52 0.52\r\nvn -0.67 0.52 0.53\r\nvn -0.67 0.52 0.54\r\nvn -0.66 0.52 0.54\r\nvn -0.72 0.23 0.65\r\nvn -0.74 0.22 0.64\r\nvn -0.71 0.24 0.66\r\nvn -0.49 0.77 0.41\r\nvn -0.52 0.74 0.43\r\nvn -0.55 0.72 0.43\r\nvn -0.67 -0.35 0.65\r\nvn -0.64 -0.43 0.64\r\nvn -0.69 -0.35 0.63\r\nvn -0.68 -0.37 0.63\r\nvn -0.66 -0.37 0.65\r\nvn -0.44 -0.45 0.78\r\nvn -0.7 -0.35 0.62\r\nvn -0.7 -0.36 0.62\r\nvn -0.71 -0.36 0.6\r\nvn -0.71 -0.36 0.6\r\nvn -0.7 -0.38 0.6\r\nvn -0.69 -0.38 0.62\r\nvn -0.07 0.99 0.11\r\nvn -0.01 0.99 0.13\r\nvn -0.01 0.99 0.13\r\nvn -0.07 0.99 0.12\r\nvn 0 0.99 0.13\r\nvn 0 0.99 0.14\r\nvn 0 0.99 0.15\r\nvn -0.01 0.99 0.14\r\nvn -0.08 0.99 0.12\r\nvn 0.91 -0.38 0.16\r\nvn 0.28 -0.96 -0.06\r\nvn 0.28 -0.96 -0.06\r\nvn 0.9 -0.39 0.18\r\nvn -0.08 -0.99 -0.15\r\nvn -0.08 -0.98 -0.16\r\nvn -0.08 -0.98 -0.17\r\nvn 0.28 -0.96 -0.06\r\nvn 0.89 -0.4 0.21\r\nvn -0.08 0.99 0.11\r\nvn -0.01 0.99 0.12\r\nvn -0.01 0.99 0.12\r\nvn -0.07 0.99 0.11\r\nvn 0 0.99 0.13\r\nvn 0 0.99 0.13\r\nvn 0.92 -0.37 0.1\r\nvn 0.29 -0.95 -0.07\r\nvn 0.29 -0.96 -0.06\r\nvn 0.92 -0.38 0.13\r\nvn -0.08 -0.99 -0.14\r\nvn -0.08 -0.99 -0.14\r\nvn -0.08 0.99 0.11\r\nvn -0.01 0.99 0.11\r\nvn -0.01 0.99 0.12\r\nvn -0.08 0.99 0.11\r\nvn 0 0.99 0.12\r\nvn 0 0.99 0.12\r\nvn 0.93 -0.36 0.03\r\nvn 0.29 -0.95 -0.09\r\nvn 0.29 -0.95 -0.08\r\nvn 0.93 -0.37 0.06\r\nvn -0.08 -0.99 -0.12\r\nvn -0.08 -0.99 -0.13\r\nvn -0.08 0.99 0.08\r\nvn -0.01 1 0.08\r\nvn -0.01 0.99 0.1\r\nvn -0.08 0.99 0.1\r\nvn 0 1 0.08\r\nvn 0 0.99 0.1\r\nvn 0.93 -0.35 -0.03\r\nvn 0.29 -0.95 -0.08\r\nvn 0.29 -0.95 -0.09\r\nvn 0.93 -0.36 -0\r\nvn -0.08 -0.99 -0.08\r\nvn -0.08 -0.99 -0.1\r\nvn -0.08 1 0.05\r\nvn -0.01 1 0.05\r\nvn -0.01 1 0.06\r\nvn -0.08 0.99 0.07\r\nvn 0 1 0.05\r\nvn 0 1 0.06\r\nvn 0.93 -0.34 -0.1\r\nvn 0.29 -0.95 -0.07\r\nvn 0.29 -0.95 -0.07\r\nvn 0.93 -0.35 -0.06\r\nvn -0.08 -1 -0.04\r\nvn -0.08 -0.99 -0.06\r\nvn -0.08 1 0.05\r\nvn -0.01 1 0.04\r\nvn -0.01 1 0.04\r\nvn -0.08 1 0.05\r\nvn -0 1 0.03\r\nvn -0 1 0.04\r\nvn 0.93 -0.34 -0.17\r\nvn 0.3 -0.95 -0.08\r\nvn 0.3 -0.95 -0.07\r\nvn 0.93 -0.34 -0.13\r\nvn -0.08 -1 -0.02\r\nvn -0.08 -1 -0.03\r\nvn -0.08 1 0.04\r\nvn -0.02 1 0.03\r\nvn -0.01 1 0.03\r\nvn -0.08 1 0.05\r\nvn -0 1 0.02\r\nvn -0 1 0.03\r\nvn 0.91 -0.34 -0.23\r\nvn 0.29 -0.95 -0.09\r\nvn 0.29 -0.95 -0.09\r\nvn 0.92 -0.34 -0.2\r\nvn -0.07 -1 -0\r\nvn -0.08 -1 -0.01\r\nvn -0.09 1 0.01\r\nvn -0.03 1 -0.01\r\nvn -0.02 1 0.01\r\nvn -0.09 1 0.03\r\nvn -0.02 1 -0.01\r\nvn -0.01 1 0.01\r\nvn 0.89 -0.34 -0.3\r\nvn 0.3 -0.95 -0.08\r\nvn 0.29 -0.95 -0.09\r\nvn 0.91 -0.34 -0.26\r\nvn -0.06 -1 0.04\r\nvn -0.07 -1 0.01\r\nvn -0.1 0.99 -0.01\r\nvn -0.04 1 -0.04\r\nvn -0.03 1 -0.03\r\nvn -0.1 1 -0\r\nvn -0.03 1 -0.04\r\nvn -0.02 1 -0.03\r\nvn 0.86 -0.34 -0.37\r\nvn 0.3 -0.95 -0.08\r\nvn 0.3 -0.95 -0.08\r\nvn 0.88 -0.34 -0.34\r\nvn -0.04 -1 0.08\r\nvn -0.05 -1 0.06\r\nvn -0.1 0.99 -0.01\r\nvn -0.05 1 -0.05\r\nvn -0.05 1 -0.05\r\nvn -0.11 0.99 -0.02\r\nvn -0.04 1 -0.05\r\nvn -0.03 1 -0.05\r\nvn 0.83 -0.34 -0.45\r\nvn 0.29 -0.95 -0.1\r\nvn 0.3 -0.95 -0.09\r\nvn 0.85 -0.34 -0.41\r\nvn -0.03 -1 0.09\r\nvn -0.04 -1 0.09\r\nvn -0.1 0.99 -0\r\nvn -0.04 1 -0.04\r\nvn -0.05 1 -0.05\r\nvn -0.1 0.99 -0.01\r\nvn -0.03 1 -0.05\r\nvn -0.04 1 -0.05\r\nvn 0.78 -0.34 -0.52\r\nvn 0.28 -0.95 -0.12\r\nvn 0.28 -0.95 -0.11\r\nvn 0.81 -0.34 -0.49\r\nvn -0.03 -0.99 0.1\r\nvn -0.03 -0.99 0.09\r\nvn -0.09 1 0\r\nvn -0.04 1 -0.04\r\nvn -0.04 1 -0.04\r\nvn -0.1 1 -0\r\nvn -0.03 1 -0.05\r\nvn -0.03 1 -0.05\r\nvn 0.74 -0.36 -0.58\r\nvn 0.26 -0.96 -0.14\r\nvn 0.27 -0.95 -0.13\r\nvn 0.76 -0.35 -0.55\r\nvn -0.03 -0.99 0.1\r\nvn -0.09 1 0.01\r\nvn -0.04 1 -0.04\r\nvn -0.04 1 -0.04\r\nvn -0.09 1 0\r\nvn -0.03 1 -0.04\r\nvn -0.03 1 -0.05\r\nvn 0.25 -0.96 -0.14\r\nvn 0.72 -0.37 -0.59\r\nvn -0.08 1 0.01\r\nvn -0.04 1 -0.04\r\nvn -0.04 1 -0.04\r\nvn -0.09 1 0.01\r\nvn -0.03 1 -0.04\r\nvn -0.03 1 -0.04\r\nvn 0.28 0.9 -0.34\r\nvn 0.28 0.9 -0.33\r\nvn 0.26 0.91 -0.32\r\nvn 0.27 0.9 -0.33\r\nvn 0.25 0.92 -0.31\r\nvn 0.25 0.91 -0.32\r\nvn -0.36 -0.86 0.37\r\nvn -0.31 -0.89 0.33\r\nvn -0.3 -0.89 0.34\r\nvn -0.35 -0.86 0.38\r\nvn -0.3 -0.9 0.32\r\nvn -0.29 -0.9 0.33\r\nvn 0.3 0.9 -0.32\r\nvn 0.29 0.9 -0.32\r\nvn 0.28 0.9 -0.33\r\nvn 0.29 0.9 -0.33\r\nvn -0.38 -0.86 0.34\r\nvn -0.33 -0.89 0.31\r\nvn -0.32 -0.89 0.32\r\nvn -0.37 -0.86 0.35\r\nvn -0.32 -0.9 0.3\r\nvn -0.31 -0.9 0.31\r\nvn 0.32 0.9 -0.3\r\nvn 0.31 0.9 -0.3\r\nvn 0.3 0.9 -0.31\r\nvn 0.31 0.9 -0.31\r\nvn 0.26 0.93 -0.26\r\nvn 0.25 0.93 -0.27\r\nvn -0.4 -0.86 0.3\r\nvn -0.35 -0.9 0.28\r\nvn -0.34 -0.89 0.29\r\nvn -0.39 -0.86 0.32\r\nvn -0.34 -0.9 0.27\r\nvn -0.33 -0.9 0.29\r\nvn 0.34 0.9 -0.27\r\nvn 0.33 0.91 -0.27\r\nvn 0.32 0.91 -0.28\r\nvn 0.33 0.9 -0.29\r\nvn 0.27 0.93 -0.24\r\nvn 0.26 0.93 -0.25\r\nvn -0.41 -0.87 0.28\r\nvn -0.36 -0.9 0.26\r\nvn -0.35 -0.9 0.26\r\nvn -0.41 -0.87 0.29\r\nvn -0.35 -0.9 0.25\r\nvn -0.34 -0.9 0.26\r\nvn 0.35 0.9 -0.25\r\nvn 0.34 0.91 -0.25\r\nvn 0.33 0.91 -0.26\r\nvn 0.34 0.9 -0.26\r\nvn 0.28 0.93 -0.22\r\nvn 0.28 0.93 -0.23\r\nvn -0.42 -0.87 0.27\r\nvn -0.36 -0.9 0.25\r\nvn -0.36 -0.9 0.25\r\nvn -0.42 -0.87 0.27\r\nvn -0.35 -0.9 0.25\r\nvn -0.35 -0.9 0.25\r\nvn 0.35 0.9 -0.25\r\nvn 0.34 0.91 -0.24\r\nvn 0.34 0.91 -0.24\r\nvn 0.35 0.9 -0.25\r\nvn 0.28 0.93 -0.23\r\nvn 0.28 0.93 -0.22\r\nvn -0.42 -0.87 0.26\r\nvn -0.36 -0.9 0.24\r\nvn -0.36 -0.9 0.25\r\nvn -0.42 -0.87 0.26\r\nvn -0.35 -0.9 0.24\r\nvn -0.35 -0.9 0.25\r\nvn 0.35 0.9 -0.24\r\nvn 0.34 0.91 -0.24\r\nvn 0.34 0.91 -0.24\r\nvn 0.35 0.9 -0.25\r\nvn 0.28 0.93 -0.23\r\nvn 0.28 0.93 -0.23\r\nvn -0.43 -0.87 0.23\r\nvn -0.37 -0.9 0.23\r\nvn -0.37 -0.9 0.24\r\nvn -0.43 -0.87 0.25\r\nvn -0.36 -0.91 0.22\r\nvn -0.36 -0.9 0.24\r\nvn 0.36 0.91 -0.22\r\nvn 0.35 0.91 -0.22\r\nvn 0.35 0.91 -0.23\r\nvn 0.36 0.9 -0.24\r\nvn 0.29 0.93 -0.21\r\nvn 0.28 0.93 -0.22\r\nvn -0.44 -0.88 0.2\r\nvn -0.37 -0.9 0.2\r\nvn -0.37 -0.9 0.21\r\nvn -0.43 -0.87 0.22\r\nvn -0.36 -0.91 0.2\r\nvn -0.36 -0.91 0.21\r\nvn 0.36 0.91 -0.2\r\nvn 0.35 0.91 -0.2\r\nvn 0.35 0.91 -0.21\r\nvn 0.36 0.91 -0.21\r\nvn 0.29 0.94 -0.2\r\nvn 0.29 0.94 -0.2\r\nvn -0.44 -0.88 0.19\r\nvn -0.38 -0.91 0.19\r\nvn -0.38 -0.91 0.2\r\nvn -0.44 -0.88 0.2\r\nvn -0.37 -0.91 0.19\r\nvn -0.36 -0.91 0.2\r\nvn 0.37 0.91 -0.19\r\nvn 0.35 0.92 -0.19\r\nvn 0.35 0.91 -0.19\r\nvn 0.36 0.91 -0.2\r\nvn 0.29 0.94 -0.19\r\nvn 0.29 0.94 -0.19\r\nvn -0.44 -0.88 0.18\r\nvn -0.38 -0.91 0.18\r\nvn -0.38 -0.91 0.19\r\nvn -0.44 -0.88 0.19\r\nvn -0.37 -0.91 0.19\r\nvn -0.37 -0.91 0.19\r\nvn 0.37 0.91 -0.19\r\nvn 0.36 0.92 -0.19\r\nvn 0.35 0.92 -0.19\r\nvn 0.37 0.91 -0.19\r\nvn 0.29 0.94 -0.19\r\nvn 0.29 0.94 -0.2\r\nvn -0.44 -0.89 0.15\r\nvn -0.38 -0.91 0.16\r\nvn -0.38 -0.91 0.17\r\nvn -0.44 -0.88 0.17\r\nvn -0.37 -0.92 0.16\r\nvn -0.37 -0.91 0.18\r\nvn 0.37 0.92 -0.16\r\nvn 0.35 0.92 -0.17\r\nvn 0.36 0.92 -0.18\r\nvn 0.37 0.91 -0.18\r\nvn 0.29 0.94 -0.18\r\nvn 0.29 0.94 -0.19\r\nvn -0.44 -0.89 0.13\r\nvn -0.38 -0.92 0.14\r\nvn -0.38 -0.91 0.15\r\nvn -0.44 -0.89 0.14\r\nvn -0.36 -0.92 0.14\r\nvn -0.37 -0.92 0.15\r\nvn 0.36 0.92 -0.14\r\nvn 0.35 0.92 -0.15\r\nvn 0.35 0.92 -0.15\r\nvn 0.37 0.92 -0.15\r\nvn 0.29 0.94 -0.16\r\nvn 0.29 0.94 -0.17\r\nvn -0.43 -0.89 0.11\r\nvn -0.37 -0.92 0.13\r\nvn -0.37 -0.92 0.13\r\nvn -0.44 -0.89 0.12\r\nvn -0.36 -0.92 0.13\r\nvn -0.36 -0.92 0.14\r\nvn 0.36 0.92 -0.13\r\nvn 0.35 0.93 -0.14\r\nvn 0.35 0.93 -0.14\r\nvn 0.36 0.92 -0.14\r\nvn 0.29 0.94 -0.16\r\nvn 0.29 0.94 -0.16\r\nvn -0.43 -0.9 0.09\r\nvn -0.37 -0.92 0.12\r\nvn -0.37 -0.92 0.13\r\nvn -0.43 -0.9 0.1\r\nvn -0.36 -0.92 0.12\r\nvn -0.36 -0.92 0.13\r\nvn 0.36 0.92 -0.12\r\nvn 0.35 0.93 -0.13\r\nvn 0.35 0.93 -0.13\r\nvn 0.36 0.92 -0.13\r\nvn 0.29 0.95 -0.15\r\nvn 0.29 0.94 -0.15\r\nvn -0.42 -0.9 0.07\r\nvn -0.37 -0.93 0.1\r\nvn -0.37 -0.92 0.11\r\nvn -0.43 -0.9 0.08\r\nvn -0.35 -0.93 0.1\r\nvn -0.36 -0.93 0.11\r\nvn 0.35 0.93 -0.1\r\nvn 0.34 0.93 -0.11\r\nvn 0.35 0.93 -0.12\r\nvn 0.36 0.93 -0.11\r\nvn 0.29 0.95 -0.14\r\nvn 0.29 0.95 -0.15\r\nvn -0.41 -0.91 0.04\r\nvn -0.36 -0.93 0.08\r\nvn -0.36 -0.93 0.09\r\nvn -0.42 -0.91 0.06\r\nvn -0.35 -0.93 0.08\r\nvn -0.35 -0.93 0.09\r\nvn 0.35 0.93 -0.08\r\nvn 0.34 0.94 -0.09\r\nvn 0.34 0.93 -0.1\r\nvn 0.35 0.93 -0.09\r\nvn 0.28 0.95 -0.12\r\nvn 0.28 0.95 -0.13\r\nvn -0.39 -0.92 0.01\r\nvn -0.34 -0.94 0.05\r\nvn -0.35 -0.94 0.07\r\nvn -0.4 -0.92 0.03\r\nvn -0.33 -0.94 0.06\r\nvn -0.34 -0.94 0.07\r\nvn 0.33 0.94 -0.06\r\nvn 0.32 0.94 -0.07\r\nvn 0.33 0.94 -0.08\r\nvn 0.34 0.94 -0.07\r\nvn 0.27 0.96 -0.1\r\nvn 0.27 0.95 -0.11\r\nvn -0.38 -0.93 0.01\r\nvn -0.32 -0.94 0.04\r\nvn -0.34 -0.94 0.04\r\nvn -0.39 -0.92 0\r\nvn -0.31 -0.95 0.05\r\nvn -0.33 -0.94 0.05\r\nvn 0.31 0.95 -0.05\r\nvn 0.31 0.95 -0.06\r\nvn 0.32 0.95 -0.06\r\nvn 0.33 0.94 -0.05\r\nvn 0.26 0.96 -0.1\r\nvn 0.26 0.96 -0.1\r\nvn -0.3 -0.95 0.06\r\nvn -0.36 -0.93 0.02\r\nvn -0.29 -0.95 0.07\r\nvn 0.28 0.96 -0.09\r\nvn 0.27 0.96 -0.1\r\nvn 0.29 0.96 -0.08\r\nvn 0.29 0.95 -0.07\r\nvn 0.23 0.96 -0.13\r\nvn 0.24 0.96 -0.11\r\nvn 0.28 0.96 -0.1\r\nvn 0.27 0.96 -0.11\r\nvn 0.27 0.96 -0.11\r\nvn 0.28 0.95 -0.1\r\nvn 0.22 0.96 -0.16\r\nvn 0.24 0.96 -0.14\r\nvn -0.75 -0.49 -0.45\r\nvn -0.7 -0.7 -0.13\r\nvn -0.33 -0.94 0.02\r\nvn -0.44 -0.89 -0.13\r\nvn -0.71 -0.7 0.08\r\nvn -0.33 -0.94 0.08\r\nvn 0.23 0.97 -0.09\r\nvn 0.22 0.97 -0.12\r\nvn -0.06 1 -0.07\r\nvn -0.11 0.97 -0.22\r\nvn -0.31 0.82 -0.48\r\nvn 0.11 0.96 -0.25\r\nvn -0.03 0.98 0.19\r\nvn -0.05 0.98 0.18\r\nvn -0.02 0.98 0.2\r\nvn -0.04 0.98 0.19\r\nvn 0.72 -0.5 0.49\r\nvn 0.24 -0.97 -0.01\r\nvn 0.24 -0.97 -0.02\r\nvn 0.7 -0.52 0.48\r\nvn -0.04 -0.97 -0.24\r\nvn -0.03 -0.97 -0.23\r\nvn -0.08 0.99 0.15\r\nvn -0.03 0.98 0.19\r\nvn -0.03 0.98 0.19\r\nvn -0.08 0.99 0.15\r\nvn -0.02 0.98 0.2\r\nvn -0.02 0.98 0.2\r\nvn 0.73 -0.48 0.48\r\nvn 0.24 -0.97 -0.02\r\nvn 0.24 -0.97 -0.01\r\nvn 0.72 -0.49 0.49\r\nvn -0.05 -0.97 -0.25\r\nvn -0.05 -0.97 -0.25\r\nvn -0.08 0.98 0.16\r\nvn -0.03 0.98 0.2\r\nvn -0.03 0.98 0.19\r\nvn -0.08 0.98 0.15\r\nvn -0.02 0.98 0.2\r\nvn -0.02 0.98 0.2\r\nvn 0.76 -0.47 0.45\r\nvn 0.25 -0.97 -0.03\r\nvn 0.25 -0.97 -0.02\r\nvn 0.74 -0.48 0.46\r\nvn -0.05 -0.97 -0.25\r\nvn -0.05 -0.97 -0.25\r\nvn -0.09 0.98 0.16\r\nvn -0.03 0.98 0.2\r\nvn -0.03 0.98 0.2\r\nvn -0.09 0.98 0.16\r\nvn -0.02 0.98 0.2\r\nvn -0.02 0.98 0.2\r\nvn 0.79 -0.46 0.4\r\nvn 0.27 -0.96 -0.04\r\nvn 0.26 -0.96 -0.04\r\nvn 0.78 -0.46 0.43\r\nvn -0.05 -0.97 -0.25\r\nvn -0.05 -0.97 -0.25\r\nvn -0.03 0.98 0.2\r\nvn -0.09 0.98 0.16\r\nvn -0.02 0.98 0.2\r\nvn 0.27 -0.96 -0.05\r\nvn 0.81 -0.45 0.38\r\nvn -0.05 -0.97 -0.24\r\nvn -0.01 0.99 0.16\r\nvn -0.08 0.99 0.14\r\nvn -0 0.99 0.16\r\nvn 0.28 -0.96 -0.06\r\nvn 0.88 -0.41 0.24\r\nvn -0.07 -0.98 -0.19\r\nvn -0.92 -0.28 -0.28\r\nvn -0.95 -0.16 -0.27\r\nvn -0.91 -0.29 -0.28\r\nvn -0.92 -0.3 -0.25\r\nvn -0.92 -0.29 -0.25\r\nvn -0.96 -0.17 -0.24\r\nvn -0.87 -0.07 -0.48\r\nvn -0.85 -0.18 -0.49\r\nvn -0.87 -0.21 -0.45\r\nvn -0.89 -0.09 -0.44\r\nvn -0.84 -0.21 -0.49\r\nvn -0.86 -0.23 -0.45\r\nvn -0.84 -0.04 -0.54\r\nvn -0.82 -0.15 -0.55\r\nvn -0.83 -0.17 -0.53\r\nvn -0.86 -0.06 -0.51\r\nvn -0.81 -0.18 -0.56\r\nvn -0.83 -0.19 -0.53\r\nvn -0.81 -0.02 -0.59\r\nvn -0.79 -0.12 -0.6\r\nvn -0.8 -0.13 -0.58\r\nvn -0.82 -0.03 -0.57\r\nvn -0.78 -0.15 -0.6\r\nvn -0.8 -0.16 -0.58\r\nvn -0.77 -0.1 -0.63\r\nvn -0.78 -0.11 -0.62\r\nvn -0.79 -0.01 -0.61\r\nvn -0.77 -0.14 -0.62\r\nvn -0.77 -0.1 -0.64\r\nvn -0.76 -0.1 -0.64\r\nvn -0.76 -0.09 -0.65\r\nvn -0.77 -0.1 -0.63\r\nvn -0.78 0 -0.62\r\nvn -0.71 -0.06 -0.7\r\nvn -0.73 -0.06 -0.69\r\nvn -0.64 0.23 -0.74\r\nvn -0.65 0.15 -0.75\r\nvn -0.6 0.33 -0.73\r\nvn -0.55 0.37 -0.75\r\nvn -0.6 0.33 -0.73\r\nvn -0.61 0.16 -0.77\r\nvn -0.79 0.17 -0.58\r\nvn -0.76 0.27 -0.59\r\nvn -0.76 0.27 -0.6\r\nvn -0.79 0.17 -0.59\r\nvn -0.81 0.17 -0.56\r\nvn -0.78 0.27 -0.57\r\nvn -0.77 0.27 -0.58\r\nvn -0.8 0.17 -0.58\r\nvn -0.84 0.19 -0.5\r\nvn -0.81 0.29 -0.51\r\nvn -0.79 0.28 -0.54\r\nvn -0.83 0.18 -0.53\r\nvn -0.88 0.21 -0.44\r\nvn -0.84 0.31 -0.45\r\nvn -0.82 0.3 -0.48\r\nvn -0.86 0.2 -0.47\r\nvn -0.91 0.23 -0.35\r\nvn -0.87 0.33 -0.37\r\nvn -0.85 0.32 -0.41\r\nvn -0.89 0.22 -0.4\r\nvn -0.93 0.25 -0.27\r\nvn -0.89 0.35 -0.29\r\nvn -0.88 0.34 -0.32\r\nvn -0.92 0.24 -0.31\r\nvn -0.94 0.26 -0.21\r\nvn -0.91 0.36 -0.22\r\nvn -0.9 0.35 -0.25\r\nvn -0.94 0.26 -0.24\r\nvn -0.95 0.27 -0.14\r\nvn -0.91 0.37 -0.16\r\nvn -0.91 0.37 -0.19\r\nvn -0.95 0.27 -0.18\r\nvn -0.95 0.29 -0.07\r\nvn -0.92 0.38 -0.09\r\nvn -0.92 0.38 -0.13\r\nvn -0.95 0.28 -0.11\r\nvn -0.95 0.3 -0.01\r\nvn -0.92 0.4 -0.03\r\nvn -0.92 0.39 -0.06\r\nvn -0.95 0.29 -0.04\r\nvn -0.95 0.31 0.06\r\nvn -0.91 0.4 0.04\r\nvn -0.92 0.4 0\r\nvn -0.95 0.31 0.02\r\nvn -0.94 0.32 0.13\r\nvn -0.9 0.41 0.11\r\nvn -0.91 0.41 0.08\r\nvn -0.94 0.32 0.1\r\nvn -0.92 0.33 0.2\r\nvn -0.89 0.42 0.18\r\nvn -0.9 0.42 0.15\r\nvn -0.93 0.33 0.17\r\nvn -0.9 0.34 0.28\r\nvn -0.87 0.43 0.25\r\nvn -0.88 0.43 0.21\r\nvn -0.91 0.34 0.24\r\nvn -0.87 0.35 0.35\r\nvn -0.84 0.43 0.33\r\nvn -0.85 0.43 0.29\r\nvn -0.88 0.35 0.32\r\nvn -0.83 0.36 0.43\r\nvn -0.8 0.44 0.4\r\nvn -0.82 0.44 0.37\r\nvn -0.85 0.35 0.39\r\nvn -0.78 0.44 0.44\r\nvn -0.81 0.36 0.46\r\nvn -0.75 -0.23 0.62\r\nvn -0.72 -0.35 0.61\r\nvn -0.7 -0.34 0.62\r\nvn -0.74 -0.22 0.64\r\nvn -0.79 -0.24 0.57\r\nvn -0.75 -0.36 0.55\r\nvn -0.73 -0.35 0.58\r\nvn -0.77 -0.23 0.6\r\nvn -0.75 -0.37 0.55\r\nvn -0.73 -0.36 0.58\r\nvn -0.83 -0.25 0.49\r\nvn -0.8 -0.37 0.48\r\nvn -0.77 -0.36 0.52\r\nvn -0.81 -0.24 0.53\r\nvn -0.79 -0.38 0.48\r\nvn -0.77 -0.37 0.52\r\nvn -0.81 -0.37 0.44\r\nvn -0.85 -0.25 0.45\r\nvn -0.81 -0.38 0.44\r\nvn -0.94 -0.26 0.22\r\nvn -0.9 -0.38 0.21\r\nvn -0.89 -0.38 0.25\r\nvn -0.93 -0.26 0.26\r\nvn -0.9 -0.39 0.21\r\nvn -0.89 -0.39 0.25\r\nvn -0.96 -0.25 0.15\r\nvn -0.92 -0.37 0.14\r\nvn -0.91 -0.38 0.17\r\nvn -0.95 -0.26 0.18\r\nvn -0.91 -0.38 0.14\r\nvn -0.91 -0.38 0.17\r\nvn -0.97 -0.24 0.07\r\nvn -0.93 -0.37 0.06\r\nvn -0.92 -0.37 0.1\r\nvn -0.96 -0.25 0.11\r\nvn -0.93 -0.37 0.06\r\nvn -0.92 -0.38 0.1\r\nvn -0.97 -0.23 -0.02\r\nvn -0.94 -0.35 -0.03\r\nvn -0.93 -0.36 0.02\r\nvn -0.97 -0.24 0.03\r\nvn -0.93 -0.36 -0.03\r\nvn -0.93 -0.37 0.02\r\nvn -0.97 -0.21 -0.1\r\nvn -0.94 -0.33 -0.11\r\nvn -0.94 -0.34 -0.07\r\nvn -0.97 -0.22 -0.06\r\nvn -0.93 -0.34 -0.11\r\nvn -0.93 -0.35 -0.07\r\nvn -0.97 -0.19 -0.18\r\nvn -0.93 -0.31 -0.19\r\nvn -0.94 -0.32 -0.15\r\nvn -0.97 -0.2 -0.14\r\nvn -0.93 -0.32 -0.19\r\nvn -0.93 -0.33 -0.15\r\nvn -0.93 -0.3 -0.22\r\nvn -0.96 -0.18 -0.21\r\nvn -0.92 -0.31 -0.22\r\nvn -0.32 0.95 0.05\r\nvn -0.32 0.95 0.05\r\nvn -0.32 0.95 0.04\r\nvn -0.68 0.73 -0.1\r\nvn -0.68 0.73 -0.08\r\nvn -0.69 0.72 -0.06\r\nvn -0.5 -0.83 -0.25\r\nvn -0.5 -0.82 -0.27\r\nvn -0.51 -0.83 -0.23\r\nvn -0.84 -0.47 -0.26\r\nvn -0.84 -0.46 -0.28\r\nvn -0.83 -0.46 -0.32\r\nvn -0.32 0.94 0.06\r\nvn -0.33 0.94 0.07\r\nvn -0.69 0.72 -0.04\r\nvn -0.7 0.72 -0.02\r\nvn -0.51 -0.84 -0.21\r\nvn -0.51 -0.84 -0.19\r\nvn -0.85 -0.48 -0.2\r\nvn -0.85 -0.48 -0.23\r\nvn -0.33 0.94 0.08\r\nvn -0.33 0.94 0.08\r\nvn -0.7 0.71 0.01\r\nvn -0.7 0.71 0.03\r\nvn -0.51 -0.84 -0.16\r\nvn -0.52 -0.85 -0.14\r\nvn -0.86 -0.5 -0.12\r\nvn -0.86 -0.49 -0.16\r\nvn -0.33 0.94 0.09\r\nvn -0.33 0.94 0.08\r\nvn -0.71 0.71 0.05\r\nvn -0.71 0.7 0.06\r\nvn -0.52 -0.85 -0.11\r\nvn -0.51 -0.85 -0.07\r\nvn -0.86 -0.51 -0.04\r\nvn -0.86 -0.5 -0.08\r\nvn -0.33 0.94 0.07\r\nvn -0.33 0.94 0.07\r\nvn -0.71 0.7 0.07\r\nvn -0.71 0.7 0.09\r\nvn -0.51 -0.86 -0.03\r\nvn -0.51 -0.86 0\r\nvn -0.85 -0.52 0.05\r\nvn -0.86 -0.51 0.01\r\nvn -0.33 0.94 0.08\r\nvn -0.33 0.94 0.09\r\nvn -0.7 0.7 0.12\r\nvn -0.7 0.7 0.14\r\nvn -0.51 -0.86 0.03\r\nvn -0.5 -0.86 0.05\r\nvn -0.84 -0.52 0.12\r\nvn -0.85 -0.52 0.09\r\nvn -0.33 0.94 0.1\r\nvn -0.33 0.94 0.1\r\nvn -0.7 0.7 0.16\r\nvn -0.69 0.7 0.18\r\nvn -0.5 -0.86 0.07\r\nvn -0.49 -0.87 0.1\r\nvn -0.83 -0.53 0.19\r\nvn -0.84 -0.53 0.15\r\nvn -0.33 0.94 0.1\r\nvn -0.33 0.94 0.1\r\nvn -0.69 0.7 0.2\r\nvn -0.68 0.7 0.22\r\nvn -0.48 -0.87 0.13\r\nvn -0.47 -0.87 0.17\r\nvn -0.8 -0.53 0.28\r\nvn -0.82 -0.53 0.23\r\nvn -0.33 0.94 0.09\r\nvn -0.33 0.94 0.1\r\nvn -0.68 0.7 0.24\r\nvn -0.67 0.7 0.26\r\nvn -0.45 -0.87 0.22\r\nvn -0.44 -0.86 0.25\r\nvn -0.76 -0.53 0.38\r\nvn -0.78 -0.53 0.33\r\nvn -0.33 0.94 0.1\r\nvn -0.32 0.94 0.11\r\nvn -0.66 0.7 0.29\r\nvn -0.64 0.7 0.31\r\nvn -0.42 -0.86 0.28\r\nvn -0.41 -0.86 0.3\r\nvn -0.72 -0.52 0.45\r\nvn -0.74 -0.52 0.42\r\nvn -0.32 0.94 0.13\r\nvn -0.31 0.94 0.14\r\nvn -0.62 0.7 0.34\r\nvn -0.61 0.7 0.37\r\nvn -0.4 -0.86 0.32\r\nvn -0.39 -0.86 0.34\r\nvn -0.68 -0.51 0.52\r\nvn -0.7 -0.52 0.49\r\nvn -0.3 0.94 0.16\r\nvn -0.29 0.94 0.16\r\nvn -0.59 0.71 0.39\r\nvn -0.57 0.71 0.41\r\nvn -0.38 -0.86 0.35\r\nvn -0.67 -0.51 0.54\r\nvn -0.28 0.94 0.17\r\nvn -0.27 0.95 0.17\r\nvn -0.56 0.71 0.43\r\nvn -0.25 0.95 0.17\r\nvn -0.22 0.96 0.15\r\nvn -0.51 -0.7 0.5\r\nvn -0.52 -0.7 0.48\r\nvn -0.54 -0.7 0.47\r\nvn -0.56 -0.7 0.44\r\nvn -0.2 0.98 0.05\r\nvn -0.2 0.98 0.04\r\nvn -0.57 -0.71 0.42\r\nvn -0.59 -0.71 0.39\r\nvn -0.79 -0.33 0.52\r\nvn -0.81 -0.34 0.48\r\nvn -0.2 0.98 0.03\r\nvn -0.2 0.98 0.02\r\nvn -0.6 -0.71 0.37\r\nvn -0.61 -0.71 0.35\r\nvn -0.83 -0.34 0.45\r\nvn -0.84 -0.34 0.42\r\nvn -0.21 0.98 0.01\r\nvn -0.21 0.98 -0.01\r\nvn -0.62 -0.71 0.33\r\nvn -0.62 -0.71 0.32\r\nvn -0.86 -0.34 0.39\r\nvn -0.87 -0.35 0.36\r\nvn -0.21 0.98 -0.03\r\nvn -0.22 0.97 -0.05\r\nvn -0.63 -0.72 0.3\r\nvn -0.63 -0.72 0.29\r\nvn -0.88 -0.35 0.33\r\nvn -0.89 -0.35 0.3\r\nvn -0.22 0.97 -0.07\r\nvn -0.22 0.97 -0.09\r\nvn -0.64 -0.72 0.27\r\nvn -0.64 -0.72 0.25\r\nvn -0.89 -0.36 0.27\r\nvn -0.9 -0.36 0.24\r\nvn -0.22 0.97 -0.11\r\nvn -0.22 0.97 -0.12\r\nvn -0.65 -0.73 0.23\r\nvn -0.65 -0.73 0.21\r\nvn -0.91 -0.37 0.21\r\nvn -0.91 -0.37 0.17\r\nvn -0.22 0.97 -0.13\r\nvn -0.22 0.96 -0.14\r\nvn -0.65 -0.73 0.19\r\nvn -0.65 -0.74 0.17\r\nvn -0.91 -0.38 0.14\r\nvn -0.92 -0.38 0.12\r\nvn -0.22 0.96 -0.16\r\nvn -0.22 0.96 -0.17\r\nvn -0.65 -0.74 0.16\r\nvn -0.66 -0.74 0.15\r\nvn -0.92 -0.39 0.1\r\nvn -0.92 -0.39 0.07\r\nvn -0.22 0.96 -0.19\r\nvn -0.22 0.95 -0.2\r\nvn -0.65 -0.75 0.13\r\nvn -0.65 -0.75 0.1\r\nvn -0.92 -0.4 0.04\r\nvn -0.91 -0.41 0\r\nvn -0.22 0.95 -0.21\r\nvn -0.21 0.95 -0.22\r\nvn -0.65 -0.75 0.08\r\nvn -0.65 -0.76 0.06\r\nvn -0.91 -0.41 -0.03\r\nvn -0.91 -0.42 -0.05\r\nvn -0.21 0.95 -0.23\r\nvn -0.21 0.95 -0.24\r\nvn -0.65 -0.76 0.05\r\nvn -0.64 -0.76 0.03\r\nvn -0.9 -0.42 -0.08\r\nvn -0.9 -0.43 -0.1\r\nvn -0.21 0.95 -0.25\r\nvn -0.2 0.94 -0.27\r\nvn -0.64 -0.77 0.01\r\nvn -0.63 -0.77 -0.01\r\nvn -0.89 -0.43 -0.14\r\nvn -0.88 -0.44 -0.17\r\nvn -0.2 0.94 -0.29\r\nvn -0.19 0.93 -0.3\r\nvn -0.63 -0.78 -0.03\r\nvn -0.62 -0.78 -0.05\r\nvn -0.87 -0.45 -0.21\r\nvn -0.85 -0.46 -0.24\r\nvn -0.19 0.93 -0.32\r\nvn -0.18 0.93 -0.34\r\nvn -0.61 -0.79 -0.07\r\nvn -0.6 -0.8 -0.09\r\nvn -0.84 -0.47 -0.27\r\nvn -0.82 -0.48 -0.3\r\nvn -0.17 0.92 -0.35\r\nvn -0.17 0.92 -0.35\r\nvn -0.59 -0.8 -0.11\r\nvn -0.58 -0.81 -0.13\r\nvn -0.81 -0.49 -0.33\r\nvn -0.79 -0.49 -0.36\r\nvn -0.17 0.92 -0.36\r\nvn -0.16 0.92 -0.36\r\nvn -0.57 -0.81 -0.15\r\nvn -0.56 -0.82 -0.15\r\nvn -0.78 -0.5 -0.38\r\nvn -0.78 -0.5 -0.38\r\nvn -0.16 0.92 -0.37\r\nvn -0.16 0.92 -0.37\r\nvn -0.55 -0.82 -0.14\r\nvn -0.55 -0.83 -0.14\r\nvn -0.77 -0.5 -0.39\r\nvn -0.16 0.91 -0.38\r\nvn -0.16 0.91 -0.39\r\nvn -0.54 -0.83 -0.14\r\nvn -0.58 -0.77 -0.25\r\nvn -0.15 0.9 -0.41\r\nvn -0.16 0.88 -0.46\r\nvn -0.67 -0.56 -0.49\r\nvn -0.72 -0.11 -0.68\r\nvn -0.7 -0.08 -0.71\r\nvn -0.66 0.12 -0.74\r\nvn -0.26 0.79 -0.56\r\nvn -0.49 0.54 -0.69\r\nvn -0.54 0.39 -0.74\r\nvn -0.52 0.47 -0.71\r\nvn -0.4 -0.78 -0.48\r\nvn -0.4 -0.78 -0.49\r\nvn -0.69 -0.36 -0.63\r\nvn -0.69 -0.36 -0.63\r\nvn -0.27 0.96 -0.01\r\nvn -0.28 0.96 -0.01\r\nvn -0.4 -0.77 -0.49\r\nvn -0.41 -0.77 -0.49\r\nvn -0.69 -0.36 -0.63\r\nvn -0.69 -0.35 -0.63\r\nvn -0.28 0.96 -0.01\r\nvn -0.29 0.96 0\r\nvn -0.58 0.77 -0.28\r\nvn -0.59 0.76 -0.26\r\nvn -0.41 -0.77 -0.48\r\nvn -0.42 -0.78 -0.47\r\nvn -0.71 -0.37 -0.6\r\nvn -0.7 -0.36 -0.61\r\nvn -0.3 0.95 0.01\r\nvn -0.3 0.95 0.02\r\nvn -0.6 0.76 -0.24\r\nvn -0.62 0.75 -0.22\r\nvn -0.42 -0.78 -0.46\r\nvn -0.43 -0.78 -0.45\r\nvn -0.74 -0.38 -0.55\r\nvn -0.72 -0.38 -0.58\r\nvn -0.31 0.95 0.03\r\nvn -0.31 0.95 0.03\r\nvn -0.63 0.75 -0.2\r\nvn -0.64 0.74 -0.18\r\nvn -0.44 -0.79 -0.43\r\nvn -0.45 -0.79 -0.41\r\nvn -0.77 -0.41 -0.5\r\nvn -0.75 -0.39 -0.53\r\nvn -0.32 0.95 0.04\r\nvn -0.32 0.95 0.04\r\nvn -0.65 0.74 -0.16\r\nvn -0.67 0.73 -0.14\r\nvn -0.46 -0.8 -0.38\r\nvn -0.48 -0.81 -0.34\r\nvn -0.8 -0.43 -0.41\r\nvn -0.79 -0.42 -0.46\r\nvn -0.32 0.95 0.04\r\nvn -0.67 0.73 -0.11\r\nvn -0.49 -0.82 -0.31\r\nvn -0.82 -0.45 -0.36\r\nvn -0.94 0.25 -0.21\r\nvn -0.94 0.26 -0.24\r\nvn -0.95 0.25 -0.19\r\nvn -0.91 -0.31 -0.28\r\nvn -0.92 -0.31 -0.25\r\nvn -0.9 -0.3 -0.32\r\nvn -0.89 -0.33 -0.32\r\nvn -0.9 -0.33 -0.28\r\nvn -0.91 -0.34 -0.25\r\nvn -0.92 0.27 -0.28\r\nvn -0.91 0.28 -0.32\r\nvn -0.89 -0.29 -0.36\r\nvn -0.87 -0.27 -0.41\r\nvn -0.86 -0.3 -0.41\r\nvn -0.88 -0.31 -0.36\r\nvn -0.89 0.29 -0.36\r\nvn -0.87 0.31 -0.4\r\nvn -0.85 -0.26 -0.46\r\nvn -0.83 -0.24 -0.5\r\nvn -0.82 -0.27 -0.5\r\nvn -0.84 -0.29 -0.46\r\nvn -0.85 0.32 -0.43\r\nvn -0.83 0.33 -0.45\r\nvn -0.82 -0.23 -0.53\r\nvn -0.8 -0.22 -0.56\r\nvn -0.79 -0.25 -0.56\r\nvn -0.81 -0.26 -0.53\r\nvn -0.81 0.34 -0.48\r\nvn -0.79 0.34 -0.5\r\nvn -0.79 -0.21 -0.58\r\nvn -0.77 -0.2 -0.6\r\nvn -0.76 -0.23 -0.6\r\nvn -0.78 -0.24 -0.58\r\nvn -0.78 0.35 -0.52\r\nvn -0.76 -0.19 -0.62\r\nvn -0.75 -0.23 -0.62\r\nvn -0.77 0.36 -0.53\r\nvn -0.66 0.09 -0.75\r\nvn -0.63 0.16 -0.76\r\nvn -0.56 0.39 -0.73\r\nvn -0.54 0.37 -0.76\r\nvn -0.83 -0.09 -0.55\r\nvn -0.84 -0.09 -0.54\r\nvn -0.84 -0.08 -0.53\r\nvn -0.85 -0.08 -0.51\r\nvn -0.87 -0.07 -0.49\r\nvn -0.89 -0.06 -0.46\r\nvn -0.9 -0.05 -0.42\r\nvn -0.92 -0.04 -0.39\r\nvn -0.94 -0.03 -0.35\r\nvn -0.95 -0.02 -0.3\r\nvn -0.97 -0.01 -0.26\r\nvn -0.98 -0 -0.22\r\nvn -0.98 0 -0.19\r\nvn -0.99 0.01 -0.16\r\nvn -0.99 0.02 -0.13\r\nvn -1 0.02 -0.1\r\nvn -1 0.03 -0.06\r\nvn -1 0.04 -0.02\r\nvn -1 0.04 0.01\r\nvn -1 0.05 0.04\r\nvn -1 0.06 0.07\r\nvn -0.99 0.06 0.11\r\nvn -0.99 0.07 0.15\r\nvn -0.98 0.07 0.19\r\nvn -0.97 0.08 0.22\r\nvn -0.96 0.08 0.25\r\nvn -0.95 0.09 0.29\r\nvn -0.94 0.09 0.32\r\nvn -0.93 0.1 0.36\r\nvn -0.91 0.1 0.4\r\nvn -0.89 0.1 0.44\r\nvn -0.87 0.11 0.47\r\nvn -0.85 0.11 0.51\r\nvn -0.74 0 0.67\r\nvn -0.73 -0.11 0.68\r\nvn -0.75 0.21 0.62\r\nvn -0.77 0.21 0.61\r\nvn -0.79 0.21 0.58\r\nvn -0.81 0.2 0.55\r\nvn -0.73 -0.36 0.58\r\nvn -0.75 -0.37 0.55\r\nvn -0.74 -0.39 0.55\r\nvn -0.72 -0.38 0.58\r\nvn -0.83 0.2 0.51\r\nvn -0.86 0.2 0.47\r\nvn -0.77 -0.37 0.52\r\nvn -0.79 -0.37 0.48\r\nvn -0.79 -0.39 0.48\r\nvn -0.76 -0.39 0.51\r\nvn -0.88 0.19 0.44\r\nvn -0.9 0.19 0.4\r\nvn -0.81 -0.38 0.44\r\nvn -0.83 -0.38 0.4\r\nvn -0.83 -0.4 0.4\r\nvn -0.81 -0.4 0.44\r\nvn -0.92 0.18 0.36\r\nvn -0.93 0.18 0.31\r\nvn -0.85 -0.38 0.36\r\nvn -0.87 -0.38 0.3\r\nvn -0.86 -0.4 0.3\r\nvn -0.85 -0.4 0.35\r\nvn -0.94 0.18 0.27\r\nvn -0.95 0.19 0.23\r\nvn -0.89 -0.38 0.25\r\nvn -0.9 -0.38 0.21\r\nvn -0.89 -0.4 0.21\r\nvn -0.88 -0.4 0.25\r\nvn -0.96 0.19 0.2\r\nvn -0.97 0.19 0.17\r\nvn -0.91 -0.38 0.17\r\nvn -0.92 -0.38 0.14\r\nvn -0.91 -0.4 0.14\r\nvn -0.9 -0.4 0.17\r\nvn -0.97 0.19 0.13\r\nvn -0.98 0.2 0.09\r\nvn -0.92 -0.37 0.1\r\nvn -0.93 -0.37 0.06\r\nvn -0.92 -0.39 0.06\r\nvn -0.91 -0.39 0.1\r\nvn -0.98 0.2 0.06\r\nvn -0.98 0.2 0.02\r\nvn -0.93 -0.37 0.02\r\nvn -0.93 -0.36 -0.03\r\nvn -0.92 -0.38 -0.03\r\nvn -0.92 -0.38 0.02\r\nvn -0.98 0.21 -0.01\r\nvn -0.97 0.22 -0.05\r\nvn -0.93 -0.35 -0.07\r\nvn -0.93 -0.35 -0.11\r\nvn -0.92 -0.37 -0.11\r\nvn -0.93 -0.37 -0.07\r\nvn -0.97 0.23 -0.08\r\nvn -0.96 0.23 -0.12\r\nvn -0.93 -0.34 -0.15\r\nvn -0.93 -0.33 -0.19\r\nvn -0.92 -0.35 -0.19\r\nvn -0.92 -0.36 -0.15\r\nvn -0.96 0.24 -0.16\r\nvn -0.92 -0.32 -0.22\r\nvn -0.91 -0.35 -0.22\r\nvn 0.89 0.34 0.31\r\nvn 0.9 0.34 0.28\r\nvn 0.93 0.26 0.28\r\nvn 0.92 0.25 0.31\r\nvn 0.9 0.35 0.25\r\nvn 0.93 0.27 0.25\r\nvn 0.91 0.36 0.22\r\nvn 0.94 0.27 0.22\r\nvn 0.91 0.36 0.19\r\nvn 0.94 0.28 0.18\r\nvn 0.92 0.37 0.15\r\nvn 0.95 0.29 0.14\r\nvn 0.92 0.38 0.11\r\nvn 0.95 0.3 0.1\r\nvn 0.92 0.39 0.07\r\nvn 0.95 0.3 0.06\r\nvn 0.92 0.39 0.03\r\nvn 0.95 0.31 0.02\r\nvn 0.92 0.4 -0.02\r\nvn 0.95 0.31 -0.02\r\nvn 0.91 0.4 -0.06\r\nvn 0.95 0.32 -0.07\r\nvn 0.91 0.41 -0.1\r\nvn 0.94 0.32 -0.11\r\nvn 0.9 0.41 -0.14\r\nvn 0.93 0.33 -0.14\r\nvn 0.9 0.41 -0.17\r\nvn 0.93 0.33 -0.18\r\nvn 0.89 0.41 -0.21\r\nvn 0.92 0.33 -0.21\r\nvn 0.88 0.41 -0.25\r\nvn 0.91 0.33 -0.26\r\nvn 0.86 0.42 -0.3\r\nvn 0.89 0.33 -0.31\r\nvn 0.84 0.41 -0.35\r\nvn 0.87 0.33 -0.36\r\nvn 0.82 0.41 -0.4\r\nvn 0.85 0.33 -0.41\r\nvn 0.8 0.41 -0.44\r\nvn 0.83 0.33 -0.45\r\nvn 0.78 0.41 -0.47\r\nvn 0.81 0.33 -0.49\r\nvn 0.76 0.4 -0.51\r\nvn 0.79 0.32 -0.52\r\nvn 0.74 0.4 -0.54\r\nvn 0.77 0.32 -0.56\r\nvn 0.72 0.4 -0.57\r\nvn 0.74 0.31 -0.59\r\nvn 0.7 0.39 -0.6\r\nvn 0.72 0.31 -0.62\r\nvn 0.68 0.39 -0.62\r\nvn 0.71 0.3 -0.64\r\nvn 0.67 0.39 -0.63\r\nvn 0.7 0.3 -0.65\r\nvn 0.71 0.4 -0.58\r\nvn 0.74 0.3 -0.59\r\nvn 0.91 0.41 -0.11\r\nvn 0.97 0.21 -0.12\r\nvn 0.8 0.26 0.55\r\nvn 0.85 0.1 0.52\r\nvn 0.69 0.19 0.7\r\nvn 0.69 -0.01 0.73\r\nvn 0.73 0.22 0.65\r\nvn 0.19 -0.18 0.96\r\nvn -0.53 -0.41 0.75\r\nvn -0.65 -0.39 0.65\r\nvn -0.67 -0.4 0.62\r\nvn -0.68 -0.4 0.61\r\nvn -0.7 -0.41 0.59\r\nvn -0.71 -0.41 0.57\r\nvn -0.73 -0.41 0.54\r\nvn -0.75 -0.42 0.51\r\nvn -0.77 -0.42 0.47\r\nvn -0.79 -0.42 0.44\r\nvn -0.81 -0.43 0.39\r\nvn -0.83 -0.43 0.35\r\nvn -0.85 -0.43 0.3\r\nvn -0.87 -0.43 0.25\r\nvn -0.88 -0.43 0.2\r\nvn -0.89 -0.42 0.17\r\nvn -0.9 -0.42 0.13\r\nvn -0.9 -0.42 0.1\r\nvn -0.91 -0.42 0.06\r\nvn -0.91 -0.41 0.01\r\nvn -0.91 -0.41 -0.03\r\nvn -0.91 -0.4 -0.07\r\nvn -0.91 -0.39 -0.11\r\nvn -0.91 -0.39 -0.15\r\nvn -0.91 -0.38 -0.19\r\nvn -0.9 -0.37 -0.22\r\nvn -0.9 -0.36 -0.25\r\nvn -0.89 -0.36 -0.28\r\nvn -0.88 -0.35 -0.32\r\nvn -0.87 -0.34 -0.36\r\nvn -0.85 -0.32 -0.41\r\nvn -0.83 -0.31 -0.46\r\nvn -0.82 -0.29 -0.5\r\nvn -0.8 -0.28 -0.53\r\nvn -0.78 -0.27 -0.56\r\nvn -0.77 -0.26 -0.58\r\nvn -0.76 -0.25 -0.6\r\nvn -0.74 -0.24 -0.62\r\nvn -0.73 -0.24 -0.63\r\nvn -0.73 -0.24 -0.64\r\nvn -0.73 -0.24 -0.64\r\nvn -0.74 -0.24 -0.63\r\nvn -0.72 -0.23 -0.65\r\nvn -0.68 -0.21 -0.7\r\nvn -0.54 -0.18 -0.82\r\nvn 0.76 0.4 -0.51\r\nvn 0.26 0.13 -0.96\r\nvn 0.77 0.41 -0.49\r\nvn 0.8 0.25 -0.55\r\nvn 0.85 0.42 -0.33\r\nvn 0.89 0.28 -0.36\r\nvn 0.91 0.36 0.21\r\nvn 0.97 0.19 0.17\r\nvn 0.75 0.23 0.63\r\nvn 0.78 0.12 0.61\r\nvn 0.7 0.2 0.68\r\nvn 0.73 0.09 0.68\r\nvn 0.71 0.21 0.67\r\nvn 0.74 0.1 0.67\r\nvn 0.72 0.21 0.66\r\nvn 0.75 0.12 0.66\r\nvn 0.73 0.22 0.65\r\nvn 0.75 0.13 0.64\r\nvn 0.74 0.22 0.63\r\nvn 0.76 0.13 0.63\r\nvn 0.75 0.23 0.62\r\nvn 0.77 0.14 0.62\r\nvn 0.76 0.24 0.6\r\nvn 0.78 0.15 0.6\r\nvn 0.77 0.25 0.58\r\nvn 0.8 0.16 0.58\r\nvn 0.79 0.26 0.56\r\nvn 0.81 0.17 0.55\r\nvn 0.81 0.27 0.53\r\nvn 0.83 0.18 0.53\r\nvn 0.82 0.28 0.5\r\nvn 0.85 0.19 0.49\r\nvn 0.84 0.29 0.45\r\nvn 0.87 0.21 0.45\r\nvn 0.86 0.31 0.41\r\nvn 0.89 0.22 0.4\r\nvn 0.88 0.32 0.36\r\nvn 0.9 0.24 0.35\r\nvn -0.7 -0.38 -0.6\r\nvn -0.24 -0.96 -0.14\r\nvn -0.35 -0.94 -0.03\r\nvn -0.81 -0.37 -0.46\r\nvn 0.02 -1 0.1\r\nvn -0.08 -0.98 0.2\r\nvn -0.48 -0.66 0.59\r\nvn -0.8 -0.53 0.28\r\nvn -0.96 -0.15 -0.22\r\nvn 0.33 -0.86 0.39\r\nvn 0.29 -0.89 0.36\r\nvn 0.29 -0.89 0.35\r\nvn 0.33 -0.86 0.39\r\nvn 0.28 -0.89 0.35\r\nvn 0.28 -0.9 0.35\r\nvn 0.29 -0.9 0.34\r\nvn 0.3 -0.89 0.35\r\nvn 0.34 -0.86 0.38\r\nvn 0.34 -0.94 0.04\r\nvn 0.29 -0.95 0.08\r\nvn 0.28 -0.95 0.1\r\nvn 0.34 -0.94 0.05\r\nvn 0.28 -0.96 0.09\r\nvn 0.27 -0.96 0.1\r\nvn 0.28 -0.95 0.1\r\nvn 0.29 -0.95 0.08\r\nvn 0.35 -0.94 0.01\r\nvn 0.12 0.98 0.14\r\nvn 0.08 0.98 0.18\r\nvn 0.08 0.98 0.18\r\nvn 0.11 0.98 0.14\r\nvn 0.07 0.98 0.19\r\nvn 0.07 0.98 0.18\r\nvn 0.06 0.98 0.18\r\nvn 0.06 0.98 0.18\r\nvn 0.1 0.98 0.14\r\nvn -0.97 -0.18 0.14\r\nvn -0.86 -0.41 -0.32\r\nvn -0.39 -0.91 -0.12\r\nvn -0.8 -0.49 0.35\r\nvn -0.65 -0.46 -0.61\r\nvn -0.16 -0.93 -0.34\r\nvn 0.01 -0.97 -0.23\r\nvn -0.23 -0.97 -0.03\r\nvn -0.68 -0.55 0.48\r\nvn 0.09 0.98 0.16\r\nvn 0.03 0.98 0.19\r\nvn 0.03 0.98 0.18\r\nvn 0.09 0.98 0.16\r\nvn 0.02 0.98 0.2\r\nvn 0.01 0.98 0.19\r\nvn 0.01 0.98 0.18\r\nvn 0.02 0.98 0.17\r\nvn 0.08 0.99 0.15\r\nvn -0.83 -0.44 0.35\r\nvn -0.28 -0.96 -0.06\r\nvn -0.28 -0.96 -0.06\r\nvn -0.85 -0.43 0.32\r\nvn 0.05 -0.97 -0.23\r\nvn 0.06 -0.97 -0.22\r\nvn 0.07 -0.98 -0.21\r\nvn -0.28 -0.96 -0.06\r\nvn -0.86 -0.42 0.28\r\nvn 0.94 -0.15 -0.3\r\nvn 0.91 -0.27 -0.31\r\nvn 0.9 -0.25 -0.36\r\nvn 0.93 -0.13 -0.35\r\nvn 0.91 -0.28 -0.31\r\nvn 0.9 -0.27 -0.36\r\nvn 0.88 -0.25 -0.41\r\nvn 0.89 -0.23 -0.4\r\nvn 0.91 -0.12 -0.39\r\nvn 0.71 0.14 -0.68\r\nvn 0.69 0.24 -0.68\r\nvn 0.72 0.25 -0.64\r\nvn 0.76 0.15 -0.64\r\nvn 0.65 0.34 -0.68\r\nvn 0.68 0.36 -0.64\r\nvn 0.7 0.37 -0.61\r\nvn 0.74 0.26 -0.61\r\nvn 0.78 0.16 -0.61\r\nvn 0.79 0.36 0.5\r\nvn 0.76 0.44 0.47\r\nvn 0.74 0.44 0.51\r\nvn 0.76 0.37 0.53\r\nvn 0.74 0.49 0.46\r\nvn 0.72 0.49 0.49\r\nvn 0.71 0.48 0.52\r\nvn 0.72 0.44 0.53\r\nvn 0.74 0.37 0.56\r\nvn 0.71 0.44 0.55\r\nvn 0.73 0.37 0.58\r\nvn 0.69 0.48 0.54\r\nvn 0.69 0.48 0.55\r\nvn 0.7 0.44 0.56\r\nvn 0.72 0.36 0.59\r\nvn 0.7 0.43 0.57\r\nvn 0.72 0.33 0.61\r\nvn 0.68 0.48 0.55\r\nvn 0.68 0.48 0.56\r\nvn 0.7 0.43 0.57\r\nvn 0.73 0.28 0.62\r\nvn 0.7 -0.22 0.68\r\nvn 0.67 -0.34 0.66\r\nvn 0.68 -0.34 0.65\r\nvn 0.71 -0.22 0.67\r\nvn 0.67 -0.34 0.66\r\nvn 0.67 -0.35 0.65\r\nvn 0.69 -0.35 0.63\r\nvn 0.69 -0.34 0.64\r\nvn 0.72 -0.22 0.65\r\nvn 0.87 -0.26 0.41\r\nvn 0.83 -0.38 0.4\r\nvn 0.85 -0.38 0.36\r\nvn 0.89 -0.26 0.37\r\nvn 0.83 -0.38 0.4\r\nvn 0.85 -0.39 0.35\r\nvn 0.87 -0.39 0.3\r\nvn 0.87 -0.38 0.3\r\nvn 0.91 -0.26 0.31\r\nvn 0.03 -0.99 0.1\r\nvn 0.37 -0.86 0.36\r\nvn 0.36 -0.86 0.37\r\nvn 0.03 -0.99 0.1\r\nvn 0.65 -0.51 0.56\r\nvn 0.64 -0.51 0.58\r\nvn 0.63 -0.5 0.59\r\nvn 0.35 -0.86 0.38\r\nvn 0.26 -0.86 0.44\r\nvn 0.59 -0.5 0.64\r\nvn 0.55 -0.45 0.7\r\nvn -0.1 -0.61 0.78\r\nvn 0.5 -0.7 0.51\r\nvn 0.48 -0.71 0.51\r\nvn 0.69 -0.35 0.64\r\nvn 0.65 -0.42 0.64\r\nvn 0.61 -0.5 0.61\r\nvn 0.46 -0.73 0.51\r\nvn -0.21 0.94 -0.27\r\nvn 0.16 0.99 0.05\r\nvn 0.19 0.98 0.08\r\nvn -0.22 0.93 -0.28\r\nvn 0.58 0.68 0.45\r\nvn 0.59 0.66 0.46\r\nvn 0.6 0.66 0.46\r\nvn 0.19 0.98 0.07\r\nvn -0.23 0.93 -0.29\r\nvn 0.19 0.98 0.07\r\nvn -0.24 0.93 -0.29\r\nvn 0.6 0.66 0.45\r\nvn 0.61 0.67 0.43\r\nvn 0.19 0.98 0.06\r\nvn -0.24 0.93 -0.28\r\nvn 0.28 0.96 -0.02\r\nvn 0.28 0.96 -0.02\r\nvn 0.57 0.77 -0.29\r\nvn 0.55 0.77 -0.31\r\nvn 0.54 0.78 -0.32\r\nvn 0.29 0.96 -0.02\r\nvn -0.19 -0.38 -0.9\r\nvn 0.23 -0.77 -0.6\r\nvn 0.57 -0.24 -0.79\r\nvn 0.62 -0.33 -0.72\r\nvn 0.68 -0.35 -0.65\r\nvn 0.38 -0.78 -0.5\r\nvn 0.08 0.99 0.15\r\nvn 0.27 0.96 -0.01\r\nvn 0.28 0.96 -0.01\r\nvn 0.09 0.99 0.14\r\nvn 0.57 0.77 -0.29\r\nvn 0.57 0.77 -0.28\r\nvn 0.78 0.01 -0.62\r\nvn 0.76 0.36 -0.54\r\nvn 0.76 0.36 -0.54\r\nvn 0.78 0.01 -0.63\r\nvn 0.57 0.77 -0.29\r\nvn 0.57 0.77 -0.29\r\nvn 0.77 0.36 -0.53\r\nvn 0.78 0 -0.62\r\nvn 0.76 -0.13 -0.63\r\nvn 0.75 -0.18 -0.64\r\nvn 0.74 -0.18 -0.64\r\nvn 0.76 -0.13 -0.64\r\nvn 0.74 -0.22 -0.64\r\nvn 0.73 -0.22 -0.64\r\nvn 0.74 -0.22 -0.64\r\nvn 0.75 -0.18 -0.64\r\nvn 0.76 -0.13 -0.64\r\nvn 0.75 -0.18 -0.63\r\nvn 0.76 -0.14 -0.63\r\nvn 0.74 -0.22 -0.63\r\nvn 0.73 -0.21 -0.65\r\nvn 0.74 -0.17 -0.65\r\nvn 0.75 -0.12 -0.65\r\nvn 0.72 0.03 -0.69\r\nvn 0.71 0.37 -0.6\r\nvn 0.73 0.37 -0.58\r\nvn 0.74 0.03 -0.67\r\nvn 0.76 0.36 -0.55\r\nvn 0.77 0.01 -0.64\r\nvn 0.71 -0.16 -0.69\r\nvn 0.72 -0.1 -0.69\r\nvn 0.69 -0.19 -0.7\r\nvn 0.42 -0.25 -0.87\r\nvn 0.66 -0.26 -0.71\r\nvn 0.7 -0.09 -0.71\r\nvn 0.82 -0.09 -0.56\r\nvn 0.8 -0.1 -0.59\r\nvn 0.77 -0.5 -0.4\r\nvn 0.75 -0.51 -0.42\r\nvn 0.75 -0.37 -0.55\r\nvn 0.74 -0.02 -0.67\r\nvn 0.61 0.43 -0.67\r\nvn 0.63 0.44 -0.63\r\nvn 0.53 0.54 -0.65\r\nvn 0.54 0.57 -0.61\r\nvn 0.56 0.58 -0.58\r\nvn 0.65 0.45 -0.61\r\nvn 0.66 0.46 -0.59\r\nvn 0.71 0.37 -0.6\r\nvn 0.57 0.59 -0.57\r\nvn 0.58 0.59 -0.56\r\nvn 0.67 0.46 -0.58\r\nvn 0.71 0.37 -0.59\r\nvn 0.67 0.47 -0.58\r\nvn 0.72 0.38 -0.58\r\nvn 0.58 0.6 -0.55\r\nvn 0.59 0.6 -0.54\r\nvn 0.68 0.47 -0.56\r\nvn 0.73 0.38 -0.57\r\nvn 0.69 0.48 -0.54\r\nvn 0.74 0.39 -0.54\r\nvn 0.6 0.61 -0.52\r\nvn 0.61 0.61 -0.5\r\nvn 0.7 0.49 -0.52\r\nvn 0.76 0.4 -0.52\r\nvn 0.72 0.5 -0.49\r\nvn 0.77 0.41 -0.49\r\nvn 0.62 0.62 -0.48\r\nvn 0.63 0.63 -0.45\r\nvn 0.73 0.51 -0.46\r\nvn 0.79 0.42 -0.46\r\nvn 0.75 0.51 -0.42\r\nvn 0.8 0.43 -0.42\r\nvn 0.65 0.64 -0.42\r\nvn 0.66 0.65 -0.38\r\nvn 0.76 0.52 -0.38\r\nvn 0.82 0.44 -0.38\r\nvn 0.77 0.53 -0.34\r\nvn 0.83 0.45 -0.34\r\nvn 0.67 0.66 -0.34\r\nvn 0.68 0.66 -0.31\r\nvn 0.78 0.54 -0.31\r\nvn 0.84 0.45 -0.3\r\nvn 0.79 0.55 -0.27\r\nvn 0.85 0.46 -0.27\r\nvn 0.69 0.67 -0.28\r\nvn 0.69 0.67 -0.26\r\nvn 0.8 0.55 -0.25\r\nvn 0.85 0.46 -0.24\r\nvn 0.8 0.56 -0.22\r\nvn 0.86 0.47 -0.21\r\nvn 0.7 0.68 -0.23\r\nvn 0.7 0.68 -0.21\r\nvn 0.81 0.56 -0.19\r\nvn 0.86 0.47 -0.18\r\nvn 0.81 0.56 -0.16\r\nvn 0.87 0.48 -0.15\r\nvn 0.71 0.68 -0.18\r\nvn 0.71 0.69 -0.15\r\nvn 0.81 0.57 -0.13\r\nvn 0.87 0.48 -0.11\r\nvn 0.82 0.57 -0.1\r\nvn 0.87 0.49 -0.08\r\nvn 0.71 0.69 -0.13\r\nvn 0.71 0.69 -0.1\r\nvn 0.82 0.57 -0.07\r\nvn 0.87 0.49 -0.05\r\nvn 0.82 0.57 -0.04\r\nvn 0.87 0.49 -0.02\r\nvn 0.72 0.69 -0.07\r\nvn 0.72 0.7 -0.03\r\nvn 0.82 0.58 -0\r\nvn 0.87 0.5 0.02\r\nvn 0.82 0.58 0.03\r\nvn 0.86 0.5 0.05\r\nvn 0.72 0.7 -0\r\nvn 0.71 0.7 0.03\r\nvn 0.81 0.58 0.07\r\nvn 0.86 0.5 0.09\r\nvn 0.81 0.58 0.1\r\nvn 0.86 0.5 0.12\r\nvn 0.71 0.7 0.06\r\nvn 0.71 0.7 0.09\r\nvn 0.81 0.58 0.13\r\nvn 0.85 0.5 0.15\r\nvn 0.8 0.58 0.17\r\nvn 0.84 0.5 0.19\r\nvn 0.7 0.7 0.12\r\nvn 0.7 0.7 0.16\r\nvn 0.79 0.57 0.21\r\nvn 0.83 0.5 0.23\r\nvn 0.78 0.57 0.25\r\nvn 0.82 0.5 0.27\r\nvn 0.69 0.7 0.2\r\nvn 0.68 0.69 0.23\r\nvn 0.77 0.57 0.29\r\nvn 0.81 0.5 0.31\r\nvn 0.76 0.56 0.33\r\nvn 0.79 0.5 0.35\r\nvn 0.67 0.69 0.27\r\nvn 0.66 0.69 0.3\r\nvn 0.75 0.56 0.36\r\nvn 0.78 0.5 0.38\r\nvn 0.73 0.55 0.4\r\nvn 0.76 0.49 0.42\r\nvn 0.65 0.68 0.34\r\nvn 0.64 0.68 0.37\r\nvn 0.72 0.54 0.43\r\nvn 0.78 0.12 0.61\r\nvn 0.8 0.12 0.58\r\nvn 0.72 -0.32 0.61\r\nvn 0.74 -0.33 0.58\r\nvn 0.77 -0.33 0.55\r\nvn 0.83 0.11 0.55\r\nvn 0.7 0.54 0.47\r\nvn 0.62 0.67 0.4\r\nvn 0.69 0.53 0.5\r\nvn 0.75 0.09 0.65\r\nvn 0.76 0.12 0.63\r\nvn 0.71 -0.32 0.63\r\nvn 0.68 0.52 0.52\r\nvn 0.67 0.52 0.53\r\nvn 0.67 0.52 0.54\r\nvn 0.66 0.52 0.54\r\nvn 0.74 0.22 0.64\r\nvn 0.72 0.23 0.65\r\nvn 0.55 0.72 0.43\r\nvn 0.52 0.74 0.43\r\nvn 0.49 0.77 0.41\r\nvn 0.71 0.24 0.66\r\nvn 0.64 -0.43 0.64\r\nvn 0.67 -0.35 0.65\r\nvn 0.44 -0.45 0.78\r\nvn 0.66 -0.37 0.65\r\nvn 0.68 -0.37 0.63\r\nvn 0.69 -0.35 0.63\r\nvn 0.7 -0.36 0.62\r\nvn 0.7 -0.35 0.62\r\nvn 0.69 -0.38 0.62\r\nvn 0.7 -0.38 0.6\r\nvn 0.71 -0.36 0.6\r\nvn 0.71 -0.36 0.6\r\nvn 0.07 0.99 0.11\r\nvn 0.07 0.99 0.12\r\nvn 0.01 0.99 0.13\r\nvn 0.01 0.99 0.13\r\nvn 0.08 0.99 0.12\r\nvn 0.01 0.99 0.14\r\nvn -0 0.99 0.15\r\nvn -0 0.99 0.14\r\nvn -0 0.99 0.13\r\nvn -0.91 -0.38 0.16\r\nvn -0.9 -0.39 0.18\r\nvn -0.28 -0.96 -0.06\r\nvn -0.28 -0.96 -0.06\r\nvn -0.89 -0.4 0.21\r\nvn -0.28 -0.96 -0.06\r\nvn 0.08 -0.98 -0.17\r\nvn 0.08 -0.98 -0.16\r\nvn 0.08 -0.99 -0.15\r\nvn 0.08 0.99 0.11\r\nvn 0.07 0.99 0.11\r\nvn 0.01 0.99 0.12\r\nvn 0.01 0.99 0.12\r\nvn -0 0.99 0.13\r\nvn -0 0.99 0.13\r\nvn -0.92 -0.37 0.1\r\nvn -0.92 -0.38 0.13\r\nvn -0.29 -0.96 -0.06\r\nvn -0.29 -0.95 -0.07\r\nvn 0.08 -0.99 -0.14\r\nvn 0.08 -0.99 -0.14\r\nvn 0.08 0.99 0.11\r\nvn 0.08 0.99 0.11\r\nvn 0.01 0.99 0.12\r\nvn 0.01 0.99 0.11\r\nvn -0 0.99 0.12\r\nvn -0 0.99 0.12\r\nvn -0.93 -0.36 0.03\r\nvn -0.93 -0.37 0.06\r\nvn -0.29 -0.95 -0.08\r\nvn -0.29 -0.95 -0.09\r\nvn 0.08 -0.99 -0.13\r\nvn 0.08 -0.99 -0.12\r\nvn 0.08 0.99 0.08\r\nvn 0.08 0.99 0.1\r\nvn 0.01 0.99 0.1\r\nvn 0.01 1 0.08\r\nvn -0 0.99 0.1\r\nvn -0 1 0.08\r\nvn -0.93 -0.35 -0.03\r\nvn -0.93 -0.36 -0\r\nvn -0.29 -0.95 -0.09\r\nvn -0.29 -0.95 -0.08\r\nvn 0.08 -0.99 -0.1\r\nvn 0.08 -0.99 -0.08\r\nvn 0.08 1 0.05\r\nvn 0.08 0.99 0.07\r\nvn 0.01 1 0.06\r\nvn 0.01 1 0.05\r\nvn -0 1 0.06\r\nvn -0 1 0.05\r\nvn -0.93 -0.34 -0.1\r\nvn -0.93 -0.35 -0.06\r\nvn -0.29 -0.95 -0.07\r\nvn -0.29 -0.95 -0.07\r\nvn 0.08 -0.99 -0.06\r\nvn 0.08 -1 -0.04\r\nvn 0.08 1 0.05\r\nvn 0.08 1 0.05\r\nvn 0.01 1 0.04\r\nvn 0.01 1 0.04\r\nvn 0 1 0.04\r\nvn 0 1 0.03\r\nvn -0.93 -0.34 -0.17\r\nvn -0.93 -0.34 -0.13\r\nvn -0.3 -0.95 -0.07\r\nvn -0.3 -0.95 -0.08\r\nvn 0.08 -1 -0.03\r\nvn 0.08 -1 -0.02\r\nvn 0.08 1 0.04\r\nvn 0.08 1 0.05\r\nvn 0.01 1 0.03\r\nvn 0.02 1 0.03\r\nvn 0 1 0.03\r\nvn 0 1 0.02\r\nvn -0.91 -0.34 -0.23\r\nvn -0.92 -0.34 -0.2\r\nvn -0.29 -0.95 -0.09\r\nvn -0.29 -0.95 -0.09\r\nvn 0.08 -1 -0.01\r\nvn 0.07 -1 -0\r\nvn 0.09 1 0.01\r\nvn 0.09 1 0.03\r\nvn 0.02 1 0.01\r\nvn 0.03 1 -0.01\r\nvn 0.01 1 0.01\r\nvn 0.02 1 -0.01\r\nvn -0.89 -0.34 -0.3\r\nvn -0.91 -0.34 -0.26\r\nvn -0.29 -0.95 -0.09\r\nvn -0.3 -0.95 -0.08\r\nvn 0.07 -1 0.01\r\nvn 0.06 -1 0.04\r\nvn 0.1 0.99 -0.01\r\nvn 0.1 1 -0\r\nvn 0.03 1 -0.03\r\nvn 0.04 1 -0.04\r\nvn 0.02 1 -0.03\r\nvn 0.03 1 -0.04\r\nvn -0.86 -0.34 -0.37\r\nvn -0.88 -0.34 -0.34\r\nvn -0.3 -0.95 -0.08\r\nvn -0.3 -0.95 -0.08\r\nvn 0.05 -1 0.06\r\nvn 0.04 -1 0.08\r\nvn 0.1 0.99 -0.01\r\nvn 0.11 0.99 -0.02\r\nvn 0.05 1 -0.05\r\nvn 0.05 1 -0.05\r\nvn 0.03 1 -0.05\r\nvn 0.04 1 -0.05\r\nvn -0.83 -0.34 -0.45\r\nvn -0.85 -0.34 -0.41\r\nvn -0.3 -0.95 -0.09\r\nvn -0.29 -0.95 -0.1\r\nvn 0.04 -1 0.09\r\nvn 0.03 -1 0.09\r\nvn 0.1 0.99 -0\r\nvn 0.1 0.99 -0.01\r\nvn 0.05 1 -0.05\r\nvn 0.04 1 -0.04\r\nvn 0.04 1 -0.05\r\nvn 0.03 1 -0.05\r\nvn -0.78 -0.34 -0.52\r\nvn -0.81 -0.34 -0.49\r\nvn -0.28 -0.95 -0.11\r\nvn -0.28 -0.95 -0.12\r\nvn 0.03 -0.99 0.09\r\nvn 0.03 -0.99 0.1\r\nvn 0.09 1 0\r\nvn 0.1 1 -0\r\nvn 0.04 1 -0.04\r\nvn 0.04 1 -0.04\r\nvn 0.03 1 -0.05\r\nvn 0.03 1 -0.05\r\nvn -0.74 -0.36 -0.58\r\nvn -0.76 -0.35 -0.55\r\nvn -0.27 -0.95 -0.13\r\nvn -0.26 -0.96 -0.14\r\nvn 0.03 -0.99 0.1\r\nvn 0.09 1 0.01\r\nvn 0.09 1 0\r\nvn 0.04 1 -0.04\r\nvn 0.04 1 -0.04\r\nvn 0.03 1 -0.05\r\nvn 0.03 1 -0.04\r\nvn -0.72 -0.37 -0.59\r\nvn -0.25 -0.96 -0.14\r\nvn 0.08 1 0.01\r\nvn 0.09 1 0.01\r\nvn 0.04 1 -0.04\r\nvn 0.04 1 -0.04\r\nvn 0.03 1 -0.04\r\nvn 0.03 1 -0.04\r\nvn -0.28 0.9 -0.34\r\nvn -0.27 0.9 -0.33\r\nvn -0.26 0.91 -0.32\r\nvn -0.28 0.9 -0.33\r\nvn -0.25 0.91 -0.32\r\nvn -0.25 0.92 -0.31\r\nvn 0.36 -0.86 0.37\r\nvn 0.35 -0.86 0.38\r\nvn 0.3 -0.89 0.34\r\nvn 0.31 -0.89 0.33\r\nvn 0.29 -0.9 0.33\r\nvn 0.3 -0.9 0.32\r\nvn -0.3 0.9 -0.32\r\nvn -0.29 0.9 -0.33\r\nvn -0.28 0.9 -0.33\r\nvn -0.29 0.9 -0.32\r\nvn 0.38 -0.86 0.34\r\nvn 0.37 -0.86 0.35\r\nvn 0.32 -0.89 0.32\r\nvn 0.33 -0.89 0.31\r\nvn 0.31 -0.9 0.31\r\nvn 0.32 -0.9 0.3\r\nvn -0.32 0.9 -0.3\r\nvn -0.31 0.9 -0.31\r\nvn -0.3 0.9 -0.31\r\nvn -0.31 0.9 -0.3\r\nvn -0.25 0.93 -0.27\r\nvn -0.26 0.93 -0.26\r\nvn 0.4 -0.86 0.3\r\nvn 0.39 -0.86 0.32\r\nvn 0.34 -0.89 0.29\r\nvn 0.35 -0.9 0.28\r\nvn 0.33 -0.9 0.29\r\nvn 0.34 -0.9 0.27\r\nvn -0.34 0.9 -0.27\r\nvn -0.33 0.9 -0.29\r\nvn -0.32 0.91 -0.28\r\nvn -0.33 0.91 -0.27\r\nvn -0.26 0.93 -0.25\r\nvn -0.27 0.93 -0.24\r\nvn 0.41 -0.87 0.28\r\nvn 0.41 -0.87 0.29\r\nvn 0.35 -0.9 0.26\r\nvn 0.36 -0.9 0.26\r\nvn 0.34 -0.9 0.26\r\nvn 0.35 -0.9 0.25\r\nvn -0.35 0.9 -0.25\r\nvn -0.34 0.9 -0.26\r\nvn -0.33 0.91 -0.26\r\nvn -0.34 0.91 -0.25\r\nvn -0.28 0.93 -0.23\r\nvn -0.28 0.93 -0.22\r\nvn 0.42 -0.87 0.27\r\nvn 0.42 -0.87 0.27\r\nvn 0.36 -0.9 0.25\r\nvn 0.36 -0.9 0.25\r\nvn 0.35 -0.9 0.25\r\nvn 0.35 -0.9 0.25\r\nvn -0.35 0.9 -0.25\r\nvn -0.35 0.9 -0.25\r\nvn -0.34 0.91 -0.24\r\nvn -0.34 0.91 -0.24\r\nvn -0.28 0.93 -0.22\r\nvn -0.28 0.93 -0.23\r\nvn 0.42 -0.87 0.26\r\nvn 0.42 -0.87 0.26\r\nvn 0.36 -0.9 0.25\r\nvn 0.36 -0.9 0.24\r\nvn 0.35 -0.9 0.25\r\nvn 0.35 -0.9 0.24\r\nvn -0.35 0.9 -0.24\r\nvn -0.35 0.9 -0.25\r\nvn -0.34 0.91 -0.24\r\nvn -0.34 0.91 -0.24\r\nvn -0.28 0.93 -0.23\r\nvn -0.28 0.93 -0.23\r\nvn 0.43 -0.87 0.23\r\nvn 0.43 -0.87 0.25\r\nvn 0.37 -0.9 0.24\r\nvn 0.37 -0.9 0.23\r\nvn 0.36 -0.9 0.24\r\nvn 0.36 -0.91 0.22\r\nvn -0.36 0.91 -0.22\r\nvn -0.36 0.9 -0.24\r\nvn -0.35 0.91 -0.23\r\nvn -0.35 0.91 -0.22\r\nvn -0.28 0.93 -0.22\r\nvn -0.29 0.93 -0.21\r\nvn 0.44 -0.88 0.2\r\nvn 0.43 -0.87 0.22\r\nvn 0.37 -0.9 0.21\r\nvn 0.37 -0.9 0.2\r\nvn 0.36 -0.91 0.21\r\nvn 0.36 -0.91 0.2\r\nvn -0.36 0.91 -0.2\r\nvn -0.36 0.91 -0.21\r\nvn -0.35 0.91 -0.21\r\nvn -0.35 0.91 -0.2\r\nvn -0.29 0.94 -0.2\r\nvn -0.29 0.94 -0.2\r\nvn 0.44 -0.88 0.19\r\nvn 0.44 -0.88 0.2\r\nvn 0.38 -0.91 0.2\r\nvn 0.38 -0.91 0.19\r\nvn 0.36 -0.91 0.2\r\nvn 0.37 -0.91 0.19\r\nvn -0.37 0.91 -0.19\r\nvn -0.36 0.91 -0.2\r\nvn -0.35 0.91 -0.19\r\nvn -0.35 0.92 -0.19\r\nvn -0.29 0.94 -0.19\r\nvn -0.29 0.94 -0.19\r\nvn 0.44 -0.88 0.18\r\nvn 0.44 -0.88 0.19\r\nvn 0.38 -0.91 0.19\r\nvn 0.38 -0.91 0.18\r\nvn 0.37 -0.91 0.19\r\nvn 0.37 -0.91 0.19\r\nvn -0.37 0.91 -0.19\r\nvn -0.37 0.91 -0.19\r\nvn -0.35 0.92 -0.19\r\nvn -0.36 0.92 -0.19\r\nvn -0.29 0.94 -0.2\r\nvn -0.29 0.94 -0.19\r\nvn 0.44 -0.89 0.15\r\nvn 0.44 -0.88 0.17\r\nvn 0.38 -0.91 0.17\r\nvn 0.38 -0.91 0.16\r\nvn 0.37 -0.91 0.18\r\nvn 0.37 -0.92 0.16\r\nvn -0.37 0.92 -0.16\r\nvn -0.37 0.91 -0.18\r\nvn -0.36 0.92 -0.18\r\nvn -0.35 0.92 -0.17\r\nvn -0.29 0.94 -0.19\r\nvn -0.29 0.94 -0.18\r\nvn 0.44 -0.89 0.13\r\nvn 0.44 -0.89 0.14\r\nvn 0.38 -0.91 0.15\r\nvn 0.38 -0.92 0.14\r\nvn 0.37 -0.92 0.15\r\nvn 0.36 -0.92 0.14\r\nvn -0.36 0.92 -0.14\r\nvn -0.37 0.92 -0.15\r\nvn -0.35 0.92 -0.15\r\nvn -0.35 0.92 -0.15\r\nvn -0.29 0.94 -0.17\r\nvn -0.29 0.94 -0.16\r\nvn 0.43 -0.89 0.11\r\nvn 0.44 -0.89 0.12\r\nvn 0.37 -0.92 0.13\r\nvn 0.37 -0.92 0.13\r\nvn 0.36 -0.92 0.14\r\nvn 0.36 -0.92 0.13\r\nvn -0.36 0.92 -0.13\r\nvn -0.36 0.92 -0.14\r\nvn -0.35 0.93 -0.14\r\nvn -0.35 0.93 -0.14\r\nvn -0.29 0.94 -0.16\r\nvn -0.29 0.94 -0.16\r\nvn 0.43 -0.9 0.09\r\nvn 0.43 -0.9 0.1\r\nvn 0.37 -0.92 0.13\r\nvn 0.37 -0.92 0.12\r\nvn 0.36 -0.92 0.13\r\nvn 0.36 -0.92 0.12\r\nvn -0.36 0.92 -0.12\r\nvn -0.36 0.92 -0.13\r\nvn -0.35 0.93 -0.13\r\nvn -0.35 0.93 -0.13\r\nvn -0.29 0.94 -0.15\r\nvn -0.29 0.95 -0.15\r\nvn 0.42 -0.9 0.07\r\nvn 0.43 -0.9 0.08\r\nvn 0.37 -0.92 0.11\r\nvn 0.37 -0.93 0.1\r\nvn 0.36 -0.93 0.11\r\nvn 0.35 -0.93 0.1\r\nvn -0.35 0.93 -0.1\r\nvn -0.36 0.93 -0.11\r\nvn -0.35 0.93 -0.12\r\nvn -0.34 0.93 -0.11\r\nvn -0.29 0.95 -0.15\r\nvn -0.29 0.95 -0.14\r\nvn 0.41 -0.91 0.04\r\nvn 0.42 -0.91 0.06\r\nvn 0.36 -0.93 0.09\r\nvn 0.36 -0.93 0.08\r\nvn 0.35 -0.93 0.09\r\nvn 0.35 -0.93 0.08\r\nvn -0.35 0.93 -0.08\r\nvn -0.35 0.93 -0.09\r\nvn -0.34 0.93 -0.1\r\nvn -0.34 0.94 -0.09\r\nvn -0.28 0.95 -0.13\r\nvn -0.28 0.95 -0.12\r\nvn 0.39 -0.92 0.01\r\nvn 0.4 -0.92 0.03\r\nvn 0.35 -0.94 0.07\r\nvn 0.34 -0.94 0.05\r\nvn 0.34 -0.94 0.07\r\nvn 0.33 -0.94 0.06\r\nvn -0.33 0.94 -0.06\r\nvn -0.34 0.94 -0.07\r\nvn -0.33 0.94 -0.08\r\nvn -0.32 0.94 -0.07\r\nvn -0.27 0.95 -0.11\r\nvn -0.27 0.96 -0.1\r\nvn 0.38 -0.93 0.01\r\nvn 0.39 -0.92 0\r\nvn 0.34 -0.94 0.04\r\nvn 0.32 -0.94 0.04\r\nvn 0.33 -0.94 0.05\r\nvn 0.31 -0.95 0.05\r\nvn -0.31 0.95 -0.05\r\nvn -0.33 0.94 -0.05\r\nvn -0.32 0.95 -0.06\r\nvn -0.31 0.95 -0.06\r\nvn -0.26 0.96 -0.1\r\nvn -0.26 0.96 -0.1\r\nvn 0.36 -0.93 0.02\r\nvn 0.3 -0.95 0.06\r\nvn 0.29 -0.95 0.07\r\nvn -0.28 0.96 -0.09\r\nvn -0.29 0.95 -0.07\r\nvn -0.29 0.96 -0.08\r\nvn -0.27 0.96 -0.1\r\nvn -0.24 0.96 -0.11\r\nvn -0.23 0.96 -0.13\r\nvn -0.28 0.96 -0.1\r\nvn -0.28 0.95 -0.1\r\nvn -0.27 0.96 -0.11\r\nvn -0.27 0.96 -0.11\r\nvn -0.24 0.96 -0.14\r\nvn -0.22 0.96 -0.16\r\nvn 0.75 -0.49 -0.45\r\nvn 0.44 -0.89 -0.13\r\nvn 0.33 -0.94 0.02\r\nvn 0.7 -0.7 -0.13\r\nvn 0.33 -0.94 0.08\r\nvn 0.71 -0.7 0.08\r\nvn -0.22 0.97 -0.12\r\nvn -0.23 0.97 -0.09\r\nvn -0.11 0.96 -0.25\r\nvn 0.31 0.82 -0.48\r\nvn 0.11 0.97 -0.22\r\nvn 0.06 1 -0.07\r\nvn 0.05 0.98 0.18\r\nvn 0.03 0.98 0.19\r\nvn 0.04 0.98 0.19\r\nvn 0.02 0.98 0.2\r\nvn -0.72 -0.5 0.49\r\nvn -0.7 -0.52 0.48\r\nvn -0.24 -0.97 -0.02\r\nvn -0.24 -0.97 -0.01\r\nvn 0.03 -0.97 -0.23\r\nvn 0.04 -0.97 -0.24\r\nvn 0.08 0.99 0.15\r\nvn 0.08 0.99 0.15\r\nvn 0.03 0.98 0.19\r\nvn 0.03 0.98 0.19\r\nvn 0.02 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn -0.73 -0.48 0.48\r\nvn -0.72 -0.49 0.49\r\nvn -0.24 -0.97 -0.01\r\nvn -0.24 -0.97 -0.02\r\nvn 0.05 -0.97 -0.25\r\nvn 0.05 -0.97 -0.25\r\nvn 0.08 0.98 0.16\r\nvn 0.08 0.98 0.15\r\nvn 0.03 0.98 0.19\r\nvn 0.03 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn -0.76 -0.47 0.45\r\nvn -0.74 -0.48 0.46\r\nvn -0.25 -0.97 -0.02\r\nvn -0.25 -0.97 -0.03\r\nvn 0.05 -0.97 -0.25\r\nvn 0.05 -0.97 -0.25\r\nvn 0.09 0.98 0.16\r\nvn 0.09 0.98 0.16\r\nvn 0.03 0.98 0.2\r\nvn 0.03 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn -0.79 -0.46 0.4\r\nvn -0.78 -0.46 0.43\r\nvn -0.26 -0.96 -0.04\r\nvn -0.27 -0.96 -0.04\r\nvn 0.05 -0.97 -0.25\r\nvn 0.05 -0.97 -0.25\r\nvn 0.09 0.98 0.16\r\nvn 0.03 0.98 0.2\r\nvn 0.02 0.98 0.2\r\nvn -0.81 -0.45 0.38\r\nvn -0.27 -0.96 -0.05\r\nvn 0.05 -0.97 -0.24\r\nvn 0.08 0.99 0.14\r\nvn 0.01 0.99 0.16\r\nvn 0 0.99 0.16\r\nvn -0.88 -0.41 0.24\r\nvn -0.28 -0.96 -0.06\r\nvn 0.07 -0.98 -0.19\r\nvn 0.95 -0.16 -0.27\r\nvn 0.92 -0.28 -0.28\r\nvn 0.96 -0.17 -0.24\r\nvn 0.92 -0.29 -0.25\r\nvn 0.92 -0.3 -0.25\r\nvn 0.91 -0.29 -0.28\r\nvn 0.87 -0.07 -0.48\r\nvn 0.89 -0.09 -0.44\r\nvn 0.87 -0.21 -0.45\r\nvn 0.85 -0.18 -0.49\r\nvn 0.86 -0.23 -0.45\r\nvn 0.84 -0.21 -0.49\r\nvn 0.84 -0.04 -0.54\r\nvn 0.86 -0.06 -0.51\r\nvn 0.83 -0.17 -0.53\r\nvn 0.82 -0.15 -0.55\r\nvn 0.83 -0.19 -0.53\r\nvn 0.81 -0.18 -0.56\r\nvn 0.81 -0.02 -0.59\r\nvn 0.82 -0.03 -0.57\r\nvn 0.8 -0.13 -0.58\r\nvn 0.79 -0.12 -0.6\r\nvn 0.8 -0.16 -0.58\r\nvn 0.78 -0.15 -0.6\r\nvn 0.79 -0.01 -0.61\r\nvn 0.78 -0.11 -0.62\r\nvn 0.77 -0.1 -0.63\r\nvn 0.77 -0.14 -0.62\r\nvn 0.76 -0.1 -0.64\r\nvn 0.77 -0.1 -0.64\r\nvn 0.78 0 -0.62\r\nvn 0.77 -0.1 -0.63\r\nvn 0.76 -0.09 -0.65\r\nvn 0.73 -0.06 -0.69\r\nvn 0.71 -0.06 -0.7\r\nvn 0.65 0.15 -0.75\r\nvn 0.64 0.23 -0.74\r\nvn 0.61 0.16 -0.77\r\nvn 0.6 0.33 -0.73\r\nvn 0.55 0.37 -0.75\r\nvn 0.6 0.33 -0.73\r\nvn 0.79 0.17 -0.58\r\nvn 0.79 0.17 -0.59\r\nvn 0.76 0.27 -0.6\r\nvn 0.76 0.27 -0.59\r\nvn 0.81 0.17 -0.56\r\nvn 0.8 0.17 -0.58\r\nvn 0.77 0.27 -0.58\r\nvn 0.78 0.27 -0.57\r\nvn 0.84 0.19 -0.5\r\nvn 0.83 0.18 -0.53\r\nvn 0.79 0.28 -0.54\r\nvn 0.81 0.29 -0.51\r\nvn 0.88 0.21 -0.44\r\nvn 0.86 0.2 -0.47\r\nvn 0.82 0.3 -0.48\r\nvn 0.84 0.31 -0.45\r\nvn 0.91 0.23 -0.35\r\nvn 0.89 0.22 -0.4\r\nvn 0.85 0.32 -0.41\r\nvn 0.87 0.33 -0.37\r\nvn 0.93 0.25 -0.27\r\nvn 0.92 0.24 -0.31\r\nvn 0.88 0.34 -0.32\r\nvn 0.89 0.35 -0.29\r\nvn 0.94 0.26 -0.21\r\nvn 0.94 0.26 -0.24\r\nvn 0.9 0.35 -0.25\r\nvn 0.91 0.36 -0.22\r\nvn 0.95 0.27 -0.14\r\nvn 0.95 0.27 -0.18\r\nvn 0.91 0.37 -0.19\r\nvn 0.91 0.37 -0.16\r\nvn 0.95 0.29 -0.07\r\nvn 0.95 0.28 -0.11\r\nvn 0.92 0.38 -0.13\r\nvn 0.92 0.38 -0.09\r\nvn 0.95 0.3 -0.01\r\nvn 0.95 0.29 -0.04\r\nvn 0.92 0.39 -0.06\r\nvn 0.92 0.4 -0.03\r\nvn 0.95 0.31 0.06\r\nvn 0.95 0.31 0.02\r\nvn 0.92 0.4 0\r\nvn 0.91 0.4 0.04\r\nvn 0.94 0.32 0.13\r\nvn 0.94 0.32 0.1\r\nvn 0.91 0.41 0.08\r\nvn 0.9 0.41 0.11\r\nvn 0.92 0.33 0.2\r\nvn 0.93 0.33 0.17\r\nvn 0.9 0.42 0.15\r\nvn 0.89 0.42 0.18\r\nvn 0.9 0.34 0.28\r\nvn 0.91 0.34 0.24\r\nvn 0.88 0.43 0.21\r\nvn 0.87 0.43 0.25\r\nvn 0.87 0.35 0.35\r\nvn 0.88 0.35 0.32\r\nvn 0.85 0.43 0.29\r\nvn 0.84 0.43 0.33\r\nvn 0.83 0.36 0.43\r\nvn 0.85 0.35 0.39\r\nvn 0.82 0.44 0.37\r\nvn 0.8 0.44 0.4\r\nvn 0.81 0.36 0.46\r\nvn 0.78 0.44 0.44\r\nvn 0.75 -0.23 0.62\r\nvn 0.74 -0.22 0.64\r\nvn 0.7 -0.34 0.62\r\nvn 0.72 -0.35 0.61\r\nvn 0.79 -0.24 0.57\r\nvn 0.77 -0.23 0.6\r\nvn 0.73 -0.35 0.58\r\nvn 0.75 -0.36 0.55\r\nvn 0.73 -0.36 0.58\r\nvn 0.75 -0.37 0.55\r\nvn 0.83 -0.25 0.49\r\nvn 0.81 -0.24 0.53\r\nvn 0.77 -0.36 0.52\r\nvn 0.8 -0.37 0.48\r\nvn 0.77 -0.37 0.52\r\nvn 0.79 -0.38 0.48\r\nvn 0.85 -0.25 0.45\r\nvn 0.81 -0.37 0.44\r\nvn 0.81 -0.38 0.44\r\nvn 0.94 -0.26 0.22\r\nvn 0.93 -0.26 0.26\r\nvn 0.89 -0.38 0.25\r\nvn 0.9 -0.38 0.21\r\nvn 0.89 -0.39 0.25\r\nvn 0.9 -0.39 0.21\r\nvn 0.96 -0.25 0.15\r\nvn 0.95 -0.26 0.18\r\nvn 0.91 -0.38 0.17\r\nvn 0.92 -0.37 0.14\r\nvn 0.91 -0.38 0.17\r\nvn 0.91 -0.38 0.14\r\nvn 0.97 -0.24 0.07\r\nvn 0.96 -0.25 0.11\r\nvn 0.92 -0.37 0.1\r\nvn 0.93 -0.37 0.06\r\nvn 0.92 -0.38 0.1\r\nvn 0.93 -0.37 0.06\r\nvn 0.97 -0.23 -0.02\r\nvn 0.97 -0.24 0.03\r\nvn 0.93 -0.36 0.02\r\nvn 0.94 -0.35 -0.03\r\nvn 0.93 -0.37 0.02\r\nvn 0.93 -0.36 -0.03\r\nvn 0.97 -0.21 -0.1\r\nvn 0.97 -0.22 -0.06\r\nvn 0.94 -0.34 -0.07\r\nvn 0.94 -0.33 -0.11\r\nvn 0.93 -0.35 -0.07\r\nvn 0.93 -0.34 -0.11\r\nvn 0.97 -0.19 -0.18\r\nvn 0.97 -0.2 -0.14\r\nvn 0.94 -0.32 -0.15\r\nvn 0.93 -0.31 -0.19\r\nvn 0.93 -0.33 -0.15\r\nvn 0.93 -0.32 -0.19\r\nvn 0.96 -0.18 -0.21\r\nvn 0.93 -0.3 -0.22\r\nvn 0.92 -0.31 -0.22\r\nvn 0.32 0.95 0.05\r\nvn 0.32 0.95 0.05\r\nvn 0.69 0.72 -0.06\r\nvn 0.68 0.73 -0.08\r\nvn 0.68 0.73 -0.1\r\nvn 0.32 0.95 0.04\r\nvn 0.5 -0.82 -0.27\r\nvn 0.5 -0.83 -0.25\r\nvn 0.83 -0.46 -0.32\r\nvn 0.84 -0.46 -0.28\r\nvn 0.84 -0.47 -0.26\r\nvn 0.51 -0.83 -0.23\r\nvn 0.33 0.94 0.07\r\nvn 0.32 0.94 0.06\r\nvn 0.7 0.72 -0.02\r\nvn 0.69 0.72 -0.04\r\nvn 0.51 -0.84 -0.21\r\nvn 0.85 -0.48 -0.23\r\nvn 0.85 -0.48 -0.2\r\nvn 0.51 -0.84 -0.19\r\nvn 0.33 0.94 0.08\r\nvn 0.33 0.94 0.08\r\nvn 0.7 0.71 0.03\r\nvn 0.7 0.71 0.01\r\nvn 0.51 -0.84 -0.16\r\nvn 0.86 -0.49 -0.16\r\nvn 0.86 -0.5 -0.12\r\nvn 0.52 -0.85 -0.14\r\nvn 0.33 0.94 0.08\r\nvn 0.33 0.94 0.09\r\nvn 0.71 0.7 0.06\r\nvn 0.71 0.71 0.05\r\nvn 0.52 -0.85 -0.11\r\nvn 0.86 -0.5 -0.08\r\nvn 0.86 -0.51 -0.04\r\nvn 0.51 -0.85 -0.07\r\nvn 0.33 0.94 0.07\r\nvn 0.33 0.94 0.07\r\nvn 0.71 0.7 0.09\r\nvn 0.71 0.7 0.07\r\nvn 0.51 -0.86 -0.03\r\nvn 0.86 -0.51 0.01\r\nvn 0.85 -0.52 0.05\r\nvn 0.51 -0.86 0\r\nvn 0.33 0.94 0.09\r\nvn 0.33 0.94 0.08\r\nvn 0.7 0.7 0.14\r\nvn 0.7 0.7 0.12\r\nvn 0.51 -0.86 0.03\r\nvn 0.85 -0.52 0.09\r\nvn 0.84 -0.52 0.12\r\nvn 0.5 -0.86 0.05\r\nvn 0.33 0.94 0.1\r\nvn 0.33 0.94 0.1\r\nvn 0.69 0.7 0.18\r\nvn 0.7 0.7 0.16\r\nvn 0.5 -0.86 0.07\r\nvn 0.84 -0.53 0.15\r\nvn 0.83 -0.53 0.19\r\nvn 0.49 -0.87 0.1\r\nvn 0.33 0.94 0.1\r\nvn 0.33 0.94 0.1\r\nvn 0.68 0.7 0.22\r\nvn 0.69 0.7 0.2\r\nvn 0.48 -0.87 0.13\r\nvn 0.82 -0.53 0.23\r\nvn 0.8 -0.53 0.28\r\nvn 0.47 -0.87 0.17\r\nvn 0.33 0.94 0.1\r\nvn 0.33 0.94 0.09\r\nvn 0.67 0.7 0.26\r\nvn 0.68 0.7 0.24\r\nvn 0.45 -0.87 0.22\r\nvn 0.78 -0.53 0.33\r\nvn 0.76 -0.53 0.38\r\nvn 0.44 -0.86 0.25\r\nvn 0.32 0.94 0.11\r\nvn 0.33 0.94 0.1\r\nvn 0.64 0.7 0.31\r\nvn 0.66 0.7 0.29\r\nvn 0.42 -0.86 0.28\r\nvn 0.74 -0.52 0.42\r\nvn 0.72 -0.52 0.45\r\nvn 0.41 -0.86 0.3\r\nvn 0.31 0.94 0.14\r\nvn 0.32 0.94 0.13\r\nvn 0.61 0.7 0.37\r\nvn 0.62 0.7 0.34\r\nvn 0.4 -0.86 0.32\r\nvn 0.7 -0.52 0.49\r\nvn 0.68 -0.51 0.52\r\nvn 0.39 -0.86 0.34\r\nvn 0.29 0.94 0.16\r\nvn 0.3 0.94 0.16\r\nvn 0.57 0.71 0.41\r\nvn 0.59 0.71 0.39\r\nvn 0.38 -0.86 0.35\r\nvn 0.67 -0.51 0.54\r\nvn 0.27 0.95 0.17\r\nvn 0.28 0.94 0.17\r\nvn 0.56 0.71 0.43\r\nvn 0.22 0.96 0.15\r\nvn 0.25 0.95 0.17\r\nvn 0.52 -0.7 0.48\r\nvn 0.51 -0.7 0.5\r\nvn 0.56 -0.7 0.44\r\nvn 0.54 -0.7 0.47\r\nvn 0.2 0.98 0.05\r\nvn 0.2 0.98 0.04\r\nvn 0.59 -0.71 0.39\r\nvn 0.57 -0.71 0.42\r\nvn 0.81 -0.34 0.48\r\nvn 0.79 -0.33 0.52\r\nvn 0.2 0.98 0.03\r\nvn 0.2 0.98 0.02\r\nvn 0.61 -0.71 0.35\r\nvn 0.6 -0.71 0.37\r\nvn 0.84 -0.34 0.42\r\nvn 0.83 -0.34 0.45\r\nvn 0.21 0.98 0.01\r\nvn 0.21 0.98 -0.01\r\nvn 0.62 -0.71 0.32\r\nvn 0.62 -0.71 0.33\r\nvn 0.87 -0.35 0.36\r\nvn 0.86 -0.34 0.39\r\nvn 0.21 0.98 -0.03\r\nvn 0.22 0.97 -0.05\r\nvn 0.63 -0.72 0.29\r\nvn 0.63 -0.72 0.3\r\nvn 0.89 -0.35 0.3\r\nvn 0.88 -0.35 0.33\r\nvn 0.22 0.97 -0.07\r\nvn 0.22 0.97 -0.09\r\nvn 0.64 -0.72 0.25\r\nvn 0.64 -0.72 0.27\r\nvn 0.9 -0.36 0.24\r\nvn 0.89 -0.36 0.27\r\nvn 0.22 0.97 -0.11\r\nvn 0.22 0.97 -0.12\r\nvn 0.65 -0.73 0.21\r\nvn 0.65 -0.73 0.23\r\nvn 0.91 -0.37 0.17\r\nvn 0.91 -0.37 0.21\r\nvn 0.22 0.97 -0.13\r\nvn 0.22 0.96 -0.14\r\nvn 0.65 -0.74 0.17\r\nvn 0.65 -0.73 0.19\r\nvn 0.92 -0.38 0.12\r\nvn 0.91 -0.38 0.14\r\nvn 0.22 0.96 -0.16\r\nvn 0.22 0.96 -0.17\r\nvn 0.66 -0.74 0.15\r\nvn 0.65 -0.74 0.16\r\nvn 0.92 -0.39 0.07\r\nvn 0.92 -0.39 0.1\r\nvn 0.22 0.96 -0.19\r\nvn 0.22 0.95 -0.2\r\nvn 0.65 -0.75 0.1\r\nvn 0.65 -0.75 0.13\r\nvn 0.91 -0.41 0\r\nvn 0.92 -0.4 0.04\r\nvn 0.22 0.95 -0.21\r\nvn 0.21 0.95 -0.22\r\nvn 0.65 -0.76 0.06\r\nvn 0.65 -0.75 0.08\r\nvn 0.91 -0.42 -0.05\r\nvn 0.91 -0.41 -0.03\r\nvn 0.21 0.95 -0.23\r\nvn 0.21 0.95 -0.24\r\nvn 0.64 -0.76 0.03\r\nvn 0.65 -0.76 0.05\r\nvn 0.9 -0.43 -0.1\r\nvn 0.9 -0.42 -0.08\r\nvn 0.21 0.95 -0.25\r\nvn 0.2 0.94 -0.27\r\nvn 0.64 -0.77 -0.01\r\nvn 0.64 -0.77 0.01\r\nvn 0.88 -0.44 -0.17\r\nvn 0.89 -0.43 -0.14\r\nvn 0.2 0.94 -0.29\r\nvn 0.19 0.93 -0.3\r\nvn 0.62 -0.78 -0.05\r\nvn 0.63 -0.78 -0.03\r\nvn 0.85 -0.46 -0.24\r\nvn 0.87 -0.45 -0.21\r\nvn 0.19 0.93 -0.32\r\nvn 0.18 0.92 -0.34\r\nvn 0.6 -0.8 -0.09\r\nvn 0.61 -0.79 -0.07\r\nvn 0.82 -0.48 -0.3\r\nvn 0.84 -0.47 -0.27\r\nvn 0.17 0.92 -0.35\r\nvn 0.17 0.92 -0.35\r\nvn 0.58 -0.81 -0.13\r\nvn 0.59 -0.8 -0.11\r\nvn 0.79 -0.49 -0.36\r\nvn 0.81 -0.49 -0.33\r\nvn 0.17 0.92 -0.36\r\nvn 0.16 0.92 -0.36\r\nvn 0.56 -0.82 -0.15\r\nvn 0.57 -0.81 -0.15\r\nvn 0.78 -0.5 -0.38\r\nvn 0.78 -0.5 -0.38\r\nvn 0.16 0.92 -0.37\r\nvn 0.16 0.92 -0.37\r\nvn 0.55 -0.83 -0.14\r\nvn 0.55 -0.82 -0.14\r\nvn 0.77 -0.5 -0.39\r\nvn 0.16 0.91 -0.38\r\nvn 0.16 0.91 -0.39\r\nvn 0.58 -0.77 -0.25\r\nvn 0.54 -0.83 -0.14\r\nvn 0.15 0.9 -0.41\r\nvn 0.16 0.88 -0.46\r\nvn 0.72 -0.11 -0.68\r\nvn 0.67 -0.56 -0.49\r\nvn 0.66 0.12 -0.74\r\nvn 0.7 -0.08 -0.71\r\nvn 0.26 0.79 -0.56\r\nvn 0.52 0.47 -0.71\r\nvn 0.54 0.39 -0.74\r\nvn 0.49 0.54 -0.69\r\nvn 0.4 -0.78 -0.48\r\nvn 0.69 -0.36 -0.63\r\nvn 0.69 -0.36 -0.63\r\nvn 0.4 -0.78 -0.49\r\nvn 0.28 0.96 -0.01\r\nvn 0.27 0.96 -0.01\r\nvn 0.4 -0.77 -0.49\r\nvn 0.69 -0.35 -0.63\r\nvn 0.69 -0.36 -0.63\r\nvn 0.41 -0.77 -0.49\r\nvn 0.29 0.96 0\r\nvn 0.28 0.96 -0.01\r\nvn 0.59 0.76 -0.26\r\nvn 0.58 0.77 -0.28\r\nvn 0.41 -0.77 -0.48\r\nvn 0.7 -0.36 -0.61\r\nvn 0.71 -0.37 -0.6\r\nvn 0.42 -0.78 -0.47\r\nvn 0.3 0.95 0.02\r\nvn 0.3 0.95 0.01\r\nvn 0.62 0.75 -0.22\r\nvn 0.6 0.76 -0.24\r\nvn 0.42 -0.78 -0.46\r\nvn 0.72 -0.38 -0.58\r\nvn 0.74 -0.38 -0.55\r\nvn 0.43 -0.78 -0.45\r\nvn 0.31 0.95 0.03\r\nvn 0.31 0.95 0.03\r\nvn 0.64 0.74 -0.18\r\nvn 0.63 0.75 -0.2\r\nvn 0.44 -0.79 -0.43\r\nvn 0.75 -0.39 -0.53\r\nvn 0.77 -0.41 -0.5\r\nvn 0.45 -0.79 -0.41\r\nvn 0.32 0.95 0.04\r\nvn 0.32 0.95 0.04\r\nvn 0.67 0.73 -0.14\r\nvn 0.65 0.74 -0.16\r\nvn 0.46 -0.8 -0.38\r\nvn 0.79 -0.42 -0.46\r\nvn 0.8 -0.43 -0.41\r\nvn 0.48 -0.81 -0.34\r\nvn 0.32 0.95 0.04\r\nvn 0.67 0.73 -0.11\r\nvn 0.49 -0.82 -0.31\r\nvn 0.82 -0.45 -0.36\r\nvn 0.94 0.26 -0.24\r\nvn 0.94 0.25 -0.21\r\nvn 0.95 0.25 -0.19\r\nvn 0.92 -0.32 -0.25\r\nvn 0.91 -0.31 -0.28\r\nvn 0.91 -0.34 -0.25\r\nvn 0.9 -0.33 -0.28\r\nvn 0.89 -0.33 -0.32\r\nvn 0.9 -0.3 -0.32\r\nvn 0.91 0.28 -0.32\r\nvn 0.92 0.27 -0.28\r\nvn 0.89 -0.29 -0.36\r\nvn 0.88 -0.31 -0.36\r\nvn 0.86 -0.3 -0.41\r\nvn 0.87 -0.27 -0.41\r\nvn 0.87 0.31 -0.4\r\nvn 0.89 0.29 -0.36\r\nvn 0.85 -0.26 -0.46\r\nvn 0.84 -0.29 -0.46\r\nvn 0.82 -0.27 -0.5\r\nvn 0.83 -0.24 -0.5\r\nvn 0.83 0.33 -0.45\r\nvn 0.85 0.32 -0.43\r\nvn 0.82 -0.23 -0.53\r\nvn 0.81 -0.26 -0.53\r\nvn 0.79 -0.25 -0.56\r\nvn 0.8 -0.22 -0.56\r\nvn 0.79 0.34 -0.5\r\nvn 0.81 0.34 -0.48\r\nvn 0.79 -0.21 -0.58\r\nvn 0.78 -0.24 -0.58\r\nvn 0.76 -0.23 -0.6\r\nvn 0.77 -0.2 -0.6\r\nvn 0.78 0.35 -0.52\r\nvn 0.76 -0.19 -0.62\r\nvn 0.75 -0.23 -0.62\r\nvn 0.77 0.36 -0.53\r\nvn 0.66 0.09 -0.75\r\nvn 0.63 0.16 -0.76\r\nvn 0.54 0.37 -0.76\r\nvn 0.56 0.39 -0.73\r\nvn 0.84 -0.09 -0.54\r\nvn 0.83 -0.09 -0.55\r\nvn 0.85 -0.08 -0.51\r\nvn 0.84 -0.08 -0.53\r\nvn 0.89 -0.06 -0.46\r\nvn 0.87 -0.07 -0.49\r\nvn 0.92 -0.04 -0.39\r\nvn 0.9 -0.05 -0.42\r\nvn 0.95 -0.02 -0.3\r\nvn 0.94 -0.03 -0.35\r\nvn 0.98 -0 -0.22\r\nvn 0.97 -0.01 -0.26\r\nvn 0.99 0.01 -0.16\r\nvn 0.98 0.01 -0.19\r\nvn 1 0.02 -0.1\r\nvn 0.99 0.02 -0.13\r\nvn 1 0.04 -0.02\r\nvn 1 0.03 -0.06\r\nvn 1 0.05 0.04\r\nvn 1 0.04 0.01\r\nvn 0.99 0.06 0.11\r\nvn 1 0.06 0.07\r\nvn 0.98 0.07 0.19\r\nvn 0.99 0.07 0.15\r\nvn 0.96 0.08 0.25\r\nvn 0.97 0.08 0.22\r\nvn 0.94 0.09 0.32\r\nvn 0.95 0.09 0.29\r\nvn 0.91 0.1 0.4\r\nvn 0.93 0.1 0.36\r\nvn 0.87 0.11 0.47\r\nvn 0.89 0.1 0.44\r\nvn 0.85 0.11 0.51\r\nvn 0.73 -0.11 0.68\r\nvn 0.74 0 0.67\r\nvn 0.77 0.21 0.61\r\nvn 0.75 0.21 0.62\r\nvn 0.81 0.2 0.55\r\nvn 0.79 0.21 0.58\r\nvn 0.73 -0.36 0.58\r\nvn 0.72 -0.38 0.58\r\nvn 0.74 -0.39 0.55\r\nvn 0.75 -0.37 0.55\r\nvn 0.86 0.2 0.47\r\nvn 0.83 0.2 0.51\r\nvn 0.77 -0.37 0.52\r\nvn 0.76 -0.39 0.51\r\nvn 0.79 -0.39 0.48\r\nvn 0.79 -0.37 0.48\r\nvn 0.9 0.19 0.4\r\nvn 0.88 0.19 0.44\r\nvn 0.81 -0.38 0.44\r\nvn 0.81 -0.4 0.44\r\nvn 0.83 -0.4 0.4\r\nvn 0.83 -0.38 0.4\r\nvn 0.93 0.18 0.31\r\nvn 0.92 0.18 0.36\r\nvn 0.85 -0.38 0.36\r\nvn 0.85 -0.4 0.35\r\nvn 0.86 -0.4 0.3\r\nvn 0.87 -0.38 0.3\r\nvn 0.95 0.19 0.23\r\nvn 0.94 0.18 0.27\r\nvn 0.89 -0.38 0.25\r\nvn 0.88 -0.4 0.25\r\nvn 0.89 -0.4 0.21\r\nvn 0.9 -0.38 0.21\r\nvn 0.97 0.19 0.17\r\nvn 0.96 0.19 0.2\r\nvn 0.91 -0.38 0.17\r\nvn 0.9 -0.4 0.17\r\nvn 0.91 -0.4 0.14\r\nvn 0.92 -0.38 0.14\r\nvn 0.98 0.2 0.09\r\nvn 0.97 0.19 0.13\r\nvn 0.92 -0.37 0.1\r\nvn 0.91 -0.39 0.1\r\nvn 0.92 -0.39 0.06\r\nvn 0.93 -0.37 0.06\r\nvn 0.98 0.2 0.02\r\nvn 0.98 0.2 0.06\r\nvn 0.93 -0.37 0.02\r\nvn 0.92 -0.38 0.02\r\nvn 0.92 -0.38 -0.03\r\nvn 0.93 -0.36 -0.03\r\nvn 0.97 0.22 -0.05\r\nvn 0.98 0.21 -0.01\r\nvn 0.93 -0.35 -0.07\r\nvn 0.93 -0.37 -0.07\r\nvn 0.92 -0.37 -0.11\r\nvn 0.93 -0.35 -0.11\r\nvn 0.96 0.23 -0.12\r\nvn 0.97 0.23 -0.08\r\nvn 0.93 -0.34 -0.15\r\nvn 0.92 -0.36 -0.15\r\nvn 0.92 -0.35 -0.19\r\nvn 0.93 -0.33 -0.19\r\nvn 0.96 0.24 -0.16\r\nvn 0.92 -0.32 -0.22\r\nvn 0.91 -0.35 -0.22\r\nvn -0.89 0.34 0.31\r\nvn -0.92 0.25 0.31\r\nvn -0.93 0.26 0.28\r\nvn -0.9 0.34 0.28\r\nvn -0.93 0.27 0.25\r\nvn -0.9 0.35 0.25\r\nvn -0.94 0.27 0.22\r\nvn -0.91 0.36 0.22\r\nvn -0.94 0.28 0.18\r\nvn -0.91 0.36 0.19\r\nvn -0.95 0.29 0.14\r\nvn -0.92 0.37 0.15\r\nvn -0.95 0.3 0.1\r\nvn -0.92 0.38 0.11\r\nvn -0.95 0.3 0.06\r\nvn -0.92 0.39 0.07\r\nvn -0.95 0.31 0.02\r\nvn -0.92 0.39 0.03\r\nvn -0.95 0.31 -0.02\r\nvn -0.92 0.4 -0.02\r\nvn -0.95 0.32 -0.07\r\nvn -0.91 0.4 -0.06\r\nvn -0.94 0.32 -0.11\r\nvn -0.91 0.41 -0.1\r\nvn -0.93 0.33 -0.14\r\nvn -0.9 0.41 -0.14\r\nvn -0.93 0.33 -0.18\r\nvn -0.9 0.41 -0.17\r\nvn -0.92 0.33 -0.21\r\nvn -0.89 0.41 -0.21\r\nvn -0.91 0.33 -0.26\r\nvn -0.88 0.41 -0.25\r\nvn -0.89 0.33 -0.31\r\nvn -0.86 0.42 -0.3\r\nvn -0.87 0.33 -0.36\r\nvn -0.84 0.42 -0.35\r\nvn -0.85 0.33 -0.41\r\nvn -0.82 0.41 -0.4\r\nvn -0.83 0.33 -0.45\r\nvn -0.8 0.41 -0.44\r\nvn -0.81 0.33 -0.49\r\nvn -0.78 0.41 -0.47\r\nvn -0.79 0.32 -0.52\r\nvn -0.76 0.4 -0.51\r\nvn -0.77 0.32 -0.56\r\nvn -0.74 0.4 -0.54\r\nvn -0.74 0.31 -0.59\r\nvn -0.72 0.4 -0.57\r\nvn -0.72 0.31 -0.62\r\nvn -0.7 0.39 -0.6\r\nvn -0.71 0.3 -0.64\r\nvn -0.68 0.39 -0.62\r\nvn -0.7 0.3 -0.65\r\nvn -0.67 0.39 -0.63\r\nvn -0.74 0.3 -0.59\r\nvn -0.71 0.4 -0.58\r\nvn -0.97 0.21 -0.12\r\nvn -0.91 0.41 -0.11\r\nvn -0.85 0.1 0.52\r\nvn -0.8 0.26 0.55\r\nvn -0.69 -0.01 0.73\r\nvn -0.69 0.19 0.7\r\nvn -0.19 -0.18 0.96\r\nvn -0.73 0.22 0.65\r\nvn 0.53 -0.41 0.75\r\nvn 0.65 -0.39 0.65\r\nvn 0.67 -0.4 0.62\r\nvn 0.68 -0.4 0.61\r\nvn 0.7 -0.41 0.59\r\nvn 0.71 -0.41 0.57\r\nvn 0.73 -0.41 0.54\r\nvn 0.75 -0.42 0.51\r\nvn 0.77 -0.42 0.47\r\nvn 0.79 -0.42 0.44\r\nvn 0.81 -0.43 0.39\r\nvn 0.83 -0.43 0.35\r\nvn 0.85 -0.43 0.3\r\nvn 0.87 -0.43 0.25\r\nvn 0.88 -0.43 0.2\r\nvn 0.89 -0.42 0.17\r\nvn 0.9 -0.42 0.13\r\nvn 0.9 -0.42 0.1\r\nvn 0.91 -0.42 0.06\r\nvn 0.91 -0.41 0.01\r\nvn 0.91 -0.41 -0.03\r\nvn 0.91 -0.4 -0.07\r\nvn 0.91 -0.39 -0.11\r\nvn 0.91 -0.39 -0.15\r\nvn 0.91 -0.38 -0.19\r\nvn 0.9 -0.37 -0.22\r\nvn 0.9 -0.36 -0.25\r\nvn 0.89 -0.36 -0.28\r\nvn 0.88 -0.35 -0.32\r\nvn 0.87 -0.34 -0.36\r\nvn 0.85 -0.32 -0.41\r\nvn 0.83 -0.31 -0.46\r\nvn 0.82 -0.29 -0.5\r\nvn 0.8 -0.28 -0.53\r\nvn 0.78 -0.27 -0.56\r\nvn 0.77 -0.26 -0.58\r\nvn 0.76 -0.25 -0.6\r\nvn 0.74 -0.24 -0.62\r\nvn 0.73 -0.24 -0.63\r\nvn 0.73 -0.24 -0.64\r\nvn 0.73 -0.24 -0.64\r\nvn 0.74 -0.24 -0.63\r\nvn 0.72 -0.23 -0.65\r\nvn 0.68 -0.21 -0.7\r\nvn 0.54 -0.18 -0.82\r\nvn -0.26 0.13 -0.96\r\nvn -0.76 0.4 -0.51\r\nvn -0.8 0.25 -0.55\r\nvn -0.77 0.41 -0.49\r\nvn -0.89 0.28 -0.36\r\nvn -0.85 0.42 -0.33\r\nvn -0.97 0.19 0.17\r\nvn -0.91 0.36 0.21\r\nvn -0.78 0.12 0.61\r\nvn -0.75 0.23 0.63\r\nvn -0.73 0.09 0.68\r\nvn -0.7 0.2 0.68\r\nvn -0.74 0.1 0.67\r\nvn -0.71 0.21 0.67\r\nvn -0.75 0.12 0.66\r\nvn -0.72 0.21 0.66\r\nvn -0.75 0.13 0.64\r\nvn -0.73 0.22 0.65\r\nvn -0.76 0.13 0.63\r\nvn -0.74 0.22 0.63\r\nvn -0.77 0.14 0.62\r\nvn -0.75 0.23 0.62\r\nvn -0.78 0.15 0.6\r\nvn -0.76 0.24 0.6\r\nvn -0.8 0.16 0.58\r\nvn -0.77 0.25 0.58\r\nvn -0.81 0.17 0.55\r\nvn -0.79 0.26 0.56\r\nvn -0.83 0.18 0.53\r\nvn -0.81 0.27 0.53\r\nvn -0.85 0.19 0.49\r\nvn -0.82 0.28 0.5\r\nvn -0.87 0.21 0.45\r\nvn -0.84 0.29 0.45\r\nvn -0.89 0.22 0.4\r\nvn -0.86 0.31 0.41\r\nvn -0.9 0.24 0.35\r\nvn -0.88 0.32 0.36\r\nvt 0.0196 0.4919\r\nvt 0.0152 0.4946\r\nvt 0.0146 0.4936\r\nvt 0.0188 0.4908\r\nvt 0.0138 0.4952\r\nvt 0.013 0.4947\r\nvt 0.0121 0.4938\r\nvt 0.0138 0.4926\r\nvt 0.0178 0.4896\r\nvt 0.265 0.3248\r\nvt 0.2704 0.325\r\nvt 0.2703 0.3265\r\nvt 0.2649 0.3262\r\nvt 0.2773 0.3255\r\nvt 0.2772 0.3269\r\nvt 0.2771 0.3287\r\nvt 0.2702 0.3283\r\nvt 0.2648 0.328\r\nvt 0.9771 0.3411\r\nvt 0.9862 0.3412\r\nvt 0.9864 0.3429\r\nvt 0.9772 0.3428\r\nvt 0.9927 0.341\r\nvt 0.993 0.3425\r\nvt 0.9933 0.3445\r\nvt 0.9867 0.345\r\nvt 0.9774 0.3449\r\nvt 0.6197 0.0026\r\nvt 0.6173 0.0045\r\nvt 0.6159 0.0033\r\nvt 0.6182 0.0015\r\nvt 0.6139 0.0068\r\nvt 0.6125 0.0057\r\nvt 0.6108 0.0042\r\nvt 0.614 0.0018\r\nvt 0.6164 -0\r\nvt 0.6289 0.0162\r\nvt 0.6279 0.0176\r\nvt 0.6271 0.0164\r\nvt 0.6283 0.015\r\nvt 0.6255 0.0203\r\nvt 0.6247 0.0188\r\nvt 0.6238 0.0173\r\nvt 0.6263 0.0151\r\nvt 0.6275 0.0138\r\nvt 0.5132 0.0822\r\nvt 0.5019 0.091\r\nvt 0.501 0.0899\r\nvt 0.5123 0.0811\r\nvt 0.49 0.1004\r\nvt 0.4891 0.0992\r\nvt 0.488 0.0978\r\nvt 0.4999 0.0885\r\nvt 0.5112 0.0797\r\nvt 0.5254 0.0948\r\nvt 0.5141 0.1036\r\nvt 0.5134 0.1025\r\nvt 0.5247 0.0937\r\nvt 0.5022 0.113\r\nvt 0.5015 0.1119\r\nvt 0.5006 0.1107\r\nvt 0.5125 0.1014\r\nvt 0.5238 0.0925\r\nvt 0.4629 0.1247\r\nvt 0.4783 0.1126\r\nvt 0.4797 0.1137\r\nvt 0.4642 0.1258\r\nvt 0.4916 0.1021\r\nvt 0.493 0.1032\r\nvt 0.4944 0.1043\r\nvt 0.4811 0.1148\r\nvt 0.4656 0.1269\r\nvt 0.9927 0.3384\r\nvt 0.9861 0.3387\r\nvt 0.9861 0.3371\r\nvt 0.9927 0.3369\r\nvt 0.977 0.3386\r\nvt 0.977 0.337\r\nvt 0.9771 0.3347\r\nvt 0.9863 0.3348\r\nvt 0.9929 0.3349\r\nvt 0.328 0.3257\r\nvt 0.3131 0.3251\r\nvt 0.3132 0.3235\r\nvt 0.328 0.3241\r\nvt 0.2996 0.3244\r\nvt 0.2997 0.3228\r\nvt 0.2996 0.3205\r\nvt 0.313 0.3212\r\nvt 0.3279 0.3219\r\nvt 0.2875 0.3237\r\nvt 0.2876 0.3221\r\nvt 0.2774 0.3231\r\nvt 0.2776 0.3215\r\nvt 0.2774 0.3192\r\nvt 0.2874 0.3198\r\nvt 0.2705 0.3227\r\nvt 0.2706 0.3211\r\nvt 0.265 0.3224\r\nvt 0.2651 0.3208\r\nvt 0.265 0.3185\r\nvt 0.2705 0.3188\r\nvt 0.0038 0.4862\r\nvt 0.0063 0.4838\r\nvt 0.0072 0.4853\r\nvt 0.0047 0.4878\r\nvt 0.0105 0.4799\r\nvt 0.0115 0.4813\r\nvt 0.0124 0.4827\r\nvt 0.0081 0.4868\r\nvt 0.0058 0.4896\r\nvt 0.1185 0.3937\r\nvt 0.1382 0.3785\r\nvt 0.1393 0.3799\r\nvt 0.1195 0.3951\r\nvt 0.1586 0.3626\r\nvt 0.1597 0.364\r\nvt 0.1607 0.3654\r\nvt 0.1403 0.3813\r\nvt 0.1206 0.3966\r\nvt 0.0358 0.475\r\nvt 0.0256 0.4834\r\nvt 0.0251 0.4828\r\nvt 0.0354 0.4744\r\nvt 0.0173 0.489\r\nvt 0.0167 0.4881\r\nvt 0.0245 0.4819\r\nvt 0.0347 0.4735\r\nvt 0.0133 0.4921\r\nvt 0.0117 0.4933\r\nvt 0.0114 0.4925\r\nvt 0.0127 0.4914\r\nvt 0.2704 0.3243\r\nvt 0.2774 0.3247\r\nvt 0.265 0.324\r\nvt 0.265 0.3236\r\nvt 0.2704 0.3239\r\nvt 0.2774 0.3243\r\nvt 0.2634 0.3118\r\nvt 0.2689 0.3122\r\nvt 0.2688 0.313\r\nvt 0.2634 0.3126\r\nvt 0.2758 0.3126\r\nvt 0.2757 0.3134\r\nvt 0.276 0.3143\r\nvt 0.269 0.3139\r\nvt 0.2636 0.3135\r\nvt 0.2858 0.3132\r\nvt 0.2857 0.314\r\nvt 0.2979 0.3139\r\nvt 0.2979 0.3146\r\nvt 0.2981 0.3156\r\nvt 0.286 0.3149\r\nvt 0.618 0.0051\r\nvt 0.6145 0.0074\r\nvt 0.6204 0.0032\r\nvt 0.6207 0.0036\r\nvt 0.6182 0.0054\r\nvt 0.6147 0.0077\r\nvt 0.626 0.0145\r\nvt 0.6271 0.0133\r\nvt 0.6234 0.0166\r\nvt 0.6227 0.0158\r\nvt 0.6255 0.0137\r\nvt 0.6265 0.0128\r\nvt 0.6019 0.0156\r\nvt 0.6087 0.0105\r\nvt 0.6093 0.0111\r\nvt 0.6023 0.0162\r\nvt 0.6095 0.0114\r\nvt 0.6026 0.0165\r\nvt 0.6035 0.0174\r\nvt 0.5945 0.024\r\nvt 0.5939 0.0235\r\nvt 0.6028 0.0169\r\nvt 0.584 0.0317\r\nvt 0.5834 0.0311\r\nvt 0.5831 0.0308\r\nvt 0.5936 0.0231\r\nvt 0.5869 0.0338\r\nvt 0.5974 0.0261\r\nvt 0.5981 0.0267\r\nvt 0.5875 0.0344\r\nvt 0.6063 0.0195\r\nvt 0.607 0.0201\r\nvt 0.6078 0.021\r\nvt 0.5988 0.0275\r\nvt 0.5883 0.0352\r\nvt 0.6132 0.0144\r\nvt 0.6139 0.015\r\nvt 0.6184 0.0107\r\nvt 0.619 0.0112\r\nvt 0.6196 0.0121\r\nvt 0.6146 0.0158\r\nvt 0.6217 0.0045\r\nvt 0.6191 0.0062\r\nvt 0.6185 0.0057\r\nvt 0.621 0.0039\r\nvt 0.6156 0.0086\r\nvt 0.615 0.008\r\nvt 0.6222 0.0083\r\nvt 0.6228 0.0089\r\nvt 0.6254 0.007\r\nvt 0.6265 0.0078\r\nvt 0.6269 0.0082\r\nvt 0.6234 0.0098\r\nvt 0.9861 0.3395\r\nvt 0.977 0.3394\r\nvt 0.9927 0.3393\r\nvt 0.9926 0.3398\r\nvt 0.9861 0.3399\r\nvt 0.977 0.3398\r\nvt 0.9866 0.332\r\nvt 0.9932 0.3322\r\nvt 0.9774 0.3319\r\nvt 0.9777 0.3295\r\nvt 0.9869 0.3296\r\nvt 0.9935 0.3298\r\nvt 0.9659 0.3346\r\nvt 0.9661 0.3318\r\nvt 0.9528 0.3346\r\nvt 0.953 0.3317\r\nvt 0.9533 0.3293\r\nvt 0.9664 0.3294\r\nvt 0.9383 0.3346\r\nvt 0.9385 0.3317\r\nvt 0.9231 0.3346\r\nvt 0.9233 0.3317\r\nvt 0.9236 0.3293\r\nvt 0.9387 0.3293\r\nvt 0.9082 0.3345\r\nvt 0.9084 0.3316\r\nvt 0.8934 0.3343\r\nvt 0.8936 0.3315\r\nvt 0.8937 0.3291\r\nvt 0.9086 0.3292\r\nvt 0.8788 0.3341\r\nvt 0.8789 0.3313\r\nvt 0.8642 0.3339\r\nvt 0.8642 0.331\r\nvt 0.8643 0.3286\r\nvt 0.879 0.3288\r\nvt 0.8495 0.3336\r\nvt 0.8496 0.3307\r\nvt 0.8344 0.3332\r\nvt 0.8344 0.3304\r\nvt 0.8344 0.328\r\nvt 0.8496 0.3283\r\nvt 0.818 0.3329\r\nvt 0.818 0.33\r\nvt 0.8 0.3325\r\nvt 0.7999 0.3296\r\nvt 0.7998 0.3272\r\nvt 0.818 0.3276\r\nvt 0.7796 0.3321\r\nvt 0.7795 0.3292\r\nvt 0.7578 0.3317\r\nvt 0.7577 0.3288\r\nvt 0.7576 0.3264\r\nvt 0.7794 0.3268\r\nvt 0.7354 0.3313\r\nvt 0.7352 0.3284\r\nvt 0.7132 0.3309\r\nvt 0.713 0.328\r\nvt 0.7128 0.3256\r\nvt 0.7351 0.326\r\nvt 0.6919 0.3304\r\nvt 0.6917 0.3276\r\nvt 0.6715 0.3299\r\nvt 0.6713 0.327\r\nvt 0.671 0.3246\r\nvt 0.6915 0.3251\r\nvt 0.6517 0.3294\r\nvt 0.6514 0.3265\r\nvt 0.6321 0.3289\r\nvt 0.6318 0.3261\r\nvt 0.6315 0.3237\r\nvt 0.6511 0.3241\r\nvt 0.6128 0.3285\r\nvt 0.6124 0.3257\r\nvt 0.5931 0.3282\r\nvt 0.5927 0.3254\r\nvt 0.5923 0.323\r\nvt 0.6121 0.3233\r\nvt 0.5726 0.3278\r\nvt 0.5722 0.325\r\nvt 0.5508 0.3273\r\nvt 0.5504 0.3245\r\nvt 0.55 0.3222\r\nvt 0.5718 0.3226\r\nvt 0.5273 0.3267\r\nvt 0.5268 0.3239\r\nvt 0.5026 0.326\r\nvt 0.5021 0.3232\r\nvt 0.5016 0.3209\r\nvt 0.5263 0.3215\r\nvt 0.4773 0.3253\r\nvt 0.4768 0.3225\r\nvt 0.4522 0.3247\r\nvt 0.4517 0.3219\r\nvt 0.4512 0.3196\r\nvt 0.4763 0.3202\r\nvt 0.4279 0.3242\r\nvt 0.4274 0.3214\r\nvt 0.4047 0.3238\r\nvt 0.4041 0.321\r\nvt 0.4035 0.3186\r\nvt 0.4268 0.3191\r\nvt 0.3828 0.3234\r\nvt 0.3822 0.3206\r\nvt 0.3626 0.3229\r\nvt 0.3619 0.3202\r\nvt 0.3613 0.3179\r\nvt 0.3816 0.3183\r\nvt 0.3443 0.3224\r\nvt 0.3437 0.3197\r\nvt 0.3272 0.3191\r\nvt 0.3265 0.3168\r\nvt 0.343 0.3174\r\nvt 0.313 0.3259\r\nvt 0.2996 0.3252\r\nvt 0.3279 0.3265\r\nvt 0.3279 0.3269\r\nvt 0.313 0.3263\r\nvt 0.2996 0.3256\r\nvt 0.3123 0.3185\r\nvt 0.2989 0.3178\r\nvt 0.3116 0.3162\r\nvt 0.2874 0.3245\r\nvt 0.2774 0.3239\r\nvt 0.2874 0.3249\r\nvt 0.2867 0.3171\r\nvt 0.2767 0.3165\r\nvt 0.2697 0.3161\r\nvt 0.2643 0.3158\r\nvt 0.0058 0.4831\r\nvt 0.0101 0.4792\r\nvt 0.0033 0.4855\r\nvt 0.003 0.4851\r\nvt 0.0055 0.4828\r\nvt 0.0098 0.4789\r\nvt 0.0087 0.4874\r\nvt 0.0064 0.4906\r\nvt 0.0129 0.4834\r\nvt 0.0136 0.4842\r\nvt 0.0094 0.4882\r\nvt 0.0067 0.491\r\nvt 0.0204 0.4761\r\nvt 0.0209 0.4768\r\nvt 0.0307 0.4677\r\nvt 0.0312 0.4684\r\nvt 0.0318 0.4693\r\nvt 0.0216 0.4777\r\nvt 0.4228 0.1529\r\nvt 0.4219 0.1518\r\nvt 0.4421 0.136\r\nvt 0.443 0.1372\r\nvt 0.4208 0.1503\r\nvt 0.441 0.1346\r\nvt 0.4593 0.1204\r\nvt 0.4604 0.1218\r\nvt 0.4613 0.1229\r\nvt 0.4347 0.1657\r\nvt 0.434 0.1647\r\nvt 0.4543 0.1489\r\nvt 0.455 0.1499\r\nvt 0.4331 0.1635\r\nvt 0.4534 0.1477\r\nvt 0.4717 0.1334\r\nvt 0.4726 0.1345\r\nvt 0.4733 0.1356\r\nvt 0.3807 0.1857\r\nvt 0.3798 0.1845\r\nvt 0.4008 0.1682\r\nvt 0.4017 0.1693\r\nvt 0.3787 0.1831\r\nvt 0.3997 0.1668\r\nvt 0.3925 0.1986\r\nvt 0.3917 0.1976\r\nvt 0.4128 0.1811\r\nvt 0.4136 0.1822\r\nvt 0.3909 0.1965\r\nvt 0.4119 0.18\r\nvt 0.3418 0.2163\r\nvt 0.3409 0.2152\r\nvt 0.3599 0.2002\r\nvt 0.3608 0.2013\r\nvt 0.3398 0.2137\r\nvt 0.3588 0.1988\r\nvt 0.3534 0.2294\r\nvt 0.3527 0.2284\r\nvt 0.3717 0.2133\r\nvt 0.3725 0.2144\r\nvt 0.3518 0.2272\r\nvt 0.3708 0.2122\r\nvt 0.3062 0.2443\r\nvt 0.3054 0.2431\r\nvt 0.3228 0.2295\r\nvt 0.3237 0.2306\r\nvt 0.3043 0.2417\r\nvt 0.3217 0.228\r\nvt 0.3177 0.2575\r\nvt 0.317 0.2565\r\nvt 0.3344 0.2427\r\nvt 0.3352 0.2438\r\nvt 0.3161 0.2553\r\nvt 0.3335 0.2416\r\nvt 0.2728 0.2702\r\nvt 0.272 0.2691\r\nvt 0.2886 0.2562\r\nvt 0.2895 0.2573\r\nvt 0.2709 0.2677\r\nvt 0.2875 0.2548\r\nvt 0.2842 0.2836\r\nvt 0.2834 0.2825\r\nvt 0.3001 0.2696\r\nvt 0.3009 0.2706\r\nvt 0.2825 0.2814\r\nvt 0.2992 0.2684\r\nvt 0.238 0.2973\r\nvt 0.2371 0.2962\r\nvt 0.255 0.2823\r\nvt 0.2559 0.2834\r\nvt 0.236 0.2948\r\nvt 0.2539 0.2809\r\nvt 0.2492 0.3108\r\nvt 0.2484 0.3098\r\nvt 0.2664 0.2958\r\nvt 0.2671 0.2968\r\nvt 0.2475 0.3087\r\nvt 0.2655 0.2947\r\nvt 0.1986 0.3283\r\nvt 0.1977 0.3272\r\nvt 0.2179 0.3113\r\nvt 0.2187 0.3124\r\nvt 0.1966 0.3258\r\nvt 0.2168 0.3099\r\nvt 0.2096 0.3419\r\nvt 0.2088 0.3409\r\nvt 0.2291 0.325\r\nvt 0.2298 0.326\r\nvt 0.2079 0.3398\r\nvt 0.2282 0.3238\r\nvt 0.1571 0.3608\r\nvt 0.1563 0.3596\r\nvt 0.177 0.3434\r\nvt 0.1779 0.3445\r\nvt 0.1552 0.3582\r\nvt 0.1759 0.342\r\nvt 0.168 0.3745\r\nvt 0.1672 0.3735\r\nvt 0.188 0.3572\r\nvt 0.1888 0.3582\r\nvt 0.1663 0.3724\r\nvt 0.1871 0.3561\r\nvt 0.1171 0.3918\r\nvt 0.1162 0.3907\r\nvt 0.1359 0.3755\r\nvt 0.1368 0.3766\r\nvt 0.1151 0.3893\r\nvt 0.1348 0.374\r\nvt 0.1277 0.4057\r\nvt 0.1269 0.4047\r\nvt 0.1467 0.3894\r\nvt 0.1475 0.3904\r\nvt 0.1261 0.4036\r\nvt 0.1459 0.3883\r\nvt 0.0811 0.4198\r\nvt 0.0802 0.4187\r\nvt 0.0975 0.4052\r\nvt 0.0984 0.4063\r\nvt 0.0791 0.4173\r\nvt 0.0964 0.4038\r\nvt 0.0915 0.4338\r\nvt 0.0907 0.4328\r\nvt 0.1082 0.4193\r\nvt 0.1089 0.4203\r\nvt 0.0899 0.4317\r\nvt 0.1073 0.4181\r\nvt 0.0514 0.4433\r\nvt 0.0505 0.4422\r\nvt 0.0646 0.431\r\nvt 0.0655 0.4321\r\nvt 0.0494 0.4408\r\nvt 0.0635 0.4296\r\nvt 0.0617 0.4574\r\nvt 0.0609 0.4565\r\nvt 0.075 0.4452\r\nvt 0.0758 0.4462\r\nvt 0.06 0.4554\r\nvt 0.0741 0.4441\r\nvt 0.0274 0.4628\r\nvt 0.0264 0.4617\r\nvt 0.0379 0.4524\r\nvt 0.0388 0.4535\r\nvt 0.0253 0.4603\r\nvt 0.0367 0.451\r\nvt 0.0376 0.4771\r\nvt 0.0368 0.4761\r\nvt 0.0482 0.4668\r\nvt 0.049 0.4677\r\nvt 0.0473 0.4656\r\nvt 0.009 0.478\r\nvt 0.008 0.4768\r\nvt 0.0161 0.4701\r\nvt 0.017 0.4713\r\nvt 0.0068 0.4754\r\nvt 0.0149 0.4687\r\nvt 0.0266 0.4846\r\nvt 0.0275 0.4856\r\nvt 0.0021 0.484\r\nvt 0.0012 0.4829\r\nvt 0.0037 0.4807\r\nvt 0.0047 0.4819\r\nvt 0 0.4815\r\nvt 0.0025 0.4793\r\nvt 0.276 0.3093\r\nvt 0.2759 0.3111\r\nvt 0.269 0.3107\r\nvt 0.2692 0.3088\r\nvt 0.2636 0.3103\r\nvt 0.2638 0.3083\r\nvt 0.2995 0.3268\r\nvt 0.2994 0.3282\r\nvt 0.2872 0.3275\r\nvt 0.2873 0.3261\r\nvt 0.2993 0.33\r\nvt 0.2871 0.3293\r\nvt 0.2981 0.3106\r\nvt 0.298 0.3124\r\nvt 0.2859 0.3118\r\nvt 0.286 0.31\r\nvt 0.3279 0.3281\r\nvt 0.3278 0.3295\r\nvt 0.3129 0.3289\r\nvt 0.313 0.3274\r\nvt 0.3278 0.3313\r\nvt 0.3128 0.3307\r\nvt 0.3264 0.3119\r\nvt 0.3264 0.3137\r\nvt 0.3115 0.3131\r\nvt 0.3115 0.3113\r\nvt 0.3263 0.3151\r\nvt 0.3114 0.3145\r\nvt 0.3626 0.3292\r\nvt 0.3626 0.3306\r\nvt 0.3443 0.3301\r\nvt 0.3444 0.3287\r\nvt 0.3626 0.3324\r\nvt 0.3443 0.3319\r\nvt 0.3612 0.3129\r\nvt 0.3611 0.3147\r\nvt 0.3429 0.3142\r\nvt 0.3429 0.3125\r\nvt 0.3611 0.3162\r\nvt 0.3428 0.3157\r\nvt 0.4048 0.33\r\nvt 0.4047 0.3314\r\nvt 0.3828 0.331\r\nvt 0.3829 0.3296\r\nvt 0.4047 0.3332\r\nvt 0.3828 0.3328\r\nvt 0.4034 0.3137\r\nvt 0.4033 0.3155\r\nvt 0.3814 0.3151\r\nvt 0.3814 0.3133\r\nvt 0.4033 0.3169\r\nvt 0.3814 0.3166\r\nvt 0.4523 0.3309\r\nvt 0.4523 0.3324\r\nvt 0.428 0.3319\r\nvt 0.428 0.3304\r\nvt 0.4522 0.3342\r\nvt 0.4279 0.3337\r\nvt 0.4511 0.3146\r\nvt 0.451 0.3164\r\nvt 0.4266 0.3159\r\nvt 0.4267 0.3141\r\nvt 0.451 0.3178\r\nvt 0.4266 0.3173\r\nvt 0.5026 0.3323\r\nvt 0.5025 0.3337\r\nvt 0.4773 0.333\r\nvt 0.4774 0.3316\r\nvt 0.5025 0.3355\r\nvt 0.4773 0.3348\r\nvt 0.5015 0.3159\r\nvt 0.5015 0.3177\r\nvt 0.4762 0.317\r\nvt 0.4762 0.3152\r\nvt 0.5014 0.3191\r\nvt 0.4761 0.3184\r\nvt 0.5509 0.3336\r\nvt 0.5508 0.335\r\nvt 0.5273 0.3344\r\nvt 0.5273 0.333\r\nvt 0.5508 0.3368\r\nvt 0.5272 0.3362\r\nvt 0.5499 0.3172\r\nvt 0.5499 0.319\r\nvt 0.5262 0.3184\r\nvt 0.5263 0.3166\r\nvt 0.5498 0.3204\r\nvt 0.5262 0.3198\r\nvt 0.5931 0.3345\r\nvt 0.5931 0.3359\r\nvt 0.5726 0.3355\r\nvt 0.5726 0.3341\r\nvt 0.593 0.3377\r\nvt 0.5726 0.3373\r\nvt 0.5923 0.318\r\nvt 0.5922 0.3198\r\nvt 0.5717 0.3194\r\nvt 0.5717 0.3176\r\nvt 0.5922 0.3212\r\nvt 0.5717 0.3209\r\nvt 0.6321 0.3352\r\nvt 0.6321 0.3366\r\nvt 0.6128 0.3362\r\nvt 0.6128 0.3348\r\nvt 0.6321 0.3384\r\nvt 0.6127 0.338\r\nvt 0.6315 0.3187\r\nvt 0.6314 0.3205\r\nvt 0.612 0.3201\r\nvt 0.612 0.3183\r\nvt 0.6314 0.3219\r\nvt 0.612 0.3216\r\nvt 0.6715 0.3361\r\nvt 0.6714 0.3376\r\nvt 0.6516 0.3371\r\nvt 0.6516 0.3356\r\nvt 0.6714 0.3394\r\nvt 0.6515 0.3389\r\nvt 0.671 0.3196\r\nvt 0.671 0.3214\r\nvt 0.651 0.3209\r\nvt 0.6511 0.3191\r\nvt 0.6709 0.3229\r\nvt 0.651 0.3224\r\nvt 0.7131 0.3371\r\nvt 0.7131 0.3386\r\nvt 0.6919 0.3381\r\nvt 0.6919 0.3367\r\nvt 0.713 0.3404\r\nvt 0.6918 0.3399\r\nvt 0.7128 0.3206\r\nvt 0.7127 0.3224\r\nvt 0.6915 0.3219\r\nvt 0.6915 0.3202\r\nvt 0.7127 0.3239\r\nvt 0.6914 0.3234\r\nvt 0.7578 0.3379\r\nvt 0.7577 0.3394\r\nvt 0.7353 0.339\r\nvt 0.7353 0.3376\r\nvt 0.7577 0.3412\r\nvt 0.7353 0.3408\r\nvt 0.7576 0.3214\r\nvt 0.7575 0.3232\r\nvt 0.735 0.3228\r\nvt 0.7351 0.321\r\nvt 0.7575 0.3246\r\nvt 0.735 0.3243\r\nvt 0.7999 0.3387\r\nvt 0.7999 0.3402\r\nvt 0.7795 0.3398\r\nvt 0.7796 0.3383\r\nvt 0.7998 0.342\r\nvt 0.7795 0.3416\r\nvt 0.7999 0.3222\r\nvt 0.7999 0.324\r\nvt 0.7794 0.3236\r\nvt 0.7795 0.3218\r\nvt 0.7998 0.3254\r\nvt 0.7794 0.325\r\nvt 0.8342 0.3395\r\nvt 0.8342 0.3409\r\nvt 0.8179 0.3406\r\nvt 0.8179 0.3391\r\nvt 0.8342 0.3427\r\nvt 0.8178 0.3424\r\nvt 0.8344 0.323\r\nvt 0.8344 0.3247\r\nvt 0.818 0.3244\r\nvt 0.818 0.3226\r\nvt 0.8344 0.3262\r\nvt 0.818 0.3258\r\nvt 0.864 0.3401\r\nvt 0.864 0.3416\r\nvt 0.8494 0.3413\r\nvt 0.8494 0.3398\r\nvt 0.864 0.3434\r\nvt 0.8494 0.3431\r\nvt 0.8644 0.3236\r\nvt 0.8644 0.3254\r\nvt 0.8497 0.3251\r\nvt 0.8497 0.3233\r\nvt 0.8644 0.3268\r\nvt 0.8497 0.3265\r\nvt 0.8933 0.3406\r\nvt 0.8933 0.342\r\nvt 0.8786 0.3418\r\nvt 0.8787 0.3404\r\nvt 0.8933 0.3438\r\nvt 0.8786 0.3436\r\nvt 0.8938 0.3241\r\nvt 0.8938 0.3258\r\nvt 0.8791 0.3256\r\nvt 0.8791 0.3238\r\nvt 0.8938 0.3273\r\nvt 0.8791 0.3271\r\nvt 0.9231 0.3408\r\nvt 0.9231 0.3423\r\nvt 0.9081 0.3422\r\nvt 0.9081 0.3408\r\nvt 0.923 0.3441\r\nvt 0.9081 0.344\r\nvt 0.9236 0.3243\r\nvt 0.9236 0.3261\r\nvt 0.9087 0.326\r\nvt 0.9087 0.3242\r\nvt 0.9236 0.3275\r\nvt 0.9086 0.3275\r\nvt 0.9527 0.3408\r\nvt 0.9527 0.3423\r\nvt 0.9382 0.3423\r\nvt 0.9382 0.3408\r\nvt 0.9527 0.3442\r\nvt 0.9382 0.3441\r\nvt 0.9534 0.3242\r\nvt 0.9534 0.326\r\nvt 0.9388 0.3261\r\nvt 0.9388 0.3243\r\nvt 0.9534 0.3275\r\nvt 0.9388 0.3275\r\nvt 0.9658 0.3425\r\nvt 0.9658 0.3409\r\nvt 0.9659 0.3445\r\nvt 0.9783 0.3238\r\nvt 0.978 0.326\r\nvt 0.9666 0.326\r\nvt 0.9667 0.324\r\nvt 0.9778 0.3276\r\nvt 0.9665 0.3276\r\nvt 0.9942 0.3247\r\nvt 0.9939 0.3265\r\nvt 0.9873 0.3261\r\nvt 0.9876 0.324\r\nvt 0.9937 0.3281\r\nvt 0.987 0.3278\r\nvt 0.9985 0.3401\r\nvt 0.9986 0.3405\r\nvt 0.9965 0.3416\r\nvt 0.9964 0.3406\r\nvt 0.9986 0.341\r\nvt 0.9967 0.3429\r\nvt 0.9977 0.3262\r\nvt 0.9975 0.3274\r\nvt 0.9997 0.3279\r\nvt 0.9997 0.3283\r\nvt 0.9996 0.3287\r\nvt 0.9974 0.3284\r\nvt 0.6009 0.0145\r\nvt 0.6076 0.0093\r\nvt 0.5997 0.0131\r\nvt 0.6061 0.0079\r\nvt 0.6142 0.0284\r\nvt 0.6136 0.0271\r\nvt 0.6203 0.0223\r\nvt 0.6209 0.0237\r\nvt 0.6127 0.0259\r\nvt 0.6194 0.0209\r\nvt 0.5825 0.0299\r\nvt 0.5816 0.0287\r\nvt 0.5921 0.0211\r\nvt 0.5929 0.0222\r\nvt 0.5805 0.0273\r\nvt 0.591 0.0196\r\nvt 0.5947 0.0423\r\nvt 0.5941 0.0412\r\nvt 0.6046 0.0335\r\nvt 0.6052 0.0347\r\nvt 0.5932 0.04\r\nvt 0.6038 0.0324\r\nvt 0.559 0.0472\r\nvt 0.5582 0.046\r\nvt 0.5701 0.0372\r\nvt 0.5709 0.0384\r\nvt 0.5571 0.0446\r\nvt 0.569 0.0358\r\nvt 0.5713 0.0597\r\nvt 0.5707 0.0585\r\nvt 0.5826 0.0497\r\nvt 0.5832 0.0508\r\nvt 0.5698 0.0574\r\nvt 0.5817 0.0485\r\nvt 0.5358 0.0648\r\nvt 0.5349 0.0636\r\nvt 0.5465 0.0548\r\nvt 0.5473 0.056\r\nvt 0.5339 0.0622\r\nvt 0.5454 0.0534\r\nvt 0.5481 0.0772\r\nvt 0.5474 0.0761\r\nvt 0.559 0.0673\r\nvt 0.5596 0.0685\r\nvt 0.5465 0.075\r\nvt 0.5581 0.0662\r\nvt 0.5236 0.0723\r\nvt 0.5244 0.0735\r\nvt 0.5225 0.0709\r\nvt 0.536 0.0849\r\nvt 0.5367 0.086\r\nvt 0.5351 0.0838\r\nvt 0.4758 0.1097\r\nvt 0.4767 0.1108\r\nvt 0.4747 0.1083\r\nvt 0.4881 0.1224\r\nvt 0.4888 0.1235\r\nvt 0.4872 0.1212\r\nvt 0.4459 0.1401\r\nvt 0.4446 0.139\r\nvt 0.4472 0.1412\r\nvt 0.427 0.157\r\nvt 0.4257 0.1558\r\nvt 0.4244 0.1547\r\nvt 0.5147 0.084\r\nvt 0.5162 0.0851\r\nvt 0.5049 0.0939\r\nvt 0.5035 0.0928\r\nvt 0.5176 0.0862\r\nvt 0.5063 0.095\r\nvt 0.5374 0.0665\r\nvt 0.5388 0.0676\r\nvt 0.5274 0.0763\r\nvt 0.526 0.0753\r\nvt 0.5403 0.0687\r\nvt 0.5289 0.0774\r\nvt 0.5606 0.049\r\nvt 0.562 0.0501\r\nvt 0.5503 0.0589\r\nvt 0.5489 0.0578\r\nvt 0.5635 0.0511\r\nvt 0.5518 0.0599\r\nvt 0.5854 0.0328\r\nvt 0.5739 0.0412\r\nvt 0.5725 0.0402\r\nvt 0.5754 0.0423\r\nvt 0.6049 0.0185\r\nvt 0.596 0.0251\r\nvt 0.617 0.0096\r\nvt 0.6118 0.0133\r\nvt 0.6104 0.0123\r\nvt 0.6235 0.0056\r\nvt 0.6207 0.0073\r\nvt 0.9965 0.3362\r\nvt 0.9964 0.3373\r\nvt 0.9966 0.3347\r\nvt 0.9989 0.3344\r\nvt 0.9988 0.3351\r\nvt 0.9988 0.3356\r\nvt 0.9527 0.3385\r\nvt 0.9527 0.3368\r\nvt 0.9658 0.3369\r\nvt 0.9658 0.3385\r\nvt 0.9231 0.3385\r\nvt 0.9231 0.3369\r\nvt 0.9382 0.3368\r\nvt 0.9382 0.3385\r\nvt 0.8934 0.3382\r\nvt 0.8934 0.3366\r\nvt 0.9081 0.3368\r\nvt 0.9081 0.3384\r\nvt 0.8641 0.3378\r\nvt 0.8641 0.3362\r\nvt 0.8787 0.3364\r\nvt 0.8787 0.338\r\nvt 0.8343 0.3371\r\nvt 0.8343 0.3355\r\nvt 0.8495 0.3359\r\nvt 0.8495 0.3375\r\nvt 0.8 0.3364\r\nvt 0.8 0.3347\r\nvt 0.818 0.3351\r\nvt 0.818 0.3368\r\nvt 0.7578 0.3356\r\nvt 0.7578 0.334\r\nvt 0.7797 0.3343\r\nvt 0.7796 0.336\r\nvt 0.7132 0.3348\r\nvt 0.7132 0.3332\r\nvt 0.7354 0.3336\r\nvt 0.7354 0.3352\r\nvt 0.6715 0.3338\r\nvt 0.6716 0.3322\r\nvt 0.692 0.3327\r\nvt 0.692 0.3343\r\nvt 0.6322 0.3328\r\nvt 0.6322 0.3312\r\nvt 0.6517 0.3316\r\nvt 0.6517 0.3333\r\nvt 0.5932 0.3321\r\nvt 0.5932 0.3305\r\nvt 0.6129 0.3308\r\nvt 0.6128 0.3324\r\nvt 0.5509 0.3312\r\nvt 0.551 0.3296\r\nvt 0.5727 0.3301\r\nvt 0.5727 0.3317\r\nvt 0.5026 0.3299\r\nvt 0.5027 0.3283\r\nvt 0.5274 0.329\r\nvt 0.5274 0.3306\r\nvt 0.4523 0.3286\r\nvt 0.4524 0.327\r\nvt 0.4775 0.3276\r\nvt 0.4774 0.3292\r\nvt 0.4048 0.3276\r\nvt 0.4048 0.326\r\nvt 0.4281 0.3265\r\nvt 0.428 0.3281\r\nvt 0.3627 0.3268\r\nvt 0.3627 0.3252\r\nvt 0.383 0.3256\r\nvt 0.3829 0.3272\r\nvt 0.3445 0.3247\r\nvt 0.3444 0.3263\r\nvt 0.0288 0.4647\r\nvt 0.0297 0.4662\r\nvt 0.0194 0.4746\r\nvt 0.0185 0.4732\r\nvt 0.0529 0.4452\r\nvt 0.0538 0.4467\r\nvt 0.0411 0.4569\r\nvt 0.0402 0.4554\r\nvt 0.0548 0.4481\r\nvt 0.0421 0.4584\r\nvt 0.0825 0.4217\r\nvt 0.0835 0.4231\r\nvt 0.0679 0.4354\r\nvt 0.0669 0.434\r\nvt 0.0845 0.4246\r\nvt 0.0688 0.4369\r\nvt 0.1008 0.4096\r\nvt 0.0998 0.4082\r\nvt 0.1018 0.4111\r\nvt 0.2 0.3302\r\nvt 0.2011 0.3315\r\nvt 0.1804 0.3478\r\nvt 0.1793 0.3464\r\nvt 0.2023 0.3328\r\nvt 0.1815 0.3491\r\nvt 0.2395 0.2992\r\nvt 0.2406 0.3005\r\nvt 0.2214 0.3156\r\nvt 0.2202 0.3143\r\nvt 0.2418 0.3018\r\nvt 0.2225 0.3169\r\nvt 0.2743 0.2721\r\nvt 0.2755 0.2733\r\nvt 0.2585 0.2866\r\nvt 0.2574 0.2853\r\nvt 0.2767 0.2746\r\nvt 0.2597 0.2879\r\nvt 0.3078 0.2461\r\nvt 0.309 0.2473\r\nvt 0.2922 0.2604\r\nvt 0.291 0.2592\r\nvt 0.3102 0.2486\r\nvt 0.2934 0.2617\r\nvt 0.3433 0.2181\r\nvt 0.3446 0.2193\r\nvt 0.3264 0.2336\r\nvt 0.3252 0.2324\r\nvt 0.3459 0.2205\r\nvt 0.3277 0.2349\r\nvt 0.3823 0.1875\r\nvt 0.3836 0.1887\r\nvt 0.3636 0.2043\r\nvt 0.3623 0.2031\r\nvt 0.3849 0.1899\r\nvt 0.3649 0.2055\r\nvt 0.4046 0.1723\r\nvt 0.4033 0.1711\r\nvt 0.4059 0.1735\r\nvt 0.4435 0.1378\r\nvt 0.4233 0.1535\r\nvt 0.4618 0.1235\r\nvt 0.462 0.1238\r\nvt 0.4437 0.1381\r\nvt 0.4235 0.1538\r\nvt 0.4529 0.1471\r\nvt 0.4713 0.1328\r\nvt 0.4326 0.1629\r\nvt 0.4319 0.1621\r\nvt 0.4522 0.1464\r\nvt 0.4705 0.132\r\nvt 0.4022 0.1699\r\nvt 0.3812 0.1863\r\nvt 0.4024 0.1702\r\nvt 0.3814 0.1866\r\nvt 0.4115 0.1794\r\nvt 0.3904 0.1959\r\nvt 0.3897 0.1951\r\nvt 0.4108 0.1786\r\nvt 0.3613 0.2019\r\nvt 0.3423 0.2169\r\nvt 0.3615 0.2022\r\nvt 0.3425 0.2172\r\nvt 0.3704 0.2116\r\nvt 0.3513 0.2266\r\nvt 0.3506 0.2258\r\nvt 0.3697 0.2108\r\nvt 0.3241 0.2312\r\nvt 0.3067 0.2449\r\nvt 0.3244 0.2315\r\nvt 0.307 0.2452\r\nvt 0.3331 0.241\r\nvt 0.3156 0.2547\r\nvt 0.3149 0.2539\r\nvt 0.3324 0.2402\r\nvt 0.2899 0.2579\r\nvt 0.2733 0.2708\r\nvt 0.2902 0.2583\r\nvt 0.2736 0.2712\r\nvt 0.2988 0.2678\r\nvt 0.2821 0.2808\r\nvt 0.2814 0.28\r\nvt 0.2981 0.267\r\nvt 0.2563 0.284\r\nvt 0.2385 0.2979\r\nvt 0.2566 0.2843\r\nvt 0.2387 0.2983\r\nvt 0.265 0.294\r\nvt 0.2471 0.3081\r\nvt 0.2464 0.3072\r\nvt 0.2643 0.2932\r\nvt 0.2192 0.313\r\nvt 0.199 0.3289\r\nvt 0.2195 0.3134\r\nvt 0.1993 0.3292\r\nvt 0.2277 0.3232\r\nvt 0.2074 0.3392\r\nvt 0.2068 0.3383\r\nvt 0.227 0.3224\r\nvt 0.1783 0.3451\r\nvt 0.1576 0.3614\r\nvt 0.1786 0.3455\r\nvt 0.1578 0.3617\r\nvt 0.1867 0.3555\r\nvt 0.1658 0.3718\r\nvt 0.1652 0.3709\r\nvt 0.186 0.3546\r\nvt 0.1372 0.3772\r\nvt 0.1175 0.3925\r\nvt 0.1375 0.3775\r\nvt 0.1178 0.3928\r\nvt 0.1454 0.3877\r\nvt 0.1256 0.403\r\nvt 0.1249 0.4022\r\nvt 0.1447 0.3868\r\nvt 0.0989 0.4069\r\nvt 0.0815 0.4204\r\nvt 0.0991 0.4072\r\nvt 0.0818 0.4207\r\nvt 0.1068 0.4175\r\nvt 0.0894 0.4311\r\nvt 0.0887 0.4303\r\nvt 0.1062 0.4167\r\nvt 0.0659 0.4327\r\nvt 0.0519 0.4439\r\nvt 0.0662 0.433\r\nvt 0.0521 0.4442\r\nvt 0.0737 0.4435\r\nvt 0.0595 0.4548\r\nvt 0.0589 0.4539\r\nvt 0.073 0.4426\r\nvt 0.0393 0.4541\r\nvt 0.0278 0.4634\r\nvt 0.0395 0.4544\r\nvt 0.0281 0.4637\r\nvt 0.0468 0.465\r\nvt 0.0462 0.4642\r\nvt 0.0175 0.4719\r\nvt 0.0095 0.4786\r\nvt 0.0178 0.4722\r\nvt 0.0052 0.4825\r\nvt 0.0027 0.4847\r\nvt 0.2874 0.3253\r\nvt 0.2995 0.326\r\nvt 0.313 0.3267\r\nvt 0.3279 0.3273\r\nvt 0.3114 0.3153\r\nvt 0.3263 0.3159\r\nvt 0.3444 0.3279\r\nvt 0.3627 0.3284\r\nvt 0.3444 0.3275\r\nvt 0.3627 0.328\r\nvt 0.3428 0.3164\r\nvt 0.3611 0.3169\r\nvt 0.3829 0.3288\r\nvt 0.4048 0.3292\r\nvt 0.3829 0.3284\r\nvt 0.4048 0.3288\r\nvt 0.3814 0.3173\r\nvt 0.4033 0.3177\r\nvt 0.428 0.3297\r\nvt 0.4523 0.3302\r\nvt 0.428 0.3293\r\nvt 0.4523 0.3298\r\nvt 0.4266 0.3181\r\nvt 0.451 0.3186\r\nvt 0.4774 0.3308\r\nvt 0.5026 0.3315\r\nvt 0.4774 0.3304\r\nvt 0.5026 0.3311\r\nvt 0.4761 0.3192\r\nvt 0.5014 0.3199\r\nvt 0.5273 0.3322\r\nvt 0.5509 0.3328\r\nvt 0.5273 0.3318\r\nvt 0.5509 0.3324\r\nvt 0.5262 0.3206\r\nvt 0.5498 0.3212\r\nvt 0.5727 0.3333\r\nvt 0.5931 0.3337\r\nvt 0.5727 0.3329\r\nvt 0.5931 0.3333\r\nvt 0.5717 0.3216\r\nvt 0.5922 0.322\r\nvt 0.6128 0.334\r\nvt 0.6321 0.3344\r\nvt 0.6128 0.3336\r\nvt 0.6322 0.334\r\nvt 0.612 0.3223\r\nvt 0.6314 0.3227\r\nvt 0.6516 0.3349\r\nvt 0.6715 0.3354\r\nvt 0.6517 0.3345\r\nvt 0.6715 0.335\r\nvt 0.651 0.3231\r\nvt 0.6709 0.3236\r\nvt 0.6919 0.3359\r\nvt 0.7131 0.3364\r\nvt 0.6919 0.3355\r\nvt 0.7131 0.336\r\nvt 0.6914 0.3241\r\nvt 0.7127 0.3246\r\nvt 0.7354 0.3368\r\nvt 0.7578 0.3372\r\nvt 0.7354 0.3364\r\nvt 0.7578 0.3368\r\nvt 0.735 0.325\r\nvt 0.7575 0.3254\r\nvt 0.7796 0.3376\r\nvt 0.7999 0.338\r\nvt 0.7796 0.3372\r\nvt 0.7999 0.3376\r\nvt 0.7794 0.3258\r\nvt 0.7998 0.3262\r\nvt 0.818 0.3384\r\nvt 0.8343 0.3387\r\nvt 0.818 0.338\r\nvt 0.8343 0.3383\r\nvt 0.818 0.3266\r\nvt 0.8344 0.3269\r\nvt 0.8494 0.3391\r\nvt 0.864 0.3394\r\nvt 0.8494 0.3387\r\nvt 0.8641 0.339\r\nvt 0.8496 0.3273\r\nvt 0.8643 0.3276\r\nvt 0.8787 0.3396\r\nvt 0.8933 0.3398\r\nvt 0.8787 0.3392\r\nvt 0.8933 0.3394\r\nvt 0.879 0.3278\r\nvt 0.8938 0.3281\r\nvt 0.9081 0.34\r\nvt 0.9231 0.3401\r\nvt 0.9081 0.3396\r\nvt 0.9231 0.3397\r\nvt 0.9086 0.3282\r\nvt 0.9236 0.3283\r\nvt 0.9382 0.3401\r\nvt 0.9527 0.3401\r\nvt 0.9382 0.3397\r\nvt 0.9527 0.3397\r\nvt 0.9388 0.3283\r\nvt 0.9534 0.3283\r\nvt 0.9658 0.3401\r\nvt 0.977 0.3402\r\nvt 0.9658 0.3397\r\nvt 0.9665 0.3284\r\nvt 0.9778 0.3285\r\nvt 0.9861 0.3403\r\nvt 0.9926 0.3402\r\nvt 0.9869 0.3286\r\nvt 0.9936 0.3288\r\nvt 0.9963 0.34\r\nvt 0.9986 0.3397\r\nvt 0.9963 0.3394\r\nvt 0.9986 0.3389\r\nvt 0.9973 0.329\r\nvt 0.9996 0.3291\r\nvt 0.9995 0.3302\r\nvt 0.9972 0.33\r\nvt 0.619 0.0202\r\nvt 0.6123 0.0252\r\nvt 0.6115 0.0245\r\nvt 0.6182 0.0194\r\nvt 0.5934 0.0228\r\nvt 0.5829 0.0305\r\nvt 0.6033 0.0317\r\nvt 0.5928 0.0394\r\nvt 0.5921 0.0387\r\nvt 0.6026 0.031\r\nvt 0.5714 0.039\r\nvt 0.5595 0.0478\r\nvt 0.5716 0.0393\r\nvt 0.5597 0.0481\r\nvt 0.5813 0.0479\r\nvt 0.5693 0.0568\r\nvt 0.5686 0.056\r\nvt 0.5805 0.0472\r\nvt 0.5478 0.0566\r\nvt 0.5363 0.0654\r\nvt 0.548 0.0569\r\nvt 0.5365 0.0657\r\nvt 0.5576 0.0656\r\nvt 0.5461 0.0744\r\nvt 0.5454 0.0736\r\nvt 0.5569 0.0648\r\nvt 0.5249 0.0741\r\nvt 0.5136 0.0828\r\nvt 0.5252 0.0744\r\nvt 0.5139 0.0831\r\nvt 0.5347 0.0832\r\nvt 0.5234 0.0919\r\nvt 0.5226 0.0912\r\nvt 0.5339 0.0824\r\nvt 0.5024 0.0916\r\nvt 0.4905 0.1009\r\nvt 0.5026 0.0919\r\nvt 0.4907 0.1013\r\nvt 0.5121 0.1008\r\nvt 0.5001 0.1101\r\nvt 0.4994 0.1094\r\nvt 0.5113 0.1\r\nvt 0.4772 0.1114\r\nvt 0.4774 0.1117\r\nvt 0.4868 0.1206\r\nvt 0.486 0.1199\r\nvt 0.444 0.1384\r\nvt 0.4623 0.1241\r\nvt 0.4238 0.1541\r\nvt 0.4479 0.1419\r\nvt 0.4276 0.1576\r\nvt 0.4662 0.1275\r\nvt 0.467 0.1284\r\nvt 0.4486 0.1427\r\nvt 0.4284 0.1585\r\nvt 0.4777 0.112\r\nvt 0.491 0.1016\r\nvt 0.4817 0.1154\r\nvt 0.495 0.1049\r\nvt 0.4958 0.1058\r\nvt 0.4825 0.1162\r\nvt 0.5029 0.0922\r\nvt 0.5141 0.0835\r\nvt 0.507 0.0956\r\nvt 0.5182 0.0868\r\nvt 0.519 0.0876\r\nvt 0.5077 0.0964\r\nvt 0.5254 0.0747\r\nvt 0.5368 0.066\r\nvt 0.5295 0.078\r\nvt 0.5409 0.0693\r\nvt 0.5417 0.0701\r\nvt 0.5303 0.0788\r\nvt 0.5483 0.0572\r\nvt 0.56 0.0485\r\nvt 0.5524 0.0605\r\nvt 0.5641 0.0517\r\nvt 0.5649 0.0525\r\nvt 0.5532 0.0613\r\nvt 0.5719 0.0396\r\nvt 0.576 0.0429\r\nvt 0.5768 0.0437\r\nvt 0.6098 0.0117\r\nvt 0.9964 0.3385\r\nvt 0.9987 0.3372\r\nvt 0.9969 0.3323\r\nvt 0.9992 0.3323\r\nvt 0.9658 0.3393\r\nvt 0.9527 0.3393\r\nvt 0.9382 0.3393\r\nvt 0.9231 0.3393\r\nvt 0.9081 0.3392\r\nvt 0.8933 0.339\r\nvt 0.8787 0.3388\r\nvt 0.8641 0.3386\r\nvt 0.8495 0.3383\r\nvt 0.8343 0.3379\r\nvt 0.818 0.3376\r\nvt 0.7999 0.3372\r\nvt 0.7796 0.3368\r\nvt 0.7578 0.3364\r\nvt 0.7354 0.336\r\nvt 0.7131 0.3356\r\nvt 0.6919 0.3351\r\nvt 0.6715 0.3346\r\nvt 0.6517 0.3341\r\nvt 0.6322 0.3336\r\nvt 0.6128 0.3332\r\nvt 0.5931 0.3329\r\nvt 0.5727 0.3325\r\nvt 0.5509 0.332\r\nvt 0.5273 0.3314\r\nvt 0.5026 0.3307\r\nvt 0.4774 0.33\r\nvt 0.4523 0.3294\r\nvt 0.428 0.3289\r\nvt 0.4048 0.3284\r\nvt 0.3829 0.328\r\nvt 0.3627 0.3276\r\nvt 0.3444 0.3271\r\nvt 0.2705 0.3235\r\nvt 0.265 0.3231\r\nvt 0.018 0.4725\r\nvt 0.0283 0.464\r\nvt 0.0398 0.4547\r\nvt 0.0524 0.4445\r\nvt 0.0426 0.4591\r\nvt 0.0552 0.4489\r\nvt 0.0559 0.4498\r\nvt 0.0432 0.46\r\nvt 0.0664 0.4333\r\nvt 0.082 0.421\r\nvt 0.0693 0.4376\r\nvt 0.085 0.4253\r\nvt 0.0857 0.4262\r\nvt 0.07 0.4385\r\nvt 0.0993 0.4076\r\nvt 0.118 0.3931\r\nvt 0.1023 0.4118\r\nvt 0.1211 0.3973\r\nvt 0.1218 0.3982\r\nvt 0.103 0.4127\r\nvt 0.1377 0.3778\r\nvt 0.1581 0.362\r\nvt 0.1408 0.382\r\nvt 0.1613 0.3661\r\nvt 0.162 0.367\r\nvt 0.1415 0.3829\r\nvt 0.1788 0.3458\r\nvt 0.1995 0.3295\r\nvt 0.1821 0.3498\r\nvt 0.2028 0.3335\r\nvt 0.2035 0.3344\r\nvt 0.1828 0.3507\r\nvt 0.2197 0.3137\r\nvt 0.239 0.2986\r\nvt 0.2231 0.3176\r\nvt 0.2424 0.3025\r\nvt 0.2431 0.3034\r\nvt 0.2238 0.3185\r\nvt 0.2568 0.2847\r\nvt 0.2738 0.2715\r\nvt 0.2603 0.2885\r\nvt 0.2773 0.2753\r\nvt 0.278 0.2762\r\nvt 0.261 0.2894\r\nvt 0.2904 0.2586\r\nvt 0.3072 0.2455\r\nvt 0.294 0.2623\r\nvt 0.3108 0.2492\r\nvt 0.3115 0.2501\r\nvt 0.2947 0.2632\r\nvt 0.3246 0.2318\r\nvt 0.3428 0.2175\r\nvt 0.3283 0.2355\r\nvt 0.3465 0.2212\r\nvt 0.3472 0.222\r\nvt 0.329 0.2364\r\nvt 0.3617 0.2026\r\nvt 0.3817 0.1869\r\nvt 0.3655 0.2062\r\nvt 0.3855 0.1905\r\nvt 0.3862 0.1913\r\nvt 0.3662 0.207\r\nvt 0.4027 0.1705\r\nvt 0.4065 0.1741\r\nvt 0.4073 0.1749\r\nvt 0.4762 0.1404\r\nvt 0.4579 0.1547\r\nvt 0.4562 0.1519\r\nvt 0.4745 0.1376\r\nvt 0.4376 0.1705\r\nvt 0.4359 0.1677\r\nvt 0.4165 0.1869\r\nvt 0.4148 0.1842\r\nvt 0.3955 0.2033\r\nvt 0.3937 0.2006\r\nvt 0.3756 0.219\r\nvt 0.3738 0.2163\r\nvt 0.3566 0.234\r\nvt 0.3547 0.2313\r\nvt 0.3384 0.2483\r\nvt 0.3365 0.2457\r\nvt 0.3209 0.2621\r\nvt 0.319 0.2594\r\nvt 0.304 0.2752\r\nvt 0.3022 0.2725\r\nvt 0.2873 0.2882\r\nvt 0.2855 0.2855\r\nvt 0.2703 0.3014\r\nvt 0.2684 0.2987\r\nvt 0.2524 0.3153\r\nvt 0.2505 0.3127\r\nvt 0.2331 0.3305\r\nvt 0.2312 0.3278\r\nvt 0.2129 0.3464\r\nvt 0.2109 0.3437\r\nvt 0.1922 0.3627\r\nvt 0.1902 0.36\r\nvt 0.1713 0.3789\r\nvt 0.1693 0.3763\r\nvt 0.1508 0.3949\r\nvt 0.1489 0.3922\r\nvt 0.1311 0.4102\r\nvt 0.1291 0.4076\r\nvt 0.1124 0.4247\r\nvt 0.1104 0.4221\r\nvt 0.0951 0.4382\r\nvt 0.093 0.4356\r\nvt 0.0795 0.4504\r\nvt 0.0774 0.4479\r\nvt 0.0655 0.4616\r\nvt 0.0633 0.4591\r\nvt 0.0529 0.4717\r\nvt 0.0506 0.4694\r\nvt 0.0416 0.481\r\nvt 0.0393 0.4787\r\nvt 0.0314 0.4896\r\nvt 0.0291 0.4873\r\nvt 0.023 0.4964\r\nvt 0.021 0.4938\r\nvt 0.0162 0.4996\r\nvt 0.0157 0.4965\r\nvt 0.0113 0.4987\r\nvt 0.0129 0.4964\r\nvt 0.0084 0.4963\r\nvt 0.0112 0.4956\r\nvt 0.0071 0.4932\r\nvt 0.0101 0.4942\r\nvt 0.0069 0.4916\r\nvt 0.0098 0.4933\r\nvt 0.0098 0.4922\r\nvt 0.011 0.4899\r\nvt 0.0152 0.4862\r\nvt 0.023 0.4798\r\nvt 0.0333 0.4714\r\nvt 0.0447 0.4621\r\nvt 0.0574 0.4519\r\nvt 0.0715 0.4406\r\nvt 0.0872 0.4283\r\nvt 0.1046 0.4147\r\nvt 0.1234 0.4002\r\nvt 0.1431 0.3849\r\nvt 0.1636 0.369\r\nvt 0.1844 0.3527\r\nvt 0.2052 0.3364\r\nvt 0.2254 0.3205\r\nvt 0.2447 0.3053\r\nvt 0.2627 0.2913\r\nvt 0.2797 0.2781\r\nvt 0.2964 0.2651\r\nvt 0.3132 0.252\r\nvt 0.3307 0.2383\r\nvt 0.3489 0.2239\r\nvt 0.368 0.2089\r\nvt 0.388 0.1932\r\nvt 0.409 0.1768\r\nvt 0.4302 0.1603\r\nvt 0.4504 0.1445\r\nvt 0.4688 0.1302\r\nvt 0.4843 0.1181\r\nvt 0.4976 0.1076\r\nvt 0.5095 0.0982\r\nvt 0.5208 0.0894\r\nvt 0.5321 0.0806\r\nvt 0.5435 0.0719\r\nvt 0.5551 0.0631\r\nvt 0.5668 0.0543\r\nvt 0.5787 0.0454\r\nvt 0.5902 0.037\r\nvt 0.6007 0.0293\r\nvt 0.6097 0.0227\r\nvt 0.6164 0.0177\r\nvt 0.6211 0.014\r\nvt 0.6245 0.0118\r\nvt 0.6269 0.0113\r\nvt 0.6276 0.0086\r\nvt 0.6277 0.0116\r\nvt 0.6295 0.0094\r\nvt 0.6285 0.0122\r\nvt 0.6321 0.0122\r\nvt 0.6298 0.0138\r\nvt 0.6331 0.0163\r\nvt 0.6303 0.0162\r\nvt 0.6321 0.0206\r\nvt 0.6296 0.0189\r\nvt 0.6286 0.0248\r\nvt 0.6268 0.0222\r\nvt 0.6234 0.0288\r\nvt 0.622 0.0259\r\nvt 0.6165 0.0335\r\nvt 0.6151 0.0305\r\nvt 0.6075 0.0398\r\nvt 0.6062 0.0369\r\nvt 0.5971 0.0475\r\nvt 0.5957 0.0445\r\nvt 0.5856 0.0559\r\nvt 0.5842 0.0529\r\nvt 0.5738 0.0647\r\nvt 0.5724 0.0618\r\nvt 0.5622 0.0734\r\nvt 0.5607 0.0705\r\nvt 0.5508 0.0822\r\nvt 0.5492 0.0793\r\nvt 0.5395 0.0909\r\nvt 0.5379 0.088\r\nvt 0.5282 0.0996\r\nvt 0.5266 0.0968\r\nvt 0.517 0.1084\r\nvt 0.5153 0.1056\r\nvt 0.5051 0.1178\r\nvt 0.5034 0.115\r\nvt 0.4917 0.1282\r\nvt 0.49 0.1254\r\nvt 0.0196 0.5081\r\nvt 0.0188 0.5092\r\nvt 0.0146 0.5064\r\nvt 0.0152 0.5054\r\nvt 0.0178 0.5104\r\nvt 0.0138 0.5074\r\nvt 0.0121 0.5062\r\nvt 0.013 0.5053\r\nvt 0.0138 0.5048\r\nvt 0.1962 0.3652\r\nvt 0.1961 0.3638\r\nvt 0.2015 0.3635\r\nvt 0.2016 0.3649\r\nvt 0.196 0.362\r\nvt 0.2014 0.3617\r\nvt 0.2083 0.3613\r\nvt 0.2084 0.3631\r\nvt 0.2085 0.3645\r\nvt 0.9083 0.3489\r\nvt 0.9084 0.3472\r\nvt 0.9176 0.3471\r\nvt 0.9174 0.3488\r\nvt 0.9086 0.3451\r\nvt 0.9179 0.3449\r\nvt 0.9245 0.3455\r\nvt 0.9242 0.3474\r\nvt 0.9239 0.349\r\nvt 0.6197 0.9974\r\nvt 0.6182 0.9985\r\nvt 0.6159 0.9967\r\nvt 0.6173 0.9955\r\nvt 0.6164 1\r\nvt 0.614 0.9982\r\nvt 0.6108 0.9958\r\nvt 0.6125 0.9943\r\nvt 0.6139 0.9932\r\nvt 0.6289 0.9838\r\nvt 0.6283 0.985\r\nvt 0.6271 0.9836\r\nvt 0.6279 0.9824\r\nvt 0.6275 0.9862\r\nvt 0.6263 0.9849\r\nvt 0.6238 0.9827\r\nvt 0.6247 0.9812\r\nvt 0.6255 0.9797\r\nvt 0.5132 0.9178\r\nvt 0.5123 0.9189\r\nvt 0.501 0.9101\r\nvt 0.5019 0.909\r\nvt 0.5112 0.9203\r\nvt 0.4999 0.9115\r\nvt 0.488 0.9022\r\nvt 0.4891 0.9008\r\nvt 0.49 0.8996\r\nvt 0.5254 0.9052\r\nvt 0.5247 0.9063\r\nvt 0.5134 0.8975\r\nvt 0.5141 0.8964\r\nvt 0.5238 0.9075\r\nvt 0.5125 0.8986\r\nvt 0.5006 0.8893\r\nvt 0.5015 0.8881\r\nvt 0.5022 0.887\r\nvt 0.4629 0.8753\r\nvt 0.4642 0.8742\r\nvt 0.4797 0.8863\r\nvt 0.4783 0.8874\r\nvt 0.4656 0.8731\r\nvt 0.4811 0.8852\r\nvt 0.4944 0.8957\r\nvt 0.493 0.8968\r\nvt 0.4916 0.8979\r\nvt 0.9239 0.3515\r\nvt 0.9239 0.353\r\nvt 0.9173 0.3528\r\nvt 0.9173 0.3512\r\nvt 0.9241 0.3551\r\nvt 0.9175 0.3551\r\nvt 0.9083 0.3552\r\nvt 0.9082 0.3529\r\nvt 0.9082 0.3513\r\nvt 0.2592 0.3642\r\nvt 0.2592 0.3658\r\nvt 0.2443 0.3665\r\nvt 0.2443 0.3649\r\nvt 0.259 0.3681\r\nvt 0.2442 0.3687\r\nvt 0.2307 0.3694\r\nvt 0.2309 0.3672\r\nvt 0.2308 0.3656\r\nvt 0.2187 0.3679\r\nvt 0.2186 0.3663\r\nvt 0.2186 0.3701\r\nvt 0.2086 0.3707\r\nvt 0.2087 0.3685\r\nvt 0.2086 0.3669\r\nvt 0.2018 0.3689\r\nvt 0.2017 0.3673\r\nvt 0.2016 0.3711\r\nvt 0.1962 0.3714\r\nvt 0.1963 0.3692\r\nvt 0.1962 0.3676\r\nvt 0.0038 0.5138\r\nvt 0.0047 0.5122\r\nvt 0.0072 0.5147\r\nvt 0.0063 0.5162\r\nvt 0.0058 0.5104\r\nvt 0.0081 0.5132\r\nvt 0.0124 0.5173\r\nvt 0.0115 0.5187\r\nvt 0.0105 0.5201\r\nvt 0.1185 0.6063\r\nvt 0.1195 0.6049\r\nvt 0.1393 0.6201\r\nvt 0.1382 0.6215\r\nvt 0.1206 0.6034\r\nvt 0.1403 0.6187\r\nvt 0.1607 0.6346\r\nvt 0.1597 0.636\r\nvt 0.1586 0.6374\r\nvt 0.0358 0.525\r\nvt 0.0354 0.5256\r\nvt 0.0251 0.5172\r\nvt 0.0256 0.5166\r\nvt 0.0347 0.5265\r\nvt 0.0245 0.5181\r\nvt 0.0167 0.5119\r\nvt 0.0173 0.511\r\nvt 0.0133 0.5079\r\nvt 0.0127 0.5086\r\nvt 0.0114 0.5075\r\nvt 0.0117 0.5067\r\nvt 0.2085 0.3653\r\nvt 0.2016 0.3657\r\nvt 0.2086 0.3657\r\nvt 0.2016 0.3661\r\nvt 0.1962 0.3664\r\nvt 0.1962 0.366\r\nvt 0.1946 0.3781\r\nvt 0.1945 0.3773\r\nvt 0.2 0.377\r\nvt 0.2 0.3778\r\nvt 0.1948 0.3764\r\nvt 0.2002 0.3761\r\nvt 0.2072 0.3757\r\nvt 0.2069 0.3766\r\nvt 0.207 0.3774\r\nvt 0.2169 0.376\r\nvt 0.217 0.3768\r\nvt 0.2172 0.3751\r\nvt 0.2293 0.3744\r\nvt 0.2291 0.3753\r\nvt 0.2291 0.3761\r\nvt 0.6145 0.9926\r\nvt 0.618 0.9949\r\nvt 0.6147 0.9923\r\nvt 0.6182 0.9946\r\nvt 0.6207 0.9964\r\nvt 0.6204 0.9968\r\nvt 0.6271 0.9867\r\nvt 0.626 0.9855\r\nvt 0.6265 0.9872\r\nvt 0.6255 0.9863\r\nvt 0.6227 0.9842\r\nvt 0.6234 0.9834\r\nvt 0.6019 0.9844\r\nvt 0.6023 0.9838\r\nvt 0.6093 0.9889\r\nvt 0.6087 0.9895\r\nvt 0.6026 0.9835\r\nvt 0.6095 0.9886\r\nvt 0.6035 0.9826\r\nvt 0.6028 0.9831\r\nvt 0.5939 0.9765\r\nvt 0.5945 0.976\r\nvt 0.5936 0.9769\r\nvt 0.5831 0.9692\r\nvt 0.5834 0.9689\r\nvt 0.584 0.9683\r\nvt 0.5869 0.9662\r\nvt 0.5875 0.9656\r\nvt 0.5981 0.9733\r\nvt 0.5974 0.9739\r\nvt 0.5883 0.9648\r\nvt 0.5988 0.9725\r\nvt 0.6078 0.979\r\nvt 0.607 0.9799\r\nvt 0.6063 0.9805\r\nvt 0.6138 0.985\r\nvt 0.6132 0.9856\r\nvt 0.6146 0.9842\r\nvt 0.6196 0.9879\r\nvt 0.619 0.9888\r\nvt 0.6184 0.9893\r\nvt 0.6217 0.9955\r\nvt 0.621 0.9961\r\nvt 0.6185 0.9943\r\nvt 0.6191 0.9938\r\nvt 0.615 0.992\r\nvt 0.6156 0.9914\r\nvt 0.6228 0.9911\r\nvt 0.6222 0.9917\r\nvt 0.6234 0.9902\r\nvt 0.6269 0.9918\r\nvt 0.6265 0.9922\r\nvt 0.6254 0.993\r\nvt 0.9082 0.3505\r\nvt 0.9173 0.3504\r\nvt 0.9082 0.3501\r\nvt 0.9173 0.35\r\nvt 0.9238 0.3501\r\nvt 0.9238 0.3507\r\nvt 0.9244 0.3578\r\nvt 0.9177 0.3579\r\nvt 0.9247 0.3602\r\nvt 0.918 0.3603\r\nvt 0.9089 0.3605\r\nvt 0.9086 0.3581\r\nvt 0.8973 0.3582\r\nvt 0.8971 0.3553\r\nvt 0.8976 0.3606\r\nvt 0.8845 0.3607\r\nvt 0.8842 0.3582\r\nvt 0.884 0.3554\r\nvt 0.8697 0.3582\r\nvt 0.8695 0.3554\r\nvt 0.8699 0.3607\r\nvt 0.8547 0.3607\r\nvt 0.8545 0.3582\r\nvt 0.8543 0.3554\r\nvt 0.8396 0.3583\r\nvt 0.8394 0.3555\r\nvt 0.8398 0.3607\r\nvt 0.8249 0.3609\r\nvt 0.8248 0.3585\r\nvt 0.8246 0.3556\r\nvt 0.8101 0.3587\r\nvt 0.81 0.3558\r\nvt 0.8102 0.3611\r\nvt 0.7955 0.3614\r\nvt 0.7954 0.3589\r\nvt 0.7953 0.3561\r\nvt 0.7808 0.3592\r\nvt 0.7807 0.3564\r\nvt 0.7808 0.3617\r\nvt 0.7656 0.362\r\nvt 0.7656 0.3596\r\nvt 0.7656 0.3567\r\nvt 0.7492 0.3599\r\nvt 0.7492 0.3571\r\nvt 0.7492 0.3624\r\nvt 0.731 0.3628\r\nvt 0.7311 0.3603\r\nvt 0.7312 0.3575\r\nvt 0.7107 0.3607\r\nvt 0.7108 0.3579\r\nvt 0.7106 0.3632\r\nvt 0.6887 0.3636\r\nvt 0.6889 0.3611\r\nvt 0.689 0.3583\r\nvt 0.6664 0.3615\r\nvt 0.6666 0.3587\r\nvt 0.6662 0.3639\r\nvt 0.6439 0.3643\r\nvt 0.6441 0.3619\r\nvt 0.6443 0.3591\r\nvt 0.6229 0.3624\r\nvt 0.6231 0.3596\r\nvt 0.6227 0.3648\r\nvt 0.6022 0.3653\r\nvt 0.6025 0.3629\r\nvt 0.6027 0.3601\r\nvt 0.5826 0.3634\r\nvt 0.5828 0.3606\r\nvt 0.5823 0.3658\r\nvt 0.5627 0.3663\r\nvt 0.563 0.3639\r\nvt 0.5633 0.361\r\nvt 0.5436 0.3642\r\nvt 0.544 0.3614\r\nvt 0.5433 0.3666\r\nvt 0.5235 0.367\r\nvt 0.5239 0.3646\r\nvt 0.5243 0.3618\r\nvt 0.5034 0.365\r\nvt 0.5038 0.3621\r\nvt 0.503 0.3673\r\nvt 0.4812 0.3678\r\nvt 0.4816 0.3654\r\nvt 0.482 0.3626\r\nvt 0.458 0.366\r\nvt 0.4585 0.3632\r\nvt 0.4575 0.3684\r\nvt 0.4328 0.3691\r\nvt 0.4333 0.3667\r\nvt 0.4337 0.3639\r\nvt 0.408 0.3674\r\nvt 0.4085 0.3646\r\nvt 0.4075 0.3698\r\nvt 0.3823 0.3704\r\nvt 0.3829 0.368\r\nvt 0.3834 0.3653\r\nvt 0.3585 0.3685\r\nvt 0.3591 0.3658\r\nvt 0.358 0.3709\r\nvt 0.3347 0.3713\r\nvt 0.3353 0.369\r\nvt 0.3359 0.3662\r\nvt 0.3134 0.3694\r\nvt 0.314 0.3666\r\nvt 0.3127 0.3717\r\nvt 0.2925 0.3721\r\nvt 0.2931 0.3698\r\nvt 0.2938 0.367\r\nvt 0.2749 0.3703\r\nvt 0.2755 0.3675\r\nvt 0.2742 0.3726\r\nvt 0.2577 0.3731\r\nvt 0.2584 0.3708\r\nvt 0.2308 0.3647\r\nvt 0.2442 0.3641\r\nvt 0.2307 0.3643\r\nvt 0.2442 0.3637\r\nvt 0.2591 0.363\r\nvt 0.2591 0.3634\r\nvt 0.2435 0.3715\r\nvt 0.2428 0.3737\r\nvt 0.23 0.3721\r\nvt 0.2086 0.3661\r\nvt 0.2186 0.3654\r\nvt 0.2186 0.365\r\nvt 0.2179 0.3728\r\nvt 0.2079 0.3734\r\nvt 0.2009 0.3738\r\nvt 0.1955 0.3742\r\nvt 0.0101 0.5208\r\nvt 0.0058 0.5169\r\nvt 0.0098 0.5211\r\nvt 0.0055 0.5172\r\nvt 0.003 0.5149\r\nvt 0.0033 0.5145\r\nvt 0.0064 0.5094\r\nvt 0.0087 0.5126\r\nvt 0.0067 0.509\r\nvt 0.0094 0.5118\r\nvt 0.0136 0.5158\r\nvt 0.0129 0.5166\r\nvt 0.0209 0.5232\r\nvt 0.0204 0.5239\r\nvt 0.0216 0.5223\r\nvt 0.0318 0.5307\r\nvt 0.0312 0.5316\r\nvt 0.0307 0.5323\r\nvt 0.4228 0.8471\r\nvt 0.443 0.8628\r\nvt 0.4421 0.864\r\nvt 0.4219 0.8482\r\nvt 0.4613 0.8771\r\nvt 0.4604 0.8782\r\nvt 0.4593 0.8796\r\nvt 0.441 0.8654\r\nvt 0.4208 0.8497\r\nvt 0.4347 0.8343\r\nvt 0.455 0.8501\r\nvt 0.4543 0.8511\r\nvt 0.434 0.8353\r\nvt 0.4733 0.8644\r\nvt 0.4726 0.8655\r\nvt 0.4717 0.8666\r\nvt 0.4534 0.8523\r\nvt 0.4331 0.8365\r\nvt 0.3807 0.8143\r\nvt 0.4017 0.8307\r\nvt 0.4008 0.8318\r\nvt 0.3798 0.8155\r\nvt 0.3997 0.8332\r\nvt 0.3787 0.8169\r\nvt 0.3925 0.8014\r\nvt 0.4136 0.8178\r\nvt 0.4128 0.8189\r\nvt 0.3917 0.8024\r\nvt 0.4119 0.82\r\nvt 0.3909 0.8035\r\nvt 0.3418 0.7837\r\nvt 0.3608 0.7987\r\nvt 0.3599 0.7998\r\nvt 0.3409 0.7848\r\nvt 0.3588 0.8012\r\nvt 0.3398 0.7863\r\nvt 0.3534 0.7706\r\nvt 0.3725 0.7856\r\nvt 0.3717 0.7867\r\nvt 0.3527 0.7716\r\nvt 0.3708 0.7878\r\nvt 0.3518 0.7728\r\nvt 0.3062 0.7557\r\nvt 0.3237 0.7694\r\nvt 0.3228 0.7705\r\nvt 0.3054 0.7569\r\nvt 0.3217 0.772\r\nvt 0.3043 0.7583\r\nvt 0.3177 0.7425\r\nvt 0.3352 0.7562\r\nvt 0.3344 0.7573\r\nvt 0.317 0.7435\r\nvt 0.3335 0.7584\r\nvt 0.3161 0.7447\r\nvt 0.2728 0.7298\r\nvt 0.2895 0.7427\r\nvt 0.2886 0.7438\r\nvt 0.272 0.7309\r\nvt 0.2875 0.7452\r\nvt 0.2709 0.7323\r\nvt 0.2842 0.7164\r\nvt 0.3009 0.7294\r\nvt 0.3001 0.7304\r\nvt 0.2834 0.7175\r\nvt 0.2992 0.7316\r\nvt 0.2825 0.7186\r\nvt 0.238 0.7027\r\nvt 0.2559 0.7166\r\nvt 0.255 0.7177\r\nvt 0.2371 0.7038\r\nvt 0.2539 0.7191\r\nvt 0.236 0.7052\r\nvt 0.2492 0.6892\r\nvt 0.2671 0.7032\r\nvt 0.2664 0.7042\r\nvt 0.2484 0.6902\r\nvt 0.2655 0.7053\r\nvt 0.2475 0.6913\r\nvt 0.1986 0.6717\r\nvt 0.2187 0.6876\r\nvt 0.2179 0.6887\r\nvt 0.1977 0.6728\r\nvt 0.2168 0.6901\r\nvt 0.1966 0.6742\r\nvt 0.2096 0.6581\r\nvt 0.2298 0.674\r\nvt 0.2291 0.675\r\nvt 0.2088 0.6591\r\nvt 0.2282 0.6762\r\nvt 0.2079 0.6602\r\nvt 0.1571 0.6392\r\nvt 0.1779 0.6555\r\nvt 0.177 0.6566\r\nvt 0.1563 0.6404\r\nvt 0.1759 0.658\r\nvt 0.1552 0.6418\r\nvt 0.168 0.6255\r\nvt 0.1888 0.6418\r\nvt 0.188 0.6428\r\nvt 0.1672 0.6265\r\nvt 0.1871 0.6439\r\nvt 0.1663 0.6276\r\nvt 0.1171 0.6082\r\nvt 0.1368 0.6234\r\nvt 0.1359 0.6245\r\nvt 0.1162 0.6093\r\nvt 0.1348 0.626\r\nvt 0.1151 0.6107\r\nvt 0.1277 0.5943\r\nvt 0.1475 0.6096\r\nvt 0.1467 0.6106\r\nvt 0.1269 0.5953\r\nvt 0.1459 0.6117\r\nvt 0.1261 0.5964\r\nvt 0.0811 0.5802\r\nvt 0.0984 0.5937\r\nvt 0.0975 0.5948\r\nvt 0.0802 0.5813\r\nvt 0.0964 0.5962\r\nvt 0.0791 0.5827\r\nvt 0.0915 0.5662\r\nvt 0.1089 0.5797\r\nvt 0.1082 0.5807\r\nvt 0.0907 0.5672\r\nvt 0.1073 0.5819\r\nvt 0.0899 0.5683\r\nvt 0.0514 0.5567\r\nvt 0.0655 0.5679\r\nvt 0.0646 0.569\r\nvt 0.0505 0.5578\r\nvt 0.0635 0.5704\r\nvt 0.0494 0.5592\r\nvt 0.0617 0.5426\r\nvt 0.0758 0.5538\r\nvt 0.075 0.5548\r\nvt 0.0609 0.5435\r\nvt 0.0741 0.5559\r\nvt 0.06 0.5446\r\nvt 0.0274 0.5372\r\nvt 0.0388 0.5465\r\nvt 0.0379 0.5476\r\nvt 0.0264 0.5383\r\nvt 0.0367 0.549\r\nvt 0.0253 0.5397\r\nvt 0.0376 0.5229\r\nvt 0.049 0.5323\r\nvt 0.0482 0.5332\r\nvt 0.0368 0.5239\r\nvt 0.0473 0.5344\r\nvt 0.009 0.522\r\nvt 0.017 0.5287\r\nvt 0.0161 0.5299\r\nvt 0.008 0.5232\r\nvt 0.0149 0.5313\r\nvt 0.0068 0.5246\r\nvt 0.0275 0.5144\r\nvt 0.0266 0.5154\r\nvt 0.0021 0.516\r\nvt 0.0047 0.5181\r\nvt 0.0037 0.5193\r\nvt 0.0012 0.5171\r\nvt 0.0025 0.5207\r\nvt 0 0.5185\r\nvt 0.2072 0.3806\r\nvt 0.2004 0.3811\r\nvt 0.2002 0.3793\r\nvt 0.2071 0.3788\r\nvt 0.195 0.3816\r\nvt 0.1948 0.3797\r\nvt 0.2307 0.3632\r\nvt 0.2185 0.3639\r\nvt 0.2184 0.3624\r\nvt 0.2306 0.3618\r\nvt 0.2183 0.3607\r\nvt 0.2305 0.36\r\nvt 0.2293 0.3793\r\nvt 0.2172 0.38\r\nvt 0.2171 0.3782\r\nvt 0.2292 0.3775\r\nvt 0.2591 0.3619\r\nvt 0.2442 0.3625\r\nvt 0.2441 0.3611\r\nvt 0.259 0.3604\r\nvt 0.244 0.3593\r\nvt 0.2589 0.3586\r\nvt 0.2576 0.378\r\nvt 0.2427 0.3787\r\nvt 0.2427 0.3769\r\nvt 0.2575 0.3763\r\nvt 0.2426 0.3754\r\nvt 0.2575 0.3748\r\nvt 0.2938 0.3608\r\nvt 0.2756 0.3613\r\nvt 0.2755 0.3598\r\nvt 0.2938 0.3593\r\nvt 0.2755 0.3581\r\nvt 0.2938 0.3576\r\nvt 0.2924 0.377\r\nvt 0.2741 0.3775\r\nvt 0.274 0.3757\r\nvt 0.2923 0.3752\r\nvt 0.274 0.3743\r\nvt 0.2923 0.3738\r\nvt 0.3359 0.36\r\nvt 0.3141 0.3604\r\nvt 0.314 0.3589\r\nvt 0.3359 0.3585\r\nvt 0.314 0.3571\r\nvt 0.3359 0.3567\r\nvt 0.3346 0.3762\r\nvt 0.3126 0.3766\r\nvt 0.3126 0.3748\r\nvt 0.3345 0.3745\r\nvt 0.3126 0.3734\r\nvt 0.3345 0.373\r\nvt 0.3835 0.359\r\nvt 0.3592 0.3595\r\nvt 0.3592 0.3581\r\nvt 0.3834 0.3576\r\nvt 0.3591 0.3563\r\nvt 0.3834 0.3558\r\nvt 0.3823 0.3753\r\nvt 0.3579 0.3758\r\nvt 0.3578 0.374\r\nvt 0.3822 0.3736\r\nvt 0.3578 0.3726\r\nvt 0.3822 0.3721\r\nvt 0.4338 0.3577\r\nvt 0.4086 0.3584\r\nvt 0.4085 0.3569\r\nvt 0.4337 0.3563\r\nvt 0.4085 0.3552\r\nvt 0.4337 0.3545\r\nvt 0.4327 0.3741\r\nvt 0.4074 0.3747\r\nvt 0.4074 0.3729\r\nvt 0.4327 0.3723\r\nvt 0.4073 0.3715\r\nvt 0.4326 0.3708\r\nvt 0.482 0.3564\r\nvt 0.4585 0.357\r\nvt 0.4584 0.3556\r\nvt 0.482 0.3549\r\nvt 0.4584 0.3538\r\nvt 0.482 0.3531\r\nvt 0.4811 0.3728\r\nvt 0.4575 0.3734\r\nvt 0.4574 0.3716\r\nvt 0.4811 0.371\r\nvt 0.4574 0.3702\r\nvt 0.481 0.3695\r\nvt 0.5243 0.3555\r\nvt 0.5038 0.3559\r\nvt 0.5038 0.3544\r\nvt 0.5243 0.3541\r\nvt 0.5037 0.3527\r\nvt 0.5242 0.3523\r\nvt 0.5235 0.3719\r\nvt 0.5029 0.3723\r\nvt 0.5029 0.3705\r\nvt 0.5234 0.3702\r\nvt 0.5029 0.3691\r\nvt 0.5234 0.3687\r\nvt 0.5633 0.3548\r\nvt 0.544 0.3552\r\nvt 0.5439 0.3537\r\nvt 0.5633 0.3533\r\nvt 0.5439 0.3519\r\nvt 0.5632 0.3516\r\nvt 0.5627 0.3713\r\nvt 0.5432 0.3716\r\nvt 0.5432 0.3698\r\nvt 0.5626 0.3695\r\nvt 0.5432 0.3684\r\nvt 0.5626 0.368\r\nvt 0.6027 0.3538\r\nvt 0.5828 0.3543\r\nvt 0.5828 0.3529\r\nvt 0.6026 0.3524\r\nvt 0.5827 0.3511\r\nvt 0.6026 0.3506\r\nvt 0.6022 0.3703\r\nvt 0.5823 0.3708\r\nvt 0.5822 0.369\r\nvt 0.6022 0.3685\r\nvt 0.5822 0.3676\r\nvt 0.6021 0.3671\r\nvt 0.6443 0.3528\r\nvt 0.6231 0.3533\r\nvt 0.6231 0.3519\r\nvt 0.6443 0.3514\r\nvt 0.623 0.3501\r\nvt 0.6442 0.3496\r\nvt 0.644 0.3693\r\nvt 0.6227 0.3698\r\nvt 0.6226 0.368\r\nvt 0.6439 0.3675\r\nvt 0.6226 0.3666\r\nvt 0.6439 0.3661\r\nvt 0.689 0.352\r\nvt 0.6665 0.3524\r\nvt 0.6665 0.351\r\nvt 0.6889 0.3506\r\nvt 0.6665 0.3492\r\nvt 0.6889 0.3488\r\nvt 0.6888 0.3686\r\nvt 0.6663 0.3689\r\nvt 0.6662 0.3671\r\nvt 0.6887 0.3668\r\nvt 0.6662 0.3657\r\nvt 0.6887 0.3653\r\nvt 0.7311 0.3512\r\nvt 0.7108 0.3516\r\nvt 0.7107 0.3502\r\nvt 0.731 0.3498\r\nvt 0.7107 0.3484\r\nvt 0.731 0.348\r\nvt 0.7311 0.3678\r\nvt 0.7106 0.3682\r\nvt 0.7106 0.3664\r\nvt 0.731 0.366\r\nvt 0.7106 0.3649\r\nvt 0.731 0.3645\r\nvt 0.7654 0.3505\r\nvt 0.7491 0.3508\r\nvt 0.7491 0.3494\r\nvt 0.7654 0.349\r\nvt 0.749 0.3476\r\nvt 0.7653 0.3472\r\nvt 0.7656 0.367\r\nvt 0.7492 0.3674\r\nvt 0.7492 0.3656\r\nvt 0.7656 0.3652\r\nvt 0.7492 0.3641\r\nvt 0.7656 0.3638\r\nvt 0.7952 0.3498\r\nvt 0.7806 0.3501\r\nvt 0.7806 0.3487\r\nvt 0.7952 0.3484\r\nvt 0.7805 0.3469\r\nvt 0.7952 0.3466\r\nvt 0.7956 0.3664\r\nvt 0.7809 0.3667\r\nvt 0.7809 0.3649\r\nvt 0.7956 0.3646\r\nvt 0.7808 0.3634\r\nvt 0.7955 0.3631\r\nvt 0.8245 0.3494\r\nvt 0.8098 0.3496\r\nvt 0.8098 0.3481\r\nvt 0.8245 0.3479\r\nvt 0.8098 0.3463\r\nvt 0.8245 0.3461\r\nvt 0.825 0.3659\r\nvt 0.8103 0.3661\r\nvt 0.8103 0.3643\r\nvt 0.825 0.3641\r\nvt 0.8102 0.3629\r\nvt 0.825 0.3627\r\nvt 0.8542 0.3491\r\nvt 0.8393 0.3492\r\nvt 0.8393 0.3478\r\nvt 0.8542 0.3477\r\nvt 0.8393 0.346\r\nvt 0.8542 0.3459\r\nvt 0.8548 0.3656\r\nvt 0.8399 0.3657\r\nvt 0.8398 0.3639\r\nvt 0.8548 0.3639\r\nvt 0.8398 0.3625\r\nvt 0.8548 0.3624\r\nvt 0.8839 0.3491\r\nvt 0.8694 0.3491\r\nvt 0.8694 0.3477\r\nvt 0.8839 0.3476\r\nvt 0.8694 0.3459\r\nvt 0.8839 0.3458\r\nvt 0.8846 0.3657\r\nvt 0.87 0.3656\r\nvt 0.87 0.3639\r\nvt 0.8846 0.3639\r\nvt 0.87 0.3624\r\nvt 0.8846 0.3624\r\nvt 0.897 0.349\r\nvt 0.897 0.3474\r\nvt 0.8971 0.3454\r\nvt 0.9094 0.3661\r\nvt 0.8979 0.366\r\nvt 0.8978 0.364\r\nvt 0.9092 0.364\r\nvt 0.8977 0.3624\r\nvt 0.909 0.3623\r\nvt 0.9254 0.3653\r\nvt 0.9187 0.366\r\nvt 0.9184 0.3639\r\nvt 0.9251 0.3634\r\nvt 0.9182 0.3622\r\nvt 0.9249 0.3619\r\nvt 0.9297 0.3498\r\nvt 0.9275 0.3494\r\nvt 0.9277 0.3484\r\nvt 0.9298 0.3494\r\nvt 0.9279 0.3471\r\nvt 0.9298 0.349\r\nvt 0.9287 0.3625\r\nvt 0.9289 0.3638\r\nvt 0.9286 0.3615\r\nvt 0.9308 0.3612\r\nvt 0.9308 0.3616\r\nvt 0.9309 0.362\r\nvt 0.6076 0.9907\r\nvt 0.6009 0.9855\r\nvt 0.6061 0.9921\r\nvt 0.5997 0.9869\r\nvt 0.6142 0.9716\r\nvt 0.6209 0.9763\r\nvt 0.6203 0.9777\r\nvt 0.6136 0.9729\r\nvt 0.6194 0.9791\r\nvt 0.6127 0.9741\r\nvt 0.5825 0.9701\r\nvt 0.5929 0.9778\r\nvt 0.5921 0.9789\r\nvt 0.5816 0.9713\r\nvt 0.591 0.9804\r\nvt 0.5805 0.9727\r\nvt 0.5947 0.9577\r\nvt 0.6052 0.9653\r\nvt 0.6046 0.9665\r\nvt 0.5941 0.9588\r\nvt 0.6038 0.9676\r\nvt 0.5932 0.96\r\nvt 0.559 0.9528\r\nvt 0.5709 0.9616\r\nvt 0.5701 0.9628\r\nvt 0.5582 0.954\r\nvt 0.569 0.9642\r\nvt 0.5571 0.9554\r\nvt 0.5713 0.9403\r\nvt 0.5832 0.9492\r\nvt 0.5826 0.9503\r\nvt 0.5707 0.9415\r\nvt 0.5817 0.9515\r\nvt 0.5698 0.9426\r\nvt 0.5358 0.9352\r\nvt 0.5473 0.944\r\nvt 0.5465 0.9452\r\nvt 0.5349 0.9364\r\nvt 0.5454 0.9466\r\nvt 0.5339 0.9378\r\nvt 0.5481 0.9228\r\nvt 0.5596 0.9315\r\nvt 0.559 0.9327\r\nvt 0.5474 0.9239\r\nvt 0.5581 0.9338\r\nvt 0.5465 0.925\r\nvt 0.5244 0.9265\r\nvt 0.5236 0.9277\r\nvt 0.5225 0.9291\r\nvt 0.5367 0.914\r\nvt 0.536 0.9151\r\nvt 0.5351 0.9162\r\nvt 0.4767 0.8892\r\nvt 0.4758 0.8903\r\nvt 0.4747 0.8917\r\nvt 0.4888 0.8765\r\nvt 0.4881 0.8776\r\nvt 0.4872 0.8788\r\nvt 0.4446 0.861\r\nvt 0.4459 0.8599\r\nvt 0.4244 0.8453\r\nvt 0.4257 0.8442\r\nvt 0.427 0.843\r\nvt 0.4472 0.8588\r\nvt 0.5147 0.916\r\nvt 0.5035 0.9072\r\nvt 0.5049 0.9061\r\nvt 0.5162 0.9149\r\nvt 0.5063 0.905\r\nvt 0.5176 0.9138\r\nvt 0.5374 0.9335\r\nvt 0.526 0.9247\r\nvt 0.5274 0.9237\r\nvt 0.5388 0.9324\r\nvt 0.5289 0.9226\r\nvt 0.5403 0.9313\r\nvt 0.5606 0.951\r\nvt 0.5489 0.9422\r\nvt 0.5503 0.9411\r\nvt 0.562 0.9499\r\nvt 0.5518 0.9401\r\nvt 0.5635 0.9489\r\nvt 0.5725 0.9598\r\nvt 0.5739 0.9588\r\nvt 0.5854 0.9672\r\nvt 0.5754 0.9577\r\nvt 0.596 0.9749\r\nvt 0.6049 0.9815\r\nvt 0.6104 0.9877\r\nvt 0.6118 0.9867\r\nvt 0.617 0.9904\r\nvt 0.6207 0.9927\r\nvt 0.6235 0.9944\r\nvt 0.9276 0.3527\r\nvt 0.9277 0.3537\r\nvt 0.93 0.3543\r\nvt 0.93 0.3548\r\nvt 0.9301 0.3555\r\nvt 0.9278 0.3552\r\nvt 0.8839 0.3515\r\nvt 0.897 0.3514\r\nvt 0.897 0.353\r\nvt 0.8839 0.3531\r\nvt 0.8542 0.3515\r\nvt 0.8694 0.3515\r\nvt 0.8694 0.3531\r\nvt 0.8542 0.3531\r\nvt 0.8245 0.3517\r\nvt 0.8393 0.3516\r\nvt 0.8393 0.3532\r\nvt 0.8246 0.3533\r\nvt 0.7953 0.3522\r\nvt 0.8099 0.3519\r\nvt 0.8099 0.3535\r\nvt 0.7953 0.3538\r\nvt 0.7655 0.3528\r\nvt 0.7807 0.3525\r\nvt 0.7807 0.3541\r\nvt 0.7655 0.3544\r\nvt 0.7311 0.3536\r\nvt 0.7492 0.3532\r\nvt 0.7492 0.3548\r\nvt 0.7312 0.3552\r\nvt 0.689 0.3544\r\nvt 0.7108 0.354\r\nvt 0.7108 0.3556\r\nvt 0.689 0.356\r\nvt 0.6443 0.3552\r\nvt 0.6666 0.3548\r\nvt 0.6666 0.3564\r\nvt 0.6444 0.3568\r\nvt 0.6027 0.3562\r\nvt 0.6231 0.3557\r\nvt 0.6232 0.3573\r\nvt 0.6028 0.3578\r\nvt 0.5634 0.3572\r\nvt 0.5829 0.3567\r\nvt 0.5829 0.3583\r\nvt 0.5634 0.3588\r\nvt 0.5243 0.3579\r\nvt 0.544 0.3575\r\nvt 0.544 0.3591\r\nvt 0.5244 0.3595\r\nvt 0.4821 0.3587\r\nvt 0.5039 0.3583\r\nvt 0.5039 0.3599\r\nvt 0.4821 0.3603\r\nvt 0.4338 0.3601\r\nvt 0.4585 0.3594\r\nvt 0.4586 0.361\r\nvt 0.4339 0.3617\r\nvt 0.3835 0.3614\r\nvt 0.4086 0.3608\r\nvt 0.4087 0.3624\r\nvt 0.3836 0.363\r\nvt 0.336 0.3623\r\nvt 0.3592 0.3619\r\nvt 0.3593 0.3635\r\nvt 0.336 0.3639\r\nvt 0.2939 0.3632\r\nvt 0.3141 0.3627\r\nvt 0.3141 0.3643\r\nvt 0.2939 0.3648\r\nvt 0.2756 0.3637\r\nvt 0.2757 0.3653\r\nvt 0.0288 0.5353\r\nvt 0.0185 0.5268\r\nvt 0.0194 0.5254\r\nvt 0.0297 0.5338\r\nvt 0.0529 0.5548\r\nvt 0.0402 0.5446\r\nvt 0.0411 0.5431\r\nvt 0.0538 0.5533\r\nvt 0.0421 0.5416\r\nvt 0.0548 0.5519\r\nvt 0.0825 0.5783\r\nvt 0.0669 0.566\r\nvt 0.0679 0.5646\r\nvt 0.0835 0.5769\r\nvt 0.0688 0.5631\r\nvt 0.0845 0.5754\r\nvt 0.0998 0.5918\r\nvt 0.1008 0.5904\r\nvt 0.1018 0.5889\r\nvt 0.2 0.6698\r\nvt 0.1793 0.6536\r\nvt 0.1804 0.6522\r\nvt 0.2011 0.6685\r\nvt 0.1815 0.6509\r\nvt 0.2023 0.6672\r\nvt 0.2395 0.7008\r\nvt 0.2202 0.6857\r\nvt 0.2214 0.6844\r\nvt 0.2406 0.6995\r\nvt 0.2225 0.6831\r\nvt 0.2418 0.6982\r\nvt 0.2743 0.7279\r\nvt 0.2574 0.7147\r\nvt 0.2585 0.7134\r\nvt 0.2755 0.7267\r\nvt 0.2597 0.7121\r\nvt 0.2767 0.7254\r\nvt 0.3078 0.7539\r\nvt 0.291 0.7408\r\nvt 0.2922 0.7396\r\nvt 0.309 0.7527\r\nvt 0.2934 0.7383\r\nvt 0.3102 0.7514\r\nvt 0.3433 0.7819\r\nvt 0.3252 0.7676\r\nvt 0.3264 0.7664\r\nvt 0.3446 0.7807\r\nvt 0.3277 0.7651\r\nvt 0.3459 0.7795\r\nvt 0.3823 0.8125\r\nvt 0.3623 0.7969\r\nvt 0.3636 0.7957\r\nvt 0.3835 0.8113\r\nvt 0.3649 0.7945\r\nvt 0.3849 0.8101\r\nvt 0.4033 0.8289\r\nvt 0.4046 0.8277\r\nvt 0.4059 0.8265\r\nvt 0.4233 0.8465\r\nvt 0.4435 0.8622\r\nvt 0.4235 0.8462\r\nvt 0.4437 0.8619\r\nvt 0.462 0.8762\r\nvt 0.4618 0.8765\r\nvt 0.4713 0.8672\r\nvt 0.4529 0.8529\r\nvt 0.4705 0.868\r\nvt 0.4522 0.8536\r\nvt 0.4319 0.8379\r\nvt 0.4326 0.8371\r\nvt 0.3812 0.8137\r\nvt 0.4022 0.8301\r\nvt 0.3814 0.8134\r\nvt 0.4024 0.8298\r\nvt 0.4115 0.8206\r\nvt 0.4108 0.8214\r\nvt 0.3897 0.8049\r\nvt 0.3904 0.8041\r\nvt 0.3423 0.7831\r\nvt 0.3613 0.7981\r\nvt 0.3425 0.7828\r\nvt 0.3615 0.7978\r\nvt 0.3704 0.7884\r\nvt 0.3697 0.7892\r\nvt 0.3506 0.7742\r\nvt 0.3513 0.7734\r\nvt 0.3067 0.7551\r\nvt 0.3241 0.7688\r\nvt 0.307 0.7548\r\nvt 0.3244 0.7685\r\nvt 0.3331 0.759\r\nvt 0.3324 0.7598\r\nvt 0.3149 0.7461\r\nvt 0.3156 0.7453\r\nvt 0.2733 0.7292\r\nvt 0.2899 0.7421\r\nvt 0.2736 0.7288\r\nvt 0.2902 0.7417\r\nvt 0.2988 0.7322\r\nvt 0.2981 0.733\r\nvt 0.2814 0.72\r\nvt 0.2821 0.7192\r\nvt 0.2385 0.7021\r\nvt 0.2563 0.716\r\nvt 0.2387 0.7017\r\nvt 0.2566 0.7157\r\nvt 0.265 0.706\r\nvt 0.2643 0.7068\r\nvt 0.2464 0.6928\r\nvt 0.2471 0.6919\r\nvt 0.199 0.6711\r\nvt 0.2192 0.687\r\nvt 0.1993 0.6708\r\nvt 0.2195 0.6866\r\nvt 0.2277 0.6768\r\nvt 0.227 0.6776\r\nvt 0.2068 0.6617\r\nvt 0.2074 0.6608\r\nvt 0.1576 0.6386\r\nvt 0.1783 0.6549\r\nvt 0.1578 0.6383\r\nvt 0.1786 0.6545\r\nvt 0.1867 0.6445\r\nvt 0.186 0.6454\r\nvt 0.1652 0.6291\r\nvt 0.1658 0.6282\r\nvt 0.1175 0.6075\r\nvt 0.1372 0.6228\r\nvt 0.1178 0.6072\r\nvt 0.1375 0.6225\r\nvt 0.1454 0.6123\r\nvt 0.1447 0.6132\r\nvt 0.1249 0.5978\r\nvt 0.1256 0.597\r\nvt 0.0815 0.5796\r\nvt 0.0989 0.5931\r\nvt 0.0818 0.5793\r\nvt 0.0991 0.5928\r\nvt 0.1068 0.5825\r\nvt 0.1062 0.5833\r\nvt 0.0887 0.5697\r\nvt 0.0894 0.5689\r\nvt 0.0519 0.5561\r\nvt 0.0659 0.5673\r\nvt 0.0521 0.5558\r\nvt 0.0662 0.567\r\nvt 0.0737 0.5565\r\nvt 0.073 0.5574\r\nvt 0.0589 0.5461\r\nvt 0.0595 0.5452\r\nvt 0.0278 0.5366\r\nvt 0.0393 0.5459\r\nvt 0.0281 0.5363\r\nvt 0.0395 0.5456\r\nvt 0.0468 0.535\r\nvt 0.0462 0.5358\r\nvt 0.0095 0.5214\r\nvt 0.0175 0.5281\r\nvt 0.0178 0.5278\r\nvt 0.0027 0.5153\r\nvt 0.0052 0.5175\r\nvt 0.2307 0.364\r\nvt 0.2185 0.3646\r\nvt 0.2591 0.3626\r\nvt 0.2442 0.3633\r\nvt 0.2425 0.3747\r\nvt 0.2575 0.3741\r\nvt 0.2938 0.3615\r\nvt 0.2756 0.3621\r\nvt 0.2939 0.3619\r\nvt 0.2756 0.3624\r\nvt 0.274 0.3735\r\nvt 0.2923 0.373\r\nvt 0.336 0.3607\r\nvt 0.3141 0.3611\r\nvt 0.336 0.3611\r\nvt 0.3141 0.3615\r\nvt 0.3125 0.3726\r\nvt 0.3345 0.3723\r\nvt 0.3835 0.3598\r\nvt 0.3592 0.3603\r\nvt 0.3835 0.3602\r\nvt 0.3592 0.3607\r\nvt 0.3578 0.3718\r\nvt 0.3822 0.3714\r\nvt 0.4338 0.3585\r\nvt 0.4086 0.3591\r\nvt 0.4338 0.3589\r\nvt 0.4086 0.3595\r\nvt 0.4073 0.3707\r\nvt 0.4326 0.3701\r\nvt 0.4821 0.3571\r\nvt 0.4585 0.3578\r\nvt 0.4821 0.3575\r\nvt 0.4585 0.3582\r\nvt 0.4574 0.3694\r\nvt 0.481 0.3688\r\nvt 0.5243 0.3563\r\nvt 0.5038 0.3566\r\nvt 0.5243 0.3567\r\nvt 0.5038 0.357\r\nvt 0.5029 0.3683\r\nvt 0.5234 0.368\r\nvt 0.5633 0.3556\r\nvt 0.544 0.3559\r\nvt 0.5633 0.356\r\nvt 0.544 0.3563\r\nvt 0.5431 0.3676\r\nvt 0.5626 0.3673\r\nvt 0.6027 0.3546\r\nvt 0.5828 0.3551\r\nvt 0.6027 0.355\r\nvt 0.5828 0.3555\r\nvt 0.5822 0.3668\r\nvt 0.6021 0.3663\r\nvt 0.6443 0.3536\r\nvt 0.6231 0.3541\r\nvt 0.6443 0.354\r\nvt 0.6231 0.3545\r\nvt 0.6226 0.3658\r\nvt 0.6439 0.3653\r\nvt 0.689 0.3528\r\nvt 0.6665 0.3532\r\nvt 0.689 0.3532\r\nvt 0.6665 0.3536\r\nvt 0.6662 0.3649\r\nvt 0.6887 0.3646\r\nvt 0.7311 0.352\r\nvt 0.7108 0.3524\r\nvt 0.7311 0.3524\r\nvt 0.7108 0.3528\r\nvt 0.7106 0.3642\r\nvt 0.731 0.3638\r\nvt 0.7654 0.3512\r\nvt 0.7491 0.3516\r\nvt 0.7655 0.3516\r\nvt 0.7491 0.352\r\nvt 0.7491 0.3634\r\nvt 0.7656 0.363\r\nvt 0.7952 0.3506\r\nvt 0.7806 0.3509\r\nvt 0.7952 0.351\r\nvt 0.7806 0.3513\r\nvt 0.7808 0.3627\r\nvt 0.7955 0.3624\r\nvt 0.8245 0.3501\r\nvt 0.8099 0.3503\r\nvt 0.8245 0.3505\r\nvt 0.8099 0.3507\r\nvt 0.8102 0.3621\r\nvt 0.825 0.3619\r\nvt 0.8542 0.3499\r\nvt 0.8393 0.35\r\nvt 0.8542 0.3503\r\nvt 0.8393 0.3504\r\nvt 0.8398 0.3617\r\nvt 0.8548 0.3617\r\nvt 0.8839 0.3499\r\nvt 0.8694 0.3499\r\nvt 0.8839 0.3503\r\nvt 0.8694 0.3503\r\nvt 0.87 0.3617\r\nvt 0.8846 0.3617\r\nvt 0.9082 0.3497\r\nvt 0.897 0.3498\r\nvt 0.897 0.3502\r\nvt 0.8977 0.3616\r\nvt 0.909 0.3615\r\nvt 0.9238 0.3497\r\nvt 0.9173 0.3496\r\nvt 0.9181 0.3613\r\nvt 0.9248 0.3611\r\nvt 0.9297 0.3503\r\nvt 0.9275 0.35\r\nvt 0.9298 0.3511\r\nvt 0.9275 0.3505\r\nvt 0.9285 0.361\r\nvt 0.9284 0.36\r\nvt 0.9307 0.3598\r\nvt 0.9308 0.3608\r\nvt 0.619 0.9798\r\nvt 0.6182 0.9806\r\nvt 0.6115 0.9755\r\nvt 0.6123 0.9748\r\nvt 0.5829 0.9695\r\nvt 0.5934 0.9772\r\nvt 0.6033 0.9683\r\nvt 0.6026 0.969\r\nvt 0.5921 0.9613\r\nvt 0.5928 0.9606\r\nvt 0.5595 0.9522\r\nvt 0.5714 0.961\r\nvt 0.5597 0.9519\r\nvt 0.5716 0.9607\r\nvt 0.5813 0.9521\r\nvt 0.5805 0.9528\r\nvt 0.5686 0.944\r\nvt 0.5693 0.9432\r\nvt 0.5363 0.9346\r\nvt 0.5478 0.9434\r\nvt 0.5365 0.9343\r\nvt 0.548 0.9431\r\nvt 0.5576 0.9344\r\nvt 0.5569 0.9352\r\nvt 0.5454 0.9264\r\nvt 0.5461 0.9256\r\nvt 0.5136 0.9172\r\nvt 0.5249 0.9259\r\nvt 0.5139 0.9169\r\nvt 0.5251 0.9256\r\nvt 0.5347 0.9168\r\nvt 0.5339 0.9176\r\nvt 0.5226 0.9088\r\nvt 0.5234 0.9081\r\nvt 0.4905 0.8991\r\nvt 0.5024 0.9084\r\nvt 0.4907 0.8987\r\nvt 0.5026 0.9081\r\nvt 0.5121 0.8992\r\nvt 0.5113 0.9\r\nvt 0.4994 0.8906\r\nvt 0.5001 0.8899\r\nvt 0.4772 0.8886\r\nvt 0.4774 0.8883\r\nvt 0.4868 0.8794\r\nvt 0.486 0.8801\r\nvt 0.4623 0.8759\r\nvt 0.444 0.8616\r\nvt 0.4238 0.8459\r\nvt 0.4276 0.8424\r\nvt 0.4479 0.8581\r\nvt 0.4284 0.8415\r\nvt 0.4486 0.8573\r\nvt 0.467 0.8716\r\nvt 0.4662 0.8725\r\nvt 0.491 0.8984\r\nvt 0.4777 0.888\r\nvt 0.4817 0.8846\r\nvt 0.4825 0.8838\r\nvt 0.4958 0.8942\r\nvt 0.495 0.8951\r\nvt 0.5141 0.9165\r\nvt 0.5029 0.9078\r\nvt 0.507 0.9044\r\nvt 0.5077 0.9036\r\nvt 0.519 0.9124\r\nvt 0.5182 0.9132\r\nvt 0.5368 0.934\r\nvt 0.5254 0.9253\r\nvt 0.5295 0.922\r\nvt 0.5303 0.9212\r\nvt 0.5417 0.9299\r\nvt 0.5409 0.9307\r\nvt 0.56 0.9515\r\nvt 0.5483 0.9428\r\nvt 0.5524 0.9395\r\nvt 0.5532 0.9387\r\nvt 0.5649 0.9475\r\nvt 0.5641 0.9483\r\nvt 0.5719 0.9604\r\nvt 0.576 0.9571\r\nvt 0.5768 0.9563\r\nvt 0.6098 0.9883\r\nvt 0.9276 0.3515\r\nvt 0.9299 0.3528\r\nvt 0.9304 0.3576\r\nvt 0.9281 0.3577\r\nvt 0.8839 0.3507\r\nvt 0.897 0.3506\r\nvt 0.8542 0.3507\r\nvt 0.8694 0.3507\r\nvt 0.8245 0.3509\r\nvt 0.8393 0.3508\r\nvt 0.7952 0.3514\r\nvt 0.8099 0.3511\r\nvt 0.7655 0.352\r\nvt 0.7806 0.3517\r\nvt 0.7311 0.3528\r\nvt 0.7492 0.3524\r\nvt 0.689 0.3536\r\nvt 0.7108 0.3532\r\nvt 0.6443 0.3544\r\nvt 0.6666 0.354\r\nvt 0.6027 0.3554\r\nvt 0.6231 0.3548\r\nvt 0.5633 0.3563\r\nvt 0.5829 0.3559\r\nvt 0.5243 0.3571\r\nvt 0.544 0.3567\r\nvt 0.4821 0.3579\r\nvt 0.5039 0.3574\r\nvt 0.4338 0.3592\r\nvt 0.4585 0.3586\r\nvt 0.3835 0.3606\r\nvt 0.4086 0.3599\r\nvt 0.336 0.3615\r\nvt 0.3592 0.3611\r\nvt 0.2939 0.3623\r\nvt 0.3141 0.3619\r\nvt 0.2756 0.3628\r\nvt 0.1962 0.3668\r\nvt 0.2016 0.3665\r\nvt 0.0283 0.536\r\nvt 0.018 0.5275\r\nvt 0.0524 0.5555\r\nvt 0.0398 0.5453\r\nvt 0.0426 0.5409\r\nvt 0.0432 0.54\r\nvt 0.0559 0.5502\r\nvt 0.0552 0.5511\r\nvt 0.082 0.579\r\nvt 0.0664 0.5667\r\nvt 0.0693 0.5624\r\nvt 0.07 0.5615\r\nvt 0.0857 0.5738\r\nvt 0.085 0.5747\r\nvt 0.118 0.6069\r\nvt 0.0993 0.5924\r\nvt 0.1023 0.5882\r\nvt 0.103 0.5873\r\nvt 0.1218 0.6018\r\nvt 0.1211 0.6027\r\nvt 0.1581 0.638\r\nvt 0.1377 0.6222\r\nvt 0.1408 0.618\r\nvt 0.1415 0.6171\r\nvt 0.162 0.633\r\nvt 0.1613 0.6339\r\nvt 0.1995 0.6705\r\nvt 0.1788 0.6542\r\nvt 0.1821 0.6502\r\nvt 0.1828 0.6493\r\nvt 0.2035 0.6656\r\nvt 0.2028 0.6665\r\nvt 0.239 0.7014\r\nvt 0.2197 0.6863\r\nvt 0.2231 0.6824\r\nvt 0.2238 0.6815\r\nvt 0.2431 0.6966\r\nvt 0.2424 0.6975\r\nvt 0.2738 0.7285\r\nvt 0.2568 0.7153\r\nvt 0.2603 0.7115\r\nvt 0.261 0.7106\r\nvt 0.278 0.7238\r\nvt 0.2773 0.7247\r\nvt 0.3072 0.7545\r\nvt 0.2904 0.7414\r\nvt 0.294 0.7377\r\nvt 0.2947 0.7368\r\nvt 0.3115 0.7499\r\nvt 0.3108 0.7508\r\nvt 0.3428 0.7825\r\nvt 0.3246 0.7682\r\nvt 0.3283 0.7645\r\nvt 0.329 0.7636\r\nvt 0.3472 0.778\r\nvt 0.3465 0.7788\r\nvt 0.3817 0.8131\r\nvt 0.3617 0.7974\r\nvt 0.3655 0.7938\r\nvt 0.3662 0.793\r\nvt 0.3862 0.8087\r\nvt 0.3855 0.8095\r\nvt 0.4027 0.8295\r\nvt 0.4065 0.8259\r\nvt 0.4073 0.8251\r\nvt 0.4762 0.8596\r\nvt 0.4745 0.8624\r\nvt 0.4562 0.8481\r\nvt 0.4579 0.8453\r\nvt 0.4359 0.8323\r\nvt 0.4376 0.8295\r\nvt 0.4148 0.8158\r\nvt 0.4165 0.8131\r\nvt 0.3937 0.7994\r\nvt 0.3955 0.7967\r\nvt 0.3738 0.7837\r\nvt 0.3756 0.781\r\nvt 0.3547 0.7687\r\nvt 0.3566 0.766\r\nvt 0.3365 0.7543\r\nvt 0.3384 0.7517\r\nvt 0.319 0.7406\r\nvt 0.3209 0.7379\r\nvt 0.3022 0.7275\r\nvt 0.304 0.7248\r\nvt 0.2855 0.7145\r\nvt 0.2873 0.7118\r\nvt 0.2684 0.7013\r\nvt 0.2703 0.6986\r\nvt 0.2505 0.6873\r\nvt 0.2524 0.6847\r\nvt 0.2312 0.6722\r\nvt 0.2331 0.6695\r\nvt 0.2109 0.6563\r\nvt 0.2129 0.6536\r\nvt 0.1902 0.64\r\nvt 0.1922 0.6373\r\nvt 0.1693 0.6237\r\nvt 0.1713 0.6211\r\nvt 0.1489 0.6078\r\nvt 0.1508 0.6051\r\nvt 0.1291 0.5924\r\nvt 0.1311 0.5898\r\nvt 0.1104 0.5779\r\nvt 0.1124 0.5753\r\nvt 0.093 0.5644\r\nvt 0.0951 0.5618\r\nvt 0.0774 0.5521\r\nvt 0.0795 0.5496\r\nvt 0.0633 0.5409\r\nvt 0.0655 0.5384\r\nvt 0.0506 0.5306\r\nvt 0.0529 0.5283\r\nvt 0.0393 0.5213\r\nvt 0.0416 0.519\r\nvt 0.0291 0.5127\r\nvt 0.0314 0.5104\r\nvt 0.021 0.5062\r\nvt 0.023 0.5036\r\nvt 0.0157 0.5035\r\nvt 0.0162 0.5004\r\nvt 0.0129 0.5036\r\nvt 0.0113 0.5013\r\nvt 0.0112 0.5044\r\nvt 0.0084 0.5037\r\nvt 0.0101 0.5058\r\nvt 0.0071 0.5068\r\nvt 0.0098 0.5067\r\nvt 0.0069 0.5084\r\nvt 0.0098 0.5078\r\nvt 0.011 0.5101\r\nvt 0.0152 0.5138\r\nvt 0.023 0.5202\r\nvt 0.0333 0.5286\r\nvt 0.0447 0.5379\r\nvt 0.0574 0.5481\r\nvt 0.0715 0.5594\r\nvt 0.0872 0.5717\r\nvt 0.1046 0.5853\r\nvt 0.1234 0.5998\r\nvt 0.1431 0.6151\r\nvt 0.1636 0.631\r\nvt 0.1844 0.6473\r\nvt 0.2052 0.6636\r\nvt 0.2254 0.6795\r\nvt 0.2447 0.6947\r\nvt 0.2627 0.7087\r\nvt 0.2797 0.7219\r\nvt 0.2964 0.7349\r\nvt 0.3132 0.748\r\nvt 0.3307 0.7617\r\nvt 0.3489 0.7761\r\nvt 0.368 0.7911\r\nvt 0.388 0.8068\r\nvt 0.409 0.8232\r\nvt 0.4302 0.8397\r\nvt 0.4504 0.8555\r\nvt 0.4688 0.8698\r\nvt 0.4843 0.8819\r\nvt 0.4976 0.8924\r\nvt 0.5095 0.9018\r\nvt 0.5208 0.9106\r\nvt 0.5321 0.9194\r\nvt 0.5435 0.9281\r\nvt 0.5551 0.9369\r\nvt 0.5668 0.9457\r\nvt 0.5787 0.9546\r\nvt 0.5902 0.963\r\nvt 0.6007 0.9707\r\nvt 0.6097 0.9773\r\nvt 0.6164 0.9823\r\nvt 0.6211 0.986\r\nvt 0.6245 0.9882\r\nvt 0.6269 0.9887\r\nvt 0.6277 0.9884\r\nvt 0.6276 0.9914\r\nvt 0.6285 0.9878\r\nvt 0.6295 0.9906\r\nvt 0.6298 0.9862\r\nvt 0.6321 0.9878\r\nvt 0.6303 0.9838\r\nvt 0.6331 0.9837\r\nvt 0.6296 0.9811\r\nvt 0.6321 0.9794\r\nvt 0.6268 0.9778\r\nvt 0.6286 0.9752\r\nvt 0.622 0.9741\r\nvt 0.6234 0.9712\r\nvt 0.6151 0.9695\r\nvt 0.6165 0.9665\r\nvt 0.6062 0.9631\r\nvt 0.6075 0.9602\r\nvt 0.5957 0.9555\r\nvt 0.5971 0.9525\r\nvt 0.5842 0.9471\r\nvt 0.5856 0.9441\r\nvt 0.5724 0.9382\r\nvt 0.5738 0.9353\r\nvt 0.5607 0.9295\r\nvt 0.5622 0.9266\r\nvt 0.5492 0.9207\r\nvt 0.5508 0.9178\r\nvt 0.5379 0.912\r\nvt 0.5395 0.9091\r\nvt 0.5266 0.9032\r\nvt 0.5282 0.9004\r\nvt 0.5153 0.8944\r\nvt 0.517 0.8916\r\nvt 0.5034 0.885\r\nvt 0.5051 0.8822\r\nvt 0.49 0.8746\r\nvt 0.4917 0.8718\r\no finishing_trim\r\nusemtl Material__38\r\nf 16009/16715/14524 16010/16716/14525 16011/16717/14526 16012/16718/14527\r\nf 16010/16716/14525 16013/16719/14528 16014/16720/14529 16011/16717/14526\r\nf 16015/16721/14530 16016/16722/14531 16011/16717/14526 16014/16720/14529\r\nf 16016/16722/14531 16017/16723/14532 16012/16718/14527 16011/16717/14526\r\nf 16018/16724/14533 16019/16725/14534 16020/16726/14535 16021/16727/14536\r\nf 16019/16725/14534 16022/16728/14537 16023/16729/14538 16020/16726/14535\r\nf 16024/16730/14539 16025/16731/14540 16020/16726/14535 16023/16729/14538\r\nf 16025/16731/14540 16026/16732/14541 16021/16727/14536 16020/16726/14535\r\nf 16027/16733/14542 16028/16734/14543 16029/16735/14544 16030/16736/14545\r\nf 16028/16734/14543 16031/16737/14546 16032/16738/14547 16029/16735/14544\r\nf 16033/16739/14548 16034/16740/14549 16029/16735/14544 16032/16738/14547\r\nf 16034/16740/14549 16035/16741/14550 16030/16736/14545 16029/16735/14544\r\nf 16036/16742/14551 16037/16743/14552 16038/16744/14553 16039/16745/14554\r\nf 16037/16743/14552 16040/16746/14555 16041/16747/14556 16038/16744/14553\r\nf 16042/16748/14557 16043/16749/14558 16038/16744/14553 16041/16747/14556\r\nf 16043/16749/14558 16044/16750/14559 16039/16745/14554 16038/16744/14553\r\nf 16045/16751/14560 16046/16752/14561 16047/16753/14562 16048/16754/14563\r\nf 16046/16752/14561 16049/16755/14564 16050/16756/14565 16047/16753/14562\r\nf 16051/16757/14566 16052/16758/14567 16047/16753/14562 16050/16756/14565\r\nf 16052/16758/14567 16053/16759/14568 16048/16754/14563 16047/16753/14562\r\nf 16054/16760/14569 16055/16761/14570 16056/16762/14571 16057/16763/14572\r\nf 16055/16761/14570 16058/16764/14573 16059/16765/14574 16056/16762/14571\r\nf 16060/16766/14575 16061/16767/14576 16056/16762/14571 16059/16765/14574\r\nf 16061/16767/14576 16062/16768/14577 16057/16763/14572 16056/16762/14571\r\nf 16063/16769/14578 16064/16770/14579 16065/16771/14580 16066/16772/14581\r\nf 16064/16770/14579 16067/16773/14582 16068/16774/14583 16065/16771/14580\r\nf 16069/16775/14584 16070/16776/14585 16065/16771/14580 16068/16774/14583\r\nf 16070/16776/14585 16071/16777/14586 16066/16772/14581 16065/16771/14580\r\nf 16072/16778/14587 16073/16779/14588 16074/16780/14589 16075/16781/14590\r\nf 16073/16779/14588 16076/16782/14591 16077/16783/14592 16074/16780/14589\r\nf 16078/16784/14593 16079/16785/14594 16074/16780/14589 16077/16783/14592\r\nf 16079/16785/14594 16080/16786/14595 16075/16781/14590 16074/16780/14589\r\nf 16081/16787/14596 16082/16788/14597 16083/16789/14598 16084/16790/14599\r\nf 16082/16788/14597 16085/16791/14600 16086/16792/14601 16083/16789/14598\r\nf 16087/16793/14602 16088/16794/14603 16083/16789/14598 16086/16792/14601\r\nf 16088/16794/14603 16089/16795/14604 16084/16790/14599 16083/16789/14598\r\nf 16090/16796/14605 16091/16797/14606 16092/16798/14607 16093/16799/14608\r\nf 16091/16797/14606 16094/16800/14609 16095/16801/14610 16092/16798/14607\r\nf 16096/16802/14611 16097/16803/14612 16092/16798/14607 16095/16801/14610\r\nf 16097/16803/14612 16098/16804/14613 16093/16799/14608 16092/16798/14607\r\nf 16094/16800/14609 16099/16805/14614 16100/16806/14615 16095/16801/14610\r\nf 16099/16805/14614 16101/16807/14616 16102/16808/14617 16100/16806/14615\r\nf 16103/16809/14618 16104/16810/14619 16100/16806/14615 16102/16808/14617\r\nf 16104/16810/14619 16096/16802/14611 16095/16801/14610 16100/16806/14615\r\nf 16101/16807/14616 16105/16811/14620 16106/16812/14621 16102/16808/14617\r\nf 16105/16811/14620 16107/16813/14622 16108/16814/14623 16106/16812/14621\r\nf 16109/16815/14624 16110/16816/14625 16106/16812/14621 16108/16814/14623\r\nf 16110/16816/14625 16103/16809/14618 16102/16808/14617 16106/16812/14621\r\nf 16111/16817/14626 16112/16818/14627 16113/16819/14628 16114/16820/14629\r\nf 16112/16818/14627 16115/16821/14630 16116/16822/14631 16113/16819/14628\r\nf 16117/16823/14632 16118/16824/14633 16113/16819/14628 16116/16822/14631\r\nf 16118/16824/14633 16119/16825/14634 16114/16820/14629 16113/16819/14628\r\nf 16120/16826/14635 16121/16827/14636 16122/16828/14637 16123/16829/14638\r\nf 16121/16827/14636 16124/16830/14639 16125/16831/14640 16122/16828/14637\r\nf 16126/16832/14641 16127/16833/14642 16122/16828/14637 16125/16831/14640\r\nf 16127/16833/14642 16128/16834/14643 16123/16829/14638 16122/16828/14637\r\nf 16129/16835/14644 16130/16836/14645 16131/16837/14646 16132/16838/14647\r\nf 16130/16836/14645 16017/16723/14532 16133/16839/14648 16131/16837/14646\r\nf 16134/16840/14649 16135/16841/14650 16131/16837/14646 16133/16839/14648\r\nf 16135/16841/14650 16136/16842/14651 16132/16838/14647 16131/16837/14646\r\nf 16017/16723/14532 16016/16722/14531 16137/16843/14652 16133/16839/14648\r\nf 16016/16722/14531 16015/16721/14530 16138/16844/14653 16137/16843/14652\r\nf 16139/16845/14654 16140/16846/14655 16137/16843/14652 16138/16844/14653\r\nf 16140/16846/14655 16134/16840/14649 16133/16839/14648 16137/16843/14652\r\nf 16022/16728/14537 16019/16725/14534 16141/16847/14656 16142/16848/14657\r\nf 16019/16725/14534 16018/16724/14533 16143/16849/14658 16141/16847/14656\r\nf 16144/16850/14659 16145/16851/14660 16141/16847/14656 16143/16849/14658\r\nf 16145/16851/14660 16146/16852/14661 16142/16848/14657 16141/16847/14656\r\nf 16147/16853/14662 16148/16854/14663 16149/16855/14664 16150/16856/14665\r\nf 16148/16854/14663 16151/16857/14666 16152/16858/14667 16149/16855/14664\r\nf 16153/16859/14668 16154/16860/14669 16149/16855/14664 16152/16858/14667\r\nf 16154/16860/14669 16155/16861/14670 16150/16856/14665 16149/16855/14664\r\nf 16151/16857/14666 16156/16862/14671 16157/16863/14672 16152/16858/14667\r\nf 16156/16862/14671 16158/16864/14673 16159/16865/14674 16157/16863/14672\r\nf 16160/16866/14675 16161/16867/14676 16157/16863/14672 16159/16865/14674\r\nf 16161/16867/14676 16153/16859/14668 16152/16858/14667 16157/16863/14672\r\nf 16040/16746/14555 16037/16743/14552 16162/16868/14677 16163/16869/14678\r\nf 16037/16743/14552 16036/16742/14551 16164/16870/14679 16162/16868/14677\r\nf 16165/16871/14680 16166/16872/14681 16162/16868/14677 16164/16870/14679\r\nf 16166/16872/14681 16167/16873/14682 16163/16869/14678 16162/16868/14677\r\nf 16053/16759/14568 16052/16758/14567 16168/16874/14683 16169/16875/14684\r\nf 16052/16758/14567 16051/16757/14566 16170/16876/14685 16168/16874/14683\r\nf 16171/16877/14686 16172/16878/14687 16168/16874/14683 16170/16876/14685\r\nf 16172/16878/14687 16173/16879/14688 16169/16875/14684 16168/16874/14683\r\nf 16174/16880/14689 16175/16881/14690 16176/16882/14691 16177/16883/14692\r\nf 16175/16881/14690 16040/16746/14555 16163/16869/14678 16176/16882/14691\r\nf 16167/16873/14682 16178/16884/14693 16176/16882/14691 16163/16869/14678\r\nf 16178/16884/14693 16179/16885/14694 16177/16883/14692 16176/16882/14691\r\nf 16180/16886/14695 16181/16887/14696 16182/16888/14697 16183/16889/14698\r\nf 16181/16887/14696 16184/16890/14699 16185/16891/14700 16182/16888/14697\r\nf 16186/16892/14701 16187/16893/14702 16182/16888/14697 16185/16891/14700\r\nf 16187/16893/14702 16179/16885/14694 16183/16889/14698 16182/16888/14697\r\nf 16188/16894/14703 16189/16895/14704 16190/16896/14705 16191/16897/14706\r\nf 16189/16895/14704 16192/16898/14707 16193/16899/14708 16190/16896/14705\r\nf 16194/16900/14709 16195/16901/14710 16190/16896/14705 16193/16899/14708\r\nf 16195/16901/14710 16196/16902/14711 16191/16897/14706 16190/16896/14705\r\nf 16192/16898/14707 16197/16903/14712 16198/16904/14713 16193/16899/14708\r\nf 16197/16903/14712 16199/16905/14714 16200/16906/14715 16198/16904/14713\r\nf 16201/16907/14716 16202/16908/14717 16198/16904/14713 16200/16906/14715\r\nf 16202/16908/14717 16194/16900/14709 16193/16899/14708 16198/16904/14713\r\nf 16203/16909/14718 16204/16910/14719 16205/16911/14720 16206/16912/14721\r\nf 16204/16910/14719 16207/16913/14722 16208/16914/14723 16205/16911/14720\r\nf 16167/16873/14682 16166/16872/14681 16205/16911/14720 16208/16914/14723\r\nf 16166/16872/14681 16165/16871/14680 16206/16912/14721 16205/16911/14720\r\nf 16199/16905/14714 16209/16915/14724 16210/16916/14725 16200/16906/14715\r\nf 16209/16915/14724 16211/16917/14726 16212/16918/14727 16210/16916/14725\r\nf 16213/16919/14728 16214/16920/14729 16210/16916/14725 16212/16918/14727\r\nf 16214/16920/14729 16201/16907/14716 16200/16906/14715 16210/16916/14725\r\nf 16085/16791/14600 16082/16788/14597 16215/16921/14730 16216/16922/14731\r\nf 16082/16788/14597 16081/16787/14596 16217/16923/14732 16215/16921/14730\r\nf 16218/16924/14733 16219/16925/14734 16215/16921/14730 16217/16923/14732\r\nf 16219/16925/14734 16220/16926/14735 16216/16922/14731 16215/16921/14730\r\nf 16089/16795/14604 16088/16794/14603 16221/16927/14736 16222/16928/14737\r\nf 16088/16794/14603 16087/16793/14602 16223/16929/14738 16221/16927/14736\r\nf 16224/16930/14739 16225/16931/14740 16221/16927/14736 16223/16929/14738\r\nf 16225/16931/14740 16226/16932/14741 16222/16928/14737 16221/16927/14736\r\nf 16087/16793/14602 16227/16933/14742 16228/16934/14743 16223/16929/14738\r\nf 16227/16933/14742 16229/16935/14744 16230/16936/14745 16228/16934/14743\r\nf 16231/16937/14746 16232/16938/14747 16228/16934/14743 16230/16936/14745\r\nf 16232/16938/14747 16224/16930/14739 16223/16929/14738 16228/16934/14743\r\nf 16229/16935/14744 16233/16939/14748 16234/16940/14749 16230/16936/14745\r\nf 16233/16939/14748 16235/16941/14750 16236/16942/14751 16234/16940/14749\r\nf 16237/16943/14752 16238/16944/14753 16234/16940/14749 16236/16942/14751\r\nf 16238/16944/14753 16231/16937/14746 16230/16936/14745 16234/16940/14749\r\nf 16235/16941/14750 16239/16945/14754 16240/16946/14755 16236/16942/14751\r\nf 16239/16945/14754 16241/16947/14756 16242/16948/14757 16240/16946/14755\r\nf 16243/16949/14758 16244/16950/14759 16240/16946/14755 16242/16948/14757\r\nf 16244/16950/14759 16237/16943/14752 16236/16942/14751 16240/16946/14755\r\nf 16241/16947/14756 16245/16951/14760 16246/16952/14761 16242/16948/14757\r\nf 16245/16951/14760 16247/16953/14762 16248/16954/14763 16246/16952/14761\r\nf 16249/16955/14764 16250/16956/14765 16246/16952/14761 16248/16954/14763\r\nf 16250/16956/14765 16243/16949/14758 16242/16948/14757 16246/16952/14761\r\nf 16247/16953/14762 16251/16957/14766 16252/16958/14767 16248/16954/14763\r\nf 16251/16957/14766 16253/16959/14768 16254/16960/14769 16252/16958/14767\r\nf 16255/16961/14770 16256/16962/14771 16252/16958/14767 16254/16960/14769\r\nf 16256/16962/14771 16249/16955/14764 16248/16954/14763 16252/16958/14767\r\nf 16253/16959/14768 16257/16963/14772 16258/16964/14773 16254/16960/14769\r\nf 16257/16963/14772 16259/16965/14774 16260/16966/14775 16258/16964/14773\r\nf 16261/16967/14776 16262/16968/14777 16258/16964/14773 16260/16966/14775\r\nf 16262/16968/14777 16255/16961/14770 16254/16960/14769 16258/16964/14773\r\nf 16259/16965/14774 16263/16969/14778 16264/16970/14779 16260/16966/14775\r\nf 16263/16969/14778 16265/16971/14780 16266/16972/14781 16264/16970/14779\r\nf 16267/16973/14782 16268/16974/14783 16264/16970/14779 16266/16972/14781\r\nf 16268/16974/14783 16261/16967/14776 16260/16966/14775 16264/16970/14779\r\nf 16265/16971/14780 16269/16975/14784 16270/16976/14785 16266/16972/14781\r\nf 16269/16975/14784 16271/16977/14786 16272/16978/14787 16270/16976/14785\r\nf 16273/16979/14788 16274/16980/14789 16270/16976/14785 16272/16978/14787\r\nf 16274/16980/14789 16267/16973/14782 16266/16972/14781 16270/16976/14785\r\nf 16271/16977/14786 16275/16981/14790 16276/16982/14791 16272/16978/14787\r\nf 16275/16981/14790 16277/16983/14792 16278/16984/14793 16276/16982/14791\r\nf 16279/16985/14794 16280/16986/14795 16276/16982/14791 16278/16984/14793\r\nf 16280/16986/14795 16273/16979/14788 16272/16978/14787 16276/16982/14791\r\nf 16277/16983/14792 16281/16987/14796 16282/16988/14797 16278/16984/14793\r\nf 16281/16987/14796 16283/16989/14798 16284/16990/14799 16282/16988/14797\r\nf 16285/16991/14800 16286/16992/14801 16282/16988/14797 16284/16990/14799\r\nf 16286/16992/14801 16279/16985/14794 16278/16984/14793 16282/16988/14797\r\nf 16283/16989/14798 16287/16993/14802 16288/16994/14803 16284/16990/14799\r\nf 16287/16993/14802 16289/16995/14804 16290/16996/14805 16288/16994/14803\r\nf 16291/16997/14806 16292/16998/14807 16288/16994/14803 16290/16996/14805\r\nf 16292/16998/14807 16285/16991/14800 16284/16990/14799 16288/16994/14803\r\nf 16289/16995/14804 16293/16999/14808 16294/17000/14809 16290/16996/14805\r\nf 16293/16999/14808 16295/17001/14810 16296/17002/14811 16294/17000/14809\r\nf 16297/17003/14812 16298/17004/14813 16294/17000/14809 16296/17002/14811\r\nf 16298/17004/14813 16291/16997/14806 16290/16996/14805 16294/17000/14809\r\nf 16295/17001/14810 16299/17005/14814 16300/17006/14815 16296/17002/14811\r\nf 16299/17005/14814 16301/17007/14816 16302/17008/14817 16300/17006/14815\r\nf 16303/17009/14818 16304/17010/14819 16300/17006/14815 16302/17008/14817\r\nf 16304/17010/14819 16297/17003/14812 16296/17002/14811 16300/17006/14815\r\nf 16301/17007/14816 16305/17011/14820 16306/17012/14821 16302/17008/14817\r\nf 16305/17011/14820 16307/17013/14822 16308/17014/14823 16306/17012/14821\r\nf 16309/17015/14824 16310/17016/14825 16306/17012/14821 16308/17014/14823\r\nf 16310/17016/14825 16303/17009/14818 16302/17008/14817 16306/17012/14821\r\nf 16307/17013/14822 16311/17017/14826 16312/17018/14827 16308/17014/14823\r\nf 16311/17017/14826 16313/17019/14828 16314/17020/14829 16312/17018/14827\r\nf 16315/17021/14830 16316/17022/14831 16312/17018/14827 16314/17020/14829\r\nf 16316/17022/14831 16309/17015/14824 16308/17014/14823 16312/17018/14827\r\nf 16313/17019/14828 16317/17023/14832 16318/17024/14833 16314/17020/14829\r\nf 16317/17023/14832 16319/17025/14834 16320/17026/14835 16318/17024/14833\r\nf 16321/17027/14836 16322/17028/14837 16318/17024/14833 16320/17026/14835\r\nf 16322/17028/14837 16315/17021/14830 16314/17020/14829 16318/17024/14833\r\nf 16319/17025/14834 16323/17029/14838 16324/17030/14839 16320/17026/14835\r\nf 16323/17029/14838 16098/16804/14613 16325/17031/14840 16324/17030/14839\r\nf 16326/17032/14841 16327/17033/14842 16324/17030/14839 16325/17031/14840\r\nf 16327/17033/14842 16321/17027/14836 16320/17026/14835 16324/17030/14839\r\nf 16094/16800/14609 16091/16797/14606 16328/17034/14843 16329/17035/14844\r\nf 16091/16797/14606 16090/16796/14605 16330/17036/14845 16328/17034/14843\r\nf 16331/17037/14846 16332/17038/14847 16328/17034/14843 16330/17036/14845\r\nf 16332/17038/14847 16333/17039/14848 16329/17035/14844 16328/17034/14843\r\nf 16098/16804/14613 16097/16803/14612 16334/17040/14849 16325/17031/14840\r\nf 16097/16803/14612 16096/16802/14611 16335/17041/14850 16334/17040/14849\r\nf 16160/16866/14675 16336/17042/14851 16334/17040/14849 16335/17041/14850\r\nf 16336/17042/14851 16326/17032/14841 16325/17031/14840 16334/17040/14849\r\nf 16101/16807/14616 16099/16805/14614 16337/17043/14852 16338/17044/14853\r\nf 16099/16805/14614 16094/16800/14609 16329/17035/14844 16337/17043/14852\r\nf 16333/17039/14848 16339/17045/14854 16337/17043/14852 16329/17035/14844\r\nf 16339/17045/14854 16146/16852/14661 16338/17044/14853 16337/17043/14852\r\nf 16096/16802/14611 16104/16810/14619 16340/17046/14855 16335/17041/14850\r\nf 16104/16810/14619 16103/16809/14618 16341/17047/14856 16340/17046/14855\r\nf 16153/16859/14668 16161/16867/14676 16340/17046/14855 16341/17047/14856\r\nf 16161/16867/14676 16160/16866/14675 16335/17041/14850 16340/17046/14855\r\nf 16103/16809/14618 16110/16816/14625 16342/17048/14857 16341/17047/14856\r\nf 16110/16816/14625 16109/16815/14624 16343/17049/14858 16342/17048/14857\r\nf 16155/16861/14670 16154/16860/14669 16342/17048/14857 16343/17049/14858\r\nf 16154/16860/14669 16153/16859/14668 16341/17047/14856 16342/17048/14857\r\nf 16115/16821/14630 16112/16818/14627 16344/17050/14859 16345/17051/14860\r\nf 16112/16818/14627 16111/16817/14626 16346/17052/14861 16344/17050/14859\r\nf 16347/17053/14862 16348/17054/14863 16344/17050/14859 16346/17052/14861\r\nf 16348/17054/14863 16349/17055/14864 16345/17051/14860 16344/17050/14859\r\nf 16119/16825/14634 16118/16824/14633 16350/17056/14865 16351/17057/14866\r\nf 16118/16824/14633 16117/16823/14632 16352/17058/14867 16350/17056/14865\r\nf 16353/17059/14868 16354/17060/14869 16350/17056/14865 16352/17058/14867\r\nf 16354/17060/14869 16355/17061/14870 16351/17057/14866 16350/17056/14865\r\nf 16117/16823/14632 16356/17062/14871 16357/17063/14872 16352/17058/14867\r\nf 16356/17062/14871 16358/17064/14873 16359/17065/14874 16357/17063/14872\r\nf 16360/17066/14875 16361/17067/14876 16357/17063/14872 16359/17065/14874\r\nf 16361/17067/14876 16353/17059/14868 16352/17058/14867 16357/17063/14872\r\nf 16362/17068/14877 16363/17069/14878 16364/17070/14879 16365/17071/14880\r\nf 16363/17069/14878 16366/17072/14881 16367/17073/14882 16364/17070/14879\r\nf 16368/17074/14883 16369/17075/14884 16364/17070/14879 16367/17073/14882\r\nf 16369/17075/14884 16370/17076/14885 16365/17071/14880 16364/17070/14879\r\nf 16371/17077/14886 16372/17078/14887 16373/17079/14888 16374/17080/14889\r\nf 16372/17078/14887 16375/17081/14890 16376/17082/14891 16373/17079/14888\r\nf 16377/17083/14892 16378/17084/14893 16373/17079/14888 16376/17082/14891\r\nf 16378/17084/14893 16379/17085/14894 16374/17080/14889 16373/17079/14888\r\nf 16380/17086/14895 16381/17087/14896 16382/17088/14897 16383/17089/14898\r\nf 16381/17087/14896 16384/17090/14899 16385/17091/14900 16382/17088/14897\r\nf 16366/17072/14881 16363/17069/14878 16382/17088/14897 16385/17091/14900\r\nf 16363/17069/14878 16362/17068/14877 16383/17089/14898 16382/17088/14897\r\nf 16386/17092/14901 16387/17093/14902 16388/17094/14903 16389/17095/14904\r\nf 16387/17093/14902 16390/17096/14905 16391/17097/14906 16388/17094/14903\r\nf 16375/17081/14890 16372/17078/14887 16388/17094/14903 16391/17097/14906\r\nf 16372/17078/14887 16371/17077/14886 16389/17095/14904 16388/17094/14903\r\nf 16392/17098/14907 16393/17099/14908 16394/17100/14909 16395/17101/14910\r\nf 16393/17099/14908 16396/17102/14911 16397/17103/14912 16394/17100/14909\r\nf 16384/17090/14899 16381/17087/14896 16394/17100/14909 16397/17103/14912\r\nf 16381/17087/14896 16380/17086/14895 16395/17101/14910 16394/17100/14909\r\nf 16398/17104/14913 16399/17105/14914 16400/17106/14915 16401/17107/14916\r\nf 16399/17105/14914 16402/17108/14917 16403/17109/14918 16400/17106/14915\r\nf 16390/17096/14905 16387/17093/14902 16400/17106/14915 16403/17109/14918\r\nf 16387/17093/14902 16386/17092/14901 16401/17107/14916 16400/17106/14915\r\nf 16404/17110/14919 16405/17111/14920 16406/17112/14921 16407/17113/14922\r\nf 16405/17111/14920 16408/17114/14923 16409/17115/14924 16406/17112/14921\r\nf 16396/17102/14911 16393/17099/14908 16406/17112/14921 16409/17115/14924\r\nf 16393/17099/14908 16392/17098/14907 16407/17113/14922 16406/17112/14921\r\nf 16410/17116/14925 16411/17117/14926 16412/17118/14927 16413/17119/14928\r\nf 16411/17117/14926 16414/17120/14929 16415/17121/14930 16412/17118/14927\r\nf 16402/17108/14917 16399/17105/14914 16412/17118/14927 16415/17121/14930\r\nf 16399/17105/14914 16398/17104/14913 16413/17119/14928 16412/17118/14927\r\nf 16416/17122/14931 16417/17123/14932 16418/17124/14933 16419/17125/14934\r\nf 16417/17123/14932 16420/17126/14935 16421/17127/14936 16418/17124/14933\r\nf 16408/17114/14923 16405/17111/14920 16418/17124/14933 16421/17127/14936\r\nf 16405/17111/14920 16404/17110/14919 16419/17125/14934 16418/17124/14933\r\nf 16422/17128/14937 16423/17129/14938 16424/17130/14939 16425/17131/14940\r\nf 16423/17129/14938 16426/17132/14941 16427/17133/14942 16424/17130/14939\r\nf 16414/17120/14929 16411/17117/14926 16424/17130/14939 16427/17133/14942\r\nf 16411/17117/14926 16410/17116/14925 16425/17131/14940 16424/17130/14939\r\nf 16428/17134/14943 16429/17135/14944 16430/17136/14945 16431/17137/14946\r\nf 16429/17135/14944 16432/17138/14947 16433/17139/14948 16430/17136/14945\r\nf 16420/17126/14935 16417/17123/14932 16430/17136/14945 16433/17139/14948\r\nf 16417/17123/14932 16416/17122/14931 16431/17137/14946 16430/17136/14945\r\nf 16434/17140/14949 16435/17141/14950 16436/17142/14951 16437/17143/14952\r\nf 16435/17141/14950 16438/17144/14953 16439/17145/14954 16436/17142/14951\r\nf 16426/17132/14941 16423/17129/14938 16436/17142/14951 16439/17145/14954\r\nf 16423/17129/14938 16422/17128/14937 16437/17143/14952 16436/17142/14951\r\nf 16440/17146/14955 16441/17147/14956 16442/17148/14957 16443/17149/14958\r\nf 16441/17147/14956 16444/17150/14959 16445/17151/14960 16442/17148/14957\r\nf 16432/17138/14947 16429/17135/14944 16442/17148/14957 16445/17151/14960\r\nf 16429/17135/14944 16428/17134/14943 16443/17149/14958 16442/17148/14957\r\nf 16446/17152/14961 16447/17153/14962 16448/17154/14963 16449/17155/14964\r\nf 16447/17153/14962 16450/17156/14965 16451/17157/14966 16448/17154/14963\r\nf 16438/17144/14953 16435/17141/14950 16448/17154/14963 16451/17157/14966\r\nf 16435/17141/14950 16434/17140/14949 16449/17155/14964 16448/17154/14963\r\nf 16452/17158/14967 16453/17159/14968 16454/17160/14969 16455/17161/14970\r\nf 16453/17159/14968 16456/17162/14971 16457/17163/14972 16454/17160/14969\r\nf 16444/17150/14959 16441/17147/14956 16454/17160/14969 16457/17163/14972\r\nf 16441/17147/14956 16440/17146/14955 16455/17161/14970 16454/17160/14969\r\nf 16458/17164/14973 16459/17165/14974 16460/17166/14975 16461/17167/14976\r\nf 16459/17165/14974 16462/17168/14977 16463/17169/14978 16460/17166/14975\r\nf 16450/17156/14965 16447/17153/14962 16460/17166/14975 16463/17169/14978\r\nf 16447/17153/14962 16446/17152/14961 16461/17167/14976 16460/17166/14975\r\nf 16464/17170/14979 16465/17171/14980 16466/17172/14981 16467/17173/14982\r\nf 16465/17171/14980 16468/17174/14983 16469/17175/14984 16466/17172/14981\r\nf 16456/17162/14971 16453/17159/14968 16466/17172/14981 16469/17175/14984\r\nf 16453/17159/14968 16452/17158/14967 16467/17173/14982 16466/17172/14981\r\nf 16470/17176/14985 16471/17177/14986 16472/17178/14987 16473/17179/14988\r\nf 16471/17177/14986 16474/17180/14989 16475/17181/14990 16472/17178/14987\r\nf 16462/17168/14977 16459/17165/14974 16472/17178/14987 16475/17181/14990\r\nf 16459/17165/14974 16458/17164/14973 16473/17179/14988 16472/17178/14987\r\nf 16476/17182/14991 16477/17183/14992 16478/17184/14993 16479/17185/14994\r\nf 16477/17183/14992 16480/17186/14995 16481/17187/14996 16478/17184/14993\r\nf 16468/17174/14983 16465/17171/14980 16478/17184/14993 16481/17187/14996\r\nf 16465/17171/14980 16464/17170/14979 16479/17185/14994 16478/17184/14993\r\nf 16482/17188/14997 16483/17189/14998 16484/17190/14999 16485/17191/15000\r\nf 16483/17189/14998 16486/17192/15001 16487/17193/15002 16484/17190/14999\r\nf 16474/17180/14989 16471/17177/14986 16484/17190/14999 16487/17193/15002\r\nf 16471/17177/14986 16470/17176/14985 16485/17191/15000 16484/17190/14999\r\nf 16488/17194/15003 16489/17195/15004 16490/17196/15005 16491/17197/15006\r\nf 16489/17195/15004 16492/17198/15007 16493/17199/15008 16490/17196/15005\r\nf 16480/17186/14995 16477/17183/14992 16490/17196/15005 16493/17199/15008\r\nf 16477/17183/14992 16476/17182/14991 16491/17197/15006 16490/17196/15005\r\nf 16494/17200/15009 16495/17201/15010 16496/17202/15011 16497/17203/15012\r\nf 16495/17201/15010 16498/17204/15013 16499/17205/15014 16496/17202/15011\r\nf 16486/17192/15001 16483/17189/14998 16496/17202/15011 16499/17205/15014\r\nf 16483/17189/14998 16482/17188/14997 16497/17203/15012 16496/17202/15011\r\nf 16500/17206/15015 16501/17207/15016 16502/17208/15017 16503/17209/15018\r\nf 16501/17207/15016 16504/17210/15019 16505/17211/15020 16502/17208/15017\r\nf 16492/17198/15007 16489/17195/15004 16502/17208/15017 16505/17211/15020\r\nf 16489/17195/15004 16488/17194/15003 16503/17209/15018 16502/17208/15017\r\nf 16506/17212/15021 16507/17213/15022 16508/17214/15023 16509/17215/15024\r\nf 16507/17213/15022 16129/16835/14644 16510/17216/15025 16508/17214/15023\r\nf 16498/17204/15013 16495/17201/15010 16508/17214/15023 16510/17216/15025\r\nf 16495/17201/15010 16494/17200/15009 16509/17215/15024 16508/17214/15023\r\nf 16511/17217/15026 16512/17218/15027 16513/17219/15028 16514/17220/15029\r\nf 16512/17218/15027 16515/17221/15030 16516/17222/15031 16513/17219/15028\r\nf 16504/17210/15019 16501/17207/15016 16513/17219/15028 16516/17222/15031\r\nf 16501/17207/15016 16500/17206/15015 16514/17220/15029 16513/17219/15028\r\nf 16009/16715/14524 16012/16718/14527 16517/17223/15032 16518/17224/15033\r\nf 16012/16718/14527 16017/16723/14532 16130/16836/14645 16517/17223/15032\r\nf 16129/16835/14644 16507/17213/15022 16517/17223/15032 16130/16836/14645\r\nf 16507/17213/15022 16506/17212/15021 16518/17224/15033 16517/17223/15032\r\nf 16519/17225/15034 16520/17226/15035 16521/17227/15036 16522/17228/15037\r\nf 16520/17226/15035 16523/17229/15038 16524/17230/15039 16521/17227/15036\r\nf 16515/17221/15030 16512/17218/15027 16521/17227/15036 16524/17230/15039\r\nf 16512/17218/15027 16511/17217/15026 16522/17228/15037 16521/17227/15036\r\nf 16525/17231/15040 16526/17232/15041 16527/17233/15042 16528/17234/15043\r\nf 16526/17232/15041 16151/16857/14666 16148/16854/14663 16527/17233/15042\r\nf 16147/16853/14662 16529/17235/15044 16527/17233/15042 16148/16854/14663\r\nf 16529/17235/15044 16530/17236/15045 16528/17234/15043 16527/17233/15042\r\nf 16531/17237/15046 16532/17238/15047 16533/17239/15048 16534/17240/15049\r\nf 16532/17238/15047 16535/17241/15050 16536/17242/15051 16533/17239/15048\r\nf 16024/16730/14539 16023/16729/14538 16533/17239/15048 16536/17242/15051\r\nf 16023/16729/14538 16022/16728/14537 16534/17240/15049 16533/17239/15048\r\nf 16537/17243/15052 16538/17244/15053 16539/17245/15054 16540/17246/15055\r\nf 16538/17244/15053 16158/16864/14673 16156/16862/14671 16539/17245/15054\r\nf 16151/16857/14666 16526/17232/15041 16539/17245/15054 16156/16862/14671\r\nf 16526/17232/15041 16525/17231/15040 16540/17246/15055 16539/17245/15054\r\nf 16541/17247/15056 16542/17248/15057 16543/17249/15058 16544/17250/15059\r\nf 16542/17248/15057 16545/17251/15060 16546/17252/15061 16543/17249/15058\r\nf 16535/17241/15050 16532/17238/15047 16543/17249/15058 16546/17252/15061\r\nf 16532/17238/15047 16531/17237/15046 16544/17250/15059 16543/17249/15058\r\nf 16547/17253/15062 16548/17254/15063 16549/17255/15064 16550/17256/15065\r\nf 16548/17254/15063 16551/17257/15066 16552/17258/15067 16549/17255/15064\r\nf 16158/16864/14673 16538/17244/15053 16549/17255/15064 16552/17258/15067\r\nf 16538/17244/15053 16537/17243/15052 16550/17256/15065 16549/17255/15064\r\nf 16553/17259/15068 16554/17260/15069 16555/17261/15070 16556/17262/15071\r\nf 16554/17260/15069 16557/17263/15072 16558/17264/15073 16555/17261/15070\r\nf 16545/17251/15060 16542/17248/15057 16555/17261/15070 16558/17264/15073\r\nf 16542/17248/15057 16541/17247/15056 16556/17262/15071 16555/17261/15070\r\nf 16559/17265/15074 16560/17266/15075 16561/17267/15076 16562/17268/15077\r\nf 16560/17266/15075 16563/17269/15078 16564/17270/15079 16561/17267/15076\r\nf 16551/17257/15066 16548/17254/15063 16561/17267/15076 16564/17270/15079\r\nf 16548/17254/15063 16547/17253/15062 16562/17268/15077 16561/17267/15076\r\nf 16565/17271/15080 16566/17272/15081 16567/17273/15082 16568/17274/15083\r\nf 16566/17272/15081 16569/17275/15084 16570/17276/15085 16567/17273/15082\r\nf 16557/17263/15072 16554/17260/15069 16567/17273/15082 16570/17276/15085\r\nf 16554/17260/15069 16553/17259/15068 16568/17274/15083 16567/17273/15082\r\nf 16571/17277/15086 16572/17278/15087 16573/17279/15088 16574/17280/15089\r\nf 16572/17278/15087 16575/17281/15090 16576/17282/15091 16573/17279/15088\r\nf 16563/17269/15078 16560/17266/15075 16573/17279/15088 16576/17282/15091\r\nf 16560/17266/15075 16559/17265/15074 16574/17280/15089 16573/17279/15088\r\nf 16577/17283/15092 16578/17284/15093 16579/17285/15094 16580/17286/15095\r\nf 16578/17284/15093 16581/17287/15096 16582/17288/15097 16579/17285/15094\r\nf 16569/17275/15084 16566/17272/15081 16579/17285/15094 16582/17288/15097\r\nf 16566/17272/15081 16565/17271/15080 16580/17286/15095 16579/17285/15094\r\nf 16583/17289/15098 16584/17290/15099 16585/17291/15100 16586/17292/15101\r\nf 16584/17290/15099 16587/17293/15102 16588/17294/15103 16585/17291/15100\r\nf 16575/17281/15090 16572/17278/15087 16585/17291/15100 16588/17294/15103\r\nf 16572/17278/15087 16571/17277/15086 16586/17292/15101 16585/17291/15100\r\nf 16589/17295/15104 16590/17296/15105 16591/17297/15106 16592/17298/15107\r\nf 16590/17296/15105 16593/17299/15108 16594/17300/15109 16591/17297/15106\r\nf 16581/17287/15096 16578/17284/15093 16591/17297/15106 16594/17300/15109\r\nf 16578/17284/15093 16577/17283/15092 16592/17298/15107 16591/17297/15106\r\nf 16595/17301/15110 16596/17302/15111 16597/17303/15112 16598/17304/15113\r\nf 16596/17302/15111 16599/17305/15114 16600/17306/15115 16597/17303/15112\r\nf 16587/17293/15102 16584/17290/15099 16597/17303/15112 16600/17306/15115\r\nf 16584/17290/15099 16583/17289/15098 16598/17304/15113 16597/17303/15112\r\nf 16601/17307/15116 16602/17308/15117 16603/17309/15118 16604/17310/15119\r\nf 16602/17308/15117 16605/17311/15120 16606/17312/15121 16603/17309/15118\r\nf 16593/17299/15108 16590/17296/15105 16603/17309/15118 16606/17312/15121\r\nf 16590/17296/15105 16589/17295/15104 16604/17310/15119 16603/17309/15118\r\nf 16607/17313/15122 16608/17314/15123 16609/17315/15124 16610/17316/15125\r\nf 16608/17314/15123 16611/17317/15126 16612/17318/15127 16609/17315/15124\r\nf 16599/17305/15114 16596/17302/15111 16609/17315/15124 16612/17318/15127\r\nf 16596/17302/15111 16595/17301/15110 16610/17316/15125 16609/17315/15124\r\nf 16613/17319/15128 16614/17320/15129 16615/17321/15130 16616/17322/15131\r\nf 16614/17320/15129 16617/17323/15132 16618/17324/15133 16615/17321/15130\r\nf 16605/17311/15120 16602/17308/15117 16615/17321/15130 16618/17324/15133\r\nf 16602/17308/15117 16601/17307/15116 16616/17322/15131 16615/17321/15130\r\nf 16619/17325/15134 16620/17326/15135 16621/17327/15136 16622/17328/15137\r\nf 16620/17326/15135 16623/17329/15138 16624/17330/15139 16621/17327/15136\r\nf 16611/17317/15126 16608/17314/15123 16621/17327/15136 16624/17330/15139\r\nf 16608/17314/15123 16607/17313/15122 16622/17328/15137 16621/17327/15136\r\nf 16625/17331/15140 16626/17332/15141 16627/17333/15142 16628/17334/15143\r\nf 16626/17332/15141 16629/17335/15144 16630/17336/15145 16627/17333/15142\r\nf 16617/17323/15132 16614/17320/15129 16627/17333/15142 16630/17336/15145\r\nf 16614/17320/15129 16613/17319/15128 16628/17334/15143 16627/17333/15142\r\nf 16631/17337/15146 16632/17338/15147 16633/17339/15148 16634/17340/15149\r\nf 16632/17338/15147 16635/17341/15150 16636/17342/15151 16633/17339/15148\r\nf 16623/17329/15138 16620/17326/15135 16633/17339/15148 16636/17342/15151\r\nf 16620/17326/15135 16619/17325/15134 16634/17340/15149 16633/17339/15148\r\nf 16637/17343/15152 16638/17344/15153 16639/17345/15154 16640/17346/15155\r\nf 16638/17344/15153 16641/17347/15156 16642/17348/15157 16639/17345/15154\r\nf 16629/17335/15144 16626/17332/15141 16639/17345/15154 16642/17348/15157\r\nf 16626/17332/15141 16625/17331/15140 16640/17346/15155 16639/17345/15154\r\nf 16643/17349/15158 16644/17350/15159 16645/17351/15160 16646/17352/15161\r\nf 16644/17350/15159 16647/17353/15162 16648/17354/15163 16645/17351/15160\r\nf 16635/17341/15150 16632/17338/15147 16645/17351/15160 16648/17354/15163\r\nf 16632/17338/15147 16631/17337/15146 16646/17352/15161 16645/17351/15160\r\nf 16649/17355/15164 16650/17356/15165 16651/17357/15166 16652/17358/15167\r\nf 16650/17356/15165 16653/17359/15168 16654/17360/15169 16651/17357/15166\r\nf 16641/17347/15156 16638/17344/15153 16651/17357/15166 16654/17360/15169\r\nf 16638/17344/15153 16637/17343/15152 16652/17358/15167 16651/17357/15166\r\nf 16655/17361/15170 16656/17362/15171 16657/17363/15172 16658/17364/15173\r\nf 16656/17362/15171 16659/17365/15174 16660/17366/15175 16657/17363/15172\r\nf 16647/17353/15162 16644/17350/15159 16657/17363/15172 16660/17366/15175\r\nf 16644/17350/15159 16643/17349/15158 16658/17364/15173 16657/17363/15172\r\nf 16661/17367/15176 16662/17368/15177 16663/17369/15178 16664/17370/15179\r\nf 16662/17368/15177 16665/17371/15180 16666/17372/15181 16663/17369/15178\r\nf 16653/17359/15168 16650/17356/15165 16663/17369/15178 16666/17372/15181\r\nf 16650/17356/15165 16649/17355/15164 16664/17370/15179 16663/17369/15178\r\nf 16667/17373/15182 16668/17374/15183 16669/17375/15184 16670/17376/15185\r\nf 16668/17374/15183 16671/17377/15186 16672/17378/15187 16669/17375/15184\r\nf 16659/17365/15174 16656/17362/15171 16669/17375/15184 16672/17378/15187\r\nf 16656/17362/15171 16655/17361/15170 16670/17376/15185 16669/17375/15184\r\nf 16673/17379/15188 16674/17380/15189 16675/17381/15190 16676/17382/15191\r\nf 16674/17380/15189 16677/17383/15192 16678/17384/15193 16675/17381/15190\r\nf 16665/17371/15180 16662/17368/15177 16675/17381/15190 16678/17384/15193\r\nf 16662/17368/15177 16661/17367/15176 16676/17382/15191 16675/17381/15190\r\nf 16679/17385/15194 16680/17386/15195 16681/17387/15196 16682/17388/15197\r\nf 16680/17386/15195 16683/17389/15198 16684/17390/15199 16681/17387/15196\r\nf 16671/17377/15186 16668/17374/15183 16681/17387/15196 16684/17390/15199\r\nf 16668/17374/15183 16667/17373/15182 16682/17388/15197 16681/17387/15196\r\nf 16685/17391/15200 16686/17392/15201 16687/17393/15202 16688/17394/15203\r\nf 16686/17392/15201 16689/17395/15204 16690/17396/15205 16687/17393/15202\r\nf 16677/17383/15192 16674/17380/15189 16687/17393/15202 16690/17396/15205\r\nf 16674/17380/15189 16673/17379/15188 16688/17394/15203 16687/17393/15202\r\nf 16691/17397/15206 16692/17398/15207 16693/17399/15208 16694/17400/15209\r\nf 16692/17398/15207 16695/17401/15210 16696/17402/15211 16693/17399/15208\r\nf 16683/17389/15198 16680/17386/15195 16693/17399/15208 16696/17402/15211\r\nf 16680/17386/15195 16679/17385/15194 16694/17400/15209 16693/17399/15208\r\nf 16697/17403/15212 16698/17404/15213 16699/17405/15214 16700/17406/15215\r\nf 16698/17404/15213 16701/17407/15216 16702/17408/15217 16699/17405/15214\r\nf 16689/17395/15204 16686/17392/15201 16699/17405/15214 16702/17408/15217\r\nf 16686/17392/15201 16685/17391/15200 16700/17406/15215 16699/17405/15214\r\nf 16703/17409/15218 16704/17410/15219 16705/17411/15220 16706/17412/15221\r\nf 16704/17410/15219 16707/17413/15222 16708/17414/15223 16705/17411/15220\r\nf 16695/17401/15210 16692/17398/15207 16705/17411/15220 16708/17414/15223\r\nf 16692/17398/15207 16691/17397/15206 16706/17412/15221 16705/17411/15220\r\nf 16709/17415/15224 16710/17416/15225 16711/17417/15226 16712/17418/15227\r\nf 16710/17416/15225 16713/17419/15228 16714/17420/15229 16711/17417/15226\r\nf 16701/17407/15216 16698/17404/15213 16711/17417/15226 16714/17420/15229\r\nf 16698/17404/15213 16697/17403/15212 16712/17418/15227 16711/17417/15226\r\nf 16715/17421/15230 16716/17422/15231 16717/17423/15232 16718/17424/15233\r\nf 16716/17422/15231 16719/17425/15234 16720/17426/15235 16717/17423/15232\r\nf 16707/17413/15222 16704/17410/15219 16717/17423/15232 16720/17426/15235\r\nf 16704/17410/15219 16703/17409/15218 16718/17424/15233 16717/17423/15232\r\nf 16721/17427/15236 16722/17428/15237 16723/17429/15238 16724/17430/15239\r\nf 16722/17428/15237 16725/17431/15240 16726/17432/15241 16723/17429/15238\r\nf 16713/17419/15228 16710/17416/15225 16723/17429/15238 16726/17432/15241\r\nf 16710/17416/15225 16709/17415/15224 16724/17430/15239 16723/17429/15238\r\nf 16727/17433/15242 16728/17434/15243 16729/17435/15244 16730/17436/15245\r\nf 16728/17434/15243 16731/17437/15246 16732/17438/15247 16729/17435/15244\r\nf 16719/17425/15234 16716/17422/15231 16729/17435/15244 16732/17438/15247\r\nf 16716/17422/15231 16715/17421/15230 16730/17436/15245 16729/17435/15244\r\nf 16733/17439/15248 16734/17440/15249 16735/17441/15250 16736/17442/15251\r\nf 16734/17440/15249 16737/17443/15252 16738/17444/15253 16735/17441/15250\r\nf 16725/17431/15240 16722/17428/15237 16735/17441/15250 16738/17444/15253\r\nf 16722/17428/15237 16721/17427/15236 16736/17442/15251 16735/17441/15250\r\nf 16739/17445/15254 16740/17446/15255 16741/17447/15256 16742/17448/15257\r\nf 16740/17446/15255 16743/17449/15258 16744/17450/15259 16741/17447/15256\r\nf 16731/17437/15246 16728/17434/15243 16741/17447/15256 16744/17450/15259\r\nf 16728/17434/15243 16727/17433/15242 16742/17448/15257 16741/17447/15256\r\nf 16027/16733/14542 16030/16736/14545 16745/17451/15260 16746/17452/15261\r\nf 16030/16736/14545 16035/16741/14550 16747/17453/15262 16745/17451/15260\r\nf 16737/17443/15252 16734/17440/15249 16745/17451/15260 16747/17453/15262\r\nf 16734/17440/15249 16733/17439/15248 16746/17452/15261 16745/17451/15260\r\nf 16748/17454/15263 16749/17455/15264 16750/17456/15265 16751/17457/15266\r\nf 16749/17455/15264 16752/17458/15267 16753/17459/15268 16750/17456/15265\r\nf 16743/17449/15258 16740/17446/15255 16750/17456/15265 16753/17459/15268\r\nf 16740/17446/15255 16739/17445/15254 16751/17457/15266 16750/17456/15265\r\nf 16754/17460/15269 16755/17461/15270 16756/17462/15271 16757/17463/15272\r\nf 16755/17461/15270 16758/17464/15273 16759/17465/15274 16756/17462/15271\r\nf 16752/17458/15267 16749/17455/15264 16756/17462/15271 16759/17465/15274\r\nf 16749/17455/15264 16748/17454/15263 16757/17463/15272 16756/17462/15271\r\nf 16760/17466/15275 16761/17467/15276 16762/17468/15277 16763/17469/15278\r\nf 16761/17467/15276 16764/17470/15279 16765/17471/15280 16762/17468/15277\r\nf 16033/16739/14548 16032/16738/14547 16762/17468/15277 16765/17471/15280\r\nf 16032/16738/14547 16031/16737/14546 16763/17469/15278 16762/17468/15277\r\nf 16754/17460/15269 16766/17472/15281 16767/17473/15282 16755/17461/15270\r\nf 16766/17472/15281 16768/17474/15283 16769/17475/15284 16767/17473/15282\r\nf 16770/17476/15285 16771/17477/15286 16767/17473/15282 16769/17475/15284\r\nf 16771/17477/15286 16758/17464/15273 16755/17461/15270 16767/17473/15282\r\nf 16174/16880/14689 16772/17478/15287 16773/17479/15288 16175/16881/14690\r\nf 16772/17478/15287 16774/17480/15289 16775/17481/15290 16773/17479/15288\r\nf 16042/16748/14557 16041/16747/14556 16773/17479/15288 16775/17481/15290\r\nf 16041/16747/14556 16040/16746/14555 16175/16881/14690 16773/17479/15288\r\nf 16776/17482/15291 16777/17483/15292 16778/17484/15293 16779/17485/15294\r\nf 16777/17483/15292 16780/17486/15295 16781/17487/15296 16778/17484/15293\r\nf 16051/16757/14566 16050/16756/14565 16778/17484/15293 16781/17487/15296\r\nf 16050/16756/14565 16049/16755/14564 16779/17485/15294 16778/17484/15293\r\nf 16782/17488/15297 16783/17489/15298 16784/17490/15299 16785/17491/15300\r\nf 16783/17489/15298 16786/17492/15301 16787/17493/15302 16784/17490/15299\r\nf 16774/17480/15289 16772/17478/15287 16784/17490/15299 16787/17493/15302\r\nf 16772/17478/15287 16174/16880/14689 16785/17491/15300 16784/17490/15299\r\nf 16788/17494/15303 16789/17495/15304 16790/17496/15305 16791/17497/15306\r\nf 16789/17495/15304 16792/17498/15307 16793/17499/15308 16790/17496/15305\r\nf 16780/17486/15295 16777/17483/15292 16790/17496/15305 16793/17499/15308\r\nf 16777/17483/15292 16776/17482/15291 16791/17497/15306 16790/17496/15305\r\nf 16794/17500/15309 16795/17501/15310 16796/17502/15311 16797/17503/15312\r\nf 16795/17501/15310 16798/17504/15313 16799/17505/15314 16796/17502/15311\r\nf 16786/17492/15301 16783/17489/15298 16796/17502/15311 16799/17505/15314\r\nf 16783/17489/15298 16782/17488/15297 16797/17503/15312 16796/17502/15311\r\nf 16800/17506/15315 16801/17507/15316 16802/17508/15317 16803/17509/15318\r\nf 16801/17507/15316 16804/17510/15319 16805/17511/15320 16802/17508/15317\r\nf 16792/17498/15307 16789/17495/15304 16802/17508/15317 16805/17511/15320\r\nf 16789/17495/15304 16788/17494/15303 16803/17509/15318 16802/17508/15317\r\nf 16806/17512/15321 16807/17513/15322 16808/17514/15323 16809/17515/15324\r\nf 16807/17513/15322 16810/17516/15325 16811/17517/15326 16808/17514/15323\r\nf 16798/17504/15313 16795/17501/15310 16808/17514/15323 16811/17517/15326\r\nf 16795/17501/15310 16794/17500/15309 16809/17515/15324 16808/17514/15323\r\nf 16812/17518/15327 16813/17519/15328 16814/17520/15329 16815/17521/15330\r\nf 16813/17519/15328 16816/17522/15331 16817/17523/15332 16814/17520/15329\r\nf 16804/17510/15319 16801/17507/15316 16814/17520/15329 16817/17523/15332\r\nf 16801/17507/15316 16800/17506/15315 16815/17521/15330 16814/17520/15329\r\nf 16054/16760/14569 16057/16763/14572 16818/17524/15333 16819/17525/15334\r\nf 16057/16763/14572 16062/16768/14577 16820/17526/15335 16818/17524/15333\r\nf 16810/17516/15325 16807/17513/15322 16818/17524/15333 16820/17526/15335\r\nf 16807/17513/15322 16806/17512/15321 16819/17525/15334 16818/17524/15333\r\nf 16063/16769/14578 16066/16772/14581 16821/17527/15336 16822/17528/15337\r\nf 16066/16772/14581 16071/16777/14586 16823/17529/15338 16821/17527/15336\r\nf 16816/17522/15331 16813/17519/15328 16821/17527/15336 16823/17529/15338\r\nf 16813/17519/15328 16812/17518/15327 16822/17528/15337 16821/17527/15336\r\nf 16370/17076/14885 16369/17075/14884 16824/17530/15339 16825/17531/15340\r\nf 16369/17075/14884 16368/17074/14883 16826/17532/15341 16824/17530/15339\r\nf 16060/16766/14575 16059/16765/14574 16824/17530/15339 16826/17532/15341\r\nf 16059/16765/14574 16058/16764/14573 16825/17531/15340 16824/17530/15339\r\nf 16379/17085/14894 16378/17084/14893 16827/17533/15342 16828/17534/15343\r\nf 16378/17084/14893 16377/17083/14892 16829/17535/15344 16827/17533/15342\r\nf 16069/16775/14584 16068/16774/14583 16827/17533/15342 16829/17535/15344\r\nf 16068/16774/14583 16067/16773/14582 16828/17534/15343 16827/17533/15342\r\nf 16072/16778/14587 16075/16781/14590 16830/17536/15345 16831/17537/15346\r\nf 16075/16781/14590 16080/16786/14595 16832/17538/15347 16830/17536/15345\r\nf 16833/17539/15348 16834/17540/15349 16830/17536/15345 16832/17538/15347\r\nf 16834/17540/15349 16835/17541/15350 16831/17537/15346 16830/17536/15345\r\nf 16836/17542/15351 16837/17543/15352 16838/17544/15353 16839/17545/15354\r\nf 16837/17543/15352 16840/17546/15355 16841/17547/15356 16838/17544/15353\r\nf 16078/16784/14593 16077/16783/14592 16838/17544/15353 16841/17547/15356\r\nf 16077/16783/14592 16076/16782/14591 16839/17545/15354 16838/17544/15353\r\nf 16842/17548/15357 16843/17549/15358 16844/17550/15359 16845/17551/15360\r\nf 16843/17549/15358 16846/17552/15361 16847/17553/15362 16844/17550/15359\r\nf 16840/17546/15355 16837/17543/15352 16844/17550/15359 16847/17553/15362\r\nf 16837/17543/15352 16836/17542/15351 16845/17551/15360 16844/17550/15359\r\nf 16848/17554/15363 16849/17555/15364 16850/17556/15365 16851/17557/15366\r\nf 16849/17555/15364 16852/17558/15367 16853/17559/15368 16850/17556/15365\r\nf 16846/17552/15361 16843/17549/15358 16850/17556/15365 16853/17559/15368\r\nf 16843/17549/15358 16842/17548/15357 16851/17557/15366 16850/17556/15365\r\nf 16184/16890/14699 16854/17560/15369 16855/17561/15370 16856/17562/15371\r\nf 16854/17560/15369 16188/16894/14703 16857/17563/15372 16855/17561/15370\r\nf 16852/17558/15367 16849/17555/15364 16855/17561/15370 16857/17563/15372\r\nf 16849/17555/15364 16848/17554/15363 16856/17562/15371 16855/17561/15370\r\nf 16180/16886/14695 16858/17564/15373 16859/17565/15374 16181/16887/14696\r\nf 16858/17564/15373 16192/16898/14707 16189/16895/14704 16859/17565/15374\r\nf 16188/16894/14703 16854/17560/15369 16859/17565/15374 16189/16895/14704\r\nf 16854/17560/15369 16184/16890/14699 16181/16887/14696 16859/17565/15374\r\nf 16207/16913/14722 16860/17566/15375 16861/17567/15376 16862/17568/15377\r\nf 16860/17566/15375 16199/16905/14714 16197/16903/14712 16861/17567/15376\r\nf 16192/16898/14707 16858/17564/15373 16861/17567/15376 16197/16903/14712\r\nf 16858/17564/15373 16180/16886/14695 16862/17568/15377 16861/17567/15376\r\nf 16203/16909/14718 16863/17569/15378 16864/17570/15379 16204/16910/14719\r\nf 16863/17569/15378 16211/16917/14726 16209/16915/14724 16864/17570/15379\r\nf 16199/16905/14714 16860/17566/15375 16864/17570/15379 16209/16915/14724\r\nf 16860/17566/15375 16207/16913/14722 16204/16910/14719 16864/17570/15379\r\nf 16081/16787/14596 16084/16790/14599 16865/17571/15380 16866/17572/15381\r\nf 16084/16790/14599 16089/16795/14604 16867/17573/15382 16865/17571/15380\r\nf 16868/17574/15383 16869/17575/15384 16865/17571/15380 16867/17573/15382\r\nf 16869/17575/15384 16870/17576/15385 16866/17572/15381 16865/17571/15380\r\nf 16871/17577/15386 16872/17578/15387 16873/17579/15388 16874/17580/15389\r\nf 16872/17578/15387 16229/16935/14744 16227/16933/14742 16873/17579/15388\r\nf 16087/16793/14602 16086/16792/14601 16873/17579/15388 16227/16933/14742\r\nf 16086/16792/14601 16085/16791/14600 16874/17580/15389 16873/17579/15388\r\nf 16875/17581/15390 16876/17582/15391 16877/17583/15392 16878/17584/15393\r\nf 16876/17582/15391 16235/16941/14750 16233/16939/14748 16877/17583/15392\r\nf 16229/16935/14744 16872/17578/15387 16877/17583/15392 16233/16939/14748\r\nf 16872/17578/15387 16871/17577/15386 16878/17584/15393 16877/17583/15392\r\nf 16879/17585/15394 16880/17586/15395 16881/17587/15396 16882/17588/15397\r\nf 16880/17586/15395 16241/16947/14756 16239/16945/14754 16881/17587/15396\r\nf 16235/16941/14750 16876/17582/15391 16881/17587/15396 16239/16945/14754\r\nf 16876/17582/15391 16875/17581/15390 16882/17588/15397 16881/17587/15396\r\nf 16883/17589/15398 16884/17590/15399 16885/17591/15400 16886/17592/15401\r\nf 16884/17590/15399 16247/16953/14762 16245/16951/14760 16885/17591/15400\r\nf 16241/16947/14756 16880/17586/15395 16885/17591/15400 16245/16951/14760\r\nf 16880/17586/15395 16879/17585/15394 16886/17592/15401 16885/17591/15400\r\nf 16887/17593/15402 16888/17594/15403 16889/17595/15404 16890/17596/15405\r\nf 16888/17594/15403 16253/16959/14768 16251/16957/14766 16889/17595/15404\r\nf 16247/16953/14762 16884/17590/15399 16889/17595/15404 16251/16957/14766\r\nf 16884/17590/15399 16883/17589/15398 16890/17596/15405 16889/17595/15404\r\nf 16891/17597/15406 16892/17598/15407 16893/17599/15408 16894/17600/15409\r\nf 16892/17598/15407 16259/16965/14774 16257/16963/14772 16893/17599/15408\r\nf 16253/16959/14768 16888/17594/15403 16893/17599/15408 16257/16963/14772\r\nf 16888/17594/15403 16887/17593/15402 16894/17600/15409 16893/17599/15408\r\nf 16895/17601/15410 16896/17602/15411 16897/17603/15412 16898/17604/15413\r\nf 16896/17602/15411 16265/16971/14780 16263/16969/14778 16897/17603/15412\r\nf 16259/16965/14774 16892/17598/15407 16897/17603/15412 16263/16969/14778\r\nf 16892/17598/15407 16891/17597/15406 16898/17604/15413 16897/17603/15412\r\nf 16899/17605/15414 16900/17606/15415 16901/17607/15416 16902/17608/15417\r\nf 16900/17606/15415 16271/16977/14786 16269/16975/14784 16901/17607/15416\r\nf 16265/16971/14780 16896/17602/15411 16901/17607/15416 16269/16975/14784\r\nf 16896/17602/15411 16895/17601/15410 16902/17608/15417 16901/17607/15416\r\nf 16903/17609/15418 16904/17610/15419 16905/17611/15420 16906/17612/15421\r\nf 16904/17610/15419 16277/16983/14792 16275/16981/14790 16905/17611/15420\r\nf 16271/16977/14786 16900/17606/15415 16905/17611/15420 16275/16981/14790\r\nf 16900/17606/15415 16899/17605/15414 16906/17612/15421 16905/17611/15420\r\nf 16907/17613/15422 16908/17614/15423 16909/17615/15424 16910/17616/15425\r\nf 16908/17614/15423 16283/16989/14798 16281/16987/14796 16909/17615/15424\r\nf 16277/16983/14792 16904/17610/15419 16909/17615/15424 16281/16987/14796\r\nf 16904/17610/15419 16903/17609/15418 16910/17616/15425 16909/17615/15424\r\nf 16911/17617/15426 16912/17618/15427 16913/17619/15428 16914/17620/15429\r\nf 16912/17618/15427 16289/16995/14804 16287/16993/14802 16913/17619/15428\r\nf 16283/16989/14798 16908/17614/15423 16913/17619/15428 16287/16993/14802\r\nf 16908/17614/15423 16907/17613/15422 16914/17620/15429 16913/17619/15428\r\nf 16915/17621/15430 16916/17622/15431 16917/17623/15432 16918/17624/15433\r\nf 16916/17622/15431 16295/17001/14810 16293/16999/14808 16917/17623/15432\r\nf 16289/16995/14804 16912/17618/15427 16917/17623/15432 16293/16999/14808\r\nf 16912/17618/15427 16911/17617/15426 16918/17624/15433 16917/17623/15432\r\nf 16919/17625/15434 16920/17626/15435 16921/17627/15436 16922/17628/15437\r\nf 16920/17626/15435 16301/17007/14816 16299/17005/14814 16921/17627/15436\r\nf 16295/17001/14810 16916/17622/15431 16921/17627/15436 16299/17005/14814\r\nf 16916/17622/15431 16915/17621/15430 16922/17628/15437 16921/17627/15436\r\nf 16923/17629/15438 16924/17630/15439 16925/17631/15440 16926/17632/15441\r\nf 16924/17630/15439 16307/17013/14822 16305/17011/14820 16925/17631/15440\r\nf 16301/17007/14816 16920/17626/15435 16925/17631/15440 16305/17011/14820\r\nf 16920/17626/15435 16919/17625/15434 16926/17632/15441 16925/17631/15440\r\nf 16927/17633/15442 16928/17634/15443 16929/17635/15444 16930/17636/15445\r\nf 16928/17634/15443 16313/17019/14828 16311/17017/14826 16929/17635/15444\r\nf 16307/17013/14822 16924/17630/15439 16929/17635/15444 16311/17017/14826\r\nf 16924/17630/15439 16923/17629/15438 16930/17636/15445 16929/17635/15444\r\nf 16931/17637/15446 16932/17638/15447 16933/17639/15448 16934/17640/15449\r\nf 16932/17638/15447 16319/17025/14834 16317/17023/14832 16933/17639/15448\r\nf 16313/17019/14828 16928/17634/15443 16933/17639/15448 16317/17023/14832\r\nf 16928/17634/15443 16927/17633/15442 16934/17640/15449 16933/17639/15448\r\nf 16090/16796/14605 16093/16799/14608 16935/17641/15450 16936/17642/15451\r\nf 16093/16799/14608 16098/16804/14613 16323/17029/14838 16935/17641/15450\r\nf 16319/17025/14834 16932/17638/15447 16935/17641/15450 16323/17029/14838\r\nf 16932/17638/15447 16931/17637/15446 16936/17642/15451 16935/17641/15450\r\nf 16937/17643/15452 16938/17644/15453 16939/17645/15454 16940/17646/15455\r\nf 16938/17644/15453 16358/17064/14873 16356/17062/14871 16939/17645/15454\r\nf 16117/16823/14632 16116/16822/14631 16939/17645/15454 16356/17062/14871\r\nf 16116/16822/14631 16115/16821/14630 16940/17646/15455 16939/17645/15454\r\nf 16941/17647/15456 16942/17648/15457 16943/17649/15458 16944/17650/15459\r\nf 16942/17648/15457 16945/17651/15460 16946/17652/15461 16943/17649/15458\r\nf 16358/17064/14873 16938/17644/15453 16943/17649/15458 16946/17652/15461\r\nf 16938/17644/15453 16937/17643/15452 16944/17650/15459 16943/17649/15458\r\nf 16947/17653/15462 16948/17654/15463 16949/17655/15464 16950/17656/15465\r\nf 16948/17654/15463 16951/17657/15466 16952/17658/15467 16949/17655/15464\r\nf 16945/17651/15460 16942/17648/15457 16949/17655/15464 16952/17658/15467\r\nf 16942/17648/15457 16941/17647/15456 16950/17656/15465 16949/17655/15464\r\nf 16120/16826/14635 16123/16829/14638 16953/17659/15468 16954/17660/15469\r\nf 16123/16829/14638 16128/16834/14643 16955/17661/15470 16953/17659/15468\r\nf 16951/17657/15466 16948/17654/15463 16953/17659/15468 16955/17661/15470\r\nf 16948/17654/15463 16947/17653/15462 16954/17660/15469 16953/17659/15468\r\nf 16956/17662/15471 16957/17663/15472 16958/17664/15473 16959/17665/15474\r\nf 16957/17663/15472 16960/17666/15475 16961/17667/15476 16958/17664/15473\r\nf 16126/16832/14641 16125/16831/14640 16958/17664/15473 16961/17667/15476\r\nf 16125/16831/14640 16124/16830/14639 16959/17665/15474 16958/17664/15473\r\nf 16962/17668/15477 16963/17669/15478 16964/17670/15479 16965/17671/15480\r\nf 16963/17669/15478 16966/17672/15481 16967/17673/15482 16964/17670/15479\r\nf 16960/17666/15475 16957/17663/15472 16964/17670/15479 16967/17673/15482\r\nf 16957/17663/15472 16956/17662/15471 16965/17671/15480 16964/17670/15479\r\nf 16968/17674/15483 16969/17675/15484 16970/17676/15485 16971/17677/15486\r\nf 16969/17675/15484 16972/17678/15487 16973/17679/15488 16970/17676/15485\r\nf 16966/17672/15481 16963/17669/15478 16970/17676/15485 16973/17679/15488\r\nf 16963/17669/15478 16962/17668/15477 16971/17677/15486 16970/17676/15485\r\nf 16974/17680/15489 16975/17681/15490 16976/17682/15491 16977/17683/15492\r\nf 16975/17681/15490 16978/17684/15493 16979/17685/15494 16976/17682/15491\r\nf 16972/17678/15487 16969/17675/15484 16976/17682/15491 16979/17685/15494\r\nf 16969/17675/15484 16968/17674/15483 16977/17683/15492 16976/17682/15491\r\nf 16980/17686/15495 16981/17687/15496 16982/17688/15497 16983/17689/15498\r\nf 16981/17687/15496 16984/17690/15499 16985/17691/15500 16982/17688/15497\r\nf 16978/17684/15493 16975/17681/15490 16982/17688/15497 16985/17691/15500\r\nf 16975/17681/15490 16974/17680/15489 16983/17689/15498 16982/17688/15497\r\nf 16986/17692/15501 16987/17693/15502 16988/17694/15503 16989/17695/15504\r\nf 16987/17693/15502 16990/17696/15505 16991/17697/15506 16988/17694/15503\r\nf 16984/17690/15499 16981/17687/15496 16988/17694/15503 16991/17697/15506\r\nf 16981/17687/15496 16980/17686/15495 16989/17695/15504 16988/17694/15503\r\nf 16835/17541/15350 16834/17540/15349 16992/17698/15507 16993/17699/15508\r\nf 16834/17540/15349 16833/17539/15348 16994/17700/15509 16992/17698/15507\r\nf 16990/17696/15505 16987/17693/15502 16992/17698/15507 16994/17700/15509\r\nf 16987/17693/15502 16986/17692/15501 16993/17699/15508 16992/17698/15507\r\nf 16362/17068/14877 16365/17071/14880 16995/17701/15510 16996/17702/15511\r\nf 16365/17071/14880 16370/17076/14885 16997/17703/15512 16995/17701/15510\r\nf 16998/17704/15513 16999/17705/15514 16995/17701/15510 16997/17703/15512\r\nf 16999/17705/15514 17000/17706/15515 16996/17702/15511 16995/17701/15510\r\nf 16377/17083/14892 16376/17082/14891 17001/17707/15516 17002/17708/15517\r\nf 16376/17082/14891 16375/17081/14890 17003/17709/15518 17001/17707/15516\r\nf 17004/17710/15519 17005/17711/15520 17001/17707/15516 17003/17709/15518\r\nf 17005/17711/15520 17006/17712/15521 17002/17708/15517 17001/17707/15516\r\nf 16380/17086/14895 16383/17089/14898 17007/17713/15522 17008/17714/15523\r\nf 16383/17089/14898 16362/17068/14877 16996/17702/15511 17007/17713/15522\r\nf 17000/17706/15515 17009/17715/15524 17007/17713/15522 16996/17702/15511\r\nf 17009/17715/15524 17010/17716/15525 17008/17714/15523 17007/17713/15522\r\nf 16375/17081/14890 16391/17097/14906 17011/17717/15526 17003/17709/15518\r\nf 16391/17097/14906 16390/17096/14905 17012/17718/15527 17011/17717/15526\r\nf 17013/17719/15528 17014/17720/15529 17011/17717/15526 17012/17718/15527\r\nf 17014/17720/15529 17004/17710/15519 17003/17709/15518 17011/17717/15526\r\nf 16392/17098/14907 16395/17101/14910 17015/17721/15530 17016/17722/15531\r\nf 16395/17101/14910 16380/17086/14895 17008/17714/15523 17015/17721/15530\r\nf 17010/17716/15525 17017/17723/15532 17015/17721/15530 17008/17714/15523\r\nf 17017/17723/15532 17018/17724/15533 17016/17722/15531 17015/17721/15530\r\nf 16390/17096/14905 16403/17109/14918 17019/17725/15534 17012/17718/15527\r\nf 16403/17109/14918 16402/17108/14917 17020/17726/15535 17019/17725/15534\r\nf 17021/17727/15536 17022/17728/15537 17019/17725/15534 17020/17726/15535\r\nf 17022/17728/15537 17013/17719/15528 17012/17718/15527 17019/17725/15534\r\nf 16404/17110/14919 16407/17113/14922 17023/17729/15538 17024/17730/15539\r\nf 16407/17113/14922 16392/17098/14907 17016/17722/15531 17023/17729/15538\r\nf 17018/17724/15533 17025/17731/15540 17023/17729/15538 17016/17722/15531\r\nf 17025/17731/15540 17026/17732/15541 17024/17730/15539 17023/17729/15538\r\nf 16402/17108/14917 16415/17121/14930 17027/17733/15542 17020/17726/15535\r\nf 16415/17121/14930 16414/17120/14929 17028/17734/15543 17027/17733/15542\r\nf 17029/17735/15544 17030/17736/15545 17027/17733/15542 17028/17734/15543\r\nf 17030/17736/15545 17021/17727/15536 17020/17726/15535 17027/17733/15542\r\nf 16416/17122/14931 16419/17125/14934 17031/17737/15546 17032/17738/15547\r\nf 16419/17125/14934 16404/17110/14919 17024/17730/15539 17031/17737/15546\r\nf 17026/17732/15541 17033/17739/15548 17031/17737/15546 17024/17730/15539\r\nf 17033/17739/15548 17034/17740/15549 17032/17738/15547 17031/17737/15546\r\nf 16414/17120/14929 16427/17133/14942 17035/17741/15550 17028/17734/15543\r\nf 16427/17133/14942 16426/17132/14941 17036/17742/15551 17035/17741/15550\r\nf 17037/17743/15552 17038/17744/15553 17035/17741/15550 17036/17742/15551\r\nf 17038/17744/15553 17029/17735/15544 17028/17734/15543 17035/17741/15550\r\nf 16428/17134/14943 16431/17137/14946 17039/17745/15554 17040/17746/15555\r\nf 16431/17137/14946 16416/17122/14931 17032/17738/15547 17039/17745/15554\r\nf 17034/17740/15549 17041/17747/15556 17039/17745/15554 17032/17738/15547\r\nf 17041/17747/15556 17042/17748/15557 17040/17746/15555 17039/17745/15554\r\nf 16426/17132/14941 16439/17145/14954 17043/17749/15558 17036/17742/15551\r\nf 16439/17145/14954 16438/17144/14953 17044/17750/15559 17043/17749/15558\r\nf 17045/17751/15560 17046/17752/15561 17043/17749/15558 17044/17750/15559\r\nf 17046/17752/15561 17037/17743/15552 17036/17742/15551 17043/17749/15558\r\nf 16440/17146/14955 16443/17149/14958 17047/17753/15562 17048/17754/15563\r\nf 16443/17149/14958 16428/17134/14943 17040/17746/15555 17047/17753/15562\r\nf 17042/17748/15557 17049/17755/15564 17047/17753/15562 17040/17746/15555\r\nf 17049/17755/15564 17050/17756/15565 17048/17754/15563 17047/17753/15562\r\nf 16438/17144/14953 16451/17157/14966 17051/17757/15566 17044/17750/15559\r\nf 16451/17157/14966 16450/17156/14965 17052/17758/15567 17051/17757/15566\r\nf 17053/17759/15568 17054/17760/15569 17051/17757/15566 17052/17758/15567\r\nf 17054/17760/15569 17045/17751/15560 17044/17750/15559 17051/17757/15566\r\nf 16452/17158/14967 16455/17161/14970 17055/17761/15570 17056/17762/15571\r\nf 16455/17161/14970 16440/17146/14955 17048/17754/15563 17055/17761/15570\r\nf 17050/17756/15565 17057/17763/15572 17055/17761/15570 17048/17754/15563\r\nf 17057/17763/15572 17058/17764/15573 17056/17762/15571 17055/17761/15570\r\nf 16450/17156/14965 16463/17169/14978 17059/17765/15574 17052/17758/15567\r\nf 16463/17169/14978 16462/17168/14977 17060/17766/15575 17059/17765/15574\r\nf 17061/17767/15576 17062/17768/15577 17059/17765/15574 17060/17766/15575\r\nf 17062/17768/15577 17053/17759/15568 17052/17758/15567 17059/17765/15574\r\nf 16464/17170/14979 16467/17173/14982 17063/17769/15578 17064/17770/15579\r\nf 16467/17173/14982 16452/17158/14967 17056/17762/15571 17063/17769/15578\r\nf 17058/17764/15573 17065/17771/15580 17063/17769/15578 17056/17762/15571\r\nf 17065/17771/15580 17066/17772/15581 17064/17770/15579 17063/17769/15578\r\nf 16462/17168/14977 16475/17181/14990 17067/17773/15582 17060/17766/15575\r\nf 16475/17181/14990 16474/17180/14989 17068/17774/15583 17067/17773/15582\r\nf 17069/17775/15584 17070/17776/15585 17067/17773/15582 17068/17774/15583\r\nf 17070/17776/15585 17061/17767/15576 17060/17766/15575 17067/17773/15582\r\nf 16476/17182/14991 16479/17185/14994 17071/17777/15586 17072/17778/15587\r\nf 16479/17185/14994 16464/17170/14979 17064/17770/15579 17071/17777/15586\r\nf 17066/17772/15581 17073/17779/15588 17071/17777/15586 17064/17770/15579\r\nf 17073/17779/15588 17074/17780/15589 17072/17778/15587 17071/17777/15586\r\nf 16474/17180/14989 16487/17193/15002 17075/17781/15590 17068/17774/15583\r\nf 16487/17193/15002 16486/17192/15001 17076/17782/15591 17075/17781/15590\r\nf 17077/17783/15592 17078/17784/15593 17075/17781/15590 17076/17782/15591\r\nf 17078/17784/15593 17069/17775/15584 17068/17774/15583 17075/17781/15590\r\nf 16488/17194/15003 16491/17197/15006 17079/17785/15594 17080/17786/15595\r\nf 16491/17197/15006 16476/17182/14991 17072/17778/15587 17079/17785/15594\r\nf 17074/17780/15589 17081/17787/15596 17079/17785/15594 17072/17778/15587\r\nf 17081/17787/15596 17082/17788/15597 17080/17786/15595 17079/17785/15594\r\nf 16486/17192/15001 16499/17205/15014 17083/17789/15598 17076/17782/15591\r\nf 16499/17205/15014 16498/17204/15013 17084/17790/15599 17083/17789/15598\r\nf 17085/17791/15600 17086/17792/15601 17083/17789/15598 17084/17790/15599\r\nf 17086/17792/15601 17077/17783/15592 17076/17782/15591 17083/17789/15598\r\nf 16500/17206/15015 16503/17209/15018 17087/17793/15602 17088/17794/15603\r\nf 16503/17209/15018 16488/17194/15003 17080/17786/15595 17087/17793/15602\r\nf 17082/17788/15597 17089/17795/15604 17087/17793/15602 17080/17786/15595\r\nf 17089/17795/15604 17090/17796/15605 17088/17794/15603 17087/17793/15602\r\nf 16498/17204/15013 16510/17216/15025 17091/17797/15606 17084/17790/15599\r\nf 16510/17216/15025 16129/16835/14644 16132/16838/14647 17091/17797/15606\r\nf 16136/16842/14651 17092/17798/15607 17091/17797/15606 16132/16838/14647\r\nf 17092/17798/15607 17085/17791/15600 17084/17790/15599 17091/17797/15606\r\nf 16511/17217/15026 16514/17220/15029 17093/17799/15608 17094/17800/15609\r\nf 16514/17220/15029 16500/17206/15015 17088/17794/15603 17093/17799/15608\r\nf 17090/17796/15605 17095/17801/15610 17093/17799/15608 17088/17794/15603\r\nf 17095/17801/15610 16349/17055/14864 17094/17800/15609 17093/17799/15608\r\nf 16519/17225/15034 16522/17228/15037 17096/17802/15611 17097/17803/15612\r\nf 16522/17228/15037 16511/17217/15026 17094/17800/15609 17096/17802/15611\r\nf 16349/17055/14864 16348/17054/14863 17096/17802/15611 17094/17800/15609\r\nf 16348/17054/14863 16347/17053/14862 17097/17803/15612 17096/17802/15611\r\nf 16531/17237/15046 16534/17240/15049 17098/17804/15613 17099/17805/15614\r\nf 16534/17240/15049 16022/16728/14537 16142/16848/14657 17098/17804/15613\r\nf 16146/16852/14661 16339/17045/14854 17098/17804/15613 16142/16848/14657\r\nf 16339/17045/14854 16333/17039/14848 17099/17805/15614 17098/17804/15613\r\nf 16541/17247/15056 16544/17250/15059 17100/17806/15615 17101/17807/15616\r\nf 16544/17250/15059 16531/17237/15046 17099/17805/15614 17100/17806/15615\r\nf 16333/17039/14848 16332/17038/14847 17100/17806/15615 17099/17805/15614\r\nf 16332/17038/14847 16331/17037/14846 17101/17807/15616 17100/17806/15615\r\nf 16158/16864/14673 16552/17258/15067 17102/17808/15617 16159/16865/14674\r\nf 16552/17258/15067 16551/17257/15066 17103/17809/15618 17102/17808/15617\r\nf 16326/17032/14841 16336/17042/14851 17102/17808/15617 17103/17809/15618\r\nf 16336/17042/14851 16160/16866/14675 16159/16865/14674 17102/17808/15617\r\nf 16553/17259/15068 16556/17262/15071 17104/17810/15619 17105/17811/15620\r\nf 16556/17262/15071 16541/17247/15056 17101/17807/15616 17104/17810/15619\r\nf 16331/17037/14846 17106/17812/15621 17104/17810/15619 17101/17807/15616\r\nf 17106/17812/15621 17107/17813/15622 17105/17811/15620 17104/17810/15619\r\nf 16551/17257/15066 16564/17270/15079 17108/17814/15623 17103/17809/15618\r\nf 16564/17270/15079 16563/17269/15078 17109/17815/15624 17108/17814/15623\r\nf 16321/17027/14836 16327/17033/14842 17108/17814/15623 17109/17815/15624\r\nf 16327/17033/14842 16326/17032/14841 17103/17809/15618 17108/17814/15623\r\nf 16565/17271/15080 16568/17274/15083 17110/17816/15625 17111/17817/15626\r\nf 16568/17274/15083 16553/17259/15068 17105/17811/15620 17110/17816/15625\r\nf 17107/17813/15622 17112/17818/15627 17110/17816/15625 17105/17811/15620\r\nf 17112/17818/15627 17113/17819/15628 17111/17817/15626 17110/17816/15625\r\nf 16563/17269/15078 16576/17282/15091 17114/17820/15629 17109/17815/15624\r\nf 16576/17282/15091 16575/17281/15090 17115/17821/15630 17114/17820/15629\r\nf 16315/17021/14830 16322/17028/14837 17114/17820/15629 17115/17821/15630\r\nf 16322/17028/14837 16321/17027/14836 17109/17815/15624 17114/17820/15629\r\nf 16577/17283/15092 16580/17286/15095 17116/17822/15631 17117/17823/15632\r\nf 16580/17286/15095 16565/17271/15080 17111/17817/15626 17116/17822/15631\r\nf 17113/17819/15628 17118/17824/15633 17116/17822/15631 17111/17817/15626\r\nf 17118/17824/15633 17119/17825/15634 17117/17823/15632 17116/17822/15631\r\nf 16575/17281/15090 16588/17294/15103 17120/17826/15635 17115/17821/15630\r\nf 16588/17294/15103 16587/17293/15102 17121/17827/15636 17120/17826/15635\r\nf 16309/17015/14824 16316/17022/14831 17120/17826/15635 17121/17827/15636\r\nf 16316/17022/14831 16315/17021/14830 17115/17821/15630 17120/17826/15635\r\nf 16589/17295/15104 16592/17298/15107 17122/17828/15637 17123/17829/15638\r\nf 16592/17298/15107 16577/17283/15092 17117/17823/15632 17122/17828/15637\r\nf 17119/17825/15634 17124/17830/15639 17122/17828/15637 17117/17823/15632\r\nf 17124/17830/15639 17125/17831/15640 17123/17829/15638 17122/17828/15637\r\nf 16587/17293/15102 16600/17306/15115 17126/17832/15641 17121/17827/15636\r\nf 16600/17306/15115 16599/17305/15114 17127/17833/15642 17126/17832/15641\r\nf 16303/17009/14818 16310/17016/14825 17126/17832/15641 17127/17833/15642\r\nf 16310/17016/14825 16309/17015/14824 17121/17827/15636 17126/17832/15641\r\nf 16601/17307/15116 16604/17310/15119 17128/17834/15643 17129/17835/15644\r\nf 16604/17310/15119 16589/17295/15104 17123/17829/15638 17128/17834/15643\r\nf 17125/17831/15640 17130/17836/15645 17128/17834/15643 17123/17829/15638\r\nf 17130/17836/15645 17131/17837/15646 17129/17835/15644 17128/17834/15643\r\nf 16599/17305/15114 16612/17318/15127 17132/17838/15647 17127/17833/15642\r\nf 16612/17318/15127 16611/17317/15126 17133/17839/15648 17132/17838/15647\r\nf 16297/17003/14812 16304/17010/14819 17132/17838/15647 17133/17839/15648\r\nf 16304/17010/14819 16303/17009/14818 17127/17833/15642 17132/17838/15647\r\nf 16613/17319/15128 16616/17322/15131 17134/17840/15649 17135/17841/15650\r\nf 16616/17322/15131 16601/17307/15116 17129/17835/15644 17134/17840/15649\r\nf 17131/17837/15646 17136/17842/15651 17134/17840/15649 17129/17835/15644\r\nf 17136/17842/15651 17137/17843/15652 17135/17841/15650 17134/17840/15649\r\nf 16611/17317/15126 16624/17330/15139 17138/17844/15653 17133/17839/15648\r\nf 16624/17330/15139 16623/17329/15138 17139/17845/15654 17138/17844/15653\r\nf 16291/16997/14806 16298/17004/14813 17138/17844/15653 17139/17845/15654\r\nf 16298/17004/14813 16297/17003/14812 17133/17839/15648 17138/17844/15653\r\nf 16625/17331/15140 16628/17334/15143 17140/17846/15655 17141/17847/15656\r\nf 16628/17334/15143 16613/17319/15128 17135/17841/15650 17140/17846/15655\r\nf 17137/17843/15652 17142/17848/15657 17140/17846/15655 17135/17841/15650\r\nf 17142/17848/15657 17143/17849/15658 17141/17847/15656 17140/17846/15655\r\nf 16623/17329/15138 16636/17342/15151 17144/17850/15659 17139/17845/15654\r\nf 16636/17342/15151 16635/17341/15150 17145/17851/15660 17144/17850/15659\r\nf 16285/16991/14800 16292/16998/14807 17144/17850/15659 17145/17851/15660\r\nf 16292/16998/14807 16291/16997/14806 17139/17845/15654 17144/17850/15659\r\nf 16637/17343/15152 16640/17346/15155 17146/17852/15661 17147/17853/15662\r\nf 16640/17346/15155 16625/17331/15140 17141/17847/15656 17146/17852/15661\r\nf 17143/17849/15658 17148/17854/15663 17146/17852/15661 17141/17847/15656\r\nf 17148/17854/15663 17149/17855/15664 17147/17853/15662 17146/17852/15661\r\nf 16635/17341/15150 16648/17354/15163 17150/17856/15665 17145/17851/15660\r\nf 16648/17354/15163 16647/17353/15162 17151/17857/15666 17150/17856/15665\r\nf 16279/16985/14794 16286/16992/14801 17150/17856/15665 17151/17857/15666\r\nf 16286/16992/14801 16285/16991/14800 17145/17851/15660 17150/17856/15665\r\nf 16649/17355/15164 16652/17358/15167 17152/17858/15667 17153/17859/15668\r\nf 16652/17358/15167 16637/17343/15152 17147/17853/15662 17152/17858/15667\r\nf 17149/17855/15664 17154/17860/15669 17152/17858/15667 17147/17853/15662\r\nf 17154/17860/15669 17155/17861/15670 17153/17859/15668 17152/17858/15667\r\nf 16647/17353/15162 16660/17366/15175 17156/17862/15671 17151/17857/15666\r\nf 16660/17366/15175 16659/17365/15174 17157/17863/15672 17156/17862/15671\r\nf 16273/16979/14788 16280/16986/14795 17156/17862/15671 17157/17863/15672\r\nf 16280/16986/14795 16279/16985/14794 17151/17857/15666 17156/17862/15671\r\nf 16661/17367/15176 16664/17370/15179 17158/17864/15673 17159/17865/15674\r\nf 16664/17370/15179 16649/17355/15164 17153/17859/15668 17158/17864/15673\r\nf 17155/17861/15670 17160/17866/15675 17158/17864/15673 17153/17859/15668\r\nf 17160/17866/15675 17161/17867/15676 17159/17865/15674 17158/17864/15673\r\nf 16659/17365/15174 16672/17378/15187 17162/17868/15677 17157/17863/15672\r\nf 16672/17378/15187 16671/17377/15186 17163/17869/15678 17162/17868/15677\r\nf 16267/16973/14782 16274/16980/14789 17162/17868/15677 17163/17869/15678\r\nf 16274/16980/14789 16273/16979/14788 17157/17863/15672 17162/17868/15677\r\nf 16673/17379/15188 16676/17382/15191 17164/17870/15679 17165/17871/15680\r\nf 16676/17382/15191 16661/17367/15176 17159/17865/15674 17164/17870/15679\r\nf 17161/17867/15676 17166/17872/15681 17164/17870/15679 17159/17865/15674\r\nf 17166/17872/15681 17167/17873/15682 17165/17871/15680 17164/17870/15679\r\nf 16671/17377/15186 16684/17390/15199 17168/17874/15683 17163/17869/15678\r\nf 16684/17390/15199 16683/17389/15198 17169/17875/15684 17168/17874/15683\r\nf 16261/16967/14776 16268/16974/14783 17168/17874/15683 17169/17875/15684\r\nf 16268/16974/14783 16267/16973/14782 17163/17869/15678 17168/17874/15683\r\nf 16685/17391/15200 16688/17394/15203 17170/17876/15685 17171/17877/15686\r\nf 16688/17394/15203 16673/17379/15188 17165/17871/15680 17170/17876/15685\r\nf 17167/17873/15682 17172/17878/15687 17170/17876/15685 17165/17871/15680\r\nf 17172/17878/15687 17173/17879/15688 17171/17877/15686 17170/17876/15685\r\nf 16683/17389/15198 16696/17402/15211 17174/17880/15689 17169/17875/15684\r\nf 16696/17402/15211 16695/17401/15210 17175/17881/15690 17174/17880/15689\r\nf 16255/16961/14770 16262/16968/14777 17174/17880/15689 17175/17881/15690\r\nf 16262/16968/14777 16261/16967/14776 17169/17875/15684 17174/17880/15689\r\nf 16697/17403/15212 16700/17406/15215 17176/17882/15691 17177/17883/15692\r\nf 16700/17406/15215 16685/17391/15200 17171/17877/15686 17176/17882/15691\r\nf 17173/17879/15688 17178/17884/15693 17176/17882/15691 17171/17877/15686\r\nf 17178/17884/15693 17179/17885/15694 17177/17883/15692 17176/17882/15691\r\nf 16695/17401/15210 16708/17414/15223 17180/17886/15695 17175/17881/15690\r\nf 16708/17414/15223 16707/17413/15222 17181/17887/15696 17180/17886/15695\r\nf 16249/16955/14764 16256/16962/14771 17180/17886/15695 17181/17887/15696\r\nf 16256/16962/14771 16255/16961/14770 17175/17881/15690 17180/17886/15695\r\nf 16709/17415/15224 16712/17418/15227 17182/17888/15697 17183/17889/15698\r\nf 16712/17418/15227 16697/17403/15212 17177/17883/15692 17182/17888/15697\r\nf 17179/17885/15694 17184/17890/15699 17182/17888/15697 17177/17883/15692\r\nf 17184/17890/15699 17185/17891/15700 17183/17889/15698 17182/17888/15697\r\nf 16707/17413/15222 16720/17426/15235 17186/17892/15701 17181/17887/15696\r\nf 16720/17426/15235 16719/17425/15234 17187/17893/15702 17186/17892/15701\r\nf 16243/16949/14758 16250/16956/14765 17186/17892/15701 17187/17893/15702\r\nf 16250/16956/14765 16249/16955/14764 17181/17887/15696 17186/17892/15701\r\nf 16721/17427/15236 16724/17430/15239 17188/17894/15703 17189/17895/15704\r\nf 16724/17430/15239 16709/17415/15224 17183/17889/15698 17188/17894/15703\r\nf 17185/17891/15700 17190/17896/15705 17188/17894/15703 17183/17889/15698\r\nf 17190/17896/15705 17191/17897/15706 17189/17895/15704 17188/17894/15703\r\nf 16719/17425/15234 16732/17438/15247 17192/17898/15707 17187/17893/15702\r\nf 16732/17438/15247 16731/17437/15246 17193/17899/15708 17192/17898/15707\r\nf 16237/16943/14752 16244/16950/14759 17192/17898/15707 17193/17899/15708\r\nf 16244/16950/14759 16243/16949/14758 17187/17893/15702 17192/17898/15707\r\nf 16733/17439/15248 16736/17442/15251 17194/17900/15709 17195/17901/15710\r\nf 16736/17442/15251 16721/17427/15236 17189/17895/15704 17194/17900/15709\r\nf 17191/17897/15706 17196/17902/15711 17194/17900/15709 17189/17895/15704\r\nf 17196/17902/15711 17197/17903/15712 17195/17901/15710 17194/17900/15709\r\nf 16731/17437/15246 16744/17450/15259 17198/17904/15713 17193/17899/15708\r\nf 16744/17450/15259 16743/17449/15258 17199/17905/15714 17198/17904/15713\r\nf 16231/16937/14746 16238/16944/14753 17198/17904/15713 17199/17905/15714\r\nf 16238/16944/14753 16237/16943/14752 17193/17899/15708 17198/17904/15713\r\nf 16027/16733/14542 16746/17452/15261 17200/17906/15715 17201/17907/15716\r\nf 16746/17452/15261 16733/17439/15248 17195/17901/15710 17200/17906/15715\r\nf 17197/17903/15712 17202/17908/15717 17200/17906/15715 17195/17901/15710\r\nf 17202/17908/15717 16220/16926/14735 17201/17907/15716 17200/17906/15715\r\nf 16743/17449/15258 16753/17459/15268 17203/17909/15718 17199/17905/15714\r\nf 16753/17459/15268 16752/17458/15267 17204/17910/15719 17203/17909/15718\r\nf 16224/16930/14739 16232/16938/14747 17203/17909/15718 17204/17910/15719\r\nf 16232/16938/14747 16231/16937/14746 17199/17905/15714 17203/17909/15718\r\nf 16031/16737/14546 16028/16734/14543 17205/17911/15720 17206/17912/15721\r\nf 16028/16734/14543 16027/16733/14542 17201/17907/15716 17205/17911/15720\r\nf 16220/16926/14735 16219/16925/14734 17205/17911/15720 17201/17907/15716\r\nf 16219/16925/14734 16218/16924/14733 17206/17912/15721 17205/17911/15720\r\nf 16752/17458/15267 16759/17465/15274 17207/17913/15722 17204/17910/15719\r\nf 16759/17465/15274 16758/17464/15273 17208/17914/15723 17207/17913/15722\r\nf 16226/16932/14741 16225/16931/14740 17207/17913/15722 17208/17914/15723\r\nf 16225/16931/14740 16224/16930/14739 17204/17910/15719 17207/17913/15722\r\nf 16760/17466/15275 16763/17469/15278 17209/17915/15724 17210/17916/15725\r\nf 16763/17469/15278 16031/16737/14546 17206/17912/15721 17209/17915/15724\r\nf 16218/16924/14733 17211/17917/15726 17209/17915/15724 17206/17912/15721\r\nf 17211/17917/15726 17212/17918/15727 17210/17916/15725 17209/17915/15724\r\nf 16758/17464/15273 16771/17477/15286 17213/17919/15728 17208/17914/15723\r\nf 16771/17477/15286 16770/17476/15285 17214/17920/15729 17213/17919/15728\r\nf 17215/17921/15730 17216/17922/15731 17213/17919/15728 17214/17920/15729\r\nf 17216/17922/15731 16226/16932/14741 17208/17914/15723 17213/17919/15728\r\nf 16051/16757/14566 16781/17487/15296 17217/17923/15732 16170/16876/14685\r\nf 16781/17487/15296 16780/17486/15295 17218/17924/15733 17217/17923/15732\r\nf 17219/17925/15734 17220/17926/15735 17217/17923/15732 17218/17924/15733\r\nf 17220/17926/15735 16171/16877/14686 16170/16876/14685 17217/17923/15732\r\nf 16782/17488/15297 16785/17491/15300 17221/17927/15736 17222/17928/15737\r\nf 16785/17491/15300 16174/16880/14689 16177/16883/14692 17221/17927/15736\r\nf 16179/16885/14694 16187/16893/14702 17221/17927/15736 16177/16883/14692\r\nf 16187/16893/14702 16186/16892/14701 17222/17928/15737 17221/17927/15736\r\nf 16780/17486/15295 16793/17499/15308 17223/17929/15738 17218/17924/15733\r\nf 16793/17499/15308 16792/17498/15307 17224/17930/15739 17223/17929/15738\r\nf 17225/17931/15740 17226/17932/15741 17223/17929/15738 17224/17930/15739\r\nf 17226/17932/15741 17219/17925/15734 17218/17924/15733 17223/17929/15738\r\nf 16794/17500/15309 16797/17503/15312 17227/17933/15742 17228/17934/15743\r\nf 16797/17503/15312 16782/17488/15297 17222/17928/15737 17227/17933/15742\r\nf 16186/16892/14701 17229/17935/15744 17227/17933/15742 17222/17928/15737\r\nf 17229/17935/15744 17230/17936/15745 17228/17934/15743 17227/17933/15742\r\nf 16792/17498/15307 16805/17511/15320 17231/17937/15746 17224/17930/15739\r\nf 16805/17511/15320 16804/17510/15319 17232/17938/15747 17231/17937/15746\r\nf 17233/17939/15748 17234/17940/15749 17231/17937/15746 17232/17938/15747\r\nf 17234/17940/15749 17225/17931/15740 17224/17930/15739 17231/17937/15746\r\nf 16806/17512/15321 16809/17515/15324 17235/17941/15750 17236/17942/15751\r\nf 16809/17515/15324 16794/17500/15309 17228/17934/15743 17235/17941/15750\r\nf 17230/17936/15745 17237/17943/15752 17235/17941/15750 17228/17934/15743\r\nf 17237/17943/15752 17238/17944/15753 17236/17942/15751 17235/17941/15750\r\nf 16804/17510/15319 16817/17523/15332 17239/17945/15754 17232/17938/15747\r\nf 16817/17523/15332 16816/17522/15331 17240/17946/15755 17239/17945/15754\r\nf 17241/17947/15756 17242/17948/15757 17239/17945/15754 17240/17946/15755\r\nf 17242/17948/15757 17233/17939/15748 17232/17938/15747 17239/17945/15754\r\nf 16054/16760/14569 16819/17525/15334 17243/17949/15758 17244/17950/15759\r\nf 16819/17525/15334 16806/17512/15321 17236/17942/15751 17243/17949/15758\r\nf 17238/17944/15753 17245/17951/15760 17243/17949/15758 17236/17942/15751\r\nf 17245/17951/15760 17246/17952/15761 17244/17950/15759 17243/17949/15758\r\nf 16816/17522/15331 16823/17529/15338 17247/17953/15762 17240/17946/15755\r\nf 16823/17529/15338 16071/16777/14586 17248/17954/15763 17247/17953/15762\r\nf 17249/17955/15764 17250/17956/15765 17247/17953/15762 17248/17954/15763\r\nf 17250/17956/15765 17241/17947/15756 17240/17946/15755 17247/17953/15762\r\nf 16058/16764/14573 16055/16761/14570 17251/17957/15766 17252/17958/15767\r\nf 16055/16761/14570 16054/16760/14569 17244/17950/15759 17251/17957/15766\r\nf 17246/17952/15761 17253/17959/15768 17251/17957/15766 17244/17950/15759\r\nf 17253/17959/15768 17254/17960/15769 17252/17958/15767 17251/17957/15766\r\nf 16071/16777/14586 16070/16776/14585 17255/17961/15770 17248/17954/15763\r\nf 16070/16776/14585 16069/16775/14584 17256/17962/15771 17255/17961/15770\r\nf 17257/17963/15772 17258/17964/15773 17255/17961/15770 17256/17962/15771\r\nf 17258/17964/15773 17249/17955/15764 17248/17954/15763 17255/17961/15770\r\nf 16370/17076/14885 16825/17531/15340 17259/17965/15774 16997/17703/15512\r\nf 16825/17531/15340 16058/16764/14573 17252/17958/15767 17259/17965/15774\r\nf 17254/17960/15769 17260/17966/15775 17259/17965/15774 17252/17958/15767\r\nf 17260/17966/15775 16998/17704/15513 16997/17703/15512 17259/17965/15774\r\nf 16069/16775/14584 16829/17535/15344 17261/17967/15776 17256/17962/15771\r\nf 16829/17535/15344 16377/17083/14892 17002/17708/15517 17261/17967/15776\r\nf 17006/17712/15521 17262/17968/15777 17261/17967/15776 17002/17708/15517\r\nf 17262/17968/15777 17257/17963/15772 17256/17962/15771 17261/17967/15776\r\nf 16072/16778/14587 16831/17537/15346 17263/17969/15778 17264/17970/15779\r\nf 16831/17537/15346 16835/17541/15350 17265/17971/15780 17263/17969/15778\r\nf 17000/17706/15515 16999/17705/15514 17263/17969/15778 17265/17971/15780\r\nf 16999/17705/15514 16998/17704/15513 17264/17970/15779 17263/17969/15778\r\nf 16833/17539/15348 16832/17538/15347 17266/17972/15781 17267/17973/15782\r\nf 16832/17538/15347 16080/16786/14595 17268/17974/15783 17266/17972/15781\r\nf 17269/17975/15784 17270/17976/15785 17266/17972/15781 17268/17974/15783\r\nf 17270/17976/15785 17271/17977/15786 17267/17973/15782 17266/17972/15781\r\nf 16076/16782/14591 16073/16779/14588 17272/17978/15787 17273/17979/15788\r\nf 16073/16779/14588 16072/16778/14587 17264/17970/15779 17272/17978/15787\r\nf 16998/17704/15513 17260/17966/15775 17272/17978/15787 17264/17970/15779\r\nf 17260/17966/15775 17254/17960/15769 17273/17979/15788 17272/17978/15787\r\nf 16080/16786/14595 16079/16785/14594 17274/17980/15789 17268/17974/15783\r\nf 16079/16785/14594 16078/16784/14593 17275/17981/15790 17274/17980/15789\r\nf 17276/17982/15791 17277/17983/15792 17274/17980/15789 17275/17981/15790\r\nf 17277/17983/15792 17269/17975/15784 17268/17974/15783 17274/17980/15789\r\nf 16836/17542/15351 16839/17545/15354 17278/17984/15793 17279/17985/15794\r\nf 16839/17545/15354 16076/16782/14591 17273/17979/15788 17278/17984/15793\r\nf 17254/17960/15769 17253/17959/15768 17278/17984/15793 17273/17979/15788\r\nf 17253/17959/15768 17246/17952/15761 17279/17985/15794 17278/17984/15793\r\nf 16078/16784/14593 16841/17547/15356 17280/17986/15795 17275/17981/15790\r\nf 16841/17547/15356 16840/17546/15355 17281/17987/15796 17280/17986/15795\r\nf 17282/17988/15797 17283/17989/15798 17280/17986/15795 17281/17987/15796\r\nf 17283/17989/15798 17276/17982/15791 17275/17981/15790 17280/17986/15795\r\nf 16842/17548/15357 16845/17551/15360 17284/17990/15799 17285/17991/15800\r\nf 16845/17551/15360 16836/17542/15351 17279/17985/15794 17284/17990/15799\r\nf 17246/17952/15761 17245/17951/15760 17284/17990/15799 17279/17985/15794\r\nf 17245/17951/15760 17238/17944/15753 17285/17991/15800 17284/17990/15799\r\nf 16840/17546/15355 16847/17553/15362 17286/17992/15801 17281/17987/15796\r\nf 16847/17553/15362 16846/17552/15361 17287/17993/15802 17286/17992/15801\r\nf 17288/17994/15803 17289/17995/15804 17286/17992/15801 17287/17993/15802\r\nf 17289/17995/15804 17282/17988/15797 17281/17987/15796 17286/17992/15801\r\nf 16848/17554/15363 16851/17557/15366 17290/17996/15805 17291/17997/15806\r\nf 16851/17557/15366 16842/17548/15357 17285/17991/15800 17290/17996/15805\r\nf 17238/17944/15753 17237/17943/15752 17290/17996/15805 17285/17991/15800\r\nf 17237/17943/15752 17230/17936/15745 17291/17997/15806 17290/17996/15805\r\nf 16846/17552/15361 16853/17559/15368 17292/17998/15807 17287/17993/15802\r\nf 16853/17559/15368 16852/17558/15367 17293/17999/15808 17292/17998/15807\r\nf 17294/18000/15809 17295/18001/15810 17292/17998/15807 17293/17999/15808\r\nf 17295/18001/15810 17288/17994/15803 17287/17993/15802 17292/17998/15807\r\nf 16184/16890/14699 16856/17562/15371 17296/18002/15811 16185/16891/14700\r\nf 16856/17562/15371 16848/17554/15363 17291/17997/15806 17296/18002/15811\r\nf 17230/17936/15745 17229/17935/15744 17296/18002/15811 17291/17997/15806\r\nf 17229/17935/15744 16186/16892/14701 16185/16891/14700 17296/18002/15811\r\nf 16852/17558/15367 16857/17563/15372 17297/18003/15812 17293/17999/15808\r\nf 16857/17563/15372 16188/16894/14703 16191/16897/14706 17297/18003/15812\r\nf 16196/16902/14711 17298/18004/15813 17297/18003/15812 16191/16897/14706\r\nf 17298/18004/15813 17294/18000/15809 17293/17999/15808 17297/18003/15812\r\nf 16207/16913/14722 16862/17568/15377 17299/18005/15814 16208/16914/14723\r\nf 16862/17568/15377 16180/16886/14695 16183/16889/14698 17299/18005/15814\r\nf 16179/16885/14694 16178/16884/14693 17299/18005/15814 16183/16889/14698\r\nf 16178/16884/14693 16167/16873/14682 16208/16914/14723 17299/18005/15814\r\nf 16081/16787/14596 16866/17572/15381 17300/18006/15815 16217/16923/14732\r\nf 16866/17572/15381 16870/17576/15385 17301/18007/15816 17300/18006/15815\r\nf 17212/17918/15727 17211/17917/15726 17300/18006/15815 17301/18007/15816\r\nf 17211/17917/15726 16218/16924/14733 16217/16923/14732 17300/18006/15815\r\nf 16868/17574/15383 16867/17573/15382 17302/18008/15817 17303/18009/15818\r\nf 16867/17573/15382 16089/16795/14604 16222/16928/14737 17302/18008/15817\r\nf 16226/16932/14741 17216/17922/15731 17302/18008/15817 16222/16928/14737\r\nf 17216/17922/15731 17215/17921/15730 17303/18009/15818 17302/18008/15817\r\nf 16871/17577/15386 16874/17580/15389 17304/18010/15819 17305/18011/15820\r\nf 16874/17580/15389 16085/16791/14600 16216/16922/14731 17304/18010/15819\r\nf 16220/16926/14735 17202/17908/15717 17304/18010/15819 16216/16922/14731\r\nf 17202/17908/15717 17197/17903/15712 17305/18011/15820 17304/18010/15819\r\nf 16875/17581/15390 16878/17584/15393 17306/18012/15821 17307/18013/15822\r\nf 16878/17584/15393 16871/17577/15386 17305/18011/15820 17306/18012/15821\r\nf 17197/17903/15712 17196/17902/15711 17306/18012/15821 17305/18011/15820\r\nf 17196/17902/15711 17191/17897/15706 17307/18013/15822 17306/18012/15821\r\nf 16879/17585/15394 16882/17588/15397 17308/18014/15823 17309/18015/15824\r\nf 16882/17588/15397 16875/17581/15390 17307/18013/15822 17308/18014/15823\r\nf 17191/17897/15706 17190/17896/15705 17308/18014/15823 17307/18013/15822\r\nf 17190/17896/15705 17185/17891/15700 17309/18015/15824 17308/18014/15823\r\nf 16883/17589/15398 16886/17592/15401 17310/18016/15825 17311/18017/15826\r\nf 16886/17592/15401 16879/17585/15394 17309/18015/15824 17310/18016/15825\r\nf 17185/17891/15700 17184/17890/15699 17310/18016/15825 17309/18015/15824\r\nf 17184/17890/15699 17179/17885/15694 17311/18017/15826 17310/18016/15825\r\nf 16887/17593/15402 16890/17596/15405 17312/18018/15827 17313/18019/15828\r\nf 16890/17596/15405 16883/17589/15398 17311/18017/15826 17312/18018/15827\r\nf 17179/17885/15694 17178/17884/15693 17312/18018/15827 17311/18017/15826\r\nf 17178/17884/15693 17173/17879/15688 17313/18019/15828 17312/18018/15827\r\nf 16891/17597/15406 16894/17600/15409 17314/18020/15829 17315/18021/15830\r\nf 16894/17600/15409 16887/17593/15402 17313/18019/15828 17314/18020/15829\r\nf 17173/17879/15688 17172/17878/15687 17314/18020/15829 17313/18019/15828\r\nf 17172/17878/15687 17167/17873/15682 17315/18021/15830 17314/18020/15829\r\nf 16895/17601/15410 16898/17604/15413 17316/18022/15831 17317/18023/15832\r\nf 16898/17604/15413 16891/17597/15406 17315/18021/15830 17316/18022/15831\r\nf 17167/17873/15682 17166/17872/15681 17316/18022/15831 17315/18021/15830\r\nf 17166/17872/15681 17161/17867/15676 17317/18023/15832 17316/18022/15831\r\nf 16899/17605/15414 16902/17608/15417 17318/18024/15833 17319/18025/15834\r\nf 16902/17608/15417 16895/17601/15410 17317/18023/15832 17318/18024/15833\r\nf 17161/17867/15676 17160/17866/15675 17318/18024/15833 17317/18023/15832\r\nf 17160/17866/15675 17155/17861/15670 17319/18025/15834 17318/18024/15833\r\nf 16903/17609/15418 16906/17612/15421 17320/18026/15835 17321/18027/15836\r\nf 16906/17612/15421 16899/17605/15414 17319/18025/15834 17320/18026/15835\r\nf 17155/17861/15670 17154/17860/15669 17320/18026/15835 17319/18025/15834\r\nf 17154/17860/15669 17149/17855/15664 17321/18027/15836 17320/18026/15835\r\nf 16907/17613/15422 16910/17616/15425 17322/18028/15837 17323/18029/15838\r\nf 16910/17616/15425 16903/17609/15418 17321/18027/15836 17322/18028/15837\r\nf 17149/17855/15664 17148/17854/15663 17322/18028/15837 17321/18027/15836\r\nf 17148/17854/15663 17143/17849/15658 17323/18029/15838 17322/18028/15837\r\nf 16911/17617/15426 16914/17620/15429 17324/18030/15839 17325/18031/15840\r\nf 16914/17620/15429 16907/17613/15422 17323/18029/15838 17324/18030/15839\r\nf 17143/17849/15658 17142/17848/15657 17324/18030/15839 17323/18029/15838\r\nf 17142/17848/15657 17137/17843/15652 17325/18031/15840 17324/18030/15839\r\nf 16915/17621/15430 16918/17624/15433 17326/18032/15841 17327/18033/15842\r\nf 16918/17624/15433 16911/17617/15426 17325/18031/15840 17326/18032/15841\r\nf 17137/17843/15652 17136/17842/15651 17326/18032/15841 17325/18031/15840\r\nf 17136/17842/15651 17131/17837/15646 17327/18033/15842 17326/18032/15841\r\nf 16919/17625/15434 16922/17628/15437 17328/18034/15843 17329/18035/15844\r\nf 16922/17628/15437 16915/17621/15430 17327/18033/15842 17328/18034/15843\r\nf 17131/17837/15646 17130/17836/15645 17328/18034/15843 17327/18033/15842\r\nf 17130/17836/15645 17125/17831/15640 17329/18035/15844 17328/18034/15843\r\nf 16923/17629/15438 16926/17632/15441 17330/18036/15845 17331/18037/15846\r\nf 16926/17632/15441 16919/17625/15434 17329/18035/15844 17330/18036/15845\r\nf 17125/17831/15640 17124/17830/15639 17330/18036/15845 17329/18035/15844\r\nf 17124/17830/15639 17119/17825/15634 17331/18037/15846 17330/18036/15845\r\nf 16927/17633/15442 16930/17636/15445 17332/18038/15847 17333/18039/15848\r\nf 16930/17636/15445 16923/17629/15438 17331/18037/15846 17332/18038/15847\r\nf 17119/17825/15634 17118/17824/15633 17332/18038/15847 17331/18037/15846\r\nf 17118/17824/15633 17113/17819/15628 17333/18039/15848 17332/18038/15847\r\nf 16931/17637/15446 16934/17640/15449 17334/18040/15849 17335/18041/15850\r\nf 16934/17640/15449 16927/17633/15442 17333/18039/15848 17334/18040/15849\r\nf 17113/17819/15628 17112/17818/15627 17334/18040/15849 17333/18039/15848\r\nf 17112/17818/15627 17107/17813/15622 17335/18041/15850 17334/18040/15849\r\nf 16090/16796/14605 16936/17642/15451 17336/18042/15851 16330/17036/14845\r\nf 16936/17642/15451 16931/17637/15446 17335/18041/15850 17336/18042/15851\r\nf 17107/17813/15622 17106/17812/15621 17336/18042/15851 17335/18041/15850\r\nf 17106/17812/15621 16331/17037/14846 16330/17036/14845 17336/18042/15851\r\nf 16107/16813/14622 16105/16811/14620 17337/18043/15852 17338/18044/15853\r\nf 16105/16811/14620 16101/16807/14616 16338/17044/14853 17337/18043/15852\r\nf 16146/16852/14661 16145/16851/14660 17337/18043/15852 16338/17044/14853\r\nf 16145/16851/14660 16144/16850/14659 17338/18044/15853 17337/18043/15852\r\nf 16937/17643/15452 16940/17646/15455 17339/18045/15854 17340/18046/15855\r\nf 16940/17646/15455 16115/16821/14630 16345/17051/14860 17339/18045/15854\r\nf 16349/17055/14864 17095/17801/15610 17339/18045/15854 16345/17051/14860\r\nf 17095/17801/15610 17090/17796/15605 17340/18046/15855 17339/18045/15854\r\nf 16941/17647/15456 16944/17650/15459 17341/18047/15856 17342/18048/15857\r\nf 16944/17650/15459 16937/17643/15452 17340/18046/15855 17341/18047/15856\r\nf 17090/17796/15605 17089/17795/15604 17341/18047/15856 17340/18046/15855\r\nf 17089/17795/15604 17082/17788/15597 17342/18048/15857 17341/18047/15856\r\nf 16358/17064/14873 16946/17652/15461 17343/18049/15858 16359/17065/14874\r\nf 16946/17652/15461 16945/17651/15460 17344/18050/15859 17343/18049/15858\r\nf 17345/18051/15860 17346/18052/15861 17343/18049/15858 17344/18050/15859\r\nf 17346/18052/15861 16360/17066/14875 16359/17065/14874 17343/18049/15858\r\nf 16947/17653/15462 16950/17656/15465 17347/18053/15862 17348/18054/15863\r\nf 16950/17656/15465 16941/17647/15456 17342/18048/15857 17347/18053/15862\r\nf 17082/17788/15597 17081/17787/15596 17347/18053/15862 17342/18048/15857\r\nf 17081/17787/15596 17074/17780/15589 17348/18054/15863 17347/18053/15862\r\nf 16945/17651/15460 16952/17658/15467 17349/18055/15864 17344/18050/15859\r\nf 16952/17658/15467 16951/17657/15466 17350/18056/15865 17349/18055/15864\r\nf 17351/18057/15866 17352/18058/15867 17349/18055/15864 17350/18056/15865\r\nf 17352/18058/15867 17345/18051/15860 17344/18050/15859 17349/18055/15864\r\nf 16120/16826/14635 16954/17660/15469 17353/18059/15868 17354/18060/15869\r\nf 16954/17660/15469 16947/17653/15462 17348/18054/15863 17353/18059/15868\r\nf 17074/17780/15589 17073/17779/15588 17353/18059/15868 17348/18054/15863\r\nf 17073/17779/15588 17066/17772/15581 17354/18060/15869 17353/18059/15868\r\nf 16951/17657/15466 16955/17661/15470 17355/18061/15870 17350/18056/15865\r\nf 16955/17661/15470 16128/16834/14643 17356/18062/15871 17355/18061/15870\r\nf 17357/18063/15872 17358/18064/15873 17355/18061/15870 17356/18062/15871\r\nf 17358/18064/15873 17351/18057/15866 17350/18056/15865 17355/18061/15870\r\nf 16124/16830/14639 16121/16827/14636 17359/18065/15874 17360/18066/15875\r\nf 16121/16827/14636 16120/16826/14635 17354/18060/15869 17359/18065/15874\r\nf 17066/17772/15581 17065/17771/15580 17359/18065/15874 17354/18060/15869\r\nf 17065/17771/15580 17058/17764/15573 17360/18066/15875 17359/18065/15874\r\nf 16128/16834/14643 16127/16833/14642 17361/18067/15876 17356/18062/15871\r\nf 16127/16833/14642 16126/16832/14641 17362/18068/15877 17361/18067/15876\r\nf 17363/18069/15878 17364/18070/15879 17361/18067/15876 17362/18068/15877\r\nf 17364/18070/15879 17357/18063/15872 17356/18062/15871 17361/18067/15876\r\nf 16956/17662/15471 16959/17665/15474 17365/18071/15880 17366/18072/15881\r\nf 16959/17665/15474 16124/16830/14639 17360/18066/15875 17365/18071/15880\r\nf 17058/17764/15573 17057/17763/15572 17365/18071/15880 17360/18066/15875\r\nf 17057/17763/15572 17050/17756/15565 17366/18072/15881 17365/18071/15880\r\nf 16126/16832/14641 16961/17667/15476 17367/18073/15882 17362/18068/15877\r\nf 16961/17667/15476 16960/17666/15475 17368/18074/15883 17367/18073/15882\r\nf 17369/18075/15884 17370/18076/15885 17367/18073/15882 17368/18074/15883\r\nf 17370/18076/15885 17363/18069/15878 17362/18068/15877 17367/18073/15882\r\nf 16962/17668/15477 16965/17671/15480 17371/18077/15886 17372/18078/15887\r\nf 16965/17671/15480 16956/17662/15471 17366/18072/15881 17371/18077/15886\r\nf 17050/17756/15565 17049/17755/15564 17371/18077/15886 17366/18072/15881\r\nf 17049/17755/15564 17042/17748/15557 17372/18078/15887 17371/18077/15886\r\nf 16960/17666/15475 16967/17673/15482 17373/18079/15888 17368/18074/15883\r\nf 16967/17673/15482 16966/17672/15481 17374/18080/15889 17373/18079/15888\r\nf 17375/18081/15890 17376/18082/15891 17373/18079/15888 17374/18080/15889\r\nf 17376/18082/15891 17369/18075/15884 17368/18074/15883 17373/18079/15888\r\nf 16968/17674/15483 16971/17677/15486 17377/18083/15892 17378/18084/15893\r\nf 16971/17677/15486 16962/17668/15477 17372/18078/15887 17377/18083/15892\r\nf 17042/17748/15557 17041/17747/15556 17377/18083/15892 17372/18078/15887\r\nf 17041/17747/15556 17034/17740/15549 17378/18084/15893 17377/18083/15892\r\nf 16966/17672/15481 16973/17679/15488 17379/18085/15894 17374/18080/15889\r\nf 16973/17679/15488 16972/17678/15487 17380/18086/15895 17379/18085/15894\r\nf 17381/18087/15896 17382/18088/15897 17379/18085/15894 17380/18086/15895\r\nf 17382/18088/15897 17375/18081/15890 17374/18080/15889 17379/18085/15894\r\nf 16974/17680/15489 16977/17683/15492 17383/18089/15898 17384/18090/15899\r\nf 16977/17683/15492 16968/17674/15483 17378/18084/15893 17383/18089/15898\r\nf 17034/17740/15549 17033/17739/15548 17383/18089/15898 17378/18084/15893\r\nf 17033/17739/15548 17026/17732/15541 17384/18090/15899 17383/18089/15898\r\nf 16972/17678/15487 16979/17685/15494 17385/18091/15900 17380/18086/15895\r\nf 16979/17685/15494 16978/17684/15493 17386/18092/15901 17385/18091/15900\r\nf 17387/18093/15902 17388/18094/15903 17385/18091/15900 17386/18092/15901\r\nf 17388/18094/15903 17381/18087/15896 17380/18086/15895 17385/18091/15900\r\nf 16980/17686/15495 16983/17689/15498 17389/18095/15904 17390/18096/15905\r\nf 16983/17689/15498 16974/17680/15489 17384/18090/15899 17389/18095/15904\r\nf 17026/17732/15541 17025/17731/15540 17389/18095/15904 17384/18090/15899\r\nf 17025/17731/15540 17018/17724/15533 17390/18096/15905 17389/18095/15904\r\nf 16978/17684/15493 16985/17691/15500 17391/18097/15906 17386/18092/15901\r\nf 16985/17691/15500 16984/17690/15499 17392/18098/15907 17391/18097/15906\r\nf 17393/18099/15908 17394/18100/15909 17391/18097/15906 17392/18098/15907\r\nf 17394/18100/15909 17387/18093/15902 17386/18092/15901 17391/18097/15906\r\nf 16986/17692/15501 16989/17695/15504 17395/18101/15910 17396/18102/15911\r\nf 16989/17695/15504 16980/17686/15495 17390/18096/15905 17395/18101/15910\r\nf 17018/17724/15533 17017/17723/15532 17395/18101/15910 17390/18096/15905\r\nf 17017/17723/15532 17010/17716/15525 17396/18102/15911 17395/18101/15910\r\nf 16984/17690/15499 16991/17697/15506 17397/18103/15912 17392/18098/15907\r\nf 16991/17697/15506 16990/17696/15505 17398/18104/15913 17397/18103/15912\r\nf 17399/18105/15914 17400/18106/15915 17397/18103/15912 17398/18104/15913\r\nf 17400/18106/15915 17393/18099/15908 17392/18098/15907 17397/18103/15912\r\nf 16835/17541/15350 16993/17699/15508 17401/18107/15916 17265/17971/15780\r\nf 16993/17699/15508 16986/17692/15501 17396/18102/15911 17401/18107/15916\r\nf 17010/17716/15525 17009/17715/15524 17401/18107/15916 17396/18102/15911\r\nf 17009/17715/15524 17000/17706/15515 17265/17971/15780 17401/18107/15916\r\nf 16990/17696/15505 16994/17700/15509 17402/18108/15917 17398/18104/15913\r\nf 16994/17700/15509 16833/17539/15348 17267/17973/15782 17402/18108/15917\r\nf 17271/17977/15786 17403/18109/15918 17402/18108/15917 17267/17973/15782\r\nf 17403/18109/15918 17399/18105/15914 17398/18104/15913 17402/18108/15917\r\nf 17404/18110/15919 17405/18111/15920 17406/18112/15921 17407/18113/15922\r\nf 17405/18111/15920 17408/18114/15923 17409/18115/15924 17406/18112/15921\r\nf 16371/17077/14886 16374/17080/14889 17406/18112/15921 17409/18115/15924\r\nf 16374/17080/14889 16379/17085/14894 17407/18113/15922 17406/18112/15921\r\nf 17408/18114/15923 17410/18116/15925 17411/18117/15926 17409/18115/15924\r\nf 17410/18116/15925 17412/18118/15927 17413/18119/15928 17411/18117/15926\r\nf 16386/17092/14901 16389/17095/14904 17411/18117/15926 17413/18119/15928\r\nf 16389/17095/14904 16371/17077/14886 17409/18115/15924 17411/18117/15926\r\nf 17412/18118/15927 17414/18120/15929 17415/18121/15930 17413/18119/15928\r\nf 17414/18120/15929 17416/18122/15931 17417/18123/15932 17415/18121/15930\r\nf 16398/17104/14913 16401/17107/14916 17415/18121/15930 17417/18123/15932\r\nf 16401/17107/14916 16386/17092/14901 17413/18119/15928 17415/18121/15930\r\nf 17416/18122/15931 17418/18124/15933 17419/18125/15934 17417/18123/15932\r\nf 17418/18124/15933 17420/18126/15935 17421/18127/15936 17419/18125/15934\r\nf 16410/17116/14925 16413/17119/14928 17419/18125/15934 17421/18127/15936\r\nf 16413/17119/14928 16398/17104/14913 17417/18123/15932 17419/18125/15934\r\nf 17420/18126/15935 17422/18128/15937 17423/18129/15938 17421/18127/15936\r\nf 17422/18128/15937 17424/18130/15939 17425/18131/15940 17423/18129/15938\r\nf 16422/17128/14937 16425/17131/14940 17423/18129/15938 17425/18131/15940\r\nf 16425/17131/14940 16410/17116/14925 17421/18127/15936 17423/18129/15938\r\nf 17424/18130/15939 17426/18132/15941 17427/18133/15942 17425/18131/15940\r\nf 17426/18132/15941 17428/18134/15943 17429/18135/15944 17427/18133/15942\r\nf 16434/17140/14949 16437/17143/14952 17427/18133/15942 17429/18135/15944\r\nf 16437/17143/14952 16422/17128/14937 17425/18131/15940 17427/18133/15942\r\nf 17428/18134/15943 17430/18136/15945 17431/18137/15946 17429/18135/15944\r\nf 17430/18136/15945 17432/18138/15947 17433/18139/15948 17431/18137/15946\r\nf 16446/17152/14961 16449/17155/14964 17431/18137/15946 17433/18139/15948\r\nf 16449/17155/14964 16434/17140/14949 17429/18135/15944 17431/18137/15946\r\nf 17432/18138/15947 17434/18140/15949 17435/18141/15950 17433/18139/15948\r\nf 17434/18140/15949 17436/18142/15951 17437/18143/15952 17435/18141/15950\r\nf 16458/17164/14973 16461/17167/14976 17435/18141/15950 17437/18143/15952\r\nf 16461/17167/14976 16446/17152/14961 17433/18139/15948 17435/18141/15950\r\nf 17436/18142/15951 17438/18144/15953 17439/18145/15954 17437/18143/15952\r\nf 17438/18144/15953 17440/18146/15955 17441/18147/15956 17439/18145/15954\r\nf 16470/17176/14985 16473/17179/14988 17439/18145/15954 17441/18147/15956\r\nf 16473/17179/14988 16458/17164/14973 17437/18143/15952 17439/18145/15954\r\nf 17440/18146/15955 17442/18148/15957 17443/18149/15958 17441/18147/15956\r\nf 17442/18148/15957 17444/18150/15959 17445/18151/15960 17443/18149/15958\r\nf 16482/17188/14997 16485/17191/15000 17443/18149/15958 17445/18151/15960\r\nf 16485/17191/15000 16470/17176/14985 17441/18147/15956 17443/18149/15958\r\nf 17444/18150/15959 17446/18152/15961 17447/18153/15962 17445/18151/15960\r\nf 17446/18152/15961 17448/18154/15963 17449/18155/15964 17447/18153/15962\r\nf 16494/17200/15009 16497/17203/15012 17447/18153/15962 17449/18155/15964\r\nf 16497/17203/15012 16482/17188/14997 17445/18151/15960 17447/18153/15962\r\nf 17448/18154/15963 17450/18156/15965 17451/18157/15966 17449/18155/15964\r\nf 17450/18156/15965 17452/18158/15967 17453/18159/15968 17451/18157/15966\r\nf 16506/17212/15021 16509/17215/15024 17451/18157/15966 17453/18159/15968\r\nf 16509/17215/15024 16494/17200/15009 17449/18155/15964 17451/18157/15966\r\nf 17452/18158/15967 17454/18160/15969 17455/18161/15970 17453/18159/15968\r\nf 17454/18160/15969 17456/18162/15971 17457/18163/15972 17455/18161/15970\r\nf 16009/16715/14524 16518/17224/15033 17455/18161/15970 17457/18163/15972\r\nf 16518/17224/15033 16506/17212/15021 17453/18159/15968 17455/18161/15970\r\nf 17456/18162/15971 17458/18164/15973 17459/18165/15974 17457/18163/15972\r\nf 17458/18164/15973 17460/18166/15975 17461/18167/15976 17459/18165/15974\r\nf 16013/16719/14528 16010/16716/14525 17459/18165/15974 17461/18167/15976\r\nf 16010/16716/14525 16009/16715/14524 17457/18163/15972 17459/18165/15974\r\nf 17460/18166/15975 17462/18168/15977 17463/18169/15978 17461/18167/15976\r\nf 17462/18168/15977 17464/18170/15979 17465/18171/15980 17463/18169/15978\r\nf 16015/16721/14530 16014/16720/14529 17463/18169/15978 17465/18171/15980\r\nf 16014/16720/14529 16013/16719/14528 17461/18167/15976 17463/18169/15978\r\nf 17464/18170/15979 17466/18172/15981 17467/18173/15982 17465/18171/15980\r\nf 17466/18172/15981 16355/17061/14870 17468/18174/15983 17467/18173/15982\r\nf 16139/16845/14654 16138/16844/14653 17467/18173/15982 17468/18174/15983\r\nf 16138/16844/14653 16015/16721/14530 17465/18171/15980 17467/18173/15982\r\nf 16355/17061/14870 16354/17060/14869 17469/18175/15984 17468/18174/15983\r\nf 16354/17060/14869 16353/17059/14868 17470/18176/15985 17469/18175/15984\r\nf 16134/16840/14649 16140/16846/14655 17469/18175/15984 17470/18176/15985\r\nf 16140/16846/14655 16139/16845/14654 17468/18174/15983 17469/18175/15984\r\nf 16353/17059/14868 16361/17067/14876 17471/18177/15986 17470/18176/15985\r\nf 16361/17067/14876 16360/17066/14875 17472/18178/15987 17471/18177/15986\r\nf 16136/16842/14651 16135/16841/14650 17471/18177/15986 17472/18178/15987\r\nf 16135/16841/14650 16134/16840/14649 17470/18176/15985 17471/18177/15986\r\nf 16360/17066/14875 17346/18052/15861 17473/18179/15988 17472/18178/15987\r\nf 17346/18052/15861 17345/18051/15860 17474/18180/15989 17473/18179/15988\r\nf 17085/17791/15600 17092/17798/15607 17473/18179/15988 17474/18180/15989\r\nf 17092/17798/15607 16136/16842/14651 17472/18178/15987 17473/18179/15988\r\nf 17345/18051/15860 17352/18058/15867 17475/18181/15990 17474/18180/15989\r\nf 17352/18058/15867 17351/18057/15866 17476/18182/15991 17475/18181/15990\r\nf 17077/17783/15592 17086/17792/15601 17475/18181/15990 17476/18182/15991\r\nf 17086/17792/15601 17085/17791/15600 17474/18180/15989 17475/18181/15990\r\nf 17351/18057/15866 17358/18064/15873 17477/18183/15992 17476/18182/15991\r\nf 17358/18064/15873 17357/18063/15872 17478/18184/15993 17477/18183/15992\r\nf 17069/17775/15584 17078/17784/15593 17477/18183/15992 17478/18184/15993\r\nf 17078/17784/15593 17077/17783/15592 17476/18182/15991 17477/18183/15992\r\nf 17357/18063/15872 17364/18070/15879 17479/18185/15994 17478/18184/15993\r\nf 17364/18070/15879 17363/18069/15878 17480/18186/15995 17479/18185/15994\r\nf 17061/17767/15576 17070/17776/15585 17479/18185/15994 17480/18186/15995\r\nf 17070/17776/15585 17069/17775/15584 17478/18184/15993 17479/18185/15994\r\nf 17363/18069/15878 17370/18076/15885 17481/18187/15996 17480/18186/15995\r\nf 17370/18076/15885 17369/18075/15884 17482/18188/15997 17481/18187/15996\r\nf 17053/17759/15568 17062/17768/15577 17481/18187/15996 17482/18188/15997\r\nf 17062/17768/15577 17061/17767/15576 17480/18186/15995 17481/18187/15996\r\nf 17369/18075/15884 17376/18082/15891 17483/18189/15998 17482/18188/15997\r\nf 17376/18082/15891 17375/18081/15890 17484/18190/15999 17483/18189/15998\r\nf 17045/17751/15560 17054/17760/15569 17483/18189/15998 17484/18190/15999\r\nf 17054/17760/15569 17053/17759/15568 17482/18188/15997 17483/18189/15998\r\nf 17375/18081/15890 17382/18088/15897 17485/18191/16000 17484/18190/15999\r\nf 17382/18088/15897 17381/18087/15896 17486/18192/16001 17485/18191/16000\r\nf 17037/17743/15552 17046/17752/15561 17485/18191/16000 17486/18192/16001\r\nf 17046/17752/15561 17045/17751/15560 17484/18190/15999 17485/18191/16000\r\nf 17381/18087/15896 17388/18094/15903 17487/18193/16002 17486/18192/16001\r\nf 17388/18094/15903 17387/18093/15902 17488/18194/16003 17487/18193/16002\r\nf 17029/17735/15544 17038/17744/15553 17487/18193/16002 17488/18194/16003\r\nf 17038/17744/15553 17037/17743/15552 17486/18192/16001 17487/18193/16002\r\nf 17387/18093/15902 17394/18100/15909 17489/18195/16004 17488/18194/16003\r\nf 17394/18100/15909 17393/18099/15908 17490/18196/16005 17489/18195/16004\r\nf 17021/17727/15536 17030/17736/15545 17489/18195/16004 17490/18196/16005\r\nf 17030/17736/15545 17029/17735/15544 17488/18194/16003 17489/18195/16004\r\nf 17393/18099/15908 17400/18106/15915 17491/18197/16006 17490/18196/16005\r\nf 17400/18106/15915 17399/18105/15914 17492/18198/16007 17491/18197/16006\r\nf 17013/17719/15528 17022/17728/15537 17491/18197/16006 17492/18198/16007\r\nf 17022/17728/15537 17021/17727/15536 17490/18196/16005 17491/18197/16006\r\nf 17399/18105/15914 17403/18109/15918 17493/18199/16008 17492/18198/16007\r\nf 17403/18109/15918 17271/17977/15786 17494/18200/16009 17493/18199/16008\r\nf 17004/17710/15519 17014/17720/15529 17493/18199/16008 17494/18200/16009\r\nf 17014/17720/15529 17013/17719/15528 17492/18198/16007 17493/18199/16008\r\nf 17271/17977/15786 17270/17976/15785 17495/18201/16010 17494/18200/16009\r\nf 17270/17976/15785 17269/17975/15784 17496/18202/16011 17495/18201/16010\r\nf 17006/17712/15521 17005/17711/15520 17495/18201/16010 17496/18202/16011\r\nf 17005/17711/15520 17004/17710/15519 17494/18200/16009 17495/18201/16010\r\nf 17269/17975/15784 17277/17983/15792 17497/18203/16012 17496/18202/16011\r\nf 17277/17983/15792 17276/17982/15791 17498/18204/16013 17497/18203/16012\r\nf 17257/17963/15772 17262/17968/15777 17497/18203/16012 17498/18204/16013\r\nf 17262/17968/15777 17006/17712/15521 17496/18202/16011 17497/18203/16012\r\nf 17276/17982/15791 17283/17989/15798 17499/18205/16014 17498/18204/16013\r\nf 17283/17989/15798 17282/17988/15797 17500/18206/16015 17499/18205/16014\r\nf 17249/17955/15764 17258/17964/15773 17499/18205/16014 17500/18206/16015\r\nf 17258/17964/15773 17257/17963/15772 17498/18204/16013 17499/18205/16014\r\nf 17282/17988/15797 17289/17995/15804 17501/18207/16016 17500/18206/16015\r\nf 17289/17995/15804 17288/17994/15803 17502/18208/16017 17501/18207/16016\r\nf 17241/17947/15756 17250/17956/15765 17501/18207/16016 17502/18208/16017\r\nf 17250/17956/15765 17249/17955/15764 17500/18206/16015 17501/18207/16016\r\nf 17288/17994/15803 17295/18001/15810 17503/18209/16018 17502/18208/16017\r\nf 17295/18001/15810 17294/18000/15809 17504/18210/16019 17503/18209/16018\r\nf 17233/17939/15748 17242/17948/15757 17503/18209/16018 17504/18210/16019\r\nf 17242/17948/15757 17241/17947/15756 17502/18208/16017 17503/18209/16018\r\nf 17294/18000/15809 17298/18004/15813 17505/18211/16020 17504/18210/16019\r\nf 17298/18004/15813 16196/16902/14711 17506/18212/16021 17505/18211/16020\r\nf 17225/17931/15740 17234/17940/15749 17505/18211/16020 17506/18212/16021\r\nf 17234/17940/15749 17233/17939/15748 17504/18210/16019 17505/18211/16020\r\nf 16196/16902/14711 16195/16901/14710 17507/18213/16022 17506/18212/16021\r\nf 16195/16901/14710 16194/16900/14709 17508/18214/16023 17507/18213/16022\r\nf 17219/17925/15734 17226/17932/15741 17507/18213/16022 17508/18214/16023\r\nf 17226/17932/15741 17225/17931/15740 17506/18212/16021 17507/18213/16022\r\nf 16194/16900/14709 16202/16908/14717 17509/18215/16024 17508/18214/16023\r\nf 16202/16908/14717 16201/16907/14716 17510/18216/16025 17509/18215/16024\r\nf 16171/16877/14686 17220/17926/15735 17509/18215/16024 17510/18216/16025\r\nf 17220/17926/15735 17219/17925/15734 17508/18214/16023 17509/18215/16024\r\nf 16201/16907/14716 16214/16920/14729 17511/18217/16026 17510/18216/16025\r\nf 16214/16920/14729 16213/16919/14728 17512/18218/16027 17511/18217/16026\r\nf 16173/16879/14688 16172/16878/14687 17511/18217/16026 17512/18218/16027\r\nf 16172/16878/14687 16171/16877/14686 17510/18216/16025 17511/18217/16026\r\nf 16213/16919/14728 17513/18219/16028 17514/18220/16029 17512/18218/16027\r\nf 17513/18219/16028 17515/18221/16030 17516/18222/16031 17514/18220/16029\r\nf 16053/16759/14568 16169/16875/14684 17514/18220/16029 17516/18222/16031\r\nf 16169/16875/14684 16173/16879/14688 17512/18218/16027 17514/18220/16029\r\nf 17515/18221/16030 17517/18223/16032 17518/18224/16033 17516/18222/16031\r\nf 17517/18223/16032 17519/18225/16034 17520/18226/16035 17518/18224/16033\r\nf 16045/16751/14560 16048/16754/14563 17518/18224/16033 17520/18226/16035\r\nf 16048/16754/14563 16053/16759/14568 17516/18222/16031 17518/18224/16033\r\nf 17519/18225/16034 17521/18227/16036 17522/18228/16037 17520/18226/16035\r\nf 17521/18227/16036 17523/18229/16038 17524/18230/16039 17522/18228/16037\r\nf 16049/16755/14564 16046/16752/14561 17522/18228/16037 17524/18230/16039\r\nf 16046/16752/14561 16045/16751/14560 17520/18226/16035 17522/18228/16037\r\nf 17523/18229/16038 17525/18231/16040 17526/18232/16041 17524/18230/16039\r\nf 17525/18231/16040 17527/18233/16042 17528/18234/16043 17526/18232/16041\r\nf 16776/17482/15291 16779/17485/15294 17526/18232/16041 17528/18234/16043\r\nf 16779/17485/15294 16049/16755/14564 17524/18230/16039 17526/18232/16041\r\nf 17527/18233/16042 17529/18235/16044 17530/18236/16045 17528/18234/16043\r\nf 17529/18235/16044 17531/18237/16046 17532/18238/16047 17530/18236/16045\r\nf 16788/17494/15303 16791/17497/15306 17530/18236/16045 17532/18238/16047\r\nf 16791/17497/15306 16776/17482/15291 17528/18234/16043 17530/18236/16045\r\nf 17531/18237/16046 17533/18239/16048 17534/18240/16049 17532/18238/16047\r\nf 17533/18239/16048 17535/18241/16050 17536/18242/16051 17534/18240/16049\r\nf 16800/17506/15315 16803/17509/15318 17534/18240/16049 17536/18242/16051\r\nf 16803/17509/15318 16788/17494/15303 17532/18238/16047 17534/18240/16049\r\nf 17535/18241/16050 17537/18243/16052 17538/18244/16053 17536/18242/16051\r\nf 17537/18243/16052 17539/18245/16054 17540/18246/16055 17538/18244/16053\r\nf 16812/17518/15327 16815/17521/15330 17538/18244/16053 17540/18246/16055\r\nf 16815/17521/15330 16800/17506/15315 17536/18242/16051 17538/18244/16053\r\nf 17539/18245/16054 17541/18247/16056 17542/18248/16057 17540/18246/16055\r\nf 17541/18247/16056 17543/18249/16058 17544/18250/16059 17542/18248/16057\r\nf 16063/16769/14578 16822/17528/15337 17542/18248/16057 17544/18250/16059\r\nf 16822/17528/15337 16812/17518/15327 17540/18246/16055 17542/18248/16057\r\nf 17543/18249/16058 17545/18251/16060 17546/18252/16061 17544/18250/16059\r\nf 17545/18251/16060 17547/18253/16062 17548/18254/16063 17546/18252/16061\r\nf 16067/16773/14582 16064/16770/14579 17546/18252/16061 17548/18254/16063\r\nf 16064/16770/14579 16063/16769/14578 17544/18250/16059 17546/18252/16061\r\nf 17547/18253/16062 17549/18255/16064 17550/18256/16065 17548/18254/16063\r\nf 17549/18255/16064 17404/18110/15919 17407/18113/15922 17550/18256/16065\r\nf 16379/17085/14894 16828/17534/15343 17550/18256/16065 17407/18113/15922\r\nf 16828/17534/15343 16067/16773/14582 17548/18254/16063 17550/18256/16065\r\nf 17551/18257/16066 17552/18258/16067 17553/18259/16068 17554/18260/16069\r\nf 17552/18258/16067 17555/18261/16070 17556/18262/16071 17553/18259/16068\r\nf 17557/18263/16072 17558/18264/16073 17553/18259/16068 17556/18262/16071\r\nf 17558/18264/16073 17559/18265/16074 17554/18260/16069 17553/18259/16068\r\nf 17560/18266/16075 17561/18267/16076 17562/18268/16077 17563/18269/16078\r\nf 17561/18267/16076 17564/18270/16079 17565/18271/16080 17562/18268/16077\r\nf 17566/18272/16081 17567/18273/16082 17562/18268/16077 17565/18271/16080\r\nf 17567/18273/16082 17568/18274/16083 17563/18269/16078 17562/18268/16077\r\nf 17569/18275/16084 17570/18276/16085 17571/18277/16086 17572/18278/16087\r\nf 17570/18276/16085 17573/18279/16088 17574/18280/16089 17571/18277/16086\r\nf 17575/18281/16090 17576/18282/16091 17571/18277/16086 17574/18280/16089\r\nf 17576/18282/16091 17577/18283/16092 17572/18278/16087 17571/18277/16086\r\nf 17578/18284/16093 17579/18285/16094 17580/18286/16095 17581/18287/16096\r\nf 17579/18285/16094 17582/18288/16097 17583/18289/16098 17580/18286/16095\r\nf 17584/18290/16099 17585/18291/16100 17580/18286/16095 17583/18289/16098\r\nf 17585/18291/16100 17586/18292/16101 17581/18287/16096 17580/18286/16095\r\nf 17587/18293/16102 17588/18294/16103 17589/18295/16104 17590/18296/16105\r\nf 17588/18294/16103 17591/18297/16106 17592/18298/16107 17589/18295/16104\r\nf 17593/18299/16108 17594/18300/16109 17589/18295/16104 17592/18298/16107\r\nf 17594/18300/16109 17595/18301/16110 17590/18296/16105 17589/18295/16104\r\nf 17596/18302/16111 17597/18303/16112 17598/18304/16113 17599/18305/16114\r\nf 17597/18303/16112 17600/18306/16115 17601/18307/16116 17598/18304/16113\r\nf 17602/18308/16117 17603/18309/16118 17598/18304/16113 17601/18307/16116\r\nf 17603/18309/16118 17604/18310/16119 17599/18305/16114 17598/18304/16113\r\nf 17605/18311/16120 17606/18312/16121 17607/18313/16122 17608/18314/16123\r\nf 17606/18312/16121 17609/18315/16124 17610/18316/16125 17607/18313/16122\r\nf 17611/18317/16126 17612/18318/16127 17607/18313/16122 17610/18316/16125\r\nf 17612/18318/16127 17613/18319/16128 17608/18314/16123 17607/18313/16122\r\nf 17614/18320/16129 17615/18321/16130 17616/18322/16131 17617/18323/16132\r\nf 17615/18321/16130 17618/18324/16133 17619/18325/16134 17616/18322/16131\r\nf 17620/18326/16135 17621/18327/16136 17616/18322/16131 17619/18325/16134\r\nf 17621/18327/16136 17622/18328/16137 17617/18323/16132 17616/18322/16131\r\nf 17623/18329/16138 17624/18330/16139 17625/18331/16140 17626/18332/16141\r\nf 17624/18330/16139 17627/18333/16142 17628/18334/16143 17625/18331/16140\r\nf 17629/18335/16144 17630/18336/16145 17625/18331/16140 17628/18334/16143\r\nf 17630/18336/16145 17631/18337/16146 17626/18332/16141 17625/18331/16140\r\nf 17632/18338/16147 17633/18339/16148 17634/18340/16149 17635/18341/16150\r\nf 17633/18339/16148 17636/18342/16151 17637/18343/16152 17634/18340/16149\r\nf 17638/18344/16153 17639/18345/16154 17634/18340/16149 17637/18343/16152\r\nf 17639/18345/16154 17640/18346/16155 17635/18341/16150 17634/18340/16149\r\nf 17640/18346/16155 17639/18345/16154 17641/18347/16156 17642/18348/16157\r\nf 17639/18345/16154 17638/18344/16153 17643/18349/16158 17641/18347/16156\r\nf 17644/18350/16159 17645/18351/16160 17641/18347/16156 17643/18349/16158\r\nf 17645/18351/16160 17646/18352/16161 17642/18348/16157 17641/18347/16156\r\nf 17646/18352/16161 17645/18351/16160 17647/18353/16162 17648/18354/16163\r\nf 17645/18351/16160 17644/18350/16159 17649/18355/16164 17647/18353/16162\r\nf 17650/18356/16165 17651/18357/16166 17647/18353/16162 17649/18355/16164\r\nf 17651/18357/16166 17652/18358/16167 17648/18354/16163 17647/18353/16162\r\nf 17653/18359/16168 17654/18360/16169 17655/18361/16170 17656/18362/16171\r\nf 17654/18360/16169 17657/18363/16172 17658/18364/16173 17655/18361/16170\r\nf 17659/18365/16174 17660/18366/16175 17655/18361/16170 17658/18364/16173\r\nf 17660/18366/16175 17661/18367/16176 17656/18362/16171 17655/18361/16170\r\nf 17662/18368/16177 17663/18369/16178 17664/18370/16179 17665/18371/16180\r\nf 17663/18369/16178 17666/18372/16181 17667/18373/16182 17664/18370/16179\r\nf 17668/18374/16183 17669/18375/16184 17664/18370/16179 17667/18373/16182\r\nf 17669/18375/16184 17670/18376/16185 17665/18371/16180 17664/18370/16179\r\nf 17671/18377/16186 17672/18378/16187 17673/18379/16188 17674/18380/16189\r\nf 17672/18378/16187 17675/18381/16190 17676/18382/16191 17673/18379/16188\r\nf 17677/18383/16192 17678/18384/16193 17673/18379/16188 17676/18382/16191\r\nf 17678/18384/16193 17555/18261/16070 17674/18380/16189 17673/18379/16188\r\nf 17555/18261/16070 17678/18384/16193 17679/18385/16194 17556/18262/16071\r\nf 17678/18384/16193 17677/18383/16192 17680/18386/16195 17679/18385/16194\r\nf 17681/18387/16196 17682/18388/16197 17679/18385/16194 17680/18386/16195\r\nf 17682/18388/16197 17557/18263/16072 17556/18262/16071 17679/18385/16194\r\nf 17568/18274/16083 17683/18389/16198 17684/18390/16199 17563/18269/16078\r\nf 17683/18389/16198 17685/18391/16200 17686/18392/16201 17684/18390/16199\r\nf 17687/18393/16202 17688/18394/16203 17684/18390/16199 17686/18392/16201\r\nf 17688/18394/16203 17560/18266/16075 17563/18269/16078 17684/18390/16199\r\nf 17689/18395/16204 17690/18396/16205 17691/18397/16206 17692/18398/16207\r\nf 17690/18396/16205 17693/18399/16208 17694/18400/16209 17691/18397/16206\r\nf 17695/18401/16210 17696/18402/16211 17691/18397/16206 17694/18400/16209\r\nf 17696/18402/16211 17697/18403/16212 17692/18398/16207 17691/18397/16206\r\nf 17697/18403/16212 17696/18402/16211 17698/18404/16213 17699/18405/16214\r\nf 17696/18402/16211 17695/18401/16210 17700/18406/16215 17698/18404/16213\r\nf 17701/18407/16216 17702/18408/16217 17698/18404/16213 17700/18406/16215\r\nf 17702/18408/16217 17703/18409/16218 17699/18405/16214 17698/18404/16213\r\nf 17586/18292/16101 17704/18410/16219 17705/18411/16220 17581/18287/16096\r\nf 17704/18410/16219 17706/18412/16221 17707/18413/16222 17705/18411/16220\r\nf 17708/18414/16223 17709/18415/16224 17705/18411/16220 17707/18413/16222\r\nf 17709/18415/16224 17578/18284/16093 17581/18287/16096 17705/18411/16220\r\nf 17591/18297/16106 17710/18416/16225 17711/18417/16226 17592/18298/16107\r\nf 17710/18416/16225 17712/18418/16227 17713/18419/16228 17711/18417/16226\r\nf 17714/18420/16229 17715/18421/16230 17711/18417/16226 17713/18419/16228\r\nf 17715/18421/16230 17593/18299/16108 17592/18298/16107 17711/18417/16226\r\nf 17716/18422/16231 17717/18423/16232 17718/18424/16233 17719/18425/16234\r\nf 17717/18423/16232 17720/18426/16235 17721/18427/16236 17718/18424/16233\r\nf 17706/18412/16221 17704/18410/16219 17718/18424/16233 17721/18427/16236\r\nf 17704/18410/16219 17586/18292/16101 17719/18425/16234 17718/18424/16233\r\nf 17722/18428/16237 17723/18429/16238 17724/18430/16239 17725/18431/16240\r\nf 17723/18429/16238 17720/18426/16235 17726/18432/16241 17724/18430/16239\r\nf 17727/18433/16242 17728/18434/16243 17724/18430/16239 17726/18432/16241\r\nf 17728/18434/16243 17729/18435/16244 17725/18431/16240 17724/18430/16239\r\nf 17730/18436/16245 17731/18437/16246 17732/18438/16247 17733/18439/16248\r\nf 17731/18437/16246 17734/18440/16249 17735/18441/16250 17732/18438/16247\r\nf 17736/18442/16251 17737/18443/16252 17732/18438/16247 17735/18441/16250\r\nf 17737/18443/16252 17738/18444/16253 17733/18439/16248 17732/18438/16247\r\nf 17738/18444/16253 17737/18443/16252 17739/18445/16254 17740/18446/16255\r\nf 17737/18443/16252 17736/18442/16251 17741/18447/16256 17739/18445/16254\r\nf 17742/18448/16257 17743/18449/16258 17739/18445/16254 17741/18447/16256\r\nf 17743/18449/16258 17744/18450/16259 17740/18446/16255 17739/18445/16254\r\nf 17745/18451/16260 17746/18452/16261 17747/18453/16262 17748/18454/16263\r\nf 17746/18452/16261 17708/18414/16223 17707/18413/16222 17747/18453/16262\r\nf 17706/18412/16221 17749/18455/16264 17747/18453/16262 17707/18413/16222\r\nf 17749/18455/16264 17750/18456/16265 17748/18454/16263 17747/18453/16262\r\nf 17744/18450/16259 17743/18449/16258 17751/18457/16266 17752/18458/16267\r\nf 17743/18449/16258 17742/18448/16257 17753/18459/16268 17751/18457/16266\r\nf 17754/18460/16269 17755/18461/16270 17751/18457/16266 17753/18459/16268\r\nf 17755/18461/16270 17756/18462/16271 17752/18458/16267 17751/18457/16266\r\nf 17631/18337/16146 17757/18463/16272 17758/18464/16273 17626/18332/16141\r\nf 17757/18463/16272 17759/18465/16274 17760/18466/16275 17758/18464/16273\r\nf 17761/18467/16276 17762/18468/16277 17758/18464/16273 17760/18466/16275\r\nf 17762/18468/16277 17623/18329/16138 17626/18332/16141 17758/18464/16273\r\nf 17627/18333/16142 17763/18469/16278 17764/18470/16279 17628/18334/16143\r\nf 17763/18469/16278 17765/18471/16280 17766/18472/16281 17764/18470/16279\r\nf 17767/18473/16282 17768/18474/16283 17764/18470/16279 17766/18472/16281\r\nf 17768/18474/16283 17629/18335/16144 17628/18334/16143 17764/18470/16279\r\nf 17629/18335/16144 17768/18474/16283 17769/18475/16284 17770/18476/16285\r\nf 17768/18474/16283 17767/18473/16282 17771/18477/16286 17769/18475/16284\r\nf 17772/18478/16287 17773/18479/16288 17769/18475/16284 17771/18477/16286\r\nf 17773/18479/16288 17774/18480/16289 17770/18476/16285 17769/18475/16284\r\nf 17774/18480/16289 17773/18479/16288 17775/18481/16290 17776/18482/16291\r\nf 17773/18479/16288 17772/18478/16287 17777/18483/16292 17775/18481/16290\r\nf 17778/18484/16293 17779/18485/16294 17775/18481/16290 17777/18483/16292\r\nf 17779/18485/16294 17780/18486/16295 17776/18482/16291 17775/18481/16290\r\nf 17780/18486/16295 17779/18485/16294 17781/18487/16296 17782/18488/16297\r\nf 17779/18485/16294 17778/18484/16293 17783/18489/16298 17781/18487/16296\r\nf 17784/18490/16299 17785/18491/16300 17781/18487/16296 17783/18489/16298\r\nf 17785/18491/16300 17786/18492/16301 17782/18488/16297 17781/18487/16296\r\nf 17786/18492/16301 17785/18491/16300 17787/18493/16302 17788/18494/16303\r\nf 17785/18491/16300 17784/18490/16299 17789/18495/16304 17787/18493/16302\r\nf 17790/18496/16305 17791/18497/16306 17787/18493/16302 17789/18495/16304\r\nf 17791/18497/16306 17792/18498/16307 17788/18494/16303 17787/18493/16302\r\nf 17792/18498/16307 17791/18497/16306 17793/18499/16308 17794/18500/16309\r\nf 17791/18497/16306 17790/18496/16305 17795/18501/16310 17793/18499/16308\r\nf 17796/18502/16311 17797/18503/16312 17793/18499/16308 17795/18501/16310\r\nf 17797/18503/16312 17798/18504/16313 17794/18500/16309 17793/18499/16308\r\nf 17798/18504/16313 17797/18503/16312 17799/18505/16314 17800/18506/16315\r\nf 17797/18503/16312 17796/18502/16311 17801/18507/16316 17799/18505/16314\r\nf 17802/18508/16317 17803/18509/16318 17799/18505/16314 17801/18507/16316\r\nf 17803/18509/16318 17804/18510/16319 17800/18506/16315 17799/18505/16314\r\nf 17804/18510/16319 17803/18509/16318 17805/18511/16320 17806/18512/16321\r\nf 17803/18509/16318 17802/18508/16317 17807/18513/16322 17805/18511/16320\r\nf 17808/18514/16323 17809/18515/16324 17805/18511/16320 17807/18513/16322\r\nf 17809/18515/16324 17810/18516/16325 17806/18512/16321 17805/18511/16320\r\nf 17810/18516/16325 17809/18515/16324 17811/18517/16326 17812/18518/16327\r\nf 17809/18515/16324 17808/18514/16323 17813/18519/16328 17811/18517/16326\r\nf 17814/18520/16329 17815/18521/16330 17811/18517/16326 17813/18519/16328\r\nf 17815/18521/16330 17816/18522/16331 17812/18518/16327 17811/18517/16326\r\nf 17816/18522/16331 17815/18521/16330 17817/18523/16332 17818/18524/16333\r\nf 17815/18521/16330 17814/18520/16329 17819/18525/16334 17817/18523/16332\r\nf 17820/18526/16335 17821/18527/16336 17817/18523/16332 17819/18525/16334\r\nf 17821/18527/16336 17822/18528/16337 17818/18524/16333 17817/18523/16332\r\nf 17822/18528/16337 17821/18527/16336 17823/18529/16338 17824/18530/16339\r\nf 17821/18527/16336 17820/18526/16335 17825/18531/16340 17823/18529/16338\r\nf 17826/18532/16341 17827/18533/16342 17823/18529/16338 17825/18531/16340\r\nf 17827/18533/16342 17828/18534/16343 17824/18530/16339 17823/18529/16338\r\nf 17828/18534/16343 17827/18533/16342 17829/18535/16344 17830/18536/16345\r\nf 17827/18533/16342 17826/18532/16341 17831/18537/16346 17829/18535/16344\r\nf 17832/18538/16347 17833/18539/16348 17829/18535/16344 17831/18537/16346\r\nf 17833/18539/16348 17834/18540/16349 17830/18536/16345 17829/18535/16344\r\nf 17834/18540/16349 17833/18539/16348 17835/18541/16350 17836/18542/16351\r\nf 17833/18539/16348 17832/18538/16347 17837/18543/16352 17835/18541/16350\r\nf 17838/18544/16353 17839/18545/16354 17835/18541/16350 17837/18543/16352\r\nf 17839/18545/16354 17840/18546/16355 17836/18542/16351 17835/18541/16350\r\nf 17840/18546/16355 17839/18545/16354 17841/18547/16356 17842/18548/16357\r\nf 17839/18545/16354 17838/18544/16353 17843/18549/16358 17841/18547/16356\r\nf 17844/18550/16359 17845/18551/16360 17841/18547/16356 17843/18549/16358\r\nf 17845/18551/16360 17846/18552/16361 17842/18548/16357 17841/18547/16356\r\nf 17846/18552/16361 17845/18551/16360 17847/18553/16362 17848/18554/16363\r\nf 17845/18551/16360 17844/18550/16359 17849/18555/16364 17847/18553/16362\r\nf 17850/18556/16365 17851/18557/16366 17847/18553/16362 17849/18555/16364\r\nf 17851/18557/16366 17852/18558/16367 17848/18554/16363 17847/18553/16362\r\nf 17852/18558/16367 17851/18557/16366 17853/18559/16368 17854/18560/16369\r\nf 17851/18557/16366 17850/18556/16365 17855/18561/16370 17853/18559/16368\r\nf 17856/18562/16371 17857/18563/16372 17853/18559/16368 17855/18561/16370\r\nf 17857/18563/16372 17858/18564/16373 17854/18560/16369 17853/18559/16368\r\nf 17858/18564/16373 17857/18563/16372 17859/18565/16374 17860/18566/16375\r\nf 17857/18563/16372 17856/18562/16371 17861/18567/16376 17859/18565/16374\r\nf 17862/18568/16377 17863/18569/16378 17859/18565/16374 17861/18567/16376\r\nf 17863/18569/16378 17864/18570/16379 17860/18566/16375 17859/18565/16374\r\nf 17864/18570/16379 17863/18569/16378 17865/18571/16380 17866/18572/16381\r\nf 17863/18569/16378 17862/18568/16377 17867/18573/16382 17865/18571/16380\r\nf 17868/18574/16383 17869/18575/16384 17865/18571/16380 17867/18573/16382\r\nf 17869/18575/16384 17636/18342/16151 17866/18572/16381 17865/18571/16380\r\nf 17640/18346/16155 17870/18576/16385 17871/18577/16386 17635/18341/16150\r\nf 17870/18576/16385 17872/18578/16387 17873/18579/16388 17871/18577/16386\r\nf 17874/18580/16389 17875/18581/16390 17871/18577/16386 17873/18579/16388\r\nf 17875/18581/16390 17632/18338/16147 17635/18341/16150 17871/18577/16386\r\nf 17636/18342/16151 17869/18575/16384 17876/18582/16391 17637/18343/16152\r\nf 17869/18575/16384 17868/18574/16383 17877/18583/16392 17876/18582/16391\r\nf 17701/18407/16216 17878/18584/16393 17876/18582/16391 17877/18583/16392\r\nf 17878/18584/16393 17638/18344/16153 17637/18343/16152 17876/18582/16391\r\nf 17646/18352/16161 17879/18585/16394 17880/18586/16395 17642/18348/16157\r\nf 17879/18585/16394 17685/18391/16200 17881/18587/16396 17880/18586/16395\r\nf 17872/18578/16387 17870/18576/16385 17880/18586/16395 17881/18587/16396\r\nf 17870/18576/16385 17640/18346/16155 17642/18348/16157 17880/18586/16395\r\nf 17638/18344/16153 17878/18584/16393 17882/18588/16397 17643/18349/16158\r\nf 17878/18584/16393 17701/18407/16216 17700/18406/16215 17882/18588/16397\r\nf 17695/18401/16210 17883/18589/16398 17882/18588/16397 17700/18406/16215\r\nf 17883/18589/16398 17644/18350/16159 17643/18349/16158 17882/18588/16397\r\nf 17644/18350/16159 17883/18589/16398 17884/18590/16399 17649/18355/16164\r\nf 17883/18589/16398 17695/18401/16210 17694/18400/16209 17884/18590/16399\r\nf 17693/18399/16208 17885/18591/16400 17884/18590/16399 17694/18400/16209\r\nf 17885/18591/16400 17650/18356/16165 17649/18355/16164 17884/18590/16399\r\nf 17661/18367/16176 17886/18592/16401 17887/18593/16402 17656/18362/16171\r\nf 17886/18592/16401 17888/18594/16403 17889/18595/16404 17887/18593/16402\r\nf 17890/18596/16405 17891/18597/16406 17887/18593/16402 17889/18595/16404\r\nf 17891/18597/16406 17653/18359/16168 17656/18362/16171 17887/18593/16402\r\nf 17657/18363/16172 17892/18598/16407 17893/18599/16408 17658/18364/16173\r\nf 17892/18598/16407 17894/18600/16409 17895/18601/16410 17893/18599/16408\r\nf 17896/18602/16411 17897/18603/16412 17893/18599/16408 17895/18601/16410\r\nf 17897/18603/16412 17659/18365/16174 17658/18364/16173 17893/18599/16408\r\nf 17659/18365/16174 17897/18603/16412 17898/18604/16413 17899/18605/16414\r\nf 17897/18603/16412 17896/18602/16411 17900/18606/16415 17898/18604/16413\r\nf 17901/18607/16416 17902/18608/16417 17898/18604/16413 17900/18606/16415\r\nf 17902/18608/16417 17903/18609/16418 17899/18605/16414 17898/18604/16413\r\nf 17904/18610/16419 17905/18611/16420 17906/18612/16421 17907/18613/16422\r\nf 17905/18611/16420 17908/18614/16423 17909/18615/16424 17906/18612/16421\r\nf 17910/18616/16425 17911/18617/16426 17906/18612/16421 17909/18615/16424\r\nf 17911/18617/16426 17912/18618/16427 17907/18613/16422 17906/18612/16421\r\nf 17913/18619/16428 17914/18620/16429 17915/18621/16430 17916/18622/16431\r\nf 17914/18620/16429 17917/18623/16432 17918/18624/16433 17915/18621/16430\r\nf 17919/18625/16434 17920/18626/16435 17915/18621/16430 17918/18624/16433\r\nf 17920/18626/16435 17921/18627/16436 17916/18622/16431 17915/18621/16430\r\nf 17922/18628/16437 17923/18629/16438 17924/18630/16439 17925/18631/16440\r\nf 17923/18629/16438 17904/18610/16419 17907/18613/16422 17924/18630/16439\r\nf 17912/18618/16427 17926/18632/16441 17924/18630/16439 17907/18613/16422\r\nf 17926/18632/16441 17927/18633/16442 17925/18631/16440 17924/18630/16439\r\nf 17928/18634/16443 17929/18635/16444 17930/18636/16445 17931/18637/16446\r\nf 17929/18635/16444 17913/18619/16428 17916/18622/16431 17930/18636/16445\r\nf 17921/18627/16436 17932/18638/16447 17930/18636/16445 17916/18622/16431\r\nf 17932/18638/16447 17933/18639/16448 17931/18637/16446 17930/18636/16445\r\nf 17934/18640/16449 17935/18641/16450 17936/18642/16451 17937/18643/16452\r\nf 17935/18641/16450 17922/18628/16437 17925/18631/16440 17936/18642/16451\r\nf 17927/18633/16442 17938/18644/16453 17936/18642/16451 17925/18631/16440\r\nf 17938/18644/16453 17939/18645/16454 17937/18643/16452 17936/18642/16451\r\nf 17940/18646/16455 17941/18647/16456 17942/18648/16457 17943/18649/16458\r\nf 17941/18647/16456 17928/18634/16443 17931/18637/16446 17942/18648/16457\r\nf 17933/18639/16448 17944/18650/16459 17942/18648/16457 17931/18637/16446\r\nf 17944/18650/16459 17945/18651/16460 17943/18649/16458 17942/18648/16457\r\nf 17946/18652/16461 17947/18653/16462 17948/18654/16463 17949/18655/16464\r\nf 17947/18653/16462 17934/18640/16449 17937/18643/16452 17948/18654/16463\r\nf 17939/18645/16454 17950/18656/16465 17948/18654/16463 17937/18643/16452\r\nf 17950/18656/16465 17951/18657/16466 17949/18655/16464 17948/18654/16463\r\nf 17952/18658/16467 17953/18659/16468 17954/18660/16469 17955/18661/16470\r\nf 17953/18659/16468 17940/18646/16455 17943/18649/16458 17954/18660/16469\r\nf 17945/18651/16460 17956/18662/16471 17954/18660/16469 17943/18649/16458\r\nf 17956/18662/16471 17957/18663/16472 17955/18661/16470 17954/18660/16469\r\nf 17958/18664/16473 17959/18665/16474 17960/18666/16475 17961/18667/16476\r\nf 17959/18665/16474 17946/18652/16461 17949/18655/16464 17960/18666/16475\r\nf 17951/18657/16466 17962/18668/16477 17960/18666/16475 17949/18655/16464\r\nf 17962/18668/16477 17963/18669/16478 17961/18667/16476 17960/18666/16475\r\nf 17964/18670/16479 17965/18671/16480 17966/18672/16481 17967/18673/16482\r\nf 17965/18671/16480 17952/18658/16467 17955/18661/16470 17966/18672/16481\r\nf 17957/18663/16472 17968/18674/16483 17966/18672/16481 17955/18661/16470\r\nf 17968/18674/16483 17969/18675/16484 17967/18673/16482 17966/18672/16481\r\nf 17970/18676/16485 17971/18677/16486 17972/18678/16487 17973/18679/16488\r\nf 17971/18677/16486 17958/18664/16473 17961/18667/16476 17972/18678/16487\r\nf 17963/18669/16478 17974/18680/16489 17972/18678/16487 17961/18667/16476\r\nf 17974/18680/16489 17975/18681/16490 17973/18679/16488 17972/18678/16487\r\nf 17976/18682/16491 17977/18683/16492 17978/18684/16493 17979/18685/16494\r\nf 17977/18683/16492 17964/18670/16479 17967/18673/16482 17978/18684/16493\r\nf 17969/18675/16484 17980/18686/16495 17978/18684/16493 17967/18673/16482\r\nf 17980/18686/16495 17981/18687/16496 17979/18685/16494 17978/18684/16493\r\nf 17982/18688/16497 17983/18689/16498 17984/18690/16499 17985/18691/16500\r\nf 17983/18689/16498 17970/18676/16485 17973/18679/16488 17984/18690/16499\r\nf 17975/18681/16490 17986/18692/16501 17984/18690/16499 17973/18679/16488\r\nf 17986/18692/16501 17987/18693/16502 17985/18691/16500 17984/18690/16499\r\nf 17988/18694/16503 17989/18695/16504 17990/18696/16505 17991/18697/16506\r\nf 17989/18695/16504 17976/18682/16491 17979/18685/16494 17990/18696/16505\r\nf 17981/18687/16496 17992/18698/16507 17990/18696/16505 17979/18685/16494\r\nf 17992/18698/16507 17993/18699/16508 17991/18697/16506 17990/18696/16505\r\nf 17994/18700/16509 17995/18701/16510 17996/18702/16511 17997/18703/16512\r\nf 17995/18701/16510 17982/18688/16497 17985/18691/16500 17996/18702/16511\r\nf 17987/18693/16502 17998/18704/16513 17996/18702/16511 17985/18691/16500\r\nf 17998/18704/16513 17999/18705/16514 17997/18703/16512 17996/18702/16511\r\nf 18000/18706/16515 18001/18707/16516 18002/18708/16517 18003/18709/16518\r\nf 18001/18707/16516 17988/18694/16503 17991/18697/16506 18002/18708/16517\r\nf 17993/18699/16508 18004/18710/16519 18002/18708/16517 17991/18697/16506\r\nf 18004/18710/16519 18005/18711/16520 18003/18709/16518 18002/18708/16517\r\nf 18006/18712/16521 18007/18713/16522 18008/18714/16523 18009/18715/16524\r\nf 18007/18713/16522 17994/18700/16509 17997/18703/16512 18008/18714/16523\r\nf 17999/18705/16514 18010/18716/16525 18008/18714/16523 17997/18703/16512\r\nf 18010/18716/16525 18011/18717/16526 18009/18715/16524 18008/18714/16523\r\nf 18012/18718/16527 18013/18719/16528 18014/18720/16529 18015/18721/16530\r\nf 18013/18719/16528 18000/18706/16515 18003/18709/16518 18014/18720/16529\r\nf 18005/18711/16520 18016/18722/16531 18014/18720/16529 18003/18709/16518\r\nf 18016/18722/16531 18017/18723/16532 18015/18721/16530 18014/18720/16529\r\nf 18018/18724/16533 18019/18725/16534 18020/18726/16535 18021/18727/16536\r\nf 18019/18725/16534 18006/18712/16521 18009/18715/16524 18020/18726/16535\r\nf 18011/18717/16526 18022/18728/16537 18020/18726/16535 18009/18715/16524\r\nf 18022/18728/16537 18023/18729/16538 18021/18727/16536 18020/18726/16535\r\nf 18024/18730/16539 18025/18731/16540 18026/18732/16541 18027/18733/16542\r\nf 18025/18731/16540 18012/18718/16527 18015/18721/16530 18026/18732/16541\r\nf 18017/18723/16532 18028/18734/16543 18026/18732/16541 18015/18721/16530\r\nf 18028/18734/16543 18029/18735/16544 18027/18733/16542 18026/18732/16541\r\nf 18030/18736/16545 18031/18737/16546 18032/18738/16547 18033/18739/16548\r\nf 18031/18737/16546 18018/18724/16533 18021/18727/16536 18032/18738/16547\r\nf 18023/18729/16538 18034/18740/16549 18032/18738/16547 18021/18727/16536\r\nf 18034/18740/16549 18035/18741/16550 18033/18739/16548 18032/18738/16547\r\nf 18036/18742/16551 18037/18743/16552 18038/18744/16553 18039/18745/16554\r\nf 18037/18743/16552 18024/18730/16539 18027/18733/16542 18038/18744/16553\r\nf 18029/18735/16544 18040/18746/16555 18038/18744/16553 18027/18733/16542\r\nf 18040/18746/16555 18041/18747/16556 18039/18745/16554 18038/18744/16553\r\nf 18042/18748/16557 18043/18749/16558 18044/18750/16559 18045/18751/16560\r\nf 18043/18749/16558 18030/18736/16545 18033/18739/16548 18044/18750/16559\r\nf 18035/18741/16550 18046/18752/16561 18044/18750/16559 18033/18739/16548\r\nf 18046/18752/16561 18047/18753/16562 18045/18751/16560 18044/18750/16559\r\nf 18048/18754/16563 18049/18755/16564 18050/18756/16565 18051/18757/16566\r\nf 18049/18755/16564 18036/18742/16551 18039/18745/16554 18050/18756/16565\r\nf 18041/18747/16556 18052/18758/16567 18050/18756/16565 18039/18745/16554\r\nf 18052/18758/16567 17671/18377/16186 18051/18757/16566 18050/18756/16565\r\nf 18053/18759/16568 18054/18760/16569 18055/18761/16570 18056/18762/16571\r\nf 18054/18760/16569 18042/18748/16557 18045/18751/16560 18055/18761/16570\r\nf 18047/18753/16562 18057/18763/16572 18055/18761/16570 18045/18751/16560\r\nf 18057/18763/16572 18058/18764/16573 18056/18762/16571 18055/18761/16570\r\nf 17551/18257/16066 18059/18765/16574 18060/18766/16575 17552/18258/16067\r\nf 18059/18765/16574 18048/18754/16563 18051/18757/16566 18060/18766/16575\r\nf 17671/18377/16186 17674/18380/16189 18060/18766/16575 18051/18757/16566\r\nf 17674/18380/16189 17555/18261/16070 17552/18258/16067 18060/18766/16575\r\nf 18061/18767/16576 18062/18768/16577 18063/18769/16578 18064/18770/16579\r\nf 18062/18768/16577 18053/18759/16568 18056/18762/16571 18063/18769/16578\r\nf 18058/18764/16573 18065/18771/16580 18063/18769/16578 18056/18762/16571\r\nf 18065/18771/16580 18066/18772/16581 18064/18770/16579 18063/18769/16578\r\nf 18067/18773/16582 18068/18774/16583 18069/18775/16584 18070/18776/16585\r\nf 18068/18774/16583 18071/18777/16586 18072/18778/16587 18069/18775/16584\r\nf 17689/18395/16204 17692/18398/16207 18069/18775/16584 18072/18778/16587\r\nf 17692/18398/16207 17697/18403/16212 18070/18776/16585 18069/18775/16584\r\nf 18073/18779/16588 18074/18780/16589 18075/18781/16590 18076/18782/16591\r\nf 18074/18780/16589 17568/18274/16083 17567/18273/16082 18075/18781/16590\r\nf 17566/18272/16081 18077/18783/16592 18075/18781/16590 17567/18273/16082\r\nf 18077/18783/16592 18078/18784/16593 18076/18782/16591 18075/18781/16590\r\nf 18079/18785/16594 18080/18786/16595 18081/18787/16596 18082/18788/16597\r\nf 18080/18786/16595 18067/18773/16582 18070/18776/16585 18081/18787/16596\r\nf 17697/18403/16212 17699/18405/16214 18081/18787/16596 18070/18776/16585\r\nf 17699/18405/16214 17703/18409/16218 18082/18788/16597 18081/18787/16596\r\nf 18083/18789/16598 18084/18790/16599 18085/18791/16600 18086/18792/16601\r\nf 18084/18790/16599 18073/18779/16588 18076/18782/16591 18085/18791/16600\r\nf 18078/18784/16593 18087/18793/16602 18085/18791/16600 18076/18782/16591\r\nf 18087/18793/16602 18088/18794/16603 18086/18792/16601 18085/18791/16600\r\nf 18089/18795/16604 18090/18796/16605 18091/18797/16606 18092/18798/16607\r\nf 18090/18796/16605 18079/18785/16594 18082/18788/16597 18091/18797/16606\r\nf 17703/18409/16218 18093/18799/16608 18091/18797/16606 18082/18788/16597\r\nf 18093/18799/16608 18094/18800/16609 18092/18798/16607 18091/18797/16606\r\nf 18095/18801/16610 18096/18802/16611 18097/18803/16612 18098/18804/16613\r\nf 18096/18802/16611 18083/18789/16598 18086/18792/16601 18097/18803/16612\r\nf 18088/18794/16603 18099/18805/16614 18097/18803/16612 18086/18792/16601\r\nf 18099/18805/16614 18100/18806/16615 18098/18804/16613 18097/18803/16612\r\nf 18101/18807/16616 18102/18808/16617 18103/18809/16618 18104/18810/16619\r\nf 18102/18808/16617 18089/18795/16604 18092/18798/16607 18103/18809/16618\r\nf 18094/18800/16609 18105/18811/16620 18103/18809/16618 18092/18798/16607\r\nf 18105/18811/16620 18106/18812/16621 18104/18810/16619 18103/18809/16618\r\nf 18107/18813/16622 18108/18814/16623 18109/18815/16624 18110/18816/16625\r\nf 18108/18814/16623 18095/18801/16610 18098/18804/16613 18109/18815/16624\r\nf 18100/18806/16615 18111/18817/16626 18109/18815/16624 18098/18804/16613\r\nf 18111/18817/16626 18112/18818/16627 18110/18816/16625 18109/18815/16624\r\nf 18113/18819/16628 18114/18820/16629 18115/18821/16630 18116/18822/16631\r\nf 18114/18820/16629 18101/18807/16616 18104/18810/16619 18115/18821/16630\r\nf 18106/18812/16621 18117/18823/16632 18115/18821/16630 18104/18810/16619\r\nf 18117/18823/16632 18118/18824/16633 18116/18822/16631 18115/18821/16630\r\nf 18119/18825/16634 18120/18826/16635 18121/18827/16636 18122/18828/16637\r\nf 18120/18826/16635 18107/18813/16622 18110/18816/16625 18121/18827/16636\r\nf 18112/18818/16627 18123/18829/16638 18121/18827/16636 18110/18816/16625\r\nf 18123/18829/16638 18124/18830/16639 18122/18828/16637 18121/18827/16636\r\nf 18125/18831/16640 18126/18832/16641 18127/18833/16642 18128/18834/16643\r\nf 18126/18832/16641 18113/18819/16628 18116/18822/16631 18127/18833/16642\r\nf 18118/18824/16633 18129/18835/16644 18127/18833/16642 18116/18822/16631\r\nf 18129/18835/16644 18130/18836/16645 18128/18834/16643 18127/18833/16642\r\nf 18131/18837/16646 18132/18838/16647 18133/18839/16648 18134/18840/16649\r\nf 18132/18838/16647 18119/18825/16634 18122/18828/16637 18133/18839/16648\r\nf 18124/18830/16639 18135/18841/16650 18133/18839/16648 18122/18828/16637\r\nf 18135/18841/16650 18136/18842/16651 18134/18840/16649 18133/18839/16648\r\nf 18137/18843/16652 18138/18844/16653 18139/18845/16654 18140/18846/16655\r\nf 18138/18844/16653 18125/18831/16640 18128/18834/16643 18139/18845/16654\r\nf 18130/18836/16645 18141/18847/16656 18139/18845/16654 18128/18834/16643\r\nf 18141/18847/16656 18142/18848/16657 18140/18846/16655 18139/18845/16654\r\nf 18143/18849/16658 18144/18850/16659 18145/18851/16660 18146/18852/16661\r\nf 18144/18850/16659 18131/18837/16646 18134/18840/16649 18145/18851/16660\r\nf 18136/18842/16651 18147/18853/16662 18145/18851/16660 18134/18840/16649\r\nf 18147/18853/16662 18148/18854/16663 18146/18852/16661 18145/18851/16660\r\nf 18149/18855/16664 18150/18856/16665 18151/18857/16666 18152/18858/16667\r\nf 18150/18856/16665 18137/18843/16652 18140/18846/16655 18151/18857/16666\r\nf 18142/18848/16657 18153/18859/16668 18151/18857/16666 18140/18846/16655\r\nf 18153/18859/16668 18154/18860/16669 18152/18858/16667 18151/18857/16666\r\nf 18155/18861/16670 18156/18862/16671 18157/18863/16672 18158/18864/16673\r\nf 18156/18862/16671 18143/18849/16658 18146/18852/16661 18157/18863/16672\r\nf 18148/18854/16663 18159/18865/16674 18157/18863/16672 18146/18852/16661\r\nf 18159/18865/16674 18160/18866/16675 18158/18864/16673 18157/18863/16672\r\nf 18161/18867/16676 18162/18868/16677 18163/18869/16678 18164/18870/16679\r\nf 18162/18868/16677 18149/18855/16664 18152/18858/16667 18163/18869/16678\r\nf 18154/18860/16669 18165/18871/16680 18163/18869/16678 18152/18858/16667\r\nf 18165/18871/16680 18166/18872/16681 18164/18870/16679 18163/18869/16678\r\nf 18167/18873/16682 18168/18874/16683 18169/18875/16684 18170/18876/16685\r\nf 18168/18874/16683 18155/18861/16670 18158/18864/16673 18169/18875/16684\r\nf 18160/18866/16675 18171/18877/16686 18169/18875/16684 18158/18864/16673\r\nf 18171/18877/16686 18172/18878/16687 18170/18876/16685 18169/18875/16684\r\nf 18173/18879/16688 18174/18880/16689 18175/18881/16690 18176/18882/16691\r\nf 18174/18880/16689 18161/18867/16676 18164/18870/16679 18175/18881/16690\r\nf 18166/18872/16681 18177/18883/16692 18175/18881/16690 18164/18870/16679\r\nf 18177/18883/16692 18178/18884/16693 18176/18882/16691 18175/18881/16690\r\nf 18179/18885/16694 18180/18886/16695 18181/18887/16696 18182/18888/16697\r\nf 18180/18886/16695 18167/18873/16682 18170/18876/16685 18181/18887/16696\r\nf 18172/18878/16687 18183/18889/16698 18181/18887/16696 18170/18876/16685\r\nf 18183/18889/16698 18184/18890/16699 18182/18888/16697 18181/18887/16696\r\nf 18185/18891/16700 18186/18892/16701 18187/18893/16702 18188/18894/16703\r\nf 18186/18892/16701 18173/18879/16688 18176/18882/16691 18187/18893/16702\r\nf 18178/18884/16693 18189/18895/16704 18187/18893/16702 18176/18882/16691\r\nf 18189/18895/16704 18190/18896/16705 18188/18894/16703 18187/18893/16702\r\nf 18191/18897/16706 18192/18898/16707 18193/18899/16708 18194/18900/16709\r\nf 18192/18898/16707 18179/18885/16694 18182/18888/16697 18193/18899/16708\r\nf 18184/18890/16699 18195/18901/16710 18193/18899/16708 18182/18888/16697\r\nf 18195/18901/16710 18196/18902/16711 18194/18900/16709 18193/18899/16708\r\nf 18197/18903/16712 18198/18904/16713 18199/18905/16714 18200/18906/16715\r\nf 18198/18904/16713 18185/18891/16700 18188/18894/16703 18199/18905/16714\r\nf 18190/18896/16705 18201/18907/16716 18199/18905/16714 18188/18894/16703\r\nf 18201/18907/16716 18202/18908/16717 18200/18906/16715 18199/18905/16714\r\nf 18203/18909/16718 18204/18910/16719 18205/18911/16720 18206/18912/16721\r\nf 18204/18910/16719 18191/18897/16706 18194/18900/16709 18205/18911/16720\r\nf 18196/18902/16711 18207/18913/16722 18205/18911/16720 18194/18900/16709\r\nf 18207/18913/16722 18208/18914/16723 18206/18912/16721 18205/18911/16720\r\nf 18209/18915/16724 18210/18916/16725 18211/18917/16726 18212/18918/16727\r\nf 18210/18916/16725 18197/18903/16712 18200/18906/16715 18211/18917/16726\r\nf 18202/18908/16717 18213/18919/16728 18211/18917/16726 18200/18906/16715\r\nf 18213/18919/16728 18214/18920/16729 18212/18918/16727 18211/18917/16726\r\nf 18215/18921/16730 18216/18922/16731 18217/18923/16732 18218/18924/16733\r\nf 18216/18922/16731 18203/18909/16718 18206/18912/16721 18217/18923/16732\r\nf 18208/18914/16723 18219/18925/16734 18217/18923/16732 18206/18912/16721\r\nf 18219/18925/16734 18220/18926/16735 18218/18924/16733 18217/18923/16732\r\nf 18221/18927/16736 18222/18928/16737 18223/18929/16738 18224/18930/16739\r\nf 18222/18928/16737 18209/18915/16724 18212/18918/16727 18223/18929/16738\r\nf 18214/18920/16729 18225/18931/16740 18223/18929/16738 18212/18918/16727\r\nf 18225/18931/16740 18226/18932/16741 18224/18930/16739 18223/18929/16738\r\nf 18227/18933/16742 18228/18934/16743 18229/18935/16744 18230/18936/16745\r\nf 18228/18934/16743 18215/18921/16730 18218/18924/16733 18229/18935/16744\r\nf 18220/18926/16735 18231/18937/16746 18229/18935/16744 18218/18924/16733\r\nf 18231/18937/16746 18232/18938/16747 18230/18936/16745 18229/18935/16744\r\nf 18233/18939/16748 18234/18940/16749 18235/18941/16750 18236/18942/16751\r\nf 18234/18940/16749 18221/18927/16736 18224/18930/16739 18235/18941/16750\r\nf 18226/18932/16741 18237/18943/16752 18235/18941/16750 18224/18930/16739\r\nf 18237/18943/16752 18238/18944/16753 18236/18942/16751 18235/18941/16750\r\nf 18239/18945/16754 18240/18946/16755 18241/18947/16756 18242/18948/16757\r\nf 18240/18946/16755 18227/18933/16742 18230/18936/16745 18241/18947/16756\r\nf 18232/18938/16747 18243/18949/16758 18241/18947/16756 18230/18936/16745\r\nf 18243/18949/16758 18244/18950/16759 18242/18948/16757 18241/18947/16756\r\nf 18245/18951/16760 18246/18952/16761 18247/18953/16762 18248/18954/16763\r\nf 18246/18952/16761 18233/18939/16748 18236/18942/16751 18247/18953/16762\r\nf 18238/18944/16753 18249/18955/16764 18247/18953/16762 18236/18942/16751\r\nf 18249/18955/16764 18250/18956/16765 18248/18954/16763 18247/18953/16762\r\nf 18251/18957/16766 18252/18958/16767 18253/18959/16768 18254/18960/16769\r\nf 18252/18958/16767 18239/18945/16754 18242/18948/16757 18253/18959/16768\r\nf 18244/18950/16759 18255/18961/16770 18253/18959/16768 18242/18948/16757\r\nf 18255/18961/16770 18256/18962/16771 18254/18960/16769 18253/18959/16768\r\nf 18257/18963/16772 18258/18964/16773 18259/18965/16774 18260/18966/16775\r\nf 18258/18964/16773 18245/18951/16760 18248/18954/16763 18259/18965/16774\r\nf 18250/18956/16765 18261/18967/16776 18259/18965/16774 18248/18954/16763\r\nf 18261/18967/16776 18262/18968/16777 18260/18966/16775 18259/18965/16774\r\nf 18263/18969/16778 18264/18970/16779 18265/18971/16780 18266/18972/16781\r\nf 18264/18970/16779 18251/18957/16766 18254/18960/16769 18265/18971/16780\r\nf 18256/18962/16771 18267/18973/16782 18265/18971/16780 18254/18960/16769\r\nf 18267/18973/16782 18268/18974/16783 18266/18972/16781 18265/18971/16780\r\nf 18269/18975/16784 18270/18976/16785 18271/18977/16786 18272/18978/16787\r\nf 18270/18976/16785 18257/18963/16772 18260/18966/16775 18271/18977/16786\r\nf 18262/18968/16777 18273/18979/16788 18271/18977/16786 18260/18966/16775\r\nf 18273/18979/16788 18274/18980/16789 18272/18978/16787 18271/18977/16786\r\nf 18275/18981/16790 18276/18982/16791 18277/18983/16792 18278/18984/16793\r\nf 18276/18982/16791 18263/18969/16778 18266/18972/16781 18277/18983/16792\r\nf 18268/18974/16783 18279/18985/16794 18277/18983/16792 18266/18972/16781\r\nf 18279/18985/16794 18280/18986/16795 18278/18984/16793 18277/18983/16792\r\nf 18281/18987/16796 18282/18988/16797 18283/18989/16798 18284/18990/16799\r\nf 18282/18988/16797 18269/18975/16784 18272/18978/16787 18283/18989/16798\r\nf 18274/18980/16789 18285/18991/16800 18283/18989/16798 18272/18978/16787\r\nf 18285/18991/16800 18286/18992/16801 18284/18990/16799 18283/18989/16798\r\nf 17569/18275/16084 18287/18993/16802 18288/18994/16803 17570/18276/16085\r\nf 18287/18993/16802 18275/18981/16790 18278/18984/16793 18288/18994/16803\r\nf 18280/18986/16795 18289/18995/16804 18288/18994/16803 18278/18984/16793\r\nf 18289/18995/16804 17573/18279/16088 17570/18276/16085 18288/18994/16803\r\nf 18290/18996/16805 18291/18997/16806 18292/18998/16807 18293/18999/16808\r\nf 18291/18997/16806 18281/18987/16796 18284/18990/16799 18292/18998/16807\r\nf 18286/18992/16801 18294/19000/16809 18292/18998/16807 18284/18990/16799\r\nf 18294/19000/16809 18295/19001/16810 18293/18999/16808 18292/18998/16807\r\nf 18296/19002/16811 18297/19003/16812 18298/19004/16813 18299/19005/16814\r\nf 18297/19003/16812 18290/18996/16805 18293/18999/16808 18298/19004/16813\r\nf 18295/19001/16810 18300/19006/16815 18298/19004/16813 18293/18999/16808\r\nf 18300/19006/16815 18301/19007/16816 18299/19005/16814 18298/19004/16813\r\nf 18302/19008/16817 18303/19009/16818 18304/19010/16819 18305/19011/16820\r\nf 18303/19009/16818 17577/18283/16092 17576/18282/16091 18304/19010/16819\r\nf 17575/18281/16090 18306/19012/16821 18304/19010/16819 17576/18282/16091\r\nf 18306/19012/16821 18307/19013/16822 18305/19011/16820 18304/19010/16819\r\nf 18296/19002/16811 18299/19005/16814 18308/19014/16823 18309/19015/16824\r\nf 18299/19005/16814 18301/19007/16816 18310/19016/16825 18308/19014/16823\r\nf 18311/19017/16826 18312/19018/16827 18308/19014/16823 18310/19016/16825\r\nf 18312/19018/16827 18313/19019/16828 18309/19015/16824 18308/19014/16823\r\nf 17716/18422/16231 17719/18425/16234 18314/19020/16829 18315/19021/16830\r\nf 17719/18425/16234 17586/18292/16101 17585/18291/16100 18314/19020/16829\r\nf 17584/18290/16099 18316/19022/16831 18314/19020/16829 17585/18291/16100\r\nf 18316/19022/16831 18317/19023/16832 18315/19021/16830 18314/19020/16829\r\nf 18318/19024/16833 18319/19025/16834 18320/19026/16835 18321/19027/16836\r\nf 18319/19025/16834 17595/18301/16110 17594/18300/16109 18320/19026/16835\r\nf 17593/18299/16108 18322/19028/16837 18320/19026/16835 17594/18300/16109\r\nf 18322/19028/16837 18323/19029/16838 18321/19027/16836 18320/19026/16835\r\nf 18324/19030/16839 18325/19031/16840 18326/19032/16841 18327/19033/16842\r\nf 18325/19031/16840 17716/18422/16231 18315/19021/16830 18326/19032/16841\r\nf 18317/19023/16832 18328/19034/16843 18326/19032/16841 18315/19021/16830\r\nf 18328/19034/16843 18329/19035/16844 18327/19033/16842 18326/19032/16841\r\nf 18330/19036/16845 18331/19037/16846 18332/19038/16847 18333/19039/16848\r\nf 18331/19037/16846 18318/19024/16833 18321/19027/16836 18332/19038/16847\r\nf 18323/19029/16838 18334/19040/16849 18332/19038/16847 18321/19027/16836\r\nf 18334/19040/16849 18335/19041/16850 18333/19039/16848 18332/19038/16847\r\nf 18336/19042/16851 18337/19043/16852 18338/19044/16853 18339/19045/16854\r\nf 18337/19043/16852 18324/19030/16839 18327/19033/16842 18338/19044/16853\r\nf 18329/19035/16844 18340/19046/16855 18338/19044/16853 18327/19033/16842\r\nf 18340/19046/16855 18341/19047/16856 18339/19045/16854 18338/19044/16853\r\nf 18342/19048/16857 18343/19049/16858 18344/19050/16859 18345/19051/16860\r\nf 18343/19049/16858 18330/19036/16845 18333/19039/16848 18344/19050/16859\r\nf 18335/19041/16850 18346/19052/16861 18344/19050/16859 18333/19039/16848\r\nf 18346/19052/16861 18347/19053/16862 18345/19051/16860 18344/19050/16859\r\nf 18348/19054/16863 18349/19055/16864 18350/19056/16865 18351/19057/16866\r\nf 18349/19055/16864 18336/19042/16851 18339/19045/16854 18350/19056/16865\r\nf 18341/19047/16856 18352/19058/16867 18350/19056/16865 18339/19045/16854\r\nf 18352/19058/16867 18353/19059/16868 18351/19057/16866 18350/19056/16865\r\nf 18354/19060/16869 18355/19061/16870 18356/19062/16871 18357/19063/16872\r\nf 18355/19061/16870 18342/19048/16857 18345/19051/16860 18356/19062/16871\r\nf 18347/19053/16862 18358/19064/16873 18356/19062/16871 18345/19051/16860\r\nf 18358/19064/16873 18359/19065/16874 18357/19063/16872 18356/19062/16871\r\nf 17596/18302/16111 18360/19066/16875 18361/19067/16876 17597/18303/16112\r\nf 18360/19066/16875 18348/19054/16863 18351/19057/16866 18361/19067/16876\r\nf 18353/19059/16868 18362/19068/16877 18361/19067/16876 18351/19057/16866\r\nf 18362/19068/16877 17600/18306/16115 17597/18303/16112 18361/19067/16876\r\nf 17605/18311/16120 18363/19069/16878 18364/19070/16879 17606/18312/16121\r\nf 18363/19069/16878 18354/19060/16869 18357/19063/16872 18364/19070/16879\r\nf 18359/19065/16874 18365/19071/16880 18364/19070/16879 18357/19063/16872\r\nf 18365/19071/16880 17609/18315/16124 17606/18312/16121 18364/19070/16879\r\nf 17908/18614/16423 18366/19072/16881 18367/19073/16882 17909/18615/16424\r\nf 18366/19072/16881 17604/18310/16119 17603/18309/16118 18367/19073/16882\r\nf 17602/18308/16117 18368/19074/16883 18367/19073/16882 17603/18309/16118\r\nf 18368/19074/16883 17910/18616/16425 17909/18615/16424 18367/19073/16882\r\nf 17917/18623/16432 18369/19075/16884 18370/19076/16885 17918/18624/16433\r\nf 18369/19075/16884 17613/18319/16128 17612/18318/16127 18370/19076/16885\r\nf 17611/18317/16126 18371/19077/16886 18370/19076/16885 17612/18318/16127\r\nf 18371/19077/16886 17919/18625/16434 17918/18624/16433 18370/19076/16885\r\nf 17614/18320/16129 18372/19078/16887 18373/19079/16888 17615/18321/16130\r\nf 18372/19078/16887 18374/19080/16889 18375/19081/16890 18373/19079/16888\r\nf 18376/19082/16891 18377/19083/16892 18373/19079/16888 18375/19081/16890\r\nf 18377/19083/16892 17618/18324/16133 17615/18321/16130 18373/19079/16888\r\nf 18378/19084/16893 18379/19085/16894 18380/19086/16895 18381/19087/16896\r\nf 18379/19085/16894 17622/18328/16137 17621/18327/16136 18380/19086/16895\r\nf 17620/18326/16135 18382/19088/16897 18380/19086/16895 17621/18327/16136\r\nf 18382/19088/16897 18383/19089/16898 18381/19087/16896 18380/19086/16895\r\nf 18384/19090/16899 18385/19091/16900 18386/19092/16901 18387/19093/16902\r\nf 18385/19091/16900 18378/19084/16893 18381/19087/16896 18386/19092/16901\r\nf 18383/19089/16898 18388/19094/16903 18386/19092/16901 18381/19087/16896\r\nf 18388/19094/16903 18389/19095/16904 18387/19093/16902 18386/19092/16901\r\nf 18390/19096/16905 18391/19097/16906 18392/19098/16907 18393/19099/16908\r\nf 18391/19097/16906 18384/19090/16899 18387/19093/16902 18392/19098/16907\r\nf 18389/19095/16904 18394/19100/16909 18392/19098/16907 18387/19093/16902\r\nf 18394/19100/16909 18395/19101/16910 18393/19099/16908 18392/19098/16907\r\nf 17729/18435/16244 18396/19102/16911 18397/19103/16912 18398/19104/16913\r\nf 18396/19102/16911 18390/19096/16905 18393/19099/16908 18397/19103/16912\r\nf 18395/19101/16910 18399/19105/16914 18397/19103/16912 18393/19099/16908\r\nf 18399/19105/16914 17730/18436/16245 18398/19104/16913 18397/19103/16912\r\nf 17722/18428/16237 17725/18431/16240 18400/19106/16915 18401/19107/16916\r\nf 17725/18431/16240 17729/18435/16244 18398/19104/16913 18400/19106/16915\r\nf 17730/18436/16245 17733/18439/16248 18400/19106/16915 18398/19104/16913\r\nf 17733/18439/16248 17738/18444/16253 18401/19107/16916 18400/19106/16915\r\nf 17750/18456/16265 18402/19108/16917 18403/19109/16918 18404/19110/16919\r\nf 18402/19108/16917 17722/18428/16237 18401/19107/16916 18403/19109/16918\r\nf 17738/18444/16253 17740/18446/16255 18403/19109/16918 18401/19107/16916\r\nf 17740/18446/16255 17744/18450/16259 18404/19110/16919 18403/19109/16918\r\nf 17745/18451/16260 17748/18454/16263 18405/19111/16920 18406/19112/16921\r\nf 17748/18454/16263 17750/18456/16265 18404/19110/16919 18405/19111/16920\r\nf 17744/18450/16259 17752/18458/16267 18405/19111/16920 18404/19110/16919\r\nf 17752/18458/16267 17756/18462/16271 18406/19112/16921 18405/19111/16920\r\nf 17623/18329/16138 18407/19113/16922 18408/19114/16923 17624/18330/16139\r\nf 18407/19113/16922 18409/19115/16924 18410/19116/16925 18408/19114/16923\r\nf 18411/19117/16926 18412/19118/16927 18408/19114/16923 18410/19116/16925\r\nf 18412/19118/16927 17627/18333/16142 17624/18330/16139 18408/19114/16923\r\nf 18413/19119/16928 18414/19120/16929 18415/19121/16930 18416/19122/16931\r\nf 18414/19120/16929 17631/18337/16146 17630/18336/16145 18415/19121/16930\r\nf 17629/18335/16144 17770/18476/16285 18415/19121/16930 17630/18336/16145\r\nf 17770/18476/16285 17774/18480/16289 18416/19122/16931 18415/19121/16930\r\nf 18417/19123/16932 18418/19124/16933 18419/19125/16934 18420/19126/16935\r\nf 18418/19124/16933 18413/19119/16928 18416/19122/16931 18419/19125/16934\r\nf 17774/18480/16289 17776/18482/16291 18419/19125/16934 18416/19122/16931\r\nf 17776/18482/16291 17780/18486/16295 18420/19126/16935 18419/19125/16934\r\nf 18421/19127/16936 18422/19128/16937 18423/19129/16938 18424/19130/16939\r\nf 18422/19128/16937 18417/19123/16932 18420/19126/16935 18423/19129/16938\r\nf 17780/18486/16295 17782/18488/16297 18423/19129/16938 18420/19126/16935\r\nf 17782/18488/16297 17786/18492/16301 18424/19130/16939 18423/19129/16938\r\nf 18425/19131/16940 18426/19132/16941 18427/19133/16942 18428/19134/16943\r\nf 18426/19132/16941 18421/19127/16936 18424/19130/16939 18427/19133/16942\r\nf 17786/18492/16301 17788/18494/16303 18427/19133/16942 18424/19130/16939\r\nf 17788/18494/16303 17792/18498/16307 18428/19134/16943 18427/19133/16942\r\nf 18429/19135/16944 18430/19136/16945 18431/19137/16946 18432/19138/16947\r\nf 18430/19136/16945 18425/19131/16940 18428/19134/16943 18431/19137/16946\r\nf 17792/18498/16307 17794/18500/16309 18431/19137/16946 18428/19134/16943\r\nf 17794/18500/16309 17798/18504/16313 18432/19138/16947 18431/19137/16946\r\nf 18433/19139/16948 18434/19140/16949 18435/19141/16950 18436/19142/16951\r\nf 18434/19140/16949 18429/19135/16944 18432/19138/16947 18435/19141/16950\r\nf 17798/18504/16313 17800/18506/16315 18435/19141/16950 18432/19138/16947\r\nf 17800/18506/16315 17804/18510/16319 18436/19142/16951 18435/19141/16950\r\nf 18437/19143/16952 18438/19144/16953 18439/19145/16954 18440/19146/16955\r\nf 18438/19144/16953 18433/19139/16948 18436/19142/16951 18439/19145/16954\r\nf 17804/18510/16319 17806/18512/16321 18439/19145/16954 18436/19142/16951\r\nf 17806/18512/16321 17810/18516/16325 18440/19146/16955 18439/19145/16954\r\nf 18441/19147/16956 18442/19148/16957 18443/19149/16958 18444/19150/16959\r\nf 18442/19148/16957 18437/19143/16952 18440/19146/16955 18443/19149/16958\r\nf 17810/18516/16325 17812/18518/16327 18443/19149/16958 18440/19146/16955\r\nf 17812/18518/16327 17816/18522/16331 18444/19150/16959 18443/19149/16958\r\nf 18445/19151/16960 18446/19152/16961 18447/19153/16962 18448/19154/16963\r\nf 18446/19152/16961 18441/19147/16956 18444/19150/16959 18447/19153/16962\r\nf 17816/18522/16331 17818/18524/16333 18447/19153/16962 18444/19150/16959\r\nf 17818/18524/16333 17822/18528/16337 18448/19154/16963 18447/19153/16962\r\nf 18449/19155/16964 18450/19156/16965 18451/19157/16966 18452/19158/16967\r\nf 18450/19156/16965 18445/19151/16960 18448/19154/16963 18451/19157/16966\r\nf 17822/18528/16337 17824/18530/16339 18451/19157/16966 18448/19154/16963\r\nf 17824/18530/16339 17828/18534/16343 18452/19158/16967 18451/19157/16966\r\nf 18453/19159/16968 18454/19160/16969 18455/19161/16970 18456/19162/16971\r\nf 18454/19160/16969 18449/19155/16964 18452/19158/16967 18455/19161/16970\r\nf 17828/18534/16343 17830/18536/16345 18455/19161/16970 18452/19158/16967\r\nf 17830/18536/16345 17834/18540/16349 18456/19162/16971 18455/19161/16970\r\nf 18457/19163/16972 18458/19164/16973 18459/19165/16974 18460/19166/16975\r\nf 18458/19164/16973 18453/19159/16968 18456/19162/16971 18459/19165/16974\r\nf 17834/18540/16349 17836/18542/16351 18459/19165/16974 18456/19162/16971\r\nf 17836/18542/16351 17840/18546/16355 18460/19166/16975 18459/19165/16974\r\nf 18461/19167/16976 18462/19168/16977 18463/19169/16978 18464/19170/16979\r\nf 18462/19168/16977 18457/19163/16972 18460/19166/16975 18463/19169/16978\r\nf 17840/18546/16355 17842/18548/16357 18463/19169/16978 18460/19166/16975\r\nf 17842/18548/16357 17846/18552/16361 18464/19170/16979 18463/19169/16978\r\nf 18465/19171/16980 18466/19172/16981 18467/19173/16982 18468/19174/16983\r\nf 18466/19172/16981 18461/19167/16976 18464/19170/16979 18467/19173/16982\r\nf 17846/18552/16361 17848/18554/16363 18467/19173/16982 18464/19170/16979\r\nf 17848/18554/16363 17852/18558/16367 18468/19174/16983 18467/19173/16982\r\nf 18469/19175/16984 18470/19176/16985 18471/19177/16986 18472/19178/16987\r\nf 18470/19176/16985 18465/19171/16980 18468/19174/16983 18471/19177/16986\r\nf 17852/18558/16367 17854/18560/16369 18471/19177/16986 18468/19174/16983\r\nf 17854/18560/16369 17858/18564/16373 18472/19178/16987 18471/19177/16986\r\nf 18473/19179/16988 18474/19180/16989 18475/19181/16990 18476/19182/16991\r\nf 18474/19180/16989 18469/19175/16984 18472/19178/16987 18475/19181/16990\r\nf 17858/18564/16373 17860/18566/16375 18475/19181/16990 18472/19178/16987\r\nf 17860/18566/16375 17864/18570/16379 18476/19182/16991 18475/19181/16990\r\nf 17632/18338/16147 18477/19183/16992 18478/19184/16993 17633/18339/16148\r\nf 18477/19183/16992 18473/19179/16988 18476/19182/16991 18478/19184/16993\r\nf 17864/18570/16379 17866/18572/16381 18478/19184/16993 18476/19182/16991\r\nf 17866/18572/16381 17636/18342/16151 17633/18339/16148 18478/19184/16993\r\nf 18479/19185/16994 18480/19186/16995 18481/19187/16996 18482/19188/16997\r\nf 18480/19186/16995 17661/18367/16176 17660/18366/16175 18481/19187/16996\r\nf 17659/18365/16174 17899/18605/16414 18481/19187/16996 17660/18366/16175\r\nf 17899/18605/16414 17903/18609/16418 18482/19188/16997 18481/19187/16996\r\nf 18483/19189/16998 18484/19190/16999 18485/19191/17000 18486/19192/17001\r\nf 18484/19190/16999 18479/19185/16994 18482/19188/16997 18485/19191/17000\r\nf 17903/18609/16418 18487/19193/17002 18485/19191/17000 18482/19188/16997\r\nf 18487/19193/17002 18488/19194/17003 18486/19192/17001 18485/19191/17000\r\nf 18489/19195/17004 18490/19196/17005 18491/19197/17006 18492/19198/17007\r\nf 18490/19196/17005 18483/19189/16998 18486/19192/17001 18491/19197/17006\r\nf 18488/19194/17003 18493/19199/17008 18491/19197/17006 18486/19192/17001\r\nf 18493/19199/17008 18494/19200/17009 18492/19198/17007 18491/19197/17006\r\nf 17662/18368/16177 18495/19201/17010 18496/19202/17011 17663/18369/16178\r\nf 18495/19201/17010 18489/19195/17004 18492/19198/17007 18496/19202/17011\r\nf 18494/19200/17009 18497/19203/17012 18496/19202/17011 18492/19198/17007\r\nf 18497/19203/17012 17666/18372/16181 17663/18369/16178 18496/19202/17011\r\nf 18498/19204/17013 18499/19205/17014 18500/19206/17015 18501/19207/17016\r\nf 18499/19205/17014 17670/18376/16185 17669/18375/16184 18500/19206/17015\r\nf 17668/18374/16183 18502/19208/17017 18500/19206/17015 17669/18375/16184\r\nf 18502/19208/17017 18503/19209/17018 18501/19207/17016 18500/19206/17015\r\nf 18504/19210/17019 18505/19211/17020 18506/19212/17021 18507/19213/17022\r\nf 18505/19211/17020 18498/19204/17013 18501/19207/17016 18506/19212/17021\r\nf 18503/19209/17018 18508/19214/17023 18506/19212/17021 18501/19207/17016\r\nf 18508/19214/17023 18509/19215/17024 18507/19213/17022 18506/19212/17021\r\nf 18510/19216/17025 18511/19217/17026 18512/19218/17027 18513/19219/17028\r\nf 18511/19217/17026 18504/19210/17019 18507/19213/17022 18512/19218/17027\r\nf 18509/19215/17024 18514/19220/17029 18512/19218/17027 18507/19213/17022\r\nf 18514/19220/17029 18515/19221/17030 18513/19219/17028 18512/19218/17027\r\nf 18516/19222/17031 18517/19223/17032 18518/19224/17033 18519/19225/17034\r\nf 18517/19223/17032 18510/19216/17025 18513/19219/17028 18518/19224/17033\r\nf 18515/19221/17030 18520/19226/17035 18518/19224/17033 18513/19219/17028\r\nf 18520/19226/17035 18521/19227/17036 18519/19225/17034 18518/19224/17033\r\nf 18522/19228/17037 18523/19229/17038 18524/19230/17039 18525/19231/17040\r\nf 18523/19229/17038 18516/19222/17031 18519/19225/17034 18524/19230/17039\r\nf 18521/19227/17036 18526/19232/17041 18524/19230/17039 18519/19225/17034\r\nf 18526/19232/17041 18527/19233/17042 18525/19231/17040 18524/19230/17039\r\nf 18528/19234/17043 18529/19235/17044 18530/19236/17045 18531/19237/17046\r\nf 18529/19235/17044 18522/19228/17037 18525/19231/17040 18530/19236/17045\r\nf 18527/19233/17042 18532/19238/17047 18530/19236/17045 18525/19231/17040\r\nf 18532/19238/17047 18533/19239/17048 18531/19237/17046 18530/19236/17045\r\nf 18374/19080/16889 18534/19240/17049 18535/19241/17050 18375/19081/16890\r\nf 18534/19240/17049 18528/19234/17043 18531/19237/17046 18535/19241/17050\r\nf 18533/19239/17048 18536/19242/17051 18535/19241/17050 18531/19237/17046\r\nf 18536/19242/17051 18376/19082/16891 18375/19081/16890 18535/19241/17050\r\nf 17904/18610/16419 18537/19243/17052 18538/19244/17053 17905/18611/16420\r\nf 18537/19243/17052 18539/19245/17054 18540/19246/17055 18538/19244/17053\r\nf 18541/19247/17056 18542/19248/17057 18538/19244/17053 18540/19246/17055\r\nf 18542/19248/17057 17908/18614/16423 17905/18611/16420 18538/19244/17053\r\nf 17919/18625/16434 18543/19249/17058 18544/19250/17059 17920/18626/16435\r\nf 18543/19249/17058 18545/19251/17060 18546/19252/17061 18544/19250/17059\r\nf 18547/19253/17062 18548/19254/17063 18544/19250/17059 18546/19252/17061\r\nf 18548/19254/17063 17921/18627/16436 17920/18626/16435 18544/19250/17059\r\nf 17922/18628/16437 18549/19255/17064 18550/19256/17065 17923/18629/16438\r\nf 18549/19255/17064 18551/19257/17066 18552/19258/17067 18550/19256/17065\r\nf 18539/19245/17054 18537/19243/17052 18550/19256/17065 18552/19258/17067\r\nf 18537/19243/17052 17904/18610/16419 17923/18629/16438 18550/19256/17065\r\nf 17921/18627/16436 18548/19254/17063 18553/19259/17068 17932/18638/16447\r\nf 18548/19254/17063 18547/19253/17062 18554/19260/17069 18553/19259/17068\r\nf 18555/19261/17070 18556/19262/17071 18553/19259/17068 18554/19260/17069\r\nf 18556/19262/17071 17933/18639/16448 17932/18638/16447 18553/19259/17068\r\nf 17934/18640/16449 18557/19263/17072 18558/19264/17073 17935/18641/16450\r\nf 18557/19263/17072 18559/19265/17074 18560/19266/17075 18558/19264/17073\r\nf 18551/19257/17066 18549/19255/17064 18558/19264/17073 18560/19266/17075\r\nf 18549/19255/17064 17922/18628/16437 17935/18641/16450 18558/19264/17073\r\nf 17933/18639/16448 18556/19262/17071 18561/19267/17076 17944/18650/16459\r\nf 18556/19262/17071 18555/19261/17070 18562/19268/17077 18561/19267/17076\r\nf 18563/19269/17078 18564/19270/17079 18561/19267/17076 18562/19268/17077\r\nf 18564/19270/17079 17945/18651/16460 17944/18650/16459 18561/19267/17076\r\nf 17946/18652/16461 18565/19271/17080 18566/19272/17081 17947/18653/16462\r\nf 18565/19271/17080 18567/19273/17082 18568/19274/17083 18566/19272/17081\r\nf 18559/19265/17074 18557/19263/17072 18566/19272/17081 18568/19274/17083\r\nf 18557/19263/17072 17934/18640/16449 17947/18653/16462 18566/19272/17081\r\nf 17945/18651/16460 18564/19270/17079 18569/19275/17084 17956/18662/16471\r\nf 18564/19270/17079 18563/19269/17078 18570/19276/17085 18569/19275/17084\r\nf 18571/19277/17086 18572/19278/17087 18569/19275/17084 18570/19276/17085\r\nf 18572/19278/17087 17957/18663/16472 17956/18662/16471 18569/19275/17084\r\nf 17958/18664/16473 18573/19279/17088 18574/19280/17089 17959/18665/16474\r\nf 18573/19279/17088 18575/19281/17090 18576/19282/17091 18574/19280/17089\r\nf 18567/19273/17082 18565/19271/17080 18574/19280/17089 18576/19282/17091\r\nf 18565/19271/17080 17946/18652/16461 17959/18665/16474 18574/19280/17089\r\nf 17957/18663/16472 18572/19278/17087 18577/19283/17092 17968/18674/16483\r\nf 18572/19278/17087 18571/19277/17086 18578/19284/17093 18577/19283/17092\r\nf 18579/19285/17094 18580/19286/17095 18577/19283/17092 18578/19284/17093\r\nf 18580/19286/17095 17969/18675/16484 17968/18674/16483 18577/19283/17092\r\nf 17970/18676/16485 18581/19287/17096 18582/19288/17097 17971/18677/16486\r\nf 18581/19287/17096 18583/19289/17098 18584/19290/17099 18582/19288/17097\r\nf 18575/19281/17090 18573/19279/17088 18582/19288/17097 18584/19290/17099\r\nf 18573/19279/17088 17958/18664/16473 17971/18677/16486 18582/19288/17097\r\nf 17969/18675/16484 18580/19286/17095 18585/19291/17100 17980/18686/16495\r\nf 18580/19286/17095 18579/19285/17094 18586/19292/17101 18585/19291/17100\r\nf 18587/19293/17102 18588/19294/17103 18585/19291/17100 18586/19292/17101\r\nf 18588/19294/17103 17981/18687/16496 17980/18686/16495 18585/19291/17100\r\nf 17982/18688/16497 18589/19295/17104 18590/19296/17105 17983/18689/16498\r\nf 18589/19295/17104 18591/19297/17106 18592/19298/17107 18590/19296/17105\r\nf 18583/19289/17098 18581/19287/17096 18590/19296/17105 18592/19298/17107\r\nf 18581/19287/17096 17970/18676/16485 17983/18689/16498 18590/19296/17105\r\nf 17981/18687/16496 18588/19294/17103 18593/19299/17108 17992/18698/16507\r\nf 18588/19294/17103 18587/19293/17102 18594/19300/17109 18593/19299/17108\r\nf 18595/19301/17110 18596/19302/17111 18593/19299/17108 18594/19300/17109\r\nf 18596/19302/17111 17993/18699/16508 17992/18698/16507 18593/19299/17108\r\nf 17994/18700/16509 18597/19303/17112 18598/19304/17113 17995/18701/16510\r\nf 18597/19303/17112 18599/19305/17114 18600/19306/17115 18598/19304/17113\r\nf 18591/19297/17106 18589/19295/17104 18598/19304/17113 18600/19306/17115\r\nf 18589/19295/17104 17982/18688/16497 17995/18701/16510 18598/19304/17113\r\nf 17993/18699/16508 18596/19302/17111 18601/19307/17116 18004/18710/16519\r\nf 18596/19302/17111 18595/19301/17110 18602/19308/17117 18601/19307/17116\r\nf 18603/19309/17118 18604/19310/17119 18601/19307/17116 18602/19308/17117\r\nf 18604/19310/17119 18005/18711/16520 18004/18710/16519 18601/19307/17116\r\nf 18006/18712/16521 18605/19311/17120 18606/19312/17121 18007/18713/16522\r\nf 18605/19311/17120 18607/19313/17122 18608/19314/17123 18606/19312/17121\r\nf 18599/19305/17114 18597/19303/17112 18606/19312/17121 18608/19314/17123\r\nf 18597/19303/17112 17994/18700/16509 18007/18713/16522 18606/19312/17121\r\nf 18005/18711/16520 18604/19310/17119 18609/19315/17124 18016/18722/16531\r\nf 18604/19310/17119 18603/19309/17118 18610/19316/17125 18609/19315/17124\r\nf 18611/19317/17126 18612/19318/17127 18609/19315/17124 18610/19316/17125\r\nf 18612/19318/17127 18017/18723/16532 18016/18722/16531 18609/19315/17124\r\nf 18018/18724/16533 18613/19319/17128 18614/19320/17129 18019/18725/16534\r\nf 18613/19319/17128 18615/19321/17130 18616/19322/17131 18614/19320/17129\r\nf 18607/19313/17122 18605/19311/17120 18614/19320/17129 18616/19322/17131\r\nf 18605/19311/17120 18006/18712/16521 18019/18725/16534 18614/19320/17129\r\nf 18017/18723/16532 18612/19318/17127 18617/19323/17132 18028/18734/16543\r\nf 18612/19318/17127 18611/19317/17126 18618/19324/17133 18617/19323/17132\r\nf 18619/19325/17134 18620/19326/17135 18617/19323/17132 18618/19324/17133\r\nf 18620/19326/17135 18029/18735/16544 18028/18734/16543 18617/19323/17132\r\nf 18030/18736/16545 18621/19327/17136 18622/19328/17137 18031/18737/16546\r\nf 18621/19327/17136 18623/19329/17138 18624/19330/17139 18622/19328/17137\r\nf 18615/19321/17130 18613/19319/17128 18622/19328/17137 18624/19330/17139\r\nf 18613/19319/17128 18018/18724/16533 18031/18737/16546 18622/19328/17137\r\nf 18029/18735/16544 18620/19326/17135 18625/19331/17140 18040/18746/16555\r\nf 18620/19326/17135 18619/19325/17134 18626/19332/17141 18625/19331/17140\r\nf 18627/19333/17142 18628/19334/17143 18625/19331/17140 18626/19332/17141\r\nf 18628/19334/17143 18041/18747/16556 18040/18746/16555 18625/19331/17140\r\nf 18042/18748/16557 18629/19335/17144 18630/19336/17145 18043/18749/16558\r\nf 18629/19335/17144 18631/19337/17146 18632/19338/17147 18630/19336/17145\r\nf 18623/19329/17138 18621/19327/17136 18630/19336/17145 18632/19338/17147\r\nf 18621/19327/17136 18030/18736/16545 18043/18749/16558 18630/19336/17145\r\nf 18041/18747/16556 18628/19334/17143 18633/19339/17148 18052/18758/16567\r\nf 18628/19334/17143 18627/19333/17142 18634/19340/17149 18633/19339/17148\r\nf 17675/18381/16190 17672/18378/16187 18633/19339/17148 18634/19340/17149\r\nf 17672/18378/16187 17671/18377/16186 18052/18758/16567 18633/19339/17148\r\nf 18053/18759/16568 18635/19341/17150 18636/19342/17151 18054/18760/16569\r\nf 18635/19341/17150 17888/18594/16403 18637/19343/17152 18636/19342/17151\r\nf 18631/19337/17146 18629/19335/17144 18636/19342/17151 18637/19343/17152\r\nf 18629/19335/17144 18042/18748/16557 18054/18760/16569 18636/19342/17151\r\nf 18061/18767/16576 18638/19344/17153 18639/19345/17154 18062/18768/16577\r\nf 18638/19344/17153 17890/18596/16405 17889/18595/16404 18639/19345/17154\r\nf 17888/18594/16403 18635/19341/17150 18639/19345/17154 17889/18595/16404\r\nf 18635/19341/17150 18053/18759/16568 18062/18768/16577 18639/19345/17154\r\nf 18073/18779/16588 18640/19346/17155 18641/19347/17156 18074/18780/16589\r\nf 18640/19346/17155 17872/18578/16387 17881/18587/16396 18641/19347/17156\r\nf 17685/18391/16200 17683/18389/16198 18641/19347/17156 17881/18587/16396\r\nf 17683/18389/16198 17568/18274/16083 18074/18780/16589 18641/19347/17156\r\nf 18083/18789/16598 18642/19348/17157 18643/19349/17158 18084/18790/16599\r\nf 18642/19348/17157 17874/18580/16389 17873/18579/16388 18643/19349/17158\r\nf 17872/18578/16387 18640/19346/17155 18643/19349/17158 17873/18579/16388\r\nf 18640/19346/17155 18073/18779/16588 18084/18790/16599 18643/19349/17158\r\nf 17703/18409/16218 17702/18408/16217 18644/19350/17159 18093/18799/16608\r\nf 17702/18408/16217 17701/18407/16216 17877/18583/16392 18644/19350/17159\r\nf 17868/18574/16383 18645/19351/17160 18644/19350/17159 17877/18583/16392\r\nf 18645/19351/17160 18094/18800/16609 18093/18799/16608 18644/19350/17159\r\nf 18095/18801/16610 18646/19352/17161 18647/19353/17162 18096/18802/16611\r\nf 18646/19352/17161 18648/19354/17163 18649/19355/17164 18647/19353/17162\r\nf 17874/18580/16389 18642/19348/17157 18647/19353/17162 18649/19355/17164\r\nf 18642/19348/17157 18083/18789/16598 18096/18802/16611 18647/19353/17162\r\nf 18094/18800/16609 18645/19351/17160 18650/19356/17165 18105/18811/16620\r\nf 18645/19351/17160 17868/18574/16383 17867/18573/16382 18650/19356/17165\r\nf 17862/18568/16377 18651/19357/17166 18650/19356/17165 17867/18573/16382\r\nf 18651/19357/17166 18106/18812/16621 18105/18811/16620 18650/19356/17165\r\nf 18107/18813/16622 18652/19358/17167 18653/19359/17168 18108/18814/16623\r\nf 18652/19358/17167 18654/19360/17169 18655/19361/17170 18653/19359/17168\r\nf 18648/19354/17163 18646/19352/17161 18653/19359/17168 18655/19361/17170\r\nf 18646/19352/17161 18095/18801/16610 18108/18814/16623 18653/19359/17168\r\nf 18106/18812/16621 18651/19357/17166 18656/19362/17171 18117/18823/16632\r\nf 18651/19357/17166 17862/18568/16377 17861/18567/16376 18656/19362/17171\r\nf 17856/18562/16371 18657/19363/17172 18656/19362/17171 17861/18567/16376\r\nf 18657/19363/17172 18118/18824/16633 18117/18823/16632 18656/19362/17171\r\nf 18119/18825/16634 18658/19364/17173 18659/19365/17174 18120/18826/16635\r\nf 18658/19364/17173 18660/19366/17175 18661/19367/17176 18659/19365/17174\r\nf 18654/19360/17169 18652/19358/17167 18659/19365/17174 18661/19367/17176\r\nf 18652/19358/17167 18107/18813/16622 18120/18826/16635 18659/19365/17174\r\nf 18118/18824/16633 18657/19363/17172 18662/19368/17177 18129/18835/16644\r\nf 18657/19363/17172 17856/18562/16371 17855/18561/16370 18662/19368/17177\r\nf 17850/18556/16365 18663/19369/17178 18662/19368/17177 17855/18561/16370\r\nf 18663/19369/17178 18130/18836/16645 18129/18835/16644 18662/19368/17177\r\nf 18131/18837/16646 18664/19370/17179 18665/19371/17180 18132/18838/16647\r\nf 18664/19370/17179 18666/19372/17181 18667/19373/17182 18665/19371/17180\r\nf 18660/19366/17175 18658/19364/17173 18665/19371/17180 18667/19373/17182\r\nf 18658/19364/17173 18119/18825/16634 18132/18838/16647 18665/19371/17180\r\nf 18130/18836/16645 18663/19369/17178 18668/19374/17183 18141/18847/16656\r\nf 18663/19369/17178 17850/18556/16365 17849/18555/16364 18668/19374/17183\r\nf 17844/18550/16359 18669/19375/17184 18668/19374/17183 17849/18555/16364\r\nf 18669/19375/17184 18142/18848/16657 18141/18847/16656 18668/19374/17183\r\nf 18143/18849/16658 18670/19376/17185 18671/19377/17186 18144/18850/16659\r\nf 18670/19376/17185 18672/19378/17187 18673/19379/17188 18671/19377/17186\r\nf 18666/19372/17181 18664/19370/17179 18671/19377/17186 18673/19379/17188\r\nf 18664/19370/17179 18131/18837/16646 18144/18850/16659 18671/19377/17186\r\nf 18142/18848/16657 18669/19375/17184 18674/19380/17189 18153/18859/16668\r\nf 18669/19375/17184 17844/18550/16359 17843/18549/16358 18674/19380/17189\r\nf 17838/18544/16353 18675/19381/17190 18674/19380/17189 17843/18549/16358\r\nf 18675/19381/17190 18154/18860/16669 18153/18859/16668 18674/19380/17189\r\nf 18155/18861/16670 18676/19382/17191 18677/19383/17192 18156/18862/16671\r\nf 18676/19382/17191 18678/19384/17193 18679/19385/17194 18677/19383/17192\r\nf 18672/19378/17187 18670/19376/17185 18677/19383/17192 18679/19385/17194\r\nf 18670/19376/17185 18143/18849/16658 18156/18862/16671 18677/19383/17192\r\nf 18154/18860/16669 18675/19381/17190 18680/19386/17195 18165/18871/16680\r\nf 18675/19381/17190 17838/18544/16353 17837/18543/16352 18680/19386/17195\r\nf 17832/18538/16347 18681/19387/17196 18680/19386/17195 17837/18543/16352\r\nf 18681/19387/17196 18166/18872/16681 18165/18871/16680 18680/19386/17195\r\nf 18167/18873/16682 18682/19388/17197 18683/19389/17198 18168/18874/16683\r\nf 18682/19388/17197 18684/19390/17199 18685/19391/17200 18683/19389/17198\r\nf 18678/19384/17193 18676/19382/17191 18683/19389/17198 18685/19391/17200\r\nf 18676/19382/17191 18155/18861/16670 18168/18874/16683 18683/19389/17198\r\nf 18166/18872/16681 18681/19387/17196 18686/19392/17201 18177/18883/16692\r\nf 18681/19387/17196 17832/18538/16347 17831/18537/16346 18686/19392/17201\r\nf 17826/18532/16341 18687/19393/17202 18686/19392/17201 17831/18537/16346\r\nf 18687/19393/17202 18178/18884/16693 18177/18883/16692 18686/19392/17201\r\nf 18179/18885/16694 18688/19394/17203 18689/19395/17204 18180/18886/16695\r\nf 18688/19394/17203 18690/19396/17205 18691/19397/17206 18689/19395/17204\r\nf 18684/19390/17199 18682/19388/17197 18689/19395/17204 18691/19397/17206\r\nf 18682/19388/17197 18167/18873/16682 18180/18886/16695 18689/19395/17204\r\nf 18178/18884/16693 18687/19393/17202 18692/19398/17207 18189/18895/16704\r\nf 18687/19393/17202 17826/18532/16341 17825/18531/16340 18692/19398/17207\r\nf 17820/18526/16335 18693/19399/17208 18692/19398/17207 17825/18531/16340\r\nf 18693/19399/17208 18190/18896/16705 18189/18895/16704 18692/19398/17207\r\nf 18191/18897/16706 18694/19400/17209 18695/19401/17210 18192/18898/16707\r\nf 18694/19400/17209 18696/19402/17211 18697/19403/17212 18695/19401/17210\r\nf 18690/19396/17205 18688/19394/17203 18695/19401/17210 18697/19403/17212\r\nf 18688/19394/17203 18179/18885/16694 18192/18898/16707 18695/19401/17210\r\nf 18190/18896/16705 18693/19399/17208 18698/19404/17213 18201/18907/16716\r\nf 18693/19399/17208 17820/18526/16335 17819/18525/16334 18698/19404/17213\r\nf 17814/18520/16329 18699/19405/17214 18698/19404/17213 17819/18525/16334\r\nf 18699/19405/17214 18202/18908/16717 18201/18907/16716 18698/19404/17213\r\nf 18203/18909/16718 18700/19406/17215 18701/19407/17216 18204/18910/16719\r\nf 18700/19406/17215 18702/19408/17217 18703/19409/17218 18701/19407/17216\r\nf 18696/19402/17211 18694/19400/17209 18701/19407/17216 18703/19409/17218\r\nf 18694/19400/17209 18191/18897/16706 18204/18910/16719 18701/19407/17216\r\nf 18202/18908/16717 18699/19405/17214 18704/19410/17219 18213/18919/16728\r\nf 18699/19405/17214 17814/18520/16329 17813/18519/16328 18704/19410/17219\r\nf 17808/18514/16323 18705/19411/17220 18704/19410/17219 17813/18519/16328\r\nf 18705/19411/17220 18214/18920/16729 18213/18919/16728 18704/19410/17219\r\nf 18215/18921/16730 18706/19412/17221 18707/19413/17222 18216/18922/16731\r\nf 18706/19412/17221 18708/19414/17223 18709/19415/17224 18707/19413/17222\r\nf 18702/19408/17217 18700/19406/17215 18707/19413/17222 18709/19415/17224\r\nf 18700/19406/17215 18203/18909/16718 18216/18922/16731 18707/19413/17222\r\nf 18214/18920/16729 18705/19411/17220 18710/19416/17225 18225/18931/16740\r\nf 18705/19411/17220 17808/18514/16323 17807/18513/16322 18710/19416/17225\r\nf 17802/18508/16317 18711/19417/17226 18710/19416/17225 17807/18513/16322\r\nf 18711/19417/17226 18226/18932/16741 18225/18931/16740 18710/19416/17225\r\nf 18227/18933/16742 18712/19418/17227 18713/19419/17228 18228/18934/16743\r\nf 18712/19418/17227 18714/19420/17229 18715/19421/17230 18713/19419/17228\r\nf 18708/19414/17223 18706/19412/17221 18713/19419/17228 18715/19421/17230\r\nf 18706/19412/17221 18215/18921/16730 18228/18934/16743 18713/19419/17228\r\nf 18226/18932/16741 18711/19417/17226 18716/19422/17231 18237/18943/16752\r\nf 18711/19417/17226 17802/18508/16317 17801/18507/16316 18716/19422/17231\r\nf 17796/18502/16311 18717/19423/17232 18716/19422/17231 17801/18507/16316\r\nf 18717/19423/17232 18238/18944/16753 18237/18943/16752 18716/19422/17231\r\nf 18239/18945/16754 18718/19424/17233 18719/19425/17234 18240/18946/16755\r\nf 18718/19424/17233 18720/19426/17235 18721/19427/17236 18719/19425/17234\r\nf 18714/19420/17229 18712/19418/17227 18719/19425/17234 18721/19427/17236\r\nf 18712/19418/17227 18227/18933/16742 18240/18946/16755 18719/19425/17234\r\nf 18238/18944/16753 18717/19423/17232 18722/19428/17237 18249/18955/16764\r\nf 18717/19423/17232 17796/18502/16311 17795/18501/16310 18722/19428/17237\r\nf 17790/18496/16305 18723/19429/17238 18722/19428/17237 17795/18501/16310\r\nf 18723/19429/17238 18250/18956/16765 18249/18955/16764 18722/19428/17237\r\nf 18251/18957/16766 18724/19430/17239 18725/19431/17240 18252/18958/16767\r\nf 18724/19430/17239 18726/19432/17241 18727/19433/17242 18725/19431/17240\r\nf 18720/19426/17235 18718/19424/17233 18725/19431/17240 18727/19433/17242\r\nf 18718/19424/17233 18239/18945/16754 18252/18958/16767 18725/19431/17240\r\nf 18250/18956/16765 18723/19429/17238 18728/19434/17243 18261/18967/16776\r\nf 18723/19429/17238 17790/18496/16305 17789/18495/16304 18728/19434/17243\r\nf 17784/18490/16299 18729/19435/17244 18728/19434/17243 17789/18495/16304\r\nf 18729/19435/17244 18262/18968/16777 18261/18967/16776 18728/19434/17243\r\nf 18263/18969/16778 18730/19436/17245 18731/19437/17246 18264/18970/16779\r\nf 18730/19436/17245 18732/19438/17247 18733/19439/17248 18731/19437/17246\r\nf 18726/19432/17241 18724/19430/17239 18731/19437/17246 18733/19439/17248\r\nf 18724/19430/17239 18251/18957/16766 18264/18970/16779 18731/19437/17246\r\nf 18262/18968/16777 18729/19435/17244 18734/19440/17249 18273/18979/16788\r\nf 18729/19435/17244 17784/18490/16299 17783/18489/16298 18734/19440/17249\r\nf 17778/18484/16293 18735/19441/17250 18734/19440/17249 17783/18489/16298\r\nf 18735/19441/17250 18274/18980/16789 18273/18979/16788 18734/19440/17249\r\nf 18275/18981/16790 18736/19442/17251 18737/19443/17252 18276/18982/16791\r\nf 18736/19442/17251 18738/19444/17253 18739/19445/17254 18737/19443/17252\r\nf 18732/19438/17247 18730/19436/17245 18737/19443/17252 18739/19445/17254\r\nf 18730/19436/17245 18263/18969/16778 18276/18982/16791 18737/19443/17252\r\nf 18274/18980/16789 18735/19441/17250 18740/19446/17255 18285/18991/16800\r\nf 18735/19441/17250 17778/18484/16293 17777/18483/16292 18740/19446/17255\r\nf 17772/18478/16287 18741/19447/17256 18740/19446/17255 17777/18483/16292\r\nf 18741/19447/17256 18286/18992/16801 18285/18991/16800 18740/19446/17255\r\nf 17569/18275/16084 18742/19448/17257 18743/19449/17258 18287/18993/16802\r\nf 18742/19448/17257 17759/18465/16274 18744/19450/17259 18743/19449/17258\r\nf 18738/19444/17253 18736/19442/17251 18743/19449/17258 18744/19450/17259\r\nf 18736/19442/17251 18275/18981/16790 18287/18993/16802 18743/19449/17258\r\nf 18286/18992/16801 18741/19447/17256 18745/19451/17260 18294/19000/16809\r\nf 18741/19447/17256 17772/18478/16287 17771/18477/16286 18745/19451/17260\r\nf 17767/18473/16282 18746/19452/17261 18745/19451/17260 17771/18477/16286\r\nf 18746/19452/17261 18295/19001/16810 18294/19000/16809 18745/19451/17260\r\nf 17577/18283/16092 18747/19453/17262 18748/19454/17263 17572/18278/16087\r\nf 18747/19453/17262 17761/18467/16276 17760/18466/16275 18748/19454/17263\r\nf 17759/18465/16274 18742/19448/17257 18748/19454/17263 17760/18466/16275\r\nf 18742/19448/17257 17569/18275/16084 17572/18278/16087 18748/19454/17263\r\nf 18295/19001/16810 18746/19452/17261 18749/19455/17264 18300/19006/16815\r\nf 18746/19452/17261 17767/18473/16282 17766/18472/16281 18749/19455/17264\r\nf 17765/18471/16280 18750/19456/17265 18749/19455/17264 17766/18472/16281\r\nf 18750/19456/17265 18301/19007/16816 18300/19006/16815 18749/19455/17264\r\nf 18302/19008/16817 18751/19457/17266 18752/19458/17267 18303/19009/16818\r\nf 18751/19457/17266 18753/19459/17268 18754/19460/17269 18752/19458/17267\r\nf 17761/18467/16276 18747/19453/17262 18752/19458/17267 18754/19460/17269\r\nf 18747/19453/17262 17577/18283/16092 18303/19009/16818 18752/19458/17267\r\nf 18301/19007/16816 18750/19456/17265 18755/19461/17270 18310/19016/16825\r\nf 18750/19456/17265 17765/18471/16280 18756/19462/17271 18755/19461/17270\r\nf 18757/19463/17272 18758/19464/17273 18755/19461/17270 18756/19462/17271\r\nf 18758/19464/17273 18311/19017/16826 18310/19016/16825 18755/19461/17270\r\nf 17593/18299/16108 17715/18421/16230 18759/19465/17274 18322/19028/16837\r\nf 17715/18421/16230 17714/18420/16229 18760/19466/17275 18759/19465/17274\r\nf 18761/19467/17276 18762/19468/17277 18759/19465/17274 18760/19466/17275\r\nf 18762/19468/17277 18323/19029/16838 18322/19028/16837 18759/19465/17274\r\nf 18324/19030/16839 18763/19469/17278 18764/19470/17279 18325/19031/16840\r\nf 18763/19469/17278 17727/18433/16242 17726/18432/16241 18764/19470/17279\r\nf 17720/18426/16235 17717/18423/16232 18764/19470/17279 17726/18432/16241\r\nf 17717/18423/16232 17716/18422/16231 18325/19031/16840 18764/19470/17279\r\nf 18323/19029/16838 18762/19468/17277 18765/19471/17280 18334/19040/16849\r\nf 18762/19468/17277 18761/19467/17276 18766/19472/17281 18765/19471/17280\r\nf 18767/19473/17282 18768/19474/17283 18765/19471/17280 18766/19472/17281\r\nf 18768/19474/17283 18335/19041/16850 18334/19040/16849 18765/19471/17280\r\nf 18336/19042/16851 18769/19475/17284 18770/19476/17285 18337/19043/16852\r\nf 18769/19475/17284 18771/19477/17286 18772/19478/17287 18770/19476/17285\r\nf 17727/18433/16242 18763/19469/17278 18770/19476/17285 18772/19478/17287\r\nf 18763/19469/17278 18324/19030/16839 18337/19043/16852 18770/19476/17285\r\nf 18335/19041/16850 18768/19474/17283 18773/19479/17288 18346/19052/16861\r\nf 18768/19474/17283 18767/19473/17282 18774/19480/17289 18773/19479/17288\r\nf 18775/19481/17290 18776/19482/17291 18773/19479/17288 18774/19480/17289\r\nf 18776/19482/17291 18347/19053/16862 18346/19052/16861 18773/19479/17288\r\nf 18348/19054/16863 18777/19483/17292 18778/19484/17293 18349/19055/16864\r\nf 18777/19483/17292 18779/19485/17294 18780/19486/17295 18778/19484/17293\r\nf 18771/19477/17286 18769/19475/17284 18778/19484/17293 18780/19486/17295\r\nf 18769/19475/17284 18336/19042/16851 18349/19055/16864 18778/19484/17293\r\nf 18347/19053/16862 18776/19482/17291 18781/19487/17296 18358/19064/16873\r\nf 18776/19482/17291 18775/19481/17290 18782/19488/17297 18781/19487/17296\r\nf 18783/19489/17298 18784/19490/17299 18781/19487/17296 18782/19488/17297\r\nf 18784/19490/17299 18359/19065/16874 18358/19064/16873 18781/19487/17296\r\nf 17596/18302/16111 18785/19491/17300 18786/19492/17301 18360/19066/16875\r\nf 18785/19491/17300 18787/19493/17302 18788/19494/17303 18786/19492/17301\r\nf 18779/19485/17294 18777/19483/17292 18786/19492/17301 18788/19494/17303\r\nf 18777/19483/17292 18348/19054/16863 18360/19066/16875 18786/19492/17301\r\nf 18359/19065/16874 18784/19490/17299 18789/19495/17304 18365/19071/16880\r\nf 18784/19490/17299 18783/19489/17298 18790/19496/17305 18789/19495/17304\r\nf 18791/19497/17306 18792/19498/17307 18789/19495/17304 18790/19496/17305\r\nf 18792/19498/17307 17609/18315/16124 18365/19071/16880 18789/19495/17304\r\nf 17604/18310/16119 18793/19499/17308 18794/19500/17309 17599/18305/16114\r\nf 18793/19499/17308 18795/19501/17310 18796/19502/17311 18794/19500/17309\r\nf 18787/19493/17302 18785/19491/17300 18794/19500/17309 18796/19502/17311\r\nf 18785/19491/17300 17596/18302/16111 17599/18305/16114 18794/19500/17309\r\nf 17609/18315/16124 18792/19498/17307 18797/19503/17312 17610/18316/16125\r\nf 18792/19498/17307 18791/19497/17306 18798/19504/17313 18797/19503/17312\r\nf 18799/19505/17314 18800/19506/17315 18797/19503/17312 18798/19504/17313\r\nf 18800/19506/17315 17611/18317/16126 17610/18316/16125 18797/19503/17312\r\nf 17908/18614/16423 18542/19248/17057 18801/19507/17316 18366/19072/16881\r\nf 18542/19248/17057 18541/19247/17056 18802/19508/17317 18801/19507/17316\r\nf 18795/19501/17310 18793/19499/17308 18801/19507/17316 18802/19508/17317\r\nf 18793/19499/17308 17604/18310/16119 18366/19072/16881 18801/19507/17316\r\nf 17611/18317/16126 18800/19506/17315 18803/19509/17318 18371/19077/16886\r\nf 18800/19506/17315 18799/19505/17314 18804/19510/17319 18803/19509/17318\r\nf 18545/19251/17060 18543/19249/17058 18803/19509/17318 18804/19510/17319\r\nf 18543/19249/17058 17919/18625/16434 18371/19077/16886 18803/19509/17318\r\nf 17614/18320/16129 18805/19511/17320 18806/19512/17321 18372/19078/16887\r\nf 18805/19511/17320 18541/19247/17056 18540/19246/17055 18806/19512/17321\r\nf 18539/19245/17054 18807/19513/17322 18806/19512/17321 18540/19246/17055\r\nf 18807/19513/17322 18374/19080/16889 18372/19078/16887 18806/19512/17321\r\nf 18376/19082/16891 18808/19514/17323 18809/19515/17324 18377/19083/16892\r\nf 18808/19514/17323 18810/19516/17325 18811/19517/17326 18809/19515/17324\r\nf 18812/19518/17327 18813/19519/17328 18809/19515/17324 18811/19517/17326\r\nf 18813/19519/17328 17618/18324/16133 18377/19083/16892 18809/19515/17324\r\nf 17622/18328/16137 18814/19520/17329 18815/19521/17330 17617/18323/16132\r\nf 18814/19520/17329 18795/19501/17310 18802/19508/17317 18815/19521/17330\r\nf 18541/19247/17056 18805/19511/17320 18815/19521/17330 18802/19508/17317\r\nf 18805/19511/17320 17614/18320/16129 17617/18323/16132 18815/19521/17330\r\nf 17618/18324/16133 18813/19519/17328 18816/19522/17331 17619/18325/16134\r\nf 18813/19519/17328 18812/19518/17327 18817/19523/17332 18816/19522/17331\r\nf 18818/19524/17333 18819/19525/17334 18816/19522/17331 18817/19523/17332\r\nf 18819/19525/17334 17620/18326/16135 17619/18325/16134 18816/19522/17331\r\nf 18378/19084/16893 18820/19526/17335 18821/19527/17336 18379/19085/16894\r\nf 18820/19526/17335 18787/19493/17302 18796/19502/17311 18821/19527/17336\r\nf 18795/19501/17310 18814/19520/17329 18821/19527/17336 18796/19502/17311\r\nf 18814/19520/17329 17622/18328/16137 18379/19085/16894 18821/19527/17336\r\nf 17620/18326/16135 18819/19525/17334 18822/19528/17337 18382/19088/16897\r\nf 18819/19525/17334 18818/19524/17333 18823/19529/17338 18822/19528/17337\r\nf 18824/19530/17339 18825/19531/17340 18822/19528/17337 18823/19529/17338\r\nf 18825/19531/17340 18383/19089/16898 18382/19088/16897 18822/19528/17337\r\nf 18384/19090/16899 18826/19532/17341 18827/19533/17342 18385/19091/16900\r\nf 18826/19532/17341 18779/19485/17294 18788/19494/17303 18827/19533/17342\r\nf 18787/19493/17302 18820/19526/17335 18827/19533/17342 18788/19494/17303\r\nf 18820/19526/17335 18378/19084/16893 18385/19091/16900 18827/19533/17342\r\nf 18383/19089/16898 18825/19531/17340 18828/19534/17343 18388/19094/16903\r\nf 18825/19531/17340 18824/19530/17339 18829/19535/17344 18828/19534/17343\r\nf 18830/19536/17345 18831/19537/17346 18828/19534/17343 18829/19535/17344\r\nf 18831/19537/17346 18389/19095/16904 18388/19094/16903 18828/19534/17343\r\nf 18390/19096/16905 18832/19538/17347 18833/19539/17348 18391/19097/16906\r\nf 18832/19538/17347 18771/19477/17286 18780/19486/17295 18833/19539/17348\r\nf 18779/19485/17294 18826/19532/17341 18833/19539/17348 18780/19486/17295\r\nf 18826/19532/17341 18384/19090/16899 18391/19097/16906 18833/19539/17348\r\nf 18389/19095/16904 18831/19537/17346 18834/19540/17349 18394/19100/16909\r\nf 18831/19537/17346 18830/19536/17345 18835/19541/17350 18834/19540/17349\r\nf 18836/19542/17351 18837/19543/17352 18834/19540/17349 18835/19541/17350\r\nf 18837/19543/17352 18395/19101/16910 18394/19100/16909 18834/19540/17349\r\nf 17729/18435/16244 17728/18434/16243 18838/19544/17353 18396/19102/16911\r\nf 17728/18434/16243 17727/18433/16242 18772/19478/17287 18838/19544/17353\r\nf 18771/19477/17286 18832/19538/17347 18838/19544/17353 18772/19478/17287\r\nf 18832/19538/17347 18390/19096/16905 18396/19102/16911 18838/19544/17353\r\nf 18395/19101/16910 18837/19543/17352 18839/19545/17354 18399/19105/16914\r\nf 18837/19543/17352 18836/19542/17351 18840/19546/17355 18839/19545/17354\r\nf 17734/18440/16249 17731/18437/16246 18839/19545/17354 18840/19546/17355\r\nf 17731/18437/16246 17730/18436/16245 18399/19105/16914 18839/19545/17354\r\nf 17750/18456/16265 17749/18455/16264 18841/19547/17356 18402/19108/16917\r\nf 17749/18455/16264 17706/18412/16221 17721/18427/16236 18841/19547/17356\r\nf 17720/18426/16235 17723/18429/16238 18841/19547/17356 17721/18427/16236\r\nf 17723/18429/16238 17722/18428/16237 18402/19108/16917 18841/19547/17356\r\nf 17623/18329/16138 17762/18468/16277 18842/19548/17357 18407/19113/16922\r\nf 17762/18468/16277 17761/18467/16276 18754/19460/17269 18842/19548/17357\r\nf 18753/19459/17268 18843/19549/17358 18842/19548/17357 18754/19460/17269\r\nf 18843/19549/17358 18409/19115/16924 18407/19113/16922 18842/19548/17357\r\nf 18411/19117/16926 18844/19550/17359 18845/19551/17360 18412/19118/16927\r\nf 18844/19550/17359 18757/19463/17272 18756/19462/17271 18845/19551/17360\r\nf 17765/18471/16280 17763/18469/16278 18845/19551/17360 18756/19462/17271\r\nf 17763/18469/16278 17627/18333/16142 18412/19118/16927 18845/19551/17360\r\nf 18413/19119/16928 18846/19552/17361 18847/19553/17362 18414/19120/16929\r\nf 18846/19552/17361 18738/19444/17253 18744/19450/17259 18847/19553/17362\r\nf 17759/18465/16274 17757/18463/16272 18847/19553/17362 18744/19450/17259\r\nf 17757/18463/16272 17631/18337/16146 18414/19120/16929 18847/19553/17362\r\nf 18417/19123/16932 18848/19554/17363 18849/19555/17364 18418/19124/16933\r\nf 18848/19554/17363 18732/19438/17247 18739/19445/17254 18849/19555/17364\r\nf 18738/19444/17253 18846/19552/17361 18849/19555/17364 18739/19445/17254\r\nf 18846/19552/17361 18413/19119/16928 18418/19124/16933 18849/19555/17364\r\nf 18421/19127/16936 18850/19556/17365 18851/19557/17366 18422/19128/16937\r\nf 18850/19556/17365 18726/19432/17241 18733/19439/17248 18851/19557/17366\r\nf 18732/19438/17247 18848/19554/17363 18851/19557/17366 18733/19439/17248\r\nf 18848/19554/17363 18417/19123/16932 18422/19128/16937 18851/19557/17366\r\nf 18425/19131/16940 18852/19558/17367 18853/19559/17368 18426/19132/16941\r\nf 18852/19558/17367 18720/19426/17235 18727/19433/17242 18853/19559/17368\r\nf 18726/19432/17241 18850/19556/17365 18853/19559/17368 18727/19433/17242\r\nf 18850/19556/17365 18421/19127/16936 18426/19132/16941 18853/19559/17368\r\nf 18429/19135/16944 18854/19560/17369 18855/19561/17370 18430/19136/16945\r\nf 18854/19560/17369 18714/19420/17229 18721/19427/17236 18855/19561/17370\r\nf 18720/19426/17235 18852/19558/17367 18855/19561/17370 18721/19427/17236\r\nf 18852/19558/17367 18425/19131/16940 18430/19136/16945 18855/19561/17370\r\nf 18433/19139/16948 18856/19562/17371 18857/19563/17372 18434/19140/16949\r\nf 18856/19562/17371 18708/19414/17223 18715/19421/17230 18857/19563/17372\r\nf 18714/19420/17229 18854/19560/17369 18857/19563/17372 18715/19421/17230\r\nf 18854/19560/17369 18429/19135/16944 18434/19140/16949 18857/19563/17372\r\nf 18437/19143/16952 18858/19564/17373 18859/19565/17374 18438/19144/16953\r\nf 18858/19564/17373 18702/19408/17217 18709/19415/17224 18859/19565/17374\r\nf 18708/19414/17223 18856/19562/17371 18859/19565/17374 18709/19415/17224\r\nf 18856/19562/17371 18433/19139/16948 18438/19144/16953 18859/19565/17374\r\nf 18441/19147/16956 18860/19566/17375 18861/19567/17376 18442/19148/16957\r\nf 18860/19566/17375 18696/19402/17211 18703/19409/17218 18861/19567/17376\r\nf 18702/19408/17217 18858/19564/17373 18861/19567/17376 18703/19409/17218\r\nf 18858/19564/17373 18437/19143/16952 18442/19148/16957 18861/19567/17376\r\nf 18445/19151/16960 18862/19568/17377 18863/19569/17378 18446/19152/16961\r\nf 18862/19568/17377 18690/19396/17205 18697/19403/17212 18863/19569/17378\r\nf 18696/19402/17211 18860/19566/17375 18863/19569/17378 18697/19403/17212\r\nf 18860/19566/17375 18441/19147/16956 18446/19152/16961 18863/19569/17378\r\nf 18449/19155/16964 18864/19570/17379 18865/19571/17380 18450/19156/16965\r\nf 18864/19570/17379 18684/19390/17199 18691/19397/17206 18865/19571/17380\r\nf 18690/19396/17205 18862/19568/17377 18865/19571/17380 18691/19397/17206\r\nf 18862/19568/17377 18445/19151/16960 18450/19156/16965 18865/19571/17380\r\nf 18453/19159/16968 18866/19572/17381 18867/19573/17382 18454/19160/16969\r\nf 18866/19572/17381 18678/19384/17193 18685/19391/17200 18867/19573/17382\r\nf 18684/19390/17199 18864/19570/17379 18867/19573/17382 18685/19391/17200\r\nf 18864/19570/17379 18449/19155/16964 18454/19160/16969 18867/19573/17382\r\nf 18457/19163/16972 18868/19574/17383 18869/19575/17384 18458/19164/16973\r\nf 18868/19574/17383 18672/19378/17187 18679/19385/17194 18869/19575/17384\r\nf 18678/19384/17193 18866/19572/17381 18869/19575/17384 18679/19385/17194\r\nf 18866/19572/17381 18453/19159/16968 18458/19164/16973 18869/19575/17384\r\nf 18461/19167/16976 18870/19576/17385 18871/19577/17386 18462/19168/16977\r\nf 18870/19576/17385 18666/19372/17181 18673/19379/17188 18871/19577/17386\r\nf 18672/19378/17187 18868/19574/17383 18871/19577/17386 18673/19379/17188\r\nf 18868/19574/17383 18457/19163/16972 18462/19168/16977 18871/19577/17386\r\nf 18465/19171/16980 18872/19578/17387 18873/19579/17388 18466/19172/16981\r\nf 18872/19578/17387 18660/19366/17175 18667/19373/17182 18873/19579/17388\r\nf 18666/19372/17181 18870/19576/17385 18873/19579/17388 18667/19373/17182\r\nf 18870/19576/17385 18461/19167/16976 18466/19172/16981 18873/19579/17388\r\nf 18469/19175/16984 18874/19580/17389 18875/19581/17390 18470/19176/16985\r\nf 18874/19580/17389 18654/19360/17169 18661/19367/17176 18875/19581/17390\r\nf 18660/19366/17175 18872/19578/17387 18875/19581/17390 18661/19367/17176\r\nf 18872/19578/17387 18465/19171/16980 18470/19176/16985 18875/19581/17390\r\nf 18473/19179/16988 18876/19582/17391 18877/19583/17392 18474/19180/16989\r\nf 18876/19582/17391 18648/19354/17163 18655/19361/17170 18877/19583/17392\r\nf 18654/19360/17169 18874/19580/17389 18877/19583/17392 18655/19361/17170\r\nf 18874/19580/17389 18469/19175/16984 18474/19180/16989 18877/19583/17392\r\nf 17632/18338/16147 17875/18581/16390 18878/19584/17393 18477/19183/16992\r\nf 17875/18581/16390 17874/18580/16389 18649/19355/17164 18878/19584/17393\r\nf 18648/19354/17163 18876/19582/17391 18878/19584/17393 18649/19355/17164\r\nf 18876/19582/17391 18473/19179/16988 18477/19183/16992 18878/19584/17393\r\nf 17652/18358/16167 18879/19585/17394 18880/19586/17395 17648/18354/16163\r\nf 18879/19585/17394 17687/18393/16202 17686/18392/16201 18880/19586/17395\r\nf 17685/18391/16200 17879/18585/16394 18880/19586/17395 17686/18392/16201\r\nf 17879/18585/16394 17646/18352/16161 17648/18354/16163 18880/19586/17395\r\nf 18479/19185/16994 18881/19587/17396 18882/19588/17397 18480/19186/16995\r\nf 18881/19587/17396 18631/19337/17146 18637/19343/17152 18882/19588/17397\r\nf 17888/18594/16403 17886/18592/16401 18882/19588/17397 18637/19343/17152\r\nf 17886/18592/16401 17661/18367/16176 18480/19186/16995 18882/19588/17397\r\nf 18483/19189/16998 18883/19589/17398 18884/19590/17399 18484/19190/16999\r\nf 18883/19589/17398 18623/19329/17138 18632/19338/17147 18884/19590/17399\r\nf 18631/19337/17146 18881/19587/17396 18884/19590/17399 18632/19338/17147\r\nf 18881/19587/17396 18479/19185/16994 18484/19190/16999 18884/19590/17399\r\nf 17903/18609/16418 17902/18608/16417 18885/19591/17400 18487/19193/17002\r\nf 17902/18608/16417 17901/18607/16416 18886/19592/17401 18885/19591/17400\r\nf 18887/19593/17402 18888/19594/17403 18885/19591/17400 18886/19592/17401\r\nf 18888/19594/17403 18488/19194/17003 18487/19193/17002 18885/19591/17400\r\nf 18489/19195/17004 18889/19595/17404 18890/19596/17405 18490/19196/17005\r\nf 18889/19595/17404 18615/19321/17130 18624/19330/17139 18890/19596/17405\r\nf 18623/19329/17138 18883/19589/17398 18890/19596/17405 18624/19330/17139\r\nf 18883/19589/17398 18483/19189/16998 18490/19196/17005 18890/19596/17405\r\nf 18488/19194/17003 18888/19594/17403 18891/19597/17406 18493/19199/17008\r\nf 18888/19594/17403 18887/19593/17402 18892/19598/17407 18891/19597/17406\r\nf 18893/19599/17408 18894/19600/17409 18891/19597/17406 18892/19598/17407\r\nf 18894/19600/17409 18494/19200/17009 18493/19199/17008 18891/19597/17406\r\nf 17662/18368/16177 18895/19601/17410 18896/19602/17411 18495/19201/17010\r\nf 18895/19601/17410 18607/19313/17122 18616/19322/17131 18896/19602/17411\r\nf 18615/19321/17130 18889/19595/17404 18896/19602/17411 18616/19322/17131\r\nf 18889/19595/17404 18489/19195/17004 18495/19201/17010 18896/19602/17411\r\nf 18494/19200/17009 18894/19600/17409 18897/19603/17412 18497/19203/17012\r\nf 18894/19600/17409 18893/19599/17408 18898/19604/17413 18897/19603/17412\r\nf 18899/19605/17414 18900/19606/17415 18897/19603/17412 18898/19604/17413\r\nf 18900/19606/17415 17666/18372/16181 18497/19203/17012 18897/19603/17412\r\nf 17670/18376/16185 18901/19607/17416 18902/19608/17417 17665/18371/16180\r\nf 18901/19607/17416 18599/19305/17114 18608/19314/17123 18902/19608/17417\r\nf 18607/19313/17122 18895/19601/17410 18902/19608/17417 18608/19314/17123\r\nf 18895/19601/17410 17662/18368/16177 17665/18371/16180 18902/19608/17417\r\nf 17666/18372/16181 18900/19606/17415 18903/19609/17418 17667/18373/16182\r\nf 18900/19606/17415 18899/19605/17414 18904/19610/17419 18903/19609/17418\r\nf 18905/19611/17420 18906/19612/17421 18903/19609/17418 18904/19610/17419\r\nf 18906/19612/17421 17668/18374/16183 17667/18373/16182 18903/19609/17418\r\nf 18498/19204/17013 18907/19613/17422 18908/19614/17423 18499/19205/17014\r\nf 18907/19613/17422 18591/19297/17106 18600/19306/17115 18908/19614/17423\r\nf 18599/19305/17114 18901/19607/17416 18908/19614/17423 18600/19306/17115\r\nf 18901/19607/17416 17670/18376/16185 18499/19205/17014 18908/19614/17423\r\nf 17668/18374/16183 18906/19612/17421 18909/19615/17424 18502/19208/17017\r\nf 18906/19612/17421 18905/19611/17420 18910/19616/17425 18909/19615/17424\r\nf 18911/19617/17426 18912/19618/17427 18909/19615/17424 18910/19616/17425\r\nf 18912/19618/17427 18503/19209/17018 18502/19208/17017 18909/19615/17424\r\nf 18504/19210/17019 18913/19619/17428 18914/19620/17429 18505/19211/17020\r\nf 18913/19619/17428 18583/19289/17098 18592/19298/17107 18914/19620/17429\r\nf 18591/19297/17106 18907/19613/17422 18914/19620/17429 18592/19298/17107\r\nf 18907/19613/17422 18498/19204/17013 18505/19211/17020 18914/19620/17429\r\nf 18503/19209/17018 18912/19618/17427 18915/19621/17430 18508/19214/17023\r\nf 18912/19618/17427 18911/19617/17426 18916/19622/17431 18915/19621/17430\r\nf 18917/19623/17432 18918/19624/17433 18915/19621/17430 18916/19622/17431\r\nf 18918/19624/17433 18509/19215/17024 18508/19214/17023 18915/19621/17430\r\nf 18510/19216/17025 18919/19625/17434 18920/19626/17435 18511/19217/17026\r\nf 18919/19625/17434 18575/19281/17090 18584/19290/17099 18920/19626/17435\r\nf 18583/19289/17098 18913/19619/17428 18920/19626/17435 18584/19290/17099\r\nf 18913/19619/17428 18504/19210/17019 18511/19217/17026 18920/19626/17435\r\nf 18509/19215/17024 18918/19624/17433 18921/19627/17436 18514/19220/17029\r\nf 18918/19624/17433 18917/19623/17432 18922/19628/17437 18921/19627/17436\r\nf 18923/19629/17438 18924/19630/17439 18921/19627/17436 18922/19628/17437\r\nf 18924/19630/17439 18515/19221/17030 18514/19220/17029 18921/19627/17436\r\nf 18516/19222/17031 18925/19631/17440 18926/19632/17441 18517/19223/17032\r\nf 18925/19631/17440 18567/19273/17082 18576/19282/17091 18926/19632/17441\r\nf 18575/19281/17090 18919/19625/17434 18926/19632/17441 18576/19282/17091\r\nf 18919/19625/17434 18510/19216/17025 18517/19223/17032 18926/19632/17441\r\nf 18515/19221/17030 18924/19630/17439 18927/19633/17442 18520/19226/17035\r\nf 18924/19630/17439 18923/19629/17438 18928/19634/17443 18927/19633/17442\r\nf 18929/19635/17444 18930/19636/17445 18927/19633/17442 18928/19634/17443\r\nf 18930/19636/17445 18521/19227/17036 18520/19226/17035 18927/19633/17442\r\nf 18522/19228/17037 18931/19637/17446 18932/19638/17447 18523/19229/17038\r\nf 18931/19637/17446 18559/19265/17074 18568/19274/17083 18932/19638/17447\r\nf 18567/19273/17082 18925/19631/17440 18932/19638/17447 18568/19274/17083\r\nf 18925/19631/17440 18516/19222/17031 18523/19229/17038 18932/19638/17447\r\nf 18521/19227/17036 18930/19636/17445 18933/19639/17448 18526/19232/17041\r\nf 18930/19636/17445 18929/19635/17444 18934/19640/17449 18933/19639/17448\r\nf 18935/19641/17450 18936/19642/17451 18933/19639/17448 18934/19640/17449\r\nf 18936/19642/17451 18527/19233/17042 18526/19232/17041 18933/19639/17448\r\nf 18528/19234/17043 18937/19643/17452 18938/19644/17453 18529/19235/17044\r\nf 18937/19643/17452 18551/19257/17066 18560/19266/17075 18938/19644/17453\r\nf 18559/19265/17074 18931/19637/17446 18938/19644/17453 18560/19266/17075\r\nf 18931/19637/17446 18522/19228/17037 18529/19235/17044 18938/19644/17453\r\nf 18527/19233/17042 18936/19642/17451 18939/19645/17454 18532/19238/17047\r\nf 18936/19642/17451 18935/19641/17450 18940/19646/17455 18939/19645/17454\r\nf 18941/19647/17456 18942/19648/17457 18939/19645/17454 18940/19646/17455\r\nf 18942/19648/17457 18533/19239/17048 18532/19238/17047 18939/19645/17454\r\nf 18374/19080/16889 18807/19513/17322 18943/19649/17458 18534/19240/17049\r\nf 18807/19513/17322 18539/19245/17054 18552/19258/17067 18943/19649/17458\r\nf 18551/19257/17066 18937/19643/17452 18943/19649/17458 18552/19258/17067\r\nf 18937/19643/17452 18528/19234/17043 18534/19240/17049 18943/19649/17458\r\nf 18533/19239/17048 18942/19648/17457 18944/19650/17459 18536/19242/17051\r\nf 18942/19648/17457 18941/19647/17456 18945/19651/17460 18944/19650/17459\r\nf 18810/19516/17325 18808/19514/17323 18944/19650/17459 18945/19651/17460\r\nf 18808/19514/17323 18376/19082/16891 18536/19242/17051 18944/19650/17459\r\nf 18946/19652/17461 18947/19653/17462 18948/19654/17463 18949/19655/17464\r\nf 18947/19653/17462 17917/18623/16432 17914/18620/16429 18948/19654/17463\r\nf 17913/18619/16428 18950/19656/17465 18948/19654/17463 17914/18620/16429\r\nf 18950/19656/17465 18951/19657/17466 18949/19655/17464 18948/19654/17463\r\nf 18951/19657/17466 18950/19656/17465 18952/19658/17467 18953/19659/17468\r\nf 18950/19656/17465 17913/18619/16428 17929/18635/16444 18952/19658/17467\r\nf 17928/18634/16443 18954/19660/17469 18952/19658/17467 17929/18635/16444\r\nf 18954/19660/17469 18955/19661/17470 18953/19659/17468 18952/19658/17467\r\nf 18955/19661/17470 18954/19660/17469 18956/19662/17471 18957/19663/17472\r\nf 18954/19660/17469 17928/18634/16443 17941/18647/16456 18956/19662/17471\r\nf 17940/18646/16455 18958/19664/17473 18956/19662/17471 17941/18647/16456\r\nf 18958/19664/17473 18959/19665/17474 18957/19663/17472 18956/19662/17471\r\nf 18959/19665/17474 18958/19664/17473 18960/19666/17475 18961/19667/17476\r\nf 18958/19664/17473 17940/18646/16455 17953/18659/16468 18960/19666/17475\r\nf 17952/18658/16467 18962/19668/17477 18960/19666/17475 17953/18659/16468\r\nf 18962/19668/17477 18963/19669/17478 18961/19667/17476 18960/19666/17475\r\nf 18963/19669/17478 18962/19668/17477 18964/19670/17479 18965/19671/17480\r\nf 18962/19668/17477 17952/18658/16467 17965/18671/16480 18964/19670/17479\r\nf 17964/18670/16479 18966/19672/17481 18964/19670/17479 17965/18671/16480\r\nf 18966/19672/17481 18967/19673/17482 18965/19671/17480 18964/19670/17479\r\nf 18967/19673/17482 18966/19672/17481 18968/19674/17483 18969/19675/17484\r\nf 18966/19672/17481 17964/18670/16479 17977/18683/16492 18968/19674/17483\r\nf 17976/18682/16491 18970/19676/17485 18968/19674/17483 17977/18683/16492\r\nf 18970/19676/17485 18971/19677/17486 18969/19675/17484 18968/19674/17483\r\nf 18971/19677/17486 18970/19676/17485 18972/19678/17487 18973/19679/17488\r\nf 18970/19676/17485 17976/18682/16491 17989/18695/16504 18972/19678/17487\r\nf 17988/18694/16503 18974/19680/17489 18972/19678/17487 17989/18695/16504\r\nf 18974/19680/17489 18975/19681/17490 18973/19679/17488 18972/19678/17487\r\nf 18975/19681/17490 18974/19680/17489 18976/19682/17491 18977/19683/17492\r\nf 18974/19680/17489 17988/18694/16503 18001/18707/16516 18976/19682/17491\r\nf 18000/18706/16515 18978/19684/17493 18976/19682/17491 18001/18707/16516\r\nf 18978/19684/17493 18979/19685/17494 18977/19683/17492 18976/19682/17491\r\nf 18979/19685/17494 18978/19684/17493 18980/19686/17495 18981/19687/17496\r\nf 18978/19684/17493 18000/18706/16515 18013/18719/16528 18980/19686/17495\r\nf 18012/18718/16527 18982/19688/17497 18980/19686/17495 18013/18719/16528\r\nf 18982/19688/17497 18983/19689/17498 18981/19687/17496 18980/19686/17495\r\nf 18983/19689/17498 18982/19688/17497 18984/19690/17499 18985/19691/17500\r\nf 18982/19688/17497 18012/18718/16527 18025/18731/16540 18984/19690/17499\r\nf 18024/18730/16539 18986/19692/17501 18984/19690/17499 18025/18731/16540\r\nf 18986/19692/17501 18987/19693/17502 18985/19691/17500 18984/19690/17499\r\nf 18987/19693/17502 18986/19692/17501 18988/19694/17503 18989/19695/17504\r\nf 18986/19692/17501 18024/18730/16539 18037/18743/16552 18988/19694/17503\r\nf 18036/18742/16551 18990/19696/17505 18988/19694/17503 18037/18743/16552\r\nf 18990/19696/17505 18991/19697/17506 18989/19695/17504 18988/19694/17503\r\nf 18991/19697/17506 18990/19696/17505 18992/19698/17507 18993/19699/17508\r\nf 18990/19696/17505 18036/18742/16551 18049/18755/16564 18992/19698/17507\r\nf 18048/18754/16563 18994/19700/17509 18992/19698/17507 18049/18755/16564\r\nf 18994/19700/17509 18995/19701/17510 18993/19699/17508 18992/19698/17507\r\nf 18995/19701/17510 18994/19700/17509 18996/19702/17511 18997/19703/17512\r\nf 18994/19700/17509 18048/18754/16563 18059/18765/16574 18996/19702/17511\r\nf 17551/18257/16066 18998/19704/17513 18996/19702/17511 18059/18765/16574\r\nf 18998/19704/17513 18999/19705/17514 18997/19703/17512 18996/19702/17511\r\nf 18999/19705/17514 18998/19704/17513 19000/19706/17515 19001/19707/17516\r\nf 18998/19704/17513 17551/18257/16066 17554/18260/16069 19000/19706/17515\r\nf 17559/18265/16074 19002/19708/17517 19000/19706/17515 17554/18260/16069\r\nf 19002/19708/17517 19003/19709/17518 19001/19707/17516 19000/19706/17515\r\nf 19003/19709/17518 19002/19708/17517 19004/19710/17519 19005/19711/17520\r\nf 19002/19708/17517 17559/18265/16074 17558/18264/16073 19004/19710/17519\r\nf 17557/18263/16072 19006/19712/17521 19004/19710/17519 17558/18264/16073\r\nf 19006/19712/17521 19007/19713/17522 19005/19711/17520 19004/19710/17519\r\nf 19007/19713/17522 19006/19712/17521 19008/19714/17523 19009/19715/17524\r\nf 19006/19712/17521 17557/18263/16072 17682/18388/16197 19008/19714/17523\r\nf 17681/18387/16196 19010/19716/17525 19008/19714/17523 17682/18388/16197\r\nf 19010/19716/17525 17894/18600/16409 19009/19715/17524 19008/19714/17523\r\nf 17894/18600/16409 19010/19716/17525 19011/19717/17526 17895/18601/16410\r\nf 19010/19716/17525 17681/18387/16196 17680/18386/16195 19011/19717/17526\r\nf 17677/18383/16192 19012/19718/17527 19011/19717/17526 17680/18386/16195\r\nf 19012/19718/17527 17896/18602/16411 17895/18601/16410 19011/19717/17526\r\nf 17896/18602/16411 19012/19718/17527 19013/19719/17528 17900/18606/16415\r\nf 19012/19718/17527 17677/18383/16192 17676/18382/16191 19013/19719/17528\r\nf 17675/18381/16190 19014/19720/17529 19013/19719/17528 17676/18382/16191\r\nf 19014/19720/17529 17901/18607/16416 17900/18606/16415 19013/19719/17528\r\nf 17901/18607/16416 19014/19720/17529 19015/19721/17530 18886/19592/17401\r\nf 19014/19720/17529 17675/18381/16190 18634/19340/17149 19015/19721/17530\r\nf 18627/19333/17142 19016/19722/17531 19015/19721/17530 18634/19340/17149\r\nf 19016/19722/17531 18887/19593/17402 18886/19592/17401 19015/19721/17530\r\nf 18887/19593/17402 19016/19722/17531 19017/19723/17532 18892/19598/17407\r\nf 19016/19722/17531 18627/19333/17142 18626/19332/17141 19017/19723/17532\r\nf 18619/19325/17134 19018/19724/17533 19017/19723/17532 18626/19332/17141\r\nf 19018/19724/17533 18893/19599/17408 18892/19598/17407 19017/19723/17532\r\nf 18893/19599/17408 19018/19724/17533 19019/19725/17534 18898/19604/17413\r\nf 19018/19724/17533 18619/19325/17134 18618/19324/17133 19019/19725/17534\r\nf 18611/19317/17126 19020/19726/17535 19019/19725/17534 18618/19324/17133\r\nf 19020/19726/17535 18899/19605/17414 18898/19604/17413 19019/19725/17534\r\nf 18899/19605/17414 19020/19726/17535 19021/19727/17536 18904/19610/17419\r\nf 19020/19726/17535 18611/19317/17126 18610/19316/17125 19021/19727/17536\r\nf 18603/19309/17118 19022/19728/17537 19021/19727/17536 18610/19316/17125\r\nf 19022/19728/17537 18905/19611/17420 18904/19610/17419 19021/19727/17536\r\nf 18905/19611/17420 19022/19728/17537 19023/19729/17538 18910/19616/17425\r\nf 19022/19728/17537 18603/19309/17118 18602/19308/17117 19023/19729/17538\r\nf 18595/19301/17110 19024/19730/17539 19023/19729/17538 18602/19308/17117\r\nf 19024/19730/17539 18911/19617/17426 18910/19616/17425 19023/19729/17538\r\nf 18911/19617/17426 19024/19730/17539 19025/19731/17540 18916/19622/17431\r\nf 19024/19730/17539 18595/19301/17110 18594/19300/17109 19025/19731/17540\r\nf 18587/19293/17102 19026/19732/17541 19025/19731/17540 18594/19300/17109\r\nf 19026/19732/17541 18917/19623/17432 18916/19622/17431 19025/19731/17540\r\nf 18917/19623/17432 19026/19732/17541 19027/19733/17542 18922/19628/17437\r\nf 19026/19732/17541 18587/19293/17102 18586/19292/17101 19027/19733/17542\r\nf 18579/19285/17094 19028/19734/17543 19027/19733/17542 18586/19292/17101\r\nf 19028/19734/17543 18923/19629/17438 18922/19628/17437 19027/19733/17542\r\nf 18923/19629/17438 19028/19734/17543 19029/19735/17544 18928/19634/17443\r\nf 19028/19734/17543 18579/19285/17094 18578/19284/17093 19029/19735/17544\r\nf 18571/19277/17086 19030/19736/17545 19029/19735/17544 18578/19284/17093\r\nf 19030/19736/17545 18929/19635/17444 18928/19634/17443 19029/19735/17544\r\nf 18929/19635/17444 19030/19736/17545 19031/19737/17546 18934/19640/17449\r\nf 19030/19736/17545 18571/19277/17086 18570/19276/17085 19031/19737/17546\r\nf 18563/19269/17078 19032/19738/17547 19031/19737/17546 18570/19276/17085\r\nf 19032/19738/17547 18935/19641/17450 18934/19640/17449 19031/19737/17546\r\nf 18935/19641/17450 19032/19738/17547 19033/19739/17548 18940/19646/17455\r\nf 19032/19738/17547 18563/19269/17078 18562/19268/17077 19033/19739/17548\r\nf 18555/19261/17070 19034/19740/17549 19033/19739/17548 18562/19268/17077\r\nf 19034/19740/17549 18941/19647/17456 18940/19646/17455 19033/19739/17548\r\nf 18941/19647/17456 19034/19740/17549 19035/19741/17550 18945/19651/17460\r\nf 19034/19740/17549 18555/19261/17070 18554/19260/17069 19035/19741/17550\r\nf 18547/19253/17062 19036/19742/17551 19035/19741/17550 18554/19260/17069\r\nf 19036/19742/17551 18810/19516/17325 18945/19651/17460 19035/19741/17550\r\nf 18810/19516/17325 19036/19742/17551 19037/19743/17552 18811/19517/17326\r\nf 19036/19742/17551 18547/19253/17062 18546/19252/17061 19037/19743/17552\r\nf 18545/19251/17060 19038/19744/17553 19037/19743/17552 18546/19252/17061\r\nf 19038/19744/17553 18812/19518/17327 18811/19517/17326 19037/19743/17552\r\nf 18812/19518/17327 19038/19744/17553 19039/19745/17554 18817/19523/17332\r\nf 19038/19744/17553 18545/19251/17060 18804/19510/17319 19039/19745/17554\r\nf 18799/19505/17314 19040/19746/17555 19039/19745/17554 18804/19510/17319\r\nf 19040/19746/17555 18818/19524/17333 18817/19523/17332 19039/19745/17554\r\nf 18818/19524/17333 19040/19746/17555 19041/19747/17556 18823/19529/17338\r\nf 19040/19746/17555 18799/19505/17314 18798/19504/17313 19041/19747/17556\r\nf 18791/19497/17306 19042/19748/17557 19041/19747/17556 18798/19504/17313\r\nf 19042/19748/17557 18824/19530/17339 18823/19529/17338 19041/19747/17556\r\nf 18824/19530/17339 19042/19748/17557 19043/19749/17558 18829/19535/17344\r\nf 19042/19748/17557 18791/19497/17306 18790/19496/17305 19043/19749/17558\r\nf 18783/19489/17298 19044/19750/17559 19043/19749/17558 18790/19496/17305\r\nf 19044/19750/17559 18830/19536/17345 18829/19535/17344 19043/19749/17558\r\nf 18830/19536/17345 19044/19750/17559 19045/19751/17560 18835/19541/17350\r\nf 19044/19750/17559 18783/19489/17298 18782/19488/17297 19045/19751/17560\r\nf 18775/19481/17290 19046/19752/17561 19045/19751/17560 18782/19488/17297\r\nf 19046/19752/17561 18836/19542/17351 18835/19541/17350 19045/19751/17560\r\nf 18836/19542/17351 19046/19752/17561 19047/19753/17562 18840/19546/17355\r\nf 19046/19752/17561 18775/19481/17290 18774/19480/17289 19047/19753/17562\r\nf 18767/19473/17282 19048/19754/17563 19047/19753/17562 18774/19480/17289\r\nf 19048/19754/17563 17734/18440/16249 18840/19546/17355 19047/19753/17562\r\nf 17734/18440/16249 19048/19754/17563 19049/19755/17564 17735/18441/16250\r\nf 19048/19754/17563 18767/19473/17282 18766/19472/17281 19049/19755/17564\r\nf 18761/19467/17276 19050/19756/17565 19049/19755/17564 18766/19472/17281\r\nf 19050/19756/17565 17736/18442/16251 17735/18441/16250 19049/19755/17564\r\nf 17736/18442/16251 19050/19756/17565 19051/19757/17566 17741/18447/16256\r\nf 19050/19756/17565 18761/19467/17276 18760/19466/17275 19051/19757/17566\r\nf 17714/18420/16229 19052/19758/17567 19051/19757/17566 18760/19466/17275\r\nf 19052/19758/17567 17742/18448/16257 17741/18447/16256 19051/19757/17566\r\nf 17742/18448/16257 19052/19758/17567 19053/19759/17568 17753/18459/16268\r\nf 19052/19758/17567 17714/18420/16229 17713/18419/16228 19053/19759/17568\r\nf 17712/18418/16227 19054/19760/17569 19053/19759/17568 17713/18419/16228\r\nf 19054/19760/17569 17754/18460/16269 17753/18459/16268 19053/19759/17568\r\nf 17754/18460/16269 19054/19760/17569 19055/19761/17570 19056/19762/17571\r\nf 19054/19760/17569 17712/18418/16227 17710/18416/16225 19055/19761/17570\r\nf 17591/18297/16106 19057/19763/17572 19055/19761/17570 17710/18416/16225\r\nf 19057/19763/17572 19058/19764/17573 19056/19762/17571 19055/19761/17570\r\nf 19058/19764/17573 19057/19763/17572 19059/19765/17574 19060/19766/17575\r\nf 19057/19763/17572 17591/18297/16106 17588/18294/16103 19059/19765/17574\r\nf 17587/18293/16102 19061/19767/17576 19059/19765/17574 17588/18294/16103\r\nf 19061/19767/17576 19062/19768/17577 19060/19766/17575 19059/19765/17574\r\nf 19062/19768/17577 19061/19767/17576 19063/19769/17578 19064/19770/17579\r\nf 19061/19767/17576 17587/18293/16102 17590/18296/16105 19063/19769/17578\r\nf 17595/18301/16110 19065/19771/17580 19063/19769/17578 17590/18296/16105\r\nf 19065/19771/17580 19066/19772/17581 19064/19770/17579 19063/19769/17578\r\nf 19066/19772/17581 19065/19771/17580 19067/19773/17582 19068/19774/17583\r\nf 19065/19771/17580 17595/18301/16110 18319/19025/16834 19067/19773/17582\r\nf 18318/19024/16833 19069/19775/17584 19067/19773/17582 18319/19025/16834\r\nf 19069/19775/17584 19070/19776/17585 19068/19774/17583 19067/19773/17582\r\nf 19070/19776/17585 19069/19775/17584 19071/19777/17586 19072/19778/17587\r\nf 19069/19775/17584 18318/19024/16833 18331/19037/16846 19071/19777/17586\r\nf 18330/19036/16845 19073/19779/17588 19071/19777/17586 18331/19037/16846\r\nf 19073/19779/17588 19074/19780/17589 19072/19778/17587 19071/19777/17586\r\nf 19074/19780/17589 19073/19779/17588 19075/19781/17590 19076/19782/17591\r\nf 19073/19779/17588 18330/19036/16845 18343/19049/16858 19075/19781/17590\r\nf 18342/19048/16857 19077/19783/17592 19075/19781/17590 18343/19049/16858\r\nf 19077/19783/17592 19078/19784/17593 19076/19782/17591 19075/19781/17590\r\nf 19078/19784/17593 19077/19783/17592 19079/19785/17594 19080/19786/17595\r\nf 19077/19783/17592 18342/19048/16857 18355/19061/16870 19079/19785/17594\r\nf 18354/19060/16869 19081/19787/17596 19079/19785/17594 18355/19061/16870\r\nf 19081/19787/17596 19082/19788/17597 19080/19786/17595 19079/19785/17594\r\nf 19082/19788/17597 19081/19787/17596 19083/19789/17598 19084/19790/17599\r\nf 19081/19787/17596 18354/19060/16869 18363/19069/16878 19083/19789/17598\r\nf 17605/18311/16120 19085/19791/17600 19083/19789/17598 18363/19069/16878\r\nf 19085/19791/17600 19086/19792/17601 19084/19790/17599 19083/19789/17598\r\nf 19086/19792/17601 19085/19791/17600 19087/19793/17602 19088/19794/17603\r\nf 19085/19791/17600 17605/18311/16120 17608/18314/16123 19087/19793/17602\r\nf 17613/18319/16128 19089/19795/17604 19087/19793/17602 17608/18314/16123\r\nf 19089/19795/17604 19090/19796/17605 19088/19794/17603 19087/19793/17602\r\nf 19090/19796/17605 19089/19795/17604 19091/19797/17606 19092/19798/17607\r\nf 19089/19795/17604 17613/18319/16128 18369/19075/16884 19091/19797/17606\r\nf 17917/18623/16432 18947/19653/17462 19091/19797/17606 18369/19075/16884\r\nf 18947/19653/17462 18946/19652/17461 19092/19798/17607 19091/19797/17606\r\nv 185.91 44.27 2.46\r\nv 183.2 42.88 12.77\r\nv 183.2 43.03 12.77\r\nv 185.9 44.4 2.46\r\nv 180.93 41.45 24.21\r\nv 180.92 41.62 24.2\r\nv 181 42.35 24.19\r\nv 183.26 43.69 12.8\r\nv 185.95 45 2.52\r\nv 186.23 37.85 84.21\r\nv 190.32 38.3 94.39\r\nv 190.31 38.52 94.36\r\nv 186.22 38.07 84.18\r\nv 194.84 38.95 103.18\r\nv 194.83 39.18 103.16\r\nv 194.86 40.2 103.01\r\nv 190.35 39.55 94.22\r\nv 186.28 39.08 84.05\r\nv 179.42 39.14 44.32\r\nv 179.8 38.61 53.24\r\nv 179.79 38.81 53.22\r\nv 179.42 39.33 44.3\r\nv 180.99 38.3 62.51\r\nv 180.99 38.51 62.49\r\nv 181.05 39.44 62.38\r\nv 179.87 39.71 53.12\r\nv 179.5 40.18 44.22\r\nv 199.39 66.58 -18.15\r\nv 199.37 66.57 -18.16\r\nv 196.24 68.11 -12.79\r\nv 196.26 68.12 -12.78\r\nv 199.33 66.31 -18.19\r\nv 196.19 67.8 -12.83\r\nv 193.52 69.32 -7.34\r\nv 193.58 69.67 -7.3\r\nv 193.6 69.68 -7.29\r\nv 183.12 37.95 73.2\r\nv 183.12 38.17 73.18\r\nv 183.18 39.14 73.06\r\nv 179.73 40.14 34.68\r\nv 179.73 40.32 34.67\r\nv 179.81 41.12 34.62\r\nv 206.08 63.87 -27.71\r\nv 206.07 63.86 -27.73\r\nv 202.95 65.08 -23.46\r\nv 202.97 65.09 -23.44\r\nv 206.04 63.67 -27.75\r\nv 202.92 64.86 -23.49\r\nv 199.21 39.45 110.01\r\nv 199.2 39.68 109.99\r\nv 203.26 39.84 115.26\r\nv 203.25 40.07 115.25\r\nv 203.3 41.09 115.13\r\nv 199.23 40.7 109.85\r\nv 183.72 63.96 23.81\r\nv 184.6 68.59 23.74\r\nv 186.21 67.37 13.78\r\nv 185.5 63.19 13.6\r\nv 185.41 72.31 23.67\r\nv 186.87 70.72 13.92\r\nv 188.78 69.18 4.85\r\nv 188.26 66.18 4.56\r\nv 187.7 62.44 4.2\r\nv 199.53 82.08 96.58\r\nv 195.42 81.32 88.29\r\nv 193.93 76.13 89.02\r\nv 198.09 76.88 97.44\r\nv 191.67 80.31 78.72\r\nv 190.23 75.19 79.38\r\nv 188.85 68.81 80.2\r\nv 192.55 69.67 89.94\r\nv 196.77 70.39 98.48\r\nv 184.09 66.91 59.3\r\nv 185.31 72.81 58.64\r\nv 184.17 71.76 49.76\r\nv 183.02 66.1 50.35\r\nv 186.51 77.53 58.11\r\nv 185.28 76.3 49.29\r\nv 184.78 75.09 41.05\r\nv 183.74 70.76 41.44\r\nv 182.65 65.36 41.93\r\nv 198.52 60.09 -18.98\r\nv 198.25 57.43 -19.31\r\nv 194.85 57.6 -14.09\r\nv 195.17 60.66 -13.72\r\nv 198.02 54.89 -19.62\r\nv 194.58 54.68 -14.45\r\nv 191.58 54.46 -9.15\r\nv 191.88 57.77 -8.75\r\nv 192.27 61.24 -8.33\r\nv 188.67 78.96 68.29\r\nv 187.34 74.02 68.88\r\nv 186.03 67.85 69.62\r\nv 183.83 69.72 32.86\r\nv 182.83 64.67 33.17\r\nv 184.77 73.77 32.61\r\nv 208.51 58.57 -31.9\r\nv 208.35 56.93 -32.13\r\nv 208.3 56.93 -32.1\r\nv 208.46 58.58 -31.88\r\nv 208.2 55.36 -32.34\r\nv 208.15 55.36 -32.31\r\nv 207.83 55.35 -31.97\r\nv 207.99 56.96 -31.75\r\nv 208.15 58.64 -31.53\r\nv 182.22 53.56 23.99\r\nv 182.9 58.63 23.9\r\nv 184.83 58.38 13.4\r\nv 184.27 53.81 13.21\r\nv 187.17 58.14 3.78\r\nv 186.74 54.05 3.39\r\nv 191.57 62.24 90.99\r\nv 195.86 62.94 99.63\r\nv 187.82 61.48 81.15\r\nv 187.13 54.5 82.05\r\nv 190.96 55.16 92\r\nv 195.32 55.84 100.7\r\nv 181.99 59.59 51.03\r\nv 183.06 60.14 60.05\r\nv 181.66 59.16 42.49\r\nv 180.85 53.24 43.03\r\nv 181.18 53.4 51.68\r\nv 182.26 53.69 60.78\r\nv 184.99 60.77 70.47\r\nv 184.24 54.03 71.27\r\nv 181.9 58.86 33.52\r\nv 181.14 53.34 33.86\r\nv 197.85 52.95 -19.86\r\nv 194.41 52.44 -14.72\r\nv 197.73 51.44 -20.05\r\nv 194.29 50.7 -14.94\r\nv 191.26 49.96 -9.69\r\nv 191.39 51.93 -9.45\r\nv 208.08 54.16 -32.5\r\nv 208.03 54.16 -32.48\r\nv 208 53.23 -32.63\r\nv 207.94 53.23 -32.6\r\nv 207.62 53.17 -32.26\r\nv 207.71 54.13 -32.14\r\nv 206.82 40.14 119.32\r\nv 206.81 40.37 119.31\r\nv 209.31 40.34 121.94\r\nv 209.31 40.56 121.93\r\nv 209.38 41.58 121.85\r\nv 206.87 41.39 119.22\r\nv 209.57 89.61 106.68\r\nv 205.94 89.15 101.82\r\nv 205.91 89.12 101.84\r\nv 209.54 89.58 106.7\r\nv 202.03 88.56 95.5\r\nv 202 88.52 95.52\r\nv 201.69 87.78 95.64\r\nv 205.62 88.38 101.99\r\nv 209.29 88.84 106.88\r\nv 205.42 71.4 111.09\r\nv 201.18 70.96 105.49\r\nv 200.33 63.46 106.7\r\nv 204.61 63.85 112.35\r\nv 199.79 56.32 107.76\r\nv 204.05 56.67 113.36\r\nv 199.03 64.27 -18.45\r\nv 198.79 62.41 -18.69\r\nv 195.51 63.33 -13.39\r\nv 195.82 65.46 -13.13\r\nv 192.68 64.26 -7.97\r\nv 193.06 66.67 -7.67\r\nv 208.78 61.15 -31.55\r\nv 208.66 60 -31.71\r\nv 208.61 60.02 -31.68\r\nv 208.74 61.17 -31.53\r\nv 208.31 60.11 -31.33\r\nv 208.43 61.28 -31.17\r\nv 186.05 74.88 23.63\r\nv 187.38 73.04 14.02\r\nv 186.47 76.39 23.61\r\nv 187.72 74.4 14.08\r\nv 189.44 72.47 5.18\r\nv 189.18 71.26 5.05\r\nv 197.95 87.78 87.37\r\nv 197.92 87.75 87.38\r\nv 194.1 86.69 77.9\r\nv 194.07 86.66 77.91\r\nv 193.77 85.93 78\r\nv 197.61 87.01 87.49\r\nv 187.5 80.8 57.75\r\nv 186.19 79.44 48.96\r\nv 188.16 82.72 57.53\r\nv 186.79 81.28 48.77\r\nv 186.16 79.85 40.62\r\nv 185.61 78.09 40.77\r\nv 192.91 83.85 78.27\r\nv 189.79 82.38 67.88\r\nv 190.55 84.39 67.65\r\nv 185.51 76.57 32.44\r\nv 186 78.21 32.34\r\nv 202.35 77.47 104.25\r\nv 206.46 77.92 109.65\r\nv 207.56 83.13 108.33\r\nv 203.63 82.67 103.17\r\nv 183.89 50.31 13.07\r\nv 181.75 49.69 24.06\r\nv 186.45 50.92 3.09\r\nv 186.24 48.47 2.85\r\nv 183.62 47.58 12.96\r\nv 181.43 46.66 24.11\r\nv 190.69 49.77 92.76\r\nv 195.09 50.43 101.51\r\nv 186.77 49.17 82.74\r\nv 186.59 45 83.28\r\nv 190.57 45.55 93.36\r\nv 195.02 46.21 102.13\r\nv 180.66 48.66 52.18\r\nv 181.77 48.77 61.33\r\nv 180.33 48.73 43.44\r\nv 179.97 45.2 43.76\r\nv 180.32 44.97 52.57\r\nv 181.46 44.92 61.76\r\nv 183.8 48.88 71.89\r\nv 183.55 44.86 72.37\r\nv 180.63 49.12 34.12\r\nv 180.28 45.82 34.33\r\nv 199.53 50.91 108.51\r\nv 203.74 51.25 114.03\r\nv 199.43 46.69 109.07\r\nv 203.56 47.06 114.48\r\nv 197.54 48.82 -20.36\r\nv 194.07 47.69 -15.29\r\nv 194.06 47.78 -15.29\r\nv 197.53 48.91 -20.36\r\nv 191.01 46.55 -10.09\r\nv 191.01 46.66 -10.09\r\nv 191.04 47.14 -10.03\r\nv 194.09 48.21 -15.24\r\nv 197.56 49.28 -20.32\r\nv 207.85 51.63 -32.83\r\nv 207.81 51.61 -32.81\r\nv 207.8 51.66 -32.82\r\nv 207.85 51.67 -32.84\r\nv 207.48 51.52 -32.48\r\nv 207.47 51.57 -32.48\r\nv 207.49 51.81 -32.45\r\nv 207.82 51.89 -32.79\r\nv 207.87 51.9 -32.81\r\nv 214.69 83.79 115.8\r\nv 214.64 83.79 115.78\r\nv 213.69 78.58 117.45\r\nv 213.74 78.58 117.47\r\nv 213.75 83.71 114.89\r\nv 212.78 78.5 116.52\r\nv 211.78 71.95 118.21\r\nv 212.69 72.03 119.18\r\nv 212.74 72.03 119.2\r\nv 211.89 64.42 120.51\r\nv 211.95 64.42 120.54\r\nv 210.99 64.35 119.54\r\nv 210.4 57.12 120.49\r\nv 211.3 57.18 121.45\r\nv 211.35 57.18 121.48\r\nv 215.9 90.27 113.71\r\nv 215.87 90.28 113.69\r\nv 215.84 90.25 113.71\r\nv 215.89 90.25 113.73\r\nv 215.08 90.2 112.85\r\nv 215.06 90.17 112.88\r\nv 214.9 89.43 113.11\r\nv 215.71 89.51 113.95\r\nv 215.75 89.51 113.97\r\nv 210.92 51.76 121.99\r\nv 210.97 51.76 122.02\r\nv 210.02 51.7 121.04\r\nv 209.76 47.52 121.38\r\nv 210.65 47.58 122.31\r\nv 210.7 47.58 122.33\r\nv 208.96 62.58 -31.34\r\nv 208.96 62.58 -31.36\r\nv 208.91 62.6 -31.33\r\nv 208.92 62.6 -31.32\r\nv 208.94 62.41 -31.38\r\nv 208.89 62.43 -31.35\r\nv 208.59 62.57 -30.99\r\nv 208.61 62.74 -30.97\r\nv 208.63 62.74 -30.96\r\nv 202.05 57.26 -24.46\r\nv 201.84 55.09 -24.74\r\nv 202.27 59.54 -24.17\r\nv 205.51 59.09 -28.36\r\nv 205.31 57.12 -28.61\r\nv 205.13 55.25 -28.86\r\nv 201.69 53.44 -24.95\r\nv 201.58 52.14 -25.11\r\nv 204.99 53.82 -29.05\r\nv 204.89 52.71 -29.19\r\nv 202.49 61.52 -23.92\r\nv 202.68 63.11 -23.72\r\nv 205.86 62.17 -27.95\r\nv 205.7 60.8 -28.13\r\nv 204.72 50.78 -29.43\r\nv 201.39 49.91 -25.38\r\nv 201.38 49.98 -25.38\r\nv 204.71 50.84 -29.43\r\nv 201.41 50.3 -25.34\r\nv 204.74 51.12 -29.4\r\nv 183.25 42.83 12.78\r\nv 180.98 41.39 24.21\r\nv 185.96 44.22 2.47\r\nv 186.03 44.21 2.49\r\nv 183.33 42.82 12.8\r\nv 181.05 41.38 24.23\r\nv 190.37 38.22 94.38\r\nv 194.88 38.87 103.16\r\nv 186.28 37.77 84.2\r\nv 186.35 37.76 84.17\r\nv 190.44 38.21 94.34\r\nv 194.95 38.86 103.13\r\nv 179.85 38.54 53.24\r\nv 181.04 38.23 62.51\r\nv 179.47 39.07 44.32\r\nv 179.55 39.06 44.32\r\nv 179.92 38.53 53.23\r\nv 181.11 38.22 62.49\r\nv 196.31 68.12 -12.76\r\nv 199.44 66.58 -18.12\r\nv 193.65 69.68 -7.26\r\nv 193.72 69.66 -7.23\r\nv 196.38 68.1 -12.72\r\nv 199.5 66.56 -18.08\r\nv 183.17 37.87 73.2\r\nv 183.25 37.86 73.18\r\nv 179.78 40.08 34.69\r\nv 179.86 40.07 34.69\r\nv 203.01 65.09 -23.41\r\nv 206.13 63.86 -27.68\r\nv 203.07 65.07 -23.37\r\nv 206.19 63.85 -27.63\r\nv 199.25 39.37 109.99\r\nv 203.3 39.76 115.23\r\nv 199.31 39.37 109.94\r\nv 203.36 39.76 115.18\r\nv 208.4 56.92 -32.1\r\nv 208.25 55.35 -32.32\r\nv 208.57 58.56 -31.88\r\nv 208.62 58.55 -31.83\r\nv 208.46 56.91 -32.05\r\nv 208.31 55.34 -32.27\r\nv 208.14 54.16 -32.48\r\nv 208.05 53.22 -32.61\r\nv 208.19 54.14 -32.43\r\nv 208.1 53.21 -32.56\r\nv 210.22 40.46 122.85\r\nv 210.18 40.4 122.84\r\nv 210.21 40.32 122.81\r\nv 210.25 40.4 122.84\r\nv 209.35 40.26 121.9\r\nv 209.41 40.25 121.85\r\nv 210.27 40.32 122.76\r\nv 210.31 40.39 122.79\r\nv 210.47 44.09 122.6\r\nv 210.52 44.08 122.57\r\nv 210.75 47.58 122.31\r\nv 210.3 41.64 122.78\r\nv 210.35 41.64 122.76\r\nv 210.41 41.63 122.71\r\nv 210.57 44.07 122.52\r\nv 210.81 47.57 122.26\r\nv 205.99 89.14 101.79\r\nv 202.08 88.55 95.48\r\nv 209.62 89.61 106.64\r\nv 209.67 89.58 106.6\r\nv 206.05 89.12 101.75\r\nv 202.14 88.52 95.44\r\nv 208.71 59.99 -31.69\r\nv 208.84 61.14 -31.53\r\nv 208.89 61.12 -31.48\r\nv 208.77 59.98 -31.64\r\nv 189.55 72.92 5.23\r\nv 187.86 74.9 14.1\r\nv 187.91 74.9 14.12\r\nv 189.6 72.91 5.25\r\nv 186.64 76.94 23.6\r\nv 186.7 76.94 23.61\r\nv 186.77 76.92 23.62\r\nv 187.99 74.88 14.13\r\nv 189.68 72.9 5.27\r\nv 198 87.78 87.35\r\nv 194.15 86.68 77.88\r\nv 198.07 87.75 87.32\r\nv 194.22 86.65 77.86\r\nv 186.38 80.5 40.56\r\nv 187.03 81.96 48.7\r\nv 187.09 81.95 48.69\r\nv 186.44 80.49 40.56\r\nv 188.43 83.43 57.45\r\nv 188.48 83.42 57.44\r\nv 188.56 83.4 57.43\r\nv 187.16 81.93 48.69\r\nv 186.51 80.47 40.56\r\nv 190.85 85.12 67.55\r\nv 190.91 85.11 67.54\r\nv 190.98 85.09 67.53\r\nv 186.2 78.82 32.31\r\nv 186.26 78.81 32.31\r\nv 186.33 78.79 32.32\r\nv 188.41 45.45 -4.67\r\nv 188.45 45.4 -4.65\r\nv 191.06 46.5 -10.07\r\nv 191.13 46.5 -10.04\r\nv 188.52 45.39 -4.63\r\nv 206.6 51.28 -31.57\r\nv 206.64 51.25 -31.54\r\nv 204.76 50.75 -29.4\r\nv 207.52 51.49 -32.44\r\nv 207.58 51.48 -32.39\r\nv 206.7 51.24 -31.49\r\nv 204.82 50.74 -29.35\r\nv 207.9 51.66 -32.82\r\nv 207.88 51.61 -32.81\r\nv 207.92 51.89 -32.79\r\nv 207.98 51.88 -32.74\r\nv 207.96 51.65 -32.77\r\nv 207.94 51.6 -32.76\r\nv 213.79 78.56 117.45\r\nv 214.74 83.77 115.78\r\nv 212.79 72.02 119.18\r\nv 212.85 72 119.13\r\nv 213.84 78.54 117.4\r\nv 214.79 83.74 115.73\r\nv 212 64.41 120.51\r\nv 211.41 57.17 121.45\r\nv 211.46 57.16 121.4\r\nv 212.05 64.4 120.46\r\nv 212.8 89.97 110.43\r\nv 212.84 89.96 110.4\r\nv 215.12 90.19 112.81\r\nv 215.17 90.16 112.77\r\nv 212.9 89.93 110.35\r\nv 215.94 90.22 113.71\r\nv 215.94 90.26 113.69\r\nv 215.8 89.48 113.95\r\nv 215.85 89.45 113.9\r\nv 215.99 90.19 113.67\r\nv 215.99 90.23 113.64\r\nv 211.02 51.75 121.99\r\nv 211.08 51.74 121.94\r\nv 208.88 61.95 -31.45\r\nv 208.93 61.93 -31.42\r\nv 208.99 62.4 -31.36\r\nv 209.04 62.38 -31.31\r\nv 208.99 61.92 -31.37\r\nv 208.97 62.59 -31.28\r\nv 209 62.57 -31.32\r\nv 208.67 62.74 -30.92\r\nv 208.72 62.72 -30.87\r\nv 209.02 62.58 -31.23\r\nv 209.06 62.56 -31.27\r\nv 201.44 49.88 -25.35\r\nv 197.58 48.79 -20.33\r\nv 201.5 49.87 -25.31\r\nv 197.64 48.78 -20.29\r\nv 206.97 55.33 -31.04\r\nv 207.14 57.02 -30.82\r\nv 207.32 58.8 -30.58\r\nv 206.76 53.02 -31.35\r\nv 206.85 54.04 -31.22\r\nv 207.49 60.34 -30.37\r\nv 207.62 61.58 -30.21\r\nv 206.59 51.34 -31.58\r\nv 206.62 51.58 -31.54\r\nv 207.73 62.44 -30.09\r\nv 208.53 62.09 -31.06\r\nv 205.97 63.11 -27.83\r\nv 207.79 62.94 -30.03\r\nv 207.85 51.58 -32.78\r\nv 207.9 51.57 -32.72\r\nv 207.83 63.13 -29.99\r\nv 207.87 63.12 -29.95\r\nv 207.92 63.11 -29.9\r\nv 181.17 44.12 24.15\r\nv 183.41 45.29 12.86\r\nv 186.07 46.43 2.66\r\nv 194.93 42.67 102.64\r\nv 190.45 42.02 93.87\r\nv 186.41 41.52 83.73\r\nv 181.22 41.69 62.13\r\nv 180.05 41.87 52.89\r\nv 179.69 42.25 44.03\r\nv 183.33 41.49 72.78\r\nv 180 43.05 34.5\r\nv 203.41 43.54 114.87\r\nv 199.32 43.17 109.53\r\nv 210.42 44.09 122.57\r\nv 210.25 41.64 122.76\r\nv 209.54 44.02 121.66\r\nv 188.54 47.39 -4.45\r\nv 188.44 46.1 -4.6\r\nv 188.68 49.24 -4.23\r\nv 191.14 48.3 -9.89\r\nv 207.55 52.37 -32.37\r\nv 207.87 52.44 -32.71\r\nv 207.92 52.44 -32.74\r\nv 197.63 50.16 -20.21\r\nv 201.48 51.06 -25.25\r\nv 204.8 51.77 -29.31\r\nv 210.23 40.63 122.86\r\nv 210.28 40.63 122.83\r\nv 210.34 40.62 122.78\r\nv 207.98 52.44 -32.71\r\nv 208.03 52.43 -32.66\r\nv 206.67 52.18 -31.46\r\nv 210.18 40.63 122.84\r\nv 211.16 83.48 112.32\r\nv 210.15 78.27 113.83\r\nv 209.16 71.74 115.42\r\nv 208.36 64.16 116.72\r\nv 207.78 56.95 117.7\r\nv 212.77 89.94 110.46\r\nv 212.57 89.2 110.67\r\nv 207.43 51.52 118.3\r\nv 207.21 47.34 118.68\r\nv 206.85 40.06 119.29\r\nv 206.91 40.06 119.24\r\nv 215.91 90.27 113.65\r\nv 215.96 90.24 113.6\r\nv 207.01 43.83 119\r\nv 199.21 65.55 -18.29\r\nv 196.05 66.94 -12.94\r\nv 193.34 68.34 -7.47\r\nv 202.83 64.21 -23.57\r\nv 200.8 85.67 95.99\r\nv 204.79 86.27 102.43\r\nv 208.56 86.73 107.42\r\nv 186.62 76.92 23.6\r\nv 187.83 74.88 14.1\r\nv 189.53 72.9 5.22\r\nv 196.71 84.91 87.78\r\nv 188.4 83.4 57.46\r\nv 187 81.93 48.7\r\nv 186.35 80.47 40.56\r\nv 190.82 85.09 67.56\r\nv 186.17 78.79 32.31\r\nv 214.46 87.32 113.77\r\nv 215.31 87.4 114.63\r\nv 215.36 87.39 114.65\r\nv 208.83 61.96 -31.42\r\nv 215.41 87.37 114.63\r\nv 215.46 87.34 114.58\r\nv 209.01 62.56 -31.34\r\nv 209.07 62.55 -31.29\r\nv 207.81 63.12 -30\r\nv 212 87.09 111.28\r\nv 191.32 70.83 -1.65\r\nv 191.39 71.21 -1.6\r\nv 191.41 71.23 -1.59\r\nv 189.07 54.26 -3.63\r\nv 189.43 57.95 -3.2\r\nv 189.86 61.82 -2.74\r\nv 188.84 51.44 -3.97\r\nv 190.34 65.18 -2.34\r\nv 190.78 67.87 -2.01\r\nv 188.4 45.57 -4.67\r\nv 191.47 71.22 -1.57\r\nv 191.54 71.21 -1.54\r\nv 194.11 47.65 -15.27\r\nv 194.18 47.64 -15.23\r\nv 194.17 49.23 -15.12\r\nv 191.11 69.74 -1.79\r\nvn -0.86 -0.4 -0.32\r\nvn -0.89 -0.38 -0.25\r\nvn -0.97 0.04 -0.22\r\nvn -0.96 0.04 -0.29\r\nvn -0.92 -0.35 -0.19\r\nvn -0.99 0.04 -0.15\r\nvn -0.99 0.1 -0.14\r\nvn -0.97 0.1 -0.21\r\nvn -0.96 0.11 -0.28\r\nvn -0.91 -0.27 0.32\r\nvn -0.87 -0.26 0.41\r\nvn -0.91 0.05 0.41\r\nvn -0.95 0.05 0.32\r\nvn -0.83 -0.26 0.5\r\nvn -0.87 0.06 0.49\r\nvn -0.87 0.1 0.49\r\nvn -0.91 0.09 0.41\r\nvn -0.94 0.09 0.32\r\nvn -0.95 -0.32 -0.02\r\nvn -0.95 -0.31 0.07\r\nvn -1 0.04 0.09\r\nvn -1 0.04 0.01\r\nvn -0.94 -0.29 0.15\r\nvn -0.99 0.04 0.16\r\nvn -0.98 0.09 0.16\r\nvn -0.99 0.09 0.09\r\nvn -1 0.09 0.01\r\nvn -0.03 0.96 -0.29\r\nvn -0.6 0.59 -0.54\r\nvn -0.65 0.57 -0.5\r\nvn -0.07 0.95 -0.31\r\nvn -0.8 0.2 -0.56\r\nvn -0.84 0.2 -0.51\r\nvn -0.87 0.21 -0.44\r\nvn -0.7 0.56 -0.45\r\nvn -0.12 0.94 -0.31\r\nvn -0.93 -0.28 0.22\r\nvn -0.97 0.05 0.23\r\nvn -0.97 0.09 0.23\r\nvn -0.94 -0.33 -0.11\r\nvn -1 0.04 -0.07\r\nvn -0.99 0.09 -0.06\r\nvn 0.06 0.97 -0.25\r\nvn -0.51 0.64 -0.58\r\nvn -0.55 0.62 -0.56\r\nvn 0.01 0.96 -0.26\r\nvn -0.76 0.18 -0.62\r\nvn -0.78 0.19 -0.6\r\nvn -0.78 -0.25 0.57\r\nvn -0.82 0.06 0.57\r\nvn -0.74 -0.26 0.63\r\nvn -0.77 0.06 0.63\r\nvn -0.77 0.1 0.63\r\nvn -0.82 0.1 0.57\r\nvn -0.98 0.16 -0.14\r\nvn -0.97 0.2 -0.14\r\nvn -0.96 0.18 -0.21\r\nvn -0.97 0.16 -0.21\r\nvn -0.96 0.22 -0.14\r\nvn -0.96 0.21 -0.21\r\nvn -0.94 0.2 -0.27\r\nvn -0.95 0.18 -0.27\r\nvn -0.95 0.16 -0.27\r\nvn -0.84 0.33 0.44\r\nvn -0.87 0.33 0.35\r\nvn -0.89 0.27 0.36\r\nvn -0.85 0.28 0.45\r\nvn -0.91 0.32 0.27\r\nvn -0.92 0.27 0.28\r\nvn -0.94 0.2 0.28\r\nvn -0.9 0.21 0.38\r\nvn -0.86 0.21 0.46\r\nvn -0.97 0.19 0.13\r\nvn -0.96 0.24 0.13\r\nvn -0.97 0.22 0.06\r\nvn -0.98 0.18 0.06\r\nvn -0.95 0.28 0.13\r\nvn -0.96 0.26 0.06\r\nvn -0.97 0.25 -0.01\r\nvn -0.98 0.22 -0.01\r\nvn -0.98 0.18 -0\r\nvn -0.81 0.16 -0.57\r\nvn -0.81 0.15 -0.57\r\nvn -0.85 0.15 -0.51\r\nvn -0.84 0.16 -0.51\r\nvn -0.81 0.14 -0.57\r\nvn -0.85 0.13 -0.51\r\nvn -0.89 0.13 -0.45\r\nvn -0.88 0.14 -0.44\r\nvn -0.88 0.16 -0.44\r\nvn -0.93 0.3 0.19\r\nvn -0.95 0.25 0.2\r\nvn -0.96 0.2 0.2\r\nvn -0.98 0.21 -0.07\r\nvn -0.98 0.17 -0.07\r\nvn -0.97 0.24 -0.07\r\nvn -0.02 0.14 -0.99\r\nvn -0.02 0.14 -0.99\r\nvn -0.6 0.17 -0.78\r\nvn -0.6 0.17 -0.78\r\nvn -0.02 0.14 -0.99\r\nvn -0.6 0.16 -0.78\r\nvn -0.73 0.16 -0.67\r\nvn -0.73 0.16 -0.67\r\nvn -0.73 0.16 -0.67\r\nvn -0.98 0.12 -0.14\r\nvn -0.98 0.14 -0.14\r\nvn -0.97 0.14 -0.21\r\nvn -0.97 0.12 -0.21\r\nvn -0.95 0.13 -0.27\r\nvn -0.95 0.12 -0.27\r\nvn -0.91 0.15 0.39\r\nvn -0.87 0.16 0.47\r\nvn -0.94 0.15 0.29\r\nvn -0.95 0.12 0.3\r\nvn -0.91 0.12 0.39\r\nvn -0.87 0.12 0.48\r\nvn -0.99 0.15 0.07\r\nvn -0.98 0.15 0.14\r\nvn -0.99 0.15 -0\r\nvn -0.99 0.13 0\r\nvn -0.99 0.13 0.07\r\nvn -0.98 0.13 0.15\r\nvn -0.97 0.15 0.21\r\nvn -0.97 0.12 0.22\r\nvn -0.99 0.14 -0.07\r\nvn -0.99 0.12 -0.07\r\nvn -0.81 0.14 -0.57\r\nvn -0.85 0.13 -0.51\r\nvn -0.81 0.14 -0.56\r\nvn -0.85 0.12 -0.51\r\nvn -0.89 0.12 -0.45\r\nvn -0.89 0.12 -0.45\r\nvn -0.02 0.14 -0.99\r\nvn -0.6 0.16 -0.78\r\nvn -0.02 0.14 -0.99\r\nvn -0.6 0.16 -0.78\r\nvn -0.73 0.16 -0.67\r\nvn -0.73 0.16 -0.67\r\nvn -0.7 -0.26 0.66\r\nvn -0.74 0.06 0.67\r\nvn -0.69 -0.26 0.68\r\nvn -0.72 0.06 0.69\r\nvn -0.72 0.1 0.68\r\nvn -0.74 0.1 0.67\r\nvn -0.28 0.95 0.14\r\nvn -0.3 0.95 0.11\r\nvn -0.7 0.57 0.43\r\nvn -0.67 0.57 0.48\r\nvn -0.31 0.95 0.09\r\nvn -0.74 0.57 0.36\r\nvn -0.81 0.41 0.42\r\nvn -0.77 0.4 0.49\r\nvn -0.73 0.39 0.56\r\nvn -0.77 0.22 0.6\r\nvn -0.81 0.21 0.54\r\nvn -0.82 0.16 0.55\r\nvn -0.77 0.17 0.61\r\nvn -0.82 0.13 0.56\r\nvn -0.77 0.13 0.62\r\nvn -0.81 0.18 -0.56\r\nvn -0.81 0.17 -0.56\r\nvn -0.84 0.18 -0.51\r\nvn -0.84 0.19 -0.51\r\nvn -0.88 0.18 -0.44\r\nvn -0.88 0.2 -0.44\r\nvn -0.02 0.14 -0.99\r\nvn -0.02 0.14 -0.99\r\nvn -0.6 0.17 -0.78\r\nvn -0.6 0.17 -0.78\r\nvn -0.73 0.17 -0.67\r\nvn -0.73 0.17 -0.67\r\nvn -0.96 0.25 -0.14\r\nvn -0.95 0.23 -0.21\r\nvn -0.95 0.26 -0.14\r\nvn -0.95 0.24 -0.2\r\nvn -0.94 0.23 -0.27\r\nvn -0.94 0.22 -0.27\r\nvn -0.33 0.94 0.05\r\nvn -0.77 0.57 0.29\r\nvn -0.34 0.94 -0\r\nvn -0.8 0.56 0.21\r\nvn -0.88 0.4 0.26\r\nvn -0.85 0.41 0.34\r\nvn -0.94 0.32 0.13\r\nvn -0.95 0.3 0.06\r\nvn -0.93 0.34 0.13\r\nvn -0.95 0.32 0.06\r\nvn -0.95 0.3 -0.01\r\nvn -0.96 0.28 -0.01\r\nvn -0.89 0.37 0.26\r\nvn -0.92 0.35 0.19\r\nvn -0.91 0.37 0.19\r\nvn -0.96 0.27 -0.07\r\nvn -0.96 0.28 -0.07\r\nvn -0.8 0.28 0.53\r\nvn -0.75 0.28 0.59\r\nvn -0.74 0.33 0.58\r\nvn -0.79 0.33 0.51\r\nvn -0.97 0.11 -0.21\r\nvn -0.98 0.11 -0.14\r\nvn -0.95 0.11 -0.28\r\nvn -0.95 0.11 -0.28\r\nvn -0.97 0.1 -0.21\r\nvn -0.98 0.1 -0.14\r\nvn -0.91 0.09 0.4\r\nvn -0.87 0.1 0.49\r\nvn -0.95 0.09 0.31\r\nvn -0.95 0.09 0.31\r\nvn -0.91 0.09 0.4\r\nvn -0.87 0.09 0.49\r\nvn -0.99 0.11 0.08\r\nvn -0.98 0.11 0.15\r\nvn -0.99 0.11 0.01\r\nvn -1 0.1 0.01\r\nvn -0.99 0.1 0.08\r\nvn -0.98 0.09 0.16\r\nvn -0.97 0.1 0.22\r\nvn -0.97 0.09 0.23\r\nvn -0.99 0.11 -0.07\r\nvn -0.99 0.1 -0.06\r\nvn -0.82 0.11 0.56\r\nvn -0.77 0.11 0.62\r\nvn -0.82 0.1 0.56\r\nvn -0.77 0.1 0.62\r\nvn -0.65 -0.5 -0.58\r\nvn -0.7 -0.47 -0.54\r\nvn -0.85 0.03 -0.53\r\nvn -0.81 0.03 -0.58\r\nvn -0.75 -0.44 -0.49\r\nvn -0.89 0.04 -0.46\r\nvn -0.89 0.12 -0.45\r\nvn -0.85 0.13 -0.51\r\nvn -0.82 0.14 -0.56\r\nvn -0.01 -0.46 -0.89\r\nvn -0.34 -0.56 -0.75\r\nvn -0.6 -0.01 -0.8\r\nvn -0.03 -0.08 -1\r\nvn -0.49 -0.6 -0.63\r\nvn -0.72 -0.01 -0.7\r\nvn -0.73 0.16 -0.67\r\nvn -0.61 0.16 -0.78\r\nvn -0.02 0.14 -0.99\r\nvn 0.03 0.3 0.95\r\nvn -0.55 0.34 0.76\r\nvn -0.57 0.32 0.76\r\nvn 0.01 0.28 0.96\r\nvn -0.68 0.33 0.65\r\nvn -0.69 0.3 0.65\r\nvn -0.72 0.23 0.66\r\nvn -0.59 0.24 0.77\r\nvn -0.02 0.22 0.98\r\nvn -0.6 0.17 0.78\r\nvn -0.02 0.15 0.99\r\nvn -0.73 0.17 0.67\r\nvn -0.73 0.13 0.67\r\nvn -0.6 0.14 0.79\r\nvn -0.01 0.12 0.99\r\nvn 0.22 0.81 0.54\r\nvn -0.12 0.94 0.33\r\nvn -0.51 0.53 0.68\r\nvn 0.1 0.52 0.85\r\nvn -0.27 0.95 0.17\r\nvn -0.64 0.53 0.56\r\nvn -0.7 0.34 0.63\r\nvn -0.56 0.35 0.76\r\nvn 0.05 0.3 0.95\r\nvn -0.6 0.11 0.79\r\nvn -0.01 0.09 1\r\nvn -0.73 0.11 0.68\r\nvn -0.73 0.1 0.68\r\nvn -0.6 0.1 0.8\r\nvn -0 0.08 1\r\nvn 0.3 0.9 -0.32\r\nvn 0.1 0.53 -0.84\r\nvn -0.36 0.62 -0.7\r\nvn 0.18 0.93 -0.31\r\nvn -0.01 0.14 -0.99\r\nvn -0.6 0.18 -0.78\r\nvn -0.72 0.18 -0.67\r\nvn -0.43 0.66 -0.62\r\nvn 0.14 0.95 -0.26\r\nvn -0.78 0.15 -0.6\r\nvn -0.79 0.15 -0.6\r\nvn -0.78 0.16 -0.6\r\nvn -0.76 0.16 -0.63\r\nvn -0.76 0.16 -0.62\r\nvn -0.77 0.16 -0.62\r\nvn -0.79 0.15 -0.6\r\nvn -0.79 0.14 -0.6\r\nvn -0.77 0.15 -0.62\r\nvn -0.77 0.15 -0.62\r\nvn -0.78 0.17 -0.6\r\nvn -0.78 0.18 -0.6\r\nvn -0.76 0.17 -0.62\r\nvn -0.76 0.17 -0.63\r\nvn -0.56 -0.56 -0.6\r\nvn -0.6 -0.53 -0.59\r\nvn -0.78 0.02 -0.62\r\nvn -0.76 0 -0.65\r\nvn -0.79 0.15 -0.6\r\nvn -0.77 0.15 -0.62\r\nvn -0.41 -0.89 -0.21\r\nvn -0.45 -0.88 -0.18\r\nvn -0.37 -0.89 -0.25\r\nvn -0.06 -0.98 -0.17\r\nvn -0.07 -0.99 -0.15\r\nvn -0.08 -0.99 -0.14\r\nvn -0.47 -0.84 0.27\r\nvn -0.44 -0.84 0.32\r\nvn -0.5 -0.84 0.19\r\nvn -0.08 -1 0.04\r\nvn -0.06 -0.99 0.09\r\nvn -0.05 -0.99 0.1\r\nvn -0.51 -0.86 0\r\nvn -0.51 -0.86 0.06\r\nvn -0.49 -0.87 -0.07\r\nvn -0.09 -0.99 -0.08\r\nvn -0.09 -0.99 -0.04\r\nvn -0.09 -1 -0.02\r\nvn 0.25 0.96 -0.14\r\nvn 0.25 0.96 -0.12\r\nvn 0.24 0.96 -0.16\r\nvn 0.29 0.95 -0.14\r\nvn 0.29 0.95 -0.12\r\nvn 0.28 0.95 -0.09\r\nvn -0.51 -0.85 0.11\r\nvn -0.09 -1 0\r\nvn -0.47 -0.87 -0.13\r\nvn -0.09 -0.99 -0.12\r\nvn 0.25 0.96 -0.1\r\nvn 0.27 0.96 -0.1\r\nvn 0.28 0.96 -0.07\r\nvn 0.29 0.95 -0.08\r\nvn -0.42 -0.84 0.36\r\nvn -0.39 -0.84 0.38\r\nvn -0.05 -0.99 0.11\r\nvn -0.04 -0.99 0.11\r\nvn 0.56 0.06 -0.83\r\nvn 0.56 0.06 -0.83\r\nvn 0.56 0.06 -0.83\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn 0.56 0.06 -0.83\r\nvn 0.56 0.06 -0.83\r\nvn 0.68 0.04 -0.73\r\nvn 0.68 0.04 -0.73\r\nvn 0.02 -0.08 1\r\nvn -0.37 -0.27 0.89\r\nvn -0.04 -0.77 0.64\r\nvn 0.34 -0.3 0.89\r\nvn -0.35 -0.84 0.4\r\nvn -0.04 -0.99 0.11\r\nvn 0.28 -0.86 0.43\r\nvn 0.63 -0.31 0.71\r\nvn 0 0.08 1\r\nvn 0.57 0.02 0.82\r\nvn 0.57 0.02 0.82\r\nvn 0.01 0.07 1\r\nvn 0.58 0.02 0.82\r\nvn 0.69 0.01 0.73\r\nvn 0.69 0.01 0.73\r\nvn 0.69 0.01 0.73\r\nvn 0.18 0.96 -0.22\r\nvn 0.19 0.96 -0.2\r\nvn 0.17 0.96 -0.23\r\nvn 0.27 0.91 -0.3\r\nvn 0.29 0.92 -0.28\r\nvn 0.3 0.92 -0.26\r\nvn 0.56 0.05 -0.83\r\nvn 0.56 0.05 -0.83\r\nvn 0.68 0.02 -0.73\r\nvn 0.68 0.03 -0.73\r\nvn -0.22 0.94 -0.27\r\nvn -0.26 0.93 -0.25\r\nvn 0.21 0.96 -0.18\r\nvn 0.22 0.96 -0.17\r\nvn -0.3 0.93 -0.22\r\nvn 0.2 0.96 -0.19\r\nvn 0.29 0.94 -0.18\r\nvn 0.29 0.94 -0.16\r\nvn 0.29 0.95 -0.16\r\nvn 0.2 0.96 -0.19\r\nvn 0.2 0.96 -0.2\r\nvn 0.31 0.92 -0.24\r\nvn 0.31 0.92 -0.24\r\nvn -0.34 0.93 -0.16\r\nvn -0.35 0.93 -0.12\r\nvn 0.19 0.96 -0.19\r\nvn 0.19 0.96 -0.19\r\nvn -0.35 0.93 -0.08\r\nvn 0.19 0.96 -0.2\r\nvn 0.3 0.93 -0.22\r\nvn 0.3 0.93 -0.2\r\nvn 0.3 0.93 -0.19\r\nvn -0.35 0.93 -0.05\r\nvn 0.19 0.96 -0.21\r\nvn 0.31 0.92 -0.23\r\nvn -0.33 0.92 -0.2\r\nvn 0.2 0.96 -0.2\r\nvn 0.29 0.94 -0.19\r\nvn -0.81 -0.42 -0.41\r\nvn -0.33 -0.9 -0.3\r\nvn -0.28 -0.9 -0.34\r\nvn -0.03 -0.97 -0.22\r\nvn -0.04 -0.98 -0.2\r\nvn -0.52 -0.58 -0.62\r\nvn -0.11 -0.93 -0.34\r\nvn -0.14 -0.93 -0.33\r\nvn -0.1 -0.94 -0.34\r\nvn 0.01 -0.97 -0.25\r\nvn 0 -0.97 -0.24\r\nvn -0.01 -0.97 -0.23\r\nvn 0.54 -0.1 -0.84\r\nvn 0.3 -0.59 -0.75\r\nvn 0.56 0.06 -0.83\r\nvn 0.68 0.04 -0.73\r\nvn 0.65 -0.12 -0.75\r\nvn 0.44 -0.63 -0.64\r\nvn 0.57 0.14 0.81\r\nvn 0.58 0.15 0.8\r\nvn 0.56 0.11 0.82\r\nvn 0.68 0.07 0.73\r\nvn 0.68 0.1 0.72\r\nvn 0.69 0.1 0.72\r\nvn 0.56 0.07 0.82\r\nvn 0.56 0.05 0.82\r\nvn 0.68 0.03 0.73\r\nvn 0.68 0.05 0.73\r\nvn -0.28 0.95 0.16\r\nvn 0.17 0.96 -0.24\r\nvn 0.17 0.95 -0.25\r\nvn 0.25 0.91 -0.33\r\nvn 0.26 0.91 -0.32\r\nvn 0.62 0.29 0.73\r\nvn 0.52 0.77 0.37\r\nvn 0.59 0.15 0.8\r\nvn 0.69 0.1 0.72\r\nvn 0.72 0.25 0.65\r\nvn 0.65 0.71 0.27\r\nvn 0.57 0.03 0.82\r\nvn 0.68 0.02 0.73\r\nvn -0.01 0.14 -0.99\r\nvn 0.56 0.04 -0.83\r\nvn 0.56 0.04 -0.83\r\nvn 0.68 0.01 -0.73\r\nvn 0.68 0.02 -0.73\r\nvn 0.32 0.94 -0.14\r\nvn 0.41 0.86 -0.31\r\nvn 0.31 0.94 -0.12\r\nvn 0.33 0.94 -0.1\r\nvn 0.34 0.93 -0.12\r\nvn 0.45 0.85 -0.26\r\nvn -0.17 -0.93 -0.33\r\nvn -0.2 -0.92 -0.35\r\nvn -0.01 -0.97 -0.22\r\nvn -0.01 -0.97 -0.23\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.17 -0.65\r\nvn -0.74 0.17 -0.65\r\nvn -0.73 -0.01 -0.68\r\nvn -0.74 0.16 -0.65\r\nvn -0.74 0.18 -0.65\r\nvn -0.72 0.18 -0.67\r\nvn -0.76 0.18 -0.62\r\nvn -0.74 0.18 -0.65\r\nvn 0 -0.9 -0.43\r\nvn 0.11 -0.93 -0.34\r\nvn 0.11 0.96 -0.26\r\nvn 0.29 0.95 -0.11\r\nvn 0.31 0.95 -0.09\r\nvn -0.98 0.1 -0.14\r\nvn -0.97 0.1 -0.21\r\nvn -0.95 0.11 -0.28\r\nvn -0.87 0.1 0.49\r\nvn -0.91 0.09 0.41\r\nvn -0.94 0.09 0.32\r\nvn -0.98 0.09 0.16\r\nvn -0.99 0.09 0.09\r\nvn -1 0.09 0.01\r\nvn -0.97 0.09 0.23\r\nvn -0.99 0.09 -0.06\r\nvn -0.77 0.1 0.63\r\nvn -0.82 0.1 0.57\r\nvn -0.59 0.1 0.8\r\nvn -0.59 0.1 0.8\r\nvn -0.72 0.1 0.68\r\nvn -0.92 0.12 -0.36\r\nvn -0.92 0.12 -0.36\r\nvn -0.93 0.11 -0.36\r\nvn -0.89 0.12 -0.45\r\nvn -0.73 0.16 -0.67\r\nvn -0.61 0.16 -0.78\r\nvn -0.02 0.14 -0.99\r\nvn -0.82 0.14 -0.56\r\nvn -0.79 0.15 -0.6\r\nvn -0.77 0.15 -0.62\r\nvn 0.01 0.02 1\r\nvn 0.57 -0.01 0.82\r\nvn 0.68 -0.03 0.73\r\nvn 0.56 0.06 -0.83\r\nvn 0.68 0.04 -0.73\r\nvn -0.74 0.16 -0.65\r\nvn -0.59 0.06 0.8\r\nvn -0.7 0.33 0.64\r\nvn -0.71 0.29 0.64\r\nvn -0.73 0.23 0.64\r\nvn -0.74 0.17 0.65\r\nvn -0.74 0.13 0.66\r\nvn -0.65 0.55 0.53\r\nvn -0.71 0.37 0.61\r\nvn -0.74 0.11 0.66\r\nvn -0.74 0.1 0.66\r\nvn -0.37 -0.84 0.4\r\nvn -0.04 -0.99 0.11\r\nvn 0.26 0.95 -0.14\r\nvn 0.35 0.91 -0.21\r\nvn -0.74 0.1 0.67\r\nvn -0.8 0.19 -0.56\r\nvn -0.84 0.2 -0.51\r\nvn -0.87 0.21 -0.44\r\nvn -0.78 0.18 -0.6\r\nvn -0.82 0.39 0.42\r\nvn -0.78 0.38 0.5\r\nvn -0.73 0.37 0.57\r\nvn -0.84 0.51 -0.18\r\nvn -0.82 0.52 -0.24\r\nvn -0.79 0.53 -0.31\r\nvn -0.86 0.38 0.34\r\nvn -0.84 0.53 0.08\r\nvn -0.85 0.52 0.01\r\nvn -0.86 0.51 -0.05\r\nvn -0.82 0.55 0.14\r\nvn -0.85 0.51 -0.12\r\nvn -0.68 0.34 0.65\r\nvn -0.55 0.35 0.76\r\nvn 0.04 0.3 0.95\r\nvn -0.6 0.18 -0.78\r\nvn 0.58 0.15 0.8\r\nvn 0.69 0.1 0.72\r\nvn 0.58 0.42 -0.69\r\nvn 0.67 0.43 -0.61\r\nvn -0.46 0.65 -0.6\r\nvn -0.7 0.36 0.62\r\nvn -0.91 0.22 -0.36\r\nvn -0.75 0.54 -0.38\r\nvn -0.17 0.94 -0.3\r\nvn -0.93 0.12 -0.36\r\nvn -0.92 0.14 -0.36\r\nvn -0.92 0.16 -0.36\r\nvn -0.93 0.11 -0.36\r\nvn -0.92 0.18 -0.36\r\nvn -0.91 0.2 -0.36\r\nvn -0.93 0.04 -0.38\r\nvn 0.23 0.96 -0.17\r\nvn 0.29 0.95 -0.15\r\nvn -0.23 -0.91 -0.35\r\nvn -0.02 -0.97 -0.23\r\nvn -0.85 0.13 -0.51\r\nvn -0.91 0.21 -0.36\r\nvt 0.2339 0.3462\r\nvt 0.302 0.3343\r\nvt 0.302 0.3352\r\nvt 0.234 0.347\r\nvt 0.377 0.3233\r\nvt 0.377 0.3243\r\nvt 0.3769 0.3289\r\nvt 0.3022 0.3394\r\nvt 0.2343 0.3508\r\nvt 0.7595 0.3241\r\nvt 0.8224 0.3373\r\nvt 0.8223 0.3386\r\nvt 0.7594 0.3255\r\nvt 0.876 0.3522\r\nvt 0.8759 0.3536\r\nvt 0.8749 0.3599\r\nvt 0.8213 0.345\r\nvt 0.7585 0.3318\r\nvt 0.5074 0.3101\r\nvt 0.5645 0.3094\r\nvt 0.5644 0.3107\r\nvt 0.5073 0.3113\r\nvt 0.6234 0.3119\r\nvt 0.6232 0.3131\r\nvt 0.6225 0.319\r\nvt 0.5637 0.3163\r\nvt 0.5067 0.3167\r\nvt 0.0927 0.5062\r\nvt 0.0926 0.5061\r\nvt 0.1291 0.5103\r\nvt 0.1292 0.5104\r\nvt 0.0924 0.5044\r\nvt 0.1289 0.5083\r\nvt 0.166 0.5134\r\nvt 0.1662 0.5156\r\nvt 0.1663 0.5157\r\nvt 0.6908 0.3162\r\nvt 0.6907 0.3175\r\nvt 0.6899 0.3236\r\nvt 0.4452 0.3149\r\nvt 0.4451 0.316\r\nvt 0.4447 0.3211\r\nvt 0.0268 0.5012\r\nvt 0.0267 0.5012\r\nvt 0.0562 0.5032\r\nvt 0.0563 0.5033\r\nvt 0.0266 0.4999\r\nvt 0.056 0.5017\r\nvt 0.9171 0.3656\r\nvt 0.917 0.367\r\nvt 0.9483 0.3773\r\nvt 0.9482 0.3786\r\nvt 0.9475 0.385\r\nvt 0.9161 0.3733\r\nvt 0.3727 0.4669\r\nvt 0.3716 0.4971\r\nvt 0.3065 0.4908\r\nvt 0.3059 0.4637\r\nvt 0.3707 0.5216\r\nvt 0.307 0.5128\r\nvt 0.2474 0.5054\r\nvt 0.2459 0.4858\r\nvt 0.244 0.4617\r\nvt 0.8305 0.625\r\nvt 0.7799 0.6103\r\nvt 0.7856 0.5756\r\nvt 0.837 0.5904\r\nvt 0.7207 0.5945\r\nvt 0.7259 0.5603\r\nvt 0.7321 0.5186\r\nvt 0.7923 0.5333\r\nvt 0.8446 0.5481\r\nvt 0.6007 0.493\r\nvt 0.5957 0.5315\r\nvt 0.5393 0.521\r\nvt 0.5439 0.484\r\nvt 0.5915 0.5628\r\nvt 0.5355 0.551\r\nvt 0.4829 0.5409\r\nvt 0.4861 0.5123\r\nvt 0.4899 0.477\r\nvt 0.0879 0.4645\r\nvt 0.086 0.4475\r\nvt 0.1217 0.4427\r\nvt 0.1239 0.4623\r\nvt 0.0841 0.4314\r\nvt 0.1196 0.4242\r\nvt 0.1556 0.4179\r\nvt 0.158 0.4389\r\nvt 0.1604 0.461\r\nvt 0.6556 0.578\r\nvt 0.6602 0.5451\r\nvt 0.6658 0.5048\r\nvt 0.4308 0.5043\r\nvt 0.4335 0.4714\r\nvt 0.4286 0.531\r\nvt -0.0017 0.4728\r\nvt -0.003 0.4623\r\nvt -0.0028 0.4623\r\nvt -0.0015 0.4728\r\nvt -0.0043 0.4524\r\nvt -0.0041 0.4523\r\nvt -0.0017 0.4517\r\nvt -0.0004 0.4619\r\nvt 0.001 0.4726\r\nvt 0.3748 0.4001\r\nvt 0.3738 0.4326\r\nvt 0.305 0.4328\r\nvt 0.3042 0.4036\r\nvt 0.2416 0.4342\r\nvt 0.2394 0.4081\r\nvt 0.7998 0.486\r\nvt 0.8525 0.5007\r\nvt 0.7389 0.4717\r\nvt 0.7451 0.4277\r\nvt 0.8066 0.4416\r\nvt 0.8598 0.4563\r\nvt 0.5489 0.4422\r\nvt 0.6062 0.4495\r\nvt 0.4942 0.4371\r\nvt 0.4981 0.3993\r\nvt 0.5536 0.4026\r\nvt 0.6114 0.4085\r\nvt 0.672 0.4595\r\nvt 0.6776 0.4167\r\nvt 0.4363 0.4339\r\nvt 0.439 0.3986\r\nvt 0.0826 0.4192\r\nvt 0.1179 0.4101\r\nvt 0.0815 0.4096\r\nvt 0.1166 0.3991\r\nvt 0.1523 0.3895\r\nvt 0.1538 0.4019\r\nvt -0.0053 0.4448\r\nvt -0.0051 0.4447\r\nvt -0.0061 0.4388\r\nvt -0.0059 0.4387\r\nvt -0.0035 0.4378\r\nvt -0.0027 0.4439\r\nvt 0.9721 0.3872\r\nvt 0.9721 0.3885\r\nvt 0.9873 0.3939\r\nvt 0.9873 0.3953\r\nvt 0.9867 0.4017\r\nvt 0.9714 0.3949\r\nvt 0.889 0.6936\r\nvt 0.8601 0.6825\r\nvt 0.8602 0.6822\r\nvt 0.8891 0.6934\r\nvt 0.822 0.6696\r\nvt 0.8221 0.6693\r\nvt 0.8231 0.6642\r\nvt 0.8614 0.6771\r\nvt 0.8905 0.6884\r\nvt 0.92 0.5744\r\nvt 0.8868 0.5619\r\nvt 0.8951 0.5145\r\nvt 0.9287 0.5267\r\nvt 0.9023 0.4698\r\nvt 0.9356 0.4817\r\nvt 0.0909 0.4912\r\nvt 0.0896 0.4793\r\nvt 0.1258 0.4794\r\nvt 0.1273 0.4932\r\nvt 0.1625 0.4805\r\nvt 0.1641 0.4961\r\nvt 0.0004 0.4893\r\nvt -0.0006 0.4819\r\nvt -0.0003 0.4819\r\nvt 0.0006 0.4893\r\nvt 0.0021 0.4819\r\nvt 0.0031 0.4894\r\nvt 0.37 0.5386\r\nvt 0.3073 0.5281\r\nvt 0.3696 0.5487\r\nvt 0.3075 0.5371\r\nvt 0.2491 0.527\r\nvt 0.2485 0.5189\r\nvt 0.7723 0.6549\r\nvt 0.7724 0.6546\r\nvt 0.7139 0.6385\r\nvt 0.714 0.6382\r\nvt 0.7148 0.6331\r\nvt 0.7733 0.6495\r\nvt 0.5885 0.5848\r\nvt 0.5329 0.572\r\nvt 0.5867 0.5979\r\nvt 0.5312 0.5845\r\nvt 0.4792 0.5728\r\nvt 0.4806 0.5609\r\nvt 0.717 0.6187\r\nvt 0.6523 0.6012\r\nvt 0.6502 0.615\r\nvt 0.4271 0.5497\r\nvt 0.4261 0.5607\r\nvt 0.878 0.604\r\nvt 0.9101 0.6162\r\nvt 0.9009 0.6501\r\nvt 0.8703 0.6383\r\nvt 0.3035 0.3813\r\nvt 0.3755 0.3754\r\nvt 0.2376 0.3882\r\nvt 0.2362 0.3728\r\nvt 0.3029 0.364\r\nvt 0.3761 0.3562\r\nvt 0.8117 0.408\r\nvt 0.8651 0.4228\r\nvt 0.7498 0.3944\r\nvt 0.7534 0.3686\r\nvt 0.8156 0.3821\r\nvt 0.8691 0.3969\r\nvt 0.5571 0.3727\r\nvt 0.6153 0.3774\r\nvt 0.5011 0.3706\r\nvt 0.5034 0.3483\r\nvt 0.5598 0.3494\r\nvt 0.6183 0.3533\r\nvt 0.6819 0.3844\r\nvt 0.6852 0.3593\r\nvt 0.441 0.3717\r\nvt 0.4425 0.3508\r\nvt 0.9073 0.4362\r\nvt 0.9401 0.448\r\nvt 0.9109 0.4102\r\nvt 0.9431 0.422\r\nvt 0.0796 0.3931\r\nvt 0.1145 0.3801\r\nvt 0.1145 0.3807\r\nvt 0.0796 0.3936\r\nvt 0.1499 0.368\r\nvt 0.1499 0.3686\r\nvt 0.1503 0.3717\r\nvt 0.1148 0.3834\r\nvt 0.0799 0.3959\r\nvt -0.0072 0.4287\r\nvt -0.0071 0.4285\r\nvt -0.0071 0.4288\r\nvt -0.0073 0.4289\r\nvt -0.0047 0.4273\r\nvt -0.0047 0.4276\r\nvt -0.0046 0.4291\r\nvt -0.0069 0.4302\r\nvt -0.0071 0.4304\r\nvt 0.9444 0.6701\r\nvt 0.9443 0.6701\r\nvt 0.9556 0.6365\r\nvt 0.9558 0.6366\r\nvt 0.9391 0.6676\r\nvt 0.9502 0.634\r\nvt 0.9618 0.5921\r\nvt 0.9674 0.5946\r\nvt 0.9675 0.5947\r\nvt 0.9765 0.5466\r\nvt 0.9766 0.5467\r\nvt 0.9708 0.5442\r\nvt 0.9773 0.4988\r\nvt 0.9829 0.5012\r\nvt 0.983 0.5013\r\nvt 0.9301 0.7119\r\nvt 0.93 0.712\r\nvt 0.9302 0.7117\r\nvt 0.9303 0.7118\r\nvt 0.9252 0.7097\r\nvt 0.9253 0.7095\r\nvt 0.9269 0.7046\r\nvt 0.9318 0.7069\r\nvt 0.9319 0.707\r\nvt 0.9866 0.4673\r\nvt 0.9868 0.4674\r\nvt 0.9811 0.4649\r\nvt 0.9834 0.4388\r\nvt 0.9889 0.4412\r\nvt 0.989 0.4413\r\nvt 0.0016 0.4984\r\nvt 0.0015 0.4984\r\nvt 0.0017 0.4984\r\nvt 0.0018 0.4985\r\nvt 0.0014 0.4973\r\nvt 0.0016 0.4974\r\nvt 0.0041 0.4977\r\nvt 0.0042 0.4988\r\nvt 0.0043 0.4988\r\nvt 0.0503 0.4532\r\nvt 0.0487 0.4394\r\nvt 0.0521 0.4677\r\nvt 0.023 0.4706\r\nvt 0.0215 0.4581\r\nvt 0.0201 0.4462\r\nvt 0.0474 0.4289\r\nvt 0.0465 0.4207\r\nvt 0.0189 0.4371\r\nvt 0.0181 0.43\r\nvt 0.0535 0.4803\r\nvt 0.0547 0.4905\r\nvt 0.0254 0.4903\r\nvt 0.0244 0.4815\r\nvt 0.0167 0.4178\r\nvt 0.0448 0.4066\r\nvt 0.0448 0.407\r\nvt 0.0166 0.4182\r\nvt 0.0451 0.409\r\nvt 0.0168 0.4199\r\nvt 0.3021 0.3341\r\nvt 0.3771 0.323\r\nvt 0.234 0.346\r\nvt 0.2341 0.3461\r\nvt 0.3021 0.3342\r\nvt 0.3771 0.3231\r\nvt 0.8223 0.3369\r\nvt 0.8759 0.3518\r\nvt 0.7595 0.3237\r\nvt 0.7593 0.3238\r\nvt 0.822 0.337\r\nvt 0.8756 0.3519\r\nvt 0.5645 0.3091\r\nvt 0.6233 0.3115\r\nvt 0.5074 0.3098\r\nvt 0.5073 0.3099\r\nvt 0.5644 0.3092\r\nvt 0.6232 0.3116\r\nvt 0.1293 0.5105\r\nvt 0.0928 0.5063\r\nvt 0.1664 0.5158\r\nvt 0.1665 0.5159\r\nvt 0.1295 0.5106\r\nvt 0.0931 0.5064\r\nvt 0.6907 0.3158\r\nvt 0.6906 0.3159\r\nvt 0.4452 0.3146\r\nvt 0.4452 0.3148\r\nvt 0.0565 0.5034\r\nvt 0.027 0.5013\r\nvt 0.0567 0.5034\r\nvt 0.0273 0.5013\r\nvt 0.917 0.3652\r\nvt 0.9481 0.3768\r\nvt 0.9166 0.3652\r\nvt 0.9477 0.3769\r\nvt -0.0029 0.4624\r\nvt -0.0042 0.4524\r\nvt -0.0016 0.4729\r\nvt -0.0013 0.4729\r\nvt -0.0026 0.4624\r\nvt -0.0039 0.4525\r\nvt -0.0052 0.4448\r\nvt -0.0059 0.4389\r\nvt -0.0049 0.4449\r\nvt -0.0056 0.439\r\nvt 0.9926 0.3967\r\nvt 0.9926 0.3963\r\nvt 0.9923 0.3959\r\nvt 0.9925 0.3964\r\nvt 0.9871 0.3935\r\nvt 0.9867 0.3936\r\nvt 0.992 0.3959\r\nvt 0.9921 0.3965\r\nvt 0.9908 0.4194\r\nvt 0.9906 0.4195\r\nvt 0.9888 0.4413\r\nvt 0.9921 0.4041\r\nvt 0.9919 0.4042\r\nvt 0.9916 0.4042\r\nvt 0.9903 0.4195\r\nvt 0.9884 0.4414\r\nvt 0.8599 0.6825\r\nvt 0.8218 0.6696\r\nvt 0.8887 0.6937\r\nvt 0.8884 0.6936\r\nvt 0.8596 0.6824\r\nvt 0.8216 0.6696\r\nvt -0.0004 0.482\r\nvt 0.0005 0.4893\r\nvt 0.0008 0.4893\r\nvt -0.0001 0.482\r\nvt 0.2494 0.5299\r\nvt 0.3076 0.5405\r\nvt 0.2495 0.53\r\nvt 0.3695 0.5525\r\nvt 0.3695 0.5526\r\nvt 0.3077 0.5406\r\nvt 0.2496 0.5301\r\nvt 0.7722 0.655\r\nvt 0.7137 0.6385\r\nvt 0.7719 0.6549\r\nvt 0.7136 0.6385\r\nvt 0.4787 0.5772\r\nvt 0.5306 0.5891\r\nvt 0.5306 0.5892\r\nvt 0.4787 0.5773\r\nvt 0.586 0.6028\r\nvt 0.5859 0.6028\r\nvt 0.5858 0.6028\r\nvt 0.5305 0.5892\r\nvt 0.4786 0.5773\r\nvt 0.6494 0.6201\r\nvt 0.6493 0.6202\r\nvt 0.6492 0.6202\r\nvt 0.4257 0.5648\r\nvt 0.4257 0.5649\r\nvt 0.1864 0.3571\r\nvt 0.1865 0.3569\r\nvt 0.15 0.3678\r\nvt 0.1501 0.3679\r\nvt 0.1867 0.357\r\nvt 0.0017 0.4243\r\nvt 0.0019 0.4242\r\nvt 0.0168 0.4177\r\nvt -0.0045 0.4273\r\nvt -0.0042 0.4273\r\nvt 0.0021 0.4242\r\nvt 0.0171 0.4178\r\nvt -0.0072 0.429\r\nvt -0.0071 0.4286\r\nvt -0.007 0.4304\r\nvt -0.0067 0.4305\r\nvt -0.0069 0.429\r\nvt -0.0069 0.4287\r\nvt 0.9556 0.6366\r\nvt 0.9442 0.6701\r\nvt 0.9673 0.5948\r\nvt 0.967 0.5948\r\nvt 0.9552 0.6366\r\nvt 0.9439 0.6701\r\nvt 0.9764 0.5468\r\nvt 0.9828 0.5014\r\nvt 0.9825 0.5014\r\nvt 0.9761 0.5468\r\nvt 0.9111 0.7032\r\nvt 0.9108 0.7032\r\nvt 0.9249 0.7097\r\nvt 0.9246 0.7096\r\nvt 0.9105 0.7031\r\nvt 0.9301 0.7117\r\nvt 0.9318 0.707\r\nvt 0.9314 0.7069\r\nvt 0.9298 0.7116\r\nvt 0.9297 0.7119\r\nvt 0.9866 0.4674\r\nvt 0.9862 0.4675\r\nvt 0.001 0.4943\r\nvt 0.0011 0.4944\r\nvt 0.0015 0.4974\r\nvt 0.0018 0.4974\r\nvt 0.0014 0.4944\r\nvt 0.002 0.4985\r\nvt 0.0017 0.4985\r\nvt 0.0045 0.4989\r\nvt 0.0048 0.4989\r\nvt 0.0023 0.4986\r\nvt 0.045 0.4065\r\nvt 0.0798 0.393\r\nvt 0.0452 0.4065\r\nvt 0.08 0.3931\r\nvt 0.0048 0.45\r\nvt 0.0062 0.4607\r\nvt 0.0076 0.4721\r\nvt 0.003 0.4353\r\nvt 0.0038 0.4417\r\nvt 0.0088 0.4819\r\nvt 0.0098 0.4898\r\nvt 0.0016 0.4246\r\nvt 0.0018 0.4262\r\nvt 0.0104 0.4953\r\nvt 0.0037 0.4946\r\nvt 0.0261 0.4963\r\nvt 0.0108 0.4985\r\nvt -0.0069 0.4284\r\nvt -0.0066 0.4284\r\nvt 0.0111 0.4998\r\nvt 0.0113 0.4998\r\nvt 0.0116 0.4998\r\nvt 0.3765 0.3401\r\nvt 0.3025 0.3495\r\nvt 0.2351 0.3598\r\nvt 0.8725 0.3751\r\nvt 0.819 0.3602\r\nvt 0.7564 0.3469\r\nvt 0.6208 0.3331\r\nvt 0.5621 0.3299\r\nvt 0.5054 0.3297\r\nvt 0.6879 0.3383\r\nvt 0.4438 0.3333\r\nvt 0.9457 0.4002\r\nvt 0.914 0.3885\r\nvt 0.9907 0.4193\r\nvt 0.992 0.404\r\nvt 0.9854 0.4169\r\nvt 0.1878 0.3693\r\nvt 0.1869 0.3612\r\nvt 0.1891 0.381\r\nvt 0.1511 0.379\r\nvt -0.0041 0.4327\r\nvt -0.0065 0.4337\r\nvt -0.0067 0.4338\r\nvt 0.0806 0.4015\r\nvt 0.0456 0.4138\r\nvt 0.0174 0.4241\r\nvt 0.9927 0.3978\r\nvt 0.9925 0.3979\r\nvt 0.9921 0.3979\r\nvt -0.0066 0.4339\r\nvt -0.0063 0.434\r\nvt 0.0023 0.4299\r\nvt 0.9926 0.3977\r\nvt 0.9243 0.6604\r\nvt 0.9346 0.6267\r\nvt 0.9455 0.5849\r\nvt 0.9544 0.5371\r\nvt 0.961 0.4919\r\nvt 0.9112 0.7029\r\nvt 0.9128 0.698\r\nvt 0.9651 0.458\r\nvt 0.9677 0.4319\r\nvt 0.9719 0.3867\r\nvt 0.9715 0.3868\r\nvt 0.9298 0.712\r\nvt 0.9294 0.7119\r\nvt 0.9699 0.4101\r\nvt 0.0919 0.4995\r\nvt 0.1283 0.5027\r\nvt 0.1653 0.507\r\nvt 0.0555 0.4976\r\nvt 0.8259 0.6495\r\nvt 0.8647 0.6626\r\nvt 0.8944 0.6741\r\nvt 0.3695 0.5523\r\nvt 0.3075 0.5403\r\nvt 0.2494 0.5298\r\nvt 0.7758 0.6348\r\nvt 0.5861 0.6025\r\nvt 0.5307 0.5889\r\nvt 0.4787 0.577\r\nvt 0.6495 0.6199\r\nvt 0.4258 0.5646\r\nvt 0.9314 0.6909\r\nvt 0.9364 0.6933\r\nvt 0.9365 0.6934\r\nvt 0.0012 0.4944\r\nvt 0.936 0.6933\r\nvt 0.0019 0.4984\r\nvt 0.011 0.4997\r\nvt 0.917 0.684\r\nvt 0.204 0.5193\r\nvt 0.2043 0.5218\r\nvt 0.2043 0.522\r\nvt 0.1927 0.4127\r\nvt 0.1953 0.4361\r\nvt 0.198 0.4608\r\nvt 0.1907 0.3949\r\nvt 0.2002 0.4825\r\nvt 0.202 0.5\r\nvt 0.1865 0.3578\r\nvt 0.2044 0.522\r\nvt 0.2045 0.5221\r\nvt 0.1146 0.38\r\nvt 0.1148 0.3801\r\nvt 0.1155 0.3899\r\nvt 0.2033 0.5121\r\no side_strip_mid_L\r\nusemtl side_strip_mid_L\r\nf 19093/19799/17608 19094/19800/17609 19095/19801/17610 19096/19802/17611\r\nf 19094/19800/17609 19097/19803/17612 19098/19804/17613 19095/19801/17610\r\nf 19099/19805/17614 19100/19806/17615 19095/19801/17610 19098/19804/17613\r\nf 19100/19806/17615 19101/19807/17616 19096/19802/17611 19095/19801/17610\r\nf 19102/19808/17617 19103/19809/17618 19104/19810/17619 19105/19811/17620\r\nf 19103/19809/17618 19106/19812/17621 19107/19813/17622 19104/19810/17619\r\nf 19108/19814/17623 19109/19815/17624 19104/19810/17619 19107/19813/17622\r\nf 19109/19815/17624 19110/19816/17625 19105/19811/17620 19104/19810/17619\r\nf 19111/19817/17626 19112/19818/17627 19113/19819/17628 19114/19820/17629\r\nf 19112/19818/17627 19115/19821/17630 19116/19822/17631 19113/19819/17628\r\nf 19117/19823/17632 19118/19824/17633 19113/19819/17628 19116/19822/17631\r\nf 19118/19824/17633 19119/19825/17634 19114/19820/17629 19113/19819/17628\r\nf 19120/19826/17635 19121/19827/17636 19122/19828/17637 19123/19829/17638\r\nf 19121/19827/17636 19124/19830/17639 19125/19831/17640 19122/19828/17637\r\nf 19126/19832/17641 19127/19833/17642 19122/19828/17637 19125/19831/17640\r\nf 19127/19833/17642 19128/19834/17643 19123/19829/17638 19122/19828/17637\r\nf 19115/19821/17630 19129/19835/17644 19130/19836/17645 19116/19822/17631\r\nf 19129/19835/17644 19102/19808/17617 19105/19811/17620 19130/19836/17645\r\nf 19110/19816/17625 19131/19837/17646 19130/19836/17645 19105/19811/17620\r\nf 19131/19837/17646 19117/19823/17632 19116/19822/17631 19130/19836/17645\r\nf 19097/19803/17612 19132/19838/17647 19133/19839/17648 19098/19804/17613\r\nf 19132/19838/17647 19111/19817/17626 19114/19820/17629 19133/19839/17648\r\nf 19119/19825/17634 19134/19840/17649 19133/19839/17648 19114/19820/17629\r\nf 19134/19840/17649 19099/19805/17614 19098/19804/17613 19133/19839/17648\r\nf 19135/19841/17650 19136/19842/17651 19137/19843/17652 19138/19844/17653\r\nf 19136/19842/17651 19139/19845/17654 19140/19846/17655 19137/19843/17652\r\nf 19124/19830/17639 19121/19827/17636 19137/19843/17652 19140/19846/17655\r\nf 19121/19827/17636 19120/19826/17635 19138/19844/17653 19137/19843/17652\r\nf 19106/19812/17621 19141/19847/17656 19142/19848/17657 19107/19813/17622\r\nf 19141/19847/17656 19143/19849/17658 19144/19850/17659 19142/19848/17657\r\nf 19145/19851/17660 19146/19852/17661 19142/19848/17657 19144/19850/17659\r\nf 19146/19852/17661 19108/19814/17623 19107/19813/17622 19142/19848/17657\r\nf 19147/19853/17662 19148/19854/17663 19149/19855/17664 19150/19856/17665\r\nf 19148/19854/17663 19151/19857/17666 19152/19858/17667 19149/19855/17664\r\nf 19153/19859/17668 19154/19860/17669 19149/19855/17664 19152/19858/17667\r\nf 19154/19860/17669 19155/19861/17670 19150/19856/17665 19149/19855/17664\r\nf 19156/19862/17671 19157/19863/17672 19158/19864/17673 19159/19865/17674\r\nf 19157/19863/17672 19160/19866/17675 19161/19867/17676 19158/19864/17673\r\nf 19162/19868/17677 19163/19869/17678 19158/19864/17673 19161/19867/17676\r\nf 19163/19869/17678 19164/19870/17679 19159/19865/17674 19158/19864/17673\r\nf 19165/19871/17680 19166/19872/17681 19167/19873/17682 19168/19874/17683\r\nf 19166/19872/17681 19169/19875/17684 19170/19876/17685 19167/19873/17682\r\nf 19171/19877/17686 19172/19878/17687 19167/19873/17682 19170/19876/17685\r\nf 19172/19878/17687 19173/19879/17688 19168/19874/17683 19167/19873/17682\r\nf 19174/19880/17689 19175/19881/17690 19176/19882/17691 19177/19883/17692\r\nf 19175/19881/17690 19178/19884/17693 19179/19885/17694 19176/19882/17691\r\nf 19180/19886/17695 19181/19887/17696 19176/19882/17691 19179/19885/17694\r\nf 19181/19887/17696 19182/19888/17697 19177/19883/17692 19176/19882/17691\r\nf 19160/19866/17675 19183/19889/17698 19184/19890/17699 19161/19867/17676\r\nf 19183/19889/17698 19169/19875/17684 19166/19872/17681 19184/19890/17699\r\nf 19165/19871/17680 19185/19891/17700 19184/19890/17699 19166/19872/17681\r\nf 19185/19891/17700 19162/19868/17677 19161/19867/17676 19184/19890/17699\r\nf 19173/19879/17688 19172/19878/17687 19186/19892/17701 19187/19893/17702\r\nf 19172/19878/17687 19171/19877/17686 19188/19894/17703 19186/19892/17701\r\nf 19151/19857/17666 19148/19854/17663 19186/19892/17701 19188/19894/17703\r\nf 19148/19854/17663 19147/19853/17662 19187/19893/17702 19186/19892/17701\r\nf 19189/19895/17704 19190/19896/17705 19191/19897/17706 19192/19898/17707\r\nf 19190/19896/17705 19193/19899/17708 19194/19900/17709 19191/19897/17706\r\nf 19195/19901/17710 19196/19902/17711 19191/19897/17706 19194/19900/17709\r\nf 19196/19902/17711 19197/19903/17712 19192/19898/17707 19191/19897/17706\r\nf 19198/19904/17713 19199/19905/17714 19200/19906/17715 19201/19907/17716\r\nf 19199/19905/17714 19147/19853/17662 19150/19856/17665 19200/19906/17715\r\nf 19155/19861/17670 19202/19908/17717 19200/19906/17715 19150/19856/17665\r\nf 19202/19908/17717 19203/19909/17718 19201/19907/17716 19200/19906/17715\r\nf 19164/19870/17679 19163/19869/17678 19204/19910/17719 19205/19911/17720\r\nf 19163/19869/17678 19162/19868/17677 19206/19912/17721 19204/19910/17719\r\nf 19207/19913/17722 19208/19914/17723 19204/19910/17719 19206/19912/17721\r\nf 19208/19914/17723 19209/19915/17724 19205/19911/17720 19204/19910/17719\r\nf 19165/19871/17680 19168/19874/17683 19210/19916/17725 19211/19917/17726\r\nf 19168/19874/17683 19173/19879/17688 19212/19918/17727 19210/19916/17725\r\nf 19213/19919/17728 19214/19920/17729 19210/19916/17725 19212/19918/17727\r\nf 19214/19920/17729 19215/19921/17730 19211/19917/17726 19210/19916/17725\r\nf 19162/19868/17677 19185/19891/17700 19216/19922/17731 19206/19912/17721\r\nf 19185/19891/17700 19165/19871/17680 19211/19917/17726 19216/19922/17731\r\nf 19215/19921/17730 19217/19923/17732 19216/19922/17731 19211/19917/17726\r\nf 19217/19923/17732 19207/19913/17722 19206/19912/17721 19216/19922/17731\r\nf 19173/19879/17688 19187/19893/17702 19218/19924/17733 19212/19918/17727\r\nf 19187/19893/17702 19147/19853/17662 19199/19905/17714 19218/19924/17733\r\nf 19198/19904/17713 19219/19925/17734 19218/19924/17733 19199/19905/17714\r\nf 19219/19925/17734 19213/19919/17728 19212/19918/17727 19218/19924/17733\r\nf 19178/19884/17693 19220/19926/17735 19221/19927/17736 19179/19885/17694\r\nf 19220/19926/17735 19222/19928/17737 19223/19929/17738 19221/19927/17736\r\nf 19224/19930/17739 19225/19931/17740 19221/19927/17736 19223/19929/17738\r\nf 19225/19931/17740 19180/19886/17695 19179/19885/17694 19221/19927/17736\r\nf 19193/19899/17708 19226/19932/17741 19227/19933/17742 19194/19900/17709\r\nf 19226/19932/17741 19228/19934/17743 19229/19935/17744 19227/19933/17742\r\nf 19230/19936/17745 19231/19937/17746 19227/19933/17742 19229/19935/17744\r\nf 19231/19937/17746 19195/19901/17710 19194/19900/17709 19227/19933/17742\r\nf 19143/19849/17658 19232/19938/17747 19233/19939/17748 19144/19850/17659\r\nf 19232/19938/17747 19234/19940/17749 19235/19941/17750 19233/19939/17748\r\nf 19236/19942/17751 19237/19943/17752 19233/19939/17748 19235/19941/17750\r\nf 19237/19943/17752 19145/19851/17660 19144/19850/17659 19233/19939/17748\r\nf 19238/19944/17753 19239/19945/17754 19240/19946/17755 19241/19947/17756\r\nf 19239/19945/17754 19242/19948/17757 19243/19949/17758 19240/19946/17755\r\nf 19244/19950/17759 19245/19951/17760 19240/19946/17755 19243/19949/17758\r\nf 19245/19951/17760 19246/19952/17761 19241/19947/17756 19240/19946/17755\r\nf 19247/19953/17762 19248/19954/17763 19249/19955/17764 19250/19956/17765\r\nf 19248/19954/17763 19164/19870/17679 19205/19911/17720 19249/19955/17764\r\nf 19209/19915/17724 19251/19957/17766 19249/19955/17764 19205/19911/17720\r\nf 19251/19957/17766 19252/19958/17767 19250/19956/17765 19249/19955/17764\r\nf 19253/19959/17768 19254/19960/17769 19255/19961/17770 19256/19962/17771\r\nf 19254/19960/17769 19174/19880/17689 19177/19883/17692 19255/19961/17770\r\nf 19182/19888/17697 19257/19963/17772 19255/19961/17770 19177/19883/17692\r\nf 19257/19963/17772 19258/19964/17773 19256/19962/17771 19255/19961/17770\r\nf 19259/19965/17774 19260/19966/17775 19261/19967/17776 19262/19968/17777\r\nf 19260/19966/17775 19189/19895/17704 19192/19898/17707 19261/19967/17776\r\nf 19197/19903/17712 19263/19969/17778 19261/19967/17776 19192/19898/17707\r\nf 19263/19969/17778 19264/19970/17779 19262/19968/17777 19261/19967/17776\r\nf 19151/19857/17666 19265/19971/17780 19266/19972/17781 19152/19858/17667\r\nf 19265/19971/17780 19267/19973/17782 19268/19974/17783 19266/19972/17781\r\nf 19269/19975/17784 19270/19976/17785 19266/19972/17781 19268/19974/17783\r\nf 19270/19976/17785 19153/19859/17668 19152/19858/17667 19266/19972/17781\r\nf 19242/19948/17757 19271/19977/17786 19272/19978/17787 19243/19949/17758\r\nf 19271/19977/17786 19273/19979/17788 19274/19980/17789 19272/19978/17787\r\nf 19275/19981/17790 19276/19982/17791 19272/19978/17787 19274/19980/17789\r\nf 19276/19982/17791 19244/19950/17759 19243/19949/17758 19272/19978/17787\r\nf 19169/19875/17684 19277/19983/17792 19278/19984/17793 19170/19876/17685\r\nf 19277/19983/17792 19279/19985/17794 19280/19986/17795 19278/19984/17793\r\nf 19281/19987/17796 19282/19988/17797 19278/19984/17793 19280/19986/17795\r\nf 19282/19988/17797 19171/19877/17686 19170/19876/17685 19278/19984/17793\r\nf 19160/19866/17675 19283/19989/17798 19284/19990/17799 19183/19889/17698\r\nf 19283/19989/17798 19275/19981/17790 19285/19991/17800 19284/19990/17799\r\nf 19279/19985/17794 19277/19983/17792 19284/19990/17799 19285/19991/17800\r\nf 19277/19983/17792 19169/19875/17684 19183/19889/17698 19284/19990/17799\r\nf 19171/19877/17686 19282/19988/17797 19286/19992/17801 19188/19894/17703\r\nf 19282/19988/17797 19281/19987/17796 19287/19993/17802 19286/19992/17801\r\nf 19267/19973/17782 19265/19971/17780 19286/19992/17801 19287/19993/17802\r\nf 19265/19971/17780 19151/19857/17666 19188/19894/17703 19286/19992/17801\r\nf 19164/19870/17679 19248/19954/17763 19288/19994/17803 19159/19865/17674\r\nf 19248/19954/17763 19247/19953/17762 19289/19995/17804 19288/19994/17803\r\nf 19290/19996/17805 19291/19997/17806 19288/19994/17803 19289/19995/17804\r\nf 19291/19997/17806 19156/19862/17671 19159/19865/17674 19288/19994/17803\r\nf 19198/19904/17713 19201/19907/17716 19292/19998/17807 19293/19999/17808\r\nf 19201/19907/17716 19203/19909/17718 19294/20000/17809 19292/19998/17807\r\nf 19295/20001/17810 19296/20002/17811 19292/19998/17807 19294/20000/17809\r\nf 19296/20002/17811 19297/20003/17812 19293/19999/17808 19292/19998/17807\r\nf 19209/19915/17724 19208/19914/17723 19298/20004/17813 19299/20005/17814\r\nf 19208/19914/17723 19207/19913/17722 19300/20006/17815 19298/20004/17813\r\nf 19301/20007/17816 19302/20008/17817 19298/20004/17813 19300/20006/17815\r\nf 19302/20008/17817 19303/20009/17818 19299/20005/17814 19298/20004/17813\r\nf 19215/19921/17730 19214/19920/17729 19304/20010/17819 19305/20011/17820\r\nf 19214/19920/17729 19213/19919/17728 19306/20012/17821 19304/20010/17819\r\nf 19307/20013/17822 19308/20014/17823 19304/20010/17819 19306/20012/17821\r\nf 19308/20014/17823 19309/20015/17824 19305/20011/17820 19304/20010/17819\r\nf 19207/19913/17722 19217/19923/17732 19310/20016/17825 19300/20006/17815\r\nf 19217/19923/17732 19215/19921/17730 19305/20011/17820 19310/20016/17825\r\nf 19309/20015/17824 19311/20017/17826 19310/20016/17825 19305/20011/17820\r\nf 19311/20017/17826 19301/20007/17816 19300/20006/17815 19310/20016/17825\r\nf 19213/19919/17728 19219/19925/17734 19312/20018/17827 19306/20012/17821\r\nf 19219/19925/17734 19198/19904/17713 19293/19999/17808 19312/20018/17827\r\nf 19297/20003/17812 19313/20019/17828 19312/20018/17827 19293/19999/17808\r\nf 19313/20019/17828 19307/20013/17822 19306/20012/17821 19312/20018/17827\r\nf 19252/19958/17767 19251/19957/17766 19314/20020/17829 19315/20021/17830\r\nf 19251/19957/17766 19209/19915/17724 19299/20005/17814 19314/20020/17829\r\nf 19303/20009/17818 19316/20022/17831 19314/20020/17829 19299/20005/17814\r\nf 19316/20022/17831 19317/20023/17832 19315/20021/17830 19314/20020/17829\r\nf 19318/20024/17833 19319/20025/17834 19320/20026/17835 19321/20027/17836\r\nf 19319/20025/17834 19322/20028/17837 19323/20029/17838 19320/20026/17835\r\nf 19324/20030/17839 19325/20031/17840 19320/20026/17835 19323/20029/17838\r\nf 19325/20031/17840 19326/20032/17841 19321/20027/17836 19320/20026/17835\r\nf 19327/20033/17842 19328/20034/17843 19329/20035/17844 19330/20036/17845\r\nf 19328/20034/17843 19331/20037/17846 19332/20038/17847 19329/20035/17844\r\nf 19333/20039/17848 19334/20040/17849 19329/20035/17844 19332/20038/17847\r\nf 19334/20040/17849 19335/20041/17850 19330/20036/17845 19329/20035/17844\r\nf 19336/20042/17851 19337/20043/17852 19338/20044/17853 19339/20045/17854\r\nf 19337/20043/17852 19340/20046/17855 19341/20047/17856 19338/20044/17853\r\nf 19342/20048/17857 19343/20049/17858 19338/20044/17853 19341/20047/17856\r\nf 19343/20049/17858 19344/20050/17859 19339/20045/17854 19338/20044/17853\r\nf 19344/20050/17859 19343/20049/17858 19345/20051/17860 19346/20052/17861\r\nf 19343/20049/17858 19342/20048/17857 19347/20053/17862 19345/20051/17860\r\nf 19348/20054/17863 19349/20055/17864 19345/20051/17860 19347/20053/17862\r\nf 19349/20055/17864 19350/20056/17865 19346/20052/17861 19345/20051/17860\r\nf 19351/20057/17866 19352/20058/17867 19353/20059/17868 19354/20060/17869\r\nf 19352/20058/17867 19355/20061/17870 19356/20062/17871 19353/20059/17868\r\nf 19357/20063/17872 19358/20064/17873 19353/20059/17868 19356/20062/17871\r\nf 19358/20064/17873 19359/20065/17874 19354/20060/17869 19353/20059/17868\r\nf 19350/20056/17865 19349/20055/17864 19360/20066/17875 19361/20067/17876\r\nf 19349/20055/17864 19348/20054/17863 19362/20068/17877 19360/20066/17875\r\nf 19363/20069/17878 19364/20070/17879 19360/20066/17875 19362/20068/17877\r\nf 19364/20070/17879 19365/20071/17880 19361/20067/17876 19360/20066/17875\r\nf 19366/20072/17881 19367/20073/17882 19368/20074/17883 19369/20075/17884\r\nf 19367/20073/17882 19370/20076/17885 19371/20077/17886 19368/20074/17883\r\nf 19372/20078/17887 19373/20079/17888 19368/20074/17883 19371/20077/17886\r\nf 19373/20079/17888 19374/20080/17889 19369/20075/17884 19368/20074/17883\r\nf 19178/19884/17693 19175/19881/17690 19375/20081/17890 19376/20082/17891\r\nf 19175/19881/17690 19174/19880/17689 19377/20083/17892 19375/20081/17890\r\nf 19378/20084/17893 19379/20085/17894 19375/20081/17890 19377/20083/17892\r\nf 19379/20085/17894 19380/20086/17895 19376/20082/17891 19375/20081/17890\r\nf 19222/19928/17737 19220/19926/17735 19381/20087/17896 19382/20088/17897\r\nf 19220/19926/17735 19178/19884/17693 19376/20082/17891 19381/20087/17896\r\nf 19380/20086/17895 19383/20089/17898 19381/20087/17896 19376/20082/17891\r\nf 19383/20089/17898 19384/20090/17899 19382/20088/17897 19381/20087/17896\r\nf 19174/19880/17689 19254/19960/17769 19385/20091/17900 19377/20083/17892\r\nf 19254/19960/17769 19253/19959/17768 19386/20092/17901 19385/20091/17900\r\nf 19387/20093/17902 19388/20094/17903 19385/20091/17900 19386/20092/17901\r\nf 19388/20094/17903 19378/20084/17893 19377/20083/17892 19385/20091/17900\r\nf 19389/20095/17904 19390/20096/17905 19391/20097/17906 19392/20098/17907\r\nf 19390/20096/17905 19318/20024/17833 19321/20027/17836 19391/20097/17906\r\nf 19326/20032/17841 19393/20099/17908 19391/20097/17906 19321/20027/17836\r\nf 19393/20099/17908 19394/20100/17909 19392/20098/17907 19391/20097/17906\r\nf 19097/19803/17612 19094/19800/17609 19395/20101/17910 19396/20102/17911\r\nf 19094/19800/17609 19093/19799/17608 19397/20103/17912 19395/20101/17910\r\nf 19398/20104/17913 19399/20105/17914 19395/20101/17910 19397/20103/17912\r\nf 19399/20105/17914 19400/20106/17915 19396/20102/17911 19395/20101/17910\r\nf 19106/19812/17621 19103/19809/17618 19401/20107/17916 19402/20108/17917\r\nf 19103/19809/17618 19102/19808/17617 19403/20109/17918 19401/20107/17916\r\nf 19404/20110/17919 19405/20111/17920 19401/20107/17916 19403/20109/17918\r\nf 19405/20111/17920 19406/20112/17921 19402/20108/17917 19401/20107/17916\r\nf 19115/19821/17630 19112/19818/17627 19407/20113/17922 19408/20114/17923\r\nf 19112/19818/17627 19111/19817/17626 19409/20115/17924 19407/20113/17922\r\nf 19410/20116/17925 19411/20117/17926 19407/20113/17922 19409/20115/17924\r\nf 19411/20117/17926 19412/20118/17927 19408/20114/17923 19407/20113/17922\r\nf 19120/19826/17635 19123/19829/17638 19413/20119/17928 19414/20120/17929\r\nf 19123/19829/17638 19128/19834/17643 19415/20121/17930 19413/20119/17928\r\nf 19416/20122/17931 19417/20123/17932 19413/20119/17928 19415/20121/17930\r\nf 19417/20123/17932 19418/20124/17933 19414/20120/17929 19413/20119/17928\r\nf 19102/19808/17617 19129/19835/17644 19419/20125/17934 19403/20109/17918\r\nf 19129/19835/17644 19115/19821/17630 19408/20114/17923 19419/20125/17934\r\nf 19412/20118/17927 19420/20126/17935 19419/20125/17934 19408/20114/17923\r\nf 19420/20126/17935 19404/20110/17919 19403/20109/17918 19419/20125/17934\r\nf 19111/19817/17626 19132/19838/17647 19421/20127/17936 19409/20115/17924\r\nf 19132/19838/17647 19097/19803/17612 19396/20102/17911 19421/20127/17936\r\nf 19400/20106/17915 19422/20128/17937 19421/20127/17936 19396/20102/17911\r\nf 19422/20128/17937 19410/20116/17925 19409/20115/17924 19421/20127/17936\r\nf 19135/19841/17650 19138/19844/17653 19423/20129/17938 19424/20130/17939\r\nf 19138/19844/17653 19120/19826/17635 19414/20120/17929 19423/20129/17938\r\nf 19418/20124/17933 19425/20131/17940 19423/20129/17938 19414/20120/17929\r\nf 19425/20131/17940 19426/20132/17941 19424/20130/17939 19423/20129/17938\r\nf 19143/19849/17658 19141/19847/17656 19427/20133/17942 19428/20134/17943\r\nf 19141/19847/17656 19106/19812/17621 19402/20108/17917 19427/20133/17942\r\nf 19406/20112/17921 19429/20135/17944 19427/20133/17942 19402/20108/17917\r\nf 19429/20135/17944 19430/20136/17945 19428/20134/17943 19427/20133/17942\r\nf 19193/19899/17708 19190/19896/17705 19431/20137/17946 19432/20138/17947\r\nf 19190/19896/17705 19189/19895/17704 19433/20139/17948 19431/20137/17946\r\nf 19434/20140/17949 19435/20141/17950 19431/20137/17946 19433/20139/17948\r\nf 19435/20141/17950 19436/20142/17951 19432/20138/17947 19431/20137/17946\r\nf 19228/19934/17743 19226/19932/17741 19437/20143/17952 19438/20144/17953\r\nf 19226/19932/17741 19193/19899/17708 19432/20138/17947 19437/20143/17952\r\nf 19436/20142/17951 19439/20145/17954 19437/20143/17952 19432/20138/17947\r\nf 19439/20145/17954 19440/20146/17955 19438/20144/17953 19437/20143/17952\r\nf 19441/20147/17956 19442/20148/17957 19443/20149/17958 19444/20150/17959\r\nf 19442/20148/17957 19234/19940/17749 19445/20151/17960 19443/20149/17958\r\nf 19446/20152/17961 19447/20153/17962 19443/20149/17958 19445/20151/17960\r\nf 19447/20153/17962 19448/20154/17963 19444/20150/17959 19443/20149/17958\r\nf 19365/20071/17880 19449/20155/17964 19450/20156/17965 19451/20157/17966\r\nf 19449/20155/17964 19452/20158/17967 19453/20159/17968 19450/20156/17965\r\nf 19454/20160/17969 19455/20161/17970 19450/20156/17965 19453/20159/17968\r\nf 19455/20161/17970 19456/20162/17971 19451/20157/17966 19450/20156/17965\r\nf 19242/19948/17757 19239/19945/17754 19457/20163/17972 19458/20164/17973\r\nf 19239/19945/17754 19238/19944/17753 19459/20165/17974 19457/20163/17972\r\nf 19460/20166/17975 19461/20167/17976 19457/20163/17972 19459/20165/17974\r\nf 19461/20167/17976 19462/20168/17977 19458/20164/17973 19457/20163/17972\r\nf 19189/19895/17704 19260/19966/17775 19463/20169/17978 19433/20139/17948\r\nf 19260/19966/17775 19259/19965/17774 19464/20170/17979 19463/20169/17978\r\nf 19465/20171/17980 19466/20172/17981 19463/20169/17978 19464/20170/17979\r\nf 19466/20172/17981 19434/20140/17949 19433/20139/17948 19463/20169/17978\r\nf 19467/20173/17982 19468/20174/17983 19469/20174/17984 19470/20175/17985\r\nf 19468/20174/17983 19471/20176/17986 19472/20176/17987 19469/20174/17984\r\nf 19473/20177/17988 19474/20178/17989 19469/20174/17984 19472/20176/17987\r\nf 19474/20178/17989 19475/20179/17990 19470/20175/17985 19469/20174/17984\r\nf 19273/19979/17788 19271/19977/17786 19476/20180/17991 19477/20181/17992\r\nf 19271/19977/17786 19242/19948/17757 19458/20164/17973 19476/20180/17991\r\nf 19462/20168/17977 19478/20182/17993 19476/20180/17991 19458/20164/17973\r\nf 19478/20182/17993 19479/20183/17994 19477/20181/17992 19476/20180/17991\r\nf 19480/20184/17995 19481/20185/17996 19482/20186/17997 19483/20187/17998\r\nf 19481/20185/17996 19484/20188/17999 19485/20189/18000 19482/20186/17997\r\nf 19486/20190/18001 19487/20191/18002 19482/20186/17997 19485/20189/18000\r\nf 19487/20191/18002 19488/20192/18003 19483/20187/17998 19482/20186/17997\r\nf 19484/20188/17999 19489/20193/18004 19490/20194/18005 19485/20189/18000\r\nf 19489/20193/18004 19273/19979/17788 19477/20181/17992 19490/20194/18005\r\nf 19479/20183/17994 19491/20195/18006 19490/20194/18005 19477/20181/17992\r\nf 19491/20195/18006 19486/20190/18001 19485/20189/18000 19490/20194/18005\r\nf 19471/20176/17986 19492/20196/18007 19493/20197/18008 19472/20176/17987\r\nf 19492/20196/18007 19480/20184/17995 19483/20187/17998 19493/20197/18008\r\nf 19488/20192/18003 19494/20197/18009 19493/20197/18008 19483/20187/17998\r\nf 19494/20197/18009 19473/20177/17988 19472/20176/17987 19493/20197/18008\r\nf 19093/19799/17608 19495/20198/18010 19496/20199/18011 19397/20103/17912\r\nf 19495/20198/18010 19322/20028/17837 19497/20200/18012 19496/20199/18011\r\nf 19498/20201/18013 19499/20202/18014 19496/20199/18011 19497/20200/18012\r\nf 19499/20202/18014 19398/20104/17913 19397/20103/17912 19496/20199/18011\r\nf 19389/20095/17904 19500/20203/18015 19501/20204/18016 19502/20205/18017\r\nf 19500/20203/18015 19331/20037/17846 19503/20206/18018 19501/20204/18016\r\nf 19504/20207/18019 19505/20208/18020 19501/20204/18016 19503/20206/18018\r\nf 19505/20208/18020 19506/20209/18021 19502/20205/18017 19501/20204/18016\r\nf 19327/20033/17842 19330/20036/17845 19507/20210/18022 19508/20211/18023\r\nf 19330/20036/17845 19335/20041/17850 19509/20212/18024 19507/20210/18022\r\nf 19510/20213/18025 19511/20214/18026 19507/20210/18022 19509/20212/18024\r\nf 19511/20214/18026 19512/20215/18027 19508/20211/18023 19507/20210/18022\r\nf 19336/20042/17851 19339/20045/17854 19513/20216/18028 19514/20217/18029\r\nf 19339/20045/17854 19344/20050/17859 19515/20218/18030 19513/20216/18028\r\nf 19516/20219/18031 19517/20220/18032 19513/20216/18028 19515/20218/18030\r\nf 19517/20220/18032 19518/20221/18033 19514/20217/18029 19513/20216/18028\r\nf 19344/20050/17859 19346/20052/17861 19519/20222/18034 19515/20218/18030\r\nf 19346/20052/17861 19350/20056/17865 19520/20223/18035 19519/20222/18034\r\nf 19521/20224/18036 19522/20225/18037 19519/20222/18034 19520/20223/18035\r\nf 19522/20225/18037 19516/20219/18031 19515/20218/18030 19519/20222/18034\r\nf 19238/19944/17753 19523/20226/18038 19524/20227/18039 19459/20165/17974\r\nf 19523/20226/18038 19355/20061/17870 19525/20228/18040 19524/20227/18039\r\nf 19526/20229/18041 19527/20230/18042 19524/20227/18039 19525/20228/18040\r\nf 19527/20230/18042 19460/20166/17975 19459/20165/17974 19524/20227/18039\r\nf 19351/20057/17866 19354/20060/17869 19528/20231/18043 19529/20058/18044\r\nf 19354/20060/17869 19359/20065/17874 19530/20232/18045 19528/20231/18043\r\nf 19531/20233/18046 19532/20234/18047 19528/20231/18043 19530/20232/18045\r\nf 19532/20234/18047 19533/20235/18048 19529/20058/18044 19528/20231/18043\r\nf 19350/20056/17865 19361/20067/17876 19534/20236/18049 19520/20223/18035\r\nf 19361/20067/17876 19365/20071/17880 19451/20157/17966 19534/20236/18049\r\nf 19456/20162/17971 19535/20237/18050 19534/20236/18049 19451/20157/17966\r\nf 19535/20237/18050 19521/20224/18036 19520/20223/18035 19534/20236/18049\r\nf 19259/19965/17774 19536/20238/18051 19537/20239/18052 19464/20170/17979\r\nf 19536/20238/18051 19370/20076/17885 19538/20240/18053 19537/20239/18052\r\nf 19539/20241/18054 19540/20242/18055 19537/20239/18052 19538/20240/18053\r\nf 19540/20242/18055 19465/20171/17980 19464/20170/17979 19537/20239/18052\r\nf 19366/20072/17881 19369/20075/17884 19541/20243/18056 19542/20244/18057\r\nf 19369/20075/17884 19374/20080/17889 19543/20245/18058 19541/20243/18056\r\nf 19544/20246/18059 19545/20247/18060 19541/20243/18056 19543/20245/18058\r\nf 19545/20247/18060 19546/20243/18061 19542/20244/18057 19541/20243/18056\r\nf 19318/20024/17833 19390/20096/17905 19547/20248/18062 19548/20249/18063\r\nf 19390/20096/17905 19389/20095/17904 19502/20205/18017 19547/20248/18062\r\nf 19506/20209/18021 19549/20250/18064 19547/20248/18062 19502/20205/18017\r\nf 19549/20250/18064 19550/20251/18065 19548/20249/18063 19547/20248/18062\r\nf 19195/19901/17710 19551/20252/18066 19552/20253/18067 19196/19902/17711\r\nf 19551/20252/18066 19380/20086/17895 19379/20085/17894 19552/20253/18067\r\nf 19378/20084/17893 19553/20254/18068 19552/20253/18067 19379/20085/17894\r\nf 19553/20254/18068 19197/19903/17712 19196/19902/17711 19552/20253/18067\r\nf 19230/19936/17745 19554/20255/18069 19555/20256/18070 19231/19937/17746\r\nf 19554/20255/18069 19384/20090/17899 19383/20089/17898 19555/20256/18070\r\nf 19380/20086/17895 19551/20252/18066 19555/20256/18070 19383/20089/17898\r\nf 19551/20252/18066 19195/19901/17710 19231/19937/17746 19555/20256/18070\r\nf 19197/19903/17712 19553/20254/18068 19556/20257/18071 19263/19969/17778\r\nf 19553/20254/18068 19378/20084/17893 19388/20094/17903 19556/20257/18071\r\nf 19387/20093/17902 19557/20258/18072 19556/20257/18071 19388/20094/17903\r\nf 19557/20258/18072 19264/19970/17779 19263/19969/17778 19556/20257/18071\r\nf 19331/20037/17846 19500/20203/18015 19558/20259/18073 19332/20038/17847\r\nf 19500/20203/18015 19389/20095/17904 19392/20098/17907 19558/20259/18073\r\nf 19394/20100/17909 19559/20260/18074 19558/20259/18073 19392/20098/17907\r\nf 19559/20260/18074 19333/20039/17848 19332/20038/17847 19558/20259/18073\r\nf 19264/19970/17779 19557/20258/18072 19560/20261/18075 19561/20262/18076\r\nf 19557/20258/18072 19387/20093/17902 19562/20263/18077 19560/20261/18075\r\nf 19139/19845/17654 19563/20264/18078 19560/20261/18075 19562/20263/18077\r\nf 19563/20264/18078 19372/20078/17887 19561/20262/18076 19560/20261/18075\r\nf 19331/20037/17846 19328/20034/17843 19564/20265/18079 19503/20206/18018\r\nf 19328/20034/17843 19327/20033/17842 19508/20211/18023 19564/20265/18079\r\nf 19512/20215/18027 19565/20266/18080 19564/20265/18079 19508/20211/18023\r\nf 19565/20266/18080 19504/20207/18019 19503/20206/18018 19564/20265/18079\r\nf 19374/20080/17889 19566/20267/18081 19567/20268/18082 19543/20245/18058\r\nf 19566/20267/18081 19135/19841/17650 19424/20130/17939 19567/20268/18082\r\nf 19426/20132/17941 19568/20269/18083 19567/20268/18082 19424/20130/17939\r\nf 19568/20269/18083 19544/20246/18059 19543/20245/18058 19567/20268/18082\r\nf 19099/19805/17614 19569/20270/18084 19570/20271/18085 19100/19806/17615\r\nf 19569/20270/18084 19297/20003/17812 19296/20002/17811 19570/20271/18085\r\nf 19295/20001/17810 19571/20272/18086 19570/20271/18085 19296/20002/17811\r\nf 19571/20272/18086 19101/19807/17616 19100/19806/17615 19570/20271/18085\r\nf 19108/19814/17623 19572/20273/18087 19573/20274/18088 19109/19815/17624\r\nf 19572/20273/18087 19303/20009/17818 19302/20008/17817 19573/20274/18088\r\nf 19301/20007/17816 19574/20275/18089 19573/20274/18088 19302/20008/17817\r\nf 19574/20275/18089 19110/19816/17625 19109/19815/17624 19573/20274/18088\r\nf 19117/19823/17632 19575/20276/18090 19576/20277/18091 19118/19824/17633\r\nf 19575/20276/18090 19309/20015/17824 19308/20014/17823 19576/20277/18091\r\nf 19307/20013/17822 19577/20278/18092 19576/20277/18091 19308/20014/17823\r\nf 19577/20278/18092 19119/19825/17634 19118/19824/17633 19576/20277/18091\r\nf 19110/19816/17625 19574/20275/18089 19578/20279/18093 19131/19837/17646\r\nf 19574/20275/18089 19301/20007/17816 19311/20017/17826 19578/20279/18093\r\nf 19309/20015/17824 19575/20276/18090 19578/20279/18093 19311/20017/17826\r\nf 19575/20276/18090 19117/19823/17632 19131/19837/17646 19578/20279/18093\r\nf 19119/19825/17634 19577/20278/18092 19579/20280/18094 19134/19840/17649\r\nf 19577/20278/18092 19307/20013/17822 19313/20019/17828 19579/20280/18094\r\nf 19297/20003/17812 19569/20270/18084 19579/20280/18094 19313/20019/17828\r\nf 19569/20270/18084 19099/19805/17614 19134/19840/17649 19579/20280/18094\r\nf 19145/19851/17660 19580/20281/18095 19581/20282/18096 19146/19852/17661\r\nf 19580/20281/18095 19317/20023/17832 19316/20022/17831 19581/20282/18096\r\nf 19303/20009/17818 19572/20273/18087 19581/20282/18096 19316/20022/17831\r\nf 19572/20273/18087 19108/19814/17623 19146/19852/17661 19581/20282/18096\r\nf 19452/20158/17967 19449/20155/17964 19582/20283/18097 19583/20284/18098\r\nf 19449/20155/17964 19365/20071/17880 19364/20070/17879 19582/20283/18097\r\nf 19363/20069/17878 19584/20285/18099 19582/20283/18097 19364/20070/17879\r\nf 19584/20285/18099 19236/19942/17751 19583/20284/18098 19582/20283/18097\r\nf 19101/19807/17616 19571/20272/18086 19585/20286/18100 19586/20287/18101\r\nf 19571/20272/18086 19295/20001/17810 19587/20288/18102 19585/20286/18100\r\nf 19224/19930/17739 19588/20289/18103 19585/20286/18100 19587/20288/18102\r\nf 19588/20289/18103 19324/20030/17839 19586/20287/18101 19585/20286/18100\r\nf 19333/20039/17848 19589/20290/18104 19590/20291/18105 19334/20040/17849\r\nf 19589/20290/18104 19230/19936/17745 19229/19935/17744 19590/20291/18105\r\nf 19228/19934/17743 19591/20292/18106 19590/20291/18105 19229/19935/17744\r\nf 19591/20292/18106 19335/20041/17850 19334/20040/17849 19590/20291/18105\r\nf 19326/20032/17841 19592/20293/18107 19593/20294/18108 19393/20099/17908\r\nf 19592/20293/18107 19222/19928/17737 19382/20088/17897 19593/20294/18108\r\nf 19384/20090/17899 19594/20295/18109 19593/20294/18108 19382/20088/17897\r\nf 19594/20295/18109 19394/20100/17909 19393/20099/17908 19593/20294/18108\r\nf 19452/20158/17967 19595/20296/18110 19596/20297/18111 19453/20159/17968\r\nf 19595/20296/18110 19441/20147/17956 19444/20150/17959 19596/20297/18111\r\nf 19448/20154/17963 19597/20298/18112 19596/20297/18111 19444/20150/17959\r\nf 19597/20298/18112 19454/20160/17969 19453/20159/17968 19596/20297/18111\r\nf 19335/20041/17850 19591/20292/18106 19598/20299/18113 19509/20212/18024\r\nf 19591/20292/18106 19228/19934/17743 19438/20144/17953 19598/20299/18113\r\nf 19440/20146/17955 19599/20300/18114 19598/20299/18113 19438/20144/17953\r\nf 19599/20300/18114 19510/20213/18025 19509/20212/18024 19598/20299/18113\r\nf 19394/20100/17909 19594/20295/18109 19600/20301/18115 19559/20260/18074\r\nf 19594/20295/18109 19384/20090/17899 19554/20255/18069 19600/20301/18115\r\nf 19230/19936/17745 19589/20290/18104 19600/20301/18115 19554/20255/18069\r\nf 19589/20290/18104 19333/20039/17848 19559/20260/18074 19600/20301/18115\r\nf 19234/19940/17749 19442/20148/17957 19601/20302/18116 19235/19941/17750\r\nf 19442/20148/17957 19441/20147/17956 19595/20296/18110 19601/20302/18116\r\nf 19452/20158/17967 19583/20284/18098 19601/20302/18116 19595/20296/18110\r\nf 19583/20284/18098 19236/19942/17751 19235/19941/17750 19601/20302/18116\r\nf 19340/20046/17855 19602/20303/18117 19603/20304/18118 19341/20047/17856\r\nf 19602/20303/18117 19290/19996/17805 19289/19995/17804 19603/20304/18118\r\nf 19247/19953/17762 19604/20305/18119 19603/20304/18118 19289/19995/17804\r\nf 19604/20305/18119 19342/20048/17857 19341/20047/17856 19603/20304/18118\r\nf 19342/20048/17857 19604/20305/18119 19605/20306/18120 19347/20053/17862\r\nf 19604/20305/18119 19247/19953/17762 19250/19956/17765 19605/20306/18120\r\nf 19252/19958/17767 19606/20307/18121 19605/20306/18120 19250/19956/17765\r\nf 19606/20307/18121 19348/20054/17863 19347/20053/17862 19605/20306/18120\r\nf 19355/20061/17870 19523/20226/18038 19607/20308/18122 19356/20062/17871\r\nf 19523/20226/18038 19238/19944/17753 19241/19947/17756 19607/20308/18122\r\nf 19246/19952/17761 19608/20309/18123 19607/20308/18122 19241/19947/17756\r\nf 19608/20309/18123 19357/20063/17872 19356/20062/17871 19607/20308/18122\r\nf 19348/20054/17863 19606/20307/18121 19609/20310/18124 19362/20068/17877\r\nf 19606/20307/18121 19252/19958/17767 19315/20021/17830 19609/20310/18124\r\nf 19317/20023/17832 19610/20311/18125 19609/20310/18124 19315/20021/17830\r\nf 19610/20311/18125 19363/20069/17878 19362/20068/17877 19609/20310/18124\r\nf 19234/19940/17749 19232/19938/17747 19611/20312/18126 19445/20151/17960\r\nf 19232/19938/17747 19143/19849/17658 19428/20134/17943 19611/20312/18126\r\nf 19430/20136/17945 19612/20313/18127 19611/20312/18126 19428/20134/17943\r\nf 19612/20313/18127 19446/20152/17961 19445/20151/17960 19611/20312/18126\r\nf 19355/20061/17870 19352/20058/17867 19613/20314/18128 19525/20228/18040\r\nf 19352/20058/17867 19351/20057/17866 19529/20058/18044 19613/20314/18128\r\nf 19533/20235/18048 19614/20315/18129 19613/20314/18128 19529/20058/18044\r\nf 19614/20315/18129 19526/20229/18041 19525/20228/18040 19613/20314/18128\r\nf 19363/20069/17878 19610/20311/18125 19615/20316/18130 19584/20285/18099\r\nf 19610/20311/18125 19317/20023/17832 19580/20281/18095 19615/20316/18130\r\nf 19145/19851/17660 19237/19943/17752 19615/20316/18130 19580/20281/18095\r\nf 19237/19943/17752 19236/19942/17751 19584/20285/18099 19615/20316/18130\r\nf 19124/19830/17639 19616/20317/18131 19617/20318/18132 19125/19831/17640\r\nf 19616/20317/18131 19253/19959/17768 19256/19962/17771 19617/20318/18132\r\nf 19258/19964/17773 19618/20319/18133 19617/20318/18132 19256/19962/17771\r\nf 19618/20319/18133 19126/19832/17641 19125/19831/17640 19617/20318/18132\r\nf 19139/19845/17654 19562/20263/18077 19619/20320/18134 19140/19846/17655\r\nf 19562/20263/18077 19387/20093/17902 19386/20092/17901 19619/20320/18134\r\nf 19253/19959/17768 19616/20317/18131 19619/20320/18134 19386/20092/17901\r\nf 19616/20317/18131 19124/19830/17639 19140/19846/17655 19619/20320/18134\r\nf 19244/19950/17759 19620/20321/18135 19621/20322/18136 19245/19951/17760\r\nf 19620/20321/18135 19156/19862/17671 19291/19997/17806 19621/20322/18136\r\nf 19290/19996/17805 19622/20323/18137 19621/20322/18136 19291/19997/17806\r\nf 19622/20323/18137 19246/19952/17761 19245/19951/17760 19621/20322/18136\r\nf 19267/19973/17782 19623/20324/18138 19624/20325/18139 19268/19974/17783\r\nf 19623/20324/18138 19471/20176/17986 19468/20174/17983 19624/20325/18139\r\nf 19467/20173/17982 19625/20326/18140 19624/20325/18139 19468/20174/17983\r\nf 19625/20326/18140 19269/19975/17784 19268/19974/17783 19624/20325/18139\r\nf 19275/19981/17790 19283/19989/17798 19626/20327/18141 19276/19982/17791\r\nf 19283/19989/17798 19160/19866/17675 19157/19863/17672 19626/20327/18141\r\nf 19156/19862/17671 19620/20321/18135 19626/20327/18141 19157/19863/17672\r\nf 19620/20321/18135 19244/19950/17759 19276/19982/17791 19626/20327/18141\r\nf 19279/19985/17794 19627/20328/18142 19628/20329/18143 19280/19986/17795\r\nf 19627/20328/18142 19484/20188/17999 19481/20185/17996 19628/20329/18143\r\nf 19480/20184/17995 19629/20330/18144 19628/20329/18143 19481/20185/17996\r\nf 19629/20330/18144 19281/19987/17796 19280/19986/17795 19628/20329/18143\r\nf 19275/19981/17790 19274/19980/17789 19630/20331/18145 19285/19991/17800\r\nf 19274/19980/17789 19273/19979/17788 19489/20193/18004 19630/20331/18145\r\nf 19484/20188/17999 19627/20328/18142 19630/20331/18145 19489/20193/18004\r\nf 19627/20328/18142 19279/19985/17794 19285/19991/17800 19630/20331/18145\r\nf 19281/19987/17796 19629/20330/18144 19631/20332/18146 19287/19993/17802\r\nf 19629/20330/18144 19480/20184/17995 19492/20196/18007 19631/20332/18146\r\nf 19471/20176/17986 19623/20324/18138 19631/20332/18146 19492/20196/18007\r\nf 19623/20324/18138 19267/19973/17782 19287/19993/17802 19631/20332/18146\r\nf 19357/20063/17872 19632/20333/18147 19633/20334/18148 19358/20064/17873\r\nf 19632/20333/18147 19340/20046/17855 19337/20043/17852 19633/20334/18148\r\nf 19336/20042/17851 19634/20335/18149 19633/20334/18148 19337/20043/17852\r\nf 19634/20335/18149 19359/20065/17874 19358/20064/17873 19633/20334/18148\r\nf 19370/20076/17885 19536/20238/18051 19635/20336/18150 19371/20077/17886\r\nf 19536/20238/18051 19259/19965/17774 19262/19968/17777 19635/20336/18150\r\nf 19264/19970/17779 19561/20262/18076 19635/20336/18150 19262/19968/17777\r\nf 19561/20262/18076 19372/20078/17887 19371/20077/17886 19635/20336/18150\r\nf 19359/20065/17874 19634/20335/18149 19636/20334/18151 19530/20232/18045\r\nf 19634/20335/18149 19336/20042/17851 19514/20217/18029 19636/20334/18151\r\nf 19518/20221/18033 19637/20337/18152 19636/20334/18151 19514/20217/18029\r\nf 19637/20337/18152 19531/20233/18046 19530/20232/18045 19636/20334/18151\r\nf 19370/20076/17885 19367/20073/17882 19638/20072/18153 19538/20240/18053\r\nf 19367/20073/17882 19366/20072/17881 19542/20244/18057 19638/20072/18153\r\nf 19546/20243/18061 19639/20338/18154 19638/20072/18153 19542/20244/18057\r\nf 19639/20338/18154 19539/20241/18054 19538/20240/18053 19638/20072/18153\r\nf 19139/19845/17654 19136/19842/17651 19640/20339/18155 19563/20264/18078\r\nf 19136/19842/17651 19135/19841/17650 19566/20267/18081 19640/20339/18155\r\nf 19374/20080/17889 19373/20079/17888 19640/20339/18155 19566/20267/18081\r\nf 19373/20079/17888 19372/20078/17887 19563/20264/18078 19640/20339/18155\r\nf 19246/19952/17761 19622/20323/18137 19641/20340/18156 19608/20309/18123\r\nf 19622/20323/18137 19290/19996/17805 19602/20303/18117 19641/20340/18156\r\nf 19340/20046/17855 19632/20333/18147 19641/20340/18156 19602/20303/18117\r\nf 19632/20333/18147 19357/20063/17872 19608/20309/18123 19641/20340/18156\r\nf 19126/19832/17641 19642/20341/18157 19643/20342/18158 19127/19833/17642\r\nf 19642/20341/18157 19269/19975/17784 19625/20326/18140 19643/20342/18158\r\nf 19467/20173/17982 19644/20343/18159 19643/20342/18158 19625/20326/18140\r\nf 19644/20343/18159 19128/19834/17643 19127/19833/17642 19643/20342/18158\r\nf 19180/19886/17695 19645/20344/18160 19646/20345/18161 19181/19887/17696\r\nf 19645/20344/18160 19203/19909/17718 19202/19908/17717 19646/20345/18161\r\nf 19155/19861/17670 19647/20346/18162 19646/20345/18161 19202/19908/17717\r\nf 19647/20346/18162 19182/19888/17697 19181/19887/17696 19646/20345/18161\r\nf 19224/19930/17739 19587/20288/18102 19648/20347/18163 19225/19931/17740\r\nf 19587/20288/18102 19295/20001/17810 19294/20000/17809 19648/20347/18163\r\nf 19203/19909/17718 19645/20344/18160 19648/20347/18163 19294/20000/17809\r\nf 19645/20344/18160 19180/19886/17695 19225/19931/17740 19648/20347/18163\r\nf 19182/19888/17697 19647/20346/18162 19649/20348/18164 19257/19963/17772\r\nf 19647/20346/18162 19155/19861/17670 19154/19860/17669 19649/20348/18164\r\nf 19153/19859/17668 19650/20349/18165 19649/20348/18164 19154/19860/17669\r\nf 19650/20349/18165 19258/19964/17773 19257/19963/17772 19649/20348/18164\r\nf 19322/20028/17837 19495/20198/18010 19651/20350/18166 19323/20029/17838\r\nf 19495/20198/18010 19093/19799/17608 19096/19802/17611 19651/20350/18166\r\nf 19101/19807/17616 19586/20287/18101 19651/20350/18166 19096/19802/17611\r\nf 19586/20287/18101 19324/20030/17839 19323/20029/17838 19651/20350/18166\r\nf 19128/19834/17643 19644/20343/18159 19652/20351/18167 19415/20121/17930\r\nf 19644/20343/18159 19467/20173/17982 19470/20175/17985 19652/20351/18167\r\nf 19475/20179/17990 19653/20352/18168 19652/20351/18167 19470/20175/17985\r\nf 19653/20352/18168 19416/20122/17931 19415/20121/17930 19652/20351/18167\r\nf 19322/20028/17837 19319/20025/17834 19654/20353/18169 19497/20200/18012\r\nf 19319/20025/17834 19318/20024/17833 19548/20249/18063 19654/20353/18169\r\nf 19550/20251/18065 19655/20354/18170 19654/20353/18169 19548/20249/18063\r\nf 19655/20354/18170 19498/20201/18013 19497/20200/18012 19654/20353/18169\r\nf 19224/19930/17739 19223/19929/17738 19656/20355/18171 19588/20289/18103\r\nf 19223/19929/17738 19222/19928/17737 19592/20293/18107 19656/20355/18171\r\nf 19326/20032/17841 19325/20031/17840 19656/20355/18171 19592/20293/18107\r\nf 19325/20031/17840 19324/20030/17839 19588/20289/18103 19656/20355/18171\r\nf 19258/19964/17773 19650/20349/18165 19657/20356/18172 19618/20319/18133\r\nf 19650/20349/18165 19153/19859/17668 19270/19976/17785 19657/20356/18172\r\nf 19269/19975/17784 19642/20341/18157 19657/20356/18172 19270/19976/17785\r\nf 19642/20341/18157 19126/19832/17641 19618/20319/18133 19657/20356/18172\r\nv 344.72 44.27 2.46\r\nv 344.73 44.4 2.46\r\nv 347.44 43.03 12.77\r\nv 347.43 42.88 12.77\r\nv 344.68 45 2.52\r\nv 347.38 43.69 12.8\r\nv 349.64 42.35 24.19\r\nv 349.71 41.62 24.2\r\nv 349.71 41.45 24.21\r\nv 344.41 37.85 84.21\r\nv 344.41 38.07 84.18\r\nv 340.32 38.52 94.36\r\nv 340.31 38.3 94.39\r\nv 344.36 39.08 84.05\r\nv 340.28 39.55 94.22\r\nv 335.78 40.2 103.01\r\nv 335.81 39.18 103.16\r\nv 335.8 38.95 103.18\r\nv 351.21 39.14 44.32\r\nv 351.22 39.33 44.3\r\nv 350.84 38.81 53.22\r\nv 350.84 38.61 53.24\r\nv 351.14 40.18 44.22\r\nv 350.77 39.71 53.12\r\nv 349.58 39.44 62.38\r\nv 349.65 38.51 62.49\r\nv 349.65 38.3 62.51\r\nv 331.25 66.58 -18.15\r\nv 334.37 68.12 -12.78\r\nv 334.39 68.11 -12.79\r\nv 331.26 66.57 -18.16\r\nv 337.03 69.68 -7.29\r\nv 337.05 69.67 -7.3\r\nv 337.12 69.32 -7.34\r\nv 334.44 67.8 -12.83\r\nv 331.31 66.31 -18.19\r\nv 347.52 38.17 73.18\r\nv 347.51 37.95 73.2\r\nv 347.45 39.14 73.06\r\nv 350.9 40.32 34.67\r\nv 350.9 40.14 34.68\r\nv 350.83 41.12 34.62\r\nv 324.55 63.87 -27.71\r\nv 327.67 65.09 -23.44\r\nv 327.69 65.08 -23.46\r\nv 324.57 63.86 -27.73\r\nv 327.72 64.86 -23.49\r\nv 324.59 63.67 -27.75\r\nv 331.44 39.68 109.99\r\nv 331.43 39.45 110.01\r\nv 331.4 40.7 109.85\r\nv 327.34 41.09 115.13\r\nv 327.38 40.07 115.25\r\nv 327.38 39.84 115.26\r\nv 346.91 63.96 23.81\r\nv 345.14 63.19 13.6\r\nv 344.43 67.37 13.78\r\nv 346.04 68.59 23.74\r\nv 342.94 62.44 4.2\r\nv 342.38 66.18 4.56\r\nv 341.86 69.18 4.85\r\nv 343.77 70.72 13.92\r\nv 345.22 72.31 23.67\r\nv 331.1 82.08 96.58\r\nv 332.54 76.88 97.44\r\nv 336.7 76.13 89.02\r\nv 335.21 81.32 88.29\r\nv 333.86 70.39 98.48\r\nv 338.09 69.67 89.94\r\nv 341.79 68.81 80.2\r\nv 340.41 75.19 79.38\r\nv 338.96 80.31 78.72\r\nv 346.54 66.91 59.3\r\nv 347.62 66.1 50.35\r\nv 346.47 71.76 49.76\r\nv 345.33 72.81 58.64\r\nv 347.99 65.36 41.93\r\nv 346.9 70.76 41.44\r\nv 345.86 75.09 41.05\r\nv 345.35 76.3 49.29\r\nv 344.13 77.53 58.11\r\nv 332.11 60.09 -18.98\r\nv 335.47 60.66 -13.72\r\nv 335.79 57.6 -14.09\r\nv 332.38 57.43 -19.31\r\nv 338.37 61.24 -8.33\r\nv 338.75 57.77 -8.75\r\nv 339.06 54.46 -9.15\r\nv 336.06 54.68 -14.45\r\nv 332.62 54.89 -19.62\r\nv 343.3 74.02 68.88\r\nv 341.97 78.96 68.29\r\nv 344.6 67.85 69.62\r\nv 347.81 64.67 33.17\r\nv 346.81 69.72 32.86\r\nv 345.87 73.77 32.61\r\nv 322.12 58.57 -31.9\r\nv 322.17 58.58 -31.88\r\nv 322.34 56.93 -32.1\r\nv 322.29 56.93 -32.13\r\nv 322.48 58.64 -31.53\r\nv 322.65 56.96 -31.75\r\nv 322.81 55.35 -31.97\r\nv 322.49 55.36 -32.31\r\nv 322.44 55.36 -32.34\r\nv 348.42 53.56 23.99\r\nv 346.37 53.81 13.21\r\nv 345.81 58.38 13.4\r\nv 347.73 58.63 23.9\r\nv 343.9 54.05 3.39\r\nv 343.46 58.14 3.78\r\nv 334.77 62.94 99.63\r\nv 339.07 62.24 90.99\r\nv 335.32 55.84 100.7\r\nv 339.67 55.16 92\r\nv 343.51 54.5 82.05\r\nv 342.82 61.48 81.15\r\nv 347.58 60.14 60.05\r\nv 348.64 59.59 51.03\r\nv 348.37 53.69 60.78\r\nv 349.45 53.4 51.68\r\nv 349.78 53.24 43.03\r\nv 348.98 59.16 42.49\r\nv 345.65 60.77 70.47\r\nv 346.4 54.03 71.27\r\nv 348.73 58.86 33.52\r\nv 349.5 53.34 33.86\r\nv 336.23 52.44 -14.72\r\nv 332.78 52.95 -19.86\r\nv 339.25 51.93 -9.45\r\nv 339.38 49.96 -9.69\r\nv 336.35 50.7 -14.94\r\nv 332.9 51.44 -20.05\r\nv 322.6 54.16 -32.48\r\nv 322.55 54.16 -32.5\r\nv 322.92 54.13 -32.14\r\nv 323.01 53.17 -32.26\r\nv 322.69 53.23 -32.6\r\nv 322.64 53.23 -32.63\r\nv 323.82 40.37 119.31\r\nv 323.82 40.14 119.32\r\nv 323.76 41.39 119.22\r\nv 321.25 41.58 121.85\r\nv 321.32 40.56 121.93\r\nv 321.32 40.34 121.94\r\nv 321.06 89.61 106.68\r\nv 321.09 89.58 106.7\r\nv 324.72 89.12 101.84\r\nv 324.69 89.15 101.82\r\nv 321.35 88.84 106.88\r\nv 325.01 88.38 101.99\r\nv 328.95 87.78 95.64\r\nv 328.64 88.52 95.52\r\nv 328.61 88.56 95.5\r\nv 325.21 71.4 111.09\r\nv 326.02 63.85 112.35\r\nv 330.31 63.46 106.7\r\nv 329.46 70.96 105.49\r\nv 326.59 56.67 113.36\r\nv 330.85 56.32 107.76\r\nv 331.6 64.27 -18.45\r\nv 334.82 65.46 -13.13\r\nv 335.13 63.33 -13.39\r\nv 331.85 62.41 -18.69\r\nv 337.57 66.67 -7.67\r\nv 337.95 64.26 -7.97\r\nv 321.85 61.15 -31.55\r\nv 321.9 61.17 -31.53\r\nv 322.02 60.02 -31.68\r\nv 321.98 60 -31.71\r\nv 322.2 61.28 -31.17\r\nv 322.33 60.11 -31.33\r\nv 343.25 73.04 14.02\r\nv 344.58 74.88 23.63\r\nv 341.46 71.26 5.05\r\nv 341.2 72.47 5.18\r\nv 342.92 74.4 14.08\r\nv 344.17 76.39 23.61\r\nv 332.72 87.75 87.38\r\nv 332.68 87.78 87.37\r\nv 333.03 87.01 87.49\r\nv 336.87 85.93 78\r\nv 336.57 86.66 77.91\r\nv 336.54 86.69 77.9\r\nv 344.45 79.44 48.96\r\nv 343.14 80.8 57.75\r\nv 345.03 78.09 40.77\r\nv 344.48 79.85 40.62\r\nv 343.85 81.28 48.77\r\nv 342.47 82.72 57.53\r\nv 340.85 82.38 67.88\r\nv 337.72 83.85 78.27\r\nv 340.08 84.39 67.65\r\nv 345.12 76.57 32.44\r\nv 344.63 78.21 32.34\r\nv 328.28 77.47 104.25\r\nv 327 82.67 103.17\r\nv 323.08 83.13 108.33\r\nv 324.18 77.92 109.65\r\nv 348.88 49.69 24.06\r\nv 346.75 50.31 13.07\r\nv 349.2 46.66 24.11\r\nv 347.01 47.58 12.96\r\nv 344.39 48.48 2.85\r\nv 344.19 50.92 3.09\r\nv 335.54 50.43 101.51\r\nv 339.95 49.77 92.76\r\nv 335.62 46.21 102.12\r\nv 340.06 45.55 93.36\r\nv 344.05 45 83.28\r\nv 343.86 49.17 82.74\r\nv 348.87 48.77 61.33\r\nv 349.98 48.66 52.18\r\nv 349.17 44.92 61.76\r\nv 350.32 44.97 52.57\r\nv 350.67 45.2 43.76\r\nv 350.31 48.73 43.44\r\nv 346.83 48.88 71.89\r\nv 347.09 44.86 72.37\r\nv 350.01 49.12 34.12\r\nv 350.36 45.82 34.33\r\nv 326.9 51.25 114.03\r\nv 331.1 50.91 108.51\r\nv 327.07 47.06 114.48\r\nv 331.21 46.69 109.07\r\nv 333.1 48.82 -20.36\r\nv 333.11 48.91 -20.36\r\nv 336.58 47.78 -15.29\r\nv 336.57 47.69 -15.29\r\nv 333.08 49.28 -20.32\r\nv 336.54 48.21 -15.24\r\nv 339.59 47.14 -10.03\r\nv 339.63 46.66 -10.09\r\nv 339.62 46.55 -10.09\r\nv 322.79 51.63 -32.83\r\nv 322.79 51.67 -32.84\r\nv 322.84 51.66 -32.82\r\nv 322.83 51.61 -32.81\r\nv 322.77 51.9 -32.81\r\nv 322.82 51.89 -32.79\r\nv 323.14 51.81 -32.45\r\nv 323.17 51.57 -32.48\r\nv 323.16 51.52 -32.48\r\nv 315.95 83.79 115.8\r\nv 316.9 78.58 117.47\r\nv 316.95 78.58 117.45\r\nv 315.99 83.79 115.78\r\nv 317.9 72.03 119.2\r\nv 317.95 72.03 119.18\r\nv 318.85 71.95 118.21\r\nv 317.86 78.5 116.52\r\nv 316.89 83.71 114.89\r\nv 318.69 64.42 120.54\r\nv 318.74 64.42 120.51\r\nv 319.28 57.18 121.48\r\nv 319.33 57.18 121.45\r\nv 320.24 57.12 120.49\r\nv 319.65 64.35 119.54\r\nv 314.74 90.27 113.71\r\nv 314.75 90.25 113.73\r\nv 314.79 90.25 113.71\r\nv 314.77 90.28 113.69\r\nv 314.88 89.51 113.97\r\nv 314.93 89.51 113.95\r\nv 315.73 89.43 113.11\r\nv 315.58 90.17 112.88\r\nv 315.56 90.2 112.85\r\nv 319.67 51.76 122.02\r\nv 319.72 51.76 121.99\r\nv 319.94 47.58 122.33\r\nv 319.99 47.58 122.31\r\nv 320.87 47.52 121.38\r\nv 320.61 51.7 121.04\r\nv 321.67 62.58 -31.34\r\nv 321.71 62.6 -31.32\r\nv 321.73 62.6 -31.33\r\nv 321.68 62.58 -31.36\r\nv 322.01 62.74 -30.96\r\nv 322.02 62.74 -30.97\r\nv 322.04 62.57 -30.99\r\nv 321.75 62.43 -31.35\r\nv 321.7 62.41 -31.38\r\nv 328.8 55.09 -24.74\r\nv 328.59 57.26 -24.46\r\nv 325.51 55.25 -28.86\r\nv 325.32 57.12 -28.61\r\nv 325.12 59.09 -28.36\r\nv 328.36 59.54 -24.17\r\nv 329.06 52.14 -25.11\r\nv 328.95 53.44 -24.95\r\nv 325.74 52.71 -29.19\r\nv 325.64 53.82 -29.05\r\nv 328.14 61.52 -23.92\r\nv 324.94 60.8 -28.13\r\nv 324.78 62.17 -27.95\r\nv 327.95 63.11 -23.72\r\nv 325.91 50.78 -29.43\r\nv 325.92 50.84 -29.43\r\nv 329.25 49.98 -25.38\r\nv 329.24 49.91 -25.38\r\nv 325.9 51.12 -29.4\r\nv 329.22 50.3 -25.34\r\nv 349.66 41.39 24.21\r\nv 347.38 42.83 12.78\r\nv 349.58 41.38 24.23\r\nv 347.31 42.82 12.8\r\nv 344.6 44.21 2.49\r\nv 344.67 44.22 2.47\r\nv 335.75 38.87 103.16\r\nv 340.27 38.22 94.38\r\nv 335.69 38.86 103.13\r\nv 340.2 38.21 94.34\r\nv 344.29 37.76 84.17\r\nv 344.36 37.77 84.2\r\nv 349.6 38.23 62.51\r\nv 350.79 38.54 53.24\r\nv 349.52 38.22 62.49\r\nv 350.71 38.53 53.23\r\nv 351.09 39.06 44.32\r\nv 351.16 39.07 44.32\r\nv 331.2 66.58 -18.12\r\nv 334.32 68.12 -12.76\r\nv 331.14 66.56 -18.08\r\nv 334.26 68.1 -12.72\r\nv 336.91 69.66 -7.23\r\nv 336.98 69.68 -7.26\r\nv 347.46 37.87 73.2\r\nv 347.39 37.86 73.18\r\nv 350.85 40.08 34.69\r\nv 350.78 40.07 34.69\r\nv 324.51 63.86 -27.68\r\nv 327.62 65.09 -23.41\r\nv 324.45 63.85 -27.63\r\nv 327.56 65.07 -23.37\r\nv 327.34 39.76 115.23\r\nv 331.39 39.37 109.99\r\nv 327.28 39.76 115.18\r\nv 331.32 39.37 109.94\r\nv 322.38 55.35 -32.32\r\nv 322.23 56.92 -32.1\r\nv 322.33 55.34 -32.27\r\nv 322.18 56.91 -32.05\r\nv 322.01 58.55 -31.83\r\nv 322.07 58.56 -31.88\r\nv 322.59 53.22 -32.61\r\nv 322.5 54.16 -32.48\r\nv 322.53 53.21 -32.56\r\nv 322.44 54.14 -32.43\r\nv 320.42 40.46 122.85\r\nv 320.38 40.4 122.84\r\nv 320.42 40.32 122.81\r\nv 320.46 40.4 122.84\r\nv 320.33 40.39 122.79\r\nv 320.37 40.32 122.76\r\nv 321.23 40.25 121.85\r\nv 321.28 40.26 121.9\r\nv 319.88 47.58 122.31\r\nv 320.12 44.08 122.57\r\nv 320.17 44.09 122.6\r\nv 319.83 47.57 122.26\r\nv 320.06 44.07 122.52\r\nv 320.23 41.63 122.71\r\nv 320.28 41.64 122.76\r\nv 320.34 41.64 122.78\r\nv 328.56 88.55 95.48\r\nv 324.65 89.14 101.79\r\nv 328.5 88.52 95.44\r\nv 324.59 89.12 101.75\r\nv 320.96 89.58 106.6\r\nv 321.02 89.61 106.64\r\nv 321.92 59.99 -31.69\r\nv 321.87 59.98 -31.64\r\nv 321.74 61.12 -31.48\r\nv 321.8 61.14 -31.53\r\nv 341.08 72.92 5.23\r\nv 341.03 72.91 5.25\r\nv 342.72 74.9 14.12\r\nv 342.78 74.9 14.1\r\nv 340.96 72.9 5.27\r\nv 342.65 74.88 14.13\r\nv 343.86 76.92 23.62\r\nv 343.94 76.94 23.61\r\nv 343.99 76.94 23.6\r\nv 336.48 86.68 77.88\r\nv 332.63 87.78 87.35\r\nv 336.42 86.65 77.86\r\nv 332.57 87.75 87.32\r\nv 344.25 80.5 40.56\r\nv 344.2 80.49 40.56\r\nv 343.55 81.95 48.69\r\nv 343.61 81.96 48.7\r\nv 344.12 80.47 40.56\r\nv 343.48 81.93 48.69\r\nv 342.08 83.4 57.43\r\nv 342.15 83.42 57.44\r\nv 342.21 83.43 57.45\r\nv 339.73 85.11 67.54\r\nv 339.78 85.12 67.55\r\nv 339.66 85.09 67.53\r\nv 344.38 78.81 32.31\r\nv 344.43 78.82 32.31\r\nv 344.3 78.79 32.32\r\nv 342.18 45.4 -4.65\r\nv 342.23 45.45 -4.67\r\nv 342.11 45.39 -4.63\r\nv 339.5 46.5 -10.04\r\nv 339.57 46.5 -10.07\r\nv 325.87 50.75 -29.4\r\nv 323.99 51.25 -31.54\r\nv 324.03 51.28 -31.57\r\nv 325.81 50.74 -29.35\r\nv 323.94 51.24 -31.49\r\nv 323.06 51.48 -32.39\r\nv 323.12 51.49 -32.44\r\nv 322.75 51.61 -32.81\r\nv 322.73 51.66 -32.82\r\nv 322.7 51.6 -32.76\r\nv 322.68 51.65 -32.77\r\nv 322.66 51.88 -32.74\r\nv 322.71 51.89 -32.79\r\nv 315.89 83.77 115.78\r\nv 316.85 78.56 117.45\r\nv 315.84 83.74 115.73\r\nv 316.79 78.54 117.4\r\nv 317.79 72 119.13\r\nv 317.84 72.02 119.18\r\nv 318.64 64.41 120.51\r\nv 318.58 64.4 120.46\r\nv 319.17 57.16 121.4\r\nv 319.23 57.17 121.45\r\nv 317.79 89.96 110.4\r\nv 317.84 89.97 110.43\r\nv 317.74 89.93 110.35\r\nv 315.46 90.16 112.77\r\nv 315.52 90.19 112.81\r\nv 314.7 90.26 113.69\r\nv 314.7 90.22 113.71\r\nv 314.65 90.23 113.64\r\nv 314.64 90.19 113.67\r\nv 314.78 89.45 113.9\r\nv 314.83 89.48 113.95\r\nv 319.61 51.75 121.99\r\nv 319.56 51.74 121.94\r\nv 321.7 61.93 -31.42\r\nv 321.76 61.95 -31.45\r\nv 321.65 61.92 -31.37\r\nv 321.59 62.38 -31.31\r\nv 321.65 62.4 -31.36\r\nv 321.64 62.57 -31.32\r\nv 321.67 62.59 -31.28\r\nv 321.58 62.56 -31.27\r\nv 321.61 62.58 -31.23\r\nv 321.91 62.72 -30.87\r\nv 321.97 62.74 -30.92\r\nv 333.05 48.79 -20.33\r\nv 329.2 49.88 -25.35\r\nv 332.99 48.78 -20.29\r\nv 329.14 49.87 -25.31\r\nv 323.49 57.02 -30.82\r\nv 323.66 55.33 -31.04\r\nv 323.31 58.8 -30.58\r\nv 323.78 54.04 -31.22\r\nv 323.88 53.02 -31.35\r\nv 323.15 60.34 -30.37\r\nv 323.01 61.58 -30.21\r\nv 324.04 51.34 -31.58\r\nv 324.02 51.58 -31.54\r\nv 322.1 62.09 -31.06\r\nv 322.91 62.44 -30.09\r\nv 322.85 62.94 -30.03\r\nv 324.66 63.11 -27.83\r\nv 322.79 51.58 -32.78\r\nv 322.73 51.57 -32.72\r\nv 322.77 63.12 -29.95\r\nv 322.81 63.13 -29.99\r\nv 322.71 63.11 -29.9\r\nv 347.23 45.29 12.86\r\nv 349.46 44.12 24.15\r\nv 344.56 46.43 2.66\r\nv 340.18 42.02 93.87\r\nv 335.7 42.67 102.64\r\nv 344.23 41.52 83.73\r\nv 350.59 41.87 52.89\r\nv 349.42 41.69 62.13\r\nv 350.95 42.25 44.03\r\nv 347.3 41.49 72.78\r\nv 350.64 43.05 34.5\r\nv 331.32 43.17 109.53\r\nv 327.23 43.54 114.87\r\nv 320.39 41.64 122.76\r\nv 320.22 44.09 122.57\r\nv 321.09 44.02 121.66\r\nv 342.2 46.1 -4.6\r\nv 342.09 47.39 -4.45\r\nv 339.5 48.3 -9.89\r\nv 341.95 49.24 -4.23\r\nv 322.77 52.44 -32.71\r\nv 323.09 52.37 -32.37\r\nv 322.71 52.44 -32.74\r\nv 329.15 51.06 -25.25\r\nv 333 50.16 -20.21\r\nv 325.83 51.77 -29.31\r\nv 320.35 40.63 122.83\r\nv 320.41 40.63 122.86\r\nv 320.3 40.62 122.78\r\nv 322.66 52.44 -32.71\r\nv 322.6 52.43 -32.66\r\nv 323.96 52.18 -31.46\r\nv 320.46 40.63 122.84\r\nv 320.49 78.27 113.83\r\nv 319.48 83.48 112.32\r\nv 321.48 71.74 115.42\r\nv 322.27 64.16 116.72\r\nv 322.85 56.95 117.7\r\nv 317.86 89.94 110.46\r\nv 318.06 89.2 110.67\r\nv 323.2 51.52 118.3\r\nv 323.43 47.34 118.68\r\nv 323.78 40.06 119.29\r\nv 323.72 40.06 119.24\r\nv 314.73 90.27 113.65\r\nv 314.68 90.24 113.6\r\nv 323.62 43.83 119\r\nv 334.59 66.94 -12.94\r\nv 331.42 65.55 -18.29\r\nv 337.29 68.34 -7.47\r\nv 327.81 64.21 -23.57\r\nv 325.85 86.27 102.43\r\nv 329.84 85.67 95.99\r\nv 322.07 86.73 107.42\r\nv 342.8 74.88 14.1\r\nv 344.02 76.92 23.6\r\nv 341.11 72.9 5.22\r\nv 333.93 84.91 87.78\r\nv 343.63 81.93 48.7\r\nv 342.24 83.4 57.46\r\nv 344.28 80.47 40.56\r\nv 339.81 85.09 67.56\r\nv 344.46 78.79 32.31\r\nv 315.32 87.4 114.63\r\nv 316.17 87.32 113.77\r\nv 315.28 87.39 114.65\r\nv 321.81 61.96 -31.42\r\nv 315.23 87.37 114.63\r\nv 315.18 87.34 114.58\r\nv 321.62 62.56 -31.34\r\nv 321.57 62.55 -31.29\r\nv 322.82 63.12 -30\r\nv 318.63 87.09 111.28\r\nv 339.24 71.21 -1.6\r\nv 339.32 70.83 -1.65\r\nv 339.22 71.23 -1.59\r\nv 341.21 57.95 -3.2\r\nv 341.57 54.26 -3.63\r\nv 340.77 61.82 -2.74\r\nv 341.79 51.44 -3.97\r\nv 340.3 65.18 -2.34\r\nv 339.86 67.87 -2.01\r\nv 342.24 45.57 -4.67\r\nv 339.17 71.22 -1.57\r\nv 339.1 71.21 -1.54\r\nv 336.52 47.65 -15.27\r\nv 336.46 47.64 -15.23\r\nv 336.46 49.23 -15.12\r\nv 339.53 69.74 -1.79\r\nvn 0.86 -0.4 -0.32\r\nvn 0.96 0.04 -0.29\r\nvn 0.97 0.04 -0.22\r\nvn 0.89 -0.38 -0.25\r\nvn 0.96 0.11 -0.28\r\nvn 0.97 0.1 -0.21\r\nvn 0.99 0.1 -0.14\r\nvn 0.99 0.04 -0.15\r\nvn 0.92 -0.35 -0.19\r\nvn 0.91 -0.27 0.32\r\nvn 0.95 0.05 0.32\r\nvn 0.91 0.05 0.41\r\nvn 0.87 -0.26 0.41\r\nvn 0.94 0.09 0.32\r\nvn 0.91 0.09 0.41\r\nvn 0.87 0.1 0.49\r\nvn 0.87 0.06 0.49\r\nvn 0.83 -0.26 0.5\r\nvn 0.95 -0.32 -0.02\r\nvn 1 0.04 0.01\r\nvn 1 0.04 0.09\r\nvn 0.95 -0.31 0.07\r\nvn 1 0.09 0.01\r\nvn 0.99 0.09 0.09\r\nvn 0.98 0.09 0.16\r\nvn 0.99 0.04 0.16\r\nvn 0.94 -0.29 0.15\r\nvn 0.03 0.96 -0.29\r\nvn 0.07 0.95 -0.31\r\nvn 0.65 0.57 -0.5\r\nvn 0.6 0.59 -0.54\r\nvn 0.12 0.94 -0.31\r\nvn 0.7 0.55 -0.45\r\nvn 0.87 0.21 -0.44\r\nvn 0.84 0.2 -0.51\r\nvn 0.8 0.2 -0.56\r\nvn 0.97 0.05 0.23\r\nvn 0.93 -0.28 0.22\r\nvn 0.97 0.09 0.23\r\nvn 1 0.04 -0.07\r\nvn 0.94 -0.33 -0.11\r\nvn 0.99 0.09 -0.06\r\nvn -0.06 0.97 -0.25\r\nvn -0.01 0.96 -0.26\r\nvn 0.55 0.62 -0.56\r\nvn 0.51 0.64 -0.58\r\nvn 0.78 0.19 -0.6\r\nvn 0.76 0.18 -0.62\r\nvn 0.82 0.06 0.57\r\nvn 0.78 -0.25 0.57\r\nvn 0.82 0.1 0.57\r\nvn 0.77 0.1 0.63\r\nvn 0.77 0.06 0.63\r\nvn 0.74 -0.26 0.63\r\nvn 0.98 0.16 -0.14\r\nvn 0.97 0.16 -0.21\r\nvn 0.96 0.18 -0.21\r\nvn 0.97 0.2 -0.14\r\nvn 0.95 0.16 -0.27\r\nvn 0.95 0.18 -0.27\r\nvn 0.94 0.2 -0.27\r\nvn 0.96 0.21 -0.21\r\nvn 0.96 0.22 -0.14\r\nvn 0.84 0.33 0.44\r\nvn 0.85 0.28 0.45\r\nvn 0.89 0.27 0.36\r\nvn 0.87 0.33 0.35\r\nvn 0.86 0.21 0.46\r\nvn 0.9 0.21 0.38\r\nvn 0.94 0.2 0.28\r\nvn 0.92 0.27 0.28\r\nvn 0.91 0.32 0.27\r\nvn 0.97 0.19 0.13\r\nvn 0.98 0.18 0.06\r\nvn 0.97 0.22 0.06\r\nvn 0.96 0.24 0.13\r\nvn 0.98 0.18 -0\r\nvn 0.98 0.22 -0.01\r\nvn 0.97 0.25 -0.01\r\nvn 0.96 0.26 0.06\r\nvn 0.95 0.28 0.13\r\nvn 0.81 0.16 -0.57\r\nvn 0.84 0.16 -0.51\r\nvn 0.85 0.15 -0.51\r\nvn 0.81 0.15 -0.57\r\nvn 0.88 0.16 -0.44\r\nvn 0.88 0.14 -0.44\r\nvn 0.89 0.13 -0.45\r\nvn 0.85 0.13 -0.51\r\nvn 0.81 0.14 -0.57\r\nvn 0.95 0.25 0.2\r\nvn 0.93 0.3 0.19\r\nvn 0.96 0.2 0.2\r\nvn 0.98 0.17 -0.07\r\nvn 0.98 0.21 -0.07\r\nvn 0.97 0.24 -0.07\r\nvn 0.02 0.14 -0.99\r\nvn 0.6 0.17 -0.78\r\nvn 0.6 0.17 -0.78\r\nvn 0.02 0.14 -0.99\r\nvn 0.73 0.16 -0.67\r\nvn 0.73 0.16 -0.67\r\nvn 0.73 0.16 -0.67\r\nvn 0.6 0.16 -0.78\r\nvn 0.02 0.14 -0.99\r\nvn 0.98 0.12 -0.14\r\nvn 0.97 0.12 -0.21\r\nvn 0.97 0.14 -0.21\r\nvn 0.98 0.14 -0.14\r\nvn 0.95 0.12 -0.27\r\nvn 0.95 0.13 -0.27\r\nvn 0.87 0.16 0.47\r\nvn 0.91 0.15 0.39\r\nvn 0.87 0.12 0.48\r\nvn 0.91 0.12 0.39\r\nvn 0.95 0.12 0.3\r\nvn 0.94 0.15 0.29\r\nvn 0.98 0.15 0.14\r\nvn 0.99 0.15 0.07\r\nvn 0.98 0.13 0.15\r\nvn 0.99 0.13 0.07\r\nvn 0.99 0.13 0\r\nvn 0.99 0.15 -0\r\nvn 0.97 0.15 0.21\r\nvn 0.97 0.12 0.22\r\nvn 0.99 0.14 -0.07\r\nvn 0.99 0.12 -0.07\r\nvn 0.85 0.13 -0.51\r\nvn 0.81 0.14 -0.57\r\nvn 0.89 0.12 -0.45\r\nvn 0.89 0.12 -0.45\r\nvn 0.85 0.12 -0.51\r\nvn 0.81 0.14 -0.56\r\nvn 0.6 0.16 -0.78\r\nvn 0.02 0.14 -0.99\r\nvn 0.73 0.16 -0.67\r\nvn 0.73 0.16 -0.67\r\nvn 0.6 0.16 -0.78\r\nvn 0.02 0.14 -0.99\r\nvn 0.74 0.06 0.67\r\nvn 0.7 -0.26 0.66\r\nvn 0.74 0.1 0.67\r\nvn 0.72 0.1 0.68\r\nvn 0.72 0.06 0.69\r\nvn 0.69 -0.26 0.68\r\nvn 0.28 0.95 0.14\r\nvn 0.67 0.57 0.48\r\nvn 0.7 0.57 0.43\r\nvn 0.3 0.95 0.11\r\nvn 0.73 0.39 0.56\r\nvn 0.77 0.4 0.49\r\nvn 0.81 0.41 0.42\r\nvn 0.74 0.57 0.36\r\nvn 0.31 0.95 0.09\r\nvn 0.77 0.22 0.6\r\nvn 0.77 0.17 0.61\r\nvn 0.82 0.16 0.55\r\nvn 0.81 0.21 0.54\r\nvn 0.77 0.13 0.62\r\nvn 0.82 0.13 0.56\r\nvn 0.81 0.18 -0.56\r\nvn 0.84 0.19 -0.51\r\nvn 0.84 0.18 -0.51\r\nvn 0.81 0.17 -0.56\r\nvn 0.88 0.2 -0.44\r\nvn 0.88 0.18 -0.44\r\nvn 0.02 0.14 -0.99\r\nvn 0.6 0.17 -0.78\r\nvn 0.6 0.17 -0.78\r\nvn 0.02 0.14 -0.99\r\nvn 0.73 0.17 -0.67\r\nvn 0.73 0.17 -0.67\r\nvn 0.95 0.23 -0.21\r\nvn 0.96 0.25 -0.14\r\nvn 0.94 0.22 -0.27\r\nvn 0.94 0.23 -0.27\r\nvn 0.95 0.24 -0.2\r\nvn 0.95 0.26 -0.14\r\nvn 0.77 0.57 0.29\r\nvn 0.33 0.94 0.05\r\nvn 0.85 0.41 0.34\r\nvn 0.88 0.4 0.26\r\nvn 0.8 0.56 0.21\r\nvn 0.34 0.94 -0\r\nvn 0.95 0.3 0.06\r\nvn 0.94 0.32 0.13\r\nvn 0.96 0.28 -0.01\r\nvn 0.95 0.3 -0.01\r\nvn 0.95 0.32 0.06\r\nvn 0.93 0.34 0.13\r\nvn 0.92 0.35 0.19\r\nvn 0.89 0.37 0.26\r\nvn 0.91 0.37 0.19\r\nvn 0.96 0.27 -0.07\r\nvn 0.96 0.28 -0.07\r\nvn 0.8 0.28 0.53\r\nvn 0.79 0.33 0.51\r\nvn 0.74 0.33 0.58\r\nvn 0.75 0.28 0.59\r\nvn 0.98 0.11 -0.14\r\nvn 0.97 0.11 -0.21\r\nvn 0.98 0.1 -0.14\r\nvn 0.97 0.1 -0.21\r\nvn 0.95 0.11 -0.28\r\nvn 0.95 0.11 -0.28\r\nvn 0.87 0.1 0.49\r\nvn 0.91 0.09 0.4\r\nvn 0.87 0.09 0.49\r\nvn 0.91 0.09 0.4\r\nvn 0.95 0.09 0.31\r\nvn 0.95 0.09 0.31\r\nvn 0.98 0.11 0.15\r\nvn 0.99 0.11 0.08\r\nvn 0.98 0.09 0.16\r\nvn 0.99 0.1 0.08\r\nvn 1 0.1 0.01\r\nvn 0.99 0.11 0.01\r\nvn 0.97 0.1 0.22\r\nvn 0.97 0.09 0.23\r\nvn 0.99 0.11 -0.07\r\nvn 0.99 0.1 -0.06\r\nvn 0.77 0.11 0.62\r\nvn 0.82 0.11 0.56\r\nvn 0.77 0.1 0.62\r\nvn 0.82 0.1 0.56\r\nvn 0.65 -0.5 -0.58\r\nvn 0.81 0.03 -0.58\r\nvn 0.85 0.03 -0.53\r\nvn 0.7 -0.47 -0.54\r\nvn 0.82 0.14 -0.56\r\nvn 0.85 0.13 -0.51\r\nvn 0.89 0.12 -0.45\r\nvn 0.89 0.04 -0.46\r\nvn 0.75 -0.44 -0.49\r\nvn 0.01 -0.46 -0.89\r\nvn 0.03 -0.08 -1\r\nvn 0.6 -0.01 -0.8\r\nvn 0.34 -0.56 -0.75\r\nvn 0.02 0.14 -0.99\r\nvn 0.61 0.16 -0.78\r\nvn 0.73 0.16 -0.67\r\nvn 0.72 -0.01 -0.7\r\nvn 0.49 -0.6 -0.63\r\nvn -0.03 0.3 0.95\r\nvn -0.01 0.28 0.96\r\nvn 0.57 0.32 0.76\r\nvn 0.55 0.34 0.76\r\nvn 0.02 0.22 0.98\r\nvn 0.59 0.24 0.77\r\nvn 0.72 0.23 0.66\r\nvn 0.69 0.3 0.65\r\nvn 0.68 0.33 0.65\r\nvn 0.02 0.15 0.99\r\nvn 0.6 0.17 0.78\r\nvn 0.01 0.12 0.99\r\nvn 0.6 0.14 0.79\r\nvn 0.73 0.13 0.67\r\nvn 0.73 0.17 0.67\r\nvn -0.22 0.81 0.54\r\nvn -0.1 0.52 0.85\r\nvn 0.51 0.53 0.68\r\nvn 0.12 0.94 0.33\r\nvn -0.04 0.3 0.95\r\nvn 0.56 0.35 0.76\r\nvn 0.7 0.34 0.63\r\nvn 0.64 0.53 0.56\r\nvn 0.27 0.95 0.17\r\nvn 0.01 0.09 1\r\nvn 0.6 0.11 0.79\r\nvn 0 0.08 1\r\nvn 0.6 0.1 0.8\r\nvn 0.73 0.1 0.68\r\nvn 0.73 0.11 0.68\r\nvn -0.3 0.9 -0.32\r\nvn -0.18 0.93 -0.31\r\nvn 0.36 0.62 -0.7\r\nvn -0.1 0.53 -0.84\r\nvn -0.14 0.95 -0.26\r\nvn 0.43 0.66 -0.62\r\nvn 0.72 0.18 -0.67\r\nvn 0.6 0.18 -0.78\r\nvn 0.01 0.14 -0.99\r\nvn 0.79 0.15 -0.6\r\nvn 0.78 0.15 -0.6\r\nvn 0.77 0.16 -0.62\r\nvn 0.76 0.16 -0.62\r\nvn 0.76 0.16 -0.63\r\nvn 0.78 0.16 -0.6\r\nvn 0.79 0.14 -0.6\r\nvn 0.79 0.15 -0.6\r\nvn 0.77 0.15 -0.62\r\nvn 0.77 0.15 -0.62\r\nvn 0.78 0.17 -0.6\r\nvn 0.76 0.17 -0.63\r\nvn 0.76 0.17 -0.62\r\nvn 0.78 0.18 -0.6\r\nvn 0.56 -0.56 -0.6\r\nvn 0.76 0 -0.65\r\nvn 0.78 0.02 -0.62\r\nvn 0.6 -0.53 -0.59\r\nvn 0.77 0.15 -0.62\r\nvn 0.79 0.15 -0.6\r\nvn 0.45 -0.88 -0.18\r\nvn 0.41 -0.89 -0.21\r\nvn 0.08 -0.99 -0.14\r\nvn 0.07 -0.99 -0.15\r\nvn 0.06 -0.98 -0.17\r\nvn 0.37 -0.89 -0.25\r\nvn 0.44 -0.84 0.32\r\nvn 0.47 -0.84 0.27\r\nvn 0.05 -0.99 0.1\r\nvn 0.06 -0.99 0.09\r\nvn 0.08 -1 0.04\r\nvn 0.5 -0.84 0.19\r\nvn 0.51 -0.86 0.06\r\nvn 0.51 -0.86 0\r\nvn 0.09 -1 -0.02\r\nvn 0.09 -0.99 -0.04\r\nvn 0.09 -0.99 -0.08\r\nvn 0.49 -0.87 -0.07\r\nvn -0.25 0.96 -0.12\r\nvn -0.25 0.96 -0.14\r\nvn -0.28 0.95 -0.09\r\nvn -0.29 0.95 -0.12\r\nvn -0.29 0.95 -0.14\r\nvn -0.24 0.96 -0.16\r\nvn 0.51 -0.85 0.11\r\nvn 0.09 -1 0\r\nvn 0.47 -0.87 -0.13\r\nvn 0.09 -0.99 -0.12\r\nvn -0.27 0.96 -0.1\r\nvn -0.25 0.96 -0.1\r\nvn -0.29 0.95 -0.08\r\nvn -0.28 0.96 -0.07\r\nvn 0.39 -0.84 0.38\r\nvn 0.42 -0.84 0.36\r\nvn 0.04 -0.99 0.11\r\nvn 0.05 -0.99 0.11\r\nvn -0.56 0.06 -0.83\r\nvn -0.56 0.06 -0.83\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.03 -0.73\r\nvn -0.56 0.06 -0.83\r\nvn -0.56 0.06 -0.83\r\nvn -0.56 0.06 -0.83\r\nvn -0.68 0.04 -0.73\r\nvn -0.68 0.04 -0.73\r\nvn -0.02 -0.08 1\r\nvn -0.34 -0.3 0.89\r\nvn 0.04 -0.77 0.64\r\nvn 0.37 -0.27 0.89\r\nvn -0.63 -0.31 0.71\r\nvn -0.28 -0.86 0.43\r\nvn 0.04 -0.99 0.11\r\nvn 0.35 -0.84 0.4\r\nvn -0.57 0.02 0.82\r\nvn -0.57 0.02 0.82\r\nvn -0 0.08 1\r\nvn -0.69 0.01 0.73\r\nvn -0.69 0.01 0.73\r\nvn -0.69 0.01 0.73\r\nvn -0.58 0.02 0.82\r\nvn -0.01 0.07 1\r\nvn -0.19 0.96 -0.2\r\nvn -0.18 0.96 -0.22\r\nvn -0.3 0.92 -0.26\r\nvn -0.29 0.92 -0.28\r\nvn -0.27 0.91 -0.3\r\nvn -0.17 0.96 -0.23\r\nvn -0.56 0.05 -0.83\r\nvn -0.68 0.03 -0.73\r\nvn -0.68 0.02 -0.73\r\nvn -0.56 0.05 -0.83\r\nvn 0.22 0.94 -0.27\r\nvn -0.22 0.96 -0.17\r\nvn -0.21 0.96 -0.18\r\nvn 0.26 0.93 -0.24\r\nvn -0.29 0.95 -0.16\r\nvn -0.29 0.94 -0.16\r\nvn -0.29 0.94 -0.18\r\nvn -0.2 0.96 -0.19\r\nvn 0.3 0.93 -0.22\r\nvn -0.2 0.96 -0.2\r\nvn -0.2 0.96 -0.19\r\nvn -0.31 0.92 -0.24\r\nvn -0.31 0.92 -0.24\r\nvn 0.34 0.93 -0.16\r\nvn -0.19 0.96 -0.19\r\nvn -0.19 0.96 -0.19\r\nvn 0.35 0.93 -0.12\r\nvn -0.3 0.94 -0.19\r\nvn -0.3 0.93 -0.2\r\nvn -0.3 0.93 -0.22\r\nvn -0.19 0.96 -0.2\r\nvn 0.35 0.93 -0.08\r\nvn -0.19 0.96 -0.21\r\nvn 0.35 0.93 -0.05\r\nvn -0.31 0.92 -0.23\r\nvn -0.2 0.96 -0.2\r\nvn 0.33 0.92 -0.2\r\nvn -0.29 0.94 -0.19\r\nvn 0.33 -0.9 -0.3\r\nvn 0.81 -0.42 -0.41\r\nvn 0.04 -0.98 -0.2\r\nvn 0.03 -0.97 -0.22\r\nvn 0.28 -0.9 -0.34\r\nvn 0.14 -0.93 -0.33\r\nvn 0.11 -0.93 -0.34\r\nvn 0.52 -0.58 -0.62\r\nvn 0.01 -0.97 -0.23\r\nvn -0 -0.97 -0.24\r\nvn -0.01 -0.97 -0.25\r\nvn 0.1 -0.94 -0.34\r\nvn -0.3 -0.59 -0.75\r\nvn -0.54 -0.1 -0.84\r\nvn -0.44 -0.63 -0.64\r\nvn -0.65 -0.12 -0.75\r\nvn -0.68 0.04 -0.73\r\nvn -0.56 0.06 -0.83\r\nvn -0.58 0.15 0.8\r\nvn -0.57 0.14 0.81\r\nvn -0.69 0.1 0.72\r\nvn -0.68 0.1 0.72\r\nvn -0.68 0.07 0.73\r\nvn -0.56 0.11 0.82\r\nvn -0.56 0.07 0.82\r\nvn -0.68 0.05 0.73\r\nvn -0.68 0.03 0.73\r\nvn -0.56 0.05 0.82\r\nvn -0.17 0.96 -0.24\r\nvn 0.28 0.95 0.16\r\nvn -0.26 0.91 -0.32\r\nvn -0.25 0.91 -0.33\r\nvn -0.17 0.95 -0.25\r\nvn -0.52 0.77 0.37\r\nvn -0.62 0.29 0.73\r\nvn -0.65 0.71 0.27\r\nvn -0.72 0.25 0.65\r\nvn -0.69 0.1 0.72\r\nvn -0.59 0.15 0.8\r\nvn -0.57 0.03 0.82\r\nvn -0.68 0.02 0.73\r\nvn -0.56 0.04 -0.83\r\nvn 0.01 0.14 -0.99\r\nvn -0.68 0.02 -0.73\r\nvn -0.68 0.01 -0.73\r\nvn -0.56 0.04 -0.83\r\nvn -0.41 0.86 -0.31\r\nvn -0.32 0.94 -0.14\r\nvn -0.45 0.85 -0.26\r\nvn -0.34 0.93 -0.12\r\nvn -0.33 0.94 -0.1\r\nvn -0.31 0.94 -0.12\r\nvn 0.2 -0.92 -0.35\r\nvn 0.17 -0.93 -0.33\r\nvn 0.01 -0.97 -0.23\r\nvn 0.01 -0.97 -0.22\r\nvn 0.74 0.16 -0.65\r\nvn 0.74 0.16 -0.65\r\nvn 0.74 0.16 -0.65\r\nvn 0.74 0.16 -0.65\r\nvn 0.74 0.16 -0.65\r\nvn 0.74 0.17 -0.65\r\nvn 0.74 0.17 -0.65\r\nvn 0.73 -0.01 -0.68\r\nvn 0.74 0.16 -0.65\r\nvn 0.72 0.18 -0.67\r\nvn 0.74 0.18 -0.65\r\nvn 0.74 0.18 -0.65\r\nvn 0.76 0.18 -0.62\r\nvn -0 -0.9 -0.43\r\nvn -0.11 -0.93 -0.34\r\nvn -0.29 0.95 -0.11\r\nvn -0.11 0.96 -0.26\r\nvn -0.31 0.95 -0.09\r\nvn 0.97 0.1 -0.21\r\nvn 0.98 0.1 -0.14\r\nvn 0.95 0.11 -0.28\r\nvn 0.91 0.09 0.41\r\nvn 0.87 0.1 0.49\r\nvn 0.94 0.09 0.32\r\nvn 0.99 0.09 0.09\r\nvn 0.98 0.09 0.16\r\nvn 1 0.09 0.01\r\nvn 0.97 0.09 0.23\r\nvn 0.99 0.09 -0.06\r\nvn 0.82 0.1 0.57\r\nvn 0.77 0.1 0.63\r\nvn 0.59 0.1 0.8\r\nvn 0.59 0.1 0.8\r\nvn 0.72 0.1 0.68\r\nvn 0.92 0.12 -0.36\r\nvn 0.92 0.12 -0.36\r\nvn 0.89 0.12 -0.45\r\nvn 0.93 0.11 -0.36\r\nvn 0.61 0.16 -0.78\r\nvn 0.73 0.16 -0.67\r\nvn 0.02 0.14 -0.99\r\nvn 0.79 0.15 -0.6\r\nvn 0.82 0.14 -0.56\r\nvn 0.77 0.15 -0.62\r\nvn -0.57 -0.01 0.82\r\nvn -0.01 0.02 1\r\nvn -0.68 -0.03 0.73\r\nvn -0.56 0.06 -0.83\r\nvn 0.74 0.16 -0.65\r\nvn 0.59 0.06 0.8\r\nvn 0.71 0.29 0.64\r\nvn 0.7 0.33 0.64\r\nvn 0.73 0.23 0.64\r\nvn 0.74 0.17 0.65\r\nvn 0.74 0.13 0.66\r\nvn 0.65 0.55 0.53\r\nvn 0.71 0.37 0.61\r\nvn 0.74 0.11 0.66\r\nvn 0.74 0.1 0.66\r\nvn 0.37 -0.84 0.4\r\nvn 0.04 -0.99 0.11\r\nvn -0.26 0.95 -0.14\r\nvn -0.35 0.91 -0.21\r\nvn 0.74 0.1 0.67\r\nvn 0.84 0.2 -0.51\r\nvn 0.8 0.19 -0.56\r\nvn 0.87 0.21 -0.44\r\nvn 0.78 0.18 -0.6\r\nvn 0.78 0.38 0.5\r\nvn 0.82 0.39 0.42\r\nvn 0.73 0.37 0.57\r\nvn 0.82 0.52 -0.24\r\nvn 0.84 0.51 -0.18\r\nvn 0.79 0.53 -0.31\r\nvn 0.86 0.38 0.34\r\nvn 0.85 0.52 0.01\r\nvn 0.84 0.53 0.08\r\nvn 0.86 0.51 -0.05\r\nvn 0.82 0.55 0.14\r\nvn 0.85 0.51 -0.12\r\nvn 0.55 0.35 0.76\r\nvn 0.68 0.34 0.65\r\nvn -0.04 0.3 0.95\r\nvn 0.6 0.18 -0.78\r\nvn -0.58 0.15 0.8\r\nvn -0.69 0.1 0.72\r\nvn -0.58 0.42 -0.69\r\nvn -0.67 0.43 -0.61\r\nvn 0.46 0.65 -0.6\r\nvn 0.7 0.36 0.62\r\nvn 0.75 0.54 -0.38\r\nvn 0.91 0.22 -0.36\r\nvn 0.17 0.94 -0.3\r\nvn 0.92 0.14 -0.36\r\nvn 0.93 0.12 -0.36\r\nvn 0.92 0.16 -0.36\r\nvn 0.93 0.11 -0.36\r\nvn 0.92 0.18 -0.36\r\nvn 0.91 0.2 -0.36\r\nvn 0.93 0.04 -0.38\r\nvn -0.23 0.96 -0.17\r\nvn -0.29 0.95 -0.15\r\nvn 0.23 -0.91 -0.35\r\nvn 0.02 -0.97 -0.23\r\nvn 0.85 0.13 -0.51\r\nvn 0.91 0.21 -0.36\r\nvt 0.7661 0.3462\r\nvt 0.766 0.347\r\nvt 0.698 0.3352\r\nvt 0.698 0.3343\r\nvt 0.7657 0.3508\r\nvt 0.6978 0.3394\r\nvt 0.6231 0.3289\r\nvt 0.623 0.3243\r\nvt 0.623 0.3233\r\nvt 0.2405 0.3241\r\nvt 0.2406 0.3255\r\nvt 0.1777 0.3386\r\nvt 0.1776 0.3373\r\nvt 0.2415 0.3318\r\nvt 0.1787 0.345\r\nvt 0.1251 0.3599\r\nvt 0.1241 0.3536\r\nvt 0.124 0.3522\r\nvt 0.4926 0.3101\r\nvt 0.4927 0.3113\r\nvt 0.4356 0.3107\r\nvt 0.4355 0.3094\r\nvt 0.4933 0.3167\r\nvt 0.4363 0.3163\r\nvt 0.3775 0.319\r\nvt 0.3768 0.3131\r\nvt 0.3766 0.3119\r\nvt 0.9073 0.5062\r\nvt 0.8708 0.5104\r\nvt 0.8709 0.5103\r\nvt 0.9074 0.5061\r\nvt 0.8337 0.5157\r\nvt 0.8338 0.5156\r\nvt 0.834 0.5134\r\nvt 0.8711 0.5083\r\nvt 0.9076 0.5044\r\nvt 0.3093 0.3175\r\nvt 0.3092 0.3162\r\nvt 0.3101 0.3236\r\nvt 0.5549 0.316\r\nvt 0.5548 0.3149\r\nvt 0.5553 0.3211\r\nvt 0.9732 0.5012\r\nvt 0.9437 0.5033\r\nvt 0.9438 0.5032\r\nvt 0.9733 0.5012\r\nvt 0.944 0.5017\r\nvt 0.9734 0.4999\r\nvt 0.083 0.367\r\nvt 0.0829 0.3656\r\nvt 0.0839 0.3733\r\nvt 0.0525 0.385\r\nvt 0.0518 0.3786\r\nvt 0.0517 0.3773\r\nvt 0.6273 0.4669\r\nvt 0.6941 0.4637\r\nvt 0.6935 0.4908\r\nvt 0.6284 0.4971\r\nvt 0.756 0.4617\r\nvt 0.7541 0.4858\r\nvt 0.7526 0.5054\r\nvt 0.693 0.5128\r\nvt 0.6293 0.5216\r\nvt 0.1695 0.625\r\nvt 0.163 0.5904\r\nvt 0.2144 0.5756\r\nvt 0.2201 0.6103\r\nvt 0.1554 0.5481\r\nvt 0.2077 0.5333\r\nvt 0.2679 0.5186\r\nvt 0.2741 0.5603\r\nvt 0.2793 0.5945\r\nvt 0.3993 0.493\r\nvt 0.4561 0.484\r\nvt 0.4607 0.521\r\nvt 0.4043 0.5315\r\nvt 0.5101 0.477\r\nvt 0.5139 0.5123\r\nvt 0.5171 0.5409\r\nvt 0.4645 0.551\r\nvt 0.4085 0.5628\r\nvt 0.9121 0.4645\r\nvt 0.8761 0.4623\r\nvt 0.8783 0.4427\r\nvt 0.914 0.4475\r\nvt 0.8396 0.461\r\nvt 0.842 0.4389\r\nvt 0.8444 0.4179\r\nvt 0.8804 0.4242\r\nvt 0.9159 0.4314\r\nvt 0.3398 0.5451\r\nvt 0.3444 0.578\r\nvt 0.3342 0.5048\r\nvt 0.5665 0.4714\r\nvt 0.5692 0.5043\r\nvt 0.5714 0.531\r\nvt 1.0017 0.4728\r\nvt 1.0015 0.4728\r\nvt 1.0028 0.4623\r\nvt 1.003 0.4623\r\nvt 0.999 0.4726\r\nvt 1.0004 0.4619\r\nvt 1.0017 0.4517\r\nvt 1.0041 0.4523\r\nvt 1.0043 0.4524\r\nvt 0.6252 0.4001\r\nvt 0.6958 0.4036\r\nvt 0.695 0.4328\r\nvt 0.6262 0.4326\r\nvt 0.7606 0.4081\r\nvt 0.7584 0.4342\r\nvt 0.1475 0.5007\r\nvt 0.2002 0.486\r\nvt 0.1402 0.4563\r\nvt 0.1934 0.4416\r\nvt 0.2549 0.4277\r\nvt 0.2611 0.4717\r\nvt 0.3938 0.4495\r\nvt 0.4511 0.4422\r\nvt 0.3886 0.4085\r\nvt 0.4464 0.4026\r\nvt 0.5019 0.3993\r\nvt 0.5058 0.4371\r\nvt 0.328 0.4595\r\nvt 0.3224 0.4167\r\nvt 0.5637 0.4339\r\nvt 0.561 0.3986\r\nvt 0.8821 0.4101\r\nvt 0.9174 0.4192\r\nvt 0.8462 0.4019\r\nvt 0.8477 0.3895\r\nvt 0.8834 0.3991\r\nvt 0.9185 0.4096\r\nvt 1.0051 0.4447\r\nvt 1.0053 0.4448\r\nvt 1.0027 0.4439\r\nvt 1.0035 0.4378\r\nvt 1.0059 0.4387\r\nvt 1.0061 0.4388\r\nvt 0.0279 0.3885\r\nvt 0.0279 0.3872\r\nvt 0.0286 0.3949\r\nvt 0.0133 0.4017\r\nvt 0.0127 0.3953\r\nvt 0.0127 0.3939\r\nvt 0.111 0.6936\r\nvt 0.1108 0.6934\r\nvt 0.1398 0.6822\r\nvt 0.1399 0.6825\r\nvt 0.1095 0.6884\r\nvt 0.1386 0.6771\r\nvt 0.1769 0.6642\r\nvt 0.1779 0.6693\r\nvt 0.178 0.6696\r\nvt 0.08 0.5744\r\nvt 0.0713 0.5267\r\nvt 0.1049 0.5145\r\nvt 0.1132 0.5619\r\nvt 0.0644 0.4817\r\nvt 0.0977 0.4698\r\nvt 0.9091 0.4912\r\nvt 0.8727 0.4932\r\nvt 0.8742 0.4794\r\nvt 0.9104 0.4793\r\nvt 0.8359 0.4961\r\nvt 0.8375 0.4805\r\nvt 0.9996 0.4893\r\nvt 0.9994 0.4893\r\nvt 1.0003 0.4819\r\nvt 1.0006 0.4819\r\nvt 0.9969 0.4894\r\nvt 0.9979 0.4819\r\nvt 0.6927 0.5281\r\nvt 0.63 0.5386\r\nvt 0.7515 0.5189\r\nvt 0.7509 0.527\r\nvt 0.6925 0.5371\r\nvt 0.6304 0.5487\r\nvt 0.2276 0.6546\r\nvt 0.2277 0.6549\r\nvt 0.2267 0.6495\r\nvt 0.2852 0.6331\r\nvt 0.286 0.6382\r\nvt 0.2861 0.6385\r\nvt 0.4671 0.572\r\nvt 0.4115 0.5848\r\nvt 0.5194 0.5609\r\nvt 0.5208 0.5728\r\nvt 0.4688 0.5845\r\nvt 0.4133 0.5979\r\nvt 0.3477 0.6012\r\nvt 0.283 0.6187\r\nvt 0.3498 0.615\r\nvt 0.5729 0.5497\r\nvt 0.5739 0.5607\r\nvt 0.122 0.604\r\nvt 0.1297 0.6383\r\nvt 0.0991 0.6501\r\nvt 0.0899 0.6162\r\nvt 0.6245 0.3754\r\nvt 0.6965 0.3813\r\nvt 0.6239 0.3562\r\nvt 0.6971 0.364\r\nvt 0.7638 0.3728\r\nvt 0.7624 0.3882\r\nvt 0.1349 0.4228\r\nvt 0.1883 0.408\r\nvt 0.1309 0.3969\r\nvt 0.1844 0.3821\r\nvt 0.2466 0.3686\r\nvt 0.2502 0.3944\r\nvt 0.3847 0.3774\r\nvt 0.4429 0.3727\r\nvt 0.3817 0.3533\r\nvt 0.4402 0.3494\r\nvt 0.4966 0.3483\r\nvt 0.4989 0.3706\r\nvt 0.3181 0.3844\r\nvt 0.3148 0.3593\r\nvt 0.559 0.3717\r\nvt 0.5575 0.3508\r\nvt 0.0599 0.448\r\nvt 0.0927 0.4362\r\nvt 0.0569 0.422\r\nvt 0.0891 0.4102\r\nvt 0.9204 0.3931\r\nvt 0.9204 0.3936\r\nvt 0.8855 0.3807\r\nvt 0.8855 0.3801\r\nvt 0.9201 0.3959\r\nvt 0.8852 0.3834\r\nvt 0.8497 0.3717\r\nvt 0.8501 0.3686\r\nvt 0.8501 0.368\r\nvt 1.0072 0.4287\r\nvt 1.0073 0.4289\r\nvt 1.0071 0.4288\r\nvt 1.0071 0.4285\r\nvt 1.0071 0.4304\r\nvt 1.0069 0.4302\r\nvt 1.0046 0.4291\r\nvt 1.0047 0.4276\r\nvt 1.0047 0.4273\r\nvt 0.0556 0.6701\r\nvt 0.0442 0.6366\r\nvt 0.0444 0.6365\r\nvt 0.0557 0.6701\r\nvt 0.0325 0.5947\r\nvt 0.0326 0.5946\r\nvt 0.0382 0.5921\r\nvt 0.0498 0.634\r\nvt 0.0609 0.6676\r\nvt 0.0234 0.5467\r\nvt 0.0235 0.5466\r\nvt 0.017 0.5013\r\nvt 0.0171 0.5012\r\nvt 0.0227 0.4988\r\nvt 0.0292 0.5442\r\nvt 0.0699 0.7119\r\nvt 0.0697 0.7118\r\nvt 0.0698 0.7117\r\nvt 0.07 0.712\r\nvt 0.0681 0.707\r\nvt 0.0682 0.7069\r\nvt 0.0731 0.7046\r\nvt 0.0747 0.7095\r\nvt 0.0748 0.7097\r\nvt 0.0132 0.4674\r\nvt 0.0134 0.4673\r\nvt 0.011 0.4413\r\nvt 0.0111 0.4412\r\nvt 0.0166 0.4388\r\nvt 0.0189 0.4649\r\nvt 0.9984 0.4984\r\nvt 0.9982 0.4985\r\nvt 0.9983 0.4984\r\nvt 0.9985 0.4984\r\nvt 0.9957 0.4988\r\nvt 0.9958 0.4988\r\nvt 0.9959 0.4977\r\nvt 0.9984 0.4974\r\nvt 0.9986 0.4973\r\nvt 0.9513 0.4394\r\nvt 0.9497 0.4532\r\nvt 0.9799 0.4462\r\nvt 0.9785 0.4581\r\nvt 0.977 0.4706\r\nvt 0.9479 0.4677\r\nvt 0.9535 0.4207\r\nvt 0.9526 0.4289\r\nvt 0.9819 0.43\r\nvt 0.9811 0.4371\r\nvt 0.9465 0.4803\r\nvt 0.9756 0.4815\r\nvt 0.9746 0.4903\r\nvt 0.9453 0.4905\r\nvt 0.9833 0.4178\r\nvt 0.9834 0.4182\r\nvt 0.9552 0.407\r\nvt 0.9552 0.4066\r\nvt 0.9832 0.4199\r\nvt 0.9549 0.409\r\nvt 0.6229 0.323\r\nvt 0.6979 0.3341\r\nvt 0.6229 0.3231\r\nvt 0.6979 0.3342\r\nvt 0.7659 0.3461\r\nvt 0.766 0.346\r\nvt 0.1241 0.3518\r\nvt 0.1777 0.3369\r\nvt 0.1244 0.3519\r\nvt 0.178 0.337\r\nvt 0.2407 0.3238\r\nvt 0.2405 0.3237\r\nvt 0.3767 0.3115\r\nvt 0.4355 0.3091\r\nvt 0.3768 0.3116\r\nvt 0.4356 0.3092\r\nvt 0.4927 0.3099\r\nvt 0.4926 0.3098\r\nvt 0.9072 0.5063\r\nvt 0.8707 0.5105\r\nvt 0.9069 0.5064\r\nvt 0.8705 0.5106\r\nvt 0.8335 0.5159\r\nvt 0.8336 0.5158\r\nvt 0.3093 0.3158\r\nvt 0.3094 0.3159\r\nvt 0.5548 0.3146\r\nvt 0.5548 0.3148\r\nvt 0.973 0.5013\r\nvt 0.9435 0.5034\r\nvt 0.9727 0.5013\r\nvt 0.9433 0.5034\r\nvt 0.0519 0.3768\r\nvt 0.083 0.3652\r\nvt 0.0523 0.3769\r\nvt 0.0834 0.3652\r\nvt 1.0042 0.4524\r\nvt 1.0029 0.4624\r\nvt 1.0039 0.4525\r\nvt 1.0026 0.4624\r\nvt 1.0013 0.4729\r\nvt 1.0016 0.4729\r\nvt 1.0059 0.4389\r\nvt 1.0052 0.4448\r\nvt 1.0056 0.439\r\nvt 1.0049 0.4449\r\nvt 0.0074 0.3967\r\nvt 0.0075 0.3964\r\nvt 0.0077 0.3959\r\nvt 0.0074 0.3963\r\nvt 0.0079 0.3965\r\nvt 0.008 0.3959\r\nvt 0.0133 0.3936\r\nvt 0.0129 0.3935\r\nvt 0.0112 0.4413\r\nvt 0.0094 0.4195\r\nvt 0.0092 0.4194\r\nvt 0.0116 0.4414\r\nvt 0.0097 0.4195\r\nvt 0.0084 0.4042\r\nvt 0.0081 0.4042\r\nvt 0.0079 0.4041\r\nvt 0.1782 0.6696\r\nvt 0.1401 0.6825\r\nvt 0.1784 0.6696\r\nvt 0.1404 0.6824\r\nvt 0.1116 0.6936\r\nvt 0.1113 0.6937\r\nvt 1.0004 0.482\r\nvt 1.0001 0.482\r\nvt 0.9992 0.4893\r\nvt 0.9995 0.4893\r\nvt 0.7506 0.5299\r\nvt 0.7505 0.53\r\nvt 0.6924 0.5405\r\nvt 0.7504 0.5301\r\nvt 0.6923 0.5406\r\nvt 0.6305 0.5526\r\nvt 0.6305 0.5525\r\nvt 0.2863 0.6385\r\nvt 0.2278 0.655\r\nvt 0.2864 0.6385\r\nvt 0.2281 0.6549\r\nvt 0.5213 0.5772\r\nvt 0.5213 0.5773\r\nvt 0.4694 0.5892\r\nvt 0.4694 0.5891\r\nvt 0.5214 0.5773\r\nvt 0.4695 0.5892\r\nvt 0.4142 0.6028\r\nvt 0.4141 0.6028\r\nvt 0.414 0.6028\r\nvt 0.3507 0.6202\r\nvt 0.3506 0.6201\r\nvt 0.3508 0.6202\r\nvt 0.5743 0.5649\r\nvt 0.5743 0.5648\r\nvt 0.8135 0.3569\r\nvt 0.8136 0.3571\r\nvt 0.8133 0.357\r\nvt 0.8499 0.3679\r\nvt 0.85 0.3678\r\nvt 0.9832 0.4177\r\nvt 0.9981 0.4242\r\nvt 0.9983 0.4243\r\nvt 0.9829 0.4178\r\nvt 0.9979 0.4242\r\nvt 1.0042 0.4273\r\nvt 1.0045 0.4273\r\nvt 1.0071 0.4286\r\nvt 1.0072 0.429\r\nvt 1.0069 0.4287\r\nvt 1.0069 0.429\r\nvt 1.0067 0.4305\r\nvt 1.007 0.4304\r\nvt 0.0558 0.6701\r\nvt 0.0444 0.6366\r\nvt 0.0561 0.6701\r\nvt 0.0448 0.6366\r\nvt 0.033 0.5948\r\nvt 0.0327 0.5948\r\nvt 0.0236 0.5468\r\nvt 0.0239 0.5468\r\nvt 0.0175 0.5014\r\nvt 0.0172 0.5014\r\nvt 0.0892 0.7032\r\nvt 0.0889 0.7032\r\nvt 0.0895 0.7031\r\nvt 0.0754 0.7096\r\nvt 0.0751 0.7097\r\nvt 0.0699 0.7117\r\nvt 0.0703 0.7119\r\nvt 0.0702 0.7116\r\nvt 0.0686 0.7069\r\nvt 0.0682 0.707\r\nvt 0.0134 0.4674\r\nvt 0.0138 0.4675\r\nvt 0.9989 0.4944\r\nvt 0.999 0.4943\r\nvt 0.9986 0.4944\r\nvt 0.9982 0.4974\r\nvt 0.9985 0.4974\r\nvt 0.9983 0.4985\r\nvt 0.998 0.4985\r\nvt 0.9977 0.4986\r\nvt 0.9952 0.4989\r\nvt 0.9955 0.4989\r\nvt 0.9202 0.393\r\nvt 0.955 0.4065\r\nvt 0.92 0.3931\r\nvt 0.9548 0.4065\r\nvt 0.9938 0.4607\r\nvt 0.9952 0.45\r\nvt 0.9924 0.4721\r\nvt 0.9962 0.4417\r\nvt 0.997 0.4353\r\nvt 0.9912 0.4819\r\nvt 0.9902 0.4898\r\nvt 0.9984 0.4246\r\nvt 0.9982 0.4262\r\nvt 0.9963 0.4946\r\nvt 0.9896 0.4953\r\nvt 0.9892 0.4986\r\nvt 0.9739 0.4963\r\nvt 1.0069 0.4284\r\nvt 1.0066 0.4284\r\nvt 0.9887 0.4998\r\nvt 0.9889 0.4998\r\nvt 0.9884 0.4998\r\nvt 0.6975 0.3495\r\nvt 0.6235 0.3401\r\nvt 0.7649 0.3598\r\nvt 0.181 0.3602\r\nvt 0.1275 0.3751\r\nvt 0.2436 0.3469\r\nvt 0.4379 0.3299\r\nvt 0.3792 0.3331\r\nvt 0.4946 0.3297\r\nvt 0.3121 0.3383\r\nvt 0.5562 0.3333\r\nvt 0.086 0.3885\r\nvt 0.0543 0.4002\r\nvt 0.008 0.404\r\nvt 0.0093 0.4193\r\nvt 0.0146 0.4169\r\nvt 0.8131 0.3612\r\nvt 0.8122 0.3693\r\nvt 0.8489 0.379\r\nvt 0.8109 0.381\r\nvt 1.0065 0.4337\r\nvt 1.0041 0.4327\r\nvt 1.0067 0.4338\r\nvt 0.9544 0.4138\r\nvt 0.9194 0.4015\r\nvt 0.9826 0.4241\r\nvt 0.0075 0.3979\r\nvt 0.0073 0.3978\r\nvt 0.0079 0.3979\r\nvt 1.0066 0.4339\r\nvt 1.0063 0.434\r\nvt 0.9977 0.4299\r\nvt 0.0074 0.3977\r\nvt 0.0654 0.6267\r\nvt 0.0757 0.6604\r\nvt 0.0545 0.5849\r\nvt 0.0456 0.5371\r\nvt 0.039 0.4919\r\nvt 0.0888 0.7029\r\nvt 0.0872 0.698\r\nvt 0.0349 0.458\r\nvt 0.0323 0.4319\r\nvt 0.0281 0.3867\r\nvt 0.0285 0.3868\r\nvt 0.0702 0.712\r\nvt 0.0706 0.7119\r\nvt 0.0301 0.4101\r\nvt 0.8717 0.5027\r\nvt 0.9081 0.4995\r\nvt 0.8347 0.507\r\nvt 0.9444 0.4976\r\nvt 0.1353 0.6626\r\nvt 0.1741 0.6495\r\nvt 0.1056 0.6741\r\nvt 0.6925 0.5403\r\nvt 0.6305 0.5523\r\nvt 0.7506 0.5298\r\nvt 0.2242 0.6348\r\nvt 0.4693 0.5889\r\nvt 0.4139 0.6025\r\nvt 0.5213 0.577\r\nvt 0.3505 0.6199\r\nvt 0.5742 0.5646\r\nvt 0.0636 0.6933\r\nvt 0.0686 0.6909\r\nvt 0.0635 0.6934\r\nvt 0.9988 0.4944\r\nvt 0.064 0.6933\r\nvt 0.9981 0.4984\r\nvt 0.989 0.4997\r\nvt 0.083 0.684\r\nvt 0.7957 0.5218\r\nvt 0.796 0.5193\r\nvt 0.7957 0.522\r\nvt 0.8047 0.4361\r\nvt 0.8073 0.4127\r\nvt 0.802 0.4608\r\nvt 0.8093 0.3949\r\nvt 0.7998 0.4825\r\nvt 0.798 0.5\r\nvt 0.8135 0.3578\r\nvt 0.7956 0.522\r\nvt 0.7955 0.5221\r\nvt 0.8854 0.38\r\nvt 0.8852 0.3801\r\nvt 0.8845 0.3899\r\nvt 0.7967 0.5121\r\no side_strip_mid_R\r\nusemtl Material__36\r\nf 19658/20357/18173 19659/20358/18174 19660/20359/18175 19661/20360/18176\r\nf 19659/20358/18174 19662/20361/18177 19663/20362/18178 19660/20359/18175\r\nf 19664/20363/18179 19665/20364/18180 19660/20359/18175 19663/20362/18178\r\nf 19665/20364/18180 19666/20365/18181 19661/20360/18176 19660/20359/18175\r\nf 19667/20366/18182 19668/20367/18183 19669/20368/18184 19670/20369/18185\r\nf 19668/20367/18183 19671/20370/18186 19672/20371/18187 19669/20368/18184\r\nf 19673/20372/18188 19674/20373/18189 19669/20368/18184 19672/20371/18187\r\nf 19674/20373/18189 19675/20374/18190 19670/20369/18185 19669/20368/18184\r\nf 19676/20375/18191 19677/20376/18192 19678/20377/18193 19679/20378/18194\r\nf 19677/20376/18192 19680/20379/18195 19681/20380/18196 19678/20377/18193\r\nf 19682/20381/18197 19683/20382/18198 19678/20377/18193 19681/20380/18196\r\nf 19683/20382/18198 19684/20383/18199 19679/20378/18194 19678/20377/18193\r\nf 19685/20384/18200 19686/20385/18201 19687/20386/18202 19688/20387/18203\r\nf 19686/20385/18201 19689/20388/18204 19690/20389/18205 19687/20386/18202\r\nf 19691/20390/18206 19692/20391/18207 19687/20386/18202 19690/20389/18205\r\nf 19692/20391/18207 19693/20392/18208 19688/20387/18203 19687/20386/18202\r\nf 19684/20383/18199 19683/20382/18198 19694/20393/18209 19695/20394/18210\r\nf 19683/20382/18198 19682/20381/18197 19696/20395/18211 19694/20393/18209\r\nf 19671/20370/18186 19668/20367/18183 19694/20393/18209 19696/20395/18211\r\nf 19668/20367/18183 19667/20366/18182 19695/20394/18210 19694/20393/18209\r\nf 19666/20365/18181 19665/20364/18180 19697/20396/18212 19698/20397/18213\r\nf 19665/20364/18180 19664/20363/18179 19699/20398/18214 19697/20396/18212\r\nf 19680/20379/18195 19677/20376/18192 19697/20396/18212 19699/20398/18214\r\nf 19677/20376/18192 19676/20375/18191 19698/20397/18213 19697/20396/18212\r\nf 19700/20399/18215 19701/20400/18216 19702/20401/18217 19703/20402/18218\r\nf 19701/20400/18216 19685/20384/18200 19688/20387/18203 19702/20401/18217\r\nf 19693/20392/18208 19704/20403/18219 19702/20401/18217 19688/20387/18203\r\nf 19704/20403/18219 19705/20404/18220 19703/20402/18218 19702/20401/18217\r\nf 19675/20374/18190 19674/20373/18189 19706/20405/18221 19707/20406/18222\r\nf 19674/20373/18189 19673/20372/18188 19708/20407/18223 19706/20405/18221\r\nf 19709/20408/18224 19710/20409/18225 19706/20405/18221 19708/20407/18223\r\nf 19710/20409/18225 19711/20410/18226 19707/20406/18222 19706/20405/18221\r\nf 19712/20411/18227 19713/20412/18228 19714/20413/18229 19715/20414/18230\r\nf 19713/20412/18228 19716/20415/18231 19717/20416/18232 19714/20413/18229\r\nf 19718/20417/18233 19719/20418/18234 19714/20413/18229 19717/20416/18232\r\nf 19719/20418/18234 19720/20419/18235 19715/20414/18230 19714/20413/18229\r\nf 19721/20420/18236 19722/20421/18237 19723/20422/18238 19724/20423/18239\r\nf 19722/20421/18237 19725/20424/18240 19726/20425/18241 19723/20422/18238\r\nf 19727/20426/18242 19728/20427/18243 19723/20422/18238 19726/20425/18241\r\nf 19728/20427/18243 19729/20428/18244 19724/20423/18239 19723/20422/18238\r\nf 19730/20429/18245 19731/20430/18246 19732/20431/18247 19733/20432/18248\r\nf 19731/20430/18246 19734/20433/18249 19735/20434/18250 19732/20431/18247\r\nf 19736/20435/18251 19737/20436/18252 19732/20431/18247 19735/20434/18250\r\nf 19737/20436/18252 19738/20437/18253 19733/20432/18248 19732/20431/18247\r\nf 19739/20438/18254 19740/20439/18255 19741/20440/18256 19742/20441/18257\r\nf 19740/20439/18255 19743/20442/18258 19744/20443/18259 19741/20440/18256\r\nf 19745/20444/18260 19746/20445/18261 19741/20440/18256 19744/20443/18259\r\nf 19746/20445/18261 19747/20446/18262 19742/20441/18257 19741/20440/18256\r\nf 19729/20428/18244 19728/20427/18243 19748/20447/18263 19749/20448/18264\r\nf 19728/20427/18243 19727/20426/18242 19750/20449/18265 19748/20447/18263\r\nf 19730/20429/18245 19733/20432/18248 19748/20447/18263 19750/20449/18265\r\nf 19733/20432/18248 19738/20437/18253 19749/20448/18264 19748/20447/18263\r\nf 19734/20433/18249 19751/20450/18266 19752/20451/18267 19735/20434/18250\r\nf 19751/20450/18266 19712/20411/18227 19715/20414/18230 19752/20451/18267\r\nf 19720/20419/18235 19753/20452/18268 19752/20451/18267 19715/20414/18230\r\nf 19753/20452/18268 19736/20435/18251 19735/20434/18250 19752/20451/18267\r\nf 19754/20453/18269 19755/20454/18270 19756/20455/18271 19757/20456/18272\r\nf 19755/20454/18270 19758/20457/18273 19759/20458/18274 19756/20455/18271\r\nf 19760/20459/18275 19761/20460/18276 19756/20455/18271 19759/20458/18274\r\nf 19761/20460/18276 19762/20461/18277 19757/20456/18272 19756/20455/18271\r\nf 19763/20462/18278 19764/20463/18279 19765/20464/18280 19766/20465/18281\r\nf 19764/20463/18279 19767/20466/18282 19768/20467/18283 19765/20464/18280\r\nf 19716/20415/18231 19713/20412/18228 19765/20464/18280 19768/20467/18283\r\nf 19713/20412/18228 19712/20411/18227 19766/20465/18281 19765/20464/18280\r\nf 19725/20424/18240 19769/20468/18284 19770/20469/18285 19726/20425/18241\r\nf 19769/20468/18284 19771/20470/18286 19772/20471/18287 19770/20469/18285\r\nf 19773/20472/18288 19774/20473/18289 19770/20469/18285 19772/20471/18287\r\nf 19774/20473/18289 19727/20426/18242 19726/20425/18241 19770/20469/18285\r\nf 19730/20429/18245 19775/20474/18290 19776/20475/18291 19731/20430/18246\r\nf 19775/20474/18290 19777/20476/18292 19778/20477/18293 19776/20475/18291\r\nf 19779/20478/18294 19780/20479/18295 19776/20475/18291 19778/20477/18293\r\nf 19780/20479/18295 19734/20433/18249 19731/20430/18246 19776/20475/18291\r\nf 19727/20426/18242 19774/20473/18289 19781/20480/18296 19750/20449/18265\r\nf 19774/20473/18289 19773/20472/18288 19782/20481/18297 19781/20480/18296\r\nf 19777/20476/18292 19775/20474/18290 19781/20480/18296 19782/20481/18297\r\nf 19775/20474/18290 19730/20429/18245 19750/20449/18265 19781/20480/18296\r\nf 19734/20433/18249 19780/20479/18295 19783/20482/18298 19751/20450/18266\r\nf 19780/20479/18295 19779/20478/18294 19784/20483/18299 19783/20482/18298\r\nf 19763/20462/18278 19766/20465/18281 19783/20482/18298 19784/20483/18299\r\nf 19766/20465/18281 19712/20411/18227 19751/20450/18266 19783/20482/18298\r\nf 19747/20446/18262 19746/20445/18261 19785/20484/18300 19786/20485/18301\r\nf 19746/20445/18261 19745/20444/18260 19787/20486/18302 19785/20484/18300\r\nf 19788/20487/18303 19789/20488/18304 19785/20484/18300 19787/20486/18302\r\nf 19789/20488/18304 19790/20489/18305 19786/20485/18301 19785/20484/18300\r\nf 19762/20461/18277 19761/20460/18276 19791/20490/18306 19792/20491/18307\r\nf 19761/20460/18276 19760/20459/18275 19793/20492/18308 19791/20490/18306\r\nf 19794/20493/18309 19795/20494/18310 19791/20490/18306 19793/20492/18308\r\nf 19795/20494/18310 19796/20495/18311 19792/20491/18307 19791/20490/18306\r\nf 19711/20410/18226 19710/20409/18225 19797/20496/18312 19798/20497/18313\r\nf 19710/20409/18225 19709/20408/18224 19799/20498/18314 19797/20496/18312\r\nf 19800/20499/18315 19801/20500/18316 19797/20496/18312 19799/20498/18314\r\nf 19801/20500/18316 19802/20501/18317 19798/20497/18313 19797/20496/18312\r\nf 19803/20502/18318 19804/20503/18319 19805/20504/18320 19806/20505/18321\r\nf 19804/20503/18319 19807/20506/18322 19808/20507/18323 19805/20504/18320\r\nf 19809/20508/18324 19810/20509/18325 19805/20504/18320 19808/20507/18323\r\nf 19810/20509/18325 19811/20510/18326 19806/20505/18321 19805/20504/18320\r\nf 19812/20511/18327 19813/20512/18328 19814/20513/18329 19815/20514/18330\r\nf 19813/20512/18328 19816/20515/18331 19817/20516/18332 19814/20513/18329\r\nf 19771/20470/18286 19769/20468/18284 19814/20513/18329 19817/20516/18332\r\nf 19769/20468/18284 19725/20424/18240 19815/20514/18330 19814/20513/18329\r\nf 19818/20517/18333 19819/20518/18334 19820/20519/18335 19821/20520/18336\r\nf 19819/20518/18334 19822/20521/18337 19823/20522/18338 19820/20519/18335\r\nf 19743/20442/18258 19740/20439/18255 19820/20519/18335 19823/20522/18338\r\nf 19740/20439/18255 19739/20438/18254 19821/20520/18336 19820/20519/18335\r\nf 19824/20523/18339 19825/20524/18340 19826/20525/18341 19827/20526/18342\r\nf 19825/20524/18340 19828/20527/18343 19829/20528/18344 19826/20525/18341\r\nf 19758/20457/18273 19755/20454/18270 19826/20525/18341 19829/20528/18344\r\nf 19755/20454/18270 19754/20453/18269 19827/20526/18342 19826/20525/18341\r\nf 19720/20419/18235 19719/20418/18234 19830/20529/18345 19831/20530/18346\r\nf 19719/20418/18234 19718/20417/18233 19832/20531/18347 19830/20529/18345\r\nf 19833/20532/18348 19834/20533/18349 19830/20529/18345 19832/20531/18347\r\nf 19834/20533/18349 19835/20534/18350 19831/20530/18346 19830/20529/18345\r\nf 19811/20510/18326 19810/20509/18325 19836/20535/18351 19837/20536/18352\r\nf 19810/20509/18325 19809/20508/18324 19838/20537/18353 19836/20535/18351\r\nf 19839/20538/18354 19840/20539/18355 19836/20535/18351 19838/20537/18353\r\nf 19840/20539/18355 19841/20540/18356 19837/20536/18352 19836/20535/18351\r\nf 19738/20437/18253 19737/20436/18252 19842/20541/18357 19843/20542/18358\r\nf 19737/20436/18252 19736/20435/18251 19844/20543/18359 19842/20541/18357\r\nf 19845/20544/18360 19846/20545/18361 19842/20541/18357 19844/20543/18359\r\nf 19846/20545/18361 19847/20546/18362 19843/20542/18358 19842/20541/18357\r\nf 19729/20428/18244 19749/20448/18264 19848/20547/18363 19849/20548/18364\r\nf 19749/20448/18264 19738/20437/18253 19843/20542/18358 19848/20547/18363\r\nf 19847/20546/18362 19850/20549/18365 19848/20547/18363 19843/20542/18358\r\nf 19850/20549/18365 19839/20538/18354 19849/20548/18364 19848/20547/18363\r\nf 19736/20435/18251 19753/20452/18268 19851/20550/18366 19844/20543/18359\r\nf 19753/20452/18268 19720/20419/18235 19831/20530/18346 19851/20550/18366\r\nf 19835/20534/18350 19852/20551/18367 19851/20550/18366 19831/20530/18346\r\nf 19852/20551/18367 19845/20544/18360 19844/20543/18359 19851/20550/18366\r\nf 19725/20424/18240 19722/20421/18237 19853/20552/18368 19815/20514/18330\r\nf 19722/20421/18237 19721/20420/18236 19854/20553/18369 19853/20552/18368\r\nf 19855/20554/18370 19856/20555/18371 19853/20552/18368 19854/20553/18369\r\nf 19856/20555/18371 19812/20511/18327 19815/20514/18330 19853/20552/18368\r\nf 19763/20462/18278 19857/20556/18372 19858/20557/18373 19764/20463/18279\r\nf 19857/20556/18372 19859/20558/18374 19860/20559/18375 19858/20557/18373\r\nf 19861/20560/18376 19862/20561/18377 19858/20557/18373 19860/20559/18375\r\nf 19862/20561/18377 19767/20466/18282 19764/20463/18279 19858/20557/18373\r\nf 19771/20470/18286 19863/20562/18378 19864/20563/18379 19772/20471/18287\r\nf 19863/20562/18378 19865/20564/18380 19866/20565/18381 19864/20563/18379\r\nf 19867/20566/18382 19868/20567/18383 19864/20563/18379 19866/20565/18381\r\nf 19868/20567/18383 19773/20472/18288 19772/20471/18287 19864/20563/18379\r\nf 19777/20476/18292 19869/20568/18384 19870/20569/18385 19778/20477/18293\r\nf 19869/20568/18384 19871/20570/18386 19872/20571/18387 19870/20569/18385\r\nf 19873/20572/18388 19874/20573/18389 19870/20569/18385 19872/20571/18387\r\nf 19874/20573/18389 19779/20478/18294 19778/20477/18293 19870/20569/18385\r\nf 19773/20472/18288 19868/20567/18383 19875/20574/18390 19782/20481/18297\r\nf 19868/20567/18383 19867/20566/18382 19876/20575/18391 19875/20574/18390\r\nf 19871/20570/18386 19869/20568/18384 19875/20574/18390 19876/20575/18391\r\nf 19869/20568/18384 19777/20476/18292 19782/20481/18297 19875/20574/18390\r\nf 19779/20478/18294 19874/20573/18389 19877/20576/18392 19784/20483/18299\r\nf 19874/20573/18389 19873/20572/18388 19878/20577/18393 19877/20576/18392\r\nf 19859/20558/18374 19857/20556/18372 19877/20576/18392 19878/20577/18393\r\nf 19857/20556/18372 19763/20462/18278 19784/20483/18299 19877/20576/18392\r\nf 19816/20515/18331 19879/20578/18394 19880/20579/18395 19817/20516/18332\r\nf 19879/20578/18394 19881/20580/18396 19882/20581/18397 19880/20579/18395\r\nf 19865/20564/18380 19863/20562/18378 19880/20579/18395 19882/20581/18397\r\nf 19863/20562/18378 19771/20470/18286 19817/20516/18332 19880/20579/18395\r\nf 19883/20582/18398 19884/20583/18399 19885/20584/18400 19886/20585/18401\r\nf 19884/20583/18399 19887/20586/18402 19888/20587/18403 19885/20584/18400\r\nf 19889/20588/18404 19890/20589/18405 19885/20584/18400 19888/20587/18403\r\nf 19890/20589/18405 19891/20590/18406 19886/20585/18401 19885/20584/18400\r\nf 19892/20591/18407 19893/20592/18408 19894/20593/18409 19895/20594/18410\r\nf 19893/20592/18408 19896/20595/18411 19897/20596/18412 19894/20593/18409\r\nf 19898/20597/18413 19899/20598/18414 19894/20593/18409 19897/20596/18412\r\nf 19899/20598/18414 19900/20599/18415 19895/20594/18410 19894/20593/18409\r\nf 19901/20600/18416 19902/20601/18417 19903/20602/18418 19904/20603/18419\r\nf 19902/20601/18417 19905/20604/18420 19906/20605/18421 19903/20602/18418\r\nf 19907/20606/18422 19908/20607/18423 19903/20602/18418 19906/20605/18421\r\nf 19908/20607/18423 19909/20608/18424 19904/20603/18419 19903/20602/18418\r\nf 19905/20604/18420 19910/20609/18425 19911/20610/18426 19906/20605/18421\r\nf 19910/20609/18425 19912/20611/18427 19913/20612/18428 19911/20610/18426\r\nf 19914/20613/18429 19915/20614/18430 19911/20610/18426 19913/20612/18428\r\nf 19915/20614/18430 19907/20606/18422 19906/20605/18421 19911/20610/18426\r\nf 19916/20615/18431 19917/20616/18432 19918/20617/18433 19919/20618/18434\r\nf 19917/20616/18432 19920/20619/18435 19921/20620/18436 19918/20617/18433\r\nf 19922/20621/18437 19923/20622/18438 19918/20617/18433 19921/20620/18436\r\nf 19923/20622/18438 19924/20623/18439 19919/20618/18434 19918/20617/18433\r\nf 19912/20611/18427 19925/20624/18440 19926/20625/18441 19913/20612/18428\r\nf 19925/20624/18440 19927/20626/18442 19928/20627/18443 19926/20625/18441\r\nf 19929/20628/18444 19930/20629/18445 19926/20625/18441 19928/20627/18443\r\nf 19930/20629/18445 19914/20613/18429 19913/20612/18428 19926/20625/18441\r\nf 19931/20630/18446 19932/20631/18447 19933/20632/18448 19934/20633/18449\r\nf 19932/20631/18447 19935/20634/18450 19936/20635/18451 19933/20632/18448\r\nf 19937/20636/18452 19938/20637/18453 19933/20632/18448 19936/20635/18451\r\nf 19938/20637/18453 19939/20638/18454 19934/20633/18449 19933/20632/18448\r\nf 19747/20446/18262 19940/20639/18455 19941/20640/18456 19742/20441/18257\r\nf 19940/20639/18455 19942/20641/18457 19943/20642/18458 19941/20640/18456\r\nf 19944/20643/18459 19945/20644/18460 19941/20640/18456 19943/20642/18458\r\nf 19945/20644/18460 19739/20438/18254 19742/20441/18257 19941/20640/18456\r\nf 19790/20489/18305 19946/20645/18461 19947/20646/18462 19786/20485/18301\r\nf 19946/20645/18461 19948/20647/18463 19949/20648/18464 19947/20646/18462\r\nf 19942/20641/18457 19940/20639/18455 19947/20646/18462 19949/20648/18464\r\nf 19940/20639/18455 19747/20446/18262 19786/20485/18301 19947/20646/18462\r\nf 19739/20438/18254 19945/20644/18460 19950/20649/18465 19821/20520/18336\r\nf 19945/20644/18460 19944/20643/18459 19951/20650/18466 19950/20649/18465\r\nf 19952/20651/18467 19953/20652/18468 19950/20649/18465 19951/20650/18466\r\nf 19953/20652/18468 19818/20517/18333 19821/20520/18336 19950/20649/18465\r\nf 19954/20653/18469 19955/20654/18470 19956/20655/18471 19957/20656/18472\r\nf 19955/20654/18470 19958/20657/18473 19959/20658/18474 19956/20655/18471\r\nf 19887/20586/18402 19884/20583/18399 19956/20655/18471 19959/20658/18474\r\nf 19884/20583/18399 19883/20582/18398 19957/20656/18472 19956/20655/18471\r\nf 19666/20365/18181 19960/20659/18475 19961/20660/18476 19661/20360/18176\r\nf 19960/20659/18475 19962/20661/18477 19963/20662/18478 19961/20660/18476\r\nf 19964/20663/18479 19965/20664/18480 19961/20660/18476 19963/20662/18478\r\nf 19965/20664/18480 19658/20357/18173 19661/20360/18176 19961/20660/18476\r\nf 19675/20374/18190 19966/20665/18481 19967/20666/18482 19670/20369/18185\r\nf 19966/20665/18481 19968/20667/18483 19969/20668/18484 19967/20666/18482\r\nf 19970/20669/18485 19971/20670/18486 19967/20666/18482 19969/20668/18484\r\nf 19971/20670/18486 19667/20366/18182 19670/20369/18185 19967/20666/18482\r\nf 19684/20383/18199 19972/20671/18487 19973/20672/18488 19679/20378/18194\r\nf 19972/20671/18487 19974/20673/18489 19975/20674/18490 19973/20672/18488\r\nf 19976/20675/18491 19977/20676/18492 19973/20672/18488 19975/20674/18490\r\nf 19977/20676/18492 19676/20375/18191 19679/20378/18194 19973/20672/18488\r\nf 19685/20384/18200 19978/20677/18493 19979/20678/18494 19686/20385/18201\r\nf 19978/20677/18493 19980/20679/18495 19981/20680/18496 19979/20678/18494\r\nf 19982/20681/18497 19983/20682/18498 19979/20678/18494 19981/20680/18496\r\nf 19983/20682/18498 19689/20388/18204 19686/20385/18201 19979/20678/18494\r\nf 19667/20366/18182 19971/20670/18486 19984/20683/18499 19695/20394/18210\r\nf 19971/20670/18486 19970/20669/18485 19985/20684/18500 19984/20683/18499\r\nf 19974/20673/18489 19972/20671/18487 19984/20683/18499 19985/20684/18500\r\nf 19972/20671/18487 19684/20383/18199 19695/20394/18210 19984/20683/18499\r\nf 19676/20375/18191 19977/20676/18492 19986/20685/18501 19698/20397/18213\r\nf 19977/20676/18492 19976/20675/18491 19987/20686/18502 19986/20685/18501\r\nf 19962/20661/18477 19960/20659/18475 19986/20685/18501 19987/20686/18502\r\nf 19960/20659/18475 19666/20365/18181 19698/20397/18213 19986/20685/18501\r\nf 19700/20399/18215 19988/20687/18503 19989/20688/18504 19701/20400/18216\r\nf 19988/20687/18503 19990/20689/18505 19991/20690/18506 19989/20688/18504\r\nf 19980/20679/18495 19978/20677/18493 19989/20688/18504 19991/20690/18506\r\nf 19978/20677/18493 19685/20384/18200 19701/20400/18216 19989/20688/18504\r\nf 19711/20410/18226 19992/20691/18507 19993/20692/18508 19707/20406/18222\r\nf 19992/20691/18507 19994/20693/18509 19995/20694/18510 19993/20692/18508\r\nf 19968/20667/18483 19966/20665/18481 19993/20692/18508 19995/20694/18510\r\nf 19966/20665/18481 19675/20374/18190 19707/20406/18222 19993/20692/18508\r\nf 19762/20461/18277 19996/20695/18511 19997/20696/18512 19757/20456/18272\r\nf 19996/20695/18511 19998/20697/18513 19999/20698/18514 19997/20696/18512\r\nf 20000/20699/18515 20001/20700/18516 19997/20696/18512 19999/20698/18514\r\nf 20001/20700/18516 19754/20453/18269 19757/20456/18272 19997/20696/18512\r\nf 19796/20495/18311 20002/20701/18517 20003/20702/18518 19792/20491/18307\r\nf 20002/20701/18517 20004/20703/18519 20005/20704/18520 20003/20702/18518\r\nf 19998/20697/18513 19996/20695/18511 20003/20702/18518 20005/20704/18520\r\nf 19996/20695/18511 19762/20461/18277 19792/20491/18307 20003/20702/18518\r\nf 20006/20705/18521 20007/20706/18522 20008/20707/18523 20009/20708/18524\r\nf 20007/20706/18522 20010/20709/18525 20011/20710/18526 20008/20707/18523\r\nf 20012/20711/18527 20013/20712/18528 20008/20707/18523 20011/20710/18526\r\nf 20013/20712/18528 19802/20501/18317 20009/20708/18524 20008/20707/18523\r\nf 19927/20626/18442 20014/20713/18529 20015/20714/18530 20016/20715/18531\r\nf 20014/20713/18529 20017/20716/18532 20018/20717/18533 20015/20714/18530\r\nf 20019/20718/18534 20020/20719/18535 20015/20714/18530 20018/20717/18533\r\nf 20020/20719/18535 20021/20720/18536 20016/20715/18531 20015/20714/18530\r\nf 19811/20510/18326 20022/20721/18537 20023/20722/18538 19806/20505/18321\r\nf 20022/20721/18537 20024/20723/18539 20025/20724/18540 20023/20722/18538\r\nf 20026/20725/18541 20027/20726/18542 20023/20722/18538 20025/20724/18540\r\nf 20027/20726/18542 19803/20502/18318 19806/20505/18321 20023/20722/18538\r\nf 19754/20453/18269 20001/20700/18516 20028/20727/18543 19827/20526/18342\r\nf 20001/20700/18516 20000/20699/18515 20029/20728/18544 20028/20727/18543\r\nf 20030/20729/18545 20031/20730/18546 20028/20727/18543 20029/20728/18544\r\nf 20031/20730/18546 19824/20523/18339 19827/20526/18342 20028/20727/18543\r\nf 20032/20731/18547 20033/20732/18548 20034/20733/18549 20035/20733/18550\r\nf 20033/20732/18548 20036/20734/18551 20037/20735/18552 20034/20733/18549\r\nf 20038/20736/18553 20039/20737/18554 20034/20733/18549 20037/20735/18552\r\nf 20039/20737/18554 20040/20737/18555 20035/20733/18550 20034/20733/18549\r\nf 19841/20540/18356 20041/20738/18556 20042/20739/18557 19837/20536/18352\r\nf 20041/20738/18556 20043/20740/18558 20044/20741/18559 20042/20739/18557\r\nf 20024/20723/18539 20022/20721/18537 20042/20739/18557 20044/20741/18559\r\nf 20022/20721/18537 19811/20510/18326 19837/20536/18352 20042/20739/18557\r\nf 20045/20742/18560 20046/20743/18561 20047/20744/18562 20048/20745/18563\r\nf 20046/20743/18561 20049/20746/18564 20050/20747/18565 20047/20744/18562\r\nf 20051/20748/18566 20052/20749/18567 20047/20744/18562 20050/20747/18565\r\nf 20052/20749/18567 20053/20750/18568 20048/20745/18563 20047/20744/18562\r\nf 20053/20750/18568 20052/20749/18567 20054/20751/18569 20055/20752/18570\r\nf 20052/20749/18567 20051/20748/18566 20056/20753/18571 20054/20751/18569\r\nf 20043/20740/18558 20041/20738/18556 20054/20751/18569 20056/20753/18571\r\nf 20041/20738/18556 19841/20540/18356 20055/20752/18570 20054/20751/18569\r\nf 20040/20737/18555 20039/20737/18554 20057/20754/18572 20058/20755/18573\r\nf 20039/20737/18554 20038/20736/18553 20059/20754/18574 20057/20754/18572\r\nf 20049/20746/18564 20046/20743/18561 20057/20754/18572 20059/20754/18574\r\nf 20046/20743/18561 20045/20742/18560 20058/20755/18573 20057/20754/18572\r\nf 19658/20357/18173 19965/20664/18480 20060/20756/18575 20061/20757/18576\r\nf 19965/20664/18480 19964/20663/18479 20062/20758/18577 20060/20756/18575\r\nf 20063/20759/18578 20064/20760/18579 20060/20756/18575 20062/20758/18577\r\nf 20064/20760/18579 19891/20590/18406 20061/20757/18576 20060/20756/18575\r\nf 19954/20653/18469 20065/20761/18580 20066/20762/18581 20067/20763/18582\r\nf 20065/20761/18580 20068/20764/18583 20069/20765/18584 20066/20762/18581\r\nf 20070/20766/18585 20071/20767/18586 20066/20762/18581 20069/20765/18584\r\nf 20071/20767/18586 19900/20599/18415 20067/20763/18582 20066/20762/18581\r\nf 19892/20591/18407 20072/20768/18587 20073/20769/18588 19893/20592/18408\r\nf 20072/20768/18587 20074/20770/18589 20075/20771/18590 20073/20769/18588\r\nf 20076/20772/18591 20077/20773/18592 20073/20769/18588 20075/20771/18590\r\nf 20077/20773/18592 19896/20595/18411 19893/20592/18408 20073/20769/18588\r\nf 19901/20600/18416 20078/20774/18593 20079/20775/18594 19902/20601/18417\r\nf 20078/20774/18593 20080/20776/18595 20081/20777/18596 20079/20775/18594\r\nf 20082/20778/18597 20083/20779/18598 20079/20775/18594 20081/20777/18596\r\nf 20083/20779/18598 19905/20604/18420 19902/20601/18417 20079/20775/18594\r\nf 19905/20604/18420 20083/20779/18598 20084/20780/18599 19910/20609/18425\r\nf 20083/20779/18598 20082/20778/18597 20085/20781/18600 20084/20780/18599\r\nf 20086/20782/18601 20087/20783/18602 20084/20780/18599 20085/20781/18600\r\nf 20087/20783/18602 19912/20611/18427 19910/20609/18425 20084/20780/18599\r\nf 19803/20502/18318 20027/20726/18542 20088/20784/18603 20089/20785/18604\r\nf 20027/20726/18542 20026/20725/18541 20090/20786/18605 20088/20784/18603\r\nf 20091/20787/18606 20092/20788/18607 20088/20784/18603 20090/20786/18605\r\nf 20092/20788/18607 19924/20623/18439 20089/20785/18604 20088/20784/18603\r\nf 19916/20615/18431 20093/20618/18608 20094/20789/18609 19917/20616/18432\r\nf 20093/20618/18608 20095/20790/18610 20096/20791/18611 20094/20789/18609\r\nf 20097/20792/18612 20098/20793/18613 20094/20789/18609 20096/20791/18611\r\nf 20098/20793/18613 19920/20619/18435 19917/20616/18432 20094/20789/18609\r\nf 19912/20611/18427 20087/20783/18602 20099/20794/18614 19925/20624/18440\r\nf 20087/20783/18602 20086/20782/18601 20100/20795/18615 20099/20794/18614\r\nf 20017/20716/18532 20014/20713/18529 20099/20794/18614 20100/20795/18615\r\nf 20014/20713/18529 19927/20626/18442 19925/20624/18440 20099/20794/18614\r\nf 19824/20523/18339 20031/20730/18546 20101/20796/18616 20102/20797/18617\r\nf 20031/20730/18546 20030/20729/18545 20103/20798/18618 20101/20796/18616\r\nf 20104/20799/18619 20105/20800/18620 20101/20796/18616 20103/20798/18618\r\nf 20105/20800/18620 19939/20638/18454 20102/20797/18617 20101/20796/18616\r\nf 19931/20630/18446 20106/20801/18621 20107/20802/18622 19932/20631/18447\r\nf 20106/20801/18621 20108/20802/18623 20109/20803/18624 20107/20802/18622\r\nf 20110/20804/18625 20111/20805/18626 20107/20802/18622 20109/20803/18624\r\nf 20111/20805/18626 19935/20634/18450 19932/20631/18447 20107/20802/18622\r\nf 19883/20582/18398 20112/20806/18627 20113/20807/18628 19957/20656/18472\r\nf 20112/20806/18627 20114/20808/18629 20115/20809/18630 20113/20807/18628\r\nf 20068/20764/18583 20065/20761/18580 20113/20807/18628 20115/20809/18630\r\nf 20065/20761/18580 19954/20653/18469 19957/20656/18472 20113/20807/18628\r\nf 19760/20459/18275 19759/20458/18274 20116/20810/18631 20117/20811/18632\r\nf 19759/20458/18274 19758/20457/18273 20118/20812/18633 20116/20810/18631\r\nf 19944/20643/18459 19943/20642/18458 20116/20810/18631 20118/20812/18633\r\nf 19943/20642/18458 19942/20641/18457 20117/20811/18632 20116/20810/18631\r\nf 19794/20493/18309 19793/20492/18308 20119/20813/18634 20120/20814/18635\r\nf 19793/20492/18308 19760/20459/18275 20117/20811/18632 20119/20813/18634\r\nf 19942/20641/18457 19949/20648/18464 20119/20813/18634 20117/20811/18632\r\nf 19949/20648/18464 19948/20647/18463 20120/20814/18635 20119/20813/18634\r\nf 19758/20457/18273 19829/20528/18344 20121/20815/18636 20118/20812/18633\r\nf 19829/20528/18344 19828/20527/18343 20122/20816/18637 20121/20815/18636\r\nf 19952/20651/18467 19951/20650/18466 20121/20815/18636 20122/20816/18637\r\nf 19951/20650/18466 19944/20643/18459 20118/20812/18633 20121/20815/18636\r\nf 19900/20599/18415 19899/20598/18414 20123/20817/18638 20067/20763/18582\r\nf 19899/20598/18414 19898/20597/18413 20124/20818/18639 20123/20817/18638\r\nf 19958/20657/18473 19955/20654/18470 20123/20817/18638 20124/20818/18639\r\nf 19955/20654/18470 19954/20653/18469 20067/20763/18582 20123/20817/18638\r\nf 19828/20527/18343 20125/20819/18640 20126/20820/18641 20122/20816/18637\r\nf 20125/20819/18640 19937/20636/18452 20127/20821/18642 20126/20820/18641\r\nf 19705/20404/18220 20128/20822/18643 20126/20820/18641 20127/20821/18642\r\nf 20128/20822/18643 19952/20651/18467 20122/20816/18637 20126/20820/18641\r\nf 19900/20599/18415 20071/20767/18586 20129/20823/18644 19895/20594/18410\r\nf 20071/20767/18586 20070/20766/18585 20130/20824/18645 20129/20823/18644\r\nf 20074/20770/18589 20072/20768/18587 20129/20823/18644 20130/20824/18645\r\nf 20072/20768/18587 19892/20591/18407 19895/20594/18410 20129/20823/18644\r\nf 19935/20634/18450 20111/20805/18626 20131/20825/18646 20132/20826/18647\r\nf 20111/20805/18626 20110/20804/18625 20133/20827/18648 20131/20825/18646\r\nf 19990/20689/18505 19988/20687/18503 20131/20825/18646 20133/20827/18648\r\nf 19988/20687/18503 19700/20399/18215 20132/20826/18647 20131/20825/18646\r\nf 19664/20363/18179 19663/20362/18178 20134/20828/18649 20135/20829/18650\r\nf 19663/20362/18178 19662/20361/18177 20136/20830/18651 20134/20828/18649\r\nf 19861/20560/18376 19860/20559/18375 20134/20828/18649 20136/20830/18651\r\nf 19860/20559/18375 19859/20558/18374 20135/20829/18650 20134/20828/18649\r\nf 19673/20372/18188 19672/20371/18187 20137/20831/18652 20138/20832/18653\r\nf 19672/20371/18187 19671/20370/18186 20139/20833/18654 20137/20831/18652\r\nf 19867/20566/18382 19866/20565/18381 20137/20831/18652 20139/20833/18654\r\nf 19866/20565/18381 19865/20564/18380 20138/20832/18653 20137/20831/18652\r\nf 19682/20381/18197 19681/20380/18196 20140/20834/18655 20141/20835/18656\r\nf 19681/20380/18196 19680/20379/18195 20142/20836/18657 20140/20834/18655\r\nf 19873/20572/18388 19872/20571/18387 20140/20834/18655 20142/20836/18657\r\nf 19872/20571/18387 19871/20570/18386 20141/20835/18656 20140/20834/18655\r\nf 19671/20370/18186 19696/20395/18211 20143/20837/18658 20139/20833/18654\r\nf 19696/20395/18211 19682/20381/18197 20141/20835/18656 20143/20837/18658\r\nf 19871/20570/18386 19876/20575/18391 20143/20837/18658 20141/20835/18656\r\nf 19876/20575/18391 19867/20566/18382 20139/20833/18654 20143/20837/18658\r\nf 19680/20379/18195 19699/20398/18214 20144/20838/18659 20142/20836/18657\r\nf 19699/20398/18214 19664/20363/18179 20135/20829/18650 20144/20838/18659\r\nf 19859/20558/18374 19878/20577/18393 20144/20838/18659 20135/20829/18650\r\nf 19878/20577/18393 19873/20572/18388 20142/20836/18657 20144/20838/18659\r\nf 19709/20408/18224 19708/20407/18223 20145/20839/18660 20146/20840/18661\r\nf 19708/20407/18223 19673/20372/18188 20138/20832/18653 20145/20839/18660\r\nf 19865/20564/18380 19882/20581/18397 20145/20839/18660 20138/20832/18653\r\nf 19882/20581/18397 19881/20580/18396 20146/20840/18661 20145/20839/18660\r\nf 20021/20720/18536 20147/20841/18662 20148/20842/18663 20016/20715/18531\r\nf 20147/20841/18662 19800/20499/18315 20149/20843/18664 20148/20842/18663\r\nf 19929/20628/18444 19928/20627/18443 20148/20842/18663 20149/20843/18664\r\nf 19928/20627/18443 19927/20626/18442 20016/20715/18531 20148/20842/18663\r\nf 19662/20361/18177 20150/20844/18665 20151/20845/18666 20136/20830/18651\r\nf 20150/20844/18665 19889/20588/18404 20152/20846/18667 20151/20845/18666\r\nf 19788/20487/18303 20153/20847/18668 20151/20845/18666 20152/20846/18667\r\nf 20153/20847/18668 19861/20560/18376 20136/20830/18651 20151/20845/18666\r\nf 19898/20597/18413 19897/20596/18412 20154/20848/18669 20155/20849/18670\r\nf 19897/20596/18412 19896/20595/18411 20156/20850/18671 20154/20848/18669\r\nf 19796/20495/18311 19795/20494/18310 20154/20848/18669 20156/20850/18671\r\nf 19795/20494/18310 19794/20493/18309 20155/20849/18670 20154/20848/18669\r\nf 19887/20586/18402 19959/20658/18474 20157/20851/18672 20158/20852/18673\r\nf 19959/20658/18474 19958/20657/18473 20159/20853/18674 20157/20851/18672\r\nf 19948/20647/18463 19946/20645/18461 20157/20851/18672 20159/20853/18674\r\nf 19946/20645/18461 19790/20489/18305 20158/20852/18673 20157/20851/18672\r\nf 20021/20720/18536 20020/20719/18535 20160/20854/18675 20161/20855/18676\r\nf 20020/20719/18535 20019/20718/18534 20162/20856/18677 20160/20854/18675\r\nf 20010/20709/18525 20007/20706/18522 20160/20854/18675 20162/20856/18677\r\nf 20007/20706/18522 20006/20705/18521 20161/20855/18676 20160/20854/18675\r\nf 19896/20595/18411 20077/20773/18592 20163/20857/18678 20156/20850/18671\r\nf 20077/20773/18592 20076/20772/18591 20164/20858/18591 20163/20857/18678\r\nf 20004/20703/18519 20002/20701/18517 20163/20857/18678 20164/20858/18591\r\nf 20002/20701/18517 19796/20495/18311 20156/20850/18671 20163/20857/18678\r\nf 19958/20657/18473 20124/20818/18639 20165/20859/18679 20159/20853/18674\r\nf 20124/20818/18639 19898/20597/18413 20155/20849/18670 20165/20859/18679\r\nf 19794/20493/18309 20120/20814/18635 20165/20859/18679 20155/20849/18670\r\nf 20120/20814/18635 19948/20647/18463 20159/20853/18674 20165/20859/18679\r\nf 19802/20501/18317 19801/20500/18316 20166/20860/18680 20009/20708/18524\r\nf 19801/20500/18316 19800/20499/18315 20147/20841/18662 20166/20860/18680\r\nf 20021/20720/18536 20161/20855/18676 20166/20860/18680 20147/20841/18662\r\nf 20161/20855/18676 20006/20705/18521 20009/20708/18524 20166/20860/18680\r\nf 19909/20608/18424 19908/20607/18423 20167/20861/18681 20168/20862/18682\r\nf 19908/20607/18423 19907/20606/18422 20169/20863/18683 20167/20861/18681\r\nf 19812/20511/18327 19856/20555/18371 20167/20861/18681 20169/20863/18683\r\nf 19856/20555/18371 19855/20554/18370 20168/20862/18682 20167/20861/18681\r\nf 19907/20606/18422 19915/20614/18430 20170/20864/18684 20169/20863/18683\r\nf 19915/20614/18430 19914/20613/18429 20171/20865/18685 20170/20864/18684\r\nf 19816/20515/18331 19813/20512/18328 20170/20864/18684 20171/20865/18685\r\nf 19813/20512/18328 19812/20511/18327 20169/20863/18683 20170/20864/18684\r\nf 19924/20623/18439 19923/20622/18438 20172/20866/18686 20089/20785/18604\r\nf 19923/20622/18438 19922/20621/18437 20173/20867/18687 20172/20866/18686\r\nf 19807/20506/18322 19804/20503/18319 20172/20866/18686 20173/20867/18687\r\nf 19804/20503/18319 19803/20502/18318 20089/20785/18604 20172/20866/18686\r\nf 19914/20613/18429 19930/20629/18445 20174/20868/18688 20171/20865/18685\r\nf 19930/20629/18445 19929/20628/18444 20175/20869/18689 20174/20868/18688\r\nf 19881/20580/18396 19879/20578/18394 20174/20868/18688 20175/20869/18689\r\nf 19879/20578/18394 19816/20515/18331 20171/20865/18685 20174/20868/18688\r\nf 19802/20501/18317 20013/20712/18528 20176/20870/18690 19798/20497/18313\r\nf 20013/20712/18528 20012/20711/18527 20177/20871/18691 20176/20870/18690\r\nf 19994/20693/18509 19992/20691/18507 20176/20870/18690 20177/20871/18691\r\nf 19992/20691/18507 19711/20410/18226 19798/20497/18313 20176/20870/18690\r\nf 19924/20623/18439 20092/20788/18607 20178/20872/18692 19919/20618/18434\r\nf 20092/20788/18607 20091/20787/18606 20179/20873/18693 20178/20872/18692\r\nf 20095/20790/18610 20093/20618/18608 20178/20872/18692 20179/20873/18693\r\nf 20093/20618/18608 19916/20615/18431 19919/20618/18434 20178/20872/18692\r\nf 19929/20628/18444 20149/20843/18664 20180/20874/18694 20175/20869/18689\r\nf 20149/20843/18664 19800/20499/18315 19799/20498/18314 20180/20874/18694\r\nf 19709/20408/18224 20146/20840/18661 20180/20874/18694 19799/20498/18314\r\nf 20146/20840/18661 19881/20580/18396 20175/20869/18689 20180/20874/18694\r\nf 19693/20392/18208 19692/20391/18207 20181/20875/18695 20182/20876/18696\r\nf 19692/20391/18207 19691/20390/18206 20183/20877/18697 20181/20875/18695\r\nf 19822/20521/18337 19819/20518/18334 20181/20875/18695 20183/20877/18697\r\nf 19819/20518/18334 19818/20517/18333 20182/20876/18696 20181/20875/18695\r\nf 19705/20404/18220 19704/20403/18219 20184/20878/18698 20128/20822/18643\r\nf 19704/20403/18219 19693/20392/18208 20182/20876/18696 20184/20878/18698\r\nf 19818/20517/18333 19953/20652/18468 20184/20878/18698 20182/20876/18696\r\nf 19953/20652/18468 19952/20651/18467 20128/20822/18643 20184/20878/18698\r\nf 19809/20508/18324 19808/20507/18323 20185/20879/18699 20186/20880/18700\r\nf 19808/20507/18323 19807/20506/18322 20187/20881/18701 20185/20879/18699\r\nf 19855/20554/18370 19854/20553/18369 20185/20879/18699 20187/20881/18701\r\nf 19854/20553/18369 19721/20420/18236 20186/20880/18700 20185/20879/18699\r\nf 19835/20534/18350 19834/20533/18349 20188/20882/18702 20189/20883/18703\r\nf 19834/20533/18349 19833/20532/18348 20190/20884/18704 20188/20882/18702\r\nf 20032/20731/18547 20035/20733/18550 20188/20882/18702 20190/20884/18704\r\nf 20035/20733/18550 20040/20737/18555 20189/20883/18703 20188/20882/18702\r\nf 19839/20538/18354 19838/20537/18353 20191/20885/18705 19849/20548/18364\r\nf 19838/20537/18353 19809/20508/18324 20186/20880/18700 20191/20885/18705\r\nf 19721/20420/18236 19724/20423/18239 20191/20885/18705 20186/20880/18700\r\nf 19724/20423/18239 19729/20428/18244 19849/20548/18364 20191/20885/18705\r\nf 19847/20546/18362 19846/20545/18361 20192/20886/18706 20193/20887/18707\r\nf 19846/20545/18361 19845/20544/18360 20194/20888/18708 20192/20886/18706\r\nf 20045/20742/18560 20048/20745/18563 20192/20886/18706 20194/20888/18708\r\nf 20048/20745/18563 20053/20750/18568 20193/20887/18707 20192/20886/18706\r\nf 19839/20538/18354 19850/20549/18365 20195/20889/18709 19840/20539/18355\r\nf 19850/20549/18365 19847/20546/18362 20193/20887/18707 20195/20889/18709\r\nf 20053/20750/18568 20055/20752/18570 20195/20889/18709 20193/20887/18707\r\nf 20055/20752/18570 19841/20540/18356 19840/20539/18355 20195/20889/18709\r\nf 19845/20544/18360 19852/20551/18367 20196/20890/18710 20194/20888/18708\r\nf 19852/20551/18367 19835/20534/18350 20189/20883/18703 20196/20890/18710\r\nf 20040/20737/18555 20058/20755/18573 20196/20890/18710 20189/20883/18703\r\nf 20058/20755/18573 20045/20742/18560 20194/20888/18708 20196/20890/18710\r\nf 19922/20621/18437 19921/20620/18436 20197/20891/18711 20198/20892/18712\r\nf 19921/20620/18436 19920/20619/18435 20199/20893/18713 20197/20891/18711\r\nf 19901/20600/18416 19904/20603/18419 20197/20891/18711 20199/20893/18713\r\nf 19904/20603/18419 19909/20608/18424 20198/20892/18712 20197/20891/18711\r\nf 19939/20638/18454 19938/20637/18453 20200/20894/18714 20102/20797/18617\r\nf 19938/20637/18453 19937/20636/18452 20125/20819/18640 20200/20894/18714\r\nf 19828/20527/18343 19825/20524/18340 20200/20894/18714 20125/20819/18640\r\nf 19825/20524/18340 19824/20523/18339 20102/20797/18617 20200/20894/18714\r\nf 19920/20619/18435 20098/20793/18613 20201/20891/18715 20199/20893/18713\r\nf 20098/20793/18613 20097/20792/18612 20202/20895/18716 20201/20891/18715\r\nf 20080/20776/18595 20078/20774/18593 20201/20891/18715 20202/20895/18716\r\nf 20078/20774/18593 19901/20600/18416 20199/20893/18713 20201/20891/18715\r\nf 19939/20638/18454 20105/20800/18620 20203/20630/18717 19934/20633/18449\r\nf 20105/20800/18620 20104/20799/18619 20204/20896/18718 20203/20630/18717\r\nf 20108/20802/18623 20106/20801/18621 20203/20630/18717 20204/20896/18718\r\nf 20106/20801/18621 19931/20630/18446 19934/20633/18449 20203/20630/18717\r\nf 19705/20404/18220 20127/20821/18642 20205/20897/18719 19703/20402/18218\r\nf 20127/20821/18642 19937/20636/18452 19936/20635/18451 20205/20897/18719\r\nf 19935/20634/18450 20132/20826/18647 20205/20897/18719 19936/20635/18451\r\nf 20132/20826/18647 19700/20399/18215 19703/20402/18218 20205/20897/18719\r\nf 19807/20506/18322 20173/20867/18687 20206/20898/18720 20187/20881/18701\r\nf 20173/20867/18687 19922/20621/18437 20198/20892/18712 20206/20898/18720\r\nf 19909/20608/18424 20168/20862/18682 20206/20898/18720 20198/20892/18712\r\nf 20168/20862/18682 19855/20554/18370 20187/20881/18701 20206/20898/18720\r\nf 19691/20390/18206 19690/20389/18205 20207/20899/18721 20208/20900/18722\r\nf 19690/20389/18205 19689/20388/18204 20209/20901/18723 20207/20899/18721\r\nf 20032/20731/18547 20190/20884/18704 20207/20899/18721 20209/20901/18723\r\nf 20190/20884/18704 19833/20532/18348 20208/20900/18722 20207/20899/18721\r\nf 19745/20444/18260 19744/20443/18259 20210/20902/18724 20211/20903/18725\r\nf 19744/20443/18259 19743/20442/18258 20212/20904/18726 20210/20902/18724\r\nf 19716/20415/18231 19768/20467/18283 20210/20902/18724 20212/20904/18726\r\nf 19768/20467/18283 19767/20466/18282 20211/20903/18725 20210/20902/18724\r\nf 19788/20487/18303 19787/20486/18302 20213/20905/18727 20153/20847/18668\r\nf 19787/20486/18302 19745/20444/18260 20211/20903/18725 20213/20905/18727\r\nf 19767/20466/18282 19862/20561/18377 20213/20905/18727 20211/20903/18725\r\nf 19862/20561/18377 19861/20560/18376 20153/20847/18668 20213/20905/18727\r\nf 19743/20442/18258 19823/20522/18338 20214/20906/18728 20212/20904/18726\r\nf 19823/20522/18338 19822/20521/18337 20215/20907/18729 20214/20906/18728\r\nf 19718/20417/18233 19717/20416/18232 20214/20906/18728 20215/20907/18729\r\nf 19717/20416/18232 19716/20415/18231 20212/20904/18726 20214/20906/18728\r\nf 19891/20590/18406 19890/20589/18405 20216/20908/18730 20061/20757/18576\r\nf 19890/20589/18405 19889/20588/18404 20150/20844/18665 20216/20908/18730\r\nf 19662/20361/18177 19659/20358/18174 20216/20908/18730 20150/20844/18665\r\nf 19659/20358/18174 19658/20357/18173 20061/20757/18576 20216/20908/18730\r\nf 19689/20388/18204 19983/20682/18498 20217/20909/18731 20209/20901/18723\r\nf 19983/20682/18498 19982/20681/18497 20218/20910/18732 20217/20909/18731\r\nf 20036/20734/18551 20033/20732/18548 20217/20909/18731 20218/20910/18732\r\nf 20033/20732/18548 20032/20731/18547 20209/20901/18723 20217/20909/18731\r\nf 19891/20590/18406 20064/20760/18579 20219/20911/18733 19886/20585/18401\r\nf 20064/20760/18579 20063/20759/18578 20220/20912/18734 20219/20911/18733\r\nf 20114/20808/18629 20112/20806/18627 20219/20911/18733 20220/20912/18734\r\nf 20112/20806/18627 19883/20582/18398 19886/20585/18401 20219/20911/18733\r\nf 19788/20487/18303 20152/20846/18667 20221/20913/18735 19789/20488/18304\r\nf 20152/20846/18667 19889/20588/18404 19888/20587/18403 20221/20913/18735\r\nf 19887/20586/18402 20158/20852/18673 20221/20913/18735 19888/20587/18403\r\nf 20158/20852/18673 19790/20489/18305 19789/20488/18304 20221/20913/18735\r\nf 19822/20521/18337 20183/20877/18697 20222/20914/18736 20215/20907/18729\r\nf 20183/20877/18697 19691/20390/18206 20208/20900/18722 20222/20914/18736\r\nf 19833/20532/18348 19832/20531/18347 20222/20914/18736 20208/20900/18722\r\nf 19832/20531/18347 19718/20417/18233 20215/20907/18729 20222/20914/18736\r\n";if(!customElements.get('mrts-scroll-helmet-v4'))customElements.define('mrts-scroll-helmet-v4',class extends HTMLElement{connectedCallback(){this.controller=new AbortController();this.start().catch(()=>{this.dataset.error='true';this.querySelector('.helmet-stage').textContent='Le casque 3D n’a pas pu démarrer. Rechargez la page.';});}disconnectedCallback(){this.controller.abort();this.cleanup?.();}async start(){const root=this,signal=this.controller.signal;if(!this.isConnected)return;

const story=root.querySelector('.story');
const stage=root.querySelector('.helmet-stage');
const annotation=root.querySelector('.annotation');
const chapters=Array.from(root.querySelectorAll('[data-mrts-chapter]'),el=>[el.querySelector('[data-label]').textContent,el.querySelector('[data-title]').innerHTML,el.querySelector('[data-body]').innerHTML]);
const reduce=matchMedia('(prefers-reduced-motion: reduce)').matches;

let progress=0,index=-1,object=null,renderer=null,scene,camera,raf=0,rim=null;
function update(){
 if(!chapters.length)return;
 const r=story.getBoundingClientRect(),span=story.offsetHeight-innerHeight;
 progress=span>0?Math.max(0,Math.min(1,-r.top/span)):0;
 const next=Math.max(0,Math.min(3,Math.floor(progress*4)));
 if(next!==index){index=next;const c=chapters[index];root.querySelector('.chapter').textContent=`0${index+1} / 04 — ${c[0]}`;root.querySelector('.story-title').innerHTML=c[1];root.querySelector('.story-body').innerHTML=c[2];annotation.classList.toggle('right',index%2===1);}
 const right=index%2===1 && !matchMedia('(max-width:600px)').matches;
 root.dataset.glowSide=right?'right':'left';
 if(rim)rim.position.x=right?3:-3;
 root.querySelector('.progress i').style.width=(progress*100)+'%';
 root.querySelector('.angle').textContent=`${chapters[index][0]} / ${String(Math.round(progress*360)).padStart(3,'0')}°`;
 if(object){object.rotation.y=Math.PI+(reduce?index/4:progress)*Math.PI*2;renderer.render(scene,camera);}
 raf=0;
}
// Capture on document: some themes (Fabric) scroll .page-wrapper instead of window.
document.addEventListener('scroll',()=>{if(!raf)raf=requestAnimationFrame(update)},{passive:true,capture:true,signal});
function resize(){if(!renderer)return;const w=stage.clientWidth,h=stage.clientHeight;if(w>0&&h>0){renderer.setSize(w,h,false);camera.aspect=w/h;camera.updateProjectionMatrix();}update();}
try{
 renderer=new THREE.WebGLRenderer({alpha:true,antialias:true,powerPreference:'low-power'});renderer.setPixelRatio(Math.min(devicePixelRatio,1.7));renderer.outputColorSpace=THREE.SRGBColorSpace;renderer.toneMapping=THREE.ACESFilmicToneMapping;renderer.toneMappingExposure=1.25;
 scene=new THREE.Scene();camera=new THREE.PerspectiveCamera(33,1,.1,30);camera.position.set(0,.55,4.5);camera.lookAt(0,0,0);
 scene.add(new THREE.HemisphereLight(0xf4eafa,0x28202b,2.2));
 const key=new THREE.DirectionalLight(0xffffff,4.5);key.position.set(-3,4,4);scene.add(key);
 rim=new THREE.DirectionalLight(root.dataset.glowColor||'#ff4b9a',root.dataset.glowEnabled==='false'?0:Math.max(0,Math.min(100,Number(root.dataset.glowIntensity)||0))*0.0857);rim.position.set(-3,1,-3);scene.add(rim);
 const fill=new THREE.DirectionalLight(0xcdd5ff,2);fill.position.set(4,2,3);scene.add(fill);
 const raw=new OBJLoader().parse(helmetData);(()=>{
  const box=new THREE.Box3().setFromObject(raw),center=box.getCenter(new THREE.Vector3()),size=box.getSize(new THREE.Vector3());
  raw.position.sub(center);const scale=2.1/Math.max(size.x,size.y,size.z);object=new THREE.Group();object.add(raw);object.scale.setScalar(scale);
  const texCache={};
  const loadFabric=url=>texCache[url]||(texCache[url]=new Promise(res=>new THREE.TextureLoader().load(url,t=>{t.wrapS=t.wrapT=THREE.RepeatWrapping;t.colorSpace=THREE.SRGBColorSpace;t.anisotropy=renderer.capabilities.getMaxAnisotropy();res(t);},undefined,()=>res(null))));
  raw.traverse(m=>{if(!m.isMesh)return;const n=m.name.toLowerCase();let color=0x222024,roughness=.72,fabric=null;
   if(n==='object002'){color=0x161412;roughness=.95;}
   if(n==='panel_front'||n==='panel_rear'||n.startsWith('side_strip_mid')){color=0x15130f;roughness=.55;fabric=root.dataset.fabricMain;}
   if(n.startsWith('side_strip_bot')){color=0xe87fa8;roughness=.55;fabric=root.dataset.fabricSide;}
   if(n.includes('finishing')||n==='shape001'){color=0xe5dfe5;roughness=.52;}
   if(n.includes('padding')||n==='trim'){color=0x0c0b0e;roughness=.85;}
   m.material=new THREE.MeshStandardMaterial({color,roughness,metalness:.04,side:THREE.DoubleSide});if(!m.geometry.attributes.normal)m.geometry.computeVertexNormals();
   if(fabric)loadFabric(fabric).then(t=>{if(!t||signal.aborted)return;m.material.map=t;m.material.color.set(0xffffff);m.material.needsUpdate=true;update();});
  });
  const front=raw.getObjectByName('panel_front');
  if(front&&root.dataset.frontLogo){
   const image=new Image();image.crossOrigin='anonymous';
   image.onload=()=>{
    if(signal.aborted)return;
    try{
    const canvas=document.createElement('canvas');canvas.width=image.width+2;canvas.height=image.height+2;
    const ctx=canvas.getContext('2d');ctx.drawImage(image,1,1);
    const pixels=ctx.getImageData(0,0,canvas.width,canvas.height),data=pixels.data;
    const hex=root.dataset.frontLogoColor||'#e3017e';
    const tint=[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16));
    for(let i=0;i<data.length;i+=4){
     if(!data[i+3])continue;
     const amount=Math.min(1,(255-Math.min(data[i],data[i+1],data[i+2]))/223);
     for(let c=0;c<3;c++)data[i+c]=255+(tint[c]-255)*amount;
    }
    ctx.putImageData(pixels,0,0);
    const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;
    texture.anisotropy=renderer.capabilities.getMaxAnisotropy();
    const geometry=front.geometry.clone();geometry.computeBoundingBox();
    const bounds=geometry.boundingBox,center=bounds.getCenter(new THREE.Vector3());
    const width=Math.min(81,(bounds.max.x-bounds.min.x)*.8);
    const height=width*canvas.height/canvas.width;
    const positions=geometry.attributes.position,uv=geometry.attributes.uv;
    for(let i=0;i<positions.count;i++)uv.setXY(i,.5-(positions.getX(i)-center.x)/width,.5+(positions.getY(i)-center.y)/height);
    uv.needsUpdate=true;
    const material=new THREE.MeshStandardMaterial({map:texture,transparent:true,roughness:.62,metalness:.02,depthWrite:false,polygonOffset:true,polygonOffsetFactor:-3,polygonOffsetUnits:-3,side:THREE.DoubleSide});
    const logo=new THREE.Mesh(geometry,material);logo.name='mrts_front_logo';logo.renderOrder=2;
    front.add(logo);update();
    }catch(err){console.error('MRTS 3D logo',err);}
   };
   image.src=root.dataset.frontLogo;
  }
  scene.add(object);stage.replaceChildren(renderer.domElement);resize();
 })();
 addEventListener('resize',resize,{signal});
}catch(e){console.error('MRTS 3D',e);if(!stage.querySelector('canvas')){stage.dataset.fallback='true';stage.textContent='La 3D ne peut pas démarrer. Vérifiez que WebGL est activé dans votre navigateur.';}}
if('ResizeObserver'in window){const ro=new ResizeObserver(()=>resize());ro.observe(stage);signal.addEventListener('abort',()=>ro.disconnect());}
update();

root.cleanup=()=>{if(raf)cancelAnimationFrame(raf);if(object)object.traverse(m=>{if(m.isMesh){m.geometry.dispose();m.material.map?.dispose();m.material.dispose();}});renderer?.dispose();};
}});})();